// EON OS — frontend (single-file, no framework)
// Editorial minimal aesthetic. Archivo grotesque for display (DESIGN FLOW type, 2026-07-06), JetBrains Mono for labels.

import { draftContext, adoptReply, safeDeskHref, safeTrackingHref, groupDeskNotifications, acknowledgeDeskNotification, recordedOrderTimeline, filterDeskActions } from './cs_interactions.js';
import { createCommonGround } from './common_ground.js';
import { createWorkroomHost } from './eon_workroom_host.js';
import { createHomeRefinement } from './home_refinement.js';
import { createLivingWorkspace, livingEnabled, readIdentity } from './living_workspace.js';
import { createWorkRoom } from './work_room.js';
import { openPaymentEvidence } from './payment_evidence.js';
import { openDecisionEvidence } from './cs_decision_evidence.js';
import { mountAscentWork } from './ascent_work.js';
import { openServiceCases, mountServiceCaseSummary } from './service_cases.js';
import { renderForward, bindForward } from './money_projection_studio.js';
import { wrapMoneyWorkspace, mountMoneyWorkspace, scheduleMoneyRead, paintMoneyRecordGaps, paintBankPositionTable, mountMoneyActivity, mountMoneyMomentum, moneyMomentumData, moneyViewScope, cleanMoneyView, readMoneyView, writeMoneyView } from './money_workspace.js';
import { renderMoneyReviewDesk, mountMoneyReviewDesk } from './money_review.js';
import { createChatConversation, canSubmitKey, sourcesHtml, sourceReferences, draftStorageKey } from './chat_conversation.js';

const API = window.location.origin;
const SUPABASE_URL = window.SUPABASE_URL || '';
const SUPABASE_ANON = window.SUPABASE_ANON || '';

// EON_BUILD — the operator reads this from the side menu (under Sign out)
// to confirm "did the new code reach my device?" vs "is this a stale
// service-worker cache?". If the version chip looks wrong, hard-refresh
// (Cmd+Shift+R) or clear the service worker.
// SINCE 15 SEPTEMBER 2026 THE DEPLOY STAMPS THIS LINE, NOT YOU: the
// served app.js carries +<sha7> of the deploy commit, so every deploy moves
// the stamp on its own. No pull request edits EON_BUILD here or
// SHELL_VERSION in sw.js — no bump, no history line, no reserved number. A
// diff that changes either declaration fails the guard; remove the change.
//   Rule:  .agents/skills/eon-ship/SKILL.md ("SHELL_VERSION / EON_BUILD —
//          do not touch them")
//   Guard: tests/test_deploy_stamps_the_shell_2026_09_15.py
//          (test_builders_do_not_touch_the_stamp_lines)
// The dated lines below are the RECORD of the hand bumps before that date.
// The stamping itself is app/web/shell_stamp.py.
// (2026-07-29) There is no longer a hand-kept _WHATS_NEW list to update
// — the What's-new sheet reads play_patch_notes, which the
// play-patch-note workflow writes automatically on every merge. The
// hand-kept list had gone two months stale, which is exactly what a
// list that depends on remembering does.
// 2026-05-19 (Daniel): "the VERSION STYLE doesn't look right". Was
// "v3.0p34 · whats-new-restock · 249a40c3" — branch name leaked into
// the chip + tenant prefix mashed onto the end. Cleaner now: just the
// version. The tenant prefix is rendered separately at the menu site
// with a muted style.
// … was: EON_BUILD = 'v3.0p1576-cs-primary-mobile' (superseded by p1621)
// … was: EON_BUILD = 'v3.0p1726-home-fluid-rooms' (superseded by p1729)
// … was: EON_BUILD = 'v3.0p1729-home-chart-alignment' (superseded by p1732)
// … was: EON_BUILD = 'v3.0p1758-shared-ui-integration' (superseded by p1759)
// … was: EON_BUILD = 'v3.0p1731-task-mail-readback' (superseded by p1759)
// … was: EON_BUILD = 'v3.0p1759-full-ui-integration' (superseded by p1812)
// … was: EON_BUILD = 'v3.0p1812-full-ui-integration' (superseded by p1821)
// … was: EON_BUILD = 'v3.0p1722-task-black-intake' (superseded by p1749)
// … was: EON_BUILD = 'v3.0p1749-colleague-and-picture-door' (superseded by p1794)
// … was: EON_BUILD = 'v3.0p1794-colleague-and-picture-door' (superseded by p1818)
// … was: EON_BUILD = 'v3.0p1818-colleague-and-picture-door' (superseded by p1824)
// Local integration includes p1794; retain the existing p1797 marker below until final release reservation.

// … was: EON_BUILD = 'v3.0p1722-task-black-intake' (superseded by p1752)
// … was: EON_BUILD = 'v3.0p1752-people-staff-ground' (superseded by p1797)
// … was: EON_BUILD = 'v3.0p1797-people-staff-ground' (superseded by p1806)
// (p1806) 2026-09-09, reserved: combined People working day/asks, Staff Colleague,
// ground motion and Workroom components; correct UUID coaching scope and unavailable reads.
// … was: EON_BUILD = 'v3.0p1824-people-staff-workroom' (superseded by final combined release)
// … was: EON_BUILD = 'v3.0p1818-colleague-and-picture-door' (superseded by p1821)
// … was: EON_BUILD = 'v3.0p1821-full-ui-integration' (superseded by p1836)
// … was: EON_BUILD = 'v3.0p1818-colleague-and-picture-door' (superseded by p1833)
// 2026-09-10 (p1833), reserved: Task Room full briefs and explicit responses,
// retaining the Colleague and operator-chat picture door on current main.
// … was: EON_BUILD = 'v3.0p1833-task-workflow-clarity' (superseded by p1836)
// 2026-09-10 (p1836): combine full UI refinement with Task Room scoped briefs.
// Reservation failed DNS; current main p1833 +3 fallback.
// … was: EON_BUILD = 'v3.0p1836-full-ui-task-integration' (superseded by the next reserved Studio release)
// … was: EON_BUILD = 'v3.0p1833-task-workflow-clarity' (superseded by the integrated Studio release)
// … was: EON_BUILD = 'v3.0p1818-colleague-and-picture-door' (superseded by p1827)
// EON_BUILD = 'v3.0p1827-studio-integrated' (superseded by p1842)
// 2026-09-10 (p1842), atomically reserved: Studio member access, viewer controls
// and read-failure presentation; retain the integrated Studio's prior history.
// EON_BUILD = 'v3.0p1842-studio-viewer-access' (superseded by p1851)
// 2026-09-10 (p1851), atomically reserved: integrate Studio with the deployed
// Task Room header/modal fixes; retain both histories and all existing behavior.
// … was: EON_BUILD = 'v3.0p1851-studio-release' (superseded by p1860)
// 2026-09-10 (p1860), reserved: Studio viewer inspection after full UI/Mail integration.
// … was: EON_BUILD = 'v3.0p1860-studio-viewer-inspection' (superseded by p1887)
// 2026-09-10 (p1887), atomically reserved: People/Staff Workroom with
// current Task Room privacy, full UI, Mail and Studio releases preserved.
// … was: EON_BUILD = 'v3.0p1887-people-staff-workroom' (superseded by p1926)
// … was: EON_BUILD = 'v3.0p1860-studio-viewer-inspection' (superseded by p1884)
// 2026-09-10 (p1884), reserved: SEV — signed-out boot no longer crashes on a
// temporal-dead-zone binding; the sign-in screen renders again.
// … was: EON_BUILD = 'v3.0p1884-signed-out-boot' (superseded by p1926)
// 2026-09-12 (p1926), atomically reserved: People/Staff Workroom plus
// current signed-out boot, Ask EON voice and approved main releases.
// … was: EON_BUILD = 'v3.0p1926-people-staff-workroom' (superseded during current-main reconciliation)
// … was: EON_BUILD = 'v3.0p1860-studio-viewer-inspection' (superseded by p1872)
// … was: EON_BUILD = 'v3.0p1725-money-forward' (superseded by p1872)
// 2026-09-10 (p1872), atomically reserved: private Money projections alongside the current Home, Tasks and Studio release.
// … was: EON_BUILD = 'v3.0p1872-money-forward' (superseded by p1905)
// … was: EON_BUILD = 'v3.0p1860-studio-viewer-inspection' (superseded by p1884)
// 2026-09-10 (p1884), reserved: SEV — signed-out boot no longer crashes on a
// temporal-dead-zone binding; the sign-in screen renders again.
// … was: EON_BUILD = 'v3.0p1884-signed-out-boot' (superseded by p1905)
// 2026-09-11 (p1905), atomically reserved: Money Forward reconciled with
// signed-out boot recovery and current CS/auth improvements; both histories retained.
// … was: EON_BUILD = 'v3.0p1905-money-forward' (superseded by p1911)
// 2026-09-11 (p1911), atomically reserved: persist forecast comparison basis
// and distinguish incomplete actuals from zero in the Money scoreboard.
// … was: EON_BUILD = 'v3.0p1911-money-forward' (superseded by p1977)
// … was: EON_BUILD = 'v3.0p1884-signed-out-boot' (superseded by p1923)
// 2026-09-12 (p1923): the Orchestra tile under RUN and the rooms strip; app.js
// changed, so the chip moves with the shell number the operator reads it against.
// … was: EON_BUILD = 'v3.0p1974-quiet-case-recovery' (superseded during accepted Core integration)
// Quiet WhatsApp and Squad payment evidence remain in this candidate.
// … was: EON_BUILD = 'v3.0p1971-case-recovery' (superseded during accepted prerequisite integration)
// … was: EON_BUILD = 'v3.0p1923-orchestra-floor' (superseded by p1977)
// 2026-09-12 (p1977), atomically reserved: Money floor integrity repairs; preserve merged shell history.
// … was: EON_BUILD = 'v3.0p1884-signed-out-boot' (superseded by p1917)
// 2026-09-11 (p1917), atomically reserved: keep Content Room image detail
// dismissible on an empty desk without enabling creation or approval controls.
// … was: EON_BUILD = 'v3.0p1917-studio-dialog-close' (superseded by p1962)
// … was: EON_BUILD = 'v3.0p1923-orchestra-floor' (superseded by p1962)
// 2026-09-12 (p1962), atomically reserved: integrate the dismissible Studio
// source picker with current shell history and preserve creation restrictions.
// … was: EON_BUILD = 'v3.0p1962-studio-dialog-close' (superseded by p1980)
// 2026-09-13 (p1980), atomically reserved: reconcile the Studio release
// with current main; later shell-only releases may advance independently.
// … was: EON_BUILD = 'v3.0p1980-studio-dialog-close' (superseded by p2055)
// 2026-09-13 (p2055), atomically reserved: connected People/Staff Workroom
// reconciled with main; preserve Studio, Marketing, Orchestra and login behavior.
// … was: EON_BUILD = 'v3.0p2055-people-staff-workroom' (superseded during current-main reconciliation)
// … was: EON_BUILD = 'v3.0p1980-studio-dialog-close' (superseded by p2022)
// 2026-09-13 (p2022), atomically reserved: join accepted strict rehearsal
// with CS case recovery, retaining Marketing, Studio and Orchestra history.
// … was: EON_BUILD = 'v3.0p2022-case-recovery' (superseded by p2025)
// 2026-09-13 (p2025), atomically reserved: quiet Design A and payment evidence
// on accepted rehearsal safeguards; retain CS, Marketing, Studio and Orchestra history.
// … was: EON_BUILD = 'v3.0p2025-quiet-case-recovery' (superseded by p2139)
// … was: EON_BUILD = 'v3.0p2022-case-recovery' (superseded during current-main reconciliation)
// … was: EON_BUILD = 'v3.0p1980-studio-dialog-close' (superseded by p2004)
// … was: EON_BUILD = 'v3.0p1977-money-floor-integrity' (superseded by p2004)
// 2026-09-13 (p2004), reserved for Money: reconcile the private projection release
// with main while retaining release histories and independent version floors.
// … was: EON_BUILD = 'v3.0p2004-money-brain' (superseded by p2034)
// 2026-09-13 (p2034), atomically reserved: Money follows the seat-billing,
// People and CS rehearsal releases; preserve both histories and existing workflows.
// … was: EON_BUILD = 'v3.0p2034-money-brain' (superseded by p2109)
// EON_BUILD = 'v3.0p1980-studio-dialog-close' (superseded by p2064)
// … was: EON_BUILD = 'v3.0p2064-factory-product-crossing' (superseded by p2091)
// … was: EON_BUILD = 'v3.0p2079-in-concert-pointer' (superseded by p2091)
// … was: EON_BUILD = 'v3.0p1980-studio-dialog-close' (superseded by p2043)
// … was: EON_BUILD = 'v3.0p2043-people-staff-ground' (superseded during main reconciliation)
// … was: EON_BUILD = 'v3.0p1980-studio-dialog-close' (superseded by p2058)
// 2026-09-13 (p2058), atomically reserved: People asks and coaching
// reconciled with current main, preserving existing decisions and release history.
// … was: EON_BUILD = 'v3.0p2058-people-asks-coaching' (superseded by p2091)
// (p2091) 2026-09-14, atomically reserved: retain People asks/coaching and
// WS-A readable controls, unobstructed receipts and default-motion regression proof.
// … was: EON_BUILD = 'v3.0p2091-people-ground-reconciled' (superseded by p2097)
// 2026-09-14: People/Staff Workroom reconciled on current main; p2097 atomically reserved at publication.
// … was: EON_BUILD = 'v3.0p2097-people-staff-workroom' (superseded during current-main reconciliation)
// … was: EON_BUILD = 'v3.0p2091-people-ground-reconciled' (superseded by p2106)
// 2026-09-14: fresh reservation p2106 invalidates cached Product crossing assets.
// … was: EON_BUILD = 'v3.0p2106-factory-product-crossing' (superseded by p2133)
// (p2133) 2026-09-14, atomically reserved at publication: connected Workroom
// with granted manager scope, resilient personal day and cross-login photo recovery.
// Saved People candidate: EON_BUILD = 'v3.0p2133-people-staff-workroom' (superseded during current-main reconciliation)

// … was: EON_BUILD = 'v3.0p2091-people-ground-reconciled' (superseded by p2109)
// (p2109) 2026-09-14, atomically reserved: Money projections reconciled with
// current main; retain People controls, existing workflows and both release histories.
// … was: EON_BUILD = 'v3.0p2091-people-ground-reconciled' (superseded by p2106)
// 2026-09-14: fresh reservation p2106 invalidates cached Product crossing assets.
// … was: EON_BUILD = 'v3.0p2106-factory-product-crossing' (superseded by p2139)
// 2026-09-14 (p2139), atomically reserved: Quiet conversation and rehearsal
// reconciled with current People, Factory and CS release history.
// … was: EON_BUILD = 'v3.0p2139-quiet-case-recovery' (superseded during current-Core source reconciliation)
// … was: EON_BUILD = 'v3.0p2106-factory-product-crossing' (superseded by p2109)

// 2026-09-14: retain Money reservation p2109 while integrating main p2106.
// … was: EON_BUILD = 'v3.0p2109-money-brain' (superseded by p2148)
// Main p2091 release is retained below alongside the Factory reservation history.
// … was: EON_BUILD = 'v3.0p1980-studio-dialog-close' (superseded by p1989)
// 2026-09-13 (p1989): the factory register joins the Stockroom; the chip moves
// with the shell number the operator reads it against. main's p1980 landed first.
// … was: EON_BUILD = 'v3.0p1989-factory-register' (superseded by p2040)
// … was: EON_BUILD = 'v3.0p2040-factory-register' (superseded by p2091)
// … was: EON_BUILD = 'v3.0p2091-people-ground-reconciled' (superseded by p2103)
// 2026-09-14: fresh reservation p2103 invalidates cached Factory register assets.
// … was: EON_BUILD = 'v3.0p2103-factory-register' (superseded by p2130)
// … was: EON_BUILD = 'v3.0p2091-people-ground-reconciled' (superseded by p2106)
// 2026-09-14: fresh reservation p2106 invalidates cached Product crossing assets.
// … was: EON_BUILD = 'v3.0p2106-factory-product-crossing' (superseded by p2121)
// … was: EON_BUILD = 'v3.0p2091-people-ground-reconciled' (superseded by p2121)
// (p2121) 2026-09-14, chair reserved: Chat Learning setting audit/history
// and the visible build label advance together with the service worker.
// … was: EON_BUILD = 'v3.0p2121-chat-learning' (superseded by p2124)
// … was: EON_BUILD = 'v3.0p2106-factory-product-crossing' (superseded by p2124)
// … was: EON_BUILD = 'v3.0p2091-people-ground-reconciled' (superseded by p2124)
// (p2124) 2026-09-14, chair-reserved for In concert default clicks and fading cursor.
// … was: EON_BUILD = 'v3.0p2124-in-concert-clicks' (superseded by current-main p2148)

// … was: EON_BUILD = 'v3.0p2121-chat-learning' (superseded by p2130)
// … was: EON_BUILD = 'v3.0p2106-factory-product-crossing' (superseded by p2130)
// 2026-09-14 (p2130): fresh reserved shell for Factory forecast creation and failed-refresh recovery.
// … was: EON_BUILD = 'v3.0p2130-factory-register-recovery' (superseded by p2148)
// (p2148) 2026-09-14, atomically reserved: Money Forward reconciled after Factory.
// Preserve both release histories, approved page sources and all current-main changes.
// … was: EON_BUILD = 'v3.0p2148-money-brain' (superseded by p2166)
// (p2166) 2026-09-15, atomically reserved: Workroom return and shared-device
// identity repair reconciled with current Money/Factory floor enforcement.
// … was: EON_BUILD = 'v3.0p2166-people-staff-workroom' (superseded during campaign publication)
// Unshipped proposal: p2148-money-brain -> p2172-quiet-case-recovery.
// 2026-09-15 (p2172), atomically reserved: Quiet conversation and strict rehearsal
// integrated with accepted Post Room, Atlas and Money evidence; preserve both histories.
// 2026-09-16: p2172 was reserved before deployment stamping and never shipped.
// … was: EON_BUILD = 'v3.0p2148-money-brain' (superseded by p2169)
// (p2169) 2026-09-15, atomically reserved: Core service cases and held replies,
// reconciled with current main while preserving both release histories.
// … was: EON_BUILD = 'v3.0p2169-cs-core' (superseded by p2181)
// … was: EON_BUILD = 'v3.0p2148-money-brain' (superseded by p2151)
// (p2151) 2026-09-15, atomically reserved: In concert shared interactions after Money.
// Dialog-only preferences; new touch visits stay silent; preserve all release histories.
// … was: EON_BUILD = 'v3.0p2151-in-concert-clicks' (superseded by p2154)
// (p2154) Reserved final UI repair, preserving every earlier release record.
// … was: EON_BUILD = 'v3.0p2154-in-concert-clicks' (superseded by p2157)
// … was: EON_BUILD = 'v3.0p2157-in-concert-clicks' (superseded by p2175)
// … was: EON_BUILD = 'v3.0p2175-pause-holds' (superseded by p2199)
// (p2199) 2026-09-15, atomically reserved: publish connected Workroom repair
// with current shared pointer, pause, cache and floor-verdict records retained.
// Unshipped proposal: p2175-pause-holds -> p2196-quiet-publication.
// 2026-09-15 (p2196), atomically reserved: Quiet publication with current shared UI.
// Keep the selected conversation desk, merged interactions and both release histories.
// 2026-09-16: p2196 was reserved before deployment stamping and never shipped.
// … was: EON_BUILD = 'v3.0p2175-pause-holds' (superseded by p2193)
// (p2193) 2026-09-15, atomically reserved: Studio PDP rows and Departures publication.
// … was: EON_BUILD = 'v3.0p2175-pause-holds' (superseded by p2211)
// (p2211) 2026-09-15, atomically reserved: Creative Tags review desk and safe confirmation.
const EON_BUILD = 'v3.0p2175-pause-holds+1d8ec20';
// v3.0p16 commit 9e (Item 5): how long to wait between sending a chat
// message and seeing the first server event (request_id / status /
// delta) before assuming the agent loop is stuck. 60s covers a normal
// multi-tool turn; longer than this is almost always a real problem.
// 2026-05-19 (Daniel): long expense batches with 10+ log_expense tool
// calls + Opus reasoning between each can exceed the previous 60s
// inactivity ceiling, false-firing the watchdog with "Response took
// too long — please retry. Reference: 5bd8b584" while the backend
// was still doing real work. Bumped to 180s. Watchdog still kicks on
// every WS event (delta, status, tool_done, request_id), so the only
// time the full 180s elapses is when the backend is genuinely silent
// — i.e. dead, not slow. Follow-up: emit a heartbeat from
// agent_stream every 30s so the watchdog measures liveness directly
// instead of inferring it from event cadence.
const STREAM_WATCHDOG_MS = 180_000;

// WS-F timing-only stage: opt-in hooks never alter requests or route work.
function eonTimingStart(kind) {
  try { return window.EON_TIMING?.start(kind, State.view) || null; } catch (_) { return null; }
}
function eonTimingFinish(ticket, status, host) {
  try { window.EON_TIMING?.finish(ticket, status, host); } catch (_) {}
}

const State = {
  jwt: localStorage.getItem('eon_jwt') || null,
  isStaff: false,        // booted from a staff PIN session token
  impersonating: false,  // owner is "viewing as" a staff member
  // True once an OWNER/email session has fully come up on this device.
  // logout() uses it so a leftover staff PIN token can never redirect the
  // owner to the staff PIN pad (2026-07-29 operator report).
  ownerSessionEstablished: false,
  user: null,
  tenant: null,
  workspaces: [],      // every workspace of a person with more than one (owner sign-in)
  workspaceLink: null, // slug a ?workspace= link opened instead of the default
  member: null,
  view: 'home',
  authMode: 'signin',  // 'signin' | 'forgot' | 'reset' | 'invite_signup'
  resetToken: null,    // recovery token from URL when in 'reset' mode
  inviteToken: null,   // invite token from URL when in 'invite_signup' mode (v3.0p13)
  inviteEmailHint: '', // optional pre-filled email for invite signup form
  chat: [],
  tasks: [],
  taskFilter: 'all',
  ws: null,
  notifs: [],
  alerts: [],         // v3.0: dashboard alerts from /api/alerts
  homePeriod: 'last_7d',  // v3.0: selected date chip on home
  notifsOpen: false,
  menuOpen: false,
  loading: false,
  voice: {
    discoveryGeneration: 0,
    transcribeAvailable: false,
    ttsAvailable: false,
    ttsEnabled: localStorage.getItem('eon_tts') === '1',
    voiceId: null,
  },
  recorder: {
    instance: null,    // MediaRecorder
    chunks: [],
    stream: null,      // MediaStream so we can stop the mic track
    cancelled: false,
    // Voice-parity pass (2026-07-12, Claude Code desktop spec):
    mode: null,        // 'hold' (push-to-talk) | 'tap' (toggle)
    pointerDown: false,
    mime: null,
    startedAt: 0,
    audioCtx: null,    // WebAudio analyser for level meter + silence stop
    analyser: null,
    meterTimer: null,
    silenceMs: 0,
    interimTimer: null,
    interimInFlight: false,
    interimSeq: 0,
  },
  // v3.0p14: file attachments staged for the next chat message
  // Each item: { id, filename, fileType, sizeBytes, charCount, status, error }
  // status: 'uploading' | 'ready' | 'failed'
  attachments: [],
  // v3.0p14: chat history / conversations
  // activeConversationId: null = let backend auto-resolve; UUID = pin to that thread
  activeConversationId: null,
  activeConversationTitle: null,
  conversations: [],         // list returned by GET /api/conversations
  chatHistoryOpen: false,    // drawer state
  // 2026-08-04 (operator): Files drawer — every upload, filed into date
  // folders, across ALL conversations. The composer strip only ever
  // showed unattached uploads from the last 24h, so older bank
  // statements read as lost.
  filesOpen: false,
  filesLibrary: null,        // last GET /api/uploads/library payload
  filesLoading: false,
  filesError: null,
  filesFilter: { group_by: 'month', bank: '', kind: 'all', q: '' },
  filesCollapsed: {},        // folder key -> true when folded shut
  // 2026-06-07 (Daniel): in-app full-screen chat. Expands the existing
  // functional chat (charts, tool-traces, attachments, permission
  // cards all intact) to fill the viewport by hiding the app header +
  // bottom nav — NOT the separate white /chat page. Persisted so the
  // expanded state survives a reload while the operator is in chat.
  chatFullscreen: localStorage.getItem('eon_chat_fullscreen') === '1',
  // v3.0p16 9e: stream watchdog timer + chat rid being watched
  _streamWatchdog: null,
  _streamWatchdogRid: null,
};

// One authenticated Workroom visit. Existing writers remain their own screens.
let eonWorkroomVisit = null;
let eonWorkroomOperations = false;
let eonWorkroomChatEntry = null;
document.addEventListener('click',event=>{
  if(event.target.closest('[data-workroom-return]') && eonWorkroomOperations && eonWorkroomAvailable()){
    eonWorkroomChatEntry=null;cswLeaveDesk();eonWorkroomOperations=false;render();
  }
});
function eonWorkroomDestroy() { eonWorkroomVisit?.destroy(); eonWorkroomVisit = null; }
// Rendering and navigating keep this person's operations door open. A fresh
// authentication/access context never inherits the previous person's choice.
function eonWorkroomReset() { eonWorkroomChatEntry = null; eonWorkroomOperations = false; eonWorkroomDestroy(); }
function cswThreadMetaInvalidate() {
  const s = State.csw;
  if (!s) return;
  s._threadMetaGeneration = (s._threadMetaGeneration || 0) + 1;
  s._threadMetaRequired = true;
  s._threadMeta = null;
  if (s.activeThread) {
    // Preserve ownership, routing, holds and uncertain-send custody.
    s.activeThread = {...s.activeThread, last_inbound_at:null, window_expires_at:null, window_open:null};
  }
}

// Only stable, readable identity can keep the CS visit across a rotation.
// This comparison grants no authority: the server still verifies each token.
function cswThreadMetaSameSession(previous, next) {
  const identity = token => {
    try {
      if (typeof token !== 'string' || token.split('.').length !== 3) return null;
      const session = eonWorkroomSession(token);
      if (!session || session.impersonated) return null;
      const part = token.split('.')[1];
      const claims = JSON.parse(atob(part.replace(/-/g,'+').replace(/_/g,'/').padEnd(Math.ceil(part.length/4)*4,'=')));
      const audience = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
      if (!audience.length || audience.some(v => typeof v !== 'string' || !v)
          || (claims.imp !== undefined && claims.imp !== false)
          || (claims.tid !== undefined && claims.tid !== session.tenantId)
          || (claims.tenant !== undefined && claims.tenant !== session.tenantId)) return null;
      if (State.isStaff === true) {
        if (!Number.isSafeInteger(claims.epoch) || claims.epoch < 0
            || !Number.isSafeInteger(claims.auth_time) || claims.auth_time <= 0) return null;
      } else if (typeof claims.session_id !== 'string' || !claims.session_id.trim()) return null;
      if (claims.session_id != null && (typeof claims.session_id !== 'string' || !claims.session_id.trim())) return null;
      return JSON.stringify([session.tenantId,session.actorId,session.authSession,
        claims.tid ?? null,claims.tenant ?? null,[...new Set(audience)].sort()]);
    } catch (_) { return null; }
  };
  const before = identity(previous), after = identity(next);
  return before !== null && after !== null && before === after;
}

for (const key of ['user','tenant','member','impersonating','isStaff','view','jwt']) {
  let value = State[key];
  Object.defineProperty(State,key,{enumerable:true,configurable:true,get:()=>value,set:next=>{
    if(next!==value){
      if(key!=='jwt'||!cswThreadMetaSameSession(value,next))cswThreadMetaInvalidate();
      value=next;
      // Fresh projection objects for the same login do not end its drafts.
      if(['user','tenant','isStaff','impersonating'].includes(key)&&State._csDraftVerified&&!cswDraftIdentity())cswDraftEnd();
      if(key==='view'&&next!=='staffos')eonWorkroomChatEntry=null;
      if(key==='view')eonWorkroomDestroy();else if(key!=='jwt')eonWorkroomReset();
    }
    value=next;
  }});
}
function eonWorkroomAvailable() { return new URLSearchParams(location.search).get('workroom') !== 'classic' && new URLSearchParams(location.search).get('design') !== 'classic' && !State.impersonating; }
function eonWorkroomEnabled() { return !eonWorkroomOperations && eonWorkroomAvailable(); }
function eonWorkroomReturn() {
  if(!eonWorkroomOperations || !eonWorkroomAvailable())return '';
  return `<nav class="wr-return-bar" aria-label="Workroom navigation"><button type="button" data-workroom-return><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m10 6-6 6 6 6M4 12h16"/></svg><span>Return to Workroom</span></button><span class="wr-return-context">Operations</span></nav>`;
}
function eonWorkroomShell(seat) {
  return `<section class="wr-host" data-seat="${seat}"><nav class="wr-host-toolbar" aria-label="Workspace access"><button id="wr-home">Home</button><strong>${seat==='people'?'People OS':'Staff OS'}</strong><button id="wr-operations">Open operations</button></nav><div id="eon-workroom-root"></div></section>`;
}
function eonWorkroomSession(token = State.jwt) {
  // PIN /me supplies slug/name, not a tenant UUID; web bootstrap has no
  // State.user. Bind the credential's stable identity, then require the
  // server projection to agree. Decoding here never grants server authority.
  try {
    const part = String(token || '').split('.')[1] || '';
    const claims = JSON.parse(atob(part.replace(/-/g,'+').replace(/_/g,'/').padEnd(Math.ceil(part.length/4)*4,'=')));
    const pin = State.isStaff === true, actorId = claims.sub;
    const localTenant = State.tenant?.id || State.tenant?.tenant_id;
    const tenantId = pin ? claims.tid : localTenant;
    const localActor = State.user?.user_id || State.user?.id;
    const uuid = value => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
    const audience = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
    if (!uuid(tenantId) || !uuid(actorId) || !State.tenant?.slug
        || !audience.includes(pin ? 'eon-staff' : 'authenticated')
        || (localTenant && localTenant !== tenantId) || (localActor && localActor !== actorId)) return null;
    return {tenantId,tenantSlug:State.tenant.slug,tenantName:typeof State.tenant.name==='string'?State.tenant.name:'',actorId,token,
      authSession:JSON.stringify([pin,claims.session_id || null,pin ? claims.epoch : null,pin ? claims.auth_time : null]),
      impersonated:State.impersonating===true || claims.imp===true,view:State.view,access:JSON.stringify(State.member)};
  } catch (_) { return null; }
}
function eonWorkroomBind(seat) {
  document.getElementById('wr-home').onclick=()=>{State.view=roleHome();render();};
  document.getElementById('wr-operations').onclick=()=>{eonWorkroomOperations=true;render();};
  const root=document.getElementById('eon-workroom-root');
  eonWorkroomVisit=createWorkroomHost({root,seat,readSession:eonWorkroomSession,
    onAction:async action=>{
      if(action.type==='relay-wire-read'){if(!memberCan('staffos:self:view')||!/^[0-9a-f-]{36}$/.test(action.threadId||'')||!/^[0-9a-f-]{36}$/.test(action.staffId||''))return {ok:false};return api('/api/staffos/relay/wire?thread_id='+encodeURIComponent(action.threadId)+'&staff_id='+encodeURIComponent(action.staffId));}
      if(action.type==='decision-desks-mount'){if(!posIsLead())return {ok:false};_posBindOverview();return {ok:true};}
      if(action.type==='task-open'){await openTaskDetail(action.id);return {ok:true};}
      if(action.type==='academy-open'){location.href='/academy';return {ok:true};}
      if(action.type==='finance-request-open'){if(!prcCanRequest())return {ok:false};prcOpenRequestSheet();return {ok:true};}
      if(action.type==='coaching-open'){
        if(!action.staffId)return {ok:false};
        if(!posIsLead()){sosState.tab='score';eonWorkroomOperations=true;State.view='staffos';render();return {ok:true};}
        if(!memberCan('analytics:staff:view'))return {ok:false};
        await _posOpenCoachingPack(action.staffId,'This person');return {ok:true};
      }
      if(action.type==='finance-record-open'){if(!memberCan('accounting:view'))return {ok:false};await prcOpenExistingRequest(action.id);return {ok:true};}
      if(action.type==='workspace-open'){
        const tab=action.tab;
        if(tab==='chat'){
          const session=eonWorkroomSession();
          if(action.seat!=='staff' || !memberCan('staffos:self:view') || !session || session.impersonated
              || !eonWorkroomEnabled() || !['staffos','people'].includes(State.view)
              || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(action.staffId||'')
              || (State.isStaff && action.staffId!==sosMyStaffId()))return {ok:false};
          // The Workroom's scoped person, never a saved owner preview. This
          // carries navigation intent only; the existing server owns access.
          const baseRole=String(State.member?.base_role||State.member?.role||'').toLowerCase();
          // The owner placeholder is intentionally excluded from the staff
          // role roster. Carry an explicit owner desk identity instead of
          // falling through to an arbitrary manager row.
          const mode=!State.isStaff && baseRole==='owner'?'owner':'staff';
          eonWorkroomChatEntry={staffId:action.staffId,mode,session:JSON.stringify([session.tenantId,session.actorId,session.authSession,session.access])};
          eonWorkroomOperations=true;State.view='staffos';render();return {ok:true};
        }
        if(tab==='tasks'||tab==='evidence'){State.view='tasks';render();return {ok:true};}
        if(!['sales','finance','rewards','attendance'].includes(tab))return {ok:false};
        if(action.seat==='people'){
          if(!posIsLead() || (tab==='rewards'&&!memberCan('hr:payroll:view')))return {ok:false};
          _posState.tab=({sales:'reps',finance:'finance',rewards:'pay',attendance:'floor'})[tab];
          eonWorkroomOperations=true;State.view='people';render();return {ok:true};
        }
        sosState.tab=({sales:'sales',finance:'finance',rewards:'score',attendance:'day'})[tab];
        eonWorkroomOperations=true;State.view='staffos';render();return {ok:true};
      }
      if(action.type==='evidence-upload-open'){State.view='tasks';render();return {ok:true};}
      return {ok:false};
    }});
  return eonWorkroomVisit.start();
}

// Common Ground is the default People/Staff presentation; Classic remains URL-local.
const cg = createCommonGround({
  state: () => State, staff: () => sosState, people: () => _posState,
  escape: esc, api: (path, opts) => api(path, opts), render: () => render(),
  peopleTab: tab => posSetTab(tab), staffTab: tab => sosSwitchTab(tab),
  goTasks: () => { State.view = 'tasks'; render(); },
  requestFinance: () => prcOpenRequestSheet(), canRequestFinance: () => prcCanRequest(),
  isPeopleLead: () => posIsLead(), canReviewFinance: () => prcCanApprove(),
  taskDetail: id => posOpenDetail(id),
  openRotaDay: (id, date, name) => _posOpenRotaFromDay(id, date, name),
  openCoachingPack: (id, name) => _posOpenCoachingPack(id, name),
  openFinanceRecord: id => prcOpenExistingRequest(id),
  recordPayment: id => prcOpenMarkPaid(id), uploadReceipt: id => prcOpenReceiptUpload(id),
  financeAction: ev => prcOnStripClick(ev),
  mapTill: id => _posOpenRepMap(id), refreshPeople: () => _posLoad(),
  legacyOverview: () => _posOwnerOverview(), bindOverview: () => _posBindOverview(),
});

const chatConversation = createChatConversation({
  state: () => State,
  busy: () => _sendInFlight || State.chat.some(m => m.role === 'pending' || m.role === 'eon-streaming'),
  replaceValue: input => _composerValueReplaced(input),
  notify: message => showToast(message),
  // Live voice in this thread: the engine's Chat door, read when used (it
  // exists once Ask's live layer has mounted), with Chat's own sign-in.
  get voice() { return window.EON_ASK_LIVE?.chat || null; },
  identity: () => ({token: State.jwt || '', tenant: State.tenant?.slug || ''}),
  sources: refs => sourceReferences(refs),
  // A decision the call needs is the same card, and the same ping, a typed answer gives.
  permissionCard: msg => {
    const intent = permCardIntent(msg);
    const card = {role: 'permission-card', action_id: msg.action_id,
      title: msg.title || (intent === 'notify' ? 'For your information' : intent === 'question' ? 'EON needs an answer' : 'Approval required'),
      payload: msg.payload, action_type: msg.action_type, ladder_decision: msg.ladder_decision,
      intent, options: permCardOptions(msg), allow_free_text: !!msg.allow_free_text, decided: null};
    if (intent === 'notify') playChime('done');
    else playChime('attention');
    return card;
  },
  renderTurns: () => { if (State.view === 'chat') renderChatBody(); },
  // Words still arriving in a voice turn grow its own bubble, as the typed
  // stream does (appendStreamText): widgets, selections and pictures in the
  // rest of the thread stay as they are. Words the full renderer would draw
  // another way (markdown, a fence, paragraphs) are drawn by it instead.
  growTurn: m => {
    const body = $('#chat-body'), i = State.chat.indexOf(m);
    const bubble = body && i >= 0 ? body.querySelector(`#msg-${i}`) : null;
    const text = bubble && bubble.querySelector(':scope > .text'), was = bubble && bubble.dataset.msgText;
    // Only this turn's own bubble, as last drawn, grows.
    if (!text || typeof was !== 'string' || !m.text.startsWith(was)) return false;
    if (m.role === 'eon' && (/[\n*#`]/.test(m.text) || /^\s*(?:[-•]|\d+[.)])\s/.test(m.text))) return false;
    const stick = (body.scrollHeight - body.scrollTop - body.clientHeight) < 120;
    if (m.role === 'eon') text.innerHTML = _linkifyHtml(esc(m.text));
    else text.textContent = m.text;
    bubble.dataset.msgText = m.text;
    if (stick) requestAnimationFrame(() => { body.scrollTop = body.scrollHeight; });
    requestAnimationFrame(() => body.dispatchEvent(new Event('scroll')));
    return true;
  },
  // A fresh Chat: the existing new-conversation route gives it its own empty
  // thread, adopted only if Chat is still that same fresh, empty conversation
  // when the answer comes. The draft and files are kept.
  prepareConversation: async still => {
    const data = await api('/api/conversations/new', { method: 'POST' });
    if (!data?.ok || !data.conversation_id || !still()) return null;
    // The draft typed so far moves with the conversation it now belongs to.
    const draft = _chatDraftLoad();
    _chatDraftClear();
    // So does a deep-mode choice made on the fresh Chat.
    _deepCarryNew(data.conversation_id);
    State.activeConversationId = data.conversation_id;
    State.activeConversationTitle = 'New chat';
    if (draft) _chatDraftSave(draft);
    return {conversationId: data.conversation_id, binding: data.binding || null};
  },
});

// Presentation and read-only joins for Home, Chat, Ads and Settings.
const lw = createLivingWorkspace({
  state: () => State, api: (path, opts) => api(path, opts),
  can: permission => memberCan(permission), canReviewFinance: () => prcCanApprove(),
  refreshHome: () => fetchAndRenderHomeData(),
  refineState: () => hf.sync(),
  refineHeader: () => hf.mountHeader(),
  refineHome: () => hf.mountHome(),
  openNavigation: trigger => hf.open(trigger),
  paintChart: (chart, quiet = false) => {
    if (State.view !== 'home') return;
    const slot = document.getElementById('chart-slot');
    if (!slot) return;
    slot.innerHTML = renderChartShell(chart);
    if (chart?.available) drawSalesSpendChart(chart, quiet);
  },
  navigate: view => { if (!['home','chat','ads','settings'].includes(view)) return; State.menuOpen=false; State.view=view; render(); },
  compose: text => {
    State.view='chat'; State.chatFullscreen=false; render();
    chatConversation.offerDraft(text);
  },
});

const hf = createHomeRefinement({
  active: () => lw.active(),
  identity: () => {
    // Home keeps its mounted reading across an owner token renewal. Reuse
    // the existing subject/tenant/session binding; unknown sessions stay strict.
    const session = eonWorkroomSession();
    const login = session && JSON.parse(session.authSession)[1];
    if (!session || State.isStaff || session.impersonated || typeof login !== 'string' || !login.trim())
      return readIdentity(State) + '|' + State.view;
    return JSON.stringify([session.actorId,session.tenantId,session.tenantSlug,session.authSession,session.access,State.user,State.view]);
  },
  menuOpen: () => State.menuOpen,
  resetMenu: () => { State.menuOpen = false; },
  openMenu: () => { State.menuOpen = true; State.notifsOpen = false; renderHeader(); },
  closeMenu: () => { State.menuOpen = false; renderHeader(); },
  menuMarkup: () => renderMenuPanel(),
});

// ──────────────────────────────────────────────────────────
// Theme (persists; system-prefers respected on first run)
// 2026-07-08 v2: eon_theme takes THREE values again —
//   'dark' | 'light'  → the Classic skin's two modes
//   'flow'            → the Design Flow skin (bone/ink/#F43B00)
// The first switch (retired same day in #997) failed because only the
// token layer flipped — hard-coded values in inventory/ads/tasks/home
// stayed dark. v2 ships per-surface remap blocks + flow.css view
// sections so the skin actually carries. eon_classic_mode remembers the
// last dark/light pick so Classic → Flow → Classic round-trips.

const THEMES = ['dark', 'light', 'flow', 'house'];

function setTheme(theme) {
  const valid = THEMES.includes(theme) ? theme : 'dark';
  document.documentElement.setAttribute('data-theme', valid);
  // Round 2 (2026-07-08): Design Flow has two modes, like /marketing —
  //   bone (default)  → paper ground, vermilion accent
  //   ink             → dark ground, EON green accent (design rule:
  //                     EON GREEN is THE accent on dark surfaces)
  // data-flow only exists while the flow skin is active.
  if (valid === 'flow') {
    const mode = localStorage.getItem('eon_flow_mode') === 'ink' ? 'ink' : 'bone';
    document.documentElement.setAttribute('data-flow', mode);
  } else {
    document.documentElement.removeAttribute('data-flow');
  }
  localStorage.setItem('eon_theme', valid);
  // house (2026-08-06 WS-A) is a single-mode skin like flow — neither may
  // overwrite the remembered classic dark/light pick.
  if (valid !== 'flow' && valid !== 'house') localStorage.setItem('eon_classic_mode', valid);
}

function setFlowMode(mode) {
  localStorage.setItem('eon_flow_mode', mode === 'ink' ? 'ink' : 'bone');
  setTheme('flow');
}
const savedTheme = localStorage.getItem('eon_theme');
if (savedTheme) {
  setTheme(savedTheme);
} else {
  // HOUSE ROLLOUT (operator, 2026-08-07: "switch to this build for
  // everyone"): a device with NO saved choice now lands in the house
  // skin. An explicit pick — any value already in eon_theme, including
  // classic dark/light — is always respected; Settings and ?skin= are
  // the doors back. APPLY WITHOUT PERSISTING (Opus vet HIGH-3): going
  // through setTheme() wrote 'house' into eon_theme on first load,
  // turning the fleet default into a billion tiny "explicit picks" —
  // a later revert of this default would have restored nobody. The
  // default stays a default until the user actually chooses.
  document.documentElement.setAttribute('data-theme', 'house');
  document.documentElement.removeAttribute('data-flow');
}

// PR CHAT-UI-PHASE1: per-user chat-style toggle. Two values:
//   "comfortable" (default — current bubble UI)
//   "claude-code" (minimal terminal-style skin, CSS-only via
//                  body.chat-style-claude)
// Stored in localStorage so it survives reloads, applies on every
// page load before first render.
function setChatStyle(style) {
  const valid = (style === 'claude-code') ? 'claude-code' : 'comfortable';
  if (valid === 'claude-code') {
    document.body.classList.add('chat-style-claude');
  } else {
    document.body.classList.remove('chat-style-claude');
  }
  localStorage.setItem('eon_chat_style', valid);
  return valid;
}
const savedChatStyle = localStorage.getItem('eon_chat_style');
if (savedChatStyle === 'claude-code') {
  // Apply on body as soon as it exists. document.body is available
  // here because this script tag sits at end of <body>.
  document.body.classList.add('chat-style-claude');
}

// ──────────────────────────────────────────────────────────
// API helper — only auto-logout on truly invalid tokens

// Staff-session death probe (2026-07-31, "EON keeps asking me to sign in"):
// a single 401 is NOT proof the session is dead — prod converts transient
// Supabase connection drops into one-off 401s on otherwise-valid sessions.
// Before ending a staff session, confirm with a raw /me fetch (never api() —
// no recursion). The probe promise is shared for 2s so the CS desk's
// visibility burst (3 concurrent calls) triggers ONE probe, and its verdict
// decides logout for every 401 that arrives while it runs. Network failure /
// timeout / 5xx ⇒ NOT dead (the next real 401 re-probes).
let _staffProbe = null;
function _staffSessionDead() {
  const token = State.jwt;
  if (!_staffProbe || _staffProbe.token !== token) {
    const probe = {token, promise:null};
    _staffProbe = probe;
    probe.promise = (async () => {
      try {
        const ctl = new AbortController();
        const t = setTimeout(() => ctl.abort(), 10000);
        const r = await fetch(API + '/api/staff-login/me', {
          headers: { Authorization: `Bearer ${token}` },
          signal: ctl.signal,
        });
        clearTimeout(t);
        return State.jwt === token && r.status === 401;
      } catch (_e) {
        return false;
      } finally {
        // An old login's timer must not evict a newer login's shared probe.
        setTimeout(() => { if (_staffProbe === probe) _staffProbe = null; }, 2000);
      }
    })();
  }
  return _staffProbe.promise;
}

// Staff-token expiry (ms since epoch), from the JWT payload (base64url).
// 0 = unreadable (treat as "no opinion", never as expired).
function _staffTokenExp() {
  try {
    const seg = String(State.jwt || '').split('.')[1] || '';
    const b64 = seg.replace(/-/g, '+').replace(/_/g, '/')
      .padEnd(Math.ceil(seg.length / 4) * 4, '=');
    return (JSON.parse(atob(b64)).exp || 0) * 1000;
  } catch (_e) { return 0; }
}

// Sliding session for the 12h staff token: when under 2h remain, swap it for
// a fresh one via POST /api/staff-login/refresh (same epoch — the server
// never bumps, so refreshing can't kick anyone). Called on boot, on a 10-min
// tick, and on tab focus; a rep who signed in at 08:00 no longer hard-expires
// at 20:00 mid-chat. Failures are silent — the next tick retries.
async function _staffRefreshIfNeeded() {
  if (!State.isStaff || State.impersonating) return;
  const exp = _staffTokenExp();
  if (!exp || exp <= Date.now()) return; // unreadable or already dead
  if (exp - Date.now() > 2 * 3600 * 1000) return;
  if (_staffRefreshIfNeeded._busy) return;
  _staffRefreshIfNeeded._busy = true;
  try {
    const r = await api('/api/staff-login/refresh', { method: 'POST' });
    if (r && r.ok && r.token) {
      State.jwt = r.token;
      try { localStorage.setItem('eon_staff_token', r.token); } catch (_e) {}
    }
  } catch (_e) { /* transient — next tick retries */ }
  finally { _staffRefreshIfNeeded._busy = false; }
}

async function api(path, opts = {}) {
  if (opts._isCurrent && !opts._isCurrent()) throw new Error('Request context changed');
  const headers = {
    'Content-Type': 'application/json',
    ...(opts.headers || {}),
  };
  if (State.jwt) headers['Authorization'] = `Bearer ${State.jwt}`;
  if (State.tenant) headers['X-Tenant-Slug'] = State.tenant.slug;

  // One manual send keeps its action id through a timeout and a page reload.
  // Storage contains only a digest and random id, never the customer's words.
  // Every JSON rep send carries one (24 Sep vet, fault 6): the server sends
  // an action once and answers a repeat with the first outcome — a typed
  // reply, a photo by link, a look, a product card, the payment link.
  let contactActionKey = opts._contactActionKey || null;
  if ((opts.method || 'GET').toUpperCase() === 'POST'
      && /^\/api\/cs\/(?:threads\/[^/]+\/send(?:-image|-look|-card)?|orders\/[^/]+\/send-invoice)$/.test(path)
      && typeof opts.body === 'string') {
    const body = JSON.parse(opts.body);
    if (!body.contact_action_id) {
      const actor = State.user?.user_id || State.user?.id || State.user?.staff_id || 'desk';
      const input = JSON.stringify([State.tenant?.id || State.tenant?.slug, actor, path, body]);
      const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
      contactActionKey = 'eon.contact.action.' + Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2,'0')).join('');
      let action = sessionStorage.getItem(contactActionKey);
      if (!action) { action = crypto.randomUUID(); sessionStorage.setItem(contactActionKey, action); }
      opts = {...opts, _contactActionKey:contactActionKey, body:JSON.stringify({...body, contact_action_id:action})};
    }
  }

  // 2026-05-31: hard timeout per request. Without this, a hung
  // Supabase query (or any unbounded await on the backend) leaves
  // every consumer awaiting forever — Daniel's symptom was the
  // Money page stuck on the loading skeleton because Promise.all
  // never resolved when one of the four /api/accounting/* calls
  // hung. 30s ceiling matches the worst-case BigQuery + cache-miss
  // path; anything slower is a real failure operators need to see
  // as an error, not as a phantom loading state.
  const timeoutMs = typeof opts.timeoutMs === 'number' ? opts.timeoutMs : 30000;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let res;
  try {
    res = await fetch(API + path, {
      ...opts,
      headers,
      signal: opts.signal || controller.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    if (e && (e.name === 'AbortError' || e.code === 20)) {
      // Vet 2026-09-24 (fault 20): the message used to end with the path,
      // and a CS path carries the chat key — often the customer's phone
      // number — straight into a rep's toast. The path rides on the error
      // for whoever logs it; the words say only what happened.
      throw Object.assign(
        new Error(`No answer from the server after ${Math.round(timeoutMs / 1000)}s.`),
        { status: 408, timeout: true, path },
      );
    }
    throw e;
  }
  clearTimeout(timer);
  if (opts._isCurrent && !opts._isCurrent()) throw new Error('Request context changed');
  if (res.status === 401) {
    const body = await res.json().catch(() => ({}));
    if (opts._isCurrent && !opts._isCurrent()) throw new Error('Request context changed');
    const msg = (body.detail || body.message || '').toString().toLowerCase();
    // Staff PIN session: a CONFIRMED-dead session (token expired, or
    // 'session_superseded' because they logged in on another device — one
    // live session per person) sends them back to the login picker. A 401
    // the probe can't reproduce is treated as transient: the caller's catch
    // handles it (a poll skips a beat) and the session survives.
    if (State.isStaff || msg.includes('session_superseded')) {
      // Owner impersonation expired/failed → the owner's own eon_jwt is still
      // valid, so drop ONLY the impersonation token and return to the owner app
      // (not the staff /login picker, which would strand the owner).
      if (State.impersonating) {
        moneyEndDrafts();
        cswDraftEnd(State.jwt);
        try { localStorage.removeItem('eon_impersonation_token'); } catch (_e) {}
        window.location.href = '/?owner=1';
        throw new Error('unauthorized');
      }
      const dead = await _staffSessionDead();
      if (opts._isCurrent && !opts._isCurrent()) throw new Error('Request context changed');
      if (!dead) {
        throw Object.assign(new Error(msg || 'transient 401'),
                            { status: 401, transient: true, body });
      }
      moneyEndDrafts();
      cswDraftEnd(State.jwt);
      try { localStorage.removeItem('eon_staff_token'); } catch (_e) {}
      forgetStaffIntent();   // the tab's staff mark ends with the staff session
      // Preserve where they were (e.g. /studio, or /?v=money) so login bounces
      // them back instead of dumping them on the dashboard. A CONFIRMED
      // supersede (one live session per person lost to a sign-in elsewhere)
      // is named on the login screen (2026-08-18: the rep should read it as
      // "someone used my PIN", not "EON logged me out again").
      window.location.href = _loginWithNext(msg.includes('session_superseded') ? 'superseded' : '');
      throw new Error('unauthorized');
    }
    if (msg.includes('invalid token') || msg.includes('not authenticated')) {
      // PR5: the access token rotates — try ONE silent refresh + retry
      // before treating this as a real sign-out.
      if (!opts._retried && window.EON_SESSION) {
        const ok = await window.EON_SESSION.refresh();
        // A guarded read may refresh its token, never switch its caller's identity.
        if (opts._isCurrent && !opts._isCurrent()) throw new Error('Request context changed');
        if (ok) {
          State.jwt = localStorage.getItem('eon_jwt');
          return api(path, { ...opts, _retried: true });
        }
      }
      logout();
      throw new Error('unauthorized');
    }
    throw Object.assign(new Error(msg || 'API error'), { status: 401, body });
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // A repeat of a send EON still cannot confirm (Sol 6.1 on #3548): the
    // same id never sends again, however long the wait. Sending the words
    // anew is the rep's own yes, asked once, and it is a NEW action with
    // its own id, so the resend's own retry is protected in turn.
    if (contactActionKey && !opts._resend && cswIsUnsureRepeat(res.status, body)
        && await cswResendAnyway(body.detail.message)) {
      if (opts._isCurrent && !opts._isCurrent()) throw new Error('Request context changed');
      const fresh = crypto.randomUUID();
      if (opts._onContactAction) opts._onContactAction(fresh);
      sessionStorage.setItem(contactActionKey, fresh);
      return api(path, { ...opts, _resend: true,
        body: JSON.stringify({ ...JSON.parse(opts.body), contact_action_id: fresh }) });
    }
    // FastAPI's HTTPException(status, detail=dict) puts the whole dict
    // in body.detail; passing that to Error() stringifies as
    // "[object Object]". Extract a readable error string.
    let msg;
    if (typeof body.detail === 'string') msg = body.detail;
    // Vet 2026-09-24 (fault 20): a 422 list was JSON-dumped at the rep.
    else if (Array.isArray(body.detail)) msg = 'Some of what was sent was not accepted. Check it and try again.';
    else if (body.detail && typeof body.detail === 'object') {
      // MESSAGE BEFORE ERROR. detail.error is a machine code
      // ("unfilled_placeholder", "outside_window"); detail.message is the
      // sentence written for the person reading it. This preferred the
      // code, so every door that surfaces e.message showed the rep a code
      // word and threw the sentence away — the blank-guard's whole point
      // is a rep reading plain words, and at four of its doors they read
      // "unfilled_placeholder" instead. Nothing branches on this string
      // (the one `=== 'unauthorized'` check is thrown from the 401 path
      // above, not here); the code-shaped callers key off e.status.
      msg = body.detail.message || body.detail.error
            || JSON.stringify(body.detail);
    } else msg = body.message || 'API error';
    // The path rides on the error, never in its words (as on a timeout):
    // cswSayWhy names the app of the send's own chat from it.
    throw Object.assign(new Error(msg), { status: res.status, body, path });
  }
  const responseBody = await res.json();
  if (contactActionKey && responseBody?.ok !== false) sessionStorage.removeItem(contactActionKey);
  return responseBody;
}

// ──────────────────────────────────────────────────────────
// Auth — direct to Supabase REST (no SDK)

async function signIn(email, password) {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = await res.json();
    throw new Error(body.error_description || body.msg || 'Sign-in failed');
  }
  const data = await res.json();
  State.jwt = data.access_token;
  localStorage.setItem('eon_jwt', State.jwt);
  forgetStaffIntent();
  // PR5: keep the refresh token — discarding it was why every session
  // died with the ~1h access token. eon_session.js rotates the pair.
  if (data.refresh_token) localStorage.setItem('eon_rt', data.refresh_token);
  await bootstrap();
}

async function signUp(email, password) {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = await res.json();
    throw new Error(body.error_description || body.msg || 'Sign-up failed');
  }
  const data = await res.json();
  if (data.access_token) {
    State.jwt = data.access_token;
    localStorage.setItem('eon_jwt', State.jwt);
    forgetStaffIntent();
    if (data.refresh_token) localStorage.setItem('eon_rt', data.refresh_token);
    await bootstrap();
  } else {
    throw new Error('Account created — check your email to confirm, then sign in.');
  }
}

async function requestPasswordReset(email) {
  // Supabase will send an email with a link of the form:
  //   <APP_URL>/#access_token=...&refresh_token=...&type=recovery
  // We use # (hash) instead of ? so the token never reaches the server logs.
  const redirectTo = `${window.location.origin}/#type=recovery`;
  const res = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON },
    body: JSON.stringify({ email, redirect_to: redirectTo }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error_description || body.msg || 'Could not send reset email');
  }
  // Supabase always returns 200 even if the email doesn't exist (anti-enumeration).
  // So we always show the same success message.
  return true;
}

async function setNewPassword(newPassword, recoveryToken) {
  // Supabase requires us to use the recovery access_token as a Bearer token
  // when calling /auth/v1/user with the new password.
  const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      apikey: SUPABASE_ANON,
      Authorization: `Bearer ${recoveryToken}`,
    },
    body: JSON.stringify({ password: newPassword }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error_description || body.msg || 'Could not update password');
  }
  // Now sign the user in fully. The recovery token IS valid as an access_token
  // for the session, so we can just save it.
  State.jwt = recoveryToken;
  localStorage.setItem('eon_jwt', State.jwt);
  forgetStaffIntent();
  await bootstrap();
}

function logout() {
  State._csBootstrapGeneration = (State._csBootstrapGeneration || 0) + 1;
  cswDraftEnd();
  chatConversation.clear();
  eonWorkroomReset();
  // Staff PIN session (or owner impersonation): end it server-side (bumps the
  // session epoch so the token can't be reused), drop the local token, and
  // return to the staff login screen — NOT the owner Supabase auth view.
  if (State.isStaff) {
    // The page leaves with the session; no Money draft may ask after it.
    moneyEndDrafts();
    if (State.impersonating) {
      // Exiting an impersonation: do NOT call the server logout — that would
      // bump the IMPERSONATED employee's epoch and kick them off their own live
      // session. Just drop the impersonation token and return to the owner app.
      try { localStorage.removeItem('eon_impersonation_token'); } catch (_e) {}
      window.location.href = '/?owner=1';
      return;
    }
    // A genuine staff logout ends the session server-side (bumps the epoch so
    // the token can't be reused), then back to the login picker.
    try { api('/api/staff-login/logout', { method: 'POST' }).catch(() => {}); } catch (_e) {}
    try { localStorage.removeItem('eon_staff_token'); } catch (_e) {}
    forgetStaffIntent();
    window.location.href = staffSignInHref();
    return;
  }
  State.jwt = null;
  State.user = null;
  State.tenant = null;
  State.workspaces = [];
  State.workspaceLink = null;
  State.member = null;
  State.chat = [];
  State.tasks = [];
  State.notifs = [];
  State.menuOpen = false;
  State.notifsOpen = false;
  localStorage.removeItem('eon_jwt');
  localStorage.removeItem('eon_rt');
  localStorage.removeItem('eon_tenant_slug');
  // The one-hour picture pass (eon_session.js) goes with the sign-in: it
  // opens every picture door, customer WhatsApp photos included, so it
  // never outlives a sign-out on a shared shop device.
  try {
    localStorage.removeItem('eon_media_tok');
    if (window.EON_MEDIA && window.EON_MEDIA.forget) window.EON_MEDIA.forget();
  } catch (_e) { /* private mode — nothing was kept */ }
  // Shadow-credential recovery (2026-07-25 vet — same class as the
  // dispatch sign-in loop): a dead owner eon_jwt on a STAFF device boots
  // the owner path, dies here, and used to strand the staffer on the
  // owner email login while their live PIN token sat in storage. The
  // owner keys are cleared above, so reloading /staff boots the staff
  // session cleanly instead.
  //
  // 2026-07-29 — operator: "everytime i click yes it shows i need to
  // login with staff pin or something like that". This branch was the
  // reason. It fires on ANY leftover eon_staff_token, and the owner's own
  // device carries one after any view-as / PIN test. So: owner taps YES on
  // a decision -> his ~1h access token has lapsed -> silent refresh fails
  // -> logout() -> a stale staff token he isn't using sends him to the PIN
  // pad, which he cannot answer. Every time.
  //
  // The discriminator is WHOSE session just died, not what's in storage. A
  // session that actually came up as the owner returns to the OWNER login;
  // the stale staff token goes with it, so it can't hijack the next one.
  try {
    if (localStorage.getItem('eon_staff_token')) {
      if (State.ownerSessionEstablished) {
        localStorage.removeItem('eon_staff_token');
      } else {
        window.location.href = '/staff';
        return;
      }
    }
  } catch (_e) { /* private mode — fall through to the auth view */ }
  State.ownerSessionEstablished = false;
  if (State.ws) { try { State.ws.close(); } catch {} State.ws = null; }
  // Remove any open sheets/panels that live as siblings of #app
  document.querySelectorAll(
    '.menu-panel, .menu-backdrop, .notif-panel, #conn-sheet, #conn-backdrop, #mng-sheet, #mng-backdrop, #sl-overlay'
  ).forEach(el => el.remove());
  State.view = 'auth';
  render();
}

// ──────────────────────────────────────────────────────────
// Staff logins — owner admin (set/reset 4-digit PINs + "view as" any staff)
// and the impersonation banner. Backend: /api/staff-login/* (PR staff-login).

async function openStaffLogins() {
  document.getElementById('sl-overlay')?.remove();
  const wrap = document.createElement('div');
  wrap.id = 'sl-overlay';
  // Its own backdrop, never class menu-backdrop: renderHeader() and render()
  // remove every .menu-backdrop as a stale menu, which took this panel away
  // mid-load on any header re-render (a live notification, the poll).
  wrap.className = 'sl-backdrop';
  wrap.style.cssText = 'position:fixed;inset:0;z-index:1000;display:flex;'
    + 'align-items:flex-start;justify-content:center;overflow:auto;padding:48px 12px;'
    + 'background:rgba(0,0,0,.45);backdrop-filter:blur(2px);-webkit-backdrop-filter:blur(2px);animation:fadeIn .24s ease;';
  wrap.innerHTML = '<div class="cc-menu" role="dialog" aria-modal="true" aria-labelledby="sl-title" style="max-width:560px;width:100%;'
    + 'background:var(--card,#14161a);border-radius:18px;padding:18px;">'
    + '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">'
    + '<div id="sl-title" tabindex="-1" style="font-size:17px;font-weight:650;">Staff logins</div>'
    + '<button id="sl-close" class="icon-btn" aria-label="Close">' + closeIcon() + '</button></div>'
    + '<div style="font-size:12px;opacity:.6;margin-bottom:14px;">4-digit PINs · any device · one live session each. '
    + '“View as” opens the app exactly as that person sees it.</div>'
    + '<div id="sl-body"><div style="opacity:.6;padding:20px;text-align:center;">Loading…</div></div></div>';
  eonBackdropClose(wrap, () => wrap.remove());
  document.body.appendChild(wrap);
  document.getElementById('sl-close')?.addEventListener('click', () => wrap.remove());
  wrap.addEventListener('keydown', (e) => { if (e.key === 'Escape') wrap.remove(); });
  document.getElementById('sl-title')?.focus();
  await renderStaffLoginsBody();
}

function _staffDeviceReleaseMessage(res, name) {
  const counts = ['released', 'attempted', 'unconfirmed'];
  const counted = !!res && counts.every(k => Number.isSafeInteger(res[k]) && res[k] >= 0)
    && res.released + res.unconfirmed === res.attempted;
  // The phone history was not read, so the server attempted no release at all.
  if (counted && res.ok !== true && (res.read_state === 'unavailable' || res.error === 'read_failed') && res.attempted === 0) {
    return 'Phone history for ' + name + ' could not be read, so no phones were released. Close and reopen Staff logins to try again.';
  }
  const valid = counted && res.read_state === 'complete';
  if (!valid) return 'Phone release could not be confirmed for ' + name + '. Check the record before another attempt.';
  if (res.ok === true && res.unconfirmed === 0) {
    return res.released ? 'Released ' + res.released + ' phone' + (res.released === 1 ? '' : 's') + ' for ' + name
      : 'No phones recorded for ' + name + ' in the last 90 days.';
  }
  if (res.ok === false && res.unconfirmed > 0) {
    return 'Confirmed ' + res.released + ' of ' + res.attempted + ' phone release' + (res.attempted === 1 ? '' : 's')
      + ' for ' + name + '; ' + res.unconfirmed + ' unconfirmed. Check the record before another attempt.';
  }
  return 'Phone release is unconfirmed for ' + name + '. Check the record before another attempt.';
}

async function renderStaffLoginsBody() {
  const body = document.getElementById('sl-body');
  if (!body) return;
  let people = [], status = [];
  try {
    // roster is owner-authed now (the pre-auth slug version leaked the org
    // chart publicly; the login page is PIN-first and never lists staff)
    const [r, s] = await Promise.all([
      api('/api/staff-login/roster'),
      api('/api/staff-login/status'),
    ]);
    people = (r && r.staff) || [];
    status = (s && s.rows) || [];
  } catch (e) {
    body.innerHTML = '<div style="padding:18px;color:var(--danger,#e66);">'
      + esc(e.message || 'Could not load staff logins') + '</div>';
    return;
  }
  const byId = {};
  status.forEach((x) => { byId[String(x.staff_id)] = x; });
  if (!people.length) { body.innerHTML = '<div style="opacity:.6;padding:18px;">No active staff.</div>'; return; }
  body.innerHTML = people.map((p) => {
    const st = byId[String(p.staff_id)] || {};
    const enrolled = !!(st.enrolled || p.enrolled);
    const locked = !!st.locked;
    const chip = locked
      ? '<span style="color:#e6a23c;font-size:11px;">locked</span>'
      : (enrolled ? '<span style="color:var(--good,#6bd38a);font-size:11px;">PIN set</span>'
                  : '<span style="opacity:.5;font-size:11px;">no PIN</span>');
    return '<div class="sl-row" data-id="' + esc(p.staff_id) + '" data-name="' + esc(p.name || '') + '" '
      + 'style="display:flex;align-items:center;gap:10px;padding:10px 4px;border-bottom:1px solid rgba(255,255,255,.06);">'
      + '<div style="flex:1;min-width:0;"><div style="font-weight:600;font-size:14px;">' + esc(p.name || p.full_name || 'Staff')
      + '  ' + chip + '</div>'
      + '<div style="opacity:.55;font-size:12px;">' + esc(p.role || '') + (p.department ? ' · ' + esc(p.department) : '') + '</div></div>'
      + '<button class="sl-pin btn-ghost" data-id="' + esc(p.staff_id) + '" data-name="' + esc(p.name || '') + '" '
      + 'style="font-size:12px;padding:6px 10px;border-radius:8px;">' + (enrolled ? 'Change' : 'Set PIN') + '</button>'
      + (enrolled ? '<button class="sl-reset btn-ghost" data-id="' + esc(p.staff_id) + '" style="font-size:12px;padding:6px 10px;border-radius:8px;">Reset</button>' : '')
      + (enrolled ? '<button class="sl-view btn-ghost" data-id="' + esc(p.staff_id) + '" data-name="' + esc(p.name || '') + '" '
        + 'style="font-size:12px;padding:6px 10px;border-radius:8px;">View as</button>' : '')
      + (enrolled ? '<button class="sl-phones btn-ghost" data-id="' + esc(p.staff_id) + '" data-name="' + esc(p.name || '') + '" '
        + 'title="Forget the phones this person has signed in from (one device, one person)" '
        + 'style="font-size:12px;padding:6px 10px;border-radius:8px;">Phones</button>' : '')
      + '</div>';
  }).join('');

  body.querySelectorAll('.sl-pin').forEach((b) => b.addEventListener('click', () => slPinEntry(b)));
  body.querySelectorAll('.sl-reset').forEach((b) => b.addEventListener('click', async () => {
    const id = b.getAttribute('data-id');
    if (!confirm('Clear this PIN and end their session? They will need a new PIN to log in.')) return;
    try { await api('/api/staff-login/reset', { method: 'POST', body: JSON.stringify({ staff_id: id }) });
      showToast('PIN reset'); renderStaffLoginsBody(); }
    catch (e) { showToast(e.message || 'Could not reset'); }
  }));
  // One device, one person (mig 680): a phone binds to whoever signs in
  // on it first and refuses a second person. "Phones" releases every
  // phone this person has used — the path for a new phone / a hand-me-down.
  body.querySelectorAll('.sl-phones').forEach((b) => b.addEventListener('click', async () => {
    if (b.disabled) return;
    const id = b.getAttribute('data-id'); const name = b.getAttribute('data-name');
    if (!confirm('Release ' + name + "'s phones? Their next sign-in binds a fresh phone; the old ones are free for others.")) return;
    b.disabled = true;
    try {
      const res = await api('/api/staff-login/release-devices', { method: 'POST', body: JSON.stringify({ staff_id: id }) });
      showToast(_staffDeviceReleaseMessage(res, name));
    } catch (e) {
      // The route refuses with 503 and its counts in detail; any other failure leaves the outcome unknown.
      const answer = e?.status === 503 && e.body?.detail && typeof e.body.detail === 'object' ? e.body.detail : null;
      showToast(answer ? _staffDeviceReleaseMessage(answer, name)
        : 'Phone release is unconfirmed for ' + name + '. Check the record before another attempt.');
    }
  }));
  body.querySelectorAll('.sl-view').forEach((b) => b.addEventListener('click', async () => {
    const id = b.getAttribute('data-id'); const name = b.getAttribute('data-name');
    try {
      const res = await api('/api/staff-login/impersonate', { method: 'POST', body: JSON.stringify({ staff_id: id }) });
      if (!res || !res.token) { showToast('Could not start view-as'); return; }
      try { localStorage.setItem('eon_impersonation_token', res.token); } catch (_e) {}
      showToast('Viewing as ' + name + '…');
      setTimeout(() => { window.location.href = '/'; }, 250);
    } catch (e) { showToast(e.message || 'Could not view as ' + name); }
  }));
}

// Set or change a PIN in the panel itself (window.prompt is dead): a labelled
// numeric field, refusals shown on the page in plain words. The PIN leaves
// this form only in the POST body: never a toast, log or URL.
function slPinEntry(btn) {
  const row = btn.closest('.sl-row');
  if (!row || row.nextElementSibling?.classList.contains('sl-pinform')) return;
  const id = btn.getAttribute('data-id'), name = btn.getAttribute('data-name') || 'this person';
  const key = 'sl-pin-' + String(id).replace(/[^A-Za-z0-9-]/g, '');
  const form = document.createElement('form');
  form.className = 'sl-pinform';
  form.noValidate = true;
  form.style.cssText = 'display:flex;flex-wrap:wrap;align-items:center;gap:8px;padding:10px 4px 14px;border-bottom:1px solid rgba(255,255,255,.06);';
  form.innerHTML = '<label for="' + key + '" style="flex-basis:100%;font-size:13px;">New 4-digit PIN for ' + esc(name) + '</label>'
    + '<input id="' + key + '" type="password" inputmode="numeric" pattern="[0-9]*" maxlength="4" autocomplete="new-password" aria-describedby="' + key + '-msg" '
    + 'style="width:9ch;min-height:44px;font-size:16px;letter-spacing:.3em;padding:0 12px;border-radius:8px;">'
    + '<button type="submit" class="btn-primary" style="min-height:44px;padding:0 16px;border-radius:8px;">Save PIN</button>'
    + '<button type="button" class="sl-pin-cancel btn-ghost" style="min-height:44px;padding:0 16px;border-radius:8px;">Cancel</button>'
    + '<p id="' + key + '-msg" role="status" aria-live="polite" style="flex-basis:100%;margin:0;font-size:13px;"></p>';
  row.after(form);
  btn.setAttribute('aria-expanded', 'true');
  const input = form.querySelector('input');
  const say = (t) => { form.querySelector('[role="status"]').textContent = t; };
  const close = () => { input.value = ''; form.remove(); btn.setAttribute('aria-expanded', 'false'); btn.focus(); };
  form.querySelector('.sl-pin-cancel').addEventListener('click', close);
  form.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } });
  input.focus();
  let busy = false;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (busy) return;
    const pin = input.value;
    if (!/^\d{4}$/.test(pin)) { say('Type exactly 4 digits.'); input.focus(); return; }
    busy = true; say('Saving…');
    try {
      await api('/api/staff-login/enroll', { method: 'POST', body: JSON.stringify({ staff_id: id, pin }) });
      input.value = '';
      showToast('PIN set for ' + name);
      renderStaffLoginsBody();
    } catch (err) {
      busy = false; input.value = ''; input.focus();
      const code = err && err.body && err.body.detail && err.body.detail.error;
      say(code === 'weak_pin' ? 'That PIN is too easy to guess. Pick another 4 digits.'
        : code === 'pin_taken' ? 'Someone else already uses that PIN. Pick another 4 digits.'
        : code === 'unknown_staff' ? 'This person is no longer on the team list. Close this and open it again.'
        : 'EON could not save this PIN. Try again in a moment.');
    }
  });
}

// The PIN sign-in page for the workspace in view. A bare /login opens the
// default workspace, so the link carries this one's slug when it has one.
function staffSignInHref() {
  let slug = '';
  try {
    slug = (State.tenant && State.tenant.slug) || new URLSearchParams(location.search).get('workspace')
      || localStorage.getItem('eon_tenant_slug') || '';
  } catch (_) {}
  return /^[a-z0-9][a-z0-9-]{0,79}$/.test(slug) ? '/login?slug=' + encodeURIComponent(slug) : '/login';
}

// The tab's staff mark goes when this tab signs out of staff, or an owner
// signs in here: the newer choice in this tab wins.
function forgetStaffIntent() {
  try { sessionStorage.removeItem('eon_staff_intent'); } catch (_e) {}
}

// A staff sign-in in this tab while an owner is signed in on this browser:
// say who this tab is. The owner's session and other tabs are unchanged.
function ensureStaffIntentBanner() {
  const shown = document.getElementById('staff-intent-banner');
  if (!State.staffOverOwner || State.impersonating) {
    if (shown) { shown.remove(); document.body.style.paddingTop = ''; }
    return;
  }
  if (shown) return;
  const bar = document.createElement('div');
  bar.id = 'staff-intent-banner';
  bar.setAttribute('role', 'status');
  bar.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:1100;background:#0b0b0d;color:#f4f4ee;'
    + 'border-bottom:2px solid #d4ff3a;font-size:13px;font-weight:600;line-height:18px;text-align:center;'
    + 'padding:7px 12px;padding-top:max(7px, env(safe-area-inset-top));';
  bar.textContent = 'Signed in as ' + (State.repName || 'staff') + ' (staff) in this tab. The owner is still signed in.';
  document.body.appendChild(bar);
  // The bar wraps on a narrow phone: make room for its real height.
  document.body.style.paddingTop = (bar.offsetHeight || 34) + 'px';
}

function ensureImpersonationBanner() {
  if (!State.impersonating) { document.getElementById('imp-banner')?.remove(); return; }
  if (document.getElementById('imp-banner')) return;
  const who = State.member?.role || State.user?.staff_id || 'staff';
  const bar = document.createElement('div');
  bar.id = 'imp-banner';
  bar.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:1100;'
    + 'background:#7c5cff;color:#fff;font-size:13px;font-weight:600;'
    + 'display:flex;align-items:center;justify-content:center;gap:12px;'
    + 'padding:7px 12px;box-shadow:0 2px 8px rgba(0,0,0,.25);';
  bar.innerHTML = 'Viewing as ' + esc(String(who)) + ' · read-only'
    + ' <button id="imp-exit" style="background:rgba(255,255,255,.2);color:#fff;'
    + 'border:0;border-radius:7px;padding:4px 12px;font-weight:650;cursor:pointer;">Exit view-as</button>';
  document.body.appendChild(bar);
  document.body.style.paddingTop = '34px';
  document.getElementById('imp-exit')?.addEventListener('click', () => {
    moneyEndDrafts();
    cswDraftEnd();
    try { localStorage.removeItem('eon_impersonation_token'); } catch (_e) {}
    window.location.href = '/?owner=1';
  });
}

// ──────────────────────────────────────────────────────────
// Bootstrap

// Post-login redirect preservation. The standalone SPAs (/studio, /inventory,
// /warehouse, /marketing, /reports, /locations) bounce a 401 to
// `/?return=<path>`; a staff token that has since expired then bounces the main
// app to `/login`. Without carrying the intended path through BOTH hops, the
// user always lands on the dashboard (the reported "logged out from studio →
// back to dashboard, URL changed" bug). These helpers thread the path through.
//
// Open-redirect guard: only same-origin absolute paths (single leading '/',
// never '//' or a scheme). Mirrors login.js nextDest().
function _safeNextPath(raw) {
  try {
    const n = String(raw || '');
    if (n && n.charAt(0) === '/' && n.charAt(1) !== '/') return n;
  } catch (_) {}
  return null;
}

// The path a standalone SPA asked us to return to, from ?return= or ?next=.
function _pendingReturnPath() {
  try {
    const qs = new URLSearchParams(location.search);
    return _safeNextPath(qs.get('return')) || _safeNextPath(qs.get('next'));
  } catch (_) { return null; }
}

// Where to send the login picker so it can bounce back here after re-auth:
// the standalone page we were asked to return to, else the current location.
function _loginWithNext(why) {
  const dest = _pendingReturnPath()
    || _safeNextPath(location.pathname + location.search)
    || '/';
  return '/login?next=' + encodeURIComponent(dest)
    + (why ? '&why=' + encodeURIComponent(why) : '');
}

// Voice capability is optional: a slow discovery must not hold the signed-in
// page on its launch screen. Keep late results tied to their boot and identity.
function _voiceTtsAttributes(style = '') {
  const unavailable = !State.voice.ttsAvailable;
  return `data-voice-tts${unavailable ? ' disabled aria-hidden="true"' : ''} style="${style}${unavailable ? 'visibility:hidden;' : ''}"`;
}

function _syncVoiceControls() {
  const mic = $('#composer-mic');
  if (mic) {
    mic.style.display = 'flex';
    mic.style.visibility = State.voice.transcribeAvailable ? '' : 'hidden';
    mic.disabled = !State.voice.transcribeAvailable;
    if (State.voice.transcribeAvailable) {
      mic.removeAttribute('aria-hidden');
      bindMicHandlers(mic);
    } else mic.setAttribute('aria-hidden', 'true');
  }
  document.querySelectorAll('[data-voice-tts]').forEach(control => {
    control.disabled = !State.voice.ttsAvailable;
    control.style.visibility = State.voice.ttsAvailable ? '' : 'hidden';
    if (State.voice.ttsAvailable) control.removeAttribute('aria-hidden');
    else control.setAttribute('aria-hidden', 'true');
  });
}

function _resetVoiceCapabilities() {
  State.voice.discoveryGeneration = (State.voice.discoveryGeneration || 0) + 1;
  State.voice.transcribeAvailable = false;
  State.voice.ttsAvailable = false;
  State.voice.voiceId = null;
  _syncVoiceControls();
  return State.voice.discoveryGeneration;
}

async function _discoverVoiceCapabilities(generation) {
  // The keeper rotates exp/iat without changing the signed-in session.
  // Decode only to bind this optional response; the API grants authority.
  const sessionKey = () => {
    try {
      const part = String(State.jwt || '').split('.')[1] || '';
      const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')
        .padEnd(Math.ceil(part.length / 4) * 4, '=')));
      if (!claims.sub) return State.jwt;
      return JSON.stringify([claims.sub, claims.aud, claims.session_id,
        claims.tid, claims.epoch, claims.auth_time, claims.imp]);
    } catch (_) { return State.jwt; }
  };
  const token = State.jwt;
  const session = sessionKey();
  const tenantId = State.tenant?.id || '';
  const tenantSlug = State.tenant?.slug || '';
  const staff = State.isStaff;
  const current = () => generation === State.voice.discoveryGeneration
    && !!State.jwt && session === sessionKey() && staff === State.isStaff
    && tenantId === (State.tenant?.id || '')
    && tenantSlug === (State.tenant?.slug || '');
  if (!token || !current()) return;
  try {
    const v = await api('/api/voice/status', { _isCurrent: current });
    // api checks after headers; JSON body completion can cross a session change.
    if (!current()) return;
    State.voice.transcribeAvailable = v.transcription_available === true;
    State.voice.ttsAvailable = v.tts_available === true;
    State.voice.voiceId = v.default_voice_id || null;
    _syncVoiceControls();
  } catch (_) { /* Optional discovery leaves unavailable controls disabled. */ }
}

async function bootstrap() {
  const draftBootstrapGeneration = State._csBootstrapGeneration = (State._csBootstrapGeneration || 0) + 1;
  const draftBootstrapToken = State.jwt, draftBootstrapStaff = State.isStaff;
  // Owner refresh can finish before /tenants/me supplies a workspace. Bind
  // the login itself here; decoding is only a stale-response guard, not access.
  const ownerSession = token => {
    try {
      if (draftBootstrapStaff || State.impersonating || typeof token !== 'string' || token.split('.').length !== 3) return null;
      const part = token.split('.')[1];
      const claims = JSON.parse(atob(part.replace(/-/g,'+').replace(/_/g,'/').padEnd(Math.ceil(part.length/4)*4,'=')));
      const audience = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
      if (typeof claims.sub !== 'string' || !claims.sub || typeof claims.session_id !== 'string' || !claims.session_id.trim()
          || !audience.includes('authenticated') || audience.some(v => typeof v !== 'string' || !v)
          || (claims.imp !== undefined && claims.imp !== false)) return null;
      return JSON.stringify([claims.sub,claims.session_id,[...new Set(audience)].sort(),
        claims.tid ?? null,claims.tenant ?? null,claims.epoch ?? null,claims.auth_time ?? null]);
    } catch (_) { return null; }
  };
  const draftBootstrapSession = ownerSession(draftBootstrapToken);
  const draftBootstrapCurrent = () => draftBootstrapGeneration === State._csBootstrapGeneration
    && draftBootstrapStaff === State.isStaff
    && (draftBootstrapToken === State.jwt || (draftBootstrapSession !== null && draftBootstrapSession === ownerSession(State.jwt)));
  const draftBootstrapOptions = {_isCurrent:draftBootstrapCurrent};
  eonWorkroomReset();
  cg.resetAccess(); // A fresh identity/workspace bootstrap must reauthorize the preview.
  const voiceGeneration = _resetVoiceCapabilities();
  // v3.0p18 9v: expose State + api on window for console debugging.
  // Top-level const/let are script-scoped, not on window — without this
  // you can't reach them from devtools. No prod risk: any user-agent
  // could already read these by reading the script source.
  window.State = State;
  window.api = api;

  // ── Invite-accept flow ──
  // v3.0p13 commit 8a: when user lands on /invite-accept?token=X:
  //   - If logged in: store token, accept it post-bootstrap (existing user joins second workspace)
  //   - If not logged in: store token + route to invite_signup view (new user creates account)
  const url = new URL(window.location.href);
  let landedOnInvite = false;
  if (url.pathname === '/invite-accept') {
    landedOnInvite = true;
    const token = url.searchParams.get('token');
    if (token) {
      sessionStorage.setItem('eon_pending_invite_token', token);
      State.inviteToken = token;
    }
    // Clean URL so reloads don't re-trigger
    window.history.replaceState({}, '', '/');
  }

  // ── Staff PIN-session bootstrap ──
  // A staff (or owner-impersonation) token resolves its identity + tenant via
  // /api/staff-login/me — staff have no tenant_members row, so the owner
  // membership path below would dead-end. Mirrors the owner tail (WS, voice,
  // deep-link view) minus the membership fetch.
  if (State.isStaff) {
    // Boot /me is retried, and the token is wiped ONLY when api() confirmed
    // the session dead (probe 401 → it redirects itself). Every other failure
    // — network drop, deploy-window 502, timeout — used to be treated as a
    // sign-out, so reopening the PWA on flaky data was a logout roulette
    // (CS reps re-logged-in 27x/day, 2026-07-31). Now the token survives and
    // the rep gets a Retry state instead of the PIN screen.
    let me = null;
    for (let attempt = 0; me === null; attempt++) {
      try {
        me = await api('/api/staff-login/me', draftBootstrapOptions);
        if (!draftBootstrapCurrent()) return;
      } catch (e) {
        if (!draftBootstrapCurrent()) return;
        if (e && e.message === 'unauthorized') return; // api() is redirecting
        if (e && e.status === 403) {
          // Definitive verdict, not a connection problem: rbac says this
          // staffer is gone/inactive (their PIN row can outlive termination).
          // End the session properly — a shared store device must land on
          // the PIN screen for the next rep, never on a Retry card.
          cswDraftEnd(draftBootstrapToken);
          try { localStorage.removeItem('eon_staff_token'); } catch (_e) {}
          forgetStaffIntent();
          window.location.href = _loginWithNext();
          return;
        }
        if (attempt >= 2) {
          const splash = document.querySelector('.splash');
          if (splash) {
            // Resurrect the splash: its boot animation ends at opacity:0 +
            // visibility:hidden + pointer-events:none — without these
            // overrides the Retry card would be invisible and unclickable.
            splash.style.animation = 'none';
            splash.style.opacity = '1';
            splash.style.visibility = 'visible';
            splash.style.pointerEvents = 'auto';
            splash.innerHTML =
              '<div style="text-align:center;padding:24px">' +
              '<div style="font-weight:600;margin-bottom:8px">Can’t reach EON</div>' +
              '<div style="opacity:.7;margin-bottom:16px">Check your connection — your session is safe.</div>' +
              '<button onclick="location.reload()" style="padding:10px 22px;border-radius:8px;border:0;cursor:pointer">Retry</button>' +
              '</div>';
          }
          return;
        }
        await new Promise((r) => setTimeout(r, (attempt + 1) * 2000));
      }
    }
    State.tenant = me.tenant || null;
    State.member = me.member || { role: 'member' };
    // Canonical rep identity (staff.email else staff_id) — the key thread
    // claims are stored under, so the CS "Mine" tab can recognise this
    // rep's own claims (member.email is never populated for PIN sessions).
    State.repKey = me.rep_key || '';
    State.repName = me.rep_name || '';
    State.user = { user_id: me.staff_id, staff_id: me.staff_id, is_staff: true };
    State.impersonating = !!me.impersonated;
    cswDraftVerify(draftBootstrapToken);
    await prcLoadRequestAccess();
    if (!draftBootstrapCurrent()) return;
    if (State.tenant && State.tenant.slug && !State.staffOverOwner) {
      // Not over an owner session: there this key is the owner's, and other tabs follow it.
      try { localStorage.setItem('eon_tenant_slug', State.tenant.slug); } catch (_e) {}
    }
    if (State.tenant && State.tenant.slug) {
      // This tab's own workspace, for the pages it opens once an owner is also
      // signed in on this browser (eon_session.js reads it there).
      try { sessionStorage.setItem('eon_staff_tab_slug', State.tenant.slug); } catch (_e) {}
    }
    // Keep long shifts alive: refresh the 12h token when it nears expiry —
    // on boot, every 10 minutes, and on tab focus (the moments a rep is
    // actually using the app; an abandoned tab is allowed to expire).
    _staffRefreshIfNeeded();
    setInterval(_staffRefreshIfNeeded, 10 * 60 * 1000);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) _staffRefreshIfNeeded();
    });
    // Auth OK. If a standalone SPA sent us here to return to it (e.g. the studio
    // token 401'd, bounced to /?return=/studio, and our token is still/again
    // valid), go straight back — don't strand the user on the dashboard.
    {
      const back = _pendingReturnPath();
      if (back && back !== (location.pathname + location.search) && back !== '/') {
        window.location.href = back;
        return;
      }
    }
    connectWS();
    // Staff sessions NEVER land on the owner home view (operator 2026-07-20:
    // "staffs cant be landing eon main page"). Back button, direct opens and
    // the home-screen icon (manifest start_url '/') all boot through here, so
    // the default — and any explicit ?v=home — is Staff OS for real staff.
    // Owner impersonation (view-as) keeps 'home' so the owner can peek any
    // surface.
    State.view = State.impersonating ? 'home' : 'staffos';
    try {
      if (url.pathname === '/staff') State.view = 'staffos';
      const qs = new URLSearchParams(location.search);
      const dv = qs.get('v');
      // 2026-07-30: 'settings' added — it was in _NAV_VIEWS (history/back
      // button) but not boot-allowlisted, so a /?v=settings link opened in
      // a fresh tab fell through to the default view. The Snapchat re-auth
      // alert (integration_health.REAUTH) deep-links there: Snap's
      // credential paste form is the Settings → Integrations connect
      // sheet, the only Snap reconnect control in the product.
      const ALLOWED = ['home', 'people', 'hr', 'me', 'cs', 'money', 'tasks', 'ads',
                       'inventory', 'recommendations', 'mydash', 'chat', 'staffos',
                       'system-audit', 'settings'];
      if (dv && ALLOWED.includes(dv)) {
        State.view = (dv === 'home' && !State.impersonating) ? 'staffos' : dv;
      }
      // Sub-tab deep-link — the staff/PWA boot path used to drop the tab
      // param the other boot path honoured, so /?v=people&tab=goals landed
      // a staffer on Overview (wave-2 truth).
      const dtab = qs.get('tab');
      if (dv === 'money') moneyReadEntry(qs);
      if (dv === 'people' && dtab && (['overview', 'tasks', 'reps', 'team', 'floor', 'pay', 'guide', 'goals'].includes(dtab) || (!cg.classic() && ['finance', 'activity'].includes(dtab)))) {
        _posState.tab = dtab;
      }
      // A11: the Buyers tile's /?v=people&tab=reps&about=unnamed_sales&days=N
      // [&store=<location id>] opens the reps tab on that problem
      // (_posAboutPanel). A lead lands on it, as the Workroom's sales door does.
      if (dv === 'people' && qs.get('about') === 'unnamed_sales') {
        const dd = qs.get('days') || '', dst = qs.get('store');
        _posState.about = 'unnamed_sales';
        _posState.aboutDays = /^\d{1,2}$/.test(dd) && +dd >= 1 && +dd <= 90 ? +dd : 30;
        _posState.aboutStore = dst ? dst.slice(0, 64) : null;
        // Its own guard: a failed role read never costs the deep links below.
        try { if (_posState.tab === 'reps' && posIsLead() && !posScopeLimited()) eonWorkroomOperations = true; } catch (_) {}
      }
      // W1-04d: /?v=people&ask=<id> (Today's "Answer in approvals") opens
      // that ask in Waiting on you (_posLoadDecisions).
      const dask = qs.get('ask');
      if (dv === 'people' && dask && /^[A-Za-z0-9-]+$/.test(dask)) _posState.ask = dask;
      // Thread deep-link (2026-08-11) — /?v=cs&thread=<platform_sender_id>
      // lands the operator ON the conversation, not just the desk. Carried
      // on BOTH boot paths so the PWA/staff shell honours it too.
      const dthread = qs.get('thread');
      if (dv === 'cs' && dthread) State.csPendingThread = dthread;
      // R5b7: /?v=cs&from=<page>&about=<what> says why the person came
      // (Marketing's buyers count; cswFromPaint). Anything else is ignored.
      const dfrom = qs.get('from'), dabout = qs.get('about'), dkey = /^[A-Za-z0-9_-]{1,64}$/;
      if (dv === 'cs' && dabout && dkey.test(dabout)) { State.csAbout = dabout; State.csFrom = dfrom && dkey.test(dfrom) ? dfrom : null; }
      // R5c A11c: /?v=cs&from=ads_desk&ad=<Meta ad id> opens the chats that ad
      // brought (cswFromCohort), staff boot. Digits only; no person rides it.
      const dad = qs.get('ad');
      if (dv === 'cs' && dfrom === 'ads_desk' && dad && /^\d{1,32}$/.test(dad)) { State.csFrom = dfrom; State.csAbout = 'ad'; State.csAd = dad; }
    } catch (_) {}
    render();
    void _discoverVoiceCapabilities(voiceGeneration);
    ensureImpersonationBanner();
    ensureStaffIntentBanner();
    installViewportKeyboardListener();
    return;
  }

  if (!State.jwt) {
    // No session. If there's a pending invite, show the signup-via-invite form
    // (NOT the public signup which is now disabled). Otherwise show signin.
    const pendingToken = State.inviteToken || sessionStorage.getItem('eon_pending_invite_token');
    if (pendingToken && landedOnInvite) {
      State.inviteToken = pendingToken;
      State.authMode = 'invite_signup';
      // 2026-05-31: pre-fetch invite metadata so the signup form can
      // pre-fill the email (read-only) and show the workspace name.
      // Previously the recipient had to type their email AND any
      // typo caused a "this invite was issued to X" rejection.
      try {
        const meta = await fetch(
          `/api/tenants/_/invites/preview?token=${encodeURIComponent(pendingToken)}`
        ).then(r => r.ok ? r.json() : null);
        if (meta && meta.ok) {
          State.inviteEmailHint = meta.email || '';
          State.inviteTenantName = meta.tenant_name || '';
          State.inviteRole = meta.role || '';
        } else if (meta) {
          // Surface the specific reason (expired / revoked / not found)
          // before the user fills in a password they can never use.
          State.inviteError = (meta && meta.detail) || 'Invite is no longer valid.';
        }
      } catch (_) { /* non-fatal — form just won't pre-fill */ }
    }
    State.view = 'auth';
    render();
    return;
  }
  
  // ── Pending invite acceptance (post-auth) ──
  // If the user just signed in OR we landed with a stored token AND already
  // have an existing account, accept the invite to join the new workspace.
  const pendingToken = sessionStorage.getItem('eon_pending_invite_token');
  if (pendingToken) {
    sessionStorage.removeItem('eon_pending_invite_token');
    State.inviteToken = null;
    try {
      const result = await api('/api/tenants/_/invites/accept', {
        method: 'POST',
        body: JSON.stringify({ token: pendingToken }),
      });
      console.log('Invite accepted:', result);
    } catch (e) {
      console.error('Invite accept failed:', e);
      setTimeout(() => {
        alert(`Invite link issue: ${e.message || 'invalid or expired'}.\n\nIf the workspace owner generated a new link, ask them to send the latest one.`);
      }, 500);
    }
  }
  
  try {
    const mine = await api('/api/tenants/me', draftBootstrapOptions);
    if (!draftBootstrapCurrent()) return;
    const memberships = mine.memberships || [];
    // The server says whether this person may open a workspace (2026-09-28:
    // only existing owners, unless the switch is on). The no-shop screen
    // shows the form only then; otherwise it says who opens workspaces.
    State.canCreateWorkspace = mine.can_create_workspace === true;
    if (memberships.length === 0) {
      State.view = 'create_workspace';
      render();
      return;
    }
    // Which workspace opens (2026-10-23). One membership opens as before.
    // With more, CRUX opens when it is one of theirs, else the first; a
    // ?workspace=<slug> link wins only when it names a membership this answer
    // just listed. The server's X-Tenant-Slug membership check stays the guard.
    const listed = memberships.length > 1 ? memberships.filter(m => m && m.tenants && m.tenants.slug) : [];
    const fallback = listed.find(m => m.tenants.id === '249a40c3-b4fd-42f8-9139-38a05cc6deb3') || memberships[0];
    const named = new URLSearchParams(location.search).get('workspace');
    const chosen = (named && listed.find(m => m.tenants.slug === named)) || fallback;
    State.tenant = chosen.tenants;
    // Settings offers the others; a link that opened one stays in the address.
    State.workspaces = listed.map(m => m.tenants);
    State.workspaceLink = chosen !== fallback ? State.tenant.slug : null;
    // Persist the tenant slug so sibling SPAs (e.g. /marketing) can
    // resume the same tenant context without re-fetching memberships.
    // marketing.js reads `eon_tenant_slug` and bounces to `/` if it's
    // missing — without this write the "Open Marketing dashboard"
    // button just looped the user back to home, looking like a restart.
    if (State.tenant?.slug) {
      try { localStorage.setItem('eon_tenant_slug', State.tenant.slug); }
      catch (_e) { /* private-mode storage block — non-fatal */ }
    }
    try {
      const member = await api(`/api/tenants/${State.tenant.slug}/me`, draftBootstrapOptions);
      if (!draftBootstrapCurrent()) return;
      State.member = member;
    } catch (e) {
      if (!draftBootstrapCurrent()) return;
      // Member context fetch failed — fall back to gentle defaults
      State.member = { role: chosen.role || 'member' };
    }
    await prcLoadRequestAccess();
    if (!draftBootstrapCurrent()) return;
    // The owner's session really came up on this device. logout() reads
    // this to decide WHOSE login to return to — see the staff-token branch
    // there. Without it a stale eon_staff_token hijacks the owner's
    // re-login and sends him to the PIN pad.
    State.ownerSessionEstablished = true;
    cswDraftVerify(State.jwt);
    connectWS();
    // Deep-link: /?v=<view> opens that view directly (shareable links to
    // hr / me / cs / money / tasks / etc.).
    // 2026-09-05 operator: make Common Ground the main page (owners entered
    // People OS). 2026-09-06 operator, after one night of it: "when I login it
    // doesn't go to the home page, it goes to people" — the owner lands on
    // Home again; /common-ground and the nav still open People OS; the
    // Classic fallback and other desk roles' CS landing are unchanged.
    State.view = cg.defaultLanding();
    try {
      // Staff OS has its own page route: /staff serves index.html and the
      // SPA boots straight into the staffos view (role-switcher OS).
      if (url.pathname === '/staff') State.view = 'staffos';
      const qs = new URLSearchParams(location.search);
      const dv = qs.get('v');
      // 2026-07-30: 'settings' added on both boot paths — see the twin
      // comment in the staff/PWA boot path above. The Snap re-auth alert
      // deep-links to /?v=settings (Integrations connect sheet).
      const ALLOWED = ['home', 'people', 'hr', 'me', 'cs', 'money', 'tasks', 'ads',
                       'inventory', 'recommendations', 'mydash', 'chat', 'staffos',
                       'system-audit', 'settings'];
      if (dv && ALLOWED.includes(dv)) State.view = dv;
      // Sub-tab deep-link: /?v=people&tab=reps lands directly on a People OS
      // tab (used by the marketing rep-board links after the Reps move).
      const dtab = qs.get('tab');
      if (dv === 'money') moneyReadEntry(qs);
      if (dv === 'people' && dtab && (['overview', 'tasks', 'reps', 'team', 'floor', 'pay', 'guide', 'goals'].includes(dtab) || (!cg.classic() && ['finance', 'activity'].includes(dtab)))) {
        _posState.tab = dtab;
      }
      // A11: the Buyers tile's /?v=people&tab=reps&about=unnamed_sales&days=N
      // [&store=<location id>] opens the reps tab on that problem
      // (_posAboutPanel). A lead lands on it, as the Workroom's sales door does.
      if (dv === 'people' && qs.get('about') === 'unnamed_sales') {
        const dd = qs.get('days') || '', dst = qs.get('store');
        _posState.about = 'unnamed_sales';
        _posState.aboutDays = /^\d{1,2}$/.test(dd) && +dd >= 1 && +dd <= 90 ? +dd : 30;
        _posState.aboutStore = dst ? dst.slice(0, 64) : null;
        // Its own guard: a failed role read never costs the deep links below.
        try { if (_posState.tab === 'reps' && posIsLead() && !posScopeLimited()) eonWorkroomOperations = true; } catch (_) {}
      }
      // W1-04d: /?v=people&ask=<id> (Today's "Answer in approvals") opens
      // that ask in Waiting on you (_posLoadDecisions).
      const dask = qs.get('ask');
      if (dv === 'people' && dask && /^[A-Za-z0-9-]+$/.test(dask)) _posState.ask = dask;
      // Thread deep-link (2026-08-11) — /?v=cs&thread=<platform_sender_id>,
      // the target of Marketing → Scout's "Open the conversation".
      const dthread = qs.get('thread');
      if (dv === 'cs' && dthread) State.csPendingThread = dthread;
      // R5b7: /?v=cs&from=<page>&about=<what> says why the person came
      // (Marketing's buyers count; cswFromPaint). Anything else is ignored.
      const dfrom = qs.get('from'), dabout = qs.get('about'), dkey = /^[A-Za-z0-9_-]{1,64}$/;
      if (dv === 'cs' && dabout && dkey.test(dabout)) { State.csAbout = dabout; State.csFrom = dfrom && dkey.test(dfrom) ? dfrom : null; }
      // R5c A11c: /?v=cs&from=ads_desk&ad=<Meta ad id> opens the chats that ad
      // brought (cswFromCohort), owner boot. Digits only; no person rides it.
      const dad = qs.get('ad');
      if (dv === 'cs' && dfrom === 'ads_desk' && dad && /^\d{1,32}$/.test(dad)) { State.csFrom = dfrom; State.csAbout = 'ad'; State.csAd = dad; }
    } catch (_) {}
    render();
    void _discoverVoiceCapabilities(voiceGeneration);
    installViewportKeyboardListener();
  } catch (e) {
    if (!draftBootstrapCurrent()) return;
    console.error('Bootstrap failed:', e);
    State.view = 'auth';
    render();
  }
}


// ──────────────────────────────────────────────────────────
// Mobile keyboard avoidance — visualViewport listener
//
// Daniel reported (2026-05-18): "when I click to start texting, the
// keypad blocks me. I have to scroll to text — unlike Claude." Cause:
// `.composer-shell` is `position: relative` at the bottom of a flex
// column; on iOS Safari the layout viewport stays 100vh even when the
// keyboard reduces the visual viewport, so the composer ends up below
// the visible area.
//
// Fix: track `visualViewport.height` and the offset of the visible
// area, write a CSS custom property `--keyboard-inset` (px from the
// bottom of layout viewport to the bottom of visual viewport), and
// have the composer add that as padding-bottom so it lifts above the
// keyboard. Smooth + works on iOS, Android Chrome, mobile Firefox.

function installViewportKeyboardListener() {
  if (typeof window === 'undefined' || !window.visualViewport) return;
  // Called from both bootstrap paths — install the document/window-level
  // recovery listeners once, not per call.
  if (installViewportKeyboardListener._installed) return;
  installViewportKeyboardListener._installed = true;
  const vv = window.visualViewport;
  const root = document.documentElement;
  let raf = 0;
  const apply = () => {
    raf = 0;
    // The keyboard can only be open while an editable element holds focus
    // (iOS dismisses it on blur; Android likewise). iOS standalone PWAs
    // sometimes SWALLOW the visualViewport resize fired on keyboard close —
    // vv.height then stays keyboard-short forever and every --app-height
    // surface (the CS shell) renders with a keyboard-sized dead band under
    // its composer (operator IMG_8752, 2026-07-09). So: when nothing
    // editable is focused, the keyboard is closed BY DEFINITION — clamp to
    // the layout viewport instead of trusting a possibly-stale vv.height.
    const ae = document.activeElement;
    const editing = !!ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable);
    // Distance from the bottom of the layout viewport (window.innerHeight)
    // to the bottom of the visual viewport. When the keyboard is closed
    // this is 0; when open, it's the keyboard height + any extra
    // browser chrome.
    const inset = editing ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0;
    // Writing a root CSS var invalidates layout for the whole SPA — during
    // the keyboard animation iOS fires a burst of resize/scroll events, and
    // redundant writes were the visible "hang" when tapping the composer.
    // Only touch the vars whose value actually changed.
    const wr = (k, v) => {
      apply._last = apply._last || {};
      if (apply._last[k] === v) return;
      apply._last[k] = v;
      root.style.setProperty(k, v);
    };
    wr('--keyboard-inset', `${inset}px`);
    // Usable height while TYPING only. At rest the var is RELEASED so the
    // CSS fallback (100dvh, UA-computed) owns the geometry: iOS standalone
    // can swallow the keyboard-close resize and leave window.innerHeight
    // keyboard-short forever — writing that stale number kept every
    // --app-height shell (home tabs, CS thread) ~110 CSS px off the floor
    // (operator screenshots 2026-08-11). removeProperty, not a '100dvh'
    // write: both consumers already declare the fallback.
    if (editing) {
      wr('--app-height', `${vv.height}px`);
    } else {
      apply._last = apply._last || {};
      if (apply._last['--app-height'] !== '') {
        apply._last['--app-height'] = '';
        root.style.removeProperty('--app-height');
      }
    }
    // CRITICAL (WhatsApp-grade composer): a `position:fixed` shell pinned
    // to top:0 does NOT follow iOS Safari when it scrolls the LAYOUT
    // viewport up to reveal a focused input near the bottom — the visual
    // viewport then has a positive offsetTop and the composer slides BELOW
    // the keyboard / off-screen. Mirror offsetTop onto the shell's top so
    // the whole CS surface stays glued to the visible area and the
    // composer rests directly above the keyboard with no jump.
    wr('--vv-offset-top', `${editing ? Math.round(vv.offsetTop) : 0}px`);
    // Flag keyboard-open state so the composer can tighten its safe-area
    // padding (the home-indicator inset is irrelevant while typing).
    // Engage on EITHER signal: the inset math (layout viewport minus visual
    // viewport) OR an editing session where the visual viewport is clearly
    // keyboard-short. On iOS-standalone builds where window.innerHeight
    // itself shrinks with the keyboard the inset computes ~0 and the old
    // single condition never engaged — the composer kept its full safe-area
    // pad as a dead gap above the keyboard (design vet 2026-07-26, SEV-2).
    // Third signal (2026-08-11): BOTH legs above lean on innerHeight, and
    // when it shrinks with the keyboard the ratio reads ~1 and never
    // engages. screen.height is orientation-stable on iPhone, so a
    // keyboard-short visual viewport is detectable against it regardless
    // of what innerHeight did. Worst wrong-engage (hardware keyboard in
    // landscape) only drops the 34px pad while typing — degraded, never
    // broken.
    const kbWas = apply._kb;
    apply._kb = root.classList.toggle('csw-kb-open',
      inset > 80 || (editing && (vv.height < window.innerHeight * 0.82 ||
        vv.height < ((window.screen && window.screen.height) || 1e9) * 0.82)));
    // The keyboard moves the Chat column (position:fixed while it is up, up
    // to 799 px) and the Classic bar with it; nothing else re-measures when
    // it closes (phones fire no window resize, sending does not re-render).
    // So measure Ask's lift and the Chat row's room again when the state
    // flips - only then, since this runs on every keyboard frame.
    if (kbWas !== undefined && apply._kb !== kbWas) liftAskEonAboveNav();
    // Keyboard-follow (operator 2026-08-07: "the text area shifts or blocks
    // chat"): while the keyboard animates, the message scroller shrinks —
    // if the reader was at (or near) the latest message, keep them pinned
    // there so the thread never appears to slide away under the composer.
    // Runs on the same coalesced frame as the var writes; the near-bottom
    // guard means reading history is never yanked. Applies to whichever
    // chat surface is mounted (CS script or the main chat body).
    if (editing) {
      const sc = document.getElementById('csw-script') || document.getElementById('chat-body');
      if (sc && sc.scrollHeight - sc.scrollTop - sc.clientHeight < 160) {
        sc.scrollTop = sc.scrollHeight;
      }
    }
    // The composer's geometry is WIDTH-derived, and until now nothing
    // re-measured it when the width moved. `_composerApplyTall` decides the
    // class from a width read off the sibling controls, and
    // `_composerAutosize` caches its height keyed on the box's own
    // clientWidth — but BOTH only ever run off an `input` event or a
    // wholesale value replacement. So a draft parked through a rotation, a
    // narrowed desktop window or an iOS split-view resize keeps the height
    // it was measured at. That is not cosmetic: autosize sets
    // `overflowY = needed > cap ? 'auto' : 'hidden'`, so a box that is now
    // too SHORT for its content hides the overflow rather than offering a
    // scrollbar — two lines of the operator's own draft, invisible AND
    // unscrollable until a character is typed. This listener already fires
    // on every signal that can move that width (vv resize, window resize,
    // orientationchange, pageshow), so re-sync from here. The measurement
    // lands one frame AFTER the CSS vars, not in the same one:
    // `_composerSync` schedules its own rAF and apply() is already inside
    // one. ~16ms later, invisible to the operator — but do not write "same
    // frame" here again, it is not what happens.
    //
    // GATED on the geometry actually moving, and that is load-bearing.
    // `heal` re-applies every 150ms for up to 2.5s after ANY focusin or
    // focusout anywhere in the app, and the comment below it promises that
    // quiet ticks cost nothing — true while apply() only wrote deduped CSS
    // vars, false the moment it re-measures, because `_composerApplyTall`
    // does a getComputedStyle plus a write-read-restore of the textarea's
    // height and that forces a synchronous layout. Ungated, that is ~17
    // forced layouts across the exact keyboard animation this listener
    // exists to keep smooth. The two inputs to the geometry are the
    // composer's own width and `window.innerHeight` (autosize's cap is 40%
    // of it), so key on both plus the element itself — a remount at the
    // same width still measures cold. Reading one clientWidth on a quiet
    // tick flushes nothing, since a quiet tick dirtied no layout.
    const composerInput = document.getElementById('composer-input');
    const composerBox = composerInput && composerInput.closest('.composer');
    if (composerBox) {
      const cw = composerBox.clientWidth;
      if (cw && (cw !== apply._cw || window.innerHeight !== apply._ch
                 || composerInput !== apply._cel)) {
        apply._cw = cw;
        apply._ch = window.innerHeight;
        apply._cel = composerInput;
        // Idempotent and frame-coalesced through `_composerFramePending`,
        // so even a real resize burst costs one measurement per frame
        // rather than one per event; the hysteresis band is what stops a
        // resize arriving mid-tall from oscillating.
        _composerSync(composerInput);
      }
    }
  };
  // Coalesce the rapid resize+scroll burst iOS fires during the keyboard
  // animation into one write per frame (smooth, no thrash).
  const update = () => { if (!raf) raf = requestAnimationFrame(apply); };
  vv.addEventListener('resize', update);
  vv.addEventListener('scroll', update);
  // Recovery signals for the swallowed-resize case above: focus leaving an
  // input is the moment the keyboard starts closing — re-apply after the
  // ~250ms iOS dismiss animation (and once more, belt-and-braces). Same for
  // returning to a backgrounded/bfcache-restored PWA and window resizes.
  // Settle-detection (design vet 2026-07-26, SEV-2): the old fixed
  // 300/800ms re-reads missed keyboards whose geometry settles later
  // (QuickType/autofill accessory bars, slow devices) — the shell then
  // froze at a mid-animation height with a dead band between composer and
  // keyboard. Poll every 150ms for up to 2.5s and stop only after three
  // consecutive stable height reads. update() is rAF-coalesced and dedups
  // its CSS-var writes, so quiet ticks cost nothing.
  const heal = () => {
    update();
    if (heal._t) clearInterval(heal._t);
    let stable = 0, last = -1, ticks = 0;
    heal._t = setInterval(() => {
      ticks++;
      const h = Math.round(vv.height);
      if (h === last) stable++; else { stable = 0; last = h; }
      update();
      if (stable >= 3 || ticks >= 17) { clearInterval(heal._t); heal._t = 0; }
    }, 150);
  };
  document.addEventListener('focusout', heal);
  // Symmetric heal on OPEN: iOS standalone PWAs can also swallow the FINAL
  // resize of the keyboard-open animation — --app-height then sticks at a
  // mid-animation value and the composer floats a dead band above the
  // keyboard (operator screenshot 2026-07-20). Re-reading vv.height after
  // the ~250ms animation settles corrects it even when the event never fired.
  document.addEventListener('focusin', heal);
  window.addEventListener('resize', update);
  window.addEventListener('orientationchange', heal);
  window.addEventListener('pageshow', heal);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) heal(); });
  // Initial read.
  apply();
}

// ──────────────────────────────────────────────────────────
// WebSocket

// eon_session.js rotates eon_jwt/eon_rt in localStorage and announces it —
// but the rotation never reached the RUNNING app: State.jwt kept the dead
// access token and the socket below redialed with it every 3s, 403 per try
// (live-measured 2026-07-31 — the "clicking approve does nothing" report;
// REST survived only because JWT verification runs in audit mode, so the
// app looked alive while its realtime channel was down). Pick the fresh
// token up the moment it exists and reconnect.
window.addEventListener('eon:session-refreshed', () => {
  if (State.isStaff) return; // The owner keeper does not own a staff or View-as token.
  let t = null;
  try { t = localStorage.getItem('eon_jwt'); } catch (_e) {}
  if (!t || t === State.jwt) return;
  State.jwt = t;
  State._wsFailStreak = 0;
  State._wsCooldownUntil = 0;
  try { if (State.ws) State.ws.close(); } catch (_e) {}
  connectWS();
});

// One workspace per device (2026-10-23). eon_ask.js and the sibling pages
// read eon_tenant_slug on every send, so when another tab opens a different
// workspace of this person's, this tab reloads into it rather than keep a
// shell naming one workspace while Ask speaks to another. Only a slug this
// boot listed moves it: one-membership owners and staff sessions never do.
function workspaceFollowsOtherTab(e) {
  if (!e || e.key !== 'eon_tenant_slug' || !e.newValue) return;
  if (State.isStaff || !State.tenant || e.newValue === State.tenant.slug) return;
  if (!(State.workspaces || []).some(t => t && t.slug === e.newValue)) return;
  const params = new URLSearchParams(location.search);
  params.set('workspace', e.newValue);
  location.replace(location.pathname + '?' + params.toString());
}
window.addEventListener('storage', workspaceFollowsOtherTab);
// eon_session.js and eon_ask.js move every other page; this shell moves itself.
window.EON_SHELL_FOLLOWS_WORKSPACE = true;

function connectWS() {
  if (!State.jwt || !State.tenant) return;
  // Handshake-storm brake: repeated closes without a single open mean the
  // server is refusing the token at upgrade (403). Cooldown set below.
  if ((State._wsCooldownUntil || 0) > Date.now()) return;
  // Don't hammer /ws with a token past its exp — prod showed 140+ 403s per
  // 4-minute window from expired-token reconnect loops. The REST path owns
  // logout; the socket just stays down and re-checks each minute (a
  // successful refresh swaps State.jwt and the next check connects).
  if (State.isStaff && !State.impersonating) {
    const exp = _staffTokenExp();
    if (exp && exp <= Date.now()) { setTimeout(connectWS, 60000); return; }
  } else if (window.EON_SESSION && typeof window.EON_SESSION.expMs === 'function') {
    // Owner path (Supabase JWT): the same discipline #1743 gave staff. A
    // token at or within 60s of expiry never dials — ask eon_session for a
    // refresh; the 'eon:session-refreshed' listener above reconnects with
    // the new token.
    const exp = window.EON_SESSION.expMs(State.jwt);
    if (exp && exp <= Date.now() + 60000) {
      try { window.EON_SESSION.refresh(); } catch (_e) {}
      setTimeout(connectWS, 60000);
      return;
    }
  }
  // Idempotency: if a socket is already open or connecting, skip. Two
  // sockets would both receive deltas for the same turn and spawn
  // duplicate bubbles (the overlap bug).
  if (State.ws && (State.ws.readyState === WebSocket.OPEN || State.ws.readyState === WebSocket.CONNECTING)) return;
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  const url = `${proto}://${location.host}/ws?token=${encodeURIComponent(State.jwt)}&tenant=${State.tenant.slug}`;
  try {
    const ws = new WebSocket(url);
    let opened = false;
    ws.onopen = () => {
      opened = true;
      State._wsFailStreak = 0;
      State._wsCooldownUntil = 0;
    };
    ws.onmessage = (ev) => {
      try { handleWSMessage(JSON.parse(ev.data)); } catch {}
    };
    ws.onclose = () => {
      State.ws = null;
      if (!opened) {
        // Refused at the handshake — a dead token loops here forever if
        // left alone (3s redial × 403). Three strikes: stop dialing for
        // 60s and force a token refresh; a success reconnects via the
        // session-refreshed listener.
        State._wsFailStreak = (State._wsFailStreak || 0) + 1;
        if (State._wsFailStreak >= 3) {
          State._wsCooldownUntil = Date.now() + 60000;
          if (window.EON_SESSION && typeof window.EON_SESSION.refresh === 'function') {
            try { window.EON_SESSION.refresh(); } catch (_e) {}
          }
        }
      }
      // v3.0p16 9e (Item 5 Gap 2): if a stream was in flight when the
      // socket closed, surface the loss to the user. Don't auto-retry —
      // user decides.
      const active = State.chat.find(m => m.role === 'eon-streaming');
      if (active) {
        active.text = (active.text || '');
        active.text += (active.text ? '\n\n' : '') + '(connection dropped, please retry)';
        active.role = 'eon';
        active.dropped = true;
        stopSmoothReveal();
        if (State.view === 'chat') renderChatBody();
      }
      _stopStreamWatchdog();
      const send = $('#composer-send');
      if (send) send.disabled = false;
      setTimeout(connectWS, 3000);
    };
    State.ws = ws;
  } catch {}
}

// ── Stream watchdog (Item 5) ─────────────────────────────────
// Detects a chat turn where the server stops streaming events
// (or never starts). 60s ceiling, configurable above.

function _startStreamWatchdog() {
  _stopStreamWatchdog();
  State._streamWatchdog = setTimeout(() => {
    const rid = State._streamWatchdogRid || '';
    const ridShort = rid ? rid.slice(0, 8) : 'no-rid';

    // Best-effort backend log so error_log captures the timeout.
    // Pass the chat rid in X-Request-ID — Item 1 middleware honors it
    // so the resulting error_log row carries the same rid as the
    // originating chat turn.
    _postClientEvent('stream_watchdog_timeout', rid, {
      timeout_ms: STREAM_WATCHDOG_MS,
    }).catch(e => console.error('client-event post failed:', e));

    State.chat = State.chat.filter(m =>
      m.role !== 'pending' && m.role !== 'status'
    );
    const active = State.chat.find(m => m.role === 'eon-streaming');
    const errMsg = `Response took too long — please retry. Reference: ${ridShort}`;
    if (active) {
      active.text = (active.text || '');
      active.text += (active.text ? '\n\n' : '') + errMsg;
      active.role = 'eon';
      active.failed = true;
    } else {
      State.chat.push({ role: 'eon', text: errMsg, failed: true });
    }
    stopSmoothReveal();
    if (State.view === 'chat') renderChatBody();

    const input = $('#composer-input');
    const send = $('#composer-send');
    if (input) { input.disabled = false; setTimeout(() => input.focus(), 0); }
    if (send)  { send.disabled  = false; }

    State._streamWatchdog = null;
    State._streamWatchdogRid = null;
    console.error(`stream_watchdog_timeout rid=${rid}`);
  }, STREAM_WATCHDOG_MS);
}

function _stopStreamWatchdog() {
  if (State._streamWatchdog) {
    clearTimeout(State._streamWatchdog);
    State._streamWatchdog = null;
  }
  State._streamWatchdogRid = null;
}

// v3.0p23: Reset the watchdog without clearing the rid. Called on
// every WS event from the server (delta, status, tool_done) so the
// timer measures "time since last activity" rather than "time since
// turn started". Without this, multi-tool queries that actively
// stream for >60s get killed mid-flight even though the backend is
// doing real work — that's the "Response took too long" bug.
function _kickStreamWatchdog() {
  if (!State._streamWatchdog) return;  // Nothing to reset
  clearTimeout(State._streamWatchdog);
  // Re-schedule with the same timeout but preserve the rid so the
  // error message (if it ever does fire) still references the right
  // chat turn.
  const rid = State._streamWatchdogRid;
  _startStreamWatchdog();
  State._streamWatchdogRid = rid;
}

async function _postClientEvent(eventType, chatRid, payload) {
  if (!State.jwt || !State.tenant?.slug) return;
  await fetch((API || '') + '/api/client-events', {
    method: 'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${State.jwt}`,
      'X-Tenant-Slug': State.tenant.slug,
      'X-Request-ID':  chatRid || '',
    },
    body: JSON.stringify({ type: eventType, payload }),
  });
}

function handleWSMessage(msg) {
  // v3.0p23: any backend activity proves the turn is alive. Kick the
  // watchdog forward so it measures inactivity, not total turn time.
  _kickStreamWatchdog();

  if (msg.type === 'request_id') {
    // v3.0p16 9e (Item 5): server-assigned rid for this chat turn.
    // Stash it so the watchdog can include it in the error message
    // and the client-event POST can carry it as X-Request-ID.
    State._streamWatchdogRid = msg.request_id;
    return;
  }
  // WS-C (2026-07-26): stream events from a turn the operator has left
  // are dropped. selectConversation/startNewConversation bump _turnEpoch;
  // without this, the next delta after a mid-stream switch spawned a
  // fresh streaming bubble INSIDE the newly-opened conversation.
  // permission_request is deliberately NOT dropped — an approval must
  // never be lost, whichever thread is on screen.
  if (['delta', 'status', 'tool_done', 'done', 'error', 'widget_preview'].includes(msg.type)
      && _sendEpoch !== _turnEpoch) {
    if (msg.type === 'done' || msg.type === 'error') {
      _sendInFlight = false;
      _stopStreamWatchdog();
    }
    return;
  }

  if (msg.type === 'delta') {
    let active = State.chat.find(m => m.role === 'eon-streaming');
    if (!active) {
      // Late delta after done? Append to the most recent completed eon
      // bubble rather than spawning a duplicate (overlap bug). The
      // `closed` flag is set in the done branch when we flip the role.
      const last = State.chat[State.chat.length - 1];
      if (last && last.role === 'eon' && last.closed) {
        last.text += msg.text;
        if (State.view === 'chat') renderChatBody();
        return;
      }
      // First delta — drop the pending dots, spawn a streaming bubble, full render once
      State.chat = State.chat.filter(m => m.role !== 'pending');
      active = { role: 'eon-streaming', text: '', buffer: '' };
      State.chat.push(active);
      if (State.view === 'chat') renderChatBody();
    }
    // Buffer chunks and reveal smoothly via the typewriter timer
    active.buffer += msg.text;
    startSmoothReveal(active);
  } else if (msg.type === 'status') {
    // v3.0p16 9f (Item 4): tool-trace card — accumulate, don't replace.
    // Each turn gets its own card; entries pile up as tools fire.
    let trace = State.chat.find(m =>
      m.role === 'tool-trace' && !m.collapsed
    );
    let traceIsNew = false;
    if (!trace) {
      // Drop the pending dots when the first tool starts
      State.chat = State.chat.filter(m => m.role !== 'pending');
      trace = { role: 'tool-trace', entries: [], collapsed: false };
      State.chat.push(trace);
      traceIsNew = true;
    }
    trace.entries.push({
      id:    msg.tool_use_id,
      name:  msg.tool || 'tool',
      text:  msg.text || '',
      state: 'running',
    });
    // WS-C: a 6-tool turn used to innerHTML-rebuild the ENTIRE thread
    // twice per tool (status + tool_done), replaying every message's
    // entry animation mid-read. Patch just the live trace node; the
    // full render happens only when the trace first mounts.
    if (State.view === 'chat') {
      if (traceIsNew) renderChatBody();
      else _patchLiveToolTrace(trace);
    }
  } else if (msg.type === 'tool_done') {
    const trace = State.chat.find(m =>
      m.role === 'tool-trace' && !m.collapsed
    );
    if (trace) {
      let entry = msg.tool_use_id
        ? trace.entries.find(e => e.id === msg.tool_use_id)
        : null;
      // Fallback: match the last running entry with this tool name
      // (legacy path, in case a backend doesn't send tool_use_id)
      if (!entry) {
        for (let i = trace.entries.length - 1; i >= 0; i--) {
          if (trace.entries[i].state === 'running'
              && trace.entries[i].name === msg.tool) {
            entry = trace.entries[i];
            break;
          }
        }
      }
      if (entry) {
        entry.state = (msg.ok === false) ? 'error' : 'done';
        if (typeof msg.duration_ms === 'number') {
          entry.durationMs = msg.duration_ms;
        }
      }
      if (State.view === 'chat') _patchLiveToolTrace(trace);
    }
  } else if (msg.type === 'done') {
    _stopStreamWatchdog();
    _sendInFlight = false;   // WS-C: double-send guard releases
    const send = $('#composer-send');
    if (send) send.disabled = false;
    State.chat = State.chat.filter(m =>
      m.role !== 'pending' && m.role !== 'status'
    );
    // v3.0p16 9f (Item 4): collapse the active tool-trace card so the
    // answer becomes the focal point. Mark any still-running entries
    // 'done' defensively (shouldn't happen if backend always emits
    // tool_done, but stops a hung spinner).
    const openTrace = State.chat.find(m =>
      m.role === 'tool-trace' && !m.collapsed
    );
    if (openTrace) {
      for (const e of openTrace.entries) {
        if (e.state === 'running') e.state = 'done';
      }
      openTrace.collapsed = true;
    }
    const active = State.chat.find(m => m.role === 'eon-streaming');
    if (active) {
      // Drain any remaining buffered text instantly when the stream ends
      if (active.buffer) {
        active.text += active.buffer;
        active.buffer = '';
      }
      // If model gave us a full_text that's longer than what we revealed
      // (rare race), use it
      if (msg.full_text && msg.full_text.length > active.text.length) {
        active.text = msg.full_text;
      }
      active.role = 'eon';
      active.source_refs = sourceReferences(msg.source_refs);
      active.closed = true;  // late deltas now append here instead of spawning a 2nd bubble
      // Suggested pin? Backend included it in done payload
      if (msg.pin_suggest) {
        active.pin_suggest = {
          question: msg.pin_suggest.question,
          reason: msg.pin_suggest.reason,
          answer: active.text,
        };
      }
      stopSmoothReveal();
      // Final render with fade-in paragraphs and pin suggestion
      if (State.view === 'chat') renderChatBody();
      // Read aloud if user has TTS enabled
      if (State.voice.ttsEnabled && State.voice.ttsAvailable && active.text) {
        speakText(active.text);
      }
      playChime('done');    // sound notification (2026-07-12)
      _callOnReplyDone();   // call mode: enter 'speaking', re-listen when TTS ends
    } else {
      State.chat.push({ role: 'eon', text: msg.full_text, source_refs: sourceReferences(msg.source_refs) });
      if (State.view === 'chat') renderChatBody();
      if (State.voice.ttsEnabled && State.voice.ttsAvailable && msg.full_text) {
        speakText(msg.full_text);
      }
      playChime('done');
      _callOnReplyDone();
    }
  } else if (msg.type === 'error') {
    _stopStreamWatchdog();
    _sendInFlight = false;   // WS-C: double-send guard releases
    const send = $('#composer-send');
    if (send) send.disabled = false;
    State.chat = State.chat.filter(m =>
      m.role !== 'pending' && m.role !== 'status' && m.role !== 'eon-streaming'
    );
    // v3.0p16 9f (Item 4): collapse trace, mark any running entries as error
    const openTrace = State.chat.find(m =>
      m.role === 'tool-trace' && !m.collapsed
    );
    if (openTrace) {
      for (const e of openTrace.entries) {
        if (e.state === 'running') e.state = 'error';
      }
      openTrace.collapsed = true;
    }
    State.chat.push({ role: 'eon', text: msg.text, failed: true });
    stopSmoothReveal();
    if (State.view === 'chat') renderChatBody();
  } else if (msg.type === 'permission_request') {
    // v3.0p17 9h item 5: inline confirmation card. Drops the pending
    // dots if any, appends a permission-card chat message, leaves the
    // streaming bubble untouched (the agent will continue).
    State.chat = State.chat.filter(m => m.role !== 'pending');
    // 2026-08-04: the card adapts to what the automation needs — a
    // decision, an answer, or nothing but an acknowledgement. Producers
    // that predate this send no intent; permCardIntent defaults them to
    // the Approve/Deny pair they've always rendered.
    const pIntent = permCardIntent(msg);
    const pTitle = msg.title || (pIntent === 'notify' ? 'For your information'
                                 : pIntent === 'question' ? 'EON needs an answer'
                                 : 'Approval required');
    State.chat.push({
      role: 'permission-card',
      action_id:       msg.action_id,
      title:           pTitle,
      payload:         msg.payload,
      action_type:     msg.action_type,
      ladder_decision: msg.ladder_decision,
      intent:          pIntent,
      options:         permCardOptions(msg),
      allow_free_text: !!msg.allow_free_text,
      decided:         null,
    });
    // An FYI gets the soft note, not the "you're blocking something" ping.
    if (pIntent === 'notify') playChime('done');
    else playChime('attention');   // approval/answer needed — the CC "needs input" ping
    if (State.view === 'chat') renderChatBody();
  } else if (msg.type === 'widget_preview') {
    // v3.0p18 9u: chat-built dashboards. Inline chart card with a Pin
    // button that POSTs to /api/dashboard/widgets to persist.
    State.chat = State.chat.filter(m => m.role !== 'pending');
    State.chat.push({
      role: 'widget-card',
      title:       msg.title || 'Widget',
      source:      msg.source,
      params:      msg.params || {},
      widget_type: msg.widget_type,
      data:        msg.data,
      pinned:      false,
    });
    if (State.view === 'chat') renderChatBody();
  } else if (msg.type === 'notification') {
    const p = msg.payload || {};
    p._live = true;                       // de-dupe vs the persisted feed
    if (!p.created_at) p.created_at = new Date().toISOString();
    State.notifs.unshift(p);
    if (p.id) _eonNotifSeen.add(p.id);    // don't replay on the next poll
    eonSoundForNotif(p.kind, p);
    renderHeader();
  }
}

// Notification-panel actions — document-level delegation, mounted once.
// The panel is rebuilt by renderHeader() from MANY paths (WS push, the 1.2s
// auto-mark, dismissAlert's render(), bindHome) — per-render addEventListener
// binds died on every rebuild, leaving deterministically-dead Dismiss buttons.
// Capture before the panel's inline propagation boundary. Its bubbling stop
// keeps inside taps from closing the panel, but must not swallow its actions.
document.addEventListener('click', (e) => {
  if (e.target.closest('#notif-close')) {
    e.stopPropagation(); _closeNotifications(); return;
  }
  const dis = e.target.closest('.alert-dismiss');
  if (dis) { e.stopPropagation(); dismissAlert(dis.dataset.alertId); return; }
  const mark = e.target.closest('#notif-mark-read');
  if (mark) { e.stopPropagation(); markNotifsRead(); return; }
  // A notification that names WHERE it happened is a link (relay R0
  // item 6). The bell has always been a read-only list: it told you a
  // task needed you and then made you go find it yourself. Producers now
  // put payload.url on every task notification, so the row travels.
  const link = e.target.closest('.notif-item[data-notif-url]');
  if (link) { e.stopPropagation(); _notifOpen(link); return; }
  // Full-screen shells may never mount a header's outside-click listener.
  // Keep the clicked control's focus and let its own action proceed.
  if (State.notifsOpen && !e.target.closest('.notif-panel,#notif-btn,#shell-bell'))
    _closeNotifications(false);
}, true);

// Same journey from the keyboard — the row carries role="link" and
// tabindex, so it must answer Enter/Space like one.
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && State.notifsOpen) {
    e.preventDefault(); e.stopPropagation(); _closeNotifications(); return;
  }
  if (e.key !== 'Enter' && e.key !== ' ') return;
  const link = e.target.closest && e.target.closest('.notif-item[data-notif-url]');
  if (!link) return;
  e.preventDefault();
  _notifOpen(link);
});

// The deep link a notification carries, or '' when it has none. Only
// same-origin app paths travel — a payload is data, and data never gets
// to choose the destination host.
function _notifHref(n) {
  const url = ((n && n.payload) || {}).url;
  if (!url || typeof url !== 'string') return '';
  if (!url.startsWith('/') || url.startsWith('//')) return '';
  return url;
}

function _notifOpen(el) {
  const url = el.getAttribute('data-notif-url');
  if (!url) return;
  const id = el.getAttribute('data-notif-id');
  // Reading it IS reading it — mark before we navigate, best-effort.
  if (id) {
    try {
      api(`/api/notifications/${id}/read`, { method: 'POST' }).catch(() => {});
    } catch (_) { /* navigation matters more than the badge */ }
  }
  State.notifsOpen = false;
  document.querySelectorAll('.notif-panel').forEach((p) => p.remove());
  location.assign(url);
}

// v3.0p18 9u: Pin-this-chart button delegation, mounted once at app load.
// Same pattern as permission-card delegation just below.
document.addEventListener('click', async (e) => {
  const pinBtn = e.target.closest('.widget-card button[data-pin]');
  if (!pinBtn) return;
  const card = pinBtn.closest('.widget-card');
  if (!card || card.classList.contains('pinned')) return;
  const idx = parseInt(card.dataset.chatIdx, 10);
  const entry = State.chat[idx];
  if (!entry || entry.role !== 'widget-card') return;
  pinBtn.disabled = true;
  pinBtn.textContent = 'Pinning…';
  try {
    const res = await api('/api/dashboard/widgets', {
      method: 'POST',
      body: JSON.stringify({
        title:       entry.title,
        source:      entry.source,
        widget_type: entry.widget_type,
        params:      entry.params,
      }),
    });
    if (res && res.ok) {
      entry.pinned = true;
      entry.widget_id = res.widget?.id;
      pinBtn.textContent = '✓ Pinned';
      card.classList.add('pinned');
    } else {
      pinBtn.textContent = 'Failed — retry';
      pinBtn.disabled = false;
    }
  } catch (err) {
    pinBtn.textContent = 'Failed';
    pinBtn.disabled = false;
  }
});

// ── Adaptive permission cards (2026-08-04, mig 675) ──────────────────
// Operator: "is it possible for system audit button adapt to the feedback
// the automation need than be stuck on approve — the button label will
// adapt to the question, if is not a question it notifys."
//
// One card component, three shapes. Every surface that renders a
// permission card reads its buttons from these helpers rather than
// hardcoding Approve/Deny, so a question's choices ARE its buttons and
// an FYI never grows an Approve.
const PERM_INTENTS = ['decision', 'question', 'notify'];

function permCardIntent(m) {
  const raw = String((m && m.intent) || '').trim().toLowerCase();
  return PERM_INTENTS.includes(raw) ? raw : 'decision';
}

function permCardOptions(m) {
  const raw = (m && m.options) || [];
  if (!Array.isArray(raw)) return [];
  return raw.map(o => (o && typeof o === 'object')
      ? { value: String(o.value != null ? o.value : o.label || ''),
          label: String(o.label != null ? o.label : o.value || '') }
      : { value: String(o), label: String(o) })
    .filter(o => o.value)
    .slice(0, 6);
}

// The controls for an undecided card, as HTML.
function permCardControlsHtml(m) {
  const intent = permCardIntent(m);
  if (intent === 'notify') {
    return `<div class="permission-buttons permission-buttons--notify">
      <button type="button" class="btn permission-ack" data-decision="ack">Got it</button>
    </div>`;
  }
  if (intent === 'question') {
    const opts = permCardOptions(m);
    const free = m.allow_free_text || !opts.length;
    const chips = opts.map(o =>
      `<button type="button" class="btn permission-answer" data-answer="${esc(o.value)}">${esc(o.label)}</button>`
    ).join('');
    const other = free
      ? `<div class="permission-reply">
           <input type="text" class="permission-reply-input" maxlength="200"
                  placeholder="${opts.length ? 'Something else…' : 'Type your answer'}"
                  aria-label="Your answer">
           <button type="button" class="btn permission-reply-send" data-answer-submit="1">Send</button>
         </div>`
      : '';
    // A question he can no longer answer must have a way out, or it
    // waits forever: it is counted in every queue (only notify is
    // filtered), and until 2026-08-16 no surface offered anything but
    // the answers. His only exit was to pick one anyway — which, once a
    // question card can reach a real executor, runs the mutation with a
    // parameter he knows is wrong.
    //
    // The label says what it DOES. "Hold" / "not now" read as later and
    // this is not later — deny closes the card on every surface at once.
    const drop = `<div class="permission-drop">
      <button type="button" class="btn permission-deny permission-drop__b"
              data-decision="deny">no longer needed — drop it</button>
    </div>`;
    return `<div class="permission-buttons permission-buttons--choices">${chips}</div>${other}${drop}`;
  }
  return `<div class="permission-buttons">
    <button type="button" class="btn permission-approve" data-decision="approve">Approve</button>
    <button type="button" class="btn permission-deny" data-decision="deny">Deny</button>
  </div>`;
}

// The line a decided card settles into, in the operator's own terms.
function permCardFooterText(m) {
  const when = m.decided_at ? new Date(m.decided_at).toLocaleTimeString() : '';
  const intent = permCardIntent(m);
  let head;
  if (intent === 'notify') head = '✓ Dismissed';
  else if (intent === 'question' && m.decided === 'approve')
    head = `✓ Answered: ${m.answer_label || m.answer || ''}`;
  else head = m.decided === 'approve' ? '✓ Approved' : '✕ Denied';
  return when ? `${head} · ${when}` : head;
}

async function permCardDecide(card, body) {
  const action_id = card.dataset.actionId;
  if (!action_id) return;
  card.querySelectorAll('button').forEach(b => b.disabled = true);
  try {
    const resp = await api(`/api/permissions/${encodeURIComponent(action_id)}/decide`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const msg = State.chat.find(m =>
      m.role === 'permission-card' && m.action_id === action_id
    );
    if (msg) {
      msg.decided = (resp && resp.decision) || body.decision
        || (body.answer != null ? 'approve' : null);
      msg.decided_at = new Date().toISOString();
      if (resp && resp.answer != null) {
        msg.answer = resp.answer;
        msg.answer_label = resp.answer_label || resp.answer;
      }
      // Hard-verified completion: server returned ok AND the
      // deferred executor confirmed the underlying mutation
      // landed. Only then does the success checkmark animation
      // qualify — anything weaker is self-attestation.
      msg.verified = !!(resp && resp.ok === true
        && resp.decision === 'approve'
        && resp.executor_result && resp.executor_result.ok === true);
    }
    if (State.view === 'chat') renderChatBody();
    // Render the ✓ animation INSIDE the card footer after the
    // re-render has happened. window.EON_UX guards this — the
    // primitive itself refuses to fire without verified:true.
    if (window.EON_UX && msg && msg.verified) {
      setTimeout(() => {
        const liveCard = document.querySelector(
          `.permission-card[data-action-id="${CSS.escape(action_id)}"] .permission-footer`
        );
        if (liveCard) {
          window.EON_UX.successMark(liveCard, { verified: true, label: 'Logged' });
        }
      }, 60);
    }
  } catch (err) {
    card.querySelectorAll('button').forEach(b => b.disabled = false);
    const detail = err.body?.detail || err.message || 'unknown';
    showToast(`Could not record that: ${detail}`);
  }
}

// v3.0p17 9h item 5: handle card clicks via event delegation so
// re-renders don't invalidate listeners. Mounted once at app load.
document.addEventListener('click', async (e) => {
  const card = e.target.closest('.permission-card');
  if (!card || card.classList.contains('decided')) return;

  const answerBtn = e.target.closest('.permission-card button[data-answer]');
  if (answerBtn) {
    await permCardDecide(card, { answer: answerBtn.dataset.answer });
    return;
  }
  const sendBtn = e.target.closest('.permission-card button[data-answer-submit]');
  if (sendBtn) {
    const input = card.querySelector('.permission-reply-input');
    const typed = (input?.value || '').trim();
    if (!typed) { input?.focus(); return; }
    await permCardDecide(card, { answer: typed });
    return;
  }

  const btn = e.target.closest('.permission-card button[data-decision]');
  if (!btn) return;
  if (!card.dataset.actionId || !btn.dataset.decision) return;
  await permCardDecide(card, { decision: btn.dataset.decision });
});

// Enter submits a typed answer — an open question shouldn't need a mouse.
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Enter' || e.shiftKey) return;
  const input = e.target.closest('.permission-card .permission-reply-input');
  if (!input) return;
  e.preventDefault();
  input.closest('.permission-card')
       ?.querySelector('button[data-answer-submit]')?.click();
});

// Copy-message button (EON + operator bubbles). Delegated at document
// level so listeners survive every renderChatBody() innerHTML replace —
// same pattern as the permission-card delegation above. Pre-fix, the
// in-bindChat() querySelectorAll wired listeners ONCE on entry; every
// subsequent message render dropped them, so the button silently failed
// for the operator on all but the very first paint.
// THE ONE COPY PATH (extracted 2026-08-10 — Thelma: "I can't also copy from
// Eon"). navigator.clipboard requires (a) a secure context (https/localhost)
// and (b) the call to sit inside a user-gesture handler. Both hold in a click
// handler — but iOS Safari STILL rejects when the page isn't fully focused,
// and some embedded webviews block the API outright. Reps live on exactly
// those platforms, so every copy must fall through to execCommand.
//
// This lived inline in the main-chat listener while the CS desk's own "Copy
// text" called `navigator.clipboard.writeText` bare, with a catch that only
// toasted "Could not copy" — so the desk failed on the one platform this
// comment was written about, with the working code 16,000 lines above it.
// One owner now; a second copy of this logic is how the desk diverged.
async function eonCopyToClipboard(text) {
  text = String(text == null ? '' : text);
  if (!text) return false;
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (_) { /* fall through to the legacy path */ }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);   // iOS needs an explicit range
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return !!ok;
  } catch (_) { return false; }
}
window.eonCopyToClipboard = eonCopyToClipboard;

document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.msg-copy');
  if (!btn) return;
  e.stopPropagation();
  e.preventDefault();
  const idx = parseInt(btn.dataset.copyMsgid, 10);
  const m = Number.isFinite(idx) ? State.chat[idx] : null;
  const fromAttr = btn.closest('.msg')?.dataset?.msgText;
  const text = (m && (m.text || m.content)) || fromAttr || '';
  if (!text) { showToast('Nothing to copy'); return; }
  const ok = await eonCopyToClipboard(text);
  showToast(ok ? 'Copied' : 'Copy failed');
});

// PR-8 (2026-07-06): welcome starter chips — delegated so they work on
// every fresh-thread render. sendChat(forcedText) does the rest.
document.addEventListener('click', (e) => {
  const chip = e.target.closest('.eon-hero-chip');
  if (!chip) return;
  e.preventDefault();
  const q = chip.dataset.starter || '';
  if (q) sendChat(q);
});

// EON Voice (2026-07-13): per-message play button — delegated at
// document level (same rationale as .msg-copy above). Reuses the chat
// TTS lane end-to-end: speakText() streams /api/voice/tts and aborts
// any earlier playback itself.
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.msg-speak');
  if (!btn) return;
  e.stopPropagation();
  e.preventDefault();
  const idx = parseInt(btn.dataset.speakMsgid, 10);
  const m = Number.isFinite(idx) ? State.chat[idx] : null;
  const fromAttr = btn.closest('.msg')?.dataset?.msgText;
  const text = (m && (m.text || m.content)) || fromAttr || '';
  if (!text) { showToast('Nothing to play'); return; }
  speakText(text);
});

// PR-4 (2026-07-06): CSV export for chat tables. Delegated at document
// level (same rationale as .msg-copy above — listeners must survive
// renderChatBody() innerHTML replaces). Reads the RENDERED table DOM
// (textContent, so inline markdown is already flattened), quotes every
// field with `"` doubling, and downloads via a Blob anchor — works
// offline, nothing round-trips to the server.
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.md-csv-btn');
  if (!btn) return;
  e.stopPropagation();
  e.preventDefault();
  const table = btn.closest('.md-table-wrap')?.querySelector('table');
  if (!table) { showToast('No table found'); return; }
  const q = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  const lines = [];
  table.querySelectorAll('tr').forEach((tr) => {
    const cells = Array.from(tr.querySelectorAll('th, td'))
      .map((c) => q(c.textContent.trim()));
    if (cells.length) lines.push(cells.join(','));
  });
  if (!lines.length) { showToast('Table is empty'); return; }
  // UTF-8 BOM so Excel opens ₦ and other non-ASCII correctly.
  const blob = new Blob(['\uFEFF' + lines.join('\r\n')],
                        { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  const stamp = new Date().toISOString().slice(0, 16)
    .replace(/[-:]/g, '').replace('T', '-');
  a.href = URL.createObjectURL(blob);
  a.download = `eon-table-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  showToast('CSV downloaded');
});

// Chat images open in the house lightbox (2026-09-07) — the same viewer
// the CS desk uses, so zoom/swipe behave identically everywhere. A reply
// carrying several images opens as a gallery (prev/next, "2 of 4").
// Delegated at document level (same rationale as .msg-copy above —
// renderChatBody() innerHTML-replaces, per-node bindings die).
document.addEventListener('click', (e) => {
  const im = e.target.closest('.msg img.md-img, .msg .msg-att-thumb');
  if (!im || typeof cswLightbox !== 'function') return;
  if (im.classList.contains('md-img--dead')) return;   // broken stays honest
  e.preventDefault();
  const msg = im.closest('.msg');
  const sel = im.classList.contains('msg-att-thumb')
    ? '.msg-att-thumb' : 'img.md-img:not(.md-img--dead)';
  const kin = msg ? Array.from(msg.querySelectorAll(sel)) : [im];
  const capOf = (el) => {
    const a = el.getAttribute('alt') || '';
    return (a && a !== 'photo') ? a : '';
  };
  const items = kin.map(el => ({ url: el.dataset.full || el.src, cap: capOf(el) }));
  const index = Math.max(0, kin.indexOf(im));
  if (items.length > 1) {
    cswLightbox(items[index].url, items[index].cap, false, { items, index });
  } else {
    cswLightbox(items[0].url, items[0].cap, false);
  }
});

// A run of image-only paragraphs in one message becomes a gallery grid —
// the transcript stops being a scroll of stacked full-width pictures.
// Idempotent per render (fresh nodes each innerHTML replace); text that
// interleaves images keeps its images in place, only pure image runs
// group. More than four: the grid shows four, the overlay counts the
// rest, and the lightbox still pages through all of them.
function eonChatGalleryWrap(root) {
  if (!root || !root.querySelectorAll) return;
  root.querySelectorAll('.msg .text').forEach((t) => {
    if (t.dataset.eonGal) return;
    t.dataset.eonGal = '1';
    const isImgOnlyP = (p) => {
      if (!p || p.tagName !== 'P') return false;
      let n = 0;
      for (const k of p.childNodes) {
        if (k.nodeType === 3) { if (k.textContent.trim()) return false; continue; }
        if (k.nodeType !== 1) continue;
        if (k.matches && k.matches('img.md-img')) { n++; continue; }
        if (k.tagName === 'BR') continue;
        return false;
      }
      return n > 0;
    };
    const ps = Array.from(t.children);
    for (let i = 0; i < ps.length; i++) {
      if (!isImgOnlyP(ps[i])) continue;
      let j = i;
      const run = [];
      while (j < ps.length && isImgOnlyP(ps[j])) { run.push(ps[j]); j++; }
      const imgs = run.flatMap(p => Array.from(p.querySelectorAll('img.md-img')));
      if (imgs.length >= 2) {
        const g = document.createElement('div');
        g.className = 'msg-gallery' + (imgs.length === 2 ? ' msg-gallery--2' : '');
        if (imgs.length > 4) g.dataset.more = String(imgs.length - 4);
        run[0].before(g);
        imgs.forEach(im => g.appendChild(im));
        run.forEach(p => { if (!p.querySelector('img') && !p.textContent.trim()) p.remove(); });
      }
      i = j - 1;
    }
  });
}
// app.js is a module — top-level names are scoped. Exported for the
// headless bench (scripts/bench_chat_gallery.mjs), same pattern as
// window.eonCopyToClipboard / window.sosOpenReportSheet.
window.eonChatGalleryWrap = eonChatGalleryWrap;

// WS-A (2026-07-26): [data-ask] / [data-go] delegated at the document
// level — same rationale as .msg-copy/.md-csv-btn above. The old
// per-render querySelectorAll bindings died whenever a slot was
// innerHTML-replaced by a tick or partial refresh (the "Set target"
// tile went dead 90s after load). One listener, zero re-binds.
document.addEventListener('click', (e) => {
  const el = e.target.closest('[data-ask]');
  if (!el) return;
  e.preventDefault();
  const msg = el.dataset.ask;
  if (msg && typeof window.goChat === 'function') window.goChat(msg);
});
// Suggest tiles — data-attribute binding instead of an inline onclick, so a
// prefill may contain an apostrophe without escaping out of its own handler.
// Delegated at DOCUMENT level, mounted once, for the reason the alert-dismiss
// comment above gives: the binder that used to do this sat inside bindChat()
// and ran querySelectorAll once, against a view that renders these tiles on
// the MONEY page — so it matched nothing on every pass and the inline onclick
// stayed the only live wiring. Delegation survives every innerHTML replace.
document.addEventListener('click', (e) => {
  const el = e.target.closest('.suggest[data-suggest-msg]');
  if (!el) return;
  e.preventDefault();
  const msg = el.dataset.suggestMsg;
  if (msg && typeof window.goChat === 'function') window.goChat(msg);
});
document.addEventListener('click', (e) => {
  const el = e.target.closest('[data-go]');
  if (!el) return;
  // CS Workspace reuses data-go for its own segment switcher with its
  // own listener (cswRenderBody) — leave that lane alone.
  if (el.closest('#csw-body')) return;
  e.preventDefault();
  const dest = el.dataset.go;
  if (!dest) return;
  State.view = dest;
  render();
});

// WS-F (2026-07-26): keyboard. The app had two shortcuts total.
// `/` jumps to Chat and focuses the composer, 1–4 switch the nav tabs,
// Escape closes the menu/notification panels. Typing surfaces are
// never hijacked; modifier combos pass through untouched.
// The check below reads the key's real target (eonKeyTarget, just after
// this listener): a key typed in Ask EON's box arrives with e.target set
// to Ask's host, a plain DIV, and without it "3" in Ask opened Ads and
// "/" moved the rest of the question into the Chat box (Opus round 4,
// O4-2, 29 Sep 2026).
document.addEventListener('keydown', (e) => {
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  const t = eonKeyTarget(e);
  if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA'
            || t.tagName === 'SELECT' || t.isContentEditable)) return;
  if (!State.jwt) return;
  if (e.key === '/') {
    e.preventDefault();
    if (State.view !== 'chat') { State.view = 'chat'; render(); }
    setTimeout(() => $('#composer-input')?.focus(), 60);
  } else if (e.key >= '1' && e.key <= '4') {
    const v = ['home', 'chat', 'ads', 'tasks'][Number(e.key) - 1];
    if (v && State.view !== v) { State.view = v; render(); }
  } else if (e.key === 'Escape' && (State.menuOpen || State.notifsOpen)) {
    State.menuOpen = false;
    State.notifsOpen = false;
    document.querySelectorAll('.menu-panel, .menu-backdrop, .notif-panel')
      .forEach((el) => el.remove());
  }
});

// The element a key was really typed into. A key typed in a box inside a
// shadow root reaches every page-level listener with e.target set to the
// shadow's host. Ask EON's box lives in an open shadow root, so there
// e.target is Ask's host, a plain DIV, and an "is the person typing?"
// check that reads it never sees the box: "n" typed in Ask on Tasks
// opened Quick add, and Up, Down or Shift+Enter on the desk opened a
// customer's chat (critical review of #3647, 1 Oct 2026). The first entry
// of composedPath() is the real element. Outside a shadow root it is
// e.target itself, and a browser with no composedPath falls back to
// e.target. Every page-level key listener that asks "is the person
// typing?" reads its target through here.
function eonKeyTarget(e) {
  const p = e.composedPath && e.composedPath();
  return (p && p[0]) || e.target;
}

// The element that really has the focus, for the same reason: while the
// cursor is in Ask EON's box, document.activeElement is Ask's host. This
// walks down through open shadow roots to the real one. The checks that
// hold a refresh or a reload while the person is typing read it here.
function eonFocusedElement() {
  let el = document.activeElement;
  while (el && el.shadowRoot && el.shadowRoot.activeElement) el = el.shadowRoot.activeElement;
  return el;
}

// ── Smooth typewriter reveal ─────────────────────────────
// Buffers incoming chunks and reveals them at a paced rate so text feels
// like a flowing read, not jumpy jumps. Pauses longer on punctuation
// to mimic natural cadence.
let _revealTimer = null;
function startSmoothReveal(active) {
  if (_revealTimer) return;  // already running
  const tick = () => {
    if (!active.buffer || active.buffer.length === 0) {
      _revealTimer = null;
      return;
    }
    // Reveal 1-3 characters per tick. More chars when buffer is large
    // (catch up if we fall behind), slower when buffer is small.
    const burstSize = active.buffer.length > 80 ? 5
                    : active.buffer.length > 30 ? 3
                    : 1;
    const next = active.buffer.slice(0, burstSize);
    active.text += next;
    active.buffer = active.buffer.slice(burstSize);

    // Update the streaming bubble's text node directly — no full re-render
    appendStreamText(active.text);

    // Pace based on the last revealed character — punctuation feels more natural
    const lastChar = next[next.length - 1];
    let delay = 18;  // base char rate
    if (lastChar === '.' || lastChar === '!' || lastChar === '?') delay = 180;
    else if (lastChar === ',' || lastChar === ';') delay = 80;
    else if (lastChar === '\n') delay = 60;
    _revealTimer = setTimeout(tick, delay);
  };
  _revealTimer = setTimeout(tick, 0);
}
function stopSmoothReveal() {
  if (_revealTimer) {
    clearTimeout(_revealTimer);
    _revealTimer = null;
  }
}

// Direct DOM update for the streaming bubble — no full re-render
function appendStreamText(fullText) {
  const el = document.querySelector('.msg.streaming .text');
  if (el) {
    // WS-C (2026-07-26): markdown renders LIVE as the answer streams.
    // The old textContent path showed raw **bold** / |pipe|rows| for
    // the whole stream, then hard-snapped into formatting on done —
    // and it destroyed the caret on the first token. The renderer
    // escapes before matching (XSS-safe) and tolerates incomplete
    // markdown mid-stream; the caret rides inside the last block.
    el.innerHTML = renderMarkdownToHtml(fullText);
    (el.lastElementChild || el).insertAdjacentHTML('beforeend', '<span class="caret"></span>');
    // Auto-scroll the chat body if user is near the bottom
    const body = document.getElementById('chat-body');
    if (body) {
      const nearBottom = body.scrollHeight - body.scrollTop - body.clientHeight < 100;
      if (nearBottom) body.scrollTop = body.scrollHeight;
    }
  }
}

// ──────────────────────────────────────────────────────────
// Render shell

const $ = (s, root = document) => root.querySelector(s);
const elFromHTML = (html) => {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstChild;
};

// WS-B (2026-07-26): per-view scroll memory. 40 call sites rebuild
// #app via innerHTML — before this, every one of them silently threw
// the operator back to the top of whatever they were reading.
let _scrollPrevView = null;

// ── WS-F (2026-07-26): the app joins the browser ─────────────
// 40 view changes recorded zero history entries — the hardware Back
// button exited the app. Every render now syncs ?v=<view> into
// history; popstate restores the view. Boot deep-links via ?v=
// already existed — this makes the address live in both directions.
let _navFromPop = false;
function _viewReadScope() {
  return JSON.stringify([!!State.jwt, State.tenant?.id, State.tenant?.slug,
    State.user?.id || State.user?.user_id, State.member, State.impersonating || false]);
}
const _NAV_VIEWS = ['home', 'people', 'hr', 'me', 'cs', 'money', 'tasks', 'ads',
                    'inventory', 'recommendations', 'mydash', 'chat', 'staffos',
                    'system-audit', 'settings'];
function _syncHistory() {
  try {
    if (_navFromPop || !State.jwt) return;
    const cur = new URLSearchParams(location.search).get('v') || 'home';
    if (State.view === 'money') { moneySyncEntry(cur !== State.view); return; }
    if (cur === State.view) return;
    const params = new URLSearchParams();
    if (State.view !== 'home') params.set('v', State.view);
    if (cg.classic()) params.set('design', 'classic');
    if (State.workspaceLink) params.set('workspace', State.workspaceLink);
    const url = location.pathname + (params.toString() ? '?' + params.toString() : '');
    history.pushState({ v: State.view }, '', url);
  } catch (_) { /* sandboxed iframes etc. — navigation still works */ }
}
window.addEventListener('popstate', (e) => {
  const v = (e.state && e.state.v)
    || new URLSearchParams(location.search).get('v') || 'home';
  if (!_NAV_VIEWS.includes(v) || !State.jwt) return;
  // Work owns task-only history. Keep its mounted room, scroll and opener;
  // changed view, URL context or identity still goes through the real render.
  if (_workWorkspace?.preservesTaskRoom(v)) return;
  _navFromPop = true;
  try {
    if (!mtxAllowPageRebuild()) return;
    State.view = v;
    if (v === 'money') moneyRestoreEntry(e.state);
    render();
  }
  finally { _navFromPop = false; }
});

// THE HOUSE FEELS ITS ROOMS (2026-09-02). Fifteen screens are views inside
// this one address and the server never saw them open — the screen census
// registered them with no writer (153 opens counted since 31 Aug, none of
// them a room, none of them a person). Every view switch now tells the
// counter which room and which skin the reader has on; the person rides
// the token. Fire-and-forget: a counter must never slow or break the room.
let _censusLastView = null;
// The rooms the census registers (surface_census.SURFACES, kind=view).
// Anything else is a sub-state of a room, not a room, and is not sent.
const _CENSUS_VIEWS = new Set(['home', 'chat', 'ads', 'tasks', 'money', 'inventory',
  'recommendations', 'settings', 'mydash', 'cs', 'me', 'hr', 'people', 'staffos',
  'system-audit']);
function _censusBeacon() {
  try {
    if (!State.jwt || !State.view || State.view === 'auth') return;
    if (!_CENSUS_VIEWS.has(State.view)) return;
    if (State.view === _censusLastView) return;
    _censusLastView = State.view;
    const sid = 'view_' + String(State.view).replace(/-/g, '_');
    const skin = document.documentElement.getAttribute('data-theme') || '';
    // A raw fetch on purpose: the shared api() helper may redirect a dead
    // PIN session to the login on a 401, and a counter must never move
    // the person. keepalive lets the beacon outlive a navigation.
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${State.jwt}` };
    if (State.tenant && State.tenant.slug) headers['X-Tenant-Slug'] = State.tenant.slug;
    fetch(API + '/api/surface/open', {
      method: 'POST', keepalive: true, headers,
      body: JSON.stringify({ surface_id: sid, skin }),
    }).catch(() => {});
  } catch (_e) { /* never let the count break the room */ }
}

function render() {
  if (!mtxAllowPageRebuild()) return;
  if (State.view !== 'money') State.moneyGateDenied = false;
  chatConversation.sync();
  eonWorkroomDestroy();
  if (typeof _workWorkspace !== 'undefined' && _workWorkspace) _workWorkspace.sync();
  // Cash evidence belongs to the current authenticated page. A body-level
  // dialog must not survive logout, workspace changes or a rebuilt view.
  document.querySelector('#money-bank-cash-evidence')?._dismissCashEvidence?.(false);
  document.querySelector('#money-forward')?._disposeForward?.();
  document.querySelector('#money-workspace')?._disposeMoneyWorkspace?.();
  document.querySelector('.sa-wrap')?._cancelAuditReads?.();
  if(document.querySelector('#tos.task-room')) _trDispose();
  document.querySelectorAll('.tr-overlay').forEach(overlay=>overlay.remove());
  const app = $('#app');
  if (!State._scroll) State._scroll = {};
  const prevScroller = document.querySelector('main.view');
  if (prevScroller && _scrollPrevView) {
    State._scroll[_scrollPrevView] = prevScroller.scrollTop;
  }
  // 2026-06-08 UX fix: the menu sheet + backdrop are appended to <body>
  // (see renderHeader), so an #app innerHTML rebuild does NOT remove them.
  // Navigating menu items (Settings/Money/Inventory/Recs/Dashboard/My
  // Performance) set State.menuOpen=false then call render() — without this,
  // the sheet stayed open over the new view. Drop stale body-level panels
  // whenever their state is closed.
  if (!State.menuOpen) document.querySelectorAll('.menu-panel, .menu-backdrop').forEach((e) => e.remove());
  if (!State.notifsOpen) document.querySelectorAll('.notif-panel').forEach((e) => e.remove());
  // Staff PIN sessions never see the owner home (operator 2026-07-20).
  // The boot path enforced this only at boot; runtime nav (back buttons,
  // view switches) could still land 'home' — the reported wrong-dashboard
  // bug. Coerce here so every render honours the rule.
  if (State.view === 'home' && State.user && State.user.is_staff && !State.impersonating) State.view = 'staffos';
  cg.apply();
  lw.apply();
  try { window.EON_TIMING?.route(State.jwt ? State.view : 'auth', true); } catch (_) {}
  // Keep the full-screen-chat body class in lockstep with the view, so
  // leaving chat (which would otherwise hide the header/nav) always
  // restores the app chrome. No-op unless we're in an expanded chat.
  syncChatFullscreenClass();
  _censusBeacon();
  // Always include the grain background
  let bg = $('.grain-bg');
  if (!bg) {
    bg = elFromHTML('<div class="grain-bg" aria-hidden="true"></div>');
    document.body.prepend(bg);
  }

  if (!State.jwt) { app.innerHTML = renderAuth(); lw.decorateAuth(); bindAuth(); return; }
  // Direct comparison links use the normal authenticated bootstrap first.
  // Only the server's actual-owner verdict activates the new presentation.
  if (cg.requested()) {
    if (!cg.accessReady()) {
      try { window.EON_TIMING?.route('other', true); } catch (_) {}
      app.innerHTML = cg.gate();
      cg.ensureAccess().then(() => render());
      return;
    }
    if (!cg.allowed()) {
      try { window.EON_TIMING?.route('other', true); } catch (_) {}
      app.innerHTML = cg.gate(); return;
    }
    if (State.view === 'home') {
      State.view = 'people';
      try { window.EON_TIMING?.route(State.view, true); } catch (_) {}
    }
    cg.apply();
  }
  if (State.view === 'create_workspace') {
    app.innerHTML = renderCreateWorkspace();
    bindCreateWorkspace();
    return;
  }

  // CS Inbox + My Performance take the full viewport — hide the app
  // header and bottom nav (immersive). Their own back button returns home.
  if (State.view === 'cs' || State.view === 'me' || State.view === 'hr' || State.view === 'tasks' || State.view === 'staffos' || (cg.active() && State.view === 'people')) {
    _scrollPrevView = null;  // full-screen shells own their scroll
    app.innerHTML = renderViewWrapper();
    bindView();
    // This branch never calls bindNav(), so the Ask-EON orb kept the
    // PREVIOUS page's nav lift and floated ~100px above a nav that is
    // not here — over the brief's rows (IMG_9375, 2026-08-20). No nav
    // in the DOM → the lift's own no-nav branch writes 0px.
    liftAskEonAboveNav();
    mountShellBell();
    _syncHistory();
    return;
  }
  unmountShellBell();
  app.innerHTML = renderHeaderHTML() + renderViewWrapper() + renderNav();
  lw.mount();
  bindHeader();
  bindNav();
  bindView();
  // WS-B: restore this view's last scroll position (rAF again for
  // content that lands a frame later; async fills stay best-effort).
  _scrollPrevView = State.view;
  const scroller = document.querySelector('main.view');
  if (scroller) {
    const y = State._scroll[State.view] || 0;
    scroller.scrollTop = y;
    if (y) requestAnimationFrame(() => { scroller.scrollTop = y; });
  }
  _syncHistory();  // WS-F: Back traverses views instead of exiting
}

function renderViewWrapper() {
  const v = State.view;
  if (v === 'chat') {
    // chat needs its own column layout (body + sticky composer)
    return `<main class="chat-wrap eon-chat">${renderChatView()}</main>`;
  }
  if (v === 'cs' || v === 'me' || v === 'hr' || v === 'tasks' || v === 'people' || v === 'staffos') {
    // Full-screen fixed shells — render bare, no wrapper.
    return renderView();
  }
  return `<main class="view">${renderView()}</main>`;
}

// ── Role-aware home (ops-manager gap map WS-A, 2026-08-01) ──────────
// Every back button in the task/people lane hardcoded State.view='home' —
// the CEO's CRUX Command — so an ops lead always "returned" to Daniel's
// dashboard, and a staff PIN session bypassed the boot-only Staff OS
// guard. One rule for every back tap: staff sessions go home to Staff OS,
// the owner keeps CRUX Command, every other member returns to their own
// dashboard.
function roleHome() {
  if (State.impersonating) return 'home';
  if (State.user && State.user.is_staff) return 'staffos';
  const role = (State.member && (State.member.base_role || State.member.role)) || '';
  // 2026-09-06 operator: the owner's home is CRUX Command, in either design.
  // People OS stays one tap away in the nav; it is no longer the back-stop.
  return role === 'owner' ? 'home' : 'mydash';
}

function renderView() {
  switch (State.view) {
    case 'home': return renderHome();
    case 'money': return wrapMoneyWorkspace(renderMoney());
    case 'tasks': return renderTaskOS();
    case 'ads': return renderAds();
    case 'inventory': if (inventoryLegacyRequested()) State.inventoryLegacy = true; if (State.inventoryLegacy) return renderInventory(); openInventoryWorkspace('replace'); return renderWorkspaceHandoff();
    case 'recommendations': return renderRecommendations();
    case 'settings': return renderSettings();
    case 'mydash': return renderMyDashboard();
    case 'cs': return renderCsInbox();
    case 'me': return renderMyPerf();
    // HOUSE floor 2 — "one room, one door": the legacy hr dashboard folds
    // into People OS. Old ?v=hr deep links land on the roster; classic
    // keeps both pages exactly as they are.
    case 'hr': return tosHouse() ? renderPeople() : renderHr();
    case 'people': return renderPeople();
    case 'staffos': return renderStaffOs();
    case 'system-audit': return renderSystemAudit();
    default:
      State.view = 'home';
      return renderHome();
  }
}

function bindView() {
  startLiveRefresh();   // idempotent — starts the single live-refresh timer once
  if (State.view === 'home') bindHome();
  if (State.view === 'chat') bindChat();
  if (State.view === 'money') bindMoney();
  if (State.view === 'tasks') bindTaskOS();
  if (State.view === 'ads') bindAds();
  if (State.view === 'inventory' && State.inventoryLegacy) bindInventory();
  if (State.view === 'recommendations') bindRecommendations();
  if (State.view === 'settings') bindSettings();
  if (State.view === 'mydash') bindMyDashboard();
  if (State.view === 'cs') bindCsInbox();
  if (State.view === 'me') bindMyPerf();
  if (State.view === 'hr') (tosHouse() ? bindPeople() : bindHr());
  if (State.view === 'people') bindPeople();
  if (State.view === 'staffos') bindStaffOs();
  if (State.view === 'system-audit') bindSystemAudit();
  if (State.view === 'chat') eonMountHeroWash();
}

// Aurora anchor #9 (operator 2026-07-26: "look and feel like an advanced
// app ... use webgl to make it better"): the EON chat HOME hero — the
// greeting screen the operator opens every day — gets the house wash
// behind the greet + starter chips only. FLAT LAW holds: the wash lives
// on the data-free hero and is gone the moment a conversation renders
// (the hero markup is replaced; aurora's detach ladder stops the loop).
function eonMountHeroWash() {
  const c = document.getElementById('eon-hero-wash');
  if (!c || typeof window.mountAurora !== 'function') return;
  if (State._heroWash && State._heroWash._canvas === c) return;
  if (State._heroWash) { try { State._heroWash.destroy(); } catch (_) {} }
  const h = window.mountAurora(c, ['#C8FF4D', '#56E39F', '#7AA6FF'],
    { alpha: true, vignette: false, glow: null, intensity: 0.3 });
  if (!h) return;
  h._canvas = c;
  State._heroWash = h;
}

// ──────────────────────────────────────────────────────────
// Header

function renderHeaderHTML() {
  const unreadNotifs = (State.notifs || []).filter(n => !n.read_at).length;
  const unread = unreadNotifs + (State.alerts || []).length;
  const wsName = State.tenant?.name || 'EON';
  const role = State.member?.role || '';
  return `
    <header class="app-bar" id="hdr">
      <div class="brand">
        <div class="brand-mark">${esc((wsName)[0].toUpperCase())}</div>
        <div class="brand-text">
          <div class="brand-name">${esc(wsName)}</div>
          ${role ? `<div class="brand-sub">${esc(role)}</div>` : ''}
        </div>
      </div>
      <div class="app-actions">
        ${_mailAllowed() ? '<button class="icon-btn" id="work-search-btn" aria-label="Find work" title="Find tasks and email">⌕</button>' : ''}
        <button class="icon-btn" id="notif-btn" aria-label="Notifications" aria-expanded="${State.notifsOpen}"${State.notifsOpen ? ' aria-controls="notif-panel"' : ''}>
          ${bellIcon()}
          ${unread > 0 ? `<span class="badge">${unread > 9 ? '9+' : unread}</span>` : ''}
        </button>
        <button class="icon-btn" id="menu-btn" aria-label="Menu">${menuIcon()}</button>
      </div>
    </header>
    ${State.notifsOpen ? renderNotifPanel() : ''}
    ${State.menuOpen ? renderMenuPanel() : ''}
  `;
}

// ── the shell bell (relay R0 item 6) ────────────────────────────────
// Task OS and Staff OS are full-screen shells: render() returns before
// renderHeaderHTML(), so neither has ever carried a bell. That is the
// half of the deep link nobody could tap — the ping named the task and
// the two surfaces a staffer lives in had no notification affordance at
// all. This is the bell alone, docked to <body> (the shells rebuild
// #app, so anything inside it would be wiped on the next render), and it
// shares renderNotifPanel() + the document-level row delegation with the
// header's, so there is one notification vocabulary, not two.
const SHELL_BELL_VIEWS = ['tasks', 'staffos'];

function unmountShellBell() {
  document.getElementById('shell-bell')?.remove();
}

function mountShellBell() {
  unmountShellBell();
  if (!SHELL_BELL_VIEWS.includes(State.view)) return;
  const unread = (State.notifs || []).filter(n => !n.read_at).length
    + (State.alerts || []).length;
  const btn = elFromHTML(
    `<button class="shell-bell icon-btn" id="shell-bell" aria-label="Notifications" aria-expanded="${State.notifsOpen}"${State.notifsOpen ? ' aria-controls="notif-panel"' : ''}>`
    + bellIcon()
    + (unread > 0 ? `<span class="badge">${unread > 9 ? '9+' : unread}</span>` : '')
    + `</button>`);
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (State.notifsOpen) { _closeNotifications(); return; }
    State._notifTriggerId = 'shell-bell';
    State.notifsOpen = true;
    _mountShellPanel();
    Promise.all([loadAlerts(), loadNotifications()]).then(() => {
      if (!State.notifsOpen) return;
      const focus = _notifPanelSnapshot();
      mountShellBell();
      _mountShellPanel(focus);
    }).catch(() => {});
  });
  document.body.appendChild(btn);
}

// The panel, docked to <body> and lifted above the shell's own stacking
// context (.sos-root is z-index 1000). Same markup as the header's — one
// notification vocabulary — with a modifier class carrying the lift.
function _mountShellPanel(focus = _notifPanelSnapshot()) {
  document.querySelectorAll('.notif-panel').forEach((p) => p.remove());
  const panel = elFromHTML(renderNotifPanel());
  panel.classList.add('notif-panel--shell');
  document.body.appendChild(panel);
  _restoreNotifPanel(focus);
}

// Keep the reader's place through feed/read-state refreshes. Only the selected
// control's identity and local scroll survive; no notification content is stored.
function _notifPanelSnapshot() {
  const panel = document.querySelector('.notif-panel'), active = document.activeElement;
  return { existed: !!panel, scroll: panel?.querySelector('.notif-list')?.scrollTop || 0,
    focused: !!panel?.contains(active), id: active?.id,
    alert: active?.getAttribute('data-alert-id'), notification: active?.getAttribute('data-notif-id') };
}
function _syncNotifButtons() {
  for (const id of ['notif-btn','shell-bell']) {
    const button = document.getElementById(id);
    if (State.notifsOpen) button?.setAttribute('aria-controls','notif-panel');
    else button?.removeAttribute('aria-controls');
    button?.setAttribute('aria-expanded', String(!!State.notifsOpen));
  }
}
function _restoreNotifPanel(focus) {
  _syncNotifButtons();
  const panel = document.querySelector('.notif-panel');
  if (!panel) return;
  const list = panel.querySelector('.notif-list');
  if (list) list.scrollTop = focus?.scroll || 0;
  let target;
  if (focus?.focused) {
    target = [...panel.querySelectorAll('[id],[data-alert-id],[data-notif-id]')].find(el =>
      focus.id ? el.id === focus.id : focus.alert ? el.matches('.alert-dismiss') && el.dataset.alertId === focus.alert
        : focus.notification ? el.dataset.notifId === focus.notification : false);
    target ||= panel.querySelector('#notif-close');
  } else if (!focus?.existed) target = panel.querySelector('#notif-title');
  else if (['notif-btn','shell-bell'].includes(focus.id)) target = document.getElementById(focus.id);
  target?.focus({preventScroll:true});
}
function _closeNotifications(restoreFocus = true) {
  State.notifsOpen = false;
  clearTimeout(State._notifMarkTimer);
  document.querySelectorAll('.notif-panel').forEach(panel => panel.remove());
  _syncNotifButtons();
  const trigger = document.getElementById(State._notifTriggerId || 'notif-btn')
    || document.getElementById('shell-bell');
  if (restoreFocus) trigger?.focus({preventScroll:true});
}

function renderHeader() {
  hf.captureMenu();
  const focus = _notifPanelSnapshot();
  const old = $('#hdr');
  if (old) {
    const next = elFromHTML(renderHeaderHTML());
    old.replaceWith(next);
    // Clean any open panels
    $('.notif-panel')?.remove();
    $('.menu-panel')?.remove();
    $('.menu-backdrop')?.remove();
    if (State.notifsOpen) {
      const p = elFromHTML(renderNotifPanel());
      next.insertAdjacentElement('afterend', p);
    }
    if (State.menuOpen) {
      // Menu has backdrop + panel — insert both
      const wrap = document.createElement('div');
      wrap.innerHTML = renderMenuPanel().trim();
      Array.from(wrap.children).forEach(child => {
        document.body.appendChild(child);
      });
    }
    lw.mountHeader();
    bindHeader();
    _restoreNotifPanel(focus);
  } else if (SHELL_BELL_VIEWS.includes(State.view)) {
    mountShellBell();
    if (State.notifsOpen) _mountShellPanel(focus);
  }
}

function bindHeader() {
  $('#work-search-btn')?.addEventListener('click', (event) => _workRoom().search(event.currentTarget));
  $('#notif-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    if (State.notifsOpen) { _closeNotifications(); return; }
    State._notifTriggerId = 'notif-btn';
    State.notifsOpen = !State.notifsOpen;
    State.menuOpen = false;
    clearTimeout(State._notifMarkTimer);
    // Render IMMEDIATELY from cached state (no blocking on two fetches),
    // then refresh the feeds and re-render if still open. Panel buttons are
    // handled by the document-level delegation below — no per-render binds,
    // so a re-render (WS push, auto-mark, full render()) can't orphan them.
    renderHeader();
    if (State.notifsOpen) {
      Promise.all([loadAlerts(), loadNotifications()]).then(() => {
        if (State.notifsOpen) renderHeader();
      }).catch(() => {});
      // Seeing them = read. Clears the badge after a beat so the unread
      // styling is briefly visible first — but only if the panel is STILL
      // open when the timer fires.
      State._notifMarkTimer = setTimeout(() => {
        if (State.notifsOpen) markNotifsRead();
      }, 1200);
    }
  });
  $('#menu-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    if (typeof hf !== 'undefined' && lw.active()) { hf.open(e.currentTarget); return; }
    State.menuOpen = !State.menuOpen;
    State.notifsOpen = false;
    renderHeader();
  });
  // Header refresh button removed (2026-06-07): data already refreshes on
  // every page load/navigation (cheap reads of synced data) + the 90s
  // live-refresh + the 15-min eon_sync cron. The contextual freshness
  // banner (#freshness-refresh-btn) still offers a manual re-sync when
  // data is actually detected stale — see triggerGlobalRefresh below.
  // Close panels on outside click
  setTimeout(() => {
    document.addEventListener('click', () => {
      if (State.menuOpen || State.notifsOpen) {
        State.menuOpen = false;
        State.notifsOpen = false;
        renderHeader();
      }
    }, { once: true });
  }, 50);

  // Bind menu items
  $('#menu-theme')?.addEventListener('click', (e) => {
    e.stopPropagation();
    // Quick toggle stays dark ↔ light; from Design Flow it toggles the
    // remembered classic mode (full skin switch lives in Settings).
    const cur = document.documentElement.getAttribute('data-theme');
    const base = cur === 'flow' ? (localStorage.getItem('eon_classic_mode') || 'dark') : cur;
    setTheme(base === 'dark' ? 'light' : 'dark');
    renderHeader();
  });
  $('#menu-settings')?.addEventListener('click', () => {
    State.menuOpen = false;
    State.view = 'settings';
    render();
  });
  $('#menu-money')?.addEventListener('click', () => {
    State.menuOpen = false;
    State.view = 'money';
    render();
  });
  $('#menu-inventory')?.addEventListener('click', () => {
    // Ordinary Inventory navigation opens the selected workspace page
    // (operator, 2026-09-06). The older hub keeps its own explicit door.
    State.menuOpen = false;
    openInventoryWorkspace('assign');
  });
  $('#menu-recs')?.addEventListener('click', () => {
    State.menuOpen = false;
    State.view = 'recommendations';
    render();
  });
  $('#menu-mydash')?.addEventListener('click', () => {
    State.menuOpen = false;
    State.view = 'mydash';
    render();
  });
  $('#menu-sysaudit')?.addEventListener('click', () => {
    State.menuOpen = false;
    State.view = 'system-audit';
    render();
  });
  $('#menu-people')?.addEventListener('click', () => {
    State.menuOpen = false;
    State.view = 'people';
    render();
  });
  $('#menu-staffos')?.addEventListener('click', () => {
    State.menuOpen = false;
    State.view = 'staffos';
    render();
  });
  // Marketing — the ads/campaign surface (/marketing). Operator ask
  // 2026-07-29: it was reachable only by typing the URL (Studio's back
  // arrow pointed at it, nothing pointed IN). Own full page like the
  // other standalone SPAs — navigate, don't switch in-app views.
  $('#menu-marketing')?.addEventListener('click', () => {
    window.location.href = '/marketing';
  });
  // Orchestra — who is working for you right now (12 Sep 2026): the two
  // companies' seats and the room left in each, the lane, the work desk.
  // Owner-only on the server; the tile shows only to the owner. Its own
  // full page, like /marketing — navigate, don't switch in-app views.
  $('#menu-orchestra')?.addEventListener('click', () => {
    window.location.href = '/orchestra';
  });
  $('#menu-experiments')?.addEventListener('click', () => {
    window.location.href = '/engine/experiments';
  });
  // 2026-06-10: Business Intelligence is its own full page (like
  // /marketing) — navigate, don't switch in-app views.
  $('#menu-intel')?.addEventListener('click', () => {
    window.location.href = '/intelligence';
  });
  // Manager Reports — its own full page (like /intelligence). Owner/ops see
  // every department tab; the page reads ?staff_id= to scope per-manager.
  $('#menu-reports')?.addEventListener('click', () => {
    window.location.href = '/reports';
  });
  // Academy — role-shaped growth (PR A-1). Own full page, Design Flow
  // native; the shell + transparency page ship first, cohorts in A-5.
  $('#menu-academy')?.addEventListener('click', () => {
    window.location.href = '/academy';
  });
  // Studio — the CRUX Look Studio (/studio) was only reachable via the
  // marketing surface; operator ask 2026-07-06: first-class menu entry.
  $('#menu-studio')?.addEventListener('click', () => {
    window.location.href = '/studio';
  });
  // Design Studio — the winner board (D-1): what the live data says is
  // winning, with UNREAD honesty + proposed reorder drafts.
  $('#menu-design')?.addEventListener('click', () => {
    window.location.href = '/design';
  });
  // Staff logins (owner-only): set/reset 4-digit PINs + "view as" any staff.
  $('#menu-stafflogins')?.addEventListener('click', () => {
    State.menuOpen = false;
    document.querySelectorAll('.menu-panel, .menu-backdrop').forEach((e) => e.remove());
    openStaffLogins();
  });
  $('#menu-whatsnew')?.addEventListener('click', (e) => {
    e.stopPropagation();
    showWhatsNew();
  });
  // WS-F: the shell handbook — same guide estate as People OS.
  $('#menu-guide')?.addEventListener('click', (e) => {
    e.stopPropagation();
    State.menuOpen = false;
    document.querySelectorAll('.menu-panel, .menu-backdrop').forEach((el) => el.remove());
    openShellGuide();
  });
  // EON's beliefs — browse/retire everything in long-term memory
  // (2026-07-24, GBrain-inspired beliefs surface).
  $('#menu-beliefs')?.addEventListener('click', (e) => {
    e.stopPropagation();
    showBeliefs();
  });
  $('#menu-version')?.addEventListener('click', (e) => {
    e.stopPropagation();
    // EON_BUILD: bumped on every shipped PR so the operator can tell
    // "did the new code reach my device?" vs "is this a service-worker
    // cache showing yesterday's bundle?" The displayed build label is
    // also embedded in the menu footer (see renderMenuPanel).
    const stamp = `EON ${EON_BUILD} · tenant ${State.tenant?.id || '—'}`;
    navigator.clipboard?.writeText(stamp).then(
      () => showToast('Build info copied'),
      () => showToast(stamp));
  });
  $('#menu-tts')?.addEventListener('click', (e) => {
    e.stopPropagation();
    State.voice.ttsEnabled = !State.voice.ttsEnabled;
    localStorage.setItem('eon_tts', State.voice.ttsEnabled ? '1' : '0');
    renderHeader();
    showToast(State.voice.ttsEnabled
      ? 'EON will read replies aloud'
      : 'EON voice replies off');
  });
  // Sound notifications toggle (2026-07-12) — chime on reply done +
  // approval needed. Plays the done chime on enable as the preview.
  $('#menu-sound')?.addEventListener('click', (e) => {
    e.stopPropagation();
    const next = soundEnabled() ? '0' : '1';
    localStorage.setItem('eon_sound', next);
    renderHeader();
    if (next === '1') playChime('done');
    showToast(next === '1'
      ? 'Sound on — chime when EON finishes or needs approval'
      : 'Sound notifications off');
  });
  $('#menu-signout')?.addEventListener('click', () => {
    if (confirm('Sign out?')) logout();
  });
  // Backdrop click closes the menu
  $('#menu-backdrop')?.addEventListener('click', () => {
    State.menuOpen = false;
    renderHeader();
  });
}

// ── WS-F (2026-07-26): the shell handbook ────────────────────
// People OS, CS Desk, Marketing and Inventory each have a plain-
// English in-app handbook; the shell itself — the spine — had none.
// Same .mkg estate + GuideKit search. Opened from the menu's book
// button; one overlay, no route.
function openShellGuide() {
  if (document.querySelector('.shell-guide-overlay')) return;
  const ov = document.createElement('div');
  ov.className = 'shell-guide-overlay';
  ov.innerHTML = `
  <button class="btn-secondary shell-guide-close" aria-label="Close guide">Close ✕</button>
  <div class="mkg mkg--shell">
    <div class="mkg-hero">
      <div class="mkg-kicker">EON OS · Shell Handbook · updated 26 July 2026</div>
      <h1>How the main app works</h1>
      <p>The four tabs and the menu, every button explained in plain English.
      Use the search box to jump to a word.</p>
    </div>
    <nav class="mkg-toc">
      <a data-jump="sg-home">Home</a>
      <a data-jump="sg-chat">Chat</a>
      <a data-jump="sg-ads">Ads</a>
      <a data-jump="sg-tasks">Tasks</a>
      <a data-jump="sg-money">Money</a>
      <a data-jump="sg-menu">Menu &amp; themes</a>
      <a data-jump="sg-keys">Shortcuts</a>
    </nav>
    <section id="sg-home"><h2>Home</h2>
      <p>Yesterday and today at a glance. The <b>target card</b> is the headline:
      the ring is how much of the month's revenue target is already in, the small
      dot on the ring is where today <i>should</i> sit, and the right rail shows the
      run-rate, the daily amount still needed, and the projection. Numbers refresh
      themselves every 90 seconds — the "live · updated" stamp in the top bar shows
      when. Every <b>Ask EON</b> button drops a ready-made question into Chat —
      nothing sends until you hit send.</p></section>
    <section id="sg-chat"><h2>Chat</h2>
      <p>The cockpit. The box grows as you type (up to ~40% of the screen), drafts
      survive switching tabs, and on the phone the return key makes a new line —
      the arrow sends. While EON works you see each tool it uses; if EON needs a
      yes from you, a <b>green approval banner</b> pins above the box and a red dot
      sits on the Chat tab until you decide. The deep-mode button routes a thread
      to the strongest model; the corners button goes full screen.</p></section>
    <section id="sg-ads"><h2>Ads</h2>
      <p>The daily read: guardian health, spend per platform, EON's verdicts, and
      the creative bench. Verdict buttons ("Review 2 kills", "Scale case") open a
      pre-filled question in Chat — EON then queues any change for your approval.
      It never touches an ad on its own.</p></section>
    <section id="sg-tasks"><h2>Tasks</h2>
      <p>The Task OS command center: what needs doing, who owns it, what's blocked.
      Ask EON inside any task — it knows that task's full context.</p></section>
    <section id="sg-money"><h2>Money</h2>
      <p>Revenue, cash by account, P&amp;L, budgets and books (in the side menu).
      Same live stamp, same 90-second self-refresh. Amounts read the same way
      everywhere: compact on tiles (₦1.2M), exact in tables (₦1,214,338).</p></section>
    <section id="sg-menu"><h2>Menu &amp; themes</h2>
      <p>The grid button opens every OS — Money, Inventory, Studio, People, and
      more. The small icon strip holds theme, voice replies, sounds, EON's beliefs,
      what's new, this guide, and sign out. Three skins live in Settings:
      Classic dark, Light, and Design Flow (bone or ink).</p></section>
    <section id="sg-keys"><h2>Shortcuts</h2>
      <p><b>/</b> — jump to Chat and start typing · <b>1–4</b> — Home, Chat, Ads,
      Tasks · <b>Esc</b> — close panels · <b>↵</b> sends on desktop, <b>⇧↵</b> makes
      a new line. Your phone's back button and gestures work everywhere — Back
      returns to the previous tab instead of leaving the app.</p></section>
  </div>`;
  document.body.appendChild(ov);
  ov.querySelector('.shell-guide-close').addEventListener('click', () => ov.remove());
  // Ratchet law: backdrop close goes through eonBackdropClose — a drag
  // that starts inside the guide and ends on the backdrop never closes.
  eonBackdropClose(ov, () => ov.remove());
  if (window.GuideKit) window.GuideKit.boot(ov);
}

function renderMenuPanel() {
  // 2026-07-08 (operator: "the side bar menu is a cluster, way too many
  // text and not well arranged" — picked Direction A "launcher grid"):
  // two-column icon+label tiles in three job groups, zero description
  // lines, utilities collapsed to an icon strip. Every element keeps its
  // historical id so the bindings below are untouched.
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  const wsName = State.tenant?.name || 'EON';
  const role = State.member?.role || '';
  // Tiles are written out literally (no builder) so ids stay source-
  // greppable — the repo's source-inspection tests pin id="menu-…".
  const av = (v) => (State.view === v ? ' active' : '');
  return `
    <div class="menu-backdrop" id="menu-backdrop"></div>
    <div class="menu-panel cc-menu" onclick="event.stopPropagation()">
      <div class="menu-handle"></div>

      <button class="menu-ws" id="menu-settings">
        ${eonLogo(20)}
        <span class="menu-ws-name">${esc(wsName)}</span>
        <span class="menu-ws-role">${esc(role || 'settings')}</span>
      </button>

      <div class="menu-glab">[ SELL ]</div>
      <div class="menu-grid">
        <button class="menu-tile${av('money')}" id="menu-money"><span class="menu-tile-ic">${moneyIcon()}</span><span class="menu-tile-l">Money</span></button>
        <button class="menu-tile${av('inventory')}" id="menu-inventory"><span class="menu-tile-ic">${inventoryIcon()}</span><span class="menu-tile-l">Inventory</span></button>
        <button class="menu-tile${av('recommendations')}" id="menu-recs"><span class="menu-tile-ic">${recsIcon()}</span><span class="menu-tile-l">Recommendations</span></button>
        <button class="menu-tile${av('mydash')}" id="menu-mydash"><span class="menu-tile-ic">${dashboardIcon()}</span><span class="menu-tile-l">Dashboard</span></button>
      </div>

      <div class="menu-glab">[ RUN ]</div>
      <div class="menu-grid">
        <button class="menu-tile${av('people')}" id="menu-people"><span class="menu-tile-ic">${teamIcon()}</span><span class="menu-tile-l">People OS</span></button>
        <button class="menu-tile${av('staffos')}" id="menu-staffos"><span class="menu-tile-ic">${staffIcon()}</span><span class="menu-tile-l">Staff OS</span></button>
        <button class="menu-tile" id="menu-reports"><span class="menu-tile-ic">${reportIcon()}</span><span class="menu-tile-l">Manager Reports</span></button>
        <button class="menu-tile${av('system-audit')}" id="menu-sysaudit"><span class="menu-tile-ic">${auditIcon()}</span><span class="menu-tile-l">System Audit</span></button>
        ${State.member?.role === 'owner' && !State.impersonating ? `
        <button class="menu-tile" id="menu-orchestra"><span class="menu-tile-ic">${orchestraIcon()}</span><span class="menu-tile-l">Orchestra</span></button>` : ''}
      </div>

      <div class="menu-glab">[ GROW ]</div>
      <div class="menu-grid">
        <button class="menu-tile" id="menu-marketing"><span class="menu-tile-ic">${adsIcon()}</span><span class="menu-tile-l">Marketing</span></button>
        <button class="menu-tile" id="menu-intel"><span class="menu-tile-ic">${eonLogo(16)}</span><span class="menu-tile-l">Intelligence</span></button>
        <button class="menu-tile" id="menu-studio"><span class="menu-tile-ic">${studioIcon()}</span><span class="menu-tile-l">Studio</span></button>
        <button class="menu-tile" id="menu-design"><span class="menu-tile-ic">${designIcon()}</span><span class="menu-tile-l">Design</span></button>
        <button class="menu-tile" id="menu-academy"><span class="menu-tile-ic">${academyIcon()}</span><span class="menu-tile-l">Academy</span></button>
        ${(State.member?.base_role || State.member?.role) === 'owner' && !State.impersonating ? `<button class="menu-tile" id="menu-experiments"><span class="menu-tile-ic">${auditIcon()}</span><span class="menu-tile-l">Experiments</span></button>` : ''}
      </div>

      <button class="menu-tile" id="menu-interactions" type="button" data-eon-interactions><span class="menu-tile-l">Display and sound</span></button>
      <div class="menu-util">
        <button class="menu-util-btn" id="menu-theme" title="${isDark ? 'Switch to light mode' : 'Switch to dark mode'}" aria-label="Toggle theme">${themeIcon()}</button>
        <button class="menu-util-btn${State.voice.ttsEnabled ? ' on' : ''}" id="menu-tts" ${_voiceTtsAttributes()} title="EON voice replies: ${State.voice.ttsEnabled ? 'on' : 'off'}" aria-label="Toggle voice replies">${speakerIcon()}</button>
        <button class="menu-util-btn${soundEnabled() ? ' on' : ''}" id="menu-sound" title="Sound notifications: ${soundEnabled() ? 'on' : 'off'}" aria-label="Toggle sound notifications">${bellIcon()}</button>
        <button class="menu-util-btn" id="menu-beliefs" title="EON's beliefs" aria-label="EON's beliefs">${beliefsIcon()}</button>
        <button class="menu-util-btn" id="menu-whatsnew" title="What's new" aria-label="What's new">${sparkleIcon()}</button>
        <button class="menu-util-btn" id="menu-guide" title="How the app works" aria-label="How the app works">${reportIcon()}</button>
        ${State.member?.role === 'owner' && !State.impersonating ? `
        <button class="menu-util-btn" id="menu-stafflogins" title="Staff logins" aria-label="Staff logins">${pinIcon()}</button>` : ''}
        <button class="menu-util-btn danger" id="menu-signout" title="Sign out" aria-label="Sign out">${signoutIcon()}</button>
        <button class="menu-version" id="menu-version" title="Tap to copy">
          <span class="menu-version-text">
            <span class="menu-version-main">${EON_BUILD}</span>
            <span class="menu-version-sub">${esc((State.tenant?.id || '').slice(0, 8))}</span>
          </span>
        </button>
      </div>
    </div>
  `;
}

function _notifPanelHead(unread = false) {
  return `<div class="notif-head">
    <h2 class="eyebrow" id="notif-title" tabindex="-1">Notifications</h2>
    <div class="notif-head-actions">
      ${unread ? '<button type="button" id="notif-mark-read" class="notif-mark-read">Mark all read</button>' : ''}
      <button type="button" id="notif-close" class="notif-close" aria-label="Close notifications">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6"/></svg>
      </button>
    </div>
  </div>`;
}

function renderNotifPanel() {
  const alerts = State.alerts || [];
  const notifs = State.notifs || [];
  const total = alerts.length + notifs.length;
  // Audit 2026-08-07 (truth doctrine): a broken feed must never wear the
  // "All clear" face. Error state renders BEFORE the empty state — but
  // NEVER suppresses the alerts list, which rides its own endpoint
  // (Opus vet HIGH-2: a 502 on /api/notifications was hiding live
  // danger alerts). Full-panel error only when BOTH feeds are empty.
  if (State.notifsError && notifs.length === 0 && alerts.length === 0) {
    return `
      <div class="notif-panel${_mailAllowed() ? ' wr-owner-notifications' : ''}" id="notif-panel" role="region" aria-labelledby="notif-title" onclick="event.stopPropagation()">
        ${_notifPanelHead()}
        <div class="empty" style="padding: 32px 24px;">
          <div class="empty-mark" style="color:var(--danger)">!</div>
          <div class="empty-sub"><b>Couldn't load notifications.</b> ${esc(String(State.notifsError).slice(0, 120))} — pull to retry or tap the bell again.</div>
        </div>
      </div>`;
  }
  if (total === 0) {
    return `
      <div class="notif-panel${_mailAllowed() ? ' wr-owner-notifications' : ''}" id="notif-panel" role="region" aria-labelledby="notif-title" onclick="event.stopPropagation()">
        ${_notifPanelHead()}
        <div class="empty" style="padding: 32px 24px;">
          <div class="empty-mark">○</div>
          <div class="empty-sub">All clear. EON will notify you when something needs your attention.</div>
        </div>
      </div>`;
  }
  const sevDot = (s) => {
    const color = s === 'danger' ? '#d23131' : s === 'warn' ? '#d49328' : '#7a7a7a';
    return `<span class="sev-dot" style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:8px;vertical-align:middle"></span>`;
  };
  const hasUnread = notifs.some(n => !n.read_at);
  const kindLabel = (k) => (k || '').replace(/_/g, ' ');
  return `
    <div class="notif-panel${_mailAllowed() ? ' wr-owner-notifications' : ''}" id="notif-panel" role="region" aria-labelledby="notif-title" onclick="event.stopPropagation()">
      ${_notifPanelHead(hasUnread)}
      ${State.notifsError ? `<div style="padding:8px 16px;font-size:11.5px;color:var(--warn);border-bottom:1px solid var(--line)">Feed unreachable — showing what loaded last. ${esc(String(State.notifsError).slice(0, 80))}</div>` : ''}
      <div class="notif-list">
        ${_mailAllowed() && alerts.length ? '<div class="notif-section">Needs a decision · alerts</div>' : ''}
        ${alerts.slice(0, 15).map(a => `
          <div class="notif-item alert-item" data-alert-id="${esc(a.id)}">
            <div class="t">${sevDot(a.severity)}${esc(a.title || '')}</div>
            <div class="b">${esc(a.body || '')}</div>
            <button type="button" class="alert-dismiss" data-alert-id="${esc(a.id)}">Dismiss</button>
          </div>
        `).join('')}
        ${_mailAllowed() && notifs.length ? '<div class="notif-section">Updates · reading does not complete work</div>' : ''}
        ${notifs.slice(0, 20).map(n => `
          <div class="notif-item${n.read_at ? '' : ' notif-unread'}${_notifHref(n) ? ' notif-link' : ''}"
               ${_notifHref(n) ? `data-notif-url="${esc(_notifHref(n))}" data-notif-id="${esc(n.id || '')}" role="link" tabindex="0"` : ''}>
            <div class="t">
              ${n.read_at ? '' : '<span class="notif-dot"></span>'}${esc(n.title || 'Update')}
              ${n.created_at ? `<span class="notif-time">${esc(_timeAgo(n.created_at))}</span>` : ''}
            </div>
            <div class="b">${esc(n.body || '')}</div>
            ${n.kind ? `<div class="notif-kind">${esc(kindLabel(n.kind))}</div>` : ''}
          </div>
        `).join('')}
      </div>
      ${alerts.length > 15 || notifs.length > 20 ? `<p class="notif-coverage">Showing ${Math.min(15, alerts.length)} of ${alerts.length} loaded alerts and ${Math.min(20, notifs.length)} of ${notifs.length} loaded updates.</p>` : ''}
    </div>`;
}

async function loadAlerts() {
  try {
    const r = await api('/api/alerts');
    State.alerts = r.alerts || [];
  } catch (e) {
    console.warn('alerts load failed', e);
    State.alerts = [];
  }
}

async function dismissAlert(alertId) {
  try {
    await api(`/api/alerts/${alertId}/acknowledge`, { method: 'POST' });
    State.alerts = State.alerts.filter(a => a.id !== alertId);
    // WS-B (2026-07-26): repaint only the two alert surfaces in place.
    // The old full-app rebuild refetched the whole home snapshot and
    // reset scroll to dismiss one row. Both renderers self-gate.
    renderDangerBanner();
    renderAlertsFeed();
    if (State.notifsOpen) renderHeader();
  } catch (e) {
    showToast('Could not dismiss');
  }
}

// 2026-06-11: the header bell used to show ONLY ephemeral dashboard alerts
// plus whatever arrived live over the WS during the session — it never read
// the persistent `notifications` table (task assignments, integrity
// escalations, approvals), and nothing ever got marked read so the badge
// never cleared. loadNotifications() pulls the real per-user feed; live
// WS pushes are preserved and de-duped by id.
const _eonNotifSeen = new Set();
let _eonNotifSeeded = false;

// ── EON sound cues (UI wave 2, 2026-06-12) ──────────────────
// Synthesized Web Audio only — NO audio files. Four cues:
//   assign — a task was assigned to me (short soft blip)
//   points — points awarded / target hit (warmer two-note rise)
//   ping   — EON/operator ping on a task: mention or comment (attention triad)
//   verify — new verify-queue item for the owner
// Master toggle persists in localStorage (default ON), surfaced as a
// speaker icon in the Task OS + People OS headers. While the tab is
// hidden, cues queue (deduped, max 2) and play on return — no blasting
// a backgrounded phone. navigator.vibrate(50) is the fallback when
// WebAudio can't run. Volume is fixed low (0.15) — ambient, not alarm.
const EonSound = (() => {
  const KEY = 'eon.sound';
  const VOL = 0.15;
  const CUES = {   // [freq Hz, offset s, duration s] per note
    assign: [[880, 0, 0.09]],
    points: [[523.25, 0, 0.12], [783.99, 0.13, 0.2]],
    ping:   [[659.25, 0, 0.1], [830.61, 0.12, 0.1], [987.77, 0.24, 0.18]],
    verify: [[440, 0, 0.09], [587.33, 0.11, 0.13]],
    // Unmissable tier (operator 2026-07-23: "use a sound i want the
    // notification to be unmissable") — longer, louder, doorbell-shaped.
    chat:   [[659.25, 0, 0.18], [523.25, 0.2, 0.18], [783.99, 0.42, 0.34]],
    alert:  [[880, 0, 0.16], [1108.73, 0.18, 0.16], [880, 0.36, 0.3]],
  };
  // Alert-tier cues play ~3× the ambient volume + vibrate on mobile.
  const LOUD = { chat: 0.5, alert: 0.5 };
  let actx = null;
  const queued = [];
  const enabled = () => localStorage.getItem(KEY) !== 'off';
  function _play(name) {
    const notes = CUES[name];
    if (!notes) return;
    try {
      actx = actx || new (window.AudioContext || window.webkitAudioContext)();
      if (actx.state === 'suspended') actx.resume();
      const t0 = actx.currentTime + 0.02;
      const vol = LOUD[name] || VOL;
      notes.forEach(([f, at, dur]) => {
        const o = actx.createOscillator(), g = actx.createGain();
        o.type = 'sine'; o.frequency.value = f;
        // gain envelope — fast attack, exponential release, no clicks
        g.gain.setValueAtTime(0.0001, t0 + at);
        g.gain.exponentialRampToValueAtTime(vol, t0 + at + 0.015);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + dur);
        o.connect(g).connect(actx.destination);
        o.start(t0 + at); o.stop(t0 + at + dur + 0.05);
      });
      if (LOUD[name] && navigator.vibrate) navigator.vibrate([180, 90, 180]);
    } catch (_) {
      try { if (navigator.vibrate) navigator.vibrate(50); } catch (_) { /* no-op */ }
    }
  }
  function play(name, urgent) {
    if (!enabled()) return;
    // Urgent cues ring even while the tab is hidden (screen-off phone on the
    // counter, backgrounded tab) — a queued-for-later chirp is missable.
    if (document.hidden && !urgent) { if (!queued.includes(name)) queued.push(name); return; }
    _play(name);
  }
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    const q = queued.splice(0);
    if (!enabled()) return;
    q.slice(0, 2).forEach((n, i) => setTimeout(() => _play(n), i * 450));
  });
  function toggle() { localStorage.setItem(KEY, enabled() ? 'off' : 'on'); return enabled(); }
  return { play, enabled, toggle };
})();

// Tab-title flash — alternates the title with the alert text until the
// rep actually looks (tab visible + focused). One interval app-wide.
let _eonFlashTimer = null, _eonFlashBase = null;
function eonTitleFlash(msg) {
  if (_eonFlashTimer) { clearInterval(_eonFlashTimer); _eonFlashTimer = null; }
  if (_eonFlashBase == null) _eonFlashBase = document.title;
  let on = false;
  _eonFlashTimer = setInterval(() => {
    if (!document.hidden && document.hasFocus()) {
      clearInterval(_eonFlashTimer); _eonFlashTimer = null;
      document.title = _eonFlashBase; _eonFlashBase = null; return;
    }
    on = !on;
    document.title = on ? msg : _eonFlashBase;
  }, 1100);
}

// OS-level banner when the tab is hidden — reaches the rep even on another
// app/window (desktop; iOS PWA falls back to the sound + title flash).
// Permission is requested lazily from a user gesture (eonNotifyAsk).
function eonOsNotify(title, body) {
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    if (!document.hidden) return;   // visible tab: the in-app cue covers it
    const n = new Notification(title, { body: body || '', tag: 'eon-alert' });
    n.onclick = () => { try { window.focus(); n.close(); } catch (_) {} };
  } catch (_) { /* sandboxed / unsupported */ }
}
function eonNotifyAsk() {
  try {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default')
      Notification.requestPermission()
        .then(p => { if (p === 'granted') eonPushSync(); })
        .catch(() => {});
    else if (Notification.permission === 'granted') eonPushSync();
  } catch (_) { /* no-op */ }
}

// Web Push (2026-07-24): the only cue that reaches a LOCKED phone. iOS
// has no Notification constructor, no vibration API, and suspends page
// JS in background — the in-tab chime/banner can't ring a pocketed rep.
// Once permission is granted this subscribes the device; the server
// pushes through APNs/FCM and sw.js shows the OS banner, so the PHONE
// supplies the sound and vibration. iOS needs the app installed to the
// home screen (the /meet-eon PIN-IT flow reps already ran).
let _eonPushSynced = false;
async function eonPushSync() {
  try {
    if (_eonPushSynced) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const cfg = await api('/api/push/config');
      if (!cfg || !cfg.public_key) return;
      const pad = '='.repeat((4 - cfg.public_key.length % 4) % 4);
      const raw = atob((cfg.public_key + pad).replace(/-/g, '+').replace(/_/g, '/'));
      const key = Uint8Array.from(raw, c => c.charCodeAt(0));
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
    }
    await api('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ subscription: sub.toJSON(), ua: navigator.userAgent }),
    });
    _eonPushSynced = true;
  } catch (_) { /* not installed / denied / unsupported — in-tab cues remain */ }
}

// notification kind → cue. Server-side, notification rows are already
// addressed to ONE recipient_user_id — anything that reaches this client
// is for the signed-in user, so no extra relevance filtering needed.
// task_assigned is UNMISSABLE (2026-07-23): alert-tier sound + title
// flash + OS banner, not a background chirp.
function eonSoundForNotif(kind, n) {
  if (kind === 'task_assigned') {
    EonSound.play('alert', true);
    eonTitleFlash('● Task assigned');
    eonOsNotify('Task assigned', (n && (n.title || n.body)) || 'A task was just assigned to you.');
  }
  else if (kind === 'task_done') EonSound.play('verify');
  else if (kind === 'task_mention' || kind === 'task_comment') EonSound.play('ping');
}

// pulse-delta cue — shared by the Task OS + People OS loaders (the app's
// existing detection paths; no new transports). My ring points rising =
// points were awarded to ME; a growing rating queue = verify work for the
// lead who is looking at it.
let _eonPulsePrev = null;
function eonSoundOnPulse(p) {
  if (!p || typeof p !== 'object') return;
  const prev = _eonPulsePrev;
  _eonPulsePrev = { ring_points: p.ring?.points ?? null, rating_queue: p.rating_queue ?? null };
  if (!prev) return;   // first observation seeds the baseline silently
  if (p.ring?.points != null && prev.ring_points != null
      && p.ring.points > prev.ring_points) EonSound.play('points');
  if (p.rating_queue != null && prev.rating_queue != null
      && p.rating_queue > prev.rating_queue && posIsLead()) EonSound.play('verify');
}

// speaker toggle button (Task OS + People OS headers share this)
function eonSoundIcon() {
  return EonSound.enabled()
    ? `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M11 5 6 9H3v6h3l5 4V5z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M18 6a8.5 8.5 0 0 1 0 12"/></svg>`
    : `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M11 5 6 9H3v6h3l5 4V5z"/><path d="M16 9l5 6M21 9l-5 6"/></svg>`;
}
function bindSoundToggle(id) {
  const b = document.getElementById(id);
  if (!b) return;
  const paint = () => { b.innerHTML = eonSoundIcon(); b.title = EonSound.enabled() ? 'Sounds on' : 'Sounds off'; };
  paint();
  b.addEventListener('click', () => {
    const on = EonSound.toggle();
    paint();
    if (on) EonSound.play('assign');   // audible confirmation it's back on
  });
}

async function loadNotifications() {
  try {
    const r = await api('/api/notifications?limit=30');
    const rows = (r && r.notifications) || [];
    // Preserve optimistic read flips: if a refresh lands while the server
    // hasn't persisted markNotifsRead yet, incoming unread copies of rows we
    // already flipped locally must not resurrect the badge.
    const prevRead = {};
    (State.notifs || []).forEach(n => { if (n.id && n.read_at) prevRead[n.id] = n.read_at; });
    rows.forEach(n => { if (n.id && !n.read_at && prevRead[n.id]) n.read_at = prevRead[n.id]; });
    const ids = new Set(rows.map(n => n.id).filter(Boolean));
    const liveOnly = (State.notifs || []).filter(
      n => n._live && !(n.id && ids.has(n.id))
    );
    State.notifs = [...liveOnly, ...rows];
    // Sound on genuinely-new unread rows only. The first poll after app
    // load seeds the seen-set silently — a stale badge must not chirp.
    rows.forEach(n => {
      if (!n.id || _eonNotifSeen.has(n.id)) return;
      _eonNotifSeen.add(n.id);
      if (_eonNotifSeeded && !n.read_at) eonSoundForNotif(n.kind, n);
    });
    _eonNotifSeeded = true;
    State.notifsError = null;
  } catch (e) {
    // A transient staff-token 401 heals itself on the next tick — never
    // paint the error face for it (Opus vet).
    if (e && e.transient) return;
    // Keep whatever rows we already have, but carry the truth — the
    // panel renders "couldn't load" / a staleness strip, never "All
    // clear" (audit 2026-08-07).
    State.notifsError = (e && e.message) || 'feed unreachable';
  }
}

// Mark every unread notification read (clears the badge). Optimistic: flips
// state locally first, then persists with ONE read-all call (was up to 30
// parallel single-row POSTs). WS-pushed rows carry real ids too — they're
// covered by the same server-side sweep, so no _live exclusion.
async function markNotifsRead() {
  const nowIso = new Date().toISOString();
  const hadUnread = (State.notifs || []).some(n => !n.read_at);
  (State.notifs || []).forEach(n => { if (!n.read_at) n.read_at = nowIso; });
  if (hadUnread) renderHeader();
  if (!hadUnread) return;
  try {
    await api('/api/notifications/read_all', { method: 'POST' });
  } catch (e) {
    // Fallback for a not-yet-deployed backend: per-row best-effort —
    // UNREAD rows only (Opus vet M-5: marking already-read rows re-fanned
    // ~90 queries into the same struggling DB that just failed read_all).
    const ids = (State.notifs || []).filter(n => n.id && (!n.read_at || n.read_at === nowIso)).map(n => n.id);
    await Promise.allSettled(
      ids.map(id => api(`/api/notifications/${id}/read`, { method: 'POST' }).catch(() => {}))
    );
  }
}

// ──────────────────────────────────────────────────────────
// Home

const HOME_PERIODS = [
  { key: 'today',      label: 'Today' },
  { key: 'last_7d',    label: '7d' },
  { key: 'last_30d',   label: '30d' },
  { key: 'this_month', label: 'MTD' },
  { key: 'custom',     label: 'Custom' },
];

// ── HOUSE floor 4 · THE FRONT DESK (WS-E, 2026-08-07) ────────────────
// The operator home page as the building's front desk. Every branch below
// is gated on this one predicate; with any other skin the page renders
// byte-identical to what shipped before (ci/house_floors.json floor 4 +
// scripts/verify_house_floor.py enforce it).
const deskHouse = () => document.documentElement.getAttribute('data-theme') === 'house';

function renderHome() {
  const period = State.homePeriod || 'last_7d';
  const isCustom = typeof period === 'string' && period.startsWith('custom:');
  const activeKey = isCustom ? 'custom' : period;
  const customLabel = isCustom ? _formatCustomChipLabel(period) : 'Custom';
  return `
    <div id="home-content" class="mk-ads3 mk-home3">
      ${((State.member?.base_role || State.member?.role) === 'owner' && !State.impersonating) ? '<a class="ascent-entry" href="/ascent"><span>ASCENT</span><strong>Your growth, put to work.</strong><span>Open your plan ↗</span></a>' : ''}
      ${((State.member?.base_role || State.member?.role) === 'owner' && !State.impersonating) ? '<a class="penthouse-entry" href="/me"><span>PENTHOUSE</span><strong>Your day, your goals, your guide.</strong><span>Open</span></a>' : ''}
      ${((State.member?.base_role || State.member?.role) === 'owner' && !State.impersonating) ? '<a class="ascent-entry" href="/engine/experiments"><span>EXPERIMENTS</span><strong>What EON is testing. What the evidence says.</strong><span>Follow the tests</span></a>' : ''}
      <!-- WS-B (2026-07-26): the five async banners used to land one by
           one, shoving the page down up to five times while the operator
           read. Both clusters start hidden; bindHome reveals them in ONE
           paint after all five fetches settle (4s safety timeout). -->
      <div id="home-banners-a" hidden>
        <div id="next-tap-slot"></div>
        <div id="freshness-slot"></div>
        <div id="memory-review-slot"></div>
        <div id="rec-summary-tile"></div>
      </div>
      <div class="mh-topbar">
        <span class="a3-brand">CRUX<span class="a3-sub">Command</span></span>
        <span class="mh-topbar-spacer"></span>
        <span class="live-stamp" id="live-stamp"></span>
        <span class="a3-live"><span class="a3-live-dot"></span>Live</span>
      </div>
      <div id="home-banners-b" hidden>
        <div id="voice-briefing-slot"></div>
        <div id="digest-slot"></div>
      </div>
      <div class="date-chips" id="date-chips">
        ${HOME_PERIODS.map(p => {
          const label = p.key === 'custom' ? customLabel : p.label;
          return `
          <button class="chip ${p.key === activeKey ? 'chip-active' : ''}" data-period="${p.key}">
            ${esc(label)}
          </button>`;
        }).join('')}
      </div>
      <div id="hero-slot">
        <div class="mh-hero">
          <div class="mh-hero-top">
            <div class="mh-hero-label">Marketing efficiency</div>
          </div>
          <div class="mh-hero-fig"><span class="mh-skel" style="display:inline-block;width:140px;height:54px;border-radius:12px;"></span></div>
        </div>
      </div>
      <!-- HOUSE floor 4 (WS-E): the tills + the ticker mount here, directly
           under the hero, because 90% of CRUX revenue walks through a store
           door. Stays EMPTY and unstyled under Classic/Light/Flow. -->
      <div id="house-desk-slot"></div>
      <!-- WS-B: chart (230px) and target (~190px) used to pop in with no
           reserved space — ~420px of downward shove after first paint. -->
      <div id="chart-slot"><div class="mh-skel" style="height:230px;border-radius:16px;"></div></div>
      <div id="target-slot"><div class="mh-skel" style="height:190px;border-radius:16px;"></div></div>
      <div id="pinned-slot"></div>
      <div id="tiles-slot">
        ${[1,2,3,4].map(() => `<div class="mh-skel" style="height: 150px;"></div>`).join('')}
      </div>
    </div>`;
}

// Render a compact human label for a 'custom:YYYY-MM-DD:YYYY-MM-DD' period.
// Examples: "Apr 1 – Apr 28", "Mar 1 – Apr 30", "2025-12-1 – 2026-2-15"
// v3.0p23: label for the length-matched comparison period shown
// next to the net hero on the Money page. Keeps the "vs ..."
// copy meaningful — e.g. "vs ₦26M same days last month" rather
// than the previous misleading "vs ₦55M last month" (which was
// full prior month compared against MTD).
function _compareLabelFor(currentPeriod) {
  if (currentPeriod === 'this_month' || currentPeriod === 'mtd') {
    return 'same days last month';
  }
  if (currentPeriod === 'last_7d')  return 'prior 7 days';
  if (currentPeriod === 'last_30d') return 'prior 30 days';
  if (currentPeriod === 'last_90d') return 'prior 90 days';
  return 'last month';
}


function _formatCustomChipLabel(period) {
  try {
    const parts = period.split(':');
    if (parts.length !== 3) return 'Custom';
    const start = new Date(parts[1] + 'T00:00:00');
    const end = new Date(parts[2] + 'T00:00:00');
    if (isNaN(start) || isNaN(end)) return 'Custom';
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${fmt(start)} – ${fmt(end)}`;
  } catch (_) {
    return 'Custom';
  }
}

// Open a modal that lets user pick a date range. On apply, sets
// State.homePeriod = 'custom:YYYY-MM-DD:YYYY-MM-DD' and triggers refetch.
function _openCustomRangeModal(onApply) {
  // v3.0p21 9xx: optional callback. When provided, fires
  // onApply(customPeriod) on Apply instead of the default home-period
  // mutation. Lets the Money page reuse the modal.
  //
  // Pre-fill: if already custom, use those dates; else default to last 14d.
  // Source of pre-fill = the active period for whichever view is calling.
  const activePeriod = onApply
    ? (State.moneyPeriod || State.homePeriod || '')
    : (State.homePeriod || '');
  let preStart, preEnd;
  if (typeof activePeriod === 'string' && activePeriod.startsWith('custom:')) {
    const parts = activePeriod.split(':');
    if (parts.length === 3) {
      preStart = parts[1];
      preEnd = parts[2];
    }
  }
  if (!preStart || !preEnd) {
    const today = new Date();
    const fortnightAgo = new Date(today);
    fortnightAgo.setDate(today.getDate() - 14);
    const iso = (d) => d.toISOString().slice(0, 10);
    preStart = iso(fortnightAgo);
    preEnd = iso(today);
  }
  
  // Today (for max attribute on inputs — no future dates)
  const todayIso = mtxLagosToday();
  
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'custom-range-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:1000;display:flex;align-items:center;justify-content:center;padding:20px;';
  overlay.innerHTML = `
    <div style="background:var(--bg);border:1px solid var(--border);border-radius:14px;padding:20px;max-width:360px;width:100%;">
      <div style="font-weight:600;font-size:18px;margin-bottom:4px;">Custom date range</div>
      <div style="font-size:13px;color:var(--ink-soft);margin-bottom:16px;">Pick start and end dates. Inclusive.</div>
      
      <label style="display:block;font-size:12px;color:var(--ink-soft);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">From</label>
      <input type="date" id="custom-range-start" value="${preStart}" max="${todayIso}"
             style="width:100%;padding:10px 12px;background:var(--bg-sub);color:var(--ink);border:1px solid var(--border);border-radius:8px;font-size:15px;margin-bottom:14px;">
      
      <label style="display:block;font-size:12px;color:var(--ink-soft);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">To</label>
      <input type="date" id="custom-range-end" value="${preEnd}" max="${todayIso}"
             style="width:100%;padding:10px 12px;background:var(--bg-sub);color:var(--ink);border:1px solid var(--border);border-radius:8px;font-size:15px;margin-bottom:8px;">
      
      <div id="custom-range-error" style="font-size:12px;color:var(--danger);min-height:18px;margin-bottom:8px;"></div>
      
      <div style="display:flex;gap:8px;margin-top:6px;">
        <button id="custom-range-cancel" type="button"
                style="flex:1;padding:12px;background:transparent;color:var(--ink-soft);border:1px solid var(--border);border-radius:8px;font-size:14px;cursor:pointer;">
          Cancel
        </button>
        <button id="custom-range-apply" type="button"
                style="flex:1;padding:12px;background:var(--accent);color:var(--bg);border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;">
          Apply
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  
  const close = () => overlay.remove();
  
  // Tap outside dialog to close
  eonBackdropClose(overlay, close);
  
  document.getElementById('custom-range-cancel').addEventListener('click', close);
  
  document.getElementById('custom-range-apply').addEventListener('click', async () => {
    const start = document.getElementById('custom-range-start').value;
    const end = document.getElementById('custom-range-end').value;
    const errorEl = document.getElementById('custom-range-error');
    
    if (!start || !end) {
      errorEl.textContent = 'Both dates required.';
      return;
    }
    if (start > end) {
      errorEl.textContent = 'Start date must be on or before end date.';
      return;
    }
    if (start > todayIso || end > todayIso) {
      errorEl.textContent = 'Dates cannot be in the future.';
      return;
    }
    
    // Set new period and trigger refetch
    const customPeriod = `custom:${start}:${end}`;
    close();
    if (typeof onApply === 'function') {
      onApply(customPeriod);
      return;
    }
    State.homePeriod = customPeriod;
    
    // Re-render the chip row with new active state + label
    const homeContent = document.getElementById('home-content');
    if (homeContent && homeContent.parentNode) {
      // Easier than partial rerender: trigger a full home rerender
      render();
    }
  });
}

// WS-B (2026-07-26): reveal both banner clusters in one paint — called
// once every loader settles (or by the 4s safety timeout so a hung
// fetch can't hide banners forever). Idempotent.
function _revealHomeBanners() {
  ['home-banners-a', 'home-banners-b'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.hidden = false;
  });
}

async function bindHome() {
  lw.loadWork();
  // WS-B: the five banner loaders fire in parallel exactly as before,
  // but their slots sit inside hidden clusters — allSettled below
  // reveals everything at once (one layout shove, not five).
  const bannerLoads = [];

  // HOME-DECISION-FIRST (2026-06-11): "since you last looked" strip.
  // Fires in parallel with everything else; never blocks the page;
  // failure = silently absent (no empty shell).
  bannerLoads.push(_loadHomeDigest());
  // THE NEXT TAP (2026-09-02): the one decision with the most money
  // behind it, first on the page. Owner only; absent on failure.
  bannerLoads.push(_loadNextTap());

  // EON Voice (2026-07-13): the 6am briefing, spoken — player card at
  // the top of home. Same contract as the digest: absent on failure.
  bannerLoads.push(_loadVoiceBriefing());

  // Freshness + memory-review banners (moved up from below the data
  // await, WS-B — they were always independent of the snapshot).
  bannerLoads.push(refreshFreshnessBanner());
  bannerLoads.push(refreshMemoryReviewBanner());

  // v3.0p18 9ll: recommendation-engine summary tile. Hits the /api/
  // recommendations endpoint and renders a one-tap card showing how
  // many critical / warn items are awaiting. Tapping opens the full
  // Recommendations view.
  bannerLoads.push((async () => {
    try {
      const current = lw.captureRead('home', false);
      const r = await api('/api/recommendations?limit=200');
      if (!current()) return;
      const items = (r && r.items) || [];
      lw.setSignals(Array.isArray(r?.items) ? r.items : null);
      if (items.length === 0) return;
      const crit = items.filter(x => x.severity === 'critical').length;
      const warn = items.filter(x => x.severity === 'warn').length;
      const pinned = items.filter(x => x.pinned).length;
      // declutter 2026-07-25: 80 rows of one scanner is ONE signal — say
      // both numbers so the count stops reading as 200 separate problems
      const signals = new Set(items.map(x => x.anomaly_type)).size;
      const slot = document.getElementById('rec-summary-tile');
      if (!slot) return;
      slot.innerHTML = `
        <button class="card" id="rec-summary-btn" type="button" data-go="recommendations"
          style="display:flex;align-items:center;gap:12px;width:100%;text-align:left;padding:12px 14px;margin:0 0 14px;cursor:pointer;border:1px solid var(--line);">
          <div style="flex:1;">
            <div style="font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:var(--ink-soft);">EON Recommendations</div>
            <div style="font-size:14px;font-weight:600;margin-top:2px;">
              In the latest ${items.length} returned items: ${signals} signal${signals === 1 ? '' : 's'} · ${crit} critical · ${warn} warnings${pinned ? ` · ${pinned} pinned` : ''}
            </div>
          </div>
          <div style="font-size:20px;color:var(--ink-mute);">→</div>
        </button>`;
      // navigation rides the WS-A [data-go] document delegate
    } catch (_) { /* silent — tile is best-effort */ }
  })());

  // One reveal for all five banners — single layout shove (WS-B).
  Promise.allSettled(bannerLoads).then(_revealHomeBanners);
  setTimeout(_revealHomeBanners, 4000);

  // Bind date chips first — they trigger refetch
  document.querySelectorAll('#date-chips .chip').forEach(btn => {
    btn.addEventListener('click', async () => {
      const periodKey = btn.dataset.period;
      
      // Custom chip: open date range modal
      if (periodKey === 'custom') {
        _openCustomRangeModal();
        return;
      }
      
      if (periodKey === State.homePeriod) return;
      State.homePeriod = periodKey;
      // Update chip active state
      document.querySelectorAll('#date-chips .chip').forEach(b => {
        b.classList.toggle('chip-active', b.dataset.period === periodKey);
      });
      // Refetch + rerender all data sections
      await fetchAndRenderHomeData();
    });
  });

  await fetchAndRenderHomeData();

  // (freshness + memory-review moved into the WS-B banner batch above)

  // Pins load independently
  try {
    const data = await api(`/api/tenants/${State.tenant.slug}/pins?limit=8`);
    renderPinnedSection(data.pins || []);
  } catch (_) { /* ignore */ }

  // Alerts (independent of period — they're tenant-wide)
  await loadAlerts();
  // Persistent per-user notifications feed (task assignments, escalations,
  // approvals) — populates the header bell + unread badge on load, not just
  // from live WS pushes. Non-blocking failure tolerated inside the helper.
  loadNotifications().then(() => renderHeader());
  // Devices that already granted notification permission re-sync their
  // push subscription on load (idempotent; new devices subscribe from
  // the eonNotifyAsk tap).
  eonPushSync();
  // Audit 2026-08-07: the owner console never ASKED for permission — only
  // the CS desk and Staff OS did, so notify_owners pushes reached zero
  // devices unless the operator happened to open the CS desk once. Same
  // one-shot first-tap pattern those surfaces use (iOS gesture law).
  if (!bindHome._pushAsk) {
    bindHome._pushAsk = true;
    document.body.addEventListener('click', () => eonNotifyAsk(), { once: true });
  }
  renderDangerBanner();
  renderAlertsFeed();
}

async function fetchAndRenderHomeData() {
  const period = State.homePeriod || 'last_7d';
  const current = lw.captureRead('home');
  lw.homeLoading(period);
  try {
    const data = await api(`/api/dashboard/snapshot?period=${encodeURIComponent(period)}`);
    if (!current()) return;
    renderHomeWithData(data);
  } catch (e) {
    if (!current()) return;
    if (lw.active()) lw.homeError();
    else renderHomeEmpty(e);
  }
}

// ── "Since you last looked" digest ──────────────────────────
// Max 3 items, severity-then-recency, each deep-linking to the owning
// page. Renders NOTHING when there is nothing to say — no empty shell.
// THE NEXT TAP (2026-09-02). Read live that morning: 233 approved cards
// never built, 1 piece of advice in 2,887 acted on, a median of 278 hours
// to a decision — and no screen ever put ONE decision in front of the
// operator. This card does: the open decision with the most money behind
// it, four answers, one tap. A card leaves when answered, never when it
// expires. Owner only; silently absent on any failure.
async function _loadNextTap() {
  const slot = document.getElementById('next-tap-slot');
  if (!slot) return;
  const role = (State.member && (State.member.base_role || State.member.role)) || '';
  if (role !== 'owner' || State.impersonating) { slot.innerHTML = ''; return; }
  let data = null;
  try { data = await api('/api/next-tap'); }
  catch (_) { slot.innerHTML = ''; return; }
  _renderNextTap(slot, data);
}

function _renderNextTap(slot, data) {
  const c = data && data.card;
  const q = (data && data.queue) || {};
  if (!c) {
    slot.innerHTML = q.held
      ? `<div class="card" style="padding:12px 14px;margin:0 0 14px;font-size:13px;color:var(--ink-soft);">Nothing needs your decision today. ${q.held} wait${q.held === 1 ? 's' : ''} for the date you set.</div>`
      : '';
    return;
  }
  // a figure above the plausibility band is not shown as money: the ledger
  // said it, but the ledger is not always right, and a wrong number at the
  // top of the page is worse than no number
  const money = (c.money_ngn && !c.figure_suspect) ? fmtNGN(c.money_ngn) : '';
  const basis = c.money_basis && c.money_basis !== 'money at stake' ? ` · ${c.money_basis}` : '';
  const hours = Number(c.age_hours || 0);
  const wait = hours >= 48 ? `${Math.round(hours / 24)} days waiting`
             : hours >= 1 ? `${Math.round(hours)} hours waiting` : 'new today';
  slot.innerHTML = `
    <div class="card" id="next-tap-card" style="padding:14px 16px;margin:0 0 14px;border:1px solid var(--accent);">
      <div style="font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:var(--accent);">The next tap · ${esc(c.kind_line || '')}</div>
      <div style="font-size:17px;font-weight:700;margin-top:4px;line-height:1.25;">${esc(c.title)}</div>
      ${money
        ? `<div style="font-family:var(--mono);font-size:22px;font-weight:700;margin-top:6px;">${esc(money)}<span style="font-size:12px;color:var(--ink-mute);font-weight:500;margin-left:8px;"> behind it${esc(basis)} · ${esc(wait)}</span></div>`
        : `<div style="font-size:12px;color:var(--ink-mute);margin-top:4px;">${esc(wait)}</div>`}
      ${c.why ? `<div style="font-size:13px;color:var(--ink-soft);margin-top:6px;">${esc(c.why)}</div>` : ''}
      ${c.figure_suspect ? `<div style="font-size:12px;color:var(--ink-mute);margin-top:6px;">The ledger puts ${esc(fmtNGN(c.money_ngn))} on this, which is too large to rank by until the figure is checked.</div>` : ''}
      ${c.ledger_figure_ngn && c.money_basis && c.money_basis.indexOf('lifetime') >= 0 ? `<div style="font-size:12px;color:var(--ink-mute);margin-top:6px;">The ledger's ${esc(fmtNGN(c.ledger_figure_ngn))} is one customer's lifetime spend; this ranks it as one in twenty of that.</div>` : ''}
      ${c.moves_money === false ? `<div style="font-size:12px;color:var(--ink-mute);margin-top:6px;">${esc(fmtNGN(c.money_ngn))} is ${esc(c.direction_words || 'a figure this decision does not move')} — so it is not what put this card here.</div>` : ''}
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:12px;">
        <button class="btn" type="button" data-nt="yes" style="min-height:44px;">Yes, do it</button>
        <button class="btn-ghost" type="button" data-nt="no" style="min-height:44px;">No</button>
        <button class="btn-ghost" type="button" data-nt="later" style="min-height:44px;">Ask me in 3 days</button>
        <button class="btn-ghost" type="button" data-nt="fence" style="min-height:44px;">Let EON decide within these limits</button>
      </div>
      <div style="font-size:11px;color:var(--ink-mute);margin-top:10px;font-family:var(--mono);">behind this card · ${Number(q.cards_unbuilt || 0)} approved builds · ${Number(q.cards_open || 0)} proposals · ${Number(q.advice_open || 0)} pieces of advice${q.held ? ` · ${Number(q.held)} held for a date` : ''} · a card leaves when answered, never when it expires</div>
    </div>`;
  slot.querySelectorAll('button[data-nt]').forEach((b) => {
    b.addEventListener('click', () => _answerNextTap(slot, c, b.dataset.nt));
  });
}

async function _answerNextTap(slot, c, ans, insist) {
  const btns = slot.querySelectorAll('button[data-nt]');
  btns.forEach((b) => { b.disabled = true; });
  let r = null;
  try {
    r = await api('/api/next-tap/answer', {
      method: 'POST', body: JSON.stringify({ kind: c.kind, ref_id: c.ref_id, answer: ans, insist: !!insist }),
    });
  } catch (_) { r = null; }
  if (!r || !r.ok) {
    showToast((r && r.message) || 'That answer did not go through. Try again in a moment.');
    btns.forEach((b) => { b.disabled = false; });
    // The value guard held it: its reason is on screen and the door stays
    // open — a second yes, marked as his insistence, goes through.
    if (r && r.error === 'value_guard' && r.can_insist && ans === 'yes') {
      const card = slot.querySelector('#next-tap-card');
      if (card && !card.querySelector('[data-nt-insist]')) {
        const row = card.querySelector('.btns') || card;
        const hold = document.createElement('div');
        hold.setAttribute('data-nt-insist', '1');
        hold.style.cssText = 'font-size:13px;color:var(--ink-soft);margin-top:10px;';
        hold.innerHTML = `${esc(r.message)} <button class="btn-ghost" type="button" data-nt-insist-btn style="min-height:44px;margin-left:8px;">Yes, I insist</button>`;
        row.insertAdjacentElement('afterend', hold);
        hold.querySelector('[data-nt-insist-btn]').addEventListener('click', () => _answerNextTap(slot, c, 'yes', true));
      }
    }
    return;
  }
  showToast(r.said || 'Recorded.');
  _loadNextTap();   // the next decision, if any
}

async function _loadHomeDigest() {
  const slot = document.getElementById('digest-slot');
  if (!slot) return;
  let items = [];
  try {
    const r = await api('/api/dashboard/digest');
    items = (r && r.items) || [];
  } catch (_) {
    slot.innerHTML = '';
    return; // silently absent on failure
  }
  items = items.slice(0, 3);
  if (!items.length) { slot.innerHTML = ''; return; }
  const sevChip = (s) => {
    const sev = String(s || '').toLowerCase();
    const cls = (sev === 'critical' || sev === 'danger') ? 'mh-chip-danger'
              : (sev === 'warning' || sev === 'warn') ? 'mh-chip-watch'
              : 'mh-chip-muted';
    return `<span class="mh-chip ${cls}">${esc(sev || 'info')}</span>`;
  };
  slot.innerHTML = `
    <div class="mh-digest">
      <div class="mh-digest-head">Since you last looked</div>
      ${items.map((it, i) => {
        const linked = !!(it.target_href || it.target_view);
        return `
        <div class="mh-digest-item ${linked ? 'is-link' : ''}"
             ${it.target_href ? `data-digest-href="${esc(it.target_href)}"` : ''}
             ${it.target_view ? `data-digest-view="${esc(it.target_view)}"` : ''}>
          ${sevChip(it.severity)}
          <span class="mh-digest-line">${esc(it.line || '')}</span>
          ${linked ? '<span class="mh-digest-arrow">&rarr;</span>' : ''}
        </div>`;
      }).join('')}
    </div>`;
  slot.querySelectorAll('.mh-digest-item.is-link').forEach(el => {
    el.addEventListener('click', () => {
      const href = el.dataset.digestHref;
      const view = el.dataset.digestView;
      if (href) { window.location.href = href; return; }
      if (view) { State.view = view; render(); }
    });
  });
}

// ── EON Voice: spoken morning briefing card (2026-07-13) ─────────────
// The Jarvis loop: play the 60-90s script EON rendered at 6am, then the
// three recommended moves as chips — tapping one opens Ask EON with the move
// in its box to talk through, nothing sent (26 Sep; typed chat only where
// Ask EON is not on the page).
// Signed audio URL is minted per load; the card never stores one.
let _vbAudioEl = null;

function _vbStopAudio() {
  if (_vbAudioEl) {
    try { _vbAudioEl.pause(); } catch (_) {}
    _vbAudioEl = null;
  }
}

function _homeBriefingAge(day, now = new Date()) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(day || ''))) return 'Date unavailable';
  const stamp = Date.parse(day + 'T00:00:00Z');
  if (!Number.isFinite(stamp) || new Date(stamp).toISOString().slice(0, 10) !== day) return 'Date unavailable';
  const today = new Intl.DateTimeFormat('en-CA', {timeZone:'Africa/Lagos',year:'numeric',month:'2-digit',day:'2-digit'}).format(now);
  const age = Math.floor((Date.parse(today + 'T00:00:00Z') - stamp) / 86400000);
  return age === 0 ? 'Today' : age > 0 ? `Older briefing · ${age} day${age === 1 ? '' : 's'} old` : 'Date is ahead of today';
}

async function _loadVoiceBriefing() {
  const slot = document.getElementById('voice-briefing-slot');
  if (!slot) return;
  let b = null;
  try { b = await api('/api/voice/briefing/latest'); }
  catch (_) { slot.innerHTML = ''; return; }  // 404 until first 6am render
  if (!b || !b.script) { slot.innerHTML = ''; return; }

  const dateLabel = (() => {
    try {
      return new Date(b.briefing_date + 'T00:00:00')
        .toLocaleDateString(undefined,
          { weekday: 'short', day: 'numeric', month: 'short' });
    } catch (_) { return b.briefing_date || ''; }
  })();
  const moves = Array.isArray(b.moves) ? b.moves.filter(m => m && m.label) : [];
  const hasAudio = !!b.audio_url;

  slot.innerHTML = `
    <div class="vb-card">
      <div class="vb-head">
        <span class="vb-title">Morning briefing — spoken</span>
        <span class="vb-date">${esc(dateLabel)} · ${esc(_homeBriefingAge(b.briefing_date))}</span>
      </div>
      <div class="vb-main">
        ${hasAudio ? `
        <button class="vb-play" id="vb-play" aria-label="Play briefing">
          <svg class="vb-ic-play" width="16" height="16" viewBox="0 0 24 24"
               fill="currentColor"><path d="M8 5v14l11-7z"></path></svg>
          <svg class="vb-ic-pause" width="16" height="16" viewBox="0 0 24 24"
               fill="currentColor" style="display:none">
            <path d="M6 5h4v14H6zM14 5h4v14h-4z"></path></svg>
        </button>` : ''}
        <div class="vb-meta">
          <div class="vb-line">${hasAudio
            ? 'EON has your rundown — tap to listen'
            : 'Audio unavailable for this briefing — transcript below'}</div>
          <div class="vb-progress"><i class="vb-progress-fill"></i></div>
        </div>
      </div>
      ${moves.length ? `
      <div class="vb-moves">
        ${moves.map(m => `
          <button class="vb-move" data-vb-ask="${esc(m.ask || m.label)}">
            ${esc(m.label)}</button>`).join('')}
      </div>` : ''}
      <button class="vb-script-toggle" id="vb-script-toggle">Transcript</button>
      <div class="vb-script" id="vb-script" hidden>${esc(b.script)}</div>
    </div>`;

  const playBtn = slot.querySelector('#vb-play');
  if (playBtn) {
    const fill = slot.querySelector('.vb-progress-fill');
    const icPlay = playBtn.querySelector('.vb-ic-play');
    const icPause = playBtn.querySelector('.vb-ic-pause');
    const setIcon = (playing) => {
      icPlay.style.display = playing ? 'none' : '';
      icPause.style.display = playing ? '' : 'none';
    };
    playBtn.addEventListener('click', () => {
      if (_vbAudioEl && !_vbAudioEl.paused) {
        _vbAudioEl.pause(); setIcon(false); return;
      }
      if (!_vbAudioEl) {
        _vbAudioEl = new Audio(b.audio_url);
        _vbAudioEl.addEventListener('timeupdate', () => {
          if (fill && _vbAudioEl && _vbAudioEl.duration > 0) {
            fill.style.transform =
              `scaleX(${_vbAudioEl.currentTime / _vbAudioEl.duration})`;
          }
        });
        _vbAudioEl.addEventListener('ended', () => setIcon(false));
        _vbAudioEl.addEventListener('error', () => {
          setIcon(false);
          showToast('Briefing audio failed to load');
        });
      }
      _vbAudioEl.play().then(() => setIcon(true)).catch(() => {
        showToast('Tap again to play');
      });
    });
  }

  slot.querySelectorAll('.vb-move').forEach(btn => {
    btn.addEventListener('click', () => {
      const ask = btn.dataset.vbAsk || '';
      if (!ask) return;
      _vbStopAudio();
      // Talk it through (26 Sep): Ask EON opens here with the move in its
      // box, nothing sent, live voice offered when ready. Only a page with no
      // Ask EON (it leaves the event unanswered) falls back to typed chat.
      if (!document.dispatchEvent(new CustomEvent('eon-ask-open',
        { detail: { text: ask, source: 'briefing' }, cancelable: true }))) return;
      State.view = 'chat';
      render();
      // sendChat(forcedText) — the starter-chip contract; composer may
      // not be mounted until after render.
      setTimeout(() => { try { sendChat(ask); } catch (_) {} }, 60);
    });
  });

  const tgl = slot.querySelector('#vb-script-toggle');
  const scr = slot.querySelector('#vb-script');
  if (tgl && scr) {
    tgl.addEventListener('click', () => { scr.hidden = !scr.hidden; });
  }
}

// ── Live refresh ────────────────────────────────────────────
// Keep the target bar (home) + money tiles current without a manual
// reload. One shared timer, visibility-aware (pauses when the tab is
// hidden so we don't burn the backend), in-place updates only (no full
// re-render → no scroll jump; data-ask buttons use delegated listeners
// so replacing innerHTML doesn't leak handlers). Numbers tick as the
// 15-min eon_sync data + cache recompute land.
const LIVE_REFRESH_MS = 90000;
let _liveRefreshHandle = null;
// WS-B (2026-07-26): fingerprint of the target payload — the tick only
// touches the DOM when a number actually moved, and when it does the
// swap happens under .tick-still (entry slide + 1.2s ring redraw +
// pace-tick fade suppressed; those replayed every 90s while reading).
let _lastTargetFp = '';
function _targetFp(t) {
  if (!t) return 'none';
  return JSON.stringify([
    t.available, t.no_target, t.progress_pct, t.expected_progress_pct,
    t.actual_value, t.target_value, t.projected_value, t.pace_diff_pct,
    t.run_rate_daily_ngn, t.needed_daily_ngn, t.gap_to_target_ngn,
    t.days_elapsed, t.days_left_incl_today, t.live_today_revenue_ngn,
    t.live_today_orders,
  ]);
}

async function liveRefreshTick() {
  if (document.hidden) return;
  // Audit 2026-08-07: the bell only refreshed on home-load and bell-tap —
  // an operator parked on Money/Chat/Inventory kept a frozen badge (the
  // WS push branch is structurally dead: ws.push_notification has zero
  // callers). Piggyback the existing tick at a ~90s cadence.
  try {
    liveRefreshTick._notifAt = liveRefreshTick._notifAt || 0;
    // >= 85000, not > 90000: the tick interval IS 90000ms, so a strict
    // 90000 guard skipped every other tick (180s cadence). And never
    // repaint the header under an open panel OR menu — renderHeader
    // replays the menu sheet's entrance and resets its scroll (Opus vet
    // HIGH-4, the tick's own no-replay contract).
    if (Date.now() - liveRefreshTick._notifAt >= 85000
        && !document.querySelector('.notif-panel')
        && !State.menuOpen && !document.querySelector('.menu-panel')) {
      liveRefreshTick._notifAt = Date.now();
      loadNotifications().then(() => renderHeader()).catch(() => {});
    }
  } catch (_e) {}
  try {
    if (State.view === 'home') {
      const period = State.homePeriod || 'last_7d';
      const current = lw.captureRead('home');
      const data = await api(`/api/dashboard/snapshot?period=${encodeURIComponent(period)}`);
      if (!current()) return;
      lw.updateHome(data, { tick: true });
      const slot = document.getElementById('target-slot');
      if (slot && data && data.target) {
        const fp = _targetFp(data.target);
        if (fp !== _lastTargetFp) {
          slot.classList.add('tick-still');
          slot.innerHTML = renderTargetTile(data.target);
          _lastTargetFp = fp;
          _bandSettle(slot);
        } else {
          // Numbers unchanged — refresh only the as-of text in place.
          const asof = slot.querySelector('.mh-target-asof');
          if (asof && data.target.as_of_iso) {
            try {
              asof.textContent = 'Updated ' + new Date(data.target.as_of_iso)
                .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            } catch (_) { /* keep old stamp */ }
          }
        }
      }
      _frontDeskTick(data);
      const stamp = document.getElementById('live-stamp');
      if (stamp) stamp.textContent = 'live · updated ' +
        new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (State.view === 'money') {
      // PR MONEY-COCKPIT: numbers-only tick. The old call re-mounted
      // the insights grid + charts via innerHTML every 90s, replaying
      // the enter animations — exactly what the contract above rules
      // out. refreshMoneyTick patches textContent on existing nodes.
      if (typeof refreshMoneyTick === 'function') await refreshMoneyTick();
      const stamp = document.getElementById('live-stamp');
      if (stamp) stamp.textContent = 'live · updated ' +
        new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  } catch (_) { /* transient — next tick retries */ }
}
function startLiveRefresh() {
  if (_liveRefreshHandle) return;          // single timer for the app's life
  _liveRefreshHandle = setInterval(liveRefreshTick, LIVE_REFRESH_MS);
}

function renderDangerBanner() {
  // Remove any existing banner
  $('#alert-banner')?.remove();
  // PR CRITICAL-HOTFIX-BUNDLE: gate to Home only. The previous
  // `container = $('.home') || $('main') || document.body` fallback
  // meant when the user is on Chat / Money / Inventory / Ads /
  // Tasks, $('main') is THAT view's container — and the alert
  // banner spawned inside it. Operator screenshot showed
  // "Whale order ₦1.68M" persistently popping up at the top of
  // the chat tab. Now: bail early if .home isn't mounted.
  const home = $('.home');
  if (!home) return;
  const danger = (State.alerts || []).filter(a => a.severity === 'danger');
  if (danger.length === 0) return;
  const top = danger[0];
  const banner = elFromHTML(`
    <div id="alert-banner" style="background:#d23131;color:white;padding:12px 16px;margin:0 0 12px;border-radius:8px;display:flex;justify-content:space-between;align-items:flex-start;gap:12px">
      <div style="flex:1;min-width:0">
        <div style="font-weight:600;font-size:14px;line-height:1.3;margin-bottom:2px">${esc(top.title)}</div>
        <div style="font-size:12px;opacity:0.95;line-height:1.4">${esc(top.body)}${danger.length > 1 ? ` (+${danger.length - 1} more)` : ''}</div>
      </div>
      <button data-alert-id="${esc(top.id)}" class="alert-banner-dismiss" aria-label="Dismiss" style="background:rgba(255,255,255,0.2);border:none;color:white;width:28px;height:28px;border-radius:14px;cursor:pointer;font-size:16px;line-height:1;flex-shrink:0">×</button>
    </div>`);
  // PR CRITICAL-HOTFIX-BUNDLE: home-only after the early-return above
  home.insertBefore(banner, home.firstChild);
  banner.querySelector('.alert-banner-dismiss').addEventListener('click', (e) => {
    e.stopPropagation();
    dismissAlert(top.id);
  });
}

function renderAlertsFeed() {
  // Remove any existing feed
  $('#alerts-feed')?.remove();
  // PR CRITICAL-HOTFIX-BUNDLE: gate to Home only — same bug as
  // renderDangerBanner above. The feed was spilling into Chat /
  // Money / Inventory views via the $('main') fallback.
  const home = $('.home');
  if (!home) return;
  // Declutter 2026-07-25: identical titles collapse to the NEWEST row
  // (live: 'Ad pool has category holes' x3) — clearing one clears its
  // hidden twins too, so the feed can never re-grow the same line.
  const seen = new Map();
  (State.alerts || []).filter(a => a.severity !== 'danger').forEach(a => {
    const k = (a.title || '').trim();
    if (!seen.has(k)) seen.set(k, { ...a, _dupes: [] });
    else seen.get(k)._dupes.push(a.id);
  });
  const alerts = [...seen.values()];
  if (alerts.length === 0) return;
  const sevColor = (s) => s === 'warn' ? '#d49328' : 'var(--ink-mute, #7a7a7a)';
  const ago = (iso) => {
    const ms = Date.now() - new Date(iso).getTime();
    const h = Math.floor(ms / 3.6e6);
    if (!isFinite(h) || h < 0) return '';
    if (h < 1) return 'just now';
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return d < 14 ? `${d}d ago` : `${Math.floor(d / 7)}w ago`;
  };
  const feed = elFromHTML(`
    <div id="alerts-feed" class="alerts-feed" style="margin-top:24px">
      <div class="section-h" style="display:flex;align-items:center;gap:10px;">
        <h2 style="flex:1;">Alerts</h2>
        <span class="meta">${alerts.length}</span>
        <button class="chip" type="button" id="alerts-clear-all" title="Clear every non-danger alert — danger stays until resolved.">Clear all</button>
      </div>
      <div class="alerts-list">
        ${alerts.slice(0, 10).map(a => `
          <div class="card alert-card" data-alert-id="${esc(a.id)}" style="display:flex;gap:10px;align-items:flex-start;padding:12px">
            <span style="display:inline-block;width:6px;align-self:stretch;background:${sevColor(a.severity)};border-radius:3px;flex-shrink:0"></span>
            <div style="flex:1;min-width:0">
              <div style="font-weight:600;font-size:14px;line-height:1.3;margin-bottom:2px">${esc(a.title)}${a._dupes.length ? ` <span class="meta">×${a._dupes.length + 1}</span>` : ''}</div>
              <div style="font-size:12px;color:var(--ink-soft,#666);line-height:1.4">${esc(a.body)}</div>
              <div style="font-size:11px;color:var(--ink-mute,#999);margin-top:3px">${ago(a.created_at)}</div>
            </div>
            <button class="alert-feed-dismiss" data-alert-id="${esc(a.id)}" style="background:none;border:none;color:#aaa;cursor:pointer;padding:0 4px;font-size:18px;line-height:1;flex-shrink:0">×</button>
          </div>
        `).join('')}
      </div>
    </div>`);
  // PR CRITICAL-HOTFIX-BUNDLE: home-only after the early-return above
  home.appendChild(feed);
  feed.querySelectorAll('.alert-feed-dismiss').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const a = alerts.find(x => x.id === btn.dataset.alertId);
      dismissAlert(btn.dataset.alertId);
      (a?._dupes || []).forEach(id => dismissAlert(id));   // twins go too
    });
  });
  feed.querySelector('#alerts-clear-all')?.addEventListener('click', async (e) => {
    const btn = e.target; btn.disabled = true; btn.textContent = 'Clearing…';
    const ids = alerts.flatMap(a => [a.id, ...a._dupes]);
    await Promise.allSettled(ids.map(id =>
      api(`/api/alerts/${id}/acknowledge`, { method: 'POST' })));
    await loadAlerts();
    renderAlertsFeed();
  });
}

function _homePinnedPreview(answer) {
  // Parse the existing escaped markdown in an inert template. Flatten BEFORE
  // shortening, so a cut through **bold** never exposes raw formatting or HTML.
  const template = document.createElement('template');
  template.innerHTML = renderMarkdownToHtml(String(answer || ''));
  template.content.querySelectorAll('p,li,h1,h2,h3,h4,pre,tr,br').forEach(node => node.append(' '));
  return esc(shorten(template.content.textContent.replace(/\s+/g, ' ').trim(), 160));
}

function renderPinnedSection(pins) {
  const slot = $('#pinned-slot');
  if (!slot) return;
  if (!pins || pins.length === 0) {
    slot.innerHTML = '';
    return;
  }
  slot.innerHTML = `
    <div class="section-h">
      <h2>Pinned</h2>
      <span class="meta">${pins.length}</span>
    </div>
    <div class="pinned-list">
      ${pins.map(p => `
        <div class="pinned-card" data-pin-id="${esc(p.id)}">
          <div class="pinned-q">${esc(p.label || shorten(p.question, 80))}</div>
          <div class="pinned-a">${_homePinnedPreview(p.answer)}</div>
          <div class="pinned-actions">
            <button class="chip" onclick="goChat('${esc(sosJs(p.question))}')">Ask again</button>
            <button class="chip danger-chip" data-unpin="${esc(p.id)}">Unpin</button>
          </div>
        </div>
      `).join('')}
    </div>
  `;
  // Bind unpin
  slot.querySelectorAll('[data-unpin]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const pinId = btn.dataset.unpin;
      btn.disabled = true;
      try {
        await api(`/api/tenants/${State.tenant.slug}/pins/${pinId}`, { method: 'DELETE' });
        const data = await api(`/api/tenants/${State.tenant.slug}/pins?limit=8`);
        renderPinnedSection(data.pins || []);
      } catch (err) {
        showToast(`Could not unpin: ${err.message}`);
        btn.disabled = false;
      }
    });
  });
}

// 2026-06-07: count-up for the home hero number (presentation only — the
// value comes straight from data). Honours prefers-reduced-motion.
function _homeCountUp(root) {
  const el = (root || document).querySelector('.hero-num-val[data-countup]');
  if (!el) return;
  const to = parseFloat(el.dataset.countup);
  const dec = parseInt(el.dataset.decimals || '0', 10);
  if (!isFinite(to)) return;
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || !to) { el.textContent = to.toFixed(dec); return; }
  const dur = 1000;
  let started = null;
  function tick(ts) {
    if (started === null) started = ts;
    const p = Math.min(1, (ts - started) / dur);
    el.textContent = (to * (1 - Math.pow(1 - p, 3))).toFixed(dec);
    if (p < 1) requestAnimationFrame(tick); else el.textContent = to.toFixed(dec);
  }
  requestAnimationFrame(tick);
}

function renderHomeWithData(data) {
  lw.updateHome(data);
  // 1. MER Hero
  const heroSlot = $('#hero-slot');
  if (heroSlot) {
    // Fresh paint from a user action — the stamp slam and count-up are
    // wanted here, so clear any stillness a 90s tick left behind
    // (mirrors the target slot below; floor-4 verifier, 2026-08-29).
    heroSlot.classList.remove('tick-still');
    heroSlot.innerHTML = renderMerHero(data.mer_hero || {});
    // The hero figure reads as a till total under house (Archivo 900 at
    // 'wdth' 75, tabular-nums — CSS only). _homeCountUp already settles it;
    // no second count-up, no house-specific JS on this number.
    _homeCountUp(heroSlot);
  }

  // 1b. HOUSE floor 4 — the tills, then the ticker. The stores come first:
  // ninth in a tile list is the wrong place for 90% of the revenue. Under
  // every other skin this slot is written empty and stays invisible.
  const deskSlot = $('#house-desk-slot');
  if (deskSlot) {
    deskSlot.innerHTML = deskHouse()
      ? renderHouseTills(data.locations || {}) + renderHouseTicker(data.live_today || {})
      : '';
    // Floor 4 lives (2026-08-29): the bars pour in one frame later, and
    // the tape + hero remember what they painted so the 90s tick can land
    // NEW orders instead of standing still between page loads.
    _deskArrive(deskSlot);
    _frontDeskRemember(data);
  }

  // 2. Sales + Spend chart
  const chartSlot = $('#chart-slot');
  if (chartSlot) {
    const chartData = lw.homeChart(data);
    chartSlot.innerHTML = renderChartShell(chartData);
    if (chartData && chartData.available) {
      // Defer to next tick so DOM is in place
      const current = lw.captureRead('home', false);
      setTimeout(() => { if (current()) drawSalesSpendChart(lw.homeChart(data)); }, 50);
    }
  }

  // 3. Target progress
  const targetSlot = $('#target-slot');
  if (targetSlot) {
    // Fresh paint from a user action — entry animation is wanted here,
    // so clear the tick's stillness and seed its change-fingerprint
    // (WS-B: the 90s tick then leaves identical data untouched).
    targetSlot.classList.remove('tick-still');
    targetSlot.innerHTML = renderTargetTile(data.target);
    _lastTargetFp = _targetFp(data.target);
    // HOUSE floor 4: the pace band's fill sweeps 0 → progress on a fresh
    // paint (hs-pour releases one frame later). The 90s tick re-render
    // carries .tick-still, where the same release snaps instead.
    _bandSettle(targetSlot);
  }

  // 4. Tiles row (customers + locations + top ad + top product + refunds)
  const tilesSlot = $('#tiles-slot');
  if (!tilesSlot) return;
  const cards = [];
  if (data.customers && data.customers.available) cards.push(renderCustomersCard(data.customers));
  // HOUSE floor 4: the tills strip above already IS the locations read, so
  // the tile is skipped rather than repeated — its two doors (Ask EON and
  // the /locations link) are carried onto the strip, so nothing is lost.
  if (data.locations && data.locations.available && data.locations.locations.length
      && !deskHouse()) {
    cards.push(renderLocationsCard(data.locations));
  }
  if (data.top_ad && data.top_ad.available) cards.push(renderTopAdCard(data.top_ad));
  if (data.top_product && data.top_product.available) cards.push(renderTopProductCard(data.top_product));
  if (data.refunds && data.refunds.available) cards.push(renderRefundsCard(data.refunds));
  if (data.live_today && data.live_today.ok) cards.push(renderOrdersCard(data.live_today, data.orders_pace));
  // v3.0p31 PR #152: rec-pressure tile. Only render when there ARE
  // open recs; an empty list means inbox-zero, no need to nag.
  if (data.recs && data.recs.available && data.recs.total_open > 0) {
    cards.push(renderRecsCard(data.recs));
  }

  tilesSlot.innerHTML = cards.length
    ? `<div class="mh-section">Signals</div>${cards.join('')}`
    : `<div class="a3-empty" style="grid-column:1/-1">No data for this period.</div>`;

  // 5. CS team performance tile (2026-06-06). Lazy-loaded so a slow
  // Zoko aggregator doesn't block the rest of the Home render.
  // The same loader appends the "Open CS Inbox" entry button right
  // under the tile so reps can jump straight into the chat UI.
  loadCsTeamPerformanceTile();

  // 6. EON ad moves (compact) — 2026-06-08. Moved off the Ads tab (where it
  // triple-rendered) to the main page per operator request. Lazy-appended
  // below the signals/cash tiles so it never crowds money.
  _adsxLoadHomeMoves();

  // [data-ask] / [data-go] taps: document-level delegation (WS-A,
  // 2026-07-26) — survives every slot innerHTML replace; no re-binds.

  // Bind expand toggles for top-ad / top-product dropdowns
  document.querySelectorAll('.expand-toggle').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const target = btn.dataset.expand;
      const list = btn.parentElement.querySelector(`[data-expand-list="${target}"]`);
      if (!list) return;
      const isOpen = !list.hasAttribute('hidden');
      if (isOpen) {
        list.setAttribute('hidden', '');
        btn.classList.remove('open');
        btn.querySelector('.label').textContent = btn.querySelector('.label').textContent.replace('Hide', 'See').replace(/^Hide /, 'See ');
      } else {
        list.removeAttribute('hidden');
        btn.classList.add('open');
        const label = btn.querySelector('.label');
        label.textContent = label.textContent.replace('See', 'Hide');
      }
    });
  });
}

// Apple Health-style structured card with big number + dim label rows
function renderOrdersCard(o, pace) {
  const locs = (o.by_location || []).slice(0, 5);
  const locMax = Math.max(1, ...locs.map(r => r.amount || 0));
  const locRows = locs.length ? _mhHBars(locs.map((r, i) => ({
    label: r.name,
    display: fmtNGN(r.amount),
    pct: ((r.amount || 0) / locMax) * 100,
    lead: i === 0,
  }))) : '';

  // HOME-DECISION-FIRST: comparative read — today's pace vs the same
  // weekday's trailing 4-week average (server-computed, deterministic).
  // Missing/failed pace = no read line at all (graceful).
  let paceRead = '';
  if (pace && pace.available) {
    if (pace.status === 'no_baseline') {
      paceRead = `
        <div class="mh-read">
          <span class="mh-chip mh-chip-muted">no baseline</span>
          <span>No prior ${esc(pace.weekday || 'weekday')} sales in the last 4 weeks to compare against yet.</span>
        </div>`;
    } else if (Number.isFinite(pace.delta_pct)) {
      const chipCls = pace.tone === 'good' ? 'mh-chip-good'
                    : pace.tone === 'danger' ? 'mh-chip-danger'
                    : 'mh-chip-watch';
      const dir = pace.status === 'ahead' ? 'ahead' : 'behind';
      paceRead = `
        <div class="mh-read">
          <span class="mh-chip ${chipCls}">${Math.abs(pace.delta_pct).toFixed(0)}% ${dir}</span>
          <span>vs typical ${esc(pace.weekday || '')} by ${esc(pace.as_of_local || 'now')} (4-week avg ${fmtNGN(pace.typical_revenue_ngn)})</span>
        </div>`;
    }
  }

  return `
    <div class="mh-tile">
      <div class="mh-tile-head">
        <span class="mh-tile-title">Orders today</span>
        <span class="mh-tile-meta">${o.total_orders} orders</span>
      </div>
      <div class="mh-stat-grid">
        <div>
          <div class="mh-stat-num">${fmtNGN(o.total_revenue)}</div>
          <div class="mh-stat-lbl">Revenue</div>
        </div>
        <div>
          <div class="mh-stat-num">${fmtNGN(o.aov)}</div>
          <div class="mh-stat-lbl">AOV</div>
        </div>
      </div>
      ${paceRead}
      ${locRows && !deskHouse() ? `<div>${locRows}</div>` : ''}
      <div class="mh-foot"><button class="mh-ask" data-ask="Break down today's orders by location and vendor">Ask EON</button></div>
    </div>
  `;
}

// HOME-DECISION-FIRST: per-ad verdict chip — same semantics the
// Marketing per-ad table uses (per_ad_verdicts_latest). mint=scale,
// amber=monitor (watch), red=kill, muted=hold/no-verdict.
function _adVerdictChip(verdict) {
  const v = String(verdict || '').toLowerCase();
  if (v === 'scale') return '<span class="mh-chip mh-chip-good">scale</span>';
  if (v === 'kill') return '<span class="mh-chip mh-chip-danger">kill</span>';
  if (v === 'monitor') return '<span class="mh-chip mh-chip-watch">monitor</span>';
  if (v === 'hold') return '<span class="mh-chip mh-chip-muted">hold</span>';
  return '<span class="mh-chip mh-chip-muted">no verdict yet</span>';
}

function renderTopAdCard(data) {
  const ad = data.top;
  const more = data.more || [];
  const period = lw.active() ? lw.periodLabel() : State.homePeriod === 'today' ? 'today'
              : State.homePeriod === 'last_30d' ? '30 days'
              : State.homePeriod === 'this_month' ? 'this month'
              : '7 days';
  // Verdict read: one short reasoning line under the chip. Honest empty
  // state when the judge hasn't scored this ad.
  const verdictText = ad.verdict
    ? (ad.suggested_action || ad.verdict_reasoning || '')
    : 'This ad has not been scored by the ad judge yet.';
  const verdictRead = `
    <div class="mh-read">
      ${_adVerdictChip(ad.verdict)}
      <span>${esc(shorten(verdictText, 110))}</span>
    </div>`;
  return `
    <div class="mh-tile" data-card-type="top-ad">
      <div class="mh-tile-head">
        <span class="mh-tile-title">Top ad · ${period}</span>
        <span class="mh-tile-meta">${esc(ad.platform || '')}</span>
      </div>
      <div class="mh-tile-name">${esc(shorten(ad.ad_name, 60))}</div>
      ${verdictRead}
      <div class="mh-stat-grid cols3">
        <div>
          <div class="mh-stat-num">${fmtNGN(ad.spend_ngn)}</div>
          <div class="mh-stat-lbl">Spend</div>
        </div>
        <div>
          <div class="mh-stat-num">${fmtNum(ad.impressions)}</div>
          <div class="mh-stat-lbl">Impressions</div>
        </div>
        <div>
          <div class="mh-stat-num">${(ad.ctr_pct || 0).toFixed(2)}<span class="u">%</span></div>
          <div class="mh-stat-lbl">CTR</div>
        </div>
      </div>
      ${more.length > 0 ? `
        <button class="expand-toggle" data-expand="ads">
          <span class="label">See ${more.length} more ${more.length === 1 ? 'ad' : 'ads'}</span>
          <span class="arrow">▼</span>
        </button>
        <div class="expand-list" data-expand-list="ads" hidden>
          ${more.map((a, i) => `
            <div class="expand-row">
              <div class="name">${i + 2}. ${esc(shorten(a.ad_name, 40))}</div>
              <div class="val">${a.verdict ? _adVerdictChip(a.verdict) + ' ' : ''}${fmtNGN(a.spend_ngn)} · ${(a.ctr_pct || 0).toFixed(2)}%</div>
            </div>
          `).join('')}
        </div>
      ` : ''}
      <div class="mh-foot"><button class="mh-ask" data-ask="Why is the top ad performing well right now?">Ask EON</button></div>
    </div>
  `;
}

function renderTopProductCard(data) {
  const p = data.top;
  const more = data.more || [];
  const period = lw.active() ? lw.periodLabel() : State.homePeriod === 'today' ? 'today'
              : State.homePeriod === 'last_30d' ? '30 days'
              : State.homePeriod === 'this_month' ? 'this month'
              : '7 days';
  return `
    <div class="mh-tile" data-card-type="top-product">
      <div class="mh-tile-head">
        <span class="mh-tile-title">Top product · ${period}</span>
        <span class="mh-tile-meta">${esc(p.vendor || '')}</span>
      </div>
      <div class="mh-tile-name">${esc(shorten(p.product_name, 60))}</div>
      <div class="mh-stat-grid">
        <div>
          <div class="mh-stat-num">${fmtNum(p.units_sold)}</div>
          <div class="mh-stat-lbl">Units sold</div>
        </div>
        <div>
          <div class="mh-stat-num">${fmtNGN(p.revenue_ngn)}</div>
          <div class="mh-stat-lbl">Revenue</div>
        </div>
      </div>
      ${more.length > 0 ? `
        <button class="expand-toggle" data-expand="products">
          <span class="label">See ${more.length} more ${more.length === 1 ? 'product' : 'products'}</span>
          <span class="arrow">▼</span>
        </button>
        <div class="expand-list" data-expand-list="products" hidden>
          ${more.map((p2, i) => `
            <div class="expand-row">
              <div class="name">${i + 2}. ${esc(shorten(p2.product_name, 40))}</div>
              <div class="val">${p2.units_sold} units · ${fmtNGN(p2.revenue_ngn)}</div>
            </div>
          `).join('')}
        </div>
      ` : ''}
      <div class="mh-foot"><button class="mh-ask" data-ask="Why is ${esc(p.product_name)} our top product right now?">Ask EON</button></div>
    </div>
  `;
}

function renderRefundsCard(r) {
  const rate = (_mhNumber(r.total_orders) === null || _mhNumber(r.total_orders) === 0) ? null : _mhPercent(r.rate_pct);
  // Existing review thresholds; these are not a measured historical baseline.
  const tone = rate === null ? 'unknown' : rate > 5 ? 'bad' : rate > 2 ? 'warn' : 'ok';
  const read = rate === null ? (_mhNumber(r.total_orders) === 0 ? 'No orders in this period.' : 'Refund rate is unavailable for this period.') : tone === 'ok'
    ? rate === 0 ? 'No refunded orders in this period.' : 'Below the 2% review threshold.'
    : tone === 'warn'
      ? 'Above the 2% review threshold — scan for a product or sizing pattern.'
      : 'Refunds are hot — find the offending product or batch now.';
  // The ring is a share of orders: 1% fills 1% of the circumference.
  const ringPct = rate;
  return `
    <div class="mh-tile">
      <div class="mh-tile-head">
        <span class="mh-tile-title">Refunds · ${lw.active() ? esc(lw.periodLabel()) : 'this month'}</span>
        <span class="mh-tile-meta">${_mhNumber(r.total_refunds) === null ? 'Count unavailable' : fmtNum(r.total_refunds) + (_mhNumber(r.total_refunds) === 1 ? ' refunded order' : ' refunded orders')}</span>
      </div>
      <div class="mh-sring-row">
        ${_mhRing(ringPct, rate === null ? '—' : rate.toFixed(1) + '%', 'of orders', tone)}
        <div class="mh-sring-side">
          <div class="mh-stat-num ${tone === 'warn' || tone === 'bad' ? 'warn' : ''}">${_mhNumber(r.total_amount_ngn) === null ? '—' : fmtNGN(r.total_amount_ngn)}</div>
          <div class="mh-stat-lbl">Value of affected orders</div>
        </div>
      </div>
      <p class="mh-chart-note">${_mhNumber(r.total_orders) === null || _mhNumber(r.total_refunds) === null ? 'Order count unavailable.' : fmtNum(r.total_refunds) + ' of ' + fmtNum(r.total_orders) + ' orders.'} Full and partial refunds; value is the affected order total, not the amount repaid.</p>
      <div class="mh-read"><span class="mh-chip ${tone === 'ok' ? 'mh-chip-good' : tone === 'warn' ? 'mh-chip-watch' : tone === 'bad' ? 'mh-chip-danger' : ''}">${tone === 'ok' ? 'normal' : tone === 'warn' ? 'elevated' : tone === 'bad' ? 'high' : 'unavailable'}</span><span>${read}</span></div>
      <div class="mh-foot"><button class="mh-ask" data-ask="What's driving refunds for ${esc(lw.active() ? lw.periodLabel() : 'this month')}? Compare with the prior equivalent period.">Ask EON</button></div>
    </div>
  `;
}

function renderRecsCard(r) {
  // Audit 2026-05-15: 0 / 54 recs actioned in production. The home
  // tile exists to put visible pressure on that loop — bigger count
  // and older oldest = warn tone so it doesn't fade into the page.
  const inspectable = typeof lw !== 'undefined' && lw.active();
  const tone = (r.critical > 0 || r.oldest_age_days > 7) ? 'warn' : 'ok';
  const ageBlurb = r.oldest_age_days > 0 ? `${r.oldest_age_days}d` : 'today';
  return `
    <div class="mh-tile">
      <div class="mh-tile-head">
        <span class="mh-tile-title">Open recommendations</span>
        <span class="mh-tile-meta">oldest ${esc(ageBlurb)}</span>
      </div>
      <div class="mh-stat-grid cols3">
        ${inspectable?'<button type="button" class="hf-metric-source" data-lw="inspect" data-source="recommendation-summary">':'<div>'}
          <div class="mh-stat-num ${tone === 'warn' ? 'warn' : ''}">${fmtNum(r.total_open)}</div>
          <div class="mh-stat-lbl">Open</div>
        ${inspectable?'</button>':'</div>'}
        ${inspectable?'<button type="button" class="hf-metric-source" data-lw="inspect" data-source="recommendation-summary">':'<div>'}
          <div class="mh-stat-num ${r.critical > 0 ? 'warn' : ''}">${fmtNum(r.critical)}</div>
          <div class="mh-stat-lbl">Critical</div>
        ${inspectable?'</button>':'</div>'}
        ${inspectable?'<button type="button" class="hf-metric-source" data-lw="inspect" data-source="recommendation-summary">':'<div>'}
          <div class="mh-stat-num">${fmtNum(r.actioned_last_7d)}</div>
          <div class="mh-stat-lbl">Actioned · 7d</div>
        ${inspectable?'</button>':'</div>'}
      </div>
      <div class="mh-foot"><button class="mh-ask mh-ask-go" data-go="recommendations">Review →</button></div>
    </div>
  `;
}

function renderEmptyCard(title, msg) {
  return `
    <div class="data-card data-card-empty">
      <div class="data-card-head"><span class="data-card-title">${esc(title)}</span></div>
      <div class="data-empty-msg">${esc(msg)}</div>
    </div>
  `;
}


// ── v3.0p32 PR 3: Money page insights tiles ───────────────────
// 5 cards backed by /api/finance/money/insights.

function renderExpensesCard(e) {
  if (!e || !e.ok) return '';
  // The ratio and the interactive plot must tell the same dated story.
  // Legacy aggregate defaults cannot fill a missing graph side with zero.
  const data=moneyMomentumData(e),rows=data.rows||[];
  const complete=data.ok&&rows.length>0&&rows.every(r=>r.revenue!==null&&r.expense!==null);
  const revenue=complete?rows.reduce((n,r)=>n+r.revenue,0):null;
  const expense=complete?rows.reduce((n,r)=>n+r.expense,0):null;
  const ratio=complete&&expense>0?revenue/expense:null;
  return `<div class="data-card">
    <div class="data-card-head"><span class="data-card-title">Revenue : OpEx · reported days</span></div>
    <div class="data-stat-num">${ratio===null?'Not established':ratio.toFixed(2)+'<span class="unit-sm">x</span>'}</div>
    <div class="data-stat-label">${complete?`${fmtNGN(revenue)} revenue · ${fmtNGN(expense)} operating spend`:'A dated revenue or spending amount is missing. The ratio stays unknown.'}</div>
    <p class="mw-fine">${complete?`${rows.length} reported day${rows.length===1?'':'s'}. `:''}The growth chart above shows each day. Running costs include ads and estimated payroll and rent; stock costs are separate. This ratio is not a profit margin.</p>
    <button class="ask-btn" data-ask="Walk me through the reported days and missing records behind this revenue and spending ratio">Ask EON</button>
  </div>`;
}

function renderValuationCard(v) {
  if (!v || !v.ok) return '';
  // PR MONEY-CASH-RESTORE-2: rebuild as a stacked Bloomberg-style
  // breakdown. The old 3-column squeeze cut off the third number
  // (₦2772.1M was being clipped). Now: each method on its own row
  // with full-width number + a sub-row showing the components that
  // produced it (cash + inventory + other - liabilities = book; LTM
  // revenue × multiple = revenue valuation; 12-mo profit × multiple =
  // EBITDA valuation). Operator can see the math, not just the answer.
  const methods = v.methods || {};
  const book = methods.book_value || {};
  const rev  = methods.revenue_multiple || {};
  const ebd  = methods.ebitda_multiple || {};

  const fmtFull = (n) => {
    if (n === null || n === undefined) return '—';
    n = Number(n);
    if (!Number.isFinite(n)) return '—';
    return '₦' + Math.round(n).toLocaleString();
  };

  const bookComp = book.components || {};
  const revComp  = rev.components || {};
  const ebdComp  = ebd.components || {};

  // 2026-06-01: replace single-point midpoint with a low/base/stretch
  // range that uses scenario-specific revenue bases (LTM / last full
  // year / peak year) — captures the Q4 seasonality and historical
  // peak that LTM alone hides during the trough months.
  const range = v.range || {};
  const scenarios = (range.scenarios) || {};
  const history = (range.history) || {};
  const ytdYear = range.ytd_current_year;
  const ytdRev = range.ytd_current;
  const fmtFullCompact = (n) => {
    if (n === null || n === undefined) return '—';
    n = Number(n);
    if (!Number.isFinite(n)) return '—';
    if (n >= 1e9) return '₦' + (n / 1e9).toFixed(2) + 'B';
    if (n >= 1e6) return '₦' + (n / 1e6).toFixed(0) + 'M';
    return '₦' + Math.round(n).toLocaleString();
  };
  const rangeRow = (range.low_ngn != null && range.base_ngn != null && range.stretch_ngn != null)
    ? `
      <div class="val-range" style="display:flex; gap:12px; padding:14px 0 10px; border-bottom:1px solid var(--line, rgba(255,255,255,0.08)); margin-bottom:14px;">
        <div style="flex:1; text-align:center;">
          <div style="font-size:10px; color:var(--ink-soft, #9ca3af); text-transform:uppercase; letter-spacing:0.06em;">Low</div>
          <div class="mono-num" style="font-size:15px; font-weight:600;">${fmtFullCompact(range.low_ngn)}</div>
          <div style="font-size:9px; color:var(--ink-soft, #9ca3af); margin-top:2px;">${esc(scenarios.low?.label || 'Last 12 mo')}</div>
        </div>
        <div style="width:1px; background:var(--line, rgba(255,255,255,0.08));"></div>
        <div style="flex:1.2; text-align:center;">
          <div style="font-size:10px; color:var(--ok, #4dd6a0); text-transform:uppercase; letter-spacing:0.06em; font-weight:600;">Base</div>
          <div class="mono-num" style="font-size:17px; font-weight:700; color:var(--ok, #4dd6a0);">${fmtFullCompact(range.base_ngn)}</div>
          <div style="font-size:9px; color:var(--ink-soft, #9ca3af); margin-top:2px;">${esc(scenarios.base?.label || 'last full year')}</div>
        </div>
        <div style="width:1px; background:var(--line, rgba(255,255,255,0.08));"></div>
        <div style="flex:1; text-align:center;">
          <div style="font-size:10px; color:var(--ink-soft, #9ca3af); text-transform:uppercase; letter-spacing:0.06em;">Stretch</div>
          <div class="mono-num" style="font-size:15px; font-weight:600;">${fmtFullCompact(range.stretch_ngn)}</div>
          <div style="font-size:9px; color:var(--ink-soft, #9ca3af); margin-top:2px;">${esc(scenarios.stretch?.label || 'peak year')}</div>
        </div>
      </div>
      ${Object.keys(history).length || ytdRev ? `
        <div style="font-size:10px; color:var(--ink-soft, #9ca3af); padding:0 2px 12px; line-height:1.5;">
          <strong style="color:var(--ink, #f5f5f5);">Your historical shape:</strong>
          ${Object.keys(history).sort().reverse().slice(0, 3).map(yr =>
            `${yr}: ${fmtFullCompact(history[yr])}`
          ).join('  ·  ')}${ytdRev ? `  ·  ${ytdYear} YTD: ${fmtFullCompact(ytdRev)}` : ''}
          <br>
          <span style="opacity:0.7;">
            Base = last 6 complete months annualized using 2024 seasonal weights. Backs out the Jun-Oct 2025 manufacturing-transition dip (recovery confirmed May 2026: first positive-YoY month at +5%).
          </span>
        </div>
      ` : ''}
    `
    : '';

  return `
    <div class="data-card val-card stagger-rise" style="--i:0">
      <div class="data-card-head">
        <span class="data-card-title">Business valuation</span>
        <span class="val-midpoint" title="Base case — see range below" style="font-size:11px;">
          base <span class="mono-num">${fmtFullCompact(range.base_ngn)}</span>
        </span>
      </div>
      ${rangeRow}

      <div class="val-method val-method-book">
        <div class="val-method-row">
          <span class="val-method-label">
            <span class="val-method-dot val-dot-book"></span>Book value
          </span>
          <span class="val-method-num val-num-book mono-num">${fmtFull(book.value_ngn)}</span>
        </div>
        <div class="val-method-formula mono-num">
          Cash ${fmtFull(bookComp.cash_ngn)}
          <span class="val-op">+</span> Inventory ${fmtFull(bookComp.inventory_at_cost_ngn)}
          <span class="val-op">+</span> Other ${fmtFull(bookComp.other_assets_ngn)}
          <span class="val-op">−</span> Liab ${fmtFull(bookComp.liabilities_ngn)}
        </div>
        <div class="val-method-note">Liquidation floor — assets net of liabilities, no going-concern premium.</div>
      </div>

      <div class="val-method val-method-rev">
        <div class="val-method-row">
          <span class="val-method-label">
            <span class="val-method-dot val-dot-rev"></span>Revenue multiple
          </span>
          <span class="val-method-num val-num-rev mono-num">${fmtFull(rev.value_ngn)}</span>
        </div>
        <div class="val-method-formula mono-num">
          Last 12 mo revenue ${fmtFull(revComp.ltm_revenue_ngn)}
          <span class="val-op">×</span> ${(Number(revComp.multiple) || 0).toFixed(1)}×
        </div>
        <div class="val-method-note">Trailing-365d revenue × growth multiple. Good for high-growth, low-EBITDA ops.</div>
      </div>

      <div class="val-method val-method-ebd">
        <div class="val-method-row">
          <span class="val-method-label">
            <span class="val-method-dot val-dot-ebd"></span>EBITDA multiple
          </span>
          <span class="val-method-num val-num-ebd mono-num">${fmtFull(ebd.value_ngn)}</span>
        </div>
        <div class="val-method-formula mono-num">
          Last 12 mo EBITDA ${fmtFull(ebdComp.ltm_ebitda_ngn)}
          <span class="val-op">×</span> ${(Number(ebdComp.multiple) || 0).toFixed(1)}×
        </div>
        ${ebdComp.ltm_revenue_ngn != null && ebdComp.ltm_cogs_ngn != null
          ? `<div class="val-method-formula mono-num" style="opacity:0.7; font-size:11px; margin-top:4px; line-height:1.5;">
               Rev ${fmtFull(ebdComp.ltm_revenue_ngn)}
               <span class="val-op">−</span> COGS ${fmtFull(ebdComp.ltm_cogs_ngn)}${ebdComp.cogs_estimated ? ' (est)' : ''}
               <span class="val-op">−</span> OpEx ${fmtFull(ebdComp.ltm_opex_ngn)}${ebdComp.opex_uses_floor ? ' (structural floor — payroll + leases)' : ''}<br>
               = ${fmtFull(ebdComp.ltm_ebitda_ngn)} EBITDA · ${(ebdComp.ltm_margin_pct != null && Number.isFinite(Number(ebdComp.ltm_margin_pct))) ? Number(ebdComp.ltm_margin_pct).toFixed(1) + '%' : '—'} margin
             </div>`
          : ''}
        <div class="val-method-note">Profit before tax and loan costs, over the last 12 months. Real product costs are used where we have them; running costs use at least your staff + rent floor so the number is never flattered.</div>
      </div>

      <div class="val-disclaimer">
        Informational estimate based on internal data. Not a professional appraisal.
        Real M&amp;A valuations factor in comparable transactions, due diligence, growth rate,
        customer concentration, and other variables this engine doesn't see.
      </div>
      <button class="ask-btn" data-ask="Walk me through how each of the three valuation methods got their number, and which is most relevant for CRUX right now">Ask EON</button>
    </div>
  `;
}

function renderLiquidityCard(l) {
  if (l?.cash_unknown === true) return `
    <div class="data-card" id="money-card-liquidity">
      <div class="data-card-head">
        <span class="data-card-title">Cash available to cover bills</span>
        <span class="pill pill-warn">Balances need checking</span>
      </div>
      <p class="data-stat-sub">Some account balances are unconfirmed. This is not zero cash: a complete cash total, and the ratios that depend on it, are not established yet.</p>
      <button type="button" class="btn-ghost" data-money-cash-check>Review account balances</button>
    </div>`;
  if (!l || !l.ok) return '';
  // PR MONEY-UI-MOBILE-POLISH: render debt-free state explicitly
  // instead of three blank dashes. Operators with no recorded
  // liabilities (CRUX) were seeing what looked like a broken tile.
  const isDebtFree = l.is_debt_free === true || l.status === 'debt_free';
  if (isDebtFree) {
    const cashNgn = l.components?.cash_ngn || 0;
    const fmtNG = (n) => '₦' + Math.round(Number(n) || 0).toLocaleString();
    return `
      <div class="data-card">
        <div class="data-card-head">
          <span class="data-card-title">Liquidity</span>
          <span class="pill pill-ok">Debt-free</span>
        </div>
        <div class="liq-debt-free">
          <div class="liq-debt-free-big mono-num tone-ok">∞</div>
          <div class="liq-debt-free-label">No liabilities recorded</div>
        </div>
        <div class="liq-debt-free-meta mono-num">
          Cash on hand ${fmtNG(cashNgn)}
        </div>
        <div class="val-method-note" style="margin-top:8px;">
          Ratios are infinite (no debt to service). Add loans, AR, or AP
          via the Accounting page if you want classic liquidity scoring.
        </div>
        <button class="ask-btn" data-ask="We have zero recorded liabilities. Should we be tracking any obligations (rent, supplier credit, factory advances)?">Ask EON</button>
      </div>
    `;
  }

  const fmt = (n) => n === null || n === undefined ? '—' : n.toFixed(2);
  const cls = (n, healthy, warn) => {
    if (n === null || n === undefined) return '';
    if (n >= healthy) return 'tone-ok';
    if (n >= warn) return 'tone-warn';
    return 'tone-danger';
  };
  return `
    <div class="data-card">
      <div class="data-card-head">
        <span class="data-card-title">Liquidity ratios</span>
      </div>
      <div class="data-stat-grid data-stat-3">
        <div class="data-stat">
          <div class="data-stat-num ${cls(l.current_ratio, 2, 1)}">${fmt(l.current_ratio)}</div>
          <div class="data-stat-label">Current</div>
        </div>
        <div class="data-stat">
          <div class="data-stat-num ${cls(l.quick_ratio, 1, 0.5)}">${fmt(l.quick_ratio)}</div>
          <div class="data-stat-label">Quick</div>
        </div>
        <div class="data-stat">
          <div class="data-stat-num ${cls(l.cash_ratio, 0.5, 0.2)}">${fmt(l.cash_ratio)}</div>
          <div class="data-stat-label">Cash</div>
        </div>
      </div>
      <button class="ask-btn" data-ask="Explain our liquidity ratios and whether we're financially healthy">Ask EON</button>
    </div>
  `;
}

function openMoneyCashAccounts() {
  const accounts = document.getElementById('money-cash-drill');
  if (!accounts) return;
  accounts.open = true;
  for (let parent = accounts.parentElement; parent; parent = parent.parentElement) {
    if (parent.tagName === 'DETAILS') parent.open = true;
  }
  accounts.querySelector('summary')?.focus({ preventScroll: true });
  accounts.scrollIntoView({ block: 'start', behavior: 'auto' });
}


// ── Sprint 1 (2026-06-05) tile renderers ─────────────────────────────
// Four operator-actionable pulses surfaced beside the legacy insight
// cards. Each one answers "what should I DO about this" rather than
// just showing a metric.

function _moneyFmtNGN(n) { return ngn(n); }

function renderPayrollDueCard(p) {
  if (!p || !p.ok) return '';
  const eomColor = p.days_until_eom <= 5 ? 'tone-warn'
                 : p.days_until_eom <= 10 ? '' : 'tone-ok';
  // PR MONEY-COCKPIT: cash_unknown (backend PR-1) — the cash position
  // couldn't be read. Render "unknown", never a fake "Cash short".
  const cashUnknown = _moneyCashUnknown(p);
  const coverPill = cashUnknown
    ? '<span class="pill" style="font-size:11px;">We can\'t see the account balance</span>'
    : p.cash_covers
      ? '<span class="pill pill-ok" style="font-size:11px;">Cash covers</span>'
      : '<span class="pill pill-danger" style="font-size:11px;">Cash short</span>';
  const overdueBlock = p.overdue_count > 0 ? `
    <div style="margin-top:10px; padding-top:8px; border-top:1px dashed var(--border);">
      <div style="font-size:10px; text-transform:uppercase; letter-spacing:0.5px; color:#ef4444; font-weight:600;">
        Overdue (>${p.overdue_threshold_days}d since last pay)
      </div>
      ${p.overdue_staff.slice(0, 4).map(s => `
        <div style="font-size:11.5px; padding:4px 0; display:flex; justify-content:space-between;">
          <span>${esc(s.full_name)}</span>
          <span style="color:#ef4444;">${s.days_since_paid}d · ${_moneyFmtNGN(s.monthly_ngn)}</span>
        </div>
      `).join('')}
      ${p.overdue_staff.length > 4 ? `<div style="font-size:10.5px; color:var(--ink-soft); margin-top:2px;">+${p.overdue_staff.length - 4} more</div>` : ''}
    </div>` : '';
  return `
    <div class="data-card" id="money-card-payroll">
      <div class="data-card-head">
        <span class="data-card-title">Payroll due</span>
        ${coverPill}
      </div>
      <div class="data-stat-grid data-stat-3">
        <div class="data-stat">
          <div class="data-stat-num ${eomColor}">${p.days_until_eom}</div>
          <div class="data-stat-label">days to EOM</div>
        </div>
        <div class="data-stat">
          <div class="data-stat-num">${p.active_staff_count}</div>
          <div class="data-stat-label">active staff</div>
        </div>
        <div class="data-stat">
          <div class="data-stat-num mono-num">${_moneyFmtNGN(p.total_due_ngn)}</div>
          <div class="data-stat-label">total due</div>
        </div>
      </div>
      <div style="font-size:11.5px; color:var(--ink-soft); margin-top:8px;">
        ${cashUnknown
          ? 'Cash position unknown — remaining-after-pay not computable'
          : p.cash_covers
            ? 'After paying: ' + _moneyFmtNGN(p.remaining_cash_ngn) + ' cash remaining'
            : '<span style="color:#ef4444;">Shortfall: ' + _moneyFmtNGN(-(p.remaining_cash_ngn)) + '</span>'}
      </div>
      ${overdueBlock}
      <button class="ask-btn" data-ask="Walk me through this month's payroll — who needs paying, when, and from which account">Ask EON</button>
    </div>
  `;
}

function renderDaysCashCard(d) {
  if (!d || !d.ok) return '';
  // PR MONEY-COCKPIT: cash_unknown (backend PR-1) — never render a
  // fake "critical" off an unreadable cash position.
  const cashUnknown = _moneyCashUnknown(d);
  const status = cashUnknown ? 'unknown' : (d.status || 'unknown');
  const pillCls = cashUnknown ? ''
                : status === 'critical' ? 'pill-danger'
                : status === 'warning'  ? 'pill-warn'
                : 'pill-ok';
  const numCls = cashUnknown ? ''
                : status === 'critical' ? 'tone-danger'
                : status === 'warning'  ? 'tone-warn'
                : 'tone-ok';
  const diff = (d.days_cash_on_hand || 0) - (d.red_threshold_days || 60);
  const c = d.components || {};
  return `
    <div class="data-card" id="money-card-dayscash">
      <div class="data-card-head">
        <span class="data-card-title">Days cash on hand</span>
        <span class="pill ${pillCls}" style="font-size:11px;">${esc(status)}</span>
      </div>
      <div class="data-stat" style="margin:4px 0 6px 0;">
        <div class="data-stat-num ${numCls}" style="font-size:32px;">${cashUnknown ? '—' : (d.days_cash_on_hand ?? '—')}<span style="font-size:13px; color:var(--ink-soft); margin-left:6px;">days</span></div>
        <div class="data-stat-label">${cashUnknown
          ? 'cash position unknown'
          : `vs ${d.red_threshold_days}d floor · ${diff >= 0 ? '+' : ''}${diff}d`}</div>
      </div>
      <div style="font-size:11.5px; color:var(--ink-soft); padding-top:6px; border-top:1px solid var(--line); margin-top:6px;">
        Fixed burn <b style="color:var(--ink);">${_moneyFmtNGN(d.daily_fixed_burn_ngn)}</b>/day
      </div>
      <div style="font-size:11px; color:var(--ink-soft); margin-top:3px;">
        Payroll ${_moneyFmtNGN(c.payroll_daily_ngn)} ·
        Lease ${_moneyFmtNGN(c.lease_daily_ngn)} ·
        Overhead ${_moneyFmtNGN(c.overhead_daily_ngn)}
      </div>
      <div style="font-size:11px; color:var(--ink-soft); margin-top:4px; font-style:italic;">
        Excludes ad spend — shows "if I pause all ads today, how long until payroll cracks"
      </div>
      <button class="ask-btn" data-ask="What's my real fixed burn and how do I stretch our days-of-cash without cutting payroll">Ask EON</button>
    </div>
  `;
}

function renderLoanCovenantCard(l) {
  if (!l || !l.ok) return '';
  if (l.is_debt_free) {
    return `
      <div class="data-card" id="money-card-covenant">
        <div class="data-card-head">
          <span class="data-card-title">Loan covenants</span>
          <span class="pill pill-ok" style="font-size:11px;">Debt-free</span>
        </div>
        <div style="font-size:13px; color:var(--ink-soft); margin-top:8px;">
          No active facilities. Covenant tracking inactive.
        </div>
      </div>
    `;
  }
  const fac = (l.facilities || []).map(f => {
    const dscrPill = f.dscr_status === 'covenant_risk' ? 'pill-danger'
                   : f.dscr_status === 'tight'         ? 'pill-warn'
                   : f.dscr_status === 'excellent'     ? 'pill-ok'
                   : 'pill-ok';
    const coverPill = f.cash_covers_next_payment
      ? '<span class="pill pill-ok" style="font-size:10px;">cash covers</span>'
      : '<span class="pill pill-danger" style="font-size:10px;">cash short</span>';
    const daysLabel = f.days_until_next_payment != null
      ? `in ${f.days_until_next_payment}d` : 'TBD';
    return `<div style="padding:10px 0; border-bottom:1px solid var(--line);">
      <div style="display:flex; justify-content:space-between; align-items:baseline;">
        <span style="font-weight:600;">${esc(f.name || f.counterparty || '—')}</span>
        <span class="pill ${dscrPill}" style="font-size:11px;" title="how many times over the cash covers this loan's repayments">covers repayments ${f.dscr ?? '—'}x · ${esc(({covenant_risk: 'lender could call it', tight: 'tight', excellent: 'comfortable', ok: 'comfortable'})[f.dscr_status] || String(f.dscr_status || '—').replace(/_/g, ' '))}</span>
      </div>
      <div style="display:flex; justify-content:space-between; font-size:12px; color:var(--ink-soft); margin-top:6px;">
        <span>Outstanding ${_moneyFmtNGN(f.outstanding_ngn)}</span>
        <span>Next pay ${_moneyFmtNGN(f.next_payment_amount_ngn)} ${daysLabel} ${coverPill}</span>
      </div>
      ${f.matures_at ? `<div style="font-size:11px; color:var(--ink-soft); margin-top:3px;">Maturity ${esc(f.matures_at)}</div>` : ''}
    </div>`;
  }).join('');
  return `
    <div class="data-card" id="money-card-covenant">
      <div class="data-card-head">
        <span class="data-card-title">Loan covenants</span>
      </div>
      ${fac || '<div style="font-size:12px; color:var(--ink-soft); padding:8px 0;">No active facilities.</div>'}
      <button class="ask-btn" data-ask="Walk me through my loan covenant status, DSCR, and next payment risk">Ask EON</button>
    </div>
  `;
}

function renderEmergencyFloorCard(e) {
  if (!e || !e.ok) return '';
  // PR MONEY-COCKPIT: cash_unknown (backend PR-1) — an unreadable
  // cash position must not render as "Breached" (or "Safe").
  const cashUnknown = _moneyCashUnknown(e);
  const pill = cashUnknown
    ? '<span class="pill" style="font-size:11px;">We can\'t see the account balance</span>'
    : e.is_breached
      ? '<span class="pill pill-danger" style="font-size:11px;">Breached</span>'
      : '<span class="pill pill-ok" style="font-size:11px;">Safe</span>';
  const cushionColor = cashUnknown ? ''
                     : e.is_breached ? 'tone-danger'
                     : (e.cushion_days != null && e.cushion_days < 30) ? 'tone-warn'
                     : 'tone-ok';
  const leversBlock = !cashUnknown && e.is_breached && e.levers && e.levers.length ? `
    <div style="margin-top:10px; padding-top:8px; border-top:1px dashed var(--border);">
      <div style="font-size:10px; text-transform:uppercase; letter-spacing:0.5px; color:var(--ink-soft); font-weight:600;">
        Levers if breached
      </div>
      ${e.levers.slice(0, 4).map(l => `
        <div style="padding:5px 0; font-size:11.5px;">
          <div style="font-weight:600;">${esc(l.action)} <span style="color:var(--ink-soft); font-weight:400;">· ${_moneyFmtNGN(l.impact_ngn)}</span></div>
          <div style="color:var(--ink-soft); font-size:11px;">${esc(l.description)}</div>
        </div>
      `).join('')}
    </div>` : '';
  return `
    <div class="data-card" id="money-card-floor">
      <div class="data-card-head">
        <span class="data-card-title">Emergency floor</span>
        ${pill}
      </div>
      <div style="font-size:11.5px; color:var(--ink-soft); margin:6px 0 4px 0;">
        Keep at least ${e.floor_multiplier}× monthly payroll in cash
      </div>
      <div class="data-stat-grid data-stat-3">
        <div class="data-stat">
          <div class="data-stat-num mono-num">${cashUnknown ? '—' : _moneyFmtNGN(e.cash_today_ngn)}</div>
          <div class="data-stat-label">cash today</div>
        </div>
        <div class="data-stat">
          <div class="data-stat-num mono-num">${_moneyFmtNGN(e.floor_ngn)}</div>
          <div class="data-stat-label">floor</div>
        </div>
        <div class="data-stat">
          <div class="data-stat-num ${cushionColor}">${cashUnknown ? '—' : (e.cushion_days != null ? e.cushion_days + 'd' : '—')}</div>
          <div class="data-stat-label">cushion</div>
        </div>
      </div>
      ${leversBlock}
      <button class="ask-btn" data-ask="Walk me through the emergency floor — am I close to breaching, and which lever should I pull first">Ask EON</button>
    </div>
  `;
}

// Vendor analysis (2026-07-09): per-vendor P&L + the ENO commission
// ledger. Backed by /api/finance/vendors.
function renderVendorsCard(d) {
  if (!d || !d.ok || !(d.ytd || []).length) return '';
  const rows = (d.ytd || []).slice(0, 6).map(v => `
    <tr>
      <td style="padding:4px 8px 4px 0; font-weight:600;">${esc(v.vendor)}</td>
      <td style="padding:4px 8px; text-align:right;">${fmtNGN(v.revenue_ngn, true)}</td>
      <td style="padding:4px 8px; text-align:right;">${v.est_margin_pct != null ? v.est_margin_pct.toFixed(1) + '%' : '—'}</td>
      <td style="padding:4px 0 4px 8px; text-align:right;">${v.est_profit_ngn != null ? fmtNGN(v.est_profit_ngn, true) : '—'}</td>
    </tr>`).join('');
  const eno = d.eno_commission || {};
  const enoLine = eno.all_time_revenue_ngn
    ? `<div class="data-stat-sub" style="margin-top:8px;">
         ENO commission (15%): profit-base ${fmtNGN(eno.commission_if_profit_base_ngn || 0, true)}
         · revenue-base ${fmtNGN(eno.commission_if_revenue_base_ngn || 0, true)}
         · paid ${fmtNGN(eno.commission_paid_ngn || 0, true)} (+${fmtNGN(eno.settlements_paid_ngn || 0, true)} settlements)
       </div>`
    : '';
  return `
    <div class="data-card">
      <div class="data-card-head">
        <span class="data-card-title">Vendor P&amp;L · 2026 YTD</span>
      </div>
      <div style="overflow-x:auto;">
        <table style="width:100%; border-collapse:collapse; font-size:12.5px;">
          <thead><tr style="opacity:0.6; text-align:right;">
            <th style="text-align:left; padding:0 8px 6px 0;">vendor</th>
            <th style="padding:0 8px 6px;">revenue</th>
            <th style="padding:0 8px 6px;">margin</th>
            <th style="padding:0 0 6px 8px;">est. profit</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      ${enoLine}
      <button class="ask-btn" data-ask="Walk me through the vendor P&L — which vendor line earns best, and is the ENO commission tracking the right base">Ask EON</button>
    </div>
  `;
}

async function refreshMoneyVendors(gen = State._moneyGen) {
  const slot = document.getElementById('money-vendors');
  if (!slot) return;
  try {
    const d = await api('/api/finance/vendors');
    if (gen !== State._moneyGen) return;
    slot.innerHTML = renderVendorsCard(d)
      || renderEmptyCard('Vendors', 'No vendor sales data yet.');
  } catch (e) {
    if (gen !== State._moneyGen) return;
    slot.innerHTML = renderEmptyCard('Vendors', 'Could not load vendor analysis.');
  }
}

// API wallets (mig 529, 2026-07-27): per-provider balance / runway /
// renewal board — "always aware of balance so nothing breaks". Hydrated
// from /api/finance/wallets; top-ups logged inline via /wallets/topup.
function renderApiWalletsCard(d) {
  if (!d || !Array.isArray(d.wallets) || !d.wallets.length) return '';
  const dot = (s) => ({
    depleted: '#ff5c5c', stale: '#ff9f43', low: '#ffcf5c', ok: '#3ddc97', unknown: '#8b877f',
  }[s] || '#8b877f');
  const fmtBal = (w) => {
    if (w.balance_native == null) return '—';
    const cur = w.balance_currency || '';
    const n = Number(w.balance_native);
    const gross = `${cur} ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    // A zero the platform has not re-read lately (unread_line) is not what
    // is in the wallet now: EON cannot tell. The old figure stays, named as
    // the last reading, never as the balance.
    if (w.unread_line) return `<span style="opacity:.75;">cannot tell</span><div style="opacity:.6;font-size:10.5px;">last reading ${gross}</div>`;
    // Both figures wherever a runway is printed (wallet-truth vet
    // 2026-09-01): the gross balance already has money spoken for.
    // Dividing the gross by burn is how 1.27 days printed as 1.7.
    if (w.unbilled_spend_native == null || !(Number(w.unbilled_spend_native) > 0)) return gross;
    const sp = Number(w.spendable_native);
    return `${gross}<div style="opacity:.6;font-size:10.5px;">${cur} ${sp.toLocaleString(undefined, { maximumFractionDigits: 0 })} spendable · ${cur} ${Number(w.unbilled_spend_native).toLocaleString(undefined, { maximumFractionDigits: 0 })} not yet billed</div>`;
  };
  const rows = d.wallets.map((w) => {
    // A runway from a number nobody read is not a runway. The reason
    // takes the cell instead — never a figure derived from a guess.
    // An ad wallet whose last zero the platform has not re-read lately
    // (unread_line, mig 927) is not "empty": the cell says EON cannot
    // read it, and the dot is the not-read amber, never the empty red.
    // Any reason the board sets takes the cell, not only the stale one: an
    // OpenAI wallet with no recorded credit says so (P2324), never a dash.
    const runway = w.unread_line
      ? `<span style="opacity:.75;font-size:11px;">${esc(w.unread_line)}</span>`
      : (w.runway_blocked_reason || w.state === 'stale')
      ? `<span style="opacity:.75;font-size:11px;">${esc(w.runway_blocked_reason || 'balance was typed, not read')}</span>`
      : (w.runway_days != null ? `${Math.round(w.runway_days)}d` : '—');
    // The board's plain sentence on how far to trust the row
    // (api_wallets.OWNER_NOTES), under the name. Never w.notes: those are
    // builder notes. It wraps inside a capped width so it never widens the table.
    const note = w.owner_note
      ? `<div style="opacity:.6;font-size:10.5px;white-space:normal;overflow-wrap:anywhere;max-width:260px;padding-left:16px;margin-top:2px;">${esc(w.owner_note)}</div>`
      : '';
    const burn = w.burn_daily_native != null
      ? `${Number(w.burn_daily_native).toLocaleString(undefined, { maximumFractionDigits: 1 })}/d` : '—';
    const renew = w.renewal_date ? esc(String(w.renewal_date)) : '—';
    return `<tr>
      <td style="white-space:nowrap;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${dot(w.unread_line ? 'stale' : w.state)};margin-right:8px;"></span>${esc(w.display_name || w.provider)}${note}</td>
      <td style="text-align:right;">${fmtBal(w)}</td>
      <td style="text-align:right;">${runway}</td>
      <td style="text-align:right;">${burn}</td>
      <td style="text-align:right;">${renew}</td>
      <td style="text-align:right;opacity:.55;font-size:11px;">${esc(w.balance_source || '')}</td>
    </tr>`;
  }).join('');
  const opts = d.wallets
    .filter((w) => ['anthropic', 'gemini', 'fal', 'higgsfield', 'openai'].includes(w.provider))
    .map((w) => `<option value="${esc(w.provider)}">${esc((w.display_name || w.provider).split(' (')[0])}</option>`)
    .join('');
  // Snapchat has no balance pull, and TikTok's is read from TikTok only
  // while EON holds the finance role on its Business Center. When a pull
  // cannot answer, a checked number enters through the same Money door the
  // operator already uses, never through a hand-edited table. Zero is a
  // real balance (and must remain typeable); it is not an empty input.
  const adWalletOpts = d.wallets
    .filter((w) => ['tiktok_ads', 'snapchat_ads'].includes(w.provider))
    .map((w) => `<option value="${esc(w.provider)}" data-currency="${esc(w.balance_currency || 'USD')}">${esc((w.display_name || w.provider).split(' (')[0])}</option>`)
    .join('');
  // The header counts what the rows say: a zero nobody has re-read counts
  // as "not read", never as empty.
  const unreadN = d.wallets.filter((w) => w.unread_line).length;
  const emptyN = Math.max(0, ((d.counts && d.counts.depleted) || 0) - unreadN);
  const notReadN = ((d.counts && d.counts.stale) || 0) + unreadN;
  return `<div class="data-card">
    <div style="display:flex;justify-content:space-between;align-items:baseline;gap:10px;flex-wrap:wrap;">
      <strong>Ad wallets and tool credits</strong>
      <span class="meta">${emptyN ? `${emptyN} empty · ` : ''}${notReadN ? `${notReadN} not read · ` : ''}${d.counts && d.counts.low ? `${d.counts.low} low · ` : ''}hourly refresh</span>
    </div>
    <div style="overflow-x:auto;margin-top:10px;">
      <table style="width:100%;border-collapse:collapse;font-size:12.5px;" class="wallets-table">
        <thead><tr style="opacity:.6;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:1px;">
          <th style="text-align:left;">Provider</th><th>Balance</th><th>Runway</th><th>Burn</th><th>Renews</th><th>Src</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
    <div id="wallet-llm-forecast" style="margin-top:12px;"></div>
    <div style="display:flex;gap:8px;margin-top:12px;align-items:center;flex-wrap:wrap;">
      <select id="wallet-topup-provider" style="min-width:120px;">${opts}</select>
      <input id="wallet-topup-amount" type="number" min="1" step="any" placeholder="Amount (USD)" style="width:120px;">
      <button class="btn-secondary" id="wallet-topup-btn">Log top-up</button>
      <span class="meta">tell EON what you topped up — that's how it works out how long it lasts</span>
    </div>
    ${adWalletOpts ? `<div style="border-top:1px solid rgba(255,255,255,.08);display:flex;gap:8px;margin-top:12px;padding-top:12px;align-items:center;flex-wrap:wrap;">
      <select id="wallet-manual-provider" aria-label="Ad wallet" style="min-width:120px;">${adWalletOpts}</select>
      <input id="wallet-manual-balance" aria-label="Checked balance" type="number" min="0" step="any" placeholder="Checked balance" style="width:140px;">
      <input id="wallet-manual-currency" aria-label="Balance currency" maxlength="8" value="USD" style="width:78px;text-transform:uppercase;">
      <button class="btn-secondary" id="wallet-manual-btn">Set checked balance</button>
      <span class="meta">after you read the ad account — typed balances expire if nobody checks them again</span>
    </div>` : ''}
  </div>`;
}

// Projected vs actual Anthropic spend (2026-07-28, "no surprises"):
// expected/day from the trailing 14 full days, today's pace against the
// fraction of the day elapsed, week actual vs expected, and the lanes
// running hot. Fetched alongside the wallets board; absent endpoint
// renders nothing.
function renderLlmForecastStrip(f) {
  if (!f || !f.ok || !f.projected) return '';
  const p = f.projected, a = f.actual || {};
  const pace = a.pace_pct != null
    ? (a.pace_pct > 150 ? `<span style="color:#ffcf5c;">${a.pace_pct}% of pace</span>`
       : `<span style="color:#3ddc97;">${a.pace_pct}% of pace</span>`)
    : '';
  const movers = (f.movers || []).slice(0, 3).map(m =>
    `<span class="meta">${esc(m.purpose)} $${m.actual_today_usd} today (exp $${m.expected_daily_usd}/d)</span>`
  ).join(' · ');
  // A real gap: a metered model nobody priced, so the bill WILL exceed
  // this figure. Seats are not this — they are the line below.
  const unpriced = (f.unpriced_models || []).length
    ? `<div class="meta" style="color:#ffcf5c;margin-top:4px;">meter gap: ${f.unpriced_models.map(u => esc(u.model)).join(', ')} recording at $0 — real bill runs higher</div>`
    : '';
  // Work done on the flat monthly seats. Real work, already paid for,
  // no extra charge per use — so it is stated, never coloured as a
  // fault. Until 2026-09-13 this sat in the amber line above and told
  // her a paid-up seat was an unbilled surprise.
  const seats = f.seat_note
    ? `<div class="meta" style="margin-top:4px;opacity:.75;">${esc(f.seat_note)}</div>`
    : '';
  const settling = (p.settling_daily_usd != null
                    && Math.abs(p.settling_daily_usd - p.daily_usd) >= 1)
    ? `<span class="meta">settling to about $${p.settling_daily_usd}/day (about $${p.settling_monthly_usd}/mo) as the switched-off tools drop out of the average</span>`
    : '';
  const cooling = (f.cooling || []).slice(0, 3).map(c =>
    `<span class="meta">${esc(c.purpose)} dropped from $${c.was_usd_day} to $${c.now_usd_day} a day</span>`
  ).join(' · ');
  return `<div style="border-top:1px solid rgba(255,255,255,.08);padding-top:10px;">
    <div style="display:flex;gap:14px;flex-wrap:wrap;align-items:baseline;">
      <strong style="font-size:12.5px;">Anthropic bill forecast</strong>
      <span style="font-variant-numeric:tabular-nums;">$${p.daily_usd}/day · $${p.weekly_usd}/wk · ≈$${p.monthly_usd}/mo</span>
      <span class="meta" style="font-variant-numeric:tabular-nums;">today $${a.today_usd} ${pace}</span>
      <span class="meta" style="font-variant-numeric:tabular-nums;">last 7d $${a.last_7d_usd} vs $${a.expected_7d_usd} expected</span>
    </div>
    ${settling ? `<div style="margin-top:4px;">${settling}</div>` : ''}
    ${movers ? `<div style="margin-top:4px;">${movers}</div>` : ''}
    ${cooling ? `<div style="margin-top:4px;">${cooling}</div>` : ''}
    ${unpriced}
    ${seats}
  </div>`;
}

// A11: the wallet a Today link named (moneyReadEntry), while the view is
// Money and the address still carries wallet=; leaving Money drops it from
// the address (_syncHistory). The board's rows follow d.wallets in order.
function moneyNamedWallet() {
  try {
    return State.view === 'money' && State.moneyWallet
      && new URLSearchParams(location.search).get('wallet') === State.moneyWallet ? State.moneyWallet : null;
  } catch (_) { return null; }
}

// The named row is marked by an inset outline, which takes no room: nothing
// moves, and the board's sideways scroller cannot clip it. Scrolled to once
// per wallet: a refresh keeps the mark, not the scroll. A wallet the board
// does not carry is said in one line, never called gone.
function moneyMarkWallet(slot, d) {
  const named = moneyNamedWallet();
  if (!named) return;
  const i = d && Array.isArray(d.wallets) ? d.wallets.findIndex(w => w && w.provider === named) : -1;
  const row = i < 0 ? null : slot.querySelectorAll('.wallets-table tbody > tr')[i];
  if (!row) {
    slot.insertAdjacentHTML('beforeend', '<p class="meta" role="status" data-wallet-unshown style="margin:8px 0 0;">That wallet is not on this board.</p>');
    return;
  }
  row.setAttribute('tabindex', '-1');
  row.setAttribute('aria-current', 'true');
  row.setAttribute('style', 'outline:2px solid var(--accent,#d4ff3a);outline-offset:-2px;scroll-margin:96px');
  if (State.moneyWalletSeen === named) return;
  State.moneyWalletSeen = named;
  const reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  row.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
  row.focus({ preventScroll: true });
}

async function refreshMoneyWallets(gen = State._moneyGen) {
  const slot = document.getElementById('money-wallets');
  if (!slot) return;
  try {
    const [d, f] = await Promise.all([
      api('/api/finance/wallets'),
      api('/api/finance/wallets/llm-forecast').catch(() => null),
    ]);
    if (gen !== State._moneyGen) return;
    slot.innerHTML = renderApiWalletsCard(d)
      || renderEmptyCard('Ad wallets and tool credits', 'Nothing here yet — this fills in on its own within the hour.');
    try { moneyMarkWallet(slot, d); } catch (_) { /* a mark never costs the board */ }
    const fcSlot = document.getElementById('wallet-llm-forecast');
    if (fcSlot) fcSlot.innerHTML = renderLlmForecastStrip(f);
    const btn = document.getElementById('wallet-topup-btn');
    if (btn) btn.onclick = async () => {
      const provider = (document.getElementById('wallet-topup-provider') || {}).value;
      const amount = parseFloat((document.getElementById('wallet-topup-amount') || {}).value);
      if (!provider || !(amount > 0)) { showToast('Pick a provider and a positive amount'); return; }
      btn.disabled = true;
      try {
        await api('/api/finance/wallets/topup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider, amount, currency: 'USD' }),
        });
        showToast('Top-up logged — runway recomputed');
        refreshMoneyWallets(State._moneyGen);
      } catch (e) {
        showToast('Top-up failed — try again');
        btn.disabled = false;
      }
    };
    const manualProvider = document.getElementById('wallet-manual-provider');
    const manualCurrency = document.getElementById('wallet-manual-currency');
    const syncManualCurrency = () => {
      if (!manualProvider || !manualCurrency) return;
      const picked = manualProvider.options[manualProvider.selectedIndex];
      manualCurrency.value = (picked && picked.dataset.currency) || 'USD';
    };
    if (manualProvider && manualCurrency) {
      syncManualCurrency();
      manualProvider.onchange = syncManualCurrency;
    }
    const manualBtn = document.getElementById('wallet-manual-btn');
    if (manualBtn) manualBtn.onclick = async () => {
      const provider = (manualProvider || {}).value;
      const rawBalance = String((document.getElementById('wallet-manual-balance') || {}).value || '').trim();
      const balance = Number(rawBalance);
      const currency = String((manualCurrency || {}).value || '').trim().toUpperCase();
      if (!provider || rawBalance === '' || !Number.isFinite(balance) || balance < 0 || !currency) {
        showToast('Enter the checked balance and its currency');
        return;
      }
      manualBtn.disabled = true;
      try {
        await api(`/api/finance/wallets/${encodeURIComponent(provider)}/update`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ balance_native: balance, balance_currency: currency }),
        });
        showToast('Checked balance saved — runway recomputed');
        refreshMoneyWallets(State._moneyGen);
      } catch (e) {
        showToast('Balance was not saved — try again');
        manualBtn.disabled = false;
      }
    };
  } catch (e) {
    if (gen !== State._moneyGen) return;
    slot.innerHTML = renderEmptyCard('Ad wallets and tool credits', 'Could not load this right now — try again.');
  }
}

// LAB1 — the operator's leverage experiment (2026-07-09): borrowed
// ₦47.9M from Moniepoint (WCL) to buy LAB1-tagged stock; the card
// scores sales contribution against loan + interest. Backed by
// /api/finance/lab1 (deterministic, TTL-cached server-side).
function renderLab1Card(d) {
  if (!d || !d.ok || !d.verdict) return '';
  const v = d.verdict, loan = d.loan || {}, cohort = d.cohort || {};
  const st = v.status || 'insufficient-data';
  const pill = st === 'winning'
    ? '<span class="delta-pill delta-pill-good">WINNING</span>'
    : st === 'on-track'
      ? '<span class="delta-pill">ON TRACK</span>'
      : st === 'behind'
        ? '<span class="delta-pill delta-pill-bad">BEHIND</span>'
        : '<span class="delta-pill">DATA GAP</span>';
  const contrib = v.est_contribution_ngn;
  const interest = v.projected_total_interest_ngn;
  const ratio = (contrib != null && interest)
    ? (contrib / interest).toFixed(2) : null;
  const monthly = cohort.monthly || [];
  const maxRev = Math.max(1, ...monthly.map(m => m.revenue_ngn || 0));
  const bars = monthly.length
    ? `<svg width="100%" height="44" viewBox="0 0 ${monthly.length * 18} 44" preserveAspectRatio="none" style="display:block; overflow:visible; max-width:${monthly.length * 26}px;">
         ${monthly.map((m, i) => {
           const h = Math.max(2, ((m.revenue_ngn || 0) / maxRev) * 38);
           return `<rect x="${i * 18 + 1}" y="${42 - h}" width="12" height="${h}" rx="2" fill="var(--ok, #4dd6a0)" opacity="${i === monthly.length - 1 ? 1 : 0.7}" />`;
         }).join('')}
       </svg>`
    : '';
  const covPct = (d.margin || {}).cost_coverage_pct;
  return `
    <div class="data-card">
      <div class="data-card-head">
        <span class="data-card-title">LAB1 · loan-funded stock vs loan + interest</span>
        ${pill}
      </div>
      <div class="data-stat-num">${ratio !== null ? `${ratio}<span class="unit-sm">x</span>` : '—'}</div>
      <div class="data-stat-sub">
        est. contribution ${fmtNGN(contrib || 0, true)} vs projected interest ${fmtNGN(interest || 0, true)}
        · ₦${((cohort.window_revenue_ngn || 0) / 1e6).toFixed(1)}M sold since ${loan.disbursed_on || ''}
      </div>
      ${bars}
      <div class="data-stat-sub" style="margin-top:6px;">
        ${cohort.stock_units || 0} units still on shelf (${fmtNGN(cohort.stock_retail_ngn || 0, true)} retail)
        · margin est. from ${covPct != null ? covPct : '?'}% cost coverage
      </div>
      <button class="ask-btn" data-ask="Walk me through the LAB1 experiment — is the loan-funded stock beating the loan plus interest, and what should I fix in the data">Ask EON</button>
    </div>
  `;
}

async function refreshMoneyLab1(gen = State._moneyGen) {
  const slot = document.getElementById('money-lab1');
  if (!slot) return;
  try {
    const d = await api('/api/finance/lab1');
    if (gen !== State._moneyGen) return;
    const html = renderLab1Card(d);
    slot.innerHTML = html
      || renderEmptyCard('LAB1', 'No data for this test yet.');
  } catch (e) {
    if (gen !== State._moneyGen) return;
    slot.innerHTML = renderEmptyCard('LAB1', 'Could not load the experiment scoreboard.');
  }
}

// Pricing OS (mig 441, 2026-07-09): tier-IMU targets on true landed
// cost. The card surfaces the weekly drift tray; approve = the ONLY
// path to a live Shopify price (snapshot-backed, rollback-able), so
// taps here are one-click by design — no confirm() ever.
function renderPricingCard(d) {
  if (!d || !d.ok) return '';
  const p = d.params || {};
  const tiers = p.tiers || {};
  const fx = p.fx || {};
  const fr = p.freight || {};
  const tierLine = Object.entries(tiers)
    .map(([k, v]) => `${k} ${(v * 100).toFixed(0)}%`).join(' · ');
  const paramLine = `FX ₦${fx.usd_ngn || '?'}${fx.spread_pct ? ` +${(fx.spread_pct * 100).toFixed(0)}% spread` : ''}
    · freight $${fr.usd_per_kg || '?'}/kg + ₦${fr.ngn_per_kg || '?'}/kg · IMU ${tierLine}`;
  const props = d.proposals || [];
  if (!props.length) {
    return `
      <div class="data-card">
        <div class="data-card-head">
          <span class="data-card-title">Pricing OS</span>
          <span class="delta-pill delta-pill-good">NO DRIFT</span>
        </div>
        <div class="data-stat-sub">Every active SKU is at or above its tier target.</div>
        <div class="data-stat-sub" style="margin-top:6px; opacity:0.7;">${paramLine}</div>
        <button class="ask-btn" data-ask="Explain my Pricing OS parameters — the tier IMU targets, freight and FX assumptions — and whether any need updating">Ask EON</button>
      </div>`;
  }
  const rows = props.slice(0, 10).map(r => `
    <tr data-pid="${r.id}">
      <td style="padding:4px 8px 4px 0; font-weight:600; max-width:150px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${(r.product_title || r.sku || '').replace(/</g, '&lt;')}</td>
      <td style="padding:4px 8px;">${r.tier || ''}${(r.demand || {}).band ? ' · ' + r.demand.band : ''}${r.cost_source === 'default' ? '<span style="opacity:0.55;" title="estimated cost — the factory hasn’t confirmed it">*</span>' : ''}</td>
      <td style="padding:4px 8px; text-align:right;">${fmtNGN(r.current_price_ngn, true)}</td>
      <td style="padding:4px 8px; text-align:right; font-weight:600;" title="${(r.demand || {}).final_target_ngn && r.demand.final_target_ngn > r.proposed_price_ngn ? 'step toward final target ' + fmtNGN(r.demand.final_target_ngn, true) : 'full tier target'}">${fmtNGN(r.proposed_price_ngn, true)}${(r.demand || {}).final_target_ngn && r.demand.final_target_ngn > r.proposed_price_ngn ? '<span style="opacity:0.55;">→' + fmtNGN(r.demand.final_target_ngn, true) + '</span>' : ''}</td>
      <td style="padding:4px 8px; text-align:right;">${r.units_90d || 0}</td>
      <td style="padding:4px 0 4px 8px; text-align:right; white-space:nowrap;">
        ${State.member?.role === 'owner' ? `
        <button class="rec-btn rec-btn-primary" type="button" data-approve="${r.id}">Approve</button>
        <button class="rec-btn" type="button" data-reject="${r.id}">Skip</button>` : ''}
      </td>
    </tr>`).join('');
  // Wave-1 403 sweep: /pricing/proposals/decide is owner-only server-side
  // — managers used to see Approve, tap it, and get a 403 toast. Buttons
  // now render only for the role that can actually use them.
  const prcIsOwner = State.member?.role === 'owner';
  return `
    <div class="data-card">
      <div class="data-card-head">
        <span class="data-card-title">Pricing OS · ${d.open_count} ready to raise</span>
        <span class="delta-pill">+${fmtNGN(d.est_uplift_90d_ngn || 0, true)} / 90d</span>
      </div>
      <div style="overflow-x:auto;">
        <table style="width:100%; border-collapse:collapse; font-size:12.5px;">
          <thead><tr style="opacity:0.6; text-align:right;">
            <th style="text-align:left; padding:0 8px 6px 0;">product</th>
            <th style="text-align:left; padding:0 8px 6px;">tier</th>
            <th style="padding:0 8px 6px;">now</th>
            <th style="padding:0 8px 6px;">target</th>
            <th style="padding:0 8px 6px;">90d units</th>
            <th style="padding:0 0 6px 8px;"></th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      <div class="data-stat-sub" style="margin-top:6px; opacity:0.7;">
        ${paramLine} · * = inferred cost · every push is snapshot-backed and revertible
      </div>
      ${(d.by_action || {}).clearance || (d.by_action || {}).hold ? `
      <div class="data-stat-sub" style="margin-top:4px;">
        demand layer held back ${(d.by_action || {}).clearance || 0} clearance candidates (slow/dead stock — bundle or gift, don't raise)
        ${(d.by_action || {}).hold ? ` and ${(d.by_action || {}).hold} discount-dependent holds` : ''} — these never push.
      </div>` : ''}
      <div style="display:flex; gap:8px; margin-top:8px; flex-wrap:wrap;">
        ${prcIsOwner
          ? `<button class="rec-btn rec-btn-primary" type="button" data-approve-all="1">Approve all ${d.open_count}</button>`
          : '<span class="data-stat-sub" style="opacity:0.7;">reprice approval is owner-only</span>'}
        <button class="ask-btn" style="margin-top:0;" data-ask="Walk me through the open reprice proposals — which tiers drifted, what would approving everything do to revenue, and which rows look wrong">Ask EON</button>
      </div>
    </div>
  `;
}

async function refreshMoneyPricing(gen = State._moneyGen) {
  const slot = document.getElementById('money-pricing');
  if (!slot) return;
  try {
    const d = await api('/api/finance/pricing');
    if (gen !== State._moneyGen) return;
    slot.innerHTML = renderPricingCard(d)
      || renderEmptyCard('Pricing OS', 'Pricing data unavailable.');
    const decide = async (ids, action, btn) => {
      btn.disabled = true;
      btn.textContent = action === 'approve' ? 'Pushing…' : '…';
      try {
        await api('/api/finance/pricing/proposals/decide', {
          method: 'POST',
          body: JSON.stringify({ ids, action }),
        });
      } catch (err) {
        showToast(err && err.message ? err.message : 'Decision failed');
      }
      refreshMoneyPricing(gen);
    };
    const openIds = (d.proposals || []).map(r => r.id);
    slot.querySelectorAll('[data-approve]').forEach(btn => {
      btn.addEventListener('click', () => decide([btn.dataset.approve], 'approve', btn));
    });
    slot.querySelectorAll('[data-reject]').forEach(btn => {
      btn.addEventListener('click', () => decide([btn.dataset.reject], 'reject', btn));
    });
    slot.querySelectorAll('[data-approve-all]').forEach(btn => {
      btn.addEventListener('click', () => decide(openIds, 'approve', btn));
    });
  } catch (e) {
    if (gen !== State._moneyGen) return;
    slot.innerHTML = renderEmptyCard('Pricing OS', 'Could not load pricing drift.');
  }
}

// PR MONEY-COCKPIT: `gen` is the refreshMoney generation token —
// defaults to the current one so standalone callers (snapshot modal,
// bank uploader) keep working unchanged.
async function refreshMoneyInsights(gen = State._moneyGen) {
  const slot = $('#money-insights-grid');
  if (!slot) return;
  const paintMomentum = expenses => mountMoneyMomentum(
    document.getElementById('money-momentum'), expenses, {retry: () => refreshMoney()});
  try {
    // PR MONEY-COCKPIT: single period contract — the window now comes
    // from moneyPeriodToWindow (the same resolver the hero + cash legs
    // use). The old local map here lacked `mtd` and `custom:` so both
    // silently fell to 7d while the hero honoured the chip.
    const w = moneyPeriodToWindow(State.moneyPeriod || 'this_month');
    const winEl = document.getElementById('money-insights-window');
    if (winEl) winEl.textContent = w.label;
    const expenseDays = w.days;
    const data = await api(`/api/finance/money/insights?expense_days=${expenseDays}`);
    if (gen !== State._moneyGen) return;  // superseded while in flight
    if (!data || !data.ok) {
      paintMomentum(null);
      // Wave-1 error≠empty: a failed insights payload is an OUTAGE, not
      // an empty state — say so and offer a retry instead of a generic
      // empty card that reads as "nothing to see here".
      const why = String((data && data.error) || 'request failed').slice(0, 120);
      slot.innerHTML = _moneyFailCard('Insights', why);
      const vs = document.getElementById('money-valuation');
      if (vs) vs.innerHTML = _moneyFailCard('Business valuation', why);
      // Charts ride this payload too — if the summary leg also has
      // nothing, stop their skeletons (wave 1).
      if (!State._moneySummary?.ok) renderMoneyChartsError(why);
      // Don't let a previous period's digest alerts outlive their data.
      _updateMoneyDigest('insights', []);
      _hydrateMoneySafetyTiles({ outage: true });
      return;
    }
    // Money-page redesign 2026-06-07: stash the insights payload so the
    // Trends charts can draw from the same daily series without a
    // second round-trip, then render the charts (summary may already
    // be cached from bindMoney).
    State._moneyInsights = data;
    paintMomentum(data.expenses);
    if (typeof renderMoneyCharts === 'function') {
      renderMoneyCharts(data, State._moneySummary || null);
    }
    // PR MONEY-COCKPIT: hydrate the safety KPI minis + the digest
    // strip from the same payload (no extra round-trips), then render
    // the insight cards worst-first (STATUS_RANK idiom from store
    // economics — problems float to the top instead of a fixed order
    // burying a breached floor under a neutral expenses tile).
    _hydrateMoneySafetyTiles(data);
    _updateMoneyDigest('insights', _moneyInsightsDigestItems(data));

    const rank = _moneyCardRanks(data);
    // 2026-06-05 Sprint 1 tiles + liquidity/covenant. When BOTH the
    // balance-sheet cards (liquidity + covenants) are all-green they
    // merge into one quiet "Balance-sheet health · OK" line — two
    // full cards saying "nothing to see" is noise.
    const bsMerged = _renderBalanceSheetHealthLine(
      data.liquidity_ratios, data.loan_covenant);
    // Wave-1 error≠empty: every renderXCard returns '' when its leg has
    // ok:false — a timed-out payroll pipeline made the Payroll card
    // silently VANISH, indistinguishable from "not applicable". A leg
    // that exists and failed now renders a compact failed line.
    const failedLeg = (title, leg) => (leg && leg.ok === false) ? `
      <div class="data-card">
        <div class="data-card-head"><span class="data-card-title">${esc(title)}</span>
          <span class="pill pill-danger">Error</span></div>
        <div class="data-stat-sub">Couldn't compute — ${esc(String(leg.error || 'error').slice(0, 100))}
          <button class="hero-retry-btn" onclick="refreshMoney()">Retry</button></div>
      </div>` : '';
    const cards = [
      { rank: rank.expenses, html: renderExpensesCard(data.expenses)
          || failedLeg('Expenses', data.expenses) },
      { rank: rank.payroll,  html: renderPayrollDueCard(data.payroll_due)
          || failedLeg('Payroll due', data.payroll_due) },
      { rank: rank.daysCash, html: renderDaysCashCard(data.days_cash)
          || failedLeg('Days of cash', data.days_cash) },
      ...(bsMerged
        ? [{ rank: 3, html: bsMerged }]
        : [
            { rank: rank.covenant,  html: renderLoanCovenantCard(data.loan_covenant)
                || failedLeg('Loan covenants', data.loan_covenant) },
            { rank: rank.liquidity, html: renderLiquidityCard(data.liquidity_ratios)
                || failedLeg('Liquidity', data.liquidity_ratios) },
          ]),
      { rank: rank.floor, html: renderEmergencyFloorCard(data.emergency_floor)
          || failedLeg('Emergency floor', data.emergency_floor) },
    ].filter(c => c.html);
    cards.sort((a, b) => a.rank - b.rank);  // stable — ties keep order
    slot.innerHTML = cards.length
      ? cards.map(c => c.html).join('')
      : renderEmptyCard('Insights', 'No insights available yet — log some transactions and check back.');
    slot.querySelectorAll('[data-money-cash-check]').forEach(button =>
      button.addEventListener('click', openMoneyCashAccounts));

    // PR money-valuation-tile-restore: hydrate the dedicated Business
    // Valuation block. PR MONEY-COCKPIT: progressive disclosure — the
    // full ~600px card collapses to a one-liner (prc-pack details
    // pattern); expanding reveals the existing full breakdown.
    const valSlot = document.getElementById('money-valuation');
    if (valSlot) {
      const valHtml = renderValuationCard(data.valuation);
      if (valHtml) {
        const rng = (data.valuation && data.valuation.range) || {};
        const sumBits = [`Valuation · base ${formatNaira(rng.base_ngn, true)}`];
        if (rng.low_ngn != null && rng.stretch_ngn != null) {
          sumBits.push(`range ${formatNaira(rng.low_ngn, true)}–${formatNaira(rng.stretch_ngn, true)}`);
        }
        valSlot.innerHTML = `
          <details class="prc-pack money-val-pack">
            <summary class="prc-pack-sum">${sumBits.join(' · ')}</summary>
            <div class="prc-pack-body">${valHtml}</div>
          </details>`;
      } else {
        valSlot.innerHTML = renderEmptyCard('Business valuation',
          'Valuation unavailable right now — needs cash, inventory, and revenue data to compute.');
      }
    }

  } catch (e) {
    console.warn('refreshMoneyInsights failed', e);
    if (gen !== State._moneyGen) return;  // superseded — don't paint stale errors
    paintMomentum(null);
    // Wave-1 error≠empty: a thrown fetch is an outage — error card with
    // retry, never a generic empty card (same treatment as ok:false).
    const why = String(e?.message || e || 'request failed').slice(0, 120);
    slot.innerHTML = _moneyFailCard('Insights', why);
    const vs = document.getElementById('money-valuation');
    if (vs) vs.innerHTML = _moneyFailCard('Business valuation', why);
    if (!State._moneySummary?.ok) renderMoneyChartsError(why);
    _updateMoneyDigest('insights', []);
    _hydrateMoneySafetyTiles({ outage: true });
  }
}

// ── PR MONEY-COCKPIT — insights-derived cockpit helpers ─────────────

// True when the backend flagged the payload's cash leg as unknown
// (PR-1 adds `cash_unknown: true` to payroll_due / days_cash /
// emergency_floor when the cash position couldn't be read). Guarded
// defensively — the flag may not exist yet.
function _moneyCashUnknown(p) {
  return !!(p && p.cash_unknown === true);
}

// Worst-first ranks for the insights grid (STATUS_RANK idiom from
// store economics): 0 = red, 1 = amber, 2 = neutral, 3 = green/quiet.
function _moneyCardRanks(data) {
  const pd = data.payroll_due || {};
  const dc = data.days_cash || {};
  const lc = data.loan_covenant || {};
  const ef = data.emergency_floor || {};
  const liq = data.liquidity_ratios || {};
  const covRisk = !lc.is_debt_free && (lc.facilities || []).some(f =>
    f.dscr_status === 'covenant_risk' || f.cash_covers_next_payment === false);
  const covTight = !lc.is_debt_free && (lc.facilities || []).some(f =>
    f.dscr_status === 'tight');
  const liqLow = !liq.is_debt_free && liq.status !== 'debt_free' && (
    (liq.current_ratio != null && liq.current_ratio < 1) ||
    (liq.cash_ratio != null && liq.cash_ratio < 0.2));
  return {
    expenses: 2,
    payroll:  (!_moneyCashUnknown(pd) && pd.ok && pd.cash_covers === false) ? 0 : 2,
    daysCash: _moneyCashUnknown(dc) ? 2
              : dc.status === 'critical' ? 0
              : dc.status === 'warning'  ? 1 : 3,
    covenant: covRisk ? 0 : covTight ? 1 : 3,
    liquidity: liqLow ? 1 : 3,
    floor: _moneyCashUnknown(ef) ? 2
           : ef.is_breached ? 0
           : (ef.cushion_days != null && ef.cushion_days < 30) ? 1 : 3,
  };
}

// One digest line per red/amber state the cards below already compute.
// cash_unknown payloads contribute NOTHING — unknown is not critical.
function _moneyInsightsDigestItems(data) {
  const items = [];
  const ef = data.emergency_floor;
  const pd = data.payroll_due;
  const dc = data.days_cash;
  const lc = data.loan_covenant;
  if (ef?.ok && !_moneyCashUnknown(ef) && ef.is_breached) {
    items.push({
      sev: 'danger',
      line: `Emergency floor breached — cash ${_moneyFmtNGN(ef.cash_today_ngn)} vs floor ${_moneyFmtNGN(ef.floor_ngn)}`,
      target: '#money-card-floor',
    });
  }
  if (pd?.ok && !_moneyCashUnknown(pd) && pd.cash_covers === false) {
    items.push({
      sev: 'danger',
      line: `Payroll ${_moneyFmtNGN(pd.total_due_ngn)} due in ${pd.days_until_eom}d — cash short`,
      target: '#money-card-payroll',
    });
  }
  if (dc?.ok && !_moneyCashUnknown(dc)
      && (dc.status === 'critical' || dc.status === 'warning')) {
    items.push({
      sev: dc.status === 'critical' ? 'danger' : 'warn',
      line: `${dc.days_cash_on_hand ?? '—'} days cash on hand vs ${dc.red_threshold_days}d floor`,
      target: '#money-card-dayscash',
    });
  }
  if (lc?.ok && !lc.is_debt_free) {
    (lc.facilities || []).forEach(f => {
      if (f.dscr_status === 'covenant_risk' || f.cash_covers_next_payment === false) {
        items.push({
          sev: 'danger',
          line: `Loan pressure — ${f.name || f.counterparty || 'facility'}: cash covers repayments only ${f.dscr ?? '—'}x over${f.cash_covers_next_payment === false ? ' · the next payment is not covered' : ''}`,
          target: '#money-card-covenant',
        });
      }
    });
  }
  return items;
}

// When BOTH balance-sheet cards are all-green, merge them into one
// quiet line. Returns '' when either has something to say (the full
// cards render instead).
function _renderBalanceSheetHealthLine(liq, cov) {
  if (!liq?.ok || !cov?.ok) return '';
  const liqGreen = liq.is_debt_free === true || liq.status === 'debt_free' ||
    ((liq.current_ratio ?? 0) >= 2 && (liq.quick_ratio ?? 0) >= 1 &&
     (liq.cash_ratio ?? 0) >= 0.5);
  const covGreen = cov.is_debt_free === true ||
    (cov.facilities || []).every(f =>
      f.dscr_status !== 'covenant_risk' && f.dscr_status !== 'tight' &&
      f.cash_covers_next_payment !== false);
  if (!liqGreen || !covGreen) return '';
  const detail = cov.is_debt_free
    ? 'Debt-free · liquidity healthy · covenant tracking inactive'
    : 'Liquidity healthy · covenants clear';
  return `
    <div class="data-card" id="money-card-balance">
      <div class="data-card-head">
        <span class="data-card-title">Balance-sheet health</span>
        <span class="pill pill-ok" style="font-size:11px;">OK</span>
      </div>
      <div style="font-size:12px; color:var(--ink-soft); margin-top:8px;">${esc(detail)}</div>
      <button class="ask-btn" data-ask="Give me the full liquidity and loan-covenant picture">Ask EON</button>
    </div>
  `;
}

// Row-1 safety minis in the KPI band: Days-cash vs floor + Next
// obligation (payroll). Fed from the same /money/insights payload as
// the full cards — no extra round-trips.
function _hydrateMoneySafetyTiles(data) {
  const dc = data.days_cash;
  const dcNum = document.getElementById('money-kpi-dayscash-num');
  const dcSub = document.getElementById('money-kpi-dayscash-sub');
  if (dcNum) {
    if (dc?.ok && _moneyCashUnknown(dc)) {
      dcNum.textContent = '—';
      dcNum.classList.remove('tone-ok', 'tone-warn', 'tone-danger');
      if (dcSub) dcSub.innerHTML = '<span class="pill" style="font-size:10px;">we can\'t see the account balance</span>';
    } else if (dc?.ok && dc.days_cash_on_hand != null) {
      const diff = (dc.days_cash_on_hand || 0) - (dc.red_threshold_days || 60);
      dcNum.textContent = `${dc.days_cash_on_hand}d`;
      dcNum.classList.toggle('tone-danger', dc.status === 'critical');
      dcNum.classList.toggle('tone-warn', dc.status === 'warning');
      dcNum.classList.toggle('tone-ok', dc.status !== 'critical' && dc.status !== 'warning');
      if (dcSub) dcSub.textContent =
        `vs ${dc.red_threshold_days}d floor · ${diff >= 0 ? '+' : ''}${diff}d`;
    } else {
      dcNum.textContent = '—';
      if (dcSub) dcSub.textContent = data?.outage ? 'Days of cash could not be read just now.' : 'Days of cash could not be established.';
    }
  }
  const pd = data.payroll_due;
  const pdNum = document.getElementById('money-kpi-payroll-num');
  const pdSub = document.getElementById('money-kpi-payroll-sub');
  if (pdNum) {
    if (pd?.ok && _moneyCashUnknown(pd)) {
      // Payroll total is still known — only the cash-cover side isn't.
      pdNum.textContent = formatNaira(pd.total_due_ngn, true);
      if (pdSub) pdSub.innerHTML =
        `Payroll · ${pd.days_until_eom} days to month-end · <span class="pill" style="font-size:10px;">we can't see the account balance</span>`;
    } else if (pd?.ok) {
      pdNum.textContent = formatNaira(pd.total_due_ngn, true);
      const coverPill = pd.cash_covers
        ? '<span class="pill pill-ok" style="font-size:10px;">covers</span>'
        : '<span class="pill pill-danger" style="font-size:10px;">cash short</span>';
      if (pdSub) pdSub.innerHTML = `Payroll · ${pd.days_until_eom} days to month-end · ${coverPill}`;
    } else {
      pdNum.textContent = '—';
      if (pdSub) pdSub.textContent = data?.outage ? 'The next obligation could not be read just now.' : 'The next obligation could not be established.';
    }
  }
}

// v3.0: Single MER hero — one big number with prior-period context
function renderMerHero(t) {
  if (lw.active() && t.period_start && t.period_end && String(State.homePeriod).startsWith('last_')) t = { ...t, label: t.label + ' · completed days' };
  if (!t.available) {
    return `
      <div class="mh-hero">
        <div class="mh-hero-top">
          <div class="mh-hero-label">${esc(t.label || 'MER')} · Marketing efficiency</div>
          <span class="mh-pill mh-pill-warn">No data</span>
        </div>
        <div class="mh-hero-fig">—<span class="unit">x</span></div>
        <div class="mh-note" style="margin-top:12px">${esc(t.note || 'No data for this period')}</div>
      </div>`;
  }
  const pillCls = t.status === 'DANGER' ? 'mh-pill-danger'
                : t.status === 'WARN' ? 'mh-pill-warn'
                : t.status === 'INVESTIGATE' ? 'mh-pill-warn'
                : t.status === 'SCALE' ? 'mh-pill-scale'
                : 'mh-pill-ok';
  const rev = fmtNGN(t.revenue_ngn);
  const spend = fmtNGN(t.spend_ngn);

  // Delta chips — mint if good, amber if bad, dim if flat, hidden if no prior data.
  // Revenue & MER: up is good. Spend: up is concerning (invertColor). Filled
  // triangles render reliably across iOS/Android/desktop unlike thin arrows.
  const pill = (pct, invertColor = false) => {
    if (pct === null || pct === undefined) return '';
    if (Math.abs(pct) < 0.5) return ` <span class="mh-delta mh-delta-flat">— 0%</span>`;
    const isUp = pct > 0;
    const isGood = invertColor ? !isUp : isUp;
    const cls = isGood ? 'mh-delta-good' : 'mh-delta-bad';
    const arrow = isUp ? '▲' : '▼';
    return ` <span class="mh-delta ${cls}">${arrow} ${Math.abs(pct).toFixed(0)}%</span>`;
  };

  let revDelta = '', spendDelta = '', merDelta = '';
  if (t.deltas) {
    revDelta = pill(t.deltas.revenue_pct, false);
    spendDelta = pill(t.deltas.spend_pct, true);
    merDelta = pill(t.deltas.mer_pct, false);
  }

  // P2291: too little ad spend to judge (under ₦1 per ₦100 of sales, zero
  // included). Plain sentence, the naira, no x figure and no MER chip. The
  // plain empty-state label keeps the unknown result readable.
  if (t.judgeable === false) {
    const floorHtml = t.spend_floor_ngn > 0
      ? `<div class="mh-note">It is judged once ads reach ₦1 for every ₦100 of sales: ${fmtNGN(t.spend_floor_ngn)} this period.</div>`
      : '';
    return `
    <div class="mh-hero">
      <div class="mh-hero-top">
        <div class="mh-hero-label">${esc(t.label || 'MER')} · Marketing efficiency</div>
        ${deskHouse()
          ? `<span class="hs-stamp hs-stamp-in mh-hero-stamp">Too little spend</span>`
          : `<span class="mh-pill mh-pill-warn">Too little spend</span>`}
      </div>
      <div class="mh-hero-fig" data-unjudged>Not judged yet</div>
      <div class="mh-hero-note">${esc(t.judge_note || 'Too little ad spend this period to judge marketing efficiency.')}</div>
      <div class="mh-hero-stats">
        ${lw.active()?'<button type="button" class="mh-hg hf-metric-source" data-lw="inspect" data-source="revenue">':'<div class="mh-hg">'}<span class="mh-hg-v">${rev}${revDelta}</span><span class="mh-hg-l">Revenue</span>${lw.active()?'</button>':'</div>'}
        ${lw.active()?'<button type="button" class="mh-hg hf-metric-source" data-lw="inspect" data-source="spend">':'<div class="mh-hg">'}<span class="mh-hg-v">${spend}${spendDelta}</span><span class="mh-hg-l">Ad spend</span>${lw.active()?'</button>':'</div>'}
        ${lw.active()?'<button type="button" class="mh-hg hf-metric-source" data-lw="inspect" data-source="orders">':'<div class="mh-hg">'}<span class="mh-hg-v">${fmtNum(t.order_count)}</span><span class="mh-hg-l">Orders</span>${lw.active()?'</button>':'</div>'}
      </div>
      ${floorHtml}
    </div>`;
  }

  // status_reason is set when MER status was overridden (e.g., revenue declining
  // despite high MER). Surface this prominently — it's the actionable signal.
  const statusReasonHtml = t.status_reason
    ? `<div class="mh-hero-note">${esc(t.status_reason)}</div>`
    : '';

  // Blended MER (incl influencer/offline spend) — the truer efficiency. Only
  // rendered when custom ad spend exists, so the tile is unchanged otherwise.
  const blendedHtml = (t.blended_value != null && (t.custom_ad_spend_ngn || 0) > 0)
    ? `<div class="mh-hg"><span class="mh-hg-v">${t.blended_value.toFixed(2)}x</span><span class="mh-hg-l">Blended · incl ${fmtNGN(t.custom_ad_spend_ngn)} influencer/offline</span></div>`
    : '';
  // P2291: platform ads alone were under the floor, so the headline is the
  // blended ratio (all ad spend). Say so; the blended row shows what it holds.
  const basisHtml = t.value_basis === 'blended'
    ? `<div class="mh-note">${esc(t.basis_note || 'This counts all ad spend, influencers and offline included.')}</div>`
    : '';

  return `
    <div class="mh-hero">
      <div class="mh-hero-top">
        <div class="mh-hero-label">${esc(t.label || 'MER')} · Marketing efficiency</div>
        ${deskHouse()
          ? `<span class="hs-stamp hs-stamp-in mh-hero-stamp">${esc(t.status)}</span>`
          : `<span class="mh-pill ${pillCls}">${esc(t.status)}</span>`}
      </div>
      <div class="mh-hero-fig"><span class="hero-num-val" data-countup="${t.value.toFixed(2)}" data-decimals="2">${t.value.toFixed(2)}</span><span class="unit">x</span>${merDelta}</div>
      <div class="mh-hero-stats">
        ${lw.active()?'<button type="button" class="mh-hg hf-metric-source" data-lw="inspect" data-source="revenue">':'<div class="mh-hg">'}<span class="mh-hg-v">${rev}${revDelta}</span><span class="mh-hg-l">Revenue</span>${lw.active()?'</button>':'</div>'}
        ${lw.active()?'<button type="button" class="mh-hg hf-metric-source" data-lw="inspect" data-source="spend">':'<div class="mh-hg">'}<span class="mh-hg-v">${spend}${spendDelta}</span><span class="mh-hg-l">Ad spend</span>${lw.active()?'</button>':'</div>'}
        ${lw.active()?'<button type="button" class="mh-hg hf-metric-source" data-lw="inspect" data-source="orders">':'<div class="mh-hg">'}<span class="mh-hg-v">${fmtNum(t.order_count)}</span><span class="mh-hg-l">Orders</span>${lw.active()?'</button>':'</div>'}
        ${blendedHtml}
      </div>
      ${statusReasonHtml}${basisHtml}
    </div>`;
}

// P2291: the hero card's floor (mer_readability.py MIN_SPEND_SHARE), one copy
// for the chart header and its per-day hover: ₦1 of ads per ₦100 of sales.
function merSpendJudgeable(rev, spend) {
  const MIN_SPEND_SHARE = 0.01;
  return spend > 0 && spend >= rev * MIN_SPEND_SHARE;
}

function renderChartShell(chart) {
  if (!chart || !chart.available || !chart.labels || chart.labels.length === 0) {
    return ''; // hide cleanly if no data
  }
  // v3.0p15 commit 9a item 4: chart title now reflects the selected period
  // instead of being hardcoded "Last 30 days." Backend sends period_label.
  const label = chart.period_label || `Last ${chart.labels.length} days`;
  // Period totals double as the chart legend — the operator reads the
  // headline numbers without hovering. Ad spend gets its own swatch so the
  // dual-scale series (right axis) is unmistakable, never hidden.
  const totRev = (chart.revenue || []).reduce((a, b) => a + (Number(b) || 0), 0);
  const totSpend = (chart.spend || []).reduce((a, b) => a + (Number(b) || 0), 0);
  // Period MER as a third headline figure — the chart's decision number:
  // "is this period's spend actually paying back?" P2291: under the hero
  // card's floor, or no spend, no x figure. The naira stay.
  const mer = merSpendJudgeable(totRev, totSpend) ? (totRev / totSpend) : null;
  const merFig = mer !== null
    ? `<div class="mh-chart-fig"><span class="v ${mer >= 4 ? 'rev' : 'spend'}">${mer.toFixed(1)}x</span><span class="l">MER</span></div>`
    : '';
  return `
    <div class="mh-chart">
      <div class="mh-chart-head">
        <div class="mh-chart-title">Sales &amp; spend<span class="sub">${esc(label)}</span></div>
        <div class="mh-chart-figs">
          <div class="mh-chart-fig"><span class="v rev">${fmtNGN(totRev)}</span><span class="l"><span class="dot" style="background:var(--ok,#56E39F)"></span>Revenue</span></div>
          <div class="mh-chart-fig"><span class="v spend">${fmtNGN(totSpend)}</span><span class="l"><span class="dot" style="background:var(--warn,#F2A24C)"></span>Ad spend</span></div>
          ${merFig}
        </div>
      </div>
      <div class="mh-chart-canvas"><canvas id="sales-spend-chart"></canvas></div>
    </div>`;
}

let _chartInstance = null;
let _chartScriptLoading = false;
function drawSalesSpendChart(chart, quiet = false) {
  if (!chart || !chart.available) return;
  // Lazy-load Chart.js if not present
  if (typeof Chart === 'undefined') {
    if (_chartScriptLoading) return;  // Already loading
    _chartScriptLoading = true;
    const s = document.createElement('script');
    // WS-B (2026-07-26): vendored (was cdnjs Chart.js 4.5.0). A blocked
    // or offline CDN silently deleted the chart, collapsing 230px of
    // layout; a local asset rides the SW cache like every other shell
    // file. Same build, byte-for-byte.
    s.src = '/static/vendor_chartjs.min.js';
    const current = lw.captureRead('home', false);
    s.onload = () => { _chartScriptLoading = false; if (current()) drawSalesSpendChart(lw.active() ? lw.homeChart() : chart, quiet); };
    s.onerror = () => {
      _chartScriptLoading = false;
      console.warn('Chart.js failed to load — hiding chart');
      const slot = document.getElementById('chart-slot');
      if (slot) slot.innerHTML = '';
    };
    document.head.appendChild(s);
    return;
  }
  const canvas = document.getElementById('sales-spend-chart');
  if (!canvas) return;
  if (_chartInstance) { try { _chartInstance.destroy(); } catch(_) {} _chartInstance = null; }

  // 2026-06-09 redesign: chart lives inside the dark .mk-home3 shell now, so
  // the a3 palette is hard-coded — the --a3-* tokens are scoped to .mk-ads3 and
  // do NOT resolve off document.documentElement. green = revenue, amber = ad
  // spend; same colour discipline as the Customers tab.
  const GREEN = '#d4ff3a', AMBER = '#ffb347', MUT = '#9A9AA2';
  const lineSoft = 'rgba(255,255,255,0.055)';

  // Scriptable vertical gradients — built once the chartArea exists.
  const grad = (area, ctx2d, stops) => {
    const g = ctx2d.createLinearGradient(0, area.top, 0, area.bottom);
    stops.forEach(([o, c]) => g.addColorStop(o, c));
    return g;
  };
  const revFill = (c) => {
    const a = c.chart.chartArea;
    if (!a) return 'rgba(212,255,58,0.12)';
    return grad(a, c.chart.ctx, [[0, 'rgba(212,255,58,0.34)'], [0.55, 'rgba(212,255,58,0.10)'], [1, 'rgba(212,255,58,0.01)']]);
  };
  const spendFill = (c) => {
    const a = c.chart.chartArea;
    if (!a) return 'rgba(255,179,71,0.6)';
    return grad(a, c.chart.ctx, [[0, 'rgba(255,179,71,0.92)'], [1, 'rgba(255,179,71,0.34)']]);
  };

  // Soft glow under the revenue line only (bars stay crisp).
  const glowPlugin = {
    id: 'mhRevGlow',
    beforeDatasetDraw(c, args) {
      if (args.meta.type === 'line') {
        c.ctx.save();
        c.ctx.shadowColor = 'rgba(212,255,58,0.45)';
        c.ctx.shadowBlur = 16;
        c.ctx.shadowOffsetY = 5;
      }
    },
    afterDatasetDraw(c, args) {
      if (args.meta.type === 'line') c.ctx.restore();
    },
  };

  // UI-wave 2026-06-11: crosshair on hover — a dashed vertical line tracks
  // the active day so the eye can connect the revenue point to the spend
  // bar underneath. Essential on touch where there is no persistent cursor.
  const crosshairPlugin = {
    id: 'mhCrosshair',
    afterDatasetsDraw(c) {
      const act = c.tooltip && c.tooltip._active;
      if (!act || !act.length) return;
      const x = act[0].element.x;
      const { top, bottom } = c.chartArea;
      const ctx2 = c.ctx;
      ctx2.save();
      ctx2.strokeStyle = 'rgba(255,255,255,0.22)';
      ctx2.lineWidth = 1;
      ctx2.setLineDash([3, 4]);
      ctx2.beginPath();
      ctx2.moveTo(x, top);
      ctx2.lineTo(x, bottom);
      ctx2.stroke();
      ctx2.restore();
    },
  };

  const labels = chart.labels.map(d => {
    const dt = new Date(d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });

  // v3.0p15 commit 9a B3-2: detect narrow viewport for tighter chart layout
  const isNarrow = window.innerWidth < 420;

  _chartInstance = new Chart(canvas.getContext('2d'), {
    type: 'bar',
    data: {
      labels,
      datasets: [
        {
          type: 'line',
          label: 'Revenue',
          data: chart.revenue,
          borderColor: GREEN,
          backgroundColor: revFill,
          tension: 0.4,
          fill: true,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: GREEN,
          pointHoverBorderColor: '#0A0A0C',
          pointHoverBorderWidth: 2,
          borderWidth: 2.5,
          yAxisID: 'y',
        },
        {
          type: 'bar',
          label: 'Spend',
          data: chart.spend,
          backgroundColor: spendFill,
          hoverBackgroundColor: AMBER,
          borderRadius: 5,
          borderSkipped: false,
          maxBarThickness: 26,
          // 2026-05-19 (Daniel): revisited the single-axis decision.
          // Revenue is in millions, spend is in tens-of-thousands —
          // on a shared y axis, spend bars disappear at the zero
          // baseline. Switched back to dual axis (y for revenue
          // left, y1 for spend right) with EXPLICIT unit labels on
          // each axis ("Revenue ₦M" / "Spend ₦k") so the operator
          // can't misread bar heights as comparable magnitudes.
          // Tooltip retains absolute ₦ values for both series so
          // the exact numbers stay accessible.
          yAxisID: 'y1',
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      // 2026-06-09: layered draw-in — bars grow up from the baseline, the
      // revenue line + fill ease in just behind them.
      animation: { duration: quiet || document.documentElement.getAttribute('data-living-motion') === 'paused' || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 1200, easing: 'easeOutQuart' },
      animations: {
        y: {
          from: (ctx) => (ctx.type === 'data' && ctx.mode === 'default')
            ? ctx.chart.scales[ctx.dataset.yAxisID || 'y'].getPixelForValue(0)
            : undefined,
        },
      },
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },   // the totals header in the card IS the legend
        tooltip: {
          backgroundColor: 'rgba(16,16,19,0.96)',
          borderColor: 'rgba(255,255,255,0.10)',
          borderWidth: 1,
          padding: 11,
          cornerRadius: 10,
          titleColor: '#F4F4F2',
          titleFont: { size: 12, weight: '600' },
          bodyColor: '#C9C9CF',
          bodyFont: { size: 12 },
          bodySpacing: 5,
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          footerColor: '#9A9AA2',
          footerFont: { size: 11, weight: '500' },
          footerMarginTop: 7,
          callbacks: {
            label: (ctx) => `  ${ctx.dataset.label}: \u20a6${Math.round(ctx.parsed.y).toLocaleString()}`,
            // Per-day MER footer \u2014 the hover answers "did that day's spend
            // pay back?" without leaving the chart.
            footer: (items) => {
              const i = items[0] ? items[0].dataIndex : -1;
              if (i < 0) return '';
              const rev = Number((chart.revenue || [])[i]) || 0;
              const spend = Number((chart.spend || [])[i]) || 0;
              if (spend <= 0) return rev > 0 ? 'No ad spend this day' : '';
              // P2291: under ₦1 of ads per ₦100 of sales the day's ratio says nothing.
              if (!merSpendJudgeable(rev, spend)) return 'Too little ad spend this day to judge';
              return `MER ${(rev / spend).toFixed(1)}x`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: {
            font: { size: isNarrow ? 9 : 10 },
            color: MUT,
            maxRotation: 0,
            // v3.0p15 commit 9a B3-2: more aggressive label skipping on
            // narrow screens so we don't get overlapping date labels
            autoSkipPadding: isNarrow ? 24 : 12,
            maxTicksLimit: isNarrow ? 5 : 10,
          },
          grid: { display: false },
          border: { color: lineSoft },
        },
        y: {
          // Left axis — revenue (₦M scale)
          position: 'left',
          title: {
            // 390px phones: the colored tick labels carry the unit story;
            // the rotated axis titles just eat plot width. Desktop keeps them.
            display: !isNarrow,
            text: 'Revenue (₦)',
            color: GREEN,
            font: { size: isNarrow ? 9 : 10, weight: '500' },
          },
          ticks: {
            font: { size: isNarrow ? 9 : 10 },
            color: MUT,
            callback: (v) => v >= 1_000_000 ? `${(v/1_000_000).toFixed(1)}M`
                              : v >= 1_000 ? `${Math.round(v/1_000)}k`
                              : v,
            maxTicksLimit: isNarrow ? 4 : 6,
          },
          grid: { color: lineSoft },
          border: { display: false },
          beginAtZero: true,
        },
        y1: {
          // Right axis — ad spend (₦k scale), only visible when chart
          // actually has spend data. Daniel's catch 2026-05-19: with
          // shared axis spend bars vanish at the zero line because
          // they're 1-2% of revenue magnitude. Dual axis with
          // explicit unit labels keeps both readable while making
          // the scale mismatch visually obvious.
          position: 'right',
          title: {
            display: !isNarrow,
            text: 'Ad spend (₦)',
            color: AMBER,
            font: { size: isNarrow ? 9 : 10, weight: '500' },
          },
          ticks: {
            font: { size: isNarrow ? 9 : 10 },
            color: AMBER,
            callback: (v) => v >= 1_000_000 ? `${(v/1_000_000).toFixed(1)}M`
                              : v >= 1_000 ? `${Math.round(v/1_000)}k`
                              : v,
            maxTicksLimit: isNarrow ? 4 : 6,
          },
          grid: { drawOnChartArea: false },   // don't double-up grid lines
          border: { display: false },
          beginAtZero: true,
        },
      },
    },
    plugins: [glowPlugin, crosshairPlugin],
  });
}

// Animated radial target tracker (2026-07-02 homepage redesign). Draws a
// stroke-dash arc on load and drops a tick at the "expected by now" pace angle,
// so the operator SEES at a glance whether the month is ahead or behind — the
// creative centrepiece the flat linear gauge lacked. Pure SVG + CSS keyframe
// (no chart lib; the .mh-ring-prog animation is disabled under reduced-motion).
function renderTargetRing(pct, expectedPct, state) {
  const R = 50, C = 2 * Math.PI * R;            // circumference
  const p = Math.min(100, Math.max(0, Number(pct) || 0));
  const dash = (C * (1 - p / 100)).toFixed(2);  // final dashoffset
  const ep = Math.min(100, Math.max(0, Number(expectedPct) || 0));
  const ang = (-90 + (ep / 100) * 360) * Math.PI / 180;  // 0% at top, clockwise
  const tx = (60 + R * Math.cos(ang)).toFixed(2);
  const ty = (60 + R * Math.sin(ang)).toFixed(2);
  const cls = state === 'behind' ? 'behind' : state === 'ahead' ? 'ahead' : 'onpace';
  const showTick = ep > 1 && ep < 99;
  // HOUSE floor 4 (WS-E): the dial becomes a band. Same two inputs, same
  // call site, same behind/ahead/onpace colours, no data change — a filled
  // bar for progress and a hard notch where today is expected to sit.
  if (deskHouse()) {
    // The fill's width rides --hs-w so the sweep is a state change, not a
    // keyframe: hs-pour holds it at 0 until _bandSettle releases it one
    // frame after mount. Under the 90s tick's .tick-still the transition
    // is suppressed, so a re-render snaps instead of replaying the sweep.
    return `
    <div class="hs-band ${cls} hs-pour" role="img" aria-label="${p.toFixed(0)}% of monthly revenue target">
      <div class="hs-band-fig">
        <span class="hs-band-pct">${p.toFixed(0)}</span><span class="hs-band-u">%</span>
        <span class="hs-band-cap">to target</span>
      </div>
      <div class="hs-band-track">
        <div class="hs-band-fill" style="--hs-w:${p.toFixed(1)}%"></div>
        ${showTick ? `<div class="hs-band-notch" style="left:${ep.toFixed(1)}%"><span class="hs-band-notch-l">expected today</span></div>` : ''}
      </div>
    </div>`;
  }
  return `
    <div class="mh-ring ${cls}" role="img" aria-label="${p.toFixed(0)}% of monthly revenue target">
      <svg viewBox="0 0 120 120" class="mh-ring-svg" aria-hidden="true">
        <circle class="mh-ring-track" cx="60" cy="60" r="${R}"></circle>
        <circle class="mh-ring-prog" cx="60" cy="60" r="${R}"
          style="stroke-dasharray:${C.toFixed(2)}; --c:${C.toFixed(2)}; --dash:${dash}"></circle>
        ${showTick ? `<circle class="mh-ring-tick" cx="${tx}" cy="${ty}" r="3.6"></circle>` : ''}
      </svg>
      <div class="mh-ring-mid">
        <div class="mh-ring-pct">${p.toFixed(0)}<span>%</span></div>
        <div class="mh-ring-cap">to target</div>
      </div>
    </div>`;
}

function renderTargetTile(t) {
  if (!t || !t.available) {
    if (t && t.no_target) {
      return `
        <div class="mh-target" style="cursor:pointer" data-ask="Set my monthly revenue target to ₦80M">
          <div class="mh-target-head">
            <div>
              <div class="a3-label" style="margin-bottom:5px">Monthly revenue target</div>
              <div class="mh-note" style="margin-top:0">No target set — tap to set one via chat.</div>
            </div>
            <span class="mh-ask mh-ask-go">Set target</span>
          </div>
        </div>`;
    }
    return deskHouse() ? '<div class="mh-target mh-target-unavailable"><p class="mh-note" style="margin:0">Monthly target unavailable for this period.</p></div>' : '';
  }
  const pct = Math.min(100, Math.max(0, t.progress_pct));
  const expectedPct = Math.min(100, Math.max(0, t.expected_progress_pct));
  const ahead = t.pace_diff_pct || 0;
  const behind = ahead < -5;
  const paceText = ahead > 5 ? `${ahead.toFixed(1)} pts ahead`
                 : behind ? `${Math.abs(ahead).toFixed(1)} pts behind`
                 : 'On pace';
  const paceClass = behind ? 'mh-pill-danger' : ahead > 5 ? 'mh-pill-scale' : 'mh-pill-ok';
  const fillClass = behind ? 'mh-gauge-fill behind' : 'mh-gauge-fill';
  const building = !!t.building_run_rate;
  const projGap = (t.projection_gap_ngn != null) ? t.projection_gap_ngn : 0;
  const projChip = projGap >= 0 ? `+${fmtNGN(projGap)} over` : `${fmtNGN(Math.abs(projGap))} short`;
  const expNow = (t.expected_value_now_ngn != null) ? fmtNGN(t.expected_value_now_ngn) : '';
  const asOf = (() => {
    // v3.0p30: prefer the data-as-of timestamp stamped by the backend
    // (t.as_of_iso) over the render wall clock — render time lies if the
    // home view re-renders without re-fetching the snapshot.
    if (t.as_of_iso) {
      try { return new Date(t.as_of_iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
      catch (e) { /* fall through */ }
    }
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  })();
  const ringState = behind ? 'behind' : (ahead > 5 ? 'ahead' : 'onpace');
  // Right rail: the three numbers that tell the operator what to DO —
  // projection vs target, current run-rate, and the required daily pace.
  // They used to scatter across full-width rows leaving the card's right
  // half empty (2026-07-23 redesign).
  const railRows = [];
  if (building) {
    railRows.push(`<div class="mh-tgt-stat"><span class="mh-tgt-stat-l">Projection</span><span class="mh-tgt-stat-v" style="color:var(--a3-dim)">builds after day 1</span></div>`);
  } else {
    railRows.push(`<div class="mh-tgt-stat"><span class="mh-tgt-stat-l">Projected</span><span class="mh-tgt-stat-v">${fmtNGN(t.projected_value)} <span class="${projGap >= 0 ? 'mh-tgt-good' : 'mh-tgt-bad'}">${projChip}</span></span></div>`);
    if (t.run_rate_daily_ngn) {
      railRows.push(`<div class="mh-tgt-stat"><span class="mh-tgt-stat-l">Run-rate</span><span class="mh-tgt-stat-v">${fmtNGN(t.run_rate_daily_ngn)}<span class="mh-tgt-stat-u">/day</span></span></div>`);
    }
    if (t.gap_to_target_ngn > 0 && t.needed_daily_ngn != null) {
      railRows.push(`<div class="mh-tgt-stat"><span class="mh-tgt-stat-l">Need</span><span class="mh-tgt-stat-v">${fmtNGN(t.needed_daily_ngn)}<span class="mh-tgt-stat-u">/day · ${t.days_left_incl_today} day${t.days_left_incl_today === 1 ? '' : 's'} left</span></span></div>`);
    } else if (t.gap_to_target_ngn === 0) {
      railRows.push(`<div class="mh-tgt-stat"><span class="mh-tgt-stat-l">Gap</span><span class="mh-tgt-stat-v mh-tgt-good">target hit</span></div>`);
    }
  }
  const liveBits = (t.live_today_revenue_ngn != null && t.live_today_revenue_ngn > 0)
    ? `<span>Live today <b>${fmtNGN(t.live_today_revenue_ngn)}</b>${t.live_today_orders ? ` · ${t.live_today_orders} order${t.live_today_orders === 1 ? '' : 's'}` : ''}${t.live_today_hours_elapsed ? ` · ${t.live_today_hours_elapsed.toFixed(1)}h in` : ''}</span>`
    : '<span></span>';
  return `
    <div class="mh-target">
      <div class="mh-target-head">
        <div class="a3-label" style="margin-bottom:0">Monthly revenue target</div>
        <span class="mh-pill ${paceClass}">${esc(paceText)}</span>
      </div>
      <div class="mh-target-hero">
        ${renderTargetRing(pct, expectedPct, ringState)}
        <div class="mh-target-hero-figs">
          <div class="mh-target-figs">
            <span class="mh-target-actual">${fmtNGN(t.actual_value)}</span>
            <span class="mh-target-of">of ${fmtNGN(t.target_value)}</span>
          </div>
          <div class="mh-target-sub mh-target-sub-col">
            <span>day ${t.days_elapsed}/${t.days_in_month}${expNow ? ` · expected ${expNow}` : ''}</span>
          </div>
          ${(expectedPct > 0 && expectedPct < 100)
            ? `<div class="mh-ring-legend"><span class="mh-ring-legend-dot"></span>pace tick · where today should sit (${expectedPct.toFixed(0)}%)</div>`
            : ''}
        </div>
        ${railRows.length ? `<div class="mh-tgt-rail">${railRows.join('')}</div>` : ''}
      </div>
      <div class="mh-target-foot">
        ${liveBits}
        <span class="mh-target-asof">Updated ${asOf}</span>
      </div>
    </div>`;
}

// ── Home viz primitives (UI-wave 2026-06-11) ────────────────
// Intelligence-grade visual variety for the Signals tiles: SVG delta
// chips (no glyph soup), progress rings, horizontal comparison bars and
// split bars. All pure-SVG/CSS — no chart library cost for tiles.

function _mhDeltaChip(pct, invert = false) {
  if (pct === null || pct === undefined || !Number.isFinite(Number(pct))) return '';
  pct = Number(pct);
  if (Math.abs(pct) < 0.5) {
    return `<span class="mh-dchip mh-dchip-flat"><svg viewBox="0 0 8 8" width="7" height="7" aria-hidden="true"><path d="M1 4h6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>0%</span>`;
  }
  const up = pct > 0;
  const good = invert ? !up : up;
  const arrow = up
    ? '<svg viewBox="0 0 8 8" width="7" height="7" aria-hidden="true"><path d="M4 1l3 5H1z" fill="currentColor"/></svg>'
    : '<svg viewBox="0 0 8 8" width="7" height="7" aria-hidden="true"><path d="M4 7L1 2h6z" fill="currentColor"/></svg>';
  return `<span class="mh-dchip ${good ? 'mh-dchip-good' : 'mh-dchip-bad'}">${arrow}${Math.abs(pct).toFixed(0)}%</span>`;
}

function _mhNumber(value) {
  return value === null || value === undefined || (typeof value === 'string' && !value.trim()) || typeof value === 'boolean' || !Number.isFinite(Number(value)) || Number(value) < 0 ? null : Number(value);
}

function _mhPercent(value) {
  const n = _mhNumber(value);
  return n !== null && n <= 100 ? n : null;
}

function _mhCustomerBreakdown(c) {
  const total = _mhNumber(c.total), first = _mhNumber(c.new), returning = _mhNumber(c.returning);
  const valid = total !== null && first !== null && returning !== null && first <= total && returning <= total && first + returning >= total;
  const overlap = valid ? Math.max(0, first + returning - total) : null;
  return '<div class="mh-customer-bars">' + [['First-time buyers',first],['Returning buyers',returning]].map(([label,count]) => {
    const share = valid && total > 0 ? count / total * 100 : null;
    return `<div class="mh-customer-bar"><div><span>${label}</span><strong>${count === null ? '—' : fmtNum(count)}${share === null ? '' : ' · ' + share.toFixed(1) + '%'}</strong></div><div class="mh-customer-track" aria-hidden="true"><i style="width:${share === null ? 0 : share}%"></i></div></div>`;
  }).join('') + `<p class="mh-chart-note">${!valid ? 'Customer group comparison unavailable.' : total === 0 ? 'No customer activity in this period.' : overlap > 0 ? fmtNum(overlap) + (overlap === 1 ? ' customer appears' : ' customers appear') + ' in both groups. Each bar uses ' + fmtNum(total) + ' unique customers.' : 'Each bar uses ' + fmtNum(total) + ' unique customers.'}</p></div>`;
}

// Small progress ring: pct 0-100, hero value in the middle.
function _mhRing(pct, centre, label, tone) {
  const p = _mhPercent(pct);
  if (window.EONVisuals?.meterHTML) return window.EONVisuals.meterHTML(p, centre, label, tone);
  const r = 25, c = 2 * Math.PI * r;
  const off = (c * (1 - p / 100)).toFixed(1);
  const color = tone === 'warn' ? 'var(--a3-amber)' : tone === 'bad' ? '#ff6b6b' : 'var(--a3-mint)';
  return `
    <div class="mh-sring" role="img" aria-label="${esc(p === null ? label + ': unavailable' : centre + ' ' + label)}">
      <svg viewBox="0 0 60 60" width="60" height="60" aria-hidden="true">
        <circle cx="30" cy="30" r="${r}" fill="none" stroke="rgba(255,255,255,.07)" stroke-width="5"/>
        ${p > 0 ? `<circle cx="30" cy="30" r="${r}" fill="none" stroke="${color}" stroke-width="5"
                stroke-linecap="butt" stroke-dasharray="${c.toFixed(1)}" stroke-dashoffset="${off}"
                transform="rotate(-90 30 30)" class="mh-sring-fg"/>` : ''}
      </svg>
      <div class="mh-sring-c"><span class="mh-sring-v">${centre}</span></div>
      ${label ? `<div class="mh-sring-l">${esc(label)}</div>` : ''}
    </div>`;
}

// Horizontal comparison bars. rows: [{label, display, pct(0-100), delta, deltaInvert, lead}]
function _mhHBars(rows) {
  if (!rows || !rows.length) return '';
  return `<div class="mh-hbars">${rows.map(r => `
    <div class="mh-hbar ${r.lead ? 'lead' : ''}">
      <div class="mh-hbar-top">
        <span class="mh-hbar-k">${esc(r.label)}${r.delta !== undefined ? ' ' + _mhDeltaChip(r.delta, r.deltaInvert) : ''}</span>
        <span class="mh-hbar-v">${r.display}</span>
      </div>
      <div class="mh-hbar-track"><i style="width:${Math.max(2, Math.min(100, r.pct || 0)).toFixed(1)}%"></i></div>
    </div>`).join('')}</div>`;
}

// Two-segment split bar (e.g. new vs returning customers).
function _mhSplitBar(aLabel, aVal, bLabel, bVal) {
  const a = Number(aVal) || 0, b = Number(bVal) || 0;
  const tot = a + b;
  const ap = tot > 0 ? (a / tot) * 100 : 50;
  return `
    <div class="mh-split">
      <div class="mh-split-bar">
        <i class="a" style="width:${ap.toFixed(1)}%"></i><i class="b" style="width:${(100 - ap).toFixed(1)}%"></i>
      </div>
      <div class="mh-split-legend">
        <span><i class="sw a"></i>${esc(aLabel)} <b>${fmtNum(a)}</b></span>
        <span><i class="sw b"></i>${esc(bLabel)} <b>${fmtNum(b)}</b></span>
      </div>
    </div>`;
}

function renderCustomersCard(c) {
  const repeat = _mhNumber(c.total) === 0 ? null : _mhPercent(c.repeat_rate_pct);
  // The decision read: is the repeat rate healthy (CRUX baseline ~45%)?
  const tone = repeat === null ? 'unknown' : repeat >= 40 ? 'ok' : repeat >= 25 ? 'warn' : 'bad';
  const read = repeat === null ? (_mhNumber(c.total) === 0 ? 'No customers in this period.' : 'Customer rate is unavailable for this period.') : repeat >= 40
    ? 'At the healthy returning baseline — retention is holding.'
    : repeat >= 25
      ? 'Below the ~45% returning baseline — worth a win-back push.'
      : 'Mostly new buyers this period — retention needs attention.';
  return `
    <div class="mh-tile">
      <div class="mh-tile-head">
        <span class="mh-tile-title">Customers${lw.active() ? ' · ' + esc(lw.periodLabel()) : ''}</span>
        <span class="mh-tile-meta">AOV ${_mhNumber(c.aov_ngn) === null ? '—' : fmtNGN(c.aov_ngn)}</span>
      </div>
      <div class="mh-sring-row">
        ${_mhRing(repeat, repeat === null ? '—' : repeat.toFixed(1) + '%', 'returning', tone)}
        <div class="mh-sring-side">
          <div class="mh-stat-num">${_mhNumber(c.total) === null ? '—' : fmtNum(c.total)}</div>
          <div class="mh-stat-lbl">Customers this period</div>
        </div>
      </div>
      ${_mhCustomerBreakdown(c)}
      <div class="mh-read"><span class="mh-chip ${tone === 'ok' ? 'mh-chip-good' : tone === 'warn' ? 'mh-chip-watch' : tone === 'bad' ? 'mh-chip-danger' : ''}">${tone === 'ok' ? 'healthy' : tone === 'unknown' ? 'unavailable' : 'watch'}</span><span>${read}</span></div>
      <div class="mh-foot"><button class="mh-ask" data-ask="Show me customer breakdown for this period">Ask EON</button></div>
    </div>`;
}

function renderLocationsCard(loc) {
  const list = loc.locations || [];
  const total = list.reduce((sum, l) => sum + (l.revenue_ngn || 0), 0);
  const max = Math.max(1, ...list.map(l => l.revenue_ngn || 0));
  const locName = (l) => l.location || l.country || 'Unknown';

  const rows = _mhHBars(list.slice(0, 6).map((l, i) => ({
    label: locName(l),
    display: `${fmtNGN(l.revenue_ngn)} <span class="dim">· ${total > 0 ? ((l.revenue_ngn / total) * 100).toFixed(0) : 0}%</span>`,
    pct: ((l.revenue_ngn || 0) / max) * 100,
    delta: l.delta_pct,
    lead: i === 0,
  })));
  const top = list[0];
  const topShare = top && total > 0 ? ((top.revenue_ngn / total) * 100).toFixed(0) : null;
  // Show a small note if coverage is incomplete
  const coverageNote = (loc.coverage_pct !== undefined && loc.coverage_pct < 95)
    ? `<div class="mh-note" style="font-style:italic">Showing ${loc.attributed_orders}/${loc.total_orders} attributed orders</div>`
    : '';
  return `
    <div class="mh-tile">
      <div class="mh-tile-head">
        <a href="/locations" class="mh-tile-title mh-tile-link"
           title="Open the Location Economics dashboard">Locations</a>
        ${topShare ? `<span class="mh-tile-meta">${esc(locName(top))} carries ${topShare}%</span>` : ''}
      </div>
      ${rows}
      ${coverageNote}
      <div class="mh-foot">
        <button class="mh-ask" data-ask="Break down revenue by fulfillment location for the current period">Ask EON</button>
      </div>
      <a href="/locations" class="mh-foot-link">Open Location Economics dashboard →</a>
    </div>`;
}

// ── HOUSE floor 4 · the tills ────────────────────────────────────────
// The four shops, read as a counter of tills directly under the hero.
// Same payload the Locations tile already receives (location, revenue_ngn,
// order_count, delta_pct) — no new fetch, no new field. The tile itself is
// skipped under house so the page never says the same thing twice, so both
// of its doors ride along here: the Ask EON prompt and the /locations link.
function renderHouseTills(loc) {
  const list = (loc && Array.isArray(loc.locations)) ? loc.locations : [];
  const head = `
      <div class="hs-desk-head">
        <span class="hs-eyebrow">The tills</span>
        <a href="/locations" class="hs-desk-link">Location economics →</a>
      </div>`;
  if (!list.length) {
    // Honest empty state — an empty rail would read as "zero sales".
    return `<div class="hs-desk">${head}
      <div class="hs-desk-none">No store revenue attributed for this period.</div>
    </div>`;
  }
  const max = Math.max(1, ...list.map(l => Number(l.revenue_ngn) || 0));
  const cols = list.slice(0, 6).map(l => {
    const name = l.location || l.country || 'Unknown';
    const rev = Number(l.revenue_ngn) || 0;
    const n = Number(l.order_count) || 0;
    const d = (l.delta_pct === null || l.delta_pct === undefined) ? null : Number(l.delta_pct);
    // No prior window for this store → no chip. A delta we cannot compute
    // is not a zero.
    const chip = (d === null || !isFinite(d)) ? ''
      : `<span class="hs-till-d ${d >= 0 ? 'up' : 'down'}">${d >= 0 ? '+' : '−'}${Math.abs(d).toFixed(0)}%</span>`;
    const h = Math.max(3, Math.round((rev / max) * 100));
    // height rides a custom property so the bar can MOVE: the desk pours
    // in on arrival (hs-pour holds 6%, _deskArrive releases it) and a
    // till whose money changed on the 90s tick rises to its new share
    // instead of being rebuilt — motion only where information arrives.
    return `
      <div class="hs-till">
        <div class="hs-till-bar"><i style="--hs-h:${h}%"></i></div>
        <div class="hs-till-name hs-sign">${esc(name)}</div>
        <div class="hs-till-rev">${esc(fmtNGN(rev))}</div>
        <div class="hs-till-n">${n} order${n === 1 ? '' : 's'}${chip}</div>
      </div>`;
  }).join('');
  return `<div class="hs-desk hs-pour">${head}
      <div class="hs-tills">${cols}</div>
      <div class="hs-desk-foot">
        <button class="mh-ask hs-desk-ask" data-ask="Break down revenue by fulfillment location for the current period">Ask EON</button>
      </div>
    </div>`;
}

// HH:MM on the shop's own clock. The rows carry Shopify ISO timestamps;
// the operator reads Lagos time, and a browser abroad must not shift them.
function _houseDeskHM(iso) {
  if (!iso) return '--:--';
  try {
    const t = new Date(iso);
    if (isNaN(t)) return '--:--';
    return t.toLocaleTimeString('en-GB', {
      hour: '2-digit', minute: '2-digit', hour12: false,
      timeZone: 'Africa/Lagos',
    });
  } catch (_) { return '--:--'; }
}

function _houseTickCell(o) {
  const store = (o && o.store) ? String(o.store) : 'Online';
  const n = Number(o && o.item_count) || 0;
  const what = (n === 1 && o.item_title) ? String(o.item_title)
             : n ? `${n} items` : '';
  // rep is resolved server-side against staff.shopify_user_id. When it is
  // null the order shows WITHOUT a rep — a fabricated name would be worse
  // than a missing one.
  const rep = (o && o.rep) ? `<span class="hs-tick-rep">${esc(String(o.rep))}</span>` : '';
  return `<span class="hs-tick-cell">`
    + `<span class="hs-tick-at">${esc(_houseDeskHM(o && o.at))}</span>`
    + `<span class="hs-tick-store">${esc(store)}</span>`
    + `<span class="hs-tick-amt">${esc(ngn(o && o.amount))}</span>`
    + (what ? `<span class="hs-tick-what">${esc(what)}</span>` : '')
    + rep
    + `</span>`;
}

// ── HOUSE floor 4 · the ticker ───────────────────────────────────────
// The room's ONE kinetic signature: today's real orders crossing the desk.
// Pauses on hover (CSS). Under prefers-reduced-motion it does not scroll at
// all — it prints a short static list, because a marquee nobody can pause
// reading is worse than four honest lines.
function renderHouseTicker(live) {
  const rows = (live && live.ok && Array.isArray(live.recent)) ? live.recent : [];
  if (!rows.length) {
    return `<div class="hs-tick hs-tick--empty"><span class="hs-tick-none">No orders yet today.</span></div>`;
  }
  const still = !!(window.EONHouse && window.EONHouse.reduced);
  const cells = (still ? rows.slice(0, 4) : rows).map(_houseTickCell).join('');
  if (still) return `<div class="hs-tick hs-tick--still">${cells}</div>`;
  // The run is duplicated so the loop has no seam; the copy is hidden from
  // assistive tech so the orders are announced once.
  return `<div class="hs-tick"><div class="hs-tick-rail">`
    + `<div class="hs-tick-run">${cells}</div>`
    + `<div class="hs-tick-run" aria-hidden="true">${cells}</div>`
    + `</div></div>`;
}

// ── HOUSE floor 4 · the desk lives (2026-08-29) ──────────────────────
// The mockup's own words: "a ticker carries each order as it lands" and
// "one hero settles like a till". Until now the desk painted once and
// stood still until the next page load. These helpers keep the SAME
// builders above and patch on the 90s tick — motion only where
// information arrives, never decoration. Every entry point is gated on
// deskHouse(), so Classic/Light/Flow render AND tick byte-identical.

let _deskSeen = null;      // Set of tape-row keys already painted
let _deskHeroVal = null;   // MER figure as last painted (for the roll)

function _deskRowKey(o) {
  return [(o && o.at) || '', (o && o.store) || '', (o && o.amount) || ''].join('|');
}

// pour → grow: the bars stand at 6% in the markup (hs-pour) and take
// their height one frame later, so the columns rise as the desk arrives.
// Reduced motion: released immediately — the facts arrive settled.
function _deskArrive(scope) {
  const desk = scope && scope.querySelector('.hs-desk.hs-pour');
  if (!desk) return;
  if (window.EONHouse && window.EONHouse.reduced) { desk.classList.remove('hs-pour'); return; }
  requestAnimationFrame(() => requestAnimationFrame(() => desk.classList.remove('hs-pour')));
}

// Same release for the pace band's fill (renderTargetTile bakes hs-pour).
// Under the 90s tick's .tick-still the width transition is suppressed in
// CSS, so this release snaps there instead of replaying the sweep.
function _bandSettle(scope) {
  const band = scope && scope.querySelector('.hs-band.hs-pour');
  if (!band) return;
  if (window.EONHouse && window.EONHouse.reduced) { band.classList.remove('hs-pour'); return; }
  requestAnimationFrame(() => requestAnimationFrame(() => band.classList.remove('hs-pour')));
}

function _frontDeskRemember(data) {
  try {
    const rows = (data && data.live_today && data.live_today.ok
      && Array.isArray(data.live_today.recent)) ? data.live_today.recent : [];
    _deskSeen = new Set(rows.map(_deskRowKey));
    _deskHeroVal = (data && data.mer_hero && data.mer_hero.available)
      ? Number(data.mer_hero.value) : null;
  } catch (_) { _deskSeen = null; _deskHeroVal = null; }
}

// A short count from the PAINTED value to the new one — the till settling
// again because money arrived, not the boot count-up replayed.
function _rollNum(el, from, to, dec) {
  if (!el || !isFinite(to)) return;
  if ((window.EONHouse && window.EONHouse.reduced) || !isFinite(from) || from === to) {
    el.textContent = to.toFixed(dec);
    return;
  }
  const dur = 600;
  let t0 = null;
  function step(ts) {
    if (t0 === null) t0 = ts;
    const p = Math.min(1, (ts - t0) / dur);
    el.textContent = (from + (to - from) * (1 - Math.pow(1 - p, 3))).toFixed(dec);
    if (p < 1) requestAnimationFrame(step); else el.textContent = to.toFixed(dec);
  }
  requestAnimationFrame(step);
}

// Patch the till columns in place: figures update, a till whose revenue
// moved flashes accent once, and its bar RISES to the new share on the
// existing height transition. Returns false when the store set no longer
// matches the DOM (caller rebuilds).
function _patchTills(deskSlot, loc) {
  const list = (loc && Array.isArray(loc.locations)) ? loc.locations : [];
  const cols = deskSlot.querySelectorAll('.hs-till');
  if (!list.length || !cols.length) return false;
  const byName = {};
  list.slice(0, 6).forEach(l => { byName[String(l.location || l.country || 'Unknown')] = l; });
  const max = Math.max(1, ...list.map(l => Number(l.revenue_ngn) || 0));
  let matched = 0;
  cols.forEach(col => {
    const nameEl = col.querySelector('.hs-till-name');
    const l = nameEl ? byName[nameEl.textContent.trim()] : null;
    if (!l) return;
    matched++;
    const rev = Number(l.revenue_ngn) || 0;
    const revEl = col.querySelector('.hs-till-rev');
    if (revEl) {
      const next = fmtNGN(rev);
      if (revEl.textContent !== next) {
        revEl.textContent = next;
        revEl.classList.remove('hs-rang');
        void revEl.offsetWidth;              // restart the flash cleanly
        revEl.classList.add('hs-rang');
      }
    }
    const bar = col.querySelector('.hs-till-bar i');
    if (bar) bar.style.setProperty('--hs-h', Math.max(3, Math.round((rev / max) * 100)) + '%');
    const n = Number(l.order_count) || 0;
    const d = (l.delta_pct === null || l.delta_pct === undefined) ? null : Number(l.delta_pct);
    const chip = (d === null || !isFinite(d)) ? ''
      : `<span class="hs-till-d ${d >= 0 ? 'up' : 'down'}">${d >= 0 ? '+' : '−'}${Math.abs(d).toFixed(0)}%</span>`;
    const nEl = col.querySelector('.hs-till-n');
    if (nEl) nEl.innerHTML = `${n} order${n === 1 ? '' : 's'}${chip}`;
  });
  return matched === cols.length && cols.length > 0;
}

// New orders land on the tape: the ticker is rebuilt with the fresh rows
// flagged, and the marquee restarts with the newest order at the front —
// the first thing the eye meets is the thing that just happened.
function _patchTicker(deskSlot, live) {
  const rows = (live && live.ok && Array.isArray(live.recent)) ? live.recent : [];
  if (!_deskSeen) { _deskSeen = new Set(rows.map(_deskRowKey)); return; }
  const fresh = rows.filter(o => !_deskSeen.has(_deskRowKey(o)));
  if (!fresh.length) return;
  const old = deskSlot.querySelector('.hs-tick');
  if (!old) return;
  const wrap = document.createElement('div');
  wrap.innerHTML = renderHouseTicker(live);
  const next = wrap.firstElementChild;
  if (!next) return;
  next.querySelectorAll('.hs-tick-run').forEach(run => {
    Array.prototype.slice.call(run.children, 0, fresh.length)
      .forEach(c => c.classList.add('hs-tick-cell--new'));
  });
  old.replaceWith(next);
  _deskSeen = new Set(rows.map(_deskRowKey));
}

// The 90s patch, called from liveRefreshTick on the home view only.
function _frontDeskTick(data) {
  if (!data || !deskHouse()) return;

  // 1 · the hero — rebuild the stats under .tick-still (no stamp slam,
  // no boot count-up replay), then roll ONLY the figure from where it
  // stood to where the money moved it.
  const heroSlot = document.getElementById('hero-slot');
  const hero = data.mer_hero;
  if (heroSlot && hero && hero.available) {
    const to = Number(hero.value);
    if (isFinite(to) && to !== _deskHeroVal) {
      const from = _deskHeroVal;
      heroSlot.classList.add('tick-still');
      heroSlot.innerHTML = renderMerHero(hero);
      _rollNum(heroSlot.querySelector('.hero-num-val'), isFinite(from) ? from : to, to, 2);
      _deskHeroVal = to;
    }
  }

  // 2 · the tills, then the tape.
  const deskSlot = document.getElementById('house-desk-slot');
  if (!deskSlot || !deskSlot.firstChild) return;
  if (!_patchTills(deskSlot, data.locations || {})) {
    // store set changed (rare) — repaint the whole desk, settled
    deskSlot.innerHTML = renderHouseTills(data.locations || {})
      + renderHouseTicker(data.live_today || {});
    const poured = deskSlot.querySelector('.hs-desk.hs-pour');
    if (poured) poured.classList.remove('hs-pour');
    _deskSeen = null;
  }
  _patchTicker(deskSlot, data.live_today || {});
}

// Compact NGN formatter — Apple Health style
// ── WS-E (2026-07-26): ONE ₦ formatter ───────────────────────
// A dozen independent implementations produced "₦1.2M on Home,
// ₦1,214,338 on Ads" for the same number, each with its own null/
// NaN/negative behavior. The main-page family below (fmtNGN,
// formatNaira, _moneyFmtNGN, bksNGN, bksParen) now delegates here —
// compact vs full stays a per-surface CHOICE; the math and edge
// cases live in one place. (CS/StaffOS keep their own wrappers —
// other lanes, other sessions.)
function ngn(n, opts = {}) {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';   // never render "₦NaN"
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (opts.paren) return `(₦${Math.round(abs).toLocaleString('en-US')})`;
  if (opts.compact) {
    if (abs >= 1_000_000) return `${sign}₦${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}₦${(abs / 1_000).toFixed(0)}k`;
  }
  return `${sign}₦${Math.round(abs).toLocaleString('en-US')}`;
}

function fmtNGN(n) { return ngn(n, { compact: true }); }

// Compact number formatter (no currency)
function fmtNum(n) {
  if (n === null || n === undefined) return '—';
  n = Number(n);
  if (!Number.isFinite(n)) return '—'; // NaN/Infinity → never render "NaN"
  if (Math.abs(n) >= 1_000_000) return `${(n/1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n/1_000).toFixed(1)}k`;
  return Math.round(n).toLocaleString();
}

// ── Freshness banner (v3.0p13 commit 8b) ─────────────────────────────
//
// v3.0p18 9j: per-source dismissal. Sources the operator has dismissed
// are filtered out of the banner for 24h. Auto-clears when the data
// actually freshens (the source drops off the stale list naturally and
// the localStorage entry just expires). Operator can re-surface a
// dismissed source by clearing localStorage.

const _FRESHNESS_DISMISS_KEY = 'eon.freshness.dismissed';
const _FRESHNESS_DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

function _getFreshnessDismissals() {
  try {
    const raw = JSON.parse(localStorage.getItem(_FRESHNESS_DISMISS_KEY) || '{}');
    const now = Date.now();
    // Prune expired entries on read so the map doesn't grow unbounded.
    const live = {};
    for (const [k, v] of Object.entries(raw)) {
      if (typeof v === 'number' && v > now) live[k] = v;
    }
    if (Object.keys(live).length !== Object.keys(raw).length) {
      localStorage.setItem(_FRESHNESS_DISMISS_KEY, JSON.stringify(live));
    }
    return live;
  } catch { return {}; }
}

function _setFreshnessDismissal(source) {
  try {
    const live = _getFreshnessDismissals();
    live[source] = Date.now() + _FRESHNESS_DISMISS_TTL_MS;
    localStorage.setItem(_FRESHNESS_DISMISS_KEY, JSON.stringify(live));
  } catch {}
}

// v3.0p18 9p / v3.0p30: shared refresh-and-poll logic. Used by the
// header refresh icon AND the freshness banner's inline Refresh button.
//
// Flow (v3.0p30 rewrite):
//   1. Snapshot per-source latest_sync from /admin/data-freshness.
//   2. POST /admin/refresh-now (202; daemon thread does the work).
//   3. Re-render the active view IMMEDIATELY on trigger success — the
//      operator sees fresh derived data within ~1s. Restore button
//      state at the same time so a follow-up click is possible.
//   4. Background-poll every 6s for up to 3 min for any source's
//      latest_sync to advance. On movement, re-render again. Silent
//      on no-movement (the freshness banner already surfaces stale
//      source state independently — no need to interrupt with an
//      alert).
//
// Why rewritten: the previous design blocked the re-render on
// poll-success, which timed out at 3 min on the common case where
// Hevo had nothing new to deliver. That manifested to operators as
// "refresh button doesn't work most of the time" because they'd wait
// 3 min and then get a misleading "didn't receive new rows" alert,
// even though /admin/refresh-now had completed successfully.
//
// @param btn  optional <button> to animate during the trigger phase.
async function triggerGlobalRefresh(btn) {
  if (!State.member?.role || State.member.role !== 'owner') {
    showToast('Owner role required to trigger a manual refresh.');
    return;
  }
  // Only the freshness banner button has a textual label. The header
  // refresh icon is icon-only and must not have its content touched.
  const isTextBtn = btn && btn.id === 'freshness-refresh-btn';
  if (btn) {
    btn.classList.add('is-refreshing');
    btn.disabled = true;
    if (isTextBtn) btn.dataset.origLabel = btn.textContent || '';
  }

  const headers = { 'Content-Type': 'application/json' };
  if (State.jwt) headers['Authorization'] = `Bearer ${State.jwt}`;
  if (State.tenant) headers['X-Tenant-Slug'] = State.tenant.slug;

  // Snapshot BEFORE so the background poll can detect source advancement.
  const before = await api('/api/admin/data-freshness').catch(() => null);
  const beforeMap = new Map(
    (before?.sources || []).map(s => [s.source, s.latest_sync || ''])
  );

  // Kick off — POST returns 202 once the daemon thread is queued.
  const trigger = await fetch(API + '/api/admin/refresh-now', {
    method: 'POST', headers,
  }).catch(e => ({ ok: false, _err: e }));

  if (!trigger.ok && trigger.status !== 409) {
    const body = await trigger.json?.().catch(() => ({})) || {};
    showToast(`Refresh failed: ${body.detail || body.message || trigger._err?.message || trigger.status}`);
    if (btn) {
      btn.classList.remove('is-refreshing');
      btn.disabled = false;
      if (isTextBtn && btn.dataset.origLabel != null) {
        btn.textContent = btn.dataset.origLabel;
      }
    }
    return;
  }

  // Re-render the active view. Factored into a closure so the
  // immediate-pass and the background-pass share one impl.
  const rerenderActiveView = async () => {
    await refreshFreshnessBanner();
    const view = State.view || 'home';
    if (view === 'home') {
      await fetchAndRenderHomeData();
    } else if (view === 'ads') {
      // WS-A (2026-07-26): the old call rendered the new HTML into a
      // string and DISCARDED it — manual refresh on Ads was a no-op.
      // Mount into the live view container and re-bind.
      const main = document.querySelector('main.view');
      if (main) { main.innerHTML = renderAds(); lw.mountAds(); bindAds(); }
    } else if (view === 'money') {
      // PR MONEY-COCKPIT: one hydrator. The old two-partial refresh
      // (cash + insights) still left the hero/summary, transactions,
      // budgets and walk-in sections frozen after a manual refresh.
      if (typeof refreshMoney === 'function') refreshMoney();
    }
  };

  // Immediate re-render — UI is responsive within ~1s of click.
  await rerenderActiveView();

  // Restore button state before the background poll, so the operator
  // can click refresh again immediately if they want.
  if (btn) {
    btn.classList.remove('is-refreshing');
    btn.disabled = false;
    if (isTextBtn && btn.dataset.origLabel != null) {
      btn.textContent = btn.dataset.origLabel;
    }
  }

  // Background poll — re-render again if/when Hevo delivers new rows
  // during the next 3 minutes. Silent on timeout; the freshness
  // banner surfaces stale-source state on its own.
  (async () => {
    const deadline = Date.now() + 180_000;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 6000));
      const snap = await api('/api/admin/data-freshness').catch(() => null);
      if (!snap) continue;
      const moved = (snap.sources || []).some(s => {
        const prev = beforeMap.get(s.source) || '';
        return s.latest_sync && s.latest_sync > prev;
      });
      if (moved) {
        await rerenderActiveView();
        return;
      }
    }
  })();
}


async function refreshFreshnessBanner() {
  const slot = $('#freshness-slot');
  if (!slot) return;

  try {
    const data = await api('/api/admin/data-freshness');
    const overall = data.overall;

    // Don't show anything when fresh — banner is for problem signaling only
    if (overall === 'fresh' || overall === 'unknown') {
      slot.innerHTML = '';
      return;
    }

    const isOwner = State.member?.role === 'owner';

    // Filter dismissed sources out of every category. If all sources
    // for a status are dismissed, the resulting list is empty and we
    // skip rendering that banner shape entirely.
    const dismissed = _getFreshnessDismissals();
    const undismissed = (data.sources || []).filter(s => !dismissed[s.source]);

    // v3.0p13 commit 8e: distinguish sync problems from "platform is dark"
    const syncBroken = undismissed.filter(s =>
      s.status === 'sync_broken' || s.status === 'dead');
    const syncStale = undismissed.filter(s => s.status === 'sync_stale');
    const dark = undismissed.filter(s => s.status === 'no_recent_data');

    // If everything got dismissed, hide the banner.
    if (syncBroken.length === 0 && syncStale.length === 0 && dark.length === 0) {
      slot.innerHTML = '';
      return;
    }
    
    let icon, color, title, detail, showRefresh;
    // The set of sources whose row appears in this banner — used by the
    // per-source dismiss × button to suppress them for 24h.
    let bannerSources = [];

    // v3.0p18 9j: choose the banner shape based on which categories
    // still have UNDISMISSED items, not just the overall verdict. If
    // the operator dismissed every sync_broken source but sync_stale
    // sources remain undismissed, render the sync_stale banner.
    const effectiveOverall =
      syncBroken.length > 0 ? 'sync_broken'
      : syncStale.length > 0 ? 'sync_stale'
      : dark.length > 0 ? 'no_recent_data'
      : null;
    if (effectiveOverall === null) {
      slot.innerHTML = '';
      return;
    }

    if (effectiveOverall === 'sync_broken') {
      icon = '⚠';
      color = 'var(--danger, #ef4444)';
      title = 'Data sync is broken';
      const list = syncBroken.map(s => s.source).slice(0, 3).join(', ');
      detail = `Cron has not refreshed: ${list}${syncBroken.length > 3 ? ' and others' : ''}. Click Refresh.`;
      showRefresh = true;
      bannerSources = syncBroken.map(s => s.source);
    } else if (effectiveOverall === 'sync_stale') {
      icon = '◐';
      color = 'var(--warn, #f59e0b)';
      title = 'Some data may be lagging';
      // v3.0p17 9h item 1: surface the through-date so users see what
      // window the cached numbers reflect, not just the sync age.
      const list = syncStale
        .map(s => `${s.source}: data through ${s.latest_date} (synced ${Math.round(s.sync_age_hours)}h ago)`)
        .slice(0, 2).join(', ');
      detail = list;
      showRefresh = true;
      bannerSources = syncStale.map(s => s.source);
    } else if (effectiveOverall === 'no_recent_data') {
      // Sync ran fine, but the source platform has no recent data — paused ad campaign etc.
      // This is informational, not a bug. Lower visual weight, no refresh button (won't help).
      icon = 'ⓘ';
      color = 'var(--ink-soft, #5a5a62)';
      title = 'Some sources have no recent data';
      const list = dark
        .map(s => `${s.source} (last data ${s.latest_date})`)
        .slice(0, 2).join(', ');
      detail = `${list}. This is normal if a campaign is paused.`;
      showRefresh = false;
      bannerSources = dark.map(s => s.source);
    } else {
      slot.innerHTML = '';
      return;
    }
    
    // v3.0p18 9j: stash the rendered banner's source list on the slot
    // element so the dismiss button can read it without juggling closures.
    slot.dataset.bannerSources = JSON.stringify(bannerSources);

    slot.innerHTML = `
      <div class="freshness-banner" style="
        background: var(--bg-sub);
        border-left: 3px solid ${color};
        padding: 10px 14px;
        margin: 0 0 12px 0;
        border-radius: 4px;
        font-size: 13px;
        line-height: 1.4;
      ">
        <div style="display: flex; align-items: flex-start; gap: 10px;">
          <div style="color: ${color}; font-size: 16px; line-height: 1;">${icon}</div>
          <div style="flex: 1; min-width: 0;">
            <div style="font-weight: 600; color: var(--ink); margin-bottom: 2px;">${esc(title)}</div>
            <div style="color: var(--ink-soft); font-size: 12px;">${esc(detail)}</div>
          </div>
          ${isOwner && showRefresh ? `
            <button id="freshness-refresh-btn" class="chip" type="button"
                    style="font-size: 12px; padding: 6px 10px; flex-shrink: 0;">
              Refresh
            </button>
          ` : ''}
          <button id="freshness-dismiss-btn" class="chip" type="button"
                  title="Dismiss for 24h (auto-clears when data freshens)"
                  style="font-size: 14px; padding: 4px 8px; flex-shrink: 0; line-height: 1;">
            ×
          </button>
        </div>
      </div>
    `;

    // v3.0p18 9j: dismiss button suppresses ALL sources currently in
    // the banner for 24h. If the data freshens during that window the
    // source naturally drops off the stale list; if it stays stale the
    // banner stays gone. Re-shows automatically after 24h.
    $('#freshness-dismiss-btn')?.addEventListener('click', () => {
      let sources = [];
      try {
        sources = JSON.parse(slot.dataset.bannerSources || '[]');
      } catch {}
      for (const s of sources) _setFreshnessDismissal(s);
      slot.innerHTML = '';
    });

    if (isOwner && showRefresh) {
      $('#freshness-refresh-btn')?.addEventListener('click', () => {
        // v3.0p18 9p: delegated to the shared helper so the banner button
        // and the header icon share the same refresh-and-poll path.
        triggerGlobalRefresh($('#freshness-refresh-btn'));
      });
    }
  } catch (e) {
    // Silent fail — banner is supplementary, don't break home page
    console.warn('freshness fetch failed:', e);
  }
}


// ── v3.0p17 9m: memory review queue banner ──────────────────
// Shows top N stale memories with promote/dismiss buttons.
// Owner-only via P.MEMORY_WRITE. Silent fail — supplementary.

async function refreshMemoryReviewBanner() {
  const slot = $('#memory-review-slot');
  if (!slot) return;
  try {
    const data = await api('/api/memories/review-queue?limit=5');
    if (!data?.ok || !data.memories || data.memories.length === 0) {
      slot.innerHTML = '';
      return;
    }
    const rows = data.memories.map(m => {
      // 9p: never-searched memories show "added Xd ago" using created_at.
      // The previous "last seen never" framing was misleading because
      // freshly-extracted memories naturally have last_searched_at NULL.
      let last;
      if (m.last_searched_at) {
        last = `last seen ${new Date(m.last_searched_at).toLocaleDateString()}`;
      } else if (m.created_at) {
        const days = Math.floor((Date.now() - new Date(m.created_at)) / 86400000);
        last = days === 0 ? 'added today'
             : days === 1 ? 'added yesterday'
             : `added ${days}d ago`;
      } else {
        last = 'last seen never';
      }
      const content = (m.content || '').slice(0, 120);
      return `
        <div class="memory-review-row" data-id="${esc(m.id)}">
          <div class="memory-review-text">
            <span class="memory-review-cat">${esc(m.category || 'general')}</span>
            <span class="memory-review-content">${esc(content)}${
              (m.content || '').length > 120 ? '…' : ''
            }</span>
            <span class="memory-review-meta">${esc(last)}</span>
          </div>
          <div class="memory-review-actions">
            <button type="button" class="btn-ghost memory-review-keep" data-id="${esc(m.id)}">Keep</button>
            <button type="button" class="btn-ghost memory-review-dismiss" data-id="${esc(m.id)}">Dismiss</button>
          </div>
        </div>`;
    }).join('');

    // v3.0p17 9r Day 4: collapsed-by-default. With 5 rows × ~80px the
    // expanded card was eating most of the mobile viewport, pushing the
    // actual dashboard below the fold. <details> is native, accessible,
    // no JS state to manage.
    //
    // 9h follow-up: if a previous render had the card already expanded,
    // preserve that. Otherwise a periodic re-render would silently collapse
    // it on the user. (The Keep/Dismiss handler avoids re-rendering at all
    // for the same reason — see the click delegation block below.)
    const wasOpen = slot.querySelector('.memory-review-card')?.open ? ' open' : '';
    slot.innerHTML = `
      <details class="memory-review-card"${wasOpen}>
        <summary class="memory-review-header">
          <span class="memory-review-title">Memory review · ${data.count} stale</span>
          <span class="memory-review-hint">Tap to review · Keep refreshes, Dismiss demotes</span>
          <button type="button" class="btn-ghost memory-review-all" id="memory-review-all">All beliefs</button>
        </summary>
        <div class="memory-review-rows">${rows}</div>
      </details>`;
  } catch (e) {
    console.warn('memory review banner fetch failed:', e);
    slot.innerHTML = '';
  }
}

// Event delegation for keep/dismiss clicks (mounted once at app load).
//
// Previous version (pre-9h) called refreshMemoryReviewBanner() after every
// vote, which re-rendered the <details> element WITHOUT the `open`
// attribute — so the expanded panel collapsed itself on every click. The
// voted-on row was actually gone server-side, but the user saw the whole
// card snap shut and assumed nothing happened.
//
// Fix: surgically remove the voted row from the DOM, decrement the count
// in the summary, and clear the slot only when all rows are gone. No
// refetch → no collapse → the user sees the row vanish exactly as they
// expect.
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.memory-review-keep, .memory-review-dismiss');
  if (!btn) return;
  const id = btn.dataset.id;
  if (!id) return;
  const isKeep = btn.classList.contains('memory-review-keep');
  const path = isKeep ? `/api/memories/${encodeURIComponent(id)}/promote`
                       : `/api/memories/${encodeURIComponent(id)}/dismiss`;
  const row = btn.closest('.memory-review-row');
  if (row) row.querySelectorAll('button').forEach(b => b.disabled = true);
  try {
    await api(path, { method: 'POST' });
    if (!row) return;

    // Fade then collapse the row. Using both opacity and max-height so
    // the surrounding rows shift up smoothly instead of jumping.
    row.style.overflow = 'hidden';
    row.style.transition = 'opacity 0.2s ease-out, max-height 0.25s ease-out, margin 0.25s ease-out, padding 0.25s ease-out';
    row.style.maxHeight = row.offsetHeight + 'px';
    // Next frame: trigger the collapse.
    requestAnimationFrame(() => {
      row.style.opacity = '0';
      row.style.maxHeight = '0';
      row.style.marginTop = '0';
      row.style.marginBottom = '0';
      row.style.paddingTop = '0';
      row.style.paddingBottom = '0';
    });

    setTimeout(() => {
      const card = row.closest('.memory-review-card');
      row.remove();
      if (!card) return;
      const remaining = card.querySelectorAll('.memory-review-row').length;
      if (remaining === 0) {
        // Last row gone — collapse the entire banner. Next page load /
        // periodic refresh will repopulate if more memories go stale.
        const slot = $('#memory-review-slot');
        if (slot) slot.innerHTML = '';
        return;
      }
      const titleEl = card.querySelector('.memory-review-title');
      if (titleEl) titleEl.textContent = `Memory review · ${remaining} stale`;
    }, 260);
  } catch (err) {
    if (row) row.querySelectorAll('button').forEach(b => b.disabled = false);
    showToast(`Failed: ${err.body?.detail || err.message || err}`);
  }
});


// CS team performance tile — loaded lazily after the main Home render.
// Shows: team totals (messages sent, customers reached, response ratio,
// median first-response), per-rep ranking. Hidden when there's no data
// (e.g. tenants without Zoko).
async function loadCsTeamPerformanceTile() {
  const tilesSlot = $('#tiles-slot');
  if (!tilesSlot) return;
  let d;
  try { d = await api('/api/cs/team-performance?days=7'); }
  catch (e) { return; }
  if (!d || !d.ok) return;
  const t = d.team_totals || {};
  if (!t.outbound_total) return;  // no Zoko activity = hide tile
  const reps = (d.reps || []).slice(0, 5);
  const fmtSec = (s) => {
    if (s == null) return '—';
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    return `${Math.round(s / 3600)}h`;
  };
  const ratioClass = (t.response_ratio_pct == null) ? ''
                   : (t.response_ratio_pct >= 90 ? 'color:var(--ok);'
                   : t.response_ratio_pct >= 70 ? 'color:var(--warn);'
                   : 'color:var(--danger);');
  // Each rep row now shows their daily ring + streak alongside
  // activity. The ring/streak fetch is fire-and-forget per rep so
  // it doesn't block initial paint — rep row renders activity first,
  // ring/streak fills in within ~200ms.
  // Rep id is now an email (agent_email from Zoko). Strip the
  // @domain for the inline label so reps see "thelije" not the
  // 2026-06-06: prefer rep_name from the API (resolved from staff
  // table) over the bare email handle. Daniel: 'USE THERE NAMES'.
  const repLabel = (r) => {
    if (r.rep_name && r.rep_name !== 'unknown') return r.rep_name;
    if (!r.rep_id) return 'unknown';
    return r.rep_id.split('@')[0];
  };
  const repRoleSuffix = (r) => r.rep_role
    ? ` · <span style="color:var(--ink-soft); font-size:10.5px;">${esc(r.rep_role)}</span>`
    : '';
  const repRows = reps.map((r, i) => `
    <div data-rep-id="${esc(r.rep_id || '')}" class="cs-rep-row" style="display:flex; justify-content:space-between; align-items:center; font-size:12px; padding:8px 0; gap:10px; ${i ? 'border-top:1px solid var(--line);' : ''}">
      <span style="font-family:var(--display); color:var(--ink); flex-shrink:0; font-weight:500;" title="${esc(r.rep_id || '')}">${esc(repLabel(r))}${repRoleSuffix(r)}</span>
      <span style="flex:1; text-align:right; color:var(--ink-soft);"><b style="color:var(--ink);">${r.messages_sent}</b> msgs · ${r.customers_reached} customers</span>
      <span class="cs-rep-ring" style="display:inline-flex; align-items:center; gap:6px; font-size:10.5px; color:var(--ink-soft); flex-shrink:0; min-width:78px; justify-content:flex-end;">
        <span class="cs-rep-ring-pct">—</span>
        <span class="cs-rep-ring-streak"></span>
      </span>
    </div>`).join('');
  const wrapper = document.createElement('div');
  wrapper.innerHTML = `
    <div class="card" style="padding:16px; margin-top:12px;">
      <div style="display:flex; justify-content:space-between; align-items:baseline; margin-bottom:8px;">
        <div style="font-size:11px; text-transform:uppercase; letter-spacing:0.5px; color:var(--ink-soft);">CS team · last 7 days</div>
        <button data-ask="show me the CS team performance over the last 7 days — who's top, who's slow on first response, any unanswered threads"
          style="background:none; border:1px solid var(--border); color:var(--ink-soft); font-size:11px; padding:3px 10px; border-radius:6px; cursor:pointer;">Ask EON</button>
      </div>
      <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(140px, 1fr)); gap:14px; margin-bottom:10px;">
        <div>
          <div style="font-size:10px; color:var(--ink-soft); text-transform:uppercase; letter-spacing:0.3px;">Messages sent</div>
          <div style="font-size:18px; font-weight:500; margin-top:2px;">${t.outbound_total.toLocaleString()}</div>
        </div>
        <div>
          <div style="font-size:10px; color:var(--ink-soft); text-transform:uppercase; letter-spacing:0.3px;">Customers reached</div>
          <div style="font-size:18px; font-weight:500; margin-top:2px;">${(t.customers_reached || 0).toLocaleString()}</div>
        </div>
        <div>
          <div style="font-size:10px; color:var(--ink-soft); text-transform:uppercase; letter-spacing:0.3px;">Response ratio</div>
          <div style="font-size:18px; font-weight:500; margin-top:2px; ${ratioClass}">${t.response_ratio_pct == null ? '—' : t.response_ratio_pct + '%'}</div>
        </div>
        <div>
          <div style="font-size:10px; color:var(--ink-soft); text-transform:uppercase; letter-spacing:0.3px;">First-response (median)</div>
          <div style="font-size:18px; font-weight:500; margin-top:2px;">${fmtSec(t.median_first_response_seconds)}</div>
        </div>
      </div>
      ${t.unanswered_threads ? `<div style="font-size:11px; color:var(--warn); margin-bottom:8px;">⚠ ${t.unanswered_threads} customer thread${t.unanswered_threads === 1 ? '' : 's'} got no reply.</div>` : ''}
      ${repRows ? `<div style="margin-top:10px; padding-top:10px; border-top:1px solid var(--line);">
        <div style="font-size:10px; color:var(--ink-soft); text-transform:uppercase; letter-spacing:0.3px; margin-bottom:6px;">Top reps</div>
        ${repRows}
      </div>` : ''}
    </div>`;
  tilesSlot.appendChild(wrapper.firstElementChild);
  // NOTE (WS-A): the old bind here ran querySelectorAll on `wrapper`
  // AFTER its child was moved into tilesSlot — it always matched
  // nothing, so this tile's Ask EON was dead since it shipped. The
  // document-level [data-ask] delegation wires it for real.

  // Hydrate per-rep ring + streak (fire-and-forget per rep).
  reps.forEach(r => {
    if (!r.rep_id) return;
    api(`/api/cs/gamification/rep/${encodeURIComponent(r.rep_id)}`)
      .then(g => {
        if (!g?.ok) return;
        const row = document.querySelector(`.cs-rep-row[data-rep-id="${CSS.escape(r.rep_id)}"]`);
        if (!row) return;
        const pctEl = row.querySelector('.cs-rep-ring-pct');
        const streakEl = row.querySelector('.cs-rep-ring-streak');
        const ring = g.ring || {};
        const pct = Math.round(ring.pct_of_target || 0);
        const closed = !!ring.closed;
        if (pctEl) {
          pctEl.textContent = `${pct}%`;
          pctEl.style.color = closed ? 'var(--ok)' : (pct >= 50 ? 'var(--warn)' : 'var(--ink-soft)');
          pctEl.style.fontWeight = closed ? '600' : '400';
        }
        const streakDays = g.streak?.streak_days || 0;
        if (streakEl && streakDays > 0) {
          streakEl.innerHTML = `<span style="color:var(--ok);">●</span> ${streakDays}d`;
          streakEl.title = `${streakDays}-day streak`;
        }
      })
      .catch(() => {});
  });

  // Entry button to the full-screen CS Inbox (v1, 2026-06-06).
  // Render once per Home paint; idempotent via the id check.
  if (!document.getElementById('cs-inbox-entry')) {
    const entry = document.createElement('button');
    entry.id = 'cs-inbox-entry';
    entry.style.cssText = (
      'display:block; width:100%; margin-top:10px; padding:14px 16px; '
      + 'background:var(--bg-sub); border:1px solid var(--border); '
      + 'border-radius:12px; color:var(--ink); cursor:pointer; '
      + 'text-align:left; font-family:inherit; font-size:13px;'
    );
    entry.innerHTML = (
      '<div style="display:flex; justify-content:space-between; align-items:center;">'
      + '<div>'
      + '<div style="font-weight:500;">Open CS Inbox →</div>'
      + '<div style="font-size:11px; color:var(--ink-soft); margin-top:3px;">Full-screen WhatsApp/IG chat with customer context built in.</div>'
      + '</div>'
      + '<div style="font-size:18px; color:var(--ink-soft);">›</div>'
      + '</div>'
    );
    entry.addEventListener('click', () => {
      State.view = 'cs';
      render();
    });
    tilesSlot.appendChild(entry);
  }

  // Sibling entry to the Staff OS page (role-switcher OS: Day · Chat · Tasks ·
  // Score · Team). Placed right under the CS Inbox link per Daniel's request
  // ("at the bottom where the CS UI link is"). Same idempotent id-guard +
  // styling; navigates to the 'staffos' view exactly like /staff routes.
  if (!document.getElementById('staffos-entry')) {
    const sEntry = document.createElement('button');
    sEntry.id = 'staffos-entry';
    sEntry.style.cssText = (
      'display:block; width:100%; margin-top:10px; padding:14px 16px; '
      + 'background:var(--bg-sub); border:1px solid var(--border); '
      + 'border-radius:12px; color:var(--ink); cursor:pointer; '
      + 'text-align:left; font-family:inherit; font-size:13px;'
    );
    sEntry.innerHTML = (
      '<div style="display:flex; justify-content:space-between; align-items:center;">'
      + '<div>'
      + '<div style="font-weight:500;">Open Staff OS →</div>'
      + '<div style="font-size:11px; color:var(--ink-soft); margin-top:3px;">Per-staff Day, chat, tasks, scoreboard and team — the role-switcher OS.</div>'
      + '</div>'
      + '<div style="font-size:18px; color:var(--ink-soft);">›</div>'
      + '</div>'
    );
    sEntry.addEventListener('click', () => {
      State.view = 'staffos';
      render();
    });
    tilesSlot.appendChild(sEntry);
  }
}


function renderHomeEmpty(err) {
  const heroSlot = $('#hero-slot');
  const tilesSlot = $('#tiles-slot');
  if (heroSlot) {
    heroSlot.innerHTML = `
      <div class="hero">
        <div class="hero-meta">
          <div class="hero-label">Welcome</div>
          <span class="pill pill-warn">Couldn't load</span>
        </div>
        <div class="hero-num" style="font-size: 30px; line-height: 1.15;">
          Dashboard didn't<br>load just now
        </div>
      </div>`;
  }
  // Hide chart and target slots when empty
  const chartSlot = $('#chart-slot');
  if (chartSlot) chartSlot.innerHTML = '';
  const targetSlot = $('#target-slot');
  if (targetSlot) targetSlot.innerHTML = '';
  // HOUSE floor 4: the tills and the ticker go with them. Leaving the last
  // good numbers on screen under a "didn't load" hero would be a lie.
  const deskSlot = $('#house-desk-slot');
  if (deskSlot) deskSlot.innerHTML = '';

  if (tilesSlot) {
    // 2026-06-08: this is the snapshot-load CATCH path — almost always a
    // transient blip (redeploy / momentary connection), NOT a disconnected
    // account. Do not say "connect your data sources" — it falsely implies the
    // integrations are gone and scares the operator. Offer a retry instead.
    tilesSlot.innerHTML = `
      <div class="card" style="padding: 24px;">
        <div class="eyebrow" style="margin-bottom: 12px;">Temporary hiccup</div>
        <div style="font-size: 14px; color: var(--ink-dim); line-height: 1.6; margin-bottom: 16px;">
          EON couldn't load your dashboard data just now — usually a momentary
          connection blip or a redeploy finishing. Your data and integrations are
          intact; just retry.
        </div>
        <button class="chip primary" onclick="location.reload()">Retry</button>
        <button class="chip" onclick="goView('settings')" style="margin-left: 6px;">Settings</button>
        <button class="chip" onclick="goChat('hi, what can you help me with?')" style="margin-left: 6px;">Try a chat</button>
      </div>`;
  }
}

// Helpers exposed globally for inline onclick
window.goView = (v) => { State.view = v; render(); };
window.goChat = (msg) => {
  State.view = 'chat';
  render();
  chatConversation.offerDraft(msg);
};

// ──────────────────────────────────────────────────────────
// Chat

// 2026-06-07: full-screen toggle for the in-app chat. Corner-brackets
// point OUT when boxed (tap to expand) and IN when expanded (tap to
// collapse / return to the boxed layout — the operator's "back").
function chatExpandToggleIcon() {
  const d = State.chatFullscreen
    ? 'M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5'   // corners-in  → collapse
    : 'M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5';  // corners-out → expand
  return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" style="display:block;">
            <path d="${d}" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round"/>
          </svg>`;
}

// Full-screen only applies while the chat view is actually mounted, so
// navigating away can never strand the operator without the app chrome.
// Called from render() on every (re)render and from the toggle.
function syncChatFullscreenClass() {
  const inChat = State.view === 'chat';
  document.body.classList.toggle('chat-full', inChat && State.chatFullscreen);
  // 2026-06-09: `view-chat` turns the whole screen into one animated field
  // (painted on the fixed .grain-bg) with the header + nav floating
  // borderless over it — the "full-page app" feel, without hiding nav.
  document.body.classList.toggle('view-chat', inChat);
}

// Toggle (or set) the in-app full-screen chat. body.chat-full hides the
// app header + bottom nav so the existing .chat-wrap (flex:1) grows to
// fill the viewport — NO chat DOM is rebuilt, so charts, tool-traces,
// attachments and permission cards all keep working. Updates the toggle
// button in place (no full re-render) and keeps the thread pinned to the
// bottom across the reflow.
function setChatFullscreen(on) {
  // Leaving full screen hides the Chat bar's motion switch (app.css): a person
  // on it (Escape) goes to the header's switch, the same setting, now shown.
  const onSwitch = !on && document.activeElement?.id === 'chat-motion-btn';
  State.chatFullscreen = !!on;
  try { localStorage.setItem('eon_chat_fullscreen', State.chatFullscreen ? '1' : '0'); } catch (e) {}
  syncChatFullscreenClass();
  if (onSwitch) ($('#hdr .lw-motion') || $('#chat-fullscreen-btn'))?.focus({ preventScroll: true });
  chatConversation.update();   // More names the bar's shown buttons: the switch came or went
  const btn = $('#chat-fullscreen-btn');
  if (btn) {
    btn.innerHTML = chatExpandToggleIcon();
    btn.setAttribute('aria-label', State.chatFullscreen ? 'Exit full screen' : 'Open in full screen');
    btn.setAttribute('title', State.chatFullscreen ? 'Collapse chat' : 'Open chat full screen');
  }
  requestAnimationFrame(() => {
    const body = $('#chat-body');
    if (body) body.scrollTop = body.scrollHeight;
    // Only grab focus on the empty state (operator just opened a fresh
    // chat). Expanding mid-conversation shouldn't pop the mobile
    // keyboard over a thread the operator is trying to read.
    if (State.chat.length === 0) $('#composer-input')?.focus();
  });
}

// ESC exits full-screen chat — a desktop convenience. The collapse
// button stays the primary control and the only one on touch. Bound
// once at module load so re-renders never stack duplicate listeners.
if (!window.__eonChatEscBound) {
  window.__eonChatEscBound = true;
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && State.view === 'chat' && State.chatFullscreen) {
      setChatFullscreen(false);
    }
  });
}

// Full-screen Chat hides the app header (app.css body.chat-full .app-bar),
// and with it the living header's Pause animation switch, and on Chat Ask's
// launcher rests (chat_conversation.css), so no motion switch was left on
// screen (live acceptance, 26 Oct 2026, step 4). The Chat bar carries one
// that app.css shows only under body.chat-full: the class that hides the
// header's switch shows this one, so the screen always has exactly one. It
// keeps no setting of its own: a tap clicks the header's switch, whose
// handler (living_workspace.js) flips the one setting, and the button reads
// that setting back from <html data-living-motion>, whoever changed it.
function chatMotionPaused() { return document.documentElement.getAttribute('data-living-motion') === 'paused'; }
function chatMotionIcon(paused) {
  const d = paused ? 'M8 5l11 7-11 7z' : 'M9 5v14M15 5v14';   // resume (play) / pause
  return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`;
}
function chatMotionButtonHtml() {
  const paused = chatMotionPaused(), label = paused ? 'Resume animation' : 'Pause animation';
  return `<button type="button" class="icon-btn-sm" id="chat-motion-btn" aria-pressed="${paused}"
           aria-label="${label}" title="${label}">${chatMotionIcon(paused)}</button>`;
}
function syncChatMotionButton() {
  const btn = $('#chat-motion-btn');
  if (!btn) return;
  const paused = chatMotionPaused(), label = paused ? 'Resume animation' : 'Pause animation';
  btn.innerHTML = chatMotionIcon(paused);
  btn.setAttribute('aria-pressed', String(paused));
  btn.setAttribute('aria-label', label);
  btn.title = label;
}
if (!window.__eonChatMotionWatch && window.MutationObserver) {
  window.__eonChatMotionWatch = new MutationObserver(syncChatMotionButton);
  window.__eonChatMotionWatch.observe(document.documentElement, { attributes: true, attributeFilter: ['data-living-motion'] });
}

function renderChatView() {
  return `
    <div class="chat-toolbar">
      <button class="icon-btn-sm" id="chat-history-btn" aria-label="Chat history">${historyIcon()}</button>
      <!-- 2026-08-04 (operator): Files — uploads filed by month, every
           conversation, so a statement from July is one tap away. -->
      <button class="icon-btn-sm" id="chat-files-btn" aria-label="Uploaded files"
              title="Uploaded files">${folderIcon()}</button>
      <div class="cc-brand">
        ${eonLogo(18, { spin: true })}
        <span class="cc-brand-title" id="chat-toolbar-title">${
          State.activeConversationId ? esc(State.activeConversationTitle || 'Chat') : 'EON'
        }</span>
      </div>
      <div style="display:flex; gap:6px;">
        <!-- 2026-06-07 (Daniel): expand/collapse the REAL chat to full
             screen, in place. Was an <a href="/chat"> to the separate
             white Gemini-style page that lacked charts/tools/etc. Now
             it toggles body.chat-full — same DOM, every feature intact.
             Icon flips between expand (corners-out) and collapse
             (corners-in / back) based on State.chatFullscreen. -->
        ${lw.active() ? chatMotionButtonHtml() : ''}
        <button class="icon-btn-sm" id="chat-fullscreen-btn"
           aria-label="${State.chatFullscreen ? 'Exit full screen' : 'Open in full screen'}"
           title="${State.chatFullscreen ? 'Collapse chat' : 'Open chat full screen'}">
          ${chatExpandToggleIcon()}
        </button>
        <!-- PR-7 (2026-07-06, operator-locked): per-thread model toggle.
             Base = Opus 4.8 (default lane, heuristic deep-mode intact);
             on = every turn in this thread pins Fable 5. Persisted per
             conversation in localStorage (eon_deep_convs). -->
        <button class="icon-btn-sm chat-deep-btn${_isDeepConv() ? ' deep-on' : ''}"
           id="chat-deep-btn" aria-pressed="${_isDeepConv()}"
           aria-label="Toggle deep mode"
           title="${_isDeepConv()
             ? 'Deep mode on: slower, more thorough answers in this thread'
             : 'Deep mode: slower, more thorough answers'}">
          ${deepIcon()}
        </button>
        <button class="icon-btn-sm" id="chat-new-btn" aria-label="New chat">${plusIcon()}</button>
      </div>
    </div>
    ${lw.active() ? lw.chatContext() : ''}
    <div class="chat-body" id="chat-body">${renderChatBodyInner()}</div>
    <div class="composer-shell">
      ${chatConversation.composerHtml()}
      ${(() => {
        // PR CLEANUP-SWEEP: conditional render. The old code always
        // emitted .attachments-strip even when empty, taking ~6px
        // vertical for no content. When there ARE attachments
        // bindChat() will create the strip on demand via
        // _renderAttachmentsStrip() called from the attach handler.
        const html = renderAttachments();
        return html
          ? `<div class="attachments-strip" id="attachments-strip">${html}</div>`
          : `<div id="attachments-strip" style="display:none;"></div>`;
      })()}
      <!-- WS-C (2026-07-26): a blocking approval could land off-screen
           behind the scroll-pin guard with only a (usually muted) chime.
           This banner pins above the composer until every card is decided. -->
      <div id="approval-banner" class="approval-banner" hidden></div>
      <div class="composer">
        <button class="attach-btn" id="composer-attach" type="button" aria-label="Attach file">${attachIcon()}</button>
        <textarea id="composer-input" placeholder="Ask EON…" rows="2"
                  aria-label="Message EON" autocapitalize="sentences" spellcheck="true"
                  aria-describedby="cq-composer-status"
                  autocomplete="off"
                  enterkeyhint="${_isTouchUA() ? 'enter' : 'send'}"></textarea>
        ${chatConversation.liveHtml()}
        <button class="mic" id="composer-mic" type="button" aria-label="Dictate: hold to record, release to send (three words or more)" title="Dictate a message, separate from live conversation" ${State.voice.transcribeAvailable ? '' : 'disabled aria-hidden="true"'} style="display:flex;${State.voice.transcribeAvailable ? '' : 'visibility:hidden;'}">${micIcon()}<span>Dictate</span></button>
        <button class="send" id="composer-send" aria-label="Send message to EON" disabled>${sendIcon()}</button>
      </div>
      ${chatConversation.statusHtml()}
      <p class="cq-live-boundary" id="cq-live-boundary">Live voice opens in Ask EON’s own conversation. Your text draft stays here.</p>
      <!-- PR CLEANUP-SWEEP: composer-hint now shows in BOTH modes —
           keyboard discoverability for new users + a quick reminder
           for power users. CSS already styles it compactly. -->
      <div class="composer-hint composer-hint-show">${_isTouchUA() ? 'Return adds a line · Send submits' : 'Enter to send · Shift + Enter for a new line'}</div>
      <input type="file" id="file-input-hidden"
             accept=".pdf,.docx,.csv,.xlsx,.txt,.md,.jpg,.jpeg,.png,.webp,.heic,.heif,image/*"
             style="display:none;" multiple />
      <!-- Voice-parity pass (2026-07-12): the overlay is a live surface
           now — level meter off the WebAudio analyser, a rolling interim
           transcript (dimmed, Claude Code style), elapsed clock, and the
           mode-aware hint line. -->
      <div class="rec-overlay" id="rec-overlay" style="display:none;">
        <div class="rec-pulse"></div>
        <div class="rec-meter" id="rec-meter" aria-hidden="true">
          <span></span><span></span><span></span><span></span><span></span>
        </div>
        <div class="rec-label">Listening…</div>
        <div class="rec-transcript" id="rec-transcript" aria-live="polite"></div>
        <div class="rec-meta">
          <span id="rec-clock">0:00</span>
          <span class="rec-cancel-hint" id="rec-hint">Slide up to cancel</span>
        </div>
      </div>
    </div>
    <button class="chat-jump" id="chat-jump" aria-label="Jump to latest" title="Jump to latest" hidden>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
    </button>
  `;
}

// WS-C (2026-07-26): tool-trace card markup, shared by the full body
// render AND the per-event node patch below.
// v3.0p16 9f (Item 4): tool-trace card. Live: full list with
// breathing-dot spinner per running tool. Collapsed (post-done):
// single muted summary line.
function _toolTraceHtml(m) {
  const totalMs = m.entries.reduce((s, e) => s + (e.durationMs || 0), 0);
  const totalSec = (totalMs / 1000).toFixed(1);
  const n = m.entries.length;
  const errCount = m.entries.filter(e => e.state === 'error').length;
  const summary = errCount > 0
    ? `${n} tool${n === 1 ? '' : 's'} used · ${totalSec}s · ${errCount} error${errCount === 1 ? '' : 's'}`
    : `${n} tool${n === 1 ? '' : 's'} used · ${totalSec}s`;

  const items = m.entries.map(e => {
    const icon = e.state === 'running'
      ? '<span class="eon-loader eon-loader-sm" aria-label="working"></span>'
      : e.state === 'error'
        ? '<span class="tool-icon err">✕</span>'
        : '<span class="tool-icon ok">✓</span>';
    return `<div class="tool-entry ${e.state}">${icon}<span class="tool-text">${esc(e.text)}</span></div>`;
  }).join('');

  // PR CHAT-UI-PHASE2: in Claude Code mode, tool-trace renders
  // as a native <details>/<summary> disclosure. Collapsed by
  // default once tools have finished (m.collapsed === true);
  // click to expand inline. While tools are still running we
  // keep the expanded view so the operator can watch progress
  // — same as today's Comfortable behaviour.
  const claudeStyle = document.body.classList.contains('chat-style-claude');
  const allDone = m.entries.every(e => e.state !== 'running');
  if (claudeStyle && allDone) {
    // open attr = expanded; absent = collapsed.
    const openAttr = m.collapsed === false ? ' open' : '';
    return `<details class="tool-trace cc"${openAttr}>
      <summary>${esc(summary)}</summary>
      <div class="tool-trace-body">${items}</div>
    </details>`;
  }

  // Existing Comfortable behaviour preserved untouched.
  if (m.collapsed) {
    return `<div class="tool-trace collapsed">${esc(summary)}</div>`;
  }
  return `<div class="tool-trace">${items}</div>`;
}

// WS-C: replace only the LIVE trace node (always the newest .tool-trace
// in the DOM while a turn runs). Falls back to a full body render when
// the node isn't mounted yet. Keeps the scroll-pin contract.
function _patchLiveToolTrace(trace) {
  const body = document.getElementById('chat-body');
  if (!body) return;
  const nodes = body.querySelectorAll('.tool-trace');
  const node = nodes.length ? nodes[nodes.length - 1] : null;
  if (!node) { renderChatBody(); return; }
  const stick = (body.scrollHeight - body.scrollTop - body.clientHeight) < 120;
  node.outerHTML = _toolTraceHtml(trace);
  if (stick) requestAnimationFrame(() => { body.scrollTop = body.scrollHeight; });
}

function renderChatBody() {
  lw.syncChatContext();
  const el = $('#chat-body');
  if (el) {
    // Scroll-pin guard (2026-06-11): only re-stick to the bottom if the
    // operator is already near it. If they've scrolled up to read history,
    // a re-render (new message, 30s poll, tool-status tick) must NOT yank
    // them back down. Measure BEFORE replacing innerHTML.
    const stick = (el.scrollHeight - el.scrollTop - el.clientHeight) < 120;
    el.innerHTML = renderChatBodyInner();
    eonChatGalleryWrap(el);   // plural images become a grid (2026-09-07)
    window.EONChatWorkspace?.sync({root: document.querySelector('.chat-wrap')});
    if (stick) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
    // Content growth changes the distance-from-bottom — re-evaluate the
    // jump-to-latest button's visibility after layout settles.
    requestAnimationFrame(() => el.dispatchEvent(new Event('scroll')));
  }
  // WS-C: keep the pinned approval banner + chat-tab dot in sync with
  // every body render (cards land, decisions resolve, history loads).
  _updateApprovalBanner();
  chatConversation.update();
}

// EON message header — the spark mark + name (replaces the old plain dot).
function eonFromHeader() {
  return `<div class="from">${eonLogo(15)}<span class="from-name">EON</span></div>`;
}

// Contextual "thinking" ticker (2026-06-09). Picks a phrase set keyed to the
// operator's last question so the wait reads as intelligent, not generic —
// then the EON spark spins beside a shimmering 3-phrase roll. Pure CSS
// animation; no interval to leak across re-renders.
function eonThinkingPhrases() {
  const lastUser = [...State.chat].reverse().find(m => m && m.role === 'user');
  const q = ((lastUser && lastUser.text) || '').toLowerCase();
  const has = (...ws) => ws.some(w => q.includes(w));
  if (has('cash', 'money', 'revenue', 'profit', 'runway', 'expense', 'margin', 'p&l', 'bank', 'spend', 'mer'))
    return ['counting the cash', 'reading the ledger', 'running the math'];
  if (has('ad', 'meta', 'tiktok', 'snap', 'roas', 'campaign', 'cpa', 'creative', 'funnel'))
    return ['reading the funnel', 'weighing the spend', 'chasing the ROAS'];
  if (has('customer', 'retention', 'cohort', 'ltv', 'churn', 'repeat', 'loyal'))
    return ['reading the base', 'tracing the cohort', 'sizing the loyalty'];
  if (has('inventory', 'stock', 'sku', 'reorder', 'warehouse', 'restock'))
    return ['counting stock', 'reading the shelves', 'sizing the gap'];
  if (has('product', 'seller', 'bestseller', 'what sells', 'top product'))
    return ['reading the floor', 'sizing demand', 'ranking movers'];
  if (has('rep', 'staff', 'team', 'customer care', 'agent', 'shift'))
    return ['reading the room', 'checking the desk', 'sizing the shift'];
  return ['considering', 'digging deep', 'plotting'];
}
function eonThinkingIndicator() {
  const phrases = eonThinkingPhrases();
  // Append a copy of the first phrase so the vertical roll loops seamlessly.
  const track = [...phrases, phrases[0]]
    .map(p => `<span class="w">${esc(p)}…</span>`).join('');
  return `<div class="cc-think">
    <span class="cc-think-mark">${eonLogo(18, { spin: true })}</span>
    <span class="cc-think-words"><span class="cc-think-track">${track}</span></span>
  </div>`;
}

function renderChatBodyInner() {
  if (State.chat.length === 0) {
    // Operator 2026-07-06 (supersedes PR-8's mark + tagline): ONE mark
    // only — the toolbar carries the brand — and ONE greeting, dynamic
    // and goofy by hour ("hey night crawler..."), random per visit.
    // Starter prompts stay: quiet utility, send on tap.
    const h = new Date().getHours();
    const pool =
        h < 5  ? ['Hey night crawler — what are we plotting?',
                  'Still up? The books never sleep either.',
                  'The city sleeps. We don’t. What’s the move?',
                  '3am thoughts hit different. Try me.']
      : h < 9  ? ['Up before the sun. Let’s get it.',
                  'Early bird. What’s first?',
                  'Fresh day, clean ledger — where do we start?']
      : h < 12 ? ['Morning, boss. What are we building today?',
                  'What’s the play this morning?',
                  'The numbers are warm. What do you want to know?']
      : h < 17 ? ['Afternoon. What needs untangling?',
                  'Midday check — what’s the move?',
                  'What are we cooking this afternoon?']
      : h < 21 ? ['Evening. Let’s close some loops.',
                  'Golden hour. What’s left on the list?',
                  'What should we wrap up tonight?']
      :          ['Night owl hours. What do you want to work on?',
                  'Late shift? Let’s make it count.',
                  'One more thing before midnight?'];
    const greet = pool[Math.floor(Math.random() * pool.length)];
    const chips = [
      'How did we do yesterday?',
      'What needs my attention today?',
      'Top products this week',
    ];
    return `
      <div class="chat-empty-state eon-hero">
        <canvas class="eon-hero-wash" id="eon-hero-wash" aria-hidden="true"></canvas>
        <div class="eon-hero-greet">${esc(greet)}</div>
        <div class="eon-hero-chips">
          ${chips.map(c =>
            `<button class="eon-hero-chip" type="button" data-starter="${esc(c)}">${esc(c)}</button>`
          ).join('')}
        </div>
      </div>
    `;
  }
  return State.chat.map((m, i) => {
    const id = `msg-${i}`;
    if (m.role === 'pending') return eonThinkingIndicator();
    if (m.role === 'permission-card') {
      // v3.0p17 9h item 5: inline confirmation card.
      // 2026-08-04: the controls come from the card's intent — Approve/
      // Deny for a decision, the answer choices for a question, a single
      // dismiss for an FYI.
      const intent = permCardIntent(m);
      const decided = !!m.decided;
      const cls = `permission-card permission-card--${intent}${decided ? ' decided' : ''}`;
      const payloadStr = (typeof m.payload === 'string')
        ? m.payload
        : (m.payload ? JSON.stringify(m.payload, null, 2) : '');
      const payloadHtml = payloadStr
        ? `<pre class="permission-payload">${esc(payloadStr.slice(0, 600))}</pre>`
        : '';
      // 2026-07-30 (p561): the safety-rail caveats ("wallet runway 1.7
      // days", "stock cover unknown") were stored in the payload but the
      // JSON preview above clips at 600 chars and the caveats key starts
      // past it — invisible every single time. Render them as plain lines
      // ABOVE the technical blob so the operator sees what was and was
      // not checkable before he taps Approve.
      const cavList = (m.payload && typeof m.payload === 'object'
                       && Array.isArray(m.payload.caveats))
        ? m.payload.caveats.filter(c => c != null && String(c).trim())
        : [];
      const caveatsHtml = cavList.length
        ? `<div class="permission-caveats">${cavList.map(c =>
             `<div class="permission-caveat">${esc(String(c))}</div>`
           ).join('')}</div>`
        : '';
      const buttonsOrFooter = decided
        ? `<div class="permission-footer">${esc(permCardFooterText(m))}</div>`
        : permCardControlsHtml(m);
      return `<div class="${cls}" data-action-id="${esc(m.action_id || '')}">
        <div class="permission-title">${esc(m.title || 'Approval required')}</div>
        ${caveatsHtml}
        ${payloadHtml}
        ${buttonsOrFooter}
      </div>`;
    }
    if (m.role === 'widget-card') {
      // v3.0p18 9u: chat-built dashboard preview card. Renders an
      // inline chart with a Pin button. data shape depends on
      // widget_type (line/bar/kpi/list).
      const pinned = !!m.pinned;
      const pinBtn = pinned
        ? `<button class="btn" type="button" disabled>✓ Pinned to dashboard</button>`
        : `<button class="btn widget-pin-btn" type="button" data-pin="1">📌 Pin to dashboard</button>`;
      const chart = renderWidgetBody(m.widget_type, m.data || {});
      return `<div class="widget-card${pinned ? ' pinned' : ''}" data-chat-idx="${i}">
        <div class="widget-card-header">
          <span class="widget-card-title">${esc(m.title || 'Widget')}</span>
          <span class="widget-card-meta">${esc(m.widget_type || '')} · ${esc(m.source || '')}</span>
        </div>
        <div class="widget-card-body">${chart}</div>
        <div class="widget-card-footer">${pinBtn}</div>
      </div>`;
    }
    if (m.role === 'tool-trace') {
      // WS-C: markup extracted to _toolTraceHtml so the live status /
      // tool_done handlers can patch just this node in place instead
      // of rebuilding the whole thread per tool event.
      return _toolTraceHtml(m);
    }
    if (m.role === 'user') {
      // 2026-05-19 (Daniel): operator messages also get a copy button +
      // raised bubble treatment, matching the EON-side affordance. The
      // copy handler is data-attribute-driven so the same listener at
      // line ~3345 catches clicks on user bubbles too.
      const userCopyBtn = `<button class="msg-copy" data-copy-msgid="${i}"
                               title="Copy message" aria-label="Copy message">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>`;
      // WS-C: attached files echo into the bubble — sent images used to
      // vanish from the transcript the moment the chips cleared.
      const attsHtml = (m.atts && m.atts.length)
        ? `<div class="msg-atts">${m.atts.map(a => {
            const isImg = /^image\//.test(a.fileType || '') && a.thumbUrl;
            return isImg
              ? `<img class="msg-att-thumb" src="${esc(a.thumbUrl)}" data-full="${esc(a.thumbUrl)}" alt="${esc(a.filename)}" loading="lazy" />`
              : `<span class="msg-att-chip">${esc(a.filename)}</span>`;
          }).join('')}</div>`
        : '';
      return `
        <div class="msg user" id="${id}" data-msg-text="${esc(m.text)}">
          ${attsHtml}
          <div class="text">${esc(m.text)}</div>
          <div class="msg-actions">${userCopyBtn}</div>
        </div>`;
    }
    if (m.role === 'eon-streaming') {
      // Live stream — markdown renders as it arrives (WS-C), caret at the
      // tail. No paragraph-fade yet (applied once the message completes).
      return `<div class="msg eon streaming" id="${id}">
        ${eonFromHeader()}
        <div class="text">${renderMarkdownToHtml(m.text)}<span class="caret"></span></div>
      </div>`;
    }
    // Completed EON message — split into paragraphs for nicer typography,
    // with pin suggestion if backend flagged it. v3.0: no more collapse/show-more.
    const text = m.text || '';
    // 2026-06-06 Generative UI: detect InteractiveWidget JSON blocks
    // and render them inline as interactive components. Surrounding
    // prose is rendered via the existing markdown/paragraph paths so
    // EON can mix tools with narrative.
    const widgetParse = extractInteractiveWidgets(text);
    if (widgetParse.hasWidget) {
      // Cache widget configs so binders can rehydrate slider math.
      State.widgets = State.widgets || {};
      let body = widgetParse.segments.map((seg, segI) => {
        if (seg.kind === 'text') {
          const t = (seg.value || '').trim();
          if (!t) return '';
          if (/\n\|.*\|/.test(t) || /\*\*/.test(t) || /^##\s/m.test(t)) {
            return `<div class="eon-prose">${renderMarkdownToHtml(t)}</div>`;
          }
          return `<div class="eon-prose">${esc(t).replace(/\n/g, '<br/>')}</div>`;
        }
        const wid = `wid-${id}-${segI}`;
        State.widgets[wid] = seg.value;
        return renderInteractiveWidget(seg.value, wid);
      }).join('');
      const copyBtn = `<button class="msg-copy" data-copy-msgid="${i}"
                         title="Copy message" aria-label="Copy message">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>`;
      // Defer binding to next tick so DOM exists.
      requestAnimationFrame(() => {
        document.querySelectorAll(`#${id} .eon-widget`).forEach(el => bindInteractiveWidget(el));
      });
      return `<div class="msg eon${m.dropped ? ' dropped' : ''}" id="${id}"
                   data-msg-text="${esc(text)}">
        ${eonFromHeader()}
        <div class="text">${body}</div>
        ${sourcesHtml(m.source_refs)}
        <div class="msg-actions">${copyBtn}${chatConversation.taskHtml(m,i)}</div>
      </div>`;
    }
    const claudeMode = document.body.classList.contains('chat-style-claude');
    let body;
    if (claudeMode && /```/.test(text)) {
      // PR CHAT-UI-PHASE3: render code fences as <pre><code> with optional
      // file-path header chip and Prism syntax highlight. Only kicks in
      // when the operator has chat-style-claude enabled AND the message
      // contains at least one triple-backtick fence.
      body = parseClaudeCodeBody(text);
      // Lazy-load Prism + highlight after the DOM lands.
      loadPrismIfNeeded();
    } else if (/\n\|.*\|/.test(text) || /\*\*/.test(text) || /^##\s/m.test(text)
               || /```/.test(text) || /^\s*(?:[-*•]|\d+[.)])\s+/m.test(text)) {
      // PR CHAT-MARKDOWN-TABLES (2026-05-20): full markdown render
      // when the text contains a pipe-table, bold markers, or
      // markdown headings. Otherwise fall through to the lighter
      // paragraph-fade path that preserves the per-para stagger
      // animation. The detection regex is deliberately cheap so
      // 99% of plain-prose messages never trigger the full parser.
      // WS-C: fences + lists route here too.
      body = renderMarkdownToHtml(text);
    } else {
      const paras = text.split(/\n\n+/).filter(p => p.trim().length > 0);
      if (paras.length > 1) {
        // Multi-paragraph: each paragraph fades in (CSS animation), staggered.
        // Linkify so bare URLs / [label](url) in plain prose are clickable too.
        body = paras.map((p, pi) =>
          `<span class="para" style="animation-delay: ${pi * 80}ms">${_linkifyHtml(esc(p))}</span>`
        ).join('');
      } else {
        body = _linkifyHtml(esc(text));
      }
    }
    const pinHtml = m.pin_suggest && !m.pinned
      ? `<button class="pin-suggest" data-msgid="${i}">${pinIcon()} Pin this</button>`
      : m.pinned
      ? `<button class="pin-suggest pinned" disabled>${pinIcon()} Pinned</button>`
      : '';
    // v3.0p32 PR MONEY-COMPLETE: copy icon under each EON message.
    // Operator-requested. EON-only (not user-side). Uses the raw text
    // so the operator gets the source string, not the rendered HTML.
    const copyBtn = `<button class="msg-copy" data-copy-msgid="${i}"
                       title="Copy message" aria-label="Copy message">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2"
             stroke-linecap="round" stroke-linejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      </button>`;
    // EON Voice (2026-07-13): on-demand playback per message — the
    // menu toggle auto-speaks every reply; this speaks just one.
    const speakBtn = `<button class="msg-speak" data-speak-msgid="${i}" ${_voiceTtsAttributes()}
                  title="Play this reply" aria-label="Play this reply">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
          </svg>
        </button>`;
    return `<div class="msg eon${m.dropped ? ' dropped' : ''}" id="${id}"
                 data-msg-text="${esc(text)}">
      ${eonFromHeader()}
      <div class="text">${body}</div>
      ${sourcesHtml(m.source_refs)}
      <div class="msg-actions">${copyBtn}${speakBtn}${pinHtml}${chatConversation.taskHtml(m,i)}</div>
    </div>`;
  }).join('');
}

// ── WS-C (2026-07-26): composer helpers ─────────────────────
// The main composer was the weakest of the app's six: 96px cap
// (~3 lines) twice-hardcoded, 15px font (iOS zoom-on-focus), no
// draft persistence, no refocus after send, Enter-sends on phone
// keyboards. Donor patterns: cswBindComposer + the retired chat.js.

function _isTouchUA() {
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
}

const _composerSizeCache = new WeakMap();
const _composerFramePending = new WeakSet();

function _composerAutosize(input) {
  // Grows to 40% of the viewport before scrolling (was 96px ≈ 3
  // lines); the scrollbar appears only when clipped so long
  // directives are never invisibly cut.
  const cap = Math.max(96, Math.round(window.innerHeight * 0.4));
  const cached = _composerSizeCache.get(input);
  const value = input.value;
  // Nothing that could change the height has changed: same box width,
  // same cap, the draft only ever gained characters at the end, and the
  // content still fits the box it has. Skipping the height:'auto' reset
  // here is what kills the per-keystroke collapse-and-regrow snap
  // (donor: cswBindComposer).
  //
  // The append test is `startsWith`, not a length comparison: a REPLACED
  // value can be longer in characters and shorter in lines (a 60-char
  // one-liner dropped in where an eight-line draft was), and
  // scrollHeight cannot catch that — a textarea's scrollHeight is
  // floored at its clientHeight, so an over-tall box reports "content
  // fits" no matter how little it holds. Only a true append is
  // guaranteed not to shrink the box.
  if (cached && cached.width === input.clientWidth && cached.cap === cap
      && value.startsWith(cached.value) && input.scrollHeight <= input.clientHeight) {
    cached.value = value;
    return;
  }
  const scrollTop = input.scrollTop;
  input.style.height = 'auto';
  const needed = input.scrollHeight;
  input.style.height = Math.min(needed, cap) + 'px';
  input.style.overflowY = needed > cap ? 'auto' : 'hidden';
  if (needed > cap) input.scrollTop = scrollTop;
  _composerSizeCache.set(input, { width: input.clientWidth, cap, value });
}

// ── Composer 1→2 row flicker (2026-08-04, rebuilt 2026-08-12) ────
// PR-7 flipped .composer--tall once the live scrollHeight passed 48px,
// measured in whatever layout was on screen. But the class IS that width:
// going tall hands the textarea the whole row, the text un-wraps onto one
// line, scrollHeight drops back under 48, the class comes off, the
// textarea narrows, the text wraps again — a loop on every keystroke near
// the boundary. Two guards. The decision is measured at the COMPACT
// (one-row) width in BOTH states, so the class can never move its own
// input; and a hysteresis band absorbs sub-pixel jitter at the edge.
// One line = 24px min-height + 16px padding = 40px, two = ~61px, so the
// 44/48 band sits in the empty space between them.
const COMPOSER_TALL_ON = 48;
const COMPOSER_TALL_OFF = 44;

function _composerTallNext(measured, wasTall) {
  return wasTall ? measured > COMPOSER_TALL_OFF : measured > COMPOSER_TALL_ON;
}

// The width the textarea has in the one-row layout, derived from the
// sibling controls so it reads the same whether or not tall is on.
function _composerCompactWidth(composer, input) {
  const cs = getComputedStyle(composer);
  const gap = parseFloat(cs.columnGap) || parseFloat(cs.gap) || 0;
  let avail = composer.clientWidth
    - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
  let items = 0;
  for (const el of composer.children) {
    const w = el.offsetWidth;
    if (!w) continue;             // display:none — not a flex item, no gap
    items += 1;
    if (el !== input) avail -= w;
  }
  return Math.max(40, Math.round(avail - gap * Math.max(0, items - 1)));
}

function _composerMeasureAtWidth(input, width) {
  // flex-basis wins over width for a flex item, so both are pinned; the
  // restore lands in the same task, so nothing paints at this width.
  const flex0 = input.style.flex;
  const width0 = input.style.width;
  const height0 = input.style.height;
  input.style.flex = '0 0 ' + width + 'px';
  input.style.width = width + 'px';
  input.style.height = 'auto';
  const measured = input.scrollHeight;
  input.style.flex = flex0;
  input.style.width = width0;
  input.style.height = height0;
  return measured;
}

function _composerApplyTall(input) {
  const composer = input.closest('.composer');
  if (!composer || !composer.clientWidth) return;   // not mounted — no verdict
  const wasTall = composer.classList.contains('composer--tall');
  const measured = _composerMeasureAtWidth(
    input, _composerCompactWidth(composer, input));
  const next = _composerTallNext(measured, wasTall);
  if (next !== wasTall) composer.classList.toggle('composer--tall', next);
}

// One layout pass per frame for both halves, in order: the class decides
// the width, so the height has to be measured after it settles.
function _composerSync(input) {
  if (!input || _composerFramePending.has(input)) return;
  _composerFramePending.add(input);
  requestAnimationFrame(() => {
    _composerFramePending.delete(input);
    _composerApplyTall(input);
    _composerAutosize(input);
  });
}

// A REPLACED value is not a typed one. Every site that assigns
// input.value outright — switching conversations, restoring a parked
// draft — goes through here, so the cached measurement is dropped and the
// next pass measures the new draft cold. Without it a taller draft's box
// survives a switch to a shorter one and eats the viewport until a
// character is deleted. (sendChat drops the cache the same way inline; it
// clears the box itself rather than re-measuring it.)
function _composerValueReplaced(input) {
  if (!input) return;
  _composerSizeCache.delete(input);
  _composerSync(input);
  _syncDictateLabel(input);   // no input event fires: Dictate's name follows the new draft (F2)
}

// Drafts persist per conversation (localStorage) — they used to die on
// ANY render(): tab switch, menu nav, alert dismiss.
function _chatDraftKey(convId) {
  // Old keys have no owner scope. Leave them intact; never guess which
  // signed-in person an unattributable legacy draft belongs to.
  return draftStorageKey(State, convId !== undefined ? convId : State.activeConversationId);
}
function _chatDraftSave(v) {
  try {
    if (v && v.trim()) localStorage.setItem(_chatDraftKey(), v);
    else localStorage.removeItem(_chatDraftKey());
  } catch (_) { /* storage full/blocked — drafts are best-effort */ }
}
function _chatDraftLoad() {
  try { return localStorage.getItem(_chatDraftKey()) || ''; } catch (_) { return ''; }
}
function _chatDraftClear() {
  try { localStorage.removeItem(_chatDraftKey()); } catch (_) {}
}

// Turn epochs: switching (or starting) a conversation abandons the
// in-flight turn — stream events stamped with an older epoch are
// dropped instead of bleeding into the newly-opened thread.
let _turnEpoch = 0;
let _sendEpoch = 0;
let _sendInFlight = false;
function _abandonInFlightTurn() {
  _turnEpoch += 1;
  _sendInFlight = false;
  stopSmoothReveal();
  _stopStreamWatchdog();
}

// Pinned approval banner — visible whenever any permission card is
// undecided, no matter where the card itself scrolled to.
function _updateApprovalBanner() {
  const b = document.getElementById('approval-banner');
  // 2026-08-04: an FYI is not an approval waiting. Counting notify cards
  // here is exactly the "stuck on approve" the operator called out.
  const pending = (State.chat || []).filter(
    m => m.role === 'permission-card' && !m.decided
         && permCardIntent(m) !== 'notify').length;
  if (b) {
    if (pending) {
      b.innerHTML = `<span class="approval-dot"></span>` +
        `<b>${pending} approval${pending === 1 ? '' : 's'} waiting</b>` +
        `<span class="approval-go">tap to review</span>`;
      b.hidden = false;
      b.onclick = () => {
        const card = document.querySelector(
          '.permission-card:not(.decided):not(.permission-card--notify)');
        if (card) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };
    } else {
      b.hidden = true;
      b.innerHTML = '';
    }
  }
  // Chat tab dot — an approval must be findable from any view.
  const navChat = document.querySelector('.nav-item[data-view="chat"]');
  if (navChat) {
    let dot = navChat.querySelector('.nav-dot');
    if (pending && !dot) {
      navChat.insertAdjacentHTML('beforeend', '<span class="nav-dot" aria-label="approval waiting"></span>');
    } else if (!pending && dot) {
      dot.remove();
    }
  }
}

function bindChat() {
  // THE MOUNT PATH (first vet, 2026-09-09). There are two ways the
  // transcript reaches the DOM: renderChatBody() sets innerHTML and wraps
  // straight after, and renderChatView() (:7493) inlines
  // renderChatBodyInner() into the view's own markup. Only the first was
  // wired, so leaving Chat and coming back rebuilt a multi-image reply as
  // stacked full-width pictures with no grid and no "+N" — the exact
  // complaint this lane exists to fix — until some unrelated event
  // happened to call renderChatBody and the layout popped. bindView()
  // calls bindChat() on every chat mount (:2476), so wrapping here covers
  // the view path; eonChatGalleryWrap is idempotent (it skips a group it
  // has already built), so the double call on other paths is free.
  eonChatGalleryWrap($('#chat-body'));
  const input = $('#composer-input');
  const send = $('#composer-send');
  if (input) {
    // A person a redraw kept on a call's control stays there (chatConversation.mount).
    setTimeout(() => { if (!chatConversation.holdsFocus()) input.focus(); }, 50);
    // Restore this conversation's draft (WS-C).
    const draft = _chatDraftLoad();
    if (draft && !input.value) {
      input.value = draft;
      send.disabled = !draft.trim();
      _composerValueReplaced(input);
    }
    _syncDictateLabel(input);
    input.addEventListener('input', () => {
      send.disabled = !input.value.trim();
      _chatDraftSave(input.value);
      _syncDictateLabel(input);
      // PR-7 (2026-07-06, operator ask): once the text wraps past one
      // line, the buttons drop BELOW the textarea (Gemini-style) instead
      // of squeezing it. Reflow + height now settle together in one
      // frame — see _composerSync for why the toggle can't oscillate.
      _composerSync(input);
    });
    input.addEventListener('keydown', (e) => {
      if (canSubmitKey(e, _isTouchUA())) {
        // Confirming an IME composition is never a send. Touch Enter
        // stays a newline; an explicit hardware Cmd/Ctrl+Enter can send.
        e.preventDefault();
        sendChat();
      }
    });
  }
  send?.addEventListener('click', sendChat);
  chatConversation.mount();

  // Jump-to-latest affordance (2026-06-11): pairs with the scroll-pin guard
  // — when the operator scrolls up to read history, a floating ↓ button
  // appears to snap back to the newest message (Gemini-style).
  const cbody = $('#chat-body');
  const jump = $('#chat-jump');
  if (cbody && jump) {
    const updateJump = () => {
      // 120px matches the scroll-pin guard — no dead zone where the chat
      // neither sticks nor offers the jump button.
      const far = (cbody.scrollHeight - cbody.scrollTop - cbody.clientHeight) > 120;
      jump.hidden = !far;
    };
    cbody.addEventListener('scroll', updateJump, { passive: true });
    jump.addEventListener('click', () => {
      cbody.scrollTo({ top: cbody.scrollHeight, behavior: 'smooth' });
      jump.hidden = true;
    });
    updateJump();
  }

  // Capability may arrive after the composer; update controls without rendering.
  _syncVoiceControls();

  // v3.0p14: chat history drawer + new chat button
  $('#chat-history-btn')?.addEventListener('click', openChatHistoryDrawer);
  $('#chat-files-btn')?.addEventListener('click', openFilesDrawer);
  $('#chat-new-btn')?.addEventListener('click', startNewConversation);

  // PR-7: deep-mode toggle — flips the per-thread flag and restyles in
  // place (no re-render needed).
  $('#chat-deep-btn')?.addEventListener('click', () => {
    _toggleDeepConv();
    const b = $('#chat-deep-btn');
    const on = _isDeepConv();
    if (b) {
      b.classList.toggle('deep-on', on);
      b.setAttribute('aria-pressed', String(on));
      b.title = on ? 'Deep mode on: slower, more thorough answers in this thread'
                   : 'Deep mode: slower, more thorough answers';
    }
    showToast(on ? 'Deep mode on: slower, more thorough answers in this thread'
                 : 'Deep mode off: quicker answers in this thread');
  });

  // 2026-06-07: expand/collapse the chat to full screen in place.
  $('#chat-fullscreen-btn')?.addEventListener('click', () => {
    setChatFullscreen(!State.chatFullscreen);
  });
  // The full-screen motion switch (chatMotionButtonHtml): a tap flips the
  // header's one setting. Bound here, in bindChat's own body, because tests
  // lift bindChat alone into a sealed scope (test_chat_inline_caller_20261015).
  $('#chat-motion-btn')?.addEventListener('click', () => {
    document.querySelector('#hdr .lw-motion')?.click();
    syncChatMotionButton();
    chatConversation.update();   // More names it by its words, which just changed
  });
  
  // v3.0p14: file attach button + hidden file input + drag-drop
  const attachBtn = $('#composer-attach');
  const hiddenInput = $('#file-input-hidden');
  if (attachBtn && hiddenInput) {
    attachBtn.addEventListener('click', () => hiddenInput.click());
    hiddenInput.addEventListener('change', async (e) => {
      const files = Array.from(e.target.files || []);
      // Allow multi-select but cap at 3 in flight to avoid token bloat.
      // 2026-06-01: improved alert copy — was "0 slot(s) available;
      // uploading first 0" which told the operator nothing useful. New
      // message names the count + tells them how to clear.
      const queued = State.attachments.length;
      const slotsLeft = Math.max(0, 3 - queued);
      if (slotsLeft === 0) {
        alert(
          `You have ${queued} file${queued === 1 ? '' : 's'} already attached.\n\n` +
          `Tap × on a chip to remove it, or use "Clear all" to remove everything ` +
          `before attaching new files. Cap is 3 files per message.`
        );
        hiddenInput.value = '';
        return;
      }
      const toUpload = files.slice(0, slotsLeft);
      if (files.length > slotsLeft) {
        alert(
          `Max 3 files per message. You already have ${queued} attached.\n` +
          `Uploading first ${slotsLeft} of ${files.length} selected.`
        );
      }
      // Upload in parallel; each manages its own optimistic chip
      await Promise.all(toUpload.map(f => uploadFile(f)));
      // Clear the input so the same file can be picked again later
      hiddenInput.value = '';
    });
  }
  
  // Render any existing attachments (e.g., uploaded but not yet sent)
  renderAttachmentsStrip();
  
  // v3.0p14: rehydrate pending uploads (uploaded but not attached to a
  // message yet — survives page reload). Only on first chat tab load to
  // avoid clobbering an in-progress upload state.
  if (State.attachments.length === 0) {
    loadPendingAttachments();
  }
  
  // Drag-and-drop on chat-body AND composer-shell — Daniel flagged
  // 2026-06-01 that drop wasn't working; turns out it was wired only
  // to chat-body. Common operator mental model is "drop near the
  // text input" which means the composer area, not the message list.
  // Wire both surfaces with a single shared handler.
  const dropZones = [$('#chat-body'), document.querySelector('.composer-shell')]
    .filter(Boolean);
  const handleFilesDropped = async (files) => {
    const queued = State.attachments.length;
    const slotsLeft = Math.max(0, 3 - queued);
    if (slotsLeft === 0) {
      alert(
        `You have ${queued} file${queued === 1 ? '' : 's'} already attached.\n\n` +
        `Tap × on a chip to remove it, or use "Clear all" before dropping new files. ` +
        `Cap is 3 files per message.`
      );
      return;
    }
    const toUpload = files.slice(0, slotsLeft);
    if (files.length > slotsLeft) {
      alert(
        `Max 3 files per message. You already have ${queued} attached.\n` +
        `Uploading first ${slotsLeft} of ${files.length} dropped.`
      );
    }
    await Promise.all(toUpload.map(f => uploadFile(f)));
  };
  dropZones.forEach((zone) => {
    let dragDepth = 0;  // browser fires dragenter/leave per child element
    zone.addEventListener('dragenter', (e) => {
      // Only react to file drags — ignore text/element drags.
      if (!(e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files'))) return;
      e.preventDefault();
      dragDepth++;
      zone.classList.add('drag-over');
    });
    zone.addEventListener('dragover', (e) => {
      if (!(e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files'))) return;
      e.preventDefault();  // required to allow drop
    });
    zone.addEventListener('dragleave', () => {
      dragDepth--;
      if (dragDepth <= 0) {
        dragDepth = 0;
        zone.classList.remove('drag-over');
      }
    });
    zone.addEventListener('drop', async (e) => {
      if (!(e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files'))) return;
      e.preventDefault();
      dragDepth = 0;
      zone.classList.remove('drag-over');
      const files = Array.from(e.dataTransfer?.files || []);
      await handleFilesDropped(files);
    });
  });

  // v3.0: show-more removed — full messages always render

  // Suggest tiles moved to a document-level delegated listener (see the
  // [data-suggest-msg] handler at the top of this file). A once-per-bindChat
  // querySelectorAll could never reach them: the tiles render on the Money
  // page, and re-render there on every load.

  // Copy-message clicks: delegated at document level (see top of file)
  // so they keep working after every renderChatBody() innerHTML replace.

  // Pin suggestion clicks
  document.querySelectorAll('.pin-suggest:not(.pinned)').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const idx = parseInt(btn.dataset.msgid, 10);
      const m = State.chat[idx];
      if (!m || !m.pin_suggest) return;
      btn.disabled = true;
      btn.textContent = 'Pinning…';
      try {
        await api(`/api/tenants/${State.tenant.slug}/pins`, {
          method: 'POST',
          body: JSON.stringify({
            question: m.pin_suggest.question,
            answer: m.pin_suggest.answer,
            suggest_reason: m.pin_suggest.reason,
          }),
        });
        m.pinned = true;
        showToast('✓ Pinned to home');
        renderChatBody();
      } catch (err) {
        btn.disabled = false;
        btn.innerHTML = `${pinIcon()} Pin this`;
        showToast(`Could not pin: ${err.message}`);
      }
    });
  });

  // Scroll to bottom on enter
  const body = $('#chat-body');
  if (body) requestAnimationFrame(() => { body.scrollTop = body.scrollHeight; });
  window.EONChatWorkspace?.mount({
    root: document.querySelector('.chat-wrap'),
    homeView: roleHome(),
    navigate: view => { State.view = view; render(); },
  });
}

// ──────────────────────────────────────────────────────────
// Voice — dictation with Claude Code desktop parity (2026-07-12)
//
// Two modes, mirroring the desktop app's voice spec:
//   HOLD (push-to-talk): press and keep holding — release finalizes.
//   TAP  (toggle):       quick tap starts, tap again finalizes.
// Both auto-submit when the transcript is >= 3 words (the CC rule;
// kill switch: localStorage eon_voice_send = '0'). While recording,
// the overlay shows a live level meter (WebAudio analyser), an
// elapsed clock, and a rolling interim transcript from the mini STT
// lane. Recording auto-stops after 15s of silence or 2min total —
// also the CC numbers. Cmd/Ctrl+M toggles tap mode from the keyboard.

const VOICE_TAP_MS = 260;          // press shorter than this = tap toggle
const VOICE_SILENCE_STOP_MS = 15000;
const VOICE_MAX_MS = 120000;
const VOICE_INTERIM_EVERY_MS = 3500;
const VOICE_AUTOSEND_MIN_WORDS = 3;
// Dictation sends on its own only into an empty box (21 Oct live assessment
// F2): a draft the person typed or attached is never sent without Send. The
// button's name says which of the two will happen. Only files staged for this
// message count, as in sendChat: a saved thread's own files (source
// 'conversation') already belong to it and leave dictation sending.
function _dictateWillSend(draft) {
  return !String(draft || '').trim()
    && !(State.attachments || []).some(a => a.source !== 'conversation')
    && localStorage.getItem('eon_voice_send') !== '0';
}
function _dictateLabel(draft) {
  return _dictateWillSend(draft) ? 'Dictate: hold to record, release to send (three words or more)'
    : 'Dictate: hold to record, release to add to your message';
}
function _syncDictateLabel(input) {
  $('#composer-mic')?.setAttribute('aria-label', _dictateLabel(input ? input.value : ''));
}
// The recording overlay's hint promises what the button's name does.
function _dictateHint(mode) {
  const send = _dictateWillSend($('#composer-input')?.value);
  if (mode === 'tap') return send ? 'Tap the mic again to send' : 'Tap the mic again to add to your message';
  return send ? 'Release to send — slide up to cancel' : 'Release to add to your message — slide up to cancel';
}

function bindMicHandlers(mic) {
  // Don't double-bind
  if (mic.dataset.bound) return;
  mic.dataset.bound = '1';

  // Pointer events handle both touch and mouse uniformly
  let pressedAt = 0;
  let startY = null;

  const onDown = (e) => {
    e.preventDefault();
    startY = e.touches ? e.touches[0].clientY : e.clientY;
    pressedAt = Date.now();
    State.recorder.pointerDown = true;
    if (State.recorder.instance) return; // tap mode: stop happens on up
    State.recorder.cancelled = false;
    startRecording('hold');
  };

  const onMove = (e) => {
    if (!State.recorder.instance) return;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    if (startY !== null && (startY - y) > 60) {
      // Slid up to cancel
      State.recorder.cancelled = true;
      const overlay = $('#rec-overlay');
      if (overlay) {
        overlay.querySelector('.rec-label').textContent = 'Release to cancel';
        overlay.classList.add('rec-cancelling');
      }
    }
  };

  const onUp = (e) => {
    e.preventDefault();
    const rec = State.recorder;
    rec.pointerDown = false;
    // getUserMedia can outlive a quick tap — if the recorder isn't
    // live yet, startRecording's landing check promotes to tap mode.
    if (!rec.instance) return;
    const heldMs = Date.now() - pressedAt;
    if (heldMs < VOICE_TAP_MS) {
      if (rec.mode === 'hold' && heldMs < VOICE_TAP_MS
          && Date.now() - rec.startedAt < VOICE_TAP_MS + 50) {
        // Quick tap on an idle mic just started this recording —
        // promote it to TAP mode and keep listening.
        rec.mode = 'tap';
        const hint = $('#rec-hint');
        if (hint) hint.textContent = _dictateHint('tap');
        return;
      }
    }
    // Hold release, or second tap in tap mode — finalize.
    stopRecording();
  };

  mic.addEventListener('pointerdown', onDown);
  mic.addEventListener('pointerup', onUp);
  mic.addEventListener('pointercancel', onUp);
  mic.addEventListener('pointerleave', (e) => {
    // Only end on pointer-leave for HOLD mode — tap mode keeps
    // recording while the pointer wanders (that's its whole point).
    if (State.recorder.instance && State.recorder.mode === 'hold') onUp(e);
  });
  mic.addEventListener('pointermove', onMove);

  // Keyboard: Cmd/Ctrl+M toggles tap-mode dictation (desktop parity —
  // CC binds Space in a terminal; a web composer needs a chord).
  if (!document.body.dataset.voiceKeys) {
    document.body.dataset.voiceKeys = '1';
    document.addEventListener('keydown', (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'm' || e.key === 'M')
          && !e.shiftKey && !e.altKey) {
        const m = $('#composer-mic');
        if (!m || m.disabled || !State.voice.transcribeAvailable || m.style.display === 'none') return;
        e.preventDefault();
        if (State.recorder.instance) stopRecording();
        else { State.recorder.cancelled = false; startRecording('tap'); }
      }
    });
  }
}

async function startRecording(mode) {
  // Pick the best supported mime — webm preferred, mp4 fallback for Safari
  const mimeCandidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  const mime = mimeCandidates.find(m => MediaRecorder.isTypeSupported(m));
  if (!mime) {
    showToast('Your browser does not support voice recording');
    return;
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });
    const R = State.recorder;
    R.stream = stream;
    R.chunks = [];
    R.mode = mode || 'hold';
    R.mime = mime;
    R.startedAt = Date.now();
    R.silenceMs = 0;
    R.interimInFlight = false;
    R.interimSeq += 1;

    // Timeslice so the interim lane always has a playable prefix —
    // chunk 0 carries the container header, so a Blob of chunks[0..n]
    // is a valid file at any point.
    const rec = new MediaRecorder(stream, { mimeType: mime });
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) R.chunks.push(e.data);
    };
    rec.onstop = () => onRecordingStopped(mime);
    rec.start(1000);
    R.instance = rec;

    // Landing check: getUserMedia (permission prompt, slow devices)
    // can resolve AFTER a quick tap already released. A hold recording
    // with no finger on the button would run headless until the
    // silence stop — promote it to tap mode instead, so the next tap
    // sends it. Mirrors the CC warmup behavior.
    if (R.mode === 'hold' && !R.pointerDown) R.mode = 'tap';

    const overlay = $('#rec-overlay');
    if (overlay) {
      overlay.style.display = 'flex';
      overlay.classList.remove('rec-cancelling');
      overlay.querySelector('.rec-label').textContent = 'Listening…';
      const t = $('#rec-transcript');
      if (t) t.textContent = '';
      const hint = $('#rec-hint');
      if (hint) hint.textContent = _dictateHint(R.mode);
    }

    // Level meter + silence auto-stop + clock, all off one 200ms tick.
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      R.audioCtx = new AC();
      const src = R.audioCtx.createMediaStreamSource(stream);
      R.analyser = R.audioCtx.createAnalyser();
      R.analyser.fftSize = 512;
      src.connect(R.analyser);
      const buf = new Uint8Array(R.analyser.fftSize);
      R.meterTimer = setInterval(() => {
        if (!R.instance) return;
        R.analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const d = (buf[i] - 128) / 128;
          sum += d * d;
        }
        const rms = Math.sqrt(sum / buf.length);
        const meter = $('#rec-meter');
        if (meter) {
          const bars = meter.children;
          const level = Math.min(1, rms * 9);
          for (let i = 0; i < bars.length; i++) {
            const gate = (i + 1) / bars.length;
            bars[i].style.transform =
              `scaleY(${level >= gate * 0.55 ? (0.35 + level) : 0.18})`;
          }
        }
        const clock = $('#rec-clock');
        const el = Date.now() - R.startedAt;
        if (clock) {
          const s = Math.floor(el / 1000);
          clock.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
        }
        // Silence + hard-cap auto-stop (CC numbers: 15s / 2min)
        R.silenceMs = rms < 0.012 ? R.silenceMs + 200 : 0;
        if (R.silenceMs >= VOICE_SILENCE_STOP_MS || el >= VOICE_MAX_MS) {
          stopRecording();
        }
      }, 200);
    } catch (e) { /* meter is progressive enhancement */ }

    // Interim transcript lane — rolling preview into the overlay.
    R.interimTimer = setInterval(() => runInterimTranscription(), VOICE_INTERIM_EVERY_MS);
  } catch (err) {
    console.error('Mic access failed:', err);
    // v3.0: surface the real error so we can debug iOS quirks
    let msg = 'Microphone error';
    if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
      msg = 'Microphone permission denied. Settings → Safari → Microphone';
    } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
      msg = 'No microphone detected on this device';
    } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
      msg = 'Microphone is in use by another app';
    } else if (err.name === 'OverconstrainedError') {
      msg = 'Mic does not support required settings';
    } else if (err.name === 'TypeError') {
      msg = 'Voice not supported in this browser';
    } else if (err.message) {
      msg = `Mic error: ${err.message}`;
    }
    showToast(msg);
  }
}

function stopRecording() {
  const rec = State.recorder.instance;
  if (!rec) return;
  if (rec.state !== 'inactive') rec.stop();
}

// Voice-parity pass: send the accumulated chunks so far to the mini
// STT lane and stream the result into the overlay, dimmed — the same
// live-preview feel as Claude Code's dictation. Failures are silent;
// only the FINAL pass owes the operator an answer.
async function runInterimTranscription() {
  const R = State.recorder;
  if (!R.instance || R.interimInFlight || R.cancelled) return;
  if (!R.chunks.length) return;
  const blob = new Blob(R.chunks, { type: R.mime });
  if (blob.size < 4000) return;
  const seq = R.interimSeq;
  R.interimInFlight = true;
  try {
    const ext = R.mime.includes('mp4') ? 'mp4' :
                R.mime.includes('ogg') ? 'ogg' : 'webm';
    const fd = new FormData();
    fd.append('audio', blob, `voice.${ext}`);
    fd.append('interim', '1');
    const res = await fetch(API + '/api/voice/transcribe', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${State.jwt}`,
        'X-Tenant-Slug': State.tenant.slug,
      },
      body: fd,
    });
    if (!res.ok) return;
    const data = await res.json();
    // A recording that ended (or restarted) while we were in flight
    // must not paint a stale preview.
    if (seq !== R.interimSeq || !R.instance) return;
    const t = $('#rec-transcript');
    if (t && data.text) t.textContent = data.text;
  } catch (e) { /* interim is best-effort by design */ }
  finally { R.interimInFlight = false; }
}

// ──────────────────────────────────────────────────────────
// Sound notifications (2026-07-12) — Claude Code desktop parity.
// Two synthesized chimes (no assets, WebAudio only): 'done' when a
// reply finishes streaming, 'attention' when EON needs an approval.
// Toggle lives in the menu util row; localStorage eon_sound ('0' =
// off, default ON). Volumes are deliberately low — a nudge, not an
// alarm.

let _chimeCtx = null;

function soundEnabled() {
  return localStorage.getItem('eon_sound') !== '0';
}

function playChime(kind) {
  if (!soundEnabled()) return;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!_chimeCtx) _chimeCtx = new AC();
    if (_chimeCtx.state === 'suspended') _chimeCtx.resume();
    const t = _chimeCtx.currentTime;
    // done: soft two-note rise; attention: double ping on one note.
    const notes = kind === 'attention'
      ? [[880, 0, 0.16], [880, 0.22, 0.20]]
      : [[659, 0, 0.12], [988, 0.11, 0.18]];
    notes.forEach(([freq, dt, dur]) => {
      const o = _chimeCtx.createOscillator();
      const g = _chimeCtx.createGain();
      o.type = 'sine';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0, t + dt);
      g.gain.linearRampToValueAtTime(0.11, t + dt + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dt + dur);
      o.connect(g).connect(_chimeCtx.destination);
      o.start(t + dt);
      o.stop(t + dt + dur + 0.05);
    });
  } catch (e) { /* sound is optional — never break the chat for it */ }
}

function teardownRecorderExtras() {
  const R = State.recorder;
  if (R.meterTimer) { clearInterval(R.meterTimer); R.meterTimer = null; }
  if (R.interimTimer) { clearInterval(R.interimTimer); R.interimTimer = null; }
  R.interimSeq += 1;             // invalidate in-flight interim paints
  if (R.audioCtx) {
    try { R.audioCtx.close(); } catch (e) { /* already closed */ }
    R.audioCtx = null;
    R.analyser = null;
  }
}

async function onRecordingStopped(mime) {
  teardownRecorderExtras();
  const overlay = $('#rec-overlay');
  if (overlay) {
    overlay.style.display = 'none';
    overlay.classList.remove('rec-cancelling');
    overlay.querySelector('.rec-label').textContent = 'Listening…';
  }

  // Stop mic tracks so the OS shows the recording indicator going away
  if (State.recorder.stream) {
    State.recorder.stream.getTracks().forEach(t => t.stop());
    State.recorder.stream = null;
  }

  const chunks = State.recorder.chunks;
  State.recorder.chunks = [];
  State.recorder.instance = null;
  State.recorder.mode = null;

  if (State.recorder.cancelled) {
    State.recorder.cancelled = false;
    return;
  }

  if (chunks.length === 0) {
    showToast('No audio detected from the microphone');
    return;
  }

  const blob = new Blob(chunks, { type: mime });
  if (blob.size < 1000) {
    showToast('Recording too short');
    return;
  }

  // Upload + transcribe. The box gets back the hint it had before it said
  // Transcribing… (renderChatView's "Ask EON…"), never a longer one: at 200%
  // text on a phone "Ask EON anything…" wrapped, the empty box grew 20 px and
  // the call's thread lost the reply's name (render cf8, Dictate at 190 x 410).
  const input = $('#composer-input');
  const hint = input && input.placeholder !== 'Transcribing…' ? input.placeholder : 'Ask EON…';
  if (input) {
    input.placeholder = 'Transcribing…';
    input.disabled = true;
  }

  try {
    const ext = mime.includes('mp4') ? 'mp4' :
                mime.includes('ogg') ? 'ogg' : 'webm';
    const fd = new FormData();
    fd.append('audio', blob, `voice.${ext}`);

    const res = await fetch(API + '/api/voice/transcribe', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${State.jwt}`,
        'X-Tenant-Slug': State.tenant.slug,
      },
      body: fd,
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `transcription failed (${res.status})`);
    }

    const data = await res.json();
    const text = (data.text || '').trim();

    if (input) {
      input.disabled = false;
      input.placeholder = hint;
      if (text) {
        const willSend = _dictateWillSend(input.value);   // before the words go in
        // Insert at the CURSOR (Claude Code behavior) — voice and
        // typing mix in any order instead of always appending at end.
        const start = input.selectionStart ?? input.value.length;
        const end = input.selectionEnd ?? input.value.length;
        const before = input.value.slice(0, start);
        const after = input.value.slice(end);
        const glueL = before && !/\s$/.test(before) ? ' ' : '';
        const glueR = after && !/^\s/.test(after) ? ' ' : '';
        input.value = before + glueL + text + glueR + after;
        const caret = (before + glueL + text).length;
        input.setSelectionRange(caret, caret);
        input.dispatchEvent(new Event('input'));
        input.focus();
        // Auto-submit when the box was empty and the words dictated are
        // >= 3 (the CC rule). Kill switch: localStorage eon_voice_send =
        // '0'. Shorter transcripts, and any draft that was already there,
        // stay in the box for review.
        const words = text.split(/\s+/).filter(Boolean);
        if (willSend && words.length >= VOICE_AUTOSEND_MIN_WORDS) {
          sendChat();
        }
      } else {
        showToast('No speech detected — try speaking closer to the mic');
      }
    }
  } catch (e) {
    console.error('Transcription error:', e);
    if (input) {
      input.disabled = false;
      input.placeholder = hint;
    }
    showToast(`Voice error: ${e.message}`);
  }
}

// ──────────────────────────────────────────────────────────
// Voice OUT — read EON's reply aloud via ElevenLabs

let _ttsAudio = null;      // currently-playing audio element
let _ttsAbort = null;      // AbortController for in-flight fetch

// ──────────────────────────────────────────────────────────
// PR CHAT-MARKDOWN-TABLES (2026-05-20)
//
// Daniel: "can you make eon display tables like you do instead of the
// way it does now which is nt cool". The chat bubble path just
// HTML-escaped text — so GFM tables (`| col | col |\n|---|---|\n`)
// surfaced as raw pipes-and-dashes.
//
// Tiny GFM-subset renderer: tables, inline bold/italic/code, and
// h2/h3 headers. NOT a full markdown library (we deliberately don't
// pull marked.js — too big for the chat surface). Code fences still
// go through parseClaudeCodeBody when Claude-mode is on.
//
// Contract: input is the raw EON text. Output is HTML that's safe to
// drop into innerHTML — every dynamic piece routes through esc()
// before assembly.

const _TABLE_RE = /(^|\n)((?:\|[^\n]*\|\s*\n)+)\|\s*[-:]+[-:|\s]*\s*\|\s*\n((?:\|[^\n]*\|\s*\n?)+)/g;

function _splitMdRow(line) {
  // GFM rows are leading-and-trailing-pipe; some operator paste-jobs
  // drop one or both. Strip the outer pipes then split.
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map(c => c.trim());
}

function _renderMdTable(headerBlock, bodyBlock) {
  // headerBlock is one or more pipe lines BEFORE the separator —
  // the GFM spec only allows one header row, so take the LAST line
  // (the line immediately above the separator). The earlier ones,
  // if any, are prose pipes that snuck in.
  const headerLines = headerBlock.trim().split('\n');
  const headerLine = headerLines[headerLines.length - 1];
  const headerCells = _splitMdRow(headerLine);

  const rows = bodyBlock.trim().split('\n')
    .map(_splitMdRow)
    .filter(r => r.some(c => c.length > 0));

  // PR-4 (2026-07-06): every chat table carries a CSV export button.
  // The download handler is delegated at document level (same pattern
  // as .msg-copy) and reads the rendered DOM, so it survives every
  // renderChatBody() innerHTML replace.
  let html = '<div class="md-table-wrap">' +
    '<button class="md-csv-btn" title="Download as CSV" ' +
    'aria-label="Download table as CSV">CSV</button>' +
    '<table class="md-table"><thead><tr>';
  for (const h of headerCells) {
    html += `<th>${_renderMdInline(h)}</th>`;
  }
  html += '</tr></thead><tbody>';
  for (const r of rows) {
    html += '<tr>';
    for (let i = 0; i < headerCells.length; i++) {
      html += `<td>${_renderMdInline(r[i] || '')}</td>`;
    }
    html += '</tr>';
  }
  html += '</tbody></table></div>';

  // Prepend any prose pipes that weren't the header row.
  if (headerLines.length > 1) {
    const stray = headerLines.slice(0, -1).join('\n');
    if (stray.trim()) {
      html = `<p>${_renderMdInline(stray)}</p>` + html;
    }
  }
  return html;
}

// Allow-list for inline markdown images. https + Shopify CDN only — we
// don't render arbitrary URLs the agent might hallucinate, and we don't
// render http (mixed-content issues). Keep this tight; the operator
// shouldn't see broken-image icons because we rejected a real Shopify
// URL, but we also shouldn't render someone-pasted external image refs.
const _MD_IMG_ALLOWED_HOSTS = [
  'cdn.shopify.com',
  'shopify.com',
];
function _isAllowedImgUrl(url) {
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:') return false;
    return _MD_IMG_ALLOWED_HOSTS.some(h =>
      u.hostname === h || u.hostname.endsWith('.' + h));
  } catch (_e) { return false; }
}
// Bench reachability (module scope hides top-level fns) — the chat
// gallery bench renders fixture transcripts through the REAL renderer.
// Hoisting makes the reference valid ahead of the declaration below.
window.renderMarkdownToHtml = renderMarkdownToHtml;

// Turn markdown links [label](url) and bare http(s) URLs into clickable,
// colored <a> tags. INPUT MUST ALREADY BE HTML-ESCAPED (so the URL is safe to
// drop straight into href + text — '&' is '&amp;', quotes are entities). Bare
// URLs are only matched when preceded by start/space/'(' so we never re-link a
// URL already inside an href="…" the markdown pass produced.
function _linkifyHtml(s) {
  // 1) markdown links — http(s) or site-relative (/money). '/(?!/)' blocks
  //    protocol-relative '//evil.com' off-site targets (phishing vector in
  //    agent-influenced text). javascript:/data: can't match (no scheme branch).
  s = s.replace(/\[([^\]\n]+)\]\(((?:https?:\/\/|\/(?!\/))[^)\s]+)\)/g,
    (full, label, url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer" class="md-link">${label}</a>`);
  // 2) bare URLs — preceded by start/space/'(' (a captured separator, re-emitted)
  //    so a URL already inside an href="…"/src="…" (preceded by ") is skipped.
  //    NOTE: deliberately NOT lookbehind — older Safari throws a *parse* error on
  //    (?<=…), which would brick the whole app.js. Trailing sentence punctuation
  //    is kept out of the link.
  s = s.replace(/(^|[\s(])(https?:\/\/[^\s<>"')\]]+)/g, (full, pre, url) => {
    // Strip trailing sentence punctuation, but NOT ';' — the input is escaped,
    // so a URL ending in '&' arrives as '…&amp;' and a query string as
    // '…?a=1&amp;b=2'; stripping ';' would sever the entity ('&amp;'→'&amp')
    // and break the href. (We can't drop '&' from the class above — that would
    // truncate every query-string URL at its first parameter.)
    const m = url.match(/^(.*?)([.,:!?]+)$/);
    const clean = m ? m[1] : url, tail = m ? m[2] : '';
    return `${pre}<a href="${clean}" target="_blank" rel="noopener noreferrer" class="md-link">${clean}</a>${tail}`;
  });
  return s;
}

function _renderMdInline(text) {
  // Inline pass: handles **bold**, *italic*, `code`, and markdown
  // images. Build by tokenising so we don't double-escape. We escape
  // first, then pattern-replace on the escaped text. This is safe
  // because our markdown markers (`*`, `\``) survive escape.
  let s = esc(text);
  // Markdown images FIRST so their alt/url aren't mangled by bold or
  // italic (e.g. an underscore in the URL). Pattern matches the
  // escaped output of `![alt](url)`, since we already passed through
  // esc() — quotes get encoded to &quot;/&#39; before this point.
  s = s.replace(/!\[([^\]\n]*)\]\(([^)\s]+)\)/g, (full, alt, url) => {
    // The url + alt have been HTML-escaped. Unescape just enough to
    // validate the host, then re-escape on output.
    const rawUrl = url
      .replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    if (!_isAllowedImgUrl(rawUrl)) return full;  // leave literal text
    return `<img class="md-img" src="${esc(rawUrl)}" alt="${alt}" `
         + `loading="lazy" decoding="async" referrerpolicy="no-referrer" `
         + `onerror="this.classList.add('md-img--dead')"/>`;
  });
  // Inline code FIRST (after image) so its content isn't mangled by
  // bold/italic.
  s = s.replace(/`([^`]+)`/g, (_, c) => `<code>${c}</code>`);
  // Bold then italic. Bold uses ** ** (non-greedy, must not span
  // newlines).
  s = s.replace(/\*\*([^\n*]+?)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[\s(])\*([^\n*]+?)\*(?=[\s).,;:!?]|$)/g,
                  '$1<em>$2</em>');
  s = _linkifyHtml(s);   // [label](url) + bare URLs → clickable, colored
  return s;
}

function renderMarkdownToHtml(text) {
  // WS-C (2026-07-26): code fences extracted FIRST (before tables and
  // the paragraph split). They previously fell through to the inline-
  // code regex, which matched across the fence markers and emitted
  // orphan backticks around a half-styled block. An unclosed fence
  // (mid-stream) swallows to the end of the text — renders as a code
  // block that simply grows until the closing fence arrives.
  const fences = [];
  let scratch = String(text || '');
  scratch = scratch.replace(/```([^\n`]*)\n?([\s\S]*?)(?:```|$)/g, (mm, info, code) => {
    const ph = `\x00FENCE${fences.length}\x00`;
    fences.push(
      `<div class="md-codeblock"><pre><code>${esc(code.replace(/\n$/, ''))}</code></pre></div>`
    );
    return '\n\n' + ph + '\n\n';
  });

  // Process tables next (multi-line, anchored).
  const tables = [];
  _TABLE_RE.lastIndex = 0;
  scratch = scratch.replace(_TABLE_RE, (match, leadNl, header, body) => {
    const placeholder = `\x00TABLE${tables.length}\x00`;
    tables.push(_renderMdTable(header, body));
    // Wrap the placeholder in blank lines so the subsequent
    // paragraph-split keeps it isolated as its own "paragraph".
    // Without the trailing \n\n, the placeholder mashes into the
    // following prose paragraph and the placeholder-detection
    // regex misses it → operator sees the literal `TABLE0`
    // sentinel string in the rendered output.
    return leadNl + '\n' + placeholder + '\n\n';
  });

  // Now paragraph-split the non-table prose. Each paragraph runs
  // through _renderMdInline (handles bold / italic / code).
  const paras = scratch.split(/\n\n+/).filter(p => p.trim().length > 0);
  const out = paras.map(p => {
    // If the paragraph IS a placeholder, render the cached table/fence.
    const tableMatch = /^\x00TABLE(\d+)\x00$/.exec(p.trim());
    if (tableMatch) {
      return tables[parseInt(tableMatch[1], 10)];
    }
    const fenceMatch = /^\x00FENCE(\d+)\x00$/.exec(p.trim());
    if (fenceMatch) {
      return fences[parseInt(fenceMatch[1], 10)];
    }
    // h2/h3 headers — lightweight subset.
    const h2 = /^##\s+(.+)$/.exec(p);
    if (h2) return `<h3 class="md-h2">${_renderMdInline(h2[1])}</h3>`;
    const h3 = /^###\s+(.+)$/.exec(p);
    if (h3) return `<h4 class="md-h3">${_renderMdInline(h3[1])}</h4>`;
    // WS-C: bullet / numbered lists. `- item` lines used to fall into
    // the paragraph branch and render as literal dashes glued by <br>.
    // Mixed blocks ("Intro:\n- a\n- b") group consecutive list lines
    // into a real <ul>/<ol> between the prose lines.
    if (/^\s*(?:[-*•]|\d+[.)])\s+/m.test(p)) {
      const chunks = [];
      let list = null;   // { tag, items }
      const flush = () => {
        if (list) {
          chunks.push(`<${list.tag} class="md-list">${list.items.join('')}</${list.tag}>`);
          list = null;
        }
      };
      for (const rawLine of p.split('\n')) {
        const line = rawLine.trim();
        if (!line) continue;
        const bullet = /^[-*•]\s+(.+)$/.exec(line);
        const ordered = /^\d+[.)]\s+(.+)$/.exec(line);
        const tag = bullet ? 'ul' : ordered ? 'ol' : null;
        if (tag) {
          if (!list || list.tag !== tag) { flush(); list = { tag, items: [] }; }
          list.items.push(`<li>${_renderMdInline((bullet || ordered)[1])}</li>`);
        } else {
          flush();
          chunks.push(`<p>${_renderMdInline(line)}</p>`);
        }
      }
      flush();
      return chunks.join('');
    }
    // Paragraph: convert single-newlines to <br> (some operator
    // copy-pastes carry hard breaks within a logical paragraph).
    const inline = _renderMdInline(p).replace(/\n/g, '<br>');
    return `<p>${inline}</p>`;
  });
  return out.join('');
}

// ──────────────────────────────────────────────────────────
// PR CHAT-UI-PHASE3 — Claude Code code-block rendering
//
// Parses markdown triple-backtick code fences inside completed EON
// messages and renders them as <pre><code class="language-…">. An
// optional file-path header chip is rendered when the agent emits
// a fence with `file=` info, e.g.:
//
//     ```python file=app/foo.py:42
//     def bar(): ...
//     ```
//
// Prism.js is lazy-loaded from CDN on first use (and only when
// Claude Code mode is active), with the autoloader plugin so we
// only download language grammars we actually render.

const _FENCE_RE = /```(\w+)?(?:[ \t]+([^\n]+))?\n([\s\S]*?)```/g;

function parseClaudeCodeBody(text) {
  const out = [];
  let last = 0;
  let m;
  _FENCE_RE.lastIndex = 0;
  while ((m = _FENCE_RE.exec(text)) !== null) {
    // 1. Emit prose preceding the fence as paragraphs.
    if (m.index > last) {
      const prose = text.slice(last, m.index);
      const paras = prose.split(/\n\n+/).filter(p => p.trim().length > 0);
      for (const p of paras) {
        out.push(`<p class="cc-prose">${esc(p)}</p>`);
      }
    }
    // 2. Emit the code block.
    const lang = (m[1] || '').toLowerCase();
    const meta = (m[2] || '').trim();      // e.g. "file=app/foo.py:42"
    const code = m[3] || '';
    let header = '';
    const fileMatch = /file=(\S+)/.exec(meta);
    if (fileMatch) {
      header = `<div class="cc-codeblock-header">${esc(fileMatch[1])}</div>`;
    }
    const cls = lang ? `language-${esc(lang)}` : '';
    out.push(
      `<div class="cc-codeblock">${header}` +
      `<pre><code class="${cls}">${esc(code)}</code></pre>` +
      `</div>`
    );
    last = m.index + m[0].length;
  }
  // 3. Trailing prose.
  if (last < text.length) {
    const prose = text.slice(last);
    const paras = prose.split(/\n\n+/).filter(p => p.trim().length > 0);
    for (const p of paras) {
      out.push(`<p class="cc-prose">${esc(p)}</p>`);
    }
  }
  return out.join('');
}

let _prismLoading = false;
let _prismReady   = false;
function loadPrismIfNeeded() {
  if (_prismReady || _prismLoading) {
    // Already loaded — just re-highlight the chat region.
    if (_prismReady && window.Prism) {
      requestAnimationFrame(() => {
        const root = document.getElementById('chat-body');
        if (root) window.Prism.highlightAllUnder(root);
      });
    }
    return;
  }
  _prismLoading = true;
  // Prism CSS (theme: tomorrow — dark-friendly, neutral).
  const cssLink = document.createElement('link');
  cssLink.rel  = 'stylesheet';
  cssLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism-tomorrow.min.css';
  document.head.appendChild(cssLink);
  // Prism core.
  const coreScript = document.createElement('script');
  coreScript.src   = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/prism.min.js';
  coreScript.onload = () => {
    // Autoloader plugin fetches language grammars on demand.
    const autoScript = document.createElement('script');
    autoScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/plugins/autoloader/prism-autoloader.min.js';
    autoScript.onload = () => {
      if (window.Prism && window.Prism.plugins && window.Prism.plugins.autoloader) {
        window.Prism.plugins.autoloader.languages_path =
          'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/components/';
      }
      _prismReady = true;
      _prismLoading = false;
      const root = document.getElementById('chat-body');
      if (root && window.Prism) window.Prism.highlightAllUnder(root);
    };
    document.head.appendChild(autoScript);
  };
  coreScript.onerror = () => { _prismLoading = false; };
  document.head.appendChild(coreScript);
}

// PR voice-mp3-streaming: MediaSource Extensions feature-detect.
// Cut once at module load — checking per-call would re-allocate
// the throwaway MediaSource probe on every reply.
const _MSE_OK = (() => {
  try {
    return typeof window !== 'undefined'
      && 'MediaSource' in window
      && typeof MediaSource.isTypeSupported === 'function'
      && MediaSource.isTypeSupported('audio/mpeg');
  } catch { return false; }
})();

async function speakText(text) {
  // Cancel any in-flight fetch AND playing audio. The old code only
  // stopped the audio element — a still-buffering fetch from a
  // previous reply could resolve and start playing AFTER a newer one.
  if (_ttsAbort) {
    try { _ttsAbort.abort(); } catch {}
    _ttsAbort = null;
  }
  if (_ttsAudio) {
    try { if ((_ttsAudio.src || '').startsWith('blob:')) URL.revokeObjectURL(_ttsAudio.src); } catch {}
    try { _ttsAudio.pause(); } catch {}
    _ttsAudio = null;
  }

  // Clip raised 1500 → 4500 (ElevenLabs caps at 5000). The old 1500
  // was silently truncating ~30% of EON's longer replies mid-sentence
  // — that was the operator's "EON cuts off" complaint.
  const clean = text
    .replace(/[*_`#=]/g, '')
    .replace(/\n{2,}/g, '. ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 4500);
  if (!clean) return;

  const abort = new AbortController();
  _ttsAbort = abort;

  try {
    const res = await fetch(API + '/api/voice/tts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${State.jwt}`,
        'X-Tenant-Slug': State.tenant.slug,
      },
      body: JSON.stringify({ text: clean }),
      signal: abort.signal,
    });

    if (!res.ok) {
      console.error('TTS request failed:', res.status);
      return;
    }

    if (_MSE_OK && res.body && typeof res.body.getReader === 'function') {
      // PR voice-mp3-streaming: pipe the MP3 bytes straight into a
      // MediaSource as they arrive. Playback starts within ~300ms
      // of the first chunk — was ~2s waiting for the full blob.
      await _playStreamingMP3(res, abort);
    } else {
      // Fallback (older Safari, Firefox quirks, missing ReadableStream):
      // buffer to blob, play the old way.
      await _playBlobMP3(res, abort);
    }
  } catch (e) {
    // AbortError is the normal overlap case — only log real failures.
    if (e?.name !== 'AbortError') {
      console.error('TTS error:', e);
    }
  } finally {
    if (_ttsAbort === abort) _ttsAbort = null;
  }
}

async function _playStreamingMP3(res, abort) {
  const ms = new MediaSource();
  const audio = new Audio();
  audio.src = URL.createObjectURL(ms);
  audio.onended = () => {
    URL.revokeObjectURL(audio.src);
    if (_ttsAudio === audio) _ttsAudio = null;
  };
  audio.onerror = () => {
    URL.revokeObjectURL(audio.src);
    if (_ttsAudio === audio) _ttsAudio = null;
  };
  _ttsAudio = audio;

  // sourceopen fires once the audio element binds to the MediaSource.
  await new Promise((resolve, reject) => {
    ms.addEventListener('sourceopen', resolve, { once: true });
    ms.addEventListener('error', reject, { once: true });
  });
  if (abort.signal.aborted) {
    if (ms.readyState === 'open') {
      try { ms.endOfStream(); } catch {}
    }
    return;
  }

  const buf = ms.addSourceBuffer('audio/mpeg');
  const reader = res.body.getReader();

  // Wait for buf.updating to clear (appendBuffer is async — only one
  // append can be in flight per SourceBuffer).
  const _whenIdle = () => new Promise((resolve) => {
    if (!buf.updating) return resolve();
    buf.addEventListener('updateend', resolve, { once: true });
  });

  // Start playback as soon as the first chunk lands. .play() is a
  // promise that resolves once audio actually begins, OR rejects on
  // autoplay-blocked browsers (iOS without prior user gesture).
  // Swallow that rejection — we still keep buffering so the user can
  // hit play manually if desired.
  let started = false;
  const _tryStart = () => {
    if (started || abort.signal.aborted) return;
    started = true;
    audio.play().catch(() => { /* autoplay blocked; harmless */ });
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (abort.signal.aborted) {
        try { reader.cancel(); } catch {}
        break;
      }
      if (done) {
        await _whenIdle();
        if (ms.readyState === 'open') {
          try { ms.endOfStream(); } catch {}
        }
        return;
      }
      await _whenIdle();
      if (abort.signal.aborted) break;
      try {
        buf.appendBuffer(value);
      } catch (e) {
        // QuotaExceeded on tiny mobile buffers — fall back to blob.
        if (e?.name === 'QuotaExceededError') {
          try { reader.cancel(); } catch {}
          try { ms.endOfStream(); } catch {}
          return; // playback continues on already-buffered audio
        }
        throw e;
      }
      _tryStart();
    }
  } catch (e) {
    if (e?.name !== 'AbortError') throw e;
  }
}

async function _playBlobMP3(res, abort) {
  // Legacy path — buffer entire response, then play. Same behaviour
  // as before MediaSource support landed.
  const blob = await res.blob();
  if (abort.signal.aborted) return;

  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  audio.onended = () => {
    URL.revokeObjectURL(url);
    if (_ttsAudio === audio) _ttsAudio = null;
  };
  audio.onerror = () => {
    URL.revokeObjectURL(url);
    if (_ttsAudio === audio) _ttsAudio = null;
  };
  _ttsAudio = audio;
  try { await audio.play(); } catch { /* autoplay blocked */ }
}

// ──────────────────────────────────────────────────────────

async function sendChat(forcedText) {
  // forcedText: starter chips (and other programmatic senders) pass text
  // directly — #composer-input may not be mounted.
  const input = $('#composer-input');
  const text = (typeof forcedText === 'string' ? forcedText : (input?.value || '')).trim();
  if (!text) return;

  // WS-C: real double-send guard. The old guard was only the button's
  // disabled flag, which the next keystroke re-enabled — and the Enter
  // handler never consulted it. Two queued turns collided in the
  // stream handlers (two pending/streaming bubbles fighting).
  if (_sendInFlight) {
    showToast('EON is answering — one moment');
    return;
  }

  // Live voice open in this thread carries the question over its call, or
  // the question stays in the draft. It is never also sent over HTTP.
  // Only files staged for this message are refused: a saved thread's own
  // files (source 'conversation') already belong to it and go nowhere.
  if (chatConversation.voiceRoute() === 'call') {
    const staged = (State.attachments || []).filter(a => a.source !== 'conversation');
    if (typeof forcedText !== 'string' && staged.length) {
      showToast('Files cannot go over live voice. End the call to send them; your draft and files are kept.');
      return;
    }
    if (!chatConversation.voiceSendText(text)) {
      // A quick question was never in the draft, so it is not said to be.
      showToast(typeof forcedText === 'string'
        ? 'Live voice did not take that question. Ask it out loud, or tap it again once the call is listening.'
        : 'Live voice did not take your question. It is still in your draft.');
      return;
    }
    clearDraft();
    chatConversation.submitted();
    return;
  }

  // 2026-06-01: block send when there are failed-state attachments.
  // Daniel: "when i attach an xlsx it spin and turn red when i submit
  // eon says it cant see it." Server-side excludes status='failed'
  // from the agent context, so the message goes through with no file
  // — confusing UX. Force the operator to resolve the failed chip
  // (retry or remove) before sending.
  const failedChips = (State.attachments || []).filter(a => a.status === 'failed');
  if (failedChips.length > 0) {
    const names = failedChips.map(a => `• ${a.filename}`).join('\n');
    const errors = failedChips
      .map(a => a.error ? `  ↳ ${a.error}` : '')
      .filter(Boolean);
    alert(
      `${failedChips.length} attachment${failedChips.length === 1 ? '' : 's'} failed to upload — EON won't see ${failedChips.length === 1 ? 'it' : 'them'} until you fix or remove ${failedChips.length === 1 ? 'it' : 'them'}.\n\n` +
      `Failed:\n${names}\n` +
      (errors.length ? `\nReason${errors.length === 1 ? '' : 's'}:\n${errors.join('\n')}\n` : '') +
      `\nTap × on the red chip${failedChips.length === 1 ? '' : 's'} to remove, then resend.`
    );
    return;  // Don't send.
  }

  // Also warn (not block) if there are still-uploading chips — those
  // would also be missed by the agent. Operator can choose to wait.
  const uploadingChips = (State.attachments || []).filter(a => a.status === 'uploading');
  if (uploadingChips.length > 0) {
    const ok = confirm(
      `${uploadingChips.length} file${uploadingChips.length === 1 ? '' : 's'} still uploading. ` +
      `Send anyway? EON won't see ${uploadingChips.length === 1 ? 'it' : 'them'} until upload completes.`
    );
    if (!ok) return;
  }

  // A fresh Chat gets its own conversation before its first message, so the
  // reply and a later live voice call share one thread. Nothing is cleared or
  // sent until it is ready; a second tap meanwhile sends nothing.
  let prepared = chatConversation.prepareForSend?.() ?? 'ready';
  if (typeof prepared !== 'string') prepared = await prepared;
  if (prepared === 'busy') { showToast('Preparing your conversation — one moment. Your draft is kept.'); return; }
  if (prepared !== 'ready') return;   // refused: said in the composer, draft and files kept
  if (input && typeof forcedText !== 'string' && input.value.trim() !== text) {
    showToast('Your conversation is ready. Your draft changed meanwhile, so nothing was sent.');
    return;
  }

  // The draft is spent only once a transport has taken it.
  function clearDraft() {
    if (!input || typeof forcedText === 'string') return;
    input.value = '';
    input.style.height = 'auto';
    input.style.overflowY = 'hidden';
    _composerSizeCache.delete(input);   // sent draft's measurements are spent
    _syncDictateLabel(input);   // the box is empty again: Dictate says release to send (F2)
    // PR-7: collapse the two-row composer back to one line on send.
    input.closest('.composer')?.classList.remove('composer--tall');
    // WS-C: the sent draft is spent; refocus so the next thought types
    // straight away (tapping the send button used to move focus there
    // and collapse the phone keyboard after every message).
    _chatDraftClear();
    input.focus();
  }
  clearDraft();
  const sendBtn = $('#composer-send');
  if (sendBtn) sendBtn.disabled = true;
  // WS-C: echo attached images/files into the transcript — sent images
  // used to vanish entirely once the chips cleared.
  const sentAtts = (State.attachments || [])
    .filter(a => a.status !== 'failed')
    .map(a => ({ filename: a.filename, fileType: a.fileType, thumbUrl: a.thumbUrl || null }));
  chatConversation.submitted();
  State.chat.push({ role: 'user', text, atts: sentAtts.length ? sentAtts : undefined });
  State.chat.push({ role: 'pending' });
  renderChatBody();
  // Sending your own message ALWAYS snaps to the bottom — the scroll-pin
  // guard (correctly) suppresses auto-scroll when reading history, but a
  // self-send is an explicit "I'm at the conversation tip now".
  const cb = $('#chat-body');
  if (cb) requestAnimationFrame(() => { cb.scrollTop = cb.scrollHeight; });

  // v3.0p14: pass active conversation_id (if any) so backend uses that thread
  // instead of auto-resolving. Null = let backend pick (works for first-time
  // chats and the 24h-gap auto-thread case).
  const convPayload = State.activeConversationId
    ? { conversation_id: State.activeConversationId }
    : {};
  // PR-7: per-thread model toggle rides every send (both transports).
  const modelPayload = { model_pref: _isDeepConv() ? 'deep' : 'base' };

  if (State.ws && State.ws.readyState === WebSocket.OPEN) {
    // v3.0p16 9e: start the stream watchdog (60s). Cancelled by
    // handleWSMessage on 'done' / 'error'; fires otherwise to surface
    // the silent failure with the chat rid.
    // WS-C: stamp this turn's epoch — a conversation switch mid-stream
    // bumps the epoch and the handlers drop the stale events instead
    // of injecting the reply into the newly-opened thread.
    _sendInFlight = true;
    _sendEpoch = _turnEpoch;
    _startStreamWatchdog();
    State.ws.send(JSON.stringify({ type: 'chat', message: text, ...convPayload, ...modelPayload }));
  } else {
    _sendInFlight = true;   // WS-C: HTTP fallback holds the guard too
    try {
      const data = await api('/api/chat', {
        method: 'POST',
        body: JSON.stringify({ message: text, ...convPayload, ...modelPayload }),
      });
      State.chat = State.chat.filter(m => m.role !== 'pending');
      State.chat.push({ role: 'eon', text: data.reply, source_refs: sourceReferences(data.source_refs) });
      // Read the reply aloud when the person has that on. (The old call
      // mode's _callOnReplyDone went with #997 and no longer exists here;
      // calling it threw, and this catch then added a false "Couldn't reach
      // EON" under every answer that came this way.)
      if (State.voice.ttsEnabled && State.voice.ttsAvailable && data.reply) {
        speakText(data.reply);
      }
    } catch (e) {
      State.chat = State.chat.filter(m => m.role !== 'pending');
      const detail = e.body?.detail || e.body?.message || e.message || 'unknown';
      const status = e.status ? ` (${e.status})` : '';
      State.chat.push({ role: 'eon', failed: true, text: `Couldn't reach EON${status}: ${detail}\n\nCheck Railway logs for the full error.` });
    }
    _sendInFlight = false;
    renderChatBody();
  }
  
  // v3.0p14: clear staged attachments after send. Backend has already
  // claimed them (set conversation_id) when the user message was processed,
  // so the next message starts fresh.
  // We don't DELETE the upload rows — the agent still references them
  // via conversation_id in subsequent turns. The chip just goes away.
  if (State.attachments.length > 0) {
    State.attachments = [];
    renderAttachmentsStrip();
  }
}


// ──────────────────────────────────────────────────────────
// File upload (v3.0p14)
// 
// Flow:
//   1. User clicks paperclip → hidden file input opens
//   2. On file picked: push optimistic chip (status='uploading'), POST /api/uploads
//   3. On success: update chip to status='ready' with char count
//   4. On failure: update chip to status='failed' with error
//   5. User can remove a chip via × button (calls DELETE /api/uploads/{id} if ready)
//   6. When user sends a message, files are already attached (conversation_id=null);
//      backend claims them on the agent's next turn

const ATTACH_MAX_BYTES = 25 * 1024 * 1024;  // 25 MB — matches backend
// v3.0p18 9z: images added. Backend already accepts these (see
// IMAGE_EXTENSIONS in app/core/image_attachments.py — anthropic
// vision supports jpeg/png/webp/gif natively, HEIC/HEIF are converted
// to JPEG at upload time). The list HAD to be kept in sync manually
// when 9t shipped image support and was missed — that's why the iOS
// share-to-EON of a .jpeg was hitting "File type not supported"
// despite the backend being ready to accept it.
const ATTACH_ALLOWED_EXT = [
  'pdf', 'docx', 'csv', 'xlsx', 'txt', 'md',
  'jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif',
];

function fileIcon(fileType) {
  // Quick mnemonic emoji — no heavy SVG needed
  const map = {
    pdf: '📄', docx: '📝', csv: '📊',
    xlsx: '📊', txt: '📃', md: '📃',
    // v3.0p18 9t: image extensions get a camera glyph as a fallback when
    // we don't have a thumbnail (e.g. before upload completes).
    jpg: '🖼️', jpeg: '🖼️', png: '🖼️', webp: '🖼️',
    heic: '🖼️', heif: '🖼️',
  };
  return map[fileType] || '📎';
}

// v3.0p18 9t: true for any image extension we accept.
function isImageFileType(t) {
  return ['jpg','jpeg','png','webp','heic','heif','gif'].includes((t||'').toLowerCase());
}

function formatBytes(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function renderAttachments() {
  if (!State.attachments || State.attachments.length === 0) return '';
  return State.attachments.map(a => {
    // 2026-06-01: explicit 'ready' state so the chip visibly turns
    // green-bordered + " · ready ✓" once the upload completes.
    // Previously only 'uploading' and 'failed' had visible styling;
    // the success path was indistinguishable from a static chip.
    const stateClass = a.status === 'uploading' ? 'uploading'
                     : a.status === 'failed' ? 'failed'
                     : (a.status === 'ready' || a.id) ? 'ready'
                     : '';
    // v3.0p18 9t: image chips render a thumbnail in place of the emoji.
    // `a.thumbUrl` is populated locally from URL.createObjectURL when the
    // operator picks the file — survives the upload, no network round-trip.
    const isImg = isImageFileType(a.fileType) || a.status === 'image_ready';
    const ico = (isImg && a.thumbUrl)
      ? `<span class="ico"><img src="${esc(a.thumbUrl)}" alt="" style="width: 28px; height: 28px; object-fit: cover; border-radius: 4px; display: block;"/></span>`
      : `<span class="ico">${fileIcon(a.fileType)}</span>`;
    // Vet 2026-08-04 D1: say what the × will do. Fresh uploads are
    // cancelled (deleted); everything else only comes off this chat.
    const rmHint = a.source === 'upload'
      ? 'Remove (deletes this upload)'
      : 'Remove from this chat (file stays in Files)';
    return `
      <div class="attach-chip ${stateClass}" data-attach-id="${esc(a.id || a.tempId)}">
        ${ico}
        <span class="name" title="${esc(a.filename)}">${esc(a.filename)}</span>
        <span class="size">${esc(formatBytes(a.sizeBytes || 0))}</span>
        <button class="rm" type="button" aria-label="${esc(rmHint)}" title="${esc(rmHint)}" data-rm-id="${esc(a.id || a.tempId)}">×</button>
      </div>
    `;
  }).join('');
}

function renderAttachmentsStrip() {
  // Every change to the staged files comes through here: with a file
  // staged, Dictate adds to the message and never sends it (F2).
  _syncDictateLabel($('#composer-input'));
  const slot = $('#attachments-strip');
  if (!slot) return;
  const html = renderAttachments();
  slot.innerHTML = html;
  // 2026-06-01: renderChatView creates the strip with display:none
  // when empty. innerHTML alone doesn't restore visibility — chips
  // get inserted but stay invisible, AND State.attachments.length
  // counts against the 3-file cap. Daniel saw "0 slot(s) available"
  // forever after a session reloaded pending uploads from server.
  // Toggle display explicitly based on content.
  slot.style.display = html ? 'flex' : 'none';
  // If there are 3+ attachments visible, surface a "Clear all" link
  // so operator can recover from the stale-state without inspecting
  // each chip individually.
  if (State.attachments.length >= 3) {
    const existing = slot.querySelector('.attach-clear-all');
    if (!existing) {
      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.className = 'attach-clear-all';
      clearBtn.textContent = `Clear all ${State.attachments.length}`;
      clearBtn.style.cssText = 'background:transparent;border:1px solid var(--line);border-radius:14px;padding:4px 10px;color:var(--ink-soft);font-size:11px;cursor:pointer;align-self:center;';
      clearBtn.onclick = async () => {
        // Vet 2026-08-04 D1: the copy says exactly what happens —
        // library/restored files are only detached (they stay in
        // Files); fresh composer uploads are deleted.
        const n = State.attachments.length;
        const fresh = State.attachments.filter(a => a.source === 'upload').length;
        const kept = n - fresh;
        let msg;
        if (fresh === 0) {
          msg = `Take all ${n} files off this chat? They stay in your Files library.`;
        } else if (kept === 0) {
          msg = `Remove all ${n} fresh uploads? They will be deleted.`;
        } else {
          msg = `Remove all ${n} files? ${kept} stay in your Files library; `
              + `${fresh} fresh upload${fresh === 1 ? '' : 's'} will be deleted.`;
        }
        if (!confirm(msg)) return;
        const ids = State.attachments.map(a => a.id || a.tempId).filter(Boolean);
        for (const id of ids) {
          try { await removeAttachment(id); } catch (_) {}
        }
      };
      slot.appendChild(clearBtn);
    }
  }
  bindAttachmentRemovals();
  chatConversation.update();
}

function bindAttachmentRemovals() {
  document.querySelectorAll('[data-rm-id]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const id = btn.dataset.rmId;
      await removeAttachment(id);
    });
  });
}

// The upload door's own fetches (a FormData body, so not api()) name the
// workspace this page is in, as api() does. Without X-Tenant-Slug the server
// files the upload under the person's first workspace, so a file attached in
// another of theirs would ride that first workspace's next chat turn.
function uploadHeaders() {
  const headers = {Authorization: `Bearer ${State.jwt}`};
  if (State.tenant) headers['X-Tenant-Slug'] = State.tenant.slug;
  return headers;
}

async function uploadFile(file) {
  // 1. Client-side validation FIRST — saves a wasted upload
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (!ATTACH_ALLOWED_EXT.includes(ext)) {
    showToast(`.${ext} not supported — use ${ATTACH_ALLOWED_EXT.join(', ')}`);
    return;
  }
  if (file.size > ATTACH_MAX_BYTES) {
    showToast(`Too large (${formatBytes(file.size)}) — max 25 MB`);
    return;
  }
  if (file.size === 0) {
    showToast('File is empty');
    return;
  }
  
  // 2. Push optimistic chip with temp ID so user sees something happening.
  // v3.0p18 9t: for image picks, create a local object URL so the chip
  // shows a thumbnail immediately (no waiting for the upload to finish).
  // The URL is revoked on chip removal or after a 5-min TTL.
  const tempId = 'tmp-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
  let thumbUrl = null;
  if (isImageFileType(ext)) {
    try { thumbUrl = URL.createObjectURL(file); } catch (_) {}
  }
  const optimistic = {
    tempId,
    filename: file.name,
    fileType: ext,
    sizeBytes: file.size,
    status: 'uploading',
    thumbUrl,
    // Provenance (vet 2026-08-04 D1): × on a chip means different
    // things. 'upload' = fresh composer pick, × cancels the upload
    // (DELETE). Anything else ('library'/'restored'/'conversation')
    // detaches only — the file stays in the Files library.
    source: 'upload',
  };
  State.attachments.push(optimistic);
  renderAttachmentsStrip();
  
  // 3. Upload
  const fd = new FormData();
  fd.append('file', file);
  
  try {
    const res = await fetch('/api/uploads', {
      method: 'POST',
      headers: uploadHeaders(),
      body: fd,
    });
    const data = await res.json();
    
    // Find the optimistic entry by tempId
    const idx = State.attachments.findIndex(a => a.tempId === tempId);
    if (idx === -1) {
      // User removed the chip while upload was in flight — clean up server-side
      if (data.ok && data.file_id) {
        try {
          await fetch(`/api/uploads/${data.file_id}`, {
            method: 'DELETE',
            headers: uploadHeaders(),
          });
        } catch (_) {}
      }
      return;
    }
    
    if (!res.ok || !data.ok) {
      State.attachments[idx] = {
        ...State.attachments[idx],
        status: 'failed',
        error: data.error || data.detail || `HTTP ${res.status}`,
      };
    } else {
      // v3.0p18 9t: preserve thumbUrl from the optimistic entry so the
      // thumbnail stays put after the server response replaces the row.
      State.attachments[idx] = {
        id: data.file_id,
        filename: data.filename,
        fileType: data.file_type,
        sizeBytes: data.size_bytes,
        charCount: data.char_count,
        mimeType: data.mime_type,
        status: data.status || 'ready',
        thumbUrl: State.attachments[idx].thumbUrl || null,
        warning: data.warning || null,
        source: 'upload',
      };
    }
    renderAttachmentsStrip();
  } catch (e) {
    const idx = State.attachments.findIndex(a => a.tempId === tempId);
    if (idx !== -1) {
      State.attachments[idx] = {
        ...State.attachments[idx],
        status: 'failed',
        error: e.message || 'network error',
      };
      renderAttachmentsStrip();
    }
  }
}

async function loadPendingAttachments() {
  // Fetch any uploaded files that haven't been attached to a conversation yet.
  // Survives page reload: if user uploaded a PDF then refreshed, the chip
  // re-appears here so they can still send the message.
  //
  // Vet 2026-08-04 D2: the agent claims EVERY conversation-less readable
  // file on the next turn with no time window, but this used to render
  // only the last 24h — an attached old statement became an invisible
  // rider on every later message. ?pending=true asks the server for the
  // exact claimable set, and every one of them gets a chip.
  try {
    const data = await api('/api/uploads?pending=true');
    if (!data.ok || !Array.isArray(data.uploads)) return;

    const pending = data.uploads.filter(u => {
      if (u.status !== 'ready' && u.status !== 'image_ready') return false;
      if (u.conversation_id) return false;
      return true;
    });

    State.attachments = pending.map(u => ({
      id: u.id,
      filename: u.filename,
      fileType: u.file_type,
      sizeBytes: u.size_bytes,
      charCount: u.char_count,
      status: u.status,
      // Reload lost the chip's provenance — × must never destroy data
      // it can't vouch for, so restored chips detach instead of delete.
      source: 'restored',
    }));
    renderAttachmentsStrip();
  } catch (e) {
    // Silent fail — pending attachments are nice-to-have, not blocking
    console.warn('loadPendingAttachments failed:', e);
  }
}

async function removeAttachment(idOrTempId) {
  const idx = State.attachments.findIndex(a =>
    a.id === idOrTempId || a.tempId === idOrTempId);
  if (idx === -1) return;

  const att = State.attachments[idx];

  // Remove from UI immediately (optimistic)
  State.attachments.splice(idx, 1);
  renderAttachmentsStrip();

  if (!att.id) {
    // Still 'uploading': the inflight POST will resolve, see the
    // missing tempId, and clean up after itself.
    return;
  }

  // Vet 2026-08-04 D1: × means two different things.
  // A fresh composer upload (source 'upload') is cancelled — DELETE,
  // row + blob, same as before. A file attached from the library (or
  // whose provenance a reload lost) is only DETACHED: it stops riding
  // the next message but stays in the Files library. The old code
  // fired DELETE on both — a July statement he deliberately dug out
  // of the archive was hard-deleted by the ×, no confirm.
  if (att.source === 'upload') {
    try {
      await fetch(`/api/uploads/${att.id}`, {
        method: 'DELETE',
        headers: uploadHeaders(),
      });
    } catch (_) {
      // Best-effort cleanup. Orphan blob will be reconciled later.
    }
  } else {
    try {
      await api(`/api/uploads/${att.id}/detach`, {
        method: 'POST',
        // The attach response's previous_conversation_id, when we have
        // it — the file goes back to the thread it came from. Without
        // it the server parks the row so it can't ride anything.
        body: JSON.stringify({
          conversation_id: att.prevConversationId || null,
        }),
      });
    } catch (_) {
      // Best-effort — worst case the chip reappears on next reload.
    }
  }
}


// ──────────────────────────────────────────────────────────
// Files drawer — uploads filed into date folders (2026-08-04)
//
// Operator: "arrange uploaded files into folders organized by date so
// it doesn't get confusing over time." He drops several bank
// statements a month across GTBank / Moniepoint / Providus / Access /
// Wema. Before this the only browse surface was the composer strip,
// which renders unattached uploads from the last 24h — so anything
// older, or already claimed by a conversation, was unreachable.
//
// GET /api/uploads/library does the grouping server-side (month /
// bank / statement period) across EVERY conversation. Attach re-pends
// a file so the agent picks it up on the next turn — that's the path
// back to "import this statement" for a file uploaded weeks ago.

const FILES_KIND_TABS = [
  { key: 'all', label: 'All' },
  { key: 'statements', label: 'Statements' },
  { key: 'images', label: 'Images' },
  { key: 'docs', label: 'Docs' },
];
const FILES_GROUP_LABELS = {
  month: 'Upload month',
  period: 'Statement period',
  bank: 'Bank',
};

async function openFilesDrawer() {
  State.filesOpen = true;
  document.body.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';
  State._filesScrollY = window.scrollY;
  document.body.style.top = `-${State._filesScrollY}px`;
  renderFilesDrawer();
  await loadFilesLibrary();
}

function closeFilesDrawer() {
  State.filesOpen = false;
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.width = '';
  document.body.style.top = '';
  if (State._filesScrollY !== undefined) {
    window.scrollTo(0, State._filesScrollY);
    State._filesScrollY = undefined;
  }
  $('#files-drawer')?.remove();
  $('#files-backdrop')?.remove();
}

async function loadFilesLibrary(opts = {}) {
  // Only a search-triggered reload re-grabs the caret. A tab/chip tap
  // must NOT, or every filter tap pops the keyboard open on mobile.
  State._filesRefocusSearch = opts.refocusSearch === true;
  State.filesLoading = true;
  renderFilesDrawer();
  const f = State.filesFilter;
  const qs = new URLSearchParams({ group_by: f.group_by, kind: f.kind });
  if (f.bank) qs.set('bank', f.bank);
  if (f.q) qs.set('q', f.q);
  try {
    const data = await api(`/api/uploads/library?${qs.toString()}`);
    State.filesLibrary = data && data.ok ? data : { folders: [], banks: [], total: 0 };
    State.filesError = (data && data.ok) ? null : (data?.error || 'Could not load files');
  } catch (e) {
    State.filesLibrary = { folders: [], banks: [], total: 0 };
    State.filesError = e.message || 'Could not load files';
  }
  State.filesLoading = false;
  renderFilesDrawer();
}

function renderFileRow(f) {
  // Meta reads left-to-right in the order he'd scan it: which bank,
  // which statement period, then the housekeeping (size, when).
  //
  // Vet 2026-08-04 D7: status was hidden — half his live files are
  // status='failed' and rendered identical to ready ones, then Attach
  // 400'd with an opaque toast. A failed file says so quietly and its
  // Attach is disabled (there is no re-extract lane for uploaded_files
  // — the honest recovery is re-uploading the file). A still-reading
  // file waits.
  const failed = f.status === 'failed';
  const reading = f.status === 'pending';
  const attachable = f.status === 'ready' || f.status === 'image_ready';
  const bits = [];
  if (failed) bits.push('<span class="file-item-bad">failed — can\'t attach</span>');
  if (reading) bits.push('still reading');
  if (f.bank_label) bits.push(esc(f.bank_label));
  if (f.period_label) bits.push(esc(f.period_label));
  bits.push(esc(formatBytes(f.size_bytes || 0)));
  bits.push(esc(formatRelativeTime(f.created_at)));
  const attachBtn = attachable
    ? `<button class="file-item-act" type="button" data-file-attach="${esc(f.id)}"
              title="Attach to this chat">Attach</button>`
    : `<button class="file-item-act" type="button" disabled
              title="${failed
                ? 'This file failed to read — upload it again to use it'
                : 'Still reading — try again in a moment'}">Attach</button>`;
  return `
    <div class="file-item${failed ? ' failed' : ''}" data-file-id="${esc(f.id)}">
      <span class="file-item-ico">${fileIcon(f.file_type)}</span>
      <div class="file-item-main">
        <div class="file-item-title" title="${esc(f.filename)}">${esc(f.filename)}</div>
        <div class="file-item-meta">${bits.join(' · ')}</div>
      </div>
      ${attachBtn}
      <button class="conv-item-del" type="button" aria-label="Delete"
              data-file-delete="${esc(f.id)}">${trashIcon()}</button>
    </div>
  `;
}

function renderFilesDrawer() {
  $('#files-drawer')?.remove();
  $('#files-backdrop')?.remove();
  if (!State.filesOpen) return;

  const backdrop = document.createElement('div');
  backdrop.id = 'files-backdrop';
  backdrop.className = 'drawer-backdrop cc-rail-bd';
  backdrop.addEventListener('click', closeFilesDrawer);

  const drawer = document.createElement('div');
  drawer.id = 'files-drawer';
  drawer.className = 'chat-history-drawer cc-rail';

  const lib = State.filesLibrary || { folders: [], banks: [], total: 0 };
  const f = State.filesFilter;
  const folders = lib.folders || [];

  let body;
  if (State.filesLoading) {
    body = '<div class="drawer-empty">Loading files…</div>';
  } else if (State.filesError) {
    body = `<div class="drawer-empty">${esc(State.filesError)}</div>`;
  } else if (!folders.length) {
    body = `<div class="drawer-empty">${
      f.q || f.bank || f.kind !== 'all'
        ? 'Nothing matches that filter.'
        : 'No uploads yet. Drop a file in the composer and it files itself.'
    }</div>`;
  } else {
    body = folders.map((g, idx) => {
      // Folders, not a flat list: only the newest one is open until he
      // says otherwise. With a year of uploads an all-open view is the
      // pile he asked us to get rid of.
      const shut = State.filesCollapsed[g.key] === undefined
        ? idx > 0
        : State.filesCollapsed[g.key] === true;
      const banks = (g.banks || []).length
        ? ` · ${esc((g.banks || []).join(', '))}` : '';
      return `
        <div class="file-folder${shut ? ' shut' : ''}">
          <button class="file-folder-head" type="button" data-folder-key="${esc(g.key)}">
            <span class="file-folder-caret">${shut ? '▸' : '▾'}</span>
            <span class="file-folder-label">${esc(g.label)}</span>
            <span class="file-folder-count">${g.count}${banks}</span>
          </button>
          <div class="file-folder-body"${shut ? ' hidden' : ''}>
            ${(g.files || []).map(renderFileRow).join('')}
          </div>
        </div>`;
    }).join('');
  }

  const bankChips = (lib.banks || []).map(b => `
    <button class="files-chip${f.bank === b.slug ? ' on' : ''}" type="button"
            data-bank-slug="${esc(b.slug)}">${esc(b.label)} ${b.count}</button>
  `).join('');

  drawer.innerHTML = `
    <div class="drawer-header">
      <div class="drawer-title">Files</div>
      <button class="icon-btn-sm" id="files-close-btn" aria-label="Close">${closeIcon()}</button>
    </div>
    <div class="files-controls">
      <div class="files-tabs">
        ${FILES_KIND_TABS.map(t => `
          <button class="files-tab${f.kind === t.key ? ' on' : ''}" type="button"
                  data-kind="${t.key}">${esc(t.label)}</button>`).join('')}
      </div>
      <div class="files-row">
        <input class="drawer-search" id="files-search" type="search"
               placeholder="Search filenames…" autocomplete="off"
               value="${esc(f.q || '')}" />
        <select class="files-group" id="files-group">
          ${Object.entries(FILES_GROUP_LABELS).map(([k, label]) => `
            <option value="${k}"${f.group_by === k ? ' selected' : ''}>${esc(label)}</option>`).join('')}
        </select>
      </div>
      ${bankChips ? `<div class="files-chips">
        <button class="files-chip${!f.bank ? ' on' : ''}" type="button"
                data-bank-slug="">All banks</button>${bankChips}</div>` : ''}
    </div>
    <div class="drawer-list" id="files-list">${body}</div>
    ${lib.truncated
      ? `<div class="files-foot">This list is capped — older files exist beyond it. A bank filter digs past the cap; search only covers what is loaded.</div>`
      : ''}
  `;

  document.body.appendChild(backdrop);
  document.body.appendChild(drawer);

  $('#files-close-btn')?.addEventListener('click', closeFilesDrawer);

  drawer.querySelectorAll('[data-kind]').forEach(btn => {
    btn.addEventListener('click', () => {
      State.filesFilter.kind = btn.dataset.kind;
      loadFilesLibrary();
    });
  });
  drawer.querySelectorAll('[data-bank-slug]').forEach(btn => {
    btn.addEventListener('click', () => {
      State.filesFilter.bank = btn.dataset.bankSlug || '';
      loadFilesLibrary();
    });
  });
  $('#files-group')?.addEventListener('change', (e) => {
    State.filesFilter.group_by = e.target.value;
    loadFilesLibrary();
  });

  // Debounced so typing doesn't fire a request per keystroke. Every
  // load rebuilds the whole drawer, so focus + caret are restored
  // explicitly — without this the operator loses the cursor mid-word
  // 280ms after they stop typing.
  const search = $('#files-search');
  if (search) {
    search.addEventListener('input', (e) => {
      const val = e.target.value;
      clearTimeout(State._filesSearchTimer);
      State._filesSearchTimer = setTimeout(() => {
        State.filesFilter.q = val.trim();
        loadFilesLibrary({ refocusSearch: true });
      }, 280);
    });
    if (State._filesRefocusSearch) {
      search.focus();
      const end = search.value.length;
      try { search.setSelectionRange(end, end); } catch (_) {}
    }
  }

  drawer.querySelectorAll('[data-folder-key]').forEach(btn => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.folderKey;
      const el = btn.closest('.file-folder');
      State.filesCollapsed[key] = !(el && el.classList.contains('shut'));
      renderFilesDrawer();
    });
  });

  drawer.querySelectorAll('[data-file-attach]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      await attachLibraryFile(btn.dataset.fileAttach);
    });
  });

  drawer.querySelectorAll('[data-file-delete]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const id = btn.dataset.fileDelete;
      const row = drawer.querySelector(`[data-file-id="${CSS.escape(id)}"] .file-item-title`);
      const name = row ? row.textContent : 'this file';
      if (!confirm(`Delete ${name}?\n\nThis removes the file from EON permanently.`)) return;
      try {
        await api(`/api/uploads/${id}`, { method: 'DELETE' });
        State.attachments = State.attachments.filter(a => a.id !== id);
        renderAttachmentsStrip();
        await loadFilesLibrary();
      } catch (err) {
        showToast(err.message || 'Delete failed');
      }
    });
  });
}

async function attachLibraryFile(fileId) {
  if (State.attachments.some(a => a.id === fileId)) {
    showToast('Already attached');
    return;
  }
  try {
    const data = await api(`/api/uploads/${fileId}/attach`, { method: 'POST' });
    if (!data || !data.ok) {
      showToast(data?.detail || data?.error || 'Attach failed');
      return;
    }
    State.attachments.push({
      id: data.file_id,
      filename: data.filename,
      fileType: data.file_type,
      sizeBytes: data.size_bytes,
      charCount: data.char_count,
      status: data.status || 'ready',
      // Library provenance (vet 2026-08-04 D1): the chip's × detaches
      // — never deletes — and hands back the conversation this file
      // belonged to before the attach, so it goes home intact.
      source: 'library',
      prevConversationId: data.previous_conversation_id || null,
    });
    renderAttachmentsStrip();
    closeFilesDrawer();
    showToast(`${data.filename} attached to this chat`);
  } catch (e) {
    showToast(e.message || 'Attach failed');
  }
}


// ──────────────────────────────────────────────────────────
// Chat history drawer (v3.0p14)
// 
// State:
//   State.activeConversationId: which thread we're in (null = backend auto)
//   State.activeConversationTitle: for toolbar display
//   State.conversations: cached list from /api/conversations
//   State.chatHistoryOpen: drawer open/closed
//
// Flow:
//   - Open drawer → load conversations → render list
//   - Tap conversation → load its messages → set as active → close drawer
//   - "+ New chat" → POST /api/conversations/new → reset State.chat → close drawer
//   - × on conversation → DELETE /api/conversations/{id} → re-render list

async function openChatHistoryDrawer() {
  State.chatHistoryOpen = true;
  // v3.0p15 commit 9a B3-1: lock body scroll while drawer open. Without this,
  // iOS allows underlying page to scroll behind the backdrop when user
  // touches anywhere off-drawer.
  document.body.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';
  // Save scroll position so we can restore it on close
  State._drawerScrollY = window.scrollY;
  document.body.style.top = `-${State._drawerScrollY}px`;
  renderChatHistoryDrawer();
  // Load fresh list (cached but probably stale)
  await loadConversations();
  renderChatHistoryDrawer();
}

function closeChatHistoryDrawer() {
  State.chatHistoryOpen = false;
  // v3.0p15 commit 9a B3-1: restore body scroll
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.width = '';
  document.body.style.top = '';
  if (State._drawerScrollY !== undefined) {
    window.scrollTo(0, State._drawerScrollY);
    State._drawerScrollY = undefined;
  }
  $('#chat-history-drawer')?.remove();
  $('#chat-history-backdrop')?.remove();
}

async function loadConversations() {
  try {
    const data = await api('/api/conversations');
    if (data.ok && Array.isArray(data.conversations)) {
      State.conversations = data.conversations;
    }
  } catch (e) {
    console.warn('loadConversations failed:', e);
  }
}

function renderChatHistoryDrawer() {
  // Remove any existing drawer
  $('#chat-history-drawer')?.remove();
  $('#chat-history-backdrop')?.remove();
  
  if (!State.chatHistoryOpen) return;
  
  const backdrop = document.createElement('div');
  backdrop.id = 'chat-history-backdrop';
  backdrop.className = 'drawer-backdrop cc-rail-bd';
  backdrop.addEventListener('click', closeChatHistoryDrawer);

  const drawer = document.createElement('div');
  drawer.id = 'chat-history-drawer';
  drawer.className = 'chat-history-drawer cc-rail';
  
  const list = State.conversations || [];
  // Daniel ask (2026-05-18): "claude side bar is rich and clear,
  // adopt claude/gemini clean simple UI." Group conversations by
  // date bucket with Today / Yesterday / This week / This month /
  // Older headers — matching Claude's left rail.
  const grouped = groupConversationsByBucket(list);
  const groupsHtml = grouped.map(g => `
    <div class="drawer-group-header">${esc(g.label)}</div>
    ${g.items.map(c => renderConvItem(c)).join('')}
  `).join('');
  drawer.innerHTML = `
    <div class="drawer-header">
      <div class="drawer-title">Chats</div>
      <button class="icon-btn-sm" id="drawer-close-btn" aria-label="Close">${closeIcon()}</button>
    </div>
    <button class="drawer-new-btn" id="drawer-new-chat-btn">
      ${plusIcon()} <span>New chat</span>
    </button>
    <input class="drawer-search" id="drawer-search" type="search"
           placeholder="Search chats…" autocomplete="off"
           ${list.length === 0 ? 'style="display:none;"' : ''} />
    <div class="drawer-list" id="drawer-list">
      ${list.length === 0
        ? '<div class="drawer-empty">No previous chats yet.</div>'
        : groupsHtml}
    </div>
  `;
  
  document.body.appendChild(backdrop);
  document.body.appendChild(drawer);
  
  // Wire up
  $('#drawer-close-btn')?.addEventListener('click', closeChatHistoryDrawer);
  $('#drawer-new-chat-btn')?.addEventListener('click', startNewConversation);

  // PR-8 (2026-07-06): client-side title filter. Hides non-matching
  // conversations in place (no re-render), and collapses any date
  // bucket header whose items are all hidden.
  $('#drawer-search')?.addEventListener('input', (e) => {
    const q = e.target.value.trim().toLowerCase();
    document.querySelectorAll('#drawer-list .conv-item').forEach(el => {
      const t = el.querySelector('.conv-item-title')?.textContent
        .toLowerCase() || '';
      el.style.display = (!q || t.includes(q)) ? '' : 'none';
    });
    document.querySelectorAll('#drawer-list .drawer-group-header')
      .forEach(h => {
        let sib = h.nextElementSibling, any = false;
        while (sib && !sib.classList.contains('drawer-group-header')) {
          if (sib.classList.contains('conv-item')
              && sib.style.display !== 'none') { any = true; break; }
          sib = sib.nextElementSibling;
        }
        h.style.display = any ? '' : 'none';
      });
  });
  
  document.querySelectorAll('[data-conv-id]').forEach(el => {
    el.addEventListener('click', async (e) => {
      // Don't trigger when × was clicked
      if (e.target.closest('[data-conv-delete]')) return;
      const cid = el.dataset.convId;
      await selectConversation(cid);
    });
  });
  
  document.querySelectorAll('[data-conv-delete]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const cid = btn.dataset.convDelete;
      const conv = State.conversations.find(c => c.id === cid);
      if (!conv) return;
      if (!confirm(`Delete this chat? "${conv.title}"\n\nThis cannot be undone.`)) return;
      await deleteConversation(cid);
    });
  });
}

function renderConvItem(c) {
  const active = c.id === State.activeConversationId;
  return `
    <div class="conv-item ${active ? 'active' : ''}" data-conv-id="${esc(c.id)}">
      <div class="conv-item-main">
        <div class="conv-item-title">${esc(c.title)}</div>
        <div class="conv-item-meta">${esc(formatRelativeTime(c.last_message_at))} · ${c.message_count} msgs</div>
      </div>
      <button class="conv-item-del" type="button" aria-label="Delete" data-conv-delete="${esc(c.id)}">
        ${trashIcon()}
      </button>
    </div>
  `;
}

function formatRelativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const now = Date.now();
  const diff = (now - t) / 1000;  // seconds
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}


// Group conversations into Today / Yesterday / This week / This month
// / Older buckets — matches Claude's left-rail organisation. Each
// bucket is an object {label, items}; bucket emitted only if it has
// at least one row, so empty buckets don't render dead headers.
function groupConversationsByBucket(list) {
  if (!list || !list.length) return [];
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterdayStart = todayStart - 86400 * 1000;
  const weekStart = todayStart - 86400 * 1000 * 7;
  const monthStart = todayStart - 86400 * 1000 * 30;
  const buckets = {
    today:     { label: 'Today',      items: [] },
    yesterday: { label: 'Yesterday',  items: [] },
    week:      { label: 'This week',  items: [] },
    month:     { label: 'This month', items: [] },
    older:     { label: 'Older',      items: [] },
  };
  for (const c of list) {
    const t = c.last_message_at ? new Date(c.last_message_at).getTime() : 0;
    if (t >= todayStart)             buckets.today.items.push(c);
    else if (t >= yesterdayStart)    buckets.yesterday.items.push(c);
    else if (t >= weekStart)         buckets.week.items.push(c);
    else if (t >= monthStart)        buckets.month.items.push(c);
    else                              buckets.older.items.push(c);
  }
  return Object.values(buckets).filter(b => b.items.length > 0);
}

async function selectConversation(convId) {
  // Opening another thread ends a live call in this one now, not after the load.
  if (convId !== State.activeConversationId) chatConversation.leaveThread();
  // The thread a call is live in keeps its turns: no reload under the call.
  else if (chatConversation.liveIn(convId)) { closeChatHistoryDrawer(); return; }
  try {
    // WS-C: leaving mid-stream abandons the in-flight turn — its events
    // are epoch-dropped instead of bleeding into the opened thread.
    // Park the composer draft under the OLD conversation's key first.
    const inp = $('#composer-input');
    if (inp) _chatDraftSave(inp.value);
    _abandonInFlightTurn();

    const data = await api(`/api/conversations/${convId}`);
    if (!data.ok || !data.messages) return;
    // A call started in this thread while it loaded owns it now.
    if (chatConversation.liveIn(convId)) { closeChatHistoryDrawer(); return; }

    // Reset chat state to this conversation's messages
    State.chat = data.messages.map(m => ({
      role: m.role === 'assistant' ? 'eon' : m.role,
      text: m.content,
      source_refs: m.role === 'assistant' ? sourceReferences(m.source_refs) : [],
    }));
    State.activeConversationId = convId;
    
    // Update title from conversations cache
    const conv = State.conversations.find(c => c.id === convId);
    State.activeConversationTitle = conv?.title || 'Chat';
    
    // Refresh attachments to reflect this conversation's files.
    // source 'conversation' (vet 2026-08-04 D1): the × on these chips
    // detaches the file from this thread — the old path hard-deleted
    // the row and blob.
    State.attachments = (data.files || []).map(f => ({
      id: f.id,
      filename: f.filename,
      fileType: f.file_type,
      sizeBytes: f.size_bytes,
      charCount: f.char_count,
      status: 'ready',
      source: 'conversation',
    }));
    
    closeChatHistoryDrawer();
    
    // Re-render the chat view with new title + body
    if (State.view === 'chat') {
      const app = $('#app');
      if (app) {
        // Re-render just the toolbar title + chat body in place to avoid losing state
        const titleEl = $('#chat-toolbar-title');
        if (titleEl) titleEl.textContent = State.activeConversationTitle;
        renderChatBody();
        renderAttachmentsStrip();
        // WS-C: bring in THIS conversation's parked draft.
        const input = $('#composer-input');
        if (input) {
          input.value = _chatDraftLoad();
          _composerValueReplaced(input);
          const send = $('#composer-send');
          if (send) send.disabled = !input.value.trim();
          // The status line was read before this conversation's draft came in.
          chatConversation.update();
        }
      }
    }
  } catch (e) {
    console.warn('selectConversation failed:', e);
    showToast('Could not load conversation: ' + (e.message || e));
  }
}

// PR-7 (2026-07-06): per-thread deep-mode flag. Keyed by conversation
// id; a not-yet-claimed thread rides the '__new__' key (the flag
// follows whichever thread is active — predictable, no server round
// trip). Only 'deep' is meaningful server-side; 'base' collapses to
// the default lane.
function _deepConvKey() { return State.activeConversationId || '__new__'; }
function _deepMap() {
  try { return JSON.parse(localStorage.getItem('eon_deep_convs') || '{}'); }
  catch (_) { return {}; }
}
function _isDeepConv() { return !!_deepMap()[_deepConvKey()]; }
function _toggleDeepConv() {
  const map = _deepMap();
  const k = _deepConvKey();
  if (map[k]) delete map[k]; else map[k] = true;
  try { localStorage.setItem('eon_deep_convs', JSON.stringify(map)); }
  catch (_) { /* private mode — toggle still works for this render */ }
}
// A fresh Chat's '__new__' choice moves to the conversation it becomes, so
// every send in that thread stays deep and the next fresh Chat starts clear.
function _deepCarryNew(conversationId) {
  const map = _deepMap();
  if (!map.__new__) return;
  delete map.__new__;
  map[conversationId] = true;
  try { localStorage.setItem('eon_deep_convs', JSON.stringify(map)); }
  catch (_) { /* private mode — nothing was stored to carry */ }
}

async function startNewConversation() {
  chatConversation.leaveThread();
  try {
    // WS-C: same stream-abandon contract as selectConversation — a new
    // chat mid-stream must not receive the old turn's reply.
    const inp = $('#composer-input');
    if (inp) _chatDraftSave(inp.value);
    _abandonInFlightTurn();

    const data = await api('/api/conversations/new', { method: 'POST' });
    if (!data.ok || !data.conversation_id) return;

    State.activeConversationId = data.conversation_id;
    // The pass for this still-empty thread: live voice may start in it.
    chatConversation.rememberBinding(data.conversation_id, data.binding);
    State.activeConversationTitle = 'New chat';
    State.chat = [];
    State.attachments = [];
    
    closeChatHistoryDrawer();
    
    // Re-render chat view
    if (State.view === 'chat') {
      const titleEl = $('#chat-toolbar-title');
      if (titleEl) titleEl.textContent = 'EON';
      renderChatBody();
      renderAttachmentsStrip();
      const nextInput = $('#composer-input');
      if (nextInput) { nextInput.value = _chatDraftLoad(); _composerValueReplaced(nextInput); }
      chatConversation.update();
      // Focus the composer for immediate typing
      setTimeout(() => $('#composer-input')?.focus(), 50);
    }
  } catch (e) {
    console.warn('startNewConversation failed:', e);
  }
}

async function deleteConversation(convId) {
  try {
    await api(`/api/conversations/${convId}`, { method: 'DELETE' });
    State.conversations = State.conversations.filter(c => c.id !== convId);
    
    // If we deleted the active conversation, reset
    if (State.activeConversationId === convId) {
      State.activeConversationId = null;
      State.activeConversationTitle = null;
      State.chat = [];
      State.attachments = [];
      if (State.view === 'chat') {
        const titleEl = $('#chat-toolbar-title');
        if (titleEl) titleEl.textContent = 'EON';
        renderChatBody();
        renderAttachmentsStrip();
      }
    }
    
    // Re-render the drawer list
    renderChatHistoryDrawer();
  } catch (e) {
    console.warn('deleteConversation failed:', e);
    showToast('Could not delete: ' + (e.message || e));
  }
}


// ──────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────
// Inventory (v3.0p18 9gg)
//
// 4 widgets + vendor leaderboard + per-location restock list, all
// reading the Bundle-1 /api/inventory/* endpoints. Each card has an
// "Ask EON" button that pre-fills a chat prompt for the long-form
// breakdown. Per-location filtering: if State.member.location_code
// is set (store managers), every read auto-scopes to that location.

// ── Inventory entry (operator, 2026-09-06: the selected Inventory desk is the
//    primary Inventory experience). Ordinary Inventory navigation — the menu
//    tile and /?v=inventory — opens the /inventory workspace page. The older
//    hub below (group-gauge upload, product-edit proposals and approvals,
//    buying calendar, saved report views, bulk price tools) stays reachable at
//    /?v=inventory&legacy=1 and from the workspace's "Older inventory tools"
//    door; nothing is removed and its role gates are unchanged. No loop: the
//    workspace is its own page and never sends anyone back here by itself; a
//    signed-out visit returns through /?return=/inventory to the login picker,
//    which then lands on the workspace again. The legacy flag is read once and
//    kept for the session, so a live re-render never yanks the hub away.
const INVENTORY_WORKSPACE = '/inventory';
let _inventoryHandoff = false;
function inventoryLegacyRequested() {
  try { return new URLSearchParams(location.search).get('legacy') === '1'; } catch (_) { return false; }
}
// mode 'replace': the automatic migration of an obsolete /?v=inventory entry.
// That entry is REPLACED, so Back returns to wherever the person came from and
// never to a page whose only job is to send them forward again (Codex, 6 Sep
// 2026: Back trapped in /inventory). mode 'assign': an explicit menu tap — the
// page they were on stays the Back destination.
function openInventoryWorkspace(mode) {
  if (_inventoryHandoff) return;
  _inventoryHandoff = true;
  try {
    if (mode === 'replace') window.location.replace(INVENTORY_WORKSPACE);
    else window.location.assign(INVENTORY_WORKSPACE);
  } catch (_) { _inventoryHandoff = false; }
}
function renderWorkspaceHandoff() {
  return `
    <div class="section-h">
      <h2>Inventory</h2>
      <span class="meta">Opening the Inventory workspace</span>
    </div>
    <div class="inv-deep-link-card" id="inv-handoff">
      <div class="inv-deep-link-body">
        <div class="inv-deep-link-title">The Inventory workspace is opening</div>
        <div class="inv-deep-link-sub">Today, stock by store, counts, transfers, arrivals and how EON learns. If it does not open by itself, use the button.</div>
      </div>
      <a class="inv-deep-link-btn" href="${INVENTORY_WORKSPACE}">Open the Inventory workspace →</a>
    </div>
    <div class="inv-deep-link-card" id="inv-legacy-door">
      <div class="inv-deep-link-body">
        <div class="inv-deep-link-title">Older inventory tools</div>
        <div class="inv-deep-link-sub">Group gauge upload, product-edit proposals and approvals, buying calendar, saved report views and bulk price tools.</div>
      </div>
      <a class="inv-deep-link-btn" href="/?v=inventory&legacy=1">Open older tools →</a>
    </div>`;
}

function renderInventory() {
  // v3.0p18 9gg: store-manager scoping reads from the existing
  // MemberContext.scope.locations array (NULL = unrestricted). When
  // exactly one location is set we treat it as the manager's home
  // store and auto-filter every inventory read to it.
  // v3.0p32 PR-INV: removed Low Stock (operator gets it from Assisty);
  // added Days-of-Cover heatmap, Transfer Suggestions, Dead Stock,
  // Inventory Trend (value + cash-ratio + turnover). Every tile
  // answers "what should I DO about this."
  const locList = State.member?.scope?.locations || null;
  const loc = (Array.isArray(locList) && locList.length === 1)
    ? locList[0] : null;
  return `
    <div class="section-h">
      <h2>Inventory</h2>
      <span class="meta">${loc ? `Scoped to: ${esc(loc)}` : 'All locations'}</span>
    </div>

    <div class="inv-deep-link-card inv-warehouse-cta" id="inv-warehouse-link">
      <div class="inv-deep-link-body">
        <div class="inv-deep-link-title">Warehouse OS — receiving, atomic transfers, counts, audit</div>
        <div class="inv-deep-link-sub">The full operational system (replaces Assisty): inbound, cross-store transfers, blind counts, anti-fraud audit trail, production-aware reorder.</div>
      </div>
      <a class="inv-deep-link-btn" href="/warehouse">Open Warehouse OS →</a>
    </div>

    <div class="inv-deep-link-card" id="inv-deep-link">
      <div class="inv-deep-link-body">
        <div class="inv-deep-link-title">Per-SKU dashboard + reorder math</div>
        <div class="inv-deep-link-sub">Stock × location, reorder points, days-of-cover, depletion forecast.</div>
      </div>
      <a class="inv-deep-link-btn" href="/inventory">Open inventory dashboard →</a>
    </div>

    <!-- Group gauge door (#1907 follow-up): the upload API shipped with
         no surface calling it — this card IS the monthly ritual. -->
    <div class="inv-deep-link-card" id="inv-gauge-card">
      <div class="inv-deep-link-body">
        <div class="inv-deep-link-title">Group gauge — monthly chat-export drop</div>
        <div class="inv-deep-link-sub">Export the team WhatsApp group <b>without media</b> and drop the .zip or _chat.txt here. EON counts what still gets answered by hand; a shape loud two months running files its own build proposal.</div>
        <div class="inv-deep-link-sub" id="inv-gauge-status" hidden></div>
      </div>
      <label class="inv-deep-link-btn" for="inv-gauge-file" style="cursor:pointer">Upload export</label>
      <input type="file" id="inv-gauge-file" accept=".txt,.zip,text/plain,application/zip" hidden>
    </div>

    <!-- BATCH B: assistant proposes a product edit → manager approval task.
         Rendered only for an inventory ASSISTANT (propose-only). -->
    <div id="inv-propose-edit-host"></div>

    <!-- BATCH B: pending product-edit approvals. Rendered only for a
         manager (P.INVENTORY_MANAGE). -->
    <div id="inv-pending-edits-host"></div>

    <!-- WO-6 — buying calendar: upcoming reorders bucketed by reorder_date -->
    <div class="section-h">
      <h2>Buying calendar</h2>
      <span class="meta">Upcoming reorders by date · next 60 days</span>
    </div>
    <div id="inv-buying-calendar"><div class="card" style="height: 64px; opacity: 0.4;"></div></div>

    <!-- WO-6 — saved report views over the reports library -->
    <div class="section-h">
      <h2>Saved report views</h2>
      <span class="meta">Replaces Assisty saved dashboards · scheduled delivery dry-run by default</span>
    </div>
    <div id="inv-saved-reports"><div class="card" style="height: 64px; opacity: 0.4;"></div></div>

    <div class="stats" id="inv-kpis">
      <div class="stat"><div class="stat-label">Total at cost</div><div class="stat-val" id="inv-kpi-cost">—</div></div>
      <div class="stat"><div class="stat-label">At retail</div><div class="stat-val" id="inv-kpi-retail">—</div></div>
      <div class="stat"><div class="stat-label">Margin</div><div class="stat-val" id="inv-kpi-margin">—</div></div>
    </div>

    <div class="section-h">
      <h2>Bulk price editor</h2>
      <span class="meta">Margin-aware · preview before apply · auto-revert + rollback</span>
    </div>
    <div class="card" id="reprice-card">
      <div class="reprice-filters" style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:10px;">
        <label class="fld"><span>Vendor</span><input id="rp-vendor" type="text" placeholder="e.g. CRUX CUSTOM"></label>
        <label class="fld"><span>Collection</span><input id="rp-collection" type="text" placeholder="title or handle"></label>
        <label class="fld"><span>Tag</span><input id="rp-tag" type="text" placeholder="single tag"></label>
        <label class="fld"><span>Status</span><input id="rp-status" type="text" placeholder="ACTIVE / DRAFT (optional)"></label>
      </div>
      <div class="reprice-rule" style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:10px;">
        <label class="fld"><span>Rule</span>
          <select id="rp-kind">
            <option value="margin">Target margin %</option>
            <option value="percent">Percent change</option>
            <option value="fixed">Fixed amount (₦)</option>
            <option value="absolute">Set exact price</option>
          </select>
        </label>
        <label class="fld" id="rp-value-wrap"><span>Value</span><input id="rp-value" type="number" step="0.01" placeholder="e.g. -20"></label>
        <label class="fld" id="rp-margin-wrap"><span>Target margin %</span><input id="rp-margin" type="number" step="0.1" placeholder="e.g. 65"></label>
        <label class="fld"><span>Rounding</span>
          <select id="rp-rounding">
            <option value="none">None</option>
            <option value="0.99">.99 charm</option>
            <option value="0.95">.95 charm</option>
            <option value="nearest">Nearest ₦1</option>
          </select>
        </label>
        <label class="fld"><span>Field</span>
          <select id="rp-field">
            <option value="price">Price</option>
            <option value="compare_at">Compare-at (sale strike)</option>
          </select>
        </label>
        <label class="fld"><span>Auto-revert at (sale end)</span><input id="rp-revert" type="datetime-local"></label>
      </div>
      <div class="reprice-actions" style="display:flex;gap:8px;flex-wrap:wrap;">
        <button class="btn-ghost" id="rp-preview-btn" type="button">Preview</button>
        <button class="btn-primary" id="rp-apply-btn" type="button" disabled>Apply</button>
        <span class="meta" id="rp-status-msg" style="align-self:center;"></span>
      </div>
      <div id="rp-preview" style="margin-top:10px;"></div>
    </div>

    <div class="section-h">
      <h2>Reprice runs</h2>
      <span class="meta">Snapshot history · per-job rollback</span>
    </div>
    <div id="rp-snapshots"><div class="card" style="height:48px;opacity:0.4;"></div></div>

    <div class="section-h">
      <h2>Inventory trend (30d)</h2>
      <button class="btn-ghost" type="button" onclick="goChat('What does my inventory trend look like over the last 30 days? Is my inventory-to-cash ratio healthy?')">Ask EON</button>
    </div>
    <div id="inv-trend"><div class="card" style="height: 120px; opacity: 0.4;"></div></div>

    <div class="section-h">
      <h2>Days of cover — heatmap (top 20)</h2>
      <button class="btn-ghost" type="button" onclick="goChat('Which fastest-moving SKUs are about to stock out at any store? Walk me through the days-of-cover heatmap.')">Ask EON</button>
    </div>
    <div id="inv-heatmap"><div class="card" style="height: 200px; opacity: 0.4;"></div></div>

    <div class="section-h">
      <h2>Transfer suggestions</h2>
      <button class="btn-ghost" type="button" onclick="goChat('Which SKUs should I move between stores? Walk me through the highest-impact transfers and the operational steps.')">Ask EON</button>
    </div>
    <div id="inv-transfers"><div class="card" style="height: 64px; opacity: 0.4;"></div></div>

    <div class="section-h">
      <h2>Dead stock — capital tied up</h2>
      <button class="btn-ghost" type="button" onclick="goChat('Which SKUs are dead stock? Tell me what to do with each — markdown, transfer, or destroy.')">Ask EON</button>
    </div>
    <div id="inv-dead-stock"><div class="card" style="height: 64px; opacity: 0.4;"></div></div>

    <div class="section-h">
      <h2>Top 10 fastest movers (30d)</h2>
      <button class="btn-ghost" type="button" onclick="goChat('Walk me through my top fastest-moving SKUs over the last 30 days. Highlight any that are about to stock out.')">Ask EON</button>
    </div>
    <div id="inv-velocity"><div class="card" style="height: 64px; opacity: 0.4;"></div></div>

    <div class="section-h">
      <h2>Refund rate by SKU (90d)</h2>
      <button class="btn-ghost" type="button" onclick="goChat('Show me products with the highest refund rates in the last 90 days. What patterns do you see?')">Ask EON</button>
    </div>
    <div id="inv-refunds"><div class="card" style="height: 64px; opacity: 0.4;"></div></div>

    <div class="section-h">
      <h2>Vendor performance (30d)</h2>
      <button class="btn-ghost" type="button" onclick="goChat('Break down vendor performance by revenue, margin and refund rate. Who are my best and worst?')">Ask EON</button>
    </div>
    <div id="inv-vendors"><div class="card" style="height: 64px; opacity: 0.4;"></div></div>

    <div class="section-h">
      <h2>Locations</h2>
      <button class="btn-ghost" type="button" onclick="goChat('Where is my inventory concentrated? Recommend any rebalancing transfers between locations.')">Ask EON</button>
    </div>
    <div id="inv-locations"><div class="card" style="height: 64px; opacity: 0.4;"></div></div>
  `;
}

async function bindInventory() {
  // Group gauge door — multipart, so a raw fetch instead of api().
  const gaugeFile = $('#inv-gauge-file');
  if (gaugeFile) gaugeFile.addEventListener('change', async () => {
    const f = gaugeFile.files && gaugeFile.files[0];
    if (!f) return;
    const st = $('#inv-gauge-status');
    st.hidden = false;
    st.textContent = 'Reading the export…';
    const fd = new FormData();
    fd.append('file', f, f.name);
    try {
      const res = await fetch(API + '/api/group-gauge/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${State.jwt}`,
          'X-Tenant-Slug': State.tenant.slug,
        },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.detail || data.error
          || 'That file did not look like a chat export.');
      }
      st.textContent = (data.report && data.report.trend
        && data.report.trend.line) || 'Run recorded.';
      showToast(data.fed && data.fed.repeat_month
        ? 'Same month as an earlier run — recorded, not re-counted'
        : 'Gauge run recorded — the loop card is updated');
    } catch (e) {
      st.textContent = e.message || 'Upload failed';
      showToast(e.message || 'Upload failed');
    } finally {
      gaugeFile.value = '';
    }
  });

  // v3.0p18 9gg: store-manager scoping reads from the existing
  // MemberContext.scope.locations array (NULL = unrestricted). When
  // exactly one location is set we treat it as the manager's home
  // store and auto-filter every inventory read to it.
  const locList = State.member?.scope?.locations || null;
  const loc = (Array.isArray(locList) && locList.length === 1)
    ? locList[0] : null;
  const locQS = loc ? `?location_code=${encodeURIComponent(loc)}` : '';

  // PR-INV: the new insights aggregator runs in parallel with the
  // legacy reads. /api/inventory/insights bundles trend + heatmap +
  // transfers + dead stock in one round-trip; the older endpoints
  // power KPI strip, velocity, refunds, vendors, locations.
  const [value, vel, refunds, vendors, insights] = await Promise.all([
    api(`/api/inventory/value${locQS}`).catch(() => null),
    api(`/api/inventory/velocity?n=10&days=30`).catch(() => null),
    api(`/api/inventory/refund-rates?days=90&min_orders=5&limit=10`).catch(() => null),
    api(`/api/inventory/vendor-performance?days=30`).catch(() => null),
    api(`/api/inventory/insights`).catch(() => null),
  ]);

  // Five awaited reads sit above this line, and bindView() re-runs on
  // every navigation — so by the time they land, renderView() may have
  // replaced the inventory markup with another view's. Every paint below
  // is a bare $('#inv-…').prop write; the first of them threw on null
  // three times in a browser pass of /?v=inventory, which killed the KPI
  // strip AND the ten paints and four binders after it. One mount check
  // beats eleven guards: #inv-kpis is unconditional in renderInventory,
  // so if it is gone the whole view is gone and there is nothing to
  // paint into — the same "am I still on screen?" bail the Line's
  // hydrate does after its own await.
  if (!$('#inv-kpis')) return;

  // ── KPI strip ──
  // When cost data hasn't been synced, the backend returns 0s. Showing
  // "₦0" reads as "NO" in the serif KPI font (the ₦ glyph has a bar
  // through the N), so collapse zero/missing values to em-dash.
  const _kpiNaira = (n) => (n && Number(n) > 0) ? '₦' + _widgetCompact(n) : '—';
  if (value && value.ok) {
    $('#inv-kpi-cost').textContent = _kpiNaira(value.value_at_cost_ngn);
    $('#inv-kpi-retail').textContent = _kpiNaira(value.value_at_retail_ngn);
    $('#inv-kpi-margin').textContent = (value.gross_margin_pct != null
      ? `${value.gross_margin_pct.toFixed(1)}%`
      : '—');
  } else {
    ['inv-kpi-cost','inv-kpi-retail','inv-kpi-margin'].forEach(id => $('#' + id).textContent = '—');
  }

  // ── Inventory trend tile ──
  $('#inv-trend').innerHTML = _renderInventoryTrend(insights?.trend);

  // ── Days-of-cover heatmap ──
  $('#inv-heatmap').innerHTML = _renderInventoryHeatmap(insights?.heatmap);

  // ── Transfer suggestions ──
  $('#inv-transfers').innerHTML = _renderInventoryList(insights?.transfers?.items, [
    { key: 'sku', label: 'SKU' },
    { key: 'product_title', label: 'Product' },
    { key: 'from_location', label: 'From' },
    { key: 'from_doc', label: 'From DoC', fmt: 'days_of_cover' },
    { key: 'to_location', label: 'To' },
    { key: 'to_doc', label: 'To DoC', fmt: 'days_of_cover' },
    { key: 'suggested_qty', label: 'Move', class: 'mono' },
    { key: 'retail_value_ngn', label: 'Retail', fmt: 'naira' },
  ], 'No inter-store rebalancing needed right now.');

  // ── Dead stock ──
  $('#inv-dead-stock').innerHTML = _renderInventoryList(insights?.dead_stock?.items, [
    { key: 'sku', label: 'SKU' },
    { key: 'product_title', label: 'Product' },
    { key: 'location_code', label: 'Loc' },
    { key: 'on_hand', label: 'On hand', class: 'mono' },
    { key: 'capital_ngn', label: 'Capital', fmt: 'naira' },
    { key: 'retail_value_ngn', label: 'Retail', fmt: 'naira' },
  ], 'No dead stock above the threshold.');

  // ── Velocity ──
  $('#inv-velocity').innerHTML = _renderInventoryList(vel?.items, [
    { key: 'sku', label: 'SKU' },
    { key: 'product_title', label: 'Product' },
    { key: 'units_sold', label: 'Units 30d', class: 'mono' },
    { key: 'on_hand', label: 'On hand', class: 'mono' },
    { key: 'days_of_cover', label: 'DoC', fmt: 'days_of_cover' },
  ], 'No sales activity in the last 30 days.');

  // ── Refund rate ──
  $('#inv-refunds').innerHTML = _renderInventoryList(refunds?.items, [
    { key: 'sku', label: 'SKU' },
    { key: 'product_title', label: 'Product' },
    { key: 'units_refunded', label: 'Refunded', class: 'mono' },
    { key: 'refund_pct', label: 'Refund %', fmt: 'pct' },
    { key: 'revenue_loss_ngn', label: 'Lost', fmt: 'naira' },
  ], 'No refund patterns above the minimum-orders threshold.');

  // ── Vendor leaderboard ──
  $('#inv-vendors').innerHTML = _renderInventoryList(vendors?.items, [
    { key: 'vendor', label: 'Vendor' },
    { key: 'revenue_ngn', label: 'Revenue', fmt: 'naira' },
    { key: 'units', label: 'Units', class: 'mono' },
    { key: 'gross_margin_pct', label: 'Margin', fmt: 'pct' },
    { key: 'refund_pct', label: 'Refund %', fmt: 'pct' },
  ], 'No vendor activity in the last 30 days.');

  // ── Per-location ──
  $('#inv-locations').innerHTML = _renderInventoryList(value?.by_location, [
    { key: 'location_code', label: 'Location' },
    { key: 'value_at_cost_ngn', label: 'At cost', fmt: 'naira' },
    { key: 'value_at_retail_ngn', label: 'At retail', fmt: 'naira' },
    { key: 'units', label: 'Units', class: 'mono' },
    { key: 'sku_count', label: 'SKUs', class: 'mono' },
  ], loc
      ? `Scoped to ${esc(loc)}. Switch user role to see all locations.`
      : 'No location data. Run an inventory sync.');

  // ── WO-6: buying calendar + saved report views ──
  _bindBuyingCalendar();
  _bindSavedReports();
  // ── WO-5 Bulk price editor (margin-aware) — INVENTORY view only ──
  bindReprice();
  // ── BATCH B: assistant propose-edit + manager approvals ──
  bindProductEditApproval();
}

// ── INVENTORY PRODUCT-EDIT APPROVAL BEGIN (BATCH B) ──────────────────
// product-edit propose + approve UI.
// Two permission-gated affordances, both fail-soft:
//  • Assistant (P.INVENTORY_ASSISTANT_EDIT, NOT manage): a small form to
//    PROPOSE a product edit (title|description|tags|price) → POST
//    /api/inventory/products/{id}/propose-edit → opens a manager task.
//    Assistants get NO direct-write button (only propose + the existing
//    transfer flow, which is gated/automated).
//  • Manager (P.INVENTORY_MANAGE): a "Pending edits" list from GET
//    /api/inventory/products/proposals?status=pending with Approve /
//    Reject buttons → POST .../approve-edit | .../reject-edit.
function bindProductEditApproval() {
  if (invCanProposeEdit()) _renderProposeEditCard();
  if (invCanManageProducts()) loadPendingProductEdits();
}

// title/description/tags need a product_gid; price needs a sku — exactly
// the backend route's validation (routes_inventory _ProposeEditBody +
// product_edit_approval.propose_edit). The picker swaps the identifier
// field + helper text so the request body always matches the chosen field.
function _renderProposeEditCard() {
  const host = $('#inv-propose-edit-host');
  if (!host) return;
  host.innerHTML = `
    <div class="section-h">
      <h2>Propose a product edit</h2>
      <span class="meta">Opens an approval task for the inventory manager · no direct write</span>
    </div>
    <div class="card inv-propose-card" id="inv-propose-card">
      <div class="inv-propose-grid">
        <label class="fld"><span>Field</span>
          <select id="ipe-field">
            <option value="title">Title</option>
            <option value="description">Description</option>
            <option value="tags">Tags (comma-separated)</option>
            <option value="price">Price (NGN)</option>
          </select>
        </label>
        <label class="fld" id="ipe-gid-wrap"><span>Product</span>
          <input id="ipe-prodsearch" type="search" placeholder="Type a product name or SKU…" autocomplete="off">
          <input id="ipe-gid" type="hidden">
          <div id="ipe-prodresults" hidden
            style="max-height:220px;overflow-y:auto;border:1px solid var(--border);border-radius:8px;margin-top:4px;"></div>
        </label>
        <label class="fld" id="ipe-sku-wrap" style="display:none;"><span>SKU</span>
          <input id="ipe-sku" type="text" placeholder="e.g. CRX-TEE-BLK-M">
        </label>
      </div>
      <label class="fld"><span>New value</span>
        <input id="ipe-newval" type="text" placeholder="The corrected value">
      </label>
      <label class="fld"><span>Reason (optional)</span>
        <input id="ipe-reason" type="text" placeholder="Why this change?">
      </label>
      <div class="inv-propose-actions">
        <button class="btn-primary" id="ipe-submit" type="button">Propose edit</button>
        <span class="meta" id="ipe-msg"></span>
      </div>
    </div>`;

  const card = $('#inv-propose-card');
  if (card.dataset.bound) return;
  card.dataset.bound = '1';

  const syncIdentifier = () => {
    const isPrice = ($('#ipe-field')?.value === 'price');
    const g = $('#ipe-gid-wrap'), s = $('#ipe-sku-wrap');
    if (g) g.style.display = isPrice ? 'none' : '';
    if (s) s.style.display = isPrice ? '' : 'none';
  };
  $('#ipe-field')?.addEventListener('change', syncIdentifier);
  syncIdentifier();

  // Vet build #9: pasted-GID input → search-as-you-type picker over the
  // same endpoint the warehouse label picker uses. Picking a row stores
  // the product GID in the hidden #ipe-gid the submit handler already
  // reads. addEventListener-bound (repo law: no inline on*= handlers).
  const psInput = $('#ipe-prodsearch');
  const psBox = $('#ipe-prodresults');
  let psTimer = null;
  const psHide = () => { if (psBox) { psBox.hidden = true; psBox.innerHTML = ''; } };
  const psRender = (items) => {
    if (!psBox) return;
    if (!items.length) {
      psBox.innerHTML = '<div class="meta" style="padding:6px 8px;">No matches.</div>';
      psBox.hidden = false;
      return;
    }
    psBox.innerHTML = items.slice(0, 8).map((it) => {
      const nm = [it.product_title, it.variant_title].filter(Boolean).join(' · ') || it.sku;
      return `<button type="button" class="ipe-prodrow" data-gid="${esc(it.product_gid || '')}"
        data-name="${esc(nm)}"
        style="display:block;width:100%;text-align:left;padding:6px 8px;background:none;border:0;border-bottom:1px solid var(--border);cursor:pointer;">
        ${esc(nm)} <span class="meta">${esc(it.sku || '')}</span>
      </button>`;
    }).join('');
    psBox.hidden = false;
    psBox.querySelectorAll('.ipe-prodrow').forEach((b) => b.addEventListener('click', () => {
      const gid = $('#ipe-gid');
      if (gid) gid.value = b.dataset.gid || '';
      if (psInput) psInput.value = b.dataset.name || '';
      psHide();
    }));
  };
  psInput?.addEventListener('input', () => {
    const gid = $('#ipe-gid');
    if (gid) gid.value = '';   // typing again un-picks — the GID must match what's shown
    clearTimeout(psTimer);
    const q = psInput.value.trim();
    if (q.length < 2) { psHide(); return; }
    psTimer = setTimeout(async () => {
      let r;
      try { r = await api(`/api/warehouse/product-search?q=${encodeURIComponent(q)}`); }
      catch { r = null; }
      psRender(((r && r.results) || []).filter((it) => it.product_gid));
    }, 300);
  });

  const msg = (t) => { const m = $('#ipe-msg'); if (m) m.textContent = t || ''; };

  $('#ipe-submit')?.addEventListener('click', async () => {
    const field = $('#ipe-field')?.value || 'title';
    const isPrice = field === 'price';
    const gid = $('#ipe-gid')?.value.trim() || '';
    const sku = $('#ipe-sku')?.value.trim() || '';
    const newValue = $('#ipe-newval')?.value.trim() || '';
    const reason = $('#ipe-reason')?.value.trim() || '';
    if (!newValue) { msg('Enter the new value first.'); return; }
    if (isPrice && !sku) { msg('Price edits need a SKU.'); return; }
    if (!isPrice && !gid) { msg('Pick a product from the search first.'); return; }
    // product_id path-param is the product GID; for price we pass a
    // placeholder ('price') since the route reads sku from the body.
    const productId = isPrice ? (gid || 'price') : gid;
    msg('Sending…');
    $('#ipe-submit').disabled = true;
    try {
      const res = await api(
        `/api/inventory/products/${encodeURIComponent(productId)}/propose-edit`, {
          method: 'POST',
          body: JSON.stringify({
            field_name: field,
            new_value: newValue,
            sku: isPrice ? sku : null,
            product_gid: isPrice ? null : gid,
            reason: reason || null,
          }),
        });
      if (res && res.ok) {
        const pid = res.proposal_id ? String(res.proposal_id).slice(0, 8) : '';
        msg(`Sent for approval${pid ? ` · proposal ${pid}` : ''}`);
        $('#ipe-newval').value = '';
        $('#ipe-reason').value = '';
        showToast('Edit sent to the inventory manager for approval.');
      } else {
        msg((res && res.error) || 'Could not send. Try again.');
      }
    } catch (e) {
      msg(e.message || 'Could not send. Try again.');
    } finally {
      $('#ipe-submit').disabled = false;
    }
  });
}

async function loadPendingProductEdits() {
  const host = $('#inv-pending-edits-host');
  if (!host) return;
  host.innerHTML = `
    <div class="section-h">
      <h2>Pending product edits</h2>
      <span class="meta">Assistant-proposed edits awaiting your approval</span>
    </div>
    <div id="inv-pending-edits"><div class="card" style="height:48px;opacity:0.4;"></div></div>`;
  let res;
  try {
    res = await api('/api/inventory/products/proposals?status=pending&limit=100');
  } catch (e) {
    $('#inv-pending-edits').innerHTML =
      `<div class="card" style="padding:14px;font-size:13px;color:var(--ink-soft);">Could not load pending edits.</div>`;
    return;
  }
  _renderPendingProductEdits((res && res.proposals) || []);
}

function _renderPendingProductEdits(items) {
  const host = $('#inv-pending-edits');
  if (!host) return;
  if (!items.length) {
    host.innerHTML =
      `<div class="card" style="padding:14px;font-size:13px;color:var(--ink-soft);">No pending product edits.</div>`;
    return;
  }
  const rows = items.map((p) => {
    const id = esc(String(p.id || ''));
    const label = esc(p.sku || p.product_gid || 'product');
    const oldV = (p.old_value == null || p.old_value === '') ? '—' : esc(String(p.old_value));
    const newV = esc(String(p.new_value == null ? '' : p.new_value));
    const reason = p.reason ? `<div class="ipe-reason-line">${esc(String(p.reason))}</div>` : '';
    return `<div class="ipe-row card" data-pid="${id}">
      <div class="ipe-row-main">
        <div class="ipe-row-top">
          <span class="ipe-field-chip">${esc(p.field_name || '')}</span>
          <span class="ipe-row-label">${label}</span>
        </div>
        <div class="ipe-change"><span class="ipe-old">${oldV}</span><span class="ipe-arrow"> → </span><span class="ipe-new">${newV}</span></div>
        ${reason}
      </div>
      <div class="ipe-row-actions">
        <button class="btn-primary ipe-approve" data-pid="${id}" type="button">Approve</button>
        <button class="btn-ghost ipe-reject" data-pid="${id}" type="button">Reject</button>
        <span class="meta ipe-row-msg"></span>
      </div>
    </div>`;
  }).join('');
  host.innerHTML = rows;

  host.querySelectorAll('.ipe-approve').forEach((btn) => {
    btn.addEventListener('click', () => _decideProductEdit(btn, 'approve'));
  });
  host.querySelectorAll('.ipe-reject').forEach((btn) => {
    btn.addEventListener('click', () => _decideProductEdit(btn, 'reject'));
  });
}

async function _decideProductEdit(btn, action) {
  const pid = btn.dataset.pid;
  if (!pid) return;
  const row = btn.closest('.ipe-row');
  const msgEl = row ? row.querySelector('.ipe-row-msg') : null;
  const setMsg = (t) => { if (msgEl) msgEl.textContent = t || ''; };
  let reason = null;
  if (action === 'reject') {
    reason = prompt('Reason for rejecting this edit? (optional)') || null;
  }
  row?.querySelectorAll('button').forEach((b) => { b.disabled = true; });
  setMsg(action === 'approve' ? 'Approving…' : 'Rejecting…');
  // approve-edit writes to Shopify; reject-edit just closes the task.
  const path = action === 'approve'
    ? `/api/inventory/products/${encodeURIComponent(pid)}/approve-edit`
    : `/api/inventory/products/${encodeURIComponent(pid)}/reject-edit`;
  const body = action === 'approve'
    ? { proposal_id: pid }
    : { proposal_id: pid, reason };
  try {
    const res = await api(path, { method: 'POST', body: JSON.stringify(body) });
    if (res && res.ok) {
      showToast(action === 'approve'
        ? 'Approved — change written to Shopify.'
        : 'Edit rejected.');
      loadPendingProductEdits();
    } else {
      setMsg((res && res.error) || 'Failed. Try again.');
      row?.querySelectorAll('button').forEach((b) => { b.disabled = false; });
    }
  } catch (e) {
    setMsg(e.message || 'Failed. Try again.');
    row?.querySelectorAll('button').forEach((b) => { b.disabled = false; });
  }
}
// ── INVENTORY PRODUCT-EDIT APPROVAL END (BATCH B) ────────────────────

// ── WO-6 — Buying calendar (gap #4) ──────────────────────────────────
// Pure presentation over the already-computed reorder_date. Renders each
// upcoming reorder date as a row: SKU count, total reorder cost, and a
// China / CRUX-Custom split chip. No new math client-side.
async function _bindBuyingCalendar() {
  const host = $('#inv-buying-calendar');
  if (!host) return;
  const res = await api('/api/inventory/reports/buying-calendar?horizon_days=60')
    .catch(() => null);
  const rows = res && res.ok ? (res.rows || []) : [];
  if (!rows.length) {
    host.innerHTML = `<div class="card" style="padding:14px;font-size:13px;color:var(--ink-soft);">No reorders due in the next 60 days.</div>`;
    return;
  }
  const tot = (res.meta && res.meta.totals) || {};
  const head = `<div class="card" style="padding:10px 14px;font-size:12px;color:var(--ink-soft);">
    ${rows.length} reorder day(s) · ₦${_widgetCompact(Number(tot.reorder_cost || 0))} total ·
    ${tot.crux_custom_count || 0} CRUX Custom / ${tot.china_count || 0} sourced</div>`;
  const body = rows.map(d => `
    <div class="card" style="padding:10px 14px;display:flex;justify-content:space-between;align-items:center;gap:12px;">
      <div>
        <div style="font-weight:600;">${esc(d.date)}</div>
        <div style="font-size:12px;color:var(--ink-soft);">${d.sku_count} SKU(s) ·
          <span title="CRUX Custom">${d.crux_custom_count} custom</span> /
          <span title="Sourced / import">${d.china_count} sourced</span></div>
      </div>
      <div style="text-align:right;font-family:var(--mono);">₦${_widgetCompact(Number(d.total_reorder_cost || 0))}</div>
    </div>`).join('');
  host.innerHTML = head + body;
}

// ── WO-6 — Saved report views (gap #1) ───────────────────────────────
// List saved views, run one (CSV download), and delete. "Save current
// report as…" prompts for a name + report key from the known list. The
// intelligence is server-side — this is a thin list + actions.
async function _bindSavedReports() {
  const host = $('#inv-saved-reports');
  if (!host) return;
  const res = await api('/api/inventory/reports/saved').catch(() => null);
  if (!res || !res.ok) {
    host.innerHTML = `<div class="card" style="padding:14px;font-size:13px;color:var(--ink-soft);">Saved views unavailable.</div>`;
    return;
  }
  const known = res.known_reports || [];
  const saved = res.saved || [];
  const list = saved.length
    ? saved.map(s => `
        <div class="card" data-saved-id="${esc(s.id)}" style="padding:10px 14px;display:flex;justify-content:space-between;align-items:center;gap:12px;">
          <div>
            <div style="font-weight:600;">${esc(s.name)}</div>
            <div style="font-size:12px;color:var(--ink-soft);">${esc(s.report_key)}${s.schedule && s.schedule.enabled ? ' · scheduled' : ''}${s.schedule && s.schedule.dry_run !== false ? ' (dry-run)' : ''}</div>
          </div>
          <div style="display:flex;gap:8px;">
            <button class="btn-ghost" type="button" onclick="window.open('/api/inventory/reports/saved/${sosEsc(sosJs(s.id))}/data?format=csv','_blank')">CSV</button>
            <button class="btn-danger" style="font-size:12px;padding:4px 10px;" type="button" onclick="_deleteSavedReport('${sosEsc(sosJs(s.id))}')">Delete</button>
          </div>
        </div>`).join('')
    : `<div class="card" style="padding:14px;font-size:13px;color:var(--ink-soft);">No saved views yet.</div>`;
  host.innerHTML = list +
    `<div style="margin-top:8px;"><button class="btn-ghost" type="button"
      onclick='_createSavedReport(${JSON.stringify(known)})'>+ Save a report view…</button></div>`;
}

async function _createSavedReport(known) {
  const key = prompt('Report to save (one of: ' + (known || []).join(', ') + ')', (known && known[0]) || 'reorder');
  if (!key) return;
  const name = prompt('Name this view', key);
  if (!name) return;
  const res = await api('/api/inventory/reports/saved', {
    method: 'POST',
    body: JSON.stringify({ name, report_key: key, config: {} }),
  }).catch(e => ({ ok: false, error: String(e) }));
  if (!res || !res.ok) { showToast('Could not save: ' + ((res && res.error) || 'error')); return; }
  _bindSavedReports();
}

async function _deleteSavedReport(id) {
  if (!confirm('Delete this saved view?')) return;
  await api('/api/inventory/reports/saved/' + id, { method: 'DELETE' }).catch(() => null);
  _bindSavedReports();
}

// WO-5: wire the bulk price editor (preview → apply → rollback). Pricing is
// an inventory decision; preview is dry-run (writes nothing), a live apply is
// confirmed before any price flips. Auto-revert + rollback restore-only-if-
// the-live-price-is-unchanged (the CAS guard lives server-side in price_ops).
function bindReprice() {
  const card = $('#reprice-card');
  if (!card || card.dataset.bound) return;
  card.dataset.bound = '1';
  let _lastPreview = null;

  // Show the value vs target-margin input per rule kind.
  const syncRuleInputs = () => {
    const kind = $('#rp-kind')?.value || 'margin';
    const isMargin = kind === 'margin';
    const vw = $('#rp-value-wrap'), mw = $('#rp-margin-wrap');
    if (vw) vw.style.display = isMargin ? 'none' : '';
    if (mw) mw.style.display = isMargin ? '' : 'none';
  };
  $('#rp-kind')?.addEventListener('change', () => { syncRuleInputs(); resetApply(); });
  syncRuleInputs();

  const resetApply = () => {
    const ab = $('#rp-apply-btn');
    if (ab) ab.disabled = true;
    _lastPreview = null;
  };
  ['rp-vendor','rp-collection','rp-tag','rp-status','rp-value','rp-margin','rp-rounding','rp-field','rp-revert']
    .forEach(id => $('#' + id)?.addEventListener('input', resetApply));

  const buildFilter = () => ({
    vendor: $('#rp-vendor')?.value.trim() || null,
    collection: $('#rp-collection')?.value.trim() || null,
    tag: $('#rp-tag')?.value.trim() || null,
    status: $('#rp-status')?.value.trim() || null,
  });
  const buildRule = () => {
    const kind = $('#rp-kind')?.value || 'margin';
    const rounding = $('#rp-rounding')?.value || 'none';
    const rule = { kind, rounding, field: $('#rp-field')?.value || 'price' };
    if (kind === 'margin') {
      const pct = parseFloat($('#rp-margin')?.value);
      // UI takes a whole percent (65); engine wants a fraction (0.65).
      rule.target_margin = isFinite(pct) ? pct / 100 : null;
    } else {
      const v = parseFloat($('#rp-value')?.value);
      rule.value = isFinite(v) ? v : null;
    }
    return rule;
  };
  const revertAtIso = () => {
    const raw = $('#rp-revert')?.value;
    return raw ? new Date(raw).toISOString() : null;
  };

  const msg = (t) => { const m = $('#rp-status-msg'); if (m) m.textContent = t || ''; };

  $('#rp-preview-btn')?.addEventListener('click', async () => {
    msg('Previewing…');
    resetApply();
    try {
      const res = await api('/api/inventory/reprice/preview', {
        method: 'POST',
        body: JSON.stringify({ filter: buildFilter(), rule: buildRule() }),
      });
      if (!res.ok) { msg(res.error || 'Preview failed'); $('#rp-preview').innerHTML = ''; return; }
      _lastPreview = res;
      $('#rp-preview').innerHTML = _renderRepricePreview(res);
      msg(`${res.changing} changing · ${res.skipped} skipped · ${res.resolved} resolved`);
      const ab = $('#rp-apply-btn');
      if (ab) ab.disabled = (res.changing || 0) === 0;
    } catch (e) {
      msg(e.message || 'Preview failed');
    }
  });

  $('#rp-apply-btn')?.addEventListener('click', async () => {
    if (!_lastPreview) return;
    const revertAt = revertAtIso();
    const note = revertAt ? `\n\nThis is a scheduled sale — prices auto-revert at ${revertAt}.` : '';
    if (!confirm(`Apply this rule to ${_lastPreview.changing} variant(s) live on Shopify?${note}`)) return;
    msg('Applying…');
    try {
      const res = await api('/api/inventory/reprice/apply', {
        method: 'POST',
        body: JSON.stringify({
          filter: buildFilter(), rule: buildRule(),
          dry_run: false, scheduled_revert_at: revertAt,
        }),
      });
      if (!res.ok && res.applied == null) { msg(res.error || 'Apply failed'); return; }
      msg(`Applied ${res.applied} · failed ${res.failed} · skipped ${res.skipped}`
          + (res.job_id ? ` · job ${res.job_id.slice(0, 8)}` : ''));
      resetApply();
      loadRepriceSnapshots();
    } catch (e) {
      msg(e.message || 'Apply failed');
    }
  });

  loadRepriceSnapshots();
}

function _renderRepricePreview(res) {
  if (!res.items || !res.items.length) {
    return `<div class="card" style="padding:14px;font-size:13px;color:var(--ink-soft);">No variants matched this filter.</div>`;
  }
  const naira = (n) => (n == null ? '—' : '₦' + _widgetCompact(Number(n)));
  const rows = res.items.slice(0, 100).map(i => {
    const skip = i.skipped
      ? `<span style="color:var(--ink-soft);">skip: ${esc(i.skip_reason || '')}</span>` : '';
    return `<tr style="${i.skipped ? 'opacity:0.45;' : ''}">
      <td style="padding:6px 8px;font-size:12px;font-family:var(--mono);">${esc(i.sku || '—')}</td>
      <td style="padding:6px 8px;font-size:12px;">${esc((i.product_title || '').slice(0, 28))}</td>
      <td style="padding:6px 8px;font-size:12px;font-family:var(--mono);">${naira(i.old_price)}</td>
      <td style="padding:6px 8px;font-size:12px;font-family:var(--mono);">${i.skipped ? skip : naira(i.new_price)}</td>
      <td style="padding:6px 8px;font-size:12px;font-family:var(--mono);">${naira(i.unit_cost_ngn)}</td>
    </tr>`;
  }).join('');
  const more = res.items.length > 100 ? `<div class="meta" style="padding:6px 8px;">+${res.items.length - 100} more…</div>` : '';
  return `<div class="card" style="padding:4px;">
    <table style="width:100%;border-collapse:collapse;">
      <thead><tr>
        ${['SKU','Product','Was','Now','Cost'].map(h => `<th style="text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:var(--ink-soft);padding:6px 8px;">${h}</th>`).join('')}
      </tr></thead><tbody>${rows}</tbody></table>${more}</div>`;
}

async function loadRepriceSnapshots() {
  const host = $('#rp-snapshots');
  if (!host) return;
  let res;
  try {
    res = await api('/api/inventory/reprice/snapshots?limit=100');
  } catch (e) {
    host.innerHTML = `<div class="card" style="padding:14px;font-size:13px;color:var(--ink-soft);">Could not load reprice runs.</div>`;
    return;
  }
  const items = (res && res.ok && res.items) || [];
  if (!items.length) {
    host.innerHTML = `<div class="card" style="padding:14px;font-size:13px;color:var(--ink-soft);">No reprice runs yet. Preview a rule above, then Apply.</div>`;
    return;
  }
  // Group by job_id so each run is one rollback-able card.
  const jobs = {};
  items.forEach(i => {
    const j = jobs[i.job_id] || (jobs[i.job_id] = {
      job_id: i.job_id, n: 0, reverted: 0, stale: 0,
      applied_at: i.applied_at, actor: i.actor,
      scheduled_revert_at: i.scheduled_revert_at,
    });
    j.n++;
    if (i.status === 'reverted') j.reverted++;
    if (i.status === 'skipped_stale') j.stale++;
  });
  const naira = (n) => (n == null ? '—' : '₦' + _widgetCompact(Number(n)));
  const rows = Object.values(jobs).map(j => {
    const live = j.n - j.reverted - j.stale;
    const when = j.applied_at ? new Date(j.applied_at).toLocaleString() : '—';
    const sched = j.scheduled_revert_at
      ? ` · auto-revert ${new Date(j.scheduled_revert_at).toLocaleString()}` : '';
    const canRoll = live > 0;
    return `<tr>
      <td style="padding:6px 8px;font-size:12px;font-family:var(--mono);">${esc((j.job_id || '').slice(0, 8))}</td>
      <td style="padding:6px 8px;font-size:12px;">${esc(when)}${sched}</td>
      <td style="padding:6px 8px;font-size:12px;">${esc(j.actor || '')}</td>
      <td style="padding:6px 8px;font-size:12px;font-family:var(--mono);">${j.n} (${live} live · ${j.reverted} reverted · ${j.stale} stale)</td>
      <td style="padding:6px 8px;">${canRoll
        ? `<button class="btn-ghost" type="button" onclick="repriceRollback('${sosEsc(sosJs(j.job_id))}')">Rollback</button>`
        : '<span class="meta">—</span>'}</td>
    </tr>`;
  }).join('');
  host.innerHTML = `<div class="card" style="padding:4px;">
    <table style="width:100%;border-collapse:collapse;">
      <thead><tr>
        ${['Job','When','By','Variants','&nbsp;'].map(h => `<th style="text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;color:var(--ink-soft);padding:6px 8px;">${h}</th>`).join('')}
      </tr></thead><tbody>${rows}</tbody></table></div>`;
}

async function repriceRollback(jobId) {
  if (!confirm('Roll back this reprice run? Variants changed since (drifted) are skipped, never clobbered.')) return;
  try {
    const res = await api('/api/inventory/reprice/rollback', {
      method: 'POST', body: JSON.stringify({ job_id: jobId }),
    });
    const m = $('#rp-status-msg');
    if (m) m.textContent = res.ok
      ? `Rolled back ${res.reverted} · skipped_stale ${res.skipped_stale} · failed ${res.failed}`
      : (res.error || 'Rollback failed');
  } catch (e) {
    const m = $('#rp-status-msg');
    if (m) m.textContent = e.message || 'Rollback failed';
  }
  loadRepriceSnapshots();
}

// Small generic list-renderer used by every Inventory section.
function _renderInventoryList(rows, cols, emptyMsg) {
  if (!rows || rows.length === 0) {
    return `<div class="card" style="padding: 14px; font-size: 13px; color: var(--ink-soft);">${esc(emptyMsg)}</div>`;
  }
  const head = cols.map(c => `<th style="text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--ink-soft); padding: 6px 8px;">${esc(c.label)}</th>`).join('');
  const body = rows.map(r => `<tr>${cols.map(c => `<td style="padding: 6px 8px; font-size: 13px; font-family: ${c.class === 'mono' ? 'var(--mono)' : 'inherit'};">${_fmtInventoryCell(r[c.key], c.fmt)}</td>`).join('')}</tr>`).join('');
  return `<div class="card" style="padding: 4px;"><table style="width: 100%; border-collapse: collapse;"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`;
}

function _fmtInventoryCell(v, fmt) {
  if (v == null || v === '') return '—';
  if (fmt === 'naira') return '₦' + _widgetCompact(Number(v));
  if (fmt === 'pct') return Number(v).toFixed(1) + '%';
  if (fmt === 'days_of_cover') {
    const n = Number(v);
    const cls = n <= 7 ? 'color: var(--danger, #ef4444);' : '';
    return `<span style="${cls}">${n.toFixed(1)}d</span>`;
  }
  return esc(String(v));
}

// PR-INV: Days-of-cover heatmap. Each cell coloured by urgency:
//   null  → blue (has stock, no recent sales — "review")
//   0     → red (out of stock)
//   <7    → red
//   7-14  → orange
//   14-30 → yellow
//   >30   → green
function _renderInventoryHeatmap(payload) {
  if (!payload || !payload.ok || !payload.rows || payload.rows.length === 0) {
    return `<div class="card" style="padding: 14px; font-size: 13px; color: var(--ink-soft);">No movement data yet for any SKU.</div>`;
  }
  const locs = payload.locations || [];
  const head = `<tr>
    <th style="text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--ink-soft); padding: 6px 8px;">SKU</th>
    <th style="text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--ink-soft); padding: 6px 8px;">Product</th>
    ${locs.map(l => `<th style="text-align: center; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--ink-soft); padding: 6px 8px;">${esc(l)}</th>`).join('')}
  </tr>`;
  const body = payload.rows.map(r => {
    const cells = locs.map(l => {
      const v = r.by_location?.[l];
      return `<td style="padding: 6px 8px; text-align: center; font-family: var(--mono); font-size: 12px;">${_heatmapCell(v)}</td>`;
    }).join('');
    return `<tr>
      <td style="padding: 6px 8px; font-family: var(--mono); font-size: 12px;">${esc(r.sku || '—')}</td>
      <td style="padding: 6px 8px; font-size: 13px;">${esc(r.product_title || '—')}</td>
      ${cells}
    </tr>`;
  }).join('');
  return `
    <div class="card" style="padding: 4px; overflow-x: auto;">
      <table style="width: 100%; border-collapse: collapse;">
        <thead>${head}</thead>
        <tbody>${body}</tbody>
      </table>
      <div style="padding: 6px 10px; font-size: 11px; color: var(--ink-soft);">
        Red ≤7d · Orange ≤14d · Yellow ≤30d · Green &gt;30d · Blue = no sales
      </div>
    </div>`;
}

function _heatmapCell(v) {
  if (v == null) {
    return `<span style="display:inline-block; min-width:42px; padding:2px 6px; background:rgba(59,130,246,0.15); color:#3b82f6; border-radius:3px;">—</span>`;
  }
  const n = Number(v);
  let bg, fg;
  if (n <= 0)       { bg = 'rgba(239,68,68,0.18)';  fg = '#ef4444'; }
  else if (n < 7)   { bg = 'rgba(239,68,68,0.15)';  fg = '#ef4444'; }
  else if (n < 14)  { bg = 'rgba(249,115,22,0.15)'; fg = '#f97316'; }
  else if (n < 30)  { bg = 'rgba(234,179,8,0.15)';  fg = '#ca8a04'; }
  else              { bg = 'rgba(34,197,94,0.15)';  fg = '#16a34a'; }
  return `<span style="display:inline-block; min-width:42px; padding:2px 6px; background:${bg}; color:${fg}; border-radius:3px;">${n.toFixed(1)}d</span>`;
}

// PR-INV: combined inventory trend tile — sparkline of value at cost,
// inventory-to-cash ratio, turnover ratio. All three answer "is
// capital tied up productively?"
function _renderInventoryTrend(payload) {
  if (!payload || !payload.ok) {
    return `<div class="card" style="padding: 14px; font-size: 13px; color: var(--ink-soft);">Inventory trend unavailable.</div>`;
  }
  const series = payload.series || [];
  const spark = _inventorySparkline(series);
  const v = payload.current_value_ngn;
  const c = payload.current_cash_ngn;
  // ⛔ never `c || 0` (fifth read of #3443): inventory_trend returns a null
  // cash whenever the position is not confirmed, and `|| 0` turned that into
  // ₦0 BEFORE _widgetCompact could guard it — so the tile read '₦447.0M / ₦0'
  // beside an em-dash ratio, the exact fabricated zero this work removes.
  const cashKnown = c != null && Number.isFinite(Number(c));
  const valKnown = v != null && Number.isFinite(Number(v));
  const cashTxt = cashKnown ? '₦' + _widgetCompact(c) : '—';
  const valTxt = valKnown ? '₦' + _widgetCompact(v) : '—';
  const ratio = payload.inventory_to_cash_ratio;
  const turn = payload.turnover_ratio;
  const turnInterp = payload.turnover_interpretation;

  // Ratio interpretation — operator's mental model: above 1.5 is
  // capital-stuck; below 0.5 is under-stocked.
  let ratioColor = 'var(--ink)';
  let ratioNote = '';
  if (ratio != null) {
    if (ratio > 1.5)      { ratioColor = '#ef4444'; ratioNote = 'Capital stuck — too much inventory relative to cash'; }
    else if (ratio < 0.5) { ratioColor = '#f97316'; ratioNote = 'Under-stocked relative to cash on hand'; }
    else                  { ratioColor = '#16a34a'; ratioNote = 'Healthy balance between inventory and cash'; }
  }

  return `
    <div class="card" style="padding: 14px;">
      <div style="display: flex; flex-wrap: wrap; gap: 16px; align-items: center; justify-content: space-between;">
        <div style="flex: 1 1 280px; min-width: 200px;">
          ${spark}
        </div>
        <div style="display: flex; gap: 24px; flex-wrap: wrap;">
          <div>
            <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--ink-soft);">Inventory / cash</div>
            <div style="font-size: 22px; font-family: var(--serif, inherit); color: ${ratioColor};">${ratio != null ? ratio.toFixed(2) + '×' : '—'}</div>
            <div style="font-size: 11px; color: var(--ink-soft);">${esc(ratioNote)}</div>
          </div>
          <div>
            <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--ink-soft);">Turnover (annualised)</div>
            <div style="font-size: 22px; font-family: var(--serif, inherit);">${turn != null ? turn.toFixed(2) + '×' : '—'}</div>
            <div style="font-size: 11px; color: var(--ink-soft);">${esc(turnInterp || '')}</div>
          </div>
          <div>
            <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--ink-soft);">Inventory / cash today</div>
            <div style="font-size: 13px;">${valTxt} / ${cashTxt}</div>
            ${cashKnown ? '' : `<div style="font-size: 11px; color: var(--ink-soft);">Cash not counted yet</div>`}
          </div>
        </div>
      </div>
    </div>`;
}

function _inventorySparkline(series) {
  if (!series || series.length < 2) {
    return `<div style="font-size: 12px; color: var(--ink-soft);">Need ≥2 daily snapshots for trend (run /api/inventory/costs/snapshot).</div>`;
  }
  const vals = series.map(s => Number(s.value_at_cost_ngn || 0));
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = (max - min) || 1;
  const W = 280, H = 60;
  const dx = W / (vals.length - 1);
  const points = vals.map((v, i) => {
    const x = (i * dx).toFixed(1);
    const y = (H - ((v - min) / range) * H).toFixed(1);
    return `${x},${y}`;
  }).join(' ');
  const last = vals[vals.length - 1];
  const first = vals[0];
  const pctDelta = first > 0 ? ((last - first) / first * 100) : 0;
  // Theme-aware: rising inventory cost = caution (danger), falling = ok.
  const trendColor = pctDelta > 5 ? 'var(--danger)' : pctDelta < -5 ? 'var(--ok)' : 'var(--ink-soft)';
  const arrow = pctDelta > 0 ? '↑' : pctDelta < 0 ? '↓' : '→';
  // Responsive: viewBox + width:100% so it fills the card on any screen;
  // non-scaling-stroke keeps the line crisp at any width.
  return `
    <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--ink-soft);">Inventory at cost · ${series.length}d</div>
    <svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" preserveAspectRatio="none" style="display: block; margin-top: 4px;" role="img" aria-label="Inventory-at-cost trend over ${series.length} days, ${pctDelta >= 0 ? 'up' : 'down'} ${Math.abs(pctDelta).toFixed(1)} percent">
      <polyline points="${points}" fill="none" stroke="${trendColor}" stroke-width="1.5" vector-effect="non-scaling-stroke"/>
    </svg>
    <div style="font-size: 11px; color: ${trendColor};">${arrow} ${Math.abs(pctDelta).toFixed(1)}% over window · current ₦${_widgetCompact(last)}</div>
  `;
}


// ──────────────────────────────────────────────────────────
// Recommendations (v3.0p18 9kk + 9ll)
//
// Six-section view (Inventory / Finance / Ads / Marketing /
// Expansion / Projections — last two empty in v1 since their
// scanners are deferred to v3.0p21). Two-zone layout per section:
// Pinned (operator-curated, persistent) and Surfaced (active anomalies).
// Each card carries Observation + Hypothesis + Action Ladder + the
// lifecycle buttons (Acted on / Snooze / Pin / Investigate).

const REC_SECTIONS = [
  { id: 'inventory',   label: 'Inventory'   },
  { id: 'finance',     label: 'Finance'     },
  { id: 'ads',         label: 'Ads'         },
  { id: 'marketing',   label: 'Marketing'   },
  { id: 'locations',   label: 'Locations'   },  // 2026-06-11: was MISSING — 10 location recs were generated but never rendered
  { id: 'expansion',   label: 'Expansion'   },
  { id: 'projections', label: 'Projections' },
  // 2026-08-16: same miss as 'locations' above, two years of ops cards
  // deep. Added to the section CHECK by mig 410 and never given a tab —
  // grouping below is `if (grouped[r.section])`, so every operations card
  // (self_spec's build directives, ops_pulse_scanners' eight emitters) was
  // dropped on the floor with no catch-all bucket. It is also where the
  // create_recommendation clamp now sends any unrecognised section, so a
  // blind tab here would turn a loud constraint bounce into a silent one.
  { id: 'operations',  label: 'Operations'  },
];

// Severity filter for the recommendations page (CRUX runs ~96 open; a flat
// list is hard to triage). null = show all.
const REC_SEV_FILTERS = [
  { id: null,        label: 'All'      },
  { id: 'critical',  label: 'Critical' },
  { id: 'warn',      label: 'Warn'     },
  { id: 'info',      label: 'Info'     },
];
// How many surfaced cards to render per section before a "Show all" expander.
const REC_SECTION_CAP = 12;
// How many to FETCH per section — matched to the backend's own clamp
// (recommendations.py: min(limit, 200)) so no section is shallower than the
// single unsectioned pull it replaces. An earlier 60 here quietly cost
// Inventory its depth: that pull returned ~196 inventory rows, and inventory
// holds 1,299 open cards (live 2026-08-16), so 60 would have been about the
// last ten hours and the header would have read a flat "60 open" forever.
// Total payload barely moves — every other section holds far less than 200
// (marketing 49, ads 13, locations 7, finance 2, operations 2), so this is
// the old 200 rows plus the ones that were being starved, not 8x anything.
const REC_PER_SECTION = 200;

const SEVERITY_BADGE = {
  critical: { color: '#ef4444', label: 'CRITICAL' },
  warn:     { color: '#f59e0b', label: 'WARN'     },
  info:     { color: '#9ca3af', label: 'INFO'     },
};

const URGENCY_LABEL = {
  immediate:  'Act today',
  this_week:  'This week',
  this_month: 'This month',
  strategic:  'Strategic',
};

function _setRecSectionExpanded(sec, expanded) {
  sec.dataset.expanded = expanded ? '1' : '0';
  const body = sec.querySelector('[data-body]');
  const chev = sec.querySelector('[data-chevron]');
  if (body) body.style.display = expanded ? 'block' : 'none';
  if (chev) chev.style.transform = expanded ? 'rotate(90deg)' : 'rotate(0deg)';
}

function renderRecommendations() {
  return `
    <div class="section-h">
      <h2>Recommendations</h2>
      <button class="btn-ghost" id="rec-refresh-btn" type="button">Refresh</button>
    </div>
    <div style="margin: -8px 0 12px 0; font-size: 13px; color: var(--ink-soft);">
      What EON is flagging for action. Tap Investigate on any card to dig in.
    </div>
    <div class="rec-filter-bar" id="rec-filter-bar">
      ${REC_SEV_FILTERS.map(f => `
        <button class="chip rec-sev-chip ${(State.recFilter || null) === f.id ? 'chip-active' : ''}"
                data-sev-filter="${f.id == null ? '' : f.id}" type="button">${esc(f.label)}</button>
      `).join('')}
    </div>
    <div id="rec-sections">
      ${REC_SECTIONS.map(s => `
        <div class="rec-section" data-section="${s.id}" data-expanded="0">
          <button class="section-h rec-section-toggle" type="button"
                  style="width:100%;background:none;border:0;padding:0;text-align:left;cursor:pointer;display:flex;align-items:center;gap:8px;">
            <h2 style="flex:1;">${esc(s.label)}</h2>
            <span class="meta" data-counts>—</span>
            <span data-chevron style="font-size:14px;color:var(--ink-soft);transition:transform 120ms;">›</span>
          </button>
          <div data-body style="display:none;">
            <div data-pinned class="rec-zone-pinned"></div>
            <div data-surfaced class="rec-zone-surfaced"></div>
          </div>
        </div>
      `).join('')}
    </div>
  `;
}

async function bindRecommendations() {
  // Which sections failed their last fetch. Function-scoped, NOT local to
  // refresh(), because the severity chips below rewrite the very same
  // [data-counts] text and would otherwise overwrite "Couldn't load" with
  // "Clear" on the first tap — putting back the exact lie the per-section
  // failure state was added to remove, and for a section holding a pinned
  // card the header is the ONLY failure signal, since the body message is
  // suppressed there.
  const failedSections = new Set();

  const refresh = async () => {
    // Pull active PER SECTION + pinned, in parallel.
    //
    // One unsectioned call could not fill these tabs: get_active_recommendations
    // clamps to the 200 most-recently-surfaced rows ACROSS ALL SECTIONS, and at
    // CRUX's volume (987 recs surfaced in the last 7 days) inventory takes the
    // whole window — measured live 2026-08-16, that 200 was 196 inventory,
    // 2 ads, 2 marketing and ZERO operations, while two operations cards sat
    // open. Every quiet section rendered "Clear" because nothing looked past
    // row 200. Per-section fetches give each tab its own top-N, so a loud
    // section can never starve a quiet one.
    // A fetch that does not come back must NOT read as a quiet section.
    // Eight calls means eight independent chances to fail, and an empty
    // list renders as "Clear" — one blank tab among seven full ones looks
    // like good news, where the old single call failing blanked everything
    // at once and obviously looked broken. Note get_active_recommendations
    // swallows its own DB errors and answers HTTP 200 with
    // {ok: false, items: []}, so a 200 is not proof of an answer: check ok.
    const [pinnedRes, ...sectionRes] = await Promise.all([
      // The ninth call, and it swallows errors the same way: get_pinned
      // answers HTTP 200 with {ok: false, items: []} on a query error, so
      // .catch() never fires and a failure reads as "nothing is pinned".
      api('/api/recommendations/pinned')
        .then(r => (r && r.ok !== false && Array.isArray(r.items))
          ? r : { failed: true, items: [] })
        .catch(() => ({ failed: true, items: [] })),
      ...REC_SECTIONS.map(s =>
        api(`/api/recommendations?section=${encodeURIComponent(s.id)}&limit=${REC_PER_SECTION}`)
          .then(r => (r && r.ok !== false && Array.isArray(r.items))
            ? { id: s.id, items: r.items }
            : { id: s.id, failed: true })
          .catch(() => ({ id: s.id, failed: true }))),
    ]);
    failedSections.clear();
    sectionRes.filter(r => r.failed).forEach(r => failedSections.add(r.id));
    const active = sectionRes.flatMap(r => r.items || []);
    const pinned = (pinnedRes && pinnedRes.items) || [];
    const pinnedFailed = !!(pinnedRes && pinnedRes.failed);

    // Group by section.
    const grouped = {};
    REC_SECTIONS.forEach(s => grouped[s.id] = { pinned: [], surfaced: [] });
    pinned.forEach(r => {
      if (grouped[r.section]) grouped[r.section].pinned.push(r);
    });
    active.forEach(r => {
      // Don't double-show pinned items in surfaced zone — UNLESS the pinned
      // call failed, in which case skipping them drops them twice over
      // (absent from the pinned zone, filtered out of the surfaced one) and
      // a section holding only pinned cards reads "Clear" over open work.
      // get_active_recommendations returns pinned rows too, so letting them
      // fall through here recovers the cards rather than losing them.
      if (r.pinned && !pinnedFailed) return;
      if (grouped[r.section]) grouped[r.section].surfaced.push(r);
    });

    document.querySelectorAll('.rec-section').forEach(sec => {
      const id = sec.dataset.section;
      const g = grouped[id] || { pinned: [], surfaced: [] };
      const total = g.pinned.length + g.surfaced.length;
      const failed = failedSections.has(id);
      sec.querySelector('[data-counts]').textContent =
        failed ? "Couldn't load" :
        total === 0 ? 'Clear' :
        `${total} open${g.pinned.length ? ` · ${g.pinned.length} pinned` : ''}`;
      sec.querySelector('[data-pinned]').innerHTML = g.pinned.length
        ? `<div class="rec-zone-label">📌 PINNED</div>` +
            g.pinned.map(renderRecCard).join('')
        : '';
      sec.querySelector('[data-surfaced]').innerHTML = g.surfaced.length
        ? _renderSurfacedZone(g.surfaced)
        : (g.pinned.length ? '' :
            failed
              ? `<div class="card" style="padding:14px;color:var(--eon-warn);font-size:13px;">Couldn't load this section — cards may be open that aren't shown. Pull to refresh.</div>`
              : `<div class="card" style="padding:14px;color:var(--ink-soft);font-size:13px;">No anomalies in this section right now.</div>`);
    });

    // Re-apply any active severity filter to the freshly-rendered cards.
    _applyRecFilter();

    // v3.0p20 polish: collapse-by-default. Each section is a tap-target.
    // Pinned sections start expanded (operator already curated them);
    // sections with zero items stay collapsed; everything else collapsed.
    document.querySelectorAll('.rec-section').forEach(sec => {
      if (sec.dataset.toggleBound === '1') return;
      sec.dataset.toggleBound = '1';
      const id = sec.dataset.section;
      const g = grouped[id] || { pinned: [], surfaced: [] };
      const startExpanded = g.pinned.length > 0;
      _setRecSectionExpanded(sec, startExpanded);
      sec.querySelector('.rec-section-toggle').addEventListener('click', () => {
        _setRecSectionExpanded(sec, sec.dataset.expanded !== '1');
      });
    });
  };

  await refresh();

  // Severity filter chips — triage a large list without scrolling.
  document.getElementById('rec-filter-bar')?.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-sev-filter]');
    if (!chip) return;
    State.recFilter = chip.dataset.sevFilter || null;
    document.querySelectorAll('#rec-filter-bar .rec-sev-chip').forEach(c =>
      c.classList.toggle('chip-active', (c.dataset.sevFilter || '') === (State.recFilter || '')));
    _applyRecFilter();
    // A filter must SURFACE matches, not hide them behind collapsed sections
    // and "Show all" wraps. When one is active: expand every more-wrap, then
    // open sections that contain a matching card; update the counts to the
    // filtered view.
    if (State.recFilter) {
      document.querySelectorAll('#rec-sections [data-more-wrap]').forEach(w => {
        w.style.display = 'block';
        const moreBtn = w.nextElementSibling;
        if (moreBtn && moreBtn.hasAttribute('data-show-more')) moreBtn.remove();
      });
      _applyRecFilter();  // re-apply over the newly-revealed cards
      document.querySelectorAll('#rec-sections .rec-section').forEach(sec => {
        const matches = sec.querySelectorAll(`.rec-card[data-sev="${State.recFilter}"]`).length;
        if (matches > 0) _setRecSectionExpanded(sec, true);
        const counts = sec.querySelector('[data-counts]');
        // A section whose fetch failed must never be relabelled "Clear" —
        // it has no cards to match because none arrived, not because none
        // exist.
        if (counts) counts.textContent =
          failedSections.has(sec.dataset.section) ? "Couldn't load" :
          matches === 0 ? 'Clear' : `${matches} ${State.recFilter}`;
      });
    } else {
      // Filter cleared → restore unfiltered counts from the live DOM.
      document.querySelectorAll('#rec-sections .rec-section').forEach(sec => {
        const total = sec.querySelectorAll('.rec-card').length;
        const counts = sec.querySelector('[data-counts]');
        if (counts) counts.textContent =
          failedSections.has(sec.dataset.section) ? "Couldn't load" :
          total === 0 ? 'Clear' : `${total} open`;
      });
    }
  });

  // "Show all N" expander per section.
  document.getElementById('rec-sections')?.addEventListener('click', async (e) => {
    const gt = e.target.closest('[data-group-toggle]');
    if (gt) {
      const wrap = document.querySelector(`[data-group-wrap="${CSS.escape(gt.dataset.groupToggle)}"]`);
      if (wrap) {
        const open = wrap.style.display !== 'none';
        wrap.style.display = open ? 'none' : 'block';
        gt.textContent = open ? `Show ${wrap.querySelectorAll('.rec-card').length}` : 'Hide';
        if (!open) _applyRecFilter();
      }
      return;
    }
    const dt = e.target.closest('[data-dismiss-type]');
    if (dt) {
      const t = dt.dataset.dismissType;
      dt.disabled = true; dt.textContent = 'Clearing…';
      try {
        const r = await api('/api/recommendations/dismiss-type', {
          method: 'POST', body: JSON.stringify({ anomaly_type: t }) });
        showToast(r.ok ? `Cleared ${r.dismissed} ${t.replace(/_/g, ' ')} suggestions` : (r.error || 'Could not clear'));
        await refresh();
      } catch (err) { showToast('Could not clear: ' + (err.message || err)); dt.disabled = false; dt.textContent = 'Clear all'; }
      return;
    }
    const more = e.target.closest('[data-show-more]');
    if (!more) return;
    const wrap = more.previousElementSibling;
    if (wrap && wrap.hasAttribute('data-more-wrap')) {
      wrap.style.display = 'block';
      more.remove();
      _applyRecFilter();
    }
  });

  // Refresh button — runs a fresh scan, then re-renders.
  document.getElementById('rec-refresh-btn')?.addEventListener('click', async () => {
    const btn = document.getElementById('rec-refresh-btn');
    btn.disabled = true; btn.textContent = 'Scanning…';
    try {
      await api('/api/recommendations/scan', { method: 'POST' });
      await refresh();
    } catch (e) {
      showToast('Scan failed: ' + (e.message || e));
    } finally {
      btn.disabled = false; btn.textContent = 'Refresh';
    }
  });

  // Card action delegation.
  document.getElementById('rec-sections')?.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-rec-action]');
    if (!btn) return;
    const card = btn.closest('[data-rec-id]');
    if (!card) return;
    const recId = card.dataset.recId;
    const action = btn.dataset.recAction;
    try {
      if (action === 'copy-directive') {
        const body = card.querySelector('.rec-directive-body');
        if (!body) return;
        try {
          await navigator.clipboard.writeText(body.textContent || '');
          btn.textContent = 'Copied';
        } catch (_) {
          // Clipboard is permission-gated and absent over plain http —
          // select the text so a manual copy still works rather than
          // leaving a dead button.
          const rng = document.createRange();
          rng.selectNodeContents(body);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(rng);
          btn.textContent = 'Select + copy';
        }
        setTimeout(() => { btn.textContent = 'Copy'; }, 2000);
        return;
      }
      if (action === 'acted_on' || action === 'wont' || action === 'pin') {
        // Tap 1 of 2: open the inline reason strip. Re-tapping the same tap
        // closes it, so a mis-tap costs nothing.
        const slot = card.querySelector('[data-reason-slot]');
        if (!slot) return;
        const mode = (action === 'wont') ? 'wont' : action;
        if (!slot.hidden && slot.dataset.mode === mode) {
          slot.hidden = true; slot.innerHTML = ''; return;
        }
        slot.dataset.mode = mode;
        slot.innerHTML = _recReasonPanel(mode);
        slot.hidden = false;
        slot.querySelector('[data-reason-input]')?.focus();
      } else if (action === 'reason-chip') {
        const input = card.querySelector('[data-reason-input]');
        if (input) { input.value = btn.dataset.reason || ''; input.focus(); }
      } else if (action === 'reason-cancel') {
        const slot = card.querySelector('[data-reason-slot]');
        if (slot) { slot.hidden = true; slot.innerHTML = ''; }
      } else if (action === 'reason-save') {
        // Tap 2 of 2: commit. outcome_type vocabulary is unchanged —
        // acted_on / ignored / wrong all still reach mark_outcome.
        const slot = card.querySelector('[data-reason-slot]');
        const notes = (card.querySelector('[data-reason-input]')?.value || '').trim();
        const outcome = btn.dataset.outcome || 'ignored';
        // 'wrong' is the only verdict that must be justified: it demotes the
        // scanner by -2.0 in rec_quality, so a blank reason teaches nothing
        // about WHAT to stop surfacing.
        if (outcome === 'wrong' && !notes) {
          showToast('Say what was wrong — that line is what retrains the scanner.');
          card.querySelector('[data-reason-input]')?.focus();
          return;
        }
        slot?.querySelectorAll('button').forEach(b => (b.disabled = true));
        if (outcome === 'pin') {
          await api(`/api/recommendations/${encodeURIComponent(recId)}/pin`, {
            method: 'POST',
            body: JSON.stringify({ reason: notes }),
          });
          await refresh();
          return;
        }
        await api(`/api/recommendations/${encodeURIComponent(recId)}/outcome`, {
          method: 'POST',
          body: JSON.stringify({ outcome_type: outcome, notes }),
        });
        showToast(outcome === 'acted_on'
          ? 'Recorded. EON checks back in 7 days.'
          : 'Recorded. EON weighs this against the scanner.');
        card.remove();
      } else if (action === 'snooze') {
        await api(`/api/recommendations/${encodeURIComponent(recId)}/snooze`, {
          method: 'POST',
          body: JSON.stringify({ duration_hours: 24 }),
        });
        card.style.opacity = '0.3';
        setTimeout(() => card.remove(), 250);
      } else if (action === 'unpin') {
        await api(`/api/recommendations/${encodeURIComponent(recId)}/unpin`, {
          method: 'POST',
        });
        await refresh();
      } else if (action === 'reasoning') {
        // EON's deeper take — Fable 5, cached server-side. Toggles an inline
        // panel; fetches once then re-shows the cached prose.
        const slot = card.querySelector('[data-reasoning-slot]');
        if (!slot) return;
        // Visible panel (loaded OR error) → tap collapses it. Errors stay
        // dismissable and re-tappable (was: error panel un-dismissable +
        // refetch on every click).
        if (!slot.hidden) { slot.hidden = true; return; }
        slot.hidden = false;
        if (slot.dataset.loaded === '1') return;
        slot.innerHTML = `<div class="rec-reasoning-load"><span class="rec-reasoning-spark">✦</span> EON is thinking through this…</div>`;
        btn.disabled = true;
        try {
          // 90s: a cold Fable-5 take (or its Opus fallback) routinely
          // exceeds the default 30s api() ceiling.
          const res = await api('/api/recommendations/' + encodeURIComponent(recId) + '/reasoning', { timeoutMs: 90000 });
          if (res && res.ok && res.reasoning) {
            slot.innerHTML = `<div class="rec-reasoning-head">✦ EON's take</div>` +
              res.reasoning.split(/\n{2,}/).map(p => `<p>${esc(p.trim())}</p>`).join('');
            slot.dataset.loaded = '1';
          } else {
            slot.innerHTML = `<div class="rec-reasoning-err">${esc((res && res.error) || 'Couldn\'t generate a take right now.')}</div>`;
          }
        } catch (err) {
          slot.innerHTML = `<div class="rec-reasoning-err">${esc(err.message || err)}</div>`;
        } finally {
          btn.disabled = false;
        }
      } else if (action === 'investigate') {
        // Pre-fill chat with the investigation prompt — pattern matches
        // Inventory view's Ask EON buttons.
        const title = card.dataset.recTitle || '';
        const obs   = card.dataset.recObservation || '';
        goChat(`Investigate this recommendation: "${title}". Observation was: ${obs} — pull the underlying data and tell me what's most actionable.`);
      } else if (action === 'authorize') {
        // v3.0p20 9rr: owner authorizes a recommendation, which routes
        // it through departments + spawns a task + (if WhatsApp wired)
        // pings the assignee. Trust-gated server-side.
        if (!confirm('Authorize this recommendation and dispatch as a task to the routed manager?')) return;
        const res = await api('/api/recommendations/' + encodeURIComponent(recId) + '/authorize', {
          method: 'POST',
          body: JSON.stringify({}),
        });
        if (res && res.task) {
          showToast('Task created and routed.');
          card.style.opacity = '0.5';
          setTimeout(() => refresh(), 300);
        } else {
          showToast(res?.message || 'Authorization queued.');
        }
      }
    } catch (err) {
      // A half-submitted reason strip must stay retryable — otherwise one
      // failed save leaves the operator with dead buttons and no verdict.
      card?.querySelectorAll('[data-reason-slot] button')
        .forEach(b => (b.disabled = false));
      showToast('Action failed: ' + (err.message || err));
    }
  });
}

// Render a section's surfaced cards, capping at REC_SECTION_CAP with a
// "Show all N" expander so a section with 80+ recs doesn't dump 80 heavy
// DOM cards (the surfaced list is already severity/urgency-sorted server-side).
// Declutter 2026-07-25 (operator: "200+ ... stop duplicating"): an
// anomaly_type with more than REC_GROUP_MIN cards collapses to ONE group
// card — count, summed impact, top examples, expand-in-place, and a
// one-tap "Clear all N" that dismisses the class server-side (pinned rows
// survive; scanners re-mint what is still true). Singles render as before.
const REC_GROUP_MIN = 4;
function _typeLabel(t) {
  return String(t || 'other').replace(/_/g, ' ');
}
function _renderSurfacedZone(surfaced) {
  if (!surfaced.length) return '';
  const byType = {};
  surfaced.forEach(r => (byType[r.anomaly_type] ||= []).push(r));
  const groups = [], singles = [];
  Object.entries(byType).forEach(([t, rows]) =>
    (rows.length >= REC_GROUP_MIN ? groups.push([t, rows]) : singles.push(...rows)));
  groups.sort((a, b) => b[1].length - a[1].length);
  const groupHtml = groups.map(([t, rows]) => {
    const impact = rows.reduce((n, r) => n + (Number(r.estimated_impact_ngn) || 0), 0);
    const crit = rows.filter(r => (r.severity || '') === 'critical').length;
    const sample = rows.slice(0, 3).map(r => esc(shorten(r.title, 60))).join(' · ');
    return `<div class="card rec-group-card" data-rec-group="${esc(t)}" style="padding:12px 14px;margin-bottom:8px;">
      <div style="display:flex;align-items:center;gap:10px;">
        <div style="flex:1;min-width:0;">
          <div style="font-weight:600;font-size:14px;">${rows.length} × ${esc(_typeLabel(t))}${crit ? ` · <span style="color:var(--danger,#c0392b)">${crit} critical</span>` : ''}${impact ? ` · ₦${Math.round(impact).toLocaleString()}` : ''}</div>
          <div style="font-size:12px;color:var(--ink-soft);margin-top:2px;">${sample}</div>
        </div>
        <button class="chip" type="button" data-group-toggle="${esc(t)}">Show ${rows.length}</button>
        <button class="chip danger-chip" type="button" data-dismiss-type="${esc(t)}" title="Dismiss every open ${esc(_typeLabel(t))} suggestion — pinned ones stay; the scanner re-surfaces anything still true.">Clear all</button>
      </div>
      <div class="rec-more-wrap" data-group-wrap="${esc(t)}" style="display:none;margin-top:10px;">${rows.map(renderRecCard).join('')}</div>
    </div>`;
  }).join('');
  if (singles.length <= REC_SECTION_CAP) return groupHtml + singles.map(renderRecCard).join('');
  const head = singles.slice(0, REC_SECTION_CAP).map(renderRecCard).join('');
  const rest = singles.slice(REC_SECTION_CAP).map(renderRecCard).join('');
  return groupHtml + head +
    `<div class="rec-more-wrap" data-more-wrap style="display:none;">${rest}</div>` +
    `<button class="rec-show-more" type="button" data-show-more>Show all ${singles.length}</button>`;
}

// Apply the active severity filter by toggling per-card visibility. Empty
// sections (after filtering) collapse their toggle count quietly.
function _applyRecFilter() {
  const sev = State.recFilter || null;
  document.querySelectorAll('#rec-sections .rec-card').forEach(card => {
    const show = !sev || card.dataset.sev === sev;
    card.style.display = show ? '' : 'none';
  });
}

// LM-01 (2026-08-01): inline reason capture, replacing the blocking dialog.
// A blocking dialog is a no-op in an installed iOS PWA (standalone mode) —
// the operator's primary surface — so the old outcome path could never be
// completed there: 643 recs written, zero operator-authored outcome rows.
// Everything below is in-card DOM driven by the same #rec-sections click
// delegation, so it survives a re-render with no extra listeners.
const REC_WONT_REASONS = [
  'Already handled',
  'Not a priority',
  'Costs more than it saves',
  'The data looks wrong',
];

function _recReasonPanel(mode) {
  if (mode === 'acted_on') {
    return `
      <div class="rec-reason-head">What did you do?</div>
      <input class="rec-reason-input" data-reason-input type="text"
             placeholder="Optional — one line is enough" autocomplete="off">
      <div class="rec-reason-actions">
        <button class="rec-btn rec-btn-primary" type="button"
                data-rec-action="reason-save" data-outcome="acted_on">Record it</button>
        <button class="rec-btn" type="button" data-rec-action="reason-cancel">Cancel</button>
      </div>`;
  }
  if (mode === 'pin') {
    return `
      <div class="rec-reason-head">Why are you pinning this?</div>
      <input class="rec-reason-input" data-reason-input type="text"
             placeholder="Optional — keeps the strategic zone legible" autocomplete="off">
      <div class="rec-reason-actions">
        <button class="rec-btn rec-btn-primary" type="button"
                data-rec-action="reason-save" data-outcome="pin">Pin it</button>
        <button class="rec-btn" type="button" data-rec-action="reason-cancel">Cancel</button>
      </div>`;
  }
  return `
    <div class="rec-reason-head">Why not?</div>
    <div class="rec-reason-chips">
      ${REC_WONT_REASONS.map(t => `<button class="rec-chip" type="button"
        data-rec-action="reason-chip" data-reason="${esc(t)}">${esc(t)}</button>`).join('')}
    </div>
    <input class="rec-reason-input" data-reason-input type="text"
           placeholder="Or say it in your own words" autocomplete="off">
    <div class="rec-reason-actions">
      <button class="rec-btn rec-btn-primary" type="button"
              data-rec-action="reason-save" data-outcome="ignored">Won't do it</button>
      <button class="rec-btn rec-btn-wrong" type="button"
              data-rec-action="reason-save" data-outcome="wrong">EON got this wrong</button>
      <button class="rec-btn" type="button" data-rec-action="reason-cancel">Cancel</button>
    </div>`;
}

function renderRecCard(r) {
  // Normalize severity to the canonical critical/warn/info set — live rows
  // carry variants ("high", "medium", "warning") that would otherwise fall
  // outside every filter chip AND default the badge to info.
  const sevKey = (s => (
    ['critical', 'high', 'block'].includes(s) ? 'critical'
    : ['warn', 'warning', 'medium'].includes(s) ? 'warn'
    : 'info'
  ))(String(r.severity || 'info').toLowerCase());
  const sevBadge = SEVERITY_BADGE[sevKey];
  const urgencyLabel = URGENCY_LABEL[r.urgency] || r.urgency;
  const confidence = r.hypothesis_confidence != null
    ? `${Math.round(r.hypothesis_confidence * 100)}%`
    : '—';
  const decisionBy = r.decision_by
    ? `Decide by: ${esc(r.decision_by.slice(0, 10))}`
    : '';
  const surfacedAgo = r.surfaced_at
    ? `Surfaced ${_timeAgo(r.surfaced_at)}`
    : '';
  const impact = r.estimated_impact_ngn
    ? `Est. impact: ₦${_widgetCompact(r.estimated_impact_ngn)}`
    : '';

  const ladder = (r.action_ladder || []).slice(0, 8).map((a, i) => {
    const impactStr = a.estimated_impact_ngn
      ? ` <span style="color:var(--ink-soft);">(~₦${_widgetCompact(a.estimated_impact_ngn)})</span>`
      : '';
    const timing = a.time_to_implement
      ? ` <span style="font-size:10px;color:var(--ink-mute);">${esc(a.time_to_implement)}</span>`
      : '';
    // action_text is the canonical key, but four emitters write "action"
    // instead (self_spec, anomaly_detection:922, demand_mining:694,
    // size_lost_sales:340) and every one of their steps has been rendering
    // as an empty bullet. Fixed at source for self_spec; accepted here too
    // so the other three stop losing their instructions.
    return `<li>${esc(a.action_text || a.action || '')}${impactStr}${timing}</li>`;
  }).join('');

  // self_spec writes a paste-ready build brief into feature_payload.directive.
  // Nothing on this page read feature_payload, so the lane's entire product
  // was invisible — the operator would have had to query the DB by hand to
  // read the brief EON wrote for him. It exists to be pasted, hence Copy.
  const directive = (r.feature_payload || {}).directive || '';

  const cardOpacity = r.pinned ? '' : '';
  const pinButton = r.pinned
    ? `<button class="rec-btn" data-rec-action="unpin" type="button">Unpin</button>`
    : `<button class="rec-btn" data-rec-action="pin" type="button">📌 Pin</button>`;

  return `
    <div class="card rec-card" data-rec-id="${esc(r.id)}"
         data-sev="${sevKey}"
         data-rec-title="${esc((r.title || '').replace(/"/g, "'"))}"
         data-rec-observation="${esc((r.observation || '').replace(/"/g, "'"))}">
      <div class="rec-card-head">
        <span class="rec-sev-badge" style="background:${sevBadge.color}22;color:${sevBadge.color};">${sevBadge.label}</span>
        <span class="rec-urg-label">${esc(urgencyLabel)}</span>
        ${impact ? `<span class="rec-impact">${impact}</span>` : ''}
      </div>
      <div class="rec-card-title">${esc(r.title || '')}</div>
      <div class="rec-card-obs">${esc(r.observation || '')}</div>
      <div class="rec-twotap">
        ${State.member?.role === 'owner' && !r.authorized_at ? `
          <button class="rec-btn rec-btn-primary" data-rec-action="authorize" type="button">Authorize → Task</button>
        ` : ''}
        <button class="rec-btn rec-btn-did" data-rec-action="acted_on" type="button">I did it</button>
        <button class="rec-btn rec-btn-wont" data-rec-action="wont" type="button">Won't — why</button>
      </div>
      <div class="rec-reason" data-reason-slot hidden></div>
      <div class="rec-card-hyp">
        <strong>Hypothesis</strong>
        <span class="rec-confidence">${confidence} confident</span>
        <div>${esc(r.hypothesis || '')}</div>
      </div>
      ${ladder ? `<ol class="rec-action-ladder">${ladder}</ol>` : ''}
      ${directive ? `
      <div class="rec-directive">
        <div class="rec-directive-head">
          <strong>Build directive</strong>
          <button class="rec-btn rec-directive-copy" data-rec-action="copy-directive"
                  type="button">Copy</button>
        </div>
        <pre class="rec-directive-body">${esc(directive)}</pre>
      </div>` : ''}
      <div class="rec-card-meta">
        ${surfacedAgo}${decisionBy ? ` · ${decisionBy}` : ''}
        ${(r.affects_sections && r.affects_sections.length) ? ` · Affects: ${r.affects_sections.map(esc).join(', ')}` : ''}
      </div>
      <div class="rec-card-actions">
        <button class="rec-btn" data-rec-action="snooze" type="button">Snooze 24h</button>
        ${pinButton}
        <button class="rec-btn rec-btn-take" data-rec-action="reasoning" type="button">✦ EON's take</button>
        <button class="rec-btn rec-btn-investigate" data-rec-action="investigate" type="button">Investigate →</button>
      </div>
      <div class="rec-reasoning" data-reasoning-slot hidden></div>
    </div>
  `;
}

function _timeAgo(isoStr) {
  if (!isoStr) return '';
  const now = Date.now();
  const then = new Date(isoStr).getTime();
  const minutes = Math.round((now - then) / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
// ──────────────────────────────────────────────────────────
// Money (accounting)

// ──────────────────────────────────────────────────────────
// v3.0p17 9h item 3 / 9l: ad considered "active" if it had spend within
// the last N days. Mirrors backend ad_judge_per_ad._ACTIVE_DAYS.
// Bumped 3 → 7 in 9l: TikTok / Snap / Google ETL can lag 4-7 days
// even when the underlying ads are running. 3 made every non-Meta ad
// look paused. 7 absorbs sync lag while still flagging truly-stopped ads.
const _AD_ACTIVE_DAYS = 7;
function _isAdRecentlyActive(ad) {
  if (!ad || !ad.last_active_date) return false;
  const last = new Date(ad.last_active_date);
  if (isNaN(last)) return false;
  const days = (Date.now() - last.getTime()) / 86_400_000;
  return days <= _AD_ACTIVE_DAYS;
}

// Ads page (v3.0p11) — platform-level verdicts
// Reads from /api/ads/platforms (cached daily) + manual refresh.

const ADS_PLATFORM_LABELS = {
  meta: 'Meta',
  tiktok: 'TikTok',
  snapchat: 'Snapchat',
  google: 'Google',
};

function adsVerdictPillClass(verdict) {
  // Maps verdict label → CSS pill class. Reuses existing pill colors
  // from the design system.
  if (verdict === 'scale') return 'pill-ok';
  if (verdict === 'cut') return 'pill-danger';
  if (verdict === 'hold') return 'pill-warn';
  return 'pill-muted';  // investigate, no_data
}

function adsConfidenceLabel(c) {
  if (c === 'high') return 'High confidence';
  if (c === 'medium') return 'Medium confidence';
  return 'Low confidence';
}

function adsFormatNgn(v) {
  // Compact NGN formatter (2.4M, 410k, 8.2k)
  if (v == null || isNaN(v)) return '—';
  const n = Math.abs(Number(v));
  if (n >= 1_000_000) return `₦${(v / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `₦${(v / 1_000).toFixed(0)}k`;
  return `₦${Math.round(v)}`;
}

// v3.0p18 9u — chat-built dashboards: My Dashboard view.
// Renders pinned widgets in a 1- or 2-column grid (responsive).
// Each widget calls /api/dashboard/widgets/{id}/data to materialise
// fresh numbers — the widget row only stores (source, params), so the
// view auto-updates as underlying data lands without a re-pin.

// ─────────────────────────────────────────────────────────────
// CS Inbox v2 (2026-06-06) — full-screen chat UI on Zoko backend.
//
// v2 upgrades over v1:
//   - Filter tabs (All / DMs / Comments / Stories) so IG comments
//     + story replies stop drowning in the DM stream
//   - Hot signal flag on threads where the customer message contains
//     high-intent keywords (price, size, availability)
//   - Editorial typography (Archivo grotesque throughout)
//   - Subtle depth via gradients, soft shadows, glassmorphism on
//     top bar + customer context strip
//   - Micro-animations: bubble entry, ring transitions, hover lifts
//
// Uses Zoko as message transport + media store (per Daniel's design
// principle). We hold only metadata + URL pointers in zoko_messages.
//
// State.csInbox shape:
//   {
//     threads:      [...],
//     active:       <zoko_customer_id>,
//     transcript:   [...],
//     filter:       'all' | 'dm' | 'comment' | 'story',
//     pollHandle:   setInterval id,
//   }
// ─────────────────────────────────────────────────────────────
// CS Workspace (2026-06-07) — Gemini-philosophy, gamified, mobile-first.
// Replaces the v1 cs2 inbox. Styles in /static/cs.css (csw-* classes).
// Entry points renderCsInbox()/bindCsInbox() are kept so the router
// (case 'cs') is unchanged. See docs/cs_workspace_gemini_spec_2026_06.md
// ─────────────────────────────────────────────────────────────

const CSW_HOT_RE = /\b(how\s*much|price|cost|naira|₦|size|small|medium|large|xl|fit|available|in\s+stock|when\s+can|delivery|how\s+to\s+(buy|order)|i\s+want|payment|pay|account|gtbank|transfer)\b/i;
// Past-recovery threshold for the Awaiting tab (2026-07-26): replies within
// 1h convert 24.4%, never-answered 0% (zoko_demand_mining) — past ~3 days a
// wait is history, not a queue. Folded below the live list, never hidden.
const CSW_RECOVERY_MIN = 72 * 60;
// Promise-keeper (2026-08-03): a thread whose LAST outbound promised a
// follow-up ("we'll check and message you right here") stays on the
// Needs-reply board with an amber "waiting on feedback" chip until a
// later send actually goes out. The chip flips to the danger color once
// the customer has waited this long on our own word (mirrors backend
// cs_promise.PROMISE_OVERDUE_MINUTES — keep the two in step).
const CSW_PROMISE_OVERDUE_MIN = 120;

// Keyboard-shortcut hint for the CS composers — mirrors the EON-main
// composer-hint (app.js ~4520) verbatim so the convention transfers (↵ sends,
// ⇧↵ newlines). Same source string, reused (no new glyphs introduced).
const CSW_COMPOSER_HINT = '↵ to send · ⇧↵ for newline';

// THE TEAM COMPOSER TEACHES THE MOVE (2026-08-30). W5 armed with zero
// staff having ever sent a job to another team, and the reason is not
// that the lane was dark — it is that nothing anywhere said the move
// existed. A person cannot use a shape they have never been shown, and
// the composer is the one place they are already looking when they
// would use it.
//
// It rides the HINT line rather than only the placeholder because a
// placeholder is clipped by the input's own width on a phone — and the
// half that clips is the teaching half. The hint line is full width and
// wraps.
//
// The middle dot is already shipping in this exact element: the hint
// above renders '·' in .csw-hint, inside the same composer, in the same
// Archivo stack (cs.css --body). Nothing new is introduced here.
const CSW_TEAM_COMPOSER_HINT =
  CSW_COMPOSER_HINT + ' · Type @sales·palms to send a job to whoever is on shift at that shop';

// HOUSE floor 3 — Switchboard (WS-B 2026-08-06). Under the house skin the
// desk reads as the approved mockup: wait is the loudest number, cleared
// threads TEAR off the rail (the room's one moving part), numbers stay
// masked on the desk, and the tray leads with five keys + More. Every
// branch below is inert off-house.
const cswHouse = () => document.documentElement.getAttribute('data-theme') === 'house';

// Privacy as a visible law (the 74-phone leak class): a phone-shaped label
// renders masked to its last two digits. Real names pass through. Masking
// happens at the RENDER SITES only — search, dedupe and routing keep the
// raw value.
// One expression, two render sites — and defined OUTSIDE the render
// functions so their source stays inside brittle fixed-window test slices.
const cswHouseName = (t) => cswHouse() ? cswHouseMask(cswDisplayName(t)) : cswDisplayName(t);
const cswHouseTitle = () => cswHouse() ? ' title="Numbers stay masked on this desk"' : '';

function cswHouseMask(label) {
  const raw = String(label || '');
  const digits = raw.replace(/[^0-9]/g, '');
  if (digits.length < 7) return raw;
  const cc = digits.startsWith('234') ? '+234' : (raw.trim().startsWith('+') ? '+' + digits.slice(0, 1) : digits.slice(0, 1));
  return cc + ' ••• ••' + digits.slice(-2);
}

function cswState() {
  if (!State.csw) {
    State.csw = {
      mode: 'inbox', threads: [], active: null, activeThread: null,
      filter: 'all', transcript: [], notes: [], cart: [], poll: null, metrics: null,
    };
  }
  const s = State.csw;
  if (!s._threadMetaWatched) {
    s._threadMetaWatched = true;
    for (const key of ['active','lockedQueue','asViewStaffId','asRepEmail']) {
      let value = s[key];
      Object.defineProperty(s,key,{enumerable:true,configurable:true,get:()=>value,set:next=>{
        if (next !== value && State.csw === s) cswThreadMetaInvalidate();
        value = next;
      }});
    }
  }
  // 2026-06-18: the chat workspace is the inbox. Pulse (a perf dashboard) was
  // removed (Performance OS lives in Staff OS) and Automated moved into the
  // hamburger drawer. Normalize any stale in-session mode back to the inbox;
  // 'automated' is set transiently by the drawer entry and is allowed through.
  if (State.csw.mode === 'abandoned') State.csw.mode = 'automated';
  if (State.csw.mode === 'pulse') State.csw.mode = 'inbox';
  return State.csw;
}

// Ask EON over the reply (two-hands round 3, F3-1 and F3-4, 29 Sep 2026).
// Ask's open panel sits on the reply area. On a phone it covers all of it
// but a strip under the panel, and a tap just below Ask's own Ask key SENT
// the reply box to the customer. On a laptop it covers Send, Emoji and the
// voice-note key. So while the panel is open, the reply area waits: the
// reply box and every key around it, and a held reply's Send now and
// Discard, cannot be tapped, tabbed to or read out. Inert parts look the
// same, but the reply area does not (cswAskCover, below): on a phone it is
// out of sight; from 600 px Send and + are out of sight, the box says "Close
// Ask to reply" or "Close Ask" (or, where neither fits, a caption shows under
// it), a draft is greyed and the pointer over it is a plain arrow. All of it
// is given back once the panel has gone. The list, the conversation and its
// menu stay usable. Ask says when its panel opens and closes; anything
// the desk draws while it is open waits too: another customer, a new held
// reply, or the whole desk when the owner reaches it from another room with
// Ask still open (chair C-1). So the watch follows "Ask is open", on every
// page; off the desk each change costs one id lookup. A failed message's
// one-tap fix waits too ("Send the words without the photo", "Pick up with
// the template"): at 1280 it showed beside the panel and one click SENT
// (Opus round 4, O4-1).
const CSW_ASK_COVERS = '#csw-conv .csw-composer, #csw-conv .csw-bands, #csw-conv .csw-held__acts, #csw-conv .csw-lact, #csw-conv .csw-flap__verb';
// Whether Ask's panel is over the desk: open, or still folding away. It lives
// on window, beside the desk's own window._cswKeysBound, because the desk's
// key listener (cswBindKeys) reads it and Node tests lift that listener out
// of this file on its own: a lifted copy reads it as off instead of failing
// on a name it does not carry ("_cswAskOver is not defined", 4 Oct 2026).
window._cswAskOver = false;
let _cswAskWatch = null, _cswAskSize = null, _cswAskOpen = false, _cswAskHold = null;
function cswAskCover() {
  if (!document.getElementById('csw-conv')) return;
  document.querySelectorAll(CSW_ASK_COVERS).forEach(n => { if (n.inert !== window._cswAskOver) n.inert = window._cswAskOver; });
  // On a phone the panel stops 18 px above the bottom, and the waiting
  // reply area showed through: a crescent of the desk's green Send under the
  // panel's corner and the reply frame along its edge, read as a second live
  // green key beside Ask's own (Opus O4-3, Fable F4-5). Below 600 px the
  // covered composer is out of sight while Ask is open, and back on close.
  const hide = window._cswAskOver && window.innerWidth < 600;
  document.querySelectorAll('#csw-conv .csw-composer').forEach(n => {
    if (hide && n.style.visibility !== 'hidden') { n.dataset.askHid = '1'; n.style.visibility = 'hidden'; }
    else if (!hide && n.dataset.askHid) { delete n.dataset.askHid; n.style.visibility = ''; }
  });
  // The reply box says it waits (the owner's yes, 8 Oct 2026, to
  // PROPOSAL_SHOW_THE_WAIT). From 600 px up part of the box shows beside the
  // open panel, and it looked ready to type in. While Ask is open there, an
  // empty box says to close Ask, a draft keeps its words in the desk's muted
  // grey, and the pointer over the box is a plain arrow. All inline, and
  // given back as found the moment Ask closes; the draft's words are never
  // touched. A placeholder the desk writes meanwhile (the reply window, EON
  // answering) is the one given back, never the older one.
  // Two wordings, never a third, and never a word cut where either fits
  // (round 4, 8 Oct 2026): the owner's "Close Ask to reply" wherever it fits
  // whole in the part of the box beside the panel, else "Close Ask". On a
  // tablet the panel left 94 px and the box read "Close Ask to r"; from 880 px
  // the desk's second pane pushes the box right, and an expanded panel covers
  // more of it, so the room is measured, not guessed from the width. Where
  // neither fits, the full waiting caption uses the bottom clearance below
  // the panel; the draft and the panel geometry stay unchanged (floor-3 low).
  const wait = window._cswAskOver && window.innerWidth >= 600, waitWords = ['Close Ask to reply', 'Close Ask'];
  const box = document.getElementById('csw-input');
  let narrow = false;
  if (box && wait) {
    if (box.dataset.askWait !== '1') { box.dataset.askWait = '1'; box.dataset.askInk = box.style.color; box.style.color = '#9A9AA2'; }
    let words = waitWords[0];
    try {
      const panel = document.getElementById('eon-ask-root').shadowRoot.querySelector('.sheet').getBoundingClientRect();
      const cs = getComputedStyle(box), b = box.getBoundingClientRect();
      const room = Math.min(panel.width ? panel.left : Infinity, b.right - parseFloat(cs.paddingRight) - parseFloat(cs.borderRightWidth))
        - (b.left + parseFloat(cs.paddingLeft) + parseFloat(cs.borderLeftWidth));
      const pen = cswAskCover.pen || (cswAskCover.pen = document.createElement('canvas').getContext('2d'));
      pen.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      if (pen.measureText(words).width + 2 > room) words = waitWords[1];   // 2 px spare: the last letter never clipped
      if (pen.measureText(words).width + 2 > room) { words = ''; narrow = true; }
    } catch (_) { /* nothing to measure against: the owner's words */ }
    // What the cover last wrote (a waiting wording, or no words at all) is
    // never kept as the desk's own; anything else in the box is the desk's.
    const ph = box.getAttribute('placeholder');
    if (ph !== words) {
      if (ph !== box.dataset.askSaid) { if (ph === null) delete box.dataset.askPh; else box.dataset.askPh = ph; }
      box.setAttribute('placeholder', words);
      box.dataset.askSaid = words;
    }
  } else if (box && box.dataset.askWait === '1') {
    if (box.getAttribute('placeholder') === box.dataset.askSaid) {
      if (box.dataset.askPh === undefined) box.removeAttribute('placeholder'); else box.setAttribute('placeholder', box.dataset.askPh);
    }
    box.style.color = box.dataset.askInk || '';
    delete box.dataset.askWait; delete box.dataset.askPh; delete box.dataset.askInk; delete box.dataset.askSaid;
  }
  // The desk's Send waits out of sight too (round 4, 8 Oct 2026). From 600 px
  // up the panel's rounded corner left an inert lime arc of it showing, a
  // second green key beside Ask's own: the look the phone hide removed. A key
  // that cannot be pressed is not drawn. Its own inline visibility comes back.
  // The attach key waits exactly like Send; neither suggests a live action.
  for (const id of ['csw-send', 'csw-plus']) {
    const key = document.getElementById(id);
    if (key && wait && key.dataset.askVis === undefined) { key.dataset.askVis = key.style.visibility; key.style.visibility = 'hidden'; }
    else if (key && !wait && key.dataset.askVis !== undefined) { key.style.visibility = key.dataset.askVis; delete key.dataset.askVis; }
  }
  document.querySelectorAll('#csw-conv .csw-composer').forEach(n => {
    if (narrow) n.dataset.askNarrow = '1'; else delete n.dataset.askNarrow;
  });
  document.querySelectorAll('#csw-conv .csw-composer, #csw-conv .csw-composer *').forEach(n => {
    if (wait && n.dataset.askCursor === undefined) { n.dataset.askCursor = n.style.cursor; n.style.cursor = 'default'; }
    else if (!wait && n.dataset.askCursor !== undefined) { n.style.cursor = n.dataset.askCursor; delete n.dataset.askCursor; }
  });
}
// A phone turned on its side while Ask is open crosses 600 px.
window.addEventListener('resize', () => { if (window._cswAskOver) cswAskCover(); }, { passive: true });
function cswAskSetOver(over) {
  window._cswAskOver = over;
  cswAskCover();
  if (window._cswAskOver && !_cswAskWatch && typeof MutationObserver === 'function') {
    _cswAskWatch = new MutationObserver(cswAskSeen);
    // Placeholders too: the desk can write the reply box's own words while
    // Ask is open without drawing anything, and the waiting words must hold.
    _cswAskWatch.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['placeholder'] });
  } else if (!window._cswAskOver && _cswAskWatch) {
    _cswAskWatch.disconnect();
    _cswAskWatch = null;
  }
  // The panel's own size, while Ask is open: it settles after this event, and
  // it grows when Ask is expanded, both inside Ask's own page where the watch
  // above cannot see. The room left for the waiting words moves with it.
  const panel = document.getElementById('eon-ask-root')?.shadowRoot?.querySelector('.sheet');
  if (window._cswAskOver && panel && !_cswAskSize && typeof ResizeObserver === 'function') {
    _cswAskSize = new ResizeObserver(() => { if (window._cswAskOver || _cswAskOpen) cswAskSeen(); });
    _cswAskSize.observe(panel);
  } else if (!window._cswAskOver && _cswAskSize) {
    _cswAskSize.disconnect();
    _cswAskSize = null;
  }
}
// Open is not over (the open point of #4161, 13 Oct 2026). After an update
// interrupted halfway, one page can hold this file and Ask's beside an
// older copy of the desk's styles, which hide Ask's whole host while a
// customer is open (cs.css before part 2). The reply area would then wait
// under a panel nobody can see or close. (An older Ask, with no deskReady,
// is never waited for at all: the listener below.) So it waits only for a
// panel that is on the screen: looked at just after Ask says "open" (the
// desk's answer below has Ask mark its host first), and again whenever the
// desk draws or the panel changes size. A panel this file cannot find is
// taken as on the screen. The watch stays for as long as Ask says "open",
// so a panel that went off the screen is waited for again the moment it is
// back: on a laptop the desk's own search drops the open-customer state
// with the conversation still showing, and the panel returns over it.
function cswAskOnScreen() {
  const boxes = document.getElementById('eon-ask-root')?.shadowRoot?.querySelector('.sheet')?.getClientRects?.();
  return !boxes || boxes.length > 0;
}
function cswAskSeen() {
  if (_cswAskOpen) window._cswAskOver = cswAskOnScreen();
  cswAskCover();
}
// Closed is not gone (#4161 round 3, second read). Ask says "not open" the
// moment Close is tapped, while its panel still shows for the panel speed and
// lets taps through: a tap meant for the fading panel reached the desk's own
// Send. So the reply area waits until Ask says its panel has left the screen
// ("eon-ask-gone"), or 480 ms where Ask never says (a tab that reports no
// fold), and comes back at once where the panel has no box left to fold
// (Ask hidden, or off the page by the desk's own rule). Ask opened again
// meanwhile calls it off: neither that clock nor a late "gone" lets go.
// Only an Ask that keeps the desk's side of this waits the desk (#4327 round
// 1). The service worker can serve an older eon_ask.js beside this file; that
// Ask has no deskReady and never marks its host, so cs.css keeps its panel
// hidden while a customer is open, and waiting under it left the reply area
// dead under no panel. An Ask that keeps it hears back that the reply area
// waits ("eon-ask-covered"), and only then shows over an open customer.
function cswAskLetGo() {
  clearTimeout(_cswAskHold);
  if (!_cswAskOpen && window._cswAskOver) cswAskSetOver(false);
}
document.addEventListener('eon-ask-visibility', (e) => {
  _cswAskOpen = e.detail?.open === true && typeof window.EON_ASK?.deskReady === 'function';
  clearTimeout(_cswAskHold);
  if (_cswAskOpen) _cswAskHold = setTimeout(cswAskSeen, 0);
  if (_cswAskOpen) { cswAskSetOver(true); document.dispatchEvent(new CustomEvent('eon-ask-covered')); return; }
  if (!window._cswAskOver) cswAskSetOver(false);   // closed while its panel was off the screen: that watch ends here
  if (!window._cswAskOver) return;
  const panel = document.getElementById('eon-ask-root')?.shadowRoot?.querySelector('.sheet');
  if (panel?.getClientRects?.().length === 0) cswAskLetGo();
  else _cswAskHold = setTimeout(cswAskLetGo, 480);
});
document.addEventListener('eon-ask-gone', cswAskLetGo);
// A press on the waiting reply area keeps the keyboard in Ask (Fable round 4,
// F4-1): a press on an inert part lands on the conversation around it, which
// took focus. Only a target holding the inert part is held; a live control over
// its box, like a sheet opened from the menu, keeps its press (#4327 round 2).
function cswAskHoldsPress(e) {
  if (!window._cswAskOver || e.target?.closest?.('#eon-ask-root')) return;
  const x = e.clientX, y = e.clientY;
  for (const n of document.querySelectorAll(CSW_ASK_COVERS)) {
    if (!n.inert || !e.target?.contains?.(n)) continue;
    const r = n.getBoundingClientRect();
    if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) { e.preventDefault(); return; }
  }
}
document.addEventListener('pointerdown', cswAskHoldsPress, true);
document.addEventListener('mousedown', cswAskHoldsPress, true);

// Ask EON's door to the open customer (two-hands round 1, A1-4, 28 Sep
// 2026). Ask read the customer from the rail's markup, which only the All
// view draws: in Priority (tickets) it said "No customer shared" while a
// customer was open. The desk's own state is the truth in every view, so the
// desk says it here, and tells Ask the moment another customer opens
// (cswOpenThread). A team thread or EON's own pane is not a customer.
function cswAskCustomerId() {
  const cid = State.csw ? State.csw.active : null;
  return cid && cid !== CSW_EON_CID && !String(cid).startsWith('team:') ? String(cid) : '';
}
function cswAskCustomerName() {
  const cid = cswAskCustomerId();
  const s = State.csw;
  const t = !cid ? null : (s.activeThread && s.activeThread.zoko_customer_id === cid
    ? s.activeThread : (s.threads || []).find(x => x.zoko_customer_id === cid));
  return t ? cswHouseName(t) : '';
}
let _cswAskToldCustomer = null;
function cswTellAskCustomer() {
  const cid = cswAskCustomerId();
  if (cid === _cswAskToldCustomer) return;
  _cswAskToldCustomer = cid;
  document.dispatchEvent(new CustomEvent('eon-desk-customer', { detail: { customer: cid } }));
}
// Ask EON's door to an assisted order (S02-2). The chat list says, on every
// poll, whether this teammate may prepare one (the server's switch AND order
// access); Ask offers its line only then. open() opens the desk's own Build
// order sheet for the open customer, or says in one sentence why it did not.
function cswAssistedOrderOn() {
  return !!State.csw && State.csw.assistedOrderOn === true;
}
function cswOpenAssistedOrder(cid) {
  if (!cswAssistedOrderOn()) return 'Assisted orders are off on this desk.';
  if (!cid || String(cid) !== cswAskCustomerId()) return 'Open this customer’s chat to prepare an order.';
  const ov = cswOrderSheet({ assisted: true });
  // Ask closes over this sheet next. The sheet takes the keyboard first, so
  // Ask's close leaves it here (#4324); closed, it goes to the chat's menu,
  // never back to Ask, which is shut by then.
  if (ov) {
    ov.restoreTaskFocus = () => cswRestoreFocus(null);
    ov.querySelector('.csw-sheet button:not(.csw-x)')?.focus({ preventScroll: true });
  }
  return '';
}
window.EON_DESK = Object.freeze({ customer: cswAskCustomerId, customerName: cswAskCustomerName });
window.EON_DESK_ORDERS = Object.freeze({ on: cswAssistedOrderOn, open: cswOpenAssistedOrder });

function renderCsInbox() {
  cswState();
  cswThreadMetaInvalidate(); // A rebuilt desk is a new visit, even at the same key.
  // Standalone /cs: "Mine" is the logged-in member. The /staff embed overrides
  // this with the acting rep's email (set right after this returns) so "Mine"
  // reflects threads routed/assigned to THAT rep, not the owner. Reset here so
  // switching back to standalone doesn't keep a stale rep override.
  cswState().asRepEmail = '';
  // lockedQueue: the /staff embed for a NON-CS staffer (logistics/sales) locks
  // the inbox to ONLY the customer threads routed to their queue (they must not
  // see the 120-customer CS desk — Daniel 2026-06-16). Reset for standalone /cs
  // and the CS rep (they see all). Set right after this returns when locked.
  cswState().lockedQueue = '';
  // Role-switcher preview id: reset for standalone /cs and the real CS rep so no
  // stale "view as" leaks across; the /staff embed sets it right after this
  // returns (sosRenderChat).
  cswState().asViewStaffId = '';
  // A lens picked on one surface must not follow into the next (vet
  // 2026-08-08: /staff -> /cs landed on a filtered list, not the book).
  cswState().houseLens = null;
  // The Desk room's freshness dies with a full page re-entry (its poll
  // belongs to the render, not the state) — landing back into it would
  // strand the surface (rig vet). /cs re-entry lands on the rail. The
  // Held-bank-credits room is a menu destination of the same shape, so it
  // follows the same rule; its door is one tap away in the menu.
  if (cswState().mode === 'desk') cswState().mode = 'inbox';
  if (cswState().mode === 'credits') cswState().mode = 'inbox';
  return `
  <div class="csw-shell" id="csw">
    <svg width="0" height="0" style="position:absolute" aria-hidden="true"><defs>
      <linearGradient id="cswGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#4796E3"/><stop offset="50%" stop-color="#9177C7"/><stop offset="100%" stop-color="#CA6673"/>
      </linearGradient>
    </defs></svg>
    <div class="csw-topbar">
      <canvas class="csw-topbar-band" id="csw-topbar-band" aria-hidden="true"></canvas>
      <div class="csw-topleft">
        <button class="csw-back" id="csw-back" aria-label="Home">‹</button>
        <button class="csw-burger" id="csw-burger" aria-label="Rooms and tools" aria-controls="csw-drawer" aria-expanded="false" type="button"><b>Rooms &amp; tools</b></button>
      </div>
      <div class="csw-title" id="csw-title">customers<i>.</i></div>
      <div class="csw-topright"><div class="csw-sub" id="csw-sub">Loading…</div>${cswIsOwner() ? '<button class="csw-notifications-key" id="csw-notifications-key" type="button" aria-label="Your notifications" title="Notifications"><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4"/></svg></button>' : ''}</div>
    </div>
    <div class="csw-body" id="csw-body"></div>
    <div class="csw-drawer-scrim" id="csw-drawer-scrim" hidden></div>
    <aside class="csw-drawer" id="csw-drawer" role="dialog" aria-modal="true" aria-label="Rooms and tools" aria-hidden="true" inert></aside>
  </div>`;
}

// A chat surface is "active" when the standalone CS view is open OR the
// Staff-OS embed is showing its Chat tab. Every freshness path (poll,
// realtime subscribe/refetch, visibility catch-up, desk keys) used to test
// State.view === 'cs' only — but staff PIN sessions boot into 'staffos',
// so inside the embed the poll self-killed on its first tick and realtime
// aborted before subscribing: the primary staff surface never live-updated
// (design vet 2026-07-26, SEV-1).
function cswSurfaceActive() {
  if (typeof State === 'undefined') return false;
  if (State.view === 'cs') return true;
  try {
    return State.view === 'staffos' && sosState.tab === 'chat';
  } catch (_) { return false; }
}

// Aurora anchor #7 (operator 2026-07-26 "make it animated ... use webgl"):
// the customers. LIST topbar band only — FLAT LAW keeps the thread list and
// every transcript flat. Handle is reused across re-binds; a re-rendered
// shell (new canvas) destroys the old handle and mounts fresh.
function cswMountTopbarBand() {
  const c = document.getElementById('csw-topbar-band');
  if (!c || typeof window.mountAurora !== 'function') return;
  const s = cswState();
  if (s._band && s._band._canvas === c) return;
  if (s._band) { try { s._band.destroy(); } catch (_) {} }
  const h = window.mountAurora(c, ['#C8FF4D', '#56E39F', '#7AA6FF'],
    { alpha: true, vignette: false, glow: null, intensity: 0.3 });
  if (!h) return;
  h._canvas = c;
  s._band = h;
}

async function bindCsInbox() {
  const timing = eonTimingStart('desk');
  cswEnsureMediaToken();   // media URLs prefer the scoped token over the JWT
  cswBindKeys();           // desk keyboard model (RS-05)
  cswMountTopbarBand();    // aurora anchor #7 — list topbar band
  // Instant offline/online feedback — the poll tick is up to 10s away.
  if (!State._cswNetBound) {
    State._cswNetBound = true;
    window.addEventListener('offline', () => cswConnCheck());
    window.addEventListener('online', () => cswConnCheck());
  }
  const s = cswState();
  document.getElementById('csw-back')?.addEventListener('click', () => {
    if (s.poll) { clearInterval(s.poll); s.poll = null; }
    cswStopRealtime();
    cswHeldTimerStop();   // the held-draft clock dies with the surface
    State.view = 'home'; render();
  });
  document.getElementById('csw-burger')?.addEventListener('click', cswOpenDrawer);
  document.getElementById('csw-notifications-key')?.addEventListener('click', cswOpenNotifications);
  document.getElementById('csw-drawer-scrim')?.addEventListener('click', cswCloseDrawer);
  cswRenderBody();
  cswLoadShift();
  cswLoadAutomatedBadge();   // drawer badge: pending robo-messaged threads
  cswLoadTeam();             // Team lens threads (folded into this one shell)
  cswDeskHeartbeat();        // WS-E: this device says what it is, every open
  eonTimingFinish(timing, 'shell_bound', 'csw-body');
}

// ── WS-E (2026-09-02) · the device heartbeat ─────────────────────────
// On 2026-08-31 a rep's photo failed on Android with nothing on screen
// and nothing in client_errors. The desk now reports itself on every
// open: which shell it runs, what phone it is, whether push is armed,
// what the camera permission reads, and the last client-error row it
// filed. The server answers with the live shell; a page behind it
// shows the quiet band. The device id is the one login.js mints.
function cswDeviceId() {
  let id = null;
  try { id = localStorage.getItem('eon_device_id'); } catch (_) {}
  if (!id) {
    id = 'd' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
    try { localStorage.setItem('eon_device_id', id); } catch (_) {}
  }
  return id;
}
function cswUaFamily() {
  const ua = String(navigator.userAgent || '');
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) return 'ios';
  if (/Macintosh/i.test(ua)) return 'mac';
  if (/Windows/i.test(ua)) return 'windows';
  return 'other';
}
// armed · failing · none · unsupported — and the endpoint, for the board.
async function cswPushState() {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return { state: 'unsupported', endpoint: '' };
    if (Notification.permission === 'denied') return { state: 'failing', endpoint: '', why: 'notifications denied on this phone' };
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return { state: Notification.permission === 'granted' ? 'failing' : 'none', endpoint: '', why: Notification.permission === 'granted' ? 'no subscription on this phone' : '' };
    return { state: 'armed', endpoint: sub.endpoint || '' };
  } catch (e) { return { state: 'failing', endpoint: '', why: String((e && e.message) || e).slice(0, 80) }; }
}
// The Permissions API's own word for the camera (granted / denied /
// prompt) — the SAME string the Photo key shows when it is 'denied'.
async function cswCameraPermission() {
  try {
    if (!navigator.permissions || !navigator.permissions.query) return 'unknown';
    const st = await navigator.permissions.query({ name: 'camera' });
    return st && st.state ? String(st.state) : 'unknown';
  } catch (_) { return 'unknown'; }
}
async function cswDeviceFacts() {
  const push = await cswPushState();
  const cam = await cswCameraPermission();
  if (cam === 'denied') cswPhotoDeviceReason('camera: denied');
  let errId = null;
  try { errId = sessionStorage.getItem('eon_last_client_error_id') || null; } catch (_) {}
  return {
    device_id: cswDeviceId(),
    shell_version: cswShellBooted(),
    ua_family: cswUaFamily(),
    push_state: push.state,
    push_why: push.why || '',
    push_endpoint: push.endpoint,
    camera_permission: cam,
    last_client_error_id: errId,
  };
}
async function cswDeskHeartbeat() {
  let facts = null;
  try { facts = await cswDeviceFacts(); } catch (_) { return; }
  cswState()._deviceFacts = facts;
  let r = null;
  try {
    r = await api('/api/cs/desk/heartbeat', { method: 'POST', body: JSON.stringify(facts) });
  } catch (_) { return; }
  if (r && r.behind && r.live_shell) cswShowNewDeskBand(r.live_shell);
  if (r && !r.behind && r.live_shell) cswShellHeard(r.live_shell);
}
// The quiet band on a stale shell. One tap rides the fleet-push path
// (ask the SW now, reload once the new worker is active, never under a
// rep's fingers) — the same contract the owner's push uses.
function cswShowNewDeskBand(live) {
  // Operator 6 Sep: updates belong in owner Notifications, never over a chat.
  _cswBoard.pendingVersion = live;
  _cswBoard.currentRun = 0;       // a "behind" stands until two current answers in a row (cswShellHeard)
  cswPaintMenuDot();
  if (document.getElementById('csw-board')) cswRenderBoard();
}

// ── WS-B (2026-09-02) · the board strip: WA · IG · PUSH · SHELL ──────
// The desk's first caller on the governor. Four keys at the head of the
// desk; the WA key becomes the stand-down face when the fleet is down,
// with the owner-gated Stand up riding the existing /eon/governor
// route. IG is permanently dashed. PUSH names THIS device. SHELL is the
// pair the page runs vs the server ships.
let _cswBoard = { data: null, at: 0 };
async function cswLoadBoard(quiet) {
  if (quiet && Date.now() - _cswBoard.at < 55 * 1000) return;
  if (!cswIsOwner()) return;
  let facts = cswState()._deviceFacts;
  if (!facts) { try { facts = await cswDeviceFacts(); cswState()._deviceFacts = facts; } catch (_) { facts = {}; } }
  const q = new URLSearchParams();
  if (facts && facts.push_endpoint) q.set('endpoint', facts.push_endpoint);
  if (cswShellBooted()) q.set('shell', cswShellBooted());
  let d = null;
  try { d = await api('/api/cs/desk/board' + (q.toString() ? '?' + q.toString() : '')); }
  catch (_) { d = null; }
  _cswBoard = { ..._cswBoard, data: d, at: Date.now() };
  if (d && d.shell) cswShellHeard(d.shell.live);
  cswRenderBoard();
}
function cswBoardSince(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!(ms >= 0)) return '';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h}h ${m % 60}m` : `${Math.floor(h / 24)}d`;
}
function cswRenderBoard() {
  // THE BOARD LEAVES THE FOLD (operator, 2026-09-04: "I don't see the
  // point of having these at the top … not a great design"). Four
  // machine chips — WA 35/120, IG no receipts, PUSH re-arm, SHELL p1201
  // — sat on every rep's first glance in system words. Now: nothing,
  // unless THIS phone needs a tap, and then one plain sentence with one
  // button. The owner keeps the quiet facts behind a dot.
  const el = document.getElementById('csw-board');
  const d = _cswBoard.data;
  const facts = cswState()._deviceFacts || {};
  const lines = [];
  // 1 · alerts on this phone — the rep's own device, so the rep is told
  const st = String(facts.push_state || 'none');
  const push = (d && d.push) || {};
  const serverFail = !!push.failing;
  const serverMissing = !!(push.registered === false && st === 'armed');
  // A SCREEN THAT CANNOT RECEIVE PUSH IS NOT A PHONE THAT STOPPED
  // (operator, 2026-09-06: "the system should be smart enough to know").
  // No service worker, no PushManager — a desktop browser, an embedded
  // pane — reads 'unsupported' from cswPushState, and the server may
  // still hold a stale failing record for this device id. Neither is a
  // phone that lost its alerts: the alerts go to the rep's phone, and
  // this board must not send someone to "turn back on" what this device
  // never had. Measured live at 00:40 WAT: the red line sat on an
  // embedded desktop pane on every view, 53px above every conversation.
  const cannotPush = st === 'unsupported';
  // THE ALERT LINE LEAVES THE FOLD (operator, 2026-09-06: "I don't like this
  // here"). It sat above every conversation the moment this phone needed a
  // tap. Now it lives in the menu: a small dot on the menu button says the
  // phone needs a tap, and the sentence with its one button waits inside
  // the drawer (cswDrawerAlertHtml). Nothing about alerts sits above a chat.
  _cswBoard.pushLine = null;
  if (!cannotPush && (st === 'failing' || serverFail || serverMissing)) {
    const why = facts.push_why || (serverFail ? 'the server could not reach this phone' : (serverMissing ? 'this phone is not registered' : ''));
    _cswBoard.pushLine = { tone: 'down', text: "This phone isn't getting alerts.", label: 'Turn them back on', title: why };
  } else if (st === 'none') {
    _cswBoard.pushLine = { tone: 'soon', text: 'Alerts are off on this phone.', label: 'Turn them on', title: '' };
  }
  cswPaintMenuDot();
  if (!el) return; // data still refreshes the menu indicator while the panel is closed
  // 2 · a newer desk is ready — the code on screen (cswShellBooted), never
  // the worker's word, against the live shell (cswShellLive)
  const mineV = cswShellBooted();
  const liveV = cswShellLive(d);
  if (cswShellBehind(mineV, liveV)) {
    lines.push({ tone: 'soon', text: 'A newer desk is ready.', act: { id: 'csw-board-reload', label: 'Reload' }, title: `you run ${mineV}, live is ${liveV}` });
  }
  // 3 · EON stood down — everyone is told in one line; the owner can stand it up
  const wa = (d && d.wa) || {};
  if (wa.standdown) {
    const by = wa.by_role || 'the record';   // plain words: no flag talk reaches a rep
    const since = wa.since ? cswBoardSince(wa.since) : '';
    lines.push({ tone: 'down', text: `EON has stopped sending${since ? ` for ${since}` : ''} — ${by} stood it down.${wa.shadow_active ? ' A 24h shadow is armed.' : ''}`,
                 act: cswIsOwner() ? { id: 'csw-board-standup', label: 'Stand up' } : null, title: '' });
  }
  if (!lines.length) {
    // Nothing for a rep. The owner keeps the quiet facts behind a dot.
    if (!cswIsOwner()) { el.hidden = true; el.innerHTML = ''; return; }
    let quiet;
    if (!d) {
      quiet = "The desk can't read its own health right now.";
    } else {
      const g = (wa.caps || {}).global || {};
      const left = (g.cap != null && g.used != null) ? Math.max(0, Number(g.cap) - Number(g.used)) : null;
      // "Newest" only when BOTH shells are known; an unknown side says nothing.
      // Reaching this branch already means not behind (else the reload line
      // above would show); the check keeps this line honest if that changes.
      // (#3433 renamed mine/live to mineV/liveV and missed this line.)
      quiet = (left != null ? `EON can still send ${left} more messages before the ceiling resets.` : 'EON is sending.')
        + " Instagram doesn't tell us when messages arrive."
        + (mineV && liveV && !cswShellBehind(mineV, liveV) ? ' This desk is on the newest version.' : '');
    }
    el.hidden = false;
    el.innerHTML = `<p class="csw-boardline csw-boardline--quiet" id="csw-board-quiet">${esc(quiet)}</p>`;
    return;
  }
  el.hidden = false;
  el.innerHTML = lines.map(l => `<span class="csw-boardline csw-boardline--${l.tone}" title="${esc(l.title || '')}">${esc(l.text)}`
    + (l.act ? ` <button type="button" class="csw-boardline__act" id="${l.act.id}" title="${esc(l.title || '')}">${esc(l.act.label)}</button>` : '') + `</span>`).join('');
  document.getElementById('csw-board-standup')?.addEventListener('click', cswBoardStandUp);
  document.getElementById('csw-board-rearm')?.addEventListener('click', cswPushRearm);
  document.getElementById('csw-board-reload')?.addEventListener('click', () => {
    const liveV = cswShellLive(d);
    if (liveV) cswShowNewDeskBand(liveV);
    try { cswShellPush({ v: liveV || window.EON_SHELL_VERSION }); } catch (_) {}
  });
}
// The menu button wears a dot while this phone needs a tap for alerts.
function cswPaintMenuDot() {
  const b = document.getElementById('csw-burger');
  if (!b) return;
  const d = _cswBoard && _cswBoard.data;
  const needsAttention = cswIsOwner() && !!(_cswBoard.pushLine || d?.wa?.standdown ||
    cswShellBehind(cswShellBooted(), cswShellLive(d)));
  b.classList.toggle('csw-burger--alert', needsAttention);
}

function cswNotificationFeed(ov) {
  if (!cswIsOwner()) return;
  const host = ov.querySelector('#csw-owner-feed');
  let rows = null, readAt = null, filter = 'unread', error = '', shown = Object.create(null), loading = false, limited = false;
  const rowErrors = new Map(), busy = new Set();
  const paint = () => {
    if (!ov.isConnected || !cswIsOwner()) return;
    const unread = rows?.filter(n => !n.read_at).length;
    const groups = groupDeskNotifications(rows, filter === 'unread');
    const readingCount = groups.reduce((n, group) => n + group.rows.length, 0);
    host.innerHTML = `<div class="csw-seven__tabs" role="group" aria-label="Notification filter"><button type="button" data-filter="unread" ${loading || busy.size ? 'disabled' : ''} aria-pressed="${filter === 'unread'}">Unread${unread == null ? '' : ` · ${unread}`}</button><button type="button" data-filter="all" ${loading || busy.size ? 'disabled' : ''} aria-pressed="${filter === 'all'}">All</button><button type="button" data-refresh ${loading || busy.size ? 'disabled' : ''}>${loading ? 'Loading…' : 'Refresh'}</button></div>
      <p class="csw-seven__muted" role="status">${esc(error ? (rows ? `Refresh failed. Showing the last reading from ${cswEvidenceTime(readAt)}.` : 'Could not load your notifications. Refresh to try again.') : rows ? `${filter === 'unread' ? 'Unread' : 'All'} reading · ${readingCount} updates · read ${cswEvidenceTime(readAt)}. Counts cover this reading.` : 'Loading your notifications…')}</p>
      ${rows && !groups.length ? `<p class="csw-seven__empty">${filter === 'unread' ? 'No unread items in this reading.' : 'No updates in this reading.'}</p>` : ''}
      ${groups.map((group, i) => `<details class="csw-seven__notice-group" ${i === 0 ? 'open' : ''} data-group="${esc(group.key)}"><summary><span>${esc(group.title)}</span><b>${group.rows.length}</b></summary><div>${group.rows.slice(0, shown[group.key] || 20).map(n => {
        const href = safeDeskHref(n.payload?.url, location.origin);
        return `<article class="csw-seven__notice ${n.read_at ? 'is-read' : ''}"><small>${esc(formatRelativeShort(n.created_at) || 'Time not recorded')} · ${n.read_at ? 'Read' : 'Unread'}</small><h4>${esc(n.title || 'Update')}</h4>${n.body ? `<p>${esc(n.body)}</p>` : ''}<div class="csw-seven__actions">${href ? `<a href="${esc(href)}">Open</a>` : ''}${!n.read_at ? `<button type="button" data-read="${esc(n.id)}" ${busy.has(n.id) ? 'disabled' : ''}>${busy.has(n.id) ? 'Saving…' : 'Mark read'}</button>` : ''}</div>${rowErrors.has(n.id) ? '<p class="csw-seven__muted" role="alert">Could not mark read. Try again.</p>' : ''}</article>`;
      }).join('')}${group.rows.length > (shown[group.key] || 20) ? `<button type="button" data-older="${esc(group.key)}">Show 20 more in this group</button>` : ''}</div></details>`).join('')}
      ${limited ? '<p class="csw-seven__muted">This reading is limited to the latest 200 matching updates. Older history is not included.</p>' : ''}`;
    host.querySelectorAll('[data-filter]').forEach(b => b.onclick = () => { filter = b.dataset.filter; rows = null; limited = false; error = ''; shown = Object.create(null); load(); });
    host.querySelector('[data-refresh]').onclick = load;
    host.querySelectorAll('[data-older]').forEach(b => b.onclick = () => { const key = b.dataset.older; shown[key] = (shown[key] || 20) + 20; paint(); const group = [...host.querySelectorAll('[data-group]')].find(x => x.dataset.group === key); if (group) { group.open = true; group.querySelector('summary').focus(); } });
    host.querySelectorAll('[data-read]').forEach(b => b.onclick = async () => {
      const id = b.dataset.read, item = rows.find(x => String(x.id) === id);
      if (!item || busy.has(item.id) || !cswIsOwner()) return;
      const groupKey = b.closest('[data-group]').dataset.group;
      busy.add(item.id); rowErrors.delete(item.id); b.disabled = true; b.textContent = 'Saving…';
      try {
        const acknowledged = await acknowledgeDeskNotification(item, () => api(`/api/notifications/${encodeURIComponent(id)}/read`, { method: 'POST', body: '{}' }));
        item.read_at = acknowledged.read_at;
      } catch (_) { rowErrors.set(item.id, true); }
      finally {
        busy.delete(item.id); paint();
        const group = [...host.querySelectorAll('[data-group]')].find(x => x.dataset.group === groupKey);
        if (group) { group.open = true; group.querySelector('summary').focus(); }
        else host.querySelector('[data-filter]')?.focus();
      }
    });
  };
  const load = async () => {
    if (loading || busy.size || !cswIsOwner()) return;
    loading = true; paint();
    try {
      const result = await api('/api/notifications?limit=200' + (filter === 'unread' ? '&unread_only=true' : ''));
      if (!Array.isArray(result?.notifications)) throw new Error('Unreadable feed');
      limited = result.notifications.length >= 200;
      rows = groupDeskNotifications(result.notifications, false).flatMap(group => group.rows); readAt = new Date().toISOString(); error = ''; shown = Object.create(null);
    } catch (_) { error = 'unavailable'; }
    finally { loading = false; paint(); }
  };
  load();
}

function cswLoopActivity(d, id) {
  const number = n => typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n.toLocaleString() : '—';
  if (id === 'sense') return `<div class="csw-seven__activity"><b>Recorded activity</b><p>${number(d?.counts?.error ? null : d?.counts?.invariant_hits)} rule hits · all time</p><small>A hit records a detected issue; it does not establish that the issue was fixed.</small></div>`;
  if (id === 'score') return `<div class="csw-seven__activity"><b>Recorded activity</b><p>${number(d?.counts?.error ? null : d?.counts?.critic_scores)} review scores · all time</p><small>Review volume is not an improvement score.</small></div>`;
  if (id === 'measure') {
    const ab = d?.model_ab;
    if (ab?.ok !== true || !ab.arms) return '<div class="csw-seven__activity"><b>Outcome comparison unavailable</b><p>No current comparison was supplied.</p></div>';
    return `<div class="csw-seven__activity"><b>Recorded handoffs and closes</b><p>Since ${esc(cswEvidenceTime(ab.since))}. These counts do not show whether EON made service or sales better.</p>${Object.entries(ab.arms).map(([name, a]) => `<div class="csw-seven__metric-row"><strong>${esc(name)}</strong><span>${number(a.n)} chat sessions</span><span>${number(a.qualified_handoffs)} price discussions passed to a rep</span><span>${number(a.handoff_closed)} closed after handoff</span></div>`).join('')}<small>EON discussed the price, then a rep took over. Check whether those customers went on to buy.</small></div>`;
  }
  return '<div class="csw-seven__activity"><b>What is still unknown</b><p>No record of the changes was supplied. The last run time alone does not show what changed or whether it helped.</p></div>';
}


function cswOpenNotifications() {
  if (!cswIsOwner()) return;
  // Both entrances return to the visible direct key, or the drawer button.
  const direct = document.getElementById('csw-notifications-key');
  (direct?.getClientRects().length ? direct : document.getElementById('csw-burger'))?.focus({ preventScroll: true });
  const ov = cswOverlay(`<div class="csw-sheet__head"><h3 id="csw-notifications-title">Notifications</h3><button class="csw-x" type="button" aria-label="Close notifications">✕</button></div>
    <div class="csw-notifications"><p class="csw-notifications__intro">Desk health, updates and alerts for you.</p>
    <div id="csw-notification-device">${cswDrawerAlertHtml()}</div><div id="csw-owner-feed"></div><details class="csw-seven__evidence"><summary>Desk status</summary>
    <div class="csw-boardstrip" id="csw-board" role="status" aria-live="polite" hidden></div></details></div>`, false);
  ov.classList.add('csw-seven');
  cswNotificationFeed(ov);
  ov.querySelector('[role="dialog"]').setAttribute('aria-labelledby', 'csw-notifications-title');
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const paintDevice = () => {
    if (!ov.isConnected) return;
    const host = ov.querySelector('#csw-notification-device');
    host.innerHTML = cswDrawerAlertHtml();
    host.querySelector('#csw-board-rearm')?.addEventListener('click', cswPushRearm);
  };
  paintDevice();
  cswRenderBoard();
  cswLoadBoard(false).then(paintDevice);
}

// The alert line's home: the top of the menu drawer, one sentence, one
// button, the same words and the same re-arm path it had on the strip.
function cswDrawerAlertHtml() {
  const l = _cswBoard && _cswBoard.pushLine;
  if (!l) return '';
  return `<div class="csw-drawer__sec">This phone</div>
    <div class="csw-drawer__alert csw-drawer__alert--${l.tone}" title="${esc(l.title || '')}"><span>${esc(l.text)}</span>
      <button type="button" class="csw-boardline__act" id="csw-board-rearm" title="${esc(l.title || '')}">${esc(l.label)}</button></div>`;
}
// Re-arm: the existing subscribe path, with its once-only latch reset so
// it actually runs again on this device.
async function cswPushRearm() {
  const btn = document.getElementById('csw-board-rearm');
  if (btn) { btn.disabled = true; }
  try {
    if ('Notification' in window && Notification.permission === 'default') {
      try { await Notification.requestPermission(); } catch (_) {}
    }
    _eonPushSynced = false;
    await eonPushSync();
  } catch (_) {}
  cswState()._deviceFacts = null;
  _cswBoard.at = 0;
  await cswLoadBoard();
  cswDeskHeartbeat();
}
// Owner only (the route's own FLAGS_MANAGE gate stands behind the key).
async function cswBoardStandUp() {
  if (!cswIsOwner()) return;
  const btn = document.getElementById('csw-board-standup');
  if (!btn || btn.disabled) return;
  btn.disabled = true; btn.textContent = 'Standing up…';
  try {
    const r = await api('/api/cs/eon/governor', { method: 'POST', body: JSON.stringify({ action: 'stand_up' }) });
    if (r && r.ok) cswToast('Fleet stood up — the 24h shadow is armed');
    else cswToast(cswSayWhy({ message: String((r && r.error) || '') }, { lead: 'Stand up did not take.' }), true);
  } catch (e) { cswToast(cswSayWhy(e, { lead: 'Stand up did not take.' }), true); }
  _cswBoard.at = 0;
  cswLoadBoard();
}

// ── WS-B (2026-09-02) · the Photo key's three honest states ──────────
// 'Photo' · 'Photo · prepare for later' · 'Photo · this
// phone can't attach here · send from gallery'. The reason is the SAME
// string the server or the Permissions API returned — it rides the
// key's title and the sheet's cause line, never a paraphrase. Photo
// stays one press deeper (30 Aug law): this paints the key, it never
// moves it.
function cswPhotoDeviceReason(reason) {
  const s = cswState();
  s._photoDeviceReason = String(reason || '').slice(0, 160) || null;
  cswPaintPhotoKey();
}
function cswPaintPhotoKey() {
  const b = document.querySelector('.csw-tool[data-tool="photo"]');
  if (!b) return;
  const s = cswState();
  const t = s.activeThread;
  if (b.dataset.lead === undefined) b.dataset.lead = (b.textContent.match(/^[^A-Za-z]*/) || [''])[0];
  const lead = b.dataset.lead;
  const windowState = cswReplyWindow(t);
  const shut = windowState.wa && windowState.state === 'closed';
  if (windowState.wa && windowState.state === 'unknown') {
    b.dataset.photoState = 'unverified';
    // Plain words (vet 2026-09-24, fault 20): was "reply window unverified".
    b.title = 'Prepare a photo now. It sends once the reply window is known to be open.';
    b.textContent = `${lead}Photo · not sure the reply window is open`;
    b.classList.add('csw-tool--off');
    return;
  }
  if (shut) {
    const mins = cswWindowMins(t);
    const h = mins != null && mins < 0 ? Math.max(1, Math.round(-mins / 60)) : null;
    b.dataset.photoState = 'shut';
    b.title = 'Prepare a photo now; sending waits for an open reply window';
    b.textContent = `${lead}Photo · prepare for later`;
    b.classList.add('csw-tool--off');
    return;
  }
  if (s._photoDeviceReason) {
    b.dataset.photoState = 'device';
    b.title = s._photoDeviceReason;
    b.textContent = `${lead}Photo · this phone can't attach here · send from gallery`;
    b.classList.remove('csw-tool--off');
    return;
  }
  b.dataset.photoState = 'ok';
  b.title = '';
  b.textContent = `${lead}Photo`;
  b.classList.remove('csw-tool--off');
}

// ── Real-time (Supabase Realtime broadcast) ──────────────────────
// A DB trigger broadcasts a content-free nudge per tenant on every new
// zoko_messages row; we subscribe (anon) and refetch via the AUTHED API.
// No message content crosses the public channel. Kills the 30s poll as
// the primary freshness signal; the poll stays as a 60s safety net.
let _cswSbLib = null;
function cswEnsureSupabase() {
  if (_cswSbLib) return Promise.resolve(_cswSbLib);
  if (window.supabase && window.supabase.createClient) { _cswSbLib = window.supabase; return Promise.resolve(_cswSbLib); }
  // Vendored copy first (/static/ = same-origin, SW-cacheable, immune to
  // ad-blockers / flaky mobile CDN routes — a blocked CDN silently killed
  // real-time for reps and left them on the slow poll). CDN is the fallback.
  const load = (src) => new Promise((resolve, reject) => {
    const sc = document.createElement('script');
    sc.src = src; sc.async = true;
    sc.onload = () => { _cswSbLib = window.supabase; _cswSbLib ? resolve(_cswSbLib) : reject(new Error('supabase lib missing')); };
    sc.onerror = () => reject(new Error('load failed: ' + src));
    document.head.appendChild(sc);
  });
  return load('/static/vendor_supabase.js')
    .catch(() => load('https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2/dist/umd/supabase.js'));
}
function cswRealtimeRefetch() {
  const s = cswState();
  clearTimeout(s._rtDebounce);
  s._rtDebounce = setTimeout(() => {
    if (!cswSurfaceActive() || document.hidden) return;
    cswLoadThreads(true);
    if (s.active && String(s.active).indexOf('team:') !== 0) cswLoadTranscript(s.active, true);
  }, 350);   // coalesce bursts (a customer firing several messages)
}
async function cswStartRealtime(anyScreen) {
  const s = cswState();
  if (s._rtChannel || s._rtStarting) return;   // idempotent
  s._rtStarting = true;
  try {
    const cfg = await api('/api/cs/realtime-config');
    if (!cfg || !cfg.enabled || !cfg.url || !cfg.anon_key) { s._rtStarting = false; return; }  // graceful: poll stays primary
    const lib = await cswEnsureSupabase();
    // A new join needs the chat screen; a move out of the open room
    // (cswRealtimeGoPrivate) joins the private room on any screen.
    if (anyScreen !== true && !cswSurfaceActive()) { s._rtStarting = false; return; }   // navigated away mid-load
    // Private room (vet fault 32, 2026-09-26): this room carries each rep's
    // email and the chat they have open. When the server says the room is
    // private, join ONLY with a short pass from EON; with no pass, stay off
    // the room (the poll keeps the desk fresh) and ask again in a minute —
    // never fall back to the open room. Switch off: nothing below changes.
    const priv = cfg.private === true;
    if (anyScreen === true && !priv) { s._rtStarting = false; return; }   // a move joins only the private room
    let pass = null;
    if (priv) {
      pass = await cswRealtimePass();
      if (!pass || pass.private !== true || !pass.token) { s._rtStarting = false; cswRealtimeAuthArm(pass && pass.refused ? 300000 : 60000); return; }
      if (anyScreen !== true && !cswSurfaceActive()) { s._rtStarting = false; return; }
    }
    // One client per room kind: a pass set on a client stays on it, so a
    // switch either way starts a fresh client.
    if (s._rtClient && !!s._rtClientPrivate !== priv) {
      try { s._rtClient.removeAllChannels(); } catch (_) {}
      s._rtClient = null;
    }
    if (!s._rtClient) {
      s._rtClient = priv
        ? lib.createClient(cfg.url, cfg.anon_key, { realtime: { params: { eventsPerSecond: 5 } },
            auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false } })
        : lib.createClient(cfg.url, cfg.anon_key, { realtime: { params: { eventsPerSecond: 5 } } });
      s._rtClientPrivate = priv;
    }
    if (priv) await s._rtClient.realtime.setAuth(pass.token);   // before subscribe: the join carries the pass
    // PIN sessions have no member.email — without repKey every PIN rep
    // shared the key 'rep' and presence collision-avoidance was dead
    // between any two of them.
    const myKey = (State.member && State.member.email) || State.repKey || 'rep';
    const chCfg = { broadcast: { ack: false }, presence: { key: myKey } };
    // The private join keeps the open room's name ON PURPOSE (lane first
    // vet, 27 Sep). Supabase's server files a private join and a public
    // join of one name under two different server topics,
    // "<project>-private:<name>" and "<project>:<name>", and tracks who is
    // here per server topic (supabase/realtime @8e5f555d3a49:
    // lib/realtime/tenants.ex 515-519, realtime_channel.ex 104,
    // presence_handler.ex 146-155). So a public join to cs:<shop>, even
    // with "Allow public access" on, never hears this room. The name must
    // stay the one mig 915's rules check ('cs:' || the pass's shop):
    // test_the_private_join_and_the_room_rules_name_the_same_room pins them.
    if (priv) chCfg.private = true;
    const ch = s._rtClient.channel(cfg.topic, { config: chCfg });
    ch.on('broadcast', { event: cfg.event || 'cs_msg' }, () => { s._rtBeat = Date.now(); cswRealtimeRefetch(); })
      // Fleet push (Lane U, 2026-08-20): the owner's "Push update to all
      // desks" key lands here as a content-free broadcast carrying the
      // shell version the SERVER ships; cswShellPush decides whether THIS
      // page is behind it. Sits BEFORE the staff_msg handler on purpose —
      // that handler's 400-char span is pinned by test_crew_group_rooms.
      .on('broadcast', { event: 'eon_shell_push' }, (m) => cswShellPush(m && m.payload))
      // Staff↔staff nudge (mig 501 trigger, content-free): route it to the
      // team lens instead of the customer inbox — the open room repaints and
      // the list/badges refresh, both through the authenticated API.
      .on('broadcast', { event: 'staff_msg' }, () => {
        s._rtBeat = Date.now();
        try { cswLoadTeam(true); } catch (_) {}
        try { cswRefreshTeamThread(); } catch (_) {}
      })
      .on('presence', { event: 'sync' }, () => { s._rtBeat = Date.now(); cswOnPresence(ch); })
      .subscribe((status) => {
        s._rtOk = (status === 'SUBSCRIBED');
        if (s._rtOk) { s._rtBeat = Date.now(); cswRealtimeRefetch(); cswUpdatePresence(false); cswConnCheck(); }   // catch up + announce + clear any banner
        cswRenderRtDot();
      });
    s._rtChannel = ch;
    s._rtPrivate = priv;
    // Private: renew the pass at half its life. Open: look again in five
    // minutes in case the owner has made the room private since.
    cswRealtimeAuthArm(priv ? cswRealtimePassDelay(pass) : 300000);
  } catch (e) { /* realtime unavailable → 10s poll remains primary */ }
  s._rtStarting = false;
}
// The pass for the private room, or null when EON could not be asked. The
// answer {private:false} means the owner turned the private room off;
// {refused:true} means EON said no (403) — asking again every minute would
// only repeat the no, so the callers wait five.
async function cswRealtimePass() {
  try { return await api('/api/cs/realtime-token'); }
  catch (e) { return (e && e.status === 403) ? { refused: true } : null; }
}
function cswRealtimePassDelay(pass) {
  const ttl = Number(pass && pass.ttl);
  return Math.max(30, Math.floor((ttl > 0 ? ttl : 900) / 2)) * 1000;
}
function cswRealtimeAuthArm(ms) {
  const s = cswState();
  clearTimeout(s._rtAuthTimer);
  s._rtAuthTimer = setTimeout(cswRealtimeAuthTick, ms);
}
// One timer per desk keeps the pass fresh and follows the owner's switch
// both ways, so the team is never split across two rooms for long. The
// pass lives as long as the room does, not the screen: a PIN rep who steps
// to another Staff-OS tab stays in the room until cswStopRealtime, which
// also clears this timer. A NEW join needs the chat surface, and so does
// the move back to the open room (cswRealtimeMove). The move OUT of the
// open room does not wait for any screen (cswRealtimeGoPrivate).
async function cswRealtimeAuthTick() {
  const s = cswState();
  s._rtAuthTimer = null;
  const ch = s._rtChannel;
  if (!ch) { if (!s._rtStarting && cswSurfaceActive()) cswStartRealtime(); return; }   // private, still waiting for a pass
  if (!s._rtPrivate) {
    // The open room shows this rep's email and open chat to anyone with
    // the shop's public key, so it reads the owner's switch on EVERY
    // screen. It used to ask nothing away from Chat, which kept a PIN rep
    // on another tab published with no end after the owner made the room
    // private (Sol 6.1 review, 1 Oct).
    let cfg = null;
    try { cfg = await api('/api/cs/realtime-config'); } catch (_) {}
    if (s._rtChannel !== ch) return;                 // restarted meanwhile: that start owns the timer
    if (cfg && cfg.private === true) { cswRealtimeGoPrivate(); return; }
    cswRealtimeAuthArm(300000);
    return;
  }
  // Switched off while this rep is away from Chat (Fable minor, 28 Sep):
  // EON has already said the private room is off and signs no pass after
  // that, so asking again every minute would only repeat the answer. Ask
  // nothing until Chat shows again, as the open room does above. Only a
  // "switched off" answer arms this: while the switch is on, the pass
  // keeps renewing away from Chat, so the room does not drop the rep.
  if (s._rtOffSeen === ch && !cswSurfaceActive()) { cswRealtimeAuthArm(60000); return; }
  const pass = await cswRealtimePass();
  if (s._rtChannel !== ch) return;
  if (pass && pass.private === false) { s._rtOffSeen = ch; cswRealtimeMove(); return; }   // switched off: back to the open room
  if (pass && pass.private === true && pass.token) {
    s._rtOffSeen = null;                             // switched on again: renew as before
    try { await s._rtClient.realtime.setAuth(pass.token); } catch (_) {}
    cswRealtimeAuthArm(cswRealtimePassDelay(pass));
    return;
  }
  cswRealtimeAuthArm(pass && pass.refused ? 300000 : 30000);   // a blip: the current pass still has minutes left
}
// Follow the owner's switch into the other room — on a chat screen only.
// Off it (a PIN rep on another Staff-OS tab), a restart would stop this
// room and could not start the new one, leaving the desk in no room with
// no timer until Chat opened again (Fable vet, 27 Sep). So the desk keeps
// the room it has and looks again in a minute; opening Chat restarts the
// room anyway (bindCsInbox), with the switch as it is by then.
function cswRealtimeMove() {
  if (cswSurfaceActive()) { cswRestartRealtime(); return; }
  cswRealtimeAuthArm(60000);
}
// Out of the open room the moment the owner makes the room private, on
// any screen (Sol 6.1 review, 1 Oct). The open room is left first (its
// presence untracked, its channel removed), so the rep's email and open
// chat stop showing even if no pass comes. Then the private room is
// joined with a fresh pass. With no pass the desk stays out of every room
// (the signed-in poll keeps it fresh) and joins again from the chat screen.
function cswRealtimeGoPrivate() {
  try { cswStopRealtime(); } catch (_) { /* the join below starts from no room */ }
  cswStartRealtime(true);
}
// Presence: which OTHER rep is viewing/typing in a thread — collision
// avoidance across the team (two reps shouldn't both answer one customer).
function cswRenewActiveHold(event, cid = cswState().active) {
  const s = cswState();
  // Deliberate work in this conversation only. Never a timer or a new seat.
  if (!event?.isTrusted || document.hidden || !cswSurfaceActive() || !cid || cid !== s.active || cid === CSW_EON_CID) return;
  const originalBall = s.activeThread?.ball;
  const mine = String(State.member?.email || State.repKey || '').toLowerCase();
  const holder = String(originalBall?.holder || '').toLowerCase();
  // A first reply or viewing EON's work is not a lapsed rep hold.
  if (originalBall?.kind !== 'rep' || !(holder === 'you' || (mine && holder === mine))) return;
  const now = Date.now(), attempts = s._holdRenewAt || (s._holdRenewAt = Object.create(null));
  if (attempts[cid] != null && now - attempts[cid] < 45000) return;
  attempts[cid] = now;
  const unconfirmed = async () => {
    if (s.active !== cid || s.activeThread?.ball !== originalBall) return;
    const notices = s._holdRenewNotice || (s._holdRenewNotice = Object.create(null));
    if (!notices[cid])
      cswToast('Your hold could not be confirmed. Checking who is handling this chat.', true);
    notices[cid] = true;
    // Drop the old claim while the authenticated list checks current ownership.
    if (s.activeThread) s.activeThread.ball = null;
    cswPaintBallBar();
    try { await cswLoadThreads(true); } catch (_) { /* ownership stays unconfirmed */ }
    if (s.active === cid) cswPaintBallBar();
  };
  api(`/api/cs/threads/${encodeURIComponent(cid)}/hold/renew`, { method: 'POST', body: '{}' })
    .then(result => {
      if (result?.ok !== true || result.renewed !== true) return unconfirmed();
      if (s._holdRenewNotice) delete s._holdRenewNotice[cid];
    }).catch(unconfirmed);
}

function cswBindHoldActivity(root, cid, selector) {
  if (!root) return;
  if (root._holdActivity) root.removeEventListener('click', root._holdActivity);
  root._holdActivity = event => {
    const control = event.target?.closest(selector);
    if (control && root.contains(control) && !control.disabled) cswRenewActiveHold(event, cid);
  };
  root.addEventListener('click', root._holdActivity);
}

function cswUpdatePresence(typing) {
  const s = cswState();
  if (!s._rtChannel || !s._rtOk) return;
  const me = (State.member && State.member.email) || State.repKey || 'rep';
  // repName beats the raw key: an emailless rep's key is a staff uuid,
  // which must never render in the "<name> is typing" chip.
  const nm = (State.member && State.member.name) || State.repName || me;
  try {
    s._rtChannel.track({ rep: me, name: nm,
                         cid: s.active || null, typing: !!typing, at: Date.now(),
                         // Lane U: the shell this desk runs — the Desk room's
                         // fleet roster reads it off the same presence channel.
                         shell: cswShellBooted() });
  } catch (_) {}
}
function cswOnPresence(ch) {
  const s = cswState();
  const me = (State.member && State.member.email) || State.repKey || 'rep';
  const byCid = {};
  // Lane U: the fleet — one entry per presence key (a rep on two tabs is
  // one desk; the newest `at` wins), ME INCLUDED: the operator's own desk
  // is part of the fleet the Desk room shows.
  const fleet = {};
  try {
    const st = ch.presenceState() || {};
    Object.keys(st).forEach(k => {
      (st[k] || []).forEach(p => {
        if (!p) return;
        const seen = fleet[k];
        if (!seen || (p.at || 0) >= (seen.at || 0)) {
          fleet[k] = { rep: p.rep || k, name: p.name || p.rep || k, shell: p.shell || null, at: p.at || 0 };
        }
        if (p.rep === me || !p.cid) return;
        const cur = byCid[p.cid];
        if (!cur || p.typing) byCid[p.cid] = { name: p.name || p.rep, typing: !!p.typing };
      });
    });
  } catch (_) {}
  s._presenceByCid = byCid;
  s._fleet = Object.keys(fleet).map(k => fleet[k]);
  cswRenderPresence();
  try { if (typeof cswRenderFleet === 'function') cswRenderFleet(); } catch (_) {}
}
// ── Lane U (2026-08-20): the fleet you can see and push ──────────────
// Operator: "is there a way to force an update for all cs rep staffs".
// The keeper (eon_session.js) already converges every page within ~10
// minutes of a deploy; this is the owner's hand on that clock. The push
// is a content-free broadcast carrying the shell version the SERVER
// ships. On arrival, each desk:
//   1. reads the shell it BOOTED on (mine, cswShellBooted) — the keeper
//      overwrites window.EON_SHELL_VERSION with the NEW version the moment
//      the SW activates, while this page is still running the old code, so
//      the global cannot say which code runs;
//   2. asks the SW for the new shell now (the 10-min tick is the slow
//      path). A page already on the pushed version stops here — it is
//      never reloaded;
//   3. 8s later, once the new SW is active (the global now reads the
//      pushed version), reloads — but never under a rep's fingers: while
//      the focus sits in a field it retries every 15s, and after 5 minutes
//      forces anyway (the fleet must converge; the keeper's own cap is the
//      same shape).
function cswShellTyping() {
  const el = eonFocusedElement();   // the cursor in Ask EON's box is typing too
  return !!(el && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT' || el.isContentEditable));
}
function cswShellPush(p) {
  const s = cswState();
  s._rtBeat = Date.now();
  const target = (p && p.v) ? String(p.v) : '';
  const mine = cswShellBooted();   // the code this page runs; the global may already name a newer worker
  try { if (window.EON_SHELL && window.EON_SHELL.check) window.EON_SHELL.check(); } catch (_) {}
  if (!target || !mine) return;                    // nothing to converge on / page's shell unknown
  // ORDERING, not equality (Opus vet F2): a page on the pushed shell OR
  // a newer one is never reloaded — a stale replica answering the push
  // behind a desk that already hard-refreshed, or an offline desk whose
  // worker cannot activate, must not be forced at the 5-minute cap.
  // The deploy stamps the shell (2026-09-15): the pNNN no longer moves, the
  // "+sha7" after it does. Equality is the only "current". The number still
  // refuses a lower-number reload, but the sha7 carries no order: during a
  // rolling deploy a stale replica may ask a newer desk to reload once; that
  // desk then fetches whichever replica answers and settles with the deploy.
  const mineP = cswShellP(mine), targetP = cswShellP(target);
  if (mine === target) return;
  if (mineP !== null && targetP !== null && mineP > targetP) return;
  // ONE navigation per page (F8): the keeper's own polite apply() may
  // reload first (idle desk, SW active at 3s); a second location.reload
  // on a slow network restarts the navigation. The keeper marks it.
  // A SCREEN WITH NO KEEPER HAS NOTHING TO WAIT FOR (independent Fable read
  // of this change, 21 Sep 2026). No service worker means eon_session.js
  // never set window.EON_SHELL or the global, so ready() below could never
  // come true and force() had nothing to call. Before this change such a
  // screen did not know its own shell, so it never offered a reload; now
  // cswShellBooted() knows it, and the owner's embedded pane (no worker,
  // measured 6 Sep — see the note above this board) would show "A newer desk
  // is ready." with a Reload that did nothing for the life of the page.
  // There the files come straight from the server, so a plain reload IS the
  // new shell. Asked at the moment of use, not here: a keeper that registers
  // a second later is still the one we go through.
  const keeper = () => !!(window.EON_SHELL && window.EON_SHELL.force);
  const force = () => {
    if (window.__eonReloading) return;
    if (!keeper()) { window.__eonReloading = true; try { location.reload(); } catch (_) {} return; }
    try { window.EON_SHELL.force(); } catch (_) {}
  };
  // SAFE = the keeper's own contract (F1): hidden, or no typing AND no
  // interaction for 90s. Focus alone is not enough — tapping Send moves
  // focus to a button while the POST is in flight, and a reload there
  // loses the message silently (the composer is already cleared).
  // A SCREEN WITH NO KEEPER COUNTS ITS OWN PAUSE (first vet of this change,
  // 2 Oct 2026): nobody else is watching for taps there, so watch from the
  // moment the push lands — a past we never saw counts as just touched — and
  // reload only after 90s with no tap, key or scroll. Every customer send
  // gives up after 30s (api()), so after 90s quiet no send a tap started can
  // still be in flight. Hidden only stops a cursor left in a field from blocking; it never
  // shortens the 90s.
  if (!keeper() && !window.__cswShellTouched) {
    window.__cswShellTouched = Date.now();
    ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'].forEach((evt) => {
      try { window.addEventListener(evt, () => { window.__cswShellTouched = Date.now(); }, { passive: true, capture: true }); } catch (_) {}
    });
  }
  const safe = () => {
    try { if (window.EON_SHELL && window.EON_SHELL.safe) return !!window.EON_SHELL.safe(); } catch (_) {}
    if (keeper()) return !cswShellTyping();
    const quiet = Date.now() - (window.__cswShellTouched || Date.now()) >= 90 * 1000;
    return quiet && ((typeof document !== 'undefined' && document.hidden) || !cswShellTyping());
  };
  const ready = () => keeper() ? window.EON_SHELL_VERSION === target : true;   // the new SW is active; a reload lands on it (no keeper: nothing to wait for)
  clearTimeout(s._shellPushTimer); clearInterval(s._shellPushRetry);
  s._shellPushTimer = setTimeout(() => {
    if (ready() && safe()) { force(); return; }
    const deadline = Date.now() + 5 * 60 * 1000;
    s._shellPushRetry = setInterval(() => {
      // at the deadline the fleet still converges — but never while the
      // new worker is not live (a reload would land on the same shell) and
      // never mid-typing; past the deadline only the typing guard holds.
      // With no keeper there is no deadline: only the 90s pause may reload.
      if ((ready() && safe()) || (keeper() && Date.now() >= deadline && ready() && !cswShellTyping())) {
        clearInterval(s._shellPushRetry); s._shellPushRetry = null;
        force();
      }
    }, 15 * 1000);
  }, 8000);
}
// pNNN off the shell string ('eon-shell-v3.0p863-…' → 863); null if unknown.
function cswShellP(v) {
  const m = /v3\.0p(\d+)/.exec(String(v || ''));
  return m ? parseInt(m[1], 10) : null;
}
// Is this page's (or a desk's) shell behind the one the server ships? The
// deploy stamps the shell (2026-09-15): the pNNN no longer moves, the "+sha7"
// after it does, so equality is the only "current"; a different pNNN orders
// as before; the same pNNN with a different stamp is an older deploy.
function cswShellBehind(mine, live) {
  mine = String(mine || ''); live = String(live || '');
  if (!mine || !live || mine === live) return false;
  const mp = cswShellP(mine), lp = cswShellP(live);
  if (mp !== null && lp !== null && mp !== lp) return mp < lp;
  return true;
}
// The shell THIS page runs. The deploy stamps EON_BUILD here and SHELL_VERSION
// in sw.js with the same +sha7, so 'eon-shell-' + EON_BUILD is the served
// shell string of the code on screen: exact from the first line, with or
// without a service worker. window.EON_SHELL_VERSION is what the worker
// reports; the keeper overwrites it the moment a new worker takes over, while
// this page keeps running the old code until its polite reload (up to 10
// minutes). "Am I behind?" and "which shell do I run?" read this; only "has
// the new worker taken over?" (cswShellPush's ready) reads the global.
function cswShellBooted() {
  try { return EON_BUILD ? 'eon-shell-' + EON_BUILD : null; } catch (_) { return null; }
}
// Every live shell the desk hears passes through here: the board's answer
// every minute and the heartbeat's when the desk opens. The deploy stamp
// carries no order, so mid-deploy one replica can answer with an older deploy
// than another. A "behind" takes effect at once and is kept in
// _cswBoard.pendingVersion; "current" clears it only when two arrive in a row.
// So one stale reply can neither erase a real "behind" nor pin a false one.
function cswShellHeard(live) {
  if (!live) return;
  const b = _cswBoard;
  if (cswShellBehind(cswShellBooted(), live)) { b.pendingVersion = live; b.currentRun = 0; return; }
  b.currentRun = (b.currentRun || 0) + 1;
  if (b.currentRun >= 2) b.pendingVersion = null;
}
// The live shell this desk measures itself against: the board's latest answer
// or a "behind" still standing (cswShellHeard). One rule for the board line,
// the menu dot and the Reload button: if either says this desk is behind, it
// is; when both do, the board's answer wins, being the fresher.
function cswShellLive(d) {
  const mine = cswShellBooted();
  const known = [d && d.shell && d.shell.live, _cswBoard.pendingVersion].filter(Boolean);
  return known.find(live => cswShellBehind(mine, live)) || known[0] || null;
}
// The Desk room roster: every desk online now with the shell it runs,
// the ones behind the newest seen tinted warn; the owner's push key under it.
function cswRenderFleet() {
  const el = document.getElementById('csw-fleet');
  if (!el) return;
  const s = cswState();
  const fleet = (s._fleet || []).slice()
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  // The reference is the SERVER's shell when we know it (the last push
  // answered with it) or this page's own (Opus vet F6: a fleet where
  // every desk is equally behind showed no tint at all), never only the
  // newest desk online.
  // The deploy stamps the shell (2026-09-15): the reference literal is the
  // server's (the last push answered with it) or this page's own; a desk on a
  // lower pNNN, or on the same pNNN with a different stamp, is behind it.
  const ref = s._fleetRef || window.EON_SHELL_VERSION || null;
  const isOwner = cswIsOwner();
  const rows = fleet.map(f => {
    const n = cswShellP(f.shell);
    const old = cswShellBehind(f.shell, ref);
    return `<div class="csw-fleet__row${old ? ' csw-fleet__old' : ''}">`
      + `<span class="csw-fleet__name">${esc(f.name || f.rep || '—')}</span>`
      + `<span class="csw-fleet__v num">${n !== null ? 'p' + n : '—'}</span></div>`;
  }).join('');
  // Lane A (Lever 8): the owner's money count beside the roster — OPEN
  // money_waiting cards on this shift's book (payment proof, paid /
  // how-to-pay with nobody confirming). Payment stays human; this is
  // the owner seeing it. Non-owners read their own book's cards instead.
  const money = Number(_cswPrio.money_waiting || 0);
  const looksW = Number(_cswPrio.looks_waiting || 0);
  const moneyRow = isOwner
    ? `<div class="csw-fleet__money${money > 0 ? ' csw-fleet__money--live' : ''}" id="csw-fleet-money">money waiting · <span class="num">${money}</span>${looksW > 0 ? ` · looks waiting · <span class="num">${looksW}</span>` : ''}</div>`
    : '';
  el.innerHTML = `<div class="csw-fleet__eyebrow">Desks online now</div>`
    + (rows || `<div class="csw-fleet__row csw-fleet__none">Presence is still connecting</div>`)
    + moneyRow
    + (isOwner ? `<button type="button" class="csw-fleet__push" id="csw-fleet-push"${s._fleetPushing ? ' disabled' : ''}>Push update to all desks</button>` : '')
    + (isOwner ? `<div class="csw-desks" id="csw-desks">${cswDesksHtml()}</div>` : '');
  document.getElementById('csw-fleet-push')?.addEventListener('click', cswFleetPush);
  if (isOwner) cswLoadDesks();
}
// ── WS-E (2026-09-02) · the Desks card — what each phone reports ────
// The presence roster above knows a desk only while its socket is up.
// The heartbeat table knows every device that opened the desk in 7
// days and what it said about itself: shell, push, camera. One state
// word per desk, worst first; named by phone family + the last four of
// the device id, never by person.
let _cswDesks = { rows: [], live_p: null, at: 0 };
function cswDesksHtml() {
  const rows = _cswDesks.rows || [];
  if (!rows.length) return '';
  return `<div class="csw-fleet__eyebrow">Desks · what each phone reports</div>`
    + rows.map(d => {
      const st = String(d.state || 'current');
      const cls = st === 'current' ? '' : (st.indexOf('push') === 0 ? ' csw-desks__fail' : ' csw-desks__old');
      const cam = d.camera_permission === 'denied' ? ' · camera denied' : '';
      return `<div class="csw-desks__row${cls}"><span class="csw-desks__name">${esc(d.label || '—')}</span><span class="csw-desks__st num">${esc(st)}${cam}</span></div>`;
    }).join('');
}
async function cswLoadDesks(force) {
  if (!force && Date.now() - _cswDesks.at < 60 * 1000) {
    const el = document.getElementById('csw-desks');
    if (el) el.innerHTML = cswDesksHtml();
    return;
  }
  let d = null;
  try { d = await api('/api/cs/desk/heartbeats'); } catch (_) { return; }
  if (!d || d.ok === false) return;
  _cswDesks = { rows: d.rows || [], live_p: d.live_p || null, at: Date.now() };
  const el = document.getElementById('csw-desks');
  if (el) el.innerHTML = cswDesksHtml();
}
// ── WS-B (2026-09-02) · the failure ledger by reason (owner) ────────
// Reason · this 7d · what changed against the 7d before · one lamp per
// cause, lit only while a failure of that cause landed in the last 24h.
// The lamp is the alert: it clears itself when the cause clears, so
// nothing here needs an acknowledge tap and nothing here names a rep.
async function cswLoadDeskLedger() {
  if (!cswIsOwner()) return;
  const card = document.getElementById('csw-desk-ledger');
  const host = document.getElementById('csw-desk-ledger-body');
  if (!card || !host) return;
  let d = null;
  try { d = await api('/api/cs/desk/failures?days=7'); } catch (_) { d = null; }
  if (!document.getElementById('csw-desk-ledger-body')) return;
  card.hidden = false;
  if (!d || d.ok === false) { host.innerHTML = '<div class="csw-ledger__none">The ledger could not be read — nothing here is known to be clear.</div>'; return; }
  const rows = d.rows || [];
  if (!rows.length) { host.innerHTML = '<div class="csw-ledger__none">No failed sends in 7 days.</div>'; return; }
  host.innerHTML = `<div class="csw-ledger__head"><span>reason</span><span class="num">7d</span><span class="num">what changed</span></div>`
    + rows.map(r => {
      const dlt = Number(r.delta) || 0;
      const chg = dlt === 0 ? 'same' : (dlt > 0 ? `+${dlt}` : String(dlt));
      return `<div class="csw-ledger__row${r.lamp ? ' csw-ledger__row--live' : ''}" data-cause="${esc(r.cause || '')}">`
        + `<span class="csw-ledger__lamp${r.lamp ? ' on' : ''}" title="${r.lamp ? 'live — a failure of this cause in the last 24h' : 'clear'}" aria-label="${r.lamp ? 'live' : 'clear'}"></span>`
        + `<span class="csw-ledger__why">${esc(r.words || r.cause || '')}${(r.codes || []).length ? ` <i class="num">${esc((r.codes || []).join(' '))}</i>` : ''}</span>`
        + `<span class="csw-ledger__n num">${Number(r.n) || 0}</span>`
        + `<span class="csw-ledger__chg num${dlt > 0 ? ' up' : (dlt < 0 ? ' down' : '')}">${esc(chg)}</span></div>`;
    }).join('')
    + `<div class="csw-ledger__foot num">${Number(d.total) || 0} failed · ${Number(d.prev_total) || 0} the week before · ${Number(d.lamps_lit) || 0} live</div>`;
}
async function cswFleetPush() {
  const s = cswState();
  if (s._fleetPushing) return;
  s._fleetPushing = true;
  const btn = document.getElementById('csw-fleet-push');
  if (btn) btn.disabled = true;
  try {
    const r = await api('/api/cs/desk/push-update', { method: 'POST', body: JSON.stringify({}) });
    if (r && r.ok) { s._fleetRef = r.v || s._fleetRef; cswToast('Pushed — desks refresh within a minute'); cswRenderFleet(); }
    else cswToast('Push did not go out', true);
  } catch (e) {
    cswToast(cswSayWhy(e, { lead: 'Push did not go out.' }), true);
  } finally {
    s._fleetPushing = false;
    const b = document.getElementById('csw-fleet-push');
    if (b) b.disabled = false;
  }
}
function cswRenderPresence() {
  const s = cswState();
  const p = (s._presenceByCid || {})[s.active];
  const el = document.getElementById('csw-presence');
  if (el) el.innerHTML = p ? `<span class="csw-typing">${esc(p.name)} ${p.typing ? 'is typing<span class="csw-typing__d">…</span>' : 'is here'}</span>` : '';
  // annotate inbox rows where another rep is active (small dot)
  document.querySelectorAll('.csw-thread').forEach(b => {
    b.classList.toggle('csw-thread--coactive', !!(s._presenceByCid || {})[b.dataset.cid]);
  });
}
// Tear down + resubscribe — the watchdog's recovery move. Fully resets
// channel state; cswStartRealtime is idempotent via _rtChannel/_rtStarting.
// Gated on the surface predicate, not the raw view: PIN reps sit in the
// Staff-OS Chat tab (view 'staffos'), and a 'cs'-only gate stopped their
// room here and never started it again — at every private-room switch and
// every watchdog heal (adversary pass on vet fault 32, 2026-09-26).
function cswRestartRealtime() {
  try { cswStopRealtime(); } catch (_) {}
  if (cswSurfaceActive()) cswStartRealtime();
}
// Tiny truth-light in the conversation header: green = live socket,
// amber = poll mode (10-30s). Reps stop guessing whether chat is live.
function cswRenderRtDot() {
  const el = document.getElementById('csw-rtdot');
  if (!el) return;
  const ok = !!cswState()._rtOk;
  el.className = 'csw-rtdot' + (ok ? ' csw-rtdot--live' : '');
  el.title = ok ? 'Live — messages arrive instantly'
                : 'Reconnecting — checking every few seconds';
}
function cswStopRealtime() {
  const s = cswState();
  s._rtOk = false;
  try { if (s._rtChannel) s._rtChannel.untrack(); } catch (_) {}
  try { if (s._rtChannel && s._rtClient) s._rtClient.removeChannel(s._rtChannel); } catch (_) {}
  s._rtChannel = null;
  s._presenceByCid = {};
  clearTimeout(s._rtDebounce); clearTimeout(s._typingTimer);
  if (s._rtAuthTimer) { clearTimeout(s._rtAuthTimer); s._rtAuthTimer = null; }   // the pass / switch timer (vet fault 32)
}

function cswRenderBody() {
  const s = cswState();
  const body = document.getElementById('csw-body');
  if (!body) return;
  if (s.poll) { clearInterval(s.poll); s.poll = null; }
  cswStopRealtime();   // (re)started below for inbox mode only
  if (s.mode !== 'inbox') cswFromDrop();  // R5b7: another room is a move
  cswHeldTimerStop();  // restarted by the next transcript paint that has one
  // ⛔ EVERY ROOM IS A BOUNDED SCROLLER, SO THIS BODY IS ALWAYS A FLEX BOX
  // (round eleven of #3228, 18 Sep 2026). `.csw-body` is the fixed shell's
  // flex:1 child with overflow:hidden (cs.css). Each room below is one
  // `.csw-pulse` (flex:1; overflow-y:auto), and that box can only scroll
  // while it is a flex item of this body — that is what gives it the
  // body's height. Since 7 Jun every room (Automated, Desk, Staff Line,
  // then Held bank credits) set this body to display:block, so each room
  // grew to its full content, the body cut it off, and nothing below the
  // fold could be scrolled to or pressed. Measured in Chrome at 390x844:
  // the third held credit's only key sat at y=864-908 on an 844px screen
  // on all four looks, and wheel and swipe both left it there. One value,
  // set here, for every mode — no room chooses its own.
  body.style.display = 'flex';
  if (s.mode === 'automated') {
    // Automated messages — reached on demand from the hamburger drawer
    // (no longer a top tab). Primary list = limited latest automated-message
    // history, not a count of replies owed; abandoned-cart
    // recovery stays available as a collapsed secondary block below it.
    body.innerHTML = `<div class="csw-pulse">
      <div class="csw-autohead"><button class="csw-autohead__back" id="csw-auto-back" aria-label="Back to inbox">‹</button><span class="csw-autohead__t">Automated history</span></div>
      <div class="csw-autox" id="csw-autox"><div class="csw-empty"><div class="csw-empty__m">○</div>Loading automated messages…</div></div>
      <details class="csw-abfold" id="csw-tplfold">
        <summary>Template send history</summary>
        <div class="csw-abx" id="csw-tplx"><div class="csw-empty"><div class="csw-empty__m">○</div>Loading template sends…</div></div>
      </details>
      <details class="csw-abfold" id="csw-galfold">
        <summary>Template gallery</summary>
        <div class="csw-abx" id="csw-galx"><div class="csw-empty"><div class="csw-empty__m">○</div>Loading templates…</div></div>
      </details>
      <details class="csw-abfold" id="csw-abfold">
        <summary>Abandoned carts</summary>
        <div class="csw-abx" id="csw-abx"><div class="csw-empty"><div class="csw-empty__m">○</div>Loading abandoned carts…</div></div>
      </details>
    </div>`;
    cswLoadAutomated();
    cswMountTabbar(body);
    // Lazy: only fetch on fold-open. Template send history = limited 30d
    // outbound records with rendered copy + recorded status
    // (operator 2026-07-26: "cant i see old delivered template").
    const tfold = document.getElementById('csw-tplfold');
    tfold?.addEventListener('toggle', () => { if (tfold.open && !tfold.dataset.loaded) { tfold.dataset.loaded = '1'; cswLoadTemplateLog(); } });
    // Template gallery (2026-08-02, fr 8e666ddb view 3): one card per
    // distinct template — what it reads like + 14d sent/failed counts.
    const gfold = document.getElementById('csw-galfold');
    gfold?.addEventListener('toggle', () => { if (gfold.open && !gfold.dataset.loaded) { gfold.dataset.loaded = '1'; cswLoadTemplateGallery(); } });
    const fold = document.getElementById('csw-abfold');
    fold?.addEventListener('toggle', () => { if (fold.open && !fold.dataset.loaded) { fold.dataset.loaded = '1'; cswLoadAbandoned(); } });
    // Back to the inbox (Automated is a drawer destination, not a tab).
    document.getElementById('csw-auto-back')?.addEventListener('click', () => { s.mode = 'inbox'; cswRenderBody(); });
  } else if (s.mode === 'desk') {
    // The Desk room (B2b): the rep's own address — the shift ring, the
    // holds queue, and the doors the hamburger used to hide. The queue
    // is why the room exists: a held reply auto-releases at its clock,
    // and until this room that clock was only visible inside its own
    // thread — a send 40 seconds out on a thread nobody had open was
    // invisible until it had already happened.
    body.innerHTML = `<div class="csw-pulse csw-deskroom" id="csw-deskroom">
      <div class="csw-autohead"><button type="button" class="csw-autohead__back" id="csw-desk-back" aria-label="Back to inbox">‹</button><span class="csw-autohead__t">Your desk</span></div>
      <div class="csw-card csw-deskcard">
        <div class="csw-card__t">Your shift</div>
        <div class="csw-deskring">
          <span class="csw-hq__ring csw-deskring__r" title="Priority cards handled this shift, out of all the cards on this shift's list"><svg viewBox="0 0 24 24" aria-hidden="true"><circle class="b" cx="12" cy="12" r="10"></circle><circle class="f" id="csw-desk-arc" cx="12" cy="12" r="10"></circle></svg></span>
          <div class="csw-deskring__bx"><div class="csw-deskring__v num" id="csw-desk-frac">—</div>
          <div class="csw-deskring__l">Priority cards handled this shift</div></div>
          <span class="csw-rehearse" hidden><i></i>REHEARSAL — NOTHING SENDS</span>
        </div>
        <div class="csw-fleet" id="csw-fleet"></div>
      </div>
      <div class="csw-card csw-deskcard">
        <div class="csw-card__t">Leaving these behind</div>
        <div id="csw-parked"><div class="csw-empty"><div class="csw-empty__m">○</div>Reading what is still open…</div></div>
        <div class="csw-desklaw">Six words each, off the chat's own last step — so the next person
          knows what is owed without reading sixty messages. No names on this card.</div>
      </div>
      <div class="csw-card csw-deskcard">
        <div class="csw-card__t">EON's held replies</div>
        <div id="csw-desk-holds"><div class="csw-empty"><div class="csw-empty__m">○</div>Reading the queue…</div></div>
        <div class="csw-desklaw">Open a held reply to review its words and current release conditions.
          In rehearsal, replies stay held. Edits live in the conversation.</div>
      </div>
      <div class="csw-card csw-deskcard" id="csw-contact-upcoming" hidden></div>
      <div class="csw-card csw-deskcard" id="csw-contact-rules" hidden></div>
      <div class="csw-card csw-deskcard" id="csw-desk-ledger" hidden>
        <div class="csw-card__t">Failures by reason · 7 days</div>
        <div id="csw-desk-ledger-body"></div>
        <div class="csw-desklaw">One lamp per cause — lit while the cause is live, clear when it clears. By reason, never by person.</div>
      </div>
      <div class="csw-card csw-deskcard">
        <div class="csw-card__t">Doors</div>
        <div id="csw-desk-doors"></div>
      </div>
    </div>`;
    document.getElementById('csw-desk-back')?.addEventListener('click', () => {
      cswTabGo('chats'); document.getElementById('csw-burger')?.focus();
    });
    cswRenderDeskDoors();
    cswLoadParked();       // WS-C: the handover — six words per parked thread
    cswContactRules();
    cswContactUpcoming();
    if (!cswHouse()) cswLoadHolds(true); // Classic reaches this room through its menu.
    cswLoadDeskLedger();   // WS-B: the owner's failure ledger by reason
    cswDeskRing();
    cswRenderFleet();   // Lane U: the fleet roster + the owner's push key
    // Lane U: the room keeps the realtime channel — presence is what the
    // roster reads (a parked Desk would otherwise show the fleet as it
    // stood when the room opened, and drop this desk off everyone
    // else's), and the owner watches the versions converge here after a
    // push. Idempotent; cswTabGo nulled s.active so presence tracks no
    // thread from this room and the nudge handlers have no transcript
    // to repaint.
    cswStartRealtime();
    cswDeskTickerStart();
    // THE RELAY (R5): "Your asks" — raised and owned, split. Mounted,
    // never reserved: with EON_RELAY off this returns before it has
    // touched the room and the Desk is the Desk it was yesterday.
    cswRelayMountChips(true);
    cswHoldDeskMount();   // LANE B: holds today · N · bought M
    // The room keeps its own modest freshness: this render killed the
    // inbox poll, and a parked Desk would otherwise never learn of a
    // new hold or repaint the badge (rig vet). Self-kills with the room.
    s.poll = setInterval(() => {
      if (!document.getElementById('csw-deskroom')) { clearInterval(s.poll); s.poll = null; return; }
      if (document.hidden) return;
      cswLoadHolds(true);
      cswLoadThreads(true);
      // No force: the 60s chip cache answers most ticks — a dark desk
      // pays one scoped request a minute at most, not one per poll
      // (R5 verifier, F1).
      cswRelayMountChips();
      cswHoldDeskMount();
    }, 30000);
    cswMountTabbar(body);
  } else if (s.mode === 'staffline') {
    // EON Staff Line — owner-only internal channel (drawer destination;
    // the entry only renders for owner/admin and every API behind it is
    // require_owner_or_admin, so a rep can't reach it either way).
    body.innerHTML = `<div class="csw-pulse">
      <div class="csw-autohead"><button class="csw-autohead__back" id="csw-sl-back" aria-label="Back to inbox">‹</button><span class="csw-autohead__t">Staff Line</span>
        <button class="csw-mini" id="csw-sl-onboard" style="margin-left:auto" title="Sends the login + install walkthrough to YOUR WhatsApp only">Test onboarding on me</button></div>
      <div id="csw-sl-requests"></div>
      <div id="csw-sl-threads"><div class="csw-empty"><div class="csw-empty__m">○</div>Loading staff line…</div></div>
      <div id="csw-sl-thread" hidden></div>
    </div>`;
    cswMountTabbar(body);
    document.getElementById('csw-sl-back')?.addEventListener('click', () => { s.mode = 'inbox'; cswRenderBody(); });
    document.getElementById('csw-sl-onboard')?.addEventListener('click', async (e) => {
      e.target.disabled = true;
      try {
        const r = await api('/api/staff-line/onboarding/test', { method: 'POST', body: '{}' });
        cswToast(r.ok ? `Onboarding sent to ${r.sent_to} (you)` : 'Send failed — check your staff phone in People OS', !r.ok);
      } catch (err) { cswToast('Send failed — is your phone on your staff row?', true); }
      e.target.disabled = false;
    });
    cswStaffLineLoad();
  } else if (s.mode === 'credits') {
    // HELD BANK CREDITS (round eleven of #3228, 18 Sep 2026). The card
    // that ends a hold used to live only in the Desk room, and the Desk
    // room is the house theme's — so a rep on dark, light or flow had no
    // control at all for a transfer the bank saw and nobody claimed. This
    // room is a menu destination like Automated messages and Staff Line:
    // the SAME card, in a shell every skin already dresses (.csw-pulse,
    // .csw-autohead, cs.css, unscoped). The house keeps its Desk-room
    // copy; this is not a second control, it is a second door to the one
    // control.
    // ⛔ THIS ROOM IS A COLUMN, NOT THE TWO-COLUMN DESKTOP GRID (the first
    // read of round eleven's head, 19 Sep 2026). `.csw-pulse` turns into
    // `grid-template-columns:340px 1fr` above 880px (cs.css:1129), and a
    // room that does not say otherwise drops its card into the 340px
    // column. Measured in Chrome at 1280: the money card was 340px wide
    // inside a 1236px room, with 880px of empty column beside it, on all
    // four looks. Every other room says otherwise in the sheet —
    // `.csw-autohead` and `.csw-autox,.csw-abfold` span 1/-1 (cs.css:1588,
    // 1594) and the house Desk room is a flex column outright (cs.css:4117)
    // — but saying it here costs no stylesheet byte, so floor 3's design
    // records still describe the tree exactly. Below 880px this is what the
    // sheet already sets, so nothing changes on a phone.
    body.innerHTML = `<div class="csw-pulse" id="csw-creditroom" style="display:flex;flex-direction:column">
      ${/* ⛔ THE WAY OUT OF THE MONEY ROOM IS A FINGER TARGET TOO. Measured
           in real Chrome on all four looks at 1280 and 375: this back
           control renders 32x32, under floor 3's own 44px contract, while
           the two money keys beside it are 44. Said in the markup, the way
           round twelve said this room is one column, so NO STYLESHEET BYTE
           MOVES and floor 3's registered records still describe the tree
           exactly. The ‹ stays the size it was; only the target grows. */''}
      <div class="csw-autohead"><button type="button" class="csw-autohead__back" id="csw-credit-back" aria-label="Back to inbox" style="min-width:44px;min-height:44px">‹</button><span class="csw-autohead__t">Held bank credits</span></div>
      <p class="crd-receipt__note" id="csw-creditrest" role="status">Reading the bank credits held for a person…</p>
    </div>`;
    cswMountTabbar(body);
    document.getElementById('csw-credit-back')?.addEventListener('click', () => { s.mode = 'inbox'; cswRenderBody(); document.getElementById('csw-burger')?.focus({ preventScroll: true }); });
    cswHeldCreditsMount();
    // The room keeps its own modest freshness — the Desk room's cadence,
    // for the same reason: this render killed the inbox poll, and a hold
    // another rep just ended must leave this list. Self-kills with the
    // room.
    s.poll = setInterval(() => {
      if (!document.getElementById('csw-creditroom')) { clearInterval(s.poll); s.poll = null; return; }
      if (document.hidden) return;
      cswHeldCreditsMount();
    }, 30000);
  } else {
    body.innerHTML = cswInboxShell();
    cswBindFilters();
    cswBindComposer();
    cswMountTabbar(body);
    // OS-banner permission: must come from a real tap (browser rule) — the
    // rep's first click anywhere in the workspace asks once, then never again.
    if (!s._notifAskBound) {
      s._notifAskBound = true;
      document.body.addEventListener('click', eonNotifyAsk, { once: true });
    }
    cswLoadThreads();
    cswPhotoPickResume();   // a reload that ate a gallery pick resumes in one tap
    cswLoadBoard();         // WS-B: WA · IG · PUSH · SHELL at the head of the desk
    cswBindFlapVerbs();     // WS-B: the flap's one verb (delegated, once)
    // re-open the active conversation (customer or team) after a body re-render.
    if (s.active && String(s.active).indexOf('team:') === 0) {
      const tid = String(s.active).slice(5);
      if (tid && tid !== 'new') cswOpenTeamThread(tid);
    } else if (s.active) { cswOpenThread(s.active, true); }
    s._tick = 0;
    // Clear any previous interval before starting a new one: every Staff-OS
    // Chat-tab entry re-runs bindCsInbox, and an orphaned interval can never
    // clear ITSELF (its tick clears whatever s.poll currently points to) —
    // each entry leaked an immortal 10s timer (design vet 2026-07-26).
    if (s.poll) { clearInterval(s.poll); s.poll = null; }
    s.poll = setInterval(() => {
      // Self-terminate if the rep navigated away from every chat surface
      // (the SPA router re-renders without clearing this) — was a permanent
      // background drain. Re-entering the surface re-runs bindCsInbox.
      // The backstop for every other way out (a view change, a deep link, a
      // surface this file does not name): the desk has already worked out that
      // the rep is no longer standing on a chat surface, so let go of the thread
      // here too. The two synchronous doors above catch the common cases first —
      // this one catches whatever they miss, within a tick.
      if (!cswSurfaceActive()) { clearInterval(s.poll); s.poll = null; cswHeldTimerStop(); cswLeaveDesk(); return; }
      if (document.hidden) return;   // pause network while backgrounded; awareness resumes on focus
      s._tick++;
      cswConnCheck();   // surface offline / reconnecting (ST-05) every tick
      if (s._tick % 6 === 0) cswLoadBoard(true);   // WS-B: the strip re-reads each minute
      // When real-time is connected the poll is just a safety net — the
      // broadcast nudge drives freshness. But on phones the socket dies
      // every time the app backgrounds and reports stale-healthy for up
      // to a minute after return (Gift, 2026-07-24: "I'm not getting
      // messages in real time"), so the ACTIVE thread nets at 20s even
      // when rt looks fine; inbox stays at 60s. Without rt: thread 10s,
      // inbox 30s.
      // BEAT-WATCHDOG (p403): an iOS-suspended WebSocket can also die
      // IN VIEW without any status event — _rtOk stays stale-true. No
      // beat (broadcast/presence/subscribe-ack) for 90s -> declare it
      // down, resubscribe, and ride the fast cadence until it re-acks.
      if (s._rtOk && (Date.now() - (s._rtBeat || 0)) > 90000) {
        s._rtOk = false;
        cswRestartRealtime();
      }
      // Team (staff↔staff) threads have no realtime broadcast of their own,
      // so they ride the SAME active-thread cadence as customer threads —
      // they used to be excluded from both branches entirely.
      const onTeam = s.active && String(s.active).indexOf('team:') === 0;
      if (s._rtOk) {
        if (s.active && !onTeam && s._tick % 2 === 0) cswLoadTranscript(s.active, true);
        if (onTeam && s._tick % 2 === 0) cswRefreshTeamThread();
        if (s._tick % 6 === 0) { cswLoadThreads(true); cswLoadTeam(true); if (cswHouse()) cswLoadHolds(true); }
        return;
      }
      if (s.active && !onTeam) cswLoadTranscript(s.active, true);
      if (onTeam) cswRefreshTeamThread();
      if (s._tick % 3 === 1) { cswLoadThreads(true); cswLoadTeam(true); if (cswHouse()) cswLoadHolds(true); }
    }, 10000);
    cswStartRealtime();   // kill the poll's role: subscribe to live nudges
    // Instant catch-up when the rep returns to the tab. Unconditional
    // (was gated on inbox mode) AND heals the realtime channel: iOS
    // kills the socket while backgrounded, supabase-js can report
    // stale-healthy for ~a minute, and a rep app-switching between
    // WhatsApp and EON lives inside that dead zone permanently.
    if (!s._visBound) {
      s._visBound = true;
      document.addEventListener('visibilitychange', () => {
        if (document.hidden || !cswSurfaceActive()) return;
        const cs = cswState();
        // ALWAYS catch up on return — the old inbox-mode-only gate meant
        // a rep sitting INSIDE a conversation got no refetch at all when
        // they came back to the app (Gift's exact report, 2026-07-24).
        cswLoadThreads(true); cswLoadTeam(true);
        if (cs.active && String(cs.active).indexOf('team:') !== 0) cswLoadTranscript(cs.active, true);
        // iOS suspends PWA sockets while backgrounded; returning is the
        // moment to verify the channel survived — heal on EITHER signal:
        // the channel object says not-joined (#1321), or no beat for 90s
        // (p403 watchdog; catches the stale-healthy socket #1321 misses).
        try {
          const notJoined = cs._rtChannel && cs._rtChannel.state !== 'joined';
          const beatStale = (Date.now() - (cs._rtBeat || 0)) > 90000;
          if (notJoined || beatStale) { cs._rtOk = false; cswRestartRealtime(); }
        } catch (_) {}
        cswRenderRtDot();
      });
    }
  }
}

// ── helpers: avatar, ring, gauge ─────────────────────────────
function cswHash(seed) { let h = 0; const x = String(seed || '?'); for (let i = 0; i < x.length; i++) h = (h * 31 + x.charCodeAt(i)) >>> 0; return h; }
function cswAvatarStyle(seed) { const h = cswHash(seed); const a = h % 360, b = (a + 48) % 360; return `background:linear-gradient(135deg,hsl(${a} 68% 52%),hsl(${b} 70% 44%));`; }
function cswInitials(name) { const p = (name || '').trim().split(/\s+/); let i = ((p[0] && p[0][0]) || '') + ((p[1] && p[1][0]) || ''); if (!i) { const m = (name || '').match(/[A-Za-z0-9]/); i = m ? m[0] : '?'; } return i.toUpperCase(); }
function cswAvatar(name, opts) {
  opts = opts || {};
  const cls = 'csw-av' + (opts.size ? ' csw-av--' + opts.size : '') + (opts.live ? ' csw-av--live' : '');
  const img = opts.url ? `<img src="${esc(opts.url)}" alt="" onerror="this.remove()"/>` : '';
  const pres = opts.pres ? `<span class="csw-av__pres csw-av__pres--${opts.pres}"></span>` : '';
  // 2026-06-11: top-corner flag — buy-intent (₦, "high chance of buying")
  // takes priority; otherwise a returning-customer star. Bottom corner
  // keeps the channel presence dot, so the two never collide.
  const flag = opts.hot
    ? '<span class="csw-av__flag csw-av__flag--hot" title="High buy intent">₦</span>'
    : (opts.vip ? '<span class="csw-av__flag csw-av__flag--vip" title="Returning customer">★</span>' : '');
  return `<div class="${cls}" style="${cswAvatarStyle(opts.seed || name)}"><span class="csw-av__ring"></span>${img}<span>${esc(cswInitials(name))}</span>${pres}${flag}</div>`;
}
// No-emoji glyphs (Daniel rule) for the CS Pulse streak — inline SVG flame /
// snowflake, consistent with the sos* SVG glyph helpers. PR-F #5.
function cswFlame() {
  return '<svg class="csw-glyph csw-glyph--flame" viewBox="0 0 24 24" aria-hidden="true">'
    + '<path d="M12 3c1.5 3 4.5 4.2 4.5 8a4.5 4.5 0 0 1-9 0c0-1.4.6-2.4 1.3-3.3.5 1 .9 1.3 1.7 1.3.8 0 1.2-.9.8-2.1C11.3 5.4 11.5 4 12 3Z"/></svg>';
}
function cswSnow() {
  return '<svg class="csw-glyph csw-glyph--snow" viewBox="0 0 24 24" aria-hidden="true">'
    + '<path d="M12 3v18M5 7l14 10M19 7 5 17"/></svg>';
}

function cswRing(pts, target) {
  const pct = Math.max(0, Math.min(1, target ? pts / target : 0));
  const r = 46, c = 2 * Math.PI * r, off = c * (1 - pct);
  return `<div class="csw-ring ${pts >= target ? 'csw-ring--closed' : ''}">
    <svg viewBox="0 0 108 108"><circle class="csw-ring__bg" cx="54" cy="54" r="${r}"/>
    <circle class="csw-ring__fg" cx="54" cy="54" r="${r}" stroke-dasharray="${c.toFixed(1)}" stroke-dashoffset="${off.toFixed(1)}"/></svg>
    <div class="csw-ring__c"><div class="csw-ring__pts">${pts}</div><div class="csw-ring__tot">/ ${target}</div></div></div>`;
}
function cswGauge(pct, label, display, stroke) {
  const r = 33, c = 2 * Math.PI * r, off = c * (1 - Math.max(0, Math.min(1, (pct || 0) / 100)));
  return `<div class="csw-gauge"><div class="csw-gauge__ring"><svg viewBox="0 0 78 78">
    <circle class="csw-gauge__bg" cx="39" cy="39" r="${r}"/>
    <circle class="csw-gauge__fg" cx="39" cy="39" r="${r}" stroke="${stroke || 'url(#cswGrad)'}" stroke-dasharray="${c.toFixed(1)}" stroke-dashoffset="${off.toFixed(1)}"/></svg>
    <div class="csw-gauge__v">${display}</div></div><div class="csw-gauge__l">${esc(label)}</div></div>`;
}
function cswFmtSec(x) { if (x == null) return '—'; if (x < 60) return x + 's'; if (x < 3600) return Math.round(x / 60) + 'm'; return Math.round(x / 3600) + 'h'; }
function cswNgn(n) { return '₦' + Math.round(n || 0).toLocaleString(); }
// The line printed under a chat close or conversion rate: how many of the
// window's chat payments the rate beside it counts (cs_payment_coverage,
// the 20 Sep review). A failed read arrives as null and prints nothing,
// never "0 of 0". `short` picks the compact wording for tight spots.
function cswPayCoverage(cov, short) {
  if (!cov || typeof cov !== 'object') return '';
  if (cov.eligible == null || cov.credited == null) return '';
  const words = String((short ? cov.short : cov.line) || '').trim();
  return words ? esc(words) : '';
}

// ── PULSE (retired 2026-06-18) ───────────────────────────────
// The Pulse performance dashboard was removed from the chat workspace top
// bar — the Performance OS block in Staff OS owns rep performance now. These
// renderers are kept (not deleted) only because cswGauge/cswFmtSec they share
// are still used by the Staff OS Score view; nothing calls cswLoadPulse.
function cswPulseSkeleton() {
  return `<div class="csw-pulse">
    <div class="csw-card csw-skel" style="height:150px"></div>
    <div class="csw-card csw-skel" style="height:160px"></div>
    <div class="csw-card csw-skel" style="height:120px"></div>
    <div class="csw-card csw-skel" style="height:180px"></div></div>`;
}

// Lane P: the on-shift roster's names, kept so the response clock can say
// "Gift" where the ledger only knows an email. Filled by cswLoadShift;
// the clock falls back to the email's local part when a rep is not here.
const _cswShiftNames = {};
async function cswLoadShift() {
  try {
    const d = await api('/api/cs/shifts/now');
    (d.on_shift || []).forEach(r => {
      if (r && r.rep_email) _cswShiftNames[String(r.rep_email).toLowerCase()] = r.rep_name || '';
    });
    const sub = document.getElementById('csw-sub');
    if (!sub) return;
    const names = (d.on_shift || []).map(r => r.rep_name).join(', ');
    sub.textContent = names ? `On shift: ${names}` : 'No one on shift right now';
  } catch (_) {}
}

async function cswLoadPulse() {
  const s = cswState();
  let d;
  try { d = await api('/api/cs/metrics/overview?days=7'); }
  catch (e) {
    const b = document.getElementById('csw-body');
    if (b) b.innerHTML = `<div class="csw-empty"><div class="csw-empty__m">!</div>Couldn't load metrics: ${esc(e.message || e)}</div>`;
    return;
  }
  s.metrics = d;
  const perf = (d.performance && d.performance.team_totals) || {};
  const conv = (d.conversion && d.conversion.team) || {};
  const convReps = (d.conversion && d.conversion.reps) || [];
  const vol = (d.volume && d.volume.by_channel) || {};
  const board = (d.gamification && d.gamification.leaderboard) || [];
  const target = (d.gamification && d.gamification.daily_target) || 50;
  const shift = (d.on_shift && d.on_shift.on_shift) || [];
  const why = d.why || {};
  const tg = d.team_goal || {};

  // who is "me" → on-shift rep, else top of leaderboard
  const myRepId = (shift[0] && shift[0].rep_email) || (board[0] && board[0].rep_id);
  const myName = (shift[0] && shift[0].rep_name) || (board[0] && board[0].name) || 'You';

  const respPct = perf.response_ratio_pct;
  const convPct = conv.conv_rate_pct || 0;
  const volTotal = (vol.dm || 0) + (vol.comment || 0) + (vol.story || 0);
  const convColor = 'var(--accent)';  // ink-mode accent (design pass 01)

  const needs = [];
  if (perf.unanswered_threads) needs.push({ cls: 'csw-need--alert', n: perf.unanswered_threads, l: `unanswered thread${perf.unanswered_threads === 1 ? '' : 's'} — reply now`, go: 'inbox' });
  if (why.refund_threads) needs.push({ cls: 'csw-need--hot', n: why.refund_threads, l: 'refund / return signals this month', go: 'inbox' });
  if (why.quality_threads) needs.push({ cls: '', n: why.quality_threads, l: 'quality complaints flagged', go: 'inbox' });
  if (!needs.length) needs.push({ cls: '', n: '✓', l: 'All caught up — nothing waiting', go: null });

  const convByRep = {};
  convReps.forEach(r => { if (r.rep_id) convByRep[r.rep_id.toLowerCase()] = r; });
  const maxPts = Math.max(target, ...board.map(b => b.points || 0));

  const html = `<div class="csw-pulse">
    <div class="csw-card csw-hero" style="animation-delay:0ms">
      <div class="csw-hero__row">
        ${cswRing(0, target)}
        <div class="csw-hero__meta">
          <div class="csw-hero__name">${esc(myName)}</div>
          <div class="csw-level" id="csw-level"></div>
          <div class="csw-streak" id="csw-streak">…</div>
          <div class="csw-hero__hint">Reply fast to fill your ring. 50 = day complete.</div>
        </div>
      </div>
    </div>

    <div class="csw-card" style="animation-delay:40ms">
      <div class="csw-card__h"><div class="csw-card__t">Team pulse · 7 days</div></div>
      <div class="csw-gauges">
        ${cswGauge(respPct, 'Response', respPct == null ? '—' : respPct + '%')}
        ${cswGauge(perf.median_first_response_seconds != null ? Math.max(8, 100 - perf.median_first_response_seconds / 6) : 0, 'Median speed', cswFmtSec(perf.median_first_response_seconds))}
        ${cswGauge(convPct, 'Conversion', convPct + '%', convColor)}
        ${cswGauge(Math.min(100, volTotal / 15), 'Volume', String(volTotal))}
      </div>
    </div>

    ${tg.target ? `<div class="csw-card" style="animation-delay:60ms">
      <div class="csw-card__h"><div class="csw-card__t">Team goal · this week</div><div class="csw-card__t">${tg.points} / ${tg.target}</div></div>
      <div class="csw-goalbar"><i style="width:${Math.min(100, tg.pct || 0)}%"></i></div>
      <div class="csw-goalmeta">${tg.hit ? 'Hit — team bonus unlocked' : `${Math.round(tg.pct || 0)}% there · everyone shares the win`}</div>
    </div>` : ''}

    <div class="csw-card" style="animation-delay:80ms">
      <div class="csw-card__h"><div class="csw-card__t">Needs you</div></div>
      <div class="csw-needs">
        ${needs.map(nd => `<button class="csw-need ${nd.cls}" ${nd.go ? `data-go="${nd.go}"` : 'disabled'}>
          <span class="csw-need__n">${esc(String(nd.n))}</span>
          <span class="csw-need__l">${esc(nd.l)}</span>
          ${nd.go ? '<span class="csw-need__arrow">›</span>' : ''}
        </button>`).join('')}
      </div>
    </div>

    <div class="csw-card" style="animation-delay:120ms">
      <div class="csw-card__h"><div class="csw-card__t">Leaderboard · this week</div><div class="csw-card__t">conv · AOV</div></div>
      <div class="csw-board">
        ${board.length ? board.map((b, i) => {
          const cr = convByRep[(b.rep_id || '').toLowerCase()] || {};
          const w = Math.round(100 * (b.points || 0) / maxPts);
          return `<div class="csw-rep ${i === 0 ? 'csw-rep--top' : ''}">
            <div class="csw-rep__rank">${i + 1}</div>
            ${cswAvatar(b.name, { size: 'sm', seed: b.rep_id })}
            <div class="csw-rep__meta">
              <div class="csw-rep__name">${esc(b.name || '—')}</div>
              <div class="csw-rep__role">${esc(b.role || 'Customer Service')}</div>
              <div class="csw-rep__bar"><i style="width:${w}%"></i></div>
            </div>
            <div class="csw-rep__stat">
              <div class="csw-rep__pts">${b.points || 0}</div>
              <div class="csw-rep__conv">${cr.conv_rate_pct != null ? cr.conv_rate_pct + '% · ' + (cr.aov_ngn ? cswNgn(cr.aov_ngn) : '—') : ''}</div>
            </div>
          </div>`;
        }).join('') : '<div class="csw-empty">No points yet this week.</div>'}
      </div>
    </div>

    ${(why.refund_sample && why.refund_sample.length) ? `<div class="csw-card" style="animation-delay:160ms">
      <div class="csw-card__h"><div class="csw-card__t">Why customers reach out</div></div>
      ${why.refund_sample.map(t => `<div class="csw-theme"><span>${esc((t.first_signal_text || '').slice(0, 70))}</span><b>refund</b></div>`).join('')}
      ${(why.quality_sample || []).map(t => `<div class="csw-theme"><span>${esc((t.first_signal_text || '').slice(0, 70))}</span><b>quality</b></div>`).join('')}
    </div>` : ''}

    <div id="csw-lifecycle-section">
      <div class="csw-card csw-skel" style="height:160px;animation-delay:200ms"></div>
    </div>

    <div id="csw-csat-section">
      <div class="csw-card csw-skel" style="height:140px;animation-delay:240ms"></div>
    </div>
  </div>`;

  const body = document.getElementById('csw-body');
  if (body) body.innerHTML = html;
  body.querySelectorAll('[data-go]').forEach(b => b.addEventListener('click', () => {
    s.mode = b.dataset.go;
    document.querySelectorAll('#csw-seg button').forEach(x => x.classList.toggle('on', x.dataset.mode === s.mode));
    cswRenderBody();
  }));

  // lazy-load lifecycle metrics (separate endpoint, ~30d window)
  cswLoadLifecycle();
  // lazy-load CSAT scoreboard (review-loop endpoint, ~30d window)
  cswLoadCsat();

  // hydrate my ring + streak
  if (myRepId) {
    api(`/api/cs/gamification/rep/${encodeURIComponent(myRepId)}`).then(g => {
      if (!g || !g.ok) return;
      const ring = g.ring || {};
      const host = body.querySelector('.csw-hero__row .csw-ring');
      if (host) host.outerHTML = cswRing(ring.points || 0, ring.target || target);
      const st = document.getElementById('csw-streak');
      const days = (g.streak && g.streak.streak_days) || 0;
      const freeze = g.streak && g.streak.freeze_available;
      if (st) st.innerHTML = days > 0
        ? `${cswFlame()} <b>${days}-day</b> streak${freeze ? ` <span class="csw-freeze">${cswSnow()} freeze ready</span>` : ''}`
        : 'Start a streak today';
      const lv = document.getElementById('csw-level');
      const L = g.level || {};
      if (lv && L.title) {
        lv.innerHTML = `<span class="csw-levelchip">Lv ${L.level} · ${esc(L.title)}</span>`
          + (L.next_at ? `<span class="csw-levelnext">${L.total_points}/${L.next_at} pts</span>` : '');
      }
    }).catch(() => {});
  }
}

// ── LIFECYCLE METRICS (Pulse sub-section) ────────────────────
// Loaded lazily after the main Pulse frame renders. Renders a
// "Lifecycle · 30 days" card with decision-first tiles.
// Matches the .mk-ads3 dark decision-first design language:
// lime/mint/amber figures, mono tile layout, one-line action per tile.

function _lcFmt(n, pfx) {
  if (n == null) return '—';
  if (pfx === 'ngn') return '₦' + Math.round(n).toLocaleString();
  if (pfx === 'pct') return Math.round((n || 0) * 100) + '%';
  return String(Math.round(n));
}

function _lcTile(figure, label, action, color) {
  return `<div class="csw-lc-tile">
    <div class="csw-lc-tile__fig" style="color:${color || 'var(--accent)'}">${esc(figure)}</div>
    <div class="csw-lc-tile__lbl">${esc(label)}</div>
    ${action ? `<div class="csw-lc-tile__act">${esc(action)}</div>` : ''}
  </div>`;
}

async function cswLoadLifecycle() {
  const slot = document.getElementById('csw-lifecycle-section');
  if (!slot) return;
  let d;
  try { d = await api('/api/cs/lifecycle-metrics?days=30'); }
  catch (e) {
    slot.innerHTML = '';
    return;
  }
  if (!d || !d.ok) { slot.innerHTML = ''; return; }

  const cart   = d.cart_recovery || {};
  const tmpl   = d.templates     || {};
  const wb     = d.winback       || {};
  const csat   = d.csat          || {};

  // ok:false carries rate 0.0 / value 0 as placeholders, not readings —
  // rendering them as '0%' and '₦0' invents a figure out of a timeout.
  const cartRead    = cart.ok !== false;
  const cartRatePct = (cartRead && cart.rate != null) ? Math.round((cart.rate || 0) * 100) + '%' : '—';
  const cartVal     = (cartRead && cart.recovered_value_ngn != null) ? _lcFmt(cart.recovered_value_ngn, 'ngn') : '—';
  // PER LEG (FR be1aa19c): the ladder runs WhatsApp and email legs and the
  // blended rate hides a weak leg behind the leg that works. Every leg is
  // scored by the SAME definition as the headline — recovered AFTER a
  // touch, over CARTS touched — so a leg can never look alive on
  // self-recoveries nobody messaged, and the leg fractions read at the
  // same scale as the headline they sum to. Messages are the leg's COST
  // side and ride alongside in parentheses; putting them on the bottom of
  // the fraction made every leg look worse than the blend it composes.
  // Legs with no sends are omitted.
  const cartLegs = Object.entries(cart.by_channel || {})
    .filter(([, v]) => (v && v.messaged))
    .sort((a, b) => (b[1].touches || 0) - (a[1].touches || 0))
    .map(([ch, v]) => `${ch} ${v.recovered || 0}/${v.messaged} (${v.touches || 0} msgs)`)
    .join(' · ');
  // A failed read returns ok:false with messaged=0 — identical in shape to
  // a genuinely quiet month. Say which one it is, or the card tells the
  // operator to switch on a ladder that is already running and billing.
  const cartAction  = !cartRead
    ? "Couldn't read cart recovery — figures unavailable, not zero."
    : cart.messaged
      ? `${cart.recovered || 0}/${cart.messaged} carts recovered.${cartLegs ? ` ${cartLegs}.` : ''} ${cart.messaged > 5 && !cart.rate ? 'Enable auto-send to recover more.' : ''}`
      : 'No cart recovery sends yet — enable auto-send in settings.';

  const wbPct     = _lcFmt(wb.reactivation_rate, 'pct');
  const wbRev     = wb.reactivated_revenue_ngn != null ? _lcFmt(wb.reactivated_revenue_ngn, 'ngn') : '—';
  const wbAction  = wb.action || (wb.sent ? `${wb.sent} win-back send(s) this period.` : 'No win-back sends tracked yet.');

  const csatPct   = csat.built === false ? 'N/A' : (csat.positive_pct != null ? _lcFmt(csat.positive_pct, 'pct') : '—');
  const csatAction = csat.built === false
    ? 'CSAT table not yet built.'
    : (csat.action || (csat.total_responses ? `${csat.total_responses} response(s) this period.` : 'No CSAT responses yet — send the CSAT template after deliveries.'));

  const tmplTotal = (tmpl.sends_with_name || 0) + (tmpl.sends_zoko_no_name || 0);
  const tmplAction = tmpl.gap_note || 'Send count only — read/click not captured.';

  slot.innerHTML = `<div class="csw-card csw-lc" style="animation-delay:200ms">
    <div class="csw-card__h">
      <div class="csw-card__t">Lifecycle · 30 days</div>
    </div>
    <div class="csw-lc-grid">
      ${_lcTile(cartRatePct, 'Cart recovery rate', cartAction, 'var(--accent)')}
      ${_lcTile(cartVal, 'Recovered revenue', (cartRead && cart.self_recovered != null) ? `${cart.self_recovered} self-recovered` : '', '#7fffb2')}
      ${_lcTile(wbPct, 'Win-back reactivation', wbAction, 'var(--accent)')}
      ${_lcTile(wbRev, 'Win-back revenue', wb.sent ? `${wb.sent} sends tracked` : '', '#7fffb2')}
      ${_lcTile(csatPct, 'CSAT positive', csatAction, '#ffd36e')}
      ${_lcTile(tmplTotal ? String(tmplTotal) : '—', 'Template sends', tmplAction, '#aaa')}
    </div>
  </div>`;
}

// ── CSAT SCOREBOARD (Pulse sub-section) ──────────────────────
// Per-store + per-rep CSAT from the review loop. Real figures only.
// Same .mk-ads3 dark decision-first language as the lifecycle card:
// big mono figure, mono labels, lime/mint/amber accents, graceful
// "no reviews yet" state — never a broken tile or a 500.

// Format a 1-5 CSAT average as "x.x" (the endpoint already rounds to 2dp).
function _csatFmt(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!isFinite(n)) return '—';
  return n.toFixed(1);
}
// Colour an average by band — amber default, mint when strong, danger when low.
function _csatColor(v) {
  const n = Number(v);
  if (!isFinite(n)) return '#aaa';
  if (n >= 4.5) return '#7fffb2';
  if (n >= 3.5) return '#ffd36e';
  return '#ff7a7a';
}

async function cswLoadCsat() {
  const slot = document.getElementById('csw-csat-section');
  if (!slot) return;
  let d;
  try { d = await api('/api/cs/csat-scoreboard?days=30'); }
  catch (e) { slot.innerHTML = ''; return; }
  if (!d || !d.ok) { slot.innerHTML = ''; return; }

  // No reviews captured yet → muted empty state, not a broken tile.
  if (!d.n) {
    slot.innerHTML = `<div class="csw-card csw-lc" style="animation-delay:240ms">
      <div class="csw-card__h"><div class="csw-card__t">CSAT · 30 days</div></div>
      <div class="csw-empty"><div class="csw-empty__m">○</div>No reviews yet — the CSAT loop captures ratings after deliveries.</div>
    </div>`;
    return;
  }

  const overall = _csatFmt(d.overall_csat);
  const overallColor = _csatColor(d.overall_csat);
  const nLabel = `${d.n} review${d.n === 1 ? '' : 's'}`;
  const negOpen = Number(d.negative_open || 0);
  const overallAction = negOpen > 0
    ? `${negOpen} negative ${negOpen === 1 ? 'review needs' : 'reviews need'} recovery — clear the open task(s).`
    : 'No open service-recovery tasks. Keep it up.';

  // Compact top-few breakdown — prefer per-store (operator-meaningful),
  // fall back to per-rep when stores didn't resolve. Real figures only.
  const stores = (d.by_store || []).filter(x => x && x.csat != null).slice(0, 3);
  const reps   = (d.by_rep   || []).filter(x => x && x.csat != null).slice(0, 3);
  const useStores = stores.length > 0;
  const breakdown = useStores ? stores : reps;
  const bdLabel = useStores ? 'By store' : 'By rep';
  const bdRows = breakdown.length
    ? breakdown.map(x => `<div class="csw-csat-row">
        <span class="csw-csat-row__name">${esc(useStores ? (x.store || 'unknown') : (x.rep || 'unknown'))}</span>
        <span class="csw-csat-row__n">${x.n}</span>
        <b class="csw-csat-row__v" style="color:${_csatColor(x.csat)}">${_csatFmt(x.csat)}</b>
      </div>`).join('')
    : '<div class="csw-empty" style="padding:14px 0">No per-store breakdown yet.</div>';

  slot.innerHTML = `<div class="csw-card csw-lc" style="animation-delay:240ms">
    <div class="csw-card__h">
      <div class="csw-card__t">CSAT · 30 days</div>
    </div>
    <div class="csw-lc-grid">
      ${_lcTile(overall, 'Overall CSAT', overallAction, overallColor)}
      ${_lcTile(String(d.n), 'Responses', nLabel, 'var(--accent)')}
      ${_lcTile(negOpen ? String(negOpen) : '0', 'Open recovery', negOpen ? 'Negative reviews awaiting action.' : 'All clear.', negOpen ? '#ff7a7a' : '#7fffb2')}
    </div>
    <div class="csw-csat-bd">
      <div class="csw-csat-bd__h">${bdLabel}</div>
      ${bdRows}
    </div>
  </div>`;
}

// ── INBOX ────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════
// ── The bottom tab bar + the Desk room (B2b — artifact 2467db14's
//    last unbuilt piece; geometry and colour law per handoff §3b) ──
// Four rooms: Chats · Priority · EON · Desk. House furniture only
// (classic keeps its own nav), below the title on desktop and fixed
// at the phone's bottom edge. On phones, NEVER over a conversation:
// the composer owns the bottom edge, so the bar yields entirely via
// `.csw-body:has(> .csw-inbox.has-active) > .csw-tabbar` (no fixed pill may
// overlap the send zone — that law has been paid for). Chats and
// Priority are the SAME rail with a saved lens — the operator restored
// the classic lenses by directive (p781), so a tab is a thumb-reach
// onto them, never a second list. ONE numeric badge on the whole bar
// (Priority's book count, written by the same painter as the lens
// key); Desk wears a wordless amber pip while a hold is ticking.
// `csw-tbtab`, never `csw-eontab` — that class is the EON pane's mode
// strip and was a named collision in the handoff.

function cswTabbarHtml() {
  return `<nav class="csw-tabbar" id="csw-tabbar" aria-label="Desk rooms">
    <button class="csw-tbtab" type="button" data-tbroom="chats">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" aria-hidden="true"><path d="M21 12a8 8 0 0 1-8 8H4l2-3a8 8 0 1 1 15-5z"/></svg>
      <span>Chats</span></button>
    <button class="csw-tbtab" type="button" data-tbroom="priority">
      <span class="csw-tbtab__badge num" id="csw-tb-badge" hidden>—</span>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" aria-hidden="true"><path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z"/></svg>
      <span>Priority</span></button>
    <button class="csw-tbtab" type="button" data-tbroom="eon">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" aria-hidden="true"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="2.6" fill="currentColor" stroke="none"/></svg>
      <span>EON</span></button>
    <button class="csw-tbtab" type="button" data-tbroom="desk">
      <span class="csw-tbtab__dot" id="csw-tb-holdsdot" hidden></span>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" aria-hidden="true"><rect x="3" y="3" width="7.5" height="7.5" rx="1.5"/><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5"/><rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5"/><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5"/></svg>
      <span>Desk</span></button>
  </nav>`;
}

function cswMountTabbar(body) {
  if (!cswHouse() || !body) return;
  if (body.querySelector('#csw-tabbar')) { cswTabbarSync(); return; }
  body.insertAdjacentHTML('afterbegin', cswTabbarHtml());
  body.querySelectorAll('.csw-tbtab').forEach(b =>
    b.addEventListener('click', () => {
      const room = b.dataset.tbroom;
      cswTabGo(room);
      const selected = document.querySelector(`#csw-tabbar [data-tbroom="${room}"]`);
      selected?.focus({ preventScroll: true });
      if (window.eonMotion) window.eonMotion.pop(selected?.querySelector('svg'), .92);
    }));
  cswTabbarSync();
  cswLoadHolds(true);
}

function cswCustomerPriorityItems(items) {
  return (items || []).filter(p => p.reason_key !== 'business_followup');
}
function cswPrioBadgeCount() {
  // ONE computation for the lens key and the bar badge — they can never
  // disagree (the one-painter law; house subtracts claimed cards).
  const items = cswCustomerPriorityItems(_cswPrio.items);
  return cswHouse()
    ? items.filter(p => p.status !== 'claimed').length
    : items.length;
}

function cswTabbarSync() {
  const s = cswState();
  // automated + staffline + credits are rooms behind Desk's doors — Desk
  // stays lit there so the bar never shows four dead tabs.
  const room = (s.mode === 'desk' || s.mode === 'automated'
                || s.mode === 'staffline' || s.mode === 'credits')
    ? 'desk'
    : s.active === CSW_EON_CID ? 'eon'
    : (s.houseLens || '') === '' && s.filter !== 'team' ? 'priority' : 'chats';
  document.querySelectorAll('#csw-tabbar .csw-tbtab').forEach(b => {
    const on = b.dataset.tbroom === room;
    b.classList.toggle('on', on);
    b.setAttribute('aria-current', on ? 'page' : 'false');
  });
  // A fresh mount starts at the '—' placeholder; paint from the cache
  // when a reading exists, and never show the placeholder (rig vet).
  const tb = document.getElementById('csw-tb-badge');
  if (tb) {
    if (_cswPrio.at) {
      const n = cswPrioBadgeCount();
      tb.textContent = String(n); tb.hidden = !n;
    } else tb.hidden = true;
  }
  liftAskEonAboveNav();
}

function cswTabGo(room, auto) {
  const s = cswState();
  // Lane P: a tab the rep TAPPED is a lens chosen by hand — the shift-start
  // landing (cswPrioLandOnShift) must never override it; `auto` is the
  // landing's own call and leaves that record alone.
  if (!auto) { s.lensPicked = true; s.prioLandNote = ''; }
  if (!auto) cswFromDrop();  // R5b7: a tab the person taps is a move
  // A tab is a navigation OUT of wherever you stand — close any open
  // conversation first so the rail (and the bar with it) come back.
  if (s.active) cswMarkLeft(s.active);     // a tab is leaving too (verifier, 2026-09-04)
  const hadChat = !!s.active;
  s.active = null; s.activeThread = null;
  if (hadChat) cswUpdatePresence(false);   // and the team hears it (vet 2026-09-24, fault 27)
  if (room === 'desk') { s.mode = 'desk'; cswRenderBody(); return; }
  if (room === 'eon') {
    if (s.mode !== 'inbox') { s.mode = 'inbox'; cswRenderBody(); }
    cswOpenEon(); return;
  }
  // chats | priority — the rail with its lens saved. Mirrors the lens
  // key binding EXACTLY (houseLens '' → null, filter '' → 'all') so
  // there is one source of truth for what a lens sets.
  const hl = room === 'priority' ? '' : 'awaiting';
  s.mode = 'inbox';
  s.houseLens = hl || null;
  s.filter = hl || 'all';
  cswRenderBody();
}

// ── holds: the Desk queue, the pip, and the rehearsal chip ─────────
let _cswHolds = { rows: [], dryRun: false, at: 0 };

async function cswLoadHolds(quiet) {
  if (!cswHouse() && cswState().mode !== 'desk') return;
  let d = null;
  try { d = await api('/api/cs/held?limit=50'); }
  catch (_) { if (!quiet) cswToast('Could not read the holds queue', true); return; }
  if (!d || d.ok === false) return;
  _cswHolds = { rows: d.holds || [], dryRun: !!d.dry_run, at: Date.now() };
  cswState()._contactRehearsal = d.contact_rehearsal === true;
  const dot = document.getElementById('csw-tb-holdsdot');
  if (dot) dot.hidden = !_cswHolds.rows.length;
  // EON_DRY_RUN made visible wherever the chip is rendered — a tester
  // must never believe a tap reached a customer while sends are being
  // swallowed, and prod (flag off) must never see the chip.
  document.querySelectorAll('.csw-rehearse').forEach(el => { el.hidden = !_cswHolds.dryRun; });
  if (cswState().mode === 'desk') cswRenderDeskHolds();
}

function cswDeskCdText(rel, state, thread) {
  if (state === 'sending') return 'Sending now';
  if (arguments.length > 2 && cswWindowBlocks(thread)) return cswHeldLabel(rel, thread);
  const label = cswHeldLabel(rel);
  // 'Sending…' / 'Sending shortly' are DUE states — appending the
  // agency clause would print "Sending… — unless you act", a lie about
  // a send already leaving (rig vet).
  return (label === 'Sending…' || label === 'Sending shortly')
    ? label : `${label} — unless you act`;
}

function cswDeskHoldRow(h) {
  const s = cswState();
  // Dual-key law: a hold stores BOTH ids at park time; the thread list
  // may speak either (modern threads share one value, legacy split).
  // Each key must be TRUTHY before it may match — two empty strings
  // compare equal, and a blank-keyed hold would bind to the first
  // blank-keyed thread and open the wrong customer (rig vet).
  const hz = String(h.zoko_customer_id || '');
  const hp = String(h.platform_sender_id || '');
  const t = (s.threads || []).find(x =>
    (hz && String(x.zoko_customer_id) === hz) ||
    (hp && (String(x.zoko_customer_id) === hp ||
            String(x.platform_sender_id || '') === hp)));
  const name = t ? cswHouseName(t)
    : cswHouseMask(String(h.zoko_customer_id || h.platform_sender_id || 'unknown'));
  const openId = t ? t.zoko_customer_id : (h.zoko_customer_id || h.platform_sender_id);
  const text = String(h.final_text || h.draft_text || '');
  return `<div class="csw-deskhold" data-hid="${esc(String(h.id))}">
    <div class="csw-deskhold__bx">
      <div class="csw-deskhold__n">${esc(name)}${h.lane ? ` <span class="csw-deskhold__lane">${esc(h.lane)}</span>` : ''}</div>
      <div class="csw-deskhold__p">${esc(shorten(text, 92))}</div>
      <div class="csw-deskhold__cd num" data-cswdeskcd="${esc(String(h.release_at || ''))}" data-cswstate="${esc(String(h.state || ''))}" data-cswholdthread="${esc(String(openId || ''))}">${esc(cswDeskCdText(h.release_at, h.state, t))}</div>
    </div>
    <button class="csw-deskhold__open" type="button" data-open="${esc(String(openId || ''))}">Open</button>
    ${h.state === 'sending' ? ''
    : `<button class="csw-deskhold__dis" type="button" data-dis="${esc(String(h.id))}">Discard</button>`}
  </div>`;
}

function cswRenderDeskHolds() {
  const host = document.getElementById('csw-desk-holds');
  if (!host) return;
  const rows = _cswHolds.rows || [];
  if (!rows.length) {
    host.innerHTML = `<div class="csw-empty" style="padding:12px 0"><div class="csw-empty__m">○</div>Nothing held — EON is not waiting on you.</div>`;
    return;
  }
  // A capped read must say so — silence reads as "that's everything"
  // when a 51st hold exists (rig vet).
  host.innerHTML = rows.map(cswDeskHoldRow).join('')
    + (rows.length >= 50
      ? `<div class="csw-desklaw">Showing the 50 soonest — the queue is longer.</div>` : '');
  host.querySelectorAll('[data-open]').forEach(b => b.addEventListener('click', () => {
    const st = cswState();
    st.mode = 'inbox'; st.active = b.dataset.open || null;
    cswRenderBody();
  }));
  host.querySelectorAll('[data-dis]').forEach(b => b.addEventListener('click', async () => {
    b.disabled = true;
    try {
      await api(`/api/cs/held/${encodeURIComponent(b.dataset.dis)}/discard`, { method: 'POST', body: '{}' });
      cswToast('Held reply discarded — nothing sends');
    } catch (_) {
      cswToast('Discard failed — it may already be releasing', true);
      b.disabled = false; return;
    }
    cswLoadHolds();
  }));
}

function cswRenderDeskDoors() {
  const host = document.getElementById('csw-desk-doors');
  if (!host) return;
  const hasStaff = !!(typeof sosState !== 'undefined' && sosState && sosState.cur);
  const doors = [];
  if (hasStaff) doors.push(['team', 'Team', 'staff ↔ staff threads']);
  doors.push(['automated', 'Automated messages', 'EON’s own sends · templates · abandoned carts']);
  host.innerHTML = doors.map(d =>
    `<button class="csw-deskdoor" type="button" data-door="${esc(d[0])}">
       <span class="csw-deskdoor__t">${esc(d[1])}</span>
       <span class="csw-deskdoor__s">${esc(d[2])}</span>
       <span class="csw-deskdoor__a" aria-hidden="true">›</span></button>`).join('');
  host.querySelectorAll('.csw-deskdoor').forEach(b => b.addEventListener('click', () => {
    const st = cswState();
    if (b.dataset.door === 'automated') { st.mode = 'automated'; cswRenderBody(); return; }
    if (b.dataset.door === 'team') {
      st.mode = 'inbox'; st.filter = 'team'; st.houseLens = null; st.lensPicked = true;
      cswRenderBody();
    }
  }));
}

function cswDeskRing() {
  // Same numbers, same math as the queue-head ring (63-dash painter) —
  // never a second computation that can disagree with the rail.
  const paint = () => {
    const items = cswCustomerPriorityItems(_cswPrio.items);
    const done = _cswPrio.cleared || 0;
    const total = items.length + done;
    const f = document.getElementById('csw-desk-frac');
    const a = document.getElementById('csw-desk-arc');
    if (f) f.textContent = `${done}/${total}`;
    if (a) a.style.strokeDashoffset = (63 * (1 - (total ? done / total : 0))).toFixed(1);
  };
  if ((Date.now() - (_cswPrio.at || 0)) > 60000) {
    api('/api/cs/priority').then(d => {
      if (!d || d.ok === false) throw new Error('Priority could not be read');
      _cswPrio = { items: d.items || [], cleared: d.cleared || 0,
                   neglected: d.neglected || 0, money_waiting: d.money_waiting || 0, at: Date.now() };
      paint();
      // Lane A: the roster's "money waiting · N" reads this same book —
      // repaint it now that the count is fresh (the room painted the
      // roster before this read came back).
      try { if (typeof cswRenderFleet === 'function') cswRenderFleet(); } catch (_) {}
    }).catch(() => {
      // CSW-03: a book it never read is not 0/0 — say it could not be read.
      if (_cswPrio.at) { paint(); return; }
      const f = document.getElementById('csw-desk-frac');
      if (f) f.textContent = '—';
      const l = document.querySelector('#csw-deskroom .csw-deskring__l');
      if (l) l.textContent = 'Couldn’t read the Priority book.';
    });
  } else paint();
}

let _cswDeskTicker = null;
function cswDeskTickerStart() {
  // ONE interval for the room, self-killing when the room leaves the
  // DOM — the same discipline as cswHeldTick (a leaked timer here would
  // tick forever behind every other screen).
  if (_cswDeskTicker) { clearInterval(_cswDeskTicker); _cswDeskTicker = null; }
  _cswDeskTicker = setInterval(() => {
    if (!document.getElementById('csw-deskroom')) {
      clearInterval(_cswDeskTicker); _cswDeskTicker = null; return;
    }
    let due = false;
    document.querySelectorAll('[data-cswdeskcd]').forEach(el => {
      if (el.getAttribute('data-cswstate') === 'sending') return;
      const txt = cswDeskCdText(el.getAttribute('data-cswdeskcd'), '', cswThreadFor(el.getAttribute('data-cswholdthread')));
      el.textContent = txt;
      if (txt === 'Sending…' || txt === 'Sending shortly') due = true;
    });
    // A hold that hit zero has left our hands — refetch so the row
    // becomes the truth (sent and gone), throttled to one ask per 5s.
    if (due && (Date.now() - (_cswHolds.at || 0)) > 5000) cswLoadHolds(true);
  }, 1000);
}

function cswOrientationHtml() {
  // Guidance only. The queue retains its own loading/error/empty state;
  // no customer is opened, claimed or marked seen by this orientation.
  return `<section class="csw-orientation" aria-labelledby="csw-orientation-title">
    <small>YOUR WORKSPACE</small><h2 id="csw-orientation-title">A clear place to begin.</h2>
    <p>Choose a conversation from the queue. Its messages and next steps will open here.</p>
    <div class="csw-orientation__paths" role="group" aria-label="Choose a queue">
      <button type="button" data-orientation-lens=""><span>Priority</span><small>Work that needs attention</small><b aria-hidden="true">→</b></button>
      <button type="button" data-orientation-lens="awaiting"><span>Needs reply</span><small>Conversations waiting for a reply</small><b aria-hidden="true">→</b></button>
      <button type="button" data-orientation-lens="mine"><span>Mine</span><small>Conversations assigned to you</small><b aria-hidden="true">→</b></button>
    </div>
    <button class="csw-orientation__desk" type="button" data-orientation-desk>Open Desk <span aria-hidden="true">→</span></button>
  </section>`;
}

function cswInboxShell() {
  const s = cswState();
  // 'awaiting' first (operator ask 2026-06-13: "a tab on top to show
  // unread or unreplied messages") — unreplied = customer spoke last.
  // It carries the live count plus an oldest-wait badge (SLA signal).
  // Customer-side channels collapsed into one inbox (2026-06-18): the core
  // CUSTOMER lenses are Awaiting / All / Mine. TEAM is the fourth lens (staff↔
  // staff @-mention threads) — the only conceptual split is TEAM vs CUSTOMERS,
  // and both now live in this ONE shell (2026-06-18: Team folded in from the
  // old top section / pinned row). Team messages open in the SAME conversation
  // pane, rendered as DM rows with the sender's staff name.
  // Team lens (staff to staff @-mention threads) only exists when there's an
  // ACTING staff member (Staff OS context: sosState.cur). On standalone /cs
  // (owner login, no staff_id) the Team tab would be empty and its threads
  // endpoint has no staff_id to query, so hide it there entirely.
  const hasStaff = !!(typeof sosState !== 'undefined' && sosState && sosState.cur);
  // Tab labels say what they hold (operator spec 2026-07-26): "Needs
  // reply" = the fresh (≤72h) queue, newest inbound first; "Recovery" =
  // the kept-for-win-back tail that used to inflate the awaiting count.
  const tabs = [['priority', 'Priority'], ['awaiting', 'Needs reply'], ['recovery', 'Recovery'], ['all', 'All'], ['mine', 'Mine']];
  if (hasStaff) tabs.push(['team', 'Team']);
  // Don't strand the list on a Team filter that no longer has a tab.
  if (s.filter === 'team' && !hasStaff) s.filter = 'all';
  return `<div class="csw-inbox ${s.active ? 'has-active' : ''}" id="csw-inbox">
    <div class="csw-panes">
    <div class="csw-side">
      ${cswHouse() ? '<div class="csw-queuebar">' : ''}
      ${cswHouse() ? `<button class="csw-queuekey" id="csw-search-toggle" type="button" aria-label="Search conversations" title="Search conversations (⌘/Ctrl K)" aria-controls="csw-searchwrap" aria-expanded="${!!s.search}"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 4 4"/></svg></button>` : ''}
      <div class="csw-searchwrap" id="csw-searchwrap" ${cswHouse() && !s.search ? 'hidden' : ''}>
        <span class="csw-searchwrap__i" aria-hidden="true">⌕</span>
        <input class="csw-searchbar" id="csw-searchbar" type="search" aria-label="Search conversations" placeholder="Search conversations" value="${esc(s.search || '')}" />
        ${cswHouse() ? '<button class="csw-queuekey" id="csw-search-close" type="button" aria-label="Close search and clear query" title="Close search and clear query"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg></button>' : ''}
      </div>
      ${/* B1 — THE NOW SHELF (2026-09-03). Above the lens row and outside
           it: the lens row is a saved VIEW and this is not a view, it is
           what the rep's hands are on right now. Both skins get it, since
           holding eight threads at once is not a house-skin problem.
           Ships [hidden]; only its painter reveals it, so a desk with an
           empty working set is the desk it was yesterday. */''}
      <!-- Recent working threads moved to the menu; Mine remains the assigned queue. -->
      ${cswHouse() ? `<div class="csw-hq" id="csw-hq">
        <details class="csw-shift" id="csw-shift">
          <summary class="csw-hq__ring" title="Priority cards handled this shift, out of all the cards on this shift's list">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle class="b" cx="12" cy="12" r="10"></circle><circle class="f" id="csw-hq-arc" cx="12" cy="12" r="10"></circle></svg>
            <span class="num" id="csw-hq-frac">—</span> <span class="csw-hq__scope">shift</span>
          </summary>
          <div class="csw-shift__detail"><strong>Priority cards</strong>
          <span class="csw-hq__ringl">handled this shift</span>
          <p>Completed out of all the cards on this shift’s list.</p></div>
        </details>
        <span class="csw-rehearse" id="csw-rehearse" hidden><i></i><span>Rehearsal <span class="csw-rehearse__sep">·</span> no sends</span></span>
        ${hasStaff ? `<button class="csw-hq__eonkey" id="csw-hq-eon" type="button" title="Your private EON line" aria-label="Open your private EON line">EON</button>` : ''}
        ${hasStaff ? `<button class="csw-hq__team" id="csw-hq-team" type="button">${s.filter === 'team' ? 'Desk' : 'Team'}</button>` : ''}
      </div>
      </div>
      <div class="csw-hlens" id="csw-hlens" role="tablist" aria-label="Desk lenses">
        ${/* Operator 2026-08-09: "priority, needs reply tabs back from
           classic; remove the queue, 0 to work". The book IS the Priority
           lens now (same claim/working render, honest name), Needs reply
           is classic's awaiting list one tap away, and the "N to work"
           eyebrow is gone — the ring alone carries the drain. Count chips
           reuse the classic ids so the existing painters (cswLoadPriority,
           cswUpdateCounts) light them with zero extra wiring; only one
           lens bar renders at a time, so the ids stay unique. */''}
        ${[['', 'Priority'], ['awaiting', 'Needs reply'], ['all', 'All'], ['mine', 'Mine'], ['recovery', 'Recovery']].map(([v, l]) =>
          `<button class="csw-hlens__k ${(s.houseLens || '') === v ? 'on' : ''}" data-hl="${v}" type="button" role="tab" aria-selected="${(s.houseLens || '') === v}">${l}${v === '' ? '<span class="csw-filter__c" id="csw-c-priority">—</span>' : ''}${v === 'awaiting' ? '<span class="csw-filter__c" id="csw-c-awaiting">—</span><span class="csw-filter__w" id="csw-w-awaiting" hidden></span>' : ''}${v === 'all' ? '<span class="csw-filter__c" id="csw-c-all">—</span>' : ''}${v === 'mine' ? '<span class="csw-filter__c" id="csw-c-mine">—</span><span class="csw-filter__w" id="csw-w-mine" hidden title="Customers waiting on a reply from you"></span>' : ''}${v === 'recovery' ? '<span class="csw-filter__c" id="csw-c-recovery">—</span>' : ''}</button>`).join('')}
      </div>` : `<div class="csw-filters" id="csw-filters">
        ${tabs.map(([id, l]) => `<button class="csw-filter ${s.filter === id ? 'on' : ''} ${id === 'awaiting' ? 'csw-filter--await' : ''}" data-f="${id}">${l}<span class="csw-filter__c" id="csw-c-${id}">—</span>${id === 'awaiting' ? '<span class="csw-filter__w" id="csw-w-awaiting" hidden></span>' : (id === 'mine' ? '<span class="csw-filter__w" id="csw-w-mine" hidden title="Customers waiting on a reply from you"></span>' : '')}</button>`).join('')}
      </div>`}
      <div class="csw-legend" aria-hidden="true"><span><i style="background:var(--accent)"></i>open</span><span><i style="background:var(--warn)"></i>30m+ wait</span><span><i style="background:var(--danger)"></i>2h+ wait</span></div>
      <div class="csw-hnote" id="csw-from" role="status" hidden></div>
      <div class="csw-activefilter" id="csw-activefilter" hidden></div>
      <div class="csw-threads" id="csw-threads">${window.EON_UX ? window.EON_UX.loader('Loading conversations…') : '<div class="csw-empty"><div class="csw-empty__m">○</div>Loading…</div>'}</div>
    </div>
    <div class="csw-conv" id="csw-conv">
      ${cswHouse() ? cswOrientationHtml() : '<div class="csw-empty" style="margin:auto"><div class="csw-empty__m">/</div>Pick a conversation</div>'}
    </div>
    </div>
  </div>`;
}

// Message KIND tag: 'comment' | 'story' | 'dm'. (DM = any direct message,
// WhatsApp or IG.) 2026-06-18: no longer a filter split — comment/story
// threads live in the single customer inbox. Used only for the small inline
// "Comment ·" / "Story ·" preview tag below. Distinct from the platform badge.
function cswChannel(t) {
  const ty = (t.last_message_type || '').toLowerCase();
  if (ty === 'comment') return 'comment';
  if (['story_mention', 'story_reply', 'ig_story', 'ig_reel'].includes(ty)) return 'story';
  return 'dm';
}
// PLATFORM badge: 'wa' | 'ig'. Uses the reliable server-side channel
// (raw platform), NOT a digit-count of the sender id (IG IGSIDs are also
// long numeric → that heuristic mislabelled IG as WhatsApp).
function cswPlatform(t) {
  const ch = (t.channel || '').toLowerCase();
  if (ch === 'whatsapp') return 'wa';
  if (ch === 'instagram') return 'ig';
  const ty = (t.last_message_type || '').toLowerCase();
  if (['comment', 'story_mention', 'story_reply', 'ig_story', 'ig_reel'].includes(ty)) return 'ig';
  return 'wa';  // last-resort fallback
}

// "Mine" identity: the acting rep in the /staff embed (asRepEmail), else the
// logged-in member, else the PIN session's canonical rep key (staff.email or
// staff_id — the key claims are stored under since #1553). Used by both the
// filter count and the thread-list filter.
function cswMyEmail() {
  const s = cswState();
  return ((s && s.asRepEmail) || (State.member && State.member.email)
    || State.repKey || '').toLowerCase();
}
async function cswLoadThreads(quiet) {
  const timing = eonTimingStart('threads');
  const s = cswState();
  let d;
  // R5c A11b: Marketing's buyers count asks for exactly those buyers. The
  // server narrows this person's OWN list to them; it never widens it.
  const ck = cswFromCohort();
  // Marked BEFORE the read: a tap while it is in flight must still bring the
  // whole desk back (cswFromDrop), or the desk sits empty until the next poll.
  if (ck) s.cohortAsked = true;
  try {
    // Non-CS staff in /staff are LOCKED to their routed queue (server-filtered)
    // so they only ever see customer threads routed to them, with full tools.
    const lq = cswState().lockedQueue;
    const qp = lq ? '&queue=' + encodeURIComponent(lq) : '';
    // Staff OS role-switcher: preview the inbox AS the viewed staff member, so an
    // owner viewing a non-CS role (e.g. Inventory) sees THAT role's scope (empty)
    // — not the owner's full desk. The server ignores this for a non-owner caller
    // and when it equals the caller, so it can only narrow an owner's preview.
    const av = cswState().asViewStaffId;
    const ap = av ? '&as_staff_id=' + encodeURIComponent(av) : '';
    // limit=400 (2026-07-25, the Josh flicker): 228 threads were active
    // in the 7-day window while this asked for the top 120 — a thread slid
    // past rank 120 between messages and VANISHED from the desk, then
    // reappeared on its next message. Fetch the whole active book.
    d = await api('/api/cs/threads?days=7&limit=400' + qp + ap + (ck ? (ck.startsWith('ad:') ? '&ad=' + ck.slice(3) : '&cohort=' + ck) : ''));
    if (!d || (d.ok !== undefined && d.ok !== true) || !Array.isArray(d.threads))
      eonTimingFinish(timing, 'unavailable');
    s._pollFail = 0;   // the desk's lifeline succeeded — banner truth input
  }
  catch (e) {
    // Poll-health for the honest connection banner (design vet 2026-07-26
    // SEV-5): the banner speaks only when THIS path — the thing that
    // actually delivers messages — fails repeatedly, not when the realtime
    // socket's heartbeat goes quiet.
    eonTimingFinish(timing, 'failed');
    s._pollFail = (s._pollFail || 0) + 1;
    // Vet 2026-09-24 (fault 20): one plain sentence, never the request path.
    if (!quiet) { const el = document.getElementById('csw-threads'); if (el) el.innerHTML = `<div class="csw-empty"><div class="csw-empty__m">!</div>${esc(cswSayWhy(e, { lead: 'Chats did not load.' }))}</div>`; }
    return;
  }
  // A buyers-list read that lands after the person left that list is stale;
  // cswFromDrop already asked for the whole desk.
  if (ck && ck !== cswFromCohort()) { eonTimingFinish(timing, 'superseded'); return; }
  const co = ck && d && d.cohort && d.cohort.key === ck ? d.cohort : null;
  // Another book is on screen: re-seed the chime and the count, never ring.
  if (!!co !== !!s.cohort) { s._inboundMax = ''; s.lastAwaiting = null; }
  if (co && !s.cohort) cswFromLens();
  s.cohort = co;
  const threads = (d.threads || []);
  // AIRTIGHT honesty (2026-07-25): threads awaiting a reply are NEVER
  // truncated server-side; if quieter (replied) threads were, say so
  // instead of silently shrinking the book. Search still reaches them.
  s.hiddenReplied = Number(d.hidden_replied || 0);
  s.assistedOrderOn = d.assisted_order === true;   // Ask EON S02-2: re-read on every poll
  threads.forEach(t => {
    t._ch = cswChannel(t);        // kind tag (preview only): dm | comment | story
    t._plat = cswPlatform(t);     // platform badge: wa | ig
    // UNREPLIED: customer spoke last → a rep reply is owed. Server-computed
    // (cs_inbox.thread_list); the last_direction fallback covers a stale
    // backend during deploy skew. _waitm = minutes the customer has waited.
    t.unreplied = (t.unreplied != null) ? !!t.unreplied : (t.last_direction === 'inbound');
    t._waitm = (t.waiting_minutes != null) ? t.waiting_minutes
      : (t.unreplied && t.last_inbound_at ? Math.max(0, Math.round((Date.now() - new Date(t.last_inbound_at)) / 60000)) : null);
    // WAITING = the one attention state (2026-07-25, operator): a customer
    // is owed a reply and the thread isn't resolved-clean. The green dot,
    // the hot flame, the tab-title count and the chime ALL key on this —
    // never on per-rep `unread` (seen-tracking), which lit dots on threads
    // another rep had already answered and went dark on waiting customers
    // the viewer had merely peeked at.
    // Sign-off enders ("thanks", "ok", 🙏) are conversations that ENDED,
    // not waits (server-computed with the reply-pager's own predicate, so
    // the tab and the pager always agree). They stay reachable in All.
    t._waiting = t.unreplied && !t.signoff && !(t.cs_status === 'resolved' && !t.unread);
    // Hot flame: urgent words from a WAITING customer. On a replied thread
    // last_message_text is the REP'S OWN outbound — testing it flagged our
    // own "your refund is processed" as urgent.
    t._hot = t._waiting && CSW_HOT_RE.test(t.last_message_text || '');
    // PROMISE (2026-08-03): our last send promised a follow-up and nothing
    // has gone out since (server-computed by cs_promise off the message
    // ledger). Such a thread reads "replied", so without this flag it
    // silently dropped off every waiting lens — the exact opposite of what
    // the promise said. It keeps its place on the Needs-reply board with a
    // "waiting on feedback" timer chip instead.
    t._promiseM = (t.promise_minutes != null) ? t.promise_minutes
      : (t.promise_open && t.promise_at ? Math.max(0, Math.round((Date.now() - new Date(t.promise_at)) / 60000)) : 0);
    t._promise = !!t.promise_open && !(t.cs_status === 'resolved' && !t.unread);
  });
  s.threads = threads;
  // Vet 2026-08-08: s.activeThread pointed at the PREVIOUS load's object
  // forever — the 60s dial ticked off a frozen snapshot, a window that
  // reopened kept the composer dead, and a window that closed mid-
  // session let free text bounce at Meta with no desk signal. Repoint
  // every load; when the WINDOW STATE actually flipped, repaint the
  // window-shaped parts of the pane in place (below, fault 13).
  if (s.active && s.activeThread) {
    const fresh = threads.find(t => t.zoko_customer_id === s.active);
    if (fresh) {
      const flipped = fresh.window_open !== s.activeThread.window_open;
      // UNKNOWN IS NOT FREE (adversary pass on #3544, 2026-09-24). The list
      // sends no `ball` both when the chat is free AND when the ball read
      // failed (cs_ball.bulk_get swallows a failed chunk), and EON's seat
      // lapses after 3 minutes while its held reply may wait up to 10. So a
      // refresh without a ball never takes down EON's hold on this screen:
      // the bar and the type-to-take-over guard stay until a teammate's hold
      // is seen, the rep takes over, or the rep leaves and the list reads the
      // chat free. The guard only protects — a stale one costs one harmless
      // takeover; a missing one let EON's held reply go out while the rep
      // was typing.
      const was = s.activeThread.ball;
      // THE SERVER'S WORD ON ITS READ (2026-09-26, #3542 round two). When
      // the list says its holder read worked (`ball_read_ok: true`, on the
      // thread or on the whole list), no ball means free, so EON's bar comes
      // down. When it says the read failed, what this screen knew stands.
      // With no word at all, the rule above holds: EON's hold stays until a
      // refresh shows someone else holding the chat. (#3542's list sends
      // both fields on every thread; a list without them gets that rule.)
      // EXCEPT WHILE EON'S REPLY WAITS (adversary pass, 2026-09-26). EON's
      // seat can lapse while its held reply still waits to go out, and the
      // list says so (`eon_reply_held`: true, or null when that read
      // failed). Then EON's bar and type-to-take-over stay, so a rep who
      // starts typing still stops that reply. Otherwise both would answer.
      const readOk = typeof fresh.ball_read_ok === 'boolean' ? fresh.ball_read_ok : d.ball_read_ok;
      const eonWaits = fresh.eon_reply_held === true || fresh.eon_reply_held === null;
      const keepEon = !!was && was.kind === 'eon' && (readOk !== true || eonWaits);
      if (!fresh.ball && was && (readOk === false || keepEon)) fresh.ball = was;
      const ballMoved = cswBallKey(fresh.ball) !== cswBallKey(was);
      s.activeThread = fresh;
      // IN PLACE, NEVER A REBUILD (vet 2026-09-24, fault 13). A window flip
      // used to rebuild the whole conversation pane under the rep: the
      // keyboard dropped, the quote chip vanished while the quote stayed
      // armed, and the "type to take over" guard went with the old box —
      // at the exact moment a customer's answer reopened the window. Every
      // window-shaped thing on the pane already has an in-place painter
      // (the dial, the closed-window note, the composer keys, the photo
      // key — cswRefreshReplyWindows runs them all, and its own 1s tick
      // would within a second anyway), so the flip repaints only those.
      // The ball is the holder of the chat: it used to be painted on open
      // only, so a later EON take never showed and a teammate's take kept
      // saying EON. Repaint whenever this refresh sees the holder move, and
      // after a flip while EON holds it (the window paint resets the
      // placeholder the ball owns; with no EON hold the window's own
      // placeholder must stand).
      if (flipped) cswRefreshReplyWindows();
      if (ballMoved || (flipped && fresh.ball && fresh.ball.kind === 'eon')) cswPaintBallBar();
    }
  }
  // Unmissable chat alert (operator 2026-07-23): a NEW inbound customer
  // message — a later max(last_inbound_at) than the previous load — rings
  // the loud chat cue, flashes the tab title, and posts an OS banner when
  // the tab is hidden. The first load seeds the baseline silently so
  // opening the workspace onto an old backlog doesn't ring.
  let _maxIn = '';
  threads.forEach(t => { if (t.last_inbound_at && String(t.last_inbound_at) > _maxIn) _maxIn = String(t.last_inbound_at); });
  if (s._inboundMax && _maxIn && _maxIn > s._inboundMax) {
    EonSound.play('chat', true);
    eonTitleFlash('● New message');
    eonOsNotify('New customer message', 'A customer is waiting in the CS inbox.');
  }
  if (_maxIn) s._inboundMax = _maxIn;
  cswSyncPins(threads);   // server pins → local cache (cross-device)
  const myEmail = cswMyEmail();
  // Only the core lenses now — comment/story channels collapsed into the inbox.
  // Lane split (design vet M6, operator spec): "Needs reply" counts only
  // the FRESH tail (≤72h) — the number matches what the tap shows. The
  // >72h waiting book is its own Recovery lane, counted separately, so
  // today's work and the win-back tail never share one scary number.
  // 'all' must count what the All lens actually RENDERS. The list drops
  // resolved-and-read threads (cswRenderThreadList), so threads.length said
  // 439 over a list of 425 — the chip and the drawer both claimed 14
  // conversations a rep could never find on that lens (vet 2026-08-09).
  const counts = {
    awaiting: 0, recovery: 0, mine: 0,
    all: threads.filter(t => !(t.cs_status === 'resolved' && !t.unread)).length,
  };
  // "Waiting on you" (2026-08-02): the signed-in rep's own share of the
  // waiting set — the per-rep leg of the daily reply-wait card (the wall
  // spam it replaced never reached the rep who could act). Same Mine
  // identity as the tab, narrowed to threads where the customer spoke last.
  let mineWaiting = 0, mineWorst = 0;
  threads.forEach(t => {
    const isMine = myEmail && (t.assigned_rep_email || '').toLowerCase() === myEmail && t.cs_status !== 'resolved';
    if (isMine) counts.mine++;
    if (t._waiting) {
      if ((t._waitm || 0) > CSW_RECOVERY_MIN) counts.recovery++; else counts.awaiting++;
      if (isMine) { mineWaiting++; mineWorst = Math.max(mineWorst, t._waitm || 0); }
    } else if (t._promise) {
      // A replied thread holding an unkept promise counts as Needs-reply
      // work — same branch shape as the tab filter so count and list agree.
      counts.awaiting++;
    }
  });
  Object.entries(counts).forEach(([k, v]) => {
    const el = document.getElementById('csw-c-' + k); if (el) el.textContent = v;
  });
  // Oldest-wait badge on the Needs-reply tab — the SLA headline for the
  // FRESH lane only (the Recovery tab carries its own age in-list).
  const wEl = document.getElementById('csw-w-awaiting');
  if (wEl) {
    const waits = threads.filter(t => t._waiting && (t._waitm || 0) <= CSW_RECOVERY_MIN).map(t => t._waitm || 0);
    const mx = waits.length ? Math.max.apply(null, waits) : 0;
    wEl.hidden = !waits.length || mx < 1;
    wEl.textContent = cswFmtWait(mx);
    wEl.className = 'csw-filter__w' + (mx >= 120 ? ' csw-filter__w--red' : (mx >= 30 ? ' csw-filter__w--amber' : ''));
  }
  // Mine-tab chip: how many customers are waiting on YOU right now.
  // Reuses the Needs-reply wait chip class; goes amber/red at the same
  // 30m / 2h marks, keyed on the rep's own worst wait.
  const mEl = document.getElementById('csw-w-mine');
  if (mEl) {
    mEl.hidden = !mineWaiting;
    mEl.textContent = mineWaiting ? (mineWaiting + ' waiting on you') : '';
    mEl.className = 'csw-filter__w' + (mineWorst >= 120 ? ' csw-filter__w--red' : (mineWorst >= 30 ? ' csw-filter__w--amber' : ''));
  }
  const sub = document.getElementById('csw-sub');
  // The header line, the chime and the tab-title badge all report WAITING
  // customers (a reply is owed) — not per-rep "unread", which counted
  // threads other reps had already answered (2026-07-25).
  if (sub && s.mode === 'inbox') sub.textContent = `${threads.length} active · ${counts.awaiting} need a reply`;
  // New-waiting awareness: alert when the awaiting count RISES (not on first load).
  if (s.lastAwaiting != null && counts.awaiting > s.lastAwaiting) {
    cswNotifyNewMessage(counts.awaiting);
    // Motion pass 2: the rising count lands with a pulse — the eye finds
    // the new work without hunting.
    if (window.eonMotion) window.eonMotion.pop(document.getElementById('csw-c-awaiting'));
  }
  // Queue chips: refresh live counts on the initial/manual load only
  // (the quiet poll already pays for /threads; don't double it).
  if (!quiet) cswLoadQueues();
  s.lastAwaiting = counts.awaiting;
  // Tab title badge so a backgrounded rep sees how many customers are waiting.
  try { document.title = counts.awaiting > 0 ? `(${counts.awaiting}) CS Workspace · EON` : 'CS Workspace · EON'; } catch (_) {}
  cswFromPaint(threads.length > 0);  // R5b7: painted with the list, so nothing jumps after
  cswRenderThreadList();
  eonTimingFinish(timing, threads.length ? 'ready' : 'empty', 'csw-threads');
  cswResolvePendingThread();  // Scout deep-link — opens once the book is in
  cswLoadStylOffers(quiet);   // Styling Desk proposals — throttled inside
  cswLoadPriority(quiet);     // Priority tab count + book — throttled inside
  cswLoadFollowups(quiet);    // wave E buyer follow-up chips — throttled inside
}

// ── Marketing's buyers count: /?v=cs&from=marketing_buyers&about=whatsapp_buyers
// R5b7 (30 Oct 2026). Marketing's "Open customer care" sits on a COUNT
// (customer_analytics: contacts seen on WhatsApp who have an order), never
// one buyer, so no person rides the link. R5c A11b (31 Oct): the desk asks
// /api/cs/threads?cohort=whatsapp_buyers, which narrows this person's own
// list to those buyers by the tile's own definition (customer_analytics
// WHATSAPP_BUYER_WHERE), and the line says what that list shows, in the
// desk's window beside the tile's (all time). A server that does not answer
// the cohort leaves the R5b7 line, which says there is no such list; the
// general desk is never labelled as those buyers. Without a cohort answer
// the line paints only when this person's own desk read returned chats.
// The keys stay in the address while the person stays put, so a reload
// shows it again; the first tab or chat they move to drops the line and the
// keys (cswFromDrop), as Studio's receiver does (studio.js stNamedDrop).
const CSW_FROM_LINES = {
  'marketing_buyers:whatsapp_buyers': "You came from Marketing's count of WhatsApp buyers with orders. The desk has no list for exactly those buyers yet.",
};
function cswFromPaint(read) {
  const el = document.getElementById('csw-from');
  if (!el) return;
  const co = cswState().cohort;
  let line = '';
  try {
    const q = new URLSearchParams(location.search);
    if (read && State.csAbout && q.get('v') === 'cs' && q.get('about') === State.csAbout && q.get('from') === State.csFrom) {
      line = co ? cswCohortLine(co) : (CSW_FROM_LINES[State.csFrom + ':' + State.csAbout] || '');
    } else if (co && cswFromCohort()) line = cswCohortLine(co);  // R5c A11c: either list says so, even empty, while the address holds its keys
  } catch (_) {}
  const arriving = !!line && el.hidden;
  el.textContent = line;
  el.hidden = !line;
  // It enters once, as the desk's rows do (house kit; still under Reduce Motion).
  if (arriving && window.eonMotion) window.eonMotion.stagger([el], { step: 0, dur: 240, dy: 6 });
}
function cswFromDrop() {
  if (!State.csAbout) return;
  State.csAbout = State.csFrom = State.csAd = null;
  const s = cswState();  // R5c A11b: the whole desk comes back, even mid-read
  if (s.cohort || s.cohortAsked) { s.cohortAsked = false; cswLoadThreads(true); }
  const el = document.getElementById('csw-from');
  if (el) { el.hidden = true; el.textContent = ''; }
  try {
    const q = new URLSearchParams(location.search);
    if (q.get('v') !== 'cs') return;
    q.delete('from'); q.delete('about');
    q.delete('ad');
    const qs = q.toString();
    history.replaceState(history.state, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
  } catch (_) {}
}
// The buyers list's key, only while the address still holds both keys.
function cswFromCohort() {
  try {
    const q = new URLSearchParams(location.search);
    if (State.csFrom === 'marketing_buyers' && State.csAbout === 'whatsapp_buyers' && q.get('v') === 'cs'
      && q.get('from') === State.csFrom && q.get('about') === State.csAbout) return State.csAbout;
    // R5c A11c: the Ads Desk's link names one Meta ad, by its id only.
    if (State.csFrom === 'ads_desk' && State.csAbout === 'ad' && State.csAd && q.get('v') === 'cs'
      && q.get('from') === State.csFrom && q.get('ad') === State.csAd) return 'ad:' + State.csAd;
  } catch (_) {}
  return '';
}
// It opens on All, the lens that shows the whole list (Queue is the shift
// book, another list), marked picked so the shift landing leaves it there.
function cswFromLens() {
  const s = cswState();
  s.houseLens = 'all'; s.filter = 'all'; s.lensPicked = true;
  document.querySelectorAll('#csw-hlens .csw-hlens__k').forEach(x => {
    x.classList.toggle('on', x.dataset.hl === 'all');
    x.setAttribute('aria-selected', String(x.dataset.hl === 'all'));
  });
  document.querySelectorAll('#csw-filters .csw-filter').forEach(x => x.classList.toggle('on', x.dataset.f === 'all'));
}
function cswCohortLine(co) {
  if (String(co.key || '').startsWith('ad:')) return cswAdLine(co);
  if (co.ok !== true) return 'The list of WhatsApp buyers with an order did not load just now. Tap any tab to see all your chats.';
  const n = Number(co.count) || 0, old = Number(co.older_waiting) || 0;
  const shown = n ? n + (n === 1 ? ' conversation' : ' conversations') : 'none you can see';
  return `WhatsApp buyers with an order, last ${Number(co.days) || 7} days: ${shown}`
    + (old ? ` (${old} older, still waiting for a reply)` : '')
    + ". Marketing's count covers all time, not just these days.";
}
// R5c A11c: the line over one ad's chats. "Brought none" is said only to a
// person who sees the whole desk; anyone else hears "none you can see".
function cswAdLine(co) {
  if (co.ok !== true) return 'The chats this ad brought did not load just now. Tap any tab to see all your chats.';
  const n = Number(co.count) || 0, old = Number(co.older_waiting) || 0, days = Number(co.days) || 7;
  if (!n && co.none_brought === true) return `This ad brought no chats in the last ${days} days.`;
  return `Chats this ad brought, last ${days} days: ${n || 'none you can see'}`
    + (old ? ` (${old} older, still waiting for a reply)` : '') + '.';
}

// ── Scout deep-link: /?v=cs&thread=<platform_sender_id> ──────────────
// 2026-08-11, operator bug report. Marketing → Scout candidate cards
// linked "Open the conversation" at `/cs?thread=<sid>`. There has NEVER
// been a /cs route on this server — proved against the real app, not by
// reading: TestClient GET /cs → 404, GET /?v=cs → 200. So every click
// opened FastAPI's 404, which is the blank page the operator reported.
//
// The house deep-link idiom is /?v=<view> (the ALLOWED lists on both boot
// paths). The card now points there and carries the sender; this resolves
// the sender to a thread once the book has loaded.
//
// ONE code path for both platforms on purpose. platform_sender_id is a
// phone on WhatsApp (+2347086602592) and an IG-scoped id on Instagram
// (4382956702032953). An IG-scoped id cannot deep-link to instagram.com —
// it is scoped to the app, not the profile — so the internal thread view
// is the only viable target for Instagram, and using it for WhatsApp too
// keeps the rep inside the desk where the reply tools live.
function cswSenderKey(v) { return String(v || '').replace(/\D/g, ''); }
function cswFindBySender(threads, sid) {
  const raw = String(sid || '');
  if (!raw) return null;
  const dig = cswSenderKey(raw);
  return (threads || []).find((t) => {
    const p = String(t.platform_sender_id || '');
    if (p && p === raw) return true;
    // A WhatsApp id differs only by the leading '+' between surfaces, so
    // compare digits too. Guarded on length so two ids can't collide on a
    // short/empty digit string.
    return !!dig && dig.length >= 6 && cswSenderKey(p) === dig;
  }) || null;
}
function cswThreadKey(t) {
  return String(t && (t.thread_key || t.zoko_customer_id || t.platform_sender_id) || '').trim();
}
function cswOpenKnownSender(sid) {
  const t = cswFindBySender(cswState().threads, sid);
  const key = cswThreadKey(t);
  if (!t || !key) return false;
  cswOpenThread(key);
  return true;
}
async function cswResolvePendingThread() {
  // CLAIM THE REQUEST BEFORE THE FIRST await, not after it. This resolver
  // runs at the end of EVERY thread-list load, and the desk reloads on any
  // live nudge — i.e. whenever any customer anywhere sends a message. If
  // the pending id were still set while the search below was in flight, a
  // nudge landing in that window would re-enter, find the id pending and
  // the search already spent, fall straight through to the bottom, and
  // toast "couldn't find this conversation" — a moment before the original
  // search returned and opened the chat in front of him. Reading it into a
  // local and clearing it here makes the re-entry a no-op at line 3.
  const sid = State.csPendingThread;
  if (!sid) return;
  State.csPendingThread = null;
  const s = cswState();
  const t = cswFindBySender(s.threads, sid);
  if (t && cswThreadKey(t)) {
    cswOpenThread(cswThreadKey(t));
    return;
  }
  // NOT IN THE LOADED LIST → ASK SEARCH. There is no "widen the window"
  // step here, and the absence is deliberate: it was tried and measured to
  // do nothing.
  //
  // Why widening looks right and isn't. thread_list already reads 30 days
  // from the DB whatever `days` says, and already keeps every OLDER thread
  // that still awaits a reply. So asking for 30 instead of 7 can only add
  // threads that are older AND already answered — and those sort below the
  // newest 400, land in the spill, and are not rescued (rescue is
  // unreplied/unread/promise_open only). Measured on this tenant
  // 2026-08-16: 495 threads inside 7 days against a 400 cap, with 1,220
  // more in the 7–30 day band. Every one of those 1,220 is discarded by the
  // cap, so the 7-day and 30-day fetches return the SAME book. Widening
  // bought a second full download of ~544 conversations, a list repaint and
  // two extra toasts, for zero new threads, on every deep link that missed.
  //
  // /api/cs/search does the whole job and strictly more: no date window, no
  // 400 cap (cs_inbox.search_all), and it returns thread_key =
  // COALESCE(zoko_customer_id, platform_sender_id) — exactly the key
  // cswOpenThread takes. The desk's own global search already opens
  // unloaded threads by that key (see the [data-open] rows), so this rides
  // the house path rather than inventing one. It also reaches PAST 30 days,
  // which widening never could: the Scout tab has no date filter at all, so
  // cards outlive the desk's window by design.
  //
  // Two spellings, because search_all is a TEXT match while the in-book
  // matcher normalizes digits: a card holding '+234…' cannot ILIKE against
  // a stored '234…'. Measured 2026-08-16: all 47 live cards match exactly
  // today, so this is insurance against one future writer normalizing one
  // side — the in-book matcher already tolerates it and the fallback should
  // not be the weaker leg of the same lookup.
  //
  // Strict on identity: search_all matches ILIKE '%q%', so open ONLY on a
  // digit-equal thread_key or a single unambiguous hit. Never a best guess
  // among several — opening the wrong customer's chat is not worth it.
  if (!s._csDeepSearched) {
    s._csDeepSearched = true;
    const digits = cswSenderKey(sid);
    const spellings = [String(sid)];
    if (digits && digits.length >= 6 && digits !== String(sid)) spellings.push(digits);
    for (const q of spellings) {
      let r;
      try {
        r = await api('/api/cs/search?q=' + encodeURIComponent(q) + '&limit=5');
      } catch (_) { r = null; }
      // A FAILED SEARCH IS NOT AN EMPTY ONE. The route answers 200 with
      // {ok:false, error:"db: …"} when the query itself fails, so reading
      // only `customers` would report a broken search as "nothing found"
      // and then point the operator at the very thing that just broke.
      if (!r || r.ok === false) { cswSearchDown(); return; }
      const rows = r.customers || [];
      const exact = rows.find(c => cswSenderKey(c.thread_key) === digits
                                || String(c.thread_key || '') === String(sid));
      const only = rows.length === 1 ? rows[0] : null;
      const pick = exact || only;
      if (pick && pick.thread_key) {
        // "outside the LOADED LIST", never "outside 30 days" — for 12 of
        // the 47 live cards the conversation is well inside 30 days and was
        // only pushed out by the 400 cap. The reproduce case in the bug
        // report is one of them: IG 4382956702032953 last wrote 8 days ago.
        // Claiming a 30-day reach for an 8-day-old chat would be the same
        // species of untruth this whole PR exists to remove.
        cswToast('Found it outside the loaded list — opening.');
        cswOpenThread(String(pick.thread_key));
        return;
      }
    }
  }
  // SAY WHAT WE ACTUALLY KNOW, WHICH IS LESS THAN "IT ISN'T THERE".
  // The desk saw a capped list and search returned no unambiguous match.
  // It has NOT established that the sender never wrote — thread_list caps
  // at 400 and counts the rest into hidden_replied, which the desk already
  // surfaces elsewhere.
  const hidden = Number(s.hiddenReplied || 0);
  cswToast(hidden > 0
    ? `Couldn't find this conversation in the ${s.threads.length} loaded — `
      + `${hidden} quieter one${hidden === 1 ? '' : 's'} not shown. Search finds them.`
    : `Couldn't find this conversation in the ${s.threads.length} loaded — try Search.`,
    true);
}

// A search that FAILED is reported as a failure, not as an absence — the
// operator must never be pointed at a surface that is itself down.
function cswSearchDown() {
  cswToast('Search is not answering — could not look for this conversation. '
           + 'Try again in a moment.', true);
}

// Wave E4: buyer threads waiting past the 24h window — a one-tap pickup
// chip. The tap rides the EXISTING /threads/{cid}/pickup route (consent,
// cooldown and the template gate all live there); nothing new sends.
let _cswFollowups = new Map();      // zoko_customer_id -> followup row
let _cswFupLoadedAt = 0;
async function cswLoadFollowups(quiet) {
  if (quiet && Date.now() - _cswFupLoadedAt < 5 * 60 * 1000) return;
  _cswFupLoadedAt = Date.now();
  let d;
  try { d = await api('/api/cs/followups'); }
  catch (_) { return; }
  const next = new Map();
  (d && d.followups || []).forEach(f => {
    if (f.zoko_customer_id) next.set(String(f.zoko_customer_id), f);
  });
  const changed = next.size !== _cswFollowups.size
    || [...next.keys()].some(k => !_cswFollowups.has(k));
  _cswFollowups = next;
  if (changed && cswState().mode === 'inbox') cswRenderThreadList();
}
// ⛔ The door words routes_cs passes through RAW on purpose, so these
// buttons can RECOGNISE them (see app/modules/wa_refusal_words.py and
// routes_cs.pickup_thread). A button may key on them; not one of them may
// ever be PRINTED for a rep — they are machine words, and a rep reading
// "no_inbound_text" is the exact defect FR da0743f8 exists to remove.
// Every catch that toasts a pickup refusal runs its text through here.
function cswSayableRefusal(msg) {
  const RAW = ['template_awaiting_approval', 'no_inbound_text', 'no_phone'];
  return RAW.some(w => msg.includes(w)) ? '' : msg;
}
async function cswSendFollowup(chip) {
  const cid = chip.dataset.fup;
  chip.textContent = 'Sending…';
  try {
    const r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/pickup`, { method: 'POST', body: '{}' });
    if (r && r.ok !== false) {
      cswPickupRemember(cid);   // every pick-up door shares one memory (2026-09-06)
      _cswFollowups.delete(cid);
      cswRenderThreadList();
      cswToast('Pickup sent — their own last words as the topic');
    } else { chip.textContent = '⏱ Follow up'; cswToast(cswSayWhy({ message: cswSayableRefusal(String((r && r.error) || '')) }, { lead: 'Follow-up not sent.' }), true); }
  } catch (e) { chip.textContent = '⏱ Follow up'; cswToast(cswSayableRefusal(String((e && e.message) || '')) ? cswSayWhy(e, { send: true }) : 'Could not send', true); }
}

// ── The Styling Desk (wave D, 2026-07-27 — PROPOSE-ONLY week one) ─────
// The 10:05 cron mints offers; they surface as a violet chip on the
// matching thread row. The chip opens the offer sheet: the EXACT rendered
// template + the curated looks the customer gets on YES. The rep's Send
// tap is the ONLY send — nothing goes out on its own.
let _cswStylOffers = new Map();      // digits-only phone -> proposal
let _cswStylLoadedAt = 0;
async function cswLoadStylOffers(quiet) {
  if (quiet && Date.now() - _cswStylLoadedAt < 5 * 60 * 1000) return;
  _cswStylLoadedAt = Date.now();
  let d;
  try { d = await api('/api/cs/styling-desk/proposals'); }
  catch (_) { return; }   // proposals are an extra — never an error surface
  const next = new Map();
  (d && d.proposals || []).forEach(p => {
    const k = String(p.recipient || '').replace(/\D/g, '');
    if (k) next.set(k, p);
  });
  const changed = next.size !== _cswStylOffers.size
    || [...next.keys()].some(k => !_cswStylOffers.has(k));
  _cswStylOffers = next;
  if (changed && cswState().mode === 'inbox') cswRenderThreadList();
  // THE STAGE: the open thread's offer (if any) stands on the Stage too.
  if (changed && cswState().active && typeof cswStageStylistSync === 'function') cswStageStylistSync(cswState().active);
}
function cswStylOfferFor(t) {
  if (!_cswStylOffers.size) return null;
  const k = String(t.platform_sender_id || t.zoko_customer_id || '')
    .replace(/\D/g, '');
  return (k && _cswStylOffers.get(k)) || null;
}
function cswStylOfferSheet(prop) {
  const wrap = document.createElement('div');
  wrap.className = 'csw-styl-ov';
  // THE STAGE anatomy (2026-08-18): the same card the desk stages — name,
  // reason line, the exact words the customer gets, at most two verbs.
  const looks = (prop.looks || []).filter(l => l && l.image_url);
  const first = prop.first_name || 'customer';
  const checking = prop.send_ready == null;
  const ready = prop.send_ready !== false;
  const lifecycleNote = prop.send_note || (ready ? 'EON checks it again when you tap send.' : 'Review the chat or dismiss this saved offer.');
  wrap.innerHTML = `<div class="csw-styl-sheet">
    <div class="csw-styl-h"><span class="csw-stage__from" aria-hidden="true"></span>${ready ? 'Stylist offer' : 'Saved stylist offer'} <span class="csw-stage__occ">· for ${esc(first)}</span></div>
    <div class="csw-stage__why"><span class="dot"></span><span>${prop.product_title ? `<b>Around the ${esc(prop.product_title)}</b> — ` : ''}${looks.length} look${looks.length === 1 ? '' : 's'} the studio already shot.</span></div>
    <div class="csw-styl-looks">${looks.map(l => `<img src="${esc(cswImgSrc(l.image_url))}" alt="" loading="lazy">`).join('')}</div>
    ${ready ? `<div class="csw-stage__preview"><div class="csw-stage__ph"><span>What ${esc(first)} gets</span><span>the stylist's words</span></div><div class="csw-stage__msg">${esc(prop.offer_text || '')}</div></div>` : ''}
    <div class="csw-styl-note">${esc(lifecycleNote)}${ready ? " On YES these looks go out automatically with the stylist's words. Any other reply lands here like a normal message." : ''}</div>
    <div class="csw-styl-actions">
      <button class="csw-looksend csw-styl-send" id="csw-styl-send" type="button">${ready ? (checking ? 'Check & send' : 'Send offer') : 'Review chat'}</button>
      <button class="csw-lookskip" id="csw-styl-dismiss" type="button">Dismiss</button>
    </div>
  </div>`;
  document.body.appendChild(wrap);
  eonBackdropClose(wrap, () => wrap.remove());
  const done = (msg, bad) => {
    cswToast(msg, !!bad); wrap.remove();
    if (typeof cswStageStylistSync === 'function' && cswState().active) cswStageStylistSync(cswState().active);
  };
  wrap.querySelector('#csw-styl-send').addEventListener('click', async (e) => {
    if (!ready) {
      wrap.remove();
      if (cswOpenKnownSender(prop.recipient)) return;
      else cswToast('Open this customer’s chat before deciding.', true);
      return;
    }
    const b = e.currentTarget; b.disabled = true; b.textContent = 'Sending…';
    try {
      const r = await api(`/api/cs/styling-desk/${encodeURIComponent(prop.id)}/send`, { method: 'POST', body: '{}' });
      if (r.ok) {
        _cswStylOffers.delete(String(prop.recipient || '').replace(/\D/g, ''));
        cswRenderThreadList();
        done('Offer sent — YES serves the looks automatically.');
      } else { b.disabled = false; b.textContent = 'Send offer'; cswToast(cswSayWhy({ message: String(r.error || '') }, { lead: 'Offer not sent.' }), true); }
    } catch (_) { b.disabled = false; b.textContent = 'Send offer'; cswToast('Could not send', true); }
  });
  wrap.querySelector('#csw-styl-dismiss').addEventListener('click', async () => {
    try {
      await api(`/api/cs/styling-desk/${encodeURIComponent(prop.id)}/dismiss`, { method: 'POST', body: '{}' });
      _cswStylOffers.delete(String(prop.recipient || '').replace(/\D/g, ''));
      cswRenderThreadList();
      done('Dismissed — it will not be offered again for this order.');
    } catch (_) { done('Could not dismiss', true); }
  });
}

// Vibrate + chime on a new inbound (gated by a mute pref). Lightweight —
// real-time push is the next step; this closes the "missed a how-much?" gap.
function cswNotifyNewMessage(unread) {
  try { if (navigator.vibrate) navigator.vibrate(40); } catch (_) {}
  if (localStorage.getItem('csw-mute') === '1') return;
  try {
    const A = window.AudioContext || window.webkitAudioContext;
    if (!A) return;
    const ctx = cswState()._actx || (cswState()._actx = new A());
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = 880; g.gain.value = 0.04;
    o.start(); o.stop(ctx.currentTime + 0.12);
  } catch (_) {}
}

// Compact wait label: '47m' / '2h 10m' / '2d 23h'.
// The day form used to floor to whole days, so the Needs-reply SLA badge
// printed '2d' for a measured 4,315-minute wait — 2d 23h 55m, understated
// by nearly a full day five minutes short of the 72h Recovery cutoff
// (CSW_RECOVERY_MIN). The badge exists to say how close the lane edge is;
// it now keeps the hours (vet 2026-08-09).
function cswFmtWait(m) {
  m = Math.max(0, Math.round(m || 0));
  if (m < 60) return m + 'm';
  if (m < 1440) return Math.floor(m / 60) + 'h ' + (m % 60) + 'm';
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  return h ? d + 'd ' + h + 'h' : d + 'd';
}


// Only this authorized CS visit owns messages metadata. No credential is
// retained here: the existing session reader supplies identity, not authority.
function cswThreadMetaScope(s, permission = true) {
  // Transcript freshness must also work when the stricter local permission
  // projection is unreadable. The server authorizes the read. Keep only
  // identity/access facts here, never the credential; identity-changing JWT writes
  // advance the generation, including an A -> B -> A transition.
  if (!permission) return JSON.stringify([State.tenant?.id,State.tenant?.tenant_id,State.tenant?.slug,
    State.user?.id,State.user?.user_id,State.user?.staff_id,State.member,
    State.view,State.isStaff,State.impersonating,
    s.lockedQueue || '',s.asViewStaffId || '',s.asRepEmail || '']);
  const a = eonWorkroomSession();
  if (!a || a.impersonated) return null;
  return JSON.stringify([a.tenantId,a.actorId,a.authSession,a.view,a.access,
    s.lockedQueue || '',s.asViewStaffId || '',s.asRepEmail || '']);
}


function cswThreadMetaCapture(s, cid, seq) {
  s._threadMetaRequired = true;
  return {cid,seq,generation:s._threadMetaGeneration || 0,scope:cswThreadMetaScope(s),
    readScope:cswThreadMetaScope(s, false),started:performance.now()};
}

function cswThreadMetaCurrent(s, read, permission = true, latest = true) {
  return (!permission || !!read.scope) && State.csw === s && s.active === read.cid
    && cswThreadMetaScope(s, false) === read.readScope
    && (s._threadMetaGeneration || 0) === read.generation
    && (!latest || s._evidenceReadSeq === read.seq) && cswThreadMetaScope(s) === read.scope;
}

function cswThreadMetaApply(s, read, meta) {
  if (!cswThreadMetaCurrent(s, read)) return false;
  const unknown = {last_inbound_at:null,window_expires_at:null,window_open:null};
  const stamp = value => typeof value === 'string' && /(?:Z|[+-]\d{2}:\d{2})$/.test(value)
    ? Date.parse(value) : NaN;
  const observed = stamp(meta?.observed_at);
  const old = s._threadMeta;
  let valid = meta?.version === 1 && meta.thread_key === read.cid && Number.isFinite(observed);
  const existing = s.activeThread;
  if (valid && meta.readable === true) {
    valid = typeof meta.thread_sender === 'string' && !!meta.thread_sender
      && Array.isArray(meta.aliases) && meta.aliases.includes(read.cid) && meta.aliases.includes(meta.thread_sender)
      && ['whatsapp','instagram'].includes(meta.platform)
      && (!existing?.zoko_customer_id || String(existing.zoko_customer_id) === read.cid)
      && (!existing?.platform_sender_id || existing.platform_sender_id === meta.thread_sender)
      && (!existing?.channel || existing.channel === meta.platform);
  }
  // Only a compatible response may be ignored as an older observation. A
  // malformed/wrong-thread response must clear any earlier permission.
  if (valid && old?.scope === read.scope && observed < old.observed) return false;
  let fields = {...unknown}, platform = null, remaining = 0, inbox = false;
  // A failed metadata read cannot revoke a compatible inbox deadline. Keep
  // this fallback separate from metadata permission, which still clears on
  // failure. Explicit identity conflicts and malformed responses fail closed.
  if (valid && meta.readable === false && meta.reason === 'read_failed'
      && (!old || old.inbox) && existing?.zoko_customer_id === read.cid
      && existing.channel === 'whatsapp' && existing.platform_sender_id) {
    const inbound = stamp(existing.last_inbound_at);
    const expiry = existing.window_expires_at == null ? inbound + 86400000 : stamp(existing.window_expires_at);
    if (Number.isFinite(inbound) && inbound <= observed && expiry - inbound === 86400000
        && typeof existing.window_open === 'boolean') {
      fields = {last_inbound_at:existing.last_inbound_at,
        window_expires_at:new Date(expiry).toISOString(),window_open:existing.window_open};
      platform = 'whatsapp'; inbox = true;
      remaining = expiry - observed - Math.max(0, performance.now() - read.started);
    }
  }
  if (valid && meta.readable === true) {
    platform = meta.platform;
    if (platform === 'instagram' && meta.reason === 'not_whatsapp') {
      // This contract supplies IG identity only; its policy/display stays separate.
      fields = {last_inbound_at:existing?.last_inbound_at || null,
        window_expires_at:existing?.window_expires_at || null,window_open:existing?.window_open ?? null};
    } else if (platform === 'whatsapp') {
      const inbound = stamp(meta.last_inbound_at), expiry = stamp(meta.window_expires_at);
      const clockOK = Number.isFinite(inbound) && inbound <= observed && expiry - inbound === 86400000;
      if (meta.state === 'open' && meta.window_open === true && clockOK && expiry > observed) {
        fields = {last_inbound_at:meta.last_inbound_at,window_expires_at:meta.window_expires_at,window_open:true};
        // Subtract the entire request duration conservatively. Browser clocks
        // can close this deadline, never give it extra time or reopen it.
        remaining = expiry - observed - Math.max(0, performance.now() - read.started);
      } else if (meta.state === 'closed' && meta.window_open === false
          && ((meta.reason === 'no_inbound' && meta.last_inbound_at === null && meta.window_expires_at === null)
            || (clockOK && expiry <= observed))) {
        fields = {last_inbound_at:meta.last_inbound_at,window_expires_at:meta.window_expires_at,window_open:false};
      }
    }
  }
  const sameDeadline = old?.scope === read.scope && fields.window_open === true
    && fields.window_expires_at === old.fields.window_expires_at;
  const tick = performance.now();
  let closedThrough = old?.closedThrough ?? -Infinity;
  if (old?.fields.window_open === true && (old.expired || tick >= old.until))
    closedThrough = Math.max(closedThrough, stamp(old.fields.window_expires_at));
  s._threadMeta = {cid:read.cid,scope:read.scope,generation:read.generation,
    observed:valid ? observed : (old?.observed ?? -Infinity),fields,platform,inbox,
    sender:inbox ? existing.platform_sender_id : valid && meta.readable === true ? meta.thread_sender : null,closedThrough,
    clock:Math.max(valid ? observed : -Infinity, old?.clock ?? -Infinity),
    until:sameDeadline ? Math.min(old.until, tick + remaining) : tick + remaining,
    expired:fields.window_open === true && stamp(fields.window_expires_at) <= closedThrough};
  s.activeThread = {zoko_customer_id:read.cid,...existing,...fields};
  if (platform && !inbox) Object.assign(s.activeThread, {channel:platform,_plat:platform === 'whatsapp' ? 'wa' : 'ig',
    platform_sender_id:meta.thread_sender});
  cswRefreshReplyWindows(); // In place; no reopen, mark-seen or composer rebuild.
  if (s.activeThread.ball?.kind === 'eon') cswPaintBallBar();
  return valid;
}

function cswThreadMetaWindow(t, now) {
  const s = State.csw;
  if (!s?._threadMetaRequired || !s.active || String(t?.zoko_customer_id || '') !== s.active) return t;
  const m = s._threadMeta;
  const unknown = {...t,last_inbound_at:null,window_expires_at:null,window_open:null};
  if (!m && t.channel === 'instagram') return t;
  if (!m || m.cid !== s.active || m.generation !== (s._threadMetaGeneration || 0)
      || !m.scope || m.scope !== cswThreadMetaScope(s)) return unknown;
  if (m.platform && ((t.channel && t.channel !== m.platform)
      || (t.platform_sender_id && t.platform_sender_id !== m.sender))) return unknown;
  if (m.platform === 'whatsapp' && m.fields.window_open === true) {
    const expiry = Date.parse(m.fields.window_expires_at);
    // Keep the display clock moving toward the same server deadline even
    // if the device clock moves backwards. It can only shorten permission.
    m.clock = Math.max(m.clock, now, expiry - Math.max(0, m.until - performance.now()));
    if (m.clock >= expiry) { m.expired = true; m.closedThrough = Math.max(m.closedThrough, expiry); }
  }
  return {...t,...m.fields,...(m.platform ? {channel:m.platform,_plat:m.platform === 'instagram' ? 'ig' : 'wa'} : {}),
    ...(m.platform === 'whatsapp' && m.fields.window_open === true ? {_threadMetaNow:m.clock} : {}),
    ...(m.expired ? {window_open:false} : {})};
}

// WhatsApp 24h free-message window — minutes left (null if unknown/n-a).
// DESK ADVANCE (2026-09-06). The reply permission and the display clock
// share an exact deadline. A cached window_open=true cannot outlive it.
// CTWA's free-pricing period is deliberately not a messaging permission.
function cswReplyWindow(t, now = Date.now()) {
  // This renderer also serves detached cards. Only an authorized active CS
  // visit overlays its metadata; a plain card remains a pure snapshot.
  if (typeof State !== 'undefined' && State.csw?._threadMetaRequired) {
    t = cswThreadMetaWindow(t, now);
    now = Math.max(now, t?._threadMetaNow || -Infinity);
  }
  const wa = !t || (t._plat || cswPlatform(t)) === 'wa';
  const inbound = Date.parse(t?.last_inbound_at || '');
  const supplied = Date.parse(t?.window_expires_at || '');
  const expires = Number.isFinite(supplied) ? supplied
    : Number.isFinite(inbound) && inbound <= now ? inbound + 86400000 : NaN;
  if (t?.window_open === false || (Number.isFinite(expires) && expires <= now)) {
    // Plain words (vet round 2, 2026-09-26): was "Window expired · template
    // needed" / "· delivery uncertain".
    return { state: 'closed', wa, expires, label: wa ? 'Reply window closed · send a template' : 'Reply window closed · Instagram may not deliver' };
  }
  if (!Number.isFinite(expires) || expires > now + 86400000) {
    // Plain words (vet 2026-09-24, fault 20): was "Reply window unverified".
    return { state: 'unknown', wa, expires: null, label: 'Not sure the reply window is open' };
  }
  const mins = Math.ceil((expires - now) / 60000);
  const left = mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ''}`;
  return { state: mins <= 120 ? 'closing' : 'open', wa, expires,
    label: mins <= 120 ? `Reply window closes in ${left}` : `Reply open · ${left} left` };
}

function cswWindowBlocks(t) {
  const w = cswReplyWindow(t);
  return w.wa && !['open', 'closing'].includes(w.state);
}

// Bind dispatch to its target, including retries and sheets opened before expiry.
// The backend remains authoritative; no local stale/open flag can extend a deadline.
function cswThreadFor(cid) {
  const s = cswState(), key = String(cid || '');
  if (!key) return null;
  if (s.activeThread && String(s.activeThread.zoko_customer_id) === key) return s.activeThread;
  return (s.threads || []).find(t => String(t.zoko_customer_id) === key) || null;
}

function cswRequireReplyWindow(cid) {
  if (!cswWindowBlocks(cswThreadFor(cid))) return;
  if (String(cswState().active) === String(cid)) cswExplainReplyBlock();
  throw new Error('Reply held. The customer reply window is expired or unverified; your words and media remain available.');
}

async function cswSendApi(path, options) {
  const target = /^\/api\/cs\/threads\/([^/]+)\/send(?:-[a-z]+)?$/.exec(path);
  if (!target) throw new Error('Unrecognised customer send route.');
  try {
    cswRequireReplyWindow(decodeURIComponent(target[1]));
  }
  catch (e) { throw Object.assign(e, {notSent:true}); }
  return api(path, options);
}

function cswWindowWords(t) {
  const w = cswReplyWindow(t);
  return `<span class="csw-windowwords csw-windowwords--${w.state}"><i aria-hidden="true"></i>${esc(w.label)}</span>`;
}

function cswPriorityFor(t) {
  const cid = String(t?.zoko_customer_id || '');
  return cid ? (_cswPrio.items || []).find(p => String(p.zoko_customer_id) === cid && p.status !== 'cleared') : null;
}

function cswServiceMeta(t, showWhy = true) {
  if (t?._also_stub) return '';
  t = t || {};
  const p = showWhy ? cswPriorityFor(t) : null;
  return `<div class="csw-service-meta"><span data-csw-window="${esc(t.zoko_customer_id || '')}">${cswWindowWords(t)}</span>`
    + (p?.reason_text ? `<span class="csw-service-meta__why">${p.reason_key === 'business_followup' ? 'Enquiry context' : 'Why priority'} · ${esc(p.reason_text)}</span>` : '') + '</div>';
}

function cswReceiptState(r, t) {
  const b = r.body || {};
  if (b.held === true) return { word: 'Held by EON', kind: 'held', detail: 'Draft only. It has not been sent.' };
  const status = String(b.status || '').toLowerCase();
  if (status === 'failed') return { word: 'Failed', kind: 'failed', detail: cswFailWhy(b.errors || b.error, t ? (t._plat || cswPlatform(t)) : undefined) };
  const ig = (t?._plat || cswPlatform(t || {})) === 'ig';
  if (ig && ['accepted', 'submitted', 'sent', 'received', 'delivered', 'seen', 'read', 'played'].includes(status)) {
    return { word: 'Accepted', kind: 'accepted', detail: 'Instagram transport acceptance. Delivery is not established here.' };
  }
  if (['seen', 'read', 'played'].includes(status)) return { word: status === 'played' ? 'Played' : 'Read', kind: 'read', detail: 'A channel receipt was recorded. This does not confirm the issue is resolved.' };
  if (['received', 'delivered'].includes(status)) return { word: 'Delivered', kind: 'delivered', detail: 'A delivery receipt was recorded. The customer may not have read it.' };
  if (['sent', 'accepted', 'submitted'].includes(status)) return { word: status === 'sent' ? 'Sent' : 'Accepted', kind: 'accepted', detail: 'The channel accepted this message. Delivery is not yet established.' };
  return { word: 'Status unverified', kind: 'unknown', detail: 'No recognised sending or delivery receipt is available.' };
}

function cswThreadProgress(t, rows, draft) {
  const time = r => Date.parse(r.platform_timestamp || r.created_at || '') || 0;
  const inbound = Math.max(Date.parse(t?.last_inbound_at || '') || 0,
    ...rows.filter(r => r.direction === 'inbound').map(time), 0);
  const out = rows.filter(r => r.direction === 'outbound' && time(r) >= inbound && !(r.body || {}).deleted);
  const replied = out.some(r => ['accepted', 'delivered', 'read'].includes(cswReceiptState(r, t).kind));
  const prepared = !!String(draft || '').trim() || out.some(r => (r.body || {}).held === true);
  // A team closure is a recorded workflow action, not a customer outcome.
  if (t?.cs_status === 'resolved' && !t.unread && !t._waiting && !prepared) return { index: 3, label: 'Marked resolved by the team', note: 'Customer satisfaction is not established by closing a thread.' };
  if (prepared) return { index: 2, label: 'Draft ready to review', note: 'A draft exists. Check the facts and reply permission before sending.' };
  if (replied) return { index: 3, label: 'Confirm the outcome', note: 'A reply is recorded. A successful customer outcome still needs confirmation.' };
  if (inbound) return { index: 1, label: 'Prepare the next reply', note: 'The customer has spoken. Use their message and the customer file to prepare a reply.' };
  return { index: 0, label: 'Understand the request', note: 'The loaded history does not yet establish a customer request.' };
}

function cswAdvanceRows() {
  const s = cswState();
  return s._advanceTranscriptCid === s.active ? s.transcript || [] : [];
}

function cswPaintServiceRail() {
  if (typeof cswEvidenceChanged === 'function') cswEvidenceChanged();
  const s = cswState(), host = document.getElementById('csw-service-rail');
  if (!host || !s.active) return;
  const t = s.activeThread || {}, p = cswPriorityFor(t);
  const inp = document.getElementById('csw-input');
  const step = cswThreadProgress(t, cswAdvanceRows(), inp?.value || cswLoadDraft(s.active));
  const sig = [s.active, step.index, step.label, p?.reason_text || ''].join('|');
  if (host.dataset.sig === sig) return;
  host.dataset.sig = sig;
  // Only the text/steps are reconciled; buttons retain keyboard focus.
  host.querySelector('.csw-service-rail__path').innerHTML = ['Understand', 'Prepare', 'Reply', 'Confirm'].map((label, i) =>
    `<span class="${i < step.index ? 'passed' : i === step.index ? 'current' : ''}"${i === step.index ? ' aria-current="step"' : ''}><i aria-hidden="true">${i + 1}</i>${label}</span>`).join('');
  host.querySelector('[data-service="progress"]').setAttribute('aria-label', `${step.label}. View the four-step service path`);
  const why = host.querySelector('[data-service="why"]');
  why.textContent = p?.reason_key === 'business_followup' ? 'Enquiry context' : p ? 'Why priority' : 'Thread context';
}

function cswEvidenceChanged() {
  window.dispatchEvent(new Event('csw-evidence-change'));
}

function cswOpenDecisionEvidence() {
  return openDecisionEvidence({
    read: () => {
      const s = cswState(), reading = s._evidenceTranscript || {};
      return {cid:s.active, loadedCid:reading.cid, rows:reading.rows,
        readState:reading.state, observedAt:reading.observedAt,
        thread:s.activeThread || {}, service:s._serviceCases?.snapshot?.(), nextStep:s._nextStep?.key === String(s.active)+'|'+cswNextStepKey(reading.rows || []) ? {...s._nextStep, visible:!!document.getElementById('csw-next-step') && !document.getElementById('csw-next-step').hidden} : null};
    }, receipt:cswReceiptState, formatTime:cswEvidenceTime, overlay:cswOverlay,
    onSource:(cid,id) => {
      if(cswState().active !== cid) return;
      const host = document.getElementById('csw-script');
      const row = host?.querySelector(`.csw-row[data-mid="${CSS.escape(id)}"]`) || [...(host?.querySelectorAll('.csw-row[data-evidence-mids]') || [])].find(el => {
        try { const ids=JSON.parse(el.dataset.evidenceMids); return Array.isArray(ids) && ids.includes(String(id)); } catch (_) { return false; }
      });
      if(!row) return null;
      return () => {
        if(cswState().active !== cid || !row.isConnected) return;
        row.setAttribute('tabindex','-1'); row.focus({preventScroll:true});
        row.scrollIntoView({block:'center',behavior:'auto'});
      };
    }, onTool:(cid,tool) => {
      if(cswState().active !== cid) return;
      if(tool === 'customer') { document.getElementById('csw-inbox')?.classList.add('has-active'); cswOpenCustomerDetails(document.getElementById('csw-360btn2')); }
      else if(['receipts','cases','payment'].includes(tool)) cswServiceSheet(tool);
    }
  });
}

function cswServiceSheet(view) {
  if (view === 'cases') return openServiceCases({cid:cswState().active,api,isActive:cid=>cswState().active===cid,overlay:cswOverlay,onChanged:()=>{_cswPrio.at=0;cswLoadPriority();cswState()._serviceCases?.refresh();}});
  if (view === 'payment') {
    return openPaymentEvidence({cid: cswState().active, api,
      isActive: cid => cswState().active === cid,
      overlay: cswOverlay, composer: () => document.getElementById('csw-input'), saveDraft: cswSaveDraft});
  }

  const s = cswState(), t = s.activeThread || {};
  const rows = cswAdvanceRows(), p = cswPriorityFor(t);
  const step = cswThreadProgress(t, rows, document.getElementById('csw-input')?.value || cswLoadDraft(s.active));
  const title = view === 'receipts' ? 'What actually happened' : view === 'why' ? 'Why this thread needs attention' : step.label;
  let body = '';
  if (view === 'receipts') {
    const events = rows.filter(r => r.direction === 'outbound').slice(-16).reverse();
    body = '<p class="csw-service-sheet__intro">The latest 16 outbound records in loaded history. Earlier messages may not be loaded. Times below are message times, not delivery-receipt times.</p>';
    body += events.length ? '<ol class="csw-receipt-trail">' + events.map(r => {
      const rs = cswReceiptState(r, t), b = r.body || {};
      const removed = b.deleted || b.is_deleted || b.revoked;
      const preview = removed ? 'Message removed' : String(b.text || b.caption || b.template_name || r.type || 'Message').slice(0, 180);
      return `<li class="csw-receipt-trail__event csw-receipt-trail__event--${rs.kind}"><div><b>${esc(rs.word)}</b><time>${esc(cswEvidenceTime(r.platform_timestamp || r.created_at))}</time></div><p>${esc(preview)}</p><small>${esc(rs.detail)}</small></li>`;
    }).join('') + '</ol>' : '<p class="csw-service-empty">No outbound receipts in the loaded history yet.</p>';
  } else if (view === 'why') {
    body = `<p class="csw-service-sheet__lead">${esc(p?.reason_text || (t._waiting ? 'The customer is still waiting for a reply.' : 'This thread has no active card on the loaded priority board.'))}</p>`;
    if (p) body += cswPrioWhyHtml(p) + '<p>The explanation comes from this thread’s priority record. The existing queue decides the order.</p>';
    body += `<div class="csw-service-fact">${cswWindowWords(t)}</div><p>A closing window changes the available way to reply. An expired window does not mean the customer’s request is resolved.</p>`;
  } else {
    body = `<p class="csw-service-sheet__intro">${esc(step.note)}</p><ol class="csw-path-detail">` + [
      ['Understand', 'Read the request, previous answers and customer context.'],
      ['Prepare', 'Check the answer, stock, order or promise using the existing desk tools.'],
      ['Reply', 'Review the draft and channel permission. Held and accepted messages have different meanings.'],
      ['Confirm', 'Establish whether the customer’s issue is resolved. A read receipt alone is not satisfaction.'],
    ].map(([name, note], i) => `<li${i === step.index ? ' aria-current="step"' : ''}><span>${i + 1}</span><div><b>${name}</b><p>${note}</p></div></li>`).join('') + '</ol>';
  }
  const ov = cswOverlay(`<div class="csw-sheet__head"><h3 id="csw-service-title">${esc(title)}</h3><button class="csw-x" type="button">✕</button></div><div class="csw-service-sheet">${body}</div>`, false);
  ov.querySelector('[role="dialog"]').setAttribute('aria-labelledby', 'csw-service-title');
  ov.querySelector('.csw-x').onclick = () => ov.remove();
}

function cswEvidenceTime(value) {
  const date = new Date(value || NaN);
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(date.getTime()))
    return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
  return Number.isFinite(date.getTime()) ? new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium', timeStyle: 'short', timeZone: 'Africa/Lagos',
  }).format(date) + ' WAT' : 'Time unverified';
}

function cswEngineReadout(d, readAt) {
  const h = d?.health || {};
  const beats = Array.isArray(h.beats) ? h.beats : [];
  const stages = [ ['sense', 'Notice', 'Find repeated friction in customer conversations.'],
    ['score', 'Review', 'Grade the answer against customer-care standards.'],
    ['measure', 'Measure', 'Link the conversation to an observed outcome.'],
    ['change', 'Improve', 'Propose a change supported by evidence.'],
    ['verify', 'Verify', 'Check the change before it earns more autonomy.'] ];
  const status = { live: 'Producing', idle: 'Quiet / idle', dark_by_design: 'Deliberately off',
    unwired: 'Not connected', absent: 'Not built here', unknown: 'Unverified' };
  const closed = h.ok === true && h.closed === true && stages.every(([id]) => beats.some(b => b.beat === id && b.verdict === 'live'));
  const readable = h.ok === true && beats.length > 0;
  const outstanding = stages.filter(([id]) => !beats.some(b => b.beat === id && b.verdict === 'live')).map(([, name]) => name);
  const summary = closed ? 'Each stage has a recorded output. Improvement still needs an outcome check.'
    : outstanding.length ? `${outstanding.join(', ')}: current output is not established. Open a stage to see why.`
    : 'Stage outputs are recorded, but the source has not confirmed that the full loop is closed.';
  const count = (key) => {
    const n = d?.counts?.error ? null : d?.counts?.[key];
    return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n.toLocaleString() : '—';
  };
  return `<div class="csw-engine-overview"><div class="csw-engine-overview__word">${closed ? 'Five stages producing' : readable ? 'The loop has work to do' : 'Loop status unverified'}</div>
    <p>${esc(readable ? summary : 'The source did not establish the loop’s state. Try refreshing the reading.')}</p>
    <small>Read ${esc(cswEvidenceTime(readAt))} · this is a snapshot</small></div>
    <div class="csw-engine-counts"><div><b>${count('drafts_30d')}</b><span>Draft events · 30 days</span></div><div><b>${count('critic_scores')}</b><span>Review scores · all time</span></div><div><b>${count('invariant_hits')}</b><span>Rule hits · all time</span></div></div>
    <ol class="csw-engine-stages">${stages.map(([id, name, purpose], i) => {
      const b = beats.find(x => x.beat === id) || {};
      const state = Object.hasOwn(status, b.verdict) ? b.verdict : 'unknown';
      return `<li class="csw-engine-stage csw-engine-stage--${state}"><details><summary><span class="csw-engine-stage__n">0${i + 1}</span><span><b>${name}</b><small>${purpose}</small></span><em>${status[state]}</em></summary><div class="csw-engine-stage__evidence"><p>${esc(b.why || 'The source did not provide a reading for this stage.')}</p><p>Latest output · ${esc(cswEvidenceTime(b.produced))}</p><p>Connected: ${b.wired === true ? 'yes' : b.wired === false ? 'no' : 'unverified'} · Allowed to run: ${b.armed === true ? 'yes' : b.armed === false ? 'no' : 'unverified'}</p>${cswLoopActivity(d, id)}</div></details></li>`;
    }).join('')}</ol>
    <p class="csw-engine-caution">Activity is not proof of improvement. This view does not establish a lift in customer satisfaction or conversion. Permission to run does not prove a customer received a message.</p>
    <a class="csw-service-link" href="/engine-room?view=learning">Open the complete Chat learning room <span aria-hidden="true">↗</span></a>`;
}

async function cswEngineSheet() {
  if (!cswIsOwner()) return;
  const ov = cswOverlay('<div class="csw-sheet__head"><h3 id="csw-engine-title">How EON is advancing</h3><div class="csw-engine-tools"><button class="csw-service-refresh" type="button">Refresh reading</button><button class="csw-x" type="button">✕</button></div></div><div class="csw-service-sheet"><div id="csw-engine-reading" aria-live="polite">Reading the operating loop…</div></div>', false);
  ov.classList.add('csw-engine-overlay');
  ov.querySelector('.csw-x').focus({ preventScroll: true });
  ov.querySelector('[role="dialog"]').setAttribute('aria-labelledby', 'csw-engine-title');
  ov.querySelector('.csw-x').onclick = () => ov.remove();
  const host = ov.querySelector('#csw-engine-reading'), refresh = ov.querySelector('.csw-service-refresh');
  const load = async () => {
    refresh.disabled = true;
    host.setAttribute('aria-busy', 'true');
    try {
      const d = await api('/api/cs/loop_room');
      if (!ov.isConnected) return;
      host.innerHTML = cswEngineReadout(d, new Date().toISOString());
    } catch (_) {
      if (!ov.isConnected) return;
      host.innerHTML = '<p class="csw-service-empty">The operating loop could not be read. No current status is established. Refresh to try again; this reading requires owner access.</p>';
    } finally {
      refresh.disabled = false;
      host.setAttribute('aria-busy', 'false');
    }
  };
  refresh.onclick = load;
  await load();
}

function cswWindowMins(t) {
  const w = cswReplyWindow(t);
  if (w.state === 'closed') return 0;
  if (w.state === 'unknown') return null;
  return Math.ceil((w.expires - Date.now()) / 60000);
}

function cswBindOrientation() {
  document.querySelectorAll('[data-orientation-lens]').forEach(b => b.addEventListener('click', () => {
    const key = [...document.querySelectorAll('#csw-hlens [data-hl]')].find(k => k.dataset.hl === b.dataset.orientationLens);
    key?.click();
    key?.focus({ preventScroll: true });
  }));
  document.querySelector('[data-orientation-desk]')?.addEventListener('click', () => {
    cswTabGo('desk');
    document.getElementById('csw-desk-back')?.focus({ preventScroll: true });
  });
}

// Bind the filter tabs (was missing → tabs did nothing when clicked).
function cswBindFilters() {
  cswBindOrientation();
  document.querySelectorAll('#csw-filters .csw-filter').forEach(b => {
    b.addEventListener('click', () => {
      cswFromDrop();
      cswState().filter = b.dataset.f;
      document.querySelectorAll('#csw-filters .csw-filter').forEach(x => x.classList.toggle('on', x === b));
      cswRenderThreadList();
    });
  });
  cswRenderActiveFilter();
  // HOUSE: the queue head's one lens key — Team (staff↔staff) in, Queue
  // back out. Exists only when the shell rendered the house head.
  const teamKey = document.getElementById('csw-hq-team');
  teamKey?.addEventListener('click', () => {
    const st = cswState();
    cswFromDrop();
    st.filter = st.filter === 'team' ? 'all' : 'team';
    // Team is its own room — stepping in (or back out) drops any lens
    // so "Queue" on the way out really is the book again.
    st.houseLens = null;
    document.querySelectorAll('#csw-hlens .csw-hlens__k').forEach(x =>
      x.classList.toggle('on', x.dataset.hl === ''));
    teamKey.textContent = st.filter === 'team' ? 'Desk' : 'Team';
    cswRenderThreadList();
    cswTabbarSync();
  });
  // R7: the queue head's EON key — the rep's private line without the
  // rail slot the pinned row used to hold on their desk.
  document.getElementById('csw-hq-eon')?.addEventListener('click', () => cswOpenEon());
  // R2: the lens row. A lens maps onto the classic filter the list
  // renderer already honors; '' returns to the book.
  document.querySelectorAll('#csw-hlens .csw-hlens__k').forEach(k => {
    k.addEventListener('click', () => {
      const st = cswState();
      // Lane P: a hand-picked lens outranks the shift-start landing.
      st.lensPicked = true; st.prioLandNote = '';
      cswFromDrop();
      st.houseLens = k.dataset.hl || null;
      st.filter = k.dataset.hl || 'all';
      document.querySelectorAll('#csw-hlens .csw-hlens__k').forEach(x => {
        x.classList.toggle('on', x === k);
        x.setAttribute('aria-selected', String(x === k));
      });
      const tk = document.getElementById('csw-hq-team');
      if (tk) tk.textContent = 'Team';
      cswRenderThreadList();
      cswTabbarSync();   // the bar mirrors the lens it just left (rig vet)
    });
  });
  const sb = document.getElementById('csw-searchbar');
  cswBindSearchNavigation(sb);
  sb?.addEventListener('input', () => {
    cswState().search = sb.value;
    cswRenderThreadList();
    cswGlobalSearchSoon(sb.value);
  });
}

// ── Global search fall-through (TL-06) ──────────────────────────────
// The local filter only ever saw the ~120 loaded threads / 7 days, so a
// customer from last month or a phrase inside an old message simply could
// not be found. When the local filter comes up short we ask the server
// (PR A's /api/cs/search: customers + message bodies, all time) and append
// the hits under a labelled section.
function cswDeskActions() {
  const actions = [
    { label: 'Automated messages', hint: 'Review outreach', run: () => { cswState().mode = 'automated'; cswRenderBody(); } },
    { label: 'Rep handbook', hint: 'Help and guidance', run: cswOpenGuide },
    { label: 'Till orders with no customer', hint: 'View unattached orders', run: cswTillQueueSheet },
    { label: 'FAQ auto-answers', hint: 'Review answers', run: cswFaqSheet },
  ];
  if (cswIsOwner()) actions.push(
    { label: 'Notifications', hint: 'Owner updates and desk status', run: cswOpenNotifications },
    { label: 'How EON is advancing', hint: 'Engine loop evidence', run: cswEngineSheet });
  if (['owner', 'admin'].includes(State.member?.role)) actions.push(
    { label: 'Staff Line', hint: 'Team conversations', run: () => { cswState().mode = 'staffline'; cswRenderBody(); } },
    { label: 'Team performance', hint: 'Rep outcomes', run: cswPerfSheet },
    { label: 'Reply hold', hint: 'Review hold settings', run: cswHoldSettingSheet });
  if (cswState().active && cswState().active !== CSW_EON_CID && document.getElementById('csw-360btn2')) actions.push(
    { label: 'Current customer details', hint: 'Order and customer file', run: () => { document.getElementById('csw-inbox')?.classList.add('has-active'); document.getElementById('csw-360btn2')?.click(); } },
    { label: 'What needs checking', hint: 'Customer requests, delivery and missing evidence', run: cswOpenDecisionEvidence },
    { label: 'Message receipts', hint: 'Selected conversation delivery evidence', run: () => cswServiceSheet('receipts') },
    { label: 'Service path', hint: 'Selected conversation progress', run: () => cswServiceSheet('progress') });
  return actions;
}

function cswPaintSearchActions(term, focusIndex) {
  focusIndex ??= document.activeElement?.closest?.('#csw-actions [data-desk-action]')?.dataset.deskAction;
  document.getElementById('csw-actions')?.remove();
  if (!term.trim()) return;
  const actions = filterDeskActions(cswDeskActions(), term), host = document.getElementById('csw-threads');
  if (!host || !actions.length) return;
  const section = document.createElement('section'); section.id = 'csw-actions'; section.className = 'csw-seven-search'; section.setAttribute('aria-label', 'Actions');
  section.innerHTML = `<div class="csw-gsec__h">Actions</div>${actions.map((a, i) => `<button type="button" class="csw-seven__choice" data-desk-action="${i}"><span>${esc(a.label)}<small>${esc(a.hint)}</small></span><span aria-hidden="true">↗</span></button>`).join('')}`;
  host.prepend(section);
  if (focusIndex != null) section.querySelectorAll('[data-desk-action]')[Number(focusIndex)]?.focus({ preventScroll: true });
  section.querySelectorAll('[data-desk-action]').forEach(b => b.onclick = () => {
    const a = actions[Number(b.dataset.deskAction)];
    // Recheck the same role predicates at activation, not just paint.
    if (cswDeskActions().some(x => x.label === a.label)) a.run();
  });
}

function cswSearchDesk() {
  if (window.innerWidth <= 879) document.getElementById('csw-inbox')?.classList.remove('has-active');
  document.querySelector('.csw-conv__more')?.removeAttribute('open');
  cswCloseDrawer();
  const input = document.getElementById('csw-searchbar');
  if (document.getElementById('csw-searchwrap')?.hidden) document.getElementById('csw-search-toggle')?.click();
  input?.focus(); input?.select();
}

function cswBindSearchNavigation(input) {
  const toggle = document.getElementById('csw-search-toggle');
  const wrap = document.getElementById('csw-searchwrap');
  const shift = document.getElementById('csw-shift');
  if (toggle && wrap) {
    const hq = document.getElementById('csw-hq');
    if (hq) hq.inert = !wrap.hidden;
    toggle.tabIndex = wrap.hidden ? 0 : -1;
    const setOpen = open => {
      wrap.hidden = !open; toggle.setAttribute('aria-expanded', String(open));
      if (hq) hq.inert = open;
      toggle.tabIndex = open ? -1 : 0;
      if (open) {
        if (shift) shift.open = false;
        window.eonMotion?.stagger?.([wrap], { step: 0, dur: 220, dy: -4 });
        input?.focus({ preventScroll: true });
      } else {
        // Closing also clears the query, so no invisible search filter remains.
        if (input?.value) { input.value = ''; input.dispatchEvent(new Event('input', { bubbles: true })); }
        toggle.focus({ preventScroll: true });
      }
    };
    toggle.addEventListener('click', () => setOpen(true));
    document.getElementById('csw-search-close')?.addEventListener('click', () => setOpen(false));
    wrap.addEventListener('keydown', e => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); }
    });
  }
  if (shift) {
    shift.addEventListener('toggle', () => { if (shift.open) window.eonMotion?.stagger?.([shift.querySelector('.csw-shift__detail')], { step: 0, dur: 220, dy: -4 }); });
    shift.addEventListener('keydown', e => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); shift.open = false; shift.querySelector('summary')?.focus({ preventScroll: true }); } });
    shift.addEventListener('focusout', e => { if (!shift.contains(e.relatedTarget)) shift.open = false; });
  }
  const rows = () => [...document.querySelectorAll('#csw-threads [data-desk-action], #csw-threads [data-open], #csw-threads .csw-thread[data-cid]')].filter(x => x.getClientRects().length && !x.closest('[inert]'));
  input?.addEventListener('keydown', e => {
    if (e.key !== 'ArrowDown' && e.key !== 'Enter') return;
    const first = rows()[0]; if (!first) return;
    e.preventDefault(); first.focus();
    if (e.key === 'Enter') first.click();
  });
  const host = document.getElementById('csw-threads');
  if (host && !host._sevenKeys) {
    host._sevenKeys = true;
    host.addEventListener('keydown', e => {
      if (!['ArrowDown', 'ArrowUp'].includes(e.key)) return;
      const items = rows(), index = items.indexOf(document.activeElement);
      if (index < 0) return;
      e.preventDefault(); e.stopPropagation();
      items[Math.max(0, Math.min(items.length - 1, index + (e.key === 'ArrowDown' ? 1 : -1)))]?.focus();
    });
  }
}


let _cswGsTimer = null, _cswGsSeq = 0;

function cswGlobalSearchSoon(q) {
  clearTimeout(_cswGsTimer);
  const sequence = ++_cswGsSeq;
  document.getElementById('csw-search-status')?.remove();
  const term = (q || '').trim();
  cswPaintSearchActions(term);
  if (term.length < 2) { document.getElementById('csw-gsec')?.remove(); return; }
  _cswGsTimer = setTimeout(() => cswGlobalSearch(term, sequence), 320);
}

async function cswGlobalSearch(term, sequence = _cswGsSeq) {
  const el = document.getElementById('csw-threads');
  if (!el || cswState().search.trim() !== term) return;
  let res;
  try {
    res = await api('/api/cs/search?q=' + encodeURIComponent(term) + '&limit=15');
  } catch (_) {
    if (sequence === _cswGsSeq && cswState().search.trim() === term && el.isConnected) {
      document.getElementById('csw-search-status')?.remove();
      el.insertAdjacentHTML('beforeend', '<p id="csw-search-status" class="csw-hint" role="status">Could not search older conversations. Current-list matches and permitted actions remain available.</p>');
    }
    return;
  }
  if (!res || !res.ok || sequence !== _cswGsSeq || !el.isConnected || cswState().search.trim() !== term) return;
  // Anything the local list already shows is not repeated below.
  const shown = new Set([...el.querySelectorAll('.csw-thread[data-cid]')]
    .map(r => String(r.dataset.cid)));
  const customers = (res.customers || []).filter(c => !shown.has(String(c.thread_key)));
  const messages = (res.messages || []).filter(m => !shown.has(String(m.thread_key)));
  document.getElementById('csw-gsec')?.remove();
  if (!customers.length && !messages.length) return;
  const sec = document.createElement('div');
  sec.id = 'csw-gsec';
  sec.className = 'csw-gsec';
  sec.innerHTML =
    (customers.length ? `<div class="csw-gsec__h">Other chats</div>` + customers.map(c =>
      `<button class="csw-gsrow" data-open="${esc(c.thread_key)}" type="button">
         <span class="csw-gsrow__n">${esc(c.name || 'Customer')}</span>
         <span class="csw-gsrow__m">${esc(formatRelativeShort(c.last_ts) || '')}</span>
       </button>`).join('') : '')
    + (messages.length ? `<div class="csw-gsec__h">Messages</div>` + messages.map(m =>
      `<button class="csw-gsrow" data-open="${esc(m.thread_key)}" type="button">
         <span class="csw-gsrow__n">${esc(m.name || 'Customer')}</span>
         <span class="csw-gsrow__s">${esc(m.snippet || '')}</span>
       </button>`).join('') : '');
  el.appendChild(sec);
  sec.querySelectorAll('[data-open]').forEach(b =>
    b.addEventListener('click', () => cswOpenThread(b.dataset.open)));
}

// ── Department queues (cs_thread_routing, 2026-06-12) ────────────
// "One number, different departments": every inbound thread is routed
// to a queue (CS / Logistics / store / Online). This chip row filters
// the inbox by queue with live OPEN counts from /api/cs/queues.
function cswQueueLabel(t) {
  const q = (t && t.route && t.route.queue) || null;
  // 'marketing' mirrors the server's _QUEUE_LABELS — it is the largest
  // routed department live and printed as a raw lowercase key without it.
  const map = { cs: 'CS', logistics: 'Logistics', sales: 'Sales', online: 'Online',
    marketing: 'Marketing',
    'sales:PALMS': 'Palms', 'sales:CHEVRON': 'Chevron', 'sales:OSAPA': 'Osapa', 'sales:ABUJA': 'Abuja' };
  return q ? (map[q] || q) : null;
}
async function cswLoadQueues() {
  // ONE filter row (research: a single row, overflow/empties hidden). The
  // dept/queue chips render INLINE after the status chips — no second row.
  const host = document.getElementById('csw-filters');
  if (!host) return;
  let d;
  try { d = await api('/api/cs/queues?days=7'); } catch (_) { return; }
  if (!d || !d.ok) return;
  const s = cswState();
  s.queueCounts = d.queues || [];
  // Drop the redundant 'All'; hide zero-count queues so the row stays calm.
  const live = (s.queueCounts || []).filter(q => q.key !== 'all' && (q.count || 0) > 0);
  // Stability under the thumb (design vet M4): only rebuild when the chip
  // SET or a count actually changed — the old remove-and-re-add on every
  // load recomposed the row mid-scroll and chips vanished/reappeared.
  const sig = live.map(q => q.key + ':' + q.count).join('|') + '·' + (s.queueFilter || '');
  if (s._queueSig === sig && host.querySelector('.csw-queue')) return;
  s._queueSig = sig;
  host.querySelectorAll('.csw-queue, .csw-filtsep').forEach(n => n.remove());
  if (!live.length) return;
  let html = '<span class="csw-filtsep" aria-hidden="true"></span>';
  html += live.map(q =>
    `<button class="csw-queue ${s.queueFilter === q.key ? 'on' : ''}" data-q="${esc(q.key)}">${esc(q.label)}<span class="csw-queue__c">${q.count != null ? q.count : '—'}</span></button>`
  ).join('');
  host.insertAdjacentHTML('beforeend', html);
  host.querySelectorAll('.csw-queue').forEach(b => b.addEventListener('click', () => {
    const k = b.dataset.q;
    s.queueFilter = (s.queueFilter === k) ? null : k;   // toggle off on re-tap
    host.querySelectorAll('.csw-queue').forEach(x => x.classList.toggle('on', x.dataset.q === s.queueFilter));
    cswRenderThreadList();
  }));
}

// ── Hamburger drawer (label filters + functions) ─────────────
// Telegram-style left drawer. Replaces the cramped chip row that used to
// sit under the channel tabs — labels + ops live behind the menu.
async function cswOpenDrawer() {
  const s = cswState();
  const drawer = document.getElementById('csw-drawer');
  const scrim = document.getElementById('csw-drawer-scrim');
  if (!drawer) return;
  drawer.innerHTML = `<div class="csw-drawer__h"><div class="csw-drawer__t">Rooms &amp; tools</div><button class="csw-x" id="csw-drawer-x" aria-label="Close menu">✕</button></div>
    <div class="csw-drawer__scroll"><div class="csw-empty" style="padding:24px 0">Loading…</div></div>`;
  drawer.classList.add('open'); drawer.setAttribute('aria-hidden', 'false');
  drawer.inert = false;
  document.getElementById('csw-burger')?.setAttribute('aria-expanded', 'true');
  if (scrim) scrim.hidden = false;
  drawer.querySelector('#csw-drawer-x')?.addEventListener('click', cswCloseDrawer);
  drawer.onkeydown = e => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cswCloseDrawer(); return; }
    if (e.key !== 'Tab') return;
    const keys = Array.from(drawer.querySelectorAll('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
      .filter(el => !el.disabled && el.getClientRects().length && !el.closest('[hidden], [inert]'));
    const first = keys[0], last = keys[keys.length - 1];
    if (first && e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (last && !e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  drawer.querySelector('#csw-drawer-x')?.focus();
  let defs = [];
  try { const d = await api('/api/cs/labels'); defs = (d && d.labels) || []; } catch (_) {}
  // counts per label — scoped to the ACTIVE tab's threads (design vet M6:
  // "VIP 1" counted across all tabs while the visible list ANDed the label
  // with the tab predicate, so the tap landed on "Nothing waiting").
  const counts = {};
  const myEmail = cswMyEmail();
  const inTab = (t) => {
    if (s.filter === 'awaiting') return (!!t._waiting && (t._waitm || 0) <= CSW_RECOVERY_MIN) || (!t._waiting && !!t._promise);
    if (s.filter === 'recovery') return !!t._waiting && (t._waitm || 0) > CSW_RECOVERY_MIN;
    if (s.filter === 'mine') return myEmail && (t.assigned_rep_email || '').toLowerCase() === myEmail && t.cs_status !== 'resolved';
    return true;
  };
  (s.threads || []).filter(inTab).forEach(t => (t.labels || []).forEach(l => { counts[l] = (counts[l] || 0) + 1; }));
  const item = (active, dot, label, count, attrs) =>
    `<button class="csw-drawer__item ${active ? 'on' : ''}" ${attrs}>
       ${dot ? `<span class="csw-drawer__dot" style="background:${dot}"></span>` : '<span class="csw-drawer__dot csw-drawer__dot--all"></span>'}
       <span class="csw-drawer__lbl">${esc(label)}</span>
       ${count != null ? `<span class="csw-drawer__c">${count}</span>` : ''}
     </button>`;
  // Returned automated-history record count (not reply debt), shown on the
  // drawer entry that opens it (it left the top bar 2026-06-18).
  const autoN = s._autoBadgeStatus === 'stale' ? 'Stale' : s._autoBadgeStatus === 'unavailable' ? 'Unavailable' : (s._autoPendingN ?? 0);
  // ⛔ HOUSE FURNITURE ONLY — the same rule cswMountTabbar keeps. The Desk
  // room is dressed entirely under [data-theme="house"] in cs.css
  // (.csw-deskroom, .csw-deskhold and its two keys, .csw-desklaw), so a
  // door to it from a shared drawer opens an undressed room under dark,
  // light and flow: app.css's button reset leaves "place it" and "not
  // theirs" as bare text with no border, no background and nothing 44px
  // to hit — on the control that ledgers the money. Classic keeps its own
  // nav; this entry belongs to the house.
  //
  // The HELD BANK CREDITS line beside it is the opposite case and belongs
  // to every seat (round eleven): its room and its card name only classes
  // cs.css dresses unscoped, and it is the one control in this shell that
  // ends a held bank credit. Gating it would leave a rep on dark, light or
  // flow holding a real transfer with nothing to tap.
  const deskDoor = cswHouse();
  drawer.querySelector('.csw-drawer__scroll').innerHTML = `
    ${cswIsOwner() ? `<div class="csw-drawer__sec">For you</div><button class="csw-drawer__item" id="csw-drawer-notifications"><span class="csw-drawer__dot csw-drawer__dot--all"></span><span class="csw-drawer__lbl">Notifications</span></button>` : ''}
    <div class="csw-drawer__sec">Rooms &amp; work</div>
    ${/* --all, the dot every PLACE in this menu wears (Desk, Staff Line,
         "Till orders with no customer"); --auto is the machine's dot and
         a held payment is the opposite of a machine's job. */''}
    <button class="csw-drawer__item" id="csw-drawer-credits">
      <span class="csw-drawer__dot csw-drawer__dot--all"></span>
      <span class="csw-drawer__lbl">Held bank credits</span>
      <span class="csw-drawer__c" id="csw-drawer-credits-c" hidden></span>
    </button>
    ${deskDoor ? `<button class="csw-drawer__item" id="csw-drawer-desk">
      <span class="csw-drawer__dot csw-drawer__dot--all"></span>
      <span class="csw-drawer__lbl">Desk</span>
    </button>` : ''}
    <button class="csw-drawer__item" id="csw-drawer-automated">
      <span class="csw-drawer__dot csw-drawer__dot--auto"></span>
      <span class="csw-drawer__lbl">Automated messages</span>
      ${autoN ? `<span class="csw-drawer__c" id="csw-drawer-auto-c">${autoN > 99 ? '99+' : autoN}</span>` : '<span class="csw-drawer__c" id="csw-drawer-auto-c" hidden></span>'}
    </button>
    ${['owner', 'admin'].includes(State.member?.role) ? `
    <button class="csw-drawer__item" id="csw-drawer-staffline">
      <span class="csw-drawer__dot csw-drawer__dot--all"></span>
      <span class="csw-drawer__lbl">Staff Line</span>
    </button>
    <button class="csw-drawer__item" id="csw-drawer-perf">
      <span class="csw-drawer__dot csw-drawer__dot--all"></span>
      <span class="csw-drawer__lbl">Team performance</span>
    </button>` : ''}
    <button class="csw-drawer__item" id="csw-drawer-till">
      <span class="csw-drawer__dot csw-drawer__dot--all"></span>
      <span class="csw-drawer__lbl">Till orders with no customer</span>
    </button>
    <button class="csw-drawer__item" id="csw-drawer-faq">
      <span class="csw-drawer__dot csw-drawer__dot--auto"></span>
      <span class="csw-drawer__lbl">FAQ auto-answers</span>
    </button>
    <div class="csw-drawer__sec">Filter by label</div>
    ${item(!s.labelFilter, null, 'All conversations', (s.threads || []).length, 'data-l=""')}
    ${defs.map(d => item(s.labelFilter === d.label, cswLabelColor(d.color), d.label, counts[d.label] || 0, `data-l="${esc(d.label)}"`)).join('')}
    ${cswIsOwner() ? `<button class="csw-drawer__item" id="csw-drawer-engine"><span class="csw-drawer__dot csw-drawer__dot--auto"></span><span class="csw-drawer__lbl">How EON is advancing</span></button>` : ''}
    <div class="csw-drawer__sec">Manage</div>
    ${['owner', 'admin'].includes(State.member?.role) ? `
    <button class="csw-drawer__item" id="csw-drawer-hold">
      <span class="csw-drawer__dot csw-drawer__dot--auto"></span>
      <span class="csw-drawer__lbl">Reply hold</span>
    </button>` : ''}
    <button class="csw-drawer__item" id="csw-drawer-newlabel"><span class="csw-drawer__dot csw-drawer__dot--add">+</span><span class="csw-drawer__lbl">New label…</span></button>
    <div class="csw-now csw-now--drawer" id="csw-now" role="group" aria-label="Recent conversations" hidden></div>
    <div class="csw-drawer__sec">Help</div>
    <button type="button" class="csw-drawer__item" id="csw-drawer-photo-practice"><span class="csw-drawer__dot csw-drawer__dot--all"></span><span class="csw-drawer__lbl">Practice photo hints</span></button>
    <button class="csw-drawer__item" id="csw-drawer-guide"><span class="csw-drawer__dot csw-drawer__dot--all"></span><span class="csw-drawer__lbl">Rep handbook</span></button>`;
  cswPaintNowShelf();
  cswCreditsDoorBadge(drawer);
  drawer.querySelector('#csw-drawer-credits')?.addEventListener('click', () => {
    cswCloseDrawer();
    cswState().mode = 'credits';
    cswRenderBody();
    document.getElementById('csw-credit-back')?.focus();
  });
  drawer.querySelector('#csw-drawer-notifications')?.addEventListener('click', () => { cswCloseDrawer(); cswOpenNotifications(); });
  if (deskDoor) drawer.querySelector('#csw-drawer-desk')?.addEventListener('click', () => {
    cswCloseDrawer(); cswTabGo('desk'); document.getElementById('csw-desk-back')?.focus();
  });
  drawer.querySelector('#csw-drawer-automated')?.addEventListener('click', () => {
    cswCloseDrawer();
    s.mode = 'automated';
    cswRenderBody();
  });
  drawer.querySelectorAll('.csw-drawer__item[data-l]').forEach(b =>
    b.addEventListener('click', () => cswSetLabelFilter(b.dataset.l || null)));
  drawer.querySelector('#csw-drawer-newlabel')?.addEventListener('click', () => {
    cswCloseDrawer();
    if (s.active) cswLabelSheet();
    else cswToast('Open a conversation to create + apply a label.', true);
  });
  const photoPractice = drawer.querySelector('#csw-drawer-photo-practice');
  photoPractice?.addEventListener('click', async () => {
    if (photoPractice.disabled) return;
    photoPractice.disabled = true;
    try {
      const {openPhotoPractice} = await import('./cs_photo_practice.js');
      if (!photoPractice.isConnected || drawer.inert || !drawer.classList.contains('open')) return;
      cswCloseDrawer();
      const dismissed = s._dismissedPracticePhotos ||= new Set();
      openPhotoPractice({overlay: cswOverlay, album: cswAlbumGridHtml, openAlbum: cswAlbumOpen,
                        lightbox: cswLightbox, escapeHtml: esc, dismissed});
    } catch (_) {
      cswToast('Photo practice could not open. Try again.', true);
    } finally {
      photoPractice.disabled = false;
    }
  });
  drawer.querySelector('#csw-drawer-guide')?.addEventListener('click', () => {
    cswCloseDrawer();
    cswOpenGuide();
  });
  drawer.querySelector('#csw-drawer-staffline')?.addEventListener('click', () => {
    cswCloseDrawer();
    s.mode = 'staffline';
    cswRenderBody();
  });
  drawer.querySelector('#csw-drawer-perf')?.addEventListener('click', () => {
    cswCloseDrawer();
    cswPerfSheet();
  });
  drawer.querySelector('#csw-drawer-till')?.addEventListener('click', () => {
    cswCloseDrawer();
    cswTillQueueSheet();
  });
  drawer.querySelector('#csw-drawer-faq')?.addEventListener('click', () => {
    cswCloseDrawer();
    cswFaqSheet();
  });
  drawer.querySelector('#csw-drawer-engine')?.addEventListener('click', () => {
    cswCloseDrawer();
    document.getElementById('csw-burger')?.focus({ preventScroll: true });
    cswEngineSheet();
  });
  drawer.querySelector('#csw-drawer-hold')?.addEventListener('click', () => {
    cswCloseDrawer();
    cswHoldSettingSheet();
  });
}

// ── Unattached till orders (capture credit, 2026-07-31) ─────────────
// Chat-closed orders are rung at the Online POS till, and POS forces no
// customer on them — so the data the rep collected in chat never reaches
// the sale. This sheet lists those orders (people attach queue, Online
// till) and, with a chat open, links one to that chat in a single tap via
// the pos_attach bind-thread lane — the durable capture row that the
// data-collection scoreboards read.
async function cswTillQueueSheet() {
  const s = cswState();
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Till orders with no customer</div><button class="csw-x" id="csw-till-x">✕</button></div>
    <div class="csw-hint" style="margin-bottom:8px">Orders rung at the Online till in the last 7 days with nobody on them. Open the chat that paid for one, then tap Link — the customer's phone lands on the sale.</div>
    <div id="csw-till-list"><div class="csw-empty" style="padding:18px">Loading…</div></div>`);
  ov.querySelector('#csw-till-x')?.addEventListener('click', () => ov.remove());
  const list = ov.querySelector('#csw-till-list');
  // The phone this desk already holds for the OPEN chat: saved contact
  // first, else the WhatsApp sender id (IG sender ids are not phones).
  let phone = null;
  const cid = s.active;
  if (cid) {
    try { const g = await api(`/api/cs/threads/${encodeURIComponent(cid)}/contact`); phone = ((g && g.contact) || {}).phone || null; } catch (_) {}
    // '+' required: a bare 15-digit IG scoped id passes any digits-only
    // length check and would bind a fake phone (backfill dry-run finding).
    const sid = String((s.activeThread || {}).platform_sender_id || '');
    if (!phone && /^\+\d{10,14}$/.test(sid)) phone = sid;
  }
  let q = null;
  try { q = await api('/api/people/attach/queue?hours=168&till=online', { timeoutMs: 20000 }); } catch (_) {}
  if (!document.contains(list)) return;
  const rows = (q && q.orders) || [];
  if (!q || q.ok === false) { list.innerHTML = `<div class="csw-empty" style="padding:18px">Could not load the till queue.</div>`; return; }
  if (!rows.length) { list.innerHTML = `<div class="csw-empty" style="padding:18px">Nothing waiting — every till order has a customer.</div>`; return; }
  const when = (iso) => { try { const d = new Date(iso); return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); } catch (_) { return ''; } };
  list.innerHTML = rows.map(o => `
    <div class="csw-oline" data-oid="${esc(o.order_shopify_id)}">
      <span style="flex:1">
        <b>${esc(o.order_name || '')}</b> · ${cswNgn(o.total_price)}
        <div class="csw-hint">${when(o.shopify_created_at)} · rung by ${esc(o.rep || '?')} · packs from ${esc(o.store || '?')}</div>
      </span>
      ${cid && phone ? `<button class="csw-mini csw-mini--p" data-bind="${esc(o.order_shopify_id)}">Link to this chat</button>` : ''}
    </div>`).join('') + (cid && phone ? '' :
      `<div class="csw-hint" style="margin-top:8px">${cid ? 'This chat has no phone saved yet — save it with Capture / link first, then come back.' : 'Open the chat that paid for an order to link it.'}</div>`);
  list.querySelectorAll('[data-bind]').forEach(b => b.addEventListener('click', async () => {
    b.disabled = true; b.textContent = 'Linking…';
    try {
      const r = await api('/api/people/attach/bind-thread', {
        method: 'POST',
        body: JSON.stringify({ order_shopify_id: b.dataset.bind, phone, zoko_customer_id: cid }),
      });
      if (r && r.ok) { b.textContent = r.already_captured ? 'Already linked' : 'Linked'; cswToast('Order linked — the customer\'s phone is now on the sale.'); }
      else { b.disabled = false; b.textContent = 'Link to this chat'; cswToast(cswSayWhy({ message: String((r && r.error) || '') }, { lead: 'Not linked.' }), true); }
    } catch (e) { b.disabled = false; b.textContent = 'Link to this chat'; cswToast('Could not link', true); }
  }));
}

// Two independent reads: composite /rep-performance and /conversion.
// Five explicit sections retain their own loading, failure and retry states.
// Exact-ID names arriving from either read also settle existing name nodes.
async function cswPerfSheet() {
  const sections = {activity:'Team activity', funnel:'Orders after a customer message', csat:'Customer ratings',
    reply_sla_yesterday:'Yesterday’s reply times', conversion:'Orders after a reply'};
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Team performance</div><button class="csw-x" id="csw-perf-x" aria-label="Close team performance">✕</button></div>
    <div class="csw-performance" id="csw-perf-body"><p>Private team overview · last 30 days, with yesterday shown separately.</p>
    ${Object.entries(sections).map(([key,title]) => `<section aria-labelledby="csw-perf-${key}"><h3 id="csw-perf-${key}">${title}</h3><div data-perf="${key}" aria-live="polite" aria-busy="true"></div></section>`).join('')}</div>`);
  ov.querySelector('#csw-perf-x').addEventListener('click', () => ov.remove());
  // Same gate as the menu; the owner/admin API remains the authority.
  if (!['owner','admin'].includes(State.member?.role)) {
    ov.querySelector('#csw-perf-body').innerHTML = '<p role="status">Restricted — this overview is for the owner and admins.</p>';
    return;
  }
  const finite = v => typeof v === 'number' && Number.isFinite(v);
  const num = (v, unit='') => finite(v) ? `${v.toLocaleString('en-GB', {maximumFractionDigits:1})}${unit}` : 'Unavailable';
  const date = (v, time=false) => {
    if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}(T|$)/.test(v) || !Number.isFinite(Date.parse(v))) return 'Date unavailable';
    return new Date(v).toLocaleString('en-GB', {timeZone:'Africa/Lagos',day:'numeric',month:'short',year:'numeric',
      ...(time ? {hour:'2-digit',minute:'2-digit'} : {})}) + (time ? ' WAT' : '');
  };
  const note = text => `<p>${esc(text)}</p>`;
  const metric = (label,value) => `<div><dt>${esc(label)}</dt><dd>${esc(value)}</dd></div>`;
  const facts = rows => `<dl class="csw-performance__facts">${rows.map(([k,v]) => metric(k,v)).join('')}</dl>`;
  const details = (label,html) => `<details><summary>${esc(label)}</summary>${html}</details>`;
  const windowNote = days => note(finite(days) ? `Last ${num(days)} days.` : 'Reporting window unavailable.');
  const coverage = c => note(c && finite(c.credited) && finite(c.eligible)
    ? `Payment coverage, team-wide: ${num(c.credited)} of ${num(c.eligible)} eligible chat payments counted · ${num(c.days)} days.`
    : 'Payment coverage unavailable.');
  const names = new Map();
  const remember = rows => (Array.isArray(rows) ? rows : []).forEach(r => {
    if (r && typeof r.rep_id === 'string' && typeof r.rep_name === 'string' && r.rep_name.trim()) names.set(r.rep_id, r.rep_name);
  });
  const name = r => typeof r.rep_name === 'string' && r.rep_name.trim() ? r.rep_name
    : names.get(r.rep_id || r.rep) || (r.rep === '(no reply)' ? 'No reply recorded' : r.rep === '(automated)' ? 'Automated replies'
      : r.rep === 'eon-autopilot@crux.ng' ? 'EON' : 'Name unavailable');
  const nameRows = new Map();
  const refreshNames = () => {
    if (!ov.isConnected) return;
    nameRows.forEach((list,key) => ov.querySelector(`[data-perf="${key}"]`).querySelectorAll('[data-perf-name]').forEach(el => {
      const label = name(list[Number(el.dataset.perfName)]);
      if (el.textContent !== label) el.textContent = label;
    }));
  };
  const rows = (key,list,render,empty='No rows in this reading.') => {
    if (!Array.isArray(list)) return note('Details unavailable.');
    return list.length ? [...list].filter(r => r && typeof r === 'object')
      .sort((a,b) => name(a).localeCompare(name(b))).map(r => {
        // Opaque local indices keep private identity keys out of the markup.
        const index = nameRows.get(key).push(r) - 1;
        return `<div class="csw-performance__person"><h4 data-perf-name="${index}">${esc(name(r))}</h4>${render(r)}</div>`;
      }).join('') : note(empty);
  };
  const fraction = (a,b) => finite(a) && finite(b) ? `${num(a)} of ${num(b)}` : 'Unavailable';
  const render = (key,d) => {
    if (key === 'activity') {
      const t = d.team_totals || {}, total = finite(t.answered_threads) && finite(t.unanswered_threads) ? t.answered_threads + t.unanswered_threads : null;
      return windowNote(d.window_days) + note(`${date(d.window_from,true)} → ${date(d.window_to,true)}`)
        + facts([['Messages sent',num(t.messages_sent)],['Customers reached',num(t.customers_reached)],['Reps with recorded messages',num(t.reps)],
          ['Incoming messages',num(t.inbound_total)],['Answered threads',fraction(t.answered_threads,total)],['Unanswered threads',num(t.unanswered_threads)],
          ['Thread response rate',num(t.response_ratio_pct,'%')],['Median first response',num(t.median_first_response_seconds,' s')],
          ['95th percentile response',num(t.p95_first_response_seconds,' s')]])
        + note('Response times use answered threads only and are capped at 7 days; the 95th percentile needs at least 20 response observations. Team totals can include automated messages.')
        + details('Activity by name', rows(key,d.reps, r => facts([['Messages sent',num(r.messages_sent)],['Customers reached',num(r.customers_reached)],
          ['Latest message',date(r.most_recent_at,true)]]), 'No rep messages recorded in this window.'));
    }
    if (key === 'funnel') {
      const t = d.totals || {}, legs = d.stitched_by_leg || {};
      return windowNote(d.days) + note(`${num(d.order_window_days)} days after the first customer message. Phone or linked email matches include self-serve orders; this is not proof that a reply caused a sale. Each matched order counts once.`)
        + (t.threads === 0 ? note('No WhatsApp threads in this window.') : '')
        + facts([['WhatsApp threads',num(t.threads)],['Matched orders',num(t.closed)],
          ['Matched order rate',`${num(finite(t.close_rate) ? t.close_rate * 100 : null,'%')} · ${fraction(t.closed,t.threads)} threads`],
          ['Matched order value',num(t.revenue_ngn,' NGN')],['Average matched order',num(t.aov_ngn,' NGN')]]) + coverage(d.coverage)
        + details('Additional order links and their evidence', facts([['Additional linked orders',num(t.closes_stitched)],['Additional linked value',num(t.revenue_stitched_ngn,' NGN')],
          ['No rep attribution',num(t.closes_stitched_unattributed)],['Paid message naming the order',num(legs.thread_confirmed?.n)],
          ['Desk link — lower confidence',num(legs.rep_window?.n)],['Rep marked Bought — rep’s own confirmation',num(legs.rep_confirmed?.n)]])
          + note('These additional links are not included in the matched rate. Missing link counts are unavailable, not zero. This report does not state whether the additional-link read was complete.'))
        + details('Order associations by name', rows(key,d.reps, r => facts([['Threads',num(r.threads)],['Matched orders',num(r.closed)],
          ['Matched order value',num(r.revenue_ngn,' NGN')],['Additional linked orders',num(r.closes_stitched)],['Additional linked value',num(r.revenue_stitched_ngn,' NGN')],
          ['Median time to matched order',num(r.median_hours_to_close,' h')],['Median first reply',num(r.median_first_reply_hours,' h')]])));
    }
    if (key === 'csat') {
      const rating = r => `${r.n === 0 || !finite(r.csat) ? 'Ungraded' : num(r.csat,' / 5')} · ${num(r.n)} reviews`;
      return windowNote(d.days) + facts([['Customer rating',rating({csat:d.overall_csat,n:d.n})],['Negative reviews still open',num(d.negative_open)]])
        + (d.n === 0 ? note('No reviews in this window. Ungraded does not mean poor service.') : '')
        + details('Ratings by store and name', (Array.isArray(d.by_store) ? [...d.by_store].sort((a,b) => String(a.store).localeCompare(String(b.store)))
          .map(r => facts([[typeof r.store === 'string' ? r.store : 'Store unavailable',rating(r)]])).join('') : note('Store details unavailable.'))
          + rows(key,d.by_rep, r => note(rating(r)), 'No rep reviews in this window.'));
    }
    if (key === 'reply_sla_yesterday') {
      return note(`Yesterday · ${date(d.day)} · Lagos day. Replies may arrive after that day.`)
        + (d.threads === 0 ? note('No inbound WhatsApp threads that day.') : '')
        + facts([['Inbound WhatsApp threads',num(d.threads)],['Answered threads',fraction(d.answered,d.threads)],
          ['Answered within one hour',num(d.within_1h_pct,'%')],['Median first reply',num(d.median_min,' min')],['90th percentile reply',num(d.p90_min,' min')],
          ['Replies after six hours',num(d.over_6h)],['Never answered',num(d.never_answered)],['Overnight threads',num(d.overnight_threads)],['Overnight unanswered',num(d.overnight_unanswered)]])
        + note('The one-hour rate uses all inbound threads that day. Reply times use answered threads only.');
    }
    const t = d.team || {};
    const association = r => facts([['Eligible replied threads',num(r.threads)],['Matched replied threads',num(r.converted)],['Average matched order',num(r.aov_ngn,' NGN')]]);
    return windowNote(d.period_days) + note(`${num(d.days_window)} days after the first reply. WhatsApp phone matching; a thread is counted when a qualifying order follows. This differs from the first-message measure above.`)
      + (t.threads === 0 ? note('No eligible replied threads in this window.') : '') + association(t)
      + facts([['Matched replied-thread rate',`${num(t.conv_rate_pct,'%')} · ${fraction(t.converted,t.threads)} eligible threads`],['Instagram / unmatchable threads excluded',num(d.ig_unmatchable)]])
      + coverage(d.coverage) + note('Team totals include automated responders. An association does not prove a reply caused the sale.')
      + details('Reply associations by name',rows(key,d.reps,association) + '<h4>Automated responders</h4>' + rows(key,d.machines,association,'No automated responders in this reading.'));
  };
  const compositeKeys = ['activity','funnel','csat','reply_sla_yesterday'];
  const loading = key => {
    nameRows.delete(key);
    const el = ov.querySelector(`[data-perf="${key}"]`);
    el.setAttribute('aria-busy','true'); el.innerHTML = '<p role="status">Loading this reading…</p>';
  };
  const show = (key,d) => {
    if (!ov.isConnected) return;
    const el = ov.querySelector(`[data-perf="${key}"]`);
    el.setAttribute('aria-busy','false');
    const restricted = d?.status === 403 || d?.status === 401;
    const valid = d && d.ok === true && !restricted;
    nameRows.set(key,[]);
    el.innerHTML = valid ? render(key,d) + note(`Read ${date(new Date().toISOString(),true)}.`)
      : note(restricted ? 'Restricted — your account cannot read this section.' : d?.ok === false ? 'Could not load this section. Try again.' : 'Unavailable — this reading was not returned. Try again.');
    if (!valid) {
      el.innerHTML += `<button class="csw-mini" type="button" data-perf-retry aria-label="Retry ${sections[key]}">Try again</button>`;
      el.querySelector('[data-perf-retry]').addEventListener('click', () => load(key === 'conversion' ? 'conversion' : 'composite',key));
    }
  };
  const busy = new Set();
  const load = async (source,only) => {
    if (!ov.isConnected || busy.has(source)) return;
    busy.add(source);
    const keys = only ? [only] : source === 'conversion' ? ['conversion'] : compositeKeys;
    const returnFocus = only && document.activeElement === ov.querySelector(`[data-perf="${only}"]`).querySelector('[data-perf-retry]');
    keys.forEach(loading);
    try {
      const d = await api(source === 'conversion' ? '/api/cs/conversion?period_days=30&days_window=14' : '/api/cs/rep-performance?days=30');
      if (!ov.isConnected) return;
      if (source === 'composite') remember(d?.activity?.ok === true ? d.activity.reps : []);
      else remember(d?.ok === true ? d.reps : []);
      // Text only: do not reorder rows or replace disclosures/focused controls.
      refreshNames();
      keys.forEach(key => show(key,source === 'conversion' || d?.ok !== true ? d : d[key]));
    } catch (err) {
      keys.forEach(key => show(key,{ok:false,status:err?.status}));
    } finally {
      busy.delete(source);
      if (returnFocus && ov.isConnected) {
        const el = ov.querySelector(`[data-perf="${only}"]`);
        el.setAttribute('tabindex','-1'); el.focus();
      }
    }
  };
  await Promise.all([load('composite'),load('conversion')]);
}

// ── EON Staff Line (owner-only channel — PLAN-staff-line.md) ────────
// Staff WhatsApp the same line customers use; armed (STAFF_LINE) their
// traffic diverts into this channel instead of the CS desk. This view is
// the owner's window: per-staff threads, category chips, and the
// build-request queue with approve / decline-with-reason.
async function cswStaffLineLoad() {
  const box = document.getElementById('csw-sl-threads');
  const reqBox = document.getElementById('csw-sl-requests');
  if (!box) return;
  let data = null, reqs = null;
  try {
    [data, reqs] = await Promise.all([
      api('/api/staff-line/threads'),
      api('/api/staff-line/requests?status=queued'),
    ]);
  } catch (err) {
    box.innerHTML = '<div class="csw-empty"><div class="csw-empty__m">⊘</div>Staff Line unavailable (owner-only).</div>';
    return;
  }
  if (reqBox) {
    const rs = (reqs && reqs.requests) || [];
    reqBox.innerHTML = rs.length ? `
      <div class="csw-drawer__sec">Build requests awaiting you</div>
      ${rs.map(r => `
        <div class="csw-note" style="margin:6px 0" data-req="${esc(r.id)}">
          <div>${esc((r.request_text || '').slice(0, 160))}${(r.request_text || '').length > 160 ? '…' : ''}</div>
          <div style="margin-top:6px;display:flex;gap:6px">
            <button class="csw-mini" data-sl-approve="${esc(r.id)}">Approve</button>
            <button class="csw-mini" data-sl-decline="${esc(r.id)}">Decline…</button>
          </div>
          <div class="csw-hint" data-sl-msg="${esc(r.id)}"></div>
        </div>`).join('')}` : '';
    reqBox.querySelectorAll('[data-sl-approve]').forEach(b =>
      b.addEventListener('click', () => cswStaffLineDecide(b.dataset.slApprove, 'approved', '')));
    reqBox.querySelectorAll('[data-sl-decline]').forEach(b =>
      b.addEventListener('click', () => {
        // decline needs a reason — inline input (confirm/prompt are killed).
        const wrap = reqBox.querySelector(`[data-req="${b.dataset.slDecline}"]`);
        if (!wrap || wrap.querySelector('.csw-sl-reason')) return;
        const row = document.createElement('div');
        row.className = 'csw-sl-reason';
        row.style.cssText = 'margin-top:6px;display:flex;gap:6px';
        row.innerHTML = `<input class="csw-input" placeholder="Reason (required)" style="flex:1"/><button class="csw-mini">Send</button>`;
        row.querySelector('button').addEventListener('click', () => {
          const val = (row.querySelector('input').value || '').trim();
          if (val) cswStaffLineDecide(b.dataset.slDecline, 'declined', val);
        });
        wrap.appendChild(row);
        row.querySelector('input').focus();
      }));
  }
  const threads = (data && data.threads) || [];
  box.innerHTML = threads.length ? threads.map(t => `
    <button class="csw-drawer__item" data-sl-thread="${esc(t.staff_id)}" style="width:100%;text-align:left">
      <span class="csw-drawer__lbl">${esc(t.name)}${t.pending_requests ? ` · ${t.pending_requests} pending` : ''}</span>
      <span class="csw-hint" style="margin-left:8px">${esc((t.last_text || '').slice(0, 48))}</span>
    </button>`).join('')
    : '<div class="csw-empty"><div class="csw-empty__m">○</div>No staff messages yet. Arm STAFF_LINE on the switchboard, then staff just WhatsApp the CRUX number.</div>';
  box.querySelectorAll('[data-sl-thread]').forEach(b =>
    b.addEventListener('click', () => cswStaffLineOpen(b.dataset.slThread)));
}

async function cswStaffLineDecide(reqId, status, reason) {
  try {
    await api(`/api/staff-line/requests/${encodeURIComponent(reqId)}/decide`, {
      method: 'POST', body: JSON.stringify({ status, reason }),
    });
    cswToast(status === 'approved' ? 'Approved — the staffer has been told.' : 'Declined — reason sent.');
    cswStaffLineLoad();
  } catch (err) { cswToast('Decision failed — try again.', true); }
}

async function cswStaffLineOpen(staffId) {
  const pane = document.getElementById('csw-sl-thread');
  const list = document.getElementById('csw-sl-threads');
  if (!pane) return;
  pane.hidden = false;
  if (list) list.hidden = true;
  pane.innerHTML = '<div class="csw-empty"><div class="csw-empty__m">○</div>Loading…</div>';
  let data = null;
  try { data = await api(`/api/staff-line/threads/${encodeURIComponent(staffId)}/messages`); }
  catch (err) { pane.innerHTML = '<div class="csw-empty">Failed to load.</div>'; return; }
  const msgs = (data && data.messages) || [];
  const who = (data && data.staff && data.staff.full_name) || 'Staff';
  pane.innerHTML = `
    <div class="csw-autohead"><button class="csw-autohead__back" id="csw-sl-tback" aria-label="Back">‹</button><span class="csw-autohead__t">${esc(who)}</span><button class="csw-mini" id="csw-sl-pin" style="margin-left:auto">Issue PIN</button></div>
    <div style="padding:8px 12px">
      ${msgs.map(m => {
        // Staff photos (2026-07-24, Gift): cloud media renders via the
        // owner-gated /api/staff-line/media exchange; zoko-era rows keep
        // their direct file_url. cswImgSrc appends the JWT for <img> auth.
        const mb = m.body || {};
        const mediaSrc = mb.media_id && (mb.media_type === 'image' || mb.media_type === 'sticker')
          ? cswImgSrc(`/api/staff-line/media?mid=${encodeURIComponent(m.id)}`)
          : (mb.file_url ? cswImgSrc(mb.file_url) : '');
        return `
        <div class="csw-row ${m.direction === 'inbound' ? 'in' : 'out'}">
          <div class="csw-bub ${m.direction === 'inbound' ? 'in' : 'out'}">
            ${m.category ? `<div class="csw-bub__src">${esc(m.category.replace(/_/g, ' '))}</div>` : ''}
            ${mediaSrc ? `<img class="csw-bub__img" src="${esc(mediaSrc)}" data-full="${esc(mediaSrc)}" data-cap="${esc(mb.text || '')}" alt="photo" loading="lazy"/>` : ''}
            <div class="csw-bub__txt">${esc(mb.text || '')}</div>
            <div class="csw-bub__t">${esc(cswMsgTime(m.created_at))}</div>
          </div>
        </div>`;
      }).join('') || '<div class="csw-empty">No messages.</div>'}
    </div>
    <div style="display:flex;gap:6px;padding:8px 12px">
      <input class="csw-input" id="csw-sl-input" placeholder="Reply as EON (@Name notifies them on WhatsApp)" style="flex:1"/>
      <button class="csw-mini" id="csw-sl-send">Send</button>
    </div>`;
  pane.querySelector('#csw-sl-tback')?.addEventListener('click', () => {
    pane.hidden = true;
    if (list) list.hidden = false;
    cswStaffLineLoad();
  });
  // Staff photos open in the same lightbox as customer-chat images.
  pane.querySelectorAll('.csw-bub__img').forEach(im =>
    im.addEventListener('click', () => cswLightbox(im.dataset.full, im.dataset.cap)));
  // One tap, no confirm — the endpoint never resets an existing PIN,
  // so the worst case is an honest "already has one" toast.
  pane.querySelector('#csw-sl-pin')?.addEventListener('click', async (e) => {
    e.target.disabled = true;
    try {
      const r = await api(`/api/staff-line/threads/${encodeURIComponent(staffId)}/issue-pin`, { method: 'POST', body: '{}' });
      cswToast(r.ok ? 'PIN sent on WhatsApp' : 'Could not issue PIN', !r.ok);
      if (r.ok) { cswStaffLineOpen(staffId); return; }
    } catch (err) {
      const m = String((err && err.message) || '');
      cswToast(/already_enrolled/.test(m) ? 'They already have a PIN — they can text RESET PIN'
        : /no_phone/.test(m) ? 'No phone on their staff row — add it in People OS first'
        : 'Could not issue PIN', true);
    }
    e.target.disabled = false;
  });
  pane.querySelector('#csw-sl-send')?.addEventListener('click', async (e) => {
    const inp = pane.querySelector('#csw-sl-input');
    const text = (inp && inp.value || '').trim();
    if (!text) return;
    e.target.disabled = true;
    try {
      const r = await api(`/api/staff-line/threads/${encodeURIComponent(staffId)}/send`, {
        method: 'POST', body: JSON.stringify({ text }),
      });
      cswToast(r.ok ? (r.mentioned && r.mentioned.length ? `Sent · ${r.mentioned.length} mentioned` : 'Sent') : 'Send failed', !r.ok);
      cswStaffLineOpen(staffId);
    } catch (err) { cswToast('Send failed.', true); e.target.disabled = false; }
  });
}
function cswCloseDrawer() {
  const drawer = document.getElementById('csw-drawer');
  const burger = document.getElementById('csw-burger');
  if (drawer?.classList.contains('open')) burger?.focus({ preventScroll: true });
  if (drawer) { drawer.inert = true; drawer.classList.remove('open'); drawer.setAttribute('aria-hidden', 'true'); }
  burger?.setAttribute('aria-expanded', 'false');
  const scrim = document.getElementById('csw-drawer-scrim'); if (scrim) scrim.hidden = true;
}

// ── CS Desk rep handbook (burger drawer → Help → Rep handbook) ─────
// Same estate as the other four handbooks (#1151): .mkg markup, GuideKit
// live-search, toc jumps. Full-screen overlay so it works from anywhere
// in the desk without losing inbox state. Illustrations are hand-drawn
// inline SVG mockups with invented names (2026-08-02, fr 791a52b2) —
// never chat screenshots, which would carry live customer data. The
// cg-new "New features" section is a hand-curated dated list (the
// /api/whats-new feed is ACCOUNTING_VIEW-gated, unreachable for rep
// PINs) — append to it when a desk feature ships.
function cswOpenGuide() {
  if (document.getElementById('csw-guide')) return;
  const ov = document.createElement('div');
  ov.id = 'csw-guide';
  ov.style.cssText = 'position:fixed;inset:0;z-index:220;background:var(--bg,#0b0d0c);overflow-y:auto;-webkit-overflow-scrolling:touch';
  ov.innerHTML = `
    <button id="csw-guide-x" aria-label="Close handbook" style="position:fixed;top:14px;right:16px;z-index:2;width:36px;height:36px;border-radius:50%;border:1px solid rgba(255,255,255,.18);background:rgba(0,0,0,.45);color:#fff;font-size:16px;cursor:pointer">✕</button>
    ${_cswGuideHtml()}`;
  document.body.appendChild(ov);
  const close = () => { ov.remove(); document.removeEventListener('keydown', onEsc); };
  const onEsc = (e) => { if (e.key === 'Escape') close(); };
  ov.querySelector('#csw-guide-x')?.addEventListener('click', close);
  document.addEventListener('keydown', onEsc);
  const beh = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  ov.querySelectorAll('.mkg-toc a[data-jump]').forEach(a => {
    a.addEventListener('click', () => {
      const el = ov.querySelector('#' + a.dataset.jump);
      if (el) el.scrollIntoView({ behavior: beh, block: 'start' });
    });
  });
  const root = ov.querySelector('.mkg');
  if (window.GuideKit && root) window.GuideKit.boot(root);
}

function _cswGuideHtml() {
  return `
  <div class="mkg mkg--shell">
    <div class="mkg-hero">
      <div class="mkg-kicker">CS Desk · Rep Handbook · updated 2 August 2026</div>
      <h1>How to run the customer desk</h1>
      <p>Every WhatsApp and Instagram message to CRUX lands in this one desk. This page explains
      every pane, every button and every rule in plain English. Use the search box to jump to a
      word — the guide is the index.</p>
    </div>

    <nav class="mkg-toc">
      <a data-jump="cg-big">The big idea</a>
      <a data-jump="cg-screens">The three panes</a>
      <a data-jump="cg-tabs">The tabs</a>
      <a data-jump="cg-inbox">The inbox</a>
      <a data-jump="cg-answer">Answering</a>
      <a data-jump="cg-tools">The tray tools</a>
      <a data-jump="cg-auto">Auto-answered threads</a>
      <a data-jump="cg-templates">Templates</a>
      <a data-jump="cg-media">What customers send</a>
      <a data-jump="cg-orders">The customer panel</a>
      <a data-jump="cg-eon">EON at your side</a>
      <a data-jump="cg-flows">Closing a sale</a>
      <a data-jump="cg-sla">Waiting times &amp; routing</a>
      <a data-jump="cg-platform">WhatsApp vs Instagram</a>
      <a data-jump="cg-staffline">The staff line</a>
      <a data-jump="cg-behind">EON behind the scenes</a>
      <a data-jump="cg-conduct">Privacy &amp; tone</a>
      <a data-jump="cg-new">New features</a>
      <a data-jump="cg-cheat">Cheat sheet</a>
    </nav>

    <section class="mkg-sec" id="cg-big">
      <h2>The one big idea (read this first)</h2>
      <div class="mkg-tldr"><b>Every chat is a sale in progress.</b> Most CRUX online orders
      close inside this desk — a customer DMs, you answer fast and honestly, you place the order
      for them. Speed and truth are the whole job: reply quickly, never guess stock or prices,
      and when you don't know — check, don't improvise.</div>
      <p><b>The desk keeps receipts.</b> Who replied, how fast, what EON sent versus what you
      sent — it's all recorded. That protects you: good work is visible without anyone standing
      over your shoulder.</p>
    </section>

    <section class="mkg-sec" id="cg-screens">
      <h2>The three panes</h2>
      <p><b>1 · Inbox list</b> — every open conversation, newest activity first. Unread counts
      are personal: a thread is "unread" only if <i>you</i> haven't seen it.</p>
      <p><b>2 · Conversation</b> — the chat itself. Bubbles group like WhatsApp, day dividers
      split the history, and the composer sits at the bottom.</p>
      <p><b>3 · Customer panel</b> — tap a customer's name to open their full picture: how much
      they've spent, every past order, their size hints, saved notes and labels.</p>
    </section>

    <section class="mkg-sec" id="cg-tabs">
      <h2>The tabs — which list to work</h2>
      <p class="mkg-lead">Six lists, one rule: work them left to right.</p>
      <svg class="mkg-svg" viewBox="0 0 760 292" role="img" aria-label="Annotated mockup of the thread list: tab bar on top, three conversation rows with wait timers below">
        <style>
          .cgtN { fill: var(--bg-card, #16161a); stroke: var(--line, #26262d); }
          .cgtP { fill: none; stroke: var(--line, #26262d); }
          .cgtA { fill: var(--accent, #d4ff3a); stroke: none; }
          .cgtT { fill: currentColor; font: 600 12px system-ui, sans-serif; }
          .cgtS { fill: currentColor; font: 400 10.5px system-ui, sans-serif; opacity: .62; }
          .cgtD { fill: #0a0a0c; font: 700 11px system-ui, sans-serif; }
          .cgtK { fill: var(--accent, #d4ff3a); font: 700 10px system-ui, sans-serif; letter-spacing: .08em; }
          .cgtOk { fill: var(--ok, #4dd6a0); font: 600 10.5px system-ui, sans-serif; }
          .cgtWa { fill: var(--warn, #ffb347); font: 600 10.5px system-ui, sans-serif; }
          .cgtDa { fill: var(--danger, #ff4747); font: 600 10.5px system-ui, sans-serif; }
          .cgtAv { fill: none; stroke: currentColor; opacity: .4; }
        </style>
        <text x="16" y="22" class="cgtK">THE TAB BAR</text>
        <rect class="cgtP" x="16" y="32" width="86" height="28" rx="14"/>
        <text x="30" y="50" class="cgtT">Priority 4</text>
        <rect class="cgtA" x="110" y="32" width="106" height="28" rx="14"/>
        <text x="124" y="50" class="cgtD">Needs reply 12</text>
        <rect class="cgtP" x="224" y="32" width="94" height="28" rx="14"/>
        <text x="238" y="50" class="cgtT">Recovery 9</text>
        <rect class="cgtP" x="326" y="32" width="52" height="28" rx="14"/>
        <text x="340" y="50" class="cgtT">All</text>
        <rect class="cgtP" x="386" y="32" width="62" height="28" rx="14"/>
        <text x="400" y="50" class="cgtT">Mine</text>
        <rect class="cgtP" x="456" y="32" width="64" height="28" rx="14"/>
        <text x="470" y="50" class="cgtT">Team</text>
        <text x="540" y="44" class="cgtS">the lit tab is the list</text>
        <text x="540" y="58" class="cgtS">you are looking at</text>
        <rect class="cgtN" x="16" y="78" width="560" height="58" rx="10"/>
        <circle class="cgtAv" cx="46" cy="107" r="16"/>
        <text x="72" y="101" class="cgtT">Ada</text>
        <text x="72" y="119" class="cgtS">Do you have the scream jeans in a 32?</text>
        <text x="522" y="110" class="cgtOk">2m</text>
        <rect class="cgtN" x="16" y="144" width="560" height="58" rx="10"/>
        <circle class="cgtAv" cx="46" cy="173" r="16"/>
        <text x="72" y="167" class="cgtT">Kunle</text>
        <text x="72" y="185" class="cgtS">Sent the transfer, please confirm</text>
        <text x="512" y="176" class="cgtWa">41m</text>
        <rect class="cgtN" x="16" y="210" width="560" height="58" rx="10"/>
        <circle class="cgtAv" cx="46" cy="239" r="16"/>
        <text x="72" y="233" class="cgtT">Chioma</text>
        <text x="72" y="251" class="cgtS">Hello? Anyone there?</text>
        <text x="498" y="242" class="cgtDa">2h 10m</text>
        <text x="596" y="100" class="cgtS">customer spoke last —</text>
        <text x="596" y="114" class="cgtS">these rows are your to-do</text>
        <text x="596" y="170" class="cgtWa">amber at 30 minutes</text>
        <text x="596" y="240" class="cgtDa">red at 2 hours</text>
      </svg>
      <p><b>Priority</b> — this shift's board. EON ranks the customers who most need a person
      right now: complaints, payment claims, paid orders still open, stalled buys, questions
      nobody answered this shift. Opening a card to read it claims nothing. Your first reply
      to the customer claims it, so two reps never work the same customer; <b>I'm on it</b>
      claims it and opens the chat. When
      nothing more is needed, tap <b>Close — reopens if they write again</b>: it closes the chat and
      clears the card, and both come back if the customer writes again. Answering through the
      normal desk clears the card on its own.</p>
      <svg class="mkg-svg" viewBox="0 0 760 168" role="img" aria-label="Annotated mockup of one Priority card: the reason line, and the I'm on it and Close buttons">
        <style>
          .cgpN { fill: var(--bg-card, #16161a); stroke: var(--line, #26262d); }
          .cgpP { fill: none; stroke: var(--line, #26262d); }
          .cgpA { fill: var(--accent, #d4ff3a); stroke: none; }
          .cgpT { fill: currentColor; font: 600 12px system-ui, sans-serif; }
          .cgpS { fill: currentColor; font: 400 10.5px system-ui, sans-serif; opacity: .62; }
          .cgpD { fill: #0a0a0c; font: 700 11px system-ui, sans-serif; }
          .cgpK { fill: var(--accent, #d4ff3a); font: 700 10px system-ui, sans-serif; letter-spacing: .08em; }
          .cgpW { fill: var(--warn, #ffb347); font: 700 12px system-ui, sans-serif; }
        </style>
        <text x="16" y="22" class="cgpK">A PRIORITY CARD</text>
        <rect class="cgpN" x="16" y="32" width="560" height="118" rx="12"/>
        <text x="34" y="60" class="cgpW">₦</text>
        <text x="52" y="60" class="cgpT">Kunle — payment claim</text>
        <text x="34" y="84" class="cgpS">Says he transferred 45 minutes ago — the order is still unpaid.</text>
        <rect class="cgpP" x="34" y="102" width="86" height="30" rx="8"/>
        <text x="50" y="121" class="cgpT">I'm on it</text>
        <rect class="cgpA" x="130" y="102" width="62" height="30" rx="8"/>
        <text x="144" y="121" class="cgpD">Close</text>
        <text x="596" y="62" class="cgpS">why this customer</text>
        <text x="596" y="76" class="cgpS">is on the board</text>
        <text x="596" y="112" class="cgpS">claim it — two reps</text>
        <text x="596" y="126" class="cgpS">never work one chat</text>
      </svg>
      <p><b>Needs reply</b> — every fresh thread (last 3 days) where the customer spoke last,
      newest first. This is the working queue; a paying customer's wait counts heavier here.</p>
      <p><b>Recovery</b> — the quiet tail: threads older than 3 days we keep for win-back,
      most recently lost first. Empty is good news here.</p>
      <p><b>All</b> — everything, including resolved threads. <b>Mine</b> — open threads
      assigned to you. <b>Team</b> — staff-to-staff chats; it only appears when you're signed
      in through Staff OS.</p>
    </section>

    <section class="mkg-sec" id="cg-inbox">
      <h2>The inbox</h2>
      <p><b>Queue chips</b> at the top split the desk by department — CS, Logistics, per-store
      sales, Online. If your role is locked to one queue you only see that queue's threads.</p>
      <p><b>Awaiting reply</b> shows every thread where the customer spoke last — that list is
      your to-do. The timer next to each shows how long they've been waiting; it turns amber at
      30 minutes and red at 2 hours.</p>
      <p><b>Pins</b> keep a thread at the top of <i>your</i> list (long-press / pin action) —
      pins are personal, not shared. <b>Labels</b> are shared tags (burger menu → Filter by
      label) for things like "VIP" or "wholesale". Create labels from an open conversation.</p>
      <p><b>Search</b> finds text across all conversations; inside a thread the in-thread search
      jumps straight to the matching bubble.</p>
      <p><b>Profile pictures:</b> Instagram threads show the customer's real photo where their
      account allows it. WhatsApp never shares profile photos with businesses — those threads
      keep the coloured initial. That's a WhatsApp rule, not a bug.</p>
      <p><b>Automated messages</b> (burger menu) holds threads EON messaged on its own — glance
      here to see what went out automatically and pick up any that need a human.</p>
    </section>

    <section class="mkg-sec" id="cg-answer">
      <h2>Answering</h2>
      <p><b>Reply</b> — type and send. Drafts save themselves per-thread; leave and come back
      and your half-written reply is still there. Instagram caps a message at 1,000 characters,
      WhatsApp at 4,000 — the desk warns you before anything is cut.</p>
      <p><b>Quote a message</b> — tap the ↩ on a bubble (or swipe it right) to reply to that
      specific message, like WhatsApp. Long-press a bubble to react.</p>
      <p><b>Send a photo</b> — the image button in the composer sends product shots or any
      picture with an optional caption.</p>
      <p><b>Voice notes</b> — customers send a LOT of them. Tap play, change speed, or tap
      <i>✦ Transcribe</i> to turn one into text you can read and search.</p>
      <p><b>Templates</b> — structured messages (header, body, buttons) render as cards, both
      the ones we send and the ones customers tap replies on.</p>
    </section>

    <section class="mkg-sec" id="cg-tools">
      <h2>The composer and the tray tools</h2>
      <p class="mkg-lead">The <b>+</b> next to the message box opens the tray — every selling
      tool lives there.</p>
      <svg class="mkg-svg" viewBox="0 0 760 216" role="img" aria-label="Annotated mockup of the composer: the tools tray open above the message box with plus, mic and send buttons">
        <style>
          .cgcN { fill: var(--bg-card, #16161a); stroke: var(--line, #26262d); }
          .cgcP { fill: none; stroke: var(--line, #26262d); }
          .cgcA { fill: var(--accent, #d4ff3a); stroke: none; }
          .cgcT { fill: currentColor; font: 600 11px system-ui, sans-serif; }
          .cgcS { fill: currentColor; font: 400 10.5px system-ui, sans-serif; opacity: .62; }
          .cgcK { fill: var(--accent, #d4ff3a); font: 700 10px system-ui, sans-serif; letter-spacing: .08em; }
          .cgcD { fill: #0a0a0c; font: 700 12px system-ui, sans-serif; }
        </style>
        <text x="16" y="22" class="cgcK">THE TRAY (opens from +)</text>
        <rect class="cgcP" x="16"  y="34" width="76" height="26" rx="13"/><text x="30" y="51" class="cgcT">Suggest</text>
        <rect class="cgcP" x="100" y="34" width="62" height="26" rx="13"/><text x="114" y="51" class="cgcT">Label</text>
        <rect class="cgcP" x="170" y="34" width="64" height="26" rx="13"/><text x="184" y="51" class="cgcT">Photo</text>
        <rect class="cgcP" x="242" y="34" width="76" height="26" rx="13"/><text x="256" y="51" class="cgcT">Product</text>
        <rect class="cgcP" x="326" y="34" width="62" height="26" rx="13"/><text x="340" y="51" class="cgcT">Stock</text>
        <rect class="cgcP" x="396" y="34" width="62" height="26" rx="13"/><text x="410" y="51" class="cgcT">Order</text>
        <rect class="cgcP" x="466" y="34" width="76" height="26" rx="13"/><text x="480" y="51" class="cgcT">Pay link</text>
        <rect class="cgcP" x="16"  y="68" width="84" height="26" rx="13"/><text x="30" y="85" class="cgcT">Their edit</text>
        <rect class="cgcP" x="108" y="68" width="76" height="26" rx="13"/><text x="122" y="85" class="cgcT">Delivery</text>
        <rect class="cgcP" x="192" y="68" width="66" height="26" rx="13"/><text x="206" y="85" class="cgcT">Try-on</text>
        <rect class="cgcP" x="266" y="68" width="56" height="26" rx="13"/><text x="280" y="85" class="cgcT">Note</text>
        <rect class="cgcP" x="330" y="68" width="62" height="26" rx="13"/><text x="344" y="85" class="cgcT">Quick</text>
        <rect class="cgcP" x="400" y="68" width="70" height="26" rx="13"/><text x="414" y="85" class="cgcT">@ Route</text>
        <rect class="cgcP" x="478" y="68" width="76" height="26" rx="13"/><text x="492" y="85" class="cgcT">/ Replies</text>
        <text x="580" y="51" class="cgcS">tap a chip — each opens</text>
        <text x="580" y="65" class="cgcS">its own small sheet</text>
        <rect class="cgcN" x="16" y="116" width="560" height="46" rx="23"/>
        <circle class="cgcP" cx="44" cy="139" r="14"/>
        <text x="40" y="144" class="cgcT">+</text>
        <text x="70" y="143" class="cgcS">Message</text>
        <circle class="cgcP" cx="500" cy="139" r="14"/>
        <text x="495" y="143" class="cgcS">o</text>
        <circle class="cgcA" cx="546" cy="139" r="16"/>
        <text x="540" y="144" class="cgcD">&gt;</text>
        <text x="596" y="132" class="cgcS">hold the small circle</text>
        <text x="596" y="146" class="cgcS">to record a voice note;</text>
        <text x="596" y="160" class="cgcS">the lit one sends</text>
        <text x="16" y="186" class="cgcS">Drafts save themselves per thread — leave and come back, your half-written reply is still there.</text>
      </svg>
      <p>One line each, left to right:</p>
      <p><b>Suggest</b> — EON drafts the next reply; you read, edit, send.
      <b>Label</b> — tag the thread with a shared label.
      <b>Photo</b> — send a picture or video, from the library or the camera.
      <b>Product</b> — search the catalogue and send a product card.
      <b>Stock</b> — live per-branch stock for any product.
      <b>Order</b> — build the customer's order right in the chat.
      <b>Pay link</b> — a secure payment link for that order (more below).
      <b>Their edit</b> — a personal page of twenty looks picked from what they buy.
      <b>Delivery</b> — reads their last message as the address and quotes fee and time; tap
      the chip to drop it into your reply.
      <b>Try-on</b> — put a CRUX piece on the customer's photo and send the picture.
      <b>Note</b> — private notes; the customer never sees these.
      <b>Quick</b> — team-shared quick replies, and the approved-template door when the window
      is closed.
      <b>@ Route</b> and <b>/ Replies</b> — shortcuts that type @ or / for you.</p>
      <p><b>Typed straight into the box:</b> a message starting <b>@eon</b> is a command to
      EON, never sent to the customer — "@eon is the scream jean in stock" drops a send-ready
      stock answer into the box. An <b>@Name</b> or <b>@Store</b> mention routes the chat to
      that teammate or store and sends them a link. <b>/</b> opens quick replies as you type.</p>
    </section>

    <section class="mkg-sec" id="cg-auto">
      <h2>Auto-answered threads and the override banner</h2>
      <p>EON answers the common repeat questions — hours, address, delivery, and the like —
      the moment they arrive, before anyone opens the thread. In the list, those threads carry
      an <b>Auto-answered</b> chip so you always know a robot spoke, not a person.</p>
      <svg class="mkg-svg" viewBox="0 0 760 130" role="img" aria-label="Annotated mockup of a thread row carrying the Auto-answered chip">
        <style>
          .cgaN { fill: var(--bg-card, #16161a); stroke: var(--line, #26262d); }
          .cgaC { fill: none; stroke: var(--accent, #d4ff3a); }
          .cgaT { fill: currentColor; font: 600 12px system-ui, sans-serif; }
          .cgaS { fill: currentColor; font: 400 10.5px system-ui, sans-serif; opacity: .62; }
          .cgaK { fill: var(--accent, #d4ff3a); font: 700 10px system-ui, sans-serif; letter-spacing: .08em; }
          .cgaL { fill: var(--accent, #d4ff3a); font: 600 10.5px system-ui, sans-serif; }
          .cgaAv { fill: none; stroke: currentColor; opacity: .4; }
        </style>
        <text x="16" y="22" class="cgaK">A THREAD EON ALREADY ANSWERED</text>
        <rect class="cgaN" x="16" y="34" width="560" height="58" rx="10"/>
        <circle class="cgaAv" cx="46" cy="63" r="16"/>
        <text x="72" y="57" class="cgaT">Emeka</text>
        <text x="72" y="75" class="cgaS">What time do you close today?</text>
        <rect class="cgaC" x="428" y="48" width="128" height="26" rx="13"/>
        <text x="442" y="65" class="cgaL">Auto-answered</text>
        <text x="596" y="58" class="cgaS">the chip marks the</text>
        <text x="596" y="72" class="cgaS">thread in the list</text>
        <text x="16" y="116" class="cgaS">Open the thread to read exactly what went out — every EON send is stamped in the chat.</text>
      </svg>
      <p>Open one of these threads and a banner sits above the chat. If the answer wasn't
      enough — or the customer kept talking — take the thread back with one tap.</p>
      <svg class="mkg-svg" viewBox="0 0 760 118" role="img" aria-label="Annotated mockup of the override banner with the Override button">
        <style>
          .cgoN { fill: var(--bg-card, #16161a); stroke: var(--line, #26262d); }
          .cgoA { fill: var(--accent, #d4ff3a); stroke: none; }
          .cgoT { fill: currentColor; font: 600 12px system-ui, sans-serif; }
          .cgoS { fill: currentColor; font: 400 10.5px system-ui, sans-serif; opacity: .62; }
          .cgoK { fill: var(--accent, #d4ff3a); font: 700 10px system-ui, sans-serif; letter-spacing: .08em; }
          .cgoD { fill: #0a0a0c; font: 700 11px system-ui, sans-serif; }
        </style>
        <text x="16" y="22" class="cgoK">THE OVERRIDE BANNER (top of the chat)</text>
        <rect class="cgoN" x="16" y="34" width="560" height="44" rx="10"/>
        <text x="32" y="61" class="cgoT">EON answered this one from the FAQ.</text>
        <rect class="cgoA" x="382" y="42" width="180" height="28" rx="8"/>
        <text x="396" y="60" class="cgoD">Override — I'll take it</text>
        <text x="596" y="52" class="cgoS">one tap takes the</text>
        <text x="596" y="66" class="cgoS">thread back — logged</text>
        <text x="16" y="102" class="cgoS">The chip leaves the list, the composer gets focus, and the override is counted — so we learn which answers weren't good enough.</text>
      </svg>
      <p><b>What is never auto-answered:</b> stock questions and payment questions — those
      always wait for a person. Stock moves faster than any saved answer, and money answers
      must come from someone who can see the order in front of them. Price, delivery, address,
      opening hours, order status and returns can go out automatically from the saved answers
      you approve. <b>Edit the words:</b> burger menu → FAQ auto-answers. Each answer goes out
      exactly as written, and each card shows how often it was sent and how often a rep
      overrode it in the last 7 days.</p>
    </section>

    <section class="mkg-sec" id="cg-templates">
      <h2>Templates — what the customer actually sees</h2>
      <p><b>Quick replies</b> (tray → Quick) are team-shared snippets. Tapping one drops the
      words into your box — edit them, then send. The customer sees a normal message from
      CRUX, never a form letter.</p>
      <p><b>Approved WhatsApp templates</b> are different: WhatsApp pre-approves the exact
      wording, and once a chat's 24-hour window closes they are the only messages WhatsApp
      will carry. The desk's <b>Send template</b> button requests an approved template using the
      customer's own last words as the topic — never invented copy. Acceptance is not delivery;
      only the customer's reply reopens free-form messaging. Your draft stays editable meanwhile. Structured templates
      (header, body, buttons) show as cards in the chat, and the customer can tap the buttons
      to reply.</p>
      <p><b>Read the send history:</b> burger menu → Automated messages.
      <b>Template send history</b> shows up to 100 records from the last 30 days within your access, with recorded statuses;
      <b>Template gallery</b> shows one card per template — how it reads and its sent and
      failed counts for the last 14 days; <b>Abandoned carts</b> lists the recovery nudges.</p>
    </section>

    <section class="mkg-sec" id="cg-media">
      <h2>What customers send — and what it means</h2>
      <p><b>Product photos</b> — when someone sends a photo asking "do you have this?", the desk
      matches it against the live catalog and shows a product chip under the bubble with price
      and stock. If the chip says the stock check is <i>unavailable</i>, say you'll confirm —
      never tell a customer something is out of stock from a failed check.</p>
      <p><b>Albums</b> — multi-photo/video messages fold into one grid bubble; tap to zoom.</p>
      <p><b>Story tags &amp; story replies</b> — when a customer tags CRUX in a story or replies
      to ours, the story itself (photo or video) shows in the thread with an "IG story" tag.
      Thank them — tagged stories are free marketing from a paying customer. A story that shows
      "Story unavailable" expired before we could save it.</p>
      <p><b>Reels</b> — a shared reel arrives as a link; open it in Instagram to see what
      they're asking about.</p>
      <p><b>View-once</b> — "can only be opened in the Instagram app" means exactly that:
      Instagram never delivers view-once photos to any business tool. Ask the customer to resend
      it as a normal photo.</p>
      <p><b>Payment screenshots</b> — a bank-transfer screenshot gets read automatically and a
      one-line summary (amount · sender · bank · reference) appears marked <b>UNVERIFIED</b>.
      That line is a hint, not a confirmation — <b>always verify money in the bank app before
      releasing an order.</b></p>
    </section>

    <section class="mkg-sec" id="cg-orders">
      <h2>The customer panel (the 360)</h2>
      <p>Open it from the customer's name. It shows their <b>lifetime value and tier</b>, full
      <b>order history</b>, <b>order status</b> lookups by order number (paid / fulfilled /
      delivery), <b>size hints</b> learned from their past purchases, a <b>delivery
      estimate</b> helper, and shared <b>notes</b> — write down anything the next rep should
      know. Labels applied here show in the inbox for everyone.</p>
      <p>If the panel looks empty for someone who says they've bought before, they probably
      purchased in-store or on the website with details we haven't linked yet — ask for the
      email or phone they used at purchase and check the panel's customer lookup.</p>
    </section>

    <section class="mkg-sec" id="cg-eon">
      <h2>EON at your side</h2>
      <p><b>Drafts</b> — EON prepares suggested replies for common questions. Read before you
      send; you are always the editor. What you actually send teaches EON what good looks
      like.</p>
      <p><b>Automated sends</b> — with the owner's switches on, EON answers some routine
      messages itself (overnight, and in daytime lanes it has proven it handles well). Anything
      it sent is stamped as EON in the thread — you'll never be blamed for a robot's message.
      Complaints and payment questions are <b>never</b> answered automatically; they always
      wait for a human.</p>
      <p><b>Escalation</b> — when EON is unsure it stops and leaves the thread for you. The
      Automated-messages view is your morning sweep of what went out overnight.</p>
    </section>

    <section class="mkg-sec" id="cg-flows">
      <h2>Closing a sale — photo to product to pay link</h2>
      <div class="mkg-flow">
        <div class="mkg-step"><div class="mkg-num">1</div><b>They ask</b><p>A photo or "do you
        have this?" — the product chip or the Stock tool gives you price, sizes and per-branch
        stock. Never guess.</p></div>
        <div class="mkg-step"><div class="mkg-num">2</div><b>Show it</b><p>Tray → Product sends
        the product card. Tray → Try-on puts it on their photo if they want to see it worn.</p></div>
        <div class="mkg-step"><div class="mkg-num">3</div><b>Build the order</b><p>Tray →
        Order collects the pieces into a cart with sizes and quantities, right in the chat.</p></div>
        <div class="mkg-step"><div class="mkg-num">4</div><b>Send the pay link</b><p>Tray →
        Pay link mints a secure payment link for that cart and drops a ready message into your
        box. Review it, send it.</p></div>
        <div class="mkg-step"><div class="mkg-num">5</div><b>Money lands</b><p>Nothing is
        held for them until they pay — the order is placed once the payment lands. A transfer
        screenshot is UNVERIFIED until the bank app agrees.</p></div>
      </div>
      <p>After that, EON takes over the follow-through — receipt, review ask, win-back — see
      <b>EON behind the scenes</b> below.</p>
    </section>

    <section class="mkg-sec" id="cg-sla">
      <h2>Waiting times &amp; routing</h2>
      <p>The clock starts when the customer's message arrives. <b>Amber at 30 minutes, red at
      2 hours.</b> The goal isn't a perfect score — it's that no paying customer sits ignored
      while we chat internally.</p>
      <p><b>Reroute</b> — a delivery question in the CS queue? Send the thread to Logistics
      (thread actions → reroute). A store question goes to that store's queue. Routing moves
      the thread, not the customer — history stays intact.</p>
      <p><b>Seen</b> — opening a thread marks it seen for you only. Your teammate still sees it
      as unread until they open it. Honest unread counts mean "0 unread" is actually true.</p>
    </section>

    <section class="mkg-sec" id="cg-platform">
      <h2>WhatsApp vs Instagram — the platform rules</h2>
      <p><b>Reply windows:</b> both channels run a 24-hour window from the customer's last
      message. WhatsApp is strict — outside it, only approved templates go through, and the
      desk blocks free text. Instagram shows you the same closed banner but does not stop
      you, and we cannot tell you whether that send lands: Instagram returns no delivery
      receipt, so the desk only ever knows the message was accepted. Treat the closed banner
      as a warning and get the customer to message first when you can.</p>
      <p><b>What the platforms refuse to give any business tool:</b> WhatsApp profile photos,
      view-once media, and Instagram reaction hearts on your messages. If a customer says "I
      reacted ❤️" — believe them; we just can't see it.</p>
      <p><b>Character limits:</b> 1,000 on Instagram, 4,000 on WhatsApp.</p>
    </section>

    <section class="mkg-sec" id="cg-staffline">
      <h2>The staff line — EON for the team</h2>
      <p><b>On WhatsApp:</b> staff message the same CRUX number customers use, and EON keeps
      that traffic out of the customer desk entirely. Ask it a stock question ("how many
      rockie jackets at Palms?") and it answers from live inventory; ask a styling question
      and it answers from the styling brain. A software or build request is queued for the
      owner to approve. Start a message with <b>#order</b> and it goes to the customer desk
      instead — that's how staff shop as customers.</p>
      <p><b>In the desk composer:</b> <b>@eon</b> is your shift assistant — stock answers,
      drafted replies, try-on renders, styling tips. Everything it gives you lands in your box
      or an overlay for review; nothing goes to the customer until you send it.</p>
      <p><b>In team chat:</b> <b>@Name</b> reaches a teammate, <b>@Palms</b> (or any store)
      reaches whoever is on that store's shift today.</p>
      <p>The drawer's <b>Staff Line</b> view — the read-out of every staff thread and request —
      is the owner's window; it only shows for owner and admin logins.</p>
    </section>

    <section class="mkg-sec" id="cg-behind">
      <h2>What EON does on its own, behind the scenes</h2>
      <p>These run without a rep touching anything. Every send is stamped as EON in the
      thread, so you always know who spoke.</p>
      <p><b>Receipts</b> — every paid walk-in with a phone number gets a WhatsApp receipt,
      which turns a store visit into a named chat you can serve.</p>
      <p><b>Review asks</b> — three days after delivery the review message goes out; a happy
      reply gets a thank-you and a public-review invite, an unhappy one opens a
      service-recovery task for a person — never an auto-reply.</p>
      <p><b>Win-back</b> — customers who've gone quiet get a measured daily drip inviting
      them back. Capped, never a blast.</p>
      <p><b>Nurture</b> — a care tip about a week after delivery, a complete-the-look
      suggestion about three weeks after.</p>
      <p><b>Cart recovery</b> — abandoned carts get a nudge; watch them under burger menu →
      Automated messages → Abandoned carts.</p>
    </section>

    <section class="mkg-sec" id="cg-conduct">
      <h2>Privacy &amp; tone</h2>
      <p><b>Customer data stays in the desk.</b> No screenshots of conversations to personal
      chats, ever. The panel exists so you never need to copy anything out.</p>
      <p><b>Your numbers are yours.</b> You see your own performance, and the team's shared
      goal. There is no public ranking of reps — we compete with the target, not each other.</p>
      <p><b>Tone: quiet authority.</b> Warm, short, certain. No begging, no walls of text, no
      fake urgency. If we made a mistake, say so plainly and fix it. When you don't know —
      "let me confirm that for you" beats a guess, every time.</p>
    </section>

    <section class="mkg-sec" id="cg-new">
      <h2>New features — how this chat keeps evolving</h2>
      <p class="mkg-lead">What shipped lately, newest first. This list is updated by hand with
      each release.</p>
      <p><b>2 Aug 2026</b> — WhatsApp catalog orders now show as a real order card in the chat
      (they used to arrive as blank bubbles).</p>
      <p><b>2 Aug 2026</b> — The night desk circles back: a question that lands while it's
      mid-reply is answered later, not dropped — and it now knows the stores.</p>
      <p><b>2 Aug 2026</b> — Store mentions in team chat: @Palms (or any store) reaches
      whoever is on that store's shift today.</p>
      <p><b>2 Aug 2026</b> — Template gallery: one card per template with its live wording and
      honest 14-day sent and failed counts.</p>
      <p><b>1 Aug 2026</b> — The WhatsApp autopilot speaks the whole catalogue — eyewear
      included — and hands over to the team honestly when it can't help.</p>
      <p><b>31 Jul 2026</b> — No more mid-chat logouts; your session survives the shift.</p>
      <p><b>31 Jul 2026</b> — FAQ auto-answers with the override banner: EON answers the
      common repeat questions instantly, and one tap takes the thread back.</p>
      <p><b>31 Jul 2026</b> — Their edit: send a customer a personal page of twenty looks
      picked from their buying pattern.</p>
      <p><b>31 Jul 2026</b> — Till orders with no customer: link the chat that paid for a till
      order so the customer lands on the sale and your capture credit counts.</p>
      <p><b>29 Jul 2026</b> — Priority cards clear when you answer through the desk, and
      Instagram's 24-hour window finally shows honestly.</p>
    </section>

    <section class="mkg-sec" id="cg-cheat">
      <h2>The 1-minute cheat sheet</h2>
      <div class="mkg-grid">
        <div class="mkg-card"><h4>Customer waiting?</h4><p>Live in the Awaiting-reply tab. Amber at 30 minutes, red at 2 hours — nobody sits ignored.</p></div>
        <div class="mkg-card"><h4>Reply to one message</h4><p>Tap ↩ on the bubble or swipe it right. Long-press to react.</p></div>
        <div class="mkg-card"><h4>Voice note → text</h4><p>✦ Transcribe under the player turns it into readable text.</p></div>
        <div class="mkg-card"><h4>"Do you have this?" photo</h4><p>The product chip under the photo has price + stock. Chip failed? Confirm before saying out-of-stock.</p></div>
        <div class="mkg-card"><h4>Transfer screenshot</h4><p>The OCR line is UNVERIFIED. Money is only real in the bank app.</p></div>
        <div class="mkg-card"><h4>Story tag</h4><p>The story shows in-thread. Thank them — that's a paying customer marketing for us.</p></div>
        <div class="mkg-card"><h4>View-once media</h4><p>Instagram won't deliver it to any business tool. Ask for a normal resend.</p></div>
        <div class="mkg-card"><h4>Wrong department</h4><p>Thread actions → reroute. History travels with the thread.</p></div>
      </div>
    </section>
  </div>`;
}

function cswSetLabelFilter(label) {
  cswState().labelFilter = label;
  cswCloseDrawer();
  cswRenderActiveFilter();
  cswRenderThreadList();
}
// Active-filter pill above the thread list + burger active-dot.
function cswRenderActiveFilter() {
  const s = cswState();
  const el = document.getElementById('csw-activefilter');
  document.getElementById('csw-burger')?.classList.toggle('on', !!s.labelFilter);
  if (!el) return;
  if (!s.labelFilter) { el.hidden = true; el.innerHTML = ''; return; }
  el.hidden = false;
  el.innerHTML = `<span class="csw-activefilter__pill">🏷 ${esc(s.labelFilter)}<button class="csw-activefilter__x" aria-label="Clear">✕</button></span>`;
  el.querySelector('.csw-activefilter__x')?.addEventListener('click', () => cswSetLabelFilter(null));
}

// Thread-row preview — never leave a dead/blank row. Media + template +
// story/comment messages carry no text, so show a typed placeholder.
const CSW_TYPE_PREVIEW = {
  image: '📷 Photo', audio: '🎤 Voice note', voice: '🎤 Voice note',
  video: '🎬 Video', ig_reel: '🎬 Reel', document: '📄 Document',
  sticker: '⬚ Sticker', contacts: '👤 Contact', location: '📍 Location',
  buttontemplate: '💬 Template', richtemplate: '💬 Template',
  template: '💬 Template', interactive_list: '☰ List', button: '💬 Reply',
  comment: '💬 Comment', story_reply: '↩ Story reply',
  story_mention: '↩ Story mention', ig_story: '↩ Story',
  multiple: '🖼 Album',
};
function cswPreview(t) {
  const s = cswState();
  // Wave A (2026-07-27, operator): the row's second line belongs to the
  // CUSTOMER — a rep must see what was said without opening the thread.
  // A waiting draft is a ✎ suffix, never a replacement (the old
  // "Draft: …" preference let EON's silent pre-draft squat on rows and
  // hide the customer's actual message).
  // Words on their way to the customer are not a draft (Fable vet, 27 Sep).
  const draftMark = (cswLoadDraft(t.zoko_customer_id) || '').trim()
    ? ' <span class="csw-draftlbl" title="draft waiting">✎</span>' : '';
  // Own-side delivery tick (TL-03) — WhatsApp carries the ladder on the
  // thread row, not just in the bubble. Only when WE spoke last.
  const mine = (t.last_direction || '').toLowerCase() === 'outbound';
  const tick = mine ? (cswPlatform(t) === 'ig' ? cswTickGlyphIg(t.last_message_status) : cswTickGlyph(t.last_message_status)) : '';
  const ty = (t.last_message_type || '').toLowerCase();
  const glyph = CSW_TYPE_PREVIEW[ty] || '';
  const txt = (t.last_message_text || '').trim();
  if (txt) {
    // A photo/voice WITH a caption used to read as plain text — the row
    // couldn't tell a complaint photo from a sentence. The glyph stays.
    const g = (glyph && ty !== 'text') ? `<span class="csw-prevglyph">${esc(glyph.split(' ')[0])}</span> ` : '';
    return { html: tick + g + esc(txt.slice(0, 80)) + draftMark, muted: false };
  }
  return { html: tick + esc(glyph || 'New message') + draftMark, muted: true };
}

// One tick / two ticks / two accent ticks — the same ladder the bubbles use,
// sized for the preview line. An unknown status renders NOTHING rather than
// a lie (PR A made the underlying status honest).
function cswTickGlyph(status) {
  const st = String(status || '').toLowerCase();
  if (!st) return '';
  // WS-B (2026-09-02): 'failed' used to render NOTHING on the row — the
  // same blank as "no status yet". A red hollow mark is the fourth state
  // the row can be in, and the only one that asks the rep to open it.
  if (st === 'failed') return cswFailMarkSvg('csw-prevtick csw-prevtick--fail') + ' ';
  const two = ['received', 'delivered', 'seen', 'read', 'played'].includes(st);
  const read = ['seen', 'read', 'played'].includes(st);
  return `<span class="csw-prevtick${read ? ' csw-prevtick--read' : ''}" aria-hidden="true">`
    + `<svg viewBox="0 0 26 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">`
    + `<path d="M2 9l3.5 3.5L14 4"/>${two ? '<path d="M9 12.5L18 4"/>' : ''}</svg></span> `;
}
// The Instagram row tick (WS-D, 2026-09-02): one open ring, always.
// Instagram's transport reports API acceptance and never a receipt —
// 1,228 IG outbounds in 42 days, zero delivery receipts — so the row may
// say "sent" and nothing after it. A failed IG send still wears the red
// hollow mark; every other outbound status collapses to the ring.
function cswTickGlyphIg(status) {
  const st = String(status || '').toLowerCase();
  if (st === 'failed') return cswFailMarkSvg('csw-prevtick csw-prevtick--fail') + ' ';
  return cswRingSvg('csw-prevtick csw-prevtick--ring') + ' ';
}
// The two drawn marks the tick family gained (the SVG family: one tick,
// two ticks, two accent ticks — plus these). aria-hidden: the words
// beside them carry the meaning for AT.
function cswFailMarkSvg(cls) {
  return `<span class="${cls}" aria-hidden="true"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="8" cy="8" r="5.5"/><path d="M5.5 5.5l5 5M10.5 5.5l-5 5"/></svg></span>`;
}
function cswRingSvg(cls) {
  return `<span class="${cls}" title="sent — no receipt exists" aria-hidden="true"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><circle cx="8" cy="8" r="5.5" stroke-dasharray="4 3"/></svg></span>`;
}
// ── Template-message card parsing (Zoko buttonTemplate / richTemplate) ──
// Automated/template outbounds persist their body.text as a flat string:
//   Header: Image <url>            (optional; or "Header: Text <copy>")
//   Body: <copy>                   (may span multiple lines)
//   Footer: <copy>                 (optional)
//   Buttons: [Label] <url-or-id>, [Label2] <url-or-id>   (optional)
// Rendering that raw is ugly. cswParseTemplate lifts the structure so we can
// render a clean WhatsApp-grade card (header image on top, body copy, footer,
// then buttons as chips — label only, the URL is the action). Returns null when
// the text doesn't look like a template, so normal messages are untouched.
function cswParseTemplate(text) {
  if (!text || typeof text !== 'string') return null;
  // Must carry an explicit Body: line and at least one of Header/Buttons —
  // that's the template shape. A plain message that happens to say "Body:" in
  // prose won't have the leading-line structure.
  if (!/^\s*(Header|Body|Buttons|Footer)\s*:/m.test(text)) return null;
  const grab = (re) => { const m = text.match(re); return m ? m[1].trim() : ''; };
  // Header: "Image <url>" | "Video <url>" | "Document <url>" | "Text <copy>".
  let headerImage = '', headerText = '';
  const hraw = grab(/^\s*Header\s*:\s*([^\n]*)/m);
  if (hraw) {
    const hm = hraw.match(/^(Image|Video|Document)\s+(\S+)/i);
    if (hm) { if (/image/i.test(hm[1])) headerImage = hm[2]; }
    else { const tm = hraw.match(/^Text\s+([\s\S]+)/i); headerText = tm ? tm[1].trim() : hraw; }
  }
  // Body spans until the next known label or end.
  const body = grab(/^\s*Body\s*:\s*([\s\S]*?)(?=\n\s*(?:Footer|Buttons)\s*:|$)/m);
  const footer = grab(/^\s*Footer\s*:\s*([^\n]*)/m);
  // Buttons: "[Label] target, [Label2] target" — split on the "], " boundary so
  // a target containing a comma/space doesn't fracture a button. Label only is
  // rendered; the target (URL or a quick-reply id) is dropped from the chip.
  const braw = grab(/^\s*Buttons\s*:\s*([\s\S]*)$/m);
  const buttons = [];
  if (braw) {
    const re = /\[([^\]]*)\]/g; let m;
    while ((m = re.exec(braw)) !== null) {
      const label = (m[1] || '').trim();
      if (label) buttons.push(label);
    }
  }
  if (!body && !headerImage && !headerText && !buttons.length) return null;
  return { headerImage, headerText, body, footer, buttons };
}

// Render a parsed template as a WhatsApp-grade card. `opts.compact` trims it for
// an Outreach preview (no footer, single-line body, smaller header).
function cswTemplateCardHtml(p, opts) {
  if (!p) return '';
  opts = opts || {};
  const compact = !!opts.compact;
  const parts = [];
  if (p.headerImage) {
    const src = cswImgSrc(p.headerImage);
    parts.push(`<div class="csw-tpl__hdr"><img class="csw-tpl__himg" src="${esc(src)}" alt="" loading="lazy"/></div>`);
  } else if (p.headerText) {
    parts.push(`<div class="csw-tpl__htext">${esc(p.headerText)}</div>`);
  }
  if (p.body) {
    const bodyTxt = compact ? p.body.replace(/\s+/g, ' ').slice(0, 120) : p.body;
    parts.push(`<div class="csw-tpl__body">${cswLinkify(bodyTxt)}</div>`);
  }
  if (!compact && p.footer) parts.push(`<div class="csw-tpl__footer">${esc(p.footer)}</div>`);
  if (p.buttons && p.buttons.length) {
    parts.push(`<div class="csw-tpl__btns">${p.buttons.map(b =>
      `<span class="csw-tpl__btn">${esc(b)}</span>`).join('')}</div>`);
  }
  return `<div class="csw-tpl${compact ? ' csw-tpl--compact' : ''}">${parts.join('')}</div>`;
}

// ── Template FAMILY map (2026-07-27) ────────────────────────────────
// ONE source for "which purpose family is this template" — the Automated
// view's §08 grouping (famOf) and the in-thread family cards both key off
// this, so a template can never sit in one family in the log and another
// in the transcript. Substring checks + order are the §08 mapping verbatim
// (zero schema: every template name encodes its family). `art` is the
// STATIC family header from /static/wa_studio — the same repo art the WABA
// send attaches. LAW (operator, receipt-fake-art 2026-07-26): the art
// NEVER carries order data — all data text renders from the stored
// send-time copy, verbatim.
function cswTplFamily(name) {
  const n = (name || '').toLowerCase();
  if (n.includes('receipt')) return { key: 'receipt', group: 'Receipts', label: 'Receipt', art: 'hdr-receipt.jpg' };
  if (n.includes('review')) return { key: 'review', group: 'Reviews', label: 'Review', art: 'hdr-review.jpg' };
  if (n.includes('nurture') || n.includes('replenish') || n.includes('anniversary')) {
    return { key: 'followup', group: 'Follow-ups', label: 'Follow-up',
             art: n.includes('anniversary') ? 'hdr-anniversary.jpg'
                : n.includes('replenish') ? 'hdr-replenish.jpg'
                : n.includes('tip') ? 'hdr-nurture-tip.jpg' : 'hdr-nurture-look.jpg' };
  }
  if (n.includes('winback')) return { key: 'winback', group: 'Win-back', label: 'Win-back', art: 'hdr-winback.jpg' };
  if (n.includes('courier') || n.includes('van') || n.includes('dispatch') || n.includes('delivery')) {
    return { key: 'delivery', group: 'Delivery', label: 'Delivery',
             art: n.includes('courier') ? 'hdr-courier.jpg' : 'hdr-van.jpg' };
  }
  if (n.includes('cart')) return { key: 'cart', group: 'Carts', label: 'Cart', art: 'hdr-cart.jpg' };
  if (n.includes('restock') || n.includes('stock')) return { key: 'restock', group: 'Restock', label: 'Restock', art: 'hdr-restock.jpg' };
  return { key: 'other', group: 'Other', label: 'Automated', art: '' };
}
// Receipt facts, DISPLAY-ONLY, parsed from the TRUE stored copy — the
// prominent order/total line lifts substrings out of the send-time body
// text ("Order #34301", "₦2,015,820"); nothing is computed or invented.
// Historical stub rows render placeholders ("Order ⟨…⟩") which don't
// match → null → the plain card. Both facts or none.
function cswReceiptFacts(txt) {
  const s = String(txt || '');
  const om = s.match(/Order\s*(#?[A-Za-z0-9-]+)/i);
  const tm = s.match(/₦\s*\d[\d,]*(?:\.\d+)?/);
  if (!om || !tm || !/\d/.test(om[1])) return null;
  return { order: om[1].startsWith('#') ? om[1] : '#' + om[1],
           total: tm[0].replace(/\s+/g, '') };
}

// Message time (HH:MM) + day grouping helpers (WhatsApp/Telegram feel).
// Chat timestamps arrive in UTC — either '+00:00'-suffixed (PostgREST
// timestamptz) or OFFSETLESS-but-UTC (outbound platform_timestamp).
// The old raw-hour extraction printed the UTC hour as if it were wall
// time, so every message sat an hour off WhatsApp's clock (operator,
// 2026-07-24: "the timing in eon chat is 1 hr [off]"). Parse — treating
// offsetless as UTC — and render in the DEVICE's zone.
function cswParseTs(ts) {
  if (!ts) return null;
  let s = String(ts);
  if (/T\d{2}:\d{2}/.test(s) && !/(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(s)) s += 'Z';
  const d = new Date(s);
  return isNaN(d) ? null : d;
}
function cswMsgTime(ts) {
  const d = cswParseTs(ts);
  return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '';
}
function cswLocalDayKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function cswDayKey(r) {
  const d = cswParseTs(r.platform_timestamp || r.created_at);
  // local-day key — a 23:30 message must not bucket under tomorrow's UTC date
  return d ? cswLocalDayKey(d)
           : String(r.platform_timestamp || r.created_at || '').slice(0, 10);
}
function cswDayLabel(r) {
  const key = cswDayKey(r);
  if (key === cswLocalDayKey(new Date())) return 'Today';
  if (key === cswLocalDayKey(new Date(Date.now() - 86400000))) return 'Yesterday';
  const d = cswParseTs(r.platform_timestamp || r.created_at);
  return d ? d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' }) : key;
}

// Delivery-status tick for outbound bubbles (WhatsApp-style).
// Who spoke, as a small chip in the bubble's time row (mig 503). EON wears
// the whale treatment (the one colour reserved for EON intelligence); a rep
// wears their name, muted. Unknown authorship renders NOTHING — an outbound
// with no author is exactly as ambiguous as it was, never mislabelled.
function cswAuthorChip(r) {
  const b = (r && r.body) || {};
  let a = b.author;
  if (!a && r && r.agent_email) {
    // EON's parallel rows are stamped with its own agent email — they must
    // wear the EON treatment, not read as a rep called "eon-autopilot".
    a = (r.agent_email === 'eon-autopilot@crux.ng')
      ? { kind: 'eon', label: 'EON' }
      : { kind: 'rep', label: String(r.agent_email).split('@')[0] };
  }
  if (!a || !a.label) return '';
  const eon = a.kind === 'eon';
  return `<span class="csw-author${eon ? ' csw-author--eon' : ''}">${esc(String(a.label).slice(0, 24))}</span>`;
}

// What a rejected send actually means, in words a rep can act on.
// Meta's own titles are jargon ("Message undeliverable"); these say what
// happened. Measured 2026-08-31: 663 outbound rows in 60 days carry
// status 'failed' and every one of them rendered EXACTLY like a
// delivered message, because cswTick fell through to '' — the rep read a
// blank tick as "sent, not yet delivered" and moved on. The reason has
// been on the wire the whole time (body.status_errors) and nothing in
// the web layer had ever read it.
const CSW_FAIL_WHY = {
  '131026': 'This number can\u2019t receive WhatsApp messages',
  '131049': 'WhatsApp held this back to limit marketing messages',
  '131053': 'The photo or file was too large to send',
  '131042': 'The WhatsApp account needs billing attention',
  '130472': 'WhatsApp is running an experiment on this number',
  '131047': 'Too long since they wrote \u2014 this needs a template',
  '470':    'Too long since they wrote \u2014 this needs a template',
};
function cswFailWhy(errs, plat) {
  const arr = Array.isArray(errs) ? errs : (errs ? [errs] : []);
  for (const e of arr) {
    const code = String((e && (e.code != null ? e.code : e.error_code)) || '');
    if (CSW_FAIL_WHY[code]) return CSW_FAIL_WHY[code];
  }
  // Vet 2026-09-24 (fault 15): an Instagram chat's failed message said
  // "WhatsApp would not deliver this". Name the app the chat is on — the
  // caller's thread when it has one, else the open chat's.
  if (plat == null) { try { plat = (cswState().activeThread || {})._plat; } catch (_) {} }
  return plat === 'ig' ? 'Instagram would not deliver this' : 'WhatsApp would not deliver this';
}
// L4 (2026-08-30): Meta REJECTED this send (663 in 60d) — it used to fall
// through to '' and render byte-identical to a status-null bubble, so the
// rep read a dead message as "sent, pending". Words, not a glyph — and
// letter-spacing back to normal (the tick's -2px kerning is for ✓✓ pairs;
// it crushes a word to an unreadable smear).
// 2026-08-31: and WHY. The mark tells the rep the message died; the reason
// tells them what to do about it. "This number can't receive WhatsApp
// messages" means stop retrying; "the photo was too large" means resend it
// smaller. It comes from body.status_errors, which has been on the wire
// the whole time and had never been read anywhere in the web layer.
// WS-D (2026-09-02): on an Instagram thread every non-failed outbound
// wears the open ring — 'sent — no receipt exists'. The transport
// returns no receipt, so a tick there would be a claim nothing holds.
function cswTick(status, errs) {
  const s = (status || '').toLowerCase();
  const _at = cswState().activeThread;
  if (_at && _at._plat === 'ig' && s !== 'failed') {
    return ' <span class="csw-tick csw-tick--ring" title="sent — no receipt exists">' + cswRingSvg('csw-tick__ring') + '</span>';
  }
  if (s === 'seen' || s === 'read') return ' <span class="csw-tick read">✓✓</span>';
  if (s === 'received' || s === 'delivered') return ' <span class="csw-tick">✓✓</span>';
  if (s === 'sent' || s === 'accepted' || s === 'submitted') return ' <span class="csw-tick">✓</span>';
  // L4 (2026-08-30) + 2026-08-31: the failed branch — see the note above
  // the function. Words, the reason, and (WS-B) the hollow mark.
  if (s === 'failed') {
    const why = cswFailWhy(errs);
    return ' <span class="csw-tick" style="color:var(--danger);letter-spacing:normal"'
         + ' title="' + esc(why) + '">' + cswFailMarkSvg('csw-tick__fail') + 'Not delivered</span>'
         + ' <span class="csw-failwhy">' + esc(why) + '</span>';
  }
  return '';
}

// ── WS-B (2026-09-02) · failure gets a face: the flap that stays ─────
// Under a failed bubble hangs a hinged strip: the reason in words, the
// raw code, the time — and ONE recovery verb chosen by cause, each wired
// to a route that already exists (no new send path):
//   window shut (131047/470) → Pick up with the template → /pickup
//   photo too large (131053) → Send the words without the photo → /send
//   any media failure        → Send as link (the media URL as text) → /send
//   number / cadence / account faults → no verb: the words say stop.
// Once a later outbound lands on the thread the flap STAYS — at half
// opacity, dated, under the replacement text — so the record of what
// died and how it came back never leaves the transcript.
const CSW_FAIL_CAUSE = {
  '131047': 'window', '470': 'window',
  '131053': 'media',
  '131026': 'unreachable', '130472': 'experiment',
  '131049': 'cadence', '131048': 'cadence', '131050': 'cadence',
  '131042': 'account', '132015': 'account', '132016': 'account', '132001': 'account', '100': 'account',
};
function cswFailCode(errs) {
  const arr = Array.isArray(errs) ? errs : (errs ? [errs] : []);
  for (const e of arr) {
    const code = String((e && (e.code != null ? e.code : e.error_code)) || '');
    if (code) return code;
  }
  return '';
}
// The one verb, by cause. {act, label} or null. `plat` matters once:
// the pickup template is a WhatsApp door — an IG window has no template
// and the customer has to write first.
function cswFailVerb(cause, b, plat, winOpen) {
  const hasMedia = !!(b && (b.image_url || b.file_url || (Array.isArray(b.media) && b.media.length)));
  const words = String((b && (b.text || b.caption)) || '').trim();
  if (cause === 'window') return plat === 'ig' ? null : { act: 'pickup', label: 'Pick up with the template' };
  if (cause === 'media') {
    if (words) return { act: 'words', label: 'Send the words without the photo' };
    if (hasMedia) return { act: 'link', label: 'Send as link' };
    return null;
  }
  if (cause === 'unreachable' || cause === 'cadence' || cause === 'account' || cause === 'experiment') return null;
  if (hasMedia) return { act: 'link', label: 'Send as link' };
  // Vet 2026-09-24 (fault 15): a plain text the app itself reported as
  // failed, for no stop-cause, had no way back — the rep copied and pasted.
  // The app said it did not deliver, so sending the same words again is
  // safe while the chat is open. Never for a template (a different kind of
  // message), and never for words longer than the key carries (900).
  if (winOpen && words && String(b.text || b.caption || '').length <= 900 && !(b.template_name || b.template)) {
    return { act: 'again', label: 'Send again' };
  }
  return null;
}
function cswFailFlapHtml(r, b) {
  const code = cswFailCode(b.status_errors);
  const cause = CSW_FAIL_CAUSE[code] || 'other';
  const why = cswFailWhy(b.status_errors);
  const when = cswMsgTime(b.status_at || r.platform_timestamp || r.created_at || '');
  const plat = ((cswState().activeThread || {})._plat) || 'wa';
  const rec = r._recovered;
  // "Send again" only while the chat is open (fault 15) — the same reply
  // window the composer obeys; an unknown window offers nothing.
  const winOpen = ['open', 'closing'].includes(cswReplyWindow(cswState().activeThread).state);
  const verb = rec ? null : cswFailVerb(cause, b, plat, winOpen);
  const mediaUrl = b.image_url || b.file_url || ((Array.isArray(b.media) && b.media[0] && b.media[0].url) || '');
  return `<div class="csw-flap${rec ? ' csw-flap--done' : ''}" data-cause="${esc(cause)}" data-fmid="${esc(String(r.id || ''))}">`
    + `<span class="csw-flap__hinge" aria-hidden="true"></span>`
    + `<span class="csw-flap__why">${esc(why)}</span>`
    + `<span class="csw-flap__meta num">${code ? esc(code) + ' · ' : ''}${esc(when)}${rec ? ' · recovered ' + esc(cswMsgTime(rec.at)) : ''}</span>`
    + (verb ? `<button type="button" class="csw-flap__verb" data-flap-act="${esc(verb.act)}" data-flap-mid="${esc(String(r.id || ''))}" data-flap-text="${esc(String((b.text || b.caption) || '').slice(0, 900))}" data-flap-url="${esc(String(mediaUrl || ''))}">${esc(verb.label)}</button>` : '')
    + `</div>`;
}
// The faded twin under the replacement text: what failed, when, and
// that this message is the one that carried it.
function cswRecoveredFlapHtml(r) {
  const f = r._recoversFrom || {};
  return `<div class="csw-flap csw-flap--done csw-flap--twin" data-cause="${esc(f.cause || 'other')}" data-fmid="${esc(String(f.mid || ''))}">`
    + `<span class="csw-flap__hinge" aria-hidden="true"></span>`
    + `<span class="csw-flap__why">Recovered — ${esc(f.why || 'a send failed')}</span>`
    + `<span class="csw-flap__meta num">${f.code ? esc(f.code) + ' · ' : ''}failed ${esc(f.when || '')}</span></div>`;
}
// Pre-pass over the transcript rows: a failed outbound followed by a
// later outbound that did not fail is RECOVERED — the failed row keeps
// its flap (faded, no verb) and the later row gets the dated twin.
function cswMarkRecoveries(rows) {
  let open = null;
  (rows || []).forEach((r) => {
    const b = r.body || {};
    const out = (r.direction || '').toLowerCase() !== 'inbound';
    if (!out) return;
    const st = String(b.status || '').toLowerCase();
    delete r._recovered; delete r._recoversFrom;
    if (st === 'failed') { open = r; return; }
    if (open && st && st !== 'pending') {
      const ob = open.body || {};
      const code = cswFailCode(ob.status_errors);
      open._recovered = { at: r.platform_timestamp || r.created_at || '', mid: r.id };
      r._recoversFrom = { mid: open.id, code, cause: CSW_FAIL_CAUSE[code] || 'other',
        why: cswFailWhy(ob.status_errors), when: cswMsgTime(ob.status_at || open.platform_timestamp || open.created_at || '') };
      open = null;
    }
  });
}
// The verbs, bound once at the document (the transcript repaints).
async function cswFlapAct(btn) {
  const s = cswState();
  const cid = s.active;
  if (!cid || btn.disabled) return;
  const act = btn.dataset.flapAct;
  btn.disabled = true;
  const was = btn.textContent;
  btn.textContent = 'Sending…';
  // Adversary pass (2026-09-24): a flap send carries a duplicate-guard id
  // of its own, one per failed message and verb, kept through its own
  // retries. Without it api() rebuilt the id from the words — the SAME id
  // as the original send when that send timed out, and the contact guard
  // refuses an id it has already used, so the words WhatsApp refused could
  // never go again. WhatsApp's failure receipt proves the first try is dead.
  const flapMid = String(btn.dataset.flapMid || '');
  const flapKey = flapMid ? `eon.contact.action.flap.${act}.${flapMid}` : '';
  // Fable vet (27 Sep): the id comes from cswActionId, which has a fallback
  // for older phones without crypto.randomUUID; there the tap used to throw
  // and nothing was sent.
  const flapId = () => {
    let id = null;
    if (flapKey) { try { id = sessionStorage.getItem(flapKey); } catch (_) {} }
    if (!id) { id = cswActionId(); if (flapKey) { try { sessionStorage.setItem(flapKey, id); } catch (_) {} } }
    return id;
  };
  const flapSeat = flapKey ? { _contactActionKey: flapKey } : {};   // api() lets the id go once confirmed
  try {
    if (act === 'pickup') {
      await api(`/api/cs/threads/${encodeURIComponent(cid)}/pickup`, { method: 'POST' }); cswPickupRemember(cid);
    } else if (act === 'words' || act === 'again') {
      // 'again' (fault 15) re-sends a failed text's own words — the same
      // guarded door as the words of a failed photo.
      const text = String(btn.dataset.flapText || '').trim();
      if (!text) throw new Error('nothing to send');
      await cswSendApi(`/api/cs/threads/${encodeURIComponent(cid)}/send`, { method: 'POST', body: JSON.stringify({ text, contact_action_id: flapId() }), ...flapSeat });
    } else if (act === 'link') {
      const url = String(btn.dataset.flapUrl || '').trim();
      if (!/^https?:\/\//.test(url)) throw new Error('no link to send');
      const cap = String(btn.dataset.flapText || '').trim();
      await cswSendApi(`/api/cs/threads/${encodeURIComponent(cid)}/send`, { method: 'POST', body: JSON.stringify({ text: cap ? cap + '\n' + url : url, contact_action_id: flapId() }), ...flapSeat });
    } else throw new Error('unknown verb');
    btn.textContent = 'Sent';
    cswMarkReplied(cid);
    setTimeout(() => cswLoadTranscript(cid, true), 1400);
  } catch (e) {
    const msg = String((e && e.message) || e || '');
    const awaiting = msg.includes('template_awaiting_approval');
    // The Pick-up button gives no_inbound_text its own short label; this
    // one used to lack that branch entirely, so when the toast was added
    // it printed the bare word. A voice note or an uncaptioned photo as
    // the customer's last message reaches exactly that path.
    const noText = msg.includes('no_inbound_text');
    // Vet 2026-09-24 (faults 6/20): no answer is not "did not go" — the
    // message may be with the customer already. A server that wrote its
    // own refusal ("This account isn't connected for sending yet.") did
    // answer: that one did not go (adversary pass). The key reads the
    // toast's own sentence, so one tap never gets two answers: a lost
    // answer, #3548's 409 "Not sure it went", a 502 with no WhatsApp code
    // and WhatsApp's "something went wrong" codes all read "Not sure it
    // went" on both (second adversary on round 2).
    const said = cswSayWhy(e, { send: true });
    const unsure = /\bnot sure\b/i.test(said);
    btn.textContent = awaiting ? 'Template awaiting approval'
      : noText ? 'No customer text to pick up' : unsure ? 'Not sure it went' : 'Did not go';
    // Same as the pickup button: the reason is a sentence, the button is
    // a button. Toast the sentence — never a machine word, never a path.
    const say = cswSayableRefusal(msg);
    if (!awaiting && say) cswToast(said, true);
    if (!awaiting) setTimeout(() => { btn.disabled = false; btn.textContent = was; }, 2500);
  }
}
function cswBindFlapVerbs() {
  if (cswBindFlapVerbs._bound) return;
  cswBindFlapVerbs._bound = true;
  document.addEventListener('click', (ev) => {
    const btn = ev.target && ev.target.closest && ev.target.closest('.csw-flap__verb');
    if (!btn) return;
    ev.preventDefault(); ev.stopPropagation();
    cswFlapAct(btn);
  });
}

// ── L4 (2026-08-30) · the jump-to-latest pill tells the truth ─────────
// ONE threshold — the same 120px the transcript's auto-scroll uses
// (cswLoadTranscript's nearBottom) — so the moment auto-scroll stops
// firing is the exact moment the pill appears: no 120-300px dead band
// where a new message lands with no movement and no pill. Called from
// the scroll listener AND from the tail of every transcript paint,
// because appending a row below the viewport changes scrollHeight but
// fires no scroll event (the scroll-only sync froze the pill's state).
// The count is plain text IN the button ('3 new') — no badge element,
// no new keyframe; s._newBelow is fed by the reconciler's fresh-inbound
// inserts and zeroed whenever the rep is at (or is taken to) the bottom.
function cswSyncScrollPill(atBottom) {
  const scr = document.getElementById('csw-script');
  const sb = document.getElementById('csw-scrollbtm');
  if (!scr || !sb) return;
  const s = cswState();
  const away = atBottom ? false
    : (scr.scrollHeight - scr.scrollTop - scr.clientHeight) >= 120;
  if (!away) s._newBelow = 0;
  const n = s._newBelow || 0;
  const label = n > 0 ? n + ' new' : '↓';
  if (sb.textContent !== label) sb.textContent = label;
  sb.hidden = !away;
}
// Display name — a raw phone as a "name" looks unfinished; format it.
function cswDisplayName(t) {
  const n = (t.customer_name || '').trim();
  const sid = String(t.platform_sender_id || '');
  const isNumeric = (s) => /^\+?\d{7,}$/.test(s.replace(/\s/g, ''));
  // A real (non-numeric) name wins for any channel.
  if (n && n !== sid && !isNumeric(n)) return n;
  // Instagram: platform_sender_id is an IGSID, NOT a phone — never phone-format
  // it (that rendered a fake "phone number"). Show a non-numeric name if we have
  // one (e.g. the enriched @handle), else 'Customer'.
  if ((t.channel || '') === 'instagram') {
    return (n && !isNumeric(n)) ? n : 'Customer';
  }
  // WhatsApp: pretty-print the phone.
  const d = (n || sid).replace(/[^0-9]/g, '');
  if (d.length >= 10) {
    const local = d.startsWith('234') ? '0' + d.slice(3) : d;
    return local.replace(/(\d{4})(\d{3})(\d{0,4}).*/, '$1 $2 $3').trim() || (n || 'Customer');
  }
  return n || 'Customer';
}

// ── 09 · THE WHALE ROW (2026-08-29) ─────────────────────────────────
// The order count off the same customer-card truth the header line reads.
// Returns 0 when the row carries no explicit count — the honest answer,
// and the one that keeps the list unchanged until the field lands.
function cswWhaleOrders(t) {
  const n = Number(t && (t.order_count != null ? t.order_count : t.lifetime_orders));
  return isFinite(n) && n >= 2 ? n : 0;
}

function cswWhaleChip(t) {
  const n = cswWhaleOrders(t);
  if (!n) return '';
  // Years is a second fact, not a required one: a row that knows the
  // count but not the tenure says the count.
  const yrs = Number(t.customer_years);
  const tail = isFinite(yrs) && yrs >= 1 ? ` · ${Math.floor(yrs)}yr${Math.floor(yrs) === 1 ? '' : 's'}` : '';
  return `<span class="crd-whalechip" title="${n} paid orders${tail ? ' · with us ' + tail.replace(' · ', '') : ''}">`
    + `${n} orders${esc(tail)}</span>`;
}

// ── 03 · THE QUOTE CLOCK, on a queue row ────────────────────────────
// The same fact the thread's ribbon prints. The row's ORDER is the
// feed's business and stays the feed's business — this only says why.
function cswQuoteChip(t) {
  const q = cswQuoteFacts(t);
  if (!q) return '';
  const age = cswQuoteAge(q.minutes);
  return `<span class="crd-quotechip" title="Quoted and not answered">${esc(cswNgn(q.amount))}${age ? ' · ' + esc(age) : ''}</span>`;
}

// ── the 24h window, on a queue row (L2, 2026-08-30) ─────────────────
// Shown ONLY while the free-reply window is still open, closing within
// 6 hours, on a thread that still owes a reply — ~12 rows today, never
// 717. The 80 shut rows say NOTHING here: the composer banner already
// owns the closed state, and a 'closed' chip on most of the book would
// be the wall-of-red mistake in a second colour. CSS is the orphaned
// .csw-win--soon rule (cs.css, "24h WhatsApp window — thread-row chip")
// on the .csw-badge base — dark since 8ed6ba7cb, no new CSS.
function cswWindowChip(t) {
  if (!t || !t._waiting) return '';
  // THE CARD SAYS THE WINDOW STATE IN WORDS (2026-09-06). The head dial
  // said 'closed' and 'shut'; the card only ever said 'Nh left' while the
  // window was open, so a rep scanning the inbox could not tell a reply
  // that still needs no template from one that needs the pick-up. Same
  // two words as the dial, on waiting rows only, in the fact-chip budget.
  if (t.window_open === false) {
    // ...and only inside recovery. The 2026-08-30 budget pin feared the
    // wall of 717 shut rows; rows past the recovery bound belong to the
    // Recovery lens and say nothing here, exactly as before.
    if ((t._waitm || 0) > CSW_RECOVERY_MIN) return '';
    return cswPlatform(t) === 'ig'
      ? `<span class="csw-badge csw-win--closed" title="Instagram 24h window shut — the customer has to write first">window shut</span>`
      : `<span class="csw-badge csw-win--closed" title="WhatsApp 24h window closed — use an approved template; only the customer's reply reopens free-form messaging">window closed</span>`;
  }
  if (t.window_open !== true) return '';
  const m = cswWindowMins(t);
  if (m == null || m <= 0 || m >= 360) return '';
  const label = m < 60 ? m + 'm left' : Math.floor(m / 60) + 'h left';
  return `<span class="csw-badge csw-win--soon" title="Free reply window is closing — answer now and it needs no template">${esc(label)}</span>`;
}

// ── THE ROW'S CHIP BUDGET (L2, 2026-08-30) ──────────────────────────
// Originally four chip sources competed for one 360px row: status (states the rep
// acts on), quote (money), window (a deadline), whale (history). Four
// locally-correct additions are the wall-of-red failure in chip form,
// so ONE composer owns the whole line on all three hosts (classic row,
// house ticket, dense row):
//   · STATUS chips always render — each is a live state another lane
//     depends on (who holds the ball, a promise owed, a ready
//     follow-up). A state is never dropped.
//   · FACT chips rank money > window > whale — the quote IS the naira
//     at stake, a closing window is actionable this hour, a whale's
//     history keeps until tomorrow — and at most TWO ride one row.
//   · The LINE carries a hard cap of 4 chips (#2755: a data-driven
//     strip ships its structural bound in the same diff; the chips
//     line keeps its 2026-08-08 wrap-under-the-preview ruling, so the
//     bound is the cap, not a scroller). When states fill the row,
//     facts yield from the tail: whale first, then window, then quote.
// 2026-09-06: the reply window now has a permanent line below these chips;
// it cannot be crowded out by status. Money and history retain their budget.
function cswRowChips(t) {
  if (!t) return '';
  const status = cswStatusChips(t);
  const nStatus = (status.match(/<span/g) || []).length;
  const room = Math.max(0, Math.min(2, 4 - nStatus));
  const extras = [!cswHouse() ? cswWindowChip(t) : '', cswQuoteChip(t), cswWhaleChip(t)]
    .filter(Boolean).slice(0, room);
  return status + extras.join('');
}

function cswRenderThreadList() {
  const s = cswState();
  const el = document.getElementById('csw-threads');
  if (!el) return;
  // The keyboard's ring outlives this paint (cswRingBack, #3541 adversary pass).
  if (document.activeElement?.matches?.('#csw-threads [data-open], #csw-threads .csw-thread[data-cid]')) cswRingBack(el, document.activeElement);
  const actionFocus = document.activeElement?.closest?.('#csw-actions [data-desk-action]')?.dataset.deskAction;
  const keepActions = () => cswPaintSearchActions(s.search || '', actionFocus);
  // The drain instrument stays live on EVERY house path — lens, team,
  // search — not only the queue render (vet 2026-08-08).
  if (cswHouse()) cswPaintHouseHead();
  // TEAM lens — staff↔staff @-mention threads (DM style), in the SAME shell.
  // Distinct from the customer thread list: no SLA/wait/route, no EON row, no
  // customer PII. Reuses the csw-thread row markup for visual parity.
  if (s.filter === 'team') cswPaintNowShelf();   // the shelf paints on this lens too (verifier, 2026-09-04)
  if (s.filter === 'team') { cswRenderTeamList(el); keepActions(); return; }
  // HOUSE floor 3, the fold (WS-B2, 2026-08-07): five tabs, the drawer's
  // lane views and the Priority board collapse into ONE wait-sorted queue —
  // the shift book rendered as tickets (reason line, live wait, one-tap
  // claim). Search / label / department lenses fall through to the classic
  // list below so nothing is stranded; Team keeps its own lens. Classic
  // renders exactly as before — this branch exists only behind cswHouse().
  // R2 (2026-08-08): the lens row — All / Mine / Recovery are one tap
  // under house (s.houseLens maps straight onto the classic filter the
  // list below already honors), and "Queue" returns to the book. The
  // search seam stays: search/label/department still fall through.
  if (cswHouse() && !(s.search || '').trim() && !s.labelFilter && !s.queueFilter) {
    // R2: a picked lens (All / Mine / Recovery) falls through to the
    // classic list below — same as search — and "Queue" comes back here.
    if (!s.houseLens) {
      cswPaintNowShelf();   // the shelf paints on the house queue too (verifier, 2026-09-04)
      cswRenderHouseQueue(el);
      keepActions();
      return;
    }
  }
  // PRIORITY lens (mig 545) — the per-shift worklist that DRAINS: server-
  // ranked cards (complaints, payment claims, paid-order questions,
  // stalled buyers, long waits), one tap to open, claim or clear.
  if (s.filter === 'priority') cswPaintNowShelf();   // the shelf paints on this lens too (verifier, 2026-09-04)
  if (s.filter === 'priority') { cswRenderPriorityList(el); keepActions(); return; }
  // Vet 2026-08-08 (blocking): the classic path reconciles .csw-thread
  // rows only — the house queue's furniture (tickets, working strip,
  // note) survived a lens/search fall-through UNDERNEATH the list, with
  // LIVE claim handlers: a rep could claim a frozen ghost from inside
  // the Mine lens. Wipe the queue's nodes before the list builds. Off
  // house (or with no prior queue paint) this matches nothing.
  el.querySelectorAll('.csw-hticket, .csw-hworking, .csw-hnote, .csw-hiddenfoot')
    .forEach(n => n.remove());
  // Lane P: the response clock is the queue's furniture too — same wipe,
  // same reason (a lens fall-through must not leave it under the list).
  el.querySelectorAll('.csw-clock').forEach(n => n.remove());
  const myEmail = cswMyEmail();
  const q = (s.search || '').trim().toLowerCase();
  let vis = s.threads.filter(t => {
    // B4 rail stubs exist only so a cross-channel jump can render its
    // header — they carry no lane truth and never appear in the list.
    if (t._also_stub) return false;
    if (q) {
      const hay = `${t.customer_name || ''} ${cswDisplayName(t)} ${t.last_message_text || ''} ${t.platform_sender_id || ''} ${(t.labels || []).join(' ')}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (s.labelFilter && !(t.labels || []).includes(s.labelFilter)) return false;
    // Department queue filter (cs_thread_routing) — unrouted threads
    // only show under All.
    if (s.queueFilter && ((t.route && t.route.queue) || null) !== s.queueFilter) return false;
    // Hide resolved threads UNLESS the customer wrote again (unread) → re-opens.
    if (t.cs_status === 'resolved' && !t.unread) return false;
    // Lane predicates (Wave 2): the fresh queue and the win-back tail are
    // separate TABS now — each tab's count matches exactly what it shows.
    if (s.filter === 'awaiting') return (!!t._waiting && (t._waitm || 0) <= CSW_RECOVERY_MIN) || (!t._waiting && !!t._promise);
    if (s.filter === 'recovery') return !!t._waiting && (t._waitm || 0) > CSW_RECOVERY_MIN;
    if (s.filter === 'mine') return myEmail && (t.assigned_rep_email || '').toLowerCase() === myEmail && t.cs_status !== 'resolved';
    return true;   // 'all' (default) — every customer thread incl. former comment/story
  });
  // Awaiting-reply tab: RECENT first (operator 2026-07-26 — the old
  // longest-first "triage order" put week-dead threads on top and buried
  // the recoverable ones: 91 of 124 waits were >7 days old). Research:
  // <1h replies convert 24.4%, never-answered 0% — the freshest wait is
  // the most winnable. Waits past ~72h fold below (counted, never
  // hidden). Everywhere else: pinned first, then newest-first.
  // Wave E buyer-priority: within Needs reply, a PAYING customer's wait
  // outranks — GOLD/VIP/returning first, then the lane's own order.
  // 'concierge' (mig 645) outranks gold — a whale waiting must not sort BELOW
  // a gold customer just because the band is newer than this comparator.
  const _buyer = (t) => (t.tier === 'concierge' || t.tier === 'gold'
    || t.tier === 'vip' || t.is_returning || t.returning_customer) ? 1 : 0;
  vis.sort((a, b) => {
    if (s.filter === 'awaiting') {
      const d = _buyer(b) - _buyer(a);
      if (d) return d;
      // A promise thread has no customer wait (we spoke last) — its
      // promise timer is its wait, so the lane's freshest-first order
      // holds for it too.
      const w = (x) => x._waiting ? (x._waitm || 0) : (x._promiseM || 0);
      return w(a) - w(b);
    }
    // Recovery: newest-abandoned first — the most recently lost customer
    // is the most winnable win-back.
    if (s.filter === 'recovery') return (a._waitm || 0) - (b._waitm || 0);
    const pa = cswIsPinned(a.zoko_customer_id) ? 1 : 0, pb = cswIsPinned(b.zoko_customer_id) ? 1 : 0;
    if (pa !== pb) return pb - pa;
    return (b.last_message_at || '').localeCompare(a.last_message_at || '');
  });
  // The old in-tab fold became the Recovery TAB (Wave 2) — the fresh lane
  // keeps a one-line link at the bottom so the tail stays discoverable.
  let pastRows = [];
  if (s.filter === 'awaiting') {
    pastRows = vis.filter(t => (t._waitm || 0) > CSW_RECOVERY_MIN);
    if (pastRows.length) vis = vis.filter(t => (t._waitm || 0) <= CSW_RECOVERY_MIN);
  }
  // Classic keeps the private EON row above customer conversations.
  // The house desk uses its EON tab; cswEonRowHtml omits the duplicate row
  // for owners as well as reps, across every lens and search result.
  const _repDesk = !!(typeof sosState !== 'undefined' && sosState && sosState.cur);
  const eonRow = (cswHouse() && _repDesk) ? '' : cswEonRowHtml();
  // Awaiting extras (2026-07-26): the past-recovery fold + the automated
  // sends section render even when the live list is empty — an empty
  // "Nothing waiting" that hides 91 folded threads would be a lie. So would
  // one that hides an automated history it could not read.
  if (s.filter === 'awaiting') cswFetchAutoPending();
  const recoveryTail = s.filter === 'awaiting'
    && (s.threads || []).some(t => t._waiting && (t._waitm || 0) > CSW_RECOVERY_MIN);
  const hasAwaitExtras = s.filter === 'awaiting'
    && (recoveryTail || (s.autoPending || []).length
      || ['stale', 'unavailable'].includes(s._autoPendingStatus));
  if (!vis.length && !hasAwaitExtras) {
    // Designed empty states (TL-09): an inbox at zero is usually GOOD news
    // for the awaiting lens and a filter mistake everywhere else — say which,
    // and offer the way out instead of a bare "No x threads (7d)".
    const EMPTY = {
      awaiting: ['Nothing waiting', 'Every customer has been answered. Nice.'],
      recovery: ['Recovery is empty', 'No quiet threads older than 3 days — the tail is clear.'],
      mine: ['Nothing assigned to you', 'Threads you claim or get routed land here.'],
      team: ['No team messages', 'Mention a teammate with @ in any chat to start one.'],
      all: ['No conversations in the last 7 days', 'New WhatsApp and Instagram messages appear here automatically.'],
    };
    const [eTitle, eSub] = EMPTY[s.filter] || EMPTY.all;
    const clearable = !!(s.search || s.labelFilter || s.queueFilter);
    el.innerHTML = eonRow + `<div class="csw-empty">
      <div class="csw-empty__m">${s.filter === 'awaiting' ? '✓' : '○'}</div>
      <div class="csw-empty__t">${esc(eTitle)}</div>
      <div class="csw-empty__s">${esc(clearable ? 'No match for the filters you have on.' : eSub)}</div>
      ${clearable ? '<button class="csw-empty__b" id="csw-empty-clear" type="button">Clear filters</button>' : ''}
    </div>`;
    document.getElementById('csw-empty-clear')?.addEventListener('click', () => {
      const st = cswState();
      st.search = ''; st.labelFilter = null; st.queueFilter = null;
      const sb = document.getElementById('csw-searchbar');
      if (sb) sb.value = '';
      document.getElementById('csw-gsec')?.remove();
      cswRenderThreadList();
    });
    cswBindEonRow(el);
    keepActions();
    return;
  }
  // Keyed reconciliation (2026-07-25, FL-01): the list used to rebuild its
  // whole innerHTML + rebind every touch handler on each 10-30s poll — a
  // nudge mid-swipe cancelled the gesture and scroll drifted. Rows are now
  // diffed by data-cid: unchanged rows are left ALONE (their handlers and
  // in-flight gestures survive), changed rows patch their inner markup,
  // new rows are created, departed rows are removed.
  const rowHtml = (t) => {
    const name = cswHouseName(t);
    const pres = t._plat;  // wa | ig → a small dot on the avatar corner (the only channel cue)
    // The TIME slot carries the triage signal: for unread threads it shows
    // how long the customer has waited, coloured amber ≥10m / red ≥30m;
    // otherwise it's the normal last-message time. No pills.
    // ONE right-side signal in the TIME slot (research: never stack three
    // metadata elements). If the customer is waiting (unreplied OR unread), the
    // time shows HOW LONG they've waited — amber ≥10m, red ≥30m; otherwise it's
    // the last-message time. The separate wait "pill" is gone: the time IS the
    // SLA. Right column = time (top) + green dot (bottom). The dot means "a
    // reply is owed" (t._waiting) — the number inside, when >1, is how many
    // of the customer's messages you haven't seen. A replied thread never
    // shows the dot (2026-07-25: it used to key on per-rep unread and lit
    // on threads another rep had already answered).
    let timeLabel = t.last_message_at ? formatRelativeShort(t.last_message_at) : '';
    let timeCls = '';
    // L2 (2026-08-30): keyed on _waiting, not unreplied — a sign-off
    // ("thanks", 🙏) is a conversation that ENDED, and 38 of the 144 red
    // rows were exactly that. _waiting is the same server-computed flag
    // the tabs and the green dot already key on.
    const waitMin = t._waiting ? (t._waitm || 0)
      : (t.unread && t.last_inbound_at ? Math.round((Date.now() - new Date(t.last_inbound_at)) / 60000) : 0);
    if (waitMin >= 1) {
      timeLabel = waitMin < 60 ? waitMin + 'm' : (waitMin < 1440 ? Math.floor(waitMin / 60) + 'h' : Math.floor(waitMin / 1440) + 'd');
      // Calm escalation (verified by render): most waiting times stay neutral/
      // amber; RED is reserved for a real 2h+ breach so the right column isn't a
      // wall of red competing with the unread badges. The 10-min CS SLA still
      // shows via the Awaiting filter + its oldest-wait badge + the red breach bar.
      timeCls = waitMin >= 120 ? 'csw-time--late' : (waitMin >= 30 ? 'csw-time--soon' : '');
      // HOUSE: any real wait carries csw-time--wait — the house layer sizes
      // it into the row's loudest number. No classic rule matches it.
      timeCls += ' csw-time--wait';
    }
    const prev = cswPreview(t);
    const tag = t._ch === 'comment' ? 'Comment · ' : (t._ch === 'story' ? 'Story · ' : '');
    // Bright title for a waiting customer; the left accent bar is reserved for a
    // RED SLA breach (≥2h) only — the single urgent cue (research: limit
    // dark-mode accents; convey depth with shade, not a bar on every row).
    const wm = t._waitm || 0;
    const urCls = t._waiting ? ('unreplied' + (wm >= 120 ? ' csw-thread--w120' : (wm >= 30 ? ' csw-thread--w30' : ''))) : '';
    // Status chips (stylist offer, reopened, promise, follow-up, FAQ) used
    // to ride the preview's own flex line and ate it — measured live
    // 2026-08-08 at a 606px pane: 247px of chips left 221px of words
    // ("the labels are blocking the awaiting-reply" — operator, same day).
    // They now render on their OWN wrap line under the preview
    // (.csw-thread__chips); the preview keeps the full width and only the
    // unread count stays on its line. Tap handlers are delegation via
    // closest() in cswBindThreadRow — position inside the row is free.
    const chips = cswRowChips(t);   // one composer owns the chip budget (L2)
    // 09 — THE WHALE ROW. A second order is the single strongest thing a
    // rep can know before they type, and the row said nothing about it.
    // Keyed on an explicit count from the row's own payload: no count →
    // no class, and the row is byte-identical to the one before this pass
    // (is_returning is deliberately NOT used — 45% of this book returns,
    // and a gold edge on half the inbox is decoration, not a signal).
    const whale = cswWhaleOrders(t) ? ' crd-whale' : '';
    return `<button class="csw-thread ${s.active === t.zoko_customer_id ? 'on' : ''} ${t.unread ? 'unread' : ''} ${urCls}${whale}" data-cid="${esc(t.zoko_customer_id)}">
      ${cswAvatar(name, { url: t.avatar_url ? cswImgSrc(t.avatar_url) : '', seed: t.zoko_customer_id || t.platform_sender_id, pres, hot: t._hot, vip: t.is_returning || t.returning_customer })}
      <div class="csw-thread__main">
        <div class="csw-thread__top"><div class="csw-thread__name">${esc(name)}${cswTierChip(t.tier)}${t._waiting && t.message_count === 1 ? '<span class="csw-firstmsg">first message</span>' : ''}</div><div class="csw-thread__time ${timeCls}">${cswIsPinned(t.zoko_customer_id) ? '<span class="csw-pinch">📌</span> ' : ''}${esc(timeLabel)}</div></div>
        <div class="csw-thread__bot">
          <div class="csw-thread__prev ${prev.muted ? 'muted' : ''}">${tag ? `<span class="csw-prevtag">${esc(tag)}</span>` : ''}${prev.html}</div>
          ${t.unread_count >= 1 ? `<span class="csw-unread csw-unread--n">${t.unread_count > 99 ? '99+' : t.unread_count}</span>` : ''}
        </div>
        ${chips ? `<div class="csw-thread__chips">${chips}</div>` : ''}
        ${cswServiceMeta(t)}
      </div>
    </button>`;
  };

  // Keep the Classic entry once, first. House has a dedicated EON tab.
  let eonEl = el.querySelector('.csw-thread--eon');
  if (eonEl && !eonRow) {
    eonEl.remove();
    eonEl = null;
  }
  if (!eonEl && eonRow) {
    el.insertAdjacentHTML('afterbegin', eonRow);
    eonEl = el.querySelector('.csw-thread--eon');
    cswBindEonRow(el);
  }
  el.querySelector('.csw-empty')?.remove();

  const seen = new Set();
  let anchor = eonEl;                      // null starts at the queue's first row
  vis.forEach(t => {
    const cid = String(t.zoko_customer_id);
    seen.add(cid);
    // Signature = a hash of the ACTUAL RENDERED MARKUP, not a list of source
    // fields (2026-07-25 regression fix). The row's time label is derived
    // from Date.now() — "26m", "2h" — so a field-list signature never
    // changed and the label FROZE at first paint: a thread reading "26m"
    // could really be 6 hours old, which is exactly what Daniel saw as
    // "dates jumbled". Hashing the output means anything that renders
    // differently repaints, with no field list to keep in sync.
    const html = rowHtml(t);
    const sig = cswHash(html) + ':' + html.length;
    let row = el.querySelector(`.csw-thread[data-cid="${CSS.escape(cid)}"]:not(.csw-past-row)`);
    if (!row) {
      if (anchor) anchor.insertAdjacentHTML('afterend', html);
      else el.insertAdjacentHTML('afterbegin', html);
      row = anchor ? anchor.nextElementSibling : el.firstElementChild;
      row.dataset.sig = sig;
      cswBindThreadRow(row);
    } else {
      if (row.dataset.sig !== sig) {
        const fresh = document.createElement('template');
        fresh.innerHTML = html.trim();
        const nb = fresh.content.firstElementChild;
        row.className = nb.className;           // state classes (unread/on/SLA)
        row.innerHTML = nb.innerHTML;           // content only — handlers live
        row.dataset.sig = sig;
      }
      if (row.previousElementSibling !== anchor) el.insertBefore(row, anchor ? anchor.nextSibling : el.firstChild);
    }
    anchor = row;
  });
  // Drop rows that left the visible set (resolved, filtered, aged out).
  // Fold/automated rows live in their own rebuilt containers — excluded.
  el.querySelectorAll('.csw-thread:not(.csw-thread--eon):not(.csw-past-row)').forEach(r => {
    if (!seen.has(String(r.dataset.cid))) {
      // HOUSE: a cleared thread tears off the rail — the room's one moving
      // part (motion = information leaving). Reduced-motion and every other
      // skin: instant removal, exactly as before.
      if (cswHouse() && !(window.EONHouse && window.EONHouse.reduced) && !r.classList.contains('hs-tearing')) {
        r.classList.add('hs-tearing');
        setTimeout(() => r.remove(), 460);
      } else if (!r.classList.contains('hs-tearing')) {
        r.remove();
      }
    }
  });
  cswRenderAwaitExtras(el, pastRows, rowHtml);
  // AIRTIGHT honesty footer (2026-07-25): replied threads beyond the cap
  // are COUNTED, never silently vanished (awaiting-reply threads are never
  // capped at all). Search reaches the hidden ones server-side.
  let hfoot = el.querySelector('.csw-hiddenfoot');
  if (s.hiddenReplied > 0 && !q) {
    const txt = `${s.hiddenReplied} quieter conversation${s.hiddenReplied === 1 ? '' : 's'} not shown — search finds them`;
    if (!hfoot) {
      el.insertAdjacentHTML('beforeend',
        `<div class="csw-hiddenfoot" style="padding:10px 14px;font-size:12px;color:var(--mute,#8a9478);text-align:center;">${esc(txt)}</div>`);
    } else { hfoot.textContent = txt; el.appendChild(hfoot); }
  } else if (hfoot) hfoot.remove();
  // Motion pass 2 (operator: "look and feel like an advanced app"): rows
  // enter with a stagger on the FIRST paint of a desk visit only — quiet
  // polls and keyed patches never re-animate under the thumb.
  if (!el._entered && window.eonMotion) {
    el._entered = true;
    window.eonMotion.stagger(el.querySelectorAll('.csw-thread:not(.csw-thread--eon)'), { step: 14, dur: 240, dy: 6 });
  }
  // B1 — the shelf rides the same paint as the list beneath it, so the
  // ball and the ages on it can never be a minute behind the rows they
  // sit over. Costs one pass over threads the painter already holds.
  cswPaintNowShelf();
  keepActions();
}

// ── Awaiting-tab extras (2026-07-26) ─────────────────────────────────
// Two rebuilt-each-render blocks BELOW the keyed thread list:
//  * past-recovery fold — waits >72h, collapsed, counted, tap to open;
//  * latest automated-message history — limited records, not reply debt,
//    showing recorded message text (operator: "there
//    should be a section ... so we see what the customer receive").
function cswRenderAwaitExtras(el, pastRows, rowHtml) {
  const s = cswState();
  el.querySelector('.csw-pastfold')?.remove();
  el.querySelector('.csw-autosec')?.remove();
  // Operator PDF 2026-07-27: the Recovery pointer stacked one copy per
  // poll — this cleanup list never included it. Remove ALL (the bug left
  // several behind on long-open desks).
  el.querySelectorAll('.csw-recovery-link').forEach(x => x.remove());
  if (s.filter !== 'awaiting') return;
  // Wave 2: the >72h tail is the Recovery TAB now — the fresh lane keeps
  // a one-line pointer so the tail stays one tap away, never a hidden fold.
  const rec = (s.threads || []).filter(t => t._waiting && (t._waitm || 0) > CSW_RECOVERY_MIN);
  if (rec.length) {
    const oldest = Math.max(...rec.map(t => t._waitm || 0));
    const link = document.createElement('button');
    link.type = 'button';
    link.className = 'csw-recovery-link';
    link.innerHTML = `▸ Recovery — ${rec.length} quiet thread${rec.length === 1 ? '' : 's'}, oldest ${Math.floor(oldest / 1440)}d · <b>open the lane</b>`;
    link.addEventListener('click', () => {
      s.lensPicked = true;          // a hand-opened lane — the shift landing must not yank it (Opus vet F9)
      s.filter = 'recovery';
      // Vet 2026-08-09: this link only ever drove the CLASSIC filter row.
      // It was unreachable under house until the Needs-reply lens key
      // landed (nothing else set filter='awaiting' there), and the moment
      // it became reachable the lens row kept saying "Needs reply" while
      // the list below showed Recovery. Drive whichever row is mounted.
      if (cswHouse()) s.houseLens = 'recovery';
      document.querySelectorAll('#csw-filters .csw-filter').forEach(x => x.classList.toggle('on', x.dataset.f === 'recovery'));
      document.querySelectorAll('#csw-hlens .csw-hlens__k').forEach(x => {
        const on = x.dataset.hl === 'recovery';
        x.classList.toggle('on', on);
        x.setAttribute('aria-selected', String(on));
      });
      cswRenderThreadList();
    });
    el.appendChild(link);
  }
  const auto = s.autoPending || [];
  if (auto.length || ['stale','unavailable'].includes(s._autoPendingStatus)) {
    const sec = document.createElement('div');
    sec.className = 'csw-autosec';
    const age = (h) => h == null ? '' : (h < 1 ? '<1h' : (h < 48 ? Math.round(h) + 'h' : Math.round(h / 24) + 'd'));
    sec.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:space-between;padding:10px 14px 6px;border-top:1px solid var(--line,#20241c);">
        <span style="font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:var(--mute,#8a9478);">Automated history · ${Math.min(auto.length, 6)} shown</span>
        <button class="csw-mini" id="csw-autosec-all" type="button" style="font-size:11.5px;">Open automated history</button>
      </div>
      <p class="csw-abrate__sub">${s._autoPendingStatus === 'stale' ? 'Refresh failed. Previous records shown. ' : s._autoPendingStatus === 'unavailable' ? 'History unavailable. Open history to retry. ' : ''}Last 14 days · up to 30 records requested · up to 6 shown. Only threads whose latest message is automated. Later customer or staff messages remove a thread. Access limits apply; this reading may be incomplete. Open history for a separate 30-day reading, up to 100 records.</p>
      ${auto.slice(0, 6).map(t => {
        const cid = t.zoko_customer_id || t.platform_sender_id || '';
        return `<button class="csw-autosec__row" data-cid="${esc(cid)}" type="button" style="display:block;width:100%;text-align:left;padding:8px 14px;background:none;border:0;border-bottom:1px solid var(--line-soft,#1a1d16);cursor:pointer;">
          <span style="display:flex;justify-content:space-between;gap:8px;">
            <b style="font-size:13px;color:var(--text,#e8e8ea);">${esc(t.customer_name || 'Customer')}</b>
            <span style="font-size:11.5px;color:var(--mute,#8a9478);white-space:nowrap;">auto · ${age(t.hours_since_send)}</span>
          </span>
          <span style="display:block;font-size:12.5px;color:var(--mute,#a9b0a0);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${esc((t.last_text || '').slice(0, 110)) || 'Automated message'}</span>
        </button>`;
      }).join('')}`;
    sec.querySelectorAll('.csw-autosec__row').forEach(b => {
      b.addEventListener('click', () => { if (b.dataset.cid) cswJumpToThread(b.dataset.cid); });
    });
    sec.querySelector('#csw-autosec-all')?.addEventListener('click', () => {
      s.mode = 'automated'; cswRenderBody();
    });
    el.appendChild(sec);
  }
}

// Throttled fetch behind the Awaiting tab's automated section. Re-renders
// only when the list actually changed, so the poll can't loop the renderer.
async function cswFetchAutoPending() {
  const s = cswState();
  if (s._autoPendingBusy) return;
  if (s._autoPendingAt && Date.now() - s._autoPendingAt < 60000) return;
  // A failed read waits the same minute before it asks again.
  if (s._autoPendingFailedAt && Date.now() - s._autoPendingFailedAt < 60000) return;
  s._autoPendingBusy = true;
  try {
    const d = await api('/api/cs/automated-pending?limit=30&days=14');
    if (d?.ok !== true || !Array.isArray(d.threads)) throw new Error('History unavailable');
    const list = d.threads;
    const previousStatus = s._autoPendingStatus;
    s._autoPendingStatus = 'fresh';
    s._autoPendingFailedAt = 0;
    const sig = list.map(t => (t.zoko_customer_id || t.platform_sender_id) + ':' + (t.hours_since_send || 0)).join('|');
    s._autoPendingAt = Date.now();
    if (sig !== s._autoPendingSig || previousStatus !== 'fresh') {
      s._autoPendingSig = sig; s.autoPending = list;
      cswRenderThreadList();
    }
  } catch (_) {
    s._autoPendingFailedAt = Date.now();
    const status = s._autoPendingAt || s.autoPending ? 'stale' : 'unavailable';
    if (s._autoPendingStatus !== status) { s._autoPendingStatus = status; cswRenderThreadList(); }
  }
  finally { s._autoPendingBusy = false; }
}

// Bind ONE thread row (tap + swipe). Called once per row at creation — a
// patched row keeps its listeners, which is the whole point of the reconciler.
function cswBindThreadRow(b) {
  {
    const cid = b.dataset.cid;
    let x0 = null, y0 = null, swiped = false;
    b.addEventListener('click', (e) => {
      if (swiped) { e.stopImmediatePropagation(); e.preventDefault(); return; }
      // Styling Desk chip: review-and-send sheet, not a thread open.
      const oc = e.target.closest && e.target.closest('.csw-styloffer');
      if (oc) {
        const p = _cswStylOffers.get(oc.dataset.k || '');
        if (p) { e.stopPropagation(); cswStylOfferSheet(p); return; }
      }
      // Wave E follow-up chip: one tap = the pickup send (its route holds
      // every gate); the row click stays a thread open.
      const fc = e.target.closest && e.target.closest('.csw-fupchip');
      if (fc) { e.stopPropagation(); cswSendFollowup(fc); return; }
      cswOpenThread(cid);
    });
    b.addEventListener('touchstart', e => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; swiped = false; }, { passive: true });
    b.addEventListener('touchmove', e => {
      if (x0 == null) return;
      const dx = e.touches[0].clientX - x0, dy = e.touches[0].clientY - y0;
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
        swiped = true;
        b.style.transform = `translateX(${Math.max(-96, Math.min(96, dx))}px)`;
        b.style.transition = '';
        cswSwipeLabel(b, dx < 0 ? 'resolve' : 'read');
      }
    }, { passive: true });
    b.addEventListener('touchend', e => {
      if (x0 == null) return;
      const dx = e.changedTouches[0].clientX - x0;
      b.style.transition = 'transform .2s var(--ease-emph)'; b.style.transform = '';
      if (dx > 72) cswMarkRead(cid);
      // Reveal-then-commit (TL-05): the row used to vanish silently with only
      // a plain toast. The action now reads under the row before it commits,
      // and resolve carries a real Undo.
      else if (dx < -72) cswResolveThread(cid, true);
      cswSwipeLabel(b, null);
      x0 = null; setTimeout(() => { swiped = false; }, 50);
    });
  }
}

// The colored action label revealed under a swiping row (WhatsApp pattern).
// dir: 'read' | 'resolve' | null (clear).
function cswSwipeLabel(row, dir) {
  let lab = row.querySelector('.csw-swipelab');
  if (!dir) { lab?.remove(); return; }
  if (!lab) {
    lab = document.createElement('span');
    lab.className = 'csw-swipelab';
    row.appendChild(lab);
  }
  lab.classList.toggle('csw-swipelab--resolve', dir === 'resolve');
  lab.textContent = dir === 'resolve' ? 'Resolve' : 'Mark read';
}

// ══════════════════════════════════════════════════════════════════
// TEAM lens (staff↔staff @-mention threads) — folded into the ONE shell.
//
// Lives entirely in cswState().teamThreads, loaded from the SAME staff_chat
// endpoint the old Staff-OS Team section used (GET /api/staffos/chat/threads).
// list_threads returns {thread_id, other:{name,init,grad,store}, last, tm, un};
// cswTeamNorm flattens that to a uniform row shape so the rest of the lens never
// reaches into `.other`. Opening a thread reuses the customer conversation pane
// (#csw-conv) via cswOpenTeamThread → cswRenderTeamConvShell + cswPaintTeamConv.
// Sending posts to the existing POST /api/staffos/chat/send. @-mentions resolve
// server-side (individual staff OR a department handle, e.g. @logistics).
// ══════════════════════════════════════════════════════════════════

// Flatten one raw team thread (list_threads shape) into a uniform row.
function cswTeamNorm(t) {
  const o = (t && t.other) || {};
  const name = o.name || t.name || 'Teammate';
  return {
    thread_id: t.thread_id || t.id || null,
    // mig 501: 'group' rooms sit in the same list as 1:1 DMs. A room has no
    // staff_id — its identity is the title and its member count.
    kind: t.kind === 'group' ? 'group' : 'dm',
    staff_id: o.staff_id || t.staff_id || null,
    name,
    init: o.init || t.init || cswInitials(name),
    seed: o.staff_id || t.thread_id || name,
    store: o.store || t.store || '',
    last: t.last != null ? t.last : (t.last_body || ''),
    tm: t.tm || t.last_at || '',
    un: Number(t.un || 0),
  };
}

// Total unread across team threads — feeds the Chat tab badge + the Team lens
// count chip. Normalised rows so a raw/normalised mix never mis-sums.
function cswTeamUnread() {
  return (cswState().teamThreads || []).reduce((a, t) => a + Number((t && t.un) || 0), 0);
}

// Load this person's team threads (best-effort: a failure leaves the lens
// honest-empty, never blanks the inbox). Sets the Team count chip + re-renders
// the list if the Team lens is the active one.
async function cswLoadTeam(quiet) {
  const s = cswState();
  const sid = sosState && sosState.cur;
  // Department rooms are derived from the roster, so a new hire's rooms should
  // appear without an admin step (mig 501). Idempotent + additive server-side;
  // fired once per session, never blocking the list.
  if (sid && !s._roomsEnsured) {
    s._roomsEnsured = true;
    api('/api/staffos/chat/rooms', { method: 'POST', body: '{}' }).catch(() => {});
  }
  if (!sid) { s.teamThreads = []; }
  else {
    try {
      const res = await api('/api/staffos/chat/threads?staff_id=' + encodeURIComponent(sid));
      s.teamThreads = ((res && res.threads) || []).map(cswTeamNorm);
    } catch (_) {
      if (!quiet) s.teamThreads = s.teamThreads || [];
    }
  }
  const cEl = document.getElementById('csw-c-team');
  if (cEl) cEl.textContent = (s.teamThreads || []).length;
  // keep the Staff-OS Chat tab badge truthful (customer unread + team unread).
  try { if (typeof sosBuildTabbar === 'function') sosBuildTabbar(); } catch (_) {}
  if (s.filter === 'team') cswRenderThreadList();
}

// Render the Team thread list into the inbox column (no EON row, no SLA).
// A room's avatar is its initials on a flat tile (no gradient seed — it isn't
// a person) plus the member count where a DM shows the store.
function cswTeamRowMeta(t) {
  if (t.kind !== 'group') return { badge: '', sub: t.store || '' };
  return {
    badge: '<span class="csw-teamgrp" title="Group room">'
      + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">'
      + '<circle cx="9" cy="8" r="3"/><path d="M3 20c1-3.4 3-5 6-5s5 1.6 6 5"/>'
      + '<circle cx="17" cy="9" r="2.4"/><path d="M16 15c2.2.3 3.6 1.7 4.4 4"/></svg></span>',
    sub: t.store || '',
  };
}

// ── Priority tab (mig 545) — the shift worklist that drains ─────────
// Server-ranked cards for THIS shift. The count chip shows open cards;
// the header shows the drain (cleared/total). Claim = "I'm on it"
// (turns the card), Clear = done. Threads answered through the normal
// desk auto-clear server-side — the book empties either way.
let _cswPrio = { items: [], cleared: 0, at: 0 };
// Lane P (2026-08-21): the response clock rides the book's payload but is
// kept OUT of _cswPrio on purpose — other painters rebuild that object
// from their own reads (the Desk ring) and would drop it.
let _cswClock = { reps: {}, unanswered_now: 0, at: 0 };
async function cswLoadPriority(quiet) {
  if (quiet && Date.now() - _cswPrio.at < 60 * 1000) return;
  let d;
  try { d = await api('/api/cs/priority'); }
  catch (_) { d = null; }
  if (!d || d.ok === false) {
    // CSW-03: a failed read is never a clear board. A later failed refresh
    // keeps the last good book; with no book yet, the board that would say
    // "No cards on this shift's board" says it could not load, with Retry.
    if (_cswPrio.at) return;
    const hf = document.getElementById('csw-hq-frac');
    if (hf) hf.textContent = '—';
    // The unread mark IS this painter: the list painters and the ring read
    // it on every repaint and draw this same state. A good read rebuilds
    // _cswPrio (here or in the Desk ring), which clears it.
    const unread = n => {
      n.dataset.prioUnread = '1';
      n.innerHTML = '<div class="csw-empty__m">!</div>Couldn’t load the Priority book. <button type="button" data-prio-retry>Retry</button>';
      n.querySelector('[data-prio-retry]').addEventListener('click', () => {
        n.innerHTML = '<div class="csw-empty__m">○</div>Loading the Priority book…';
        cswLoadPriority();
      });
    };
    _cswPrio.unread = unread;
    document.querySelectorAll('#csw-threads .csw-empty').forEach(n => {
      if (!n.dataset.prioUnread && !/No cards on this shift's board/.test(n.textContent)) return;
      unread(n);
    });
    return;
  }
  _cswPrio = { items: d.items || [], cleared: d.cleared || 0,
               neglected: d.neglected || 0, money_waiting: d.money_waiting || 0,
               looks_waiting: d.looks_waiting || 0, contact_checks_unreadable: !!d.contact_checks_unreadable, at: Date.now() };
  if (d.clock && typeof d.clock === 'object') {
    _cswClock = { reps: d.clock.reps || {}, unanswered_now: d.clock.unanswered_now || 0, at: Date.now() };
  }
  // The book has loaded — the shift-start landing decides ONCE per shift
  // per device whether the rep stands on it (every guard inside).
  cswPrioLandOnShift(d);
  // The Priority key's count = what is still to WORK. House moves a
  // claimed card out of the rail into the working strip, so counting it
  // as queue work would contradict the strip on the same screen (the
  // p776 `neglected` lesson); classic's list keeps every card, so it
  // counts every card. ONE painter either way.
  const _prioCount = cswPrioBadgeCount();
  const c = document.getElementById('csw-c-priority');
  if (c) c.textContent = String(_prioCount);
  // The bar's ONE numeric badge mirrors this same painter — never a
  // second computation that can disagree with the lens key it shadows.
  const tb = document.getElementById('csw-tb-badge');
  if (tb) { tb.textContent = String(_prioCount); tb.hidden = !_prioCount; }
  // House: the book IS the rail (queue fold) — any book change repaints it.
  if (cswState().filter === 'priority' || cswHouse()) cswRenderThreadList();
}
// ── Lane P (2026-08-21): Priority is the rep's instrument ────────────
// Measured over 30 days: reps claimed ONE priority card; 131 of 132
// manual clears were the operator. Reps work the inbox lenses and never
// land on the book. So at shift start the desk lands them on it — ONCE
// per shift per device (localStorage remembers the shift key), never
// again mid-shift, never over a thread deep-link or an open conversation,
// never over a lens the rep picked by hand, never for the owner, never
// onto an empty book, and only under the house skin.
const CSW_PRIO_LANDED_KEY = 'csw.prio.landed';
function cswIsOwner() {
  const m = State.member || {};
  return m.role === 'owner' || m.base_role === 'owner';
}
function cswPrioLandOnShift(d) {
  try {
    if (!cswHouse()) return false;
    if (cswIsOwner()) return false;
    // Customer Priority owns the count, but enquiries are still real work.
    // An enquiry-only shift lands on the book so the rep can route it; it
    // simply does not inflate the customer ring or badge.
    const items = (d && d.items) || [];
    const skey = String((d && d.shift_key) || '');
    if (!skey) return false;
    if (!items.length) {
      // A quiet start still spends the shift's one landing (Opus vet F9):
      // otherwise the first card minted an hour later would walk the rep
      // off whatever lane they had opened by hand in the meantime.
      try { localStorage.setItem(CSW_PRIO_LANDED_KEY, skey); } catch (_) {}
      return false;
    }
    const s = cswState();
    if (s.active || s.activeThread || State.csPendingThread) return false;
    const qs = new URLSearchParams(location.search || '');
    if (qs.get('thread') || /(^|[#&?])thread=/.test(location.hash || '')) return false;
    if (s.lensPicked) return false;
    let landed = '';
    try { landed = localStorage.getItem(CSW_PRIO_LANDED_KEY) || ''; } catch (_) {}
    if (landed === skey) return false;
    try { localStorage.setItem(CSW_PRIO_LANDED_KEY, skey); } catch (_) {}
    s.prioLandNote = skey;
    // Already standing on the book (fresh boot lands there): the repaint
    // that follows this call paints the note — no body re-render, no
    // realtime restart. Anywhere else: the tab's own navigation, marked
    // auto so it does not count as a hand-picked lens.
    const onBook = s.mode === 'inbox' && !s.houseLens && s.filter !== 'team';
    if (!onBook) cswTabGo('priority', true);
    return true;
  } catch (_) { return false; }
}
// The response clock — quiet mono lines under the book's note. The rep
// reads their own first-reply time; the owner reads one line per rep;
// both read how many recent conversations nobody has answered. Empty
// clock = no line at all (no placeholder).
function cswClockName(email) {
  const e = String(email || '').toLowerCase();
  if (e && e === cswMyEmail()) {
    const nm = (State.member && State.member.name) || State.repName || '';
    if (nm) return String(nm).trim().split(/\s+/)[0];
  }
  const roster = _cswShiftNames[e];
  if (roster) return String(roster).trim().split(/\s+/)[0];
  return e.split('@')[0] || e;
}
function cswClockHtml() {
  const reps = (_cswClock && _cswClock.reps) || {};
  const emails = Object.keys(reps);
  const un = Number((_cswClock && _cswClock.unanswered_now) || 0);
  if (!emails.length && !un) return '';
  const rows = [];
  const late = (r) => (Number(r.median_min) > 30 ? ' csw-clock__late' : '');
  // The MEDIAN is the honest number: the slowest tenth mostly measured
  // the overnight backlog whoever opened the shift inherited (Opus vet
  // F4: a rep who answered everything in 15 minutes read "slowest tenth
  // 376m"). Owner lines cover the ROSTER's reps — the operator's own
  // sends are not a rep's shift and sat permanently warn-tinted.
  if (cswIsOwner()) {
    // WS-B (2026-09-02): the per-rep rows sorted by median were a
    // staff-facing ranking — forbidden (cohesion, not competition: a
    // name never sits beside a clock or a failure count). The owner
    // reads the DESK: how many first replies it made today, as one
    // line. Per-cause failures live on the Desk room's ledger.
    const roster = Object.keys(_cswShiftNames || {});
    const shown = roster.length ? emails.filter(e => roster.includes(e)) : emails.filter(e => e !== cswMyEmail());
    const answered = shown.reduce((a, e) => a + (Number((reps[e] || {}).answered) || 0), 0);
    if (shown.length) rows.push(`<div class="csw-clock__row">Desk · ${answered} first repl${answered === 1 ? 'y' : 'ies'} today across ${shown.length} desk${shown.length === 1 ? '' : 's'}</div>`);
  } else {
    const me = cswMyEmail();
    const r = me ? reps[me] : null;
    if (r) rows.push(`<div class="csw-clock__row${late(r)}">Your first reply today · median ${Number(r.median_min) || 0}m · ${Number(r.answered) || 0} answered</div>`);
  }
  if (un > 0) rows.push(`<div class="csw-clock__row">${un} conversation${un === 1 ? '' : 's'} started in the last 12h still unanswered</div>`);
  if(_cswPrio.contact_checks_unreadable)rows.push('<div class="csw-clock__row">Contact checks could not be read. Open the conversation’s plan to check.</div>');
  if (!rows.length) return '';
  return `<div class="csw-clock">${rows.join('')}</div>`;
}
async function cswPrioTap(id, status, settleMs) {
  const serviceCard=(_cswPrio.items||[]).find(p=>p.id===id);
  if(serviceCard?.service_obligations?.length){await cswOpenThread(serviceCard.zoko_customer_id);cswServiceSheet('cases');return;}
  if(String(id).startsWith('contact-task:')) {
    const task=(_cswPrio.items||[]).find(p=>p.id===id);
    if(task)cswContactResolveCheck(task);
    return;
  }
  try {
    const result=await api(`/api/cs/priority/${encodeURIComponent(id)}/status`,
      { method: 'POST', body: JSON.stringify({ status }) });
    if(result?.ok!==true){cswToast(result?.error==='service_obligation_open'?'This service case is still open. Open Service cases to take the next step.':'The card could not be updated. Refresh and check its status.',true);return;}
  } catch (error) { cswToast(cswServiceRefusal(error),true);return; }
  _cswPrio.at = 0;               // force the next load
  // House claim: the reload rebuilds the rail, which would cut the tear
  // off mid-animation (the POST answers faster than 450ms). The claim
  // handler passes the tear's duration so the ticket finishes leaving
  // before the rail repaints. Classic taps reload immediately, as before.
  if (settleMs) setTimeout(() => cswLoadPriority(), settleMs);
  else cswLoadPriority();
}
// 2026-09-24 (vet 17): Priority cards were almost never claimed by a
// person. Opening a card only opened the chat and "I'm on it" only
// claimed, so the natural act (open, answer, move on) left no claim: two
// reps could work one customer and the "nobody has picked this up" count
// kept counting it. A rep's first send from a card's chat (cswMarkReplied)
// now claims its open cards through the same /priority/{id}/status call,
// once and quietly; "I'm on it" claims and opens the chat. A card someone
// else holds is left alone (the server refuses that claim too); service
// cases and contact checks keep their own doors; the owner claims nothing.
// 2026-09-26 (adversary on #3559): a card asked for once was never asked
// for again, so a card that closed and then reopened (the customer wrote
// again) could not be claimed from this desk. Each ask now remembers the
// book it was made from; a newer book that shows the card open again
// lets the desk claim it once more.
// 2026-09-27 (Fable 5.1 vet): opening a card used to claim it too. That
// made every card a rep only looked at 'claimed', and the board's drain
// retires and expires only 'open' cards, so a looked-at card stayed on the
// "you're on it" strip after the board let it go, and its automatic clear
// was filed under the opener's name as a rep's own clear (the 14-day
// latch, the handled count, and no demotion for that class). Opening now
// only reads; the claim waits for real work: a send, or "I'm on it".
function cswClaimCardFor(cid) {
  if (!cid || cswIsOwner()) return;
  const sent = cswClaimCardFor._sent || (cswClaimCardFor._sent = new Map());
  const book = _cswPrio.at || 0;
  (_cswPrio.items || []).filter(p => String(p.zoko_customer_id || '') === String(cid)
    && p.status !== 'claimed' && p.status !== 'cleared' && !cswCardHasOwnDoor(p)
    && !(sent.has(p.id) && sent.get(p.id) >= book)).forEach(card => {
    sent.set(card.id, book);
    api(`/api/cs/priority/${encodeURIComponent(card.id)}/status`,
      { method: 'POST', body: JSON.stringify({ status: 'claimed' }) })
      .then(result => {
        // Held by someone else, or already closed: not again from this
        // book. Any other refusal may retry.
        if (result?.ok !== true) {
          if (!result?.claimed_by && result?.error !== 'card is closed') sent.delete(card.id);
          return;
        }
        _cswPrio.at = 0;
        cswLoadPriority();
      })
      .catch(() => sent.delete(card.id));
  });
}
// A card whose key opens its own door: a service case opens its case sheet
// and a contact check opens its check (both inside cswPrioTap). Opening the
// chat never claims one, and "I'm on it" on one never opens the chat under
// that door (it would mark the chat seen and start a silent EON draft).
function cswCardHasOwnDoor(p) {
  return !!p && (!!p.service_obligations?.length || String(p.id || '').startsWith('contact-task:'));
}

// 2026-09-24 (vet 28): the house queue was rebuilt with innerHTML on every
// poll and realtime nudge (every card replaced, even on an idle desk), so a
// focused card dropped focus to the page and a tap mid-redraw hit a card
// that no longer existed. cswRenderHouseQueue now builds off-screen and
// this patches the live list card by card, the keyed idea the chat list
// already uses: an unchanged card is left alone, a changed one takes the
// fresh markup in place, a card mid-tear keeps tearing, and anything
// without a key (notes, clock, footer) is replaced as before.
function cswHqKey(n) {
  if (!n || n.nodeType !== 1) return '';
  if (n.classList.contains('csw-business-followups')) return 'enq';
  if (n.tagName === 'SUMMARY' && n.parentNode?.classList.contains('csw-business-followups')) return 'enq-summary';
  if (n.classList.contains('csw-thread--eon')) return 'eon';
  if (n.dataset.card) return (n.classList.contains('csw-hworking') ? 'w:' : 't:') + n.dataset.card;
  if (n.classList.contains('csw-thread--dense') && n.dataset.open) return 'd:' + n.dataset.open;
  return '';
}
function cswHqPatch(host, fresh) {
  const was = document.activeElement;
  const hadFocus = !!was && was !== host && host.contains(was);
  const wasCard = hadFocus ? was.closest('[data-card], .csw-thread--dense, .csw-thread--eon, summary') : null;
  const wasKey = cswHqKey(wasCard);
  const wasKeySel = hadFocus && was !== wasCard && was.dataset
    ? (was.dataset.noreply ? `[data-noreply="${CSS.escape(was.dataset.noreply)}"]`
      : was.dataset.prio ? `[data-prio="${CSS.escape(was.dataset.prio)}"][data-act="${CSS.escape(was.dataset.act || '')}"]` : '')
    : '';
  const old = new Map();
  const now = Date.now();
  Array.from(host.children).forEach(n => {
    const k = cswHqKey(n);
    // A card mid-tear finishes tearing; one still torn 1.5 s after its tap
    // (its claim or Done failed, so no reload came) is redrawn whole, as
    // before. 2026-09-26: the tap stamps the clock (_cswTornAt), so the
    // first refresh after a failure redraws it; the clock used to start
    // at that refresh and hid the card for one more.
    const torn = n.classList.contains('hs-tearing');
    if (torn && !n._cswTornAt) n._cswTornAt = now;
    if (k && !old.has(k) && !(torn && now - n._cswTornAt > 1500)) old.set(k, n);
  });
  const plan = Array.from(fresh.children).map(nb => {
    const k = cswHqKey(nb), cur = k ? old.get(k) : null;
    if (cur) old.delete(k);
    return { nb, cur };
  });
  const kept = new Set(plan.map(x => x.cur).filter(Boolean));
  Array.from(host.children).forEach(n => { if (!kept.has(n)) n.remove(); });
  let prev = null;
  plan.forEach(({ nb, cur }) => {
    const sig = nb.outerHTML;
    let node = nb;
    if (cur) {
      node = cur;
      if (cur._cswHqSig !== sig && !cur.classList.contains('hs-tearing')) {
        Array.from(cur.attributes).forEach(a => { if (!nb.hasAttribute(a.name)) cur.removeAttribute(a.name); });
        Array.from(nb.attributes).forEach(a => { if (cur.getAttribute(a.name) !== a.value) cur.setAttribute(a.name, a.value); });
        if (!cur.classList.contains('csw-business-followups')) {
          while (cur.firstChild) cur.removeChild(cur.firstChild);
          while (nb.firstChild) cur.appendChild(nb.firstChild);   // fresh children arrive already bound
        }
      }
      if (cur.classList.contains('csw-business-followups')) {
        // Enquiries use the same key/focus/tear contract as the customer rail,
        // including an unchanged poll after a failed action's tear expires.
        cswHqPatch(cur, nb);
      }
    } else if (node.classList.contains('csw-business-followups')) {
      // Newly mounted nested cards are already bound, just like top-level
      // cards. Seed their signatures so the first poll keeps their buttons.
      Array.from(node.children).forEach(n => { n._cswHqSig = n.outerHTML; });
    }
    node._cswHqSig = sig;
    const want = prev ? prev.nextSibling : host.firstChild;
    if (want !== node) host.insertBefore(node, want);
    prev = node;
  });
  if (hadFocus && !host.contains(document.activeElement)) {
    const card = wasKey && Array.from(host.querySelectorAll('[data-card], .csw-thread--dense, .csw-thread--eon, summary')).find(n => cswHqKey(n) === wasKey);
    const again = card && ((wasKeySel && card.querySelector(wasKeySel)) || card);
    if (again && again.focus) again.focus({ preventScroll: true });
  }
}
// "No reply needed" (2026-08-20): the human door the reopen guard kept
// locked. Every priority thread's last word is the customer's by
// definition, so plain resolve always bounced back to 'open' and the
// Priority tab refilled after every clear. One tap force-resolves the
// thread (the server writes the audit note) and, when a card id rides
// along, clears that priority card in the SAME POST. ONE CLICK law: no
// confirm — the toast carries Undo, a plain reopen through the existing
// /status route. All three surfaces (classic prio row, house ticket,
// 360 sheet) route through this one helper.
async function cswNoReplyNeeded(cid, cardId, settleMs) {
  if (!cid) return false;
  // One flight per thread (vet S15): the classic and house keys carry no
  // disabled state of their own, so a double-tap was two forced resolves
  // and two audit notes.
  cswNoReplyNeeded._busy = cswNoReplyNeeded._busy || new Set();
  if (cswNoReplyNeeded._busy.has(cid)) return false;
  cswNoReplyNeeded._busy.add(cid);
  let r = null;
  try {
    r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/no-reply-needed`,
      { method: 'POST', body: JSON.stringify(cardId ? { card_id: cardId } : {}) });
  } catch (e) {
    cswNoReplyNeeded._busy.delete(cid);
    cswToast(cswServiceRefusal(e), true);
    return false;
  }
  cswNoReplyNeeded._busy.delete(cid);
  // api() throws on non-2xx, but success is still GATED on r.ok —
  // never on merely reaching the line after the await.
  if (!(r && r.ok)) {
    cswToast(cswServiceRefusal({message:r?.error}), true);
    return false;
  }
  const th = (cswState().threads || []).find(x => x.zoko_customer_id === cid);
  if (th) { th.cs_status = 'resolved'; th.unread = false; th.unread_count = 0; }
  const reload = () => { _cswPrio.at = 0; cswLoadPriority(); };
  if (settleMs) setTimeout(reload, settleMs); else reload();
  cswRenderThreadList();
  if (typeof cswToastUndo === 'function') {
    cswToastUndo('Closed — reopens if they write again', () => {
      const t2 = (cswState().threads || []).find(x => x.zoko_customer_id === cid);
      if (t2) t2.cs_status = 'open';
      cswRenderThreadList();
      // The undo action (vet S5): reopening the thread alone left the
      // card's clear stamp standing — and the stamp IS the latch's
      // ledger, so the mint stayed suppressed while the toast said
      // "Reopened". The route's undo reopens the thread AND un-stamps
      // this rep's recent clears for it.
      api(`/api/cs/threads/${encodeURIComponent(cid)}/no-reply-needed`,
        { method: 'POST', body: JSON.stringify({ undo: true }) })
        .then((u) => { if (u && u.ok) { _cswPrio.at = 0; cswLoadPriority(); cswToast('Reopened'); } })
        .catch(() => cswToast('Could not reopen — retry.', true));
    });
  } else {
    cswToast('Closed — reopens if they write again.');
  }
  return true;
}
// ONE CLOSE KEY ON A PRIORITY CARD (vet A10, 2026-09-26). A card carried
// three keys: "No reply needed", "Done" (renamed "Handled elsewhere" by
// open PR #3540) and "I'm on it". The first two differed only in a hover
// title a phone never shows, and only one of them closed the chat, so a
// card cleared with Done came back at the next rebuild. The close key
// now does both: it closes the chat's reply need and clears the card in
// one tap (cswNoReplyNeeded), and a new customer message reopens both.
// A card with no chat to close (none are minted today) clears the card
// alone through the same status call Done used. Service cases and
// contact checks keep their own doors; "I'm on it" stays.
async function cswCloseCard(cid, cardId, settleMs) {
  if (cid) return cswNoReplyNeeded(cid, cardId, settleMs);
  if (!cardId) return false;
  await cswPrioTap(cardId, 'cleared', settleMs);
  return true;
}
// The "waiting Nh" chip (Lane A, 2026-08-21). Reads the thread's own
// waiting_minutes (server-computed from the last inbound); nothing when
// the thread is not loaded or has been answered. Warm at the hour, late
// at four — the floor's own two clocks (cs_desk_floors).
function cswWaitChipHtml(t) {
  const w = t ? Number(t.waiting_minutes || t._waitm || 0) : 0;
  if (!t || !t.unreplied || !(w > 0)) return '';
  const txt = w < 60 ? `waiting ${w}m` : `waiting ${Math.floor(w / 60)}h`;
  const cls = w >= 240 ? ' csw-prio__wait--late' : (w >= 60 ? ' csw-prio__wait--warm' : '');
  return `<span class="csw-prio__wait num${cls}">${esc(txt)}</span>`;
}
// THE EMPTY BOARD TELLS THE WHOLE TRUTH (operator, 2026-09-06). At 00:40
// WAT the Priority board wore a tick and "No cards on this shift's board"
// while the chip beside it read 21 need a reply, the oldest waiting
// 2d 16h. A rep reads the tick as all clear. The board's own rule is
// right — threads past recovery stay off the board — so the empty state
// now says, in the rep's numbers, what is still waiting below it.
function cswEmptyBoardTruth() {
  // Counts what the "Needs reply" chip beside it counts — waiting rows
  // inside the recovery bound — so the two numbers on one screen agree.
  // Live on the first deploy the line said 67 while the chip said 21:
  // the 46 past recovery belong to the Recovery lens, and are named as
  // such rather than folded into the number a rep is asked to clear.
  const all = (cswState().threads || []).filter(t => t && t._waiting && !t._also_stub);
  const rows = all.filter(t => (t._waitm || 0) <= CSW_RECOVERY_MIN);
  const older = all.length - rows.length;
  if (!rows.length && !older) return '';
  const oldest = Math.max(0, ...rows.map(t => t._waitm || 0));
  const who = rows.length === 1 ? '1 customer still needs' : `${rows.length} customers still need`;
  const head = rows.length ? `${who} a reply below${oldest ? ` — the longest wait is ${cswFmtWait(oldest)}` : ''}.` : '';
  const tail = older ? ` ${older} older ${older === 1 ? 'one waits' : 'ones wait'} in Recovery.` : '';
  return `<div class="csw-empty__s csw-empty__truth">${(head + tail).trim()}</div>`;
}

// WHY THIS ONE IS ON THE BOARD, said under the name (2026-09-06). The
// board's ticket says the reason and quotes the customer; the conversation
// head said nothing, so a rep who opened from the inbox, a search or a
// notification never saw why the desk ranked it. One line, the ticket's
// own words, only while a live card exists for this thread.
function cswConvWhyHtml(t) {
  if (!t) return '';
  const cid = String(t.zoko_customer_id || '');
  const p = (_cswPrio.items || []).find(x => String(x.zoko_customer_id) === cid && x.status !== 'cleared');
  if (!p || !p.title) return '';
  return `<div class="csw-conv__why" id="csw-conv-why"><span class="csw-conv__why-k">On the board</span><span class="csw-conv__why-t">${esc(p.title)}</span>${cswPrioWhyHtml(p)}</div>`;
}

// THE FOUR-STEP PATH (2026-09-06). Understand · Prepare · Reply · Confirm,
// read from what the desk already knows: a draft saved for this thread or
// EON's own reply held for the rep's veto means Prepare; the thread no
// longer waiting on us means Reply; resolved means Confirm. Nothing here
// is a new state — it is the existing states, said in order.
function cswConvPathHtml(t) {
  if (!t) return '';
  const cid = String(t.zoko_customer_id || '');
  let draft = '';
  try { draft = cswLoadDraft(cid) || ''; } catch (_) { draft = ''; }
  const held = (_cswHolds.rows || []).some(h => String(h.zoko_customer_id || '') === cid && (h.state === 'held' || h.state === 'sending'));
  const resolved = t.cs_status === 'resolved';
  const replied = !t._waiting;
  const prepared = !!(draft.trim() || held);
  // Unsent work outranks a past reply (Opus floor 3 verdict, 2026-09-06): a
  // held follow-up on a thread already answered reads Prepare, not Reply.
  const step = resolved ? 3 : prepared ? 1 : replied ? 2 : 0;
  const labels = ['Understand', 'Prepare', 'Reply', 'Confirm'];
  return `<ol class="csw-conv__path" aria-label="Where this conversation is">${labels.map((l, i) =>
    `<li class="${i < step ? 'done' : i === step ? 'cur' : ''}"><i>${i < step ? '✓' : String(i + 1)}</i>${l}</li>`).join('')}</ol>`;
}

// WHAT HAPPENED (2026-09-06): one list, newest first, from the rows the
// desk already holds — the transcript, EON's held reply, the store holds,
// the promise, the claim, the resolution. Plain words, a time, no ids.
function cswTrailTime(x) {
  if (!x) return '';
  const d = new Date(x); if (isNaN(d)) return '';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
function cswTrailHtml(t) {
  const s = cswState();
  if (!t) return '';
  const cid = String(t.zoko_customer_id || '');
  const first = String(cswDisplayName ? cswDisplayName(t) : (t.customer_name || 'Customer')).split(' ')[0] || 'Customer';
  const ev = [];
  const rows = s._advanceTranscriptCid === cid && Array.isArray(s.transcript) ? s.transcript : [];
  rows.forEach(r => {
    const b = r.body || {};
    if (b.deleted || b.is_deleted) return;
    const at = r.platform_timestamp || r.created_at;
    if (r.direction === 'inbound') { ev.push({ at, k: 'in', t: `${first} wrote` }); return; }
    const eon = r.agent_email === 'eon-autopilot@crux.ng' || (b.author && b.author.kind === 'eon');
    const who = eon ? 'EON' : (b.author && b.author.label) || (r.agent_email ? String(r.agent_email).split('@')[0] : 'We');
    const receipt = cswReceiptState(r, t);
    const said = receipt.kind === 'failed' ? 'not delivered' : receipt.word.toLowerCase();
    ev.push({ at, k: receipt.kind === 'held' ? 'held' : eon ? 'eon' : 'rep', t: `${who} ${b.template_name ? 'template' : 'message'} · ${said}` });
  });
  (_cswHolds.rows || []).forEach(h => {
    if (String(h.zoko_customer_id || '') !== cid) return;
    if (h.state === 'held' || h.state === 'sending') ev.push({ at: h.created_at || null, k: 'held', t: 'EON’s reply is held for a rep to stop or send' });
  });
  const holds = (s._trailHolds && s._trailHolds.cid === cid) ? (s._trailHolds.holds || []) : [];
  holds.forEach(h => {
    const what = [h.product_title, h.variant_title].filter(Boolean).join(' · ');
    const st = h.redeemed_at ? 'collected' : (h.status || 'held');
    ev.push({ at: h.held_at, k: 'hold', t: `Hold at ${h.store_name || 'the store'}${what ? ' · ' + what : ''} · ${st}` });
  });
  if (t.promise_at) ev.push({ at: t.promise_at, k: 'promise', t: t.promise_open === true ? 'A follow-up is due · still open' : t.promise_open === false ? 'A follow-up was marked closed' : 'A follow-up is recorded · status unverified' });
  if (t.assigned_rep_email) ev.push({ at: null, k: 'claim', t: `${String(t.assigned_rep_email).split('@')[0]} is on it` });
  if (t.cs_status === 'resolved') ev.push({ at: null, k: 'done', t: 'Marked resolved' });
  if (!ev.length) return '';
  ev.sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));
  return `<div class="csw-trail" id="csw-trail">${ev.slice(0, 12).map(e =>
    `<div class="csw-trail__row csw-trail__row--${e.k}"><span class="csw-trail__k">${esc(cswTrailTime(e.at))}</span><span class="csw-trail__t">${esc(e.t)}</span></div>`).join('')}</div>`;
}
// The store holds are the one fact the desk does not already hold in
// memory: fetched once per opened panel, quietly, and painted in place.
async function cswLoadTrailHolds(cid) {
  const s = cswState();
  if (!cid || (s._trailHolds && s._trailHolds.cid === String(cid))) return;
  let d = null;
  try { d = await api(`/api/cs/holds?thread_key=${encodeURIComponent(cid)}`); } catch (_) { return; }
  if (!d || d.ok === false) return;
  s._trailHolds = { cid: String(cid), holds: d.holds || [] };
  const host = document.getElementById('csw-trail-host');
  if (host && String(s.active) === String(cid)) host.innerHTML = cswTrailHtml(s.activeThread || {});
}

// ── 08 · THE BUYER CARD SAYS WHY (2026-08-29) ───────────────────────
// A card that says "Asking about the pieces" is a verdict; a card that
// shows the customer's OWN words is evidence a rep can answer. When the
// feed row carries the second pass's read — the class it placed, the
// sentence it placed it on, and how sure it was — the card prints all
// three. No fields → nothing extra, and the reason line stands alone
// exactly as it did.
function cswPrioWhyHtml(p) {
  if (!p) return '';
  const bits = [];
  const ev = String(p.evidence || '').trim();
  if (ev) bits.push(`<span class="crd-why__quote">“${esc(ev.slice(0, 120))}”</span>`);
  const cls = String(p.intent2_class || '').trim();
  if (cls) bits.push(`<span class="crd-why__cls">${esc(cls.replace(/_/g, ' '))}</span>`);
  const conf = Number(p.confidence);
  // A confidence is a number between 0 and 1 or a percent — say it as a
  // percent, and never invent one for a row that carries none.
  if (isFinite(conf) && conf > 0) {
    const pct = conf <= 1 ? Math.round(conf * 100) : Math.round(conf);
    bits.push(`<span class="crd-why__conf">${pct}% sure</span>`);
  }
  return bits.length ? `<div class="crd-why">${bits.join('')}</div>` : '';
}


function cswRenderPriorityList(el) {
  const items = _cswPrio.items || [];
  const done = _cswPrio.cleared || 0;
  if (!items.length && _cswPrio.unread) {
    // CSW-03: a book never read is not an empty board.
    el.innerHTML = '<div class="csw-empty"></div>';
    _cswPrio.unread(el.firstChild);
    return;
  }
  if (!items.length) {
    // Says what it knows: this SHIFT'S BOARD is empty. It used to say
    // "Nothing waiting", which the mint window made false — the board now
    // deliberately declines threads past recovery, so a rep could read
    // "nothing waiting" with 137 customers genuinely waiting in the inbox.
    el.innerHTML = `<div class="csw-empty"><div class="csw-empty__m">✓</div>` +
      (done ? `Book clear — ${done} handled this shift.`
            : `No cards on this shift's board.`) +
      `<div class="csw-empty__s">Older threads stay in the inbox.</div>` +
      cswEmptyBoardTruth() +
      `</div>`;
    return;
  }
  // 'intent2_buy' (2026-08-29) — the SECOND pass placed a purchase shape
  // the first pass read as 'other'. cs_priority mints these cards; the
  // icon map had no entry, so the desk's loudest buyers rendered with the
  // generic bullet.
  const REASON_ICON = { money_waiting: '₦', complaint: '⚠', payment_claim: '₦', paid_order_open: '📦', catalog_order: '🛒', ad_lead: '◎',
                        returning_customer: '↺', stalled_buy: '◔', intent2_buy: '◆',
                        voice_to_hear: '♪', file_to_read: '▭', video_to_watch: '▶', story_to_thank: '☆',
                        unanswered_in_shift: '…',
                        promise_open: '◷' };
  // Lane A (2026-08-21): the card wears its live wait as a chip, joined
  // from the loaded thread so it moves with every poll (the house ticket
  // already prints the wait in its foot; classic only had it buried in
  // the reason line's words).
  const byCid = new Map((cswState().threads || []).map(t => [String(t.zoko_customer_id), t]));
  const customerItems = cswCustomerPriorityItems(items);
  const followups = items.filter(p => p.reason_key === 'business_followup');
  const renderCard = p => `
    <div class="csw-prio ${p.status === 'claimed' ? 'csw-prio--claimed' : ''}" data-open="${esc(p.zoko_customer_id || '')}">
      <div class="csw-prio__row">
        <span class="csw-prio__i">${REASON_ICON[p.reason_key] || '•'}</span>
        <span class="csw-prio__name">${esc(p.customer_name || p.platform_sender_id || 'Customer')}</span>
        ${p.reason_key === 'business_followup' ? '' : cswWaitChipHtml(byCid.get(String(p.zoko_customer_id)))}
        ${p.status === 'claimed' ? `<span class="csw-prio__claim">${esc((p.claimed_by || '').split('@')[0])}</span>` : ''}
      </div>
      <div class="csw-prio__why">${esc(p.reason_text || '')}${p.contact_task_id?` · due ${esc(cswContactWhen(p.due_at))}`:''}</div>
      ${cswPrioWhyHtml(p)}
      ${cswServiceMeta(byCid.get(String(p.zoko_customer_id)), false)}
      <div class="csw-prio__acts">
        ${p.service_obligations?.length ? `<button class="csw-prio__btn" data-prio="${esc(p.id)}" data-act="claimed">Open case</button>` : p.status !== 'claimed' ? `<button class="csw-prio__btn" data-prio="${esc(p.id)}" data-act="claimed">I'm on it</button>` : ''}
        ${/* vet A10: one close key (cswCloseCard) in place of "No reply
           needed" + "Done". A contact check keeps its own door: its key
           opens the check (cswPrioTap), as Done did. A business enquiry
           (open PR #3507) keeps a Done that clears the card only: the
           chat stays open for the team that answers it. */''}
        ${p.service_obligations?.length ? '' : p.contact_task_id
          ? `<button class="csw-prio__btn csw-prio__btn--done" data-prio="${esc(p.id)}" data-act="cleared">Record the check</button>`
          : p.reason_key === 'business_followup'
          ? `<button class="csw-prio__btn csw-prio__btn--done" data-prio="${esc(p.id)}" data-act="cleared" title="Clears the card. The chat stays open for the team that answers it.">Done</button>`
          : `<button class="csw-prio__btn csw-prio__btn--done" data-noreply="${esc(p.id)}" data-cid="${esc(p.zoko_customer_id || '')}" title="Nothing more to do here: closes the chat and clears the card. Both come back if the customer writes again.">Close — reopens <br>if they write again</button>`}
      </div>
    </div>`;
  el.innerHTML = `<div class="csw-prio-head">${customerItems.length} customer requests · ${done} cleared</div>` +
    ((_cswPrio.neglected || 0) > 0 ? `<div class="csw-hnote">${_cswPrio.neglected} waiting across shifts — nobody has picked ${_cswPrio.neglected === 1 ? 'this' : 'these'} up</div>` : '') +
    ((_cswPrio.looks_waiting || 0) > 0 ? `<div class="csw-hnote csw-hnote--looks">${_cswPrio.looks_waiting} look${_cswPrio.looks_waiting === 1 ? '' : 's'} waiting on the Stage — staged, never sent</div>` : '') +
    cswClockHtml() +
    customerItems.map(renderCard).join('') + cswBusinessFollowupsHtml(followups, renderCard);
  el.querySelectorAll('.csw-prio').forEach(card => {
    card.addEventListener('click', (e) => {
      if (e.target.closest('[data-prio],[data-noreply]')) return;   // buttons handle themselves
      const cid = card.dataset.open;
      if (cid) cswOpenThread(cid);   // opening reads; a first send claims (cswMarkReplied)
    });
  });
  el.querySelectorAll('[data-prio]').forEach(b => {
    b.addEventListener('click', () => {
      cswPrioTap(b.dataset.prio, b.dataset.act);
      // vet 17: "I'm on it" also opens the chat it claims ("Open case" and
      // a contact check open their own door inside cswPrioTap).
      const p = (_cswPrio.items || []).find(x => x.id === b.dataset.prio);
      if (b.dataset.act === 'claimed' && p?.zoko_customer_id && !cswCardHasOwnDoor(p)) cswOpenThread(p.zoko_customer_id);
    });
  });
  el.querySelectorAll('[data-noreply]').forEach(b => {
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      cswCloseCard(b.dataset.cid, b.dataset.noreply);
    });
  });
}

// ── HOUSE floor 3 — the queue (WS-B2, 2026-08-07) ───────────────────
// The fold from the approved mockup (docs/house/floors/3-switchboard-
// cs-desk.html): one rail of tickets in the shift book's rank order,
// each printing WHY it is next, its live wait (joined from the loaded
// thread so it moves with every poll) and a one-tap claim. Claiming
// tears the ticket off the rail — the floor's one moving part, spent
// where the work happens. Claimed cards ride a working strip above the
// rail; threads the book declines are counted by the honesty footer,
// never vanished. Classic keeps tabs + board untouched: this renderer
// is reached only behind cswHouse() (see the dispatch in
// cswRenderThreadList) and reuses the board's own endpoints — claim is
// still POST /api/cs/priority/{id}/status, nothing new sends.
const CSW_HQ_ICON = { money_waiting: '₦', complaint: '⚠', payment_claim: '₦', paid_order_open: '▣', catalog_order: '▤', ad_lead: '◎',
                      returning_customer: '↺', stalled_buy: '◔', intent2_buy: '◆',
                      voice_to_hear: '♪', file_to_read: '▭', video_to_watch: '▶', story_to_thank: '☆',
                      unanswered_in_shift: '…',
                      promise_open: '◷' };
function cswHouseNum(sender) {
  // Masked-number signage: privacy as a visible law, not a policy note.
  // Only rendered when the sender id actually is a phone number.
  const digits = String(sender || '').replace(/\D/g, '');
  if (digits.length < 7) return '';
  return `+${digits.slice(0, 3)} ••• ••${digits.slice(-2)}`;
}
// Status chips (stylist offer, reopened, promise, follow-up, FAQ) — ONE
// builder for the classic row AND the house ticket (operator 2026-08-09:
// "all the tags are missing (stylist, concierge etc)" — the book's cards
// carried none of them; tap handlers are delegation via closest(), so
// the same markup works on both hosts).
function cswStatusChips(t) {
  const s = cswState();
  return [
    // The ball (PR-B1): the single-holder state every surface reads the
    // same way — violet is EON's hand, and a teammate pill means EON has
    // yielded. Absent ball = free thread, no chip at all.
    (t.ball && t.ball.kind === 'eon') ? `<span class="csw-eonball" title="${esc(t.ball.note || 'EON is handling this conversation — open to watch or take over')}"><i></i>EON is chatting</span>` : '',
    (t.ball && t.ball.kind === 'rep') ? `<span class="csw-repball" title="A teammate holds this thread">${esc((String(t.ball.holder || '').split('@')[0]) || 'rep')} is on it</span>` : '',
    (() => { const so = cswStylOfferFor(t); return so ? `<span class="csw-styloffer" data-k="${esc(String(so.recipient || '').replace(/\D/g, ''))}" title="A stylist offer is ready for this buyer — tap to review and send">✦ Stylist offer</span>` : ''; })(),
    (() => { const r = (s._reopened || {})[t.zoko_customer_id]; return (r && Date.now() - r < 86400000) ? '<span class="csw-reopench" title="Resolve refused — the customer’s message is still the last word">reopened — never answered</span>' : ''; })(),
    t._promise ? `<span class="csw-promisechip ${(t._promiseM || 0) >= CSW_PROMISE_OVERDUE_MIN ? 'csw-promisechip--late' : ''}" title="We promised this customer a follow-up and nothing has gone out since — open the chat and send them the answer">waiting on feedback · ${cswFmtWait(t._promiseM)}</span>` : '',
    (_cswFollowups && _cswFollowups.has(t.zoko_customer_id)) ? `<span class="csw-fupchip" data-fup="${esc(t.zoko_customer_id)}" title="Buyer waiting past the 24h window — tap to send the pickup template with their own last words">⏱ Follow up</span>` : '',
    (t.labels || []).includes('auto-answered') ? '<span class="csw-faqchip" title="EON answered this from the FAQ — open the chat to review or override">⚡ Auto-answered</span>' : '',
  ].filter(Boolean).join('');
}

// Head numbers live in the shell (csw-hq). Vet 2026-08-08: they were
// painted only inside cswRenderHouseQueue, so under a lens the drain
// instrument froze (or sat at the shell's literal "—"). ONE painter,
// called from every house render path — the head can never disagree
// with the book again.
function cswPaintHouseHead() {
  // 2026-08-09: the "The queue · N to work · M cleared" eyebrow is gone
  // (operator: "remove the queue, 0 to work") — the ring alone carries
  // the drain, and the work count moved onto the Priority lens key. Its
  // two spans went with it; this painter owns the ring only.
  const items = cswCustomerPriorityItems(_cswPrio.items);
  const done = _cswPrio.cleared || 0;
  const total = items.length + done;
  const hFrac = document.getElementById('csw-hq-frac');
  const hArc = document.getElementById('csw-hq-arc');
  // CSW-03: a book never read is "—" with no arc, never 0/0.
  if (_cswPrio.unread) {
    if (hFrac) hFrac.textContent = '—';
    if (hArc) hArc.style.strokeDashoffset = '63';
    return;
  }
  if (hFrac) hFrac.textContent = `${done}/${total}`;
  if (hArc) hArc.style.strokeDashoffset = (63 * (1 - (total ? done / total : 0))).toFixed(1);
}
function cswBusinessFollowupsHtml(items, renderCard) {
  if (!items.length) return '';
  const hasCustomerWork = cswCustomerPriorityItems(_cswPrio.items).length > 0;
  const expanded = !hasCustomerWork || document.querySelector('.csw-business-followups')?.open === true;
  return `<details class="csw-business-followups"${expanded ? ' open' : ''}><summary>Marketing and other enquiries <span class="num">${items.length}</span></summary>`
    + `<p>These enquiries still need attention from the right team. Review attached media before deciding what is needed.</p>`
    + items.map(renderCard).join('') + `</details>`;
}

function cswRenderHouseQueue(el) {
  // vet 28: everything below builds and binds into a detached box; the
  // live list is patched from it card by card at the end (cswHqPatch).
  // Anything that is not a page element (a string-only host) is still
  // written whole, as before.
  const host = el;
  const live = !!host && host.nodeType === 1;
  if (live) el = document.createElement('div');
  const s = cswState();
  const items = _cswPrio.items || [];
  const customerItems = cswCustomerPriorityItems(items);
  const done = _cswPrio.cleared || 0;
  const open = customerItems.filter(p => p.status !== 'claimed');
  const followups = items.filter(p => p.status !== 'claimed' && p.reason_key === 'business_followup');
  // A business enquiry never enters customer Priority, but claiming it must
  // not make the work disappear. The existing working strip carries its
  // named owner and Done door; customer counts still use customerItems.
  const claimed = items.filter(p => p.status === 'claimed');
  const byCid = new Map((s.threads || []).map(t => [String(t.zoko_customer_id), t]));
  cswPaintHouseHead();
  const meL = (cswMyEmail() || '').toLowerCase();
  // R6 (2026-08-08): past 48h the number speaks in days — "71:54" reads
  // as minutes at a glance when it is really three days of silence.
  // 2026-08-09 (vet): the SAME misread ran from 1h to 48h — a 46h29m wait
  // printed "46:31", which on a shop floor reads as 46 minutes. Every wait
  // over an hour now names its unit; only sub-hour waits stay bare minutes.
  const fmtW = (m) => !m ? '' : (m >= 2880
    ? `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`
    : (m >= 60
      ? `${Math.floor(m / 60)}h ${m % 60}m`
      : `${m}m`));
  // The book's reason_text already ends with the server's own "· waiting
  // 46h 29m" on every card, and the foot prints the LIVE wait joined from
  // the polled thread — so each card said the same fact twice, in two
  // notations, from two clocks (one card read 261 minutes beside 03:14).
  // Keep the live one (it moves with the poll, colour-coded hot/warm) and
  // drop the stale copy — but ONLY when the foot actually has a wait to
  // show, so a card with no joined thread never loses the fact entirely.
  // TWO shapes, because reason_text is not one template. The ranked reasons
  // append the server's own "· waiting 46h 29m"; stalled_buy's line is
  // sweep-WRITTEN DATA whose tail reads "— no shop reply, 261 minutes
  // waiting" — reversed order, spelled-out unit. A single-shape strip left
  // that card showing "261 minutes" beside a foot of "3h 14m" (194 min),
  // which is the very contradiction this fix exists to remove (caught by
  // the design verifier — my first pattern and my first test BOTH used a
  // shortened string and never met the live shape). Both patterns are
  // anchored to the END so they can never eat the reason itself: the
  // stalled_buy strip removes the duplicated NUMBER and keeps "no shop
  // reply", which is a fact the foot does not carry.
  const _WAIT_TAILS = [
    /\s*[·・]\s*waiting\s+\d+\s*[dhm](\s+\d+\s*[hm])?\s*$/i,
    /\s*[,;—-]?\s*\d+\s*(?:minutes?|mins?|hours?|hrs?|days?)\s+waiting\s*$/i,
  ];
  const reasonOf = (p, hasFootWait) => {
    let rt = String(p.reason_text || '');
    if (!hasFootWait) return rt;
    _WAIT_TAILS.forEach((re) => { rt = rt.replace(re, ''); });
    return rt;
  };
  const nameOf = (p) => {
    const t = byCid.get(String(p.zoko_customer_id));
    return t ? cswHouseName(t) : cswHouseMask(p.customer_name || p.platform_sender_id || 'Customer');
  };
  const ticket = (p) => {
    const t = byCid.get(String(p.zoko_customer_id));
    const w = t ? (t._waitm || 0) : 0;
    const wCls = p.reason_key === 'business_followup' ? '' : (w >= 120 ? ' hot' : (w >= 30 ? ' warm' : ''));
    // Operator 2026-08-09 ("all the tags are missing"): the card wears the
    // SAME tags as the classic row — cswTierChip covers concierge too (the
    // inline gold/vip pair silently dropped the band ABOVE gold, the exact
    // mig-645 trap), and the status chips ride one shared builder.
    const tier = t ? cswTierChip(t.tier) : '';
    // L2: the ticket wears the SAME budgeted chip line as the classic row
    // — money, window and whale finally reach the default skin.
    const chips = cswRowChips(t);
    const foot = fmtW(w);
    return `<div class="csw-hticket" data-open="${esc(p.zoko_customer_id || '')}" data-card="${esc(p.id)}" tabindex="0" role="button" aria-label="Open ${esc(nameOf(p))}">
      <div class="csw-hticket__top"><span class="csw-hticket__ch${t && t._plat === 'ig' ? ' ig' : ''}"></span><b>${esc(nameOf(p))}</b>${tier}<span class="csw-draftlbl csw-draftmark" data-csw-draft="${esc(p.zoko_customer_id || '')}" title="Draft saved on this device" aria-label="Draft saved on this device" ${cswLoadDraft(p.zoko_customer_id).trim() ? '' : 'hidden'}>✎</span></div>
      <div class="csw-hticket__why"><span class="csw-hticket__i">${CSW_HQ_ICON[p.reason_key] || '•'}</span>${esc(reasonOf(p, !!foot))}</div>
      ${/* WS-1 (his 8-chat priority): the card carries the customer's own
         last words BESIDE the reason — a rep reads the question without
         opening the thread. Both facts fit; classic's density, house's why. */''}
      ${t ? (() => { const pv = cswPreview(t); return `<div class="csw-hticket__prev ${pv.muted ? 'muted' : ''}">${pv.html}</div>`; })() : ''}
      ${chips ? `<div class="csw-thread__chips">${chips}</div>` : ''}
      ${cswServiceMeta(t, false)}
      <div class="csw-hticket__foot">
        <span class="csw-hticket__wait num${wCls}" style="white-space:nowrap">${foot}</span>
        ${/* Work that happened OFF the desk — a phone call, a walk-in, another
           rep's own line — still has to leave the book, or the drain ring
           can never be moved by the person doing the work and the
           "nobody has picked this up" note keeps accusing them. Classic
           always had this key; house shipped without it (vet 2026-08-09). */''}
        ${/* The "no reply needed" door (2026-08-20): closes the THREAD
           (force-resolve + audit note) and clears this card in one POST —
           Done alone only cleared the card, so the feeders re-minted it
           next rebuild. Same key class as Done (no cs.css edits); inline
           min-height keeps the 44px tap floor. */''}
        ${/* vet A10 (2026-09-26): ONE close key replaces "No reply needed"
           and "Done" (cswCloseCard): it closes the chat and clears the
           card, and both reopen when the customer writes. A contact check
           keeps its own door (its key opens the check, as Done did). The
           close key breaks after "reopens" so it reads as two even lines,
           and the wait and "I'm on it" stay on one line each (measured in
           the review build: without the break the long label squeezed
           "I'm on it" and the wait onto two lines each). */''}
        ${/* Adversary on #3559 round 2: a business enquiry (open PR #3507's
           'business_followup' cards) still needs the right team after the
           rep routes it, so its key clears the card and leaves the chat
           open; closing the chat would drop it out of the lists. */''}
        ${p.service_obligations?.length ? `<button class="csw-hclaim" data-prio="${esc(p.id)}" data-act="claimed" type="button">Open case</button>` : `${p.contact_task_id
          ? `<button class="csw-hdone" data-prio="${esc(p.id)}" data-act="cleared" type="button">Record the check</button>`
          : p.reason_key === 'business_followup'
          ? `<button class="csw-hdone" data-prio="${esc(p.id)}" data-act="cleared" type="button" style="min-height:44px" title="Clears the card. The chat stays open for the team that answers it.">Done</button>`
          : `<button class="csw-hdone" data-noreply="${esc(p.id)}" data-cid="${esc(p.zoko_customer_id || '')}" type="button" style="min-height:44px" title="Nothing more to do here: closes the chat and clears the card. Both come back if the customer writes again.">Close — reopens <br>if they write again</button>`}
        <button class="csw-hclaim" data-prio="${esc(p.id)}" data-act="claimed" type="button" style="white-space:nowrap">I'm on it</button>`}
      </div>
    </div>`;
  };
  const working = claimed.map(p => {
    const mine = (p.claimed_by || '').toLowerCase() === meL;
    return `<div class="csw-hworking" data-open="${esc(p.zoko_customer_id || '')}" data-card="${esc(p.id)}" tabindex="0" role="button">
      <span><b>${esc(nameOf(p))}</b> — ${p.service_obligations?.length ? (p.owner_accepted_at?'case owner accepted':'case needs an accepted owner') : mine ? 'you’re on it' : esc(((p.claimed_by || '').split('@')[0] || 'claimed') + ' is on it')}</span>
      ${/* A claimed card had no exit at all: the only way off the strip was
         the shift rolling over. Whoever is on it can now finish it. */''}
      ${/* Adversary on #3559 round 2: a card reaches this strip once a rep
         answers its customer or taps "I'm on it" (vet 17; since the Fable
         5.1 vet, 27 Sep, opening a card alone claims nothing). Its key was a
         bare Done that cleared the card and left the chat open, the very
         pair A10 removed from the rail. It is now the same close key. A
         service case, a contact check (always minted claimed, so this
         strip is where it lives) and a business enquiry (open PR #3507)
         keep their own doors. */''}
      ${p.service_obligations?.length ? `<button class="csw-hworking__done" data-prio="${esc(p.id)}" data-act="cleared" type="button" title="Open or update this work">Open case</button>`
        : p.contact_task_id ? `<button class="csw-hworking__done" data-prio="${esc(p.id)}" data-act="cleared" type="button">Record the check</button>`
        : p.reason_key === 'business_followup' ? `<button class="csw-hworking__done" data-prio="${esc(p.id)}" data-act="cleared" type="button" title="Clears the card. The chat stays open for the team that answers it.">Done</button>`
        : `<button class="csw-hworking__done" data-noreply="${esc(p.id)}" data-cid="${esc(p.zoko_customer_id || '')}" type="button" title="Nothing more to do here: closes the chat and clears the card. Both come back if the customer writes again.">Close — reopens <br>if they write again</button>`}
      <span class="csw-hworking__chip">${p.contact_task_id?`check due ${esc(cswContactWhen(p.due_at))}`:'open thread'}</span>
    </div>`;
  }).join('');
  // Honesty footer, queue edition: the book deliberately declines quiet
  // threads — count them, never vanish them. Search still reaches all.
  const inBook = new Set(items.map(p => String(p.zoko_customer_id)));
  // The footer must count what the LIST would show but the book declined —
  // so it uses the list's own visibility rule (drop resolved AND read),
  // not "drop everything resolved". With the blunt version a REOPENED
  // thread (resolved, then the customer spoke again → unread) was in
  // neither the book nor this count while still rendering in the list:
  // 3 live threads that the honesty footer silently failed to own
  // (vet 2026-08-09; csw-reopench exists for exactly this class).
  const quiet = (s.threads || []).filter(t => !t._also_stub
    && !(t.cs_status === 'resolved' && !t.unread)
    && !inBook.has(String(t.zoko_customer_id))).length;
  const rail = open.length ? open.map(ticket).join('')
    // CSW-03: a book never read is not a clear board (painted below).
    : _cswPrio.unread ? `<div class="csw-empty" data-prio-unread="1"></div>`
    : followups.length
      ? `<div class="csw-empty csw-empty--enquiries"><div class="csw-empty__m">↗</div>`
        + `Customer Priority is clear. ${followups.length} ${followups.length === 1 ? 'enquiry needs' : 'enquiries need'} routing below.`
        + `<div class="csw-empty__s">Open the enquiry and send it to the right team.</div></div>`
    : `<div class="csw-empty"><div class="csw-empty__m">✓</div>`
      + (done ? `Book clear — ${done} handled this shift.` : `No cards on this shift's board.`)
      + `<div class="csw-empty__s">Quieter threads stay in the inbox — search finds them.</div>` + cswEmptyBoardTruth() + `</div>`;
  // R4 (2026-08-08): the shared fact said once, at book level — the
  // per-card "shift N of waiting" suffix is gone server-side. rank < 0
  // = an escalated money/anger card (queue_now counts them too).
  const neg = _cswPrio.neglected || 0;
  const note = neg > 0
    ? `<div class="csw-hnote">${neg} waiting across shifts — nobody has picked ${neg === 1 ? 'this' : 'these'} up</div>`
    : '';
  // Lane P: the shift-start landing line (set by cswPrioLandOnShift for
  // this shift, cleared by the rep's own next lens/tab) and the response
  // clock under it. N is the live book, never a frozen count.
  const land = s.prioLandNote
    ? `<div class="csw-hnote csw-hnote--land">Your shift's book — ${customerItems.length
        ? `${customerItems.length} customer request${customerItems.length === 1 ? '' : 's'}`
        : `${followups.length} ${followups.length === 1 ? 'enquiry' : 'enquiries'} to route`}</div>`
    : '';
  const clock = cswClockHtml();
  // The house EON tab is the private line's entry point; no pinned duplicate.
  const repDesk = !!(typeof sosState !== 'undefined' && sosState && sosState.cur);
  el.innerHTML = (repDesk ? '' : cswEonRowHtml()) + note + land + clock + working + rail + cswBusinessFollowupsHtml(followups, ticket)
    + (quiet > 0 ? `<div class="csw-hiddenfoot">${quiet} quieter conversation${quiet === 1 ? '' : 's'} not shown — search finds them</div>` : '');
  const unreadBox = el.querySelector('.csw-empty[data-prio-unread]');
  if (unreadBox) _cswPrio.unread(unreadBox);
  cswBindEonRow(el);
  // WS-1 second half: the book's cards no longer end the fold. The quiet-
  // but-real conversations render as dense rows UNDER the rail — name,
  // preview, wait, chips — instead of hiding whole behind the footer
  // count. The honesty footer now owns only the remainder past the cap.
  {
    const quietThreads = (s.threads || []).filter(t => !t._also_stub
      && !(t.cs_status === 'resolved' && !t.unread)
      && !inBook.has(String(t.zoko_customer_id)));
    // L2 (2026-08-30): the 12 slots go to threads that OWE a reply first.
    // A PARTITION, not a re-sort — each group keeps the feed's own
    // freshest-first order (operator 2026-07-26: longest-first put
    // week-dead threads on top and buried the recoverable ones). Before
    // this, 11 of 12 slots went to threads where the rep spoke last.
    const shown = quietThreads.filter(t => t._waiting)
      .concat(quietThreads.filter(t => !t._waiting)).slice(0, 12);
    const dense = (t) => {
      const w = t._waitm || 0;
      const pv = cswPreview(t);
      const chips2 = cswRowChips(t);
      // Same colour language as the rest of the desk (rules already at
      // cs.css .csw-time--soon/--late under the house layer): amber ≥30m,
      // red ≥2h, and only while a reply is genuinely owed.
      const wCls = t._waiting ? (w >= 120 ? ' csw-time--late' : (w >= 30 ? ' csw-time--soon' : '')) : '';
      return `<button class="csw-thread csw-thread--dense" data-open="${esc(t.zoko_customer_id)}" type="button">
        <div class="csw-thread__main">
          <div class="csw-thread__top"><div class="csw-thread__name">${esc(cswHouseName(t))}${cswTierChip(t.tier)}</div><div class="csw-thread__time csw-time--wait${wCls}">${fmtW(w)}</div></div>
          <div class="csw-thread__bot"><div class="csw-thread__prev ${pv.muted ? 'muted' : ''}">${pv.html}</div>
          ${t.unread_count >= 1 ? `<span class="csw-unread csw-unread--n">${t.unread_count > 99 ? '99+' : t.unread_count}</span>` : ''}</div>
          ${chips2 ? `<div class="csw-thread__chips">${chips2}</div>` : ''}
          ${cswServiceMeta(t)}
        </div>
      </button>`;
    };
    const rest = quietThreads.length - shown.length;
    const foot = el.querySelector('.csw-hiddenfoot');
    // Rows land FIRST, against a still-attached anchor; only then may the
    // footer shrink or leave. The reverse order detached the anchor and
    // threw for every quiet count 1–12 — the whole band the cap serves —
    // killing all tap bindings below (design verifier, 2026-08-10).
    if (shown.length) {
      if (foot) foot.insertAdjacentHTML('beforebegin', shown.map(dense).join(''));
      else el.insertAdjacentHTML('beforeend', shown.map(dense).join(''));
    }
    if (foot) {
      if (rest > 0) foot.textContent =
        `${rest} quieter conversation${rest === 1 ? '' : 's'} not shown — search finds them`;
      else foot.remove();
    }
  }
  el.querySelectorAll('.csw-hticket, .csw-hworking, .csw-thread--dense').forEach(card => {
    // Opening a card reads it and claims nothing (Fable 5.1 vet, 27 Sep):
    // the rep's first send (cswMarkReplied) or "I'm on it" claims it.
    const openCard = () => {
      if (!card.dataset.open) return;
      cswOpenThread(card.dataset.open);
    };
    card.addEventListener('click', (e) => {
      if (e.target.closest('[data-prio],[data-noreply]')) return;   // the keys handle themselves
      // The card's tags act exactly as they do on a classic row — the
      // stylist chip opens the offer sheet, the follow-up chip sends the
      // pickup; only a plain tap opens the thread.
      const oc = e.target.closest && e.target.closest('.csw-styloffer');
      if (oc) { const p = _cswStylOffers.get(oc.dataset.k || ''); if (p) { e.stopPropagation(); cswStylOfferSheet(p); return; } }
      const fc = e.target.closest && e.target.closest('.csw-fupchip');
      if (fc) { e.stopPropagation(); cswSendFollowup(fc); return; }
      openCard();
    });
    // vet 28: Enter AND Space open a focused card (role="button"). A key
    // pressed on one of the card's own buttons stays with that button, and
    // a real <button> row already turns both keys into its click.
    card.addEventListener('keydown', (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && e.target === card && card.tagName !== 'BUTTON') {
        e.preventDefault();
        openCard();
      }
    });
  });
  // Done tears the card off the rail (or the working strip) the same way a
  // claim does, and routes the SAME endpoint with the button's own act —
  // never a hardcoded status, which is how house lost 'cleared' entirely.
  // [data-prio] narrows the selector: the close key shares the csw-hdone
  // class (existing classes only) but carries [data-noreply] and its own
  // binding below — one tap must never fire both handlers.
  // vet A10: on the rail this is now only a contact check's "Record the
  // check". A key that opens its card's own door (the check's dialog, a
  // service case's sheet) leaves the card where it is, so it does not tear.
  // Round 2 adversary: the strip's close key carries [data-noreply] too,
  // so both selectors name their attribute.
  el.querySelectorAll('.csw-hdone[data-prio], .csw-hworking__done[data-prio]').forEach(b => {
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      const tk = b.closest('.csw-hticket') || b.closest('.csw-hworking');
      const card = (_cswPrio.items || []).find(p => p.id === b.dataset.prio) || { id: b.dataset.prio };
      const tearing = tk && !cswCardHasOwnDoor(card) && !(window.EONHouse && window.EONHouse.reduced);
      if (tearing) { tk.classList.add('hs-tearing'); tk._cswTornAt = Date.now(); }
      cswPrioTap(b.dataset.prio, b.dataset.act || 'cleared', tearing ? 470 : 0);
    });
  });
  el.querySelectorAll('.csw-hdone[data-noreply], .csw-hworking__done[data-noreply]').forEach(b => {
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      const tk = b.closest('.csw-hticket') || b.closest('.csw-hworking');
      const tearing = tk && !(window.EONHouse && window.EONHouse.reduced);
      // Tear on SUCCESS only (vet S14): tearing on the tap left the
      // ticket torn off the rail after a failed POST until the next
      // poll. The claim/Done keys keep their tap-tear — their POST path
      // reloads on failure too; this one returns and repaints.
      cswCloseCard(b.dataset.cid, b.dataset.noreply, tearing ? 470 : 0)
        .then((ok) => { if (ok && tearing && tk.isConnected) { tk.classList.add('hs-tearing'); tk._cswTornAt = Date.now(); } });
    });
  });
  // The tear IS the claim feedback — fire it on the tap, then let the
  // book reload move the card into the working strip. Reduced motion
  // claims without the animation, exactly like the thread-list tear.
  // Round 2 adversary: a key that opens its card's own door (a service
  // case's "Open case", a contact check) leaves the card on the rail.
  el.querySelectorAll('.csw-hclaim').forEach(b => {
    b.addEventListener('click', () => {
      const tk = b.closest('.csw-hticket');
      const own = cswCardHasOwnDoor((_cswPrio.items || []).find(p => p.id === b.dataset.prio) || { id: b.dataset.prio });
      const tearing = tk && !own && !(window.EONHouse && window.EONHouse.reduced);
      if (tearing) { tk.classList.add('hs-tearing'); tk._cswTornAt = Date.now(); }
      cswPrioTap(b.dataset.prio, b.dataset.act || 'claimed', tearing ? 470 : 0);
      // vet 17: "I'm on it" also opens the chat it just claimed. A service
      // case's "Open case" key and a contact check open their own door
      // inside cswPrioTap, so the chat stays shut under it.
      if (tk && tk.dataset.open && !own) cswOpenThread(tk.dataset.open);
    });
  });
  if (live) cswHqPatch(host, el);
}

function cswRenderTeamList(el) {
  const s = cswState();
  const q = (s.search || '').trim().toLowerCase();
  const threads = (s.teamThreads || []).filter(t => {
    if (!q) return true;
    return (`${t.name} ${t.last}`).toLowerCase().includes(q);
  });
  let h = `<button class="csw-thread csw-teamnew" data-teamnew="1">
    <div class="csw-eonav csw-teamnew__ic">@</div>
    <div class="csw-thread__main"><div class="csw-thread__top">
      <div class="csw-thread__name">New team message</div></div>
      <div class="csw-thread__bot"><div class="csw-thread__prev muted">Tag a teammate or a department</div></div>
    </div></button>`;
  if (!threads.length) {
    el.innerHTML = h + `<div class="csw-empty"><div class="csw-empty__m">@</div>No team threads yet. Use @ to ask a teammate.</div>`;
    cswBindTeamRows(el);
    return;
  }
  const prevScroll = el.scrollTop;
  h += threads.map(t => {
    const on = (s.active === ('team:' + t.thread_id)) ? 'on' : '';
    const when = t.tm ? formatRelativeShort(t.tm) : '';
    const meta = cswTeamRowMeta(t);
    return `<button class="csw-thread csw-thread--team ${on} ${t.un ? 'unread' : ''} ${t.kind === 'group' ? 'csw-thread--room' : ''}" data-tid="${esc(t.thread_id || '')}">
      ${cswAvatar(t.name, { seed: t.seed })}
      <div class="csw-thread__main">
        <div class="csw-thread__top"><div class="csw-thread__name">${esc(t.name)}${meta.badge}</div><div class="csw-thread__time">${esc(when)}</div></div>
        <div class="csw-thread__bot">
          <div class="csw-thread__prev">${meta.sub && t.kind === 'group' ? `<span class="csw-roomsub">${esc(meta.sub)}</span> ` : ''}${esc(t.last || '')}</div>
          ${t.un >= 1 ? `<span class="csw-unread csw-unread--n">${t.un > 99 ? '99+' : t.un}</span>` : ''}
        </div>
      </div>
    </button>`;
  }).join('');
  el.innerHTML = h;
  el.scrollTop = prevScroll;
  cswBindTeamRows(el);
}

function cswBindTeamRows(el) {
  el.querySelector('.csw-teamnew')?.addEventListener('click', () => cswNewTeamThread());
  el.querySelectorAll('.csw-thread--team').forEach(b => {
    b.addEventListener('click', () => cswOpenTeamThread(b.dataset.tid));
  });
}

// Open a team thread in the SAME conversation pane as a customer thread.
// kind:'team' marks the conv so the composer posts to staff_chat and messages
// render as DM rows with the sender's staff name (never customer PII).
// Quiet refresh of the OPEN team thread. Staff↔staff conversations were
// excluded from every poll (2026-07-25 vet): a rep with the thread open saw
// nothing new until they backed out and re-entered. Repaints only when the
// message set actually changed, so the composer and scroll stay put.
async function cswRefreshTeamThread() {
  const s = cswState();
  const ta = s.teamActive;
  if (!ta || !ta.thread_id || String(s.active) !== 'team:' + ta.thread_id) return;
  // THE CHIP IS LIVE — it must move when the ASK moves, and an ask is
  // acknowledged and closed without anybody sending another message. So
  // the chip refresh runs on every tick, BEFORE the no-new-message early
  // return below: hanging it off a new-message signature would have
  // frozen every chip at the state it was minted in.
  rlwLoadChips(ta.thread_id);
  try {
    const res = await api('/api/staffos/chat/thread?staff_id=' + encodeURIComponent(sosState.cur)
      + '&thread_id=' + encodeURIComponent(ta.thread_id));
    if (String(s.active) !== 'team:' + ta.thread_id) return;   // switched away
    const next = (res && res.convo) || [];
    const sig = next.length + ':' + (next.length ? JSON.stringify(next[next.length - 1]).length : 0);
    if (sig === ta._sig) return;              // nothing new — no repaint
    ta._sig = sig;
    ta.convo = next;
    ta.suggest = (res && res.suggest) || ta.suggest;
    cswPaintTeamConv();
  } catch (_) { /* transient — the next tick tries again */ }
}

async function cswOpenTeamThread(tid) {
  const s = cswState();
  const t = (s.teamThreads || []).find(x => x.thread_id === tid);
  if (!t) return;
  s.active = 'team:' + tid;
  s.activeThread = null;
  s.teamActive = { thread_id: tid, other_name: t.name, kind: t.kind || 'dm',
                   convo: [], suggest: '' };
  document.getElementById('csw-inbox')?.classList.add('has-active');
  cswTabbarSync();
  cswRenderThreadList();
  cswRenderTeamConvShell(t.name);
  // fetch the full convo (stamps read server-side) then paint.
  try {
    const res = await api('/api/staffos/chat/thread?staff_id=' + encodeURIComponent(sosState.cur)
      + '&thread_id=' + encodeURIComponent(tid));
    if (s.active !== 'team:' + tid) return;   // switched away mid-fetch
    s.teamActive.convo = (res && res.convo) || [];
    s.teamActive.suggest = (res && res.suggest) || '';
    // The server is authoritative on kind + membership count.
    if (res && res.kind) s.teamActive.kind = res.kind;
    if (res && res.members) s.teamActive.members = res.members;
  } catch (_) { /* keep the empty pane rather than an error */ }
  // opening stamps read — zero the local badge + refresh.
  t.un = 0;
  try { if (typeof sosBuildTabbar === 'function') sosBuildTabbar(); } catch (_) {}
  cswPaintTeamConv();
  cswRenderThreadList();
  // THE RELAY's chips for this room — fire-and-forget, never awaited:
  // the transcript is what the staffer came for, and a dark lane answers
  // {armed:false} and paints nothing. FORCED: opening a room is the one
  // moment a thread with no chips is worth asking about again, and every
  // send lands here, so a freshly minted ask still chips immediately.
  rlwLoadChips(tid, true);
}

// Start a fresh team thread (no thread_id yet — the first @mention sets the
// recipient(s) server-side on send).
function cswNewTeamThread() {
  const s = cswState();
  s.active = 'team:new';
  s.activeThread = null;
  s.teamActive = { thread_id: null, other_name: 'New team message', convo: [], suggest: '' };
  document.getElementById('csw-inbox')?.classList.add('has-active');
  cswTabbarSync();
  cswRenderThreadList();
  cswRenderTeamConvShell('New team message');
  cswPaintTeamConv();
}

// The conversation shell for a TEAM thread — same #csw-conv surface + the same
// composer pattern as the customer pane, minus the customer-only tools (360,
// route, templates, pay link). Carries the @-mention picker.
function cswRenderTeamConvShell(name) {
  const conv = document.getElementById('csw-conv');
  if (!conv) return;
  conv.classList.add('show');
  conv.innerHTML = `
    <div class="csw-conv__bar">
      <button class="csw-conv__back" id="csw-team-back" aria-label="Back">‹</button>
      ${cswAvatar(name || 'Team', { size: 'sm', seed: name })}
      <div class="csw-conv__id">
        <div class="csw-conv__name">${esc(name || 'Team')}</div>
        <div class="csw-conv__sub"><span class="csw-conv__scroll"><span class="csw-ch csw-ch--team">Team</span></span></div>
      </div>
    </div>
    <div class="csw-script" id="csw-team-script"><div class="csw-empty"><div class="csw-empty__m">@</div>Loading…</div></div>
    <div class="csw-composer">
      <div class="csw-team-eonctx" id="csw-team-eonctx" hidden></div>
      <div class="csw-inputbox" id="csw-team-inputbox">
        <button class="csw-plus" id="csw-team-at" aria-label="Mention">@</button>
        <div class="csw-mention" id="csw-team-mention" hidden></div>
        <textarea class="csw-input" id="csw-team-input" placeholder="Message — @ to mention, or @sales·palms to send a job" rows="1" enterkeyhint="send"></textarea>
        <input type="file" id="csw-team-file" accept="image/*" style="display:none">
        <button class="csw-mic" id="csw-team-photo" aria-label="Send a photo" title="Send a photo">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="6" width="18" height="14" rx="2.5" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="13" r="3.4" stroke="currentColor" stroke-width="2"/><path d="M8 6l1.6-2h4.8L16 6" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>
        </button>
        <button class="csw-send" id="csw-team-send" aria-label="Send">
          <svg viewBox="0 0 24 24" fill="none"><path d="M4 12l16-8-6 16-2.5-6.5L4 12z" fill="currentColor"/></svg>
        </button>
      </div>
      <div class="csw-hint">${CSW_TEAM_COMPOSER_HINT}</div>
    </div>`;
  // Photo into a staff thread or room (mig 501) — staff chat was text-only,
  // so a floor rep photographing a damaged item had to detour via the
  // customer desk. Same confirm-free flow as the picker: pick, upload, post.
  const tf = document.getElementById('csw-team-file');
  document.getElementById('csw-team-photo')?.addEventListener('click', () => tf?.click());
  tf?.addEventListener('change', async () => {
    const f = tf.files && tf.files[0];
    const st = cswState();
    const tid = st.teamActive && st.teamActive.thread_id;
    if (!f || !tid) return;
    const btn = document.getElementById('csw-team-photo');
    if (btn) btn.disabled = true;
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('sender_staff_id', sosState.cur || '');
      fd.append('thread_id', tid);
      fd.append('caption', (document.getElementById('csw-team-input')?.value || '').trim());
      const headers = { 'Authorization': `Bearer ${State.jwt}` };
      if (State.tenant) headers['X-Tenant-Slug'] = State.tenant.slug;
      const r = await fetch(API + '/api/staffos/chat/photo', { method: 'POST', headers, body: fd });
      if (!r.ok) {
        const b = await r.json().catch(() => null);
        throw new Error((b && (b.detail && (b.detail.error || b.detail))) || ('upload failed (' + r.status + ')'));
      }
      const inp = document.getElementById('csw-team-input');
      if (inp) { inp.value = ''; inp.dispatchEvent(new Event('input')); }
      cswRefreshTeamThread();
    } catch (e) {
      cswToast(cswSayWhy(e, { lead: 'The photo did not send.' }), true);
    } finally {
      if (btn) btn.disabled = false;
      tf.value = '';
    }
  });

  document.getElementById('csw-team-back')?.addEventListener('click', () => {
    const s = cswState();
    s.active = null; s.teamActive = null;
    document.getElementById('csw-inbox')?.classList.remove('has-active');
    cswTabbarSync();
    conv.classList.remove('show');
    cswRenderThreadList();
  });
  document.getElementById('csw-team-at')?.addEventListener('click', cswTeamMentionOpen);
  const inp = document.getElementById('csw-team-input');
  inp?.addEventListener('input', () => cswTeamMentionOnInput(inp.value));
  // ↵ sends, ⇧↵ newlines — matches the EON-main composer (and the hint line).
  inp?.addEventListener('keydown', e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); cswTeamSend(); } });
  document.getElementById('csw-team-send')?.addEventListener('click', cswTeamSend);
}

// Paint the team conversation messages (DM rows, sender name on inbound).
// Stable per-sender colour index (1-6) so each voice in a room is
// distinguishable at a glance — Telegram's group-chat convention. Reuses the
// existing cswHash so the same person is always the same colour.
function cswWhoColor(seed) {
  return (cswHash(String(seed || '?')) % 6) + 1;
}

// ══ THE TEAM WIRE · an @ becomes an ask ═══════════════════════════
// Design contract: the rooms artifact — "@dept·store is an address, not
// a broadcast. It resolves to the on-shift person and mints an ask; the
// thread gets a live chip, not fourteen phones buzzing. Closure returns
// to the room that asked."
//
// THIS IS THE STAFF TEAM LENS, NOT THE CS REP DESK. Floor 3 is excluded
// from this contract by operator order: nothing here touches the
// customer pane, its painter or its stylesheet.
//
// DARK PAINTS NOTHING. The chips only exist when the server answers
// armed:true; `a.relayChips` is undefined otherwise and the transcript
// is the transcript it always was.
// Threads the server has answered `chips: []` for. THE TICK'S JOB IS TO
// MOVE A CHIP THAT EXISTS — an ask is acknowledged and closed in silence,
// so a thread WITH chips must keep being asked. A thread that has never
// had one has nothing to move, and asking anyway cost a request every
// 10-20 seconds, per staffer, forever, for a room the relay has never
// touched. Opening the thread asks again (`force`), and every send ends
// in cswOpenTeamThread — so the chip for an ask minted from this room
// appears on the same round trip it always did.
const _rlNone = new Set();

async function rlwLoadChips(tid, force) {
  const s = cswState();
  if (!tid) return;
  if (!force && _rlNone.has(tid)) return;
  let d = null;
  try {
    d = await api('/api/staffos/relay/wire?thread_id=' + encodeURIComponent(tid)
      + '&staff_id=' + encodeURIComponent(sosState.cur || ''));
  } catch (_) { return; }
  const a = s.teamActive;
  if (!a || a.thread_id !== tid) return;      // switched away mid-fetch
  if (!d || d.armed !== true || !d.ok) return;
  a.relayChips = d.chips || [];
  if (a.relayChips.length) _rlNone.delete(tid); else _rlNone.add(tid);
  cswPaintTeamConv();
}

// One chip. It carries the short id, who owns it, the clock and the
// state — and it MOVES: the room that asked is told when the ask is
// acknowledged and when it closes, so nobody screenshots a task page
// into chat again.
function rlwChipHTML(c) {
  // THE CLOCK IS THE STATE while the ask is live — it already reads
  // "14m to ack" or "acked", so a chip repeating either is the same
  // word twice (measured in the harness 2026-08-13: the acked chip
  // rendered "acked · acked"). Only CLOSURE gets its own chip, because
  // closure is the one state a clock cannot say — and it is the whole
  // point of the wire: the room that asked is told, in place.
  const state = c.closed
    ? '<span class="rlw-chip__st rlw-chip__st--back">closed · returned here</span>'
    : '';
  const to = [c.dept, c.store_name].filter(Boolean).map(esc).join(' · ');
  return `<div class="rlw-chip">
    <div class="rlw-chip__no"><span class="rlw-chip__tid">${esc(c.short_id || '')}</span>
      <span>minted from this thread</span></div>
    <p>${esc(c.title || 'Ask')}${to ? ' · to ' + to : ''}</p>
    <div class="rlw-chip__row">
      ${c.owner ? `<span class="rlw-chip__own">owner: ${esc(c.owner)}</span>` : '<span class="rlw-chip__own">not routed yet</span>'}
      ${relayClockHTML(c)}${state}
      <a class="rlw-chip__open" href="${esc(c.url || '#')}">open the task</a>
    </div></div>`;
}

// Chips are placed in the transcript BY TIME — an ask is minted
// milliseconds after the message that asked for it, so it lands
// immediately under that message without the chat module ever having to
// return a message id (which it does not).
function rlwMerge(parts, convo, chips) {
  if (!chips || !chips.length) return parts;
  const times = convo.map(m => (m && !Array.isArray(m) && m.tm)
    ? Date.parse(m.tm) : NaN);
  const at = {};
  chips.forEach(c => {
    const t = Date.parse(c.created_at || '');
    // THE FOOT IS THE FALLBACK, NEVER THE HEAD. This used to reset to 0
    // the moment the chip's own time parsed, and only climb on a
    // message it could compare against — so a transcript in the LEGACY
    // TUPLE SHAPE (arrays, which carry no `tm`; cswPaintTeamConv still
    // tolerates them) and an ask stamped a second early by ordinary
    // clock skew both rendered the chip ABOVE every message in the room.
    // A chip's whole job is to sit under the words that minted it; when
    // it cannot know where that is, the end of what is on screen is the
    // honest answer.
    let idx = parts.length;
    if (!isNaN(t)) {
      let after = -1;
      for (let i = 0; i < times.length && i < parts.length; i++) {
        if (!isNaN(times[i]) && times[i] <= t) after = i;
      }
      if (after >= 0) idx = after + 1;
    }
    (at[idx] = at[idx] || []).push(rlwChipHTML(c));
  });
  const merged = [];
  for (let i = 0; i <= parts.length; i++) {
    (at[i] || []).forEach(h => merged.push(h));
    if (i < parts.length) merged.push(parts[i]);
  }
  return merged;
}

function cswPaintTeamConv() {
  const s = cswState();
  const a = s.teamActive || {};
  const body = document.getElementById('csw-team-script');
  if (!body) return;
  const convo = a.convo || [];
  if (!convo.length) {
    body.innerHTML = `<div class="csw-empty"><div class="csw-empty__m">@</div>No messages yet — say hello.</div>`;
  } else {
    body.innerHTML = rlwMerge(convo.map(m => {
      // staff_chat.get_thread returns OBJECTS {me, body, tm, eon, suggest, ...};
      // tolerate the legacy tuple shape only as a denormalised fallback.
      if (Array.isArray(m)) {
        const kind = m[0];
        if (kind === 'day') return `<div class="csw-daydiv">${esc(m[1])}</div>`;
        if (kind === 'sys') return `<div class="csw-sysnote">${esc(m[1])}</div>`;
        const out = kind === 'out';
        const tm = m[2] ? `<div class="csw-bub__t">${esc(m[2])}</div>` : '';
        return `<div class="csw-row ${out ? 'out' : 'in'}"><div class="csw-bub ${out ? 'out' : 'in'}">`
          + `${(!out && m[3]) ? `<div class="csw-bub__who">${esc(m[3])}</div>` : ''}`
          + `<div class="csw-bub__txt">${esc(m[1])}</div>${tm}</div></div>`;
      }
      if (m && m.body == null && m.suggest) return '';   // suggestion-only meta row
      const out = !!(m && m.me);
      const tm = (m && m.tm) ? `<div class="csw-bub__t">${esc(cswMsgTime(m.tm))}</div>` : '';
      // Whose voice this is. A room carries a per-message sender (many people
      // speak); a DM has exactly one other party. Never any customer PII —
      // this is a staff↔staff thread either way.
      const senderName = (a.kind === 'group') ? (m && m.sender) : a.other_name;
      const who = (!out && senderName)
        ? `<div class="csw-bub__who csw-who--c${cswWhoColor(m && (m.sender_id || m.sender))}">${esc(senderName)}</div>`
        : '';
      // Photo in a staff thread (mig 501) — same lightbox as the customer lane.
      const md = m && m.media;
      const mediaHtml = (md && md.url)
        ? `<img class="csw-bub__img" src="${esc(cswImgSrc(md.url))}" data-full="${esc(cswImgSrc(md.url))}" data-cap="" alt="photo" loading="lazy">`
        : '';
      // A routed customer-chat link (cs_routing.route_to_handles): a tappable
      // chip the teammate uses to deep-open the real customer thread (name +
      // link only; the transcript opens in the customer pane, gated by E1).
      const rl = m && m.route_link;
      const link = (rl && (rl.zoko_customer_id || rl.platform_sender_id))
        ? `<button class="csw-routelink" data-rlcid="${esc(rl.zoko_customer_id || rl.platform_sender_id)}">Open customer chat${rl.customer_name ? ': ' + esc(rl.customer_name) : ''}</button>`
        : '';
      return `<div class="csw-row ${out ? 'out' : 'in'}"><div class="csw-bub ${out ? 'out' : 'in'}">`
        + `${who}${mediaHtml}<div class="csw-bub__txt">${esc(m && m.body)}</div>${link}${tm}</div></div>`;
    }), convo, a.relayChips).join('');
    body.scrollTop = body.scrollHeight;
    // Wire the deep-link chips to open the linked customer thread.
    body.querySelectorAll('.csw-routelink').forEach(b =>
      b.addEventListener('click', () => cswJumpToThread(b.dataset.rlcid)));
  }
  // EON-drafted suggestion (accept / edit, never auto-send).
  const ctx = document.getElementById('csw-team-eonctx');
  if (ctx) {
    if (a.suggest) {
      ctx.hidden = false;
      ctx.innerHTML = `EON drafted a reply. <button class="csw-team-usedraft" id="csw-team-usedraft">Use draft</button>`;
      ctx.querySelector('#csw-team-usedraft')?.addEventListener('click', () => {
        const inp = document.getElementById('csw-team-input');
        if (inp) { inp.value = a.suggest; inp.focus(); }
      });
    } else { ctx.hidden = true; ctx.innerHTML = ''; }
  }
}

// @-typeahead for the team composer — fed by the served roster (no new fetch).
// Suggests individual staff AND the department handles (a dept fans out on send).
function cswTeamMentionOpen() {
  const t = document.getElementById('csw-team-input');
  cswFetchMentionTargets();
  if (t) { t.value = (t.value || '') + '@'; t.focus(); cswTeamMentionOnInput(t.value); }
}
// Store mentions (fr dcee5a2a): '@Palms' reaches whoever is on TODAY's
// roster at that store, and the picker shows who that is right now so
// the sender knows who they are reaching. Fetched LIVE on every picker
// open (law: never a cached list — the roster changes daily), kept for
// the keystrokes of one composition only.
function cswFetchMentionTargets() {
  api('/api/staffos/chat/mention-targets')
    .then(d => { if (d && d.ok) sosState._mentionStores = d.stores || []; })
    .catch(() => {});
}
const CSW_TEAM_DEPTS = [
  ['sales', 'Sales'], ['inventory', 'Inventory'], ['logistics', 'Logistics'],
  ['customer-service', 'Customer Service'], ['operations', 'Operations'], ['marketing', 'Marketing'],
];
function cswTeamMentionOnInput(val) {
  if (/@$/.test(val || '')) cswFetchMentionTargets(); // fresh '@' → live store roster (fr dcee5a2a)
  cswMentionTypeahead('csw-team-input', 'csw-team-mention', val);
}
function cswTeamMentionPick(key) { cswMentionPickInto('csw-team-input', 'csw-team-mention', key); }

// Shared @-typeahead, fed by the served roster (no new fetch). Suggests
// individual staff AND the department handles (a dept fans out on send).
// Used by BOTH the team composer and the CUSTOMER-thread composer (the
// latter @-routes the customer chat to that staff/dept).
function cswMentionTypeahead(inputId, boxId, val) {
  const box = document.getElementById(boxId);
  if (!box) return;
  const m = /@([A-Za-z][\w .'-]*)?$/.exec(val || '');
  if (!m) { box.hidden = true; box.innerHTML = ''; return; }
  const q = (m[1] || '').toLowerCase();
  const roster = (sosState && sosState.roster) || [];
  const people = roster.filter(p => {
    if (p.staff_id === (sosState && sosState.cur)) return false;
    const key = (p.key || '').toLowerCase();
    const name = (p.full_name || p.name || '').toLowerCase();
    return !q || key.indexOf(q) === 0 || name.indexOf(q) !== -1;
  }).slice(0, 5);
  const depts = CSW_TEAM_DEPTS.filter(([k, l]) => !q || k.indexOf(q) === 0 || l.toLowerCase().indexOf(q) !== -1).slice(0, 3);
  // Stores are TEAM-composer only ('@Palms' resolves to today's shift on
  // send; the customer-thread router has no store lane).
  const stores = (inputId === 'csw-team-input')
    ? ((sosState && sosState._mentionStores) || []).filter(s =>
        !q || String(s.name || '').toLowerCase().indexOf(q) === 0).slice(0, 4)
    : [];
  if (!people.length && !depts.length && !stores.length) { box.hidden = true; box.innerHTML = ''; return; }
  let h = '';
  stores.forEach(s => {
    const today = (s.today || []).filter(Boolean);
    const sub = today.length ? ('today: ' + today.join(', '))
                             : 'nobody rostered today';
    h += `<button class="scw-mention__r" data-pick="${esc(s.name)}">`
      + `<span class="rav csw-mention__dept">◆</span>`
      + `<span class="scw-mention__n">${esc(s.name)}</span>`
      + `<span class="scw-mention__role">${esc(sub)}</span></button>`;
  });
  depts.forEach(([k, l]) => {
    h += `<button class="scw-mention__r" data-pick="${esc(k)}">`
      + `<span class="rav csw-mention__dept">@</span>`
      + `<span class="scw-mention__n">${esc(l)}</span>`
      + `<span class="scw-mention__role">Department</span></button>`;
  });
  people.forEach(p => {
    const key = p.key || cswInitials(p.full_name || p.name || '').toLowerCase();
    h += `<button class="scw-mention__r" data-pick="${esc(key)}">`
      + `<span class="rav">${esc((p.init || cswInitials(p.full_name || p.name)))}</span>`
      + `<span class="scw-mention__n">${esc(p.full_name || p.name)}</span>`
      + `<span class="scw-mention__role">${esc(p.role || p.department || '')}</span></button>`;
  });
  box.innerHTML = h;
  box.hidden = false;
  box.querySelectorAll('.scw-mention__r').forEach(b =>
    b.addEventListener('click', () => cswMentionPickInto(inputId, boxId, b.dataset.pick)));
}
function cswMentionPickInto(inputId, boxId, key) {
  const t = document.getElementById(inputId);
  if (t) {
    t.value = (t.value || '').replace(/@([A-Za-z][\w .'-]*)?$/, '@' + key + ' ');
    t.focus();
    t.dispatchEvent(new Event('input'));   // re-sync send/mic + grow + draft
  }
  const box = document.getElementById(boxId);
  if (box) { box.hidden = true; box.innerHTML = ''; }
}

// The one line a staffer gets back after addressing a shop. Plain words
// only (operator 2026-08-29): it names the team, the shop, the person
// and the label they will hear the job called by, and it never mentions
// a piece of machinery.
//
// EVERY PART IS OPTIONAL BECAUSE THE ANSWER'S PARTS ARE. A re-fire of a
// job that already exists comes back without an owner name, and a job
// nobody was on shift for comes back without one either — so the line
// degrades to the truth rather than inventing a person.
function cswRelaySentLine(ra) {
  const where = [ra.dept, ra.store].filter(Boolean).join(' · ');
  const who = ra.owner ? `${ra.owner} has it next.`
                       : 'Nobody has picked it up yet.';
  const label = ra.short_id ? ` ${ra.short_id}.` : '';
  return (where ? `Sent to ${where}. ` : 'Sent on. ') + who + label;
}

// THE SEND SAYS WHAT IT DID (2026-08-30). Two halves of one question —
// "did that go anywhere?" — and until today the answer to both was
// nothing at all.
//
// The route has always put `relay_ask` on the send answer and this file
// has never once read it, so a person who addressed a shop watched
// their message send like any other line and had no idea a job had been
// created, or who now had it. The chip arrives seconds later at the
// bottom of a thread they may already have scrolled away from; this is
// the moment they are still looking.
//
// The other half: an address naming a shop nobody has minted nothing
// and said nothing either (relay_rooms.address_miss). A job the sender
// believes they passed on is worse than one they know they did not.
//
// A created job WINS when both could apply — it is the more useful of
// the two facts, and they cannot both be true of one message anyway.
//
// Kept as its own function on purpose: cswTeamSend is read through a
// fixed window by tests/test_staffos_ui.py (the reopen line must stay
// inside it), so reasoning lives out here rather than in that body.
function cswTeamSayWhatHappened(res) {
  if (!res) return;
  if (res.relay_ask) { cswToast(cswRelaySentLine(res.relay_ask)); return; }
  if (res.relay_note) cswToast(res.relay_note, true);
}

// Send a team message → POST /api/staffos/chat/send {sender_staff_id, body,
// thread_id?}. On success refresh the threads + reopen the resolved thread.
async function cswTeamSend() {
  const s = cswState();
  const a = s.teamActive;
  const t = document.getElementById('csw-team-input');
  if (!a || !t) return;
  const body = (t.value || '').trim();
  if (!body) return;
  const payload = { sender_staff_id: sosState.cur, body };
  if (a.thread_id) payload.thread_id = a.thread_id;
  const sendBtn = document.getElementById('csw-team-send');
  if (sendBtn) sendBtn.setAttribute('disabled', 'disabled');
  // Optimistic insert (mirrors cswSendText): clear the composer + drop a pending
  // bubble NOW so the ~2s round-trip isn't a dead pause. Success repaints from
  // the server (de-dupes this bubble); failure marks it + restores the text.
  t.value = ''; t.style.height = 'auto';
  const script = document.getElementById('csw-team-script');
  let pending = null;
  if (script) {
    if (script.querySelector('.csw-empty')) script.innerHTML = '';   // drop empty-state
    script.insertAdjacentHTML('beforeend', `<div class="csw-row out"><div class="csw-bub out pending"><div class="csw-bub__txt">${esc(body)}</div><div class="csw-bub__t">⏱</div></div></div>`);
    pending = script.lastElementChild; script.scrollTop = script.scrollHeight;
  }
  let res;
  try {
    res = await api('/api/staffos/chat/send', { method: 'POST', body: JSON.stringify(payload) });
  } catch (e) {
    if (pending) { const b = pending.querySelector('.csw-bub'); if (b) { b.classList.remove('pending'); b.classList.add('failed'); const ts = b.querySelector('.csw-bub__t'); if (ts) ts.textContent = '✕ tap to retry'; b.addEventListener('click', () => { pending.remove(); t.value = body; t.dispatchEvent(new Event('input')); t.focus(); }); } }
    if (sendBtn) sendBtn.removeAttribute('disabled');
    cswToast(cswSayWhy(e, { lead: 'The message did not send.' }), true);
    return;
  }
  if (res && res.hint) cswToast(res.hint, true);   // e.g. "couldn't find @X"
  cswTeamSayWhatHappened(res);                     // did that go anywhere?
  await cswLoadTeam();
  // reopen the resolved thread (send returns thread_ids, one per recipient).
  const tid = res && ((res.thread_ids && res.thread_ids[0]) || res.thread_id || (res.thread && res.thread.id));
  if (tid) { cswOpenTeamThread(tid); return; }
  if (sendBtn) sendBtn.removeAttribute('disabled');
}

// Swipe-right: mark a thread read (seen). Swipe-left: resolve (archive).
function cswMarkRead(cid) {
  const t = (cswState().threads || []).find(x => x.zoko_customer_id === cid);
  if (t) { t.unread = false; t.unread_count = 0; }
  api(`/api/cs/threads/${encodeURIComponent(cid)}/seen`, { method: 'POST', body: JSON.stringify({}) }).catch(() => {});
  cswRenderThreadList(); cswToast('Marked read');
}
function cswServiceRefusal(error) {
  const detail=error?.body?.detail;
  const code=typeof detail==='string'?detail:detail?.error || error?.message;
  return ({
    service_obligation_open:'There is still service work open. Open the case to accept it or record what happened.',
    service_obligation_read_failed:'The service cases could not be checked. Refresh the conversation before changing it.',
    obligation_read_failed:'The service cases could not be checked. Refresh the conversation before changing it.',
    owner_required:'This case belongs to another teammate. Check its owner before continuing.',
    accepted_owner_required:'Accept this case before taking the next step.',
    service_financial_context_requires_review:'The linked payment needs a check before this request can close.',
    service_payment_pending:'This case has an unpaid service fee. Check the fee and its owner before closing it.',
  })[code] || 'Could not confirm this change. Refresh the conversation before trying again.';
}

async function cswResolveThread(cid, undoable) {
  const t = (cswState().threads || []).find(x => x.zoko_customer_id === cid);
  const was = t ? t.cs_status : null;
  const busy=cswResolveThread._busy || (cswResolveThread._busy=new Set());
  if(busy.has(cid)) return false;
  busy.add(cid);
  const put = (status) => api(`/api/cs/threads/${encodeURIComponent(cid)}/status`,
    { method: 'POST', body: JSON.stringify({ status }) });
  try {
    const r=await put('resolved');
    if (r && r.reopened) {
      const th=(cswState().threads || []).find(x=>x.zoko_customer_id===cid);
      if(th) th.cs_status='open';
      (cswState()._reopened=cswState()._reopened || {})[cid]=Date.now();
      cswRenderThreadList();
      cswToast('Customer is still waiting — kept in Needs reply');
      return false;
    }
    if(r?.ok!==true || r.status && r.status!=='resolved') throw new Error('status_unconfirmed');
    const th=(cswState().threads || []).find(x=>x.zoko_customer_id===cid);
    if(th) th.cs_status='resolved';
    cswRenderThreadList();
    if(undoable) cswToastUndo('Resolved',async()=>{
      try {
        const u=await put(was || 'open');
        if(u?.ok!==true) throw new Error('status_unconfirmed');
        const current=(cswState().threads || []).find(x=>x.zoko_customer_id===cid);
        if(current) current.cs_status=u.status || was || 'open';
        cswRenderThreadList();cswToast('Reopened');
      } catch(error) {cswToast(cswServiceRefusal(error),true);}
    });
    else cswToast('Resolved');
    return true;
  } catch(error) {
    cswToast(cswServiceRefusal(error),true);
    return false;
  } finally {
    busy.delete(cid);
  }
}

// ══════════════════════════════════════════════════════════════════
// EON private line (v3.0p103)
//
// The house desk opens this line from its EON tab. Classic keeps a pinned
// pseudo-thread because it has no EON tab. Talks to
// EON SOLO over the SAME /ws agent stream the main chat uses (channel='cs'
// → isolated persistence in chat_history; NEVER zoko_messages, NEVER customer
// analytics). Mirrors the customer-transcript visual language (csw-bub in|out)
// but marks EON's side distinctly. Streaming, the __permission__ approve/deny
// affordance, and a structured customer-context hand-off are all supported.
// ══════════════════════════════════════════════════════════════════

const CSW_EON_CID = '__eon__';   // sentinel; can't collide with a zoko id

function cswEonRowHtml() {
  if (cswHouse()) return '';
  const s = cswState();
  const on = s.active === CSW_EON_CID ? 'on' : '';
  return `<button class="csw-thread csw-thread--eon ${on}" data-eon="1">
    <canvas class="csw-eonwash" id="csw-eonwash-row" aria-hidden="true"></canvas>
    <div class="csw-eonav">${eonLogo(26)}</div>
    <div class="csw-thread__main">
      <div class="csw-thread__top">
        <div class="csw-thread__name">EON</div>
        <div class="csw-thread__time csw-eonpin">Pinned</div>
      </div>
      <div class="csw-thread__bot">
        <div class="csw-thread__prev muted">Your private line — paste an order, ask about a customer, get help</div>
      </div>
    </div>
  </button>`;
}

function cswBindEonRow(el) {
  const row = el.querySelector('.csw-thread--eon');
  if (row) row.addEventListener('click', () => cswOpenEon());
}

function cswOpenEon() {
  const s = cswState();
  s.active = CSW_EON_CID;
  s.activeThread = null;
  s._custIntel = null;
  // Sim is owner/admin-only now (no tab for reps) — force the normal Ops line.
  if ((s._eonMode === 'sim' || s._eonMode === 'ready') && !cswCanManageGolive()) s._eonMode = 'ops';
  document.getElementById('csw-inbox')?.classList.add('has-active');
  cswRenderThreadList();
  cswRenderEonShell();
  cswTabbarSync();
  if ((s._eonMode || 'ops') === 'sim') cswLoadSimThread(); else cswLoadEonThread();
}

// Two-state header toggle for the pinned EON thread: "Ops" (the private
// agent line, /ws channel='cs') vs "Customer sim" (sandbox persona testing
// over HTTP /api/cs/eon/simulate). 2026-06-18: the EON line reads like a
// normal private DM, so the mode toggle is hidden for reps — only owner/admin
// (who run the persona sandbox) see the tabs. Everyone else gets Ops only.
function cswEonModeTabsHtml(mode) {
  if (!cswCanManageGolive()) return '';
  // 2026-08-10 (B2): the graduation board joins as a THIRD mode rather
  // than a sixth desk lens or a new bottom bar — the five lens keys
  // already overrun 375px by 58px, and this pane is where an owner
  // already comes to ask "how is EON doing". Same owner gate as the
  // sandbox: only whoever can arm a lane can read the board that says
  // whether a lane earned it.
  return `<div class="csw-eontabs" role="tablist">
    <button class="csw-eontab ${mode === 'ops' ? 'on' : ''}" data-eonmode="ops" role="tab">Ops</button>
    <button class="csw-eontab csw-eontab--ready ${mode === 'ready' ? 'on' : ''}" data-eonmode="ready" role="tab">Readiness</button>
    <button class="csw-eontab csw-eontab--sim ${mode === 'sim' ? 'on' : ''}" data-eonmode="sim" role="tab">Customer sim</button>
  </div>`;
}

function cswBindEonModeTabs() {
  document.querySelectorAll('.csw-eontab').forEach(b => b.addEventListener('click', () => {
    const s = cswState();
    const m = b.dataset.eonmode;
    if ((s._eonMode || 'ops') === m) return;
    s._eonMode = m;
    cswRenderEonShell();
    if (m === 'sim') cswLoadSimThread();
    else if (m === 'ready') cswLoadReadiness();
    else cswLoadEonThread();
  }));
}

// Carry the customer the rep was just viewing into the EON line as a
// STRUCTURED field on the FIRST message of this open (then cleared) — so
// "what's this customer's history" resolves without retyping. We capture it
// at open time from the last-active customer thread.
function cswEonCaptureContext() {
  const s = cswState();
  // s.activeThread is null in EON mode; use the last customer thread the rep
  // opened (tracked separately) so the hand-off survives the mode switch.
  const t = s._lastCustomerThread;
  if (!t || !t.zoko_customer_id) return null;
  return { zoko_customer_id: t.zoko_customer_id,
           customer_name: t.customer_name || cswDisplayName(t) || '' };
}

function cswRenderEonShell() {
  const s = cswState();
  if ((s._eonMode || 'ops') === 'ready') return cswRenderReadyShell();
  if ((s._eonMode || 'ops') === 'sim') return cswRenderSimShell();
  const conv = document.getElementById('csw-conv');
  if (!conv) return;
  const ctxHint = cswEonCaptureContext();
  conv.classList.add('show');
  // Inline the captured-customer context into the composer placeholder so the
  // panel reads like a plain DM (no banner). Falls back to a clean prompt.
  const ctxName = ctxHint ? (ctxHint.customer_name || 'this customer') : '';
  const ph = ctxHint ? `Message EON — context from ${ctxName} attached` : 'Message EON';
  conv.innerHTML = `
    <div class="csw-conv__bar csw-conv__bar--eon">
      <button class="csw-conv__back" id="csw-eon-back" aria-label="Back">‹</button>
      <div class="csw-eonav csw-eonav--bar">${eonLogo(22)}</div>
      <div class="csw-conv__id">
        <div class="csw-conv__name">EON</div>
        <div class="csw-conv__sub"><span class="csw-conv__scroll"><span class="csw-ch csw-ch--eon">Private message</span></span></div>
      </div>
      ${cswEonModeTabsHtml('ops')}
    </div>
    <canvas class="csw-eonwash" id="csw-eonwash-conv" aria-hidden="true"></canvas>
    <div class="csw-script csw-script--eon" id="csw-eon-script"><div class="csw-empty"><div class="csw-empty__m">○</div>Loading…</div></div>
    <div class="csw-composer">
      <div class="csw-inputbox" id="csw-eon-inputbox">
        <textarea class="csw-input" id="csw-eon-input" placeholder="${esc(ph)}" rows="1"></textarea>
        <button class="csw-send" id="csw-eon-send" disabled aria-label="Send">
          <svg viewBox="0 0 24 24" fill="none"><path d="M4 12l16-8-6 16-2.5-6.5L4 12z" fill="currentColor"/></svg>
        </button>
      </div>
    </div>`;
  // Keep/drop the context hand-off (the hint now lives in the placeholder).
  s._eonCtx = ctxHint;
  s._eonCtxPending = !!ctxHint;
  document.getElementById('csw-eon-back')?.addEventListener('click', () => {
    s.active = null;
    document.getElementById('csw-inbox')?.classList.remove('has-active');
    conv.classList.remove('show');
    cswRenderThreadList();
    cswTabbarSync();
  });
  const inp = document.getElementById('csw-eon-input');
  const send = document.getElementById('csw-eon-send');
  const grow = () => { inp.style.height = 'auto'; inp.style.height = Math.min(inp.scrollHeight, 160) + 'px'; };
  inp.addEventListener('input', () => { grow(); if (send) send.disabled = !inp.value.trim(); });
  inp.addEventListener('keydown', e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); cswEonSend(); } });
  send?.addEventListener('click', cswEonSend);
  cswBindEonModeTabs();
}

// One EON-pane bubble. 2026-06-18: reads like a normal chat — the agent side
// uses the standard incoming bubble (no heavy distinct background / badge),
// just a small inline "EON" label above the text.
// role: 'user' (the rep) → out; 'assistant'/'eon' → in.
function cswEonBubble(role, html, opts) {
  opts = opts || {};
  const rep = role === 'user';
  const cls = rep ? 'out' : 'in';
  const stream = opts.streaming ? ' csw-bub--stream' : '';
  return `<div class="csw-bub ${cls}${stream}">
    ${!rep ? '<div class="csw-bub__eonlbl">EON</div>' : ''}
    <div class="csw-bub__txt">${html}</div>
  </div>`;
}

// EON aurora wash (design pass 02, anchor 3): the violet field behind the
// pinned row + EON conversation is ALIVE ONLY WHILE EON STREAMS — still (a
// faint CSS wash on the canvas) when idle. Lazy-mounts the shared aurora
// module per canvas; re-renders swap the canvas nodes, so stale handles are
// destroyed and remounted on demand.
function cswEonWash(active) {
  const s = cswState();
  s._eonWash = s._eonWash || {};
  ['csw-eonwash-row', 'csw-eonwash-conv'].forEach(id => {
    const c = document.getElementById(id);
    const cur = s._eonWash[id];
    if (!c) { if (cur) { cur.destroy(); delete s._eonWash[id]; } return; }
    if (!cur || cur._canvas !== c) {
      if (cur) cur.destroy();
      delete s._eonWash[id];
      if (!active || typeof window.mountAurora !== 'function') return;
      const h = window.mountAurora(c, ['#B58CFF', '#6EA8FE', '#CA6673'],
        { alpha: true, autostart: false, vignette: false, glow: null, intensity: 0.5 });
      if (!h) return;
      h._canvas = c;
      s._eonWash[id] = h;
    }
    if (active) s._eonWash[id].start(); else s._eonWash[id].stop();
  });
}

function cswEonRenderScript() {
  const s = cswState();
  const scr = document.getElementById('csw-eon-script');
  if (!scr) return;
  const msgs = s._eonMsgs || [];
  cswEonWash(msgs.some(m => m.streaming));
  if (!msgs.length) {
    scr.innerHTML = `<div class="csw-empty"><div class="csw-empty__m">${eonLogo(28)}</div>Ask EON anything — it can read your CS data, draft replies, and process orders. This stays private to you.</div>`;
    return;
  }
  const atBottom = (scr.scrollHeight - scr.scrollTop - scr.clientHeight) < 120;
  scr.innerHTML = msgs.map(m => {
    if (m.role === 'permission') {
      // 2026-08-04: same three intents as the main chat card — a
      // question shows its answers, an FYI shows a single dismiss.
      const intent = permCardIntent(m);
      const decided = m.decided;
      const aid = esc(m.action_id || '');
      let foot;
      if (decided) {
        foot = `<div class="csw-eonperm__done">${esc(
          intent === 'notify' ? 'Dismissed'
          : intent === 'question' && decided === 'approve'
            ? `Answered: ${m.answer_label || m.answer || ''}`
          : decided === 'approve' ? 'Approved' : 'Denied')}</div>`;
      } else if (intent === 'notify') {
        foot = `<div class="csw-eonperm__btns">
             <button class="csw-eonperm__b deny" data-eonperm="ack" data-aid="${aid}">Got it</button>
           </div>`;
      } else if (intent === 'question' && permCardOptions(m).length) {
        foot = `<div class="csw-eonperm__btns">${permCardOptions(m).map(o =>
             `<button class="csw-eonperm__b approve" data-eonans="${esc(o.value)}" data-aid="${aid}">${esc(o.label)}</button>`
           ).join('')}</div>`;
      } else if (intent === 'question') {
        foot = `<div class="csw-eonperm__done">Answer EON in the message box below.</div>`;
      } else {
        foot = `<div class="csw-eonperm__btns">
             <button class="csw-eonperm__b approve" data-eonperm="approve" data-aid="${aid}">Approve</button>
             <button class="csw-eonperm__b deny" data-eonperm="deny" data-aid="${aid}">Deny</button>
           </div>`;
      }
      return `<div class="csw-eonperm" data-aid="${aid}">
        <div class="csw-eonperm__t">${esc(m.title || 'Approval required')}</div>${foot}</div>`;
    }
    const body = m.role === 'user'
      ? esc(m.text || '')
      : (m.streaming
          ? (m.text ? esc(m.text) + '<span class="csw-eoncaret"></span>'
                    : '<span class="csw-steps" aria-hidden="true"><i></i><i></i><i></i></span><span class="csw-steps__lbl">eon is working</span>')
          : cswLinkify(m.text || ''));
    return cswEonBubble(m.role, body, { streaming: m.streaming });
  }).join('');
  if (atBottom || msgs[msgs.length - 1]?.role === 'user') scr.scrollTop = scr.scrollHeight;
  // Bind permission buttons (delegation-free; pane is re-rendered each turn).
  scr.querySelectorAll('[data-eonperm]').forEach(b =>
    b.addEventListener('click', () => cswEonDecide(b.dataset.aid, { decision: b.dataset.eonperm })));
  scr.querySelectorAll('[data-eonans]').forEach(b =>
    b.addEventListener('click', () => cswEonDecide(b.dataset.aid, { answer: b.dataset.eonans })));
}

async function cswLoadEonThread() {
  const s = cswState();
  s._eonMsgs = s._eonMsgs || [];
  // Show cached immediately, then hydrate from the server (per-rep persisted).
  cswEonRenderScript();
  try {
    const d = await api('/api/cs/eon/thread?limit=200');
    if (s.active !== CSW_EON_CID) return;   // rep navigated away mid-fetch
    s._eonConvId = d.conversation_id || null;
    s._eonMsgs = (d.messages || []).map(m => ({
      role: m.role === 'assistant' ? 'eon' : 'user',
      text: m.content || '',
    }));
    cswEonRenderScript();
  } catch (_) { /* keep whatever is cached */ }
}

// Dedicated WS for the EON pane so its stream never entangles with the main
// chat's State.chat. Same /ws endpoint + protocol, channel='cs'.
function cswEonWS() {
  const s = cswState();
  if (s._eonWs && (s._eonWs.readyState === WebSocket.OPEN || s._eonWs.readyState === WebSocket.CONNECTING)) return s._eonWs;
  if (!State.jwt || !State.tenant) return null;
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  const url = `${proto}://${location.host}/ws?token=${encodeURIComponent(State.jwt)}&tenant=${State.tenant.slug}`;
  try {
    const ws = new WebSocket(url);
    ws.onmessage = (ev) => { try { cswEonHandleWS(JSON.parse(ev.data)); } catch (_) {} };
    ws.onclose = () => {
      if (cswState()._eonWs === ws) cswState()._eonWs = null;
      const act = (cswState()._eonMsgs || []).find(m => m.streaming);
      if (act) { act.streaming = false; act.text = (act.text || '') + '\n\n(connection dropped, please retry)'; cswEonRenderScript(); }
    };
    s._eonWs = ws;
    return ws;
  } catch (_) { return null; }
}

function cswEonSend() {
  const s = cswState();
  const inp = document.getElementById('csw-eon-input');
  const send = document.getElementById('csw-eon-send');
  const text = (inp?.value || '').trim();
  if (!text) return;
  s._eonMsgs = s._eonMsgs || [];
  s._eonMsgs.push({ role: 'user', text });
  if (inp) { inp.value = ''; inp.style.height = 'auto'; }
  if (send) send.disabled = true;
  cswEonRenderScript();

  const frame = { type: 'chat', message: text, channel: 'cs' };
  if (s._eonConvId) frame.conversation_id = s._eonConvId;
  // Structured customer-context hand-off — only on the first message after a
  // jump from a customer thread; NOT string-concatenated in the frontend.
  if (s._eonCtxPending && s._eonCtx) {
    frame.cs_context = { zoko_customer_id: s._eonCtx.zoko_customer_id,
                         customer_name: s._eonCtx.customer_name };
    s._eonCtxPending = false;
    // Context consumed by the first message — drop the placeholder hint so the
    // composer reads like a normal DM from here on.
    const inp0 = document.getElementById('csw-eon-input');
    if (inp0) inp0.placeholder = 'Message EON';
  }
  const ws = cswEonWS();
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(frame));
  } else if (ws) {
    // Socket still opening — send once it's ready.
    ws.addEventListener('open', () => { try { ws.send(JSON.stringify(frame)); } catch (_) {} }, { once: true });
  }
}

function cswEonHandleWS(msg) {
  const s = cswState();
  s._eonMsgs = s._eonMsgs || [];
  if (msg.type === 'request_id') { return; }
  if (msg.type === 'delta') {
    let act = s._eonMsgs.find(m => m.streaming);
    if (!act) { act = { role: 'eon', text: '', streaming: true }; s._eonMsgs.push(act); }
    act.text += msg.text || '';
    cswEonRenderScript();
  } else if (msg.type === 'done') {
    const act = s._eonMsgs.find(m => m.streaming);
    if (act) {
      act.streaming = false;
      if (msg.full_text && msg.full_text.length > (act.text || '').length) act.text = msg.full_text;
    } else if (msg.full_text) {
      s._eonMsgs.push({ role: 'eon', text: msg.full_text });
    }
    cswEonRenderScript();
  } else if (msg.type === 'permission_request') {
    s._eonMsgs.push({
      role: 'permission', action_id: msg.action_id,
      title: msg.title || 'Approval required', decided: null,
      intent: permCardIntent(msg), options: permCardOptions(msg),
      allow_free_text: !!msg.allow_free_text,
    });
    cswEonRenderScript();
  } else if (msg.type === 'error') {
    const act = s._eonMsgs.find(m => m.streaming);
    if (act) act.streaming = false;
    s._eonMsgs.push({ role: 'eon', text: msg.text || 'EON hit an error — please retry.' });
    cswEonRenderScript();
  }
  // status / tool_done / widget_preview are intentionally ignored in the CS
  // pane — it shows a clean answer, not the tool trace.
}

async function cswEonDecide(actionId, body) {
  const s = cswState();
  const card = (s._eonMsgs || []).find(m => m.role === 'permission' && m.action_id === actionId);
  if (card) {
    card.decided = body.decision || (body.answer != null ? 'approve' : null);
    if (body.answer != null) card.answer_label = body.answer;
  }
  cswEonRenderScript();
  try {
    await api(`/api/permissions/${encodeURIComponent(actionId)}/decide`, {
      method: 'POST', body: JSON.stringify(body),
    });
  } catch (err) {
    if (card) card.decided = null;
    cswEonRenderScript();
    // SAY THE SERVER'S REASON (complaints fix 2026-09-01): the head of
    // ops tapped Approve for three weeks against an expired delegation
    // and read only "retry" — the 403 carried the real sentence all
    // along, and a permission refusal is not retryable.
    const detail = err && (err.body?.detail || err.message);
    const perm = err && err.status === 403;
    cswToast(perm ? String(detail || 'Not allowed for this seat.')
                  : ('Could not record decision — ' + (detail || 'retry.')), true);
  }
}

// ══════════════════════════════════════════════════════════════════
// CUSTOMER SIMULATOR (v3.0p108) — the pinned EON thread's second mode.
//
// The operator types AS a customer; EON answers with the SAME persona a
// real customer gets (cs_copilot persona, shared server-side — not a
// copy). Pure sandbox over HTTP POST /api/cs/eon/simulate — nothing here
// touches the /ws ops stream, Zoko, or any send path. Optional anchor
// ("test as: <customer>") grounds replies in a real customer's
// orders/LTV. Distinct amber treatment so it can never be mistaken for
// the ops line.
// ══════════════════════════════════════════════════════════════════

// ── The graduation board (B2, artifact 2467db14 S5) ──────────────────
// What EON is allowed to be, per lane, and WHY — read straight off the
// engine that already grades every draft. No composer: this pane answers
// a question, it never sends (the EON pane's send-call-free law).
//
// LANES NEVER GRADUATE for two different reasons and the board must not
// blur them. The belt (NEVER_AUTONOMOUS) has FIVE members of two kinds:
// `complaint`, `payment` and `other` are the GATE'S OWN exclusions
// (irreversible relationship; money truth; unclassified fails closed) —
// nobody lifts those. `price` and `wholesale` are COUNCIL BRAKES the
// operator can lift (price until the ×1.04 checkout truth lands,
// wholesale because a trade ask routes senior). A rep reading this must
// be able to tell the permanent from the liftable at a glance — the
// first cut mapped everything-but-complaint to "operator lifts this",
// which told reps that payment autonomy was one tap away. It is not.
const cswGateOwnNever = {
  complaint: 'irreversible relationship',
  payment: 'money truth — the gate itself',
  other: 'unclassified — fails closed',
};
// THE BELT BY KIND (2026-09-02, WS-P 7). The LABEL stays: payment is
// still HUMAN, ALWAYS on this board. What the gate reads now is the
// KIND inside the label — the server sends it (never_kinds.payment):
// an ASK ("send acct") opens EON's card turn, a CLAIM ("I have paid")
// opens a verify-only turn where the only tools are check_payment and
// check_recent_order. The reason line names both so a rep reading
// "HUMAN, ALWAYS" beside payment is not told EON never touches money.
function cswPaymentKindLine(kinds) {
  const pk = (kinds && kinds.payment) || {};
  const card = (pk.card_turn || ['ask']).join('/');
  const verify = (pk.verify_only_turn || ['claim']).join('/');
  return 'by kind — ' + card + ' opens the card turn, ' + verify
    + ' is verified by a tool, never asserted';
}
function cswReadyLaneRow(b, never, kinds) {
  const graded = b.graded || 0;
  const pct = b.acceptance_pct;
  const isNever = never.indexOf(b.intent) !== -1;
  // The KIND comes from the server when it speaks (never_kinds) — a
  // client-only copy mislabels every future belt member as a liftable
  // brake (rig vet). The local map keeps the REASONS copy and the
  // no-payload fallback.
  const gateOwn = (kinds && kinds.gate_own)
    ? kinds.gate_own.indexOf(b.intent) !== -1
    : !!cswGateOwnNever[b.intent];
  const kindLine = (gateOwn && b.intent === 'payment')
    ? cswPaymentKindLine(kinds) : '';
  const state = isNever
    ? (gateOwn ? 'human' : 'braked')
    : (b.ready ? 'auto' : 'super');
  const label = { auto: 'AUTONOMOUS', super: 'SUPERVISED',
                  braked: 'BRAKED', human: 'HUMAN, ALWAYS' }[state];
  // The meter shows acceptance against the bar it must clear, so a lane
  // at 41.7% reads as "two-thirds of the way", not as a naked number.
  // Never lanes stay flat — progress toward a door that is shut would be
  // a promise the belt refuses.
  const w = (pct === null || pct === undefined || isNever)
    ? 0 : Math.max(0, Math.min(100, Math.round(pct)));
  const stats = `${graded} graded · ${pct === null || pct === undefined ? 'no reading yet' : pct + '% accepted'}`;
  // A braked lane that statistically earns it does not hide the fact —
  // the stats print under the brake line, so the operator lifting the
  // brake knows exactly what the lane has already proven.
  const sub = isNever
    ? (gateOwn
        ? `never autonomous — ${cswGateOwnNever[b.intent] || 'the gate itself excludes it'}${kindLine ? ` · ${kindLine}` : ''}`
        : `council brake — the operator lifts this, not the gate${graded ? ` · ${stats}` : ''}`)
    : stats;
  // The meter's aria tells the same truth the eye gets: a state word for
  // never lanes, "no reading yet" for ungraded — never "0% accepted".
  const meterAria = isNever ? label.toLowerCase()
    : (pct === null || pct === undefined ? 'no reading yet' : `${w}% accepted`);
  return `<div class="csw-gradrow">
    <div class="csw-gradrow__l">
      <div class="csw-gradrow__n">${esc(b.intent)}</div>
      <div class="csw-gradrow__s">${esc(sub)}</div>
    </div>
    <div class="csw-gradmeter" role="img" aria-label="${esc(meterAria)}">
      <i style="width:${w}%"></i>
    </div>
    <div class="csw-gradstate csw-gradstate--${state}">${label}</div>
  </div>`;
}

function cswRenderReadyShell() {
  const conv = document.getElementById('csw-conv');
  if (!conv) return;
  conv.classList.add('show');
  conv.innerHTML = `
    <div class="csw-conv__bar csw-conv__bar--eon">
      <button class="csw-conv__back" id="csw-eon-back" aria-label="Back">‹</button>
      <div class="csw-eonav csw-eonav--bar">${eonLogo(22)}</div>
      <div class="csw-conv__id">
        <div class="csw-conv__name">EON</div>
        <div class="csw-conv__sub"><span class="csw-conv__scroll"><span class="csw-ch csw-ch--eon">What EON is allowed to be, per lane</span></span></div>
      </div>
      ${cswEonModeTabsHtml('ready')}
    </div>
    <div class="csw-gradwrap" id="csw-gradwrap">
      <div class="csw-empty"><div class="csw-empty__m">○</div>Reading the board…</div>
    </div>`;
  const s = cswState();
  document.getElementById('csw-eon-back')?.addEventListener('click', () => {
    s.active = null;
    document.getElementById('csw-inbox')?.classList.remove('has-active');
    conv.classList.remove('show');
    cswRenderThreadList();
    cswTabbarSync();
  });
  // The board is a MODE, and a mode strip with no listeners is a trap:
  // without this bind the owner who opened Readiness could never return
  // to Ops or the sandbox for the whole session (rig vet, SEV1).
  cswBindEonModeTabs();
}

async function cswLoadReadiness() {
  const host = document.getElementById('csw-gradwrap');
  if (!host) return;
  let d = null;
  try {
    d = await api('/api/cs/eon/readiness?days=28');
  } catch (e) {
    host.innerHTML = `<div class="csw-empty"><div class="csw-empty__m">!</div>Could not read the board.<div class="csw-empty__s">${esc(String(e && e.message || e))}</div></div>`;
    return;
  }
  if (!d || !d.ok) {
    host.innerHTML = `<div class="csw-empty"><div class="csw-empty__m">!</div>Could not read the board.</div>`;
    return;
  }
  const lanes = (d.intents || []).filter(b => (b.drafts || 0) > 0
    || (d.never_autonomous || []).indexOf(b.intent) !== -1);
  const never = d.never_autonomous || [];
  // The honest headline: how many lanes have EARNED autonomy, not how
  // many are switched on. Arming stays the operator's tap — a board that
  // implied otherwise would be claiming a decision it does not make.
  // Both sides of the fraction speak the belt: the engine's `ready`
  // subtracts only complaint, so a braked lane clearing the stats gate
  // would otherwise count into a headline its own row contradicts — and
  // a denominator counting lanes that can never graduate makes "0 of 8"
  // read as failure when three lanes are simply not on the ladder.
  const graduable = lanes.filter(b => never.indexOf(b.intent) === -1);
  const live = graduable.filter(b => b.ready).length;
  host.innerHTML = `
    <div class="csw-gradhead">
      <div class="csw-gradhead__v">${live}<span>of ${graduable.length}</span></div>
      <div class="csw-gradhead__l">lanes have earned autonomy${live ? '' : ' — none yet'}</div>
      <div class="csw-gradhead__r">${(d.total_graded || 0)} graded replies · ${d.days || 28}d${d.overall_acceptance_pct !== null && d.overall_acceptance_pct !== undefined ? ` · ${d.overall_acceptance_pct}% accepted overall` : ''}</div>
    </div>
    <div class="csw-gradlanes">${lanes.map(b => cswReadyLaneRow(b, never, d.never_kinds || null)).join('')}</div>
    <div class="csw-gradrule">
      Earned = ${(d.gate || {}).min_graded || 20}+ graded and ${(d.gate || {}).acceptance_pct || 70}% accepted, on the trailing window.
      Recomputed every read, so a lane that stops earning falls out by itself.
      <b>Earning it is not the same as being switched on — that stays your tap.</b>
    </div>`;
}

function cswRenderSimShell() {
  const s = cswState();
  const conv = document.getElementById('csw-conv');
  if (!conv) return;
  conv.classList.add('show');
  const a = s._simAnchor;
  conv.innerHTML = `
    <div class="csw-conv__bar csw-conv__bar--eon csw-conv__bar--sim">
      <button class="csw-conv__back" id="csw-eon-back" aria-label="Back">‹</button>
      <div class="csw-eonav csw-eonav--bar">${eonLogo(22)}</div>
      <div class="csw-conv__id">
        <div class="csw-conv__name">EON</div>
        <div class="csw-conv__sub"><span class="csw-conv__scroll"><span class="csw-ch csw-ch--sim">Live agent · real stock, cart & checkout · no WhatsApp sent</span></span></div>
      </div>
      ${cswEonModeTabsHtml('sim')}
    </div>
    <div class="csw-simstrip">LIVE AGENT — same Opus model + tools a customer gets. Orders/payment links are REAL (tagged [EON TESTER]). No WhatsApp is sent.</div>
    <div class="csw-golive" id="csw-golive"></div>
    <div class="csw-simanchor">
      <span class="csw-simanchor__lab">Test as:</span>
      ${a
        ? `<span class="csw-simanchor__chip">${esc(a.name || a.zoko_customer_id)}<button class="csw-simanchor__x" id="csw-sim-anchor-x" aria-label="Clear customer">✕</button></span>`
        : `<div class="csw-simanchor__box"><input class="csw-simanchor__q" id="csw-sim-anchor-q" placeholder="search customer — blank = new customer" autocomplete="off"><div class="csw-simanchor__list" id="csw-sim-anchor-list"></div></div>`}
      <button class="csw-simovn${s._simOvernight ? ' is-on' : ''}" id="csw-sim-ovn" title="Test the off-shift order-taking frame: EON opens with 'the team is off shift, I can take your order'">Overnight ${s._simOvernight ? 'ON' : 'off'}</button>
      <button class="csw-simreset" id="csw-sim-reset" title="Clear the simulated conversation">Reset</button>
    </div>
    <div class="csw-script csw-script--eon csw-script--sim" id="csw-sim-script"><div class="csw-empty"><div class="csw-empty__m">○</div>Loading…</div></div>
    <div class="csw-composer">
      <div class="csw-simstaged" id="csw-sim-staged" hidden></div>
      <div class="csw-inputbox csw-inputbox--sim" id="csw-sim-inputbox">
        <input type="file" id="csw-sim-file" accept="image/jpeg,image/png,image/webp" style="display:none">
        <button class="csw-simattach" id="csw-sim-attach" title="Send a photo as the customer" aria-label="Attach photo">
          <svg viewBox="0 0 24 24" fill="none" width="20" height="20"><path d="M21 12.5l-8.5 8.5a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-3-3l8-8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <textarea class="csw-input" id="csw-sim-input" placeholder="Type as the customer…" rows="1"></textarea>
        <button class="csw-send" id="csw-sim-send" disabled aria-label="Send">
          <svg viewBox="0 0 24 24" fill="none"><path d="M4 12l16-8-6 16-2.5-6.5L4 12z" fill="currentColor"/></svg>
        </button>
      </div>
    </div>`;
  cswBindEonModeTabs();
  document.getElementById('csw-eon-back')?.addEventListener('click', () => {
    s.active = null;
    document.getElementById('csw-inbox')?.classList.remove('has-active');
    conv.classList.remove('show');
    cswRenderThreadList();
    cswTabbarSync();
  });
  document.getElementById('csw-sim-anchor-x')?.addEventListener('click', () => {
    s._simAnchor = null;
    cswRenderSimShell(); cswSimRenderScript();
  });
  document.getElementById('csw-sim-anchor-q')?.addEventListener('input', e => cswSimAnchorSearch(e.target.value));
  document.getElementById('csw-sim-reset')?.addEventListener('click', cswSimReset);
  document.getElementById('csw-sim-ovn')?.addEventListener('click', () => {
    const st = cswState(); st._simOvernight = !st._simOvernight;
    cswRenderSimShell(); cswSimRenderScript();
  });
  const inp = document.getElementById('csw-sim-input');
  const send = document.getElementById('csw-sim-send');
  const grow = () => { inp.style.height = 'auto'; inp.style.height = Math.min(inp.scrollHeight, 160) + 'px'; };
  inp.addEventListener('input', () => { grow(); if (send) send.disabled = !inp.value.trim() && !cswState()._simStagedImg; });
  inp.addEventListener('keydown', e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); cswSimSend(); } });
  send?.addEventListener('click', cswSimSend);
  document.getElementById('csw-sim-attach')?.addEventListener('click', () => document.getElementById('csw-sim-file')?.click());
  document.getElementById('csw-sim-file')?.addEventListener('change', cswSimPickImage);
  cswSimRenderStaged();
  cswLoadAutopilot();
}

// GO-LIVE: flip EON from "@eon-only" to answering ALL customer DMs. Owner/admin
// can toggle (server hard-gates on flags:manage); everyone else sees status.
function cswCanManageGolive() {
  const r = (State.member && State.member.role) || '';
  return r === 'owner' || r === 'admin';
}

async function cswLoadAutopilot() {
  try {
    const d = await api('/api/cs/eon/autopilot');
    cswState()._autopilot = !!(d && d.enabled);
  } catch (_) { cswState()._autopilot = false; }
  cswRenderGolive();
}

function cswRenderGolive() {
  const host = document.getElementById('csw-golive');
  if (!host) return;
  const on = !!cswState()._autopilot;
  const pill = `<span class="csw-golive__pill ${on ? 'on' : ''}">${on ? '● EON is LIVE — answering all customers' : '○ EON is in test mode (@eon only)'}</span>`;
  const btn = cswCanManageGolive()
    ? `<button class="csw-golive__btn ${on ? 'live' : ''}" id="csw-golive-btn">${on ? 'Pause EON' : 'Go live'}</button>`
    : '';
  host.innerHTML = pill + btn;
  document.getElementById('csw-golive-btn')?.addEventListener('click', cswToggleAutopilot);
}

async function cswToggleAutopilot() {
  // ONE CLICK, NO CONFIRMS (standing rule): the flip happens immediately and
  // the toast carries Undo — no blocking window.confirm. Going live is fully
  // reversible (Undo re-toggles through the same endpoint).
  const on = !!cswState()._autopilot;
  try {
    const d = await api('/api/cs/eon/autopilot', { method: 'POST', body: JSON.stringify({ enabled: !on }) });
    if (!d || !d.ok) throw new Error((d && d.error) || 'failed');
    cswState()._autopilot = !!d.enabled;
    cswRenderGolive();
    cswToastUndo(
      d.enabled ? 'EON is LIVE — answering every customer DM' : 'EON paused — back to @eon only',
      () => cswToggleAutopilot());
  } catch (_) {
    cswToast('Could not change EON status — retry.', true);
  }
}

// Customer-sent photo: upload to the public bucket, stage the URL. On send it
// goes to the agent as image_url → EON recognises the piece (search_products).
async function cswSimPickImage(e) {
  const file = e.target.files && e.target.files[0];
  e.target.value = '';
  if (!file) return;
  const s = cswState();
  s._simStagedImg = '__uploading__';
  cswSimRenderStaged();
  try {
    const fd = new FormData();
    fd.append('file', file);
    const hdr = { 'Authorization': `Bearer ${State.jwt}` };
    if (State.tenant && State.tenant.slug) hdr['X-Tenant-Slug'] = State.tenant.slug;
    const res = await fetch('/api/cs/upload', { method: 'POST', body: fd, headers: hdr });
    const data = await res.json();
    if (!res.ok || !data.url) throw new Error(data.detail || 'upload failed');
    s._simStagedImg = data.url;
  } catch (_) {
    s._simStagedImg = null;
    cswToast('Photo upload failed — retry.', true);
  }
  cswSimRenderStaged();
  const send = document.getElementById('csw-sim-send');
  if (send) send.disabled = !(document.getElementById('csw-sim-input')?.value.trim()) && !cswState()._simStagedImg;
}

function cswSimRenderStaged() {
  const host = document.getElementById('csw-sim-staged');
  if (!host) return;
  const img = cswState()._simStagedImg;
  if (!img) { host.hidden = true; host.innerHTML = ''; return; }
  host.hidden = false;
  host.innerHTML = img === '__uploading__'
    ? `<span class="csw-simstaged__up">Uploading photo…</span>`
    : `<img class="csw-simstaged__thumb" src="${esc(img)}" alt="staged photo"><button class="csw-simstaged__x" id="csw-sim-staged-x" aria-label="Remove photo">✕</button>`;
  document.getElementById('csw-sim-staged-x')?.addEventListener('click', () => {
    cswState()._simStagedImg = null;
    cswSimRenderStaged();
    const send = document.getElementById('csw-sim-send');
    if (send) send.disabled = !(document.getElementById('csw-sim-input')?.value.trim());
  });
}

// Anchor picker: search the loaded inbox threads by name / sender id.
// The anchor is a zoko_customer_id — the backend resolves the real
// profile (orders / LTV / segment) from it.
function cswSimAnchorSearch(q) {
  const s = cswState();
  const list = document.getElementById('csw-sim-anchor-list');
  if (!list) return;
  q = (q || '').trim().toLowerCase();
  if (!q) { list.innerHTML = ''; list.classList.remove('show'); return; }
  const hits = (s.threads || []).filter(t => {
    const hay = `${t.customer_name || ''} ${cswDisplayName(t)} ${t.platform_sender_id || ''}`.toLowerCase();
    return hay.includes(q);
  }).slice(0, 6);
  if (!hits.length) {
    list.innerHTML = '<div class="csw-simanchor__none">No match in the loaded inbox</div>';
    list.classList.add('show');
    return;
  }
  list.innerHTML = hits.map(t =>
    `<button class="csw-simanchor__opt" data-cid="${esc(t.zoko_customer_id)}" data-name="${esc(cswDisplayName(t))}">${esc(cswDisplayName(t))}</button>`
  ).join('');
  list.classList.add('show');
  list.querySelectorAll('.csw-simanchor__opt').forEach(b => b.addEventListener('click', () => {
    cswState()._simAnchor = { zoko_customer_id: b.dataset.cid, name: b.dataset.name };
    cswRenderSimShell(); cswSimRenderScript();
  }));
}

// Sim bubbles read like a REAL customer thread reads to a rep: the
// operator's typed lines sit customer-side (in/left), EON's replies sit
// agent-side (out/right) — exactly what the customer-facing exchange
// would look like.
// Map each tool call to a human chip label so the operator sees what EON
// actually DID this turn (not just the prose) — proof it's the real agent.
const CSW_SIM_TOOL_LABELS = {
  search_products: 'Searched catalog',
  lookup_product_url: 'Opened product',
  check_stock: 'Checked live stock',
  get_customer_history: 'Read order history',
  save_customer_note: 'Saved a note',
  add_to_cart: 'Added to cart',
  view_cart: 'Viewed cart',
  remove_from_cart: 'Removed from cart',
  create_payment_link: 'Created payment link',
};

function cswSimActions(actions) {
  if (!actions || !actions.length) return '';
  const chips = actions.map(a => {
    const lab = CSW_SIM_TOOL_LABELS[a.tool] || a.tool;
    return `<span class="csw-simchip ${a.ok === false ? 'bad' : ''}">${esc(lab)}</span>`;
  }).join('');
  // Product cards (with images) + a pay button from the tool results.
  let cards = '';
  for (const a of actions) {
    const d = a.display;
    if (!d) continue;
    if (d.kind === 'products' && (d.products || []).length) {
      cards += '<div class="csw-simcards">' + d.products.filter(Boolean).map(p => `
        <a class="csw-simcard" href="${esc(p.url || '#')}" target="_blank" rel="noopener">
          ${p.image ? `<img class="csw-simcard__img" src="${esc(p.image)}" alt="${esc(p.title || '')}" loading="lazy">` : '<div class="csw-simcard__img csw-simcard__img--none"></div>'}
          <div class="csw-simcard__t">${esc(p.title || '')}</div>
          ${p.price != null && p.price !== '' ? `<div class="csw-simcard__p">${esc(String(p.price))}</div>` : ''}
        </a>`).join('') + '</div>';
    } else if (d.kind === 'payment' && d.payment_url) {
      cards += `<a class="csw-simpay" href="${esc(d.payment_url)}" target="_blank" rel="noopener">Pay${d.total ? ' ' + esc(String(d.total)) : ''} →</a>`;
    }
  }
  return `<div class="csw-simacts">${chips}</div>${cards}`;
}

function cswSimBubble(m) {
  if (m.role === 'user') {
    const img = m.image ? `<img class="csw-bub__simimg" src="${esc(m.image)}" alt="sent photo">` : '';
    return `<div class="csw-bub in csw-bub--simcust">
      <div class="csw-bub__simtag">Customer (you)</div>
      ${img}
      ${m.text ? `<div class="csw-bub__txt">${esc(m.text)}</div>` : ''}
    </div>`;
  }
  const body = m.pending
    ? '<span class="csw-steps" aria-hidden="true"><i></i><i></i><i></i></span><span class="csw-steps__lbl">eon is working</span>'
    : cswLinkify(m.text || '');
  return `<div class="csw-bub out csw-bub--simeon">
    <div class="csw-bub__simtag">${eonLogo(13)} EON → customer</div>
    <div class="csw-bub__txt">${body}</div>
    ${m.pending ? '' : cswSimActions(m.actions)}
  </div>`;
}

function cswSimRenderScript() {
  const s = cswState();
  const scr = document.getElementById('csw-sim-script');
  if (!scr) return;
  const msgs = s._simMsgs || [];
  if (!msgs.length) {
    scr.innerHTML = `<div class="csw-empty"><div class="csw-empty__m">${eonLogo(28)}</div>Play the customer — ask about a piece, sizing, price, delivery, send a photo, or run a full checkout. This is the REAL agent (Opus + live tools); test orders are real and tagged [EON TESTER]. No WhatsApp is sent.</div>`;
    return;
  }
  const atBottom = (scr.scrollHeight - scr.scrollTop - scr.clientHeight) < 120;
  scr.innerHTML = msgs.map(cswSimBubble).join('');
  if (atBottom || msgs[msgs.length - 1]?.role === 'user' || msgs[msgs.length - 1]?.pending) scr.scrollTop = scr.scrollHeight;
}

async function cswLoadSimThread() {
  const s = cswState();
  s._simMsgs = s._simMsgs || [];
  cswSimRenderScript();
  try {
    const d = await api('/api/cs/eon/simulate/thread?limit=200');
    if (s.active !== CSW_EON_CID || (s._eonMode || 'ops') !== 'sim') return;
    s._simConvId = d.conversation_id || null;
    s._simMsgs = (d.messages || []).map(m => ({
      role: m.role === 'assistant' ? 'eon' : 'user',
      text: m.content || '',
    }));
    cswSimRenderScript();
  } catch (_) { /* keep whatever is cached */ }
}

async function cswSimSend() {
  const s = cswState();
  const inp = document.getElementById('csw-sim-input');
  const send = document.getElementById('csw-sim-send');
  const text = (inp?.value || '').trim();
  const img = (s._simStagedImg && s._simStagedImg !== '__uploading__') ? s._simStagedImg : null;
  if ((!text && !img) || s._simBusy) return;
  s._simMsgs = s._simMsgs || [];
  s._simMsgs.push({ role: 'user', text, image: img });
  const pend = { role: 'eon', text: '', pending: true };
  s._simMsgs.push(pend);
  s._simBusy = true;
  s._simStagedImg = null;
  if (inp) { inp.value = ''; inp.style.height = 'auto'; }
  if (send) send.disabled = true;
  cswSimRenderStaged();
  cswSimRenderScript();
  try {
    const d = await api('/api/cs/eon/simulate', {
      method: 'POST',
      body: JSON.stringify({ text, image_url: img, overnight: !!s._simOvernight, customer_anchor: (s._simAnchor && s._simAnchor.zoko_customer_id) || null }),
    });
    pend.text = (d && d.ok && d.reply) ? d.reply
      : 'Agent error' + (d && d.error ? ': ' + d.error : ' — please retry.');
    pend.actions = (d && d.ok && d.tool_actions) ? d.tool_actions : null;
  } catch (_) {
    pend.text = 'Agent error — please retry.';
  }
  pend.pending = false;
  s._simBusy = false;
  cswSimRenderScript();
}

async function cswSimReset() {
  const s = cswState();
  s._simMsgs = [];
  cswSimRenderScript();
  try {
    await api('/api/cs/eon/simulate', { method: 'POST', body: JSON.stringify({ text: '', reset: true }) });
    cswToast('Simulator cleared');
  } catch (_) {
    cswToast('Could not reset — retry.', true);
  }
}

// Who holds the chat, as one comparable word (vet 2026-09-24, fault 13):
// the list refresh compares the old and new holder and repaints the bar
// only when it moved, so a live bar is never rebuilt under the rep's thumb.
function cswBallKey(b) {
  return b ? [b.kind || '', String(b.holder || '').toLowerCase(), b.note || ''].join('|') : '';
}

function cswPaintBallBar() {
  // THE STAGE (2026-08-18, artifact ebede26e): the ball is a MODE, not a
  // proposal — so it no longer takes a bar of its own above the strip
  // (both could render at once and eat ~130px of transcript). It is the
  // composer's own state: a violet edge, one line — "EON has the ball —
  // type to take over" — and TYPING IS THE TAKEOVER (one gesture; the
  // 44px key stays for thumbs). #csw-ballbar remains the mount so classic
  // and house both show who holds the thread; it now lives INSIDE the
  // composer's flow rather than over the send zone.
  const s = cswState();
  const el = document.getElementById('csw-ballbar');
  if (!el) return;
  const composer = document.querySelector('#csw-conv .csw-composer');
  const b = s.activeThread && s.activeThread.ball;
  const inp = document.getElementById('csw-input');
  const stand = () => {
    el.hidden = true; el.innerHTML = '';
    composer?.classList.remove('csw-composer--eon');
    // A shut WhatsApp window keeps its own placeholder (2026-09-06): this
    // painter runs after the closed-window branch on every ball repaint and
    // used to put 'Message' back over 'Window closed — write now; Send holds
    // it and sends the pick-up'. Live on p1507 the box read 'Message' on a
    // closed thread. The ball bar still says who holds the thread.
    if (inp) { if (!cswWinClosed()) inp.placeholder = 'Message'; delete inp.dataset.eonBall; }
  };
  if (!b || b.kind !== 'eon') { stand(); return; }
  // Plain words (vet 2026-09-24, fault 13): who is answering, then the one
  // thing the rep can do about it. "The ball" was desk shop talk.
  el.innerHTML = `<span class="csw-ballbar__w"><i class="csw-ballbar__dot"></i><b>EON is answering this chat.</b> Type to take over.${b.note ? ` <span class="csw-ballbar__note">· ${esc(b.note)}</span>` : ''}</span>
    <button class="csw-takeover" id="csw-takeover" type="button">Take over</button>`;
  el.hidden = false;
  composer?.classList.add('csw-composer--eon');
  if (inp) { if (!cswWinClosed()) inp.placeholder = 'Type to take over…'; inp.dataset.eonBall = '1'; }
  const takeover = async () => {
    const cid = s.active;
    if (el.dataset.busy) return;
    el.dataset.busy = '1';
    try {
      const r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/takeover`, { method: 'POST', body: JSON.stringify({}) });
      if (r && r.ok) {
        if (s.activeThread) s.activeThread.ball = { kind: 'rep', holder: 'you' };
        stand();
        s._txSig = null; if (typeof cswLoadTranscript === 'function') cswLoadTranscript(cid, true);   // repaint: a waiting pre-draft may come now
        cswToast('The thread is yours — EON has yielded.');
        cswRenderThreadList();
        document.getElementById('csw-input')?.focus();
      } else { cswToast('Could not take over — retry.', true); }
    } catch (e) {
      // A 409 is the desk's own refusal in plain words (another rep holds
      // the thread, or rehearsal): show it, never a bare "retry".
      cswToast(cswSayWhy(e, { lead: 'Could not take over.' }), true);
    }
    delete el.dataset.busy;
  };
  el.querySelector('#csw-takeover')?.addEventListener('click', takeover);
  // Type to take over: the first keystroke while EON holds the thread IS
  // the claim. One-shot, and only while the flag the paint set is live —
  // a stale listener from a previous thread's ball must never fire.
  if (inp && !inp._cswBallListen) {
    inp._cswBallListen = true;
    inp.addEventListener('input', () => {
      // A PROGRAMMATIC fill is not a keystroke: cswAutoAssist pre-drafts
      // into the composer with inp._cswProgFill set — without this guard
      // that fill stripped the ball from EON and discarded its queued
      // reply with no tap (Opus vet, 2026-08-19). Explicit legacy tools
      // (quick replies, slash picks, stock inserts) also dispatch input.
      // Preserve their takeover; silent/confirmed prepared fills set the
      // programmatic flag instead of rejecting every synthetic input.
      if (inp.dataset.eonBall && inp.value.trim() && !inp._cswProgFill) takeover();
    });
  }
}

function cswOpenThread(cid, keep) {
  const s = cswState();
  const key = String(cid || '').trim();
  if (!key) { cswToast('This conversation is unavailable.', true); return false; }
  cswThreadMetaInvalidate(); // Same-key reopen must reject the previous visit's response.
  if (!keep) cswFromDrop();  // R5b7: a chat the person opens is a move
  cid = key;
  // B2 stamps the thread being LEFT, B1 the one opened. (Terse on
  // purpose: a fixed-width pin measures this head — see cswMarkLeft.)
  if (s.active && s.active !== cid) cswMarkLeft(s.active);
  cswNowTouch(cid);
  s.active = cid;
  s._workContext = cswDeskWorkContext(cid);
  s.activeThread = s.threads.find(t => cswThreadKey(t) === cid) || null;
  s._lastCustomerThread = s.activeThread || s._lastCustomerThread;   // for the EON context hand-off
  s._custIntel = null;   // drop the previous customer's intel card on switch
  // Mark seen for this rep (truthful unread) + clear it locally now.
  // NOTE: `unreplied` is deliberately NOT cleared here — reading is not
  // replying; only an outbound rep message clears the awaiting state
  // (server computes it from the latest direction on the next load).
  if (s.activeThread) { s.activeThread.unread = false; s.activeThread.unread_count = 0; }
  api(`/api/cs/threads/${encodeURIComponent(cid)}/seen`, { method: 'POST', body: JSON.stringify({}) }).catch(() => {});
  document.getElementById('csw-inbox')?.classList.add('has-active');
  cswClearReply();
  cswRenderThreadList();
  cswTabbarSync();
  cswRenderConvShell();
  cswPaintBallBar();
  cswLoadLookOffers(cid);
  cswStageStylistSync(cid);   // the stylist offer's card, when this thread has one
  cswHoldSync(cid);           // LANE B: the live counter hold's card, when this thread has one
  cswLoadTranscript(cid);   // paints messages, then enriches (360 loads labels)
  cswLoadConvAvatar(cid);
  cswLoadTier(cid);         // loyalty/VIP tier badge (silent on bronze/none)
  cswLoadAlso(cid);         // B4 one-customer rail (other-channel threads)
  cswLoadFaqBanner(cid);    // 'auto-answered' oversight bar + Override
  cswLoadCustomerCard(cid); // Lane A: "N orders · last: …" under the name
  cswUpdatePresence(false);   // announce I'm viewing this thread
  cswRenderPresence();        // show any other rep already here
  cswPaintNowShelf();         // B1: this thread is now at the front of the shelf
  cswContactLoad(cid);        // quiet contact plan; one read per opened conversation
  cswLoadRemembered(cid);     // B6: the size and address the chat already carries
  cswPaintWorkContext(cid);       // Current work, independent of the optional close ladder.
  cswLoadRung(cid);               // Saved close stage; never owns reply guidance.
  cswLoadPhrases();           // B5: the rep's own lines, cached ten minutes
  cswTellAskCustomer();       // Ask EON's private panel follows the open customer (A1-4)
  return true;
}

/* ══════════════════════════════════════════════════════════════════
   THE STAGE — one slot above the composer where every EON proposal
   shows its face before it asks for a tap (operator 2026-08-18: "i like
   the redesign build it all"; design artifact ebede26e "EON's Stage").

   Anatomy, fixed order, 64px at rest: MEDIA (a thumb stack, a countdown
   ring, an avatar) · NAME people use ("Look 113", never a tag list or an
   id) · a mono line of reason + count + AGE · at most TWO VERBS (one
   filled — CRUX yellow when the tap reaches the customer, violet when it
   hands to EON — one ghost) · a caret that OPENS IN PLACE to the exact
   thing that will happen. Cards queue in the slot: the clock (a held
   reply) outranks a look, a look outranks an offer; the next one peeks.
   Nothing here is position:fixed — the sheet grows inside the slot and
   pushes the transcript up like the keyboard does; the composer keeps
   the bottom edge (floor 3's law).

   The look-offer strip is a CUSTOMER-thread affordance, so it is
   defined BELOW cswOpenThread on purpose: everything between the
   CSW_EON_CID sentinel and cswOpenThread is the EON pane's region,
   and tests/test_cs_eon_thread.test_eon_pane_has_no_zoko_send_call
   reads that whole span to prove the EON pane posts to no send
   route at all. Declarations hoist, so cswOpenThread still calls it.
   ══════════════════════════════════════════════════════════════════ */
const CSW_STAGE_ORDER = { held: 0, look: 1, stylist: 2, hold: 3, draft: 4, photo: 5 };   // hold: LANE B counter hold (mig 692)
const CSW_STAGE_AGE_AMBER_MS = 6 * 3600 * 1000;

function cswStageState() {
  const s = cswState();
  return (s._stage = s._stage || { cards: {}, open: false, cid: null });
}

// Upsert / remove one card by kind, then repaint. A card is
// {kind, key, media, name, sub, fill:{label, cls, act}, ghost:{label, act},
//  sheet: () => html, bind: (root) => void, onOpen}. Painters keep their
// own state; the Stage only orders and draws.
function cswStageSet(kind, card) {
  const st = cswStageState();
  const s = cswState();
  if (st.cid !== s.active) { st.cards = {}; st.open = false; st.selectedKind = null; st.cid = s.active; }
  if (card) card.kind = kind; else delete st.cards[kind];
  if (card) st.cards[kind] = card;
  cswStagePaint();
}

function cswStageTop() {
  const st = cswStageState();
  const kinds = Object.keys(st.cards).sort((a, b) => (CSW_STAGE_ORDER[a] ?? 9) - (CSW_STAGE_ORDER[b] ?? 9));
  if (!st.cards.held && st.selectedKind && kinds.includes(st.selectedKind)) { kinds.splice(kinds.indexOf(st.selectedKind), 1); kinds.unshift(st.selectedKind); }
  return { top: kinds.length ? st.cards[kinds[0]] : null, rest: kinds.length - 1, kinds };
}

function cswStageAge(iso) {
  const t = Date.parse(iso || '');
  if (!isFinite(t)) return { label: '', old: false };
  const ms = Date.now() - t;
  const m = Math.max(0, Math.floor(ms / 60000));
  const label = m < 1 ? 'now' : m < 60 ? `${m}m` : m < 1440 ? `${Math.floor(m / 60)}h` : `${Math.floor(m / 1440)}d`;
  return { label, old: ms > CSW_STAGE_AGE_AMBER_MS };
}

function cswSyncHeldActions(top) {
  const stagedId = cswHouse() && top?.kind === 'held' ? String(top.key) : null;
  document.querySelectorAll('.csw-row--held[data-hold-id]').forEach(row => {
    const staged = stagedId !== null && String(row.dataset.holdId) === stagedId;
    row.querySelectorAll('[data-held-act="send"], [data-held-act="discard"]').forEach(button => {
      button.hidden = staged;
    });
  });
}

function cswStagePaint() {
  const el = document.getElementById('csw-lookoffer');
  if (!el) return;
  const st = cswStageState();
  const { top, rest } = cswStageTop();
  cswSyncHeldActions(top);
  if (!top) { el.hidden = true; el.innerHTML = ''; el.classList.remove('has-queue'); el.dataset.state = ''; return; }
  const chev = `<svg class="csw-stage__caret" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>`;
  el.innerHTML = `<div class="csw-stage__peek" aria-hidden="true"></div>
    <div class="csw-stage__row${top.kind === 'draft' || top.kind === 'photo' ? ' csw-stage__row--prepared' : ''}">
      <button class="csw-stage__media" type="button" aria-label="${rest > 0 ? `Show ${rest + 1} waiting items` : 'Review prepared item'}">${top.media || ''}${rest > 0 ? `<b class="csw-stage__queue-count">${rest + 1}</b>` : ''}</button>
      <button class="csw-stage__txt" type="button" aria-expanded="${st.open ? 'true' : 'false'}">
        <div class="csw-stage__h"><span class="csw-stage__from" title="Staged by EON"></span>${top.name}${top.sheet ? chev : ''}</div>
        <div class="csw-stage__s">${rest > 0 ? `<b class="csw-stage__more">+${rest} waiting</b> · ` : ''}${top.sub || ''}</div>
      </button>
      <div class="csw-stage__acts">
        ${top.fill ? `<button class="csw-looksend ${top.fill.cls || ''}" data-stage-act="fill" type="button">${top.fill.label}</button>` : ''}
        ${top.ghost ? `<button class="csw-lookskip" data-stage-act="ghost" type="button">${top.ghost.label}</button>` : ''}
      </div>
    </div>
    <div class="csw-stage__sheet" ${st.open && top.sheet ? '' : 'hidden'}>${st.open && top.sheet ? top.sheet() : ''}</div>`;
  el.hidden = false;
  el.classList.toggle('has-queue', rest > 0);
  el.dataset.state = st.open && top.sheet ? 'open' : 'rest';
  const toggle = () => {
    if (!top.sheet) { if (top.onOpen) top.onOpen(); return; }
    st.open = !st.open;
    cswStagePaint();
    if (st.open) {
      el.scrollIntoView({ block: 'nearest', behavior: cswHouse() && !(window.EONHouse && window.EONHouse.reduced) ? 'smooth' : 'auto' });
      if (top.onOpen) top.onOpen();
    }
  };
  el.querySelector('.csw-stage__media')?.addEventListener('click', rest > 0 ? cswStageQueue : toggle);
  el.querySelector('.csw-stage__txt')?.addEventListener('click', toggle);
  el.querySelector('[data-stage-act="fill"]')?.addEventListener('click', () => top.fill && top.fill.act());
  el.querySelector('[data-stage-act="ghost"]')?.addEventListener('click', () => top.ghost && top.ghost.act());
  if (st.open && top.sheet && top.bind) top.bind(el.querySelector('.csw-stage__sheet'));
}

// A card's settle line: the verb happened. Reads for 2.6s, then the
// next card (or nothing) takes the slot.
function cswStageDone(kind, html, muted) {
  const el = document.getElementById('csw-lookoffer');
  const st = cswStageState();
  delete st.cards[kind]; st.open = false;
  if (!el) return;
  el.hidden = false; el.dataset.state = 'done'; el.classList.remove('has-queue');
  el.innerHTML = `<div class="csw-stage__done${muted ? ' muted' : ''}">${html}</div>`;
  setTimeout(() => { if (el.dataset.state === 'done') cswStagePaint(); }, 2600);
}

function cswStageThumbStack(lead, pieces, total) {
  const withImg = (pieces || []).filter(x => x && x.image_url);
  const p = withImg.slice(0, 2);
  // +N counts what the stack did NOT show — of the pieces that exist,
  // not of the thumbs that happened to have an image (Opus vet).
  const more = Math.max(0, (Number.isFinite(total) ? total : (pieces || []).length) - p.length);
  return `<span class="csw-stack" aria-hidden="true">
    ${lead ? `<img class="csw-stack__lead" src="${esc(cswImgSrc(lead))}" alt="" loading="lazy" onerror="cswPmThumbGone(this)">` : '<span class="csw-stack__lead csw-stack__lead--none"></span>'}
    ${p[0] ? `<img class="csw-stack__p1" src="${esc(cswImgSrc(p[0].image_url))}" alt="" loading="lazy" onerror="this.remove()">` : ''}
    ${p[1] ? `<img class="csw-stack__p2" src="${esc(cswImgSrc(p[1].image_url))}" alt="" loading="lazy" onerror="this.remove()">` : ''}
    ${more > 0 ? `<span class="csw-stack__more">+${more}</span>` : ''}
  </span>`;
}

function cswNaira(v) {
  if (v === null || v === undefined || v === '') return '';   // the server's _naira: no price, no number
  const n = Number(v);
  return isFinite(n) ? '₦' + Math.round(n).toLocaleString('en-NG') : '';
}

// The caption the customer will receive — mirrors cs_look_offers
// .compose_caption exactly (name — occasion / numbered kept pieces /
// the reply line), so "What ⟨name⟩ gets" is the contract, not a guess.
function cswLookCaption(o, keptIds) {
  const title = String(o.look_title || 'The look').toUpperCase();
  const occ = o.occasion ? String(o.occasion).toUpperCase() : '';
  const lines = [occ ? `${title} — ${occ}` : title];
  const kept = (o.pieces || []).filter(p => !keptIds || keptIds.has(String(p.shopify_id)));
  const use = kept.length ? kept : (o.pieces || []);
  use.forEach((p, i) => {
    const tail = p.in_stock ? cswNaira(p.price) : 'back soon';
    lines.push(`${i + 1}. ${String(p.title || '').trim()}${tail ? ` — ${tail}` : ''}`);
  });
  lines.push("Reply with a number and your size, or 'the whole look'.");
  return lines;
}

// The product the customer's NEWEST photo/ask names, if the enrichment
// stamped one — so the reason line can say "not the jersey she just
// sent" with a fact, not a feeling. Read from the transcript already on
// screen; null when nothing confident is there.
function cswLatestInboundProduct() {
  const s = cswState();
  const rows = (s.transcript || []);
  for (let i = rows.length - 1; i >= 0; i--) {
    const r = rows[i];
    if ((r.direction || '').toLowerCase() !== 'inbound') continue;
    const pm = (r.body || {}).product_match;
    if (!pm) continue;
    // Same read as cs_look_offers._pm_pid: the first AUTO item in any
    // position — a 2-item photo whose first item is no_match must not
    // read as "no product" here while the server expires the card.
    for (const it of (pm.items || [])) {
      const m = it && it.status === 'auto' && (it.matches || [])[0];
      if (m && m.product_shopify_id) return { id: String(m.product_shopify_id), title: m.title || m.handle || '', at: i };
    }
    return null;
  }
  return null;
}

// 2026-09-24 (vet 18): the product link the customer's NEWEST message
// carries (crux.ng/products/<handle>, read by the same exact parser the
// transcript links with). null when no inbound message names one.
function cswLatestProductLink() {
  const rows = cswState().transcript || [];
  for (let i = rows.length - 1; i >= 0; i--) {
    const r = rows[i];
    if ((r.direction || '').toLowerCase() !== 'inbound') continue;
    const b = r.body || {};
    for (const l of cswMessageLinks(b.text || b.caption || '')) {
      const url = cswCruxProductUrl(l.url);
      if (!url) continue;
      const handle = new URL(url).pathname.split('/')[2].toLowerCase();
      return { handle, words: handle.replace(/-/g, ' '), at: i };
    }
  }
  return null;
}

async function cswLoadLookOffers(cid) {
  const el = document.getElementById('csw-lookoffer');
  if (!el) return;
  cswStageSet('look', null);
  let offers = [];
  try {
    const r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/look-offers`);
    offers = (r && r.offers) || [];
  } catch (_) { return; }
  const s = cswState();
  if (s.active !== cid) return;              // rep moved on mid-fetch
  const o = offers.find(x => x && x.status === 'staged');
  if (!o) return;
  cswStageLookCard(cid, o);
}

function cswStageLookCard(cid, o) {
  const s = cswState();
  // A switch/refetch that lands after the rep opened ANOTHER thread must
  // not paint this thread's card there — the offer row sends to its own
  // thread_key while the key would read "Send to ⟨new name⟩" (Opus vet).
  if (s.active !== cid) return;
  const first = (cswDisplayName(s.activeThread || {}) || 'customer').split(/\s+/)[0] || 'customer';
  const pieces = o.pieces || [];
  const kept = new Set(pieces.map(p => String(p.shopify_id)));
  const total = () => pieces.filter(p => kept.has(String(p.shopify_id))).reduce((a, p) => a + (Number(p.price) || 0), 0);
  const age = cswStageAge(o.staged_at);
  const stock = pieces.every(p => p.in_stock) ? 'in stock' : `${pieces.filter(p => !p.in_stock).length} back soon`;
  let busy = false;
  const send = async () => {
    if (busy) return; busy = true;
    try {
      const body = { piece_ids: [...kept] };
      const r = await api(`/api/cs/look-offers/${encodeURIComponent(o.id)}/send`, { method: 'POST', body: JSON.stringify(body) });
      if (r && r.ok) {
        cswStageDone('look', `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l5 5L20 7"/></svg>${esc(o.look_title || 'The look')} sent to ${esc(first)}`);
        cswLoadTranscript(cid);
      } else cswToast(cswSayWhy({ message: String((r && r.error) || '') }, { lead: 'The look was not sent.' }), true);
    } catch (e) { cswToast(cswSayWhy(e, { send: true }), true); }
    busy = false;
  };
  const skip = async () => {
    if (busy) return; busy = true;
    let r = null;
    try { r = await api(`/api/cs/look-offers/${encodeURIComponent(o.id)}/skip`, { method: 'POST', body: JSON.stringify({}) }); }
    catch (e) { r = { ok: false, code: (e && e.status === 409) ? 'already_settled' : 'error', error: e && e.message }; }
    // Only a RECORDED vote reads as one. An expired card says so; an
    // already-settled card (EON sent it / another rep acted) leaves
    // quietly; a transport error keeps the card and tells the rep.
    if (r && r.ok) cswStageDone('look', `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>Skipped — one vote against ${esc(o.look_title || 'this look')} here. Nothing sent.`, true);
    else if (r && r.code === 'expired') cswStageDone('look', `${esc(o.look_title || 'The look')} had already expired — no vote taken.`, true);
    else if (r && r.code === 'already_settled') cswStageDone('look', `${esc(o.look_title || 'The look')} was already acted on — no vote taken.`, true);
    else cswToast(cswSayWhy({ message: String((r && r.error) || '') }, { lead: 'Not skipped.' }), true);
    busy = false;
  };
  const sub = () => {
    const n = kept.size;
    return `${n} pc${n === 1 ? '' : 's'} · <span class="csw-stage__age${age.old ? ' old' : ''}">${esc(age.label)}</span> · ${cswNaira(total())}${o.occasion ? ` · ${esc(o.occasion)}` : ''}`;
  };
  const sheet = () => {
    const now = cswLatestInboundProduct();
    const anchor = o.anchor_title || '';
    const mismatch = now && o.anchor_shopify_id && now.id !== String(o.anchor_shopify_id);
    const why = anchor
      ? (mismatch
        ? `<span class="warn">▲</span><span><b>Anchored on ${esc(anchor)}</b> (mentioned earlier in this chat) — not ${esc(now.title || 'the product')} she just sent. Your call.</span>`
        : `<span class="dot"></span><span><b>Anchored on ${esc(anchor)}</b> — the product in play in this chat.</span>`)
      : `<span class="dot"></span><span>Staged by EON from this conversation.</span>`;
    const pcs = pieces.map(p => `<button class="csw-piece" type="button" data-pid="${esc(String(p.shopify_id))}" aria-pressed="${kept.has(String(p.shopify_id)) ? 'true' : 'false'}">
        ${p.image_url ? `<img src="${esc(cswImgSrc(p.image_url))}" alt="" loading="lazy" onerror="cswPmThumbGone(this)">` : '<span class="csw-pm__thumb csw-pm__thumb--none"></span>'}
        <span class="csw-piece__m"><span class="csw-piece__t">${esc(p.title || 'Piece')}</span><span class="csw-piece__p">${cswNaira(p.price)}${p.in_stock === undefined || p.in_stock === null ? '' : `<span class="csw-piece__stk${p.in_stock ? '' : ' no'}">${p.in_stock ? 'In stock' : 'Back soon'}</span>`}</span></span>
        <span class="csw-piece__tick"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l5 5L20 7"/></svg></span>
      </button>`).join('');
    const alts = (o.alternates || []).length
      ? `<div class="csw-stage__alts"><span class="csw-stage__altl">Other looks for ${esc(anchor || 'this piece')}</span><div class="csw-stage__altrail">${o.alternates.map(a => `<button class="csw-stage__alt" type="button" data-alt="${esc(a.look_id)}" title="${esc(a.look_title)}${a.occasion ? ' · ' + esc(a.occasion) : ''}">${a.image_url ? `<img src="${esc(cswImgSrc(a.image_url))}" alt="" loading="lazy">` : ''}<span>${esc(a.look_title)}</span></button>`).join('')}</div></div>`
      : '';
    const cap = cswLookCaption(o, kept);
    const rec = o.record || {};
    const recLine = rec.shares ? `<b>${esc(o.look_title)}</b> · sent ${rec.shares}× · ${rec.paid} bought${rec.proven ? ' · proven' : ''}` : `<b>${esc(o.look_title)}</b> · no record yet`;
    const exp = o.expires_at ? `expires in ${esc(cswStageExpiresIn(o.expires_at))}` : 'expires with the chat';
    return `<div class="csw-stage__why">${why}</div>
      <div class="csw-stage__plate">
        ${o.image_url ? `<img class="csw-stage__big" src="${esc(cswImgSrc(o.image_url))}" data-full="${esc(cswImgSrc(o.image_url))}" alt="${esc(o.look_title || 'The look')}" loading="lazy">` : '<span class="csw-stage__big csw-stage__big--none"></span>'}
        <div class="csw-stage__pieces">${pcs}</div>
      </div>
      ${alts}
      <div class="csw-stage__preview"><div class="csw-stage__ph"><span>What ${esc(first)} gets</span><span>photo + caption</span></div><div class="csw-stage__msg" data-role="caption"><b>${esc(cap[0])}</b>\n${cap.slice(1).map(esc).join('\n')}</div></div>
      <div class="csw-stage__record"><span>${recLine}</span><span>staged by EON · <b>${esc(age.label === 'now' ? 'just now' : age.label + ' ago')}</b> · ${exp}</span></div>
      <div class="csw-stage__sheetacts"><button class="csw-looksend" data-sheet-act="send" type="button">Send to ${esc(first)}</button><button class="csw-lookskip" data-sheet-act="skip" type="button">Skip — not for ${esc(first)}</button></div>`;
  };
  const bind = (root) => {
    if (!root) return;
    root.querySelector('[data-sheet-act="send"]')?.addEventListener('click', send);
    root.querySelector('[data-sheet-act="skip"]')?.addEventListener('click', skip);
    root.querySelector('.csw-stage__big')?.addEventListener('click', (e) => {
      const u = e.currentTarget.getAttribute('data-full');
      if (u) cswLightbox(u, o.look_title || 'The look');
    });
    root.querySelectorAll('.csw-piece').forEach(btn => btn.addEventListener('click', () => {
      const pid = btn.dataset.pid;
      const on = kept.has(pid);
      if (on && kept.size === 1) return;             // never an empty caption
      if (on) kept.delete(pid); else kept.add(pid);
      btn.setAttribute('aria-pressed', on ? 'false' : 'true');
      const cap = cswLookCaption(o, kept);
      const m = root.querySelector('[data-role="caption"]');
      if (m) m.innerHTML = `<b>${esc(cap[0])}</b>\n${cap.slice(1).map(esc).join('\n')}`;
      const card = cswStageState().cards.look;
      if (card) { card.sub = sub(); const s2 = document.querySelector('#csw-lookoffer .csw-stage__s'); const { rest } = cswStageTop(); if (s2) s2.innerHTML = (rest > 0 ? `<b class="csw-stage__more">+${rest} waiting</b> · ` : '') + card.sub; }
    }));
    root.querySelectorAll('.csw-stage__alt').forEach(btn => btn.addEventListener('click', async () => {
      if (busy) return; busy = true;
      btn.classList.add('busy');
      try {
        const r = await api(`/api/cs/look-offers/${encodeURIComponent(o.id)}/switch`, { method: 'POST', body: JSON.stringify({ look_id: btn.dataset.alt }) });
        if (r && r.ok && r.offer) { busy = false; cswStageLookCard(cid, r.offer); return; }
        cswToast(cswSayWhy({ message: String((r && r.error) || '') }, { lead: 'The look did not switch.' }), true);
      } catch (e) { cswToast(cswSayWhy(e, { lead: 'The look did not switch.' }), true); }
      busy = false;
    }));
  };
  cswStageSet('look', {
    key: o.id,
    media: cswStageThumbStack(o.image_url, pieces, pieces.length),
    name: `${esc(o.look_title || 'The look')}`,
    sub: sub(),
    fill: { label: `Send to ${esc(first)}`, act: send },
    ghost: { label: 'Skip', act: skip },
    sheet, bind,
  });
}

function cswStageExpiresIn(iso) {
  const t = Date.parse(iso || '');
  if (!isFinite(t)) return '';
  const m = Math.max(0, Math.round((t - Date.now()) / 60000));
  if (m < 60) return `${m}m`;
  if (m < 48 * 60) return `${Math.floor(m / 60)}h`;
  return `${Math.floor(m / 1440)}d`;
}

// ── the held reply's card: the clock at the top of the queue ──────────
// EON's written reply already waits in the transcript as a dashed bubble
// with Edit / Send now / Discard (2026-08-03) — that bubble is the
// PREVIEW; the Stage is the CONTROL. The card wears the countdown ring
// and the same two verbs, and opening it walks to the bubble. Called from
// the held ticker's sync so it lives and dies with the rows on screen.
function cswStageHeldSync() {
  const rows = document.querySelectorAll('.csw-row--held[data-release-at]');
  const st = cswStageState();
  if (!rows.length) { if (st.cards.held) cswStageSet('held', null); return; }
  const row = rows[0];
  const holdId = row.dataset.holdId;
  const txt = (row.querySelector('.csw-bub__txt')?.textContent || '').trim();
  const s = cswState();
  const first = (cswDisplayName(s.activeThread || {}) || 'customer').split(/\s+/)[0] || 'customer';
  const rehearsal = s._contactRehearsal === true;
  const label = rehearsal ? 'Rehearsal · remains held' : cswHeldLabel(row.dataset.releaseAt, s.activeThread);
  const blocked = cswWindowBlocks(s.activeThread);
  const left = blocked ? 0 : Math.max(0, Math.round((Date.parse(row.dataset.releaseAt || '') - Date.now()) / 1000));
  const ringWord = blocked ? 'Held' : left >= 60 ? Math.ceil(left / 60) + 'm' : left > 0 ? left + 's' : '…';
  const pct = Math.max(0, Math.min(100, Math.round(100 * left / 45)));
  const media = `<span class="csw-ring" style="--pct:${pct}%" aria-hidden="true"><span>${ringWord}</span></span>`;
  const existing = st.cards.held;
  if (existing && existing.key === holdId) {
    cswSyncHeldActions(cswStageTop().top);
    // tick only: no repaint, no scroll — write the two moving parts
    const el = document.getElementById('csw-lookoffer');
    const r = el?.querySelector('.csw-ring'); if (r) { r.style.setProperty('--pct', pct + '%'); const sp = r.querySelector('span'); if (sp) sp.textContent = ringWord; }
    if (cswStageTop().top === existing) { const send = el?.querySelector('[data-stage-act="fill"]'); if (send) { send.disabled = blocked && !rehearsal; send.textContent = rehearsal ? 'Review held reply' : blocked ? 'Held' : 'Send now'; } }
    const sub = el?.querySelector('.csw-stage__s'); if (sub && cswStageTop().top === existing) sub.innerHTML = existing.sub = `“${esc(txt.slice(0, 60))}${txt.length > 60 ? '…' : ''}” · ${esc(label.toLowerCase())}`;
    return;
  }
  const act = (which) => () => row.querySelector(`[data-held-act="${which}"]`)?.click();
  cswStageSet('held', {
    key: holdId,
    media,
    name: `Reply to ${esc(first)} <span class="csw-stage__occ">· EON wrote it</span>`,
    sub: `“${esc(txt.slice(0, 60))}${txt.length > 60 ? '…' : ''}” · ${esc(label.toLowerCase())}`,
    fill: { label: rehearsal ? 'Review held reply' : blocked ? 'Held' : 'Send now', act: act('send') },
    ghost: { label: rehearsal ? 'Rehearse discard' : 'Discard', act: act('discard') },
    sheet: null,
    onOpen: () => { row.scrollIntoView({ block: 'center', behavior: 'smooth' }); row.querySelector('[data-held-act="edit"]')?.focus(); },
  });
  if (cswStageTop().top === cswStageState().cards.held) { const send = document.querySelector('#csw-lookoffer [data-stage-act="fill"]'); if (send) send.disabled = blocked && !rehearsal; }
}

// ── the stylist offer's card ──────────────────────────────────────────
// The per-customer styling proposal (styling desk, 2026-07-27) reached
// the rep only from a chip on the thread list. When its thread is OPEN
// it now also stands on the Stage; the sheet it opens is the same
// cswStylOfferSheet, the send/dismiss doors unchanged.
function cswStageStylistSync(cid) {
  const s = cswState();
  const t = s.activeThread || {};
  const prop = (typeof cswStylOfferFor === 'function') ? cswStylOfferFor(t) : null;
  if (!prop || s.active !== cid) { if (cswStageState().cards.stylist) cswStageSet('stylist', null); return; }
  const first = (cswDisplayName(t) || 'customer').split(/\s+/)[0] || 'customer';
  const looks = (prop.looks || []).filter(l => l && l.image_url);
  const ready = prop.send_ready !== false;
  cswStageSet('stylist', {
    key: String(prop.id || prop.recipient || ''),
    media: cswStageThumbStack(looks[0] && looks[0].image_url, looks.slice(1).map(l => ({ image_url: l.image_url })), Math.max(0, looks.length - 1)),
    name: `Stylist offer <span class="csw-stage__occ">· for ${esc(first)}</span>`,
    sub: `${ready ? `${looks.length} look${looks.length === 1 ? '' : 's'}` : 'Needs review'} · ${esc(String(prop.product_title || '').slice(0, 40))}`,
    fill: { label: ready ? 'Send offer' : 'Review', act: () => cswStylOfferSheet(prop) },
    ghost: { label: 'Review', act: () => cswStylOfferSheet(prop) },
    sheet: null,
    onOpen: () => cswStylOfferSheet(prop),
  });
}

// Fetch a real profile pic for the open conversation (IG via the avatar
// resolver once the scope lands; gradient initials otherwise). Only the
// open thread — keeps the list fast.
async function cswLoadConvAvatar(cid) {
  const s = cswState(); const t = s.activeThread || {};
  if (!t.platform_sender_id) return;
  const channel = cswPlatform(t) === 'wa' ? 'whatsapp' : 'instagram';
  // IG: pass the username (the customer_name for IG) for Business Discovery.
  const uname = channel === 'instagram' ? (t.customer_name || '') : '';
  try {
    const d = await api(`/api/cs/avatar?platform_sender_id=${encodeURIComponent(t.platform_sender_id)}&channel=${channel}${uname ? '&username=' + encodeURIComponent(uname) : ''}`);
    if (!d || !d.found || !d.url || cswState().active !== cid) return;
    const host = document.querySelector('#csw-conv .csw-conv__bar .csw-av');
    if (host && !host.querySelector('img')) {
      const im = document.createElement('img');
      im.src = cswImgSrc(d.url); im.alt = ''; im.onerror = () => im.remove();
      host.insertBefore(im, host.querySelector('span'));
    }
  } catch (_) {}
}

// Loyalty/VIP tier badge for the open conversation. Keyed on the thread's
// zoko_customer_id (the endpoint resolves it to a phone the same way the
// send path does). Degrades silently: a bronze / no-history / errored
// lookup renders NOTHING — never an empty pill, never an error in the bar.
async function cswLoadTier(cid) {
  const host = document.getElementById('csw-tier');
  if (!host) return;
  host.innerHTML = '';   // clear any prior thread's badge
  let d;
  try { d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/tier`); }
  catch (_) { return; }
  if (!d || !d.ok) return;
  if (cswState().active !== cid) return;   // rep switched threads mid-fetch
  const tier = (d.tier || '').toLowerCase();
  // Only surface a standing tier — bronze / default is the baseline, no pill.
  // 'concierge' (mig 645) is the band ABOVE gold: it must render, or promoting
  // a whale would silently blank the badge a rep relies on.
  if (tier !== 'silver' && tier !== 'gold' && tier !== 'concierge') return;
  const orders = Number(d.lifetime_orders || 0);
  const title = orders ? `${orders} lifetime order${orders === 1 ? '' : 's'}` : 'Loyalty tier';
  const tgt = document.getElementById('csw-tier');
  if (tgt) tgt.innerHTML = `<span class="csw-tier csw-tier--${tier}" title="${esc(title)}">${esc(tier.toUpperCase())}</span>`;
}

// The thread header's customer line (CS desk Lane A, Lever 9, 2026-08-21):
// "Name · N orders · last: <title>, <store>, <Mon YYYY>" off
// customer_identities + eon_sync orders, server-rendered and cached 10
// min. Silent for a first-time sender (the common case — 85 of 1,113
// senders this month had bought before). House masks names at render
// sites, so there the line starts at the count; the name is the
// header's own first line already.
async function cswLoadCustomerCard(cid) {
  const host = document.getElementById('csw-cust');
  if (!host) return;
  host.hidden = true; host.textContent = '';
  let d;
  try { d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/customer-card`); }
  catch (_) { return; }
  if (!d || !d.ok || !d.found) return;
  if (cswState().active !== cid) return;   // rep switched threads mid-fetch
  // L5 (vet: store-fact-missing-pos-chip-dead): the card payload now names
  // the customer's most-frequent WALK-IN store (orders.sale_channel truth).
  // Cached under the thread key so the hold sheet can pre-light it — the
  // fact lands where the tap already is, not as another panel line.
  if (d.preferred_store) cswState()._storePref = { cid, name: String(d.preferred_store) };
  const tgt = document.getElementById('csw-cust');
  if (!tgt) return;
  let line = String(d.line || '');
  if (cswHouse() && d.name && line.startsWith(d.name)) line = line.slice(d.name.length).replace(/^\s*·\s*/, '');
  if (!line) return;
  tgt.textContent = line;
  tgt.title = line;
  tgt.hidden = false;
}

// ── B4 one-customer rail (directive 2026-07-27 WS-B) ──
// The same human often lives in two windows — the WA phone thread and the
// IG thread. The rail names every OTHER thread that resolves to the same
// identity; one tap jumps. Silence when there's nothing — never a stub row.
async function cswLoadAlso(cid) {
  const host = document.getElementById('csw-also');
  if (!host) return;
  host.hidden = true; host.innerHTML = '';
  // No identity hints on the query string: the server derives the sender id
  // and profile name from the gated thread. Passing them let a caller ask
  // "whose phone is this?" through a thread they were allowed to see.
  let d;
  try { d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/also`); }
  catch (_) { return; }
  if (!d || !d.ok || cswState().active !== cid) return;
  cswAlsoApplyName(cid, d);
  const rows = d.threads || [];
  if (!rows.length) return;
  host.innerHTML = '<span class="csw-also__l">Also this customer:</span>' + rows.map(r => {
    const ch = String(r.channel || '').toLowerCase().includes('insta') ? 'Instagram' : 'WhatsApp';
    const n = Number(r.n_messages || 0);
    // Say how we know. A mined link (contact read out of the transcript)
    // is worth offering but must not read as established fact.
    const via = r.via === 'confirmed' ? 'A rep linked these threads'
      : r.via === 'mined' ? 'Linked by a contact found in the chat — worth checking'
      : 'Same customer record';
    return `<button class="csw-also__chip${r.via === 'mined' ? ' csw-also__chip--soft' : ''}" type="button" title="${esc(via)}" data-also="${esc(r.zoko_customer_id)}" data-ch="${esc(r.channel || '')}" data-nm="${esc(r.customer_name || '')}">${esc(r.customer_name || 'thread')} on ${ch} · ${n} message${n === 1 ? '' : 's'}${r.via === 'mined' ? ' ?' : ''}</button>`;
  }).join('');
  host.hidden = false;
  host.querySelectorAll('[data-also]').forEach(b => b.addEventListener('click', () => {
    const id = b.dataset.also;
    const st = cswState();
    // A rail target outside the loaded list still opens — a minimal stub
    // carries the header truth (name/channel); the list render skips stubs.
    // The stub declares the WhatsApp window CLOSED on purpose: it does not
    // know the real state, and an unknown window read as open would let a
    // free-form message go out past the 24h mark and bounce at Meta. The
    // quiet reload below replaces the stub with the real row within a poll,
    // which re-opens the composer if the window truly is open.
    if (!st.threads.some(x => x.zoko_customer_id === id)) {
      const wa = !String(b.dataset.ch || '').toLowerCase().includes('insta');
      st.threads.push({ zoko_customer_id: id, customer_name: b.dataset.nm || '',
                        channel: b.dataset.ch || '', _plat: wa ? 'wa' : 'ig',
                        window_open: wa ? false : undefined,
                        _also_stub: true });
      try { cswLoadThreads(true); } catch (_) {}
    }
    cswOpenThread(id);
  }));
}

// B4 name hierarchy applied to the open header: rep-confirmed always wins;
// an order/channel name only replaces a numeric or 'Customer' shell. A real
// name replaced by a better one keeps a "was <old>" chip for 24h.
function cswAlsoApplyName(cid, d) {
  const el = document.querySelector('#csw-conv .csw-conv__name');
  if (!el) return;
  const cur = (el.childNodes[0] ? el.childNodes[0].textContent : el.textContent || '').trim();
  const next = (d.name || '').trim();
  const generic = !cur || cur === 'Customer' || /^\+?[\d\s()-]{7,}$/.test(cur);
  if (next && next !== cur &&
      (d.source === 'confirmed' || ((d.source === 'order' || d.source === 'channel') && generic))) {
    try {
      // Write ONCE. cswRenderConvShell repaints the header from thread-list
      // data (which still carries the old name), so this branch re-runs on
      // every poll — re-stamping ts here would keep the chip alive forever
      // instead of retiring it after a day.
      if (!generic && !localStorage.getItem('csw_was_' + cid))
        localStorage.setItem('csw_was_' + cid,
          JSON.stringify({ old: cur, ts: Date.now() }));
    } catch (_) {}
    el.textContent = next;
  }
  try {
    const v = JSON.parse(localStorage.getItem('csw_was_' + cid) || 'null');
    if (v && v.old && (Date.now() - v.ts) <= 864e5) {
      if (!el.querySelector('.csw-waschip'))
        el.insertAdjacentHTML('beforeend', ` <span class="csw-waschip">was ${esc(v.old)}</span>`);
    } else if (v) { localStorage.removeItem('csw_was_' + cid); }
  } catch (_) {}
}

// Standing chip for a thread-LIST row. One place that knows which tiers earn a
// chip, so the header badge (above) and the list row can't drift apart again —
// they had, and 'concierge' (mig 645) would have rendered in neither.
// Bronze / unknown gets nothing: standing is the signal, not the baseline.
function cswTierChip(tier) {
  const t = String(tier || '').toLowerCase();
  const label = { concierge: 'CONCIERGE', gold: 'GOLD', vip: 'VIP' }[t];
  if (!label) return '';
  // 'vip' is the legacy customers.tier spelling; it shares gold's styling.
  const cls = t === 'vip' ? 'gold' : t;
  return `<span class="csw-tier csw-tier--${cls}" style="margin-left:6px;"`
    + ` title="${label} customer">${label}</span>`;
}

function cswRenderConvShell() {
  const s = cswState();
  const conv = document.getElementById('csw-conv');
  if (!conv) return;
  const t = s.activeThread || {};
  const name = t.customer_name || 'Customer';
  const plat = cswPlatform(t);
  const chLabel = plat === 'wa' ? 'WhatsApp' : 'Instagram';
  conv.classList.add('show');
  conv.innerHTML = `
    <div class="csw-conv__bar">
      <button class="csw-conv__back" id="csw-conv-back" aria-label="Back to conversations"><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M14 6l-6 6 6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
      ${cswAvatar(name, { size: 'sm', seed: t.zoko_customer_id, pres: plat })}
      <div class="csw-conv__id" id="csw-360btn" role="button" tabindex="0" aria-label="Customer details" aria-expanded="false" aria-controls="csw-360">
        <div class="csw-conv__name"${cswHouseTitle()}>${esc(cswHouseName(t))}</div>
        <div class="csw-conv__cust" id="csw-cust" hidden></div>
        <div class="csw-conv__sub"><span class="csw-conv__scroll"><span id="csw-rtdot" class="csw-rtdot"></span><span id="csw-presence"></span><span class="csw-ch csw-ch--${plat}">${chLabel}</span>${cswHouse() && cswHouseNum(t.platform_sender_id) ? `<span class="csw-mask" title="Numbers stay masked on this desk">${esc(cswHouseNum(t.platform_sender_id))}</span>` : ''}${cswSourceChip(t)}${cswAltName(t)}</span><span class="csw-conv__pin"><span class="csw-rung" id="csw-rung" hidden></span><span id="csw-dialslot">${cswWindowDial(t)}</span><span class="csw-rehearse" hidden><i></i>REHEARSAL</span><span id="csw-tier"></span></span></div>
      </div>
      <details class="csw-conv-menu" id="csw-conv-menu">
      <summary aria-label="Conversation actions"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg></summary>
      <div class="csw-conv-menu__items">
      <button type="button" id="csw-menu-ask" hidden>Ask EON about this customer</button>
      <button class="csw-conv__searchbtn" id="csw-searchbtn" aria-label="Search in conversation" title="Search this chat">
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2"/><path d="M16.5 16.5L21 21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        <span>Search this conversation</span>
      </button>
      <button class="csw-routebtn ${t.route ? 'routed' : ''}" id="csw-routebtn" title="Department queue — tap to re-route" aria-label="Department queue — tap to re-route">${esc(cswQueueLabel(t) || 'Route to a team')}</button>
      <button class="csw-conv__360btn" id="csw-360btn2" aria-label="Customer info">Customer details</button>
      <button type="button" id="csw-menu-priority">Why this priority</button>
      <button type="button" id="csw-menu-progress">Service path</button>
      <button type="button" id="csw-menu-evidence" aria-haspopup="dialog">What needs checking</button>
      <button type="button" id="csw-menu-receipts">Message receipts</button>
      <button type="button" id="csw-menu-payment" aria-haspopup="dialog">Payment evidence</button>
      <button type="button" id="csw-menu-desk-search">Search the desk…</button>
      <button type="button" id="csw-menu-cases" aria-haspopup="dialog">Service cases</button>
      <button type="button" id="csw-menu-contact" aria-haspopup="dialog" hidden>Contact plan</button>
      <button type="button" id="csw-menu-emoji">Emoji picker</button>
      </div></details>
    </div>
    <div class="csw-legacy-context">${cswConvWhyHtml(t)}
    ${cswConvPathHtml(t)}</div>
    <div class="csw-contactline" id="csw-contactline" hidden></div>
    ${/* 01 — THE CUSTOMER FILE (the desk re-dressed, 2026-08-29). The
         /customer-context payload the 360 panel already fetches, read at
         the TOP of the thread where the rep is looking: how many orders,
         what they are worth, the sizes we know, how long since we saw
         them — and, when the file carries one, the red repeat-incident
         band. Painted from the SAME cache (cswState()._custIntel); no
         second fetch exists. Every chip is keyed on its own field, so a
         payload without it renders nothing at all. */''}
    <div class="csw-service-rail" id="csw-service-rail">
      <button type="button" data-service="progress" class="csw-service-rail__progress"><span class="csw-service-rail__path"></span></button>
      <div class="csw-service-rail__actions"><button type="button" data-service="why">Why priority</button><button type="button" data-service="receipts">Receipts</button><button type="button" data-service="payment" aria-haspopup="dialog">Payment</button></div>
    </div>
    <div class="scase-brief" id="csw-service-cases" hidden></div>
    ${/* X1 (2026-09-24, vet plan item 12): the chat's one next step —
         Send account details, Hold it at a store, or Confirm payment —
         in the service card's own place and dress. It stands only when
         that card does not (cswPaintNextStep). */''}
    <div class="scase-brief csw-next" id="csw-next-step" hidden></div>
    <div class="crd-file" id="csw-file" hidden></div>
    ${/* B6 — ASK ONCE (2026-09-03). Its own slot beside the customer
         file rather than inside it: the file's painter is keyed on the
         /customer-context payload, and a customer with no file at all
         still has a size sitting in their chat. Same class family so it
         reads as one card; separate painter so neither can blank the
         other. */''}
    <div class="crd-file csw-remem" id="csw-remem" hidden></div>
    <div class="csw-msearch" id="csw-msearch" hidden>
      <div class="csw-msearch__row">
        <input class="csw-search csw-msearch__q" id="csw-msearch-q" type="search" placeholder="Search this chat…" autocomplete="off" aria-label="Search messages in this conversation"/>
        <button class="csw-msearch__x" id="csw-msearch-close" aria-label="Close search">✕</button>
      </div>
      <div class="csw-msearch__res" id="csw-msearch-res" hidden></div>
    </div>
    <div class="csw-also" id="csw-also" hidden></div>
    <div class="csw-360" id="csw-360" inert aria-hidden="true" tabindex="-1" role="region" aria-label="Customer details"></div>
    <div id="csw-pins" class="csw-pins" hidden></div>
    <div class="csw-script" id="csw-script"><div class="csw-empty"><div class="csw-empty__m">○</div>Loading…</div></div>
    <button class="csw-scrollbtm" id="csw-scrollbtm" aria-label="Jump to latest" hidden>↓</button>
    ${/* THE STAGE (2026-08-18, artifact ebede26e). Things that already
         HAPPENED live in the transcript at the moment they happened —
         the FAQ auto-answer note is a system line under EON's bubble
         (the tail of the script), never a banner over the top. Things
         AWAITING THE REP live in ONE in-flow slot above the composer:
         #csw-lookoffer is that slot (the Stage) — the staged look, a
         held reply's clock, a stylist offer — one card at rest, the
         next one peeking, each opening IN PLACE to a full preview. The
         ball (B1) is a MODE, so it moved into the composer's own frame:
         #csw-ballbar sits inside .csw-composer and typing takes over. */''}
    ${/* THE BAND BUDGET (L1, 2026-08-30). Every band between the
         transcript and the composer lives inside ONE bounded wrapper:
         .csw-bands is flex:0 1 auto with a max-height on the SHELL's
         own scale and its own scroll, so a fourth lit band scrolls
         inside the stack instead of eating the conversation — and the
         composer can never be pushed past the shell edge, where
         .csw-body{overflow:hidden} clips it unreachable (measured:
         a lit close thread left the transcript 28-80px and past that
         the send key left the screen entirely). Painters and ids are
         untouched; only the parent changed. */''}
    <div class="csw-bands" id="csw-bands">
    <div class="csw-faqbanner" id="csw-faqbanner" hidden></div>
    <div class="csw-lookoffer csw-stage" id="csw-lookoffer" hidden></div>
    ${/* THE RE-DRESSED SLOTS (2026-08-29). Four narrow bands between the
         Stage and the composer, in the order a rep needs them: what we
         are still owed (03), what just landed (05), who we are waiting
         on (07), and what is on the shelf (06). Each ships [hidden] and
         only its own painter reveals it — a desk with none of these
         facts is byte-identical to the one before this pass. */''}
    <div class="crd-quote" id="csw-quoteclock" hidden></div>
    ${/* B7 (2026-09-03): the Account key's own word. Sits with the
         receipt card because they are the two halves of one rail — this
         one says the details went and what the row says has come back;
         the receipt card lays the amount beside the screenshot. Painted
         only from the server's answer to the transfer-intent call. */''}
    <div class="csw-acct" id="csw-acct" hidden></div>
    <div class="crd-receipt" id="csw-receipt" hidden></div>
    <div class="crd-askstate" id="csw-askstate" hidden></div>
    <div class="crd-stock" id="csw-stockchips" hidden></div>
    </div>
    <div class="csw-composer">
      <div class="csw-compose-context">
      <div class="csw-ballbar" id="csw-ballbar" hidden></div>
      ${/* The DEFAULT copy. Both closed-window branches overwrite it (WA gets
           the Pick up button, IG its own "may not deliver" line) and the
           reveal is gated on dataset.armed, which only they set — so this
           string is unreachable today. It still said "send an approved
           template (Quick)" after the door was fixed (vet 2026-08-09):
           ⚡ Quick is a free-text phrase book WhatsApp REFUSES on a shut
           window, so the default named the one action guaranteed to fail. A
           dead default that contradicts the shipped instruction is a trap for
           the next reader and for every grep — it fooled the post-merge
           verification of the fix itself. */''}
      <div class="csw-winclosed" id="csw-winclosed" hidden>24h window closed — use an approved template; the customer’s reply reopens free-form messaging.</div>
      <div class="csw-reply-ctx" id="csw-reply-ctx" hidden></div>
      <div class="csw-macros" id="csw-macros" hidden></div>
      ${/* 02 — THE CLOSE DOCK (2026-08-29). Same tray, same fourteen
           tools, NOTHING removed: the keys that actually end a sale now
           LEAD it, and the one that ends it at a counter — where ~90% of
           this brand's money is taken — is the only one wearing the
           accent. Two keys are new (Hold at store, Account) and both
           call lanes that already shipped: cswHoldSheet (cs_holds) and
           the canned-reply pack. The order itself is one classic-cascade
           CSS rule (.csw-tray .csw-tool[data-tool="…"]{order}); the
           house floor keeps its own five-key order untouched. */''}
      <div class="csw-tray collapsed" id="csw-tray">
        <button class="csw-tool crd-dockkey crd-dockkey--go" data-tool="hold">Hold at store</button>
        <button class="csw-tool crd-dockkey" data-tool="order">🧾 Send total</button>
        <button class="csw-tool crd-dockkey" data-tool="account">Account</button>
        <button class="csw-tool csw-tool--ai" data-tool="suggest">✦ Suggest</button>
        <button class="csw-tool" data-tool="label">🏷 Label</button>
        <button class="csw-tool" data-tool="photo">📷 Photo</button>
        <button class="csw-tool" data-tool="product">◫ Product</button>
        <button class="csw-tool" data-tool="stock">📦 Stock</button>
        <button class="csw-tool" data-tool="paylink">💳 Pay link</button>
        <button class="csw-tool" data-tool="bought">✓ Bought</button>
        <button class="csw-tool" data-tool="editlink">✂ Their edit</button>
        <button class="csw-tool" data-tool="delivery">🚚 Delivery</button>
        <button class="csw-tool" data-tool="studio">👗 Try-on</button>
        <button class="csw-tool" data-tool="note">✎ Note</button>
        <button class="csw-tool" data-tool="template">⚡ Quick</button>
        <button class="csw-tool" data-tool="route-at">@ Route</button>
        <button class="csw-tool" data-tool="slash">/ Replies</button>
        <button class="csw-tool csw-tool--more" id="csw-house-more" type="button" hidden aria-expanded="false">⋯ More</button>
      </div>
      ${/* B5 + B6 (2026-09-03). Two quiet lines directly over the box,
           in the order they matter: the warning that this question is
           already answered, then the rep's own line offered back. Both
           ship [hidden] and both are pure functions of what has been
           typed — neither ever writes into the box on its own. */''}
      <div class="csw-reask" id="csw-reask" role="status" hidden></div>
      <div class="csw-ghost" id="csw-ghost" aria-hidden="true" hidden></div>
      ${/* Commerce C1: the catalog check of EON's draft (cswTruthPaint). */''}
      <div class="csw-truthslot"><p class="csw-reask" id="csw-truth" role="status" style="font-size:14px;color:var(--warn)" hidden></p></div>
      </div>
      <div class="csw-inputbox" id="csw-inputbox">
        <button class="csw-plus" id="csw-plus" aria-label="Tools">+</button>
        <div class="csw-mention" id="csw-cust-mention" hidden></div>
        <textarea class="csw-input" id="csw-input" aria-label="Reply message" placeholder="Message" rows="1" enterkeyhint="send" autocomplete="off" autocorrect="on" autocapitalize="sentences" data-1p-ignore data-lpignore="true" data-form-type="other"></textarea>
        <button class="csw-emoji" id="csw-emoji" type="button" aria-label="Emoji" title="Emoji">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/><circle cx="9" cy="10" r="1.2" fill="currentColor"/><circle cx="15" cy="10" r="1.2" fill="currentColor"/><path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
        <button class="csw-mic" id="csw-mic" aria-label="Hold to record a voice note" title="Hold to record">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" stroke-width="2"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
        <button class="csw-send" id="csw-send" disabled aria-label="Send">
          <svg viewBox="0 0 24 24" fill="none"><path d="M4 12l16-8-6 16-2.5-6.5L4 12z" fill="currentColor"/></svg>
        </button>
      </div>
      <div class="csw-hint">${CSW_COMPOSER_HINT}</div>
    </div>`;
  cswPaintWorkContext(s.active); // Shell rebuilds also replace the guidance nodes.
  cswLoadMacros();
  const conversationMenu = document.getElementById('csw-conv-menu');
  document.getElementById('csw-menu-priority')?.addEventListener('click', () => cswServiceSheet('why'));
  document.getElementById('csw-menu-progress')?.addEventListener('click', () => cswServiceSheet('progress'));
  document.getElementById('csw-menu-desk-search')?.addEventListener('click', cswSearchDesk);
  document.getElementById('csw-menu-evidence')?.addEventListener('click', cswOpenDecisionEvidence);
  document.getElementById('csw-menu-receipts')?.addEventListener('click', () => cswServiceSheet('receipts'));
  document.getElementById('csw-menu-payment')?.addEventListener('click', () => {
    conversationMenu.open = false;
    conversationMenu.querySelector('summary').focus();
    cswServiceSheet('payment');
  });
  document.getElementById('csw-menu-emoji')?.addEventListener('click', () => document.getElementById('csw-emoji')?.click());
  // Ask EON about this customer (two-hands round 1, A1-3): the explicit way
  // into Ask while a customer is open. The resting Ask button stays hidden
  // here (it sat on Send); this opens the panel itself, growing from this
  // menu, with the open customer shared. Shown only where Ask can answer.
  const askItem = document.getElementById('csw-menu-ask');
  const askReady = () => { if (askItem) askItem.hidden = !window.EON_ASK?.deskReady?.(); };
  askReady();
  conversationMenu?.addEventListener('toggle', askReady);
  // A menu left open follows Ask too: Ask says "eon-ask-desk-ready" each time
  // it can or cannot open, and the line goes or comes back at once. If the
  // line held the keyboard as it went, the menu's own button takes it. One
  // listener per desk: a new draw takes the last one off, and one whose menu
  // has left the page takes itself off.
  const askHeard = () => {
    if (!conversationMenu?.isConnected) { document.removeEventListener('eon-ask-desk-ready', askHeard); return; }
    const held = document.activeElement === askItem;
    askReady();
    if (held && askItem.hidden) conversationMenu.querySelector('summary').focus();
  };
  document.removeEventListener('eon-ask-desk-ready', window._cswAskHeard);
  window._cswAskHeard = askHeard;
  document.addEventListener('eon-ask-desk-ready', askHeard);
  askItem?.addEventListener('click', () => {
    conversationMenu.open = false;
    window.EON_ASK?.openFrom?.(conversationMenu.querySelector('summary'));
  });
  conversationMenu?.addEventListener('click', ev => {
    if (ev.target.closest('button')) conversationMenu.open = false;
  });
  conversationMenu?.addEventListener('keydown', ev => {
    if (ev.key === 'Escape') { conversationMenu.open = false; conversationMenu.querySelector('summary').focus(); ev.stopPropagation(); }
  });
  conv.onpointerdown = ev => {
    if (conversationMenu?.open && !conversationMenu.contains(ev.target)) conversationMenu.open = false;
  };
  cswPaintServiceRail();
  s._serviceCases?.dispose();
  s._serviceCases=mountServiceCaseSummary({cid:s.active,api,host:document.getElementById('csw-service-cases'),isCurrent:()=>cswState().active===t.zoko_customer_id,onOpen:()=>cswServiceSheet('cases'),onEvidenceChanged:cswEvidenceChanged});
  document.getElementById('csw-menu-cases')?.addEventListener('click',()=>cswServiceSheet('cases'));
  document.querySelectorAll('#csw-service-rail [data-service]').forEach(b => b.addEventListener('click', () => cswServiceSheet(b.dataset.service)));
  // One shared refresh for the clock, queue words and composer permission.
  // Resuming a suspended phone refreshes immediately, before the next tick.
  const dialTick = () => {
    const slot = document.getElementById('csw-dialslot');
    if (!slot) { clearInterval(s._dialTick); s._dialTick = null; return; }
    if (document.hidden) return;
    cswRefreshReplyWindows();
  };
  if (s._dialTick) clearInterval(s._dialTick);
  s._dialTick = setInterval(dialTick, 1000);
  cswBindWindowResume();
  dialTick();
  // "+" toggles the tools tray (WhatsApp/Telegram pattern) — keeps the
  // default composer to a single clean row. Operator 2026-08-09 ("the
  // suggest, payment link is only suppose to show when click the plus
  // button"): the canned-reply strip AND the tray now both live behind
  // the one key, on BOTH themes — the house tray no longer stands open.
  const plusBtn = document.getElementById('csw-plus');
  plusBtn?.addEventListener('click', () => {
    const tray = document.getElementById('csw-tray');
    const closed = tray.classList.toggle('collapsed');
    plusBtn.classList.toggle('on', !closed);
    const mac = document.getElementById('csw-macros');
    if (mac) mac.hidden = closed || !mac.innerHTML;
    // Opening the strips SHRINKS the transcript; if the rep was pinned to
    // the bottom, re-pin so the last message never slides under them.
    if (!closed) {
      const sc = document.getElementById('csw-script');
      if (sc && sc.scrollHeight - sc.scrollTop - sc.clientHeight < 80) sc.scrollTop = sc.scrollHeight;
    }
  });
  // HOUSE: the tray opens as a labelled panel (2026-08-30). The close
  // bank leads under its strip — Hold at store, Send total, Account,
  // Pay link, ✦ Suggest, ⋯ More — everything else one press deeper
  // behind the "More tools" strip (thumbs, not menus). Until this pass
  // the house law still led with the 2026-08-06 five and hid the Close
  // Dock's keys entirely — the desk's best new keys were invisible on
  // the default skin. The More button ships [hidden]; only the house
  // layer displays it, so classic renders the full tray exactly as
  // before. Leading keys drop their emoji under house (drawn-glyph
  // law); ✦ is a glyph and stays.
  const houseMore = document.getElementById('csw-house-more');
  if (houseMore && cswHouse()) {
    // The button ships with the hidden ATTRIBUTE so classic never paints
    // it, and house used to un-hide it from CSS — the only rule in the
    // repo that made a [hidden] element visible. Vet 2026-08-09: the new
    // `.csw-conv [hidden]{display:none !important}` beat it, taking the
    // More key to 0x0 and stranding TEN tools, ⚡ Quick among them — the
    // one door out of a closed 24h WhatsApp window, which the banner
    // right above it tells the rep to use. The attribute now means what
    // it says everywhere: house REMOVES it instead of overriding it.
    houseMore.hidden = false;
    const houseTray = document.getElementById('csw-tray');
    const RELABEL = { order: 'Send total', paylink: 'Pay link' };
    Object.keys(RELABEL).forEach((k) => {
      const b = houseTray.querySelector(`[data-tool="${k}"]`);
      if (b) b.textContent = RELABEL[k];
    });
    // Every glyph is drawn (plate law): the second bank's chips shed
    // their emoji under house — the words already match the floor
    // mockup, only the leading glyph goes. The glyphs the mockup DRAWS
    // stay: ✦ Suggest, ⋯ More, ⚡ Quick, @ Route, / Replies.
    ['label', 'photo', 'product', 'stock', 'editlink', 'delivery',
     'studio', 'note'].forEach((k) => {
      const b = houseTray.querySelector(`[data-tool="${k}"]`);
      if (b) b.textContent = b.textContent.replace(/^[^A-Za-z]+/, '');
    });
    // STICKY (operator 2026-08-09, decided on the numbers): reps send
    // ~2,142 free-text messages a month against 32 photos and ONE
    // template — the tray is not a high-traffic surface, so opening all
    // fourteen by default would spend a permanent second chip row over
    // the transcript for about one human tool-use a day. Instead the
    // choice belongs to the rep and it PERSISTS: one tap and the full
    // tray is theirs on every thread, on this device, until they fold it
    // back. Everyone else keeps the calm four.
    const _applyAll = (all) => {
      houseTray.classList.toggle('house-all', all);
      houseMore.setAttribute('aria-expanded', String(all));
      houseMore.textContent = all ? '· Less' : '⋯ More';
    };
    let _wantAll = false;
    try { _wantAll = localStorage.getItem('csw-tray-all') === '1'; } catch (_) {}
    if (_wantAll) _applyAll(true);
    houseMore.addEventListener('click', () => {
      const all = !houseTray.classList.contains('house-all');
      _applyAll(all);
      try { localStorage.setItem('csw-tray-all', all ? '1' : '0'); } catch (_) {}
    });
  }
  // Reply windows: Zoko ig_primary exposes acceptance without delivery
  // receipts, so IG retains its warning. The native route REFUSES with
  // 409 if that flag flips. WA keeps drafts editable and refuses customer
  // sending at the exact deadline; the same reconciler runs on the clock.
  const _wcReveal = () => {
    const wc = document.getElementById('csw-winclosed');
    if (wc && wc.dataset.armed) wc.hidden = false;
  };
  const _ib = document.getElementById('csw-inputbox');
  _ib?.addEventListener('pointerdown', _wcReveal, { once: false });
  // Pointer and keyboard focus both reveal the channel explanation.
  _ib?.addEventListener('focusin', _wcReveal, { once: false });
  cswSyncWindowComposer();
  // B5 + B6 (2026-09-03). Two listeners on the box the composer already
  // owns, wired AFTER the window branches above have settled whether the
  // box is even usable — and deliberately not between them, because the
  // closed-window branch and its Pick up key are measured by a
  // fixed-width pin that prose in the middle silently pushes its subject
  // out of (the fixed-width window trap, named in
  // test_one_customer_curated_first's own comment as a 7th recurrence).
  //
  // Both painters are pure functions of what is typed, and both hide
  // their own slot when they have nothing: nothing here puts a character
  // in the box the rep did not ask for.
  //
  // TAB IS THE ONLY KEY THIS TAKES, and only while a ghost is showing —
  // with no ghost it does what it always did (leaves the box), so a rep
  // tabbing to Send is never trapped. Enter is untouched: the send path
  // is not a place to be clever.
  const _gi = document.getElementById('csw-input');
  if (_gi && !_gi._cswGhostB) {
    _gi._cswGhostB = 1;
    _gi.addEventListener('input', () => { cswPaintGhost(); cswPaintReask(); });
    _gi.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || e.shiftKey || e.altKey || e.metaKey || e.ctrlKey) return;
      if (!cswState()._ghost) return;
      e.preventDefault();
      cswGhostTake();
      cswPaintGhost();
    });
  }
  cswPendingPaint();
  cswPaintPhotoKey();   // WS-B: Photo / window shut Nh / this phone can't attach
  // Scroll-to-latest pill — appears when scrolled up from the bottom.
  const scr = document.getElementById('csw-script');
  const sb = document.getElementById('csw-scrollbtm');
  if (scr) scr.scrollTop = 0;   // start at top after paint; no inherited offset that would push the sticky header out of view before the transcript lands
  if (scr && sb) {
    // L4 (2026-08-30): one shared sync, one threshold (120 — the auto-scroll
    // boundary). The old 300px show-threshold left a 120-300px dead band.
    scr.addEventListener('scroll', () => cswSyncScrollPill());
    sb.addEventListener('click', () => {
      cswState()._newBelow = 0;
      scr.scrollTo({ top: scr.scrollHeight, behavior: 'smooth' });
      sb.hidden = true;
    });
  }
  // the chip's state is already known — don't wait for the next poll
  document.querySelectorAll('.csw-rehearse').forEach(el => { el.hidden = !_cswHolds.dryRun; });
  document.getElementById('csw-conv-back')?.addEventListener('click', () => {
    if (s.active) cswMarkLeft(s.active);   // the back key is leaving too (verifier, 2026-09-04)
    s.active = null; s.activeThread = null;
    // Tell the team you left (vet 2026-09-24, fault 27): switching chats
    // already re-announces, but the back arrow — the phone's only way out
    // of a chat — left teammates seeing you "in" a chat nobody was working.
    cswUpdatePresence(false);
    document.getElementById('csw-inbox')?.classList.remove('has-active');
    conv.classList.remove('show');
    cswRenderThreadList();
  });
  const toggle360 = ev => {
    const p = document.getElementById('csw-360');
    if (!p) return;
    if (p.classList.contains('open')) cswSetCustomerDetails(false, ev.currentTarget);
    else cswOpenCustomerDetails(ev.currentTarget);
  };
  document.getElementById('csw-360btn')?.addEventListener('click', toggle360);
  document.getElementById('csw-360btn')?.addEventListener('keydown', ev => {
    if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); ev.stopPropagation(); toggle360(ev); }
  });
  document.getElementById('csw-360btn2')?.addEventListener('click', toggle360);
  document.getElementById('csw-routebtn')?.addEventListener('click', cswRouteSheet);
  cswBindMsgSearch();
  cswBindComposer();
  // THE RELAY (R5): the "Ask a department" key joins the tray only when
  // the lane is armed. Not awaited and not reserved — a dark desk keeps
  // exactly the keys it had.
  cswRelayMountAsk();
  // THE DESK, RE-DRESSED — repaint every face that can render from cache
  // the moment the shell exists. Each is a no-op without its field.
  cswPaintCustomerFile();
  cswPaintQuoteClock();
  cswPaintAskState();
}

/* ══════════════════════════════════════════════════════════════════
   THE DESK, RE-DRESSED (2026-08-29) — the ten faces
   ------------------------------------------------------------------
   Operator: "proceed with all" on the approved artifact. Every face
   here renders facts the desk ALREADY HAD and no surface printed.

   THE ONE LAW THAT GOVERNS ALL TEN: graceful absence. Several of the
   payload fields below land in a later wave. A face whose field is
   missing renders NOTHING — no empty chrome, no dash, no 'undefined'.
   That is why every painter starts by hiding its own slot and returns
   early: the desk with none of these facts must be byte-identical to
   the desk before this pass.
   ══════════════════════════════════════════════════════════════════ */

// The desk's dialog, in the ONE house grammar. coreConfirm is the repo's
// single promise-based confirm (inventory_core.js, vet build #9) — the
// dialog that retired window.confirm/prompt — and it is imported here
// LAZILY so the desk pays nothing for a module it may never open.
//
// The class tag is load-bearing: coreConfirm paints with .mk-modal, which
// index.html does not ship a stylesheet for, so an untagged dialog would
// render as unstyled prose — the exact "bare system alert" failure the
// one-grammar rule exists to kill. cs.css paints .mk-modal.crd-confirm
// only, so no other page that uses coreConfirm is touched. The executor
// runs synchronously, so the node exists the instant the promise does.
//
// A module that will not load returns {ok:false}: a confirm that cannot
// be shown must never read as a yes.
async function cswConfirm(opts) {
  let mod;
  try { mod = await import('./inventory_core.js'); }
  catch (_) { cswToast('Could not open the confirmation — try again', true); return { ok: false }; }
  const p = mod.coreConfirm(opts || {});
  const nodes = document.querySelectorAll('.mk-modal.mk-modal-open');
  const node = nodes[nodes.length - 1];
  if (node) node.classList.add('crd-confirm');
  return await p;
}

// "Still not sure the first one went" on a repeat of the SAME send (the
// server's 409 send_unconfirmed with repeat: true, cs_send_once). That id
// never sends again; only this answer opens the rep's question below.
function cswIsUnsureRepeat(status, body) {
  const d = body && body.detail;
  return status === 409 && !!d && typeof d === 'object'
    && d.error === 'send_unconfirmed' && d.repeat === true;
}

// The rep's explicit yes to send again words EON cannot confirm went (Sol
// 6.1 on #3548): never a timer, never a reflex tap. A yes is a NEW action
// with its own id; a dialog that cannot open is a no.
async function cswResendAnyway(why) {
  const res = await cswConfirm({
    title: 'Send it again?',
    body: `<p>${esc(why || 'EON is still not sure the first one went.')}</p>`
      + '<p>Check the chat first. If it is already there, the customer gets it twice.</p>',
    confirmLabel: 'Send it again',
  });
  return !!(res && res.ok);
}

/* ══════════════════════════════════════════════════════════════════
   THE DESK FACES, part B (2026-09-03) — the working set and the close
   ------------------------------------------------------------------
   Part A gave the desk a face for what BREAKS. This is the face for
   what a rep is HOLDING: the shelf of live threads, the line the desk
   draws where they left off, the six-word handover at ring-off, their
   own words offered back inside the box, and the two things the chat
   has already answered.

   THE SAME LAW AS THE TEN: graceful absence. Every painter here hides
   its own slot first and returns early on a missing field, so a desk
   with none of these facts is byte-identical to the desk before this
   pass. Nothing here sends.
   ══════════════════════════════════════════════════════════════════ */

// ── B1 · THE NOW SHELF ──────────────────────────────────────────────
// Above the lens row, never inside it: the lens row is a saved view and
// this is not a view, it is what the rep's hands are on right now. Eight
// tiles at most, because past eight it stops being a shelf and becomes
// the list that already exists two inches below it.
//
// "Touched" is BOTH the desk's own memory and the server's. A device
// that has just been opened remembers nothing, so the set is seeded from
// two facts the server already sends on every thread: the ball this rep
// holds (cs_ball, which expires by itself in ten minutes) and a thread
// whose last word is this rep's own. Local touches carry the rest.
const CSW_NOW_MINUTES = 60;
const CSW_NOW_CAP = 8;

function cswNowTouches() {
  const s = cswState();
  if (!s._nowTouch) {
    s._nowTouch = {};
    try {
      const raw = JSON.parse(localStorage.getItem('csw_now_touch') || '{}');
      if (raw && typeof raw === 'object') s._nowTouch = raw;
    } catch (_) { s._nowTouch = {}; }
  }
  return s._nowTouch;
}

function cswNowTouch(cid) {
  if (!cid) return;
  const t = cswNowTouches();
  t[String(cid)] = Date.now();
  // Keep the store small: anything past the window is not the working
  // set and never becomes it again.
  const floor = Date.now() - CSW_NOW_MINUTES * 60000;
  Object.keys(t).forEach(k => { if (!(t[k] > floor)) delete t[k]; });
  try { localStorage.setItem('csw_now_touch', JSON.stringify(t)); } catch (_) {}
}

// Who is holding a thread, in one word a rep reads without thinking.
// 'you' is the only one that means "do not walk away from this".
function cswNowBall(t, me) {
  const b = (t && t.ball) || null;
  if (!b || !b.kind) return { word: '—', cls: 'none' };
  if (b.kind === 'eon') return { word: 'EON', cls: 'eon' };
  const holder = String(b.holder || '').toLowerCase();
  if (me && holder && holder === String(me).toLowerCase())
    return { word: 'you', cls: 'you' };
  return { word: 'a teammate', cls: 'them' };
}

// PURE. The whole shelf is decided here so it can be replayed in a test
// against fixtures rather than against a browser.
function cswNowSet(threads, me, touches, now) {
  const at = now || Date.now();
  const floor = at - CSW_NOW_MINUTES * 60000;
  const touch = touches || {};
  const mine = String(me || '').toLowerCase();
  const out = [];
  (threads || []).forEach(t => {
    if (!t || !t.zoko_customer_id) return;
    const lastMs = Date.parse(t.last_message_at || '') || 0;
    const touchedAt = Number(touch[String(t.zoko_customer_id)] || 0);
    const ballMine = !!(t.ball && t.ball.kind === 'rep'
      && String(t.ball.holder || '').toLowerCase() === mine && mine);
    // A thread ASSIGNED to this rep that has moved inside the window —
    // the rep is on it even if this device has never opened it.
    //
    // NOT `last_agent_email`. That column does not exist on a thread-list
    // row (cs_inbox.thread_list sets last_direction, last_message_at,
    // last_message_text / _type / _status and open_quote_ngn, and nothing
    // else), so a clause reading it seeds nothing at all on a fresh phone
    // — and no fixture would ever have said so, because a fixture carries
    // whatever field you name it. assigned_rep_email is the field the
    // Mine lens itself filters on: the same truth, and a real one.
    const isMine = !!(mine && String(t.assigned_rep_email || '').toLowerCase() === mine
      && t.cs_status !== 'resolved' && lastMs > floor);
    if (!(touchedAt > floor || ballMine || isMine)) return;
    out.push({
      id: t.zoko_customer_id,
      tail: cswNowTail(t),
      plat: cswPlatform(t),
      mins: lastMs ? Math.max(0, Math.floor((at - lastMs) / 60000)) : null,
      ball: cswNowBall(t, mine),
      // The most recent hand wins the front of the shelf — the rep's own
      // touch beats a message that arrived while they were elsewhere.
      rank: Math.max(touchedAt, lastMs),
    });
  });
  out.sort((a, b) => b.rank - a.rank);
  return out.slice(0, CSW_NOW_CAP);
}

// The last four, and nothing before them. An Instagram scoped id is not
// a phone, so it shows its own last four under the IG dot rather than
// dressing as a number this desk could dial.
function cswNowTail(t) {
  const digits = String((t && t.platform_sender_id) || '').replace(/\D/g, '');
  if (digits.length >= 4) return '••••' + digits.slice(-4);
  const raw = String((t && t.zoko_customer_id) || '');
  return raw ? '••••' + raw.slice(-4) : '••••';
}

function cswNowAge(mins) {
  if (mins == null) return '';
  if (mins < 1) return 'now';
  if (mins < 60) return mins + 'm';
  return Math.floor(mins / 60) + 'h';
}

function cswNowTileHtml(tile) {
  return `<button type="button" class="csw-now__t" data-now="${esc(String(tile.id))}"`
    + ` title="Open this conversation">`
    + `<i class="csw-now__d csw-now__d--${esc(tile.plat)}"></i>`
    + `<b class="num">${esc(tile.tail)}</b>`
    + `<span class="csw-now__a num">${esc(cswNowAge(tile.mins))}</span>`
    + `<span class="csw-now__b csw-now__b--${esc(tile.ball.cls)}">${esc(tile.ball.word)}</span>`
    + `</button>`;
}

function cswPaintNowShelf() {
  const host = document.getElementById('csw-now');
  if (!host) return;
  host.hidden = true; host.innerHTML = '';
  const s = cswState();
  const tiles = cswNowSet(s.threads || [], cswMyEmail(), cswNowTouches(), Date.now());
  if (!tiles.length) return;
  // ONE ROW, ALWAYS. Wrapped, eight tiles cost four rows of a phone above
  // the list — a strip that eats the room it sits in is the exact shape
  // #3045 cost the operator on this same desk. The tiles live in their own
  // scroller so the shelf is 44px whether it holds three or eight, and the
  // eighth is a swipe away rather than three rows down.
  host.innerHTML = `<span class="csw-now__l">Recent conversations</span>`
    + `<div class="csw-now__r">${tiles.map(cswNowTileHtml).join('')}</div>`;
  host.hidden = false;
  host.querySelectorAll('[data-now]').forEach(b => {
    if (b._cswB) return; b._cswB = 1;
    b.addEventListener('click', () => cswOpenThread(b.dataset.now));
  });
}

// ── B2 · THE SEAM ───────────────────────────────────────────────────
// A rep switches threads twenty times an hour. Every switch used to land
// them at the bottom of a chat with no mark anywhere saying which part
// of it is new since they were last here — so they re-read, or they miss
// it. The seam is that mark: a line at the exact message they had not
// seen, and the scroll parked on it rather than on the end.
//
// "Since YOU left" is this device's own memory of when this rep last had
// this thread open, which is the only clock that can answer it. The
// server's seen_at is the fallback and says nearly the same thing.
function cswLeftAt(cid) {
  if (!cid) return 0;
  try { return Number(JSON.parse(localStorage.getItem('csw_left') || '{}')[String(cid)] || 0); }
  catch (_) { return 0; }
}

function cswMarkLeft(cid) {
  if (!cid) return;
  try {
    const all = JSON.parse(localStorage.getItem('csw_left') || '{}');
    all[String(cid)] = Date.now();
    // 200 threads of timestamps is a few kilobytes; past that the oldest
    // go, because a thread nobody has opened in weeks has no seam.
    const keys = Object.keys(all).sort((a, b) => all[b] - all[a]).slice(0, 200);
    const trimmed = {};
    keys.forEach(k => { trimmed[k] = all[k]; });
    localStorage.setItem('csw_left', JSON.stringify(trimmed));
  } catch (_) {}
}

// Leaving the desk is leaving the conversation. cswRenderBody re-opens
// cswState().active after a body re-render — right for an in-desk repaint, wrong
// once the rep has walked out, because every way back in re-runs bindCsInbox and
// drops them straight into the chat they just left. The inbox then has no door.
//
// Reported by Thelma (customer care) on 2026-09-17: "How do I leave this page to
// the page where all the chats are? When i click on return to work room..it takes
// me back to this chat." Nothing on her screen was broken — the desk simply kept
// holding the thread open for her.
//
// Marks the thread left first, exactly like the two clears that already exist
// (cswTabGo and the conversation's own back chevron), so the unread seam still
// counts from when she actually stopped reading.
function cswLeaveDesk() {
  try {
    const s = cswState();
    if (!s.active) return;
    cswMarkLeft(s.active);
    s.active = null;
    s.activeThread = null;
    // The desk closing is leaving too (vet 2026-09-24, fault 27): while the
    // presence channel still lives, say so, or teammates keep seeing you in.
    cswUpdatePresence(false);
  } catch (_) { /* the desk was never mounted on this screen */ }
}

// PURE. {index, n, mins} or null. The index is the first row the rep has
// not seen; n counts every row from there on, whoever sent it — a rep
// coming back wants to know how much happened, not how much of it was
// inbound.
function cswSeamFacts(rows, leftAt, now) {
  if (!rows || !rows.length || !leftAt) return null;
  const at = now || Date.now();
  const idx = rows.findIndex(r => {
    const ts = Date.parse(r.platform_timestamp || r.created_at || '');
    return isFinite(ts) && ts > leftAt;
  });
  if (idx < 0) return null;
  const firstTs = Date.parse(rows[idx].platform_timestamp || rows[idx].created_at || '');
  return {
    index: idx,
    n: rows.length - idx,
    mins: isFinite(firstTs) ? Math.max(0, Math.floor((at - firstTs) / 60000)) : 0,
  };
}

function cswSeamHtml(n, mins) {
  const msg = n === 1 ? '1 message' : `${n} messages`;
  const ago = mins >= 60 ? `${Math.floor(mins / 60)} hr` : `${mins} min`;
  return `<div class="csw-seam"><span>— since you left · ${esc(msg)} · ${esc(ago)} —</span></div>`;
}

// ── B3 · THE PARKED STRIP AND THE SHIFT BAR ─────────────────────────
// The end of a shift, on the rep's own room. Each thread they are
// leaving open gets one line of at most six words, written by the server
// off the thread's own last rung. No customer names: this is a thing
// left on a screen at the end of a shift, and it says what is owed,
// never who owes it.
async function cswLoadParked() {
  const host = document.getElementById('csw-parked');
  if (!host) return;
  let d = null;
  try { d = await api('/api/cs/desk/ringoff'); } catch (_) { d = null; }
  if (!document.getElementById('csw-parked')) return;
  if (!d || d.ok === false) {
    host.innerHTML = `<div class="csw-desklaw">Could not read what is still open. Try again in a moment.</div>`;
    return;
  }
  host.innerHTML = cswParkedHtml(d);
  host.querySelectorAll('[data-park]').forEach(b => {
    if (b._cswB) return; b._cswB = 1;
    b.addEventListener('click', () => { cswTabGo('chats'); cswOpenThread(b.dataset.park); });
  });
}

function cswParkedHtml(d) {
  const rows = (d && d.parked) || [];
  const bar = (d && d.shift) || {};
  const shift = bar.words
    ? `<div class="csw-shiftbar ${bar.closed ? 'is-shut' : ''}">${esc(bar.words)}</div>` : '';
  if (!rows.length) {
    return `<div class="csw-desklaw">No chat handover notes are recorded. Check Priority for work you have accepted.</div>` + shift;
  }
  const more = (d.total || rows.length) > rows.length
    ? `<div class="csw-desklaw">${esc(String(d.total - rows.length))} more are open past the eight shown.</div>` : '';
  return rows.map(p => `<button type="button" class="csw-park" data-park="${esc(String(p.id || ''))}">`
    + `<i class="csw-now__d csw-now__d--${esc(p.channel === 'instagram' ? 'ig' : 'wa')}"></i>`
    + `<b class="num">••••${esc(p.tail || '')}</b>`
    + `<span class="csw-park__n">${esc(p.note || '')}</span></button>`).join('')
    + more + shift;
}

// ── B4 · THE RUNG ON THE HEADER ─────────────────────────────────────
// Which rung of the close this thread is on, so the composer's one ask
// is the NEXT one. The close ladder merges on its own branch; this door
// feature-detects it server-side and answers armed:false until it lands,
// and an unarmed answer paints nothing at all.
// One request per thread open is the right price for a face that lights.
// It is the wrong price for one that cannot: while the ladder is absent
// the door answers armed:false every time, and a rep opening fifty
// threads a shift would pay fifty requests for a chip that draws nothing.
// The explicit absent-capability answer is remembered for the session. A
// working ladder with no saved row stays available for the next thread. The
// same unchanged thread reuses its read; a new message version reads afresh.
let _cswRungOff = false;
const _cswRungReads = new Map();

// The loaded Priority book already says whether this thread is customer
// work or a business enquiry. Use that local fact for reply help; the close
// ladder is optional and may be absent or briefly unreadable. A mixed thread
// keeps customer help because an open customer request outranks the enquiry.
function cswDeskWorkContext(cid) {
  const cards = (_cswPrio.items || []).filter(p =>
    String(p.zoko_customer_id || '') === String(cid) && p.status !== 'cleared');
  const customer = cards.some(p => p.reason_key !== 'business_followup');
  const enquiry = cards.some(p => p.reason_key === 'business_followup');
  const businessOnly = enquiry && !customer;
  return { cid, kind: businessOnly ? 'business_followup' : 'customer',
    label: businessOnly ? 'Marketing or business enquiry' : 'Customer conversation',
    sales_guidance: !businessOnly, delivery_guidance: !businessOnly };
}

function cswPaintWorkContext(cid) {
  const s = cswState();
  if (String(s.active || '') !== String(cid)) return null;
  const context = cswDeskWorkContext(cid);
  s._workContext = context;
  const tray = document.getElementById('csw-tray');
  if (tray) tray.dataset.guidance = context.sales_guidance ? 'customer' : 'review';
  const el = document.getElementById('csw-rung');
  if (el && context.kind === 'business_followup') {
    el.textContent = context.label;
    el.title = 'Review the enquiry and route it to the right team';
    el.hidden = false;
  }
  return context;
}

async function cswLoadRung(cid) {
  if (!cid || cswState().active !== cid) return;
  const host = document.getElementById('csw-rung');
  if (!host) return;
  host.hidden = true; host.textContent = '';
  const context = cswPaintWorkContext(cid);
  if (_cswRungOff) return;
  const thread = (cswState().threads || []).find(t => String(t.zoko_customer_id) === String(cid)) || cswState().activeThread;
  const version = String(thread?.last_message_at || thread?.last_inbound_at || '');
  let cached = _cswRungReads.get(String(cid));
  if (!cached || cached.version !== version) {
    cached = { version, promise: api(`/api/cs/desk/rung/${encodeURIComponent(cid)}`) };
    _cswRungReads.set(String(cid), cached);
    if (_cswRungReads.size > 40) _cswRungReads.delete(_cswRungReads.keys().next().value);
  }
  let d = null;
  try { d = await cached.promise; } catch (_) {
    if (_cswRungReads.get(String(cid)) === cached) _cswRungReads.delete(String(cid));
    return;
  }
  if (cswState().active !== cid) return; // A late read cannot retry into another thread's shell.
  const currentThread = (cswState().threads || []).find(t => String(t.zoko_customer_id) === String(cid)) || cswState().activeThread;
  const currentVersion = String(currentThread?.last_message_at || currentThread?.last_inbound_at || '');
  if (currentVersion !== version) {
    if (_cswRungReads.get(String(cid)) === cached) _cswRungReads.delete(String(cid));
    return cswLoadRung(cid);
  }
  if (d && d.ok && d.capability === 'absent') _cswRungOff = true;
  if (cswState().active !== cid) return;
  const el = document.getElementById('csw-rung');
  if (el && context && context.kind === 'business_followup') {
    el.textContent = context.label; el.title = 'Review the enquiry and route it to the right team'; el.hidden = false;
    return context;
  }
  if (!el || !d || !d.armed || !d.rung) return;
  el.textContent = String(d.rung_label || d.rung);
  el.title = d.next ? `Next: ${d.next}` : 'Where this sale has got to';
  el.hidden = false;
}

// ── B5 · THE GHOST: THE REP'S OWN WORDS ─────────────────────────────
// One rep typed the same welcome ninety-two times in thirty days. The
// phrase book (mig 818) counted them; this offers them back — the rep's
// OWN line, in grey, with a mono tag saying whose it is and how often
// they use it. Tab takes it. It replaces nothing: no auto-insert, no
// silent rewrite, and it never appears for a line somebody else typed.
let _cswPhrases = { rows: null, at: 0 };

async function cswLoadPhrases() {
  if (_cswPhrases.rows && Date.now() - _cswPhrases.at < 10 * 60000) return _cswPhrases.rows;
  let d = null;
  try { d = await api('/api/cs/desk/phrases'); } catch (_) { d = null; }
  _cswPhrases = { rows: (d && d.phrases) || [], at: Date.now() };
  return _cswPhrases.rows;
}

// PURE. The one line worth offering for what has been typed so far, or
// null. A rep who has typed nothing gets nothing — the book is a
// completion, not a menu, and a menu over an empty box is chrome.
function cswGhostFor(draft, phrases) {
  const d = String(draft || '');
  if (d.length < 2) return null;
  const low = d.toLowerCase();
  let best = null;
  (phrases || []).forEach(p => {
    const text = String((p && p.text) || '');
    if (text.length <= d.length) return;
    if (text.toLowerCase().indexOf(low) !== 0) return;
    if (!best || (p.count || 0) > (best.count || 0)) best = { text, count: p.count || 0 };
  });
  return best;
}

function cswPaintGhost() {
  const host = document.getElementById('csw-ghost');
  const inp = document.getElementById('csw-input');
  if (!host || !inp) return;
  const hit = cswGhostFor(inp.value, _cswPhrases.rows || []);
  cswState()._ghost = hit;
  if (!hit) { host.hidden = true; host.innerHTML = ''; return; }
  host.innerHTML = `<span class="csw-ghost__t">${esc(hit.text)}</span>`
    + `<span class="csw-ghost__k num">yours · ${esc(String(hit.count))}× · Tab</span>`;
  host.hidden = false;
}

function cswGhostTake() {
  const hit = cswState()._ghost;
  const inp = document.getElementById('csw-input');
  if (!hit || !inp) return false;
  inp.value = hit.text;
  inp.dispatchEvent(new Event('input'));
  inp.focus();
  try { inp.setSelectionRange(inp.value.length, inp.value.length); } catch (_) {}
  return true;
}

// ── B6 · ASK ONCE: WHAT THE CHAT ALREADY ANSWERED ───────────────────
// A customer types "L" at three in the morning. At nine a rep opens
// sixty messages, reads the last four, and asks the size again. The size
// was in the chat the whole time. These two chips put it where the rep
// is looking, and the line under the box says so if they start to ask
// again — with the time and the answer on it, so they can judge whether
// it has gone stale. A reminder, never a wall.
async function cswLoadRemembered(cid) {
  const s = cswState();
  s._remembered = null;
  cswPaintRemembered();
  let d = null;
  try { d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/remembered`); } catch (_) { return; }
  if (cswState().active !== cid) return;
  cswState()._remembered = d || null;
  cswPaintRemembered();
}

function cswRememberedChips(d) {
  const bits = [];
  if (d && d.size && d.size.value)
    bits.push(`<span class="csw-remem__c">Size <b>${esc(String(d.size.value))}</b> · remembered</span>`);
  if (d && d.address && d.address.value) {
    const a = String(d.address.value);
    bits.push(`<span class="csw-remem__c" title="${esc(a)}">Address <b>${esc(a.length > 34 ? a.slice(0, 31) + '…' : a)}</b> · remembered</span>`);
  }
  if (d && d.area && d.area.value) {
    // WHERE, NOT AN ADDRESS. She named a place when the desk asked where
    // it goes — worth showing so nobody asks again — but it never fills
    // the order form: a form that looks filled is a form nobody checks.
    const q = String(d.area.value);
    bits.push(`<span class="csw-remem__c" title="${esc(q)}">Where <b>${esc(q.length > 34 ? q.slice(0, 31) + '…' : q)}</b> · remembered</span>`);
  }
  return bits.join('');
}

function cswPaintRemembered() {
  const host = document.getElementById('csw-remem');
  if (!host) return;
  const html = cswRememberedChips(cswState()._remembered);
  host.innerHTML = html;
  host.hidden = !html;
}

// PURE. The words under the box, or null. The server owns the same rule
// (cs_remembered.reask_warning) for anything that has to be right when
// the page is not looking; this is the copy the rep reads as they type.
const CSW_REASK = [
  ['size', ['what size', 'which size', 'your size', 'size are you', 'size do you']],
  ['address', ['delivery address', 'delivery location', 'your address',
    'where should we deliver', 'where do we deliver', 'send your address']],
];

function cswReaskWarn(draft, known) {
  const d = String(draft || '').toLowerCase();
  for (const [field, words] of CSW_REASK) {
    const have = known && known[field];
    if (!have || !have.value) continue;
    if (!words.some(w => d.indexOf(w) >= 0)) continue;
    let v = String(have.value);
    if (field === 'address' && v.length > 42) v = v.slice(0, 39) + '…';
    const when = cswMsgTime(have.at);
    return when ? `Asked at ${when} — they said ${v}` : `Already answered — they said ${v}`;
  }
  return null;
}

function cswPaintReask() {
  const host = document.getElementById('csw-reask');
  const inp = document.getElementById('csw-input');
  if (!host || !inp) return;
  const w = cswReaskWarn(inp.value, cswState()._remembered || {});
  host.textContent = w || '';
  host.hidden = !w;
}

// ── B7 · THE ORDER SHEET ────────────────────────────────────────────
// "Mark as ordered" used to be a bare browser confirm asking one
// yes/no question — "Place this order on Shopify now?" — when the desk
// has THREE ways a sale actually ends and only one of them is Shopify.
// On a brand taking ~90% of its money at counters, a dialog that offers
// only the online route sends the other two to the wrong place. This
// sheet names all three, opens in place, and scrolls inside itself.
function cswEndingSheet(rev, opts) {
  const o = opts || {};
  const store = o.store || 'a store';
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">How is this one ending?</div><button class="csw-x" id="csw-os-x">✕</button></div>
    <div class="csw-ordersheet">
      <button type="button" class="csw-oschoice" data-os="till">
        <b>Ring it on the Online till</b>
        <span>The sale goes through the shop's own till. Ring it there, then link it back to this chat so the customer's phone lands on it.</span>
      </button>
      <button type="button" class="csw-oschoice" data-os="hold">
        <b>Hold it at ${esc(store)}</b>
        <span>Keep the piece behind the counter with a code the customer says when they come. Nothing is charged now.</span>
      </button>
      <button type="button" class="csw-oschoice" data-os="build">
        <b>Build the order here</b>
        <span>Place it on Shopify from this desk, with the pieces and the address on this chat.</span>
      </button>
    </div>`);
  ov.querySelector('#csw-os-x')?.addEventListener('click', () => ov.remove());
  ov.querySelectorAll('[data-os]').forEach(b => b.addEventListener('click', () => {
    if (o.isCurrent && !o.isCurrent()) { ov.remove(); return; }
    const pick = b.dataset.os;
    ov.remove();
    if (pick === 'till') { cswTillQueueSheet(); return; }
    // The dock key's own handler, not the sheet underneath it: the hold
    // sheet needs a product, and cswDockHold is the one place that
    // resolves the piece from the thread and falls through to Stock when
    // there is none. Calling the sheet directly with no product returns
    // silently — a dead choice on a sheet is worse than no choice.
    if (pick === 'hold') { cswDockHold(); return; }
    if (typeof o.onBuild === 'function') o.onBuild();
  }));
  return ov;
}

// ── 01 · THE CUSTOMER FILE ──────────────────────────────────────────
// Reads cswState()._custIntel — the /customer-context payload the 360
// panel already fetched and cached. No new request exists in this face.
function cswPaintCustomerFile() {
  const host = document.getElementById('csw-file');
  if (!host) return;
  host.hidden = true; host.innerHTML = '';
  const s = cswState();
  const cache = s._custIntel;
  if (!cache || cache.cid !== s.active) return;
  const d = cache.data || {};
  if (!d.ok) return;
  const p = d.profile || {};
  const chips = [];
  const chip = (label, value, title) => `<span class="crd-chip"${title ? ` title="${esc(title)}"` : ''}>`
    + `<span class="crd-chip__l">${esc(label)}</span><b>${esc(value)}</b></span>`;
  // ORDERS — the number a rep needs before the first sentence.
  const orders = Number(p.order_count);
  if (isFinite(orders) && orders > 0) chips.push(chip('orders', String(orders), 'Paid orders on this customer'));
  // LIFETIME — what they are worth, in the desk's own money format.
  if (p.ltv_ngn != null && Number(p.ltv_ngn) > 0) chips.push(chip('lifetime', cswNgn(p.ltv_ngn)));
  // SIZES — from their OWN orders (customer_dossier's size truth). Lands
  // with the dossier wire; until then this chip does not exist.
  const sizes = cswFileSizes(d);
  if (sizes) chips.push(chip('sizes', sizes, 'Sizes we know from their own orders'));
  // LAST SEEN — cswRelDays already speaks the desk's dialect.
  if (p.days_since_last != null) chips.push(chip('last seen', cswRelDays(p.days_since_last)));
  // THE REPEAT-INCIDENT BAND. A person who has complained before is not a
  // new complaint, and a rep who does not know that writes the wrong first
  // line. Red, once, at the top — never a chip among chips.
  const priors = Number(d.prior_complaints);
  let band = '';
  if (isFinite(priors) && priors >= 1) {
    const when = d.prior_complaint_at ? ` · last ${esc(cswAgo(d.prior_complaint_at))}` : '';
    band = `<div class="crd-file__band">⚠ <span><b>They have raised this before</b> — `
      + `${priors} earlier ${priors === 1 ? 'incident' : 'incidents'}${when}. `
      + `Open with that, not with a fresh apology.</span></div>`;
  }
  if (!chips.length && !band) return;      // nothing known → no chrome at all
  host.innerHTML = (chips.length ? `<div class="crd-file__chips">${chips.join('')}</div>` : '') + band;
  host.hidden = false;
}

// The size line, from whichever shape the payload carries. `sizes_map` is
// the unified truth (chat over derived); `sizes` is the per-dossier count
// list. Neither present → '' and the chip never renders.
function cswFileSizes(d) {
  const m = d && d.sizes_map;
  if (m && typeof m === 'object' && Object.keys(m).length) {
    return Object.keys(m).slice(0, 3).map(k => `${k} ${m[k]}`).join(' · ');
  }
  const arr = (d && d.sizes) || [];
  if (Array.isArray(arr) && arr.length) {
    return arr.slice(0, 3).map(x => (x && x.size) ? String(x.size) : String(x)).join(' · ');
  }
  return '';
}

// ── 02 · THE CLOSE DOCK's two new keys ──────────────────────────────
// HOLD AT STORE — the counter close. The sheet needs a product; the
// thread's latest matched inbound piece is the one the customer is
// asking about, and when there is none the Stock tool is the honest
// door (it finds the piece, and carries its own Hold key).
// 2026-09-24 (vet 18): a customer who named the piece with a crux.ng
// link got the same detour as one who named nothing: a toast, then an
// empty stock search, many taps from a hold, so counter holds went
// unused. The newest of the two facts now wins, the matched photo or
// the link; the link is read by its exact handle, never a free-text
// guess. When the link cannot be read, the search opens already filled.
async function cswDockHold() {
  const cid = cswState().active;
  const p = cswLatestInboundProduct();
  const link = cswLatestProductLink();
  if (p && p.id && !(link && link.at > p.at)) { cswHoldSheet(p.id); return; }
  if (link) {
    let d = null;
    try { d = await api('/api/cs/availability?handle=' + encodeURIComponent(link.handle)); } catch (_) {}
    if (cswState().active !== cid) return;
    const pid = d && d.ok !== false && d.product && d.product.product_id;
    if (pid) { cswHoldSheet(String(pid)); return; }
  }
  cswToast('Find the piece first — then hold it at a store');
  cswStockSheet();
  // The search starts from the words the link names, typed for the rep.
  const q = link && document.getElementById('csw-stk-q');
  if (q) { q.value = link.words; q.dispatchEvent(new Event('input')); }
}

// ACCOUNT — the bank details, out of the SAME canned pack every other
// reply surface reads (cswCannedReplies → DB rows override defaults).
// Inserts into the composer for the rep to send; it never sends. No
// account-shaped reply saved → the Quick sheet opens so the rep can
// write one once, for everybody.
async function cswDockAccount() {
  const inp = document.getElementById('csw-input');
  if (!inp || inp.disabled) { cswToast('The window is closed — only a template reopens it', true); return; }
  let replies = [];
  try { replies = await cswCannedReplies(); } catch (_) { replies = []; }
  const hit = replies.find(r => /account|bank|transfer|gtb|details/i.test(
    `${(r && r.label) || ''} ${(r && r.body) || ''}`));
  if (!hit) { cswToast('No account reply saved yet — add one and it is there for every rep'); cswTemplateSheet(); return; }
  inp.value = inp.value ? `${inp.value}\n${hit.body || ''}` : (hit.body || '');
  inp.dispatchEvent(new Event('input'));
  inp.focus();
  // THE MISSING WIRE (L3, vet 2026-08-30): the autonomous tool opens a
  // transfer intent when it sends these same details — this key never
  // did, which is why payment_intents held ZERO transfer rows and the
  // receipt that followed had nothing to land on. Fire-and-forget: the
  // server opens/reuses the intent from the thread's own open quote
  // (never a client-supplied amount), writes an INTENT only, and a
  // failure changes nothing the rep sees. The composer insert above is
  // already done — this never blocks it.
  const cid = cswState().active;
  if (cid) {
    try {
      api(`/api/cs/threads/${encodeURIComponent(cid)}/transfer-intent`,
        { method: 'POST', body: JSON.stringify({}) })
        .then(r => {
          cswPaintAcctState(cid, r);
          // A price is on the chat but EON cannot tell the total (2 Oct
          // 2026): no card opened. Say so now, before the rep sends the
          // details, so the chat never ends with no card and no word.
          if (r && r.opened === false && r.reason === 'quote_unclear' && cswState().active === cid)
            cswToast(CSW_QUOTE_UNCLEAR_WORDS, true);
        }).catch(() => {});
    } catch (_) {}
  }
}

// B7 — "I've sent it", and the stamp under it. The rep pressed Account
// and the words are in the box; what they could not see was whether the
// desk actually has anything for a receipt to land on. This says so, and
// it says it from SERVER TRUTH ONLY — the status on the payment_intents
// row the call just opened or reused, never a local guess that the tap
// worked. No intent, no line: an unopened intent is exactly the case
// where a green tick would be a lie.
// 2026-09-24 (vet fault 20): a CONFIRMED payment read "they say it has
// gone" — the words for a customer's claim. payment_confirmed is a bank
// credit or a rep who checked the bank, so it reads as confirmed;
// payment_claimed (mig 814, a receipt image) is their word and reads as
// their word.
// What the rep reads when the chat's total cannot be told (2 Oct 2026):
// an item price, then a smaller line such as a delivery fee, and no
// reader yet that tells a fee from a price.
const CSW_QUOTE_UNCLEAR_WORDS = 'No payment card was opened: this chat has more than one price, so EON cannot tell the total. Confirm the amount with the customer before they pay, then use Mark paid when the money is in.';

function cswAcctWords(r) {
  if (!r || r.ok === false) return null;
  if (r.opened === false && r.reason === 'quote_unclear')
    return { words: "I've sent it — no payment card: the total isn't clear, confirm it with the customer", cls: 'open' };
  const st = String(r.status || (r.intent && r.intent.status) || '').toLowerCase();
  if (st === 'payment_confirmed')
    return { words: "I've sent it — payment confirmed", cls: 'seen' };
  if (st === 'payment_claimed')
    return { words: "I've sent it — they say it has gone; not confirmed yet", cls: 'open' };
  if (r.opened || r.reused || st === 'pending')
    return { words: "I've sent it — waiting on their transfer", cls: 'open' };
  return null;
}

function cswPaintAcctState(cid, r) {
  const host = document.getElementById('csw-acct');
  if (!host || cswState().active !== cid) return;
  const w = cswAcctWords(r);
  if (!w) { host.hidden = true; host.textContent = ''; return; }
  host.className = `csw-acct csw-acct--${w.cls}`;
  host.textContent = w.words;
  host.hidden = false;
}

// ── 03 · THE QUOTE CLOCK ────────────────────────────────────────────
// The biggest measured leak on this desk is a quote nobody chased. When
// the thread payload names an open quote and how long it has been quiet,
// the thread wears an amber ribbon that says both.
//
// SORT IS THE FEED'S JOB. The priority feed already ranks the book, and a
// client-side re-sort would silently disagree with the server's own
// order — this face only ever SAYS the fact, never moves a row.
function cswQuoteFacts(t) {
  if (!t) return null;
  // Three shapes are accepted on purpose, because three lanes could carry
  // this fact and none of them has reached the thread payload yet: a flat
  // field on the row, a nested `open_quote` object (cs_quote_clock's own
  // candidate shape — {amount, amount_text, quoted_at}), or the receipt
  // card's `open_quote_amount`. Whichever lands, the ribbon lights; none
  // of them, and it stays dark.
  const q = (t.open_quote && typeof t.open_quote === 'object') ? t.open_quote : {};
  const amt = Number([t.open_quote_ngn, q.amount, t.open_quote_amount]
    .find(v => v != null));
  if (!isFinite(amt) || amt <= 0) return null;
  let mins = Number(t.quote_silent_minutes);
  if (!(isFinite(mins) && mins >= 0)) {
    // Derive it from the quote's own timestamp when the row carries one.
    const at = Date.parse(t.quoted_at || q.quoted_at || '');
    mins = isFinite(at) ? Math.max(0, Math.floor((Date.now() - at) / 60000)) : NaN;
  }
  return { amount: amt, minutes: isFinite(mins) && mins >= 0 ? mins : null };
}

function cswQuoteAge(mins) {
  if (mins == null) return '';
  if (mins < 60) return `quiet ${mins}m`;
  if (mins < 1440) return `quiet ${Math.floor(mins / 60)}h`;
  return `quiet ${Math.floor(mins / 1440)}d`;
}

function cswPaintQuoteClock() {
  const host = document.getElementById('csw-quoteclock');
  if (!host) return;
  host.hidden = true; host.innerHTML = '';
  const q = cswQuoteFacts(cswState().activeThread);
  if (!q) return;
  const age = cswQuoteAge(q.minutes);
  host.innerHTML = `<span>Quoted <span class="crd-quote__amt">${esc(cswNgn(q.amount))}</span> — not answered yet</span>`
    + (age ? `<span class="crd-quote__age">${esc(age)}</span>` : '');
  host.hidden = false;
}

// ── 07 · THE RELAY'S IN-THREAD STATE ────────────────────────────────
// The relay's board lives on the Desk room; inside a thread the rep could
// not see whether the department they asked had picked it up. This chip
// prints the ask's own short id and a four-bead statebar — raised,
// acknowledged, worked, closed — lit as far as the ask has actually got.
//
// THE ARMED GATE IS UNCHANGED. Nothing renders unless cswRelayLoad has
// said armed:true, exactly like the composer key.
//
// The desk chip deliberately carries NO customer field (relay_desk._chip:
// "NO CUSTOMER FIELD IS COPIED"), so the board's rows cannot be joined to
// a thread client-side. Two sources feed this face: an ask THIS rep just
// raised from THIS thread (remembered locally the moment the door
// answers), and `activeThread.relay_asks` when the thread payload starts
// carrying its own asks.
function cswThreadAsks(cid) {
  const s = cswState();
  const local = ((s._threadAsks || {})[cid] || []);
  const fromPayload = ((s.activeThread || {}).relay_asks || []);
  const out = [], seen = new Set();
  [...fromPayload, ...local].forEach(a => {
    const k = String((a && (a.task_id || a.short_id)) || '');
    if (!a || (k && seen.has(k))) return;
    if (k) seen.add(k);
    out.push(a);
  });
  return out;
}

function cswAskBeads(a) {
  // 4 beads: raised · acknowledged · being worked · closed.
  const k = cswRelayStateKey(a);
  const lit = k === 'done' ? 4 : (k === 'blocked' ? 2 : (k === 'live' ? 3 : 1));
  return `<span class="crd-bar" role="img" aria-label="${esc(k)}">`
    + [1, 2, 3, 4].map(i => `<i class="${i <= lit ? 'on' : ''}"></i>`).join('') + '</span>';
}

function cswPaintAskState() {
  const host = document.getElementById('csw-askstate');
  if (!host) return;
  host.hidden = true; host.innerHTML = '';
  if (!_cswRelay.armed) return;                    // the gate, exactly as it was
  const asks = cswThreadAsks(cswState().active);
  if (!asks.length) return;
  host.innerHTML = asks.slice(0, 3).map(a => {
    const k = cswRelayStateKey(a);
    return `<div class="crd-ask crd-ask--${esc(k)}">`
      + `<span class="crd-ask__id">${esc(a.short_id || 'T-—')}</span>`
      + `<span class="crd-ask__t">${esc(cswRelayLine(a, true))}</span>`
      + cswAskBeads(a) + '</div>';
  }).join('');
  host.hidden = false;
}

// ── 05 · THE RECEIPT CARD ───────────────────────────────────────────
// Every observed close on this desk is a manual bank transfer, a receipt
// screenshot, and a rep comparing two numbers by eye. The card lays the
// amount we KNOW beside the receipt sitting in the chat, and CONFIRM
// PAID routes through the same mark-paid-manual gate a rep always had.
// The machine only pre-reads — the rep still presses.
//
// L6 (2026-08-31): the payload rides the THREAD ROW, and nothing else.
// This card shipped on 2026-08-29 calling a GET
// /threads/{id}/receipt-verify that was never built. L3 (2026-08-30)
// answered that with the bake (cs_inbox._bake_receipt_verify, stamped
// onto every thread_list row) but left the fetch standing underneath as
// a fallback, where it could only ever 404 — 45 of them across 17
// threads between 2026-08-30 10:45Z and 2026-08-31 15:12Z, each one
// swallowed by the catch below it. The fallback is gone: no bake means
// no known amount, and a card with nothing to lay beside the receipt is
// exactly the card this version declines to draw. s.activeThread is
// always a baked thread_list row (assigned from s.threads on open and
// repointed from the same array on every refresh), so the row is the
// only supplier this card has ever had.
//
// COST: still gated on the thread actually holding an inbound image. A
// thread of pure text never asks the question.
function cswThreadHasInboundImage() {
  return (cswState().transcript || []).some(r => {
    if ((r.direction || '').toLowerCase() !== 'inbound') return false;
    const b = r.body || {};
    return !!(b.image_url || (Array.isArray(b.media) && b.media.some(m => m && m.url)));
  });
}

async function cswLoadReceiptCard(cid) {
  const host = document.getElementById('csw-receipt');
  if (!host) return;
  host.hidden = true; host.innerHTML = '';
  const t = cswState().activeThread || {};
  let card = t.receipt_verify || null;             // the thread-list bake (L3)
  if (card && !card.receipt_detected) {
    // The bake carries the KNOWN amount (open intent, else the open
    // quote) — but whether a receipt actually sits in this chat is the
    // transcript's own fact. Gate here, or a quoted thread with no
    // image would say 'Receipt in this chat'. The card renders
    // match:'unknown' → 'nothing to compare' — its shipped degrade path.
    if (!cswThreadHasInboundImage()) return;
    card = { ...card, receipt_detected: true };
  }
  if (!card) return;                               // no bake → no known amount
                                                   // → nothing to compare. See L6.
  cswRenderReceiptCard(cid, card);
}

function cswRenderReceiptCard(cid, card) {
  const host = document.getElementById('csw-receipt');
  if (!host || !card || !card.receipt_detected) return;   // absent → nothing
  const match = String(card.match || 'unknown');
  const stateWord = match === 'exact' ? 'matches the quote'
    : (match === 'mismatch' ? 'does NOT match' : 'nothing to compare');
  const num = (v, l, cls) => `<div class="crd-num ${cls || ''}">`
    + `<div class="crd-num__v">${esc(v)}</div><div class="crd-num__l">${esc(l)}</div></div>`;
  const paid = card.candidate_amount != null ? cswNgn(card.candidate_amount) : 'not read';
  const quoted = card.open_quote_amount != null ? cswNgn(card.open_quote_amount) : 'none on file';
  const who = [card.bank, card.sender_name].filter(Boolean).join(' · ');
  host.className = 'crd-receipt crd-receipt--' + esc(match);
  host.innerHTML = `
    <div class="crd-receipt__head">Receipt in this chat
      <span class="crd-receipt__state">${esc(stateWord)}</span></div>
    <div class="crd-receipt__nums">
      ${num(paid, 'landed', 'crd-num--paid')}
      ${num(quoted, 'we quoted')}
    </div>
    ${who || card.note ? `<div class="crd-receipt__note">${esc([who, card.note].filter(Boolean).join(' — '))}</div>` : ''}
    <div class="crd-receipt__row">
      <button type="button" class="crd-btn crd-btn--go" data-crd="receipt-confirm">Confirm paid</button>
      <button type="button" class="crd-btn crd-btn--ghost" data-crd="receipt-no">Not a match</button>
    </div>`;
  host.hidden = false;
  host.querySelector('[data-crd="receipt-confirm"]')?.addEventListener('click',
    () => cswReceiptConfirm(cid, card, host));
  // NOT A MATCH is reversible — nothing is written, nothing is paid — so
  // it takes ONE tap and no dialog (the desk's one-click law). Only the
  // money-moving key earns the confirmation sheet.
  host.querySelector('[data-crd="receipt-no"]')?.addEventListener('click', () => {
    host.hidden = true; host.innerHTML = '';
    cswToast('Left unconfirmed — nothing was marked paid');
  });
}

async function cswReceiptConfirm(cid, card, host) {
  const mismatch = String(card.match || '') === 'mismatch';
  // THE MISMATCH COSTS A SENTENCE. The route refuses a mismatched amount
  // without override + note (409), so asking here is not a nicety — it is
  // the only way the tap can succeed, and a written reason is the audit
  // trail behind it.
  const res = await cswConfirm({
    title: mismatch ? 'The amounts do not agree' : 'Mark this order paid?',
    // L3: version one bakes NO read of the receipt's own number
    // (match:'unknown', candidate_amount absent) — the dialog must not
    // say '₦0 landed'. It says what we actually know: the quote.
    body: mismatch
      ? `<p>${esc(cswNgn(card.candidate_amount))} landed; this chat quoted `
        + `${esc(cswNgn(card.open_quote_amount))}. Confirming anyway needs a reason — `
        + `it goes on the record.</p>`
      : (card.candidate_amount != null
        ? `<p>${esc(cswNgn(card.candidate_amount))} against a quote of `
          + `${esc(cswNgn(card.open_quote_amount))}. This marks the order paid by transfer.</p>`
        : `<p>We quoted ${esc(cswNgn(card.open_quote_amount))} — check the receipt in the chat `
          + `against it. This marks the order paid by transfer.</p>`),
    confirmLabel: mismatch ? 'Confirm anyway' : 'Confirm paid',
    requireNote: mismatch,
    notePlaceholder: 'Why are you confirming a different amount? (required)',
  });
  if (!res || !res.ok) return;
  const btn = host.querySelector('[data-crd="receipt-confirm"]');
  if (btn) { btn.disabled = true; btn.textContent = 'Confirming…'; }
  let r = null;
  try {
    // The receipt lane yielded to the Converting Engine's transfer close;
    // the one-tap lands on the endpoint the yielded route always
    // delegated to. The card renders only from a pre-baked payload, so
    // this key is unreachable until that lane bakes one.
    // 2026-09-24 (vet fault 4): the card's own reference rides along so
    // the server settles THIS card (it checks the card is this chat's),
    // and the amount read off the receipt, when there is one, so a
    // different amount always carries its written reason.
    r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/mark-paid-manual`, {
      method: 'POST',
      body: JSON.stringify({ draft_id: card.draft_id || null,
                             reference: card.reference || null,
                             amount_ngn: card.candidate_amount != null ? card.candidate_amount : null,
                             override: mismatch, note: res.note || null }),
      timeoutMs: 30000,
    });
  } catch (e) {
    if (btn) { btn.disabled = false; btn.textContent = 'Confirm paid'; }
    // Round 2 (2026-09-26): one plain sentence. A lost answer may still
    // have marked it paid, so it says check first, never "try again".
    cswToast(cswSayWhy(e, { unsure: 'Not sure that went through. Refresh the chat before confirming again.', otherwise: 'Not confirmed. Try again.' }), true);
    return;
  }
  host.hidden = true; host.innerHTML = '';
  // Say what actually happened (vet fault 4): a card with no order behind
  // it records the rep's word and places nothing — the old toast claimed
  // an order that did not exist.
  if (r && r.placed === false) cswToast(r.message || 'Payment noted. No order is linked to it yet.');
  // The customer's "Payment received" message did not go (seat B): say so,
  // so the rep tells them in the chat instead of leaving them waiting.
  else if (r && r.receipt_note) cswToast(r.receipt_note, true);
  else cswToast(`Marked paid${r && r.order_name ? ' · ' + r.order_name : ''} — order placed`);
  cswLoadPayment(cid);
  setTimeout(() => cswLoadTranscript(cid, true), 1200);
}

// ── 06 · STOCK CHIPS ────────────────────────────────────────────────
// The rep answers "do you have my size" by opening a sheet and typing a
// name the customer already said. When the thread's newest inbound photo
// or message matched a piece, the per-size branch counts sit AT the
// composer instead — one tap holds it at that store, and a size nobody
// has offers the waitlist the desk already had (backinstock).
//
// ONE availability read per (thread, product), cached on state; the same
// endpoint the Stock tool and the hold sheet call.
function cswLatestInboundMatch() {
  const rows = (cswState().transcript || []);
  for (let i = rows.length - 1; i >= 0; i--) {
    const r = rows[i];
    if ((r.direction || '').toLowerCase() !== 'inbound') continue;
    const pm = (r.body || {}).product_match;
    if (!pm) continue;
    for (const it of (pm.items || [])) {
      const m = it && it.status === 'auto' && (it.matches || [])[0];
      if (m && m.product_shopify_id) {
        return { id: String(m.product_shopify_id), title: m.title || m.handle || '',
                 mid: String(r.id || '') };
      }
    }
    return null;
  }
  return null;
}

async function cswLoadStockChips(cid) {
  const host = document.getElementById('csw-stockchips');
  if (!host) return;
  host.hidden = true; host.innerHTML = '';
  const hit = cswLatestInboundMatch();
  if (!hit) return;                                 // no matched piece → no chips
  const s = cswState();
  const key = cid + ':' + hit.id;
  let av = (s._avail && s._avail.key === key) ? s._avail.data : null;
  if (!av) {
    try { av = await api('/api/cs/availability?product_shopify_id=' + encodeURIComponent(hit.id), { timeoutMs: 20000 }); }
    catch (_) { return; }
    s._avail = { key, data: av };
  }
  if (cswState().active !== cid) return;
  cswRenderStockChips(cid, hit, av);
}

// 'M / Blue' and 'Blue / 36/37' carry the size token in no fixed slot —
// rank by the first token that reads as a size word, else the first
// number (numerics sort after letter sizes), else last.
const CSW_SIZE_RANK = { XXS: 0, XS: 1, S: 2, M: 3, L: 4, XL: 5, XXL: 6, '2XL': 6, XXXL: 7, '3XL': 7, '4XL': 8 };
function cswSizeRank(t) {
  let num = null;
  for (const tok of String(t || '').split('/').map(x => x.trim().toUpperCase())) {
    if (tok in CSW_SIZE_RANK) return CSW_SIZE_RANK[tok];
    if (num == null && /^\d{1,3}$/.test(tok)) num = 100 + Number(tok);
  }
  return num == null ? 999 : num;
}

function cswRenderStockChips(cid, hit, av) {
  const host = document.getElementById('csw-stockchips');
  if (!host || !av || av.ok === false) return;
  const ph = (x) => ['default title', 'one', '—', ''].includes(String(x || '').trim().toLowerCase());
  // ONE chip per size, never per (size, store) pair. A size x colour
  // piece stocked at all five branches is dozens of live pairs — the
  // flat pair grid wrapped ~14 rows deep on a phone and crushed the
  // transcript to nothing (operator photo, 2026-08-30; live worst case
  // that day: 84 pairs). The tray answers "which sizes exist"; the
  // STORE is chosen in the hold sheet the tap opens, which already
  // paints a per-branch ledger hint on every store. A size sitting at
  // exactly one branch keeps the one-tap store pre-selection.
  const bySize = new Map();
  (av.locations || []).forEach(l => {
    (l.sizes || []).forEach(sz => {
      const n = Number(sz.available || 0);
      if (!(n > 0) || ph(sz.size)) return;
      const k = String(sz.size).trim();
      const e = bySize.get(k) || { total: 0, stores: [] };
      e.total += n;
      e.stores.push({ name: l.location_name || '', n });
      bySize.set(k, e);
    });
  });
  const chips = [...bySize.entries()]
    .sort((a, b) => cswSizeRank(a[0]) - cswSizeRank(b[0]) || a[0].localeCompare(b[0]))
    .map(([size, e]) => {
      const one = e.stores.length === 1 ? e.stores[0] : null;
      const sub = one ? `${one.name} · ${one.n}` : `${e.total} across ${e.stores.length} stores`;
      const split = e.stores.map(s => `${s.name} ${s.n}`).join(' · ');
      return `<button type="button" class="crd-stock__chip" data-crd="hold-size"`
        + ` data-pid="${esc(hit.id)}" data-vt="${esc(size)}" data-loc="${esc(one ? one.name : '')}"`
        + ` title="${esc(split)} — tap to hold">`
        + `<b>${esc(size)}</b><i>${esc(sub)}</i></button>`;
    });
  // Nothing on any shelf → the ONE honest key: put them on the list. It
  // posts to the backinstock lane that already shipped, so this is a door,
  // not a new promise.
  if (!chips.length) {
    if (!hit.mid) return;                           // no message to attach the waitlist to
    chips.push(`<button type="button" class="crd-stock__chip crd-stock__chip--oos" data-crd="waitlist"`
      + ` data-pid="${esc(hit.id)}" data-mid="${esc(hit.mid)}">`
      + `<b>Sold out</b><i>tap to ping them on restock</i></button>`);
  }
  // The header says what the chips DO. A grid of sizes with no verb is a
  // read-out; one line turns it into an action a stranger can take.
  const lead = chips.length === 1 && chips[0].includes('crd-stock__chip--oos')
    ? 'nothing on any shelf' : 'tap a size to hold it';
  host.innerHTML = `<div class="crd-stock__h">${esc(hit.title || 'This piece')} · ${esc(lead)}</div>`
    + `<div class="crd-stock__row">${chips.join('')}</div>`;
  host.hidden = false;
  host.querySelectorAll('[data-crd="hold-size"]').forEach(b => b.addEventListener('click', () =>
    cswHoldSheet(b.dataset.pid, { variant: b.dataset.vt, storeName: b.dataset.loc })));
  host.querySelectorAll('[data-crd="waitlist"]').forEach(b => b.addEventListener('click', async () => {
    b.disabled = true;
    try {
      await api(`/api/cs/threads/${encodeURIComponent(cid)}/product-match/backinstock`,
        { method: 'POST', body: JSON.stringify({ message_id: b.dataset.mid, product_shopify_id: b.dataset.pid }) });
      b.innerHTML = '<b>On the list</b><i>we ping them on restock</i>';
    } catch (e) { b.disabled = false; cswToast(cswSayWhy(e, { lead: 'Not added to the list.' }), true); }
  }));
}

// ── 04 · DRAFT RECEIPTS ─────────────────────────────────────────────
// A draft that KNEW this customer and a draft written blind used to look
// identical. cs_copilot now reports which trusted blocks actually rode
// the generation (`grounding` / `grounded_kind`), so the card prints its
// own receipt — and Send, Edit and Discard stop being three equal words
// in a row.
const CRD_GROUND_WORDS = {
  dossier: 'knows this customer',
  stock: 'live stock checked',
};

function cswGroundChips(src) {
  // Accepts either shape: the raw list (`grounding: ['dossier','stock']`)
  // or the stamped string (`grounded_kind: 'dossier+stock'`).
  let kinds = [];
  if (Array.isArray(src && src.grounding)) kinds = src.grounding.slice();
  else if (src && src.grounded_kind) kinds = String(src.grounded_kind).split('+');
  kinds = kinds.map(k => String(k || '').trim()).filter(Boolean);
  if (!kinds.length) return '';                     // ungrounded → NO chip at all
  return `<div class="crd-grounds">` + kinds.map(k =>
    `<span class="crd-ground"><i>✓</i>${esc(CRD_GROUND_WORDS[k] || k)}</span>`).join('') + '</div>';
}

// Seven interaction upgrades: pending work is scoped to its conversation.
function cswPending(cid = cswState().active) {
  const s = cswState();
  const reg = s._prepared || (s._prepared = Object.create(null));
  return reg[cid] || (reg[cid] = { draft: null, media: null, request: 0 });
}

function cswKeepReviewFieldVisible(ov) {
  if (!ov.isConnected) return;
  const body = ov.querySelector('.csw-seven__body'), field = document.activeElement;
  if (!body || !field || !body.contains(field) || !field.matches('input,textarea,[contenteditable="true"]')) return;
  const f = field.getBoundingClientRect(), b = body.getBoundingClientRect();
  if (f.bottom > b.bottom + 1 || f.top < b.top - 1) field.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

function cswReviewSheet(title, inner) {
  const ov = cswOverlay(`<div class="csw-sheet__head"><h3>${esc(title)}</h3><button class="csw-x" type="button" aria-label="Close ${esc(title)}">✕</button></div><div class="csw-seven__body">${inner}</div>`, false);
  ov.classList.add('csw-seven');
  ov.querySelector('[role="dialog"]').setAttribute('aria-label', title);
  const body = ov.querySelector('.csw-seven__body');
  const actions = body.querySelector(':scope > .csw-seven__actions');
  if (actions) { actions.classList.add('csw-seven__footer'); ov.querySelector('.csw-sheet').appendChild(actions); }
  let frame = 0;
  const keepVisible = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(() => cswKeepReviewFieldVisible(ov)); };
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(keepVisible);
  observer?.observe(body);
  ov.addEventListener('focusin', keepVisible);
  window.visualViewport?.addEventListener('resize', keepVisible);
  const remove = ov.remove.bind(ov);
  ov.remove = () => { observer?.disconnect(); cancelAnimationFrame(frame); window.visualViewport?.removeEventListener('resize', keepVisible); remove(); };
  ov.querySelector('.csw-x').onclick = () => ov.remove();
  return ov;
}

function cswPendingPaint() {
  const cid = cswState().active;
  if (!cid || cid === CSW_EON_CID) return;
  const p = cswPending(cid);
  cswStageSet('draft', p.draft ? {
    key: cid, name: 'Reply prepared', sub: esc(p.draft.text.slice(0, 90)), media: '<span aria-hidden="true">✦</span>',
    fill: { label: 'Review', act: () => cswPreparedDraftSheet(cid) },
    onOpen: () => cswPreparedDraftSheet(cid),
  } : null);
  cswStageSet('photo', p.media ? {
    key: cid, name: p.media.kind === 'video' ? 'Video prepared' : 'Photo prepared',
    sub: esc(cswWindowBlocks(cswState().activeThread) ? 'Kept here · waiting for an open reply window' : `${cswMediaSize(p.media.file?.size)} · ready to review`),
    media: p.media.kind === 'video' ? '<span aria-hidden="true">▶</span>' : `<img src="${esc(p.media.url)}" alt=""/>`,
    fill: { label: 'Review', act: () => cswPreparedMediaSheet(cid) },
    onOpen: () => cswPreparedMediaSheet(cid),
  } : null);
}

function cswStageQueue() {
  const st = cswStageState(), cid = cswState().active;
  const { kinds } = cswStageTop();
  const ov = cswReviewSheet('Waiting in this conversation', kinds.map((kind, i) => {
    const card = st.cards[kind];
    return `<button type="button" class="csw-seven__choice" data-pending="${i}"><span>${card.name}<small>${card.sub || ''}</small></span><span aria-hidden="true">›</span></button>`;
  }).join(''));
  ov.querySelectorAll('[data-pending]').forEach(b => b.onclick = () => {
    if (cswState().active !== cid) return ov.remove();
    st.selectedKind = kinds[Number(b.dataset.pending)]; st.open = false;
    ov.remove(); cswStagePaint();
    const card = st.cards[st.selectedKind];
    if (card?.onOpen && !card.sheet) card.onOpen();
    else document.querySelector('#csw-lookoffer .csw-stage__txt')?.focus();
  });
}

async function cswClaimDraftOwnership(cid, event) {
  const s = cswState();
  if (!event?.isTrusted || document.hidden || !cswSurfaceActive() || !cid || cid !== s.active || cid === CSW_EON_CID) return false;
  try {
    const result = await api(`/api/cs/threads/${encodeURIComponent(cid)}/takeover`, { method: 'POST', body: '{}' });
    if (result?.ok !== true) throw new Error('Takeover not confirmed');
    if (s.active === cid && s.activeThread) {
      s.activeThread.ball = { kind: 'rep', holder: 'you' };
      cswPaintBallBar();
    }
    return true;
  } catch (_) {
    if (s.active === cid) cswToast('Reply kept. Could not confirm your takeover. Try again.', true);
    return false;
  }
}

function cswAdoptDraft(cid, text, onUsed = () => {}, event, opts = {}) {
  const inp = document.getElementById('csw-input');
  if (cswState().active !== cid || !inp) return cswToast('Open the original conversation to use this reply.', true);
  // Vet 2026-09-24 (fault 1, adversary pass): the rep's own insert keys
  // (quick replies, stock and product replies, the customer card's offer)
  // borrow only the ask-before-replace step — `claim: false`. They never
  // claimed the chat on the server: their words land like typed words, and
  // the ball bar's type-to-take-over claims only while EON holds the ball,
  // as before. Routed through the claim, a refused or offline takeover
  // inserted nothing and a colleague's hold moved to this rep.
  const local = opts.claim === false;
  let busy = false;
  const apply = async (mode, expected, intent) => {
    if (busy || (!local && !intent?.isTrusted)) return false;
    // A caller that bound its words to one sign-in, channel and draft (the
    // message library) is asked again HERE, at the moment of the change —
    // after any wait on the choose-how sheet below, not only before it.
    if (opts.isCurrent && !opts.isCurrent()) { cswToast('Your reply or this conversation changed. Nothing was added.', true); return false; }
    if (cswState().active !== cid || !inp.isConnected) { cswToast('Reply kept in its original conversation.', true); return false; }
    const before = inp.value;
    const result = adoptReply(inp.value, text, mode, expected);
    if (!result.ok) { cswToast('Your reply changed. Review both versions again.', true); return false; }
    if (local) {
      // No wait on this path, so the focus lands inside the tap itself —
      // a phone raises its keyboard only for that, as the old keys did.
      inp.value = result.text; inp.dispatchEvent(new Event('input'));
      cswSaveDraft(cid, inp.value);   // saved now, not after the 400ms wait
      onUsed();
      inp.focus(); inp.setSelectionRange(inp.value.length, inp.value.length);
      return true;
    }
    // The explicit Use/Add/Replace tap claims the conversation first, using
    // the existing route that also discards EON's queued reply. A scripted
    // input event only resizes/saves the text; it is never a takeover.
    busy = true;
    const claimed = await cswClaimDraftOwnership(cid, intent);
    busy = false;
    if (!claimed) return false;
    if (cswState().active !== cid || !inp.isConnected || inp.value !== before) {
      cswToast('Your reply changed. The prepared reply is kept for review.', true); return false;
    }
    const wasProgrammatic = inp._cswProgFill;
    inp._cswProgFill = true;
    try { inp.value = result.text; inp.dispatchEvent(new Event('input')); }
    finally { inp._cswProgFill = wasProgrammatic; }
    // This reply was explicitly accepted. Save it before consuming the
    // prepared item; the programmatic event only suppresses duplicate takeover.
    cswSaveDraft(cid, result.text);
    onUsed();
    setTimeout(() => { if (inp.isConnected) { inp.focus(); inp.setSelectionRange(inp.value.length, inp.value.length); } }, 0);
    return true;
  };
  if (!inp.value.trim()) return apply('use', undefined, event);
  const snapshot = inp.value;
  // 2026-09-24 (vet 12): words EON's silent pre-draft typed are not the
  // rep's. While they sit in the box untouched, the sheet names them as
  // EON's and its keys stop saying "mine".
  const eon = inp._cswEonDraft != null && inp._cswEonDraft === snapshot;
  // 2026-09-26 (adversary on #3559): with EON's draft in the box, "Add
  // below it" joined two EON answers into one message, the very thing
  // this sheet is for. EON's words get two keys: replace them (the main
  // key) or keep them. The rep's own words keep all three, as before.
  const keys = eon
    ? '<button type="button" class="csw-seven__primary" data-adopt="replace">Replace it</button><button type="button" data-adopt="keep">Keep it</button>'
    : '<button type="button" class="csw-seven__primary" data-adopt="append">Add below mine</button><button type="button" data-adopt="replace">Replace my reply</button><button type="button" data-adopt="keep">Keep mine</button>';
  const ov = cswReviewSheet(eon ? 'Choose one reply' : 'Keep your words', `<p class="csw-seven__muted">Choose how to use the prepared reply.</p><label>${eon ? 'EON’s draft in the box' : 'Your current reply'}<textarea readonly rows="3">${esc(snapshot)}</textarea></label><label>Prepared reply<textarea readonly rows="3">${esc(text)}</textarea></label><div class="csw-seven__actions">${keys}</div>`);
  const add = ov.querySelector('[data-adopt="append"]');
  if (add) add.onclick = async e => { if (await apply('append', undefined, e)) ov.remove(); };
  ov.querySelector('[data-adopt="replace"]').onclick = async e => { if (await apply('replace', snapshot, e)) ov.remove(); };
  ov.querySelector('[data-adopt="keep"]').onclick = () => ov.remove();
}

// What the customer last said, for the review sheet (vet 2026-09-24,
// fault 11): reviewing a reply means reading it against the question, and
// the sheet used to cover the chat and never show it. The newest inbound
// row of THIS chat's transcript; the list preview is the fallback when the
// transcript has not loaded. Words are trimmed, never rewritten.
function cswCustomerAsked(cid) {
  const s = cswState();
  const rows = s._advanceTranscriptCid === cid && Array.isArray(s.transcript) ? s.transcript : [];
  const kinds = { image: 'a photo', photo: 'a photo', sticker: 'a sticker', audio: 'a voice note', voice: 'a voice note', ptt: 'a voice note', video: 'a video', document: 'a file', file: 'a file' };
  for (let i = rows.length - 1; i >= 0; i--) {
    const r = rows[i] || {};
    if (String(r.direction || '').toLowerCase() !== 'inbound') continue;
    const b = r.body || {};
    const words = String(b.text || b.caption || '').replace(/\s+/g, ' ').trim();
    if (words) return { words: words.length > 220 ? words.slice(0, 219).trimEnd() + '…' : words };
    return { sent: kinds[String(r.type || b.type || '').toLowerCase()] || 'a message' };
  }
  const t = s.activeThread || {};
  const preview = String(t.last_message_text || '').replace(/\s+/g, ' ').trim();
  if (t.zoko_customer_id === cid && t.last_direction === 'inbound' && preview)
    return { words: preview.length > 220 ? preview.slice(0, 219).trimEnd() + '…' : preview };
  return null;
}

function cswPreparedDraftSheet(cid) {
  const pending = cswPending(cid), draft = pending.draft;
  if (!draft || cswState().active !== cid) return;
  const context = draftContext(draft.meta);
  const name = cswDisplayName(cswState().activeThread || {}) || 'this customer';
  const asked = cswCustomerAsked(cid);
  const askedHtml = asked && asked.words
    ? `<blockquote class="csw-seven__asked"><p class="csw-seven__eyebrow">${esc(name)} asked:</p><p>“${esc(asked.words)}”</p></blockquote>`
    : asked ? `<p class="csw-seven__eyebrow">${esc(name)} sent ${esc(asked.sent)}.</p>` : `<p class="csw-seven__eyebrow">For ${esc(name)}</p>`;
  // The model's name is gone from the context (fault 11): it told the rep
  // nothing they could act on. Commerce C1: the catalog check sits under the draft, painted with it.
  const truthNote = typeof cswTruthNote === 'function' ? cswTruthNote(draft.meta && draft.meta.truth) : '';
  const ov = cswReviewSheet('Review prepared reply', `${askedHtml}<div class="csw-seven__draft">${esc(draft.text)}</div>${truthNote ? `<p class="csw-reask" role="status" style="font-size:14px;color:var(--warn)">${esc(truthNote)}</p>` : ''}
    <div class="csw-seven__chips" aria-label="Prepare another version"><button type="button" data-refine="shorter">Shorter</button><button type="button" data-refine="warmer">Warmer</button></div>
    <details class="csw-seven__evidence"><summary>Draft context</summary><div>${context.length ? context.map(x => `<p>${esc(x)}</p>`).join('') : '<p>EON did not say which extra information it used.</p>'}<p>Check current stock and customer details before making a promise.</p><button type="button" data-context>Open current customer details</button></div></details>
    <div class="csw-seven__actions"><button type="button" class="csw-seven__primary" data-use>Use this reply</button><button type="button" data-discard>Discard draft</button></div><p class="csw-seven__muted" role="status" data-draft-status>Review first. Sending stays in your message box.</p>`);
  const valid = () => pending.draft === draft && cswState().active === cid;
  cswBindHoldActivity(ov, cid, '[data-use], [data-refine], [data-context]');
  ov.querySelector('[data-use]').onclick = e => {
    if (!valid()) return ov.remove();
    ov.remove(); cswAdoptDraft(cid, draft.text, () => { if (pending.draft === draft) pending.draft = null; cswPendingPaint(); }, e);
  };
  ov.querySelector('[data-discard]').onclick = () => { if (pending.draft === draft) pending.draft = null; ov.remove(); cswPendingPaint(); };
  ov.querySelector('[data-context]').onclick = () => {
    if (!valid()) return ov.remove();
    ov.remove(); document.getElementById('csw-360btn2')?.click();
  };
  ov.querySelectorAll('[data-refine]').forEach(b => b.onclick = async () => {
    if (!valid()) return ov.remove();
    const status = ov.querySelector('[data-draft-status]');
    ov.querySelectorAll('[data-refine]').forEach(x => x.disabled = true);
    status.textContent = 'Preparing another version. Your message stays as you wrote it.';
    const result = await cswSuggestReply(false, b.dataset.refine);
    if (!ov.isConnected) return;
    if (result && cswState().active === cid) { ov.remove(); cswPreparedDraftSheet(cid); }
    else { status.textContent = 'Could not prepare another version. The previous draft is kept.'; ov.querySelectorAll('[data-refine]').forEach(x => x.disabled = false); }
  });
  if ((cswState().activeThread || {})._ch === 'comment') ov.querySelector('.csw-seven__chips').hidden = true;
}

function cswMediaSize(size) {
  return Number.isFinite(size) ? size < 1048576 ? `${Math.max(1, Math.round(size / 1024))} KB` : `${(size / 1048576).toFixed(1)} MB` : 'Image link';
}

function cswStageMedia(cid, file, kind, caption, url) {
  if (!cid || cid === CSW_EON_CID || (!file && !url)) return;
  if (kind === 'video' && file?.size > 16 * 1024 * 1024) return cswToast('Video is over 16 MB. Choose a shorter clip.', true);
  const pending = cswPending(cid);
  const stage = () => {
    if (pending.media?.busy) return cswToast('The previous attachment is still being prepared for sending.', true);
    const previous = pending.media;
    pending.media = { file, kind, caption: caption || '', url: url || URL.createObjectURL(file), local: !url, busy: false };
    if (cswState().active === cid) { cswPendingPaint(); cswToast('Attachment prepared. Review it before sending.'); }
    if (previous?.local) URL.revokeObjectURL(previous.url);
  };
  if (!pending.media) return stage();
  const previous = pending.media;
  const ov = cswReviewSheet('Replace the prepared attachment?', '<p>Your message and caption stay separate. Only one attachment waits per conversation.</p><div class="csw-seven__actions"><button type="button" data-replace>Replace attachment</button><button type="button" data-keep>Keep previous</button></div>');
  ov.querySelector('[data-replace]').onclick = () => { if (pending.media === previous) stage(); ov.remove(); };
  ov.querySelector('[data-keep]').onclick = () => ov.remove();
}

function cswPreparedMediaSheet(cid) {
  const pending = cswPending(cid), media = pending.media;
  if (!media || cswState().active !== cid) return;
  const name = cswDisplayName(cswState().activeThread || {}) || 'this customer';
  const ov = cswReviewSheet('Review attachment', `<p class="csw-seven__eyebrow">For ${esc(name)} · ${esc(cswMediaSize(media.file?.size))}</p><div class="csw-seven__preview">${media.kind === 'video' ? `<video src="${esc(media.url)}" controls playsinline preload="metadata"></video>` : `<img src="${esc(media.url)}" alt="Prepared attachment"/>`}</div><label>Caption<textarea rows="2" maxlength="600" data-caption placeholder="Add a caption (optional)">${esc(media.caption)}</textarea></label><p class="csw-seven__muted" data-media-status role="status"></p><div class="csw-seven__actions"><button type="button" class="csw-seven__primary" data-media-send>Send ${media.kind === 'video' ? 'video' : 'photo'}</button><button type="button" data-media-remove>Remove attachment</button></div>`);
  const send = ov.querySelector('[data-media-send]'), status = ov.querySelector('[data-media-status]');
  const valid = () => pending.media === media && cswState().active === cid;
  const sync = () => {
    const blocked = !valid() || cswWindowBlocks(cswState().activeThread);
    send.disabled = blocked || media.busy;
    ov.querySelector('[data-caption]').disabled = media.busy;
    ov.querySelector('[data-media-remove]').disabled = media.busy;
    status.textContent = media.busy ? 'Preparing the attachment for sending…' : blocked ? 'Attachment kept. Sending needs a verified open reply window.' : 'Ready on this device. Your typed message will stay in the composer.';
  };
  const ticker = setInterval(sync, 1000), remove = ov.remove.bind(ov);
  ov.remove = () => { clearInterval(ticker); remove(); };
  cswBindHoldActivity(ov, cid, '[data-media-send], [data-media-remove]');
  ov.querySelector('[data-caption]').oninput = e => { if (pending.media === media) { media.caption = e.target.value; cswRenewActiveHold(e, cid); } };
  ov.querySelector('[data-media-remove]').onclick = () => {
    if (media.busy) return;
    if (pending.media === media) pending.media = null;
    ov.remove(); cswPendingPaint();
    if (media.local) URL.revokeObjectURL(media.url);
  };
  send.onclick = async () => {
    if (!valid() || media.busy || cswWindowBlocks(cswState().activeThread)) return sync();
    media.busy = true; sync();
    let handedOff = false;
    try {
      cswRequireReplyWindow(cid);
      if (media.file) handedOff = await (media.kind === 'video' ? cswStartVideoSend(media.file, media.caption, cid) : cswStartPhotoSend(media.file, media.caption, cid));
      else { await cswSendApi(`/api/cs/threads/${encodeURIComponent(cid)}/send-image`, { method: 'POST', body: JSON.stringify({ file_url: media.url, caption: media.caption }) }); handedOff = true; setTimeout(() => cswLoadTranscript(cid, true), 1400); }
      if (handedOff) { if (pending.media === media) pending.media = null; ov.remove(); if (cswState().active === cid) cswPendingPaint(); if (media.local) URL.revokeObjectURL(media.url); }
    } catch (err) { cswToast(cswSayWhy(err, { send: true, lead: 'Attachment kept.' }), true); }
    finally { media.busy = false; if (ov.isConnected) sync(); }
  };
  sync();
}


function cswShowDraftCard(cid, text, meta) {
  if (!cid || !text) return;
  cswPending(cid).draft = { text, meta: meta || {} };
  if (cswState().active === cid) cswPendingPaint();
}

async function cswDraftCardSend(panel, text) {
  const cid = panel.dataset.cid;
  const btn = panel.querySelector('[data-crd="draft-send"]');
  if (!cid || !(text || '').trim()) return;
  const thread = cswState().active === cid ? cswState().activeThread : (cswState().threads || []).find(t => String(t.zoko_customer_id) === String(cid));
  if (cswWindowBlocks(thread)) {
    cswToast('Draft kept. Customer sending waits for a verified open reply window.', true);
    return;
  }
  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
  try {
    await cswSendApi(`/api/cs/threads/${encodeURIComponent(cid)}/send`,
      { method: 'POST', body: JSON.stringify({ text }) });
  } catch (e) {
    if (btn) { btn.disabled = false; btn.textContent = 'Send'; }
    cswToast(cswSayWhy(e, { send: true }), true);
    return;
  }
  panel.remove();
  cswToast('Sent');
  try { cswMarkReplied(cid); } catch (_) {}
  setTimeout(() => cswLoadTranscript(cid, true), 1400);
}

// ── In-thread message search (WhatsApp parity, 2026-06-13) ─────────
// Reps scroll-hunt for addresses/order numbers daily. The header search
// icon opens an inline bar; results hit GET /threads/{id}/search (escaped
// ILIKE over this thread's text+captions); tapping a result scrolls the
// transcript to that message (data-mid anchor) and flash-highlights it.
function cswBindMsgSearch() {
  const btn = document.getElementById('csw-searchbtn');
  const panel = document.getElementById('csw-msearch');
  const q = document.getElementById('csw-msearch-q');
  const res = document.getElementById('csw-msearch-res');
  const close = document.getElementById('csw-msearch-close');
  if (!btn || !panel || !q || !res) return;
  const hide = () => {
    panel.hidden = true; res.hidden = true; res.innerHTML = ''; q.value = '';
    btn.classList.remove('on');
    cswRestoreFocus(panel._returnFocus || btn);
    panel._returnFocus = null;
  };
  btn.addEventListener('click', () => {
    if (panel.hidden) { panel._returnFocus = document.activeElement; panel.hidden = false; btn.classList.add('on'); q.focus(); }
    else hide();
  });
  close.addEventListener('click', hide);
  q.addEventListener('keydown', e => { if (e.key === 'Escape') { e.stopPropagation(); hide(); } });
  // Debounced live search — min 2 chars (matches the backend gate).
  let timer = null, seq = 0;
  q.addEventListener('input', () => {
    clearTimeout(timer);
    const term = q.value.trim();
    if (term.length < 2) { res.hidden = true; res.innerHTML = ''; return; }
    timer = setTimeout(() => cswMsgSearchRun(term, res, ++seq, () => seq), 280);
  });
}

async function cswMsgSearchRun(term, res, mySeq, curSeq) {
  const cid = cswState().active;
  if (!cid) return;
  res.hidden = false;
  res.innerHTML = '<div class="csw-msearch__hint"><span class="csw-think">searching…</span></div>';
  let d = null;
  try {
    d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/search?q=${encodeURIComponent(term)}`);
  } catch (e) {
    if (mySeq !== curSeq()) return;   // a newer keystroke superseded this query
    res.innerHTML = `<div class="csw-msearch__hint">Search failed: ${esc(e.message || e)}</div>`;
    return;
  }
  if (mySeq !== curSeq() || cswState().active !== cid) return;
  const items = (d && d.ok && d.results) || [];
  if (!items.length) { res.innerHTML = '<div class="csw-msearch__hint">No matches in this chat</div>'; return; }
  res.innerHTML = items.map(r => {
    const dt = new Date(r.ts);
    const when = isNaN(dt) ? String(r.ts || '').slice(0, 16)
      : dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' · ' + cswMsgTime(r.ts);
    return `<button class="csw-msearch__item" data-mid="${esc(r.id)}">
      <span class="csw-msearch__who">${r.direction === 'inbound' ? 'Customer' : 'You'}</span>
      <span class="csw-msearch__snip">${esc(r.snippet || '')}</span>
      <span class="csw-msearch__ts">${esc(when)}</span>
    </button>`;
  }).join('');
  res.querySelectorAll('.csw-msearch__item').forEach(b =>
    b.addEventListener('click', () => cswJumpToMsg(b.dataset.mid)));
}

// Scroll the transcript to a message by id + flash-highlight its bubble.
// Honest miss: the transcript loads the latest 200 — an older hit can't
// be anchored, so say so instead of silently doing nothing.
function cswJumpToMsg(mid) {
  if (!mid) return;
  const body = document.getElementById('csw-script');
  const sel = (window.CSS && CSS.escape) ? CSS.escape(mid) : mid.replace(/"/g, '\\"');
  const row = body && body.querySelector(`.csw-row[data-mid="${sel}"]`);
  if (!row) { cswToast('Message is older than the loaded history'); return; }
  const reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  row.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
  const bub = row.querySelector('.csw-bub') || row;
  bub.classList.remove('csw-flash');
  void bub.offsetWidth;   // restart the flash animation on repeat taps
  bub.classList.add('csw-flash');
  setTimeout(() => bub.classList.remove('csw-flash'), 2000);
}

// ── Re-route sheet (cs_thread_routing, 2026-06-12) ────────────────
// Rep correction: move the open thread to another department queue.
// POST /api/cs/threads/{platform_sender_id}/reroute → source='manual'.
const CSW_QUEUE_CHOICES = [
  { label: 'CS', department: 'cs', location_code: null, color: 'var(--g-violet)' },
  { label: 'Logistics', department: 'logistics', location_code: null, color: 'var(--g-blue)' },
  { label: 'Palms', department: 'sales', location_code: 'PALMS', color: 'var(--accent)' },
  { label: 'Chevron', department: 'sales', location_code: 'CHEVRON', color: 'var(--accent)' },
  { label: 'Osapa', department: 'sales', location_code: 'OSAPA', color: 'var(--accent)' },
  { label: 'Abuja', department: 'sales', location_code: 'ABUJA', color: 'var(--accent)' },
  { label: 'Online', department: 'orders', location_code: null, color: 'var(--g-magenta)' },
];
function cswRouteSheet() {
  const s = cswState();
  const t = s.activeThread;
  if (!t || !t.platform_sender_id) { cswToast('No sender id on this thread', true); return; }
  const cur = cswQueueLabel(t);
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Route to department</div><button class="csw-x">✕</button></div>
    <div style="font-size:11px;color:var(--ink-mute);margin-bottom:12px">${cur ? `Currently in <b>${esc(cur)}</b>${t.route && t.route.source ? ` (${esc(t.route.source)})` : ''}. ` : 'Not routed yet. '}Pick the queue this conversation belongs to.</div>
    <div class="csw-labelpick">${CSW_QUEUE_CHOICES.map(q =>
      `<button class="csw-labelchip ${cur === q.label ? 'on' : ''}" data-d="${q.department}" data-l="${q.location_code || ''}" style="--lc:${q.color}">${esc(q.label)}</button>`).join('')}</div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  ov.querySelectorAll('.csw-labelchip').forEach(btn => btn.addEventListener('click', async () => {
    try {
      const d = await api(`/api/cs/threads/${encodeURIComponent(t.platform_sender_id)}/reroute`, {
        method: 'POST',
        body: JSON.stringify({ department: btn.dataset.d, location_code: btn.dataset.l || null }),
      });
      if (!d || !d.ok) throw new Error((d && d.error) || 'reroute failed');
      t.route = d.route || null;
      const rb = document.getElementById('csw-routebtn');
      if (rb) { rb.textContent = cswQueueLabel(t) || 'Route'; rb.classList.toggle('routed', !!t.route); }
      cswLoadQueues(); cswRenderThreadList();
      cswToast('Re-routed ✓');
      setTimeout(() => ov.remove(), 220);
    } catch (e) { cswToast(cswSayWhy(e, { lead: 'Could not re-route.' }), true); }
  }));
}

// ── THE RELAY · the desk face (PR-R5) ──────────────────────────────
// Two halves of one promise. A rep in a conversation needs another
// department to do something — until now that was a message in a
// WhatsApp group and a hope. The sheet turns it into ONE ask with one
// owner and one clock; the chips bring it back, because the ask is
// deliberately owned by SOMEBODY ELSE and every task surface EON has
// ever shipped answers "what is assigned to me", never "what am I
// waiting on".
//
// DARK MEANS BYTE-IDENTICAL. The server answers {armed:false} without a
// database read and every mount below returns before it touches the
// DOM, so a desk on an unarmed deployment is the desk it was yesterday,
// element for element. Nothing is reserved in any shell's markup for
// the same reason the relay meter reserves nothing on the Tasks page.

let _cswRelay = { armed: false, at: 0, departments: [], stores: [], raised: [], owned: [], capped: false };

async function cswRelayLoad(force) {
  // ONE fetch feeds both mounts — the sheet and the chips are the same
  // feature, and two endpoints would be two flag answers that can
  // disagree for the length of a round trip (a sheet offered while its
  // door is dark). 60s is generous for clocks measured in tens of
  // minutes; the Desk room forces a refresh while a rep is watching.
  if (!force && _cswRelay.at && (Date.now() - _cswRelay.at) < 60000) return _cswRelay;
  let d = null;
  try { d = await api('/api/cs/relay/desk'); } catch (_) { return _cswRelay; }
  if (!d || d.ok === false) return _cswRelay;
  _cswRelay = {
    armed: d.armed === true, at: Date.now(),
    departments: d.departments || [], stores: d.stores || [],
    raised: d.raised || [], owned: d.owned || [], capped: !!d.capped,
  };
  return _cswRelay;
}

// The composer key. INSERTED, never declared in the shell markup: the
// tray is rendered synchronously and this runs a microtask later, so
// with the flag off the tray keeps exactly the keys it had. It carries
// data-tool like every other key, so the house tray's own law applies
// unchanged — behind ⋯ More, never a sixth resting key.
async function cswRelayMountAsk() {
  const r = await cswRelayLoad();
  if (!r.armed) return;
  // The armed answer lands a microtask after the shell painted — the
  // in-thread ask chip (07) is gated on the SAME answer, so it repaints
  // here rather than guessing on a flag that had not arrived yet.
  try { cswPaintAskState(); } catch (_) {}
  const tray = document.getElementById('csw-tray');
  if (!tray || tray.querySelector('[data-tool="ask"]')) return;
  const b = document.createElement('button');
  b.type = 'button';
  // csw-tool--ask carries this floor's 44px tap floor. The shared
  // .csw-tool rule stops at 40px for fourteen keys at once, and raising
  // it would reshape the whole composer for a floor that owns one key.
  b.className = 'csw-tool csw-tool--ask';
  b.dataset.tool = 'ask';
  b.textContent = 'Ask a department';
  // Bound HERE because the generic [data-tool] dispatcher already ran
  // (cswBindComposer is the synchronous tail of the conv shell). The
  // sheet guards its own double-open, so a future re-bind is harmless.
  b.addEventListener('click', () => cswAskSheet());
  tray.appendChild(b);
}

function cswRelayStripId(title) {
  // relay.ask titles read "T-014 · Confirm the transfer" — the chip
  // prints the label in its own slot, so printing it twice is noise.
  return String(title || '').replace(/^T-\d+\s*·\s*/, '');
}

function cswRelayClock(a) {
  // THE ACK CLOCK, in words, and it stops when the ack lands — a chip
  // still counting down after somebody said "I've got it" tells the
  // asker their ask is late when it is being worked on.
  if (a.closed) return '';
  if (a.acked_at) return '';
  const m = a.ack_minutes_left;
  if (m === null || m === undefined) return '';
  if (m >= 0) return `ack due in ${m}m`;
  return `ack overdue ${Math.abs(m)}m`;
}

function cswRelayVia(v) {
  const s = String(v || '').toLowerCase();
  if (!s) return '';
  return s === 'whatsapp' ? ' on WhatsApp' : (s === 'web' || s === 'desk') ? ' at the desk' : ` via ${s}`;
}

// The one honest line under a chip. NEVER a blank byline and never a
// borrowed human name: an EON-minted ask says EON asked (created_by_eon),
// and an ask whose requester will not resolve says so in words.
function cswRelayLine(a, mine) {
  if (a.closed) {
    if (a.done_by) return `done by ${a.done_by}${cswRelayVia(a.done_via)}`;
    return a.status === 'cancelled' ? 'cancelled' : 'closed';
  }
  if (a.status === 'blocked') {
    return `blocked${a.blocked_by ? ' by ' + a.blocked_by : ''}${cswRelayVia(a.blocked_via)} — it needs you`;
  }
  if (a.acked_at) {
    const who = a.acked_by || a.owner || 'someone';
    return `${who} has it${cswRelayVia(a.acked_via)}`;
  }
  if (mine) {
    if (a.owner) return `waiting on ${a.owner}`;
    return a.rung === 'operator'
      ? 'nobody was on shift — it is with the operator'
      : 'waiting to be picked up';
  }
  const asker = a.requester || (a.requester_is_eon ? 'EON' : null);
  return asker ? `${asker} asked you` : 'requester unknown — open it on the board';
}

function cswRelayStateKey(a) {
  if (a.closed) return 'done';
  if (a.status === 'blocked') return 'blocked';
  if (a.acked_at) return 'live';
  if ((a.ack_minutes_left !== null && a.ack_minutes_left !== undefined) && a.ack_minutes_left < 0) return 'late';
  return 'open';
}

// The store an ask was addressed to, BY NAME. `a.store` is a location
// uuid (relay_desk._chip passes metadata.relay.store straight through),
// and the chip printed the literal word "store" beside the department
// for any ask that had one — so every Lagos shop read "Sales · store"
// and the one fact the rep needed, WHICH shop, was the one the chip
// threw away. The id→name map is already in hand: the same payload's
// `stores` list is what the Ask sheet renders its picker from.
function cswRelayStoreName(id) {
  const sid = String(id || '');
  if (!sid) return '';
  const hit = (_cswRelay.stores || []).find(s => String(s.id) === sid);
  // An id the picker does not know is a store outside this desk's
  // scope — say nothing rather than print a uuid at a rep.
  return hit ? (hit.name || '') : '';
}

function cswRelayChip(a, mine) {
  const k = cswRelayStateKey(a);
  const clock = cswRelayClock(a);
  const where = [a.dept, cswRelayStoreName(a.store)].filter(Boolean).join(' · ');
  return `<div class="csw-askchip csw-askchip--${k}">
    <div class="csw-askchip__h">
      <span class="csw-askchip__id num">${esc(a.short_id || 'T-—')}</span>
      <span class="csw-askchip__k">${esc(k === 'done' ? 'closed' : k === 'live' ? 'acked' : k)}</span>
      ${where ? `<span class="csw-askchip__d">${esc(where)}</span>` : ''}
      ${clock ? `<span class="csw-askchip__c num">${esc(clock)}</span>` : ''}
    </div>
    <div class="csw-askchip__t">${esc(cswRelayStripId(a.title))}</div>
    <div class="csw-askchip__m">${esc(cswRelayLine(a, mine))}</div>
  </div>`;
}

function cswRelayCardHTML(r) {
  const raised = (r.raised || []).map(a => cswRelayChip(a, true)).join('')
    || `<div class="csw-askempty">Nothing raised. When you ask a department, it lands here until it comes back.</div>`;
  const owned = (r.owned || []).map(a => cswRelayChip(a, false)).join('')
    || `<div class="csw-askempty">Nothing is waiting on you.</div>`;
  return `<div class="csw-card csw-deskcard" id="csw-askcard">
    <div class="csw-card__t">Your asks</div>
    <div class="csw-asklab">Asks you raised</div>
    <div class="csw-asklist">${raised}</div>
    <div class="csw-asklab">Asks you own</div>
    <div class="csw-asklist">${owned}</div>
    ${r.capped ? `<div class="csw-desklaw">Showing the newest — there are more on the board.</div>` : ''}
    <button class="csw-askact" id="csw-ask-new" type="button">Ask a department</button>
    <div class="csw-desklaw">An ask you raise is owned by somebody else on purpose — that is the control. It comes back here when it closes.</div>
  </div>`;
}

// The Desk room card. Inserted above the Doors card so the rep's own
// work sits with the rest of their address, never appended past the
// doors out of the room.
async function cswRelayMountChips(force) {
  const r = await cswRelayLoad(force);
  if (!r.armed) return;
  const room = document.getElementById('csw-deskroom');
  if (!room) return;
  const el = elFromHTML(cswRelayCardHTML(r));
  const old = document.getElementById('csw-askcard');
  if (old) old.replaceWith(el);
  else {
    const doors = document.getElementById('csw-desk-doors');
    const anchor = doors ? doors.closest('.csw-deskcard') : null;
    if (anchor) room.insertBefore(el, anchor); else room.appendChild(el);
  }
  el.querySelector('#csw-ask-new')?.addEventListener('click', () => cswAskSheet());
}

async function cswAskSheet() {
  if (document.getElementById('csw-asksheet')) return;   // one sheet, one ask
  const r = await cswRelayLoad();
  if (!r.armed) return;
  const t = cswState().activeThread || null;
  const sender = (t && t.platform_sender_id) ? String(t.platform_sender_id) : '';
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Ask a department</div><button class="csw-x">✕</button></div>
    <div class="csw-asksheet" id="csw-asksheet">
      <div class="csw-askhint">${sender
        ? 'Raised from this conversation. The customer is never named on an ask — it carries a reference to the thread and nothing else.'
        : 'Raised from your desk. The customer is never named on an ask.'}</div>
      <div class="csw-asklab">Who you need</div>
      <div class="csw-deptpick" id="csw-ask-depts" role="radiogroup" aria-label="Department">${
        (r.departments || []).map(d =>
          `<button class="csw-deptchip" type="button" role="radio" aria-checked="false" data-dept="${esc(d)}">${esc(d)}</button>`).join('')
      }</div>
      <div class="csw-asklab">Which store <span class="csw-askopt">optional</span></div>
      <div class="csw-deptpick" id="csw-ask-stores" role="radiogroup" aria-label="Store">
        <button class="csw-storechip on" type="button" role="radio" aria-checked="true" data-store="">Anywhere</button>${
        (r.stores || []).map(s =>
          `<button class="csw-storechip" type="button" role="radio" aria-checked="false" data-store="${esc(s.id)}">${esc(s.name)}</button>`).join('')
      }</div>
      <div class="csw-asklab">What you need</div>
      <input class="csw-askin" id="csw-ask-subj" type="text" maxlength="120" autocomplete="off"
             placeholder="Confirm the transfer on order 8841" aria-label="What you need" />
      <div class="csw-asklab">Why it matters <span class="csw-askopt">optional</span></div>
      <textarea class="csw-askta" id="csw-ask-why" rows="3" maxlength="600"
                placeholder="The customer is on the line waiting for the answer." aria-label="Why it matters"></textarea>
      <div class="csw-askerr" id="csw-ask-err" hidden></div>
      <button class="csw-askact" id="csw-ask-go" type="button" disabled>Raise the ask</button>
      <div class="csw-asklaw">EON names whoever is on shift for that department, tells them, and gives them 30 minutes to say they have it. If nobody is on shift it climbs — it never quietly lands nowhere.</div>
    </div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const go = ov.querySelector('#csw-ask-go');
  const subj = ov.querySelector('#csw-ask-subj');
  const err = ov.querySelector('#csw-ask-err');
  let dept = '', store = '';
  const sync = () => { go.disabled = !(dept && (subj.value || '').trim()); };
  ov.querySelectorAll('#csw-ask-depts .csw-deptchip').forEach(b => b.addEventListener('click', () => {
    dept = b.dataset.dept || '';
    ov.querySelectorAll('#csw-ask-depts .csw-deptchip').forEach(x => {
      const on = x === b;
      x.classList.toggle('on', on);
      x.setAttribute('aria-checked', String(on));
    });
    sync();
  }));
  ov.querySelectorAll('#csw-ask-stores .csw-storechip').forEach(b => b.addEventListener('click', () => {
    store = b.dataset.store || '';
    ov.querySelectorAll('#csw-ask-stores .csw-storechip').forEach(x => {
      const on = x === b;
      x.classList.toggle('on', on);
      x.setAttribute('aria-checked', String(on));
    });
  }));
  subj.addEventListener('input', sync);
  go.addEventListener('click', async () => {
    go.disabled = true; err.hidden = true;
    let d = null;
    try {
      d = await api('/api/cs/relay/ask', {
        method: 'POST',
        body: JSON.stringify({
          dept, subject: (subj.value || '').trim(),
          why: (ov.querySelector('#csw-ask-why').value || '').trim(),
          store: store || null,
          platform_sender_id: sender || null,
        }),
      });
    } catch (e) {
      err.textContent = 'Could not raise it: ' + ((e && e.message) || e);
      err.hidden = false; go.disabled = false; return;
    }
    if (d && d.armed === false) {
      // A dark door refused; saying "raised" here is the receipt-dedup
      // lie pointed the other way (R5 verifier, F2).
      err.textContent = 'The relay is not armed — nothing was raised.';
      err.hidden = false; go.disabled = false; return;
    }
    if (!d || d.ok === false) {
      // The door's own words, never a generic failure — "sign in with
      // your staff PIN" is actionable; "something went wrong" is not.
      err.textContent = (d && (d.message || d.error)) || 'EON could not raise that ask.';
      err.hidden = false; go.disabled = false; return;
    }
    const env = d.envelope || {};
    if (d.mode === 'deduped') cswToast(`You already have that ask open — ${env.short_id || ''}`.trim());
    else cswToast(`${env.short_id || 'Asked'} — ${env.owner_name ? env.owner_name + ' has it' : 'raised'}`);
    // 07 — THE ASK'S STATE, IN THE THREAD IT CAME FROM. The desk board
    // deliberately copies no customer field onto an ask, so its rows
    // cannot be joined back to a thread client-side. What the rep raised
    // FROM here is remembered here, and the chip appears immediately.
    const askCid = cswState().active;
    if (askCid) {
      const st = cswState();
      st._threadAsks = st._threadAsks || {};
      const list = (st._threadAsks[askCid] = st._threadAsks[askCid] || []);
      if (env.short_id && !list.some(a => a.short_id === env.short_id)) {
        list.push({ short_id: env.short_id, task_id: env.task_id || null,
                    status: 'open', closed: false, owner: env.owner_name || null,
                    dept, ack_minutes_left: env.ack_minutes_left });
      }
      cswPaintAskState();
    }
    ov.remove();
    cswRelayMountChips(true);
  });
}

// Composer emoji picker — insert at cursor (WhatsApp/Slack staple).
const CSW_EMOJIS = ['😊', '😂', '🙏', '🔥', '💚', '👍', '❤️', '😍', '🙌', '✨', '💯', '🥹', '😅', '👌', '🎉', '😎', '🤝', '💪', '🛍️', '📦', '✅', '⏳', '😢', '🙇'];
// Static waveform pattern for voice notes (decorative — WhatsApp draws a real
// PCM waveform; a fixed pleasant pattern reads identically at this size and
// needs zero audio decoding). Heights are percentages of the bar height.
const CSW_WAVE = [30, 55, 40, 70, 50, 85, 60, 45, 75, 55, 90, 65, 50, 80, 45, 60, 35, 70, 50, 40, 65, 30, 55, 45];
// ── "/" quick-reply palette ──────────────────────────────────────────
// Filters the SAME canned replies the macro strip and Quick sheet use
// (cswCannedReplies → DB rows override defaults), so there is one source of
// truth for rep copy. Opens only on a leading "/", never mid-sentence.
let _cswSlashSel = 0;

function cswSlashClose() {
  document.getElementById('csw-slashpop')?.remove();
  _cswSlashSel = 0;
}

async function cswSlashSync(inp) {
  const m = (inp.value || '').match(/^\/([\w-]*)$/);
  if (!m) { cswSlashClose(); return; }
  const q = (m[1] || '').toLowerCase();
  // One read per list, never an old copy (#3541 adversary pass, 2026-09-24):
  // the list reads fresh as it OPENS, and every key typed while it stays
  // open filters that same read (the kept copy is never older than it).
  // What the rep picks is what the list shows, as it stands now.
  const listOpen = !!document.getElementById('csw-slashpop');
  const all = await cswCannedReplies(listOpen ? { cached: true } : undefined);
  const hits = all.filter(r => !q
    || String(r.label || '').toLowerCase().includes(q)
    || String(r.body || '').toLowerCase().includes(q)).slice(0, 6);
  if (!hits.length) { cswSlashClose(); return; }
  let pop = document.getElementById('csw-slashpop');
  if (!pop) {
    pop = document.createElement('div');
    pop.id = 'csw-slashpop';
    pop.className = 'csw-slashpop';
    (document.getElementById('csw-inputbox') || inp.parentElement).appendChild(pop);
    _cswSlashSel = 0;
  }
  pop._hits = hits;
  if (_cswSlashSel >= hits.length) _cswSlashSel = 0;
  pop.innerHTML = `<div class="csw-slashpop__h">Quick replies</div>` + hits.map((r, i) =>
    `<button class="csw-slashpop__r ${i === _cswSlashSel ? 'on' : ''}" data-i="${i}" type="button">`
    + `<span class="csw-slashpop__k">/${esc(String(r.label || '').toLowerCase().replace(/\s+/g, ''))}</span>`
    + `<span class="csw-slashpop__t">${esc(String(r.body || '').slice(0, 64))}</span></button>`).join('');
  pop.querySelectorAll('.csw-slashpop__r').forEach(b =>
    b.addEventListener('click', () => cswSlashPick(inp, hits[Number(b.dataset.i)])));
}

function cswSlashPick(inp, r) {
  if (!r) return;
  inp.value = r.body || '';
  cswSlashClose();
  inp.dispatchEvent(new Event('input'));
  inp.focus();
}

// Returns true when the palette consumed the key (caller must not send).
function cswSlashKey(e, inp) {
  const pop = document.getElementById('csw-slashpop');
  if (!pop || !pop._hits || !pop._hits.length) return false;
  if (e.key === 'Escape') { e.preventDefault(); cswSlashClose(); return true; }
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault();
    const n = pop._hits.length;
    _cswSlashSel = (_cswSlashSel + (e.key === 'ArrowDown' ? 1 : n - 1)) % n;
    cswSlashSync(inp);
    return true;
  }
  if (e.key === 'Enter' || e.key === 'Tab') {
    e.preventDefault();
    cswSlashPick(inp, pop._hits[_cswSlashSel]);
    return true;
  }
  return false;
}

function cswEmojiPicker(inp) {
  // No-emoji law (staff surfaces, build contract §4.5; audit PRF-3): in a
  // PIN session the picker exists ONLY for the CS unit, whose composer
  // writes customer WhatsApp messages (emoji is the desk's customer
  // register). Team chat and every non-CS queue are staff surfaces.
  if (!sosEmojiAllowed()) return;
  if (document.getElementById('csw-emojipop')) { document.getElementById('csw-emojipop').remove(); return; }
  const pop = document.createElement('div'); pop.id = 'csw-emojipop'; pop.className = 'csw-emojipop';
  pop.innerHTML = CSW_EMOJIS.map(e => `<button class="csw-emojipop__e" type="button">${e}</button>`).join('');
  (document.querySelector('.csw-composer') || document.body).appendChild(pop);
  pop.querySelectorAll('.csw-emojipop__e').forEach(b => b.addEventListener('click', () => {
    const e = b.textContent;
    const start = inp.selectionStart ?? inp.value.length, end = inp.selectionEnd ?? inp.value.length;
    inp.value = inp.value.slice(0, start) + e + inp.value.slice(end);
    inp.dispatchEvent(new Event('input')); inp.focus();
    try { inp.selectionStart = inp.selectionEnd = start + e.length; } catch (_) {}
    pop.remove();
  }));
  setTimeout(() => { const off = (ev) => { if (!pop.contains(ev.target) && ev.target.id !== 'csw-emoji') { pop.remove(); document.removeEventListener('click', off); } }; document.addEventListener('click', off); }, 0);
}

// Per-thread drafts (Telegram/WhatsApp keep a half-typed reply per chat).
// Draft plaintext and unresolved sends belong to a server-verified login.
// JWT decoding supplies stable IDs only after bootstrap has verified membership.
function cswDraftIdentity() {
  const me = eonWorkroomSession(), verified = State._csDraftVerified;
  if (!me || !verified) return null;
  const scope = JSON.stringify([me.tenantId, me.actorId, State.isStaff ? 'staff' : 'owner', me.impersonated]);
  return scope === verified.scope && me.authSession === verified.authSession ? verified : null;
}
function cswDraftVerify(verifiedToken) {
  if (!verifiedToken || verifiedToken !== State.jwt) return;
  const me = eonWorkroomSession();
  if (!me) { cswDraftEnd(); return; }
  const scope = JSON.stringify([me.tenantId, me.actorId, State.isStaff ? 'staff' : 'owner', me.impersonated]);
  if (State._csDraftVerified && (State._csDraftVerified.scope !== scope || State._csDraftVerified.authSession !== me.authSession)) cswDraftEnd();
  State._csDraftVerified = {scope, authSession:me.authSession, key:'csw-private-v2:' + scope};
  try {
    cswDraftMigrateLegacy();
    localStorage.removeItem('csw-drafts'); localStorage.removeItem('csw-sending');
  } catch (_) { State._csDraftStorageFailed = true; }
}
function cswDraftLegacy() {
  const empty = {v:1,threads:[],unknown:false};
  const raw = localStorage.getItem('csw-legacy-pending-v1');
  if (!raw) return empty;
  let data;
  try { data = JSON.parse(raw); } catch (_) { return {...empty,unknown:true}; }
  if (data?.v !== 1 || typeof data.unknown !== 'boolean' || !Array.isArray(data.threads)
      || data.threads.length > 256 || data.threads.some(r => !r || typeof r.cid !== 'string'
        || !r.cid || r.cid.length > 200 || ![null,'whatsapp','instagram'].includes(r.channel)))
    return {...empty,unknown:true};
  return data;
}
function cswDraftMigrateLegacy() {
  const kept = cswDraftLegacy(), raw = localStorage.getItem('csw-sending');
  // Old versions already destroyed the context behind this flag. Preserve that
  // uncertainty; never guess that the next signed-in person owns the operation.
  if (localStorage.getItem('csw-legacy-send-hold')) kept.unknown = true;
  if (raw) {
    let old;
    try { old = raw.length <= 131072 ? JSON.parse(raw) : false; } catch (_) { old = false; }
    if (old !== null && !(Array.isArray(old) && !old.length)) {
      if (!old || typeof old !== 'object' || Array.isArray(old)) kept.unknown = true;
      else for (const [cid, op] of Object.entries(old)) {
        if (!cid || cid.length > 200 || !op || typeof op !== 'object' || Array.isArray(op)) { kept.unknown = true; continue; }
        const channel = ['wa','whatsapp'].includes(op.channel) ? 'whatsapp' : ['ig','instagram'].includes(op.channel) ? 'instagram' : null;
        const actionId = typeof op.actionId === 'string' && /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(op.actionId) ? op.actionId : null;
        if (!(typeof op.w === 'string' && op.w.trim()) && !actionId) { kept.unknown = true; continue; }
        const row = {cid,channel,actionId}; // No words, body, quote or inferred login.
        if (kept.threads.some(r => JSON.stringify(r) === JSON.stringify(row))) continue;
        if (kept.threads.length < 256) kept.threads.push(row);
        else kept.unknown = true; // Overflow is unresolved, never silently expired.
      }
    }
  }
  // Durable quarantine precedes removal of any original record; repeat boots
  // do not broaden a known thread hold into a global one or erase uncertainty.
  if (raw || kept.unknown || kept.threads.length)
    localStorage.setItem('csw-legacy-pending-v1', JSON.stringify(kept));
  localStorage.removeItem('csw-legacy-send-hold');
}
function cswDraftStore(handle) {
  if (!handle) return null;
  try {
    const raw = localStorage.getItem(handle.key);
    if (!raw) return {v:2,scope:handle.scope,drafts:{},pending:{}};
    const data = JSON.parse(raw);
    const object = x => x && typeof x === 'object' && !Array.isArray(x);
    if (data.v !== 2 || data.scope !== handle.scope || !object(data.drafts) || !object(data.pending)
        || Object.values(data.drafts).some(d => !object(d) || typeof d.w !== 'string' || typeof d.rev !== 'string')
        || Object.values(data.pending).some(p => !object(p) || typeof p.actionId !== 'string' || typeof p.fingerprint !== 'string'
          || !['pending','uncertain'].includes(p.status))) return null;
    return data;
  } catch (_) { return null; }
}
function cswDraftWrite(handle, data) {
  try { localStorage.setItem(handle.key, JSON.stringify(data)); return true; }
  catch (_) { return false; }
}
function cswDraftEnd(rejectedToken) {
  let old = State._csDraftVerified;
  // Boot can reject a staff token before bootstrap verifies it. That token's
  // own claims still name the envelope its login left on this device.
  if (!old && rejectedToken && State.isStaff === true) try {
    const part = String(rejectedToken).split('.')[1] || '';
    const claims = JSON.parse(atob(part.replace(/-/g,'+').replace(/_/g,'/').padEnd(Math.ceil(part.length/4)*4,'=')));
    const uuid = value => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
    const scope = JSON.stringify([claims.tid, claims.sub, 'staff', State.impersonating === true || claims.imp === true]);
    if (uuid(claims.tid) && uuid(claims.sub) && localStorage.getItem('csw-private-v2:' + scope))
      old = {scope, key:'csw-private-v2:' + scope};
  } catch (_) { /* An unreadable token names no envelope. */ }
  if (old) {
    const data = cswDraftStore(old);
    if (data) {
      data.drafts = {};
      for (const [key, op] of Object.entries(data.pending)) {
        data.pending[key] = {actionId:op.actionId,fingerprint:op.fingerprint,status:'uncertain',at:op.at};
      }
      if (!cswDraftWrite(old, data)) State._csDraftStorageFailed = true;
    } else {
      // An unreadable envelope may still hold an unresolved send. Keep it
      // intact and report failed cleanup; never turn unknown storage into empty.
      State._csDraftStorageFailed = true;
    }
  }
  document.getElementById('csw-send-recovery')?.remove();
  State._csDraftVerified = null;
  State._csDraftGeneration = (State._csDraftGeneration || 0) + 1;
  if (State.csw?.poll) clearInterval(State.csw.poll);
  State.csw = null;
  const input = document.getElementById('csw-input'); if (input) input.value = '';
}
function cswDraftCapture(cid) {
  const identity = cswDraftIdentity(), t = cswThreadFor(cid);
  // A missing thread/channel must not fall back to another channel's store.
  const raw = String(t?.channel || t?._plat || '').toLowerCase();
  const channel = ({wa:'whatsapp',ig:'instagram',whatsapp:'whatsapp',instagram:'instagram'})[raw];
  if (!identity || !channel || !cid) return null;
  return {...identity,slot:JSON.stringify([channel,String(cid)]),generation:State._csDraftGeneration || 0};
}
function cswDraftCurrent(handle) {
  const current = cswDraftIdentity();
  return !!handle && !!current && current.scope === handle.scope && current.authSession === handle.authSession
    && (State._csDraftGeneration || 0) === handle.generation;
}
function cswDraftRevision(cid) {
  const handle = cswDraftCapture(cid), data = cswDraftStore(handle);
  return (handle && cswState()._draftEdits?.[handle.slot]?.rev) || data?.drafts[handle.slot]?.rev || null;
}
function cswDraftSchedule(cid, input) {
  const handle = cswDraftCapture(cid);
  if (!cswDraftCurrent(handle)) return;
  const s = cswState(), edit = {w:input.value,rev:cswActionId()};
  (s._draftEdits = s._draftEdits || {})[handle.slot] = edit;
  setTimeout(() => {
    if (cswDraftCurrent(handle) && s._draftEdits?.[handle.slot] === edit)
      cswSaveDraft(cid, edit.w, handle, edit.rev);
  }, 400);
}
function cswSaveDraft(cid, val, captured, revision) {
  const handle = captured || cswDraftCapture(cid);
  if (!cswDraftCurrent(handle)) return null;
  const data = cswDraftStore(handle);
  if (!data) { cswToast('Draft could not be saved on this device.', true); return null; }
  const rev = revision || cswActionId();
  data.drafts[handle.slot] = {w:String(val || ''),rev};
  if (!cswDraftWrite(handle, data)) { cswToast('Draft could not be saved on this device.', true); return null; }
  if (cswState()._draftEdits) delete cswState()._draftEdits[handle.slot];
  if (!val) document.querySelector('#csw-winclosed .csw-heldline')?.remove();
  document.querySelectorAll('[data-csw-draft]').forEach(mark => {
    if (mark.dataset.cswDraft === String(cid)) mark.hidden = !String(val || '').trim();
  });
  return rev;
}
// ── Latest automated-message history (2026-06-11) ───────────────
// Latest-per-sender matches within the window, scoped before the response cap.
// A newer inbound or staff send removes a match. Failed reads are unavailable.
async function cswLoadAutomated() {
  const box = document.getElementById('csw-autox');
  if (!box) return;
  let d;
  const s = cswState();
  try {
    d = await api('/api/cs/automated-pending?limit=100&days=30');
    if (d?.ok !== true || !Array.isArray(d.threads)) throw new Error('History unavailable');
  } catch (e) {
    s._autoBadgeStatus = s._autoPendingN != null ? 'stale' : 'unavailable';
    cswSetAutomatedBadge(null, s._autoBadgeStatus);
    box.innerHTML = '<div class="csw-empty"><div class="csw-empty__m">!</div>Couldn’t load automated messages. <button type="button" id="csw-auto-retry">Retry</button></div>';
    box.querySelector('#csw-auto-retry')?.addEventListener('click', cswLoadAutomated);
    return;
  }
  const threads = d.threads;
  s._autoBadgeStatus = 'fresh';
  cswSetAutomatedBadge(threads.length);
  const head = `<div class="csw-abrate"><b>${threads.length}</b> shown <span class="csw-abrate__sub">Latest automated message history · last 30 days · up to 100 records. A thread appears only when its latest message in this window is automated. A later customer or staff message removes it. Access limits apply; this reading may be incomplete beyond the window and record limit. These records do not confirm delivery or a reply owed.</span></div>`;
  if (!threads.length) { box.innerHTML = head + '<div class="csw-empty"><div class="csw-empty__m">○</div>No records returned for this reading.</div>'; return; }
  const age = (h) => h == null ? '' : (h < 1 ? '<1h ago' : (h < 48 ? Math.round(h) + 'h ago' : Math.round(h / 24) + 'd ago'));
  box.innerHTML = head + threads.map(t => {
    const cid = t.zoko_customer_id || t.platform_sender_id || '';
    return `
    <div class="csw-ab csw-auto" data-cid="${esc(cid)}" role="button" tabindex="0">
      <div class="csw-ab__main">
        <div class="csw-ab__name">${esc(t.customer_name || 'Customer')}</div>
        <div class="csw-ab__meta">${esc((t.last_text || '').slice(0, 90)) || 'Automated message'}</div>
      </div>
      <div class="csw-ab__right">
        <span class="csw-ab__badge sent">${esc(t.template_type === 'buttonTemplate' ? 'flow' : (t.template_type || 'auto'))}</span>
        <span class="csw-ab__badge mut">${age(t.hours_since_send)}</span>
      </div>
    </div>`;
  }).join('');
  box.querySelectorAll('.csw-auto').forEach(el => {
    const open = () => { if (el.dataset.cid) cswJumpToThread(el.dataset.cid); };
    el.addEventListener('click', open);
    el.addEventListener('keydown', (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); open(); } });
  });
}
// Template send history (2026-07-26): limited outbound records in the
// window — recorded copy + status (sent/delivered/read/failed + code).
// Complements the latest-message matches above; neither list is exhaustive.
// Template gallery (2026-08-02, fr 8e666ddb view 3): one card per distinct
// template seen in the 14d outbound ledger — family art + plain label
// (cswTplFamily, the SAME source as the log and the in-thread cards, so the
// three views can never disagree), the newest real rendered copy as the
// sample, and honest counts. Full-desk only server-side; an empty result
// renders the quiet empty state, never an error.
async function cswLoadTemplateGallery() {
  const box = document.getElementById('csw-galx');
  if (!box) return;
  let d;
  try { d = await api('/api/cs/template-gallery?days=14'); }
  catch (e) { box.innerHTML = '<div class="csw-empty"><div class="csw-empty__m">!</div>Couldn’t load the gallery.</div>'; return; }
  const tpls = (d && d.templates) || [];
  if (!tpls.length) { box.innerHTML = '<div class="csw-empty"><div class="csw-empty__m">○</div>No template sends in the last 14 days.</div>'; return; }
  const ago = (iso) => {
    const t = Date.parse(iso || '');
    if (!isFinite(t)) return '';
    const h = (Date.now() - t) / 3600000;
    return h < 1 ? '<1h ago' : (h < 48 ? Math.round(h) + 'h ago' : Math.round(h / 24) + 'd ago');
  };
  box.innerHTML = `<div class="csw-gal">` + tpls.map(t => {
    const fam = cswTplFamily(t.template_name);
    const art = fam.art ? `<img class="csw-galcard__img" src="/static/wa_studio/${esc(fam.art)}" alt="" loading="lazy" decoding="async"/>` : '';
    const counts = `${t.sends} sent` +
      (t.delivered ? ` · ${t.delivered} delivered` : '') +
      (t.failed ? ` · <span class="csw-galcard__bad">${t.failed} failed</span>` : '');
    return `
    <div class="csw-galcard">
      ${art}
      <div class="csw-galcard__body">
        <div class="csw-galcard__fam">${esc(fam.label)} <span class="csw-galcard__raw">${esc(t.template_name || '')}</span></div>
        <div class="csw-galcard__sample">${esc((t.sample_text || '').slice(0, 180)) || 'No copy captured for this one yet.'}</div>
        <div class="csw-galcard__meta">${counts}${t.last_sent_at ? ` · last ${esc(ago(t.last_sent_at))}` : ''}</div>
      </div>
    </div>`;
  }).join('') + `</div>`;
}

async function cswLoadTemplateLog() {
  const box = document.getElementById('csw-tplx');
  if (!box) return;
  let d;
  try { d = await api('/api/cs/template-log?limit=100&days=30'); }
  catch (e) {
    box.innerHTML = '<div class="csw-empty"><div class="csw-empty__m">!</div>Couldn’t load template sends. <button type="button" id="csw-tplx-retry">Retry</button></div>';
    box.querySelector('#csw-tplx-retry')?.addEventListener('click', () => {
      box.innerHTML = '<div class="csw-empty"><div class="csw-empty__m">○</div>Loading template sends…</div>';
      cswLoadTemplateLog();
    });
    return;
  }
  const sends = (d && d.sends) || [];
  const reading = '<p class="csw-abrate__sub">Last 30 days · up to 100 records. Access limits apply; this reading may be incomplete. Each group shows up to 3 records; choose a group to see its returned records.</p>';
  if (!sends.length) { box.innerHTML = reading + '<div class="csw-empty"><div class="csw-empty__m">○</div>No records returned for this reading.</div>'; return; }
  // §08 (operator spec 2026-07-26: "sorted by receipt, review, followup
  // etc") — group the ledger by PURPOSE. Zero schema: every template name
  // already encodes its family. 2026-07-27: the mapping moved to
  // cswTplFamily (shared with the in-thread family cards) so the log and
  // the transcript can never disagree about a template's family.
  const famOf = (name) => cswTplFamily(name).group;
  const ago = (iso) => {
    const t = Date.parse(iso || '');
    if (!isFinite(t)) return '';
    const h = (Date.now() - t) / 3600000;
    return h < 1 ? '<1h ago' : (h < 48 ? Math.round(h) + 'h ago' : Math.round(h / 24) + 'd ago');
  };
  const stBadge = (s0, code) => {
    const st = (s0 || 'sent').toLowerCase();
    if (st === 'failed') return `<span class="csw-ab__badge due">✗ failed${code ? ' · ' + esc(code) : ''}</span>`;
    if (st === 'read') return '<span class="csw-ab__badge ok">✓✓ read</span>';
    if (st === 'delivered') return '<span class="csw-ab__badge ok">✓✓ delivered</span>';
    return '<span class="csw-ab__badge sent">✓ sent</span>';
  };
  const FAM_ORDER = ['Receipts', 'Reviews', 'Follow-ups', 'Win-back', 'Delivery', 'Carts', 'Restock', 'Other'];
  const groups = {};
  sends.forEach(t => { const f = famOf(t.template_name); (groups[f] = groups[f] || []).push(t); });
  const fams = FAM_ORDER.filter(f => groups[f]);
  const s = cswState();
  const render = () => {
    const fam = s._tplFam && groups[s._tplFam] ? s._tplFam : null;
    const rowHtml = (t) => {
      const cid = t.zoko_customer_id || t.platform_sender_id || '';
      return `
      <div class="csw-ab csw-tplrow" data-cid="${esc(cid)}" role="button" tabindex="0">
        <div class="csw-ab__main">
          <div class="csw-ab__name">${esc(t.customer_name || t.platform_sender_id || 'Customer')} <span class="csw-tplrow__tpl">${esc(t.template_name || '')}</span></div>
          <div class="csw-ab__meta">${esc((t.text || '').slice(0, 120)) || 'Template message'}</div>
        </div>
        <div class="csw-ab__right">
          ${stBadge(t.status, t.error_code)}
          <span class="csw-ab__badge mut">${ago(t.sent_at)}</span>
        </div>
      </div>`;
    };
    // Per-family health rollup: delivered/read/failed at a glance — "what
    // are my customers receiving, and did it land?"
    const famHead = (f) => {
      const rows = groups[f];
      const n = (st) => rows.filter(t => (t.status || '').toLowerCase() === st).length;
      const lead = rows.find(t => (t.text || '').trim());
      return `<div class="csw-tplfam__h">${esc(f)} · ${rows.length}
          <span class="ok">${n('delivered') + n('read')} delivered</span>
          ${n('read') ? `<span class="ok">${n('read')} read</span>` : ''}
          ${n('failed') ? `<span class="bad">${n('failed')} failed</span>` : ''}</div>
        ${lead ? `<div class="csw-ab__meta" style="padding:0 2px 4px">“${esc((lead.text || '').slice(0, 140))}”</div>` : ''}`;
    };
    box.innerHTML = reading + `<div class="csw-tplfam">${fams.map(f =>
        `<button class="csw-tplfam__c ${fam === f ? 'on' : ''}" data-fam="${esc(f)}" type="button">${esc(f)} <b>${groups[f].length}</b></button>`).join('')}</div>`
      + (fam ? famHead(fam) + groups[fam].map(rowHtml).join('')
             : fams.map(f => famHead(f) + groups[f].slice(0, 3).map(rowHtml).join('')).join(''));
    box.querySelectorAll('.csw-tplfam__c').forEach(b => b.addEventListener('click', () => {
      s._tplFam = s._tplFam === b.dataset.fam ? null : b.dataset.fam;
      render();
    }));
    box.querySelectorAll('.csw-tplrow').forEach(el => {
      const open = () => { if (el.dataset.cid) cswJumpToThread(el.dataset.cid); };
      el.addEventListener('click', open);
      el.addEventListener('keydown', (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); open(); } });
    });
  };
  render();
}
function cswSetAutomatedBadge(n, status = 'fresh') {
  // The Automated entry moved into the hamburger drawer (2026-06-18), so the
  // count lives in state and surfaces on the drawer entry (when open) + as a
  // small dot on the burger so the rep knows there's something behind it.
  const s = cswState();
  s._autoBadgeStatus = status;
  if (status === 'fresh') s._autoPendingN = n;
  const dc = document.getElementById('csw-drawer-auto-c');
  if (dc) { dc.hidden = status === 'fresh' && !n; dc.textContent = status === 'fresh' ? (n > 99 ? '99+' : String(n || 0)) : status === 'stale' ? 'Stale' : 'Unavailable'; }
  document.getElementById('csw-burger')?.classList.toggle('csw-burger--dot', !!n);
}
// Quiet count fetch for the drawer badge (on workspace entry).
async function cswLoadAutomatedBadge() {
  const s = cswState();
  try {
    const d = await api('/api/cs/automated-pending?limit=100&days=30');
    if (d?.ok !== true || !Array.isArray(d.threads)) throw new Error('History unavailable');
    s._autoBadgeStatus = 'fresh';
    cswSetAutomatedBadge(d.threads.length);
  } catch (_) {
    s._autoBadgeStatus = s._autoPendingN != null ? 'stale' : 'unavailable';
    cswSetAutomatedBadge(null, s._autoBadgeStatus);
  }
}
// Open a thread from the Automated list exactly like an inbox item:
// flip to inbox mode with the thread pre-selected.
function cswJumpToThread(cid) {
  const s = cswState();
  s.mode = 'inbox';   // inbox is the only body; the top segment is gone
  s.active = cid;
  cswRenderBody();
}

// ── Abandoned-cart recovery view (Phase 4) ──────────────────────
async function cswLoadAbandoned() {
  const box = document.getElementById('csw-abx');
  if (!box) return;
  let d;
  try { d = await api('/api/cs/abandoned-carts?limit=50&days=30'); }
  catch (e) { box.innerHTML = '<div class="csw-empty"><div class="csw-empty__m">!</div>Couldn’t load abandoned carts.</div>'; return; }
  const carts = (d && d.carts) || [];
  // Audit M5: the API returns `recovery_rate` (d.rate never existed — the
  // header rendered '—' forever).
  const rate = (d && d.recovery_rate) || {};
  const ngn = (n) => '₦' + Math.round(Number(n) || 0).toLocaleString();
  const pct = (rate.rate != null) ? Math.round(rate.rate * 100) + '%'
            : (rate.recovery_rate != null ? Math.round(rate.recovery_rate * 100) + '%' : '—');
  const recovered = rate.recovered ?? 0, messaged = rate.messaged ?? rate.sent ?? 0;
  const rateLine = `<div class="csw-abrate"><b>${pct}</b> recovered <span class="csw-abrate__sub">${recovered}/${messaged} messaged · 30d</span></div>`;
  if (!carts.length) { box.innerHTML = rateLine + '<div class="csw-empty"><div class="csw-empty__m">○</div>No open abandoned carts.</div>'; return; }
  const badge = (c) => {
    if (c.status === 'recovered') return '<span class="csw-ab__badge ok">recovered</span>';
    if (!c.reachable) return '<span class="csw-ab__badge mut">no WhatsApp</span>';
    if (c.due_stage) return `<span class="csw-ab__badge due">touch ${c.due_stage} due</span>`;
    if (c.touches_sent) return `<span class="csw-ab__badge sent">${c.touches_sent} sent</span>`;
    return '<span class="csw-ab__badge mut">waiting</span>';
  };
  box.innerHTML = rateLine + carts.map(c => `
    <div class="csw-ab">
      <div class="csw-ab__main">
        <div class="csw-ab__name">${esc(c.first_name || 'Customer')}${c.value_ngn ? ` <span class="csw-ab__val">${ngn(c.value_ngn)}</span>` : ''}</div>
        <div class="csw-ab__meta">${c.age_hours != null ? Math.round(c.age_hours) + 'h ago' : ''}${c.phone ? ' · ' + esc(c.phone) : ''}</div>
      </div>
      <div class="csw-ab__right">${badge(c)}${(c.reachable && c.status !== 'recovered')
        ? `<button class="csw-mini csw-mini--p csw-ab__send" data-tok="${esc(c.checkout_token)}">Send recovery</button>` : ''}</div>
    </div>`).join('');
  box.querySelectorAll('.csw-ab__send').forEach(b => b.addEventListener('click', async () => {
    b.disabled = true; b.textContent = '…';
    try {
      const r = await api(`/api/cs/abandoned-carts/${encodeURIComponent(b.dataset.tok)}/recover`, { method: 'POST', body: JSON.stringify({}) });
      if (r && r.ok) { b.textContent = 'Sent ✓'; b.classList.add('done'); }
      else { b.disabled = false; b.textContent = 'Send recovery'; cswToast(cswSayWhy({ message: String((r && (r.message || r.error)) || '') }, { lead: 'Not sent.' }), true); }
    // api() THROWS on the 409, so the else above never runs for a refusal
    // and this catch is where every held message lands. It used to toast a
    // fixed 'Send failed' and throw the reason away — so the rep read
    // "failed" even when the message had merely been paused for a few
    // hours, which is the whole defect FR da0743f8 was filed for. The
    // sentence the door sends is already plain words; show it.
    // Round 2 (2026-09-26): through cswSayWhy, which keeps that sentence
    // and drops only what is not one (a code word, a path). A cart has no
    // chat open to check, so a lost answer (a timeout, a dropped line, a
    // server error with no sentence) says to refresh this list, which
    // counts the touches that went (adversary pass on round 2).
    } catch (e) {
      b.disabled = false; b.textContent = 'Send recovery';
      cswToast(cswSayWhy(e, { send: true, unsure: 'Not sure it went. Refresh this list before sending again.' }), true);
    }
  }));
}

function cswLoadDraft(cid) {
  const handle = cswDraftCapture(cid), data = cswDraftStore(handle);
  if (!data) return '';
  const draft = cswState()._draftEdits?.[handle.slot] || data.drafts[handle.slot], op = data.pending[handle.slot];
  return draft && !(op?.status === 'pending' && op.draftRev === draft.rev) ? draft.w : '';
}
function cswSendingRecords() {
  return cswDraftStore(cswDraftIdentity())?.pending || {};
}
function cswSendingWords(cid) {
  const handle = cswDraftCapture(cid), data = cswDraftStore(handle);
  return data?.pending[handle.slot]?.w || '';
}
function cswKeepSending(cid, operation, previousId) {
  const handle = cswDraftCapture(cid), data = cswDraftStore(handle);
  if (!data || !operation?.actionId) return false;
  const old = data.pending[handle.slot];
  if (old && old.actionId !== operation.actionId && old.actionId !== previousId) return false;
  data.pending[handle.slot] = operation;
  if (!cswDraftWrite(handle, data)) return false;
  document.querySelectorAll('[data-csw-draft]').forEach(mark => {
    if (mark.dataset.cswDraft === String(cid)) mark.hidden = !cswLoadDraft(cid).trim();
  });
  return true;
}
function cswDraftNotSent(e) {
  const d = e?.body?.detail;
  return e?.notSent === true
    || (e?.status === 400 && (d?.error === 'unfilled_placeholder'
      || d === 'text is required' || /^text exceeds \d+ char (instagram|whatsapp) limit$/.test(d)))
    || (e?.status === 404 && d === 'no recipient found for this customer')
    || (e?.status === 409 && ['outside_window','contact_held'].includes(d?.error));
}
function cswDraftFinish(handle, operation, confirmed, notSent = false) {
  const data = cswDraftStore(handle), current = data?.pending[handle.slot];
  if (confirmed) {
    // An accepted action must never answer a later send of the same words.
    try {
      const actionKey = 'eon.contact.action.' + operation.fingerprint;
      if (sessionStorage.getItem(actionKey) === operation.actionId) sessionStorage.removeItem(actionKey);
    } catch (_) {}
  }
  const mine = !!current && current.actionId === operation.actionId;
  // Settled first by another read: a success still drops the words it saved.
  if (!mine && !(confirmed && data?.drafts[handle.slot] && data.drafts[handle.slot].rev === operation.draftRev)) return;
  if (mine && cswDraftCurrent(handle)) {
    const edits = cswState()._draftEdits;
    if (edits?.[handle.slot]) { data.drafts[handle.slot] = edits[handle.slot]; delete edits[handle.slot]; }
  }
  if (confirmed) {
    if (mine) delete data.pending[handle.slot];
    // A resend of this same action may have saved its words as a newer revision.
    const rev = data.drafts[handle.slot]?.rev;
    if (rev && (rev === operation.draftRev || (mine && rev === current.draftRev))) delete data.drafts[handle.slot];
  } else if (notSent) {
    delete data.pending[handle.slot];
    try { sessionStorage.removeItem('eon.contact.action.' + operation.fingerprint); } catch (_) {}
  } else current.status = 'uncertain';
  // Read-modify-write only the captured operation. A logged-out record has
  // already lost its plaintext; a late failure must never put it back.
  cswDraftWrite(handle, data);
  if (cswDraftCurrent(handle)) document.querySelectorAll('[data-csw-draft]').forEach(mark => {
    const cid = mark.dataset.cswDraft;
    if (cswDraftCapture(cid)?.slot === handle.slot) mark.hidden = !cswLoadDraft(cid).trim();
  });
}
function cswDraftHeld(handle, data) {
  return Object.entries(data?.pending || {}).filter(([slot]) => slot === handle.slot
    || slot.startsWith(handle.slot.slice(0, -1) + ','));
}
function cswDraftArchive(handle, operation) {
  const data = cswDraftStore(handle);
  if (!cswDraftCurrent(handle) || !data || data.pending[handle.slot]?.actionId !== operation.actionId) return false;
  const {actionId,fingerprint,at} = operation;
  // Reviewed is not resolved. Keep metadata under the original chat and login.
  data.pending[JSON.stringify([...JSON.parse(handle.slot),actionId])] = {actionId,fingerprint,at,status:'uncertain'};
  delete data.pending[handle.slot];
  return cswDraftWrite(handle, data);
}
function cswSettleSending(cid) {
  // Every chat read asks; one read per chat at a time answers them all.
  const s = cswState(), reads = s._outcomeReads = s._outcomeReads || {};
  return reads[cid] || (reads[cid] = (async () => {
    const handle = cswDraftCapture(cid), data = cswDraftStore(handle);
    if (!handle || !data) return;
    for (const [slot, op] of cswDraftHeld(handle, data)) {
      if (!cswDraftCurrent(handle)) return;
      if (s._sendingNow?.[cid] === op.actionId) continue; // this page still waits on it
      try {
        const result = await api(`/api/cs/threads/${encodeURIComponent(cid)}/send-outcome/${encodeURIComponent(op.actionId)}`,
          {_isCurrent:() => cswDraftCurrent(handle)});
        if (!cswDraftCurrent(handle)) return;
        if (result?.accepted === true && result.outcome === 'accepted' && result.retry_allowed === false
            && result.action_id === op.actionId && result.thread_key === String(cid)
            && result.channel === JSON.parse(handle.slot)[0]) cswDraftFinish({...handle,slot}, op, true);
      } catch (_) { /* A failed read does not prove that a send failed. */ }
    }
    if (cswDraftCurrent(handle)) cswDraftRecovery(cid);
  })().finally(() => { delete reads[cid]; }));
}
function cswDraftRecovery(cid) {
  const handle = cswDraftCapture(cid), data = cswDraftStore(handle);
  if (!handle || !data || cswState().active !== cid) return;
  const composer = document.getElementById('csw-input')?.closest?.('.csw-composer');
  if (!composer) return;
  document.getElementById('csw-send-recovery')?.remove();
  let legacy;
  try { legacy = cswDraftLegacy(); } catch (_) { return; }
  const held = cswDraftHeld(handle, data), channel = JSON.parse(handle.slot)[0];
  const legacyHold = legacy.unknown || legacy.threads.some(r => r.cid === String(cid) && (!r.channel || r.channel === channel));
  if (!held.length && !legacyHold) return;
  const box = document.createElement('div'); box.id = 'csw-send-recovery'; box.className = 'csw-heldline';
  const note = document.createElement('p'); note.setAttribute('role','status');
  note.textContent = 'Earlier send not confirmed. Check its outcome and review this chat. To send different words, type them and choose Send; you will be asked before a new message is sent.';
  box.appendChild(note);
  const words = data.pending[handle.slot]?.w;
  if (words) { const p = document.createElement('p'); p.textContent = words; box.appendChild(p); }
  if (held.length) {
    const check = document.createElement('button'); check.type = 'button'; check.className = 'csw-mini';
    check.textContent = 'Check send outcome';
    check.addEventListener('click', async () => {
      if (!cswDraftCurrent(handle) || cswState().active !== cid) return;
      check.disabled = true; check.textContent = 'Checking…';
      await cswSettleSending(cid);
      if (cswDraftCurrent(handle) && cswState().active === cid)
        cswToast(cswDraftHeld(handle, cswDraftStore(handle)).length ? 'Still not confirmed. Review the chat before sending a new message.' : 'The earlier message was accepted.');
    });
    box.appendChild(check);
  }
  composer.prepend(box);
}

// Pin-to-top (per-rep, localStorage) — keep VIP/active chats above the fold.
function cswPins() {
  const s = cswState();
  if (!s.pins) { try { s.pins = JSON.parse(localStorage.getItem('csw-pins') || '[]'); } catch (_) { s.pins = []; } }
  return s.pins;
}
function cswIsPinned(cid) { return cswPins().includes(cid); }
function cswTogglePin(cid) {
  const s = cswState(); const p = cswPins();
  const i = p.indexOf(cid);
  const pinned = i < 0;
  if (i >= 0) p.splice(i, 1); else p.unshift(cid);
  try { localStorage.setItem('csw-pins', JSON.stringify(p)); } catch (_) {}
  cswRenderThreadList();
  // Persist server-side so the pin follows the rep across devices/sessions.
  // Round 2 (2026-09-26): a failed save was silent, and the next refresh
  // took the server's flag back, so the pin quietly undid itself. Now the
  // screen goes back to what was saved and the rep is told.
  api(`/api/cs/threads/${encodeURIComponent(cid)}/pin`, { method: 'POST', body: JSON.stringify({ pinned }) }).catch((e) => {
    const q = cswPins(), j = q.indexOf(cid);
    if (pinned && j >= 0) q.splice(j, 1);
    else if (!pinned && j < 0) q.unshift(cid);
    try { localStorage.setItem('csw-pins', JSON.stringify(q)); } catch (_) {}
    cswRenderThreadList();
    const key = document.getElementById('csw-pin');
    if (key && cswState().active === cid) key.textContent = cswIsPinned(cid) ? '📌 Unpin' : '📌 Pin to top';
    cswToast(cswSayWhy(e, { lead: pinned ? 'Not pinned.' : 'Not unpinned.' }), true);
  });
}
// Reconcile the local pin cache from the server's authoritative `pinned`
// flags (so a pin set on another device shows here too).
function cswSyncPins(threads) {
  const s = cswState(); const p = cswPins();
  let changed = false;
  (threads || []).forEach(t => {
    const cid = t.zoko_customer_id; if (!cid || t.pinned === undefined) return;
    const has = p.includes(cid);
    if (t.pinned && !has) { p.unshift(cid); changed = true; }
    else if (!t.pinned && has) { p.splice(p.indexOf(cid), 1); changed = true; }
  });
  if (changed) { try { localStorage.setItem('csw-pins', JSON.stringify(p)); } catch (_) {} }
}

// The pick-up send, shared (Opus vet 2026-09-06): the banner button and the
// holding composer both go through this one door, and it answers whether
// the pick-up actually went. The hold records a pick-up only on a true
// answer, so a refused pick-up (template awaiting approval, a 409 with a
// plain sentence, no customer text) leaves the next Send free to try again.
// The pick-up memory (Opus floor 3 verdict, 2026-09-06): remembered where
// the pick-up is SENT, so a banner tap and a held Send share one record —
// per thread, on this device, like drafts and pins. A pick-up older than
// the 24h window is forgotten, since a new one is then real.
function cswPickupMemory() {
  const s = cswState();
  if (!s._pickupSent) { try { s._pickupSent = JSON.parse(localStorage.getItem('csw-pickups') || '{}'); } catch (_) { s._pickupSent = {}; } }
  return s._pickupSent;
}
function cswPickupRecent(cid) { return (Date.now() - (cswPickupMemory()[String(cid)] || 0)) < 24 * 3600 * 1000; }
function cswPickupRemember(cid) {
  const m = cswPickupMemory(); m[String(cid)] = Date.now();
  try { localStorage.setItem('csw-pickups', JSON.stringify(m)); } catch (_) {}
}

async function cswPickupSend(pb, cid = cswState().active) {
  if (!pb || pb.disabled) return false;
  if(cswState()._contactRehearsal){
    cswContactDialog('Approved template · rehearsal','<p>REHEARSAL — NOTHING SENDS</p><p>Any template would still need current approval, permission and a useful reason to contact this person. Only their reply reopens the conversation window. No template was submitted and your draft is unchanged.</p>',pb);
    return false;
  }
  const state = cswState(), key = String(cid);
  const pending = state._pickupPending || (state._pickupPending = new Set());
  if (cswPickupRecent(cid) || pending.has(key)) {
    cswPaintPickupButton(pb, cid);
    return false;
  }
  pending.add(key);
  pb.disabled = true; pb.textContent = 'Sending…';
  try {
    await api(`/api/cs/threads/${encodeURIComponent(cid)}/pickup`, { method: 'POST' });
    pb.textContent = 'Template accepted';
    cswPickupRemember(cid);
    return true;
  } catch (e) {
    delete pb.dataset.pickupState;
    const msg = String((e && e.message) || '');
    if (msg.includes('template_awaiting_approval')) {
      // Stays disabled — nothing a rep can do until the template is
      // approved + registered; retrying would refuse identically.
      pb.textContent = 'Template awaiting approval';
    } else {
      // The 409 carries a plain sentence from wa_refusal_words —
      // "paused for a few hours… ask the shop owner" and the rest.
      // The button is too small to hold it and used to say
      // "Pickup failed — retry", which is wrong for every HELD
      // message and invites a rep to tap for six hours. Short
      // label on the button, the real reason in the toast.
      const known = msg.includes('no_inbound_text');
      pb.textContent = known ? 'No customer text to pick up' : 'Did not send';
      const say = cswSayableRefusal(msg);
      if (!known && say) cswToast(say, true);
      setTimeout(() => { pb.disabled = false; pb.textContent = 'Send template'; }, 2500);
    }
  } finally {
    pending.delete(key);
    // The original button may have left the DOM while the request ran.
    // Paint only this customer's current button; never the next customer's.
    if (String(cswState().active) === key) {
      cswPaintPickupButton(document.getElementById('csw-pickup'), cid);
    }
  }
  return false;
}

function cswPaintPickupButton(pb, cid) {
  if (!pb) return;
  if(cswState()._contactRehearsal){pb.disabled=false;pb.textContent='Rehearse template';pb.title='Review the template rules. Rehearsal sends nothing.';return;}
  const accepted = cswPickupRecent(cid);
  const pending = cswState()._pickupPending?.has(String(cid));
  if (accepted || pending) {
    pb.disabled = true;
    pb.textContent = accepted ? 'Template accepted' : 'Sending…';
    pb.dataset.pickupState = accepted ? 'accepted' : 'pending';
  } else if (['accepted', 'pending'].includes(pb.dataset.pickupState)) {
    // A 24h-old local receipt is no longer a repeat guard. The server
    // remains authoritative across devices and on every actual send.
    pb.disabled = false; pb.textContent = 'Send template';
    delete pb.dataset.pickupState;
  }
}

// Will a free-form send be REFUSED? (WhatsApp 24h rule — text and the
// mic are already disabled on it; a photo is a free-form message under
// the SAME rule, but its button wasn't gated, so a rep picked a photo,
// watched it upload, and only then got the failure. Gift, 2026-07-25:
// "I can't upload photos still".)
// Deliberately WhatsApp-only — and the reason is a FLAG, not a fact
// about Instagram. WhatsApp's transport refuses an out-of-window send;
// Instagram's does not, WHILE ig_primary routes IG through Zoko
// (tenant_integrations(zoko).config.ig_primary = true, 2026-07-29), and
// Zoko accepts the call. Nothing here can prove such a send is
// delivered — no receipt comes back — so the desk warns instead of
// refusing, and IG's closed window is surfaced as a banner.
// ⚠ WHEN ig_primary FLIPS OFF (zoko_io.ig_primary: "flip the flag off
// on approval to restore the native cutover state") the native path
// REFUSES: instagram_messaging maps graph code 10 → outside_window and
// routes_cs answers 409. On that day this predicate must widen to IG,
// or a rep picks a photo, watches it upload, and only then gets the
// refusal — Gift's 2026-07-25 bug, reborn on the other channel.
function cswRefreshReplyWindows() {
  const s = cswState(), slot = document.getElementById('csw-dialslot');
  if (slot && s.activeThread) {
    const dial = cswWindowDial(s.activeThread);
    if (slot.dataset.dial !== dial) { slot.innerHTML = dial; slot.dataset.dial = dial; }
  }
  cswSyncWindowComposer();
  const byCid = new Map((s.threads || []).map(t => [String(t.zoko_customer_id), t]));
  if (s.activeThread && s.active) byCid.set(String(s.active), s.activeThread);
  document.querySelectorAll('[data-csw-window]').forEach(el => {
    const words = cswWindowWords(byCid.get(el.dataset.cswWindow));
    if (el.innerHTML !== words) el.innerHTML = words;
  });
}

function cswBindWindowResume() {
  if (window._cswWindowResumeBound) return;
  window._cswWindowResumeBound = true;
  const resume = () => {
    if (!document.hidden && document.getElementById('csw-dialslot')) cswRefreshReplyWindows();
    cswPositionToasts();
  };
  document.addEventListener('visibilitychange', resume);
  window.addEventListener('pageshow', resume);
  window.addEventListener('focus', resume);
  window.addEventListener('resize', cswPositionToasts);
  window.visualViewport?.addEventListener('resize', cswPositionToasts);
}

function cswSyncWindowComposer() {
  const s = cswState(), t = s.activeThread, cid = s.active;
  const inp = document.getElementById('csw-input');
  const wc = document.getElementById('csw-winclosed');
  if (!inp || !wc || !cid) return;
  wc.setAttribute('role', 'status');
  wc.setAttribute('aria-live', 'polite');
  wc.setAttribute('aria-atomic', 'true');
  const w = cswReplyWindow(t), blocked = cswWindowBlocks(t);
  const key = `${cid}|${w.wa}|${w.state}`;
  const mic = document.getElementById('csw-mic');
  if (mic) mic.disabled = blocked;
  const hint = document.querySelector('.csw-composer > .csw-hint');
  if (hint) hint.textContent = s._contactRehearsal ? 'Rehearse a reply · nothing is sent' : blocked ? 'Draft only · customer sending is held' : CSW_COMPOSER_HINT;
  if(s._contactRehearsal)document.getElementById('csw-send')?.setAttribute('aria-label','Rehearse reply');
  cswPaintPhotoKey();
  if (wc.dataset.windowKey !== key) {
    wc.dataset.windowKey = key;
    delete wc.dataset.armed;
    wc.hidden = true;
    inp.placeholder = 'Message…';
    if (w.state === 'closed' && !w.wa) {
      wc.dataset.armed = '1';
      inp.placeholder = 'Reply window closed — Instagram may not deliver';
      wc.textContent = 'Instagram delivery is uncertain outside the reply window. Channel acceptance does not establish delivery.';
    }
    if (blocked) {
      wc.dataset.armed = '1';
      // Plain words (vet 2026-09-24, fault 20): was "reply window unverified".
      inp.placeholder = w.state === 'closed' ? 'Keep drafting · customer reply needed to send' : 'Keep drafting · not sure the reply window is open';
      wc.innerHTML = w.state === 'closed'
        ? '<div class="csw-window-note"><strong>Customer reply needed</strong><span>Your draft stays editable.</span></div><button class="csw-pickupbtn" id="csw-pickup" type="button" title="Send an approved template. Only the customer’s reply reopens free-form messaging.">Send template</button>'
        : 'Your draft stays editable. The reply window could not be verified, so customer sending is held. Refresh the thread to check again.';
      wc.hidden = false;
      const pb = document.getElementById('csw-pickup');
      pb?.addEventListener('click', (ev) => { ev.stopPropagation(); cswPickupSend(pb, cid); });
    }
  }
  if (blocked && w.state === 'closed') {
    cswPaintHeldLine(cid);
    cswPaintPickupButton(document.getElementById('csw-pickup'), cid);
  }
  cswSyncComposerKeys();
}

// Zoko ig_primary reports acceptance without delivery receipts. Keep IG
// warning-only while that flag is on; native routing REFUSES with 409.
function cswWinClosed() {
  const t = cswState().activeThread;
  return cswWindowBlocks(t);
}

// Keep the existing per-thread held/draft memory in the mobile composer.
// The separate Send template action owns pick-up; keyboard Send holds words
// locally and never substitutes another customer message for the draft.
async function cswHoldReply(cid, typed) {
  cswSaveDraft(cid, typed);
  cswHoldRemember(cid);
  cswPaintHeldLine(cid);
  cswSyncComposerKeys();
  // The reviewed mobile design gives templates their own named action.
  // Enter never turns an unsendable free-form draft into a template send.
  cswToast('Draft held here. Send a template to invite a reply; your words stay saved.');
}

// The held line on the closed-window banner: painted on Send, and again
// when a shut thread reopens with a draft waiting, so a rep on a phone —
// who cannot see the inbox row's ✎ mark with the chat open — still sees
// that a reply is waiting here.
// A hold is remembered per thread on this device (Opus floor 3 verdict,
// third card): the line says "held" only for words a rep actually pressed
// Send on; a draft merely typed and left behind says what it is.
function cswHoldMemory() {
  const s = cswState();
  if (!s._holdsSent) { try { s._holdsSent = JSON.parse(localStorage.getItem('csw-holds') || '{}'); } catch (_) { s._holdsSent = {}; } }
  return s._holdsSent;
}
function cswHoldRemember(cid) {
  const m = cswHoldMemory(); m[String(cid)] = Date.now();
  try { localStorage.setItem('csw-holds', JSON.stringify(m)); } catch (_) {}
}
function cswHoldRecent(cid) { return (Date.now() - (cswHoldMemory()[String(cid)] || 0)) < 24 * 3600 * 1000; }

function cswPaintHeldLine(cid) {
  const wc = document.getElementById('csw-winclosed');
  if (!wc) return;
  wc.querySelector('.csw-heldline')?.remove();
  if (!(cswLoadDraft(cid) || '').trim()) return;
  const held = cswHoldRecent(cid);
  const line = held ? 'Reply held here. You can send it when they answer.' : 'Your words are saved here. Keep drafting.';
  const note = wc.querySelector('.csw-window-note span');
  if (note) { note.textContent = line; }
  else wc.insertAdjacentHTML('afterbegin', `<b class="csw-heldline">${line}</b> `);
  wc.dataset.armed = '1'; wc.hidden = false;
}

// Mic ⇄ send swap. MODULE-LEVEL on purpose (vet 2026-08-09): this lived as
// a closure inside cswBindComposer, so only a keystroke could restore it —
// and it writes INLINE styles. Every send path clears the textarea
// programmatically, which fires no `input` event, so `mic{display:none}`
// and `send{display:flex}` survived the send: after a rep's first typed
// reply the mic was GONE for the rest of the thread (measured on both
// skins) and the visible key was a disabled send. Hold-to-record came back
// only by typing a character and deleting it. Any path that changes the
// textarea's value without a real keystroke MUST call this.
function cswExplainReplyBlock() {
  cswSyncWindowComposer();
  const wc = document.getElementById('csw-winclosed');
  if (wc) { wc.hidden = false; wc.scrollIntoView({ block: 'nearest' }); }
  cswToast('Customer sending is held until the reply window is verified open. The draft stays here.', true);
}

function cswInternalDraft(text) {
  return /^@?eon\b/i.test(String(text || '').trim()) || cswHasStaffMention(String(text || ''));
}

function cswSyncComposerKeys() {
  const inp = document.getElementById('csw-input');
  const send = document.getElementById('csw-send');
  const mic = document.getElementById('csw-mic');
  if (!inp) return;
  // 2026-09-24 (vet 12): EON's pre-filled words keep their label only
  // while they sit in the box unchanged (see cswEonDraftMark).
  if (inp._cswEonDraft != null && inp._cswEonDraft !== inp.value) {
    inp._cswEonDraft = null;
    const tag = document.getElementById('csw-eondraft');
    if (tag) tag.hidden = true;
  }
  const has = !!inp.value.trim();
  // Asked when it exists: the desk's function harnesses lift this function alone.
  if (typeof cswTruthSync === 'function') cswTruthSync(inp.value);
  if (mic && send) {
    // No voice path reaches Instagram (the server refuses a voice note
    // there), and /voice-capability answers for the whole shop, so the chat
    // itself decides (vet round 2, 2026-09-26): no mic key on an Instagram
    // chat, and the (disabled) send key keeps its place. A WhatsApp chat on
    // the other (legacy) number keeps the mic: the desk cannot tell that
    // line apart, and the server's own plain refusal says so there.
    const vt = cswState().activeThread;
    const noMic = !!vt && (vt._plat || cswPlatform(vt)) === 'ig';
    mic.style.display = has || noMic ? 'none' : 'flex';
    send.style.display = has || noMic ? 'flex' : 'none';
  }
  // A disabled input still blocks sending for any other reason. Expiry
  // now leaves editing available and gates customer sending independently.
  // Internal EON requests remain usable while the customer window is shut.
  if (send) send.disabled = !has || !!inp.disabled || (cswWinClosed() && !cswInternalDraft(inp.value));
  cswPaintServiceRail();
}

function cswBindComposer() {
  const inp = document.getElementById('csw-input');
  const send = document.getElementById('csw-send');
  const mic = document.getElementById('csw-mic');
  if (!inp) return;
  // WhatsApp swap: mic when the composer is empty, send when there's text.
  const syncMic = () => cswSyncComposerKeys();
  // Restore a saved draft for this thread.
  const cid0 = cswState().active;
  cswBindHoldActivity(document.getElementById('csw-conv'), cid0,
    '.csw-tool[data-tool], #csw-360btn2, .csw-next__main, [data-open-service-case], [data-refresh-service-case], #csw-orderstatus button, #csw-orderstatus summary');
  // …except words that are on their way to the customer right now: the
  // draft is kept until the send is confirmed (fault 6), and refilling it
  // mid-send would invite a second tap on Send.
  if (cid0) { const dft = cswLoadDraft(cid0); if (dft && (cswState()._sendingNow || {})[cid0] !== dft.trim()) { inp.value = dft; } }
  // A reply that failed while the rep was elsewhere gets its quote back
  // with its words (fault 6, adversary pass) — quietly, no keyboard.
  if (cid0) cswArmFailedQuote(cid0, inp.value, true);
  if (cid0) cswDraftRecovery(cid0);
  // Auto-grow 1→multi-line, then scroll (WhatsApp). Cap matches the CSS
  // max-height (160px ≈ 6 lines); past that the textarea scrolls.
  // Coalesced into one rAF per frame (composer flicker fix, 2026-07-26):
  // the old version reset height to 'auto' and read scrollHeight
  // synchronously on EVERY keystroke — two forced reflows per key, and
  // once a long message hit the 160px cap the collapse/re-expand PAINTED
  // between keys, which is the visible flicker on 500+ character
  // messages. Inside rAF the intermediate 'auto' never paints, and the
  // textarea's own scroll position survives the reset.
  let growPending = false;
  const grow = () => {
    if (growPending) return;
    growPending = true;
    requestAnimationFrame(() => {
      growPending = false;
      // Re-resolve at frame time: a shell re-render can replace the textarea
      // between schedule and frame — the captured node is then detached, the
      // write lands nowhere, and the fresh composer sits at one line until
      // the next keystroke (design vet 2026-07-26, SEV-3).
      const el = document.getElementById('csw-input') || inp;
      const st = el.scrollTop;
      el.style.height = 'auto';
      const sh = el.scrollHeight;
      // The CSS phone cap can be shorter than 160px. Use the rendered cap
      // for scrolling too, or a 4-line draft is clipped with overflow:hidden.
      const cssCap = typeof getComputedStyle === 'function' ? parseFloat(getComputedStyle(el).maxHeight) : 160;
      const cap = Number.isFinite(cssCap) ? Math.min(160, cssCap) : 160;
      el.style.height = Math.min(sh, cap) + 'px';
      el.style.overflowY = sh > cap ? 'auto' : 'hidden';
      if (sh > cap) {
        const atEnd = document.activeElement === el && el.selectionStart === el.selectionEnd
          && el.selectionEnd === el.value.length;
        el.scrollTop = atEnd ? el.scrollHeight : st;
      }
    });
  };
  // A rotation or pane resize can wrap a saved draft without an input
  // event. Watch width only; height changes must not trigger a resize loop.
  const state = cswState();
  state._draftResize?.disconnect();
  if (typeof ResizeObserver !== 'undefined') {
    let width = inp.clientWidth;
    let cap = typeof getComputedStyle === 'function' ? getComputedStyle(inp).maxHeight : '';
    const observer = new ResizeObserver(() => {
      if (!inp.isConnected) { observer.disconnect(); return; }
      const nextCap = typeof getComputedStyle === 'function' ? getComputedStyle(inp).maxHeight : '';
      if (inp.clientWidth !== width || nextCap !== cap) {
        width = inp.clientWidth; cap = nextCap; grow();
      }
    });
    state._draftResize = observer;
    observer.observe(inp);
  }
  // Edits receive a revision immediately; disk writes still wait for a pause.
  // A send result flushes newer edits before settling its own saved draft.
  const saveDraftSoon = () => cswDraftSchedule(cswState().active, inp);
  inp.addEventListener('input', event => {
    cswRenewActiveHold(event, cid0);
    grow();
    syncMic();
    if (!inp._cswProgFill) saveDraftSoon();       // persist per-thread draft (never a silent pre-fill)
    // @-routing typeahead: @ a teammate/department to route THIS customer
    // chat to them (and DM them a link). Reuses the team picker. Gated —
    // the roster filter + popover rebuild only run when an @ is present
    // or the popover is already open (it self-closes on no-match).
    const mbox = document.getElementById('csw-cust-mention');
    if (inp.value.indexOf('@') !== -1 || (mbox && !mbox.hidden)) {
      cswMentionTypeahead('csw-input', 'csw-cust-mention', inp.value);
    }
    // Broadcast "typing…" to other reps; auto-clear after 3s of stillness.
    // Throttled to one broadcast per 2s — a realtime track() per keystroke
    // was measurable jank on phones (part of the composer "hanging").
    const s = cswState();
    const now = Date.now();
    if (!s._typingSentAt || now - s._typingSentAt > 2000) {
      s._typingSentAt = now;
      cswUpdatePresence(true);
    }
    clearTimeout(s._typingTimer);
    s._typingTimer = setTimeout(() => { s._typingSentAt = 0; cswUpdatePresence(false); }, 3000);
  });
  if (inp.value) grow();   // cswSyncComposerKeys() below owns send.disabled
  syncMic();
  cswBindMic();
  // On focus, keep the latest message visible above the keyboard. The
  // viewport listener pins the composer; this re-anchors the transcript so
  // the rep never lands on old history when the keyboard opens.
  inp.addEventListener('focus', () => {
    setTimeout(() => {
      const scr = document.getElementById('csw-script');
      // L4 (2026-08-30): 120, the ONE bottom-proximity threshold on this desk
      // (auto-scroll + jump pill use the same number). The old 240 yanked a
      // rep who was deliberately re-reading two bubbles up.
      if (scr && (scr.scrollHeight - scr.scrollTop - scr.clientHeight) < 120) {
        scr.scrollTop = scr.scrollHeight;
      }
    }, 320);   // after the keyboard animation settles
  });
  inp.addEventListener('keydown', e => {
    // Quick-reply palette (CO-03/AW-03): a leading "/" filters canned replies
    // inline — arrows walk, Enter/Tab inserts, Esc dismisses. Reps used to
    // detour through the + tray for the same text.
    if (cswSlashKey(e, inp)) return;
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); cswSendText(); }
  });
  // Slash palette sync — gated the same way as the mention typeahead:
  // the async canned-replies lookup only runs for a leading "/" or while
  // the palette is open (cswSlashSync closes it on no-match).
  inp.addEventListener('input', () => {
    if (inp.value.charCodeAt(0) === 47 || document.getElementById('csw-slashpop')) {
      cswSlashSync(inp);
    }
  });
  send?.addEventListener('click', cswSendText);
  document.getElementById('csw-emoji')?.addEventListener('click', (e) => { e.stopPropagation(); cswEmojiPicker(inp); });
  // Desk ergonomics (CO-04): reps screenshot orders and receipts constantly —
  // paste and drag-drop now open the same confirm-then-send path as the sheet.
  inp.addEventListener('paste', (e) => {
    const items = (e.clipboardData && e.clipboardData.items) || [];
    for (const it of items) {
      if (it.kind === 'file' && String(it.type || '').startsWith('image/')) {
        const f = it.getAsFile();
        if (f) { e.preventDefault(); cswPastedPhotoSheet(f); return; }
      }
    }
  });
  const convPane = document.getElementById('csw-conv') || document.getElementById('csw-script');
  if (convPane && !convPane._dropBound) {
    convPane._dropBound = true;
    const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
    convPane.addEventListener('dragover', (e) => { stop(e); convPane.classList.add('csw-dropping'); });
    convPane.addEventListener('dragleave', (e) => { stop(e); convPane.classList.remove('csw-dropping'); });
    convPane.addEventListener('drop', (e) => {
      stop(e); convPane.classList.remove('csw-dropping');
      const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f && String(f.type || '').startsWith('image/')) cswPastedPhotoSheet(f);
    });
  }
  // [data-tool] keeps the house ⋯ More key (no tool id) out of this
  // dispatcher — its own handler toggles the tray instead of closing it.
  document.querySelectorAll('.csw-tool[data-tool]').forEach(b => b.addEventListener('click', () => {
    cswTool(b.dataset.tool);
    // HOUSE: the five-key tray is the composer's resting face — it stays
    // put after a pick (thumbs, not menus). Classic closes as before.
    if (!cswHouse()) {
      document.getElementById('csw-tray')?.classList.add('collapsed');   // close after pick
      document.getElementById('csw-plus')?.classList.remove('on');
      // Vet 2026-08-09: this closed the tray and darkened the + while
      // leaving the macro strip — which the + had just opened — standing.
      // Harmless when the strip was always-on; now that the + owns both,
      // it stranded the exact composer state this change exists to
      // remove, two taps from clean. The + owns both, so both close.
      const _mac = document.getElementById('csw-macros');
      if (_mac) _mac.hidden = true;
    }
  }));
}

// ── Rep voice notes (push-to-talk, 2026-06-13) ─────────────────────
// Hold the mic to record (MediaRecorder), release to preview (static
// waveform + play + discard/send). Delivery is capability-gated: until a
// transport can carry audio (Cloud API at cutover / a live-verified Zoko
// flag) the preview shows the honest parked note instead of pretending.

// Best supported recording mime: Chrome/Firefox → webm/opus; iOS Safari
// has no webm — it records audio/mp4 (AAC). null = no MediaRecorder at
// all; '' = let the browser pick its default container.
function cswVoiceMime() {
  if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia) return null;
  const cands = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  for (const c of cands) { try { if (MediaRecorder.isTypeSupported(c)) return c; } catch (_) {} }
  return '';
}

async function cswVoiceCapability() {
  const s = cswState();
  if (s._voiceCap) return s._voiceCap;
  try { s._voiceCap = await api('/api/cs/voice-capability'); }
  // Round 2 (2026-09-26): a failed check is not remembered — it used to
  // park every voice note for the rest of the session after one blip —
  // and it says what it means in plain words.
  catch (_) { return { ok: false, can_send: false, note: 'Could not check if voice notes can go right now. Record again in a moment.' }; }
  return s._voiceCap;
}

function cswBindMic() {
  const mic = document.getElementById('csw-mic');
  const box = document.getElementById('csw-inputbox');
  if (!mic || !box || mic._bound) return;
  mic._bound = true;
  let rec = null;   // {mr, stream, chunks, t0, timer, cancelled}
  const fmt = (ms) => { const s = Math.floor(ms / 1000); return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0'); };
  const stop = (cancelled) => {
    if (!rec) return;
    rec.cancelled = !!cancelled;
    try { if (rec.mr.state !== 'inactive') rec.mr.stop(); }
    catch (_) { const r = rec; rec = null; clearInterval(r.timer); try { r.stream.getTracks().forEach(t => t.stop()); } catch (_) {} document.getElementById('csw-recbar')?.remove(); box.classList.remove('recording'); }
  };
  const start = async () => {
    if (rec || mic.disabled) return;
    const mime = cswVoiceMime();
    if (mime === null) { cswToast('Voice notes are not supported in this browser', true); return; }
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch (_) { cswToast('Microphone blocked — allow mic access for this site to record voice notes', true); return; }
    // Finger lifted while the permission prompt was up → don't ghost-record.
    if (!mic._held) { stream.getTracks().forEach(t => t.stop()); return; }
    let mr = null;
    try { mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream); }
    catch (_) { try { mr = new MediaRecorder(stream); } catch (_2) {} }
    if (!mr) { stream.getTracks().forEach(t => t.stop()); cswToast('Could not start recording', true); return; }
    rec = { mr, stream, chunks: [], t0: Date.now(), timer: null, cancelled: false };
    mr.addEventListener('dataavailable', e => { if (rec && e.data && e.data.size) rec.chunks.push(e.data); });
    mr.addEventListener('stop', () => {
      const r = rec; rec = null;
      if (!r) return;
      clearInterval(r.timer);
      try { r.stream.getTracks().forEach(t => t.stop()); } catch (_) {}
      document.getElementById('csw-recbar')?.remove();
      box.classList.remove('recording');
      mic.classList.remove('rec');
      const dur = Date.now() - r.t0;
      if (r.cancelled || !r.chunks.length) return;
      if (dur < 600) { cswToast('Hold to record, release to preview'); return; }
      const type = (r.mr.mimeType || 'audio/webm').split(';')[0];
      cswVoicePreview(new Blob(r.chunks, { type }), type, Math.max(1, Math.round(dur / 1000)));
    });
    const bar = document.createElement('div');
    bar.id = 'csw-recbar'; bar.className = 'csw-recbar';
    bar.innerHTML = '<span class="csw-recbar__dot" aria-hidden="true"></span><span class="csw-recbar__t" id="csw-rec-t">0:00</span><span class="csw-recbar__hint">recording — release to preview</span>';
    box.classList.add('recording'); box.appendChild(bar);
    mic.classList.add('rec');
    rec.timer = setInterval(() => {
      if (!rec) return;
      const el = document.getElementById('csw-rec-t');
      if (el) el.textContent = fmt(Date.now() - rec.t0);
      if (Date.now() - rec.t0 > 120000) { mic._held = false; stop(false); }   // 2-min cap
    }, 250);
    try { mr.start(250); } catch (_) { mr.start(); }
  };
  mic.addEventListener('pointerdown', e => {
    e.preventDefault();
    mic._held = true;
    try { mic.setPointerCapture(e.pointerId); } catch (_) {}
    start();
  });
  mic.addEventListener('pointerup', () => { mic._held = false; stop(false); });
  mic.addEventListener('pointercancel', () => { mic._held = false; stop(true); });
  mic.addEventListener('contextmenu', e => e.preventDefault());   // long-press menu would break the hold
  // Keyboard a11y: Space/Enter toggles record (holding a key isn't reliable).
  mic.addEventListener('keydown', e => {
    if (e.key !== ' ' && e.key !== 'Enter') return;
    e.preventDefault();
    if (rec) { mic._held = false; stop(false); }
    else { mic._held = true; start(); }
  });
}

// Release → preview strip above the composer: static waveform (matches the
// transcript voice bubbles), play/pause, duration, discard, send.
function cswVoicePreview(blob, mime, durS) {
  document.getElementById('csw-vprev')?.remove();
  const composer = document.querySelector('#csw-conv .csw-composer');
  const anchor = document.getElementById('csw-inputbox');
  if (!composer || !anchor) return;
  const url = URL.createObjectURL(blob);
  const fmt = s => Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
  const el = document.createElement('div');
  el.id = 'csw-vprev'; el.className = 'csw-vprev';
  el.innerHTML = `
    <button class="csw-voice__play csw-vprev__play" aria-label="Play recording"><span class="csw-voice__ico csw-voice__ico--play"></span></button>
    <div class="csw-vprev__mid">
      <div class="csw-voice__wave csw-vprev__wave">${CSW_WAVE.map(h => `<i style="height:${h}%"></i>`).join('')}</div>
      <div class="csw-vprev__note" hidden></div>
    </div>
    <span class="csw-voice__t">${fmt(durS)}</span>
    <button class="csw-vprev__del" aria-label="Discard recording">✕</button>
    <button class="csw-send csw-vprev__send" disabled aria-label="Send voice note">
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 12l16-8-6 16-2.5-6.5L4 12z" fill="currentColor"/></svg>
    </button>`;
  composer.insertBefore(el, anchor);
  let au = null;
  const ico = el.querySelector('.csw-voice__ico');
  el.querySelector('.csw-vprev__play').addEventListener('click', () => {
    if (!au) {
      au = new Audio(url);
      au.addEventListener('ended', () => { ico.className = 'csw-voice__ico csw-voice__ico--play'; });
    }
    if (au.paused) { au.play(); ico.className = 'csw-voice__ico csw-voice__ico--pause'; }
    else { au.pause(); ico.className = 'csw-voice__ico csw-voice__ico--play'; }
  });
  const discard = () => { try { au && au.pause(); } catch (_) {} URL.revokeObjectURL(url); el.remove(); };
  el.querySelector('.csw-vprev__del').addEventListener('click', discard);
  const sendBtn = el.querySelector('.csw-vprev__send');
  // Capability gate — the send stays parked (honest note) until a transport
  // can actually deliver audio. Recording/preview always work.
  cswVoiceCapability().then(cap => {
    if (!el.isConnected) return;
    if (cap && cap.can_send) { sendBtn.disabled = false; return; }
    const note = el.querySelector('.csw-vprev__note');
    note.hidden = false;
    note.textContent = (cap && cap.note) || 'Voice sending will be available after the WhatsApp cutover.';
    el.classList.add('parked');
  });
  sendBtn.addEventListener('click', () => cswVoiceSend(blob, mime, durS, el, discard));
}

async function cswVoiceSend(blob, mime, durS, el, discard) {
  const cid = cswState().active;
  if (!cid) return;
  if (cswWinClosed()) { cswExplainReplyBlock(); return; }
  const sendBtn = el.querySelector('.csw-vprev__send');
  sendBtn.disabled = true; el.classList.add('sending');
  const ext = mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg'
    : mime.includes('mpeg') ? 'mp3' : mime.includes('wav') ? 'wav' : 'webm';
  try {
    // One id for this recording, kept across every tap of Send until EON
    // says it went (Sol 6.1 on #3548); keyed on the recording's own bytes.
    const act = await cswMediaAction(cid, 'voice', blob, String(durS));
    const fd = new FormData();
    fd.append('file', blob, 'voice.' + ext);
    fd.append('duration_s', String(durS));
    const headers = { 'Authorization': `Bearer ${State.jwt}` };
    if (State.tenant) headers['X-Tenant-Slug'] = State.tenant.slug;
    cswRequireReplyWindow(cid);
    let r, d;
    for (let ask = true; ; ask = false) {
      fd.set('contact_action_id', act.id);
      r = await fetch(API + `/api/cs/threads/${encodeURIComponent(cid)}/send-voice`,
        { method: 'POST', headers, body: fd });
      d = await r.json().catch(() => null);
      if (!(ask && cswIsUnsureRepeat(r.status, d)
            && await cswResendAnyway(d.detail.message))) break;
      cswMediaActionRenew(act);           // the rep said yes: a new send
    }
    if (d && d.ok) {
      cswMediaActionDone(act);
      discard();
      cswToast('Voice note sent');
      setTimeout(() => cswLoadTranscript(cid, true), 1800);
      return;
    }
    if (d && d.parked) {
      cswState()._voiceCap = { ok: true, can_send: false, note: d.note };   // server is the truth — re-park
      el.classList.add('parked');
      const note = el.querySelector('.csw-vprev__note');
      if (note) { note.hidden = false; note.textContent = d.note || 'Voice sending will be available after the WhatsApp cutover.'; }
      return;
    }
    // The sheet prints the server's sentence; a one-send answer ("not
    // sure it went", "can't check") carries it in detail.message.
    const det = d && d.detail;
    const why = (det && typeof det === 'object') ? det.message : det;
    if (det && typeof det === 'object' && det.error === 'send_unconfirmed') {
      cswToast(why, true);          // never "failed": it may be on their phone
      sendBtn.disabled = false; el.classList.remove('sending');
      return;
    }
    // Round 2 (2026-09-26): the status and body ride on the error, so the
    // catch's toast can read "That file is too big to send" instead of
    // "send failed (502)".
    throw Object.assign(new Error(String(why || (d && d.error) || '')),
      { status: r.status, body: d || {}, path: `/api/cs/threads/${encodeURIComponent(cid)}/send-voice` });
  } catch (e) {
    cswToast(cswSayWhy(e, { send: true }), true);
    sendBtn.disabled = false; el.classList.remove('sending');
  }
}

// An IG story whose ephemeral media has expired (the CDN url 404s through the
// media-proxy) → replace the broken <img>/<video> with a clean placeholder
// instead of the browser's broken-image glyph. Called inline from onerror so it
// fires even when the error happened before any JS listener attached.
function cswStoryGone(el) {
  // A story still waiting for its media token is not gone (fault 21):
  // cswMediaRepaint gives it the real address when the token lands.
  if (String((el && el.getAttribute && el.getAttribute('src')) || '').includes('#csw-media=')) return;
  try {
    const target = el.closest('.csw-bub__vidwrap') || el;
    const d = document.createElement('div');
    d.className = 'csw-bub__storygone';
    d.textContent = '⊘ Story unavailable';
    target.replaceWith(d);
  } catch (_) {
    try { el.remove(); } catch (_) {}
  }
}

// Media-scoped HMAC token (2026-07-25): owner sessions used to ride the
// FULL session JWT on ?access_token= for <img>/<audio> tags — URLs land in
// access logs and browser history. /api/cs/media-token mints a 1h token the
// server honours ONLY on the media proxy routes; we refresh it 5 min early.
// Since 2026-09-24 (vet fault 21) there is no JWT fallback at all: a photo
// painted before the first mint waits behind a placeholder instead.
const CSW_MEDIA_RETRY_MS = [5000, 15000, 60000];   // a failed mint asks again, then stops
function cswEnsureMediaToken() {
  if (State.isStaff) return;   // staff media auth = HttpOnly cookie
  if (State.csMediaTok && Date.now() < (State.csMediaTokExp || 0)) return;
  if (State._csMediaTokInflight) return;
  State._csMediaTokInflight = true;
  let minted = false;
  api('/api/cs/media-token').then((j) => {
    if (j && j.ok && j.mt) {
      const ttl = j.ttl || 3600;
      State.csMediaTok = j.mt;
      State.csMediaTokExp = Date.now() + (ttl - 300) * 1000;     // refresh from here
      State.csMediaTokHardExp = Date.now() + (ttl - 30) * 1000;  // still honoured until here
      minted = true;
    }
  }).catch(() => {}).finally(() => {
    State._csMediaTokInflight = false;
    if (minted) { State._csMediaFails = 0; cswMediaRepaint(); return; }
    if (!State._csMediaWaiting) return;
    // Pictures are waiting and the mint failed (adversary pass on #3544,
    // 2026-09-24): say so on each picture, with a tap that asks again, and
    // try again on our own a few times, further apart each time — then
    // stop. It used to leave blank frames and ask every 5s for as long as
    // the desk was open. Zero never stands in for unknown.
    const n = State._csMediaFails = (State._csMediaFails || 0) + 1;
    cswMediaStalled();
    const wait = CSW_MEDIA_RETRY_MS[n - 1];
    if (wait && !State._csMediaRetry && typeof setTimeout === 'function')
      State._csMediaRetry = setTimeout(() => { State._csMediaRetry = null; cswEnsureMediaToken(); }, wait);
  });
}

// Owner media auth param: the media-scoped token, or NOTHING (vet
// 2026-09-24, fault 21). It used to fall back to the full sign-in token
// (?access_token=<session JWT>) while the short token loaded or after it
// lapsed — and addresses land in access logs, browser history and any
// photo opened in a new tab. The old token is kept through its refresh
// window (it stays valid for 5 more minutes), so a refresh never exposes
// the sign-in token either; with no media token at all, cswImgSrc paints
// a placeholder and cswMediaRepaint swaps the real address in on arrival.
function cswMediaAuthParam() {
  cswEnsureMediaToken();
  if (State.csMediaTok && Date.now() < (State.csMediaTokHardExp || State.csMediaTokExp || 0)) {
    return 'mt=' + encodeURIComponent(State.csMediaTok);
  }
  return '';
}

// A transparent 1px picture standing where a photo waits for its media
// token. The real address rides in the fragment, encoded, so the repaint
// can find it; the fragment is never sent to any server.
const CSW_MEDIA_WAIT = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7#csw-media=';
// The words on a stalled photo are set in Archivo, the house face (floor-3
// verdict, 2026-09-26). A picture drawn from a data address cannot reach the
// page's fonts, so it carries its own: Archivo (SIL OFL, © 2020 The Archivo
// Project Authors), one weight (500), cut to the 16 letters these two
// sentences use — 1.9 KB. New words need a new cut; the round-two test
// checks that every letter of both sentences is in it.
const CSW_MEDIA_FACE = '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';
// The same wait, once a mint has failed: the picture says so in words, in
// the house amber for a gap, on a dark tile so the words read in every skin.
// Still carries the real address, so a later token repaints it like any
// other waiting picture.
const CSW_MEDIA_STALLED = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="240" height="120" viewBox="0 0 240 120">'
  + '<style>@font-face{font-family:Archivo;font-weight:500;src:url(data:font/woff2;base64,' + CSW_MEDIA_FACE + ') format("woff2")}</style>'
  + '<rect x="0.5" y="0.5" width="239" height="119" rx="10" fill="#131316" stroke="#ffb347" stroke-opacity="0.45"/>'
  + '<text x="120" y="56" text-anchor="middle" font-family="Archivo,sans-serif" font-weight="500" font-size="15" fill="#ffb347">Photo still loading.</text>'
  + '<text x="120" y="79" text-anchor="middle" font-family="Archivo,sans-serif" font-weight="500" font-size="14" fill="#ffb347">Tap to try again.</text></svg>')
  + '#csw-media=';
function cswMediaWaitSrc(url) {
  State._csMediaWaiting = true;
  // One tap on a waiting picture asks for the token again (and never opens
  // the placeholder in the photo viewer). Bound once, on first need.
  if (!State._csMediaTap && typeof document !== 'undefined' && document.addEventListener) {
    State._csMediaTap = true;
    document.addEventListener('click', e => {
      const im = e.target && e.target.closest ? e.target.closest('img[src*="#csw-media="]') : null;
      if (!im) return;
      e.preventDefault(); e.stopPropagation();
      State._csMediaFails = 0;
      cswEnsureMediaToken();
    }, true);
  }
  return (State._csMediaFails ? CSW_MEDIA_STALLED : CSW_MEDIA_WAIT) + encodeURIComponent(url);
}

// A mint failed: every picture still waiting says so, in words.
function cswMediaStalled() {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('img[src*="#csw-media="]').forEach(im => {
    const v = im.getAttribute('src') || '';
    if (!v.startsWith(CSW_MEDIA_STALLED)) im.setAttribute('src', CSW_MEDIA_STALLED + v.slice(v.indexOf('#csw-media=') + 11));
  });
}

// The media token arrived: give every waiting picture, voice note and
// video its real address, in place (no transcript rebuild, no lost scroll).
function cswMediaRepaint() {
  if (!State._csMediaWaiting || typeof document === 'undefined') return;
  State._csMediaWaiting = false;
  const real = v => {
    const at = String(v || '').indexOf('#csw-media=');
    if (at < 0) return null;
    const rest = v.slice(at + 11), cut = rest.indexOf('#');
    const url = decodeURIComponent(cut < 0 ? rest : rest.slice(0, cut));
    const next = cswImgSrc(url);
    return next.startsWith('data:') ? null : next + (cut < 0 ? '' : rest.slice(cut));
  };
  const attrs = ['src', 'data-full', 'data-src', 'data-thumb', 'href'];
  document.querySelectorAll(attrs.map(a => `[${a}*="#csw-media="]`).join(', ')).forEach(el => {
    for (const attr of attrs) {
      const next = real(el.getAttribute(attr));
      if (next) el.setAttribute(attr, next);
    }
    // A voice note drawn while it waited built its player on the
    // placeholder (adversary pass, 2026-09-24): point the player at the
    // real address too, or the play key stays dead after the token lands.
    if (typeof el._cswVoiceSrc === 'function') el._cswVoiceSrc();
  });
}

function cswImgSrc(url) {
  if (!url) return '';
  // Staff PIN sessions authenticate media via the HttpOnly cookie set at
  // login (2026-07-21): the server deliberately refuses staff tokens on
  // ?access_token= (URLs land in access logs), which broke every chat photo
  // for staff. So for staff, append NOTHING — the browser sends the cookie
  // on its own. Owners carry the short media token (mt=), never the JWT.
  // (View-as impersonation keeps the old behavior; media there is best-effort.)
  const staffCookieAuth = (typeof State !== 'undefined'
    && State.isStaff && !State.impersonating);
  // WhatsApp-Cloud inbound media is served by our authenticated wa-media proxy
  // (/api/cs/wa-media?mid=…) because the Cloud API only gives a media_id. Like
  // media-proxy below, an <img>/<audio>/<video> tag can't send the
  // Authorization header, so append the short media token as a query param.
  if (/^\/api\/(?:cs\/wa-media|staff-line\/media)\b/.test(url)) {
    if (!staffCookieAuth && typeof State !== 'undefined') {
      const q = cswMediaAuthParam();
      if (q) return url + (url.indexOf('?') >= 0 ? '&' : '?') + q;
      return cswMediaWaitSrc(url);   // never the sign-in token (fault 21)
    }
    return url;
  }
  // IG/FB CDN images (lookaside/cdninstagram/fbcdn) are signed/no-CORS, so
  // they go through our media-proxy. The proxy requires auth, but a browser
  // <img>/<video>/<audio> request can't send the Authorization header — so we
  // append the short media token as a query param (never the JWT — fault 21).
  // Without this, every IG image rendered broken ("📷photo"). WhatsApp images
  // are zoko-CDN URLs served direct (no proxy), which is why only IG broke.
  if (/lookaside\.fbsbx|cdninstagram|fbcdn\.net/i.test(url)) {
    const owner = !staffCookieAuth && typeof State !== 'undefined';
    const q = owner ? cswMediaAuthParam() : '';
    if (owner && !q) return cswMediaWaitSrc(url);   // never the sign-in token (fault 21)
    return '/api/cs/media-proxy?url=' + encodeURIComponent(url)
      + (q ? '&' + q : '');
  }
  return url;
}

// One album tile — an image OR a video. Videos get a real thumbnail
// (the <video> element with preload="metadata" paints its first frame as
// the poster) plus a play affordance, so a mixed image+video album never
// shows a blank/black square. The 4th tile carries the "+N more" overlay.
function cswAlbumCell(m, idx, extra) {
  const src = cswImgSrc(m.url);
  const more = (idx === 3 && extra > 0) ? `<span class="csw-album__more">+${extra}</span>` : '';
  if (m.kind === 'video') {
    return `<button class="csw-album__c csw-album__c--vid" data-full="${esc(src)}" data-kind="video" aria-label="View video">
      <video src="${esc(src)}#t=0.1" preload="metadata" muted playsinline></video>
      <span class="csw-album__play" aria-hidden="true"></span>${more}</button>`;
  }
  // C1 round 2 (2026-09-26): the "+N" count goes AFTER the photo. The
  // progressive-media rule (cs.css ME-01) gives every album photo
  // opacity .999, which paints the photo above an earlier absolutely
  // placed sibling — so on a real desk the "+2" sat under the fourth
  // photo, invisible, and a rep had no sign the album held more. Measured
  // in the review desk: the element at the centre of the "+2" was the
  // IMG. A video tile already draws its count last.
  return `<button class="csw-album__c" data-full="${esc(src)}" data-kind="image" aria-label="View photo"><img src="${esc(src)}" loading="lazy" alt="photo"/>${more}</button>`;
}

// Render a unified media album (body.media[]) as a WhatsApp grid: up to 4
// tiles, "+N more" on the 4th, the caption ONCE beneath the whole album.
// C1 round 2 (2026-09-26): the grid draws four tiles, and the "+N" tile
// used to open only the fourth picture, so a receipt sent as the sixth
// photo of an album could not be seen on the desk at all. The grid now
// carries every tile it stands for (data-gal: address + kind, in order) and
// a tap opens the album's photos as one gallery (cswAlbumOpen), so every
// photo is reachable. The four tiles look exactly as they did.
function cswAlbumHtml(media, caption, when, inb, statusTick) {
  const cap = caption ? `<div class="csw-bub__txt csw-album__cap">${cswLinkify(caption)}</div>` : '';
  return `<div class="csw-bub ${inb ? 'in' : 'out'} csw-bub--album">
      ${cswAlbumGridHtml(media)}
      ${cap}
      <div class="csw-bub__t">${esc(when)}${statusTick || ''}</div>
    </div>`;
}

// The grid alone, the same four tiles and gallery list the album bubble
// always drew (C1 round 2c, 2026-09-27): cswAlbumHtml wraps it in the
// album bubble, and a native Instagram DM of several photos draws it
// inside its own bubble (cswMoreMediaTiles).
function cswAlbumGridHtml(media) {
  const shown = media.slice(0, 4), extra = media.length - shown.length;
  const cells = shown.map((m, idx) => cswAlbumCell(m, idx, extra)).join('');
  const gal = media.map(m => ({ u: cswImgSrc(m.url), k: m.kind === 'video' ? 'video' : 'image' }));
  return `<div class="csw-album csw-album--${shown.length}" data-gal="${esc(JSON.stringify(gal))}">${cells}</div>`;
}

// What a tap on album tile `idx` opens (C1 round 2, 2026-09-26). `gal` is
// the grid's data-gal list: every tile the album stands for, in order, not
// only the four drawn.
//   * a video tile opens that video, as it always did — the "+N" tile too
//     when it is a video (second outside pass, 2026-09-26: round 2 had
//     turned a "+N" video into the photo gallery, and that video could
//     then be played from nowhere on the desk);
//   * a photo tile opens ALL the album's photos as one gallery (prev/next,
//     "2 of 6") at the tapped photo, so a photo past the fourth tile is a
//     swipe away from any photo tile. Each photo is captioned by its tile
//     number, the same "Photo 6" the line under the album uses.
// Returns { video } | { items, index } | null (nothing to open).
function cswAlbumOpen(gal, idx) {
  const list = Array.isArray(gal) ? gal.filter(m => m && m.u) : [];
  const at = Math.max(0, idx | 0);
  const tile = list[at];
  if (tile && tile.k === 'video') return { video: tile.u };
  const photos = [];
  list.forEach((m, k) => { if (m.k !== 'video') photos.push({ url: m.u, cap: list.length > 1 ? `Photo ${k + 1}` : '', k }); });
  if (!photos.length) return null;
  let index = photos.findIndex(p => p.k >= at);
  if (index < 0) index = photos.length - 1;
  return { items: photos.map(p => ({ url: p.url, cap: p.cap })), index };
}

// ── Product-match chip (auto product-ID on customer photos) ──────────────
// Renders body.product_match (written by cs_product_match at ingest) under
// the image bubble. Always shows a STATE — matched / candidates / no match /
// skipped / error — so a rep can tell "nothing close" from "didn't run".

// Candidate thumbs are the Shopify CDN URLs captured AT INGEST — a product
// image replaced later 404s forever (live case: Silver Nordic Drop Lamp,
// 2026-07-09). Swap the broken-image glyph for the clean no-thumb block.
function cswPmThumbGone(el) {
  const s = document.createElement('span');
  s.className = 'csw-pm__thumb csw-pm__thumb--none';
  el.replaceWith(s);
}
// Inline onerror= handlers need module-scope escape hatches (4th instance
// of the dead-onclick class — client_errors logged 15 ReferenceErrors in
// 48h, 2026-07-23; the broken-thumb fallback never ran so dead <img>
// icons stayed on screen).
window.cswStoryGone = cswStoryGone;
window.cswPmThumbGone = cswPmThumbGone;

function cswPmCard(m, mid, confirmable) {
  const price = m.price_min ? `₦${Number(m.price_min).toLocaleString()}` : '';
  const state = m.status === 'ACTIVE'
    ? (m.in_stock ? '<span class="csw-pm__ok">In stock</span>'
                  : '<span class="csw-pm__warn">Sold out</span>')
    : '<span class="csw-pm__warn">Past drop</span>';
  const pct = m.similarity ? `<span class="csw-pm__pct">${Math.round(m.similarity * 100)}%</span>` : '';
  // LANE B (2026-08-21): "Hold" — the chat→counter bridge. A live, in-stock
  // piece can be held at a selling store till close; the customer gets a
  // code for the counter. Only where a hold can mean something: ACTIVE +
  // in stock (a sold-out piece gets "Notify when back" instead).
  const holdKey = (m.status === 'ACTIVE' && m.in_stock)
    ? `<button class="csw-mini csw-mini--hold" data-pm-act="hold" data-mid="${esc(mid)}" data-pid="${esc(m.product_shopify_id)}" title="Hold it at a store till close">Hold</button>` : '';
  const acts = confirmable
    ? `<button class="csw-mini" data-pm-act="confirm" data-mid="${esc(mid)}" data-pid="${esc(m.product_shopify_id)}">This one</button>`
    : `<button class="csw-mini" data-pm-act="draft" data-mid="${esc(mid)}" data-pid="${esc(m.product_shopify_id)}">✦ Draft reply</button>` +
      (!m.in_stock ? `<button class="csw-mini" data-pm-act="bis" data-mid="${esc(mid)}" data-pid="${esc(m.product_shopify_id)}">Notify when back</button>` : '') +
      holdKey;
  return `<div class="csw-pm__card">
    ${m.thumb ? `<img class="csw-pm__thumb" src="${esc(cswImgSrc(m.thumb))}" loading="lazy" alt="" onerror="cswPmThumbGone(this)"/>` : '<span class="csw-pm__thumb csw-pm__thumb--none"></span>'}
    <div class="csw-pm__meta">
      <div class="csw-pm__title">${esc(m.title || m.handle || 'Product')}</div>
      <div class="csw-pm__sub">${price} ${state} ${pct}</div>
    </div>
    <div class="csw-pm__acts">${acts}</div>
  </div>`;
}

function cswPmItemsHtml(items, mid) {
  return (items || []).map(it => {
    const ms = it.matches || [];
    if (!ms.length) return '';
    if (it.status === 'auto') return cswPmCard(ms[0], mid, false);
    // candidates — rep picks; the pick is a calibration label
    return `<div class="csw-pm__which">Which one?</div>` +
           ms.slice(0, 3).map(m => cswPmCard(m, mid, true)).join('');
  }).join('');
}

// Chip stack under an inbound bubble: product-ID chip + the styling chip
// (body.styled_looks, written by cs_styling when the matched product lives
// in a studio look). One entry point so every mount path gets both.
function cswProductChipHtml(b, mid) {
  return cswPmChipHtml(b, mid) + cswStyledChipHtml(b);
}

// "Styled in studio" — REP-FACING intelligence (spec 2026-07-14): the look
// render + companion pieces with live stock. Info only — the rep writes the
// customer message themselves; customer-facing styled sends are deferred
// until the operator green-lights the brain.
function cswStLookHtml(lk, productTitle, sendable) {
  const pieces = (lk.pieces || []).map(p => {
    const stock = p.in_stock
      ? Object.entries(p.stock_by_location || {}).map(([k, v]) => `${esc(k)} ${v}`).join(' · ')
      : 'sold out';
    const price = p.price ? `₦${Number(p.price).toLocaleString()}` : '';
    return `<div class="csw-st__piece">
      ${p.image_url ? `<img class="csw-st__pthumb" src="${esc(cswImgSrc(p.image_url))}" loading="lazy" alt="" onerror="cswPmThumbGone(this)"/>` : '<span class="csw-pm__thumb csw-pm__thumb--none"></span>'}
      <div class="csw-st__pmeta">
        <div class="csw-st__pt">${esc(p.title || 'Piece')}</div>
        <div class="csw-st__ps">${price} <span class="${p.in_stock ? 'csw-pm__ok' : 'csw-pm__warn'}">${stock}</span></div>
      </div></div>`;
  }).join('');
  // C3: sending is OPT-IN per surface, never automatic. The desk chip and
  // the @eon tip sheet stay REP-FACING ONLY (standing law 2026-07-14: those
  // surfaces answer the rep, who writes to the customer in their own
  // words). Only the try-on entry opts in — that surface already sends an
  // image, and a real studio look beats the AI render it would send instead.
  // The button carries the look ID, never the image url: the server
  // re-signs the private-bucket object at send time.
  const cap = productTitle
    ? `How we styled the ${productTitle}` : 'How we styled it';
  const send = (sendable && lk.image_url && lk.look_id)
    ? `<button class="csw-mini csw-sendlook" type="button" data-sendlook="${esc(lk.look_id)}" data-lkcap="${esc(cap)}">Send this look</button>` : '';
  return `<div class="csw-st__look">
    ${lk.image_url ? `<img class="csw-st__render" src="${esc(cswImgSrc(lk.image_url))}" data-full="${esc(cswImgSrc(lk.image_url))}" loading="lazy" alt="studio look"/>` : ''}
    <div class="csw-st__body">
      ${lk.rationale ? `<div class="csw-st__note">${esc(lk.rationale)}</div>` : ''}
      ${pieces}${send}
    </div></div>`;
}

// The 'Pairs well' strip — the ladder's rung 2 (status 'affinity'):
// proven pairings from real looks + real orders, pieces only, no fake
// look image. Quieter header than the curated chip.
function cswStPairsHtml(pieces) {
  return `<div class="csw-st__look"><div class="csw-st__body">
    ${(pieces || []).map(p => {
      const stock = Object.entries(p.stock_by_location || {}).map(([k, v]) => `${esc(k)} ${v}`).join(' · ');
      const price = p.price ? `₦${Number(p.price).toLocaleString()}` : '';
      return `<div class="csw-st__piece">
        ${p.image_url ? `<img class="csw-st__pthumb" src="${esc(cswImgSrc(p.image_url))}" loading="lazy" alt="" onerror="cswPmThumbGone(this)"/>` : '<span class="csw-pm__thumb csw-pm__thumb--none"></span>'}
        <div class="csw-st__pmeta">
          <div class="csw-st__pt">${esc(p.title || 'Piece')}</div>
          <div class="csw-st__ps">${price} <span class="csw-pm__ok">${stock}</span></div>
        </div></div>`;
    }).join('')}</div></div>`;
}

function cswStyledChipHtml(b) {
  const sl = b && b.styled_looks;
  if (!sl) return '';
  if (sl.status === 'affinity' && (sl.pieces || []).length) {
    return `<div class="csw-st">
      <div class="csw-st__h csw-st__h--quiet">◇ Pairs well · from real orders — for you</div>
      ${cswStPairsHtml(sl.pieces)}</div>`;
  }
  if (!(sl.looks || []).length) return '';
  return `<div class="csw-st">
    <div class="csw-st__h">✦ Styled in studio · for you — not auto-sent</div>
    ${sl.looks.slice(0, 2).map(l => cswStLookHtml(l, sl.product_title)).join('')}</div>`;
}

function cswPmChipHtml(b, mid) {
  const pm = b && b.product_match;
  if (!pm) return '';
  if (pm.status === 'skipped')
    return `<div class="csw-pm csw-pm--quiet">◌ ${pm.reason === 'pos_receipt' ? 'Receipt' : 'Payment proof'} — product ID skipped</div>`;
  if (pm.status === 'error' || pm.status === 'unavailable')
    return '<div class="csw-pm csw-pm--quiet">◌ Product ID didn\'t run — ask @eon</div>';
  if (pm.status === 'no_match')
    return '<div class="csw-pm csw-pm--quiet">◌ No catalog match — likely not ours</div>';
  let inner = '';
  if (pm.album) {
    inner = pm.album.map(a => cswPmItemsHtml(a.items, mid)).join('');
    if (!inner) return '<div class="csw-pm csw-pm--quiet">◌ No catalog match in album</div>';
  } else {
    inner = cswPmItemsHtml(pm.items, mid);
    if (!inner) return '<div class="csw-pm csw-pm--quiet">◌ No catalog match — likely not ours</div>';
  }
  return `<div class="csw-pm">${inner}</div>`;
}

// ── C1 · A PLAIN LINE UNDER A CUSTOMER'S PHOTO (vet 2026-09-24, plan 13) ──
// 6 of 8 waiting customers (19 Sep) had last sent a photo, voice note or
// file, and the desk showed them raw. The server already reads every
// photo at ingest — body.image_triage (what kind of picture; the amount,
// bank and time on a bank receipt) and body.product_match (which piece)
// — but the thread only drew a card for a match at 0.9 or better, so a
// receipt or a likely piece sat there as a bare picture. This line says
// what EON read, in plain words, for the REP ONLY: nothing here is sent
// to a customer, no reply changes, and it reads only what the row
// already carries (no model call). The rules are
// cs_action_context.photo_reading's:
//   * a bank-receipt picture names the amount, bank and time it SHOWS
//     and tells the rep to check the bank — a picture is never money;
//   * a card-machine receipt or a damage photo never names a piece;
//   * a piece is named only when the eye was sure (status 'auto'); a
//     guess ('needs_review') says nothing;
//   * when the card above already names the piece, the line stays quiet
//     rather than say it twice.
const CSW_PHOTO_NO_PIECE = new Set(['bank_transfer_screenshot', 'pos_receipt', 'damage_or_defect']);

// The card cswIntelChips draws inside a single photo's bubble — the same
// test it runs. The caption tests run both functions on one fixture so
// the two can never disagree about whether the piece is already named.
function cswPmInlineCard(pm) {
  const items = (pm && !pm.album && Array.isArray(pm.items)) ? pm.items : [];
  return !!(items.length === 1 && items[0] && items[0].status === 'auto'
    && (items[0].matches || [])[0] && (items[0].matches[0].similarity || 0) >= 0.9);
}

// Customer-derived text (a receipt the eye read, a file's own name) is
// trimmed, stripped of direction-override characters and clipped. It is
// escaped where it is drawn (cswCaptionHtml), never here.
function cswClipWords(v, max) {
  const t = String(v == null ? '' : v).replace(/[\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '')
    .replace(/\s+/g, ' ').trim();
  return t.length > max ? t.slice(0, max - 1).trim() + '…' : t;
}

function cswPieceWords(it) {
  const m = ((it && it.matches) || [])[0] || {};
  const title = cswClipWords(m.title, 60).replace(/^the\s+/i, '');
  if (!title) return '';
  const colour = cswClipWords(it.colour, 24).toLowerCase();
  return 'the ' + title + (colour && !title.toLowerCase().includes(colour) ? ' in ' + colour : '');
}

// C1 round 2, second outside pass (2026-09-26): the receipt's own status is
// said when it is not a plain success word, so the receipt of a pending,
// failed or reversed transfer never reads like a paid one: "Bank receipt
// says “Pending” · ₦45,000 · …". "Successful" and its kin add nothing. The
// status is the bank's word on the picture — cleaned and clipped here,
// escaped where it is drawn.
function cswReceiptWords(ocr) {
  ocr = (ocr && typeof ocr === 'object') ? ocr : {};
  const status = cswClipWords(ocr.status_text, 24);
  const worry = /\b(?:no|not|on hold|under review)\b|\b(?:fail|pend|revers|declin|unsuccess|process|cancel|queue|initiat|progress|await|reject|error|incomplete|refund)/i;
  const fine = /\b(?:success|complete|approved|paid|sent|credited|done)/i;
  const bits = [status && (worry.test(status) || !fine.test(status))
    ? `Bank receipt says “${status}”` : 'Bank receipt'];
  const amt = ocr.amount_ngn;
  if (typeof amt === 'number' || (typeof amt === 'string' && amt.trim())) {
    const n = Number(amt);
    if (isFinite(n) && n > 0) bits.push(cswNaira(n));
  }
  const bank = cswClipWords(ocr.bank, 40), when = cswClipWords(ocr.paid_at, 32);
  if (bank) bits.push(bank);
  if (when) bits.push(when);
  return bits.join(' · ') + ' — check the bank before you confirm';
}

// The plain words for ONE photo, or '' when EON read nothing worth
// saying. `noCard` = no inline card is drawn beside this photo (an
// Instagram album's photos never get one).
function cswPhotoCaption(b, noCard) {
  b = (b && typeof b === 'object') ? b : {};
  const tri = (b.image_triage && typeof b.image_triage === 'object') ? b.image_triage : {};
  const pm = (b.product_match && typeof b.product_match === 'object') ? b.product_match : null;
  // The match run records WHY it skipped a photo; when the triage row
  // itself did not land, that reason still says what the picture is.
  const cat = String(tri.category || (pm && pm.status === 'skipped' ? pm.reason : '') || '');
  if (cat === 'bank_transfer_screenshot') return cswReceiptWords(tri.ocr);
  if (CSW_PHOTO_NO_PIECE.has(cat)) return '';
  if (!pm || pm.album) return '';
  if (!noCard && cswPmInlineCard(pm)) return '';
  const sure = (Array.isArray(pm.items) ? pm.items : [])
    .filter(it => it && it.status === 'auto').map(cswPieceWords).filter(Boolean).slice(0, 2);
  return sure.length ? 'Looks like ' + sure.join(' and ') : '';
}

function cswCaptionHtml(words) {
  return words ? `<div class="csw-pm csw-pm--quiet csw-mcap">${esc(words)}</div>` : '';
}

// A run of photos folded into one grid, or an Instagram album: one line
// per photo EON read. `tiles` is the grid's own list, in the grid's order:
// a photo's body, or null for a tile that is not a photo (a video).
// C1 adversary pass (2026-09-24): lines were numbered by the photo's place
// among the PHOTOS, so in [video, tee, receipt] the receipt read "Photo 2"
// while it was the third tile, and a rep counting tiles put the amount on
// the tee. The number is now the tile's own place. The grid draws four
// tiles and a "+N" count on the fourth.
// C1 round 2 (2026-09-26): a photo past the fourth tile used to say "(not
// shown here)" because nothing could open it. The "+N" tile now opens the
// album's photos (cswAlbumOpen), so the line says where to tap.
// Second outside pass (2026-09-26): a "+N" tile that is a VIDEO plays that
// video again (round 2 had turned it into the photo gallery, and the video
// could then be played from nowhere). Photos past it are reached by opening
// any photo tile and swiping, so the line says that instead; with no photo
// among the four tiles, it says the photo is not shown here.
function cswPhotoRunCaptions(tiles, noCard) {
  const list = Array.isArray(tiles) ? tiles : [];
  return list.map((bb, k) => {
    const w = bb ? cswPhotoCaption(bb, noCard) : '';
    if (!w) return '';
    if (list.length < 2) return cswCaptionHtml(w);
    const behind = k < 4 ? ''
      : list[3] ? ` (tap +${list.length - 4} to see it)`
      : list.slice(0, 3).some(Boolean) ? ' (open any photo and swipe to it)'
      : ' (not shown here)';
    return cswCaptionHtml(`Photo ${k + 1}${behind}: ${w}`);
  }).join('');
}

// A folded run of single-photo/video messages, as the grid's tiles: the
// same `solo` test and order the painter builds the grid's media from,
// the photo's body for a photo tile, null for a video tile.
function cswFoldTiles(runRows, solo) {
  return (Array.isArray(runRows) ? runRows : [])
    .filter(rr => solo((rr && rr.body) || {}, String((rr && rr.type) || '').toLowerCase()))
    .map(rr => ((rr.body || {}).image_url ? rr.body : null));
}

// An Instagram album keeps one product_match with an `album` list keyed
// by media_index (cs_product_match.match_album). Each photo gets the
// shape cswPhotoCaption reads; a skipped photo keeps its reason (a bank
// receipt is still named one). One entry per TILE the grid draws (the same
// `m && m.url` filter the painter hands cswAlbumHtml), null for a video, so
// the line's number is the tile's.
// C1 round 2 (2026-09-26): the album run now reads a receipt photo the way
// a single photo is read, and keeps the amount, bank and time it read on
// that photo's entry (`ocr`). It rides through here, so an album's receipt
// says "Bank receipt · ₦45,000 · GTBank" like the same picture sent alone.
// An album that arrived before this keeps no amount and still says
// "Bank receipt".
function cswAlbumPhotoBodies(b) {
  const media = Array.isArray(b && b.media) ? b.media : [];
  const pm = (b && b.product_match && typeof b.product_match === 'object') ? b.product_match : {};
  const album = Array.isArray(pm.album) ? pm.album : [];
  const out = [];
  media.forEach((m, idx) => {
    if (!m || !m.url) return;
    if (m.kind !== 'image') { out.push(null); return; }
    const a = album.find(x => x && x.media_index === idx);
    if (!a) out.push({});
    else if (a.status === 'skipped') out.push({ image_triage: { category: String(a.reason || ''),
      ocr: (a.ocr && typeof a.ocr === 'object') ? a.ocr : undefined } });
    else out.push({ product_match: { status: a.status, items: a.items || [] } });
  });
  return out;
}

// ── C1 round 2c · EVERY PHOTO OF A NATIVE INSTAGRAM DM (2026-09-27) ──
// Instagram sends a DM of several photos as ONE message. EON's Instagram
// door kept the first photo only, so a bank receipt sent as the second
// photo never reached the desk. The door now keeps the rest under
// body.more_media, for the rep only; the row's own photo (image_url, the
// photo EON reads and may answer) stays first and unchanged. The row draws
// all of them as one grid inside its OWN bubble, so reply, copy, quote and
// reactions work as they did, and a tap opens every photo (cswAlbumOpen).

// The grid's tiles for a row that carries more than its own photo, in the
// order the customer sent them, or null for every other row. The row's own
// photo comes first; when its lasting copy turned out to be a video (the
// door moves it to file_url), that video is the first tile.
function cswMoreMediaTiles(b) {
  const more = (b && Array.isArray(b.more_media))
    ? b.more_media.filter(m => m && m.url) : [];
  const first = !b ? null : b.image_url ? { kind: 'image', url: b.image_url }
    : b.file_url ? { kind: 'video', url: b.file_url } : null;
  if (!more.length || !first) return null;
  return [first]
    .concat(more.map(m => ({ kind: m.kind === 'video' ? 'video' : 'image', url: m.url })));
}

// One entry per tile, in the grid's order, in the shape cswPhotoCaption
// reads: tile 1 is the row's OWN body (its image_triage and product_match,
// so photo 1 gets exactly the line it gets alone), a later photo what the
// door read on it (the receipt's amount, bank, time and status only), a
// video null.
function cswMoreMediaBodies(b) {
  const tiles = cswMoreMediaTiles(b);
  if (!tiles) return [];
  return [tiles[0].kind === 'image' ? b : null].concat(b.more_media.filter(m => m && m.url).map(m => {
    if (m.kind === 'video') return null;
    const cat = String(m.category || '');
    return cat ? { image_triage: { category: cat,
      ocr: (m.ocr && typeof m.ocr === 'object') ? m.ocr : undefined } } : {};
  }));
}

// A file's own name beats the bare word "document" on the attachment
// link. Customer-derived — cleaned here, escaped where it is drawn.
function cswFileLabel(b, ty) {
  return cswClipWords(b && b.filename, 48) || ty || 'file';
}

// ── C1 round 2 · READ A CUSTOMER'S FILE ON THE REP'S TAP (2026-09-26) ──
// Every photo is read when it arrives; a file never was, so a customer who
// paid and sent the bank's PDF receipt showed the rep only its name. A tap
// on "Read this file" asks the server (/read-file, cs_file_read) to read it
// with the same reader a photo gets, and the answer is kept on the message
// (body.file_read) so it is never paid for twice and every rep sees it.
// For the rep only: nothing is sent, nothing confirms a payment, and EON's
// replies never read it. A receipt always carries "check the bank before
// you confirm" (cswReceiptWords), because a file is never money either.

// Offer the tap only where a read can work: a PDF or a picture, or a file
// that says nothing about itself (the server checks its first bytes before
// anything is paid for). A named Word file, zip or sound gets no key.
function cswFileReadable(b) {
  const mime = String((b && b.mime_type) || '').toLowerCase().split(';')[0].trim();
  const name = String((b && b.filename) || '').toLowerCase().trim();
  if (mime === 'application/pdf' || mime.startsWith('image/')) return true;
  if (/\.(pdf|jpe?g|png|webp|heic|heif)$/.test(name)) return true;
  const saysType = (mime && mime !== 'application/octet-stream') || /\.[a-z0-9]{1,5}$/.test(name);
  return !saysType;
}

// How long a kept failure stands before the key comes back, so a later fix
// can read the file again (nothing else ever clears body.file_read). The
// server honours a kept failure for exactly as long: cs_file_read
// FAILURE_KEPT_FOR_S, held equal by a test.
const CSW_FILE_FAIL_KEPT_MS = 3 * 24 * 3600 * 1000;

// The plain words for a kept answer (body.file_read), or '' for none.
// C1 round 2 adversary pass (2026-09-26): a card-machine slip is a payment
// proof too, so it is named as one instead of "Not a bank receipt"; a file
// WhatsApp or Instagram no longer holds says what the rep can do about it.
// Second outside pass (2026-09-26): a file the reader refuses (a locked or
// damaged PDF) and a file kept at an address EON does not fetch from are
// final answers too, each with what the rep can do instead.
function cswFileReadWords(fr) {
  if (!fr || typeof fr !== 'object') return '';
  const e = String(fr.error || '');
  if (e && fr.read_at) {
    const t = Date.parse(fr.read_at);
    if (!isFinite(t) || Date.now() - t > CSW_FILE_FAIL_KEPT_MS) return '';
  }
  if (e === 'too_big') return 'Couldn’t read this file: it is too big';
  if (e === 'not_pdf_or_picture') return 'Couldn’t read this file: EON reads only PDFs and photos';
  if (e === 'gone') return 'Couldn’t read this file: it is no longer available. Ask the customer to send it again';
  if (e === 'locked_or_damaged') return 'Couldn’t read this file: it may be locked or damaged. Ask the customer for a screenshot';
  if (e === 'host_refused') return 'Couldn’t read this file here. Open it to check it yourself';
  if (e) return 'Couldn’t read this file';
  if (fr.category === 'bank_transfer_screenshot') return cswReceiptWords(fr.ocr);
  if (fr.category === 'pos_receipt') return 'Card machine receipt — check the bank before you confirm';
  if (fr.category) return 'Not a bank receipt';
  return '';
}

// Under an inbound file: the kept answer, or the key that asks for one.
function cswFileReadHtml(b, mid) {
  const words = cswFileReadWords(b && b.file_read);
  if (words) return cswCaptionHtml(words);
  if (!mid || !cswFileReadable(b)) return '';
  return `<div class="csw-fileread__w"><button class="csw-mini crd-transcribe csw-fileread" type="button" data-mid="${esc(String(mid))}">✦ Read this file</button></div>`;
}

// What the tap leaves behind: the line for an answer (a final failure is an
// answer too), or '' to offer the tap again. On the rehearsal desk every
// POST is refused before it runs, so the tap says so instead of a retry
// that can never work.
function cswFileReadAfterTap(d, err) {
  if (err) {
    return (err.status === 409 && err.body && err.body.rehearsal)
      ? cswCaptionHtml('Rehearsal: EON did not read this file') : '';
  }
  if (d && d.ok && d.file_read) return cswCaptionHtml(cswFileReadWords(d.file_read) || 'Couldn’t read this file');
  // A final answer the server just gave stands as given: the kept copy's
  // age only matters when the chat is painted again (cswFileReadWords).
  if (d && d.final) return cswCaptionHtml(cswFileReadWords({ error: String(d.error || (d.file_read && d.file_read.error) || 'unread') }));
  return '';
}

// The key's words when a tap left no answer. A read that is still running
// — the server says another tap holds the file, or the desk stopped waiting
// first — is said as such, never as a failure: the server keeps its answer
// when it lands, and the next tap shows it without paying again.
function cswFileReadRetryWords(d, err) {
  const slow = !!err && (!!err.timeout || [408, 504, 524].includes(err.status));
  if (slow || (d && d.error === 'reading')) return 'Still reading this file. Tap again in a minute';
  return 'Couldn’t read this file. Tap to try again';
}

async function cswReadFile(btn) {
  const cid = cswState().active, mid = btn && btn.dataset.mid;
  if (!cid || !mid || btn.disabled) return;
  btn.disabled = true;
  btn.innerHTML = '<span class="csw-think">Reading the file…</span>';
  let d = null, err = null;
  try {
    // A PDF read can take minutes (download, then two reader calls). The
    // desk waits 95 s, just inside the 100 s the web edge holds a request;
    // past that the key says the read is still running (above).
    d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/read-file`,
      { method: 'POST', body: JSON.stringify({ message_id: mid }), timeoutMs: 95000 });
  } catch (e) { err = e; }
  if (d && d.file_read && cswState().active === cid) {
    // The same answer the server kept, on the local row, so a repaint draws
    // the line and never the key again.
    for (const r of (cswState().transcript || [])) {
      if (String(r.id || '') === String(mid)) r.body = Object.assign({}, r.body || {}, { file_read: d.file_read });
    }
  }
  if (!btn.isConnected) return;
  const html = cswFileReadAfterTap(d, err);
  if (html) { (btn.closest('.csw-fileread__w') || btn).outerHTML = html; return; }
  btn.textContent = cswFileReadRetryWords(d, err);
  btn.disabled = false;
}

// C1 adversary pass (2026-09-24): a voice note that cannot be read on a
// second try is said plainly, never "Tap to try again". The server keeps
// a no-words hearing on the row (body.transcript_error) and never pays
// for it twice; a row with no sound attached costs nothing and says so.
// The painter shows this line in place of the read key, so the desk's
// own read on chat open (cswAutoAssist) finds nothing to tap.
function cswVoiceFinalWords(err) {
  const e = String(err || '');
  if (e === 'empty transcript') return 'EON heard no words in this voice note';
  if (e === 'message holds no audio') return 'No sound came with this voice note';
  return '';
}

// Wave 2 (§05 — operator: EON's suggestions "crowd the cs rep"): the
// transcript carries at most ONE compact confident card; everything else
// folds into a chip line that opens EON's full board in a sheet. The full
// builders (cswPmChipHtml / cswStyledChipHtml) are unchanged — they render
// the SHEET body now instead of interleaving the conversation, so zero
// intelligence is lost. Quiet no-match states leave the thread entirely.
function cswIntelChips(b, mid) {
  const pm = b && b.product_match, sl = b && b.styled_looks;
  if (!pm && !sl) return '';
  const s = cswState();
  (s._intelByMid = s._intelByMid || {})[mid] = b;
  const items = (pm && !pm.album && pm.items) || [];
  const autoTop = items.length === 1 && items[0].status === 'auto'
    && (items[0].matches || [])[0] && (items[0].matches[0].similarity || 0) >= 0.9
    ? items[0].matches[0] : null;
  const inline = autoTop ? `<div class="csw-pm">${cswPmCard(autoTop, mid, false)}</div>` : '';
  let matchN = 0;
  if (pm) {
    matchN = pm.album
      ? pm.album.reduce((n, a) => n + (a.items || []).reduce((m, it) => m + ((it.matches || []).length), 0), 0)
      : items.reduce((m, it) => m + ((it.matches || []).length), 0);
    if (autoTop) matchN -= 1;
  }
  // Wave C (operator, 2026-07-27): the board speaks ONLY when the Studio
  // has something real — a curated look for the matched product. Generic
  // match counts and pairing affinity were pure noise (13 of 15 chip
  // events in the 30h ledger had zero looks — including twice at 10:33 as
  // the Scream-pants photo arrived while 8 curated looks sat unused).
  // And once per product per thread: the same jeans photo'd three times
  // chips once. Matches-only / affinity-only messages render nothing
  // beyond the ≥0.9 auto card above.
  const curated = (sl && sl.status === 'looks' && (sl.looks || []).length) ? sl : null;
  let chip = '';
  if (curated) {
    const cid = s.active || '';
    const pid = String(curated.product_shopify_id || (curated.looks[0] || {}).anchor_shopify_id || mid);
    const seen = (s._intelChipSeen = s._intelChipSeen || {});
    const k = cid + ':' + pid;
    if (!seen[k] || seen[k] === mid) {
      seen[k] = mid;
      const pname = (curated.product_title || '').trim();
      const label = pname ? `✦ See how we styled the ${esc(pname)}` : '✦ See how we styled this';
      chip = `<div class="csw-intel"><button class="csw-intel__chip" data-intel="${esc(mid)}" type="button">${label} — EON's board</button></div>`;
    }
  }
  return inline + chip;
}

function cswIntelSheet(mid) {
  const s = cswState();
  const b = (s._intelByMid || {})[mid];
  if (!b) return;
  cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">EON's board</div><button class="csw-x" id="csw-intel-x">✕</button></div>
    <div class="csw-intel-sheet">${cswPmChipHtml(b, mid)}${cswStyledChipHtml(b)}</div>`);
  const ov = document.body.lastElementChild;
  if (!ov || !ov.classList || !ov.classList.contains('csw-overlay')) return;
  ov.querySelector('#csw-intel-x')?.addEventListener('click', () => ov.remove());
  // pm actions are per-render bindings on the transcript — the sheet body
  // needs its own pass (same handler, same optimistic states).
  ov.querySelectorAll('[data-pm-act]').forEach(bt => bt.addEventListener('click', (e) => { e.stopPropagation(); cswPmAction(bt, e); }));
}

// C3 (directive WS-C): a curated look the studio already made goes to the
// customer in ONE tap, instead of the rep generating an AI try-on.
// Posts the look ID — never the image url. 490 of 493 look images are
// Supabase SIGNED urls on a private bucket, so the stored url is a ticking
// clock; the server re-signs from the object key at send time.
async function cswSendLook(btn) {
  const cid = cswState().active;
  const lookId = btn.dataset.sendlook;
  if (!cid || !lookId || btn.dataset.sending === '1') return;
  btn.dataset.sending = '1';
  const orig = btn.textContent;
  btn.disabled = true; btn.textContent = 'Sending…';
  try {
    const r = await cswSendApi(`/api/cs/threads/${encodeURIComponent(cid)}/send-look`,
      { method: 'POST', body: JSON.stringify({ look_id: lookId, caption: btn.dataset.lkcap || '' }) });
    if (r && r.ok === false) throw new Error(r.error || 'send failed');
    btn.textContent = 'Sent';
    cswToast('Look sent');
    setTimeout(() => cswLoadTranscript(cid, true), 1400);
  } catch (e) {
    btn.dataset.sending = '';
    btn.disabled = false; btn.textContent = orig;
    cswToast(cswSayWhy(e, { send: true }), true);
  }
}

function cswBindSendLooks(root) {
  root.querySelectorAll('[data-sendlook]').forEach(b =>
    b.addEventListener('click', (e) => { e.stopPropagation(); cswSendLook(b); }));
}

async function cswPmAction(el, ev) {
  const act = el.dataset.pmAct, mid = el.dataset.mid, pid = el.dataset.pid;
  const cid = cswState().active;
  if (!cid || !mid || !pid) return;
  el.disabled = true;
  try {
    if (act === 'confirm') {
      await api(`/api/cs/threads/${encodeURIComponent(cid)}/product-match/confirm`,
        { method: 'POST', body: JSON.stringify({ message_id: mid, product_shopify_id: pid }) });
      cswToast('Confirmed — thanks, that trains the matcher');
      // L3 (vet 2026-08-30, stock-tray-paints-once-per-open): Confirm is
      // what promotes the item to 'auto' — the ONLY status the tray, the
      // dock-hold key and the order-sheet suggestion accept — yet the
      // quiet reload's signature hashes no product_match status, so
      // every one of those readers kept seeing needs_review until the
      // rep backed out and reopened (623 needs_review vs 294 auto in
      // 30d: confirm IS the majority path). Mirror the server's own flip
      // (cs_product_match.confirm sets status='auto', confirmed match
      // first) on the local row, then paint the tray from it. One truth,
      // no side channel; the 30s-poll gate at the transcript's own call
      // site stays untouched, and the tray is height-bounded by
      // construction since #2755 (nowrap + overflow-x).
      try {
        for (const r of (cswState().transcript || [])) {
          if (String(r.id || '') !== String(mid)) continue;
          const pm = (r.body || {}).product_match || {};
          (pm.items || []).forEach(it => {
            const ms = it.matches || [];
            const k = ms.findIndex(x => String(x.product_shopify_id) === String(pid));
            if (k >= 0) { if (k > 0) ms.unshift(ms.splice(k, 1)[0]); it.status = 'auto'; }
          });
        }
      } catch (_) {}
      cswLoadTranscript(cid, true);
      cswLoadStockChips(cid);
    } else if (act === 'draft') {
      el.textContent = '…';
      const d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/product-match/draft`,
        { method: 'POST', body: JSON.stringify({ product_shopify_id: pid }) });
      el.textContent = '✦ Draft reply';
      // Vet 2026-09-24 (fault 1): the draft used to land in whichever chat
      // was open when it came back — over the rep's own words, saved as
      // THAT customer's draft ("Hi Amara…" in Tobi's box). A draft for a
      // chat the rep has left is dropped; one for this chat goes through
      // the adopt step, which asks before it replaces anything typed.
      if (cswState().active !== cid) { cswToast('Draft not added — you moved to another chat.', true); el.disabled = false; return; }
      if (!d || !d.reply) { cswToast('No draft came back. Try again.', true); el.disabled = false; return; }
      cswAdoptDraft(cid, d.reply, () => cswToast(d.live ? 'Draft ready — live stock included' : 'Draft ready — sold out + closest live piece'), ev, { claim: false });
    } else if (act === 'bis') {
      await api(`/api/cs/threads/${encodeURIComponent(cid)}/product-match/backinstock`,
        { method: 'POST', body: JSON.stringify({ message_id: mid, product_shopify_id: pid }) });
      el.textContent = '✓ On the list';
      cswToast('Customer queued — pinged automatically on restock');
    } else if (act === 'hold') {
      // LANE B: the sheet does the work; the key only opens it.
      el.disabled = false;
      cswHoldSheet(pid);
      return;
    }
  } catch (e) {
    if (act === 'draft') el.textContent = '✦ Draft reply';
    cswToast(cswSayWhy(e), true); el.disabled = false; return;
  }
  el.disabled = false;
}

// ── LANE B — "Hold it at Palms": the chat→counter bridge (mig 692) ───
// CRUX takes 89.9% of its orders at a counter; a chat that ended with
// "I'll come to Palms" was invisible. The rep holds ONE piece at ONE
// selling store till close; the customer gets a code for the counter;
// the POS order that carries the code marks the hold bought. Three
// surfaces: the sheet (size + store + one act), the Stage card (the
// live hold's code + Release), the Desk room line (holds today · N ·
// bought M).

// `opts` (2026-08-29, the re-dressed desk's stock chips): {variant,
// storeName} pre-select the sheet's two choices when the rep tapped a
// chip that already named both. Omitted → the sheet behaves exactly as
// it always has; nothing about the hold itself changes.
async function cswHoldSheet(pid, opts) {
  const cid = cswState().active;
  if (!cid || !pid) return;
  if (document.getElementById('csw-holdsheet')) return;   // one sheet, one hold
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Hold it at a store</div><button class="csw-x">✕</button></div>
    <div class="csw-holdsheet" id="csw-holdsheet"><div class="csw-empty" style="padding:18px"><span class="csw-think">Reading stock…</span></div></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const box = ov.querySelector('#csw-holdsheet');
  let av, desk;
  try {
    [av, desk] = await Promise.all([
      api('/api/cs/availability?product_shopify_id=' + encodeURIComponent(pid)),
      api('/api/cs/holds/desk'),
    ]);
  } catch (e) { box.innerHTML = `<div class="csw-empty" style="padding:18px">${esc(e.message || e)}</div>`; return; }
  if (!av || av.ok === false) { box.innerHTML = `<div class="csw-empty" style="padding:18px">${esc((av && av.error) || 'No product matched')}</div>`; return; }
  const p = av.product || {};
  const ph = s => ['default title', 'one', ''].includes((s || '').trim().toLowerCase());
  const variants = (av.variants || []).filter(v => v.sellable && !ph(v.title));
  const stores = (desk && desk.stores) || [];
  if (!av.sellable) { box.innerHTML = `<div class="csw-empty" style="padding:18px">${esc(p.title || 'That piece')} isn't sellable right now — nothing to hold.</div>`; return; }
  if (!stores.length) { box.innerHTML = '<div class="csw-empty" style="padding:18px">No selling store is set up — ask the owner.</div>'; return; }
  // Where the branch ledger shows this piece: a quiet hint under each
  // store so the rep doesn't hold at a branch that has none.
  const byStore = {};
  (av.locations || []).forEach(l => { byStore[(l.location_name || '').toLowerCase()] = l; });
  const stockHint = (s, vt) => {
    const l = byStore[(s.name || '').toLowerCase()];
    if (!l) return 'no units in the branch ledger';
    if (!vt) return `${+l.total || 0} in the ledger`;
    // the branch ledger keys the FULL variant title ('Black / 2XL') —
    // compare it whole, or every two-axis product reads "not in the
    // branch ledger" at a store that holds it (Opus vet: 313/649 live
    // pairs wrong under a stripped compare)
    const want = (vt || '').trim().toLowerCase();
    const sz = (l.sizes || []).find(x => (x.size || '').trim().toLowerCase() === want);
    return sz && +sz.available > 0 ? `${+sz.available} in the ledger` : 'not in the branch ledger';
  };
  // Pre-selection from a stock chip: only ever a value the sheet's OWN
  // lists already contain, so a stale chip can never select a size the
  // product does not carry or a store the desk does not sell from.
  const want = String((opts && opts.variant) || '').trim().toLowerCase();
  const preVar = want ? (variants.find(v => String(v.title || '').trim().toLowerCase() === want
    || String(v.title || '').trim().toLowerCase().endsWith('/ ' + want)) || null) : null;
  const wantStore = String((opts && opts.storeName) || '').trim().toLowerCase();
  let preStore = wantStore ? (stores.find(x => String(x.name || '').trim().toLowerCase() === wantStore) || null) : null;
  // L5 (vet 2026-08-30, store-fact-missing-pos-chip-dead): when the caller
  // named no store, the customer's most-frequent WALK-IN store (from the
  // customer-card payload, orders.sale_channel truth — 79% of identified
  // chat customers buy only at a counter) pre-lights. Only ever a name the
  // sheet's own list contains; the other stores stay one tap away, and a
  // customer with no walk-in history changes nothing.
  if (!preStore) {
    const sp = cswState()._storePref;
    if (sp && sp.cid === cid && sp.name) {
      preStore = stores.find(x => String(x.name || '').trim().toLowerCase()
        === String(sp.name).trim().toLowerCase()) || null;
    }
  }
  let variant = preVar ? preVar.title : (variants.length === 1 ? variants[0].title : '');
  let store = preStore ? preStore.id : '';
  box.innerHTML = `
    <div class="csw-holdpiece">
      ${p.image_url ? `<img class="csw-holdpiece__img" src="${esc(cswImgSrc(p.image_url))}" alt="" loading="lazy"/>` : ''}
      <div class="csw-holdpiece__t">${esc(p.title || '')}</div>
    </div>
    ${variants.length ? `<div class="csw-holdlab">Size</div>
    <div class="csw-holdpick" id="csw-hold-vars" role="radiogroup" aria-label="Size">${
      variants.map(v => `<button class="csw-holdchip${v.title === variant ? ' on' : ''}" type="button" role="radio" aria-checked="${v.title === variant}" data-vt="${esc(v.title)}">${esc(v.title)}<i>${+v.qty || 0}</i></button>`).join('')
    }</div>` : ''}
    <div class="csw-holdlab">Store</div>
    <div class="csw-holdpick" id="csw-hold-stores" role="radiogroup" aria-label="Store">${
      stores.map(s => `<button class="csw-holdchip${s.id === store ? ' on' : ''}" type="button" role="radio" aria-checked="${s.id === store}" data-store="${esc(s.id)}">${esc(s.name)}<i data-hint="${esc(s.id)}">${esc(stockHint(s, variant))}</i></button>`).join('')
    }</div>
    <div class="csw-holderr" id="csw-hold-err" hidden></div>
    <button class="csw-holdact" id="csw-hold-go" type="button" disabled>Hold till close</button>
    <div class="csw-holdnote">One piece, one store, till the store closes. The customer gets the code on WhatsApp and says it at the counter. The order that carries the code counts the sale back to this chat.</div>`;
  const go = box.querySelector('#csw-hold-go');
  const err = box.querySelector('#csw-hold-err');
  const sync = () => {
    go.disabled = !(store && (variant || !variants.length));
    const s = stores.find(x => x.id === store);
    go.textContent = s ? `Hold at ${s.name} till ${s.close_label || 'close'}` : 'Hold till close';
    box.querySelectorAll('[data-hint]').forEach(i => { const st = stores.find(x => x.id === i.dataset.hint); if (st) i.textContent = stockHint(st, variant); });
  };
  box.querySelectorAll('#csw-hold-vars .csw-holdchip').forEach(b => b.addEventListener('click', () => {
    variant = b.dataset.vt || '';
    box.querySelectorAll('#csw-hold-vars .csw-holdchip').forEach(x => { const on = x === b; x.classList.toggle('on', on); x.setAttribute('aria-checked', String(on)); });
    sync();
  }));
  box.querySelectorAll('#csw-hold-stores .csw-holdchip').forEach(b => b.addEventListener('click', () => {
    store = b.dataset.store || '';
    box.querySelectorAll('#csw-hold-stores .csw-holdchip').forEach(x => { const on = x === b; x.classList.toggle('on', on); x.setAttribute('aria-checked', String(on)); });
    sync();
  }));
  sync();
  go.addEventListener('click', async () => {
    go.disabled = true; err.hidden = true;
    let d = null;
    try {
      d = await api('/api/cs/hold', { method: 'POST', body: JSON.stringify({
        thread_key: cid, product_shopify_id: pid, variant_title: variant || null, location_id: store }) });
    } catch (e) {
      err.textContent = String((e && e.message) || e || 'Could not hold it').slice(0, 160); err.hidden = false; go.disabled = false; return;
    }
    if (!d || !d.ok) { err.textContent = (d && (d.error || d.message)) || 'Could not hold it'; err.hidden = false; go.disabled = false; return; }
    ov.remove();
    const h = d.hold || {};
    if (d.sent) cswToast(`Held — ${h.code} sent to the customer`);
    else cswToast(`Held — ${h.code}. Card didn't send: ${String(d.send_message || d.send_error || '').slice(0, 90)}`, true);
    // L3: a hold changed the shelf — the cached availability is stale
    // now. Drop it so the next tray/sheet read fetches the real counts
    // instead of repeating the pre-hold number.
    cswState()._avail = null;
    cswHoldSync(cid, true);
    cswLoadTranscript(cid, true);
  });
}

// ── X1 · ONE NEXT STEP IN THE OPEN CHAT (CS desk vet, plan item 12) ──
// 2026-09-24. The operator: "build all fixes dont stop until you finish
// all". The vet's flagship: reps do not visit a board, so the close sits
// inside the chat. The vet found counter holds going unused and money
// notes expiring untouched while these three keys waited behind "+".
// The server (cs_next_step) names at most ONE of them for this chat, from
// server truth only — the transfer card's own status, the recorded quote,
// the piece EON matched or the crux.ng link the customer sent — and the
// browser adds no guess of its own. A failed read, a chat whose open
// service case already shows its own step, or a business enquiry (the
// work context, when the desk has one) draws nothing. A tap runs the key
// the desk already has, never a new one, and the step then steps aside
// until the chat moves. The Priority board stays the owner's view.
const CSW_NEXT_STEPS = ['send_account_details', 'hold_at_store', 'confirm_payment'];

// The card for one step, or ''. Every word from the server is escaped.
function cswNextStepHtml(step, rehearsal) {
  if (!step || !CSW_NEXT_STEPS.includes(step.key)) return '';
  let act = 'Send account details';
  let why = 'You gave a price. No bank details sent since.';
  let tip = 'Puts the bank details in your reply. You send it.';
  if (step.key === 'hold_at_store') {
    if (!step.product_id) return '';
    act = step.store_name ? `Hold it at ${step.store_name}` : 'Hold it at a store';
    why = step.title ? `They talked about coming to the store. Keep ${step.title} for them.`
      : 'They talked about coming to the store. Keep the piece for them.';
    tip = 'Pick the size and the store, then hold it.';
  } else if (step.key === 'confirm_payment') {
    const amt = Number(step.amount_ngn);
    act = 'Confirm payment';
    why = (step.amount_ngn != null && isFinite(amt) && amt > 0)
      ? `They sent a receipt. We asked for ${cswNgn(amt)}. Check the bank, then confirm.`
      : 'They sent a receipt. Check the bank, then confirm.';
    tip = 'Shows the receipt next to what we asked for.';
  }
  // While the rep types, the room caps this card (48px, 45px with the
  // screen-room fix) and hides the reason line (.csw-next__owner). The 2px
  // of room around the button at rest comes from a spacer wearing that
  // class, so it folds away too: typing, the card is the 44px button and
  // its rule, and it no longer scrolls a clipped button (Fable 5.1 vet,
  // 27 Sep). The spacer's -8px cancels the row's gap.
  return `<div class="csw-next__actions" style="align-items:center;padding:0">`
    + `<span class="csw-next__owner" aria-hidden="true" style="flex:none;width:0;min-height:48px;margin-right:-8px"></span>`
    + `<span class="csw-next__copy"><span class="csw-next__label">${rehearsal ? 'PRACTICE · NEXT STEP' : 'NEXT STEP'}</span>`
    + `<span class="csw-next__owner">${esc(why)}</span></span>`
    + `<button type="button" class="csw-next__open" data-next-step="${esc(step.key)}" title="${esc(tip)}">${esc(act)}</button></div>`;
}

// One photo match as the step reads it: its status, the confirmed piece,
// and each item's status and top piece (album photos too). A rep's
// Confirm rewrites these on the SAME row (cs_product_match.confirm_match:
// needs_review -> auto, the picked piece first), so counting matched rows
// never saw it, and the desk kept the old step (Sol 6.1 review, 1 Oct).
function cswMatchSig(pm) {
  if (!pm || typeof pm !== 'object') return '';
  const items = Array.isArray(pm.items) ? pm.items.slice() : [];
  for (const a of (Array.isArray(pm.album) ? pm.album : [])) {
    if (a && Array.isArray(a.items)) items.push(...a.items);
  }
  return [pm.status || '', pm.confirmed_product_id || '', ...items.map(it => {
    const top = it && Array.isArray(it.matches) ? it.matches[0] : null;
    return ((it && it.status) || '') + '>' + (top && top.product_shopify_id != null ? top.product_shopify_id : '');
  })].join(',');
}

// The photo-match half of the step's key: how many rows EON matched, and
// what each match now says.
function cswNextStepMatchKey(rows) {
  let matched = 0, h = 0;
  for (const r of (Array.isArray(rows) ? rows : [])) {
    const pm = r && r.body && r.body.product_match;
    if (!pm) continue;
    matched++;
    h = (h * 33 + cswHash(String(r.id || '') + '|' + cswMatchSig(pm))) >>> 0;
  }
  return matched + '.' + h;
}

// What the step depends on in the transcript: a new message, EON matching
// a photo, or a match changing in place. A status tick or a reaction does
// not ask again.
function cswNextStepKey(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const last = list.length ? list[list.length - 1] : null;
  return list.length + ':' + (last ? String(last.id || last.platform_timestamp || last.created_at || '') : '') + ':' + cswNextStepMatchKey(list);
}

function cswPaintNextStep(cid) {
  const host = document.getElementById('csw-next-step');
  if (!host) return;
  const s = cswState();
  const ns = s._nextStep;
  const svc = document.getElementById('csw-service-cases');
  // A chat with an open service case keeps that card, and the step waits
  // for the case read to answer, so the two never stand together.
  const caseQuiet = !svc || (svc.hidden && !svc.hasAttribute('aria-busy'));
  const wc = s._workContext;
  const business = !!(wc && String(wc.cid) === String(cid) && wc.sales_guidance === false);
  const step = (ns && ns.cid === cid && s.active === cid && caseQuiet) ? ns.step : null;
  const html = step && !(business && step.key !== 'confirm_payment') ? cswNextStepHtml(step, ns.rehearsal) : '';
  if (!html) { host.hidden = true; host.innerHTML = ''; if(typeof cswEvidenceChanged === 'function') cswEvidenceChanged(); return; }
  host.innerHTML = html;
  host.hidden = false;
  if(typeof cswEvidenceChanged === 'function') cswEvidenceChanged();
  host.querySelector('[data-next-step]')?.addEventListener('click', () => cswNextStepGo(cid, step));
}

// The service card answers on its own clock; repaint when it does.
function cswWatchCaseCard() {
  const svc = document.getElementById('csw-service-cases');
  if (!svc || svc._cswStepWatch || typeof MutationObserver !== 'function') return;
  svc._cswStepWatch = new MutationObserver(() => cswPaintNextStep(cswState().active));
  svc._cswStepWatch.observe(svc, { attributes: true, attributeFilter: ['hidden', 'aria-busy'] });
}

async function cswLoadNextStep(cid, rows, fresh) {
  const s = cswState();
  if (!cid || s.active !== cid || String(cid).indexOf('team:') === 0) return;
  cswWatchCaseCard();
  const key = String(cid) + '|' + cswNextStepKey(rows);
  const mk = cswNextStepMatchKey(rows);
  const was = s._nextStep && s._nextStep.cid === cid ? s._nextStep : null;
  if (!fresh && was && was.key === key) { cswPaintNextStep(cid); return; }
  // A fresh open starts blank; a moved chat keeps its step until the new
  // answer lands, so a new message never makes the card blink. A Hold step
  // names a piece, so when a photo match moved it steps aside until the
  // new answer lands: a tap in between never holds the old piece.
  const stale = !!(was && was.mk !== mk && was.step && was.step.key === 'hold_at_store');
  const mine = { key, mk, cid, readState:'loading', step: (fresh || stale) ? null : (was ? was.step : null), rehearsal: !!(was && was.rehearsal) };
  s._nextStep = mine;
  if (typeof cswEvidenceChanged === 'function') cswEvidenceChanged();
  cswPaintNextStep(cid);
  let d = null;
  try { d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/next-step`, { timeoutMs: 15000 }); }
  catch (_) { d = null; }
  if (cswState()._nextStep !== mine || cswState().active !== cid) return;   // moved on mid-read
  const step = d && d.ok === true && d.step && typeof d.step === 'object' ? d.step : null;
  mine.step = step && CSW_NEXT_STEPS.includes(step.key) ? step : null;       // a failed read draws nothing
  mine.rehearsal = !!(d && d.rehearsal === true);
  mine.readState = d?.ok === true && Object.hasOwn(d,'step') && (d.step === null || step && CSW_NEXT_STEPS.includes(step.key)) ? 'ready' : 'failed';
  if (typeof cswEvidenceChanged === 'function') cswEvidenceChanged();
  cswPaintNextStep(cid);
}

function cswNextStepGo(cid, step) {
  const s = cswState();
  if (!step || s.active !== cid) return;
  // Tapped: the step steps aside until the chat moves — a second tap on
  // the account key would paste the bank details twice. A NEW record, not
  // an edit of the old one: a read still out for this chat then fails its
  // own `!== mine` check and cannot bring the tapped step back (adversary
  // pass, 2026-09-24). Same key, so the same transcript does not re-read.
  if (s._nextStep && s._nextStep.cid === cid) s._nextStep = { ...s._nextStep, step: null };
  cswPaintNextStep(cid);
  if (step.key === 'send_account_details') { cswDockAccount(); return; }
  if (step.key === 'hold_at_store') {
    if (step.product_id) cswHoldSheet(String(step.product_id), step.store_name ? { storeName: String(step.store_name) } : undefined);
    return;
  }
  if (step.key === 'confirm_payment') cswNextStepReceipt(cid, step);
}

// Confirm payment opens the receipt card the desk already has — its
// Confirm paid key, its mismatch sentence, its mark-paid route — for the
// SAME card the server named, and brings the customer's receipt photo
// into view above it.
function cswNextStepReceipt(cid, step) {
  const t = cswState().activeThread || {};
  const rv = t.receipt_verify && typeof t.receipt_verify === 'object' ? t.receipt_verify : null;
  const base = rv && step.reference && rv.reference === step.reference ? rv : {};
  const amt = Number(step.amount_ngn);
  cswRenderReceiptCard(cid, { match: 'unknown', ...base,
    open_quote_amount: (step.amount_ngn != null && isFinite(amt) && amt > 0) ? amt
      : (base.open_quote_amount != null ? base.open_quote_amount : null),
    reference: step.reference || base.reference || null,
    source: 'transfer_intent', receipt_detected: true });
  const host = document.getElementById('csw-receipt');
  if (!host || host.hidden) return;
  const rows = cswState().transcript || [];
  for (let i = rows.length - 1; i >= 0; i--) {
    const r = rows[i] || {};
    const b = r.body || {};
    if ((r.direction || '').toLowerCase() !== 'inbound') continue;
    if (!(b.image_url || (Array.isArray(b.media) && b.media.some(m => m && m.url)))) continue;
    const row = document.querySelector(`#csw-script .csw-row[data-mid="${CSS.escape(String(r.id || ''))}"]`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'center' });
    break;
  }
  if (host.scrollIntoView) host.scrollIntoView({ block: 'nearest' });
  host.querySelector('[data-crd="receipt-confirm"]')?.focus();
}

// The Stage card: the thread's LIVE hold (code + where + till when), with
// Release as the ghost act. Settled holds (bought / expired / released)
// are not awaiting the rep, so they do not stand on the Stage — the card
// message in the transcript is their record.
async function cswHoldSync(cid, force) {
  const s = cswState();
  if (!cid || s.active !== cid) return;
  let d;
  try { d = await api('/api/cs/holds?thread_key=' + encodeURIComponent(cid)); }
  catch (e) { return; }
  if (s.active !== cid) return;      // thread switched mid-fetch
  const live = ((d && d.holds) || []).filter(h => h.status === 'held');
  s._holds = { cid, rows: (d && d.holds) || [] };
  if (!live.length) { if (cswStageState().cards.hold) cswStageSet('hold', null); return; }
  const h = live[0];
  const exp = Date.parse(h.expires_at || '');
  const till = isFinite(exp) ? new Date(exp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'close';
  const piece = `${esc(h.product_title || 'Piece')}${h.variant_title ? ` · ${esc(h.variant_title)}` : ''}`;
  const by = (h.held_by || '').split('@')[0];
  const existing = cswStageState().cards.hold;
  if (existing && existing.key === h.id && !force) return;
  cswStageSet('hold', {
    key: h.id,
    media: `<span class="csw-holdcode" aria-label="Hold code">${esc(h.code || '')}</span>`,
    name: `Held at ${esc(h.store_name || 'the store')} <span class="csw-stage__occ">· ${esc(h.code || '')}</span>`,
    sub: `${piece} · till ${esc(till)}${by ? ` · ${esc(by)}` : ''}${live.length > 1 ? ` · +${live.length - 1} more` : ''}`,
    fill: null,
    ghost: { label: 'Release', act: async () => {
      let r = null;
      try { r = await api(`/api/cs/hold/${encodeURIComponent(h.id)}/release`, { method: 'POST', body: '{}' }); }
      catch (e) { cswToast(cswSayWhy(e, { lead: 'Not released.' }), true); return; }
      if (r && r.ok) cswStageDone('hold', `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>Released — ${esc(h.code || '')} is free again.`, true);
      else cswStageDone('hold', `${esc(h.code || '')} had already settled.`, true);
      setTimeout(() => cswHoldSync(cid, true), 2700);
    } },
    sheet: null,
  });
}

// The Desk room card: today's holds and how many were bought, per store.
async function cswHoldDeskMount() {
  const room = document.getElementById('csw-deskroom');
  if (!room) return;
  // The held-bank-credits card rides the same mount and the same poll
  // (round five of #3228): one desk-room cadence, and the pinned desk
  // block above stays byte-identical to main.
  cswHeldCreditsMount();
  let d;
  try { d = await api('/api/cs/holds/desk'); } catch (e) { return; }
  if (!d || d.ok === false || !document.getElementById('csw-deskroom')) return;
  const t = d.today || {};
  const rows = (t.by_store || []).map(s =>
    `<div class="csw-holdroom__row"><span>${esc(s.store_name || '—')}</span><b class="num">${s.held}</b><span class="csw-holdroom__b">bought ${s.redeemed}</span></div>`).join('');
  const el = elFromHTML(`<div class="csw-card csw-deskcard" id="csw-holdcard">
    <div class="csw-card__t">Counter holds</div>
    <div class="csw-holdroom__n num">holds today · ${t.held || 0} · bought ${t.redeemed || 0}</div>
    ${rows || '<div class="csw-holdroom__empty">Nothing held today. Hold sits on a product chip in any chat.</div>'}
    <div class="csw-desklaw">A hold keeps one piece at one store till close. The customer says the code at the counter; the order that carries it counts the sale back to the chat.</div>
  </div>`);
  const old = document.getElementById('csw-holdcard');
  if (old) old.replaceWith(el);
  else {
    const doors = document.getElementById('csw-desk-doors');
    const anchor = doors ? doors.closest('.csw-deskcard') : null;
    if (anchor) room.insertBefore(el, anchor); else room.appendChild(el);
  }
}

// The "Held bank credits" card: bank credits held for a person (17 Sep
// 2026, round five of #3228). Each row is a transfer the bank saw and
// nobody has claimed: what landed, in plain words why it is held, and the
// two acts that end it. Placing it runs the desk's own paid path; "not
// ours" closes it. The bank's queue row is drained the moment the hold is
// recorded (round seven): the record is the copy, and this list reads the
// records.
//
// ⛔ THE ONE CONTROL THAT ENDS A HOLD BELONGS TO EVERY SEAT. The card
// mounts wherever `cswHeldCreditsHost()` finds a room: the house Desk room
// (`#csw-deskroom`), and — since round eleven — the "Held bank credits"
// room every skin reaches from the desk menu (`#csw-creditroom`). Round
// ten's first vet: this card was the ONLY control that ends a hold, and it
// lived in a room only the house theme could open, so a rep on dark, light
// or flow took a ₦52,000 transfer, saw no order placed, saw the customer's
// reminders go quiet, and had nothing to tap.
//
// ⛔ UNREADABLE IS NOT EMPTY, AND A FULL PAGE IS NOT THE WHOLE LIST. The
// server says `ok: false` when the records could not be read and
// `truncated` when the page came back full. The first version of this
// mount removed the card on both, which told the desk a hold had gone
// when nobody knew (the hosted first read of round six). Now a read that
// failed says so and keeps the last list that worked; a full page says
// more are held than shown; only a readable, complete, empty read takes
// the card down.
//
// ⛔ A CLASS NO SHEET DEFINES IS A CONTROL NOBODY CAN TAP — AND A CLASS ONE
// SKIN DEFINES IS A CONTROL ONLY THAT SKIN CAN TAP. Round eight: the first
// draft of this card invented its own names (csw-btn, csw-btn--ghost,
// csw-heldcredit) and not one of them is defined in any stylesheet this
// desk loads, so the two keys that END a held credit rendered as bare
// text. Round eight re-dressed them in the Desk room's own pair
// (.csw-deskhold__open / __dis) — every rule of which is
// [data-theme="house"]-scoped, so the same card was still bare text on
// dark, light and flow. Round eleven names only classes cs.css dresses for
// EVERY skin, in theme tokens that follow the ground the rep stands on:
//   .crd-receipt        the desk's own payment-record box — border
//                       var(--line) on var(--bg-card) (cs.css, unscoped)
//   .crd-btn--go        THE act: var(--accent) on var(--accent-text),
//                       44px, and it already carries a :disabled face for
//                       the moment a tap is in flight (cs.css, unscoped)
//   .crd-btn            the quiet key that closes it, 44px (unscoped)
//   .crd-receipt__note  the one note line, var(--ink-mute) (unscoped)
//   .csw-holdpick       the wrapping act strip, so a long customer name
//                       never squeezes the words (unscoped)
// `.csw-deskcard` stays undressed on purpose — it is the mounting hook
// every desk card finds its anchor by, not a dress.
// tests/test_quiet_desk_round_eight_held_credit_2026_09_18.py fails the
// build if this markup ever names an undressed class again, and round
// eleven's file fails it if one of those rules ever becomes skin-scoped.
//
// ⛔ THE LAST LINE NAMES ONLY THE KEYS THIS CARD DREW (round eleven's
// second read, 18 Sep 2026). The card used to end on one fixed sentence:
// 'Placing it … "not ours" closes it … their reminders are paused'. No
// key on any card says "not ours" — a row with a customer draws "Not
// theirs", a row with none draws "Already handled / not ours" — and the
// same sentence sat under cards with no key at all: a read that failed,
// a desk in rehearsal, a login that may not act. So the line is built
// from what the rows above it actually drew: "Placing it" only when a
// place key is on the card, each closing key quoted by its own label,
// and — when there is nothing to press — why not, instead of what to
// press. A customer's paused reminders are said on that customer's own
// row (its `why`), never here: a till sale has no customer to pause.
// `rehearsal` is the server's own flag; it only changes WHY the keys are
// missing. tests/test_quiet_desk_round_eleven_card_foot_2026_09_18.py
// requires every quoted label on this card to be a key on the same card.
function cswHeldCreditsHtml(items, note, canResolve, rehearsal, noticesOnly) {
  // ⛔ PRESERVE MINOR UNITS — floor 3's registered contract says so in as
  // many words ("Use one currency format and preserve minor units",
  // docs/design/quiet-contact-desk-contract.html), and this is a bank
  // transfer: it can carry kobo. Math.round showed a customer who sent
  // ₦52,000.50 the figure ₦52,001, which is more than they sent, on the
  // card where a rep decides whether to pay it out. Whole naira still
  // print whole; only a real fraction shows its kobo, and it is the same
  // rule the server writes its own lines with (squad_listen._naira).
  const money = n => {
    const v = Number(n) || 0;
    return '₦' + v.toLocaleString('en-NG', {
      minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
      maximumFractionDigits: 2 });
  };
  let placed = false;
  const shuts = [];
  const rows = items.map(it => {
    const c = it.candidate || {};
    const who = esc(c.name || 'the customer on that thread');
    // One label, used by the key AND by the last line, so the two can
    // never disagree about what the key says.
    const shut = c.thread_key ? 'Not theirs' : 'Already handled / not ours';
    // ⛔ NO PLACE KEY WHERE PLACING CANNOT WORK — AND THE KEY COMES BACK
    // THE MOMENT IT CAN. Round twelve's second read: a quote hold is
    // raised only when no unpaid order of that amount exists, so its
    // place key was refused on every press. Round thirteen answered by
    // reading `can_place` off the stored basis, which nothing ever
    // rewrites — so the key was gone for good, and the first vet of
    // round fourteen found the cost: a rep who raises the order exactly
    // as the card says comes back to one key, "Not theirs", which
    // records a real customer's money nowhere. The server now works
    // `can_place` out from the world on every read, and says the one
    // step that changes it (`next_step`) when it is false. An older
    // server that says neither falls back to the old condition.
    const canPlace = ('can_place' in it) ? !!it.can_place : !!c.thread_key;
    // The step, on the row where the rep is standing. Empty when there is
    // nothing to do but press.
    const step = it.next_step
      ? `<div class="crd-receipt__note" data-state="attention">${esc(it.next_step)}</div>` : '';
    const place = (c.thread_key && canResolve && canPlace)
      ? `<button type="button" class="crd-btn crd-btn--go" data-credit-place="${esc(it.squad_ref)}" data-thread="${esc(c.thread_key)}">This is ${who}'s — place it</button>` : '';
    const nope = canResolve
      ? `<button type="button" class="crd-btn" data-credit-nope="${esc(it.squad_ref)}">${shut}</button>` : '';
    if (place) placed = true;
    if (nope && !shuts.includes(shut)) shuts.push(shut);
    const acts = (place || nope) ? `<div class="csw-holdpick">${place}${nope}</div>` : '';
    // `why` is the reason with no instruction attached (squad_listen
    // .hold_why): the rep reading this row is already standing at the
    // keys. `words` is the wall card's full sentence and only stands in
    // for an older server that has not got `why` yet.
    // The amount wears the desk's own money face (.crd-num__v, 16px mono
    // on var(--ink)) — the same one the payment receipt card uses, because
    // this is the same question asked from the other end. The time is the
    // server's own stamp with the machine's "T" taken out of it; it is
    // never re-parsed into a Date, which is how a Lagos desk ends up
    // printing yesterday.
    const stamp = String(it.credit_at || '').slice(0, 16);
    const date = stamp.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}:\d{2})$/);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const when = esc(date && months[Number(date[2]) - 1] ? `${Number(date[3])} ${months[Number(date[2]) - 1]}, ${date[4]}` : stamp.replace('T', ', '));
    return `<div class="crd-receipt" data-ref="${esc(it.squad_ref)}"><div class="crd-receipt__nums"><div class="crd-num__v">${money(it.amount_ngn)}</div><div class="crd-num__l">landed${when ? ` ${when}` : ''}</div></div><div class="crd-receipt__head">from ${esc(it.sender || 'an unnamed sender')}</div><div class="crd-receipt__note">${esc(it.why || it.words || it.basis || '')}</div>${step}<div class="crd-receipt__note" role="status"></div>${acts}</div>`;
  }).join('');
  const acts = [];
  if (placed) acts.push('Placing it records the money, places the order if one is waiting and tells the customer');
  if (shuts.length) acts.push(shuts.map(l => `"${l}"`).join(' or ') + ' closes it');
  const foot = noticesOnly ? 'These payments were placed. Their customer updates still need checking.' : !items.length
    ? 'There is nothing to press until the list can be read again.'
    : !acts.length
      ? (rehearsal
          ? 'This desk is in rehearsal, so nothing here can be placed or closed. Each one stays held until someone ends it on the live desk.'
          : 'Your login cannot place or close these. Ask whoever runs the desk to do it.')
      : `A transfer the bank saw and nobody has claimed. ${acts.join('; ')}. Until then it stays on this desk.`;
  return `<div class="csw-card csw-deskcard csw-creditcard" id="csw-heldcredits">
    <div class="csw-card__t">Held bank credits</div>
    <p class="crd-receipt__note" data-state="attention" id="csw-heldcredits-note" role="status"${note ? '' : ' hidden'}>${esc(note || '')}</p>
    ${rows}
    <div class="crd-receipt__note">${esc(foot)}</div>
  </div>`;
}

// The one line the card says about its own read: a read that failed, or a
// page that is not the whole list. Empty text hides the line. It is found
// by id, never by class — every row wears the same note class.
function cswHeldCreditsNote(card, text) {
  if (!card) return;
  let p = card.querySelector('#csw-heldcredits-note');
  if (!p) {
    p = elFromHTML('<p class="crd-receipt__note" data-state="attention" id="csw-heldcredits-note" role="status"></p>');
    const t = card.querySelector('.csw-card__t');
    if (t) t.insertAdjacentElement('afterend', p); else card.prepend(p);
  }
  p.textContent = text || '';
  p.hidden = !text;
}

// Where this card mounts. The house Desk room and the menu's own "Held
// bank credits" room are the same card in two rooms; only one is ever on
// screen, because both are `s.mode` renders of the one body.
function cswHeldCreditsHost() {
  return document.getElementById('csw-creditroom')
      || document.getElementById('csw-deskroom');
}

// What the last read found, so the room around the card can say the truth
// when there is no card: 'holds' | 'none' | 'denied' | 'unreadable'.
let _cswCredits = { state: '', n: 0, at: 0 };

async function cswHeldCreditsMount() {
  await cswHeldCreditsPaint();
  cswCreditRoomRest();
}

async function cswHeldCreditsPaint() {
  const room = cswHeldCreditsHost();
  if (!room) return;
  const revision = room._creditRevision || 0;
  if (room._creditPending) return;
  let d = null, unreadable = false;
  try { d = await api('/api/cs/held-credits'); }
  catch (e) {
    if (e && e.status === 403) {
      // Not this desk's list. Nothing shown, nothing said, nothing removed
      // — round seven's rule, kept exactly: a list that was readable a
      // minute ago is not wrong because this read was refused. The room's
      // resting line reads this state only when there is no card at all.
      _cswCredits = { state: 'denied', n: 0, at: Date.now() };
      return;
    }
    unreadable = true;                   // the network, a timeout, a 5xx: the records could not be read
  }
  if (cswHeldCreditsHost() !== room || room._creditPending || revision !== (room._creditRevision || 0)) return;
  const old = document.getElementById('csw-heldcredits');
  if (!unreadable && (!d || d.ok === false)) unreadable = true;
  if (unreadable) {
    _cswCredits = { state: 'unreadable', n: _cswCredits.n, at: Date.now() };
    if (old) { cswHeldCreditsNote(old, 'Held bank credits could not be read just now. This list is from the last read that worked.'); return; }
    cswHeldCreditsPlace(room, null, cswHeldCreditsHtml([], 'Held bank credits could not be read just now. Any hold still stands.', false));
    return;
  }
  const items = d.items || [];
  const notices = room._creditNotes;
  if (notices) for (const [ref, n] of notices) if (!n.owed && !items.some(it => it.squad_ref === ref)) notices.delete(ref);
  const owed = !!notices && Array.from(notices.values()).some(n => n.owed);
  _cswCredits = { state: (items.length || d.truncated) ? 'holds' : 'none',
                  n: items.length, at: Date.now() };
  if (!items.length && !d.truncated && !owed) {
    const restore = old?.contains(document.activeElement);
    if (old) old.remove();
    room._creditRevision = revision + 1;
    if (restore) (document.getElementById('csw-credit-back') || document.getElementById('csw-desk-back') || document.getElementById('csw-burger'))?.focus({ preventScroll: true });
    return;   // a readable, complete "nothing held"
  }
  // "Place or close some" only where this seat has keys to do it with — a
  // rehearsing desk or a login that may not act reads the count alone.
  const note = d.truncated ? `More are held than shown here — this is the newest ${items.length}.${d.can_resolve ? ' Place or close some to see the rest.' : ''}` : '';
  cswHeldCreditsPlace(room, old, cswHeldCreditsHtml(items, note, !!d.can_resolve, !!d.rehearsal, owed && !items.length));
}

function cswHeldCreditsPlace(room, old, html) {
  const active = document.activeElement;
  const restore = old?.contains(active);
  const focusedRef = restore ? active.closest('[data-ref]')?.dataset.ref : null;
  const wasNope = !!active?.dataset?.creditNope;
  const el = elFromHTML(html);
  if (room.id === 'csw-creditroom') el.querySelector('.csw-card__t').hidden = true;
  room._creditNotes?.forEach((saved, ref) => {
    let row = Array.from(el.querySelectorAll('[data-ref]')).find(x => x.dataset.ref === ref);
    if (!row && saved.owed) { row = saved.row.cloneNode(true); el.insertBefore(row, el.lastElementChild); }
    if (row) { const out = row.querySelector('[role="status"]'); out.textContent = saved.afterRead || saved.text; out.dataset.state = 'attention'; }
  });
  el.querySelectorAll('[data-credit-place],[data-credit-nope]').forEach(b => b.addEventListener('click', async () => {
    if (b.disabled || !el.isConnected) return;
    const row = b.closest('.crd-receipt'); const out = row.querySelector('[role="status"]');
    const ref = b.dataset.creditPlace || b.dataset.creditNope;
    const body = b.dataset.creditPlace ? { outcome: 'attributed', thread: b.dataset.thread } : { outcome: 'not_ours' };
    room._creditNotes?.delete(ref);
    const remember = (text, owed = false) => {
      out.textContent = text; out.dataset.state = 'attention';
      const saved = { text, owed };
      if (owed) {
        saved.row = row.cloneNode(true);
        saved.row.querySelectorAll('.csw-holdpick, .crd-receipt__note:not([role="status"])').forEach(x => x.remove());
      }
      (room._creditNotes ||= new Map()).set(ref, saved);
    };
    const hadFocus = document.activeElement === b;
    room._creditPending = (room._creditPending || 0) + 1;
    room._creditRevision = (room._creditRevision || 0) + 1;
    const finish = () => { room._creditPending -= 1; room._creditRevision += 1; };
    row.querySelectorAll('button').forEach(x => { x.disabled = true; });
    out.dataset.state = 'pending';
    out.textContent = b.dataset.creditPlace ? 'Placing…' : 'Closing…';
    if (hadFocus) { out.tabIndex = -1; out.focus({ preventScroll: true }); }
    let r = null;
    try { r = await api(`/api/cs/held-credits/${encodeURIComponent(ref)}/resolve`, { method: 'POST', body: JSON.stringify(body) }); }
    catch (_) {
      finish(); remember('EON could not confirm this went through. Wait for the list to refresh before trying again.');
      room._creditNotes.get(ref).afterRead = 'The list has refreshed. This credit is still held; the earlier attempt could not be confirmed.';
      return; // Only a successful fresh read can offer these keys again.
    }
    if (r && r.ok) {
      out.textContent = r.status || 'Done'; out.dataset.state = 'complete';
      if (r.evidence_kept) remember('Payment placed; the customer update was not confirmed. Open this customer’s conversation, then Payment evidence → Customer update. This reminder stays here until you leave.', true);
      setTimeout(() => { finish(); if (cswHeldCreditsHost() === room) cswHeldCreditsMount(); }, r.evidence_kept ? 6000 : 1200);
    }
    else { finish(); remember((r && r.status) || 'This credit could not be placed. Check the latest list before trying again.'); row.querySelectorAll('button').forEach(x => { x.disabled = false; }); if (document.activeElement === out) b.focus({ preventScroll: true }); }
  }));
  room._creditRevision = (room._creditRevision || 0) + 1;
  if (old) { el.style.animation = 'none'; old.replaceWith(el); }
  else {
    const doors = document.getElementById('csw-desk-doors');
    const anchor = doors ? doors.closest('.csw-deskcard') : null;
    if (anchor) room.insertBefore(el, anchor); else room.appendChild(el);
  }
  if (restore) {
    const row = Array.from(el.querySelectorAll('[data-ref]')).find(x => x.dataset.ref === focusedRef);
    const key = row?.querySelector(wasNope ? '[data-credit-nope]' : '[data-credit-place]');
    const target = key || row || document.getElementById('csw-credit-back') || document.getElementById('csw-desk-back') || document.getElementById('csw-burger');
    if (target) { if (target === row) target.tabIndex = -1; target.focus({ preventScroll: true }); }
  }
}

// The menu's own room has nothing else in it, so when there is no card it
// must say which of the four things happened — never "nothing is held"
// when the truth is "this desk may not see the list" or "the read failed".
function cswCreditRoomRest() {
  const rest = document.getElementById('csw-creditrest');
  if (!rest) return;
  if (document.getElementById('csw-heldcredits')) { rest.hidden = true; return; }
  rest.hidden = false;
  rest.textContent = _cswCredits.state === 'denied'
    ? 'Held bank credits are shown on the main desk only. Ask whoever runs the desk to place this one.'
    : _cswCredits.state === 'unreadable'
      ? 'Held bank credits could not be read just now. Any hold still stands — try again in a moment.'
      : _cswCredits.state === 'none'
        ? 'No bank credit is waiting for a person right now.'
        : 'Reading the bank credits held for a person…';
}

// The count on the menu's "Held bank credits" line. A door nobody knows to
// open is a door: the number is what makes a rep open it on the day a
// transfer lands. At most ONE bounded read per menu open, and the last
// reading answers for a minute (the Relay chips' cadence). It never
// throws — a menu must not fail because a count did — and it never
// guesses: no successful read, no number.
async function cswCreditsDoorBadge(drawer) {
  try {
    const chip = drawer && drawer.querySelector('#csw-drawer-credits-c');
    if (!chip) return;
    if (!(_cswCredits.at && Date.now() - _cswCredits.at < 60000)) {
      let d = null;
      try { d = await api('/api/cs/held-credits'); }
      catch (e) {
        _cswCredits = { state: (e && e.status === 403) ? 'denied' : 'unreadable',
                        n: 0, at: Date.now() };
      }
      if (d && d.ok !== false && Array.isArray(d.items)) {
        _cswCredits = { state: (d.items.length || d.truncated) ? 'holds' : 'none',
                        n: d.items.length, at: Date.now() };
      } else if (d) {
        _cswCredits = { state: 'unreadable', n: 0, at: Date.now() };
      }
    }
    const n = _cswCredits.state === 'holds' ? _cswCredits.n : 0;
    chip.textContent = n > 99 ? '99+' : String(n);
    chip.hidden = !n;
  } catch (_) { /* the menu opens whatever the count did */ }
}

// "Money-shaped" is not a guess: it is the shift book's own verdict. The
// priority feed is already loaded on this desk, so this costs nothing and
// can never disagree with the board the rep is looking at.
const CRD_MONEY_REASONS = new Set(['money_waiting', 'payment_claim', 'paid_order_open',
                                   'catalog_order', 'stalled_buy', 'intent2_buy']);
function cswMoneyShaped(cid) {
  if (!cid) return false;
  return (_cswPrio.items || []).some(p =>
    String(p.zoko_customer_id || '') === String(cid) && CRD_MONEY_REASONS.has(p.reason_key));
}

async function cswLoadTranscript(cid, quiet) {
  const timing = eonTimingStart('transcript');
  // Seat B's vet, A6 (2026-09-23): after a rep left a chat, the delayed
  // re-reads scheduled in it (1.2 to 2.5 s after a send, a look, a voice
  // note, a mark-paid) still fetched that chat's 200 messages, only to throw
  // them away below ("superseded"), and the stale reply's "Load earlier"
  // cursor overwrote the open chat's. Only the chat on screen is read.
  if (cswState().active !== cid) { eonTimingFinish(timing, 'superseded'); return; }
  const s = cswState();
  const evidenceRead = s._evidenceReadSeq = (s._evidenceReadSeq || 0) + 1;
  const metaRead = cswThreadMetaCapture(s, cid, evidenceRead);
  // A different chat invalidates history even if its read fails. Same-chat
  // attempts leave the last accepted cursor usable until a reply replaces it.
  if (s._older?.cid !== cid) s._older = null;
  if (!quiet) s._threadEnrichment = metaRead; // The visit owns this duty, even if a quiet read wins.
  if (!quiet) { s._evidenceTranscript = {cid,state:'loading'}; if (typeof cswEvidenceChanged === 'function') cswEvidenceChanged(); }
  // Latency: fetch + paint the MESSAGES first; the customer/360 enrichment
  // loads after so the transcript never waits on the lookup.
  let tx;
  let readFailure = 'failed';
  // FETCH ONLY WHAT CHANGED (CS vet 29, 2026-09-26). Every quiet refresh
  // (the poll, each realtime nudge from ANY customer, the re-read after a
  // send) used to pull this chat's 200 newest messages again. It now sends
  // the cursor of its last read and gets back only the rows written since
  // (the server answers `delta: true`), merged by id below. Opening a chat,
  // a missing cursor (the server hands none out until migration 914 is
  // live) and a five-minute-old full read all still read in full.
  const changedSince = (quiet && s._txCursor) ? cswTxCursorFor(s, cid) : null;
  try {
    tx = await api(`/api/cs/threads/${encodeURIComponent(cid)}/messages?limit=200`
      + (changedSince ? `&changed_since=${encodeURIComponent(changedSince)}` : ''));
    if (!cswThreadMetaCurrent(s, metaRead, false)) { eonTimingFinish(timing, 'superseded'); return; }
    cswThreadMetaApply(s, metaRead, tx?.ok === true && Array.isArray(tx.rows) ? tx.thread_meta : null);
    if (!tx || (tx.ok !== undefined && tx.ok !== true) || !Array.isArray(tx.rows)) {
      readFailure = 'unavailable'; // A response arrived, but it cannot supply messages.
      throw new Error('unavailable');
    }
    if (changedSince && tx.delta === true && s._txBase?.cid !== cid) { eonTimingFinish(timing, 'superseded'); return; }
    // Cursor state for "Load earlier" (PR A cursors). A full page means older
    // history exists past the 200-cap — the wall the vet found.
    if (!(changedSince && tx.delta === true) && !(s._olderRows?.cid === cid && s._older?.cid === cid))
      s._older = { has: !!tx.has_more, oldest: tx.oldest_ts || null, cid, request:evidenceRead };
    else if (s._older?.cid === cid) s._older.request = evidenceRead;
  } catch (e) {
    if (!cswThreadMetaCurrent(s, metaRead, false)) { eonTimingFinish(timing, 'superseded'); return; }
    cswThreadMetaApply(s, metaRead, null);
    // Vet 2026-09-24 (fault 20): the pane printed "Request timed out (30s) —
    // /api/cs/threads/<customer's number>/messages". One plain sentence, and
    // only into this chat's pane — never the one the rep has moved on to.
    if (!quiet && cswState().active === cid) { const b = document.getElementById('csw-script'); if (b) b.innerHTML = `<div class="csw-empty"><div class="csw-empty__m">!</div>${esc(cswSayWhy(e, { lead: 'Messages did not load.' }))}</div>`; }
    if (cswState().active === cid && s._evidenceReadSeq === evidenceRead) {
      const last=s._evidenceTranscript;
      s._evidenceTranscript = quiet && last?.cid === cid && Array.isArray(last.rows) ? {...last,state:'stale'} : {cid,state:'failed'};
      if (typeof cswEvidenceChanged === 'function') cswEvidenceChanged();
    }
    eonTimingFinish(timing, readFailure);
    return;
  }
  if (cswState().active !== cid) { eonTimingFinish(timing, 'superseded'); return; }   // thread switched mid-fetch — don't paint stale messages
  // Oldest→newest (latest at the bottom) — sort explicitly by timestamp so
  // a null platform_timestamp can't scramble the order (don't trust reverse).
  // Older pages the rep already pulled stay in view across polls — a refresh
  // must never yank history back to the most recent 200.
  const olderRows = (s._olderRows && s._olderRows.cid === cid)
    ? (s._olderRows.rows || []) : [];
  // A page of changes lays over what this chat already shows; anything else
  // (a full read, or the server answering in full) replaces it. A page of
  // changes with nothing of this chat's to lay over (the rep moved away and
  // back while it was on its way) is never painted as the whole chat.
  const txDelta = !!changedSince && tx.delta === true;
  if (txDelta && !(s._txBase && s._txBase.cid === cid)) { eonTimingFinish(timing, 'superseded'); return; }
  let rows;
  if (txDelta) {
    rows = cswMergeTranscript(olderRows, s._txBase.rows, tx.rows || [],
      !!(s._older && s._older.cid === cid && s._older.has));
  } else {
    const _byId = new Map();
    const ts = r => String(r.platform_timestamp || r.created_at || '');
    const floor = tx.oldest_ts || '';
    // Loaded pages stay below the full read's boundary; its covered range
    // is authoritative, including omissions. A capped page can move forward:
    // retain the displaced edge only when history was explicitly loaded.
    olderRows.filter(r => !floor || ts(r) < floor).forEach(r => _byId.set(String(r.id), r));
    if (tx.has_more && floor && s._olderRows?.cid === cid && s._txBase?.cid === cid)
      s._txBase.rows.filter(r => ts(r) && ts(r) < floor && !String(r.id).startsWith('held:') && !r.body?.held)
        .forEach(r => _byId.set(String(r.id), r));
    (tx.rows || []).forEach(r => _byId.set(String(r.id), r));
    rows = [..._byId.values()].sort((a, b) =>
      String(a.platform_timestamp || a.created_at || '').localeCompare(String(b.platform_timestamp || b.created_at || '')));
  }
  // Never bring back an omitted row or an old pre-edit/pre-delete copy from
  // the older-page cache on a later full read. Displaced recent rows remain
  // only in _txBase: a subsequent uncapped full read replaces them normally.
  if (s._olderRows?.cid === cid) {
    const ids = new Set(olderRows.map(r => String(r.id)));
    s._olderRows = {cid,rows:rows.filter(r => ids.has(String(r.id)))};
  }
  // What the next quiet refresh builds on and asks from — kept before the
  // unchanged-skip below, so a refresh that changed nothing still moves on.
  s._txBase = { cid, rows };
  s._txCursor = tx.changed_cursor
    ? { cid, at: String(tx.changed_cursor),
        fullAt: txDelta ? ((s._txCursor && s._txCursor.cid === cid && s._txCursor.fullAt) || 0) : Date.now() }
    : null;
  // Latency: on a quiet (30s) poll, skip the full DOM rebuild + listener
  // re-bind when the message set is unchanged.
  // Enrichment marker: product-match / triage / transcript merges change no
  // row count and no last-id — without this term the realtime nudge from the
  // body-UPDATE trigger refetches, matches the old sig, and skips the repaint
  // (the chip would never appear until the next message).
  let enr = 0;
  for (const r of rows) {
    const bb = r.body || {};
    if (bb.product_match) enr += (bb.product_match.confirmed_product_id ? 3 : 1) + (bb.product_match.status === 'auto' ? 1 : 0);
    if (bb.styled_looks) enr += 1;   // styling chip lands async after the match
    if (bb.image_triage) enr += 1;
    if (bb.transcript) enr += 1;
  }
  // 2026-07-27 (keyed reconcile): per-row delta hash. The old sig was blind
  // to tick promotions, edits/deletes, reactions and media swaps — a quiet
  // poll skipped those repaints entirely. Now that a change reconciles only
  // the rows it touched (no full rebuild), the sig can afford to see them.
  let rh = 0;
  for (const r of rows) {
    const bb = r.body || {};
    rh = (rh * 33 + cswHash([r.id, bb.status, bb.edited || bb.is_edited, bb.deleted || bb.is_deleted || bb.revoked || r.type,
      String(bb.text || bb.caption || '').length, bb.image_url, bb.audio_url, bb.file_url, bb.template_name,
      JSON.stringify(bb.reactions || 0), JSON.stringify(bb.quote || 0),
      // Held drafts (2026-08-03): a rep on another desk can edit the words
      // or push the release out — without these terms a quiet poll would
      // match the old signature and the stale draft would sit there.
      bb.held ? 'held|' + (bb.hold_id || '') + '|' + (bb.release_at || '') : '',
      // C1 round 2c: photos 2..N of an Instagram DM land after the row (the
      // lasting addresses, then what EON read on each), and a file another
      // rep read lands on its row: a quiet poll must repaint both.
      bb.more_media ? JSON.stringify(bb.more_media) : '', bb.file_read ? String(bb.file_read.read_at || '') : '',
      // A photo's match changing in place (a confirmed piece swapped for
      // another, same status) moves no count above; without this term the
      // chat never repainted and the next step was never asked again.
      bb.product_match ? cswMatchSig(bb.product_match) : ''].join('|'))) >>> 0;
  }
  const sig = rows.length + ':' + enr + ':' + rh + ':' + olderRows.length + ':' + (rows.length ? (rows[rows.length - 1].id || rows[rows.length - 1].platform_timestamp || '') : '');
  // A reply that was on its way when a page closed is settled from what the
  // chat shows, on every read, a quiet one that changed nothing too
  // (Fable vet, 27 Sep). Only a read that came back whole: a failed one
  // carries no rows, and "not in the chat" would hand back words that went.
  const txWhole = (tx.ok === undefined || tx.ok === true) && Array.isArray(tx.rows);
  if (s._evidenceReadSeq === evidenceRead) {
    const last=s._evidenceTranscript;
    s._evidenceTranscript = !txWhole && quiet && last?.cid === cid && Array.isArray(last.rows) ? {...last,state:'stale'} : {cid,state:txWhole?'ready':'failed',rows:txWhole?rows:[],observedAt:new Date().toISOString()};
    if (typeof cswEvidenceChanged === 'function') cswEvidenceChanged();
  }
  if (txWhole && typeof cswSettleSending === 'function') cswSettleSending(cid, rows);
  // The newest read fulfils the opening visit once, before an unchanged return.
  if (s._threadEnrichment && cswThreadMetaCurrent(s, s._threadEnrichment, false, false)) {
    s._threadEnrichment = null;
    cswLoadNotes(cid);
    cswLoadLtv(cid);
    cswLoadCircle(cid);
    cswLoadPins(cid);
    cswLoadCustomerIntel(cid);
    cswLoadRescueDraft(cid);
    (async () => {
      const t = s.activeThread; let cust = null;
      if (t && t.platform_sender_id) { try { cust = await api(`/api/cs/lookup-customer?phone=${encodeURIComponent(t.platform_sender_id)}`); } catch (_) {} }
      if (cswThreadMetaCurrent(s, metaRead, false, false)) cswRender360(cust && cust.identity);
    })();
  }
  if (quiet && sig === s._txSig) { eonTimingFinish(timing, 'unchanged'); return; }
  s._txSig = sig;
  s.transcript = rows;
  s._advanceTranscriptCid = cid;
  cswPaintServiceRail();
  const body = document.getElementById('csw-script');
  if (!body) { eonTimingFinish(timing, 'unavailable'); return; }
  // Optimization: on a quiet (30s poll) refresh, don't yank the rep off
  // history — only auto-scroll to the newest if they were already near the
  // bottom. Fresh open (not quiet) always lands at the latest.
  const nearBottom = !quiet || (body.scrollHeight - body.scrollTop - body.clientHeight) < 120;
  if (!rows.length) {
    s._txKeyed = null;   // keyed reconcile: nothing to key against an empty card
    body.innerHTML = `<div class="csw-empty"><div class="csw-empty__m">○</div>
      <div class="csw-empty__t">No messages yet</div>
      <div class="csw-empty__s">Nothing in the last 30 days${(s._older && s._older.has) ? ' — older history is still there.' : '.'}</div></div>`;
    eonTimingFinish(timing, 'empty', 'csw-script');
    return;
  }
  body.classList.toggle('no-anim', !!quiet);   // only a fresh open animates — poll refresh shouldn't re-animate the whole list
  const albumConsumed = new Set();   // indices folded into a preceding image album
  // "Load earlier" (FL-01): the 200-message wall becomes a cursor.
  const olderHead = (s._older && s._older.cid === cid && s._older.has)
    ? `<button class="csw-loadold" id="csw-loadold" type="button">Load earlier messages</button>` : '';
  // Unread divider (CV-09): where the rep stopped reading. Only worth a line
  // when there is a real block to catch up on — one new message is obvious.
  const _seenAt = (s.activeThread && (s.activeThread.seen_at || s.activeThread._seen_at)) || null;
  let dividerAt = -1;
  if (_seenAt) {
    const fu = rows.findIndex(r => (r.direction || '').toLowerCase() === 'inbound'
      && String(r.platform_timestamp || r.created_at || '') > String(_seenAt));
    if (fu > -1 && (rows.length - fu) >= 4) dividerAt = fu;
  }
  // B2 — THE SEAM (2026-09-03). The unread divider above answers "is
  // there a block of new inbound worth a line?" and stays quiet under
  // four. The seam answers a different question — "what happened while
  // you were on another thread?" — and it fires on the FIRST message,
  // counts outbound too (a teammate answering is exactly the thing a rep
  // must not miss), and says how long ago. Where both would land on the
  // same row the seam wins and the divider stands down: two lines a row
  // apart saying nearly the same thing is worse than either alone.
  // computed on EVERY paint, quiet or not (verifier, 2026-09-04): the first
  // quiet poll used to rebuild the row without it and swap the seam away
  const _seam = cswSeamFacts(rows, cswLeftAt(cid), Date.now());
  if (_seam && _seam.index === dividerAt) dividerAt = -1;
  // ── Keyed per-row reconciliation (2026-07-27) ──────────────────────
  // The transcript used to rebuild ALL ~200 rows via innerHTML on every
  // change (any new row, enrichment, or poll refetch), killing mid-gesture
  // bubble swipes, destroying playing voice-note Audio elements, and
  // resetting lightbox/unfurl state. The thread LIST got keyed
  // reconciliation long ago — the transcript now gets the same: rows are
  // keyed by message id (data-mid), quiet refreshes APPEND new rows, PATCH
  // only rows whose content changed, REMOVE vanished rows, and leave every
  // untouched row's DOM node alone. cswRowChunk is the ONE row-HTML builder
  // shared by the fresh-open innerHTML paint and the reconciler — markup is
  // byte-identical to the pre-reconcile renderer; only WHEN a row renders
  // changed, never WHAT it renders.
  cswMarkRecoveries(rows);   // WS-B: which failed sends a later send recovered
  const cswRowChunk = (r, i) => {
    const _divider = i === dividerAt
      ? '<div class="csw-unreadline"><span>Unread messages</span></div>' : '';
    const _seamLine = (_seam && i === _seam.index)
      ? cswSeamHtml(_seam.n, _seam.mins) : '';
    return _divider + _seamLine + ((r, i) => {
    if (albumConsumed.has(i)) return '';   // this image was rendered inside an album above
    const inb = (r.direction || '').toLowerCase() === 'inbound';
    // Anchor every row by message id so in-thread search can scroll to it.
    const midAttr = r.id ? ` data-mid="${esc(String(r.id))}"` : '';
    const b = r.body || {};
    const deleted = b.deleted || b.is_deleted || b.revoked || r.type === 'revoked';
    const edited = !!(b.edited || b.is_edited);
    // L4 (2026-08-30): a Meta-rejected outbound must not dress like a
    // delivered one. Same .failed treatment cswSendText's local HTTP-failure
    // bubble wears (cs.css .csw-bub.failed rules) — reachable from a SERVER
    // row for the first time; cswTick prints the words ('Not delivered').
    const sendFailed = (r.direction || '').toLowerCase() !== 'inbound'
      && String(b.status || '').toLowerCase() === 'failed';
    // WS-B: the flap under a failed bubble; the faded twin under the
    // send that recovered it. Outside the bubble, inside the row, so the
    // keyed reconciler carries it with the row it belongs to.
    const flap = sendFailed ? cswFailFlapHtml(r, b) : (r._recoversFrom ? cswRecoveredFlapHtml(r) : '');
    // body.inbox_note: words for the REP that are not the customer's — an
    // unsent message, a shape Instagram would not deliver, a shared card's
    // title and price (#3659). Only this bubble reads it; body.text stays
    // what the customer typed, because every answering lane reads that.
    let txt = b.text || b.caption || b.inbox_note || '';
    // Native-style reply quote: a "↪ who: "quoted"\nreply" message renders
    // the quote as a bonded chip above the body (not an ugly inline prefix).
    let quoteHtml = '';
    const qm = txt.match(/^↪ (?:([^:\n]+): )?"([\s\S]*?)"\n([\s\S]*)$/);
    if (qm) { quoteHtml = `<div class="csw-quote"><span class="csw-quote__who">${esc(qm[1] || 'Reply')}</span><span class="csw-quote__t">${esc((qm[2] || '').slice(0, 140))}</span></div>`; txt = qm[3]; }
    // Structured quote (WA context / IG reply_to captured at ingest):
    // body.quote = {who, text, image_url} — renders the WhatsApp-style
    // quoted chip, with a thumbnail when the quoted message was a photo
    // ("when customers quote a photo you can't see it", 2026-07-20).
    const bq = b.quote;
    if (!quoteHtml && bq && typeof bq === 'object' && (bq.text || bq.image_url || bq.who)) {
      const qthumb = bq.image_url ? `<img class="csw-quote__img" src="${esc(cswImgSrc(bq.image_url))}" alt="" loading="lazy" onerror="this.remove()">` : '';
      const qtxt = String(bq.text || (bq.image_url ? 'Photo' : '')).slice(0, 140);
      quoteHtml = `<div class="csw-quote${qthumb ? ' csw-quote--media' : ''}"><div class="csw-quote__col"><span class="csw-quote__who">${esc(bq.who || 'Reply')}</span><span class="csw-quote__t">${esc(qtxt)}</span></div>${qthumb}</div>`;
    }
    // body.card_image_url: a shared card's picture (#3659). Card media, not
    // the customer's photo: only this bubble draws it; no lane reads it.
    const img = b.image_url || b.card_image_url;
    // C1 round 2c: a native Instagram DM of several photos draws every one
    // of them inside this row's own bubble; null for every other row.
    const moreTiles = Array.isArray(b.more_media) ? cswMoreMediaTiles(b) : null;
    const audio = b.audio_url;
    const fileUrl = b.file_url;            // video/document/sticker/reel
    const transcript = b.transcript;
    const ty0 = (r.type || '').toLowerCase();
    const when = cswMsgTime(r.platform_timestamp || r.created_at);   // HH:MM
    // Grouping + day dividers (WhatsApp/Telegram feel).
    const prevR = rows[i - 1];
    const dayDiv = (!prevR || cswDayKey(prevR) !== cswDayKey(r))
      ? `<div class="csw-daydiv">${esc(cswDayLabel(r))}</div>` : '';
    const grouped = !dayDiv && prevR && (prevR.direction || '').toLowerCase() === (r.direction || '').toLowerCase();
    // ── HELD REPLY (2026-08-03) ──────────────────────────────────────
    // WhatsApp will not let a message be edited once it is SENT, so EON's
    // written replies wait in a hold window instead: the draft sits in the
    // thread as a real bubble that any rep can edit, send early, or drop,
    // and goes out as written when the clock runs out. Rendered here — the
    // ONE row builder — so the keyed reconciler treats it like every other
    // row. The countdown span is left EMPTY on purpose: the 1s ticker fills
    // it from data-release-at, so the builder's markup stays byte-identical
    // between polls and the reconciler never swaps a live bubble out from
    // under the rep mid-edit.
    if (b.held === true) {
      const holdId = String(b.hold_id || '');
      const htxt = String(b.text || '');
      const heldMid = midAttr || ` data-mid="held:${esc(holdId)}"`;
      return `${dayDiv}<div class="csw-row out csw-row--held"${heldMid} data-hold-id="${esc(holdId)}" data-release-at="${esc(String(b.release_at || ''))}" style="animation-delay:${Math.min(i * 14, 280)}ms">
        <div class="csw-bub out csw-bub--held" data-who="EON">
          <div class="csw-bub__txt">${cswLinkify(htxt)}</div>
          <div class="csw-held__meta">${cswAuthorChip(r)}${b.edited ? '<span class="csw-edited">edited</span>' : ''}<span class="csw-held__count"></span></div>
          <div class="csw-held__acts">
            <button class="csw-held__btn" data-held-act="edit" type="button">Edit</button>
            <button class="csw-held__btn csw-held__btn--go" data-held-act="send" type="button">Send now</button>
            <button class="csw-held__btn csw-held__btn--drop" data-held-act="discard" type="button">Discard</button>
          </div>
        </div></div>`;
    }
    // Mixed-media album (IG 'multiple') — body.media is the unified
    // [{kind,url}] array (images + videos). Render one WhatsApp grid with
    // the caption rendered ONCE beneath, with proper video thumbnails.
    const mediaArr = Array.isArray(b.media) ? b.media.filter(m => m && m.url) : null;
    if (mediaArr && mediaArr.length && !deleted) {
      const caption = b.text || b.caption || '';
      return `${dayDiv}<div class="csw-row ${inb ? 'in' : 'out'}${grouped ? ' grouped' : ''}"${midAttr} style="animation-delay:${Math.min(i * 14, 280)}ms">
        ${cswAlbumHtml(mediaArr, caption, when, inb, !inb ? cswTick(b.status, b.status_errors) : '')}${inb ? cswPhotoRunCaptions(cswAlbumPhotoBodies(b), true) : ''}${inb ? cswIntelChips(b, r.id || '') : ''}${flap}</div>`;
    }
    // Multi-media album: fold a run of consecutive media-only messages (same
    // direction) into one grid bubble (WhatsApp). Folds images AND videos
    // together so a mixed run never breaks into single bubbles. Only the
    // first of the run renders here; the rest are marked consumed.
    const _solo = (bb, ty2) => {
      // A lone image OR video message with no caption/audio — eligible to fold.
      if (bb.text || bb.caption || bb.audio_url) return null;
      // A DM of several photos is already its own grid (C1 round 2c):
      // folding it into a neighbour's run would drop photos 2..N.
      if (Array.isArray(bb.more_media) && cswMoreMediaTiles(bb)) return null;
      if (bb.image_url) return { kind: 'image', url: bb.image_url };
      if (bb.file_url && (ty2 === 'video' || ty2 === 'ig_reel')) return { kind: 'video', url: bb.file_url };
      return null;
    };
    const selfSolo = _solo(b, ty0);
    if (selfSolo && !deleted) {
      let j = i;
      while (j + 1 < rows.length) {
        const nr = rows[j + 1], nb = nr.body || {};
        const ns = _solo(nb, (nr.type || '').toLowerCase());
        if (ns && (nr.direction || '').toLowerCase() === (r.direction || '').toLowerCase()) j++;
        else break;
      }
      if (j > i) {
        for (let k = i + 1; k <= j; k++) albumConsumed.add(k);
        const media = rows.slice(i, j + 1)
          .map(rr => _solo(rr.body || {}, (rr.type || '').toLowerCase()))
          .filter(Boolean);
        // Chips survive the fold: each consumed message carries its own
        // product_match — the 'which of these do you have?' photo run is the
        // feature's core case, so losing chips here would read as flaky.
        const foldChips = inb ? rows.slice(i, j + 1)
          .map(rr => cswIntelChips(rr.body || {}, rr.id || '')).join('') : '';
        // C1: what EON read in each photo of the run, one entry per tile
        // in the grid's order (a video is a tile with no line).
        const foldCaps = inb ? cswPhotoRunCaptions(cswFoldTiles(rows.slice(i, j + 1), _solo)) : '';
        return `${dayDiv}<div class="csw-row ${inb ? 'in' : 'out'}${grouped ? ' grouped' : ''}"${midAttr} data-evidence-mids="${esc(JSON.stringify(rows.slice(i, j + 1).map(rr => String(rr.id || ''))))}" style="animation-delay:${Math.min(i * 14, 280)}ms">
          ${cswAlbumHtml(media, '', when, inb, !inb ? cswTick(b.status, b.status_errors) : '')}${foldCaps}${foldChips}${flap}</div>`;
      }
    }
    const ty = ty0;
    const src = ty === 'comment' ? '<div class="csw-bub__src">IG comment</div>' : (['story_mention', 'story_reply', 'ig_story', 'ig_reel'].includes(ty) ? '<div class="csw-bub__src">IG story</div>' : '');
    // IG stories are EPHEMERAL — the media CDN url dies within ~24h, so a story
    // received earlier renders a broken-image glyph. Tag story media so a failed
    // load swaps to a clean "Story unavailable" placeholder (cswStoryGone).
    const isStory = ['story_mention', 'story_reply', 'ig_story'].includes(ty);
    const storyErr = isStory ? ' onerror="cswStoryGone(this)"' : '';
    const audioBlock = audio ? `
        <div class="csw-voice" data-src="${esc(cswImgSrc(audio))}">
          <button class="csw-voice__play" aria-label="Play"><span class="csw-voice__ico csw-voice__ico--play"></span></button>
          <div class="csw-voice__bar" role="slider" aria-label="Seek">
            <div class="csw-voice__wave">${CSW_WAVE.map(h => `<i style="height:${h}%"></i>`).join('')}</div>
            <div class="csw-voice__fillwrap" style="width:0%"><div class="csw-voice__wave csw-voice__wave--played">${CSW_WAVE.map(h => `<i style="height:${h}%"></i>`).join('')}</div></div>
          </div>
          <span class="csw-voice__t">0:00</span>
          <button class="csw-voice__spd" title="Speed" aria-label="Playback speed">1×</button>
        </div>
        ${transcript
          ? `<div class="csw-bub__txt csw-bub__transcript">“${esc(transcript)}”</div>`
          : cswVoiceFinalWords(b.transcript_error)
          ? cswCaptionHtml(cswVoiceFinalWords(b.transcript_error))
          : /* 10 — THE VOICE NOTE'S ONE TAP (2026-08-29). The key was a
               40px chip reading "✦ Transcribe" — a verb with no object,
               below the desk's own tap floor. It now carries the floor,
               says what it does, and on a MONEY-SHAPED thread (the shift
               book has a money card open on it) it is loud. The endpoint
               is unchanged and is never called from here: the tap is the
               call, because the call costs a model. */
            `<button class="csw-mini csw-transcribe crd-transcribe${cswMoneyShaped(cswState().active) ? ' crd-transcribe--money' : ''}"`
            + ` data-mid="${esc(r.id || '')}" data-url="${esc(audio)}">✦ Read this voice note</button>`}` : '';
    // Video / document / sticker / reel — tappable attachment, never a blank
    // bubble. Single video gets a poster (first frame via preload=metadata)
    // + a play affordance so it never shows a black square.
    let mediaBlock = '';
    // A file_url whose PATH carries a video extension plays as video no
    // matter what type Zoko labelled it — the ingest catch-all re-hosts
    // GIF/share media under type 'unknown', and a video filed as a plain
    // attachment link would still read as "not showing".
    const fileIsVideo = /\.(mp4|mov|webm|m4v)$/i.test((fileUrl || '').split('?')[0]);
    if (moreTiles) {
      // photo 1 is a video and the DM carried more: drawn above, as the grid
    } else if (fileUrl) {
      if (fileIsVideo || ty === 'video' || ty === 'ig_reel' || ty === 'story_reply' || ty === 'story_mention' || ty === 'ig_story') {
        const vsrc = cswImgSrc(fileUrl);
        mediaBlock = `<div class="csw-bub__vidwrap"><video class="csw-bub__video" controls preload="metadata" playsinline src="${esc(vsrc)}#t=0.1"${storyErr}></video><span class="csw-bub__vidplay" aria-hidden="true"></span></div>`;
      } else if (ty === 'sticker') mediaBlock = `<img class="csw-bub__img csw-bub__sticker" src="${esc(cswImgSrc(fileUrl))}" alt="sticker"/>`;
      else mediaBlock = `<a class="csw-bub__file" href="${esc(cswImgSrc(fileUrl))}" target="_blank" rel="noopener">📎 ${esc(cswFileLabel(b, ty))} · open</a>${inb ? cswFileReadHtml(b, r.id) : ''}`;
    } else if (isStory && !img && !audio && !txt && !(Array.isArray(b.media) && b.media.length)) {
      // A story with NO renderable media (e.g. a story_reply Zoko forwards with
      // an empty body, or a story whose media wasn't captured) → show the clean
      // placeholder instead of a bare empty bubble.
      mediaBlock = '<div class="csw-bub__storygone">⊘ Story unavailable</div>';
    } else if (ty === 'ephemeral' && !img && !audio && !txt) {
      // View-once photo/video: Meta never delivers the media to ANY business
      // API — an honest label beats the cryptic "[ephemeral message]".
      mediaBlock = '<div class="csw-bub__storygone">⊘ View-once — can only be opened in the Instagram app</div>';
    } else if (!img && !audio && !txt && !(Array.isArray(b.media) && b.media.length) && ty && !['text', 'comment', 'story_reply', 'story_mention', 'system', 'multiple'].includes(ty)) {
      mediaBlock = `<div class="csw-bub__txt csw-bub__unsupported">[${esc(ty)} message]</div>`;
    }
    const cap = (txt || '').replace(/"/g, '&quot;');
    const reactsHtml = deleted ? '' : cswReactsHtml(b.reactions);
    // Deleted/revoked message → WhatsApp-style "this message was deleted"
    // placeholder (no media/text). 'edited' → a small tag by the time.
    if (deleted) {
      return `${dayDiv}<div class="csw-row ${inb ? 'in' : 'out'}${grouped ? ' grouped' : ''}"${midAttr} style="animation-delay:${Math.min(i * 14, 280)}ms">
        <div class="csw-bub ${inb ? 'in' : 'out'} csw-bub--deleted">
          <div class="csw-bub__txt csw-bub__deleted">🚫 This message was deleted</div>
          <div class="csw-bub__t">${esc(when)}</div>
        </div></div>`;
    }
    const editedTag = edited ? '<span class="csw-edited">edited</span>' : '';
    // FAMILY CARD (2026-07-27): an outbound template send renders as a card
    // — static family header art + family label, receipt sends get a
    // prominent order/total line (parsed from the true copy), then the REAL
    // send-time body text, then the Wave-2 meta row. Keyed on
    // body.template_name (send-time rows carry it from the cloud mirror;
    // get_thread lifts it out of historical '[template:x]' stubs). The
    // operator's receipt read as a wall of text — a receipt should LOOK
    // like a receipt. LAW: the art is static family identity; every data
    // character comes verbatim from the stored copy (txt).
    if (!inb && txt && b.template_name && !img && !audio && !fileUrl) {
      const fam = cswTplFamily(b.template_name);
      const facts = fam.key === 'receipt' ? cswReceiptFacts(txt) : null;
      const famArt = fam.art
        ? `<div class="csw-tplcard__art"><img class="csw-tplcard__img" src="/static/wa_studio/${esc(fam.art)}" alt="" loading="lazy" decoding="async"/></div>` : '';
      return `${dayDiv}<div class="csw-row out${grouped ? ' grouped' : ''}"${midAttr} style="animation-delay:${Math.min(i * 14, 280)}ms">
        <div class="csw-bub out csw-bub--tplcard${sendFailed ? ' failed' : ''}" data-reply="${cap.slice(0, 200)}" data-who="You">
          <button class="csw-bub__reply" title="Reply" aria-label="Reply to this message">↩</button>
          ${famArt}
          <div class="csw-tplcard__fam">${esc(fam.label)}</div>
          ${facts ? `<div class="csw-tplcard__facts"><span class="csw-tplcard__total">${esc(facts.total)}</span><span class="csw-tplcard__ord">Order ${esc(facts.order)}</span></div>` : ''}
          <div class="csw-bub__txt">${cswLinkify(txt)}</div>
          <div class="csw-bub__t">${cswAuthorChip(r)}${editedTag}${esc(when)}${cswTick(b.status, b.status_errors)}</div>
        </div>${flap}</div>`;
    }
    // Template/automated message → render as a clean card (header image, body,
    // buttons-as-chips) instead of the raw "Header: … Body: … Buttons: …" text.
    // Detect by message type OR by the structured text shape (covers historical
    // rows + any echo whose type wasn't normalised). Only for non-media bubbles.
    const tplType = ['buttontemplate', 'richtemplate', 'template'].includes(ty);
    if (!img && !audio && !fileUrl && txt && (tplType || /^\s*(Header|Body|Buttons)\s*:/m.test(txt))) {
      const parsed = cswParseTemplate(txt);
      if (parsed) {
        return `${dayDiv}<div class="csw-row ${inb ? 'in' : 'out'}${grouped ? ' grouped' : ''}"${midAttr} style="animation-delay:${Math.min(i * 14, 280)}ms">
          <div class="csw-bub ${inb ? 'in' : 'out'} csw-bub--tpl${sendFailed ? ' failed' : ''}" data-reply="${cap.slice(0, 200)}" data-who="${inb ? esc((s.activeThread && s.activeThread.customer_name) || 'Customer') : 'You'}">
            <button class="csw-bub__reply" title="Reply" aria-label="Reply to this message">↩</button>${src}
            ${cswTemplateCardHtml(parsed, {})}
            <div class="csw-bub__t">${!inb ? cswAuthorChip(r) : ''}${editedTag}${esc(when)}${!inb ? cswTick(b.status, b.status_errors) : ''}</div>
          </div>${flap}</div>`;
      }
    }
    // Link preview (unfurl): tag a bubble that carries a shareable URL so the
    // post-render pass can lazily fetch + APPEND a card. Skip image bubbles
    // (the photo is already shown). cswFirstUrl filters our own media URLs.
    const unfurlUrl = (txt && !img) ? cswFirstUrl(txt) : '';
    const unfurlAttr = unfurlUrl ? ` data-csw-unfurl="${esc(unfurlUrl)}"` : '';
    return `${dayDiv}<div class="csw-row ${inb ? 'in' : 'out'}${grouped ? ' grouped' : ''}"${midAttr} style="animation-delay:${Math.min(i * 14, 280)}ms">
      <div class="csw-bub ${inb ? 'in' : 'out'}${sendFailed ? ' failed' : ''}${img || moreTiles ? ' has-img' : ''}${reactsHtml ? ' has-react' : ''}"${unfurlAttr} data-reply="${cap.slice(0, 200)}" data-who="${inb ? esc((s.activeThread && s.activeThread.customer_name) || 'Customer') : 'You'}">
        <button class="csw-bub__reply" title="Reply" aria-label="Reply to this message">↩</button>${txt ? '<button class="csw-bub__copy" title="Copy message" aria-label="Copy message"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V6a2 2 0 0 1 2-2h9"/></svg></button>' : ''}${src}${quoteHtml}
        ${moreTiles ? cswAlbumGridHtml(moreTiles) : img ? `<img class="csw-bub__img" src="${esc(cswImgSrc(img))}" data-full="${esc(cswImgSrc(img))}" data-cap="${esc(txt)}" alt="photo" loading="lazy"${storyErr}/>` : ''}
        ${inb && moreTiles ? cswPhotoRunCaptions(cswMoreMediaBodies(b)) : inb && img ? cswCaptionHtml(cswPhotoCaption(b)) : ''}
        ${inb && img ? cswIntelChips(b, r.id || '') : ''}
        ${audioBlock}${mediaBlock}
        ${txt ? `<div class="csw-bub__txt">${cswLinkify(txt)}</div>` : ''}
        <div class="csw-bub__t">${!inb ? cswAuthorChip(r) : ''}${editedTag}${esc(when)}${!inb ? cswTick(b.status, b.status_errors) : ''}</div>
        ${reactsHtml}
      </div>${flap}</div>`;
  })(r, i);
  };
  // Units: '__old' (the Load-earlier button) then one unit per message row.
  // A chunk may carry 0-3 root nodes (unread line / day divider / row —
  // album-consumed rows carry none); u.raw/u.out feed the optimistic-send
  // promotion below.
  const units = [{ k: '__old', html: olderHead }];
  rows.forEach((r, i) => {
    const bb = r.body || {};
    units.push({
      k: 'm:' + String(r.id || ('i' + i)),
      html: cswRowChunk(r, i),
      out: (r.direction || '').toLowerCase() !== 'inbound',
      raw: String(bb.text || bb.caption || ''),
      // L4: the arrival gate — only a row NEWER than the previous paint's
      // newest may animate/count (a Load-earlier prepend re-keys 100 old
      // rows as inserts; a timestamp can't be fooled by that).
      ts: String(r.platform_timestamp || r.created_at || ''),
    });
  });
  const _nroots = (h) => h ? (h.match(/<div class="csw-daydiv">|<div class="csw-unreadline">|<div class="csw-seam">|<div class="csw-row |<button class="csw-loadold"/g) || []).length : 0;
  // Comparison normalizers: the stagger delay is index-derived (a prepend
  // shifts every index — it must never count as a content change), and a
  // chunk whose ONLY delta is inside the meta row (tick promotion, author
  // chip) patches the meta span in place instead of swapping the node.
  const _normHtml = (h) => h.replace(/ style="animation-delay:\d+ms"/g, '');
  const _metaOff = (h) => h.replace(/<div class="csw-bub__t">[\s\S]*?<\/div>/g, '');
  const _mk = (h) => { const t = document.createElement('template'); t.innerHTML = h; return [...t.content.children]; };
  const _stk = (quiet && s._txKeyed && s._txKeyed.cid === cid && s._txKeyed.body === body
    && s._txKeyed.first && s._txKeyed.first.isConnected) ? s._txKeyed : null;
  // L4 (2026-08-30): motion = arrival. freshIn counts the reconciler's
  // genuinely-NEW inbound inserts (newer than the previous paint's newest
  // row) — they get .csw-row--new (the one entrance animation left on this
  // desk) and they feed the jump pill's count when the rep is scrolled up.
  let freshIn = 0;
  const prevNewest = (_stk && s._txNewest && s._txNewest.cid === cid) ? s._txNewest.ts : '';
  if (_stk) {
    // Reconcile: walk units against the keyed node map. Untouched rows are
    // never touched (swipe transforms, playing audio, open menus survive).
    const prev = _stk.map, next = new Map();
    const owned = new Set();
    prev.forEach(e => e.nodes.forEach(n => owned.add(n)));
    let cursor = null;
    const _place = (ns, before) => {
      const ref = before !== undefined ? before
        : (cursor ? cursor.nextSibling : (_stk.first.isConnected ? _stk.first : body.firstChild));
      ns.forEach(n => body.insertBefore(n, ref));
    };
    for (const u of units) {
      const old = prev.get(u.k);
      let nodes = null;
      if (old && old.nodes.every(n => n.isConnected)) {
        const a = _normHtml(old.html), b2 = _normHtml(u.html);
        if (a === b2) {
          nodes = old.nodes;   // untouched — the DOM node is preserved as-is
        } else if (old.nodes.length && _metaOff(a) === _metaOff(b2)) {
          // Meta-only delta (tick promotion): patch the meta span, not the
          // whole bubble — playing audio/gestures in this row survive too.
          const fm = [], lm = [];
          _mk(u.html).forEach(n => fm.push(...n.querySelectorAll('.csw-bub__t')));
          old.nodes.forEach(n => lm.push(...n.querySelectorAll('.csw-bub__t')));
          if (fm.length === lm.length) { lm.forEach((el, ix) => { el.innerHTML = fm[ix].innerHTML; }); nodes = old.nodes; }
        }
        if (!nodes) {          // content changed — swap ONLY this row
          const fresh = _mk(u.html);
          _place(fresh, old.nodes.length ? old.nodes[0] : undefined);
          old.nodes.forEach(n => n.remove());
          nodes = fresh;
        }
      } else {
        // (safety) a half-disconnected old entry: sweep its survivors so the
        // fresh nodes never coexist with stale ones under the same key
        if (old) old.nodes.forEach(n => { if (n.isConnected) n.remove(); });
        const fresh = _mk(u.html);
        let adopted = null, stalePending = null;
        if (u.out && u.raw && fresh.length) {
          // Optimistic-send promotion: the composer's temp bubble BECOMES this
          // server row in place — the key lands on the same DOM node, never
          // delete+recreate, so the send animation/position never restarts. A
          // temp still marked .pending means the refetch beat the send's HTTP
          // return: the server row supersedes it, drop the duplicate (exactly
          // what the old innerHTML rebuild did implicitly).
          for (const rw of body.querySelectorAll('.csw-row.out:not([data-mid])')) {
            if (owned.has(rw)) continue;
            const ob = rw.querySelector('.csw-bub');
            if (!ob || ob.classList.contains('failed') || ob.classList.contains('csw-bub--local') || ob.hasAttribute('data-lid')) continue;
            const ot = rw.querySelector('.csw-bub__txt');
            if (!ot || ot.textContent !== u.raw) continue;
            if (ob.classList.contains('pending')) { stalePending = rw; continue; }
            adopted = rw; break;
          }
        }
        if (adopted) {
          const rowEl = fresh[fresh.length - 1];   // chunk order: divider?, daydiv?, row LAST
          [...adopted.attributes].forEach(at => adopted.removeAttribute(at.name));
          [...rowEl.attributes].forEach(at => adopted.setAttribute(at.name, at.value));
          adopted.innerHTML = rowEl.innerHTML;
          fresh.slice(0, -1).forEach(n => body.insertBefore(n, adopted));
          nodes = [...fresh.slice(0, -1), adopted];
        } else {
          if (stalePending) stalePending.remove();
          _place(fresh);
          nodes = fresh;
          // L4: a genuinely-new INBOUND row (never the adopted-optimistic
          // path — that swaps a bubble the rep is already looking at, and
          // it is outbound-only anyway; never a Load-earlier prepend — the
          // ts gate above filters those). The inline stagger delay is
          // index-derived history theatre; arrival plays NOW.
          if (!old && !u.out && prevNewest && u.ts && u.ts > prevNewest) {
            freshIn++;
            fresh.forEach(n => {
              if (n.classList && n.classList.contains('csw-row')) {
                n.classList.add('csw-row--new');
                n.style.animationDelay = '0ms';
              }
            });
          }
        }
      }
      if (nodes.length) {
        // keep document order; move only when a node is genuinely out of
        // place (moves pause playing media — never move an untouched row).
        if (cursor && nodes[0].previousElementSibling !== cursor) _place(nodes);
        cursor = nodes[nodes.length - 1];
      }
      nodes.forEach(n => owned.add(n));
      next.set(u.k, { html: u.html, nodes });
    }
    // rows that vanished from the payload leave the DOM
    prev.forEach((e, k2) => { if (!next.has(k2)) e.nodes.forEach(n => { if (n.isConnected) n.remove(); }); });
    let first2 = null;
    for (const u of units) { const e2 = next.get(u.k); if (e2 && e2.nodes.length) { first2 = e2.nodes[0]; break; } }
    s._txKeyed = { cid, body, first: first2 || body.firstElementChild, map: next };
  } else {
    // Fresh open (or nothing to reconcile against): the classic one-shot
    // innerHTML paint — entrance stagger happens ONLY here (quiet polls set
    // no-anim above and take the reconcile branch).
    body.innerHTML = units.map(u => u.html).join('');
    const nm = new Map();
    let ch = body.firstElementChild;
    for (const u of units) {
      const arr = []; let n = _nroots(u.html);
      while (n-- > 0 && ch) { arr.push(ch); ch = ch.nextElementSibling; }
      nm.set(u.k, { html: u.html, nodes: arr });
    }
    s._txKeyed = { cid, body, first: body.firstElementChild, map: nm };
  }
  // Load-earlier: page with ?before=, PREPEND, and hold the scroll anchor so
  // the rep's eye doesn't jump when the older block lands.
  const _lob = document.getElementById('csw-loadold');
  if (_lob && !_lob._cswB) { _lob._cswB = 1; _lob.addEventListener('click', ev => cswLoadEarlier(cid, body, ev.currentTarget)); }
  // Land on the divider instead of the bottom when there is real unread.
  if (!quiet && dividerAt > -1) {
    setTimeout(() => body.querySelector('.csw-unreadline')?.scrollIntoView({ block: 'center' }), 30);
  }
  // B2 — park the scroll ON the seam. The whole point of the line is
  // that the rep does not have to hunt for it; landing at the bottom
  // with the seam three screens up is the same as having no seam.
  if (_seam) {
    setTimeout(() => body.querySelector('.csw-seam')?.scrollIntoView({ block: 'center' }), 30);
  }
  // Keyed reconcile keeps the DOM between paints — drop the previous poll's
  // local photo rows before the registry re-appends them (the innerHTML
  // rebuild used to clear them implicitly; without this they'd duplicate).
  body.querySelectorAll('.csw-bub--local').forEach(lb => { const lr = lb.closest('.csw-row'); if (lr) lr.remove(); });
  cswAppendLocalBubbles(cid, body);   // optimistic photos survive re-renders
  // Row bindings (2026-07-27): idempotent via a per-element _cswB flag —
  // under keyed reconciliation preserved rows keep their live listeners, so
  // this pass binds only elements it hasn't seen (never double-binds).
  // Progressive load (ME-01): the shimmer box holds the space until the
  // photo decodes, then the image fades in — the transcript used to jump
  // under the rep's thumb as each one popped in at its natural size.
  const markLoaded = (im) => {
    if (im.complete && im.naturalWidth) { im.classList.add('loaded'); return; }
    im.addEventListener('load', () => im.classList.add('loaded'), { once: true });
    im.addEventListener('error', () => im.classList.add('loaded'), { once: true });
  };
  const bindRows = (root) => {
  root.querySelectorAll('.csw-bub__img').forEach(im => {
    if (im._cswB) return; im._cswB = 1;
    markLoaded(im);
    im.addEventListener('click', () => cswLightbox(im.dataset.full, im.dataset.cap));
  });
  root.querySelectorAll('.csw-album__c img').forEach(im => { if (!im._cswB) { im._cswB = 1; markLoaded(im); } });
  root.querySelectorAll('.csw-st__render').forEach(im => { if (im._cswB) return; im._cswB = 1; im.addEventListener('click', () => cswLightbox(im.dataset.full, '')); });
  root.querySelectorAll('[data-pm-act]').forEach(bt => { if (bt._cswB) return; bt._cswB = 1; bt.addEventListener('click', (e) => { e.stopPropagation(); cswPmAction(bt, e); }); });
  root.querySelectorAll('[data-intel]').forEach(bt => { if (bt._cswB) return; bt._cswB = 1; bt.addEventListener('click', (e) => { e.stopPropagation(); cswIntelSheet(bt.dataset.intel); }); });
  root.querySelectorAll('.csw-album__c').forEach(c => { if (c._cswB) return; c._cswB = 1; c.addEventListener('click', () => {
    // C1 round 2: a tap opens the whole album's photos (cswAlbumOpen), so a
    // photo past the fourth tile is reachable; a video tile opens its video.
    const grid = c.closest('.csw-album');
    let gal = null;
    try { gal = JSON.parse((grid && grid.dataset.gal) || 'null'); } catch (_) { gal = null; }
    const cells = grid ? Array.from(grid.querySelectorAll('.csw-album__c')) : [c];
    const o = cswAlbumOpen(gal, cells.indexOf(c));
    if (!o) cswLightbox(c.dataset.full, '', c.dataset.kind === 'video');
    else if (o.video) cswLightbox(o.video, '', true);
    else if (o.items.length > 1) cswLightbox(o.items[o.index].url, o.items[o.index].cap, false, o);
    else cswLightbox(o.items[0].url, o.items[0].cap, false);
  }); });
  // Quoted-photo thumbs pop too (operator 2026-07-25: "make images pop
  // up when clicked" — every photo class in the thread, no exceptions).
  root.querySelectorAll('.csw-quote__img').forEach(im => { if (im._cswB) return; im._cswB = 1; im.addEventListener('click', (e) => { e.stopPropagation(); cswLightbox(im.src, 'Quoted photo'); }); });
  // Single video: hide the play overlay once it starts (browser shows its
  // own controls/scrubber thereafter).
  root.querySelectorAll('.csw-bub__vidwrap').forEach(w => {
    if (w._cswB) return; w._cswB = 1;
    const v = w.querySelector('video');
    v?.addEventListener('play', () => w.classList.add('playing'));
  });
  // Held draft: Edit · Send now · Discard, bound once per button.
  root.querySelectorAll('[data-held-act]').forEach(bt => {
    if (bt._cswB) return; bt._cswB = 1;
    bt.addEventListener('click', (e) => { e.stopPropagation(); cswHeldAct(bt); });
  });
  // Tap-to-reply (the ↩ affordance) + swipe-to-reply (drag a bubble right).
  root.querySelectorAll('.csw-bub').forEach(bub => {
    if (bub._cswB) return; bub._cswB = 1;
    // A held draft has not been sent to anyone yet — there is nothing to
    // reply to, pin or react to, and its own three buttons ARE the menu.
    if (bub.classList.contains('csw-bub--held')) return;
    const reply = () => cswSetReply(bub.dataset.reply, bub.dataset.who, cswBubMid(bub));
    bub.querySelector('.csw-bub__reply')?.addEventListener('click', (e) => { e.stopPropagation(); reply(); });
    // The copy icon (operator ask, 2026-08-28): visible on every text
    // bubble — the long-press menu's Copy existed but nobody could see
    // it. Copies the FULL text (data-reply is capped at 200 chars).
    bub.querySelector('.csw-bub__copy')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      const ok = await eonCopyToClipboard(cswBubFullText(bub));
      cswToast(ok ? 'Copied' : 'Could not copy', !ok);
    });
    let x0 = null, lp = null;
    bub.addEventListener('touchstart', e => { x0 = e.touches[0].clientX; lp = setTimeout(() => { lp = null; cswBubbleActions(bub); }, 480); }, { passive: true });
    bub.addEventListener('touchmove', e => {
      if (x0 == null) return;
      const dx = e.touches[0].clientX - x0;
      if (Math.abs(dx) > 6 && lp) { clearTimeout(lp); lp = null; }   // it's a swipe, not a long-press
      if (dx > 0) bub.style.transform = `translateX(${Math.min(dx, 56)}px)`;
    }, { passive: true });
    bub.addEventListener('touchend', e => {
      if (lp) { clearTimeout(lp); lp = null; }
      const dx = (e.changedTouches[0].clientX - (x0 || 0));
      bub.style.transform = '';
      if (dx > 44) reply();
      x0 = null;
    });
    bub.addEventListener('contextmenu', (e) => { e.preventDefault(); cswBubbleActions(bub); });   // desktop right-click
  });
  // C1 round 2: "Read this file" under a customer's file (cswReadFile).
  root.querySelectorAll('.csw-fileread').forEach(btn => { if (btn._cswB) return; btn._cswB = 1; btn.addEventListener('click', () => cswReadFile(btn)); });
  root.querySelectorAll('.csw-transcribe').forEach(btn => { if (btn._cswB) return; btn._cswB = 1; btn.addEventListener('click', async () => {
    btn.innerHTML = '<span class="csw-think">Reading the voice note…</span>'; btn.disabled = true;
    // C1 (2026-09-24): a WhatsApp voice note's data-url is the proxy
    // path, not an http address; the server now reads the row's own
    // audio from the message id, so this tap works on the main line.
    const _vFail = 'Couldn’t read it. Tap to try again';
    try {
      const d = await api('/api/cs/transcribe', { method: 'POST', body: JSON.stringify({ url: btn.dataset.url, message_id: btn.dataset.mid }), timeoutMs: 30000 });
      if (d && d.ok && d.text) btn.outerHTML = `<div class="csw-bub__txt csw-bub__transcript">“${esc(d.text)}”</div>`;
      else if (d && d.final) btn.outerHTML = cswCaptionHtml(cswVoiceFinalWords(d.error) || 'EON couldn’t read this voice note');
      else { btn.textContent = _vFail; btn.disabled = false; }
    } catch (e) { btn.textContent = _vFail; btn.disabled = false; }
  }); });
  };
  bindRows(body);
  cswBindVoice(body);
  cswBindStickyDate(body);   // pinned day chip (CV-04)
  cswBindVideos(body);       // §07 — duration badge + locked aspect
  cswAutoAssist(body);       // Wave 3 — pre-draft · auto-transcribe · quote
  // M5 — media that decodes after the bottom-anchor grew the content under
  // the thumb: if the rep was pinned to the bottom, stay pinned. Capture
  // phase — image load events don't bubble.
  if (!body._repinBound) {
    body._repinBound = true;
    body.addEventListener('load', (e) => {
      if (!(e.target instanceof HTMLImageElement)) return;
      if (body.scrollHeight - body.scrollTop - body.clientHeight < 80) body.scrollTop = body.scrollHeight;
    }, true);
  }
  // M4 — the scroll-FAB rides the composer's REAL height (macros strip,
  // reply row, grown textarea) via --csw-comp-h on the conv pane.
  const compEl = document.querySelector('.csw-composer'), convEl = document.querySelector('.csw-conv');
  if (compEl && convEl && !compEl._roBound && typeof ResizeObserver === 'function') {
    compEl._roBound = true;
    const bandEl = convEl.querySelector('.csw-bands');
    const ro = new ResizeObserver(() => {
      convEl.style.setProperty('--csw-comp-h', compEl.offsetHeight + 'px');
      convEl.style.setProperty('--csw-bands-h', (bandEl?.offsetHeight || 0) + 'px');
      cswPositionToasts();
    });
    ro.observe(compEl);
    if (bandEl) ro.observe(bandEl);
  }
  cswUnfurlScan(body);   // ADDITIVE: lazily append link-preview cards under URL bubbles
  cswHeldTimerSync();    // one 1s clock for every held draft on screen
  // THE DESK, RE-DRESSED — the two faces that need the MESSAGES to exist
  // before they can answer: the receipt reading (gated on the thread
  // actually holding an inbound image) and the per-size shelf chips
  // (gated on a matched piece). Fresh open only — never on a 30s poll.
  if (!quiet) {
    cswLoadReceiptCard(cid);
    cswLoadStockChips(cid);
  }
  // X1 (2026-09-24): the chat's one next step — read on open, and again
  // only when a message or a photo match moved the chat (cswNextStepKey).
  cswLoadNextStep(cid, rows, !quiet);
  if (nearBottom) { if (quiet) body.scrollTo({ top: body.scrollHeight, behavior: 'smooth' }); else body.scrollTop = body.scrollHeight; }
  // L4 (2026-08-30): the pill's truth is recomputed at the end of EVERY
  // paint — an appended row fires no scroll event, so the scroll-only sync
  // froze the pill at whatever the rep's last scroll left it. nearBottom is
  // passed explicitly: the smooth auto-scroll above is async, so reading
  // scrollTop here would still see the old position and flash the pill.
  if (!quiet) s._newBelow = 0;
  else if (freshIn && !nearBottom) s._newBelow = (s._newBelow || 0) + freshIn;
  s._txNewest = { cid, ts: rows.length ? String(rows[rows.length - 1].platform_timestamp || rows[rows.length - 1].created_at || '') : '' };
  cswSyncScrollPill(nearBottom);
  if (!quiet) cswLoadIntent(cid);   // classify on open only — not on every poll (Haiku cost/latency)
  eonTimingFinish(timing, 'ready', 'csw-script');
}

// FETCH ONLY WHAT CHANGED (CS vet 29, 2026-09-26) — the cursor a quiet
// refresh may send: only for the chat whose rows this desk holds, and only
// within five minutes of its last full read. The full read still comes round
// because it is the one that notices a message removed at the source.
async function cswLoadEarlier(cid, body, btn) {
  const st = cswState(), cursor = st._older;
  if (st.active !== cid || cursor?.cid !== cid || !cursor.oldest) return;
  const before = cursor.oldest;
  const request = st._olderRequest = (st._olderRequest || 0) + 1;
  const current = () => cswState() === st && st.active === cid && st._older === cursor
    && st._olderRequest === request;
  btn.disabled = true; btn.textContent = 'Loading…';
  try {
    const older = await api(`/api/cs/threads/${encodeURIComponent(cid)}/messages?limit=100&before=${encodeURIComponent(before)}`);
    if (!current()) return;
    if (!older || (older.ok !== undefined && older.ok !== true) || !Array.isArray(older.rows)) throw new Error('Earlier messages unavailable');
    const prevH = body.scrollHeight, prevT = body.scrollTop;
    const keep = st._olderRows?.cid === cid ? st._olderRows.rows : [];
    const byId = new Map([...older.rows,...keep].map(r => [String(r.id),r]));
    st._olderRows = {cid,rows:[...byId.values()]};
    st._older = {has:!!older.has_more,oldest:older.oldest_ts || null,cid,request:cursor.request};
    st._txSig = null;
    await cswLoadTranscript(cid, true);
    if (cswState() === st && st.active === cid && st._olderRequest === request)
      body.scrollTop = prevT + (body.scrollHeight - prevH);
  } catch (_) {
    if (current()) cswToast('Could not load earlier messages. Please retry.', true);
  } finally {
    if (cswState() === st && st.active === cid && st._olderRequest === request) {
      btn.disabled = false; btn.textContent = 'Load earlier messages';
    }
  }
}

function cswTxCursorFor(s, cid) {
  const c = s && s._txCursor, b = s && s._txBase;
  if (!c || !b || !c.at || String(c.cid) !== String(cid) || String(b.cid) !== String(cid)) return null;
  if (!(Date.now() - (Number(c.fullAt) || 0) < 5 * 60 * 1000)) return null;
  return c.at;
}

// The rows a page of changes leaves on screen, oldest first, one per
// message id: the pages the rep loaded, then what the chat already shows
// (`base`), then the changed rows laid over it by id. Three rules:
//  - EON's held replies come from this read alone (the server sends every
//    live one each time), so one that went out or was dropped leaves;
//  - while the server still holds older history (the last read said
//    has_more), a changed row older than anything on screen stays there
//    for "Load earlier" to fetch, instead of appearing out of place. With
//    no older history left, such a row is a message that arrived late (a
//    delayed webhook stores it with an earlier send time) and shows in its
//    place: nothing else would ever bring it back (Sol 6.1 review, 1 Oct);
//  - EON's own record of a send gives way to the platform's copy of the
//    same words, the rule the server applies to a full page (cs_inbox
//    get_thread, _is_eon_ledger), because the two can now land in
//    different reads.
// (A full read keeps the merge it always had, in cswLoadTranscript.)
function cswMergeTranscript(older, base, fresh, hasOlder) {
  const ts = r => String((r && (r.platform_timestamp || r.created_at)) || '');
  // A held reply: the server's 'held:' row, marked body.held like the one
  // row builder reads it (cswLoadTranscript, "HELD REPLY").
  const held = r => String(r.id).startsWith('held:') || !!(r.body && r.body.held === true);
  const byId = new Map();
  const put = r => byId.set(String(r.id), r);
  (older || []).forEach(put);
  let floor = '';
  if (hasOlder === true) {
    for (const r of [...(older || []), ...(base || [])]) { const t = ts(r); if (t && (!floor || t < floor)) floor = t; }
  }
  (base || []).forEach(r => { if (!held(r)) put(r); });
  (fresh || []).forEach(r => {
    if (!held(r) && !byId.has(String(r.id)) && floor && ts(r) && ts(r) < floor) return;
    put(r);
  });
  const olderIds = new Set((older || []).map(r => String(r.id)));
  const ledger = r => /^(eon|rep):/.test(String(r.id || ''));
  const key = r => JSON.stringify([String(((r.body || {}).text) || ''),
    r.platform_sender_id == null ? null : String(r.platform_sender_id)]);
  const near = [...byId.values()].filter(r => !olderIds.has(String(r.id)));
  // (a held reply is not on the platform yet: it is nobody's echo)
  const echoes = new Set(near.filter(r => (r.direction || '') === 'outbound' && !ledger(r)
    && !held(r)).map(key));
  near.forEach(r => { if (ledger(r) && echoes.has(key(r))) byId.delete(String(r.id)); });
  return [...byId.values()].sort((a, b) => ts(a).localeCompare(ts(b)));
}

// Render message text safely + turn bare URLs into links. esc() first
// (XSS-safe), then linkify. .csw-bub__txt is white-space:pre-wrap so
// newlines render natively (no <br> needed).
// NOTE: this was referenced but UNDEFINED — its absence threw a
// ReferenceError inside the transcript .map(), leaving every text thread
// stuck on "Loading…". Caught by honored-width screenshot verification.
// Product references from the storefront omit the scheme. Only exact CRUX
// product hosts/paths may gain one; ordinary words, emails and lookalike
// domains stay text. Return a link destination, never rewrite the message.
function cswCruxProductUrl(value) {
  try {
    const u = new URL(String(value || ''));
    if (!/^https?:$/.test(u.protocol) || u.username || u.password || u.port
      || !/^(?:www\.)?(?:crux\.ng|anyiamobum\.myshopify\.com)$/i.test(u.hostname)
      || !/^\/products\/[a-z0-9][a-z0-9-]*\/?$/i.test(u.pathname)) return '';
    u.protocol = 'https:';
    return u.href;
  } catch (_) { return ''; }
}

function cswMessageLinks(text) {
  const raw = String(text || '');
  // Take complete tokens, then validate the product path. A partial regex
  // match must not turn a lookalike URL or an email into a CRUX product.
  const re = /https?:\/\/[^\s<>"']+|(?:www\.)?(?:crux\.ng|anyiamobum\.myshopify\.com)\/products\/[^\s<>"']+/gi;
  const links = [];
  for (const match of raw.matchAll(re)) {
    const label = match[0].replace(/[.,!?;:)\]}]+$/, '');
    const bare = !/^https?:\/\//i.test(label);
    const before = raw[match.index - 1] || '';
    if (bare && before && !/[\s([{"']/.test(before)) continue;
    const product = cswCruxProductUrl(bare ? 'https://' + label : label);
    if (bare && !product) continue;
    links.push({start: match.index, end: match.index + label.length,
      text: label, url: product || label});
  }
  return links;
}

function cswLinkify(t) {
  const raw = String(t || '');
  let html = '', offset = 0;
  for (const link of cswMessageLinks(raw)) {
    html += esc(raw.slice(offset, link.start))
      + `<a href="${esc(link.url)}" target="_blank" rel="noopener">${esc(link.text)}</a>`;
    offset = link.end;
  }
  return html + esc(raw.slice(offset));
}

// ── Link previews (unfurls) — ADDITIVE render path (2026-06-08) ────────
// After the transcript paints, lazily fetch the FIRST shared URL. Preserve
// its original message, timestamp, copy and reply affordances. CRUX product
// references gain a compact real-photo card; other previews stay unchanged.
const _cswUnfurlCache = new Map();   // url → preview payload (or null = no card)
let _cswUnfurlObs = null;

// First non-trivial URL in a message, or '' — skips our own media/proxy URLs
// (an unfurl of our proxy/CDN image would be noise; those are already shown).
function cswFirstUrl(text) {
  const first = cswMessageLinks(text)[0];
  if (!first) return '';
  const u = first.url;
  if (/\/api\/cs\/media-proxy|cdn\.shopify\.com\/.*\.(jpe?g|png|webp|gif)|cdninstagram|lookaside\.fbsbx|fbcdn\.net/i.test(u)) return '';
  return u;
}

async function cswFetchUnfurl(url) {
  if (_cswUnfurlCache.has(url)) return _cswUnfurlCache.get(url);
  let data = null;
  try {
    const d = await api('/api/cs/unfurl?url=' + encodeURIComponent(url), { timeoutMs: 12000 });
    if (d && d.ok && (d.image || d.title || d.description)) data = d;
  } catch (_) { data = null; }   // never let a failed unfurl disturb the chat
  _cswUnfurlCache.set(url, data);
  return data;
}

function cswUnfurlImageUrl(image, url) {
  if (!image) return '';
  try {
    const u = new URL(String(image), url);
    if (u.username || u.password || !/^https?:$/.test(u.protocol)) return '';
    if (cswCruxProductUrl(url)) {
      // Older cached CRUX previews contain the HTTP og:image. These known
      // storefront/CDN hosts serve HTTPS; no arbitrary-host rewriting.
      if (u.protocol === 'http:' && /^(?:www\.)?(?:crux\.ng|anyiamobum\.myshopify\.com|cdn\.shopify\.com)$/i.test(u.hostname)) u.protocol = 'https:';
      if (u.protocol !== 'https:') return '';
      // The card is 80px wide. Ask Shopify's known image routes for a
      // sharp 3x thumbnail, rather than making a phone fetch the master.
      const storefrontImage = /^(?:www\.)?(?:crux\.ng|anyiamobum\.myshopify\.com)$/i.test(u.hostname) && u.pathname.startsWith('/cdn/shop/');
      const shopifyImage = u.hostname.toLowerCase() === 'cdn.shopify.com' && u.pathname.startsWith('/s/files/');
      if (!u.port && (storefrontImage || shopifyImage)) u.searchParams.set('width', '240');
    }
    return u.href;
  } catch (_) { return ''; }
}

function cswUnfurlCardHtml(d, url) {
  const product = cswCruxProductUrl(url);
  // The card link goes through the desk's safe-link check (vet 2026-09-24,
  // fault 31): the server only returns web links today, but this was the one
  // place the desk printed a server-given address as-is. A link that is not
  // plain http(s) falls back to the customer's own link; with neither, the
  // card shows without a link.
  const target = safeTrackingHref(product || d.url || url) || safeTrackingHref(url || '');
  d = {...d, image: cswUnfurlImageUrl(d.image, target)};
  const img = d.image
    ? `<div class="csw-unfurl__imgwrap"><img class="csw-unfurl__img" src="${esc(cswImgSrc(d.image))}" alt="" loading="lazy" onerror="this.closest('.csw-unfurl').classList.add('csw-unfurl--noimg');this.remove()"/></div>`
    : '';
  const site = !product && d.site_name ? `<div class="csw-unfurl__site">${esc(shorten(d.site_name, 42))}</div>` : '';
  const title = d.title ? `<div class="csw-unfurl__title">${esc(shorten(d.title, 120))}</div>` : '';
  const desc = !product && d.description ? `<div class="csw-unfurl__desc">${esc(shorten(d.description, 140))}</div>` : '';
  const open = product ? '<span class="csw-unfurl__open">View product<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 12 12 4M4 4h8v8"/></svg></span>' : '';
  const cls = `csw-unfurl${product ? ' csw-unfurl--product' : ''}${d.image ? '' : ' csw-unfurl--noimg'}`;
  if (!target) return `<div class="${cls}">
      ${img}<div class="csw-unfurl__body">${site}${title}${desc}</div>
    </div>`;
  return `<a class="${cls}" href="${esc(target)}" target="_blank" rel="noopener">
      ${img}<div class="csw-unfurl__body">${site}${title}${desc}${open}</div>
    </a>`;
}

// Recognize only the exact storefront-generated sentence. Freeform customer
// text, extra words or a different product destination must remain visible.
function cswStylingReference(text, url) {
  const match = String(text || '').match(/^Styling question — ([^\n()]+) \(([^\s()]+)\) · ref ([0-9a-f]{6})$/i);
  if (!match || match[0] !== String(text || '')) return null;
  const product = cswCruxProductUrl(/^https?:\/\//i.test(match[2]) ? match[2] : 'https://' + match[2]);
  if (!product || product !== cswCruxProductUrl(url)) return null;
  return {ref: match[3]};
}

function cswFoldStylingReference(bub, url, d, receipt) {
  if (!d.title || !receipt || bub.querySelector('.csw-product-original')) return;
  const original = bub.querySelector('.csw-bub__txt');
  if (!original || original.parentNode !== bub) return;
  const reference = cswStylingReference(original.textContent, url);
  if (!reference) return;
  // A slow preview must not hide a link someone has already focused.
  if (original.contains(document.activeElement)) return;
  const selected = typeof window !== 'undefined' && window.getSelection?.();
  if (selected && selected.type === 'Range'
    && (original.contains(selected.anchorNode) || original.contains(selected.focusNode))) return;
  const label = document.createElement('div');
  label.className = 'csw-product-intent';
  label.textContent = 'Styling question';
  const details = document.createElement('details');
  details.className = 'csw-product-original';
  const summary = document.createElement('summary');
  summary.textContent = 'Original message · ref ' + reference.ref;
  details.appendChild(summary);
  bub.insertBefore(label, original);
  bub.insertBefore(details, receipt);
  // MOVE the original node, including its link; never clone/replace text.
  // Copy searches descendants, and Reply retains the bubble's data-reply.
  details.appendChild(original);
}

function cswPreserveUnfurlPosition(bub, paint) {
  const scroller = bub.closest?.('.csw-script');
  if (!scroller) { paint(); return; }
  const pinned = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 80;
  const edge = scroller.getBoundingClientRect().top;
  const anchor = Array.from(scroller.children).find(row => row.getBoundingClientRect().bottom > edge + 1);
  const top = anchor?.getBoundingClientRect().top;
  paint();
  if (pinned) scroller.scrollTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  else if (anchor?.isConnected) scroller.scrollTop += anchor.getBoundingClientRect().top - top;
}

// Insert the preview and optional original-message disclosure in one paint.
// The receipt stays below both; reading position survives async hydration.
async function cswUnfurlBubble(bub) {
  if (!bub || bub.dataset.cswUnfurled) return;
  bub.dataset.cswUnfurled = '1';
  const url = bub.dataset.cswUnfurl;
  if (!url) return;
  const d = await cswFetchUnfurl(url);
  if (!d) return;
  // Bubble may have been re-rendered (poll refresh) while we awaited.
  if (!bub.isConnected) return;
  cswPreserveUnfurlPosition(bub, () => {
    const receipt = cswCruxProductUrl(url) && bub.querySelector('.csw-bub__t');
    if (receipt) {
      receipt.insertAdjacentHTML('beforebegin', cswUnfurlCardHtml(d, url));
      cswFoldStylingReference(bub, url, d, receipt);
    } else bub.insertAdjacentHTML('beforeend', cswUnfurlCardHtml(d, url));
  });
}

// Scan freshly-rendered bubbles, tag the ones carrying a URL, and observe
// them. Called after each transcript paint. Re-uses one observer.
function cswUnfurlScan(body) {
  if (!body) return;
  if (!('IntersectionObserver' in window)) {
    // No IO (very old engine) → just unfurl all tagged bubbles directly.
    body.querySelectorAll('.csw-bub[data-csw-unfurl]').forEach(cswUnfurlBubble);
    return;
  }
  if (!_cswUnfurlObs) {
    _cswUnfurlObs = new IntersectionObserver((entries, obs) => {
      entries.forEach(en => {
        if (en.isIntersecting) { obs.unobserve(en.target); cswUnfurlBubble(en.target); }
      });
    }, { rootMargin: '120px' });
  }
  body.querySelectorAll('.csw-bub[data-csw-unfurl]:not([data-csw-unfurled])').forEach(b => _cswUnfurlObs.observe(b));
}

// Reactions. Zoko sends no native inbound reactions, so a rep "reacts" by
// sending an emoji as a reply-quote to the message (reaction degradation —
// works over any channel). Native body.reactions render too, if they ever
// arrive.
const CSW_REACTS = ['❤️', '👍', '😂', '🔥', '🙏', '😮'];
function cswReactsHtml(rx) {
  if (!rx) return '';
  let pairs = [];
  if (Array.isArray(rx)) { const m = {}; rx.forEach(r => { const e = typeof r === 'string' ? r : (r && (r.emoji || r.reaction)); if (e) m[e] = (m[e] || 0) + 1; }); pairs = Object.entries(m); }
  else if (typeof rx === 'object') pairs = Object.entries(rx).filter(([, v]) => v);
  if (!pairs.length) return '';
  return `<div class="csw-reacts">${pairs.map(([e, c]) => `<span class="csw-react">${esc(e)}${(+c) > 1 ? '<b>' + esc(c) + '</b>' : ''}</span>`).join('')}</div>`;
}
function cswReactTo(bub, emoji) {
  // TRUE WhatsApp reaction (2026-07-27) when the message has a wamid —
  // the emoji lands ON the customer's bubble via the Cloud API. Threads
  // without a mid (old rows, IG) keep the emoji-as-text fallback.
  const _mid = cswBubMid(bub);
  if (_mid) { cswSendReaction(_mid, emoji); return; }
  // Opus floor 3 verdict (2026-09-06): on a shut WhatsApp window the text
  // fallback would overwrite a held reply and fire the pick-up. Say so, stop.
  if (cswWinClosed()) { cswToast('The window is closed — a reaction cannot go as text now.'); return; }
  cswSetReply(bub.dataset.reply, bub.dataset.who);   // bond the reaction to the message
  const inp = document.getElementById('csw-input');
  if (inp) { inp.value = emoji; inp.dispatchEvent(new Event('input')); }
  cswSendText();
}
// ── Bubble action sheet (CV-11) ─────────────────────────────────────
// Long-press used to open the react bar alone. The full menu now sits
// under it — the react row IS the sheet's top row, so nothing is lost and
// the reactions stay one tap away.
function cswBubFullText(bub) {
  // The message as the customer sent it: the rendered text node (full),
  // falling back to data-reply (capped at 200 — fine as a fallback,
  // wrong as the source: the action bar's Copy used to truncate long
  // messages to 200 chars through it).
  const nodes = bub.querySelectorAll('.csw-bub__txt');
  const last = nodes.length ? nodes[nodes.length - 1] : null;
  return (last?.textContent || '').trim()
    || (bub.dataset.reply || '').trim();
}

function cswBubbleActions(bub) {
  document.getElementById('csw-reactbar')?.remove();
  const txt = bub.dataset.reply || (bub.querySelector('.csw-bub__t')?.textContent || '').trim();
  const who = bub.dataset.who || '';
  const rows = [
    ['reply', 'Reply'],
    ['pinmsg', 'Pin message'],
    ['copy', 'Copy text'],
    ['note', 'Save as internal note'],
  ];
  // "Correct this" — only on a message EON already sent. WhatsApp treats a
  // sent message as final, so the correction is a NEW message from the rep,
  // pre-drafted here and typed out by them before it goes.
  if (bub.classList.contains('out') && bub.querySelector('.csw-author--eon')
      && !bub.classList.contains('csw-bub--held')) {
    rows.push(['correct', 'Correct this']);
  }
  const ov = cswOverlay(
    (sosEmojiAllowed()
      ? `<div class="csw-bubacts__reacts">`
        + CSW_REACTS.map(e => `<button class="csw-reactbar__e" data-react="${esc(e)}" type="button">${e}</button>`).join('')
        + `</div>`
      : '')
    + rows.map(([k, label]) =>
        `<button class="csw-bubact" data-act="${k}" type="button">${label}</button>`).join('')
    + (txt ? `<div class="csw-bubacts__prev">${esc(txt.slice(0, 120))}</div>` : ''));
  ov.querySelectorAll('[data-react]').forEach(b => b.addEventListener('click', () => {
    cswReactTo(bub, b.dataset.react); ov.remove();
  }));
  ov.querySelectorAll('[data-act]').forEach(b => b.addEventListener('click', async () => {
    const act = b.dataset.act;
    ov.remove();
    if (act === 'reply') return cswSetReply(txt, who, cswBubMid(bub));
    if (act === 'correct') return cswCorrectEonMessage(bub);
    if (act === 'pinmsg') return cswPinMessage(bub, txt);
    if (act === 'copy') {
      // Through the ONE hardened path: iOS Safari rejects the raw clipboard
      // API when the page isn't fully focused, which is where reps live.
      // This used to call navigator.clipboard bare and toast "Could not
      // copy" — Thelma, 2026-08-10: "I can't also copy from Eon".
      const copied = await eonCopyToClipboard(cswBubFullText(bub) || txt);
      cswToast(copied ? 'Copied' : 'Could not copy', !copied);
      return;
    }
    if (act === 'note') {
      const cid = cswState().active;
      if (!cid || !txt) return;
      try {
        await api(`/api/cs/threads/${encodeURIComponent(cid)}/notes`,
          { method: 'POST', body: JSON.stringify({ body: txt.slice(0, 500) }) });
        cswToast('Saved to notes');
        cswLoadNotes(cid);
      } catch (e) { cswToast('Could not save note', true); }
    }
  }));
  if (navigator.vibrate) { try { navigator.vibrate(8); } catch (_) {} }
}

// ══ Held replies + corrections (2026-08-03) ════════════════════════
// WhatsApp's Business API treats a SENT message as final — there is no
// edit. So EON's written replies do not go straight out: they land in the
// thread as a held draft for a short window (45s by default, 0 = instant)
// that any rep can edit, send early, or discard. When the window closes
// the draft goes as written. For anything EON already sent, "Correct this"
// pre-drafts a follow-up the rep sends in their own name — a correction is
// a new message, never an edit.

// Countdown copy for one held draft. Reads the clock, never the server.
function cswHeldLabel(releaseAt, thread) {
  if (arguments.length > 1 && cswWindowBlocks(thread)) {
    return cswReplyWindow(thread).state === 'closed' ? 'Waiting for customer reply' : 'Not sure the reply window is open';
  }
  const t = Date.parse(releaseAt || '');
  if (arguments.length > 1) {
    const w = cswReplyWindow(thread);
    if (w.wa && Number.isFinite(w.expires) && Number.isFinite(t) && t >= w.expires) return 'Reply window closes before release';
  }
  if (!isFinite(t)) return 'Sending shortly';
  const left = Math.round((t - Date.now()) / 1000);
  if (left <= 0) return 'Sending…';
  if (left < 60) return `Sending in ${left}s`;
  return `Sending in ${Math.floor(left / 60)}m ${String(left % 60).padStart(2, '0')}s`;
}

// ONE interval for the whole transcript (never one timer per bubble): it
// walks the held rows on screen, writes each countdown, and stops itself
// the moment none are left — including when the CS surface is torn down
// and the rows leave the DOM.
function cswHeldTick() {
  const s = cswState();
  const rows = document.querySelectorAll('.csw-row--held[data-release-at]');
  if (!rows.length) { cswHeldTimerStop(); cswStageHeldSync(); return; }
  cswStageHeldSync();                  // the Stage's clock card follows the rows
  let due = false;
  rows.forEach(row => {
    const el = row.querySelector('.csw-held__count');
    if (!el) return;
    // A row the rep is already acting on owns its own status line — the
    // clock must not write "Sending in 38s" back over "Discarding…".
    if (row.classList.contains('csw-row--held-busy')) return;
    const blocked = cswWindowBlocks(s.activeThread);
    const rehearsal = !!s._contactRehearsal;
    const send = row.querySelector('[data-held-act="send"]');
    if (send) { send.disabled = blocked && !rehearsal; send.textContent = rehearsal ? 'Review held reply' : 'Send now'; }
    const discard = row.querySelector('[data-held-act="discard"]');
    if (discard) discard.textContent = rehearsal ? 'Rehearse discard' : 'Discard';
    const label = rehearsal ? 'Rehearsal · remains held' : cswHeldLabel(row.dataset.releaseAt, s.activeThread);
    if (el.textContent !== label) el.textContent = label;
    if (label === 'Sending…') { row.classList.add('csw-row--held-due'); due = true; }
  });
  // Expiry is the server's job — the draft sends itself. One refetch a
  // beat later turns the held bubble into the real sent message. Throttled
  // so a screenful of expiring drafts is still one fetch.
  if (due && (Date.now() - (s._heldRefetchAt || 0)) > 8000) {
    s._heldRefetchAt = Date.now();
    const cid = s.active;
    if (!cid) return;
    setTimeout(() => {
      const cs = cswState();
      if (cs.active !== cid) return;
      cs._txSig = null;
      cswLoadTranscript(cid, true);
    }, 2500);
  }
}

function cswHeldTimerSync() {
  const s = cswState();
  if (!document.querySelector('.csw-row--held')) { cswHeldTimerStop(); cswStageHeldSync(); return; }
  cswHeldTick();                       // paint immediately, don't wait 1s
  if (s._heldTimer) return;
  s._heldTimer = setInterval(cswHeldTick, 1000);
}

function cswHeldTimerStop() {
  const s = cswState();
  if (s._heldTimer) { clearInterval(s._heldTimer); s._heldTimer = null; }
}

// Shared tail for the three actions: settle the bubble against the server
// instead of guessing. A 409 / already_sent means the window closed while
// the rep was deciding — refetching is the honest answer, not an error.
async function cswHeldSettle(cid, msg, isError) {
  if (msg) cswToast(msg, !!isError);
  if (!cid) return;
  const s = cswState();
  s._txSig = null;
  try { await cswLoadTranscript(cid, true); } catch (_) {}
}

function cswHeldAct(btn) {
  const row = btn.closest('.csw-row--held');
  const holdId = row && row.dataset.holdId;
  if (!holdId) return;
  const act = btn.dataset.heldAct;
  if (act === 'edit') {
    return cswHeldEditSheet(holdId, row.querySelector('.csw-bub__txt')?.textContent || '');
  }
  const cid = cswState().active;
  if(cswState()._contactRehearsal)return cswHeldSend(holdId,null,cid,act);
  row.classList.add('csw-row--held-busy');
  row.querySelectorAll('.csw-held__btn').forEach(b => { b.disabled = true; });
  const count = row.querySelector('.csw-held__count');
  if (count) count.textContent = act === 'send' ? 'Sending…' : 'Discarding…';
  return cswHeldSend(holdId, act === 'send' ? null : undefined, cid, act);
}

// One call for all three send/discard shapes. `text` null = send as-is,
// a string = edit then send, undefined with act 'discard' = drop it.
async function cswHeldSend(holdId, text, cid, act) {
  if(cswState()._contactRehearsal) {
    cswContactDialog('Held reply · rehearsal',`<p>REHEARSAL — NOTHING SENDS</p>${text?`<blockquote>${esc(text)}</blockquote>`:''}<p>This draft remains held. Rehearsal does not release, replace or discard it, and adds no delivery receipt.</p>`,document.activeElement);
    return;
  }
  const path = act === 'discard'
    ? `/api/cs/held/${encodeURIComponent(holdId)}/discard`
    : `/api/cs/held/${encodeURIComponent(holdId)}/send`;
  const payload = (act === 'discard' || text == null) ? {} : { text };
  try {
    if (act !== 'discard') cswRequireReplyWindow(cid);
    const d = await api(path, { method: 'POST', body: JSON.stringify(payload) });
    if (!d || d.ok === false) throw new Error((d && d.error) || 'failed');
    return cswHeldSettle(cid, act === 'discard'
      ? 'Discarded — nothing was sent.'
      : 'Sent.');
  } catch (e) {
    const msg = String((e && e.message) || 'Could not do that');
    const gone = /already[_ ]sent|already[_ ]gone|already[_ ]settled|not[_ ]found/i.test(msg);
    return cswHeldSettle(cid, gone
      ? 'That draft is no longer available. Check its receipt in the conversation.'
      : msg, true);
  }
}

// Edit the words, then send. Saving IS the send — the hold stops counting,
// which is the plain reading of "Save and send".
function cswHeldEditSheet(holdId, current) {
  const cid = cswState().active;
  const rehearsal=cswState()._contactRehearsal===true;
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">${rehearsal?'Rehearse an edit':'Edit before it sends'}</div><button class="csw-x">✕</button></div>
    <div class="csw-hint" style="margin-bottom:10px">${rehearsal?'The edit stays here for review. Nothing is sent and the held reply is unchanged.':'This goes to the customer word-for-word. Saving sends it straight away — the hold stops counting.'}</div>
    <textarea class="csw-input" id="csw-held-body" style="width:100%;min-height:130px;margin-bottom:10px">${esc(current)}</textarea>
    <button class="csw-confirm" id="csw-held-save">${rehearsal?'Rehearse edit':'Save and send'}</button>`);
  ov.querySelector('.csw-x')?.addEventListener('click', () => ov.remove());
  ov.querySelector('#csw-held-save')?.addEventListener('click', async () => {
    const box = ov.querySelector('#csw-held-body');
    const text = (box && box.value || '').trim();
    if (!text) { cswToast('Write something first, or discard it.', true); return; }
    if(rehearsal){cswSaveDraft(cid,text);await cswHeldSend(holdId,text,cid,'send');return;}
    try { cswRequireReplyWindow(cid); }
    catch (e) { cswSaveDraft(cid, text); cswToast(cswSayWhy(e, { lead: 'Edited draft kept.' }), true); return; }
    ov.remove();
    await cswHeldSend(holdId, text, cid, 'send');
  });
}

// ── Correct an EON message that already went out ────────────────────
// Not an edit — WhatsApp does not allow one. The server hands back a
// correction opener; the rep finishes the sentence and sends it themselves.
async function cswCorrectEonMessage(bub) {
  const cid = cswState().active;
  const mid = cswBubMid(bub);
  if (!cid || !mid) { cswToast('Could not find that message.', true); return; }
  let prefill = '';
  try {
    const d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/correct`,
      { method: 'POST', body: JSON.stringify({ message_id: mid }) });
    if (!d || d.ok === false) throw new Error((d && d.error) || 'failed');
    prefill = String((d && d.prefill) || '');
  } catch (e) {
    cswToast(cswSayWhy(e, { lead: 'Could not start a correction.' }), true);
    return;
  }
  const inp = document.getElementById('csw-input');
  if (!inp) return;
  inp.value = prefill || 'Small correction — ';
  inp.dispatchEvent(new Event('input'));   // grows the box + arms Send
  inp.focus();
  try { inp.setSelectionRange(inp.value.length, inp.value.length); } catch (_) {}
  cswToast('WhatsApp will not let a sent message be edited — finish this line and it goes as a new message from you.');
}

// ── Reply hold window (owner/admin) ─────────────────────────────────
const CSW_HOLD_CHOICES = [
  [0, 'Off (send instantly)'],
  [15, '15 seconds'],
  [30, '30 seconds'],
  [45, '45 seconds'],
  [60, '60 seconds'],
];

async function cswHoldSettingSheet() {
  let d;
  try { d = await api('/api/cs/eon/hold'); }
  catch (e) { cswToast(cswSayWhy(e, { lead: 'Could not load the hold setting.' }), true); return; }
  const cur = Number((d && d.hold_seconds) || 0);
  const canManage = !(d && d.can_manage === false);
  // A window set elsewhere (or by an older build) can sit off this list —
  // say so rather than showing five options with none of them marked.
  const offList = cur > 0 && !CSW_HOLD_CHOICES.some(([v]) => v === cur);
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Reply hold</div><button class="csw-x">✕</button></div>
    <div class="csw-hint" style="margin-bottom:10px">EON's written replies wait this long in the conversation before they reach the customer, so a rep can edit one, send it early, or drop it. ${(d && d.faq_hold) ? 'FAQ auto-answers are held too.' : 'FAQ auto-answers are not held — those still go out instantly.'}</div>
    ${offList ? `<div class="csw-hint" style="margin-bottom:10px">Set to ${cur} seconds right now. Picking one below replaces it.</div>` : ''}
    ${CSW_HOLD_CHOICES.map(([v, l]) => `<button class="csw-holdopt${v === cur ? ' on' : ''}" data-hold="${v}" type="button"${canManage ? '' : ' disabled'}>
      <span class="csw-holdopt__l">${esc(l)}</span>${v === cur ? '<span class="csw-holdopt__now">now</span>' : ''}</button>`).join('')}
    ${canManage ? '' : '<div class="csw-hint" style="margin-bottom:0">Only an owner or admin can change this.</div>'}`);
  ov.querySelector('.csw-x')?.addEventListener('click', () => ov.remove());
  if (!canManage) return;
  ov.querySelectorAll('[data-hold]').forEach(b => b.addEventListener('click', async () => {
    const secs = Number(b.dataset.hold);
    ov.querySelectorAll('[data-hold]').forEach(x => { x.disabled = true; });
    try {
      const r = await api('/api/cs/eon/hold',
        { method: 'POST', body: JSON.stringify({ hold_seconds: secs }) });
      if (!r || r.ok === false) throw new Error((r && r.error) || 'save failed');
      ov.remove();
      cswToast(secs ? `Saved — EON's replies now wait ${secs} seconds.`
                    : 'Saved — EON now replies instantly.');
    } catch (e) {
      ov.querySelectorAll('[data-hold]').forEach(x => { x.disabled = false; });
      cswToast(cswSayWhy(e, { lead: 'Not saved.' }), true);
    }
  }));
}


// Custom voice-note player (play/scrub/speed) — replaces the stock
// <audio controls>, which looks nothing like WhatsApp.
// ── Sticky date chip (CV-04) ────────────────────────────────────────
// The day dividers scroll away with the content; a rep deep in history
// loses the date entirely. An IntersectionObserver promotes the current
// day into a pinned chip at the top of the transcript, exactly the
// WhatsApp/Telegram behaviour. Observer-based, so no scroll handler runs
// on the main thread per frame.
function cswBindStickyDate(body) {
  if (!body) return;
  const divs = [...body.querySelectorAll('.csw-daydiv')];
  body._stickyObs?.disconnect();
  document.getElementById('csw-stickyday')?.remove();
  if (divs.length < 2) return;          // one day in view needs no chip
  const chip = document.createElement('div');
  chip.id = 'csw-stickyday';
  chip.className = 'csw-stickyday';
  chip.textContent = divs[0].textContent;
  // PREPEND, never append (2026-07-25 fix): .csw-script is a flex COLUMN, so
  // an appended sticky element takes the LAST slot — the chip rendered at the
  // bottom of the conversation, under the newest message, reading like a
  // stray date. As the first child it pins at the top, which is the point.
  body.insertBefore(chip, body.firstChild);
  // Idle-fade (design vet 2026-07-26, M3): visible while the thumb moves,
  // gone ~1.1s after it stops — the permanently-on chip floated OVER
  // whatever bubble sat at the fold and printed across its words. Opacity
  // only: the chip keeps its flex slot, so nothing nudges mid-scroll (the
  // 2026-07-25 concern that motivated always-on). Reduced-motion pins it
  // visible; without eon_motion (old cached shell) behaviour is unchanged.
  body._stickyFade?.detach?.();
  body._stickyFade = (window.eonMotion && window.eonMotion.idleFade)
    ? window.eonMotion.idleFade(chip, body, { idle: 1100 })
    : null;
  const seen = new Map();
  const obs = new IntersectionObserver((entries) => {
    entries.forEach(e => seen.set(e.target, e));
    // The topmost divider at or above the fold names the current day.
    let current = divs[0];
    for (const d of divs) {
      const r = d.getBoundingClientRect();
      const br = body.getBoundingClientRect();
      if (r.top - br.top <= 44) current = d; else break;
    }
    chip.textContent = current.textContent;
    // Visibility is owned by the idle-fade above (scroll shows, stillness
    // hides). Fallback to always-on only when eon_motion is absent.
    if (!body._stickyFade) chip.classList.add('on');
  }, { root: body, threshold: [0, 1], rootMargin: '-44px 0px 0px 0px' });
  divs.forEach(d => obs.observe(d));
  body._stickyObs = obs;
}

function cswBindVoice(body) {
  const fmt = (s) => { s = Math.max(0, s | 0); return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0'); };
  body.querySelectorAll('.csw-voice').forEach(el => {
    if (el._bound) return; el._bound = true;
    const play = el.querySelector('.csw-voice__play'), ico = el.querySelector('.csw-voice__ico'),
      clip = el.querySelector('.csw-voice__fillwrap'),
      playedWave = el.querySelector('.csw-voice__wave--played'),
      tEl = el.querySelector('.csw-voice__t'), spd = el.querySelector('.csw-voice__spd'), bar = el.querySelector('.csw-voice__bar');
    let au = null; const speeds = [1, 1.5, 2]; let si = 0;
    // Pin the inner played wave to the bar's pixel width so its bars line up
    // 1:1 with the base wave; the clipper's % width reveals the played part.
    const syncW = () => { if (playedWave && bar) playedWave.style.width = bar.clientWidth + 'px'; };
    syncW();
    const setIco = (playing) => { if (ico) ico.className = 'csw-voice__ico ' + (playing ? 'csw-voice__ico--pause' : 'csw-voice__ico--play'); };
    const ensure = () => {
      if (!au) {
        syncW();
        au = new Audio(el.dataset.src); au.preload = 'metadata';
        au.addEventListener('loadedmetadata', () => {
          if (isFinite(au.duration)) { tEl.textContent = fmt(au.duration); return; }
          // Streamed opus/webm reports Infinity until a far seek forces the
          // real duration (the "0:00 until played" defect, design vet §07).
          const fix = () => { if (isFinite(au.duration)) { tEl.textContent = fmt(au.duration); au.currentTime = 0; au.removeEventListener('durationchange', fix); } };
          au.addEventListener('durationchange', fix);
          try { au.currentTime = 1e7; } catch (_) {}
        });
        au.addEventListener('timeupdate', () => { const p = au.duration ? au.currentTime / au.duration : 0; if (clip) clip.style.width = (p * 100) + '%'; tEl.textContent = fmt(au.currentTime); });
        au.addEventListener('ended', () => { setIco(false); el.classList.remove('playing'); if (clip) clip.style.width = '0%'; tEl.textContent = fmt(au.duration || 0); });
      }
      return au;
    };
    play.addEventListener('click', () => { const a = ensure(); if (a.paused) { a.play(); setIco(true); el.classList.add('playing'); } else { a.pause(); setIco(false); el.classList.remove('playing'); } });
    spd.addEventListener('click', () => { si = (si + 1) % speeds.length; spd.textContent = speeds[si] + '×'; if (au) au.playbackRate = speeds[si]; });
    bar.addEventListener('click', (e) => { const a = ensure(); if (!a.duration) return; const rect = bar.getBoundingClientRect(); a.currentTime = ((e.clientX - rect.left) / rect.width) * a.duration; });
    // Real duration up front (§07): metadata-only fetch at bind time — the
    // row read "0:00" until the rep pressed play. A note whose address still
    // waits for the media token (fault 21) is fetched when the real address
    // lands: cswMediaRepaint calls this hook, which also re-points a player
    // already built on the placeholder (adversary pass, 2026-09-24).
    el._cswVoiceSrc = () => { if (au) au.src = el.dataset.src; else ensure(); };
    if (String(el.dataset.src || '').includes('#csw-media=')) return;
    ensure();
  });
}

// Wave 3 — the desk works before the rep does. Once per thread per
// session, on open: auto-transcribe the NEWEST inbound voice note (the
// rep reads instead of listening on a loud shop floor); when the last
// inbound reads like an address, fetch the delivery quote unprompted;
// and when the thread is waiting with an empty composer, EON pre-drafts
// the reply through the existing /suggest-reply (silent — the rep edits
// or clears; a failure never toasts).
async function cswAutoAssist(body) {
  const s = cswState();
  const cid = s.active;
  if (!cid || String(cid).indexOf('team:') === 0) return;
  s._assisted = s._assisted || new Set();
  const first = !s._assisted.has(cid);   // transcribe + quote: once per chat
  s._assisted.add(cid);
  const tx = first ? body.querySelectorAll('.csw-row.in .csw-transcribe') : [];
  if (tx.length) { try { tx[tx.length - 1].click(); } catch (_) {} }
  const ins = first ? body.querySelectorAll('.csw-bub.in .csw-bub__txt') : [];
  const last = ins.length ? (ins[ins.length - 1].textContent || '').trim() : '';
  if (cswState().active !== cid || !body.isConnected) return;
  // Re-read the already loaded local book here. Rung availability, latency or
  // failure must never switch off address help or a waiting customer's draft.
  const context = cswDeskWorkContext(cid);
  s._workContext = context;
  const outs = body.querySelectorAll('.csw-bub.out .csw-bub__txt');
  const asked = cswAskedForAddress(outs.length ? outs[outs.length - 1].textContent : '');
  if (context.delivery_guidance === true && last.length >= 12 && cswLooksLikeAddress(last, asked)) {
    try { cswDeliveryQuote(); } catch (_) {}
  }
  // The pre-draft is decided ONCE, when the chat opens waiting, as it always
  // was — but a stand-down no longer spends it (Fable vet, 2026-09-27). It
  // rechecks the server's waiting state on every paint: a closer or a
  // resolved chat ends the chance. The transcript also blocks a second
  // answer while the list's `_waiting` flag is stale after a send:
  //   last row EON's queued reply  -> wait (Send now or Discard decides);
  //   last row an answer that went -> drop it (EON's reply was sent, or a
  //                                   teammate answered: nothing to draft);
  //   last row the customer's      -> draft, if EON is not answering here.
  // A chat opened while not waiting never gets one mid-session.
  s._predraftWanted = s._predraftWanted || new Set();
  const t = s.activeThread || {};
  if (first && t._waiting) s._predraftWanted.add(cid);
  if (!s._predraftWanted.has(cid)) return;
  if (!t._waiting) { s._predraftWanted.delete(cid); return; }
  // A reply that FAILED never reached the customer: it is not an answer.
  const rows = [...body.querySelectorAll('.csw-row.in, .csw-row.out')]
    .filter(r => !(r.querySelector && r.querySelector('.csw-bub.failed')));
  const lastRow = rows.length ? rows[rows.length - 1] : null;
  // The chance belongs to the customer message it was parked on (2026-10-06).
  // The list's `_waiting` is only as new as the last list read, and the
  // small transcript read can paint the customer's next message (a
  // "thanks") before the list reads again: the flag says nothing about
  // that message. So a newer customer message ends the chance. That is
  // main's own rule: the pre-draft answers what was waiting when the chat
  // opened, and a message that arrives later never gets one.
  const theirs = rows.filter(r => r.classList.contains('in'));
  const newest = theirs.length ? String((theirs[theirs.length - 1].dataset || {}).mid || '') : '';
  s._predraftFor = s._predraftFor || new Map();
  if (first) s._predraftFor.set(cid, newest);
  if (s._predraftFor.has(cid) && newest !== s._predraftFor.get(cid)) { s._predraftWanted.delete(cid); return; }
  if (lastRow && !lastRow.classList.contains('in')) {
    if (!lastRow.classList.contains('csw-row--held')) s._predraftWanted.delete(cid);
    return;
  }
  const inp = document.getElementById('csw-input');
  if (context.sales_guidance !== true) return;
  if (!(inp && !inp.value.trim() && !inp.disabled)) { s._predraftWanted.delete(cid); return; }
  if (cswEonAlreadyAnswering(t, body)) return;
  s._predraftWanted.delete(cid);
  cswSuggestReply(true);
}

// ONE EON ANSWER AT A TIME (CS rep chat vet, 2026-09-24). On a chat where
// EON already has a reply QUEUED — the "Reply to <name> · Send now ·
// Discard" band that sends itself when its clock runs out — or where EON
// holds the chat, the silent pre-draft still typed a second, different EON
// answer into the empty box and paid for a second draft: two EON answers
// to one question, one of them about to go out on its own. Measured in the
// repo's own review build (scripts/cs_desk_review): Amara's chat opened
// with both. Either sign means EON is already answering here.
// The check before the fill (cswSilentDraftBlocked) has only the thread in
// hand. With no transcript passed, the queued reply is read from this
// chat's own pane: never from the whole page, where a team or rehearsal
// transcript may show a queued reply that is not this chat's (2026-10-06).
function cswEonAlreadyAnswering(t, body) {
  const pane = body || document.getElementById('csw-script');
  if (pane && pane.querySelector('.csw-row--held')) return true;
  return !!(t && t.ball && t.ball.kind === 'eon');
}

// An address, not a mention of a place (CS rep chat vet, 2026-09-24). The
// old test fired on any "close" ("Are you closed on Sunday?"), any
// "estate", any area name ("Do you have a store in Abuja?") and fetched a
// delivery quote for a question. An address carries a house or plot
// number, or a street word after a name, or is said to be where to send.
// Did the desk's last message ask where to deliver? Then a bare place
// ("My place is Ikeja GRA, Lagos") is the answer, chat words and all.
function cswAskedForAddress(text) {
  return /\b(?:address|location|which area|where (?:should|do|can|will) (?:we|i) (?:deliver|send|drop)|where are you|deliver(?:y)? to where)\b/i.test(String(text || ''));
}

function cswLooksLikeAddress(text, asked) {
  const t = String(text || '').trim();
  const ask = /\?\s*$/.test(t);
  if (ask && !/\b(deliver|send|address)\b/i.test(t)) return false;
  // Fable vet (2026-09-27): a Nigerian address is often "Area, City" with no
  // number ("Ikeja GRA, Lagos", "Uyo, Akwa Ibom"), and a house number can be
  // two digits ("Deliver to 12 Bode Thomas"). But a message that talks ABOUT
  // a place ("Ok, Lagos is fine for pickup", "I am in Ikeja, Lagos") is chat,
  // not an address, so the number-free shapes only count when the message
  // carries no chat words.
  const CITY = "(?:lagos|abuja|lekki|ajah|ikeja|ikoyi|yaba|surulere|vi|victoria island|port harcourt|ibadan|benin|enugu|kano|uyo|warri|ota|akwa ibom|ogun|delta|rivers|oyo|edo|[a-z]+ state)";
  // When the desk asked for the address, the place-only shapes count even
  // with chat words around them (Fable re-vet, 2026-09-27).
  const chat = !asked && /\b(?:i|i'm|im|am|is|are|was|we|you|ok|okay|sorry|thank|thanks|fine|far|store|shop|pick ?up|collect|coming|come|from|closed|open|tomorrow|today|same|last time|the|maybe|wow|hello|hi|nice|which|traffic|prices?|team|branch)\b/i.test(t);
  // A question that only names a place asks whether we deliver there; it
  // gives no address ("Do you deliver to Abuja?", "Do you deliver to Lekki
  // Phase 1?" — Sol 6.1 review, 1 Oct). In a question the place-only shapes
  // do not count, and the place after "deliver to" counts only when it
  // starts with a house number. Street-number shapes count either way.
  return /\b(?:no\.?|plot|block|house|flat|apt)\s?\d+/i.test(t)
    || /\b\d+[a-z]?,?\s+[a-z][a-z .'-]{1,40}\s(?:street|str\.|road|rd|avenue|ave|close|crescent|way|drive|lane|estate)\b/i.test(t)
    || (!chat && !ask && /\b[a-z][a-z .'-]{1,40}\s(?:street|road|avenue|crescent|close|drive|lane|way|estate),/i.test(t))
    || (!chat && !ask && t.length <= (asked ? 200 : 60) && new RegExp("\\b[a-z0-9][a-z0-9 .'-]{1,30},\\s*" + CITY + "\\b", "i").test(t))
    || (ask ? /\b(?:deliver(?:y)?\s+to|send\s+(?:it\s+)?to)\s+\d+[a-z]?\s+\S{2,}/i
      : /\b(?:deliver(?:y)?\s+to|send\s+(?:it\s+)?to)\s+(?!(?:my|me|her|him|them|us|you)\b)\S{2,}/i).test(t)
    || /\b(?:my\s+address\s+is|address\s*:)\s*(?=[^?]*\d)\S+/i.test(t)
    || new RegExp("\\b(?:my\\s+address\\s+is|address\\s*:)\\s*[^?]*,\\s*" + CITY + "\\b", "i").test(t)
    || (!ask && /\bphase\s?\d\b/i.test(t));
}

// §07 — videos: duration badge + locked aspect once metadata lands. The
// wrap's min-height reserves space pre-load (Law 03); aspect-ratio takes
// over after so the transcript never jumps as videos arrive.
function cswBindVideos(body) {
  const fmt = (s) => { s = Math.max(0, s | 0); return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0'); };
  body.querySelectorAll('.csw-bub__vidwrap').forEach(w => {
    if (w._vbound) return; w._vbound = true;
    const v = w.querySelector('video'); if (!v) return;
    const apply = () => {
      if (v.videoWidth && v.videoHeight) { w.style.aspectRatio = v.videoWidth + ' / ' + v.videoHeight; w.style.minHeight = '0'; }
      if (isFinite(v.duration) && v.duration > 0 && !w.querySelector('.csw-bub__viddur')) {
        const b = document.createElement('span'); b.className = 'csw-bub__viddur'; b.textContent = fmt(v.duration); w.appendChild(b);
      }
    };
    if (v.readyState >= 1) apply(); else v.addEventListener('loadedmetadata', apply, { once: true });
  });
}

// Intent/sentiment chip in the conversation bar (async, non-blocking).
async function cswLoadIntent(cid) {
  try {
    const d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/intent`, { timeoutMs: 20000 });
    if (!d || !d.ok || !d.intent || cswState().active !== cid) return;   // never render 'undefined'
    // 2026-06-11: append into the sub-row (channel · window) so the
    // intent/BUY chip sits ON THE SAME LINE as WhatsApp/23h instead of
    // dropping to a third line under the name and eating vertical space.
    // Vet 2026-08-08: post-R1 the sub is scroll-half + pinned-half; an
    // append to the sub itself landed the chip RIGHT OF the pinned dial
    // and pushed the row under the header buttons. The chip is
    // informational — it rides the scroller.
    const host = document.querySelector('.csw-conv__scroll')
      || document.querySelector('.csw-conv__sub') || document.querySelector('.csw-conv__id');
    if (!host) return;
    let chip = document.getElementById('csw-intent');
    if (!chip) { chip = document.createElement('span'); chip.id = 'csw-intent'; host.appendChild(chip); }
    const cls = d.priority === 'high' ? 'csw-intent--high' : (d.sentiment === 'negative' ? 'csw-intent--neg' : '');
    chip.className = 'csw-intentchip ' + cls;
    chip.textContent = d.intent + (d.sentiment === 'negative' ? ' · upset' : '');
    chip.title = d.reason || '';
  } catch (_) {}
}

function cswRestoreFocus(trigger) {
  // Menu commands disappear when their details element closes. Return to
  // that menu's summary instead of focusing an invisible former command.
  const menu = trigger?.closest?.('details');
  const target = menu && !menu.open ? menu.querySelector('summary') : trigger;
  if (target?.isConnected && !target.closest('[inert]') && target.getClientRects().length) {
    target.focus({ preventScroll: true });
  } else {
    document.querySelector('#csw-conv-menu summary')?.focus({ preventScroll: true });
  }
}

function cswSetCustomerDetails(open, trigger) {
  const panel = document.getElementById('csw-360');
  if (!panel) return;
  if (open) panel._returnFocus = trigger || document.activeElement;
  panel.classList.toggle('open', open);
  panel.inert = !open;
  panel.setAttribute('aria-hidden', String(!open));
  document.getElementById('csw-360btn')?.setAttribute('aria-expanded', String(open));
  if (open) (panel.querySelector('#csw-360-close') || panel).focus({ preventScroll: true });
  else cswRestoreFocus(panel._returnFocus);
}

// Both entry points use the same first-open readers. Reopening an already
// open file keeps it open and does not duplicate the order/payment reads.
function cswOpenCustomerDetails(trigger) {
  const panel = document.getElementById('csw-360');
  if (!panel) return;
  cswSetCustomerDetails(true, trigger);
  const host = document.getElementById('csw-trail-host');
  if (host) host.innerHTML = cswTrailHtml(cswState().activeThread || {}) || '<div class="csw-trail csw-trail--empty">Nothing recorded yet.</div>';
  cswLoadTrailHolds(cswState().active);
  if (!panel._osLoaded) {
    panel._osLoaded = true;
    cswLoadOrderStatus(cswState().active);
    cswLoadPayment(cswState().active);
  }
}

function cswRender360(id) {
  const s = cswState();
  const el = document.getElementById('csw-360');
  if (!el) return;
  const t = s.activeThread || {};
  const ltv = id && id.total_spend_ngn != null ? cswNgn(id.total_spend_ngn) : '—';
  const orders = (id && id.total_orders) || 0;
  const chip = (on, l) => `<span class="csw-chip ${on ? 'on' : ''}">${l}</span>`;
  // 2026-07-25 (operator: "redesign the customer info dropdown — more
  // organized"): the panel had grown into 10 sibling slots in arrival
  // order. Same slots, same ids (every async loader keeps working),
  // now grouped under four labelled sections in the order a rep reads:
  // who they are → how we know them → their money → working context.
  el.innerHTML = `<div class="csw-360__head"><strong>Customer details</strong><button type="button" id="csw-360-close" aria-label="Close customer details">Close <span aria-hidden="true">×</span></button></div><div class="csw-360__in">
    <div class="csw-360sec">
      <div class="csw-360sec__h">What happened</div>
      <div id="csw-trail-host">${cswTrailHtml(t) || '<div class="csw-trail csw-trail--empty">Nothing recorded yet.</div>'}</div>
    </div>
    <div class="csw-360sec">
      <div class="csw-360sec__h">Customer</div>
      ${/* L5 (vet 2026-08-30, csw-cust-duplicate-id): this slot carried
           the SAME csw-cust id the header line at the top of the shell
           already owns. getElementById returns the first in document
           order, so every intel paint landed in the nowrap 10px header and
           this hero slot read 'Loading customer…' forever. Its own id now;
           the header keeps the old id for cswLoadCustomerCard. The inline
           conditional is the cache-repaint — a 360 rebuild with _custIntel
           already cached re-paints the card exactly like the source/circle
           badges below. */''}
      <div id="csw-cust-360">${s._custIntel && s._custIntel.cid === s.active ? cswCustomerCardHtml(s._custIntel.data) : '<div class="csw-cust-loading">Loading customer…</div>'}</div>
    </div>
    <div class="csw-360sec">
      <div class="csw-360sec__h">Identity &amp; links</div>
      ${/* POS chip retired (vet: store-fact-missing-pos-chip-dead) — it was
           on for 7 of 20,361 identities; the field it read comes from a
           source retired with pos_transactions. The store truth lives on
           the hold sheet's pre-lit store instead (orders.sale_channel). */''}
      <div class="csw-chips" id="csw-id-chips">${chip(id && id.seen_in_shopify, 'Shopify')}${chip(id && id.seen_in_zoko, 'WhatsApp')}</div>
      <div id="csw-src-badge"></div>
      <div id="csw-circle-badge"></div>
      <div id="csw-tagclaim"></div>
    </div>
    <div class="csw-360sec">
      <div class="csw-360sec__h">Orders &amp; payments</div>
      <div id="csw-orderstatus"></div>
      <div id="csw-paystatus"></div>
    </div>
    <div class="csw-360sec">
      <div class="csw-360sec__h">Labels &amp; notes</div>
      <div class="csw-labels" id="csw-labels"></div>
      <div id="csw-pinnote"></div>
      <div class="csw-notes" id="csw-notes-panel"></div>
    </div>
    <div class="csw-360__acts">
      <button class="csw-mini" id="csw-capture">🔗 Capture / link</button>
      <button class="csw-mini" id="csw-pin">${cswIsPinned(s.active) ? '📌 Unpin' : '📌 Pin to top'}</button>
      <button class="csw-mini" id="csw-assign-me">Assign to me</button>
      <button class="csw-mini" id="csw-noreply" title="Nothing to answer — close it; reopens if the customer writes again">No reply needed</button>
      <button class="csw-mini" id="csw-resolve">Mark resolved</button>
    </div>
  </div>`;
  // G5: re-paint the source badge from cache after every 360 rebuild (this
  // innerHTML wipe is what raced the old sibling-insert). Only for the thread
  // the cache belongs to, so a previous thread's ad never bleeds through.
  document.getElementById('csw-360-close')?.addEventListener('click', () => {
    cswSetCustomerDetails(false);
  });
  el.onkeydown = ev => {
    if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); cswSetCustomerDetails(false); }
  };
  const sc = s._source360;
  cswRenderSource(sc && sc.cid === s.active ? sc.src : null);
  // Same survival trick for the web-Circle badge (mig 537).
  const cc = s._circle360;
  cswRenderCircle(cc && cc.cid === s.active ? cc.d : null);
  const cid = s.active;
  document.getElementById('csw-pin')?.addEventListener('click', (e) => { cswTogglePin(cid); e.target.textContent = cswIsPinned(cid) ? '📌 Unpin' : '📌 Pin to top'; });
  document.getElementById('csw-capture')?.addEventListener('click', () => {
    cswContactCard(cid, async (contact) => {
      // Capture credit (2026-07-31): the contact save now ALSO binds the
      // till orders this chat provably produced (server sweeps outbound
      // order-number mentions and calls the pos_attach bind-thread lane) —
      // the durable capture row every data-collection metric reads.
      const r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/contact`, { method: 'POST', body: JSON.stringify(contact) });
      const bound = (r && r.till_captures) || [];
      if (bound.length) cswToast(`Saved — till order ${bound.map(b => b.order_name).filter(Boolean).join(', ')} now carries this customer.`);
      cswLoadLtv(cid);
    }, 'Save & link customer');
  });
  document.getElementById('csw-assign-me')?.addEventListener('click', async (e) => {
    e.target.disabled = true;
    try { await api(`/api/cs/threads/${encodeURIComponent(cid)}/assign`, { method: 'POST', body: JSON.stringify({}) }); e.target.textContent = 'Assigned to you'; cswLoadThreads(true); }
    // Round 2 (2026-09-26): these two keys failed in silence — the button
    // came back and nothing said the tap had not taken.
    catch (err) { e.target.disabled = false; cswToast(cswSayWhy(err, { lead: 'Not assigned to you.' }), true); }
  });
  document.getElementById('csw-resolve')?.addEventListener('click', async (e) => {
    e.target.disabled = true;
    try { await api(`/api/cs/threads/${encodeURIComponent(cid)}/status`, { method: 'POST', body: JSON.stringify({ status: 'resolved' }) }); e.target.textContent = '✓ Resolved'; cswLoadThreads(true); }
    catch (err) { e.target.disabled = false; cswToast(cswSayWhy(err, { lead: 'Not marked resolved.' }), true); }
  });
  // "No reply needed" — the rep reading the thread closes it right here,
  // without hunting the priority row. Unlike Mark resolved (which the
  // reopen guard bounces back to open whenever the customer spoke last),
  // this force-resolves through cswNoReplyNeeded and audits the decision.
  document.getElementById('csw-noreply')?.addEventListener('click', async (e) => {
    e.target.disabled = true;
    const ok = await cswNoReplyNeeded(cid, null);
    if (ok) { e.target.textContent = 'Closed'; cswLoadThreads(true); }
    else { e.target.disabled = false; }
  });
  cswLoadLabels(cid);   // labels now live in the 360 panel
  cswRenderNotesPanel(cid);   // editable notes surface (renders from state, refreshed by cswLoadNotes)
  cswBindCustomerCard(cid);   // (re)bind the Draft-offer button if card present
  // order-status (3 backend queries) is lazy — loaded on first panel expand,
  // not on every thread open (the panel starts collapsed).
  // Rebuild just wiped the lazy slots (#csw-orderstatus/#csw-paystatus).
  // Expanding BEFORE this async rebuild landed used to burn the one-shot
  // (_osLoaded) against slots that didn't exist yet, and the wipe erased
  // anything already painted — the order history simply never showed
  // (operator, 2026-07-13). Reset the ledger; if the rep already has the
  // panel open, reload straight into the fresh slots.
  el._osLoaded = false;
  if (el.classList.contains('open')) {
    el._osLoaded = true;
    cswLoadOrderStatus(cid);
    cswLoadPayment(cid);
  }
  cswRenderTagClaim(cid, t);
}

// ── IG Tag & Claim (PLAN-ig-tag-reconciliation.md, PROCEED 2026-07-13) ──
// A store/website buyer who tags us on IG has NO chat sales record — the
// rep verifies ONE key (order number / purchase email / phone), the
// backend links the identities (ig:<username> tag on the Shopify
// customer) and sends a single-use 5% code in-thread. IG threads only:
// WhatsApp senders already link by phone automatically.
const CSW_CLAIM_ERRORS = {
  already_claimed_sender: 'This customer already claimed a code.',
  already_claimed_order: 'That order was already claimed.',
  no_order_found: 'No recent order matches — try another key, or check the spelling.',
  unrecognized_key: 'Send an order number, email, or phone.',
  order_has_no_customer: 'That order has no customer on it and this chat has no phone saved yet. Save the customer\'s phone with Capture / link, then link the order again.',
  lookup_unavailable: 'Order lookup is unavailable right now — try again shortly.',
  claim_write_failed: 'Could not save the claim — try again.',
};
async function cswRenderTagClaim(cid, t) {
  const slot = document.getElementById('csw-tagclaim');
  if (!slot) return;
  // 2026-07-25 (operator: DM'd customer bought in-store as order #34288 —
  // "just add the order number and link it"): the slot works on EVERY
  // channel now. Primary action = pure link (attribution, no code); the
  // IG-only Tag & Claim 5% reward stays as the secondary button.
  const isIG = !String((t && t.platform_sender_id) || '').startsWith('+');
  let claim = null;
  try { claim = (await api(`/api/cs/threads/${encodeURIComponent(cid)}/tag-claim`)).claim; } catch (_) {}
  if (cswState().active !== cid) return;   // thread changed while we awaited
  if (claim) {
    slot.innerHTML = `<div class="csw-note" style="margin-top:8px">✓ Purchase linked — order ${esc(claim.order_number || '')}${claim.promo_code ? ` · code ${esc(claim.promo_code)}` : ''}</div>`;
    return;
  }
  slot.innerHTML = `
    <div class="csw-tagclaim" style="margin-top:8px">
      <button class="csw-mini" id="csw-tagclaim-open">🧾 Link an order to this chat</button>
      <div id="csw-tagclaim-form" hidden style="margin-top:6px">
        <input class="csw-input" id="csw-tagclaim-key" placeholder="Order number, email or phone" style="width:100%;margin-bottom:6px"/>
        <div style="display:flex;gap:6px;flex-wrap:wrap">
          <button class="csw-mini csw-mini--p" id="csw-tagclaim-link">Link order</button>
          ${isIG ? '<button class="csw-mini" id="csw-tagclaim-go">Link + send 5% code</button>' : ''}
        </div>
        <div class="csw-hint" id="csw-tagclaim-msg" style="margin-top:4px"></div>
      </div>
    </div>`;
  slot.querySelector('#csw-tagclaim-open')?.addEventListener('click', () => {
    const f = slot.querySelector('#csw-tagclaim-form');
    if (f) { f.hidden = !f.hidden; if (!f.hidden) slot.querySelector('#csw-tagclaim-key')?.focus(); }
  });
  const submit = async (btn, linkOnly) => {
    const key = (slot.querySelector('#csw-tagclaim-key')?.value || '').trim();
    const msg = slot.querySelector('#csw-tagclaim-msg');
    if (!key) { if (msg) msg.textContent = 'Enter the order number (or the email / phone on the order).'; return; }
    btn.disabled = true;
    if (msg) msg.textContent = 'Verifying…';
    try {
      // username resolved server-side from the avatar pointer row
      const res = await api(`/api/cs/threads/${encodeURIComponent(cid)}/tag-claim`, {
        method: 'POST', body: JSON.stringify(linkOnly ? { key, link_only: true } : { key }),
      });
      if (res.ok && res.captured) {
        // Capture credit (2026-07-31): the order had no customer on it —
        // the server bound this chat's phone to it through the
        // pos_attach bind-thread lane instead of dead-ending.
        slot.innerHTML = `<div class="csw-note" style="margin-top:8px">✓ Order ${esc(res.order_number || '')} captured to this chat — the customer's phone is now on the sale.</div>`;
      } else if (res.ok) {
        // Round 2 (2026-09-26): when the code did not go, the server now
        // says why in `send_note` (#3548): a shut reply window, a held
        // contact, or "not sure it went". That sentence replaces the bare
        // "NOT sent", which was untrue when the send may have gone. A note
        // that is only a code word falls back to the plain line.
        const notSent = 'Not sent. Send it to them yourself.';
        const sendLine = res.sent ? 'sent in the chat.'
          : res.send_note ? cswSayWhy({ message: String(res.send_note) }, { send: true, otherwise: notSent })
          : notSent;
        slot.innerHTML = linkOnly
          ? `<div class="csw-note" style="margin-top:8px">✓ Order ${esc(res.order_number || '')} linked — this chat now owns the sale.</div>`
          : `<div class="csw-note" style="margin-top:8px">✓ Linked order ${esc(res.order_number || '')} — code ${esc(res.code || '(code failed — see promo codes)')}${res.sent ? ' ' : '. '}${esc(sendLine)}</div>`;
      } else {
        btn.disabled = false;
        if (msg) msg.textContent = CSW_CLAIM_ERRORS[res.error] || `Could not verify (${res.error || 'unknown'}).`;
      }
    } catch (err) {
      btn.disabled = false;
      // Round 2: one plain sentence. With the code, a lost answer may
      // already have sent it, so it says check the chat.
      if (msg) msg.textContent = cswSayWhy(err, { send: !linkOnly });
    }
  };
  slot.querySelector('#csw-tagclaim-link')?.addEventListener('click', (e) => submit(e.target, true));
  slot.querySelector('#csw-tagclaim-go')?.addEventListener('click', (e) => submit(e.target, false));
}

// ── Customer-Intelligence panel ──────────────────────────────────
// Value + history + a one-click on-brand outreach action, surfaced INSIDE
// the open thread. The 24h WhatsApp window is open, so the rep can SEND
// free-form — the "Draft offer" button opens an Opus-drafted, name+history
// aware message in the composer for review, then the rep sends inline.

const CSW_SEG_CLASS = {
  Champion: 'csw-seg--champ', Loyal: 'csw-seg--loyal',
  'At-risk': 'csw-seg--risk', Lapsed: 'csw-seg--lapsed',
  New: 'csw-seg--new', Standard: 'csw-seg--std',
};

function cswRelDays(n) {
  if (n == null) return '—';
  n = +n;
  if (n <= 0) return 'today';
  if (n === 1) return '1d ago';
  return n + 'd ago';
}

// Build the card HTML from a /customer-context payload. Returns '' when
// there is nothing to show (defensive).
function cswCustomerCardHtml(d) {
  if (!d || !d.ok) return '';
  // New / unmatched contact — graceful, decision-first.
  if (!d.found || !d.profile) {
    // L5 (vet: prior-conversation-verdict): when the server names a prior
    // episode ('Last conversation: quoted ₦X, no order — 10 days ago') it
    // renders as ONE line — the conversation record, never a rep stat.
    // The draft button says what its kind actually drafts: the server
    // stops sending 'welcome' the moment a prior quote exists.
    const nk = d.draft_kind || 'welcome';
    const nkLabel = nk === 'winback' ? 'Draft win-back' : 'Draft welcome';
    return `<div class="csw-cust csw-cust--new">
      <div class="csw-cust__hd"><span class="csw-cust__name">${esc(d.headline || 'New contact')}</span></div>
      ${d.prior_episode_line ? `<div class="csw-cust__prior">${esc(d.prior_episode_line)}</div>` : ''}
      <div class="csw-cust__act">
        <div class="csw-cust__sug">${esc(d.suggested_action || '')}</div>
        <button class="csw-mini csw-mini--p csw-cust__draft" data-kind="${esc(nk)}">${nkLabel}</button>
      </div>
    </div>`;
  }
  const p = d.profile;
  const seg = p.rfm_segment || 'Standard';
  const segCls = CSW_SEG_CLASS[seg] || 'csw-seg--std';
  const cats = (p.top_categories || []).slice(0, 3);
  const draftLabel = d.draft_kind === 'welcome' ? 'Draft welcome'
    : (d.draft_kind === 'winback' ? 'Draft win-back' : 'Draft offer');
  const stat = (n, l) => `<div class="csw-cstat"><div class="csw-cstat__n">${n}</div><div class="csw-cstat__l">${l}</div></div>`;
  return `<div class="csw-cust">
    <div class="csw-cust__hd">
      <span class="csw-cust__name">${esc(p.name || 'Customer')}</span>
      <span class="csw-segbadge ${segCls}">${esc(seg)}</span>
    </div>
    <div class="csw-cust__stats">
      ${stat(p.order_count || 0, 'Orders')}
      ${stat(cswNgn(p.ltv_ngn), 'LTV')}
      ${stat(cswNgn(p.aov_ngn), 'AOV')}
      ${stat(cswRelDays(p.days_since_last), 'Last order')}
      ${stat(p.is_returning ? 'Returning' : 'New', 'Type')}
    </div>
    ${cats.length ? `<div class="csw-cust__cats">buys: ${cats.map(c => `<span class="csw-catchip">${esc(c)}</span>`).join('')}</div>` : ''}
    <div class="csw-cust__act">
      <div class="csw-cust__sug">${esc(d.suggested_action || '')}</div>
      <button class="csw-mini csw-mini--p csw-cust__draft" data-kind="${esc(d.draft_kind || 'exclusive')}">${draftLabel}</button>
    </div>
  </div>`;
}

// Bind the Draft-offer button → Opus draft → prefill composer (editable).
function cswBindCustomerCard(cid) {
  // L5: the panel slot's own id — never the header's csw-cust, where this
  // selector used to resolve and bind a button that node never rendered.
  const btn = document.querySelector('#csw-cust-360 .csw-cust__draft');
  if (!btn || btn._bound) return;
  btn._bound = true;
  btn.addEventListener('click', async (e) => {
    const kind = btn.dataset.kind || 'exclusive';
    const orig = btn.textContent;
    btn.disabled = true; btn.textContent = 'Drafting…';
    try {
      const d = await api('/api/cs/draft-customer-message', {
        method: 'POST', timeoutMs: 30000,
        body: JSON.stringify({ zoko_customer_id: cid, kind }),
      });
      if (cswState().active !== cid) { cswToast('Draft not added — you moved to another chat.', true); return; }   // thread switched mid-draft
      if (!d || !d.ok || !d.draft) throw new Error((d && d.error) || 'draft failed');
      // Vet 2026-09-24 (fault 1): this replaced whatever the rep typed
      // while the draft was being written. The adopt step asks first.
      cswAdoptDraft(cid, d.draft, () => cswToast('Draft ready — review, edit, then send'), e, { claim: false });
    } catch (err) {
      cswToast('Couldn\'t draft that — try again', true);
    } finally {
      btn.disabled = false; btn.textContent = orig;
    }
  });
}

async function cswLoadCustomerIntel(cid) {
  try {
    const d = await api(`/api/cs/customer-context?zoko_customer_id=${encodeURIComponent(cid)}`, { timeoutMs: 25000 });
    if (!d || !d.ok || cswState().active !== cid) return;
    cswState()._custIntel = { cid, data: d };
    // 01 — THE CUSTOMER FILE rides this SAME payload and this same cache;
    // the thread top and the 360 panel now read one fetch between them.
    cswPaintCustomerFile();
    // L5: paint the PANEL slot (csw-cust-360), never the header's csw-cust.
    // No fallback to the header on purpose — writing the card there is the
    // exact clipped-sliver bug this id split retires. If the 360 hasn't
    // rendered yet, cswRender360's own inline cache read paints it when it
    // does (the _custIntel cache above is already set).
    const host = document.getElementById('csw-cust-360');
    if (!host) return;
    host.innerHTML = cswCustomerCardHtml(d);
    cswBindCustomerCard(cid);
  } catch (_) { /* never break the chat on the intel panel */ }
}

// Proactive order status + size hint, injected into the 360 (async).
async function cswLoadOrderStatus(cid) {
  try {
    const d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/order-status`, { timeoutMs: 20000 });
    if (!d || !d.ok || cswState().active !== cid) return;
    const el = document.getElementById('csw-orderstatus');
    if (!el) return;
    const o = d.order || {};
    const size = d.size_hint && d.size_hint.size;
    let html = '';
    if (o.found) {
      const ff = (o.fulfillment_status || 'unfulfilled');
      const cls = ff === 'fulfilled' ? 'csw-ord--done' : 'csw-ord--pending';
      html += `<div class="csw-ord ${cls}">
        <div><b>${esc(o.order_name || 'Last order')}</b> · ${esc(ff)}</div>
        <div class="csw-ord__sub">${esc(o.financial_status || '')} · ${cswNgn(o.total_ngn)} · ${esc(o.created_at || '')}</div>
        <div id="csw-olc"></div>
        ${o.order_status_url ? `<button class="csw-mini csw-mini--p" id="csw-send-status">Write order update</button>` : ''}
      </div>`;
    }
    if (size) html += `<div class="csw-sizehint">Usual size: <b>${esc(size)}</b></div>`;
    el.innerHTML = html;
    // Lifecycle door (2026-07-27 tail-doors): rides THIS lazy load — the
    // panel-expand-only _osLoaded gate above us — so opening a thread never
    // adds an upfront fetch. Walk-in/store orders have no lifecycle row by
    // design (audit C2) and the slot stays quiet for them.
    if (o.found && o.shopify_order_id) { el.dataset.placedAt = o.created_at || ''; cswLoadOrderLc(cid, o.shopify_order_id); }
    // CS rep chat vet (2026-09-24): this key SENT at one tap, unreviewed,
    // told the customer Shopify's own word ("it's currently unfulfilled"),
    // and a failed send just re-enabled the button — the rep believed it
    // went. It now writes the update into the message box in plain words,
    // like every other prepared line; the rep reads it and sends it.
    document.getElementById('csw-send-status')?.addEventListener('click', () => {
      const msg = cswOrderUpdateText(o);
      const inp = document.getElementById('csw-input');
      if (!inp || cswState().active !== cid) return;
      if (inp.value.includes(msg)) { inp.focus(); return; }   // a second tap adds nothing
      inp.value = inp.value.trim() ? `${inp.value.trim()}\n${msg}` : msg;
      inp.dispatchEvent(new Event('input'));
      inp.focus();
      cswToast('Order update is in the message box. Check it, then send.');
    });
  } catch (_) {}
}

// The order update in the customer's words. Shopify's fulfilment status
// is a system word ("unfulfilled", "partial"); the customer hears what it
// means for them, and an unknown status says nothing it cannot back.
function cswOrderUpdateText(o) {
  const said = { unfulfilled: 'being prepared', partial: 'partly sent out',
    partially_fulfilled: 'partly sent out', fulfilled: 'sent out' };
  const where = said[String(o.fulfillment_status || 'unfulfilled').toLowerCase()];
  const name = o.order_name ? `Your order ${o.order_name}` : 'Your order';
  const first = where ? `${name} is ${where}.`
    : `Here's an update on your order${o.order_name ? ' ' + o.order_name : ''}.`;
  return o.order_status_url ? `${first} You can follow it here: ${o.order_status_url}` : first;
}

// ── Order lifecycle door (2026-07-27 tail-doors) ──────────────────────
// The Phase-5 lifecycle endpoints (/api/cs/orders/{id}/lifecycle ·
// /mark-delivered · /api/cs/reviews) were fully built server-side with zero
// consumers. This is their door: a stage trail on the 360 order card, a
// Mark-delivered tap (optimistic + revert — house pattern), and a
// record-review form once delivered. Loaded ONLY from cswLoadOrderStatus
// (panel-expand lazy path) — never upfront.
const CSW_OLC_STAGES = ['received', 'shipped', 'delivered', 'reviewed'];
function cswOlcStageIdx(stage) {
  if (stage === 'review_sent') return 2;   // delivered; review follow-up sent
  if (stage === 'closed') return 3;
  const i = CSW_OLC_STAGES.indexOf(stage);
  return i < 0 ? -1 : i;
}

async function cswLoadOrderLc(cid, oid) {
  if (!oid) return;
  try {
    const d = await api(`/api/cs/orders/${encodeURIComponent(oid)}/lifecycle`, { timeoutMs: 15000 });
    if (!d || !d.ok || cswState().active !== cid) return;
    if (!d.found) return;   // store/walk-in order — lifecycle is online-only, stay quiet
    cswRenderOrderLc(cid, oid, d);
  } catch (_) {}
}

function cswRenderOrderLc(cid, oid, d) {
  const el = document.getElementById('csw-olc');
  if (!el) return;
  const idx = cswOlcStageIdx(d.stage);
  const timeline = recordedOrderTimeline(d, document.getElementById('csw-orderstatus')?.dataset.placedAt);
  const trail = timeline.map(row => `<li class="csw-seven__timeline-step ${row.reached ? 'is-recorded' : ''}"><span class="csw-seven__timeline-dot" aria-hidden="true"></span><div><b>${esc(row.name)}</b><span>${row.at ? esc(cswEvidenceTime(row.at)) : esc(row.label)}</span>${row.update ? `<small>${esc(row.updateLabel || 'Customer update sent')} · ${esc(cswEvidenceTime(row.update))}</small>` : ''}</div></li>`).join('');
  const trackingHref = safeTrackingHref(d.tracking_url);
  let sub = '';
  if (d.carrier || d.tracking_url) {
    sub += `<div class="csw-olc__sub">${esc(d.carrier || 'Tracking')}${trackingHref ? ` · <a href="${esc(trackingHref)}" target="_blank" rel="noopener">Track order · ${esc(new URL(trackingHref).hostname)}</a>` : ''}</div>`;
  }
  if (d.due_now) sub += `<div class="csw-olc__sub csw-olc__due">${esc(d.due_now)} message due${d.auto_lifecycle ? '' : ' — auto-lifecycle off'}</div>`;
  if (d.reviewed_at) sub += `<div class="csw-olc__sub">Reviewed${d.incentive_code ? ` · code ${esc(d.incentive_code)}` : ''}</div>`;
  let acts = '';
  if (idx >= 0 && idx < 2) {
    acts = `<button class="csw-mini csw-mini--p" id="csw-olc-delivered">Mark delivered</button>`;
  } else if (idx >= 2 && !d.reviewed_at) {
    acts = `<button class="csw-mini" id="csw-olc-review">Record customer review</button>
      <div id="csw-olc-revform" hidden>
        <div class="csw-olc__stars" id="csw-olc-stars">${[1, 2, 3, 4, 5].map(n => `<button data-n="${n}" aria-label="${n} of 5">${n}</button>`).join('')}</div>
        <input class="csw-input" id="csw-olc-revtxt" placeholder="What did they say? (optional)"/>
        <div class="csw-hint">Saves the review + sends their 5% thank-you code.</div>
        <button class="csw-mini csw-mini--p" id="csw-olc-revsave">Save review</button>
      </div>`;
  }
  el.innerHTML = `<div class="csw-olc">
    <ol class="csw-seven__timeline" aria-label="Recorded order progress">${trail}</ol>${idx < 0 ? '<p class="csw-hint">Order stage unverified.</p>' : ''}${sub}
    ${acts ? `<div class="csw-olc__acts">${acts}</div>` : ''}
  </div>`;

  // Mark delivered — optimistic paint, revert to the pre-tap snapshot on error.
  document.getElementById('csw-olc-delivered')?.addEventListener('click', async (e) => {
    e.target.disabled = true; e.target.textContent = 'Marking…';
    cswRenderOrderLc(cid, oid, { ...d, stage: 'delivered', due_now: null });
    try {
      const r = await api(`/api/cs/orders/${encodeURIComponent(oid)}/mark-delivered`, { method: 'POST', body: JSON.stringify({}) });
      if (!r || !r.ok) throw new Error((r && r.error) || 'mark-delivered failed');
      cswLoadOrderLc(cid, oid);   // fresh truth: message stamps + review clock
    } catch (err) {
      if (cswState().active !== cid) return;
      cswRenderOrderLc(cid, oid, d);   // revert
      cswToast('Could not mark delivered', true);
    }
  });

  // Record review — rating buttons + optional quote → POST /api/cs/reviews.
  document.getElementById('csw-olc-review')?.addEventListener('click', () => {
    const f = document.getElementById('csw-olc-revform');
    if (f) f.hidden = !f.hidden;
  });
  const stars = document.getElementById('csw-olc-stars');
  stars?.addEventListener('click', (ev) => {
    const b = ev.target.closest('button[data-n]');
    if (!b) return;
    stars._rating = Number(b.dataset.n);
    stars.querySelectorAll('button').forEach(x => x.classList.toggle('on', Number(x.dataset.n) <= stars._rating));
  });
  document.getElementById('csw-olc-revsave')?.addEventListener('click', async (e) => {
    const rating = stars && stars._rating;
    if (!rating) { cswToast('Tap a rating first', true); return; }
    e.target.disabled = true; e.target.textContent = 'Saving…';
    try {
      const txt = (document.getElementById('csw-olc-revtxt')?.value || '').trim();
      const r = await api('/api/cs/reviews', {
        method: 'POST',
        body: JSON.stringify({ order_id: String(oid), product_rating: rating, free_text: txt || null }),
      });
      if (!r || !r.ok) throw new Error((r && r.error) || 'review failed');
      cswToast(`Review saved${r.incentive_code ? ' · code ' + r.incentive_code : ''}`);
      cswLoadOrderLc(cid, oid);
    } catch (err) {
      e.target.disabled = false; e.target.textContent = 'Save review';
      cswToast('Could not save review', true);
    }
  });
}

// ── Paystack settlement: payment status chip + rep actions ────────────
// Closes the WhatsApp-checkout commerce loop in the 360 panel. The rep sees
// the thread's payment state and acts: "Check payment" (customer says "I've
// paid"), "Place order" (one-tap when verified), "Mark paid — manual" (Squad/
// Cash/Transfer fallback when Paystack/EON is down). All wire to the existing
// /api/cs endpoints — no new commerce logic on the client.

// Map an intent status → an on-theme chip {label, cls}.
// 2026-09-24 (vet faults 3, 4): the chip now also carries the desk's own
// transfer cards. A customer's claim reads as their claim (it used to
// print the raw word 'payment_claimed'), and a confirmed card with no
// order behind it asks for the order instead of offering a Place order
// that cannot work.
function cswPayChip(st, needsOrder, short, confirmedBy) {
  if (st === 'success') return { label: 'Paid', cls: 'csw-pay--paid' };
  // A teammate's tap, not a bank credit (follow-up to #3543's round-3
  // read, 2 Oct 2026): the chip says who vouched, never "the bank".
  if (st === 'payment_confirmed' && confirmedBy === 'rep') {
    if (short) return { label: 'Confirmed by a teammate, not the bank — less than the order', cls: 'csw-pay--wait' };
    if (needsOrder) return { label: 'Confirmed by a teammate, not the bank — no order linked yet', cls: 'csw-pay--ok' };
    return { label: 'Confirmed by a teammate, not the bank — place order', cls: 'csw-pay--ok' };
  }
  // The order linked to this payment costs more than the money that came
  // in (adversary pass on round 2 of #3543) — the card stays open.
  if (st === 'payment_confirmed' && short) return { label: 'Paid — less than the order', cls: 'csw-pay--wait' };
  if (st === 'payment_confirmed' && needsOrder) return { label: 'Paid — no order linked yet', cls: 'csw-pay--ok' };
  if (st === 'payment_confirmed') return { label: 'Payment confirmed — place order', cls: 'csw-pay--ok' };
  if (st === 'payment_claimed') return { label: 'They say they paid — not confirmed yet', cls: 'csw-pay--wait' };
  if (st === 'pending' || st === 'initialized') return { label: 'Awaiting payment', cls: 'csw-pay--wait' };
  if (st === 'failed' || st === 'abandoned' || st === 'reversed') return { label: 'Payment failed', cls: 'csw-pay--fail' };
  // The bank listener's mid-placement state and a refund, in words; any
  // other status never prints its raw code (vet fault 20).
  if (st === 'completing') return { label: 'Paid — placing the order', cls: 'csw-pay--ok' };
  if (st === 'refunded') return { label: 'Refunded', cls: 'csw-pay--fail' };
  return { label: st ? 'Payment unclear — refresh the chat' : 'No payment link', cls: 'csw-pay--wait' };
}

// What "Check payment" found, in the rep's words (vet fault 20,
// 2026-09-24). The last branch of the check used to print the server's
// code after "Payment: " — "amount_mismatch", "abandoned_pending",
// "complete_failed" — and the rep had to guess what it meant for the sale.
function cswPayCheckWords(res) {
  const words = {
    amount_mismatch: ["The amount paid doesn't match this order, so it wasn't placed. Check the bank app, then use Mark paid with a note.", true],
    complete_failed: ["The payment is confirmed, but Shopify didn't place the order. Tap Place order again in a minute.", true],
    abandoned_pending: ["They closed the payment page without paying. A transfer can still land — check again in a few minutes.", false],
    failed: ["The payment didn't go through. Ask them to try again.", true],
    reversed: ["The bank reversed this payment. Nothing was placed — ask them how they want to pay.", true],
    no_reference: ["This payment has nothing to check against. Refresh the chat and try again.", true],
  }[String(res || '')];
  return words ? { text: words[0], bad: words[1] }
               : { text: 'Checked. Refresh the chat to see where it stands.', bad: false };
}

async function cswLoadPayment(cid) {
  let d;
  try { d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/payment-status`, { timeoutMs: 15000 }); }
  catch (_) { return; }
  if (!d || !d.ok || cswState().active !== cid) return;
  cswRenderPayment(cid, d);
}

function cswRenderPayment(cid, d) {
  const el = document.getElementById('csw-paystatus');
  if (!el) return;
  // No payment link on this thread → offer only the manual fallback (a rep can
  // always mark an out-of-band payment), kept compact.
  if (!d.found) {
    el.innerHTML = `<div class="csw-pay">
      <div class="csw-pay__row"><span class="csw-pay__chip csw-pay--none">No payment link</span></div>
      <div class="csw-pay__acts"><button class="csw-mini" id="csw-pay-manual">Mark paid — manual</button></div>
    </div>`;
    document.getElementById('csw-pay-manual')?.addEventListener('click', () => cswPayManualPicker(cid));
    return;
  }
  const st = d.status;
  const chip = cswPayChip(st, !!d.needs_order, !!d.order_short, d.confirmed_by);
  const amt = d.amount_ngn != null ? ` · ${cswNgn(d.amount_ngn)}` : '';
  let acts = '';
  // An order placed on this chat waits for the rep's yes, on a paid card
  // or on one still waiting for its money (2 Oct 2026): a card is never
  // tied to an order on amount alone.
  const waitingCard = st === 'pending' || st === 'initialized' || st === 'payment_claimed';
  const offer = ((st === 'payment_confirmed' || waitingCard) && d.needs_order && d.link_offer && d.link_offer.order_id) ? d.link_offer : null;
  if (st === 'success') {
    // Terminal — order placed. Show the method, no actions.
    acts = `<div class="csw-pay__done">Order placed${d.paid_method ? ' · ' + esc(d.paid_method) : ''}</div>`;
  } else if (st === 'payment_confirmed' && d.order_short) {
    // The order linked to this payment costs more than the money that
    // came in (adversary pass on round 2 of #3543). Nothing here closes
    // it: the rest has to come in first.
    const on = d.order_name ? 'Order ' + esc(String(d.order_name)) : 'The order';
    const tot = d.order_total_ngn != null ? cswNgn(d.order_total_ngn) : '';
    const got = d.paid_ngn != null ? cswNgn(d.paid_ngn) : '';
    acts = `<div class="csw-pay__done">${tot && got ? `${on} is ${esc(tot)}, but ${esc(got)} came in.` : `${on} costs more than the money that came in.`} Collect the rest, then mark the order paid in Shopify.</div>`;
  } else if (offer) {
    // An order was placed on this chat after the money was confirmed
    // (adversary pass on round 2 of #3543). A paid card is never tied to
    // a later order on its own — the next same-priced sale could be a
    // new, unpaid one — so the rep says whether it is this payment's.
    const on = offer.order_name ? 'order ' + esc(String(offer.order_name)) : 'the order';
    const ask = waitingCard
      ? `${on[0].toUpperCase() + on.slice(1)} was placed on this chat. Is it the order this transfer pays for?`
      : `${on[0].toUpperCase() + on.slice(1)} was placed on this chat after this payment. Is it for this payment?`;
    acts = `<div class="csw-pay__done">${ask}</div>
            <button class="csw-mini csw-mini--p" id="csw-pay-link">Yes — link ${on}</button>`
      + (waitingCard ? `<button class="csw-mini" id="csw-pay-check">Check payment</button>` : '');
  } else if (st === 'payment_confirmed' && d.needs_order) {
    // The money is in (bank or a rep's check) but this card was opened
    // with no order behind it. Say so and name where orders are placed —
    // no one-tap "build" key: the sale may already be rung on the till,
    // and a key here would invite a second order for one payment.
    acts = `<div class="csw-pay__done">If the order isn't in yet, place it — Tray → Order, or the till.</div>`;
  } else if (st === 'completing') {
    // The bank confirmed it and the order is going in (adversary pass on
    // #3543). No Check payment — it would only say "not paid" beside a
    // Paid chip. No Mark paid either (Fable 5.1 vet, 2026-09-27): it
    // stamped the bank's money as the rep's and counted it twice. A
    // placement that died is handed back after 15 minutes, and Place
    // order then works.
    acts = `<div class="csw-pay__done">The bank confirmed it. The order is going in now — refresh in a minute. If it's still not in after 15 minutes, Place order comes back. Tap that.</div>`;
  } else if (st === 'payment_confirmed') {
    // Verified paid, flag-OFF path → one-tap place. Manual still available.
    acts = `<button class="csw-mini csw-mini--p" id="csw-pay-place">Place order</button>
            <button class="csw-mini" id="csw-pay-manual">Mark paid — manual</button>`;
  } else {
    // Awaiting / pending / failed → "Check payment" + manual fallback.
    acts = `<button class="csw-mini csw-mini--p" id="csw-pay-check">Check payment</button>
            <button class="csw-mini" id="csw-pay-manual">Mark paid — manual</button>`;
  }
  // The order placed from this chat is linked to the card, and the money
  // isn't confirmed yet (round 2 on #3543) — say the order is in.
  if (st !== 'success' && d.order_linked && !d.order_short) {
    acts = `<div class="csw-pay__done">Order${d.order_name ? ' ' + esc(d.order_name) : ''} is in. This payment is linked to it.</div>` + acts;
  }
  el.innerHTML = `<div class="csw-pay">
    <div class="csw-pay__row"><span class="csw-pay__chip ${chip.cls}">${esc(chip.label)}</span><span class="csw-pay__amt">${amt}</span></div>
    <div class="csw-pay__acts">${acts}</div>
  </div>`;

  document.getElementById('csw-pay-check')?.addEventListener('click', async (e) => {
    e.target.disabled = true; e.target.textContent = 'Checking…';
    try {
      const r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/check-payment`, { method: 'POST', timeoutMs: 25000 });
      if (cswState().active !== cid) return;
      const res = r && (r.result || (r.found === false ? 'none' : ''));
      if (!r || !r.ok) { cswToast('Could not check payment', true); e.target.disabled = false; e.target.textContent = 'Check payment'; return; }
      if (r.found === false) { cswToast('No payment link on this thread'); }
      else if (res === 'completed') { cswToast('Confirmed — order placed'); setTimeout(() => cswLoadTranscript(cid, true), 1400); }
      else if (res === 'payment_confirmed' && r.order_short) { cswToast('Paid, but less than the order linked to it. Check the amount with the customer.', true); }
      // A teammate confirmed it and the bank has not (2 Oct 2026): never
      // say "the bank" for a rep's tap.
      else if (res === 'payment_confirmed' && r.confirmed_by === 'rep') { cswToast(r.needs_order ? 'A teammate confirmed this payment. The bank hasn’t yet. Place the order if it isn’t in.' : 'A teammate confirmed this payment. The bank hasn’t yet. Tap Place order.'); }
      else if (res === 'payment_confirmed' && r.needs_order) { cswToast('The bank confirmed it — place the order if it isn’t in yet'); }
      else if (res === 'payment_confirmed') { cswToast('Confirmed — tap Place order'); }
      else if (res === 'completing') { cswToast('The bank confirmed it — the order is going in now'); }
      else if (res === 'still_pending') { cswToast('Not paid yet — still pending'); }
      else { const w = cswPayCheckWords(res); cswToast(w.text, w.bad); }
      cswLoadPayment(cid);   // re-render with the fresh status
    } catch (err) { cswToast('Could not check payment', true); e.target.disabled = false; e.target.textContent = 'Check payment'; }
  });

  document.getElementById('csw-pay-place')?.addEventListener('click', async (e) => {
    if (!d.reference) { cswToast('No payment reference', true); return; }
    e.target.disabled = true; e.target.textContent = 'Placing…';
    try {
      // The chat rides along: the server places only THIS chat's payment.
      const r = await api(`/api/cs/orders/${encodeURIComponent(d.reference)}/complete-confirmed`, { method: 'POST', body: JSON.stringify({ zoko_customer_id: cid }), timeoutMs: 30000 });
      if (!r || !r.ok) throw new Error((r && r.error) || 'place failed');
      cswToast(`Order placed${r.order_name ? ' · ' + r.order_name : ''}`);
      prcBagConfirm(r.shopify_order_id || r.order_id);
      cswLoadPayment(cid); cswLoadOrderStatus(cid); setTimeout(() => cswLoadTranscript(cid, true), 1400);
    } catch (err) {
      // The server's refusal is a sentence written for the rep (vet fault
      // 20) — show it. A timeout or a dropped line keeps the short line.
      const why = err && err.status >= 400 && err.status < 500 && err.status !== 408 && err.message;
      cswToast(why ? String(why).slice(0, 180) : 'Could not place order', true);
      e.target.disabled = false; e.target.textContent = 'Place order';
    }
  });

  // "Yes — link order #…" (adversary pass on round 2 of #3543): ties the
  // order offered to this chat's card, paid or still waiting for its
  // money (2 Oct 2026). The server reads the order's
  // total itself; the page sends only which card and which order.
  document.getElementById('csw-pay-link')?.addEventListener('click', async (e) => {
    const label = e.target.textContent;
    e.target.disabled = true; e.target.textContent = 'Linking…';
    try {
      const r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/payment/link-order`,
        { method: 'POST', body: JSON.stringify({ reference: d.reference, order_id: offer && offer.order_id }), timeoutMs: 20000 });
      if (!r || !r.ok) throw new Error('not linked');
      const on = r.order_name ? 'order ' + r.order_name : 'the order';
      cswToast(r.settled ? `Linked. The payment is on ${on}.`
        : waitingCard ? `Linked to ${on}. When the transfer lands, it pays for it.` : `Linked to ${on}.`);
      cswLoadPayment(cid);
    } catch (err) {
      const why = err && err.status >= 400 && err.status < 500 && err.status !== 408 && err.message;
      cswToast(why ? String(why).slice(0, 180) : 'Couldn’t link the order. Try again in a minute.', true);
      e.target.disabled = false; e.target.textContent = label;
    }
  });

  document.getElementById('csw-pay-manual')?.addEventListener('click', () => cswPayManualPicker(cid, d));
}

// Manual mark-paid: a small method picker (Squad / Cash / Transfer). The
// fallback when Paystack/EON is down — places the order out-of-band.
// Audit M12: TWO-STEP — picking a method shows an explicit confirm with the
// amount ("Mark ₦82,500 as paid via Squad?") before anything is posted.
// This is money-inflow recognition; a single mis-tap used to place a real
// paid order silently (the exact "silent write" class the bulk-approve
// incident guard exists to prevent).
function cswPayManualPicker(cid, intent) {
  const methods = [['squad', 'Squad'], ['transfer', 'Transfer'], ['cash', 'Cash']];
  const amt = (intent && intent.amount_ngn != null)
    ? '₦' + Math.round(Number(intent.amount_ngn) || 0).toLocaleString() : '';
  // A card with no order behind it (adversary pass on #3543): the tap
  // records the payment and places nothing, so the words say that — the
  // confirm used to promise "a real, paid order" that never came.
  const noOrder = !!(intent && intent.needs_order);
  // The order placed from this chat is already linked to the card (round
  // 2 on #3543): the tap records the payment against it, nothing new.
  const linkedOrder = !noOrder && !!(intent && intent.order_linked);
  const orderName = linkedOrder && intent.order_name ? ' ' + esc(String(intent.order_name)) : '';
  const intro = noOrder
    ? 'Records that the customer paid. No order is linked to this payment, so nothing is placed — place it in Tray → Order, or on the till.'
    : linkedOrder
      ? `Records that the customer paid for the order${orderName} already placed from this chat. Nothing new is placed.`
      : 'Fallback for when Paystack or EON is down. Confirms the customer paid out-of-band and places the order.';
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Mark paid — manual</div><button class="csw-x">✕</button></div>
    <div style="font-size:11.5px;color:var(--ink-mute);margin-bottom:12px">${intro}${amt ? ' Total: <b>' + amt + '</b>.' : ''}</div>
    <div class="csw-paymethods">${methods.map(([v, l]) => `<button class="csw-paymethod" data-m="${v}">${l}</button>`).join('')}</div>
    <div class="csw-payconfirm" id="csw-payconfirm" style="display:none;margin-top:12px">
      <div class="csw-payconfirm__q" id="csw-payconfirm-q" style="font-size:12.5px;margin-bottom:8px"></div>
      <button class="csw-mini csw-mini--p" id="csw-payconfirm-yes">Confirm — mark paid</button>
      <button class="csw-mini" id="csw-payconfirm-no">Cancel</button>
    </div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  let picked = null;
  const confirmBox = ov.querySelector('#csw-payconfirm');
  const confirmQ = ov.querySelector('#csw-payconfirm-q');
  ov.querySelectorAll('.csw-paymethod').forEach(b => b.addEventListener('click', () => {
    picked = b.dataset.m;
    ov.querySelectorAll('.csw-paymethod').forEach(x => x.classList.toggle('sel', x === b));
    const label = methods.find(m => m[0] === picked)[1];
    confirmQ.innerHTML = noOrder
      ? `Mark ${amt ? '<b>' + amt + '</b> ' : 'this payment '}as paid via <b>${label}</b>? This records it. No order is placed.`
      : linkedOrder
        ? `Mark ${amt ? '<b>' + amt + '</b> ' : 'this payment '}as paid via <b>${label}</b>? This records it against the order${orderName} already placed. No new order is placed.`
        : `Mark ${amt ? '<b>' + amt + '</b> ' : 'this order '}as paid via <b>${label}</b>? This places a real, paid order.`;
    confirmBox.style.display = '';
  }));
  ov.querySelector('#csw-payconfirm-no').addEventListener('click', () => {
    picked = null; confirmBox.style.display = 'none';
    ov.querySelectorAll('.csw-paymethod').forEach(x => x.classList.remove('sel'));
  });
  ov.querySelector('#csw-payconfirm-yes').addEventListener('click', async (e) => {
    if (!picked) return;
    const method = picked;
    e.target.disabled = true; e.target.textContent = (noOrder || linkedOrder) ? 'Saving…' : 'Placing…';
    ov.querySelectorAll('.csw-paymethod').forEach(x => { x.disabled = true; });
    try {
      const r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/mark-paid-manual`, { method: 'POST', body: JSON.stringify({ method }) });
      if (!r || !r.ok) throw new Error((r && r.error) || 'mark-paid failed');
      ov.remove();
      // A card with no order behind it records the payment and places
      // nothing (vet fault 4) — say that, not "Marked paid".
      if (r.placed === false) cswToast(r.message || 'Payment noted. No order is linked to it yet.');
      else if (r.receipt_note) cswToast(r.receipt_note, true);
      else cswToast(`Marked paid (${method})${r.order_name ? ' · ' + r.order_name : ''}`);
      prcBagConfirm(r.shopify_order_id || r.order_id);
      cswLoadPayment(cid); cswLoadOrderStatus(cid); setTimeout(() => cswLoadTranscript(cid, true), 1400);
    } catch (err) {
      // Round 2 (2026-09-26): one plain sentence. A lost answer may have
      // placed the order anyway, so it says check first.
      cswToast(cswSayWhy(err, { unsure: 'Not sure that went through. Refresh the chat before marking it paid again.', otherwise: 'Not marked paid. Try again.' }), true);
      e.target.disabled = false; e.target.textContent = 'Confirm — mark paid';
      ov.querySelectorAll('.csw-paymethod').forEach(x => { x.disabled = false; });
    }
  });
}

// Resolve LTV via captured phone/email (works for Instagram threads,
// not just WhatsApp phone). Updates the 360 stats + identity chips.
async function cswLoadLtv(cid) {
  const s = cswState(); const t = s.activeThread || {};
  try {
    const d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/ltv?platform_sender_id=${encodeURIComponent(t.platform_sender_id || '')}`);
    if (!d || !d.ok || cswState().active !== cid) return;
    const oEl = document.getElementById('csw-stat-orders');
    const lEl = document.getElementById('csw-stat-ltv');
    if (d.found && d.identity) {
      const id = d.identity;
      if (oEl) oEl.textContent = id.total_orders || 0;
      if (lEl) lEl.textContent = id.total_spend_ngn != null ? cswNgn(id.total_spend_ngn) : '—';
      const chips = document.getElementById('csw-id-chips');
      if (chips) {
        const c = (on, l) => `<span class="csw-chip ${on ? 'on' : ''}">${l}</span>`;
        // L5: POS chip retired here too (7 of 20,361 identities ever lit it).
        chips.innerHTML = c(id.seen_in_shopify, 'Shopify') + c(id.seen_in_zoko, 'WhatsApp') + (d.matched_on ? `<span class="csw-chip on">via ${esc(d.matched_on)}</span>` : '');
      }
    }
    // G5: where this customer came from — the click-to-WhatsApp ad they
    // tapped to start the chat. Cache it (keyed by thread) so cswRender360's
    // innerHTML rebuilds re-paint it instead of wiping it, then render now.
    cswState()._source360 = { cid, src: d.source };
    cswRenderSource(d.source);
  } catch (_) {}
}

// The thread's WEB story (mig 537 claim-code handoff): what this customer
// saved on crux.ng before saying hello. Same stable-host pattern as the
// source badge; absent claim (most threads) renders nothing.
async function cswLoadCircle(cid) {
  const s = cswState(); const t = s.activeThread || {};
  try {
    const d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/circle?platform_sender_id=${encodeURIComponent(t.platform_sender_id || '')}`);
    if (!d || cswState().active !== cid) return;
    cswState()._circle360 = { cid, d };
    cswRenderCircle(d);
  } catch (_) {}
}

function cswRenderCircle(d) {
  const el = document.getElementById('csw-circle-badge');
  if (!el) return;
  if (!d || !d.found) { el.className = ''; el.innerHTML = ''; return; }
  el.className = 'csw-src';
  const bits = [];
  if ((d.pieces || []).length) bits.push(`${d.pieces.length} saved piece${d.pieces.length > 1 ? 's' : ''}`);
  if (d.looks_saved) bits.push(`${d.looks_saved} saved look${d.looks_saved > 1 ? 's' : ''}`);
  if (d.fires) bits.push(`${d.fires} fired`);
  const links = (d.pieces || []).slice(0, 3).filter(p => p.handle)
    .map(p => `<a href="https://crux.ng/products/${esc(p.handle)}" target="_blank" rel="noopener">${esc(p.handle.replace(/-/g, ' '))}</a>`)
    .join(' · ');
  el.innerHTML = `<span class="csw-src__dot"></span><span class="csw-src__t">Web: ${esc(bits.join(' · ') || 'claimed device')}${links ? ` — ${links}` : ''}</span>`;
}

// Paint the "came from ad X" badge into its stable host in the 360 panel.
// Host lives inside the cswRender360 template, so this just fills it (no
// sibling inserts → survives re-render). Empty/absent source clears it.
function cswRenderSource(src) {
  const el = document.getElementById('csw-src-badge');
  if (!el) return;
  if (!src || !src.found) { el.className = ''; el.innerHTML = ''; el.removeAttribute('title'); return; }
  el.className = 'csw-src';
  const when = cswAgo(src.clicked_at);
  el.title = (src.body || src.headline || '') + (src.ad_id ? ` (ad ${src.ad_id})` : '');
  el.innerHTML = `<span class="csw-src__dot"></span><span class="csw-src__t">From: ${esc(src.label || 'Click-to-WhatsApp ad')}${when ? ` · clicked ${esc(when)}` : ''}</span>`;
}

// Compact "3d ago" for an ISO timestamp; '' when unparseable.
function cswAgo(iso) {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (isNaN(t)) return '';
  const mins = Math.max(0, Math.floor((Date.now() - t) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

async function cswLoadNotes(cid) {
  try {
    const d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/notes`);
    cswState().notes = d.notes || [];
    const pin = d.pinned;
    const el = document.getElementById('csw-pinnote');
    if (el) el.innerHTML = pin ? `<div class="csw-pinnote"><b>Pinned note</b><br>${esc(pin.body)}</div>` : '';
    if (cswState().active === cid) cswRenderNotesPanel(cid);
  } catch (_) {}
}

// Editable notes surface in the 360 side panel — notes are an internal,
// private memory ("prefers oversized", "VIP", "returned once") that were
// previously buried behind the Note tool. Visible + add-inline here.
function cswRenderNotesPanel(cid) {
  const host = document.getElementById('csw-notes-panel');
  if (!host) return;
  const notes = cswState().notes || [];
  host.innerHTML = `
    <div class="csw-notes__hd">Notes <span class="csw-notes__count">${notes.length || ''}</span></div>
    <div class="csw-notes__list">${notes.length
      ? notes.map(n => `<div class="csw-noteitem"><span>${esc(n.body)}</span><button class="csw-noteitem__x" data-id="${esc(n.id || '')}" aria-label="Delete">✕</button></div>`).join('')
      : '<div class="csw-notes__empty">No notes yet — add a quick reminder.</div>'}</div>
    <div class="csw-notes__add">
      <input class="csw-notes__inp" id="csw-noteadd-inp" placeholder="Add a note…" maxlength="280"/>
      <button class="csw-notes__btn" id="csw-noteadd-btn" aria-label="Save note">+</button>
    </div>`;
  const inp = host.querySelector('#csw-noteadd-inp');
  const save = async () => {
    const body = (inp.value || '').trim();
    if (!body) return;
    inp.value = '';
    try { await api(`/api/cs/threads/${encodeURIComponent(cid)}/notes`, { method: 'POST', body: JSON.stringify({ body }) }); cswLoadNotes(cid); cswToast('Note saved'); }
    catch (e) { cswToast('Could not save note', true); }
  };
  host.querySelector('#csw-noteadd-btn').addEventListener('click', save);
  inp.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); save(); } });
  host.querySelectorAll('.csw-noteitem__x').forEach(b => b.addEventListener('click', async () => {
    const id = b.dataset.id; if (!id) return;
    try { await api(`/api/cs/threads/${encodeURIComponent(cid)}/notes/${encodeURIComponent(id)}`, { method: 'DELETE' }); cswLoadNotes(cid); }
    catch (e) { cswToast('Could not delete note', true); }
  }));
}

// ── sending ──────────────────────────────────────────────────
// Optimistic reply flip (2026-07-25): the moment ANY send succeeds, the
// thread stops being "waiting" locally — dot, counts, title and flame all
// clear NOW instead of on the next 10-30s poll (which read as "the dot
// ignores my reply"). The poll then reconciles with the server truth.
function cswMarkReplied(cid) {
  cswClaimCardFor(cid);   // vet 17: a rep's first send from a card's chat claims it
  const s = cswState();
  _cswRungReads.delete(String(cid));
  const t = (s.threads || []).find(x => String(x.zoko_customer_id) === String(cid));
  if (!t) return;
  t.unreplied = false; t._waiting = false; t._hot = false; t._waitm = 0;
  t.last_direction = 'outbound';
  try { cswRenderThreadList(); } catch (_) {}
  // Vet 2026-08-09: this counted EVERY waiting thread — including the
  // >72h recovery tail, and excluding promise-only threads — while the
  // chip's owning painter counts the fresh lane the list actually shows.
  // Harmless while the id existed only on classic's hidden row; the
  // Needs-reply lens key put it on the default skin, where one send made
  // the chip jump to the all-waiting number over a fresh-lane list. Same
  // formula as the owner now (see the counts pass above).
  const c = document.getElementById('csw-c-awaiting');
  const n = (s.threads || []).filter(x =>
    (x._waiting && (x._waitm || 0) <= CSW_RECOVERY_MIN) || (!x._waiting && x._promise)).length;
  if (c) c.textContent = n;
  s.lastAwaiting = n;
  try { document.title = n > 0 ? `(${n}) CS Workspace · EON` : 'CS Workspace · EON'; } catch (_) {}
}

async function cswSendText() {
  const s = cswState();
  const inp = document.getElementById('csw-input');
  const cid = s.active;
  if (!cid || !inp) return;
  const typed = (inp.value || '').trim();
  if (!typed) return;
  if (s._contactRehearsal || (s._contact?.cid === cid && s._contact.data?.rehearsal)) {
    await cswContactRehearseReply(cid, typed);
    return; // Keep both the draft and the real conversation exactly as they are.
  }
  // @eon command line — rep-facing, NOT sent to the customer.
  if (/^@?eon\b/i.test(typed)) {
    inp.value = ''; inp.style.height = 'auto'; cswClearReply(); cswSaveDraft(cid, '');
    cswSyncComposerKeys();   // a programmatic clear fires no `input` event
    const sb = document.getElementById('csw-send'); if (sb) sb.disabled = true;
    cswAssistRun(typed);
    return;
  }
  // @-routing: a teammate/department @-mention (NOT @eon) routes THIS
  // customer chat to them: re-queues the thread to their queue AND DMs them
  // a link. Internal-only, NEVER sent to the customer.
  if (cswHasStaffMention(typed)) { cswRouteToHandles(typed); return; }
  if (inp.disabled || cswWindowBlocks(s.activeThread)) {
    cswSaveDraft(cid, typed);
    cswSyncWindowComposer();
    if (cswReplyWindow(s.activeThread).state === 'closed') {
      await cswHoldReply(cid, typed);
    } else {
      cswToast('Draft kept. Customer sending waits for a verified open reply window.', true);
    }
    const wc = document.getElementById('csw-winclosed'); if (wc) wc.hidden = false;
    return;
  }
  // Quote (2026-07-27): with a wamid we send a TRUE WhatsApp quoted
  // reply (Cloud context.message_id) — the customer sees the real
  // quote bubble. Only mid-less messages keep the old text prefix.
  const rt = s.replyTo;
  const quoteMid = rt && rt.mid ? rt.mid : '';
  const text = rt && rt.text && !quoteMid
    ? `↪ ${rt.who ? rt.who + ': ' : ''}"${rt.text.slice(0, 120)}"\n${typed}`
    : typed;
  const handle = cswDraftCapture(cid), data = cswDraftStore(handle);
  if (!data || State._csDraftStorageFailed) { cswToast('Reply kept. Sign in again or restore device storage before sending.', true); return; }
  let legacy;
  try { legacy = cswDraftLegacy(); } catch (_) { cswToast('Reply kept. This device could not read the send record.', true); return; }
  const channel = JSON.parse(handle.slot)[0];
  const legacyHold = legacy.threads.some(r => r.cid === String(cid) && (!r.channel || r.channel === channel));
  let operation = data.pending[handle.slot];
  const requestedPayload = quoteMid ? {text,quote_message_id:quoteMid} : {text};
  const changedQuote = operation && rt && JSON.stringify(operation.body) !== JSON.stringify(requestedPayload);
  if (s._sendingNow?.[cid]) {
    // The last send is still going. This reply follows it once that one is
    // confirmed, if the chat, login, words and quote are still the same.
    const rev = cswSaveDraft(cid, typed, handle);
    if (!rev) return;
    (s._sendNext = s._sendNext || {})[cid] = {after:s._sendingNow[cid],words:typed,quote:JSON.stringify(rt || null),rev,inp,
      scope:handle.scope,authSession:handle.authSession,generation:handle.generation};
    cswToast('Reply kept. It goes as soon as the last one is confirmed.');
    return;
  }
  let newAction = false;
  if (legacyHold || changedQuote || (operation && (!operation.body || operation.w !== typed)) || (operation && s._closedFailedQuote?.[cid] === operation.actionId)) {
    cswSaveDraft(cid, typed, handle); cswDraftRecovery(cid);
    if (!window.confirm('An earlier send is still not confirmed. Review this chat first to avoid sending twice. Send these words as a NEW message?')) return;
    if (!cswDraftCurrent(handle)) return;
    if (operation && !cswDraftArchive(handle, operation)) { cswToast('Reply kept. The earlier send record could not be saved.', true); return; }
    operation = null; newAction = true;
  }
  if (!operation && legacy.unknown) {
    cswSaveDraft(cid, typed, handle); cswDraftRecovery(cid);
    if (!window.confirm('An older send has lost its conversation record. Check this chat first. Send this reply as a new message?')) return;
    newAction = true; // Never settles the unknown old send.
  }
  const earlierUncertain = !!operation;
  const path = `/api/cs/threads/${encodeURIComponent(cid)}/send`;
  const payload = operation?.body || requestedPayload;
  const draftRev = cswSaveDraft(cid, typed, handle);
  if (!draftRev) return;
  if (!operation) {
    const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify([handle.scope,path,payload])));
    if (!cswDraftCurrent(handle) || s.active !== cid || inp !== document.getElementById('csw-input') || inp.value.trim() !== typed) return;
    const fingerprint = Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2,'0')).join('');
    const actionKey = 'eon.contact.action.' + fingerprint;
    let actionId;
    try { actionId = (!newAction && sessionStorage.getItem(actionKey)) || crypto.randomUUID(); sessionStorage.setItem(actionKey, actionId); }
    catch (_) { cswToast('Reply kept. This device could not keep the send record.', true); return; }
    operation = {w:typed,at:Date.now(),body:payload,quote:rt ? {...rt,words:typed} : null,actionId,fingerprint};
  }
  operation = {...operation,status:'pending',draftRev};
  if (!cswKeepSending(cid, operation)) { cswToast('Reply kept. This device could not keep the send record.', true); return; }
  inp.value = ''; inp.style.height = 'auto'; cswClearReply();
  (s._sendingNow = s._sendingNow || {})[cid] = operation.actionId;
  cswSyncComposerKeys();
  const sb = document.getElementById('csw-send'); if (sb) sb.disabled = true;
  const body = document.getElementById('csw-script');
  let pending = null;
  if (body) {
    body.insertAdjacentHTML('beforeend', `<div class="csw-row out"><div class="csw-bub out pending"><div class="csw-bub__txt">${esc(payload.text)}</div><div class="csw-bub__t">⏱</div></div></div>`);
    pending = body.lastElementChild; body.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
  }
  try {
    const result = await cswSendApi(path, {method:'POST',
      body:JSON.stringify({...payload,contact_action_id:operation.actionId}),
      _contactActionKey:'eon.contact.action.' + operation.fingerprint,
      _isCurrent:() => cswDraftCurrent(handle),
      // The rep's yes to "Send it again?" is a new action; the old one stays held.
      _onContactAction: actionId => {
        if (!cswDraftCurrent(handle) || !cswDraftArchive(handle, operation)) throw new Error('Send record unavailable');
        operation = {...operation,actionId};
        if (!cswKeepSending(cid, operation)) throw new Error('Send record unavailable');
        s._sendingNow[cid] = actionId;
      }});
    if (result?.ok !== true) throw new Error('Send outcome unavailable');
    cswDraftFinish(handle, operation, true);
    if (!cswDraftCurrent(handle)) return;
    if (s._sendingNow?.[cid] === operation.actionId) delete s._sendingNow[cid];
    if (s._failedQuote) delete s._failedQuote[cid];
    cswMarkReplied(cid); cswDraftRecovery(cid);
    if (pending) { const b = pending.querySelector('.csw-bub'); if (b) { b.classList.remove('pending'); const ts = b.querySelector('.csw-bub__t'); if (ts) ts.innerHTML = esc(cswMsgTime(new Date().toISOString())) + '<span class="csw-tick">✓</span>'; } }
    setTimeout(() => { if (cswDraftCurrent(handle)) cswLoadTranscript(cid, true); }, 2500);
    cswDraftSendNext(cid, operation.actionId);
  } catch (e) {
    cswDraftFinish(handle, operation, false, !earlierUncertain && cswDraftNotSent(e));
    if (!cswDraftCurrent(handle)) return;
    if (s._sendingNow?.[cid] === operation.actionId) delete s._sendingNow[cid];
    // Failed payload/quote/action remain together, separate from newer words.
    if (operation.quote) (s._failedQuote = s._failedQuote || {})[cid] = operation.quote;
    const box = s.active === cid ? document.getElementById('csw-input') : null;
    if (box && !box.value.trim() && cswDraftRevision(cid) === operation.draftRev) {
      box.value = typed; box.dispatchEvent(new Event('input')); cswArmFailedQuote(cid, typed);
    }
    if (pending) pending.remove();
    cswDraftRecovery(cid);
    cswToast(cswSayWhy(e, {send:true}), true);
    cswDraftSendNext(cid, null);
  }
}
// One reply sent while the last one was still going follows it, once, right
// after that one is confirmed. Anything else keeps it in the box as a draft.
function cswDraftSendNext(cid, confirmedId) {
  const s = cswState(), next = s._sendNext?.[cid];
  if (!next) return;
  delete s._sendNext[cid];
  if (!confirmedId || next.after !== confirmedId) {
    cswToast('Your next reply was not sent: the one before it is not confirmed. It is kept as a draft.', true);
    return;
  }
  const handle = cswDraftCapture(cid), inp = document.getElementById('csw-input');
  if (s.active !== cid || inp !== next.inp || !cswDraftCurrent(handle) || handle.scope !== next.scope
      || handle.authSession !== next.authSession || handle.generation !== next.generation) {
    cswToast('Your next reply was not sent because its chat closed. It is kept as a draft there.', true);
    return;
  }
  if (s._sendingNow?.[cid] || inp.value.trim() !== next.words || JSON.stringify(s.replyTo || null) !== next.quote
      || cswDraftRevision(cid) !== next.rev) {
    cswToast('Your next reply was not sent because it changed. It is kept as a draft.', true);
    return;
  }
  cswSendText();
}

// True when `txt` carries a real staff/department @-mention (NOT @eon, NOT an
// email). Mirrors staff_chat._MENTION_RE: a leading-boundary '@' then a letter,
// so "name@gmail.com" / "gift@frank" never trigger. '@eon' is handled earlier.
function cswHasStaffMention(txt) {
  const re = /(?:^|[^\w.@])@([A-Za-z][\w .'-]{1,40})/g;
  let m;
  while ((m = re.exec(txt || ''))) {
    if (!/^eon\b/i.test((m[1] || '').trim())) return true;
  }
  return false;
}

// @-route THIS customer chat to the @'d teammate(s)/department(s): POST
// /api/cs/threads/{platform_sender_id}/route-to. Re-queues the thread to
// their queue AND DMs them a link (server reuses cs_routing.reroute +
// staff_chat.send_message; their reply returns to the originating rep's
// inbox). Internal-only: nothing is sent to the customer.
async function cswRouteToHandles(typed) {
  const s = cswState();
  const inp = document.getElementById('csw-input');
  const t = s.activeThread || {};
  const sid = t.platform_sender_id;
  const cid = s.active;
  if (!sid) { cswToast('No sender id on this thread, cannot route', true); return; }
  // Clear the composer + dismiss the picker (don't lock; routing is quick).
  if (inp) { inp.value = ''; inp.style.height = 'auto'; }
  cswSyncComposerKeys();   // a programmatic clear fires no `input` event
  const box = document.getElementById('csw-cust-mention'); if (box) { box.hidden = true; box.innerHTML = ''; }
  cswClearReply(); cswSaveDraft(cid, '');
  const sb = document.getElementById('csw-send'); if (sb) sb.disabled = true;
  const payload = {
    body: typed,
    zoko_customer_id: t.zoko_customer_id || cid || null,
    customer_name: cswDisplayName(t) || t.customer_name || null,
  };
  // Carry the acting staff_id when we have one (Staff OS context) so the
  // routed teammate's reply returns to THIS rep; the server falls back to
  // resolving by email otherwise.
  if (typeof sosState !== 'undefined' && sosState && sosState.cur) payload.by_staff_id = sosState.cur;
  let d;
  try {
    d = await api(`/api/cs/threads/${encodeURIComponent(sid)}/route-to`, { method: 'POST', body: JSON.stringify(payload) });
  } catch (e) {
    cswToast(cswSayWhy(e, { lead: 'Could not route.' }), true);
    if (inp) { inp.value = typed; inp.dispatchEvent(new Event('input')); inp.focus(); }
    return;
  }
  // Reflect the new queue on the route chip + threads.
  if (d && d.queue) {
    t.route = Object.assign({}, t.route, { queue: d.queue });
    const rb = document.getElementById('csw-routebtn');
    if (rb) { rb.textContent = cswQueueLabel(t) || 'Route'; rb.classList.toggle('routed', !!t.route); }
    cswLoadQueues(); cswRenderThreadList();
  }
  const who = (d && d.recipients && d.recipients.length)
    ? (d.recipients.length + ' teammate' + (d.recipients.length > 1 ? 's' : ''))
    : 'the team';
  cswToast((d && d.hint) || ('Routed to ' + who), !!(d && d.hint && !d.routed && !(d.recipients || []).length));
}

// ONE window dial for the conversation bar (design pass 02): a tiny draining
// ring replaces the three banner treatments (open pill / closing pill / CTWA
// chip). Ring = fraction of the free window left. Mint while the CTWA 72h
// free-marketing window is live (this thread came from an ad click — Meta
// lets us message FREE for 72h, then templates cost), amber when the 24h
// window is under 2h, red ring when closed (the composer's template gate
// carries the CTA — behavior unchanged).
// Where this customer came from (operator 2026-07-24: the rep should see
// which page sent them). site_ref = a tap on the website's WhatsApp
// button — the beacon stores the product page. Quiet chip; the CTWA ad
// origin already rides the window dial above.
function cswSourceChip(t) {
  if (!t || !t.site_ref_at) return '';
  const page = t.site_ref_product
    ? `the ${String(t.site_ref_product).replace(/-/g, ' ')} page`
    : 'the website';
  return `<span class="csw-src" title="Tapped the WhatsApp button on ${esc(page)} before this chat">via ${esc(page)}</span>`;
}

// Two names, two facts (operator 2026-07-29). The name on WhatsApp is what
// the rep recognises; the name on the orders is who this is. The backend
// (cs_inbox._fill_dual_names) sets profile_name + checkout_name ONLY when it
// holds both and they genuinely differ — 'Dinero' answering to 'Chukwudi
// Okeke' — so a rep who is about to type a greeting can see it is one person.
// Absent otherwise: no chip is drawn when the desk knows only one name.
function cswAltName(t) {
  const ck = String((t && t.checkout_name) || '').trim();
  const pf = String((t && t.profile_name) || '').trim();
  if (!ck || !pf) return '';
  return `<span class="csw-src" title="Their orders are under ${esc(ck)}; ${esc(pf)} is the name on their ${cswPlatform(t) === 'wa' ? 'WhatsApp' : 'Instagram'} profile">orders: ${esc(ck)}</span>`;
}

function cswWindowDial(t) {
  const m = cswWindowMins(t);
  const ctwaMin = t && t.ctwa_free_until ? Math.floor((new Date(t.ctwa_free_until) - Date.now()) / 60000) : 0;
  if (ctwaMin > 0 && m !== null && m > 0) {
    const label = ctwaMin < 120 ? `${Math.floor(ctwaMin / 60) ? Math.floor(ctwaMin / 60) + 'h ' : ''}${ctwaMin % 60}m` : `${Math.floor(ctwaMin / 60)}h`;
    const replyLabel = m < 120 ? `${Math.floor(m / 60) ? Math.floor(m / 60) + 'h ' : ''}${m % 60}m` : `${Math.floor(m / 60)}h`;
    // esc() — the ad headline is external Meta data landing in an HTML attribute
    const head = t.ctwa_ad_headline ? ` · ${esc(t.ctwa_ad_headline)}` : '';
    return `<span class="csw-windial csw-windial--ctwa" title="Reply window: ${replyLabel}. From an ad click${head} — free pricing for ${label} more; pricing does not extend reply permission">${cswDialSvg(m / (24 * 60))}◱ ad ${replyLabel}</span>`;
  }
  if (m === null) return '';
  if (m <= 0) {
    // Channel-correct guidance: WhatsApp outside 24h needs an approved
    // template. Instagram used to claim replies "send under the
    // human-agent tag" — this desk does not apply that tag on any send,
    // and Meta has not approved HUMAN_AGENT for this app anyway
    // (instagram_messaging probes subcode 2534044), so the line promised
    // a guarantee nothing delivers (audit 2026-07-29). The honest answer
    // is: the customer has to speak first.
    // NOTE the branch itself was NOT dead. cs_thread_summaries returns
    // channel as body->>'platform', which is lowercase in all 14,738
    // live rows — the uppercase values sit in raw->>'platform', which no
    // UI path reads. cswPlatform() is used here for the reason every
    // other call site uses it (it lowercases and falls back on
    // last_message_type), not to revive a branch that never fired.
    const closedTitle = cswPlatform(t) === 'ig'
      ? 'Instagram 24h window closed — delivery is not guaranteed; the customer has to message you first'
      : 'WhatsApp 24h window closed — use an approved template';
    // WS-D (2026-09-02): on Instagram the shut window is a HARD STOP —
    // no template reopens it, so the dial wears the stop bar and the
    // word is 'shut', not 'closed' (closed on WhatsApp has a door).
    if (cswPlatform(t) === 'ig') {
      return `<span class="csw-windial csw-windial--closed csw-windial--igstop" title="${closedTitle}">${cswDialSvg(1, true)}shut</span>`;
    }
    return `<span class="csw-windial csw-windial--closed" title="${closedTitle}">${cswDialSvg(1)}closed</span>`;
  }
  const label = m < 120 ? `${Math.floor(m / 60) ? Math.floor(m / 60) + 'h ' : ''}${m % 60}m` : `${Math.floor(m / 60)}h`;
  // The OPEN dial's title is pinned untouched (test_desk_ui_truth); on
  // Instagram only the track dashes and the stop bar sits at 24h, so the
  // hard stop is visible before the window shuts.
  const ig = cswPlatform(t) === 'ig';
  const title = m < 120 ? 'Free-reply window closing' : 'Free-reply window open';
  return `<span class="csw-windial${m < 120 ? ' csw-windial--soon' : ''}${ig ? ' csw-windial--ig' : ''}" title="${title}">${cswDialSvg(m / (24 * 60), ig)}${label}</span>`;
}
// `stop` (WS-D): the hard-stop bar on the ring's rim for Instagram —
// drawn in the un-rotated frame so it sits at 12 o'clock after the CSS turn.
function cswDialSvg(frac, stop) {
  const C = 56.5;  // 2πr, r=9
  const off = (C * (1 - Math.max(0, Math.min(1, frac)))).toFixed(1);
  const bar = stop ? '<path class="csw-windial__stop" d="M21 9.5v5" />' : '';
  return `<svg viewBox="0 0 24 24" aria-hidden="true"><circle class="csw-windial__tr" cx="12" cy="12" r="9"/><circle class="csw-windial__pr" cx="12" cy="12" r="9" stroke-dasharray="${C}" stroke-dashoffset="${off}"/>${bar}</svg>`;
}

// ── tools dispatch ───────────────────────────────────────────
function cswTool(tool) {
  // Photos may be prepared while closed; the explicit Send still checks the reply window.
  // Wave 3: the typed-only superpowers get visible doors — tapping seeds
  // the trigger character and fires the same input path both palettes
  // already listen on (they were invisible: the hint line documenting
  // @/​/ is hidden on touch, exactly where reps live).
  if (tool === 'route-at' || tool === 'slash') {
    const gi = document.getElementById('csw-input');
    if (!gi || gi.disabled) return;
    const ch = tool === 'slash' ? '/' : '@';
    if (!gi.value.endsWith(ch)) gi.value += ch;
    gi.dispatchEvent(new Event('input'));
    gi.focus();
    return;
  }
  // THE RELAY (R5). The key binds itself at mount time — the generic
  // [data-tool] dispatcher has already run by then — so this arm exists
  // for a future re-bind, and cswAskSheet refuses to open twice.
  if (tool === 'ask') { cswAskSheet(); return; }
  // 02 — the close dock's two new keys. Neither is a new lane: Hold opens
  // the hold sheet that shipped with cs_holds, and Account inserts the
  // bank-details reply out of the SAME canned pack the macro strip, the
  // Quick sheet and the "/" palette all read.
  if (tool === 'hold') { cswDockHold(); return; }
  if (tool === 'account') { cswDockAccount(); return; }
  if (tool === 'suggest') cswSuggestReply();
  else if (tool === 'label') cswLabelSheet();
  else if (tool === 'photo') cswPhotoSheet();
  else if (tool === 'product') cswProductSheet();
  else if (tool === 'stock') cswStockSheet();
  else if (tool === 'order') cswOrderSheet();
  else if (tool === 'paylink') cswPaymentLink();
  else if (tool === 'bought') cswBoughtOrder();
  else if (tool === 'editlink') cswEditLink();
  else if (tool === 'note') cswNoteSheet();
  else if (tool === 'template') cswTemplateSheet();
  else if (tool === 'studio') cswTryOnStudio();
  else if (tool === 'delivery') cswDeliveryQuote();
}

// Delivery quote — REP-ONLY chip above the composer. Reads the customer's
// last message as the address, fetches a live Sendbox fee/ETA, and shows
// est.label (always safe). Tap the chip to drop a ready line into the draft.
async function cswDeliveryQuote() {
  const cid = cswState().active;
  const host = document.getElementById('csw-reply-ctx');
  // Best-effort: the most recent inbound bubble is the likely address.
  const ins = document.querySelectorAll('#csw-script .csw-bub.in .csw-bub__txt');
  const address = ins.length ? (ins[ins.length - 1].textContent || '').trim() : '';
  if (!address) { cswToast('No customer message to read an address from', true); return; }
  if (host) { host.hidden = false; host.innerHTML = '<span class="csw-delivery csw-delivery--load">Quoting delivery…</span>'; }
  let est = null;
  try {
    est = await api('/api/cs/delivery-estimate', { method: 'POST', body: JSON.stringify({ address }) });
  } catch (_) { est = { ok: false, label: 'Couldn\'t fetch a delivery estimate.' }; }
  if (!host || cswState().active !== cid || !host.isConnected) return;
  const label = (est && est.label) || 'No estimate.';
  const insertable = est && est.ok && est.fee_ngn;
  host.innerHTML = `<span class="csw-delivery ${insertable ? 'csw-delivery--ok' : ''}" id="csw-delivery-chip" title="${insertable ? 'Tap to insert into your reply' : ''}">🚚 ${esc(label)}${insertable ? ' · tap to insert' : ''}</span>`
    + `<button class="csw-delivery__x" id="csw-delivery-x" aria-label="Dismiss">✕</button>`;
  document.getElementById('csw-delivery-x')?.addEventListener('click', () => { host.hidden = true; host.innerHTML = ''; });
  if (insertable) {
    document.getElementById('csw-delivery-chip')?.addEventListener('click', () => {
      const fee = Number(est.fee_ngn).toLocaleString();
      const line = `Delivery is ₦${fee}${est.eta ? ', ~' + est.eta : ''}.`;
      const inp = document.getElementById('csw-input');
      if (inp) { inp.value = (inp.value ? inp.value.trim() + ' ' : '') + line; inp.dispatchEvent(new Event('input', { bubbles: true })); inp.focus(); }
      host.hidden = true; host.innerHTML = '';
    });
  }
}

// Pay link — mint (or reuse) a Paystack link for this thread's cart and drop
// a ready message into the composer for the rep to review + send. Transport-
// agnostic backend (cs_checkout) so it survives the Zoko cutover.
// 2026-09-24 (seat-B): the key posted {} — only EON's own conversation
// cart could ever fill a link, so a rep who built the order on the desk
// (Tray → Order) still got "cart is empty". The desk's own cart now rides
// along when there is one (the charge is still the Shopify draft's own
// total, never these prices). And the customer was told "Your order is
// reserved" — nothing reserves stock until they pay, so it no longer
// says so.
// The Tray cart belongs to ONE chat (adversary pass on #3543,
// 2026-09-26). It was one array for the whole desk, and opening another
// chat kept it — so the pay link, which now sends the cart, could post
// chat A's pieces under chat B's name and charge B for them, with the rep
// shown only a total. Each chat keeps its own cart: switching chats puts
// the last one aside and brings this chat's back.
function cswCart() {
  const s = cswState();
  const cid = s.active || '';
  if (s.cartCid !== cid) {
    s._carts = s._carts || {};
    if (s.cartCid !== undefined && (s.cart || []).length) s._carts[s.cartCid] = s.cart;
    s.cart = s._carts[cid] || [];
    delete s._carts[cid];
    s.cartCid = cid;
  }
  return s.cart;
}

function cswPayLinkLines(cart) {
  return (cart || []).filter(c => c && c.variant_id).map(c => ({
    variant_id: c.variant_id, qty: Number(c.quantity) || 1,
    title: c.title || '', variant_title: c.variant_title || '',
    unit_price_kobo: Math.round((parseFloat(c.price) || 0) * 100),
  }));
}

function cswPayLinkMessage(r) {
  return `Here's your secure payment link for ${r.total}:\n${r.payment_url}\n\nOnce your payment comes through, we'll confirm your order. 🖤`;
}

async function cswPaymentLink() {
  const s = cswState(); const t = s.activeThread;
  if (!t || !t.zoko_customer_id) { cswToast('Open a conversation first', true); return; }
  cswToast('Creating payment link…');
  // This chat's own cart, and only when the cart says it is this chat's.
  const cart = cswCart();
  const lines = s.cartCid === t.zoko_customer_id ? cswPayLinkLines(cart) : [];
  try {
    const r = await api(`/api/cs/threads/${encodeURIComponent(t.zoko_customer_id)}/payment-link`,
      { method: 'POST', body: JSON.stringify(lines.length ? { line_items: lines } : {}) });
    if (!r || !r.ok || !r.payment_url) { cswToast((r && r.error) || 'Could not create link', true); return; }
    cswPayLinkReady(r, t.zoko_customer_id);
  } catch (e) {
    // A new buyer's link needs details this chat doesn't hold yet (round
    // 2 on #3543, seat B's A2): open the short form to type them in,
    // instead of only naming them.
    const detail = e && e.body && e.body.detail;
    if (detail && Array.isArray(detail.missing) && detail.missing.length
        && cswState().active === t.zoko_customer_id) {
      cswPayLinkDetails(t.zoko_customer_id, detail, lines);
      return;
    }
    // 409/422 carry one sentence naming what to ask the customer for
    // (seat-B) — show it as it is. A timeout keeps the short line.
    const why = e && e.status >= 400 && e.status < 500 && e.status !== 408 && e.message;
    cswToast(why ? String(why).slice(0, 180) : 'Couldn’t make the payment link. Try again in a minute.', true);
  }
}

// This chat bought order # (2026-09-27, the owner's ask) — the rep's word
// for a sale rung at the till or under another contact, which nothing else
// can tie back to this chat or to the ad that started it. Two steps: the
// server first SHOWS the order (number, day, amount, name) and only a
// "Count it" tap writes; "Undo" takes it back. Every answer is the
// server's own plain sentence.
async function cswBoughtOrder() {
  const t = cswState().activeThread;
  if (!t || !t.zoko_customer_id) { cswToast('Open a conversation first', true); return; }
  const host = document.getElementById('csw-reply-ctx');
  if (!host) return;
  // The field takes the reply slot: drop any armed quote so a reply the rep
  // can no longer see is never sent (the send path reads cswState().replyTo).
  cswClearReply();
  const url = `/api/cs/threads/${encodeURIComponent(t.zoko_customer_id)}/bought-order`;
  const post = (path, body) => api(url + path, { method: 'POST', body: JSON.stringify(body) });
  const close = () => { host.hidden = true; host.innerHTML = ''; };
  const xBtn = '<button class="csw-delivery__x" id="csw-bought-x" type="button" aria-label="Dismiss">✕</button>';
  const wireClose = () => document.getElementById('csw-bought-x')?.addEventListener('click', close);
  const busy = (el, on) => { if (el) el.disabled = on; };

  const counted = (order, message) => {
    host.innerHTML = `<span class="csw-delivery csw-delivery--ok">${esc(message)}</span>`
      + '<button class="csw-tool" id="csw-bought-undo" type="button">Undo</button>' + xBtn;
    wireClose();
    const undo = document.getElementById('csw-bought-undo');
    undo?.addEventListener('click', async () => {
      busy(undo, true);
      try {
        const r = await post('/undo', { order });
        cswToast((r && r.message) || 'Could not undo it', !(r && r.undone));
        if (r && r.undone) close();
      } catch (e) { cswToast('Undo: ' + (e.message || 'could not undo it'), true); }
      finally { busy(undo, false); }
    });
  };

  const preview = (order, r) => {
    host.innerHTML = `<span class="csw-delivery">${esc(r.message)}</span>`
      + '<button class="csw-tool" id="csw-bought-yes" type="button">Count it</button>' + xBtn;
    wireClose();
    const yes = document.getElementById('csw-bought-yes');
    yes?.addEventListener('click', async () => {
      busy(yes, true);
      try {
        const c = await post('', { order, confirm: true });
        if (c && c.counted) counted(order, c.message);
        else cswToast((c && c.message) || 'Could not count it', true);
      } catch (e) { cswToast('Bought: ' + (e.message || 'could not count it'), true); }
      finally { busy(yes, false); }
    });
  };

  host.hidden = false;
  host.innerHTML = '<label class="csw-delivery" for="csw-bought-num">This chat bought order #</label>'
    + '<input id="csw-bought-num" type="text" autocomplete="off" placeholder="34559" aria-label="Order number">'
    + '<button class="csw-tool" id="csw-bought-check" type="button">Check</button>' + xBtn;
  wireClose();
  const num = document.getElementById('csw-bought-num');
  const check = document.getElementById('csw-bought-check');
  const submit = async () => {
    const order = (num && num.value || '').trim();
    if (!order) { cswToast('Type the order number from the receipt', true); return; }
    busy(check, true);
    try {
      const r = await post('', { order });
      if (r && r.preview) preview(order, r);
      else cswToast((r && r.message) || 'Could not check it', true);
    } catch (e) {
      cswToast('Bought: ' + (e.message || 'could not check it'), true);
    } finally { busy(check, false); }
  };
  check?.addEventListener('click', submit);
  num?.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } });
  num?.focus();
}

// The link is made: drop the ready message into the composer for the rep
// to read and send — but only the composer of the chat that asked for it
// (Sol 6.1 on #3543, 2026-10-01). The first request could finish after
// the rep moved to another chat and drop one customer's link into the
// next customer's message box; only the details form checked. Every
// caller now passes the chat it asked for, and a link for a chat the rep
// has left is kept on that chat's own draft.
function cswPayLinkReady(r, cid) {
  const msg = cswPayLinkMessage(r);
  if (!cid || cswState().active !== cid) {
    if (cid) {
      const held = cswLoadDraft(cid);
      if (!held.includes(msg)) cswSaveDraft(cid, held ? `${held}\n${msg}` : msg);
    }
    cswToast('Payment link ready. Open the chat again to send it.');
    return;
  }
  const inp = document.getElementById('csw-input');
  if (inp) {
    inp.value = inp.value ? `${inp.value}\n${msg}` : msg;
    inp.dispatchEvent(new Event('input'));
    inp.focus();
  }
  cswToast(r.reused ? 'Existing link reused ✓' : 'Payment link ready ✓');
}

// THE PAY LINK'S DETAILS FORM (round 2 on #3543, seat B's A2). A buyer
// who has never ordered online has no name, email or address on file, so
// the link stopped at one sentence naming what was missing — with nowhere
// to type it, and the rep sent bank details by hand instead. This short
// form starts from what the chat already knows (its saved contact, the
// name it carries, the address the customer typed), asks for the rest —
// name, email, delivery or pickup — keeps what the rep types on the
// chat's contact, and tries the link again. The charge is still the
// Shopify order's own total.
function cswPayLinkDetails(cid, detail, lines) {
  const known = (detail && detail.known) || {};
  const missing = (detail && detail.missing) || [];
  const rem = cswState()._remembered;
  const remAddr = (rem && rem.address && rem.address.value) ? String(rem.address.value) : '';
  let mode = missing.includes('pickup_store') ? 'pickup' : 'delivery';
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Details for the payment link</div><button class="csw-x">✕</button></div>
    <div id="pl-why" style="font-size:11.5px;color:var(--ink-mute);margin-bottom:10px">${esc((detail && detail.message) || 'The link needs a few details. Ask the customer for what is empty.')}</div>
    <input class="csw-search" id="pl-name" placeholder="Full name" autocomplete="off" style="margin-bottom:8px"/>
    <input class="csw-search" id="pl-email" type="email" placeholder="Email" autocomplete="off" style="margin-bottom:8px"/>
    <div class="csw-chips" id="pl-mode" style="margin-bottom:8px">
      <button class="csw-chip" data-mode="delivery">Delivery</button>
      <button class="csw-chip" data-mode="pickup">Pickup at a store</button>
    </div>
    <input class="csw-search" id="pl-address" placeholder="Delivery address" autocomplete="off" style="margin-bottom:12px"/>
    <input class="csw-search" id="pl-store" placeholder="Which store will they collect from?" autocomplete="off" style="margin-bottom:12px;display:none"/>
    <button class="csw-confirm" id="pl-go">Make the payment link</button>
    <div class="csw-warn">What you type here is saved on this chat, so nobody has to type it again.</div>`);
  const q = sel => ov.querySelector(sel);
  const put = (sel, v) => { const el = q(sel); if (el && !el.value && v) el.value = String(v); };
  put('#pl-name', known.full_name); put('#pl-email', known.email);
  put('#pl-address', known.address || remAddr);
  const setMode = m => {
    mode = m;
    ov.querySelectorAll('#pl-mode [data-mode]').forEach(b => b.classList.toggle('on', b.dataset.mode === m));
    q('#pl-address').style.display = m === 'pickup' ? 'none' : '';
    q('#pl-store').style.display = m === 'pickup' ? '' : 'none';
  };
  setMode(mode);
  ov.querySelectorAll('#pl-mode [data-mode]').forEach(b => b.addEventListener('click', () => setMode(b.dataset.mode)));
  q('.csw-x').addEventListener('click', () => ov.remove());
  const first = ['#pl-name', '#pl-email', mode === 'pickup' ? '#pl-store' : '#pl-address']
    .map(q).find(el => el && !el.value);
  if (first && first.focus) first.focus();
  const go = q('#pl-go');
  go.addEventListener('click', async () => {
    const v = sel => (q(sel).value || '').trim();
    const body = { full_name: v('#pl-name'), email: v('#pl-email'), fulfilment: mode, save_contact: true };
    if (mode === 'pickup') body.pickup_store = v('#pl-store');
    else body.address = v('#pl-address');
    if (lines && lines.length) body.line_items = lines;
    if (!body.full_name || !body.email || !(mode === 'pickup' ? body.pickup_store : body.address)) {
      cswToast('Fill in every box. Ask the customer for what you don’t have.', true);
      return;
    }
    go.disabled = true; go.textContent = 'Making the link…';
    try {
      const r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/payment-link`,
        { method: 'POST', body: JSON.stringify(body) });
      if (!r || !r.ok || !r.payment_url) throw Object.assign(new Error((r && r.error) || ''), { status: 422 });
      ov.remove();
      // The rep may have moved to another chat while this was working —
      // cswPayLinkReady keeps the link on its own chat (one guard, every
      // caller).
      cswPayLinkReady(r, cid);
    } catch (e) {
      go.disabled = false; go.textContent = 'Make the payment link';
      const why = e && e.status >= 400 && e.status < 500 && e.status !== 408 && e.message;
      const said = why ? String(why).slice(0, 180) : 'Couldn’t make the payment link. Try again in a minute.';
      const w = q('#pl-why'); if (w) w.textContent = said;
      cswToast(said, true);
    }
  });
  return ov;
}

// Their edit — one tap fetches (curating on first use) the customer's
// personal edit link (crux.xyz/edit/<slug>, ~20 looks from their buying
// pattern) and drops a laid-back line into the composer for the rep to
// review + send. Two-beat law: this is a BEAT-2 shape — only ever sent
// into a live conversation, never as an opener.
async function cswEditLink() {
  const s = cswState(); const t = s.activeThread;
  if (!t || !t.zoko_customer_id) { cswToast('Open a conversation first', true); return; }
  cswToast('Building their edit…');
  try {
    const r = await api(`/api/cs/threads/${encodeURIComponent(t.zoko_customer_id)}/edit-link`,
      { method: 'GET', timeoutMs: 30000 });
    if (!r || !r.ok || !r.url) { cswToast(cswSayWhy({ message: String((r && (r.message || r.error)) || '') }, { lead: 'Their edit was not built.' }), true); return; }
    const msg = `Put a short edit together around what you already own — ${r.looks || 'twenty'} looks, no noise. Whenever you get a minute: ${r.url}`;
    const inp = document.getElementById('csw-input');
    if (inp) {
      inp.value = inp.value ? `${inp.value}\n${msg}` : msg;
      inp.dispatchEvent(new Event('input'));
      inp.focus();
    }
    cswToast(`Edit ready — ${r.looks || 0} live looks ✓`);
  } catch (e) {
    // 409 = thread has no phone (IG handle) — the edit keys on buying history.
    // Round 2 (2026-09-26): the route's own sentence, never a code or path.
    cswToast(cswSayWhy(e, { lead: 'Their edit was not built.' }), true);
  }
}

// 2026-09-24 (vet 12): ONE EON DRAFT PER CHAT. Opening a waiting chat
// typed EON's silent pre-draft into the empty box with nothing saying the
// words were EON's, on chats WhatsApp had shut too, and beside a rescue
// draft or a prepared reply: two EON answers at once, and sending one
// left the other ready to go. The silent draft now stands down when this
// chat already shows an EON draft, or when the desk's own reply-window
// rule would hold a typed reply anyway.
function cswSilentDraftBlocked(cid) {
  const panel = document.getElementById('csw-rescue');
  if (panel && panel.dataset.cid === String(cid)) return true;
  if (cswPending(cid).draft) return true;
  // 2026-09-26: EON already answering here (its reply queued to send
  // itself, or EON holding the chat) is read by open PR #3558's
  // cswEonAlreadyAnswering. Asked here too, when it exists, so the check
  // before the fill reads the same signs as the check before the ask.
  if (typeof cswEonAlreadyAnswering === 'function' && cswEonAlreadyAnswering(cswThreadFor(cid))) return true;
  return cswWindowBlocks(cswThreadFor(cid));
}

// Claude Commerce C1: the catalog's check of an EON draft (`truth`, catalog_truth.check_draft).
// The rep sees the first price, size or stock line the catalog does not back, then any price or
// stock line no read could check, and sees when the check could not run. Nothing when it passed.
function cswTruthNote(truth) {
  if (!truth) return '';
  if (truth.status === 'unavailable') return 'This draft could not be checked against the catalog. Check prices, sizes and stock before sending.';
  const wrong = Number(truth.unverified) > 0 && typeof truth.note === 'string' ? truth.note.trim() : '';
  const unread = typeof truth.unchecked_note === 'string' ? truth.unchecked_note.trim() : '';
  return [wrong, unread].filter(Boolean).join(' ');
}

// The composer's line (#csw-truth, in the shell from first paint). Its parent holds the line's
// height while EON's draft is in the box, so the draft never jumps when the line goes.
function cswTruthPaint(truth) {
  const line = document.getElementById('csw-truth');
  if (!line) return;
  line.textContent = cswTruthNote(truth);
  // A line that says what could not be checked stays said until the box is emptied.
  line._claims = truth && truth.unchecked_note ? [] : ((truth && truth.claims) || []).filter(c => c && !c.verified && c.text).map(c => String(c.text));
  line.hidden = !line.textContent;
  line.parentElement.style.minHeight = line.hidden ? '' : `${line.offsetHeight}px`;
}

// The line goes when every claim it names has left the box (the height stays held), and the
// held height goes with an empty box. A check that could not run stays said until then.
function cswTruthSync(value) {
  const line = document.getElementById('csw-truth');
  if (!line) return;
  if (!value.trim()) { line.hidden = true; line.parentElement.style.minHeight = ''; }
  else if ((line._claims || []).length && !line._claims.some(c => value.includes(c))) line.hidden = true;
}

// Words the silent pre-draft typed wear a plain line over the box until
// they change or leave it (cswSyncComposerKeys drops it on any edit, send
// or clear). Existing warn-line class; no new styling.
function cswEonDraftMark(cid, inp, report, note) {
  inp._cswEonDraft = inp.value;
  let tag = document.getElementById('csw-eondraft');
  if (!tag) {
    tag = document.createElement('div');
    tag.id = 'csw-eondraft';
    tag.className = 'csw-reask';
    tag.setAttribute('role', 'status');
    const ghost = document.getElementById('csw-ghost');
    if (ghost) ghost.insertAdjacentElement('afterend', tag);
    else document.getElementById('csw-inputbox')?.insertAdjacentElement('beforebegin', tag);
  }
  tag.textContent = 'EON’s draft. Read before sending.';
  // Proof E1-A: the server's one plain sentence, when it sends one, sits inside this same line.
  if (typeof note === 'string' && note.trim()) tag.textContent = `EON’s draft. ${note.trim()} Read before sending.`;
  tag.hidden = false;
  // Seat B: the draft is on the rep's screen now, so it counts as shown.
  if (report) api(`/api/cs/threads/${encodeURIComponent(cid)}/suggest-reply/shown`,
    { method: 'POST', body: JSON.stringify({ text: inp.value }) }).catch(() => {});
}

// AI copilot — draft the next reply into the composer (rep edits + sends).
async function cswSuggestReply(silent, nudge) {
  const inp = document.getElementById('csw-input');
  const btn = document.querySelector('.csw-tool[data-tool="suggest"]');
  if (!inp || !cswState().active) return;
  // 2026-09-24 (vet 12): one EON draft per chat. The silent pre-draft
  // waits for this chat's rescue-draft read, then stands down when EON
  // already has a draft showing or the chat cannot take a free reply.
  if (silent) {
    const want = cswState().active, rescue = cswState()._rescueRead, asked = cswPending(want).request;
    if (rescue && rescue.cid === want) await rescue.done;
    // A draft the rep asked for while we waited outranks this one.
    if (cswState().active !== want || cswPending(want).request !== asked || cswSilentDraftBlocked(want)) return false;
  }
  const cid = cswState().active, pending = cswPending(cid), request = ++pending.request;
  const old = btn ? btn.innerHTML : '';
  if (btn) { btn.innerHTML = '<span class="csw-think">✦ drafting…</span>'; btn.disabled = true; }
  // Public IG comments get the brand-voice comment generator (Opus); DMs
  // get the conversational reply generator. Both are generate-only —
  // the rep reviews + sends.
  const isComment = (cswState().activeThread || {})._ch === 'comment';
  const endpoint = isComment ? 'suggest-comment-reply' : 'suggest-reply';
  try {
    // 2026-09-24 (Seat B): a silent request says so. The server files its
    // draft as not yet shown, and the rep's name goes on it only when the
    // box is actually filled (cswEonDraftMark reports that).
    const ask = nudge && !isComment ? { nudge } : (silent && !isComment ? { prefill: true } : {});
    const d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/${endpoint}`,
      { method: 'POST', body: JSON.stringify(ask), timeoutMs: 30000 });
    if (request !== pending.request) return false;
    if (d && d.ok && d.suggestion) {
      // An auto pre-draft never steals a reply the rep started typing
      // between request and response.
      if (silent && (cswState().active !== cid || !inp.isConnected || inp.value.trim())) return false;
      // …nor lands beside a rescue or prepared draft that arrived while
      // EON was writing, nor on a chat whose window shut meanwhile.
      if (silent && cswSilentDraftBlocked(cid)) return false;
      // Wave A: a SILENT pre-draft is EON's homework, not the rep's words —
      // flag the programmatic fill so the input listener skips draft
      // persistence (the fill used to ride saveDraftSoon into csw-drafts
      // and squat on the list row as "Draft: …"). First real keystroke
      // clears the flag and drafts persist as before.
      // Requested drafts wait for review and explicit adoption. The silent
      // pre-draft still fills only an untouched composer in its own thread.
      if (silent) {
        inp._cswProgFill = true;
        inp.value = d.suggestion; inp.dispatchEvent(new Event('input'));
        inp._cswProgFill = false;
        cswEonDraftMark(cid, inp, !isComment, d.draft_note);
        if (typeof cswTruthPaint === 'function') cswTruthPaint(d.truth);
      } else {
        cswShowDraftCard(cid, d.suggestion, d);
      }
      return true;
    } else if (!silent) {
      cswToast(cswSayWhy({ message: String((d && d.error) || '') }, { lead: 'EON could not write a draft.' }), true);
    }
  } catch (e) { if (!silent) cswToast(cswSayWhy(e, { lead: 'EON could not write a draft.' }), true); }
  finally { if (btn) { btn.innerHTML = old; btn.disabled = false; } }
}

// ── Desk-rescue draft panel (mig 686) ───────────────────────────────
// While a chat sat past the reply SLA, the rescue sweep drafted a reply
// server-side and persisted it. Opening the thread surfaces that draft
// in a panel above the composer: Send posts it through the EXISTING
// /send route (the tap IS the human approval — nothing auto-sends),
// Edit drops it into the composer, Dismiss records the verdict.
async function cswLoadRescueDraft(cid) {
  const host = document.querySelector('#csw-conv .csw-composer');
  const gone = () => { const p = document.getElementById('csw-rescue'); if (p) p.remove(); };
  if (!host || !cid) { gone(); return; }
  let d = null;
  // 2026-09-24 (vet 12): the silent pre-draft waits on this read so a
  // rescue draft is never followed by a second EON reply in the box.
  const read = api(`/api/cs/threads/${encodeURIComponent(cid)}/reply-draft`);
  cswState()._rescueRead = { cid, done: read.then(() => {}, () => {}) };
  try { d = await read; }
  catch (_) { gone(); return; }
  if (cswState().active !== cid) return;          // thread switched mid-fetch
  const draft = d && d.ok && d.draft;
  if (!draft || !(draft.draft_text || '').trim()) { gone(); return; }
  gone();
  const p = document.createElement('div');
  p.id = 'csw-rescue';
  p.className = 'csw-rescue';
  p.dataset.cid = cid;
  p.dataset.draftId = draft.id;
  const waited = Number(draft.waiting_minutes || 0);
  const wmin = waited >= 120 ? `${Math.floor(waited / 60)}h ${waited % 60}m`
    : (waited >= 60 ? `${Math.floor(waited / 60)}h` : `${waited}m`);
  p.innerHTML = `
    <div class="csw-rescue__head">Drafted while this chat waited${waited ? ' ' + esc(wmin) : ''} — review before sending</div>
    <span class="csw-rescue__fold" aria-hidden="true">Tap to read</span>
    <div class="csw-rescue__text">${esc(draft.draft_text || '')}</div>
    ${/* 04 — the rescue draft wears the SAME receipt as the rep's own.
         cs_desk_rescue already stamps grounded/grounded_kind onto the
         draft event; the row this panel reads does not select them yet,
         so today this renders nothing and the panel is unchanged. */''}
    ${cswGroundChips(draft)}
    <div class="csw-rescue__row">
      <button type="button" class="csw-rescue__btn csw-rescue__btn--send" data-act="send">Send</button>
      <button type="button" class="csw-rescue__btn" data-act="edit">Edit first</button>
      <button type="button" class="csw-rescue__btn csw-rescue__btn--ghost" data-act="dismiss">Dismiss</button>
    </div>`;
  p.addEventListener('click', (e) => {
    const act = e.target && e.target.dataset && e.target.dataset.act;
    if (act === 'send') cswRescueSend(p);
    else if (act === 'edit') cswRescueEdit(p, draft.draft_text || '', e);
    else if (act === 'dismiss') cswRescueDismiss(p);
    // While the rep types, the band is one line with no keys (cs.css,
    // adversary pass on #3544, 2026-09-24): Send posts the whole draft in
    // one tap, so it only shows with the draft in view. A tap opens it.
    else if (!act && !p.classList.contains('csw-rescue--open')) {
      p.classList.add('csw-rescue--open');
      p.scrollIntoView({ block: 'nearest' });
    }
  });
  // L1 (verifier, 2026-08-30): the rescue panel is a BAND and lives
  // inside the band budget — same move as the draft card above. Its old
  // home (beforebegin of the composer) sat OUTSIDE .csw-bands as a
  // flex:0 0 auto member: on a keyboard-up shell the floored transcript
  // plus composer plus this panel (~230px lit) exceed --app-height, and
  // .csw-body{overflow:hidden} clips the send key — the exact fault the
  // budget closes. Appended last (its old visual slot, nearest the
  // composer) and scrolled into view so a lit band stack can't hide it.
  const bands = document.getElementById('csw-bands');
  if (bands) { bands.appendChild(p); p.scrollIntoView({ block: 'nearest' }); }
  else host.insertAdjacentElement('beforebegin', p);
  // 2026-09-24 (vet 12): a rescue draft that lands after the silent
  // pre-draft takes the box back. EON's untouched words leave; the rep's
  // own words never do.
  const inp = document.getElementById('csw-input');
  if (inp && inp._cswEonDraft != null && inp.value === inp._cswEonDraft) {
    inp._cswProgFill = true;
    inp.value = ''; inp.dispatchEvent(new Event('input'));
    inp._cswProgFill = false;
  }
}

async function cswRescueSend(panel) {
  const cid = panel.dataset.cid, draftId = panel.dataset.draftId;
  const text = (panel.querySelector('.csw-rescue__text') || {}).textContent || '';
  if (!cid || !text.trim()) return;
  const btn = panel.querySelector('[data-act="send"]');
  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
  try {
    await cswSendApi(`/api/cs/threads/${encodeURIComponent(cid)}/send`,
      { method: 'POST', body: JSON.stringify({ text }) });
  } catch (e) {
    if (btn) { btn.disabled = false; btn.textContent = 'Send'; }
    // Round 2 (2026-09-26): the same plain sentence as a typed send — a lost
    // answer may already be with the customer.
    cswToast(cswSayWhy(e, { send: true }), true);
    return;
  }
  api(`/api/cs/reply-drafts/${encodeURIComponent(draftId)}/status`,
    { method: 'POST', body: JSON.stringify({ status: 'sent' }) }).catch(() => {});
  panel.remove();
  cswToast('Sent');
  if (typeof cswMarkReplied === 'function') { try { cswMarkReplied(cid); } catch (_) {} }
  setTimeout(() => cswLoadTranscript(cid, true), 1400);
}

function cswRescueEdit(panel, text, event) {
  cswAdoptDraft(panel.dataset.cid, text, () => panel.remove(), event);
}

function cswRescueDismiss(panel) {
  const draftId = panel.dataset.draftId;
  // Round 2 (2026-09-26): a dismiss that did not save was silent, and the
  // draft came back the next time the chat opened. Say so.
  api(`/api/cs/reply-drafts/${encodeURIComponent(draftId)}/status`,
    { method: 'POST', body: JSON.stringify({ status: 'dismissed' }) })
    .catch((e) => cswToast(cswSayWhy(e, { lead: 'Not dismissed.', unsure: 'Not sure the dismiss saved. EON’s draft may come back.' }), true));
  panel.remove();
}

// In-UI toast (replaces native alert() — on-brand, non-blocking).
// Toast with an inline Undo action (ONE CLICK, NO CONFIRMS: undo replaces
// confirm). Longer dwell so the undo is actually reachable.
function cswToastUndo(msg, undoFn) {
  let host = document.getElementById('csw-toasts');
  if (!host) { host = document.createElement('div'); host.id = 'csw-toasts'; host.className = 'csw-toasts'; document.body.appendChild(host); }
  const t = document.createElement('div');
  t.className = 'csw-toast csw-toast--undo';
  const label = document.createElement('span');
  label.textContent = msg;
  const undo = document.createElement('button');
  undo.className = 'csw-toast__undo';
  undo.textContent = 'Undo';
  undo.addEventListener('click', () => { t.remove(); undoFn(); });
  // L6 (2026-08-30): the six-second window gets a face — a 2px hairline
  // drains along the toast's bottom edge (CSS csw-toast-drain, 6s: keep
  // in step with the dwell below). Under reduced motion it holds FULL.
  const bar = document.createElement('i');
  bar.className = 'csw-toast__drain';
  t.appendChild(label); t.appendChild(undo); t.appendChild(bar);
  host.appendChild(t);
  cswPositionToasts();
  setTimeout(() => { t.classList.add('out'); setTimeout(() => t.remove(), 300); }, 6000);
}

// ── Desktop keyboard model (RS-05) ──────────────────────────────────
// The two-pane desk layout already exists (>=880px) but was mouse-only:
// a rep at a monitor had to click every thread. Cmd/Ctrl+K focuses search,
// arrows walk the queue, Enter opens, Esc clears search or closes the
// thread. Bound once, and inert while the rep is typing.
function cswBindKeys() {
  if (window._cswKeysBound) return;
  window._cswKeysBound = true;
  // The list's own arrow walk (cswBindSearchNavigation) moves focus between
  // rows without passing through the keydown handler below; note where it
  // lands so the ring resumes from there after a repaint.
  // The ring is put down when the rep moves on (#3541 adversary pass,
  // 2026-09-24): focus landing anywhere that is not a row, or a click
  // outside the rows, forgets it, so Enter never opens a chat the rep can
  // no longer see ringed. A repaint moves neither, so the ring outlives it.
  document.addEventListener('focusin', (e) => {
    const row = e.target?.closest?.('#csw-threads [data-open], #csw-threads .csw-thread[data-cid]');
    if (!row) { if (cswSurfaceActive()) cswState()._kbRing = null; return; }
    const s = cswState();
    s._kbRing = { cid: row.dataset.cid || row.dataset.open, active: s.active };
  });
  document.addEventListener('pointerdown', (e) => {
    if (e.target?.closest?.('#csw-threads [data-open], #csw-threads .csw-thread[data-cid]')) return;
    if (cswSurfaceActive()) cswState()._kbRing = null;
  }, { passive: true });
  document.addEventListener('keydown', (e) => {
    if (!cswSurfaceActive()) return;
    // While Ask EON is open over the desk the desk's keys stand down: an
    // arrow opened the next customer behind the panel and restarted Ask's
    // conversation, and Cmd/Ctrl+K moved focus to the search behind it
    // (Fable F4-1, Opus O4-2). Cmd/Ctrl+K does nothing until Ask closes.
    if (window._cswAskOver) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) e.preventDefault();
      return;
    }
    if (e.defaultPrevented || e.target?.closest?.('.csw-overlay, dialog[open], .csw-drawer.open')) return;
    // The key's real target (eonKeyTarget): typed in Ask EON's box, a key
    // arrives as Ask's host DIV, and Up, Down or Shift+Enter there opened
    // a customer's chat, marked it seen and hid Ask with the draft in it.
    const el = eonKeyTarget(e);
    const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA'
      || el.isContentEditable);

    // Cmd/Ctrl+K — jump to search from anywhere, including the composer.
    if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
      const sb = document.getElementById('csw-searchbar');
      e.preventDefault();
      if (sb && (sb.getClientRects().length || document.getElementById('csw-search-toggle')?.getClientRects().length)) cswSearchDesk();
      else {
        const panel = document.getElementById('csw-msearch');
        if (panel?.hidden) document.getElementById('csw-searchbtn')?.click();
        document.getElementById('csw-msearch-q')?.focus();
      }
      return;
    }
    if (e.key === 'Escape') {
      const sb = document.getElementById('csw-searchbar');
      if (sb && sb.value) {
        e.preventDefault();
        sb.value = ''; cswState().search = '';
        document.getElementById('csw-gsec')?.remove();
        cswGlobalSearchSoon('');
        cswRenderThreadList();
      }
      return;
    }
    if (typing) return;                       // never steal keys mid-message
    // THE ARROWS MOVE A RING, THEY DO NOT OPEN (CS vet 29, 2026-09-24).
    // Each arrow press used to OPEN the chat it landed on: marked it read
    // for this rep (POST /seen) and ran the whole open path, EON's reply
    // suggestion included — four presses were 86 requests and 7 model
    // calls, and every chat walked past read as "seen" though nobody read
    // it. Now the arrows only move the focus ring down the list (the row
    // is a <button>, so the ring is the browser's own focus ring) and the
    // conversation stays where it is; Enter opens the ringed row, once.
    // The list is read off the event's path, not the row's parents: a queue
    // card's own Enter handler repaints the list before this runs, which
    // takes the card out of the page.
    const inList = (e.composedPath ? e.composedPath() : []).some(n => n && n.id === 'csw-threads');
    const onRow = inList && el?.matches?.('[data-open], .csw-thread[data-cid]') ? el : null;   // the row itself, never a key inside it
    if (!onRow && el?.closest?.('button, summary, [role="button"]')) return;
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
    const s = cswState();
    const active = s.active;
    const idOf = r => r.dataset.cid || r.dataset.open;
    if (onRow && e.key === 'Enter') {
      // A queue card opens itself on Enter (its own key handler, which ran
      // first); the browser would then press the button and open it a
      // second time. Stop that press, and open a plain row here instead.
      e.preventDefault();
      if (String(s.active) !== String(idOf(onRow))) cswOpenThread(idOf(onRow));
      return;
    }

    const rows = [...document.querySelectorAll('#csw-threads [data-open], #csw-threads .csw-thread[data-cid]')]
      .filter(r => r.getClientRects().length && !r.closest('[inert]'));
    if (!rows.length) return;
    if (e.key === 'Enter') {
      // A repaint can take the ring's focus away before Enter lands (#3541
      // adversary pass, 2026-09-24): Enter still opens the ringed chat,
      // never the first one in the list, and never nothing while the
      // ringed chat is still listed.
      const ringSet = !!(s._kbRing && s._kbRing.active === active);
      const ringed = ringSet ? rows.find(r => idOf(r) === String(s._kbRing.cid)) : null;
      if (ringed && idOf(ringed) !== String(active)) {
        e.preventDefault();
        return cswOpenThread(idOf(ringed));
      }
      // The ringed chat has left the list (answered on another desk) and
      // the repaint took the ring with it (Fable 5.1 vet on #3541,
      // 2026-09-27): open nothing in its place. Opening the first chat
      // would mark it read and ask EON for a reply the rep never chose.
      if (ringSet && !ringed) {
        e.preventDefault();
        cswToast('That chat is no longer in the list.');
        return;
      }
      if (rows.some(r => idOf(r) === String(active))) return;   // already open
      e.preventDefault();
      return cswOpenThread(idOf(rows[0]));
    }
    e.preventDefault();
    // Where the ring is: the focused row; else where it last stood (a list
    // repaint drops focus) while the same chat is still open; else the
    // open chat.
    const ring = s._kbRing && s._kbRing.active === active ? s._kbRing.cid : active;
    let i = rows.indexOf(onRow);
    if (i < 0) i = rows.findIndex(r => idOf(r) === String(ring));
    if (i < 0) i = e.key === 'ArrowDown' ? -1 : rows.length;
    const next = rows[Math.max(0, Math.min(rows.length - 1,
      i + (e.key === 'ArrowDown' ? 1 : -1)))];
    if (next) {
      s._kbRing = { cid: idOf(next), active };
      // A priority row is a plain <div>: let it take the ring.
      if (!next.hasAttribute('tabindex') && next.tabIndex < 0) next.setAttribute('tabindex', '-1');
      next.focus({ preventScroll: true });
      next.scrollIntoView({ block: 'nearest' });
    }
  });
}

// THE RING SURVIVES A REPAINT (#3541 adversary pass, 2026-09-24). A poll or
// a realtime nudge repaints the chat list many times a minute. The house
// queue rebuilds its cards outright and the classic list moves rows, and
// either one dropped the keyboard's ring to the page: the next Enter opened
// the FIRST chat in the list (marked it read, asked EON for a reply), or
// nothing. cswRenderThreadList hands over the ringed row before it paints;
// once the paint is done, the same chat takes the ring back. Only a ring the
// rep can see (a mouse click leaves none), and never when focus has moved
// somewhere else on purpose.
function cswRingBack(el, was) {
  const cid = was.dataset.cid || was.dataset.open;
  let seen = true;
  try { seen = was.matches(':focus-visible'); } catch (_) {}
  if (!cid || !seen) return;
  Promise.resolve().then(() => {
    const now = document.activeElement;
    if (now && now !== document.body) return;   // kept its focus, or the rep moved on
    const row = [...el.querySelectorAll('[data-open], .csw-thread[data-cid]')]
      .find(r => (r.dataset.cid || r.dataset.open) === cid
        && !r.classList.contains('hs-tearing') && !r.closest('[inert]'));
    if (!row) return;
    if (!row.hasAttribute('tabindex') && row.tabIndex < 0) row.setAttribute('tabindex', '-1');
    row.focus({ preventScroll: true });
  });
}

// ── Connection state banner (ST-05) ─────────────────────────────────
// The workspace already healed dropped sockets silently (adaptive poll +
// visibilitychange). A rep saw nothing: messages simply stopped arriving.
// This surfaces the state — offline, reconnecting — and clears itself the
// moment realtime is healthy again.
function cswConnBanner(state) {
  const shell = document.querySelector('.csw-shell');
  if (!shell) return;
  let el = document.getElementById('csw-conn');
  if (!state) { el?.remove(); return; }
  if (!el) {
    el = document.createElement('div');
    el.id = 'csw-conn';
    el.className = 'csw-conn';
    el.setAttribute('role', 'status');
    shell.insertBefore(el, shell.firstChild);
  }
  el.classList.toggle('csw-conn--off', state === 'offline');
  // Vet 2026-09-24 (fault 19): this promised "messages will send when you
  // are back online", but nothing is queued — a rep who trusted it locked
  // the phone and the customer waited. Say what is true.
  el.textContent = state === 'offline'
    ? "No connection. Your reply stays in the box. Send it when you're back online."
    : 'Reconnecting…';
}

// Re-evaluated on every poll tick: offline wins, then a failing POLL,
// else clear. Realtime staleness is deliberately NOT a banner trigger
// (design vet 2026-07-26 SEV-5): on a quiet desk the broadcast beat goes
// stale 40–90s of every 90s cycle by construction while the 10s REST poll
// keeps every message flowing — the old check told reps "Reconnecting…"
// most of the day on a healthy desk (and forever when realtime is
// disabled/blocked). Live-vs-poll transport state stays on the header
// rt-dot, which already has that vocabulary.
function cswConnCheck() {
  const s = cswState();
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    cswConnBanner('offline'); return;
  }
  cswConnBanner(((s._pollFail || 0) >= 2) ? 'connecting' : null);
}

function cswPositionToasts() {
  const host = document.getElementById('csw-toasts');
  if (!host) return;
  const composer = document.querySelector('.csw-conv.show .csw-composer');
  const rect = composer?.getBoundingClientRect();
  const bands = document.querySelector('.csw-conv.show #csw-bands')?.getBoundingClientRect();
  const top = bands?.height ? Math.min(bands.top, rect?.top ?? bands.top) : rect?.top;
  host.style.bottom = Number.isFinite(top) ? `${Math.max(16, window.innerHeight - top + 12)}px` : '';
}

// Vet 2026-09-24 (fault 20): reps read the machine's own words when a call
// failed — "Request timed out (30s) — /api/cs/threads/<the customer's
// number>/send", "Load failed", a JSON dump, a bare "API error". This turns
// any failure into one plain sentence, what it means for the rep first:
// no signal, not sure it went (a customer send that got no answer may
// already be out, so it says check the chat before sending again), or the
// sentence the server wrote for people. Never a path, a code word or a dump.
// opts.lead names what failed ("The label was not added."); opts.send marks
// a customer send, where a lost answer does not mean a lost message.
// Round 2 (2026-09-26): opts.unsure is the sentence for a lost answer on an
// action that may have happened anyway (placing an order, marking it paid).
// It is said alone, never after the lead: the lead names a failure, and
// this one is not known. opts.otherwise replaces the last-resort line when
// nothing plain can be said. Also round 2: WhatsApp's own refusal words
// ("(#131030) Recipient phone number not in allowed list", the typed-send
// route's 502 with a graph_code, or a media door's message that starts
// "(#131053)") never reach the rep. With a code the send surely did not go,
// and it reads as the failed-message flap reads that code (cswFailWhy),
// naming the app of the send's own chat; with none, it is "not sure"
// unless the error names a definite no. A code-shaped "<word> failed"
// ("mark-paid failed") is not a
// reason, but a sentence ("The transfer failed.") is; "media upload failed"
// is said before anything is sent, so it says nothing reached the customer;
// and `\bload failed` keeps Safari's network words from catching "upload
// failed" as no signal.
// Python's own words after a route's colon ("reverser unavailable:
// 'NoneType' object has no attribute 'get'") are not a reason either.
const CSW_RAW_WORDS = /[{}[\]<>]|https?:|\/api\/|\b\w+_\w+\b|\(\d{3}\)|\w*Error:|exception|traceback|internal server|bad gateway|service unavailable|gateway time|NoneType|object has no attribute|object is not (subscriptable|iterable|callable)/i;
const CSW_HTTP_PHRASE = /^(not found|forbidden|unauthorized|bad request|method not allowed|unprocessable (entity|content)|too many requests|(payload|request entity) too large|conflict|request context changed|api error)\.?$/i;
function cswSayWhy(err, opts = {}) {
  const e = (err && typeof err === 'object') ? err : { message: String(err || '') };
  const send = !!opts.send;
  const lead = opts.lead ? String(opts.lead).trim() : '';
  const unsure = opts.unsure ? String(opts.unsure).trim() : '';
  const plain = (s) => {
    s = typeof s === 'string' ? s.trim() : '';
    // A server sentence may carry its support tag after it — "…if it keeps
    // refusing. (pickup_failed)" (wa_refusal_words.say). The sentence is
    // for the rep; the bracketed word is not (adversary pass, 2026-09-24).
    s = s.replace(/([.!?])\s*\(\s*[\w.:-]+\s*\)$/, '$1');
    if (!s || s.length > 240 || !/\s/.test(s) || CSW_RAW_WORDS.test(s) || CSW_HTTP_PHRASE.test(s)) return '';
    if (/^[a-z][\w-]*(?: [\w-]+){0,2} failed\.?$/.test(s)) return '';   // "mark-paid failed"
    s = s[0].toUpperCase() + s.slice(1);
    return /[.!?)]$/.test(s) ? s : s + '.';
  };
  const msg = String(e.message || '');
  const st = Number(e.status) || 0;
  const detail = e.body && e.body.detail;
  const dobj = detail && typeof detail === 'object' && !Array.isArray(detail) ? detail : null;
  // WhatsApp's own refusal starts with its code, "(#131053) Media upload
  // error"; the photo, video and voice doors pass it on with no graph_code
  // (adversary pass on round 2).
  const dmsg = dobj ? detail.message : (typeof detail === 'string' ? detail : '');
  const graphSaid = /^\s*\(#(\d+)\)/.exec(String(dmsg || msg));
  // The app named in a refusal is the send's own chat (its path), never
  // whichever chat is open when the answer lands (adversary pass).
  const platOf = () => {
    if (opts.plat != null) return opts.plat;
    const m = /^\/api\/cs\/threads\/([^/?#]+)\//.exec(String(e.path || ''));
    try { const t = m && cswThreadFor(decodeURIComponent(m[1])); return t ? (t._plat || cswPlatform(t)) : undefined; } catch (_) { return undefined; }
  };
  // No signal: the browser's own network-failure words, or a lost answer
  // while the device says it is offline. A local refusal ("Reply held…")
  // keeps its own sentence even offline.
  const lost = e.timeout || st === 408 || e.name === 'AbortError' || e.name === 'TypeError';
  const dropped = !st && /failed to fetch|\bload failed|networkerror|network request failed|network connection was lost|internet connection appears to be offline/i.test(msg);
  // Fable vet (27 Sep): a customer send whose line dropped while the phone
  // still says it is online may have reached EON first (a server that went
  // down mid-request reads the same way), so it says check the chat, never
  // "Send it again". A phone that says it has no signal keeps "No signal".
  const saysOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const offline = (dropped && (!send || saysOffline)) || (lost && saysOffline);
  // A key that may have worked anyway (opts.unsure) says so for a line that
  // dropped mid-request too: the request may have reached EON before the
  // line went, and "No signal. Try again" invited a second payment
  // (adversary pass on round 2).
  if (unsure && (dropped || e.timeout || st === 408 || e.name === 'AbortError')) return unsure;
  let why = '';
  if (offline) {
    why = send ? 'No signal. Your message is kept. Send it again when you’re back online.'
      : 'No signal. Try again when you’re back online.';
  } else if (dropped) {
    why = 'Not sure it went. Check the chat before sending again.';   // a send, online: see above
  } else if (e.timeout || st === 408 || e.name === 'AbortError') {
    why = send ? 'Not sure it went. Check the chat before sending again.'
      : 'No answer in time. Try again.';
  } else if (st === 401) {
    why = 'Your sign-in needs a refresh. Try again.';
  } else if ((dobj && ('graph_code' in dobj)) || graphSaid) {
    // WhatsApp refused: see the note above. WhatsApp's own "something went
    // wrong" codes stay "not sure".
    const given = dobj ? dobj.graph_code : null;
    const code = (given != null && String(given) !== '') ? String(given) : (graphSaid ? graphSaid[1] : '');
    // No code: WhatsApp's answer never came. Main's /send passes a lost
    // answer ("network_error: ReadTimeout", a gateway page, a Zoko 5xx) as
    // this same 502, and WhatsApp may have taken the message; #3548 makes
    // those a 409 "Not sure it went". So only an error that names a
    // definite no (never reached, or refused before any send) reads "Not
    // sent" (second adversary on round 2). The flap's key reads this sentence.
    const perr = String((dobj && dobj.error) || '');
    const maybe = !perr || perr === 'send_failed' || /^zoko_(408|5\d\d)$/.test(perr)
      || (/^network_error/.test(perr) && !/ConnectTimeout/.test(perr));
    if (!code && maybe) {
      if (unsure) return unsure;
      why = 'Not sure it went. Check the chat before sending again.';
    } else if (!code) why = lead ? 'Try again.' : 'Not sent. Try again.';
    else if (/^(1|2|131000|131016)$/.test(code)) {
      why = send ? 'Not sure it went. Check the chat before sending again.' : 'WhatsApp had a problem. Try again in a moment.';
    } else {
      const w = cswFailWhy([{ code }], platOf());
      why = lead ? `${w}.` : `Not sent. ${w}.`;
    }
  } else if (/^media upload failed\.?$/i.test(msg.trim())) {
    // Said before any provider call: nothing went (adversary pass).
    why = 'The file did not upload. Nothing reached the customer. Try again.';
  } else {
    // A slip in the page's own code ("Cannot read properties of undefined
    // (reading 'x')") is not a sentence for the rep (adversary pass).
    const codeSlip = /^(TypeError|ReferenceError|RangeError|SyntaxError)$/.test(String(e.name || ''));
    why = plain(dobj ? detail.message : '')
      || plain(typeof detail === 'string' ? detail : '')
      || (codeSlip ? '' : plain(msg));
    if (!why && st >= 500 && unsure) return unsure;
    if (!why && st) {
      why = st === 403 ? 'You don’t have access to do that.'
        : st === 404 ? 'That is no longer there. Refresh and try again.'
        // A refusal with no sentence of its own: say only that it was not
        // taken — never a guess at why (it read "This changed while you
        // were on it" after WhatsApp itself refused).
        : st === 409 ? (send ? 'That was not accepted. Check the chat before trying again.'
          : 'That was not accepted. Refresh and try again.')
        : st === 413 ? 'That file is too big to send.'
        : st === 429 ? 'Too many tries at once. Wait a moment, then try again.'
        : st >= 500 ? (send ? 'Not sure it went. Check the chat before sending again.'
          : 'Something went wrong on EON’s side. Try again in a moment.')
        : st >= 400 ? 'That was not accepted as it is. Check it and try again.'
        : '';
    }
  }
  if (why) return lead ? `${lead} ${why}` : why;
  if (opts.otherwise) return String(opts.otherwise);
  return `${lead || 'That did not work.'} Try again.`;
}

function cswToast(msg, isError) {
  let host = document.getElementById('csw-toasts');
  if (!host) { host = document.createElement('div'); host.id = 'csw-toasts'; host.className = 'csw-toasts'; document.body.appendChild(host); }
  const now = Date.now();
  const last = host.lastElementChild;
  const repeated = last && last.textContent === String(msg) && last._isError === !!isError
    && now - last._shownAt < 1000;
  const t = repeated ? last : document.createElement('div');
  clearTimeout(t._leaveTimer);
  clearTimeout(t._removeTimer);
  t._shownAt = now; t._isError = !!isError;
  t.className = 'csw-toast' + (isError ? ' err' : '');
  t.setAttribute('role', 'status');
  t.textContent = msg;
  if (!repeated) host.appendChild(t);
  cswPositionToasts();
  t._leaveTimer = setTimeout(() => {
    t.classList.add('out'); t._removeTimer = setTimeout(() => t.remove(), 300);
  }, isError ? 4200 : 2600);
}

// iOS ghost-click law (2026-07-25, operator video: "you add photo — it
// closes down"): the tap that OPENS a sheet can re-fire as a click on
// the freshly painted backdrop and close it inside the same gesture, so
// on phones sheets flash open then vanish. Every backdrop-close in the
// app goes through this: arm only after the opening gesture settles,
// and close only when the press STARTED on the backdrop too (a drag
// from inside the sheet that ends outside must not close it either).

// Vet 2026-07-27: the backend is Lagos-pinned everywhere; two FE date
// defaults used UTC and went stale between 00:00-01:00 WAT.
function mtxLagosToday() {
  return new Intl.DateTimeFormat('en-CA',
    { timeZone: 'Africa/Lagos' }).format(new Date());
}

// Vet 2026-07-27: Escape closes money sheets (only the snapshot modal
// had it). Listener self-removes with the sheet.
function mtxEscClose(ov, close) {
  const onKey = (e) => {
    if (e.key !== 'Escape') return;
    if (!document.body.contains(ov)) {
      document.removeEventListener('keydown', onKey);
      return;
    }
    document.removeEventListener('keydown', onKey);
    close();
  };
  document.addEventListener('keydown', onKey);
}

function eonBackdropClose(el, close) {
  let armed = false, downOnEl = false;
  setTimeout(() => { armed = true; }, 400);
  el.addEventListener('pointerdown', (e) => { downOnEl = (e.target === el); });
  el.addEventListener('click', (e) => { if (armed && downOnEl && e.target === el) close(); });
}

// THE KEY A SHEET OPENED FROM (floor-3 verdict, 2026-09-26). Safari and iOS
// never give a pressed button focus, so when a sheet opened from a tap the
// page itself was "where the rep was", and Escape or ✕ dropped focus on the
// page. The desk remembers the last key a pointer pressed; a sheet opened
// while nothing has focus hands focus back to that key when it closes. A
// field with focus (the reply box) still gets it back, as before.
const CSW_KEY_SEL = 'button, summary, a[href], [role="button"]';
let cswPressedKey = null, cswPressedAt = 0;
if (typeof document !== 'undefined' && document.addEventListener) {
  document.addEventListener('pointerdown', e => {
    cswPressedKey = e.target && e.target.closest ? e.target.closest(CSW_KEY_SEL) : null;
    cswPressedAt = Date.now();
  }, true);
}
function cswSheetOpener(active) {
  if (active && active !== document.body && active !== document.documentElement) return active;
  const k = cswPressedKey;
  const fresh = k && k.isConnected && Date.now() - cswPressedAt < 2000;
  return fresh && !(k.closest && k.closest('.csw-overlay')) ? k : active;
}

function cswOverlay(inner, center) {
  const prevFocus = cswSheetOpener(document.activeElement);
  const ov = document.createElement('div');
  ov.className = 'csw-overlay' + (center ? ' csw-overlay--center' : '');
  ov.returnFocusTarget = prevFocus;
  if (typeof _trActive === 'function' && _trActive()) ov.classList.add('tr-overlay');
  ov.innerHTML = `<div class="csw-sheet" role="dialog" aria-modal="true">${center ? '' : '<div class="csw-grab"></div>'}${inner}</div>`;
  const title = ov.querySelector('.csw-sheet__t');
  // A caller may replace this default with a more specific task title.
  if (title?.textContent?.trim()) ov.querySelector('[role="dialog"]').setAttribute('aria-label', title.textContent.trim());
  eonBackdropClose(ov, () => ov.remove());
  document.body.appendChild(ov);
  const taskRoom = ov.classList.contains('tr-overlay') ? document.querySelector('#tos.task-room') : null;
  if (taskRoom) taskRoom.setAttribute('inert', '');
  // A11y (2026-06-13, vision-doc backlog #7): every sheet built here —
  // photo/label/template/note/product/order/route/simulator pickers —
  // gets the same treatment: icon-only ✕ buttons receive a name, focus
  // moves INTO the sheet, Tab is trapped inside it, Escape closes it,
  // and focus returns to where the rep was. All close paths call
  // ov.remove(), so the focus-restore wrap covers every exit.
  ov.querySelectorAll('.csw-x').forEach(b => {
    if (!b.getAttribute('aria-label')) b.setAttribute('aria-label', 'Close');
  });
  const origRemove = ov.remove.bind(ov);
  ov.remove = () => {
    origRemove();
    if (taskRoom && !document.querySelector('.tr-overlay')) taskRoom.removeAttribute('inert');
    try {
      if (ov.restoreTaskFocus) ov.restoreTaskFocus(prevFocus);
      else cswRestoreFocus(prevFocus);
    } catch (_) {}
  };
  const focusables = () => Array.from(ov.querySelectorAll(
    'button, summary, a[href], area[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
    .filter(el => !el.disabled && el.offsetParent !== null);
  setTimeout(() => {
    if (!ov.isConnected || ov.contains(document.activeElement)) return;
    const f = focusables();
    (f.find(el => !el.classList.contains('csw-x')) || f[0])?.focus();
  }, 30);
  ov.addEventListener('keydown', e => {
    if (e.key === 'Escape') { e.stopPropagation(); ov.remove(); return; }
    if (e.key !== 'Tab') return;
    const f = focusables();
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
  return ov;
}

// Escape closes the TOPMOST csw layer consistently (quick-react bar →
// emoji picker → lightbox → sheet). A sheet with focus inside handles
// its own Escape (and stops propagation); this catches the rest — e.g.
// a lightbox opened by mouse where focus never moved off the page.
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  const top = document.getElementById('csw-reactbar')
    || document.getElementById('csw-emojipop')
    || Array.from(document.querySelectorAll('.csw-lightbox')).pop()
    || Array.from(document.querySelectorAll('.csw-overlay')).pop();
  if (top) { top.remove(); e.stopPropagation(); }
});

function cswLightbox(url, cap, isVideo, group) {
  // Optional 4th arg (2026-09-07, operator-chat galleries): {items:
  // [{url, cap}], index} adds prev/next paging, a "2 of 5" counter and
  // arrow-key / sideways-swipe navigation. Absent — every existing CS
  // call site — the single-photo behavior is unchanged.
  const gal = (!isVideo && group && Array.isArray(group.items)
               && group.items.length > 1)
    ? { items: group.items,
        at: Math.min(Math.max(0, group.index | 0), group.items.length - 1) }
    : null;
  const lb = document.createElement('div');
  lb.className = 'csw-lightbox';
  lb.setAttribute('role', 'dialog');
  lb.setAttribute('aria-modal', 'true');
  const media = isVideo
    ? `<video src="${esc(url)}" controls autoplay playsinline></video>`
    : `<img src="${esc(url)}" alt="${esc(cap || 'photo')}"/>`;
  const navHtml = gal
    ? `<button class="csw-lightbox__nav csw-lightbox__nav--prev" aria-label="Previous image">&lsaquo;</button>`
      + `<button class="csw-lightbox__nav csw-lightbox__nav--next" aria-label="Next image">&rsaquo;</button>`
      + `<div class="csw-lightbox__count">${gal.at + 1} of ${gal.items.length}</div>`
    : '';
  lb.innerHTML = `<button class="csw-lightbox__x" aria-label="Close">&times;</button>${media}${(cap || gal) ? `<div class="csw-lightbox__cap"${cap ? '' : ' hidden'}>${esc(cap || '')}</div>` : ''}${isVideo ? '' : '<div class="csw-lightbox__hint">pinch or double-tap to zoom</div>'}${navHtml}`;
  const show = (i) => {
    if (!gal) return;
    gal.at = ((i % gal.items.length) + gal.items.length) % gal.items.length;
    const it = gal.items[gal.at];
    const old = lb.querySelector('img');
    if (!old) return;
    const im = document.createElement('img');
    im.src = it.url;
    im.alt = it.cap || 'photo';
    old.replaceWith(im);
    lb._zoomScale = 1;
    const capEl = lb.querySelector('.csw-lightbox__cap');
    if (capEl) { capEl.textContent = it.cap || ''; capEl.hidden = !it.cap; }
    const cnt = lb.querySelector('.csw-lightbox__count');
    if (cnt) cnt.textContent = `${gal.at + 1} of ${gal.items.length}`;
    cswLightboxZoom(lb, im);
    // Warm the next door so paging never opens onto a blank frame.
    const nx = gal.items[(gal.at + 1) % gal.items.length];
    if (nx) { const warm = new Image(); warm.src = nx.url; }
  };
  if (gal) lb._galStep = (d) => show(gal.at + d);
  lb.addEventListener('click', e => {
    // The close arm keeps its exact shipped shape — the ghost-click pin
    // (2026-07-25) reads this literal to prove the X never waits out the
    // guard, and a gallery is no reason to rewrite another author's proof.
    if (e.target.classList.contains('csw-lightbox__x')) lb.remove();
    else if (gal && e.target.classList.contains('csw-lightbox__nav--prev')) show(gal.at - 1);
    else if (gal && e.target.classList.contains('csw-lightbox__nav--next')) show(gal.at + 1);
  });
  eonBackdropClose(lb, () => lb.remove());
  lb.addEventListener('keydown', e => {
    if (e.key === 'Escape') { e.stopPropagation(); lb.remove(); }
    else if (gal && e.key === 'ArrowLeft') { e.preventDefault(); show(gal.at - 1); }
    else if (gal && e.key === 'ArrowRight') { e.preventDefault(); show(gal.at + 1); }
  });
  document.body.appendChild(lb);
  lb.querySelector('.csw-lightbox__x')?.focus();
  if (!isVideo) cswLightboxZoom(lb, lb.querySelector('img'));
  cswLightboxDismiss(lb);   // swipe down to close (ME-04)
  return lb;
}

// Pinch / double-tap zoom + drag pan for the photo lightbox (operator
// 2026-07-25: reps can't inspect customer photos — size labels, defect
// close-ups). The PWA viewport locks page zoom, so the gesture is
// implemented here: pointer events (mouse + touch unified), scale
// clamped 1–4, transform-only (no layout), double-tap toggles 2.5×
// at the tapped point, drag pans while zoomed, everything resets at 1×.
// Swipe-down-to-dismiss (ME-04). Only engages at rest scale — a pan while
// zoomed must never close the viewer mid-inspection.
function cswLightboxDismiss(lb) {
  let y0 = null, x0 = null, dragging = false;
  // Resolved per event, not captured once: gallery paging (2026-09-07)
  // replaces the <img> node, and a captured reference would animate a
  // detached element.
  const mediaEl = () => lb.querySelector('img, video');
  lb.addEventListener('touchstart', (e) => {
    if (e.touches.length !== 1) { y0 = null; return; }
    if (lb._zoomScale && lb._zoomScale > 1.02) { y0 = null; return; }   // zoomed → pan
    y0 = e.touches[0].clientY; x0 = e.touches[0].clientX; dragging = false;
  }, { passive: true });
  lb.addEventListener('touchmove', (e) => {
    const media = mediaEl();
    if (y0 == null || !media) return;
    const dy = e.touches[0].clientY - y0;
    const dx = e.touches[0].clientX - x0;
    if (dy > 8 && dy > Math.abs(dx)) {
      dragging = true;
      media.style.transition = 'none';
      media.style.transform = `translateY(${Math.min(dy, 400)}px) scale(${Math.max(0.82, 1 - dy / 1200)})`;
      lb.style.background = `rgba(0,0,0,${Math.max(0.25, 0.92 - dy / 500)})`;
    }
  }, { passive: true });
  lb.addEventListener('touchend', (e) => {
    const media = mediaEl();
    if (y0 == null || !media) { y0 = null; return; }
    const dy = (e.changedTouches[0].clientY - y0);
    const dx = (e.changedTouches[0].clientX - x0);
    if (dragging && dy > 110) { lb.remove(); return; }
    media.style.transition = 'transform .22s var(--ease-emph-decel)';
    media.style.transform = '';
    lb.style.background = '';
    // Sideways flick pages a gallery — only at rest scale, and the
    // vertical dismiss above always wins a diagonal.
    if (!dragging && lb._galStep && Math.abs(dx) > 60
        && Math.abs(dx) > Math.abs(dy) * 1.4
        && !(lb._zoomScale && lb._zoomScale > 1.02)) {
      lb._galStep(dx < 0 ? 1 : -1);
    }
    y0 = null; dragging = false;
  });
}

function cswLightboxZoom(lb, img) {
  if (!img) return;
  let scale = 1, tx = 0, ty = 0;
  const ptrs = new Map();
  let pinchD0 = 0, pinchS0 = 1, lastTap = 0, dragX = 0, dragY = 0, moved = false;
  const apply = () => {
    img.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    img.classList.toggle('zoomed', scale > 1.01);
    // Published for the swipe-down dismiss (ME-04): a pan while zoomed must
    // never be read as a dismiss gesture.
    lb._zoomScale = scale;
  };
  const clamp = () => {
    scale = Math.min(4, Math.max(1, scale));
    if (scale <= 1.01) { scale = 1; tx = 0; ty = 0; }
    const mx = (scale - 1) * img.clientWidth / 2 + 40;
    const my = (scale - 1) * img.clientHeight / 2 + 40;
    tx = Math.min(mx, Math.max(-mx, tx));
    ty = Math.min(my, Math.max(-my, ty));
  };
  const zoomAt = (cx, cy, s) => {
    const r = img.getBoundingClientRect();
    const ox = cx - (r.left + r.width / 2), oy = cy - (r.top + r.height / 2);
    tx -= ox * (s / scale - 1);
    ty -= oy * (s / scale - 1);
    scale = s;
    clamp(); apply();
  };
  img.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    img.setPointerCapture(e.pointerId);
    ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    moved = false;
    if (ptrs.size === 2) {
      const [a, b] = [...ptrs.values()];
      pinchD0 = Math.hypot(a.x - b.x, a.y - b.y);
      pinchS0 = scale;
    } else { dragX = e.clientX; dragY = e.clientY; }
  });
  img.addEventListener('pointermove', (e) => {
    if (!ptrs.has(e.pointerId)) return;
    ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (ptrs.size === 2) {
      const [a, b] = [...ptrs.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchD0 > 0) zoomAt((a.x + b.x) / 2, (a.y + b.y) / 2,
                              pinchS0 * (d / pinchD0));
      moved = true;
    } else if (scale > 1) {
      tx += e.clientX - dragX; ty += e.clientY - dragY;
      dragX = e.clientX; dragY = e.clientY;
      if (Math.abs(e.clientX - dragX) + Math.abs(e.clientY - dragY) > 2) moved = true;
      clamp(); apply();
      moved = true;
    }
  });
  const up = (e) => {
    ptrs.delete(e.pointerId);
    if (ptrs.size < 2) pinchD0 = 0;
    if (ptrs.size === 0 && !moved) {
      const now = Date.now();
      if (now - lastTap < 320) {           // double-tap: toggle 2.5×
        zoomAt(e.clientX, e.clientY, scale > 1.01 ? 1 : 2.5);
        lastTap = 0;
      } else lastTap = now;
    }
  };
  img.addEventListener('pointerup', up);
  img.addEventListener('pointercancel', up);
  img.addEventListener('wheel', (e) => {   // desktop: scroll to zoom
    e.preventDefault();
    zoomAt(e.clientX, e.clientY, scale * (e.deltaY < 0 ? 1.15 : 0.87));
  }, { passive: false });
  // a tap on the image at 1× must not fall through and close the box
  img.addEventListener('click', (e) => e.stopPropagation());
}

// ── Labels ───────────────────────────────────────────────────
function cswLabelColor(c) {
  return ({ lime: 'var(--accent)', blue: 'var(--g-blue)', violet: 'var(--g-violet)',
    magenta: 'var(--g-magenta)', warn: 'var(--warn)', ok: 'var(--ok)',
    danger: 'var(--danger)' }[c || 'lime']) || 'var(--accent)';
}
async function cswLabelSheet() {
  const cid = cswState().active; if (!cid) return;
  let defs = [], current = [], unread = '';
  try {
    const [a, b] = await Promise.all([
      api('/api/cs/labels'),
      api(`/api/cs/threads/${encodeURIComponent(cid)}/labels`)]);
    defs = (a && a.labels) || []; current = (b && b.labels) || [];
  // Round 2 (2026-09-26): a failed read showed "No labels yet" — the rep
  // tapped Labels and was told the shop has none. Say it did not load.
  } catch (e) { unread = cswSayWhy(e, { lead: 'Labels did not load.' }); }
  const cur = new Set(current);
  const COLORS = ['lime', 'blue', 'violet', 'magenta', 'warn', 'ok', 'danger'];
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Labels</div><button class="csw-x">✕</button></div>
    <div style="font-size:11px;color:var(--ink-mute);margin-bottom:12px">Tap to add/remove — labels show on the thread and are filterable.</div>
    <div class="csw-labelpick" id="csw-labelpick">${defs.map(d => `<button class="csw-labelchip ${cur.has(d.label) ? 'on' : ''}" data-l="${esc(d.label)}" style="--lc:${cswLabelColor(d.color)}">${esc(d.label)}</button>`).join('') || `<div class="csw-empty" style="padding:8px">${esc(unread || 'No labels yet')}</div>`}</div>
    <div class="csw-newlabel">
      <input class="csw-search" id="csw-newlabel-input" placeholder="Create a new label…" maxlength="28" style="margin:14px 0 8px"/>
      <div class="csw-colorpick" id="csw-colorpick">${COLORS.map((c, i) => `<button class="csw-colordot ${i === 0 ? 'on' : ''}" data-c="${c}" style="--lc:${cswLabelColor(c)}"></button>`).join('')}</div>
      <button class="csw-confirm" id="csw-newlabel-add" style="margin-top:10px">Add label</button>
    </div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const bindChip = (btn) => btn.addEventListener('click', async () => {
    const label = btn.dataset.l, on = btn.classList.contains('on');
    btn.classList.toggle('on');
    try {
      if (on) await api(`/api/cs/threads/${encodeURIComponent(cid)}/labels?label=${encodeURIComponent(label)}`, { method: 'DELETE' });
      else await api(`/api/cs/threads/${encodeURIComponent(cid)}/labels`, { method: 'POST', body: JSON.stringify({ label }) });
      cswLoadLabels(cid, true); cswLoadThreads(true);
      // Confirm + close (WhatsApp/Telegram label menus dismiss on action).
      // Adding closes immediately; removing stays open so the rep can
      // re-pick without re-opening the sheet.
      if (!on) {
        cswToast('Labelled ✓');
        setTimeout(() => ov.remove(), 220);
      } else {
        cswToast('Label removed');
      }
    } catch (e) { btn.classList.toggle('on'); cswToast('Could not update label', true); }
  });
  ov.querySelectorAll('.csw-labelchip').forEach(bindChip);
  let newColor = 'lime';
  ov.querySelectorAll('.csw-colordot').forEach(d => d.addEventListener('click', () => {
    newColor = d.dataset.c;
    ov.querySelectorAll('.csw-colordot').forEach(x => x.classList.toggle('on', x === d));
  }));
  ov.querySelector('#csw-newlabel-add').addEventListener('click', async () => {
    const inp = ov.querySelector('#csw-newlabel-input');
    const label = (inp.value || '').trim();
    if (!label) return;
    try {
      await api('/api/cs/labels', { method: 'POST', body: JSON.stringify({ label, color: newColor }) });        // create in palette
      await api(`/api/cs/threads/${encodeURIComponent(cid)}/labels`, { method: 'POST', body: JSON.stringify({ label }) }); // apply to this customer
      inp.value = '';
      cswLoadLabels(cid, true); cswLoadThreads(true); cswRenderActiveFilter();
      cswToast('Labelled ✓');
      setTimeout(() => ov.remove(), 220);   // confirm + close
    } catch (e) { cswToast(cswSayWhy(e, { lead: 'Label not added.' }), true); }
  });
}
// ONE LABELS READ PER OPEN (CS vet 29, 2026-09-24). Opening a chat read
// its labels twice: the auto-answer line (cswLoadFaqBanner) and the 360
// panel (cswLoadLabels) each asked the server on their own. They now share
// one read for a few seconds. A failed read is never shared, and a label
// the rep adds or removes always reads again (fresh).
const CSW_LABELS_SHARE_MS = 10000;
const _cswLabelsRead = new Map();
function cswThreadLabels(cid, fresh) {
  const key = String(cid), now = Date.now();
  for (const [k, v] of _cswLabelsRead) if (now - v.at >= CSW_LABELS_SHARE_MS) _cswLabelsRead.delete(k);
  const hit = _cswLabelsRead.get(key);
  if (!fresh && hit) return hit.p;
  const entry = { at: now, p: api(`/api/cs/threads/${encodeURIComponent(cid)}/labels`) };
  _cswLabelsRead.set(key, entry);
  entry.p.catch(() => { if (_cswLabelsRead.get(key) === entry) _cswLabelsRead.delete(key); });
  return entry.p;
}
async function cswLoadLabels(cid, fresh) {
  try {
    const d = await cswThreadLabels(cid, fresh);
    const el = document.getElementById('csw-labels');
    if (!el || cswState().active !== cid) return;
    el.innerHTML = ((d && d.labels) || []).map(l => `<span class="csw-labeltag">${esc(l)}</span>`).join('');
  } catch (_) {}
}

// ── Reply / quote ────────────────────────────────────────────
// ── WA-native features (2026-07-27): reaction / message pins ────────
async function cswSendReaction(mid, emoji) {
  const cid = cswState().active;
  if (!cid) return;
  try {
    const r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/react`,
      { method: 'POST', body: JSON.stringify({ message_id: mid, emoji }) });
    if (r && r.ok) cswToast('Reacted ' + emoji);
    else cswToast(r && r.error === 'reactions_whatsapp_cloud_only'
      ? 'Reactions work on WhatsApp threads only' : 'Could not react', true);
  } catch (_) { cswToast('Could not react', true); }
}

async function cswPinMessage(bub, txt) {
  const cid = cswState().active;
  const mid = cswBubMid(bub);
  if (!cid || !mid) { cswToast('This message cannot be pinned', true); return; }
  try {
    await api(`/api/cs/threads/${encodeURIComponent(cid)}/pin-message`,
      { method: 'POST', body: JSON.stringify({ message_id: mid, snippet: (txt || '').slice(0, 200) }) });
    cswToast('Pinned');
    cswLoadPins(cid);
  } catch (_) { cswToast('Could not pin', true); }
}

async function cswLoadPins(cid) {
  try {
    const d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/pinned-messages`);
    if (!d || cswState().active !== cid) return;
    cswRenderPins(d.pins || []);
  } catch (_) {}
}

function cswRenderPins(pins) {
  const el = document.getElementById('csw-pins');
  if (!el) return;
  if (!pins.length) { el.hidden = true; el.innerHTML = ''; return; }
  el.hidden = false;
  el.innerHTML = pins.map(p =>
    `<span class="csw-pin-chip" data-pin-mid="${esc(p.message_id)}">` +
    `<span class="csw-pin-chip__t">${esc((p.body_snippet || 'pinned message').slice(0, 60))}</span>` +
    `<button class="csw-pin-chip__x" aria-label="Unpin" type="button">✕</button></span>`).join('');
  el.querySelectorAll('.csw-pin-chip').forEach(chip => {
    const mid = chip.dataset.pinMid;
    chip.querySelector('.csw-pin-chip__x').addEventListener('click', async (e) => {
      e.stopPropagation();
      const cid = cswState().active;
      try {
        await api(`/api/cs/threads/${encodeURIComponent(cid)}/pin-message`,
          { method: 'POST', body: JSON.stringify({ message_id: mid, action: 'remove' }) });
        cswLoadPins(cid);
      } catch (err) { cswToast(cswSayWhy(err, { lead: 'Not unpinned.' }), true); }   // was silent (round 2)
    });
    chip.addEventListener('click', () => {
      const row = document.querySelector(`.csw-row[data-mid="${(window.CSS && CSS.escape) ? CSS.escape(mid) : mid}"]`);
      if (row) { row.scrollIntoView({ behavior: 'smooth', block: 'center' }); row.classList.add('csw-row--flash'); setTimeout(() => row.classList.remove('csw-row--flash'), 1600); }
    });
  });
}

function cswBubMid(bub) {
  const row = bub && bub.closest ? bub.closest('.csw-row') : null;
  return (row && row.dataset.mid) || '';
}
function cswSetReply(text, who, mid, quiet) {
  cswState().replyTo = { text: (text || '').slice(0, 200), who: who || '', mid: mid || '' };
  const el = document.getElementById('csw-reply-ctx'); if (!el) return;
  el.hidden = false;
  el.innerHTML = `<div class="csw-reply-q"><div class="csw-reply-q__t"><b>${esc(who || 'Replying')}</b><br>${esc((text || '').slice(0, 140))}</div><button class="csw-reply-x" aria-label="Cancel">✕</button></div>`;
  el.querySelector('.csw-reply-x').addEventListener('click', cswClearReply);
  // `quiet`: a quote put back on its own (opening a chat) must not raise
  // the phone's keyboard; a rep's own Reply tap still focuses the box.
  if (!quiet) document.getElementById('csw-input')?.focus();
}
function cswClearReply() {
  cswState().replyTo = null;
  const el = document.getElementById('csw-reply-ctx');
  if (el) { el.hidden = true; el.innerHTML = ''; }
}
// Vet 2026-09-24 (fault 6, adversary pass): the quote of a reply that did
// not go rides with that chat's failed words until a send is confirmed, so
// a rep who moved on and came back retries the SAME request — same words,
// same quote, same duplicate-guard id. Only while the box holds exactly
// the words that failed (edited words are a new message); the ✕ lets it go.
function cswArmFailedQuote(cid, words, quiet) {
  const s = cswState();
  const handle = cswDraftCapture(cid), data = cswDraftStore(handle);
  const op = data?.pending[handle.slot];
  if (op && s._closedFailedQuote?.[cid] === op.actionId) return;
  const fq = (s._failedQuote || {})[cid] || op?.quote;
  if (!fq) return;
  if (fq.words !== String(words || '').trim()) { if (s._failedQuote) delete s._failedQuote[cid]; return; }
  if (s.replyTo || s.active !== cid) return;
  cswSetReply(fq.text, fq.who, fq.mid, quiet);
  document.querySelector('#csw-reply-ctx .csw-reply-x')?.addEventListener('click', () => {
    if (s._failedQuote && s._failedQuote[cid] === fq) delete s._failedQuote[cid];
    if (op) (s._closedFailedQuote = s._closedFailedQuote || {})[cid] = op.actionId;
  });
}

// ── Outbound photos, WhatsApp-grade (2026-07-24) ─────────────────────
// Pick → auto-shrink on-device → optimistic bubble with live upload % →
// ✓ on server accept, ✗ + Retry on failure. The bubble survives transcript
// re-renders via the _localPhotos registry and sunsets 60s after ✓ (by then
// the webhook echo carries the real message).

// On-device compression: longest edge ≤2048px, JPEG, quality-stepped until
// ≤4MB (WhatsApp's image ceiling is 5MB; phones shoot 4-12MB). EXIF
// orientation is honored via createImageBitmap; any failure falls back to
// the original file — compression must never block a send.
async function cswCompressImage(file, maxEdge, targetBytes) {
  maxEdge = maxEdge || 2048; targetBytes = targetBytes || (4 * 1024 * 1024);
  try {
    if (!/^image\//.test(file.type || '')) return file;
    let bmp = null;
    try { bmp = await createImageBitmap(file, { imageOrientation: 'from-image' }); }
    catch (_) {
      bmp = await new Promise((res, rej) => {
        const im = new Image();
        im.onload = () => res(im); im.onerror = rej;
        im.src = URL.createObjectURL(file);
      });
    }
    const w = bmp.width || bmp.naturalWidth, h = bmp.height || bmp.naturalHeight;
    if (!w || !h) return file;
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    if (scale === 1 && file.size <= targetBytes && file.type === 'image/jpeg') return file;
    const cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
    const cv = document.createElement('canvas'); cv.width = cw; cv.height = ch;
    cv.getContext('2d').drawImage(bmp, 0, 0, cw, ch);
    let q = 0.85, blob = null;
    for (let i = 0; i < 4 && q > 0.3; i++, q -= 0.15) {
      blob = await new Promise(res => cv.toBlob(res, 'image/jpeg', q));
      if (blob && blob.size <= targetBytes) break;
    }
    if (!blob) return file;
    return (blob.size < file.size || file.size > targetBytes) ? blob : file;
  } catch (_) { return file; }
}

function cswLocalPhotoReg() {
  const s = cswState();
  if (!s._localPhotos) s._localPhotos = {};
  return s._localPhotos;
}

function cswLocalBubbleHtml(e) {
  const pct = (e.state === 'sending' && e.pct != null) ? ` <b class="csw-localpct">${e.pct}%</b>` : '';
  const state = e.state === 'sent' ? '<span class="csw-lok">✓ sent</span>'
    : e.state === 'failed' ? `<span class="csw-lfail">✗ ${esc(e.err || 'failed')}</span>`
    : `<span class="csw-lsend">◔ sending…${pct}</span>`;
  const retry = e.state === 'failed'
    ? `<div class="csw-lact"><button class="csw-mini" data-lretry="${e.id}">Retry</button><button class="csw-mini csw-mini--ghost" data-ldrop="${e.id}">Discard</button></div>` : '';
  const media = e.kind === 'video'
    ? `<video class="csw-bub__video" src="${e.url}#t=0.1" preload="metadata" muted playsinline controls></video>`
    : `<img class="csw-bub__img" src="${e.url}" alt="photo"/>`;
  return `<div class="csw-row out"><div class="csw-bub out has-img csw-bub--local" data-lid="${e.id}">
      ${media}
      ${e.caption ? `<div class="csw-bub__txt">${esc(e.caption)}</div>` : ''}
      <div class="csw-bub__t">${state}</div>${retry}
    </div></div>`;
}

// Re-append pending local bubbles after any transcript re-render (the
// innerHTML rebuild wipes them — same defect class as the p315 order-
// history lesson: rebuilds must replay local state).
function cswAppendLocalBubbles(cid, body) {
  const reg = (cswLocalPhotoReg()[cid] || []).filter(e =>
    !(e.state === 'sent' && Date.now() - e.ts > 60000));
  cswLocalPhotoReg()[cid] = reg;
  if (!body || !reg.length) return;
  body.insertAdjacentHTML('beforeend', reg.map(cswLocalBubbleHtml).join(''));
  body.scrollTop = body.scrollHeight;
  cswBindLocalPhotoKeys(body, cid);
}

// Vet 2026-09-24 (fault 2): every paint added another click listener to
// EVERY Retry/Discard key in the chat, so with two failed photos one tap on
// Retry uploaded the first photo three times. Each key is bound once, and
// cswRetryLocalPhoto ignores a photo that is not in the failed state.
function cswBindLocalPhotoKeys(root, cid) {
  root.querySelectorAll('[data-lretry], [data-ldrop]').forEach(b => {
    if (b._cswLocalKey) return;
    b._cswLocalKey = true;
    b.addEventListener('click', () => (b.dataset.lretry
      ? cswRetryLocalPhoto(cid, b.dataset.lretry)
      : cswDropLocalPhoto(cid, b.dataset.ldrop)));
  });
}

function cswPaintLocalBubble(cid, id) {
  if (cswState().active !== cid) return;
  const body = document.getElementById('csw-script');
  const e = (cswLocalPhotoReg()[cid] || []).find(x => x.id === id);
  if (!body || !e) return;
  const cur = body.querySelector(`[data-lid="${id}"]`);
  if (cur) { cur.closest('.csw-row').outerHTML = cswLocalBubbleHtml(e); }
  else { body.insertAdjacentHTML('beforeend', cswLocalBubbleHtml(e)); body.scrollTop = body.scrollHeight; }
  cswBindLocalPhotoKeys(body, cid);
}

function cswDropLocalPhoto(cid, id) {
  const reg = cswLocalPhotoReg();
  const e = (reg[cid] || []).find(x => x.id === id);
  if (e && e.url) { try { URL.revokeObjectURL(e.url); } catch (_) {} }
  reg[cid] = (reg[cid] || []).filter(x => x.id !== id);
  const body = document.getElementById('csw-script');
  const cur = body && body.querySelector(`[data-lid="${id}"]`);
  if (cur) cur.closest('.csw-row').remove();
}

// ── One upload, one action id (Sol 6.1 review of #3548, 1 Oct 2026) ──
// The photo, video and voice doors send nothing without the desk's id
// (routes_cs._desk_action). Like api()'s, the id is keyed on what is
// being sent (the file's own bytes, the chat, the caption), so a Retry,
// a lost answer and a reload all name the SAME send and the server sends
// it once. It is let go when EON says the send went. A resend after
// "still not sure" is a new id, and only on the rep's yes.
function cswActionStore(op, key, id) {
  try {
    if (op === 'get') return sessionStorage.getItem(key);
    if (op === 'set') sessionStorage.setItem(key, id); else sessionStorage.removeItem(key);
  } catch (_) { /* storage blocked: the entry's own id still covers its Retry */ }
  return null;
}

async function cswMediaAction(cid, kind, file, extra) {
  let key = null;
  try {
    const hex = b => Array.from(new Uint8Array(b), x => x.toString(16).padStart(2, '0')).join('');
    const bytes = hex(await crypto.subtle.digest('SHA-256', await file.arrayBuffer()));
    const actor = State.user?.user_id || State.user?.id || State.user?.staff_id || 'desk';
    const input = JSON.stringify([State.tenant?.id || State.tenant?.slug, actor, String(cid), kind, extra || '', bytes]);
    key = 'eon.contact.action.' + hex(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input)));
  } catch (_) { /* no digest here: the id still holds for this entry's Retry */ }
  let id = key && cswActionStore('get', key);
  if (!id) { id = crypto.randomUUID(); if (key) cswActionStore('set', key, id); }
  return { key, id };
}

function cswMediaActionDone(act) { if (act && act.key) cswActionStore('drop', act.key); }

function cswMediaActionRenew(act) {
  act.id = crypto.randomUUID();
  if (act.key) cswActionStore('set', act.key, act.id);
  return act.id;
}

async function cswRetryLocalPhoto(cid, id) {
  const e = (cswLocalPhotoReg()[cid] || []).find(x => x.id === id);
  // Only a failed photo is retried: one already sending (a double tap, a
  // stale key) or already sent must never upload again (fault 2).
  if (!e || !e.blob || e.state !== 'failed') return;
  // "Still not sure" about this same send: its id never sends again, so a
  // Retry is the rep's yes to a NEW send, asked first.
  if (e.unsure && e.act) {
    if (!(await cswResendAnyway(e.err))) return;
    cswMediaActionRenew(e.act); e.unsure = false;
  }
  e.state = 'sending'; e.pct = 0; e.err = '';
  cswPaintLocalBubble(cid, id);
  cswUploadLocalPhoto(cid, e);
}

// A fresh contact-action id for the failed-message flap's Send again (vet
// round 2, 2026-09-26). The server sends an action once and answers a
// repeat with the first result (cs_send_once, #3548). It must be a UUID
// (the server ignores anything else), and crypto.randomUUID is missing on
// older phones, so there is a fallback. Photos, videos and voice notes
// take theirs from cswMediaAction instead (Sol 6.1 on #3548).
function cswActionId() {
  const c = typeof crypto !== 'undefined' ? crypto : null;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;   // version 4
  b[8] = (b[8] & 0x3f) | 0x80;   // RFC 4122 variant
  const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// XHR (fetch has no upload progress) → live % in the bubble.
async function cswUploadLocalPhoto(cid, e) {
  try { cswRequireReplyWindow(cid); }
  catch (err) { e.state = 'failed'; e.err = err.message; cswPaintLocalBubble(cid, e.id); return; }
  try {
  if (!e.act) e.act = await cswMediaAction(cid, e.kind || 'photo', e.src || e.blob, e.caption);
  const fd = new FormData();
  fd.append('file', e.blob, e.kind === 'video' ? (e.fname || 'video.mp4') : 'photo.jpg');
  if (e.caption) fd.append('caption', e.caption);
  fd.append('contact_action_id', e.act.id);
  const xhr = new XMLHttpRequest();
  xhr.open('POST', API + `/api/cs/threads/${encodeURIComponent(cid)}/send-${e.kind === 'video' ? 'video' : 'photo'}`);
  xhr.setRequestHeader('Authorization', 'Bearer ' + State.jwt);
  if (State.tenant) xhr.setRequestHeader('X-Tenant-Slug', State.tenant.slug);
  xhr.upload.onprogress = (ev) => {
    if (!ev.lengthComputable) return;
    e.pct = Math.min(99, Math.round((ev.loaded / ev.total) * 100));
    const el = document.querySelector(`[data-lid="${e.id}"] .csw-localpct`);
    if (el) el.textContent = e.pct + '%';
    else cswPaintLocalBubble(cid, e.id);
  };
  const fail = (why) => {
    e.state = 'failed'; e.err = why || 'send failed';
    cswPaintLocalBubble(cid, e.id);
  };
  // Vet 2026-09-24 (fault 20): the bubble read "✗ failed (413)" or a bare
  // server code. The same plain sentence as a typed send — and a photo that
  // got no answer may already be with the customer, so it says check first.
  xhr.onerror = () => fail(cswSayWhy(new TypeError('Failed to fetch'), { send: true }));
  xhr.ontimeout = () => fail(cswSayWhy({ timeout: true }, { send: true }));
  xhr.onload = () => {
    let body = null;
    try { body = JSON.parse(xhr.responseText || 'null'); } catch (_) {}
    if (xhr.status >= 200 && xhr.status < 300 && body && body.ok) {
      e.state = 'sent'; e.ts = Date.now(); cswMediaActionDone(e.act);
      cswPaintLocalBubble(cid, e.id);
      setTimeout(() => cswLoadTranscript(cid, true), 1200);
      return;
    }
    e.unsure = cswIsUnsureRepeat(xhr.status, body);
    const why = body && (typeof body.detail === 'string' ? body.detail
      : (body.detail && body.detail.message) || body.message);
    fail(cswSayWhy({ message: why || '', status: xhr.status, body: body || {},
      path: `/api/cs/threads/${encodeURIComponent(cid)}/send-${e.kind === 'video' ? 'video' : 'photo'}` }, { send: true }));
  };
  xhr.timeout = 60000;
  xhr.send(fd);
  } catch (err) {   // past the await no caller's catch sees a throw: fail the bubble here
    e.state = 'failed'; e.err = String((err && err.message) || err).slice(0, 80);
    cswPaintLocalBubble(cid, e.id);
  }
}

async function cswStartPhotoSend(file, caption, recipient) {
  const cid = recipient || cswState().active;
  if (!cid || !file) return;
  if (cswWinClosed()) {
    cswExplainReplyBlock();
    return;
  }
  // Diagnosis instrument (2026-08-28: attach attempts stopped reaching
  // the server with NOTHING on screen): any throw between the picker
  // and the upload now says so out loud instead of dying silently —
  // and an entry already pushed flips to 'failed' (Retry/Discard render
  // only for that state; a stranded 'sending…' ghost had no exit).
  const blob = await cswCompressImage(file);   // never throws; falls back to file
  // The request belongs to cid even if the rep switched during compression.
  try { cswRequireReplyWindow(cid); } catch (err) { cswToast(err.message, true); return false; }
  if (!blob) { cswPhotoDeviceReason('Could not read that photo'); cswToast('Could not read that photo — try another or restart EON.', true); return; }
  let e = null;
  try {
  e = {
    id: 'lp' + Date.now().toString(36) + Math.floor(Math.random() * 1e4).toString(36),
    url: URL.createObjectURL(blob),
    caption: (caption || '').trim(),
    blob, src: file,   // the action id keys on the picked file, not its shrink
    state: 'sending', pct: 0, ts: Date.now(), err: '',
  };
  const reg = cswLocalPhotoReg();
  (reg[cid] = reg[cid] || []).push(e);
  cswPaintLocalBubble(cid, e.id);
  cswUploadLocalPhoto(cid, e);
  return true;
  } catch (err) {
    cswPhotoDeviceReason(String((err && err.message) || err).slice(0, 120));
    cswToast('Photo attach failed on this device: '
      + String((err && err.message) || err).slice(0, 90), true);
    if (e && e.state === 'sending') { e.state = 'failed'; e.err = String((err && err.message) || err).slice(0, 80); try { cswPaintLocalBubble(cid, e.id); } catch (_) {} }
  }
}

// Video send (operator go, 2026-07-25): no client compression exists
// for video, so the 16MB gate lives at the picker AND the endpoint;
// everything else rides the photo pipeline with kind='video'.
async function cswStartVideoSend(file, caption, recipient) {
  const cid = recipient || cswState().active;
  if (!cid || !file) return;
  if (cswWinClosed()) {
    cswExplainReplyBlock();
    return;
  }
  const e = {
    id: 'lv' + Date.now().toString(36) + Math.floor(Math.random() * 1e4).toString(36),
    url: URL.createObjectURL(file),
    caption: (caption || '').trim(),
    blob: file, kind: 'video', fname: file.name || 'video.mp4',
    state: 'sending', pct: 0, ts: Date.now(), err: '',
  };
  const reg = cswLocalPhotoReg();
  (reg[cid] = reg[cid] || []).push(e);
  cswPaintLocalBubble(cid, e.id);
  cswUploadLocalPhoto(cid, e);
  return true;
}

// ONE upload path for every photo entry point — the sheet's picker, a desk
// paste (Cmd-V of a screenshot), and drag-drop onto the conversation.
async function cswSendPhotoFile(f, onStatus) {
  const cid = cswState().active;
  const say = onStatus || (() => {});
  try {
    // One id for this send, keyed on the picked file (Sol 6.1 on #3548).
    const act = await cswMediaAction(cid, 'photo', f, '');
    // Same on-device compression as the picker path (PR B gap): a pasted
    // 4K screenshot went up raw and could hit the server's 8MB cap.
    try { f = await cswCompressImage(f); } catch (_) { /* raw fallback */ }
    const fd = new FormData();
    fd.append('file', f, 'photo.jpg');
    const headers = { 'Authorization': `Bearer ${State.jwt}` };
    if (State.tenant) headers['X-Tenant-Slug'] = State.tenant.slug;
    cswRequireReplyWindow(cid);
    let r, body;
    for (let ask = true; ; ask = false) {
      fd.set('contact_action_id', act.id);
      r = await fetch(API + `/api/cs/threads/${encodeURIComponent(cid)}/send-photo`,
        { method: 'POST', headers, body: fd });
      body = await r.json().catch(() => null);
      if (!(ask && cswIsUnsureRepeat(r.status, body)
            && await cswResendAnyway(body.detail.message))) break;
      cswMediaActionRenew(act);           // the rep said yes: a new send
    }
    if (!r.ok) {
      const why = body && (typeof body.detail === 'string' ? body.detail
        : (body.detail && body.detail.message) || body.message);
      throw new Error(why || ('upload failed (' + r.status + ')'));
    }
    cswMediaActionDone(act);
    cswMarkReplied(cid);
    setTimeout(() => cswLoadTranscript(cid, true), 1500);
    return true;
  } catch (e) {
    say(e.message || String(e));
    return false;
  }
}

// Paste / drop confirm sheet — thumbnail + explicit Send. A stray paste must
// never fire an image at a customer.
function cswPastedPhotoSheet(file) {
  const cid = cswState().active;
  if (!cid || cid === CSW_EON_CID) return;
  cswStageMedia(cid, file, 'photo', '');
  if (cswPending(cid).media?.file === file) cswPreparedMediaSheet(cid);
}

function cswPhotoPickResume() {
  // The other half of the pick() stash: if this page load finds a fresh
  // pending pick, the app died under the native gallery (or the sheet
  // never got its file back). Reopen the thread and the sheet with the
  // caption kept, say plainly what happened, and file ONE row through
  // the client-error sink so the loop can count the class. Stale or
  // absent -> do nothing, silently.
  let p = null;
  try { p = JSON.parse(sessionStorage.getItem('csw_photo_pending') || 'null'); } catch (_) {}
  if (!p || !p.cid) return;
  try { sessionStorage.removeItem('csw_photo_pending'); } catch (_) {}
  if (!p.t || (Date.now() - p.t) > 10 * 60 * 1000) return;
  cswPhotoDeviceReason('photo_pick_lost_on_return');   // the Android gallery kill — the key says so
  try {
    api('/api/client-errors', { method: 'POST', body: JSON.stringify({
      message: 'photo_pick_lost_on_return',
      source: 'csw-photo-resume', severity: 'warn',
      url: location.pathname,
    }) }).catch(() => {});
  } catch (_) {}
  setTimeout(() => {
    try {
      cswOpenThread(p.cid);
      cswPhotoSheet();
      const cap = document.getElementById('csw-photo-cap');
      if (cap && p.cap) cap.value = p.cap;
      cswToast("That photo didn't attach — the app reloaded while your gallery was open. Tap Photo library once more to prepare it again.", true);
    } catch (_) {}
  }, 600);
}

function cswPhotoSheet() {
  const cid = cswState().active;
  if (!cid || cid === CSW_EON_CID) return;
  // Two distinct inputs: the LIBRARY picker has NO `capture` attr (so the OS
  // shows the photo library / file browser), the CAMERA picker sets
  // capture="environment". Forcing `capture` on the only input is exactly
  // why the library was unreachable before.
  const _cause = cswState()._photoDeviceReason || '';
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Prepare a photo or video</div><button class="csw-x">✕</button></div>
    ${_cause ? `<div class="csw-photocause" id="csw-photocause">This phone: ${esc(_cause)} — send from your gallery; the camera door is the one that fails here.</div>` : ''}
    <div class="csw-photopick">
      <button class="csw-pill csw-pill--accent csw-photopick__b" id="csw-pick-lib"><span class="csw-pill__i">▦</span> Photo library</button>
      <button class="csw-pill csw-photopick__b" id="csw-pick-cam"><span class="csw-pill__i">◉</span> Camera</button>
      <button class="csw-pill csw-photopick__b" id="csw-pick-vid"><span class="csw-pill__i">▶</span> Video</button>
    </div>
    <input type="file" id="csw-file-lib" accept="image/*" class="csw-fileoff" tabindex="-1" aria-hidden="true"/>
    <input type="file" id="csw-file-cam" accept="image/*" capture="environment" class="csw-fileoff" tabindex="-1" aria-hidden="true"/>
    <input type="file" id="csw-file-vid" accept="video/mp4,video/quicktime,video/3gpp,video/*" class="csw-fileoff" tabindex="-1" aria-hidden="true"/>
    <input class="csw-search" id="csw-photo-cap" placeholder="Add a caption… (optional)" style="margin-top:12px" maxlength="600"/>
    <div style="margin:16px 0 8px;font-size:11px;color:var(--ink-mute);text-align:center">— or paste an image URL —</div>
    <input class="csw-search" id="csw-photo-url" placeholder="https://…" style="margin-bottom:8px"/>
    <button class="csw-mini" id="csw-photo-sendurl" style="width:100%">Prepare image link</button>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const libInput = ov.querySelector('#csw-file-lib');
  const camInput = ov.querySelector('#csw-file-cam');
  // iOS Safari (the reps' world): a display:none file input can silently
  // ignore a programmatic click in a standalone PWA, and a re-picked
  // SAME photo fires no `change` unless the value is reset first —
  // both read as "I tap and it just ignores" (Thelma, 2026-08-28). The
  // inputs sit off-screen instead of display:none, and every open
  // resets the value.
  const pick = (inp) => {
    // Android PWA reality (operator report 2026-08-31, HYPOTHESIS with
    // the bench green end-to-end and client_errors silent): while the
    // native gallery is open the OS may kill the backgrounded app;
    // the rep returns to a fresh reload — sheet gone, pick gone,
    // nothing attached, no error anywhere. "It just closes." Stash the
    // intent so the reload can resume it (cswPhotoPickResume), and
    // count the loss so the learning loop can see how often this
    // actually happens instead of guessing.
    try { sessionStorage.setItem('csw_photo_pending', JSON.stringify({
      cid,
      cap: (ov.querySelector('#csw-photo-cap').value || '').trim(),
      t: Date.now() })); } catch (_) {}
    try { inp.value = ''; } catch (_) {} inp.click(); };
  const pickSurvived = () => {
    try { sessionStorage.removeItem('csw_photo_pending'); } catch (_) {} };
  ov.querySelector('.csw-x').addEventListener('click', pickSurvived);
  ov.querySelector('#csw-pick-lib').addEventListener('click', () => pick(libInput));
  ov.querySelector('#csw-pick-cam').addEventListener('click', () => pick(camInput));
  const vidInput = ov.querySelector('#csw-file-vid');
  ov.querySelector('#csw-pick-vid').addEventListener('click', () => pick(vidInput));
  vidInput.addEventListener('change', () => {
    pickSurvived();
    const f = vidInput.files && vidInput.files[0];
    if (!f) return;
    if (f.size > 16 * 1024 * 1024) { cswToast("That video is over WhatsApp's 16MB limit — trim it or record a shorter clip.", true); return; }
    const cap = (ov.querySelector('#csw-photo-cap').value || '').trim();
    ov.remove();
    cswStageMedia(cid, f, 'video', cap);
  });
  const onPick = (fileInput) => {
    pickSurvived();               // change fired — the app lived
    const f = fileInput.files && fileInput.files[0];
    if (!f) return;
    const cap = (ov.querySelector('#csw-photo-cap').value || '').trim();
    ov.remove();                          // WhatsApp-grade: sheet closes NOW —
    cswStageMedia(cid, f, 'photo', cap);   // explicit Send follows review.
  };
  libInput.addEventListener('change', () => onPick(libInput));
  camInput.addEventListener('change', () => onPick(camInput));
  ov.querySelector('#csw-photo-sendurl').addEventListener('click', () => {
    const url = safeTrackingHref(ov.querySelector('#csw-photo-url').value.trim());
    if (!url) return cswToast('Use a complete http or https image link.', true);
    const cap = (ov.querySelector('#csw-photo-cap').value || '').trim();
    ov.remove(); cswStageMedia(cid, null, 'photo', cap, url);
  });
}

// Canned replies — ONE source for the macro strip AND the Quick sheet
// (DB rows override the built-in defaults; see /api/cs/canned-replies).
//
// SAVED REPLIES ARE KEPT (CS vet 29, 2026-09-24). Every chat open (the
// strip) and every key typed after "/" read the whole pack again. Those
// two glance surfaces now pass {cached:true} and share one read for five
// minutes. Every other caller (the Quick sheet, the account key) still
// reads fresh, and a fresh read refreshes the kept copy. A failed read is
// never kept, and this desk's own add / edit / remove drops the kept copy
// at once (cswCannedRepliesForget).
// WORDS GO IN FRESH (#3541 adversary pass, 2026-09-24): the kept copy
// only DRAWS the strip. The words a strip tap puts in the composer are read
// again at the tap (cswCannedNow), and the "/" list reads fresh as it
// opens: an owner can change the bank account on another desk, and a kept
// copy would hand a paying customer the old one. A read already on its way
// is shared by every caller, so a burst of keys is still one read.
const CSW_CANNED_KEEP_MS = 5 * 60 * 1000;
let _cswCannedKept = null;   // { at, done, p } — p resolves to { replies, good }
function cswCannedRepliesForget() { _cswCannedKept = null; }
async function cswCannedReplies(opts) {
  const o = opts || {};
  let entry = _cswCannedKept;
  const share = entry && (!entry.done || (o.cached && Date.now() - entry.at < CSW_CANNED_KEEP_MS));
  if (!share) {
    const mine = entry = { at: Date.now(), done: false, p: null };
    mine.p = (async () => {
      let replies = [], good = false;
      try {
        const d = await api('/api/cs/canned-replies');
        replies = (d && d.replies) || [];
        good = !!d && d.ok !== false && Array.isArray(d.replies);
      } catch (_) {}
      mine.done = true;
      if (!good && _cswCannedKept === mine) _cswCannedKept = null;
      if (!replies.length) replies = [
        { label: 'Welcome', body: 'Hi! Thanks for reaching out — how can I help?' },
        { label: 'Available', body: 'That piece is available. Which size are you?' },
        { label: 'Payment', body: "We've got your order — sending the payment link now." },
        { label: 'Confirmed', body: 'Thank you! Your order is confirmed and being prepared.' },
      ];
      return { replies, good };
    })();
    _cswCannedKept = mine;
  }
  const got = await entry.p;
  if (o.strict && !got.good) return null;   // the caller must know the read failed
  return [...got.replies];
}

// The reply as it stands on the server NOW (see "words go in fresh" above):
// matched by id, else by label; null when it has gone (removed or renamed
// on another desk); false when the read itself failed.
async function cswCannedNow(r) {
  const fresh = await cswCannedReplies({ strict: true });
  if (!fresh) return false;
  return fresh.find(x => r.id != null && String(x.id) === String(r.id))
    || fresh.find(x => String(x.label || '') === String(r.label || '')) || null;
}

// Macro strip (design pass 02): the research macro pack — the measured
// closers — surfaces ONE TAP above the composer instead of behind the +
// tray's Quick sheet. Tap inserts the reply into the composer; ✦ Suggest
// stays violet and keeps its AI draft behavior.
async function cswLoadMacros() {
  const host = document.getElementById('csw-macros');
  if (!host) return;
  const replies = await cswCannedReplies({ cached: true });   // kept pack: one read, not one per open
  if (!document.getElementById('csw-macros')) return;  // pane re-rendered
  // ONE ✦ Suggest (seat-B vet A5, 2026-09-26): the + tray already carries
  // the key, and it is the one that says "drafting…" while EON writes. The
  // strip drew a second copy right above it. It adds its own only when no
  // tray key is on the page.
  const traySuggest = document.querySelector('#csw-tray .csw-tool[data-tool="suggest"]');
  host.innerHTML = replies.slice(0, 6).map((r, i) =>
    `<button class="csw-macro" data-macro="${i}">${esc(r.label || '')}</button>`).join('')
    + (traySuggest ? '' : '<button class="csw-macro csw-macro--ai" data-macro="suggest">✦ Suggest</button>');
  // Operator 2026-08-09: the strip lives behind the + key with the tray —
  // it never stands open on its own. Content loads here; the + toggle
  // owns visibility (and the bottom re-pin).
  host.hidden = document.getElementById('csw-tray')?.classList.contains('collapsed') ?? true;
  // If the rep opened the + before this fetch returned, the strip lands
  // late and shrinks the transcript with nobody re-pinning it (the M5
  // guarantee used to live here, then moved to the + tap). Cover the
  // race at the only other moment the strip can appear.
  if (!host.hidden) {
    const sc = document.getElementById('csw-script');
    if (sc && sc.scrollHeight - sc.scrollTop - sc.clientHeight < 80) sc.scrollTop = sc.scrollHeight;
  }
  host.querySelectorAll('.csw-macro').forEach(b => b.addEventListener('click', async (ev) => {
    if (b.dataset.macro === 'suggest') return cswSuggestReply();
    const r = replies[Number(b.dataset.macro)];
    const inp = document.getElementById('csw-input');
    if (!r || !inp || inp.disabled || !String(r.body || '').trim()) return;
    // The strip was drawn from the kept pack; the words are read fresh at
    // the tap (#3541 adversary pass). If the rep typed, switched chat or the
    // window closed while that read ran, their composer is left alone.
    // The composer takes focus inside the tap itself, before that read: a
    // phone only raises its keyboard for a focus made in the tap, never for
    // one made after a wait (the old tap focused at once).
    inp.focus();
    const cid = cswState().active, was = inp.value;
    const now = await cswCannedNow(r);
    if (cswState().active !== cid || inp.value !== was || inp.disabled) return;
    if (now === false) { cswToast('Could not load that reply. Try again.', true); return; }
    if (!now) { cswToast('That reply was just changed. Tap it again.', true); cswLoadMacros(); return; }
    if (!String(now.body || '').trim()) return;
    // Vet 2026-09-24 (fault 1): a tap here replaced the rep's typed words.
    // The adopt step asks before it replaces anything.
    cswAdoptDraft(cid, now.body, () => {}, ev, { claim: false });
  }));
}

// Quick replies sheet — tap to insert, plus rep-editable (operator
// 2026-07-23: "a quick reply section … that can be customized by cs rep").
// The pack is TEAM-SHARED: ✎ edits and ＋ adds land for every rep. Rows
// with a real DB id (uuid) are editable; the built-in defaults are not —
// the first ＋ materializes them server-side, then everything is.
async function cswTemplateSheet() {
  const replies = await cswCannedReplies();
  const editable = (c) => c.id && String(c.id).indexOf('-') > 0;
  // The Library view (17 Oct 2026) sits beside the team's replies in this
  // same sheet: one small switch, the team's replies first and unchanged.
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Quick replies</div><button class="csw-x">✕</button></div>
    <div class="csw-seven csw-lib__switch"><div class="csw-seven__tabs" role="group" aria-label="Which replies"><button type="button" data-lib-view="saved" aria-pressed="true">Team replies</button><button type="button" data-lib-view="library" aria-pressed="false">Library</button></div></div>
    <div data-lib-pane="saved">
    ${replies.map((c, i) => `<div style="display:flex;gap:6px;align-items:stretch;margin-bottom:8px">
      <button class="csw-need" data-i="${i}" style="flex:1;margin-bottom:0">
        <span class="csw-need__l"><b style="display:block;font-size:10.5px;color:var(--ink-mute);text-transform:uppercase;letter-spacing:.05em;margin-bottom:3px">${esc(c.label || '')}</b>${esc(c.body)}</span>
        <span class="csw-need__arrow">›</span></button>
      ${editable(c) ? `<button class="csw-qr-edit" data-e="${i}" title="Edit" style="border:1px solid var(--line);background:none;color:var(--ink-dim);border-radius:10px;padding:0 10px;cursor:pointer">✎</button>` : ''}
    </div>`).join('')}
    <button class="csw-confirm" id="csw-qr-new" style="margin-top:4px">＋ New quick reply</button>
    </div>
    <div class="csw-seven csw-lib" data-lib-pane="library" hidden></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  // Vet 2026-09-24 (fault 1): the sheet belongs to the chat it opened on,
  // and a pick goes through the adopt step instead of replacing the words.
  const cid = cswState().active;
  ov.querySelectorAll('[data-i]').forEach(b => b.addEventListener('click', (ev) => {
    const body = replies[+b.dataset.i]?.body;
    ov.remove();
    if (document.getElementById('csw-input') && String(body || '').trim()) cswAdoptDraft(cid, body, () => {}, ev, { claim: false });
  }));
  const openEditor = (c) => { ov.remove(); cswQuickReplyEditor(c); };
  ov.querySelectorAll('[data-e]').forEach(b => b.addEventListener('click', (e) => {
    e.stopPropagation(); openEditor(replies[+b.dataset.e]);
  }));
  ov.querySelector('#csw-qr-new').addEventListener('click', () => openEditor(null));
  cswLibraryBind(ov, cid);
}

// THE LIBRARY VIEW (17 Oct 2026). CRUX's reviewed messages — the eighteen
// families of the communication library — open inside the Quick replies
// sheet, beside the team's own replies. A small reply workbench: choose a
// message, give it this customer's facts, preview the exact wording, then
// put it in the message box on a deliberate tap. Nothing here sends, claims
// the chat, submits or activates a template, or looks a customer up. The
// examples the library carries are made-up facts: they are shown as
// examples and never typed into a field.
//
// The catalogue is read when the rep opens the view, never per key. Every
// answer is held to the chat, sign-in, channel, message box and draft
// revision it was asked for (cswDraftCapture / Current / Revision): a reply
// that returns after any of them changed is dropped, and the words go in
// only through cswAdoptDraft, which asks before it replaces anything.
const CSW_LIB_URL = '/api/whatsapp/studio/communication-library';
const CSW_LIB_PREVIEW = '/api/whatsapp/studio/communication-preview';
const CSW_LIB_FAMILIES = 18;
// The receipt's two shapes are fixed (Communication, 48f0c82): the named
// receipt is the default; the no-name one is chosen by hand and never holds
// a name. A catalogue that says otherwise is not read at all.
const CSW_LIB_RECEIPT = { named: 'name,store,order_reference,amount_ngn', no_name: 'store,order_reference,amount_ngn' };
const CSW_LIB_FIELD = {
  name: 'Customer first name', order_reference: 'Order number', item_phrase: 'What they ordered',
  amount_ngn: 'Amount (₦)', next_update: 'What happens next', carrier: 'Delivery company',
  verified_eta: 'Arrival time (checked)', situation: 'What happened', next_update_at: 'Next update by',
  store: 'Store', collection_hours: 'Collection hours', payment_state: 'Payment',
  purchased_piece: 'Piece they bought', cart_piece: 'Piece in their bag', requested_piece: 'Piece they asked for',
  requested_size: 'Size they asked for', collection: 'Collection', request_subject: 'What they asked about',
  verified_answer: 'Answer (checked)', opt_in_purpose: 'What they signed up for',
};
const CSW_LIB_APPROVAL = { approved: 'approved', pending: 'waiting for approval', rejected: 'rejected',
  paused: 'paused', disabled: 'disabled', not_registered: 'not registered', unknown: 'not checked' };

function cswLibWords(key) {
  const s = String(key || '').replace(/_/g, ' ').trim();
  return s ? s[0].toUpperCase() + s.slice(1) : '';
}
function cswLibLabel(field) { return CSW_LIB_FIELD[field.name] || cswLibWords(field.name); }

// One family or nested variant, as the desk will use it; null when any part
// of it is off. Positions run 1..n in order, every body token has exactly
// one field, a name appears once, and nothing in it claims to be sendable.
function cswLibShape(raw, version) {
  const str = x => typeof x === 'string';
  const list = (x, ok) => Array.isArray(x) && x.every(ok);
  if (!raw || typeof raw !== 'object' || Array.isArray(raw) || raw.version !== version
      || !str(raw.title) || !raw.title.trim() || !str(raw.body) || !raw.body.trim() || !str(raw.footer)
      || !['template', 'conversation_reply'].includes(raw.kind) || !list(raw.actions, str)
      || !list(raw.states, str) || new Set(raw.states).size !== raw.states.length
      || !raw.availability || raw.availability.sendable !== false
      || !Array.isArray(raw.variables) || !raw.variables.length) return null;
  const names = new Set(), fields = [];
  for (const [i, v] of raw.variables.entries()) {
    if (!v || typeof v !== 'object' || v.position !== String(i + 1) || !str(v.name) || !/^[a-z_]{1,40}$/.test(v.name)
        || names.has(v.name) || !['text', 'enum', 'amount_ngn', 'state_bound'].includes(v.type)
        || !list(v.choices, str) || new Set(v.choices).size !== v.choices.length
        || !Number.isInteger(v.max_length) || v.max_length < 1 || v.max_length > 200) return null;
    if (v.type === 'enum' ? !v.choices.length
        : v.type === 'state_bound' ? v.choices.join('\n') !== raw.states.join('\n')
        : v.choices.length) return null;
    names.add(v.name);
    fields.push({ position: v.position, name: v.name, type: v.type, choices: [...v.choices], max: v.max_length });
  }
  if (fields.some(f => f.type === 'state_bound') !== raw.states.length > 0) return null;
  const tokens = (raw.body.match(/\{\{\d+\}\}/g) || []).map(t => t.slice(2, -2));
  if (new Set(tokens).size !== fields.length || fields.some(f => !tokens.includes(f.position))) return null;
  const sample = raw.sample && raw.sample.sample === true && str(raw.sample.rendered_text) ? raw.sample.rendered_text : '';
  return { title: raw.title, when: str(raw.trigger) ? raw.trigger : '', kind: raw.kind, footer: raw.footer,
    actions: [...raw.actions], states: [...raw.states], fields, example: sample,
    approval: raw.approval && str(raw.approval.state) ? raw.approval.state : 'unknown' };
}

// The whole catalogue, or null. Exactly eighteen families; a nested variant
// is never a nineteenth. The version is the one this answer carries — the
// desk never assumes it.
function cswLibCatalogue(raw) {
  if (!raw || typeof raw !== 'object' || typeof raw.version !== 'string' || !raw.version.trim()
      || raw.sendable !== false || !Array.isArray(raw.families)) return null;
  if (!raw.families.length) return { version: raw.version, families: [] };
  if (raw.families.length !== CSW_LIB_FAMILIES) return null;
  const seen = new Set(), families = [];
  for (const f of raw.families) {
    const id = f && f.family_id;
    if (typeof id !== 'string' || !/^[a-z_]{1,40}$/.test(id) || seen.has(id)) return null;
    seen.add(id);
    const named = cswLibShape(f, raw.version);
    if (!named || (f.variants !== undefined && !Array.isArray(f.variants))) return null;
    const variants = [], vseen = new Set();
    for (const v of f.variants || []) {
      const vid = v && v.variant_id, shape = cswLibShape(v, raw.version);
      if (typeof vid !== 'string' || !/^[a-z_]{1,40}$/.test(vid) || vseen.has(vid) || !shape) return null;
      vseen.add(vid);
      variants.push({ ...shape, id: vid });
    }
    if (id === 'store_receipt' && (named.fields.map(x => x.name).join() !== CSW_LIB_RECEIPT.named
        || variants.some(v => v.id !== 'no_name' || v.fields.map(x => x.name).join() !== CSW_LIB_RECEIPT.no_name))) return null;
    families.push({ ...named, id, variants });
  }
  return { version: raw.version, families };
}

// What a preview asks for: this GET's version, the facts for the chosen
// shape only, the stage only where the message has one, and variant_id only
// when a variant was chosen by hand — omitted, never null, for the default.
// Stage-bound words come from the stage and are never sent as facts.
function cswLibPayload(version, fam, variant, state, values) {
  const shape = variant || fam, out = { family_id: fam.id, version, values: {} };
  for (const f of shape.fields) if (f.type !== 'state_bound') out.values[f.position] = values[f.position];
  if (shape.states.length) out.state = state;
  if (variant) out.variant_id = variant.id;
  return out;
}

// A preview is used only when it is the one asked for and still says what
// it is: a draft, facts unchecked, not sendable.
function cswLibPreviewOk(res, req) {
  return !!res && typeof res === 'object' && res.family_id === req.family_id && res.version === req.version
    && typeof res.rendered_text === 'string' && !!res.rendered_text.trim() && !/\{\{\d+\}\}/.test(res.rendered_text)
    && typeof res.footer === 'string' && Array.isArray(res.actions) && res.actions.every(a => typeof a === 'string')
    && res.facts_verified === false && res.sendable === false && res.sample === false
    && (req.variant_id === undefined ? !('variant_id' in res) : res.variant_id === req.variant_id)
    && (res.state ?? null) === (req.state ?? null);
}

// The refusal the rep reads. Each one says nothing was added; none retries.
function cswLibRefusal(e, fields) {
  const status = e && e.status;
  if (status === 403) return 'Your role cannot use the message library. Nothing was added.';
  if (status === 404) return 'The message library is not available for this shop. Nothing was added.';
  if (status === 408) return 'No answer from the server. Nothing was added.';
  if (status === 422) {
    const code = String((e && e.message) || ''), m = code.match(/^invalid_(variable|amount|state_choice):(\d+)$/);
    const f = m && (fields || []).find(x => x.position === m[2]);
    if (f) return `${cswLibLabel(f)}: ${m[1] === 'amount' ? 'numbers only, like 48,500' : 'check this fact'}. Nothing was added.`;
    if (code === 'refund_state_required') return 'Choose the stage first. Nothing was added.';
    return 'The library did not accept these facts. Nothing was added. Check them and preview again.';
  }
  if (!status) return 'You look offline. Nothing was added. Preview again when you are back online.';
  return 'The message library did not answer. Nothing was added.';
}

// The conversation a library answer belongs to: chat and its visit (the
// desk's visit generation, so A to B and back to A is a new visit), verified
// sign-in and channel (cswDraftCapture), the connected message box, its
// words and its draft revision. `draft` also requires the words and revision
// unchanged — equal words after an edit and a revert are a new revision.
function cswLibCapture(cid) {
  const handle = cswDraftCapture(cid), input = document.getElementById('csw-input'), s = cswState();
  if (!handle || !input || !input.isConnected || s.active !== cid) return null;
  return { cid, visit: s._threadMetaGeneration || 0, handle, input, text: input.value, rev: cswDraftRevision(cid) };
}
function cswLibCurrent(c, draft) {
  const s = cswState();
  if (!c || s.active !== c.cid || (s._threadMetaGeneration || 0) !== c.visit || !cswDraftCurrent(c.handle)) return false;
  if (cswDraftCapture(c.cid)?.slot !== c.handle.slot) return false;
  const input = document.getElementById('csw-input');
  if (input !== c.input || !input.isConnected) return false;
  return !draft || (input.value === c.text && cswDraftRevision(c.cid) === c.rev);
}

// Free text goes in only where the rep could type it and send it. A closed
// WhatsApp window keeps its own approved-template door.
function cswLibInsertBlock(cid) {
  const inp = document.getElementById('csw-input');
  if (!inp || inp.disabled) return 'The message box is not open for this conversation.';
  if (cswWindowBlocks(cswThreadFor(cid))) return 'The WhatsApp reply window is closed, so typed text cannot send. Use Send template instead.';
  return '';
}

function cswLibraryBind(ov, cid) {
  const keys = Array.from(ov.querySelectorAll('[data-lib-view]'));
  const pane = ov.querySelector('[data-lib-pane="library"]'), saved = ov.querySelector('[data-lib-pane="saved"]');
  if (!keys.length || !pane || !saved) return;
  const lib = { seq: 0, closed: false, cat: null, loading: null, fam: null, variant: null, state: null, values: {}, preview: null, step: '' };
  const anchor = cswLibCapture(cid);
  const base = () => !lib.closed && cswLibCurrent(anchor, false);
  // Closing the sheet, by any path, drops every answer still on its way.
  const remove = ov.remove.bind(ov);
  ov.remove = () => { lib.closed = true; lib.seq++; remove(); };
  const status = (msg, err) => {
    const s = pane.querySelector('[data-lib-status]');
    if (s) { s.textContent = msg || ''; s.classList.toggle('is-err', !!err); }
  };
  const statusEl = '<p class="csw-seven__muted" role="status" aria-live="polite" data-lib-status></p>';
  const show = (html, focus) => {
    pane.innerHTML = `<div class="csw-lib__step">${html}</div>${statusEl}`;
    const sheet = ov.querySelector('.csw-sheet');
    if (sheet) sheet.scrollTop = 0;
    // Focus moves to a heading or a key, never to a text field: opening a
    // step must not raise a phone's keyboard.
    const el = focus && pane.querySelector(focus);
    if (el) el.focus({ preventScroll: true });
  };
  const invalidate = () => { lib.seq++; lib.preview = null; };
  const shapeOf = () => lib.variant || lib.fam;
  const sig = s => JSON.stringify([s.fields, s.states]);

  const load = () => lib.loading || (lib.loading = (async () => {
    let raw, err = null;
    try { raw = await api(CSW_LIB_URL, { cache: 'no-store', _isCurrent: base }); } catch (e) { err = e; }
    lib.loading = null;
    if (lib.closed) return false;
    if (!base()) { show('<p class="csw-seven__empty">This conversation changed. Close and reopen the library.</p>'); return false; }
    const cat = err ? null : cswLibCatalogue(raw);
    lib.cat = cat;
    if (!cat) {
      show(`<p class="csw-seven__empty">${esc(err ? cswLibRefusal(err, []) : 'The library answered in a form this desk cannot read. Nothing was added.')}</p>
        <div class="csw-seven__actions"><button type="button" data-lib-retry>Try again</button></div>`);
      pane.querySelector('[data-lib-retry]')?.addEventListener('click', () => open());
      return false;
    }
    return true;
  })());

  const open = async () => {
    if (!anchor) { show('<p class="csw-seven__empty">Open a customer conversation to use the library.</p>'); return; }
    if (!lib.cat) {
      show('<p class="csw-seven__empty" aria-busy="true">Loading the library…</p>');
      if (!await load()) return;
    }
    if (lib.step === 'facts' || lib.step === 'preview') paintFacts();
    else paintChoose();
  };

  const paintChoose = (back) => {
    lib.step = 'choose'; invalidate();
    const fams = lib.cat.families;
    if (!fams.length) { show('<p class="csw-seven__empty">The library has no messages yet.</p>'); return; }
    show(`<p class="csw-seven__muted">CRUX’s reviewed wording. Choose one, add this customer’s facts, then preview it.</p>
      ${fams.map((f, i) => `<button type="button" class="csw-seven__choice" data-lib-fam="${i}"><span>${esc(f.title)}<small>${esc(f.when || (f.kind === 'template' ? 'WhatsApp template wording' : 'Conversation reply'))}</small></span><span aria-hidden="true">›</span></button>`).join('')}`,
      back != null ? `[data-lib-fam="${fams.indexOf(back)}"]` : null);
    pane.querySelectorAll('[data-lib-fam]').forEach(b => b.addEventListener('click', () => {
      lib.fam = fams[+b.dataset.libFam]; lib.variant = null; lib.state = null; lib.values = {};
      paintFacts('[data-lib-head]');
    }));
  };

  const paintFacts = (focus) => {
    lib.step = 'facts'; invalidate();
    const f = lib.fam, shape = shapeOf();
    const pick = (attr, name, items, on) => `<div class="csw-seven__tabs" role="group" aria-label="${name}">${items.map(([v, label]) =>
      `<button type="button" ${attr}="${esc(v)}" aria-pressed="${String(v === on)}">${esc(label)}</button>`).join('')}</div>`;
    const versions = f.variants.length
      ? `<p class="csw-lib__label">Which version</p>${pick('data-lib-variant', 'Which version', [['', 'With first name'], ...f.variants.map(v => [v.id, v.id === 'no_name' ? 'No name on file' : v.title])], lib.variant ? lib.variant.id : '')}` : '';
    const stages = shape.states.length
      ? `<p class="csw-lib__label">Stage</p>${pick('data-lib-state', 'Stage', shape.states.map(s => [s, cswLibWords(s)]), lib.state)}` : '';
    const fields = shape.fields.filter(x => x.type !== 'state_bound').map(x => {
      const v = lib.values[x.position] ?? '';
      return x.type === 'enum'
        ? `<label>${esc(cswLibLabel(x))}<select class="csw-search" data-lib-field="${x.position}"><option value="">Choose…</option>${x.choices.map(c =>
            `<option value="${esc(c)}"${c === v ? ' selected' : ''}>${esc(cswLibWords(c))}</option>`).join('')}</select></label>`
        : `<label>${esc(cswLibLabel(x))}<input class="csw-search" data-lib-field="${x.position}" maxlength="${x.max}" autocomplete="off" spellcheck="false"${x.type === 'amount_ngn' ? ' inputmode="decimal"' : ''} value="${esc(v)}"></label>`;
    }).join('');
    show(`<button type="button" data-lib-back>‹ All messages</button>
      <h4 tabindex="-1" data-lib-head>${esc(shape.title)}</h4>
      ${shape.when ? `<p class="csw-seven__muted">${esc(shape.when)}</p>` : ''}
      ${versions}${stages}${fields}
      ${shape.example ? `<details class="csw-seven__evidence"><summary>Example wording</summary><div><p>An example with made-up facts. It is not this customer.</p><div class="csw-lib__card csw-lib__card--example">${esc(shape.example)}</div></div></details>` : ''}
      <div class="csw-seven__actions"><button type="button" class="csw-seven__primary" data-lib-preview>Preview</button></div>`, focus);
    pane.querySelector('[data-lib-back]').addEventListener('click', () => paintChoose(lib.fam));
    // A different version is a different message: its facts start empty, so
    // four-field words never land in the three-field receipt.
    pane.querySelectorAll('[data-lib-variant]').forEach(b => b.addEventListener('click', () => {
      const next = f.variants.find(v => v.id === b.dataset.libVariant) || null;
      if (next === lib.variant) return;
      lib.variant = next; lib.state = null; lib.values = {};
      paintFacts(`[data-lib-variant="${esc(b.dataset.libVariant)}"]`);
    }));
    pane.querySelectorAll('[data-lib-state]').forEach(b => b.addEventListener('click', () => {
      lib.state = b.dataset.libState; invalidate(); status('');
      pane.querySelectorAll('[data-lib-state]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    }));
    pane.querySelectorAll('[data-lib-field]').forEach(el => {
      const keep = () => { lib.values[el.dataset.libField] = el.value; invalidate(); status(''); };
      el.addEventListener('input', keep); el.addEventListener('change', keep);
    });
    pane.querySelector('[data-lib-preview]').addEventListener('click', e => preview(e.currentTarget));
  };

  const preview = async (btn) => {
    const shape = shapeOf(), facts = shape.fields.filter(x => x.type !== 'state_bound');
    if (shape.states.length && !shape.states.includes(lib.state)) return status('Choose the stage first.', true);
    const missing = facts.filter(x => !String(lib.values[x.position] ?? '').trim());
    if (missing.length) return status(`Add ${missing.map(cswLibLabel).join(', ')}.`, true);
    const values = {};
    for (const x of facts) values[x.position] = String(lib.values[x.position]).trim();
    const ctx = cswLibCapture(cid);
    if (!ctx || !base() || ctx.handle.slot !== anchor.handle.slot) return status('This conversation changed. Close and reopen the library.', true);
    const req = cswLibPayload(lib.cat.version, lib.fam, lib.variant, lib.state, values);
    const mine = ++lib.seq;
    btn.disabled = true; status('Preparing the preview…');
    let res, err = null;
    try {
      res = await api(CSW_LIB_PREVIEW, { method: 'POST', body: JSON.stringify(req), cache: 'no-store',
        _isCurrent: () => !lib.closed && mine === lib.seq && cswLibCurrent(ctx, true) });
    } catch (e) { err = e; }
    if (btn.isConnected) btn.disabled = false;
    // A newer choice, an edit or a closed sheet owns the view now.
    if (lib.closed || mine !== lib.seq) return;
    if (!cswLibCurrent(ctx, true)) return status('Your reply or this conversation changed while the preview loaded. Preview again.', true);
    if (err && err.status === 409) {
      // The library moved on: read it once more, then the rep previews again
      // by hand. Facts are kept only when the message's fields are unchanged.
      const was = { fam: lib.fam.id, variant: lib.variant && lib.variant.id, sig: sig(shape), state: lib.state };
      lib.cat = null; invalidate(); status('The library was updated. Reading it again…');
      const after = lib.seq;
      if (!await load() || lib.seq !== after) return;
      const fam = lib.cat.families.find(x => x.id === was.fam);
      const variant = fam && was.variant ? fam.variants.find(v => v.id === was.variant) : null;
      if (!fam || (was.variant && !variant) || sig(variant || fam) !== was.sig) {
        paintChoose(); return status('The library was updated and this message changed. Choose it again.', true);
      }
      lib.fam = fam; lib.variant = variant; lib.state = was.state;
      paintFacts('[data-lib-head]'); return status('The library was updated. Check the facts and preview again.', true);
    }
    if (err) return status(cswLibRefusal(err, shape.fields), true);
    if (!cswLibPreviewOk(res, req)) return status('The preview came back in a form this desk cannot read. Nothing was added.', true);
    lib.preview = { res, ctx };
    paintPreview(shape);
  };

  const paintPreview = (shape) => {
    lib.step = 'preview';
    const { res } = lib.preview, blocked = cswLibInsertBlock(cid), approval = CSW_LIB_APPROVAL[shape.approval];
    show(`<button type="button" data-lib-edit>‹ Edit facts</button>
      <h4 tabindex="-1" data-lib-head>${esc(shape.title)}</h4>
      <div class="csw-lib__card" data-lib-text>${esc(res.rendered_text)}</div>
      ${res.footer ? `<p class="csw-lib__foot">${esc(res.footer)}</p>` : ''}
      ${res.actions.length ? `<p class="csw-seven__muted">Buttons on the WhatsApp template: ${res.actions.map(a => `<span class="csw-lib__btn">${esc(a)}</span>`).join(' ')}. Putting this text in your message box does not add them, or any link.</p>` : ''}
      <ul class="csw-lib__truth"><li>EON has not checked these facts. Check them against the order before you send.</li>
        <li>Not sendable from here. It goes in your message box as text, and you send it yourself.</li>
        ${approval && shape.kind === 'template' ? `<li>Template on WhatsApp: ${esc(approval)}. That does not change what this sheet can do.</li>` : ''}</ul>
      ${blocked ? `<p class="csw-seven__muted">${esc(blocked)}</p>` : ''}
      <div class="csw-seven__actions"><button type="button" class="csw-seven__primary" data-lib-use${blocked ? ' disabled' : ''}>Put in message box</button></div>`, '[data-lib-head]');
    pane.querySelector('[data-lib-edit]').addEventListener('click', () => paintFacts('[data-lib-head]'));
    pane.querySelector('[data-lib-use]').addEventListener('click', ev => {
      const p = lib.preview;
      if (!p || lib.closed) return;
      if (!cswLibCurrent(p.ctx, true)) { paintFacts('[data-lib-head]'); return status('Your reply or this conversation changed. Preview again before you use it.', true); }
      const why = cswLibInsertBlock(cid);
      if (why) return status(why, true);
      const text = p.res.rendered_text, ctx = p.ctx;
      ov.remove();
      // The existing adopt step: use, add below or replace, asked first;
      // a local insert that never claims the chat, checked again at the change.
      cswAdoptDraft(cid, text, () => {}, ev, { claim: false, isCurrent: () => cswLibCurrent(ctx, true) });
    });
  };

  keys.forEach(k => k.addEventListener('click', () => {
    if (k.getAttribute('aria-pressed') === 'true') return;
    const library = k.dataset.libView === 'library';
    keys.forEach(x => x.setAttribute('aria-pressed', String(x === k)));
    saved.hidden = library; pane.hidden = !library;
    if (library) open();
    else { invalidate(); if (lib.step === 'preview') lib.step = 'facts'; }
  }));
}

// Editor for one quick reply — create (c=null), edit, or remove.
function cswQuickReplyEditor(c) {
  const isNew = !c;
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">${isNew ? 'New quick reply' : 'Edit quick reply'}</div><button class="csw-x">✕</button></div>
    <div style="font-size:11px;color:var(--ink-mute);margin-bottom:10px">Shared with the whole CS team.</div>
    <input class="csw-search" id="csw-qr-label" placeholder="Label (shows on the strip)" maxlength="60" value="${esc((c && c.label) || '')}" style="margin-bottom:8px"/>
    <textarea class="csw-input" id="csw-qr-body" placeholder="The message this inserts…" style="width:100%;min-height:110px;margin-bottom:10px">${esc((c && c.body) || '')}</textarea>
    <button class="csw-confirm" id="csw-qr-save">${isNew ? 'Add reply' : 'Save changes'}</button>
    ${isNew ? '' : '<button id="csw-qr-del" style="width:100%;margin-top:8px;border:1px solid var(--line);background:none;color:var(--danger);border-radius:10px;padding:10px;cursor:pointer">Remove this reply</button>'}`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const done = (msg) => { cswCannedRepliesForget(); ov.remove(); cswToast(msg); cswLoadMacros(); cswTemplateSheet(); };
  ov.querySelector('#csw-qr-save').addEventListener('click', async () => {
    const label = ov.querySelector('#csw-qr-label').value.trim();
    const body = ov.querySelector('#csw-qr-body').value.trim();
    if (!label || !body) { cswToast('Label and message are both needed', true); return; }
    try {
      const r = isNew
        ? await api('/api/cs/canned-replies', { method: 'POST', body: JSON.stringify({ label, body }) })
        : await api(`/api/cs/canned-replies/${encodeURIComponent(c.id)}`, { method: 'PATCH', body: JSON.stringify({ label, body }) });
      if (!r || !r.ok) throw new Error((r && r.error) || 'save failed');
      done(isNew ? 'Added' : 'Saved');
    } catch (e) { cswToast(cswSayWhy(e, { lead: 'Quick reply not saved.' }), true); }
  });
  const del = ov.querySelector('#csw-qr-del');
  if (del) del.addEventListener('click', async () => {
    try {
      const r = await api(`/api/cs/canned-replies/${encodeURIComponent(c.id)}`, { method: 'DELETE' });
      if (!r || !r.ok) throw new Error((r && r.error) || 'delete failed');
      done('Removed');
    } catch (e) { cswToast(cswSayWhy(e, { lead: 'Quick reply not removed.' }), true); }
  });
}

// ── FAQ auto-answers (mig 568) — rep oversight + editor ──────────────
// EON answers the eight repeatable question classes instantly and tags
// the thread 'auto-answered'. The banner is the rep's oversight surface:
// review what was sent, take the thread back with one tap (Override).
async function cswLoadFaqBanner(cid) {
  const host = document.getElementById('csw-faqbanner');
  if (!host || !cid || String(cid).indexOf('team:') === 0) return;
  host.hidden = true; host.innerHTML = '';
  let labels = [];
  try { const d = await cswThreadLabels(cid); labels = (d && d.labels) || []; } catch (_) { return; }
  if (cswState().active !== cid) return;   // rep switched threads mid-fetch
  if (!labels.includes('auto-answered')) return;
  // Operator, 2026-08-07 (live phone test): "the green area below the
  // header should only appear when there is a notification — it takes
  // space." The label persists on the thread forever, so the banner
  // squatted on every thread EON had EVER auto-answered. The banner is
  // oversight for a LIVE auto-answer: the moment the customer speaks
  // again the rep is already working the thread normally and the row's
  // ⚡ chip carries the history — a banner that never leaves is chrome.
  // Also dismissible (✕) for the remaining case, per-desk-session.
  const t = cswState().threads.find(x => x.zoko_customer_id === cid);
  if (t && (t._waiting || t.unreplied)) return;   // customer spoke last
  // Opus vet M-6: the label persists forever, so a handled thread
  // (customer replied, rep replied, done) wore the band again the next
  // week. The banner is oversight for TODAY's auto-answer — after 24h
  // the row chip alone carries the history.
  if (t && t.last_message_at
      && Date.now() - new Date(t.last_message_at).getTime() > 86400000) return;
  cswLoadFaqBanner._gone = cswLoadFaqBanner._gone || new Set();
  if (cswLoadFaqBanner._gone.has(cid)) return;
  // THE STAGE (2026-08-18): something that already HAPPENED reads as a
  // system line at the tail of the transcript — under EON's own bubble,
  // which is the last message whenever this shows — not a banner pinned
  // over the top of the conversation for the session.
  host.innerHTML = `<span class="csw-faqbanner__t"><i class="csw-faqbanner__dot" aria-hidden="true"></i>EON answered this one from the FAQ.</span>
    <button class="csw-faqbanner__btn" id="csw-faq-override">Override — I'll take it</button>
    <button class="csw-faqbanner__x" id="csw-faq-dismiss" aria-label="Dismiss">✕</button>`;
  host.hidden = false;
  host.querySelector('#csw-faq-dismiss')?.addEventListener('click', () => {
    cswLoadFaqBanner._gone.add(cid);
    host.hidden = true;
  });
  host.querySelector('#csw-faq-override')?.addEventListener('click', async () => {
    try {
      const r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/faq-override`, { method: 'POST', body: JSON.stringify({}) });
      if (!r || !r.ok) throw new Error((r && r.error) || 'override failed');
      host.hidden = true;
      _cswLabelsRead.delete(String(cid));   // its labels just changed: the next read asks again
      const t = cswState().threads.find(x => x.zoko_customer_id === cid);
      if (t && t.labels) t.labels = t.labels.filter(l => l !== 'auto-answered');
      cswRenderThreadList();
      cswToast('Yours now — the auto-answer is logged as overridden.');
      document.getElementById('csw-input')?.focus();
    } catch (e) { cswToast(cswSayWhy(e, { lead: 'Could not take it over.' }), true); }
  });
}

// Editor: the eight classes, team-shared, plain words (customer-facing
// copy — CRUX voice). Availability is display-locked: stock questions
// always go to a rep, and the server refuses activation too.
async function cswFaqSheet() {
  let d;
  try { d = await api('/api/cs/faq'); }
  catch (e) { cswToast(cswSayWhy(e, { lead: 'The answers did not load.' }), true); return; }
  const classes = (d && d.classes) || [];
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">FAQ auto-answers</div><button class="csw-x">✕</button></div>
    <div class="csw-hint" style="margin-bottom:10px">EON sends these instantly when a customer asks one of the eight common questions — before a rep even opens the thread. Edit the words any time; they go out exactly as written. Store hours, addresses and order status fill in live.</div>
    ${classes.length ? classes.map((c, i) => `
      <button class="csw-need" data-fq="${i}" style="margin-bottom:8px">
        <span class="csw-need__l">
          <b style="display:block;font-size:10.5px;color:var(--ink-mute);text-transform:uppercase;letter-spacing:.05em;margin-bottom:3px">
            ${esc(c.label || c.class)} · ${c.never_auto ? 'always a rep' : (c.active ? 'ON' : 'OFF')} · ${c.sends_7d || 0} sent / ${c.overrides_7d || 0} overridden (7d)</b>
          ${esc(c.answer_text || '')}</span>
        <span class="csw-need__arrow">›</span></button>`).join('')
      : '<div class="csw-empty" style="padding:18px">No FAQ classes yet — the migration seeds them.</div>'}`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  ov.querySelectorAll('[data-fq]').forEach(b => b.addEventListener('click', () => {
    const c = classes[+b.dataset.fq];
    ov.remove(); cswFaqEditor(c);
  }));
}

function cswFaqEditor(c) {
  const locked = !!c.never_auto;
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">${esc(c.label || c.class)}</div><button class="csw-x">✕</button></div>
    <div class="csw-hint" style="margin-bottom:10px">${locked
      ? `${c.class === 'payment' ? 'Payment' : 'Stock'} questions are never auto-answered — a person always replies. You can still edit the words a rep can reuse.`
      : 'This goes to customers word-for-word. Keep it short and warm.'}
      ${c.class === 'hours' || c.class === 'address' ? ' Keep <b>{stores}</b> — the live store list drops in there.' : ''}
      ${c.class === 'order_status' ? ' Keep <b>{order_line}</b> — the customer\'s own order drops in there.' : ''}</div>
    <textarea class="csw-input" id="csw-fq-body" style="width:100%;min-height:130px;margin-bottom:10px">${esc(c.answer_text || '')}</textarea>
    <label style="display:flex;gap:8px;align-items:center;font-size:12.5px;color:var(--ink-dim);margin-bottom:12px">
      <input type="checkbox" id="csw-fq-active" ${c.active ? 'checked' : ''} ${locked ? 'disabled' : ''}/>
      ${locked ? 'Always a rep — can\'t be switched on' : 'Auto-answer this question'}</label>
    <button class="csw-confirm" id="csw-fq-save">Save</button>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  ov.querySelector('#csw-fq-save').addEventListener('click', async () => {
    const body = { answer_text: ov.querySelector('#csw-fq-body').value.trim() };
    if (!locked) body.active = !!ov.querySelector('#csw-fq-active').checked;
    try {
      const r = await api(`/api/cs/faq/${encodeURIComponent(c.id)}`, { method: 'PATCH', body: JSON.stringify(body) });
      if (!r || !r.ok) throw new Error((r && r.error) || 'save failed');
      ov.remove(); cswToast('Saved — live within 5 minutes.'); cswFaqSheet();
    } catch (e) { cswToast(cswSayWhy(e, { lead: 'Answer not saved.' }), true); }
  });
}

async function cswNoteSheet() {
  const cid = cswState().active;
  const notes = cswState().notes || [];
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Internal notes</div><button class="csw-x">✕</button></div>
    <div style="font-size:11px;color:var(--ink-mute);margin-bottom:10px">Private — the customer never sees these.</div>
    <textarea class="csw-input" id="csw-note-body" placeholder="e.g. prefers oversized · returned once · VIP" style="width:100%;margin-bottom:8px"></textarea>
    <label style="display:flex;gap:8px;align-items:center;font-size:12.5px;color:var(--ink-dim);margin-bottom:12px"><input type="checkbox" id="csw-note-pin"/> Pin to customer card</label>
    <button class="csw-confirm" id="csw-note-add">Add note</button>
    <div id="csw-note-list" style="margin-top:14px">${notes.map(n => `<div class="csw-note">${esc(n.body)}<div class="csw-note__meta"><span>${esc((n.author_rep_email || '').split('@')[0])}</span>${n.pinned ? '<span>📌 pinned</span>' : ''}</div></div>`).join('') || '<div class="csw-empty" style="padding:18px">No notes yet</div>'}</div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  ov.querySelector('#csw-note-add').addEventListener('click', async () => {
    const body = ov.querySelector('#csw-note-body').value.trim();
    const pinned = ov.querySelector('#csw-note-pin').checked;
    if (!body) return;
    try { await api(`/api/cs/threads/${encodeURIComponent(cid)}/notes`, { method: 'POST', body: JSON.stringify({ body, pinned }) }); ov.remove(); cswLoadNotes(cid); }
    catch (e) { cswToast(cswSayWhy(e, { lead: 'Note not saved.' }), true); }
  });
}

// L3 (vet 2026-08-30, cart-from-the-thread): the customer's usual size,
// from data the desk already loaded — the /customer-context sizes_map
// (their OWN orders' size truth, cached per thread on open). '' when
// nothing is known; only ever used to PRE-select an in-stock variant,
// never to add anything.
function cswSizeHintGuess() {
  const s = cswState();
  const cache = s._custIntel;
  if (!cache || cache.cid !== s.active) return '';
  const m = (cache.data || {}).sizes_map;
  if (!m || typeof m !== 'object') return '';
  let best = '', n = 0;
  for (const k of Object.keys(m)) {
    const c = Number(m[k]) || 0;
    if (c > n) { best = k; n = c; }
  }
  return best;
}

// The thread's matched piece as a full picker card, resolved through the
// SAME search shape every other card uses (the availability cache has no
// variant ids, so an Add from it could never build an order line). Gated
// on live stock read from the ALREADY-cached availability — a sold-out
// suggestion mid-sale is a dead end, so it simply doesn't render.
async function cswThreadPieceCard(hit) {
  const s = cswState();
  const key = s.active + ':' + hit.id;
  const av = (s._avail && s._avail.key === key) ? s._avail.data : null;
  if (av && !(av.sellable && Number(av.total_sellable_qty) > 0)) return null;
  try {
    const d = await api('/api/cs/products/search?q='
      + encodeURIComponent(hit.title || ''), { timeoutMs: 15000 });
    const p = ((d && d.products) || []).find(x =>
      String(x.product_id) === String(hit.id));
    if (!p) return null;
    if (!(p.variants || []).some(v => Number(v.available) > 0)) return null;
    return p;
  } catch (_) { return null; }
}

// opts.assistedFor (Ask EON S02-2): the chat whose assisted order this
// picker adds to; its Add to order reopens that order assisted.
function cswProductSheet(forOrder, opts) {
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">${forOrder ? 'Add product to order' : 'Send a product'}</div><button class="csw-x">✕</button></div>
    <input class="csw-search" id="csw-prod-q" placeholder="Search products…" autofocus/>
    <div id="csw-prod-res"><div class="csw-empty" style="padding:18px">Type to search the catalogue</div></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  // WA-tap styled cards (2026-07-24): pre-fill the empty state with what
  // customers are TAPPING the WhatsApp button on — tap-ranked, send-ready.
  // Typing replaces it with normal search; failure keeps the plain empty.
  // L3 (2026-08-30): the piece THIS thread is about goes at the head,
  // above the global tap list — the rep stops typing a name the customer
  // already sent a photo of. One suggested card, never more; inside this
  // sheet overlay only (never a band above the composer); absent match,
  // out-of-stock piece, or a failed lookup → the sheet is byte-identical
  // to yesterday's.
  (async () => {
    try {
      const hit = cswLatestInboundMatch();
      const [d, threadPiece] = await Promise.all([
        api('/api/cs/products/tapped-cards'),
        hit && hit.id ? cswThreadPieceCard(hit) : Promise.resolve(null),
      ]);
      // STYLED cards (2026-07-24 vet, re-applied after the #1307 stale-base
      // clobber): a styled render IS the card image, so the send-card flow
      // ships the render, not the packshot.
      let items = ((d && d.cards) || []).map(p =>
        p.styled_image_url ? { ...p, image_url: p.styled_image_url } : p);
      const tappedCount = items.length;
      if (threadPiece) {
        items = [threadPiece,
                 ...items.filter(p => String(p.product_id) !== String(threadPiece.product_id))];
      }
      const res = ov.querySelector('#csw-prod-res');
      if (!items.length || !res || !res.querySelector('.csw-empty')) return;
      if ((ov.querySelector('#csw-prod-q').value || '').trim()) return;
      const tappedHead = `<div class="csw-empty" style="padding:10px 18px 4px">Most tapped on WhatsApp · 30d${d.generic_taps ? ` · +${d.generic_taps} generic taps` : ''}</div>`;
      res.innerHTML = (threadPiece
        ? `<div class="csw-empty" style="padding:10px 18px 4px">From this chat — they asked about:</div>`
          + cswProdCard(items[0], 0, forOrder)
          + (items.length > 1 ? tappedHead + items.slice(1).map((p, i) => cswProdCard(p, i + 1, forOrder)).join('') : '')
        : (tappedCount ? tappedHead + items.map((p, i) => cswProdCard(p, i, forOrder)).join('') : ''));
      if (!res.innerHTML) return;
      cswBindProdCards(res, items, forOrder, ov, opts);
      // taps badge per card (upgrade 2026-07-24) — injected, never in the
      // title (the title ships to the customer in the card send)
      res.querySelectorAll('.csw-prod').forEach(card => {
        const p = items[+card.dataset.i];
        if (!p || !p.taps) return;
        const wrap = card.querySelector('.csw-prod__imgwrap');
        if (wrap) wrap.insertAdjacentHTML('beforeend',
          `<span class="csw-taps">×${p.taps} taps</span>`);
      });
    } catch (_) { /* plain empty state stays */ }
  })();
  const q = ov.querySelector('#csw-prod-q');
  let timer;
  q.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(async () => {
      const term = q.value.trim();
      if (term.length < 2) return;
      const res = ov.querySelector('#csw-prod-res');
      res.innerHTML = '<div class="csw-empty" style="padding:18px"><span class="csw-think">Searching…</span></div>';
      try {
        const d = await api('/api/cs/products/search?q=' + encodeURIComponent(term));
        const items = d.products || [];
        if (!items.length) { res.innerHTML = '<div class="csw-empty" style="padding:18px">No matches</div>'; return; }
        res.innerHTML = items.map((p, i) => cswProdCard(p, i, forOrder)).join('');
        cswBindProdCards(res, items, forOrder, ov, opts);
      } catch (e) { res.innerHTML = `<div class="csw-empty" style="padding:18px">${esc(e.message || e)}</div>`; }
    }, 320);
  });
}

// "L·1 — Content Creation · M·2 — Pop Up (closed)": each size no shop
// shelf holds, with the doors that do hold its units (cs_inbox held_at).
// The caller escapes the whole line.
function cswHeldSizesLine(variants) {
  const parts = [];
  (variants || []).forEach(v => {
    if (Number(v.available) > 0) return;
    (v.held_at || []).forEach(h => parts.push(
      `${v.title || 'One'}·${h.units} — ${h.location_name}${h.closed ? ' (closed)' : ''}`));
  });
  return parts.join(' · ');
}

// Notify-when-back names ONE size, and never a size whose units already
// sit off a shop shelf (#3653 round 3, first read on 5921bee). The sweep
// reads Shopify's all-locations total (back_in_stock._back_in_stock), so a
// product-level row, or a row for a size held at the studio, reads "back" on
// its very next run and the customer is told it is back when no shop can
// hand it over. Returns {error} for the rep's toast, or {payload}.
function cswNotifyRestockPlan(p, v) {
  if (!v || !v.variant_id) return { error: 'Pick the size they want first' };
  if (!(Number(v.available) > 0) && v.needs_verification) {
    const where = (v.held_at || []).map(h => h.location_name).filter(Boolean).join(', ');
    return { error: `${v.title || 'That size'} is held at ${where || 'a place that is not a shop'}, `
      + 'not on a shop shelf, so a waitlist would tell them it is back straight away. '
      + 'Ask the team to move it to a shop.' };
  }
  return { payload: { product_shopify_id: p.product_id, variant_shopify_id: v.variant_id,
                      sku: v.sku || p.sku || null, product_title: p.title } };
}

function cswProdCard(p, i, forOrder) {
  const variants = p.variants || [];
  // ONE series on the card: the chip is the sum of the per-size shelf
  // counts the size buttons print (cs_inbox._variant_entries — what a
  // shop can hand over). Shopify's total_stock counts studio and
  // warehouse units, so it put "1 in stock" above a row of dark sizes
  // (fallback read on #3653, 2026-10-01).
  const stock = variants.length
    ? variants.reduce((a, v) => a + (v.available > 0 ? v.available : 0), 0)
    : (p.sellable_stock != null ? p.sellable_stock : (p.total_stock || 0));
  const offShelf = variants.some(v => !(v.available > 0) && v.needs_verification);
  const stockChip = stock > 0
    ? `<span class="csw-prod__stk ok">${stock} in stock</span>`
    : (offShelf
      ? '<span class="csw-prod__stk zero">Not on a shop shelf</span>'
      : '<span class="csw-prod__stk zero">Out of stock</span>');
  // A dark size says WHERE its units are, so the rep is never left
  // guessing: "L·1 — Content Creation". Door names stay on this screen;
  // the chat lane never receives them.
  const heldLine = cswHeldSizesLine(variants);
  const canAdd = variants.some(v => Number(v.available) > 0);
  const draft = p.status && p.status !== 'active'
    ? `<span class="csw-prod__stk draft">${esc(p.status)}</span>` : '';
  const imgSrc = p.image_url ? cswImgSrc(p.image_url) : '';
  return `<div class="csw-prod" data-i="${i}">
    <div class="csw-prod__imgwrap${imgSrc ? '' : ' noimg'}">${imgSrc
      ? `<img class="csw-prod__img" src="${esc(imgSrc)}" alt="" loading="lazy" onerror="this.parentNode.classList.add('noimg');this.remove()"/>`
      : ''}<span class="csw-prod__ph">◫</span></div>
    <div class="csw-prod__m">
      <div class="csw-prod__t">${esc(p.title)}</div>
      <div class="csw-prod__row"><span class="csw-prod__p">${esc(p.currency || 'NGN')} ${esc(p.price)}</span>${stockChip}${draft}</div>
      <div class="csw-sizes">${variants.map((v, vi) => `<button class="csw-size ${v.available > 0 ? '' : 'oos'}" data-v="${vi}" ${v.available > 0 ? '' : `${forOrder ? 'disabled' : ''}${v.needs_verification ? ' title="Not on a shop shelf"' : ''}`}>${esc(v.title || 'One')}${v.available > 0 ? ` ·${v.available}` : ''}</button>`).join('')}</div>${heldLine
        ? `<div class="csw-stock__l zero" data-held style="margin-top:6px">Not on a shop shelf: ${esc(heldLine)}</div>` : ''}
      <div class="csw-prod__acts">
        ${forOrder
          ? `<button class="csw-pill csw-pill--accent" data-act="add"${canAdd ? '' : ' disabled title="No size is on a shop shelf"'}>Add to order</button><button class="csw-pill" data-act="stock">Stock</button>`
          : '<button class="csw-pill csw-pill--accent" data-act="send">Send to chat</button><button class="csw-pill" data-act="tryon"><span class="csw-pill__i">◊</span> Try on</button><button class="csw-pill" data-act="stock">Stock</button><button class="csw-pill" data-act="notify-restock">Notify when back</button>'}
      </div>
      <div class="csw-stock" data-stock style="margin-top:8px"></div>
    </div></div>`;
}

function cswBindProdCards(root, items, forOrder, ov, opts) {
  // L3: the customer's usual size (their own order history) pre-selects
  // its variant — labelled by selection, never asserted as what they
  // asked for, overridable with the same tap as before. IN-STOCK only:
  // a zero-stock size hint falls through to the first available size,
  // so the pre-selection can never point the Add at a dead variant.
  // PRE-SELECTION IS FOR ADD ONLY (#3653, the Fable critical reads on
  // 9d297fe). The order card pre-selects so Add has a size. The chat card's
  // one size-bound action is Notify, which must name the size the CUSTOMER
  // asked for: a silent pick there filed a waiting row for the in-stock M
  // when they wanted the dark L, and the hourly sweep told them "it's back"
  // about a size they never asked for. So on the chat card nothing is chosen
  // until the rep taps a size.
  const hint = cswSizeHintGuess().trim().toLowerCase();
  root.querySelectorAll('.csw-prod').forEach(card => {
    const p = items[+card.dataset.i];
    let selV = -1;
    if (forOrder && hint) {
      selV = (p.variants || []).findIndex(v => Number(v.available) > 0
        && (String(v.title || '').trim().toLowerCase() === hint
            || String(v.title || '').trim().toLowerCase().endsWith('/ ' + hint)));
      if (selV >= 0) {
        const b = card.querySelector(`.csw-size[data-v="${selV}"]`);
        if (b) b.classList.add('sel');
      }
    }
    if (forOrder && selV < 0) selV = (p.variants || []).findIndex(v => v.available > 0);
    // No size on a shop shelf: nothing is pre-selected, and Add refuses
    // below. The old fallback to the FIRST size pointed Add at a button
    // this card had just greyed out, so a studio unit could still reach a
    // customer's order (fallback read on #3653, 2026-10-01).
    card.querySelectorAll('.csw-size').forEach(btn => btn.addEventListener('click', () => {
      if (btn.disabled) return; selV = +btn.dataset.v;
      card.querySelectorAll('.csw-size').forEach(x => x.classList.toggle('sel', x === btn));
    }));
    card.querySelectorAll('[data-act]').forEach(btn => btn.addEventListener('click', async () => {
      const act = btn.dataset.act;
      const v = (p.variants || [])[selV] || {};
      if (act === 'send') {
        const orig = btn.textContent; btn.disabled = true; btn.textContent = 'Sending…';
        try {
          // Product card: image card when there's an image, else a clean
          // text card (title · price · link) — never a hard 400 on no-image.
          const payload = { image_url: p.image_url || '', title: p.title, price: p.price, currency: p.currency, product_url: p.product_url };
          const r = await cswSendApi(`/api/cs/threads/${encodeURIComponent(cswState().active)}/send-card`, { method: 'POST', body: JSON.stringify(payload) });
          if (r && r.ok === false) throw new Error(r.error || 'send failed');
          ov.remove(); cswToast('Sent to chat'); setTimeout(() => cswLoadTranscript(cswState().active, true), 1400);
        } catch (e) { btn.disabled = false; btn.textContent = orig; cswToast(cswSayWhy(e, { send: true }), true); }
      } else if (act === 'stock') {
        const box = card.querySelector('[data-stock]');
        if (box.dataset.open === '1') { box.dataset.open = '0'; box.innerHTML = ''; return; }
        box.dataset.open = '1';
        box.innerHTML = '<span class="csw-think">Checking…</span>';
        try {
          // SIZES per location (e.g. "XL·11 · L·9 — Abuja") keyed on the product.
          const d = await api('/api/cs/inventory/sizes?product_id=' + encodeURIComponent(p.product_id || ''));
          const locs = (d && d.locations) || [];
          box.innerHTML = locs.length
            ? locs.map(l => `<div class="csw-stockloc"><div class="csw-stockloc__sizes">${l.sizes.map(s => `<span class="csw-szchip">${esc(s.size)}<b>·${s.available}</b></span>`).join('')}</div><div class="csw-stockloc__name">${esc(l.location_name)}</div></div>`).join('')
            : '<span class="csw-stock__l zero">Out of stock everywhere</span>';
        } catch (e) { box.textContent = 'n/a'; }
      } else if (act === 'add') {
        // Never a size no shop can hand over. The button is disabled when
        // no size is on a shelf; this holds even if a stale card is tapped.
        if (!(Number(v.available) > 0)) { cswToast('Pick a size a shop can hand over', true); return; }
        cswCart().push({ variant_id: v.variant_id, product_id: p.product_id, title: p.title, variant_title: v.title, price: p.price, quantity: 1, image_url: p.image_url || '' });
        cswCart()._orderRevision = (cswCart()._orderRevision || 0) + 1;
        cswCartStyleHint(p.product_id, p.title);
        // Back assisted only for the chat it was opened for, while the switch
        // and the teammate's order access are still on; otherwise bare.
        const assisted = !!opts?.assistedFor && opts.assistedFor === cswState().active && cswAssistedOrderOn();
        ov.remove(); cswOrderSheet(assisted ? { assisted: true } : undefined);
      } else if (act === 'tryon') {
        cswTryOnSheet(p);
      } else if (act === 'notify-restock') {
        // Opt-in capture: waitlist this customer for the selected variant.
        // No send happens here — the gated cron messages them when it's back.
        // One size, never a held one: see cswNotifyRestockPlan.
        const plan = cswNotifyRestockPlan(p, selV >= 0 ? v : null);
        if (plan.error) { cswToast(plan.error, true); return; }
        const orig = btn.textContent; btn.disabled = true; btn.textContent = 'Adding…';
        try {
          const r = await api(`/api/cs/threads/${encodeURIComponent(cswState().active)}/notify-restock`,
                              { method: 'POST', body: JSON.stringify(plan.payload) });
          if (r && r.ok === false) throw new Error(r.error || 'failed');
          btn.textContent = 'On waitlist'; cswToast("We'll alert them when it's back");
        } catch (e) { btn.disabled = false; btn.textContent = orig; cswToast(cswSayWhy(e, { lead: 'Not added to the waitlist.' }), true); }
      }
    }));
  });
}

// ── In-thread live stock lookup (operator card, executed 2026-07-30) ──
// One tap in the tray → per-store stock for any product, without the
// product-picker's card-send framing and without knowing the typed
// "@eon …" ritual. Rides /api/cs/availability (the unified resolver),
// so the branch split and the 404-Tee honesty rules match what @eon
// and checkout already say. Seeds itself from the customer's last
// message when it reads like an availability question — the blind
// answer is a rep who never searches at all.
function cswStockEvidenceContext(anchor = null) {
  const s = cswState(), reading = s._evidenceTranscript || {};
  // Product facts depend on source content and product selection, not receipt
  // ticks/reactions. This is a scoped browser comparison, not a server version.
  const receiptKeys = ['status','delivery_status','sent_at','delivered_at','read_at',
    'failed_at','status_at','receipt_at','receipt_status','delivery_receipts','receipts','reactions',
    // Match cs_action_context.signature bookkeeping; spoken words, quotes and
    // product/image readings remain content and still invalidate this snapshot.
    'status_errors','cs_delivery_receipt','intent2','intent2_meta','more_media','file_read'];
  // Match the server's content projection, with recipient identity retained.
  // Renderer-only recovery labels are deliberately outside this whitelist.
  const contentKeys = ['id','direction','type','body','quote','quote_mid','quoted_id',
    'agent_email','platform_timestamp','created_at','tenant_id','platform_sender_id','zoko_customer_id'];
  const stable = value => Array.isArray(value) ? value.map(stable)
    : value && typeof value === 'object'
      ? Object.fromEntries(Object.keys(value).sort().map(key => [key,stable(value[key])])) : value;
  const rows = Array.isArray(reading.rows) ? reading.rows.map(row => {
    const content = Object.fromEntries(contentKeys.map(key => [key,row?.[key] ?? null]));
    let body = row?.body;
    if (typeof body === 'string') { try { body = JSON.parse(body); } catch (_) { body = {text:body}; } }
    content.body = body && typeof body === 'object' && !Array.isArray(body) ? {...body} : {};
    for (const key of receiptKeys) delete content.body[key];
    return stable(content);
  }) : null;
  const snapshot = {cid: typeof s.active === 'string' ? s.active : null,
    loadedCid: reading.cid ?? null, readState: Array.isArray(reading.rows) ? reading.state ?? 'unavailable' : 'unavailable',
    observedAt: reading.observedAt ?? null,
    contextVersion: rows ? JSON.stringify(rows) : null};
  // Keep the original observed time only for identical source content.
  // Read time and a receipt tick alone do not change these product facts.
  if (anchor && snapshot.cid === anchor.cid && snapshot.loadedCid === anchor.loadedCid
      && snapshot.readState === anchor.readState && snapshot.contextVersion === anchor.contextVersion) {
    snapshot.observedAt = anchor.observedAt;
  }
  return snapshot;
}

function cswStockSheet() {
  const cid = cswState().active;
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Stock</div><button class="csw-x">✕</button></div>
    <div class="csw-stock-search"><label class="csw-piece-sr" for="csw-stk-q">Find a product</label><input class="csw-search" id="csw-stk-q" placeholder="Product name — e.g. scream jeans" autofocus/><button class="csw-pill" id="csw-stk-refresh" type="button">Check again</button></div>
    <p class="csw-piece-note" id="csw-stk-notice" role="status" hidden></p>
    <div id="csw-stk-res"><div class="csw-empty" style="padding:18px">Type a product to see per-branch stock</div></div>`);
  ov.classList.add('csw-stock-overlay');
  const setActionsDisabled = disabled => ov.querySelectorAll('[data-act]').forEach(button => { button.disabled = disabled; });
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const q = ov.querySelector('#csw-stk-q'), refresh = ov.querySelector('#csw-stk-refresh');
  const notice = ov.querySelector('#csw-stk-notice');
  const tell = text => { notice.textContent = text; notice.hidden = !text; };
  let requestSeq = 0, productView = null, queryContext = null, invalidatedContext = false;
  let settledCurrent = null, timer, refreshing = false;
  const sameContext = context => {
    const current = cswStockEvidenceContext(context);
    return current.cid === context?.cid && current.loadedCid === context?.loadedCid
      && current.readState === context?.readState && current.contextVersion === context?.contextVersion
      && current.observedAt === context?.observedAt;
  };
  const invalidate = () => {
    // A stock sheet belongs to one recipient. Never offer a refresh for the
    // new recipient inside the old recipient's sheet.
    if (cswState().active !== cid) { ov.remove(); return; }
    if (!queryContext) return;
    if (sameContext(queryContext)) {
      if (invalidatedContext && settledCurrent?.()) {
        invalidatedContext = false;
        productView?.restore(); setActionsDisabled(false); tell('');
      }
      return;
    }
    invalidatedContext = true;
    const ready = cswStockEvidenceContext().readState === 'ready';
    const message = ready
      ? (queryContext.contextVersion === null
        ? 'Messages have loaded. Check this product again.'
        : 'New information in this chat. Check this product again.')
      : 'Messages did not refresh. Check again before using these facts.';
    productView?.invalidate(message); setActionsDisabled(true);
    tell(productView ? '' : message);
  };
  window.addEventListener('csw-evidence-change', invalidate);
  const originalRemove = ov.remove.bind(ov);
  ov.remove = () => {
    requestSeq += 1; clearTimeout(timer); productView?.dispose();
    window.removeEventListener('csw-evidence-change', invalidate);
    originalRemove();
  };
  const run = async () => {
    if (!ov.isConnected || cswState().active !== cid) return;
    const term = q.value.trim();
    if (term.length < 2) { tell('Enter at least two letters to find a product.'); return; }
    const res = ov.querySelector('#csw-stk-res');
    const request = ++requestSeq, context = cswStockEvidenceContext();
    queryContext = context; invalidatedContext = false; settledCurrent = null; tell('');
    productView?.dispose(); productView = null;
    const isCurrent = () => ov.isConnected && request === requestSeq
      && q.value.trim() === term && cswState().active === cid && sameContext(context);
    res.innerHTML = '<div class="csw-empty" style="padding:18px"><span class="csw-think">Checking every branch…</span></div>';
    let d;
    try { d = await api('/api/cs/availability?q=' + encodeURIComponent(term)); }
    catch (e) { if (!isCurrent()) return; res.innerHTML = `<div class="csw-empty" style="padding:18px">${esc(cswSayWhy(e, { lead: 'Stock did not load.' }))}</div>`; return; }
    if (!isCurrent()) return;
    const stockReadAt = new Date().toISOString();
    if (!d || d.ok === false) {
      res.innerHTML = `<div class="csw-empty" style="padding:18px">${esc((d && d.error) || 'No product matched')}</div>`;
      return;
    }
    res.innerHTML = cswStockResult(d); settledCurrent = isCurrent;
    cswLoadStockFacts(res, d, context, stockReadAt, isCurrent, () => q.focus()).then(view => {
      if (isCurrent()) productView = view;
      else view?.dispose();
    });
    const ins = res.querySelector('[data-act="insert-reply"]');
    if (ins) ins.addEventListener('click', (ev) => {
      if (!isCurrent()) return;
      ov.remove();
      if (!document.getElementById('csw-input') || !String(d.reply || '').trim()) return;
      cswAdoptDraft(cid, d.reply, () => cswToast('Stock reply drafted — review + send'), ev, { claim: false });
    });
    const hold = res.querySelector('[data-act="hold"]');
    if (hold) hold.addEventListener('click', () => { if (!isCurrent()) return; ov.remove(); cswHoldSheet(hold.dataset.pid); });
  };
  const checkAgain = async () => {
    if (refreshing || !ov.isConnected || cswState().active !== cid) return;
    clearTimeout(timer);
    if (q.value.trim().length < 2) { tell('Enter at least two letters to find a product.'); q.focus(); return; }
    const heldRefreshFocus = document.activeElement === refresh;
    refreshing = true; refresh.disabled = true;
    try {
      // A deliberate retry can reuse the desk's existing transcript loader.
      // No background reader or poller; ordinary lookups still use two reads.
      if (typeof cid === 'string' && !cid.startsWith('team:') && cswStockEvidenceContext().readState !== 'ready') {
        tell('Refreshing messages…');
        // A failed read is deliberate here — nothing below checks it. The
        // messages read and /api/cs/availability are separate doors: a thread
        // whose messages keep timing out still has stock to show, and
        // cswLoadStockFacts already tells the rep the product notes stay
        // hidden until this chat's messages load. Refusing the lookup left a
        // rep who typed a product and pressed Enter with no stock at all —
        // less than the typed path gives, because Enter also cancels the
        // lookup typing had queued.
        try { await cswLoadTranscript(cid, true); } catch (_) { /* deliberate */ }
        if (!ov.isConnected || cswState().active !== cid) return;
      }
      // The retry can outlast the 320 ms debounce, so the rep may have queued
      // another lookup while it ran. Drop it: run() re-reads the box, so the
      // newest words are still what gets looked up — in one read, not two.
      clearTimeout(timer);
      await run();
    } finally {
      refreshing = false; refresh.disabled = false;
      if (heldRefreshFocus && ov.isConnected && document.activeElement === document.body) refresh.focus();
    }
  };
  refresh.addEventListener('click', checkAgain);
  q.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); checkAgain(); } });
  q.addEventListener('input', () => {
    requestSeq += 1; settledCurrent = null; clearTimeout(timer); setActionsDisabled(true);
    const message = q.value.trim().length < 2 ? 'Enter at least two letters to find a product.' : 'Checking this product…';
    productView?.invalidate(message); tell(productView ? '' : message);
    if (q.value.trim().length >= 2) timer = setTimeout(run, 320);
  });
  const bubs = document.querySelectorAll('#csw-script .csw-bub.in .csw-bub__txt');
  const last = bubs.length ? (bubs[bubs.length - 1].textContent || '').trim() : '';
  if (last && last.length <= 60
      && /\b(availab|in stock|stock|size|price|how much|do (?:you|u) have|left|colou?r)/i.test(last)) {
    q.value = last;
    run();
  }
}

function cswStockResult(d) {
  const p = d.product || {};
  const locs = d.locations || [];
  let price = '';
  if (p.price) {
    const n = parseFloat(p.price);
    price = `${esc(p.currency || 'NGN')} ${isNaN(n) ? esc(p.price) : n.toLocaleString()}`;
  }
  const chip = d.sellable
    ? '<span class="csw-prod__stk ok">Sellable now</span>'
    : `<span class="csw-prod__stk zero">${p.status === 'ARCHIVED' ? 'Drop ended' : 'Sold out'}</span>`;
  const imgSrc = p.image_url ? cswImgSrc(p.image_url) : '';
  const locHtml = locs.map(l => {
    const named = (l.sizes || []).filter(s => (+s.available || 0) > 0);
    const placeholderOnly = named.length && named.every(
      s => ['default title', 'one', '—'].includes((s.size || '').trim().toLowerCase()));
    const sizes = placeholderOnly
      ? `<span class="csw-szchip">${+l.total || 0} left</span>`
      : named.map(s => `<span class="csw-szchip">${esc(s.size)}<b>·${+s.available}</b></span>`).join('');
    return `<div class="csw-stockloc"><div class="csw-stockloc__sizes">${sizes}</div><div class="csw-stockloc__name">${esc(l.location_name)}</div></div>`;
  }).join('');
  // The honest-hedge case (404-Tee rule): Shopify sells it, no branch
  // shows units — say yes online, never promise a branch pickup.
  // A FAILED branch-ledger read (d.branch_read_failed) is NOT an empty
  // one: say the check failed and to retry, never "no branch shows
  // units" over a pg error.
  const hedge = d.branch_read_failed
    ? '<div class="csw-stockhedge">Couldn’t check branch stock — try again.</div>'
    : (d.sellable && !locs.length
      ? '<div class="csw-stockhedge">Available online — no branch shows units. Don’t promise a store pickup until a branch confirms.</div>'
      : '');
  return `<div class="csw-prod csw-stock-result" style="cursor:default">
      <div class="csw-prod__imgwrap${imgSrc ? '' : ' noimg'}">${imgSrc
        ? `<img class="csw-prod__img" src="${esc(imgSrc)}" alt="" loading="lazy" onerror="this.parentNode.classList.add('noimg');this.remove()"/>`
        : ''}<span class="csw-prod__ph">◫</span></div>
      <div class="csw-prod__m">
        <div class="csw-prod__t">${esc(p.title || '')}</div>
        <div class="csw-prod__row"><span class="csw-prod__p">${price}</span>${chip}</div>
        <div class="csw-stock" data-stock-fallback style="margin-top:8px">${locHtml}${hedge}</div></div>
        <div class="csw-stockfacts" data-pid="${esc(p.product_id || '')}"></div>
        <div class="csw-prod__acts">${d.reply
          ? '<button class="csw-pill csw-pill--accent" data-act="insert-reply">Insert reply</button>' : ''}${
          // LANE B: a sellable piece can be held at a store from the Stock
          // tool too — the rep already has the product in hand here.
          (d.sellable && p.product_id)
          ? `<button class="csw-pill" data-act="hold" data-pid="${esc(p.product_id)}">Hold at a store</button>` : ''}</div>
      </div>`;
}

// Reuse the stock tool's existing facts request. Optional evidence modules
// load behind the ordinary stock result, which remains the failure fallback.
async function cswLoadStockFacts(root, availability, context, stockReadAt, isCurrent = () => true, focusFallback) {
  const box = root.querySelector('.csw-stockfacts'), pid = box?.dataset.pid;
  if (!box || !pid || !isCurrent()) return null;
  box.innerHTML = '<p class="csw-piece-note">Reading garment measurements…</p>';
  const factsRead = api('/api/catalog-facts?product_id=' + encodeURIComponent(pid)).then(
    data => ({data, state: data?.ok === true ? 'ready' : 'failed', observedAt: new Date().toISOString()}),
    () => ({data: null, state: 'failed', observedAt: new Date().toISOString()}));
  try {
    const [{normalizeProductEvidence}, {mountProductEvidence}, read] = await Promise.all([
      import('./cs_product_evidence.js'), import('./cs_product_evidence_view.js'), factsRead]);
    if (!box.isConnected) return null;
    if (!isCurrent()) { box.textContent = 'Product notes need refreshing. Check again.'; return null; }
    const model = normalizeProductEvidence({context, selection:{product_id:pid},
      availability, facts:read.data, reads:{
        availability:{state:'ready', requestedProductId:pid, observedAt:stockReadAt},
        facts:{state:read.state, requestedProductId:pid, observedAt:read.observedAt}}},
      {now:new Date().toISOString(), origin:window.location.origin, currentContext:cswStockEvidenceContext(context)});
    // Preserve ordinary stock, but never silently erase successfully read notes.
    // They stay hidden until this exact customer's conversation is verified.
    if (model.identity.status !== 'product_bound' || model.binding.context.status !== 'bound') {
      box.textContent = context?.cid?.startsWith('team:')
        ? 'Open a customer conversation to see product notes.'
        : model.binding.context.status !== 'bound'
          ? 'Product notes are hidden until the chat messages load. Check again.'
          : 'Product notes could not be matched to this piece. Check again.';
      return null;
    }
    const view = mountProductEvidence(box, model, {isCurrent, focusFallback});
    if (view) root.querySelector('[data-stock-fallback]')?.setAttribute('hidden', '');
    return view;
  } catch (_) {
    if (box.isConnected && isCurrent()) box.textContent = 'Product notes did not load.';
    return null;
  }
}

// ── Virtual try-on (FASHN) — rep generates, reviews, then sends ──
async function cswTryOnSheet(p) {
  const cid = cswState().active;
  if (!cid) return;
  if (!p.image_url) { cswToast('This product has no image to try on.', true); return; }
  let opt = {};
  try { opt = await api(`/api/cs/threads/${encodeURIComponent(cid)}/tryon/options`); } catch (_) {}
  if (!opt.configured) {
    cswToast('Virtual try-on isn’t set up yet — add a FASHN API key + a default model image.', true);
    return;
  }
  const hasCustomer = !!opt.customer_photo;
  // C3 (directive WS-C, 2026-07-27): CURATED FIRST. Real studio looks for
  // this exact product outrank an AI render of it — curated looks sat
  // unused while reps generated try-ons. Any look the studio already shot
  // shows above the generator, sendable in one tap; generation stays, but
  // labelled for what it is (new, AI, may differ from the real product).
  // The lookup fills its slot ASYNCHRONOUSLY — awaiting it before building
  // the overlay left the rep staring at nothing for the length of the
  // styling query (up to the api() ceiling) with no spinner and no sheet.
  let curated = null;
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">👗 Virtual try-on</div><button class="csw-x">✕</button></div>
    <div class="csw-tryon__prod"><img src="${esc(p.image_url)}" alt=""/><div><div class="csw-prod__t">${esc(p.title)}</div><div class="csw-prod__p">${esc(p.currency || 'NGN')} ${esc(p.price)}</div></div></div>
    <div id="csw-tryon-curated"></div>
    <div style="font-size:11px;color:var(--ink-mute);margin:12px 0 8px" id="csw-tryon-genlabel">Render this on:</div>
    <div class="csw-tryon__models">
      ${opt.default_model ? '<button class="csw-modelpick on" data-m="default">A CRUX model</button>' : ''}
      <button class="csw-modelpick ${!opt.default_model ? 'on' : ''} ${hasCustomer ? '' : 'disabled'}" data-m="customer" ${hasCustomer ? '' : 'disabled'}>${hasCustomer ? 'The customer’s photo' : 'Customer photo (none sent)'}</button>
    </div>
    <div class="csw-tryon__stage" id="csw-tryon-stage"></div>
    <button class="csw-confirm" id="csw-tryon-go">Generate try-on</button>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  // Fill the curated slot when the styling read lands. If the sheet was
  // closed meanwhile, drop it — never paint into a detached overlay.
  (async () => {
    let st = null;
    try {
      const cs = await api('/api/cs/styling/'
        + encodeURIComponent(p.product_id || '') + '?source=tryon_entry');
      st = cs && cs.styling;
    } catch (_) { return; }
    if (!st || st.status !== 'looks' || !(st.looks || []).length) return;
    if (!ov.isConnected) return;
    curated = st;
    const slot = ov.querySelector('#csw-tryon-curated');
    if (!slot) return;
    const n = st.looks.length;
    slot.className = 'csw-st csw-st--sheet csw-tryon__curated';
    slot.innerHTML = `<div class="csw-st__h">✦ ${n} look${n === 1 ? '' : 's'} already styled with this — send one</div>`
      + st.looks.slice(0, 3).map(l => cswStLookHtml(l, st.product_title || p.title, true)).join('');
    cswBindSendLooks(slot);
    slot.querySelectorAll('.csw-st__render').forEach(im =>
      im.addEventListener('click', () => cswLightbox(im.dataset.full, '')));
    const lab = ov.querySelector('#csw-tryon-genlabel');
    if (lab) lab.textContent = 'Or generate a new one — AI, may differ from the real product. Render on:';
    const go = ov.querySelector('#csw-tryon-go');
    if (go && !go.disabled) go.textContent = 'Generate new (AI) — may differ from product';
  })();
  let model = opt.default_model ? 'default' : (hasCustomer ? 'customer' : 'default');
  ov.querySelectorAll('.csw-modelpick:not(.disabled)').forEach(b => b.addEventListener('click', () => {
    model = b.dataset.m;
    ov.querySelectorAll('.csw-modelpick').forEach(x => x.classList.toggle('on', x === b));
  }));
  ov.querySelector('#csw-tryon-go').addEventListener('click', async () => {
    const go = ov.querySelector('#csw-tryon-go');
    const stage = ov.querySelector('#csw-tryon-stage');
    go.disabled = true; go.textContent = 'Generating…';
    stage.innerHTML = '<div class="csw-tryon__load"><span class="csw-think">✦ rendering try-on… (20–40s)</span></div>';
    try {
      const r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/tryon`, { method: 'POST', body: JSON.stringify({ garment_image: p.image_url, model }) });
      if (!r.ok || !r.prediction_id) throw new Error(r.error || 'could not start');
      const url = await cswTryOnPoll(r.prediction_id);
      if (!url) throw new Error('render timed out');
      stage.innerHTML = `<img class="csw-tryon__out" src="${esc(url)}" alt="try-on"/>`;
      go.outerHTML = `<button class="csw-confirm" id="csw-tryon-send">Send to chat</button><button class="csw-mini" id="csw-tryon-again" style="margin-top:8px">↻ Regenerate</button>`;
      ov.querySelector('#csw-tryon-send').addEventListener('click', async () => {
        try { await cswSendApi(`/api/cs/threads/${encodeURIComponent(cid)}/send-image`, { method: 'POST', body: JSON.stringify({ file_url: url, caption: p.title }) }); ov.remove(); cswToast('Try-on sent'); setTimeout(() => cswLoadTranscript(cid, true), 1400); }
        catch (e) { cswToast(cswSayWhy(e, { send: true }), true); }
      });
      ov.querySelector('#csw-tryon-again').addEventListener('click', () => { ov.remove(); cswTryOnSheet(p); });
    } catch (e) {
      stage.innerHTML = `<div class="csw-tryon__load" style="color:var(--danger)">${esc(e.message || e)}</div>`;
      go.disabled = false;
      go.textContent = curated ? 'Generate new (AI) — may differ from product' : 'Generate try-on';
    }
  });
}
// Upload an image to the bucket → returns its URL (for Studio garments/person).
async function cswUpload(file) {
  const fd = new FormData();
  fd.append('file', file);
  const headers = { 'Authorization': `Bearer ${State.jwt}` };
  if (State.tenant) headers['X-Tenant-Slug'] = State.tenant.slug;
  const r = await fetch(API + '/api/cs/upload', { method: 'POST', headers, body: fd });
  if (!r.ok) throw new Error('upload failed (' + r.status + ')');
  return (await r.json()).url;
}

// ── Try-on STUDIO — drop/upload or pick garments + a person → compose ──
// FASHN does one garment per pass, so multiple garments are CHAINED:
// each render becomes the base for the next. (FASHN covers tops/bottoms/
// one-pieces; hats/shoes/accessories aren't supported by its model.)
function cswTryOnStudio() {
  const cid = cswState().active;
  if (!cid) return;
  const st = { person: null, garments: [] };   // person:{url,label}, garments:[{url,category,title}]
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">👗 Try-on studio</div><button class="csw-x">✕</button></div>
    <div class="csw-studio" id="csw-studio"></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());

  async function pickFile(onUrl) {
    const inp = document.createElement('input'); inp.type = 'file'; inp.accept = 'image/*';
    inp.addEventListener('change', async () => {
      const f = inp.files && inp.files[0]; if (!f) return;
      cswToast('Uploading…');
      try { onUrl(await cswUpload(f)); } catch (e) { cswToast(cswSayWhy(e, { lead: 'Upload failed.' }), true); }
    });
    inp.click();
  }
  function addGarmentFromCatalogue() {
    cswProductPick((p) => { st.garments.push({ url: p.image_url, category: 'auto', title: p.title }); render(); });
  }
  function render() {
    const body = ov.querySelector('#csw-studio');
    const cats = ['auto', 'tops', 'bottoms', 'one-pieces'];
    body.innerHTML = `
      <div class="csw-studio__sec">Person</div>
      <div class="csw-studio__person">
        ${st.person ? `<img src="${esc(st.person.url)}" alt=""/><span>${esc(st.person.label || 'Person')}</span><button class="csw-mini" data-x="person">Change</button>`
          : `<button class="csw-mini" data-p="customer">Customer's photo</button><button class="csw-mini" data-p="upload">Upload / drop</button>`}
      </div>
      <div class="csw-studio__drop" id="csw-drop">drag &amp; drop a person or garment image here</div>
      <div class="csw-studio__sec">Garments <span style="color:var(--ink-faint)">(stacked in order)</span></div>
      <div class="csw-studio__garments">
        ${st.garments.map((g, i) => `<div class="csw-garment"><img src="${esc(g.url)}" alt=""/>
          <select class="csw-search csw-garment__cat" data-i="${i}" style="min-height:34px;padding:6px">${cats.map(c => `<option value="${c}" ${g.category === c ? 'selected' : ''}>${c}</option>`).join('')}</select>
          <button class="csw-mini" data-rm="${i}">✕</button></div>`).join('')}
      </div>
      <div class="csw-studio__add"><button class="csw-mini" data-g="cat">+ Catalogue</button><button class="csw-mini" data-g="upload">+ Upload garment</button></div>
      <div class="csw-tryon__stage" id="csw-studio-stage"></div>
      <button class="csw-confirm" id="csw-studio-go" ${st.person && st.garments.length ? '' : 'disabled'}>Generate the look</button>`;
    // person
    body.querySelector('[data-p="customer"]')?.addEventListener('click', async () => {
      try { const o = await api(`/api/cs/threads/${encodeURIComponent(cid)}/tryon/options`); if (o.customer_photo) { st.person = { url: o.customer_photo, label: "Customer's photo" }; render(); } else cswToast('No customer photo in this chat', true); } catch (_) {}
    });
    body.querySelector('[data-p="upload"]')?.addEventListener('click', () => pickFile(u => { st.person = { url: u, label: 'Uploaded' }; render(); }));
    body.querySelector('[data-x="person"]')?.addEventListener('click', () => { st.person = null; render(); });
    // garments
    body.querySelector('[data-g="cat"]')?.addEventListener('click', addGarmentFromCatalogue);
    body.querySelector('[data-g="upload"]')?.addEventListener('click', () => pickFile(u => { st.garments.push({ url: u, category: 'auto', title: 'Uploaded' }); render(); }));
    body.querySelectorAll('[data-rm]').forEach(b => b.addEventListener('click', () => { st.garments.splice(+b.dataset.rm, 1); render(); }));
    body.querySelectorAll('.csw-garment__cat').forEach(sel => sel.addEventListener('change', () => { st.garments[+sel.dataset.i].category = sel.value; }));
    // drop zone
    const dz = body.querySelector('#csw-drop');
    if (dz) {
      dz.addEventListener('dragover', e => { e.preventDefault(); dz.classList.add('over'); });
      dz.addEventListener('dragleave', () => dz.classList.remove('over'));
      dz.addEventListener('drop', async e => {
        e.preventDefault(); dz.classList.remove('over');
        const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (!f) return;
        cswToast('Uploading…');
        try { const u = await cswUpload(f); if (!st.person) st.person = { url: u, label: 'Uploaded' }; else st.garments.push({ url: u, category: 'auto', title: 'Uploaded' }); render(); }
        catch (er) { cswToast('Upload failed', true); }
      });
    }
    body.querySelector('#csw-studio-go')?.addEventListener('click', () => cswStudioGenerate(ov, st, cid));
  }
  render();
}

async function cswStudioGenerate(ov, st, cid) {
  const stage = ov.querySelector('#csw-studio-stage');
  const go = ov.querySelector('#csw-studio-go');
  go.disabled = true; go.textContent = 'Composing…';
  let current = st.person.url;
  try {
    for (let i = 0; i < st.garments.length; i++) {
      const g = st.garments[i];
      stage.innerHTML = `<div class="csw-tryon__load"><span class="csw-think">✦ applying ${esc(g.title || g.category)} (${i + 1}/${st.garments.length})…</span></div>`;
      const r = await api(`/api/cs/tryon/raw`, { method: 'POST', body: JSON.stringify({ garment_image: g.url, model_image: current, category: g.category }) });
      if (!r.ok || !r.prediction_id) throw new Error(r.error || 'could not start');
      const out = await cswTryOnPoll(r.prediction_id);
      if (!out) throw new Error('render timed out on ' + (g.title || g.category));
      current = out;
    }
    stage.innerHTML = `<img class="csw-tryon__out" src="${esc(current)}" alt="look"/>`;
    go.outerHTML = `<button class="csw-confirm" id="csw-studio-send">Send to chat</button>`;
    ov.querySelector('#csw-studio-send').addEventListener('click', async () => {
      try { await cswSendApi(`/api/cs/threads/${encodeURIComponent(cid)}/send-image`, { method: 'POST', body: JSON.stringify({ file_url: current }) }); ov.remove(); cswToast('Look sent'); setTimeout(() => cswLoadTranscript(cid, true), 1400); }
      catch (e) { cswToast(cswSayWhy(e, { send: true }), true); }
    });
  } catch (e) {
    stage.innerHTML = `<div class="csw-tryon__load" style="color:var(--danger)">${esc(e.message || e)}</div>`;
    go.disabled = false; go.textContent = 'Generate the look';
  }
}

// Lightweight product picker (returns a product to a callback) for the Studio.
function cswProductPick(onPick) {
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Pick a garment</div><button class="csw-x">✕</button></div>
    <input class="csw-search" id="csw-pp-q" placeholder="Search products…" autofocus/>
    <div id="csw-pp-res"><div class="csw-empty" style="padding:16px">Type to search</div></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const q = ov.querySelector('#csw-pp-q'); let timer;
  q.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(async () => {
      const term = q.value.trim(); if (term.length < 2) return;
      const res = ov.querySelector('#csw-pp-res');
      res.innerHTML = '<div class="csw-empty" style="padding:16px"><span class="csw-think">Searching…</span></div>';
      try {
        const d = await api('/api/cs/products/search?q=' + encodeURIComponent(term));
        const items = d.products || [];
        res.innerHTML = items.length ? items.map((p, i) => `<button class="csw-prod" data-i="${i}" style="width:100%;text-align:left;border:none;background:transparent"><div class="csw-prod__imgwrap">${p.image_url ? `<img class="csw-prod__img" src="${esc(p.image_url)}"/>` : ''}<span class="csw-prod__ph">◫</span></div><div class="csw-prod__m"><div class="csw-prod__t">${esc(p.title)}</div></div></button>`).join('') : '<div class="csw-empty" style="padding:16px">No matches</div>';
        res.querySelectorAll('.csw-prod').forEach(b => b.addEventListener('click', () => { ov.remove(); onPick(items[+b.dataset.i]); }));
      } catch (e) { res.innerHTML = `<div class="csw-empty" style="padding:16px">${esc(e.message || e)}</div>`; }
    }, 320);
  });
}

// @eon command runner — routes to ONE skill:
//   tryon        → render overlay (poll → preview → send)
//   availability → drop a send-ready stock reply into the composer
//   reply        → drop a drafted reply into the composer
async function cswAssistRun(command) {
  const cid = cswState().active;
  if (!cid) return;
  cswToast('✦ EON is working…');
  let r;
  try { r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/assist`, { method: 'POST', body: JSON.stringify({ command }), timeoutMs: 30000 }); }
  catch (e) { cswToast(cswSayWhy(e, { lead: 'EON did not answer.' }), true); return; }
  if (!r || !r.ok) { cswToast(cswSayWhy({ message: String((r && r.error) || '') }, { lead: 'EON could not do that.' }), true); return; }
  if (cswState().active !== cid && r.action !== 'reply' && r.action !== 'availability' && r.action !== 'ask_store') return;
  if (r.action === 'tryon') { cswTryOnResult(r.prediction_id, r.product || {}); return; }
  if (r.action === 'style') { cswStyleTipSheet(r); return; }
  if (r.action === 'ask_store') {
    // The room bridge (Telegram Room wave 3): the store was asked in the
    // team's room; its answer lands on this conversation as a note. The
    // receipt says exactly that, so the rep does not ask twice — and the
    // desk watches for the note so the rep does not have to.
    cswToast(r.reply || ('Asked ' + (r.store || 'the store') + ' in the room — the answer will land here as a note'));
    if (r.filed === false) return;      // the card is up but the answer cannot come back here
    cswWatchRoomAnswer(cid, r.store || 'the store', r.message_id);
    return;
  }
  if ((r.action === 'availability' || r.action === 'reply') && r.reply) {
    cswShowDraftCard(cid, r.reply, r);
    cswToast(r.action === 'availability'
      ? (r.identified_from_photo ? 'Identified from photo — stock drafted, review + send' : 'Stock drafted — review + send')
      : 'Reply drafted — review + send');
    return;
  }
  cswToast('EON had nothing to add', true);
}

// The room's answer, watched for (Telegram Room wave 3). After an
// ask_store the desk polls THAT conversation's notes for half an hour;
// a "From the room" note written after the ask, carrying its "(room
// ask N)" marker, is a store's answer: a toast naming the store the
// note names, and if the rep is still on this conversation, the notes
// panel refreshed. Each ask has its own watcher (asking Palms and then
// Abuja on one conversation keeps both); what was already on the thread
// before the ask is read from the server, never from whichever
// conversation the desk happens to be showing; a note that lands
// between the ask and the first read is still announced (it is dated
// after the ask); another conversation's state is never touched.
const _cswRoomWatch = {};
function cswWatchRoomAnswer(cid, store, askId) {
  const started = Date.now();
  const key = cid + '|' + (askId || store);
  if (_cswRoomWatch[key]) clearInterval(_cswRoomWatch[key].timer);
  const seen = new Set();
  let primed = false;
  const mine = (n) => !!askId && (n.body || '').indexOf('(room ask ' + askId + ')') >= 0;
  const isAnswer = (n) => /^From the room/.test(n.body || '')
    && (askId ? mine(n)
              : (!n.created_at || Date.parse(n.created_at) >= started - 5 * 60 * 1000));
  const prime = async () => {
    try {
      const d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/notes`);
      // what was there before the ask is not its answer — unless it
      // carries this ask's own marker (the store answered while the
      // desk was still hearing back): that one is announced
      (d.notes || []).forEach(n => { if (!mine(n) && (!n.created_at || Date.parse(n.created_at) < started)) seen.add(n.id); });
    } catch (_) {}
    primed = true;
  };
  prime();
  const timer = setInterval(async () => {
    if (Date.now() - started > 30 * 60 * 1000) { clearInterval(timer); delete _cswRoomWatch[key]; return; }
    if (!primed) return;
    try {
      const d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/notes`);
      const notes = d.notes || [];
      const fresh = notes.find(n => !seen.has(n.id) && isAnswer(n));
      if (fresh) {
        clearInterval(timer); delete _cswRoomWatch[key];
        if (cswState().active === cid) { cswState().notes = notes; cswRenderNotesPanel(cid); }
        const m = /^From the room — ([^,]+),/.exec(fresh.body || '');
        cswToast(`${(m && m[1]) || store} answered — see the note on this chat`);
      }
    } catch (_) {}
  }, 20000);
  _cswRoomWatch[key] = { timer, started };
}

// Styling tip sheet (@eon style …) — REP-FACING: the studio looks + pieces
// with live stock, in an overlay. Nothing lands in the composer and nothing
// is sent; the rep relays the tip in their own words (spec 2026-07-14).
function cswStyleTipSheet(r) {
  const prod = r.product || {};
  const looks = r.looks || [];
  const inner = looks.length
    ? looks.slice(0, 3).map(l => cswStLookHtml(l, prod.title)).join('')
    : ((r.pairs || []).length
      ? `<div class="csw-st__h csw-st__h--quiet">◇ ${esc(r.note || 'Pairs well — from real orders')}</div>` + cswStPairsHtml(r.pairs)
      : `<div class="csw-empty" style="padding:16px">${esc(r.note || 'No studio look for this one yet — the studio queue sees the ask.')}</div>`);
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">✦ Styling · ${esc(prod.title || '')}</div><button class="csw-x">✕</button></div>
    <div class="csw-st csw-st--sheet">${inner}</div>
    <div class="csw-warn">For you — write the customer message in your own words.</div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  ov.querySelectorAll('.csw-st__render').forEach(im => im.addEventListener('click', () => cswLightbox(im.dataset.full, '')));
}

// Try-on result overlay (already-started render): poll → preview → send.
function cswTryOnResult(predId, product) {
  const cid = cswState().active;
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">👗 Try-on${product.title ? ' · ' + esc(product.title) : ''}</div><button class="csw-x">✕</button></div>
    <div class="csw-tryon__stage" id="csw-tryon-stage"><div class="csw-tryon__load"><span class="csw-think">✦ rendering try-on… (20–40s)</span></div></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  (async () => {
    const stage = ov.querySelector('#csw-tryon-stage');
    let url;
    try { url = await cswTryOnPoll(predId); } catch (e) { url = null; }
    if (!url) { stage.innerHTML = `<div class="csw-tryon__load" style="color:var(--danger)">Render failed or timed out.</div>`; return; }
    stage.innerHTML = `<img class="csw-tryon__out" src="${esc(url)}" alt="try-on"/>
      <button class="csw-confirm" id="csw-tryon-send" style="margin-top:12px">Send to chat</button>`;
    ov.querySelector('#csw-tryon-send').addEventListener('click', async () => {
      try { await cswSendApi(`/api/cs/threads/${encodeURIComponent(cid)}/send-image`, { method: 'POST', body: JSON.stringify({ file_url: url, caption: product.title || '' }) }); ov.remove(); cswToast('Try-on sent'); setTimeout(() => cswLoadTranscript(cid, true), 1400); }
      catch (e) { cswToast(cswSayWhy(e, { send: true }), true); }
    });
  })();
}

async function cswTryOnPoll(pid) {
  for (let i = 0; i < 24; i++) {           // ~72s max (3s × 24)
    await new Promise(r => setTimeout(r, 3000));
    let d;
    try { d = await api(`/api/cs/tryon/${encodeURIComponent(pid)}`); } catch (_) { continue; }
    if (d && d.status === 'completed' && (d.output || []).length) return d.output[0];
    if (d && (d.status === 'failed' || d.error)) throw new Error(d.error || 'render failed');
  }
  return null;
}

// Cart styling hint (the cart door, spec 2026-07-14): adding a piece to
// the order quietly asks the styling ladder about it. REP-FACING — a row
// under the cart the rep can tap for the full tip sheet; never a send.
async function cswCartStyleHint(productId, title) {
  if (!productId) return;
  const s = cswState();
  s._styleHints = s._styleHints || {};
  if (productId in s._styleHints) { cswRenderCartHints(); return; }
  s._styleHints[productId] = null;   // fetching
  try {
    const d = await api(`/api/cs/styling/${encodeURIComponent(productId)}`);
    const st = d && d.styling;
    if (!st) return;
    s._styleHints[productId] = { title, styling: st };
    cswRenderCartHints();
    if ((st.looks || []).length) cswToast('✦ Styled in studio — hint added to the order sheet');
  } catch (_) { /* hint only — never blocks the cart */ }
}

function cswRenderCartHints() {
  const box = document.getElementById('csw-ost');
  if (!box) return;
  const s = cswState();
  const inCart = new Set((s.cart || []).map(c => String(c.product_id || '')));
  const hints = Object.entries(s._styleHints || {})
    .filter(([pid, h]) => h && inCart.has(String(pid))).map(([, h]) => h);
  if (!hints.length) { box.innerHTML = ''; return; }
  box.innerHTML = hints.map(h => {
    const st = h.styling;
    if ((st.looks || []).length) {
      const names = (st.looks[0].pieces || []).map(p => p.title).filter(Boolean).slice(0, 3).join(' + ');
      return `<button class="csw-ost__row" data-ost="${esc(st.product_shopify_id)}">✦ ${esc(h.title)} is styled in studio${names ? ' — with ' + esc(names) : ''}</button>`;
    }
    const names = (st.pieces || []).map(p => p.title).filter(Boolean).slice(0, 3).join(' + ');
    return names ? `<button class="csw-ost__row csw-ost__row--quiet" data-ost="${esc(st.product_shopify_id)}">◇ ${esc(h.title)} pairs well with ${esc(names)}</button>` : '';
  }).join('');
  box.querySelectorAll('[data-ost]').forEach(bt => bt.addEventListener('click', () => {
    const h = (cswState()._styleHints || {})[bt.dataset.ost];
    if (!h) return;
    const st = h.styling;
    cswStyleTipSheet({ product: { title: h.title }, looks: st.looks || [],
      pairs: st.pieces || [], note: (st.looks || []).length ? '' : 'Pairs well — from real orders' });
  }));
}

// A draft belongs to the cart that made it, including edits that were undone.
// The content stamp also catches edits from callers that do not bump the counter.
function cswOrderCartRevision(cart) {
  return `${cart._orderRevision || 0}:${JSON.stringify(cart)}`;
}

function cswOrderCartMatches(order) {
  const s = cswState();
  const cart = s.cartCid === order.cid ? s.cart : s._carts?.[order.cid];
  return s === order.state && cart === order.cart
    && cswOrderCartRevision(cart) === order.cartRevision;
}

function cswOrderOwnsDraft(order, rev) {
  return cswState() === order.state && order.state._orderDrafts?.[order.cid] === order
    && order.review === rev
    && order.draftId === rev.draft_id;
}

function cswOrderFinish(order, rev) {
  if (!cswOrderOwnsDraft(order, rev)) return;
  const s = order.state;
  if (cswOrderCartMatches(order)) {
    if (s.cartCid === order.cid) s.cart = [];
    else delete s._carts[order.cid];
  }
  delete s._orderDrafts[order.cid];
}

// Ask EON S02 B3b: an assisted quote's own reference. The sheet makes it
// before the first POST and keeps it in this login's own device envelope
// (cswDraftIdentity: the server-verified tenant, actor, role and
// impersonation), under the chat, until the answer settles it: the quote
// itself, or a refusal that proves nothing was made. A lost or unresolved
// answer stays held through reloads and sign-ins of that login, with no
// expiry. A held reference is not a receipt and permits no retry.
function cswQuoteRef() {
  const c = typeof crypto !== 'undefined' ? crypto : null;
  if (!c || typeof c.getRandomValues !== 'function') return null;   // no weak fallback
  return Array.from(c.getRandomValues(new Uint8Array(16)), x => x.toString(16).padStart(2, '0')).join('');
}
// {} claims the chat for ref only while it is free; a row updates ref's own
// hold; null lets ref's own hold go. Another reference's hold is never touched.
function cswQuoteKeep(login, cid, ref, row) {
  const data = cswDraftStore(login), quotes = data?.quotes || {};
  if (!data || !ref || typeof quotes !== 'object' || Array.isArray(quotes)) return false;
  const old = quotes[cid];
  if (old ? old.ref !== ref : row === null) return false;
  if (row) quotes[cid] = { ...row, ref, at: old?.at || Date.now() }; else delete quotes[cid];
  data.quotes = quotes;
  return cswDraftWrite(login, data);
}
function cswQuoteHeld(cid) {
  const row = cswDraftStore(cswDraftIdentity())?.quotes?.[cid];
  if (!row) return null;
  // A hold this device cannot read is still a hold: shown, with no reference.
  const ok = typeof row === 'object' && /^[0-9a-f]{32}$/.test(row.ref);
  return { quoteId: ok ? row.ref : '', reference: 'caller',
    draftId: ok && typeof row.draft === 'string' && /^\d+$/.test(row.draft) ? row.draft : '',
    message: (ok && typeof row.said === 'string' && row.said)
      || 'EON didn’t get an answer for this quote, so it may already be in Shopify. Check Shopify’s drafts before making another.' };
}
// A quote answer that proves nothing was made: the server's own refusal (a
// 4xx that is not a timeout, a 5xx with its own error code) or a sign-out.
function cswQuoteRefused(e) {
  if (e?.message === 'unauthorized' && e.status === undefined) return true;
  if (e?.timeout || typeof e?.status !== 'number') return false;
  return e.status < 500 || typeof e.body?.detail?.error === 'string';
}
// The read-back (S02 B2b's door) for a held quote, by its own reference only.
// It reads; it never quotes, places or lets the hold go. Zero is not proof
// the quote was never made, and unknown is not either.
async function cswQuoteCheck(cid, ref) {
  let r = null;
  try {
    r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/assisted/reconcile?quote_id=${encodeURIComponent(ref)}&kind=quote`);
  } catch (_) { /* a refusal or no answer: it could not check */ }
  const sc = r?.scope;
  const done = r?.ok === true && r.schema === 'eon.s02_reconcile.v2' && r.kind === 'quote' && r.reason === null
    && sc?.thread_id === String(cid) && sc.traversal_complete === true && sc.retry_authorized === false;
  if (done && r.match === 'one_in_scope' && r.record?.quote_id === ref && /^\d+$/.test(String(r.record.draft_id ?? '')))
    return `Shopify has this quote as draft ${r.record.draft_id}. Don’t make another.`;
  if (done && r.match === 'multiple_in_scope')
    return 'Shopify has more than one draft with this quote. Check them in Shopify before anything else.';
  if (done && r.match === 'zero_in_scope')
    return 'EON looked through Shopify’s recent drafts and didn’t see this quote. That doesn’t prove it wasn’t made, so don’t make another.';
  return 'EON couldn’t check Shopify just now. Nothing was changed.';
}
// S02 positive review: is this login, with the same sign-in, still holding
// ref for this chat on this device? Asked before the read and after it.
function cswQuoteStill(login, cid, ref) {
  const now = cswDraftIdentity();
  return !!(login && now && now.key === login.key && now.authSession === login.authSession
    && cswDraftStore(login)?.quotes?.[cid]?.ref === ref);
}
// The held quote read fresh by its own reference (GET .../assisted/review,
// eon.s02_review.v1). It reads; it never quotes, places or lets the hold go.
// Only an answer that is exactly this quote, this chat and this draft, still
// open and unplaced, comes back as a review. Anything else is a reason the
// quote stays held: the server's own words for a held answer, or ours.
async function cswQuoteReview(cid, ref, draftId) {
  let r = null;
  try {
    r = await api(`/api/cs/threads/${encodeURIComponent(cid)}/assisted/review?quote_id=${encodeURIComponent(ref)}`);
  } catch (_) { /* a refusal or no answer: it stays held */ }
  const v1 = r?.schema === 'eon.s02_review.v1' && r.place_authorized === false && r.retry_authorized === false;
  const word = x => typeof x === 'string' && x.length > 0, money = x => typeof x === 'string' && /^[0-9]+(?:\.[0-9]+)?$/.test(x);
  const lines = Array.isArray(r?.line_items) && r.line_items.length > 0 && r.line_items.every(li => li && word(li.title)
    && (li.variant_title === null || typeof li.variant_title === 'string') && Number.isInteger(li.quantity) && li.quantity >= 1 && money(li.price));
  const did = typeof r?.draft_id === 'string' && /^\d+$/.test(r.draft_id) ? r.draft_id : '';
  if (v1 && r.ok === true && r.quote_id === ref && r.for_customer === String(cid) && did && (!draftId || did === draftId)
      && ['open', 'invoice_sent'].includes(r.status) && money(r.total) && /^[A-Z]{3}$/.test(r.currency ?? '')
      && word(r.quoted_by) && lines && (r.customer_name === null || typeof r.customer_name === 'string'))
    return { rev: { draft_id: did, name: '', total: r.total, currency: r.currency, invoice_url: null,
      line_items: r.line_items.map(li => ({ title: li.title, variant_title: li.variant_title, quantity: li.quantity, price: li.price })),
      quoteId: ref, quotedBy: r.quoted_by, forName: r.customer_name || '' } };
  return { said: v1 && r.ok === false && r.held === true && word(r.error) ? r.error
    : 'EON couldn’t show this quote just now. It stays held. Nothing was changed.' };
}
// The held quote as Shopify has it now: its own sheet, with the fresh lines,
// total and customer, never the cart. Accept is the only way on. It writes
// the proved draft to the hold first, then moves this same reference into
// the existing review, where Place order is its own separate yes. The hold
// goes only once an order is placed. Nothing on this sheet sends anything.
function cswQuoteFresh(order, rev, login, same) {
  const s = order.state, cid = order.cid, ref = rev.quoteId;
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Quote for ${esc(rev.forName || 'this customer')}</div><button class="csw-x">✕</button></div>
    <div class="csw-warn" role="status">This is the quote as Shopify has it now. Nothing is placed yet.</div>
    ${cswReceiptHtml(rev, null, { forName: rev.forName })}
    <div class="csw-rct__to" data-reference="caller">Quote <b>${esc(ref)}</b> · Shopify draft <b>${esc(rev.draft_id)}</b></div>
    <button class="csw-confirm" id="csw-quote-accept">Accept this quote</button>
    <div class="csw-warn">Accepting opens it for Place order. Placing is a separate step.</div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  ov.querySelector('#csw-quote-accept').addEventListener('click', () => {
    ov.remove();
    if (!same()) { cswToast('The chat or sign-in changed, so this quote stays held. Open Build order again.', true); return; }
    const said = cswDraftStore(login).quotes[cid].said;
    if (!cswQuoteKeep(login, cid, ref, { draft: rev.draft_id, said: typeof said === 'string' ? said : '' })) {
      cswToast('This device couldn’t save that you accepted this quote, so it stays held. Nothing was changed.', true);
      return;
    }
    const next = { state: s, cid, cart: null, remembered: null, review: rev, draftId: rev.draft_id, held: { login, ref },
      assisted: { quoteId: ref, total: rev.total, quotedBy: rev.quotedBy, forName: rev.forName } };
    s._orderDrafts[cid] = next;
    cswOrderReview(rev, next);
  });
  return ov;
}

// opts.assisted (Ask EON S02-2): the same sheet, cart and details card for an
// assisted order. The last step prepares a quote for this chat's customer as
// this teammate, instead of a draft. Bare cswOrderSheet() is as before.
function cswOrderSheet(opts) {
  const s = cswState();
  const cart = cswCart();
  const cid = s.active;
  // The product picker carries the mode itself (cswProductSheet's opts): a
  // picker closed without adding carries nothing, so the dock's Build order
  // stays bare (#4689 round 1).
  const assisted = opts?.assisted === true;
  const forName = assisted ? cswAskCustomerName() : '';
  const cartRevision = cswOrderCartRevision(cart);
  let order = s._orderDrafts?.[cid];
  // A quote this login holds for this chat on this device (S02 B3b) comes
  // back after a reload or a sign-in as it was: never silently dropped.
  const restored = !order && cid ? cswQuoteHeld(cid) : null;
  if (restored) order = (s._orderDrafts = s._orderDrafts || {})[cid] = { state: s, cid, cart, cartRevision, unresolved: restored };
  const current = () => cswState() === s && s.active === cid
    && s.cartCid === cid && s.cart === cart && cswOrderCartRevision(cart) === cartRevision;
  const total = cart.reduce((a, c) => a + (parseFloat(c.price) || 0) * c.quantity, 0);
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">${assisted ? `Build order for ${esc(forName || 'this customer')}` : 'Build order'}</div><button class="csw-x">✕</button></div>
    ${cart.length ? cart.map((c, i) => {
      const thumb = c.image_url
        ? `<button class="csw-oline__thumb" data-thumb="${esc(cswImgSrc(c.image_url))}"><img src="${esc(cswImgSrc(c.image_url))}" alt="" loading="lazy"/></button>`
        : '<span class="csw-oline__thumb csw-oline__thumb--ph">◫</span>';
      return `<div class="csw-oline">${thumb}<span class="csw-oline__q">${c.quantity}×</span><span style="flex:1">${esc(c.title)} ${c.variant_title ? '· ' + esc(c.variant_title) : ''}</span><span>${cswNgn((parseFloat(c.price) || 0) * c.quantity)}</span><button class="csw-x" data-rm="${i}" style="width:26px;height:26px;font-size:12px">✕</button></div>`;
    }).join('') : '<div class="csw-empty" style="padding:18px">No items yet</div>'}
    ${cart.length ? `<div class="csw-ototal"><span>Total</span><span>${cswNgn(total)}</span></div>` : ''}
    ${order ? (order.unresolved
      ? `<div class="csw-warn" role="status">${esc(order.unresolved.message)}</div><div class="csw-rct__to" data-reference="${esc(order.unresolved.reference)}">Quote <b>${esc(order.unresolved.quoteId)}</b>${order.unresolved.draftId ? ` · Shopify draft <b>${esc(order.unresolved.draftId)}</b>` : ''}</div>${order.unresolved.check ? `<div class="csw-warn" role="status">${esc(order.unresolved.check)}</div>` : ''}${order.unresolved.quoteId ? '<button class="csw-mini" id="csw-quote-check" style="margin-top:8px;width:100%">Check Shopify for this quote</button><button class="csw-mini" id="csw-quote-review" style="margin-top:8px;width:100%">Show this quote as Shopify has it now</button>' : ''}`
      : order.review && !order.busy
      ? '<button class="csw-confirm" id="csw-resume-order">Review saved draft</button>'
      : '<div class="csw-warn" role="status">This order is still being saved. Open Build order again to check it.</div>') : ''}
    <div id="csw-ost" class="csw-ost"></div>
    <button class="csw-mini" id="csw-add-prod" style="margin-top:12px;width:100%">+ Add product</button>
    ${cart.length && !order ? `<button class="csw-confirm" id="csw-build">Continue to customer details</button><div class="csw-warn">${assisted
      ? 'Next: confirm the customer\'s details → prepares a quote (nothing charged or placed yet).'
      : 'Next: confirm name/phone/email/address → creates a Shopify DRAFT (nothing charged yet).'}</div>` : ''}`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  cswRenderCartHints();
  // Tap a cart thumbnail to enlarge for confirmation (item picked is correct).
  ov.querySelectorAll('[data-thumb]').forEach(b => b.addEventListener('click', () => cswLightbox(b.dataset.thumb, '')));
  ov.querySelectorAll('[data-rm]').forEach(b => b.addEventListener('click', () => {
    if (!current()) { ov.remove(); return; }
    cart.splice(+b.dataset.rm, 1); cart._orderRevision = (cart._orderRevision || 0) + 1;
    ov.remove(); cswOrderSheet({ assisted });
  }));
  ov.querySelector('#csw-add-prod').addEventListener('click', () => {
    ov.remove(); if (!current()) return;
    cswProductSheet(true, assisted ? { assistedFor: cid } : undefined);
  });
  ov.querySelector('#csw-resume-order')?.addEventListener('click', () => {
    ov.remove(); if (cswState() === s && s.active === cid) cswOrderReview(order.review, order);
  });
  // Read only: whatever the check finds, the quote stays held right here.
  const check = ov.querySelector('#csw-quote-check');
  check?.addEventListener('click', async () => {
    const kept = order.unresolved;
    if (kept.checking) return;
    kept.checking = check.disabled = true;
    kept.check = await cswQuoteCheck(cid, kept.quoteId);
    kept.checking = false;
    if (ov.isConnected && cswState() === s && s.active === cid) { ov.remove(); cswOrderSheet({ assisted }); }
  });
  // S02 positive review, read only: the login, chat and reference are taken
  // before the read, and an answer for any other one is dropped. A fresh,
  // exact quote opens its own sheet; anything else is said here, still held.
  const look = ov.querySelector('#csw-quote-review');
  look?.addEventListener('click', async () => {
    const kept = order.unresolved, login = cswDraftIdentity();
    const same = () => cswState() === s && s.active === cid && s._orderDrafts?.[cid] === order
      && order.unresolved === kept && cswQuoteStill(login, cid, kept.quoteId);
    if (kept.checking) return;
    if (!same()) { ov.remove(); cswToast('The chat or sign-in changed, so this quote stays held. Open Build order again.', true); return; }
    kept.checking = look.disabled = true;
    const got = await cswQuoteReview(cid, kept.quoteId, kept.draftId);
    kept.checking = false;
    if (!same() || !ov.isConnected) return;
    ov.remove();
    if (got.rev) cswQuoteFresh(order, got.rev, login, same);
    else { kept.check = got.said; cswOrderSheet({ assisted }); }
  });
  ov.querySelector('#csw-build')?.addEventListener('click', () => {
    ov.remove();
    if (!current() || s._orderDrafts?.[cid]) return;
    const captured = { state: s, cid, cart, cartRevision,
      remembered: JSON.parse(JSON.stringify(s._remembered || null)), store: s._holdStore || 'a store' };
    const lineItems = cart.map(c => ({ variant_id: c.variant_id, quantity: c.quantity }));
    cswContactCard(cid, async (contact) => {
      if (!current() || s._orderDrafts?.[cid]) throw new Error('The chat or cart changed. Open Build order again.');
      s._orderDrafts = s._orderDrafts || {};
      s._orderDrafts[cid] = captured;
      // S02 B3b: the quote's own reference, made and kept on this device
      // BEFORE the first POST, so a lost answer leaves a reference to look
      // for, never a reason to quote again. No reference kept, nothing sent.
      const ref = assisted ? cswQuoteRef() : null, login = assisted ? cswDraftIdentity() : null;
      let posted = false;
      try {
        if (assisted) {
          // Refused while another tab holds the chat: that quote may be in Shopify.
          if (!ref || !cswQuoteKeep(login, cid, ref, {})) throw new Error(cswDraftStore(login)?.quotes?.[cid]
            ? 'A quote for this chat may already be in Shopify from another tab, so nothing was sent from here. Open Build order again to see it.'
            : 'This device couldn’t keep a reference for this quote, so nothing was sent.');
          posted = true;
          // The server's own quote: its total and pieces are Shopify's, and
          // only its own draft, quote and total can place it later.
          const q = await api(`/api/cs/threads/${encodeURIComponent(cid)}/assisted/quote`, { method: 'POST', body: JSON.stringify({ line_items: lineItems, contact, quote_id: ref }) });
          if (!q?.ok || !q.draft_id || q.quote_id !== ref) throw new Error(q?.error || 'The quote was not prepared. Try again.');
          posted = false;
          cswQuoteKeep(login, cid, ref, null);   // answered: the quote is in hand
          captured.review = { draft_id: q.draft_id, name: '', total: q.total, currency: q.currency,
            line_items: q.line_items || [], invoice_url: null };
          captured.draftId = q.draft_id;
          captured.assisted = { quoteId: q.quote_id, total: q.total, quotedBy: q.quoted_by || '', forName };
        } else {
          const d = await api('/api/cs/orders/draft', { method: 'POST', body: JSON.stringify({ zoko_customer_id: cid, line_items: lineItems, contact }) });
          if (!d.ok || !d.order_review?.draft_id) throw new Error(d.error || 'draft failed');
          captured.review = d.order_review; captured.draftId = d.order_review.draft_id;
        }
        if (current()) cswOrderReview(captured.review, captured);
        else cswToast(assisted ? 'Quote saved in the original chat. Open Build order there to review it.'
          : 'Draft saved in the original chat. Open Build order there to review it.');
      } catch (e) {
        // An unresolved quote (a 502 whose detail says unresolved): a draft may
        // already be in Shopify. The sheet keeps it with its quote, reference
        // and draft id and the server's own words, and offers no fresh quote
        // for it. Every other refusal is as before.
        // S02 B3b: so is an answer lost on the way back (no answer, a timeout,
        // a 5xx with no refusal of the server's own, a success not naming this
        // reference). Both stay held on this device under the reference sent;
        // only a refusal that proves nothing was made lets the reference go.
        const u = assisted && e?.body?.detail?.unresolved === true ? e.body.detail : null;
        const held = !!u || (posted && !cswQuoteRefused(e));
        const said = typeof u?.message === 'string' ? u.message.trim() : '';
        const draftId = /^\d+$/.test(String(u?.draft_id ?? '')) ? String(u.draft_id) : '';
        if (assisted) cswQuoteKeep(login, cid, ref, held ? { draft: draftId, said } : null);
        if (held && s._orderDrafts[cid] === captured) {
          captured.unresolved = { quoteId: ref, reference: 'caller', draftId,
            message: said || 'This quote may already be in Shopify. Check Shopify’s drafts before making another.' };
          if (current()) cswOrderSheet({ assisted });
          else cswToast(`${captured.unresolved.message} It is kept in the original chat’s Build order.`, true);
          return;
        }
        if (s._orderDrafts[cid] === captured) delete s._orderDrafts[cid];
        throw e;
      }
    }, assisted ? 'Prepare quote' : undefined);
  });
  return ov;
}

// Order-intake card — name/phone/email + a CONFIRM/EDIT delivery address.
// Pre-filled by scanning the chat (Sonnet) AND by the customer's known
// Shopify address book (default first) so the rep confirms the right
// drop-off instead of trusting a stale on-file address — ~45% of CRUX
// customers are returning and often want a NEW delivery spot. The confirmed
// structured address rides onto the Shopify draft → order (and the payment
// intent), and never overwrites the customer's saved default.
function cswContactCard(cid, onConfirm, label) {
  const remembered = cswState().active === cid ? cswState()._remembered : null;
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Customer details</div><button class="csw-x">✕</button></div>
    <div style="font-size:11px;color:var(--ink-mute);margin-bottom:10px">Name, phone, email + delivery address. <span class="csw-think" id="cc-scan">✦ scanning chat…</span></div>
    <input class="csw-search" id="cc-name" placeholder="Full name" style="margin-bottom:8px"/>
    <input class="csw-search" id="cc-phone" placeholder="Phone (e.g. 080…)" style="margin-bottom:8px"/>
    <input class="csw-search" id="cc-email" placeholder="Email" style="margin-bottom:8px"/>
    <div style="font-size:10.5px;color:var(--ink-mute);text-transform:uppercase;letter-spacing:.04em;margin:4px 0 6px">Confirm delivery address</div>
    <div class="csw-chips" id="cc-addr-pick" style="margin-bottom:10px;display:none"></div>
    <input class="csw-search" id="cc-address1" placeholder="Address line 1 (street, area)" style="margin-bottom:8px"/>
    <input class="csw-search" id="cc-address2" placeholder="Address line 2 (optional)" style="margin-bottom:8px"/>
    <input class="csw-search" id="cc-city" placeholder="City" style="margin-bottom:8px"/>
    <input class="csw-search" id="cc-province" placeholder="State / province" style="margin-bottom:8px"/>
    <input class="csw-search" id="cc-zip" placeholder="Postcode (optional)" style="margin-bottom:12px"/>
    <button class="csw-confirm" id="cc-create">${esc(label || 'Create draft order')}</button>
    <div class="csw-warn">Confirm or edit the delivery address — it ships to exactly what's here.</div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const setIf = (id, v) => { const el = ov.querySelector(id); if (el && !el.value && v) el.value = v; };
  const fillAddr = (a, force) => {
    if (!a) return;
    const put = (id, v) => { const el = ov.querySelector(id); if (el && (force || !el.value) && v) el.value = v; };
    put('#cc-address1', a.address1); put('#cc-address2', a.address2);
    put('#cc-city', a.city); put('#cc-province', a.province || a.province_code);
    put('#cc-zip', a.zip);
  };
  (async () => {
    // 1) Saved thread contact (manual / prior confirm).
    try { const g = await api(`/api/cs/threads/${encodeURIComponent(cid)}/contact`); const c = (g && g.contact) || {}; setIf('#cc-name', c.name); setIf('#cc-phone', c.phone); setIf('#cc-email', c.email); setIf('#cc-address1', c.address); } catch (_) {}
    // 2) Known Shopify address book → prefill latest/default + offer a picker.
    try {
      const ab = await api(`/api/cs/threads/${encodeURIComponent(cid)}/address-book`, { timeoutMs: 15000 });
      const addrs = (ab && ab.addresses) || [];
      if (addrs.length) {
        fillAddr(addrs[0], false);
        setIf('#cc-name', addrs[0].name);
        setIf('#cc-phone', addrs[0].phone);
        if (addrs.length > 1) {
          const pick = ov.querySelector('#cc-addr-pick'); pick.style.display = 'flex';
          pick.innerHTML = addrs.map((a, i) => {
            const lbl = [a.address1, a.city].filter(Boolean).join(', ') || ('Address ' + (i + 1));
            return `<button class="csw-chip${i === 0 ? ' on' : ''}" data-ai="${i}">${esc(lbl)}${a.is_default ? ' · default' : ''}</button>`;
          }).join('');
          pick.querySelectorAll('[data-ai]').forEach(b => b.addEventListener('click', () => {
            pick.querySelectorAll('.csw-chip').forEach(x => x.classList.remove('on'));
            b.classList.add('on');
            fillAddr(addrs[+b.dataset.ai], true);
          }));
        }
      }
    } catch (_) {}
    // 3) Chat extraction last (only fills still-empty fields).
    try { const e = await api(`/api/cs/threads/${encodeURIComponent(cid)}/extract-contact`, { timeoutMs: 25000 }); const c = (e && e.contact) || {}; setIf('#cc-name', c.name); setIf('#cc-phone', c.phone); setIf('#cc-email', c.email); setIf('#cc-address1', c.address); } catch (_) {}
    // B6 — ask once. The address the customer typed themselves, from the
    // desk's own remembered read. LAST and via setIf, so it only ever
    // fills a field still empty: the saved contact and the Shopify
    // address book are both better sources than a line out of a chat,
    // and this is the backstop for the customer who has neither.
    try {
      const rem = remembered;
      if (rem && rem.address && rem.address.value) setIf('#cc-address1', rem.address.value);
    } catch (_) {}
    const sc = ov.querySelector('#cc-scan'); if (sc) sc.remove();
  })();
  ov.querySelector('#cc-create').addEventListener('click', async () => {
    const v = id => (ov.querySelector(id).value || '').trim();
    const address1 = v('#cc-address1');
    const contact = {
      name: v('#cc-name'), phone: v('#cc-phone'), email: v('#cc-email'),
      address1, address2: v('#cc-address2'), city: v('#cc-city'),
      province: v('#cc-province'), zip: v('#cc-zip'), country: 'Nigeria',
      // Flat block kept for back-compat with anything reading contact.address.
      address: [address1, v('#cc-address2'), v('#cc-city'), v('#cc-province'), v('#cc-zip')].filter(Boolean).join(', '),
    };
    if (!contact.phone && !contact.email) { cswToast('Phone or email is required to place an order.', true); return; }
    if (!address1) { cswToast('Confirm the delivery address line before continuing.', true); return; }
    const btn = ov.querySelector('#cc-create'); btn.disabled = true; btn.textContent = 'Creating draft…';
    try { ov.remove(); await onConfirm(contact); cswLoadLtv(cid); }
    // Round 2 (2026-09-26): one plain sentence, never "Failed: <code>".
    catch (e) { cswToast(cswSayWhy(e), true); }
  });
}

// B7 — SEND TOTAL IS A RECEIPT NOW. Same lines, same total, same two
// keys: what changed is that it reads like the thing the customer is
// about to be asked to pay, with the pieces named as the chat named
// them, the remembered address under them, and the total on its own
// rule. A rep reads it back to a customer over WhatsApp; a list of rows
// with a bold row at the end is harder to read aloud than a receipt.
// `who` (an assisted order, S02-2): the two people on it, the customer it is
// for and the teammate who quoted it.
function cswReceiptHtml(rev, remembered, who) {
  const lines = (rev.line_items || []).map(li =>
    `<div class="csw-rct__l"><span class="csw-rct__q num">${esc(String(li.quantity))}×</span>`
    + `<span class="csw-rct__t">${esc(li.title || '')}${li.variant_title ? ` · ${esc(li.variant_title)}` : ''}</span>`
    + `<span class="csw-rct__p num">${esc(rev.currency)} ${esc(li.price)}</span></div>`).join('');
  const addr = (remembered && remembered.address && remembered.address.value)
    ? `<div class="csw-rct__to">To <b>${esc(String(remembered.address.value))}</b> · remembered from the chat</div>` : '';
  const size = (remembered && remembered.size && remembered.size.value)
    ? `<div class="csw-rct__to">Size <b>${esc(String(remembered.size.value))}</b> · remembered from the chat</div>` : '';
  const people = who
    ? `<div class="csw-rct__to">For <b>${esc(String(who.forName || 'this customer'))}</b> · this chat</div>`
      + '<div class="csw-rct__to">Quoted by <b>you</b></div>' : '';
  return `<div class="csw-rct">${lines}`
    + `<div class="csw-rct__sum"><span>Total</span><span class="num">${esc(rev.currency)} ${esc(rev.total)}</span></div>`
    + size + addr + people + `</div>`;
}

function cswOrderReview(rev, order) {
  const s = cswState();
  if (!order || !cswOrderOwnsDraft(order, rev) || s.active !== order.cid || order.busy) return;
  const cid = order.cid;
  const current = () => cswOrderOwnsDraft(order, rev) && s.active === cid && !order.busy;
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Review order ${esc(rev.name || '')}</div><button class="csw-x">✕</button></div>
    ${cswReceiptHtml(rev, order.remembered, order.assisted ? { forName: order.assisted.forName } : undefined)}
    ${cswOrderCartMatches(order) ? '' : '<div class="csw-warn">This draft keeps the items you confirmed. Your newer cart edits stay in Build order.</div>'}
    ${order.assisted ? `<button class="csw-confirm" id="csw-assisted-place">Place order</button>
    <div class="csw-warn">Places it now, awaiting payment, as you. The server checks your access, this chat, your quote and the total first.</div>` : `<button class="csw-confirm" id="csw-send-invoice">Send payment link to customer</button>
    <button class="csw-mini" id="csw-complete" style="width:100%;margin-top:10px">Mark as ordered</button>
    <div class="csw-warn">Sending the link lets the customer pay (card / transfer). “Mark as ordered” asks how this one is ending — the till, a hold at a store, or here.</div>`}`);
  ov.querySelector('.csw-x').addEventListener('click', () => { if (!order.busy) cswOrderFinish(order, rev); ov.remove(); });
  // An assisted quote has one key: Place order (S02-2). No payment link
  // here; the order is placed awaiting payment, as the teammate who quoted.
  if (order.assisted) {
    const place = ov.querySelector('#csw-assisted-place');
    place?.addEventListener('click', () => cswAssistedPlace(rev, order, ov, place));
    return;
  }
  ov.querySelector('#csw-send-invoice').addEventListener('click', async () => {
    if (!current()) { cswToast('Open the original chat to review this draft.', true); return; }
    order.busy = true;
    try {
      const d = await api(`/api/cs/orders/${encodeURIComponent(order.draftId)}/send-invoice`, { method: 'POST', body: JSON.stringify({ zoko_customer_id: cid, invoice_url: rev.invoice_url }) });
      if (d?.ok === false) throw new Error(d.error || 'The payment link was not sent.');
      cswOrderFinish(order, rev); ov.remove();
      setTimeout(() => { if (cswState() === s && s.active === cid) cswLoadTranscript(cid, true); }, 1400);
    }
    // A customer send: a lost answer may already be with them (round 2).
    catch (e) { cswToast(cswSayWhy(e, { send: true }), true); }
    finally { order.busy = false; }
  });
  ov.querySelector('#csw-complete').addEventListener('click', () => {
    if (!current()) { cswToast('Open the original chat to review this draft.', true); return; }
    // The bare browser confirm is gone. It asked one yes/no question —
    // "Place this order on Shopify now?" — on a desk where a sale ends
    // three ways and ~90% of this brand's money is taken at a counter,
    // so the only route it offered was the rarest one. The sheet names
    // all three and opens in place.
    cswEndingSheet(rev, {
      store: order.store,
      isCurrent: () => {
        if (current()) return true;
        cswToast('Open the original chat to review this draft.', true); return false;
      },
      onBuild: async () => {
        if (!current()) return;
        order.busy = true;
        try {
          const d = await api(`/api/cs/orders/${encodeURIComponent(order.draftId)}/complete`, { method: 'POST', body: JSON.stringify({ confirm: true, zoko_customer_id: cid }) });
          if (!d.ok) throw new Error(d.error || 'failed');
          cswOrderFinish(order, rev); ov.remove();
          if (cswState() !== s || s.active !== cid) {
            // The order was placed, but its payment words belong to the original chat.
            cswToast(cswOrderPlacedWords({ order_name: d.order_name, order_id: d.order_id }));
            prcBagConfirm(d.order_id);
            return;
          }
          cswCelebrate();
          cswToast(cswOrderPlacedWords(d));
          // The order may now be linked to this chat's payment, or offered
          // to it (round 2 on #3543) — re-read the chip so it says which.
          if (d.payment_linked || d.payment_note || d.payment_offer) cswLoadPayment(cid);
          prcBagConfirm(d.order_id);
        // Round 2 (2026-09-26): the route's own sentence when it wrote one,
        // and a lost answer may have placed the order, so it says check.
        } catch (e) { cswToast(cswSayWhy(e, { unsure: 'Not sure the order was placed. Refresh the chat and check before trying again.', otherwise: 'Order not placed. Try again.' }), true); }
        finally { order.busy = false; }
      },
    });
  });
}

// Place order on an assisted quote (S02-2). Only this quote's own draft,
// quote id and quoted total go, with the teammate's yes; the server checks
// access, the chat, the teammate and the total again before it places. A
// refusal shows the server's own sentence and leaves the quote as it was.
async function cswAssistedPlace(rev, order, ov, btn) {
  const s = order.state;
  const cid = order.cid;
  if (order.busy) return;   // one tap at a time; the key is off meanwhile
  const current = () => s.active === cid && cswOrderOwnsDraft(order, rev);
  if (!current()) { cswToast('Open the original chat to review this quote.', true); return; }
  order.busy = true;
  if (btn) btn.disabled = true;
  try {
    const d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/assisted/order`, { method: 'POST', body: JSON.stringify({
      draft_id: order.draftId, quote_id: order.assisted.quoteId, total: order.assisted.total, confirm: true }) });
    if (!d?.ok) throw new Error(d?.error || 'Order not placed. Prepare a new quote.');
    // A held quote accepted from its fresh review (S02): placed, so its hold goes.
    if (order.held) cswQuoteKeep(order.held.login, cid, order.held.ref, null);
    cswOrderFinish(order, rev); ov.remove();
    const placed = { order_name: d.order_name, order_id: d.order_id };
    if (cswState() !== s || s.active !== cid) { cswToast(cswOrderPlacedWords(placed)); prcBagConfirm(d.order_id); return; }
    cswCelebrate();
    cswToast(cswOrderPlacedWords(d));
    if (d.payment_offer || d.payment_linked || d.payment_note) cswLoadPayment(cid);
    prcBagConfirm(d.order_id);
  } catch (e) {
    cswToast(cswSayWhy(e, { unsure: 'Not sure the order was placed. Refresh the chat and check before trying again.', otherwise: 'Order not placed. Prepare a new quote.' }), true);
  } finally {
    order.busy = false;
    if (btn) btn.disabled = false;
  }
}

// What the rep reads after "Mark as ordered" (round 2 on #3543). The
// order may close this chat's paid transfer card, or its total may not be
// the payment's — say which, in one line.
function cswOrderPlacedWords(d) {
  const name = (d && (d.order_name || d.order_id)) || '';
  if (d && d.payment_note) return String(d.payment_note);
  // The chat's payment was already made before this order (adversary pass
  // on round 2): the rep ties the two, or not, on the payment panel.
  if (d && d.payment_offer && d.payment_offer.waiting) return `Order placed${name ? ' · ' + name : ''}. If it's the order this chat's transfer is for, link them on the payment panel.`;
  if (d && d.payment_offer) return `Order placed${name ? ' · ' + name : ''}. If it's for the payment already made on this chat, link them on the payment panel.`;
  if (d && d.payment_linked && d.payment_settled) return `Order placed${name ? ' · ' + name : ''}. The payment on this chat is linked to it.`;
  if (d && d.payment_linked) return `Order placed${name ? ' · ' + name : ''}. It's linked to this chat's payment, which isn't confirmed yet.`;
  return 'Order placed: ' + (name || 'done');
}

function cswCelebrate() {
  const wrap = document.createElement('div');
  wrap.className = 'csw-conf';
  wrap.style.left = '50%'; wrap.style.top = '40%';
  const colors = ['var(--eon-accent)', '#4796E3', '#9177C7', '#CA6673', '#7fd6c0'];  // body-appended -> :root token
  for (let i = 0; i < 16; i++) {
    const p = document.createElement('i');
    const ang = (Math.PI * 2 * i) / 16, dist = 90 + (i % 4) * 26;
    p.style.setProperty('--cx', Math.cos(ang) * dist + 'px');
    p.style.setProperty('--cy', Math.sin(ang) * dist + 'px');
    p.style.background = colors[i % colors.length];
    wrap.appendChild(p);
  }
  document.body.appendChild(wrap);
  setTimeout(() => wrap.remove(), 1000);
}


// ─────────────────────────────────────────────────────────────
// My Performance — per-staff page (any role). Reuses the gamification
// components (ring/level/avatar) on the shared engine. See
// docs/people_os_architecture.md.
// ─────────────────────────────────────────────────────────────
function renderMyPerf() {
  return `
  <div class="csw-shell" id="myperf">
    <svg width="0" height="0" style="position:absolute" aria-hidden="true"><defs>
      <linearGradient id="cswGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#4796E3"/><stop offset="50%" stop-color="#9177C7"/><stop offset="100%" stop-color="#CA6673"/>
      </linearGradient></defs></svg>
    <div class="csw-topbar">
      <button class="csw-back" id="myperf-back" aria-label="Home">‹</button>
      <div class="csw-grow"><div class="csw-title">My Performance</div><div class="csw-sub" id="myperf-sub">Loading…</div></div>
    </div>
    <div class="csw-body" id="myperf-body" style="display:block"><div class="csw-pulse">
      <div class="csw-card csw-skel" style="height:160px"></div>
      <div class="csw-card csw-skel" style="height:120px"></div>
      <div class="csw-card csw-skel" style="height:180px"></div>
    </div></div>
  </div>`;
}

async function bindMyPerf() {
  document.getElementById('myperf-back')?.addEventListener('click', () => { State.view = 'home'; render(); });
  let d;
  try { d = await api('/api/cs/me/performance'); }
  catch (e) {
    const b = document.getElementById('myperf-body');
    if (b) b.innerHTML = `<div class="csw-empty"><div class="csw-empty__m">!</div>${esc(e.message || e)}</div>`;
    return;
  }
  if (!d || !d.ok) {
    const b = document.getElementById('myperf-body');
    if (b) b.innerHTML = `<div class="csw-empty"><div class="csw-empty__m">○</div>${esc((d && d.error) || 'No performance data yet')}</div>`;
    return;
  }
  const ring = d.ring || {}, lvl = d.level || {}, st = d.streak || {}, team = d.team_goal || {}, share = d.my_share || {};
  const target = ring.target || 50;
  const sub = document.getElementById('myperf-sub');
  if (sub) sub.textContent = `${esc(d.name || '')}${d.role ? ' · ' + esc(d.role) : ''}`;
  const ev = (d.receipt && d.receipt.events) || [];
  // group receipt by behaviour for a "where points came from" list
  const byB = {};
  ev.forEach(e => { const k = e.behaviour_label || e.behaviour_code || 'Points'; byB[k] = (byB[k] || 0) + (e.points || 0); });
  const srcRows = Object.entries(byB).sort((a, b) => b[1] - a[1]).slice(0, 6);

  document.getElementById('myperf-body').innerHTML = `<div class="csw-pulse">
    <div class="csw-card csw-hero">
      <div class="csw-hero__row">
        ${cswRing(ring.points || 0, target)}
        <div class="csw-hero__meta">
          <div class="csw-hero__name">${esc(d.name || 'You')}</div>
          <div class="csw-level">
            ${lvl.title ? `<span class="csw-levelchip">Lv ${lvl.level} · ${esc(lvl.title)}</span>` : ''}
            ${lvl.next_at ? `<span class="csw-levelnext">${lvl.total_points}/${lvl.next_at} pts</span>` : ''}
          </div>
          <div class="csw-streak">${(st.streak_days || 0) > 0 ? `${cswFlame()} <b>${st.streak_days}-day</b> streak${st.freeze_available ? ` <span class="csw-freeze">${cswSnow()} freeze ready</span>` : ''}` : 'Start a streak today'}</div>
          <div class="csw-hero__hint">${ring.closed ? 'Ring closed today — nice.' : `${Math.max(0, target - (ring.points || 0))} points to close today.`}</div>
        </div>
      </div>
    </div>

    ${(() => {
      // My work — the job numbers (operator 2026-07-25: reps must see
      // median response time + conversion on their own page)
      const w = d.work || {}, r = w.response, c = w.conversion;
      if (!r && !(c && c.mine)) {
        // A rep with a quiet week used to get NOTHING here and conclude
        // the numbers were missing (exactly the confusion Gift had) —
        // say what the card is and when it fills in.
        return `<div class="csw-card">
        <div class="csw-card__h"><div class="csw-card__t">My work</div></div>
        <div class="csw-emptywork">No replies in the last 7 days yet \u2014 your median reply time and conversion appear here after your first customer replies.</div>
      </div>`;
      }
      const fmtS = (s) => s == null ? '—' : (s >= 60 ? `${Math.floor(s / 60)}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`);
      const m = (c && c.mine) || {};
      return `<div class="csw-card">
      <div class="csw-card__h"><div class="csw-card__t">My work</div><div class="csw-card__t">7d · conv 30d</div></div>
      <div class="csw-gauges">
        ${cswGauge(r && r.median_s != null ? Math.max(6, 100 - Math.min(100, r.median_s / 18)) : 0, 'Median reply', fmtS(r && r.median_s))}
        ${cswGauge(r && r.p90_s != null ? Math.max(6, 100 - Math.min(100, r.p90_s / 60)) : 0, 'Slowest 10%', fmtS(r && r.p90_s))}
        ${cswGauge(m.conv_rate_pct != null ? Math.min(100, m.conv_rate_pct * 2) : 0, 'My conversion', m.conv_rate_pct != null ? `${m.conv_rate_pct}%` : '—')}
        ${cswGauge(c && c.team_pct != null ? Math.min(100, c.team_pct * 2) : 0, 'Team conv', c && c.team_pct != null ? `${c.team_pct}%` : '—')}
      </div>
      <div class="csw-goalmeta">${r ? `${r.replies || 0} replies this week${r.slow ? ' · running slow — aim under 5m' : ''}` : ''}${m.threads ? ` · ${m.converted || 0} of ${m.threads} chats became orders${m.aov_ngn ? ` · avg ${cswNgn(m.aov_ngn)}` : ''}` : ''}</div>
      ${cswPayCoverage(c && c.coverage) ? `<div class="csw-goalmeta">${cswPayCoverage(c && c.coverage)}</div>` : ''}
    </div>`;
    })()}

    <div class="csw-card">
      <div class="csw-card__h"><div class="csw-card__t">This week</div><div class="csw-card__t">rank in ${esc(d.role || 'team')}</div></div>
      <div class="csw-gauges">
        ${cswGauge(Math.min(100, ((ring.points || 0) / target) * 100), 'Today', `${ring.points || 0}`)}
        ${cswGauge(lvl.into_pct || 0, 'To next lvl', `${Math.round(lvl.into_pct || 0)}%`)}
        ${cswGauge(d.rank ? Math.max(8, 100 - (d.rank - 1) / Math.max(1, d.peer_size || d.role_size) * 100) : 0, 'Rank', d.rank ? `#${d.rank}` : '—')}
        ${cswGauge(team.pct || 0, 'Team goal', `${Math.round(team.pct || 0)}%`)}
      </div>
    </div>

    ${share && share.points != null ? `<div class="csw-card">
      <div class="csw-card__h"><div class="csw-card__t">Your bonus share</div></div>
      <div class="csw-hero__name" style="font-size:24px">${share.payout_ngn != null ? cswNgn(share.payout_ngn) : (d.ngn_per_point ? cswNgn(Math.round((share.points || 0) * d.ngn_per_point)) : share.points + ' pts')}</div>
      <div class="csw-goalmeta">${share.points || 0} pts · ${share.share_pct || 0}% of the pool${d.ngn_per_point ? ` · ₦${d.ngn_per_point}/pt` : ' · pool not funded yet (projection)'}</div>
    </div>` : ''}

    ${srcRows.length ? `<div class="csw-card">
      <div class="csw-card__h"><div class="csw-card__t">Where your points came from · 7d</div></div>
      ${srcRows.map(([k, v]) => `<div class="csw-theme"><span>${esc(k)}</span><b style="color:var(--accent)">+${v}</b></div>`).join('')}
    </div>` : ''}

    ${team.target ? `<div class="csw-card">
      <div class="csw-card__h"><div class="csw-card__t">Team goal · this week</div><div class="csw-card__t">${team.points} / ${team.target}</div></div>
      <div class="csw-goalbar"><i style="width:${Math.min(100, team.pct || 0)}%"></i></div>
      <div class="csw-goalmeta">${team.hit ? 'Hit — everyone shares the win' : `${Math.round(team.pct || 0)}% there · win it together`}</div>
    </div>` : ''}
  </div>`;
}


// ─────────────────────────────────────────────────────────────
// People OS — HR Admin Dashboard (?v=hr) — 2026-06-07
// One cross-department surface over the ONE points engine: pulse,
// leaderboard, targets, pool + pay-gap detector, flight-risk/integrity
// flags, and a staff drill-down. Reuses the csw-* dark design language.
// ─────────────────────────────────────────────────────────────
let _hrState = { data: null, dept: 'all', days: 7 };

function renderHr() {
  return `
  <div class="csw-shell" id="hr">
    <svg width="0" height="0" style="position:absolute" aria-hidden="true"><defs>
      <linearGradient id="cswGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#4796E3"/><stop offset="50%" stop-color="#9177C7"/><stop offset="100%" stop-color="#CA6673"/>
      </linearGradient></defs></svg>
    <div class="csw-topbar">
      <button class="csw-back" id="hr-back" aria-label="Home">‹</button>
      <div class="csw-grow"><div class="csw-title">People · HR</div><div class="csw-sub" id="hr-sub">Loading…</div></div>
      <div class="hr-seg" id="hr-period" role="tablist">
        <button class="${_hrState.days === 7 ? 'on' : ''}" data-days="7">7d</button>
        <button class="${_hrState.days === 30 ? 'on' : ''}" data-days="30">30d</button>
      </div>
      <button class="hr-iconbtn" id="hr-flag-btn" title="Report friction" aria-label="Report friction">⚑</button>
      <button class="hr-iconbtn" id="hr-refresh" title="Refresh" aria-label="Refresh">↻</button>
    </div>
    <div class="csw-body" id="hr-body" style="display:block">${_hrSkeleton()}</div>
  </div>`;
}

function _hrSkeleton() {
  return `<div class="hr-wrap">
    <div class="hr-hero csw-skel" style="height:74px"></div>
    <div class="hr-cols">
      <div class="hr-col"><div class="csw-card csw-skel" style="height:360px"></div></div>
      <div class="hr-col">
        <div class="csw-card csw-skel" style="height:150px"></div>
        <div class="csw-card csw-skel" style="height:160px"></div>
      </div>
    </div></div>`;
}

async function bindHr() {
  document.getElementById('hr-back')?.addEventListener('click', () => { State.view = roleHome(); render(); });
  // topbar controls (bound once on the shell, survive body re-renders)
  document.querySelectorAll('#hr-period button').forEach(b => b.addEventListener('click', () => {
    _hrState.days = parseInt(b.getAttribute('data-days'), 10) || 7;
    document.querySelectorAll('#hr-period button').forEach(x => x.classList.toggle('on', x === b));
    _hrLoad();
  }));
  document.getElementById('hr-refresh')?.addEventListener('click', () => _hrLoad());
  document.getElementById('hr-flag-btn')?.addEventListener('click', () => openFrictionReport());
  _hrLoad();
}

async function _hrLoad() {
  const body = document.getElementById('hr-body');
  if (body && !_hrState.data) body.innerHTML = _hrSkeleton();
  let d;
  try { d = await api(`/api/people/overview?days=${_hrState.days}`); }
  catch (e) {
    if (body) body.innerHTML = `<div class="csw-empty"><div class="csw-empty__m">!</div>${esc(e.message || e)}</div>`;
    return;
  }
  if (!d || !d.ok) {
    if (body) body.innerHTML = `<div class="csw-empty"><div class="csw-empty__m">○</div>${esc((d && d.error) || 'No people data yet')}</div>`;
    return;
  }
  _hrState.data = d;
  const pulse = d.pulse || {}, onshift = pulse.on_shift || {};
  const sub = document.getElementById('hr-sub');
  if (sub) sub.textContent = `${pulse.headcount || 0} staff · ${onshift.count || 0} on shift`;
  if (body) body.innerHTML = _hrRender(d);
  _hrBind();
}

// the engine's risk signals → one number the manager's eye cannot miss
function _hrSignalCount(d) {
  const fr = ((d.flags || {}).flight_risk || {}).flags || [];
  const ig = ((d.flags || {}).integrity || {}).findings || [];
  const frs = (((d.friction || {}).summary) || {});
  return fr.length + ig.filter(f => f.severity === 'review').length +
         (frs.critical_open || 0) + (frs.high_open || 0);
}

function _hrRender(d) {
  const pulse = d.pulse || {}, onshift = pulse.on_shift || {};
  const cg = d.company_goal || {};
  const pool = d.pool || {}, pg = d.pay_gap || {};
  const board = d.leaderboard || [];
  const signals = _hrSignalCount(d);

  // ── TIER 1 — company-pulse hero strip (the designed first-hit) ──
  const poolFig = pool.pool_ngn ? cswNgn(pool.pool_ngn) : '—';
  const hero = `<div class="hr-hero">
    <button class="hr-kpi" data-jump="lb"><div class="hr-kpi__v">${onshift.count || 0}<span class="hr-kpi__u">/${pulse.headcount || 0}</span></div><div class="hr-kpi__l"><span class="hr-dot ${onshift.count ? 'live' : ''}"></span>On shift</div></button>
    <div class="hr-kpi"><div class="hr-kpi__v">${pulse.week_points || 0}</div><div class="hr-kpi__l">Points · ${_hrState.days}d</div></div>
    <button class="hr-kpi" data-jump="pool"><div class="hr-kpi__v">${poolFig}</div><div class="hr-kpi__l">${pool.pool_ngn ? 'Pool funded' : 'Pool unfunded'}</div></button>
    <button class="hr-kpi ${signals ? 'hr-kpi--alert' : ''}" data-jump="signals"><div class="hr-kpi__v">${signals}</div><div class="hr-kpi__l">${signals ? 'Need attention' : 'All clear'}</div></button>
    ${cg.configured ? `<div class="hr-kpi"><div class="hr-kpi__v">${Math.round(cg.pct || 0)}%</div><div class="hr-kpi__l">Company goal</div></div>` : ''}
  </div>`;

  // ── TIER 2 — action cards (float up; rendered first in the side column) ──
  const actionCards = `${_hrFlagsCard(d)}${_hrFrictionCard(d)}${_hrEquityCard(d)}`;
  // ── TIER 3 — reference: targets + pool/bonus ──
  const refCards = `${_hrTargetsCard(d)}${_hrPoolCard(d)}`;

  return `<div class="hr-wrap">
    ${hero}
    <div class="hr-cols">
      <div class="hr-col hr-col--main">${_hrLeaderboardCard(d)}</div>
      <div class="hr-col hr-col--side">${actionCards}${refCards}</div>
    </div>
  </div>`;
}

function _hrLeaderboardCard(d) {
  const board = d.leaderboard || [];
  const depts = Array.from(new Set(board.map(b => b.department || b.role || 'Other'))).sort();
  const onNow = new Set((((d.pulse || {}).on_shift || {}).on_shift || []).map(s => (s.name || '')));
  _hrState._onNow = onNow;
  return `<div class="csw-card hr-board" id="hr-lb-card">
    <div class="csw-card__h"><div class="csw-card__t">Leaderboard · ${_hrState.days}d</div><div class="csw-card__t">${board.length} staff</div></div>
    <div class="csw-chips" id="hr-deptchips">
      <button class="csw-chip ${_hrState.dept === 'all' ? 'on' : ''}" data-dept="all">All</button>
      ${depts.map(dp => `<button class="csw-chip ${_hrState.dept === dp ? 'on' : ''}" data-dept="${esc(dp)}">${esc(dp)}</button>`).join('')}
    </div>
    ${(d.pulse && d.pulse.roster_degraded) ? `<div class="hr-note">The staff list couldn't be read just now — this board shows only point earners, not the whole team.</div>` : ''}
    <div id="hr-lb">${_hrLeaderboardRows(board, _hrState.dept)}</div>
  </div>`;
}

function _hrLeaderboardRows(board, dept) {
  const rows = (dept && dept !== 'all') ? board.filter(b => (b.department || b.role || 'Other') === dept) : board;
  if (!rows.length) return `<div class="hr-empty"><div class="hr-empty__i">○</div>No one in this department has earned points yet.</div>`;
  const max = Math.max(1, ...rows.map(b => b.points || 0));
  const onNow = _hrState._onNow || new Set();
  return rows.map((b, i) => {
    const name = b.name || b.rep_id;
    const lvl = b.level || {};
    const top = i < 3;
    return `<button class="hr-rep${top ? ' hr-rep--top' : ''}" data-email="${esc(b.rep_id)}" data-staff-id="${esc(b.staff_id)}">
    <span class="hr-rep__rank">${i + 1}</span>
    ${cswAvatar(name, { size: 'sm', seed: b.rep_id, pres: onNow.has(name) ? 'on' : '' })}
    <div class="hr-rep__meta">
      <div class="hr-rep__name">${esc(name)}</div>
      <div class="hr-rep__role">${esc(b.department || b.role || '—')}${b.streak_days ? ` · ${cswFlame()}${b.streak_days}` : ''}</div>
      <div class="hr-rep__bar"><i style="width:${Math.round((b.points || 0) / max * 100)}%"></i></div>
    </div>
    <div class="hr-rep__pts"><b>${b.points || 0}</b><span>pts</span></div>
    <span class="hr-rep__chev">›</span>
  </button>`;
  }).join('');
}

// ── pay-gap equity strip (the marquee anti-pay-gap viz) ──
function _hrEquityCard(d) {
  const pg = d.pay_gap || {};
  if (pg.ok === false) {
    return `<div class="csw-card">
      <div class="csw-card__h"><div class="csw-card__t">Pay equity · 30d</div></div>
      <div class="hr-empty"><div class="hr-empty__i">!</div>The pay-equity read is unavailable right now — it will be back when the pool split heals.</div>
    </div>`;
  }
  const depts = (pg.departments || []).filter(x => (x.rep_count || 0) > 0);
  if (!depts.length) return '';
  const thr = pg.threshold || 0.6;
  const inBand = depts.filter(x => x.flag !== 'amber').length;
  const fundedNote = pg.funded ? '' : ' (by points — fund the pool to see ₦)';
  return `<div class="csw-card hr-card--action">
    <div class="csw-card__h"><div class="csw-card__t">Pay equity · 30d</div>
      <div class="hr-pill ${pg.any_amber ? 'hr-pill--warn' : 'hr-pill--ok'}">${inBand}/${depts.length} in band</div></div>
    <div class="hr-equity">
      ${depts.map(x => {
        const r = Math.max(0, Math.min(1, x.ratio_to_top || 0));
        const amber = x.flag === 'amber';
        return `<div class="hr-equity__row">
          <span class="hr-equity__name">${esc(x.department)}</span>
          <span class="hr-equity__track"><i class="${amber ? 'is-low' : ''}" style="width:${Math.round(r * 100)}%"></i><span class="hr-equity__thr" style="left:${Math.round(thr * 100)}%"></span></span>
          <span class="hr-equity__v">${pg.funded ? cswNgn(x.median) : (x.median + 'p')}</span>
        </div>`;
      }).join('')}
    </div>
    ${pg.any_amber
      ? `<div class="hr-note hr-note--warn">${esc(depts.filter(x => x.flag === 'amber').map(x => x.department).join(', '))} below the fair band — raise their pool_pct so critical roles aren't out-earned.</div>`
      : `<div class="hr-note">Every department within ${Math.round(thr * 100)}% of the top — pay is equitable${fundedNote}.</div>`}
  </div>`;
}

// ── flags: split retention-risk / integrity, danger-sorted ──
function _hrFlagsCard(d) {
  const fr = ((d.flags || {}).flight_risk || {}).flags || [];
  const ig = (((d.flags || {}).integrity || {}).findings || []).slice().sort((a, b) => (a.severity === 'review' ? -1 : 1) - (b.severity === 'review' ? -1 : 1));
  if (!fr.length && !ig.length) return '';
  return `<div class="csw-card hr-card--action hr-card--danger">
    <div class="csw-card__h"><div class="csw-card__t">Needs attention</div><div class="hr-pill hr-pill--warn">${fr.length + ig.length}</div></div>
    ${fr.length ? `<div class="hr-sub">Retention risk</div>` + fr.map(f => `<button class="hr-flag" data-email="${esc(f.rep_id)}">
      <div class="hr-flag__top"><b>${esc(f.name || f.rep_id)}</b><span class="hr-flag__tag hr-flag__tag--risk">flight-risk</span></div>
      <div class="hr-flag__d">${esc((f.reasons || []).join(' · '))}</div>
      <div class="hr-flag__d" style="color:var(--accent)">${esc(f.coach_nudge || '')}</div>
    </button>`).join('') : ''}
    ${ig.length ? `<div class="hr-sub">Integrity review</div>` + ig.map(f => `<div class="hr-flag">
      <div class="hr-flag__top"><b>${esc(f.name || f.rep_id)}</b><span class="hr-flag__tag hr-flag__tag--${f.severity === 'review' ? 'review' : 'watch'}">${esc(f.kind)}</span></div>
      <div class="hr-flag__d">${esc(f.detail || '')}</div>
    </div>`).join('') : ''}
  </div>`;
}

// ── inter-department friction (open issues + reliability rating) ──
function _hrFrictionCard(d) {
  const f = d.friction || {};
  const open = ((f.open || {}).reports) || [];
  const reliab = ((f.reliability || {}).departments) || [];
  const worst = reliab.filter(r => r.reliability < 85).slice(0, 4);
  if (!open.length && !worst.length) return '';
  return `<div class="csw-card hr-card--action">
    <div class="csw-card__h"><div class="csw-card__t">Friction · cross-dept</div>
      ${open.length ? `<div class="hr-pill hr-pill--warn">${open.length} open</div>` : `<div class="hr-pill hr-pill--ok">clear</div>`}</div>
    ${open.slice(0, 6).map(r => `<div class="hr-fric" data-fid="${esc(r.id)}">
      <div class="hr-fric__top">
        <span class="hr-sev hr-sev--${esc(r.severity)}">${esc(r.severity)}</span>
        <b>${esc(r.title)}</b>
      </div>
      <div class="hr-flag__d">${esc(r.reporter_department || '?')} → ${esc(r.against_department || '?')}${r.related_request ? ' · ' + esc(r.related_request) : ''}${r.age_hours != null ? ` · ${r.age_hours < 48 ? Math.round(r.age_hours) + 'h' : Math.round(r.age_hours / 24) + 'd'} open` : ''}</div>
      <div class="hr-fric__act">
        <button class="hr-mini" data-fact="task" data-fid="${esc(r.id)}">Make task</button>
        <button class="hr-mini" data-fact="resolve" data-fid="${esc(r.id)}">Resolve</button>
      </div>
    </div>`).join('')}
    ${worst.length ? `<div class="hr-sub">Reliability</div><div class="hr-reliab">${worst.map(r => `<span class="hr-reliab__d hr-reliab--${esc(r.rating)}">${esc(r.department)} <b>${r.reliability}</b></span>`).join('')}</div>` : ''}
  </div>`;
}

function _hrTargetsCard(d) {
  const trows = ((d.targets || {}).rows) || [];
  if (!trows.length) return `<div class="csw-card"><div class="csw-card__h"><div class="csw-card__t">Targets</div></div><div class="hr-empty"><div class="hr-empty__i">◷</div>Targets compute once stores have order history this period.</div></div>`;
  return `<div class="csw-card">
    <div class="csw-card__h"><div class="csw-card__t">Targets · attainment</div></div>
    ${trows.map(r => `<div class="hr-target">
      <div class="hr-target__top"><b>${esc(r.team)}</b><span>${esc(r.target_label || '')}</span></div>
      <div class="csw-goalbar"><i class="${r.on_track ? '' : 'is-behind'}" style="width:${Math.min(100, r.attainment_pct || 0)}%"></i></div>
      <div class="csw-goalmeta">${Math.round(r.attainment_pct || 0)}%${r.on_track ? ' · on track' : ''}${r.unlock_pct != null ? ` · unlock ${Math.round(r.unlock_pct)}%` : ''} · ${esc(r.detail || '')}</div>
    </div>`).join('')}
  </div>`;
}

function _hrPoolCard(d) {
  const pool = d.pool || {};
  const cs = pool.company_slice || {};
  if (pool.ok === false || pool.split_refused) {
    // A refused split must never wear the funded card (vet round 2):
    // a failed goal/staff read used to render a live Draft button over
    // a "₦0 each" slice.
    return `<div class="csw-card" id="hr-pool-card">
      <div class="csw-card__h"><div class="csw-card__t">Bonus pool</div></div>
      <div class="hr-empty"><div class="hr-empty__i">!</div>The pool split couldn't be computed just now (a source read failed). Nothing is wrong with the pool itself — refresh in a minute; drafting is paused until the read heals.</div>
    </div>`;
  }
  if (!pool.pool_ngn) {
    return `<div class="csw-card" id="hr-pool-card">
      <div class="csw-card__h"><div class="csw-card__t">Bonus pool</div></div>
      <div class="hr-empty"><div class="hr-empty__i">₦</div>Fund a monthly pool so points convert to real ₦.
        <button class="csw-mini" id="hr-fund-btn" style="margin-top:10px">How to fund →</button></div>
    </div>`;
  }
  return `<div class="csw-card" id="hr-pool-card">
    <div class="csw-card__h"><div class="csw-card__t">Bonus pool · 30d</div><div class="csw-card__t">${cswNgn(pool.pool_ngn)} · ₦${pool.ngn_per_point || '—'}/pt</div></div>
    ${cs.configured ? `<div class="hr-note">Shared "we win together" slice: ${cs.hit ? `<b>${cswNgn(cs.shared_each_ngn)}</b> each (goal hit)` : `${Math.round(cs.pct || 0)}% to goal — unlocks ${cswNgn(Math.round((cs.shared_total_ngn || (pool.pool_ngn * (cs.pool_pct || 0))) / Math.max(1, cs.active_staff || 1)))} each at 100%`}</div>` : ''}
    <button class="csw-confirm" id="hr-bonus-btn" style="width:100%;margin-top:10px">Draft bonus run →</button>
  </div>`;
}

function _hrBind() {
  document.querySelectorAll('#hr-deptchips .csw-chip').forEach(c => {
    c.addEventListener('click', () => {
      _hrState.dept = c.getAttribute('data-dept');
      document.querySelectorAll('#hr-deptchips .csw-chip').forEach(x => x.classList.toggle('on', x === c));
      const lb = document.getElementById('hr-lb');
      if (lb) lb.innerHTML = _hrLeaderboardRows(_hrState.data.leaderboard || [], _hrState.dept);
      _hrBindRows();
    });
  });
  _hrBindRows();
  document.getElementById('hr-bonus-btn')?.addEventListener('click', openBonusSheet);
  document.getElementById('hr-fund-btn')?.addEventListener('click', openFundHelp);
  // hero KPI jump-to
  document.querySelectorAll('.hr-kpi[data-jump]').forEach(k => k.addEventListener('click', () => {
    const t = k.getAttribute('data-jump');
    const el = t === 'lb' ? document.getElementById('hr-lb-card')
      : t === 'pool' ? document.getElementById('hr-pool-card')
      : document.querySelector('.hr-card--danger') || document.querySelector('.hr-card--action');
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }));
  // friction actions — resolve gets an INLINE reason input (confirm/prompt
  // are killed, operator law #1149; the resolver earns points so the
  // resolution note is required, typed in place).
  document.querySelectorAll('.hr-mini[data-fact]').forEach(b => b.addEventListener('click', (e) => {
    e.stopPropagation();
    const act = b.getAttribute('data-fact');
    if (act === 'resolve') { _hrFrictionResolveInline(b); return; }
    _hrFrictionAction(act, b.getAttribute('data-fid'));
  }));
}

function _hrFrictionResolveInline(btn) {
  const fid = btn.getAttribute('data-fid');
  if (!fid || btn.dataset.inline) return;
  btn.dataset.inline = '1';
  const wrap = document.createElement('span');
  wrap.className = 'hr-resolve-inline';
  wrap.style.cssText = 'display:inline-flex;gap:6px;align-items:center';
  wrap.innerHTML = '<input type="text" class="csw-input" ' +
    'placeholder="How was it resolved? (required)" maxlength="300" ' +
    'style="font-size:11px;padding:4px 8px;width:180px">' +
    '<button type="button" class="hr-mini">Resolve</button>';
  btn.style.display = 'none';
  btn.after(wrap);
  const input = wrap.querySelector('input');
  const go = wrap.querySelector('button');
  const restore = () => { wrap.remove(); btn.style.display = ''; delete btn.dataset.inline; };
  const submit = async () => {
    const resolution = (input.value || '').trim();
    if (!resolution) { input.focus(); return; }
    go.disabled = true; go.textContent = '…';
    try {
      const d = await api(`/api/people/friction/${fid}/resolve`, {
        method: 'POST', body: JSON.stringify({ resolution }) });
      showToast(d && d.ok ? 'Resolved' : ((d && d.error) || 'Failed'));
      if (State.view === 'hr') _hrLoad(); else restore();
    } catch (err) { showToast(err.message || String(err)); restore(); }
  };
  go.addEventListener('click', submit);
  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') submit();
    if (ev.key === 'Escape') restore();
  });
  input.focus();
}

function _hrBindRows() {
  document.querySelectorAll('#hr-lb .hr-rep, #hr .hr-flag[data-email]').forEach(r =>
    r.addEventListener('click', () => openHrStaff(_hrRowIdentity(r))));
}

// The identity a team row opens its profile by: the email, else (a staffer
// with no email) the staff id roster_board carries. '' when it has neither;
// openHrStaff then says so in the sheet, never a silent tap.
function _hrRowIdentity(r) {
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const email = (r.getAttribute('data-email') || '').trim();
  const sid = (r.getAttribute('data-staff-id') || '').trim();
  if (email.includes('@')) return email;
  return uuid.test(sid) ? sid : uuid.test(email) ? email : '';
}

function _hrPts(n) {
  // signed points chip — a clawback must read "-5", never "+-5"
  const v = n || 0;
  return `${v >= 0 ? '+' : ''}${v}`;
}

function _hrReceiptCard(rc) {
  // "How they got their points" — ALWAYS rendered on the staff expand
  // (operator, 2026-08-02). Zero events is an answer too, not a missing
  // card: a 0-point ring with no explanation reads as broken.
  const ev = rc.events || [];
  // No total means the points could not be counted (past 200 events, or a
  // failed read): show a dash, never "+0" or "no points".
  const noTotal = rc.state === 'partial' || rc.total_points == null;
  if (!ev.length && noTotal) {
    return `<div class="csw-card">
      <div class="csw-card__h"><div class="csw-card__t">Where the points came from · ${rc.days || 7}d</div><div class="csw-card__t">—</div></div>
      <div class="csw-goalmeta">Their points can't be read right now.</div>
    </div>`;
  }
  if (!ev.length) {
    return `<div class="csw-card">
      <div class="csw-card__h"><div class="csw-card__t">Where the points came from · ${rc.days || 7}d</div><div class="csw-card__t">0</div></div>
      <div class="csw-goalmeta">No points in the last ${rc.days || 7} days — nothing came in from tasks, sales or the desk.</div>
    </div>`;
  }
  // grouped first: the mix at a glance (chores vs sales vs desk), then the
  // freshest lines. 78 raw chore rows tell a manager nothing; 4 grouped
  // rows tell the whole story.
  const byB = {};
  ev.forEach(e => {
    const k = e.behaviour_label || e.behaviour_code || 'Points';
    const g = byB[k] = byB[k] || { pts: 0, n: 0 };
    g.pts += (e.points || 0); g.n += 1;
  });
  const grouped = Object.entries(byB).sort((a, b) => b[1].pts - a[1].pts).slice(0, 6)
    .map(([k, g]) => `<div class="csw-theme"><span>${esc(k)}${g.n > 1 ? ` × ${g.n}` : ''}</span><b>${_hrPts(g.pts)}</b></div>`).join('');
  const recent = ev.slice(0, 5)
    .map(e => `<div class="csw-theme"><span>${e.occurred_at ? String(e.occurred_at).slice(5, 10) + ' · ' : ''}${esc(e.behaviour_label || e.behaviour_code || '')}${e.notes ? ` — ${esc(String(e.notes).slice(0, 60))}` : ''}</span><b>${_hrPts(e.points)}</b></div>`).join('');
  return `<div class="csw-card">
    <div class="csw-card__h"><div class="csw-card__t">Where the points came from · ${rc.days || 7}d</div><div class="csw-card__t">${noTotal ? '—' : _hrPts(rc.total_points)}</div></div>
    ${grouped}
    <div class="csw-goalmeta" style="margin-top:8px">Latest</div>
    ${recent}
    ${noTotal ? `<div class="csw-goalmeta">Too many points to count here. These are the latest ${ev.length}; the ${rc.days || 7}-day total isn't shown.</div>`
      : ev.length > 5 ? `<div class="csw-goalmeta">${ev.length} events counted in the ${rc.days || 7}-day total above</div>` : ''}
  </div>`;
}

async function openHrStaff(email) {
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Staff</div><button class="csw-x">✕</button></div>
    <div id="hr-staff-body"><div class="csw-card csw-skel" style="height:160px"></div></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const say = (t) => { ov.querySelector('#hr-staff-body').innerHTML = `<div class="csw-empty">${esc(t)}</div>`; };
  if (!email) { say("This person's profile can't be opened here."); return; }
  let d;
  try { d = await api('/api/people/staff/' + encodeURIComponent(email)); }
  catch (e) {
    const s = e && e.status;
    say(s === 403 ? 'Outside your stores.' : (s === 400 || s === 404) ? "This person's profile can't be opened here." : "Can't be read right now.");
    return;
  }
  if (!d || !d.ok) { say("Can't be read right now."); return; }
  const ring = d.ring || {}, lvl = d.level || {}, st = d.streak || {}, pb = d.personal_best || {}, hr = d.hr || {}, share = d.my_share || {};
  // A personal best that could not be read is withheld, never shown as a zero best.
  const pbOff = pb.ok === false || pb.state === 'unavailable';
  // rc: the points receipt (explain_recent_points) — already on the wire,
  // never rendered before wave-3. The exact question the award box below
  // asks a manager to judge.
  const rc = d.receipt || {};
  const target = ring.target || 50;
  const isManager = ['manager', 'admin', 'owner'].includes(State.member?.role || '');
  const tenure = hr.tenure_days != null ? (hr.tenure_days >= 365 ? (hr.tenure_days / 365).toFixed(1) + 'y' : hr.tenure_days + 'd') : '—';
  ov.querySelector('#hr-staff-body').innerHTML = `
    <div class="csw-card csw-hero"><div class="csw-hero__row">
      ${cswRing(ring.points || 0, target)}
      <div class="csw-hero__meta">
        <div class="csw-hero__name">${esc(d.name || email)}</div>
        <div class="csw-level">${lvl.title ? `<span class="csw-levelchip">Lv ${lvl.level} · ${esc(lvl.title)}</span>` : ''}</div>
        <div class="csw-streak">${(st.streak_days || 0) > 0 ? `${cswFlame()} ${st.streak_days}-day streak` : 'No active streak'}</div>
        <div class="csw-hero__hint">${esc(d.role || '')}${d.department ? ' · ' + esc(d.department) : ''}${d.rank ? ` · #${d.rank} in dept` : ''}</div>
      </div>
    </div></div>
    <div class="csw-card">
      <div class="csw-card__h"><div class="csw-card__t">Personal best</div></div>
      <div class="csw-gauges">${pbOff ? `
        ${cswGauge(0, 'Best day', '—')}
        ${cswGauge(0, 'This wk', '—')}
        ${cswGauge(0, 'Best wk', '—')}` : `
        ${cswGauge(100, 'Best day', `${pb.best_day_points || 0}`)}
        ${cswGauge(pb.best_week_points ? Math.min(100, (pb.this_week_points || 0) / pb.best_week_points * 100) : 0, 'This wk', `${pb.this_week_points || 0}`)}
        ${cswGauge(0, 'Best wk', `${pb.best_week_points || 0}`)}`}
      </div>
      ${pbOff ? `<div class="csw-goalmeta">Personal best can't be read right now.</div>` : ''}
      ${pb.beating_best_week ? `<div class="csw-goalmeta" style="color:var(--accent)">Beating their best week</div>` : ''}
    </div>
    ${_hrReceiptCard(rc)}
    ${isManager ? `<div class="csw-card">
      <div class="csw-card__h"><div class="csw-card__t">HR</div></div>
      <div class="csw-theme"><span>Salary</span><b>${hr.monthly_salary != null ? cswNgn(hr.monthly_salary) : '—'}</b></div>
      <div class="csw-theme"><span>Tenure</span><b>${tenure}</b></div>
      <div class="csw-theme"><span>Status</span><b>${esc(hr.status || '—')}</b></div>
      ${share && share.payout_ngn != null ? `<div class="csw-theme"><span>Bonus share</span><b>${cswNgn(share.payout_ngn)}</b></div>` : ''}
    </div>
    <div class="csw-card">
      <div class="csw-card__h"><div class="csw-card__t">Adjust points</div></div>
      <input class="csw-search" id="hr-award-pts" type="number" placeholder="points (negative = clawback)" style="margin-bottom:8px"/>
      <input class="csw-search" id="hr-award-reason" placeholder="reason (required, audited)" style="margin-bottom:8px"/>
      <button class="csw-confirm" id="hr-award-go" data-email="${esc(email)}">Apply (confirm)</button>
      <div class="csw-goalmeta" id="hr-award-msg"></div>
    </div>` : ''}`;
  ov.querySelector('#hr-award-go')?.addEventListener('click', () => _hrAwardSubmit(ov, email));
}

async function _hrAwardSubmit(ov, email) {
  const pts = parseInt(ov.querySelector('#hr-award-pts')?.value || '0', 10);
  const reason = (ov.querySelector('#hr-award-reason')?.value || '').trim();
  const msg = ov.querySelector('#hr-award-msg');
  if (!pts || !reason) { if (msg) msg.textContent = 'Points (non-zero) + reason required.'; return; }
  const btn = ov.querySelector('#hr-award-go'); if (btn) { btn.disabled = true; btn.textContent = 'Applying…'; }
  // Two-phase confirm (wave-1 integrity 2026-07-26): the server has always
  // implemented preview→confirm; this client used to post confirm:true on
  // the FIRST tap, silently bypassing its own gate. First tap previews,
  // second tap (armed) confirms — parity with the bonus flow.
  const armed = btn?.dataset.armed === '1';
  try {
    const d = await api('/api/people/award', { method: 'POST', body: JSON.stringify({ email, points: pts, reason, confirm: armed }) });
    if (d && d.requires_confirm && !armed) {
      if (btn) { btn.disabled = false; btn.dataset.armed = '1'; btn.textContent = 'Tap again to confirm'; }
      if (msg) msg.textContent = d.message || `Confirm: ${pts > 0 ? 'award' : 'claw back'} ${Math.abs(pts)} pts?`;
      return;
    }
    if (d && d.ok) { showToast(`${pts > 0 ? 'Awarded' : 'Clawed back'} ${Math.abs(pts)} pts`); ov.remove(); if (State.view === 'hr') bindHr(); }
    else { if (msg) msg.textContent = (d && d.error) || 'Failed'; if (btn) { btn.disabled = false; btn.dataset.armed = ''; btn.textContent = 'Apply (confirm)'; } }
  } catch (e) { if (msg) msg.textContent = e.message || String(e); if (btn) { btn.disabled = false; btn.dataset.armed = ''; btn.textContent = 'Apply (confirm)'; } }
}

function openBonusSheet() {
  const now = new Date();
  const ms = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const me = now.toISOString().slice(0, 10);
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Draft bonus run</div><button class="csw-x">✕</button></div>
    <div class="csw-goalmeta" style="margin-bottom:8px">Turns the gamification pool into a DRAFT payroll run. Preview first; nothing is paid without separate approval.</div>
    <label class="hr-lbl">Period start<input class="csw-search" type="date" id="hr-bonus-start" value="${ms}"/></label>
    <label class="hr-lbl">Period end<input class="csw-search" type="date" id="hr-bonus-end" value="${me}"/></label>
    <button class="csw-confirm" id="hr-bonus-preview" style="margin-top:8px">Preview</button>
    <div id="hr-bonus-out" style="margin-top:10px"></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  ov.querySelector('#hr-bonus-preview').addEventListener('click', () => _hrBonus(ov, false));
}

async function _hrBonus(ov, confirm) {
  const period_start = ov.querySelector('#hr-bonus-start')?.value;
  const period_end = ov.querySelector('#hr-bonus-end')?.value;
  const out = ov.querySelector('#hr-bonus-out');
  if (out) out.innerHTML = `<div class="csw-goalmeta">Working…</div>`;
  try {
    const d = await api('/api/people/payroll/close', { method: 'POST', body: JSON.stringify({ period_start, period_end, confirm }) });
    if (!d || !d.ok) { if (out) out.innerHTML = `<div class="csw-goalmeta">${esc((d && d.error) || 'Failed')}</div>`; return; }
    if (d.confirmed) { showToast('Draft bonus run created'); ov.remove(); return; }
    const lines = d.lines || [];
    out.innerHTML = `
      <div class="csw-theme"><span>${d.staff_count} staff</span><b>${cswNgn(d.total_bonus_ngn)}</b></div>
      ${lines.slice(0, 8).map(l => `<div class="csw-theme"><span>${esc(l.name || l.email)}</span><b>${cswNgn(l.bonus_ngn)}</b></div>`).join('')}
      ${(d.unmatched || []).length ? `<div class="csw-goalmeta" style="color:#E3A047">${d.unmatched.length} unmatched rep(s) skipped (no staff row).</div>` : ''}
      <button class="csw-confirm" id="hr-bonus-confirm" style="margin-top:10px">Create draft run</button>`;
    out.querySelector('#hr-bonus-confirm').addEventListener('click', () => _hrBonus(ov, true));
  } catch (e) { if (out) out.innerHTML = `<div class="csw-goalmeta">${esc(e.message || e)}</div>`; }
}

function openFundHelp() {
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Fund the bonus pool</div><button class="csw-x">✕</button></div>
    <div class="csw-goalmeta">Points convert to ₦ once you set a monthly pool. It's a share of the margin gamified actions generate — self-funding, capped, never a fixed cost.</div>
    <div class="hr-note" style="margin-top:10px">Set in <b>gamification_config</b>: <code>monthly_pool_ngn</code> (start ~₦300–400k), optional per-dept <code>dept_pools</code>, and <code>company_goal {metric,target,pool_pct}</code> for the shared "we win together" slice. Tunable live — no deploy.</div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
}

// Friction Log — any staff reports inter-department friction / a defect / a blocker.
function openFrictionReport() {
  const depts = ['Sales', 'Customer Service', 'Inventory', 'Logistics', 'Marketing', 'Production', 'Operations'];
  const cats = ['delay', 'defect', 'quality', 'process', 'communication', 'resource', 'safety', 'other'];
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Report friction</div><button class="csw-x">✕</button></div>
    <div class="csw-goalmeta" style="margin-bottom:10px">Surfaced problems get fixed. Reporting is never held against you — Ops + the owner see this and make the system fluid.</div>
    <input class="csw-search" id="fr-title" placeholder="What happened? (e.g. Caps for the shoot arrived late)" style="margin-bottom:8px"/>
    <div class="hr-row2">
      <label class="hr-lbl">Which dept?<select class="csw-search" id="fr-against"><option value="">—</option>${depts.map(x => `<option>${x}</option>`).join('')}</select></label>
      <label class="hr-lbl">Type<select class="csw-search" id="fr-cat">${cats.map(x => `<option>${x}</option>`).join('')}</select></label>
    </div>
    <div class="hr-row2">
      <label class="hr-lbl">Severity<select class="csw-search" id="fr-sev"><option>low</option><option selected>medium</option><option>high</option><option>critical</option></select></label>
      <label class="hr-lbl">Request<input class="csw-search" id="fr-req" placeholder="e.g. caps for shoot"/></label>
    </div>
    <div class="hr-row2">
      <label class="hr-lbl">From<input class="csw-search" id="fr-from" placeholder="Warehouse"/></label>
      <label class="hr-lbl">To<input class="csw-search" id="fr-to" placeholder="Content Creation"/></label>
    </div>
    <textarea class="csw-search" id="fr-desc" placeholder="What was the impact / what should change?" rows="2" style="margin-bottom:8px"></textarea>
    <button class="csw-confirm" id="fr-submit">Log it</button>
    <div class="csw-goalmeta" id="fr-msg"></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  ov.querySelector('#fr-submit').addEventListener('click', async () => {
    const title = (ov.querySelector('#fr-title').value || '').trim();
    const msg = ov.querySelector('#fr-msg');
    if (!title) { msg.textContent = 'A short title is required.'; return; }
    const btn = ov.querySelector('#fr-submit'); btn.disabled = true; btn.textContent = 'Logging…';
    try {
      const d = await api('/api/people/friction', { method: 'POST', body: JSON.stringify({
        title, against_department: ov.querySelector('#fr-against').value || null,
        category: ov.querySelector('#fr-cat').value, severity: ov.querySelector('#fr-sev').value,
        related_request: ov.querySelector('#fr-req').value || null,
        location_from: ov.querySelector('#fr-from').value || null,
        location_to: ov.querySelector('#fr-to').value || null,
        description: ov.querySelector('#fr-desc').value || null,
      }) });
      if (d && d.ok) { showToast('Friction logged — Ops will see it'); ov.remove(); if (State.view === 'hr') _hrLoad(); }
      else { msg.textContent = (d && d.error) || 'Failed'; btn.disabled = false; btn.textContent = 'Log it'; }
    } catch (e) { msg.textContent = e.message || String(e); btn.disabled = false; btn.textContent = 'Log it'; }
  });
}

async function _hrFrictionAction(act, fid) {
  if (!fid) return;
  try {
    if (act === 'task') {
      const d = await api(`/api/people/friction/${fid}/task`, { method: 'POST', body: '{}' });
      showToast(d && d.ok ? 'Fix-task created' : ((d && d.error) || 'Failed'));
    }
    // 'resolve' is handled by _hrFrictionResolveInline — window.prompt is dead.
    if (State.view === 'hr') _hrLoad();
  } catch (e) { showToast(e.message || String(e)); }
}

// ─────────────────────────────────────────────────────────────
// Generative UI — InteractiveWidget engine (2026-06-06)
//
// EON emits JSON code blocks shaped like:
//   ```json
//   { "component": "InteractiveWidget", "type": "calculator",
//     "title": "...", "config": {...} }
//   ```
// The chat renderer extracts those blocks, replaces them with
// inline interactive components, and leaves the surrounding prose
// untouched. Types supported in v1: calculator, chart, form.
// See docs in renderInteractiveWidget below for the per-type schema.
// ─────────────────────────────────────────────────────────────

// Detect + extract InteractiveWidget JSON code blocks. Returns
// { segments: [{kind:'text'|'widget', value:string|object}] }.
//
// Schema tolerance — Gemini's protocol description and its example
// payload disagree about whether the type/title/config fields live
// at the root or inside a `props` wrapper. Both forms are accepted:
//   FLAT:    { component, type, title, config }
//   WRAPPED: { component, props: { type, title, config } }
// Normalised to the flat shape before being passed to the renderer.
function normaliseInteractiveWidget(raw) {
  if (!raw || raw.component !== 'InteractiveWidget') return null;
  // If the agent emitted the `props`-wrapped form, flatten it.
  if (raw.props && typeof raw.props === 'object'
      && (raw.props.type || raw.props.config)) {
    return {
      component: 'InteractiveWidget',
      type:      raw.props.type || raw.type,
      title:     raw.props.title || raw.title || '',
      config:    raw.props.config || raw.config || {},
    };
  }
  return {
    component: 'InteractiveWidget',
    type:      raw.type,
    title:     raw.title || '',
    config:    raw.config || {},
  };
}

function extractInteractiveWidgets(text) {
  if (!text || !text.includes('"InteractiveWidget"')) {
    return { segments: [{ kind: 'text', value: text || '' }], hasWidget: false };
  }
  const fence = /```(?:json)?\s*\n?([\s\S]*?)```/g;
  const segments = [];
  let last = 0;
  let hasWidget = false;
  let m;
  while ((m = fence.exec(text)) !== null) {
    const json = m[1].trim();
    if (!json.includes('"InteractiveWidget"')) continue;
    let parsed;
    try { parsed = JSON.parse(json); }
    catch (_) { continue; }
    const widget = normaliseInteractiveWidget(parsed);
    if (widget) {
      if (m.index > last) {
        segments.push({ kind: 'text', value: text.slice(last, m.index) });
      }
      segments.push({ kind: 'widget', value: widget });
      hasWidget = true;
      last = m.index + m[0].length;
    }
  }
  if (last < text.length) {
    segments.push({ kind: 'text', value: text.slice(last) });
  }
  if (!hasWidget) {
    return { segments: [{ kind: 'text', value: text }], hasWidget: false };
  }
  return { segments, hasWidget: true };
}

// Safe arithmetic eval: allowlist digits + operators + identifiers,
// then build a Function. Prevents arbitrary code execution while
// letting EON-emitted expressions like "monthly_volume * 0.05" run.
function safeEvalCalc(expr, vars) {
  if (typeof expr !== 'string') return null;
  if (!/^[\d\w\s\.\+\-\*\/\(\)\,]+$/.test(expr)) return null;
  // Allowlist identifiers — must each exist in vars.
  const idents = (expr.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [])
                       .filter(i => !/^\d/.test(i));
  for (const i of idents) {
    if (!(i in vars) && !['Math'].includes(i)) return null;
  }
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(...Object.keys(vars), `return (${expr});`);
    const out = fn(...Object.values(vars));
    return (typeof out === 'number' && isFinite(out)) ? out : null;
  } catch (_) { return null; }
}

const CALC_PALETTE = {
  blue:   '#6ba3ff',
  red:    '#ff6b6b',
  green:  '#4dd6a0',
  purple: '#b8a4ff',
  orange: '#ffb347',
  yellow: '#ffd84d',
  pink:   '#ff8fb1',
};

// Render one InteractiveWidget. Returns HTML string; the caller is
// responsible for binding inputs after insertion. Each widget gets
// a unique id (passed in `wid`) so the binders can find inputs.
function renderInteractiveWidget(widget, wid) {
  const t = (widget.type || '').toLowerCase();
  const title = widget.title || 'Widget';
  const typeLabel = t || 'widget';
  const head = `
    <div class="eon-widget__head">
      <div class="eon-widget__title">${esc(title)}</div>
      <div class="eon-widget__type">${esc(typeLabel)}</div>
    </div>`;
  if (t === 'calculator') {
    return `<div class="eon-widget" data-wid="${esc(wid)}">${head}
      ${renderCalculatorBody(widget.config || {}, wid)}
    </div>`;
  }
  if (t === 'chart') {
    return `<div class="eon-widget" data-wid="${esc(wid)}">${head}
      ${renderChartBody(widget.config || {}, wid)}
    </div>`;
  }
  if (t === 'form') {
    return `<div class="eon-widget" data-wid="${esc(wid)}">${head}
      ${renderFormBody(widget.config || {}, wid)}
    </div>`;
  }
  return `<div class="eon-widget" data-wid="${esc(wid)}">${head}
    <div style="font-size:12px; color:var(--ink-mute);">Unsupported widget type: ${esc(typeLabel)}</div>
  </div>`;
}

function renderCalculatorBody(config, wid) {
  const inputs = config.inputs || [];
  const providers = config.providers || [];
  const inputsHtml = inputs.map((inp, i) => {
    const id = inp.id || `inp${i}`;
    const def = (inp.defaultValue != null) ? inp.defaultValue : (inp.min || 0);
    if (inp.type === 'slider') {
      return `<div class="eon-widget__input">
        <div class="eon-widget__input-head">
          <div class="eon-widget__label">${esc(inp.label || id)}</div>
          <div class="eon-widget__value" id="${esc(wid)}-val-${esc(id)}">${def.toLocaleString()}</div>
        </div>
        <input class="eon-widget__slider" type="range"
               data-wid="${esc(wid)}" data-input-id="${esc(id)}"
               min="${inp.min || 0}" max="${inp.max || 100}" value="${def}"
               step="${inp.step || 1}" />
      </div>`;
    }
    return `<div class="eon-widget__input">
      <div class="eon-widget__label">${esc(inp.label || id)}</div>
      <input class="eon-widget__text-input" type="${inp.type || 'text'}"
             data-wid="${esc(wid)}" data-input-id="${esc(id)}" value="${esc(def)}" />
    </div>`;
  }).join('');
  const resultsHtml = providers.map((p, i) => {
    const color = CALC_PALETTE[p.color] || 'var(--ink-mute)';
    const cls = `eon-widget__result-row ${p.highlight ? 'eon-widget__result-row--highlight' : ''}`;
    return `<div class="${cls}" data-prov-idx="${i}">
      <div class="eon-widget__result-name">
        <div class="eon-widget__result-dot" style="background:${color};"></div>
        ${esc(p.name)}
      </div>
      <div>
        <div class="eon-widget__result-val" id="${esc(wid)}-result-${i}">—</div>
      </div>
    </div>`;
  }).join('');
  return `<div data-widget-body="${esc(wid)}">${inputsHtml}
    <div class="eon-widget__results" data-widget-results="${esc(wid)}">${resultsHtml}</div>
  </div>`;
}

function renderChartBody(config, wid) {
  const bars = config.bars || [];
  const max = Math.max(...bars.map(b => Number(b.value) || 0), 1);
  const barsHtml = bars.map((b, i) => {
    const h = (Number(b.value) / max) * 100;
    const color = CALC_PALETTE[b.color] || 'var(--accent)';
    const valLabel = b.valueLabel || b.value;
    return `<div class="eon-widget__bar" style="height:${h}%; background:linear-gradient(180deg, ${color}, ${color}88);"
                 title="${esc(b.label || '')}: ${esc(String(valLabel))}">
      <div class="eon-widget__bar-val">${esc(String(valLabel))}</div>
      <div class="eon-widget__bar-label">${esc(b.label || '')}</div>
    </div>`;
  }).join('');
  return `<div class="eon-widget__chart" style="padding-bottom:30px;">${barsHtml}</div>`;
}

function renderFormBody(config, wid) {
  const fields = config.fields || [];
  const submitLabel = config.submitLabel || 'Submit';
  const fieldsHtml = fields.map((f, i) => {
    const id = f.id || `f${i}`;
    return `<div class="eon-widget__field">
      <label class="eon-widget__label">${esc(f.label || id)}</label>
      <input class="eon-widget__text-input" type="${f.type || 'text'}"
             data-form-field="${esc(id)}" placeholder="${esc(f.placeholder || '')}" />
    </div>`;
  }).join('');
  return `<form class="eon-widget__form" data-form-wid="${esc(wid)}">
    ${fieldsHtml}
    <button type="submit" class="eon-widget__submit">${esc(submitLabel)}</button>
  </form>`;
}

// Bind sliders for a calculator widget: on input, re-compute every
// provider's calculation and update the displayed value.
function bindInteractiveWidget(rootEl) {
  if (!rootEl) return;
  const wid = rootEl.dataset.wid;
  const sliders = rootEl.querySelectorAll('input[data-input-id]');
  const widget = (State.widgets || {})[wid];
  if (!widget) return;
  const config = widget.config || {};
  const providers = config.providers || [];
  const recompute = () => {
    const vars = {};
    sliders.forEach(s => {
      const v = Number(s.value);
      vars[s.dataset.inputId] = isFinite(v) ? v : 0;
      const valEl = document.getElementById(`${wid}-val-${s.dataset.inputId}`);
      if (valEl) valEl.textContent = v.toLocaleString();
    });
    providers.forEach((p, i) => {
      const out = safeEvalCalc(p.calculation || '', vars);
      const el = document.getElementById(`${wid}-result-${i}`);
      if (el) {
        if (out == null) {
          el.textContent = '—';
        } else {
          const unit = p.unit || '$';
          el.textContent = unit + Math.round(out).toLocaleString();
        }
      }
    });
  };
  sliders.forEach(s => s.addEventListener('input', recompute));
  recompute();
}


function formatRelativeShort(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const sec = Math.floor((now - d) / 1000);
    if (sec < 60) return 'now';
    if (sec < 3600) return Math.floor(sec / 60) + 'm';
    if (sec < 86400) return Math.floor(sec / 3600) + 'h';
    if (sec < 86400 * 7) return Math.floor(sec / 86400) + 'd';
    return d.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
  } catch (_) { return ''; }
}


function renderMyDashboard() {
  return `
    <div class="section-h">
      <h2>Dashboard</h2>
      <span class="meta">Pinned by you</span>
    </div>
    <div style="margin: -8px 0 12px 0; font-size: 13px; color: var(--ink-soft);">
      Tap the chat tab and ask EON to build a chart, then pin it here.
    </div>
    <div id="mydash-grid" class="dashboard-custom-grid">
      <div class="card" style="opacity: 0.4; height: 96px;"></div>
      <div class="card" style="opacity: 0.3; height: 96px;"></div>
    </div>
  `;
}

// v3.0p18 9v Phase C: drag-to-reorder + per-widget time-range picker.
// `days` is the only source-side knob today (revenue/mer/orders
// _timeseries all accept it); the picker shows up only for those.

const TIMESERIES_SOURCES = new Set([
  'revenue_timeseries', 'mer_timeseries', 'orders_timeseries',
]);
const DAYS_PRESETS = [
  { label: '7d',  value: 7 },
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
];

function widgetCardHTML(w) {
  const isTs = TIMESERIES_SOURCES.has(w.source);
  const days = (w.params && Number(w.params.days)) || 30;
  const picker = isTs
    ? `<div class="widget-range-picker" role="tablist">
        ${DAYS_PRESETS.map(p => `
          <button type="button" class="widget-range-opt${p.value === days ? ' active' : ''}"
            data-range="${p.value}" title="${p.label}">${p.label}</button>
        `).join('')}
      </div>`
    : '';
  return `
    <div class="widget-card" data-widget-id="${esc(w.id)}" data-source="${esc(w.source)}" draggable="true">
      <div class="widget-card-header">
        <span class="widget-drag-grip" title="Drag to reorder">⋮⋮</span>
        <span class="widget-card-title">${esc(w.title || 'Widget')}</span>
        ${picker}
        <button class="widget-card-meta widget-rm" type="button" title="Remove"
          data-rm-id="${esc(w.id)}" style="background:none;border:0;cursor:pointer;color:var(--ink-mute);">✕</button>
      </div>
      <div class="widget-card-body" data-body="1">
        <div class="widget-empty">Loading…</div>
      </div>
    </div>
  `;
}

async function hydrateWidgetBody(grid, w) {
  const body = grid.querySelector(`[data-widget-id="${CSS.escape(w.id)}"] [data-body]`);
  if (!body) return;
  body.innerHTML = `<div class="widget-empty">Loading…</div>`;
  try {
    const r = await api(`/api/dashboard/widgets/${encodeURIComponent(w.id)}/data`);
    if (!r || r.ok === false) {
      body.innerHTML = `<div class="widget-empty">Couldn't render: ${esc((r && r.error) || 'unknown')}</div>`;
      return;
    }
    body.innerHTML = renderWidgetBody(r.widget_type, r.data || {});
  } catch (err) {
    body.innerHTML = `<div class="widget-empty">Load failed</div>`;
  }
}

async function bindMyDashboard() {
  const grid = document.getElementById('mydash-grid');
  if (!grid) return;
  let widgets;
  try {
    const res = await api('/api/dashboard/widgets');
    widgets = (res && res.widgets) || [];
  } catch (e) {
    grid.innerHTML = `<div class="empty">
      <div class="empty-mark">!</div>
      <div class="empty-sub">Couldn't load widgets: ${esc(e.message || String(e))}</div>
    </div>`;
    return;
  }
  if (widgets.length === 0) {
    grid.innerHTML = `<div class="empty">
      <div class="empty-mark">○</div>
      <div class="empty-sub">No widgets yet. Open chat and try:<br/>
        <em>"Show me a chart of revenue for the last 30 days"</em><br/>
        then tap <strong>Pin to dashboard</strong>.
      </div>
    </div>`;
    return;
  }
  // Keep widgets in a closure so reorder / picker / remove can mutate
  // one source of truth and re-render predictably.
  const widgetsById = new Map(widgets.map(w => [w.id, w]));

  grid.innerHTML = widgets.map(widgetCardHTML).join('');

  // Hydrate bodies in parallel.
  await Promise.all(widgets.map(w => hydrateWidgetBody(grid, w)));

  // ── Remove buttons ──
  grid.querySelectorAll('.widget-rm').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = btn.dataset.rmId;
      if (!id) return;
      if (!confirm('Remove this widget?')) return;
      try {
        await api(`/api/dashboard/widgets/${encodeURIComponent(id)}`, { method: 'DELETE' });
        widgetsById.delete(id);
        const card = grid.querySelector(`[data-widget-id="${CSS.escape(id)}"]`);
        if (card) card.remove();
        if (widgetsById.size === 0) bindMyDashboard();
      } catch (err) { showToast('Failed: ' + (err.message || err)); }
    });
  });

  // ── Time-range picker (Phase C) ──
  // Only the 'days' param is exposed today. PATCHing params re-renders
  // the body in place — same widget id, fresh data.
  grid.querySelectorAll('.widget-range-opt').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const card = btn.closest('.widget-card');
      if (!card) return;
      const id = card.dataset.widgetId;
      const w = widgetsById.get(id);
      if (!w) return;
      const newDays = parseInt(btn.dataset.range, 10);
      const oldParams = w.params || {};
      if (oldParams.days === newDays) return;
      // Optimistic toggle.
      card.querySelectorAll('.widget-range-opt').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const newParams = { ...oldParams, days: newDays };
      try {
        await api(`/api/dashboard/widgets/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          body: JSON.stringify({ params: newParams }),
        });
        w.params = newParams;
        await hydrateWidgetBody(grid, w);
      } catch (err) {
        showToast('Failed to update range: ' + (err.message || err));
        // Revert visual state.
        card.querySelectorAll('.widget-range-opt').forEach(b => {
          b.classList.toggle('active',
            parseInt(b.dataset.range, 10) === (oldParams.days || 30));
        });
      }
    });
  });

  // ── Drag-to-reorder (Phase C) ──
  // HTML5 DnD: track which card is being dragged, drop it before the
  // hovered target. After drop, PATCH every card's position in order
  // so server state matches the new DOM ordering. Position values are
  // re-numbered from 0 so future inserts (next pin) still land at the
  // end via MAX+1.
  let dragSrc = null;

  function clearDragVisuals() {
    grid.querySelectorAll('.widget-card.dragging, .widget-card.drop-target')
      .forEach(el => el.classList.remove('dragging', 'drop-target'));
  }

  grid.querySelectorAll('.widget-card').forEach(card => {
    card.addEventListener('dragstart', (e) => {
      dragSrc = card;
      card.classList.add('dragging');
      e.dataTransfer.effectAllowed = 'move';
      // Firefox requires setData for the drag to fire.
      try { e.dataTransfer.setData('text/plain', card.dataset.widgetId); } catch (_) {}
    });
    card.addEventListener('dragend', () => {
      clearDragVisuals();
      dragSrc = null;
    });
    card.addEventListener('dragover', (e) => {
      if (!dragSrc || dragSrc === card) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      grid.querySelectorAll('.widget-card.drop-target')
        .forEach(el => el.classList.remove('drop-target'));
      card.classList.add('drop-target');
    });
    card.addEventListener('drop', async (e) => {
      e.preventDefault();
      if (!dragSrc || dragSrc === card) {
        clearDragVisuals();
        return;
      }
      // Decide before/after based on cursor position relative to the
      // target's vertical midpoint. Feels natural in both 1- and 2-col
      // layouts since cards are tall enough that vertical = the
      // primary axis even in a grid.
      const rect = card.getBoundingClientRect();
      const after = (e.clientY - rect.top) > rect.height / 2;
      if (after) card.after(dragSrc); else card.before(dragSrc);
      clearDragVisuals();

      // Persist new order. Fire PATCHes in parallel; failure is
      // logged but the DOM stays in the new order (operator's view
      // is the source of truth at this moment).
      const ids = Array.from(grid.querySelectorAll('.widget-card'))
        .map(c => c.dataset.widgetId);
      await Promise.all(ids.map((id, i) =>
        api(`/api/dashboard/widgets/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          body: JSON.stringify({ position: i }),
        }).catch(err => console.warn('reorder PATCH failed for', id, err))
      ));
    });
  });
}


// 2026-06-07: the in-app ADS tab is the decision-first dark dashboard
// (same .mk-ads3 system as elsewhere). Leads with blended cost/conv + the
// channel efficiency gap + a 2x2 channel grid, then walk-in attribution,
// then the decision rail, then the existing action-wired detail lists
// (boost / kill / scale / follower — reused verbatim via their element ids,
// so all pause/draft/scale actions keep working). New top surface derives
// per-channel CPAs client-side from /api/marketing/ads/deep (no new endpoint).
// 2026-07-23 (operator: "the ad section in eon main page is outdated —
// rebuild and restructure it based on data needed to be seen, simplify"):
// the section now renders from ONE payload (/api/marketing/live_ads_board)
// in the order he reads it — is it healthy → what did it spend/buy →
// what does EON think → what's on the bench. Deep dives live on
// /marketing and /pulse, linked, not duplicated. The 2026-06-07 surface
// (hero CPC, efficiency gap, channel grid, boost/kill/scale lists,
// follower card) is retired; its loaders below are no longer called
// from this view.
function renderAds() {
  return `
    <div class="mk-ads3">
      <div class="a3-topbar a3-reveal" style="--a3-i:0">
        <span class="a3-brand">CRUX<span class="a3-sub">Ads</span></span>
        <span class="a3-topbar-spacer"></span>
        <span class="live-stamp" id="live-stamp"></span>
        <span class="a3-live"><span class="a3-live-dot"></span>EON live</span>
      </div>

      <div class="a3-stack">
        <div class="a3-tile a3-reveal" style="--a3-i:1">
          <div class="a3-label">System health — the guardian's daily read</div>
          <div id="adsb-guardian"><div class="mh-skel" style="height:44px;border-radius:10px;"></div></div>
        </div>

        <div class="a3-tile a3-reveal" style="--a3-i:2">
          <div class="a3-label">Live campaigns — spend and what it bought</div>
          <div id="adsb-spend"><div class="mh-skel" style="height:44px;border-radius:10px;"></div></div>
        </div>

        <div class="a3-tile a3-reveal" style="--a3-i:3">
          <div class="a3-label">EON's verdicts — last judged night · active ads · 14-day window totals</div>
          <div id="adsb-verdicts"><div class="mh-skel" style="height:44px;border-radius:10px;"></div></div>
        </div>

        <div class="a3-tile a3-reveal" style="--a3-i:4">
          <div class="a3-label">Creative bench</div>
          <div id="adsb-bench"><div class="mh-skel" style="height:44px;border-radius:10px;"></div></div>
        </div>

        <div class="a3-footer a3-reveal" style="--a3-i:5">
          <!-- Operator 2026-08-05: "Change language to click to see ads."
               The old label ("Act on ads (kill / swap / scale)") read as an
               instruction with homework attached, not a door. The
               destination was always right — it lands on the live board. -->
          <a class="a3-cta" href="/marketing?v=platforms">See the ads →</a>
          <a class="a3-cta" href="/pulse">Watch them live →</a>
        </div>
      </div>
    </div>
  `;
}

async function bindAds() {
  const current = lw.captureRead('ads');
  // WS-E: the local ₦ closure shadowed the canonical ngn() — same
  // rendering, one implementation now.
  let b = null, loadErr = null;
  // WS-D (2026-07-26): the catch used to swallow the error entirely and
  // paint a generic line into ONE of the four tiles. Name the failure,
  // clear every skeleton, offer a retry.
  try { b = await api('/api/marketing/live_ads_board'); } catch (e) { loadErr = e; }
  if (!current()) return;
  lw.updateAds(b);
  if (!b || !b.ok) {
    const why = loadErr ? String(loadErr.message || loadErr).slice(0, 120) : (b && b.note) || 'empty response';
    const g = document.getElementById('adsb-guardian');
    if (g) g.innerHTML = `<div class="a3-empty" style="border:none">Couldn't reach the board (${esc(why)}) —
      <button class="btn-ghost" id="adsb-retry" style="font-size:11.5px;padding:4px 10px;">Retry</button>
      or open <a href="/marketing?v=platforms">the full dashboard</a>.</div>`;
    ['adsb-spend', 'adsb-verdicts', 'adsb-bench'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.innerHTML = '<div class="a3-ctx">—</div>';
    });
    document.getElementById('adsb-retry')?.addEventListener('click', () => {
      const main = document.querySelector('main.view');
      if (main) { main.innerHTML = renderAds(); lw.mountAds(); bindAds(); }
    });
    return;
  }

  // 1. Guardian — is it healthy
  const gEl = document.getElementById('adsb-guardian');
  if (gEl) {
    const gd = b.guardian;
    if (gd && Array.isArray(gd.checks)) {
      const bad = gd.checks.filter(c => c.level !== 'ok');
      const gates = gd.checks.filter(c => (c.key || '').startsWith('council_'));
      const gateLine = gates.length
        ? `<div class="a3-ctx" style="margin-top:6px;">${gates.map(c => esc(c.headline)).join(' · ')}</div>` : '';
      // WS-D: a red/amber read gets its one next move attached — the ask
      // rides the document [data-ask] delegate into chat, where the agent
      // acts through the permission ladder (nothing autonomous).
      const askGuardian = (gd.reds || bad.length)
        ? `<button class="btn-ghost adsb-act"
             data-ask="Walk me through today's ads guardian findings — what turned ${gd.reds ? 'red' : 'amber'}, why, and queue the fixes for my approval">What do I do?</button>`
        : '';
      gEl.innerHTML = `
        <div style="display:flex; gap:8px; align-items:center; flex-wrap:wrap;">
          <span class="a3-flag ${gd.reds ? 'a3-flag-red' : bad.length ? 'a3-flag-cut' : 'a3-flag-scale'}">
            ${gd.reds ? gd.reds + ' red' : bad.length ? bad.length + ' amber' : 'All clear'}</span>
          ${bad.slice(0, 3).map(c => `<span class="a3-ctx" title="${esc(c.detail || '')}">${esc(c.headline)}</span>`).join('')}
          ${askGuardian}
        </div>${gateLine}`;
    } else {
      gEl.innerHTML = `<div class="a3-ctx">First guardian sweep lands at 05:35 — findings appear here and in the 6am brief.</div>`;
    }
  }

  // 2. Spend — yesterday is the truth day, today is the ticker
  const sEl = document.getElementById('adsb-spend');
  if (sEl) {
    const sp = b.spend || {};
    const plats = [...new Set([...Object.keys(sp.yesterday || {}), ...Object.keys(sp.today || {})])];
    sEl.innerHTML = plats.length ? plats.map(p => {
      const y = (sp.yesterday || {})[p] || {}; const t = (sp.today || {})[p] || {};
      const cpa = y.purchases > 0 ? ` · CPA ${ngn(y.spend / y.purchases)}` : '';
      // purchases_known=false means the platform's actions sync hasn't
      // landed — say "syncing", never assert 0 (operator caught the card
      // claiming 0 purchases beside verdicts showing 7 conv, 2026-07-23)
      const purch = y.purchases_known === false
        ? 'purchases syncing — see verdicts'
        : `${Math.round(y.purchases || 0)} purchase${y.purchases === 1 ? '' : 's'}${cpa}`;
      return `<div style="display:flex; justify-content:space-between; gap:10px; padding:7px 0; border-top:1px solid var(--line); flex-wrap:wrap;">
        <strong style="text-transform:capitalize;">${esc(p)}</strong>
        <span class="a3-mono">${ngn(y.spend)} yesterday · ${purch}</span>
        <span class="a3-ctx a3-mono">today so far ${ngn(t.spend)}</span>
      </div>`;
    }).join('') : `<div class="a3-ctx">No spend recorded yet.</div>`;
  }

  // 3. Verdicts — what EON thinks, and where to act
  const vEl = document.getElementById('adsb-verdicts');
  if (vEl) {
    const vm = b.verdict_mix || {};
    const rows = Object.entries(vm).map(([p, m]) => {
      const bits = ['scale', 'hold', 'monitor', 'kill']
        .filter(k => m[k]).map(k => `${m[k]} ${k}`).join(' · ');
      // WS-D: verdicts become actionable in place. Kills queue through
      // the permission ladder via chat; scales get their case argued;
      // "why" opens the Ads Coach explanation (already cached per
      // verdict) — the tab stops being a poster.
      const acts = [];
      if (m.kill) acts.push(`<button class="btn-ghost adsb-act adsb-act-kill"
        data-ask="Show me the ${esc(p)} ads judged KILL — name each ad, why it earned the verdict (use the Ads Coach explanation), and queue the kills for my approval. Do not touch anything without my yes.">Review ${m.kill} kill${m.kill === 1 ? '' : 's'}</button>`);
      if (m.scale) acts.push(`<button class="btn-ghost adsb-act"
        data-ask="Make the case for scaling the ${esc(p)} ads judged SCALE — current spend, MER, fatigue risk, and the exact budget change you'd queue for my approval.">Scale case</button>`);
      if (!m.kill && !m.scale && (m.hold || m.monitor)) acts.push(`<button class="btn-ghost adsb-act"
        data-ask="Explain the ${esc(p)} hold/monitor verdicts — what would flip them to scale or kill?">Why?</button>`);
      return `<div style="display:flex; justify-content:space-between; align-items:center; gap:10px; padding:7px 0; border-top:1px solid var(--line); flex-wrap:wrap;">
        <strong style="text-transform:capitalize;">${esc(p)}</strong>
        <span>${bits || (m.no_data ? m.no_data + ' gathering data' : '—')}</span>
        <span class="a3-ctx a3-mono">${Math.round(m.conversions || 0)} conv on ${ngn(m.spend)}</span>
        ${acts.length ? `<span style="display:flex;gap:6px;flex-wrap:wrap;">${acts.join('')}</span>` : ''}
      </div>`;
    }).join('');
    const snz = b.snoozed_kills
      ? `<div class="a3-ctx" style="margin-top:6px;">${b.snoozed_kills} kill card${b.snoozed_kills === 1 ? '' : 's'} snoozed by you.</div>` : '';
    vEl.innerHTML = (rows || `<div class="a3-ctx">${esc(b.note || 'No verdicts yet.')}</div>`) + snz;
  }

  // 4. Bench
  const bEl = document.getElementById('adsb-bench');
  if (bEl) {
    const bench = b.bench || {};
    const next = (bench.items || []).map(i => esc(i.name || '')).join(' · ');
    bEl.innerHTML = `<div><strong>${bench.depth || 0}</strong> vetted replacement${bench.depth === 1 ? '' : 's'} ready${next ? ` — next up: ${next}` : ''}.
      ${(bench.depth || 0) < 3
        ? `<span class="a3-ctx"> Thin — shoot or transcode more before fatigue hits.</span>
           <button class="btn-ghost adsb-act" data-ask="The creative bench is thin (${bench.depth || 0} ready). Which looks or products should we shoot or transcode next, based on what's fatiguing and what sells?">Plan the next batch</button>`
        : ''}</div>`;
  }
}
// ── EON ad moves (compact, HOME dashboard) — 2026-06-08 ───────────
// Top 3 suggestions from /api/marketing/ad-suggestions, surfaced on the
// MAIN page (Home) — the operator asked for an ad section there. It used
// to live on the Ads tab but duplicated that tab's Kill/Scale rail + lists
// (triple render), so it moved here. Compact card: kind chip + one-line
// suggested_change + ₦ at stake, deep-linking to /marketing?v=platforms.
//
// Chip colours UNIFIED with marketing.js _AI_KIND so the same verdict is
// the same colour on both surfaces: kill/pause = red, creative_swap/refresh
// = amber, scale = mint. Backend kinds are PREFIXED with `ads_`; _adsxMoveKind
// strips that prefix before lookup (else the chip falls through to grey).
const _ADSX_MOVE_KIND = {
  creative_swap: { label: 'creative swap', color: 'var(--a3-amber, #F2A24C)', bg: '#F2A24C1f' },
  refresh:       { label: 'refresh',       color: 'var(--a3-amber, #F2A24C)', bg: '#F2A24C1f' },
  kill:          { label: 'kill',          color: '#ef4444',                  bg: '#ef444418' },
  pause:         { label: 'pause',         color: '#ef4444',                  bg: '#ef444418' },
  scale:         { label: 'scale',         color: 'var(--a3-mint, #56E39F)',  bg: '#56E39F1f' },
};
function _adsxMoveKind(kind) {
  const bare = String(kind || '').replace(/^ads_/, '');
  return _ADSX_MOVE_KIND[bare] || { label: bare || 'review', color: 'var(--ink-soft)', bg: '#ffffff10' };
}

// Appended to the Home #tiles-slot from renderHomeWithData(), below the
// money/signals tiles so it never crowds cash. Idempotent via the id check
// (mirrors the cs-inbox-entry pattern). The card removes itself when there
// are no recommended moves so Home stays uncluttered.
async function _adsxLoadHomeMoves() {
  const tilesSlot = $('#tiles-slot');
  if (!tilesSlot || document.getElementById('home-adsx-moves')) return;
  const seeAll = `<a href="/marketing?v=platforms" style="font-size:12px; color:var(--accent, #C8FF4D); text-decoration:none;">See all in Marketing →</a>`;
  // Union Meta + TikTok suggestions, rank by ₦ at stake so the single most
  // expensive mistake shows regardless of platform (Home is platform-agnostic).
  const plats = ['meta', 'tiktok'];
  let all = [];
  try {
    const results = await Promise.all(plats.map(p =>
      api(`/api/marketing/ad-suggestions?platform=${p}`).catch(() => null)));
    results.forEach((r, i) => {
      if (r && r.ok && Array.isArray(r.suggestions)) {
        r.suggestions.forEach(s => all.push(Object.assign({ _plat: plats[i] }, s)));
      }
    });
  } catch (e) {
    return;  // fail quiet on Home — the Ads tab owns the full surface
  }
  all.sort((a, b) => (Number(b.spend_at_stake_ngn) || 0) - (Number(a.spend_at_stake_ngn) || 0));
  const top = all.slice(0, 3);
  if (!top.length) return;  // no moves → don't render anything on Home
  const rows = top.map(s => {
    const k = _adsxMoveKind(s.kind);
    const stake = (s.spend_at_stake_ngn != null && Number(s.spend_at_stake_ngn) > 0)
      ? `<span style="font-family:var(--mono); font-size:12px; color:var(--ink); white-space:nowrap;">${_adsNgn(s.spend_at_stake_ngn)} at stake</span>` : '';
    return `<div style="display:flex; gap:10px; align-items:flex-start; padding:10px 0; border-top:1px solid var(--line, rgba(255,255,255,.07));">
      <span style="font-size:10px; text-transform:uppercase; letter-spacing:.04em; font-weight:600; color:${k.color}; background:${k.bg}; border-radius:5px; padding:3px 7px; white-space:nowrap; margin-top:1px;">${esc(k.label)}</span>
      <div style="min-width:0; flex:1;">
        <div style="font-size:13px; font-weight:600; color:var(--ink); overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${esc(s.ad_name || s.ad_id || '(unnamed ad)')}</div>
        <div style="font-size:12.5px; color:var(--ink-soft); margin-top:2px;">${esc(s.suggested_change || s.issue || 'Review this ad.')}</div>
      </div>
      ${stake}
    </div>`;
  }).join('');
  const wrapper = document.createElement('div');
  wrapper.innerHTML = `
    <div id="home-adsx-moves" class="card" style="padding:16px; margin-top:12px;">
      <div style="display:flex; justify-content:space-between; align-items:baseline; margin-bottom:4px;">
        <div style="font-size:11px; text-transform:uppercase; letter-spacing:0.5px; color:var(--ink-soft);">EON ad moves</div>
        <span style="font-weight:400; font-size:11px; color:var(--ink-soft);">${all.length} total · top ${top.length}</span>
      </div>
      <div style="font-size:12px; color:var(--ink-soft); margin-bottom:2px;">The top changes EON would make right now — the action first, then what it's worth.</div>
      ${rows}
      <div style="display:flex; justify-content:flex-end; margin-top:10px;">${seeAll}</div>
    </div>`;
  // Re-check idempotency after the awaits resolved (paint may have re-fired).
  if (document.getElementById('home-adsx-moves')) return;
  tilesSlot.appendChild(wrapper.firstElementChild);
}

// ── Ads v3 decision-surface helpers (2026-06-07) ──────────────────
const _ADSX_PLAT = [
  { key: 'meta', label: 'Meta' }, { key: 'tiktok', label: 'TikTok' },
  { key: 'snapchat', label: 'Snap' }, { key: 'google', label: 'Google', disabled: true },
];
const _adsxReduce = () => !!(window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches);

function _adsxCountUp(el, value, opts) {
  opts = opts || {};
  if (!el) return;
  const dec = opts.decimals || 0;
  const fmt = (n) => (opts.prefix || '') +
    Number(n).toLocaleString('en-NG', { minimumFractionDigits: dec, maximumFractionDigits: dec }) +
    (opts.suffix || '');
  const target = Number(value) || 0;
  if (_adsxReduce() || !target) { el.textContent = fmt(target); return; }
  const dur = opts.dur || 1100;
  let started = null;
  function tick(ts) {
    if (started === null) started = ts;
    const p = Math.min(1, (ts - started) / dur);
    el.textContent = fmt(target * (1 - Math.pow(1 - p, 3)));
    if (p < 1) requestAnimationFrame(tick); else el.textContent = fmt(target);
  }
  requestAnimationFrame(tick);
}
// _adsxChannelCard DELETED 2026-08-06 (e2e wiring): zero call sites —
// the channel grid it drew is the a3 board on /marketing now.
// ── Per-platform inline expand (2026-06-06) ─────────────────────────
async function togglePlatformExpand(platform, label, header) {
  const row = header.closest('.ad-platform-row');
  if (!row) return;
  const panel = row.querySelector('.ad-platform-row__panel');
  const chev  = header.querySelector('.ad-platform-row__chev');
  if (!panel) return;

  const isOpen = panel.style.display !== 'none';
  if (isOpen) {
    panel.style.display = 'none';
    if (chev) chev.style.transform = 'rotate(0deg)';
    return;
  }

  // Collapse siblings so only one platform expanded at a time —
  // keeps the page scannable on mobile.
  document.querySelectorAll('.ad-platform-row__panel').forEach(p => {
    if (p !== panel) p.style.display = 'none';
  });
  document.querySelectorAll('.ad-platform-row__chev').forEach(c => {
    if (c !== chev) c.style.transform = 'rotate(0deg)';
  });

  panel.style.display = 'block';
  if (chev) chev.style.transform = 'rotate(90deg)';
  panel.innerHTML = `<div style="font-size:12px; color:var(--ink-soft); padding:6px 0;">Loading…</div>`;

  let data;
  try {
    data = await api(`/api/ads/per_ad?platform=${encodeURIComponent(platform)}`);
  } catch (e) {
    panel.innerHTML = `<div style="font-size:12px; color:#ef4444; padding:6px 0;">Couldn't load: ${esc(e.message || String(e))}</div>`;
    return;
  }

  const byPlat = (data && data.platforms) || {};
  const ads = byPlat[platform] || byPlat[Object.keys(byPlat)[0]] || [];
  const ngn = (n) => '₦' + Math.round(Number(n) || 0).toLocaleString();
  const pct = (n) => (n == null ? '—' : (Math.round(Number(n) * 1000) / 10) + '%');

  if (!ads.length) {
    panel.innerHTML = `<div style="font-size:12px; color:var(--ink-soft); padding:6px 0;">No per-ad verdicts on ${esc(label)} yet. The judging cron runs daily at 03:00 WAT.</div>`;
    return;
  }

  const rows = ads.map(ad => {
    const verdict = (ad.verdict || '').toLowerCase();
    const verdictColor = verdict === 'scale' ? '#4ade80'
                       : verdict === 'kill'  ? '#ef4444'
                       : verdict === 'hold'  ? '#fbbf24'
                       : 'var(--ink-soft)';
    // per_ad_verdicts_latest exposes avg_ctr (ratio), avg_cpp, and
    // `reasoning` — NOT `reason` / `roas_estimate` / `avg_frequency`.
    // The earlier code referenced columns that don't exist, so the
    // reasoning blurb (the most useful part) was silently hidden.
    const ctr = ad.avg_ctr != null ? pct(Number(ad.avg_ctr)) : '—';
    const cpp = ad.avg_cpp != null ? ngn(ad.avg_cpp) : '—';
    return `<div style="border-bottom:1px solid var(--line); padding:10px 0;">
      <div style="display:flex; justify-content:space-between; align-items:baseline; gap:8px;">
        <div style="font-weight:500; font-size:12.5px; max-width:60%; overflow:hidden; text-overflow:ellipsis;">${esc(ad.ad_name || ad.campaign_name || ad.ad_id)}</div>
        <div style="font-size:10.5px; padding:1px 7px; border-radius:99px; border:1px solid ${verdictColor}55; color:${verdictColor}; font-weight:600; text-transform:uppercase; letter-spacing:0.3px;">${esc(ad.verdict || '—')}</div>
      </div>
      <div style="display:flex; flex-wrap:wrap; gap:12px; font-size:11.5px; color:var(--ink-soft); margin-top:5px;">
        <span><b style="color:var(--ink);">${ngn(ad.total_spend_ngn || 0)}</b> spend</span>
        <span><b style="color:var(--ink);">${ad.total_conversions || 0}</b> conv</span>
        <span>CTR <b style="color:var(--ink);">${ctr}</b></span>
        <span>CPP <b style="color:var(--ink);">${cpp}</b></span>
      </div>
      ${ad.reasoning ? `<div style="font-size:11.5px; color:var(--ink-soft); margin-top:5px; line-height:1.4;">${esc(ad.reasoning)}</div>` : ''}
      <div style="margin-top:7px;">
        <button onclick="askEonAboutAd('${sosEsc(sosJs(ad.ad_id))}', '${sosEsc(sosJs(ad.ad_name || ''))}', '${sosEsc(sosJs(platform))}')"
          style="background:var(--bg-sub); border:1px solid var(--border); color:var(--ink); font-size:11px; padding:4px 9px; border-radius:6px; cursor:pointer;">
          Ask EON about this ad
        </button>
      </div>
    </div>`;
  }).join('');

  panel.innerHTML = rows;
}

function askEonAboutAd(adId, adName, platform) {
  // Jump to chat with prefilled question. (No modal to close anymore —
  // the per-ad detail is rendered inline.)
  const q = `Analyze ad ${adId} (${platform}${adName ? ' · ' + adName : ''}). Pull the per-ad verdict, spend trend, audience overlap, fatigue signals. Tell me: scale, hold, kill — and why in 3 lines.`;
  try {
    // Prefer the existing goChat helper if defined
    if (typeof goChat === 'function') {
      goChat(q);
      return;
    }
  } catch (_) { /* fall through */ }
  // Fallback: route to /chat with hash query
  window.location.href = '/?v=chat#ask=' + encodeURIComponent(q);
}
// ── In-app kill / scale lists (mirror of /marketing, app.js styling) ──

const _ADS_PLAT_LABEL = { meta: 'Meta', tiktok: 'TikTok', google: 'Google', snapchat: 'Snap' };
const _ADS_PLAT_COLOR = { meta: '#4a8cff', tiktok: '#ff4d6d', google: '#e8a33d', snapchat: '#d6c700' };

function _adsNgn(n) { return '₦' + Math.round(Number(n) || 0).toLocaleString(); }

function _adsPlatBadge(p) {
  const c = _ADS_PLAT_COLOR[p] || 'var(--ink-soft)';
  return `<span style="font-size:11px; font-weight:600; padding:2px 8px; border-radius:999px; color:${c}; border:1px solid ${c}55;">${esc(_ADS_PLAT_LABEL[p] || p)}</span>`;
}

function _adsErrCard(msg) {
  return `<div class="card" style="padding:14px; font-size:13px; color:var(--ink-soft);">Couldn't load: ${esc(msg || 'unknown error')}</div>`;
}
function _adsKillTile(it) {
  const ctr = it.ctr_pct != null ? `${it.ctr_pct}% CTR` : 'no CTR';
  const mode = it.pause_mode === 'auto'
    ? '<span style="font-size:10px; text-transform:uppercase; color:#ef4444; margin-left:6px;">auto</span>'
    : '<span style="font-size:10px; text-transform:uppercase; color:var(--ink-soft); margin-left:6px;">confirm</span>';
  return `<div class="card" style="padding:14px; margin-bottom:12px;">
    <div style="display:flex; align-items:center; gap:8px; margin-bottom:6px; flex-wrap:wrap;">
      ${_adsPlatBadge(it.platform)}
      <span style="font-weight:600; font-size:14px;">${esc(it.ad_name)}</span>
      ${it.is_fatiguing ? '<span style="font-size:10px; text-transform:uppercase; color:#ef4444; border:1px solid #ef444455; border-radius:4px; padding:1px 5px;">fatiguing</span>' : ''}
    </div>
    <div style="font-size:12px; color:var(--ink-soft); margin-bottom:8px;">
      <b style="color:var(--ink);">${_adsNgn(it.spend_ngn)}</b> spent · ${it.days_running}d · ${esc(ctr)} · ${it.conversions || 0} conv
    </div>
    <div style="font-size:13px; color:var(--ink-soft); margin-bottom:10px;">${esc(it.verdict_reason || 'Underperforming vs platform baseline.')}</div>
    <div style="display:flex; gap:8px; flex-wrap:wrap; align-items:center;">
      <button data-ads-act="pause" data-plat="${esc(it.platform)}" data-adid="${esc(it.ad_id)}" data-spend="${it.spend_ngn}"
        style="padding:6px 14px; border-radius:8px; border:1px solid #ef4444; background:#ef4444; color:#fff; font-size:13px; cursor:pointer;">Pause${mode}</button>
      <button class="btn-ghost" data-ads-act="snooze" data-plat="${esc(it.platform)}" data-adid="${esc(it.ad_id)}">Snooze 24h</button>
    </div>
    <div class="ads-act-status" style="font-size:12px; margin-top:8px; min-height:14px;"></div>
  </div>`;
}
function _adsScaleTile(it) {
  const cur = it.current_budget_ngn != null ? _adsNgn(it.current_budget_ngn) : '—';
  const rec = it.recommended_budget_ngn != null ? _adsNgn(it.recommended_budget_ngn) : '—';
  const stats = [
    it.ctr_pct != null ? `${it.ctr_pct}% CTR` : null,
    `${it.conversions || 0} conv`,
    it.cpp_ngn != null ? `${_adsNgn(it.cpp_ngn)}/conv` : null,
  ].filter(Boolean).join(' · ');
  const btn = it.scalable
    ? `<button data-ads-act="scale" data-plat="${esc(it.platform)}" data-target="${esc(it.target_id)}" data-rec="${it.recommended_budget_ngn}"
        style="padding:6px 14px; border-radius:8px; border:1px solid #4ad06b; background:#4ad06b; color:#04210e; font-weight:600; font-size:13px; cursor:pointer;">Scale to ${rec}/day</button>`
    : `<button class="btn-ghost" disabled title="Budget/id not resolved">Open in Ads Manager</button>`;
  return `<div class="card" style="padding:14px; margin-bottom:12px;">
    <div style="display:flex; align-items:center; gap:8px; margin-bottom:6px; flex-wrap:wrap;">
      ${_adsPlatBadge(it.platform)}
      <span style="font-weight:600; font-size:14px;">${esc(it.ad_name)}</span>
    </div>
    <div style="font-size:12px; color:var(--ink-soft); margin-bottom:8px;">${esc(stats)} · ${_adsNgn(it.spend_ngn)} spent</div>
    <div style="font-size:13px; color:var(--ink-soft); margin-bottom:10px;">${cur} → <b style="color:#4ad06b;">${rec}</b>/day</div>
    <div style="display:flex; gap:8px; flex-wrap:wrap; align-items:center;">${btn}</div>
    <div class="ads-act-status" style="font-size:12px; margin-top:8px; min-height:14px;"></div>
  </div>`;
}

function _bindAdsActions(container) {
  container.querySelectorAll('[data-ads-act]').forEach(btn => {
    btn.addEventListener('click', () => _adsAction(btn));
  });
}

async function _adsAction(btn) {
  const act = btn.dataset.adsAct;
  const platform = btn.dataset.plat;
  const adId = btn.dataset.adid;
  const card = btn.closest('.card');
  const status = card ? card.querySelector('.ads-act-status') : null;
  const set = (html) => { if (status) status.innerHTML = html; };
  const ok = (t) => `<span style="color:#4ad06b;">${t}</span>`;
  const bad = (t) => `<span style="color:#ef4444;">${esc(t)}</span>`;
  btn.disabled = true;
  try {
    if (act === 'pause') {
      set('Pausing…');
      const r = await api('/api/marketing/kill_list/pause', { method: 'POST',
        body: JSON.stringify({ platform, ad_id: adId, spend_ngn: Number(btn.dataset.spend) || 0, reason: 'Kill-list pause (Ads tab)' }) });
      if (r && r.__permission__) set(ok('Sent for approval.'));
      else if (r && r.ok) { set(ok('Paused ✓')); if (card) card.style.opacity = '0.5'; }
      else { btn.disabled = false; set(bad('Failed: ' + ((r && r.error) || 'unknown'))); }
    } else if (act === 'snooze') {
      const r = await api('/api/marketing/snooze', { method: 'POST',
        body: JSON.stringify({ platform, ad_id: adId, hours: 24, reason: 'Snoozed (Ads tab)' }) });
      if (r && r.ok) { set('Snoozed 24h.'); if (card) card.style.opacity = '0.5'; }
      else { btn.disabled = false; set(bad('Snooze failed')); }
    } else if (act === 'scale') {
      set('Submitting…');
      const r = await api('/api/marketing/scale', { method: 'POST',
        body: JSON.stringify({ platform, target_id: btn.dataset.target, new_budget_ngn: Number(btn.dataset.rec) || 0, reason: 'Scale winner (Ads tab)' }) });
      if (r && r.__permission__) set(ok('Sent for approval.'));
      else if (r && r.ok) set(ok('Scaled ✓' + (r.paired ? ' · control created' : '')));
      else { btn.disabled = false; set(bad('Failed: ' + ((r && r.error) || 'unknown'))); }
    }
  } catch (e) {
    btn.disabled = false;
    set(bad('Failed: ' + (e.message || e)));
  }
}
// _renderFatigueTile / _renderConcentrationTile / _renderAttributionTile /
// renderCreativeTile DELETED 2026-08-06 (e2e wiring): four tiles with a
// definition and no caller anywhere in app/, tests/ or scripts/. The
// Ads surface they were built for moved to /marketing years of sprints
// ago; renderCreativeRow (below, live) is the one that survived.


function renderPlatformDetail(d) {
  if (!d || !d.ok) return '';
  const sparkSVG = renderSparkline(d.sparkline);
  const campaigns = d.campaigns || [];
  const totals = d.totals || {};
  const deltas = d.deltas || {};

  const spendDelta = deltas.spend_wow_pct;
  const convDelta = deltas.conversions_wow_pct;
  const fmtDelta = (n) => {
    if (n == null) return '<span style="color: var(--ink-soft);">—</span>';
    const sign = n >= 0 ? '▲' : '▼';
    const color = n >= 0 ? 'var(--good, #10b981)' : 'var(--danger, #ef4444)';
    return `<span style="color: ${color}; font-weight: 600;">${sign} ${Math.abs(n).toFixed(1)}%</span>`;
  };

  return `
    <div style="margin: 14px 0 4px 0; display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
      <div>
        <div style="font-size: 10px; color: var(--ink-soft); text-transform: uppercase; letter-spacing: 0.5px;">Spend · ${d.window_days}d</div>
        <div style="font-size: 16px; font-weight: 600;">${adsFormatNgn(totals.spend || 0)}</div>
        <div style="font-size: 11px; margin-top: 2px;">vs prev ${fmtDelta(spendDelta)}</div>
      </div>
      <div>
        <div style="font-size: 10px; color: var(--ink-soft); text-transform: uppercase; letter-spacing: 0.5px;">Conversions</div>
        <div style="font-size: 16px; font-weight: 600;">${Math.round(totals.conversions || 0).toLocaleString()}</div>
        <div style="font-size: 11px; margin-top: 2px;">vs prev ${fmtDelta(convDelta)}</div>
      </div>
    </div>

    ${sparkSVG ? `
      <div style="margin-top: 10px;">
        <div style="font-size: 10px; color: var(--ink-soft); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">
          Daily spend · last ${d.window_days} days
        </div>
        ${sparkSVG}
      </div>
    ` : ''}

    ${campaigns.length > 0 ? `
      <div style="margin-top: 16px; padding-top: 12px; border-top: 1px solid var(--border);">
        <div style="font-size: 11px; color: var(--ink-soft); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px;">
          Top campaigns · ${d.window_days}d
        </div>
        ${(() => {
          // v3.0p18 9r: backend returns active campaigns first, paused after.
          // PR-ADS: paused/inactive campaigns now collapse into a <details>
          // block by default — operator asked to deprioritize them on the
          // page. Active campaigns render inline; paused live behind one
          // click. Counts shown in the summary so they're not invisible.
          const active = campaigns.filter(c => c.is_active);
          const paused = campaigns.filter(c => !c.is_active);
          const activeHtml = active.map(renderCampaignRow).join('');
          if (!paused.length) return activeHtml;
          const pausedHtml = paused.map(renderCampaignRow).join('');
          return activeHtml + `
            <details style="margin-top: 8px;">
              <summary style="font-size: 10px; color: var(--ink-soft); text-transform: uppercase; letter-spacing: 0.5px; cursor: pointer; padding: 6px 0;">
                Show ${paused.length} paused / inactive campaign${paused.length === 1 ? '' : 's'}
              </summary>
              <div style="margin-top: 4px;">${pausedHtml}</div>
            </details>`;
        })()}
      </div>
    ` : ''}

    ${(d.creatives || []).length > 0 ? `
      <div style="margin-top: 16px; padding-top: 12px; border-top: 1px solid var(--border);">
        <div style="display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px;">
          <div style="font-size: 11px; color: var(--ink-soft); text-transform: uppercase; letter-spacing: 0.5px;">
            Top creatives · ${d.window_days}d
          </div>
          <div style="font-size: 10px; color: var(--ink-soft);">
            ${d.creatives.length} judged
          </div>
        </div>
        ${d.creatives.map(renderCreativeRow).join('')}
      </div>
    ` : ''}
  `;
}

// v3.0p30: per-creative row inside the platform deep-dive panel. Renders
// the per_ad_verdicts data the backend was already returning but the UI
// silently ignored — which made Snap appear "campaign-only" because Snap's
// 3 campaigns left nothing else visible. Verdict pill + fatigue tag come
// from ad_judge_per_ad.py; suggested_action is the model's one-line nudge.
//
// per_ad_verdicts.avg_ctr is stored as a decimal (0-1) unlike
// campaigns[].ctr which the platform_detail endpoint pre-multiplies to
// percent. We re-multiply here rather than mutate the endpoint, since
// other callers may want the raw decimal form.
function renderCreativeRow(cr) {
  const verdict = (cr.verdict || 'hold').toLowerCase();
  const verdictColor = {
    scale:   ['var(--good, #10b981)', 'rgba(16,185,129,0.10)'],
    hold:    ['var(--ink-soft, #6b7280)', 'rgba(107,114,128,0.10)'],
    monitor: ['#d97706', 'rgba(217,119,6,0.12)'],
    kill:    ['var(--danger, #ef4444)', 'rgba(239,68,68,0.10)'],
  }[verdict] || ['var(--ink-soft, #6b7280)', 'rgba(107,114,128,0.10)'];
  const verdictPill = `<span style="display: inline-block; font-size: 9px; padding: 2px 6px; border-radius: 4px; background: ${verdictColor[1]}; color: ${verdictColor[0]}; letter-spacing: 0.3px; text-transform: uppercase; font-weight: 600; margin-left: 6px; vertical-align: middle;">${esc(verdict)}</span>`;
  const fatiguePill = cr.is_fatiguing
    ? `<span style="display: inline-block; font-size: 9px; padding: 2px 6px; border-radius: 4px; background: rgba(217,119,6,0.12); color: #d97706; letter-spacing: 0.3px; text-transform: uppercase; font-weight: 600; margin-left: 4px; vertical-align: middle;">FATIGUING</span>`
    : '';
  const topPill = cr.is_top_performer
    ? `<span style="display: inline-block; font-size: 9px; padding: 2px 6px; border-radius: 4px; background: rgba(16,185,129,0.10); color: var(--good, #10b981); letter-spacing: 0.3px; text-transform: uppercase; font-weight: 600; margin-left: 4px; vertical-align: middle;">★ TOP</span>`
    : '';
  // avg_ctr is a string from Supabase numeric → cast then ×100 for %.
  const ctrPct = (cr.avg_ctr != null && cr.avg_ctr !== '')
    ? (parseFloat(cr.avg_ctr) * 100).toFixed(2) + '% CTR'
    : '—';
  const convCount = Math.round(parseFloat(cr.total_conversions) || 0);
  const convText = convCount > 0
    ? `${convCount} conv`
    : `<span style="color: var(--danger, #ef4444); opacity: 0.7;">0 conv</span>`;
  const action = cr.suggested_action
    ? `<div style="font-size: 10px; color: var(--ink-soft); margin-top: 3px; line-height: 1.35;">${esc(cr.suggested_action.slice(0, 110))}${cr.suggested_action.length > 110 ? '…' : ''}</div>`
    : '';
  return `
    <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; padding: 8px 0; border-bottom: 1px dashed var(--border);">
      <div style="flex: 1; min-width: 0;">
        <div style="font-size: 13px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
          ${esc(cr.ad_name || cr.ad_id || 'Unnamed ad')}${topPill}${verdictPill}${fatiguePill}
        </div>
        <div style="font-size: 11px; color: var(--ink-soft); margin-top: 2px;">
          ${esc(cr.campaign_name || '—')} · ${ctrPct} · ${convText}
        </div>
        ${action}
      </div>
      <div style="text-align: right; flex-shrink: 0;">
        <div style="font-size: 13px; font-weight: 600;">${adsFormatNgn(parseFloat(cr.total_spend_ngn) || 0)}</div>
      </div>
    </div>
  `;
}

// v3.0p18 9r: extracted from renderPlatformDetail so we can pass each
// campaign through `is_active` logic and emit a paused-pill when needed
// without losing readability of the parent template.
function renderCampaignRow(c) {
  const trend = c.trend_pct;
  const trendBadge = trend == null
    ? ''
    : trend >= 0
      ? `<span style="color: var(--good, #10b981); font-size: 11px;">▲ ${Math.abs(trend).toFixed(0)}%</span>`
      : `<span style="color: var(--danger, #ef4444); font-size: 11px;">▼ ${Math.abs(trend).toFixed(0)}%</span>`;
  const ctr = c.ctr != null ? `${c.ctr.toFixed(2)}% CTR` : '—';
  const paused = !c.is_active;
  // Show "Nd ago" / "today" hint for paused campaigns so the operator can
  // judge whether it just rolled off or has been dead for weeks.
  let activityHint = '';
  if (paused) {
    if (c.days_since_active == null) {
      activityHint = ' · never active';
    } else if (c.days_since_active === 0) {
      activityHint = ' · paused today';
    } else if (c.days_since_active === 1) {
      activityHint = ' · last active 1d ago';
    } else {
      activityHint = ` · last active ${c.days_since_active}d ago`;
    }
  }
  const pausedPill = paused
    ? `<span class="pill pill-muted" style="font-size: 9px; padding: 2px 6px; margin-left: 6px; vertical-align: middle; letter-spacing: 0.3px;">PAUSED</span>`
    : '';
  const rowOpacity = paused ? 'opacity: 0.65;' : '';
  return `
    <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; padding: 8px 0; border-bottom: 1px dashed var(--border); ${rowOpacity}">
      <div style="flex: 1; min-width: 0;">
        <div style="font-size: 13px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${esc(c.name)}${pausedPill}</div>
        <div style="font-size: 11px; color: var(--ink-soft); margin-top: 2px;">
          ${Math.round(c.impressions).toLocaleString()} imps · ${Math.round(c.clicks).toLocaleString()} clicks · ${ctr}${activityHint}
        </div>
      </div>
      <div style="text-align: right; flex-shrink: 0;">
        <div style="font-size: 13px; font-weight: 600;">${adsFormatNgn(c.spend)}</div>
        <div style="margin-top: 2px;">${trendBadge}</div>
      </div>
    </div>
  `;
}

// Minimal SVG sparkline. Two lines (spend) over the window with a soft
// gradient fill. No deps — inline SVG so it inherits CSS variables.
// v3.0p18 9u — chat-built dashboard widgets.
// Inline SVG renderers for line / bar / kpi / list widget types. Same
// renderer powers both the chat preview card and the persisted
// /dashboard/custom view.

function renderWidgetBody(type, data) {
  if (!data) return `<div class="widget-empty">No data</div>`;
  if (type === 'kpi')   return renderKpiTile(data);
  if (type === 'list')  return renderListTile(data);
  if (type === 'bar')   return renderBarChart(data);
  if (type === 'line')  return renderLineChart(data);
  if (type === 'table') return renderTableTile(data);
  return `<div class="widget-empty">Unknown widget type: ${esc(type)}</div>`;
}

// Series palette + legend, shared by line/bar. First series keeps the
// accent; the rest use tokens that already read correctly in all three
// themes (--ok/--warn ship theme values; the blue matches the aurora).
const WIDGET_SERIES_COLORS = [
  'var(--accent, #c0ff3a)', 'var(--ok, #56E39F)',
  'var(--warn, #F2A24C)', '#7AA6FF', '#CA6673', '#9177C7',
];
function _widgetSeriesColor(i) {
  return WIDGET_SERIES_COLORS[i % WIDGET_SERIES_COLORS.length];
}
function _widgetLegend(series) {
  if (!Array.isArray(series) || series.length < 2) return '';
  return `<div class="widget-legend">${series.map((s, i) =>
    `<span class="widget-legend-item"><span class="widget-legend-dot"
       style="background:${_widgetSeriesColor(i)}"></span>${esc(s.name || `Series ${i + 1}`)}</span>`
  ).join('')}</div>`;
}

// v3.0p513: detailed tables from chat (custom_query source). Wide
// content scrolls inside its own container — the card never stretches
// the page. Numbers right-align and localize; nulls render as —.
function renderTableTile(d) {
  const cols = d.columns || [];
  const rows = d.rows || [];
  if (cols.length === 0 || rows.length === 0) {
    return `<div class="widget-empty">${esc(d.empty_note || 'No rows')}</div>`;
  }
  const fmtCell = (v) => {
    if (v == null) return '<span class="widget-td-null">—</span>';
    if (typeof v === 'number') {
      const s = Number.isInteger(v) ? v.toLocaleString('en-NG')
        : v.toLocaleString('en-NG', { maximumFractionDigits: 2 });
      return `<span class="widget-td-num">${esc(s)}</span>`;
    }
    return esc(String(v).slice(0, 120));
  };
  return `
    <div class="widget-table-wrap">
      <table class="widget-table">
        <thead><tr>${cols.map(c =>
          `<th>${esc(String(c).replace(/_/g, ' '))}</th>`).join('')}</tr></thead>
        <tbody>${rows.map(r =>
          `<tr>${(r || []).map(fmtCell).map(c => `<td>${c}</td>`).join('')}</tr>`
        ).join('')}</tbody>
      </table>
    </div>
    ${d.note ? `<div class="widget-axis-label">${esc(d.note)}</div>` : ''}`;
}

function renderKpiTile(d) {
  const sentClass = d.sentiment === 'good' ? 'kpi-good'
                   : d.sentiment === 'bad' ? 'kpi-bad' : 'kpi-neutral';
  return `
    <div class="widget-kpi ${sentClass}">
      <div class="widget-kpi-label">${esc(d.label || '')}</div>
      <div class="widget-kpi-value">${esc(d.value_str || (d.value != null ? String(d.value) : '—'))}</div>
      ${d.subtext ? `<div class="widget-kpi-sub">${esc(d.subtext)}</div>` : ''}
    </div>`;
}

function renderListTile(d) {
  const rows = d.rows || [];
  if (rows.length === 0) {
    return `<div class="widget-empty">${esc(d.empty_note || 'No items')}</div>`;
  }
  return `
    <div class="widget-list">
      ${d.header ? `<div class="widget-list-header">${esc(d.header)}</div>` : ''}
      ${rows.map(r => `
        <div class="widget-list-row">
          <div class="widget-list-label">
            <div class="widget-list-title">${esc(r.label || '')}</div>
            ${r.sub ? `<div class="widget-list-sub">${esc(r.sub)}</div>` : ''}
          </div>
          <div class="widget-list-value">${esc(r.value_str || (r.value != null ? String(r.value) : ''))}</div>
        </div>
      `).join('')}
    </div>`;
}

function renderBarChart(d) {
  const xs = d.x || [];
  // v3.0p513: multi-series — grouped bars, one color per series.
  const allSeries = (d.series || []).filter(s => s && Array.isArray(s.values));
  if (xs.length === 0 || allSeries.length === 0) {
    return `<div class="widget-empty">${esc(d.empty_note || 'No data')}</div>`;
  }
  const W = 320, H = 140, PAD_X = 24, PAD_Y = 18, PAD_BOTTOM = 28;
  const innerW = W - PAD_X * 2;
  const innerH = H - PAD_Y - PAD_BOTTOM;
  const nS = allSeries.length;
  const maxV = Math.max(
    ...allSeries.flatMap(s => (s.values || []).map(v => Number(v) || 0)), 1);
  const slotW = innerW / xs.length;
  const gap = slotW * 0.3;
  const barW = (slotW - gap) / nS;
  const bars = allSeries.map((s, si) => (s.values || []).map((v, i) => {
    const h = (Number(v) || 0) / maxV * innerH;
    const x = PAD_X + i * slotW + gap / 2 + si * barW;
    const y = PAD_Y + (innerH - h);
    return `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${Math.max(barW - 1, 1).toFixed(1)}" height="${h.toFixed(1)}" rx="2" fill="${_widgetSeriesColor(si)}" opacity="0.85"/>`;
  }).join('')).join('');
  const labels = xs.map((lbl, i) => {
    const cx = PAD_X + i * slotW + slotW / 2;
    return `<text x="${cx.toFixed(1)}" y="${(H - 8).toFixed(1)}" font-size="9" text-anchor="middle" fill="currentColor" opacity="0.65">${esc(String(lbl).slice(0, 8))}</text>`;
  }).join('');
  // Value annotations only when a single series — grouped bars would
  // overprint each other.
  const valueLabels = nS === 1 ? (allSeries[0].values || []).map((v, i) => {
    const cx = PAD_X + i * slotW + slotW / 2;
    const h = (Number(v) || 0) / maxV * innerH;
    const y = PAD_Y + (innerH - h) - 3;
    return `<text x="${cx.toFixed(1)}" y="${y.toFixed(1)}" font-size="9" text-anchor="middle" fill="currentColor" opacity="0.7">${esc(_widgetCompact(v))}</text>`;
  }).join('') : '';
  return `
    <svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" style="width: 100%; height: 160px;">
      ${bars}
      ${valueLabels}
      ${labels}
    </svg>
    ${_widgetLegend(allSeries)}
    ${d.x_label ? `<div class="widget-axis-label">${esc(d.x_label)}</div>` : ''}`;
}

function renderLineChart(d) {
  const xs = d.x || [];
  // v3.0p513: multi-series — shared scale, one color per series, area
  // fill only under the first (keeps overlap readable).
  const allSeries = (d.series || []).filter(s => s && Array.isArray(s.values));
  const series = allSeries[0] || {values: []};
  const vals = series.values || [];
  const numericVals = vals.map(v => v == null ? null : Number(v));
  const definedVals = allSeries
    .flatMap(s => (s.values || []).map(v => v == null ? null : Number(v)))
    .filter(v => v != null && !Number.isNaN(v));
  if (xs.length < 2 || definedVals.length === 0) {
    return `<div class="widget-empty">${esc(d.empty_note || 'No data')}</div>`;
  }
  const W = 320, H = 140, PAD_X = 24, PAD_Y = 16, PAD_BOTTOM = 28;
  const innerW = W - PAD_X * 2;
  const innerH = H - PAD_Y - PAD_BOTTOM;
  const minV = Math.min(...definedVals, 0);
  const maxV = Math.max(...definedVals, minV + 1);
  const range = maxV - minV || 1;
  // Build a path per series, skipping nulls (broken segments).
  const pathFor = (values) => {
    let p = '';
    let started = false;
    values.forEach((raw, i) => {
      const v = raw == null ? null : Number(raw);
      if (v == null || Number.isNaN(v)) { started = false; return; }
      const x = PAD_X + (i / Math.max(1, xs.length - 1)) * innerW;
      const y = PAD_Y + (innerH - (v - minV) / range * innerH);
      p += (started ? ' L ' : ' M ') + x.toFixed(1) + ' ' + y.toFixed(1);
      started = true;
    });
    return p;
  };
  const path = pathFor(vals);
  const extraPaths = allSeries.slice(1).map((s, i) =>
    `<path d="${pathFor(s.values || [])}" fill="none" stroke="${_widgetSeriesColor(i + 1)}" stroke-width="1.8" stroke-linejoin="round" stroke-linecap="round"/>`
  ).join('');
  // Area fill: only valid where the line is.
  let fillPath = '';
  if (path) {
    fillPath = path + ` L ${(W - PAD_X).toFixed(1)} ${(H - PAD_BOTTOM).toFixed(1)} L ${PAD_X.toFixed(1)} ${(H - PAD_BOTTOM).toFixed(1)} Z`;
  }
  const firstLbl = xs[0];
  const lastLbl = xs[xs.length - 1];
  const lastVal = numericVals[numericVals.length - 1];
  return `
    <svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" style="width: 100%; height: 160px;">
      <defs>
        <linearGradient id="wfill-${Math.random().toString(36).slice(2,7)}" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stop-color="var(--accent, #c0ff3a)" stop-opacity="0.3"/>
          <stop offset="100%" stop-color="var(--accent, #c0ff3a)" stop-opacity="0"/>
        </linearGradient>
      </defs>
      ${fillPath ? `<path d="${fillPath}" fill="var(--accent, #c0ff3a)" fill-opacity="0.15"/>` : ''}
      <path d="${path}" fill="none" stroke="var(--accent, #c0ff3a)" stroke-width="1.8" stroke-linejoin="round" stroke-linecap="round"/>
      ${extraPaths}
      <text x="${PAD_X}" y="${(H - 8).toFixed(1)}" font-size="9" fill="currentColor" opacity="0.55">${esc(firstLbl || '')}</text>
      <text x="${(W - PAD_X).toFixed(1)}" y="${(H - 8).toFixed(1)}" font-size="9" fill="currentColor" opacity="0.55" text-anchor="end">${esc(lastLbl || '')}</text>
      <text x="${(W - PAD_X).toFixed(1)}" y="${(PAD_Y - 4).toFixed(1)}" font-size="10" fill="currentColor" text-anchor="end" opacity="0.85">${esc(_widgetCompact(lastVal))}</text>
    </svg>
    ${_widgetLegend(allSeries)}
    ${d.x_label ? `<div class="widget-axis-label">${esc(d.x_label)}</div>` : ''}`;
}

// Compact number formatter for axis labels & inline annotations.
function _widgetCompact(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(0) + 'k';
  if (Math.abs(n) >= 10) return n.toFixed(0);
  return n.toFixed(2).replace(/\.?0+$/, '');
}


function renderSparkline(points) {
  if (!Array.isArray(points) || points.length < 2) return '';
  const W = 280, H = 48, PAD = 2;
  const xs = points.map((_, i) => i);
  const spends = points.map(p => Number(p.spend) || 0);
  const maxS = Math.max(...spends, 1);
  const minS = Math.min(...spends, 0);
  const range = (maxS - minS) || 1;
  const path = xs.map((x, i) => {
    const px = PAD + (x / Math.max(1, xs.length - 1)) * (W - PAD * 2);
    const py = H - PAD - ((spends[i] - minS) / range) * (H - PAD * 2);
    return `${i === 0 ? 'M' : 'L'} ${px.toFixed(1)} ${py.toFixed(1)}`;
  }).join(' ');
  const fillPath = `${path} L ${W - PAD} ${H - PAD} L ${PAD} ${H - PAD} Z`;
  // Last and first labels
  const last = points[points.length - 1];
  const first = points[0];
  return `
    <svg viewBox="0 0 ${W} ${H + 14}" preserveAspectRatio="none" style="width: 100%; height: 62px;">
      <defs>
        <linearGradient id="sparkfill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stop-color="var(--accent, #c0ff3a)" stop-opacity="0.35"/>
          <stop offset="100%" stop-color="var(--accent, #c0ff3a)" stop-opacity="0"/>
        </linearGradient>
      </defs>
      <path d="${fillPath}" fill="url(#sparkfill)"/>
      <path d="${path}" fill="none" stroke="var(--accent, #c0ff3a)" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"/>
      <text x="${PAD}" y="${H + 11}" font-size="9" fill="currentColor" opacity="0.55">${first?.date || ''}</text>
      <text x="${W - PAD}" y="${H + 11}" font-size="9" fill="currentColor" opacity="0.55" text-anchor="end">${last?.date || ''}</text>
    </svg>
  `;
}
function perAdPillClass(verdict) {
  if (verdict === 'scale') return 'pill-ok';
  if (verdict === 'kill') return 'pill-danger';
  if (verdict === 'monitor') return 'pill-warn';
  if (verdict === 'hold') return 'pill-muted';
  return 'pill-muted';  // no_data
}
const MONEY_PERIODS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'last_7d',    label: '7d' },
  { key: 'last_30d',   label: '30d' },
  { key: 'mtd',        label: 'MTD' },
  { key: 'custom',     label: 'Custom' },
];

// One navigation decision for cold links, in-page jumps and render dispatch.
// Server permissions still decide access to every record and action.
function moneyResolveView(key) {
  const role = State.member?.base_role || State.member?.role || 'member';
  const canFinance = ['owner', 'admin', 'manager'].includes(role);
  const owner = role === 'owner' && !State.impersonating;
  const known = ['overview', 'ledger', 'workbench', 'tools', 'books', 'tax', 'projection'].includes(key);
  const denied = !known || (key !== 'overview' && !canFinance);
  const tools = !denied && owner && key === 'tools';
  return {key: denied ? 'overview' : tools ? 'workbench' : key === 'workbench' && !owner ? 'tools' : key,
    canFinance, owner, denied, tools};
}

function moneyEntryPeriod(value) {
  if (MONEY_PERIODS.some(p => p.key !== 'custom' && p.key === value)) return value;
  const dates = /^custom:(\d{4}-\d{2}-\d{2}):(\d{4}-\d{2}-\d{2})$/.exec(value || '');
  if (dates && dates[1] <= dates[2] && dates.slice(1).every(day => {
    const date = new Date(day + 'T00:00:00Z');
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === day;
  })) return value;
  return 'this_month';
}

function moneyReadEntry(qs) {
  const target = moneyResolveView(qs.has('tab') ? qs.get('tab') : 'overview');
  State.moneyTab = target.key;
  State.moneyGateDenied = target.denied;
  State.moneyToolsOpen = target.tools;
  if (qs.has('period')) State.moneyPeriod = moneyEntryPeriod(qs.get('period'));
  else State.moneyPeriod = moneyEntryPeriod(readMoneyView(history,moneyViewScope(State),'sections')?.summaryPeriod);
  State.booksPeriod = readMoneyView(history,moneyViewScope(State),'sections')?.booksPeriod || moneyEntryMonth(history.state?.booksPeriod) || bksCurrentMonth();
  // A11: /?v=money&tab=tools&wallet=<provider> (Today's wallet cards) names
  // one wallet on the board (moneyMarkWallet). Both boot paths come here.
  const wallet = qs.get('wallet');
  State.moneyWallet = wallet && /^[a-z0-9_]{1,40}$/.test(wallet) ? wallet : null;
  // /?v=money&tab=overview&approval=<approval id> shows one approved bank
  // instruction once. The id leaves the address as soon as it is read, so a
  // reload, Back or a kept unsaved form never shows it again. An older card's
  // signature is taken too, so it can be said plainly that it cannot be shown.
  const approval = qs.get('approval') || '';
  if (qs.has('approval')) {
    const left = new URLSearchParams(location.search);
    left.delete('approval');
    history.replaceState(history.state, '', location.pathname + (left.toString() ? '?' + left : '') + location.hash);
  }
  State.moneyApprovalRef = target.key !== 'overview' ? null
    : /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(approval) ? {ref: approval.toLowerCase(), scope: _viewReadScope()}
    : /^adviser:[a-z0-9_-]{1,64}:\d{4}-\d{2}-\d{2}$/.test(approval) ? {ref: approval, scope: _viewReadScope()} : null;
  // /?v=money&tab=workbench&file=<statement_files.id> names one statement file
  // for Statement lines. Only its opaque id rides the address; the server
  // resolves it. It belongs to the person who opened it (moneyStatementFileId).
  State.moneyStatementFile = target.key === 'workbench' && qs.has('file')
    ? {id: String(qs.get('file')).slice(0, 100), scope: _viewReadScope()} : null;
  // /?v=money&tab=workbench&request=<finance_requests.id> opens that request
  // once. The id leaves the address as soon as it is read, so a reload, Back
  // or a kept unsaved form never opens it again.
  const request = qs.get('request');
  State.moneyRequestRef = target.key === 'workbench' && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(request || '')
    ? {id: request.toLowerCase(), scope: moneyDraftScope()} : null;
  if (new URLSearchParams(location.search).has('request')) {
    const left = new URLSearchParams(location.search); left.delete('request');
    history.replaceState(history.state, '', location.pathname + (left.toString() ? '?' + left : '') + location.hash);
  }
  if (qs.has('tab')) {
    const scope = moneyViewScope(State), sections = readMoneyView(window.history, scope, 'sections') || {};
    if (['ledger', 'workbench', 'tools'].includes(target.key)) sections.transactions = target.key;
    if (['books', 'tax'].includes(target.key)) sections.close = target.key;
    writeMoneyView(window.history, scope, 'sections', sections);
  }
  // /?v=money&tab=ledger&entry=<transactions.id> opens one book entry once,
  // through the typed door (_ldgFocus), only where this role may open Book
  // entries. One typed reference per address: the tab decides which is read.
  // The id leaves the address at once, so reload, Back or a kept unsaved form
  // never reopens it.
  const entry = qs.get('entry') || '';
  if (qs.has('entry')) {
    const left = new URLSearchParams(location.search);
    left.delete('entry');
    history.replaceState(history.state, '', location.pathname + (left.toString() ? '?' + left : '') + location.hash);
  }
  State.moneyEntryRef = target.key === 'ledger' && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(entry)
    ? {id: entry.toLowerCase(), scope: _viewReadScope()} : null;
}

// The named statement file, only on Statement lines and only for the identity
// that opened it; any other view or person drops it.
function moneyStatementFileId() {
  const named = State.moneyStatementFile;
  if (named && State.moneyTab === 'workbench' && named.scope === _viewReadScope()) return named.id;
  State.moneyStatementFile = null;
  return null;
}

// Only route choices enter history. Editors and financial values stay in memory.
function moneySyncEntry(push=false) {
  const scope=moneyViewScope(State), sections=readMoneyView(history,scope,'sections')||{};
  sections.summaryPeriod=moneyEntryPeriod(State.moneyPeriod||'this_month');
  sections.booksPeriod=State.booksPeriod;
  const route=new URL(location.href);
  route.searchParams.set('v','money');route.searchParams.set('tab',State.moneyTab||'overview');
  route.searchParams.set('period',sections.summaryPeriod);
  if(State.workspaceLink)route.searchParams.set('workspace',State.workspaceLink);
  const file=State.moneyStatementFile?moneyStatementFileId():null;
  if(file===null)route.searchParams.delete('file');else route.searchParams.set('file',file);
  const state={...history.state,v:'money',moneyTab:State.moneyTab||'overview',moneyPeriod:sections.summaryPeriod,booksPeriod:moneyEntryMonth(State.booksPeriod)};
  history[push?'pushState':'replaceState'](state,'',route);
  writeMoneyView(history,scope,'sections',sections);
  for(const probe of mtxAllowPageRebuild.drafts||[])if(probe.current()){
    probe.url=location.href;probe.history=history.state;
  }
}

function moneyRestoreEntry(state) {
  const qs=new URLSearchParams(location.search), sections=readMoneyView(history,moneyViewScope(State),'sections')||{};
  const target=moneyResolveView(state?.moneyTab||qs.get('tab')||'overview');
  State.moneyTab=target.key;State.moneyGateDenied=target.denied;State.moneyToolsOpen=target.tools;
  State.moneyStatementFile=target.key==='workbench'&&qs.has('file')?{id:String(qs.get('file')).slice(0,100),scope:_viewReadScope()}:null;
  State.moneyPeriod=moneyEntryPeriod(state?.moneyPeriod||qs.get('period')||sections.summaryPeriod);
  // Email sign-in has no scoped memory: the entry's own month, then the month on screen.
  State.booksPeriod=sections.booksPeriod||moneyEntryMonth(state?.booksPeriod)||moneyEntryMonth(State.booksPeriod)||bksCurrentMonth();
}

function moneyEntryMonth(value) {
  return cleanMoneyView('sections',{booksPeriod:value}).booksPeriod;
}

// The one Books month the P&L card opens: the end month of the window its
// endpoint reads. Named periods use the Lagos clock (books.parse_period ->
// accounting._period_bounds); a custom span is read as sent, unclamped.
function moneyPnlBooksMonth(period, now=new Date()) {
  const today=new Intl.DateTimeFormat('en-CA',{timeZone:'Africa/Lagos',year:'numeric',month:'2-digit',day:'2-digit'}).format(now).slice(0,7);
  const p=moneyEntryPeriod(period), end=/^custom:\d{4}-\d{2}-\d{2}:(\d{4}-\d{2})-\d{2}$/.exec(p)?.[1];
  const [y,m]=today.split('-').map(Number);
  const month=p==='last_month'?(m===1?`${y-1}-12`:`${y}-${String(m-1).padStart(2,'0')}`):end||today;
  return {month,label:new Intl.DateTimeFormat('en-GB',{month:'long',year:'numeric',timeZone:'UTC'}).format(new Date(month+'-01T00:00:00Z')),
    range:!['this_month','mtd','last_month'].includes(p)};
}

// PR MONEY-COCKPIT: single period contract. Every Money consumer —
// hero/summary (apiPeriod), insights expense window (days), cash
// runway lookback (days) — reads the SAME resolved window. Previously
// each kept its own partial period→days map: `mtd` fell to 7d on
// insights and 30d on cash while the hero honoured it, and
// `custom:START:END` fell through everywhere. Returns:
//   days      — lookback length for days-based endpoints
//   apiPeriod — period string for /api/accounting/* endpoints
//   label     — human meta-line label ("month to date", "Jun 1 – Jun 15")
function moneyPeriodToWindow(period) {
  const p = period || 'this_month';
  if (typeof p === 'string' && p.startsWith('custom:')) {
    // custom:YYYY-MM-DD:YYYY-MM-DD → days from the span (inclusive).
    let days = 30;
    const parts = p.split(':');
    if (parts.length === 3) {
      const start = new Date(parts[1] + 'T00:00:00');
      const end = new Date(parts[2] + 'T00:00:00');
      if (!isNaN(start) && !isNaN(end)) {
        days = Math.max(1, Math.round((end - start) / 86400000) + 1);
      }
    }
    return { days, apiPeriod: p, label: _formatCustomChipLabel(p) };
  }
  const dayOfMonth = new Date().getDate();
  const sinceJan1 = Math.floor(
    (Date.now() - new Date(new Date().getFullYear(), 0, 1).getTime()) / 86400000);
  const map = {
    // 'today' alone is too narrow for ratio/lookback math — floor at 7.
    today:      { days: 7,  label: 'today' },
    last_7d:    { days: 7,  label: 'last 7 days' },
    last_30d:   { days: 30, label: 'last 30 days' },
    last_90d:   { days: 90, label: 'last 90 days' },
    // 2026-06-01 fix carried over: floor at 7 on the 1st of the month
    // so ratio cards keep a meaningful window.
    this_month: { days: Math.max(7, dayOfMonth), label: 'this month' },
    mtd:        { days: Math.max(7, dayOfMonth), label: 'month to date' },
    last_month: { days: 30, label: 'last month' },
    ytd:        { days: Math.max(30, sinceJan1), label: 'year to date' },
  };
  const w = map[p] || { days: 30, label: 'this month' };
  return { days: w.days, apiPeriod: p, label: w.label };
}

// ── HOUSE floor 8 — THE VAULT (WS-I, 2026-08-07) ────────────────────
//
// The money page's real job is one question — "am I safe?" — so under
// the house skin safety answers FIRST: days of cash, the floor under
// it, the next obligation, before any P&L pride. The four experiments
// (LAB1 / vendors / AI credits / Pricing OS) move one drawer deeper
// into a Workbench tab and out of the morning path. The register
// prints like a till receipt — the room's one kinetic signature.
//
// Every branch below is gated on this one predicate; Classic/Light/
// Flow take the untouched else-branch. (Floor-3 precedent, cswHouse.)
const mnyHouse = () => document.documentElement.getAttribute('data-theme') === 'house';

let _moneyViewScopeSeen=null;
function renderMoney() {
  const viewScope=moneyViewScope(State);
  if(viewScope!==_moneyViewScopeSeen){
    _moneyViewScopeSeen=viewScope;
    Object.assign(_ldgS,cleanMoneyView('ledger'),readMoneyView(window.history,viewScope,'ledger')||{},
      {rows:[],total:0,offset:0,sel:new Set(),cats:null,sugg:{},gen:_ldgS.gen+1});
  }
  // v3.0p18 9aa: Cash tiles + accounts list moved here from Settings.
  // Settings was the wrong shelf — Money is the dedicated section and
  // operators looking for "what's in our accounts?" land here first.
  // refreshCashSection() reads /api/finance/* regardless of where the
  // markup mounts, so it just gets re-pointed.
  // Vet 2026-07-27: gate on base_role — `role` is the display LABEL
  // (a custom "Accountant" label silently hid Books/Tax from a manager).
  const role = State.member?.base_role || State.member?.role || 'member';
  const target = moneyResolveView(State.moneyTab || 'overview');
  const canFinance = target.canFinance;
  State.moneyTab = target.key;
  if (target.denied) State.moneyGateDenied = true;
  State.moneyToolsOpen = target.owner && (State.moneyToolsOpen || target.tools);

  // v3.0p106 BOOKS+TAX: Money grew two sibling tabs — Books (audit-grade
  // double-entry statements from /api/accounting/books/*) and Tax (the
  // Tax OS cockpit on /api/tax/*). The Overview branch below is unchanged;
  // the new tabs render + fetch ONLY when selected (lazy-load by
  // construction — bindMoney dispatches to the tab binder, which is the
  // only place those endpoints are called).
  const moneyTab = State.moneyTab || 'overview';
  if (canFinance && moneyTab === 'books') return renderBooksTab();
  if (canFinance && moneyTab === 'tax') return renderTaxTab();
  if (canFinance && moneyTab === 'ledger') return renderMoneyLedger();
  if (canFinance && moneyTab === 'projection') return renderMoneyProjection();
  // HOUSE floor 8: the Workbench drawer — the four experiments live
  // one tap away instead of four sections deep in the morning scroll.
  if (canFinance && moneyTab === 'workbench') return renderVaultWorkbench();
  if (canFinance && moneyTab === 'tools') return renderVaultWorkbench();

  const period = State.moneyPeriod || 'this_month';
  const isCustom = typeof period === 'string' && period.startsWith('custom:');
  const activeKey = isCustom ? 'custom' : period;
  const customLabel = isCustom ? _formatCustomChipLabel(period) : 'Custom';

  return `
    <div class="section-h money-section-h">
      <h2>Money</h2>
      <span class="meta" id="money-period-meta">${new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
    </div>
    ${State.moneyGateDenied ? `<p id="money-gate-notice" role="status">That Money page isn't available here; showing Position.</p>` : ''}
    ${canFinance ? renderMoneyTabBar('overview') : ''}
    ${role === 'owner' && !State.impersonating ? '<a class="ascent-entry" href="/ascent?tab=capital"><span>ASCENT / CAPITAL</span><strong>See what your next move leaves for growth.</strong><span>Explore the plan ↗</span></a>' : ''}

    <!-- 2026-07-27 (Daniel): REMOVED the top Inventory link ("remove the
         inventory button at the top too") — reverses his own 2026-06-07
         "keep both" directive. Do not restore without a fresh ask; the
         menu launcher carries the inventory entry point. -->

    <div class="date-chips money-chips" id="money-date-chips">
      ${MONEY_PERIODS.map(p => {
        const label = p.key === 'custom' ? customLabel : p.label;
        return `
        <button class="chip ${p.key === activeKey ? 'chip-active' : ''}" data-money-period="${p.key}">
          ${esc(label)}
        </button>`;
      }).join('')}
    </div>

    <!-- PR MONEY-COCKPIT: alerts-first digest strip. One line per
         red/amber state the page already computes inside cards (floor
         breach, payroll cover, budget over, covenant risk, days-cash);
         tap scrolls to the owning card. Hydrated by _updateMoneyDigest
         from the insights + budgets fetches. Empty = renders nothing. -->
    <div id="money-digest-slot"></div>

    <!-- Capital OS PR-5: EON Financial Adviser — latest weekly CFO memo
         (deterministic findings + Fable prose), expandable, with the
         Academy "Sharpen" links (PR-6). Hydrated by refreshAdviserCard
         from /api/finance/adviser/latest; renders nothing until the
         first memo exists. Personal book is stripped server-side for
         non-owner roles. -->
    ${canFinance ? '<div id="money-adviser-slot"></div>' : ''}

    ${canFinance ? `
    <!-- Capital OS PR-3: entity chips — the three books (CRUX business /
         China ops / Personal). A tap on China/Personal reads that book
         live (accounts held, newest statement) — see the binder. The
         China + Personal books are operator-only (charter §0 decision
         1), so non-owner roles see the CRUX chip alone — and the server
         already strips personal accounts out of /api/finance/cash-position
         for them. -->
    <div class="date-chips money-entity-chips" id="money-entity-chips">
      <button class="chip chip-active" data-entity="crux">CRUX</button>
      ${role === 'owner' ? `
      <button class="chip" data-entity="china">China</button>
      <button class="chip" data-entity="personal">Personal</button>
      ` : ''}
      <span class="meta" id="money-entity-note"></span>
    </div>
    ` : ''}

    <!-- Money-page redesign 2026-06-07: KPI hero + tile row are now a
         single responsive grid of polished fintech cards with count-up
         numbers and a stagger-fade enter. The legacy #money-hero /
         #money-stats IDs are preserved so the existing refresh code
         keeps writing into the right nodes.
         PR MONEY-COCKPIT: cash cockpit — row 1 answers "am I safe":
         Net profit | Cash | Days-cash vs floor | Next obligation. The
         Cash-section runway + total-cash tiles folded in here; the
         per-account list moved behind the Cash drill-down below. -->
    ${mnyHouse() ? `
    <!-- HOUSE floor 8: the safety wall. The three "am I safe" panels
         are lifted OUT of the KPI grid and stand across the top —
         days of cash, cash on hand, the next obligation. Same node
         ids as the classic tiles, so _hydrateMoneySafetyTiles and
         refreshCashSection keep writing into exactly the same places:
         this is a hierarchy change, not a second set of plumbing. Net
         profit and the flow tiles follow underneath. -->
    <div class="hs-vault-safety">
      <div class="money-kpi money-kpi-mini hs-vault-panel" id="money-kpi-dayscash">
        <div class="mini-label">Days of cash</div>
        <div class="mini-num mono-num" id="money-kpi-dayscash-num">—</div>
        <div class="mini-sub" id="money-kpi-dayscash-sub"></div>
      </div>
      <div class="money-kpi money-kpi-mini hs-vault-panel" id="money-kpi-cash">
        <div class="mini-label">Known bank cash</div>
        <div class="mini-num mono-num" id="money-kpi-cash-num">—</div>
        <div class="mini-sub" id="money-kpi-cash-sub"></div><button class="btn-ghost" type="button" id="money-cash-evidence-btn" hidden>See the evidence</button>
      </div>
      <div class="money-kpi money-kpi-mini hs-vault-panel" id="money-kpi-payroll">
        <div class="mini-label">Next obligation</div>
        <div class="mini-num mono-num" id="money-kpi-payroll-num">—</div>
        <div class="mini-sub" id="money-kpi-payroll-sub"></div>
      </div>
    </div>

    <!-- One kinetic signature per room (the register prints), so the
         house tiles drop .stagger-rise — nothing else on this floor
         moves. -->
    <div class="money-kpi-grid hs-vault-rest" id="money-kpi-grid">
      <div id="money-hero" class="money-kpi money-kpi-hero">
        <div class="hero">
          <div class="hero-meta">
            <div class="hero-label">Net profit</div>
            <span class="pill pill-ok">Loading</span>
          </div>
          <div class="hero-num" id="money-net">— <span class="unit">NGN</span></div>
          <div class="hero-trend" id="money-vs"></div>
        </div>
      </div>
      <div class="stats money-kpi-stats" id="money-stats">
        <div class="stat money-kpi"><div class="stat-label">Revenue</div><div class="stat-val">—</div></div>
        <div class="stat money-kpi"><div class="stat-label">Expenses</div><div class="stat-val">—</div></div>
      </div>
      <div class="money-kpi money-kpi-mini" id="money-kpi-flow">
        <div class="mini-label">Net cashflow / mo
          <span class="pill capital-band-pill" id="money-kpi-runway-band" hidden></span>
        </div>
        <div class="mini-num mono-num" id="money-kpi-flow-num">—</div>
        <div class="mini-sub" id="money-kpi-flow-sub"></div>
      </div>
    </div>
    ` : `
    <div class="money-kpi-grid" id="money-kpi-grid">
      <div id="money-hero" class="money-kpi money-kpi-hero stagger-rise" style="--i:0">
        <div class="hero">
          <div class="hero-meta">
            <div class="hero-label">Net profit</div>
            <span class="pill pill-ok">Loading</span>
          </div>
          <div class="hero-num" id="money-net">— <span class="unit">NGN</span></div>
          <div class="hero-trend" id="money-vs"></div>
        </div>
      </div>

      <!-- Cash + safety mini-tiles. Cash/flow hydrated by
           refreshCashSection; days-cash + next-obligation by
           refreshMoneyInsights (same /money/insights payload as the
           full cards below — no extra round-trips). -->
      <div class="money-kpi money-kpi-mini stagger-rise" id="money-kpi-cash" style="--i:1">
        <div class="mini-label">Known bank cash</div>
        <div class="mini-num mono-num" id="money-kpi-cash-num">—</div>
        <div class="mini-sub" id="money-kpi-cash-sub"></div><button class="btn-ghost" type="button" id="money-cash-evidence-btn" hidden>See the evidence</button>
      </div>
      <div class="money-kpi money-kpi-mini stagger-rise" id="money-kpi-dayscash" style="--i:2">
        <div class="mini-label">Days cash vs floor</div>
        <div class="mini-num mono-num" id="money-kpi-dayscash-num">—</div>
        <div class="mini-sub" id="money-kpi-dayscash-sub"></div>
      </div>
      <div class="money-kpi money-kpi-mini stagger-rise" id="money-kpi-payroll" style="--i:3">
        <div class="mini-label">Next obligation</div>
        <div class="mini-num mono-num" id="money-kpi-payroll-num">—</div>
        <div class="mini-sub" id="money-kpi-payroll-sub"></div>
      </div>

      <div class="stats money-kpi-stats" id="money-stats">
        <div class="stat money-kpi stagger-rise" style="--i:4"><div class="stat-label">Revenue</div><div class="stat-val">—</div></div>
        <div class="stat money-kpi stagger-rise" style="--i:5"><div class="stat-label">Expenses</div><div class="stat-val">—</div></div>
      </div>

      <div class="money-kpi money-kpi-mini stagger-rise" id="money-kpi-flow" style="--i:6">
        <!-- Capital OS PR-4: runway-band verdict pill (BELOW FLOOR /
             DEFENSIVE / BALANCED / AGGRESSIVE-OK) hydrated by
             refreshMoneyCapital from /api/finance/capital-metrics.
             Its own node — refreshCashSection owns -sub via textContent
             and must never clobber it. -->
        <div class="mini-label">Net cashflow / mo
          <span class="pill capital-band-pill" id="money-kpi-runway-band" hidden></span>
        </div>
        <div class="mini-num mono-num" id="money-kpi-flow-num">—</div>
        <div class="mini-sub" id="money-kpi-flow-sub"></div>
      </div>
    </div>`}

    ${canFinance ? `
    <!-- Money-page redesign 2026-06-07: animated SVG charts. Fed entirely
         from data already fetched (insights sparkline + accounting
         summary breakdown) — no extra round-trips. -->
    <div class="section-h"><h2>Trends</h2><span class="meta" id="money-charts-window"></span></div>
    <div class="money-charts" id="money-charts">
      ${[1,2,3].map(() => `<div class="money-chart-card chart-skel"><div class="skel skel-line" style="width:40%;height:12px;"></div><div class="skel skel-line" style="width:100%;height:90px;margin-top:14px;border-radius:8px;"></div></div>`).join('')}
    </div>
    ` : ''}

    <!-- PR MONEY-CASH-WALKIN (2026-07-04): "Walk-in cash by store"
         mounts here — offline-gateway Shopify orders per store.
         Revenue-by-location itself still lives on Home only
         (LATE-NIGHT-POLISH decision). Unhidden by the render when
         the period has walk-in orders. -->
    <div id="money-by-location" hidden></div>

    ${canFinance ? `
    <!-- v3.0p32 PR 3: Money insights tile grid.
         PR MONEY-COCKPIT: 6 skeletons to match the 6 real cards
         (expenses / payroll / days-cash / covenant / floor / liquidity)
         + the resolved-window meta line. -->
    <div class="section-h"><h2>Insights</h2><span class="meta" id="money-insights-window"></span></div>
    <div id="money-insights-grid" class="data-card-grid">
      ${[1,2,3,4,5,6].map(() => `<div class="data-card" style="height: 110px; opacity: 0.4;"></div>`).join('')}
    </div>
    ` : ''}

    ${canFinance ? `
    <!-- Capital OS PR-4: Capital card row — debt-service wall (next 6
         months) + receivables countdown. Hydrated by
         refreshMoneyCapital() from /api/finance/capital-metrics; the
         idle-cash line lands in the digest strip, the runway-band
         verdict pill on the Net-cashflow KPI. -->
    <div class="section-h"><h2>Capital</h2><span class="meta" id="money-capital-window"></span></div>
    <div id="money-capital-row" class="data-card-grid">
      ${[1,2].map(() => `<div class="data-card" style="height: 130px; opacity: 0.4;"></div>`).join('')}
    </div>
    ` : ''}

    ${canFinance ? `
    <!-- CRUX Custom P&L thin slice (2026-08-20): the factory tier entry
         surface. Mig 467's factory/wholesale columns sat empty on ALL
         14,399 cost rows because no write path existed — this section +
         /api/crux-custom/* are that path. Empty-honest at 0% coverage
         (law 7); the contribution line lights only when tiers exist.
         Hydrated by refreshMoneyCruxCustom(); existing Vault classes
         only, no CSS changes. -->
    <div class="section-h"><h2>CRUX Custom — factory economics</h2><span class="meta" id="money-cruxcustom-meta"></span></div>
    <div id="money-cruxcustom" class="stagger-rise" style="--i:0">
      <div class="data-card" style="height: 110px; opacity: 0.4;"></div>
    </div>
    ` : ''}

    ${canFinance ? `
    <!-- HORIZON-1 wave 2 (2026-08-29): the second cash account. For a
         vertical retailer, inventory IS cash wearing clothes — ₦
         locked, GMROI (the margin bar, target 2.5-4.0), turns, with
         the cost-coverage caveat disclosed (a GMROI riding the 45%
         default is a shape, not a measurement). Hydrated by
         refreshMoneyInvCash() from /api/finance/gmroi; existing Vault
         classes only, no CSS changes — the CRUX Custom precedent. -->
    <div class="section-h"><h2>Inventory — the second cash account</h2><span class="meta" id="money-invcash-meta"></span></div>
    <div id="money-invcash" class="stagger-rise" style="--i:0">
      <div class="data-card" style="height: 96px; opacity: 0.4;"></div>
    </div>
    ` : ''}

    ${canFinance ? `
    <!-- HORIZON-1 wave 3 (2026-08-29): the four walls, per store — the
         question gate №7 asks ("is this store paying for itself?"),
         answered per location: revenue − COGS (measured ratio or the
         flagged 45% default) − rent bundle − store payroll, occupancy
         vs the 10–15% band, and the above-the-walls burden named as
         one honest line. Honors the period chips. Hydrated by
         refreshMoneyStorePnl() from /api/finance/store-pnl; existing
         Vault classes only. -->
    <div class="section-h"><h2>Stores — the four walls</h2><span class="meta" id="money-storepnl-meta"></span></div>
    <div id="money-storepnl" class="stagger-rise" style="--i:0">
      <div class="data-card" style="height: 120px; opacity: 0.4;"></div>
    </div>
    ` : ''}

    ${canFinance ? `
    <!-- HORIZON-1 wave 5 (2026-08-29): the next naira. Every capital
         lane's own evidence beside one hurdle, a deterministic first
         call, nothing moved — each lane's action stays behind its own
         door (envelope approval, reorder tray, debt payment, the
         masterplan gates). Hydrated by refreshMoneyArbiter() from
         /api/finance/capital-arbiter; existing Vault classes only. -->
    <div class="section-h"><h2>The next naira</h2><span class="meta" id="money-arbiter-meta"></span></div>
    <div id="money-arbiter" class="stagger-rise" style="--i:0">
      <div class="data-card" style="height: 110px; opacity: 0.4;"></div>
    </div>
    ` : ''}

    ${canFinance ? `
    <!-- PR money-valuation-tile-restore (2026-06-07): the Business
         Valuation block lost its mount in the 06-07 Money redesign — it
         had been the trailing card in the generic Insights grid and got
         dropped when that array was re-curated. Restored here as a
         first-class, dedicated section (KPIs → Trends → Insights →
         Valuation → Cash) so the liquidation floor + revenue/EBITDA
         multiples + low/base/stretch range stay front-and-centre on the
         operator's daily screen. Hydrated by refreshMoneyInsights() into
         #money-valuation from the same /money/insights payload (no extra
         round-trip). -->
    <div class="section-h"><h2>Business valuation</h2></div>
    <div id="money-valuation" class="money-valuation-slot stagger-rise" style="--i:0">
      <div class="data-card val-card" style="opacity: 0.4;">
        <div class="skel skel-line" style="width: 45%; height: 13px;"></div>
        <div class="skel skel-line" style="width: 100%; height: 56px; margin-top: 16px; border-radius: 10px;"></div>
        <div class="skel skel-line" style="width: 80%; height: 13px; margin-top: 16px;"></div>
      </div>
    </div>
    ` : ''}

    <!-- HOUSE floor 8: the four blocks below (LAB1 · Vendors · AI
         credits · Pricing OS) are the experiments. On the house skin
         they leave the wall and mount inside renderVaultWorkbench() —
         one tap away, never in the morning path. Their hydrators
         already early-return on a missing host, so refreshMoney()
         stays exactly as it is. -->
    ${canFinance && !mnyHouse() ? `
    <!-- LAB1 leverage experiment (operator directive 2026-07-09):
         borrowed ₦47.9M WCL to buy LAB1-tagged stock — does the
         cohort's contribution beat loan + interest? Hydrated by
         refreshMoneyLab1() from /api/finance/lab1. -->
    <div class="section-h"><h2>Test 1 — borrow to buy stock</h2><span class="meta">debt-funded stock vs its cost of capital</span></div>
    <div id="money-lab1" class="stagger-rise" style="--i:0">
      <div class="data-card" style="opacity: 0.4;">
        <div class="skel skel-line" style="width: 45%; height: 13px;"></div>
        <div class="skel skel-line" style="width: 100%; height: 44px; margin-top: 16px; border-radius: 10px;"></div>
      </div>
    </div>
    ` : ''}

    ${canFinance && !mnyHouse() ? `
    <!-- Vendor analysis (operator directive 2026-07-09): per-vendor
         P&L + ENO commission ledger. Hydrated by refreshMoneyVendors()
         from /api/finance/vendors. -->
    <div class="section-h"><h2>Vendors</h2><span class="meta">per-vendor P&L · commission ledger</span></div>
    <div id="money-vendors" class="stagger-rise" style="--i:0">
      <div class="data-card" style="opacity: 0.4;">
        <div class="skel skel-line" style="width: 45%; height: 13px;"></div>
        <div class="skel skel-line" style="width: 100%; height: 44px; margin-top: 16px; border-radius: 10px;"></div>
      </div>
    </div>
    ` : ''}

    ${canFinance && !mnyHouse() ? `
    <!-- API wallets (mig 529, 2026-07-27): per-provider balance/runway/
         renewal board — "always aware of balance so nothing breaks".
         Hydrated by refreshMoneyWallets() from /api/finance/wallets. -->
    <div class="section-h"><h2>Ad wallets and tool credits</h2><span class="meta">what's left · how long it lasts · renewal dates</span></div>
    <div id="money-wallets" class="stagger-rise" style="--i:0">
      <div class="data-card" style="opacity: 0.4;">
        <div class="skel skel-line" style="width: 45%; height: 13px;"></div>
        <div class="skel skel-line" style="width: 100%; height: 44px; margin-top: 16px; border-radius: 10px;"></div>
      </div>
    </div>
    ` : ''}

    ${canFinance && !mnyHouse() ? `
    <!-- Pricing OS (mig 441, 2026-07-09): tier-IMU targets on true
         landed cost. Weekly drift cron fills the proposal tray; the
         approve tap here is the ONLY path to a live Shopify price.
         Hydrated by refreshMoneyPricing() from /api/finance/pricing. -->
    <div class="section-h"><h2>Pricing OS</h2><span class="meta">tier targets · reprice tray</span></div>
    <div id="money-pricing" class="stagger-rise" style="--i:0">
      <div class="data-card" style="opacity: 0.4;">
        <div class="skel skel-line" style="width: 45%; height: 13px;"></div>
        <div class="skel skel-line" style="width: 100%; height: 44px; margin-top: 16px; border-radius: 10px;"></div>
      </div>
    </div>
    ` : ''}

    ${canFinance ? `
    <!-- PR MONEY-COCKPIT: the runway + total-cash tiles folded into
         the top KPI band (row 1). What remains here is the drill-down:
         per-account list, snapshot buttons and the bank-statement
         uploader — behind a <details> (prc-pack precedent) so the
         daily screen stays compact. -->
    <div class="section-h"><h2>Cash</h2><span class="meta" id="money-cash-window"></span></div>
    <details class="prc-pack money-cash-drill" id="money-cash-drill">
      <summary class="prc-pack-sum" id="money-cash-drill-sum">Accounts · snapshots · statement upload</summary>
      <div class="prc-pack-body">
        <div id="cash-accounts-list"></div>

        <!-- Capital OS PR-3: MULTI-file statement uploader. Every file
             routes through the entities ingest engine (bank_ingest):
             account matched via entity_accounts, rows normalized into
             bank_transactions (dedup-hashed — re-upload inserts 0),
             closing balance reconciled against account_balance_snapshots,
             T+28 family receivables auto-created. Per-file result rows
             render below. -->
        <div id="bank-statement-upload" class="card" style="margin-top: 12px; padding: 12px 14px;">
          <div class="row-label" style="margin-bottom: 6px;">Upload bank statements</div>
          <div class="row-value" style="font-size: 12px; opacity: 0.7; margin-bottom: 10px;">
            Multi-file: Moniepoint xlsx · GTB / GTWORLD / Providus PDF · GTB HTML-xls · Alipay CSV.
            Re-uploads are safe — duplicates skip.
          </div>
          <input id="bank-statement-file" type="file" accept=".xlsx,.xls,.pdf,.csv" multiple
                 style="display: block; width: 100%; font-size: 13px;" />
          <button id="bank-statement-submit" class="btn-primary"
                  style="margin-top: 10px; width: 100%;" disabled>Upload</button>
          <div id="bank-statement-result" style="margin-top: 10px; font-size: 12px;"></div>
        </div>

        <!-- Phase A accounting intake (mig 572): statement freshness per
             account + door status, fed by
             /api/finance/statement-intake/summary. Hidden until the
             endpoint answers. -->
        <div id="bank-intake-status" class="card" style="margin-top: 12px; padding: 12px 14px; display:none;">
          <div class="row-label" style="margin-bottom: 6px;">Bank intake</div>
          <div id="bank-intake-lines" style="font-size: 12px;"></div>
        </div>
      </div>
    </details>
    ` : ''}
    <!-- PR MONEY-COMPLETE: removed "Recent movements" section here.
         It duplicated the "Recent" transactions list below (operator
         flagged the two-section redundancy). The single Recent list
         under Quick Log now covers both cash + non-cash movements. -->

    <!-- HOUSE floor 8: the wall/register split. Under the house skin
         the logging half of the page becomes two columns — the reading
         column (P&L, budgets, the chat prefills) beside the register,
         which prints. Classic keeps the single stacked scroll. -->
    ${mnyHouse() ? '<div class="hs-vault-cols"><div class="hs-vault-col">' : ''}

    <!-- HOUSE floor 8 DEFECT 1: "+ Log a line" is a REAL control now —
         it opens a sheet that POSTs to /api/accounting/expenses or
         /revenue (both endpoints existed with zero frontend callers;
         every "logged" naira went through a chat prefill and hoped the
         agent's finance tool wrote the row). Outside canFinance on
         purpose: P.ACCOUNTING_LOG is granted to every member. On the
         house skin the same control rides the register head instead —
         one act per screen, so only ever ONE #money-log-line exists. -->
    <div class="section-h"><h2>Quick log</h2>
      ${mnyHouse() ? '' : '<button class="bks-tool-btn" id="money-log-line" type="button">+ Log a line</button>'}
    </div>
    <!-- PR MONEY-COCKPIT: the "Report → P&L" chat-prefill button is
         replaced by the real compact P&L card below (books-backed).
         The other quick-log buttons stay — they are the conversational
         fallback now that the register has a real posting door. -->
    <div class="suggest-grid" id="money-quick">
      <button class="suggest" type="button" data-suggest-msg="I spent ">
        <span class="lbl">Expense</span>
        <span>Log money out</span>
      </button>
      <button class="suggest" type="button" data-suggest-msg="I made ">
        <span class="lbl">Revenue</span>
        <span>Log money in</span>
      </button>
      <button class="suggest" type="button" data-suggest-msg="compare this month to last month">
        <span class="lbl">Compare</span>
        <span>Period vs period</span>
      </button>
    </div>

    ${canFinance ? `
    <!-- PR MONEY-COCKPIT: compact month-to-date P&L, hydrated from the
         Books endpoint (/api/accounting/books/pnl) via bksLoadCard so a
         failure renders a quiet per-card retry. Links into the Books
         tab for the full statement. -->
    <div class="section-h"><h2>P&amp;L</h2><span class="meta" id="money-pnl-meta"></span>${
      mnyHouse() ? '<span class="hs-eyebrow hs-vault-trace">every line traces to a source row</span>' : ''}</div>
    <div id="money-pnl-card" class="card bks-card">${bksSkel(5)}</div>
    ` : ''}

    <div id="money-budgets-section"></div>

    ${mnyHouse() ? '</div><div class="hs-vault-col hs-vault-col-reg">' : ''}

    ${mnyHouse() ? `
    <!-- HOUSE floor 8: the register reads as a receipt printer — a
         perforated head, the accent spent once on "+ log a line", and
         the tape below. #money-tx-list keeps its id so
         _hydrateMoneySummary paints into the same node. -->
    <div class="hs-vault-print">
      <div class="hs-vault-print-head">
        <span class="hs-eyebrow">Recent — the register</span>
        <span class="hs-vault-slot" aria-hidden="true"></span>
        <button class="hs-vault-log" id="money-log-line" type="button">+ log a line</button>
      </div>
      <div class="hs-vault-print-sub">
        <span class="meta" id="money-tx-count"></span>
        <span class="meta" id="money-reg-inbox"></span>
        <button class="bks-tool-btn" type="button" id="money-book-entries" data-money-book-entries="${moneyResolveView('ledger').denied ? 'Open the transaction register' : 'Open Book entries'}">View all</button>
      </div>
      <div id="money-tx-list" class="hs-vault-tape">
        ${[1,2,3].map(() => `<div class="card" style="height: 64px; opacity: 0.4;"></div>`).join('')}
      </div>
    </div>
    <p class="hs-vault-note">Re-uploads are safe — duplicates skip. Correcting a category teaches the categorizer.</p>
    ` : `
    <div class="section-h">
      <h2>Recent</h2>
      <span class="meta" id="money-tx-count"></span>
      <span class="meta" id="money-reg-inbox"></span>
      <button class="bks-tool-btn" type="button" id="money-book-entries" data-money-book-entries="${moneyResolveView('ledger').denied ? 'Open the transaction register' : 'Open Book entries'}">View all</button>
    </div>
    <div id="money-tx-list">
      ${[1,2,3].map(() => `<div class="card" style="height: 64px; opacity: 0.4;"></div>`).join('')}
    </div>`}

    ${mnyHouse() ? '</div></div>' : ''}

    <!-- 2026-06-09: the "Customers — true returning rate" section moved off
         Money — that data now lives in /marketing > Customers tab (shipped).
         In its place we surface "Store economics" (per-store P&L), which the
         backend already computes (/api/locations/grid) but was never shown.
         Decision-first: the status badge + cost-coverage ratio answer
         "which stores pull their weight". -->
    ${canFinance ? `
    <div class="section-h" style="margin-top: 28px;">
      <h2>Store economics</h2>
      <span class="meta" id="store-economics-window">last 90 days</span>
    </div>
    <div id="store-economics-list">
      ${[1,2,3].map(() => `<div class="card" style="height: 86px; opacity: 0.4; margin-bottom: 10px;"></div>`).join('')}
    </div>
    ` : ''}

    <!-- 2026-06-06: Rep capture rate dashboard MOVED to Marketing >
         Reps section per Daniel — that page already resolves the
         shopify_user_id → name mapping via staff_performance.rep_performance().
         Capture rate is now an extra column on the existing rep table there. -->

    <!-- Money-page redesign 2026-06-07 (Daniel): the "Data tools" block
         (Refresh Shopify costs / Vet bronze / Re-run approvals / shadow
         syncs / backfill / parity report) was internal plumbing that
         didn't belong on the operator's daily Money screen. Removed from
         the rendered view. PR MONEY-COCKPIT: the unmounted
         bindMoneyDataTools() helper was deleted outright (dead code,
         zero call sites); the /api/accounting + /api/eon-sync endpoints
         it drove remain available for ops use. -->
  `;
}

// 2026-06-09: bindMoneyInsights() (true-returning-rate + capture-rate
// hydrate) was removed — that surface moved to /marketing > Customers tab.
// Its replacement is bindStoreEconomics(), which surfaces the existing,
// already-computed per-store P&L from /api/locations/grid.
async function bindStoreEconomics() {
  const host = document.getElementById('store-economics-list');
  if (!host) return;  // role without canFinance — section not rendered.

  // Worst status first so problems surface. Retail at_risk/losing_money
  // float to the top; non-retail (warehouse/lab) sink to the bottom but
  // still render with their own status + cost so they aren't mistaken
  // for "₦0 healthy".
  const STATUS_RANK = {
    losing_money: 0,
    at_risk: 1,
    underproducing: 2,
    marginal: 3,
    under_utilized: 4,
    in_buildout_paused: 5,
    in_buildout: 6,
    dormant: 7,
    ramping: 8,
    serving_well: 9,
    over_capacity: 9,
    producing: 9,
    healthy: 10,
  };
  // Pill tone per status. Problem states get danger/warn; everything
  // healthy/working is ok; passive non-retail states stay neutral.
  const STATUS_TONE = {
    losing_money: 'pill-danger',
    at_risk: 'pill-danger',
    underproducing: 'pill-danger',
    over_capacity: 'pill-warn',
    marginal: 'pill-warn',
    under_utilized: 'pill-warn',
    in_buildout_paused: 'pill-warn',
    dormant: 'pill-warn',
    in_buildout: '',
    ramping: '',
    healthy: 'pill-ok',
    serving_well: 'pill-ok',
    producing: 'pill-ok',
  };
  const PURPOSE_LABEL = {
    warehouse: 'Warehouse · fulfilment',
    brand_investment: 'Brand investment · Lab',
  };
  const statusText = (s) => (s || 'unknown').replace(/_/g, ' ');

  try {
    const r = await api('/api/locations/grid');
    const cards = (r && r.cards) || [];
    if (!cards.length) {
      host.innerHTML = `<div class="card" style="padding:16px;color:var(--ink-mute);font-size:13px;">No locations configured.</div>`;
      return;
    }

    cards.sort((a, b) => {
      const ra = STATUS_RANK[(a.economics || {}).status] ?? 99;
      const rb = STATUS_RANK[(b.economics || {}).status] ?? 99;
      return ra - rb;
    });

    const rowHtml = (c) => {
      const eco = c.economics || {};
      const status = eco.status;
      const tone = STATUS_TONE[status] || '';
      const purpose = c.location_purpose;
      const isRetail = purpose === 'retail';

      // Revenue: retail has revenue{ngn}; warehouse/lab are revenue-0 by
      // design — show their purpose instead of a misleading "₦0".
      const revNgn = (eco.revenue || {}).ngn;
      // Cost: retail/warehouse use `cost.total_ngn`; lab uses
      // `operating_cost.total_ngn`.
      const costNgn = (eco.cost || eco.operating_cost || {}).total_ngn;
      const ratio = (eco.ratios || {}).revenue_to_total_cost;
      const marginPct = (eco.contribution || {}).margin_pct;

      const ratioStr = (ratio != null)
        ? `${ratio.toFixed(ratio >= 10 ? 0 : 1)}× cost covered`
        : null;
      const marginStr = (marginPct != null) ? `${marginPct.toFixed(0)}% margin` : null;

      // Secondary line: retail shows revenue + ratio + margin; non-retail
      // shows what it is + its cost so the operator reads it correctly.
      let metrics;
      if (isRetail) {
        const bits = [`${fmtNGN(revNgn)} revenue`, `${fmtNGN(costNgn)} cost`];
        const tail = [ratioStr, marginStr].filter(Boolean).join(' · ');
        metrics = bits.join(' · ') + (tail ? ` · ${tail}` : '');
      } else {
        metrics = `${PURPOSE_LABEL[purpose] || purpose} · ${fmtNGN(costNgn)} cost · no retail revenue`;
      }

      const badge = status
        ? `<span class="pill ${tone}">${statusText(status)}</span>`
        : '';

      return `
        <div class="row" style="cursor:default;border-radius:var(--r-md);margin-bottom:10px;align-items:flex-start;">
          <div class="row-content">
            <div class="row-label" style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;">
              <span>${esc(c.location_name || 'Location')}</span>
              ${badge}
            </div>
            <div class="row-value" style="margin-top:4px;">${metrics}</div>
          </div>
        </div>`;
    };

    // PR MONEY-COCKPIT: progressive disclosure — problem stores render
    // in full; the healthy tail collapses behind "Show all N healthy
    // stores" (renderCollapsedList with initial: 0). One healthy store
    // isn't worth a toggle — render it inline.
    const OK_STATUSES = new Set(['healthy', 'serving_well', 'producing']);
    const problems = cards.filter(c => !OK_STATUSES.has((c.economics || {}).status));
    const healthy = cards.filter(c => OK_STATUSES.has((c.economics || {}).status));
    if (healthy.length > 1) {
      host.innerHTML = problems.map(rowHtml).join('')
        + `<div id="store-econ-healthy"></div>`;
      renderCollapsedList(host.querySelector('#store-econ-healthy'), healthy,
        rowHtml, { initial: 0, label: 'healthy store' });
    } else {
      host.innerHTML = cards.map(rowHtml).join('');
    }
  } catch (e) {
    console.warn('store economics failed', e);
    host.innerHTML = `<div class="card" style="padding:16px;color:var(--danger);font-size:13px;">Couldn't load the store figures just now. Try again in a moment.</div>`;
  }
}


// ── Capital OS PR-5: EON Financial Adviser card (Money → Overview) ──
// Latest weekly CFO memo: finding headlines collapsed; full memo markdown
// + personal addendum (owner only, gated server-side) + Academy "Sharpen"
// links on expand. The Money page never blocks on the adviser lane.
//
// M5c: every read state is said in plain words. A failed or refused read
// is not "no advice"; a saved note keeps its own date and is never called
// current; paused cash advice is never an all-clear. One read per card and
// scope at a time; a reply paints only the card and the person, tenant and
// role it was asked for, and only if it is the newest read.
function _adviserDate(value) {
  const day = /^(\d{4}-\d{2}-\d{2})/.exec(typeof value === 'string' ? value : '')?.[1];
  const stamp = day ? Date.parse(day + 'T00:00:00Z') : NaN;
  if (!Number.isFinite(stamp) || new Date(stamp).toISOString().slice(0, 10) !== day) return null;
  return new Intl.DateTimeFormat('en-GB', {day:'numeric', month:'long', year:'numeric', timeZone:'UTC'}).format(stamp);
}
async function refreshAdviserCard() {
  const slot = document.getElementById('money-adviser-slot');
  if (!slot) return;
  const scope = _viewReadScope();
  // An approved instruction shown for another person, workspace, role or
  // View-as goes at once, before this read answers or fails.
  const approvalNote = document.getElementById('money-approval-note');
  if (approvalNote && approvalNote._approvalScope !== scope) approvalNote.remove();
  if (slot._adviserReading && slot._adviserScope === scope) return slot._adviserReading;
  const seq = slot._adviserSeq = (slot._adviserSeq || 0) + 1;
  slot._adviserScope = scope;
  const current = () => slot.isConnected && slot._adviserSeq === seq && _viewReadScope() === scope
    && document.getElementById('money-adviser-slot') === slot;
  const notice = (text, retry) => {
    slot.innerHTML = `<div class="card" id="money-adviser-card" style="padding:16px; margin: 12px 0;"><b>EON Adviser</b>
      <p class="meta" role="status" style="margin:8px 0 0;">${esc(text)}</p>${retry ? '<button class="chip" type="button" data-adviser-retry style="margin-top:8px;">Try again</button>' : ''}</div>`;
    slot.querySelector('[data-adviser-retry]')?.addEventListener('click', () => refreshAdviserCard());
  };
  const unavailable = 'The adviser note did not load. This does not mean there is no advice.';
  const incomplete = 'The adviser note is incomplete, so it is not shown. This does not mean there is no advice.';
  // A reply dropped because the view changed under it, with no newer read
  // for this card, is said too: the card is never left on "Reading".
  const dropped = () => { if (slot.isConnected && slot._adviserSeq === seq) notice(unavailable, true); };
  const read = (async () => {
    // A saved note stays up while it is re-read only for the same person,
    // tenant, role and View-as it was drawn for; any other view clears it now.
    if (!slot.querySelector('[data-adviser-memo]') || slot._adviserPaintedScope !== scope) notice('Reading the adviser note…', false);
    let data;
    try { data = await api('/api/finance/adviser/latest', { _isCurrent: current }); }
    catch (err) {
      if (!current()) return dropped();
      return err?.status === 403 ? notice('Your role does not include the adviser note.', false) : notice(unavailable, true);
    }
    if (!current()) return dropped();
    if (!data || data.ok !== true || !('memo' in data)) return notice(unavailable, true);
    if (data.memo === null) return notice('No adviser note has been saved yet.', false);
    const memo = data.memo;
    if (!memo || typeof memo !== 'object' || typeof memo.memo_md !== 'string' || !memo.memo_md.trim()
        || !Array.isArray(memo.findings || [])) {
      return notice(incomplete, true);
    }
    const sevColor = (s) => s === 'red' ? 'var(--bad, #ef4444)'
      : s === 'amber' ? 'var(--warn, #f59e0b)' : 'var(--muted, #9ca3af)';
    const findings = (memo.findings || []).filter(f => f && typeof f === 'object');
    const reds = findings.filter(f => f.severity === 'red').length;
    const ambers = findings.filter(f => f.severity === 'amber').length;
    const paused = data.cash_paused === true || data.cash_unknown === true;
    const pausedCount = Array.isArray(data.paused_findings) ? data.paused_findings.length : 0;
    const rows = findings.map(f => `<li data-adviser-finding="${esc(typeof f.id === 'string' ? f.id.toLowerCase() : '')}" style="margin:4px 0; overflow-wrap:anywhere;"><span class="chip" style="pointer-events:none; border-color:${sevColor(f.severity)}; color:${sevColor(f.severity)}">${f.severity === 'red' ? 'Red' : f.severity === 'amber' ? 'Amber' : 'Note'}</span>
      ${esc(typeof f.headline === 'string' && f.headline.trim() ? f.headline.trim() : 'A finding without a headline')}</li>`).join('');
    const week = _adviserDate(memo.week), written = _adviserDate(memo.generated_at);
    const dated = week || written
      ? `Saved note${week ? ` · week of ${week}` : ''}${written ? ` · written ${written}` : ''}` : 'Saved note · date unavailable';
    const pauseLine = paused
      ? `<p class="meta" style="margin:8px 0 0;">${esc(typeof data.cash_sentence === 'string' && data.cash_sentence.trim() ? data.cash_sentence.trim() : 'Cash balances are not confirmed.')} Advice that depends on cash is paused until the balances are confirmed${pausedCount ? ` (${pausedCount} ${pausedCount === 1 ? 'finding' : 'findings'} held back)` : ''}.</p>` : '';
    const sharpen = (Array.isArray(memo.courses) ? memo.courses : []).filter(c => c && typeof c === 'object').map(c =>
      `<a href="/academy" style="text-decoration:underline">${esc(c.title)}</a>`
    ).join(' · ');
    const hasActions = (memo.prepared_action_ids || []).length > 0;
    slot.innerHTML = `
    <div class="card" id="money-adviser-card" data-adviser-memo data-adviser-week="${esc(typeof memo.week === 'string' ? memo.week.slice(0, 10) : '')}" style="padding:16px; margin: 12px 0;">
      <div style="display:flex; align-items:baseline; gap:10px; flex-wrap:wrap;">
        <b>EON Adviser</b>
        <span class="meta">${dated}</span>
        <span class="meta">${findings.length ? `${findings.length} ${findings.length === 1 ? 'finding' : 'findings'} listed${reds || ambers ? ` (${reds} red, ${ambers} amber)` : ''}` : ''}</span>
        <button class="chip" id="money-adviser-toggle" type="button" aria-expanded="false" aria-controls="money-adviser-body" style="margin-left:auto;">Read memo</button>
      </div>
      ${pauseLine}
      ${rows ? `<ul style="margin:8px 0 0; padding:0; list-style:none;">${rows}</ul>`
        : `<p class="meta" style="margin:8px 0 0;">No findings are listed in this saved note.${paused ? ' Cash advice is paused, so this is not an all-clear.' : ''}</p>`}
      <div id="money-adviser-body" hidden style="margin-top:12px; border-top:1px solid var(--border, #2a2a2a); padding-top:12px; overflow-wrap:anywhere;">
        <div class="adviser-memo-md">${renderMarkdownToHtml(memo.memo_md)}</div>
        ${memo.personal_md ? `
        <div style="margin-top:12px;">
          <div class="meta" style="margin-bottom:4px;">Personal book (only you see this)</div>
          <div class="adviser-memo-md">${renderMarkdownToHtml(memo.personal_md)}</div>
        </div>` : ''}
        ${hasActions ? `
        <div class="meta" style="margin-top:12px;">Prepared moves are waiting in your approvals tray — EON never
        moves bank money; approving logs the decision and tasks the bank-side execution to you.</div>` : ''}
        ${sharpen ? `<div class="meta" style="margin-top:8px;">Sharpen: ${sharpen}</div>` : ''}
      </div>
    </div>`;
    slot._adviserPaintedScope = scope;
    const toggle = slot.querySelector('#money-adviser-toggle');
    const body = slot.querySelector('#money-adviser-body');
    if (toggle && body) {
      toggle.addEventListener('click', () => {
        body.hidden = !body.hidden;
        toggle.textContent = body.hidden ? 'Read memo' : 'Collapse';
        toggle.setAttribute('aria-expanded', String(!body.hidden));
      });
    }
  })().catch(() => { if (current()) notice('The adviser note could not be shown here. This does not mean there is no advice.', true); });

  slot._adviserReading = read;
  try { await read; } finally { if (slot._adviserSeq === seq) slot._adviserReading = null; }
}

// One approved bank instruction, opened from its task. It is dated by its own
// memo week and never replaced by the newest memo. Reading it moves no money,
// approves nothing and starts nothing; a reply for another person, workspace
// or role is dropped, and so is the note.
async function moneyShowApproval() {
  const named = State.moneyApprovalRef; State.moneyApprovalRef = null;
  const slot = document.getElementById('money-adviser-slot'), scope = _viewReadScope();
  if (!named || named.scope !== scope || !slot) return;
  const details = slot.closest('details'); if (details) details.open = true;
  document.getElementById('money-approval-note')?.remove();
  const note = document.createElement('section');
  note.id = 'money-approval-note'; note.className = 'card'; note.setAttribute('role', 'status'); note._approvalScope = scope;
  note.style.cssText = 'padding:16px; margin:12px 0; overflow-wrap:anywhere;';
  slot.before(note);
  const current = () => note.isConnected && _viewReadScope() === scope;
  const say = (text, retry) => {
    note.innerHTML = `<b>Approved bank instruction</b><p class="meta" style="margin:8px 0 0;">${esc(text)}</p>${retry ? '<button class="chip" type="button" data-approval-retry style="margin-top:8px; min-height:44px;">Try again</button>' : ''}`;
    note.querySelector('[data-approval-retry]')?.addEventListener('click', () => { State.moneyApprovalRef = named; moneyShowApproval(); });
  };
  const failed = 'The approved instruction did not load. This does not mean it is missing.';
  // Same rule as the private activity panel: the owner, not viewing as someone else.
  if ((State.member?.base_role || State.member?.role) !== 'owner' || State.impersonating) return say('Only the owner can see approved bank instructions.', false);
  say('Reading the approved instruction…', false);
  let data;
  try { data = await api('/api/finance/adviser/approval/' + encodeURIComponent(named.ref), { _isCurrent: current }); }
  catch (err) {
    if (!current()) return note.remove();
    if (err?.status === 404) return say('This approved instruction can’t be shown. It may not be approved, or it may come from an older card EON can’t check. Opening it here does not move money.', false);
    return err?.status === 403 ? say('Only the owner can see approved bank instructions.', false) : say(failed, true);
  }
  if (!current()) return note.remove();
  const a = data?.approval;
  if (data?.ok !== true || !a || a.ref !== named.ref || typeof a.instruction !== 'string' || !a.instruction.trim()
      || !_adviserDate(a.week) || (a.finding_id != null && typeof a.finding_id !== 'string')) return say(failed, true);
  note.innerHTML = `<b>Approved instruction from the memo of week ${esc(_adviserDate(a.week))}.</b>${typeof a.headline === 'string' && a.headline.trim() ? `<p class="meta" style="margin:8px 0 0;">${esc(a.headline)}</p>` : ''}<p style="margin:8px 0 0;">${esc(a.instruction)}</p><p class="meta" style="margin:8px 0 0;">Creating or starting the task did not move money.</p><p class="meta" data-approval-match style="margin:8px 0 0;">Checking the saved adviser note…</p>`;
  // Compared with the saved note the card shows, by its own date: that note
  // may be an older one, so it is never called this week's.
  if (!slot.querySelector('[data-adviser-memo]') || slot._adviserPaintedScope !== scope) await refreshAdviserCard().catch(() => {});
  if (!current()) return note.remove();
  const match = note.querySelector('[data-approval-match]'), card = slot.querySelector('#money-adviser-card[data-adviser-memo]');
  const row = card && slot._adviserPaintedScope === scope && card.dataset.adviserWeek === a.week && a.finding_id
    ? [...card.querySelectorAll('[data-adviser-finding]')].find(li => li.dataset.adviserFinding === a.finding_id) : null;
  const noteWeek = card && slot._adviserPaintedScope === scope ? _adviserDate(card.dataset.adviserWeek) : null;
  if (!row) {
    match.textContent = !card || slot._adviserPaintedScope !== scope ? 'No saved adviser note is shown here, so it is not compared.'
      : noteWeek ? `The saved adviser note shown below is for the week of ${noteWeek} and does not list this finding.`
      : 'The saved adviser note shown below has no week date, so it is not compared.';
    return;
  }
  match.innerHTML = `This finding is also in the saved adviser note for the week of ${esc(noteWeek)}. <button class="chip" type="button" data-approval-show style="min-height:44px;">Show it</button>`;
  match.querySelector('[data-approval-show]').addEventListener('click', () => { row.scrollIntoView({block: 'center'}); row.setAttribute('tabindex', '-1'); row.focus({preventScroll: true}); });
}

async function bindMoney() {
  mountMoneyWorkspace(document.getElementById('money-workspace'));
  mountMoneyActivity(document.getElementById('money-workspace'),{api,owner:(State.member?.base_role||State.member?.role)==='owner'&&!State.impersonating});
  // v3.0p21 9xx: chip wiring for the new date picker.
  State.moneyPeriod = State.moneyPeriod || 'this_month';
  // v3.0p106 BOOKS+TAX: tab dispatch. Books/Tax own their whole view —
  // none of the Overview hydration below runs for them (their DOM
  // doesn't exist), and their endpoints are only hit when opened.
  bindMoneyTabBar();
  // Procurement (v3.0p107): hydrate the owner Requests strip +
  // packaging expandable on every Money tab (mount lives in the tab
  // bar). No-ops for non-approver roles; fail-soft on fetch errors.
  if (typeof prcHydrateMoneyStrip === 'function') prcHydrateMoneyStrip();
  {
    const target = moneyResolveView(State.moneyTab || 'overview');
    const _tabOk = target.canFinance;
    const _tab = target.key;
    if (_tabOk && _tab === 'books') { bindBooksTab(); return; }
    if (_tabOk && _tab === 'tax') { bindTaxTab(); return; }
    if (_tabOk && _tab === 'ledger') { bindMoneyLedger(); return; }
    if (_tabOk && _tab === 'projection') { bindMoneyProjection(); return; }
    if (_tabOk && _tab === 'workbench') { bindVaultWorkbench(); return; }
    if (_tabOk && _tab === 'tools') { bindVaultWorkbench(); return; }
  }
  // HOUSE floor 8 / DEFECT 1: the real posting door (one node either skin).
  document.getElementById('money-log-line')
    ?.addEventListener('click', event => mnyLogLineOpen(event.currentTarget));
  document.getElementById('money-book-entries')?.addEventListener('click', () => moneyOpenBookEntry());
  // Capital OS PR-5: hydrate the Adviser card (fail-soft, fire-and-
  // forget — the rest of the page never waits on it). Runs only on the
  // Overview tab: Books/Tax returned above and their DOM has no slot.
  moneyRead('money-adviser-slot', refreshAdviserCard);
  moneyShowApproval();
  // Money-page redesign 2026-06-07: the "Data tools" section was removed
  // from renderMoney (operator doesn't want internal plumbing on his
  // daily Money screen). PR MONEY-COCKPIT: the unmounted
  // bindMoneyDataTools helper is deleted — dead code, zero call sites.
  // The endpoints it drove remain available for ops use elsewhere.
  document.querySelectorAll('#money-date-chips .chip').forEach(btn => {
    btn.addEventListener('click', async (event) => {
      const key = btn.dataset.moneyPeriod;
      const renderPeriod = () => {
        render();
        // Keyboard activation replaces its focused button with the view.
        // Return to that period; pointer use keeps normal browser focus.
        if (event.detail === 0) {
          document.querySelector('#money-date-chips .chip-active')?.focus({ preventScroll: true });
        }
      };
      if (key === 'custom') {
        _openCustomRangeModal((customPeriod) => {
          State.moneyPeriod = customPeriod;
          renderPeriod();
        });
        return;
      }
      if (key === State.moneyPeriod) return;
      State.moneyPeriod = key;
      renderPeriod();  // refresh chip active state + reload
    });
  });

  // Capital OS PR-3 chips (CRUX | China | Personal). The note under
  // them used to promise a "PR-4" that never came; a promise on a money
  // page is a lie after a week. Now a tap reads the book LIVE from
  // /api/finance/entities — how many accounts it holds and when its
  // newest statement arrived — the one fact the chip can honestly give
  // today. Non-owner roles never get the China/Personal chips (server
  // strips those books for them).
  document.querySelectorAll('#money-entity-chips .chip').forEach(chip => {
    chip.addEventListener('click', async () => {
      document.querySelectorAll('#money-entity-chips .chip').forEach(c =>
        c.classList.toggle('chip-active', c === chip));
      const note = document.getElementById('money-entity-note');
      if (!note) return;
      const ent = chip.dataset.entity;
      if (ent === 'crux') { note.textContent = ''; return; }
      note.textContent = 'Reading the book…';
      try {
        if (!State._entityBooks) State._entityBooks = await api('/api/finance/entities');
        const book = ((State._entityBooks && State._entityBooks.entities) || [])
          .find((e) => e.slug === ent || (e.name || '').toLowerCase().startsWith(ent));
        if (!book) { note.textContent = `${ent === 'china' ? 'China ops' : 'Personal'} book — no accounts registered yet`; return; }
        const accts = book.accounts || [];
        const newest = accts.map((a) => a.last_batch_at).filter(Boolean).sort().pop();
        note.textContent = `${book.name}: ${accts.length} account${accts.length === 1 ? '' : 's'} · ${newest ? `newest statement ${String(newest).slice(0, 10)}` : 'no statement yet'}`;
      } catch (e) {
        note.textContent = `Couldn't read the ${ent === 'china' ? 'China ops' : 'Personal'} book — ${e.message}`;
      }
    });
  });

  // Capital OS PR-3: MULTI-file statement uploader wiring. One request
  // carries every selected file; the response is per-file → per-
  // statement (a GTB online PDF can hold two statements in one file).
  const bsFile = document.getElementById('bank-statement-file');
  const bsBtn  = document.getElementById('bank-statement-submit');
  const bsRes  = document.getElementById('bank-statement-result');
  if (bsFile && bsBtn) {
    bsFile.addEventListener('change', () => {
      bsBtn.disabled = !bsFile.files?.length;
      const n = bsFile.files?.length || 0;
      bsBtn.textContent = n > 1 ? `Upload ${n} files` : 'Upload';
    });
    bsBtn.addEventListener('click', async () => {
      const picked = Array.from(bsFile.files || []);
      if (!picked.length) return;
      bsBtn.disabled = true;
      bsBtn.textContent = 'Uploading…';
      if (bsRes) bsRes.textContent = '';
      try {
        const fd = new FormData();
        picked.forEach(f => fd.append('files', f));
        // FormData sets its own multipart boundary; do NOT set Content-Type.
        const headers = {};
        if (State.jwt) headers['Authorization'] = `Bearer ${State.jwt}`;
        if (State.tenant) headers['X-Tenant-Slug'] = State.tenant.slug;
        const resp = await fetch(API + '/api/finance/bank-statement', {
          method: 'POST',
          body: fd,
          headers,
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || data.ok === false) {
          if (bsRes) bsRes.innerHTML =
            `<span style="color: var(--bad,#ef4444)">Failed: ${esc(
              typeof data.detail === 'string' ? data.detail : (data.error || resp.statusText))}</span>`;
        } else {
          // Per-file result rows: account matched · rows ingested ·
          // duplicates skipped · reconciliation OK/DRIFT · unexplained.
          const rows = [];
          (data.files || []).forEach(f => {
            if (!f.ok && !(f.statements || []).length) {
              rows.push(`<div style="padding: 4px 0; border-bottom: 1px solid var(--border, #2a2a2a);">
                <span style="color: var(--bad,#ef4444)">✕</span> ${esc(f.filename)} — ${esc(f.error || 'failed')}
              </div>`);
              return;
            }
            (f.statements || []).forEach(s => {
              if (!s.ok) {
                rows.push(`<div style="padding: 4px 0; border-bottom: 1px solid var(--border, #2a2a2a);">
                  <span style="color: var(--bad,#ef4444)">✕</span> ${esc(f.filename)} — ${esc(s.error || 'failed')}
                </div>`);
                return;
              }
              const rec = s.reconciliation || {};
              const recBadge = rec.status === 'ok'
                ? `<span style="color: var(--ok,#10b981)">recon OK</span>`
                : rec.status === 'drift'
                  ? `<span style="color: var(--bad,#ef4444)">recon DRIFT</span>`
                  : rec.status === 'newer_snapshot'
                    ? `<span style="opacity: 0.6">snapshot newer — not comparable</span>`
                    : `<span style="opacity: 0.6">recon —</span>`;
              rows.push(`<div style="padding: 4px 0; border-bottom: 1px solid var(--border, #2a2a2a);">
                <span style="color: var(--ok,#10b981)">✓</span> <b>${esc(s.account || f.filename)}</b>
                · ${s.rows_ingested} ingested
                · ${s.duplicates_skipped} dup skipped
                · ${recBadge}
                ${s.unexplained ? ` · <span style="color: var(--warn,#f59e0b)">${s.unexplained} unexplained</span>` : ''}
                ${s.receivables_created ? ` · ${s.receivables_created} receivable(s), due in 28 days` : ''}
              </div>`);
            });
          });
          const t = data.totals || {};
          rows.push(`<div style="padding-top: 6px; opacity: 0.75;">
            ${t.files || 0} file(s) · ${t.rows_ingested || 0} rows in ·
            ${t.duplicates_skipped || 0} dups skipped${
              t.unexplained ? ` · ${t.unexplained} to explain` : ''}${
              t.files_failed ? ` · <span style="color: var(--bad,#ef4444)">${t.files_failed} failed</span>` : ''}
          </div>`);
          if (bsRes) bsRes.innerHTML = rows.join('');
          // Refresh cash + accounts so the new snapshots show up.
          if (typeof refreshCashSection === 'function') refreshCashSection();
        }
      } catch (e) {
        if (bsRes) bsRes.innerHTML =
          `<span style="color: var(--bad,#ef4444)">Upload error: ${esc(e?.message || String(e))}</span>`;
      } finally {
        bsBtn.disabled = !bsFile.files?.length;
        bsBtn.textContent = 'Upload';
      }
    });
  }

  // PR MONEY-COCKPIT: one honest hydrator. Chip clicks, the global
  // Refresh button and the 90s live tick previously refreshed
  // different subsets of this page; everything now funnels through
  // refreshMoney() (numbers-only ticks go through refreshMoneyTick).
  refreshMoney();
}

// ── PR MONEY-COCKPIT — single Money hydrator ────────────────────────
//
// One entry point covering hero/summary, insights, cash, compact P&L,
// transactions, budgets and walk-in. A generation token guards every
// async leg: each call bumps State._moneyGen and any in-flight leg
// from a previous call bails before touching the DOM — this kills the
// stale-response race on rapid chip switching (a slow last-month fetch
// could previously overwrite a fresh this-month paint).
function moneyRead(hostId, load, visible=false) {
  const root=document.getElementById('money-workspace');
  const host=document.getElementById(hostId);
  return scheduleMoneyRead(root,host,load,{visible});
}
async function refreshMoney() {
  State._moneyGen = (State._moneyGen || 0) + 1;
  const gen = State._moneyGen;
  // Legs run in parallel; each re-checks the token after its awaits.
  moneyRead('store-economics-list',bindStoreEconomics);
  if (typeof refreshCashSection === 'function') refreshCashSection(gen);
  if (typeof refreshMoneyInsights === 'function') refreshMoneyInsights(gen);
  if (typeof refreshMoneyLab1 === 'function') moneyRead('money-lab1',()=>refreshMoneyLab1(gen));
  if (typeof refreshMoneyVendors === 'function') moneyRead('money-vendors',()=>refreshMoneyVendors(gen));
  if (typeof refreshMoneyWallets === 'function') moneyRead('money-wallets',()=>refreshMoneyWallets(gen));
  if (typeof refreshMoneyPricing === 'function') moneyRead('money-pricing',()=>refreshMoneyPricing(gen));
  // This read also fills the visible cash warning above the closed business cards.
  if (typeof refreshMoneyCapital === 'function') refreshMoneyCapital(gen);
  if (typeof refreshMoneyCruxCustom === 'function') moneyRead('money-cruxcustom',()=>refreshMoneyCruxCustom(gen));
  if (typeof refreshMoneyInvCash === 'function') moneyRead('money-invcash',()=>refreshMoneyInvCash(gen));
  if (typeof refreshMoneyStorePnl === 'function') moneyRead('money-storepnl',()=>refreshMoneyStorePnl(gen));
  if (typeof refreshMoneyArbiter === 'function') moneyRead('money-arbiter',()=>refreshMoneyArbiter(gen));
  // Wave-2 register inbox chips (best-effort, never blocks the page).
  if (typeof _mtxRegInboxCounts === 'function') moneyRead('money-pnl-card',()=>{bksLoadCard('money-pnl-card', _moneyLoadCompactPnl);_mtxRegInboxCounts();});
  await _hydrateMoneySummary(gen);
}
// Exposed for inline Retry affordances in error states (app.js is a
// module — top-level declarations aren't global).
window.refreshMoney = refreshMoney;

// 90s live tick (see liveRefreshTick): patch NUMBERS ONLY via
// textContent on existing nodes. The old tick re-mounted the insights
// grid + charts via innerHTML, replaying the stagger-rise/chart-draw
// enter animations every 90 seconds — contradicting the "in-place
// updates only" contract the timer was built on.
async function refreshMoneyTick() {
  const gen = State._moneyGen || 0;
  const w = moneyPeriodToWindow(State.moneyPeriod || 'this_month');
  const [sum, cash, runway] = await Promise.all([
    api(`/api/accounting/summary?period=${encodeURIComponent(w.apiPeriod)}`).catch(() => null),
    api('/api/finance/bank-cash-position').catch(() => null),
    api(`/api/finance/runway?lookback_days=${w.days}`).catch(() => null),
  ]);
  if (gen !== State._moneyGen || State.view !== 'money') return;
  const set = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };
  if (sum && sum.ok) {
    set('money-net-countup', formatNaira(sum.net_ngn || 0, true));
    set('money-rev-countup', formatNaira(sum.total_income_ngn || 0, true));
    set('money-exp-countup', formatNaira(sum.total_expense_ngn || 0, true));
  }
  _paintBankCashPosition(cash, gen);
  if (runway && runway.ok) {
    const flow = (runway.monthly_revenue_ngn || 0) - (runway.monthly_expenses_ngn || 0);
    const el = document.getElementById('money-kpi-flow-num');
    if (el) {
      el.classList.toggle('tone-ok', flow >= 0);
      el.classList.toggle('tone-danger', flow < 0);
      el.textContent = `${flow < 0 ? '' : '+'}${formatNaira(flow, true)}`;
    }
  }
}

// Alerts-first digest strip under the period chips (Home mh-digest
// pattern). The page already computes these red/amber states inside
// cards further down — this surfaces them first, one line each, tap
// scrolls to the owning card. Parts land from different fetches
// (insights vs budgets), so each caller contributes its slice and the
// strip repaints from the merged set. Renders NOTHING when all green.
function _updateMoneyDigest(part, items) {
  State._moneyDigestParts = State._moneyDigestParts || {};
  State._moneyDigestParts[part] = items || [];
  const slot = document.getElementById('money-digest-slot');
  if (!slot) return;
  const all = Object.values(State._moneyDigestParts).flat();
  const rank = { danger: 0, warn: 1 };
  all.sort((a, b) => (rank[a.sev] ?? 9) - (rank[b.sev] ?? 9));
  if (!all.length) { slot.innerHTML = ''; return; }
  slot.innerHTML = `
    <div class="mh-digest money-digest">
      <div class="mh-digest-head">Needs attention</div>
      ${all.map(it => `
        <button type="button" class="mh-digest-item is-link" data-digest-target="${esc(it.target || '')}">
          <span class="mh-chip ${it.sev === 'danger' ? 'mh-chip-danger' : 'mh-chip-watch'}">${it.sev === 'danger' ? 'red' : 'amber'}</span>
          <span class="mh-digest-line">${esc(it.line || '')}</span>
          <span class="mh-digest-arrow">&rarr;</span>
        </button>`).join('')}
    </div>`;
  slot.querySelectorAll('.mh-digest-item').forEach(el => {
    el.addEventListener('click', () => {
      const t = el.dataset.digestTarget;
      const node = t ? document.querySelector(t) : null;
      if (!node) return;
      // Open an enclosing <details> drill-down first so the card is
      // actually visible when we scroll to it.
      let pack = node.closest('details');
      while (pack) {
        pack.open = true;
        pack = pack.parentElement?.closest('details');
      }
      node.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    });
  });
}

// ── Capital OS PR-4: Capital card row ───────────────────────────────
// Debt-service wall mini-chart (next 6 months) + receivables countdown
// from /api/finance/capital-metrics. Idle-cash contributes a digest
// line ('capital' part of the mh-digest strip); the runway-band verdict
// lands as a pill on the Net-cashflow KPI. All figures deterministic —
// the backend computes, this only paints.
const CAPITAL_BAND_TONE = {
  BELOW_FLOOR:   'pill-danger',
  DEFENSIVE:     'pill-warn',
  BALANCED:      'pill-ok',
  AGGRESSIVE_OK: 'pill-ok',
};

function _capitalMonthLabel(ym) {
  const m = Number((ym || '').slice(5, 7));
  const names = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return names[m] || ym;
}

function _renderDebtWallCard(wall) {
  if (!wall || !wall.ok) {
    return `<div class="data-card"><div class="row-label">Debt wall · next 6 months</div>
      <div class="money-chart-empty">Couldn't load the debt schedule.</div></div>`;
  }
  const months = (wall.months || []).slice(0, 6);
  const total6 = months.reduce((s, m) => s + (m.debt_service_ngn || 0), 0);
  if (!months.length || total6 <= 0) {
    return `<div class="data-card"><div class="row-label">Debt wall · next 6 months</div>
      <div class="money-chart-empty">No debt service due in the next 6 months.</div></div>`;
  }
  const maxV = Math.max(1, ...months.map(m => m.debt_service_ngn || 0));
  const draw = _prefersReducedMotion() ? '' : 'chart-grow-v';
  const bars = months.map((m, i) => {
    const v = m.debt_service_ngn || 0;
    const hPct = (v / maxV) * 100;
    const facs = Object.entries(m.by_facility || {})
      .map(([n, a]) => `${n}: ${fmtNGN(a)}`).join(' · ');
    return `
      <div class="capwall-col" title="${esc(`${m.month}: ${fmtNGN(v)}${facs ? ' — ' + facs : ''}`)}">
        <div class="capwall-barbox">
          <span class="capwall-bar ${m.flagged ? 'capwall-flag' : ''} ${draw}"
                style="height:${hPct.toFixed(1)}%;--h:${hPct.toFixed(1)}%;--i:${i}"></span>
        </div>
        <span class="capwall-lbl">${esc(_capitalMonthLabel(m.month))}</span>
        <span class="capwall-val mono-num">${v > 0 ? fmtNGN(v) : ''}</span>
      </div>`;
  }).join('');
  const flagged = months.filter(m => m.flagged).length;
  return `
    <div class="data-card capital-card" id="capital-debt-wall">
      <div class="row-label">Debt wall · next 6 months</div>
      <div class="capwall-track">${bars}</div>
      <div class="mini-sub">
        ${fmtNGN(total6)} debt service over 6 mo · payroll ${fmtNGN(wall.monthly_payroll_ngn || 0)}/mo on top
        ${flagged ? ` · <span class="tone-danger">${flagged} month${flagged === 1 ? '' : 's'} over the ${Math.round(wall.alert_pct || 0)}% line</span>` : ''}
      </div>
    </div>`;
}

function _renderReceivablesCard(recv) {
  if (!recv || !recv.ok || recv.restricted) return '';
  const total = recv.total_outstanding_ngn || 0;
  if (total <= 0) {
    return `<div class="data-card capital-card" id="capital-receivables">
      <div class="row-label">Family receivables</div>
      <div class="money-chart-empty">Nothing outstanding.</div></div>`;
  }
  const days = recv.days_until_next_return;
  const countdown = days == null ? ''
    : days < 0 ? `<span class="tone-danger">${Math.abs(days)}d overdue</span>`
    : days === 0 ? '<span class="tone-ok">due today</span>'
    : `returns in <strong>${days}d</strong>`;
  const rows = (recv.by_borrower || []).slice(0, 4).map(b => `
    <div class="ebar-row">
      <span class="ebar-key">${esc(b.borrower)}</span>
      <span class="ebar-val mono-num">${fmtNGN(b.outstanding_ngn)}${b.days_until_return != null ? ` · ${b.days_until_return}d` : ''}</span>
    </div>`).join('');
  return `
    <div class="data-card capital-card" id="capital-receivables">
      <div class="row-label">Family receivables · due in 28 days</div>
      <div class="mini-num mono-num">${fmtNGN(total)}</div>
      <div class="mini-sub">${countdown}${recv.next_expected_return ? ` · next ${esc(recv.next_expected_return)}` : ''}</div>
      <div class="ebar-rows">${rows}</div>
    </div>`;
}

async function refreshMoneyCapital(gen = State._moneyGen) {
  const slot = document.getElementById('money-capital-row');
  if (!slot) return;
  try {
    const w = moneyPeriodToWindow(State.moneyPeriod || 'this_month');
    const data = await api(`/api/finance/capital-metrics?period=${encodeURIComponent(w.apiPeriod)}`);
    if (gen !== State._moneyGen) return;  // superseded while in flight
    if (!data || !data.ok) {
      slot.innerHTML = renderEmptyCard('Capital', 'Unable to load capital metrics right now.');
      _updateMoneyDigest('capital', []);
      return;
    }
    const winEl = document.getElementById('money-capital-window');
    if (winEl) winEl.textContent = 'debt · receivables · idle cash';

    const cards = [
      _renderDebtWallCard(data.debt_wall),
      _renderReceivablesCard(data.receivables),
    ].filter(Boolean);
    slot.innerHTML = cards.length ? cards.join('')
      : renderEmptyCard('Capital', 'No capital data yet.');

    // Runway-band verdict pill on the Net-cashflow KPI.
    const band = data.runway_bands || {};
    const pill = document.getElementById('money-kpi-runway-band');
    if (pill) {
      if (band.ok && band.verdict && band.verdict !== 'UNKNOWN') {
        pill.textContent = band.verdict_label || band.verdict;
        pill.className = `pill capital-band-pill ${CAPITAL_BAND_TONE[band.verdict] || 'pill-ok'}`;
        pill.title = band.surplus_deployable
          ? `Deployable surplus ${fmtNGN(band.deployable_surplus_ngn)} above the ${band.aggressive_min_months}-month reserve`
          : `Bands: floor ${band.safety_floor_months} mo · aggressive ${band.aggressive_min_months} mo`;
        pill.hidden = false;
      } else {
        pill.hidden = true;
      }
    }

    // Idle-cash digest line(s) — only when triggered (mh-digest pattern:
    // contribute a slice, render nothing when all green).
    const ic = data.idle_cash || {};
    const idleItems = [];
    if (ic.ok && (ic.total_idle_ngn || 0) > 0) {
      const top = (ic.accounts || []).filter(a => a.is_idle)[0];
      idleItems.push({
        sev: 'warn',
        target: '#capital-debt-wall',
        line: `${formatNaira(ic.total_idle_ngn, true)} idle at 0% yield` +
              (top ? ` (largest: ${top.display_name})` : '') +
              ` — ~${formatNaira(ic.foregone_yield_monthly_ngn, true)}/mo foregone at ${ic.tbill_rate_pct}%`,
      });
    }
    _updateMoneyDigest('capital', idleItems);
  } catch (e) {
    console.warn('refreshMoneyCapital failed', e);
    if (gen !== State._moneyGen) return;
    slot.innerHTML = renderEmptyCard('Capital', 'Unable to load capital metrics right now.');
    _updateMoneyDigest('capital', []);
  }
}

// CRUX Custom P&L thin slice (2026-08-20): tier coverage + entry
// surface. Reads /api/crux-custom/costs (SKUs + with_factory /
// with_wholesale counters — open_to_buy's coverage scheme scoped to
// the factory's own SKUs); when any factory cost exists it also pulls
// the /pnl waterfall summary. Writes ride /api/crux-custom/cost
// (inline prompt edit) and /costs-csv (file input). Empty-honest at
// 0% — the API's own note renders verbatim, nothing is invented.
async function refreshMoneyCruxCustom(gen = State._moneyGen) {
  const slot = document.getElementById('money-cruxcustom');
  if (!slot) return;
  try {
    const data = await api('/api/crux-custom/costs');
    if (gen !== State._moneyGen) return;
    if (!data || !data.ok) {
      slot.innerHTML = renderEmptyCard('CRUX Custom',
        'Unable to load factory SKUs right now.');
      return;
    }
    const s = data.summary || {};
    const meta = document.getElementById('money-cruxcustom-meta');
    if (meta) meta.textContent =
      `${s.skus || 0} product variants · ${s.with_factory || 0} with factory costs · ${s.with_wholesale || 0} with wholesale prices`;

    let pnlLine = '';
    if ((s.with_factory || 0) > 0) {
      try {
        const p = await api('/api/crux-custom/pnl?period=last_90d&top=1');
        if (gen !== State._moneyGen) return;
        const ps = p?.summary || {};
        if (p?.ok && ps.full_contribution_ngn != null) {
          pnlLine = `<div class="mini-sub" style="margin-top: 8px;">
            90d full contribution (covered lines):
            <strong>${fmtNGN(ps.full_contribution_ngn)}</strong>
            on ${fmtNGN(ps.covered_revenue_ngn)} of
            ${fmtNGN(ps.revenue_ngn)} revenue
            (${ps.rows_with_full_contribution}/${ps.rows} lines costed)</div>`;
        }
      } catch (e) { /* the coverage card stands on its own */ }
    }

    const items = (data.items || []).slice(0, 6);
    const rows = items.map(it => `
      <div class="mccx-row" data-sku="${esc(it.sku)}" style="display: flex; flex-wrap: wrap; gap: 8px 16px; align-items: baseline; padding: 12px 0; border-bottom: 1px solid var(--line); font-size: 13px;">
        <span style="flex: 1 1 200px; min-width: 0; overflow-wrap: anywhere;">
          <strong style="display: block;">${esc(it.product_title?.trim() || 'Product name not supplied')}</strong>
          <span class="meta">Product code ${esc(it.sku)}</span></span>
        <span class="meta">Factory cost: ${it.factory_cost_ngn != null ? fmtNGN(it.factory_cost_ngn) : 'Not entered'}</span>
        <span class="meta">Wholesale price: ${it.wholesale_price_ngn != null ? fmtNGN(it.wholesale_price_ngn) : 'Not entered'}</span>
        <button class="btn-ghost mccx-edit" data-sku="${esc(it.sku)}"
                aria-label="Edit costs for ${esc(it.product_title?.trim() || 'unnamed product')} · ${esc(it.sku)}"
                style="font-size: 12px; min-height: 44px; padding: 8px; border-radius: 0;">Edit costs</button>
      </div>`).join('');

    slot.innerHTML = `
      <div class="data-card">
        <div class="mini-sub">Factory costs entered for ${s.with_factory || 0} of ${s.skus || 0} product variants; wholesale prices for ${s.with_wholesale || 0}. Products without costs have no calculated factory profit.</div>
        ${pnlLine}
        ${rows ? `<p class="mini-sub" style="margin-top: 12px;">Showing ${items.length} of ${(data.items || []).length} product variants. Product codes distinguish variants of the same design.</p><div>${rows}</div>` : ''}
        <div style="display: flex; gap: 8px; align-items: center; margin-top: 12px; flex-wrap: wrap;">
          <input id="mccx-csv" type="file" accept=".csv" style="font-size: 12px; max-width: 220px;">
          <button id="mccx-upload" class="btn-primary" style="font-size: 12px;" disabled>Import tier CSV</button>
          <span class="meta">columns: sku, factory_cost_ngn, wholesale_price_ngn</span>
        </div>
        <div id="mccx-result" class="mini-sub" style="margin-top: 8px;"></div>
      </div>`;

    // Typed IN PLACE — window.prompt is dead (operator law): Edit swaps
    // the row's readouts for two inline fields; Save posts, Cancel
    // re-renders. A blank field keeps the current value.
    slot.querySelectorAll('.mccx-edit').forEach(btn => {
      btn.addEventListener('click', () => {
        const row = btn.closest('.mccx-row');
        if (!row) return;
        const sku = row.dataset.sku;
        const item = items.find(it => it.sku === sku);
        row.innerHTML = `
          <span style="flex: 1 1 200px; min-width: 0; overflow-wrap: anywhere;"><strong style="display: block;">${esc(item?.product_title?.trim() || 'Product name not supplied')}</strong><span class="meta">Product code ${esc(sku)}</span></span>
          <label style="display: grid; gap: 4px; flex: 1 1 132px; min-width: 0;">Factory cost (₦)<input class="mccx-f" type="text" inputmode="decimal" aria-label="Factory cost in naira" style="width: 100%; min-height: 44px; padding: 8px; border-radius: 0; font-size: 13px;"></label>
          <label style="display: grid; gap: 4px; flex: 1 1 132px; min-width: 0;">Wholesale price (₦)<input class="mccx-w" type="text" inputmode="decimal" aria-label="Wholesale price in naira" style="width: 100%; min-height: 44px; padding: 8px; border-radius: 0; font-size: 13px;"></label>
          <button class="btn-primary mccx-save" style="font-size: 12px; min-height: 44px; padding: 8px 12px; border-radius: 0;">Save</button>
          <button class="btn-ghost mccx-cancel" style="font-size: 12px; min-height: 44px; padding: 8px 12px; border-radius: 0;">Cancel</button>`;
        row.querySelector('.mccx-f').focus();
        row.querySelector('.mccx-cancel').addEventListener('click',
          () => refreshMoneyCruxCustom(gen));
        row.querySelector('.mccx-save').addEventListener('click', async () => {
          const f = row.querySelector('.mccx-f').value;
          const w = row.querySelector('.mccx-w').value;
          const body = { sku };
          if (f.trim() !== '') body.factory_cost_ngn = Number(f.replace(/,/g, ''));
          if (w.trim() !== '') body.wholesale_price_ngn = Number(w.replace(/,/g, ''));
          if (body.factory_cost_ngn == null && body.wholesale_price_ngn == null) {
            refreshMoneyCruxCustom(gen); return;
          }
          const out = await api('/api/crux-custom/cost',
                                { method: 'POST', body: JSON.stringify(body) });
          const res = document.getElementById('mccx-result');
          if (res) res.textContent = out?.ok
            ? `${sku} updated.` : `Failed: ${out?.error || 'write refused'}`;
          refreshMoneyCruxCustom(gen);
        });
      });
    });

    // CSV import: FormData multipart — raw fetch (api() pins JSON
    // Content-Type; FormData must set its own boundary), same shape as
    // the bank-statement uploader above.
    const csv = document.getElementById('mccx-csv');
    const up = document.getElementById('mccx-upload');
    if (csv && up) {
      csv.addEventListener('change', () => { up.disabled = !csv.files?.length; });
      up.addEventListener('click', async () => {
        const f = csv.files?.[0];
        if (!f) return;
        up.disabled = true; up.textContent = 'Importing…';
        try {
          const fd = new FormData();
          fd.append('file', f);
          const headers = {};
          if (State.jwt) headers['Authorization'] = `Bearer ${State.jwt}`;
          if (State.tenant) headers['X-Tenant-Slug'] = State.tenant.slug;
          const resp = await fetch(API + '/api/crux-custom/costs-csv',
                                   { method: 'POST', body: fd, headers });
          const out = await resp.json().catch(() => ({}));
          const res = document.getElementById('mccx-result');
          if (res) {
            const errs = [...(out.errors || []), ...(out.parse_errors || [])];
            const unk = out.unknown_skus || [];
            res.textContent = resp.ok
              ? `${out.updated || 0} SKU(s) updated`
                + (unk.length ? ` · ${unk.length} unknown SKU(s): ${unk.slice(0, 5).join(', ')}${unk.length > 5 ? '…' : ''}` : '')
                + (errs.length ? ` · ${errs.length} row error(s): ${errs[0]}` : '')
              : `Failed: ${out.detail || out.error || resp.statusText}`;
          }
          if (resp.ok) refreshMoneyCruxCustom(gen);
        } finally {
          up.textContent = 'Import tier CSV';
          up.disabled = !csv.files?.length;
        }
      });
    }
  } catch (e) {
    console.warn('refreshMoneyCruxCustom failed', e);
    if (gen !== State._moneyGen) return;
    slot.innerHTML = renderEmptyCard('CRUX Custom',
      'Unable to load factory SKUs right now.');
  }
}

// Compact month-to-date P&L on Overview — real Books figures
// (accrual, /api/accounting/books/pnl), not a chat prefill. Splits an
// ads line out of OpEx when the books carry one so the card reads
// Revenue − COGS = Gross − OpEx − Ads = Net.
// THE PROVISIONAL CHIP (2026-08-10). Only 'voided' is excluded from
// the P&L / books / budgets reads — unreviewed drafts are already
// INSIDE the figure above (147 of them, ₦47.3M on the day the bench
// shipped) and it never said so. One chip names the provisional
// amount and taps into the Ledger's review queue. Reads the bench's
// own summary endpoint so the two surfaces can never disagree, and
// fails soft: the P&L is the card, this is a footnote on it.
async function _mnyPnlProvisional() {
  const slot = document.getElementById('money-pnl-provisional');
  if (!slot) return;
  try {
    const r = await api('/api/accounting/transactions/inbox-summary');
    const n = r?.pending_review?.count || 0;
    if (!n) return;                       // nothing provisional, no chip
    const ngnSum = Number(r.pending_review.sum_ngn || 0);
    slot.innerHTML = `
      <button class="mny-prov-chip" type="button"
              aria-label="Open the ledger's review queue">
        <span class="mny-prov-dot"></span>
        <span class="mono-num">${formatNaira(ngnSum)}</span>
        of this is still unreviewed · ${n} draft${n === 1 ? '' : 's'}
      </button>`;
    slot.querySelector('.mny-prov-chip')?.addEventListener('click', () => {
      if (typeof _ldgS === 'object' && _ldgS) _ldgS.queue = 'review';
      State.moneyTab = 'ledger';
      render();
    });
  } catch (e) { /* footnote only — the P&L card itself already rendered */ }
}

// ── HORIZON-1 wave 2: the second cash account (GMROI) ───────────────
async function refreshMoneyInvCash(gen = State._moneyGen) {
  const slot = document.getElementById('money-invcash');
  if (!slot) return;
  try {
    const g = await api('/api/finance/gmroi?period_days=365');
    if (gen !== State._moneyGen) return;
    if (!g || g.ok === false) {
      slot.innerHTML = `
        <div class="data-card">
          <div class="data-card-head"><span class="data-card-title">Money held in stock</span></div>
          <div class="data-stat-sub">${esc(g?.error || 'EON could not read this figure. Try again later.')}</div>
        </div>`;
      return;
    }
    const meta = document.getElementById('money-invcash-meta');
    if (meta) meta.textContent = `last ${g.period_days} days · shown per year`;
    const pill = g.band === 'losing' ? 'pill-danger'
      : g.band === 'below_target' ? 'pill-warn' : 'pill-ok';
    const bandLabel = { losing: 'LOSING', below_target: 'BELOW TARGET',
      target: 'ON TARGET', thin_stock: 'THIN STOCK' }[g.band] || g.band;
    const turns = g.avg_inventory_ngn > 0
      ? ((g.cogs_ngn / g.avg_inventory_ngn) * (365 / g.period_days)).toFixed(1)
      : null;
    const covCaveat = g.cost_coverage_pct < 50
      ? `<div class="data-stat-sub" style="color: var(--warn, #F2A24C);">
           Only ${g.cost_coverage_pct}% of the products sold have a real cost on file; the
           rest use a 45% default. Read this as a rough guide until more costs are entered.</div>`
      : '';
    slot.innerHTML = `
      <div class="data-card">
        <div class="data-card-head"><span class="data-card-title">Money held in stock</span>
          <span class="pill ${pill}">${esc(bandLabel)}</span></div>
        <div class="mini-num mono-num" style="font-size: 26px;">₦${Number(g.gmroi).toFixed(2)} per ₦1</div>
        <div class="data-stat-sub">Margin earned in a year for each ₦1 held in stock (GMROI).</div>
        <div class="data-stat-sub mono-num">₦${(g.avg_inventory_ngn / 1e6).toFixed(1)}M held in stock
          · ₦${(g.gross_margin_ngn / 1e6).toFixed(1)}M margin a year on it${turns ? ` · sold through about ${turns} times a year` : ''}</div>
        <div class="data-stat-sub">${esc(g.interpretation || '')}</div>
        ${covCaveat}
      </div>`;
  } catch (e) {
    if (gen !== State._moneyGen) return;
    slot.innerHTML = _moneyFailCard('Money held in stock', e?.message || e);
  }
}

// ── HORIZON-1 wave 3: the four walls, per store ─────────────────────
async function refreshMoneyStorePnl(gen = State._moneyGen) {
  const slot = document.getElementById('money-storepnl');
  if (!slot) return;
  try {
    const w = moneyPeriodToWindow(State.moneyPeriod || 'this_month');
    const p = await api(`/api/finance/store-pnl?period=${encodeURIComponent(w.apiPeriod)}`);
    if (gen !== State._moneyGen) return;
    if (!p || p.ok === false) {
      slot.innerHTML = _moneyFailCard('What each store keeps', p?.error || 'no payload');
      return;
    }
    const meta = document.getElementById('money-storepnl-meta');
    if (meta) meta.textContent = `${p.start || ''} → ${p.end || ''}`;
    const stores = p.stores || [];
    if (!stores.length) {
      slot.innerHTML = `
        <div class="data-card">
          <div class="data-card-head"><span class="data-card-title">What each store keeps</span></div>
          <div class="data-stat-sub">No sales were tied to a store in this period.</div>
        </div>`;
      return;
    }
    const fmtM = (n) => `₦${(n / 1e6).toFixed(1)}M`;
    const rows = stores.map((s) => {
      const neg = s.four_wall_ngn < 0;
      const occ = s.occupancy_pct == null ? '—' : `${s.occupancy_pct}%`;
      const occHot = s.occupancy_pct != null
        && s.occupancy_pct > p.occupancy_healthy_max_pct;
      const flagBits = [
        s.flags.includes('cogs_default') ? 'stock cost estimated at 45%' : '',
        s.flags.includes('no_lease_row') ? 'no rent on file' : '',
        s.flags.includes('no_staff_assigned') ? 'no staff assigned' : '',
      ].filter(Boolean).join(' · ');
      return `
        <div class="hz-lanerow5 mono-num">
          <span class="hz-lane-label">${esc(s.name || '')}</span>
          <span class="hz-wkc">${fmtM(s.revenue_ngn)}</span>
          <span class="hz-wkc">${s.gross_margin_pct == null ? '—' : `${s.gross_margin_pct}%`}</span>
          <span class="hz-wkc ${occHot ? 'hz-neg' : ''}">${occ}</span>
          <b class="hz-wkc ${neg ? 'hz-neg' : ''}">${fmtM(s.four_wall_ngn)}</b>
        </div>
        ${flagBits ? `<div class="hz-wkitems">${esc(flagBits)}</div>` : ''}`;
    }).join('');
    const covered = p.walls_cover_burden;
    slot.innerHTML = `
      <div class="data-card">
        <div class="data-card-head"><span class="data-card-title">What each store keeps</span>
          ${covered == null ? '' : `<span class="pill ${covered ? 'pill-ok' : 'pill-warn'}">${covered ? 'COVERS OTHER STAFF COSTS' : 'SHORT OF OTHER STAFF COSTS'}</span>`}</div>
        <div class="data-stat-sub">Sales after stock cost, the store's rent and its own staff (four-wall).</div>
        <div class="hz-scroll">
        <div class="hz-lanerow5 hz-wkhead">
          <span class="hz-lane-label">store</span>
          <span class="hz-wkc">sales</span><span class="hz-wkc">gross margin</span>
          <span class="hz-wkc">rent of sales</span><span class="hz-wkc">kept</span>
        </div>
        ${rows}
        </div>
        <div class="data-stat-sub mono-num" style="margin-top:8px;">
          stores kept ${fmtM(p.total_four_wall_ngn)} · other staff costs (head office, online, not tied to a store) ${fmtM(p.above_walls_burden_period_ngn)}
          ${p.unallocated?.unattributed_revenue_ngn ? ` · sales not tied to a store ${fmtM(p.unallocated.unattributed_revenue_ngn)}` : ''}</div>
        <div class="data-stat-sub">${esc(p.unallocated?.note || '')}</div>
      </div>`;
  } catch (e) {
    if (gen !== State._moneyGen) return;
    slot.innerHTML = _moneyFailCard('What each store keeps', e?.message || e);
  }
}

// ── HORIZON-1 wave 5: the next naira ────────────────────────────────
async function refreshMoneyArbiter(gen = State._moneyGen) {
  const slot = document.getElementById('money-arbiter');
  if (!slot) return;
  try {
    const p = await api('/api/finance/capital-arbiter');
    if (gen !== State._moneyGen) return;
    if (!p || p.ok === false) {
      slot.innerHTML = _moneyFailCard('The next naira', p?.error || 'no payload');
      return;
    }
    const meta = document.getElementById('money-arbiter-meta');
    if (meta) meta.textContent = `debt and cash test: ${p.hurdle_pct}% a year${p.hurdle_source === 'seeded_mpr_plus_spread' ? ' · starting figure' : ''}`;
    // capital_arbiter.py judges each option on its own test, never one bar
    // for all: debt and cash on the yearly rate, stock on its margin target,
    // ads on the least that pays for them; a new store is never judged.
    const bar = `${p.hurdle_pct}% a year`;
    const laneTest = (l) => {
      const naira = (re) => { const m = re.exec(l.metric_value || ''); return m ? `₦${Number(m[1]).toFixed(2)}` : ''; };
      if (l.key === 'inventory') {
        const t = naira(/vs the ([\d.]+) target/);
        return { yes: 'Meets its stock target', no: 'Below its stock target',
          line: t ? `Stock's target: ${t} of margin a year for each ₦1 held.` : `Stock is judged on its own target, not on ${bar}.` };
      }
      if (l.key === 'ads') {
        const f = naira(/vs floor ([\d.]+)/);
        return { yes: 'Meets its ad floor', no: 'Below its ad floor',
          line: f ? `Ads' floor: ${f} of sales for each ₦1 spent, the least that pays for them.` : `Ads are judged on their own floor, not on ${bar}.` };
      }
      if (l.key === 'stores') return { line: 'Your call: EON never ranks a new store.' };
      if (l.key === 'debt_paydown' || l.key === 'hold') return { yes: `Beats ${bar}`, no: `Below ${bar}` };
      return { yes: 'Passes its own test', no: 'Below its own test' };
    };
    const laneRows = (p.lanes || []).map((l) => {
      const test = laneTest(l);
      const verdict = !test.yes ? ''
        : l.clears_hurdle === true ? `<span class="pill pill-ok">${esc(test.yes)}</span>`
        : l.clears_hurdle === false ? `<span class="pill pill-warn">${esc(test.no)}</span>`
        : '<span class="pill">Your call</span>';
      return `<div class="hz-lanerow">
          <span class="hz-lane-label">${l.first_call ? '→ ' : ''}${esc(l.label)}</span>
          <span class="mono-num" style="opacity:.8; min-width:0;">${esc(l.metric_value || '')}</span>
          <span>${verdict}</span>
        </div>
        <div class="hz-wkitems">${esc(l.evidence || '')}${test.line ? `<br>${esc(test.line)}` : ''}</div>`;
    }).join('');
    slot.innerHTML = `
      <div class="data-card">
        <div class="data-card-head"><span class="data-card-title">Where the next naira goes</span></div>
        <div class="data-stat-sub" style="font-size:13px; color: var(--ink, inherit);">${esc(p.first_call || '')}</div>
        <div style="margin-top:10px;">${laneRows}</div>
        <div class="data-stat-sub" style="margin-top:8px;">Each choice has its own test. ${esc(bar)} is the test for paying down debt and holding cash: the return a naira must beat to be worth putting in.${p.hurdle_source === 'seeded_mpr_plus_spread' ? ' It is a starting figure, the central bank rate plus a lending margin, until a rate is set for the business.' : ''}</div>
        <div class="data-stat-sub">This is advice only. Each choice is still made in its own place. These are averages; the next naira is judged when you act.</div>
      </div>`;
  } catch (e) {
    if (gen !== State._moneyGen) return;
    slot.innerHTML = _moneyFailCard('The next naira', e?.message || e);
  }
}

function _moneyLoadCompactPnl(host) {
  // DEFECT (fixed 2026-08-07, HOUSE floor 8): this card hard-coded
  // bksCurrentMonth() and never read State.moneyPeriod — every other
  // Money consumer honours the chips, this one silently answered "this
  // month" whatever was picked. It reads the SAME single resolver now.
  // books.parse_period splits on the first ':' before any prefix
  // handling, so 'custom:START:END' would call date.fromisoformat
  // ('custom') and 500 the card — send the bare 'START:END' span.
  const w = moneyPeriodToWindow(State.moneyPeriod || 'this_month');
  const period = w.apiPeriod.startsWith('custom:')
    ? w.apiPeriod.slice('custom:'.length) : w.apiPeriod;
  // Bound to this request, so a late reply never pairs its figures with a newer chip's month.
  const books = moneyPnlBooksMonth(State.moneyPeriod || 'this_month');
  return api(`/api/accounting/books/pnl?period=${encodeURIComponent(period)}`)
    .then((p) => {
      if (!p || p.ok === false) throw new Error(p?.error || 'P&L unavailable');
      const meta = document.getElementById('money-pnl-meta');
      if (meta) meta.textContent = w.label;
      const rev = p.revenue || {};
      const opexLines = p.opex_lines || [];
      const adsNgn = opexLines
        .filter(l => /advertis|marketing|ad[\s_-]?spend|\bads\b/i.test(l.name || ''))
        .reduce((s, l) => s + (Number(l.amount_ngn) || 0), 0);
      const opexRest = (Number(p.opex_ngn) || 0) - adsNgn;
      const below = Number(p.below_ebitda_ngn || 0);
      const net = Number(p.net_profit_ngn || 0);
      host.innerHTML = `
        <div class="bks-stmt">
          ${bksStmtRow('Revenue', rev.net_ngn)}
          ${bksStmtRow('Cost of goods sold', p.cogs_ngn, { paren: true })}
          ${bksStmtRow('Gross profit', p.gross_profit_ngn, { total: true })}
          ${bksStmtRow('Operating expenses', opexRest, { paren: true })}
          ${adsNgn ? bksStmtRow('Ad spend', adsNgn, { paren: true }) : ''}
          ${below ? bksStmtRow('After that: loans + tax', below, { paren: true }) : ''}
          ${bksStmtRow(net >= 0 ? 'Net profit' : 'Net loss', p.net_profit_ngn,
            { grand: true, tone: net >= 0 ? 'ok' : 'warn' })}
        </div>
        <div id="money-pnl-provisional"></div>${books.range ? `
        <div class="data-stat-sub" id="money-pnl-books-note">Books shows one whole month: ${esc(books.label)}, the month this range ends in.</div>` : ''}
        <button class="btn-ghost money-pnl-books-btn" type="button"${books.range ? ' aria-describedby="money-pnl-books-note"' : ''}
                style="margin-top: 8px; font-size: 12px;">Open Books for ${esc(books.label)} &rarr;</button>`;
      _mnyPnlProvisional();   // how much of the above is still belief
      host.querySelector('.money-pnl-books-btn')?.addEventListener('click', () => {
        moneyOpenView('books', false, {booksPeriod: books.month});
      });
    });
}

// Hero/summary + budgets + transactions + walk-in leg (formerly the
// tail of bindMoney). `gen` is the refreshMoney generation token.
async function _hydrateMoneySummary(gen) {
  const w = moneyPeriodToWindow(State.moneyPeriod || 'this_month');
  const period = encodeURIComponent(w.apiPeriod);
  // v3.0p23: length-matched comparison period so "this month
  // (MTD)" doesn't get compared against a full prior month —
  // that was producing misleading ↓45% pills for what was
  // actually just half a month elapsed.
  const currentPeriod = w.apiPeriod;
  const comparePeriodMap = {
    'this_month': 'last_month_mtd',
    'mtd':        'last_month_mtd',
    'last_month': 'last_month',  // already a full month — match it
    'last_7d':    'last_7d_prev',
    'last_30d':   'last_30d_prev',
    'last_90d':   'last_90d_prev',
  };
  const comparePeriod = comparePeriodMap[currentPeriod] || 'last_month_mtd';
  // PR MONEY-CASH-RESTORE: per-call error capture. Capturing the error
  // string lets the hero render an error state instead of staying
  // stuck on the "Loading" skeleton when /api/accounting/summary
  // returns a 5xx or times out. Previously the .catch(() => null)
  // collapsed errors to null which the existing else-branch interpreted
  // as "Welcome to Money — empty state", masking real failures.
  const captureErr = (p) => p.then(v => ({v, e: null}),
                                   e => ({v: null, e: e?.message || String(e)}));
  // PR MONEY-CASH-WALKIN (2026-07-04, operator go): the cash-by-location
  // fetch is back — repointed server-side at walk-in Shopify orders
  // (offline gateways). revenue-by-location rides along ONLY to feed the
  // walk-in-share hint; the revenue section itself stays a Home surface
  // (LATE-NIGHT-POLISH decision unchanged).
  const results = await Promise.all([
    captureErr(api(`/api/accounting/summary?period=${period}`)),
    captureErr(api(`/api/accounting/transactions?period=${period}&limit=10`)),
    captureErr(api('/api/accounting/budgets')),
    captureErr(api(`/api/accounting/summary?period=${comparePeriod}`)),
    captureErr(api(`/api/accounting/cash-by-location?period=${period}`)),
    captureErr(api(`/api/accounting/revenue-by-location?period=${period}`)),
  ]);
  const summary        = results[0].v;
  // Wave-1 error≠empty: period_summary returns {ok:false} with HTTP 200
  // when the backend throws (accounting.py wraps exceptions) — that is
  // a FAILURE, not an empty business. Without this, a DB outage rendered
  // as "Welcome to Money — no transactions yet".
  const summaryFailed  = !!(summary && summary.ok === false);
  const summaryErr     = results[0].e
    || (summaryFailed ? (summary.error || 'something went wrong — try again') : null);
  const txData         = results[1].v;
  const txErr          = results[1].e;
  const budgets        = results[2].v;
  const budgetsErr     = results[2].e;
  const lastMonth      = results[3].v;
  const cashByLocation = results[4].v;
  const walkinErr      = results[4].e;
  const byLocation     = results[5].v;

  // PR MONEY-COCKPIT: stale-response guard — a newer refreshMoney()
  // superseded this call while the fetches were in flight.
  if (gen !== State._moneyGen) return;

  // Money-page redesign 2026-06-07: stash the summary so the Trends
  // charts (expense breakdown leg) can read its component figures.
  // Re-render charts now that the summary is in — refreshMoneyInsights
  // may have already drawn the trend/cashflow legs with insights data.
  State._moneySummary = summary;
  if (summary?.ok && typeof renderMoneyCharts === 'function') {
    renderMoneyCharts(State._moneyInsights || null, summary);
  } else if (summaryErr && !State._moneyInsights) {
    // Both chart feeds are down (summary failed here, insights never
    // stashed) — stop the skeletons spinning forever (wave 1).
    renderMoneyChartsError(summaryErr);
  }

  // Hero: net + month-over-month
  const heroSlot = $('#money-hero');
  if (heroSlot && summary?.ok) {
    const net = summary.net_ngn || 0;
    const lastNet = lastMonth?.net_ngn || 0;
    const delta = net - lastNet;
    const pct = lastNet !== 0 ? ((delta / Math.abs(lastNet)) * 100) : null;
    const status = net >= 0 ? 'pill-ok' : 'pill-danger';
    // 2026-05-31 (Daniel: "relabel with accurate accounting terms"):
    // "Profit"/"Loss" → "Net profit"/"Net loss" for accounting
    // accuracy. The number IS Revenue − Total Expenses (operating +
    // ad spend + COGS) which is bottom-line net income/loss.
    const statusLabel = net >= 0 ? 'Net profit' : 'Net loss';

    // v3.0p21 9xx: expense_estimated flag tells us the COGS leg was
    // pipeline-incomplete and we used the 45% fallback. Mark "(est)"
    // so the operator knows the comparison is approximate. 2026-05-31:
    // tighter copy — "COGS pipeline gap" was operator-confusing.
    const thisEst = summary.expense_estimated;
    const lastEst = lastMonth?.expense_estimated;
    const estTag = (thisEst || lastEst)
      ? `<span style="color:var(--ink-soft);font-size:10px;margin-left:6px;">(stock cost estimated)</span>`
      : '';
    // Period label — the single resolver drives this (same label the
    // Insights/Cash/Recent meta lines show).
    const periodLabel = w.label;

    heroSlot.innerHTML = `
      <div class="hero">
        <div class="hero-meta">
          <div class="hero-label">Net profit · ${esc(periodLabel)}</div>
          <span class="pill ${status}">${statusLabel}</span>
        </div>
        <div class="hero-num"><span id="money-net-countup" class="${net < 0 ? 'tone-danger' : ''}">${formatNaira(net, true)}</span></div>
        ${lastMonth?.ok && lastNet !== 0 ? `
          <div class="hero-trend">
            <span class="eyebrow">vs ${formatNaira(lastNet, true)} ${esc(_compareLabelFor(currentPeriod))}
            ${pct !== null ? `· ${pct >= 0 ? '↑' : '↓'} ${Math.abs(pct).toFixed(0)}%` : ''}
            ${estTag}</span>
          </div>
        ` : ''}
      </div>`;
    // Count-up the hero net number (skips when reduced-motion is on).
    _countUp(document.getElementById('money-net-countup'), net,
             (v) => formatNaira(v, true));
  } else if (heroSlot && summaryErr) {
    // PR MONEY-CASH-RESTORE: explicit error state. Previously the
    // loading skeleton stayed visible forever when the summary
    // endpoint 5xx'd or timed out, because the else-branch only
    // handled the "empty" case.
    heroSlot.innerHTML = `
      <div class="hero">
        <div class="hero-meta">
          <div class="hero-label">Net · this month</div>
          <span class="pill pill-danger">Error</span>
        </div>
        <div class="hero-num" style="font-size: 24px; line-height: 1.15; color: var(--bad, #ef4444);">
          Couldn't load
        </div>
        <div class="hero-trend">
          <span class="eyebrow">${esc(String(summaryErr).slice(0, 140))}</span>
          <!-- PR MONEY-COCKPIT: was onclick="render()" — app.js is a
               module, so render() was never global and the button
               silently threw. refreshMoney IS exposed on window. -->
          <button class="hero-retry-btn" onclick="refreshMoney()">Retry</button>
        </div>
      </div>`;
  } else if (heroSlot) {
    heroSlot.innerHTML = `
      <div class="hero">
        <div class="hero-meta">
          <div class="hero-label">Welcome to Money</div>
          <span class="pill pill-warn">Empty</span>
        </div>
        <div class="hero-num" style="font-size: 32px; line-height: 1.15;">No transactions<br>yet this month</div>
        <div class="hero-trend">
          <span class="eyebrow">Tap an action below or try saying:<br>"spent ₦25k on petty cash today"</span>
        </div>
      </div>`;
  }

  // Stats
  const statsSlot = $('#money-stats');
  if (statsSlot && summary?.ok) {
    // 2026-05-31 (Daniel: "relabel with accurate accounting terms"):
    //   "Income"   → "Revenue"   (gross sales; "income" reads as net)
    //   "Expenses" → "Expenses"  (kept, but a breakdown line now
    //                              shows the components instead of
    //                              just the total)
    //
    // Revenue split (shopify_revenue_ngn + manual_income_ngn) was
    // already surfaced. Now adding the expense split so the operator
    // sees what makes up the total — ops + ad spend + COGS (est).
    // All three fields come from period_summary in accounting.py
    // (cogs_ngn / ad_spend_ngn added 2026-05-31).
    const shopifyRev = summary.shopify_revenue_ngn || 0;
    const manualInc  = summary.manual_income_ngn  || 0;
    const revSplitLine = (shopifyRev > 0 || manualInc > 0)
      ? `<div style="font-size:10px;color:var(--ink-soft);margin-top:4px;line-height:1.3;">
           ${shopifyRev > 0 ? `Shopify ${formatNaira(shopifyRev, true)}` : ''}${shopifyRev > 0 && manualInc > 0 ? ' · ' : ''}${manualInc > 0 ? `Manual ${formatNaira(manualInc, true)}` : ''}
         </div>`
      : '';

    const totalExp  = summary.total_expense_ngn  || 0;
    const opsExp    = summary.logged_expense_ngn || 0;  // rent+salary+util
    const adSpend   = summary.ad_spend_ngn       || 0;
    const cogs      = summary.cogs_ngn           || 0;
    // The ops total already excludes ad_spend if logged separately
    // (period_summary subtracts logged_ad_spend before reconciling).
    // Show whichever components are non-zero in plain accounting terms.
    const expParts = [];
    if (opsExp   > 0) expParts.push(`OpEx ${formatNaira(opsExp, true)}`);
    if (adSpend  > 0) expParts.push(`Ad spend ${formatNaira(adSpend, true)}`);
    if (cogs     > 0) expParts.push(
      `COGS ${formatNaira(cogs, true)}${summary.expense_estimated ? ' (est)' : ''}`
    );
    const expSplitLine = expParts.length
      ? `<div style="font-size:10px;color:var(--ink-soft);margin-top:4px;line-height:1.3;">
           ${expParts.join(' · ')}
         </div>`
      : '';

    const revVal = summary.total_income_ngn || 0;
    statsSlot.innerHTML = `
      <div class="stat money-kpi">
        <div class="stat-label">Revenue</div>
        <div class="stat-val"><span id="money-rev-countup">${formatNaira(revVal, true)}</span></div>
        ${revSplitLine}
      </div>
      <div class="stat money-kpi">
        <div class="stat-label">Expenses</div>
        <div class="stat-val"><span id="money-exp-countup">${formatNaira(totalExp, true)}</span></div>
        ${expSplitLine}
      </div>`;
    _countUp(document.getElementById('money-rev-countup'), revVal, (v) => formatNaira(v, true));
    _countUp(document.getElementById('money-exp-countup'), totalExp, (v) => formatNaira(v, true));
  } else if (statsSlot && summaryErr) {
    // 2026-05-31: explicit error state. Without this branch, a
    // failing /api/accounting/summary left the "—" skeleton stats
    // visible forever — operator couldn't tell loading from broken.
    const msg = String(summaryErr).slice(0, 120);
    statsSlot.innerHTML = `
      <div class="stat money-kpi">
        <div class="stat-label">Revenue</div>
        <div class="stat-val" style="font-size: 14px; color: var(--bad, #ef4444);">Couldn't load</div>
        <div style="font-size:10px;color:var(--ink-soft);margin-top:4px;">${esc(msg)}</div>
      </div>
      <div class="stat money-kpi">
        <div class="stat-label">Expenses</div>
        <div class="stat-val" style="font-size: 14px; color: var(--bad, #ef4444);">Couldn't load</div>
      </div>`;
  }

  // PR MONEY-CASH-WALKIN (2026-07-04, operator go): "Walk-in cash by
  // store" — per-store takings from offline-gateway Shopify orders
  // (cash / POS terminal / transfer at the store). Replaces the dead
  // Moniepoint-POS block; the Revenue-by-location section stays a Home
  // surface (LATE-NIGHT-POLISH decision unchanged) — its fetch here
  // only feeds the walk-in-share hint per store.
  const locSlot = $('#money-by-location');
  if (locSlot && walkinErr) {
    // PR MONEY-COCKPIT: errors ≠ empty. A failed fetch previously left
    // the section hidden — indistinguishable from "no walk-in orders".
    locSlot.hidden = false;
    locSlot.innerHTML = `
      <div class="section-h"><h2>Walk-in takings by store (orders, not bank cash)</h2></div>
      <div class="card bks-soft-err" style="margin-bottom: 12px;">
        <span>Couldn't load — </span>
        <button class="bks-retry-btn" type="button" onclick="refreshMoney()">Retry</button>
        <span class="bks-soft-err-msg">${esc(String(walkinErr).slice(0, 120))}</span>
      </div>`;
  } else if (locSlot && cashByLocation?.ok
      && (cashByLocation.by_location?.length || 0) > 0) {
    locSlot.hidden = false;
    const cashTotal = cashByLocation.total_inflow_ngn || 0;
    // Store-revenue lookup so each row can show what share of the
    // store's total revenue walked in through the door.
    const revByLocId = {};
    ((byLocation && byLocation.by_location) || []).forEach(r => {
      if (r.location_id) revByLocId[r.location_id] = r.revenue_ngn || 0;
    });
    locSlot.innerHTML = `
      <div class="section-h"><h2>Walk-in takings by store (orders, not bank cash)</h2></div>
      <div class="eyebrow" style="margin: -4px 0 8px; color: var(--ink-mute);">
        In-store orders · cash, POS &amp; transfer · ${formatNaira(cashTotal, true)}
      </div>
      <div class="card" style="padding: 4px 0; margin-bottom: 12px;">
        ${cashByLocation.by_location.map(loc => {
          const inflow = loc.inflow_ngn || 0;
          const pct = cashTotal > 0 ? Math.round((inflow / cashTotal) * 100) : 0;
          const isUnmapped = loc.kind === 'unmatched';
          const isExcluded = !!loc.exclude_from_analytics;
          const rev = loc.location_id ? (revByLocId[loc.location_id] || 0) : 0;
          // Walk-in share of the store's total revenue — the gap is
          // the store's online-fulfilled share. Walk-in cash is a
          // subset of revenue, so >100% flags a data-quality issue.
          const shareHint = (rev > 0 && !isUnmapped && !isExcluded)
            ? (() => {
                const share = Math.round((inflow / rev) * 100);
                const color = share > 100 ? 'var(--warn)' : 'var(--ink-mute)';
                return `<span style="font-size:10px;color:${color};margin-left:6px;">
                  ${share}% of store revenue</span>`;
              })()
            : '';
          const kindTag = isExcluded
            ? `<span style="font-size:10px;color:var(--ink-soft);margin-left:6px;">excluded from analytics</span>`
            : loc.kind === 'virtual'
              ? `<span style="font-size:10px;color:var(--ink-soft);margin-left:6px;">rep-closed transfer</span>`
              : isUnmapped
                ? `<span style="font-size:10px;color:var(--warn);margin-left:6px;">unmapped store — add to locations</span>`
                : '';
          const rowStyle = isExcluded ? 'opacity:0.55;' : '';
          const metaLine = isExcluded
            ? `${loc.txn_count} order${loc.txn_count === 1 ? '' : 's'} · not counted in total`
            : `${loc.txn_count} order${loc.txn_count === 1 ? '' : 's'} · ${pct}% of walk-in${shareHint}`;
          return `
            <div style="display:flex; justify-content:space-between; align-items:center; padding: 12px 16px; border-bottom: 1px solid var(--line-soft); ${rowStyle}">
              <div style="flex:1; min-width:0;">
                <div style="font-size:14px; font-weight:500;">
                  ${esc(loc.name)}${kindTag}
                </div>
                <div style="font-size:10px; color:var(--ink-mute); letter-spacing:0.04em; margin-top:2px;">
                  ${metaLine}
                </div>
              </div>
              <div style="text-align:right;">
                <div style="font-family:var(--mono); font-size:14px; font-weight:500;">
                  ${formatNaira(inflow, true)}
                </div>
              </div>
            </div>
          `;
        }).join('')}
      </div>`;
  }

  // Budgets (only show if any exist). PR MONEY-COCKPIT: null-guard
  // pct_used (was rendering "null%"), explicit error state, and the
  // over-budget rows feed the digest strip.
  const budgetsSlot = $('#money-budgets-section');
  if (budgetsSlot && budgetsErr) {
    budgetsSlot.innerHTML = `
      <div class="section-h"><h2>Budgets</h2></div>
      <div class="card bks-soft-err" style="margin-bottom: 8px;">
        <span>Couldn't load — </span>
        <button class="bks-retry-btn" type="button" onclick="refreshMoney()">Retry</button>
        <span class="bks-soft-err-msg">${esc(String(budgetsErr).slice(0, 120))}</span>
      </div>`;
    _updateMoneyDigest('budgets', []);
  } else if (budgetsSlot && budgets?.ok && budgets.budgets?.length > 0) {
    budgetsSlot.innerHTML = `
      <div class="section-h"><h2>Budgets</h2>
        ${(State.member?.base_role || State.member?.role) === 'viewer' ? '' : `<button class="bks-tool-btn" type="button" onclick="mtxBudgetOpen()">Set budget</button>`}</div>
      ${budgets.budgets.slice(0, 5).map(b => {
        const pct = Number(b.pct_used);
        const pctLabel = Number.isFinite(pct) ? `${pct}%` : '—';
        const pctWidth = Number.isFinite(pct) ? Math.min(pct, 100) : 0;
        return `
        <div class="card" style="margin-bottom: 8px; padding: 14px 16px; cursor: pointer;"
             onclick="mtxBudgetOpen('${esc(sosJs(b.category || b.name))}')" title="Tap to edit or remove this budget">
          <div style="display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px;">
            <div style="font-size: 14px; font-weight: 500;">${esc(b.name)}</div>
            <div class="eyebrow">${pctLabel}</div>
          </div>
          <div style="height: 4px; background: var(--line-soft); border-radius: 2px; overflow: hidden;">
            <div style="height: 100%; width: ${pctWidth}%; background: ${
              b.status === 'over' ? 'var(--danger)' :
              b.status === 'warn' ? 'var(--warn)' : 'var(--ok)'
            }; transition: width 0.5s ease;"></div>
          </div>
          <div style="margin-top: 6px; font-family: var(--mono); font-size: 10px; color: var(--ink-mute); letter-spacing: 0.06em;">
            ${formatNaira(b.spent_ngn, true)} of ${formatNaira(b.limit_ngn, true)}
          </div>
        </div>`;
      }).join('')}
    `;
    _updateMoneyDigest('budgets', budgets.budgets
      .filter(b => b.status === 'over')
      .map(b => ({
        sev: 'warn',
        line: `Budget over — ${b.name}: ${formatNaira(b.spent_ngn, true)} of ${formatNaira(b.limit_ngn, true)}`,
        target: '#money-budgets-section',
      })));
  } else if (budgetsSlot) {
    // Wave-2: zero budgets used to mean the whole section vanished with
    // no way to create one (chat only). Empty state with a CTA instead.
    budgetsSlot.innerHTML = budgets?.ok ? `
      <div class="section-h"><h2>Budgets</h2></div>
      <div class="card" style="margin-bottom: 8px; padding: 14px 16px;">
        <span class="data-stat-sub">No budgets yet — set a monthly cap per category and EON tracks spend against it (budgets now roll over month to month).</span>
        ${(State.member?.base_role || State.member?.role) === 'viewer' ? '' : `
        <button class="bks-tool-btn" type="button" style="margin-top:8px;" onclick="mtxBudgetOpen()">Set a budget</button>`}
      </div>` : '';
    _updateMoneyDigest('budgets', []);
  }

  // Recent transactions. PR MONEY-COCKPIT: honest count label — the
  // API returns at most the 10 requested rows with no total, so say
  // "10 most recent · <window>" instead of the old hardcoded
  // "10 this month" (wrong for every other chip); explicit error
  // state distinct from true empty.
  const txSlot = $('#money-tx-list');
  const txCount = $('#money-tx-count');
  if (txErr && txSlot) {
    if (txCount) txCount.textContent = '';
    txSlot.innerHTML = `
      <div class="card bks-soft-err">
        <span>Couldn't load — </span>
        <button class="bks-retry-btn" type="button" onclick="refreshMoney()">Retry</button>
        <span class="bks-soft-err-msg">${esc(String(txErr).slice(0, 120))}</span>
      </div>`;
  } else if (txData?.transactions?.length > 0) {
    const n = txData.transactions.length;
    const total = txData.total_count ?? txData.total;
    if (txCount) txCount.textContent = (total && total > n)
      ? `${n} of ${total} · ${w.label}`
      : `${n} most recent · ${w.label}`;
    if (txSlot) {
      txSlot.innerHTML = _renderMoneyRecent(txData.transactions);
      _wireTxReview(txSlot);
      _vaultPrintTape(txSlot);   // HOUSE floor 8 — the register prints
    }
  } else if (txSlot) {
    if (txCount) txCount.textContent = '';
    txSlot.innerHTML = `
      <div class="empty" style="padding: 32px 24px;">
        <div class="empty-mark">○</div>
        <div class="empty-sub">No transactions yet in this period. Use “+ Log a transaction” above to add an entry, or choose another period.</div>
      </div>`;
  }
}

// HOUSE floor 8 — THE VAULT's one kinetic signature. The register is a
// receipt printer: rows feed down off the head, once, when the tape
// first scrolls into view. Everything about it is inert off the house
// skin — the class that hides a row is scoped [data-theme="house"], so
// flipping skins mid-session can never strand an invisible register.
// Reduced motion lands every row settled (EONHouse.signature fires
// immediately in that case; this belt-and-braces path covers a missing
// motion engine too).
function _vaultPrintTape(tape) {
  if (!tape || !mnyHouse()) return;
  const rows = tape.querySelectorAll('.mtx-row');
  if (!rows.length) return;
  const still = !!(window.EONHouse && window.EONHouse.reduced);
  if (still || !window.EONHouse || typeof window.EONHouse.signature !== 'function') {
    rows.forEach((r) => r.classList.add('hs-vault-tx', 'hs-fed'));
    return;
  }
  rows.forEach((r) => r.classList.add('hs-vault-tx'));
  window.EONHouse.signature(tape, () => {
    rows.forEach((r, i) => setTimeout(
      () => r.classList.add('hs-fed'), Math.min(i, 14) * 70));
  });
}

// PR EMAIL-INBOUND: one-tap approve/dismiss on pending_review drafts
// (email bank alerts, FX-pending entries). Delegated on the slot via
// .onclick assignment so re-renders stay idempotent; optimistic repaint
// (rehydrate-on-toggle is a known defect class) with a full refresh
// only on failure.
function _wireTxReview(slot) {
  slot.onclick = async (e) => {
    const btn = e.target.closest('[data-tx-review]');
    if (!btn) return;
    const wrap = btn.closest('.mtx-review');
    const txid = wrap?.dataset.txid;
    if (!txid) return;
    const action = btn.dataset.txReview;
    const row = btn.closest('.mtx-row');
    wrap.querySelectorAll('button').forEach((b) => { b.disabled = true; });
    try {
      const res = await api(`/api/accounting/transactions/${txid}/review`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      if (!res || res.ok === false) throw new Error(res?.error || 'failed');
      wrap.remove();
      row?.querySelector('.mtx-tag.warn')?.remove();
      if (action === 'reject' && row) row.style.opacity = '0.45';
    } catch (err) {
      showToast(`Couldn't ${action === 'approve' ? 'approve' : 'dismiss'} that — ${String(err.message || err).slice(0, 80)}`);
      refreshMoney();
    }
  };
}

// 2026-06-09 (Daniel): redesigned the "Recent" expenses list to the
// Customers-tab/cohort structure — a summary strip on top, then entries
// grouped by day with a per-day net + clean tabular rows. Uses occurred_at
// (the real expense/income date) for grouping, so dated logs land on the
// right day. COGS-reversal rows stay muted + tagged; pending rows flagged.
function _renderMoneyRecent(txns) {
  const isRev = (tx) => (tx.description || '').startsWith('COGS reversal');
  const fmtAbs = (n) => `₦${Math.round(Math.abs(Number(n) || 0)).toLocaleString('en-US')}`;

  let totalOut = 0, totalIn = 0;
  txns.forEach((tx) => {
    if (isRev(tx)) return;  // accounting reversal, not a real cash movement
    // Vet 2026-07-27: amount is source-currency; amount_ngn is the ₦
    // truth the register already uses. Same row, same number, one page.
    const amt = Number(tx.amount_ngn ?? tx.amount) || 0;
    if (tx.kind === 'income') totalIn += amt; else totalOut += amt;
  });

  // group by occurred_at day (most recent first)
  const groups = {};
  txns.forEach((tx) => {
    const day = (tx.occurred_at || '').slice(0, 10) || 'date not recorded';
    (groups[day] = groups[day] || []).push(tx);
  });
  const days = Object.keys(groups).sort().reverse();

  const summary = `
    <div class="mtx-summary">
      <div class="mtx-sum-item"><span class="mtx-sum-v out">−${fmtAbs(totalOut)}</span><span class="mtx-sum-l">Out</span></div>
      <div class="mtx-sum-item"><span class="mtx-sum-v in">+${fmtAbs(totalIn)}</span><span class="mtx-sum-l">In</span></div>
      <div class="mtx-sum-item"><span class="mtx-sum-v">${txns.length}</span><span class="mtx-sum-l">Entries</span></div>
    </div>`;

  const dayBlocks = days.map((day) => {
    const rows = groups[day];
    let net = 0;
    rows.forEach((tx) => {
      // PR MONEY-COCKPIT: reversals are excluded here exactly like the
      // summary strip above excludes them — previously the strip
      // skipped them but the day-net SUBTRACTED them (an income-kind
      // reversal fell through to the minus branch), so the two
      // aggregations disagreed on the same rows.
      if (isRev(tx)) return;
      const a = Number(tx.amount_ngn ?? tx.amount) || 0;
      net += (tx.kind === 'income') ? a : -a;
    });
    const dLabel = day === 'date not recorded'
      ? 'Undated'
      : new Date(day + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const netStr = `${net >= 0 ? '+' : '−'}${fmtAbs(net)}`;

    const rowsHtml = rows.map((tx, i) => {
      const isIncome = tx.kind === 'income';
      const rev = isRev(tx);
      // PR MONEY-COCKPIT: a COGS reversal backs OUT an expense — money
      // returned, not spent. It was hardcoded '−' regardless, rendering
      // expense-reversals as fresh negatives.
      const sign = rev ? '+' : (isIncome ? '+' : '−');
      const cls = rev ? 'rev' : (isIncome ? 'in' : 'out');
      const cat = tx.transaction_categories?.name || 'Uncategorized';
      const isPending = tx.status === 'pending_review';
      // Email bank alerts (PR EMAIL-INBOUND) land as pending_review
      // drafts — approve/reject inline, one tap each (no dialogs, the
      // hold-to-approve lane is for money-MOVING decisions; this only
      // records what the bank already did).
      // Wave-1 403 sweep: the review route needs ACCOUNTING_LOG
      // (member+) — the viewer role saw Approve/Dismiss, tapped, and
      // got a 403 toast. Viewers now see the pending tag only.
      const canReviewTx = (State.member?.role || '') !== 'viewer';
      const reviewBtns = isPending && tx.id && canReviewTx ? `
              <span class="mtx-review" data-txid="${esc(tx.id)}">
                <button class="mtx-review-btn ok" data-tx-review="approve" type="button">Approve</button>
                <button class="mtx-review-btn no" data-tx-review="reject" type="button">Dismiss</button>
              </span>` : '';
      return `
        <div class="mtx-row" style="animation-delay:${Math.min(i, 12) * 0.03}s">
          <div class="mtx-row-main">
            <span class="mtx-desc">${esc(tx.description || '—')}</span>
            <span class="mtx-tags">
              <span class="mtx-cat">${esc(cat)}</span>
              ${rev ? '<span class="mtx-tag">reversal</span>' : ''}
              ${isPending ? '<span class="mtx-tag warn">review</span>' : ''}
              ${reviewBtns}
            </span>
          </div>
          <span class="mtx-amt ${cls}">${sign}${fmtAbs(tx.amount_ngn ?? tx.amount)}</span>
        </div>`;
    }).join('');

    return `
      <div class="mtx-day">
        <div class="mtx-day-head">
          <span class="mtx-day-label">${esc(dLabel)}</span>
          <span class="mtx-day-net ${net >= 0 ? 'in' : 'out'}">${netStr}</span>
        </div>
        <div class="mtx-rows">${rowsHtml}</div>
      </div>`;
  }).join('');

  return `<div class="mtx">${summary}${dayBlocks}</div>`;
}

function formatNaira(n, compact = false) { return ngn(n, { compact }); }

// ──────────────────────────────────────────────────────────
// Money-page redesign 2026-06-07 — animation + chart helpers
//
// All vanilla. No new deps. Charts are inline SVG; animation is CSS
// transform/opacity + a tiny rAF count-up. prefers-reduced-motion is
// honoured by _prefersReducedMotion() so the whole page degrades to
// instant, no-animation rendering for users who ask for it.
// ──────────────────────────────────────────────────────────

function _prefersReducedMotion() {
  try {
    return window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (_) { return false; }
}

// Animate a numeric element from 0 → target. `formatter(value)` turns
// the in-flight number into display text. Cancels any prior run on the
// same node (data-countup-raf) so rapid period switches don't stack.
function _countUp(el, target, formatter, durationMs = 720) {
  if (!el) return;
  target = Number(target) || 0;
  if (el.dataset.countupRaf) {
    cancelAnimationFrame(Number(el.dataset.countupRaf));
    delete el.dataset.countupRaf;
  }
  if (_prefersReducedMotion()) { el.textContent = formatter(target); return; }
  const start = performance.now();
  const from = 0;
  const ease = (t) => 1 - Math.pow(1 - t, 3);  // easeOutCubic
  const step = (now) => {
    const t = Math.min(1, (now - start) / durationMs);
    const val = from + (target - from) * ease(t);
    el.textContent = formatter(val);
    if (t < 1) {
      el.dataset.countupRaf = String(requestAnimationFrame(step));
    } else {
      el.textContent = formatter(target);
      delete el.dataset.countupRaf;
    }
  };
  el.dataset.countupRaf = String(requestAnimationFrame(step));
}

// Smooth catmull-rom-ish path for a line chart (rounded, no library).
function _smoothLinePath(points) {
  if (!points.length) return '';
  if (points.length === 1) return `M ${points[0][0]} ${points[0][1]}`;
  let d = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i === 0 ? 0 : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2 < points.length ? i + 2 : i + 1];
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2[0]} ${p2[1]}`;
  }
  return d;
}

// Chart displays preserve missing amounts; only explicit finite numbers are known.
const _moneyChartKnown = value => typeof value === 'number' && Number.isFinite(value);
const _moneyChartAmount = value => _moneyChartKnown(value) ? fmtNGN(value) : 'Unknown';
const _moneyChartExact = value => _moneyChartKnown(value) ? new Intl.NumberFormat('en-NG',{minimumFractionDigits:2,maximumFractionDigits:2}).format(value) : 'Unknown';
const _moneyChartNet = day => _moneyChartKnown(day.revenue) && _moneyChartKnown(day.expense) ? day.revenue - day.expense : null;

// Supplied daily amounts, with explicit limits of the existing daily feed.
function _chartTrendLine(series) {
  if (!series.length) return _chartEmpty('No daily data for this window yet.');
  const rev = series.map(d => d.revenue), net = series.map(_moneyChartNet);
  const all = rev.concat(net).filter(_moneyChartKnown);
  if (!all.length) return _chartEmpty('Daily amounts are not established for this window.');
  const maxV = Math.max(0, ...all), minV = Math.min(0, ...all), span = Math.max(maxV - minV, 1);
  const x = i => 54 + (series.length === 1 ? .5 : i / (series.length - 1)) * 254;
  const y = v => 150 - (v - minV) / span * 134;
  const path = values => values.map((v,i) => _moneyChartKnown(v) ? `${i === 0 || !_moneyChartKnown(values[i-1]) ? 'M' : 'L'}${x(i).toFixed(2)},${y(v).toFixed(2)}` : '').join(' ');
  const last = series.length - 1;
  const ticks = minV < 0 && maxV > 0 ? [minV, 0, maxV] : [...new Set([minV, (minV + maxV) / 2, maxV])];
  const grids = ticks.map(v => `<line class="${v===0?'money-chart-zero':'money-chart-grid'}" x1="54" x2="308" y1="${y(v)}" y2="${y(v)}" stroke="currentColor" stroke-opacity="${v===0?.45:.16}" vector-effect="non-scaling-stroke"/><text x="47" y="${y(v)+4}" text-anchor="end" fill="currentColor" font-size="13">${esc(fmtNGN(v))}</text>`).join('');
  return `<p class="money-chart-note">Missing or unreadable daily records can appear as ₦0.</p><svg class="money-chart-svg" viewBox="0 0 320 190" role="img" aria-label="Daily revenue and revenue less counted spending. Missing daily records can appear as zero. Spending includes expected payroll and rent. Daily values follow.">
    ${grids}
    <path d="${path(rev)}" fill="none" stroke="var(--ink-dim)" stroke-width="2" vector-effect="non-scaling-stroke"/>
    <path d="${path(net)}" fill="none" stroke="var(--accent)" stroke-width="2" vector-effect="non-scaling-stroke"/>
    ${[rev,net].map((values,j)=>values.map((v,i)=>_moneyChartKnown(v)?`<circle cx="${x(i)}" cy="${y(v)}" r="2.5" fill="${j?'var(--accent)':'var(--ink-dim)'}"/>`:'').join('')).join('')}
    <text x="54" y="178" fill="currentColor" font-size="13">${esc(series[0].date)}</text>
    ${last?`<text x="308" y="178" text-anchor="end" fill="currentColor" font-size="13">${esc(series[last].date)}</text>`:''}
  </svg>
  <p class="money-chart-caption">Latest day · ${esc(series[last].date)}</p>
  <div class="money-chart-legend">
    <span><i class="dot" style="background:var(--ink-dim)"></i>Revenue ${_moneyChartAmount(rev[last])}</span>
    <span><i class="dot" style="background:var(--accent)"></i>After counted spending ${_moneyChartAmount(net[last])}</span>
  </div>
  <p class="money-chart-note">Counted spending includes expected payroll and rent when not fully logged. This difference is not your bank balance or full profit.</p>
  <details class="money-chart-values"><summary>Read each day</summary><div><table><caption>Daily amounts · NGN · includes estimates</caption><thead><tr><th scope="col">Date</th><th scope="col">Revenue</th><th scope="col">Counted spending</th><th scope="col">Difference</th></tr></thead><tbody>${series.map((d,i)=>`<tr><th scope="row">${esc(d.date)}</th><td>${_moneyChartExact(d.revenue)}</td><td>${_moneyChartExact(d.expense)}</td><td>${_moneyChartExact(net[i])}</td></tr>`).join('')}</tbody></table></div></details>`;
}

// A cost share is meaningful only when every supplied component is known.
function _chartExpenseBreakdown(parts) {
  const complete = parts.every(p => _moneyChartKnown(p.value));
  const nonnegative = complete && parts.every(p => p.value >= 0);
  const total = complete ? parts.reduce((sum,p) => sum + p.value, 0) : null;
  if (nonnegative && total === 0) return _chartEmpty('No expense components logged for this window.');
  const shares = nonnegative && total > 0;
  const segments = shares ? parts.filter(p=>p.value>0).map(p=>`<div class="ebar-seg" style="width:${(p.value/total*100).toFixed(1)}%;background:${p.color}" title="${esc(p.label)} ${fmtNGN(p.value)}"></div>`).join('') : '';
  const rows = parts.map(p=>`<div class="ebar-row"><span class="ebar-key"><i class="dot" style="background:${p.color}"></i>${esc(p.label)}</span><span class="ebar-val mono-num">${_moneyChartAmount(p.value)}${shares?` · ${(p.value/total*100).toFixed(0)}%`:''}</span></div>`).join('');
  const note = !complete ? 'The cost split is incomplete. Missing amounts are shown as unknown; percentages are withheld.' : !nonnegative ? 'These amounts include a negative adjustment. Percentages are withheld.' : '';
  return `${shares?`<div class="ebar-track">${segments}</div>`:''}<div class="ebar-rows">${rows}</div>${note?`<p class="money-chart-note">${note}</p>`:''}`;
}

// Daily revenue less counted spending; this is not a cash-account balance.
function _chartCashflowBars(series) {
  if (!series.length) return _chartEmpty('No daily data for this window yet.');
  const net = series.map(_moneyChartNet), values = net.filter(_moneyChartKnown);
  const maxAbs = Math.max(1, ...values.map(Math.abs));
  const bars = net.map((v,i) => {
    if (!_moneyChartKnown(v)) return `<div class="cfbar-col" title="${esc(series[i].date)}: Unknown"><span aria-hidden="true">?</span></div>`;
    const height = (Math.abs(v)/maxAbs*46).toFixed(1), up = v >= 0;
    return `<div class="cfbar-col"><span class="cfbar ${up?'cfbar-up':'cfbar-down'}" style="height:${height}%;${up?'bottom':'top'}:50%" title="${esc(series[i].date)}: ${fmtNGN(v)}"></span></div>`;
  }).join('');
  const complete = values.length === series.length;
  const sum = complete ? values.reduce((s,v)=>s+v,0) : null;
  return `<div class="cfbar-track"><div class="cfbar-mid"></div>${bars}</div>
    <div class="money-chart-legend"><span class="mono-num">${complete?`${sum>=0?'+':''}${fmtNGN(sum)} across ${series.length} day${series.length===1?'':'s'} shown`:'Total unknown · some daily amounts are missing'}</span></div>`;
}

function _chartEmpty(msg) {
  return `<div class="money-chart-empty">${esc(msg)}</div>`;
}

// Wave-1 error≠empty: shared failed-card for Money sections — an outage
// says Error + Retry, never a generic empty card.
function _moneyFailCard(title, why) {
  return `
    <div class="data-card">
      <div class="data-card-head"><span class="data-card-title">${esc(title)}</span>
        <span class="pill pill-danger">Error</span></div>
      <div class="data-stat-sub">Couldn't load — ${esc(String(why || 'error').slice(0, 120))}
        <button class="hero-retry-btn" onclick="refreshMoney()">Retry</button></div>
    </div>`;
}

// Wave-1 error≠empty: when BOTH chart feeds fail, the three skeleton
// cards used to spin forever (no code path touched #money-charts on
// error). Paint an explicit failed state with a retry instead.
function renderMoneyChartsError(msg) {
  const slot = document.getElementById('money-charts');
  if (!slot) return;
  slot.innerHTML = `
    <div class="money-chart-empty">
      Charts couldn't load — ${esc(String(msg || 'error').slice(0, 120))}
      <button class="hero-retry-btn" onclick="refreshMoney()">Retry</button>
    </div>`;
}

// Build all three charts from data already on the page. Called by
// bindMoney after the insights + summary fetches resolve. Re-runs on
// every period switch (render() re-mounts the chart skeletons).
function renderMoneyCharts(insights, summary) {
  const slot = document.getElementById('money-charts');
  if (!slot) return;
  const exp = (insights && insights.expenses) || {};
  const expSeries = (exp.sparkline_7d || []);
  const revSeries = (exp.revenue_sparkline_7d || []);
  // Zip the two day-keyed series into one [{date, revenue, expense}].
  const byDate = {};
  expSeries.forEach(d => { byDate[d.date] = { date: d.date, expense: _moneyChartKnown(d.amount_ngn) ? d.amount_ngn : null, revenue: null }; });
  revSeries.forEach(d => {
    byDate[d.date] = byDate[d.date] || { date: d.date, expense: null, revenue: null };
    byDate[d.date].revenue = _moneyChartKnown(d.amount_ngn) ? d.amount_ngn : null;
  });
  const series = Object.values(byDate).sort((a, b) => (a.date < b.date ? -1 : 1));

  // Expense breakdown from the accounting summary (already fetched).
  const s = summary || {};
  const parts = [
    { label: 'Stock cost', value: s.cogs_ngn,           color: 'var(--ink-dim)' },
    { label: 'Running costs', value: s.logged_expense_ngn, color: 'var(--ink)' },
    { label: 'Ad spend', value: s.ad_spend_ngn,       color: 'var(--accent)' },
  ];

  const days = exp.period_days || series.length || 0;
  const winEl = document.getElementById('money-charts-window');
  if (winEl) winEl.textContent = days ? `last ${days} day${days === 1 ? '' : 's'}` : '';

  const card = (title, sub, body, i) => `
    <div class="money-chart-card stagger-rise" style="--i:${i}">
      <div class="money-chart-head">
        <span class="money-chart-title">${esc(title)}</span>
        ${sub ? `<span class="money-chart-sub">${esc(sub)}</span>` : ''}
      </div>
      <div class="money-chart-body">${body}</div>
    </div>`;

  slot.innerHTML =
    card('Revenue & daily difference', 'daily', _chartTrendLine(series), 0) +
    card('Expense breakdown', 'this period', _chartExpenseBreakdown(parts), 1) +
    card('Daily difference', 'includes payroll/rent estimates', _chartCashflowBars(series), 2);
}

// ──────────────────────────────────────────────────────────
// Settings

function renderSettings() {
  // Show team/usage sections only to admins+ (since the API requires it).
  // Members can still see workspace/integrations/appearance but not invite.
  const role = State.member?.role || 'member';
  const canManageTeam = ['owner', 'admin', 'manager'].includes(role);

  // v3.0p18 9aa: Cash section moved out of Settings to the Money view,
  // which is the user-correct home for cash/runway/accounts. Settings
  // is the wrong shelf for an operationally hot read.
  return `
    <div class="section-h">
      <h2>Settings</h2>
    </div>
    <p class="set-intro">Workspace, connections, and appearance up top. Technical &amp; admin controls are tucked into the groups below to keep this calm.</p>

    <div class="settings-section">
      <h3>Workspace</h3>
      <div class="row"><div class="row-content"><div class="row-label">${esc(State.tenant?.name || 'Workspace')}</div><div class="row-value">${esc(State.tenant?.slug || '')}</div></div></div>
      ${State.isStaff ? '' : (State.workspaces || []).filter(t => t.slug !== State.tenant?.slug).map(t => `<a class="row settings-action" data-workspace-open href="/?v=settings&amp;workspace=${encodeURIComponent(t.slug)}"><span class="row-content"><span class="row-label">${esc(t.name || t.slug)}</span><span class="row-value">You are in this workspace too</span></span><span class="row-action">Open</span></a>`).join('')}
      <div class="row"><div class="row-content"><div class="row-label">Your role</div><div class="row-value">${esc(State.member?.role || 'member')}</div></div></div>
    </div>

    <div class="settings-section">
      <h3>Integrations</h3>
      <div id="integrations-list"><div class="row" style="opacity:0.5;"><div class="row-content"><div class="row-label">Loading…</div></div></div></div>
      <div class="meta-connect-row" id="meta-connect-row">
        <div class="meta-connect-info">
          <div class="meta-connect-title">Meta · ads_management (writes)</div>
          <div class="meta-connect-sub" id="meta-connect-status">Checking…</div>
        </div>
        <button class="btn-primary" id="meta-connect-btn" type="button" disabled>Connect</button>
      </div>
      <div class="meta-connect-row" id="ig-connect-row">
        <div class="meta-connect-info">
          <div class="meta-connect-title">Instagram · the bigbag account</div>
          <div class="meta-connect-sub" id="ig-connect-status">Sign in to Instagram so its messages land in the inbox and replies send from it. Instagram stops trusting the sign-in after 60 days — do this again when messages stop arriving.</div>
        </div>
        <div style="display:flex; gap:8px; align-items:center; flex-shrink:0;">
          <input id="ig-connect-label" type="text" value="bigbag" maxlength="40"
            aria-label="Which Instagram account"
            style="width:110px; padding:7px 10px; border:1px solid var(--line); border-radius:8px; background:var(--bg); color:var(--ink); font-size:12px;" />
          <button class="btn-primary" id="ig-connect-btn" type="button">Connect Instagram</button>
        </div>
      </div>
    </div>

    ${canManageTeam ? `
    <div class="settings-section">
      <h3>Telegram briefings</h3>
      <div style="font-size:12px; opacity:0.65; margin:0 4px 10px;">Chats that receive the 6am operator brief, 9am team brief, and mid-day alerts. Give EON's Telegram bot access, DM /start, then paste the chat_id.</div>
      <div id="telegram-bindings-list"><div class="row" style="opacity:0.5;"><div class="row-content"><div class="row-label">Loading…</div></div></div></div>
      <button id="telegram-binding-add-btn" class="btn-primary" style="margin-top:12px; width:100%;">Add Telegram chat</button>
    </div>` : ''}

    <div class="settings-section">
      <h3>Appearance</h3>
      ${cg.entry('people')}
      <div style="font-size:11px; text-transform:uppercase; letter-spacing:0.5px; color:var(--ink-soft); margin-bottom:8px; padding:0 4px;">Theme</div>
      ${[
        // HOUSE ROLLOUT (operator, 2026-08-07): House is the default and
        // leads the list; Flow is HIDDEN from the picker but not deleted —
        // flow.css is 20KB of inert scoped rules, eon_theme='flow' and
        // ?skin=flow still work, so devices already on Flow keep it and
        // the system stays modifiable later. Deleting it would also trip
        // the deletion gate for zero weight saved.
        { value: 'house',   label: 'House', desc: 'The nine-floor skin — the default. Signage voice, one moving part per room; un-adopted floors render Classic dark.' },
        { value: 'classic', label: 'Classic', desc: 'The previous EON look — pick dark or light mode below.' },
        // Opus vet M-9: a device still ON flow saw a picker with nothing
        // selected. Flow renders as an option ONLY where it is the current
        // skin — its exit door, not an entry.
        ...(document.documentElement.getAttribute('data-theme') === 'flow'
          ? [{ value: 'flow', label: 'Design Flow', desc: 'This device is still on Flow. It keeps working; pick House or Classic to move off.' }] : []),
      ].map(opt => {
        const _skinT = document.documentElement.getAttribute('data-theme');
        const curSkin = (_skinT === 'flow' || _skinT === 'house') ? _skinT : 'classic';
        const active = curSkin === opt.value;
        return `
          <label class="row theme-option" data-theme-option="${esc(opt.value)}" style="cursor:pointer; ${active ? 'background: var(--accent-soft);' : ''}">
            <div class="row-content">
              <div class="row-label"><input type="radio" name="theme-skin" value="${esc(opt.value)}" ${active ? 'checked' : ''} style="margin-right:8px;" />${esc(opt.label)}</div>
              <div class="row-value" style="font-size:12px; opacity:0.65; margin-left:24px;">${esc(opt.desc)}</div>
            </div>
          </label>`;
      }).join('')}
      ${document.documentElement.getAttribute('data-theme') === 'flow' ? `
      <button type="button" class="row settings-action" id="flow-mode-row"><span class="row-content"><span class="row-label">Mode</span><span class="row-value">${document.documentElement.getAttribute('data-flow') === 'ink' ? '[ INK ]' : '[ BONE ]'}</span></span><span class="row-action">Toggle</span></button>` : document.documentElement.getAttribute('data-theme') === 'house' ? `` : `
      <button type="button" class="row settings-action" id="theme-row"><span class="row-content"><span class="row-label">Mode</span><span class="row-value">${document.documentElement.getAttribute('data-theme')}</span></span><span class="row-action">Toggle</span></button>`}
      <div style="margin-top:12px;">
        <div style="font-size:11px; text-transform:uppercase; letter-spacing:0.5px; color:var(--ink-soft); margin-bottom:8px; padding:0 4px;">Chat appearance</div>
        ${[
          { value: 'comfortable', label: 'Comfortable', desc: 'Default bubble chat, dense, brand-colored.' },
          { value: 'claude-code', label: 'Claude Code',  desc: 'Minimal terminal style — lines, boxes, monospace tool blocks.' },
        ].map(opt => {
          const cur = localStorage.getItem('eon_chat_style') || 'comfortable';
          const active = cur === opt.value;
          return `
            <label class="row chat-style-option" data-chat-style="${esc(opt.value)}" style="cursor:pointer; ${active ? 'background: var(--accent-bg, rgba(16,185,129,0.08));' : ''}">
              <div class="row-content">
                <div class="row-label"><input type="radio" name="chat-style" value="${esc(opt.value)}" ${active ? 'checked' : ''} style="margin-right:8px;" />${esc(opt.label)}</div>
                <div class="row-value" style="font-size:12px; opacity:0.65; margin-left:24px;">${esc(opt.desc)}</div>
              </div>
            </label>`;
        }).join('')}
      </div>
    </div>

    ${canManageTeam ? `
    <details class="settings-group">
      <summary>Team &amp; usage</summary>
      <div class="settings-section">
        <h3>Team</h3>
        <div id="members-list"><div class="row" style="opacity:0.5;"><div class="row-content"><div class="row-label">Loading members…</div></div></div></div>
        <div id="invites-section" style="margin-top:16px;">
          <div style="font-size:11px; text-transform:uppercase; letter-spacing:0.5px; color:var(--ink-soft); margin-bottom:8px; padding:0 4px;">Pending invites</div>
          <div id="invites-list"><div class="row" style="opacity:0.5;"><div class="row-content"><div class="row-label">Loading invites…</div></div></div></div>
        </div>
        <button id="invite-btn" class="btn-primary" style="margin-top:12px; width:100%;">Invite teammate</button>
      </div>
      <div class="settings-section">
        <h3>Usage</h3>
        <div id="usage-summary"><div class="row" style="opacity:0.5;"><div class="row-content"><div class="row-label">Loading usage…</div></div></div></div>
      </div>
    </details>

    <details class="settings-group">
      <summary>EON behaviour</summary>
      <div class="settings-section">
        <h3>Agent model</h3>
        <div id="agent-model-row" class="row"><div class="row-content"><div class="row-label">Model</div><div class="row-value" id="agent-model-current">Loading…</div></div></div>
        <div id="agent-model-options" style="margin-top:8px;"></div>
      </div>
      <div class="settings-section">
        <h3>Ads brain</h3>
        <div style="font-size:12px; opacity:0.65; margin:0 4px 10px;">Model behind the marketing brain — analyst, media buyer, creative director, copywriter, ads coach. Falls back to Opus 4.8 automatically if the frontier model is unavailable.</div>
        <div id="ads-model-row" class="row"><div class="row-content"><div class="row-label">Model</div><div class="row-value" id="ads-model-current">Loading…</div></div></div>
        <div id="ads-model-options" style="margin-top:8px;"></div>
      </div>
      <div class="settings-section" id="voice-picker-section">
        <h3>EON voice</h3>
        <div style="font-size:12px; opacity:0.65; margin:0 4px 10px;">ElevenLabs voice + model for spoken replies. Click a voice to preview.</div>
        <div class="row" style="padding-bottom:8px;"><div class="row-content"><div class="row-label">Current</div><div class="row-value" id="voice-current-name">Loading…</div></div></div>
        <div class="row" style="padding-bottom:8px;"><div class="row-content"><div class="row-label">Model</div><div class="row-value">
          <select id="voice-model-select" style="padding:4px 6px; font-size:13px;">
            <option value="">(env default)</option>
            <option value="eleven_multilingual_v2">eleven_multilingual_v2 (higher quality)</option>
            <option value="eleven_turbo_v2_5">eleven_turbo_v2_5 (fast)</option>
            <option value="eleven_v3">eleven_v3 (latest)</option>
          </select>
        </div></div></div>
        <div id="voice-list" style="max-height:320px; overflow-y:auto; margin-top:8px;"><div class="row" style="opacity:0.5;"><div class="row-content"><div class="row-label">Loading voices…</div></div></div></div>
      </div>
      <div class="settings-section">
        <h3>Data ingest <span style="font-size:11px; opacity:0.55; font-weight:normal;">(dlt pipelines)</span></h3>
        <div style="font-size:12px; opacity:0.65; margin:0 4px 10px;">Per-platform ingest enablement. Flip a platform on once its credentials are verified in Integrations; it pulls on the next cron.</div>
        <div id="ingest-flags-list"><div class="row" style="opacity:0.5;"><div class="row-content"><div class="row-label">Loading…</div></div></div></div>
      </div>
    </details>

    <details class="settings-group">
      <summary>Developer &amp; automation</summary>
      ${role === 'owner' ? `
      <div class="settings-section">
        <h3>Switchboard <span style="font-size:11px; opacity:0.55; font-weight:normal;">(every gated feature)</span></h3>
        <div style="font-size:12px; opacity:0.65; margin:0 4px 10px;">Every automation EON ships dark behind a flag — what it does, what happens when it flips, and its LIVE state on this deployment. Flip by setting the env var on the Railway service; it applies on the next deploy.</div>
        <div id="switchboard-summary" style="font-size:12px; margin:0 4px 10px;"></div>
        <div id="switchboard-list"><div class="row" style="opacity:0.5;"><div class="row-content"><div class="row-label">Loading switchboard…</div></div></div></div>
      </div>
      <div class="settings-section">
        <h3>External MCP servers</h3>
        <div style="font-size:12px; opacity:0.65; margin:0 4px 10px;">Outbound MCP servers EON's agent can call. Bearer tokens are encrypted at rest and never re-displayed.</div>
        <div id="mcp-servers-list"><div class="row" style="opacity:0.5;"><div class="row-content"><div class="row-label">Loading…</div></div></div></div>
        <button id="mcp-add-btn" class="btn-primary" style="margin-top:10px; width:100%;">Add MCP server</button>
        <div id="mcp-add-form" style="display:none; margin-top:12px; padding:12px; border:1px solid var(--line); border-radius:8px;">
          <div style="margin-bottom:8px;"><input id="mcp-add-name" type="text" placeholder="name (lowercase, e.g. supabase-readonly)" style="width:100%; padding:6px 8px; font-size:13px;" /></div>
          <div style="margin-bottom:8px;"><input id="mcp-add-url" type="text" placeholder="https://...mcp endpoint" style="width:100%; padding:6px 8px; font-size:13px;" /></div>
          <div style="margin-bottom:8px;"><input id="mcp-add-token" type="password" placeholder="bearer token (optional)" style="width:100%; padding:6px 8px; font-size:13px;" /></div>
          <div style="display:flex; gap:8px;"><button id="mcp-add-save" class="btn-primary" style="flex:1;">Save</button><button id="mcp-add-cancel" class="btn-secondary" style="flex:1;">Cancel</button></div>
          <div id="mcp-add-error" style="margin-top:8px; font-size:12px; color:var(--bad, #ef4444);"></div>
        </div>
      </div>` : ''}
      ${canManageTeam ? `
      <div class="settings-section">
        <h3>Claude oversight findings</h3>
        <div style="font-size:12px; opacity:0.65; margin:0 4px 10px;">Drifts, anomalies, and fixes logged during Claude review sessions.</div>
        <div id="oversight-filter-chips" style="margin-bottom:10px;">
          <button class="chip chip-active" data-oversight-status="active">Active</button>
          <button class="chip" data-oversight-status="fixed">Fixed</button>
          <button class="chip" data-oversight-status="all">All</button>
        </div>
        <div id="oversight-findings-list"><div class="row" style="opacity:0.5;"><div class="row-content"><div class="row-label">Loading…</div></div></div></div>
      </div>
      <div class="settings-section">
        <h3>Feature requests <span style="font-size:11px; opacity:0.55; font-weight:normal;">(auto-build queue)</span></h3>
        <div style="font-size:12px; opacity:0.65; margin:0 4px 10px;">Build briefs queued for the headless agent. Ask EON: "build a feature that…" — it files here.</div>
        <div id="fr-filter-chips" style="margin-bottom:10px;">
          <button class="chip chip-active" data-fr-status="active">Active</button>
          <button class="chip" data-fr-status="merged">Merged</button>
          <button class="chip" data-fr-status="abandoned">Abandoned</button>
          <button class="chip" data-fr-status="all">All</button>
        </div>
        <div id="fr-list"><div class="row" style="opacity:0.5;"><div class="row-content"><div class="row-label">Loading…</div></div></div></div>
      </div>` : ''}
    </details>` : ''}

    <div class="settings-section">
      <h3>Account</h3>
      <button type="button" class="row settings-action danger" id="logout-row"><span class="row-content"><span class="row-label">Sign out</span></span></button>
    </div>
  `;
}

// WS-B (2026-07-26): repaint the Settings view IN PLACE. The theme /
// chat-style previews used to call full render(), which rebuilt #app
// and hurled the operator from the Appearance section back to the top
// of the page on every try. Keeping the <main> element (only swapping
// its children) also avoids replaying the viewIn entry animation.
function _repaintSettingsView() {
  const main = document.querySelector('main.view');
  if (!main || State.view !== 'settings') { render(); return; }
  const sc = main.scrollTop;
  const active = document.activeElement;
  const focus = main.contains(active)
    ? { id: active.id, name: active.getAttribute('name'), value: active.value }
    : null;
  main.innerHTML = renderSettings();
  lw.mountSettings();
  bindSettings();
  main.scrollTop = sc;
  // Replacing the controls must not strand a keyboard user at the document.
  const next = focus?.id ? document.getElementById(focus.id)
    : focus?.name ? [...main.querySelectorAll('input[name]')].find(input =>
      input.name === focus.name && input.value === focus.value) : null;
  if (next && main.contains(next)) next.focus({ preventScroll: true });
}

async function bindSettings() {
  // The Meta connect's outcome, taken off the address FIRST, before anything
  // here awaits. render() syncs the address to ?v=settings while this
  // function waits on the network, and that used to wipe ?meta_connected
  // before it was read: no outcome, good or bad, ever reached her.
  const metaOutcome = takeMetaConnectOutcome();
  const shopifyOutcome = takeShopifyInstallOutcome();

  // Skin picker (theme v2): Classic restores the last dark/light mode;
  // Design Flow is a single-mode skin. Applies immediately (no reload)
  // so the operator can preview.
  document.querySelectorAll('.theme-option').forEach(el => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      const pick = el.dataset.themeOption;
      if (pick === 'flow') setTheme('flow');
      else if (pick === 'house') setTheme('house');
      else setTheme(localStorage.getItem('eon_classic_mode') || 'dark');
      _repaintSettingsView();
    });
  });
  // Mode row (Classic only): dark ↔ light
  $('#theme-row')?.addEventListener('click', () => {
    const cur = document.documentElement.getAttribute('data-theme');
    setTheme(cur === 'dark' ? 'light' : 'dark');
    _repaintSettingsView();
  });
  // Mode row (Design Flow only): bone ↔ ink — same idea as the
  // /marketing [ BONE ]/[ INK ] toggle, applied app-wide.
  $('#flow-mode-row')?.addEventListener('click', () => {
    const cur = document.documentElement.getAttribute('data-flow');
    setFlowMode(cur === 'ink' ? 'bone' : 'ink');
    _repaintSettingsView();
  });
  $('#logout-row')?.addEventListener('click', () => {
    if (confirm('Sign out?')) logout();
  });

  // PR CHAT-UI-PHASE1: chat style radio handler. Apply immediately
  // (no reload) so the operator can preview the change.
  document.querySelectorAll('.chat-style-option').forEach(el => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      const style = el.dataset.chatStyle;
      setChatStyle(style);
      _repaintSettingsView();  // refresh selection state, keep scroll (WS-B)
    });
  });
  
  // Team management — only renders for admin+ (gated in renderSettings)
  if ($('#members-list')) {
    refreshMembersList();
    refreshInvitesList();
    refreshUsageSummary();

    $('#invite-btn')?.addEventListener('click', () => {
      openInviteSheet();
    });
  }

  // (9aa: removed lazy-load of cash section; lives in Money now.)

  // PR MONEY-COMPLETE: agent model toggle (owner/admin only)
  if (document.getElementById('agent-model-options')) {
    refreshAgentModel();
  }

  // 2026-06-10: ads-brain model toggle (Fable 5 ↔ Opus 4.8)
  if (document.getElementById('ads-model-options')) {
    refreshAdsModel();
  }

  // Telegram bindings section (Daniel's "easier UI" ask) — only when
  // admin+ since the API requires P.MEMBERS_CHANGE_ROLE.
  if (document.getElementById('telegram-bindings-list')) {
    refreshTelegramBindings();
    $('#telegram-binding-add-btn')?.addEventListener('click', () =>
      openTelegramBindingSheet()
    );
  }

  // PR voice-picker-and-ingest-flags: voice picker + ingest flags
  if (document.getElementById('voice-picker-section')) {
    refreshVoicePicker();
  }
  if (document.getElementById('ingest-flags-list')) {
    refreshIngestFlags();
  }

  // Switchboard (owner only): every gated feature + live state
  if (document.getElementById('switchboard-list')) {
    refreshSwitchboard();
  }

  // PR EON-MCP-TWOWAY: external MCP servers (owner only)
  if (document.getElementById('mcp-servers-list')) {
    refreshMcpServers();
    bindMcpServersForm();
  }

  // PR EON-MCP-TWOWAY: oversight findings (manager+)
  if (document.getElementById('oversight-findings-list')) {
    refreshOversightFindings('active');
    bindOversightFilters();
  }

  // PR FEATURE-REQUESTS-INBOX: auto-build queue (manager+)
  if (document.getElementById('fr-list')) {
    refreshFeatureRequests('active');
    bindFeatureRequestFilters();
  }

  await refreshIntegrationsList();
  showShopifyInstallOutcome(shopifyOutcome);
  const metaNow = await refreshMetaConnectStatus();
  bindMetaConnectButton();
  bindInstagramConnectButton();
  showMetaConnectOutcome(metaOutcome, metaNow);
}


// 2026-05-19 (Daniel): Facebook sends the Meta connect back to
// /?v=settings&meta_connected=1, or =error&msg=<code>
// (routes_marketing.meta_oauth_callback). Read it once and take it off the
// address at once, so a repaint or a reload never shows it twice.
function takeMetaConnectOutcome() {
  try {
    const qp = new URLSearchParams(location.search);
    const flag = qp.get('meta_connected');
    if (flag !== '1' && flag !== 'error') return null;
    const msg = qp.get('msg') || '';
    qp.delete('meta_connected');
    qp.delete('msg');
    const rest = qp.toString();
    history.replaceState(history.state, '',
                         location.pathname + (rest ? '?' + rest : ''));
    return { ok: flag === '1', msg };
  } catch (_) { return null; }
}

// Anyone can send a link carrying ?meta_connected=1, so "connected" is said
// only when the Meta row's own status read agrees. A failure is said in the
// toast AND left on the Meta row's line: a toast is gone in 2.4 seconds,
// and the line keeps the cause and the next step until she acts.
function showMetaConnectOutcome(outcome, metaNow) {
  if (!outcome) return;
  if (outcome.ok) {
    if (metaNow && metaNow.connected) {
      showToast('Meta is connected. EON can pause and scale your ads now.');
    }
    return;
  }
  const words = metaConnectFailureWords(outcome.msg);
  showToast(words);
  const sub = $('#meta-connect-status');
  if (sub && !(metaNow && metaNow.connected)) sub.textContent = words;
}


// The Meta connect comes back with a short code in the web address
// (routes_marketing.meta_oauth_callback, which only ever sends a plain
// code). She reads a sentence instead: what went wrong, and a next step
// that can actually work. Every code the callback can send has its own
// sentence here; tests/test_meta_connect_outcome_words_20261002.py drives
// each failure through the real callback and fails if one falls through.
// (First vet on 0b8b37bd, 1 Oct: the old fallback swallowed four causes,
// no_ad_accounts among them, behind "Tap Connect to try again".)
function metaConnectFailureWords(msg) {
  const later = 'Tap Connect again in a minute.';
  const handover = 'Facebook said yes, but the handover to EON broke '
                   + 'halfway, so nothing was saved. ' + later;
  const words = {
    state_expired:      'That took longer than 15 minutes, so the sign-in '
                        + 'expired. Tap Connect again — it only takes a moment.',
    state_reused:       'That sign-in link had already been used. Tap Connect '
                        + 'again to start a fresh one.',
    state_burn_failed:  'EON could not finish the sign-in just now. Tap '
                        + 'Connect again in a moment.',
    state_lookup_failed:'EON could not finish the sign-in just now. Tap '
                        + 'Connect again in a moment.',
    invalid_state:      'That link is not one EON handed out. Start again '
                        + 'from the Connect button here.',
    missing_params:     'Facebook sent us back without the sign-in. Tap '
                        + 'Connect again.',
    tenant_not_found:   'EON could not find your workspace, so nothing was '
                        + 'saved. Tell Daniel if this keeps happening.',
    // Facebook's own answers
    access_denied:      'You left Facebook\'s screen before allowing EON in, '
                        + 'so nothing changed. Tap Connect and press Continue '
                        + 'on Facebook to finish.',
    facebook_error:     'Facebook stopped the sign-in on its side, so nothing '
                        + 'changed. ' + later,
    ads_management_not_granted:
                        'Facebook connected without permission to manage your '
                        + 'ads (that switch was off on Facebook\'s screen), so '
                        + 'nothing was saved. Tap Connect and leave every '
                        + 'switch on: EON needs it to pause and scale ads.',
    no_ad_accounts:     'The Facebook profile you signed in with cannot see '
                        + 'any ad account, so nothing was saved. Log in to '
                        + 'Facebook as someone who runs the shop\'s ad account '
                        + '(or have the business admin add you to it in Meta '
                        + 'Business Settings), then tap Connect.',
    ad_accounts_unreadable:
                        'Facebook let EON in but would not list your ad '
                        + 'accounts just now, so nothing was saved. ' + later,
    scopes_unchecked:   'Facebook let EON in, but EON could not check which '
                        + 'permissions you allowed, so nothing was saved. '
                        + later,
    // the handover from Facebook's yes to a saved connection
    code_exchange_failed:
                        'Facebook said yes, but would not hand the sign-in '
                        + 'over to EON, so nothing was saved. Tap Connect '
                        + 'again. If it fails the same way twice, EON\'s '
                        + 'Facebook app settings need fixing: tell Daniel.',
    no_short_token_in_response: handover,
    long_lived_exchange_failed: handover,
    exchange_failed:            handover,
    network_error:      'EON could not reach Facebook just now, so nothing '
                        + 'was saved. ' + later,
    meta_app_credentials_not_configured:
                        'EON\'s Facebook app has no secret key set on the '
                        + 'server, so no connect can finish yet. Tell Daniel '
                        + 'to set META_APP_SECRET; tapping Connect will not '
                        + 'help until then.',
    store_failed:       'Facebook let EON in, but EON could not save the '
                        + 'connection, so nothing changed. Tap Connect again. '
                        + 'If it fails twice, tell Daniel.',
  };
  // Own keys only: `constructor` or `toString` in a crafted link must not
  // come back as a function.
  if (Object.prototype.hasOwnProperty.call(words, msg)) return words[msg];
  const base = 'Facebook did not finish connecting, so nothing was saved. '
               + 'Tap Connect to try again.';
  // A code this list does not know yet keeps its name, so the cause is
  // never lost. Anything else is someone's own text in a link anyone can
  // craft: never shown, and never put in Facebook's mouth.
  return /^[a-z_]{1,40}$/.test(msg)
    ? `${base} If it happens twice, tell Daniel it said "${msg}".`
    : base;
}

// 2026-05-19 (Daniel): Meta OAuth connect button. One tap → Facebook
// approval → backend persists long-lived token + ad-account binding.
async function refreshMetaConnectStatus() {
  const btn = $('#meta-connect-btn');
  const sub = $('#meta-connect-status');
  if (!btn || !sub) return null;
  let meta = null;   // what the status read said; null when it failed
  try {
    const s = await api('/api/marketing/autopilot/status');
    const m = s.meta || {};
    meta = m;
    if (m.connected) {
      sub.textContent = `Connected · ${m.ad_account || 'no account bound'} · ${(m.scopes || []).join(', ')}`;
      btn.textContent = 'Rotate token';
      btn.disabled = false;
    } else if (m.error === 'no_meta_integration') {
      sub.textContent = 'Not connected. Tap to authorize.';
      btn.disabled = false;
    } else if (m.error === 'missing_ads_management_scope') {
      sub.textContent = 'Connected (read-only). Tap to upgrade to writes.';
      btn.disabled = false;
    } else {
      sub.textContent = m.error || 'Unknown state';
      btn.disabled = false;
    }
  } catch (e) {
    sub.textContent = 'Could not fetch status';
    btn.disabled = false;
  }
  return meta;
}

function bindMetaConnectButton() {
  const btn = $('#meta-connect-btn');
  if (!btn || btn.dataset.bound === '1') return;
  btn.dataset.bound = '1';
  btn.addEventListener('click', async () => {
    btn.disabled = true;
    btn.textContent = 'Checking existing token…';
    // 2026-05-19 (Daniel): try env-bootstrap first — if the existing
    // Meta token in Railway already has ads_management, just import.
    // Surface the real error if bootstrap fails so the operator knows
    // why, and offer paste fallback before going to OAuth.
    let bootErr = null;
    try {
      const boot = await api('/api/integrations/meta/bootstrap-from-env',
        { method: 'POST', body: JSON.stringify({}) });
      if (boot && boot.ok) {
        showToast(`Connected · ${boot.ad_account_id} · ${boot.scopes.join(', ')}`);
        await refreshMetaConnectStatus();
        btn.disabled = false;
        btn.textContent = 'Rotate token';
        return;
      }
      bootErr = (boot && boot.error) || 'unknown';
    } catch (e) {
      // api() throws on non-200 with the FastAPI `detail` in e.message.
      bootErr = e.message || 'request failed';
    }
    console.warn('meta bootstrap failed:', bootErr);

    // Three failure paths:
    //  (a) bootErr starts with "no_token" → env not set; offer paste.
    //  (b) bootErr mentions ad_account / token_expired → offer paste.
    //  (c) anything else → fall to OAuth.
    if (/no_token|ad_account|token_expired|not_pauseable|token_invalid/i.test(bootErr)) {
      const pasted = prompt(
        'Bootstrap failed: ' + bootErr + '\n\n' +
        'Paste a long-lived Meta access token with ads_management scope to bind directly. ' +
        'Leave blank to cancel.');
      if (!pasted) {
        btn.disabled = false;
        btn.textContent = 'Connect';
        return;
      }
      try {
        const r2 = await api('/api/integrations/meta/bootstrap-from-env', {
          method: 'POST',
          body: JSON.stringify({ access_token: pasted.trim() }),
        });
        if (r2 && r2.ok) {
          showToast(`Connected · ${r2.ad_account_id} · ${r2.scopes.join(', ')}`);
          await refreshMetaConnectStatus();
          btn.disabled = false;
          btn.textContent = 'Rotate token';
          return;
        }
        showToast('Paste-bootstrap failed: ' + (r2.error || 'unknown'));
      } catch (e2) {
        showToast('Paste-bootstrap failed: ' + (e2.message || 'unknown'));
      }
      btn.disabled = false;
      btn.textContent = 'Connect';
      return;
    }

    // Fallback: full OAuth dance (needs META_APP_ID/SECRET/REDIRECT_URI
    // on the server; will 500 with a clear error if not set).
    btn.textContent = 'Opening Facebook…';
    try {
      const r = await api('/api/integrations/meta/oauth/start',
        { method: 'POST' });
      if (r && r.ok && r.authorize_url) {
        window.location = r.authorize_url;
      } else {
        showToast('Could not start OAuth');
        btn.disabled = false;
        btn.textContent = 'Connect';
      }
    } catch (e) {
      console.error('meta connect failed', e);
      showToast(`Connect failed: ${e.message || 'unknown'} (bootstrap error: ${bootErr})`);
      btn.disabled = false;
      btn.textContent = 'Connect';
    }
  });
}

// Connect an Instagram account (the bigbag sub-brand by default). Until
// now nothing in EON started this — the address was pasted by hand, and
// the sign-in door is a signed-in POST now, so a pasted link cannot work
// at all. Instagram drops the sign-in after 60 days and nothing renews
// it, so this button is the only way back in.
function bindInstagramConnectButton() {
  const btn = $('#ig-connect-btn');
  if (!btn || btn.dataset.bound === '1') return;
  btn.dataset.bound = '1';
  btn.addEventListener('click', async () => {
    const label = ($('#ig-connect-label')?.value || 'bigbag').trim() || 'bigbag';
    const orig = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Opening Instagram…';
    try {
      const r = await api('/api/instagram/oauth/start', {
        method: 'POST',
        body: JSON.stringify({ label }),
      });
      if (r && r.authorize_url) {
        window.location = r.authorize_url;
        return;                  // leaving the page — keep the button busy
      }
      showToast((r && (r.hint || r.error))
                || 'Could not open the Instagram sign-in page. Try again in a moment.');
    } catch (e) {
      // api() hangs the JSON body on the error; a 503 here carries the
      // server's own plain sentence in `hint`.
      const b = e && e.body;
      showToast((b && (b.hint || b.error))
                || `Could not open the Instagram sign-in page. Try again in a moment. (${e.message || e})`);
    }
    btn.disabled = false;
    btn.textContent = orig;
  });
}

// The rung that built a queued/merged brief, in plain words — one
// vocabulary with the server's model_debt.plain_model. Unknown returns
// null so a card shows nothing rather than guessing a builder.
function _buildModelLabel(metadata) {
  const raw = String((metadata && metadata.built_by_model) || '').toLowerCase();
  if (!raw) return null;
  if (raw.indexOf('fallback') >= 0) return 'Opus (fallback rung)';
  if (raw.indexOf('fable') >= 0) return 'Fable';
  if (raw.indexOf('opus') >= 0) return 'Opus';
  if (raw.indexOf('sonnet') >= 0) return 'Sonnet';
  if (raw.indexOf('haiku') >= 0) return 'Haiku';
  return raw;
}

async function refreshFeatureRequests(status = 'active') {
  const list = $('#fr-list');
  if (!list) return;
  const data = await api(
    `/api/feature-requests?status=${encodeURIComponent(status)}&limit=50`
  ).catch(() => null);
  if (!data?.ok) {
    list.innerHTML = '<div class="row"><div class="row-content">'
      + '<div class="row-label" style="opacity:0.6;">Unable to load.</div>'
      + '</div></div>';
    return;
  }
  if (!data.rows.length) {
    list.innerHTML = '<div class="row"><div class="row-content">'
      + '<div class="row-label" style="opacity:0.6;">Queue is empty. Ask EON to file a request.</div>'
      + '</div></div>';
    return;
  }
  const statusColor = {
    queued:       'var(--ink-soft,#9ca3af)',
    picked_up:    'var(--warn,#f59e0b)',
    in_progress:  'var(--warn,#f59e0b)',
    pr_open:      '#3b82f6',
    merged:       'var(--ok,#10b981)',
    abandoned:    'var(--bad,#ef4444)',
    withdrawn:    'var(--ink-soft,#9ca3af)',
  };
  const priorityColor = {
    critical: 'var(--bad,#ef4444)',
    high:     'var(--warn,#f59e0b)',
    normal:   'var(--ink-soft,#9ca3af)',
    low:      'var(--ink-soft,#9ca3af)',
  };
  list.innerHTML = data.rows.map(r => {
    const created = r.created_at ? new Date(r.created_at).toLocaleString() : '';
    const stat = `<span style="color:${statusColor[r.status] || '#9ca3af'};font-size:11px;font-weight:600;text-transform:uppercase;">${esc(r.status)}</span>`;
    const pri  = `<span style="color:${priorityColor[r.priority] || '#9ca3af'};font-size:11px;font-weight:600;text-transform:uppercase;">${esc(r.priority)}</span>`;
    const pr   = r.pr_url ? ` · <a href="${esc(r.pr_url)}" target="_blank" style="font-size:11px;">PR #${esc(String(r.pr_number || ''))}</a>` : '';
    const kill = r.kill_switch ? ' · <span style="color:var(--bad,#ef4444);font-size:11px;">KILL</span>' : '';
    // Which rung built it (operator directive 2026-07-31). The metadata
    // stamp is written before the agent spawns, so even an abandoned
    // build names the model; older rows fall back to built_by, and an
    // unstamped built row says "model unknown". (Two parallel PRs each
    // declared their own `built` and the merge kept both — a SyntaxError
    // that killed the whole shell, 2026-08-15.)
    const model = _buildModelLabel(r.metadata);
    const built = model
      ? ` · <span style="font-size:11px;opacity:0.75;">built by ${esc(model)}</span>`
      : (r.built_by
          ? ` · <span style="font-size:11px;">built by ${esc(r.built_by)}</span>`
          : (['pr_open', 'merged'].includes(r.status)
              ? ' · <span style="font-size:11px;">model unknown</span>' : ''));
    const active = ['queued','picked_up','in_progress','pr_open'].includes(r.status);
    const story = `<button class="chip" data-story-kind="build" data-story-id="${esc(r.id)}"
              style="margin-right: 4px;">Full story</button>`;
    const actions = story + (active ? `
      <button class="chip fr-kill" data-id="${esc(r.id)}" data-on="${!r.kill_switch}"
              style="margin-right: 4px;">${r.kill_switch ? 'Resume' : 'Kill'}</button>
      ${r.status === 'queued' ? `<button class="chip fr-withdraw" data-id="${esc(r.id)}"
              style="color: var(--bad,#ef4444);">Withdraw</button>` : ''}
    ` : '');
    return `
      <div class="row" data-fr-id="${esc(r.id)}">
        <div class="row-content">
          <div class="row-label">${esc(r.brief.slice(0, 120))}${r.brief.length > 120 ? '…' : ''}</div>
          <div class="row-value" style="font-size: 11px; opacity: 0.6;">
            ${pri} · ${stat}${pr}${built}${kill} · ${esc(created)}
          </div>
          ${r.clarification ? `<div class="row-value" style="font-size: 11px; margin-top: 4px; opacity: 0.55; font-style: italic;">EON: ${esc(r.clarification)}</div>` : ''}
          ${r.error ? `<div class="row-value" style="font-size: 11px; margin-top: 4px; color: var(--bad,#ef4444);">${esc(r.error.slice(0, 200))}</div>` : ''}
        </div>
        <div class="row-action">${actions}</div>
      </div>
    `;
  }).join('');

  list.querySelectorAll('.fr-kill').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.id;
      const on = btn.dataset.on === 'true';
      btn.disabled = true;
      try {
        await api(`/api/feature-requests/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          body: JSON.stringify({ kill_switch: on }),
        });
        refreshFeatureRequests(status);
      } catch (e) {
        showToast?.('Update failed');
      } finally {
        btn.disabled = false;
      }
    });
  });
  list.querySelectorAll('.fr-withdraw').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.id;
      if (!confirm('Withdraw this queued request?')) return;
      btn.disabled = true;
      try {
        await api(`/api/feature-requests/${encodeURIComponent(id)}`, {
          method: 'DELETE',
        });
        refreshFeatureRequests(status);
      } catch (e) {
        showToast?.(e?.body?.detail || 'Withdraw failed');
      } finally {
        btn.disabled = false;
      }
    });
  });
}

function bindFeatureRequestFilters() {
  document.querySelectorAll('#fr-filter-chips [data-fr-status]').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('#fr-filter-chips .chip').forEach(c => c.classList.remove('chip-active'));
      btn.classList.add('chip-active');
      refreshFeatureRequests(btn.dataset.frStatus);
    });
  });
}

async function refreshOversightFindings(status = 'active') {
  const list = $('#oversight-findings-list');
  if (!list) return;
  const data = await api(
    `/api/oversight/findings?status=${encodeURIComponent(status)}&limit=50`
  ).catch(() => null);
  if (!data?.ok) {
    list.innerHTML = '<div class="row"><div class="row-content">'
      + '<div class="row-label" style="opacity:0.6;">Unable to load.</div>'
      + '</div></div>';
    return;
  }
  if (!data.rows.length) {
    list.innerHTML = '<div class="row"><div class="row-content">'
      + '<div class="row-label" style="opacity:0.6;">No findings.</div>'
      + '</div></div>';
    return;
  }
  const sevColor = {
    critical: 'var(--bad,#ef4444)',
    high:     'var(--warn,#f59e0b)',
    medium:   'var(--ink-soft,#9ca3af)',
    low:      'var(--ink-soft,#9ca3af)',
  };
  const statusColor = {
    open:           'var(--bad,#ef4444)',
    investigating:  'var(--warn,#f59e0b)',
    fixed:          'var(--ok,#10b981)',
    false_positive: 'var(--ink-soft,#9ca3af)',
    wont_fix:       'var(--ink-soft,#9ca3af)',
    duplicate:      'var(--ink-soft,#9ca3af)',
  };
  list.innerHTML = data.rows.map(r => {
    const created = r.created_at ? new Date(r.created_at).toLocaleString() : '';
    const sev = `<span style="color:${sevColor[r.severity] || '#9ca3af'};font-size:11px;font-weight:600;text-transform:uppercase;">${esc(r.severity)}</span>`;
    const stat = `<span style="color:${statusColor[r.status] || '#9ca3af'};font-size:11px;font-weight:600;text-transform:uppercase;">${esc(r.status)}</span>`;
    const pr = r.pr_url ? `<a href="${esc(r.pr_url)}" target="_blank" style="font-size:11px;">PR</a>` : '';
    return `
      <div class="row" data-finding-id="${esc(r.id)}">
        <div class="row-content">
          <div class="row-label">${esc(r.title)}</div>
          <div class="row-value" style="font-size:11px;opacity:0.6;">
            ${sev} · ${stat} · ${esc(r.detected_by)} · ${esc(created)} ${pr}
          </div>
          ${r.body ? `<div class="row-value" style="font-size:12px;margin-top:4px;opacity:0.8;">${esc(r.body.slice(0, 240))}${r.body.length > 240 ? '…' : ''}</div>` : ''}
          ${r.resolution_note ? `<div class="row-value" style="font-size:11px;margin-top:4px;opacity:0.65;font-style:italic;">→ ${esc(r.resolution_note)}</div>` : ''}
        </div>
      </div>
    `;
  }).join('');
}

function bindOversightFilters() {
  document.querySelectorAll('#oversight-filter-chips [data-oversight-status]').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('#oversight-filter-chips .chip').forEach(c => c.classList.remove('chip-active'));
      btn.classList.add('chip-active');
      refreshOversightFindings(btn.dataset.oversightStatus);
    });
  });
}

async function refreshSwitchboard() {
  const list = $('#switchboard-list');
  if (!list) return;
  const data = await api('/api/switchboard').catch(() => null);
  if (!data?.ok) {
    list.innerHTML = '<div class="row"><div class="row-content">'
      + '<div class="row-label" style="opacity:0.6;">Unable to load (owner only).</div>'
      + '</div></div>';
    return;
  }
  const sum = $('#switchboard-summary');
  if (sum) {
    const flippable = data.live_rows
      ? ` · <b>${data.live_rows}</b> changeable without a restart`
      : '';
    sum.innerHTML = `<b>${data.gates_on}</b> live · <b>${data.gates_off}</b> dark of ${data.count} switches${flippable}`;
  }
  const areas = {};
  (data.rows || []).forEach(r => { (areas[r.area] = areas[r.area] || []).push(r); });
  list.innerHTML = Object.keys(areas).sort().map(area => {
    const rows = areas[area].map(r => {
      let pill;
      // A LIVE row's pill shows what the GATE will actually read
      // (effective_on), not the environment-only answer — otherwise a
      // lane armed from the switchboard renders as a grey OFF while it
      // is sending. The env answer is not lost: it is shown below,
      // beside the switchboard's, so a disagreement is visible rather
      // than blended into one number nobody can attribute.
      const effective = (r.live && r.override !== null && r.override !== undefined)
        ? r.effective_on : r.on;
      if (r.type === 'dial') {
        pill = `<span style="font-size:11px; padding:2px 8px; border-radius:10px; border:1px solid var(--line); opacity:0.85;">${esc(String(r.value ?? r.default))}</span>`;
      } else if (effective) {
        pill = '<span style="font-size:11px; padding:2px 8px; border-radius:10px; background:rgba(16,185,129,0.15); color:var(--ok,#10b981); font-weight:600;">ON</span>';
      } else {
        pill = '<span style="font-size:11px; padding:2px 8px; border-radius:10px; border:1px solid var(--line); opacity:0.55;">OFF</span>';
      }
      const overridden = r.live && r.override !== null && r.override !== undefined;
      const src = overridden
        ? '<span style="font-size:10px; color:var(--accent,#d4ff3a); opacity:0.9;">from the switchboard</span>'
        : (r.live
            ? '<span style="font-size:10px; opacity:0.5;">live · from Railway</span>'
            : (r.set_in_env
                ? '<span style="font-size:10px; opacity:0.55;">set in env</span>'
                : '<span style="font-size:10px; opacity:0.4;">default</span>'));
      return `
        <details class="row" style="display:block; cursor:pointer;">
          <summary style="display:flex; align-items:center; gap:10px; list-style:none;">
            ${pill}
            <span style="flex:1; font-size:13px;">${esc(r.title)}</span>
            ${src}
          </summary>
          <div style="padding:8px 4px 4px; font-size:12px; opacity:0.8;">
            <div>${esc(r.what)}</div>
            <div style="margin-top:6px; opacity:0.85;"><b>When on:</b> ${esc(r.when_on)}</div>
            <div style="margin-top:6px; font-family:monospace; font-size:11px; opacity:0.7;">${esc(r.flag)} = ${esc(r.flip)} <span style="opacity:0.6;">${r.live ? '(switchboard → about a minute, no restart)' : '(Railway env → redeploy)'}</span></div>
            ${overridden ? `<div style="margin-top:6px; font-size:11px; opacity:0.85;">Railway says <b>${esc(String(r.env_value ?? r.default))}</b>, the switchboard says <b>${esc(String(r.override))}</b>. The switchboard wins while it can be reached; if it cannot, Railway's answer stands.</div>` : ''}
          </div>
        </details>`;
    }).join('');
    return `<div style="font-size:11px; text-transform:uppercase; letter-spacing:0.5px; color:var(--ink-soft); margin:12px 4px 6px;">${esc(area)}</div>${rows}`;
  }).join('');
}

async function refreshMcpServers() {
  const slug = State.tenant?.slug;
  const list = $('#mcp-servers-list');
  if (!slug || !list) return;
  const data = await api(`/api/tenants/${encodeURIComponent(slug)}/mcp-servers`)
    .catch(() => null);
  if (!data?.ok) {
    list.innerHTML = '<div class="row"><div class="row-content">'
      + '<div class="row-label" style="opacity:0.6;">Unable to load.</div>'
      + '</div></div>';
    return;
  }
  if (!data.servers.length) {
    list.innerHTML = '<div class="row"><div class="row-content">'
      + '<div class="row-label" style="opacity:0.6;">None configured.</div>'
      + '</div></div>';
    return;
  }
  list.innerHTML = data.servers.map(s => {
    const status = s.is_active
      ? '<span style="color: var(--ok,#10b981); font-size: 11px;">active</span>'
      : '<span style="opacity: 0.5; font-size: 11px;">disabled</span>';
    const tok = s.has_token
      ? '<span style="font-size: 11px; opacity: 0.7;">· token set</span>'
      : '';
    return `
      <div class="row" data-mcp-id="${esc(s.id)}">
        <div class="row-content">
          <div class="row-label">${esc(s.name)} ${status} ${tok}</div>
          <div class="row-value" style="font-size: 12px; opacity: 0.7; word-break: break-all;">
            ${esc(s.url)}
          </div>
          ${s.note ? `<div class="row-value" style="font-size: 11px; opacity: 0.55;">${esc(s.note)}</div>` : ''}
        </div>
        <div class="row-action">
          <button class="chip mcp-toggle" data-id="${esc(s.id)}" data-active="${s.is_active}"
                  style="margin-right: 4px;">${s.is_active ? 'Disable' : 'Enable'}</button>
          <button class="btn-danger mcp-delete" data-id="${esc(s.id)}"
                  style="font-size:12px;padding:4px 10px;">Delete</button>
        </div>
      </div>
    `;
  }).join('');

  list.querySelectorAll('.mcp-toggle').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.id;
      const next = btn.dataset.active === 'false';  // toggling current → flip
      btn.disabled = true;
      try {
        await api(
          `/api/tenants/${encodeURIComponent(slug)}/mcp-servers/${encodeURIComponent(id)}`,
          { method: 'PATCH', body: JSON.stringify({ is_active: next }) },
        );
        refreshMcpServers();
      } catch (e) {
        showToast?.('Update failed');
      } finally {
        btn.disabled = false;
      }
    });
  });
  list.querySelectorAll('.mcp-delete').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.id;
      if (!confirm('Delete this MCP server?')) return;
      btn.disabled = true;
      try {
        await api(
          `/api/tenants/${encodeURIComponent(slug)}/mcp-servers/${encodeURIComponent(id)}`,
          { method: 'DELETE' },
        );
        refreshMcpServers();
      } catch (e) {
        showToast?.('Delete failed');
      } finally {
        btn.disabled = false;
      }
    });
  });
}

function bindMcpServersForm() {
  const addBtn   = $('#mcp-add-btn');
  const form     = $('#mcp-add-form');
  const cancel   = $('#mcp-add-cancel');
  const save     = $('#mcp-add-save');
  const nameEl   = $('#mcp-add-name');
  const urlEl    = $('#mcp-add-url');
  const tokenEl  = $('#mcp-add-token');
  const errEl    = $('#mcp-add-error');
  if (!addBtn || !form) return;
  addBtn.addEventListener('click', () => {
    form.style.display = 'block';
    addBtn.style.display = 'none';
    nameEl.focus();
  });
  cancel?.addEventListener('click', () => {
    form.style.display = 'none';
    addBtn.style.display = '';
    nameEl.value = urlEl.value = tokenEl.value = '';
    errEl.textContent = '';
  });
  save?.addEventListener('click', async () => {
    errEl.textContent = '';
    const name = (nameEl.value || '').trim().toLowerCase();
    const url  = (urlEl.value  || '').trim();
    const token= (tokenEl.value|| '').trim();
    if (!name || !url) {
      errEl.textContent = 'name and url are required';
      return;
    }
    const slug = State.tenant?.slug;
    save.disabled = true;
    save.textContent = 'Saving…';
    try {
      await api(`/api/tenants/${encodeURIComponent(slug)}/mcp-servers`, {
        method: 'POST',
        body: JSON.stringify({ name, url, token: token || undefined }),
      });
      cancel.click();
      refreshMcpServers();
    } catch (e) {
      errEl.textContent = e?.body?.detail || e?.message || 'Save failed';
    } finally {
      save.disabled = false;
      save.textContent = 'Save';
    }
  });
}

async function refreshVoicePicker() {
  const slug = State.tenant?.slug;
  if (!slug) return;
  const [pref, list] = await Promise.all([
    api('/api/voice/preference').catch(() => null),
    api('/api/voice/voices').catch(() => null),
  ]);
  const curName = $('#voice-current-name');
  const sel     = $('#voice-model-select');
  const listEl  = $('#voice-list');
  const curId   = pref?.voice_id;
  const curMod  = pref?.voice_model_id || '';
  if (sel) sel.value = curMod;
  if (!list?.ok) {
    if (listEl) listEl.innerHTML = '<div class="row"><div class="row-content">'
      + '<div class="row-label" style="opacity:0.6;">Unable to load voices.</div>'
      + '</div></div>';
    if (curName) curName.textContent = curId || '—';
    return;
  }
  const current = list.voices.find(v => v.voice_id === curId);
  if (curName) curName.textContent = current ? current.name : (curId || '—');

  // #voice-list gets the same guard its two siblings already have.
  // curName and sel are both `if (x) x.…`, and listEl itself is guarded
  // on the !list.ok branch nine lines up — only the success path wrote
  // to it bare, and that is the path that threw on /?v=settings: the two
  // awaited reads land after the settings markup is gone (this function
  // also re-enters itself from the Select handler, so the window is real).
  if (listEl) listEl.innerHTML = list.voices.map(v => {
    const labels = Object.entries(v.labels || {})
      .filter(([k,_]) => ['accent','age','gender','use_case','descriptive'].includes(k))
      .map(([k,val]) => `<span style="font-size:10px;opacity:0.55;margin-right:4px;">${esc(val)}</span>`)
      .join('');
    const active = v.voice_id === curId;
    return `
      <div class="row" style="${active ? 'background: var(--accent-bg, rgba(16,185,129,0.08));' : ''}">
        <div class="row-content">
          <div class="row-label">${esc(v.name)} ${active ? '<span style="color:var(--ok,#10b981);font-size:11px;">· active</span>' : ''}</div>
          <div class="row-value" style="font-size:11px;opacity:0.7;">${esc(v.category)} ${labels}</div>
        </div>
        <div class="row-action">
          ${v.preview_url ? `<button class="chip voice-preview" data-url="${esc(v.preview_url)}" style="margin-right:4px;">▶ Preview</button>` : ''}
          ${active ? '' : `<button class="chip voice-pick" data-id="${esc(v.voice_id)}">Select</button>`}
        </div>
      </div>
    `;
  }).join('');

  if (listEl) listEl.querySelectorAll('.voice-preview').forEach(btn => {
    btn.addEventListener('click', () => {
      const url = btn.dataset.url;
      try {
        if (window._voicePreviewAudio) window._voicePreviewAudio.pause();
        const a = new Audio(url);
        window._voicePreviewAudio = a;
        a.play();
      } catch (e) {
        showToast?.('Preview failed');
      }
    });
  });
  if (listEl) listEl.querySelectorAll('.voice-pick').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.id;
      btn.disabled = true;
      try {
        await api('/api/voice/preference', {
          method: 'PUT',
          body: JSON.stringify({ voice_id: id }),
        });
        showToast?.('Voice updated');
        refreshVoicePicker();
      } catch (e) {
        showToast?.('Update failed');
      } finally {
        btn.disabled = false;
      }
    });
  });

  if (sel) {
    sel.onchange = async () => {
      const v = sel.value;
      sel.disabled = true;
      try {
        await api('/api/voice/preference', {
          method: 'PUT',
          body: JSON.stringify({ voice_model_id: v }),
        });
        showToast?.('Model updated');
      } catch (e) {
        showToast?.('Update failed');
      } finally {
        sel.disabled = false;
      }
    };
  }
}

async function refreshIngestFlags() {
  const slug = State.tenant?.slug;
  const listEl = $('#ingest-flags-list');
  if (!slug || !listEl) return;
  const data = await api(`/api/tenants/${encodeURIComponent(slug)}/ingest-flags`).catch(() => null);
  if (!data?.ok) {
    listEl.innerHTML = '<div class="row"><div class="row-content">'
      + '<div class="row-label" style="opacity:0.6;">Unable to load flags.</div>'
      + '</div></div>';
    return;
  }
  const platforms = Object.entries(data.flags);
  listEl.innerHTML = platforms.map(([platform, enabled]) => {
    const dot = enabled
      ? '<span style="color: var(--ok,#10b981);">●</span>'
      : '<span style="opacity: 0.4;">○</span>';
    return `
      <div class="row">
        <div class="row-content">
          <div class="row-label">${dot} ${esc(platform)}</div>
          <div class="row-value" style="font-size:11px;opacity:0.6;">
            ${enabled ? 'pulling on schedule' : 'skipped every tick — flip on once credentials are verified'}
          </div>
        </div>
        <div class="row-action">
          <button class="chip ingest-toggle" data-platform="${esc(platform)}" data-on="${!enabled}">
            ${enabled ? 'Disable' : 'Enable'}
          </button>
        </div>
      </div>
    `;
  }).join('');
  listEl.querySelectorAll('.ingest-toggle').forEach(btn => {
    btn.addEventListener('click', async () => {
      const platform = btn.dataset.platform;
      const on = btn.dataset.on === 'true';
      btn.disabled = true;
      try {
        await api(`/api/tenants/${encodeURIComponent(slug)}/ingest-flags`, {
          method: 'PUT',
          body: JSON.stringify({ platform, enabled: on }),
        });
        showToast?.(`${platform}: ${on ? 'enabled' : 'disabled'}`);
        refreshIngestFlags();
      } catch (e) {
        showToast?.(e?.body?.detail || 'Update failed');
      } finally {
        btn.disabled = false;
      }
    });
  });
}

async function refreshAgentModel() {
  const slug = State.tenant?.slug;
  if (!slug) return;
  const cur = $('#agent-model-current');
  const opts = $('#agent-model-options');
  if (!opts) return;
  const data = await api(`/api/tenants/${encodeURIComponent(slug)}/preferred-model`).catch(() => null);
  if (!data?.ok) {
    if (cur) cur.textContent = '—';
    opts.innerHTML = '<div class="row-value" style="font-size:12px;opacity:0.6;">Unable to load.</div>';
    return;
  }
  const current = data.current;
  const currentOpt = (data.options || []).find(o => o.id === current);
  if (cur) cur.textContent = currentOpt?.label || current;
  opts.innerHTML = (data.options || []).map(o => {
    const active = o.id === current;
    return `
      <button class="chip ${active ? 'chip-active' : ''}"
              data-model-id="${esc(o.id)}"
              style="margin-right: 6px; margin-bottom: 6px;">
        ${esc(o.label)} <span style="opacity:0.6; font-size: 11px;">· ${esc(o.tier)}</span>
      </button>
    `;
  }).join('');
  opts.querySelectorAll('button[data-model-id]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const model = btn.dataset.modelId;
      if (model === current) return;
      btn.disabled = true;
      try {
        const resp = await api(
          `/api/tenants/${encodeURIComponent(slug)}/preferred-model`,
          { method: 'PUT', body: JSON.stringify({ model }) }
        );
        if (resp?.ok) {
          showToast?.(`Agent model set to ${model}`);
          refreshAgentModel();
        }
      } catch (e) {
        showToast?.('Failed to update model');
      } finally {
        btn.disabled = false;
      }
    });
  });
}

// 2026-06-10: ads-brain model toggle — mirrors refreshAgentModel but
// drives the marketing brain only (analyst / media buyer / creative
// director / copywriter / ads coach) via /ads-model. Fable 5 default;
// runtime falls back to Opus 4.8 on Anthropic errors (model_router).
async function refreshAdsModel() {
  const slug = State.tenant?.slug;
  if (!slug) return;
  const cur = $('#ads-model-current');
  const opts = $('#ads-model-options');
  if (!opts) return;
  const data = await api(`/api/tenants/${encodeURIComponent(slug)}/ads-model`).catch(() => null);
  if (!data?.ok) {
    if (cur) cur.textContent = '—';
    opts.innerHTML = '<div class="row-value" style="font-size:12px;opacity:0.6;">Unable to load.</div>';
    return;
  }
  const current = data.current;
  const currentOpt = (data.options || []).find(o => o.id === current);
  if (cur) cur.textContent = currentOpt?.label || current;
  opts.innerHTML = (data.options || []).map(o => {
    const active = o.id === current;
    return `
      <button class="chip ${active ? 'chip-active' : ''}"
              data-ads-model-id="${esc(o.id)}"
              style="margin-right: 6px; margin-bottom: 6px;">
        ${esc(o.label)} <span style="opacity:0.6; font-size: 11px;">· ${esc(o.tier)}</span>
      </button>
    `;
  }).join('');
  opts.querySelectorAll('button[data-ads-model-id]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const model = btn.dataset.adsModelId;
      if (model === current) return;
      btn.disabled = true;
      try {
        const resp = await api(
          `/api/tenants/${encodeURIComponent(slug)}/ads-model`,
          { method: 'PUT', body: JSON.stringify({ model }) }
        );
        if (resp?.ok) {
          showToast?.(`Ads brain set to ${model}`);
          refreshAdsModel();
        }
      } catch (e) {
        showToast?.('Failed to update ads model');
      } finally {
        btn.disabled = false;
      }
    });
  });
}

// ──────────────────────────────────────────────────────────
// Money-page redesign 2026-06-07 — grouped bank accounts
//
// Classify each account into one of five buckets purely from the
// (name, kind, currency) the cash-position endpoint already returns.
// Verified against the live CRUX roster:
//   bank, no store keyword            → operating
//   bank, store keyword (Monipoint…)  → store (walk-in cash)
//   cash                              → drawer
//   wallet                            → ad wallet (ad credit, NOT cash)
//   liability / "loan …"              → liability (NOT cash)
// ──────────────────────────────────────────────────────────

const _STORE_KEYWORDS = ['palms', 'chevron', 'osapa', 'abuja'];

function _classifyAccount(a) {
  const name = (a.name || '').toLowerCase();
  const kind = (a.kind || '').toLowerCase();
  if (kind === 'liability' || name.startsWith('loan ') || name.includes('loan from')) {
    return 'liability';
  }
  if (kind === 'wallet' || name.includes('prepaid') || name.includes(' ads ')) {
    return 'wallet';
  }
  if (kind === 'cash' || name.startsWith('cash drawer')) {
    return 'drawer';
  }
  // bank: store vs operating
  const isStore = _STORE_KEYWORDS.some(k => name.includes(k)) &&
    (name.includes('monipoint') || name.includes('moniepoint'));
  return isStore ? 'store' : 'operating';
}

// A row carries a balance only when the server sent a real number for it.
function _cashBalanceKnown(a) {
  return typeof a.balance_ngn === 'number' && Number.isFinite(a.balance_ngn);
}

// The one test a row and its group total both use, so the two can never
// disagree about what was counted.
function _cashRowCounted(a) {
  // ONE test for a row, its group total and the gaps list (money_workspace.js
  // recordGapRows): a row is uncounted only when the server MARKS it so —
  // needs_snapshot, cash_status 'unavailable', a formula_error — or when it
  // carries no number. A missing snapshot_at alone is not a verdict: a row the
  // formula merge never touched still carries a real transaction-derived
  // balance, and dropping it printed "₦0 · some not counted" with nothing
  // named (first vet of 9eddc4259).
  return _cashBalanceKnown(a) && a.needs_snapshot !== true
    && a.cash_status !== 'unavailable'
    && !(typeof a.formula_error === 'string' && a.formula_error.trim() !== '');
}

// Render one account row (shared markup across all groups). `opts`:
//   { adWallet: bool }  — wallet rows label balance as ad credit.
function _renderAccountRow(a, fmt, opts = {}) {
  // Liability accounts (loans) are sourced from business_liabilities via the
  // backend overlay — they show real outstanding + status, NOT a cash
  // snapshot. Render a loan-appropriate row (no "needs snapshot", no cash
  // Update button which would write the wrong table).
  // One test for "is there a balance here" — the same one the group total and
  // the gaps list use (_cashRowCounted), so a row, its total and that list can
  // never disagree about what was counted. A number the server MARKED as not
  // counted is not a balance.
  const known = _cashRowCounted(a);
  const reason = (a.reasons || []).filter(x => typeof x === 'string').join(' ')
    || (a.problems || []).map(x => typeof x === 'string' ? x : x.message || '').join(' ')
    || a.formula_error || 'This balance cannot be confirmed yet.';
  const range = typeof a.range_low_ngn === 'number' && Number.isFinite(a.range_low_ngn)
    && typeof a.range_high_ngn === 'number' && Number.isFinite(a.range_high_ngn)
    && a.range_low_ngn <= a.range_high_ngn
      ? `${fmt(a.range_low_ngn)} to ${fmt(a.range_high_ngn)} · possible range` : '';
  if (a.is_liability && !known) {
    return `<div class="cash-acct-row cash-acct-row-liab"><span class="cash-acct-name">${esc(_formatAccountDisplay(a.name))}</span><span class="cash-acct-bal">Unknown</span><span class="cash-acct-delta">${esc(reason)}</span></div>`;
  }
  if (a.is_liability) {
    const out = a.balance_ngn;
    const status = (a.liability_status || '').toLowerCase();
    const paid = status === 'paid_off' || status === 'paid' || out <= 0;
    const pillCls = paid ? 'snap-pill-ok' : 'snap-pill-warn';
    const pillLabel = paid ? 'paid off' : 'active';
    let hint = '<span class="tone-mute">no payment scheduled</span>';
    if (paid) hint = '<span class="tone-mute">settled</span>';
    else if (a.next_payment_due_at) {
      const amt = Number(a.next_payment_amount_ngn) || 0;
      const due = String(a.next_payment_due_at).slice(0, 10);
      hint = `<span class="tone-mute">next ${amt ? fmt(amt).replace('NGN ', '₦') + ' · ' : ''}${esc(due)}</span>`;
    }
    const displayName = _formatAccountDisplay(a.name);
    const balCls = paid ? 'tone-mute' : 'tone-danger';
    return `
      <div class="cash-acct-row cash-acct-row-liab">
        <div class="cash-acct-left">
          <span class="cash-acct-name">${esc(displayName)}</span>
          <span class="cash-acct-meta">
            <span class="snap-pill ${pillCls}">${pillLabel}</span>
            <span class="cash-acct-delta">${hint}</span>
          </span>
        </div>
        <div class="cash-acct-right">
          <span class="cash-acct-bal mono-num ${balCls}">${paid ? '₦0' : '−' + fmt(out).replace('NGN ', '₦')}</span>
        </div>
      </div>
    `;
  }
  // A liability-kind account the business_liabilities overlay could NOT
  // match must not fall through to the cash renderer — that resurrects the
  // phantom "NEVER / needs snapshot" state with an Add button that writes
  // the WRONG table (a cash snapshot for a loan).
  if (_classifyAccount(a) === 'liability') {
    return `
      <div class="cash-acct-row cash-acct-row-liab">
        <div class="cash-acct-left">
          <span class="cash-acct-name">${esc(_formatAccountDisplay(a.name))}</span>
          <span class="cash-acct-meta">
            <span class="snap-pill snap-pill-cold">not linked</span>
            <span class="cash-acct-delta"><span class="tone-mute">register this loan in chat — "loan from X, ₦Y outstanding"</span></span>
          </span>
        </div>
        <div class="cash-acct-right">
          <span class="cash-acct-bal mono-num tone-mute">—</span>
        </div>
      </div>
    `;
  }
  // A balance EON could not count is not ₦0. Main writes 0.0 beside its
  // "needs a record" mark (finance.py:251), and a partly-read payload can send
  // the row with no number at all — both must read as no balance, never as a
  // balance of nothing.
  const needsSnap = !_cashRowCounted(a);
  // Counted is not the same as snapshotted: a row the formula merge never
  // touched carries a real balance and no dated snapshot. Its balance counts;
  // its MOVEMENT cannot be measured (there is nothing to measure it against),
  // and its key still reads Add (first vet of cccb92c28).
  const hasSnap = a.snapshot_at != null && Number.isFinite(Number(a.snapshot_balance));
  // An uncounted row has NO balance — not a zero. `|| 0` here is the very bug
  // this PR exists to end (#3443): it prints ₦0 for money nobody counted.
  const bal = known ? a.balance_ngn : null;
  const snap = Number(a.snapshot_balance) || 0;
  const stmtCr = Number(a.statement_credits) || 0;
  const stmtDr = Number(a.statement_debits) || 0;
  const shopIn = Number(a.shopify_inflows) || 0;
  const adsOut = Number(a.ad_spend_deducted) || 0;
  const delta = known ? bal - snap : null;
  const deltaCls = delta > 0 ? 'tone-ok' : delta < 0 ? 'tone-danger' : 'tone-mute';
  const deltaSign = delta > 0 ? '↑ ' : delta < 0 ? '↓ ' : '';
  const age = _snapshotAgeDays(a.snapshot_at);
  const ageCls = age === null ? 'snap-pill-cold'
    : age <= 3 ? 'snap-pill-ok'
    : age <= 7 ? 'snap-pill-warn'
    : 'snap-pill-danger';
  const ageLabel = age === null ? 'never'
    : age === 0 ? 'today'
    : age === 1 ? '1d'
    : `${age}d`;
  const tip = [
    `Snapshot: ${fmt(snap)}`,
    stmtCr || stmtDr ? `Statement: +${fmt(stmtCr)} / −${fmt(stmtDr)}` : '',
    shopIn ? `Shopify: +${fmt(shopIn)}` : '',
    adsOut ? `Ad spend: −${fmt(adsOut)}` : '',
    known ? `= ${fmt(bal)}` : reason,
  ].filter(Boolean).join(' · ');

  // Snap is a USD balance shown in NGN — flag the original currency.
  const isFx = a.currency && a.currency !== 'NGN';
  const balDisplay = !known
    ? `<span class="tone-mute cash-acct-needs-snap">${hasSnap ? 'Unknown' : '— needs snapshot —'}</span>`
    : fmt(bal);
  const stateNote = !known ? `${reason}${range ? ' ' + range : ''}`
    : a.cash_status === 'estimated' ? 'Estimate · ' + reason : '';
  const lastKnown = !known && typeof a.last_known_balance_ngn === 'number'
    && Number.isFinite(a.last_known_balance_ngn) && a.last_known_at
    ? ` Last saved: ${fmt(a.last_known_balance_ngn)} on ${String(a.last_known_at).slice(0, 10)}.` : '';
  const deltaDisplay = (needsSnap || !hasSnap)
    ? `<span class="tone-mute">—</span>`
    : (delta === 0
        ? '<span class="tone-mute">no change</span>'
        : `<span class="${deltaCls}">${deltaSign}${fmt(Math.abs(delta)).replace('NGN ', '')}</span>`);
  const btnLabel = hasSnap ? 'Update' : 'Add';
  const btnCls = hasSnap ? 'snap-update-btn' : 'snap-update-btn snap-update-btn-primary';
  const displayName = _formatAccountDisplay(a.name);
  return `
    <div class="cash-acct-row ${needsSnap ? 'cash-acct-row-cold' : ''}" title="${esc(tip)}">
      <div class="cash-acct-left">
        <span class="cash-acct-name">
          ${esc(displayName)}${isFx ? ` <span class="cash-acct-cur" title="Balance held in ${esc(a.currency)}, shown in NGN">${esc(a.currency)}</span>` : ''}
        </span>
        <span class="cash-acct-meta">
          <span class="snap-pill ${ageCls}">${ageLabel}</span>
          <span class="cash-acct-delta mono-num">${deltaDisplay}</span>
          ${stateNote ? `<span class="cash-acct-delta">${esc(stateNote + lastKnown)}</span>` : ''}
          ${Number(a.transfer_in_waiting_ngn)>0?`<span class="cash-acct-delta">${fmt(a.transfer_in_waiting_ngn)} transferred in is not counted yet. The source bank statement has not reached that transfer date.</span>`:''}
        </span>
      </div>
      <div class="cash-acct-right">
        <span class="cash-acct-bal mono-num ${opts.adWallet ? 'tone-mute' : ''}">${balDisplay}</span>
        <button class="${btnCls}" data-snap-acct="${esc(a.account_id || '')}"
                data-snap-name="${esc(a.name)}"
                data-snap-display="${esc(displayName)}"
                data-snap-cur-bal="${known ? bal : ''}">
          ${btnLabel}
        </button>
      </div>
    </div>
  `;
}

function _renderGroupedAccounts(accts, fmt) {
  const buckets = { operating: [], store: [], drawer: [], wallet: [], liability: [] };
  accts.forEach(a => { buckets[_classifyAccount(a)].push(a); });

  // A group total counts only the rows the row itself counts (one predicate,
  // _cashRowCounted); an uncounted balance never adds ₦0 — or a stale figure —
  // to a total that then says "some not counted" (first vet of 5eed67c).
  const isKnown = a => a.is_liability || _cashRowCounted(a);
  const subtotal = (rows) =>
    rows.reduce((s, a) => s + ((a.is_liability || _cashRowCounted(a)) ? (Number(a.balance_ngn) || 0) : 0), 0);

  // Group spec: title, note, the accounts, whether it counts as cash,
  // and a per-row option flag. Order matters — cash-first.
  const groups = [
    {
      key: 'operating', title: 'Operating banks',
      note: 'Dated balances and estimates', rows: buckets.operating, countsAsCash: true,
    },
    {
      key: 'store', title: 'Store accounts',
      note: 'Moniepoint · walk-in revenue', rows: buckets.store, countsAsCash: true,
    },
    {
      key: 'drawer', title: 'Cash drawers',
      note: 'Physical cash at each store', rows: buckets.drawer, countsAsCash: true,
    },
    {
      key: 'wallet', title: 'Ad wallets',
      note: 'Prepaid ad credit — NOT operating cash', rows: buckets.wallet,
      countsAsCash: false, rowOpts: { adWallet: true },
    },
    {
      key: 'liability', title: 'Loans',
      note: 'Liabilities — excluded from cash', rows: buckets.liability,
      countsAsCash: false, liability: true,
    },
  ];

  const sections = groups.filter(g => g.rows.length).map((g, gi) => {
    const sub = subtotal(g.rows);
    const knownCount = g.rows.filter(isKnown).length;
    // A total taken over a row whose balance is unknown is not a total. Say so
    // once in the heading (why) and once beside the number (what was left out).
    const partly = knownCount !== g.rows.length;
    const note = partly ? 'Some balances are unknown · subtotal only'
      : g.rows.some(a => a.cash_status === 'estimated') ? 'Estimated balances · not confirmed cash' : g.note;
    const subCls = g.liability ? 'tone-danger' : (g.countsAsCash ? '' : 'tone-mute');
    const rowsHtml = g.rows
      .map(a => _renderAccountRow(a, fmt, g.rowOpts || {}))
      .join('');
    // Drawers that all need a snapshot get a clearer empty-state subtotal.
    const allCold = g.key === 'drawer' &&
      g.rows.every(a => !_cashRowCounted(a));   // the same test the rows and the total use
    const subDisplay = allCold
      ? '<span class="tone-mute">needs snapshot</span>'
      : knownCount === 0 ? '<span class="tone-mute">Unknown</span>'
      : `<span class="${subCls}">${g.liability ? '−' + fmt(Math.abs(sub)).replace('NGN ', '₦') : fmt(sub).replace('NGN ', '₦')}</span>`
        + (partly ? '<span class="tone-mute"> · some not counted</span>' : '');
    return `
      <div class="cash-group stagger-rise" style="--i:${gi}">
        <div class="cash-group-head">
          <div class="cash-group-titles">
            <span class="cash-group-title">${esc(g.title)}</span>
            <span class="cash-group-note">${esc(note)}</span>
          </div>
          <span class="cash-group-sub mono-num">${subDisplay}</span>
        </div>
        <div class="cash-group-rows">${rowsHtml}</div>
      </div>`;
  }).join('');

  return sections;
}

// PR MONEY-UI-MOBILE-POLISH: reformat account name to lead with the
// store location so phone-truncated rows ("Monipoin...") are still
// distinguishable. "Moniepoint Palms operating" → "PALMS · Moniepoint
// operating"; "Cash drawer Chevron" → "CHEVRON · Cash drawer".
// Accounts with no store location pass through unchanged.
const _CRUX_LOCATIONS = ['palms', 'chevron', 'osapa', 'abuja'];
function _formatAccountDisplay(name) {
  if (!name) return '';
  const lower = name.toLowerCase();
  for (const loc of _CRUX_LOCATIONS) {
    const idx = lower.indexOf(loc);
    if (idx === -1) continue;
    // Strip the location token from the name, collapse whitespace,
    // then prepend it (uppercased) with a Bloomberg-style separator.
    const rest = (name.slice(0, idx) + name.slice(idx + loc.length))
                   .replace(/\s+/g, ' ').trim();
    return rest
      ? `${loc.toUpperCase()} · ${rest}`
      : loc.toUpperCase();
  }
  return name;
}

// PR MONEY-CASH-RESTORE: snapshot age helper. Converts an ISO
// timestamp into days-since (round-half-up). null/undefined → null
// (used by the UI to render "never").
//
// PR MONEY-UI-MOBILE-POLISH: two fixes here:
//   1. Postgres serializes TIMESTAMPTZ as "YYYY-MM-DD HH:MM:SS+TZ"
//      with a SPACE separator. Safari (and a few other parsers)
//      reject the space form and return NaN. Normalize to T form
//      so all browsers parse the same instant.
//   2. Floor gave off-by-one when the snapshot was set 4d+12h ago
//      (operator saw "4D" when it had been 5 days in conversation).
//      Use Math.round so the pill snaps to the calendar-day reading
//      most operators expect.
function _snapshotAgeDays(iso) {
  if (!iso) return null;
  const normalized = String(iso).replace(' ', 'T');
  const t = new Date(normalized).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 86400000));
}

// PR MONEY-CASH-RESTORE: "Update balance" modal. Captures the
// operator's authoritative point-in-time balance per account and POSTs
// it to /api/finance/cash-snapshots. The cash_formula module reads
// the resulting row as the anchor for current-balance computation.
//
// Bloomberg-style: dense, mono input, escape-to-close, enter-to-submit.
function _openSnapshotModal({accountId, accountName, displayName, currentBalance}) {
  if (!accountId) return;
  // Remove any existing overlay first.
  document.querySelectorAll('.snap-modal-overlay').forEach(el => el.remove());

  const overlay = document.createElement('div');
  overlay.className = 'snap-modal-overlay';
  overlay.innerHTML = `
    <div class="snap-modal">
      <div class="snap-modal-head">
        <span class="snap-modal-title">Update balance</span>
        <button class="snap-modal-close" type="button" aria-label="Close">×</button>
      </div>
      <div class="snap-modal-acct">${esc(displayName || accountName || '')}</div>
      <div class="snap-modal-prev">Current shown: <span class="mono-num">${typeof currentBalance === 'number' && Number.isFinite(currentBalance) ? 'NGN ' + currentBalance.toLocaleString(undefined, {maximumFractionDigits: 0}) : 'Unknown — enter a balance from your account'}</span></div>
      <label class="snap-modal-label">New balance (NGN)</label>
      <input type="number" inputmode="decimal" step="0.01" class="snap-modal-input mono-num"
             placeholder="0.00" />
      <div class="snap-modal-hint">
        Authoritative point-in-time balance.
        Recent Shopify orders + bank credits since this moment are layered on top automatically.
      </div>
      <div class="snap-modal-actions">
        <button class="snap-modal-cancel" type="button">Cancel</button>
        <button class="snap-modal-submit" type="button">Save snapshot</button>
      </div>
      <div class="snap-modal-result"></div>
    </div>
  `;
  document.body.appendChild(overlay);

  const input  = overlay.querySelector('.snap-modal-input');
  const sub    = overlay.querySelector('.snap-modal-submit');
  const cancel = overlay.querySelector('.snap-modal-cancel');
  const close  = overlay.querySelector('.snap-modal-close');
  const result = overlay.querySelector('.snap-modal-result');

  const teardown = () => overlay.remove();
  cancel.addEventListener('click', teardown);
  close.addEventListener('click', teardown);
  eonBackdropClose(overlay, teardown);
  document.addEventListener('keydown', function onKey(e) {
    if (e.key === 'Escape') { teardown(); document.removeEventListener('keydown', onKey); }
  });

  const submit = async () => {
    const raw = input.value.trim();
    if (!raw) {
      result.innerHTML = `<span class="snap-modal-err">Enter a balance.</span>`;
      input.focus();
      return;
    }
    const bal = Number(raw);
    if (!Number.isFinite(bal) || bal < 0) {
      result.innerHTML = `<span class="snap-modal-err">Balance must be a non-negative number.</span>`;
      input.focus();
      return;
    }
    sub.disabled = true;
    sub.textContent = 'Saving…';
    result.innerHTML = '';
    try {
      const resp = await api('/api/finance/cash-snapshots', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          account_id: accountId,
          account_name: accountName,
          balance_ngn: bal,
          currency: 'NGN',
        }),
      });
      if (resp && resp.ok !== false) {
        result.innerHTML = `<span class="snap-modal-ok">Saved · refreshing</span>`;
        setTimeout(() => {
          teardown();
          if (typeof refreshCashSection === 'function') refreshCashSection();
          if (typeof refreshMoneyInsights === 'function') refreshMoneyInsights();
        }, 600);
      } else {
        result.innerHTML = `<span class="snap-modal-err">${esc(resp?.error || resp?.detail || 'Save failed')}</span>`;
        sub.disabled = false;
        sub.textContent = 'Save snapshot';
      }
    } catch (e) {
      result.innerHTML = `<span class="snap-modal-err">${esc(e?.message || String(e))}</span>`;
      sub.disabled = false;
      sub.textContent = 'Save snapshot';
    }
  };
  sub.addEventListener('click', submit);
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });
  setTimeout(() => input.focus(), 50);
}

// v3.0p18 9k — Cash section (now on Money). Fans out the cash-position
// + runway reads in parallel and hydrates the KPI band minis + the
// per-account drill-down. PR MONEY-COCKPIT: the standalone runway +
// total-cash tiles folded into the top KPI band; figures unified on
// _moneyFmtNGN (the local fmt printed "NGN 5,000,000" unlike every
// other tile); lookback comes from the single period resolver (the
// old local map lacked mtd/custom); `gen` guards stale responses.
function renderBankIntakeStatus(d) {
  // Phase A accounting intake (mig 572): statement freshness per
  // account + door status. The card stays hidden when the endpoint
  // fails or the role can't see finance.
  const card = document.getElementById('bank-intake-status');
  const lines = document.getElementById('bank-intake-lines');
  if (!card || !lines) return;
  if (!d?.ok) { card.style.display = 'none'; return; }
  // 2026-08-21: the line states COVERAGE, not last-movement. A statement
  // running to the 3rd covers the 3rd whether or not money moved that
  // day, and 'covered to X, 18d missing' is the sentence he can act on —
  // a bare date is not. verdict: fresh | late (past 14d) | dark (past
  // 35d or never); older payloads without it fall back to a.stale.
  // A HIDDEN CARD IS A SILENCE TOO. When EON could not read the account
  // list, say so where the accounts would have been — the file counts
  // and the Connect-Gmail ask below still hold, and vanishing the whole
  // card reads identically to "the endpoint died".
  const rosterNote = d.roster_unread
    ? `<div style="color:var(--warn, #d79a4a); padding:2px 0;">couldn't read your accounts just now — coverage unknown, not clean</div>`
    : '';
  const rows = rosterNote + (d.accounts || []).map(a => {
    const through = a.covered_through || (a.last_txn_at ? a.last_txn_at.slice(0, 10) : null);
    const verdict = a.verdict || (a.stale ? 'dark' : 'fresh');
    // 'unknown' wears AMBER, not the neutral grey a healthy account
    // wears. An account EON could not grade is not a clean one, and
    // rendering it in the calm colour is the same silence this card was
    // rebuilt to end — one shade quieter.
    const tone = verdict === 'dark' ? 'var(--danger, #d9534f)'
      : (verdict === 'late' || verdict === 'unknown')
        ? 'var(--warn, #d79a4a)' : 'var(--ink-soft)';
    const gap = (a.days_uncovered != null && verdict !== 'fresh')
      ? ` · ${a.days_uncovered}d missing` : '';
    // THREE WAYS TO HAVE NO COVER, and only one of them is his fault.
    // 'no statement ever' against an account whose file he uploaded
    // yesterday is the card calling him negligent for doing the thing
    // it asked; the backend's _coverage_phrase has said all three
    // apart since round 7 and this line now matches it.
    const label = verdict === 'unknown'
      ? (a.unknown_reason === 'empty_statement'
        ? `statement ${String(a.last_statement_at || '').slice(0, 10)} — nothing in it${gap}`
        : "couldn't read")
      : through ? `covered to ${through}${gap}` : 'no statement ever';
    return `<div style="display:flex; justify-content:space-between; gap:8px; padding:2px 0;">
      <span>${esc(a.display_name || '?')}</span>
      <span style="color:${tone};">${esc(label)}</span>
    </div>`;
  }).join('');
  // Vet 2026-08-01: the door is Gmail-direct now (operator: no
  // forwarding, no Drive). No grant → the one-tap connect link; grant
  // but no mail yet → say the pull is coming, not that it is broken.
  const door = d.email_wired
    ? `alerts live · last ${esc((d.last_alert_at || '').slice(0, 10))}`
    : (d.gmail_connected
      ? 'Gmail connected — first pull lands within 15 minutes'
      : `<a href="${API}/api/gmail-oauth/start" style="color:var(--accent, #38c172); text-decoration:underline;">Connect Gmail</a> — approve once as mailus@ and the ledger starts itself`);
  // The stuck tray, split by whose move it is. "N failed" over 7 days
  // was the number that hid nine locked statements she could have
  // released in a minute — every one of them older than the window —
  // behind the handful waiting on EON. Yours first: it is the half
  // she can act on.
  const stuckYours = Number(d.stuck_yours || 0);
  const stuckMine = Number(d.stuck_mine || 0);
  const stuck = (stuckYours || stuckMine)
    ? `<div style="margin-top:4px; color:var(--ink-soft);">`
      + (stuckYours
        ? `<span style="color:var(--warn,#f59e0b)">${esc(String(stuckYours))} waiting on you</span>`
        : '')
      + (stuckYours && stuckMine ? ' · ' : '')
      + (stuckMine ? `${esc(String(stuckMine))} on EON` : '')
      + ` — ${(d.stuck_kinds || []).map((k) => esc(`${k.n} ${k.words}`)).join(' · ')}`
      + `</div>`
    : '';
  lines.innerHTML = rows
    + `<div style="margin-top:6px; padding-top:6px; border-top:1px solid var(--line); color:var(--ink-soft);">`
    + `${esc(String(d.files_7d ?? 0))} file(s) 7d · `
    + `${esc(String(d.files_failed_7d ?? 0))} failed · ${door}</div>`
    + stuck;
  card.style.display = '';
}

// One bank-cash evidence contract is shared with all three forecast horizons.
function _bankCashDisplay(position) {
  const raw = position?.known_bank_cash_ngn;
  const hasAmount = position?.ok === true && position.observed_account_count > 0
    && raw !== null && raw !== undefined && typeof raw !== 'boolean'
    && String(raw).trim() !== '' && Number.isFinite(Number(raw));
  const count = Number(position?.observed_account_count || 0);
  const missing = Number(position?.not_counted_account_count || 0);
  const scope = position?.scope === 'business_books' ? 'business book' : 'all visible books';
  return {
    hasAmount,
    amount: hasAmount ? Number(raw) : null,
    note: !position?.ok ? "Bank balances couldn't load."
      : !hasAmount ? 'No owned bank balance established yet.'
        : `Known across ${count} account${count === 1 ? '' : 's'} · `
          // the count of uncounted accounts comes from the same evidence read; an
          // incomplete position with no count still says so (never 'all counted')
          + (position.complete === true ? 'all counted · bank-cash coverage complete'
              : missing ? `${missing} not counted` : 'full cash still unresolved')
          + ` · ${scope}`,
  };
}

function _paintBankCashPosition(position, gen) {
  if (gen !== State._moneyGen) return;
  const table=document.getElementById('money-bank-position-table');
  paintBankPositionTable(table,position,{openTools:accountId=>{
    const tools=document.getElementById('money-cash-drill');
    if(!tools)return;
    tools.open=true;
    const control=[...tools.querySelectorAll('[data-snap-acct]')].find(b=>b.dataset.snapAcct===accountId);
    const target=control||tools.querySelector('summary');target?.focus();target?.scrollIntoView({block:'center'});
  }});
  if(table&&!table.dataset.retryBound){table.dataset.retryBound='true';table.addEventListener('money-cash-retry',()=>refreshCashSection());}
  const view = _bankCashDisplay(position);
  const amount = document.getElementById('money-kpi-cash-num');
  const note = document.getElementById('money-kpi-cash-sub');
  const button = document.getElementById('money-cash-evidence-btn');
  if (amount) {
    amount.textContent = view.hasAmount ? formatNaira(view.amount, true) : '—';
    amount.title = view.hasAmount ? _moneyFmtNGN(view.amount) : '';
  }
  if (note) note.textContent = view.note;
  if (button) {
    button.hidden = false;
    button.textContent = position?.ok ? 'See the evidence' : 'Retry bank balances';
    button.onclick = position?.ok ? () => _openBankCashEvidence(position, button) : () => refreshCashSection();
  }
}

function _bankCashStateWords(state) {
  return ({
    observed: 'Counted', balance_stale: 'Too old to count', balance_missing: 'Balance needed',
    balance_unknown: 'Balance unreadable', balance_conflict: 'Conflicting balances',
    snapshot_date_unreadable: 'Source date unreadable', balance_evidence_missing: 'Source evidence needed',
    ownership_missing: 'Ownership link needed', ownership_conflict: 'Conflicting ownership links',
    ownership_evidence_missing: 'Ownership evidence needed', book_missing: 'Book link needed',
    account_identity_conflict: 'Account identity conflict', account_state_unknown: 'Account status unknown',
    liquidity_not_established: 'Cash availability not established', fx_evidence_needed: 'Currency evidence needed',
    currency_conflict: 'Currencies disagree', owned_registration_unlinked: 'Account link needed',
    snapshot_identity_missing: 'A balance has no account identity', book_balance_missing: 'Book balance needed',
    scope_not_established: 'Some evidence cannot be assigned to a book',
    no_observed_balances: 'No dated owned balance could be counted', source_unreadable: 'A source could not be read',
  })[state] || 'Evidence needs reconciliation';
}

function _closeBankCashEvidence(restoreFocus = true) {
  document.getElementById('money-bank-cash-evidence')?._dismissCashEvidence?.(restoreFocus);
}

function _openBankCashEvidence(position, trigger = document.activeElement) {
  if (document.getElementById('money-bank-cash-evidence')) return;
  const view = _bankCashDisplay(position);
  const overlay = document.createElement('div');
  overlay.id = 'money-bank-cash-evidence';
  overlay.className = 'snap-modal-overlay';
  const rows = (position.accounts || []).map(account => {
    const counted = account.state === 'observed';
    const amount = counted ? _moneyFmtNGN(Number(account.balance_ngn)) : account.reported_balance_ngn!=null ? `${_moneyFmtNGN(Number(account.reported_balance_ngn))} · not counted` : 'Not established';
    const day = account.source_date ? ` · source ${account.source_date}` : '';
    return `<div style="padding:12px 0;border-top:1px solid var(--line)">
      <div style="display:flex;justify-content:space-between;gap:16px"><strong>${esc(account.name)}</strong><span>${esc(amount)}</span></div>
      <div style="color:var(--ink-soft);margin-top:4px">${esc(_bankCashStateWords(account.state) + day)}</div></div>`;
  }).join('');
  const gaps = [...new Set((position.problems || []).map(problem => _bankCashStateWords(problem.state)))];
  const readAt = String(position.observed_at || '').slice(0, 16).replace('T', ' ');
  overlay.innerHTML = `<section class="snap-modal" role="dialog" aria-modal="true" aria-labelledby="money-cash-evidence-title" style="max-height:calc(100dvh - 48px);overflow-y:auto">
    <div class="snap-modal-head"><h2 id="money-cash-evidence-title" style="font-size:18px;margin:0">Behind the bank cash</h2><button class="snap-modal-close" type="button" aria-label="Close cash evidence" style="min-width:44px;min-height:44px">×</button></div>
    <p>${esc(view.note)}</p><p style="color:var(--ink-soft)">Read ${esc(readAt)} UTC. Balance dates belong to their sources. The freshness policy is ${esc(String(position.source_age_policy_days))} days.</p>
    ${rows || '<p>No owned account balance could be established.</p>'}
    ${gaps.length ? `<p style="color:var(--warn)">${gaps.map(esc).join(' · ')}</p>` : ''}
    <p style="color:var(--ink-soft)">This is bank-cash evidence. Stock, other assets, debts and commitments are assessed separately. It is not net worth or an amount approved to spend.</p></section>`;
  const close = (restoreFocus = true) => {
    document.removeEventListener('keydown', onKey);
    overlay.remove();
    if (restoreFocus && trigger?.isConnected) trigger.focus({ preventScroll: true });
  };
  const closeButton = overlay.querySelector('.snap-modal-close');
  const onKey = event => {
    if (event.key === 'Escape') { event.preventDefault(); close(); }
    // The only interactive element is the dismiss button; keep focus in the dialog.
    if (event.key === 'Tab') { event.preventDefault(); closeButton.focus(); }
  };
  closeButton.addEventListener('click', close);
  eonBackdropClose(overlay, close);
  document.addEventListener('keydown', onKey);
  overlay._dismissCashEvidence = close;
  document.body.appendChild(overlay);
  closeButton.focus();
}

async function refreshCashSection(gen = State._moneyGen) {
  const fmt = _moneyFmtNGN;

  // Hit the dedicated /api/finance/* read endpoints (cash position +
  // runway). Each returns the bare result dict; no tool-result wrapping.
  // 2026-05-19 (Daniel): runway reacts to the Money page's active
  // data-range chip.
  const w = moneyPeriodToWindow(State.moneyPeriod || 'this_month');
  const winEl = document.getElementById('money-cash-window');
  if (winEl) winEl.textContent = `runway over ${w.label}`;
  const lookback = w.days;
  // The small evidence read paints immediately, without waiting for the
  // legacy account drawer or runway calculation.
  const bankCashRead = api('/api/finance/bank-cash-position').catch(() => null)
    .then(position => { _paintBankCashPosition(position, gen); return position; });
  Promise.all([
    api('/api/finance/cash-position').catch(() => null),
    api(`/api/finance/runway?lookback_days=${lookback}`).catch(() => null),
    api('/api/finance/statement-intake/summary').catch(() => null),
    bankCashRead,
  ]).then(([cash, runway, intake, bankCash]) => {
    renderBankIntakeStatus(intake);
    if (gen !== State._moneyGen) return;  // superseded while in flight
    paintMoneyRecordGaps(document.getElementById('money-workspace'), cash);

    // Money-page redesign 2026-06-07: hydrate the two KPI mini-tiles at
    // the top of the page (Cash on hand + Net cashflow/mo) from the same
    // cash + runway payloads. Count-up the headline numbers.
    const kpiFlowNum = document.getElementById('money-kpi-flow-num');
    if (kpiFlowNum) {
      if (runway && runway.ok) {
        const flow = (runway.monthly_revenue_ngn || 0) - (runway.monthly_expenses_ngn || 0);
        kpiFlowNum.classList.toggle('tone-ok', flow >= 0);
        kpiFlowNum.classList.toggle('tone-danger', flow < 0);
        _countUp(kpiFlowNum, flow, (v) => `${flow < 0 ? '' : '+'}${formatNaira(v, true)}`);
        const sub = document.getElementById('money-kpi-flow-sub');
        if (sub) {
          const isProf = runway.is_profitable === true || runway.runway_status === 'profitable';
          sub.textContent = isProf ? 'Profitable' :
            (bankCash?.complete && flow < 0
              ? `${(Number(bankCash.complete_bank_cash_ngn) / -flow).toFixed(1)} mo at this net burn`
              : 'Net cashflow · cash coverage incomplete');
        }
      } else {
        kpiFlowNum.textContent = '—';
        const sub = document.getElementById('money-kpi-flow-sub');
        if (sub) sub.textContent = runway === null ? "Couldn't load" : '';
      }
    }

    // PR MONEY-COCKPIT: the standalone Runway + Total-cash tiles were
    // folded into the top KPI band (Cash on hand + Net cashflow minis
    // above carry the same payload); their render blocks are gone.

    // Per-account list — Money-page redesign 2026-06-07.
    //
    // Grouped, fintech-grade. Accounts are bucketed into clear sections
    // with per-group subtotals so the operator never confuses ad-credit
    // or loan principal with spendable cash:
    //   operating  — AccessBank, Wema, GTB Online, GTWorld, Providus
    //   store      — Monipoint Palms/Chevron/Osapa/Abuja (walk-in cash)
    //   drawer     — Cash drawer Palms/Chevron/Osapa/Abuja
    //   wallet     — Meta / TikTok / Snap prepaid (AD CREDIT, not cash)
    //   liability  — Loan from ENO / Moniepoint (NOT cash)
    // The headline Total cash = operating + store (+ Shopify-since) and
    // EXCLUDES wallets, loans, and un-snapshotted drawers — the cash
    // tile above states this explicitly.
    const accountsList = $('#cash-accounts-list');
    if (accountsList) {
      const accts = (cash && cash.by_account) || [];
      // Three faces, in this order. A read that RAN is never an outage — not
      // when it found nothing (main sends {ok:true, by_account:[]}) and not
      // when it could only count some of the rows (#3443 sends
      // {ok:false, cash_read_ok:true} with the rows it DID read still in it).
      // Only a read that did not run, or that never said it ran (a null, an
      // {ok:false} with no receipt, an unrecognised body), keeps the Retry.
      const readRan = cash?.ok === true || cash?.cash_read_ok === true;
      if (!readRan) {
        // Vet 2026-07-27: a failed cash-position fetch painted an empty
        // list — indistinguishable from "no accounts snapshotted".
        accountsList.innerHTML = `<div class="bks-error">Cash accounts couldn't load · <button class="bks-retry-btn" type="button" onclick="refreshCashSection()">Retry</button></div>`;
      } else if (accts.length === 0) {
        // House words, never the server's. The only note the endpoint sends
        // for this state (app/modules/finance.py:202-204) reads "No active
        // accounts yet. Use set_starting_cash to initialize." — an agent tool
        // name on a page the owner reads. Nothing from the payload is printed
        // here, so there is nothing to escape.
        accountsList.innerHTML = '<p class="mw-fine">No account balance can be counted yet. Add a dated balance for an account and it will be counted here.</p>';
      } else {
        accountsList.innerHTML = _renderGroupedAccounts(accts, fmt);
        // PR MONEY-COCKPIT: the drill-down summary carries the account
        // count so the collapsed line still says what's inside.
        const drillSum = document.getElementById('money-cash-drill-sum');
        if (drillSum) drillSum.textContent =
          `Account tools · ${accts.length} saved entries and estimates`;
        // Wire up the Update / Add buttons (delegated would also work;
        // explicit binding mirrors the prior behaviour).
        accountsList.querySelectorAll('.snap-update-btn').forEach(btn => {
          btn.addEventListener('click', () => {
            _openSnapshotModal({
              accountId: btn.dataset.snapAcct,
              accountName: btn.dataset.snapName,
              displayName: btn.dataset.snapDisplay || btn.dataset.snapName,
              currentBalance: btn.dataset.snapCurBal === '' ? null : Number(btn.dataset.snapCurBal),
            });
          });
        });
      }
    }
  }).catch(() => {});
  // PR MONEY-COCKPIT: the "Recent movements" block here was dead code —
  // its #cash-recent-list mount was removed from renderMoney by PR
  // MONEY-COMPLETE (the Recent transactions list covers it). Deleted.
}

// v3.0p20 polish: design principle — long lists collapse to N items with
// a "Show all N" toggle. Click reveals the rest in place, click again to
// collapse. Used for recent movements, recommendations per section, etc.
// `renderRow(item)` returns the HTML for a single item.
function renderCollapsedList(container, items, renderRow, opts = {}) {
  // PR MONEY-COCKPIT: `??` not `||` — initial: 0 is a valid ask
  // (collapse EVERYTHING behind the "Show all N" toggle; used for
  // healthy stores in store economics).
  const initial = opts.initial ?? 5;
  const label = opts.label || 'item';
  const noun = items.length === 1 ? label : (opts.labelPlural || `${label}s`);
  let expanded = false;
  const paint = () => {
    const shown = expanded ? items : items.slice(0, initial);
    const more = items.length - initial;
    const toggle = (more > 0)
      ? `<button type="button" class="btn-ghost"
            style="margin-top: 8px; font-size: 12px;"
            id="collapsed-toggle-${container.id || Math.random().toString(36).slice(2)}">
            ${expanded ? `Show fewer` : `Show all ${items.length} ${noun}`}
          </button>`
      : '';
    container.innerHTML = shown.map(renderRow).join('') + toggle;
    const btn = container.querySelector('button.btn-ghost');
    if (btn) btn.addEventListener('click', () => { expanded = !expanded; paint(); });
  };
  paint();
}


async function refreshMembersList() {
  const list = $('#members-list');
  if (!list) return;
  try {
    const data = await api(`/api/tenants/${State.tenant.slug}/members`);
    const members = data.members || [];
    if (members.length === 0) {
      list.innerHTML = `<div class="row"><div class="row-content"><div class="row-label">No members</div></div></div>`;
      return;
    }
    list.innerHTML = members.map(m => {
      const display = m.email || `User ${m.user_id.slice(0, 8)}`;
      const lastSeen = m.last_sign_in_at ? formatRelativeDate(m.last_sign_in_at) : 'never signed in';
      const isOwner = State.member?.role === 'owner';
      return `
        <div class="row" data-user-id="${esc(m.user_id)}" style="flex-wrap:wrap;">
          <div class="row-content">
            <div class="row-label">${esc(display)}</div>
            <div class="row-value" style="font-size: 11px; color: var(--ink-soft);">
              ${esc(m.role)} · ${esc(lastSeen)}
            </div>
          </div>
          ${isOwner && m.role !== 'owner' ? `
            <button class="btn-ghost tool-groups-btn" data-user-id="${esc(m.user_id)}"
                    data-display="${esc(display)}"
                    style="font-size:11px;padding:4px 10px;flex-shrink:0;">
              EON tools…
            </button>
          ` : ''}
        </div>
      `;
    }).join('');

    // v3.0p20 9rr: open whitelist editor for a member.
    list.querySelectorAll('.tool-groups-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        await _openToolGroupsEditor(btn.dataset.userId, btn.dataset.display);
      });
    });
  } catch (e) {
    list.innerHTML = renderEmptyInline(`Could not load members: ${esc(e.message)}`);
  }
}

// v3.0p20 9rr (Commit 4): owner whitelist editor. Modal-style overlay
// listing every tool group with a checkbox; owner picks which ones
// this manager can invoke from the per-task Ask EON drawer. Empty
// selection = chat-only. "Use department defaults" sets the column
// to NULL.
async function _openToolGroupsEditor(userId, displayName) {
  let catalog, member;
  try {
    const slug = State.tenant.slug;
    [catalog, member] = await Promise.all([
      api(`/api/tenants/${slug}/tool_groups`),
      api(`/api/tenants/${slug}/members`).then(r =>
        (r.members || []).find(m => m.user_id === userId) || {}),
    ]);
  } catch (e) {
    alert('Could not load tool groups: ' + (e.message || e));
    return;
  }
  const groups = catalog.groups || [];
  // tenant_members.allowed_tool_groups may be null (defaults) or an
  // array (explicit). The /members endpoint doesn't return it
  // currently, so default to null on first open.
  let selection = member.allowed_tool_groups; // null | []
  const useDefaults = selection == null;
  const selSet = new Set(Array.isArray(selection) ? selection : []);

  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:9999;
    display:flex;align-items:flex-end;justify-content:center;
  `;
  overlay.innerHTML = `
    <div style="background:var(--card);border-radius:16px 16px 0 0;
                padding:20px;max-width:520px;width:100%;max-height:85vh;
                overflow-y:auto;border:1px solid var(--border);">
      <div style="font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:var(--ink-soft);">EON tool whitelist</div>
      <div style="font-weight:600;font-size:15px;margin:4px 0 4px;">${esc(displayName)}</div>
      <div style="font-size:12px;color:var(--ink-soft);margin-bottom:14px;">
        Pick which tool groups this manager can invoke from the per-task Ask EON drawer.
        Empty selection = chat-only (no tool calls).
      </div>
      <label style="display:flex;align-items:center;gap:8px;padding:10px 0;border-bottom:1px solid var(--border);cursor:pointer;">
        <input type="checkbox" id="use-defaults" ${useDefaults ? 'checked' : ''} />
        <span style="font-size:13px;">Use department defaults</span>
      </label>
      <div id="group-list" style="margin-top:10px;${useDefaults ? 'opacity:0.5;pointer-events:none;' : ''}">
        ${groups.map(g => `
          <label style="display:flex;align-items:flex-start;gap:8px;padding:8px 0;border-bottom:1px solid var(--border);cursor:pointer;">
            <input type="checkbox" class="group-cb" data-group="${esc(g.name)}"
                   ${selSet.has(g.name) ? 'checked' : ''} />
            <div style="flex:1;">
              <div style="font-size:13px;font-weight:500;">${esc(g.name)}</div>
              <div style="font-size:11px;color:var(--ink-soft);margin-top:2px;">
                ${esc((g.tools || []).join(', '))}
              </div>
            </div>
          </label>
        `).join('')}
      </div>
      <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:16px;">
        <button class="btn-ghost" id="cancel-tg" type="button" style="font-size:13px;">Cancel</button>
        <button class="btn-ghost" id="save-tg" type="button"
                style="font-size:13px;background:var(--accent,#fde047);color:#1a1a1a;font-weight:600;">Save</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const defaultsCb = overlay.querySelector('#use-defaults');
  const groupList = overlay.querySelector('#group-list');
  defaultsCb.addEventListener('change', () => {
    groupList.style.opacity = defaultsCb.checked ? '0.5' : '1';
    groupList.style.pointerEvents = defaultsCb.checked ? 'none' : 'auto';
  });
  overlay.querySelector('#cancel-tg').addEventListener('click', () => overlay.remove());
  overlay.querySelector('#save-tg').addEventListener('click', async () => {
    const isDefault = defaultsCb.checked;
    let body;
    if (isDefault) {
      body = { allowed_tool_groups: null };
    } else {
      const picked = Array.from(overlay.querySelectorAll('.group-cb:checked'))
        .map(cb => cb.dataset.group);
      body = { allowed_tool_groups: picked };
    }
    try {
      await api(`/api/tenants/${State.tenant.slug}/members/${encodeURIComponent(userId)}/tool_groups`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      showToast('Tool groups updated.');
      overlay.remove();
    } catch (e) {
      alert('Save failed: ' + (e.message || e));
    }
  });
  eonBackdropClose(overlay, () => overlay.remove());
}


async function refreshInvitesList() {
  const list = $('#invites-list');
  if (!list) return;
  try {
    const data = await api(`/api/tenants/${State.tenant.slug}/invites`);
    const invites = data.invites || [];
    if (invites.length === 0) {
      list.innerHTML = `<div class="row" style="opacity: 0.6;"><div class="row-content"><div class="row-label" style="font-size: 13px;">No pending invites</div></div></div>`;
      return;
    }
    list.innerHTML = invites.map(inv => {
      const inviteUrl = `${window.location.origin}/invite-accept?token=${encodeURIComponent(inv.token)}`;
      return `
        <div class="row" data-invite-id="${esc(inv.id)}">
          <div class="row-content" style="min-width: 0;">
            <div class="row-label">${esc(inv.email)}</div>
            <div class="row-value" style="font-size: 11px; color: var(--ink-soft);">${esc(inv.role)} · invited ${formatRelativeDate(inv.created_at)}</div>
          </div>
          <div style="display: flex; gap: 8px; flex-shrink: 0;">
            <button class="btn-ghost copy-invite-btn" data-url="${esc(inviteUrl)}" type="button" style="font-size: 12px; padding: 4px 8px;">Copy link</button>
            <button class="btn-danger revoke-invite-btn" data-invite-id="${esc(inv.id)}" type="button" style="font-size: 12px; padding: 4px 8px;">Revoke</button>
          </div>
        </div>
      `;
    }).join('');
    
    // Wire copy + revoke
    list.querySelectorAll('.copy-invite-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const url = btn.dataset.url;
        try {
          await navigator.clipboard.writeText(url);
          const original = btn.textContent;
          btn.textContent = 'Copied!';
          setTimeout(() => { btn.textContent = original; }, 1500);
        } catch (err) {
          alert('Copy failed. Link:\n\n' + url);
        }
      });
    });
    list.querySelectorAll('.revoke-invite-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const inviteId = btn.dataset.inviteId;
        if (!confirm('Revoke this invite? The link will stop working.')) return;
        try {
          await api(`/api/tenants/${State.tenant.slug}/invites/${inviteId}`, { method: 'DELETE' });
          await refreshInvitesList();
        } catch (err) {
          alert(`Revoke failed: ${err.message || err}`);
        }
      });
    });
  } catch (e) {
    list.innerHTML = renderEmptyInline(`Could not load invites: ${esc(e.message)}`);
  }
}

async function refreshUsageSummary() {
  const list = $('#usage-summary');
  if (!list) return;
  try {
    const data = await api(`/api/tenants/${State.tenant.slug}/usage?days=30`);
    if (!data.ok) {
      list.innerHTML = renderEmptyInline('Usage unavailable');
      return;
    }
    const totalCost = data.total_cost_usd || 0;
    const totalCalls = data.total_calls || 0;
    const totalTokens = (data.total_input_tokens || 0) + (data.total_output_tokens || 0);
    const byUser = data.by_user || [];
    
    list.innerHTML = `
      <div class="row">
        <div class="row-content">
          <div class="row-label">Last 30 days</div>
          <div class="row-value">$${totalCost.toFixed(2)} · ${totalCalls.toLocaleString()} calls · ${(totalTokens / 1000).toFixed(0)}k tokens</div>
        </div>
      </div>
      ${byUser.length > 0 ? `
        <div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--ink-soft); margin-top: 12px; margin-bottom: 6px; padding: 0 4px;">
          By user
        </div>
        ${byUser.slice(0, 10).map(u => `
          <div class="row">
            <div class="row-content">
              <div class="row-label" style="font-family: ${u.user_id === 'system' ? 'inherit' : 'var(--font-mono)'}; font-size: 13px;">
                ${esc(u.display)}
              </div>
              <div class="row-value">${u.calls} calls · ${(u.tokens / 1000).toFixed(0)}k tokens</div>
            </div>
            <div class="row-action" style="font-family: var(--font-mono); font-weight: 600;">
              $${u.cost_usd.toFixed(2)}
            </div>
          </div>
        `).join('')}
      ` : ''}
    `;
  } catch (e) {
    list.innerHTML = renderEmptyInline(`Could not load usage: ${esc(e.message)}`);
  }
}

function openInviteSheet() {
  // Match existing pattern (menu-backdrop + menu-panel, see openConnectSheet).
  const wrap = document.createElement('div');
  wrap.innerHTML = `
    <div class="menu-backdrop" id="invite-backdrop"></div>
    <div class="menu-panel" id="invite-sheet" onclick="event.stopPropagation()" style="max-height: 85vh; padding-bottom: 24px;">
      <div class="menu-handle"></div>
      <div style="padding: 0 16px 12px;">
        <div class="eyebrow" style="margin-bottom: 6px;">Invite teammate</div>
        <div style="font-size: 13px; color: var(--ink-dim); line-height: 1.55; margin-bottom: 18px;">
          Generates a one-time link. Share via WhatsApp/text — they sign in or sign up, then auto-join this workspace.
        </div>
        
        <div class="field">
          <label class="field-label">Email</label>
          <input class="field-input" id="invite-email-input" type="email"
                 placeholder="teammate@example.com"
                 autocomplete="off" autocapitalize="off" autocorrect="off" />
        </div>
        
        <div class="field">
          <label class="field-label">Role</label>
          <select class="field-input" id="invite-role-select">
            <option value="member">Member — view + use EON</option>
            <option value="manager">Manager — invite below</option>
            <option value="admin">Admin — full access except ownership transfer</option>
          </select>
        </div>
        
        <div id="invite-result" style="display: none; margin-top: 14px; padding: 10px 12px; background: var(--bg-sub); border-radius: 6px; font-size: 13px; line-height: 1.5;"></div>
        
        <button class="auth-btn" id="invite-submit" style="margin-top: 18px;">Generate invite link</button>
        <button class="chip" id="invite-cancel" style="margin-top: 10px; width: 100%; padding: 12px;">Cancel</button>
      </div>
    </div>
  `;
  Array.from(wrap.children).forEach(c => document.body.appendChild(c));
  
  const close = () => {
    $('#invite-backdrop')?.remove();
    $('#invite-sheet')?.remove();
  };
  $('#invite-backdrop')?.addEventListener('click', close);
  $('#invite-cancel')?.addEventListener('click', close);
  
  $('#invite-submit')?.addEventListener('click', async () => {
    const email = $('#invite-email-input').value.trim();
    const role = $('#invite-role-select').value;
    const resultDiv = $('#invite-result');
    
    if (!email) {
      resultDiv.style.display = 'block';
      resultDiv.style.color = 'var(--danger)';
      resultDiv.textContent = 'Enter an email address';
      return;
    }
    
    const submit = $('#invite-submit');
    submit.disabled = true;
    submit.textContent = 'Creating…';
    
    try {
      const res = await api(`/api/tenants/${State.tenant.slug}/invites`, {
        method: 'POST',
        body: JSON.stringify({ email, role }),
      });
      const inviteUrl = res.invite_url
        || `${window.location.origin}/invite-accept?token=${encodeURIComponent(res.invite_token)}`;
      // 2026-05-31: backend now auto-sends the invite via Resend if
      // RESEND_API_KEY is configured. Surface that status so the
      // operator knows whether they still need to copy/paste manually.
      const emailSent = res.email_sent === true;
      const sentLine = emailSent
        ? `<div style="margin-bottom: 8px; color: var(--ok);">✓ Invite emailed to <strong>${esc(email)}</strong></div>`
        : `<div style="margin-bottom: 8px;">✓ Invite created for <strong>${esc(email)}</strong> <span style="opacity:0.6; font-size:11px;">(email auto-send not configured — copy the link)</span></div>`;
      resultDiv.style.display = 'block';
      resultDiv.style.color = 'var(--ink)';
      resultDiv.innerHTML = `
        ${sentLine}
        <div style="word-break: break-all; padding: 8px; background: var(--bg); border-radius: 4px; font-family: var(--font-mono); font-size: 11px; margin-bottom: 8px;">${esc(inviteUrl)}</div>
        <button id="copy-fresh-invite" class="chip" type="button" style="font-size: 12px; padding: 6px 10px;">Copy link</button>
      `;
      submit.style.display = 'none';
      
      $('#copy-fresh-invite')?.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(inviteUrl);
          $('#copy-fresh-invite').textContent = '✓ Copied — share via WhatsApp';
        } catch {
          // Fallback: select the URL text for manual copy
          alert('Tap the URL above and long-press to copy.');
        }
      });
      
      // Refresh background list
      refreshInvitesList();
    } catch (e) {
      resultDiv.style.display = 'block';
      resultDiv.style.color = 'var(--danger)';
      resultDiv.textContent = `Failed: ${e.message || e}`;
      submit.disabled = false;
      submit.textContent = 'Generate invite link';
    }
  });
}

// Helper for relative dates ("2 days ago")
function formatRelativeDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  if (diffHours < 1) return 'just now';
  if (diffHours < 24) return `${Math.round(diffHours)}h ago`;
  const diffDays = diffHours / 24;
  if (diffDays < 30) return `${Math.round(diffDays)}d ago`;
  return d.toLocaleDateString();
}

async function refreshIntegrationsList() {
  const list = $('#integrations-list');
  if (!list) return;
  checkShopifyInstallReady();
  try {
    const data = await api(`/api/tenants/${State.tenant.slug}/integrations`);
    // Telegram is hidden until validator is built. BigQuery removed 2026-06-08
    // — the Hevo→BigQuery/bronze pipeline was cut; EON now reads Shopify + ad
    // platforms directly (eon_sync), so BigQuery is no longer an integration.
    // GrowthBook (2026-10-09): the place to save the secret key growthbook_admin reads.
    const all = ['shopify', 'meta_ads', 'tiktok_ads', 'snapchat_ads', 'growthbook'];
    const byProvider = Object.fromEntries((data.integrations || []).map(i => [i.provider, i]));
    list.innerHTML = all.map(p => {
      const i = byProvider[p];
      const connected = i && i.is_active;
      const label = INTEGRATION_LABELS[p] || p;
      const subValue = connected
        ? formatIntegrationSubtitle(p, i.config)
        : 'Not connected';
      return `
        <div class="row" data-provider="${p}">
          <div class="row-content">
            <div class="row-label">${esc(label)}</div>
            <div class="row-value">${esc(subValue)}</div>
          </div>
          <div class="row-action">${connected ? 'Manage' : 'Connect'}</div>
        </div>`;
    }).join('');

    // Bind row clicks
    list.querySelectorAll('.row').forEach(row => {
      row.addEventListener('click', () => {
        const provider = row.dataset.provider;
        const connected = !!byProvider[provider]?.is_active;
        if (connected) {
          openManageSheet(provider, byProvider[provider]);
        } else {
          openConnectSheet(provider);
        }
      });
    });
  } catch (e) {
    list.innerHTML = renderEmptyInline(`Could not load integrations: ${esc(e.message)}`);
  }
}

const INTEGRATION_LABELS = {
  shopify: 'Shopify',
  meta_ads: 'Meta Ads',
  tiktok_ads: 'TikTok Ads',
  snapchat_ads: 'Snapchat Ads',
  telegram: 'Telegram',
  growthbook: 'GrowthBook',
};

// The key's own name says its role; GrowthBook's read cannot (validators.py).
function growthbookKeyWords(config) {
  if (config.key_role === 'admin') return 'Admin key';
  if (config.key_role === 'readonly') return 'Readonly key: EON can check its copy, not make it';
  return '';
}

function formatIntegrationSubtitle(provider, config) {
  config = config || {};
  if (provider === 'shopify' && config.shop_name) return `${config.shop_name} · ${config.currency || ''}`.trim();
  if (provider === 'meta_ads' && config.ad_account_name) return `${config.ad_account_name} · ${config.currency || ''}`.trim();
  if (provider === 'tiktok_ads' && config.advertiser_name) return `${config.advertiser_name}`;
  if (provider === 'snapchat_ads' && config.ad_account_name) return `${config.ad_account_name}`;
  if (provider === 'growthbook' && growthbookKeyWords(config)) return growthbookKeyWords(config);
  return 'Connected';
}

// ── Schemas: which fields each platform needs ─────────────
const INTEGRATION_FIELDS = {
  shopify: {
    label: 'Shopify',
    instructions: 'Generate a custom Admin API access token from your Shopify admin: Settings → Apps and sales channels → Develop apps → Create app. Required scopes: read_orders, read_products, read_inventory.',
    fields: [
      { key: 'store_url', label: 'Store domain', placeholder: 'crux.myshopify.com', kind: 'config', required: true },
      { key: 'access_token', label: 'Admin API access token', placeholder: 'shpat_...', kind: 'cred', required: true, secret: true },
    ],
  },
  meta_ads: {
    label: 'Meta Ads',
    instructions: 'Use a long-lived user token or System User token. Required permissions: ads_read, ads_management.',
    fields: [
      { key: 'ad_account_id', label: 'Ad account ID', placeholder: 'act_1234567890', kind: 'config', required: true },
      { key: 'access_token', label: 'Access token', placeholder: 'EAA...', kind: 'cred', required: true, secret: true },
    ],
  },
  tiktok_ads: {
    label: 'TikTok Ads',
    instructions: 'Get this from TikTok for Business → Marketing API → Apps → your app → Authorization.',
    fields: [
      { key: 'advertiser_id', label: 'Advertiser ID', placeholder: '7123456789012345678', kind: 'config', required: true },
      { key: 'access_token', label: 'Access token', placeholder: '', kind: 'cred', required: true, secret: true },
    ],
  },
  snapchat_ads: {
    label: 'Snapchat Ads',
    instructions: 'Snap access tokens expire every 30 min. Provide refresh_token + client_id + client_secret for auto-refresh.',
    fields: [
      { key: 'ad_account_id', label: 'Ad account ID', placeholder: '', kind: 'config', required: true },
      { key: 'access_token', label: 'Access token (optional if refresh provided)', placeholder: '', kind: 'cred', required: false, secret: true },
      { key: 'refresh_token', label: 'Refresh token', placeholder: '', kind: 'cred', required: false, secret: true },
      { key: 'client_id', label: 'Client ID', placeholder: '', kind: 'cred', required: false },
      { key: 'client_secret', label: 'Client secret', placeholder: '', kind: 'cred', required: false, secret: true },
    ],
  },
  telegram: {
    label: 'Telegram',
    instructions: 'Get a bot token from @BotFather, then have CRUX team members /start the bot.',
    fields: [
      { key: 'bot_token', label: 'Bot token', placeholder: '123:ABC-DEF...', kind: 'cred', required: true, secret: true },
    ],
  },
  growthbook: {
    label: 'GrowthBook',
    instructions: 'In GrowthBook: Settings, API Keys, New Secret Key, role Admin. Paste it here. EON keeps it encrypted; it never goes in Railway.',
    fields: [
      { key: 'admin_key', label: 'Secret key', placeholder: 'secret_admin_...', kind: 'cred', required: true, secret: true },
      { key: 'api_host', label: 'GrowthBook address (only if you host your own)', placeholder: 'https://api.growthbook.io', kind: 'config', required: false },
    ],
  },
};

function openConnectSheet(provider) {
  const schema = INTEGRATION_FIELDS[provider];
  if (!schema) return;

  const fields = schema.fields.map(f => `
    <div class="field">
      <label class="field-label">${esc(f.label)}${f.required ? '' : ' <span style="color: var(--ink-mute); font-weight:400;">(optional)</span>'}</label>
      ${f.multiline
        ? `<textarea class="field-input" id="conn-${f.key}" placeholder="${esc(f.placeholder)}" rows="6"></textarea>`
        : `<input class="field-input" id="conn-${f.key}" type="${f.secret ? 'password' : 'text'}" placeholder="${esc(f.placeholder)}" autocomplete="off" autocapitalize="off" autocorrect="off" />`
      }
    </div>
  ` + (provider === 'shopify' && f.key === 'store_url' && shopifyInstallReady
    ? shopifyInstallBlock() : '')).join('');

  const wrap = document.createElement('div');
  wrap.innerHTML = `
    <div class="menu-backdrop" id="conn-backdrop"></div>
    <div class="menu-panel" id="conn-sheet" onclick="event.stopPropagation()" style="max-height: 85vh; padding-bottom: 24px;">
      <div class="menu-handle"></div>
      <div style="padding: 0 16px 12px;">
        <div class="eyebrow" style="margin-bottom: 6px;">Connect ${esc(schema.label)}</div>
        <div style="font-size: 13px; color: var(--ink-dim); line-height: 1.55; margin-bottom: 18px;">
          ${esc(schema.instructions)}
        </div>
        ${fields}
        <div id="conn-error" style="margin-top: 8px;"></div>
        <button class="auth-btn" id="conn-submit" style="margin-top: 18px;">Validate &amp; connect</button>
        <button class="chip" id="conn-cancel" style="margin-top: 10px; width: 100%; padding: 12px;">Cancel</button>
      </div>
    </div>
  `;
  Array.from(wrap.children).forEach(c => document.body.appendChild(c));

  const close = () => {
    $('#conn-backdrop')?.remove();
    $('#conn-sheet')?.remove();
  };
  $('#conn-backdrop')?.addEventListener('click', close);
  $('#conn-cancel')?.addEventListener('click', close);
  $('#conn-install')?.addEventListener('click', (e) => startShopifyInstall(e.currentTarget));

  $('#conn-submit')?.addEventListener('click', async () => {
    const credentials = {};
    const config = {};
    for (const f of schema.fields) {
      const val = $(`#conn-${f.key}`)?.value.trim();
      if (val) {
        if (f.kind === 'cred') credentials[f.key] = val;
        else config[f.key] = val;
      } else if (f.required) {
        $('#conn-error').innerHTML = `<div class="auth-error">${esc(f.label)} is required.</div>`;
        return;
      }
    }
    const submit = $('#conn-submit');
    submit.disabled = true;
    submit.textContent = 'Validating with platform…';
    $('#conn-error').innerHTML = '';
    try {
      const res = await api(`/api/tenants/${State.tenant.slug}/integrations`, {
        method: 'POST',
        body: JSON.stringify({ provider, credentials, config }),
      });
      close();
      const detail = res.info?.shop_name ? ` · ${res.info.shop_name}`
                  : res.info?.ad_account_name ? ` · ${res.info.ad_account_name}`
                  : res.info?.advertiser_name ? ` · ${res.info.advertiser_name}`
                  : provider === 'growthbook' && growthbookKeyWords(res.info || {}) ? ` · ${growthbookKeyWords(res.info || {})}` : '';
      showToast(`✓ ${schema.label} connected${detail}`);
      await refreshIntegrationsList();
    } catch (e) {
      $('#conn-error').innerHTML = `<div class="auth-error">${esc(e.message)}</div>`;
      submit.disabled = false;
      submit.textContent = 'Validate & connect';
    }
  });

  setTimeout(() => {
    const first = $(`#conn-${schema.fields[0].key}`);
    if (first) first.focus();
  }, 320);
}

// Install through Shopify (2026-10-26, routes_shopify_install.py): no token to
// copy. Shown only when the server says the app is set up and she is the owner.
let shopifyInstallReady = false;
function checkShopifyInstallReady() {
  api('/api/integrations/shopify/install/ready').then(r => { shopifyInstallReady = !!(r && r.ready); })
    .catch(() => { shopifyInstallReady = false; });
}

function shopifyInstallBlock() {
  return `
    <button class="auth-btn" id="conn-install" style="margin: 2px 0 6px;">Install through Shopify</button>
    <div style="font-size: 12px; color: var(--ink-dim); line-height: 1.5; margin-bottom: 18px;">
      No token needed. Shopify asks you to approve, then brings you back here. Or paste a token below.
    </div>`;
}

async function startShopifyInstall(btn) {
  const shop = ($('#conn-store_url')?.value || '').trim().toLowerCase()
    .replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  if (!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(shop)) {
    $('#conn-error').innerHTML = '<div class="auth-error">Type your store address first. It looks like yourshop.myshopify.com.</div>';
    return;
  }
  btn.disabled = true; btn.textContent = 'Opening Shopify…';
  $('#conn-error').innerHTML = '';
  try {
    const res = await api(`/api/integrations/shopify/install?shop=${encodeURIComponent(shop)}`,
                          { headers: { Accept: 'application/json' } });
    window.location.assign(res.authorize_url);
  } catch (e) {
    $('#conn-error').innerHTML = `<div class="auth-error">${esc(e.message)}</div>`;
    btn.disabled = false; btn.textContent = 'Install through Shopify';
  }
}

// The callback sends back only a short code; she reads a sentence.
const SHOPIFY_INSTALL_WORDS = {
  expired: 'That took longer than 10 minutes. Tap Install through Shopify again.',
  other_browser: 'Finish the install in the browser you started it in. Nothing was saved.',
  not_owner: 'Only the workspace owner can install Shopify. Nothing was saved.',
  not_saved: "Shopify said yes, but EON couldn't save the connection. Try again.",
  not_set_up: "Shopify install isn't set up yet.",
  already_connected: 'This workspace is already connected to a Shopify store. Disconnect it first, then install. Nothing was saved.',
};

function takeShopifyInstallOutcome() {
  try {
    const qp = new URLSearchParams(location.search);
    const flag = qp.get('shopify_connected');
    if (flag !== '1' && flag !== 'error') return null;
    const msg = qp.get('msg') || '';
    qp.delete('shopify_connected'); qp.delete('msg');
    const rest = qp.toString();
    history.replaceState(history.state, '', location.pathname + (rest ? '?' + rest : ''));
    return { ok: flag === '1', msg };
  } catch (_) { return null; }
}

// Anyone can send ?shopify_connected=1, so "connected" is said only when the list agrees.
function showShopifyInstallOutcome(outcome) {
  if (!outcome) return;
  const row = $('#integrations-list .row[data-provider="shopify"]');
  const connected = row?.querySelector('.row-action')?.textContent === 'Manage';
  if (outcome.ok) return void (connected && showToast('Shopify is connected.'));
  const words = SHOPIFY_INSTALL_WORDS[outcome.msg] || "Shopify didn't confirm this install. Try again.";
  showToast(words);
  const sub = row?.querySelector('.row-value');
  if (sub && !connected) sub.textContent = words;
}

function openManageSheet(provider, integration) {
  const schema = INTEGRATION_FIELDS[provider];
  if (!schema) return;
  const config = integration.config || {};
  const lastSync = integration.last_sync_at
    ? new Date(integration.last_sync_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : 'never';

  const wrap = document.createElement('div');
  wrap.innerHTML = `
    <div class="menu-backdrop" id="mng-backdrop"></div>
    <div class="menu-panel" id="mng-sheet" onclick="event.stopPropagation()">
      <div class="menu-handle"></div>
      <div style="padding: 0 16px 12px;">
        <div class="eyebrow" style="margin-bottom: 12px;">${esc(schema.label)}</div>
        <div class="card" style="padding: 14px 16px; margin-bottom: 16px;">
          ${Object.entries(config).filter(([k]) => !['credentials'].includes(k)).map(([k, v]) => `
            <div style="display: flex; justify-content: space-between; padding: 4px 0; font-size: 13px;">
              <span style="color: var(--ink-mute); font-family: var(--mono); font-size: 11px; letter-spacing: 0.06em;">${esc(k)}</span>
              <span style="color: var(--ink); max-width: 60%; text-align: right; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${esc(String(v))}</span>
            </div>
          `).join('')}
          <div style="display: flex; justify-content: space-between; padding: 4px 0; font-size: 13px; border-top: 1px solid var(--line-soft); margin-top: 8px; padding-top: 10px;">
            <span style="color: var(--ink-mute); font-family: var(--mono); font-size: 11px; letter-spacing: 0.06em;">last_test</span>
            <span style="color: var(--ink-dim);">${esc(lastSync)}</span>
          </div>
        </div>
        <div id="mng-result" style="margin-bottom: 12px;"></div>
        <button class="auth-btn" id="mng-test">Test connection</button>
        <button class="chip" id="mng-reconnect" style="margin-top: 10px; width: 100%; padding: 12px;">Update credentials</button>
        <button class="btn-danger" id="mng-disconnect" style="margin-top: 10px; width: 100%; padding: 12px;">Disconnect</button>
        <button class="chip" id="mng-cancel" style="margin-top: 10px; width: 100%; padding: 12px;">Close</button>
      </div>
    </div>
  `;
  Array.from(wrap.children).forEach(c => document.body.appendChild(c));

  const close = () => {
    $('#mng-backdrop')?.remove();
    $('#mng-sheet')?.remove();
  };
  $('#mng-backdrop')?.addEventListener('click', close);
  $('#mng-cancel')?.addEventListener('click', close);
  $('#mng-reconnect')?.addEventListener('click', () => {
    close();
    openConnectSheet(provider);
  });

  $('#mng-test')?.addEventListener('click', async () => {
    const btn = $('#mng-test');
    btn.disabled = true;
    btn.textContent = 'Testing…';
    $('#mng-result').innerHTML = '';
    try {
      const res = await api(`/api/tenants/${State.tenant.slug}/integrations/${provider}/test`, {
        method: 'POST',
      });
      if (res.ok) {
        $('#mng-result').innerHTML = `
          <div class="card" style="padding: 12px 14px; border-color: var(--ok); background: var(--ok-soft);">
            <div style="color: var(--ok); font-size: 13px; font-weight: 500;">✓ Connection healthy</div>
          </div>`;
      } else {
        $('#mng-result').innerHTML = `<div class="auth-error">${esc(res.error || 'Test failed')}</div>`;
      }
    } catch (e) {
      $('#mng-result').innerHTML = `<div class="auth-error">${esc(e.message)}</div>`;
    } finally {
      btn.disabled = false;
      btn.textContent = 'Test connection';
    }
  });

  $('#mng-disconnect')?.addEventListener('click', async () => {
    if (!confirm(`Disconnect ${schema.label}? Your credentials will be removed.`)) return;
    try {
      await api(`/api/tenants/${State.tenant.slug}/integrations/${provider}`, {
        method: 'DELETE',
      });
      close();
      showToast(`Disconnected ${schema.label}`);
      await refreshIntegrationsList();
    } catch (e) {
      $('#mng-result').innerHTML = `<div class="auth-error">${esc(e.message)}</div>`;
    }
  });
}

function showToast(msg) {
  const old = $('#toast');
  if (old) old.remove();
  // role=status: save/refuse feedback must reach screen readers too
  // (worksheet a11y vet, 2026-08-30) — polite live region, app-wide
  const t = elFromHTML(`<div id="toast" class="toast" role="status" aria-live="polite">${esc(msg)}</div>`);
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('toast-in'), 10);
  setTimeout(() => {
    t.classList.remove('toast-in');
    setTimeout(() => t.remove(), 320);
  }, 2400);
}

// What's new — LIVE feed (2026-07-29). Operator: "i want to also be
// notified of new code updates, massive ideas in code, and new ideas in
// any department on entire business or codebase", to land as a feed
// inside the app.
//
// What stood here was a HARD-CODED array, last touched 2026-05-15 — two
// months stale while 106 real patch notes piled up in play_patch_notes,
// written automatically on every merge by the play-patch-note workflow.
// The changelog was never the missing piece; showing it to him was.
//
// Three sections, from GET /api/whats-new:
//   BIG      worth over ₦1m a month, or the first time EON has raised
//            that kind of idea. Pinned first — he chose a feed, not a
//            push, so "big" means top of the list, never an interruption.
//   SHIPPED  every merge, grouped by what part of the business it
//            touched (~15/day is unreadable as a flat list).
//   IDEAS    every department. Stock collapses to one row that still
//            OPENS to its items — 109 of last week's 133 ideas were
//            stock, and stock belongs to the Head of Inventory now.
async function showWhatsNew() {
  $('#whats-new-modal')?.remove();
  const modal = elFromHTML(`
    <div id="whats-new-modal" class="modal-backdrop">
      <div class="modal-panel" onclick="event.stopPropagation()">
        <div class="modal-head">
          <h3>What's new</h3>
          <button class="modal-close" id="wn-close" aria-label="Close">×</button>
        </div>
        <div class="modal-body" id="wn-body">
          <div class="wn-loading">reading…</div>
        </div>
      </div>
    </div>`);
  document.body.appendChild(modal);
  const close = () => modal.remove();
  modal.addEventListener('click', close);
  $('#wn-close').addEventListener('click', close);

  let d;
  try {
    d = await api('/api/whats-new');
  } catch (err) {
    const b = $('#wn-body');
    if (b) b.innerHTML = `<div class="wn-loading">Could not read it just now. ${esc(err.message || '')}</div>`;
    return;
  }
  const body = $('#wn-body');
  if (!body) return;                       // closed while loading

  const money = (v) => {
    const n = Number(v || 0);
    return n >= 1e6 ? `₦${(n / 1e6).toFixed(1)}m` : `₦${n.toLocaleString()}`;
  };
  const when = (iso) => {
    if (!iso) return '';
    const h = Math.floor((Date.now() - new Date(iso)) / 3600000);
    if (h < 1) return 'just now';
    if (h < 24) return `${h}h ago`;
    const dd = Math.floor(h / 24);
    return dd === 1 ? 'yesterday' : `${dd} days ago`;
  };

  const big = (d.big || []).map(b => `
    <div class="wn-big">
      <div class="wn-big-tag">${esc(b.dept_label || '')}</div>
      <div class="wn-big-t">${esc(b.title || '')}</div>
      <div class="wn-big-w">${esc(b.why_big || '')}</div>
    </div>`).join('');

  // The deeper read, and ONLY for builds that exist because he asked —
  // a change that came from a build request he filed or a card he
  // approved. A passing internal fix keeps its one line. The cost row
  // always renders, even when the agent skipped its own report, so a
  // missing report reads as missing rather than as "no cost".
  const report = (r) => {
    if (!r) return '';
    const sec = (label, body) => body
      ? `<div class="wn-rep__h">${label}</div><div class="wn-rep__b">${esc(body)}</div>`
      : '';
    const c = r.cost || {};
    const cost = [
      c.minutes != null ? `${c.minutes} min` : '',
      // who built it — the server maps the raw id to a plain name;
      // a report with no recorded model says so, never guesses one
      r.built_by ? `built by ${r.built_by}` : 'model unknown',
      c.paid_with ? `paid with ${c.paid_with}` : '',
    ].filter(Boolean).join(' · ');
    return `
      <details class="wn-rep">
        <summary>the longer story${r.priority === 'high' || r.priority === 'critical'
          ? '<span class="wn-rep__p">you marked this urgent</span>' : ''}</summary>
        ${r.asked_for ? `<div class="wn-rep__h">You asked for</div>
          <div class="wn-rep__b">${esc(r.asked_for)}</div>` : ''}
        ${sec('What changed', r.what_changed)}
        ${sec('What this might break', r.might_break)}
        ${sec('What EON decided on its own', r.decided_alone)}
        ${!r.agent_wrote_a_report
          ? '<div class="wn-rep__b wn-rep__b--warn">This build finished before it wrote its report, so the three answers above are missing. What it cost is still real.</div>'
          : ''}
        ${cost ? `<div class="wn-rep__h">What it cost</div>
          <div class="wn-rep__b">${esc(cost)}</div>` : ''}
        ${r.pr_url ? `<a class="wn-rep__l" href="${esc(r.pr_url)}" target="_blank" rel="noopener">see the change itself</a>` : ''}
      </details>`;
  };

  const shipped = (d.shipped?.groups || []).map(g => `
    <details class="wn-grp">
      <summary><b>${esc(g.label)}</b><span class="wn-n">${g.n}</span></summary>
      ${g.items.map(i => `
        <div class="wn-item">
          <div class="wn-title">${esc(i.note || '')}</div>
          ${i.detail ? `<div class="wn-body-t">${esc(i.detail)}</div>` : ''}
          <div class="wn-date">${esc(when(i.at))}${i.pr ? ` · change ${i.pr}` : ''}</div>
          ${report(i.report)}
        </div>`).join('')}
    </details>`).join('');

  const ideas = (d.ideas?.groups || []).map(g => `
    <details class="wn-grp${g.collapsed ? ' wn-grp--folded' : ''}">
      <summary><b>${esc(g.label)}</b><span class="wn-n">${g.n}</span>${
        g.collapsed && g.owner
          ? `<span class="wn-owner">${esc(g.owner)} has these</span>` : ''}</summary>
      ${g.items.map(i => `
        <div class="wn-item">
          <div class="wn-title">${esc(i.title || '')}</div>
          ${i.why ? `<div class="wn-body-t">${esc(String(i.why).slice(0, 220))}</div>` : ''}
          <div class="wn-date">${esc(when(i.at))}${
            i.value_ngn_mo ? ` · about ${money(i.value_ngn_mo)} a month` : ''}</div>
        </div>`).join('')}
    </details>`).join('');

  body.innerHTML = `
    ${big ? `<div class="wn-sec">Worth your attention</div>${big}` : ''}
    <div class="wn-sec">What EON shipped<span class="wn-sub">${
      d.shipped?.total || 0} in the last ${d.window_days} days</span></div>
    ${shipped || '<div class="wn-loading">nothing shipped in this window.</div>'}
    <div class="wn-sec">New ideas<span class="wn-sub">${
      d.ideas?.total || 0} raised in the last ${d.window_days} days</span></div>
    ${ideas || '<div class="wn-loading">no new ideas in this window.</div>'}`;
}

// ──────────────────────────────────────────────────────────
// EON's beliefs (2026-07-24, GBrain-inspired) — read-only browse of
// everything in long-term memory (core_memory), with the typed entity
// links from migration 486 rendered as chips. Until this surface,
// memories were invisible unless a search happened to hit them — a
// wrong learned rule could steer the agent for months unseen. Retire
// reuses the existing /dismiss endpoint (soft delete).

function beliefsIcon() {
  // Open book — "what EON has learned".
  return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M2 4h6a3 3 0 0 1 3 3v13a2.4 2.4 0 0 0-2.4-2.4H2z"/><path d="M22 4h-6a3 3 0 0 0-3 3v13a2.4 2.4 0 0 1 2.4-2.4H22z"/></svg>`;
}

const _BELIEF_CAT_LABELS = {
  brand_rule: 'Brand rules', strategy: 'Strategy', pricing: 'Pricing',
  inventory: 'Inventory', marketing: 'Marketing', hr: 'HR',
  supply_chain: 'Supply chain', system_log: 'System', general: 'General',
};

// Why each retired belief died (mig 487 lifecycle — Zep-style: beliefs
// are invalidated, never deleted, so the graveyard stays auditable).
const _BELIEF_DEATH_LABELS = {
  superseded: 'superseded by a newer rule',
  dismissed_by_owner: 'dismissed',
};

function _beliefRow(m) {
  const cat = m.category || 'general';
  const retired = m.is_active === false;
  const age = m.created_at
    ? Math.max(0, Math.round((Date.now() - new Date(m.created_at)) / 86400000))
    : null;
  const chips = (m.entity_refs || []).map(r =>
    `<button type="button" class="belief-chip" data-etype="${esc(r.type)}" data-eid="${esc(String(r.id))}" title="Filter to ${esc(r.name)}">${esc(r.name)}</button>`
  ).join('');
  const death = retired
    ? `<span class="belief-death">${esc(_BELIEF_DEATH_LABELS[m.demoted_reason] || 'stale')}</span>`
    : '';
  const action = retired
    ? `<button type="button" class="btn-ghost belief-restore" data-id="${esc(m.id)}">${m.review_due ? 'Needs a review date' : 'Restore'}</button>`
    : `<button type="button" class="btn-ghost belief-retire" data-id="${esc(m.id)}">Retire</button>`;
  return `
    <div class="belief-row${retired ? ' belief-row--retired' : ''}" data-id="${esc(m.id)}" data-cat="${esc(cat)}">
      <div class="belief-main">
        <span class="belief-cat">${esc(_BELIEF_CAT_LABELS[cat] || cat)}</span>
        <span class="belief-content">${esc(m.content || '')}</span>
        <span class="belief-meta">${death}${chips}${age !== null ? `<span class="belief-age">${age}d</span>` : ''}</span>
      </div>
      ${action}
    </div>`;
}

async function showBeliefs() {
  $('#beliefs-modal')?.remove();
  const modal = elFromHTML(`
    <div id="beliefs-modal" class="modal-backdrop">
      <div class="modal-panel beliefs-panel" onclick="event.stopPropagation()">
        <div class="modal-head">
          <h3 id="beliefs-title">EON's beliefs</h3>
          <button class="modal-close" id="beliefs-close" aria-label="Close">×</button>
        </div>
        <div class="beliefs-controls">
          <input type="search" id="beliefs-search" class="beliefs-search" placeholder="Search beliefs…" autocomplete="off">
          <div class="beliefs-cats" id="beliefs-cats"></div>
        </div>
        <div class="modal-body beliefs-body" id="beliefs-body">
          <div class="beliefs-loading">Loading…</div>
        </div>
      </div>
    </div>`);
  document.body.appendChild(modal);
  const close = () => modal.remove();
  modal.addEventListener('click', close);
  $('#beliefs-close').addEventListener('click', close);

  let all = [];
  const filt = { cat: '', q: '', etype: '', eid: '', ename: '', retired: false };
  const activeRows = () => all.filter(m => m.is_active !== false);
  const retiredRows = () => all.filter(m => m.is_active === false);

  const apply = () => {
    const q = filt.q.toLowerCase();
    const pool = filt.retired ? retiredRows() : activeRows();
    let rows = pool;
    if (filt.cat) rows = rows.filter(m => (m.category || 'general') === filt.cat);
    if (q) rows = rows.filter(m => (m.content || '').toLowerCase().includes(q));
    if (filt.eid) rows = rows.filter(m => (m.entity_refs || [])
      .some(r => r.type === filt.etype && String(r.id) === filt.eid));
    const body = $('#beliefs-body');
    if (!body) return;
    const note = filt.eid
      ? `<div class="beliefs-entity-note">Showing beliefs about <b>${esc(filt.ename)}</b> · <button type="button" class="btn-ghost" id="beliefs-entity-clear">Clear</button></div>`
      : '';
    body.innerHTML = note + (rows.length
      ? rows.map(_beliefRow).join('')
      : '<div class="beliefs-empty">Nothing here.</div>');
    const title = $('#beliefs-title');
    if (title) title.textContent = `EON's beliefs${filt.retired ? ' · retired' : ''} · ${rows.length}${rows.length !== pool.length ? ` of ${pool.length}` : ''}`;
    $('#beliefs-entity-clear')?.addEventListener('click', () => {
      filt.etype = filt.eid = filt.ename = ''; apply();
    });
  };

  const renderCats = () => {
    const el = $('#beliefs-cats');
    if (!el) return;
    const byCat = {};
    activeRows().forEach(m => {
      const c = m.category || 'general';
      byCat[c] = (byCat[c] || 0) + 1;
    });
    const cats = Object.entries(byCat).sort((a, b) => b[1] - a[1]);
    const nRet = retiredRows().length;
    el.innerHTML = `<button type="button" class="belief-cat-chip${(filt.cat || filt.retired) ? '' : ' active'}" data-cat="">All</button>`
      + cats.map(([c, n]) =>
        `<button type="button" class="belief-cat-chip${(filt.cat === c && !filt.retired) ? ' active' : ''}" data-cat="${esc(c)}">${esc(_BELIEF_CAT_LABELS[c] || c)} · ${n}</button>`
      ).join('')
      + (nRet ? `<button type="button" class="belief-cat-chip belief-cat-chip--retired${filt.retired ? ' active' : ''}" id="beliefs-retired-chip">Retired · ${nRet}</button>` : '');
    el.querySelectorAll('.belief-cat-chip').forEach(b =>
      b.addEventListener('click', () => {
        if (b.id === 'beliefs-retired-chip') {
          filt.retired = !filt.retired;
          filt.cat = '';
        } else {
          filt.cat = b.dataset.cat || '';
          filt.retired = false;
        }
        renderCats(); apply();
      }));
  };

  $('#beliefs-search').addEventListener('input', (e) => {
    filt.q = e.target.value || ''; apply();
  });

  // Entity-chip + Retire clicks — delegated on the PANEL, not the
  // backdrop: the panel's inline stopPropagation (which keeps inner
  // clicks from closing the modal) would swallow them before they
  // reached a modal-level listener.
  modal.querySelector('.beliefs-panel').addEventListener('click', async (e) => {
    const chip = e.target.closest('.belief-chip');
    if (chip) {
      e.stopPropagation();
      filt.etype = chip.dataset.etype; filt.eid = chip.dataset.eid;
      filt.ename = chip.textContent; apply();
      return;
    }
    const btn = e.target.closest('.belief-retire, .belief-restore');
    if (!btn) return;
    e.stopPropagation();
    const id = btn.dataset.id;
    const restoring = btn.classList.contains('belief-restore');
    btn.disabled = true;
    try {
      await api(`/api/memories/${encodeURIComponent(id)}/${restoring ? 'restore' : 'dismiss'}`, { method: 'POST' });
      // Rows move between the active and retired pools instead of
      // vanishing — retire is reversible (mig 487), and the UI says so.
      const m = all.find(x => x.id === id);
      if (m) {
        m.is_active = restoring;
        m.demoted_reason = restoring ? null : 'dismissed_by_owner';
      }
      renderCats(); apply();
    } catch (err) {
      btn.disabled = false;
      showToast(err.body?.detail || err.message || 'Could not update this belief. Try again.');
      console.warn('belief retire/restore failed:', err);
    }
  });

  try {
    const data = await api('/api/memories?include_inactive=1&limit=1000');
    all = data?.memories || [];
    renderCats();
    apply();
  } catch (e) {
    const body = $('#beliefs-body');
    if (body) body.innerHTML = '<div class="beliefs-empty">Could not load beliefs — try again.</div>';
  }
}

// "All beliefs" on the memory-review banner. preventDefault so the
// click doesn't also toggle the <details> the button lives inside.
document.addEventListener('click', (e) => {
  const b = e.target.closest('#memory-review-all');
  if (!b) return;
  e.preventDefault();
  e.stopPropagation();
  showBeliefs();
});

// ──────────────────────────────────────────────────────────
// Bottom nav

function renderNav() {
  // 4-slot nav. Inventory moved to the side menu under Operations
  // alongside Recommendations / Dashboard / Money — the bottom nav
  // is reserved for the daily-action surfaces.
  const items = [
    { id: 'home',      lbl: 'Home',      icon: homeIcon() },
    { id: 'chat',      lbl: 'Chat',      icon: chatIcon() },
    { id: 'ads',       lbl: 'Ads',       icon: adsIcon() },
    { id: 'tasks',     lbl: 'Tasks',     icon: tasksIcon() },
  ];
  return `<nav class="nav">${items.map(i => `
    <button class="nav-item ${State.view === i.id ? 'active' : ''}" data-view="${i.id}">
      ${i.icon}<span class="nav-label">${i.lbl}</span>
    </button>`).join('')}</nav>`;
}

function bindNav() {
  document.querySelectorAll('.nav-item').forEach(el => {
    el.addEventListener('click', () => {
      State.view = el.dataset.view;
      render();
    });
  });
  liftAskEonAboveNav();
}

// The Ask-EON orb is pinned bottom-right at
// `18px + var(--eon-ask-offset) + env(safe-area-inset-bottom)`
// (eon_ask.js). That variable is READ in three places and, until now,
// WRITTEN NOWHERE — so the orb defaulted to 0 and landed square on the
// Tasks tab, eating its label and its taps (operator IMG_9317). Same
// class the CS desk paid for on 2026-08-08: "a fixed max-z pill in a
// scrolling lane EATS TAPS". A variable nothing writes is half-built.
//
// Measured, not assumed: the orb already adds the safe inset itself, so
// the lift it still needs is the nav's height MINUS that inset, minus
// the 18px it already sits up, plus a little air. A probe element reads
// the inset the same way CSS does, because JS cannot evaluate env().
let askNavObserver = null;
let askObservedNavs = [];
// Ask's own launcher is watched too (an observation, never a write): a late
// un-hide after its access check, or a wider label ("3 flagged here"),
// changes its size with no event, and the Chat row must make room for it.
// Hovering it widens it ~2 px (its label's letter-spacing); that is not worth
// moving the message box, so a launcher-only change under 5 px is ignored.
function askEonPill() { return document.getElementById('eon-ask-root')?.shadowRoot?.querySelector('.pill') || null; }
function askEonCompanionControls() {
  const host = document.getElementById('eon-ask-root');
  if (host?.dataset?.botAppearance !== '3d') return [];
  return ['.eon-bot-eon-toggle', '.eon-bot-eon-restore']
    .map(selector => host.shadowRoot?.querySelector(selector)).filter(Boolean);
}
let askEonPillSeen = 0;
function askEonResized(entries) {
  const pill = askEonPill(), width = pill ? pill.offsetWidth : 0;
  if (pill && entries.every(e => e.target === pill) && Math.abs(width - askEonPillSeen) <= 4) return;
  askEonPillSeen = width;
  liftAskEonAboveNav();
}
function watchAskEonNav(navs) {
  navs = navs.concat(askEonCompanionControls());
  if (typeof ResizeObserver !== 'function') return;
  if (!askNavObserver) askNavObserver = new ResizeObserver(askEonResized);
  if (navs.length === askObservedNavs.length && navs.every((nav, i) => nav === askObservedNavs[i])) return;
  // Room renders replace these nodes. Release detached bars, while keeping
  // hidden bars observed so a class-only conversation Back restores the lift.
  askNavObserver.disconnect();
  askObservedNavs = navs;
  navs.forEach(nav => askNavObserver.observe(nav, {box: 'border-box'}));
}
// A bar lifts Ask when it is wider than it is tall. From 1024 px the
// Classic shell turns this same .nav into a full-height LEFT rail (app.css,
// WS-F). Lifting Ask by that rail's height (760-892 px) put the launcher
// above the top of the screen and left an opened panel with nothing visible
// on Home, Chat and Ads (round-5 judge, O5-1). A rail sits beside Ask, not
// under it, so it lifts nothing. Where a bar sits does not matter: with a
// phone keyboard up, main's layout moves the Classic bar up under the
// header, and it still lifts Ask by its height, as on main - so the lift is
// right the moment the keyboard closes (the second review of #3652).
function askEonBottomBar(nav) {
  // CS has a fixed phone bar, but its desktop/Workroom strip sits above content.
  if (nav.classList?.contains('csw-tabbar') && getComputedStyle(nav).position !== 'fixed') return false;
  const r = nav.getBoundingClientRect();
  return r.width > r.height;
}
// On Chat the message row sits at the bottom too, and on main its Send key
// sat under the launcher from a phone up to 1200 px wide, in both designs:
// Ask took 4-25 of 25 taps aimed at Send. A phone has no Enter to send, so
// a covered Send key is the whole fault. The launcher does NOT move: lifting
// it (S2 part 2) put it into the thread, on Jump to latest and a last card's
// Deny (both reviews of #3652). Instead the ROW makes room: when a line of
// the row would meet the resting launcher (8 px of air), the row gets a
// transparent right border (--eon-ask-row-room, app.css) so every line ends
// before the launcher's left edge. A border adds to each design's own
// padding instead of replacing it. Where there is room already, it is 0.
const ASK_EON_ROW_AIR = 8;
// The resting launcher's box, or null when it has no size: Ask not mounted,
// its host hidden (no access, display:none) or not laid out yet. Then there
// is nothing to make room for, and the watch above measures again once it
// has a size. Ask's own box is READ, never written: offsetWidth/offsetHeight
// ignore the .82 scale of .pill.gone (open panel) and the scroll dodge, so
// the room holds still while Ask opens, closes or dodges; the place comes
// from its own inset rules.
function askEonLauncherBox() {
  const pill = askEonPill();
  const root = document.documentElement, boxes = [];
  if (pill?.offsetWidth) {
    const cs = getComputedStyle(pill);
    const right = root.clientWidth - (parseFloat(cs.right) || 0);
    const bottom = root.clientHeight - (parseFloat(cs.bottom) || 0);
    const width = pill.offsetWidth, height = pill.offsetHeight;
    boxes.push({left: right - width, right, top: bottom - height, bottom});
  }
  const host = document.getElementById('eon-ask-root');
  const panelOpen = host?.shadowRoot?.querySelector('.sheet')?.classList.contains('on');
  for (const control of askEonCompanionControls()) {
    const held = control.dataset?.eonRestingHeld === 'true';
    if (host?.hidden || host?.getClientRects?.().length === 0) continue;
    if (!held && (control.hidden || !control.offsetWidth || !control.offsetHeight)) continue;
    const cs = getComputedStyle(control);
    // Dodge and the open panel temporarily hide/move controls. Their HOME
    // footprint still belongs to this mount, so neither can move the composer.
    const transient = pill?.classList?.contains('dodge') || panelOpen;
    if ((!held && cs.display === 'none') || (cs.visibility === 'hidden' && !transient)) continue;
    let left = parseFloat(cs.left), top = parseFloat(cs.top);
    const resting = control.dataset?.eonRestingBox?.split(',').map(Number);
    if (resting?.length === 4 && resting.every(Number.isFinite)) {
      const width = control.offsetWidth || resting[2], height = control.offsetHeight || resting[3];
      left = resting[0] + resting[2] - width;
      top = control.dataset.eonRestingAlign === 'bottom' ? resting[1] + resting[3] - height : resting[1];
      boxes.push({left, top, right: left + width, bottom: top + height});
      continue;
    }
    // Before the native adapter publishes its first home measurement, keep
    // the existing untransformed control box. Foreign controls never enter.
    if (!Number.isFinite(left) || !Number.isFinite(top)) continue;
    boxes.push({left, top, right: left + control.offsetWidth, bottom: top + control.offsetHeight});
  }
  if (!boxes.length) return null;
  return {left: Math.min(...boxes.map(b => b.left)), right: Math.max(...boxes.map(b => b.right)),
    top: Math.min(...boxes.map(b => b.top)), bottom: Math.max(...boxes.map(b => b.bottom))};
}
// The room one row needs, from its NATURAL geometry: its border box minus
// its own padding, never minus the room it already has, so applying the
// room never changes the next answer (no second pass, nothing to swing).
// Each line is centred with a max width (the message box, the hint, the
// approval banner: 760 px) or fills the row (attachments): a room of x
// moves a centred line's right edge by x/2 until it fills, then by x.
// A line of plain words (Chat's status and its live-voice note) fills the
// row as a box but draws only as far as its words. Its words count, from
// its left: the room never moves that left, and wrapped pieces add up to
// the same words, so the answer still holds once the room is applied.
function askEonRowRoomFor(row, launcher) {
  const r = row.getBoundingClientRect(), cs = getComputedStyle(row);
  const left = r.left + (parseFloat(cs.borderLeftWidth) || 0) + (parseFloat(cs.paddingLeft) || 0);
  const width = r.right - left - (parseFloat(cs.paddingRight) || 0);
  const edge = launcher.left - ASK_EON_ROW_AIR;
  let room = 0;
  for (const line of row.children) {
    const box = line.getBoundingClientRect();
    if (!box.width || !box.height) continue;                        // hidden line
    if (box.top >= launcher.bottom + ASK_EON_ROW_AIR || box.bottom <= launcher.top - ASK_EON_ROW_AIR) continue;
    const ls = getComputedStyle(line);
    if (line.childElementCount === 0 && /^(start|left)$/.test(ls.textAlign) && ls.direction !== 'rtl') {
      const words = document.createRange();
      words.selectNodeContents(line);
      const reach = [...words.getClientRects()].reduce((sum, w) => sum + w.width, 0);
      if (box.left + (parseFloat(ls.paddingLeft) || 0) + reach <= edge) continue;   // its words stop short
    }
    // max-width: px, a share of the row ('100%': the Claude chat style on a
    // phone), or 'none'.
    const mw = ls.maxWidth;
    const natural = Math.min(mw.endsWith('%') ? width * parseFloat(mw) / 100 : parseFloat(mw) || Infinity, width);
    const over = left + (width + natural) / 2 - edge;
    if (over <= 0) continue;
    const slack = width - natural;
    room = Math.max(room, 2 * over <= slack ? 2 * over : over + slack / 2);
  }
  return Math.ceil(room);
}
function askEonRowRoom(rows) {
  if (!rows.length) return;                                         // off Chat
  const launcher = askEonLauncherBox();
  for (const row of rows) {
    const room = (launcher ? askEonRowRoomFor(row, launcher) : 0) + 'px';
    // Written only when it changes: a style write restyles the row.
    if ((row.style.getPropertyValue('--eon-ask-row-room') || '0px') !== room) row.style.setProperty('--eon-ask-row-room', room);
  }
}
function liftAskEonAboveNav() {
  // Task Room also calls its vertical sidebar lists .nav; only the direct
  // shell child owns the bottom edge.
  const navs = Array.from(document.querySelectorAll('#app > .nav, .csw-tabbar'));
  const rows = Array.from(document.querySelectorAll('body.view-chat .composer-shell'));
  watchAskEonNav(navs.concat(rows, askEonPill() || []));
  const navHeight = Math.max(0, ...navs.filter(askEonBottomBar).map(nav => nav.getBoundingClientRect().height));
  const root = document.documentElement;
  // The row's room is measured after the bar lift is written, because the
  // launcher's own bottom rule reads that lift.
  if (!navHeight) { root.style.setProperty('--eon-ask-offset', '0px'); askEonRowRoom(rows); return; }
  let safe = 0;
  try {
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:absolute;visibility:hidden;height:env(safe-area-inset-bottom,0px)';
    document.body.appendChild(probe);
    safe = probe.getBoundingClientRect().height || 0;
    probe.remove();
  } catch (_) { safe = 0; }
  const lift = Math.max(0, Math.round(navHeight - safe - 18 + 10));
  root.style.setProperty('--eon-ask-offset', lift + 'px');
  askEonRowRoom(rows);
}
// CS mounts its own bar after the shell render; on desktop it is hidden or
// becomes an in-flow top strip. Re-measure so its old phone lift cannot linger.
window.addEventListener('resize', liftAskEonAboveNav);
// Ask can mount after the shell's first lift (it waits for its access
// check). eon_ask.js announces the mount on the document; measure again
// then, so the Chat row makes room for the launcher's real size.
document.addEventListener('eon-ask-mounted', liftAskEonAboveNav);
// The native adapter announces only a changed placement or visibility. Reuse
// this one observer for late controls, tuck/restore and removed old mounts.
document.addEventListener('eon-ask-companion-layout', liftAskEonAboveNav);

// ──────────────────────────────────────────────────────────
// Auth screen

// HOUSE floor G — the owner door joins the shutter's room. One named
// gate per floor (the cswHouse/deskHouse convention).
const authHouse = () => document.documentElement.getAttribute('data-theme') === 'house';

function renderAuth() {
  const mode = State.authMode;
  if (!livingEnabled()) {
    if (authHouse()) return renderHouseAuth(mode);
  }
  const isForgot = mode === 'forgot';
  const isReset = mode === 'reset';
  const isInviteSignup = mode === 'invite_signup';

  if (isReset) return renderResetView();
  if (isForgot) return renderForgotView();
  if (isInviteSignup) return renderInviteSignupView();

  // v3.0p13 commit 8a: invite-only access. Public signup is disabled at the
  // Supabase level. Users join via invite link only — that path lives in
  // renderInviteSignupView and is reached via /invite-accept?token=X.
  return `
    <div class="auth has-aurora">
      <canvas class="auth-webgl" id="eon-auth-webgl" aria-hidden="true"></canvas>
      <div class="auth-top">
        <div class="auth-mark">EON</div>
        <div class="auth-tag">Business · Operating · System</div>

        <form class="auth-form auth-glass" id="auth-form" novalidate>
          <div id="auth-err" role="alert" aria-atomic="true"></div>
          <div class="field">
            <input type="email" id="auth-email" aria-label="Email" placeholder="Email" autocomplete="email" required />
          </div>
          <div class="field field-with-toggle">
            <input type="password" id="auth-password" aria-label="Password" placeholder="Password" autocomplete="current-password" required />
            <button type="button" class="reveal-toggle" id="auth-reveal" aria-label="Show password">${eyeIcon()}</button>
          </div>
          <div class="auth-aux">
            <button type="button" id="auth-forgot">Forgot password?</button>
          </div>
          <button type="submit" class="auth-btn" id="auth-submit">Sign in</button>
          <div class="auth-toggle" style="font-size: 12px; line-height: 1.5;">
            EON is invite-only. If you've received an invite link, open it to create your account.
          </div>
          <div class="auth-aux" style="justify-content:center; margin-top:14px;">
            <a href="${esc(staffSignInHref())}" style="font-size:13px;">Staff? Sign in with your PIN &rarr;</a>
          </div>
        </form>
      </div>
      <div class="auth-bottom">
        <span>${EON_BUILD}</span>
        <a href="https://instagram.com/unothordox" target="_blank" rel="noopener">Built on Claude by Unothordox (CEO)</a>
      </div>
    </div>
  `;
}

// EON auth aurora — animated WebGL backdrop for the owner sign-in (the
// "2 text fields" the operator wanted redesigned). Ops design pass 04
// (2026-07-11): rides the shared /static/aurora.js module (same shader as
// the staff /login page, palette as uniforms). The module's ladder covers
// what the old inline copy did and more: stops itself when the canvas
// leaves the DOM (post-sign-in view swap), pauses when the tab hides or the
// canvas scrolls offscreen, renders one static frame under reduced-motion,
// and falls back to the CSS gradient (.auth stays styled) without WebGL.
function initAuthAurora() {
  if (livingEnabled()) return;
  const canvas = document.getElementById('eon-auth-webgl');
  if (!canvas) return;
  if (typeof window.mountAurora !== 'function') { canvas.classList.add('auth-webgl-fallback'); return; }
  window.mountAurora(canvas, ['#C8FF4D', '#56E39F', '#7AA6FF', '#B58CFF'], {
    alpha: false,
    fallback: () => canvas.classList.add('auth-webgl-fallback'),
  });
}

function renderInviteSignupView() {
  // v3.0p13 commit 8a: signup-via-invite form. Reached when user lands on
  // /invite-accept?token=X without an existing session. State.inviteToken
  // is populated from the URL during bootstrap.
  // 2026-05-31: email pre-fill from /_/invites/preview + workspace
  // name surface + readonly email so typo-mismatch can't happen.
  const hintedEmail = State.inviteEmailHint || '';
  const tenantName = State.inviteTenantName || '';
  const role = State.inviteRole || '';
  const inviteErr = State.inviteError || '';
  const subtitle = tenantName
    ? `Join <strong>${esc(tenantName)}</strong>${role ? ` as <strong>${esc(role)}</strong>` : ''}`
    : "You've been invited to a workspace. Set up your account below.";
  return `
    <div class="auth">
      <div class="auth-top">
        <div class="auth-mark">EON</div>
        <div class="auth-tag">Create your account</div>

        <form class="auth-form" id="invite-signup-form" novalidate>
          <div id="auth-err" role="alert" aria-atomic="true">${inviteErr ? `<div class="auth-err">${esc(inviteErr)}</div>` : ''}</div>
          <p style="font-size: 13px; color: var(--ink-dim); line-height: 1.55; text-align: center; margin-bottom: 18px; max-width: 320px;">
            ${subtitle}
          </p>
          <div class="field">
            <input type="email" id="invite-email" aria-label="Email" placeholder="Email"
                   autocomplete="email" required
                   value="${esc(hintedEmail)}"
                   ${hintedEmail ? 'readonly style="opacity:0.85; cursor: not-allowed;"' : ''} />
          </div>
          <div class="field field-with-toggle">
            <input type="password" id="invite-password" aria-label="Choose a password (8+ characters)"
                   placeholder="Choose a password (8+ characters)"
                   autocomplete="new-password" required minlength="8" />
            <button type="button" class="reveal-toggle" id="invite-reveal"
                    aria-label="Show password">${eyeIcon()}</button>
          </div>
          <button type="submit" class="auth-btn" id="invite-signup-submit"
                  ${inviteErr ? 'disabled' : ''}>
            Create account & join workspace
          </button>
          <div class="auth-toggle" style="font-size: 12px;">
            Already have an account?
            <button type="button" id="invite-to-signin">Sign in instead</button>
          </div>
        </form>
      </div>
      <div class="auth-bottom">
        <span>${EON_BUILD}</span>
        <a href="https://anthropic.com" target="_blank" rel="noopener">Built on Claude</a>
      </div>
    </div>
  `;
}

function renderForgotView() {
  return `
    <div class="auth">
      <div class="auth-top">
        <div class="auth-mark">EON</div>
        <div class="auth-tag">Reset password</div>
        <form class="auth-form" id="forgot-form" novalidate>
          <div id="auth-err" role="alert" aria-atomic="true"></div>
          <div id="auth-success" role="status" aria-atomic="true"></div>
          <p style="font-size: 13px; color: var(--ink-dim); line-height: 1.55; text-align: center; margin-bottom: 24px; max-width: 320px;">
            Enter your email and we'll send you a link to reset your password.
          </p>
          <div class="field">
            <input type="email" id="forgot-email" aria-label="Email" placeholder="Email" autocomplete="email" required />
          </div>
          <button type="submit" class="auth-btn" id="forgot-submit">Send reset link</button>
          <div class="auth-toggle">
            <button type="button" id="forgot-back">← Back to sign in</button>
          </div>
        </form>
      </div>
      <div class="auth-bottom">
        <span>${EON_BUILD}</span>
        <a href="https://anthropic.com" target="_blank" rel="noopener">Built on Claude</a>
      </div>
    </div>
  `;
}

function renderResetView() {
  return `
    <div class="auth">
      <div class="auth-top">
        <div class="auth-mark">EON</div>
        <div class="auth-tag">New password</div>
        <form class="auth-form" id="reset-form" novalidate>
          <div id="auth-err" role="alert" aria-atomic="true"></div>
          <p style="font-size: 13px; color: var(--ink-dim); line-height: 1.55; text-align: center; margin-bottom: 24px; max-width: 320px;">
            Choose a new password for your EON account.
          </p>
          <div class="field field-with-toggle">
            <input type="password" id="reset-password" aria-label="New password" placeholder="New password" autocomplete="new-password" required minlength="8" />
            <button type="button" class="reveal-toggle" id="reset-reveal" aria-label="Show password">${eyeIcon()}</button>
          </div>
          <div class="field field-with-toggle">
            <input type="password" id="reset-confirm" aria-label="Confirm new password" placeholder="Confirm new password" autocomplete="new-password" required minlength="8" />
          </div>
          <button type="submit" class="auth-btn" id="reset-submit">Update password</button>
        </form>
      </div>
      <div class="auth-bottom">
        <span>${EON_BUILD}</span>
        <a href="https://anthropic.com" target="_blank" rel="noopener">Built on Claude</a>
      </div>
    </div>
  `;
}

// ── HOUSE floor G · the owner door ──────────────────────────────────
// "One generation." Sign-in, forgot, reset and invite stop being four
// pages of different vintage — they render the SAME shop the staff PIN
// door renders (house.css §3), with the shutter already up because
// there is no PIN to lift it. Field ids are unchanged, so bindAuth /
// bindForgot / bindReset / bindInviteSignup wire this markup with no
// house branch of their own — the behaviour is shared code, not a copy.
function renderHouseAuth(mode) {
  const room = {
    forgot: { chip: 'password reset', title: 'Reset password' },
    reset: { chip: 'new password', title: 'Choose a new one' },
    invite_signup: { chip: 'invitation', title: 'Create your account' },
  }[mode] || { chip: 'owner entrance', title: 'Welcome back' };

  let form;
  if (mode === 'forgot') {
    form = `
        <form class="auth-form" id="forgot-form" novalidate>
          <div id="auth-err" role="alert" aria-atomic="true"></div>
          <div id="auth-success" role="status" aria-atomic="true"></div>
          <p class="auth-note">Enter your email and we'll send you a link to reset your password.</p>
          <div class="field">
            <input type="email" id="forgot-email" aria-label="Email" placeholder="Email" autocomplete="email" required />
          </div>
          <button type="submit" class="auth-btn" id="forgot-submit">Send reset link</button>
          <div class="auth-toggle">
            <button type="button" id="forgot-back">← Back to sign in</button>
          </div>
        </form>`;
  } else if (mode === 'reset') {
    form = `
        <form class="auth-form" id="reset-form" novalidate>
          <div id="auth-err" role="alert" aria-atomic="true"></div>
          <p class="auth-note">Choose a new password for your EON account.</p>
          <div class="field field-with-toggle">
            <input type="password" id="reset-password" aria-label="New password" placeholder="New password" autocomplete="new-password" required minlength="8" />
            <button type="button" class="reveal-toggle" id="reset-reveal" aria-label="Show password">${eyeIcon()}</button>
          </div>
          <div class="field field-with-toggle">
            <input type="password" id="reset-confirm" aria-label="Confirm new password" placeholder="Confirm new password" autocomplete="new-password" required minlength="8" />
          </div>
          <button type="submit" class="auth-btn" id="reset-submit">Update password</button>
        </form>`;
  } else if (mode === 'invite_signup') {
    // same truths the classic invite screen renders: the workspace it is
    // an invite TO, and a readonly email when the server hinted one.
    const hintedEmail = State.inviteEmailHint || '';
    const tenantName = State.inviteTenantName || '';
    const role = State.inviteRole || '';
    const inviteErr = State.inviteError || '';
    const subtitle = tenantName
      ? `Join <strong>${esc(tenantName)}</strong>${role ? ` as <strong>${esc(role)}</strong>` : ''}`
      : "You've been invited to a workspace. Set up your account below.";
    form = `
        <form class="auth-form" id="invite-signup-form" novalidate>
          <div id="auth-err" role="alert" aria-atomic="true">${inviteErr ? `<div class="auth-err">${esc(inviteErr)}</div>` : ''}</div>
          <p class="auth-note">${subtitle}</p>
          <div class="field">
            <input type="email" id="invite-email" aria-label="Email" placeholder="Email" autocomplete="email" required
                   value="${esc(hintedEmail)}"
                   ${hintedEmail ? 'readonly style="opacity:0.85; cursor: not-allowed;"' : ''} />
          </div>
          <div class="field field-with-toggle">
            <input type="password" id="invite-password" aria-label="Choose a password (8+ characters)" placeholder="Choose a password (8+ characters)"
                   autocomplete="new-password" required minlength="8" />
            <button type="button" class="reveal-toggle" id="invite-reveal" aria-label="Show password">${eyeIcon()}</button>
          </div>
          <button type="submit" class="auth-btn" id="invite-signup-submit" ${inviteErr ? 'disabled' : ''}>
            Create account &amp; join workspace
          </button>
          <div class="auth-toggle">
            Already have an account?
            <button type="button" id="invite-to-signin">Sign in instead</button>
          </div>
        </form>`;
  } else {
    form = `
        <form class="auth-form" id="auth-form" novalidate>
          <div id="auth-err" role="alert" aria-atomic="true"></div>
          <div class="field">
            <input type="email" id="auth-email" aria-label="Email" placeholder="Email" autocomplete="email" required />
          </div>
          <div class="field field-with-toggle">
            <input type="password" id="auth-password" aria-label="Password" placeholder="Password" autocomplete="current-password" required />
            <button type="button" class="reveal-toggle" id="auth-reveal" aria-label="Show password">${eyeIcon()}</button>
          </div>
          <div class="auth-aux">
            <button type="button" id="auth-forgot">Forgot password?</button>
          </div>
          <button type="submit" class="auth-btn" id="auth-submit">Sign in</button>
          <div class="auth-toggle">
            EON is invite-only. If you've received an invite link, open it to create your account.
          </div>
        </form>`;
  }

  return `
    <div class="auth hs-auth">
      <div class="hs-shop hs-open">
        <div class="hs-fascia">
          <span class="hs-sign hs-fascia-nm">CRU<i>X</i> · EON</span>
          <span class="hs-fascia-chip">${room.chip}</span>
        </div>
        <div class="hs-window">
          <div class="hs-scene">
            <span class="hs-bars" aria-hidden="true"><i></i><i></i><i></i></span>
            <span class="hs-welcome">${room.title}</span>
          </div>
          <div class="hs-slats" aria-hidden="true" style="--hs-lift:100%"></div>
          <div class="hs-coil" aria-hidden="true"></div>
        </div>
        <div class="hs-pad">${form}</div>
        <div class="hs-foot">
          <a class="hs-foot-owner" href="${esc(staffSignInHref())}">staff? pin sign-in &rarr;</a>
          <span class="hs-foot-build">${EON_BUILD}</span>
        </div>
      </div>
    </div>
  `;
}

function bindAuth() {
  const mode = State.authMode;
  if (mode === 'forgot') return bindForgot();
  if (mode === 'reset') return bindReset();
  if (mode === 'invite_signup') return bindInviteSignup();

  // Sign-in only. Signup removed in v3.0p13 commit 8a (invite-only).
  // HOUSE floor G — the aurora retires under House (one room, one moving
  // part). Gated at the CALL SITE so initAuthAurora itself is untouched.
  if (!authHouse()) { try { initAuthAurora(); } catch (_) { /* aurora is decorative — never block sign-in */ } }
  const form = $('#auth-form');
  if (!form) return;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = $('#auth-email').value.trim();
    const password = $('#auth-password').value;
    if (!email || !password) {
      showAuthErr('Email and password required.');
      return;
    }
    const btn = $('#auth-submit');
    btn.disabled = true;
    btn.textContent = 'Signing in…';
    try {
      await signIn(email, password);
    } catch (e) {
      showAuthErr(e.message);
      btn.disabled = false;
      btn.textContent = 'Sign in';
    }
  });
  $('#auth-forgot')?.addEventListener('click', () => {
    State.authMode = 'forgot';
    render();
    setTimeout(() => $('#forgot-email')?.focus(), 50);
  });
  bindReveal('auth-reveal', 'auth-password');
}

function bindInviteSignup() {
  // v3.0p13 commit 8a: signup-via-invite flow.
  const form = $('#invite-signup-form');
  if (!form) return;
  
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = $('#invite-email').value.trim().toLowerCase();
    const password = $('#invite-password').value;
    const token = State.inviteToken;
    
    if (!email || !password) {
      showAuthErr('Email and password required.');
      return;
    }
    if (password.length < 8) {
      showAuthErr('Password must be at least 8 characters.');
      return;
    }
    if (!token) {
      showAuthErr('Invite token missing. Open your invite link again.');
      return;
    }
    
    const btn = $('#invite-signup-submit');
    btn.disabled = true;
    btn.textContent = 'Creating account…';
    
    try {
      // Step 1: create the auth user via our signup-with-invite endpoint
      // (which uses Supabase admin API, bypassing the disabled public signup).
      const res = await fetch('/api/tenants/_/invites/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email, password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Signup failed (${res.status})`);
      }
      
      // Step 2: sign the user in normally to get a session.
      // 2026-05-31: retry once with a 600ms backoff — Supabase has
      // sub-second eventual consistency between admin.create_user
      // and the password-grant token endpoint, and a first-call
      // "Invalid login credentials" was occasionally bouncing
      // legitimate new users back to the auth screen.
      State.inviteToken = null;  // consumed
      sessionStorage.removeItem('eon_pending_invite_token');
      try {
        await signIn(email, password);
      } catch (firstErr) {
        await new Promise(r => setTimeout(r, 600));
        try {
          await signIn(email, password);
        } catch (retryErr) {
          throw new Error(
            `Account created but sign-in didn't take. Try signing in `
            + `directly with your email and the password you just set. `
            + `(${retryErr.message || retryErr})`
          );
        }
      }
    } catch (e) {
      showAuthErr(e.message || 'Signup failed.');
      btn.disabled = false;
      btn.textContent = 'Create account & join workspace';
    }
  });
  
  $('#invite-to-signin')?.addEventListener('click', () => {
    // User says they have an account already. Switch to signin; the pending
    // invite token stays in sessionStorage and gets accepted post-signin in
    // the bootstrap flow.
    State.authMode = 'signin';
    render();
    setTimeout(() => $('#auth-email')?.focus(), 50);
  });
  
  bindReveal('invite-reveal', 'invite-password');
}

function bindForgot() {
  $('#forgot-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = $('#forgot-email').value.trim();
    if (!email) {
      showAuthErr('Email required.');
      return;
    }
    const btn = $('#forgot-submit');
    btn.disabled = true;
    btn.textContent = 'Sending…';
    $('#auth-err').innerHTML = '';
    try {
      await requestPasswordReset(email);
      $('#auth-success').innerHTML = `
        <div style="background: var(--ok-soft); border: 1px solid rgba(77,214,160,0.25); color: var(--ok); padding: 14px 16px; border-radius: var(--r-sm); font-size: 13px; margin-bottom: 8px; line-height: 1.5;">
          ✓ If an account exists for <strong>${esc(email)}</strong>, a reset link is on its way. Check your inbox (and spam folder) in the next minute or two.
        </div>`;
      // Hide the form so the success state stands alone
      $('#forgot-email').value = '';
      btn.style.display = 'none';
    } catch (e) {
      showAuthErr(e.message);
      btn.disabled = false;
      btn.textContent = 'Send reset link';
    }
  });
  $('#forgot-back')?.addEventListener('click', () => {
    State.authMode = 'signin';
    render();
    setTimeout(() => $('#auth-email')?.focus(), 50);
  });
}

function bindReset() {
  $('#reset-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const password = $('#reset-password').value;
    const confirm = $('#reset-confirm').value;
    if (!password || !confirm) {
      showAuthErr('Both fields required.');
      return;
    }
    if (password.length < 8) {
      showAuthErr('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      showAuthErr('Passwords don\'t match.');
      return;
    }
    if (!State.resetToken) {
      showAuthErr('Reset link expired or invalid. Request a new one.');
      return;
    }
    const btn = $('#reset-submit');
    btn.disabled = true;
    btn.textContent = 'Updating…';
    try {
      await setNewPassword(password, State.resetToken);
      State.resetToken = null;
      // bootstrap() runs inside setNewPassword on success; user lands on home/workspace
    } catch (e) {
      showAuthErr(e.message);
      btn.disabled = false;
      btn.textContent = 'Update password';
    }
  });
  bindReveal('reset-reveal', 'reset-password');
}

// Reusable password reveal toggle.
function bindReveal(btnId, inputId) {
  const btn = $(`#${btnId}`);
  const input = $(`#${inputId}`);
  if (!btn || !input) return;
  btn.setAttribute('aria-controls', input.id);
  btn.addEventListener('click', () => {
    const showing = input.type === 'text';
    input.type = showing ? 'password' : 'text';
    btn.innerHTML = showing ? eyeIcon() : eyeOffIcon();
    btn.setAttribute('aria-label', showing ? 'Show password' : 'Hide password');
  });
}

function showAuthErr(msg) {
  const el = $('#auth-err');
  if (el) el.innerHTML = `<div class="auth-error">${esc(msg)}</div>`;
}

// ──────────────────────────────────────────────────────────
// Create-workspace screen

function renderCreateWorkspace() {
  // Signed in, but in no workspace. Offer the form only to someone the
  // server will let open one (State.canCreateWorkspace, from
  // /api/tenants/me). Everyone else is told plainly who opens workspaces,
  // with a way to sign out — never a form that ends in a refusal.
  if (!State.canCreateWorkspace) {
    return `
    <div class="ws-create">
      <h1>You are signed in, but not in a <em>workspace</em> yet.</h1>
      <p class="lede">New workspaces are opened by EON's team. Ask the person who invited you to send you a new invite link.</p>
      <button type="button" class="auth-btn" id="ws-signout">Sign out</button>
    </div>
  `;
  }
  return `
    <div class="ws-create">
      <h1>Create your <em>workspace</em>.</h1>
      <p class="lede">This becomes your AI-powered business OS. You can invite teammates after.</p>
      <form class="ws-form" id="ws-form">
        <div id="cw-err"></div>
        <div class="field"><input type="text" id="ws-name" placeholder="Workspace name (e.g. CRUX)" required /></div>
        <div class="field"><input type="text" id="ws-slug" placeholder="URL slug (e.g. crux)" required /></div>
        <div class="field"><input type="text" id="ws-currency" value="NGN" placeholder="Currency" /></div>
        <div class="field"><input type="text" id="ws-tz" value="Africa/Lagos" placeholder="Timezone" /></div>
        <button type="submit" class="auth-btn" id="ws-submit">Create workspace</button>
      </form>
    </div>
  `;
}

function bindCreateWorkspace() {
  $('#ws-signout')?.addEventListener('click', (e) => {
    e.preventDefault();
    logout();
  });
  $('#ws-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = $('#ws-name').value.trim();
    const slug = $('#ws-slug').value.trim().toLowerCase();
    const currency = $('#ws-currency').value.trim().toUpperCase() || 'USD';
    const timezone = $('#ws-tz').value.trim() || 'UTC';
    if (!name || !slug) {
      $('#cw-err').innerHTML = `<div class="auth-error">Name and slug required.</div>`;
      return;
    }
    const btn = $('#ws-submit');
    btn.disabled = true;
    btn.textContent = 'Creating…';
    try {
      await api('/api/tenants', {
        method: 'POST',
        body: JSON.stringify({ name, slug, currency, timezone }),
      });
      await bootstrap();
    } catch (e) {
      $('#cw-err').innerHTML = `<div class="auth-error">${esc(e.message)}</div>`;
      btn.disabled = false;
      btn.textContent = 'Create workspace';
    }
  });
}

// ──────────────────────────────────────────────────────────
// SVG icons

function themeIcon() {
  return document.documentElement.getAttribute('data-theme') === 'dark'
    ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" stroke-linecap="round"/></svg>`
    : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke-linejoin="round"/></svg>`;
}
function bellIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" stroke-linecap="round" stroke-linejoin="round"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" stroke-linecap="round"/></svg>`; }
function refreshIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>`; }
function menuIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4 7h16M4 12h16M4 17h16"/></svg>`; }
function signoutIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
// ── EON mark ─────────────────────────────────────────────────────────
// opts.spin makes it ALIVE for the thinking state — the bars extend from
// the left one after the other, like moving steps (eonSteps in app.css;
// operator-directed motion, 2026-07-06). Nothing rotates.
function eonLogo(size = 24, opts = {}) {
  // EON-MARK (2026-07-06, operator-picked concept 04 "Data-bar E"):
  // three data bars form the E, middle bar carries the accent. Outer
  // bars ride currentColor so the mark adapts to any surface; the
  // legacy class names stay so the spin/pulse CSS keeps working.
  const cls = `eon-logo${opts.spin ? ' eon-logo-spin' : ''}${opts.cls ? ' ' + opts.cls : ''}`;
  return `<svg class="${cls}" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect class="eon-logo-orbit" x="3" y="4.6" width="18" height="3.6" rx="1.8" fill="currentColor"/>
    <rect class="eon-logo-spark" x="3" y="10.2" width="13" height="3.6" rx="1.8" fill="var(--accent, currentColor)"/>
    <rect class="eon-logo-orbit" x="3" y="15.8" width="18" height="3.6" rx="1.8" fill="currentColor"/>
  </svg>`;
}
function homeIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 12L12 3l9 9M5 10v11h14V10" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
function chatIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M21 12a8 8 0 0 1-11.5 7.2L3 21l1.8-6.5A8 8 0 1 1 21 12z" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
function tasksIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
function moneyIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
function adsIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 11l18-8v18l-18-8v-2zM7 11v6" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
// v3.0p18 9gg — stack-of-boxes glyph for the Inventory tab
function inventoryIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 7l9-4 9 4-9 4-9-4zM3 7v10l9 4 9-4V7M12 11v10" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
function recsIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v2M4.93 4.93l1.41 1.41M2 12h2M19.07 4.93l-1.41 1.41M22 12h-2"/><path d="M9 18h6M10 21h4M12 6a5 5 0 0 0-3 9c0 1 1 2 1 3h4c0-1 1-2 1-3a5 5 0 0 0-3-9z"/></svg>`; }
function dashboardIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></svg>`; }
function sparkleIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2zM19 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1zM5 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1z"/></svg>`; }
function tagIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41L13 21l-9-9V4h8l8.59 8.59a2 2 0 0 1 0 2.82z"/><circle cx="7.5" cy="7.5" r="1.2"/></svg>`; }
function auditIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>`; }
function settingsIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
function sendIcon() { return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
function eyeIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`; }
function eyeOffIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`; }
function pinIcon() { return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 17v5M5 12l7-7 7 7-3 3-8-8-3 3z" transform="rotate(-45 12 12)"/></svg>`; }
function micIcon() { return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3"/></svg>`; }
function attachIcon() { return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>`; }
function historyIcon() { return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h13M3 12h13M3 18h8"/><circle cx="20" cy="18" r="2.4"/></svg>`; }
function teamIcon() { return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="8" r="3.2"/><path d="M3 20c0-3.2 2.7-5 6-5s6 1.8 6 5"/><circle cx="17.5" cy="9.5" r="2.4"/><path d="M16 15.2c2.9.2 5 1.8 5 4.8"/></svg>`; }
function orchestraIcon() { return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="12" r="5.5"/><circle cx="15" cy="12" r="5.5"/><path d="M12 7.2v9.6"/></svg>`; }
function staffIcon() { return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="7.5" r="3.4"/><path d="M5 21c0-3.8 3.1-6 7-6s7 2.2 7 6"/></svg>`; }
function reportIcon() { return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 5h16v12H4zM8 21h8M12 17v4M8 12l3-3 2 2 3-4"/></svg>`; }
function studioIcon() { return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9.5"/><circle cx="12" cy="12" r="3.2"/><path d="M12 2.5v6M21.5 12h-6M12 21.5v-6M2.5 12h6"/></svg>`; }
function designIcon() { return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19l7-7a4.95 4.95 0 0 0-7-7l-7 7v7h7z"/><path d="M18 13L11 6"/></svg>`; }
function academyIcon() { return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10L12 5 2 10l10 5 10-5zM6 12.5V17c3.5 2.6 8.5 2.6 12 0v-4.5M22 10v5"/></svg>`; }
function plusIcon() { return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`; }
function folderIcon() { return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4.2l1.8 2.2h9A1.5 1.5 0 0 1 21 9.7v8.8a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5z"/></svg>`; }
function deepIcon() { return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>`; }
function trashIcon() { return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`; }
function closeIcon() { return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`; }
function speakerIcon() { return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5zM15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>`; }
// bellIcon lives in the SVG-icons block above (~22217) — the sound-
// notifications pass (#1148) redeclared it here, which is legal in a
// classic script (node --check passed) but a FATAL SyntaxError in an ES
// module, and index.html loads app.js as type="module". The whole SPA
// failed to parse — home page dead on p312/p313 until this hotfix.
// tests/test_module_parse_2026_07_12.py now guards the class.

// ──────────────────────────────────────────────────────────
// Utilities

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}
function shorten(s, n) {
  s = String(s ?? '').replace(/\s+/g, ' ').trim();
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
function renderEmptyInline(msg) {
  return `<div class="empty"><div class="empty-mark">○</div><div class="empty-sub">${esc(msg)}</div></div>`;
}

// ──────────────────────────────────────────────────────────
// Telegram bindings — Daniel's "easier UI to add Telegram/WhatsApp
// number for updates" ask. Backed by:
//   GET    /api/team/setup/telegram-bindings
//   POST   /api/team/setup/telegram-bindings           {chat_id, audience, label, is_primary}
//   PATCH  /api/team/setup/telegram-bindings/{id}      {audience?, label?, is_primary?}
//   DELETE /api/team/setup/telegram-bindings/{id}
//
// Audience values: 'operator' | 'team' | 'all' (default 'all'). Routes
// the 6am operator brief, 9am team brief, and mid-day alerts.

async function refreshTelegramBindings() {
  const host = $('#telegram-bindings-list');
  if (!host) return;
  try {
    const data = await api('/api/team/setup/telegram-bindings');
    const rows = (data && data.bindings) || [];
    if (!rows.length) {
      host.innerHTML = `
        <div class="row" style="opacity: 0.5;">
          <div class="row-content">
            <div class="row-label">No Telegram chats bound yet.</div>
            <div class="row-value" style="font-size: 11px;">
              Add the EON Telegram bot to a chat, then click "Add" below.
            </div>
          </div>
        </div>`;
      return;
    }
    host.innerHTML = rows.map(b => {
      const audienceBadge = {
        operator: '👤 Operator',
        team:     '👥 Team',
        all:      '· All',
      }[b.audience] || b.audience || '· All';
      const labelEsc = esc(b.label || '');
      const cidEsc = esc(b.chat_id || '');
      const idEsc = esc(b.id);
      const primary = b.is_primary ? ' · Primary' : '';
      return `
        <div class="row" data-tg-binding="${idEsc}"
             style="cursor: pointer; padding: 10px 12px;">
          <div class="row-content" style="flex: 1; min-width: 0;">
            <div class="row-label" style="font-weight: 500;">
              ${labelEsc || '<span style="opacity: 0.55;">(no label)</span>'}
            </div>
            <div class="row-value" style="font-size: 11.5px; opacity: 0.7;
                                            font-family: var(--mono);">
              ${cidEsc} · ${audienceBadge}${primary}
            </div>
          </div>
          <button class="icon-btn-sm" data-tg-edit="${idEsc}"
                  aria-label="Edit"
                  style="opacity: 0.5;">✏️</button>
          <button class="icon-btn-sm" data-tg-delete="${idEsc}"
                  aria-label="Delete"
                  style="opacity: 0.5; margin-left: 4px;">🗑</button>
        </div>`;
    }).join('');

    // Wire edit + delete.
    host.querySelectorAll('[data-tg-edit]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = btn.dataset.tgEdit;
        const binding = rows.find(r => r.id === id);
        if (binding) openTelegramBindingSheet(binding);
      });
    });
    host.querySelectorAll('[data-tg-delete]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const id = btn.dataset.tgDelete;
        const binding = rows.find(r => r.id === id);
        const label = binding?.label || binding?.chat_id || 'this chat';
        if (!confirm(`Remove "${label}" from Telegram briefings?`)) return;
        try {
          await api(`/api/team/setup/telegram-bindings/${id}`, {
            method: 'DELETE',
          });
          refreshTelegramBindings();
        } catch (err) {
          alert('Delete failed: ' + (err.message || err));
        }
      });
    });
  } catch (e) {
    host.innerHTML = `
      <div class="row" style="opacity: 0.6;">
        <div class="row-content">
          <div class="row-label">Couldn't load bindings.</div>
          <div class="row-value" style="font-size: 11px;">${esc(e.message || '')}</div>
        </div>
      </div>`;
  }
}

function openTelegramBindingSheet(existing = null) {
  // Lightweight modal — match the invite-sheet pattern.
  const isEdit = !!existing;
  const sheet = document.createElement('div');
  sheet.id = 'telegram-binding-sheet';
  sheet.innerHTML = `
    <div class="sheet-backdrop" id="tg-sheet-backdrop"></div>
    <div class="sheet" role="dialog" aria-modal="true">
      <div class="sheet-header">
        <div class="sheet-title">
          ${isEdit ? 'Edit Telegram chat' : 'Add Telegram chat'}
        </div>
        <button class="icon-btn-sm" id="tg-sheet-close" aria-label="Close">×</button>
      </div>

      <div class="sheet-body" style="display: flex; flex-direction: column; gap: 14px;">
        <label style="display: flex; flex-direction: column; gap: 4px;">
          <span style="font-size: 11px; text-transform: uppercase;
                       letter-spacing: 0.06em; opacity: 0.65;">
            Chat ID
          </span>
          <input id="tg-chat-id" type="text" inputmode="numeric"
                 placeholder="-1001234567890"
                 value="${existing ? esc(existing.chat_id || '') : ''}"
                 ${isEdit ? 'disabled' : ''}
                 style="padding: 9px 11px; border-radius: 8px;
                        border: 1px solid var(--line);
                        background: var(--bg-card); color: var(--ink);
                        font-family: var(--mono); font-size: 14px;${
                          isEdit ? ' opacity: 0.6;' : ''}">
          <span style="font-size: 11px; opacity: 0.55;">
            DM the EON bot from this chat first — the webhook handler
            logs the chat_id; paste it here.
          </span>
        </label>

        <label style="display: flex; flex-direction: column; gap: 4px;">
          <span style="font-size: 11px; text-transform: uppercase;
                       letter-spacing: 0.06em; opacity: 0.65;">
            Label (optional)
          </span>
          <input id="tg-label" type="text"
                 placeholder="Owner DM, Lagos team, Abuja manager…"
                 value="${existing ? esc(existing.label || '') : ''}"
                 style="padding: 9px 11px; border-radius: 8px;
                        border: 1px solid var(--line);
                        background: var(--bg-card); color: var(--ink);
                        font-size: 14px;">
        </label>

        <fieldset style="border: none; padding: 0; margin: 0;
                          display: flex; flex-direction: column; gap: 6px;">
          <legend style="font-size: 11px; text-transform: uppercase;
                          letter-spacing: 0.06em; opacity: 0.65;">
            Audience
          </legend>
          ${['operator','team','all'].map(a => {
            const sel = (existing?.audience || 'all') === a;
            const desc = {
              operator: 'Receives the 6am operator brief + cash + critical alerts',
              team:     'Receives the 9am team brief — no cash, plain language',
              all:      'Receives every stream (default for backwards compat)',
            }[a];
            return `
              <label style="display: flex; align-items: flex-start; gap: 8px;
                             padding: 8px 10px; border-radius: 8px;
                             cursor: pointer;
                             ${sel ? 'background: var(--bg-card); box-shadow: inset 0 0 0 1px var(--accent);' : ''}">
                <input type="radio" name="tg-audience" value="${a}"
                       ${sel ? 'checked' : ''}
                       style="margin-top: 2px;">
                <div style="flex: 1; min-width: 0;">
                  <div style="font-size: 13px; font-weight: 500;
                              text-transform: capitalize;">${a}</div>
                  <div style="font-size: 11.5px; opacity: 0.65;">${esc(desc)}</div>
                </div>
              </label>`;
          }).join('')}
        </fieldset>

        <label style="display: flex; align-items: center; gap: 8px;
                       padding: 8px 0;">
          <input id="tg-is-primary" type="checkbox"
                 ${existing?.is_primary ? 'checked' : ''}>
          <span style="font-size: 13px;">Mark as primary</span>
          <span style="font-size: 11px; opacity: 0.55;">
            (fallback target when no audience-matching binding exists)
          </span>
        </label>

        <div id="tg-sheet-err" style="display: none; font-size: 12px;
                                         color: var(--danger, #ef4444);
                                         padding: 6px 8px;
                                         background: rgba(239, 68, 68, 0.08);
                                         border-radius: 6px;"></div>

        <button id="tg-sheet-save" class="btn-primary" style="margin-top: 4px;">
          ${isEdit ? 'Save changes' : 'Add chat'}
        </button>
      </div>
    </div>`;
  document.body.appendChild(sheet);

  const close = () => sheet.remove();
  $('#tg-sheet-backdrop').addEventListener('click', close);
  $('#tg-sheet-close').addEventListener('click', close);

  $('#tg-sheet-save').addEventListener('click', async () => {
    const chatId = ($('#tg-chat-id').value || '').trim();
    const label  = ($('#tg-label').value || '').trim();
    const audience = sheet.querySelector('input[name="tg-audience"]:checked')?.value || 'all';
    const isPrimary = $('#tg-is-primary').checked;
    const errEl = $('#tg-sheet-err');
    errEl.style.display = 'none';
    if (!chatId && !isEdit) {
      errEl.textContent = 'Chat ID is required.';
      errEl.style.display = 'block';
      return;
    }
    try {
      if (isEdit) {
        await api(`/api/team/setup/telegram-bindings/${existing.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ audience, label, is_primary: isPrimary }),
        });
      } else {
        await api('/api/team/setup/telegram-bindings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chat_id: chatId, audience, label, is_primary: isPrimary,
          }),
        });
      }
      close();
      refreshTelegramBindings();
    } catch (e) {
      errEl.textContent = e.message || 'Save failed.';
      errEl.style.display = 'block';
    }
  });
}


// ──────────────────────────────────────────────────────────
// Boot

(async function init() {
  // Finish evaluating the whole file before the first render (2026-09-10).
  // The signed-in path always yielded first (await bootstrap()), but the
  // signed-out path rendered SYNCHRONOUSLY while this script was still
  // mid-evaluation — so any top-level let/const declared below this line
  // (75 of them, e.g. _workWorkspace, sosState) was still in its temporal
  // dead zone, and render threw "Cannot access '_workWorkspace' before
  // initialization". Everyone signed out got a blank screen and no way to
  // sign in. One microtask puts both paths on the same footing.
  await Promise.resolve();
  // Check for auth callback in URL hash
  // Supabase sends:
  //   <app>/#access_token=...&type=recovery&...   (password reset)
  //   <app>/#access_token=...&refresh_token=...   (OAuth — Google sign-in)
  const hash = window.location.hash.slice(1);
  if (hash.includes('type=recovery') || hash.includes('access_token=')) {
    const params = new URLSearchParams(hash);
    const type = params.get('type');
    const token = params.get('access_token');
    if (type === 'recovery' && token) {
      // Clear the URL so the token doesn't sit in browser history
      history.replaceState(null, '', window.location.pathname);
      State.resetToken = token;
      State.authMode = 'reset';
      State.view = 'auth';
      render();
      return;
    }
    // OAuth callback — has access_token but no type=recovery → sign user in
    if (token && !type) {
      const rt = params.get('refresh_token');
      history.replaceState(null, '', window.location.pathname);
      State.jwt = token;
      localStorage.setItem('eon_jwt', token);
      forgetStaffIntent();
      if (rt) localStorage.setItem('eon_rt', rt);
      try {
        await bootstrap();
        return;
      } catch (e) {
        // Fall through to auth screen if bootstrap fails
        State.jwt = null;
        localStorage.removeItem('eon_jwt');
        State.view = 'auth';
        State.authMode = 'signin';
        render();
        showAuthErr('Sign-in failed: ' + (e.message || 'unknown error'));
        return;
      }
    }
  }

  // ── Staff PIN session precedence ──
  // Resolve which identity boots this device:
  //   1. ?owner=1  → owner escape hatch: drop any staff/impersonation token.
  //   2. impersonation token → owner is "viewing as" a staff (takes precedence
  //      over the owner's own eon_jwt so the swap actually takes effect).
  //   3. owner eon_jwt → normal owner session (always wins over a plain staff
  //      token, so the owner's device is never hijacked by a stray token).
  //   4. staff PIN token → staff-mode boot.
  // Staff-entry intent (P3239): a PIN sign-in in THIS tab marks the tab
  // (sessionStorage is per tab), and the tab then boots as that staff member
  // even with an owner eon_jwt present. The owner token is never written or
  // removed here, and the owner's own tabs (no mark) still boot as the owner.
  try {
    const _qs = new URLSearchParams(location.search);
    if (_qs.get('owner') === '1') {
      localStorage.removeItem('eon_staff_token');
      cswDraftEnd();
      localStorage.removeItem('eon_impersonation_token');
    }
    const _imp = localStorage.getItem('eon_impersonation_token');
    const _staffTok = localStorage.getItem('eon_staff_token');
    // A PIN sign-in in this tab wins over the owner's view-as running elsewhere.
    const _staffTab = !!_staffTok && sessionStorage.getItem('eon_staff_intent') === '1';
    if (_imp && !_staffTab) {
      State.jwt = _imp; State.isStaff = true; State.impersonating = true;
    } else if (_staffTok && (!State.jwt || sessionStorage.getItem('eon_staff_intent') === '1')) {
      State.staffOverOwner = !!State.jwt;
      State.jwt = _staffTok; State.isStaff = true;
    } else {
      // Not a staff tab now: a mark left from an ended staff session must not
      // turn this tab into whichever staff member signs in next.
      forgetStaffIntent();
    }
  } catch (_) { /* private-mode storage — fall through to owner/auth path */ }

  if (State.jwt) {
    await bootstrap();
  } else {
    State.view = 'auth';
    render();
  }
})();


// ═══════════════════════════════════════════════════════════════════
// Task OS (tos-*) — Asana-grade work management, fused with People OS.
// Closes the loop: assigned work → execute → manager verify+rate → points.
// Consumes /api/tasks/board + /pulse (one model, client-side views).
// ═══════════════════════════════════════════════════════════════════
let _tosState = { board: null, pulse: null, mine: null, view: null, tasks: [], byId: {} };
// 'Ideas' added 2026-07-30 — operator: "cant see ideas... this section
// should be added to task". Ideas lived on two other surfaces (System
// Audit for code, the Decisions board for business) and he looks here.
// This tab does NOT move them: it reads both, shows what is waiting on a
// decision in plain words, and links out to decide. Tasks stays the list
// of things to DO; deciding still happens where the evidence lives.
const TOS_VIEWS = [['mywork', 'My Work'], ['board', 'Board'], ['list', 'List'],
                   ['dept', 'Department'], ['ideas', 'Ideas'],
                   ['recurring', 'Recurring']];
// HOUSE floors 6 + 2 (WS-J/WS-R, 2026-08-07): one predicate for the two
// working floors that live in this file region — the Job Board and the
// Roster. Same shape as cswHouse()/deskHouse(); declared here because
// neither of those lanes owns tasks/people.
const tosHouse = () => document.documentElement.getAttribute('data-theme') === 'house';

const TOS_COL = [['todo', 'To do', ['open', 'assigned', 'acknowledged']],
                 ['doing', 'In progress', ['in_progress']],
                 ['blocked', 'Blocked', ['blocked']],
                 ['review', 'To verify', ['completed', 'done']],
                 ['verified', 'Verified', ['verified']]];

function renderTaskOS() {
  if (tosHouse()) return _trShell();
  return `<div class="tos-shell" id="tos">
    <svg width="0" height="0" style="position:absolute"><defs>
      <linearGradient id="cswGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#4796E3"/><stop offset="50%" stop-color="#9177C7"/><stop offset="100%" stop-color="#CA6673"/></linearGradient></defs></svg>
    <div class="tos-topbar">
      <button class="tos-back" id="tos-back" aria-label="Home">‹</button>
      <div class="tos-grow"><div class="tos-title">tasks.</div></div>
      <button class="tos-iconbtn" id="tos-sound" title="Sounds"></button>
      <button class="tos-iconbtn" id="tos-add" title="Quick add">＋</button>
      <button class="tos-iconbtn" id="tos-refresh" title="Refresh">↻</button>
    </div>
    <div id="tos-pulse"></div>
    <div class="tos-body" id="tos-body">${_tosSkel()}</div>
  </div>`;
}
function _tosSkel() { return `<div class="tos-card csw-skel" style="height:64px;margin-bottom:8px"></div>`.repeat(5); }

async function bindTaskOS() {
  _tosState.view = _tosState.view || localStorage.getItem('tos.view') || 'mywork';
  // CEO command center (2026-07-06): leads land on Command; staff keep the
  // execution board untouched. Mode persists per device.
  const ccLead = typeof posIsLead === 'function' && posIsLead();
  // Default seat (WS-A): only the OWNER lands in the CEO Command frame.
  // Every other lead's daily job is the team — they land on Team board
  // (a stored preference still wins). Non-leads stay on their own board.
  const ownerSeat = ((State.member && (State.member.base_role || State.member.role)) || '') === 'owner';
  _tosState.mode = ccLead ? (localStorage.getItem('tos.mode') || (ownerSeat ? 'command' : 'staff')) : 'staff';
  document.getElementById('tos-back')?.addEventListener('click', () => { State.view = tosHouse() ? _trHome() : roleHome(); render(); });
  bindSoundToggle('tos-sound');
  document.getElementById('tos-refresh')?.addEventListener('click', () =>
    (_trActive() && _tosState.view==='email' ? _mailLoad(true)
     : _trActive() && _tosState.mode==='staff' && _tosState.view==='pages' ? (_tr.pages=null,_trRenderView())
     : _trActive() && _tosState.mode==='staff' && _tosState.view==='recurring' ? (_tosState.recurring=null,_trRenderView())
     : _trActive() && _tosState.mode==='staff' && _tosState.view==='ideas' ? (_tosState.ideas=null,_trRenderView())
     : _tosState.mode === 'command' ? _ccLoad(true)
     : _tosState.mode === 'brief' ? _briefLoad(true)
     : _tosState.mode === 'ideas' ? (_tosState.ideas = null, _ideasModeLoad())
     : _tosState.mode === 'mail' ? _mailLoad(true)
     : _tosLoad()));
  document.getElementById('tos-add')?.addEventListener('click', () => openQuickAdd());
  if (tosHouse()) { _tosState.view = 'board'; _tosState.mode = 'staff'; }
  if (ccLead) _ccMountSwitch();
  if (_trActive()) { _tr.pages = null; _trBind(); }
  if (_tosState.mode === 'command') await _ccLoad();
  else if (_tosState.mode === 'brief') await _briefLoad();
  else if (_tosState.mode === 'ideas') await _ideasModeLoad();
  else if (_tosState.mode === 'mail') await _mailLoad(true);
  else await _tosLoad();
}

// Task Room — a connected living workroom, backed by Task OS adapters.
// No demo rows, invented learning, timers that award points, or duplicate writes.
let _tr = { limits: {}, search: '', store: '', focus: false, metric: '', scope: 'all', assignee: '', due: '', view: 'board', pages: null, events: [], readAt: 0, quiet: false };
function _trHome() {
  const role=State.member?.base_role||State.member?.role;
  if(State.impersonating)return roleHome();
  if(State.isStaff||State.user?.is_staff)return 'staffos';
  return role==='owner'?'home':roleHome();
}
function _trActive() { return tosHouse() && !!document.querySelector('#tos.task-room'); }
function _trIcon(name) { return `<svg class="icon" aria-hidden="true"><use href="#tr-i-${name}"/></svg>`; }
function _trShell() {
  const person = State.member?.full_name || State.user?.user_metadata?.full_name || 'Your workspace';
  const tenant = State.tenant?.name || 'EON';
  return `<div class="tos-shell task-room" id="tos"><svg width="0" height="0" style="position:absolute" aria-hidden="true"><defs><symbol id="tr-i-board" viewBox="0 0 24 24"><rect x="3" y="4" width="5" height="16" rx="1"/><rect x="10" y="4" width="5" height="11" rx="1"/><path d="M18 4h3v14h-3"/></symbol><symbol id="tr-i-list" viewBox="0 0 24 24"><path d="M8 6h13M8 12h13M8 18h13M3 6h.1M3 12h.1M3 18h.1"/></symbol><symbol id="tr-i-grid" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></symbol><symbol id="tr-i-check" viewBox="0 0 24 24"><path d="m5 12 4 4L19 6"/></symbol><symbol id="tr-i-circlecheck" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/></symbol><symbol id="tr-i-plus" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></symbol><symbol id="tr-i-arrow" viewBox="0 0 24 24"><path d="M4 12h16m-6-6 6 6-6 6"/></symbol><symbol id="tr-i-up" viewBox="0 0 24 24"><path d="M6 18 18 6M6 6h12v12"/></symbol><symbol id="tr-i-search" viewBox="0 0 24 24"><circle cx="10" cy="10" r="6"/><path d="m15 15 5 5"/></symbol><symbol id="tr-i-person" viewBox="0 0 24 24"><circle cx="12" cy="7" r="4"/><path d="M4 21v-2a8 8 0 0 1 16 0v2"/></symbol><symbol id="tr-i-spark" viewBox="0 0 24 24"><path d="m12 2 2.5 7.5L22 12l-7.5 2.5L12 22l-2.5-7.5L2 12l7.5-2.5Z"/></symbol><symbol id="tr-i-repeat" viewBox="0 0 24 24"><path d="M4 8h13l-3-3m6 11H7l3 3M4 8v5m16 3v-5"/></symbol><symbol id="tr-i-page" viewBox="0 0 24 24"><path d="M5 3h10l4 4v14H5ZM15 3v5h4M8 12h8M8 16h6"/></symbol><symbol id="tr-i-close" viewBox="0 0 24 24"><path d="m6 6 12 12M6 18 18 6"/></symbol><symbol id="tr-i-lock" viewBox="0 0 24 24"><rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3m-4 4v3"/></symbol><symbol id="tr-i-pause" viewBox="0 0 24 24"><path d="M8 5v14M16 5v14"/></symbol><symbol id="tr-i-play" viewBox="0 0 24 24"><path d="m8 4 12 8-12 8Z"/></symbol><symbol id="tr-i-star" viewBox="0 0 24 24"><path d="m12 2 3 6 7 1-5 5 1 8-6-4-6 4 1-8-5-5 7-1Z"/></symbol><symbol id="tr-i-clock" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></symbol><symbol id="tr-i-menu" viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16"/></symbol><symbol id="tr-i-flag" viewBox="0 0 24 24"><path d="M5 22V3l7 2 7-2v11l-7 2-7-2"/></symbol><symbol id="tr-i-paperclip" viewBox="0 0 24 24"><path d="m9 13 6-6a3 3 0 0 1 4 4l-9 9a5 5 0 0 1-7-7L14 2m-8 12 9-9"/></symbol><symbol id="tr-i-message" viewBox="0 0 24 24"><path d="M21 16a3 3 0 0 1-3 3H9l-6 3V6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3Z"/><path d="M7 8h10M7 12h7"/></symbol><linearGradient id="cswGrad"><stop stop-color="var(--eon-green)"/><stop offset="1" stop-color="var(--eon-green)"/></linearGradient></defs></svg>
    <div class="tr-app"><aside class="sidebar" id="tr-sidebar" aria-label="Workspace navigation">
      <button class="menu-dismiss" id="tr-close-nav" aria-label="Close navigation">${_trIcon('close')}</button>
      <button class="brand" id="tos-back" aria-label="Home"><span class="brand-mark" aria-hidden="true"></span><span class="brand-logo">eon</span></button>
      <a class="tr-home-link" href="/?v=${_trHome()}" data-tr-go="home">${_trIcon('arrow')}<span>Home</span><small>WORKSPACE</small></a><div class="workspace"><span>${esc(tenant.slice(0,1))}.</span><div>${esc(tenant)}<small>Operations workspace</small></div></div>
      <nav class="nav" aria-label="Task scope"><button data-tr-nav="all" class="active">${_trIcon('grid')}All work<span class="count" id="tr-all">—</span></button><button data-tr-nav="mine">${_trIcon('person')}My work<span class="count" id="tr-mine">—</span></button>${typeof _mailAllowed==='function'&&_mailAllowed()?`<button data-tr-nav="email">${_trIcon('message')}Email work</button>`:''}</nav>
      <div class="nav-label">THE WORKSPACE</div><nav class="nav" id="tr-tools" aria-label="Task tools">${typeof posIsLead==='function'&&posIsLead()?'':`<button data-tr-nav="ideas">${_trIcon('spark')}Ideas</button>`}${memberCan('tasks:assign') ? `<button data-tr-nav="recurring">${_trIcon('repeat')}Recurring</button>` : ''}<button data-tr-nav="pages">${_trIcon('page')}Living pages</button><button data-tr-nav="insights">${_trIcon('circlecheck')}Work insights</button></nav>
      <div id="tr-modes"></div><div class="sidebar-foot"><div id="tr-locations"></div><div class="profile"><span class="avatar">${esc(person.slice(0,2).toUpperCase())}</span><div>${esc(person)}<small>${esc(State.member?.role || 'Team member')}</small></div></div>
    </aside><main id="tr-main"><div class="topline tos-topbar"><div class="crumb"><button class="icon-btn mobile-menu" id="tr-open-nav" aria-label="Open navigation" aria-expanded="false">${_trIcon('menu')}</button><a class="tr-home-crumb" href="/?v=${_trHome()}" data-tr-go="home">Home</a><span class="crumb-divider">/</span><strong>Tasks</strong><span class="tr-room-tag">THE WORK BETWEEN PEOPLE</span></div><div class="top-actions"><span class="clock" id="tr-clock">LAGOS · WAT</span><button class="icon-btn" id="tos-sound" title="Sounds" aria-label="Toggle sounds"></button><button class="icon-btn" id="tos-refresh" title="Refresh" aria-label="Refresh tasks">${_trIcon('repeat')}</button></div></div>
    <header class="mast"><div class="mast-title"><div class="tr-edition"><i></i> THE WORKSPACE, IN MOTION</div><h1>tasks<span class="period">.</span></h1><div class="mast-note">Good work<br><strong>moves together.</strong></div></div><div class="mast-right"><div class="completion-dial" id="tr-dial" aria-label="Verified share of loaded board"><div class="dial-face"><svg viewBox="0 0 58 58" aria-hidden="true"><circle class="dial-ticks" cx="29" cy="29" r="28"/><circle class="dial-track" cx="29" cy="29" r="24"/><circle class="dial-value" id="tr-dial-value" cx="29" cy="29" r="24"/></svg><span class="dial-number"><span id="tr-dial-number">—</span><small>%</small></span></div><div class="dial-copy"><strong>Work, verified.</strong><span id="tr-dial-caption">Reading the board…</span></div></div><button class="primary" id="tos-add">${_trIcon('plus')}New task</button></div></header>
    <div class="tr-command-deck"><div id="tos-pulse"></div><section class="intelligence" id="tr-intelligence" data-phase="observing" aria-label="Task activity and evidence"><div class="intelligence-core" aria-hidden="true"><i class="core-ring"></i><i class="core-center"></i></div><div class="intelligence-copy"><div class="intelligence-title"><strong id="tr-signal-title">Reading the room.</strong><span class="intelligence-mode" id="tr-freshness">CONNECTING</span></div><div class="intelligence-message" id="tr-signal-message" role="status">Waiting for task evidence.</div></div><div class="intelligence-phases" aria-hidden="true"><span data-step="observing">Observe</span><i></i><span data-step="connecting">Connect</span><i></i><span data-step="learning">Learn</span></div><div class="intelligence-count"><span id="tr-signal-count">00</span><small>handoffs noticed</small></div></section></div><div id="tr-workline" class="tr-workline"></div>
    <div class="tos-body" id="tos-body">${_tosSkel()}</div><section class="lower" id="tr-lower"><div><div class="lower-head">LIVING PAGES<button class="text-button" data-tr-nav="pages">Explore ${_trIcon('up')}</button></div><div id="tr-page-preview" class="live-page-preview"><div class="mini-page" aria-hidden="true"><i></i><i></i><i></i></div><div class="live-page-copy"><strong>Work with a trail of evidence.</strong><p>Open a living page to inspect what the data proves.</p></div></div></div><div id="tr-team" class="tr-team" aria-label="Team workload"></div><div class="recent"><div class="lower-head">RECENT COMPLETIONS<span class="mono">BOARD RECORDS</span></div><div id="tr-activity">Reading task records…</div></div></section>
    <footer class="footer"><span id="tr-read-time">Waiting for a successful read</span><button class="motion-control" id="tr-motion" aria-pressed="false">${_trIcon('pause')}<span>Pause motion</span></button><span>MADE FOR THE WORK BETWEEN PEOPLE.</span></footer></main></div><div class="tr-nav-scrim" id="tr-nav-scrim" hidden></div></div>`;
}
function _trDispose() {
  _tr.abort?.abort(); clearInterval(_tr.poll); clearTimeout(_tr.phaseTimer);
}
function _trBind() {
  const root = document.getElementById('tos');
  _trDispose();
  _tr.abort = new AbortController(); const ac = _tr.abort; _tr.navGone = false; _tr.meter=null; _tr.loading = false; _tr.readAt = 0; _tr.events = []; _tr.snapshot = null;
  const opts = { signal: _tr.abort.signal };
  _tr.quiet = localStorage.getItem('tos.room.quiet') === '1';
  root.classList.toggle('quiet', _tr.quiet);
  const modebar = document.getElementById('tos-modebar');
  // The label belongs to the modes slot, so it only goes in when the bar actually moved there.
  // Inserting it beside a bar that never moved leaves a stray OPERATIONS heading in the menu.
  if (modebar) { const ideas=document.getElementById('cc-sw-ideas'); if(ideas){ideas.innerHTML=_trIcon('spark')+'Ideas'; document.getElementById('tr-tools')?.prepend(ideas);} const modes=document.getElementById('tr-modes'); if (modes) { modes.append(modebar); modebar.insertAdjacentHTML('beforebegin','<div class="nav-label">OPERATIONS</div>'); } }
  // One breadcrumb per bind for the state nobody has explained yet: the room is on the page
  // and its nav is not. _reportClientError, not console.warn — only window errors and rejections
  // are collected, so a warn on her phone reaches nobody and the next session has the same
  // zero evidence. The reporter never throws and dedupes per page load.
  // The stack is not decoration: client_errors.has_app_frame reads it, and a note with no
  // stack is filed "browser/network noise", never diagnosed and never promoted. With one it
  // ranks medium and the lane can work it.
  const navGone = (id) => { if (_tr.navGone) return; _tr.navGone = true; _reportClientError('[task-room] ' + id + ' missing while the room is still on the page', new Error().stack, 'onerror'); };
  function closeNav() {
    // The viewport listener below outlives the room until the poll notices; a resize on / after leaving must not touch a dead DOM.
    // ac, not _tr.abort: a stale closeNav must never abort the controller of a room bound after it.
    if (!root.isConnected) { ac.abort(); return; }
    const sidebar = document.getElementById('tr-sidebar');
    if (!sidebar) navGone('tr-sidebar');
    sidebar?.classList.remove('open');
    if (matchMedia('(max-width:1040px)').matches) sidebar?.setAttribute('inert','');
    const scrim = document.getElementById('tr-nav-scrim'); if (scrim) scrim.hidden = true;
    document.getElementById('tr-main')?.removeAttribute('inert');
    document.getElementById('tr-open-nav')?.setAttribute('aria-expanded','false');
  }
  root.addEventListener('click', e => {
    const destination=e.target.closest('[data-tr-go]');
    if(destination){e.preventDefault(); State.view=destination.dataset.trGo==='home'?_trHome():destination.dataset.trGo; render(); return;}
    const nav = e.target.closest('[data-tr-nav]');
    if (nav) { _trNavigate(nav.dataset.trNav); closeNav(); }
    if (e.target.closest('#tr-open-nav')) {
      // The menu tap is the travelled path, not the resize that got recorded — it files the same breadcrumb.
      const openSidebar = document.getElementById('tr-sidebar');
      if (!openSidebar) navGone('tr-sidebar');
      openSidebar?.classList.add('open');
      openSidebar?.removeAttribute('inert');
      const openScrim = document.getElementById('tr-nav-scrim'); if (openScrim) openScrim.hidden = false;
      document.getElementById('tr-main')?.setAttribute('inert','');
      document.getElementById('tr-open-nav')?.setAttribute('aria-expanded','true');
      document.getElementById('tr-close-nav')?.focus();
    }
    if (e.target.closest('#tr-close-nav, #tr-nav-scrim')) { closeNav(); document.getElementById('tr-open-nav')?.focus(); }
    if (e.target.closest('#tr-motion')) {
      _tr.quiet = !_tr.quiet; root.classList.toggle('quiet',_tr.quiet); localStorage.setItem('tos.room.quiet',_tr.quiet?'1':'0'); _trMotionLabel();
    }
    if (e.target.closest('#cc-switch button, #cc-sw-ideas')) { _trSyncNav(); closeNav(); }
  }, opts);
  document.addEventListener('keydown', e => {
    if (!root.isConnected || document.querySelector('.csw-overlay')) return;
    if (e.key === 'Escape') { closeNav(); document.getElementById('tr-open-nav')?.focus(); return; }
    if (document.getElementById('tr-sidebar')?.classList.contains('open') && e.key === 'Tab') {
      const els = [...document.querySelectorAll('#tr-sidebar button, #tr-sidebar a[href]')].filter(x=>x.offsetParent!==null);
      const first=els[0],last=els.at(-1);
      if(e.shiftKey&&document.activeElement===first){e.preventDefault();last.focus();} else if(!e.shiftKey&&document.activeElement===last){e.preventDefault();first.focus();}
      return;
    }
    // The key's real target (eonKeyTarget): typed in Ask EON's box, a key
    // arrives as Ask's host DIV, so "n" opened Quick add and took the rest
    // of the question into a task title, and "/" jumped to Find a task.
    if (eonKeyTarget(e).closest('input, textarea, select, [contenteditable="true"]') || e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === '/') { e.preventDefault(); document.getElementById('tr-search')?.focus(); }
    if (e.key.toLowerCase() === 'n') { e.preventDefault(); openQuickAdd(); }
  }, opts);
  _tr.poll = setInterval(() => {
    // ac, not _tr.abort — the same reason as closeNav and the width watcher below:
    // a stale tick must never abort the controller of a room bound after it.
    if (!root.isConnected) { clearInterval(_tr.poll); ac.abort(); return; }
    _trClock();
    // eonFocusedElement: the cursor in Ask EON's box holds the refresh too,
    // as any page box does (a refresh can reopen a linked task over Ask).
    if (!document.hidden && _tosState.view !== 'email' && _tosState.mode === 'staff' && !_tr.loading && !document.querySelector('.csw-overlay') && !eonFocusedElement()?.closest('input, textarea, select, [contenteditable="true"]') && Date.now()-_tr.readAt>45000) _tosLoad();
  },15000);
  const mobile = matchMedia('(max-width:1040px)');
  // The bail is the watcher's own: a stale watcher must never write to a room bound after it, and it disarms itself rather than waiting for the poll.
  const syncSidebar = () => { if (!root.isConnected) { ac.abort(); return; } closeNav(); if (!mobile.matches) document.getElementById('tr-sidebar')?.removeAttribute('inert'); };
  mobile.addEventListener('change', syncSidebar, opts); syncSidebar();
  _trClock(); _trMotionLabel(); _trSyncNav();
}
function _trMotionLabel() {
  const b=document.getElementById('tr-motion'); if(!b)return;
  b.setAttribute('aria-pressed',String(_tr.quiet)); b.querySelector('span').textContent=_tr.quiet?'Resume motion':'Pause motion';
}
function _trClock() {
  const el=document.getElementById('tr-clock'); if(el)el.textContent=new Intl.DateTimeFormat('en-GB',{hour:'2-digit',minute:'2-digit',timeZone:'Africa/Lagos'}).format(new Date())+' WAT';
  const freshness=document.getElementById('tr-freshness');
  if(freshness && _tr.readAt) freshness.textContent=Date.now()-_tr.readAt>90000?'NEEDS A REFRESH':'UP TO DATE';
}
function _trNavigate(key) {
  _tr.search='';_tr.store='';_tr.metric='';_tr.focus=false;_tr.assignee='';_tr.due='';
  if(key==='all'||key==='mine') { _tr.scope=key;_tosState.view=_tr.view; }
  else _tosState.view=key;
  if(_tosState.mode!=='staff') document.getElementById('cc-sw-staff')?.click();
  else _tosRenderView();
  _trSyncNav();
  const room=document.getElementById('tos');if(room)room.scrollTop=0;
}
function _trSyncNav() {
  document.querySelectorAll('#tos [data-tr-nav]').forEach(b=>{
    const active=_tosState.mode==='staff' && (['board','list','dept'].includes(_tosState.view)?b.dataset.trNav===_tr.scope:b.dataset.trNav===_tosState.view);
    b.classList.toggle('active',active);
    if(active)b.setAttribute('aria-current','page');else b.removeAttribute('aria-current');
  });
  const root=document.getElementById('tos');
  if(root){
    const screen=(mode,view)=>mode==='staff'?(['board','list','dept','mywork'].includes(view)?'work':view):mode;
    if(screen(root.dataset.roomMode,root.dataset.roomView)!==screen(_tosState.mode,_tosState.view))root.scrollTop=0;
    root.dataset.roomMode=_tosState.mode;root.dataset.roomView=_tosState.view;
  }
  document.querySelectorAll('#cc-switch button, #cc-sw-ideas').forEach(b=>{
    const active=b.id==='cc-sw-ideas'?_tosState.mode==='staff'&&_tosState.view==='ideas':b.id==='cc-sw-'+_tosState.mode;
    if(b.id==='cc-sw-ideas')b.classList.toggle('on',active);
    if(active)b.setAttribute('aria-current','page');else b.removeAttribute('aria-current');
  });
  const context={email:['tasks','Email is work.','Keep the reply and next step together.'],insights:['insights','See the work.','Find the next move.'],pages:['pages','Ideas into action.','Evidence into progress.'],recurring:['rhythm','The work repeats.','Keep getting better.'],ideas:['ideas','Make room','for what comes next.']};
  const entry=_tosState.mode!=='staff'?[_tosState.mode,'Your workspace.','One connected view.']:context[_tosState.view]||['tasks','Good work','moves together.'];
  const title=root?.querySelector('.mast h1');if(title)title.innerHTML=entry[0]+'<span class="period">.</span>';
  const note=root?.querySelector('.mast-note');if(note)note.innerHTML=entry[1]+'<br><strong>'+entry[2]+'</strong>';
}
function _trRows() {
  let rows=_tr.scope==='mine'?(_tosState.mine?.tasks||[]).map(t=>({...t,...(_tosState.byId[t.id]||{})})):_tosState.tasks;
  const term=_tr.search.trim().toLowerCase();
  return rows.filter(t=>TOS_COL.some(c=>c[2].includes(t.status)) && (!term||[t.title,t.assignee_name,t.department,t.action_required,t.relay?.short_id].join(' ').toLowerCase().includes(term)) && (!_tr.store||(t.location_name||t.relay?.store_name||'')===_tr.store) && (!_tr.focus||t.status==='blocked'||['critical','high'].includes(t.priority)||(t.due_at&&new Date(t.due_at)<new Date()&&!['completed','done','verified'].includes(t.status))) && (!_tr.assignee||_trAssigneeKey(t)===_tr.assignee) && (!_tr.due||(_tr.due==='overdue'&&t.due_at&&new Date(t.due_at)<new Date()&&!['completed','done','verified'].includes(t.status))) && (!_tr.metric||(_tr.metric==='open'?!['verified','completed','done'].includes(t.status):_tr.metric==='review'?['completed','done'].includes(t.status):t.status==='verified')));
}
function _trPulse() {
  const rows=_tosState.tasks.filter(t=>TOS_COL.some(c=>c[2].includes(t.status))), verified=rows.filter(t=>t.status==='verified'), review=rows.filter(t=>['completed','done'].includes(t.status));
  const working=rows.length-verified.length-review.length;
  const tile=(key,n,label,note)=>`<button class="tr-score tr-score--${key}" data-tr-metric="${key}" aria-pressed="${_tr.metric===key}"><span class="tr-score-label"><i></i>${label}</span><strong>${String(n).padStart(2,'0')}</strong><span>${note}${_trIcon('up')}</span></button>`;
  const el=document.getElementById('tos-pulse'); if(!el)return; el.className='tr-pulse';
  el.innerHTML=`<div class="tr-flow-map"><div class="tr-flow-heading"><span>THE SHAPE OF TODAY</span><span>${rows.length} tasks on this board</span></div><svg viewBox="0 0 560 140" aria-hidden="true"><defs><linearGradient id="tr-flow-ink"><stop stop-color="var(--eon-green)"/><stop offset=".48" stop-color="var(--eon-green)"/><stop offset="1" stop-color="var(--eon-green)"/></linearGradient></defs><path class="tr-map-grid" d="M0 35H560M0 70H560M0 105H560M70 0V140M140 0V140M210 0V140M280 0V140M350 0V140M420 0V140M490 0V140"/><path class="tr-flow-track" d="M0 108H58C120 108 108 28 190 28H283C358 28 340 111 417 111H560"/><path class="tr-flow-track tr-flow-track--return" d="M0 34H72C158 34 136 114 214 114H295C373 114 361 35 432 35H560"/><path class="tr-flow-trace" d="M0 108H58C120 108 108 28 190 28H283C358 28 340 111 417 111H560"/><path class="tr-flow-trace tr-flow-trace--return" d="M0 34H72C158 34 136 114 214 114H295C373 114 361 35 432 35H560"/><circle class="tr-flow-node" cx="58" cy="108" r="6"/><circle class="tr-flow-node" cx="238" cy="28" r="6"/><circle class="tr-flow-node" cx="432" cy="35" r="6"/></svg><div class="tr-flow-caption"><strong>Every handoff<br>has a next move<span>.</span></strong><span>Different people.<br>A shared next step.</span></div></div><div class="tr-score-grid">${tile('open',working,'In the works','Open work')}${tile('review',review.length,'Ready to verify','Your next look')}${tile('verified',verified.length,'Verified','On board · last 48h')}</div>`;
  el.querySelectorAll('[data-tr-metric]').forEach(b=>b.addEventListener('click',()=>{_tr.metric=_tr.metric===b.dataset.trMetric?'':b.dataset.trMetric;_tosState.view=_tr.view;_tr.scope='all';_trRenderView();_trPulse();}));
  const percent=rows.length?Math.round(verified.length/rows.length*100):0;
  document.getElementById('tr-dial-number').textContent=percent;
  document.getElementById('tr-dial-value').style.strokeDashoffset=151*(1-percent/100);
  document.getElementById('tr-dial-caption').textContent=`${verified.length} of ${rows.length} tasks`;
  document.getElementById('tr-all').textContent=rows.length;
  document.getElementById('tr-mine').textContent=_tosState.mine?.error?'—':(_tosState.mine?.tasks||[]).length;
  const p=_tosState.pulse||{},ring=p.ring||{};
  const workline=document.getElementById('tr-workline');
  workline.innerHTML=`<button data-tr-personal="all">${_trIcon('person')}<span>Mine <b>${p.my_open??'—'}</b></span></button><button data-tr-personal="overdue" class="${p.my_overdue?'tr-attention':''}">${_trIcon('clock')}<span>Overdue <b>${p.my_overdue??'—'}</b></span></button><span class="tr-points">${_trIcon('star')}<span>My ring <b>${ring.points??'—'} / ${ring.target??'—'}</b></span></span><button id="tr-eon-queue">${_trIcon('spark')}<span>EON needs you <b>${p.eon_queued??'—'}</b></span>${_trIcon('arrow')}</button><button data-tr-nav="insights" class="tr-insights-link">Work insights ${_trIcon('up')}</button>`;
  workline.querySelectorAll('[data-tr-personal]').forEach(b=>b.addEventListener('click',()=>{_tr.scope='mine';_tr.due=b.dataset.trPersonal==='overdue'?'overdue':'';_tr.metric='';_tr.assignee='';_tosState.view=_tr.view;_trRenderView();}));
  workline.querySelector('#tr-eon-queue').addEventListener('click',openEonQueue);
  _trRenderTeam();
}
function _trAssigneeKey(t) {
  return t.assigned_to_staff_id?'s:'+t.assigned_to_staff_id:t.assigned_to_user_id?'u:'+t.assigned_to_user_id:'n:'+(t.assignee_name||'Unassigned');
}
function _trTeamRows() {
  const teams=new Map();
  _tosState.tasks.filter(t=>!['verified','cancelled','expired'].includes(t.status)).forEach(t=>{const key=_trAssigneeKey(t);if(!teams.has(key))teams.set(key,{key,name:t.assignee_name||'Unassigned',count:0,review:0});const row=teams.get(key);row.count++;if(['completed','done'].includes(t.status))row.review++;});
  return [...teams.values()].sort((a,b)=>b.count-a.count);
}
function _trRenderTeam() {
  const host=document.getElementById('tr-team');if(!host)return;
  const rows=_trTeamRows().slice(0,4),max=Math.max(1,...rows.map(r=>r.count));
  host.innerHTML=`<div class="lower-head">PEOPLE IN MOTION<button class="text-button" data-tr-nav="insights">See all ${_trIcon('up')}</button></div>${rows.map((r,i)=>`<button class="tr-person-row" data-tr-person="${esc(r.key)}"><span class="avatar" style="--person-index:${i}">${esc(r.name.split(' ').map(s=>s[0]).slice(0,2).join(''))}</span><span><strong>${esc(r.name)}</strong><i class="tr-load-track"><i style="width:${Math.round(r.count/max*100)}%"></i></i></span><b>${r.count}</b></button>`).join('')||'<p class="muted">No open work on the board.</p>'}`;
  host.querySelectorAll('[data-tr-person]').forEach(b=>b.addEventListener('click',()=>_trSelectPerson(b.dataset.trPerson)));
}
function _trSelectPerson(key) {
  _tr.scope='all';_tr.assignee=key;_tr.search='';_tr.store='';_tr.metric='';_tr.due='';_tr.focus=false;_tosState.view='list';_trRenderView();document.getElementById('tos-body')?.scrollIntoView({behavior:_tr.quiet||matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth',block:'start'});
}
function _trInsightsHTML() {
  const p=_tosState.pulse||{},m=_tr.meter,teams=_trTeamRows();
  const meter=m?.armed&&m?.ok?_tosMeterHTML(m):`<div class="tr-insights-empty">${m?.error?'Relay signals could not be refreshed. Use Refresh to try again.':m?.armed===false?'Relay signals will appear when the connection is active.':'Reading the handoff signals…'}</div>`;
  return `<div class="section-intro"><div><span class="eyebrow">WORK INSIGHTS</span><h2>See where<br>the work goes<span>.</span></h2><p>People, handoffs and proof. One connected view.</p></div><div class="tr-insight-total"><strong>${p.team_week_points??'—'}</strong><span>team points · last 7 days</span></div></div><div class="tr-insight-top"><section class="tr-insight-panel"><div class="lower-head">WHO'S CARRYING WHAT<span>${_tosState.tasks.length} board records</span></div><div class="tr-team-full">${teams.map(r=>`<button class="tr-person-row" data-tr-person="${esc(r.key)}"><span class="avatar">${esc(r.name.split(' ').map(s=>s[0]).slice(0,2).join(''))}</span><span><strong>${esc(r.name)}</strong><small>${r.review} waiting for verification</small></span><b>${r.count}</b>${_trIcon('up')}</button>`).join('')||'<p>No open work on this board.</p>'}</div></section><section class="tr-insight-panel tr-proof-panel"><span class="eyebrow">THE NEXT STEP</span><strong>${p.org_stuck??'—'}</strong><h3>tasks need a hand.</h3><p>Stuck work across the organisation. Open the priority view to find the next action in the loaded board.</p><button class="primary" id="tr-insight-focus">Find the next move ${_trIcon('arrow')}</button><div class="tr-proof-note">Learning follows recorded outcomes. A restock observation is shown only when its receipt is available.</div></section></div><section class="tr-insight-panel tr-relay-panel"><div class="lower-head">THE HANDOFF SIGNALS<span>Actual Relay records</span></div><div id="tr-meter-slot">${meter}</div></section>`;
}

function _trReadAccepted() {
  const snapshot=new Map(_tosState.tasks.map(t=>[t.id,`${t.status}|${t.assigned_to_staff_id||''}|${t.assigned_to_user_id||''}`]));
  const changes=_tr.snapshot?_tosState.tasks.filter(t=>_tr.snapshot.get(t.id)!==snapshot.get(t.id)):[];
  _tr.snapshot=snapshot;_tr.readAt=Date.now();_tr.loading=false;
  const label=new Intl.DateTimeFormat('en-GB',{hour:'2-digit',minute:'2-digit',timeZone:'Africa/Lagos'}).format(new Date());
  document.getElementById('tr-read-time').textContent=`Board read ${label} WAT · up to 600 tasks · verified 48h`;
  if(changes.length){_tr.events.push(...changes.map(t=>({title:t.title,status:t.status})));_trSignal(changes[0]);}
  else if(!_tr.events.length){document.getElementById('tr-signal-title').textContent='EON is observing.';document.getElementById('tr-signal-message').textContent='Watching the handoffs. New outcomes will leave their mark here.';}
  const recent=_tosState.tasks.filter(t=>t.verified_at||t.completed_at).sort((a,b)=>new Date(b.verified_at||b.completed_at)-new Date(a.verified_at||a.completed_at)).slice(0,3);
  document.getElementById('tr-activity').innerHTML=recent.map(t=>`<button class="activity-row" data-tr-task="${esc(t.id)}"><i class="activity-dot"></i><span><strong>${esc(t.assignee_name||'Team')}</strong> · ${esc(t.title)} <span class="muted">${t.status==='verified'?'verified':'completed'}</span></span><time>${esc(new Intl.DateTimeFormat('en-GB',{day:'2-digit',month:'short',timeZone:'Africa/Lagos'}).format(new Date(t.verified_at||t.completed_at)))}</time></button>`).join('')||'<p class="muted">No completions in this board snapshot.</p>';
  document.querySelectorAll('#tr-activity [data-tr-task]').forEach(b=>b.addEventListener('click',()=>openTaskDetail(b.dataset.trTask)));
  const learned = _tosState.tasks.filter(t => t.learning_observation?.kind === 'restock')
    .sort((a,b) => new Date(b.learning_observation.closed_at)-new Date(a.learning_observation.closed_at))[0];
  if (learned) {
    const obs = learned.learning_observation;
    const rail = document.getElementById('tr-intelligence');
    document.getElementById('tr-signal-title').textContent = 'An outcome EON can learn from.';
    document.getElementById('tr-signal-message').textContent = `Restock observation: ${obs.days} days. At least ${obs.minimum_observations} observations are needed before a lead-time adjustment.`;
    let proof = document.getElementById('tr-learning-proof');
    if (!proof) { proof = document.createElement('button'); proof.id = 'tr-learning-proof'; proof.className = 'text-button'; document.getElementById('tr-signal-message').after(proof); }
    proof.textContent = 'View recorded task'; proof.onclick = () => openTaskDetail(learned.id);
    const receipt = learned.id + obs.closed_at;
    if (_tr.learningReceipt !== receipt) {
      _tr.learningReceipt = receipt; rail.dataset.phase = 'learning'; clearTimeout(_tr.phaseTimer);
      _tr.phaseTimer = setTimeout(() => { if(rail.isConnected) rail.dataset.phase='observing'; },6500);
    }
  } else { document.getElementById('tr-learning-proof')?.remove(); }
  const locations = [...new Set(_tosState.tasks.map(t=>t.location_name||t.relay?.store_name).filter(Boolean))].sort();
  document.getElementById('tr-locations').innerHTML = locations.length ? '<div class="nav-label">WORK LOCATIONS</div><div class="store-grid">'+locations.map(n=>`<span>${esc(n)}</span>`).join('')+'</div>' : '';
  if (!_tr.pagesLoading && (!_tr.pagesReadAt || Date.now()-_tr.pagesReadAt>120000)) _trLoadPages();
  _trClock();_trSyncNav();
}
function _trSignal(t) {
  clearTimeout(_tr.phaseTimer);
  const rail=document.getElementById('tr-intelligence');if(!rail)return;
  rail.dataset.phase='connecting'; document.getElementById('tr-signal-title').textContent='A handoff moved.';
  document.getElementById('tr-signal-count').textContent=String(_tr.events.length).padStart(2,'0');
  const outcome=t.status==='verified'?'Verification is recorded.':t.status==='blocked'?'A blocker needs a next step.':['completed','done'].includes(t.status)?'Completion is recorded. Verification comes next.':'The next step has changed.';
  document.getElementById('tr-signal-message').textContent=`${t.title||'Task'} — ${outcome}`;
  _tr.phaseTimer=setTimeout(()=>{if(rail.isConnected)rail.dataset.phase='observing';},6500);
}
function _trCard(t, draggable, index=0) {
  const verified=t.status==='verified',review=['completed','done'].includes(t.status),featured=t.status==='in_progress'&&['high','critical'].includes(t.priority);
  const name=t.assignee_name||'Unassigned', r=t.relay;
  return `<button class="tos-card card ${verified?'verified':''} ${featured?'featured':''} ${r?'tos-card--ask':''}" data-status="${esc(t.status)}" data-tid="${esc(t.id)}" ${draggable?'draggable="true"':''} style="--i:${Math.min(index,10)}">
    <span class="card-top"><span class="dept">${esc(t.department||'General')}${['high','critical'].includes(t.priority)?' · '+esc(t.priority):''}</span>${t.created_by_eon?'<span class="eon-badge">'+_trIcon('spark')+'EON</span>':''}</span>
    ${verified?`<span class="verified-stamp">${_trIcon('circlecheck')}VERIFIED${t.quality_rating?' · '+esc(t.quality_rating)+'/5':''}</span>`:''}<span class="tr-card-title">${esc(t.title||'Untitled')}</span>
    ${r?`<span class="tos-ask__id">${esc(r.short_id||'')}${r.reason?' · '+esc(relayReasonWord(r.reason)):''}</span>${relayAskerHTML(t.requester_name,t.created_by_eon,r.dept,r.store_name)}${relayClockHTML(r)}${relayRungHTML(r)}`:''}
    ${t.status==='blocked'?'<span class="blocker">Handoff paused. Open the task for its blocker and next step.</span>':''}
    ${featured?'<span class="tr-active-trace" aria-hidden="true"><i></i><i></i><i></i></span><span class="card-progress-label">Work is in motion</span>':''}
    ${t.estimated_impact_ngn?`<span class="card-value">${cswNgn(t.estimated_impact_ngn)}<small>Estimated impact</small></span>`:''}
    ${verified&&Number.isFinite(Number(t.points_awarded))&&t.points_awarded!=null?`<span class="card-value">${esc(t.points_awarded)} pts<small>Awarded by verification</small></span>`:''}
    ${review?`<span class="review-btn">${memberCan('tasks:verify')?'Review evidence':'Awaiting verification'}${_trIcon('arrow')}</span>`:''}
    <span class="card-footer"><span class="person"><span class="avatar">${esc(name.split(' ').map(x=>x[0]).slice(0,2).join(''))}</span>${esc(name)}</span><span class="due">${_tosFmtDue(t.due_at)||'No deadline'}</span></span></button>`;
}
function _trCartFold(carts, key) {
  if (!carts.length) return '';
  const limit = _tr.limits[key] || 6;
  return `<details class="tos-cartfold" data-tr-fold="${key}"><summary>Abandoned carts · ${carts.length}</summary>${carts.slice(0,limit).map((t,i)=>_trCard(t,false,i)).join('')}${carts.length>limit?`<button class="lane-add" data-tr-more="${key}">Show more · ${carts.length-limit} remaining</button>`:''}</details>`;
}
function _trBoard(rows) {
  return `<div class="tr-board-workstream"><nav class="tr-stages" aria-label="Board stages">${TOS_COL.map(([key,label,statuses],i)=>`<button data-tr-stage="${key}" aria-pressed="${i===0}" tabindex="${i===0?0:-1}"><span class="tr-stage-index">0${i+1}</span><span class="tr-stage-name">${label}</span><b>${rows.filter(t=>statuses.includes(t.status)).length}</b></button>`).join('')}</nav><div class="board-scroll"><div class="board tos-board">${TOS_COL.map(([key,label,statuses])=>{
    const all=rows.filter(t=>statuses.includes(t.status)),{carts,rest}=_tosCartFold(all);
    const lane=key==='doing'?'in_progress':key==='review'?'completed':key;
    const limit = _tr.limits[key] || 6;
    return `<section class="lane tos-col" data-lane="${lane}" data-col="${key}" aria-label="${label}"><div class="lane-heading"><span class="lane-heading-left"><i class="status-icon"></i>${label}<span class="lane-count">${all.length}</span></span></div><div class="tos-col__drop" data-col="${key}">${rest.slice(0,limit).map((t,i)=>_trCard(t,true,i)).join('')}${rest.length>limit?`<button class="lane-add" data-tr-more="${key}">Show more · ${rest.length-limit} remaining</button>`:''}${_trCartFold(carts,'cart-'+key)}${!all.length?'<div class="empty-lane">Nothing here right now.</div>':''}</div>${key==='todo'?`<button class="lane-add" id="tr-lane-add">${_trIcon('plus')}Add a task</button>`:''}</section>`;
  }).join('')}</div></div></div>`;
}
function _trBindStages(body) {
  const host=body.querySelector('.tr-board-workstream');if(!host)return;
  const scroller=host.querySelector('.board-scroll'),buttons=[...host.querySelectorAll('[data-tr-stage]')],lanes=[...host.querySelectorAll('.lane[data-col]')];
  const select=key=>{_tr.stage=key;buttons.forEach(b=>{const active=b.dataset.trStage===key;b.setAttribute('aria-pressed',String(active));b.tabIndex=active?0:-1;});lanes.forEach(l=>l.toggleAttribute('data-current',l.dataset.col===key));};
  const sync=()=>{
    const bounds=scroller.getBoundingClientRect(),selected=buttons.find(b=>b.getAttribute('aria-pressed')==='true'),current=lanes.find(l=>l.dataset.col===selected?.dataset.trStage),rect=current?.getBoundingClientRect();
    if(rect&&rect.left>=bounds.left-2&&rect.right<=bounds.right+2)return;
    const nearest=lanes.reduce((a,l)=>Math.abs(l.getBoundingClientRect().left-bounds.left)<Math.abs(a.getBoundingClientRect().left-bounds.left)?l:a,lanes[0]);
    if(nearest)select(nearest.dataset.col);
  };
  buttons.forEach((b,i)=>{
    b.addEventListener('click',()=>{
      const lane=lanes.find(l=>l.dataset.col===b.dataset.trStage);if(!lane)return;
      select(b.dataset.trStage);
      scroller.scrollTo({left:scroller.scrollLeft+lane.getBoundingClientRect().left-scroller.getBoundingClientRect().left,behavior:_tr.quiet||matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
    });
    b.addEventListener('keydown',e=>{if(!['ArrowLeft','ArrowRight','Home','End'].includes(e.key))return;e.preventDefault();const next=e.key==='Home'?0:e.key==='End'?buttons.length-1:(i+(e.key==='ArrowRight'?1:buttons.length-1))%buttons.length;buttons[next].click();buttons[next].focus();});
  });
  // Update the navigator after a manual pan settles, preserving an explicitly
  // chosen lane throughout a smooth jump and when several stages fit on screen.
  let settle;
  scroller.addEventListener('scroll',()=>{clearTimeout(settle);settle=setTimeout(()=>{if(host.isConnected)sync();},140);},{passive:true});
  scroller.addEventListener('scrollend',()=>{clearTimeout(settle);sync();});
  select(buttons.some(b=>b.dataset.trStage===_tr.stage)?_tr.stage:buttons[0]?.dataset.trStage);sync();
}
function _trList(rows) {
  const { carts, rest } = _tosCartFold(rows);
  return `<div class="board-scroll"><table class="list-table"><thead><tr><th>Task</th><th>Status</th><th>Assignee</th><th>Due</th></tr></thead><tbody>${rest.map(t=>`<tr><td class="list-title"><button class="tr-list-open" data-tr-task="${esc(t.id)}">${esc(t.title||'Untitled')}</button><small>${esc(t.department||'General')}</small></td><td><span class="pill ${esc(t.status)}">${esc(TOS_COL.find(c=>c[2].includes(t.status))?.[1]||t.status)}</span></td><td>${esc(t.assignee_name||'Unassigned')}</td><td>${_tosFmtDue(t.due_at)||'—'}</td></tr>`).join('')}</tbody></table>${_trCartFold(carts,'cart-list')}${!rows.length?'<div class="empty-state"><h2>No matching work.</h2><p>Clear your filters or add a task.</p></div>':''}</div>`;
}
function _trRenderView(resultsOnly=false) {
  if(_tosState.mode!=='staff')return;
  const body=document.getElementById('tos-body');if(!body)return;
  const work=['board','list','dept','mywork'].includes(_tosState.view);
  if(_tosState.view==='mywork'){_tr.scope='mine';_tosState.view=_tr.view;}
  if(!work) {
    if(_tosState.view==='email' && _mailAllowed()){if(!body.querySelector('.wr-queue'))_mailLoad(true);}
    else if(_tosState.view==='insights'){body.innerHTML=_trInsightsHTML();body.querySelectorAll('[data-tr-person]').forEach(b=>b.addEventListener('click',()=>_trSelectPerson(b.dataset.trPerson)));body.querySelector('#tr-insight-focus')?.addEventListener('click',()=>{_trNavigate('all');_tr.focus=true;_trRenderView();});}
    else if(_tosState.view==='pages'){body.innerHTML=_trPagesHTML();body.querySelector('#tr-pages-retry')?.addEventListener('click',()=>{_tr.pages=null;_trRenderView();});if(!_tr.pages&&!_tr.pagesLoading)_trLoadPages();}
    else if(_tosState.view==='ideas'){body.innerHTML=_tosIdeas();_tosBindIdeas();if(!_tosState.ideas)_tosLoadIdeas().then(()=>{if(_tosState.view==='ideas')_trRenderView();});}
    else if(_tosState.view==='recurring'){body.innerHTML=_tosRecurring();_tosBindRecurring();if(!_tosState.recurring)_tosLoadRecurring().then(()=>{if(_tosState.view==='recurring')_trRenderView();});}
    _trSyncNav();return;
  }
  _tr.view=_tosState.view;
  const rows=_trRows();const stores=[...new Set(_tosState.tasks.map(t=>t.location_name||t.relay?.store_name).filter(Boolean))].sort();
  const tabs=[['board','Board','board'],['list','List','list'],['dept','Departments','grid']];
  let view=_tr.view==='board'?_trBoard(rows):_tr.view==='list'?_trList(rows):`<div class="department-grid">${[...new Set(rows.map(t=>t.department||'General'))].sort().map(dept=>`<section class="department-section"><h2>${esc(dept)}<span>${rows.filter(t=>(t.department||'General')===dept).length}</span></h2>${rows.filter(t=>(t.department||'General')===dept).map((t,i)=>_trCard(t,false,i)).join('')}</section>`).join('')}</div>`;
  if(_tr.scope==='mine'&&_tosState.mine?.error)view=`<div class="empty-state"><h2>My work could not be read.</h2><p>${esc(_tosState.mine.error)}</p><button class="secondary" id="tr-retry">Try again</button></div>`;
  const person=_tr.assignee?_trTeamRows().find(t=>t.key===_tr.assignee)?.name:'';
  const filterNote=_tr.search||_tr.store||_tr.focus||_tr.metric||_tr.assignee||_tr.due?`<div class="tr-filter-note"><span>${rows.length} matching tasks in this view${person?' · '+esc(person):''}${_tr.due?' · overdue':''}</span>${_tr.search.trim().length>=2?'<button id="tr-search-all" class="text-button">Search beyond this board →</button>':''}<button id="tr-clear" class="text-button">Clear filters</button></div>`:'';
  if(resultsOnly && body.querySelector('#tr-results')){
    const scroll=body.querySelector('.board-scroll')?.scrollLeft||0;
    body.querySelector('#tr-results').innerHTML=view;body.querySelector('#tr-filter-host').innerHTML=filterNote;
    const scroller=body.querySelector('.board-scroll');if(scroller)scroller.scrollLeft=scroll;_trBindResults(body);return;
  }
  const scroll=body.querySelector('.board-scroll')?.scrollLeft||0;
  body.innerHTML=`<div class="toolbar"><div class="view-tabs" role="tablist" aria-label="Task views">${tabs.map(([key,label,icon])=>`<button data-tr-view="${key}" role="tab" aria-selected="${_tr.view===key}" tabindex="${_tr.view===key?0:-1}" class="${_tr.view===key?'active':''}">${_trIcon(icon)}${label}</button>`).join('')}</div><div class="controls"><label class="searchbox">${_trIcon('search')}<input id="tr-search" type="search" placeholder="Find a task" aria-label="Search tasks" value="${esc(_tr.search)}"><kbd>/</kbd></label><select class="filter-select" id="tr-store" aria-label="Filter by store"><option value="">All stores</option>${stores.map(s=>`<option ${s===_tr.store?'selected':''}>${esc(s)}</option>`).join('')}</select><button class="focus-toggle" id="tr-focus" aria-pressed="${_tr.focus}"><span class="toggle-track"><i></i></span>Focus</button></div></div>${_tr.scope==='mine'?`<div class="tr-mywork-head"><strong>Your work</strong>${typeof prcCanRequest==='function'&&prcCanRequest()?'<button class="secondary" id="prc-tos-btn">Request finance</button>':''}<div class="prc-mine" id="prc-mine" hidden></div><div class="tr-due-tabs" aria-label="My work due filter"><button data-tr-due="" aria-pressed="${!_tr.due}">All my work</button><button data-tr-due="overdue" aria-pressed="${_tr.due==='overdue'}">Overdue</button></div></div>`:''}<div id="tr-filter-host">${filterNote}</div><div id="tr-results" role="tabpanel" aria-label="Tasks">${view}</div><div class="board-note"><span>${_trIcon('lock')}Completed is a claim. Verified is the proof.</span><span>${_tr.scope==='mine'?'Your work':'Drag to move · click to open'}</span></div>`;
  body.querySelectorAll('[data-tr-view]').forEach(b=>{
    const activate=()=>{_tosState.view=b.dataset.trView;_tr.metric='';_trRenderView();body.querySelector(`[data-tr-view="${_tosState.view}"]`)?.focus();};
    b.addEventListener('click',activate);
    b.addEventListener('keydown',e=>{const keys=['ArrowLeft','ArrowRight','Home','End'];if(!keys.includes(e.key))return;e.preventDefault();let i=tabs.findIndex(t=>t[0]===b.dataset.trView);i=e.key==='Home'?0:e.key==='End'?2:(i+(e.key==='ArrowRight'?1:2))%3;body.querySelector(`[data-tr-view="${tabs[i][0]}"]`)?.click();});
  });
  body.querySelector('#tr-search').addEventListener('input',e=>{_tr.search=e.target.value;_trRenderView(true);});
  body.querySelector('#tr-store').addEventListener('change',e=>{_tr.store=e.target.value;_trRenderView();});
  body.querySelector('#tr-focus').addEventListener('click',()=>{_tr.focus=!_tr.focus;_trRenderView();});
  body.querySelectorAll('[data-tr-due]').forEach(b=>b.addEventListener('click',()=>{_tr.due=b.dataset.trDue;_trRenderView();}));
  body.querySelector('#prc-tos-btn')?.addEventListener('click',prcOpenRequestSheet);
  const scroller=body.querySelector('.board-scroll');if(scroller)scroller.scrollLeft=scroll;
  _trBindResults(body);prcHydrateMyRequests();_trSyncNav();
}
function _trSearchAll() {
  const term=String(_tr.search || '').trim();
  if(term.length<2)return;
  const ov=cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Find work beyond this board</div><button class="csw-x" aria-label="Close task search">×</button></div><p class="tos-search-context">Searching task titles for <strong>${esc(term)}</strong> across the work you can access. Includes older open tasks and work awaiting verification; recently verified work stays for 48 hours. Board filters do not apply.</p><div data-search-results role="status">Searching…</div>`);
  ov.classList.add('tos-search-dialog');
  ov.querySelector('[role="dialog"]')?.setAttribute('aria-label','Find work beyond this board');
  ov.querySelector('.csw-x').onclick=()=>ov.remove();
  const host=ov.querySelector('[data-search-results]');
  const read=async()=>{
    host.textContent='Searching…';
    try {
      const d=await api(`/api/tasks/search?q=${encodeURIComponent(term)}`);
      if(!ov.isConnected)return;
      if(!d?.ok||!Array.isArray(d.tasks))throw Error('Task search could not be read.');
      host.innerHTML=`<p class="tos-search-count">${d.tasks.length} matching task${d.tasks.length===1?'':'s'}${d.at_limit?' · first 100, newest first; narrow your search':''}</p>${d.tasks.map(t=>`<button class="tos-search-result" data-search-task="${esc(t.id)}"><strong>${esc(t.title||'Untitled task')}</strong><span>${esc(t.assignee_name || 'No owner recorded')} · ${esc(String(t.status||'open').replaceAll('_',' '))} · ${t.due_at?'Due '+_tosTaskDate(t.due_at):'No deadline set'}</span><span class="tos-search-open">Read full brief →</span></button>`).join('')||'<p>No matching titles in accessible active work. Try a shorter part of the title.</p>'}`;
      host.querySelectorAll('[data-search-task]').forEach(button=>button.onclick=()=>{
        const t=d.tasks.find(row=>row.id===button.dataset.searchTask);
        _tosState.byId[t.id]=t;ov.remove();openTaskDetail(t.id);
      });
    } catch(e) {
      if(!ov.isConnected)return;
      host.innerHTML=`<p>${esc(e.message||'Search could not be read.')}</p><button class="csw-mini" data-search-retry>Try search again</button>`;
      host.querySelector('[data-search-retry]').onclick=read;
    }
  };
  read();
}

function _trBindResults(body) {
  body.querySelector('#tr-search-all')?.addEventListener('click',_trSearchAll);
  body.querySelector('#tr-clear')?.addEventListener('click',()=>{_tr.search='';_tr.store='';_tr.metric='';_tr.focus=false;_tr.assignee='';_tr.due='';_trRenderView();});
  body.querySelector('#tr-retry')?.addEventListener('click',_tosLoad);
  body.querySelector('#tr-lane-add')?.addEventListener('click',openQuickAdd);
  body.querySelectorAll('[data-tr-more]').forEach(b=>b.addEventListener('click',()=>{const key=b.dataset.trMore,fold=b.closest('[data-tr-fold]')?.dataset.trFold;_tr.limits[key]=(_tr.limits[key]||6)+12;_trRenderView();if(fold)body.querySelector(`[data-tr-fold="${fold}"]`).open=true;}));
  body.querySelectorAll('[data-tr-task]').forEach(b=>b.addEventListener('click',()=>openTaskDetail(b.dataset.trTask)));
  _trBindStages(body);
  _tosBindCards();
}
async function _trLoadPages() {
  _tr.pagesLoading=true;
  try{const d=await api('/api/task-pages');if(d.ok===false)throw new Error(d.error||'Could not read pages');_tr.pages={items:d.pages||[]};_tr.pagesReadAt=Date.now();await _trPagePreview();}
  catch(e){_tr.pages={items:[],error:e.message};}
  finally{_tr.pagesLoading=false;if(_trActive()&&_tosState.view==='pages')_trRenderView();}
}
async function _trPagePreview() {
  // One bounded detail read for the featured page, never an N+1 over the index.
  const page = _tr.pages?.items.find(p=>!p.archived_at);
  const host = document.getElementById('tr-page-preview');
  if(!host) return;
  if(!page) { host.innerHTML='<div class="live-page-copy"><strong>No active living pages.</strong><p>Evidence-backed findings will appear here.</p></div>'; return; }
  let totals = null;
  try { const d=await api(`/api/task-pages/${encodeURIComponent(page.finding_key)}`); if(d.ok && d.totals)totals=d.totals; } catch(_) { /* unreadable progress stays unknown */ }
  if(!host.isConnected)return;
  const known=totals && Number.isFinite(totals.total) && Number.isFinite(totals.done) && totals.total>=0 && totals.done>=0 && totals.done<=totals.total;
  const percent=known&&totals.total?Math.round(totals.done/totals.total*100):0;
  host.innerHTML=`<div class="mini-page" aria-hidden="true"><i></i><i></i><i></i></div><div class="live-page-copy"><strong>${esc(page.title||page.finding_key)}</strong><p>${known?`${totals.done} of ${totals.total} proven done`:'Progress could not be read'} · ${esc(page.owner_dept||'Team')}</p>${known?`<div class="micro-progress" role="progressbar" aria-label="Evidence verified" aria-valuenow="${percent}" aria-valuemin="0" aria-valuemax="100"><i style="width:${percent}%"></i></div>`:''}</div>${known?`<span class="mono muted">${percent}%</span>`:''}`;
}
function _trPagesHTML() {
  if(!_tr.pages)return _tosSkel();
  if(_tr.pages.error)return `<div class="empty-state"><h2>Living pages unavailable.</h2><p>${esc(_tr.pages.error)}</p><button class="secondary" id="tr-pages-retry">Try again</button></div>`;
  return `<div class="section-intro"><div><h2>Work that proves itself.</h2><p>A claim records the handoff. An evidence rerun verifies the result.</p></div></div><div class="idea-grid">${_tr.pages.items.map(p=>`<article class="living-card"><div class="eyebrow">${esc(p.owner_dept||'Team')} · ${esc(p.status||'')}</div><h3>${esc(p.title||p.finding_key)}</h3><p>${p.last_rerun_at?'Evidence checked '+esc(new Date(p.last_rerun_at).toLocaleDateString('en-GB')):'Evidence has not been rerun yet.'}</p><a class="secondary" href="/tasks/${encodeURIComponent(p.finding_key)}">Open living page ${_trIcon('arrow')}</a></article>`).join('')||'<div class="empty-state"><h2>No living pages yet.</h2><p>They appear here when a finding creates evidence-backed work.</p></div>'}</div>`;
}

// Ideas as a first-class MODE (2026-08-01). The tab shipped by #1730
// lived only inside Team-board mode, but a lead lands on Command — the
// operator opened Tasks and the section built "so it's visible" was
// invisible from where he stands ("i dont see the changes even in
// incognito"; the code was live and byte-identical, the ENTRANCE was
// mode-gated). Same render, same binds, no board load needed —
// _tosIdeas() draws purely from _tosState.ideas.
async function _ideasModeLoad() {
  const body = document.getElementById('tos-body');
  if (!body) return;
  if (!_tosState.ideas) {
    body.innerHTML = _tosSkel();
    await _tosLoadIdeas();
  }
  if (_tosState.mode !== 'ideas') return;   // switched away mid-load
  body.innerHTML = `<div style="margin-top:12px">${_tosIdeas()}</div>`;
  _tosBindIdeas();
  // the mail desk's line on the ideas mast opens the Mail mode
  body.querySelector('[data-idmail]')?.addEventListener('click',
    () => _trActive() ? _trNavigate('email') : document.getElementById('cc-sw-mail')?.click());
}

// ═══════════════════════════════════════════════════════════════════
// THE MAIL DESK (2026-09-03) — "show me the reply in tasks or a
// surfaced tab; I click or make changes to the draft and it is edited
// and sent." One card per PERSON waiting on mailus@crux.ng, EON's
// reply under it as text he can change, three taps: Send it (or Send
// my version once he has typed), Leave it. The tap stays human —
// nothing here sends until he taps, and the send rides
// /api/mail/drafts/{id}/send → email_os → the same Gmail thread. The
// payload is address-free (who wrote, what they want; never the
// address). One orchestrated moment: the reply folds and leaves the
// desk when it is sent; everything else stays quiet. Motion respects
// prefers-reduced-motion (tasks.css).
// ═══════════════════════════════════════════════════════════════════
const _mailState = { data: null, timers: {} };
// var, not let (2026-09-10): the shell header guards this with
// `typeof _workWorkspace !== 'undefined'`, and typeof does NOT protect a let
// binding in its temporal dead zone — it throws. A var is hoisted as
// undefined, so the guard now does what it says.
var _workWorkspace = null;
function _workRoom() {
  if (!_workWorkspace) _workWorkspace = createWorkRoom({
    state: () => State, allowed: () => _mailAllowed(), api, esc,
    scope: _viewReadScope, fromHistory: () => _navFromPop,
    overlay: cswOverlay, openTask: openTaskDetail, workHTML: _tosDetailHTML,
    bindWork: (ov,t) => { _tosBindDetail(ov,t); _tosLoadDeps(ov,t); _tosLoadSubtasks(ov,t); _tosLoadComments(ov,t); },
  });
  return _workWorkspace;
}
async function _mailLoad(force) {
  const body = document.getElementById('tos-body');
  if (!body || !_mailAllowed()) return;
  return _workRoom().mountQueue(body);
}

// Who sees the desk: the owner or an admin — the same gate the server
// holds on every one of its four doors. Not a store manager's PIN
// session: mailus@ carries the bank, the lawyers and partnership terms.
const _mailAllowed = () => ['owner', 'admin'].includes(
  ((State.member && (State.member.base_role || State.member.role)) || '').toLowerCase());
// what the ledger stores (Gmail keeps CRLF) and what the browser hands
// back (LF) must read as the same words
const _mailNorm = (s) => String(s || '').replace(/\r\n?/g, '\n').trim();

async function _mailPreparedDraftsLoad(force) {
  const body = document.getElementById('tos-body');
  if (!body) return;
  if (!force && _mailState.data) { _mailRender(); return; }
  body.innerHTML = _tosSkel();
  try {
    const d = await api('/api/mail/desk');
    if (!d || !d.ok) throw new Error((d && d.error) || 'empty response');
    _mailState.data = d;
    if (_tosState.mode !== 'mail') return;   // switched away mid-load
    _mailRender();
  } catch (e) {
    if (_tosState.mode !== 'mail') return;   // never stomp the view he moved to
    body.innerHTML = `<div class="tos-empty">Couldn't open the mail desk
      (${esc(String(e.message || e).slice(0, 120))}) —
      <button class="btn-ghost" id="mail-retry" style="font-size:11.5px;padding:3px 10px;">Retry</button></div>`;
    document.getElementById('mail-retry')?.addEventListener('click', () => _mailLoad(true));
  }
}

function _mailWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function _mailCard(r, i) {
  const edited = r.status === 'edited';
  const when = _mailWhen(r.wrote_at || r.rose_at);
  const pitch = r.pile === 'pitch';
  return `<article class="mdk-card${edited ? ' is-edited' : ''}" data-id="${esc(r.id)}" style="--i:${i}">
    <div class="mdk-row">
      <div class="mdk-who">${esc(r.who || 'someone')}<small>${esc(r.domain || '')}${when ? ` · ${esc(when)}` : ''}${Number(r.messages) > 1 ? ` · ${Number(r.messages)} messages` : ''}</small></div>
      <span class="mdk-chip${pitch ? ' mdk-chip--hold' : ''}">${pitch ? 'a pitch' : 'waiting on you'}</span>
    </div>
    ${r.subject ? `<div class="mdk-subj">${esc(r.subject)}</div>` : ''}
    ${r.want ? `<p class="mdk-want">${esc(r.want)}</p>` : ''}
    <div class="mdk-lbl"><span data-lbl>${edited ? 'Your version' : "EON's reply"}</span><span class="mdk-saved" data-saved></span></div>
    <div class="mdk-draft" contenteditable="true" spellcheck="true" data-draft>${esc(r.draft_text || '')}</div>
    <div class="mdk-acts">
      <button class="mdk-btn mdk-btn--go" data-act="send">${edited ? 'Send my version' : 'Send it'}</button>
      <button class="mdk-btn mdk-btn--quiet" data-act="leave">Leave it</button>
      ${r.thread_link ? `<a class="mdk-gm" href="${esc(r.thread_link)}" target="_blank" rel="noopener">Open in Gmail</a>` : ''}
    </div>
    <p class="mdk-explain">${pitch
      ? 'Send it sends this short no from mailus@crux.ng on the same thread. Leave it keeps the draft in Gmail and EON stays quiet about this one.'
      : 'Send it sends these words from mailus@crux.ng on the same thread. Leave it keeps the draft in Gmail and EON stays quiet about this one.'}</p>
  </article>`;
}

function _mailRender() {
  if(tosHouse() && (!_trActive() || _tosState.mode!=='mail'))return;
  const body = document.getElementById('tos-body');
  const d = _mailState.data;
  if (!body || !d) return;
  const rows = Array.isArray(d.rows) ? d.rows : [];
  // the count is the whole number waiting; the cards are the first page
  const n = Math.max(Number(d.waiting) || 0, rows.length);
  const filed = Number(d.filed) || 0;
  body.innerHTML = `<div class="mdk">
    <div class="mdk-mast">mail<span class="mdk-dot">.</span></div>
    <div class="mdk-line"><span class="mdk-count" data-count>${n}</span>${n === 1 ? 'person' : 'people'} waiting on your word${filed ? `<span class="mdk-line__s"> · and ${filed} others filed without you</span>` : ''}</div>
    ${rows.length ? `<div class="mdk-cards" data-cards>${rows.map(_mailCard).join('')}</div>`
        : `<div class="mdk-empty"><div class="mdk-empty__mark"></div><b>Nobody is waiting on you.</b>
           <span>EON reads mailus@crux.ng every fifteen minutes. When a person writes, their reply will be here with a Send button under it.</span></div>`}
  </div>`;
  _mailCountTo(body.querySelector('[data-count]'), n);
  _mailBind(body);
}

// the count climbs to its number — a small tell that the desk is live
function _mailCountTo(el, n) {
  if (!el) return;
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || n <= 1) { el.textContent = String(n); return; }
  const t0 = performance.now(), ms = 520;
  const step = (t) => {
    const k = Math.min(1, (t - t0) / ms);
    el.textContent = String(Math.round(n * (1 - Math.pow(1 - k, 3))));
    if (k < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function _mailSetCount(delta) {
  const d = _mailState.data;
  if (!d) return;
  d.waiting = Math.max(0, (Number(d.waiting) || 0) + delta);
  const el = document.querySelector('.mdk [data-count]');
  if (el) el.textContent = String(d.waiting);
  const line = document.querySelector('.mdk-line');
  if (line) {
    const word = line.childNodes[1];
    if (word && word.nodeType === 3) word.textContent = `${d.waiting === 1 ? 'person' : 'people'} waiting on your word`;
  }
  if (d.waiting === 0 && !document.querySelector('.mdk-card:not(.is-posting):not(.is-leaving)')) {
    setTimeout(() => { if (_tosState.mode === 'mail' && _mailState.data && _mailState.data.waiting === 0) { _mailState.data.rows = []; _mailRender(); } }, 800);
  }
}

function _mailBind(body) {
  body.querySelectorAll('.mdk-card').forEach((card) => {
    const id = card.getAttribute('data-id');
    const draft = card.querySelector('[data-draft]');
    const sendBtn = card.querySelector('[data-act="send"]');
    const leaveBtn = card.querySelector('[data-act="leave"]');
    const saved = card.querySelector('[data-saved]');
    const lbl = card.querySelector('[data-lbl]');
    const row = (_mailState.data.rows || []).find(r => r.id === id) || {};
    const original = _mailNorm(row.draft_text);

    // typing = his version; saved to the ledger (and Gmail) when he pauses
    draft?.addEventListener('input', () => {
      const text = _mailNorm(draft.innerText);
      const changed = text !== original || row.status === 'edited';
      card.classList.toggle('is-edited', changed);
      if (sendBtn) sendBtn.textContent = changed ? 'Send my version' : 'Send it';
      if (lbl) lbl.textContent = changed ? 'Your version' : "EON's reply";
      clearTimeout(_mailState.timers[id]);
      if (!changed) return;
      _mailState.timers[id] = setTimeout(async () => {
        try {
          const res = await api(`/api/mail/drafts/${encodeURIComponent(id)}`, {
            method: 'PATCH', body: JSON.stringify({ draft_text: text }) });
          row.status = 'edited'; row.draft_text = text;
          if (saved) { saved.textContent = res && res.mirrored ? 'saved · in Gmail too' : 'saved'; saved.classList.add('on'); setTimeout(() => saved.classList.remove('on'), 2200); }
        } catch (e) {
          if (saved) { saved.textContent = 'not saved — try again'; saved.classList.add('on'); }
        }
      }, 900);
    });

    sendBtn?.addEventListener('click', async () => {
      if (sendBtn.disabled) return;
      const text = draft ? _mailNorm(draft.innerText) : '';
      if (!text) { showToast('There are no words to send.'); return; }
      sendBtn.disabled = true; if (leaveBtn) leaveBtn.disabled = true;
      const was = sendBtn.textContent; sendBtn.textContent = 'Sending…';
      clearTimeout(_mailState.timers[id]);
      try {
        const res = await api(`/api/mail/drafts/${encodeURIComponent(id)}/send`, {
          method: 'POST', body: JSON.stringify({ draft_text: text }) });
        if (!res || !res.ok) throw new Error((res && (res.message || res.error)) || 'not sent');
        card.classList.add('is-posting');
        if (draft) draft.setAttribute('contenteditable', 'false');
        showToast(text !== original ? 'Sent your version. EON keeps the change.' : 'Sent. Filed in the reply ledger.');
        _mailSetCount(-1);
        setTimeout(() => card.remove(), 760);
      } catch (e) {
        const msg = (e && e.detail && (e.detail.message || e.detail.error))
          || (e && e.message) || 'Gmail did not accept the send. Nothing left.';
        showToast(String(msg).slice(0, 160));
        sendBtn.disabled = false; if (leaveBtn) leaveBtn.disabled = false;
        sendBtn.textContent = was;
      }
    });

    leaveBtn?.addEventListener('click', async () => {
      if (leaveBtn.disabled) return;
      leaveBtn.disabled = true; if (sendBtn) sendBtn.disabled = true;
      clearTimeout(_mailState.timers[id]);   // a pending edit never lands on a closed row
      try {
        const res = await api(`/api/mail/drafts/${encodeURIComponent(id)}/leave`, { method: 'POST', body: '{}' });
        if (!res || !res.ok) throw new Error((res && (res.message || res.error)) || 'not left');
        card.classList.add('is-leaving');
        showToast('Left it. EON stays quiet on this one.');
        _mailSetCount(-1);
        setTimeout(() => card.remove(), 500);
      } catch (e) {
        showToast(String((e && e.message) || 'Could not leave it').slice(0, 160));
        leaveBtn.disabled = false; if (sendBtn) sendBtn.disabled = false;
      }
    });
  });
}

// ── THE BRIEF (BR-1/2, operator directive 2026-07-26) ──────────────
// "EON already did the desk work; you do the decisions." Third mode of
// the Tasks page (leads only): a business breakdown per lens —
// yesterday / 7 / 30 / 90 days — plus the needs-you schedule. The
// done-for-you artifact rail (excel packs, drafted emails, PO drafts)
// lands in BR-3; until then the rail is an honest teaching card, never
// a dead button. Voice + the one-report fold land in BR-5.
const _briefState = {
  lens: localStorage.getItem('tos.brief.lens') || 'yesterday',
  data: null,
};
const _BRIEF_LENSES = [['yesterday', 'Yesterday'], ['last_7d', '7 days'],
                       ['last_30d', '30 days'], ['last_90d', '90 days']];

// BR-5: the brief as speech — plain sentences off the SAME loaded data
// (₦2.9M reads as "2.9 million naira"; speakText clips at 4,500 chars).
function _ngnSpeech(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 'unknown';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(1)} billion naira`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)} million naira`;
  if (abs >= 1e3) return `${Math.round(v / 1e3)} thousand naira`;
  return `${Math.round(v)} naira`;
}

function _briefSpeechText(d) {
  const label = (_BRIEF_LENSES.find(l => l[0] === d.lens) || ['', d.lens])[1];
  const biz = d.business || {};
  const m = biz.mer || {};
  const cust = biz.customers || {};
  const stores = (biz.by_store && biz.by_store.locations) || [];
  const ny = d.needs_you || {};
  const parts = [`Your brief, ${label.toLowerCase()}.`];
  if (m.revenue_ngn != null) {
    let s = `Revenue ${_ngnSpeech(m.revenue_ngn)}`;
    if (m.order_count != null) s += ` across ${m.order_count} orders`;
    if (m.mer != null) s += `, MER ${Number(m.mer).toFixed(1)}`;
    parts.push(s + '.');
  }
  if (stores.length) {
    const top = stores[0];
    parts.push(`${top.location || 'Top store'} led with ${_ngnSpeech(top.revenue_ngn)}.`);
  }
  if (cust.new_customers != null || cust.returning_customers != null) {
    parts.push(`${cust.new_customers || 0} new customers, `
      + `${cust.returning_customers || 0} returning.`);
  }
  const appr = (ny.approvals || []).length;
  const due = (ny.tasks_due || []).length;
  // The overdue total is the server's exact count, not the length of the
  // short list it sends — the list is capped at five on purpose.
  const od = ny.overdue_count || 0;
  const dec = (ny.decisions || []).length;
  const cnt = (ny.counts_to_review || []).length;
  if (appr || due || dec || od || cnt) {
    const bits = [];
    if (cnt) bits.push(`${cnt} stock count${cnt === 1 ? '' : 's'} waiting for your review`);
    if (appr) bits.push(`${appr} approval${appr === 1 ? '' : 's'} waiting`);
    if (due) bits.push(`${due} task${due === 1 ? '' : 's'} due in the next two days`);
    if (od) bits.push(`${od} overdue`);
    if (dec) bits.push(`${dec} decision${dec === 1 ? '' : 's'} open`);
    parts.push('Needs you: ' + bits.join('; ') + '.');
  } else {
    parts.push('Nothing needs you right now. The desk is clear.');
  }
  return parts.join(' ');
}

async function _briefLoad(force) {
  const body = document.getElementById('tos-body');
  if(tosHouse() && (!body?.isConnected || _tosState.mode!=='brief'))return;
  if (!body) return;
  if (!force && _briefState.data
      && _briefState.data.lens === _briefState.lens) {
    _briefRender();
    return;
  }
  body.innerHTML = _tosSkel();
  try {
    const d = await api(`/api/tasks/brief?lens=${encodeURIComponent(_briefState.lens)}`);
    if (!d || !d.ok) throw new Error((d && d.error) || 'empty response');
    if(tosHouse() && (!body?.isConnected || _tosState.mode!=='brief'))return;
    _briefState.data = d;
    _briefRender();
  } catch (e) {
    if(tosHouse() && (!body?.isConnected || _tosState.mode!=='brief'))return;
    body.innerHTML = `<div class="tos-empty">Couldn't load the brief
      (${esc(String(e.message || e).slice(0, 120))}) —
      <button class="btn-ghost" id="brief-retry" style="font-size:11.5px;padding:3px 10px;">Retry</button></div>`;
    document.getElementById('brief-retry')
      ?.addEventListener('click', () => _briefLoad(true));
  }
}

function _briefRender() {
  if(tosHouse() && (!_trActive() || _tosState.mode!=='brief'))return;
  const body = document.getElementById('tos-body');
  const d = _briefState.data;
  if (!body || !d) return;
  const biz = d.business || {};
  const m = biz.mer || {};
  const cust = biz.customers || {};
  const stores = (biz.by_store && biz.by_store.locations) || [];
  const prods = (biz.top_products && (biz.top_products.items
                 || biz.top_products.products)) || biz.top_products || [];
  const refunds = biz.refunds || {};
  const ny = d.needs_you || {};
  const approvals = ny.approvals || [];
  const due = ny.tasks_due || [];
  const overdue = ny.tasks_overdue || [];
  const overdueCount = ny.overdue_count || 0;
  const decisions = ny.decisions || [];
  const staffAsks = ny.staff_asks || [];
  const countsToReview = ny.counts_to_review || [];

  // Pulse strip: the lens headline — revenue, MER, orders — and the
  // voice read (BR-5): same TTS lane as chat replies and the 6am
  // voice card; the button only exists when the lane does.
  const pulse = document.getElementById('tos-pulse');
  if (pulse) {
    pulse.className = 'tos-pulse ccf-strip';
    pulse.innerHTML = `
      <div class="ccf-hero">
        <div class="ccf-hero__n">${m.revenue_ngn != null ? ngn(m.revenue_ngn, { compact: true }) : '—'}</div>
        <div class="ccf-hero__l">revenue · ${esc((_BRIEF_LENSES.find(l => l[0] === d.lens) || ['', d.lens])[1].toLowerCase())}
          ${m.mer != null ? `<span class="ccf-hero__ngn">MER ${Number(m.mer).toFixed(1)}</span>` : ''}
          ${m.order_count != null ? `<span class="ccf-hero__ngn">${m.order_count} order${m.order_count === 1 ? '' : 's'}</span>` : ''}
        </div>
        <button class="btn-ghost" id="brief-voice" ${_voiceTtsAttributes('font-size:12px;padding:6px 13px;margin-left:auto;')} aria-label="Read this brief aloud">▸ Read it to me</button>
      </div>`;
    document.getElementById('brief-voice')
      ?.addEventListener('click', () => speakText(_briefSpeechText(d)));
  }

  const lensChips = `<div class="date-chips" id="brief-lens" style="margin:0 0 14px;">
    ${_BRIEF_LENSES.map(([k, l]) =>
      `<button class="chip ${k === d.lens ? 'chip-active' : ''}" data-lens="${k}">${l}</button>`).join('')}
  </div>`;

  // A grid, not a wrapping flex (2026-08-20, IMG_9375): at 402px any
  // title wider than ~300px threw the time onto its own left-aligned
  // line and doubled the row. The title wraps inside its column; the
  // value stays right, on the first line. Rules: #tos .brief-row.
  // `wrap` is for the one free-text value (an approval's kind): it
  // wraps inside its column instead of ellipsizing; money + times
  // stay on one line.
  const row = (label, value, sub, wrap) => `
    <div class="brief-row">
      <span class="brief-row__l">${label}</span><span class="brief-row__v${wrap ? ' brief-row__v--wrap' : ''}">${value}${sub ? ` <span class="a3-ctx">${sub}</span>` : ''}</span>
    </div>`;

  const storeRows = stores.length
    ? stores.map(l => row(esc(l.location || l.country || '—'),
        ngn(l.revenue_ngn, { compact: true }),
        (l.delta_pct != null ? `${l.delta_pct > 0 ? '+' : ''}${Number(l.delta_pct).toFixed(0)}%` : '')))
        .join('')
    : '<div class="a3-ctx" style="padding:6px 0;">No store revenue in this window.</div>';

  const prodRows = (Array.isArray(prods) ? prods : []).slice(0, 5).map(p =>
    row(esc(p.product_name || p.title || '—'),
        p.revenue_ngn != null ? ngn(p.revenue_ngn, { compact: true })
          : `${p.units_sold || 0} sold`,
        p.revenue_ngn != null && p.units_sold != null ? `${p.units_sold} sold` : ''))
    .join('') || '<div class="a3-ctx" style="padding:6px 0;">No product sales in this window.</div>';

  // get_customers speaks new/returning + avg_* (live-verified shape —
  // NOT the home tile's remapped keys).
  const custNew = cust.new_customers, custRet = cust.returning_customers;
  const custTotal = cust.shopify_truth_customers != null
    ? cust.shopify_truth_customers
    : (custNew != null || custRet != null) ? (custNew || 0) + (custRet || 0) : null;
  const custRepeat = cust.avg_repeat_rate_pct;
  const custAov = cust.avg_aov_ngn;
  const custLine = custTotal != null
    ? `${custTotal} customer${custTotal === 1 ? '' : 's'}`
      + (custNew != null ? ` · ${custNew} new` : '')
      + (custRet != null ? ` · ${custRet} returning` : '')
      + (custRepeat != null ? ` · ${Number(custRepeat).toFixed(0)}% repeat` : '')
      + (custAov != null ? ` · AOV ${ngn(custAov, { compact: true })}` : '')
    : 'No customer data in this window.';

  const refundLine = refunds.total_refunds != null
    ? `${refunds.total_refunds} refund${refunds.total_refunds === 1 ? '' : 's'}`
      + (refunds.rate_pct != null ? ` · ${Number(refunds.rate_pct).toFixed(1)}% of orders` : '')
    : 'No refund data in this window.';

  // Merge-on-tap cards (mig 569): a critical-tier build waits here with
  // its plain-words brief and ONE Merge button (one-click law — the tap
  // IS the confirmation; delegation auth is server-side on decide).
  const mergeCard = (a) => `
    <div class="brief-merge-card" data-action-id="${esc(String(a.id))}"
         style="border:1px solid var(--line);border-radius:10px;padding:10px 12px;margin:8px 0;">
      <div style="font-weight:600;">${esc(a.title || 'A build is ready')}
        ${a.pr_number ? `<span class="a3-ctx">PR #${esc(String(a.pr_number))}</span>` : ''}</div>
      ${a.plain_brief ? `<div style="white-space:pre-line;margin:6px 0 2px;">${esc(a.plain_brief)}</div>` : ''}
      ${a.tier_reason ? `<div class="a3-ctx" style="margin:2px 0;">${esc(a.tier_reason)}</div>` : ''}
      <div style="display:flex;gap:8px;align-items:center;margin-top:8px;flex-wrap:wrap;">
        <button class="btn-primary brief-merge-go" style="font-size:12px;padding:6px 14px;">Merge</button>
        <button class="btn-ghost brief-merge-no" style="font-size:12px;padding:6px 12px;">Not now</button>
        ${a.pr_url ? `<a href="${esc(a.pr_url)}" target="_blank" rel="noopener" class="a3-ctx">view the code</a>` : ''}
      </div>
    </div>`;
  const apprRows = approvals.map(a => a.action_type === 'pr_merge_tap'
    ? mergeCard(a)
    : row(esc(a.title), `<span class="a3-ctx">${esc(String(a.action_type || '').replace(/_/g, ' '))}</span>`, '', true))
    .join('');
  // Staff build asks (mig 637): the same R-id Daniel can decide from
  // WhatsApp ("approve R-47") — the card and the reply run the same
  // server decision road, so both surfaces always agree. One-tap
  // buttons: the staffer is told either way, approve also queues the
  // build.
  const askCard = (a) => `
    <div class="brief-ask-card" data-req-id="${esc(String(a.id))}"
         style="border:1px solid var(--line);border-radius:10px;padding:10px 12px;margin:8px 0;">
      <div style="font-weight:600;">${a.req_no != null ? `R-${esc(String(a.req_no))} · ` : ''}${esc(a.staff_name || 'A staffer')}
        ${a.role ? `<span class="a3-ctx">${esc(a.role)}</span>` : ''}</div>
      <div style="margin:6px 0 2px;">${esc(a.text || '')}</div>
      <div style="display:flex;gap:8px;align-items:center;margin-top:8px;flex-wrap:wrap;">
        <button class="btn-primary brief-ask-yes" style="font-size:12px;padding:6px 14px;">Approve</button>
        <button class="btn-ghost brief-ask-no" style="font-size:12px;padding:6px 12px;">Reject</button>
        <span class="a3-ctx">or reply on WhatsApp: approve ${a.req_no != null ? `R-${esc(String(a.req_no))}` : 'it'}</span>
      </div>
    </div>`;
  const askRows = staffAsks.map(askCard).join('');
  // Blind stock counts waiting for this person: every line counted and
  // they did not count it (Head of Operations, 30 Sep: the count check
  // was nowhere on the dashboard). Review opens the count on its review
  // step in Inventory → Counts.
  const countRows = countsToReview.map(c => row(
    esc([c.product_type || c.name, c.location_name].filter(Boolean).join(' · ') || 'Stock count'),
    `<a class="btn-ghost" href="/inventory?v=counts&count=${encodeURIComponent(c.id)}" style="font-size:11px;padding:3px 10px;">Review</a>`,
    `${c.lines || 0} line${c.lines === 1 ? '' : 's'}`)).join('');
  // Two lists, never one. "Due next" is a forward window; overdue is its
  // own fold with its own cap and an honest total. Before 2026-08-11 they
  // shared eight slots sorted oldest-first, so 200 overdue rows starved
  // the window completely and the step showed neither today's work nor
  // the worst of the backlog.
  const _dueWhen = (t) => {
    const iso = String(t.due_at || '');
    const d = new Date(iso);
    if (isNaN(d)) return esc(iso.slice(0, 10));
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    return sameDay ? `today ${hh}:${mm}` : `tomorrow ${hh}:${mm}`;
  };
  const dueRows = due.map(t => row(
    esc(t.title || 'Task'),
    `<span class="a3-ctx">${_dueWhen(t)}</span>`)).join('');
  const _daysLate = (t) => {
    const d = new Date(String(t.due_at || ''));
    if (isNaN(d)) return '';
    const n = Math.floor((Date.now() - d.getTime()) / 86400000);
    return n <= 0 ? 'today' : `${n}d late`;
  };
  const overdueRows = overdue.map(t => row(
    esc(t.title || 'Task'),
    `<span style="color:var(--danger);font-weight:600;">${esc(_daysLate(t))}</span>`
  )).join('');

  // Stepped project walk (operator 2026-07-27: "it can't look
  // overwhelming … steps and break down, it shouldn't look like a
  // lump"). Each section is a numbered <details> step whose CLOSED
  // state already answers the question in one line — the whole brief
  // scans in six lines; expanding is opt-in. Native details/summary:
  // no open/closed state to manage, keyboard + screen-reader free.
  const step = (n, title, answer, inner, open) => `
    <details class="brief-step"${open ? ' open' : ''}>
      <summary class="brief-step-sum">
        <span class="brief-step-n">${String(n).padStart(2, '0')}</span>
        <span class="brief-step-t">${title}
          <span class="brief-step-a">${answer}</span></span>
        <span class="brief-step-chev" aria-hidden="true">▾</span>
      </summary>
      <div class="brief-step-body">${inner}</div>
    </details>`;

  // One-line answers for the closed state — each is the sentence the
  // operator would have dug the card open to find.
  const topStore = stores[0];
  const storeAns = topStore
    ? `${esc(topStore.location || topStore.country || 'Top store')} led · ${ngn(topStore.revenue_ngn, { compact: true })}`
      + (stores.length > 1 ? ` · ${stores.length} stores` : '')
    : 'No store revenue in this window';
  const custAns = custTotal != null
    ? `${custTotal} customer${custTotal === 1 ? '' : 's'}`
      + (custNew != null ? ` · ${custNew} new` : '')
      + (custRet != null ? ` · ${custRet} returning` : '')
    : 'No customer data in this window';
  const topProd = (Array.isArray(prods) ? prods : [])[0];
  const prodAns = topProd
    ? `${esc((topProd.product_name || topProd.title || '—').slice(0, 36))}`
      + (topProd.revenue_ngn != null ? ` · ${ngn(topProd.revenue_ngn, { compact: true })}` : '')
    : 'No product sales in this window';
  const refundAns = refunds.total_refunds != null
    ? (refunds.total_refunds === 0 ? 'None — clean window'
        : `${refunds.total_refunds} refund${refunds.total_refunds === 1 ? '' : 's'}`
          + (refunds.rate_pct != null ? ` · ${Number(refunds.rate_pct).toFixed(1)}%` : ''))
    : 'No refund data in this window';
  const nyCount = approvals.length + due.length + overdueCount
    + decisions.length + staffAsks.length + countsToReview.length;
  const nyBits = [];
  if (countsToReview.length) nyBits.push(`${countsToReview.length} count${countsToReview.length === 1 ? '' : 's'} to review`);
  if (approvals.length) nyBits.push(`${approvals.length} approval${approvals.length === 1 ? '' : 's'}`);
  if (due.length) nyBits.push(`${due.length} due next`);
  if (overdueCount) nyBits.push(`${overdueCount} overdue`);
  if (decisions.length) nyBits.push(`${decisions.length} decision${decisions.length === 1 ? '' : 's'}`);
  if (staffAsks.length) nyBits.push(`${staffAsks.length} staff ask${staffAsks.length === 1 ? '' : 's'}`);
  const nyAns = nyCount ? nyBits.join(' · ') : 'Desk clear — nothing waiting';
  // The action step opens itself only when there is action to take.
  const nyOpen = nyCount > 0;

  body.innerHTML = `
    ${lensChips}
    <div class="brief-steps">
    ${step(1, 'Where the money came from', storeAns, storeRows)}
    ${step(2, 'Who bought', custAns, `<div style="padding:6px 0;">${custLine}</div>`)}
    ${step(3, 'What sold', prodAns, prodRows)}
    ${step(4, 'What came back', refundAns, `<div style="padding:6px 0;">${refundLine}</div>`)}
    ${step(5, 'What needs you', nyAns,
      (apprRows || dueRows || overdueRows || decisions.length || askRows || countRows)
        ? (countRows ? `<div class="a3-ctx" style="margin:4px 0 2px;">Stock counts waiting for your review — every line counted, by someone else</div>${countRows}` : '')
          + (dueRows ? `<div class="a3-ctx" style="margin:10px 0 2px;">Do next — due in the next 48 hours</div>${dueRows}` : '')
          + (overdueRows ? `<div class="a3-ctx" style="margin:10px 0 2px;">Overdue — worst first${overdueCount > overdue.length ? `, showing ${overdue.length} of ${overdueCount}` : ''}</div>${overdueRows}` : '')
          + (askRows ? `<div class="a3-ctx" style="margin:10px 0 2px;">Staff asks — approve sends it to the build queue</div>${askRows}` : '')
          + (apprRows ? `<div class="a3-ctx" style="margin:10px 0 2px;">Approvals waiting</div>${apprRows}` : '')
          + (decisions.length ? `<div class="a3-ctx" style="margin:10px 0 2px;">Decisions open</div>`
              + decisions.map(x => row(esc(x.title),
                  '<button class="btn-ghost" data-go="recommendations" style="font-size:11px;padding:3px 10px;">Decide</button>')).join('') : '')
        : '<div style="padding:6px 0;">Nothing needs you right now — the desk is clear.</div>',
      nyOpen)}
    ${step(6, 'Take it with you', 'Excel pack · Email · PO draft',
      `<div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;padding:6px 0;">
        <button class="btn-primary" id="brief-pack-dl" style="font-size:12px;padding:6px 13px;">Download Excel pack</button>
        <button class="btn-ghost" id="brief-pack-mail" style="font-size:12px;padding:6px 13px;">Email it to me</button>
        <span class="a3-ctx">the numbers behind this lens as a workbook — summary, by-store, top products</span>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;padding:8px 0 6px;border-top:1px solid var(--line);">
        <button class="btn-ghost" id="brief-po-dl" style="font-size:12px;padding:6px 13px;">Download PO draft</button>
        <button class="btn-secondary" id="brief-po-file" style="font-size:12px;padding:6px 13px;">File PO for approval</button>
        <span class="a3-ctx">reorder engine, per vendor — filing opens ONE approval task; nothing is ordered until you approve</span>
      </div>
      <div class="a3-ctx" style="padding:2px 0 4px;">Next on this rail: pre-drafted vendor emails — released only by your tap.</div>`)}
    </div>
    <div class="a3-ctx" style="margin-top:4px;">Generated ${esc(String(d.generated_at || '').slice(11, 16))} UTC · numbers are the same
      sb_analytics reads the 6am briefing uses.</div>`;

  document.querySelectorAll('#brief-lens .chip').forEach(b =>
    b.addEventListener('click', () => {
      const k = b.dataset.lens;
      if (!k || k === _briefState.lens) return;
      _briefState.lens = k;
      try { localStorage.setItem('tos.brief.lens', k); } catch (_) {}
      _briefLoad(true);
    }));

  // Merge-on-tap decide (mig 569): one tap POSTs the existing
  // permissions decide endpoint — approve fires the merge workflow
  // via repository_dispatch, deny parks the card. Server re-checks
  // authorization (owner or delegation), so this is render-side only.
  document.querySelectorAll('.brief-merge-card').forEach(card => {
    const actionId = card.dataset.actionId;
    const send = async (decision, btn) => {
      if (!actionId || btn.disabled) return;
      btn.disabled = true;
      const was = btn.textContent;
      btn.textContent = decision === 'approve' ? 'Merging…' : 'Parking…';
      try {
        const res = await api(`/api/permissions/${encodeURIComponent(actionId)}/decide`, {
          method: 'POST', body: JSON.stringify({ decision }) });
        if (res && (res.ok || res.already_decided)) {
          const ex = res.executor_result;
          if (decision === 'approve' && ex && ex.ok === false) {
            showToast(`Merge dispatch failed: ${String(ex.error || 'unknown').slice(0, 140)}`);
          } else if (decision === 'approve' && ex && ex.pr_state
                     && ex.pr_state !== 'open') {
            // Ghost card: the PR was merged/closed by another actor
            // after the card was minted (2026-08-01: three of these).
            showToast(ex.already_merged
              ? 'Already merged — that card was stale. It leaves the list now.'
              : 'That PR was closed elsewhere — the card was stale and leaves the list now.');
          } else {
            showToast(decision === 'approve'
              ? 'Merging — GitHub is doing it now. The build brief lands when it ships.'
              : 'Parked. The PR stays open for whenever you want it.');
          }
          _briefLoad(true);
        } else {
          throw new Error((res && res.error) || 'decide failed');
        }
      } catch (e) {
        btn.disabled = false;
        btn.textContent = was;
        showToast(`Couldn't record that: ${String(e.message || e).slice(0, 120)}`);
      }
    };
    card.querySelector('.brief-merge-go')
      ?.addEventListener('click', ev => send('approve', ev.currentTarget));
    card.querySelector('.brief-merge-no')
      ?.addEventListener('click', ev => send('deny', ev.currentTarget));
  });

  // Staff-ask decide (mig 637): one tap runs the same server road as
  // the WhatsApp reply — the staffer is told either way, approve also
  // files the build. Reject needs no note (operator directive
  // 2026-07-30); a note can ride the WhatsApp reply instead.
  document.querySelectorAll('.brief-ask-card').forEach(card => {
    const reqId = card.dataset.reqId;
    const send = async (status, btn) => {
      if (!reqId || btn.disabled) return;
      btn.disabled = true;
      const was = btn.textContent;
      btn.textContent = '…';
      try {
        const res = await api(`/api/staff-line/requests/${encodeURIComponent(reqId)}/decide`, {
          method: 'POST', body: JSON.stringify({ status }) });
        if (res && res.ok) {
          // Read what the server actually did, never assert it. The
          // route returns feature_request_id and staffer_told for
          // exactly this reason, and the WhatsApp confirmation already
          // degrades on both — this toast used to hardcode the happy
          // path, so a swallowed filing failure or an unreachable
          // staffer still read "they've been told and it's in the
          // build queue" on the one surface Daniel was looking at.
          const told = res.staffer_told
            ? 'they\'ve been told'
            : 'I couldn\'t reach them — tell them yourself';
          showToast(status === 'approved'
            ? `Approved — ${told} and ${res.feature_request_id ? 'it\'s in the build queue.' : 'it\'s marked approved.'}`
            : `Rejected — ${told}.`);
          _briefLoad(true);
        } else {
          throw new Error((res && res.error) || 'decide failed');
        }
      } catch (e) {
        btn.disabled = false;
        btn.textContent = was;
        showToast(`Couldn't record that: ${String(e.message || e).slice(0, 120)}`);
      }
    };
    card.querySelector('.brief-ask-yes')
      ?.addEventListener('click', ev => send('approved', ev.currentTarget));
    card.querySelector('.brief-ask-no')
      ?.addEventListener('click', ev => send('declined', ev.currentTarget));
  });

  // BR-3a: the pack producers. Download = authed fetch → blob (the
  // books-export pattern); email = one tap to the SIGNED-IN member's
  // own address — the door never invents a recipient.
  const dl = document.getElementById('brief-pack-dl');
  dl?.addEventListener('click', async () => {
    dl.disabled = true;
    const was = dl.textContent;
    dl.textContent = 'Building…';
    try {
      const headers = { Authorization: `Bearer ${State.jwt}` };
      if (State.tenant?.slug) headers['X-Tenant-Slug'] = State.tenant.slug;
      const resp = await fetch(
        `${API}/api/tasks/brief/pack.xlsx?lens=${encodeURIComponent(d.lens)}`,
        { headers });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const cd = resp.headers.get('Content-Disposition') || '';
      const fname = (cd.match(/filename="([^"]+)"/) || [])[1]
        || `crux_brief_${d.lens}.xlsx`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      showToast('Pack downloaded');
    } catch (e) {
      showToast('Pack failed: ' + (e.message || e));
    }
    dl.disabled = false;
    dl.textContent = was;
  });
  // BR-3b: the PO producers. Download = the draft workbook; File =
  // ONE procurement request through the existing money door (approval
  // task + owner ping — nothing ordered until his approve).
  const poDl = document.getElementById('brief-po-dl');
  poDl?.addEventListener('click', async () => {
    poDl.disabled = true;
    const was = poDl.textContent;
    poDl.textContent = 'Building…';
    try {
      const headers = { Authorization: `Bearer ${State.jwt}` };
      if (State.tenant?.slug) headers['X-Tenant-Slug'] = State.tenant.slug;
      const resp = await fetch(`${API}/api/tasks/brief/po_draft.xlsx`, { headers });
      if (resp.status === 404) { showToast('Nothing to reorder right now'); }
      else if (!resp.ok) { throw new Error(`HTTP ${resp.status}`); }
      else {
        const blob = await resp.blob();
        const cd = resp.headers.get('Content-Disposition') || '';
        const fname = (cd.match(/filename="([^"]+)"/) || [])[1] || 'crux_po_draft.xlsx';
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
        showToast('PO draft downloaded');
      }
    } catch (e) {
      showToast('PO draft failed: ' + (e.message || e));
    }
    poDl.disabled = false;
    poDl.textContent = was;
  });
  const poFile = document.getElementById('brief-po-file');
  poFile?.addEventListener('click', async () => {
    poFile.disabled = true;
    const was = poFile.textContent;
    poFile.textContent = 'Filing…';
    try {
      const r = await api('/api/tasks/brief/po_request', { method: 'POST', body: '{}' });
      showToast(`Filed for approval — ${r.lines} line${r.lines === 1 ? '' : 's'}`);
    } catch (e) {
      const msg = e.status === 404 ? 'Nothing to reorder right now'
        : 'Filing failed: ' + (e.body?.detail || e.message || e);
      showToast(msg);
    }
    poFile.disabled = false;
    poFile.textContent = was;
  });
  const mail = document.getElementById('brief-pack-mail');
  mail?.addEventListener('click', async () => {
    const to = State.member?.email;
    if (!to) { showToast('No email on your account'); return; }
    mail.disabled = true;
    const was = mail.textContent;
    mail.textContent = 'Sending…';
    try {
      await api('/api/tasks/brief/email_pack', {
        method: 'POST',
        body: JSON.stringify({ lens: d.lens, to }),
      });
      showToast(`Pack sent to ${to}`);
    } catch (e) {
      showToast('Send failed: ' + (e.body?.detail || e.message || e));
    }
    mail.disabled = false;
    mail.textContent = was;
  });
}

// ── CEO Command Center (leads-only mode of the Tasks page) ─────────
// One /api/command/center payload: decide-now queue, EON approvals,
// inventory irregularities, staff watch, ad leakage, department chips.
// Actions reuse existing endpoints (tasks / recommendations / alerts).

function _ccMountSwitch() {
  // Mounts on the TOPBAR, not inside .tos-grow (operator 2026-08-07:
  // "top bar is over lapping"). .cc-switch is an inline-flex that
  // cannot shrink: at 402px it measured 248px inside a 194px
  // .tos-grow — 66px of overflow that landed on the sound and ＋
  // buttons. As a direct child of the wrapping topbar it takes its
  // own full-width row instead of running over the chrome.
  // 2026-08-20 (operator IMG_9375, "the task page is poorly designed
  // for mobile"): as a wrapped flex item of the STICKY topbar the
  // switch rendered, on his iPhone, in the same band as the ₦ hero —
  // the bar's box stayed one row tall and the wrapped row overflowed
  // into the pulse strip (Chromium wraps it correctly; the WebKit
  // trigger is a hypothesis). So it is no longer a flex item at all:
  // its own block row UNDER the bar, no wrap, no sticky — the same in
  // every engine. Still mounted off the topbar, never .tos-grow.
  const bar = document.querySelector('#tos .tos-topbar');
  if (!bar || document.getElementById('cc-switch')) return;
  bar.insertAdjacentHTML('afterend',
    `<div class="tos-modebar" id="tos-modebar"><div class="cc-switch" id="cc-switch">
      <button id="cc-sw-command" class="${_tosState.mode === 'command' ? 'on' : ''}">Command</button>
      <button id="cc-sw-brief" class="${_tosState.mode === 'brief' ? 'on' : ''}">Brief</button>
      <button id="cc-sw-ideas" class="${_tosState.mode === 'ideas' ? 'on' : ''}">Ideas</button>
      ${_mailAllowed() && !tosHouse() ? `<button id="cc-sw-mail" class="${_tosState.mode === 'mail' ? 'on' : ''}">Mail</button>` : ''}
      <button id="cc-sw-staff" class="${_tosState.mode === 'staff' ? 'on' : ''}">Team board</button>
    </div></div>`);
  const setMode = (m) => {
    if (_tosState.mode === m) return;
    _tosState.mode = m; localStorage.setItem('tos.mode', m);
    document.getElementById('cc-sw-command')?.classList.toggle('on', m === 'command');
    document.getElementById('cc-sw-brief')?.classList.toggle('on', m === 'brief');
    document.getElementById('cc-sw-ideas')?.classList.toggle('on', m === 'ideas');
    document.getElementById('cc-sw-mail')?.classList.toggle('on', m === 'mail');
    document.getElementById('cc-sw-staff')?.classList.toggle('on', m === 'staff');
    const body = document.getElementById('tos-body');
    if (body) body.innerHTML = _tosSkel();
    const pulse = document.getElementById('tos-pulse');
    if (pulse) pulse.innerHTML = '';
    (m === 'command' ? _ccLoad() : m === 'brief' ? _briefLoad()
     : m === 'ideas' ? _ideasModeLoad()
     : m === 'mail' ? _mailLoad(true) : _tosLoad());
  };
  document.getElementById('cc-sw-brief')?.addEventListener('click', () => setMode('brief'));
  document.getElementById('cc-sw-command')?.addEventListener('click', () => setMode('command'));
  document.getElementById('cc-sw-ideas')?.addEventListener('click', () => {
    if(_trActive()){_tosState.view='ideas';if(_tosState.mode!=='staff')setMode('staff');else _trRenderView();_trSyncNav();}
    else setMode('ideas');
  });
  document.getElementById('cc-sw-mail')?.addEventListener('click', () => setMode('mail'));
  document.getElementById('cc-sw-staff')?.addEventListener('click', () => setMode('staff'));
}

let _ccAuthority = window.AdsAuthority.normalize(null);
let _ccAuthorityVisit = 0;

async function _ccLoad(force) {
  const authorityVisit = ++_ccAuthorityVisit;
  _ccAuthority = window.AdsAuthority.normalize(null);
  const body = document.getElementById('tos-body');
  if(tosHouse() && (!body?.isConnected || _tosState.mode!=='command'))return;
  if (body && (!_tosState.cc || force)) body.innerHTML = _tosSkel();
  else if (body && _tosState.cc) _ccRender();
  // Command Center itself is cached for five minutes. Permission is not:
  // start a fresh, independent read for this visit and never hold the rest
  // of the board behind it.
  window.AdsAuthority.load(path => api(path), { force: true }).then(snapshot => {
    if (authorityVisit !== _ccAuthorityVisit) return;
    _ccAuthority = snapshot;
    if (_tosState.cc) _ccRender();
  });
  try {
    _tosState.cc = await api(`/api/command/center${force ? '?force=1' : ''}`);
  } catch (e) {
    if(tosHouse() && (!body?.isConnected || _tosState.mode!=='command'))return;
    if (body) body.innerHTML = `<div class="tos-empty"><div class="tos-empty__i">!</div>${esc(e.message || e)}</div>`;
    return;
  }
  if(tosHouse() && (!body?.isConnected || _tosState.mode!=='command'))return;
  _ccRender();
}

// ── An answered card stays answered (audit 2026-07-29) ──────────────
// Prod runs 3 uvicorn workers (Procfile: --workers ${WEB_CONCURRENCY:-3})
// and the command-center cache is a process-local dict on a 300s TTL, so
// a board load right after a decision can be served by a worker that
// never saw it. The server half of this is fixed (command_center
// ._live_sections re-reads the card list on every cache hit) — THIS is
// the client's half, and it holds against a stale payload from any
// source: a card id this tab has answered is never rendered again.
// Entries expire after 15 minutes — comfortably past the 300s TTL — so a
// genuinely re-minted card (new row, new id) is never suppressed by an
// old answer, and the map cannot grow without bound on a long session.
// Keyed by LANE ('decide' | 'built') because a card leaves two different
// queues: a YES retires it from decisions and immediately BELONGS in the
// to-build strip, so one flat id set would hide the card from the very
// surface it just earned.
const _CC_ANSWERED_MS = 15 * 60 * 1000;
const _ccAnswered = new Map();          // "lane:id" → expiry (epoch ms)

function _ccMarkAnswered(id, lane) {
  if (id) _ccAnswered.set(`${lane || 'decide'}:${id}`, Date.now() + _CC_ANSWERED_MS);
}

function _ccIsAnswered(id, lane) {
  const k = `${lane || 'decide'}:${id}`;
  const exp = _ccAnswered.get(k);
  if (!exp) return false;
  if (exp <= Date.now()) { _ccAnswered.delete(k); return false; }
  return true;
}

function _ccSevClass(sev) { return sev === 'critical' ? 'cc-card--crit' : 'cc-card--warn'; }

function _ccActionCard(a) {
  // DESIGN FLOW (2026-07-14): decide rows are a numbered ledger, not
  // chip-heavy cards. One title, one why, one do-line, ₦ + section in
  // quiet mono, bracket actions. A handled row REMOVES itself.
  const btns = a.kind === 'recommendation'
    ? `<button class="cc-btn" data-cc-task="${esc(a.id)}" data-cc-title="${esc(a.title || '')}">task it</button>
       <button class="cc-btn cc-btn--ghost" data-cc-snooze="${esc(a.id)}">snooze 7d</button>`
    : `<button class="cc-btn cc-btn--ghost" data-cc-ack="${esc(a.id)}">clear</button>`;
  return `<div class="cc-card ${_ccSevClass(a.severity)}">
    <div class="cc-card__t">${esc(a.title || '')}</div>
    ${a.why ? `<div class="cc-card__why">${esc(a.why)}</div>` : ''}
    ${a.next_step ? `<div class="cc-card__do"><span>do</span> ${esc(a.next_step)}</div>` : ''}
    <div class="cc-card__foot">
      ${a.impact_ngn ? `<span class="ccf-ngn">${cswNgn(a.impact_ngn)} at stake</span>` : ''}
      ${a.section ? `<span class="ccf-tag">${esc(a.section)}</span>` : ''}
      <div class="cc-sp"></div>${btns}</div>
  </div>`;
}

function _ccRender() {
  if(tosHouse() && (!_trActive() || _tosState.mode!=='command'))return;
  // DESIGN FLOW rebuild (2026-07-14, operator: "remove the noise …
  // minimalist and clean showing important details"). One loud hero
  // (decisions + ₦ at stake), numbered lowercase sections, ledger rows.
  // A section with no signal does not render at all; handled rows
  // remove themselves (see _ccBind).
  const d = _tosState.cc || {};
  const body = document.getElementById('tos-body');
  if (!body) return;
  const acts = d.actions || {}; const eonq = d.eon_queue || {};
  const inv = d.inventory || {}; const staff = d.staff || {}; const ads = d.ads || {};
  const sys = acts.system || {};
  const eng = d.engine || {};

  const pulse = document.getElementById('tos-pulse');
  if (pulse) {
    const mer = ads.mer || {};
    const att = staff.attendance_today || {};
    pulse.className = 'tos-pulse ccf-strip';
    pulse.innerHTML = `
      <div class="ccf-hero">
        <div class="ccf-hero__n">${acts.total || 0}</div>
        <div class="ccf-hero__l">to look at
          ${acts.at_stake_ngn ? `<span class="ccf-hero__ngn">${cswNgn(acts.at_stake_ngn)} at stake</span>` : ''}</div>
      </div>
      <div class="ccf-stats">
        ${eonq.pending ? `<button class="ccf-stat ccf-stat--live" id="ccf-eonq" type="button"><b>${eonq.pending}</b>eon to approve</button>` : ''}
        ${ads.leak_daily_ngn ? `<button class="ccf-stat ccf-stat--warn" id="ccf-leak" type="button" title="jump to the leaking ads"><b>${cswNgn(ads.leak_daily_ngn)}/d</b>ad leak</button>` : ''}
        <span class="ccf-stat"><b>${mer.mer_7d_rolling != null ? Number(mer.mer_7d_rolling).toFixed(2) : '—'}</b>mer 7d</span>
        <span class="ccf-stat"><b>${att.checked_in || 0}/${att.active_staff || 0}</b>in today</span>
      </div>`;
    document.getElementById('ccf-eonq')?.addEventListener('click', () => openEonQueue());
    // The leak number is a door now (vet finding 10): it lands on the
    // watch rows that name the ads, where each carries its own control.
    document.getElementById('ccf-leak')?.addEventListener('click', () =>
      document.getElementById('cc-watch')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  const secs = [];

  // The mast (design-flow completion 2026-08-01): the command view gets
  // the same lowercase-word-with-a-period grammar the Ideas tab carries
  // — quiet, above the ledger; the hero strip stays the loud element.
  secs.push(`<div class="ccm"><span class="ccm-mast">command<span class="idm-dot">.</span></span><span class="ccm-idx">[ ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }).toLowerCase()} ] [ ${(d.proposals && d.proposals.open) || 0} to answer ] [ ${sys.red || 0} red feed${(sys.red || 0) === 1 ? '' : 's'} ]</span></div>`);

  // Obligations (2026-07-27, operator: "all my obligations real time") —
  // the money's word leads the page. Fills async via _ccObligMount();
  // non-finance roles get a 403 and the placeholder removes itself.
  secs.push(`<div id="cc-oblig"></div>`);

  if (eonq.pending) {
    secs.push(`<div class="cc-sec">approve</div>
      <div class="cc-card">
        <div class="cc-card__t">${eonq.pending} EON action${eonq.pending === 1 ? '' : 's'} waiting on your word</div>
        <div class="cc-card__why">${(eonq.items || []).map(i => esc(i.description || i.action_type)).join(' · ')}</div>
        <div class="cc-card__foot"><div class="cc-sp"></div><button class="cc-btn" id="cc-open-eonq">review</button></div>
      </div>`);
  }

  // Proposal cards (Autonomy Constitution PR A) — the machine's yes/no
  // queue. Cards NEVER expire (silence = still waiting); the board is
  // where they get answered. Deciding is owner-only (server-enforced);
  // buttons render only for the owner so staff never see a dead control.
  const props = d.proposals || {};
  // Client state is authoritative for cards THIS tab answered — a stale
  // worker payload must never put a decided card back with live buttons.
  const propTop = (props.top || []).filter(c => !_ccIsAnswered(c.id));
  const propHidden = (props.top || []).length - propTop.length;
  const propOpen = Math.max(0, (props.open || 0) - propHidden);
  if (propTop.length) {
    const isOwner = (State.member && State.member.role) === 'owner';
    const env = props.envelope || {};
    // Operator design law (2026-07-28): clear · minimalist · bold ·
    // quiet authority. Ghost rank numeral carries the order; the title
    // is the loudest thing on the card; YES is the single weighted
    // control. Flat 1px lines, no decoration.
    const _age = (iso) => {
      const days = daysSince(iso);
      if (days == null) return '';
      return days >= 1 ? `waiting ${days}d` : 'new today';
    };
    // Plain words (operator 2026-07-29: "simplify the words like i am 5
    // no bogus words"). Every label below is what the thing IS, in words
    // he'd say out loud. No house jargon on a card he has to answer:
    // "always asks" -> "we always check with you", the class slug
    // ("engineering") -> "a fix", "$X of $Y envelope" -> what's left to
    // spend this month.
    const CLASS_WORDS = {
      engineering: 'a fix', operating: 'how we run things',
      spend: 'spending money', function: 'something new',
    };
    const _waited = (iso) => {
      const days = daysSince(iso);
      if (days == null) return '';
      if (days >= 14) return `been waiting ${Math.floor(days / 7)} weeks`;
      if (days >= 1) return `been waiting ${days} day${days === 1 ? '' : 's'}`;
      return 'came in today';
    };
    const _money = (c) => {
      const bits = [];
      if (c.expected_value_ngn) bits.push(`could bring in ${cswNgn(c.expected_value_ngn)} a month`);
      // What leaving it alone costs. Same plain-words rule as the rest of
      // this row — the backend computes it, so say it out loud.
      if (c.cost_of_no_ngn_wk) bits.push(`leaving it costs ${cswNgn(c.cost_of_no_ngn_wk)} a week`);
      if (c.run_cost_usd_mo) bits.push(`costs $${c.run_cost_usd_mo} a month to run`);
      return bits;
    };
    // DESIGN FLOW 2026-07-31 (feature_requests 5071a0b4): the Decisions
    // board takes the editorial grammar — 001-index, bracketed see-it
    // door, adaptive button copy (ideaDecisionWords) with a plain-words
    // line saying what each tap causes. data-pdec stays yes|no: the
    // labels change the WORDS, never the door.
    const cards = propTop.map((c, i) => {
      const w = ideaDecisionWords(c);
      return `
      <div class="ccp" data-prop="${esc(c.id)}" style="--i:${i}">
        ${isOwner ? `<label class="ccp__pick"><input type="checkbox" class="ccp__cb"
          data-ppick="${esc(c.id)}" aria-label="pick this one"></label>` : ''}
        <div class="ccp__n">${String(i + 1).padStart(3, '0')}</div>
        <div class="ccp__body">
          <div class="ccp__t">${esc(c.title || '')}</div>
          <div class="ccp__why">${esc(c.why_line || '')}</div>
          <div class="ccp__meta">${[
            ..._money(c),
            CLASS_WORDS[c.class] || esc(c.class || ''),
            c.tier === 'ask_only' ? 'we always check with you' : '',
            _waited(c.created_at),
          ].filter(Boolean).join(' · ')}</div>
          <button class="ccp-see" data-psee="${esc(c.id)}">see it</button>
        </div>
        ${isOwner ? `<div class="ccp__act">
          <button class="ccp-yes" data-pdec="yes" data-pid="${esc(c.id)}">${esc(w.yes)}</button>
          <button class="ccp-no" data-pdec="no" data-pid="${esc(c.id)}">${esc(w.no)}</button>
          <button class="ccp-note${c.operator_note ? ' on' : ''}" data-pnote="${esc(c.id)}"
            title="${c.operator_note ? 'Your note' : 'Add a note'}"
            aria-label="${c.operator_note ? 'Your note' : 'Add a note'}">${noteIcon()}</button>
        </div>` : ''}
        ${isOwner ? `<div class="ccp__consq">${esc(w.why)}</div>` : ''}
        ${isOwner ? `<div class="ccp-drawer" id="ccp-drawer-${esc(c.id)}" hidden>
          <label class="ccp-drawer__l" for="ccp-note-${esc(c.id)}">Anything you want changed about this?</label>
          <textarea class="ccp-drawer__ta" id="ccp-note-${esc(c.id)}"
            rows="3" placeholder="e.g. do it, but only for the Lagos stores — and leave the old screen alone until it's proven."
            >${esc(c.operator_note || '')}</textarea>
          <div class="ccp-drawer__row">
            <span class="ccp-drawer__msg" id="ccp-msg-${esc(c.id)}"></span>
            <div class="cc-sp"></div>
            <button class="ccp-drawer__b" data-pnotesave="${esc(c.id)}">save note</button>
            ${c.built_request_id
              ? '<span class="ccp-drawer__queued">already sent to build</span>'
              : `<button class="ccp-drawer__b ccp-drawer__b--go" data-pbuild="${esc(c.id)}">build this</button>`}
          </div>
        </div>` : ''}
      </div>`;
    }).join('');
    // The envelope line said "$3.20 of $50 envelope". What he needs to
    // know is what is LEFT.
    const left = env.total != null
      ? Math.max(0, Number(env.total) - Number(env.spent || 0)) : null;
    const sub = [
      `${propOpen} waiting on you`,
      'nothing here expires — leaving it alone is not a no',
      left != null ? `$${left.toFixed(2)} left to spend this month` : '',
    ].filter(Boolean).join(' · ');
    // One name per queue (vet finding 4): the cards are "answer these";
    // the recommendations + alarms below are "look at these" — hero,
    // mast, sections and the Ideas mast all use the same two words.
    secs.push(`<div class="cc-sec">answer these<span class="cc-sec__s">${sub}</span></div>
      <div class="ccp-queue" id="cc-props">${cards}</div>
      ${isOwner ? `<div class="ccp-bulk" id="cc-props-bulk" hidden>
        <span class="ccp-bulk__n" id="cc-bulk-n">0 picked</span>
        <div class="cc-sp"></div>
        <button class="ccp-bulk__b" id="cc-bulk-clear" type="button">clear</button>
        <button class="ccp-bulk__b" id="cc-bulk-no" type="button">say no to these</button>
        <button class="ccp-bulk__b ccp-bulk__b--yes" id="cc-bulk-yes" type="button">approve these</button>
      </div>` : ''}`);
  }

  // Approved and waiting on a HUMAN. Filled async by _ccBuiltMount()
  // from the owner-gated /api/proposals?status=approved; a non-owner
  // 403 removes the placeholder silently, like the obligations strip.
  secs.push(`<div id="cc-built"></div>`);

  // EON ACTED ALONE — the receipts ledger (redesign 2026-08-09,
  // operator: "geared towards autonomous growth … simple clear
  // explanation of jobs eon did"). What was three 80-char rows is now
  // the whole week: a 7-day rail on top (one dot per move, hollow
  // amber = did not work), then every move in plain words — what EON
  // did, in which lane, how it went, when. Beyond six rows the rest
  // folds behind the same animated fold the task log uses. The undo
  // rows keep their controls unchanged — the receipt must never hide
  // the way back.
  const auto = d.automation || {};
  // Same rule as the decision queue: an undo THIS tab already made must
  // not come back from a stale worker with a live UNDO button.
  const undoable = (auto.undoable || []).filter(u => !_ccIsAnswered(u.id));
  const moves = auto.automated || [];
  // the scoreboard + lane chips ride the CACHED `autonomy` section (they
  // move on a nightly clock); the moves stay live
  const aut = d.autonomy || {};
  const score = aut.scoreboard || null;
  const lanes = aut.lanes || [];
  if (undoable.length || moves.length || (score && score.speak) || lanes.length) {
    // One row per act (ceiling 2026-09-02, vet finding 7): an undoable
    // card whose act already sits in the ledger carries UNDO on that
    // receipt row; only an undoable with no receipt yet (approved, not
    // executed) keeps a row of its own.
    const undoByCard = {};
    undoable.forEach(u => { undoByCard[u.id] = u; });
    const claimed = new Set();
    moves.forEach(m => { if (m.card_id && undoByCard[m.card_id]) claimed.add(m.card_id); });
    const loose = undoable.filter(u => !claimed.has(u.id));
    const undoRows = loose.map((u, i) => `
      <div class="ccp ccp--auto" data-prop="${esc(u.id)}" style="--i:${i}">
        <div class="ccp__n ccp__n--auto">A</div>
        <div class="ccp__body">
          <div class="ccp__t">${esc(u.title || '')}</div>
          <div class="ccp__meta">EON did this on its own · you have 24 hours to undo it · if you undo, it goes back to asking you first</div>
        </div>
        <div class="ccp__act"><button class="ccp-no" data-pundo="${esc(u.id)}">UNDO</button></div>
      </div>`).join('');
    // The sub-line counts what is TRUE: the server's exact week total
    // (the list is capped), each outcome by its own name, and no count
    // at all when there are no moves (vet findings 4 + 9).
    const stateOf = (m) => m.state || (m.ok ? 'executed' : 'failed');
    const landed = moves.filter(m => stateOf(m) === 'executed').length;
    const failed = moves.filter(m => stateOf(m) === 'failed').length;
    const undone = moves.filter(m => stateOf(m) === 'undone').length;
    const total = Number(auto.total) > moves.length ? Number(auto.total) : moves.length;
    const sub = [
      moves.length ? `${total} move${total === 1 ? '' : 's'} this week${total > moves.length ? ` · the last ${moves.length} shown` : ''}` : '',
      moves.length ? [`${landed} landed`, failed ? `${failed} did not work` : '',
        undone ? `${undone} you undid` : ''].filter(Boolean).join(' · ') : '',
      undoable.length ? 'UNDO stays live for 24 hours' : '',
    ].filter(Boolean).join(' · ');
    const SHOW = 6;
    const rowsAll = moves.map((m, i) => _ccMoveRow(m, i, m.card_id ? undoByCard[m.card_id] : null));
    const head = rowsAll.slice(0, SHOW).join('');
    const rest = rowsAll.slice(SHOW).join('');
    secs.push(`<div class="cc-sec">EON did this without asking<span class="cc-sec__s">${sub}</span></div>
      <div class="ccp-queue" id="cc-auto">${_ccScoreLine(score)}${_ccLaneChips(lanes, _ccAuthority)}${undoRows}${moves.length ? `
        <div class="ccr-card">
          ${_ccMoveRail(moves)}
          <div class="ccr-list">${head}</div>
          ${rest ? _tosFold('cc-auto-fold', `<div class="ccr-list">${rest}</div>`, `show all ${moves.length} moves`, 'fold the week back') : ''}
        </div>` : ''}
      </div>`);
  }

  const queue = (acts.queue || []).map(_ccActionCard).join('')
    || '<div class="ccf-quiet">nothing needs your decision.</div>';
  secs.push(`<div class="cc-sec">look at these<span class="cc-sec__s">${acts.total || 0} open · severity × ₦</span></div>
    <div class="ccf-queue">${queue}</div>`);

  // the engine — the chat learning loop, only when it owes him a line.
  // A healthy or deliberately-dark loop renders nothing at all (the
  // section law at the top of this function, and the operator's own
  // cadence law: silence costs zero).
  if (eng.speak) {
    const bits = [];
    if (eng.hits) {
      bits.push(`${eng.hits} thing${eng.hits === 1 ? '' : 's'} EON said `
        + `contradicted the shop's own records`
        + (eng.hit_kinds > 1 ? ` across ${eng.hit_kinds} rules` : '')
        + ' in the last day');
    }
    const faults = eng.faults || [];
    if (faults.length) {
      bits.push(`${faults.length} part${faults.length === 1 ? '' : 's'} of the `
        + `learning loop ${faults.length === 1 ? 'is' : 'are'} built but `
        + `nothing runs ${faults.length === 1 ? 'it' : 'them'}: `
        + faults.map(f => esc(String(f))).join(', '));
    }
    secs.push(`<div class="cc-sec">the engine</div>
      <div class="cc-card">
        <div class="cc-card__t">${bits[0] || 'the engine has something to say'}</div>
        ${bits[1] ? `<div class="cc-card__why">${bits[1]}</div>` : ''}
        <div class="cc-card__foot"><a class="ccf-link" href="/engine-room?view=learning" target="_blank" rel="noopener">open chat learning</a></div>
      </div>`);
  }

  // the model test — Fable against Opus 5 on the customer desk, on his
  // board until the test is done (operator 2026-09-01: "measure it").
  // Speaks while the A/B is switched on; retires itself when it is not.
  const mt = d.model_test || {};
  if (mt.speak) {
    const ma = mt.arms || {}; const mo = ma.opus || {}; const mf = ma.fable || {};
    const pct = (v) => (v == null ? '—' : `${v}%`);
    const arm = (name, x) => `<div style="flex:1 1 0;min-width:0;padding:8px 10px;border:1px solid rgba(160,170,200,.18);border-radius:6px">`
      + `<div style="font-weight:700">${esc(name)}</div>`
      + `<div style="font-size:12px;opacity:.85">${x.n || 0} of ${mt.need || 60} episodes</div>`
      + `<div style="font-size:12px;opacity:.85">reached a price ${pct(x.handoff_pct)} · closed after hand-off ${pct(x.handoff_close_pct)}</div>`
      + `<div style="font-size:12px;opacity:.85">${x.depth || 0} messages per thread</div></div>`;
    const cost = (mt.cost || []).map(c => `${esc(c.model)} ${c.calls} calls $${c.usd}`).join(' · ');
    secs.push(`<div class="cc-sec">the model test<span class="cc-sec__s">${mt.settled ? 'settled — switch the test off once you have read it' : 'still accumulating · needs 60 episodes each side'}</span></div>
      <div class="cc-card">
        <div class="cc-card__t">Fable against Opus 5 on the customer desk, since ${esc(mt.since || '')}</div>
        <div style="display:flex;gap:10px;margin:8px 0;flex-wrap:wrap">${arm('Opus 5', mo)}${arm('Fable', mf)}</div>
        <div class="cc-card__why">the number that matters is closed after hand-off — EON reaches the price, a person closes${cost ? ' · cost so far: ' + cost : ''}</div>
        ${cswPayCoverage(mt.coverage) ? `<div class="cc-card__why">${cswPayCoverage(mt.coverage)}</div>` : ''}
        <div class="cc-card__foot"><a class="ccf-link" href="/engine-room?view=learning" target="_blank" rel="noopener">open chat learning</a></div>
      </div>`);
  }

  if (sys.red) {
    const names = (sys.items || []).slice(0, 3)
      .map(i => esc((i.title || '').replace(/^Brain:\s*/i, ''))).join(' · ');
    secs.push(`<div class="cc-sec">system</div>
      <div class="cc-card">
        <div class="cc-card__t">${sys.red} feed${sys.red === 1 ? '' : 's'} red on the brain</div>
        <div class="cc-card__why">${names}${sys.red > 3 ? ' · …' : ''}</div>
        <div class="cc-card__foot"><a class="ccf-link" href="/engine" target="_blank" rel="noopener">open engine</a>
          <div class="cc-sp"></div>
          <button class="cc-btn cc-btn--ghost" id="ccf-sys-clear" data-ids="${esc((sys.items || []).map(i => i.id).join(','))}">clear all</button></div>
      </div>`);
  }

  const sh = inv.shrinkage_month || {};
  const watch = [];
  if (inv.oos_active_products) watch.push(`<div class="cc-line"><b>${inv.oos_active_products}</b> active products at zero stock</div>`);
  if (sh.ngn) watch.push(`<div class="cc-line"><b>${cswNgn(sh.ngn)}</b> shrinkage this month${sh.pct_of_revenue ? ' · ' + sh.pct_of_revenue + '% of sales' : ''}</div>`);
  if (inv.integrity_open) watch.push(`<div class="cc-line"><b>${inv.integrity_open}</b> open integrity flags</div>`);
  (inv.open_recommendations || []).slice(0, 3).forEach(r =>
    watch.push(`<div class="cc-line">${esc(r.title)}${r.estimated_impact_ngn ? ` <span class="ccf-ngn">${cswNgn(r.estimated_impact_ngn)}</span>` : ''}</div>`));
  (staff.overdue_by_staff || []).slice(0, 3).forEach(o =>
    watch.push(`<div class="cc-line">${esc(o.who)} — <b>${o.open_overdue}</b> overdue${o.worst_hours_over ? ` · worst ${o.worst_hours_over}h over` : ''}</div>`));
  (staff.bottom_reps || []).slice(0, 2).forEach(r =>
    watch.push(`<div class="cc-line">${esc(r.name || '?')} — <b>${cswNgn(r.revenue_ngn || 0)}</b> · ${r.orders || 0} orders · 28d${r.location ? ' · ' + esc(r.location) : ''}</div>`));
  // A leaking ad carries a door (vet finding 10): "task it" files the
  // do-line as a high-priority task through the same door the decide
  // cards use — the operator no longer carries the ad name by hand.
  (ads.leaking_ads || []).slice(0, 4).forEach(l =>
    watch.push(`<div class="cc-line">${esc(l.ad_name)} · ${esc(l.platform)} · ${esc(l.verdict)}${l.fatiguing ? ' · fatiguing' : ''} · <b>${cswNgn(l.spend_7d_ngn)}</b>/7d${l.do ? ` — ${esc(l.do)}` : ''}
      <button class="cc-btn cc-btn--ghost cc-line__do" type="button" data-cc-leaktask="${esc(l.ad_name || '')}" data-cc-do="${esc(l.do || '')}">task it</button></div>`));
  if (watch.length) secs.push(`<div class="cc-sec" id="cc-watch">watch<span class="cc-sec__s">inventory · staff · ads</span></div>${watch.join('')}`);

  const depts = (d.departments || []).filter(dp => (dp.open || 0) || (dp.overdue || 0));
  if (depts.length) {
    secs.push(`<div class="cc-sec">floor</div>
      <div class="cc-depts">${depts.map(dp =>
        `<button class="cc-dept ${dp.overdue ? 'cc-dept--alert' : ''}" data-cc-dept="${esc(dp.slug || '')}">
          <span class="cc-dept__n">${esc(dp.name || dp.slug)}</span>
          <span class="cc-dept__m">${dp.open} open${dp.overdue ? ` · ${dp.overdue} overdue` : ''}</span>
        </button>`).join('')}</div>`);
  }

  body.innerHTML = secs.join('');
  _ccBind();
  _tosBindFolds(body);
  _ccObligMount();
  _ccBuiltMount(props.awaiting_done);
}

// ── "Built" — the done-signal for cards a HUMAN executes ────────────
// An approved card with no lane executor (mark down 5 SKUs, place a
// reorder, swap a creative) had no way to say the work happened: it sat
// 'approved' forever and its proposer went QUIET about the claim —
// proposal_cards.submit_or_refresh dedups on status in (open, approved)
// and returns "skipped" under an unexecuted yes. So the failure was not
// nagging; it was silence. Nothing else in the product could ever flip
// such a card past 'approved'. The backend door is
// POST /api/proposals/{id}/built (owner-gated by P.PROPOSALS_DECIDE,
// same as decide/undo). The route shipped in #1631; _ccBuiltDead below
// stays as the 404 safety net, not as a placeholder for missing work.
//
// The list comes from the command-center payload's proposals
// .awaiting_done when the server sends it (the same teammate's PR adds
// that field; taking it costs nothing and avoids a second round trip on
// every render). Until then it falls back to the EXISTING owner-gated
// GET /api/proposals?status=approved — no new backend surface either
// way.
//
// MACHINE-DECIDED cards are excluded, and there are TWO notes for that
// now, not one. 'auto' = a lane executed the card itself; 'auto:routine'
// = a routine code fix EON cleared WITHOUT ASKING (#1646, 2026-07-29 —
// proposal_cards._undo_routine_autoclear says it "skipped the ASKING").
// Both already sit in the "acted alone" strip with an UNDO inside a 24h
// window (recommendations.automation_summary filters on exactly those
// two values), so listing either here would put two contradictory
// controls on one card — and the headline would count as a yes HE gave
// something he was never asked. proposal_cards.board() does not filter
// these, so on the fallback path this client filter is the only guard;
// it is a NOT-IN over both values, matching the server's awaiting_done()
// list when that lands.
async function _ccBuiltMount(served) {
  const host = document.getElementById('cc-built');
  if (!host) return;
  if ((State.member && State.member.role) !== 'owner') { host.innerHTML = ''; return; }
  let cards = [];
  try {
    if (Array.isArray(served)) cards = served;
    else {
      const d = await api('/api/proposals?status=approved');
      cards = (d && d.cards) || [];
    }
  } catch (e) {
    // A 403 means this reader may not see the list — render nothing.
    // ANY OTHER failure (500, timeout) must NOT render as "nothing to
    // do": this strip is the only surface for work the operator owes,
    // and silence here would assert an empty list we never read.
    if (e && e.status === 403) { host.innerHTML = ''; return; }
    host.innerHTML = '<div class="cc-sec">waiting on the work'
      + '<span class="cc-sec__s">could not read this list just now — '
      + 'that is unknown, not empty</span></div>';
    return;
  }
  // Every note the MACHINE writes when it decides for him. A card
  // carrying one of these is not a yes he gave, so it never belongs in
  // this strip or its count.
  const MACHINE_NOTES = ['auto', 'auto:routine'];
  const rows = cards
    .filter(c => c && !MACHINE_NOTES.includes(c.decision_note)
                 && !_ccIsAnswered(c.id, 'built'));
  if (!rows.length) { host.innerHTML = ''; return; }
  // Plain words, same as the decisions queue after #1640 — one board,
  // one voice. No "built", no "approved card": what happened and when.
  const since = (iso) => {
    const days = daysSince(iso);
    if (days == null) return 'you said yes';
    if (days >= 14) return `you said yes ${Math.floor(days / 7)} weeks ago`;
    if (days >= 1) return `you said yes ${days} day${days === 1 ? '' : 's'} ago`;
    return 'you said yes today';
  };
  // Redesign 2026-08-09 (operator: "the button done in task i dont know
  // whats for"): the bare DONE with only "you said yes 3 days ago"
  // beside it explained nothing. Every row now says what it IS (the
  // card's own why_line), that a PERSON finishes it, and the button
  // says what tapping it does. Same door, clearer words.
  host.innerHTML = `
    <div class="cc-sec">waiting on the work<span class="cc-sec__s">${rows.length} you said yes to — a person does these, EON only watches. When one is really done, close it here.</span></div>
    <div class="ccp-queue" id="cc-builtq">${rows.map((c, i) => `
      <div class="ccp" data-prop="${esc(c.id)}" style="--i:${i}">
        <div class="ccp__n ccp__n--ok">✓</div>
        <div class="ccp__body">
          <div class="ccp__t">${esc(c.title || '')}</div>
          ${c.why_line ? `<div class="ccp__why">${esc(c.why_line)}</div>` : ''}
          <div class="ccp__meta">${[
            since(c.decided_at),
            'still waiting on a person to do it',
            c.expected_value_ngn ? `could bring in ${cswNgn(c.expected_value_ngn)} a month` : '',
          ].filter(Boolean).join(' · ')}</div>
        </div>
        <div class="ccp__act">
          <button class="ccp-yes ccp-yes--done" data-pbuilt="${esc(c.id)}">the work is done</button>
          <span class="ccp-act__hint">closes it — EON stops waiting</span>
        </div>
      </div>`).join('')}</div>`;
  host.querySelectorAll('[data-pbuilt]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const el = e.currentTarget;
      const id = el.getAttribute('data-pbuilt');
      const row = el.closest('.ccp');
      // Every button on the row goes dead on the tap — one card takes
      // ONE answer (the same discipline the decide path learned).
      const rowBtns = row ? Array.from(row.querySelectorAll('button')) : [el];
      rowBtns.forEach(b => { b.disabled = true; });
      el.textContent = '…';
      try {
        await api(`/api/proposals/${encodeURIComponent(id)}/built`,
          { method: 'POST', body: JSON.stringify({ note: 'marked done from the board' }) });
        _ccMarkAnswered(id, 'built');
        if (row) row.remove(); else el.remove();
        if (!host.querySelector('.ccp')) host.innerHTML = '';
        // Says only what happened: the card leaves this list. It does NOT
        // say "EON stops asking" — marking done is what REMOVES the card
        // from submit_or_refresh's (open, approved) dedup set, so the
        // proposer may raise the claim again if it recurs. That is the
        // design; the toast must not promise the opposite of it.
        cswToast('Done. It leaves this list.');
      } catch (err) {
        // Honest failure: the card stays, the button comes back. Nothing
        // is marked done on this board that the server did not accept.
        rowBtns.forEach(b => { b.disabled = false; });
        el.textContent = 'the work is done';
        // The route may not exist yet (it ships in a teammate's PR). A
        // 404/405 is not "that did not save" — it is "this control is
        // not live". Say so in words, and retire every DONE control on
        // the strip so the operator taps a dead button exactly once.
        if (err && (err.status === 404 || err.status === 405)) {
          _ccBuiltDead(host);
          return;
        }
        // One plain sentence, never the server's code (CS vet round 2).
        cswToast(cswSayWhy(err, { lead: 'That did not save.' }));
      }
    });
  });
}

// The mark-done door (POST /api/proposals/{id}/built) lands in a
// teammate's PR. If this ships first the route answers 404 and FastAPI's
// body is {"detail":"Not Found"} — api() would surface that verbatim and
// the operator's toast would read "Not Found", which explains nothing.
// So the FIRST 404 retires every DONE control on the strip and says why.
// The list itself is still true and still useful, so it stays: this is
// the work he said yes to that no machine will finish for him.
function _ccBuiltDead(host) {
  if (!host) return;
  host.querySelectorAll('[data-pbuilt]').forEach(b => b.remove());
  host.querySelectorAll('.ccp-act__hint').forEach(h => h.remove());
  const sub = host.querySelector('.cc-sec__s');
  if (sub) sub.textContent = sub.textContent.split(' — ')[0]
    + ' — marking one done is not live yet; this list is the record for now';
  cswToast('That button is not live yet. Nothing was changed.');
}

// ── The receipts ledger (2026-08-09; ceiling 2026-09-02) — helpers ──
// Lane slugs come from eon_autonomous_actions.action_type; the reader
// deserves the act in words he'd say out loud, not a snake_case slug.
function _ccLaneWords(lane) {
  const MAP = {
    routine_fix: 'fixed its own code',
    meta_pause_ad: 'paused a Meta ad',
    tiktok_pause_ad: 'paused a TikTok ad',
    google_pause_ad: 'paused a Google ad',
    snap_pause_ad: 'paused a Snap ad',
    ad_cull_pause: 'paused a losing ad',
    ad_cull_restore: 'turned an ad back on',
    ad_kill_pause: 'paused a losing ad',
    ad_budget: 'moved ad budget',
    ad_budget_step: 'moved ad budget',
    ad_kill: 'killed an ad',
    ad_swap: 'swapped a creative',
    ad_creative_swap: 'swapped a creative',
    ad_radius_launch: 'launched a store-radius ad',
    ad_recycle_stand_down: 'stood a recycle down',
    ad_refresh_add: 'added a fresh ad',
    meta_scale_raise_campaign: 'raised a Meta budget',
    meta_restart_resume_campaigns: 'restarted Meta campaigns',
    transcript_read: 'read the desk transcripts',
    chat_autopsy: 'reviewed a customer chat',
    task_allocation: 'handed out a task',
    // every live autonomy_lanes row renders a chip — no slug may leak
    // onto the board (Opus vet 2026-09-02, seat 2 #5)
    reorder_gap: 'reordered before a stockout',
    money_recon: 'matched the money to the bank',
    merchant_brain: 'studied what sells',
    opus5_audit: 'audited its own work',
    factory_loop: 'chased the factory line',
    competitor_sweep: 'watched the competition',
  };
  const L = String(lane || '').toLowerCase();
  return MAP[L] || L.replace(/[_:]+/g, ' ') || 'a move';
}

// One calendar-day count from local midnights — the rail's own
// bucketing — so a row and its dot never disagree (vet 2026-09-02: three
// copies of floor((now-iso)/864e5) called yesterday-23:30 "today").
function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const a = new Date(); a.setHours(0, 0, 0, 0);
  const b = new Date(d); b.setHours(0, 0, 0, 0);
  return Math.round((a - b) / 864e5);
}

function _ccMoveWhen(iso) {
  const days = daysSince(iso);
  if (days == null) return '';
  const hm = new Date(iso).toTimeString().slice(0, 5);
  if (days <= 0) return 'today ' + hm;
  if (days === 1) return 'yesterday ' + hm;
  return `${days} days ago`;
}

// Outcome states come classified from the server (executed / failed /
// undone / blocked). A permission ASK never reaches this ledger — it
// sits in "approve" — so nothing here can call a polite question a
// failure (vet 2026-09-02, finding 1).
const _CCR_STATE = {
  executed: { cls: '', word: 'landed' },
  failed: { cls: ' ccr--fail', word: 'did not work' },
  undone: { cls: ' ccr--undone', word: 'you undid this' },
  blocked: { cls: ' ccr--blocked', word: 'a rule stopped it' },
};

// One receipt row: lane in words · what (with the naira and the
// after-grade when they exist) · outcome · when. A failed move carries
// its WHY and the retry clock; a move with a card carries its see-it
// door; a move still inside its undo window carries UNDO on the row
// itself — one row per act, never two (vet finding 7).
function _ccMoveRow(m, i, undoRow) {
  const st = _CCR_STATE[m.state] || (m.ok ? _CCR_STATE.executed : _CCR_STATE.failed);
  const meta = [st.word, _ccMoveWhen(m.at)].filter(Boolean).join(' · ');
  const ngn = m.ngn_line ? `<span class="ccr__ngn">${esc(m.ngn_line)}</span>` : '';
  const grade = m.grade_line ? `<span class="ccr__grade">${esc(m.grade_line)}</span>` : '';
  const whyBits = [];
  if (m.state === 'failed' || (!m.state && !m.ok)) {
    if (m.error) whyBits.push(esc(m.error));
    if (m.retry_line) whyBits.push(esc(m.retry_line));
    if (m.streak > 1) whyBits.push(`failed ${m.streak} mornings running`);
  }
  const why = whyBits.length ? `<div class="ccr__why">${whyBits.join(' · ')}</div>` : '';
  const door = m.card_id ? `<button class="ccr__door" type="button" data-psee="${esc(m.card_id)}">see it</button>` : '';
  const undo = undoRow ? `<button class="ccp-no ccr__undo" type="button" data-pundo="${esc(undoRow.id)}">UNDO</button>` : '';
  // The naira and the grade sit BESIDE the prose, never inside its
  // two-line clamp (Opus vet 2026-09-02, seat 2 #1: a 220-char title
  // ate both lines and the wave's two headline additions were clipped
  // into invisibility).
  return `<div class="ccr${st.cls}${undoRow ? ' ccr--undoable' : ''}" style="--i:${i}"${m.card_id ? ` data-prop="${esc(m.card_id)}"` : ''}>
    <span class="ccr__dot"></span>
    <span class="ccr__lane">${esc(_ccLaneWords(m.lane))}</span>
    <span class="ccr__what">${esc(m.what || '')}</span>${ngn}${grade}
    <span class="ccr__meta">${esc(meta)}</span>
    ${door}${undo}${why}
  </div>`;
}

// One settle for a decided card, in every room (vet 2026-09-02, finding
// 3): mark it answered for this tab, prune the Command payload AND the
// Ideas cache. Command pruned only cc.proposals; Ideas pruned only
// ideas.biz/code — answer a card in one room, switch rooms, and it came
// back with live buttons.
function _propSettleEverywhere(id, lane) {
  _ccMarkAnswered(id, lane);
  const cc = _tosState.cc;
  if (cc && cc.proposals && Array.isArray(cc.proposals.top)) {
    const before = cc.proposals.top.length;
    cc.proposals.top = cc.proposals.top.filter(c => c.id !== id);
    if (cc.proposals.top.length < before && cc.proposals.open > 0) cc.proposals.open -= 1;
  }
  const st = _tosState.ideas;
  if (st) {
    ['biz', 'code'].forEach((k) => {
      if (Array.isArray(st[k])) st[k] = st[k].filter(c => c.id !== id);
    });
  }
}

// The fold, assembled once (vet finding 11): hidden content + the
// honest button. btnFirst puts the button above the fold (earlier
// comments open ABOVE the recent ones); default puts it below.
function _tosFold(id, inner, more, less, btnFirst) {
  const fold = `<div class="tos-fold" id="${esc(id)}"><div class="tos-fold__in">${inner}</div></div>`;
  const btn = `<button class="tos-fold__btn" type="button" data-fold="${esc(id)}" data-more="${esc(more)}" data-less="${esc(less)}">${esc(more)}</button>`;
  return btnFirst ? btn + fold : fold + btn;
}

// The scoreboard line (ceiling 2026-09-02, ladder rung 2): the operator's
// own criterion — "if revenue doesn't increase the autonomy has failed"
// — read where he decides, not only in the /ads room and one 6am line.
// Server shape: ads_autonomy_pnl.scoreboard folded by command_center.
function _ccScoreLine(s) {
  if (!s || !s.speak) return '';
  const ahead = (s.attributed_per_day != null && s.baseline_per_day != null)
    ? (Number(s.attributed_per_day) >= Number(s.baseline_per_day) ? 'ahead of' : 'behind') : null;
  const bits = [];
  if (s.day_n != null) bits.push(`day ${s.day_n} since the lanes armed`);
  if (s.attributed_per_day != null) {
    // three lenses, never the platform's alone (house rule; Opus twin
    // verifier #8): what Meta/TikTok claim · what Shopify confirms ·
    // what the walk-in bridge sees — 90% of revenue is at the counter
    bits.push(`the platforms claim ${cswNgn(s.attributed_per_day)}/day`
      + (ahead ? ` — ${ahead} the ${cswNgn(s.baseline_per_day)}/day before` : ''));
    if (s.shopify_per_day != null) bits.push(`Shopify confirms ${cswNgn(s.shopify_per_day)}/day`);
    if (s.walk_in_per_day != null) bits.push(`the walk-in bridge sees ${cswNgn(s.walk_in_per_day)}/day`);
    if (s.views_ok === false) bits.push('the Shopify and walk-in lenses could not be read this morning');
  }
  if (s.baseline_thinness) bits.push(String(s.baseline_thinness));
  if (s.roas != null) {
    bits.push(`${Number(s.roas).toFixed(2)}x back per naira`
      + (s.baseline_roas != null ? ` (was ${Number(s.baseline_roas).toFixed(2)}x)` : ''));
  }
  if (s.removed_ngn != null) {
    bits.push(`${cswNgn(s.removed_ngn)}/day taken off losers → ${cswNgn(s.redeployed_ngn || 0)}/day moved to winners`
      + (s.redeploy_rate_pct != null ? ` (${s.redeploy_rate_pct}%)` : ''));
  }
  const warn = [];
  if (s.below_days) {
    warn.push(`${s.below_days} day${s.below_days === 1 ? '' : 's'} below the old line — `
      + `the lanes stand down at ${s.demote_at_days || 14}`);
  }
  if (s.verifier_dark) {
    warn.push('the nightly verifier that enforces that is switched off — arming it is your tap');
  }
  return `<div class="ccr-score${ahead === 'behind' ? ' is-behind' : ''}">
    <div class="ccr-score__l">${bits.map(esc).join(' · ')}</div>
    ${warn.length ? `<div class="ccr-score__w">${warn.map(esc).join(' · ')}</div>` : ''}
  </div>`;
}

// Trust per lane (ladder rung 6): which lanes act alone, which still
// ask, the recorded approval progress, the graded hit-rate, and the
// last stand-down with its reason — visible BEFORE the next tap.
function _ccLaneChips(lanes, snapshot) {
  const source = new Map((Array.isArray(lanes) ? lanes : [])
    .filter(l => l && typeof l.lane === 'string' && l.lane)
    .map(l => [l.lane, l]));
  const adLanes = window.AdsAuthority.LANE_IDS;
  const laneIds = [...new Set([...adLanes, ...source.keys()])];
  const current = laneIds.map(lane => {
    const measured = source.get(lane) || {};
    // The fresh advertising contract governs only its registered ad lanes.
    // Other tasks retain their server-reported status, never ad defaults.
    if (adLanes.includes(lane)) {
      return {
        lane, measured,
        view: window.AdsAuthority.authority(snapshot, lane),
        undo: window.AdsAuthority.undo(snapshot, lane),
      };
    }
    const mode = ['ask_first', 'act_first'].includes(measured.mode)
      ? measured.mode : 'unavailable';
    const knownNumber = value => typeof value === 'number' && Number.isFinite(value);
    return {
      lane, measured,
      view: {
        state: mode,
        line: mode === 'act_first' ? 'acts alone.'
          : mode === 'ask_first' ? 'asks first.' : 'Lane mode could not be read.',
        streak: knownNumber(measured.streak) ? measured.streak : null,
        threshold: knownNumber(measured.threshold) ? measured.threshold : null,
      },
      undo: { line: knownNumber(measured.undo_h) && measured.undo_h > 0
        ? `${measured.undo_h}h undo.` : 'Undo rule unavailable.' },
    };
  });
  if (!current.length) return '';
  const chip = (row) => {
    const l = row.measured;
    const view = row.view;
    const acts = view.state === 'act_first';
    // A hit-rate needs its sample floor (Opus vet, seat 3 #7): under it
    // the chip says how many grades exist, never "0% held of 1".
    const rate = l.outcome_rate != null && l.enough
      ? ` · ${Math.round(Number(l.outcome_rate) * 100)}% held of ${l.graded_n}`
      : (l.graded_n ? ` · ${l.graded_n} graded — too few to say` : ' · no grades yet');
    const streak = acts || view.streak == null || view.threshold == null
      ? '' : ` · ${view.streak}/${view.threshold} yeses`;
    const dem = l.demoted_at
      ? ` · stood down ${_ccMoveWhen(l.demoted_at)}` + (l.demote_reason ? `: ${l.demote_reason}` : '') : '';
    return `<span class="ccr-lane${acts ? ' is-armed' : ''}"><b>${esc(_ccLaneWords(row.lane))}</b>`
      + esc(` · ${view.line} ${row.undo.line}` + streak + rate + dem)
      + '</span>';
  };
  const verifier = window.AdsAuthority.verifier(snapshot);
  return `<div class="ccr-lanes">${current.map(chip).join('')}<span class="ccr-lane"><b>budget verifier</b> · ${esc(verifier.line)}</span></div>`;
}

// The week-rail: seven ticks, one dot per move, drawn once on entry.
// Inline SVG so it costs nothing and inherits the flat law — hairline
// stroke, EON green fill, hollow amber for a move that did not land.
function _ccMoveRail(moves) {
  // Vet 2026-09-02: PAD 18 clipped the edge dots by a pixel; a day with
  // more than ten moves stacked dots off the top of the viewBox; and at
  // phone width the 9px day labels scaled to ~4.6px. Now: two rows of
  // five, a "+n" past ten, and under 560px the CSS swaps the SVG for
  // an HTML day row (real font size, tappable) carrying the same counts.
  const W = 700, H = 46, PAD = 24;
  const step = (W - PAD * 2) / 6;
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - i);
    days.push(d);
  }
  const counts = new Array(7).fill(0);
  const fails = new Array(7).fill(0);
  const dots = [];
  moves.forEach(m => {
    if (!m.at) return;
    const t = new Date(m.at);
    if (isNaN(t)) return;
    t.setHours(0, 0, 0, 0);
    const idx = days.findIndex(d => d.getTime() === t.getTime());
    if (idx < 0) return;
    const bad = m.state ? m.state === 'failed' : !m.ok;
    const n = counts[idx]++;
    if (bad) fails[idx]++;
    if (n >= 10) return;   // two rows of five; the rest reads as "+n"
    const x = PAD + idx * step + ((n % 5) - 2) * 8;
    const y = 20 - Math.floor(n / 5) * 8;
    dots.push(`<circle class="ccr-rail__dot${bad ? ' ccr-rail__dot--fail' : ''}" cx="${x}" cy="${y}" r="3.2" style="--i:${dots.length}"><title>${esc((m.what || '').slice(0, 80))}</title></circle>`);
  });
  const ticks = days.map((d, i) =>
    `<line class="ccr-rail__tick" x1="${PAD + i * step}" y1="26" x2="${PAD + i * step}" y2="32"/>`).join('');
  const labels = days.map((d, i) =>
    `<text class="ccr-rail__day" x="${PAD + i * step}" y="${H - 3}" text-anchor="middle">${d.toLocaleDateString('en-GB', { weekday: 'short' }).toLowerCase()}</text>`).join('');
  const plus = counts.map((c, i) => c > 10
    ? `<text class="ccr-rail__plus" x="${PAD + i * step}" y="7" text-anchor="middle">+${c - 10}</text>` : '').join('');
  // The day row is the ONLY week view on a phone, so it is read, not
  // decorative (Opus vet, seat 2 #11): no aria-hidden, a label per cell.
  const dayRow = `<div class="ccr-days" role="list" aria-label="moves per day, last seven days">${days.map((d, i) =>
    `<span class="ccr-days__d${counts[i] ? ' is-on' : ''}${fails[i] ? ' has-fail' : ''}" role="listitem" aria-label="${counts[i] || 0} move${counts[i] === 1 ? '' : 's'}${fails[i] ? `, ${fails[i]} did not work` : ''} on ${d.toLocaleDateString('en-GB', { weekday: 'long' })}"><b>${counts[i] || '·'}</b>${d.toLocaleDateString('en-GB', { weekday: 'short' }).toLowerCase()}</span>`).join('')}</div>`;
  return `<svg class="ccr-rail" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
    <line class="ccr-rail__line" x1="${PAD}" y1="29" x2="${W - PAD}" y2="29"/>
    ${ticks}${labels}${plus}${dots.join('')}
  </svg>${dayRow}`;
}

// ── The fold (2026-08-09) — any log longer than the room collapses ──
// Operator: "logs above 10 lines should have a collapse button".
// grid-template-rows 0fr→1fr gives the browser-animated open with no
// height math; prefers-reduced-motion turns the ease off in CSS.
function _tosBindFolds(root) {
  root.querySelectorAll('[data-fold]').forEach(btn => {
    if (btn._foldBound) return;
    // Resolve the target BEFORE marking bound (Opus vet, seat 2 #9): a
    // fold rendered later must stay bindable, not die on first miss.
    const t = root.querySelector('#' + CSS.escape(btn.dataset.fold))
      || document.getElementById(btn.dataset.fold);
    if (!t) return;
    btn._foldBound = true;
    // A fold REMEMBERS per device (vet 2026-09-02, finding 12): every
    // re-render used to snap it shut, so the operator re-opened the
    // week on every tap. localStorage can throw (private mode) — guard.
    const key = 'tos.fold.' + btn.dataset.fold;
    const apply = (open) => {
      t.classList.toggle('is-open', open);
      btn.classList.toggle('is-open', open);
      btn.textContent = open ? (btn.dataset.less || 'fold it back')
        : (btn.dataset.more || 'show more');
    };
    let remembered = null;
    try { remembered = localStorage.getItem(key); } catch (e) { /* no store */ }
    if (remembered === '1') apply(true);
    btn.addEventListener('click', () => {
      const open = !t.classList.contains('is-open');
      apply(open);
      try { localStorage.setItem(key, open ? '1' : '0'); } catch (e) { /* no store */ }
    });
  });
}

// ── Obligations — the money's word (2026-07-27) ─────────────────────────
// Composes /api/tasks/obligations (existing engines: liabilities, tax
// filings, payroll, structure, receivables, runway bands). Real-time =
// refetch every 60s while the Command view is on screen; the timer kills
// itself the moment the view changes. 403 (non-finance role) removes the
// section silently — the hard gate is the server's.
let _ccObligTimer = null;
// Accounts for the add-form picker (mig 668 — a commitment says which
// account it will leave, so settle books against a real one instead of
// guessing). Fetched once per mount, not per 60s tick.
let _ccObligAccounts = [];

async function _ccObligMount() {
  const host = document.getElementById('cc-oblig');
  if (!host) return;
  if (_ccObligTimer) { clearInterval(_ccObligTimer); _ccObligTimer = null; }
  try {
    _ccObligAccounts = ((await api('/api/accounting/accounts')).accounts || [])
      .filter((a) => a.kind === 'bank' || a.kind === 'cash');
  } catch (_) { _ccObligAccounts = []; }
  const load = async () => {
    if (State.view !== 'tasks' || !document.getElementById('cc-oblig')) {
      if (_ccObligTimer) { clearInterval(_ccObligTimer); _ccObligTimer = null; }
      return;
    }
    try {
      const d = await api('/api/tasks/obligations');
      _ccObligRender(d);
    } catch (e) {
      const h = document.getElementById('cc-oblig');
      if (h) h.innerHTML = '';                       // 403 or engine down — quiet
      if (_ccObligTimer) { clearInterval(_ccObligTimer); _ccObligTimer = null; }
    }
  };
  await load();
  if (document.getElementById('cc-oblig')?.innerHTML) {
    _ccObligTimer = setInterval(load, 60000);
  }
  // one delegated listener on the (stable) host — innerHTML refreshes
  // every 60s, so per-button bindings would die on the first tick
  host.addEventListener('click', async (e) => {
    const settle = e.target.closest('[data-oblig-settle]');
    const voidBtn = e.target.closest('[data-oblig-void]');
    if (settle || voidBtn) {
      const id = (settle || voidBtn).getAttribute(settle ? 'data-oblig-settle' : 'data-oblig-void');
      (settle || voidBtn).disabled = true;
      try {
        // "settled" means the money MOVED — so it must hit the books.
        // Until 2026-08-10 this posted no body, so log_expense defaulted
        // false: settling dropped the leg out of the projection's month-0
        // outflow and wrote nothing anywhere, which made cash APPEAR TO
        // IMPROVE by the amount that had just left the bank. Voiding is
        // the opposite claim (it never happened) and books nothing.
        const r = await api(`/api/tasks/commitments/${id}/${settle ? 'settle' : 'void'}`,
          settle ? { method: 'POST', body: JSON.stringify({ log_expense: true }) }
                 : { method: 'POST' });
        if (settle && r && r.books_skipped) {
          cswToast(r.books_skipped === 'no_cash_account'
            ? 'Settled, but nothing was booked — no NGN bank account with a balance snapshot to book against.'
            : `Settled, but the expense was not booked (${r.books_skipped}).`);
        } else if (settle && r && r.booked_account) {
          cswToast(r.account_inferred
            ? `Settled — booked against ${r.booked_account} (assumed). Change it on the transaction if it left another account.`
            : `Settled — booked against ${r.booked_account}.`);
        }
        await load();
      } catch (err) { cswToast(cswSayWhy(err, { lead: settle ? 'Not marked settled.' : 'Not voided.', unsure: 'Not sure that saved. Wait for the list to refresh before trying again.' })); (settle || voidBtn).disabled = false; }
      return;
    }
    if (e.target.closest('#cc-oblig-addbtn')) {
      host.querySelector('.cc-oblig__form')?.classList.toggle('is-open');
      return;
    }
    if (e.target.closest('#cc-oblig-save')) {
      const f = host.querySelector('.cc-oblig__form');
      if (!f) return;
      const v = sel => f.querySelector(sel)?.value || '';
      const body = {
        label: v('[name=oblig-label]').trim(),
        amount_ngn: parseFloat(v('[name=oblig-amount]')) || 0,
        due_date: v('[name=oblig-date]'),
        direction: v('[name=oblig-direction]') || 'out',
        kind: v('[name=oblig-kind]') || 'other',
        counterparty: v('[name=oblig-counterparty]').trim(),
        // Which account it leaves — carried so settle books against a
        // real account instead of guessing (mig 668). '' = let EON pick.
        account_id: v('[name=oblig-account]') || null,
      };
      if (!body.label || !body.amount_ngn || !body.due_date) {
        cswToast('label, amount and date are needed'); return;
      }
      const btn = f.querySelector('#cc-oblig-save');
      btn.disabled = true; btn.textContent = 'saving…';
      try {
        await api('/api/tasks/commitments', { method: 'POST', body: JSON.stringify(body) });
        await load();
      } catch (err) {
        cswToast(cswSayWhy(err, { lead: 'Not saved.' }));
        btn.disabled = false; btn.textContent = 'save';
      }
    }
  });
}

function _ccObligRender(d) {
  const host = document.getElementById('cc-oblig');
  if (!host || !d || !d.ok) return;
  const pos = d.position || {};
  const col = d.collections || {};
  const due = d.due || [];
  const wall = d.wall || {};
  const ngn = v => (v == null ? '—' : cswNgn(v));
  const dstr = iso => {
    const dt = new Date(iso + 'T00:00:00');
    return `${String(dt.getDate()).padStart(2, '0')} ${dt.toLocaleString('en', { month: 'short' }).toLowerCase()}`;
  };

  const strip = pos.ok ? `
    <div class="cc-oblig__strip">
      <div class="cc-oblig__cell"><b>${ngn(pos.cash_now_ngn)}</b><span>cash now</span></div>
      <div class="cc-oblig__cell cc-oblig__cell--out"><b>${ngn(pos.owed_out_30d_ngn)}</b><span>owed out · 30d${pos.unknown_out_30d_ngn > 0 ? ` · ${ngn(pos.unknown_out_30d_ngn)} of it may already be paid` : ''}</span></div>
      <div class="cc-oblig__cell cc-oblig__cell--in"><b>${ngn(pos.expected_in_30d_ngn)}</b><span>expected in · 30d</span></div>
      <div class="cc-oblig__cell"><b>${ngn(pos.free_after_obligations_ngn)}</b><span>free after obligations</span></div>
      ${pos.verdict_label ? `<span class="cc-oblig__verdict ${pos.verdict === 'BELOW_FLOOR' ? 'is-red' : ''}">${esc(pos.verdict_label)}${pos.runway_months != null ? ` · ${Number(pos.runway_months).toFixed(1)}mo` : ''}</span>` : ''}
    </div>` : '';

  const rows = due.map(r => `
    <div class="cc-oblig__row${r.overdue && !r.payment_unknown ? ' is-overdue' : ''}">
      <span class="cc-oblig__d">${r.payment_unknown ? 'NOT KNOWN IF PAID' : r.overdue ? 'OVERDUE' : dstr(r.due_date)}</span>
      <span class="cc-oblig__l">${esc(r.label)}${r.detail ? `<i>${esc(r.detail)}</i>` : ''}</span>
      <span class="cc-oblig__k">${esc(r.kind_label)}</span>
      <span class="cc-oblig__n">${ngn(r.amount_ngn)}</span>
      ${r.id ? `<span class="cc-oblig__acts"><button class="cc-oblig__act" data-oblig-settle="${esc(r.id)}">settled</button><button class="cc-oblig__act cc-oblig__act--void" data-oblig-void="${esc(r.id)}" title="wrong or cancelled — void it">void</button></span>` : ''}
    </div>`).join('');

  // hand-curated commitments (factory PO legs, funding) enter here —
  // the engines own everything else on the ledger
  const addForm = `
    <div class="cc-oblig__addrow"><button id="cc-oblig-addbtn" class="cc-oblig__act">+ commitment</button></div>
    <div class="cc-oblig__form">
      <input name="oblig-label" placeholder="what is it (e.g. Factory PO W1 balance)" maxlength="200">
      <input name="oblig-amount" type="number" min="1" step="any" placeholder="amount ₦">
      <input name="oblig-date" type="date">
      <select name="oblig-direction"><option value="out">money out</option><option value="in">money in</option></select>
      <select name="oblig-kind"><option value="other">one-off</option><option value="po_leg">factory PO</option><option value="funding">funding</option></select>
      <input name="oblig-counterparty" placeholder="who (optional)" maxlength="120">
      <select name="oblig-account"><option value="">which account (EON picks if blank)</option>${_ccObligAccounts.map(a => `<option value="${esc(a.id)}">${esc(a.name)}</option>`).join('')}</select>
      <button id="cc-oblig-save" class="cc-oblig__act">save</button>
    </div>`;

  const borrowers = (col.by_borrower || []).slice(0, 3)
    .map(b => `${esc(b.borrower)} ${cswNgn(b.outstanding_ngn)}${b.days_until_return != null ? ` (${b.days_until_return}d)` : ''}`)
    .join(' · ');
  const collections = col.ok && col.total_outstanding_ngn ? `
    <div class="cc-oblig__col">owed to you <b>${ngn(col.total_outstanding_ngn)}</b>
      across ${col.count}${col.days_until_next_return != null
        ? (col.days_until_next_return >= 0
          ? ` · next back in ${col.days_until_next_return}d`
          : ` · oldest expected ${-col.days_until_next_return}d ago — chase it`)
        : ''}
      ${borrowers ? `<i>${borrowers}</i>` : ''}</div>` : '';

  const flagged = (wall.flagged_months || []);
  const wallLine = wall.ok && flagged.length ? `
    <div class="cc-oblig__wall">heavy months ahead: ${flagged.slice(0, 4).map(esc).join(' · ')}
      — loans + payroll lean on operating cash</div>` : '';

  // Tax parked (2026-07-29). An absent tax section with no explanation
  // reads as "nothing owed" — say it plainly, and keep the way back one
  // tap away. Owner-only server-side; the button renders for the owner so
  // nobody else sees a control that would 403.
  const park = d.tax_park || {};
  const parkLine = park.parked ? `
    <div class="cc-oblig__park">
      <div class="cc-oblig__park-t">Tax is parked.</div>
      <div class="cc-oblig__park-w">${esc(park.note || '')}</div>
      ${(State.member && State.member.role) === 'owner'
        ? '<button class="cc-oblig__park-b" id="cc-tax-unpark" type="button">turn tax back on</button>'
        : ''}
    </div>` : '';

  host.innerHTML = `
    <div class="cc-sec">obligations<span class="cc-sec__s">what the money owes · real time</span></div>
    <div class="cc-oblig">${strip}
      <div class="cc-oblig__ledger">${rows || '<div class="cc-oblig__row"><span class="cc-oblig__l">nothing due inside 60 days</span></div>'}</div>
      ${parkLine}${addForm}${collections}${wallLine}
    </div>`;

  document.getElementById('cc-tax-unpark')?.addEventListener('click', async (e) => {
    const b = e.currentTarget;
    b.disabled = true; b.textContent = 'turning on…';
    try {
      await api('/api/tasks/obligations/tax-park', {
        method: 'POST', body: JSON.stringify({ parked: false }) });
      cswToast('Tax is back on. Your filings will show again.');
      api('/api/tasks/obligations').then(_ccObligRender).catch(() => {});
    } catch (err) {
      b.disabled = false; b.textContent = 'turn tax back on';
      cswToast(cswSayWhy(err, { lead: 'Tax is still off.' }));
    }
  });
}

// Multi-select on the decisions board (operator 2026-07-29: "add a multi-
// select function so i can bulk approve"). With 19 open cards, one-tap-one-
// card is 19 taps and 19 round trips.
//
// The bar only appears once something is ticked — an empty board must not
// grow a control that does nothing. "approve these" acts on the TICKED
// cards only; there is deliberately no blanket accept-everything control,
// because the whole point of the queue is that he chose each one.
// Pencil-on-a-page — "leave a note on this one".
function noteIcon() {
  return `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h13a2 2 0 0 0 2-2v-6"/><path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>`;
}

// The note drawer (operator 2026-07-29: "add a note icon so when i click
// a drawer opens under any build suggestion so i can add a build note or
// modification") — plus the door his note was really for: "build this",
// which files the card as a real request on the build queue.
//
// The drawer lives INSIDE .ccp. It began as a sibling, which orphans it
// the moment a decision removes the card — every remove path here reaches
// for .closest('.ccp'), so anything that must die with the card has to
// live in it.
function _ccNoteBind() {
  const queue = document.getElementById('cc-props');
  if (!queue) return;

  queue.querySelectorAll('[data-pnote]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const id = e.currentTarget.getAttribute('data-pnote');
      const drawer = document.getElementById(`ccp-drawer-${id}`);
      if (!drawer) return;
      drawer.hidden = !drawer.hidden;
      if (!drawer.hidden) document.getElementById(`ccp-note-${id}`)?.focus();
    });
  });

  const say = (id, msg) => {
    const el = document.getElementById(`ccp-msg-${id}`);
    if (el) el.textContent = msg;
  };

  queue.querySelectorAll('[data-pnotesave]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const b = e.currentTarget;
      const id = b.getAttribute('data-pnotesave');
      const ta = document.getElementById(`ccp-note-${id}`);
      if (!ta) return;
      b.disabled = true; say(id, 'saving…');
      try {
        await api(`/api/proposals/${encodeURIComponent(id)}/note`,
          { method: 'POST', body: JSON.stringify({ note: ta.value }) });
        say(id, ta.value.trim() ? 'saved' : 'note cleared');
        // Keep the local payload honest so a re-render shows the note.
        const cc = _tosState.cc;
        const card = cc?.proposals?.top?.find(x => x.id === id);
        if (card) card.operator_note = ta.value.trim() || null;
        queue.querySelector(`[data-pnote="${CSS.escape(id)}"]`)
          ?.classList.toggle('on', !!ta.value.trim());
      } catch (err) {
        say(id, err.message || 'did not save');
      } finally {
        b.disabled = false;
      }
    });
  });

  queue.querySelectorAll('[data-pbuild]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const b = e.currentTarget;
      const id = b.getAttribute('data-pbuild');
      const ta = document.getElementById(`ccp-note-${id}`);
      b.disabled = true; b.textContent = 'sending…';
      try {
        // Save the note FIRST when it has unsaved text. He types the
        // modification and taps build; losing it there would send EON to
        // build the wrong thing, which is worse than an extra call.
        if (ta && ta.value.trim()) {
          await api(`/api/proposals/${encodeURIComponent(id)}/note`,
            { method: 'POST', body: JSON.stringify({ note: ta.value }) });
        }
        await api(`/api/proposals/${encodeURIComponent(id)}/build`,
          { method: 'POST', body: JSON.stringify({ priority: 'normal' }) });
        b.replaceWith(elFromHTML(
          '<span class="ccp-drawer__queued">on the build list</span>'));
        say(id, '');
        cswToast('On the build list. Nothing starts until you turn the builder on.');
      } catch (err) {
        b.disabled = false; b.textContent = 'build this';
        say(id, err.message || 'could not queue it');
      }
    });
  });
}

// ── value-guard hold (vet 2026-08-01) ───────────────────────────────
// A yes on a card that switches off an earning ad comes back 409 with
// the money the card's author could not see (routes_proposals wrote
// that body "so the UI can show them and offer the override in one
// step" — this is that step; before it the operator got a raw
// "value_guard" toast and no way to insist).
function _vgDetail(err) {
  const d = err && err.status === 409 && err.body && err.body.detail;
  return (d && d.error === 'value_guard') ? d : null;
}

function _vgHold(row, vg, onOverride) {
  if (!row) return false;
  row.querySelector('.vg-hold')?.remove();
  const vgSay = (m) => (typeof cswToast === 'function' ? cswToast(m) : showToast(m));
  const fmt = (n) => (typeof formatNaira === 'function')
    ? formatNaira(n || 0, true) : `₦${Math.round(n || 0).toLocaleString()}`;
  const chk = vg.value_check || {};
  const earners = (chk.targets || []).filter(t => t && t.earns);
  const lines = earners.slice(0, 4).map(t =>
    `<div style="display:flex; justify-content:space-between; gap:8px; padding:1px 0;">
       <span>${esc(t.name || '?')}</span>
       <span style="white-space:nowrap;">${fmt(t.value_ngn)}${t.roas != null ? ` · ${Number(t.roas).toFixed(1)}x` : ''}</span>
     </div>`).join('');
  const box = document.createElement('div');
  box.className = 'vg-hold';
  box.style.cssText = 'margin-top:8px; padding:10px 12px; border:1px solid var(--warn, #d79a4a); border-radius:8px; font-size:12px;';
  box.innerHTML = `
    <div style="font-weight:600; margin-bottom:4px;">Held — approving this switches off ${fmt(vg.value_at_risk_ngn)} of measured revenue</div>
    <div style="color:var(--ink-soft); margin-bottom:6px;">${esc(vg.reason || 'Some of the ads this card retires still earn above the floor.')}</div>
    ${lines}
    <div style="margin-top:8px; display:flex; gap:8px;">
      <button class="vg-go" style="padding:5px 10px; border-radius:6px; border:1px solid var(--warn, #d79a4a); background:transparent; color:inherit; cursor:pointer;">Approve anyway</button>
      <button class="vg-keep" style="padding:5px 10px; border-radius:6px; border:1px solid var(--line); background:transparent; color:inherit; cursor:pointer;">Keep them running</button>
    </div>`;
  row.appendChild(box);
  box.querySelector('.vg-keep').addEventListener('click', () => box.remove());
  box.querySelector('.vg-go').addEventListener('click', async (e) => {
    const b = e.currentTarget;
    b.disabled = true; b.textContent = '…';
    try {
      await onOverride();
    } catch (err2) {
      b.disabled = false; b.textContent = 'Approve anyway';
      vgSay(cswSayWhy(err2, { lead: 'That did not save.' }));
    }
  });
  return true;
}

function _ccBulkBind(errWords) {
  const queue = document.getElementById('cc-props');
  const bar = document.getElementById('cc-props-bulk');
  if (!queue || !bar) return;
  const picked = () => Array.from(
    queue.querySelectorAll('.ccp__cb:checked')).map(cb => cb.getAttribute('data-ppick'));
  const sync = () => {
    const n = picked().length;
    bar.hidden = n === 0;
    const label = document.getElementById('cc-bulk-n');
    if (label) label.textContent = `${n} picked`;
  };
  queue.querySelectorAll('.ccp__cb').forEach(
    cb => cb.addEventListener('change', sync));
  document.getElementById('cc-bulk-clear')?.addEventListener('click', () => {
    queue.querySelectorAll('.ccp__cb').forEach(cb => { cb.checked = false; });
    sync();
  });
  const run = async (decision, btn) => {
    const ids = picked();
    if (!ids.length) return;
    const buttons = Array.from(bar.querySelectorAll('button'));
    buttons.forEach(b => { b.disabled = true; });
    const was = btn.textContent;
    btn.textContent = 'saving…';
    try {
      const res = await api('/api/proposals/decide-many', {
        method: 'POST',
        body: JSON.stringify({ ids, decision }),
      });
      // Only remove what actually landed — a card another device already
      // answered stays visible with its real state on the next refresh.
      const won = new Set((res.results || [])
        .filter(r => r.ok).map(r => r.id));
      won.forEach((id) => {
        // Same authority rule as the single-card path: once this tab has
        // answered an id, a payload from a worker that has not caught up
        // must not put the card back with live controls.
        _propSettleEverywhere(id);   // both rooms + the Command payload (verifier #6)
        queue.querySelector(`.ccp[data-prop="${CSS.escape(id)}"]`)?.remove();
      });
      const cc = _tosState.cc;
      if (cc && cc.proposals && Array.isArray(cc.proposals.top)) {
        cc.proposals.top = cc.proposals.top.filter(c => !won.has(c.id));
        cc.proposals.open = Math.max(0, (cc.proposals.open || 0) - won.size);
      }
      const verb = decision === 'yes' ? 'Approved' : 'Said no to';
      // Guard-held cards are not "already answered" — say which is which
      // (vet 2026-08-01: a held card looked like a save failure).
      const held = (res.results || [])
        .filter(r => !r.ok && r.error === 'value_guard').length;
      const other = (res.failed || 0) - held;
      cswToast([`${verb} ${res.decided}.`,
        held ? `${held} held by the money guard — open those one at a time to see what they earn.` : '',
        other ? `${other} did not save — they may already be answered.` : '',
      ].filter(Boolean).join(' '));
    } catch (err) {
      cswToast(errWords ? errWords(err)
                        : cswSayWhy(err, { lead: 'That did not save.' }));
    } finally {
      buttons.forEach(b => { b.disabled = false; });
      btn.textContent = was;
      sync();
    }
  };
  document.getElementById('cc-bulk-yes')?.addEventListener(
    'click', (e) => run('yes', e.currentTarget));
  document.getElementById('cc-bulk-no')?.addEventListener(
    'click', (e) => run('no', e.currentTarget));
  sync();
}

// An undo that only partly worked, in his words (second adversary on round
// 2, #3540). The undo route sends the ads it turned back on (reversed) and
// the ones still paused (not_reversed). Each is "<platform>:<ad>", the ad
// ends with its name ("meta:meta:<campaign>:<ad set>:WHALE-LOOK"), and a
// failed one may carry its reason after ": ". He knows his ads by name, so
// he reads names, three at most, and where to turn them back on.
function _ccUndoPartly(back, still) {
  const name = (x) => {
    const s = String(x || '').split(': ')[0];
    return s.slice(s.lastIndexOf(':') + 1).trim() || s;
  };
  const n = still.length;
  const shown = still.slice(0, 3).map(name);
  const more = n > shown.length ? ` and ${n - shown.length} more` : '';
  const on = Array.isArray(back) ? back.length : 0;
  const plats = new Set(still.map(x => String(x || '').split(':')[0].toLowerCase()));
  const where = plats.size === 1 && plats.has('meta') ? 'Meta Ads Manager'
    : plats.size === 1 && plats.has('tiktok') ? 'TikTok Ads Manager' : 'Meta or TikTok Ads Manager';
  const head = on ? `Undo only partly worked: ${on} of ${on + n} ads ${on === 1 ? 'is' : 'are'} back on.`
    : 'Not undone.';
  return `${head} Still paused: ${shown.join(', ')}${more}. Turn ${n === 1 ? 'it' : 'them'} back on by hand in ${where}.`;
}

function _ccBind() {
  const body = document.getElementById('tos-body');
  if (!body) return;
  document.getElementById('cc-open-eonq')?.addEventListener('click', () => openEonQueue());
  // Proposal yes/no (owner-only server-side). The row removes itself on
  // success — the queue drains visibly; a 'no' is training data, never
  // a scold. Cards left alone just keep waiting (Constitution #4).
  document.querySelectorAll('#cc-auto [data-pundo]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const el = e.currentTarget;
      const id = el.getAttribute('data-pundo');
      el.disabled = true; el.textContent = '…';
      try {
        await api(`/api/proposals/${encodeURIComponent(id)}/undo`,
          { method: 'POST', body: JSON.stringify({ note: 'operator undo from board' }) });
        // No answered-mark and no cache prune on an undo (Opus vet
        // 2026-09-02, seat 2 #3): a ROUTINE card an undo reopens must
        // come back in "answer these" — the server now flips the ledger
        // row to 'undone' and the reopened card to 'open', so the truth
        // is one forced reload away. The DOM patch is the instant
        // feedback; the reload is the record.
        const receipt = el.closest('.ccr');
        if (receipt) {
          receipt.classList.remove('ccr--undoable');
          receipt.classList.add('ccr--undone');
          const meta = receipt.querySelector('.ccr__meta');
          if (meta) meta.textContent = 'you undid this · just now';
          el.remove();
        } else {
          el.closest('.ccp')?.remove();
        }
        cswToast('Undone. EON will ask you first from now on.');
        setTimeout(() => { if (State.view === 'tasks') _ccLoad(true); }, 600);
      } catch (err) {
        el.disabled = false; el.textContent = 'UNDO';
        // A part-undo is said from the route's own lists, by ad name
        // (_ccUndoPartly): its sentence ran past what cswSayWhy shows once
        // three ads were still paused, and he read "not accepted" with no
        // idea which ads were still dark (second adversary on round 2).
        // The route answers every refusal with a 400, so the status says
        // nothing: only its sentence speaks, or "Try again". A lost answer
        // may hide an undo that worked, so it says check first.
        const d = (err && err.body && err.body.detail) || {};
        const still = Array.isArray(d.not_reversed) ? d.not_reversed : [];
        cswToast(still.length ? _ccUndoPartly(d.reversed, still)
          : cswSayWhy(err && err.status === 400 ? { message: err.message } : err,
            { lead: 'Not undone.', unsure: 'Not sure the undo went through. Refresh this page before trying again.' }), true);
      }
    });
  });
  // The receipt's see-it door opens the same evidence sheet the decide
  // cards use (the ledger row carries the card it acted on).
  document.querySelectorAll('#cc-auto [data-psee]').forEach(b =>
    b.addEventListener('click', () => openIdeaEvidence(b.getAttribute('data-psee'))));
  // A tap that dies because the session lapsed is NOT a failed decision —
  // the card is untouched and the answer was simply never sent. Say that,
  // in words that tell him what to do next. Never auto-replay the tap
  // after he signs back in: an approval has to be a fresh decision.
  const _decideErr = (err) => {
    if (err && (err.status === 401 || err.status === 403)) {
      return 'You got signed out before that saved. Sign in and tap again — nothing was answered.';
    }
    return cswSayWhy(err, { lead: 'That did not save.' });
  };

  document.querySelectorAll('#cc-props [data-pdec]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const el = e.currentTarget;
      const id = el.getAttribute('data-pid');
      const dec = el.getAttribute('data-pdec');
      // The row is .ccp — #1617 renamed the container and this handler
      // kept reaching for the old .cc-card, so an answered card never
      // left the board (audit 2026-07-29). One card takes ONE answer:
      // both buttons go dead on the tap, or the untouched sibling stays
      // live on a decided card and 400s.
      const row = el.closest('.ccp');
      const rowBtns = row ? Array.from(row.querySelectorAll('button')) : [el];
      // Adaptive labels (design flow 2026-07-31): the button may say
      // Approve / Yes / Retire them / Pause them — restore what it
      // actually said, never a hardcoded YES/no.
      const was = el.textContent;
      rowBtns.forEach(b => { b.disabled = true; });
      el.textContent = '…';
      try {
        await api(`/api/proposals/${encodeURIComponent(id)}/decide`,
          { method: 'POST', body: JSON.stringify({ decision: dec }) });
        if (row) row.remove(); else el.remove();
        // Client state is authoritative for this id from here on — a
        // board payload served by a worker that has not seen the
        // decision can no longer put the card back (audit 2026-07-29),
        // and the Ideas room's cache is pruned in the same settle.
        _propSettleEverywhere(id);
        // "EON will get on it" was the board's second stated owner for
        // one card: most approved cards have NO lane executor and a
        // HUMAN does the work — that is what the "waiting on the work"
        // strip below is. Say only what the yes actually did.
        cswToast(dec === 'yes' ? 'Approved. It moves to the work list.'
                               : 'Noted. It will stop suggesting this.');
      } catch (err) {
        rowBtns.forEach(b => { b.disabled = false; });
        el.textContent = was;
        const vg = _vgDetail(err);
        if (vg && dec === 'yes' && _vgHold(row, vg, async () => {
          await api(`/api/proposals/${encodeURIComponent(id)}/decide`,
            { method: 'POST', body: JSON.stringify({ decision: 'yes', override_value_guard: true }) });
          if (row) row.remove(); else el.remove();
          _propSettleEverywhere(id);
          cswToast('Approved over the money guard. It moves to the work list.');
        })) return;
        cswToast(_decideErr(err));
      }
    });
  });
  // 'see it' — the evidence door (design flow 2026-07-31). Rendered for
  // every viewer of the queue; the endpoint enforces the same permission
  // as the board read itself.
  document.querySelectorAll('#cc-props [data-psee]').forEach(b =>
    b.addEventListener('click', () => openIdeaEvidence(b.getAttribute('data-psee'))));
  _ccBulkBind(_decideErr);
  _ccNoteBind();
  // System-health fold: one tap acknowledges every red-feed alert via
  // the EXISTING per-alert endpoint — the row then disappears.
  document.getElementById('ccf-sys-clear')?.addEventListener('click', async (e) => {
    const b = e.currentTarget; b.disabled = true; b.textContent = 'clearing…';
    const ids = (b.getAttribute('data-ids') || '').split(',').filter(Boolean);
    try {
      await Promise.all(ids.map(id => api(`/api/alerts/${id}/acknowledge`, { method: 'POST' })));
      b.closest('.cc-card')?.remove();
    } catch (err) { b.disabled = false; b.textContent = 'clear all'; cswToast(cswSayWhy(err, { lead: 'Not cleared.' })); }
  });
  body.querySelectorAll('[data-cc-task]').forEach(b => b.addEventListener('click', async () => {
    b.disabled = true; b.textContent = 'creating…';
    try {
      await _tosCreateTask({
        title: b.getAttribute('data-cc-title') || 'Follow up',
        priority: 'high',
        related_entity_type: 'recommendation',
        related_entity_id: b.getAttribute('data-cc-task'),
      });
      // handled → the row leaves the desk (operator: auto disappear)
      b.closest('.cc-card')?.remove();
      showToast('Task created and routed.');
    } catch (e) { b.disabled = false; b.textContent = 'task it'; cswToast(cswSayWhy(e, { lead: 'No task made.' })); }
  }));
  body.querySelectorAll('[data-cc-snooze]').forEach(b => b.addEventListener('click', async () => {
    b.disabled = true;
    try {
      await api(`/api/recommendations/${b.getAttribute('data-cc-snooze')}/snooze`,
        { method: 'POST', body: JSON.stringify({ duration_hours: 168 }) });
      b.closest('.cc-card')?.remove();
    } catch (e) { b.disabled = false; cswToast(cswSayWhy(e, { lead: 'Not snoozed.' })); }
  }));
  body.querySelectorAll('[data-cc-ack]').forEach(b => b.addEventListener('click', async () => {
    b.disabled = true;
    try {
      await api(`/api/alerts/${b.getAttribute('data-cc-ack')}/acknowledge`, { method: 'POST' });
      b.closest('.cc-card')?.remove();
    } catch (e) { b.disabled = false; cswToast(cswSayWhy(e, { lead: 'Not cleared.' })); }
  }));
  // A floor chip opens ITS department (vet finding 9): the slug it
  // carried was written and then ignored — every chip opened the same
  // unfiltered page. People OS reads its tab from _posState.
  body.querySelectorAll('[data-cc-dept]').forEach(b => b.addEventListener('click', () => {
    const slug = b.getAttribute('data-cc-dept');
    if (slug && typeof _posState === 'object') _posState.tab = 'dept:' + slug;
    State.view = 'people'; render();
  }));
  body.querySelectorAll('[data-cc-leaktask]').forEach(b => b.addEventListener('click', async () => {
    b.disabled = true; b.textContent = 'creating…';
    const name = b.getAttribute('data-cc-leaktask') || 'an ad';
    const doLine = b.getAttribute('data-cc-do') || 'stop the leak';
    try {
      await _tosCreateTask({
        title: `Ad leak: ${name} — ${doLine}`.slice(0, 200),
        priority: 'high',
      });
      b.textContent = 'tasked';
      showToast('Task created and routed.');
    } catch (e) { b.disabled = false; b.textContent = 'task it'; cswToast(cswSayWhy(e, { lead: 'No task made.' })); }
  }));
}

async function _tosLoad() {
  const room = document.querySelector('#tos.task-room');
  if (room && _tr.loading) return;
  if (room) _tr.loading = true;
  const body = document.getElementById('tos-body');
  try {
    const [b, p, m] = await Promise.all([
      api('/api/tasks/board'), api('/api/tasks/pulse'),
      api('/api/tasks/mine').catch(e => ({ tasks: [], error: e.message })),
    ]);
    if (b?.ok === false || p?.ok === false) throw new Error(b.error || p.error || 'Task data could not be read');
    if (room && (!room.isConnected || _tosState.mode !== 'staff')) { _tr.loading = false; return; }
    if (m?.ok === false && !m.error) m.error = 'My work could not be read';
    _tosState.board = b; _tosState.pulse = p; _tosState.mine = m;
    eonSoundOnPulse(p);
    _tosState.tasks = (b && b.tasks) || [];
    _tosState.byId = {}; _tosState.tasks.forEach(t => _tosState.byId[t.id] = t);
  } catch (e) {
    if(room && (!room.isConnected || _tosState.mode!=='staff')){_tr.loading=false;return;}
    if (room) { _tr.loading = false; document.getElementById("tr-freshness").textContent = "READ FAILED"; document.getElementById("tr-signal-message").textContent = "Task data could not be refreshed. Use Refresh to retry."; }
    if (body) body.innerHTML = `<div class="tos-empty"><div class="tos-empty__i">!</div>${esc(e.message || e)}</div>`;
    return;
  }
  if (room) { _tr.loading = false; _trReadAccepted(); }
  // A refresh rebuilds every card and list row. The one holding keyboard focus
  // hands it to its replacement, so the room's poll never drops focus on BODY.
  const held = document.activeElement?.closest?.('#tos-body [data-tid], #tos-body [data-tr-task]');
  const heldAttr = held?.hasAttribute('data-tid') ? 'data-tid' : 'data-tr-task';
  _tosRenderPulse();
  _tosRenderView();
  const heldLost = held && !held.isConnected && [document.body, document.documentElement, null].includes(document.activeElement);
  const heldNext = heldLost && document.querySelector(`#tos-body [${heldAttr}="${CSS.escape(held.getAttribute(heldAttr))}"]`);
  if (heldNext && !heldNext.closest('[inert]') && heldNext.getClientRects().length) heldNext.focus({ preventScroll: true });
  _tosOpenDeepLink();
  _tosLoadMeter();
}

// ── the relay meter (PR-R1) ─────────────────────────────────────────
// Measurement before mechanism: before a single relay wire is laid, the
// Tasks page can show what today's rails actually do with an ask — how
// many get told to somebody, how many get acknowledged, how many close,
// and how long each leg takes.
//
// DARK until the operator arms EON_RELAY. The door answers
// {armed:false} without touching the database, and this returns having
// painted nothing — so a flag-off Tasks page is the page it was
// yesterday, element for element. Nothing is appended to the shell's
// own markup for the same reason: the panel is INSERTED after the pulse
// strip once the server says the relay is armed, never reserved.
//
// Fire-and-forget, never awaited: the board is what the reader came for
// and a slow meter must not hold it back, nor a failing one blank it.
//
// The host check comes BEFORE the fetch and the existing panel is
// REPLACED rather than treated as a reason to stop (R1 verifier, F2):
// _tosLoad() runs on every board refresh, so early-returning on a panel
// that is already there froze the meter at its first reading for the
// life of the page — and a reader on another view paid for a call whose
// answer had nowhere to land.
async function _tosLoadMeter() {
  const host = document.getElementById('tos-pulse');
  if (!host) return;
  let d = null;
  try { d = await api('/api/tasks/relay-meter'); } catch (error) {
    if (_trActive()) { _tr.meter={error:true}; if(_tosState.view==='insights'&&_tosState.mode==='staff')_trRenderView(); }
    return;
  }
  if (_trActive()) { _tr.meter=d; if(_tosState.view==='insights'&&_tosState.mode==='staff')_trRenderView(); return; }
  if (!d || d.armed !== true || !d.ok) return;
  const old = document.getElementById('tos-meter');
  const el = elFromHTML(_tosMeterHTML(d));
  if (old) old.replaceWith(el); else host.insertAdjacentElement('afterend', el);
}

// The frozen 2026-08-10 completion figure, shown beside today's so the
// panel is a comparison and not just a number. A literal because it is a
// fact about a date, not a reading.
const BASELINE_CLOSURE = 26;

// ══ THE RELAY · the rooms the ask lands in ═════════════════════════
// Design contract: the operator-approved artifact "THE RELAY — the rooms
// it lands in" (2026-08-11), amended into docs/house/floors/6 and /1 in
// the same diff. Three surfaces share these three helpers so the clock,
// the ladder and the byline read the SAME on the Job Board, in the Crew
// Room and on the team wire — a fourth vocabulary is how one of them
// starts lying.
//
// EVERY ONE OF THEM IS PAYLOAD-GATED. They render from a key the server
// only sends when EON_RELAY is armed (`t.relay`, `armed:true`), so a
// dark deployment paints nothing and the three rooms are the rooms they
// were yesterday, element for element.

// The clock. Amber while it ticks, LIME once a human stopped it — and
// it stops on the ack, never on a timer, because the whole promise of
// the ack clock is that a person said "I've got it".
function relayClockHTML(c) {
  if (!c) return '';
  if (c.acked_at) return `<span class="rl-clock rl-clock--stopped">acked</span>`;
  const m = c.ack_minutes_left;
  if (m === null || m === undefined) return '';
  const over = m < 0;
  const n = Math.abs(m);
  const t = n >= 60 ? `${Math.floor(n / 60)}h ${n % 60}m` : `${n}m`;
  return `<span class="rl-clock${over ? ' rl-clock--over' : ''}">`
    + `${over ? t + ' over' : t + ' to ack'}</span>`;
}

// The ladder, DRAWN rather than implied: on-shift → dept head →
// Operations. Three pips, the lit ones filled, the last one red because
// there is nowhere above the operator to go.
function relayRungHTML(c) {
  if (!c || !c.rung_index) return '';
  const of = c.rung_of || 3;
  let pips = '';
  for (let i = 1; i <= of; i++) {
    pips += `<i class="${i <= c.rung_index ? (i === of ? 'top' : 'lit') : ''}"></i>`;
  }
  return `<span class="rl-rung">${pips}<em>rung ${c.rung_index} · ${esc(c.rung_word || '')}</em></span>`;
}

// The byline. "Task OS" stops signing other people's work here: a
// resolved human says their name, an EON-minted ask says EON, and an
// unresolvable requester says so IN WORDS rather than letting the board
// borrow somebody else's name for it.
function relayAskerHTML(requester, isEon, dept, store) {
  const who = isEon
    ? '<i class="rl-eon">EON</i>'
    : `<i>${esc(requester || 'requester not recorded')}</i>`;
  const to = [dept, store].filter(Boolean).map(esc).join(' · ');
  return `<span class="rl-from">from ${who}${to ? ' &rarr; ' + to : ''}</span>`;
}

// THE REASON, IN WORDS A PERSON USES. Every ask carries a reason token
// — the key the wires dedup on and the meter groups by — and three
// surfaces were printing that key raw: a Job Board card read
// "T-311 · team_wire", the meter's wire table read "low_cover", and
// neither is a phrase anybody in this company says out loud.
//
// The token stays the token everywhere it is a KEY (dedup, grouping,
// the server's own vocabulary); this map is only what a reader sees.
// All eight doors are here — the six wires plus the two free-text
// surfaces — so a card can never fall back to showing the raw key.
const RELAY_REASON_WORDS = {
  payment_claim:    'payment check',
  delivery_promise: 'delivery promise',
  order_complete:   'order to pack',
  low_cover:        'running low',
  stock_out:        'stock check',
  complaint:        'complaint',
  desk_ask:         'asked at the desk',
  team_wire:        'asked in chat',
};

// The meter's own bucket for every reason a WIRE did not write
// (relay_meter.FREE_TEXT_BUCKET) — a server key, humanised here rather
// than renamed there, because it is half a grouping contract.
const RELAY_FREE_TEXT_WORDS = 'asked in their own words';

function relayReasonWord(reason) {
  const key = String(reason || '').split(':')[0].trim();
  if (!key) return '';
  if (key === 'desk (free-text)') return RELAY_FREE_TEXT_WORDS;
  // An unknown key is shown with its underscores taken out rather than
  // hidden: a wire added tomorrow reads a little plainly instead of
  // vanishing off the card that was meant to explain it.
  return RELAY_REASON_WORDS[key] || key.replace(/_/g, ' ');
}

function _tosMeterPct(v) {
  return (v === null || v === undefined) ? '—' : `${Math.round(v * 100)}%`;
}

// A median with no sample behind it is not a number the page may state.
// The server sends n with every leg; under 3 the cell says so.
function _tosMeterLeg(legs, key) {
  const l = (legs || {})[key] || {};
  if (l.median_min === null || l.median_min === undefined) return '—';
  if ((l.n || 0) < 3) return `~${Math.round(l.median_min)}m (n=${l.n || 0})`;
  const m = l.median_min;
  return m >= 90 ? `${(m / 60).toFixed(1)}h` : `${Math.round(m)}m`;
}

function _tosMeterHTML(d) {
  const counts = {};
  (d.stages || []).forEach(s => { counts[s.stage] = s.tasks; });
  const minted = counts.minted || 0;
  const legs = d.legs || {};
  // The stages are reached-flags, not a cascade — closed can and does
  // exceed acked, because people finish work they never acknowledged.
  // The row shows them in road order and lets that read as the finding.
  //
  // THE FROZEN BASELINE RIDES UNDER EACH STAGE (the rooms artifact's
  // third ruling): the panel is a COMPARISON, not four numbers, and
  // "if the meter doesn't show the lift, the relay didn't earn it" only
  // means anything if the thing it must clear is printed beside it. The
  // sentences come from the server (relay_rooms.stage_baselines, off
  // relay_meter's own BASELINE_2026_08_10) so the figures live in one
  // place and the panel cannot drift from the brief that quotes them.
  const base = {};
  (d.stage_baselines || []).forEach(b => { base[b.stage] = b.note; });
  // The ack cell carries the median it finally has a rail to measure.
  const ackLeg = _tosMeterLeg(legs, 'pinged_to_acked');
  const stageLabel = { minted: 'minted', pinged: 'pinged',
                       acked: ackLeg === '—' ? 'acked' : `acked · ${ackLeg} median`,
                       closed: 'closed' };
  const stages = ['minted', 'pinged', 'acked', 'closed']
    .map(k => `<div class="tos-meter__stage${k === 'acked' ? ' tos-meter__stage--ack' : ''}">
        <div class="tos-meter__v">${counts[k] || 0}</div>
        <div class="tos-meter__l">${esc(stageLabel[k])}</div>
        <div class="tos-meter__p">${k === 'minted' ? '' :
          esc(_tosMeterPct(minted ? (counts[k] || 0) / minted : null))}</div>
        <div class="tos-meter__b">${esc(base[k] || '')}</div>
      </div>`).join('');
  const rows = (d.sources || []).slice(0, 6).map(s => `<tr>
      <td class="tos-meter__src">${esc(s.source)}</td>
      <td>${s.minted}</td>
      <td>${esc(_tosMeterPct(s.ping_rate))}</td>
      <td>${esc(_tosMeterPct(s.ack_rate))}</td>
      <td>${esc(_tosMeterPct(s.closure_rate))}</td>
    </tr>`).join('');
  const depts = (d.departments || []).slice(0, 5).map(r => `<tr>
      <td class="tos-meter__src">${esc(r.department)} · ${esc(r.store)}</td>
      <td>${r.asks}</td>
      <td>${r.answered}</td>
      <td colspan="2">${esc(_tosMeterPct(r.answer_rate))}</td>
    </tr>`).join('');
  // WHICH WIRE, not just which entity (R6 item 5a). by_reason slices on
  // metadata.relay.reason — the lane that FIRED — where the source table
  // above slices on what the ask points AT, and three wires point at a
  // cs_thread. Only rows the relay's own door minted appear, so on a
  // dark deployment this table is simply empty rather than padded with
  // every task in the company.
  const wires = (d.by_reason || []).slice(0, 8).map(r => `<tr>
      <td class="tos-meter__src">${esc(relayReasonWord(r.reason))}</td>
      <td>${r.minted}</td>
      <td>${esc(_tosMeterPct(r.ping_rate))}</td>
      <td>${esc(_tosMeterPct(r.ack_rate))}</td>
      <td>${esc(_tosMeterPct(r.closure_rate))}</td>
    </tr>`).join('');
  // THE TWO BLOCKS THE SERVER HAS ALWAYS SENT AND THE PAGE NEVER READ
  // (R6 item 5b). ping_health is the per-channel delivery + read that
  // R1's frozen 0.4% WhatsApp rail lives in, and ack_doors is the web-
  // tap-vs-WhatsApp-button split the ack door was instrumented for —
  // both computed on every meter read, both paid for, neither rendered.
  // Two compact rows, not two more tables: they are one sentence each.
  const ph = d.ping_health || {};
  const chans = (ph.channels || []).slice(0, 4).map(c =>
    `${esc(c.channel)} ${esc(_tosMeterPct(c.delivered_rate))} landed`
    + ` · ${esc(_tosMeterPct(c.read_rate))} read (${c.queued})`).join(' · ');
  const doorRow = Object.entries(d.ack_doors || {})
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${esc(k)} ${v}`).join(' · ');
  const health = chans
    ? `<div class="tos-meter__legs tos-meter__legs--ping"><span>pings</span>
         <span>${chans}</span></div>` : '';
  const doors = doorRow
    ? `<div class="tos-meter__legs tos-meter__legs--doors"><span>ack doors</span>
         <span>${doorRow}</span></div>` : '';
  // The figure that may be set BESIDE the baseline is the two-cohort one
  // — closed-in-window over asked-in-window, the 26% figure's own
  // arithmetic. The funnel's own `closed` below is single-cohort and is
  // NOT comparable to it (relay_meter docstring, R1 verifier F4).
  const twoCohort = _tosMeterPct(d.closure_two_cohort);
  // A read that came back clipped says so, quietly (F6).
  const partial = d.partial ? ' · ~partial' : '';
  return `<div class="tos-meter" id="tos-meter">
    <div class="tos-meter__h">relay meter · ${d.days || 30}d${partial}
      <span>${esc(twoCohort)} completed vs ${BASELINE_CLOSURE}% baseline 2026-08-10</span></div>
    <div class="tos-meter__funnel">
      <div class="tos-meter__lbl"><b>The meter</b>
        <span>per source · ${d.days || 30} days · frozen 2026-08-10 baseline under each stage</span></div>
      ${stages}</div>
    <div class="tos-meter__legs">
      <span>told ${esc(_tosMeterLeg(legs, 'minted_to_pinged'))}</span>
      <span>ack ${esc(_tosMeterLeg(legs, 'pinged_to_acked'))}</span>
      <span>close ${esc(_tosMeterLeg(legs, 'minted_to_closed'))}</span>
    </div>
    <table class="tos-meter__t"><thead><tr>
        <th>source (worst first)</th><th>asked</th><th>told</th>
        <th>ack</th><th>closed</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="5">no asks in window</td></tr>'}</tbody></table>
    ${health}${doors}
    ${wires ? `<table class="tos-meter__t"><thead><tr>
        <th>wire (worst first)</th><th>asked</th><th>told</th>
        <th>ack</th><th>closed</th></tr></thead>
      <tbody>${wires}</tbody></table>` : ''}
    <table class="tos-meter__t"><thead><tr>
        <th>department · store</th><th>asks</th><th>answered</th>
        <th colspan="2">rate</th></tr></thead>
      <tbody>${depts || '<tr><td colspan="5">nothing addressed yet</td></tr>'}</tbody></table>
  </div>`;
}

// ?task=<id> opens that task's drawer once (relay R0 item 6). This is the
// far end of the deep link every task notification now carries: the bell,
// the lock-screen push and the WhatsApp ping all point here. The param is
// kept for the owner's connected task history; legacy seats consume it once.
function _tosOpenDeepLink() {
  try {
    const qs = new URLSearchParams(location.search);
    const id = qs.get('task');
    if (!id || document.querySelector('[data-task-detail="'+CSS.escape(id)+'"]')) return;
    if (_mailAllowed()) { openTaskDetail(id); return; }
    qs.delete('task');
    const rest = qs.toString();
    history.replaceState(history.state, '',
      location.pathname + (rest ? `?${rest}` : ''));
    openTaskDetail(id);
  } catch (_) { /* sandboxed history — the board still rendered */ }
}

function _tosRenderPulse() {
  if (_trActive()) return _trPulse();
  const p = _tosState.pulse || {};
  const el = document.getElementById('tos-pulse');
  if (!el) return;
  const ring = p.ring || {};
  const tiles = [
    ['mywork', p.my_open ?? '—', `Mine${p.my_overdue ? ' · ' + p.my_overdue + ' overdue' : ''}`, p.my_overdue > 0],
    ['stuck', p.org_stuck ?? '—', 'Stuck org-wide', (p.org_stuck ?? '—') > 0],
    ['ring', `${ring.points ?? '—'}/${ring.target ?? '—'}`, 'My ring', false],
    ['week', p.team_week_points ?? '—', 'Team · 7d', false],
    ['eonq', p.eon_queued ?? '—', 'EON to approve', (p.eon_queued ?? '—') > 0],
    ['rate', p.rating_queue ?? '—', 'Awaiting verify', false],
  ];
  el.className = 'tos-pulse';
  el.innerHTML = tiles.map(([k, v, l, alert]) =>
    `<button class="tos-stat ${alert ? 'tos-stat--alert' : ''}" data-pulse="${k}"><div class="tos-stat__v">${v}</div><div class="tos-stat__l">${esc(l)}</div></button>`).join('');
  el.querySelectorAll('.tos-stat').forEach(b => b.addEventListener('click', () => {
    const k = b.getAttribute('data-pulse');
    if (k === 'eonq') return openEonQueue();
    if (k === 'rate') {
      // "Awaiting verify" used to open My Work — the tapper's OWN tasks —
      // where the team's finished work never appears (gap-map point 8).
      // The verify queue lives on People OS; go straight to it.
      _posState.tab = 'tasks'; State.view = 'people'; render(); return;
    }
    if (k === 'mywork') { _tosState.view = 'mywork'; _tosRenderView(); }
    else { _tosState.view = 'list'; _tosRenderView(); }
  }));
}

function _tosRenderView() {
  if (_trActive()) return _trRenderView();
  const body = document.getElementById('tos-body');
  if (!body) return;
  const views = TOS_VIEWS.filter(([k]) => k !== 'recurring' || memberCan('tasks:assign'));
  const sw = `<div class="tos-seg" id="tos-seg">${views.map(([k, l]) =>
    `<button class="${_tosState.view === k ? 'on' : ''}" data-v="${k}">${l}</button>`).join('')}</div>`;
  let view = '';
  if (_tosState.view === 'mywork') view = _tosMyWork();
  else if (_tosState.view === 'board') view = _tosBoard();
  else if (_tosState.view === 'list') view = _tosList();
  else if (_tosState.view === 'dept') view = _tosDept();
  else if (_tosState.view === 'recurring') {
    view = _tosRecurring();
    if (!_tosState.recurring) _tosLoadRecurring().then(() => {
      if (_tosState.view === 'recurring') _tosRenderView();
    });
  }
  else if (_tosState.view === 'ideas') {
    view = _tosIdeas();
    // Lazy: only pay for the two reads when the tab is actually opened.
    if (!_tosState.ideas) _tosLoadIdeas().then(() => {
      if (_tosState.view === 'ideas') _tosRenderView();
    });
  }
  body.innerHTML = `${sw}<div style="margin-top:12px">${view}</div>`;
  document.querySelectorAll('#tos-seg button').forEach(b => b.addEventListener('click', () => {
    _tosState.view = b.getAttribute('data-v'); localStorage.setItem('tos.view', _tosState.view); _tosRenderView();
  }));
  // Procurement: My Work hero carries the staff "Request finance" entry.
  document.getElementById('prc-tos-btn')?.addEventListener('click', () => prcOpenRequestSheet());
  prcHydrateMyRequests();
  _tosBindCards();
  // Ideas tab: see-it + adaptive decide controls (design flow 2026-07-31).
  if (_tosState.view === 'ideas') _tosBindIdeas();
  if (_tosState.view === 'recurring') _tosBindRecurring();
}

function _tosFmtDue(due) {
  if (!due) return '';
  const d = new Date(due), now = new Date();
  const h = Math.round((d - now) / 3.6e6);
  if (h < 0) return `<span style="color:var(--danger)">${Math.abs(h) < 48 ? -h + 'h over' : Math.round(-h / 24) + 'd over'}</span>`;
  return h < 48 ? `${h}h left` : `${Math.round(h / 24)}d left`;
}

function _tosCard(t, draggable) {
  if (_trActive()) return _trCard(t, draggable);
  // One-line meta, signal only (2026-07-14): EON origin, HIGH priority
  // (normal is implied — the pill was noise), assignee, stuck/slow
  // velocity (fast/normal implied), due, ₦. Department + the rest live
  // in the detail sheet.
  const bits = [];
  if (t.created_by_eon) bits.push('<span class="tos-eon">EON</span>');
  if (t.priority === 'high') bits.push('<span class="tos-pri tos-pri--high">high</span>');
  bits.push(esc(t.assignee_name || 'unassigned'));
  if (t.velocity === 'stuck' || t.velocity === 'slow')
    bits.push(`<span class="tos-vel--${t.velocity}">${t.velocity}</span>`);
  if (t.due_at) bits.push(_tosFmtDue(t.due_at));
  if (t.estimated_impact_ngn) bits.push(`<span class="ccf-ngn">${cswNgn(t.estimated_impact_ngn)}</span>`);
  // THE ASK HAS A FACE (the rooms artifact, floor 6). `t.relay` is sent
  // ONLY for a relay ask and ONLY when the operator has armed the lane,
  // so a wall of SOP cards is untouched and a dark board is the board it
  // was yesterday — this whole block is skipped on a falsy key.
  const r = t.relay;
  if (r) {
    // The clock rides the card, in the meta line where the eye already
    // reads state. The rung and the byline get their own rows because
    // "who asked" is the question the board could not answer at all.
    //
    // FIRST, NOT LAST. Measured 2026-08-13 at 1280px: `.tos-card__m` is
    // `white-space:nowrap; overflow:hidden`, and on a busy ask (high +
    // owner + due + ₦) the clock was pushed to the tail and clipped to
    // 28 of its 74 pixels — the card that most needed its clock was the
    // one that lost it. `.tos-card--ask` now wraps that line (tasks.css)
    // AND the clock leads it, so the live state cannot be the thing that
    // falls off the end.
    const clk = relayClockHTML(r);
    if (clk) bits.unshift(clk);
    return `<button class="tos-card tos-card--ask" data-tid="${esc(t.id)}"${draggable ? ' draggable="true"' : ''}>
      <div class="tos-ask__id">${esc(r.short_id || '')}${r.reason ? ' <span>· ' + esc(relayReasonWord(r.reason)) + '</span>' : ''}</div>
      <div class="tos-card__t">${esc(t.title || 'Untitled')}</div>
      ${relayAskerHTML(t.requester_name, t.created_by_eon, r.dept, r.store_name)}
      <div class="tos-card__m">${bits.join(' <span class="tos-dotsep">·</span> ')}</div>
      ${relayRungHTML(r)}</button>`;
  }
  return `<button class="tos-card" data-tid="${esc(t.id)}"${draggable ? ' draggable="true"' : ''}>
    <div class="tos-card__t">${esc(t.title || 'Untitled')}</div>
    <div class="tos-card__m">${bits.join(' <span class="tos-dotsep">·</span> ')}</div></button>`;
}

// Abandoned-cart fold (2026-07-14): the hourly recovery cron creates
// one task per cart — rendered inline they drown everything (the
// operator's May complaint, fixed then in a now-dead render path).
// Live carts collapse to one whisper row; stale ones expire server-side
// (tasks_recurring.expire_stale_cart_tasks, 14d TTL).
function _tosCartFold(tasks) {
  const carts = tasks.filter(t => (t.category || '') === 'abandoned_checkout_recovery');
  const rest = tasks.filter(t => (t.category || '') !== 'abandoned_checkout_recovery');
  return { carts, rest };
}

function _tosCartSummary(carts) {
  if (!carts.length) return '';
  return `<details class="tos-cartfold"><summary class="tos-sec">abandoned carts · ${carts.length}</summary>
    ${carts.map(t => _tosCard(t)).join('')}</details>`;
}

function _tosMyWork() {
  const mine = (_tosState.mine && _tosState.mine.tasks) || [];
  const ring = (_tosState.pulse || {}).ring || {};
  const now = new Date();
  const nowIso = now.toISOString();
  // Section boundaries on the staff member's local clock (Lagos phones).
  const sod = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  const eod = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString();
  const open = mine.filter(t => !['verified', 'cancelled', 'done', 'completed'].includes(t.status));
  const overdue = open.filter(t => t.due_at && t.due_at < nowIso);
  const today = open.filter(t => !overdue.includes(t) && t.due_at && t.due_at < eod);
  const upcoming = open.filter(t => !overdue.includes(t) && !today.includes(t));
  const doneToday = mine.filter(t => ['completed', 'done', 'verified'].includes(t.status)
    && ((t.verified_at || t.completed_at || '') >= sod));
  const pointsKnown = ring.points != null && ring.target != null;
  const hero = `<div class="csw-card csw-hero" style="margin-bottom:14px"><div class="csw-hero__row">${pointsKnown ? cswRing(ring.points, ring.target) : ''}
    <div class="csw-hero__meta"><div class="csw-hero__name">Your work</div>
    <div class="csw-hero__hint">${!pointsKnown ? 'Points unavailable' : ring.closed === true ? 'Ring closed today — nice.' : `${Math.max(0, ring.target - ring.points)} pts to close · verify quality tasks to earn`}</div>
    ${typeof prcCanRequest === 'function' && prcCanRequest() ? '<button class="prc-req-btn prc-req-btn--sm" id="prc-tos-btn" type="button">Request finance</button>' : ''}
    <div class="prc-mine" id="prc-mine" hidden></div></div></div></div>`;
  if (!open.length && !doneToday.length) return hero + `<div class="tos-empty"><div class="tos-empty__i">✓</div>No tasks routed to you. EON allocates from business insight automatically.</div>`;
  const sec = (label, list) => list.length ? `<div class="tos-sec">${label} · ${list.length}</div>${list.map(t => _tosCard(t)).join('')}` : '';
  const done = doneToday.length ? `<details class="tos-done"><summary class="tos-sec">Done today · ${doneToday.length}</summary>${doneToday.map(t => _tosCard(t)).join('')}</details>` : '';
  return hero + sec('Overdue', overdue) + sec('Today', today) + sec('Upcoming', upcoming) + done;
}

// HOUSE floor 6 — the load rail (approved refinement, 2026-08-07): one
// strip answering "who is carrying what, right now" — cards in flight as
// visible capacity slots per pair of hands. Built purely off the board
// payload (assignee_name rides every task); no extra fetch. A full rail
// burns amber. House-only: classic board renders byte-identical.
function _tosHouseLoadRail(tasks) {
  if (!tosHouse()) return '';
  const INFLIGHT = ['assigned', 'acknowledged', 'in_progress', 'blocked', 'completed', 'done'];
  const by = {};
  (tasks || []).forEach(t => {
    if (INFLIGHT.includes(t.status) && t.assignee_name) {
      (by[t.assignee_name] = by[t.assignee_name] || []).push(t);
    }
  });
  const rows = Object.entries(by).sort((a, b) => b[1].length - a[1].length).slice(0, 8);
  if (!rows.length) return '';
  const CAP = 5;
  return `<div class="tos-hload">
    <span class="tos-hload__eyebrow">The load rail — cards in flight · who's carrying what</span>
    ${rows.map(([who, list]) => {
      const n = list.length;
      const full = n >= CAP;
      return `<div class="tos-hload__row"><span class="tos-hload__who">${esc(who)}</span>
        <span class="tos-hload__rail">${Array.from({ length: CAP }, (_, i) =>
          `<i class="${i < Math.min(n, CAP) ? (full ? 'hot' : 'on') : ''}"></i>`).join('')}</span>
        <span class="tos-hload__n num${full ? ' full' : ''}">${n} / ${CAP}${full ? ' · full' : ''}</span></div>`;
    }).join('')}</div>`;
}

function _tosBoard() {
  const { carts, rest } = _tosCartFold(_tosState.tasks);
  return `${_tosHouseLoadRail(_tosState.tasks)}<div class="tos-board">${TOS_COL.map(([k, label, statuses]) => {
    const col = rest.filter(t => statuses.includes(t.status));
    const imp = col.reduce((s, t) => s + (t.estimated_impact_ngn || 0), 0);
    const fold = k === 'todo'
      ? _tosCartSummary(carts.filter(t => ['open', 'assigned', 'acknowledged'].includes(t.status))) : '';
    return `<div class="tos-col" data-col="${k}"><div class="tos-col__h"><span>${label} · ${col.length}</span>${imp ? `<span>${cswNgn(imp)}</span>` : ''}</div>
      <div class="tos-col__drop" data-col="${k}">${col.map(t => _tosCard(t, true)).join('') || '<div class="tos-empty" style="padding:14px;font-size:11px">—</div>'}${fold}</div></div>`;
  }).join('')}</div>`;
}

// Drag a card between board columns = optimistic status transition.
// Column → API mapping: To do = acknowledge, In progress = start,
// Blocked and Review open their response forms before any state change.
// Verified opens the rating sheet; a drop cannot claim an outcome.
function _tosBindBoardDrag() {
  document.querySelectorAll('#tos-body .tos-card[draggable="true"]').forEach(c => {
    c.addEventListener('dragstart', e => {
      e.dataTransfer.setData('text/tos-task', c.getAttribute('data-tid'));
      e.dataTransfer.effectAllowed = 'move';
      c.classList.add('tos-card--drag');
    });
    c.addEventListener('dragend', () => c.classList.remove('tos-card--drag'));
  });
  document.querySelectorAll('#tos-body .tos-col__drop').forEach(z => {
    z.addEventListener('dragover', e => { e.preventDefault(); z.classList.add('tos-col--over'); });
    z.addEventListener('dragleave', () => z.classList.remove('tos-col--over'));
    z.addEventListener('drop', e => {
      e.preventDefault(); z.classList.remove('tos-col--over');
      const id = e.dataTransfer.getData('text/tos-task');
      if (id) _tosDropTransition(id, z.getAttribute('data-col'));
    });
  });
}

async function _tosDropTransition(id, col) {
  const t = _tosState.byId[id];
  if (!t || t._moving) return;
  const colDef = TOS_COL.find(c => c[0] === col);
  if (!colDef || colDef[2].includes(t.status)) return; // same column
  if (['verified','cancelled','expired'].includes(t.status)) {
    showToast('This task is closed. Read its recorded outcome before creating follow-up work.');
    return openTaskDetail(id);
  }
  if (['blocked','review','verified'].includes(col)) {
    const ov=await openTaskDetail(id);
    if(!ov?.isConnected)return;
    const current=_tosState.byId[id];
    if (col === 'verified') return openVerifyRate(ov, current);
    return _tosOutcomeForm(ov,current,col==='blocked'?'blocked':'complete');
  }
  const target = { todo: 'acknowledged', doing: 'in_progress' }[col];
  if (!target) return;
  const prev = t.status;
  t._moving=true;
  t.status = target; // optimistic — rolled back below if the API rejects
  _tosRenderView();
  try {
    const d = await api(`/api/tasks/${id}/transition`, { method: 'POST', body: JSON.stringify({ status: target }) });
    if (!d?.ok) throw new Error((d && (d.error?.error || d.error)) || 'Move rejected');
    showToast('Moved');
    _tosLoad();
  } catch (e) {
    t.status = prev; // roll back the optimistic move
    _tosRenderView();
    showToast(String(e.message || e));
  } finally { delete t._moving; }
}

function _tosList() {
  // verified rows live in the Board's Done column + My Work's "done
  // today" whisper — repeating them in List is noise.
  const { carts, rest } = _tosCartFold(_tosState.tasks.filter(t => t.status !== 'verified'));
  const tasks = rest.slice().sort((a, b) => (a.due_at || 'z').localeCompare(b.due_at || 'z'));
  if (!tasks.length && !carts.length) return `<div class="tos-empty"><div class="tos-empty__i">○</div>No tasks.</div>`;
  return tasks.map(t => _tosCard(t)).join('') + _tosCartSummary(carts);
}

function _tosDept() {
  const tasks = _tosState.tasks;
  const depts = Array.from(new Set(tasks.map(t => t.department || 'Unassigned'))).sort();
  return depts.map(dp => {
    const col = tasks.filter(t => (t.department || 'Unassigned') === dp);
    const stuck = col.filter(t => t.velocity === 'stuck').length;
    return `<div class="csw-card" style="margin-bottom:10px"><div class="csw-card__h"><div class="csw-card__t">${esc(dp)}</div>
      <div class="csw-card__t">${col.length} tasks${stuck ? ` · ${stuck} stuck` : ''}</div></div>
      ${col.slice(0, 8).map(t => _tosCard(t)).join('')}${col.length > 8 ? `<div class=\"csw-goalmeta\">${col.length - 8} more hidden</div>` : ''}</div>`;
  }).join('');
}

// ── Ideas: what EON has noticed and is waiting on a decision for ──────
//
// DESIGN FLOW editorial rebuild (2026-07-31, feature_requests 5071a0b4;
// operator: "i want the idea section to be more pronounced ... ideas
// must be actionable ... the approve button must adapt to what is being
// approved"). Grammar from his reference folder: huge lowercase mast
// word with a period, a numbered 001-index as structure, the card's
// HERO is its biggest NUMBER, bracketed micro-labels in tiny
// wide-tracked mono, ONE saturated accent block per card, ruthless
// negative space — adapted onto EON's internal dark/green tokens
// (CRUX yellow/black stays customer-facing only).
//
// Three sources, deliberately not merged into the task list:
//   proposal_cards           -> the Decisions board  (business ideas)
//   system_audit_proposals   -> System Audit         (code ideas)
//   evolution gaps           -> what EON cannot do yet
//
// Tasks is the list of things to DO. An idea is something to DECIDE —
// different verb. Business cards now decide RIGHT HERE through the same
// owner-only /api/proposals doors as the board, and every card carries
// the 'see it' door to the rows it was built from (requirement B).
//
// Fails soft in all directions: any source 404ing or erroring costs
// that section, never the tab.
async function _tosLoadIdeas() {
  // 2026-08-02 (his "i want ideas on fable showing in the section
  // delegated to it"): the old system-audit code source
  // went DEAD 06-15 — the engine's live code ideas mint as
  // proposal_cards class 'engineering' (23 last week). ONE fetch,
  // split by class: engineering → the system section, everything
  // else → business. This also de-dupes biz, which was silently
  // carrying the engineering cards.
  const [cards, gaps, alerts, mailDesk] = await Promise.all([
    api('/api/proposals?status=open').catch(() => null),
    // limit=100: the server default (30) was silently capping the gaps
    // ledger — 55 gaps rendered as a "30" chip (2026-08-02).
    api('/api/evolution/gaps?limit=100&include_treated=true').catch(() => null),
    // 2026-08-02 (his "system audit says i have 13"): the loud alarms
    // are half of "waiting for a human" — they must be VISIBLE here,
    // not just counted elsewhere.
    api('/api/alerts').catch(() => null),
    // the mail desk (2026-09-03): people waiting with a reply drafted
    api('/api/mail/desk').catch(() => null),
  ]);
  const asList = (r, k) => Array.isArray(r) ? r
    : (r && Array.isArray(r[k])) ? r[k] : null;
  const all = asList(cards, 'cards') || asList(cards, 'proposals');
  const alarmList = asList(alerts, 'alerts');
  _tosState.ideas = {
    biz:  all ? all.filter(c => c.class !== 'engineering') : null,
    code: all ? all.filter(c => c.class === 'engineering') : null,
    gaps: asList(gaps, 'gaps'),
    treated: gaps?.treated_ok ? asList(gaps, 'treated') : null,
    updateScope: gaps?.update_scope || null,
    mail: (mailDesk && mailDesk.ok) ? mailDesk : null,
    // Password requests are actionable warnings: include them alongside
    // danger alarms so the file inspector and notice acknowledgement are
    // reachable. The endpoint already excludes seen and expired notices;
    // never drop a returned alert merely because it has no expires_at.
    alarms: alarmList
      ? alarmList.filter(a => a.severity === 'danger' || a.alert_type === 'statement_password_needed')
      : null,
    deadLetters: (alerts && alerts.dead_letters) || 0,
  };
  // Fresh data resets the view: filter chip + per-section reveal counts
  // (2026-08-02, his "i cant see all because it only shows 5").
  _tosState.ideasView = { filter: 'all', biz: 8, code: 8, gaps: 8,
                          alarms: 20 };
}

// ── requirement C: adaptive decision labels ─────────────────────────
// Mirrors app/modules/idea_evidence.py (DECISION_LABELS + decision_kind)
// — one derivation, two runtimes; the python side is the contract and
// the tests pin both. The kind changes ONLY the words: every tap still
// answers yes|no through the existing owner-only decide door.
const IDEA_DECISION_LABELS = {
  build: { yes: 'Approve', no: 'Reject',
    why: 'Approve says build it — it still goes through the build queue you control; nothing starts by itself. Reject puts it away and EON stays quiet about this for 30 days.' },
  yes_no: { yes: 'Yes', no: 'No',
    why: 'Yes answers the question as asked. No says it is not so. Either way EON only records your answer.' },
  merch: { yes: 'Retire them', no: 'Keep selling',
    why: 'Retire them records your call to take these off sale — the shop change is still made by hand. Keep selling leaves them live and EON stays quiet about this for 30 days.' },
  pause: { yes: 'Pause them', no: 'Keep them running',
    why: 'Pause them says switch these off. Keep them running leaves them live and EON stays quiet about this for 30 days.' },
  approve: { yes: 'Approve', no: 'Reject',
    why: 'Approve says do it. Reject says leave things as they are, and EON stays quiet about this for 30 days.' },
};

function ideaDecisionKind(c) {
  const ev = (c && c.evidence && typeof c.evidence === 'object') ? c.evidence : {};
  const stamped = String(ev.decision_kind || '').trim().toLowerCase();
  if (IDEA_DECISION_LABELS[stamped]) return stamped;
  const title = String((c && c.title) || '').trimEnd();
  const why = String((c && c.why_line) || '').trimEnd();
  if (title.endsWith('?') || why.endsWith('?')) return 'yes_no';
  const text = `${title} ${why}`;
  if (/\b(pause|switch(ed)? off|turn(ed)? off)\b/i.test(text)) return 'pause';
  if (/\b(retire|mark(ed)?[ -]?down|markdown|stop selling|discontinue|clear(ance| out))\b/i.test(text)) return 'merch';
  if (c && (c.class === 'function' || c.class === 'engineering')) return 'build';
  return 'approve';
}

function ideaDecisionWords(c) {
  return IDEA_DECISION_LABELS[ideaDecisionKind(c)] || IDEA_DECISION_LABELS.approve;
}

// The hero figure — the card's biggest NUMBER (reference grammar: the
// hero is a NUMBER in massive type, claim under it in one calm line).
// Deterministic: expected ₦/mo, else cost-of-no ₦/wk, else the first
// stored list's row count with its key as the unit. No number, no block.
function _ideaNgnShort(n) {
  const v = Math.abs(Number(n) || 0);
  if (v >= 1e9) return '₦' + (v / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
  if (v >= 1e6) return '₦' + (v / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (v >= 1e3) return '₦' + Math.round(v / 1e3) + 'K';
  return '₦' + Math.round(v);
}

function _ideaHero(c) {
  if (c.expected_value_ngn) return { n: _ideaNgnShort(c.expected_value_ngn), u: 'a month expected' };
  if (c.cost_of_no_ngn_wk) return { n: _ideaNgnShort(c.cost_of_no_ngn_wk), u: 'a week if ignored' };
  const ev = (c.evidence && typeof c.evidence === 'object') ? c.evidence : {};
  for (const [k, v] of Object.entries(ev)) {
    if (Array.isArray(v) && v.length) return { n: String(v.length), u: k.replace(/_/g, ' ') };
  }
  return null;
}

function _ideaFiled(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(+d)) return '';
  return `filed ${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const IDEA_CLASS_WORDS = {
  engineering: 'a fix', operating: 'how we run things',
  spend: 'spending money', function: 'something new',
};

function _ideaBizCard(c, i) {
  const isOwner = (State.member && State.member.role) === 'owner';
  const w = ideaDecisionWords(c);
  const hero = _ideaHero(c);
  const ks = [
    IDEA_CLASS_WORDS[c.class] || c.class || '',
    _ideaFiled(c.created_at),
    c.cost_of_no_ngn_wk ? `${_ideaNgnShort(c.cost_of_no_ngn_wk)}/wk left alone` : '',
    c.proposer ? String(c.proposer).replace(/_/g, ' ') : '',
  ].filter(Boolean);
  return `<div class="idc" data-prop="${esc(c.id)}">
    <div class="idc-ix">${String(i + 1).padStart(3, '0')}</div>
    <div class="idc-main">
      ${hero ? `<div class="idc-hero"><span class="idc-hero__n">${esc(hero.n)}</span><span class="idc-hero__u">${esc(hero.u)}</span></div>` : ''}
      ${hero && c.evidence && c.evidence.hero_math ? `<div class="idc-hero__m">how: ${esc(c.evidence.hero_math)}</div>` : ''}
      <div class="idc-t">${esc(c.title || 'Untitled')}</div>
      ${c.why_line ? `<div class="idc-why">${esc(c.why_line)}</div>` : ''}
      <div class="idc-k">${ks.map(k => `[ ${esc(k)} ]`).join(' ')}</div>
      <div class="idc-act">
        <button class="idc-see" data-idsee="${esc(c.id)}">see it</button>
        <button class="idc-see" data-story-kind="idea" data-story-id="${esc(c.id)}">full story</button>
        ${isOwner ? `<span class="idc-sp"></span>
        <button class="idc-yes" data-iddec="yes" data-idid="${esc(c.id)}">${esc(w.yes)}</button>
        <button class="idc-no" data-iddec="no" data-idid="${esc(c.id)}">${esc(w.no)}</button>` : ''}
      </div>
      ${isOwner ? `<div class="idc-consq">${esc(w.why)}</div>` : ''}
    </div>
  </div>`;
}

// The mail desk's line on the ideas mast (2026-09-03): kept OUTSIDE
// _tosIdeas so the mast template stays short — tests read that function
// by a character window and its closing words must stay inside it.
function _idmMailLine() {
  const m = _tosState.ideas && _tosState.ideas.mail;
  const n = Number(m && m.waiting) || 0;
  if (!n || !_mailAllowed()) return '';
  return `<div class="idm-mail"><button class="idm-chip idm-mailgo" data-idmail>[ ${n} ${n === 1 ? 'reply' : 'replies'} waiting on the mail desk → ]</button></div>`;
}

// Browser-local read markers do not change a gap's operational status.
function _ideaUpdateKey() {
  // Normal owner bootstrap has no State.user. The API supplies a scope
  // from its verified caller and tenant, also covering staff PIN sessions.
  const scope = _tosState.ideas?.updateScope;
  return typeof scope === 'string' && scope ? `eon.idea-updates.${scope}` : null;
}
function _ideaUpdateSeen() {
  try { const key = _ideaUpdateKey(); const value = key && JSON.parse(localStorage.getItem(key) || '[]'); return new Set(Array.isArray(value) ? value : []); }
  catch (_) { return new Set(); }
}
function _ideaUpdateId(g) {
  return `${g.id}:${g.merged_at}`;
}
function _ideaBuildNotice(st) {
  const files = memberCan('accounting:view') ? '<button class="tr-statement-tool" data-statement-review>Statement files <span aria-hidden="true">↗</span></button>' : '';
  if (!Array.isArray(st.treated)) return `<div class="tr-build-notice"><p>Build updates could not be read. Refresh to try again.</p>${files}</div>`;
  const unseen = _ideaUpdateSeen(), count = st.treated.filter(g => !unseen.has(_ideaUpdateId(g))).length;
  return `<div class="tr-build-notice"><p>See what happened to the gaps EON picked up.</p><div class="tr-idea-tools"><button data-idtab="treated" aria-pressed="${_tosState.ideasView?.filter === 'treated'}">${count ? '<i class="tr-build-dot" aria-hidden="true"></i>' : ''}Build updates · ${count ? count + ' unread' : 'all seen'} <span aria-hidden="true">↗</span></button>${files}</div></div>`;
}
function _ideaBuildUpdates(st) {
  if (!Array.isArray(st.treated)) return '<div class="idm-fail">Build updates could not be read. Refresh to try again.</div>';
  const seen = _ideaUpdateSeen();
  const rows = st.treated.map(g => {
    let href = '';
    try { const u = new URL(g.pr_url); if (u.protocol === 'https:' && !u.username && !u.password) href = u.href; } catch (_) {}
    const date = new Date(g.merged_at);
    const when = Number.isFinite(+date) ? date.toLocaleDateString(undefined,{day:'numeric',month:'short',year:'numeric'}) : 'Date unavailable';
    return `<article class="idr"><div class="tr-build-meta"><span class="tr-build-state">Build merged${seen.has(_ideaUpdateId(g)) ? '' : ' · unread'}</span><time datetime="${esc(g.merged_at)}">${esc(when)}</time></div><div class="idr-t">${esc(g.title)}</div><div class="idr-w">The linked build landed. Deployment and successful live use are not recorded in this ledger.</div>${href ? `<a class="tr-build-link" href="${esc(href)}" target="_blank" rel="noopener noreferrer">View build evidence ↗</a>` : '<p class="idr-w">No pull request link was recorded.</p>'}</article>`;
  }).join('');
  return `<div class="idm-sec">Build updates<span class="idm-sec__s">Last 30 days · up to 100 recorded gap outcomes</span>${st.treated.length ? '<button class="idm-ackbtn" data-idupdates-seen>Mark updates seen</button>' : ''}</div>${rows || '<div class="idm-quiet">No gap builds were recorded as merged in the last 30 days.</div>'}`;
}
function _ideaAlarmRows(alarms, aRow) {
  const password = alarms.filter(a => a.alert_type === 'statement_password_needed');
  const other = alarms.filter(a => a.alert_type !== 'statement_password_needed');
  const grouped = password.length ? `<section class="tr-intake-notice"><div class="tr-intake-notice-head"><div><span class="tr-kicker">STATEMENT INTAKE</span><h3>Which file needs attention?</h3></div><button class="secondary" data-statement-review>Review files ↗</button></div><p>Check filenames, received dates and the latest read result. An older notice may refer to a different copy from your latest upload.</p><details class="tr-alarm-group"><summary>${password.length} saved bank statement notice${password.length === 1 ? '' : 's'}<span>Expand to read or mark individual notices seen</span></summary><div class="tr-alarm-help">Marking seen clears the notification. It does not open a PDF or confirm that another file replaces it.</div>${password.map(aRow).join('')}</details></section>` : '';
  return grouped + other.map(aRow).join('');
}

function _trStatementDate(value) {
  const date = new Date(value || '');
  return Number.isFinite(+date) ? new Intl.DateTimeFormat('en-GB', {timeZone:'Africa/Lagos',day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}).format(date) + ' WAT' : 'Not recorded';
}
function _trStatementFileHTML(file, locked) {
  const states = {parsed:'Read successfully',partial:'Partly read',failed:'Not read',received:'Received',pending:'Waiting to be read',processing:'Reading'};
  const state = locked ? 'Needs PDF password' : states[file.parse_status] || 'Read result unavailable';
  const source = {email:'Email',upload:'EON upload',whatsapp:'WhatsApp',drive:'Drive',telegram:'Telegram'}[file.source] || 'Source not recorded';
  return `<article class="tr-intake-file"><div class="tr-intake-file-icon" aria-hidden="true">${_trIcon('pages')}</div><div><div class="tr-intake-file-heading"><h4>${esc(file.filename || 'Filename not recorded')}</h4><span class="tr-intake-state" data-state="${locked ? 'locked' : file.parse_status === 'parsed' ? 'parsed' : 'other'}">${esc(state)}</span></div><dl><div><dt>First received</dt><dd>${esc(_trStatementDate(file.created_at))}</dd></div><div><dt>Through</dt><dd>${source}</dd></div><div><dt>Last read attempt</dt><dd>${esc(_trStatementDate(file.parsed_at))}</dd></div><div><dt>File reference</dt><dd>${esc(file.id || 'Not recorded')}</dd></div></dl></div></article>`;
}
function _trFilterStatementFiles(files, value) {
  const words = String(value || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  return files.filter(file => {
    const identity = `${file.filename || ''} ${file.id || ''}`.toLowerCase();
    return words.every(word => identity.includes(word));
  });
}
function _trStatementDialog() {
  const ov = cswOverlay(`<div class="csw-sheet__h"><div><span class="tr-kicker">STATEMENT INTAKE</span><h2 class="tr-intake-title">Every file, accounted for<span>.</span></h2></div><button class="csw-x" aria-label="Close statement details">×</button></div><p class="tr-intake-intro">Match the filename and received date to the copy you sent. A later upload is not automatically a replacement for an older locked PDF.</p><div class="tr-intake-search">${_trIcon('search')}<input type="search" data-intake-search aria-label="Find a statement" aria-describedby="tr-intake-search-status" placeholder="Find by filename or reference" autocomplete="off" spellcheck="false"><button data-intake-clear aria-label="Clear file search" hidden>Clear</button></div><p class="tr-intake-search-status" id="tr-intake-search-status" data-intake-search-status role="status"></p><div data-intake-content></div><div class="tr-intake-footer"><button class="csw-mini" data-intake-refresh>Refresh file status</button><a class="csw-mini" href="/?v=money">Open Money ↗</a></div>`, true);
  ov.classList.add('tr-intake-dialog');
  if (_tr.quiet) ov.classList.add('tr-components-quiet');
  ov.querySelector('[role="dialog"]')?.setAttribute('aria-label','Statement file details');
  ov.querySelector('.csw-x').onclick = () => ov.remove();
  let data = null, tab = 'locked', request = 0;
  const host = ov.querySelector('[data-intake-content]');
  const search = ov.querySelector('[data-intake-search]'), clear = ov.querySelector('[data-intake-clear]'), searchStatus = ov.querySelector('[data-intake-search-status]');
  const paint = () => {
    const all = tab === 'locked' ? data.locked : data.recent;
    const rows = _trFilterStatementFiles(all, search.value), filtering = !!search.value.trim();
    searchStatus.textContent = filtering ? `${rows.length} of ${all.length} loaded files match · ${tab === 'locked' ? 'Needs password' : 'Recent files'}` : 'Search the files in this view · filename or full reference';
    host.innerHTML = `<div class="tr-intake-readtime">${data.demo ? 'SAMPLE FILES · DESIGN PREVIEW' : 'Registry checked'} · ${esc(_trStatementDate(data.checked_at))}</div><div class="tr-intake-tabs" aria-label="Statement file views"><button data-intake-tab="locked" aria-pressed="${tab==='locked'}">Needs password <span>${data.locked_total}</span></button><button data-intake-tab="recent" aria-pressed="${tab==='recent'}">Recent files <span>${data.recent.length}</span></button></div><p class="tr-intake-context">${tab === 'locked' ? 'These exact file records failed their latest read because a PDF password was required.' : 'Latest 20 first-registered files. Identical reuploads reuse the same record. Read successfully describes that file; it does not confirm account coverage.'}</p>${rows.map(f=>_trStatementFileHTML(f,tab==='locked')).join('') || `<div class="tr-intake-empty">${all.length && filtering ? 'No matching files in this view. Try the other tab or clear your search.' : tab==='locked' ? 'No files currently recorded as needing a PDF password.' : 'No received files were returned.'}</div>`}${tab==='locked'&&data.locked_truncated ? '<p class="tr-intake-context">Showing the first 100 files. Open Money for the wider intake view.</p>' : ''}`;
    host.querySelectorAll('[data-intake-tab]').forEach(b=>b.onclick=()=>{tab=b.dataset.intakeTab;paint();host.querySelector(`[data-intake-tab="${tab}"]`)?.focus();});
  };
  const read = async () => {
    const current = ++request;
    data = null;
    searchStatus.textContent = 'Reading file details…';
    host.innerHTML = '<p class="tr-evidence-state">Reading file receipts and latest results…</p>';
    try {
      const result = await api('/api/finance/statement-intake/task-context');
      if (!ov.isConnected || current !== request) return;
      if (!result?.ok || !Array.isArray(result.locked) || !Array.isArray(result.recent) || !Number.isFinite(result.locked_total)) throw Error('Unreadable registry');
      data = result; paint();
    } catch (_) {
      if (ov.isConnected && current === request) {
        searchStatus.textContent = 'File search is unavailable until the read succeeds.';
        host.innerHTML = '<div class="tr-intake-empty"><strong>Statement details could not be read.</strong><p>Try refreshing. File details require accounting access; no missing or resolved files are inferred from this failed read.</p></div>';
      }
    }
  };
  search.oninput = () => { clear.hidden = !search.value; if (data) paint(); };
  clear.onclick = () => { search.value = ''; search.oninput(); search.focus(); };
  ov.querySelector('[data-intake-refresh]').onclick = read;
  read();
}

function _tosIdeas() {
  const st = _tosState.ideas;
  if (!st) return _tosSkel();
  // Cards this tab already answered in EITHER room never render with
  // live buttons again (the session cache is reused without a refetch).
  ['biz', 'code'].forEach((k) => {
    if (Array.isArray(st[k])) st[k] = st[k].filter(c => !_ccIsAnswered(c.id));
  });
  const vw = _tosState.ideasView
    || (_tosState.ideasView = { filter: 'all', biz: 8, code: 8, gaps: 8 });
  const bizN = Array.isArray(st.biz) ? st.biz.length : 0;
  const codeN = Array.isArray(st.code) ? st.code.length : 0;
  const gapN = Array.isArray(st.gaps) ? st.gaps.length : 0;
  const alarmN = Array.isArray(st.alarms) ? st.alarms.length : 0;
  const deadN = st.deadLetters || 0;
  // THE number (2026-08-02, his "system audit says i have 13 — it
  // needs to be clear"): decisions + loud alarms + parked messages —
  // the same three parts, the same arithmetic, as the Engine Room's
  // "waiting for a human". The two surfaces can no longer disagree.
  const total = bizN + codeN + alarmN + deadN;

  // Counts remain navigable; each chip opens its complete section.
  const chip = (key, label, n) =>
    `<button class="idm-chip${vw.filter === key ? ' on' : ''}"
       data-idtab="${key}" aria-pressed="${vw.filter === key}">[ ${label} ${n} ]</button>`;
  const mast = `<div class="idm">
    <div class="idm-mast">ideas<span class="idm-dot">.</span></div>
    <div class="idm-line"><span class="idm-count">${total}</span> waiting on your word
      <span class="idm-line__s">${bizN + codeN} to answer · ${alarmN + deadN} to look at${gapN ? ` · ${gapN} gaps on the ledger` : ''}</span></div>
    ${_idmMailLine()}
    <div class="idm-k">${chip('all', 'all', total)}${chip('biz', 'business', bizN)}${chip('code', 'system', codeN)}${chip('alarms', 'alarms', alarmN + deadN)}${chip('gaps', 'gaps', gapN)}</div>
    ${_ideaBuildNotice(st)}
  </div>`;
  const showSec = (key) => vw.filter === 'all' || vw.filter === key;
  // Incremental reveal, client-side: the data is already loaded, only
  // the rendering was hiding it. Never a dead-end link to "the board".
  const moreBtn = (key, hidden) => hidden > 0
    ? `<div class="idm-more"><button class="idm-morebtn"
         data-idmore="${key}">[ show ${Math.min(hidden, 20)} more — ${hidden} hidden ]</button></div>`
    : '';

  // null = the read failed. Say so — an empty list and a broken read must
  // never look the same, or "no ideas" quietly means "EON went blind".
  const failed = (label) => `<div class="idm-sec">${esc(label)}</div>
    <div class="idm-fail">Could not read this right now — nothing has been
    lost, try refresh. This is not "no ideas".</div>`;
  const quiet = (label) => `<div class="idm-sec">${esc(label)}</div>
    <div class="idm-quiet">nothing waiting on you here.</div>`;

  let biz = '';
  if (showSec('biz')) {
    if (st.biz === null) biz = failed('for the business');
    else if (!st.biz.length) biz = quiet('for the business');
    else {
      biz = `<div class="idm-sec">for the business<span class="idm-sec__s">decide right here — see it opens the rows each idea came from</span></div>
        ${st.biz.slice(0, vw.biz).map(_ideaBizCard).join('')}
        ${moreBtn('biz', st.biz.length - vw.biz)}`;
    }
  }

  const row = (title, why, href) => `<a class="idr" href="${esc(href)}">
    <div class="idr-t">${esc(title || 'Untitled')}</div>
    ${why ? `<div class="idr-w">${esc(String(why).slice(0, 200))}</div>` : ''}</a>`;

  let code = '';
  if (showSec('code')) {
    if (st.code === null) code = failed('for the system');
    else if (!st.code.length) code = quiet('for the system');
    else {
      // 2026-08-02: these are live engineering cards from the same
      // queue as business ones (the old link-out source died 06-15) —
      // so they get the same decide-right-here card, not a dumb link.
      code = `<div class="idm-sec">for the system<span class="idm-sec__s">built by the engine, for the engine — decide right here</span></div>
        ${st.code.slice(0, vw.code).map(_ideaBizCard).join('')}
        ${moreBtn('code', st.code.length - vw.code)}`;
    }
  }

  let alarms = '';
  if (showSec('alarms')) {
    if (st.alarms === null) alarms = failed('needs your eyes');
    else if (!st.alarms.length && !deadN) alarms = quiet('needs your eyes');
    else {
      const aRow = (a) => {
        const days = Math.round((Date.now() - new Date(a.created_at)) / 864e5);
        return `<div class="idr idr-alarm">
          <div class="idr-t">${esc(a.title || a.alert_type)}</div>
          <div class="idr-w">${esc(String(a.body || '').slice(0, 160))}</div>
          <div class="idr-foot"><span class="idr-age">${days ? `waiting ${days} day${days === 1 ? '' : 's'}` : 'today'}</span>
            <button class="idm-ackbtn" data-idack="${esc(a.id)}">[ seen — clear it ]</button></div>
        </div>`;
      };
      const deadLine = deadN
        ? `<div class="idr idr-alarm"><div class="idr-t">${deadN} message(s) parked after failed handling</div>
           <div class="idr-w">Saved safely with everything kept — they need eyes, nothing is lost.</div></div>`
        : '';
      alarms = `<div class="idm-sec">needs your eyes<span class="idm-sec__s">loud alarms — the machine caught it, a person decides. clearing one only marks it seen. alarms that heal themselves leave on their own; the rest wait here until you clear them.</span></div>
        ${_ideaAlarmRows(st.alarms.slice(0, vw.alarms), aRow)}${deadLine}
        ${moreBtn('alarms', st.alarms.length - vw.alarms)}`;
    }
  }

  let gaps = '';
  // Source string keeps its capital W (suite pin); the section-head CSS
  // lowercases it on screen like every other rail label.
  if (showSec('gaps')) {
    if (st.gaps === null) gaps = failed('What EON cannot do yet');
    else if (!st.gaps.length) gaps = quiet('What EON cannot do yet');
    else {
      // Link truth (2026-08-03, loop v3): a gap EON already filed a
      // build for belongs in the build yard — the loop page's room 04
      // shows its station, promise and landing. A gap still being
      // watched keeps the audit door. A PR link always wins.
      gaps = `<div class="idm-sec">What EON cannot do yet</div>
        ${st.gaps.slice(0, vw.gaps).map(g => row(g.title, (g.status_words || '') + (g.hits ? ` · seen ${g.hits}x` : ''), g.pr_url || (g.status && g.status !== 'open' ? '/loop#lp-room-yard' : '/?v=system-audit'))).join('')}
        ${moreBtn('gaps', st.gaps.length - vw.gaps)}`;
    }
  }

  return mast + biz + code + alarms + gaps
    + (vw.filter === 'treated' ? _ideaBuildUpdates(st) : '')
    + `<div class="idm-foot">These are decisions, not jobs.
       Nothing here starts until you say yes. Gaps EON files for
       itself still wait for your merge.
       What happened after every yes lives on
       <a href="/loop#lp-room-yes">the loop page</a>.</div>`;
}

// Bind the Ideas tab's live controls: see-it + the adaptive yes/no.
// Same owner-only decide door as the board — the server enforces; the
// buttons only render for the owner so staff never meet a dead control.
function _tosBindIdeas() {
  const body = document.getElementById('tos-body');
  if (!body) return;
  body.querySelectorAll('[data-statement-review]').forEach(b => b.addEventListener('click', _trStatementDialog));
  // Section tabs + incremental reveal (2026-08-02): pure view state,
  // re-render from data already in hand — no refetch, no scroll loss
  // beyond the section jump.
  body.querySelectorAll('[data-idtab]').forEach(b => b.addEventListener('click', () => {
    const vw = _tosState.ideasView
      || (_tosState.ideasView = { filter: 'all', biz: 8, code: 8, gaps: 8 });
    vw.filter = b.getAttribute('data-idtab');
    _tosRenderView();
    Array.from(body.querySelectorAll('[data-idtab]')).find(el => el.getAttribute('data-idtab') === vw.filter)?.focus();
  }));
  body.querySelector('[data-idupdates-seen]')?.addEventListener('click', () => {
    const key = _ideaUpdateKey();
    if (!key) { showToast('Could not identify this workspace. Refresh to try again.'); return; }
    try {
      const seen = _ideaUpdateSeen();
      (_tosState.ideas?.treated || []).forEach(g => seen.add(_ideaUpdateId(g)));
      localStorage.setItem(key, JSON.stringify([...seen].slice(-500)));
      _tosRenderView();
      document.querySelector('[data-idupdates-seen]')?.focus();
      showToast('Build updates marked seen on this browser.');
    } catch (_) { showToast('This browser could not save the seen state.'); }
  });
  body.querySelectorAll('[data-idmore]').forEach(b => b.addEventListener('click', () => {
    const vw = _tosState.ideasView
      || (_tosState.ideasView = { filter: 'all', biz: 8, code: 8, gaps: 8 });
    const k = b.getAttribute('data-idmore');
    vw[k] = (vw[k] || 8) + 20;
    _tosRenderView();
  }));
  body.querySelectorAll('[data-idsee]').forEach(b => b.addEventListener('click', () =>
    openIdeaEvidence(b.getAttribute('data-idsee'))));
  // Alarm acknowledge (2026-08-02): "seen - clear it" marks the alarm
  // acknowledged on the wall; it fixes nothing and says so.
  body.querySelectorAll('[data-idack]').forEach(b => b.addEventListener('click', async (e) => {
    const el = e.currentTarget;
    const id = el.getAttribute('data-idack');
    const grouped = !!el.closest('.tr-alarm-group')?.open;
    el.disabled = true; el.textContent = '\u2026';
    try {
      const saved = await api(`/api/alerts/${encodeURIComponent(id)}/acknowledge`,
        { method: 'POST' });
      if (!saved?.ok) throw Error('Acknowledgement did not save');
      const row = el.closest('.idr-alarm');
      if (row) row.remove();
      if (_tosState.ideas && Array.isArray(_tosState.ideas.alarms)) {
        _tosState.ideas.alarms = _tosState.ideas.alarms.filter(a => a.id !== id);
      }
      _tosRenderView();
      const group = body.querySelector('.tr-alarm-group');
      if (grouped && group) { group.open = true; group.querySelector('[data-idack]')?.focus(); }
      else body.querySelector('[data-idtab][aria-pressed="true"]')?.focus();
    } catch (err) {
      el.disabled = false; el.textContent = '[ seen \u2014 clear it ]';
      showToast('Could not clear it \u2014 try again.');
    }
  }));
  // Every way a decided card leaves the board goes through ONE settle:
  // drop the DOM row and prune the card from BOTH idea lists. The old
  // success path pruned only ideas.biz — a decided SYSTEM card (class
  // 'engineering' lives in ideas.code) survived in state, so the next
  // re-render (section chip, show-more) resurrected it with a live
  // Approve; the second tap then 400'd "card is approved, not open"
  // and the board read as stuck (operator report 2026-08-30; five live
  // 400 re-taps in the 07:39 UTC deploy log, all class=engineering).
  const settleIdea = (id, row, el) => {
    if (row) row.remove(); else if (el) el.remove();
    // …and the Command room's payload + answered-guard, in one settle
    // (vet 2026-09-02, finding 3) — a card answered here must not come
    // back with live buttons when he switches to Command.
    _propSettleEverywhere(id);
  };
  body.querySelectorAll('[data-iddec]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const el = e.currentTarget;
      const id = el.getAttribute('data-idid');
      const dec = el.getAttribute('data-iddec');
      const row = el.closest('.idc');
      const rowBtns = row ? Array.from(row.querySelectorAll('button')) : [el];
      // Adaptive labels: restore what the button actually said, never a
      // hardcoded YES/no.
      const was = el.textContent;
      rowBtns.forEach(b => { b.disabled = true; });
      el.textContent = '…';
      try {
        await api(`/api/proposals/${encodeURIComponent(id)}/decide`,
          { method: 'POST', body: JSON.stringify({ decision: dec }) });
        settleIdea(id, row, el);
        showToast(dec === 'yes' ? 'Saved. It moves to the work list.'
                                : 'Noted. It will stop suggesting this.');
      } catch (err) {
        // The server says this card was already decided (an earlier tap
        // here, or another device). The row on screen is stale — settle
        // it; restoring a button that can never succeed is the trap.
        if (err && err.status === 400
            && /not open|already decided/i.test(err.message || '')) {
          settleIdea(id, row, el);
          showToast('Already saved — this one was decided earlier.');
          return;
        }
        rowBtns.forEach(b => { b.disabled = false; });
        el.textContent = was;
        const vg = _vgDetail(err);
        if (vg && dec === 'yes' && _vgHold(row, vg, async () => {
          await api(`/api/proposals/${encodeURIComponent(id)}/decide`,
            { method: 'POST', body: JSON.stringify({ decision: 'yes', override_value_guard: true }) });
          settleIdea(id, row, el);
          showToast('Approved over the money guard. It moves to the work list.');
        })) return;
        showToast((err && err.message) || 'That did not save. Try again.');
      }
    });
  });
}

// ── the full story sheet (2026-08-02, fr efbe9476) ──────────────────
// Every idea, audit and build card opens into its five-part plain-words
// story: GET /api/cards/<kind>/<id>/detail. The server serves the stored
// brief or writes it once and stores it (cache-on-first-read) — a second
// open costs nothing. One delegated listener binds every surface: a card
// emitter only has to print a [data-story-kind][data-story-id] button.
async function openCardStory(kind, id) {
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">the full story</div><button class="csw-x">✕</button></div>
    <div class="iev" id="story-body"><div class="tos-card csw-skel" style="height:64px"></div></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const body = ov.querySelector('#story-body');
  let d;
  try {
    d = await api(`/api/cards/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/detail`);
  } catch (err) {
    body.innerHTML = `<div class="idm-fail">Could not open the story
      (${esc(String((err && err.message) || err).slice(0, 120))}). The card itself is unchanged.</div>`;
    return;
  }
  if (!d || !d.ok) {
    // Honest fallback: the raw fields, plainly labelled — never a spinner,
    // never invented content.
    const raw = (d && d.row) || {};
    body.innerHTML = `<div class="idm-quiet">could not write the simple version right now — here is the raw card.</div>
      <pre class="story-raw">${esc(JSON.stringify(raw, null, 2).slice(0, 4000))}</pre>`;
    return;
  }
  body.innerHTML = `<div class="iev-mast">the full story<span class="idm-dot">.</span></div>
    <div class="story-txt">${_storyHtml(d.detailed_brief)}</div>`;
}
// The five headings come back as literal caps lines from the generator;
// tolerate a trailing dash/colon and legacy free-text briefs (no headings
// found → the text renders as plain paragraphs).
function _storyHtml(text) {
  let html = esc(String(text || ''));
  ['WHAT THIS IS ABOUT', 'THE PLAN', 'WHAT CHANGES FOR YOU',
   'THE EVIDENCE', 'IF YOU IGNORE IT'].forEach(h => {
    html = html.replace(new RegExp(h + '\\s*(?:—|:|-)?\\s*', 'g'),
      `<div class="story-h">${h.toLowerCase()}</div>`);
  });
  return html.replace(/\n{2,}/g, '<br/><br/>').replace(/\n/g, '<br/>');
}
document.addEventListener('click', (e) => {
  const b = e.target && e.target.closest && e.target.closest('[data-story-kind]');
  if (b) openCardStory(b.getAttribute('data-story-kind'), b.getAttribute('data-story-id'));
});

// ── requirement B: 'see it' — the evidence sheet ────────────────────
// GET /api/ideas/<id>/evidence opens the rows the idea was built from.
// Stored results render directly; stored query text was re-run
// READ-ONLY through sql_safe server-side (non-SELECT refused — the
// refusal renders honestly, never as an empty table). Paged: each page
// re-fetches; the PostgREST 1000-clip law is handled server-side and
// a full window says so.
async function openIdeaEvidence(id, page) {
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">the rows behind it</div><button class="csw-x">✕</button></div>
    <div class="iev" id="iev-body"><div class="tos-card csw-skel" style="height:64px"></div></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  await _ievLoad(ov, id, page || 1);
}

async function _ievLoad(ov, id, page) {
  const body = ov.querySelector('#iev-body');
  if (!body) return;
  let d;
  try {
    d = await api(`/api/ideas/${encodeURIComponent(id)}/evidence?page=${page}&per=50`);
  } catch (err) {
    body.innerHTML = `<div class="idm-fail">Could not open the evidence
      (${esc(String((err && err.message) || err).slice(0, 120))}). The idea itself is unchanged.</div>`;
    return;
  }
  const c = d.card || {};
  const secs = (d.sections || []).map(_ievSection).join('')
    || '<div class="idm-quiet">this idea stored no evidence rows — the claim text is all it carries.</div>';
  const pager = (d.has_more || page > 1) ? `<div class="iev-pager">
      ${page > 1 ? `<button class="idc-see" data-ievpage="${page - 1}">previous page</button>` : ''}
      <span class="iev-pageno">page ${page}</span>
      ${d.has_more ? `<button class="idc-see" data-ievpage="${page + 1}">next page</button>` : ''}
    </div>` : '';
  body.innerHTML = `
    <div class="iev-mast">evidence<span class="idm-dot">.</span></div>
    <div class="iev-title">${esc(c.title || '')}</div>
    ${c.why_line ? `<div class="iev-why">${esc(c.why_line)}</div>` : ''}
    <div class="idc-k">${[c.class, c.proposer ? String(c.proposer).replace(/_/g, ' ') : '',
      d.decision ? String(d.decision.kind || '').replace(/_/g, ' ') : '']
      .filter(Boolean).map(k => `[ ${esc(k)} ]`).join(' ')}</div>
    ${secs}${pager}`;
  body.querySelectorAll('[data-ievpage]').forEach(b => b.addEventListener('click', () =>
    _ievLoad(ov, id, parseInt(b.getAttribute('data-ievpage'), 10) || 1)));
}

function _ievSection(s) {
  const head = `<div class="idm-sec">${esc(String(s.label || '').replace(/_/g, ' '))}</div>`;
  if (s.kind === 'facts') {
    return head + `<div class="iev-facts">${(s.facts || []).map(f =>
      `<div class="iev-fact"><span class="iev-fact__k">${esc(String(f.k).replace(/_/g, ' '))}</span><span class="iev-fact__v">${esc(f.v)}</span></div>`).join('')}</div>`;
  }
  if (s.kind === 'query' && !s.ok) {
    return head + `<div class="iev-refused">did not run this: ${esc(s.refused || 'refused')}.
      Only a plain SELECT may run here — the stored text was left untouched.</div>`
      + (s.sql ? `<code class="iev-sql">${esc(s.sql)}</code>` : '');
  }
  const rows = s.rows || [];
  const cols = s.columns || [];
  const total = s.kind === 'rows' ? s.total : s.row_count;
  const table = rows.length ? `<div class="iev-tablewrap"><table class="iev-table">
      <thead><tr>${cols.map(col => `<th>${esc(String(col).replace(/_/g, ' '))}</th>`).join('')}</tr></thead>
      <tbody>${rows.map(r => `<tr>${cols.map(col => `<td>${esc(r[col] == null ? '' : String(r[col]))}</td>`).join('')}</tr>`).join('')}</tbody>
    </table></div>` : '<div class="idm-quiet">no rows in this window.</div>';
  const note = [
    total != null ? `${total} row${total === 1 ? '' : 's'}` : '',
    s.kind === 'query' ? 're-run read-only just now' : 'stored with the idea',
    s.truncated ? 'window full — there may be more' : '',
  ].filter(Boolean).map(k => `[ ${esc(k)} ]`).join(' ');
  return head + (s.sql ? `<code class="iev-sql">${esc(s.sql)}</code>` : '')
    + `<div class="idc-k">${note}</div>` + table;
}

function _tosBindCards() {
  document.querySelectorAll('#tos-body .tos-card[data-tid]').forEach(c =>
    c.addEventListener('click', () => openTaskDetail(c.getAttribute('data-tid'))));
  if (_tosState.view === 'board') _tosBindBoardDrag();
}

async function openTaskDetail(id) {
  const currentScope = () => JSON.stringify([State.tenant?.id, State.user?.id || State.user?.user_id, State.member, !!State.impersonating]);
  const requestScope = currentScope();
  const sameContext = () => requestScope === currentScope();
  const epoch=(openTaskDetail.epoch || 0)+1;openTaskDetail.epoch=epoch;
  document.querySelectorAll('[data-task-detail]').forEach(overlay=>{overlay.dataset.replaced='true';overlay.remove();});
  const summary = _tosState.byId[id] || (_tosState.mine?.tasks || []).find(x => x.id === id) || { id };
  if (_mailAllowed() && (summary.email || summary.metadata?.email || summary.related_entity_type === 'inbound_email')) return _workRoom().openMail(id);
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Task</div><button class="csw-x">✕</button></div>
    <div id="tos-det"></div>`);
  ov.dataset.taskDetail=id;
  if(_mailAllowed())_workRoom().trackTask(ov,id);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  if (_trActive()) ov.classList.add('tr-detail');
  ov.querySelector('[role="dialog"]')?.setAttribute('aria-label', summary.title || 'Task detail');
  const host=ov.querySelector('#tos-det');
  host.innerHTML='<p role="status" class="tr-brief-loading">Reading the full task brief…</p>';
  try {
    const result=await api(`/api/tasks/${encodeURIComponent(id)}`);
    if(!sameContext()){ov.dataset.replaced='true';ov.remove();return;}
    if(epoch!==openTaskDetail.epoch || !ov.isConnected)return;
    if(!result?.ok || !result.task || result.task.id!==id)throw Error('Task details could not be read.');
    const t={...summary,...result.task};
    if (_mailAllowed() && (t.email || t.metadata?.email || t.related_entity_type === 'inbound_email')) {
      ov.dataset.replaced='true';ov.remove();return _workRoom().openMail(id);
    }
    _tosState.byId[id]=t;
    host.innerHTML=_tosDetailHTML(t);
    _tosBindDetail(ov,t);_tosLoadDeps(ov,t);_tosLoadSubtasks(ov,t);_tosLoadComments(ov,t);
    if(_trActive())_trInitDetail(ov,t);
    return ov;
  } catch(e) {
    if(!sameContext()){ov.dataset.replaced='true';ov.remove();return;}
    if(epoch!==openTaskDetail.epoch || !ov.isConnected)return;
    host.innerHTML=`<div class="tr-brief-error" role="alert"><h2>The full brief is unavailable.</h2><p>${esc(e.message||'Try reading the task again.')}</p><button class="csw-mini" data-detail-retry>Read task again</button></div>`;
    host.querySelector('[data-detail-retry]').onclick=()=>{ov.remove();openTaskDetail(id);};
  }
}

// EON evidence room — native adaptations of gallery/lightbox and composer
// patterns researched on 21st.dev. See docs/design/task-room/components-pass/.
function _trEvidenceItems(rows) {
  let storageOrigin = '';
  try {
    const storage = new URL(typeof SUPABASE_URL === 'string' ? SUPABASE_URL : '');
    if (storage.protocol === 'https:') storageOrigin = storage.origin;
  } catch (_) {}
  return (Array.isArray(rows) ? rows : []).map((a, index) => {
    a = a && typeof a === 'object' ? a : {};
    let url = '', previewAllowed = false;
    try {
      if (!String(a.storage_url || '').trim()) throw Error('Missing URL');
      const parsed = new URL(String(a.storage_url), location.origin);
      const secure = parsed.protocol === 'https:' || (parsed.protocol === 'http:' && parsed.origin === location.origin);
      if (secure && !parsed.username && !parsed.password) {
        url = parsed.href;
        previewAllowed = parsed.origin === location.origin ||
          (parsed.origin === storageOrigin && parsed.pathname.startsWith('/storage/v1/object/public/'));
      }
    } catch (_) {}
    const mime = String(a.mime_type || '').toLowerCase().split(';')[0];
    return {url, image: previewAllowed && ['image/jpeg','image/png','image/webp','image/gif','image/avif'].includes(mime),
      label: `${a.is_verification ? 'Verification photo' : 'Attachment'} ${index + 1}`,
      proof: !!a.is_verification, mime, size: Number(a.size_bytes) || 0, created: a.created_at || ''};
  });
}
function _trFileSize(bytes) {
  return bytes > 0 ? (bytes >= 1048576 ? (bytes / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(bytes / 1024)) + ' KB') : '';
}
function _trInitDetail(ov, t) {
  ov.classList.add('tr-detail');
  if (_tr.quiet) ov.classList.add('tr-components-quiet');
  ov.querySelector('[role="dialog"]')?.setAttribute('aria-label', t.title || 'Task detail');
  const host = ov.querySelector('#tr-evidence');
  if (!host) return;
  host.innerHTML = `<div class="tr-section-heading"><div><span class="tr-kicker">THE PROOF OF WORK</span><h3>Evidence<span>.</span></h3></div><button class="csw-mini" data-evidence-add>${_trIcon('plus')} Add photo</button></div>
    <div data-evidence-list aria-live="polite"><p class="tr-evidence-state">Loading attachments…</p></div>
    <div data-evidence-upload hidden></div>
    <input type="file" accept="image/jpeg,image/png,image/webp" aria-label="Choose an evidence photo" data-evidence-file hidden>`;
  const state = {items: [], file: null, preview: '', uploading: false, viewer: null, request: 0};
  ov._trEvidence = state;
  const dispose = ov.remove.bind(ov);
  ov.remove = () => {
    state.viewer?.remove();
    if (state.preview) URL.revokeObjectURL(state.preview);
    state.preview = ''; state.file = null; state.request++;
    dispose();
  };
  host.querySelector('[data-evidence-add]').addEventListener('click', () => host.querySelector('[data-evidence-file]').click());
  host.querySelector('[data-evidence-file]').addEventListener('change', e => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (file) _trChooseEvidence(ov, t, file);
  });
  _trLoadEvidence(ov, t);
}
async function _trLoadEvidence(ov, t) {
  const host = ov.querySelector('[data-evidence-list]'), state = ov._trEvidence;
  if (!host || !state) return;
  const request = ++state.request;
  try {
    const d = await api(`/api/tasks/${encodeURIComponent(t.id)}/attachments`);
    if (!ov.isConnected || request !== state.request) return;
    if (!d?.ok || !Array.isArray(d.attachments)) throw Error('Attachments could not be read.');
    state.items = _trEvidenceItems(d.attachments);
    _trPaintEvidence(ov, t);
  } catch (_) {
    if (!ov.isConnected || request !== state.request) return;
    host.innerHTML = '<div class="tr-evidence-state tr-evidence-error"><strong>Evidence could not be loaded.</strong><p>Your files may still be there. Try the read again.</p><button class="csw-mini" data-evidence-retry>Retry</button></div>';
    host.querySelector('[data-evidence-retry]').onclick = () => _trLoadEvidence(ov, t);
  }
}
function _trPaintEvidence(ov, t) {
  const host = ov.querySelector('[data-evidence-list]'), items = ov._trEvidence.items;
  if (!items.length) {
    host.innerHTML = `<div class="tr-evidence-empty"><div class="tr-evidence-outline" aria-hidden="true">${_trIcon('pages')}</div><div><strong>Let the work be seen.</strong><p>Add a clear photo of the result. It stays with this task for review.</p><span>JPG, PNG or WebP · up to 8 MB</span></div></div>`;
    return;
  }
  const images = items.filter(a => a.image && a.url), files = items.filter(a => !a.image || !a.url);
  host.innerHTML = `<div class="tr-evidence-summary"><span>${images.length} photo${images.length === 1 ? '' : 's'}${files.length ? ` · ${files.length} other file${files.length === 1 ? '' : 's'}` : ''}</span><span>Open to inspect</span></div>
    <div class="tr-evidence-grid" data-photo-count="${Math.min(images.length, 3)}">${images.map((a, i) => `<button class="tr-evidence-tile" data-evidence-index="${i}" aria-label="View ${esc(a.label)}"><span class="tr-evidence-image"><img src="${esc(a.url)}" alt="${esc(a.label)}" loading="lazy" decoding="async" referrerpolicy="no-referrer"><span class="tr-image-fallback" hidden>Preview unavailable · open image</span><span class="tr-evidence-expand" aria-hidden="true">${_trIcon('up')}</span></span><span class="tr-evidence-caption"><strong>${esc(a.label)}</strong><small>${esc(_trFileSize(a.size) || a.mime.split('/')[1]?.toUpperCase() || 'Image')}</small></span></button>`).join('')}</div>
    ${files.length ? `<div class="tr-evidence-files">${files.map(a => a.url ? `<a href="${esc(a.url)}" target="_blank" rel="noopener noreferrer" class="tr-evidence-file">${_trIcon('pages')}<span>${esc(a.label)}<small>${esc(a.mime || 'File')} ${esc(_trFileSize(a.size))}</small></span>${_trIcon('up')}</a>` : `<p class="tr-evidence-state">${esc(a.label)} · link unavailable</p>`).join('')}</div>` : ''}`;
  host.querySelectorAll('img').forEach(img => _trImageState(img));
  host.querySelectorAll('[data-evidence-index]').forEach(b => b.onclick = () => _trOpenEvidence(ov, images, +b.dataset.evidenceIndex, t.title, b));
}
function _trImageState(img) {
  const fail = () => { img.hidden = true; const fallback = img.parentElement.querySelector('.tr-image-fallback'); if (fallback) fallback.hidden = false; };
  img.addEventListener('error', fail, {once:true});
  if (img.complete && !img.naturalWidth) fail();
}
function _trEvidenceArrival(img, origin) {
  if (!origin || _tr.quiet || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  requestAnimationFrame(() => {
    if (!img.isConnected) return;
    const target = img.getBoundingClientRect();
    if (!target.width || !target.height) return;
    img.style.setProperty('--tr-image-x', `${origin.x+origin.width/2-target.x-target.width/2}px`);
    img.style.setProperty('--tr-image-y', `${origin.y+origin.height/2-target.y-target.height/2}px`);
    img.style.setProperty('--tr-image-scale', String(Math.min(origin.width/target.width,origin.height/target.height)));
    img.classList.add('tr-image-arriving');
    img.addEventListener('animationend',()=>img.classList.remove('tr-image-arriving'),{once:true});
  });
}
function _trOpenEvidence(parentOv, images, start, title, opener) {
  if (!images.length) return;
  let origin = opener?.getBoundingClientRect();
  parentOv._trEvidence?.viewer?.remove();
  const ov = cswOverlay(`<div class="tr-viewer-head"><div><span class="tr-kicker">EVIDENCE VIEWER</span><h2>${esc(title || 'Task evidence')}</h2></div><button class="csw-x" aria-label="Close image viewer">×</button></div>
    <div class="tr-viewer-stage"><button class="tr-viewer-arrow" data-image-prev aria-label="Previous image">${_trIcon('arrow')}</button><div class="tr-viewer-canvas" data-image-canvas></div><button class="tr-viewer-arrow" data-image-next aria-label="Next image">${_trIcon('arrow')}</button></div>
    <div class="tr-viewer-meta"><div><strong data-image-label></strong><span data-image-count role="status"></span></div><div class="tr-viewer-tools"><button class="csw-mini" data-image-fit>Fit image</button><a class="csw-mini" data-image-original target="_blank" rel="noopener noreferrer">Open original ${_trIcon('up')}</a></div></div>
    <div class="tr-viewer-strip" aria-label="Evidence images">${images.map((a,i)=>`<button data-image-thumb="${i}" aria-label="View ${esc(a.label)}"><img src="${esc(a.url)}" alt="" loading="lazy" referrerpolicy="no-referrer"><span>${String(i+1).padStart(2,'0')}</span></button>`).join('')}</div>
    <p class="tr-viewer-hint">Scroll or pinch to inspect · arrows to browse · Esc to return</p>`, true);
  ov.classList.add('tr-evidence-viewer');
  if (_tr.quiet) ov.classList.add('tr-components-quiet');
  ov.querySelector('[role="dialog"]').setAttribute('aria-label', 'Task evidence viewer');
  parentOv.setAttribute('inert','');
  if (parentOv._trEvidence) parentOv._trEvidence.viewer = ov;
  const remove = ov.remove.bind(ov);
  ov.remove = () => {
    parentOv.removeAttribute('inert'); if(parentOv._trEvidence)parentOv._trEvidence.viewer=null; remove();
    // Evidence can repaint while the image is open. Recover the opening tile
    // by its index instead of sending keyboard users to the first sheet field.
    if(parentOv.isConnected){
      const tile=opener?.isConnected?opener:parentOv.querySelector(`[data-evidence-index="${start}"]`);
      tile?.focus({preventScroll:true});
    }
  };
  let index = Math.max(0, Math.min(start, images.length-1));
  const paint = () => {
    const a = images[index], canvas = ov.querySelector('[data-image-canvas]');
    canvas.innerHTML = `<img src="${esc(a.url)}" alt="${esc(a.label)}" referrerpolicy="no-referrer"><div class="tr-image-fallback" hidden>Image unavailable. Try opening the original file.</div>`;
    const img = canvas.querySelector('img'); _trImageState(img); cswLightboxZoom(canvas, img);
    if (origin) { _trEvidenceArrival(img, origin); origin = null; }
    ov.querySelector('[data-image-label]').textContent = a.label;
    ov.querySelector('[data-image-count]').textContent = `${index+1} / ${images.length}${a.proof ? ' · Verification evidence' : ''}`;
    ov.querySelector('[data-image-original]').href = a.url;
    ov.querySelectorAll('[data-image-thumb]').forEach(b=>b.setAttribute('aria-pressed',String(+b.dataset.imageThumb===index)));
    ov.querySelector('[data-image-prev]').disabled = images.length < 2;
    ov.querySelector('[data-image-next]').disabled = images.length < 2;
  };
  const move = delta => {index = (index+delta+images.length)%images.length; paint();};
  ov.querySelector('.csw-x').onclick=()=>ov.remove();
  ov.querySelector('[data-image-prev]').onclick=()=>move(-1);
  ov.querySelector('[data-image-next]').onclick=()=>move(1);
  ov.querySelector('[data-image-fit]').onclick=paint;
  ov.querySelectorAll('[data-image-thumb]').forEach(b=>b.onclick=()=>{index=+b.dataset.imageThumb;paint();});
  ov.addEventListener('keydown',e=>{if(e.key==='ArrowRight'||e.key==='ArrowLeft'){e.preventDefault();move(e.key==='ArrowRight'?1:-1);}});
  paint();ov.querySelector('.csw-x').focus();
}
function _trPhotoError(file) {
  if (!['image/jpeg','image/png','image/webp'].includes(file.type)) return 'Choose a JPG, PNG or WebP image.';
  if (!file.size) return 'This file is empty. Choose another photo.';
  if (file.size > 8 * 1024 * 1024) return 'This photo is over 8 MB. Choose a smaller image.';
  return '';
}
function _trChooseEvidence(ov, t, file) {
  const state = ov._trEvidence, slot = ov.querySelector('[data-evidence-upload]');
  if (state.uploading) return;
  const error = _trPhotoError(file);
  if (error) {showToast(error);return;}
  if (state.preview) URL.revokeObjectURL(state.preview);
  state.file=file;state.preview=URL.createObjectURL(file);slot.hidden=false;
  slot.innerHTML=`<div class="tr-upload-preview"><img src="${esc(state.preview)}" alt="Selected photo preview"><div><span class="tr-kicker">READY TO ATTACH</span><strong>${esc(file.name)}</strong><small>${esc(_trFileSize(file.size))} · Verification evidence</small></div></div><div class="tr-upload-actions"><button class="csw-mini" data-upload-remove>Remove selection</button><button class="csw-confirm" data-upload-submit>Attach photo ${_trIcon('arrow')}</button></div><p class="tr-upload-message" role="status">Review your selection. Attaching a photo does not complete or verify this task.</p>`;
  slot.querySelector('[data-upload-remove]').onclick=()=>{URL.revokeObjectURL(state.preview);state.preview='';state.file=null;slot.hidden=true;slot.innerHTML='';ov.querySelector('[data-evidence-add]').focus();};
  slot.querySelector('[data-upload-submit]').onclick=()=>_trUploadEvidence(ov,t);
}
async function _trUploadEvidence(ov, t) {
  const state=ov._trEvidence,slot=ov.querySelector('[data-evidence-upload]');
  if (!state?.file || state.uploading) return;
  state.uploading=true;
  slot.querySelectorAll('button').forEach(b=>b.disabled=true);
  ov.querySelector('[data-evidence-add]').disabled=true;
  slot.querySelector('.tr-upload-message').textContent='Attaching photo…';
  try {
    const fd=new FormData();fd.append('file',state.file);
    const headers={'Authorization':`Bearer ${State.jwt}`};
    if(State.tenant)headers['X-Tenant-Slug']=State.tenant.slug;
    const response=await fetch(API+`/api/tasks/${encodeURIComponent(t.id)}/verification-photo`,{method:'POST',headers,body:fd});
    const d=await response.json().catch(()=>null);
    if(!response.ok||!d?.ok)throw Error(typeof d?.detail==='string'?d.detail:'The photo could not be attached. Try again.');
    if(!ov.isConnected)return;
    URL.revokeObjectURL(state.preview);state.preview='';state.file=null;slot.hidden=true;slot.innerHTML='';
    await _trLoadEvidence(ov,t);
    showToast('Photo attached. Ready for review.');ov.querySelector('[data-evidence-add]').focus();
  }catch(e){if(ov.isConnected)slot.querySelector('.tr-upload-message').textContent=String(e.message||e);}
  finally{state.uploading=false;if(ov.isConnected){slot.querySelectorAll('button').forEach(b=>b.disabled=false);ov.querySelector('[data-evidence-add]').disabled=false;}}
}
function _trCommentComposer() {
  return `<div class="tr-composer"><label for="tos-cmt-in">Add an update</label><textarea id="tos-cmt-in" rows="3" maxlength="500" placeholder="What changed, or what does the next person need?" aria-describedby="tr-comment-hint tr-comment-count"></textarea><div class="tr-composer-footer"><span id="tr-comment-hint">Enter for a new line · ⌘ / Ctrl + Enter to post</span><span id="tr-comment-count">0 / 500</span><button class="csw-confirm" id="tos-cmt-send" disabled>Post update ${_trIcon('arrow')}</button></div><p class="tr-composer-status" role="status"></p></div>`;
}
async function _trLoadComments(ov, t) {
  const el=ov.querySelector('#tos-comments');if(!el)return;
  if(el._trRefreshComments)return el._trRefreshComments();
  el.innerHTML=`<section class="tr-updates"><div class="tr-section-heading"><div><span class="tr-kicker">THE CONVERSATION</span><h3>Updates<span>.</span></h3></div><span data-comment-total></span></div><div data-comment-history><p class="tr-evidence-state">Loading updates…</p></div>${_trCommentComposer()}</section>`;
  const input=el.querySelector('#tos-cmt-in'),send=el.querySelector('#tos-cmt-send'),status=el.querySelector('.tr-composer-status');
  let sending=false,readRequest=0;
  const sync=()=>{input.style.height='auto';input.style.height=Math.min(220,Math.max(90,input.scrollHeight))+'px';el.querySelector('#tr-comment-count').textContent=`${input.value.length} / 500`;send.disabled=sending||!input.value.trim();};
  const read=async()=>{
    const request=++readRequest;
    const history=el.querySelector('[data-comment-history]');
    try{
      const d=await api(`/api/tasks/${encodeURIComponent(t.id)}/comments`);
      if(!ov.isConnected||request!==readRequest)return;
      if(!d?.ok||!Array.isArray(d.comments))throw Error('Unreadable updates');
      const comments=d.comments, earlier=comments.slice(0,-10),recent=comments.slice(-10);
      const row=c=>`<div class="tr-update"><span class="tr-update-avatar" aria-hidden="true">${esc(String(c.author_name||c.author||'Team').split(' ').map(n=>n[0]).slice(0,2).join(''))}</span><div><div class="tr-update-byline"><strong>${esc(c.author_name||c.author||'Team')}</strong><time>${esc(String(c.created_at||'').slice(0,16).replace('T',' '))}</time></div><p>${esc(c.body||c.comment||'')}</p></div></div>`;
      history.innerHTML=(earlier.length?`<details class="tr-earlier"><summary>${earlier.length} earlier update${earlier.length===1?'':'s'}</summary>${earlier.map(row).join('')}</details>`:'')+recent.map(row).join('')||'<p class="tr-evidence-state">No updates yet. Leave a useful next step.</p>';
      el.querySelector('[data-comment-total]').textContent=comments.length?String(comments.length).padStart(2,'0'):'';
    }catch(_){if(ov.isConnected&&request===readRequest){history.innerHTML='<p class="tr-evidence-state">Updates could not be loaded. <button class="csw-mini" data-comments-retry>Retry</button></p>';history.querySelector('[data-comments-retry]').onclick=read;}}
  };
  const post=async()=>{
    const body=input.value.trim();if(!body||sending)return;
    sending=true;sync();input.readOnly=true;send.textContent='Posting…';status.textContent='';
    try{
      const d=await api(`/api/tasks/${encodeURIComponent(t.id)}/comments`,{method:'POST',body:JSON.stringify({body})});
      if(!d?.ok)throw Error(d?.error||'The update could not be posted.');
      if(!ov.isConnected)return;
      input.value='';status.textContent='Update posted.';await read();
    }catch(e){if(ov.isConnected)status.textContent=String(e.message||e)+' Your draft is still here.';}
    finally{sending=false;if(ov.isConnected){input.readOnly=false;send.innerHTML='Post update '+_trIcon('arrow');sync();input.focus();}}
  };
  input.addEventListener('input',sync);input.addEventListener('keydown',e=>{if(e.key==='Enter'&&(e.metaKey||e.ctrlKey)&&!e.isComposing){e.preventDefault();post();}});send.onclick=post;
  el._trRefreshComments=read;
  await read();
}

// SLA gauge: % of the SLA window consumed, colored by velocity bucket.
function _tosSlaGauge(t) {
  if (!t.sla_minutes) return '';
  const used = Math.max(0, t.elapsed_minutes || 0);
  const pct = Math.min(100, Math.round(used / t.sla_minutes * 100));
  const left = t.sla_minutes - used;
  const display = left > 0
    ? (left < 90 ? Math.round(left) + 'm' : Math.round(left / 60) + 'h')
    : 'over';
  const stroke = t.velocity === 'stuck' || left <= 0 ? 'var(--danger)'
    : (t.velocity === 'slow' || pct >= 75 ? 'var(--warn)' : 'var(--ok)');
  return `<div class="tos-sla">${cswGauge(pct, 'SLA left', display, stroke)}</div>`;
}

function _tosTaskDate(value) {
  const date = new Date(value || '');
  return Number.isFinite(+date) ? esc(new Intl.DateTimeFormat('en-GB', {timeZone:'Africa/Lagos',day:'numeric',month:'short',year:'numeric'}).format(date)) : 'Date unavailable';
}

function _tosBriefHTML(t) {
  const description = String(t.description || '').trim(), action = String(t.action_required || '').trim();
  const relative = /\b(today|tomorrow|this week|next week|tonight)\b/i.test(`${t.title || ''} ${description} ${action}`);
  const overdue = t.due_at && Number.isFinite(+new Date(t.due_at)) && +new Date(t.due_at) < Date.now() && !['completed','done','verified','cancelled','expired'].includes(t.status);
  const gaps = [!description && !action ? 'A full brief' : '', !t.assigned_to_staff_id && !t.assigned_to_user_id ? 'An owner' : '', !t.due_at ? 'A deadline, if time matters' : ''].filter(Boolean);
  return `<section class="tos-brief" aria-label="Task brief"><div class="tos-brief-heading"><span class="tos-brief-kicker">THE BRIEF</span><span>Created ${_tosTaskDate(t.created_at)}</span></div>
    ${t.source_context?.kind === 'bank_instruction' ? '<div class="tos-source-context"><a href="/?v=money">From Money · Adviser ↗</a><p>This task records an approved instruction. Creating or starting it does not move funds.</p></div>' : ''}
    ${description ? `<div class="tos-brief-copy">${esc(description)}</div>` : action ? '' : '<p class="tos-brief-missing">Only a title was recorded. Clarify the expected result before starting.</p>'}
    ${action && action !== description ? `<div class="tos-next-action"><h3>Requested action</h3><p>${esc(action)}</p></div>` : ''}
    ${relative || overdue ? `<div class="tos-time-context"><strong>${overdue ? 'The recorded deadline has passed.' : 'Read relative dates in context.'}</strong><span>${relative ? '“Today” or “this week” belongs to when this brief was written. ' : ''}Confirm the current timing and instruction before acting.</span></div>` : ''}
    ${gaps.length ? `<div class="tos-brief-gaps"><strong>Still to clarify</strong><span>${gaps.map(esc).join(' · ')}</span></div>` : ''}
    ${t.completion_notes ? `<div class="tos-recorded-outcome"><h3>Recorded outcome</h3><p>${esc(t.completion_notes)}</p></div>` : ''}
  </section>`;
}

function _tosOutcomeForm(ov, t, action) {
  const host = ov.querySelector('[data-task-response]');
  if (!host) return;
  if (host.dataset.action === action) { host.querySelector('textarea')?.focus(); return; }
  if (host.dataset.pending === 'true') return;
  const blocked = action === 'blocked';
  host.dataset.action = action;
  host.innerHTML = `<form class="tos-response"><label for="tos-result">${blocked ? 'What is preventing progress?' : 'What did you complete?'}</label><p id="tos-result-help">${blocked ? 'Name the blocker and the next step needed to resolve it. This is saved in the task’s activity.' : 'Record the result and where it can be checked. Any required photo must be attached before submission.'}</p><textarea id="tos-result" rows="4" maxlength="${blocked ? 480 : 2000}" required aria-describedby="tos-result-help" placeholder="${blocked ? 'Waiting for… The next step is…' : 'Completed… Checked against…'}"></textarea><div class="tos-response-actions"><button class="csw-confirm" type="submit">${blocked ? 'Save blocker' : 'Submit outcome'}</button><button class="csw-mini" type="button" data-response-cancel>Keep working</button></div><p class="tos-response-status" role="status"></p></form>`;
  const input = host.querySelector('textarea'), status = host.querySelector('[role="status"]');
  let reportRecorded=false;
  host.querySelector('[data-response-cancel]').onclick = () => { host.innerHTML = ''; delete host.dataset.action; ov.querySelector(`[data-act="${action}"]`)?.focus(); };
  host.querySelector('form').onsubmit = async e => {
    e.preventDefault();
    const note = input.value.trim();
    if (!note || host.dataset.pending === 'true' || ov.dataset.taskPending === 'true') { input.focus(); return; }
    host.dataset.pending = 'true'; ov.dataset.taskPending='true'; input.readOnly = true;
    host.querySelectorAll('button').forEach(b => b.disabled = true);
    status.textContent = 'Saving…';
    try {
      if(reportRecorded){
        const current=await api(`/api/tasks/${encodeURIComponent(t.id)}`);
        if(!current?.ok||!current.task)throw Error('The current task status could not be checked.');
        if(current.task.status==='blocked'){ov.remove();await _tosLoad();openTaskDetail(t.id);return;}
      }
      const endpoint = blocked ? `/api/tasks/${encodeURIComponent(t.id)}/transition` : `/api/tasks/${encodeURIComponent(t.id)}/complete`;
      const result = await api(endpoint, {method:'POST',body:JSON.stringify(blocked ? (reportRecorded ? {status:'blocked'} : {status:'blocked',note}) : {note})});
      if (!result?.ok) throw Object.assign(Error(result?.error?.error || result?.error || 'The update could not be saved.'),{body:{detail:result}});
      if (!ov.isConnected) return;
      if(result.already_completed){status.textContent='This task is already complete. Check the recorded outcome before adding another update. Your draft is kept here.';return;}
      showToast(blocked ? 'Blocker recorded.' : 'Outcome recorded. Verification is separate.');
      ov.remove(); await _tosLoad(); openTaskDetail(t.id);
    } catch (e) {
      if (ov.isConnected) {
        if(blocked && e.body?.detail?.blocker_recorded){
          reportRecorded=true;
          const current=e.body.detail.current_status;
          status.textContent=`Your report is saved in activity. ${current ? 'The task is still '+String(current).replaceAll('_',' ')+'.' : 'Its current status could not be confirmed.'} Retry marking it blocked without posting the report again.`;
          host.querySelector('[type="submit"]').textContent='Retry marking blocked';
          if(_trActive())await _trLoadComments(ov,t);
        } else status.textContent = `${String(e.message || e)} ${reportRecorded ? 'Your report is already saved in activity.' : 'Your draft is still here.'}`;
      }
    } finally {
      delete host.dataset.pending; delete ov.dataset.taskPending;
      if (ov.isConnected) { input.readOnly = reportRecorded; host.querySelectorAll('button').forEach(b => b.disabled = false); }
    }
  };
  input.focus();
}

function _tosDetailHTML(t) {
  const isManager = memberCan('tasks:verify');
  const st = t.status || 'open';
  const stateLabel={open:'To do',assigned:'Assigned',acknowledged:'Taken',in_progress:'In progress',blocked:'Blocked',completed:'Awaiting verification',done:'Awaiting verification',verified:'Verified',verification_failed:'Needs rework',cancelled:'Cancelled',expired:'Expired'}[st] || String(st).replaceAll('_',' ');
  let rail = '';
  if (['open', 'assigned'].includes(st)) rail = `<button class="csw-confirm" data-act="in_progress">Start work</button><button class="csw-mini" data-act="acknowledged">Got it</button><button class="csw-mini" data-act="complete">Already done?</button>`;
  else if (st === 'acknowledged') rail = `<button class="csw-confirm" data-act="in_progress">Start work</button><button class="csw-mini" data-act="complete">Record outcome</button>`;
  else if (st === 'in_progress') rail = `<button class="csw-confirm" data-act="complete">Record outcome</button><button class="csw-mini" data-act="blocked">Explain a blocker</button>`;
  else if (st === 'blocked') rail = `<button class="csw-confirm" data-act="in_progress">Resume work</button>`;
  else if (['completed', 'done'].includes(st)) rail = isManager ? `<button class="csw-confirm" data-act="verify">Verify + rate ★</button>` : `<div class="csw-goalmeta">Awaiting a manager's verify + rating.</div>`;
  else if (st === 'verified') rail = `<div class="csw-goalmeta" style="color:var(--ok)">✓ Verified${t.points_awarded ? ` · ${t.points_awarded} pts awarded` : ''}${t.quality_rating ? ` · rated ${t.quality_rating}/5` : ''}</div>`;
  else if (st === 'verification_failed') rail = `<button class="csw-confirm" data-act="in_progress">Rework</button>`;
  return `<div class="csw-card csw-hero" style="margin-bottom:10px">
      <div class="csw-hero__name" style="font-size:16px">${esc(t.title || 'Task')}</div>
      <div class="csw-hero__hint">${t.created_by_eon ? '<span class="tos-eon">EON</span> ' : ''}${esc(stateLabel)}${t.department ? ' · ' + esc(t.department) : ''}${t.estimated_impact_ngn ? ' · ' + cswNgn(t.estimated_impact_ngn) + ' at stake' : ''}</div>
    </div>
    ${_tosBriefHTML(t)}
    <div class="csw-card" style="margin-bottom:10px">
      <div class="tos-det__row">
        ${_tosSlaGauge(t)}
        <div class="tos-det__meta">
          <button class="csw-theme tos-assignee" data-act="assign" ${memberCan('tasks:assign') ? '' : 'disabled'}><span>Responsible</span><b>${esc(t.assignee_name || ((t.assigned_to_staff_id||t.assigned_to_user_id)?'Assigned · name unavailable':'Choose an owner'))}${memberCan('tasks:assign') ? ' · change' : ''}</b></button>
          <div class="csw-theme"><span>Requested by</span><b>${esc(t.requester_name || (t.created_by_eon?'EON':'Not recorded'))}</b></div>
          <div class="csw-theme"><span>Due</span><b>${t.due_at?_tosTaskDate(t.due_at):'No deadline set'}</b></div>
          ${t.priority ? `<div class="csw-theme"><span>Priority</span><b>${esc(t.priority)}</b></div>` : ''}
        </div>
      </div>
      ${t.allocation_reason ? `<div class="csw-goalmeta" style="margin-top:6px">EON routed: ${esc(t.allocation_reason)}</div>` : ''}
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:10px">${rail}
        <button class="csw-mini" data-act="askeon">Clarify with EON</button></div>
      <p class="tos-action-help">${['completed','done'].includes(st)?'Review the recorded outcome and supporting evidence before verifying.':st==='blocked'?'Resume only when the blocker has been resolved.':st==='in_progress'?'Record the result when the work is done, or explain what is preventing progress.':st==='verified'?'Verification is recorded. Review the outcome and its supporting evidence below.':['cancelled','expired'].includes(st)?'This task is closed. Its recorded brief and activity remain available.':'Starting updates this task’s status. Record an outcome when the work is done.'}</p>
      <div data-task-response></div>
      ${memberCan('tasks:assign') && !['verified', 'cancelled', 'expired'].includes(st)
        ? `<div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px">
            <button class="csw-mini" data-act="edit">Edit brief</button>
            <button class="csw-mini tos-danger" data-act="cancel">Cancel task</button>
          </div><div id="tos-editcard"></div>` : ''}
    </div>
    ${_tosEmailBlock(t)}
    ${_trActive() ? '<section id="tr-evidence" class="tr-evidence" aria-label="Task evidence"></section>' : ''}
    <div id="tos-subtasks"></div>
    <div id="tos-deps"></div>
    <div id="tos-comments"></div>`;
}

// Email OS fold (691a10c2): tasks minted from mailus@ threads carry an
// email block (tasks.board ships metadata.email only — address-free).
// The card shows who wrote, the one-line summary, the thread link, and
// where the reply stands. Drafts are DRAFT-ONLY: EON never sends — the
// send tap lives in Gmail, with the human.
function _tosEmailDraftLine(e) {
  // The mail desk (2026-09-03) keeps its own ledger of these states;
  // every one gets a true sentence — 'stale' is a draft STILL sitting
  // in Gmail, never "no reply drafted".
  if (e.draft_status === 'drafted') return 'Reply drafted — waiting in Gmail for your review and send.';
  if (e.draft_status === 'sent-by-human') return 'Reply sent from Gmail.';
  if (e.draft_status === 'sent-by-desk') return 'Reply sent from the mail desk.';
  if (e.draft_status === 'left') return 'You left this one — the draft stays in Gmail, nothing was sent.';
  if (e.draft_status === 'stale') return 'Reply drafted a month ago and never sent — it is still sitting in Gmail.';
  return 'No reply drafted.';
}

function _tosEmailBlock(t) {
  const e = t.email;
  if (!e) return '';
  const who = e.from_name || e.from_domain || 'unknown sender';
  const n = Number(e.message_count) || 1;
  return `<div class="csw-card" style="margin-bottom:10px">
    <div class="tos-sec" style="margin-top:0">Email thread</div>
    <div class="csw-goalmeta">From ${esc(who)}${n > 1 ? ` · ${n} messages` : ''}${e.lane ? ` · ${esc(e.lane)}` : ''}</div>
    ${e.summary ? `<div class="csw-goalmeta">${esc(e.summary)}</div>` : ''}
    <div class="csw-goalmeta">${_tosEmailDraftLine(e)}</div>
    ${e.thread_link ? `<a class="csw-mini" style="display:inline-block;margin-top:8px;text-decoration:none" href="${esc(e.thread_link)}" target="_blank" rel="noopener">Open in Gmail</a>` : ''}
  </div>`;
}

function _tosBindDetail(ov, t) {
  ov.querySelectorAll('[data-act]').forEach(b => b.addEventListener('click', () => {
    const act = b.getAttribute('data-act');
    if (ov.dataset.taskPending === 'true') return;
    if (act === 'verify') return openVerifyRate(ov, t);
    if (act === 'assign') return openAssignPicker(ov, t);
    if (act === 'askeon') return openAskEon(t);
    if (act === 'edit') return _tosEditOpen(ov, t);
    if (act === 'cancel') return _tosCancelOpen(ov, t);
    if (act === 'complete' || act === 'blocked') return _tosOutcomeForm(ov,t,act);
    return _tosDo(ov, `/api/tasks/${t.id}/transition`, { status: act }, 'Updated');
  }));
}

// Subtasks: checklist + done/total progress. Rows open their own detail.
async function _tosLoadSubtasks(ov, t) {
  const el = ov.querySelector('#tos-subtasks');
  if (!el) return;
  try {
    const d = await api(`/api/tasks/${t.id}/subtasks`);
    const subs = (d && d.subtasks) || [];
    if (!subs.length) return;
    const doneSet = ['completed', 'done', 'verified'];
    const done = subs.filter(s => doneSet.includes(s.status)).length;
    el.innerHTML = `<div class="csw-card" style="margin-bottom:10px">
      <div class="tos-sec" style="margin-top:0">Subtasks · ${done}/${subs.length}</div>
      <div class="tos-prog"><div class="tos-prog__fill" style="width:${Math.round(done / subs.length * 100)}%"></div></div>
      ${subs.map(s => `<button class="tos-sub" data-sub="${esc(s.id)}">
        <span class="tos-sub__dot ${doneSet.includes(s.status) ? 'tos-sub__dot--done' : ''}"></span>
        <span class="tos-sub__t ${doneSet.includes(s.status) ? 'tos-sub__t--done' : ''}">${esc(s.title || '')}</span>
        <span class="tos-sub__s">${esc(s.status)}</span></button>`).join('')}</div>`;
    el.querySelectorAll('[data-sub]').forEach(b => b.addEventListener('click', () => {
      ov.remove(); openTaskDetail(b.getAttribute('data-sub'));
    }));
  } catch (e) {}
}

// Dependencies: blocked-by / blocks lists with status dots + add/remove.
async function _tosLoadDeps(ov, t) {
  const el = ov.querySelector('#tos-deps');
  if (!el) return;
  const name = (tid) => { const x = _tosState.byId[tid]; return x ? x.title : tid.slice(0, 8); };
  const dot = (tid) => {
    const x = _tosState.byId[tid];
    const done = x && ['completed', 'done', 'verified'].includes(x.status);
    return `<span class="tos-sub__dot ${done ? 'tos-sub__dot--done' : ''}"></span>`;
  };
  let dep;
  try { dep = await api(`/api/tasks/${t.id}/dependencies`); } catch (e) { return; }
  if (!(dep && dep.ok)) return;
  const bb = dep.blocked_by || [], bl = dep.blocks || [];
  const paired = t.paired_with_task_id ? `<div class="tos-dep"><span class="tos-dep__k">paired</span>${dot(t.paired_with_task_id)}<button class="tos-dep__t" data-open="${esc(t.paired_with_task_id)}">${esc(name(t.paired_with_task_id))}</button></div>` : '';
  el.innerHTML = `<div class="csw-card" style="margin-bottom:10px">
    <div class="tos-sec" style="margin-top:0">Dependencies</div>
    ${bb.map(x => `<div class="tos-dep"><span class="tos-dep__k tos-dep__k--blockedby">blocked by</span>${dot(x.depends_on)}<button class="tos-dep__t" data-open="${esc(x.depends_on)}">${esc(name(x.depends_on))}</button><button class="tos-dep__x" data-deldep="${esc(x.depends_on)}">remove</button></div>`).join('')}
    ${bl.map(x => `<div class="tos-dep"><span class="tos-dep__k">blocks</span>${dot(x.task_id)}<button class="tos-dep__t" data-open="${esc(x.task_id)}">${esc(name(x.task_id))}</button></div>`).join('')}
    ${paired}
    ${(!bb.length && !bl.length && !paired) ? '<div class="csw-goalmeta">No dependencies.</div>' : ''}
    <button class="csw-mini" id="tos-dep-add" style="margin-top:8px">Add blocked-by</button></div>`;
  el.querySelectorAll('[data-open]').forEach(b => b.addEventListener('click', () => { ov.remove(); openTaskDetail(b.getAttribute('data-open')); }));
  el.querySelectorAll('[data-deldep]').forEach(b => b.addEventListener('click', async () => {
    try {
      const r = await api(`/api/tasks/${t.id}/dependencies/${b.getAttribute('data-deldep')}`, { method: 'DELETE' });
      if (r && r.ok) { showToast('Dependency removed'); _tosLoadDeps(ov, t); } else showToast((r && r.error) || 'Failed');
    } catch (e) { showToast(String(e.message || e)); }
  }));
  el.querySelector('#tos-dep-add')?.addEventListener('click', () => openDepPicker(ov, t));
}

// Pick another open task this one is blocked by.
function openDepPicker(parentOv, t) {
  const cands = _tosState.tasks.filter(x => x.id !== t.id &&
    !['verified', 'cancelled'].includes(x.status)).slice(0, 80);
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Blocked by which task?</div><button class="csw-x">✕</button></div>
    <input class="csw-search" id="dp-q" placeholder="Filter tasks…" style="margin-bottom:8px"/>
    <div id="dp-list">${cands.map(x => `<button class="tos-sub" data-dep="${esc(x.id)}"><span class="tos-sub__t">${esc(x.title || '')}</span><span class="tos-sub__s">${esc(x.status)}</span></button>`).join('') || '<div class="tos-empty">No other open tasks.</div>'}</div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  ov.querySelector('#dp-q').addEventListener('input', e => {
    const q = e.target.value.toLowerCase();
    ov.querySelectorAll('[data-dep]').forEach(b => { b.style.display = b.textContent.toLowerCase().includes(q) ? '' : 'none'; });
  });
  ov.querySelectorAll('[data-dep]').forEach(b => b.addEventListener('click', async () => {
    try {
      const r = await api(`/api/tasks/${t.id}/dependencies`, { method: 'POST', body: JSON.stringify({ depends_on: b.getAttribute('data-dep'), kind: 'blocks' }) });
      if (r && r.ok !== false) { showToast('Dependency added'); ov.remove(); _tosLoadDeps(parentOv, t); }
      else showToast((r && r.error) || 'Failed');
    } catch (e) { showToast(String(e.message || e)); }
  }));
}

// Per-task Ask EON drawer — history + one-turn ask with task context.
function openAskEon(t) {
  // WS-E (2026-07-26): the drawer joins the composer family — multiline
  // textarea (was a single-line <input> at 13px = iOS zoom + no room for
  // a real question), autosize, 16px, and EON replies render markdown.
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Ask EON · ${esc((t.title || 'Task').slice(0, 40))}</div><button class="csw-x">✕</button></div>
    <div class="tos-eonchat" id="ae-log"><div class="tos-empty">Loading…</div></div>
    <div class="tos-ask-prompts"><button class="csw-mini" data-ask-prompt="Explain the recorded request and the next step. Separate recorded facts from assumptions.">Understand the request</button><button class="csw-mini" data-ask-prompt="What information is missing before this task can be acted on?">Find missing details</button><button class="csw-mini" data-ask-prompt="What result and evidence would demonstrate that this task is done? Do not mark anything complete.">Define done</button></div><div class="tos-eonchat__bar"><textarea class="csw-search" id="ae-in" rows="1" maxlength="4000" aria-label="Ask about this task" placeholder="Ask about this task…"
      style="resize:none;font-size:16px;line-height:1.4;max-height:30dvh;" enterkeyhint="${_isTouchUA() ? 'enter' : 'send'}"></textarea>
    <button class="csw-confirm" id="ae-send" style="flex:none;width:auto;padding:0 16px">Send</button></div><p class="tos-ask-status" role="status"></p>`);
  // The sheet must not blind you to the thing you're asking about
  // (operator 2026-08-07: "ask eon blocks chat"). --aside drops the
  // 8px backdrop blur to a light scrim and caps the sheet, so the
  // task detail behind stays readable while you type.
  ov.classList.add('csw-overlay--aside');
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const log = ov.querySelector('#ae-log');
  const bub = (role, text) => role === 'user'
    ? `<div class="tos-bub tos-bub--me">${esc(text)}</div>`
    : `<div class="tos-bub tos-bub--eon">${renderMarkdownToHtml(text)}</div>`;
  // A row EON wrote by looking something up. The table has carried
  // tool_name/tool_input/tool_output since it shipped; nothing wrote
  // them until the loop landed (2026-08-07), and nothing rendered
  // them either — a tool row used to paint an empty EON bubble.
  const toolPill = (name) =>
    `<div class="tos-toolpill">read ${esc(String(name || 'data'))}</div>`;
  const paint = (msgs) => {
    log.innerHTML = (msgs || []).map(m => {
      if (m.role === 'tool') return toolPill(m.tool_name);
      const text = m.content || m.message || '';
      if (!text) return '';
      return bub(m.role === 'user' ? 'user' : 'eon', text);
    }).join('')
      || '<div class="csw-goalmeta">Start with the recorded brief. Ask EON to explain it, identify missing information, or help define the result.</div>';
    log.scrollTop = log.scrollHeight;
  };
  let sending = false, interacted = false;
  const status = ov.querySelector('.tos-ask-status'), sendButton = ov.querySelector('#ae-send');
  api(`/api/tasks/${t.id}/ask_eon/messages`).then(d => {
    if (ov.isConnected && !interacted) {
      if (!d?.ok || !Array.isArray(d.messages)) throw Error('Unreadable conversation');
      paint(d.messages);
    }
  }).catch(() => { if (ov.isConnected && !interacted) { paint([]); status.textContent='Earlier messages could not be read.'; } });
  const send = async () => {
    const inp = ov.querySelector('#ae-in'), msg = inp.value.trim();
    if (!msg || sending) return;
    sending = true; interacted = true; inp.readOnly = true; sendButton.disabled = true;
    status.textContent = '';
    log.insertAdjacentHTML('beforeend', '<div class="tos-bub tos-bub--eon tos-bub--wait">EON is thinking…</div>');
    log.scrollTop = log.scrollHeight;
    try {
      const d = await api(`/api/tasks/${t.id}/ask_eon`, {method:'POST',body:JSON.stringify({message:msg}),timeoutMs:120000});
      const reply = String(d && (d.reply || d.answer || d.message) || '').trim();
      if (d?.ok === false || !reply) throw Error('EON could not return an answer. Try again.');
      if (!ov.isConnected) return;
      ov.querySelector('.tos-bub--wait')?.remove();
      log.insertAdjacentHTML('beforeend',bub('user',msg));
      (Array.isArray(d.tool_calls) ? d.tool_calls : []).forEach(tc => log.insertAdjacentHTML('beforeend',toolPill(tc?.name)));
      log.insertAdjacentHTML('beforeend',bub('eon',reply));
      inp.value=''; inp.style.height='auto'; log.scrollTop=log.scrollHeight;
    } catch (e) {
      if (ov.isConnected) status.textContent=`${String(e.message || e)} Your question is still here.`;
    } finally {
      sending=false;
      if (ov.isConnected) { ov.querySelector('.tos-bub--wait')?.remove(); inp.readOnly=false; sendButton.disabled=false; inp.focus(); }
    }
  };
  ov.querySelectorAll('[data-ask-prompt]').forEach(button => button.onclick=()=>{
    if(sending)return;
    const input=ov.querySelector('#ae-in'); input.value=button.dataset.askPrompt; _composerAutosize(input); input.focus();
  });
  ov.querySelector('#ae-send').addEventListener('click', send);
  const aeIn = ov.querySelector('#ae-in');
  aeIn.addEventListener('input', () => _composerAutosize(aeIn));
  aeIn.addEventListener('keydown', e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      if (_isTouchUA()) return;           // WS-E: phones get a newline
      e.preventDefault();
      send();
    }
  });
}

async function _tosDo(ov, path, body, okMsg) {
  if (ov.dataset.taskPending === 'true') return;
  ov.dataset.taskPending='true';
  const buttons=Array.from(ov.querySelectorAll('[data-act]')).filter(b=>!b.disabled);
  buttons.forEach(b=>b.disabled=true);
  try {
    const d=await api(path,{method:'POST',body:JSON.stringify(body)});
    if (!d?.ok) throw Error(d?.error?.error || d?.error || 'The task could not be updated.');
    showToast(okMsg);
    if(ov.isConnected && ov.dataset.taskDetail)await openTaskDetail(ov.dataset.taskDetail);
    else ov.remove();
    await _tosLoad();
  } catch(e) { showToast(String(e.message || e)); }
  finally { delete ov.dataset.taskPending; if(ov.isConnected)buttons.forEach(b=>b.disabled=false); }
}

function openVerifyRate(parentOv, t) {
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Verify + rate</div><button class="csw-x">✕</button></div>
    <div class="csw-goalmeta" style="margin-bottom:10px">Check the result against the task brief, then rate the work. Your rating sets the points awarded. You cannot verify work assigned to you.</div>
    <div id="vr-stars" style="font-size:30px;letter-spacing:6px;text-align:center;margin-bottom:8px">${'<span data-s="1">☆</span><span data-s="2">☆</span><span data-s="3">☆</span><span data-s="4">☆</span><span data-s="5">☆</span>'}</div>
    <input class="csw-search" id="vr-note" placeholder="note (optional)" style="margin-bottom:8px"/>
    <button class="csw-confirm" id="vr-pass">Verify ✓</button>
    <button class="csw-mini" id="vr-fail" style="width:100%;margin-top:8px">Send back (rework)</button>`, true);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  let rating = 0, verifying = false;
  ov.querySelector('#vr-note').setAttribute('aria-label', 'Verification note');
  ov.querySelectorAll('#vr-stars span').forEach(s => { s.setAttribute('role','button'); s.setAttribute('tabindex','0'); s.setAttribute('aria-label', `${s.dataset.s} ${s.dataset.s === '1' ? 'star' : 'stars'}`); s.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); s.click(); } }); });
  const paint = () => {
    ov.querySelectorAll('#vr-stars span').forEach(s => {
      s.textContent = (+s.dataset.s <= rating ? '★' : '☆');
      if (ov.classList.contains('tr-rating')) s.setAttribute('aria-pressed', String(+s.dataset.s === rating));
    });
    const caption = ov.querySelector('.tr-rating-caption');
    if (caption) caption.textContent = rating ? `${rating} / 5 · ${['Needs rework','Below standard','Meets standard','Strong execution','Exceptional'][rating-1]}` : 'Choose an execution rating';
  };
  ov.querySelectorAll('#vr-stars span').forEach(s => s.addEventListener('click', () => { rating = +s.dataset.s; paint(); }));
  const submit = async (passed) => {
    if (verifying) return;
    if (passed && !rating) { showToast('Choose an execution rating first.'); ov.querySelector('#vr-stars span')?.focus(); return; }
    verifying = true; ov.querySelectorAll('#vr-pass,#vr-fail').forEach(b => b.disabled = true);
    try {
      const d = await api(`/api/tasks/${t.id}/verify`, { method: 'POST', body: JSON.stringify({ passed, rating: rating || null, note: ov.querySelector('#vr-note').value || null }) });
      if (d && d.ok) {
        showToast(passed ? `Verified${d.award?.points_awarded ? ' · ' + d.award.points_awarded + ' pts awarded' : ''}` : 'Sent back');
        ov.remove(); parentOv?.remove();
        // HOUSE floor 6 — the stamp is the only ceremony: verification is
        // where work becomes real and points become money, so the slam
        // lands ON the card before the board repaints (motion spent where
        // the work completes — the same law as the CS tear). Reduced
        // motion and every other skin reload immediately, as before.
        const card = passed && tosHouse() && !(window.EONHouse && window.EONHouse.reduced)
          ? document.querySelector(`[data-tid="${CSS.escape(String(t.id))}"]`) : null;
        if (card) {
          card.classList.add('tos-stamped');
          card.insertAdjacentHTML('beforeend', '<span class="tos-stampmark">VERIFIED</span>'
            + (d.award?.points_awarded ? `<span class="tos-ptschip">✓ ${d.award.points_awarded} pts</span>` : ''));
          setTimeout(_tosLoad, 900);
        } else {
          _tosLoad();
        }
      } else showToast((d && (d.error?.error || d.error)) || 'Failed');
    } catch (e) { showToast(String(e.message || e)); }
    finally { verifying = false; ov.querySelectorAll('#vr-pass,#vr-fail').forEach(b => b.disabled = false); }
  };
  ov.querySelector('#vr-pass').addEventListener('click', () => submit(true));
  ov.querySelector('#vr-fail').addEventListener('click', () => submit(false));
  if (_trActive()) {
    ov.classList.add('tr-rating');
    if (_tr.quiet) ov.classList.add('tr-components-quiet');
    ov.querySelector('[role="dialog"]').setAttribute('aria-label', 'Verify task execution');
    ov.querySelector('#vr-stars').removeAttribute('style');
    ov.querySelector('#vr-stars').setAttribute('role','group');
    ov.querySelector('#vr-stars').setAttribute('aria-label','Execution quality');
    ov.querySelector('#vr-stars').insertAdjacentHTML('beforebegin', '<h2 class="tr-rating-title">How did the work land?</h2>');
    ov.querySelector('#vr-stars').insertAdjacentHTML('afterend', '<div class="tr-rating-caption" role="status">Choose an execution rating</div>');
    const note = ov.querySelector('#vr-note');
    note.outerHTML = '<label for="vr-note">A note for the next step <span>(optional)</span></label><textarea class="csw-search" id="vr-note" aria-label="Verification note" rows="3" maxlength="2000" placeholder="What went well, or what needs another look?" style="margin-bottom:16px"></textarea>';
    ov.querySelectorAll('#vr-stars span').forEach(s=>s.setAttribute('aria-pressed','false'));
  }
}

async function openAssignPicker(parentOv, t) {
  const dept = t.department || 'Operations';
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Reassign · ${esc(dept)}</div><button class="csw-x">✕</button></div><div id="ap-list"><div class="tos-empty">Loading…</div></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  try {
    const d = await api(`/api/tasks/eligible-assignees?department=${encodeURIComponent(dept)}`);
    const cands = (d && d.candidates) || [];
    ov.querySelector('#ap-list').innerHTML = cands.length ? cands.map(c =>
      `<button class="hr-rep" data-sid="${esc(c.staff_id)}"><div class="hr-rep__meta"><div class="hr-rep__name">${esc(c.name || c.email)}</div><div class="hr-rep__role">${esc(c.department || '')}</div></div><span class="hr-rep__chev">›</span></button>`).join('')
      : `<div class="tos-empty">No eligible staff in ${esc(dept)}.</div>`;
    ov.querySelectorAll('.hr-rep[data-sid]').forEach(b => b.addEventListener('click', async () => {
      const r = await api(`/api/tasks/${t.id}/assign`, { method: 'POST', body: JSON.stringify({ staff_id: b.getAttribute('data-sid') }) });
      if (r && r.ok) { showToast('Reassigned'); ov.remove(); parentOv?.remove(); _tosLoad(); } else showToast('Failed');
    }));
  } catch (e) { ov.querySelector('#ap-list').innerHTML = `<div class="tos-empty">${esc(e.message || e)}</div>`; }
}

async function openEonQueue() {
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">EON — queued to approve</div><button class="csw-x">✕</button></div><div id="eq-list"><div class="tos-empty">Loading…</div></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  try {
    const d = await api('/api/tasks/autogen/queue');
    const q = (d && d.queue) || [];
    ov.querySelector('#eq-list').innerHTML = q.length ? q.map(x =>
      `<div class="hr-fric ${x.orphan ? 'hr-fric--orphan' : ''}"><div class="hr-fric__top"><b>${esc(x.title)}</b>${x.orphan ? '<span class="hr-fric__badge">no assignee</span>' : ''}</div>
       <div class="hr-flag__d">${esc(x.department || '?')}${x.estimated_impact_ngn ? ' · ' + cswNgn(x.estimated_impact_ngn) + ' at stake' : ''}${x.orphan ? ` · ${esc(x.orphan_reason || 'no eligible assignee — add staff or hand-route')}` : (x.proposed?.name ? ' · routes to ' + esc(x.proposed.name) : '')}</div>
       <div class="hr-fric__act"><button class="hr-mini" data-approve="${esc(x.id)}">${x.orphan ? 'Retry route' : 'Approve & route'}</button></div></div>`).join('')
      : `<div class="tos-empty"><div class="tos-empty__i">✓</div>Nothing waiting. EON auto-routes low-risk work; high-stakes lands here.</div>`;
    ov.querySelectorAll('[data-approve]').forEach(b => b.addEventListener('click', async () => {
      const r = await api(`/api/tasks/autogen/${b.getAttribute('data-approve')}/approve`, { method: 'POST', body: '{}' });
      if (r && r.ok) { showToast('Approved & routed'); b.closest('.hr-fric').remove(); _tosLoad(); } else showToast((r && r.error) || 'Failed');
    }));
  } catch (e) { ov.querySelector('#eq-list').innerHTML = `<div class="tos-empty">${esc(e.message || e)}</div>`; }
}

// Quick-add natural parser: "!high" / "!critical" / "!low" sets priority;
// "today" / "tomorrow" / "5pm" set due_at (local clock). Unparsed text = title.
function _tosParseQuickAdd(raw) {
  let text = raw, priority;
  const pm = text.match(/!(urgent|critical|high|normal|low)\b/i);
  if (pm) {
    priority = pm[1].toLowerCase();
    if (priority === 'critical') priority = 'urgent';
    text = text.replace(pm[0], '');
  }
  let due = null;
  const now = new Date();
  let day = null;
  if (/\btomorrow\b/i.test(text)) { day = 1; text = text.replace(/\btomorrow\b/i, ''); }
  else if (/\btoday\b/i.test(text)) { day = 0; text = text.replace(/\btoday\b/i, ''); }
  const tm = text.match(/\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/i);
  if (tm || day !== null) {
    let h = 18, min = 0; // default end-of-shift
    if (tm) {
      h = (+tm[1] % 12) + (tm[3].toLowerCase() === 'pm' ? 12 : 0);
      min = +(tm[2] || 0);
      text = text.replace(tm[0], '');
    }
    due = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (day || 0), h, min);
    if (day === null && due < now) due.setDate(due.getDate() + 1); // bare "5pm" already past = tomorrow
  }
  return { title: text.replace(/\s{2,}/g, ' ').trim(), priority, due_at: due ? due.toISOString() : null };
}

async function _tosCreateTask(payload) {
  try {
    return await api('/api/tasks', {method:'POST', body:JSON.stringify(payload)});
  } catch (error) {
    if(error.body?.detail?.code !== 'task_scope_selection_required')throw error;
    return _tosChooseTaskScope(payload, error.body.detail);
  }
}

function _tosChooseTaskScope(payload, issue) {
  return new Promise((resolve, reject) => {
    let fields=[], pending=false, settled=false, uncertain=false;
    const returnFocus=document.activeElement;
    const parent=returnFocus?.closest?.('.csw-overlay')||Array.from(document.querySelectorAll?.('.csw-overlay')||[]).at(-1);
    const parentWasInert=parent?.hasAttribute('inert');
    const ov=cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Where does this work belong?</div><button class="csw-x">×</button></div><div class="tos-scope"><p class="tos-scope-title">${esc(payload.title || 'New task')}</p><p>Choose where this task should be routed. Your draft stays with this request.</p><form novalidate><div data-scope-fields></div><div class="tos-response-actions"><button class="csw-confirm" type="submit">Create task</button><button class="csw-mini" type="button" data-scope-back>Back to draft</button></div><p role="status" aria-live="polite"></p></form></div>`);
    parent?.setAttribute('inert','');
    const remove=ov.remove.bind(ov), status=ov.querySelector('[role="status"]');
    ov.remove=()=>{
      if(pending)return;
      if(parent&&!parentWasInert)parent.removeAttribute('inert');
      remove();
      if(!settled){settled=true;reject(Object.assign(Error(uncertain ? 'Task creation was not confirmed. Check the task list before trying again.' : 'Task not created. Your draft is unchanged.'),{cancelled:!uncertain}));}
      // The caller re-enables its submit button in the promise handler.
      // Restore focus afterwards, while its original draft is still open.
      setTimeout(()=>{
        if(parent?.isConnected&&!parent.hasAttribute('inert')){
          const field=parent.querySelector('input:not([disabled]),textarea:not([disabled]),select:not([disabled])');
          (field||parent.querySelector('button:not([disabled])'))?.focus();
        }else if(returnFocus?.isConnected&&!returnFocus.disabled&&returnFocus!==document.body){returnFocus.focus();}
      },0);
    };
    ov.querySelector('.csw-x').onclick=()=>ov.remove();
    ov.querySelector('[data-scope-back]').onclick=()=>ov.remove();
    const paint=next=>{
      fields=(Array.isArray(next.fields)?next.fields:[]).filter(f=>['location_id','department_id'].includes(f.key)&&Array.isArray(f.options)&&f.options.length);
      if(!fields.length){ov.remove();return false;}
      ov.querySelector('[data-scope-fields]').innerHTML=fields.map(f=>`<label for="tos-scope-${f.key}">${esc(f.label)}</label><select class="csw-search" id="tos-scope-${f.key}" required><option value="">Choose ${esc(String(f.label).toLowerCase())}…</option>${f.options.map(o=>`<option value="${esc(o.id)}">${esc(o.label)}</option>`).join('')}</select>`).join('');
      return true;
    };
    if(!paint(issue))return;
    ov.querySelector('[data-scope-fields]').addEventListener('change',()=>{
      if(!pending&&!uncertain)status.textContent='';
    });
    ov.querySelector('form').onsubmit=async event=>{
      event.preventDefault();
      if(pending||uncertain)return;
      const selected={};
      for(const f of fields){
        const input=ov.querySelector(`#tos-scope-${f.key}`);
        if(!f.options.some(o=>String(o.id)===input.value)){status.textContent=`Choose a ${String(f.label).toLowerCase()} to continue.`;input.focus();return;}
        selected[f.key]=input.value;
      }
      pending=true;status.textContent='Creating task…';
      ov.querySelectorAll('button,select').forEach(el=>el.disabled=true);
      try {
        const result=await api('/api/tasks',{method:'POST',body:JSON.stringify({...payload,...selected})});
        if(!result?.ok&&!result?.id)throw Error('Task creation was not confirmed.');
        settled=true;pending=false;ov.remove();resolve(result);
      } catch(error) {
        if(error.body?.detail?.code==='task_scope_selection_required'){
          pending=false;paint(error.body.detail);status.textContent='The available choices changed. Choose again to continue.';
        } else {
          uncertain=!error.status||error.status>=500;
          status.textContent=uncertain ? 'Task creation was not confirmed. Check the task list before trying again.' : String(error.message||'The task could not be created. Your selections are kept here.');
        }
      } finally {
        pending=false;
        if(ov.isConnected){ov.querySelectorAll('button,select').forEach(el=>el.disabled=false);ov.querySelector('[type="submit"]').disabled=uncertain;}
      }
    };
    ov.querySelector('select')?.focus();
  });
}

function openQuickAdd() {
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Quick add task</div><button class="csw-x">✕</button></div>
    <input class="csw-search" id="qa-title" placeholder="e.g. count Palms stock today 5pm !high" style="margin-bottom:8px"/>
    <div class="csw-goalmeta" id="qa-parse" style="margin-bottom:8px"></div>
    <button class="csw-confirm" id="qa-run">Create + let EON route</button>
    <div class="csw-goalmeta" style="margin-top:8px">Or run EON's autonomous pass over current business insights:</div>
    <button class="csw-mini" id="qa-auto" style="width:100%;margin-top:6px">Run EON auto-allocate now</button>
    <div class="csw-goalmeta" id="qa-msg"></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  ov.querySelector('#qa-title').addEventListener('input', e => {
    const p = _tosParseQuickAdd(e.target.value);
    const bits = [];
    if (p.priority) bits.push('priority ' + p.priority);
    if (p.due_at) bits.push('due ' + new Date(p.due_at).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' }));
    ov.querySelector('#qa-parse').textContent = bits.length ? 'Parsed: ' + bits.join(' · ') : '';
  });
  ov.querySelector('#qa-run').addEventListener('click', async (ev) => {
    const btn = ev.currentTarget;
    const p = _tosParseQuickAdd(ov.querySelector('#qa-title').value);
    if (!p.title) { ov.querySelector('#qa-msg').textContent = 'Title required.'; return; }
    const payload = { title: p.title };
    if (p.priority) payload.priority = p.priority;
    if (p.due_at) payload.due_at = p.due_at;
    btn.disabled = true; btn.textContent = 'Creating…';
    try {
      const d = await _tosCreateTask(payload);
      if (d && (d.ok || d.id)) { showToast('Task created'); ov.remove(); _tosLoad(); }
      else { showToast('Failed'); btn.disabled = false; btn.textContent = 'Create + let EON route'; }
    } catch (e) {
      ov.querySelector('#qa-msg').textContent = e.message || String(e);
      btn.disabled = false; btn.textContent = 'Create + let EON route';
    }
  });
  ov.querySelector('#qa-auto').addEventListener('click', async () => {
    const m = ov.querySelector('#qa-msg'); m.textContent = 'EON scanning insights + routing…';
    try {
      const d = await api('/api/tasks/autogen/run', { method: 'POST', body: '{}' });
      m.textContent = d && d.ok ? `Routed ${d.created || 0}, staged ${d.staged || 0} for approval.` : 'Failed';
      _tosLoad();
    } catch (e) { m.textContent = e.message || String(e); }
  });
}

// ═══════════════════════════════════════════════════════════════════
// PEOPLE OS (pos-*) — production port of docs/people_os_design_v2.html.
// A tabbed people surface: Overview (owner dashboard / staff self-view) +
// Tasks (Asana-grade board · list · right-docked detail · verify-celebrate).
// Role-aware: leads (owner/admin/manager) default to whole-team; staff see
// self + their own tasks. Bound to the live people/tasks APIs.
// ═══════════════════════════════════════════════════════════════════
const POS_DEPT = {
  'Sales': 'var(--d-sales)', 'Customer Service': 'var(--d-cs)', 'CS': 'var(--d-cs)',
  'Inventory': 'var(--d-inv)', 'Logistics': 'var(--d-log)', 'Marketing': 'var(--d-mkt)',
  'Operations': 'var(--d-ops)',
};
const POS_STATUS = {
  open: { lbl: 'Backlog', c: 'var(--st-open)' },
  assigned: { lbl: 'Assigned', c: 'var(--st-assigned)' },
  acknowledged: { lbl: 'Taken', c: 'var(--st-ack)' },
  in_progress: { lbl: 'In progress', c: 'var(--st-prog)' },
  blocked: { lbl: 'Blocked', c: 'var(--st-block)' },
  completed: { lbl: 'In review', c: 'var(--st-review)' },
  done: { lbl: 'Done', c: 'var(--st-done)' },
  verified: { lbl: 'Verified', c: 'var(--st-done)' },
};
const POS_SPARK = `<svg class="spark" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.8 5.5L19 9l-5.2 1.5L12 16l-1.8-5.5L5 9l5.2-1.5z"/></svg>`;

// Aurora anchor #10 (felt-polish 2026-07-26): the People OS header band —
// same shared shader + mount pattern as CS anchor #7 / Staff OS anchor #8.
// Header band ONLY; never behind the leaderboard, the pool or any number
// (FLAT LAW). Decorative: every failure path is silent.
function _posMountHeroBand() {
  const c = document.getElementById('pos-heroband');
  if (!c || typeof window.mountAurora !== 'function') return;
  if (_posState._band && _posState._band._canvas === c) return;
  if (_posState._band) { try { _posState._band.destroy(); } catch (_) {} }
  const h = window.mountAurora(c, ['#C8FF4D', '#56E39F', '#7AA6FF'],
    { alpha: true, vignette: false, glow: null, intensity: 0.26 });
  if (!h) return;
  h._canvas = c;
  _posState._band = h;
}

let _posState = {
  role: 'owner',     // 'owner' (lead/whole-team) | 'staff' (self)
  tab: 'overview',   // overview | tasks(=Verify) | reps | reviews | goals | org
  execDays: 7,       // Execution analytics window (7 | 30)
  // tasks starts null, NOT [] — the tab badge renders blank until the first
  // load instead of flashing "0" then jumping to the real count (felt-polish).
  board: null, deps: {}, tasks: null, byId: {},
  pulse: null, overview: null, mine: null, selfPerf: null,
  loaded: false,
  _lastPaintTab: null,   // quiet-repaint discipline (felt-polish)
};

// Wave-3 (2026-07-26): gate on PERMISSIONS, not role labels. A PIN
// Store-Manager's role is the label "Store Manager" — the name check
// hid Verify/Assign from exactly the people the backend authorizes.
// Falls back to role names for sessions predating the permissions list.
function memberCan(perm) {
  const m = State.member || {};
  if (Array.isArray(m.permissions) && m.permissions.length) {
    return m.permissions.includes(perm);
  }
  return ['manager', 'admin', 'owner'].includes(m.base_role || m.role || '');
}
function posIsLead() {
  return memberCan('analytics:staff:view');
}
// A manager limited to some shops or teams (tenant_members.scope_*): the
// company-wide People reads refuse them (routes_people._company_scope), so
// ask first and say so, as Common Ground does — never a failed read.
function posScopeLimited() {
  const sc = State.member?.scope;
  return sc?.locations != null || sc?.departments != null;
}
function posNgn(v) {
  // Delegates to the canonical ngn() (one-system law, #1488). ₦0 renders
  // as ₦0 — the old `if (!v) return ''` made a zero bonus share an empty
  // box (wave-2 truth).
  const n = Number(v);
  if (v == null || !Number.isFinite(n)) return '';
  if (!n) return '₦0';
  return ngn(n, { compact: true });
}
function posAvColor(n) { return cswAvatarStyle(n); }
function posAvatar(name, size) {
  if (!name) return `<span class="pos-av pos-av--ghost${size ? ' pos-av--' + size : ''}">＋</span>`;
  return `<span class="pos-av${size ? ' pos-av--' + size : ''}" style="${posAvColor(name)}">${esc(cswInitials(name))}</span>`;
}
function posDeptColor(d) {
  if (POS_DEPT[d]) return POS_DEPT[d];
  // The live vocabulary is store-suffixed and brand-named ('Retail ·
  // Palms', 'CRUX Custom Manufacturing', 'Customer Care') -- exact keys
  // matched 1 of 6 real departments and painted the rest --ink-mute grey
  // (floor-2 verifier catch). Keywords, 'customer' before 'custom'.
  const s = String(d || '').toLowerCase();
  if (s.startsWith('retail') || s.includes('sales') || s.includes('store')) return 'var(--d-sales)';
  if (s.includes('customer')) return 'var(--d-cs)';
  if (s.includes('manufactur') || s.includes('custom')) return 'var(--d-ops)';
  if (s.includes('inventory') || s.includes('warehouse')) return 'var(--d-inv)';
  if (s.includes('logistic')) return 'var(--d-log)';
  if (s.includes('market')) return 'var(--d-mkt)';
  return 'var(--ink-mute)';
}

// ONE overdue definition for every People OS surface: real Date compare
// (the old ribbon string-compare broke on +01:00 offsets) over the same
// OPEN set the Execution tab uses (wave-2 truth).
function posIsOverdue(t) {
  return !!(t && t.due_at && POS_OPEN_ST.includes(t.status)
            && new Date(t.due_at).getTime() < Date.now());
}
function posDueState(iso) {
  if (!iso) return null;
  const d = new Date(iso), now = new Date();
  const h = (d - now) / 3.6e6;
  if (h < 0) { const a = Math.abs(h); return { cls: 'over', txt: a < 24 ? `${Math.round(a)}h overdue` : `${Math.round(a / 24)}d overdue` }; }
  if (h < 6) return { cls: 'soon', txt: `${Math.round(h)}h left` };
  return { cls: '', txt: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) };
}
function posPriLabel(p) { return ({ urgent: 'Urgent', high: 'High', med: 'Medium', medium: 'Medium', normal: 'Normal', low: 'Low' })[p] || (p || 'Normal'); }
function posPriGlyph(p) {
  const cls = ({ urgent: 'pos-prig--urgent', high: 'pos-prig--high', med: 'pos-prig--med', medium: 'pos-prig--med', normal: 'pos-prig--normal', low: 'pos-prig--low' })[p] || 'pos-prig--normal';
  return `<span class="pos-prig ${cls}"><i></i><i></i><i></i></span>`;
}
function posStatusGlyph(st, size) {
  size = size || 13;
  const c = POS_STATUS[st]?.c || 'var(--st-open)';
  if (st === 'open' || st === 'assigned') return `<svg width="${size}" height="${size}" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" fill="none" stroke="${c}" stroke-width="1.6" stroke-dasharray="2.6 2.4"/></svg>`;
  if (st === 'blocked') return `<svg width="${size}" height="${size}" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6.4" fill="none" stroke="${c}" stroke-width="1.6"/><path d="M5.4 5.4l5.2 5.2" stroke="${c}" stroke-width="1.6"/></svg>`;
  if (st === 'completed') return `<svg width="${size}" height="${size}" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6.4" fill="none" stroke="${c}" stroke-width="1.6"/><path d="M8 8 V3.5" stroke="${c}" stroke-width="1.6" stroke-linecap="round"/><path d="M8 8 L11 9.5" stroke="${c}" stroke-width="1.6" stroke-linecap="round"/></svg>`;
  if (st === 'done' || st === 'verified') return `<svg width="${size}" height="${size}" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6.4" fill="${c}" fill-opacity=".16" stroke="${c}" stroke-width="1.6"/><path d="M5 8.2l2 2 4-4.4" fill="none" stroke="${c}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  return `<svg width="${size}" height="${size}" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6.4" fill="none" stroke="var(--line-strong)" stroke-width="1.6"/><path d="M8 1.6 A6.4 6.4 0 0 1 14.4 8" fill="none" stroke="${c}" stroke-width="1.8" stroke-linecap="round"/></svg>`;
}
// points preview for a not-yet-awarded task (mockup heuristic)
function posPtsPreview(t) { return t.points_awarded || null; }  // real award only — verify pays round((6+4+3)×quality 0.5–1.5) = 3–20; chores flat 3
function posPtsLabel(t) { const p = posPtsPreview(t); return p ? `+${p}` : '+3–20'; }
// derive an EON "why now" tail when allocation_reason lacks one
function posWhyLine(t) {
  if (t.allocation_reason) return t.allocation_reason;
  const bits = [];
  if (t.estimated_impact_ngn) bits.push(`${posNgn(t.estimated_impact_ngn)} at stake`);
  if (t.due_at) { const ds = posDueState(t.due_at); if (ds) bits.push(ds.cls === 'over' ? ds.txt : ds.txt + ' due'); }
  if (t.category) bits.push(`${t.category} scanner`);
  return bits.length ? `Routed from business insight — ${bits.join(' · ')}.` : 'Allocated by EON from current business insight.';
}

// ── eonAnimNums (vet 2026-07-05): count-up for freshly rendered stat
// numbers. Finds numeric text in the given scope's stat selectors and eases
// 0 → value over ~550ms, preserving prefix/suffix/commas/decimals. Runs once
// per element (data-eon-counted), skips tiny values, and is a no-op under
// prefers-reduced-motion.
function eonAnimNums(scope) {
  try {
    // The default seats show source values; opening a view is not a new receipt.
    if (cg.active() && !cg.requested()) return;
    if (cg.motionPaused() || (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches)) return;
    const root = scope || document;
    const els = root.querySelectorAll('.pos-pulse__v, .sos-root .kv b, .sos-root .ring2 .mid b, .pos-pb__v');
    els.forEach(el => {
      if (el.dataset.eonCounted || el.children.length) return;
      const txt = el.textContent || '';
      // NUMBERS THAT ARE NOT COUNTS (2026-09-02): the Day's "My info" rows
      // ride the same .kv b slot, so a rep's phone "+234 704 …" counted up
      // from "+45 704 …" and "2024-08-22" from "391-08-22" on every open.
      // A value with more than one number group, a leading plus, or a
      // date shape is an identity, not a count — leave it be.
      if (el.dataset.nocount != null) return;
      if (/\+\s*\d/.test(txt) || /\d{4}-\d{2}-\d{2}/.test(txt)) return;
      if ((txt.match(/\d[\d,]*(?:\.\d+)?/g) || []).length !== 1) return;
      const m = txt.match(/-?[\d,]+(\.\d+)?/);
      if (!m) return;
      const raw = m[0];
      const target = parseFloat(raw.replace(/,/g, ''));
      if (!isFinite(target) || Math.abs(target) < 2) return;
      el.dataset.eonCounted = '1';
      const hasComma = raw.indexOf(',') !== -1;
      const dec = m[1] ? m[1].length - 1 : 0;
      const t0 = performance.now(), ms = 550;
      let frame = 0;
      const untrack = cg.trackMotion(() => {
        if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
        el.textContent = txt;
      });
      const tick = (now) => {
        const p = Math.min(1, (now - t0) / ms);
        if (p >= 1 || cg.motionPaused()) { untrack(); el.textContent = txt; return; }
        const eased = 1 - Math.pow(1 - p, 3);
        let v = target * eased;
        let sVal = dec ? v.toFixed(dec) : String(Math.round(v));
        if (hasComma) sVal = Number(sVal).toLocaleString('en-US');
        el.textContent = txt.replace(raw, sVal);
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    });
  } catch (_) { /* decoration only — never break a render */ }
}

function renderPeople() {
  if(eonWorkroomEnabled())return eonWorkroomShell('people');
  const d = new Date();
  const dateStr = d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' }) +
    ' · ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) + ' WAT';
  return `<div class="pos-shell" id="pos">
    ${eonWorkroomReturn()}
    ${cg.active() ? cg.frame('people') : cg.entry('people')}
    <svg width="0" height="0" style="position:absolute" aria-hidden="true"><defs>
      <linearGradient id="cswGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#d4ff3a"/><stop offset="40%" stop-color="#7fd6c0"/><stop offset="74%" stop-color="#9177C7"/><stop offset="100%" stop-color="#CA6673"/></linearGradient>
      <linearGradient id="posEqGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#4dd6a0"/><stop offset="100%" stop-color="#9177C7"/></linearGradient></defs></svg>
    <header class="pos-sh">
      <canvas class="pos-heroband" id="pos-heroband" aria-hidden="true"></canvas>
      <div class="pos-sh__top">
        <button class="pos-sh__back" id="pos-back" aria-label="Home">‹</button>
        <h1 class="pos-wordmark">People<span class="pos-wordmark__os">OS</span></h1>
        <span class="pos-sh__role" id="pos-role"><span class="dot"></span>Loading…</span>
        <div class="pos-sh__sp"></div>
        <span class="pos-sh__date pos-mono" id="pos-date">${esc(dateStr)}</span>
        ${typeof prcCanRequest === 'function' && prcCanRequest() ? '<button class="prc-req-btn" id="prc-people-btn" type="button">Request finance</button>' : ''}
        <button class="pos-iconbtn" id="pos-sound" title="Sounds"></button>
        <button class="pos-iconbtn" id="pos-eonq" title="EON queue">⊕</button>
        <button class="pos-iconbtn" id="pos-refresh" title="Refresh">↻</button>
        <div class="pos-switch" id="pos-switch" style="display:none">
          <button id="pos-sw-owner" class="on">Team</button>
          <button id="pos-sw-staff">Me</button>
        </div>
      </div>
      <div class="pos-ribbon" id="pos-ribbon"></div>
      <div id="ascent-people-work"></div>
    </header>
    <nav class="pos-tabbar" id="pos-tabbar"></nav>
    <div class="pos-surface" id="pos-surface">${_posSkel()}</div>
    <div class="pos-scrim" id="pos-scrim"></div>
    <aside class="pos-detail" id="pos-detail">
      <div class="pos-detail__bar">
        <span class="pos-detail__crumb" id="pos-dcrumb">CRUX · Tasks</span>
        <div class="pos-detail__bsp"></div>
        <button class="pos-detail__ib" id="pos-dclose" title="Close"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M6 6l12 12M18 6L6 18"/></svg></button>
      </div>
      <div class="pos-detail__scroll" id="pos-dscroll"></div>
      <div class="pos-detail__foot" id="pos-dfoot"></div>
    </aside>
  </div>`;
}
function _posSkel() { return `<div style="padding:24px;display:grid;grid-template-columns:1fr 332px;gap:20px"><div style="display:flex;flex-direction:column;gap:20px"><div class="pos-skel" style="height:108px"></div><div class="pos-skel" style="height:300px"></div></div><div style="display:flex;flex-direction:column;gap:16px"><div class="pos-skel" style="height:220px"></div><div class="pos-skel" style="height:200px"></div></div></div>`; }

async function bindPeople() {
  if(eonWorkroomEnabled())return eonWorkroomBind('people');
  // default role-view: leads → owner (whole-team), staff → self
  _posState.role = posIsLead() ? (_posState.role || 'owner') : 'staff';
  // Floor 2's entrance is ARMED here and SPENT by _posBindTeam on the
  // first team paint -- at this point the surface still holds the
  // skeleton (the adversary's catch: stamping now touches zero rows),
  // and stamping on every re-render would re-hide the drawer per
  // search keystroke. One flag, one rise per visit.
  _posState.drawerReveal = true;
  document.getElementById('pos-back')?.addEventListener('click', () => { State.view = roleHome(); render(); });
  bindSoundToggle('pos-sound');
  document.getElementById('pos-refresh')?.addEventListener('click', () => {
    // Refresh means REFRESH: the department dashboards cache per
    // (slug, window) and were immune to ↻ forever (felt-polish).
    _posDeptState.cache = {};
    // ...and the unnamed-sales panel reads again, in place (_posAboutPanel).
    const about = document.getElementById('pos-about');
    if (about) about.dataset.reread = '1';
    _posLoad();
  });
  // The header clock used to freeze at render time — leave the page open
  // and it lied. One shared minute tick keeps it honest (felt-polish).
  if (!State._posClock) {
    State._posClock = setInterval(() => {
      const el = document.getElementById('pos-date');
      if (!el) return;
      const d = new Date();
      el.textContent = d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' }) +
        ' · ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) + ' WAT';
    }, 60000);
  }
  _posMountHeroBand();     // aurora anchor #10 — header band only (FLAT LAW below)
  // Procurement: staff "Request finance" entry point (header, all roles
  // holding accounting:manage — store/inventory/marketing managers).
  document.getElementById('prc-people-btn')?.addEventListener('click', () => prcOpenRequestSheet());
  document.getElementById('pos-eonq')?.addEventListener('click', () => posOpenEonQueue());
  document.getElementById('pos-dclose')?.addEventListener('click', () => posCloseDetail());
  document.getElementById('pos-scrim')?.addEventListener('click', () => posCloseDetail());
  // role switch (leads only)
  if (posIsLead()) {
    const sw = document.getElementById('pos-switch');
    if (sw) sw.style.display = '';
    document.getElementById('pos-sw-owner')?.addEventListener('click', () => posSetRole('owner'));
    document.getElementById('pos-sw-staff')?.addEventListener('click', () => posSetRole('staff'));
  }
  document.addEventListener('keydown', _posKeydown);
  _posRenderTabs();
  _posRenderRoleTag();
  await _posLoad();
}

function _posKeydown(e) {
  if (State.view !== 'people') { document.removeEventListener('keydown', _posKeydown); return; }
  if (e.key === 'Escape') { posCloseDetail(); return; }
}

function posSetRole(r) {
  if (r === 'staff' && !posIsLead()) r = 'staff';
  if (r === 'owner' && !posIsLead()) return; // staff can't see whole team
  if (r === _posState.role) return;
  _posState.role = r;
  document.getElementById('pos-sw-owner')?.classList.toggle('on', r === 'owner');
  document.getElementById('pos-sw-staff')?.classList.toggle('on', r === 'staff');
  _posRenderRoleTag();
  // The third payload (team overview vs self performance) and the ribbon are
  // role-specific. When a lead first flips to "Me" we haven't fetched
  // /api/cs/me/performance yet (and vice-versa) — reload so the new view isn't
  // a permanent empty state, then re-render tabs/ribbon/surface from _posLoad.
  const need = r === 'owner' ? !_posState.overview : !_posState.selfPerf;
  if (need) { _posState.loaded = false; _posLoad(); return; }
  _posRenderTabs();
  _posRenderRibbon();
  _posRenderActive();
}
function posSetTab(t) {
  _posState.tab = t;
  _posRenderTabs();
  _posRenderActive();
}

function _posRenderRoleTag() {
  const tag = document.getElementById('pos-role');
  if (!tag) return;
  const owner = _posState.role === 'owner';
  tag.innerHTML = owner ? '<span class="dot"></span>' + ((State.member?.base_role || State.member?.role) === 'owner' ? 'Owner' : 'Manager') + ' · whole team' : '<span class="dot"></span>You · personal view';
}

function _posRenderTabs() {
  const bar = document.getElementById('pos-tabbar');
  if (!bar) return;
  // Badge = OPEN work, the same set the ribbon's "open tasks" counts — the
  // old not-verified/cancelled filter also counted in-review tasks, so the
  // badge and the ribbon 2px below it disagreed (wave-2 truth).
  const openCount = _posState.tasks ? _posState.tasks.filter(t => POS_OPEN_ST.includes(t.status)).length : '';
  const T = _posState.tab;
  bar.innerHTML = `
    <button class="pos-tab ${T === 'overview' ? 'on' : ''}" data-tab="overview">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="9"/><path d="M12 12l4-2M12 12v5"/></svg>Overview</button>
    <button class="pos-tab ${T === 'tasks' ? 'on' : ''}" data-tab="tasks">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 20v-6M10 20V8M16 20v-9M22 20v-4M2 20h20"/></svg>Verify${openCount !== '' ? `<span class="pos-tabnum">${openCount}</span>` : ''}</button>
    <button class="pos-tab ${T === 'reps' ? 'on' : ''}" data-tab="reps"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 17l5-5 4 3 7-8"/><path d="M21 7v5h-5"/></svg>Reps</button>
    ${posIsLead() ? `<button class="pos-tab ${T === 'team' ? 'on' : ''}" data-tab="team"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="9" cy="8" r="3.2"/><path d="M2.5 20c0-3.3 2.9-5.5 6.5-5.5S15.5 16.7 15.5 20"/><circle cx="17" cy="9" r="2.6"/><path d="M15.5 14.8c.5-.1 1-.2 1.5-.2 2.9 0 5.5 1.8 5.5 4.9"/></svg>Team</button>` : ''}
    ${posIsLead() ? `<button class="pos-tab ${T === 'floor' ? 'on' : ''}" data-tab="floor"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9h18M8 9v11M16 9v11"/></svg>Floor</button>` : ''}
    ${memberCan('hr:payroll:view') ? `<button class="pos-tab ${T === 'pay' ? 'on' : ''}" data-tab="pay"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="6" width="18" height="13" rx="2"/><path d="M3 10h18M7 15h4"/></svg>Pay</button>` : ''}
    ${tosHouse() ? '' : `<button class="pos-tab ${T === 'reviews' ? 'on' : ''}" data-tab="reviews"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7"/></svg>Coaching</button>`}
    <button class="pos-tab ${T === 'goals' ? 'on' : ''}" data-tab="goals"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.4" fill="currentColor"/></svg>Goals</button>
    ${tosHouse() ? '' : `<button class="pos-tab ${T === 'org' ? 'on' : ''}" data-tab="org"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="18" r="3"/><path d="M12 9v3M9 16l-1.5-1M15 16l1.5-1"/></svg>Org</button>`}
    <button class="pos-tab ${T === 'guide' ? 'on' : ''}" data-tab="guide"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V4H6.5A2.5 2.5 0 0 0 4 6.5v13z"/><path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-2.5"/></svg>Guide</button>
    ${posIsLead() ? _posDeptDropdown(T) : ''}`;
  bar.querySelectorAll('.pos-tab[data-tab]').forEach(b => b.addEventListener('click', () => posSetTab(b.getAttribute('data-tab'))));
  _posBindDeptDropdown(bar);
  cg.tabsExtra(bar, 'people');
}

async function _posLoad() {
  mountAscentWork(document.getElementById('ascent-people-work'), {api, query:_posState.role==='staff'?'?self_only=true':'', identity:()=>State.jwt+'|'+State.tenant?.slug, owner:(State.member?.base_role||State.member?.role)==='owner'&&!State.impersonating});
  const surface = document.getElementById('pos-surface');
  if (surface && !_posState.loaded) surface.innerHTML = _posSkel();
  const owner = _posState.role === 'owner';
  try {
    const jobs = [
      // verified_hours=720: People OS computes 7d/30d execution figures
      // from this payload — the Task OS default (48h) silently falsified
      // every window label past two days (wave-2 truth, 2026-07-26).
      api('/api/tasks/board?verified_hours=720').catch(() => ({ tasks: [], deps: [], _cgUnavailable: true })),
      api('/api/tasks/pulse').catch(() => ({})),
    ];
    jobs.push(owner ? api('/api/people/overview?days=7').catch(() => null)
                    : api('/api/cs/me/performance').catch(() => null));
    jobs.push(api('/api/tasks/mine').catch(() => ({ tasks: [] })));
    const [b, p, third, mine] = await Promise.all(jobs);
    _posState.board = b;
    _posState.tasks = (b && b.tasks) || [];
    _posState.byId = {}; _posState.tasks.forEach(t => _posState.byId[t.id] = t);
    // deps index: task_id -> [{depends_on, kind}]
    _posState.deps = {};
    ((b && b.deps) || []).forEach(e => { (_posState.deps[e.task_id] = _posState.deps[e.task_id] || []).push(e); });
    _posState.pulse = p;
    eonSoundOnPulse(p);
    if (owner) _posState.overview = third; else _posState.selfPerf = third;
    _posState.mine = mine;
    _posState.loaded = true;
  } catch (e) {
    if (surface) surface.innerHTML = `<div class="pos-empty"><div class="pos-empty__i">!</div>${esc(e.message || e)}</div>`;
    return;
  }
  _posRenderTabs();
  _posRenderRibbon();
  _posRenderActive();
}

function _posRenderRibbon() {
  // UI-wave 2026-06-11: the old ribbon was a pipe-separated sentence that
  // jumbled on mobile. Now an Intelligence-style stat strip — aligned cells,
  // mono number over uppercase label, wraps as a clean grid at 390px. Sound
  // hook: a growing verify queue (owner) pings the verify cue.
  const el = document.getElementById('pos-ribbon');
  if (!el) return;
  const p = _posState.pulse || {};
  // Cells with a destination render as buttons (WS-A): the old ribbon
  // showed "to verify · 6" as a dead div — the count you most need to act
  // on had no door. A tab target makes the number the door.
  const cell = (v, l, tone, tab) =>
    tab
      ? `<button class="pos-sbar__c pos-sbar__c--tap ${tone ? 'pos-sbar__c--' + tone : ''}" data-goto="${tab}"><span class="pos-sbar__v pos-mono">${v}</span><span class="pos-sbar__l">${l}</span></button>`
      : `<div class="pos-sbar__c ${tone ? 'pos-sbar__c--' + tone : ''}"><span class="pos-sbar__v pos-mono">${v}</span><span class="pos-sbar__l">${l}</span></div>`;
  if (_posState.role === 'owner') {
    const ov = _posState.overview || {};
    const pulse = ov.pulse || {};
    const open = (_posState.tasks || []).filter(t => POS_OPEN_ST.includes(t.status)).length;
    const overdue = (_posState.tasks || []).filter(posIsOverdue).length;
    // "to verify" counts the board's completed/done rows — the same set the
    // Execution tab shows and tapping reveals (the old p.rating_queue was a
    // second, server-side tally that could disagree with what you saw).
    const toVerify = (_posState.tasks || []).filter(t => t.status === 'completed' || t.status === 'done').length;
    const onshift = pulse.on_shift || {};
    el.innerHTML = `<div class="pos-sbar">${[
      (onshift.count != null
        ? cell(onshift.count, 'on shift')
        : cell((pulse.staff_active != null ? pulse.staff_active : (pulse.headcount || (ov.leaderboard || []).length || 0)), 'on the roster')),
      cell(open, 'open tasks', '', 'tasks'),
      cell(toVerify, 'to verify', toVerify > 0 ? 'warn' : '', 'tasks'),
      cell(overdue, 'overdue', overdue > 0 ? 'danger' : '', 'tasks'),
      cell(pulse.week_points ?? p.team_week_points ?? '—', 'pts · last 7d', 'ok'),
    ].join('')}</div>`;
    el.querySelectorAll('[data-goto]').forEach(b =>
      b.addEventListener('click', () => posSetTab(b.getAttribute('data-goto'))));
  } else {
    const d = _posState.selfPerf || {};
    const ring = d.ring || {}, st = d.streak || {}, lvl = d.level || {};
    el.innerHTML = `<div class="pos-sbar">${[
      cell(ring.points || 0, `points · lvl ${lvl.level || 1}`),
      cell((st.streak_days || 0) + 'd', 'streak', 'ok'),
      cell(p.my_open ?? '—', 'your open tasks'),
      cell(d.rank ? '#' + d.rank : '—', 'on the team'),
    ].join('')}</div>`;
  }
}

// ── Department pulse dashboards (2026-07-06) ───────────────────────
// Dropdown next to Org; each department renders the SAME uniform shape
// from /api/people/dept/{slug} (kpis + lists). Logistics is the detailed
// flagship (delivery speed / expense / tracking speed); others surface
// their existing computed signals. Additive — no other People OS code
// was touched.
const _POS_DEPTS = [
  ['logistics', 'Logistics'],
  ['customer_sales_care', 'Customer Care'],
  ['inventory', 'Inventory'],
  ['marketing', 'Marketing'],
  ['retail', 'Retail Stores'],
];
const _posDeptState = { days: 28, cache: {} };

function _posDeptDropdown(T) {
  const active = T && T.startsWith('dept:');
  const cur = active ? (_POS_DEPTS.find(d => d[0] === T.slice(5)) || [])[1] : '';
  return `<div class="pos-deptdd" id="pos-deptdd">
    <button class="pos-tab ${active ? 'on' : ''}" id="pos-deptdd-btn">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 21V8l7-5 7 5v13"/><path d="M13 21v-6h-4v6"/><path d="M21 21V11l-4-3"/></svg>
      ${cur ? esc(cur) : 'Departments'}<span class="pos-deptdd__car">▾</span></button>
    <div class="pos-deptdd__menu" id="pos-deptdd-menu">
      ${_POS_DEPTS.map(([slug, name]) =>
        `<button data-dept="${slug}" class="${T === 'dept:' + slug ? 'on' : ''}">${esc(name)}</button>`).join('')}
    </div>
  </div>`;
}

function _posBindDeptDropdown(bar) {
  const dd = document.getElementById('pos-deptdd');
  if (!dd) return;
  const menu = document.getElementById('pos-deptdd-menu');
  document.getElementById('pos-deptdd-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    menu?.classList.toggle('open');
  });
  menu?.querySelectorAll('[data-dept]').forEach(b => b.addEventListener('click', () => {
    menu.classList.remove('open');
    posSetTab('dept:' + b.getAttribute('data-dept'));
  }));
  // Persistent outside-click close. The old { once: true } listener was
  // consumed by the FIRST outside click after binding — every later open
  // left the menu stuck over the content (felt-polish).
  if (!State._posDdClose) {
    State._posDdClose = true;
    document.addEventListener('click', (e) => {
      const m = document.getElementById('pos-deptdd-menu');
      if (m && m.classList.contains('open') && !e.target.closest('#pos-deptdd')) {
        m.classList.remove('open');
      }
    });
  }
}

function _posDeptSkel() {
  return `<div style="padding:24px"><div class="pos-skel" style="height:96px;margin-bottom:14px"></div><div class="pos-skel" style="height:280px"></div></div>`;
}

async function _posLoadDept(slug) {
  const surface = document.getElementById('pos-surface');
  let d = _posDeptState.cache[slug + ':' + _posDeptState.days];
  if (!d) {
    try {
      d = await api(`/api/people/dept/${encodeURIComponent(slug)}?days=${_posDeptState.days}`);
      _posDeptState.cache[slug + ':' + _posDeptState.days] = d;
    } catch (e) {
      if (surface) surface.innerHTML = `<div class="pos-empty" style="padding:40px">${esc(e.message || e)}</div>`;
      return;
    }
  }
  if (!surface || _posState.tab !== 'dept:' + slug) return;
  if (!d.ok) {
    surface.innerHTML = `<div class="pos-empty" style="padding:40px">${esc(d.error || 'unavailable')}</div>`;
    return;
  }
  const kpis = (d.kpis || []).map(k =>
    `<div class="pos-dk ${k.alert ? 'pos-dk--alert' : ''}">
      <div class="pos-dk__v">${esc(String(k.value ?? '—'))}</div>
      <div class="pos-dk__l">${esc(k.label)}</div>
      ${k.sub ? `<div class="pos-dk__s">${esc(k.sub)}</div>` : ''}
    </div>`).join('');
  const lists = (d.lists || []).map(l =>
    `<div class="pos-dlist">
      <div class="pos-dlist__t">${esc(l.title)}</div>
      ${(l.rows || []).map(r =>
        `<div class="pos-dlist__r"><span>${esc(r.label || '')}</span>
         <span class="pos-dlist__v">${esc(String(r.value ?? ''))}${r.sub ? ` <em>${esc(r.sub)}</em>` : ''}</span></div>`).join('')
        || '<div class="pos-dlist__r"><span>Nothing yet</span></div>'}
    </div>`).join('');
  surface.innerHTML = `<div class="pos-dept">
    <div class="pos-dept__head">
      <h2>${esc(d.name || slug)}</h2>
      <div class="pos-dept__win" id="pos-dept-win">
        ${[7, 28, 90].map(n => `<button data-days="${n}" class="${_posDeptState.days === n ? 'on' : ''}">${n}d</button>`).join('')}
      </div>
    </div>
    <div class="pos-dept__kpis">${kpis || '<div class="pos-empty">No data yet.</div>'}</div>
    <div class="pos-dept__lists">${lists}</div>
  </div>`;
  document.querySelectorAll('#pos-dept-win [data-days]').forEach(b => b.addEventListener('click', () => {
    _posDeptState.days = parseInt(b.getAttribute('data-days'), 10) || 28;
    surface.innerHTML = _posDeptSkel();
    _posLoadDept(slug);
  }));
  requestAnimationFrame(() => eonAnimNums(surface));
}

function _posRenderActive() {
  const surface = document.getElementById('pos-surface');
  if (!surface) return;
  const T = _posState.tab;
  _posAboutPanel(T);
  if (cg.renderPeopleTab(T, surface)) return;
  // Quiet-repaint discipline (felt-polish 2026-07-26): re-rendering the SAME
  // tab (refresh, task action, role data landing) must not feel like a page
  // load — keep the scroll position, skip the panel entrance animations and
  // don't replay the count-ups. A real tab CHANGE keeps the full entrance.
  const still = _posState._lastPaintTab === T;
  const sy = window.scrollY, sx = surface.scrollTop;
  surface.classList.toggle('pos-surface--still', still);
  if (T === 'overview') {
    surface.innerHTML = _posState.role === 'owner' ? _posOwnerOverview() : _posStaffOverview();
    _posBindOverview();
  } else if (T === 'tasks') {
    surface.innerHTML = _posExec();
    _posBindExec();
  } else if (T === 'goals') {
    surface.innerHTML = _posGoals();
    _posBindGoals();
  } else if (T === 'reps') {
    surface.innerHTML = _posReps();
    _posBindReps();
  } else if (T === 'team') {
    surface.innerHTML = _posTeam();
    _posBindTeam();
    // A stored failure reads again on the next visit or ↻ Refresh: the failed
    // read leaves teamRoster = [], so the first check alone never retried it.
    if (!_posState.teamRoster || _posState.teamRosterError) _posTeamLoad();
    if (_posState.leaveBoard === undefined) _posTimeoffLoad();
    if (_posState.attDay === undefined) _posAttendanceLoad();
    if (_posState.rota === undefined) _posRotaLoad();
  } else if (T === 'floor') {
    surface.innerHTML = _posFloor();
    _posBindFloor();
    if (!_posState.floorBoard) _posFloorLoad();
  } else if (T === 'pay') {
    surface.innerHTML = _posPay();
    _posBindPay();
    if (_posState.payRuns === undefined) _posPayLoad();
  } else if (T === 'guide') {
    surface.innerHTML = _posGuide();
    _posBindGuide();
  } else if (T === 'reviews') {
    surface.innerHTML = _posCoaching();
    _posBindCoaching();
    if (!_posState.coachBoard) _posCoachLoad();
  } else if (T === 'org') {
    surface.innerHTML = _posOrg();
    if (!_posState.teamRoster || _posState.teamRosterError) _posTeamLoad();
  } else if (T && T.startsWith('dept:')) {
    surface.innerHTML = _posDeptSkel();
    _posLoadDept(T.slice(5));
  } else {
    surface.innerHTML = _posSoon(T);
  }
  _posState._lastPaintTab = T;
  if (still) {
    requestAnimationFrame(() => { window.scrollTo(0, sy); surface.scrollTop = sx; });
  } else {
    requestAnimationFrame(() => eonAnimNums(surface));
  }
}

// ── Reps (relocated from Marketing 2026-06-11) ───────────────
// Sales-rep performance from Shopify-attributed orders (user_id, now
// REST-backfilled). Lives in People OS — it's a people/performance surface,
// not a marketing one. Reuses the /api/marketing/reps* endpoints unchanged.
const POS_REP_DAYS = [7, 30, 90];

function _posReps() {
  const d = _posState.repDays || 30;
  return `
    <div class="pos-panel">
      <div class="pos-panel__h">
        <span class="pos-panel__t">Sales reps · Shopify-attributed</span>
        <span class="pos-repdays">${POS_REP_DAYS.map(n =>
          `<button class="pos-repday ${n === d ? 'on' : ''}" data-repday="${n}" aria-pressed="${n === d}">${n}d</button>`).join('')}</span>
      </div>
      <div class="pos-pulse">
        <div class="pos-pulse__c"><span class="pos-pulse__l">Revenue · all reps</span><span class="pos-pulse__v" id="pos-rep-rev" style="font-size:22px">—</span><span class="pos-pulse__d flat" id="pos-rep-rev-sub">loading…</span></div>
        <div class="pos-pulse__c"><span class="pos-pulse__l">Top rep</span><span class="pos-pulse__v" id="pos-rep-top" style="font-size:18px">—</span><span class="pos-pulse__d flat" id="pos-rep-top-sub">—</span></div>
        <div class="pos-pulse__c"><span class="pos-pulse__l">Unmapped IDs</span><span class="pos-pulse__v" id="pos-rep-unmapped">—</span><span class="pos-pulse__d flat">tap Map to attach</span></div>
      </div>
      <div class="pos-lb" id="pos-rep-lb"><div class="pos-empty" style="padding:30px"><div class="pos-empty__i">○</div>Loading reps…</div></div>
    </div>`;
}

function _posBindReps() {
  const surface = document.getElementById('pos-surface');
  surface?.querySelectorAll('[data-repday]').forEach(b =>
    b.addEventListener('click', () => {
      _posState.repDays = +b.getAttribute('data-repday');
      _posRenderActive();
    }));
  _posLoadReps();
}

async function _posLoadReps() {
  const days = _posState.repDays || 30;
  // Sequence guard: rapid 7d/30d/90d taps overlap; a stale (slower) response
  // must not overwrite the pulse tiles of the newest selection.
  const seq = (_posState.repSeq = (_posState.repSeq || 0) + 1);
  const lb = document.getElementById('pos-rep-lb');
  const set = (id, v) => { const e = document.getElementById(id); if (e) e.textContent = v; };
  let r;
  try { r = await api(`/api/marketing/reps?days_back=${days}`); }
  catch (e) { if (seq === _posState.repSeq && lb) lb.innerHTML = `<div class="pos-empty">${esc(e.message || e)}</div>`; return; }
  if (seq !== _posState.repSeq) return;
  if (!r || !r.ok) { if (lb) lb.innerHTML = `<div class="pos-empty">Couldn't load reps.</div>`; return; }
  const reps = r.reps || [];
  const totalRev = reps.reduce((s, x) => s + (x.revenue_ngn || 0), 0);
  set('pos-rep-rev', posNgn(totalRev) || '₦0');
  set('pos-rep-rev-sub', `${reps.length} active · ${days}d`);
  if (reps[0]) {
    set('pos-rep-top', reps[0].display_name || '—');
    set('pos-rep-top-sub', `${posNgn(reps[0].revenue_ngn) || '₦0'} · ${reps[0].orders_count} orders`);
  }
  set('pos-rep-unmapped', String(r.unmapped_count || 0));
  const maxRev = Math.max(1, ...reps.map(x => x.revenue_ngn || 0));
  // Map needs MEMBERS_CHANGE_ROLE server-side (admin/owner) — rendering the
  // button for staff/managers guarantees a 403 on tap, so role-gate it here.
  const canMap = ['admin', 'owner'].includes(State.member?.role || '');
  // UI-wave 2026-06-11: stacked rep card replaces the 5-column squeeze that
  // wrapped character-by-character at 390px. Line 1 = rank · avatar · name ·
  // revenue (right-aligned, prominent). Line 2 = structured meta chips.
  // Line 3 = full-width revenue-share bar.
  if (lb) lb.innerHTML = reps.length ? reps.map((rep, i) => {
    const unmapped = rep.mapped ? '' : ' <span class="pos-rep-unmap">unmapped</span>';
    const mapBtn = (!rep.mapped && canMap)
      ? `<button class="pos-repmap" data-mapid="${esc(rep.shopify_user_id)}">Map</button>`
      : '';
    const chips = [
      rep.location ? esc(rep.location) : null,
      esc(rep.role || 'rep'),
      `${rep.orders_count} orders`,
      `AOV ${posNgn(rep.aov_ngn) || '₦0'}`,
      (rep.capture_pct != null && (rep.pos_orders || 0) > 0) ? `${rep.capture_pct}% attach` : null,
    ].filter(Boolean).map(c => `<span class="pos-rep2__chip">${c}</span>`).join('');
    return `<div class="pos-rep2 ${i === 0 ? 'pos-rep2--lead' : ''}">
      <div class="pos-rep2__top">
        <span class="pos-rep2__rank pos-mono">${i + 1}</span>
        ${posAvatar(rep.display_name, 'sm')}
        <span class="pos-rep2__name">${esc(rep.display_name)}${unmapped}</span>
        <span class="pos-rep2__rev pos-mono">${posNgn(rep.revenue_ngn) || '₦0'}</span>
        ${mapBtn}
      </div>
      <div class="pos-rep2__chips">${chips}</div>
      <div class="pos-rep2__bar"><i style="width:${Math.round((rep.revenue_ngn || 0) / maxRev * 100)}%"></i></div>
    </div>`;
  }).join('') : `<div class="pos-empty" style="padding:30px"><div class="pos-empty__i">○</div>No rep-attributed orders in ${days}d yet.</div>`;
  lb?.querySelectorAll('[data-mapid]').forEach(b =>
    b.addEventListener('click', (e) => { e.stopPropagation(); _posOpenRepMap(b.getAttribute('data-mapid')); }));
}

// A11: the problem a Buyers tile named, above the reps list (either
// renderer: Common Ground's sales read or _posReps). It stands outside
// #pos-surface, so both lists below stay as they are, and only while the
// address still says about=unnamed_sales — a view change drops the key, the
// close control drops it here. Counts and naira only, never a buyer.
function _posAboutPanel(T) {
  const surface = document.getElementById('pos-surface');
  const old = document.getElementById('pos-about');
  let on = false;
  try {
    on = T === 'reps' && _posState.about === 'unnamed_sales' && posIsLead()
      && new URLSearchParams(location.search).get('about') === 'unnamed_sales';
  } catch (_) {}
  const days = _posState.aboutDays || 30, store = _posState.aboutStore || '';
  const key = on ? `${days}|${store}` : '';
  if (old && old.dataset.key === key) {  // a quiet repaint keeps it; ↻ reads it again
    if (old.dataset.reread) { delete old.dataset.reread; _posAboutRead(old, days, store); }
    return;
  }
  old?.remove();
  if (!on || !surface) return;
  const el = document.createElement('section');
  el.id = 'pos-about';
  el.dataset.key = key;
  el.setAttribute('aria-label', 'Walk-in sales with no buyer name');
  const reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Collapsed until the read lands; then the list below slides down once.
  el.style.cssText = 'display:grid;grid-template-rows:0fr;flex:none;max-width:1440px;width:100%;margin:0 auto;box-sizing:border-box;padding:0 clamp(20px,3.4vw,48px)'
    + (reduce ? '' : ';transition:grid-template-rows .26s ease');
  surface.before(el);
  _posAboutRead(el, days, store);
}

function _posAboutRead(el, days, store) {
  const seq = el.dataset.seq = String(+(el.dataset.seq || 0) + 1);
  const q = `/api/people/attach/unnamed?days=${days}` + (store ? `&store=${encodeURIComponent(store)}` : '');
  let scoped = false;
  try { scoped = posScopeLimited(); } catch (_) {}
  (scoped ? Promise.resolve({scoped: true}) : api(q).then(d => d, () => null)).then(d => {
    if (!el.isConnected || el.dataset.seq !== seq) return;
    el.innerHTML = `<div style="overflow:hidden;min-height:0"><div class="pos-panel" style="animation:none;margin-top:20px;border-color:var(--accent)">${_posAboutBody(d, days)}</div></div>`;
    el.querySelector('[data-about-close]')?.addEventListener('click', () => {
      try {
        const qs = new URLSearchParams(location.search);
        qs.delete('about');
        history.replaceState(history.state, '', location.pathname + (qs.toString() ? `?${qs}` : ''));
      } catch (_) {}
      _posState.about = null;
      el.remove();
    });
    void el.offsetHeight;  // the collapsed height is the slide's start
    el.style.gridTemplateRows = '1fr';
  });
}

function _posAboutBody(d, days) {
  const ok = d && d.ok === true && Number.isSafeInteger(d.unnamed) && Number.isSafeInteger(d.orders)
    && Array.isArray(d.reps);
  const at = ok && d.store && d.store.name ? `, at ${esc(d.store.name)}` : '';
  const span = days === 1 ? 'the last day' : `the last ${days} days`;
  const head = `<div class="pos-panel__h"><span class="pos-panel__t" style="color:var(--accent)">Walk-in sales with no buyer name — last ${days} day${days === 1 ? '' : 's'}${at}</span>
    <button type="button" class="pos-repday" data-about-close>Close</button></div>`;
  if (d && d.scoped) return `${head}<div class="pos-flag__m" role="status" data-about-scoped>This count covers every shop, and your access is limited to some of them, so it is not shown here.</div>`;
  if (!ok) return `${head}<div class="pos-flag__m" role="alert">These sales could not be read. Reload to try again.</div>`;
  if (d.store_unlinked) return `${head}<div class="pos-flag__m" role="status" data-about-unlinked>That shop is not linked to its Shopify till yet, so its sales cannot be counted here.</div>`;
  const refused = d.store_refused ? '<div class="pos-flag__m" role="status" data-about-refused>That shop is not one of yours.</div>' : '';
  if (!d.unnamed) {
    return `${head}${refused}<div class="pos-flag__m" role="status">${d.orders ? `Every walk-in sale in ${span} had a buyer name or phone.` : `No walk-in sales in ${span}.`}</div>`;
  }
  const reps = d.reps.map(r => `<div data-about-rep style="display:flex;justify-content:space-between;gap:12px;padding:6px 0;border-top:1px solid var(--line-soft)">
      <span>${esc(r.rep)}</span><span class="pos-mono">${esc(r.unnamed)} of ${esc(r.orders)} · ${esc(r.share_pct)}% of their walk-in sales</span></div>`).join('');
  return `${head}${refused}
    <div data-about-total style="margin:0 0 12px"><b class="pos-mono">${d.unnamed}</b> of ${d.orders} walk-in sales had no buyer name or phone · <b class="pos-mono">${posNgn(d.unnamed_ngn) || '₦0'}</b></div>
    ${reps}`;
}

function _posOpenRepMap(shopifyId) {
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Map Shopify ID → staff</div><button class="csw-x">✕</button></div>
    <div class="csw-goalmeta" style="margin-bottom:10px">Attaching Shopify ID ${esc(shopifyId)} to a staff member so their sales roll up under their name.</div>
    <select class="csw-search" id="pos-rep-staff" style="margin-bottom:10px"><option value="">Loading staff…</option></select>
    <button class="csw-confirm" id="pos-rep-save">Save mapping</button>
    <div id="pos-rep-maperr" class="csw-goalmeta" style="color:var(--danger);display:none;margin-top:8px"></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const sel = ov.querySelector('#pos-rep-staff');
  api('/api/marketing/reps/staff-candidates').then(r => {
    const staff = (r && r.staff) || [];
    sel.innerHTML = `<option value="">— Choose —</option>` + staff.map(s =>
      `<option value="${esc(s.id)}">${esc(s.full_name)}${s.shopify_user_id ? ` (till number ${esc(s.shopify_user_id)})` : ''}</option>`).join('');
  }).catch(() => { sel.innerHTML = `<option value="">Failed to load staff</option>`; });
  ov.querySelector('#pos-rep-save').addEventListener('click', async () => {
    const sid = sel.value; const err = ov.querySelector('#pos-rep-maperr');
    if (!sid) { err.textContent = 'Pick a staff member first.'; err.style.display = 'block'; return; }
    try {
      await api('/api/marketing/reps/map', { method: 'POST', body: JSON.stringify({ staff_id: sid, shopify_user_id: shopifyId }) });
      ov.remove(); _posLoadReps();
      document.dispatchEvent(new CustomEvent('eon:rep-mapping-changed'));
    } catch (e) { err.textContent = 'Map failed: ' + (e.message || e); err.style.display = 'block'; }
  });
}

function _posSoon(t) {
  const copy = {
    reviews: ['Performance reviews', 'Lightweight 360s + manager check-ins, scored against the same points engine that already runs your team.'],
    org: ['Org chart', 'The live people graph — departments, reporting lines, on-shift presence — rendered from the one roster.'],
  }[t] || ['Coming soon', ''];
  return `<div class="pos-soon"><div class="pos-soon__i">◷</div><div class="pos-soon__t">${esc(copy[0])}</div><div class="pos-soon__s">${esc(copy[1])}</div></div>`;
}


/* ──────────── PEOPLE OS · GUIDE (team handbook) ────────────
 * Plain-English handbook for the whole People lane: People OS (this page),
 * Staff OS (each person's app) and the HR dashboard. Guides refresh
 * 2026-07-12. KEEP IN SYNC: new tab / renamed button ⇒ update here
 * (tests pin the tab names into this guide). Styles: guide_kit.css
 * (.mkg set + .mkg--shell bridge — the / shell has no marketing.css). */
function _posGuide() {
  return `
  <div class="mkg mkg--shell">
    <div class="mkg-hero">
      <div class="mkg-kicker">People OS · Team Handbook · updated 12 July 2026</div>
      <h1>How our team system works </h1>
      <p>One system watches the work (tasks), turns finished work into <b>points</b>, and turns
      points into <b>real bonus money</b>. This page explains every screen and every button in
      plain English — for staff and for managers. Use the search box to jump to a word.
      Numbers in the screenshots are examples, not live data.</p>
    </div>

    <nav class="mkg-toc">
      <a data-jump="pg-big">The big idea</a>
      <a data-jump="pg-map">The three screens</a>
      <a data-jump="pg-tabs">The tabs</a>
      <a data-jump="pg-overview">Overview</a>
      <a data-jump="pg-exec">Verify</a>
      <a data-jump="pg-task">A task's life</a>
      <a data-jump="pg-eonq">⊕ EON's task queue</a>
      <a data-jump="pg-reps">Reps</a>
      <a data-jump="pg-goals">Goals</a>
      <a data-jump="pg-depts">Departments</a>
      <a data-jump="pg-staffos">Staff OS</a>
      <a data-jump="pg-hr">HR dashboard</a>
      <a data-jump="pg-soon">Built, landing soon</a>
      <a data-jump="pg-privacy">Who sees what</a>
      <a data-jump="pg-words">Words</a>
      <a data-jump="pg-cheat">Cheat sheet</a>
    </nav>

    <section class="mkg-sec" id="pg-big">
      <h2> The one big idea (read this first)</h2>
      <div class="mkg-tldr"><b>Work → points → money.</b> Every task you finish and a manager
      verifies earns points. Points fill your day ring, build your streak, and decide your share
      of the monthly bonus pool. Nothing is hidden: your Overview shows the exact receipt of what
      earned what.</div>
      <p><b>EON proposes; people decide.</b> EON writes tasks, routes them to the right person and
      nudges when things go stale — but a human approves its queue, a human verifies finished
      work, and only the owner closes a bonus run. The machine keeps score; people keep judgement.</p>
      <div class="mkg-note"> <b>Cohesion rule:</b> staff never see a ranking of each other.
      You see <i>your</i> numbers and the team's shared goal. Leaderboards live on manager
      screens only — we compete with the target, not with each other.</div>
    </section>

    <section class="mkg-sec" id="pg-map">
      <h2> One engine, three screens</h2>
      <p class="mkg-lead">The People lane is one data engine wearing three faces. Pick the door
      that matches your job today:</p>
      <div class="mkg-diagram">                ┌───────────────────────────────┐
                │      ONE PEOPLE ENGINE        │
                │  roster · tasks · points · ₦  │
                └──────┬───────┬───────┬────────┘
                       │       │       │
        PEOPLE OS      │   STAFF OS    │   HR DASHBOARD
        (this page)    │   (/staff)    │   (?v=hr)
        the manager's  │   every       │   pay, fairness,
        team cockpit — │   staffer's   │   friction, bonus
        pulse, tasks,  │   own app —   │   runs — the
        goals, depts   │   check-in,   │   owner's money
                       │   day plan,   │   view
                       │   payslip     │</div>
      <div class="mkg-grid">
        <div class="mkg-card"><h4>People OS</h4><p>You are here. Managers run the team from it; staff get a personal view of their own day.</p></div>
        <div class="mkg-card"><h4>Staff OS</h4><p>The phone-shaped app each person works from on the floor. Managers can view it <i>as</i> any teammate.</p></div>
        <div class="mkg-card"><h4>HR dashboard</h4><p>Pay equity, bonus pool, friction between departments. Owner and managers only.</p></div>
      </div>
    </section>

    <section class="mkg-sec" id="pg-tabs">
      <h2> The tabs, one line each</h2>
      <div class="mkg-grid">
        <div class="mkg-card"><h4>Overview</h4><p>The pulse. Managers see the whole team; staff see their own ring, receipt and tasks.</p></div>
        <div class="mkg-card"><h4>Verify</h4><p>Finished work waits here — one tap pays it (★4), or open it for a closer look. Below the queue: points trend, quality stars, overdue aging. The number badge = open tasks across the team.</p></div>
        <div class="mkg-card"><h4>Reps</h4><p>Sales-rep leaderboard built from real Shopify orders — who closed what, per store.</p></div>
        <div class="mkg-card"><h4>Coaching</h4><p>The month's one-to-ones: who has talked, who is waiting, and each person's pack — their own numbers pre-digested so the minutes go to the conversation. One person per pack; it compares nobody.</p></div>
        <div class="mkg-card"><h4>Goals</h4><p>Company, department and personal goals. Some track themselves from live data.</p></div>
        <div class="mkg-card"><h4>Org <i>(soon)</i></h4><p>The live org chart, drawn from the roster.</p></div>
        <div class="mkg-card"><h4>Departments ▾</h4><p>(Managers) Per-department dashboards: Logistics, Customer Care, Inventory, Marketing, Retail.</p></div>
        <div class="mkg-card"><h4>Guide</h4><p>This page.</p></div>
      </div>
      <div class="mkg-note"> <b>Header buttons:</b> <b>Team / Me</b> switches between the whole-team
      view and your personal view (managers only — staff always see Me). <b>⊕</b> opens EON's task
      queue. <b>↻</b> refreshes. <b>Request finance</b> (if your role can) opens a money request
      that goes to an approver — nothing is spent until they say yes.</div>
    </section>

    <section class="mkg-sec" id="pg-overview">
      <h2> Overview — the pulse</h2>
      <figure class="gk-shot"><img loading="lazy" width="1440" height="900" src="/static/guide_shots/people_overview.webp" alt="People OS Overview tab"><figcaption>Overview (manager view): pulse tiles, leaderboard, approvals and the bonus pool.</figcaption></figure>
      <h3>If you're a manager (Team view)</h3>
      <div class="mkg-grid">
        <div class="mkg-card"><h4>Team pulse</h4><p>Four tiles: points this week, verified tasks, average quality stars, and the money value of work at stake.</p></div>
        <div class="mkg-card"><h4>Leaderboard</h4><p>Who's earning. Tap a row to open that person's full profile (points history, tasks, pay context).</p></div>
        <div class="mkg-card"><h4>Approvals</h4><p>Time-off and shift-swap requests wait here. <b>Approve</b> writes the roster instantly; <b>Decline</b> asks you for a reason the person will see. A  on a request means coverage will be thin that day — approving asks you to own that call.</p></div>
        <div class="mkg-card"><h4>Bonus pool</h4><p>The month's pool and how it's tracking. The ring next to it is pay equity — amber means someone sits below the fair band.</p></div>
        <div class="mkg-card"><h4>Integrity &amp; retention</h4><p>Quiet flags: someone's pattern looks like flight risk, or a points pattern looks gamed. Handled privately, never posted publicly.</p></div>
      </div>
      <h3>If you're staff (Me view)</h3>
      <div class="mkg-grid">
        <div class="mkg-card"><h4>Your day ring</h4><p>Today's points vs your target, your level and streak. Fill the ring, keep the streak.</p></div>
        <div class="mkg-card"><h4>Points receipt</h4><p>Exactly which actions earned points this week — no mystery maths.</p></div>
        <div class="mkg-card"><h4>Your bonus share</h4><p>What your points are currently worth in naira if the month ended today.</p></div>
        <div class="mkg-card"><h4>Your tasks</h4><p>Up to six next things. Tap one to open it; <b>Open board →</b> shows everything.</p></div>
      </div>
    </section>

    <section class="mkg-sec" id="pg-exec">
      <h2> Verify — finished work waits here</h2>
      <figure class="gk-shot"><img loading="lazy" width="1440" height="900" src="/static/guide_shots/people_tasks.webp" alt="Verify tab"><figcaption>Verify: the queue of finished work first, then points trend, quality stars, department throughput, overdue aging.</figcaption></figure>
      <p class="mkg-lead">No buttons that change anything here — this tab is a mirror. It answers:
      is work moving, is it good, and where is it stuck?</p>
      <div class="mkg-grid">
        <div class="mkg-card"><h4>7d / 30d</h4><p>The window every panel reads. Seven days for "this week", thirty for the month's shape.</p></div>
        <div class="mkg-card"><h4>Quality stars</h4><p>When managers verify work they rate it 1–5. This panel shows the spread — and flags tasks closed without a rating.</p></div>
        <div class="mkg-card"><h4>Overdue aging</h4><p>Late tasks in buckets by how late. The oldest offenders are listed — tap one to open it and unstick it.</p></div>
        <div class="mkg-card"><h4>Open Task OS</h4><p>Jumps to the full task board (the complete Task OS page) when you need to work the list, not read about it.</p></div>
      </div>
    </section>

    <section class="mkg-sec" id="pg-task">
      <h2> A task's life — from born to banked</h2>
      <p class="mkg-lead">Tap any task anywhere and the detail pane slides in: what it is, why EON
      routed it to this person ("why this / why now"), its deadline meter, the handoff chain, and
      the conversation. The buttons at the bottom follow the task's stage:</p>
      <div class="mkg-flow">
        <div class="mkg-step"><div class="mkg-num">1</div><b>Got it</b><p>"I've seen it." The clock for response time stops blaming you.</p></div>
        <div class="mkg-step"><div class="mkg-num">2</div><b>Start</b><p>You're on it now.</p></div>
        <div class="mkg-step"><div class="mkg-num">3</div><b>Mark complete</b><p>Done, waiting for a manager's eyes.</p></div>
        <div class="mkg-step"><div class="mkg-num">4</div><b>Verify &amp; rate </b><p>A manager confirms it's really done and rates 1–5. THIS is the moment points are banked. You can never verify your own task.</p></div>
      </div>
      <div class="mkg-grid">
        <div class="mkg-card"><h4>Block / Resolve</h4><p>Stuck on something outside your control? <b>Block</b> says so honestly (with a reason). <b>Resolve &amp; resume</b> when it clears. Blocked time doesn't rot your stats silently.</p></div>
        <div class="mkg-card"><h4>Send back</h4><p>A manager can return completed work for another pass — with a note, not a penalty.</p></div>
        <div class="mkg-card"><h4>Re-assign</h4><p>(Managers) Move the task to someone eligible — the picker only offers people who can actually do it.</p></div>
        <div class="mkg-card"><h4>Ask EON</h4><p>Ask the task itself a question ("where's the vendor sheet for this?"). A few questions a day per person — the counter shows what's left.</p></div>
      </div>
    </section>

    <section class="mkg-sec" id="pg-eonq">
      <h2>⊕ EON's task queue — approve the robot's homework</h2>
      <p class="mkg-lead">Every morning EON drafts tasks from what the business needs (low stock →
      a count task; a spike of DMs → a care task). They wait in the queue — the <b>⊕</b> button in
      the header — until a person approves them.</p>
      <div class="mkg-grid">
        <div class="mkg-card"><h4>Approve &amp; route</h4><p>The task goes live and lands with the right person.</p></div>
        <div class="mkg-card"><h4>Retry route</h4><p>For orphans — tasks EON couldn't match to a person. It tries again with fresh roster data.</p></div>
        <div class="mkg-card"><h4>New task</h4><p>Type a title and either <b>Create + let EON route</b> it, or <b>Run EON auto-allocate now</b> to have it draft the day's whole batch.</p></div>
      </div>
    </section>

    <section class="mkg-sec" id="pg-reps">
      <h2> Reps — who's selling</h2>
      <figure class="gk-shot"><img loading="lazy" width="1440" height="900" src="/static/guide_shots/people_reps.webp" alt="Reps tab"><figcaption>Reps: revenue per rep from real Shopify orders. Names blurred here.</figcaption></figure>
      <p class="mkg-lead">Every store sale in Shopify carries the rep who rang it up. This tab adds
      them up — real revenue per person, per window (7 / 30 / 90 days).</p>
      <div class="mkg-note"> <b>See an "unmapped" ID?</b> That's sales credited to a Shopify
      login nobody has claimed. Tap <b>Map</b> and pick the teammate it belongs to — from then on
      their sales count to their name. Unmapped sales are invisible bonuses; map them.</div>
    </section>

    <section class="mkg-sec" id="pg-goals">
      <h2> Goals — the scoreboard we choose</h2>
      <figure class="gk-shot"><img loading="lazy" width="1440" height="900" src="/static/guide_shots/people_goals.webp" alt="Goals tab"><figcaption>Goals: company, department and personal — some update themselves.</figcaption></figure>
      <p class="mkg-lead">Three shelves: <b>Company</b>, <b>Departments</b>, <b>Personal</b>.
      Finished goals fold away under "closed".</p>
      <div class="mkg-grid">
        <div class="mkg-card"><h4>New goal</h4><p>(Managers) Title, target number + unit (₦ / % / points), the period, who owns it — and the metric.</p></div>
        <div class="mkg-card"><h4>Auto-live goals</h4><p>Pick a live metric (monthly revenue, MER, returning-customer rate, team points) and the goal updates ITSELF every day. No typing, no forgetting — it carries a "live" badge.</p></div>
        <div class="mkg-card"><h4>Manual goals</h4><p>Anything EON can't measure yet. Open the goal, type the current value, <b>Update</b>.</p></div>
        <div class="mkg-card"><h4>Closing</h4><p><b>Mark achieved</b> / <b>Mark missed</b> / <b>Archive</b> — honest endings only. Reactivate brings one back.</p></div>
      </div>
    </section>

    <section class="mkg-sec" id="pg-depts">
      <h2> Departments — five dashboards, one dropdown</h2>
      <figure class="gk-shot"><img loading="lazy" width="1440" height="900" src="/static/guide_shots/people_dept_retail.webp" alt="Departments view"><figcaption>A department dashboard (Retail): its own KPIs and lists, same layout for every department.</figcaption></figure>
      <p class="mkg-lead">(Managers) The <b>Departments ▾</b> dropdown opens a per-department
      dashboard — Logistics, Customer Care, Inventory, Marketing, Retail Stores. Same shape every
      time: KPI tiles, then the lists that explain them, with a 7 / 28 / 90-day window. Read-only —
      the fixing happens in each department's own OS.</p>
    </section>

    <section class="mkg-sec" id="pg-staffos">
      <h2> Staff OS — every person's own app</h2>
      <figure class="gk-shot"><img loading="lazy" width="430" height="505" src="/static/guide_shots/people_staffos.webp" alt="Staff OS"><figcaption>Staff OS: the phone-shaped daily app. Managers can view it as any teammate.</figcaption></figure>
      <p class="mkg-lead">Staff OS (<b>/staff</b>) is the app each teammate actually works from —
      phone-first, five tabs at the bottom: <b>Day · Chat · Tasks · Team (managers) · Score</b>.
      The server decides which cards a person sees, based on their job.</p>
      <h3>The Day screen's cards</h3>
      <div class="mkg-grid">
        <div class="mkg-card"><h4>Check in</h4><p>Tap when you arrive — the phone checks you're near the store and awards arrival points (the ladder shows the exact price). Too far away? It says so honestly. Stores without a location pin use presence mode instead — no GPS tracked, just "app open during shift".</p></div>
        <div class="mkg-card"><h4>Payslip</h4><p>Your REAL monthly statement — net pay and tax, computed from the actual payroll run (new July 2026). If something's off it says so in plain flags: "no payroll run yet", "salary ₦0 on record". Declare your yearly rent to HR and the tax relief shows up here.</p></div>
        <div class="mkg-card"><h4>My products</h4><p>(Retail) Your store's top sellers, bundles worth activating, dead stock worth marking down. Money actions (markdown, transfer) always ask "sure?" before they touch Shopify.</p></div>
        <div class="mkg-card"><h4>Send tracking</h4><p>(Logistics) Pick the carrier, enter the waybill, preview the message the customer gets, send.</p></div>
        <div class="mkg-card"><h4>Approvals</h4><p>(Managers/finance) Money requests from the team. <b>Press and HOLD to approve</b> — the button fills up for about a second and letting go early releases nothing; that hold IS the confirmation. Reject asks for a reason inline.</p></div>
        <div class="mkg-card"><h4>Coaching card</h4><p>EON's daily one-liner for you — what went well yesterday, one thing to try today.</p></div>
      </div>
      <div class="mkg-note"> <b>Managers: the role pill.</b> Tap the name pill at the top →
      pick anyone on the roster → you now see Staff OS exactly as they do. One app, every life —
      it's also how you answer "what does the app ask Chidi to do today?"</div>
    </section>

    <section class="mkg-sec" id="pg-hr">
      <h2> The HR dashboard — pay, fairness, friction</h2>
      <figure class="gk-shot"><img loading="lazy" width="1440" height="900" src="/static/guide_shots/people_hr.webp" alt="HR dashboard"><figcaption>HR: leaderboard, pay equity, friction, bonus pool. Names blurred here.</figcaption></figure>
      <p class="mkg-lead">Deep-link <b>/?v=hr</b> (managers+). Where the people system meets money.</p>
      <div class="mkg-grid">
        <div class="mkg-card"><h4>Award points</h4><p>Open a person → award (or claw back) points with a written reason and a confirm step. Every manual award is on the record.</p></div>
        <div class="mkg-card"><h4>Pay equity</h4><p>The strip goes amber when someone's pay sits below the fair band for their role — surfaced before it festers.</p></div>
        <div class="mkg-card"><h4>Friction</h4><p>Cross-department complaints ("dispatch keeps missing pickups"). Each one: <b>Make task</b> (turn it into accountable work) or <b>Resolve</b>.</p></div>
        <div class="mkg-card"><h4>Bonus run</h4><p><b>Draft bonus run</b> computes everyone's share from points. It's a DRAFT until the owner confirms — the ₦-moving step is always deliberate.</p></div>
      </div>
      <div class="mkg-tldr"> <b>Payroll rhythm (new July 2026):</b> from the 25th of each month
      EON automatically guarantees the month's draft salary run exists and hands the owner ONE
      approval task with the full picture — net pay, tax per authority, and any ₦0-salary rows
      named. EON never approves and never pays by itself.</div>
    </section>

    <section class="mkg-sec" id="pg-soon">
      <h2> Already built, landing on screen soon</h2>
      <p class="mkg-lead">Three engines are live in the background but don't have their screens
      yet — so you don't go hunting for buttons that aren't there:</p>
      <div class="mkg-grid">
        <div class="mkg-card"><h4>Monthly 1:1 check-ins</h4><p>EON schedules a monthly 1:1 for every person and writes the manager a private coaching pack. Opens under <b>Coaching</b> on the People page.</p></div>
        <div class="mkg-card"><h4>UK runway board</h4><p>The UK-launch people plan (visas, roles, dates) is tracked; its board lands with the runway work.</p></div>
        <div class="mkg-card"><h4>Benefits</h4><p>The benefits engine (HMO, 13th month) is modelled; the enrolment screen follows.</p></div>
      </div>
    </section>

    <section class="mkg-sec" id="pg-privacy">
      <h2> Who sees what</h2>
      <table class="mkg-cheat">
        <thead><tr><th>Thing</th><th>Staff</th><th>Managers</th><th>Owner</th></tr></thead>
        <tbody>
          <tr><td>Your own points, tasks, payslip</td><td class="mkg-ok"> yours only</td><td class="mkg-ok"></td><td class="mkg-ok"></td></tr>
          <tr><td>Team leaderboard &amp; other people's numbers</td><td class="mkg-no"> by design</td><td class="mkg-ok"></td><td class="mkg-ok"></td></tr>
          <tr><td>Salaries, pay equity, pool splits</td><td class="mkg-no"></td><td class="mkg-ok"></td><td class="mkg-ok"></td></tr>
          <tr><td>Award / claw back points, close a bonus run</td><td class="mkg-no"></td><td>award only</td><td class="mkg-ok"> money moves</td></tr>
          <tr><td>Where you are (GPS)</td><td colspan="3">Only the moment you tap Check in, only near-the-store yes/no. Shift mode tracks presence, never location.</td></tr>
        </tbody>
      </table>
    </section>

    <section class="mkg-sec" id="pg-words">
      <h2> Words (decoder ring)</h2>
      <table class="mkg-cheat">
        <thead><tr><th>Word</th><th>Means</th></tr></thead>
        <tbody>
          <tr><td><b>Points</b></td><td>The score for verified work. Points → bonus share.</td></tr>
          <tr><td><b>Verify</b></td><td>A manager confirms a task is truly done and rates it 1–5 . Points bank here.</td></tr>
          <tr><td><b>Day ring</b></td><td>Today's points vs your daily target. Fill it.</td></tr>
          <tr><td><b>Streak</b></td><td>Days in a row you hit the ring.</td></tr>
          <tr><td><b>Impact at stake</b></td><td>The naira value tied to open tasks (a delayed reorder holds money hostage).</td></tr>
          <tr><td><b>Blocked</b></td><td>Honestly stuck on something outside your control — declared, not hidden.</td></tr>
          <tr><td><b>Pool</b></td><td>The month's bonus money, split by points.</td></tr>
          <tr><td><b>Pay equity band</b></td><td>The fair-pay range for a role. Below it = amber flag.</td></tr>
          <tr><td><b>Friction</b></td><td>A logged complaint between departments — fixed with a task, not a group chat argument.</td></tr>
          <tr><td><b>Unmapped rep</b></td><td>Shopify sales credited to a login nobody claimed yet. Map it to a person.</td></tr>
          <tr><td><b>Auto-live goal</b></td><td>A goal wired to a real metric — it updates itself daily.</td></tr>
          <tr><td><b>EON queue</b></td><td>Tasks EON drafted that wait for human approval.</td></tr>
        </tbody>
      </table>
    </section>

    <section class="mkg-sec" id="pg-cheat">
      <h2> The 1-minute cheat sheet</h2>
      <div class="mkg-grid">
        <div class="mkg-card"><h4>Check in when you arrive</h4><p>Staff OS → Check in. Points for punctuality, honestly measured.</p></div>
        <div class="mkg-card"><h4>Say you have it, then start</h4><p>Seen it? Say so. The two taps that keep your response time clean.</p></div>
        <div class="mkg-card"><h4>Blocked ≠ lazy</h4><p>Hit Block with a reason the moment you're stuck. Declared blocks protect you; silent ones don't.</p></div>
        <div class="mkg-card"><h4>Managers: verify daily</h4><p>Unverified work is unpaid points. Rate honestly — the stars teach EON who to route to.</p></div>
        <div class="mkg-card"><h4>Clear the ⊕ queue each morning</h4><p>EON's drafted tasks only help once a human approves them.</p></div>
        <div class="mkg-card"><h4>Map every rep ID</h4><p>Unmapped Shopify sales are someone's missing credit.</p></div>
        <div class="mkg-card"><h4>Approvals same-day</h4><p>Time-off requests rot morale when they sit. Approve or decline with a reason.</p></div>
        <div class="mkg-card"><h4>Payslip looks wrong?</h4><p>Read its flags first — "no run yet" or "rent not declared" usually explains it. Then ask HR.</p></div>
      </div>
    </section>
  </div>`;
}

function _posBindGuide() {
  const surface = document.getElementById('pos-surface');
  if (!surface) return;
  const root = surface.querySelector('.mkg');
  const beh = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  surface.querySelectorAll('.mkg-toc a[data-jump]').forEach(a => {
    a.addEventListener('click', () => {
      const el = document.getElementById(a.dataset.jump);
      if (el) el.scrollIntoView({ behavior: beh, block: 'start' });
    });
  });
  if (window.GuideKit && root) window.GuideKit.boot(root);
}

/* ──────────── OVERVIEW · OWNER ──────────── */
function _posOwnerOverview() {
  const d = _posState.overview;
  if (!d || !d.ok) return `<div class="pos-empty"><div class="pos-empty__i">○</div>${esc((d && d.error) || 'No team data yet. Points accrue as staff close verified tasks.')}</div>`;
  const board = d.leaderboard || [];
  const pool = d.pool || {}, pg = d.pay_gap || {}, fr = (d.flags || {});
  const cg = d.company_goal || {};

  // ── team pulse (4 figures) ──
  const tasks = _posState.tasks || [];
  const done30 = tasks.filter(t => t.status === 'verified').length;
  // Impact = OPEN work only — the old sum included verified and blocked
  // tasks while its caption claimed "open work" (wave-2 truth).
  const impact = tasks.filter(t => POS_OPEN_ST.includes(t.status))
    .reduce((s, t) => s + (t.estimated_impact_ngn || 0), 0);
  const rated = tasks.filter(t => t.quality_rating);
  const avgStar = rated.length ? (rated.reduce((s, t) => s + (t.quality_rating || 0), 0) / rated.length) : 0;
  const weekPts = (d.pulse || {}).week_points ?? _posState.pulse?.team_week_points ?? '—';
  const pulse = `<div class="pos-pulse">
    <div class="pos-pulse__c"><span class="pos-pulse__l">Team points · last 7 days</span><span class="pos-pulse__v">${weekPts}</span><span class="pos-pulse__d flat">every earn lane · ${board.length} staff</span></div>
    <div class="pos-pulse__c"><span class="pos-pulse__l">Verified · last 30 days</span><span class="pos-pulse__v">${done30}</span><span class="pos-pulse__d ${done30 ? 'up' : 'flat'}">${done30 ? 'closed + rated' : '—'}</span></div>
    <div class="pos-pulse__c"><span class="pos-pulse__l">Avg ★ · last 30 days</span><span class="pos-pulse__v">${avgStar ? avgStar.toFixed(1) : '—'}</span><span class="pos-pulse__d flat">${rated.length} rated</span></div>
    <div class="pos-pulse__c"><span class="pos-pulse__l">Impact at stake</span><span class="pos-pulse__v" style="font-size:24px">${impact ? posNgn(impact) : '₦0'}</span><span class="pos-pulse__d ${impact ? 'up' : 'flat'}">${impact ? 'open work only' : '—'}</span></div>
  </div>`;

  // ── leaderboard ──
  const maxPts = Math.max(1, ...board.map(b => b.points || 0));
  const lb = `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Team leaderboard · last 7 days</span></div>
    <div class="pos-lb">${board.length ? board.map((p, i) => {
      const name = p.name || p.rep_id;
      const star = (p.quality_avg && p.quality_avg >= 4.5) || i === 0;
      return `<button class="pos-lb__row ${i === 0 ? 'pos-lb__row--lead' : ''}" data-email="${esc(p.rep_id || '')}" data-staff-id="${esc(p.staff_id)}">
        <span class="pos-lb__rank">${i + 1}</span>
        <span class="pos-lb__who">${posAvatar(name)}<span style="min-width:0"><span class="pos-lb__nm" style="display:block">${esc(name)}</span><span class="pos-lb__rl" style="display:block">${esc(p.role || p.department || '—')}</span></span></span>
        <span class="pos-lb__bar"><i style="width:${Math.round((p.points || 0) / maxPts * 100)}%"></i></span>
        <span class="pos-lb__pts">${p.points || 0}${star && i === 0 ? '<span class="pos-lb__star">★</span>' : ''}</span>
      </button>`;
    }).join('') : '<div class="pos-empty" style="padding:30px"><div class="pos-empty__i">○</div>No points in the last 7 days — most lanes pay the morning after the action.</div>'}</div></div>`;

  // ── department reliability ──
  const reliab = ((d.friction || {}).reliability || {}).departments || [];
  const deptStrip = reliab.length ? `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Department reliability · on-time × quality</span></div>
    <div class="pos-dept">${reliab.slice().sort((a, b) => (b.reliability || 0) - (a.reliability || 0)).map(r => {
      const v = Math.round(r.reliability || 0);
      const hue = posDeptColor(r.department);
      const tone = v >= 92 ? 'var(--ok)' : v >= 82 ? hue : 'var(--warn)';
      return `<div class="pos-dept__r"><span class="pos-dept__n"><span class="pos-dept__dot" style="background:${hue}"></span><span>${esc(r.department)}</span></span>
        <span class="pos-dept__track"><i style="width:${v}%;background:${tone}"></i></span>
        <span class="pos-dept__v" style="color:${v < 82 ? 'var(--warn)' : 'var(--ink)'}">${v}%</span></div>`;
    }).join('')}</div></div>` : '';

  // ── bonus pool + pay equity (side) ──
  const byDept = pool.by_department || [];
  const poolTotal = byDept.reduce((s, x) => s + (x.payout_ngn || 0), 0) || pool.pool_ngn || 0;
  const poolBar = byDept.length ? byDept.map(x => {
    const pct = poolTotal ? Math.round((x.payout_ngn || 0) / poolTotal * 100) : 0;
    return `<span class="pos-pool__seg" style="width:${pct}%;background:${posDeptColor(x.department)};opacity:.85" title="${esc(x.department)} ${pct}%"></span>`;
  }).join('') : '<span class="pos-pool__seg" style="width:100%;background:var(--line)"></span>';
  const poolLegend = byDept.map(x => {
    const pct = poolTotal ? Math.round((x.payout_ngn || 0) / poolTotal * 100) : 0;
    return `<span class="pos-pool__li"><span class="sw" style="background:${posDeptColor(x.department)}"></span>${esc(x.department)}<span class="pc">${pct}%</span></span>`;
  }).join('');
  const eqDepts = (pg.departments || []).filter(x => (x.rep_count || 0) > 0);
  const eqScore = eqDepts.length ? Math.round(eqDepts.reduce((s, x) => s + Math.min(1, x.ratio_to_top || 0), 0) / eqDepts.length * 100) : null;
  const eqDash = 119.4, eqOff = eqScore != null ? Math.round(eqDash * (1 - eqScore / 100)) : eqDash;
  const amberDepts = eqDepts.filter(x => x.flag === 'amber').map(x => x.department);
  const poolCard = `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Bonus pool${pool.pool_ngn ? ' · funded' : ''}</span></div>
    <div class="pos-pool__head"><span class="pos-pool__amt">${posNgn(pool.pool_ngn || 0)}</span>
      <span class="pos-pool__cap">${pool.ngn_per_point ? '₦' + pool.ngn_per_point + '/point' : 'allocated by points'}</span></div>
    <div class="pos-pool__bar">${poolBar}</div>
    <div class="pos-pool__legend">${poolLegend}</div>
    ${eqScore != null ? `<div class="pos-equity">
      <div class="pos-equity__ring"><svg viewBox="0 0 46 46"><circle class="pos-ring__bg" cx="23" cy="23" r="19" stroke-width="6"/><circle cx="23" cy="23" r="19" stroke-width="6" fill="none" stroke="url(#posEqGrad)" stroke-linecap="round" stroke-dasharray="${eqDash}" stroke-dashoffset="${eqOff}"/></svg><div class="pos-equity__c">${eqScore}</div></div>
      <div class="pos-equity__t"><b>Pay equity ${eqScore}/100.</b> Bonus share tracks points, not tenure or gender.${amberDepts.length ? ` <b>${esc(amberDepts.join(', '))}</b> below the fair band — review.` : ' Every department within band.'}</div>
    </div>` : ''}
  </div>`;

  // ── Monthly league (season = calendar month; resets so new hires race
  // a fresh field). Data: overview → league (people_creative). Board rows
  // reuse .pos-lb so the motion layer animates them for free.
  const lg = d.league || {};
  const lgBoard = lg.board || [];
  const lgLast = lg.last_season && lg.last_season.winner_name
    ? `<div class="pos-cw__champ">Last season: <b>${esc(lg.last_season.winner_name)}</b> · ${lg.last_season.winner_points || 0} pts · ${esc(lg.last_season.period || '')}</div>` : '';
  const lgMax = Math.max(1, ...lgBoard.map(x => x.points || 0));
  const leaguePanel = lgBoard.length ? `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">League · season ${esc(lg.period || '')}</span><span class="pos-panel__act" style="cursor:default">${lg.days_left} day${lg.days_left === 1 ? '' : 's'} left</span></div>
    <div class="pos-lb">${lgBoard.map((pRow, i) => `<div class="pos-lb__row ${i === 0 ? 'pos-lb__row--lead' : ''}" style="cursor:default">
        <span class="pos-lb__rank">${i + 1}</span>
        <span class="pos-lb__who">${posAvatar(pRow.name || pRow.rep_id)}<span style="min-width:0"><span class="pos-lb__nm" style="display:block">${esc(pRow.name || pRow.rep_id)}</span><span class="pos-lb__rl" style="display:block">${esc(pRow.role || pRow.department || '—')}</span></span></span>
        <span class="pos-lb__bar"><i style="width:${Math.round((pRow.points || 0) / lgMax * 100)}%"></i></span>
        <span class="pos-lb__pts">${pRow.points || 0}</span>
      </div>`).join('')}</div>
    ${lgLast}
    <div class="pos-cw__foot">Season resets on the 1st — lifetime levels stay; everyone starts the month even.</div>
  </div>` : '';

  // ── integrity & retention flags (side) ──
  const flightFlags = (fr.flight_risk || {}).flags || [];
  const integFindings = (fr.integrity || {}).findings || [];
  const flagItems = [];
  integFindings.forEach(f => flagItems.push({ ic: f.severity === 'review' ? 'danger' : 'warn', icn: 'alert', em: f.rep_id, t: `${f.name || f.rep_id} — ${String(f.kind || 'worth a look').replace(/_/g, ' ')}`, m: f.detail || '' }));
  flightFlags.forEach(f => flagItems.push({ ic: 'warn', icn: 'trend', em: f.rep_id, t: `${f.name || f.rep_id} — flight risk`, m: (f.reasons || []).join(' · ') + (f.coach_nudge ? ' · ' + f.coach_nudge : '') }));
  if (board[0]) flagItems.push({ ic: 'ok', icn: 'check', em: board[0].rep_id, t: `${board[0].name || board[0].rep_id} — leading this week`, m: `${board[0].points || 0} pts${board[0].streak_days ? ` · ${board[0].streak_days}-day streak` : ''}. Top performer — approve the milestone on the reward desk.` });
  const fic = {
    alert: '<path d="M12 9v4M12 17h.01M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/>',
    trend: '<path d="M3 17l6-6 4 4 8-8M21 7v5h-5"/>',
    check: '<path d="M20 6 9 17l-5-5"/>',
  };
  const flagsCard = `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Integrity & retention</span><span class="pos-panel__act" style="cursor:default">${flagItems.length}</span></div>
    ${flagItems.length ? flagItems.map(f => `<${f.em ? `button class="pos-flag" data-fem="${esc(f.em)}"` : 'div class="pos-flag"'}><span class="pos-flag__ic ${f.ic}"><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.9">${fic[f.icn]}</svg></span>
      <span class="pos-flag__b"><span class="pos-flag__t">${esc(f.t)}</span><span class="pos-flag__m">${esc(f.m)}</span></span></${f.em ? 'button' : 'div'}>`).join('')
      : '<div class="pos-flag"><span class="pos-flag__ic ok"><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.9">' + fic.check + '</svg></span><span class="pos-flag__b"><span class="pos-flag__t">All clear</span><span class="pos-flag__m">No retention or integrity flags this week.</span></span></div>'}
  </div>`;

  return `<div class="pos-ov">
    <div class="pos-ov__main">${pulse}${lb}${leaguePanel}${deptStrip}</div>
    <div class="pos-ov__side">
      <div style="display:flex;gap:8px"><button class="pos-xcta" id="pos-bonus-run">Draft bonus run</button><button class="pos-xcta" id="pos-frict-log">Log friction</button></div>
      <div id="pos-appr-slot"></div><div id="pos-decide-slot"></div><div id="pos-reward-slot"></div>${poolCard}${flagsCard}</div>
  </div>`;
}

// ── Shift-request approval queue (Operations) ────────────────
// Off-day / leave / swap requests land here for the Operations manager.
// Approve writes the roster exception server-side; coverage warnings flag
// any day a store would be left empty.
const POS_REQKIND = { off_day: 'Off day', leave: 'Leave', swap: 'Shift swap' };
// ── Waiting on you — undecided EON approvals (WS-A, gap-map point 9) ──
// A ₦200k comp change for one staffer sat undecided for five days while
// the "enrol her" nudge kept re-minting — the decision queue lived only
// in chat. Pending rows now sit on the People OS Overview with their
// age; one tap decides. The server enforces who may decide (owner, or a
// member the owner delegated to) — this panel never widens authority.
async function _posLoadDecisions() {
  const slot = document.getElementById('pos-decide-slot');
  if (!slot) return;
  // W1-04d: the ask the link named (boot reads ?ask= beside ?tab=), while the
  // address still carries it; leaving People drops it from the address.
  let ask = null;
  try { ask = _posState.ask && new URLSearchParams(location.search).get('ask') === _posState.ask ? _posState.ask : null; } catch (_) {}
  // "No longer waiting" needs the whole queue: the route keeps the 30 oldest
  // asks of every kind, and a delegate's read carries no total. So an ask the
  // list does not carry is read by id (R5c A11a); only an unread one falls back.
  let rows = [], complete = false, read = null;
  const line = (k, t) => `<div class="pos-flag__m" role="status" data-pdask-${k}>${esc(t)}</div>`;
  const on = at => { const d = at ? new Date(at) : null; return d && !isNaN(d) ? 'On ' + d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Africa/Lagos' }) + ': ' : ''; };
  const gone = () => !ask || rows.some(r => r.id === ask) ? ''
    : read === 'missing' ? line('missing', 'That ask is not in this workspace.')
    : read === 'refused' ? line('refused', 'That ask is not yours to answer.')
    : read ? line('answered', on(read.answered_at) + (read.answer_words || 'That ask is no longer waiting.'))
    : complete ? line('gone', 'That ask is no longer waiting.')
    : line('unshown', 'That ask is not among the ones shown here. It may still be waiting.');
  const empty = () => `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Waiting on you</span></div>${gone()}<div class="pos-flag__m" role="status">Nothing waiting for your approval. New requests will appear here.</div></div>`;
  try {
    const d = await api('/api/permissions/pending');
    if (!d || d.ok !== true || !Array.isArray(d.pending) || d.pending.some(r =>
      !r || typeof r !== 'object' || Array.isArray(r) || typeof r.id !== 'string'
      || !/^[A-Za-z0-9-]+$/.test(r.id) || typeof r.description !== 'string'
      || typeof r.action_type !== 'string')) throw new Error('Unreadable approvals');
    rows = d.pending;
    complete = Number.isSafeInteger(d.total) && d.total <= rows.length;
  }
  catch (_) {
    slot.innerHTML = `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Waiting on you</span></div><div class="pos-flag__m" role="alert">Approval requests could not be read. Reload to try again.</div></div>`;
    return;
  }
  if (ask && !rows.some(r => r.id === ask)) {
    try {
      const n = await api('/api/permissions/ask/' + encodeURIComponent(ask));
      const a = n && n.ok === true && n.ask && typeof n.ask === 'object' && n.ask.id === ask ? n.ask : null;
      if (n && n.ok === true && n.found === false) read = 'missing';
      // still waiting: at the top, with its own buttons on the same decide door
      else if (a && n.waiting === true && typeof a.description === 'string' && typeof a.action_type === 'string') rows = [a, ...rows];
      else if (a && n.waiting === false) read = a;
    } catch (e) { if (e && e.status === 403) read = 'refused'; }
  }
  if (!rows.length) { slot.innerHTML = empty(); return; }
  const age = h => h >= 48 ? Math.round(h / 24) + ' days' : h >= 24 ? 'a day' : h >= 1 ? Math.round(h) + 'h' : 'just now';
  // 2026-08-04: a question's choices ARE its buttons here too — the row
  // asks what it needs, not "Approve" over the top of an open question.
  const acts = (r) => {
    if (permCardIntent(r) === 'question') {
      const opts = permCardOptions(r);
      // ...and the way out, on every question shape — open-ended
      // included, since "Answer in chat" is not an exit either.
      const drop = `<button class="pos-appr__no" data-pdno="${esc(r.id)}">No longer needed</button>`;
      if (opts.length) {
        return opts.map(o =>
          `<button class="pos-appr__ok" data-pdans="${esc(r.id)}" data-pdval="${esc(o.value)}">${esc(o.label)}</button>`
        ).join('') + drop;
      }
      return `<button class="pos-appr__no" data-pdchat="${esc(r.id)}">Answer in chat</button>` + drop;
    }
    if (r.action_type === 'ad_cull_restore') return `<button class="pos-appr__ok" style="min-height:44px" data-pdok="${esc(r.id)}">Resume ad</button>
      <button class="pos-appr__no" style="min-height:44px" data-pdno="${esc(r.id)}">Keep paused</button>`;
    return `<button class="pos-appr__ok" data-pdok="${esc(r.id)}">Approve</button>
      <button class="pos-appr__no" data-pdno="${esc(r.id)}">Deny</button>`;
  };
  // The named row is marked by an outline, which takes no room: nothing moves.
  const named = r => r.id === ask ? ' tabindex="-1" aria-current="true" style="outline:2px solid var(--accent);outline-offset:4px;scroll-margin:96px"' : '';
  slot.innerHTML = `<div class="pos-panel"><div class="pos-panel__h">
      <span class="pos-panel__t">Waiting on you · ${rows.length}</span></div>${gone()}
    ${rows.map(r => `<div class="pos-appr__row" data-pdrow="${esc(r.id)}"${named(r)}>
      <div class="pos-appr__meta"><b>${esc(r.description || r.action_type || 'Pending request')}</b>
        <span class="${r.age_hours >= 24 ? 'pos-decide--old' : ''}">waiting ${age(r.age_hours || 0)}</span></div>
      <div class="pos-appr__acts">${acts(r)}</div></div>`).join('')}</div>`;
  // Scrolled to once per ask: a quiet repaint keeps the mark, not the scroll.
  const target = ask && slot.querySelector(`[data-pdrow="${ask}"]`);
  if (target && _posState.askSeen !== ask) {
    _posState.askSeen = ask;
    const reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    target.focus({ preventScroll: true });
  }
  const restoring = new Set();
  const post = async (id, body, okMsg) => {
    const request = rows.find(r => r.id === id);
    const restore = request && request.action_type === 'ad_cull_restore';
    if (restore && restoring.has(id)) return;
    if (restore) restoring.add(id);
    const restoreResult = message => {
      const row = slot.querySelector(`[data-pdrow="${id}"]`);
      if (row) row.querySelector('.pos-appr__acts').innerHTML = `<span class="pos-flag__m" role="status">${esc(message)}</span>`;
      showToast(message);
    };
    const uncertain = 'Could not confirm this decision or activation. Check the recorded result before trying again.';
    if (restore) slot.querySelector(`[data-pdrow="${id}"]`)?.querySelectorAll('button').forEach(b => b.disabled = true);
    try {
      const res = await api(`/api/permissions/${id}/decide`, { method: 'POST', body: JSON.stringify(body) });
      if (restore) {
        if (res && (res.ok === true || res.already_decided === true)) {
          // Saving a YES is not proof that money is now spending. Keep the
          // actual execution receipt visible; never offer an uncertain retry.
          const done = res.executor_result;
          const message = res.already_decided === true
            ? 'A decision was already recorded. Check its result before trying again.'
            : body.decision === 'deny' ? 'Restoration declined. This pause will not be proposed again.'
            : done && done.ok === true && done.activation_confirmed === true && done.receipt_saved === true
              ? 'Ad is running again. Approval and activation confirmed.'
              : done && done.activation_confirmed === true && done.receipt_saved === false
                ? 'Ad is running again, but its confirmation could not be saved. Do not approve it again.'
                : done && done.activation_confirmed === true
                  ? 'Ad is running again, but the recorded result needs review. Do not approve it again.'
                : 'Approval saved; activation is not confirmed. Check the recorded result before trying again.';
          restoreResult(message);
        } else {
          restoreResult(uncertain);
        }
        return;
      }
      if (res && (res.ok || res.already_decided)) {
        showToast(okMsg);
        slot.querySelector(`[data-pdrow="${id}"]`)?.remove();
        if (!slot.querySelector('[data-pdrow]')) slot.innerHTML = empty();
      } else showToast((res && res.error) || 'Could not decide');
    } catch (e) {
      if (restore) restoreResult(uncertain);
      else showToast(e.message || 'Could not decide');
    }
  };
  const decide = (id, decision) => post(id, { decision },
    decision === 'approve' ? 'Approved — running it now' : 'Denied');
  slot.querySelectorAll('[data-pdok]').forEach(b => b.addEventListener('click', () => decide(b.getAttribute('data-pdok'), 'approve')));
  slot.querySelectorAll('[data-pdno]').forEach(b => b.addEventListener('click', () => decide(b.getAttribute('data-pdno'), 'deny')));
  slot.querySelectorAll('[data-pdans]').forEach(b => b.addEventListener('click', () =>
    post(b.getAttribute('data-pdans'), { answer: b.getAttribute('data-pdval') },
         `Answered: ${b.getAttribute('data-pdval')}`)));
  slot.querySelectorAll('[data-pdchat]').forEach(b => b.addEventListener('click', () => window.goView('chat')));
}

// ── Pay — the payroll section (WS-F, gap-map point 4) ─────────────────
// "The platform requires a dedicated Payroll section." The engine existed
// (runs, PAYE, payslips, bonus close) with no screens — approve was a
// dead-end and nothing was ever marked paid. Salaries are NET (employer
// bears tax): net is what gets paid; gross + PAYE columns are the honest
// statutory picture. NOTHING is filed from here — the accountant first.
async function _posPayLoad() {
  try { const d = await api('/api/people/payroll/runs'); _posState.payRuns = (d && d.runs) || []; }
  catch (e) { _posState.payRuns = null; _posState.payErr = (e && e.message) || 'Could not load payroll'; }
  if (_posState.tab === 'pay') { const su = document.getElementById('pos-surface'); if (su) { su.innerHTML = _posPay(); _posBindPay(); } }
}

function _posPay() {
  const runs = _posState.payRuns;
  if (runs === undefined) return '<div class="pos-empty" style="padding:40px"><div class="pos-empty__i">…</div>Loading payroll…</div>';
  if (runs === null) return `<div class="pos-empty" style="padding:40px"><div class="pos-empty__i">!</div>${esc(_posState.payErr || 'Could not load payroll')}</div>`;
  const chip = st => ({ draft: ['draft', 'warn'], approved: ['approved', 'ok'], paid: ['paid', 'ok'], cancelled: ['cancelled', ''] }[st] || [st, '']);
  const fmt = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }) : '';
  return `<div class="pos-pay">
    <div class="pos-panel"><div class="pos-panel__h">
      <span class="pos-panel__t">Payroll</span>
      <button class="pos-panel__act" id="pos-pay-run">Draft this month</button></div>
      <div class="pos-flag__m" style="padding:6px 2px">Salaries are take-home — the business bears the tax. Net is what gets paid; the gross and tax columns are the statutory picture. Nothing is filed from here until the accountant signs off.</div></div>
    <div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Payslips</span></div>
      <div class="pos-flag__m" style="padding:4px 2px 8px">One person's month, computed from the same tax line every surface uses. WhatsApp send stays off until the switchboard arms it.</div>
      <div id="pos-payslip-list">${(_posState.teamRoster || []).filter(r2 => (r2.status || 'active') === 'active' && Number(r2.monthly_salary) > 0).map(r2 =>
        `<button class="pos-team__row" data-slip="${esc(r2.id)}">${posAvatar(r2.full_name, 'sm')}
         <span class="pos-team__b"><span class="pos-team__n">${esc(r2.full_name)}</span><span class="pos-team__m">${esc(r2.role || '')}</span></span>
         <span class="pos-team__gap">open statement</span></button>`).join('')
        || '<div class="pos-flag__m" style="padding:8px 2px">Open the Team tab once — the payslip list reads the same roster.</div>'}</div></div>
    ${runs.length ? runs.map(r => {
      const [label, tone] = chip(r.status);
      return `<div class="pos-panel"><div class="pos-panel__h">
        <span class="pos-panel__t">${fmt(r.period_start)}${r.kind === 'bonus' ? ' · bonus' : ''} · ${posNgn(r.total_amount)}</span>
        <span class="pos-att__chip ${tone ? 'pos-att__chip--' + tone : ''}">${esc(label)}</span></div>
        <div class="pos-appr__acts" style="padding:4px 0 8px">
          <button class="pos-appr__no" data-payitems="${esc(r.id)}">See lines</button>
          ${r.status === 'draft' ? `<button class="pos-appr__ok" data-payapprove="${esc(r.id)}">Approve</button>` : ''}
          ${r.status === 'approved' ? `<button class="pos-appr__ok" data-paypaid="${esc(r.id)}">Mark paid</button>` : ''}</div>
        <div id="pay-items-${esc(r.id)}"></div></div>`;
    }).join('') : '<div class="pos-empty" style="padding:30px"><div class="pos-empty__i">·</div>No pay runs yet. Draft this month to start.</div>'}</div>`;
}

function _posBindPay() {
  document.querySelectorAll('[data-slip]').forEach(btn => btn.addEventListener('click', async () => {
    const sid = btn.getAttribute('data-slip');
    const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Statement</div><button class="csw-x">✕</button></div><div id="slip-body" class="pos-flag__m" style="padding:12px 4px">Computing…</div>`);
    ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
    try {
      const d = await api(`/api/people/payslip?staff_id=${encodeURIComponent(sid)}`);
      if (!d || d.ok === false) { ov.querySelector('#slip-body').textContent = (d && d.error) || 'No statement yet.'; return; }
      const L = [];
      L.push(`<div style="padding:6px 0"><b>${esc(d.full_name || '')}</b> · ${esc(d.period || '')}</div>`);
      if (d.net_payable_computed_ngn != null) L.push(`<div>Take-home: <b class="num">${posNgn(d.net_payable_computed_ngn)}</b></div>`);
      if (d.gross_month_ngn != null) L.push(`<div>Gross (statutory picture): ${posNgn(d.gross_month_ngn)}</div>`);
      if (d.paye_month_ngn != null) L.push(`<div>PAYE the business bears: ${posNgn(d.paye_month_ngn)}</div>`);
      (d.flags || []).forEach(f => L.push(`<div class="pos-flag__m">${esc(typeof f === 'string' ? f : (f.note || ''))}</div>`));
      L.push(`<div class="pos-flag__m" style="padding-top:6px">Nothing is filed from this screen.</div>`);
      L.push(`<button class="pos-xcta" id="slip-wa" style="margin-top:8px">Send to their WhatsApp</button><div id="slip-note" class="pos-flag__m"></div>`);
      ov.querySelector('#slip-body').innerHTML = L.join('');
      ov.querySelector('#slip-wa')?.addEventListener('click', async () => {
        const note = ov.querySelector('#slip-note');
        try {
          const r = await api('/api/people/payslip/send', { method: 'POST', body: JSON.stringify({ staff_id: sid }) });
          if (r && r.ok) { note.textContent = 'Sent to their chat.'; }
          else note.textContent = (r && r.error) || 'Could not send.';
        } catch (e) { note.textContent = e.message || 'Could not send.'; }
      });
    } catch (e) { ov.querySelector('#slip-body').textContent = e.message || 'Could not load'; }
  }));
  document.getElementById('pos-pay-run')?.addEventListener('click', async () => {
    try {
      const res = await api('/api/people/payroll/run-month', { method: 'POST', body: '{}' });
      showToast((res && (res.message || res.status)) || 'Drafted');
      _posState.payRuns = undefined; _posPayLoad();
    } catch (e) { showToast(e.message || 'Could not draft'); }
  });
  document.querySelectorAll('[data-payapprove]').forEach(b => b.addEventListener('click', async () => {
    try {
      // The router mounts at prefix="/api" (routes_app.py:22), so the
      // served path is /api/hr/payroll/{run_id}/approve. Shipping it
      // without the prefix meant every Approve tap 404'd silently into
      // the catch below and showed "Could not approve".
      const res = await api(`/api/hr/payroll/${b.getAttribute('data-payapprove')}/approve`, { method: 'POST', body: '{}' });
      showToast((res && res.message) || 'Approved');
      _posState.payRuns = undefined; _posPayLoad();
    } catch (e) { showToast(e.message || 'Could not approve'); }
  }));
  document.querySelectorAll('[data-paypaid]').forEach(b => b.addEventListener('click', async () => {
    try {
      const res = await api(`/api/people/payroll/${b.getAttribute('data-paypaid')}/mark-paid`, { method: 'POST', body: '{}' });
      if (res && res.ok) { showToast('Marked paid'); _posState.payRuns = undefined; _posPayLoad(); }
      else showToast((res && res.error) || 'Could not mark paid');
    } catch (e) { showToast(e.message || 'Could not mark paid'); }
  }));
  document.querySelectorAll('[data-payitems]').forEach(b => b.addEventListener('click', async () => {
    const id = b.getAttribute('data-payitems');
    const slot = document.getElementById(`pay-items-${id}`);
    if (!slot) return;
    if (slot.innerHTML) { slot.innerHTML = ''; return; }
    slot.innerHTML = '<div class="pos-flag__m" style="padding:6px 2px">Loading…</div>';
    try {
      const d = await api(`/api/people/payroll/runs/${id}/items`);
      const items = (d && d.items) || [];
      slot.innerHTML = items.length ? items.map(it =>
        `<div class="pos-appr__row"><div class="pos-appr__meta"><b>${esc(it.name)}</b>
          <span>pays ${posNgn(it.net_pay)}${Number(it.deductions) > 0 ? ` · gross ${posNgn(it.base_salary)} · tax+pension ${posNgn(it.deductions)}` : ''}${it.notes ? ' · ' + esc(it.notes) : ''}</span></div></div>`).join('')
        : '<div class="pos-flag__m" style="padding:6px 2px">No lines on this run.</div>';
    } catch (e) { slot.innerHTML = `<div class="pos-flag__m" style="padding:6px 2px">${esc(e.message || 'Could not load lines')}</div>`; }
  }));
}

// ── Team — the employee directory (WS-B, gap-map point 1) ─────────────
// "I need the ability to view and edit employee profiles directly."
// Before this tab there was NO profile editor anywhere — every staff-record
// change went through chat and waited on an approval card. The list reads
// GET /api/hr/staff (manager+); saves PATCH /api/hr/staff/{id}
// (HR_STAFF_EDIT — ops leads hold it on PIN sessions as of WS-B). Money
// fields are display-only here: comp changes keep their approval lane.
// ── The last mile — arming meter (Working Floor WS-B, 2026-08-29) ────
// Probes 2026-08-29: the estate is alive (37 check-ins this week, points
// daily, staff line busy) — what remains is the last mile of identity:
// phones 19/25, PINs 21/25, rota 21/25, till IDs 14/25, salaries 21/25.
// This card counts it live, says whose tap closes each gap, and carries
// the one-tap "Fix from Shopify" (fill-blanks-only, dry-run first).
// WS-H: benefits + lifecycle get their doors on the Team tab. The
// benefits engine has scanned since July with no screen; exits and the
// regretted count — THE retention number — were computed and rendered
// nowhere until this card.
function _posCareCards() {
  const bf = _posState.benefitsBoard, lc = _posState.lifecycleBoard;
  let out = '';
  if (bf) {
    const due = bf.due || bf.upcoming || [];
    const rows = due.slice(0, 5).map(b2 => `<div class="pos-floor__hm" style="padding:5px 2px"><b>${esc(b2.staff_name || b2.name || '—')}</b>
        <span>${esc((b2.benefit_type || '').replace('_', ' '))}${b2.due_date ? ' · ' + esc(b2.due_date) : ''}${b2.amount_ngn ? ' · ' + posNgn(b2.amount_ngn) : ''}</span></div>`).join('');
    out += `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Gifts and housing due</span></div>
      ${rows || '<div class="pos-flag__m" style="padding:8px 2px">Nothing due right now.</div>'}
      ${bf.dob_gap != null ? `<div class="pos-flag__m">${bf.dob_gap} people have no birthday on record — their gift can never fire. The Team list carries the gap chips.</div>` : ''}</div>`;
  }
  if (_posState.ukChecklist && (_posState.ukChecklist.dormant_leave || []).length) {
    const uk = _posState.ukChecklist;
    out += `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">UK switch-on · September 2026</span></div>
      ${(uk.schema_work || []).map(w => `<div class="pos-flag__m">${esc(w)}</div>`).join('')}
      <div class="pos-flag__m">${uk.dormant_leave.length} UK leave profile${uk.dormant_leave.length === 1 ? '' : 's'} seeded dormant — arming is a flip, not a build.</div></div>`;
  }
  if (lc) {
    const kits = lc.kits_in_flight || lc.kits || [];
    const exits = lc.exits_ytd != null ? lc.exits_ytd : (lc.exits || []).length;
    const regretted = lc.regretted_ytd != null ? lc.regretted_ytd : lc.regretted;
    out += `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Joining and leaving</span></div>
      <div class="pos-floor__hm" style="padding:5px 2px"><b>${kits.length || 0}</b><span>onboarding${kits.length === 1 ? '' : 's'} in flight</span></div>
      <div class="pos-floor__hm" style="padding:5px 2px"><b>${exits || 0}</b><span>exit${exits === 1 ? '' : 's'} this year${regretted != null ? ` — ${regretted} we were sorry to lose` : ''}</span></div>
      <div class="pos-flag__m">People we were sorry to lose is the number to watch — it says whether the good ones stay.</div></div>`;
  }
  return out;
}

function _posArmingCard() {
  const m = _posState.idMeter;
  if (!m || !m.rollup) return '';
  const R = m.rollup, n = R.active || 0;
  const bar = (label, got) => `<div class="pos-arm__c ${got < n ? 'short' : ''}">
      <span class="pos-arm__v pos-mono">${got}/${n}</span><span class="pos-arm__l">${label}</span></div>`;
  const att = m.attendance || {};
  const pay = m.payroll || {};
  const vitals = [];
  vitals.push(att.status === 'alive'
    ? `Clock-ins are flowing — ${att.checkins_7d || 0} this week.`
    : att.status === 'reviving' ? `Clock-ins are starting — ${att.checkins_7d || 0} this week.`
    : 'Nobody has clocked in this week.');
  if (pay.latest_period_end) vitals.push(`Latest pay run: to ${pay.latest_period_end} — ${pay.latest_status}.`);
  (att.blockers || []).forEach(b => vitals.push(b));
  const unpinned = (m.sites || []).filter(x => !x.pin_precise);
  const sites = unpinned.length
    ? `<div class="pos-arm__sites">${unpinned.map(x =>
        `<span>${esc(x.name)} has no precise pin</span>
         <button class="pos-arm__pin" data-armpin="${esc(x.id)}">Pin this spot</button>`).join('')}
       <div class="pos-flag__m">Stand at the door and tap — the phone's location becomes the clock-in fence.</div></div>`
    : '';
  return `<div class="pos-panel pos-arm"><div class="pos-panel__h">
      <span class="pos-panel__t">The last mile · ${R.complete || 0} of ${n} fully wired</span>
      <button class="pos-panel__act" id="pos-arm-sync">Fix from Shopify</button></div>
    <div class="pos-arm__row">${bar('phones', R.phone || 0)}${bar('PINs', R.pin || 0)}${bar('on the rota', R.roster || 0)}${bar('till IDs', R.till_id || 0)}${bar('salaries', R.salary || 0)}${bar('birthdays', R.dob || 0)}</div>
    <div class="pos-arm__vitals">${vitals.map(v => `<span>${esc(v)}</span>`).join('')}</div>
    ${sites}
    <div id="pos-arm-note" class="pos-flag__m"></div></div>`;
}

function _posBindArming() {
  const note = () => document.getElementById('pos-arm-note');
  document.getElementById('pos-arm-sync')?.addEventListener('click', async () => {
    const el = note(); if (el) el.textContent = 'Checking Shopify…';
    try {
      const d = await api('/api/people/staff-sync', { method: 'POST', body: JSON.stringify({ dry_run: true }) });
      if (!d || !d.ok) { if (el) el.textContent = (d && d.error) || 'Shopify did not answer.'; return; }
      if (!d.updated) { if (el) el.textContent = 'Nothing to fill — every blank Shopify knows about is already set.'; return; }
      const names = (d.details || []).map(x => x.staff).filter(Boolean).slice(0, 6).join(', ');
      if (el) el.innerHTML = `Shopify can fill ${d.updated} record${d.updated === 1 ? '' : 's'} (${esc(names)}${d.updated > 6 ? '…' : ''}). <button class="pos-arm__pin" id="pos-arm-sync-go">Fill them</button>`;
      document.getElementById('pos-arm-sync-go')?.addEventListener('click', async () => {
        const r = await api('/api/people/staff-sync', { method: 'POST', body: '{}' });
        if (r && r.ok) { showToast(`Filled ${r.updated} record${r.updated === 1 ? '' : 's'}`); _posState.idMeter = null; _posTeamLoad(); }
        else showToast((r && r.error) || 'Could not fill');
      });
    } catch (e) { if (el) el.textContent = e.message || 'Could not reach Shopify.'; }
  });
  document.querySelectorAll('[data-armpin]').forEach(b => b.addEventListener('click', () => {
    const id = b.getAttribute('data-armpin');
    if (!navigator.geolocation) { showToast('This device has no location'); return; }
    b.textContent = 'Reading location…';
    navigator.geolocation.getCurrentPosition(async pos => {
      try {
        const r = await api('/api/attendance/pin', { method: 'POST', body: JSON.stringify({ location_id: id, lat: pos.coords.latitude, lng: pos.coords.longitude }) });
        if (r && r.ok !== false) { showToast('Pinned — clock-ins here now verify'); _posState.idMeter = null; _posTeamLoad(); }
        else showToast((r && r.error) || 'Could not pin');
      } catch (e) { showToast(e.message || 'Could not pin'); }
    }, () => { b.textContent = 'Pin this spot'; showToast('Location was refused'); }, { enableHighAccuracy: true, timeout: 12000 });
  }));
}

// ── The Floor Board (Working Floor WS-E, 2026-08-29) ─────────────────
// Today per site — who is expected, who is in, whether the door opened
// with someone rostered — and every store-day in the next 14 that leave
// or a day off has emptied, with the people who could cover it one tap
// away. The rota editor stays the only pen; asking is a human tap.
async function _posFloorLoad() {
  // The till codes are their own read: a shop manager the company-wide board refuses still sees them.
  const tcRead = api('/api/people/floor/till-codes').catch(() => ({ ok: false }));
  try {
    const [d, g, lv, li, fb] = await Promise.all([
      api('/api/people/floor/board'),
      api('/api/people/day-grades?days=2').catch(() => null),
      api('/api/people/leave/board').catch(() => null),
      api('/api/people/leave/liability').catch(() => null),
      api('/api/people/factory/board?days=7').catch(() => null),
    ]);
    _posState.floorBoard = (d && d.ok) ? d : { error: (d && d.error) || 'Could not load the board' };
    _posState.dayGrades = (g && g.ok) ? (g.grades || []) : [];
    _posState.floorLeave = (lv && lv.ok !== false) ? lv : null;
    _posState.floorLiability = (li && li.ok !== false) ? li : null;
    _posState.factoryBoard = (fb && fb.ok) ? fb : null;
  } catch (e) { _posState.floorBoard = { error: e.message || 'Could not load the board' }; }
  _posState.tillCodes = (await tcRead) || null;
  if (_posState.tab === 'floor') { const su = document.getElementById('pos-surface'); if (su) { su.innerHTML = _posFloor(); _posBindFloor(); } }
}

function _posFloor() {
  const b = _posState.floorBoard;
  if (!b) return '<div class="pos-empty" style="padding:40px"><div class="pos-empty__i">…</div>Reading the floor…</div>';
  if (b.error) return `<div class="pos-empty" style="padding:40px"><div class="pos-empty__i">!</div>${esc(b.error)}</div>${_posFloorTillCodes()}`;
  const site = t => {
    const known = t.state !== 'unavailable' && Number.isFinite(t.present) && Number.isFinite(t.expected);
    const miss = (t.no_shows || []).map(x => (x.name || '').split(' ')[0]).filter(Boolean);
    const tone = !known || t.expected === 0 ? '' : t.present >= t.expected ? 'ok' : t.present === 0 ? 'danger' : 'warn';
    // yesterday's letter, the store's own story (WS-G) — never a table
    // of stores ranked, just each row wearing its last graded day
    const dg = (_posState.dayGrades || []).find(x => String(x.location_id) === String(t.location_id));
    const gchip = dg ? `<span class="pos-floor__grade pos-floor__grade--${esc(dg.grade)}" title="last graded day">${esc(dg.grade)}</span>` : '';
    return `<div class="pos-floor__row">
      <span class="pos-floor__site">${esc(t.name)}${gchip}</span>
      <span class="pos-floor__n pos-mono ${tone}">${known ? t.present + "/" + t.expected : "—"}</span>
      <span class="pos-floor__m">${!known ? 'Coverage unavailable' : t.expected === 0 ? 'nobody rostered today'
        : miss.length ? 'waiting on ' + esc(miss.join(', '))
        : 'everyone in'}${t.opens_ok === false ? ' · nobody rostered for the door' : ''}</span></div>`;
  };
  const deskKnown = b.desk?.state !== 'unavailable' && Number.isFinite(b.desk?.present) && Number.isFinite(b.desk?.expected);
  const holes = (b.holes || []);
  const holeRow = h => `<div class="pos-floor__hole" data-hole="${esc(h.location_id)}:${esc(h.date)}">
      <div class="pos-floor__hm"><b>${esc(h.weekday)} ${esc(h.date.slice(8))}/${esc(h.date.slice(5, 7))}</b> · ${esc(h.location)}<span>${esc(h.cause)}</span></div>
      <div class="pos-floor__asks">${(h.eligible || []).length
        ? h.eligible.map(e2 => `<button class="pos-arm__pin" data-cover="${esc(h.location_id)}|${esc(h.date)}|${esc(e2.staff_id)}" ${e2.reachable ? '' : 'title="No WhatsApp reach — the ask files as a task only"'}>Ask ${esc((e2.name || '').split(' ')[0])}</button>`).join('')
        : '<span class="pos-flag__m">Nobody free who knows this site — the rota editor is the move.</span>'}</div></div>`;
  return `<div class="pos-floor">
    <div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Today on the floor</span><button class="pos-panel__act" id="pos-floor-report">Follow-through</button></div>
      ${(b.today || []).map(site).join('')}
      <div class="pos-floor__row"><span class="pos-floor__site">Customer desk</span>
        <span class="pos-floor__n pos-mono ${deskKnown && b.desk.present >= b.desk.expected ? 'ok' : 'warn'}">${deskKnown ? b.desk.present + '/' + b.desk.expected : '—'}</span>
        <span class="pos-floor__m">${deskKnown ? "" : "Coverage unavailable · "}<button class="pos-floor__door" id="pos-floor-deskmodel">how the desk model reads</button></span></div></div>
    <div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Next 14 days · ${holes.length ? holes.length + (holes.length === 1 ? ' hole' : ' holes') : 'no holes'}</span></div>
      ${holes.length ? holes.map(holeRow).join('')
        : '<div class="pos-flag__m" style="padding:8px 2px">Every rostered day in the next two weeks has its people. Leave and days off are already counted.</div>'}</div>
    ${_posFloorLeavePanel()}
    ${_posFloorTillCodes()}
    ${_posFactoryPanel()}
    <div class="pos-wall"><p>"A hole named two weeks out is a rota edit; a hole found at 9am is a closed door."</p></div></div>`;
}

// The till codes each shop can take (2026-10-26). Read-only: it sends nothing
// to anyone, and typing a code is never needed to finish a sale.
function _posFloorTillCodes() {
  const t = _posState.tillCodes;
  if (!t) return '';
  const head = n => `<div class="pos-panel" data-till-codes><div class="pos-panel__h"><span class="pos-panel__t">Till codes for this shop${n ? ' · ' + esc(n) : ''}</span></div>`;
  if (t.ok === false || !Array.isArray(t.shops)) return `${head('')}<div class="pos-flag__m" style="padding:8px 2px">${esc(t.error || 'The till codes could not be read just now. Try again in a moment.')}</div></div>`;
  if (!t.shops.length) return '';
  const pick = t.shops.find(s => String(s.location_id) === String(_posState.tillShop)) || t.shops[0];
  const shops = t.shops.length > 1 ? `<div class="pos-floor__asks">${t.shops.map(s => `<button class="pos-arm__pin" data-till-shop="${esc(s.location_id)}" aria-pressed="${s === pick}">${esc(s.name)}</button>`).join('')}</div>` : '';
  const rows = (pick.codes || []).map(c => `<div class="pos-floor__hm" style="padding:5px 2px" data-till-code="${esc(c.code)}" data-at-till="${c.at_till === true}"><b class="pos-mono">${esc(c.code)}</b><span>${esc(c.what)} · ${esc(c.words)}</span></div>`).join('');
  return `${head(pick.name)}<div class="pos-flag__m" style="padding:8px 2px">${esc(t.words)}</div>${shops}
    ${rows || '<div class="pos-flag__m" style="padding:8px 2px">No till codes for this shop yet.</div>'}</div>`;
}

// WS-H: leave finally reaches the board it affects — who is away now
// and in the next 14 days (the same window the holes scan), plus the
// unused-annual liability in naira (₦0-salary staff named as excluded,
// the engine's own honesty rule).
function _posFloorLeavePanel() {
  const lv = _posState.floorLeave, li = _posState.floorLiability;
  if (!lv && !li) return '';
  const rows = ((lv || {}).on_leave) || [];
  const items = rows.map(r => `<div class="pos-floor__hm" style="padding:5px 2px"><b>${esc(r.name || '—')}</b>
      <span>${esc(r.leave_type || r.kind || 'off')}${r.start_date ? ' · ' + esc(r.start_date) : ''}${r.end_date ? ' → ' + esc(r.end_date) : ''}</span></div>`).join('');
  const lia = li && li.total_ngn != null
    ? `<div class="pos-flag__m" style="padding-top:6px">Unused annual leave is worth ${posNgn(li.total_ngn)} if everyone cashed out today${(li.excluded || []).length ? ` — ${li.excluded.length} people excluded for having no salary on record` : ''}.</div>`
    : '';
  return `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Away · now and next 14 days</span></div>
    ${items || '<div class="pos-flag__m" style="padding:8px 2px">Nobody is on approved leave in the window.</div>'}${lia}</div>`;
}

// WS-J: CRUX Custom's day on the same board as the stores. Rows come
// from one supervisor text (dark until armed) or the manual line below.
// Piece-rate money never rides these rows.
function _posFactoryPanel() {
  const fb = _posState.factoryBoard;
  const canEdit = memberCan('hr:staff:edit');
  const rows = (fb && fb.days) || [];
  if (!rows.length && !canEdit) return '';
  const row = r => `<div class="pos-floor__hm" style="padding:5px 2px"><b>Line ${esc(r.line_name)}</b>
      <span>${esc(r.work_date)} · ${r.pieces} pieces in ${r.hours_run}h = <b class="num">${r.pieces_per_hour ?? '—'}</b>/h${r.vs_target_pct != null ? ` (${r.vs_target_pct}% of what the style allows)` : ''}${r.dhu_pct != null ? ` · DHU ${r.dhu_pct}%` : ''}</span></div>`;
  const form = canEdit ? `<div style="display:flex;gap:6px;flex-wrap:wrap;padding-top:6px">
      <input id="fx-line" placeholder="line" style="width:70px" class="pos-team__q">
      <input id="fx-pieces" placeholder="pieces" type="number" style="width:80px" class="pos-team__q">
      <input id="fx-hours" placeholder="hours" type="number" step="0.5" style="width:76px" class="pos-team__q">
      <input id="fx-def" placeholder="defects" type="number" style="width:80px" class="pos-team__q">
      <input id="fx-chk" placeholder="checked" type="number" style="width:84px" class="pos-team__q">
      <button class="pos-arm__pin" id="fx-save">Log the day</button></div>
      <div class="pos-flag__m">The supervisor's WhatsApp form stays off until the switchboard arms it — this line always works.</div>` : '';
  return `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">The factory · last 7 days</span></div>
    ${rows.length ? rows.map(row).join('') : '<div class="pos-flag__m" style="padding:8px 2px">No line days logged yet.</div>'}${form}</div>`;
}

function _posBindFloor() {
  document.querySelectorAll('[data-till-shop]').forEach(b => b.addEventListener('click', () => {
    _posState.tillShop = b.dataset.tillShop;
    const su = document.getElementById('pos-surface'); if (su) { su.innerHTML = _posFloor(); _posBindFloor(); }
  }));
  document.getElementById('fx-save')?.addEventListener('click', async () => {
    const v = id => (document.getElementById(id) || {}).value;
    const body = { line_name: v('fx-line'), pieces: v('fx-pieces'), hours_run: v('fx-hours'), defects: v('fx-def') || 0, checked: v('fx-chk') || 0 };
    if (!body.line_name || !body.pieces || !body.hours_run) { showToast('Line, pieces and hours are the minimum'); return; }
    try {
      const r = await api('/api/people/factory/day', { method: 'POST', body: JSON.stringify(body) });
      if (r && r.ok) { showToast(`Logged — ${r.pieces_per_hour ?? '—'}/h${r.dhu_pct != null ? ', DHU ' + r.dhu_pct + '%' : ''}`); _posState.floorBoard = null; _posFloorLoad(); }
      else showToast((r && r.error) || 'Could not log');
    } catch (e) { showToast(e.message || 'Could not log'); }
  });
  document.getElementById('pos-floor-report')?.addEventListener('click', async () => {
    const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Follow-through · last 30 days</div><button class="csw-x">✕</button></div><div id="fr-body" class="pos-flag__m" style="padding:12px 4px">Loading…</div>`);
    ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
    try {
      const d = await api('/api/people/staff-report');
      const rows = (d && d.staff) || [];
      ov.querySelector('#fr-body').innerHTML = rows.length
        ? rows.map(r2 => `<div style="padding:6px 0;border-bottom:1px solid var(--border)"><b>${esc(r2.name || r2.user_id)}</b> — ${r2.completed_30d || 0} done · ${r2.overdue_open || 0} overdue open${r2.on_time_pct != null ? ` · ${r2.on_time_pct}% on time` : ''}</div>`).join('')
        : 'No task owners in the window yet.';
    } catch (e) { ov.querySelector('#fr-body').textContent = e.message || 'Could not load'; }
  });
  document.getElementById('pos-floor-deskmodel')?.addEventListener('click', async () => {
    const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">How the desk model reads</div><button class="csw-x">✕</button></div><div id="fd-body" class="pos-flag__m" style="padding:12px 4px">Loading…</div>`);
    ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
    try {
      const d = await api('/api/people/cs-shift-model');
      const rows = Object.entries(d || {}).filter(([, v]) => ['string', 'number', 'boolean'].includes(typeof v));
      ov.querySelector('#fd-body').innerHTML = rows.length
        ? rows.map(([k, v]) => `<div><b>${esc(k.replace(/_/g, ' '))}</b>: ${esc(String(v))}</div>`).join('')
        : 'The model returned nothing scalar to show — the raw payload feeds the desk itself.';
    } catch (e) { ov.querySelector('#fd-body').textContent = e.message || 'Could not load'; }
  });
  document.querySelectorAll('[data-cover]').forEach(btn => btn.addEventListener('click', async () => {
    const [location_id, date, staff_id] = btn.getAttribute('data-cover').split('|');
    btn.disabled = true; btn.textContent = 'Asking…';
    try {
      const r = await api('/api/people/floor/ask-cover', { method: 'POST', body: JSON.stringify({ location_id, date, staff_id }) });
      if (r && r.ok) {
        showToast(r.pinged ? 'Asked on WhatsApp — the task holds the thread' : (r.note || 'Ask filed as a task'));
        btn.textContent = r.pinged ? 'Asked' : 'Task filed';
      } else { showToast((r && r.error) || 'Could not ask'); btn.disabled = false; btn.textContent = 'Ask again'; }
    } catch (e) { showToast(e.message || 'Could not ask'); btn.disabled = false; }
  }));
}

// ── WS-H (Working Floor, 2026-08-29): doors for the screenless engines ─
// ~2,600 lines of tested backend spoke only through Telegram lines. The
// "soon" stubs die where they render: Coaching (the check-ins board +
// per-person pack — check_ins.py, five endpoints, zero callers till now)
// and a real Org read from the staff rows. The house floor's approved
// design (which hides both tabs) is untouched — these render in classic.
async function _posCoachLoad() {
  try {
    const d = await api('/api/people/checkins');
    _posState.coachBoard = (d && d.ok !== false) ? d : { error: (d && d.error) || 'Could not load' };
  } catch (e) { _posState.coachBoard = { error: e.message || 'Could not load' }; }
  if (_posState.tab === 'reviews') { const su = document.getElementById('pos-surface'); if (su) { su.innerHTML = _posCoaching(); _posBindCoaching(); } }
}

function _posCoaching() {
  const b = _posState.coachBoard;
  if (!b) return '<div class="pos-empty" style="padding:40px"><div class="pos-empty__i">…</div>Reading the month…</div>';
  if (b.error) return `<div class="pos-empty" style="padding:40px"><div class="pos-empty__i">!</div>${esc(b.error)}</div>`;
  const rows = b.rows || b.checkins || [];
  const done = rows.filter(r => (r.status || '') === 'completed');
  const waiting = rows.filter(r => (r.status || '') !== 'completed');
  const row = r => `<button class="pos-team__row" data-coach="${esc(r.staff_id)}">
      ${posAvatar(r.staff_name || r.name, 'sm')}
      <span class="pos-team__b"><span class="pos-team__n">${esc(r.staff_name || r.name || '—')}</span>
      <span class="pos-team__m">${r.status === 'completed' ? 'talked this month' : 'conversation waiting'}${r.manager_name ? ' · with ' + esc(r.manager_name) : ''}</span></span>
      <span class="pos-team__gap">${r.status === 'completed' ? '' : 'open the pack'}</span></button>`;
  return `<div class="pos-coach">
    <div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">One-to-ones · ${b.period || 'this month'}</span>
      <span class="pos-panel__act" style="cursor:default">${done.length} done · ${waiting.length} waiting</span></div>
      ${rows.length ? waiting.concat(done).map(row).join('')
        : '<div class="pos-flag__m" style="padding:8px 2px">No conversations scheduled this month yet — they seed with the month.</div>'}</div>
    <div class="pos-wall"><p>"The pack does the arithmetic so the minutes go to the conversation. One person per pack — it compares nobody."</p></div></div>`;
}

function _posBindCoaching() {
  document.querySelectorAll('[data-coach]').forEach(btn => btn.addEventListener('click', () => _posOpenCoachingPack(btn.getAttribute('data-coach'))));
}
async function _posOpenCoachingPack(sid, name) {
  if(!sid || !State.jwt || !State.tenant?.id || !memberCan('analytics:staff:view'))return;
  const token=State.jwt,tenant=State.tenant.id,view=State.view,access=JSON.stringify(State.member||{});
  const ov=cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">${esc(name || 'Private coaching pack')}</div><button class="csw-x" aria-label="Close coaching pack">×</button></div><p class="pos-flag__m">Prepare a one-to-one. Opening this pack does not book or notify anyone.</p><div id="cp-body" class="pos-flag__m" style="padding:12px 4px">Reading their month…</div>`);
  ov.querySelector('[role="dialog"]')?.setAttribute('aria-label','Private coaching pack');
  ov.querySelector('.csw-x').addEventListener('click',()=>ov.remove());
  const same=()=>ov.isConnected && State.jwt===token && State.tenant?.id===tenant && State.view===view && JSON.stringify(State.member||{})===access && memberCan('analytics:staff:view');
  try {
    const d=await api(`/api/people/checkins/pack?staff_id=${encodeURIComponent(sid)}`);
    if(!same()){ov.remove();return;}
    const body=ov.querySelector('#cp-body');
    if(!d?.ok || String(d.staff_id)!==String(sid)){body.textContent='This person’s coaching pack could not be read. Close and try again.';return;}
    const sections=(Array.isArray(d.sections)?d.sections:[]).map(x=>`<div class="pd-pack-section"><b>${esc(x.signal || x.title || 'Observation')}</b><p>${esc(x.outcome || x.line || 'Outcome not returned')}</p>${x.driver?`<p>${esc(x.driver)}</p>`:''}${x.action?`<p>${esc(x.action)}</p>`:''}</div>`).join('');
    const prompts=(Array.isArray(d.soon_prompts)?d.soon_prompts:[]).map(text=>`<li>${esc(text)}</li>`).join('');
    body.innerHTML=sections || '<p>No measured sections returned. Use the conversation to establish what is happening.</p>';
    if(prompts)body.insertAdjacentHTML('beforeend',`<div class="pd-pack-section"><b>For the conversation</b><ul>${prompts}</ul></div>`);
  }catch(_){if(same())ov.querySelector('#cp-body').textContent='The coaching pack is unavailable. Close and try again.';else ov.remove();}
}

function _posOrg() {
  // The real org, read from the rows that exist — never the 5-name
  // matrix (that stays what it is: a permissions gate, not a chart).
  const rows = (_posState.teamRoster || []).filter(r => (r.status || 'active') === 'active');
  if (!rows.length) return '<div class="pos-empty" style="padding:40px"><div class="pos-empty__i">…</div>Open the Team tab once and the org fills from the same read.</div>';
  const byDept = {};
  rows.forEach(r => { const d = r.department || 'No department yet'; (byDept[d] = byDept[d] || []).push(r); });
  const isLead = r => /manager|head|cmo|lead/i.test(r.role || '');
  const short = n => { const t = String(n || '').trim().split(/\s+/); return t.length > 1 ? `${t[0]} ${[...t[t.length - 1]][0]}.` : (t[0] || '—'); };
  const dept = ([d, list]) => `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">${esc(d)} · ${list.length}</span></div>
      <div class="pos-org__chips">${list.sort((a, b2) => (isLead(b2) ? 1 : 0) - (isLead(a) ? 1 : 0)).map(r =>
        `<span class="pos-org__chip ${isLead(r) ? 'lead' : ''}" title="${esc(r.full_name || '')}">${esc(short(r.full_name))}${isLead(r) ? ` · ${esc(r.role)}` : ''}</span>`).join('')}</div></div>`;
  return `<div class="pos-org">${Object.entries(byDept).sort((a, b2) => b2[1].length - a[1].length).map(dept).join('')}</div>`;
}

async function _posTeamLoad() {
  try {
    const [d, m, bf, lc, uk] = await Promise.all([
      api('/api/hr/staff?strict=1'),
      api('/api/people/identity-meter').catch(() => null),
      api('/api/people/benefits').catch(() => null),
      api('/api/people/lifecycle/board').catch(() => null),
      api('/api/people/uk-checklist').catch(() => null),
    ]);
    _posState.teamRoster = (d && d.staff) || [];
    _posState.teamRosterError = null;   // a read that worked replaces an earlier failure
    _posState.idMeter = (m && m.ok) ? m : null;
    _posState.benefitsBoard = (bf && bf.ok !== false) ? bf : null;
    _posState.lifecycleBoard = (lc && lc.ok !== false) ? lc : null;
    _posState.ukChecklist = (uk && uk.ok) ? uk : null;
  } catch (e) {
    _posState.teamRoster = [];
    _posState.teamRosterError = (e && e.message) || 'Could not load the team list';
  }
  if (_posState.tab === 'team') { const su = document.getElementById('pos-surface'); if (su) { su.innerHTML = _posTeam(); _posBindTeam(); } }
}

function _posTeam() {
  const rows = _posState.teamRoster;
  if (!rows) return '<div class="pos-empty" style="padding:40px"><div class="pos-empty__i">…</div>Loading the team…</div>';
  if (_posState.teamRosterError && !rows.length) return `<div class="pos-empty" style="padding:40px"><div class="pos-empty__i">!</div>${esc(_posState.teamRosterError)}</div>`;
  const q = (_posState.teamQ || '').toLowerCase();
  const match = r => !q || `${r.full_name || ''} ${r.role || ''} ${r.department || ''}`.toLowerCase().includes(q);
  const active = rows.filter(r => (r.status || 'active') === 'active' && match(r));
  // The leave board lists the next 14 days; "off" and "on leave" mean today.
  const now = new Date(), today = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const leaveToday = x => !!x && typeof x.from === 'string' && x.from <= today && (x.to || x.from) >= today;
  // The board's "to" is the last day of leave, so the person is back the next calendar day.
  // This path does not read the rota: a weekend or an off day after the leave is not skipped.
  const dayAfter = d => { if (!/^\d{4}-\d{2}-\d{2}$/.test(String(d))) return ''; const t = new Date(d + 'T00:00:00Z'); t.setUTCDate(t.getUTCDate() + 1); return isNaN(t) ? '' : t.toISOString().slice(0, 10); };
  const byDept = {};
  active.forEach(r => { const d = r.department || 'No department yet'; (byDept[d] = byDept[d] || []).push(r); });
  const gaps = r => {
    const g = [];
    if (!r.email) g.push('no email');
    if (!r.phone) g.push('no phone');
    if (!(Number(r.monthly_salary) > 0)) g.push('no salary');
    // Working Floor WS-B: the identity meter knows what the row can't —
    // PIN enrolment, rota membership, till identity, birthday. Same chip
    // dialect; the words stay plain.
    const mp = (((_posState.idMeter || {}).people) || [])
      .find(x => String(x.id) === String(r.id));
    if (mp) {
      // dob stays OFF the tab chip (design verdict 2026-08-29: with all
      // 25 birthdays unset, one universal gap blanked the floor's whole
      // live-status vocabulary — the face and the arming card carry it).
      const word = { pin: 'no PIN yet', roster: 'not on the rota',
                     till_id: 'no till ID' };
      (mp.missing || []).forEach(f => { if (word[f]) g.push(word[f]); });
    }
    return g;
  };
  const faceGaps = r => {
    const g = gaps(r).slice();
    const mp = (((_posState.idMeter || {}).people) || [])
      .find(x => String(x.id) === String(r.id));
    if (mp && (mp.missing || []).includes('dob')) g.push('no birthday');
    return g;
  };
  const deptBlock = ([d, list]) => `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">${esc(d)} · ${list.length}</span></div>
    ${list.map(r => {
      const g = gaps(r);
      return `<button class="pos-team__row" data-tmrow="${esc(r.id)}">
        ${posAvatar(r.full_name, 'sm')}
        <span class="pos-team__b"><span class="pos-team__n">${esc(r.full_name || '—')}</span>
        <span class="pos-team__m">${esc(r.role || '')}${r.phone ? ' · ' + esc(r.phone) : ''}</span></span>
        ${g.length ? `<span class="pos-team__gap">${g.length > 1 ? g.length + ' gaps' : esc(g[0])}</span>` : ''}</button>`;
    }).join('')}</div>`;
  // HOUSE floor 2 — people are FILES you pull from a drawer, not rows in
  // tabs (the approved contract's central claim). Each file's face joins
  // what the view has already loaded — points off the overview
  // leaderboard, leave off the leave board, today's clock off the
  // attendance rows — and a GAP wears its chip on the tab, never hides
  // in a sub-tab. "Full file" keeps the deep HR sheet one tap away.
  // Classic renders the shipped rows above (same 'N gaps' dialect as the
  // drawer since p883; everything else byte-identical).
  // Floor-2 contract: the row shows "Chidera A.", never the 29-char legal
  // name — the department is already the panel heading, and the full name
  // rides title= and the Full file page.
  const posShortName = (n) => { const t = String(n || '').trim().split(/\s+/).filter(Boolean); if (!t.length) return '\u2014'; return t.length > 1 ? `${t[0]} ${[...t[t.length - 1]][0]}.` : t[0]; };
  const houseFile = (r) => {
    const g = gaps(r);
    const nm = (r.full_name || '').toLowerCase();
    const lb = ((_posState.overview || {}).leaderboard || [])
      .find(x => (x.name || '').toLowerCase() === nm);
    const leave = (((_posState.leaveBoard || {}).on_leave_next_14d) || [])
      .find(x => String(x.staff_id) === String(r.id) && leaveToday(x));   // today's row only; the board is in request order
    const att = (((_posState.attDay || {}).rows) || [])
      .find(x => (x.name || '').toLowerCase() === nm);
    const attChip = att ? ({ in: ['on shift', 'lime'], late: ['in late', 'warn'],
      not_in: ['not in', 'bad'], not_in_yet: ['not in yet', 'warn'],
      off: ['off today', ''], no_tracking: ['', ''] }[att.status] || ['', ''])
      : ['', ''];
    // Live state outranks record gaps on the tab (the approved chip
    // vocabulary — lime 'on shift' — must stay reachable); gaps chip
    // only when there is no live state to show.
    const tabChip = leave ? '<span class="pos-file__chip">on leave</span>'
      : attChip[0] ? `<span class="pos-file__chip ${attChip[1]}">${attChip[0]}</span>`
      : g.length
        ? `<span class="pos-file__chip warn">${g.length > 1 ? g.length + ' gaps' : esc(g[0])}</span>`
        : '';
    const cells = [];
    if (lb) cells.push(`<div class="pos-file__cell"><h6>Points · this week</h6><span class="pos-file__kv"><b class="num">${lb.points || 0}</b> pts${lb.quality_avg ? ` · ★ ${Number(lb.quality_avg).toFixed(1)}` : ''}${lb.streak_days ? ` · ${lb.streak_days}d streak` : ''}</span></div>`);
    cells.push(`<div class="pos-file__cell"><h6>Pay</h6><span class="pos-file__kv">${Number(r.monthly_salary) > 0 ? 'salary on file' : '<b class="pos-file__warn">nothing on file</b> — add a salary so the next run can draft their line'}</span></div>`);
    if (leave) cells.push(`<div class="pos-file__cell"><h6>Leave</h6><span class="pos-file__kv">${esc(leave.leave_type || leave.kind || 'off')}${dayAfter(leave.to) ? ` · back ${esc(dayAfter(leave.to))}` : ''}</span></div>`);
    if (att && att.in) cells.push(`<div class="pos-file__cell"><h6>Today</h6><span class="pos-file__kv">in at <b class="num">${esc(att.in)}</b>${att.location ? ` · ${esc(att.location)}` : ''}</span></div>`);
    const fg = faceGaps(r);
    if (fg.length > 1) cells.push(`<div class="pos-file__cell"><h6>Gaps</h6><span class="pos-file__kv">${esc(fg.join(' · '))}</span></div>`);
    return `<div class="pos-file" data-file="${esc(r.id)}">
      <button class="pos-file__tab" data-filetab="${esc(r.id)}" aria-expanded="false" title="${esc(r.full_name || '')}" aria-label="${esc(r.full_name || '')}">
        <span class="pos-file__dot" style="background:${posDeptColor(r.department || '')}"></span>
        <b>${esc(posShortName(r.full_name))}</b><span>${esc(r.role || '')}</span>
        ${tabChip}
      </button>
      <div class="pos-file__face">
        ${cells.join('')}
        <div class="pos-file__cell"><h6>The whole file</h6><button class="pos-file__more" data-tmrow="${esc(r.id)}">Full file →</button></div>
      </div>
    </div>`;
  };
  const houseDeptBlock = ([d, list]) => `<div class="pos-panel pos-panel--drawer"><div class="pos-panel__h"><span class="pos-panel__t">${esc(d)} · ${list.length}</span></div>
    ${list.map(houseFile).join('')}</div>`;
  const houseAttStrip = () => {
    const day = _posState.attDay;
    if (!day || !day.ok) return '';
    const rowsA = day.rows || [];
    const sm = day.summary || {};
    const notIn = rowsA.filter(x => x.status === 'not_in' || x.status === 'not_in_yet').length;
    // One per person: the union of staff ids off today and on leave today. A row without a staff id counts on its own.
    const who = (x, i, tag) => (x.staff_id == null || x.staff_id === '') ? tag + i : 'id:' + x.staff_id;
    const off = new Set([...rowsA.filter(x => x.status === 'off').map((x, i) => who(x, i, 'off#')),
      ...((((_posState.leaveBoard || {}).on_leave_next_14d) || []).filter(leaveToday).map((x, i) => who(x, i, 'leave#')))]).size;
    return `<div class="pos-hatt">
      <div><b class="num">${sm.in || 0}</b><span>in</span></div>
      <div class="late"><b class="num">${sm.late || 0}</b><span>late</span></div>
      <div class="miss"><b class="num">${notIn}</b><span>not in</span></div>
      <div><b class="num">${off}</b><span>off / leave</span></div>
    </div>`;
  };
  if (tosHouse()) {
    return `<div class="pos-team pos-team--house">
      ${houseAttStrip()}
      ${_posArmingCard()}
      ${_posCareCards()}
      ${_posTeamWorkspace()}
      <input class="pos-team__q" id="pos-team-q" type="search" placeholder="Find someone — name, role, department" value="${esc(_posState.teamQ || '')}">
      ${Object.entries(byDept).sort((a, b) => a[0].localeCompare(b[0])).map(houseDeptBlock).join('')
        || _posTeamEmpty(rows)}
      <div id="pos-timeoff-slot">${_posTimeoffCard()}</div>
      <div id="pos-coach-slot">${_posCoachingCard()}</div>
      <div id="pos-rota-slot">${_posRotaCard()}</div>
      <div id="pos-att-slot" hidden></div>
      <div class="pos-wall"><p>"A pattern is a question to ask, not a score — pay never docks for lateness."</p>
        <p>"Salaries are take-home — the business bears the tax. Nothing is filed from here until the accountant signs off."</p></div>
    </div>`;
  }
  return `<div class="pos-team">
    ${_posArmingCard()}
    ${_posCareCards()}
    <div id="pos-att-slot">${_posAttendanceCard()}</div>
    <div id="pos-timeoff-slot">${_posTimeoffCard()}</div>
    <div id="pos-coach-slot">${_posCoachingCard()}</div>
    <div id="pos-rota-slot">${_posRotaCard()}</div>
    ${_posTeamWorkspace()}
    <input class="pos-team__q" id="pos-team-q" type="search" placeholder="Find someone — name, role, department" value="${esc(_posState.teamQ || '')}">
    ${Object.entries(byDept).sort((a, b) => a[0].localeCompare(b[0])).map(deptBlock).join('')
      || _posTeamEmpty(rows)}</div>`;
}

// ── Weekly rota — see it, change it (WS-D2) ──────────────────────────
// The roster was migration-seeded and FROZEN: no screen or endpoint
// could change a shift pattern, so new hires never got rows. This grid
// is the editor: tap a day, set the window or make it an off day; the
// staffer gets a bell. Standing-week model — dated exceptions still ride
// the Time off lane.
const _ROTA_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
function _rotaHM(m) { return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`; }

async function _posRotaLoad() {
  try {
    const d = await api('/api/people/roster');
    _posState.rota = (d && d.grid) || {};
    // off_days = days holding a switched-off row (a closed store's
    // leftovers). home_gap = people whose home store is shut, so Teams
    // asks for a new one instead of rostering them at a closed door.
    _posState.rotaOff = (d && d.off_days) || {};
    _posState.rotaOffClosed = (d && d.off_days_closed) || {};
    _posState.rotaHomeGap = (d && d.home_gap) || [];
  }
  catch (_) { _posState.rota = null; }
  const slot = document.getElementById('pos-rota-slot');
  if (slot && _posState.tab === 'team') { slot.innerHTML = _posRotaCard(); _posBindRota(); }
}

function _posRotaCard() {
  const g = _posState.rota;
  if (g === undefined || g === null) return '';
  const rows = (_posState.teamRoster || []).filter(r => (r.status || 'active') === 'active');
  if (!rows.length) return '';
  const canEdit = memberCan('hr:staff:edit');
  return `<div class="pos-panel"><div class="pos-panel__h">
      <span class="pos-panel__t">Weekly rota</span>
      <button class="pos-panel__act" id="pos-rota-power">Power hours</button></div>
    <div class="pos-flag__m" style="padding:0 2px 6px">${canEdit ? 'Power hours shows where the money lands by hour — and you can still tap a day to change it.' : 'View only.'}</div>
    <div class="pos-rota__wrap"><table class="pos-rota"><tr><th></th>${_ROTA_DAYS.map(d => `<th>${d}</th>`).join('')}</tr>
    ${rows.map(r => {
      const mine = g[String(r.id)] || {};
      const noHome = (_posState.rotaHomeGap || []).some(x => String(x.staff_id) === String(r.id));
      return `<tr><td class="pos-rota__n">${esc((r.full_name || '—').split(' ')[0])}${noHome ? '<br><span class="pos-rota__gap">no live home store — pick one</span>' : ''}</td>
        ${_ROTA_DAYS.map((_, dow) => {
          const c = mine[String(dow)];
          const label = c ? `${_rotaHM(c.start_minute)}<br>${_rotaHM(c.end_minute)}` : '<span class="pos-rota__off">off</span>';
          return canEdit
            ? `<td><button class="pos-rota__c" data-rota="${esc(r.id)}:${dow}" data-rname="${esc(r.full_name || '')}">${label}</button></td>`
            : `<td><span class="pos-rota__c">${label}</span></td>`;
        }).join('')}</tr>`;
    }).join('')}</table></div>
    <div class="pos-flag__m" style="padding:6px 2px">Changing a day tells the person straight away. One-off days off still go through Time off.</div></div>`;
}

function _posBindRota() {
  document.getElementById('pos-rota-power')?.addEventListener('click', async () => {
    const sites = ((_posState.idMeter || {}).sites || []).filter(x2 => x2.pin_precise);
    const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Power hours — where the money lands</div><button class="csw-x">✕</button></div>
      <div class="pos-flag__m" style="padding:4px 4px 8px">Bars = share of the last 28 days' money in that hour. The number under each bar = rostered heads. An empty hour that carries money is the cheapest fix in retail.</div>
      <div id="ph-tabs" style="display:flex;gap:6px;flex-wrap:wrap;padding-bottom:8px">${sites.map((x2, i2) => `<button class="pos-arm__pin ${i2 === 0 ? 'on' : ''}" data-ph="${esc(x2.id)}">${esc(x2.name)}</button>`).join('') || 'Open the Team tab once to load the sites.'}</div>
      <div id="ph-body" class="pos-flag__m">Pick a store.</div>`);
    ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
    const load = async id => {
      const body = ov.querySelector('#ph-body');
      body.textContent = 'Reading 28 days of receipts…';
      try {
        const d = await api(`/api/people/power-hours?location_id=${encodeURIComponent(id)}`);
        if (!d || !d.ok) { body.textContent = (d && (d.error || d.note)) || 'Could not read'; return; }
        const max = Math.max(1, ...d.hours.map(h => h.revenue_share_pct));
        body.innerHTML = `<div class="ph-chart">${d.hours.map(h =>
          `<div class="ph-col"><div class="ph-bar" style="height:${Math.round(56 * h.revenue_share_pct / max) + 2}px" title="${h.revenue_share_pct}% of the money"></div>
           <span class="ph-h">${h.hour}</span><span class="ph-n ${h.rostered_heads ? '' : 'ph-n--empty'}">${h.rostered_heads || 0}</span></div>`).join('')}</div>
          <div class="pos-flag__m" style="padding-top:6px">${d.cover_pct != null ? `${d.cover_pct}% of the money falls in staffed hours.` : ''}</div>`;
      } catch (e) { body.textContent = e.message || 'Could not read'; }
    };
    ov.querySelectorAll('[data-ph]').forEach(b2 => b2.addEventListener('click', () => load(b2.getAttribute('data-ph'))));
    if (sites[0]) load(sites[0].id);
  });
  document.querySelectorAll('[data-rota]').forEach(b => b.addEventListener('click', () => {
    const [sid, dow] = b.getAttribute('data-rota').split(':');
    _posEditRotaDay(sid, Number(dow), b.getAttribute('data-rname') || 'this person');
  }));
}

function _posEditRotaDay(sid, dow, name) {
  if (!memberCan('hr:staff:edit') || !Number.isInteger(dow) || dow < 0 || dow > 6) return;
    const cur = ((_posState.rota || {})[sid] || {})[dow];
    // ONLY the days a SHUT store left behind get the closed-store line.
    // Most switched-off rows are ordinary off days at an open store, and
    // saying "a store that has since shut" about those sends a lead
    // hunting for a closed store that does not exist.
    const held = !cur && ((_posState.rotaOffClosed || {})[sid] || [])
                 .indexOf(Number(dow)) >= 0;
    const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">${esc(name)} · ${_ROTA_DAYS[Number(dow)]}</div><button class="csw-x">✕</button></div>
      <div class="pos-team__sheet">
        <p class="pos-flag__m">Weekly pattern. Saving changes this weekday and notifies the person. One-off leave uses Time off.</p>
        ${held ? '<div class="pos-flag__m" style="padding:0 2px 6px">They used to work this day at a store that has since shut. Saving it puts the day back on at their store now.</div>' : ''}
        <label class="pos-team__f"><span>Starts</span><input id="rota-start" type="time" value="${cur ? _rotaHM(cur.start_minute) : '09:00'}"></label>
        <label class="pos-team__f"><span>Ends</span><input id="rota-end" type="time" value="${cur ? _rotaHM(cur.end_minute) : '17:00'}"></label>
        <button class="pos-xcta" id="rota-save">Save this day</button>
        ${cur ? '<button class="pos-appr__no" id="rota-off" style="margin-top:4px">Make it an off day</button>' : ''}
      </div>`);
    ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
    const mins = v => { const [h, m] = String(v || '').split(':').map(Number); return (h * 60 + (m || 0)) || 0; };
    ov.querySelector('#rota-save').addEventListener('click', async () => {
      try {
        const res = await api(`/api/people/roster/${sid}/${dow}`, { method: 'PUT',
          body: JSON.stringify({ start_minute: mins(ov.querySelector('#rota-start').value),
                                 end_minute: mins(ov.querySelector('#rota-end').value) }) });
        if (res && res.ok) {
          // It takes BOTH flags to say anything but "saved", and each one
          // alone was wrong in its own way:
          //   home_gap alone  — announced "counts for none" about a day
          //                     that kept an OPEN store.
          //   location_id alone — nagged the 10 of 25 staff who have no
          //                     store BY DESIGN (Customer Service,
          //                     Marketing, Operations, Inventory; mig 310
          //                     calls it remote/mobile) to pick one.
          // home_gap is true only when they HAD a store and it shut. So:
          // no complaint unless a store was actually lost.
          let said = 'Saved — they have been told';
          if (res.home_gap && !res.location_id) {
            said = 'Saved — but their store has shut and they have no other, '
                 + 'so this day counts for none. Give them a store.';
          } else if (res.home_gap) {
            said = 'Saved — this day stays at the store it was already at. '
                 + 'Their own store has shut. Give them a new one.';
          }
          showToast(said);
          ov.remove(); _posRotaLoad();
        }
        else showToast((res && res.error) || 'Could not save');
      } catch (e) { showToast(e.message || 'Could not save'); }
    });
    ov.querySelector('#rota-off')?.addEventListener('click', async () => {
      try {
        const res = await api(`/api/people/roster/${sid}/${dow}`, { method: 'DELETE' });
        if (res && res.ok) { showToast('Off day set — they have been told'); ov.remove(); _posRotaLoad(); }
        else showToast((res && res.error) || 'Could not change it');
      } catch (e) { showToast(e.message || 'Could not change it'); }
    });
}

async function _posOpenRotaFromDay(sid, isoDate, name) {
  if (!memberCan('hr:staff:edit') || !sid || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return;
  const date = new Date(isoDate + 'T12:00:00Z');
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0,10) !== isoDate) return;
  const tenant = State.tenant?.id, jwt = State.jwt, view = State.view;
  if (!tenant || !jwt) return;
  try {
    const read = await api('/api/people/roster');
    if (tenant !== State.tenant?.id || jwt !== State.jwt || view !== State.view) return;
    if (!read?.ok || !read.grid || typeof read.grid !== 'object' || Array.isArray(read.grid)) { showToast('The rota could not be read. Try again.'); return; }
    _posState.rota = read.grid;
    _posEditRotaDay(sid, (date.getUTCDay() + 6) % 7, name);
  } catch (_) { if (tenant === State.tenant?.id && jwt === State.jwt) showToast('The rota could not be read. Try again.'); }
}

// ── Attendance today + coaching (WS-D, gap-map: BizEdge parity) ───────
// The daily attendance list the team ran BizEdge for — same statuses
// (in / late / not in yet / off), judged against each person's OWN shift
// start, honest 'no tracking' for roles without a clock. The coaching
// card is private to leads and framed for a conversation — never a
// leaderboard, never a pay deduction (Labour Act s.5(1) + cohesion rule).
function _posAttendanceCard() {
  const d = _posState.attDay;
  if (d === undefined) return '';
  if (!d || !d.ok) {
    const note = d?.reason === 'scope_unavailable'
      ? 'Attendance is outside your current access scope.'
      : 'Attendance could not be read. Try again shortly.';
    return `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Attendance today</span></div><p class="pos-flag__m" role="status">${note}</p></div>`;
  }
  const chip = st => ({
    in: ['in', 'ok'], late: ['late', 'warn'], not_in: ['not in', 'danger'],
    not_in_yet: ['not in yet', 'warn'], off: ['off today', ''],
    no_tracking: ['no clock for this role', ''],
  }[st] || [st, '']);
  const sm = d.summary || {};
  return `<div class="pos-panel"><div class="pos-panel__h">
      <span class="pos-panel__t">Attendance today</span>
      <span class="pos-panel__act" style="cursor:default">${sm.in || 0} in · ${sm.late || 0} late</span></div>
    ${(d.rows || []).map(r => {
      const [label, tone] = chip(r.status);
      // away_from_home (2026-08-20): the row names the store the person
      // checked in at; say when that isn't their own store so "Bola under
      // Chevron" doesn't read as a reassignment.
      const cover = (r.away_from_home && r.home_location)
        ? ' · cover (home ' + esc(r.home_location) + ')' : '';
      return `<div class="pos-appr__row">
        <div class="pos-appr__meta"><b>${esc(r.name || '—')}</b>
          <span>${esc(r.location || r.department || '')}${cover}${(r.expected && r.status !== 'off' && r.status !== 'no_tracking') ? ' · expected ' + esc(r.expected) : ''}${r.in ? ' · in ' + esc(r.in) : ''}</span></div>
        <span class="pos-att__chip ${tone ? 'pos-att__chip--' + tone : ''}">${esc(label)}</span></div>`;
    }).join('')}</div>`;
}

function _posCoachingCard() {
  const c = _posState.attCoach;
  if (c === undefined || !c || !c.ok) return '';
  const flagged = (c.people || []).filter(p => p.note);
  if (!flagged.length) return '';
  return `<div class="pos-panel"><div class="pos-panel__h">
      <span class="pos-panel__t">Worth a conversation · last ${c.days} days</span></div>
    ${flagged.slice(0, 5).map(p => `<div class="pos-appr__row">
      <div class="pos-appr__meta"><b>${esc(p.name)}</b><span>${esc(p.note)}</span></div></div>`).join('')}
    <div class="pos-flag__m" style="padding:8px 2px">Private to leads. A pattern is a question to ask, not a score — pay never docks for lateness.</div></div>`;
}

async function _posAttendanceLoad() {
  try {
    const [day, coach] = await Promise.all([
      api('/api/attendance/day').catch(() => null),
      api('/api/attendance/coaching').catch(() => null),
    ]);
    _posState.attDay = day; _posState.attCoach = coach;
  } catch (_) { _posState.attDay = null; _posState.attCoach = null; }
  if (_posState.tab !== 'team') return;
  // HOUSE floor 2: the attendance strip and every file's "today" cell
  // read this payload — repaint the whole drawer (the same wholesale
  // re-render the search box already does). Classic keeps slot writes.
  if (tosHouse()) {
    const su = document.getElementById('pos-surface');
    if (su) { su.innerHTML = _posTeam(); _posBindTeam(); }
    return;
  }
  const a = document.getElementById('pos-att-slot');
  if (a) a.innerHTML = _posAttendanceCard();
  const g = document.getElementById('pos-coach-slot');
  if (g) g.innerHTML = _posCoachingCard();
}

// ── Time off — who is away now and in the next two weeks (WS-C) ───────
// The leave board endpoint existed with zero UI callers; this card is its
// door. Coverage lives where the roster decisions are made.
function _posTimeoffCard() {
  const b = _posState.leaveBoard;
  if (b === undefined) return '';
  const rows = (b && b.on_leave) || [];
  const fmt = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
  return `<div class="pos-panel"><div class="pos-panel__h">
      <span class="pos-panel__t">Time off · now and next 14 days</span></div>
    ${rows.length ? rows.map(r => `<div class="pos-appr__row">
      <div class="pos-appr__meta"><b>${esc(r.name)}</b>
        <span>${esc(r.leave_type || r.kind || 'off')} · ${fmt(r.from)}${r.to && r.to !== r.from ? ' – ' + fmt(r.to) : ''}</span></div></div>`).join('')
      : '<div class="pos-flag__m" style="padding:8px 2px">Nobody is away or booked off in the next two weeks.</div>'}
    ${((b && b.blockers) || []).map(x => `<div class="pos-flag__m" style="padding:4px 2px;color:var(--warn)">${esc(x)}</div>`).join('')}</div>`;
}

async function _posTimeoffLoad() {
  try { _posState.leaveBoard = await api('/api/people/leave/board'); }
  catch (_) { _posState.leaveBoard = null; }
  if (_posState.tab !== 'team') return;
  // HOUSE floor 2: leave feeds the strip's off/leave cell and the files'
  // Leave cells — full drawer repaint, same rule as the attendance load.
  if (tosHouse()) {
    const su = document.getElementById('pos-surface');
    if (su) { su.innerHTML = _posTeam(); _posBindTeam(); }
    return;
  }
  const slot = document.getElementById('pos-timeoff-slot');
  if (slot) slot.innerHTML = _posTimeoffCard();
}

function _posBindTeam() {
  _posBindArming();
  const qEl = document.getElementById('pos-team-q');
  if (qEl) qEl.addEventListener('input', () => {
    _posState.teamQ = qEl.value;
    const su = document.getElementById('pos-surface');
    const keep = qEl.selectionStart;
    if (su) { su.innerHTML = _posTeam(); _posBindTeam(); const nq = document.getElementById('pos-team-q'); if (nq) { nq.focus(); nq.setSelectionRange(keep, keep); } }
  });
  document.querySelectorAll('[data-tmrow]').forEach(b =>
    b.addEventListener('click', () => _posTeamSheet(b.getAttribute('data-tmrow'))));
  const addBtn = document.getElementById('pos-team-add');
  if (addBtn) addBtn.addEventListener('click', () => _posAddPerson());
  // HOUSE floor 2: pulling a file out of the drawer. The tab toggles its
  // own card; the deep HR sheet stays one tap away on "Full file".
  document.querySelectorAll('[data-filetab]').forEach(b =>
    b.addEventListener('click', () => {
      const f = b.closest('.pos-file');
      if (!f) return;
      const out = f.classList.toggle('out');
      b.setAttribute('aria-expanded', String(out));
    }));
  // HOUSE floor 2's entrance (mockup .ro-file rise): armed once per view
  // mount in bindPeople, spent on the first paint that actually holds
  // files. Search re-renders arrive with the flag spent, so typing never
  // re-hides the drawer. Rides the foundation's .hs-reveal primitive
  // (house.css) -- one trigger releases every file, staggered by --hs-d.
  if (_posState.drawerReveal && window.EONHouse
      && document.documentElement.getAttribute('data-theme') === 'house') {
    const files = [...document.querySelectorAll('.pos-panel--drawer .pos-file')];
    if (files.length) {
      _posState.drawerReveal = false;
      files.forEach((el, i) => {
        el.classList.add('hs-reveal');
        el.style.setProperty('--hs-d', Math.min(i * 70, 560) + 'ms');
      });
      const first = document.querySelector('.pos-panel--drawer');
      window.EONHouse.signature(first, () => {
        files.forEach((el) => el.classList.add('hs-in'));
      });
    }
  }
}

// WS-G: company property on the profile — issue and return with the
// record, so offboarding's "collect assets" step checks a register, not
// anyone's memory.
// The Person Card (Working Floor WS-F): one staffer's joined truth —
// till receipts, attach, follow-through, level, the latest 1:1 and the
// coaching feed — read from the four modules that never met on one
// screen. Single-person by law; it compares nobody.
async function _tmWorkLoad(ov, staffId) {
  const el = ov.querySelector('#tm-work'), el2 = ov.querySelector('#tm-work2');
  if (!el) return;
  try {
    const c = await api(`/api/people/person-card?staff_id=${encodeURIComponent(staffId)}`);
    if (!c || !c.ok) { el.textContent = (c && c.error) || 'nothing to read yet'; return; }
    const bits = [];
    if (c.till && c.till.mapped) bits.push(`${posNgn(c.till.revenue_ngn) || '₦0'} rung · ${c.till.orders} orders`);
    else if (c.till) bits.push('till not mapped yet');
    if (c.attach && c.attach.attach_pct != null) bits.push(`${c.attach.attach_pct}% attach`);
    if (c.desk && c.desk.orders != null) bits.push(`${c.desk.orders} desk orders`);
    if (c.tasks && c.tasks.assigned) bits.push(`${c.tasks.done} of ${c.tasks.assigned} tasks done${c.tasks.on_time_pct != null ? ` · ${c.tasks.on_time_pct}% on time` : ''}`);
    el.textContent = bits.join(' · ') || 'no work signals yet';
    const sub = [];
    if (c.level && c.level.title) sub.push(`${c.level.title} · ${c.level.total_points || 0} pts`);
    if (c.checkin) sub.push(c.checkin.status === 'pending' ? `this month's one-to-one is waiting` : `one-to-one done for ${c.checkin.period}`);
    (c.observations || []).slice(0, 2).forEach(o => sub.push(`${o.when}: ${o.line}`));
    if (el2) el2.textContent = sub.join(' · ');
  } catch (e) { el.textContent = e.message || 'could not read'; }
}

async function _tmAssetsLoad(ov, staffId) {
  const el = () => ov.querySelector('#tm-assets');
  try {
    const d = await api(`/api/people/assets?staff_id=${encodeURIComponent(staffId)}`);
    if (!el()) return;
    const live = ((d && d.assets) || []).filter(a => !a.returned_at);
    el().innerHTML = live.length
      ? live.map(a => `${esc(a.item)} <button class="pos-appr__no" data-tmret="${esc(a.id)}" style="font-size:10px;padding:1px 7px">returned</button>`).join(' · ')
      : 'nothing issued';
    ov.querySelectorAll('[data-tmret]').forEach(b => b.addEventListener('click', async () => {
      try {
        const r2 = await api(`/api/people/assets/${b.getAttribute('data-tmret')}/return`, { method: 'POST', body: '{}' });
        if (r2 && r2.ok) { showToast('Returned'); _tmAssetsLoad(ov, staffId); }
      } catch (e) { showToast(e.message || 'Could not mark returned'); }
    }));
  } catch (_) { if (el()) el().textContent = 'nothing issued'; }
  const add = ov.querySelector('#tm-asset-add');
  if (add && !add.dataset.bound) {
    add.dataset.bound = '1';
    add.addEventListener('click', async () => {
      const inp = ov.querySelector('#tm-asset-new');
      const item = (inp && inp.value || '').trim();
      if (!item) return;
      try {
        const r2 = await api('/api/people/assets', { method: 'POST', body: JSON.stringify({ staff_id: staffId, item }) });
        if (r2 && r2.ok) { if (inp) inp.value = ''; showToast('Issued'); _tmAssetsLoad(ov, staffId); }
        else showToast('Could not issue');
      } catch (e) { showToast(e.message || 'Could not issue'); }
    });
  }

}

function _posTeamSheet(staffId) {
  const r = (_posState.teamRoster || []).find(x => String(x.id) === String(staffId));
  if (!r) return;
  const F = (id, label, val, ph) => `<label class="pos-team__f"><span>${label}</span>
    <input id="tm-${id}" value="${esc(val || '')}" placeholder="${ph || ''}"></label>`;
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">${esc(r.full_name || 'Staff profile')}</div><button class="csw-x">✕</button></div>
    <div class="pos-team__sheet">
      ${F('name', 'Full name', r.full_name)}
      ${F('phone', 'Phone', r.phone, '080…')}
      ${F('email', 'Email', r.email, 'name@…')}
      ${F('role', 'Role', r.role)}
      ${F('dept', 'Department', r.department)}
      <div class="pos-team__ro"><span>Pay</span><b>${Number(r.monthly_salary) > 0 ? posNgn(r.monthly_salary) + '/mo' : 'not on record'}</b><i>Pay changes go through an approval — ask in chat.</i></div>
      <div class="pos-team__ro"><span>Time off</span><b id="tm-leave">loading…</b><i>Sick days are capped at 3 a month.</i></div>
      <div class="pos-team__ro"><span>Attendance</span><b id="tm-att">loading…</b><i>Judged against their own shift start.</i></div>
      <div class="pos-team__ro"><span>The work · last 30 days</span><b id="tm-work">loading…</b><i id="tm-work2"></i></div>
      <div class="pos-team__ro"><span>Company property</span><b id="tm-assets">loading…</b>
        <span style="display:flex;gap:6px;margin-top:4px"><input id="tm-asset-new" placeholder="Issue an item — e.g. Samsung till phone" style="flex:1;padding:8px 10px;border-radius:8px;border:1px solid var(--border);background:var(--bg-card);color:var(--text);font:inherit">
        <button class="pos-appr__ok" id="tm-asset-add">Issue</button></span></div>
      <button class="pos-xcta" id="tm-save">Save</button>
    </div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  // Leave balances land async (WS-C) — the sheet opens instantly and the
  // Time off line fills in when the two reads return.
  (async () => {
    const el = () => ov.querySelector('#tm-leave');
    try {
      const q = t => api(`/api/people/leave/balance?staff_id=${encodeURIComponent(r.id)}&leave_type=${t}`).catch(() => null);
      const [a, sk, hist] = await Promise.all([
        q('annual'), q('sick'),
        api(`/api/attendance/history?staff_id=${encodeURIComponent(r.id)}&days=14`).catch(() => null),
      ]);
      if (!el()) return;
      const bits = [];
      if (a && a.ok) bits.push(`${a.available} of ${a.entitlement + (a.carried_over || 0)} annual days left`);
      if (sk && sk.ok) bits.push(`${sk.available} sick days left this year`);
      el().textContent = bits.join(' · ') || 'no balances yet';
      _tmAssetsLoad(ov, r.id);
      _tmWorkLoad(ov, r.id);
      const at = ov.querySelector('#tm-att');
      if (at) {
        if (hist && hist.ok) {
          const rows = hist.rows || [];
          const worked = rows.filter(x => x.status === 'in' || x.status === 'late').length;
          const late = rows.filter(x => x.status === 'late').length;
          const rostered = rows.filter(x => ['in', 'late', 'no_record', 'not_in_yet'].includes(x.status)).length;
          at.textContent = rostered
            ? `${worked} of ${rostered} rostered days in, ${late} late — last 14 days`
            : 'no rostered days in the last 14';
        } else at.textContent = 'no clock for this role';
      }
    } catch (_) { if (el()) el().textContent = 'no balances yet'; }
  })();
  ov.querySelector('#tm-save').addEventListener('click', async () => {
    const val = id => (ov.querySelector('#tm-' + id) || {}).value?.trim() ?? '';
    const updates = {};
    const map = { name: 'full_name', phone: 'phone', email: 'email', role: 'role', dept: 'department' };
    Object.entries(map).forEach(([id, col]) => { const v = val(id); if (v !== String(r[col] || '')) updates[col] = v; });
    if (!Object.keys(updates).length) { ov.remove(); return; }
    try {
      const res = await api(`/api/hr/staff/${r.id}`, { method: 'PATCH', body: JSON.stringify(updates) });
      if (res && res.message && String(res.message).includes('Updated')) {
        Object.assign(r, updates);
        showToast('Saved');
        ov.remove();
        const su = document.getElementById('pos-surface');
        if (su && _posState.tab === 'team') { su.innerHTML = _posTeam(); _posBindTeam(); }
      } else showToast((res && res.message) || 'Could not save');
    } catch (e) { showToast(e.message || 'Could not save'); }
  });
}

// ── Add a person: a staff profile only (no sign-in, PIN, message or pay) ──
// POST /api/hr/staff answers {message} only, even when its insert failed,
// so "added" is said only after the same workspace's team list shows one
// new row with these details. No answer is an unknown result: the sheet
// will not send again, so a slow reply can never make two people.
function _posTeamWorkspace() {
  const t = State.tenant || {};
  return `<p class="pos-team__m" id="pos-team-ws">Workspace: <b>${esc(t.name || t.slug || 'none selected')}</b>${t.slug ? ` (${esc(t.slug)})` : ''}
    ${memberCan('hr:staff:edit') ? ' <button type="button" class="pos-xcta" id="pos-team-add" style="min-height:44px">Add person</button>' : ''}</p>`;
}

function _posTeamEmpty(rows) {
  return `<div class="pos-empty" style="padding:40px"><div class="pos-empty__i">·</div>${(rows || []).length
    ? 'Nobody matches that search.' : 'No one is on this team yet.'}</div>`;
}

function _posAddCtx() {
  const u = State.user || {};
  return {slug: (State.tenant || {}).slug || '', actor: u.user_id || u.id || u.staff_id || ''};
}

function _posAddPerson() {
  if (!memberCan('hr:staff:edit')) return;
  const ctx = _posAddCtx();
  const isCurrent = () => { const n = _posAddCtx(); return !!ctx.slug && n.slug === ctx.slug && n.actor === ctx.actor; };
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Add a person</div><button class="csw-x">✕</button></div>
    <div class="pos-team__sheet pos-addp">
      <p class="pos-team__m">Adding to <b>${esc((State.tenant || {}).name || ctx.slug)}</b> (${esc(ctx.slug)}). This makes a staff profile only: no sign-in, PIN, message or pay.</p>
      <label class="pos-team__f"><span>Full name</span><input id="ap-name" autocomplete="off"></label>
      <label class="pos-team__f"><span>Role</span><input id="ap-role" autocomplete="off"></label>
      <label class="pos-team__f"><span>Department</span><input id="ap-dept" autocomplete="off"></label>
      <p class="pos-team__m" id="ap-msg" role="status" aria-live="polite"></p>
      <button type="button" class="pos-xcta" id="ap-save" style="min-height:44px">Add to the team</button>
    </div>`);
  const el = sel => ov.querySelector(sel), save = el('#ap-save');
  const say = t => { el('#ap-msg').textContent = t; };
  const plain = t => /duplicate key|already exists/i.test(t) ? 'Someone with these details is already saved here. Nothing was added: check the team list.'
    : !t || /^Error adding staff/i.test(t) ? 'EON could not save this person. Nothing was added. Try again, or ask the owner to check.' : t;
  let busy = false, unknown = false;
  // The once-only lock outlives this sheet: closing it mid-add and opening a
  // new one must not send a second create. One hold per workspace, so an add
  // in another workspace can neither take nor clear this one's; a definite
  // answer releases it, an unknown one keeps it until a reload.
  const mine = {slug: ctx.slug, unknown: false};
  const slot = ctx.slug;
  const holds = () => _posState.addHeld || (_posState.addHeld = {});
  const held = () => { const h = holds()[slot]; return !!h && h !== mine && h.slug === ctx.slug; };
  const heldSay = () => say(holds()[slot].unknown
    ? 'An earlier add got no clear answer. Do not add again: reload this page and check the team list first.'
    : 'Another add is still being sent. Wait for it, then check the team list.');
  const release = () => { if (holds()[slot] === mine) delete holds()[slot]; };
  const stuck = () => { unknown = true; mine.unknown = true; };
  const open = () => { busy = false; save.disabled = false; release(); };
  const lost = () => { stuck(); say(`The workspace changed while adding. They may already be on the ${ctx.slug} team: check that team list before adding again.`); };
  if (held()) { save.disabled = true; heldSay(); }
  el('.csw-x').addEventListener('click', () => ov.remove());
  save.addEventListener('click', async () => {
    if (busy || unknown) return;
    if (held()) { save.disabled = true; heldSay(); return; }
    const full_name = el('#ap-name').value.trim(), role = el('#ap-role').value.trim(), department = el('#ap-dept').value.trim();
    if (!full_name || !role) { say('Add a full name and a role.'); return; }
    if (!isCurrent()) { say('The workspace changed. Nothing was added. Close this and open it again.'); return; }
    busy = true; save.disabled = true; holds()[slot] = mine; say('Checking the team list…');
    const same = r => String(r.full_name || '').trim().toLowerCase() === full_name.toLowerCase();
    const read = async () => { try { const d = await api('/api/hr/staff?strict=1', {_isCurrent: isCurrent}); return d && Array.isArray(d.staff) ? d.staff : null; } catch (_) { return null; } };
    const before = await read();
    if (!isCurrent()) { release(); say('The workspace changed. Nothing was added. Close this and open it again.'); return; }
    if (!before) { open(); say('The team list could not be read, so nothing was added. Try again.'); return; }
    if (before.some(same)) { open(); say(`${full_name} is already on this team. Nothing was added.`); return; }
    say('Adding…');
    let res;
    try {
      res = await api('/api/hr/staff', {_isCurrent: isCurrent, method: 'POST',
        body: JSON.stringify({full_name, role, department: department || null, monthly_salary: 0})});
    } catch (e) {
      if (!isCurrent()) { lost(); return; }
      if (e && e.status && !e.timeout) { open(); say(plain(e.message)); return; }
      stuck();
      say('No answer from the server, so we cannot tell whether they were added. Do not add them again: close this and check the team list first.');
      return;
    }
    if (!isCurrent()) { lost(); return; }
    const said = String((res && res.message) || '');
    if (!said.startsWith('\u2705')) { open(); say(plain(said)); return; }
    const after = await read();
    if (!isCurrent()) { lost(); return; }
    if (!after) { stuck(); say('The server said yes, but the team list could not be read to confirm it. Check the team list before adding again.'); return; }
    const seen = new Set(before.map(r => String(r.id)));
    const added = after.filter(r => same(r) && r.role === role && !seen.has(String(r.id)));
    if (added.length !== 1) {
      stuck();
      say('The server said yes, but the team list does not show them. Do not add them again: close this and check the team list.');
      return;
    }
    release();
    _posState.teamRoster = after;
    showToast(`Added ${full_name}`);
    ov.remove();
    const su = document.getElementById('pos-surface');
    if (su && _posState.tab === 'team') { su.innerHTML = _posTeam(); _posBindTeam(); }
  });
}

function _posApprovalsRender(reqs) {
  const slot = document.getElementById('pos-appr-slot');
  if (!slot) return;
  if (!reqs || !reqs.length) { slot.innerHTML = '<div class="pos-panel"><h3>Shift requests</h3><p>No pending shift requests returned.</p></div>'; return; }
  const rows = reqs.map(r => {
    // A swap's own type lives in swap_kind, so POS_REQKIND alone read every
    // swap as "Shift swap" whether the coworker was trading a day or just
    // covering one — and the coworker was never named at all.
    // Both surfaces say the same words for the same request: "annual
    // leave", "off day", "shift swap" / "shift cover" (staff_os
    // _request_words). The web card used to read "Leave · annual" — the
    // middle-dot slot that sat empty on every swap.
    const kind = r.kind === 'leave'
      ? `${r.leave_type || 'annual'} leave`
      : r.kind === 'swap'
        ? (r.swap_kind === 'swap' ? 'shift swap' : 'shift cover')
        : (r.kind === 'off_day' ? 'off day' : (POS_REQKIND[r.kind] || r.kind));
    const who = r.kind === 'swap' && (r.counterparty_name || r.counterparty_email)
      ? ` · with ${r.counterparty_name || r.counterparty_email}`
        + (r.swap_kind === 'swap' && r.counterparty_date
           ? `, takes ${r.counterparty_date.slice(5)} back` : '')
      : '';
    const dates = (r.start_date || '').slice(5) + (r.end_date && r.end_date !== r.start_date ? '→' + r.end_date.slice(5) : '');
    const warns = r.coverage_warnings || [];
    const warn = warns.length
      ? `<div class="pos-appr__warn">${warns.length} ${warns.length === 1 ? 'day' : 'days'} would leave ${esc((warns[0] || {}).location || 'a store')} with nobody</div>` : '';
    return `<div class="pos-appr__row" data-id="${esc(r.id)}">
      <div class="pos-appr__meta"><b>${esc(r.requester_name || 'Staff')} · ${esc(kind)}${esc(who)}</b>
        <span>${esc(dates)} — ${esc((r.reason || '').trim() || 'No reason given')}</span></div>
      ${warn}
      <div class="pos-appr__acts">
        <button class="pos-appr__ok" data-appr="${esc(r.id)}"${warns.length ? ' data-cov="open"' : ''}>Approve</button>
        <button class="pos-appr__no" data-rej="${esc(r.id)}">Decline</button>
      </div></div>`;
  }).join('');
  slot.innerHTML = `<div class="pos-panel pos-appr"><div class="pos-panel__h">
      <span class="pos-panel__t">Approvals · time off & swaps</span>
      <span class="pos-panel__act">${reqs.length}</span></div>${rows}</div>`;
  slot.querySelectorAll('[data-appr]').forEach(b =>
    b.addEventListener('click', () => _posDecideRequest(b.getAttribute('data-appr'), true, b.getAttribute('data-cov'))));
  slot.querySelectorAll('[data-rej]').forEach(b =>
    b.addEventListener('click', () => _posDeclineInline(b)));
}

function _posDeclineInline(btn) {
  // decline reason typed IN PLACE — window.prompt is dead (operator law).
  const id = btn.getAttribute('data-rej');
  if (!id || btn.dataset.inline) return;
  btn.dataset.inline = '1';
  const wrap = document.createElement('span');
  wrap.style.cssText = 'display:inline-flex;gap:6px;align-items:center;margin-left:6px';
  wrap.innerHTML = '<input type="text" class="csw-input" placeholder="Reason (optional)" ' +
    'maxlength="300" style="font-size:11px;padding:4px 8px;width:160px">' +
    '<button type="button" class="hr-mini">Decline</button>';
  btn.style.display = 'none';
  btn.after(wrap);
  const input = wrap.querySelector('input');
  wrap.querySelector('button').setAttribute('data-rej', id);
  const restore = () => { wrap.remove(); btn.style.display = ''; delete btn.dataset.inline; };
  const submit = () => _posDecideRequest(id, false, null, (input.value || '').trim());
  wrap.querySelector('button').addEventListener('click', submit);
  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') submit();
    if (ev.key === 'Escape') restore();
  });
  input.focus();
}
function _posLoadApprovals() {
  const slot=document.getElementById('pos-appr-slot');
  if(!slot)return;
  const unavailable=()=>{if(slot.isConnected)slot.innerHTML='<div class="pos-panel"><h3>Shift requests</h3><p role="status">Shift requests could not be read. Refresh to try again.</p></div>';};
  api('/api/people/shift-requests/pending').then(res => {
    if(!slot.isConnected || document.getElementById('pos-appr-slot')!==slot)return;
    if(res?.ok!==true||!Array.isArray(res.requests)){unavailable();return;}
    _posApprovalsRender(res.requests);
  }).catch(unavailable);
}
const _posPendingDecisions = new Set();
async function _posDecideRequest(id, approve, coverage, declineReason) {
  if (!id || _posPendingDecisions.has(id)) return;
  _posPendingDecisions.add(id);
  const buttons = Array.from(document.querySelectorAll('[data-appr], [data-rej]'))
    .filter(b => b.getAttribute('data-appr') === id || b.getAttribute('data-rej') === id);
  buttons.forEach(b => { b.disabled = true; });
  // reason arrives from the inline input (_posDeclineInline) — never a prompt.
  const reason = approve ? null : (declineReason || '');
  try {
    const body = { approve: !!approve };
    if (reason) body.reason = reason;
    if (approve && coverage) body.coverage_decision = coverage;
    const result = await api('/api/people/shift-requests/' + encodeURIComponent(id) + '/decide',
              { method: 'POST', body: JSON.stringify(body) });
    if (result?.ok !== true) throw new Error(result?.error || 'Decision not confirmed. Reload before trying again.');
    showToast(result.warning || (approve ? 'Approved — roster updated' : 'Declined'));
    _posLoadApprovals();
  } catch (e) {
    showToast((e && e.message) || 'Could not decide');
  } finally {
    _posPendingDecisions.delete(id);
    buttons.forEach(b => { b.disabled = false; });
  }
}

/* ──────────── OVERVIEW · STAFF (self) ──────────── */
function _posStaffOverview() {
  const d = _posState.selfPerf;
  if (!d || !d.ok) return `<div class="pos-empty"><div class="pos-empty__i">○</div>${esc((d && d.error) || 'No performance data yet. Close a verified task to start your ring.')}</div>`;
  const ring = d.ring || {}, lvl = d.level || {}, st = d.streak || {}, share = d.my_share || {}, team = d.team_goal || {};
  const target = ring.target || 50;
  const pts = ring.points || 0;
  const r = 56, c = 2 * Math.PI * r, off = c * (1 - Math.max(0, Math.min(1, target ? pts / target : 0)));

  const ev = (d.receipt && d.receipt.events) || [];
  const weekTotal = ev.reduce((s, e) => s + (e.points || 0), 0);
  const receiptRows = ev.slice(0, 8).map(e => `<div class="pos-receipt__r">
    <span class="pos-receipt__t">${esc(e.behaviour_label || e.behaviour_code || e.reason || 'Points')}</span>
    <span class="pos-receipt__stars">${e.quality_rating ? '★'.repeat(e.quality_rating) : ''}</span>
    <span class="pos-receipt__pts">+${e.points || 0}</span></div>`).join('') ||
    '<div class="pos-receipt__r"><span class="pos-receipt__t" style="color:var(--ink-mute)">No points logged yet this week.</span><span></span><span></span></div>';

  const shareNgn = share.payout_ngn != null ? posNgn(share.payout_ngn)
    : (d.ngn_per_point ? posNgn(Math.round((share.points || 0) * d.ngn_per_point)) : (share.points || 0) + ' pts');

  const mine = (_posState.mine?.tasks || []).filter(t => !['verified', 'cancelled', 'done', 'completed'].includes(t.status));
  const myTasks = mine.length ? mine.slice(0, 6).map(t => {
    const ds = posDueState(t.due_at);
    return `<button class="pos-mytask" data-tid="${esc(t.id)}">
      <span class="pos-mytask__st">${posStatusGlyph(t.status, 14)}</span>
      <span class="pos-mytask__t">${esc(t.title || 'Task')}</span>
      ${ds ? `<span class="pos-mytask__due ${ds.cls === 'over' ? 'over' : ''}">${ds.txt}</span>` : ''}
      <span class="pos-mytask__pts">${posPtsLabel(t)}</span></button>`;
  }).join('') : '<div class="pos-empty" style="padding:24px"><div class="pos-empty__i">✓</div>No tasks routed to you. EON allocates from business insight.</div>';

  const toNext = lvl.next_at && lvl.total_points != null ? Math.max(0, lvl.next_at - lvl.total_points) : null;

  // Personal best — a private anchor (improvement is rewardable even when the
  // collective bar isn't hit). Never exposes anyone else's numbers.
  const pb = d.personal_best || {};
  // A personal best that could not be read is withheld, never shown as a zero best.
  const pbOff = pb.ok === false || pb.state === 'unavailable';
  const pbWk = pb.best_week_points || 0, pbThis = pb.this_week_points || 0;
  const pbPct = pbWk ? Math.min(100, Math.round(pbThis / pbWk * 100)) : 0;
  const pbPanel = `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Personal best</span>${pb.beating_best_week ? `<span class="pos-panel__act" style="color:var(--accent);cursor:default">new best week</span>` : ''}</div>
    <div class="pos-pbstats">
      <div class="pos-pbstat"><span class="pos-pbstat__v pos-mono">${pbOff ? '—' : pb.best_day_points || 0}</span><span class="pos-pbstat__l">best day</span></div>
      <div class="pos-pbstat"><span class="pos-pbstat__v pos-mono">${pbOff ? '—' : pbThis}</span><span class="pos-pbstat__l">this week</span></div>
      <div class="pos-pbstat"><span class="pos-pbstat__v pos-mono">${pbOff ? '—' : pbWk}</span><span class="pos-pbstat__l">best week</span></div>
    </div>
    <div class="pos-pbbar"><i style="width:${pbOff ? 0 : pbPct}%"></i></div>
    <div class="pos-pbcap">${pbOff ? "Your personal best can't be read right now." : pbWk ? (pb.beating_best_week ? 'Ahead of your best week — keep it.' : `${Math.max(0, pbWk - pbThis)} pts to beat your best week.`) : 'Stack a few days — your best week starts forming.'}</div></div>`;

  return `<div class="pos-self">
    <div class="pos-self__hero">
      <div class="pos-ring">
        <svg viewBox="0 0 128 128"><circle class="pos-ring__bg" cx="64" cy="64" r="56" stroke-width="9"/><circle class="pos-ring__fg" cx="64" cy="64" r="56" stroke-dasharray="${c.toFixed(1)}" stroke-dashoffset="${off.toFixed(1)}"/></svg>
        <div class="pos-ring__c"><div class="pos-ring__pts">${pts}</div><div class="pos-ring__tot pos-mono">/ ${target} · lvl ${lvl.level || 1}</div></div>
      </div>
      <div class="pos-self__meta">
        <div class="pos-self__nm">${esc(d.name || 'You')}</div>
        <div class="pos-self__rl">${esc(d.role || '')}${d.department ? ' · ' + esc(d.department) : ''}</div>
        <div class="pos-self__chips">
          ${lvl.title ? `<span class="pos-chip pos-chip--lvl">Level ${lvl.level} · ${esc(lvl.title)}</span>` : ''}
          ${(st.streak_days || 0) > 0 ? `<span class="pos-chip pos-chip--streak"><b>${st.streak_days}-day</b> streak${st.freeze_available ? ' · freeze ready' : ''}</span>` : ''}
          ${d.rank ? `<span class="pos-chip"><b>#${d.rank}</b> on team this week</span>` : ''}
        </div>
        <div class="pos-self__next">${toNext != null ? `<b>${toNext} pts to Level ${(lvl.level || 1) + 1}.</b> ` : ''}${ring.closed ? 'Ring closed today — nice.' : `${Math.max(0, target - pts)} pts to close today's ring.`}</div>
      </div>
    </div>

    <div class="pos-self__grid">
      <div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Points receipt · last 7 days</span><span class="pos-panel__act" style="cursor:default">+${weekTotal}</span></div>
        <div class="pos-receipt">${receiptRows}</div></div>
      <div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Your bonus share</span></div>
        <div class="pos-pb"><div class="pos-pb__big pos-mono">${shareNgn}</div>
          <div class="pos-pb__lbl">${share.share_pct != null ? `tracking <b>${share.share_pct}%</b> of the pool. ` : ''}${d.ngn_per_point ? `<b>₦${d.ngn_per_point}</b> per point.` : 'Pool not funded yet — projection.'}</div></div>
        ${team.target ? `<div style="margin-top:18px;padding-top:16px;border-top:1px solid var(--line-soft)">
          <div class="pos-panel__h" style="margin-bottom:10px"><span class="pos-panel__t">Team goal · this week</span><span class="pos-panel__act" style="cursor:default">${team.points || 0}/${team.target}</span></div>
          <div class="pos-pb"><div class="pos-pb__big pos-mono" style="font-size:38px;color:var(--accent)">${Math.round(team.pct || 0)}%</div><div class="pos-pb__lbl">${team.hit ? 'Hit — everyone shares the win.' : 'Win it together.'}</div></div></div>` : ''}
      </div>
    </div>

    ${pbPanel}

    <div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Your tasks · ${mine.length} open</span><button class="pos-panel__act" id="pos-self-board">Open board →</button></div>
      <div class="pos-mytasks">${myTasks}</div></div>
  </div>`;
}

function _posBindOverview() {
  // leaderboard rows → staff drill-down (reuses the HR staff sheet)
  document.querySelectorAll('#pos-surface .pos-lb__row[data-email]').forEach(r =>
    r.addEventListener('click', () => openHrStaff(_hrRowIdentity(r))));
  document.querySelectorAll('#pos-surface .pos-mytask[data-tid]').forEach(t =>
    t.addEventListener('click', () => posOpenDetail(t.getAttribute('data-tid'))));
  document.getElementById('pos-self-board')?.addEventListener('click', () => posSetTab('tasks'));
  // Wave-3: flags drill into the same staff sheet the leaderboard uses.
  document.querySelectorAll('#pos-surface .pos-flag[data-fem]').forEach(r => {
    const em = r.getAttribute('data-fem');
    if (em && em.includes('@')) r.addEventListener('click', () => openHrStaff(em));
  });
  // The bonus run + friction desk lost their page when ?v=hr lost its
  // menu tile — they live here now (wave-3 fold-in).
  document.getElementById('pos-bonus-run')?.addEventListener('click', () => openBonusSheet());
  document.getElementById('pos-frict-log')?.addEventListener('click', () => openFrictionReport());
  // Operations approval queue + reward desk (owner/manager view only).
  // Every lead, not just the owner-labelled state (complaints fix
  // 2026-09-01): the backend gates these at ANALYTICS_STAFF_VIEW.
  if (posIsLead()) { _posLoadDecisions(); _posLoadApprovals(); _posLoadRewardDesk(); }
}

// ── Reward desk (wave-3): EON proposes, the manager decides ─────────
// GET /api/people/reward-proposals is deterministic + receipt-backed;
// approving posts through the standard award door (two-phase + the
// 'manual' family dedup), so a period's proposal can't pay twice.
async function _posLoadRewardDesk() {
  const slot = document.getElementById('pos-reward-slot');
  if (!slot) return;
  let d = null;
  try { d = await api('/api/people/reward-proposals?days=7'); } catch (_) { /* explicit unavailable below */ }
  if(!slot.isConnected)return;
  if(d?.ok!==true||!Array.isArray(d.proposals)){slot.innerHTML='<div class="pos-panel"><h3>Reward proposals</h3><p role="status">Reward proposals could not be read. Refresh to try again.</p></div>';return;}
  const props = d.proposals;
  if (!props.length) { slot.innerHTML = '<div class="pos-panel"><h3>Reward proposals</h3><p>No reward proposals returned.</p></div>'; return; }
  slot.innerHTML = `<div class="pos-panel"><div class="pos-panel__h">
      <span class="pos-panel__t">EON proposes · rewards</span>
      <span class="pos-panel__act" style="cursor:default">${props.length}</span></div>
    ${props.map((p, i) => `<div class="pos-appr__row" data-rdi="${i}">
      <div class="pos-appr__meta"><b>${esc(p.name || '')} · +${p.points}</b>
        <span>${esc(p.title || '')}${(p.receipt || []).length ? ' — ' + esc(p.receipt[0]) : ''}</span></div>
      <div class="pos-appr__acts">${p.email
        ? `<button class="pos-appr__ok" data-rdok="${i}">Approve +${p.points}</button>`
        : '<span class="pos-flag__m">needs an email on the staff row</span>'}</div>
    </div>`).join('')}</div>`;
  _posState.rewardProps = props;
  slot.querySelectorAll('[data-rdok]').forEach(b =>
    b.addEventListener('click', () => _posApproveProposal(+b.getAttribute('data-rdok'), b)));
}

async function _posApproveProposal(i, btn) {
  const p = (_posState.rewardProps || [])[i];
  if (!p || !p.email) return;
  btn.disabled = true; btn.textContent = 'Paying…';
  const body = { email: p.email, points: p.points, reason: p.reason };
  try {
    // The award door is two-phase; the proposal card IS the preview, so
    // one manager tap arms and confirms in sequence.
    const pre = await api('/api/people/award', { method: 'POST', body: JSON.stringify(body) });
    const d = (pre && pre.requires_confirm)
      ? await api('/api/people/award', { method: 'POST', body: JSON.stringify({ ...body, confirm: true }) })
      : pre;
    if (d && d.ok) {
      btn.textContent = `Paid +${p.points} ✓`;
      showToast(`Awarded +${p.points} to ${p.name}`);
    } else if (d && d.duplicate) {
      btn.textContent = 'Already paid this period';
    } else {
      btn.disabled = false; btn.textContent = 'Retry';
    }
  } catch (e) { btn.disabled = false; btn.textContent = 'Retry'; }
}

/* ──────────── EXECUTION (performance analytics) ────────────
   2026-06-12 UI wave 2: the main-app Task OS (?v=tasks) is THE task
   execution surface — this tab stopped duplicating its list and now
   answers "how well is the team executing": gamification output,
   ratings, department throughput, overdue aging. Everything is computed
   client-side from the /api/tasks/board + /api/tasks/pulse payloads
   People OS already loads — no new backend. */

const POS_OPEN_ST = ['open', 'assigned', 'acknowledged', 'in_progress', 'blocked'];

function _posExecData(days, srcTasks) {
  // srcTasks (wave-2 privacy): non-leads pass their OWN tasks so the
  // Execution tab computes a personal view — the org board never reaches
  // a staff render (the Guide's own promise: "staff never see a ranking
  // of each other; leaderboards live on manager screens only").
  const tasks = srcTasks || _posState.tasks || [];
  const now = Date.now();
  const start = now - days * 864e5;
  const prevStart = now - 2 * days * 864e5;
  const doneTs = (t) => { const v = t.verified_at || t.completed_at; return v ? new Date(v).getTime() : null; };
  const verified = tasks.filter(t => t.status === 'verified');
  const inWin = verified.filter(t => { const ts = doneTs(t); return ts && ts >= start; });
  const inPrev = verified.filter(t => { const ts = doneTs(t); return ts && ts >= prevStart && ts < start; });
  const sumPts = (list) => list.reduce((s, t) => s + (t.points_awarded || 0), 0);
  // daily points trend, oldest → newest
  const trend = Array.from({ length: days }, (_, i) => {
    const d0 = start + i * 864e5, d1 = d0 + 864e5;
    return sumPts(inWin.filter(t => { const ts = doneTs(t); return ts >= d0 && ts < d1; }));
  });
  // top earners + points by department (from verified-in-window awards)
  const earners = {}, deptPts = {};
  inWin.forEach(t => {
    const who = t.assignee_name || 'Unassigned';
    earners[who] = (earners[who] || 0) + (t.points_awarded || 0);
    const dp = t.department || 'Unassigned';
    deptPts[dp] = (deptPts[dp] || 0) + (t.points_awarded || 0);
  });
  // ratings from verifies
  const rated = inWin.filter(t => t.quality_rating);
  const dist = [0, 0, 0, 0, 0];
  rated.forEach(t => { const r = Math.min(5, Math.max(1, t.quality_rating)); dist[r - 1]++; });
  const avgStar = rated.length ? rated.reduce((s, t) => s + t.quality_rating, 0) / rated.length : 0;
  const awaitingVerify = tasks.filter(t => t.status === 'completed' || t.status === 'done').length;
  const unrated = awaitingVerify + inWin.filter(t => !t.quality_rating).length;
  // department performance (live open/overdue + completed-this-week +
  // 30d execution rate = completed / created)
  const open = tasks.filter(t => POS_OPEN_ST.includes(t.status));
  const overdue = open.filter(t => t.due_at && new Date(t.due_at).getTime() < now);
  const wkStart = now - 7 * 864e5;
  const created30 = tasks.filter(t => t.created_at && new Date(t.created_at).getTime() >= now - 30 * 864e5);
  const depts = {};
  const bump = (d, k) => { const dp = d || 'Unassigned'; (depts[dp] = depts[dp] || { open: 0, overdue: 0, doneWk: 0, created30: 0, done30: 0 })[k]++; };
  open.forEach(t => bump(t.department, 'open'));
  overdue.forEach(t => bump(t.department, 'overdue'));
  verified.filter(t => { const ts = doneTs(t); return ts && ts >= wkStart; }).forEach(t => bump(t.department, 'doneWk'));
  created30.forEach(t => bump(t.department, 'created30'));
  created30.filter(t => ['verified', 'done', 'completed'].includes(t.status)).forEach(t => bump(t.department, 'done30'));
  // overdue aging buckets + oldest offenders
  const aged = overdue.map(t => ({ t, d: (now - new Date(t.due_at).getTime()) / 864e5 }));
  const buckets = [
    ['1-3d', aged.filter(x => x.d < 3).length],
    ['3-7d', aged.filter(x => x.d >= 3 && x.d < 7).length],
    ['7d+', aged.filter(x => x.d >= 7).length],
  ];
  const oldest = aged.sort((a, b) => b.d - a.d).slice(0, 5);
  return { pts: sumPts(inWin), ptsPrev: sumPts(inPrev), verified: inWin.length, trend,
           earners, deptPts, avgStar, dist, ratedCount: rated.length, unrated,
           awaitingVerify, depts, openCount: open.length, overdueCount: overdue.length,
           buckets, oldest };
}

const POS_EXEC_DAYS = [7, 30];

function _posExec() {
  const days = _posState.execDays || 7;
  const lead = posIsLead();
  const x = _posExecData(days, lead ? null : ((_posState.mine || {}).tasks || []));
  const delta = x.ptsPrev ? Math.round((x.pts - x.ptsPrev) / x.ptsPrev * 100) : null;

  // ── toolbar: window toggle + the deep-link into the real Task OS ──
  const bar = `<div class="pos-xbar">
    <span class="pos-repdays">${POS_EXEC_DAYS.map(n =>
      `<button class="pos-repday ${n === days ? 'on' : ''}" data-xdays="${n}" aria-pressed="${n === days}">${n}d</button>`).join('')}</span>
    <span class="pos-xbar__sp"></span>
    <button class="pos-xcta" id="pos-open-tos">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
      Open Task OS</button>
  </div>`;

  // ── hero: the four execution figures ──
  const hero = `<div class="pos-pulse">
    <div class="pos-pulse__c"><span class="pos-pulse__l">${lead ? 'Task points' : 'My task points'} · last ${days}d</span><span class="pos-pulse__v">${x.pts}</span>
      <span class="pos-pulse__d ${delta == null ? 'flat' : delta >= 0 ? 'up' : 'down'}">${delta == null ? 'no prior window' : (delta >= 0 ? '+' : '') + delta + '% vs prev ' + days + 'd'}</span></div>
    <div class="pos-pulse__c"><span class="pos-pulse__l">Verified · last ${days}d</span><span class="pos-pulse__v">${x.verified}</span>
      <span class="pos-pulse__d ${x.awaitingVerify ? 'flat' : 'up'}">${x.awaitingVerify ? x.awaitingVerify + ' awaiting verify' : 'queue clear'}</span></div>
    <div class="pos-pulse__c"><span class="pos-pulse__l">Avg quality</span><span class="pos-pulse__v">${x.avgStar ? x.avgStar.toFixed(1) : '—'}</span>
      <span class="pos-pulse__d flat">${x.ratedCount} rated verifies</span></div>
    <div class="pos-pulse__c"><span class="pos-pulse__l">Overdue now</span><span class="pos-pulse__v" ${x.overdueCount ? 'style="color:var(--danger)"' : ''}>${x.overdueCount}</span>
      <span class="pos-pulse__d ${x.overdueCount ? 'down' : 'up'}">${x.overdueCount ? 'of ' + x.openCount + ' open' : x.openCount + ' open · on time'}</span></div>
  </div>`;

  // ── points trend ──
  const maxT = Math.max(1, ...x.trend);
  const trend = `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Points trend · daily · last ${days}d</span><span class="pos-panel__act" style="cursor:default">${x.pts} pts total</span></div>
    <div class="pos-xtr" role="img" aria-label="Daily team points, last ${days} days">${x.trend.map(v =>
      `<span class="pos-xtr__b ${v ? '' : 'pos-xtr__b--zero'}" style="height:${Math.max(5, Math.round(v / maxT * 100))}%" title="+${v} pts"></span>`).join('')}</div>
    <div class="pos-xtr__cap"><span>${days}d ago</span><span>today</span></div></div>`;

  // ── top earners ──
  const earnRows = Object.entries(x.earners).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const maxE = Math.max(1, ...earnRows.map(e => e[1]));
  const earners = `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Top earners · last ${days}d</span></div>
    <div class="pos-lb">${earnRows.length ? earnRows.map(([who, p], i) =>
      `<div class="pos-lb__row ${i === 0 ? 'pos-lb__row--lead' : ''}" style="cursor:default">
        <span class="pos-lb__rank">${i + 1}</span>
        <span class="pos-lb__who">${posAvatar(who)}<span style="min-width:0"><span class="pos-lb__nm" style="display:block">${esc(who)}</span></span></span>
        <span class="pos-lb__bar"><i style="width:${Math.round(p / maxE * 100)}%"></i></span>
        <span class="pos-lb__pts">${p}</span></div>`).join('')
      : '<div class="pos-empty" style="padding:26px"><div class="pos-empty__i">○</div>No points in the last ' + days + ' days.</div>'}</div></div>`;

  // ── points by department ──
  const dpRows = Object.entries(x.deptPts).sort((a, b) => b[1] - a[1]);
  const maxDp = Math.max(1, ...dpRows.map(d => d[1]));
  const deptPoints = `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Points by department · last ${days}d</span></div>
    <div class="pos-dept">${dpRows.length ? dpRows.map(([d, p]) =>
      `<div class="pos-dept__r"><span class="pos-dept__n"><span class="pos-dept__dot" style="background:${posDeptColor(d)}"></span><span>${esc(d)}</span></span>
        <span class="pos-dept__track"><i style="width:${Math.round(p / maxDp * 100)}%;background:${posDeptColor(d)}"></i></span>
        <span class="pos-dept__v">${p}</span></div>`).join('')
      : '<div class="pos-empty" style="padding:26px"><div class="pos-empty__i">○</div>Nothing verified yet in this window.</div>'}</div></div>`;

  // ── ratings: star distribution from verifies ──
  const totRated = Math.max(1, x.ratedCount);
  const starsRows = [5, 4, 3, 2, 1].map(s => {
    const n = x.dist[s - 1];
    return `<div class="pos-xstar"><span class="pos-xstar__l">${s} <span class="st">★</span></span>
      <span class="pos-xstar__track"><i style="width:${Math.round(n / totRated * 100)}%"></i></span>
      <span class="pos-xstar__v">${n}</span></div>`;
  }).join('');
  const ratings = `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Quality ratings · last ${days}d</span>
      <span class="pos-panel__act" style="cursor:default">${x.avgStar ? x.avgStar.toFixed(1) + ' avg' : '—'}</span></div>
    ${starsRows}
    <div class="pos-xunrated ${x.unrated ? 'warn' : ''}">${x.unrated
      ? `${x.unrated} completion${x.unrated === 1 ? '' : 's'} without a rating — rate on verify so points stay fair.`
      : 'Every verified completion carries a rating.'}</div></div>`;

  // ── department performance: open / overdue / done-wk + execution rate ──
  const deptRows = Object.entries(x.depts).sort((a, b) => (b[1].open + b[1].doneWk) - (a[1].open + a[1].doneWk));
  const deptPerf = `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Department performance</span>
      <span class="pos-panel__act" style="cursor:default">execution rate · 30d</span></div>
    <div class="pos-xdept">${deptRows.length ? deptRows.map(([d, v]) => {
      const rate = v.created30 ? Math.min(100, Math.round(v.done30 / v.created30 * 100)) : null;
      const tone = rate == null ? 'var(--ink-faint)' : rate >= 70 ? 'var(--ok)' : rate >= 40 ? 'var(--warn)' : 'var(--danger)';
      return `<div class="pos-xdept__r">
        <div class="pos-xdept__top"><span class="pos-xdept__n"><span class="pos-dept__dot" style="background:${posDeptColor(d)}"></span>${esc(d)}</span>
          <span class="pos-xdept__chips"><span class="pos-xdept__chip">${v.open} open</span><span class="pos-xdept__chip ${v.overdue ? 'bad' : ''}">${v.overdue} overdue</span><span class="pos-xdept__chip ok">${v.doneWk} done · wk</span></span></div>
        <div class="pos-xdept__bar"><span class="pos-dept__track"><i style="width:${rate == null ? 0 : rate}%;background:${tone}"></i></span>
          <span class="pos-xdept__rate" style="color:${rate != null && rate < 40 ? 'var(--danger)' : 'var(--ink-dim)'}">${rate == null ? '—' : rate + '%'}</span></div>
      </div>`;
    }).join('') : '<div class="pos-empty" style="padding:26px"><div class="pos-empty__i">○</div>No departmental tasks yet.</div>'}</div></div>`;

  // ── overdue aging ──
  const aging = `<div class="pos-panel"><div class="pos-panel__h"><span class="pos-panel__t">Overdue aging</span>
      <span class="pos-panel__act" style="cursor:default">${x.overdueCount} overdue</span></div>
    <div class="pos-xage__buckets">${x.buckets.map(([l, n]) =>
      `<div class="pos-xage__bucket ${n ? (l === '7d+' ? 'crit' : 'warn') : ''}"><span class="pos-xage__bv pos-mono">${n}</span><span class="pos-xage__bl">${l}</span></div>`).join('')}</div>
    ${x.oldest.length ? `<div class="pos-xage__list">${x.oldest.map(({ t, d }) =>
      `<button class="pos-xage__row" data-tid="${esc(t.id)}">
        <span class="pos-xage__t">${esc(t.title || 'Untitled')}</span>
        <span class="pos-xage__who">${posAvatar(t.assignee_name, 'sm')}<span>${esc((t.assignee_name || 'Unassigned').split(' ')[0])}</span></span>
        <span class="pos-xage__age">${d < 1 ? '<1d' : Math.round(d) + 'd'} over</span></button>`).join('')}</div>`
      : '<div class="pos-empty" style="padding:20px"><div class="pos-empty__i">✓</div>Nothing overdue. Clean board.</div>'}</div>`;

  // Wave-3: the verify queue — the single bottleneck between effort and
  // pay (122 of 134 completions in 30d paid nothing). One tap = verify at
  // a neutral ★4; Open = full detail sheet for a closer look; the 05:10
  // auto-verify cron sweeps whatever the desk leaves past 48h.
  const vqRows = (_posState.tasks || []).filter(t => t.status === 'completed' || t.status === 'done');
  const verifyQ = !vqRows.length ? '' : `<div class="pos-panel"><div class="pos-panel__h">
      <span class="pos-panel__t">Verify queue · ${vqRows.length}</span>
      <button class="pos-panel__act" id="pos-vq-all">Verify all ${vqRows.length} · ★4</button></div>
    <p class="pos-flag__m" style="padding:8px 2px">Quick verify rates work ★4: 1.25× the quality-rated points formula. Fixed-point tasks stay unchanged. Showing ${Math.min(8, vqRows.length)} of ${vqRows.length} queued tasks; Verify all covers all ${vqRows.length}, including any not shown. Open a task to choose a different rating.</p>
    ${vqRows.slice(0, 8).map(t => `<div class="pos-appr__row" data-vqr="${esc(t.id)}">
      <div class="pos-appr__meta"><b>${esc(t.title || 'Untitled')}</b><span>${esc(t.assignee_name || 'Unassigned')}${t.completed_at ? ' · done ' + new Date(t.completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}</span></div>
      <div class="pos-appr__acts"><button class="pos-appr__ok" data-vq="${esc(t.id)}">Verify ★4</button>
        <button class="pos-appr__no" data-vqopen="${esc(t.id)}">Open</button></div></div>`).join('')}
    ${vqRows.length > 8 ? `<div class="pos-flag__m" style="padding:8px 2px">${vqRows.length - 8} more — Verify all covers every one.</div>` : ''}</div>`;

  // Wave-2 privacy: org-wide boards (named earners, department points and
  // performance, named overdue offenders) are MANAGER surfaces. A staffer
  // gets their own numbers + their own overdue list, per the Guide's
  // cohesion promise.
  if (!lead) {
    return `<div class="pos-exec">${bar}${hero}${trend}
      <div class="pos-exec__grid">${ratings}</div>
      ${aging}
      <div class="pos-empty" style="padding:14px;font-size:12.5px">Team boards live on manager screens — your rank inside your role is on your Score card.</div></div>`;
  }
  return `<div class="pos-exec">${bar}${hero}${verifyQ}${trend}
    <div class="pos-exec__grid">${earners}${deptPoints}${ratings}${deptPerf}</div>
    ${aging}</div>`;
}

function _posBindExec() {
  const surface = document.getElementById('pos-surface');
  surface?.querySelectorAll('[data-xdays]').forEach(b => b.addEventListener('click', () => {
    _posState.execDays = +b.getAttribute('data-xdays');
    _posRenderActive();
  }));
  // Deep-link into the full Task OS — same nav pattern the rest of app.js
  // uses (set State.view, re-render).
  document.getElementById('pos-open-tos')?.addEventListener('click', () => { State.view = 'tasks'; render(); });
  surface?.querySelectorAll('.pos-xage__row[data-tid]').forEach(r =>
    r.addEventListener('click', () => posOpenDetail(r.getAttribute('data-tid'))));
  surface?.querySelectorAll('[data-vq]').forEach(b =>
    b.addEventListener('click', () => _posQuickVerify(b.getAttribute('data-vq'), b)));
  surface?.querySelectorAll('[data-vqopen]').forEach(b =>
    b.addEventListener('click', () => posOpenDetail(b.getAttribute('data-vqopen'))));
  document.getElementById('pos-vq-all')?.addEventListener('click', _posVerifyAll);
}

// ── quick verify (wave-3): one tap pays the completion; the row patches
// itself in place (nothing shakes, no scroll loss). Verify-all walks the
// queue sequentially then refreshes counts ONCE.
async function _posQuickVerify(tid, btn) {
  if (btn) { btn.disabled = true; btn.textContent = 'Verifying…'; }
  try {
    const d = await api(`/api/tasks/${encodeURIComponent(tid)}/verify`, {
      method: 'POST',
      body: JSON.stringify({ passed: true, rating: 4, note: 'Quick-verified from People OS' }),
    });
    if (d && d.ok !== false) {
      const t = _posState.byId[tid]; if (t) t.status = 'verified';
      if (btn) btn.textContent = `Paid${d && d.award && d.award.points_awarded ? ' +' + d.award.points_awarded : ''} ✓`;
      document.querySelector(`[data-vqr="${tid}"]`)?.style.setProperty('opacity', '.55');
      return true;
    }
    if (btn) { btn.disabled = false; btn.textContent = 'Retry'; if (d && d.error) btn.title = d.error; }
  } catch (e) { if (btn) { btn.disabled = false; btn.textContent = 'Retry'; } }
  return false;
}

async function _posVerifyAll() {
  const ids = (_posState.tasks || []).filter(t => t.status === 'completed' || t.status === 'done').map(t => t.id);
  const all = document.getElementById('pos-vq-all');
  if (all) { all.disabled = true; all.textContent = 'Verifying…'; }
  let ok = 0;
  for (const id of ids) {
    if (await _posQuickVerify(id, document.querySelector(`[data-vq="${id}"]`))) ok++;
  }
  showToast(`${ok} of ${ids.length} verified`);
  await _posLoad();
}

/* ──────────── GOALS & OKRs (mig 262 — live backend) ────────────
   UI wave 2 2026-06-12. GET/POST/PATCH /api/people/goals. auto_track
   goals are refreshed by EON at read time → "live" badge. Grouped
   company (no department/owner) / department / personal; closed
   (achieved · missed · archived) collapsed below. */

function _posGoals() {
  return `<div class="pos-goals" id="pos-goals">
    <div class="pos-skel" style="height:96px;margin-bottom:12px"></div>
    <div class="pos-skel" style="height:96px"></div>
  </div>`;
}

function _posBindGoals() { _posLoadGoals(); }

async function _posLoadGoals() {
  const el = document.getElementById('pos-goals');
  if (!el) return;
  let r;
  try { r = await api('/api/people/goals?status=all'); }
  catch (e) {
    el.innerHTML = `<div class="pos-empty"><div class="pos-empty__i">○</div>${(e.status === 403 || /403|forbidden/i.test(e.message || ''))
      ? 'Goals are a manager surface — ask an admin for access.'
      : esc(e.message || e)}</div>`;
    return;
  }
  if (!r || !r.ok) {
    el.innerHTML = `<div class="pos-empty"><div class="pos-empty__i">!</div>${esc((r && r.error) || "Couldn't load goals.")}</div>`;
    return;
  }
  _posState.goals = r.goals || [];
  _posRenderGoals();
}

function _posGoalVal(v, unit) {
  if (v == null) return '—';
  const u = String(unit || '').toLowerCase();
  if (u === 'ngn' || u === '₦' || u === 'naira') return posNgn(v) || '₦0';
  if (u === '%' || u === 'pct' || u === 'percent') return (Math.round(v * 10) / 10) + '%';
  const n = v >= 1e6 ? (v / 1e6).toFixed(v % 1e6 ? 1 : 0) + 'M'
          : v >= 1e3 ? Math.round(v / 1e3) + 'k' : String(Math.round(v * 10) / 10);
  return unit ? `${n} ${unit}` : n;
}

function _posGoalCard(g) {
  const pct = Math.max(0, Math.min(100, Math.round(g.progress_pct || 0)));
  const r = 23, c = 2 * Math.PI * r, off = (c * (1 - pct / 100)).toFixed(1);
  const closed = g.status !== 'active';
  const tone = g.status === 'achieved' ? 'var(--ok)'
             : g.status === 'missed' ? 'var(--danger)'
             : g.pace === 'off_track' ? 'var(--warn)' : 'var(--accent)';
  const paceChip = !closed && g.pace
    ? `<span class="pos-goal__pace ${g.pace === 'on_track' ? 'ok' : 'warn'}">${g.pace === 'on_track' ? 'on pace' : 'off pace'}</span>` : '';
  const liveBadge = g.auto_track
    ? `<span class="pos-goal__live" title="EON tracks this metric live"><i></i>live</span>` : '';
  const who = g.owner_name ? esc(g.owner_name) : (g.department ? esc(g.department) : 'Company');
  const ends = g.period_end
    ? new Date(g.period_end + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null;
  return `<button class="pos-goal ${closed ? 'pos-goal--closed' : ''}" data-gid="${esc(g.id)}">
    <span class="pos-goal__ring">
      <svg viewBox="0 0 52 52"><circle class="pos-ring__bg" cx="26" cy="26" r="${r}" stroke-width="5"/>
        <circle cx="26" cy="26" r="${r}" fill="none" stroke="${tone}" stroke-width="5" stroke-linecap="round"
          stroke-dasharray="${c.toFixed(1)}" stroke-dashoffset="${off}"/></svg>
      <span class="pos-goal__pct pos-mono">${pct}</span>
    </span>
    <span class="pos-goal__body">
      <span class="pos-goal__t">${esc(g.title || 'Goal')}${liveBadge}${paceChip}</span>
      <span class="pos-goal__nums pos-mono">${_posGoalVal(g.current_value, g.unit)} <span class="of">/ ${_posGoalVal(g.target_value, g.unit)}</span></span>
      <span class="pos-goal__meta">${who}${ends ? ` · ends ${ends}` : ''}${closed ? ` · ${esc(g.status)}` : ''}</span>
    </span>
  </button>`;
}

function _posRenderGoals() {
  const el = document.getElementById('pos-goals');
  if (!el) return;
  const all = _posState.goals || [];
  const active = all.filter(g => g.status === 'active');
  const closed = all.filter(g => g.status !== 'active');
  const company = active.filter(g => !g.department && !g.owner_name);
  const dept = active.filter(g => g.department && !g.owner_name);
  const personal = active.filter(g => g.owner_name);
  const lead = posIsLead();
  const group = (label, list) => list.length
    ? `<div class="pos-goal__sec">${label} · ${list.length}</div>${list.map(_posGoalCard).join('')}` : '';
  const addBtn = lead
    ? `<button class="pos-xcta" id="pos-goal-add"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M12 5v14M5 12h14"/></svg>New goal</button>` : '';
  const emptyState = `<div class="pos-empty" style="padding:40px 16px"><div class="pos-empty__i">○</div>
    No goals yet. Set goals from chat — tell EON "set a goal: ₦130M June revenue" — or add one here.</div>`;
  el.innerHTML = `
    <div class="pos-xbar"><span class="pos-panel__t" style="margin:0">Goals & OKRs</span><span class="pos-xbar__sp"></span>${addBtn}</div>
    ${active.length ? group('Company', company) + group('Departments', dept) + group('Personal', personal) : emptyState}
    ${closed.length ? `<details class="pos-goal__closed"><summary class="pos-goal__sec" style="cursor:pointer">Achieved · missed · archived — ${closed.length}</summary>${closed.map(_posGoalCard).join('')}</details>` : ''}`;
  document.getElementById('pos-goal-add')?.addEventListener('click', () => _posGoalAdd());
  if (lead) el.querySelectorAll('.pos-goal[data-gid]').forEach(card =>
    card.addEventListener('click', () => {
      const g = (_posState.goals || []).find(x => x.id === card.getAttribute('data-gid'));
      if (g) _posGoalSheet(g);
    }));
}

/* quick-add sheet */
function _posGoalAdd() {
  const now = new Date();
  const d2 = (n) => String(n).padStart(2, '0');
  const ps = `${now.getFullYear()}-${d2(now.getMonth() + 1)}-01`;
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const pe = `${now.getFullYear()}-${d2(now.getMonth() + 1)}-${d2(lastDay)}`;
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">New goal</div><button class="csw-x">✕</button></div>
    <input class="csw-search" id="pos-g-title" placeholder="Goal — e.g. June revenue ₦130M" style="margin-bottom:8px"/>
    <div class="pos-goal__formrow">
      <input class="csw-search" id="pos-g-target" type="number" inputmode="decimal" placeholder="Target value"/>
      <select class="csw-search" id="pos-g-unit"><option value="ngn">₦</option><option value="%">%</option><option value="pts">pts</option><option value="">count</option></select>
    </div>
    <select class="csw-search" id="pos-g-metric" style="margin-bottom:8px">
      <option value="">Manual tracking — update by hand</option>
      <option value="monthly_revenue">Auto: monthly revenue (live)</option>
      <option value="mer">Auto: MER (live)</option>
      <option value="returning_rate">Auto: returning rate (live)</option>
      <option value="team_points_week">Auto: team points this week (live)</option>
    </select>
    <div class="pos-goal__formrow">
      <input class="csw-search" id="pos-g-start" type="date" value="${ps}"/>
      <input class="csw-search" id="pos-g-end" type="date" value="${pe}"/>
    </div>
    <input class="csw-search" id="pos-g-dept" placeholder="Department (optional — blank = company)" style="margin-bottom:10px"/>
    <button class="csw-confirm" id="pos-g-save">Create goal</button>
    <div class="csw-goalmeta" id="pos-g-err" style="color:var(--danger);display:none;margin-top:8px"></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  ov.querySelector('#pos-g-save').addEventListener('click', async () => {
    const err = ov.querySelector('#pos-g-err');
    const title = ov.querySelector('#pos-g-title').value.trim();
    const target = parseFloat(ov.querySelector('#pos-g-target').value);
    if (!title || !isFinite(target)) { err.textContent = 'Title and a numeric target are required.'; err.style.display = 'block'; return; }
    const metric = ov.querySelector('#pos-g-metric').value || null;
    const body = {
      title, target_value: target,
      unit: ov.querySelector('#pos-g-unit').value || null,
      metric_key: metric, auto_track: !!metric,
      period_start: ov.querySelector('#pos-g-start').value || null,
      period_end: ov.querySelector('#pos-g-end').value || null,
      department: ov.querySelector('#pos-g-dept').value.trim() || null,
    };
    try {
      const d = await api('/api/people/goals', { method: 'POST', body: JSON.stringify(body) });
      if (d && d.ok) { showToast('Goal set'); ov.remove(); _posLoadGoals(); }
      else { err.textContent = (d && d.error) || 'Failed'; err.style.display = 'block'; }
    } catch (e) { err.textContent = e.message || String(e); err.style.display = 'block'; }
  });
}

/* per-goal sheet — progress update + close actions (PATCH) */
function _posGoalSheet(g) {
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">${esc(g.title || 'Goal')}</div><button class="csw-x">✕</button></div>
    <div class="csw-goalmeta" style="margin-bottom:10px">${_posGoalVal(g.current_value, g.unit)} of ${_posGoalVal(g.target_value, g.unit)} · ${Math.round(g.progress_pct || 0)}%${g.auto_track ? ' · tracked live by EON' : ''}</div>
    ${g.auto_track ? '' : `<div class="pos-goal__formrow"><input class="csw-search" id="pos-g-cur" type="number" inputmode="decimal" placeholder="Current value" value="${g.current_value != null ? esc(String(g.current_value)) : ''}"/><button class="csw-mini" id="pos-g-upd" style="white-space:nowrap">Update</button></div>`}
    <div class="pos-goal__acts">
      ${g.status === 'active' ? `<button class="csw-mini" data-gst="achieved">Mark achieved</button><button class="csw-mini" data-gst="missed">Mark missed</button>` : ''}
      ${g.status !== 'archived' ? `<button class="csw-mini" data-gst="archived">Archive</button>` : `<button class="csw-mini" data-gst="active">Reactivate</button>`}
    </div>
    <div class="csw-goalmeta" id="pos-g-serr" style="color:var(--danger);display:none;margin-top:8px"></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const patch = async (body) => {
    const err = ov.querySelector('#pos-g-serr');
    try {
      const d = await api(`/api/people/goals/${encodeURIComponent(g.id)}`, { method: 'PATCH', body: JSON.stringify(body) });
      if (d && d.ok) { showToast('Goal updated'); ov.remove(); _posLoadGoals(); }
      else { err.textContent = (d && d.error) || 'Failed'; err.style.display = 'block'; }
    } catch (e) { err.textContent = e.message || String(e); err.style.display = 'block'; }
  };
  ov.querySelector('#pos-g-upd')?.addEventListener('click', () => {
    const v = parseFloat(ov.querySelector('#pos-g-cur').value);
    if (isFinite(v)) patch({ current_value: v });
  });
  ov.querySelectorAll('[data-gst]').forEach(b =>
    b.addEventListener('click', () => patch({ status: b.getAttribute('data-gst') })));
}

/* ──────────── DETAIL PANE ──────────── */
async function posOpenDetail(id) {
  const t = _posState.byId[id] || (_posState.mine?.tasks || []).find(x => x.id === id) || { id };
  const scroll = document.getElementById('pos-dscroll'), foot = document.getElementById('pos-dfoot');
  if (!scroll || !foot) return;   // detail shell not mounted (defensive)
  document.querySelectorAll('#pos-surface .pos-card, #pos-surface .pos-lst tbody tr').forEach(el => el.classList.remove('sel'));
  document.querySelectorAll(`#pos-surface [data-tid="${CSS.escape(id)}"]`).forEach(el => el.classList.add('sel'));
  // short task code in the crumb (ids are UUIDs; mock shows "T-401")
  const code = 'T-' + String(id).replace(/-/g, '').slice(0, 4).toUpperCase();
  const crumb = document.getElementById('pos-dcrumb');
  if (crumb) crumb.textContent = `CRUX · Tasks · ${code}`;
  scroll.innerHTML = _posDetailHTML(t);
  foot.innerHTML = _posDetailFoot(t);
  _posBindDetail(t);
  document.getElementById('pos-detail').classList.add('open');
  document.getElementById('pos-scrim').classList.add('on');
  // lazy: dependencies (full handoff) + comments + ask-EON history
  _posLoadDeps(id);
  _posLoadComments(id);
  _posLoadAskEon(id);
}
function posCloseDetail() {
  document.getElementById('pos-detail')?.classList.remove('open');
  document.getElementById('pos-scrim')?.classList.remove('on');
  document.querySelectorAll('#pos-surface .pos-card, #pos-surface .pos-lst tbody tr').forEach(el => el.classList.remove('sel'));
}

function _posDetailHTML(t) {
  const ds = posDueState(t.due_at);
  const dept = t.department || '—';
  let html = '';
  if (t.created_by_eon) html += `<div class="pos-detail__eon">${POS_SPARK}EON-allocated</div>`;
  html += `<div class="pos-detail__title">${esc(t.title || 'Task')}</div>`;
  if (t.created_by_eon) {
    const who = t.assignee_name ? t.assignee_name.split(' ')[0] : 'this team';
    html += `<div class="pos-detail__why"><b>Why this, why ${esc(who)}, why now —</b> ${esc(posWhyLine(t))}
      <span class="src">▲ insight · ${esc(t.category || 'scanner')}${ds ? ' · ' + ds.txt : ''}${t.recommendation_id ? ' · rec ' + esc(String(t.recommendation_id).slice(0, 8)) : ''}</span></div>`;
  }
  if (t.action_required) html += `<div class="pos-detail__desc"><b>Action required.</b> ${esc(t.action_required)}</div>`;
  // property grid
  html += `<div class="pos-props">
    <div class="pos-prop"><span class="pos-prop__k">Status</span><span class="pos-prop__v ink"><span class="pos-psel">${posStatusGlyph(t.status, 13)}${POS_STATUS[t.status]?.lbl || t.status}</span></span></div>
    <div class="pos-prop"><span class="pos-prop__k">Assignee</span><span class="pos-prop__v ink">${t.assignee_name ? `${posAvatar(t.assignee_name, 'sm')} ${esc(t.assignee_name)}` : '<span class="pos-av pos-av--ghost pos-av--sm">＋</span> Unassigned'}${posIsLead() ? ` <button class="pos-psel" id="pos-reassign" style="margin-left:auto">Re-assign</button>` : ''}</span></div>
    <div class="pos-prop"><span class="pos-prop__k">Department</span><span class="pos-prop__v"><span class="pos-psel"><span class="dot" style="width:9px;height:9px;border-radius:3px;background:${posDeptColor(dept)}"></span>${esc(dept)}</span></span></div>
    <div class="pos-prop"><span class="pos-prop__k">Priority</span><span class="pos-prop__v">${posPriGlyph(t.priority)} ${posPriLabel(t.priority)}</span></div>
    <div class="pos-prop"><span class="pos-prop__k">Due</span><span class="pos-prop__v"><span style="color:${ds && ds.cls === 'over' ? 'var(--danger)' : ds && ds.cls === 'soon' ? 'var(--warn)' : 'var(--ink-dim)'};font-family:var(--mono);font-size:12px">${ds ? ds.txt : '—'}</span> <span style="color:var(--ink-mute);font-size:11px;font-family:var(--mono)">${t.due_at ? new Date(t.due_at).toLocaleString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span></span></div>
    ${t.estimated_impact_ngn > 0 ? `<div class="pos-prop"><span class="pos-prop__k">Impact</span><span class="pos-prop__v"><span class="pos-psel impact">${posNgn(t.estimated_impact_ngn)} at stake</span></span></div>` : ''}
    <div class="pos-prop"><span class="pos-prop__k">Reward</span><span class="pos-prop__v"><span style="font-family:var(--mono);color:var(--accent)">${posPtsLabel(t)} pts</span> ${t.quality_rating ? `· ${'★'.repeat(t.quality_rating)} from manager` : 'on verify'}</span></div>
  </div>`;
  // SLA meter
  if (t.sla_minutes) {
    const elapsed = t.elapsed_minutes || 0;
    const used = Math.min(100, Math.round(elapsed / t.sla_minutes * 100));
    const left = Math.max(0, t.sla_minutes - elapsed);
    html += `<div class="pos-sla"><div class="pos-sla__h"><span class="pos-sla__l">SLA · ${t.sla_minutes} min</span><span class="pos-sla__v">${left} min left</span></div><div class="pos-sla__track"><i style="width:${used}%"></i></div></div>`;
  }
  // handoff chain placeholder (filled by _posLoadDeps)
  html += `<div id="pos-handoff"></div>`;
  // EON allocation receipt + activity
  html += `<div class="pos-act"><div class="pos-chain__h" style="margin-bottom:6px">Activity</div>
    ${t.created_by_eon ? `<div class="pos-act__r"><span class="pos-act__dot eon"></span><span class="pos-act__b"><b>EON</b> generated and allocated this task${t.allocation_reason ? '' : ''}<div class="pos-act__t">${t.created_at ? new Date(t.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'recently'}</div></span></div>` : ''}
    <div class="pos-act__r"><span class="pos-act__dot"></span><span class="pos-act__b">${esc(t.assignee_name || 'Unassigned')} · ${esc(POS_STATUS[t.status]?.lbl || t.status)}${t.velocity ? ' · ' + esc(t.velocity) : ''}</span></div>
    ${t.points_awarded ? `<div class="pos-act__r"><span class="pos-act__dot pts"></span><span class="pos-act__b">Verified <span class="pos-act__pts">+${t.points_awarded} pts</span>${t.quality_rating ? ` · ${'★'.repeat(t.quality_rating)}` : ''}<div class="pos-act__t">${t.verified_at ? new Date(t.verified_at).toLocaleString('en-GB', { day: 'numeric', month: 'short' }) : ''}</div></span></div>` : ''}
  </div>`;
  // Ask EON — the assignee (or a lead) can ask EON how to move THIS task
  // forward; EON answers with the task's context + the tenant's data tools.
  const myEmail = (State.member?.email || '').toLowerCase();
  const canAsk = (t.assignee_email && t.assignee_email === myEmail)
    || ['admin', 'owner'].includes(State.member?.role || '');
  if (canAsk) {
    html += `<div class="pos-act pos-ask"><div class="pos-chain__h" style="margin-bottom:6px">${POS_SPARK}Ask EON</div>
      <div id="pos-ask-thread"><div class="pos-ask__hint">Stuck? Ask EON how to move this task forward — it knows the task, the SOPs, and your data.</div></div>
      <div class="pos-cmt-box pos-ask__box"><input id="pos-ask-in" placeholder="Ask EON about this task…"/><button id="pos-ask-send">Ask</button></div>
      <div class="pos-ask__meta" id="pos-ask-meta"></div></div>`;
  }
  // comments
  html += `<div class="pos-act"><div class="pos-chain__h" style="margin-bottom:6px">Comments</div><div id="pos-comments"><div class="pos-act__t" style="margin:6px 0">Loading…</div></div>
    <div class="pos-cmt-box"><input id="pos-cmt-in" placeholder="Add a comment…"/><button id="pos-cmt-send">Send</button></div></div>`;
  return html;
}

function _posDetailFoot(t) {
  const st = t.status || 'open';
  const isLead = posIsLead();
  if (st === 'completed' || st === 'done') {
    return isLead
      ? `<button class="ghost" data-act="rework">Send back</button><button class="verify" data-act="verify"><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg>Verify & rate ★</button>`
      : `<button class="ghost" disabled style="opacity:.6">Awaiting verify</button>`;
  }
  if (st === 'verified') return `<button class="prime" disabled>Closed ✓</button>`;
  if (st === 'blocked') return `<button class="ghost" data-act="acknowledged">Got it</button><button class="prime" data-act="in_progress">Resolve & resume</button>`;
  if (st === 'open' || st === 'assigned') return `<button class="ghost" data-act="acknowledged">Got it</button><button class="prime" data-act="in_progress">Start</button>`;
  if (st === 'acknowledged') return `<button class="ghost" data-act="blocked">Block</button><button class="prime" data-act="in_progress">Start</button>`;
  if (st === 'in_progress') return `<button class="ghost" data-act="blocked">Block</button><button class="prime" data-act="complete">Mark complete</button>`;
  return `<button class="prime" data-act="in_progress">Start</button>`;
}

function _posBindDetail(t) {
  document.querySelectorAll('#pos-dfoot [data-act]').forEach(b => b.addEventListener('click', () => {
    const act = b.getAttribute('data-act');
    if (act === 'verify') return posOpenVerify(t.id);
    if (act === 'rework') return posVerifySubmit(t.id, false, 0, '');
    if (act === 'complete') return posTaskAction(t.id, `/api/tasks/${t.id}/complete`, { notes: '' }, 'Marked complete');
    return posTaskAction(t.id, `/api/tasks/${t.id}/transition`, { status: act }, 'Updated');
  }));
  document.getElementById('pos-reassign')?.addEventListener('click', () => posOpenReassign(t));
  document.getElementById('pos-cmt-send')?.addEventListener('click', () => posSendComment(t.id));
  document.getElementById('pos-cmt-in')?.addEventListener('keydown', (e) => { if (e.key === 'Enter') posSendComment(t.id); });
  document.getElementById('pos-ask-send')?.addEventListener('click', () => posAskEon(t.id));
  document.getElementById('pos-ask-in')?.addEventListener('keydown', (e) => { if (e.key === 'Enter') posAskEon(t.id); });
}

function _posAskMsg(m) {
  const who = m.role === 'assistant' ? 'EON' : 'You';
  return `<div class="pos-askmsg pos-askmsg--${m.role === 'assistant' ? 'eon' : 'me'}">
    <span class="pos-askmsg__who">${who}</span><span class="pos-askmsg__b">${esc(m.content || '')}</span></div>`;
}

async function _posLoadAskEon(id) {
  const thread = document.getElementById('pos-ask-thread');
  if (!thread) return;
  try {
    const d = await api(`/api/tasks/${id}/ask_eon/messages`);
    const msgs = (d && d.messages || []).filter(m => m.content);
    if (msgs.length) thread.innerHTML = msgs.map(_posAskMsg).join('');
  } catch (e) { /* leave the hint in place */ }
}

async function posAskEon(id) {
  const inp = document.getElementById('pos-ask-in');
  const thread = document.getElementById('pos-ask-thread');
  const meta = document.getElementById('pos-ask-meta');
  const msg = (inp?.value || '').trim();
  if (!msg || !thread) return;
  inp.value = '';
  const hint = thread.querySelector('.pos-ask__hint'); if (hint) hint.remove();
  thread.insertAdjacentHTML('beforeend', _posAskMsg({ role: 'user', content: msg }));
  thread.insertAdjacentHTML('beforeend', `<div class="pos-askmsg pos-askmsg--eon" id="pos-ask-pending"><span class="pos-askmsg__who">EON</span><span class="pos-askmsg__b pos-ask__typing">…</span></div>`);
  thread.scrollTop = thread.scrollHeight;
  const btn = document.getElementById('pos-ask-send'); if (btn) btn.disabled = true;
  try {
    const d = await api(`/api/tasks/${id}/ask_eon`, { method: 'POST', body: JSON.stringify({ message: msg }) });
    document.getElementById('pos-ask-pending')?.remove();
    if (d && d.ok) {
      thread.insertAdjacentHTML('beforeend', _posAskMsg({ role: 'assistant', content: d.reply || '(no reply)' }));
      if (meta && d.remaining_today != null) meta.textContent = `${d.remaining_today} question${d.remaining_today === 1 ? '' : 's'} left today`;
    } else {
      thread.insertAdjacentHTML('beforeend', _posAskMsg({ role: 'assistant', content: (d && d.error) || 'EON could not answer right now.' }));
    }
  } catch (e) {
    document.getElementById('pos-ask-pending')?.remove();
    thread.insertAdjacentHTML('beforeend', _posAskMsg({ role: 'assistant', content: String(e.message || e) }));
  } finally {
    if (btn) btn.disabled = false;
    thread.scrollTop = thread.scrollHeight;
  }
}

async function posTaskAction(id, path, body, okMsg) {
  try {
    const d = await api(path, { method: 'POST', body: JSON.stringify(body) });
    if (d && (d.ok || d.id || d.status)) { showToast(okMsg); posCloseDetail(); await _posLoad(); }
    else showToast((d && (d.error?.error || d.error)) || 'Failed');
  } catch (e) { showToast(String(e.message || e)); }
}

async function _posLoadDeps(id) {
  try {
    const dep = await api(`/api/tasks/${id}/dependencies`);
    const slot = document.getElementById('pos-handoff');
    if (!slot || !dep || !dep.ok) return;
    const bb = dep.blocked_by || [], bl = dep.blocks || [];
    if (!bb.length && !bl.length) return;
    // build a cross-department stepper: blockers (done/now) → this → downstream
    const steps = [];
    bb.forEach(x => {
      const bt = _posState.byId[x.depends_on] || {};
      steps.push({ dept: bt.department || 'Upstream', who: bt.assignee_name, det: bt.title || x.depends_on, state: (bt.status === 'verified' || bt.status === 'done' || bt.status === 'completed') ? 'done' : 'now' });
    });
    const self = _posState.byId[id] || {};
    steps.push({ dept: self.department || 'This task', who: self.assignee_name, det: self.title || id, state: bb.length ? 'now' : 'now' });
    bl.forEach(x => {
      const dt = _posState.byId[x.task_id] || {};
      steps.push({ dept: dt.department || 'Downstream', who: dt.assignee_name, det: dt.title || x.task_id, state: 'next' });
    });
    slot.innerHTML = `<div class="pos-chain"><div class="pos-chain__h">Cross-department handoff</div>${steps.map(s => {
      const ic = s.state === 'done' ? '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M20 6 9 17l-5-5"/></svg>' : s.state === 'now' ? '●' : '○';
      return `<div class="pos-chain__step"><div class="pos-chain__rail"></div><div class="pos-chain__node ${s.state}">${ic}</div>
        <div class="pos-chain__b"><div class="pos-chain__dept">${esc(s.dept)}</div><div class="pos-chain__det">${esc((s.det || '').slice(0, 70))}</div>
        ${s.who ? `<div class="pos-chain__who">${posAvatar(s.who, 'sm')}${esc(s.who)}</div>` : ''}</div></div>`;
    }).join('')}</div>`;
  } catch (e) { /* silent */ }
}

async function _posLoadComments(id) {
  try {
    const d = await api(`/api/tasks/${id}/comments`);
    const slot = document.getElementById('pos-comments');
    if (!slot) return;
    const cs = (d && d.comments) || [];
    const myUid = State.member?.user_id || State.member?.id;
    slot.innerHTML = cs.length ? cs.map(c => {
      const who = c.author_name || (myUid && c.author_user_id === myUid ? (State.member?.name || 'You') : null)
        || c.author_email || 'Teammate';
      return `<div class="pos-cmt">${posAvatar(who, 'sm')}<div class="pos-cmt__b"><div class="pos-cmt__who">${esc(who)}</div><div class="pos-cmt__txt">${esc(c.body || '')}</div><div class="pos-cmt__t">${c.created_at ? new Date(c.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</div></div></div>`;
    }).join('') : '<div class="pos-act__t" style="margin:6px 0">No comments yet.</div>';
  } catch (e) { const slot = document.getElementById('pos-comments'); if (slot) slot.innerHTML = '<div class="pos-act__t">Could not load comments.</div>'; }
}

async function posSendComment(id) {
  const inp = document.getElementById('pos-cmt-in');
  const body = (inp?.value || '').trim();
  if (!body) return;
  inp.value = '';
  try {
    const d = await api(`/api/tasks/${id}/comments`, { method: 'POST', body: JSON.stringify({ body }) });
    if (d && (d.ok || d.id)) _posLoadComments(id);
    else showToast('Comment failed');
  } catch (e) { showToast(String(e.message || e)); }
}

async function posOpenReassign(t) {
  const dept = t.department || 'Operations';
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Re-assign · ${esc(dept)}</div><button class="csw-x">✕</button></div><div id="pos-ap"><div class="pos-empty">Loading…</div></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  try {
    const d = await api(`/api/tasks/eligible-assignees?department=${encodeURIComponent(dept)}`);
    const cands = (d && d.candidates) || [];
    ov.querySelector('#pos-ap').innerHTML = cands.length ? cands.map(c =>
      `<button class="hr-rep" data-sid="${esc(c.staff_id)}">${cswAvatar(c.name || c.email, { size: 'sm', seed: c.staff_id })}<div class="hr-rep__meta"><div class="hr-rep__name">${esc(c.name || c.email)}</div><div class="hr-rep__role">${esc(c.department || '')}</div></div><span class="hr-rep__chev">›</span></button>`).join('')
      : `<div class="pos-empty">No eligible staff in ${esc(dept)}.</div>`;
    ov.querySelectorAll('.hr-rep[data-sid]').forEach(b => b.addEventListener('click', async () => {
      const r = await api(`/api/tasks/${t.id}/assign`, { method: 'POST', body: JSON.stringify({ staff_id: b.getAttribute('data-sid') }) });
      if (r && r.ok) { showToast('Re-assigned'); ov.remove(); posCloseDetail(); await _posLoad(); } else showToast('Failed');
    }));
  } catch (e) { ov.querySelector('#pos-ap').innerHTML = `<div class="pos-empty">${esc(e.message || e)}</div>`; }
}

/* ──────────── VERIFY + RATE (with celebrate) ──────────── */
function posOpenVerify(id) {
  const t = _posState.byId[id] || { id };
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Verify & rate ★</div><button class="csw-x">✕</button></div>
    <div class="csw-goalmeta" style="margin-bottom:10px">Check the result against the task brief, then rate the work. Your rating sets the points awarded. You cannot verify work assigned to you.</div>
    <div class="pos-stars" id="pos-vr-stars"><span data-s="1">☆</span><span data-s="2">☆</span><span data-s="3">☆</span><span data-s="4">☆</span><span data-s="5">☆</span></div>
    <input class="csw-search" id="pos-vr-note" placeholder="note (optional)" style="margin-bottom:8px"/>
    <button class="csw-confirm" id="pos-vr-pass">Verify ✓</button>
    <button class="csw-mini" id="pos-vr-fail" style="width:100%;margin-top:8px">Send back (rework)</button>`, true);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  let rating = 0;
  const paint = () => ov.querySelectorAll('#pos-vr-stars span').forEach(s => s.textContent = (+s.dataset.s <= rating ? '★' : '☆'));
  ov.querySelectorAll('#pos-vr-stars span').forEach(s => s.addEventListener('click', () => { rating = +s.dataset.s; paint(); }));
  ov.querySelector('#pos-vr-pass').addEventListener('click', () => { const note = ov.querySelector('#pos-vr-note').value || ''; ov.remove(); posVerifySubmit(id, true, rating, note); });
  ov.querySelector('#pos-vr-fail').addEventListener('click', () => { const note = ov.querySelector('#pos-vr-note').value || ''; ov.remove(); posVerifySubmit(id, false, 0, note); });
}

async function posVerifySubmit(id, passed, rating, note) {
  const t = _posState.byId[id] || {};
  try {
    const d = await api(`/api/tasks/${id}/verify`, { method: 'POST', body: JSON.stringify({ passed, rating: rating || null, note: note || null }) });
    if (d && d.ok) {
      if (passed) {
        const pts = d.award?.points_awarded;
        posCelebrate(t.assignee_name, pts, rating);
      } else showToast('Sent back for rework');
      posCloseDetail();
      await _posLoad();
    } else showToast((d && (d.error?.error || d.error)) || 'Failed');
  } catch (e) { showToast(String(e.message || e)); }
}

function posCelebrate(name, pts, rating) {
  const first = name ? name.split(' ')[0] : 'staff';
  let toast = document.getElementById('pos-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'pos-toast';
    toast.id = 'pos-toast';
    toast.innerHTML = `<div class="pos-toast__ic"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2l1.8 5.5L19 9l-5.2 1.5L12 16l-1.8-5.5L5 9l5.2-1.5z"/></svg></div><div><div class="pos-toast__t" id="pos-toast-t"></div><div class="pos-toast__s" id="pos-toast-s"></div></div>`;
    document.body.appendChild(toast);
  }
  document.getElementById('pos-toast-t').textContent = `Verified · ${pts ? '+' + pts + ' to ' : ''}${first}`;
  document.getElementById('pos-toast-s').textContent = `${rating ? '★'.repeat(rating) + ' rated · ' : ''}points banked`;
  toast.classList.add('on');
  // The default shows the verified receipt without free-floating confetti.
  if (cg.active()) { setTimeout(() => toast.classList.remove('on'), 2600); return; }
  const cols = ['#d4ff3a', '#9177C7', '#CA6673', '#4dd6a0', '#4796E3'];
  for (let i = 0; i < 14; i++) {
    const c = document.createElement('span');
    c.style.cssText = `position:fixed;left:50%;bottom:44px;width:7px;height:7px;border-radius:2px;z-index:90;background:${cols[i % 5]};--cx:${(Math.random() * 220 - 110)}px;--cy:${-(Math.random() * 120 + 40)}px;animation:pos-confetti ${600 + Math.random() * 400}ms var(--ease-emph) forwards;`;
    document.body.appendChild(c); setTimeout(() => c.remove(), 1100);
  }
  setTimeout(() => toast.classList.remove('on'), 2600);
}

/* ──────────── EON QUEUE + NEW TASK ──────────── */
async function posOpenEonQueue() {
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">EON — queued to approve</div><button class="csw-x">✕</button></div><div id="pos-eq"><div class="pos-empty">Loading…</div></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  try {
    const d = await api('/api/tasks/autogen/queue');
    const q = (d && d.queue) || [];
    ov.querySelector('#pos-eq').innerHTML = q.length ? q.map(x =>
      `<div class="hr-fric ${x.orphan ? 'hr-fric--orphan' : ''}"><div class="hr-fric__top"><b>${esc(x.title)}</b>${x.orphan ? '<span class="hr-fric__badge">no assignee</span>' : ''}</div>
       <div class="hr-flag__d">${esc(x.department || '?')}${x.estimated_impact_ngn ? ' · ' + posNgn(x.estimated_impact_ngn) + ' at stake' : ''}${x.orphan ? ` · ${esc(x.orphan_reason || 'no eligible assignee — add staff or hand-route')}` : (x.proposed?.name ? ' · routes to ' + esc(x.proposed.name) : '')}</div>
       <div class="hr-fric__act"><button class="hr-mini" data-approve="${esc(x.id)}">${x.orphan ? 'Retry route' : 'Approve & route'}</button></div></div>`).join('')
      : `<div class="pos-empty"><div class="pos-empty__i">✓</div>Nothing waiting. EON auto-routes low-risk work; high-stakes lands here.</div>`;
    ov.querySelectorAll('[data-approve]').forEach(b => b.addEventListener('click', async () => {
      const r = await api(`/api/tasks/autogen/${b.getAttribute('data-approve')}/approve`, { method: 'POST', body: '{}' });
      if (r && r.ok) { showToast('Approved & routed'); b.closest('.hr-fric').remove(); await _posLoad(); } else showToast((r && r.error) || 'Failed');
    }));
  } catch (e) { ov.querySelector('#pos-eq').innerHTML = `<div class="pos-empty">${esc(e.message || e)}</div>`; }
}

function posNewTask() {
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">New task</div><button class="csw-x">✕</button></div>
    <input class="csw-search" id="pos-nt-title" placeholder="Task title…" style="margin-bottom:8px"/>
    <button class="csw-confirm" id="pos-nt-run">Create + let EON route →</button>
    <div class="csw-goalmeta" style="margin-top:10px">Or run EON's autonomous pass over current business insights:</div>
    <button class="csw-mini" id="pos-nt-auto" style="width:100%;margin-top:6px">Run EON auto-allocate now</button>
    <div class="csw-goalmeta" id="pos-nt-msg"></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  ov.querySelector('#pos-nt-run').addEventListener('click', async event => {
    const button=event.currentTarget;
    if(button.disabled)return;
    const title = ov.querySelector('#pos-nt-title').value.trim();
    if (!title) { ov.querySelector('#pos-nt-msg').textContent = 'Title required.'; return; }
    button.disabled=true;
    try {
      const d = await _tosCreateTask({ title });
      if (d && (d.ok || d.id)) { showToast('Task created'); ov.remove(); await _posLoad(); } else showToast('Failed');
    } catch (e) { ov.querySelector('#pos-nt-msg').textContent = e.message || String(e); }
    finally { if(ov.isConnected)button.disabled=false; }
  });
  ov.querySelector('#pos-nt-auto').addEventListener('click', async () => {
    const m = ov.querySelector('#pos-nt-msg'); m.textContent = 'EON scanning insights + routing…';
    try {
      const d = await api('/api/tasks/autogen/run', { method: 'POST', body: '{}' });
      m.textContent = d && d.ok ? `Routed ${d.created || 0}, staged ${d.staged || 0} for approval.` : 'Failed';
      await _posLoad();
    } catch (e) { m.textContent = e.message || String(e); }
  });
}

// ──────────────────────────────────────────────────────────
// System Audit (?v=system-audit) — 2026-06-11
//
// Where EON shows how it evaluated itself, what it proposes to
// improve, and lets the operator approve/reject. Merge always stays
// manual on GitHub — this surface NEVER ships code by itself.
//
// Backend contract (built in parallel — every call is defensive; a
// 404 or missing field renders an honest pending/empty state):
//   GET  /api/system-audit/proposals?status=        → { proposals: [...] }
//   POST /api/system-audit/proposals/{id}/approve   → { ok }
//   POST /api/system-audit/proposals/{id}/reject    → { ok }   body: { reason }
//   GET  /api/system-audit/telemetry                → digest dict (shape varies)
//
// Design: reuses the .mk-ads3 dark decision-first family (lime/mint/
// amber, mono figures, staggered reveals) with an .sa-* sub-namespace.

const _saState = {
  scope: null, reads: {},
  proposals: null,      // null = endpoint unavailable; [] = live but empty
  telemetry: null,      // null = endpoint unavailable
  engine: null,         // the Engine Room feed (2026-07-28)
  truth: null,          // Panel 1 — what ran, what didn't (2026-07-30)
  eval: null,           // Panel 4 — the live eval scorecard (2026-07-30)
  shipped: null,        // Panel 2 — the shipped feed (2026-07-30)
  biz: null,            // Panel 3 — proposal_cards rail (2026-07-30)
  book: null,           // Panel 5 — house documents + fingerprints (2026-08-03)
  machine: null,        // the two-model machine panel (2026-08-03)
  pollHandle: null,
  acting: false,        // guards double-submits on approve/reject
};

const _SA_OPEN_STATUSES = ['proposed', 'approved', 'building', 'pr_open'];

const _SA_KIND = {
  bugfix:       { label: 'bugfix',       color: '#ef4444',           bg: '#ef444418' },
  perf:         { label: 'perf',         color: 'var(--a3-mint)',    bg: '#56E39F1f' },
  capability:   { label: 'capability',   color: 'var(--a3-lime)',    bg: '#C8FF4D1f' },
  data_quality: { label: 'data quality', color: '#6CA8FF',           bg: '#6CA8FF1f' },
  ux_friction:  { label: 'ux friction',  color: '#B98CFF',           bg: '#B98CFF1f' },
};

const _SA_RISK = {
  low:    { label: 'low risk',    color: 'var(--a3-mut)', bg: '#ffffff10' },
  medium: { label: 'medium risk', color: 'var(--a3-amber)', bg: '#F2A24C1f' },
  high:   { label: 'high risk',   color: '#ef4444', bg: '#ef444418' },
};

// Plain-language one-liners for known telemetry groups — each card
// answers "what does this mean" without jargon.
const _SA_MEANING = {
  error_clusters:      'Repeated errors EON found in its own logs — the raw material for bugfix proposals.',
  errors:              'Repeated errors EON found in its own logs — the raw material for bugfix proposals.',
  errors_by_component: 'Which parts of EON are throwing errors, and how often.',
  cron_health:         'Scheduled jobs — whether each one ran on time and finished clean.',
  crons:               'Scheduled jobs — whether each one ran on time and finished clean.',
  chat_friction:       'Questions you had to ask more than once — friction EON should remove.',
  questions_repeated:  'Questions you had to ask more than once — friction EON should remove.',
  verdict_calibration: 'Ad verdicts EON issued vs what actually happened afterwards.',
  verdicts_ignored:    'Kill or scale verdicts that were surfaced but never acted on.',
  freshness:           'How fresh each data source is right now — stale inputs make wrong answers.',
  stale_data:          'Data sources running behind — stale inputs make wrong answers.',
};

function _saHumanize(key) {
  return String(key || '').replace(/[_-]+/g, ' ').trim();
}

function _saWhen(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  try {
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
      + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch (_) { return d.toISOString().slice(0, 16).replace('T', ' '); }
}

// Stringify an arbitrary telemetry/evidence value into short, safe text.
function _saShort(v, max = 80) {
  let s;
  if (v == null) s = '—';
  else if (typeof v === 'object') { try { s = JSON.stringify(v); } catch (_) { s = String(v); } }
  else s = String(v);
  s = s.replace(/\s+/g, ' ').trim();
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}

function renderSystemAudit() {
  // Rebuilt 2026-07-30 (operator: "rebuild the system audit section to
  // show truthfully what ran, what didnt, a detailed simplified audit
  // of everything going on with eon"). Three honest panels:
  //   1. What ran, what didn't — the truth panel (cron rollup with
  //      hung-job detection, routine families vs cadence, build lane,
  //      verifier visibility). A missing check says "did not run",
  //      never blank; a failed read says so, never a confident 0.
  //   2. What got fixed, improved, added — the shipped feed, plus the
  //      Engine Room strip (2026-07-28) which stays below it.
  //   3. Ideas that move the needle — business ideas (Decisions board)
  //      and code ideas (self-improvement proposals) in one rail,
  //      deduped, with the approve wiring that already exists.
  // The raw telemetry digest folds away under panel 1.
  return `
    <div class="mk-ads3 sa-wrap">
      <div class="a3-topbar a3-reveal" style="--a3-i:0">
        <span class="a3-brand">EON<span class="a3-sub">System Audit</span></span>
        <span class="a3-topbar-spacer"></span>
        <span class="a3-live"><span class="a3-live-dot"></span>the engine, checking its own work</span>
      </div>

      <div class="sa-mast a3-reveal" style="--a3-i:0">audit<span class="sa-mast-dot">.</span></div>
      <div class="sa-mast-idx a3-mono a3-reveal" style="--a3-i:0"><a href="#sa-truth">[ truth ]</a><a href="#sa-eval">[ eval ]</a><a href="#sa-machine">[ two models ]</a><a href="#sa-shipped">[ shipped ]</a><a href="#sa-book">[ book ]</a><a href="#sa-engine">[ engine ]</a><a href="#sa-ideas">[ ideas ]</a></div>

      <div class="a3-stack">
        <details class="a3-tile sa-explainer a3-reveal" style="--a3-i:1">
          <summary>What is this page?</summary>
          <p>EON is a machine that watches itself, and this page is where
          it tells the truth about its own work. First: what ran and what
          did not — every scheduled job and every off-site check, with the
          ones that failed or went quiet said out loud. Then: how it is
          actually doing — builds, checks, decisions, cost. Then: what got
          fixed, improved or added, newest first. Last: the ideas waiting
          on your yes or no. When a number cannot be read, the page says
          so — it never shows a confident zero it did not measure. Tap
          "under the hood" on any line if you want the machine names.</p>
        </details>

        <div class="a3-reveal" style="--a3-i:2">
          <div class="a3-section-title">What ran, what didn't</div>
          <div class="sa-truth" id="sa-truth">
            <div class="a3-tile sa-pending">Reading the run log…</div>
          </div>
        </div>

        <div class="a3-reveal" style="--a3-i:3">
          <div class="a3-section-title">The eval — how the machine is actually doing</div>
          <div class="sa-eval" id="sa-eval">
            <div class="a3-tile sa-pending">Reading the scorecard…</div>
          </div>
        </div>

        <details class="a3-tile sa-digest-fold a3-reveal" style="--a3-i:3">
          <summary>Under the hood — the raw self-evaluation digest</summary>
          <div class="sa-header" id="sa-header">
            <div class="a3-label">EON evaluated itself</div>
            <div class="sa-header-row">
              <div class="sa-scanline" id="sa-scanline">Loading scan state…</div>
              <div class="sa-counts" id="sa-counts"></div>
            </div>
          </div>
          <div class="sa-telemetry" id="sa-telemetry">
            <div class="a3-tile sa-pending">Reading the self-evaluation digest…</div>
          </div>
        </details>

        <div class="a3-reveal" style="--a3-i:4">
          <div class="a3-section-title">The two-model machine</div>
          <div class="sa-machine" id="sa-machine">
            <div class="a3-tile sa-pending">Reading the build rungs…</div>
          </div>
        </div>

        <div class="a3-reveal" style="--a3-i:4">
          <div class="a3-section-title">What got fixed, improved, added</div>
          <div class="sa-shipped" id="sa-shipped">
            <div class="a3-tile sa-pending">Reading the shipped feed…</div>
          </div>
        </div>

        <div class="a3-reveal" style="--a3-i:5">
          <div class="a3-section-title">What the work is designed from</div>
          <div class="sa-book" id="sa-book">
            <div class="a3-tile sa-pending">Reading the house documents…</div>
          </div>
        </div>

        <div class="a3-reveal" style="--a3-i:6">
          <div class="a3-section-title">The engine this week <a class="sa-loop-link" href="/loop" target="_blank" rel="noopener">see the whole loop →</a></div>
          <div class="sa-engine" id="sa-engine">
            <div class="a3-tile sa-pending">Reading the engine's notebook…</div>
          </div>
        </div>

        <div class="a3-reveal" style="--a3-i:7">
          <div class="a3-section-title">Ideas that move the needle <span class="meta" id="sa-prop-meta"></span></div>
          <div class="sa-ideas" id="sa-ideas">
            <div class="a3-tile sa-pending">Reading the ideas rail…</div>
          </div>
          <div class="sa-proposals" id="sa-proposals">
            <div class="a3-tile sa-pending">Loading proposals…</div>
          </div>
        </div>
      </div>
    </div>
  `;
}

// ── Panel 1: What ran, what didn't ─────────────────────────
//
// Consumes /api/system-audit/truth. Every sub-section carries its own
// measured_at and its own honest failure state — a dead read renders
// "could not read" copy, never a confident 0.

function _saDot(color) {
  return `<span class="sa-dot sa-dot-${esc(color)}"></span>`;
}

function _saMeasured(iso) {
  const w = _saWhen(iso);
  return w ? `<div class="sa-measured">measured ${esc(w)}</div>` : '';
}

function _saCouldNotReadTile(section) {
  return `<div class="a3-tile sa-pending">${esc(section)}: Could not read
    this right now — this is not zero. It will try again on the next
    visit.</div>`;
}

function _saFamilyLine(f) {
  const state = f.state || 'never';
  const color = state === 'ran' ? 'green' : 'red';
  let verdict;
  if (state === 'ran') {
    verdict = `ran — last report ${_saWhen(f.last_seen) || 'today'}`;
  } else if (state === 'did_not_run') {
    verdict = `did not run — last report ${f.days_since != null ? f.days_since + ' day(s) ago' : 'too long ago'}`;
  } else {
    verdict = 'did not run — has never reported';
  }
  return `
    <div class="sa-truth-line">
      ${_saDot(color)}
      <div class="sa-truth-text">
        <div class="sa-truth-t">${esc(_saHumanize(f.family))} <span class="sa-cadence">${esc(f.cadence || '')}</span></div>
        <div class="sa-truth-d">${esc(f.does || '')} — <span class="${state === 'ran' ? 'sa-ok' : 'sa-bad'}">${esc(verdict)}</span></div>
      </div>
    </div>`;
}

function _saCronLine(j) {
  const hood = [`${j.runs_24h || 0} runs 24h`, `${j.runs_7d || 0} runs 7d`,
                j.last_error ? String(j.last_error) : null]
    .filter(Boolean).join(' · ');
  return `
    <div class="sa-truth-line">
      ${_saDot(['red', 'amber', 'green'].includes(j.verdict) ? j.verdict : 'grey')}
      <div class="sa-truth-text">
        <div class="sa-truth-t a3-mono">${esc(_saShort(String(j.job_id).replace(/_/g, ' '), 46))}</div>
        <div class="sa-truth-d">${esc(j.reason || '')}${j.last_run ? ` — last run ${esc(_saWhen(j.last_run) || '')}` : ''}</div>
        <details class="sa-eng-hood"><summary>under the hood</summary><code>${esc(_saShort(hood, 220))}</code></details>
      </div>
    </div>`;
}

function _saRenderTruth() {
  const el = document.getElementById('sa-truth');
  if (!el) return;
  const t = _saState.truth;
  if (!t) {
    el.innerHTML = `<div class="a3-tile sa-pending">Could not read the run
      log right now — this is not zero. It retries on the next refresh.</div>`;
    return;
  }
  const parts = [];

  // 1a. Routine families — the off-site checks. Missing = say so.
  const fam = t.families || {};
  if (fam.ok && Array.isArray(fam.items)) {
    const missing = fam.items.filter(i => i.state !== 'ran').length;
    parts.push(`
      <div class="a3-tile sa-truth-card">
        <div class="a3-label">The checks that watch EON</div>
        <div class="sa-truth-big"><span class="a3-mono">${fam.ran || 0}/${fam.expected || 0}</span> ran inside their window${missing ? ` — <span class="sa-bad">${missing} did not run</span>` : ''}</div>
        ${fam.items.map(_saFamilyLine).join('')}
        ${_saMeasured(fam.measured_at)}
      </div>`);
  } else {
    parts.push(_saCouldNotReadTile('The checks that watch EON'));
  }

  // 1b. The cron machine — every scheduled job on the box.
  const cr = t.crons || {};
  if (cr.ok && Array.isArray(cr.jobs)) {
    const reds = cr.jobs.filter(j => j.verdict === 'red');
    const ambers = cr.jobs.filter(j => j.verdict === 'amber');
    const completed = cr.jobs.filter(j => j.verdict === 'green' && j.execution_state === 'completed_one_shot');
    const greens = cr.jobs.filter(j => j.verdict === 'green' && j.execution_state !== 'completed_one_shot');
    const paused = cr.jobs.filter(j => j.verdict === 'gray' && j.execution_state === 'paused');
    const unknown = cr.jobs.filter(j => !['red', 'amber', 'green'].includes(j.verdict) && !paused.includes(j));
    const tot = cr.totals || {};
    parts.push(`
      <div class="a3-tile sa-truth-card">
        <div class="a3-label">The machine's own chores</div>
        <div class="sa-truth-big"><span class="a3-mono">${(tot.runs_24h || 0).toLocaleString()}</span> rounds in 24h
          (<span class="a3-mono">${tot.fails_24h || 0}</span> failed) ·
          <span class="a3-mono">${(tot.runs_7d || 0).toLocaleString()}</span> in 7 days
          (<span class="a3-mono">${tot.fails_7d || 0}</span> failed)</div>
        ${reds.length ? `<div class="sa-group-label">Stuck or quiet right now</div>${reds.map(_saCronLine).join('')}` : ''}
        ${ambers.length ? `<div class="sa-group-label">Ran with failures</div>${ambers.slice(0, 8).map(_saCronLine).join('')}${ambers.length > 8 ? `<div class="sa-truth-d">and ${ambers.length - 8} more with failures — all counted above.</div>` : ''}` : ''}
        ${paused.length ? `<div class="sa-group-label">Deliberately paused</div>${paused.map(_saCronLine).join('')}` : ''}
        ${unknown.length ? `<details class="sa-greens" open><summary>${unknown.length} job(s) — execution expectation unknown</summary>${unknown.map(_saCronLine).join('')}</details>` : ''}
        ${completed.length ? `<div class="sa-group-label">Recorded catch-up completion</div>${completed.map(_saCronLine).join('')}` : ''}
        <details class="sa-greens"><summary>${greens.length} job(s) ran clean — tap to see</summary>
          <div class="sa-chiprow">${greens.slice(0, 300).map(j => `<span class="sa-chip a3-mono">${esc(_saShort(String(j.job_id).replace(/_/g, ' '), 40))}</span>`).join('')}</div>
        </details>
        ${_saMeasured(cr.measured_at)}
      </div>`);
  } else {
    parts.push(_saCouldNotReadTile("The machine's own chores"));
  }

  // 1c. Speed — the hang watchdog's own reading (probe_timings, mig 573).
  // 'empty' renders as no-data, NEVER as fast; failures are said out loud.
  const sp = t.speed || {};
  if (sp.ok && sp.state !== 'empty' && sp.p95_now_ms != null) {
    const spColor = sp.state === 'danger' ? 'red'
      : (sp.state === 'warn' || sp.state === 'failed') ? 'amber' : 'green';
    parts.push(`
      <div class="a3-tile sa-truth-card">
        <div class="a3-label">Server probe timings</div>
        <div class="sa-truth-big">${_saDot(spColor)} server probes answer in <span class="a3-mono">${esc(sp.p95_now_ms)}</span> ms at p95${sp.p95_yesterday_ms != null ? ` — yesterday <span class="a3-mono">${esc(sp.p95_yesterday_ms)}</span> ms` : ''}</div>
        <div class="sa-truth-d">Server HTTP checks, including auth rejection — not browser completion or authenticated CS thread loading.</div>
        <div class="sa-truth-d">${esc(sp.plain || '')}${sp.failed_15m ? ` <span class="sa-bad">${sp.failed_15m} check(s) got no answer at all.</span>` : ''}</div>
        ${_saMeasured(sp.measured_at)}
      </div>`);
  } else if (sp.ok) {
    parts.push(`
      <div class="a3-tile sa-truth-card">
        <div class="a3-label">Server probe timings</div>
        <div class="sa-truth-d">${esc(sp.plain || 'No speed readings in the last 15 minutes — the watchdog has not reported yet. That means no data, not fast.')}</div>
        ${_saMeasured(sp.measured_at)}
      </div>`);
  } else {
    parts.push(_saCouldNotReadTile('Server probe timings'));
  }

  // 1d. The build lane.
  const bl = t.build_lane || {};
  if (bl.ok) {
    const bs = bl.by_status || {};
    const n = k => (bs[k] && bs[k].n) || 0;
    const hold = bl.hold || {};
    const rel = bl.release_state || {};
    const holdClass = hold.held ? 'sa-bad' : (hold.held === false ? 'sa-ok' : '');
    const releaseLine = rel.ok
      ? `<div class="sa-truth-d"><strong>Current GitHub read:</strong> ${esc(rel.coverage_text || 'The selected request records')} point to ${esc(rel.distinct_prs)} distinct PRs. Within that selection, ${esc(rel.open)} are open (${esc(rel.draft)} draft, ${esc(rel.not_draft)} not draft); ${esc(rel.merged)} merged and ${esc(rel.closed)} closed without merging.</div>
         <div class="sa-truth-d">Open PR checks: ${esc(rel.checks?.green || 0)} green · ${esc(rel.checks?.red || 0)} red · ${esc(rel.checks?.running || 0)} running · ${esc(rel.checks?.unknown || 0)} not reported. GitHub review decisions: ${esc(rel.reviews?.approved || 0)} approved · ${esc(rel.reviews?.changes_requested || 0)} changes requested · ${esc(rel.reviews?.not_decided || 0)} none recorded.</div>
         ${_saMeasured(rel.provider_observed_at)}`
      : `<div class="sa-truth-d sa-honest">${esc(rel.text || 'Could not freshly read the linked PRs from GitHub. The request-row count below is recorded history, not current PR state.')}</div>`;
    parts.push(`
      <div class="a3-tile sa-truth-card">
        <div class="a3-label">The build lane — work EON queues for itself</div>
        <div class="sa-truth-big">
          <span class="a3-mono">${n('queued')}</span> queued request records ·
          <span class="a3-mono">${n('picked_up') + n('in_progress')}</span> building ·
          <span class="a3-mono">${n('pr_open')}</span> request records marked PR open ·
          <span class="a3-mono">${n('merged')}</span> landed request records ·
          <span class="a3-mono">${n('abandoned')}</span> stopped request records
        </div>
        ${releaseLine}
        <div class="sa-truth-d ${holdClass}">${esc(hold.text || 'Hold state unknown.')}</div>
        ${bl.last_pickup ? `<div class="sa-truth-d">Last time work was picked up: ${esc(_saWhen(bl.last_pickup) || bl.last_pickup)}</div>` : `<div class="sa-truth-d">No work has been picked up yet.</div>`}
        ${(bl.abandoned_7d || []).length ? `
          <details class="sa-greens"><summary>${bl.abandoned_7d.length} stopped this week — why, in plain words</summary>
            ${bl.abandoned_7d.map(a => `
              <div class="sa-truth-line">${_saDot('amber')}
                <div class="sa-truth-text">
                  <div class="sa-truth-t">${esc(_saShort(a.title, 90))}</div>
                  <div class="sa-truth-d">${esc(a.reason || '')}</div>
                </div>
              </div>`).join('')}
          </details>` : ''}
        ${_saMeasured(bl.measured_at)}
      </div>`);
  } else {
    parts.push(_saCouldNotReadTile('The build lane'));
  }

  // 1e. Verifier visibility — honest about what cannot be measured.
  const vf = t.verifier || {};
  if (vf.ok) {
    const rep = vf.verifier_reported_today;
    const repLine = rep === true
      ? '<span class="sa-ok">the work-checker reported today</span>'
      : rep === false
        ? '<span class="sa-bad">the work-checker did not report today</span>'
        : 'whether the work-checker reported today could not be read';
    parts.push(`
      <div class="a3-tile sa-truth-card">
        <div class="a3-label">Is finished work being checked?</div>
        ${vf.github_ok
          ? `<div class="sa-truth-big"><span class="a3-mono">${esc(vf.current_open_prs)}</span> current open PR(s) in the selected records; <span class="a3-mono">${esc(vf.draft_prs)}</span> are drafts</div>`
          : `<div class="sa-truth-big">Current PR state could not be read from GitHub.</div>`}
        <div class="sa-truth-d">The database has ${vf.request_rows_marked_pr_open != null ? esc(vf.request_rows_marked_pr_open) : '?'} request record(s) marked PR open. That recorded status does not mean a decision is waiting for you.</div>
        <div class="sa-truth-d">This reading covers ${esc(vf.coverage_text || 'an unread selection of records')}, linked to ${vf.distinct_linked_prs != null ? esc(vf.distinct_linked_prs) : '?'} distinct PR(s).</div>
        <div class="sa-truth-d">${repLine}${vf.verifier_last_seen ? ` — last report ${esc(_saWhen(vf.verifier_last_seen) || '')}` : ''}</div>
        <div class="sa-truth-d sa-honest">${esc(vf.cannot_measure || '')}</div>
        ${_saMeasured(vf.measured_at)}
      </div>`);
  } else {
    parts.push(_saCouldNotReadTile('Is finished work being checked?'));
  }

  el.innerHTML = parts.join('');
}

// ── Panel 2: What got fixed, improved, added ───────────────

const _SA_SHIP_STATE = {
  'landed': 'green',
  'shipped': 'green',
  'done — you approved it and it was carried out': 'green',
  'being built now': 'amber',
  'in flight': 'amber',
};

function _saShipEntry(e) {
  const stateColor = _SA_SHIP_STATE[e.state] || (String(e.state || '').indexOf('pr open') === 0 ? 'amber' : 'green');
  const url = String(e.url || '');
  const safeUrl = /^https?:\/\//i.test(url) ? url : '';
  const detail = [e.was_broken, e.what_changed, e.you_get]
    .filter(Boolean).map(x => `<div class="sa-truth-d">${esc(x)}</div>`).join('');
  return `
    <div class="sa-truth-line">
      ${_saDot(stateColor)}
      <div class="sa-truth-text">
        <div class="sa-truth-t">${esc(_saShort(e.title, 110))}
          <span class="sa-cadence">${esc(e.date || '')}</span></div>
        <div class="sa-truth-d"><span class="sa-chip sa-chip-dim">${esc(e.state || '')}</span>
          ${e.built_by !== undefined ? `<span class="sa-chip sa-chip-dim">${e.built_by ? `built by ${esc(e.built_by)}` : 'model unknown'}</span>` : ''}
          ${e.plain ? esc(_saShort(e.plain, 140)) : ''}
          ${safeUrl ? `<a class="sa-history-link" href="${esc(safeUrl)}" target="_blank" rel="noopener noreferrer">view</a>` : ''}</div>
        ${detail}
      </div>
    </div>`;
}

// ── The two-model machine (2026-08-03) ─────────────────────
//
// Consumes /api/system-audit/model-debt. Three things the operator
// asked for: which rung built each of the last merges, what the judging
// rung still owes a re-read, and the design itself drawn as a working
// pipeline — each node carrying its LIVE count and pulsing when that
// count moved in the last 24h. The animation is pure CSS and is turned
// off entirely under prefers-reduced-motion (see .sa-flow in app.css).

const _SA_MACHINE_STAGES = ['brief', 'builder', 'ci', 'verifier', 'merge',
  'ledger', 'revet'];

function _saMachineNode(stage, holds) {
  const count = (stage.count === null || stage.count === undefined)
    ? '—' : String(stage.count);
  const live = stage.moved_24h ? ' sa-node-live' : '';
  const unread = (stage.count === null || stage.count === undefined)
    ? `<div class="sa-node-note sa-honest">could not read this — not zero</div>`
    : `<div class="sa-node-note">${esc(stage.note || '')}</div>`;
  // The builder node is the one that shows WHICH rung is free: the
  // badges light off the live hold rows, so the page shows the ladder
  // as it actually stands, not as it was designed.
  let badges = '';
  if (stage.key === 'builder' && holds && holds.ok !== false) {
    const f = (holds.fable_5_hold || {}).held;
    const o = (holds.opus_5_hold || {}).held;
    badges = `<div class="sa-rungs">
      <span class="sa-rung ${f ? 'sa-rung-held' : 'sa-rung-free'}">Fable ${f ? 'walled' : 'free'}</span>
      <span class="sa-rung ${o ? 'sa-rung-held' : 'sa-rung-free'}">Opus ${o ? 'walled' : 'free'}</span>
    </div>`;
  }
  return `
    <div class="sa-node${live}">
      <div class="sa-node-count a3-mono">${esc(count)}</div>
      <div class="sa-node-label">${esc(stage.label || stage.key || '')}</div>
      ${unread}
      ${badges}
    </div>`;
}

function _saMachineFlow(pipeline, holds) {
  if (!pipeline || pipeline.ok === false || !Array.isArray(pipeline.stages)) {
    return `<div class="sa-truth-d sa-honest">Could not read the pipeline
      counters right now — this is not zero.</div>`;
  }
  const byKey = {};
  pipeline.stages.forEach(s => { byKey[s.key] = s; });
  const nodes = _SA_MACHINE_STAGES
    .map(k => byKey[k])
    .filter(Boolean)
    .map(s => _saMachineNode(s, holds));
  if (!nodes.length) {
    return `<div class="sa-truth-d sa-honest">No pipeline counters came
      back — this is not zero.</div>`;
  }
  return `<div class="sa-flow" role="img"
    aria-label="the build pipeline: brief, builder, checks, verifier, merge, ledger, re-vet">
    ${nodes.join('<div class="sa-flow-arrow" aria-hidden="true"></div>')}
  </div>`;
}

function _saRenderMachine() {
  const el = document.getElementById('sa-machine');
  if (!el) return;
  const m = _saState.machine;
  if (!m) {
    el.innerHTML = `<div class="a3-tile sa-pending">Could not read the two
      rungs right now — this is not zero. It retries on the next
      refresh.</div>`;
    return;
  }
  const debt = m.debt || {};
  const builds = m.builds || {};
  const split = debt.split || {};
  const debtBody = debt.ok === false
    ? `<div class="sa-truth-d sa-honest">${esc(debt.text || 'could not read the ledger')}</div>`
    : `<div class="sa-truth-big">${esc(String(debt.open || 0))}
         <span class="sa-truth-unit">waiting on a re-vet</span></div>
       <div class="sa-truth-d">${debt.oldest_days === null || debt.oldest_days === undefined
         ? 'no open debt has a merge date recorded'
         : `oldest is ${esc(String(debt.oldest_days))} day(s) old`}
         · ${esc(String(debt.overdue || 0))} past the seven-day window</div>
       <div class="sa-truth-d">
         <span class="sa-chip sa-chip-dim">clean ${esc(String(split.clean || 0))}</span>
         <span class="sa-chip sa-chip-dim">fixed ${esc(String(split.fixed || 0))}</span>
         <span class="sa-chip sa-chip-dim">escalated ${esc(String(split.escalated || 0))}</span>
       </div>
       ${(debt.items || []).map(d => `
         <div class="sa-truth-line">
           ${_saDot(d.status === 'escalated' ? 'amber' : 'grey')}
           <div class="sa-truth-text">
             <div class="sa-truth-t">PR #${esc(String(d.pr_number || ''))}
               <span class="sa-cadence">${esc(String(d.due || '').slice(0, 10))}</span></div>
             <div class="sa-truth-d">
               <span class="sa-chip sa-model-chip">built by ${esc(d.built_by || 'unknown')}</span>
               <span class="sa-chip sa-chip-dim">${esc(d.risk_tier || 'standard')}</span>
               ${d.claimed ? '<span class="sa-chip sa-chip-dim">re-vet filed</span>' : ''}
               ${/^https?:\/\//i.test(String(d.pr_url || ''))
                 ? `<a class="sa-history-link" href="${esc(d.pr_url)}" target="_blank" rel="noopener noreferrer">view</a>` : ''}
               <button class="sa-vet-btn" data-sa-vet="${esc(String(d.pr_number || ''))}"
                 data-sa-verdict="clean">read it — nothing to change</button>
               <button class="sa-vet-btn" data-sa-vet="${esc(String(d.pr_number || ''))}"
                 data-sa-verdict="escalated">needs you</button>
             </div>
           </div>
         </div>`).join('')}`;

  const buildsBody = builds.ok === false
    ? `<div class="sa-truth-d sa-honest">${esc(builds.text || 'could not read the merges')}</div>`
    : ((builds.items || []).length
      ? (builds.items || []).map(b => `
          <div class="sa-truth-line">
            ${_saDot(b.owes_revet ? 'amber' : 'green')}
            <div class="sa-truth-text">
              <div class="sa-truth-t">${esc(_saShort(b.title, 90))}
                <span class="sa-cadence">${esc(String(b.merged_at || '').slice(0, 10))}</span></div>
              <div class="sa-truth-d">
                <span class="sa-chip sa-model-chip">built by ${esc(b.built_by || 'unknown')}</span>
                ${b.owes_revet ? '<span class="sa-chip sa-chip-dim">re-vet owed</span>' : '<span class="sa-chip sa-chip-dim">judged rung</span>'}
                ${/^https?:\/\//i.test(String(b.pr_url || ''))
                  ? `<a class="sa-history-link" href="${esc(b.pr_url)}" target="_blank" rel="noopener noreferrer">view</a>` : ''}
              </div>
            </div>
          </div>`).join('')
      : `<div class="sa-truth-d">Nothing has merged yet in this window.</div>`);

  el.innerHTML = `
    <div class="a3-tile sa-truth-card">
      <div class="a3-label">The design, working</div>
      <div class="sa-truth-d">EON builds on two rungs. Fable does the
        judging and the large work; Opus builds when Fable's meter is
        walled. A build the judging rung never read is written down as
        debt and re-read within seven days. Each box below carries its
        live count; a box lights up when its count moved in the last day.</div>
      ${_saMachineFlow(m.pipeline, m.holds)}
      ${_saMeasured((m.pipeline || {}).measured_at || m.as_of)}
    </div>
    <div class="a3-tile sa-truth-card">
      <div class="a3-label">What the judging rung still owes</div>
      ${debtBody}
      ${_saMeasured(debt.measured_at)}
    </div>
    <div class="a3-tile sa-truth-card">
      <div class="a3-label">Who built the last merges</div>
      ${buildsBody}
      ${_saMeasured(builds.measured_at)}
    </div>
    <details class="a3-tile sa-explainer">
      <summary>How to test this machine</summary>
      <p>${esc(m.how_to_test || '')}</p>
    </details>`;
}

function _saRenderShipped() {
  const el = document.getElementById('sa-shipped');
  if (!el) return;
  const s = _saState.shipped;
  if (!s || !Array.isArray(s.entries)) {
    el.innerHTML = `<div class="a3-tile sa-pending">Could not read the
      shipped feed right now — this is not zero. It retries on the next
      refresh.</div>`;
    return;
  }
  const errNote = s.errors && Object.keys(s.errors).length
    ? `<div class="sa-truth-d sa-honest">${Object.keys(s.errors).length} source(s) could not be read right now — the feed below is what COULD be read, not the whole story.</div>`
    : '';
  if (!s.entries.length) {
    el.innerHTML = `<div class="a3-tile sa-pending">Nothing shipped inside
      this window.${errNote}</div>`;
    return;
  }
  const first = s.entries.slice(0, 14);
  const rest = s.entries.slice(14);
  el.innerHTML = `
    <div class="a3-tile sa-truth-card">
      <div class="a3-label">Newest first — fixes, improvements, upgrades</div>
      ${errNote}
      ${first.map(_saShipEntry).join('')}
      ${rest.length ? `<details class="sa-greens"><summary>${rest.length} more</summary>${rest.map(_saShipEntry).join('')}</details>` : ''}
      ${_saMeasured(s.measured_at)}
    </div>`;
}

// ── Panel 4: the eval — how the machine is actually doing ──
//
// The weekly_eval measurements, live. Every tile carries measured_at;
// a dead section renders the honest could-not-read tile, never a 0.

function _saEvalTile(label, bigHtml, lines, measuredAt) {
  return `
    <div class="a3-tile sa-truth-card">
      <div class="a3-label">${esc(label)}</div>
      <div class="sa-truth-big">${bigHtml}</div>
      ${lines.filter(Boolean).map(l => `<div class="sa-truth-d">${l}</div>`).join('')}
      ${_saMeasured(measuredAt)}
    </div>`;
}

function _saRenderEval() {
  const el = document.getElementById('sa-eval');
  if (!el) return;
  const ev = _saState.eval;
  if (!ev) {
    el.innerHTML = `<div class="a3-tile sa-pending">Could not read the
      scorecard right now — this is not zero. It retries on the next
      refresh.</div>`;
    return;
  }
  const parts = [];

  const bl = ev.build_lane || {};
  if (bl.ok) {
    parts.push(_saEvalTile('Builds this week',
      `<span class="a3-mono">${bl.total || 0}</span> asked ·
       <span class="a3-mono">${bl.pr_open || 0}</span> reached a PR ·
       <span class="a3-mono">${bl.merged || 0}</span> landed ·
       <span class="a3-mono ${bl.abandoned ? 'sa-bad' : ''}">${bl.abandoned || 0}</span> died`,
      [
        bl.usage_walls
          ? `<span class="sa-bad">${bl.usage_walls} stopped because the AI usage cap was hit</span> — they can be re-run.`
          : 'None stopped at the AI usage cap.',
        bl.median_build_minutes != null
          ? `A successful build took <span class="a3-mono">${esc(String(bl.median_build_minutes))}</span> minutes in the middle.`
          : 'No build recorded its minutes this week.',
        `Paid with: <span class="a3-mono">${bl.on_subscription || 0}</span> on the subscription, <span class="a3-mono">${bl.on_api_credit || 0}</span> on API credit.`,
      ], bl.measured_at));
  } else {
    parts.push(_saCouldNotReadTile('Builds this week'));
  }

  const vf = ev.verifier || {};
  if (vf.ok) {
    const g = vf.last_grading;
    parts.push(_saEvalTile('Is finished work checked before it lands?',
      g ? `<span class="a3-mono">${g.merged_prs_verifier_labelled != null ? g.merged_prs_verifier_labelled : '?'}</span> of <span class="a3-mono">${g.merged_prs != null ? g.merged_prs : '?'}</span> merged pieces carried a checker's verdict` : 'no Monday grading has landed yet',
      [
        g ? `As of the last Monday grading (${esc(g.when || '')}): <span class="a3-mono">${g.verifier_verified != null ? g.verifier_verified : '?'}</span> passed, <span class="a3-mono">${g.verifier_rejected != null ? g.verifier_rejected : '?'}</span> rejected.` : null,
        `<span class="sa-honest">${esc(vf.cannot_measure || '')}</span>`,
      ], vf.measured_at));
  } else {
    parts.push(_saCouldNotReadTile('Is finished work checked before it lands?'));
  }

  const id = ev.ideas || {};
  if (id.ok) {
    parts.push(_saEvalTile('Ideas — from noticing to done',
      `<span class="a3-mono">${id.created_7d || 0}</span> new this week ·
       <span class="a3-mono">${id.approved_7d || 0}</span> approved ·
       <span class="a3-mono">${id.rejected_7d || 0}</span> rejected ·
       <span class="a3-mono">${id.built_7d || 0}</span> done`,
      [
        id.median_days_to_decision != null
          ? `Your typical time from seeing an idea to deciding: <span class="a3-mono">${esc(String(id.median_days_to_decision))}</span> day(s).`
          : 'No decisions inside the window to time yet.',
        id.approved_waiting_to_be_built
          ? `<span class="${id.approved_waiting_to_be_built > 5 ? 'sa-bad' : ''}">${id.approved_waiting_to_be_built} approved idea(s) are still waiting to be built</span>${id.oldest_approved_waiting ? ` — the oldest since ${esc(String(id.oldest_approved_waiting).slice(0, 10))}` : ''}. A yes that never ships is a quiet broken promise.`
          : 'Nothing approved is waiting to be built — the promise queue is clear.',
      ], id.measured_at));
  } else {
    parts.push(_saCouldNotReadTile('Ideas — from noticing to done'));
  }

  const dc = ev.decisions || {};
  if (dc.ok) {
    parts.push(_saEvalTile('Were EON’s calls right?',
      dc.scored
        ? `<span class="a3-mono">${dc.hit_rate_pct != null ? dc.hit_rate_pct + '%' : '?'}</span> of <span class="a3-mono">${dc.scored}</span> graded calls turned out right`
        : 'no calls graded yet',
      [
        dc.note ? `<span class="sa-honest">${esc(dc.note)}</span>` : null,
        dc.scored ? `${dc.scored_7d || 0} graded this week.` : null,
        dc.latest_summary ? esc(dc.latest_summary) : null,
      ], dc.measured_at));
  } else {
    parts.push(_saCouldNotReadTile('Were EON’s calls right?'));
  }

  const co = ev.cost || {};
  if (co.ok) {
    const delta = (co.total_usd_this_week || 0) - (co.total_usd_prior_week || 0);
    const dirWord = delta > 0 ? 'more than' : (delta < 0 ? 'less than' : 'same as');
    parts.push(_saEvalTile('What the thinking cost',
      `<span class="a3-mono">$${(co.total_usd_this_week || 0).toLocaleString()}</span> this week ·
       <span class="a3-mono">$${(co.total_usd_prior_week || 0).toLocaleString()}</span> last week`,
      [
        `${Math.abs(delta) >= 0.01 ? '$' + Math.abs(delta).toFixed(2) + ' ' : ''}${dirWord} last week.`,
        (co.lanes || []).length
          ? `<span class="sa-chiprow">${co.lanes.slice(0, 8).map(l =>
              `<span class="sa-chip">${esc(_saHumanize(l.purpose))} <span class="a3-mono">$${(l.usd_this_week || 0).toLocaleString()}</span></span>`).join('')}</span>`
          : null,
      ], co.measured_at));
  } else {
    parts.push(_saCouldNotReadTile('What the thinking cost'));
  }

  el.innerHTML = parts.join('');
}

// ── Panel 3: the ideas rail (business + code, deduped) ─────

function _saNormTitle(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ')
    .split(/\s+/).filter(w => w.length > 2).sort().join(' ');
}

function _saRenderIdeas() {
  const el = document.getElementById('sa-ideas');
  if (!el) return;
  const biz = _saState.biz;
  if (biz === null) {
    el.innerHTML = `<div class="a3-tile sa-pending">Could not read the
      business ideas right now — this is not zero. The code ideas below
      still load on their own.</div>`;
    return;
  }
  const cards = Array.isArray(biz) ? biz : [];
  // Dedup: a business card whose title matches an open code proposal is
  // shown once — in the code lane below, which carries the evidence.
  const codeOpen = (_saState.proposals || [])
    .filter(p => p.status === 'proposed')
    .map(p => _saNormTitle(p.plain_title || p.title));
  const kept = [];
  let skipped = 0;
  for (const c of cards) {
    if (codeOpen.length && codeOpen.includes(_saNormTitle(c.title))) skipped += 1;
    else kept.push(c);
  }
  if (!kept.length) {
    el.innerHTML = `<div class="a3-tile sa-pending">No business ideas
      waiting on you right now.${skipped ? ` ${skipped} idea(s) appear in both lists — shown once below.` : ''}</div>`;
    return;
  }
  el.innerHTML = `
    <div class="a3-tile sa-truth-card">
      <div class="a3-label">From the Decisions board — plain why first, newest first</div>
      ${skipped ? `<div class="sa-truth-d sa-honest">${skipped} idea(s) appear in both lists — shown once below.</div>` : ''}
      ${kept.map(c => {
        // Adaptive decision copy + the see-it evidence door (design
        // flow 2026-07-31) — same grammar as the Decisions board; the
        // wire decision stays yes|no.
        const w = ideaDecisionWords(c);
        return `
        <div class="sa-truth-line" data-sa-biz-id="${esc(String(c.id || ''))}">
          ${_saDot('amber')}
          <div class="sa-truth-text">
            ${c.why_line ? `<div class="sa-truth-t">${esc(_saShort(c.why_line, 180))}</div>` : ''}
            <div class="sa-truth-d">${esc(_saShort(c.title, 120))}
              ${c.proposer ? `<span class="sa-chip sa-chip-dim">${esc(_saHumanize(c.proposer))}</span>` : ''}
              ${c.created_at ? `<span class="sa-cadence">${esc(_saWhen(c.created_at) || '')}</span>` : ''}</div>
            <div class="sa-actions">
              <button class="sa-btn" data-sa-biz-see="${esc(String(c.id || ''))}">see it</button>
              <button class="sa-btn" data-story-kind="idea" data-story-id="${esc(String(c.id || ''))}">full story</button>
              <button class="sa-btn sa-btn-approve" data-sa-biz-yes="${esc(String(c.id || ''))}">${esc(w.yes)}</button>
              <button class="sa-btn sa-btn-reject" data-sa-biz-no="${esc(String(c.id || ''))}">${esc(w.no)}</button>
            </div>
            <div class="sa-truth-d sa-consq">${esc(w.why)}</div>
          </div>
        </div>`;
      }).join('')}
    </div>`;
}

async function _saOnIdeaClick(e) {
  // The see-it door first — a read, no acting lock needed.
  const seeBtn = e.target.closest('[data-sa-biz-see]');
  if (seeBtn) { openIdeaEvidence(seeBtn.getAttribute('data-sa-biz-see')); return; }
  const yesBtn = e.target.closest('[data-sa-biz-yes]');
  const noBtn = e.target.closest('[data-sa-biz-no]');
  if (!yesBtn && !noBtn) return;
  if (_saState.acting) return;
  const id = yesBtn ? yesBtn.dataset.saBizYes : noBtn.dataset.saBizNo;
  if (!id) return;
  const btn = yesBtn || noBtn;
  _saState.acting = true;
  const prevText = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Working…';
  try {
    const r = await api(`/api/proposals/${encodeURIComponent(id)}/decide`, {
      method: 'POST',
      body: JSON.stringify({ decision: yesBtn ? 'yes' : 'no' }),
    }).catch((err) => ({ ok: false, error: err && err.message }));
    if (r && r.ok) {
      showToast(yesBtn ? 'Approved' : 'Rejected');
      // the same card can sit in Command/Ideas — one settle everywhere
      // (Opus twin verifier 2026-09-02, #6)
      _propSettleEverywhere(id);
      await _saLoad();
    } else {
      showToast((r && r.error) || 'Action failed — try again');
      btn.disabled = false;
      btn.textContent = prevText;
    }
  } finally {
    _saState.acting = false;
  }
}

// ── House documents (2026-08-03) ────────────────────────────
// The rules everything is designed from. A governing document nobody
// can see drift on is a document that rots — so each one carries its
// fingerprint and how long that version has been in force.

function _saBookCard(d) {
  if (!d) return '';
  if (d.missing) {
    return `
      <div class="a3-tile sa-truth-card sa-book-card">
        <div class="a3-label">${esc(d.name || 'Document')}</div>
        <div class="sa-truth-big sa-bad">Not in this build</div>
        <div class="sa-truth-d">EON could not find <code>${esc(d.source || '')}</code> on the server. Nothing is designing from it right now.</div>
      </div>`;
  }
  const kb = d.bytes ? Math.round(d.bytes / 1024) + ' KB' : '';
  const since = d.in_force_since ? _saWhen(d.in_force_since) : null;
  // the ledger is down: the file and its fingerprint are still true,
  // the history is not — never guess a date the page cannot stand behind
  const noHistory = !!d.history_unavailable;
  return `
    <div class="a3-tile sa-truth-card sa-book-card">
      <div class="a3-label">${esc(d.name || 'Document')}
        ${d.fresh ? '<span class="sa-chip sa-book-new">just updated</span>' : ''}</div>
      <div class="sa-truth-big">${(noHistory || !since) ? 'in force' : esc(since)}</div>
      <div class="sa-truth-d">${noHistory
        ? (d.changed
            ? 'the book changed — EON could not record when, so it will not put a date on it'
            : 'EON could not reach the record of when this changed — the book itself is fine')
        : (since
            ? 'this version has been in force since then'
            : (d.first_sight
                ? 'EON is watching it now — nothing has changed since'
                : 'no change recorded yet'))}</div>
      <div class="sa-truth-d sa-book-what">${esc(d.what || '')}</div>
      <div class="sa-book-foot">
        <button class="sa-btn sa-btn-approve" data-book-open="${esc(d.key)}">Open the book</button>
        <span class="sa-book-fp a3-mono">${esc(d.fingerprint || '')}${kb ? ' · ' + esc(kb) : ''}</span>
      </div>
      <div class="sa-truth-d sa-book-src a3-mono">${d.skill ? esc(d.skill) + ' · ' : ''}${esc(d.source || '')}</div>
    </div>`;
}

// The design-gate panel (Panel 6, 2026-08-07) lived here until
// 2026-08-29 — operator: it does not belong on the audit page
// permanently. The gate itself (ci/design_verdicts/ + guards) stands;
// only this surface is gone.

function _saRenderBook() {
  const el = document.getElementById('sa-book');
  if (!el) return;
  const docs = _saState.book;
  if (docs == null) { el.innerHTML = _saCouldNotReadTile('house documents'); return; }
  if (!docs.length) { el.innerHTML = '<div class="a3-tile sa-truth-card"><div class="sa-truth-d">No house documents are being watched.</div></div>'; return; }
  el.innerHTML = docs.map(_saBookCard).join('');
  el.querySelectorAll('[data-book-open]').forEach((b) => {
    b.onclick = () => _saOpenBook(b.dataset.bookOpen, b);
  });
}

// The book is member-gated, and the shell authenticates with a Bearer
// header — a plain <a href> would 401. Fetch it with credentials, hand
// the browser a blob. The tab is opened SYNCHRONOUSLY on the click so
// popup blockers don't eat it, then pointed at the blob when it lands.
async function _saOpenBook(key, btn) {
  if (!key) return;
  const tab = window.open('', '_blank');
  if (btn) { btn.disabled = true; btn.textContent = 'Opening…'; }
  try {
    const headers = {};
    if (State.jwt) headers['Authorization'] = `Bearer ${State.jwt}`;
    if (State.tenant) headers['X-Tenant-Slug'] = State.tenant.slug;
    const res = await fetch(`${API}/api/system-audit/house-docs/${encodeURIComponent(key)}/open`, { headers });
    if (!res.ok) throw new Error('read failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    if (tab) tab.location = url;
    else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (tab) tab.close();
    showToast('Could not open the book — try again');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Open the book'; }
  }
}

function _saRenderEngine() {
  const el = document.getElementById('sa-engine');
  if (!el) return;
  const eng = _saState.engine;
  if (!eng || !Array.isArray(eng.sections)) {
    el.innerHTML = '<div class="a3-tile sa-pending">The engine feed is not live on this build yet.</div>';
    return;
  }
  el.innerHTML = eng.sections.map((s, i) => `
    <div class="a3-tile sa-eng-card a3-reveal" style="--a3-i:${i}">
      <div class="sa-eng-head">
        <div class="sa-eng-titles">
          <div class="a3-label">${esc(s.title || '')}</div>
          <div class="sa-eng-unit">${esc(s.unit || '')}</div>
        </div>
        <div class="sa-eng-big">${esc(String(s.big != null ? s.big : '—'))}</div>
      </div>
      <div class="sa-eng-lines">
        ${(s.lines || []).map(l => `
          <div class="sa-eng-line">
            <div class="sa-eng-text">${esc(l.text || '')}</div>
            ${l.hood ? `<details class="sa-eng-hood"><summary>under the hood</summary><code>${esc(l.hood)}</code></details>` : ''}
          </div>`).join('')}
      </div>
    </div>`).join('');
}

async function bindSystemAudit() {
  _saResetScope();
  const root = document.querySelector('.sa-wrap'), scope = _viewReadScope();
  // Leaving or rebuilding the room cancels its reads and its status poll.
  if (root) root._cancelAuditReads = _saCancelLoad;
  window.EONAuditRoom?.mount({
    root: document.querySelector('.sa-wrap'), state: _saState, loading: true,
    homeView: roleHome(),
    formatDate: _saWhen,
    navigate: view => { State.view = view; render(); },
    refresh: () => _saLoad(),
  });
  // Single delegated listener on the stable container — inner re-renders
  // (60s poll) replace innerHTML only, so the binding survives.
  const list = document.getElementById('sa-proposals');
  if (list && !list.dataset.bound) {
    list.dataset.bound = '1';
    list.addEventListener('click', _saOnProposalClick);
  }
  const rail = document.getElementById('sa-ideas');
  if (rail && !rail.dataset.bound) {
    rail.dataset.bound = '1';
    rail.addEventListener('click', _saOnIdeaClick);
  }
  const machine = document.getElementById('sa-machine');
  if (machine && !machine.dataset.bound) {
    machine.dataset.bound = '1';
    machine.addEventListener('click', _saOnVetClick);
  }
  await _saLoad();
  if (State.view !== 'system-audit' || !root?.isConnected || scope !== _viewReadScope()) return;
  // 60s poll while the view is open, so building → pr_open transitions
  // show without a reload. Self-clears when the operator navigates away.
  if (_saState.pollHandle) clearInterval(_saState.pollHandle);
  _saState.pollHandle = setInterval(() => {
    if (State.view !== 'system-audit') {
      clearInterval(_saState.pollHandle);
      _saState.pollHandle = null;
      return;
    }
    _saLoad(true);
  }, 60000);
}

// One request group per mounted room. A refresh supersedes the older reads;
// a quiet status poll never cancels a still-running full read.
var _saRequest = null;
function _saCancelLoad() {
  if (_saRequest) { _saRequest.controller.abort(); _saRequest = null; }
  if (_saState.pollHandle) { clearInterval(_saState.pollHandle); _saState.pollHandle = null; }
}
function _saResetScope() {
  const scope = _viewReadScope();
  if (_saState.scope === scope) return false;
  _saCancelLoad();
  for (const key of ['proposals','telemetry','engine','biz','truth','eval','shipped','book','machine']) _saState[key] = null;
  _saState.reads = {}; _saState.scope = scope;
  return true;
}
function _saPaintRead(key) {
  const panels = {
    proposals:['sa-proposals', _saRenderProposals], telemetry:['sa-telemetry', _saRenderTelemetry],
    engine:['sa-engine', _saRenderEngine], biz:['sa-ideas', _saRenderIdeas],
    truth:['sa-truth', _saRenderTruth], eval:['sa-eval', _saRenderEval],
    shipped:['sa-shipped', _saRenderShipped], book:['sa-book', _saRenderBook],
    machine:['sa-machine', _saRenderMachine],
  };
  const [id, paint] = panels[key], read = _saState.reads[key], host = document.getElementById(id);
  if (!host) return;
  if (_saState[key] == null && read?.status !== 'read') {
    host.innerHTML = read?.status === 'reading'
      ? '<div class="a3-tile sa-pending" role="status">Reading this source…</div>'
      : '<div class="a3-tile sa-pending" role="alert">This source could not be read. Refresh sources to try again.</div>';
  } else {
    paint();
    if (read?.status === 'reading' || read?.status === 'error') {
      const note = document.createElement('div');
      note.className = 'a3-tile sa-pending';
      note.setAttribute('role', read.status === 'error' ? 'alert' : 'status');
      note.textContent = read.status === 'reading'
        ? 'Reading again. The previous reading is shown below with its original source times.'
        : 'Refresh failed. The previous reading is shown below with its original source times.';
      host.prepend(note);
    }
  }
  if (key === 'proposals' || key === 'telemetry') _saRenderHeader();
  // The ideas rail de-duplicates against open code proposals, so proposals
  // answering after the business feed must repaint the rail too.
  if (key === 'proposals' && _saState.biz != null) _saPaintRead('biz');
}
async function _saLoad(quiet) {
  const root = document.querySelector('.sa-wrap');
  if (State.view !== 'system-audit' || !State.jwt || !root) return;
  const changedScope = _saResetScope();
  if (quiet && _saRequest && _saRequest.root === root) return _saRequest.done;
  if (_saRequest) _saRequest.controller.abort();
  const request = {root, scope:_viewReadScope(), controller:new AbortController()};
  _saRequest = request;
  const current = () => _saRequest === request && !request.controller.signal.aborted
    && State.view === 'system-audit' && request.scope === _viewReadScope()
    && root.isConnected && document.querySelector('.sa-wrap') === root;
  // Keep the api helper's existing 30-second request ceiling when supplying cancellation.
  // This is a request deadline, not a new poll or a backend timeout change.
  const timeout = setTimeout(() => request.controller.abort(), 30000);
  const object = value => value && typeof value === 'object' && !Array.isArray(value) && value.ok !== false ? value : null;
  const array = value => Array.isArray(value) ? value : null;
  const reads = [
    ['proposals','/api/system-audit/proposals', r => r?.ok === false ? null : array(r?.proposals) || array(r)],
    ['telemetry','/api/system-audit/telemetry', object],
    ['engine','/api/system-audit/engine', r => r?.ok === false ? null : object(r?.engine)],
    ['biz','/api/proposals?status=open', r => r?.ok === false ? null : array(r?.cards)],
    ...(!quiet || changedScope ? [
      ['truth','/api/system-audit/truth', r => r?.ok === false ? null : object(r?.truth)],
      ['eval','/api/system-audit/eval', r => r?.ok === false ? null : object(r?.eval)],
      ['shipped','/api/system-audit/shipped', r => r?.ok === false ? null : object(r?.shipped)],
      ['book','/api/system-audit/house-docs', r => r?.ok ? array(r.docs) : null],
      ['machine','/api/system-audit/model-debt', r => r?.ok === false ? null : object(r?.machine)],
    ] : []),
  ];
  for (const [key] of reads) {
    _saState.reads[key] = {..._saState.reads[key], status:'reading'};
    _saPaintRead(key);
  }
  window.EONAuditRoom?.update(_saState);
  request.done = Promise.all(reads.map(async ([key, path, decode]) => {
    let value = null;
    try { value = decode(await api(path, {signal:request.controller.signal, _isCurrent:current}).catch(() => null)); }
    catch (_) { /* The affected source gets an explicit failure below. */ }
    // An expired deadline is a failure for this group; superseded/navigation/
    // identity responses must never reach either the cache or the current room.
    if (_saRequest !== request || State.view !== 'system-audit' || request.scope !== _viewReadScope()
        || !root.isConnected || document.querySelector('.sa-wrap') !== root) return;
    if (value != null && !request.controller.signal.aborted) {
      _saState[key] = value;
      _saState.reads[key] = {status:'read', receivedAt:new Date().toISOString()};
    } else {
      _saState.reads[key] = {..._saState.reads[key], status:'error'};
    }
    _saPaintRead(key);
    window.EONAuditRoom?.update(_saState);
  })).finally(() => {
    clearTimeout(timeout);
    if (_saRequest === request) _saRequest = null;
  });
  await request.done;
  if (_saRequest || State.view !== 'system-audit' || request.scope !== _viewReadScope()
      || !root.isConnected || document.querySelector('.sa-wrap') !== root) return;
  window.EONAuditRoom?.update(_saState);
}

// ── Header strip ───────────────────────────────────────────

function _saLastScan() {
  const t = _saState.telemetry || {};
  const direct = t.generated_at || t.last_scan || t.scanned_at || t.as_of || null;
  if (direct) return _saWhen(direct);
  // Fall back to the newest proposal's created_at.
  const ps = _saState.proposals || [];
  const newest = ps.map(p => p.created_at).filter(Boolean).sort().pop();
  return newest ? _saWhen(newest) : null;
}

function _saTelemetryGroups() {
  const t = _saState.telemetry;
  if (!t || typeof t !== 'object') return [];
  const metaKeys = ['ok', 'generated_at', 'last_scan', 'scanned_at', 'as_of', 'tenant_id'];
  return Object.keys(t).filter(k => !metaKeys.includes(k));
}

function _saRenderHeader() {
  const scanEl = document.getElementById('sa-scanline');
  const countsEl = document.getElementById('sa-counts');
  if (!scanEl || !countsEl) return;
  const pending = ['proposals','telemetry'].some(key => _saState.reads[key]?.status === 'reading');
  const failed = ['proposals','telemetry'].some(key => _saState.reads[key]?.status === 'error');
  // An earlier reading exists only once both sources were read in this scope;
  // _saResetScope() clears both when the shop, user or role changes.
  const prior = _saState.proposals != null && _saState.telemetry != null;
  if ((pending || failed) && !prior) {
    scanEl.textContent = pending ? 'Reading scan history…' : 'Scan history could not be read.';
    countsEl.textContent = 'Counts await both source readings.';
    return;
  }
  // A refresh in flight leaves the earlier line and counts as drawn; a failed
  // one says so beside each source's last good reading and its own scan time.
  const last = _saLastScan();
  scanEl.textContent = (last
    ? `Last scan ${last}`
    : 'No scan yet — first self-evaluation runs Monday 06:30')
    + (failed ? ' · Refresh failed. Showing the last good reading.' : '');
  const ps = _saState.proposals || [];
  const open = ps.filter(p => _SA_OPEN_STATUSES.includes(p.status)).length;
  const thisMonth = new Date().toISOString().slice(0, 7);
  const merged = ps.filter(p => p.status === 'merged'
    && String(p.created_at || '').slice(0, 7) === thisMonth).length;
  const clusters = _saTelemetryGroups().length;
  countsEl.innerHTML = `
    <span class="sa-count"><span class="a3-mono">${open}</span> open</span>
    <span class="sa-count"><span class="a3-mono">${merged}</span> merged this month</span>
    <span class="sa-count"><span class="a3-mono">${clusters}</span> telemetry clusters watched</span>
  `;
}

// ── Telemetry panel — generic stat groups ──────────────────

function _saStatRows(val) {
  // Render any telemetry group value as label/value rows or chips.
  if (Array.isArray(val)) {
    if (val.length === 0) return `<div class="sa-stat-row"><span class="sa-stat-k">items</span><span class="sa-stat-v a3-mono">0</span></div>`;
    const chips = val.slice(0, 4).map(item => {
      if (item && typeof item === 'object') {
        const name = item.name || item.component || item.cluster || item.label
          || item.question || item.source || item.cron || item.id || null;
        const num = ['count', 'n', 'errors', 'times', 'days_stale', 'hours_stale']
          .map(k => item[k]).find(x => typeof x === 'number');
        return `<span class="sa-chip">${esc(_saShort(name != null ? name : item, 42))}${typeof num === 'number' ? ` <span class="a3-mono">${esc(String(num))}</span>` : ''}</span>`;
      }
      return `<span class="sa-chip">${esc(_saShort(item, 42))}</span>`;
    }).join('');
    const more = val.length > 4 ? `<span class="sa-chip sa-chip-dim">+${val.length - 4} more</span>` : '';
    return `<div class="sa-stat-row"><span class="sa-stat-k">items</span><span class="sa-stat-v a3-mono">${val.length}</span></div>
            <div class="sa-chiprow">${chips}${more}</div>`;
  }
  if (val && typeof val === 'object') {
    const keys = Object.keys(val).slice(0, 6);
    if (keys.length === 0) return `<div class="sa-stat-row"><span class="sa-stat-k">nothing flagged</span><span class="sa-stat-v a3-mono">0</span></div>`;
    return keys.map(k => `
      <div class="sa-stat-row">
        <span class="sa-stat-k">${esc(_saHumanize(k))}</span>
        <span class="sa-stat-v a3-mono">${esc(_saShort(val[k], 36))}</span>
      </div>`).join('');
  }
  return `<div class="sa-stat-row"><span class="sa-stat-k">value</span><span class="sa-stat-v a3-mono">${esc(_saShort(val, 36))}</span></div>`;
}

function _saRenderTelemetry() {
  const el = document.getElementById('sa-telemetry');
  if (!el) return;
  const t = _saState.telemetry;
  if (!t) {
    el.innerHTML = `<div class="a3-tile sa-pending">Telemetry digest not available yet —
      it goes live with the first self-evaluation run. Proposals below still work.</div>`;
    return;
  }
  const groups = _saTelemetryGroups();
  if (groups.length === 0) {
    el.innerHTML = `<div class="a3-tile sa-pending">Digest is live but empty — nothing flagged on the last scan.</div>`;
    return;
  }
  el.innerHTML = groups.map(k => `
    <div class="a3-tile sa-stat-card">
      <div class="a3-label">${esc(_saHumanize(k))}</div>
      ${_saStatRows(t[k])}
      <div class="sa-meaning">${esc(_SA_MEANING[k] || 'A signal EON watches about its own behaviour.')}</div>
    </div>
  `).join('');
}

// ── Proposal cards ─────────────────────────────────────────

function _saEvidenceHTML(ev) {
  if (!ev || typeof ev !== 'object') return '';
  const keys = Object.keys(ev);
  if (keys.length === 0) return '';
  const rows = keys.slice(0, 6).map(k => {
    const v = ev[k];
    if (k === 'sample' || k === 'example' || k === 'trace') {
      return `<div class="sa-ev-sample a3-mono">${esc(_saShort(v, 160))}</div>`;
    }
    return `<span class="sa-chip">${esc(_saHumanize(k))}: <span class="a3-mono">${esc(_saShort(v, 48))}</span></span>`;
  });
  const chips = rows.filter(r => r.startsWith('<span')).join('');
  const samples = rows.filter(r => r.startsWith('<div')).join('');
  return `<div class="sa-evidence"><div class="sa-ev-label">Evidence</div><div class="sa-chiprow">${chips}</div>${samples}</div>`;
}

// What to say under an APPROVED card. The backend already knows whether
// the build was dispatched — it writes decision_note on approve — so read
// it instead of asserting a state the frontend cannot see.
function _saApprovedNote(p) {
  const note = String(p.decision_note || '');
  if (/dispatched/i.test(note)) return 'Approved. EON is building it now.';
  if (/not yet enabled|not enabled/i.test(note)) {
    return 'Approved. Waiting to be picked up — automatic building is '
         + 'switched off, so this needs a person to start it.';
  }
  if (note) return `Approved. ${note}`;
  return 'Approved. Waiting to be picked up.';
}

function _saActionsHTML(p) {
  const id = esc(String(p.id ?? ''));
  const url = String(p.pr_url || '');
  const safeUrl = /^https?:\/\//i.test(url) ? url : '';
  switch (p.status) {
    case 'proposed':
      return `
        <button class="sa-btn sa-btn-approve" data-sa-approve="${id}">Approve</button>
        <button class="sa-btn sa-btn-reject" data-sa-reject="${id}">Reject</button>`;
    case 'approved':
      // Say what actually happened, not a build-time guess. This used to
      // hardcode "Auto-build is not enabled yet" for every approved card,
      // which was a standing lie the moment it WAS enabled — and a
      // confusing one before that, because "queued" and "not enabled"
      // contradict each other on the same line. The approve response
      // carries `dispatched` + `note`; the list carries decision_note.
      return `<span class="sa-status-note">${esc(_saApprovedNote(p))}</span>`;
    case 'building':
      return `<span class="sa-status-note"><span class="sa-spinner"></span>Builder is writing the code and tests…</span>`;
    case 'pr_open':
      // Copy reconciled 2026-08-03: the old line said "never
      // automatically", which predated the merge governance — an
      // operator-approved proposal whose checks all pass CAN land on
      // its own now. Say what is true instead of repeating the old law.
      return `
        ${safeUrl ? `<a class="sa-btn sa-btn-pr" href="${esc(safeUrl)}" target="_blank" rel="noopener noreferrer">Review PR on GitHub</a>` : `<span class="sa-status-note">PR open — link not provided yet.</span>`}
        <span class="sa-micro">Lands when every check passes — on its own for approved work, otherwise a person merges it.</span>`;
    case 'merged': {
      // The receipt (2026-08-03): merged_at existed since the landing
      // stamp shipped but was never rendered — "Merged." with no date
      // is a receipt with no time on it.
      const landed = p.merged_at ? ` ${String(p.merged_at).slice(0, 10)}` : '';
      return `${safeUrl ? `<a class="sa-history-link" href="${esc(safeUrl)}" target="_blank" rel="noopener noreferrer">View merged PR</a>` : ''}<span class="sa-status-note">Landed${landed}.</span>`;
    }
    case 'rejected':
      // The operator's reason lives in decision_note (mig 254) — this
      // table has no reject_reason column; that name belongs to other
      // lanes (schedule_studio, automation_tray).
      return `<span class="sa-status-note">Rejected${p.decision_note ? ` — ${esc(_saShort(p.decision_note, 120))}` : ''}.</span>`;
    case 'failed':
      return `<span class="sa-status-note sa-status-bad">Build failed — EON will fold the failure into its next evaluation.</span>`;
    default:
      return `<span class="sa-status-note">${esc(_saHumanize(p.status || 'unknown'))}</span>`;
  }
}

function _saProposalCard(p) {
  const kind = _SA_KIND[p.kind] || { label: _saHumanize(p.kind || 'proposal'), color: 'var(--a3-mut)', bg: '#ffffff10' };
  const risk = _SA_RISK[p.risk] || null;
  const isHistory = ['merged', 'rejected', 'failed'].includes(p.status);
  const files = Array.isArray(p.affected_files) ? p.affected_files : [];
  const when = _saWhen(p.created_at);
  return `
    <div class="a3-tile sa-card${isHistory ? ' sa-card-history' : ''}" data-sa-id="${esc(String(p.id ?? ''))}">
      <div class="sa-card-top">
        <span class="sa-chip" style="color:${kind.color};background:${kind.bg};border-color:transparent">${esc(kind.label)}</span>
        ${risk ? `<span class="sa-chip" style="color:${risk.color};background:${risk.bg};border-color:transparent">${esc(risk.label)}</span>` : ''}
        <span class="sa-chip sa-chip-dim">${esc(_saHumanize(p.status || ''))}</span>
        ${when ? `<span class="sa-when">${esc(when)}</span>` : ''}
      </div>
      <div class="sa-title">${esc(p.plain_title || p.title || 'Untitled proposal')}</div>
      ${(p.plain_why || p.rationale) ? `<div class="sa-why"><span class="sa-why-k">Why</span>${esc(p.plain_why || p.rationale)}</div>` : ''}
      ${(p.plain_title || p.plain_why) && (p.title || p.rationale) ? `
        <details class="sa-tech">
          <summary>The technical version</summary>
          ${p.title ? `<div class="sa-tech-t">${esc(p.title)}</div>` : ''}
          ${p.rationale ? `<div class="sa-tech-r">${esc(p.rationale)}</div>` : ''}
        </details>` : ''}
      ${_saEvidenceHTML(p.evidence)}
      ${files.length ? `<div class="sa-chiprow sa-files">${files.slice(0, 8).map(f =>
        `<span class="sa-chip sa-file a3-mono">${esc(_saShort(f, 52))}</span>`).join('')}${files.length > 8 ? `<span class="sa-chip sa-chip-dim">+${files.length - 8} more</span>` : ''}</div>` : ''}
      ${p.test_plan ? `<details class="sa-testplan"><summary>Test plan</summary><div>${esc(p.test_plan)}</div></details>` : ''}
      <div class="sa-actions">${_saActionsHTML(p)}<button class="sa-btn" data-story-kind="audit" data-story-id="${esc(String(p.id ?? ''))}">Full story</button></div>
    </div>
  `;
}

function _saRenderProposals() {
  const el = document.getElementById('sa-proposals');
  const meta = document.getElementById('sa-prop-meta');
  if (!el) return;
  const ps = _saState.proposals;
  if (ps == null) {
    if (meta) meta.textContent = '';
    el.innerHTML = `<div class="a3-tile sa-pending">Self-evaluation backend is not live yet.
      Proposals will appear here after the first weekly run — Mondays 06:30.
      Nothing ships without your approval, and merging always stays manual on GitHub.</div>`;
    return;
  }
  if (ps.length === 0) {
    if (meta) meta.textContent = '';
    el.innerHTML = `<div class="a3-tile sa-pending">First self-evaluation runs Monday 06:30.
      EON will propose up to 3 grounded improvements; you approve or reject each one here.</div>`;
    return;
  }
  const decide = ps.filter(p => p.status === 'proposed');
  const flight = ps.filter(p => ['approved', 'building', 'pr_open'].includes(p.status));
  const history = ps.filter(p => !['proposed', 'approved', 'building', 'pr_open'].includes(p.status));
  if (meta) meta.textContent = `${decide.length} awaiting decision`;
  const block = (label, items) => items.length
    ? `<div class="sa-group-label">${esc(label)}</div>` + items.map(_saProposalCard).join('')
    : '';
  el.innerHTML =
    block('Awaiting your decision', decide) +
    block('In flight', flight) +
    block('History', history);
}

// Closing a debt row by hand. Without this the ledger had three exits
// and all three were shut for the ordinary case: the sweep's own close
// path only fires on a re-vet that lands code, and this endpoint had no
// caller at all — so the panel's "waiting on a re-vet" count could only
// ever rise. The operator needs one honest way to say "I read it".
async function _saOnVetClick(e) {
  const btn = e.target.closest('[data-sa-vet]');
  if (!btn || _saState.acting) return;
  const pr = btn.dataset.saVet;
  const verdict = btn.dataset.saVerdict;
  if (!pr || !verdict) return;
  const notes = verdict === 'escalated'
    ? prompt('What needs a person? This goes on the ledger row.')
    : '';
  if (notes === null) return;                  // cancelled
  _saState.acting = true;
  const prevText = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Working…';
  try {
    const path = `/api/system-audit/model-debt/${encodeURIComponent(pr)}/vet`;
    const r = await api(path, {
      method: 'POST',
      body: JSON.stringify({ verdict, notes: (notes || '').trim() }),
    }).catch((err) => ({ ok: false, error: err && err.message }));
    if (r && r.ok) {
      showToast(verdict === 'clean' ? 'Marked read' : 'Flagged for you');
      await _saLoad();
    } else {
      showToast((r && r.error) || 'Could not close that row');
      btn.disabled = false;
      btn.textContent = prevText;
    }
  } finally {
    _saState.acting = false;
  }
}


async function _saOnProposalClick(e) {
  const approveBtn = e.target.closest('[data-sa-approve]');
  const rejectBtn = e.target.closest('[data-sa-reject]');
  if (!approveBtn && !rejectBtn) return;
  if (_saState.acting) return;
  const id = approveBtn ? approveBtn.dataset.saApprove : rejectBtn.dataset.saReject;
  if (!id) return;
  let body = null;
  if (rejectBtn) {
    const reason = prompt('Why reject? EON folds this into its next evaluation.');
    if (reason === null) return;             // cancelled
    body = JSON.stringify({ reason: reason.trim() });
  }
  const btn = approveBtn || rejectBtn;
  _saState.acting = true;
  const prevText = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Working…';
  try {
    const path = `/api/system-audit/proposals/${encodeURIComponent(id)}/${approveBtn ? 'approve' : 'reject'}`;
    const r = await api(path, { method: 'POST', body: body || '{}' }).catch((err) => ({ ok: false, error: err && err.message }));
    if (r && r.ok) {
      showToast(approveBtn ? 'Approved — queued for build' : 'Rejected — EON will learn from it');
      await _saLoad();
    } else {
      showToast((r && r.error) || 'Action failed — try again');
      btn.disabled = false;
      btn.textContent = prevText;
    }
  } finally {
    _saState.acting = false;
  }
}

// ══════════════════════════════════════════════════════════════════
// HOUSE floor 8 — THE VAULT · the Workbench drawer (WS-I, 2026-08-07)
//
// Twenty sections became a wall and a drawer. Four experiments left
// the morning path: LAB1 (borrow-to-buy-stock), the vendors ledger,
// the AI-credits board and Pricing OS. Same mount ids, same four
// hydrators — the only thing that changed is WHERE they mount, so the
// drawer is lazy by construction exactly like Books and Tax.
// ══════════════════════════════════════════════════════════════════

function renderVaultWorkbench() {
  const contextDesk = (State.member?.base_role || State.member?.role) === 'owner' && !State.impersonating;
  return `
    ${contextDesk ? '' : `<div class="section-h money-section-h">
      <h2>Money</h2>
      <span class="meta" id="money-period-meta">Tools</span>
    </div>`}
    ${renderMoneyTabBar(contextDesk ? 'workbench' : 'tools')}
    ${contextDesk ? `${renderMoneyReviewDesk()}<details class="mr-related" id="mr-requests"><summary>Requests and receipts<small>Approval, payment and receipt records stay separate.</small></summary><div id="money-request-history"><p>Open to read the latest requests.</p></div></details><details class="mr-related" id="mr-tools"${State.moneyToolsOpen ? ' open' : ''}><summary>Planning and money tools<small>Borrowing scenarios, vendors, tool credits and pricing.</small></summary>` : ''}
    <p class="hs-vault-bench-note">Explore a decision here before committing money. These tools do not approve spending.</p>

    <div class="section-h"><h2>Borrowing to buy stock</h2><span class="meta">does stock bought with a loan earn more than the loan costs?</span></div>
    <div id="money-lab1">
      <div class="data-card" style="opacity: 0.4;">
        <div class="skel skel-line" style="width: 45%; height: 13px;"></div>
        <div class="skel skel-line" style="width: 100%; height: 44px; margin-top: 16px; border-radius: 10px;"></div>
      </div>
    </div>

    <div class="section-h"><h2>Vendors</h2><span class="meta">what each vendor's goods earn · commissions recorded</span></div>
    <div id="money-vendors">
      <div class="data-card" style="opacity: 0.4;">
        <div class="skel skel-line" style="width: 45%; height: 13px;"></div>
        <div class="skel skel-line" style="width: 100%; height: 44px; margin-top: 16px; border-radius: 10px;"></div>
      </div>
    </div>

    <div class="section-h"><h2>Ad wallets and tool credits</h2><span class="meta">what's left · how long it lasts · renewal dates</span></div>
    <div id="money-wallets">
      <div class="data-card" style="opacity: 0.4;">
        <div class="skel skel-line" style="width: 45%; height: 13px;"></div>
        <div class="skel skel-line" style="width: 100%; height: 44px; margin-top: 16px; border-radius: 10px;"></div>
      </div>
    </div>

    <div class="section-h"><h2>Prices</h2><span class="meta">target margin for each price tier · suggested price changes for you to decide</span></div>
    <div id="money-pricing">
      <div class="data-card" style="opacity: 0.4;">
        <div class="skel skel-line" style="width: 45%; height: 13px;"></div>
        <div class="skel skel-line" style="width: 100%; height: 44px; margin-top: 16px; border-radius: 10px;"></div>
      </div>
    </div>${contextDesk ? '</details>' : ''}`;
}

let _moneyReviewDispose = null;
function bindVaultWorkbench() {
  bindMoneyTabBar();
  if (typeof prcHydrateMoneyStrip === 'function') prcHydrateMoneyStrip();
  // Own generation token, same idiom as refreshMoney: a slow leg from a
  // previous tab visit must never paint over a fresh one.
  State._moneyGen = (State._moneyGen || 0) + 1;
  const gen = State._moneyGen;
  const reviewRoot = document.getElementById('money-review');
  if (reviewRoot) {
    document.querySelector('.money-tab[aria-selected="true"]')?.scrollIntoView({block:'nearest',inline:'nearest'});
    _moneyReviewDispose?.();
    const viewScope=moneyViewScope(State), file=State.moneyStatementFile?moneyStatementFileId():null;
    _moneyReviewDispose = mountMoneyReviewDesk(reviewRoot, {
      api,
      registerDraft:moneyRegisterDraft,
      // Saved filters return only to the same statement file (or to none).
      readView:()=>(window.history.state?.moneyReviewFile??null)===file?readMoneyView(window.history,viewScope,'review'):null,
      saveView:data=>{if(viewScope!==moneyViewScope(State))return;writeMoneyView(window.history,viewScope,'review',data);
        try{window.history.replaceState({...window.history.state,moneyReviewFile:file},'');}catch{/* history storage refused */}},
      file,
      leaveFile:()=>{if(!mtxAllowPageRebuild())return;State.moneyStatementFile=null;moneySyncEntry(true);render();},
      openLedger: () => { _ldgS.queue = 'all'; moneyOpenView('ledger'); },
      openAccounting: (line, callbacks) => _ldgBankOpen(line, callbacks),
      openTools: () => {
        const tools=document.getElementById('mr-tools');
        tools.open=true;tools.querySelector('summary').focus();tools.scrollIntoView({block:'start'});
      },
    });
    const workspace = reviewRoot.closest('#money-workspace');
    if (workspace) {
      const previousDispose = workspace._disposeMoneyWorkspace;
      workspace._disposeMoneyWorkspace = () => {
        _moneyReviewDispose?.(); _moneyReviewDispose = null; previousDispose?.();
      };
    }
    // M3b: a statement line names its entry by the server's typed kind.
    // The statement file this desk shows (M3a) travels with it, so the return keeps it.
    reviewRoot.addEventListener('money:open-entry', e => moneyOpenBookEntry({...e.detail, file}));
    const backTo = State.moneyReturnLine; State.moneyReturnLine = null;
    if (typeof backTo === 'string') reviewRoot.dispatchEvent(new CustomEvent('money:open-line', {detail: {id: backTo}}));
    const requests = document.getElementById('mr-requests');
    requests.addEventListener('toggle',()=>{if(requests.open&&!requests.dataset.historyLoaded){requests.dataset.historyLoaded='true';prcLoadMoneyHistory();}});
    ['prc-strip', 'prc-pack'].forEach(id => { const node=document.getElementById(id); if(node) requests.append(node); });
    const namedRequest = State.moneyRequestRef; State.moneyRequestRef = null;
    if (namedRequest && namedRequest.scope === moneyDraftScope()) { requests.open = true; prcOpenExistingRequest(namedRequest.id, false); }
    const tools = document.getElementById('mr-tools');
    const loadTools = () => {
      if (!tools.open || tools.dataset.loaded) return;
      tools.dataset.loaded = 'true';
      refreshMoneyLab1(gen); refreshMoneyVendors(gen); refreshMoneyWallets(gen); refreshMoneyPricing(gen);
    };
    tools.addEventListener('toggle', loadTools);
    loadTools(); // A cold tools link may already be open before listeners attach.
    State.moneyToolsOpen = false;
    return;
  }
  if (typeof refreshMoneyLab1 === 'function') refreshMoneyLab1(gen);
  if (typeof refreshMoneyVendors === 'function') refreshMoneyVendors(gen);
  if (typeof refreshMoneyWallets === 'function') refreshMoneyWallets(gen);
  if (typeof refreshMoneyPricing === 'function') refreshMoneyPricing(gen);
}

// ══════════════════════════════════════════════════════════════════
// BOOKS+TAX BEGIN (v3.0p106)
// Money → Books tab (audit-grade double-entry statements from
// /api/accounting/books/*) + Tax tab (Tax OS cockpit on /api/tax/*).
// Built for LIRS/NRS audit sessions: every figure drills to its
// journal source, the audit pack prints clean, and the tax cards
// show band-by-band computation detail — quiet authority, no fluff.
//
// Design rules honored here:
//   - Lazy: nothing below fetches until its tab is opened.
//   - Fail-soft: each card loads independently; an error renders a
//     quiet retry affordance instead of killing the page.
//   - Mono figures, right-aligned, totals ruled (see .bks-* CSS).
//   - No emoji glyphs anywhere in UI strings.
// ══════════════════════════════════════════════════════════════════

const MONEY_TABS_DEF = [
  { key: 'overview', label: 'Position', views: ['overview'] },
  { key: 'ledger', label: 'Transactions', views: ['ledger', 'workbench', 'tools'] },
  { key: 'books', label: 'Close & tax', views: ['books', 'tax'] },
  { key: 'projection', label: 'Plan & grow', views: ['projection'] },
];

function renderMoneyTabBar(active) {
  const owner=(State.member?.base_role || State.member?.role)==='owner' && !State.impersonating;
  const remembered=readMoneyView(window.history,moneyViewScope(State),'sections')||{};
  const transactions=['ledger','workbench','tools'].includes(active);
  const close=['books','tax'].includes(active);
  const sub=transactions ? [['ledger','Book entries'],...(owner?[['workbench','Statement lines']]:[['tools','Money tools']])]
    : close ? [['books','Close & books'],['tax','Tax records']] : [];
  return `
    <div class="money-tabbar" id="money-tabbar" role="tablist" data-active="${active}">
      ${MONEY_TABS_DEF.map((t,i) => {
        const selected=t.views.includes(active), destination=moneyResolveView(selected?active:t.key==='ledger'?(remembered.transactions||(owner?'workbench':'ledger')):t.key==='books'?(remembered.close||'books'):t.key).key;
        return `<button class="money-tab ${selected?'money-tab-active':''}" role="tab" aria-selected="${selected}"
          data-money-tab="${destination}"><span aria-hidden="true">0${i+1}</span>${t.label}</button>`;
      }).join('')}
    </div>
    ${sub.length?`<div class="money-subnav" id="money-subnav" role="group" aria-label="${transactions?'Transaction records':'Close and tax tools'}">${sub.map(([key,label])=>`<button type="button" data-money-tab="${key}" aria-pressed="${key===active}">${label}</button>`).join('')}${transactions&&owner?'<button type="button" data-money-requests>Requests & receipts</button>':''}</div>`:''}
    <div id="prc-strip" class="prc-strip" hidden></div>
    <details id="prc-pack" class="prc-pack" hidden>
      <summary class="prc-pack-sum">Packaging control</summary>
      <div id="prc-pack-body" class="prc-pack-body"></div>
    </details>`;
}

function moneyOpenView(key, keyboard=false, {booksPeriod=null}={}) {
  const target=moneyResolveView(key);
  if(!target.denied&&!target.tools&&!State.moneyGateDenied&&target.key===(State.moneyTab||'overview'))return;
  // DOM-wide first: a typed Tax correction is asked about whatever the sign-in shape.
  if(!mtxLeaveCorrection()||!mtxAllowPageRebuild())return;
  const group=view=>MONEY_TABS_DEF.find(tab=>tab.views.includes(view))?.key;
  const push=group(State.moneyTab||'overview')!==group(target.key);
  key=target.key;
  State.moneyGateDenied=target.denied;
  State.moneyToolsOpen=target.tools;
  _moneyReviewDispose?.();_moneyReviewDispose=null;
  const scope=moneyViewScope(State),sections=readMoneyView(window.history,scope,'sections')||{};
  for(const view of [State.moneyTab,key]){if(['ledger','workbench','tools'].includes(view))sections.transactions=view;if(['books','tax'].includes(view))sections.close=view;}
  writeMoneyView(window.history,scope,'sections',sections);
  // Only an explicit drill (the P&L card) carries a month, after the guards
  // and the leaving entry are saved; the Books tab itself keeps its own.
  if(booksPeriod&&!target.denied&&key==='books')State.booksPeriod=moneyEntryMonth(booksPeriod)||State.booksPeriod;
  State.moneyTab=key;
  moneySyncEntry(push);
  render();
  requestAnimationFrame(()=>{
    const workspace=document.getElementById('money-workspace');
    workspace?.scrollIntoView({block:'start',behavior:'instant'});
    const heading=workspace?.querySelector('.mw-page-heading h2,.mf-header h2,.mr-mast h1,.mw-bank-head h2')||workspace?.querySelector('.mw-body');
    if(heading){heading.tabIndex=-1;heading.focus({preventScroll:true});}
  });
}

function bindMoneyTabBar() {
  document.querySelector('[data-money-requests]')?.addEventListener('click',()=>{
    moneyOpenView('workbench');requestAnimationFrame(()=>{
      const requests=document.getElementById('mr-requests');if(!requests)return;
      requests.open=true;requests.querySelector('summary').focus();requests.scrollIntoView({block:'start'});
    });
  });
  document.querySelectorAll('#money-tabbar [data-money-tab],#money-subnav [data-money-tab]').forEach(btn=>{
    btn.addEventListener('click',event=>moneyOpenView(btn.dataset.moneyTab,event.detail===0));
  });
}

// ── Shared helpers ────────────────────────────────────────────────

function bksCurrentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function bksStepMonth(period, delta) {
  const y = parseInt(period.slice(0, 4), 10);
  const m = parseInt(period.slice(5, 7), 10);
  const d = new Date(y, m - 1 + delta, 1);
  const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  // Never step into the future — tax/books periods are realized months.
  return next > bksCurrentMonth() ? bksCurrentMonth() : next;
}

function bksMonthLabel(period) {
  const y = parseInt(period.slice(0, 4), 10);
  const m = parseInt(period.slice(5, 7), 10);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US',
    { month: 'long', year: 'numeric' });
}

// Audit-grade figure: full naira, thousands separators, sign preserved.
// Compact fmtNGN() stays for chips; statements never abbreviate.
function bksNGN(n) { return ngn(n); }

// Contra/deduction presentation: (₦x) — accounting statement style.
function bksParen(n) { return ngn(Number(n) || 0, { paren: true }); }

function bksSkel(rows) {
  let out = '';
  for (let i = 0; i < rows; i++) {
    out += `<div class="skel skel-line" style="width:${85 - i * 12}%;height:13px;margin:12px 0;"></div>`;
  }
  return out;
}

// Fail-soft card loader: one card erroring renders a quiet retry,
// never a dead page. loadFn(host) must replace host.innerHTML itself.
async function bksLoadCard(hostId, loadFn) {
  const host = document.getElementById(hostId);
  if (!host) return;
  try {
    await loadFn(host);
  } catch (e) {
    host.innerHTML = `
      <div class="bks-soft-err">
        <span>This record did not load.</span>
        <button class="bks-retry-btn" type="button">Try again</button>
        <span class="bks-soft-err-msg">No amount or completed status can be established from this read.</span>
      </div>`;
    host.querySelector('.bks-retry-btn')?.addEventListener('click',
      () => bksLoadCard(hostId, loadFn));
  }
}

// ══════════════════════════════════════════════════════════════════
// BOOKS TAB
// ══════════════════════════════════════════════════════════════════

function renderBooksTab() {
  const period = State.booksPeriod || (State.booksPeriod = bksCurrentMonth());
  // Vet 2026-07-27: manager+ to match the backend gate (ACCOUNTING_
  // MANAGE) — managers were locked out of JE/Rebuild they hold.
  const role = State.member?.base_role || State.member?.role || 'member';
  const canManage = ['owner', 'admin', 'manager'].includes(role);
  return `
    <div class="section-h money-section-h">
      <h2>Close the books.</h2>
      <span class="meta">Money earned · money paid · supporting records</span>
    </div>
    ${renderMoneyTabBar('books')}

    <div class="bks-toolbar">
      <div class="bks-month-step" id="bks-month-step">
        <button class="bks-step-btn" data-bks-step="-1" aria-label="Previous month">&lsaquo;</button>
        <span class="bks-month-label" id="bks-month-label">${esc(bksMonthLabel(period))}</span>
        <button class="bks-step-btn" data-bks-step="1" aria-label="Next month">&rsaquo;</button>
      </div>
      <div class="bks-toolbar-actions">
        <button class="bks-tool-btn" id="bks-audit-btn" type="button">Audit pack</button>
        <button class="bks-tool-btn" id="bks-export-btn" type="button">Export</button>
        ${canManage ? `<button class="bks-tool-btn bks-tool-quiet" id="bks-je-btn" type="button" onclick="bksJournalOpen()">Journal entry</button>
        <button class="bks-tool-btn bks-tool-quiet" id="bks-derive-btn" type="button">Rebuild period</button>
        <button class="bks-tool-btn" id="bks-close-btn" type="button">Close month</button>` : ''}
      </div>
    </div>
    <!-- Money engine lane 5 (2026-09-02): the truth line. Before any
         statement renders, the reader knows how far the books are
         derived, how far the bank statements reach, how many lines are
         unexplained, how many close checks pass, and when the month was
         last closed. Freshness in words, never a status code. -->
    <div class="bks-truth" id="bks-truth" aria-live="polite"></div>
    <div class="bks-close-report" id="bks-close-report" hidden></div>

    <div class="section-h"><h2>Profit and loss</h2><span class="meta" id="bks-pnl-meta"></span></div>
    <div id="bks-pnl" class="card bks-card">${bksSkel(5)}</div>

    <div class="section-h"><h2>Trial balance</h2><span class="meta" id="bks-tb-meta"></span></div>
    <div id="bks-tb" class="card bks-card">${bksSkel(5)}</div>

    <div class="section-h"><h2>Balance sheet</h2><span class="meta" id="bks-bs-meta"></span></div>
    <div id="bks-bs" class="card bks-card">${bksSkel(4)}</div>

    <div class="section-h"><h2>Bank &harr; books</h2><span class="meta" id="bks-rec-meta"></span></div>
    <div id="bks-rec" class="card bks-card">${bksSkel(3)}</div>

    <div class="section-h"><h2>Settlement truth</h2><span class="meta" id="bks-setl-meta"></span></div>
    <div id="bks-setl" class="card bks-card">${bksSkel(3)}</div>

    <div class="bks-foot-note">Every line traces to a source row. Tap any account to open its ledger — the "where does this naira come from" answer for an auditor.</div>
  `;
}

function bindBooksTab() {
  // Month stepper — reloads the three statements in place.
  document.querySelectorAll('#bks-month-step .bks-step-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const cur = State.booksPeriod || bksCurrentMonth();
      const next = bksStepMonth(cur, parseInt(btn.dataset.bksStep, 10));
      if (next === cur) return;
      State.booksPeriod = next;
      moneySyncEntry();
      const lbl = document.getElementById('bks-month-label');
      if (lbl) lbl.textContent = bksMonthLabel(next);
      bksReloadStatements();
    });
  });
  document.getElementById('bks-audit-btn')
    ?.addEventListener('click', () => bksOpenAuditPack());
  document.getElementById('bks-export-btn')
    ?.addEventListener('click', () => bksExportOpen());
  document.getElementById('bks-derive-btn')
    ?.addEventListener('click', () => bksDerivePeriod());
  document.getElementById('bks-close-btn')
    ?.addEventListener('click', () => bksCloseMonth());
  bksReloadStatements();
}

function bksReloadStatements() {
  const ids = ['bks-pnl', 'bks-tb', 'bks-bs', 'bks-rec'];
  ids.forEach((id) => {
    const host = document.getElementById(id);
    if (host) host.innerHTML = bksSkel(5);
  });
  bksLoadTruth();
  moneyRead('bks-pnl',()=>bksLoadCard('bks-pnl', bksLoadPnl),true);
  moneyRead('bks-tb',()=>bksLoadCard('bks-tb', bksLoadTrialBalance),true);
  moneyRead('bks-bs',()=>bksLoadCard('bks-bs', bksLoadBalanceSheet),true);
  moneyRead('bks-rec',()=>bksLoadCard('bks-rec', bksLoadBankRec),true);
  moneyRead('bks-setl',()=>bksLoadCard('bks-setl', bksLoadSettlement),true);
}

// Ledger drill-down clicks — delegated per rendered container so the
// same statement HTML works in the card AND in the audit-pack overlay.
function bksBindLedgerClicks(root) {
  if (!root || root.dataset.bksLedgerBound) return;
  root.dataset.bksLedgerBound = '1';
  const open = (e) => {
    const row=e.target.closest('[data-bks-ledger]');
    if(!row || e.target.closest('summary')) return;
    e.preventDefault();
    bksOpenLedger(row.dataset.bksLedger);
  };
  root.addEventListener('click',open);
  root.addEventListener('keydown',e=>{if(e.key==='Enter'||e.key===' ')open(e);});
}

// ── The truth line + the close (money engine, 2026-09-02) ────────

function bksTruthItem(tone, text) {
  return `<span class="bks-truth-item bks-truth-${tone}">${text}</span>`;
}

function bksDaysAgo(iso) {
  if (!iso) return null;
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  const age=Math.round((Date.now() - d.getTime()) / 86400000);
  return age < 0 ? null : age;
}

function bksTruthHTML(h, closes) {
  const f = (h && h.figures) || {};
  const out = [];
  const dAge = bksDaysAgo(f.derived_through);
  out.push(bksTruthItem(dAge == null ? 'mute' : (dAge <= 1 ? 'ok' : (dAge <= 3 ? 'warn' : 'bad')),
    f.derived_through ? `Books updated through ${esc(f.derived_through)}` : 'Books update date unavailable'));
  const bAge = bksDaysAgo(f.bank_covered_through);
  out.push(bksTruthItem(bAge == null ? 'mute' : (bAge <= 7 ? 'ok' : (bAge <= 21 ? 'warn' : 'bad')),
    f.bank_covered_through ? `Bank statements reach ${esc(f.bank_covered_through)}` : 'Bank statement coverage date unavailable'));
  const count=v=>(typeof v==='number'||typeof v==='string'&&v.trim()!=='')&&Number.isInteger(Number(v))&&Number(v)>=0;
  const counted=count(f.bank_lines)&&count(f.bank_explained)&&Number(f.bank_explained)<=Number(f.bank_lines);
  const lines=counted?Number(f.bank_lines):null,unex=counted?lines-Number(f.bank_explained):null;
  out.push(bksTruthItem(!counted||lines===0?'mute':unex===0?'ok':unex/lines<=0.1?'warn':'bad',
    !counted?'Statement counts unavailable':lines===0?'No statement lines in this period':unex===0?`All ${lines.toLocaleString('en-US')} statement lines explained`:`${unex.toLocaleString('en-US')} of ${lines.toLocaleString('en-US')} statement lines unexplained`));
  const checks = (h && h.checks) || [];
  const pass = checks.filter((c) => c.ok === true).length;
  const gaps = checks.filter((c) => c.ok === false);
  const unknown=checks.length-pass-gaps.length;
  out.push(bksTruthItem(!checks.length||unknown ? 'mute' : (gaps.length === 0 ? 'ok' : (gaps.length <= 2 ? 'warn' : 'bad')),
    checks.length ? `${pass} of ${checks.length} close checks pass${unknown?` · ${unknown} results unavailable`:''}${gaps.length ? ` — ${esc(gaps.map((g) => g.label.toLowerCase()).join(', '))}` : ''}` : 'Close checks unavailable'));
  const last = closes && closes.closes && closes.closes[0];
  const selected=typeof State!=='undefined'&&State.booksPeriod||new Date().toISOString().slice(0,7);
  const monthIndex=p=>/^\d{4}-(0[1-9]|1[0-2])$/.test(p||'')?Number(p.slice(0,4))*12+Number(p.slice(5,7)):null;
  const lag=monthIndex(selected)!=null&&monthIndex(last?.period)!=null?monthIndex(selected)-monthIndex(last.period):null;
  out.push(bksTruthItem(!last ? 'mute' : (last.status === 'closed'&&lag!==null&&lag>=0&&lag<=1 ? 'ok' : 'warn'),
    last ? `Last close ${esc(last.period)}${lag>1?` · ${lag-1} later month${lag>2?'s':''} not closed`:''}${last.status === 'closed' ? '' : ` with ${(last.gaps || []).length} gap${(last.gaps || []).length === 1 ? '' : 's'}`}` : closes?.unavailable?'Close history unavailable':'No month close is recorded'));
  return out.join('');
}

async function bksLoadTruth() {
  const host = document.getElementById('bks-truth');
  if (!host) return;
  const period = State.booksPeriod || bksCurrentMonth();
  host.innerHTML = bksTruthItem('mute', 'Reading the books…');
  try {
    const [h, closes] = await Promise.all([
      api(`/api/accounting/books/health?period=${encodeURIComponent(period)}`),
      api('/api/accounting/books/closes?limit=1').then(r=>r&&r.ok!==false&&Array.isArray(r.closes)?r:{unavailable:true}).catch(() => ({unavailable:true})),
    ]);
    if (!h || h.ok === false) throw new Error((h && h.error) || 'health unavailable');
    host.innerHTML = bksTruthHTML(h, closes);
  } catch (e) {
    // error ≠ empty: a truth line that cannot read says so
    host.innerHTML = bksTruthItem('bad', 'The books could not be read. Close status is unavailable.');
  }
}

function bksCloseReportHTML(r) {
  const checks = (r && r.checks) || [];
  const rows = checks.map((c) => {
    const mark = c.ok === true ? ['ok', 'ok'] : (c.ok === false ? ['gap', 'gap'] : ['unk', '?']);
    return `<div class="bks-close-row">
      <span class="bks-close-mark bks-close-${mark[0]}">${mark[1]}</span>
      <span>${esc(c.label)}</span>
      <span class="mono-num">${esc(c.figure || '')}</span>
      ${c.ok === false || c.ok == null ? `<span class="bks-close-detail">${esc(c.detail || '')}</span>` : ''}
    </div>`;
  }).join('');
  const lessons = (r && r.lessons) || [];
  const belief = r && r.belief;
  return `<div class="bks-close-head"><span>${esc(r.note || '')}</span>
      <button class="bks-tool-btn bks-tool-quiet" type="button" id="bks-close-hide">Hide</button></div>
    ${rows}
    ${belief && belief.error_pct != null ? `<p class="bks-close-detail">The projection believed ${formatNaira(belief.projected_revenue_ngn)} for this month; actual was ${formatNaira(belief.actual_revenue_ngn)} (${belief.error_pct > 0 ? '+' : ''}${belief.error_pct}%).</p>` : ''}
    <p class="bks-close-detail">${lessons.length ? `${lessons.length} lesson${lessons.length === 1 ? '' : 's'} written to memory` : 'No lesson written'}${r && r.recorded === false ? ` · ${esc(bksMonthLabel(r.period || ''))} closed, but the close could not be recorded` : ''}.</p>`;
}

// M11: what Close and Rebuild do, said on the first tap, before anything is
// sent (books.close_month / derive_journal). Cancel or waiting sends nothing;
// the second tap acts only on the month the words named.
function bksConfirmFirst(btn, period, text, idle, ask) {
  if (btn.dataset.confirming === '1' && btn.dataset.confirmPeriod === period) return false;
  const stripId = `${btn.id}-explain`;
  const reset = () => {
    clearTimeout(Number(btn.dataset.confirmTimer || 0));
    btn.dataset.confirming = ''; btn.dataset.confirmPeriod = '';
    btn.textContent = idle; btn.classList.remove('bks-confirming');
    document.getElementById(stripId)?.remove();
  };
  reset();
  btn.dataset.confirming = '1'; btn.dataset.confirmPeriod = period;
  btn.textContent = ask;
  btn.classList.add('bks-confirming');
  const bar = document.createElement('div');
  bar.id = stripId; bar.className = 'bks-locked-strip'; bar.setAttribute('role', 'status');
  bar.innerHTML = `<span>${esc(text)}</span><button class="bks-tool-btn bks-tool-quiet" type="button" data-bks-confirm-cancel>Cancel</button>`;
  (btn.closest('.bks-toolbar') || btn.parentElement)?.appendChild(bar);
  bar.querySelector('[data-bks-confirm-cancel]')?.addEventListener('click', reset);
  // Long enough to read the words above; then it cancels by itself.
  btn.dataset.confirmTimer = String(setTimeout(reset, 30000));
  return true;
}

function bksConfirmDone(btn) {
  clearTimeout(Number(btn.dataset.confirmTimer || 0));
  btn.dataset.confirming = ''; btn.dataset.confirmPeriod = '';
  btn.classList.remove('bks-confirming');
  document.getElementById(`${btn.id}-explain`)?.remove();
}

// An answer EON did not give clearly (timeout, a 5xx, no body) is not a refusal.
const bksUnclear = (e) => !(e?.status && e.status < 500 && !e.timeout);

function bksCloseMonth() {
  const period = State.booksPeriod || bksCurrentMonth();
  const btn = document.getElementById('bks-close-btn');
  if (!btn) return;
  const month = bksMonthLabel(period);
  // confirm IN PLACE (house law: no browser dialogs on money paths)
  if (bksConfirmFirst(btn, period, `Closing ${month} rebuilds its book entries from the source records, runs the month-end checks, compares the projection with what happened, and writes what it learned to memory. If an older month is missing its depreciation, it may rebuild that one month too. Closing does not lock ${month}.`, 'Close month', `Tap again to close ${month}`)) return;
  bksConfirmDone(btn);
  btn.disabled = true; btn.textContent = 'Closing…';
  const unclear = () => { showToast(`EON gave no clear answer, so ${month} may or may not have closed. Do not close again until the result is confirmed.`); bksReloadStatements(); };
  api('/api/accounting/books/close', {
    method: 'POST', body: JSON.stringify({ period }),
  }).then((r) => {
    const rep = document.getElementById('bks-close-report');
    if (r && r.ok) {
      showToast(r.recorded === false ? `${month} closed, but the close could not be recorded. Its checks and lessons are below.` : (r.note || `${month} closed`));
      if (rep) {
        rep.innerHTML = bksCloseReportHTML(r);
        rep.hidden = false;
        rep.querySelector('#bks-close-hide')?.addEventListener('click', () => { rep.hidden = true; });
      }
      bksReloadStatements();
    } else if (r && r.locked) {
      showToast(`${month}'s VAT is already filed, so the month is locked and was not closed. Closing a filed month is not available here.`);
    } else if (r && r.ok === false) {
      showToast(`Not closed: ${r.error || 'EON gave no reason.'}`);
    } else unclear();
  }).catch((e) => (bksUnclear(e) ? unclear() : showToast(`Not closed: ${e.message}`)))
    .finally(() => { btn.disabled = false; btn.textContent = 'Close month'; });
}

// ── P&L statement ────────────────────────────────────────────────

function bksLoadPnl(host) {
  const period = State.booksPeriod || bksCurrentMonth();
  return api(`/api/accounting/books/pnl?period=${encodeURIComponent(period)}`)
    .then((p) => {
      if (!p || p.ok === false) throw new Error(p?.error || 'P&L unavailable');
      const meta = document.getElementById('bks-pnl-meta');
      if (meta) meta.textContent = `${bksMonthLabel(period)} · accrual basis`;
      host.innerHTML = bksPnlStatementHTML(p);
      bksBindLedgerClicks(host);
    });
}

function bksStmtRow(label, amount, opts = {}) {
  const cls = ['bks-stmt-row'];
  if (opts.total) cls.push('bks-stmt-total');
  if (opts.grand) cls.push('bks-stmt-grand');
  if (opts.indent) cls.push('bks-stmt-indent');
  const clickable = opts.code ? ` data-bks-ledger="${esc(opts.code)}" role="button" tabindex="0"` : '';
  const tone = opts.tone ? ` style="color:var(--${opts.tone})"` : '';
  const amtHtml = opts.paren ? bksParen(amount) : bksNGN(amount);
  return `
    <div class="${cls.join(' ')}"${clickable}>
      <span class="bks-stmt-label">${esc(label)}${opts.code ? `<span class="bks-stmt-code">${esc(opts.code)}</span>` : ''}</span>
      <span class="bks-stmt-amt mono-num"${tone}>${amtHtml}${opts.suffix || ''}</span>
    </div>`;
}

function bksPnlStatementHTML(p) {
  const rev = p.revenue || {};
  const revLines = (rev.lines || []).map((l) =>
    bksStmtRow(l.name, l.amount_ngn, { indent: true, code: l.code })).join('');
  const byLoc = (rev.by_location || []).map((l) => `
    <div class="bks-stmt-row bks-stmt-indent bks-stmt-soft">
      <span class="bks-stmt-label">${esc(l.name || '(unattributed)')}</span>
      <span class="bks-stmt-amt mono-num">${bksNGN(l.revenue_ngn)}</span>
    </div>`).join('');
  const otherIncome = Number(p.other_income_ngn || 0);
  const otherLines = (p.other_income_lines || []).map((l) =>
    bksStmtRow(l.name, l.amount_ngn, { indent: true, code: l.code })).join('');
  const opexLines = (p.opex_lines || []).map((l) =>
    bksStmtRow(l.name, l.amount_ngn, { indent: true, code: l.code, paren: true })).join('');
  const belowLines = (p.below_ebitda_lines || []).map((l) =>
    bksStmtRow(l.name, l.amount_ngn, { indent: true, code: l.code, paren: true })).join('');
  const marginPct = p.gross_margin_pct;
  const net = Number(p.net_profit_ngn || 0);

  return `
    <div class="bks-stmt">
      <div class="bks-stmt-section">Revenue</div>
      ${revLines || bksStmtRow('No revenue posted', 0, { indent: true })}
      ${(rev.by_location || []).length ? `
        <details class="bks-stmt-details">
          <summary>By location</summary>
          ${byLoc}
        </details>` : ''}
      ${bksStmtRow('Refunds', rev.refunds_ngn, { indent: true, paren: true })}
      ${bksStmtRow('Net revenue', rev.net_ngn, { total: true })}

      ${bksStmtRow('Cost of goods sold', p.cogs_ngn, { paren: true })}
      ${bksStmtRow('Gross profit', p.gross_profit_ngn, { total: true,
        suffix: (marginPct == null ? '' : ` <span class="bks-margin-chip">${esc(String(marginPct))}% margin</span>`) })}

      ${otherIncome ? `
        <div class="bks-stmt-section">Other income</div>
        ${otherLines}` : ''}

      <div class="bks-stmt-section">Operating expenses</div>
      <details class="bks-stmt-details" ${((p.opex_lines || []).length <= 8) ? 'open' : ''}>
        <summary>${(p.opex_lines || []).length} expense lines</summary>
        ${opexLines}
      </details>
      ${bksStmtRow('Total operating expenses', p.opex_ngn, { paren: true })}

      ${bksStmtRow('EBITDA', p.ebitda_ngn, { total: true })}
      ${(p.below_ebitda_lines || []).length ? `
        <div class="bks-stmt-section">After that: loans + tax</div>
        ${belowLines}` : ''}
      ${bksStmtRow(net >= 0 ? 'Net profit' : 'Net loss', p.net_profit_ngn,
        { grand: true, tone: net >= 0 ? 'ok' : 'warn' })}
    </div>`;
}

// ── Trial balance ────────────────────────────────────────────────

const BKS_TYPE_ORDER = ['asset', 'liability', 'equity', 'income',
  'contra_income', 'cogs', 'expense'];
const BKS_TYPE_LABEL = {
  asset: 'Assets', liability: 'Liabilities', equity: 'Equity',
  income: 'Income', contra_income: 'Contra income',
  cogs: 'Cost of goods sold', expense: 'Expenses',
};

function bksLoadTrialBalance(host) {
  const period = State.booksPeriod || bksCurrentMonth();
  return api(`/api/accounting/books/trial-balance?period=${encodeURIComponent(period)}`)
    .then((tb) => {
      if (!tb || tb.ok === false) throw new Error(tb?.error || "Couldn't load the books right now — try again.");
      const meta = document.getElementById('bks-tb-meta');
      if (meta) meta.textContent = `${(tb.accounts || []).length} accounts`;
      host.innerHTML = bksTbTableHTML(tb);
      bksBindLedgerClicks(host);
    });
}

function bksTbTableHTML(tb) {
  const accounts = tb.accounts || [];
  const groups = {};
  accounts.forEach((a) => {
    (groups[a.type] = groups[a.type] || []).push(a);
  });
  const balanced = !!tb.balanced;
  const diff = Number(tb.total_debits || 0) - Number(tb.total_credits || 0);
  const badge = balanced
    ? `<span class="bks-chip bks-chip-mint">BALANCED</span>`
    : `<span class="bks-chip bks-chip-amber">OUT OF BALANCE ${bksNGN(diff)}</span>`;

  const groupRows = BKS_TYPE_ORDER.filter((t) => groups[t]).map((t) => {
    const rows = groups[t];
    const sub = rows.reduce((acc, a) => {
      acc.d += Number(a.debits || 0); acc.c += Number(a.credits || 0);
      return acc;
    }, { d: 0, c: 0 });
    return `
      <tr class="bks-tb-group"><td colspan="6">${esc(BKS_TYPE_LABEL[t] || t)}</td></tr>
      ${rows.map((a) => `
        <tr class="bks-tb-row" data-bks-ledger="${esc(a.code)}" tabindex="0" role="button" aria-label="Open ${esc(a.name)} account entries">
          <td class="mono-num">${esc(a.code)}</td>
          <td>${esc(a.name)}</td>
          <td class="mono-num bks-num" data-label="Opening">${bksNGN(a.opening)}</td>
          <td class="mono-num bks-num" data-label="Debits">${bksNGN(a.debits)}</td>
          <td class="mono-num bks-num" data-label="Credits">${bksNGN(a.credits)}</td>
          <td class="mono-num bks-num" data-label="Closing">${bksNGN(a.closing)}</td>
        </tr>`).join('')}
      <tr class="bks-tb-subtotal">
        <td></td><td>${esc(BKS_TYPE_LABEL[t] || t)} subtotal</td>
        <td></td>
        <td class="mono-num bks-num" data-label="Debits">${bksNGN(sub.d)}</td>
        <td class="mono-num bks-num" data-label="Credits">${bksNGN(sub.c)}</td>
        <td></td>
      </tr>`;
  }).join('');

  return `
    <div class="bks-tb-head">${badge}</div>
    <div class="bks-scroll">
      <table class="bks-table mw-trial-balance">
        <thead>
          <tr><th>Code</th><th>Account</th><th class="bks-num">Opening</th>
              <th class="bks-num">Debits</th><th class="bks-num">Credits</th>
              <th class="bks-num">Closing</th></tr>
        </thead>
        <tbody>
          ${groupRows || '<tr><td colspan="6">No journal activity in this period.</td></tr>'}
          <tr class="bks-tb-grand">
            <td></td><td>Totals</td>
            <td class="mono-num bks-num" data-label="Opening">${bksNGN(tb.total_opening)}</td>
            <td class="mono-num bks-num" data-label="Debits">${bksNGN(tb.total_debits)}</td>
            <td class="mono-num bks-num" data-label="Credits">${bksNGN(tb.total_credits)}</td>
            <td class="mono-num bks-num" data-label="Closing">${bksNGN(tb.total_closing)}</td>
          </tr>
        </tbody>
      </table>
    </div>`;
}

// ── Balance sheet ────────────────────────────────────────────────

function bksMonthEndAsOf(period) {
  const y = parseInt(period.slice(0, 4), 10);
  const m = parseInt(period.slice(5, 7), 10);
  const last = new Date(y, m, 0);                       // last day of month
  const today = new Date();
  const d = last > today ? today : last;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function bksLoadBalanceSheet(host) {
  const period = State.booksPeriod || bksCurrentMonth();
  const asOf = bksMonthEndAsOf(period);
  return api(`/api/accounting/books/balance-sheet?as_of=${encodeURIComponent(asOf)}`)
    .then((bs) => {
      if (!bs || bs.ok === false) throw new Error(bs?.error || 'balance sheet unavailable');
      const meta = document.getElementById('bks-bs-meta');
      if (meta) meta.textContent = `as of ${bs.as_of || asOf}`;
      host.innerHTML = bksBsHTML(bs);
      bksBindLedgerClicks(host);
    });
}

function bksBsHTML(bs) {
  const assets = bs.assets || {};
  const liab = bs.liabilities || {};
  const eq = bs.equity || {};
  const eqOk = !!bs.equation_ok;
  const chip = eqOk
    ? `<span class="bks-chip bks-chip-mint">A = L + E</span>`
    : `<span class="bks-chip bks-chip-amber">A − (L + E) = ${bksNGN(bs.equation_diff_ngn)}</span>`;
  const lines = (arr) => (arr || []).map((l) => `
    <div class="bks-stmt-row bks-stmt-indent" data-bks-ledger="${esc(l.code)}" role="button" tabindex="0">
      <span class="bks-stmt-label">${esc(l.name)}<span class="bks-stmt-code">${esc(l.code)}</span></span>
      <span class="bks-stmt-amt mono-num">${bksNGN(l.balance_ngn)}</span>
    </div>`).join('');
  const notes = (bs.notes || []).map((n) =>
    `<div class="bks-footnote">${esc(n)}</div>`).join('');

  return `
    <div class="bks-stmt">
      <div class="bks-bs-eq">${chip}</div>
      <div class="bks-stmt-section">Assets</div>
      ${lines(assets.lines)}
      ${bksStmtRow('Total assets', assets.total_ngn, { total: true })}

      <div class="bks-stmt-section">Liabilities</div>
      ${lines(liab.lines) || bksStmtRow('No liabilities posted', 0, { indent: true })}
      ${bksStmtRow('Total liabilities', liab.total_ngn, { total: true })}

      <div class="bks-stmt-section">Equity</div>
      ${lines(eq.posted_lines)}
      ${bksStmtRow('Retained earnings (prior years)', eq.retained_earnings_prior_ngn, { indent: true })}
      ${bksStmtRow('Current year net', eq.current_year_net_ngn, { indent: true })}
      ${bksStmtRow('Total equity', eq.total_ngn, { total: true })}
      ${notes}
    </div>`;
}

// ── General-ledger slide-over ────────────────────────────────────

function bksLedgerRowsHTML(gl, query='') {
  const lines=Array.isArray(gl.lines)?gl.lines:[];
  const term=query.trim().toLocaleLowerCase();
  const selected=lines.filter(l=>[l.entry_date,l.memo,l.source,l.source_reference].some(v=>String(v||'').toLocaleLowerCase().includes(term)));
  const rows=selected.map(l=>`<tr>
    <td data-label="Date">${esc(String(l.entry_date||'').slice(0,10))}</td>
    <td data-label="Entry">${esc(l.memo||'No description')}<small>${esc(String(l.source||'').replace(/_/g,' '))}</small>${l.source_reference?`<small>${esc(l.source_reference)}</small><button class="bks-copy-btn" type="button" data-bks-copy="${esc(l.source_reference)}">Copy reference</button>`:''}</td>
    <td data-label="Debit" class="bks-num">${bksNGN(l.debit)}</td>
    <td data-label="Credit" class="bks-num">${bksNGN(l.credit)}</td>
    <td data-label="Recorded balance" class="bks-num">${bksNGN(l.running_balance)}</td>
  </tr>`).join('');
  return `<p class="bks-gl-open">Full-period opening <strong>${bksNGN(gl.opening_balance)}</strong></p>
    <p role="status">${selected.length} of ${lines.length} returned lines. Recorded balances keep their original order and do not change with search.</p>
    <div class="bks-scroll"><table class="bks-table mw-ledger-detail"><thead><tr><th>Date</th><th>Entry and source</th><th>Debit</th><th>Credit</th><th>Recorded balance</th></tr></thead><tbody>${rows||'<tr><td colspan="5">No returned lines match this search.</td></tr>'}</tbody></table></div>
    <p class="bks-gl-close">Full-period closing <strong>${bksNGN(gl.closing_balance)}</strong></p>
    ${gl.truncated?'<p class="bks-footnote">This is a limited set of the period’s entries. Search covers only these returned lines; choose another month or use the export for a complete check.</p>':''}`;
}

function bksOpenLedger(code) {
  if (!code) return;
  const opener=document.activeElement;
  document.getElementById('bks-ledger-pane')?.remove();
  document.getElementById('bks-ledger-backdrop')?.remove();
  const period = State.booksPeriod || bksCurrentMonth();
  const backdrop = document.createElement('div');
  backdrop.id = 'bks-ledger-backdrop';
  backdrop.className = 'bks-ledger-backdrop';
  const pane = document.createElement('div');
  pane.id = 'bks-ledger-pane';
  pane.className = 'bks-ledger-pane mw-ledger-pane';
  pane.setAttribute('role','dialog');pane.setAttribute('aria-modal','true');pane.setAttribute('aria-label','Account entries');
  pane.innerHTML = `
    <div class="bks-ledger-head">
      <div>
        <div class="bks-ledger-title">Ledger · <span class="mono-num">${esc(code)}</span></div>
        <div class="bks-ledger-sub" id="bks-ledger-sub">${esc(bksMonthLabel(period))}</div>
      </div>
      <button class="bks-ledger-close" id="bks-ledger-close" aria-label="Close">&times;</button>
    </div>
    <div class="bks-ledger-body" id="bks-ledger-body">${bksSkel(6)}</div>`;
  document.body.appendChild(backdrop);
  document.body.appendChild(pane);
  requestAnimationFrame(() => pane.classList.add('bks-ledger-in'));
  const close=()=>{document.removeEventListener('keydown',onKey);pane.remove();backdrop.remove();if(opener?.isConnected)opener.focus();};
  const onKey=e=>{
    if(e.key==='Escape'){e.preventDefault();close();return;}
    if(e.key!=='Tab')return;
    const items=[...pane.querySelectorAll('button,input,[tabindex="0"]')].filter(el=>!el.disabled&&el.getClientRects().length);
    const first=items[0],last=items[items.length-1];
    if(e.shiftKey&&(document.activeElement===first||!pane.contains(document.activeElement))){e.preventDefault();last?.focus();}
    else if(!e.shiftKey&&(document.activeElement===last||!pane.contains(document.activeElement))){e.preventDefault();first?.focus();}
  };
  document.addEventListener('keydown',onKey);
  pane.querySelector('#bks-ledger-close').focus();
  eonBackdropClose(backdrop, close);  // ghost-click law (2026-07-25)
  pane.querySelector('#bks-ledger-close').addEventListener('click', close);

  api(`/api/accounting/books/ledger/${encodeURIComponent(code)}?period=${encodeURIComponent(period)}&limit=500`)
    .then((gl) => {
      const body = pane.querySelector('#bks-ledger-body');
      if (!body) return;
      if (!gl || gl.ok === false) {
        body.innerHTML = `<div class="bks-soft-err">${gl?.error ? `couldn't load — ${esc(gl.error)}` : "Couldn't load the books right now — try again."}</div>`;
        return;
      }
      const sub = pane.querySelector('#bks-ledger-sub');
      if (sub) sub.textContent =
        `${gl.account_name || ''} · ${bksMonthLabel(period)} · ${gl.line_count} lines`;
      if(!Array.isArray(gl.lines))throw new Error('Account entries unavailable');
      body.innerHTML='<label class="mw-ledger-search">Search these account entries<input type="search" aria-label="Search account entries" placeholder="Description, source or reference"></label><div data-bks-ledger-results></div>';
      const paint=()=>{body.querySelector('[data-bks-ledger-results]').innerHTML=bksLedgerRowsHTML(gl,body.querySelector('input').value);};
      paint();body.querySelector('input').addEventListener('input',paint);
      body.addEventListener('click',e=>{
        const btn=e.target.closest('[data-bks-copy]');if(!btn)return;
        if(navigator.clipboard?.writeText)navigator.clipboard.writeText(btn.dataset.bksCopy).then(()=>showToast('Source reference copied'),()=>showToast('Copy failed'));
        else showToast('Copy unavailable in this browser');
      });
    })
    .catch((e) => {
      const body = pane.querySelector('#bks-ledger-body');
      if (body) body.innerHTML =
        `<div class="bks-soft-err">The account entries could not be read. Close this view and try again.</div>`;
    });
}

// ── Audit pack (print-friendly) ──────────────────────────────────

function bksOpenAuditPack() {
  const period = State.booksPeriod || bksCurrentMonth();
  document.getElementById('bks-audit-overlay')?.remove();
  const overlay = document.createElement('div');
  overlay.id = 'bks-audit-overlay';
  overlay.className = 'bks-audit-overlay';
  overlay.innerHTML = `
    <div class="bks-pack">
      <div class="bks-pack-actions">
        <button class="bks-tool-btn" id="bks-pack-print" type="button">Print</button>
        <button class="bks-tool-btn bks-tool-quiet" id="bks-pack-close" type="button">Close</button>
      </div>
      <div id="bks-pack-body">${bksSkel(8)}</div>
    </div>`;
  document.body.appendChild(overlay);
  document.body.classList.add('bks-print-mode');
  const close = () => {
    overlay.remove();
    document.body.classList.remove('bks-print-mode');
  };
  overlay.querySelector('#bks-pack-close').addEventListener('click', close);
  mtxEscClose(overlay, close);
  overlay.querySelector('#bks-pack-print').addEventListener('click', () => window.print());

  api(`/api/accounting/books/audit-pack?period=${encodeURIComponent(period)}`)
    .then((pack) => {
      const body = overlay.querySelector('#bks-pack-body');
      if (!body) return;
      if (!pack || pack.ok === false) {
        body.innerHTML = `<div class="bks-soft-err">couldn't load — ${esc(pack?.error || 'audit pack unavailable')}</div>`;
        return;
      }
      body.innerHTML = bksAuditPackHTML(pack, period);
      bksBindLedgerClicks(body);
    })
    .catch((e) => {
      const body = overlay.querySelector('#bks-pack-body');
      if (body) body.innerHTML =
        `<div class="bks-soft-err">couldn't load — ${esc(e?.message || '')}</div>`;
    });
}

function bksAuditPackHTML(pack, period) {
  const ws = State.tenant?.name || 'Workspace';
  const cov = pack.coverage || {};
  const integrity = pack.integrity_ok
    ? `<span class="bks-chip bks-chip-mint">INTEGRITY OK — every entry balances</span>`
    : `<span class="bks-chip bks-chip-amber">INTEGRITY CHECK FAILED — ${(pack.unbalanced_entries || []).length} unbalanced entries</span>`;
  const srcRows = Object.entries(pack.entries_by_source || {}).map(([s, v]) => `
    <tr><td>${esc(s)}</td>
        <td class="mono-num bks-num">${Number(v.entries || 0).toLocaleString('en-US')}</td>
        <td class="mono-num bks-num">${bksNGN(v.total_debits_ngn)}</td></tr>`).join('');
  const covRow = (label, jrn, src, pct) => `
    <tr><td>${esc(label)}</td>
        <td class="mono-num bks-num">${Number(jrn || 0).toLocaleString('en-US')} / ${Number(src || 0).toLocaleString('en-US')}</td>
        <td class="mono-num bks-num">${pct == null ? '—' : pct + '%'}</td></tr>`;
  const syncRows = (pack.sync_state || []).map((s) => `
    <tr><td>${esc(s.source || '')}</td>
        <td class="mono-num">${esc(String(s.last_synced_through || '').slice(0, 10))}</td></tr>`).join('');

  return `
    <div class="bks-pack-head">
      <div class="bks-pack-brand">${esc(ws)} — Audit pack</div>
      <div class="bks-pack-period">${esc(bksMonthLabel(period))} · ${esc(pack.start || '')} to ${esc(pack.end || '')} · generated ${new Date().toLocaleDateString('en-US')}</div>
      <div class="bks-pack-integrity">${integrity}</div>
    </div>

    <div class="bks-pack-card">
      <div class="bks-pack-h">Derivation coverage</div>
      <table class="bks-table">
        <thead><tr><th>Source</th><th class="bks-num">Journalized / in window</th><th class="bks-num">Coverage</th></tr></thead>
        <tbody>
          ${covRow('Shopify orders', cov.shopify_orders_journalized, cov.shopify_orders_in_window, cov.shopify_orders_pct)}
          ${covRow('Refunds', cov.refunds_journalized, cov.refunds_in_window, cov.refunds_pct)}
          ${covRow('Transactions', cov.transactions_journalized, cov.transactions_in_window, cov.transactions_pct)}
        </tbody>
      </table>
    </div>

    <div class="bks-pack-card">
      <div class="bks-pack-h">Entries by source</div>
      <table class="bks-table">
        <thead><tr><th>Source</th><th class="bks-num">Entries</th><th class="bks-num">Total debits</th></tr></thead>
        <tbody>${srcRows || '<tr><td colspan="3">No entries.</td></tr>'}</tbody>
      </table>
    </div>

    <div class="bks-pack-card">
      <div class="bks-pack-h">Profit and loss — accrual</div>
      ${pack.profit_and_loss && pack.profit_and_loss.ok !== false
        ? bksPnlStatementHTML(pack.profit_and_loss)
        : '<div class="bks-soft-err">P&amp;L unavailable in this pack.</div>'}
    </div>

    <div class="bks-pack-card">
      <div class="bks-pack-h">Trial balance</div>
      ${pack.trial_balance && pack.trial_balance.ok !== false
        ? bksTbTableHTML(pack.trial_balance)
        : '<div class="bks-soft-err">Trial balance unavailable in this pack.</div>'}
    </div>

    <div class="bks-pack-card">
      <div class="bks-pack-h">Balance sheet</div>
      ${pack.balance_sheet && pack.balance_sheet.ok !== false
        ? bksBsHTML(pack.balance_sheet)
        : '<div class="bks-soft-err">Balance sheet unavailable in this pack.</div>'}
    </div>

    <div class="bks-pack-card">
      <div class="bks-pack-h">Sync state</div>
      <table class="bks-table">
        <thead><tr><th>Source</th><th>Synced through</th></tr></thead>
        <tbody>${syncRows || '<tr><td colspan="2">No sync watermarks.</td></tr>'}</tbody>
      </table>
    </div>
    <div class="bks-footnote">Derived double-entry journal. Every figure traces to a Shopify order/refund, a categorized transaction, or an approved payroll run via its source reference.</div>
  `;
}

// ── Rebuild period (owner action) ────────────────────────────────

function bksDerivePeriod(force) {
  // Vet 2026-07-27: the last two native window.confirm dialogs on Money
  // retired — confirm IN PLACE on the button (house law: sheets and
  // in-place confirms, never browser dialogs on money paths).
  const period = State.booksPeriod || bksCurrentMonth();
  const btn = document.getElementById('bks-derive-btn');
  const month = bksMonthLabel(period);
  document.getElementById('bks-derive-locked')?.remove();
  if (!force && btn && bksConfirmFirst(btn, period, `Rebuilding ${month} recomputes its book entries from the source records. It does not bring back an earlier version.`, 'Rebuild period', `Tap again to rebuild ${month}`)) return;
  if (btn) {
    bksConfirmDone(btn);
    btn.disabled = true; btn.textContent = 'Rebuilding…';
  }
  api('/api/accounting/books/derive', {
    method: 'POST',
    body: JSON.stringify(force ? { period, force: true } : { period }),
  }).then((r) => {
    if (r && r.ok !== false) {
      showToast(`Rebuilt ${bksMonthLabel(period)} — ${Number(r.entries_posted ?? r.entries ?? 0).toLocaleString('en-US')} entries posted${force ? ' (locked period override)' : ''}`);
      bksReloadStatements();
    } else if (r?.locked && !force) {
      // Wave-2 filed-period lock: this month's VAT is already with the
      // tax office. Overriding is an explicit second decision — an
      // inline strip, not a browser dialog.
      const bar = document.createElement('div');
      bar.id = 'bks-derive-locked';
      bar.className = 'bks-locked-strip';
      bar.innerHTML = `
        <span>${esc(r.error || 'Period is filed-locked.')} The filed figures stay frozen in the filing snapshot; drift will alarm on the Tax tab.</span>
        <button class="bks-tool-btn" type="button" id="bks-derive-force">Rebuild anyway</button>
        <button class="bks-tool-btn bks-tool-quiet" type="button" id="bks-derive-abort">Cancel</button>`;
      (btn?.closest('.bks-toolbar') || btn?.parentElement)?.appendChild(bar);
      bar.querySelector('#bks-derive-force')?.addEventListener('click',
        () => bksDerivePeriod(true));
      bar.querySelector('#bks-derive-abort')?.addEventListener('click',
        () => bar.remove());
    } else if (r?.ok === false) {
      showToast(`Not rebuilt: ${r.error || 'EON gave no reason.'}`);
    } else {
      showToast(`EON gave no clear answer, so ${month} may or may not have been rebuilt. Check the statements before rebuilding again.`); bksReloadStatements();
    }
  }).catch((e) => {
    if (bksUnclear(e)) { showToast(`EON gave no clear answer, so ${month} may or may not have been rebuilt. Check the statements before rebuilding again.`); bksReloadStatements(); }
    else showToast(`Not rebuilt: ${e?.message || e}`);
  }).finally(() => {
    if (btn) { btn.disabled = false; btn.textContent = 'Rebuild period'; }
  });
}

// ══════════════════════════════════════════════════════════════════
// TAX TAB (v3.0p106)
// Money → Tax: the Tax OS cockpit on /api/tax/* (routes_tax.py thin
// over app/modules/tax_engine.py, migration 266). Filing calendar
// with status progression, VAT / PAYE / WHT month packs, CIT-vs-PIT
// provision + minimization sweep by year, the capital-asset register
// and the consultant variance check. Same construction rules as the
// Books tab above: lazy by construction (nothing fetches until the
// tab opens), fail-soft per card via bksLoadCard, mono figures,
// no emoji glyphs in UI strings.
// ══════════════════════════════════════════════════════════════════

const MTX_TYPE_LABEL = {
  vat: 'VAT', paye: 'PAYE', wht: 'WHT',
  cit: 'CIT', pit: 'PIT', dev_levy: 'Dev levy',
};

// Operator-driven status ladder. upcoming/overdue advance to prepared;
// filed and paid each prompt inline for their artifact (acknowledgment
// URL / payment reference) before the POST.
const MTX_NEXT_STATUS = {
  upcoming: 'prepared', overdue: 'prepared',
  prepared: 'filed', filed: 'paid',
};

function mtxCurrentYear() { return new Date().getFullYear(); }

// Percent formatter for fractional rates (0.075 -> "7.5%").
function mtxPct(rate) {
  const v = Number(rate) * 100;
  if (!Number.isFinite(v)) return '—';
  return `${(Math.round(v * 10) / 10).toLocaleString('en-US')}%`;
}

function renderTaxTab() {
  const period = State.taxPeriod || (State.taxPeriod = bksCurrentMonth());
  const year = State.taxYear || (State.taxYear = mtxCurrentYear());
  return `
    <div class="section-h money-section-h">
      <h2>Tax records.</h2>
      <span class="meta">Calculations · filing records · payments</span>
    </div>
    ${renderMoneyTabBar('tax')}

    <section class="mw-tax-basis" id="mtx-basis" aria-label="Tax calculation basis">${bksSkel(2)}</section>

    <div class="section-h"><h2>Filing calendar</h2><span class="meta" id="mtx-cal-meta"></span></div>
    <div id="mtx-calendar" class="mtx-cal-strip">${bksSkel(3)}</div>

    <div id="mtx-lawwatch"></div>

    <div class="bks-toolbar">
      <div class="bks-month-step" id="mtx-month-step">
        <button class="bks-step-btn" data-mtx-step="-1" aria-label="Previous month">&lsaquo;</button>
        <span class="bks-month-label" id="mtx-month-label">${esc(bksMonthLabel(period))}</span>
        <button class="bks-step-btn" data-mtx-step="1" aria-label="Next month">&rsaquo;</button>
      </div>
      <span class="mtx-quiet">Monthly packs — VAT, PAYE, WHT</span>
    </div>

    <div class="section-h"><h2>VAT</h2><span class="meta" id="mtx-vat-meta"></span></div>
    <div id="mtx-vat" class="card bks-card">${bksSkel(5)}</div>

    <div class="section-h"><h2>PAYE</h2><span class="meta" id="mtx-paye-meta"></span></div>
    <div id="mtx-paye" class="card bks-card">${bksSkel(4)}</div>

    <div class="section-h"><h2>WHT</h2><span class="meta" id="mtx-wht-meta"></span></div>
    <div id="mtx-wht" class="card bks-card">${bksSkel(4)}</div>

    <div class="bks-toolbar">
      <div class="bks-month-step" id="mtx-year-step">
        <button class="bks-step-btn" data-mtx-ystep="-1" aria-label="Previous year">&lsaquo;</button>
        <span class="bks-month-label" id="mtx-year-label">${esc(String(year))}</span>
        <button class="bks-step-btn" data-mtx-ystep="1" aria-label="Next year">&rsaquo;</button>
      </div>
      <span class="mtx-quiet">Annual — provision + minimization</span>
    </div>

    <div class="section-h"><h2>CIT / PIT provision</h2><span class="meta" id="mtx-cit-meta"></span></div>
    <div id="mtx-cit" class="card bks-card">${bksSkel(5)}</div>

    <div class="section-h"><h2>Minimization</h2><span class="meta" id="mtx-min-meta"></span></div>
    <div id="mtx-min" class="card bks-card">${bksSkel(4)}</div>

    <div class="section-h"><h2>Capital assets</h2><span class="meta" id="mtx-assets-meta"></span></div>
    <div id="mtx-assets" class="card bks-card">${bksSkel(3)}</div>

    <div class="section-h"><h2>Variance check</h2><span class="meta">quote vs EON</span></div>
    <div id="mtx-variance" class="card bks-card"></div>

    <div class="bks-foot-note">Every figure here is computed from declared records — full revenue, all locations. Legal minimization only; nothing on this page reduces what is declared.</div>
  `;
}

// EON learns tax law weekly (mig 529): Monday sweep -> this strip.
// Absent until the first sweep lands; failure = absent, never fake.
async function mtxLoadLawWatch() {
  const host = document.getElementById('mtx-lawwatch');
  if (!host) return;
  let d = null;
  const unavailable=()=>{host.innerHTML='<p class="mtx-watch-note" role="status">Tax watch did not load. This does not establish that the law is unchanged.</p>';};
  try { d = await api('/api/tax/intel/latest'); } catch (_) { unavailable(); return; }
  if (!d || !d.ok) { unavailable(); return; }
  const items = Array.isArray(d.items) ? d.items : [];
  host.innerHTML = `
    <div class="mtx-lawwatch-card">
      <div class="mtx-lawwatch-head">
        <span>Tax watch notes · ${esc(String(d.week || 'Date not recorded'))}</span>
        <span class="meta">Research notes · legal verification not recorded</span>
      </div>
      ${d.summary ? `<div class="mtx-lawwatch-sum">${esc(d.summary)}</div>` : ''}
      ${items.length ? items.slice(0, 4).map((it) => `
        <div class="mtx-lawwatch-item">
          <span class="bks-chip ${it.urgency === 'now' ? 'bks-chip-red' : (it.urgency === 'this_month' ? 'bks-chip-amber' : '')}">${esc(String(it.urgency || 'watch'))}</span>
          <span class="mtx-lawwatch-line">${esc(String(it.headline || ''))} — ${esc(String(it.action_for_crux || ''))}
            ${/^https?:\/\//i.test(String(it.source_url || '')) ? `<a href="${esc(it.source_url)}" target="_blank" rel="noopener">source</a>` : ''}</span>
        </div>`).join('')
      : `<div class="mtx-lawwatch-sum">No watch notes were returned. That is not proof that the law is unchanged.</div>`}
      <p class="mtx-watch-note">A linked article is a lead to check with your consultant. It does not update a tax rate, file a return or prove a legal change.</p>
    </div>`;
}

function mtxBasisHTML(d) {
  const reminder=d.reminders||{};
  const title=!reminder.known?'Reminder state is unknown':reminder.parked?'Tax reminders are paused':'Tax reminders are on';
  const note=!reminder.known?'The reminder setting did not load. No setting was changed.':reminder.parked
    ? `Paused${reminder.since?' since '+String(reminder.since).slice(0,10):''}${reminder.note?' · '+reminder.note:''}. Calculations continue. This pause applies to reminders; it does not file or pay tax.`
    : 'Opening this page reads the records. Filing and payment remain separate actions.';
  const labels={vat:'VAT',paye:'PAYE',wht:'WHT',cit:'Company income tax',fiscal:'Business and filing rules',pit_bands:'Income tax bands',pit_reliefs:'Income tax reliefs',employer_contrib:'Employer contributions',wht_rates:'Withholding rates',capital_allowances:'Capital allowances'};
  const source={workspace_setting:'Your saved setting',shared_setting:'Shared saved setting',built_in_rule:'Rule built into EON'};
  return `<header><span class="mw-eyebrow">Records before decisions</span><h2>${esc(title)}</h2><p>${esc(note)}</p></header><details><summary>How the ${esc(d.period||'selected month')} calculations choose their rules</summary><p>These are the sources chosen for this month. A saved setting does not establish its country or confirm current law. Annual packs use their selected year. Ask your consultant to check the rules, figures and supporting records before filing.</p><table class="mw-rule-table"><thead><tr><th scope="col">Calculation</th><th scope="col">Rule source</th><th scope="col">Country</th><th scope="col">Applies from</th></tr></thead><tbody>${(d.settings||[]).map(s=>`<tr><th scope="row">${esc(labels[s.key]||s.key)}</th><td data-label="Rule source">${s.read_ok?esc(source[s.source]||'Unknown source'):'Settings unreadable · built-in fallback'}<details><summary>Trace this rule</summary><p>${s.source_reference?.kind==='code'?'Default written into EON. This is not a tax-authority verification.':s.source_reference?.kind==='saved_parameter'?'Saved rule, selected by its scope, name and effective date. Country has not been recorded on this setting.':'Source reference was not returned.'}</p>${s.source_reference?`<small>${esc(s.source_reference.location||'Location not recorded')} · ${esc(s.source_reference.key||s.key)} · ${esc(s.source_reference.scope||'Scope not recorded')}</small>`:''}</details></td><td data-label="Country">${s.jurisdiction==='NG'&&s.jurisdiction_basis==='coded_default'?'Nigeria · coded default':'Not recorded · needs confirmation'}</td><td data-label="Applies from">${esc(s.effective_from||'Date unavailable')}</td></tr>`).join('')}</tbody></table></details>`;
}

function mtxLoadBasis(host) {
  const period=State.taxPeriod||bksCurrentMonth();
  return api(`/api/tax/basis/${encodeURIComponent(period)}`).then(d=>{
    if(!d||d.ok!==true) throw new Error('Tax settings did not load');
    if(period===(State.taxPeriod||bksCurrentMonth())) host.innerHTML=mtxBasisHTML(d);
  });
}

function bindTaxTab() {
  mtxLoadLawWatch();
  document.querySelectorAll('#mtx-month-step .bks-step-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const cur = State.taxPeriod || bksCurrentMonth();
      const next = bksStepMonth(cur, parseInt(btn.dataset.mtxStep, 10));
      if (next === cur) return;
      State.taxPeriod = next;
      const lbl = document.getElementById('mtx-month-label');
      if (lbl) lbl.textContent = bksMonthLabel(next);
      mtxReloadMonthCards();
    });
  });
  document.querySelectorAll('#mtx-year-step .bks-step-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const cur = State.taxYear || mtxCurrentYear();
      const next = Math.min(cur + parseInt(btn.dataset.mtxYstep, 10),
        mtxCurrentYear());
      if (next === cur) return;
      State.taxYear = next;
      const lbl = document.getElementById('mtx-year-label');
      if (lbl) lbl.textContent = String(next);
      mtxReloadYearCards();
    });
  });
  bksLoadCard('mtx-calendar', mtxLoadCalendar);
  mtxReloadMonthCards();
  mtxReloadYearCards();
  moneyRead('mtx-assets',()=>bksLoadCard('mtx-assets', mtxLoadAssets),true);
  mtxRenderVariance();
}

function mtxReloadMonthCards() {
  bksLoadCard('mtx-basis', mtxLoadBasis);
  ['mtx-vat', 'mtx-paye', 'mtx-wht'].forEach((id) => {
    const host = document.getElementById(id);
    if (host) host.innerHTML = bksSkel(4);
  });
  moneyRead('mtx-vat',()=>bksLoadCard('mtx-vat', mtxLoadVat),true);
  moneyRead('mtx-paye',()=>bksLoadCard('mtx-paye', mtxLoadPaye),true);
  moneyRead('mtx-wht',()=>bksLoadCard('mtx-wht', mtxLoadWht),true);
}

function mtxReloadYearCards() {
  ['mtx-cit', 'mtx-min'].forEach((id) => {
    const host = document.getElementById(id);
    if (host) host.innerHTML = bksSkel(4);
  });
  moneyRead('mtx-cit',()=>bksLoadCard('mtx-cit', mtxLoadCit),true);
  moneyRead('mtx-min',()=>bksLoadCard('mtx-min', mtxLoadMin),true);
}

// ── Filing calendar strip ────────────────────────────────────────

function mtxDaysLeft(dueIso) {
  const due = new Date(`${String(dueIso).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due - today) / 86400000);
}

function mtxDueChip(f) {
  if (f.status === 'paid') {
    const supported=mtxFilingEvidence(f);
    return `<span class="bks-chip ${supported.complete?'bks-chip-mint':'bks-chip-amber'}">${supported.complete?'PAYMENT RECORDED':'MARKED PAID · evidence missing'}</span>`;
  }
  // Vet 2026-07-27: a return FILED on time rendered a red OVERDUE chip
  // (only 'paid' short-circuited). Filed = filed; payment is its own state.
  if (f.status === 'filed') {
    const late=mtxDaysLeft(f.due_date);
    return `<span class="bks-chip ${late!==null&&late<0?'bks-chip-red':'bks-chip-amber'}">${mtxFilingEvidence(f).complete?'FILING RECORDED':'MARKED FILED · evidence missing'} · ${late!==null&&late<0?'payment overdue '+Math.abs(late)+' days':'payment open'}</span>`;
  }
  if (f.status === 'prepared') {
    const late=mtxDaysLeft(f.due_date);
    return `<span class="bks-chip ${late!==null&&late<0?'bks-chip-red':'bks-chip-amber'}">PREPARATION MARKED${late!==null&&late<0?' · overdue '+Math.abs(late)+' days':''}</span>`;
  }
  const days = mtxDaysLeft(f.due_date);
  if (days == null) return '';
  if (f.status === 'overdue' || days < 0) {
    return `<span class="bks-chip bks-chip-red">OVERDUE ${Math.abs(days)}d</span>`;
  }
  if (days <= 7) {
    return `<span class="bks-chip bks-chip-amber">DUE IN ${days}d</span>`;
  }
  return `<span class="bks-chip">DUE IN ${days}d</span>`;
}

function mtxFilingEvidence(f) {
  const amount=v=>(typeof v==='number'||typeof v==='string'&&v.trim()!=='')&&Number.isFinite(Number(v))&&Number(v)>=0;
  const reference=v=>typeof v==='string'&&!!v.trim();
  const when=v=>typeof v==='string'&&!!v&&Number.isFinite(Date.parse(v));
  const filing=reference(f.acknowledgment_url)&&amount(f.filed_ngn)&&when(f.filed_at);
  const payment=reference(f.payment_reference)&&amount(f.paid_ngn)&&when(f.paid_at);
  return {complete:f.status==='paid'?filing&&payment:f.status==='filed'?filing:false,
    note:f.status==='paid'?(filing&&payment?'Filing acknowledgment and payment reference recorded. External acceptance has not been checked here.':'Marked paid, but filing or payment support is missing. Check the original records.')
      :f.status==='filed'?(filing?'Filing acknowledgment recorded. Payment is a separate record.':'Marked filed, but acknowledgment, amount or filing date is missing.')
      :f.status==='prepared'?'Preparation was marked in EON. This is not proof of submission or payment.'
      :'This is a calendar record and estimate. Confirm the underlying records with your consultant.'};
}

function mtxLoadCalendar(host) {
  return api('/api/tax/calendar').then((cal) => {
    if (!cal || cal.ok === false) {
      throw new Error(cal?.error || 'filing calendar unavailable');
    }
    const meta = document.getElementById('mtx-cal-meta');
    if (meta) {
      meta.textContent =
        `as of ${cal.as_of || ''} · ${(cal.filings || []).length} filings`;
    }
    // Keep the estimate labelled beside a blank declared-amount field.
    State._mtxComputed = {};
    State._mtxRecords = Object.fromEntries((cal.filings||[]).filter(f=>f.id).map(f=>[f.id,f]));
    (cal.filings || []).forEach((f) => {
      if (f.id && f.computed_ngn != null) {
        State._mtxComputed[f.id] = f.computed_ngn;
      }
    });
    host._moneyTaxCalendar=cal;
    host.innerHTML = mtxCalendarHTML(cal);
    mtxBindCalendar(host);
  });
}


function mtxLeaveCorrection(scope=document) {
  const draft=scope.querySelector('.mw-tax-correction[data-dirty="true"]');
  if(!draft)return true;
  if(!window.confirm('Discard the unsaved tax correction? The saved record has not changed.'))return false;
  draft.dataset.dirty='false';return true;
}

function moneyDraftIdentityCurrent() {
  if(moneyEndDrafts.ended||!State.jwt||State.view==='auth')return false;
  // The selector reloads before State changes. A valid cross-tab switch must
  // also bypass beforeunload from this workspace, without changing the selector.
  try {
    const slug=window.localStorage?.getItem('eon_tenant_slug');
    if(!State.isStaff&&!State.impersonating&&slug&&slug!==State.tenant?.slug&&
       State.workspaces?.some(t=>t?.slug===slug))return false;
  }catch{/* Storage denial does not discard an ordinary same-person draft. */}
  return true;
}

// The session has ended and the page is leaving: no draft may ask, and none
// outlives the token. Called before every navigation that ends a session.
function moneyEndDrafts() {
  moneyEndDrafts.ended=true;
  for(const probe of [...(mtxAllowPageRebuild.drafts||[])])probe.dispose();
}

// Email sign-in leaves State.user unset and /me carries no user_id, so the
// view-memory scope is null there. The token's subject names the person.
function moneyDraftScope() {
  let sub=null;
  try{sub=JSON.parse(atob(State.jwt.split('.')[1].replace(/-/g,'+').replace(/_/g,'/'))).sub??null;}catch{/* no readable subject */}
  return JSON.stringify([_viewReadScope(),sub,State.workspaceLink??null]);
}

function moneyRegisterDraft({dirty,label,keep}) {
  const scope=moneyDraftScope();
  let active=true;
  const probe={dirty,label,keep,url:location.href,history:history.state,
    state:{view:'money',moneyTab:State.moneyTab,moneyPeriod:State.moneyPeriod,booksPeriod:State.booksPeriod,
      moneyGateDenied:State.moneyGateDenied,moneyToolsOpen:State.moneyToolsOpen},
    current:()=>active&&moneyDraftIdentityCurrent()&&scope===moneyDraftScope(),
    dispose:()=>{active=false;mtxAllowPageRebuild.drafts.delete(probe);}};
  (mtxAllowPageRebuild.drafts??=new Set()).add(probe);
  return probe;
}

function mtxCurrentCorrection() {
  const draft=document.querySelector('.mw-tax-correction[data-dirty="true"]');
  // A previous person's form must never block logout or a workspace change.
  // The form holds the full draft scope it was opened under (subject, tenant,
  // role); a form without one is never proof that it belongs to this person.
  return moneyDraftIdentityCurrent()&&draft?._moneyScope&&draft._moneyScope===moneyDraftScope()?draft:null;
}

function mtxAllowPageRebuild() {
  const draft=mtxCurrentCorrection();
  if(draft&&!mtxLeaveCorrection()){
    State.view='money';State.moneyTab='tax';
    // Back has already moved the URL. Restore this form's route and view
    // memory without storing any input values in browser history.
    if(location.href!==draft._moneyURL)history.pushState(draft._moneyHistory,'',draft._moneyURL);
    return false;
  }
  for(const probe of mtxAllowPageRebuild.drafts||[]){
    if(!probe.current()){probe.dispose();continue;}
    if(!probe.dirty())continue;
    if(!window.confirm(`Discard the unsaved ${probe.label}? Cancel to keep editing and use Save.`)){
      Object.assign(State,probe.state);
      if(location.href!==probe.url)history.pushState(probe.history,'',probe.url);
      probe.keep?.();return false;
    }
    probe.dispose();
  }
  return true;
}

function mtxCorrectionBeforeUnload(event) {
  if(!mtxCurrentCorrection()&&![...(mtxAllowPageRebuild.drafts||[])].some(p=>p.current()&&p.dirty()))return;
  event.preventDefault();event.returnValue='';
}
window.addEventListener('beforeunload',mtxCorrectionBeforeUnload);

function mtxCorrectionHistoryHTML(f) {
  const history=f.filed_snapshot?._eon_record_corrections;
  if(!Array.isArray(history)||!history.length)return '';
  const labels={filed_ngn:'Amount filed',paid_ngn:'Amount paid',filed_at:'Filing date',paid_at:'Payment date',acknowledgment_url:'Acknowledgment',payment_reference:'Payment reference'};
  const display=(key,value)=>value==null?'Not recorded':key.endsWith('_ngn')?bksNGN(value):key.endsWith('_at')?String(value).slice(0,10):String(value);
  return `<details class="mw-tax-history"><summary>Correction history · ${history.length}</summary><p>Earlier values stay here. Corrections in EON do not repeat a filing or payment.</p>${history.slice().reverse().map(h=>`<article><h4>${h.record==='payment'?'Payment record':'Filing record'} · ${esc(String(h.recorded_at||'Date not recorded').slice(0,16).replace('T',' '))}</h4><p>${esc(h.reason||'Reason not recorded')}</p><dl>${Object.entries(h.after||{}).map(([key,value])=>`<div><dt>${esc(labels[key]||key)}</dt><dd>${esc(display(key,h.before?.[key]))} → ${esc(display(key,value))}</dd></div>`).join('')}</dl><small>Recorded by account ${esc(h.by||'not recorded')}</small></article>`).join('')}</details>`;
}

function mtxCorrectionFormHTML(f,record) {
  const filing=record==='filing',prefix=filing?'filed':'paid',ref=filing?'acknowledgment_url':'payment_reference';
  const start=/^\d{4}-(0[1-9]|1[0-2])$/.test(f.period||'')?f.period+'-01':/^\d{4}$/.test(f.period||'')?f.period+'-01-01':'';
  return `<div class="mtx-cal-inline mw-tax-correction"><strong>Correct the ${filing?'filing':'payment'} record</strong><p>The earlier values and the first saved pack remain in history. This changes EON’s record only; it does not file or pay tax.</p><label>Amount on the actual record (NGN)<input class="mtx-input" data-mtx-correction-amount type="number" min="0" step="0.01" value="${esc(f[prefix+'_ngn']??'')}" required></label><label>Actual ${filing?'filing':'payment'} date<input class="mtx-input" data-mtx-correction-date type="date" min="${start}" max="${mtxLagosToday()}" value="${esc(String(f[prefix+'_at']||'').slice(0,10))}" required></label><label>${filing?'Acknowledgment':'Payment reference'}<input class="mtx-input" data-mtx-correction-reference maxlength="2000" value="${esc(f[ref]||'')}" required></label><label>Why this record needs correcting<textarea class="mtx-input" data-mtx-correction-reason minlength="8" maxlength="2000" rows="3" required></textarea></label><button class="bks-tool-btn" type="button" data-mtx-correction-confirm="${record}"${/^[0-9a-f]{64}$/.test(f.evidence_revision||'')?'':' disabled'}>Save correction with history</button><button class="bks-tool-btn bks-tool-quiet" data-mtx-cancel type="button">Cancel</button>${f.evidence_revision?'':'<p>The saved record needs reloading before a correction can be saved.</p>'}</div>`;
}

async function mtxSaveCorrection(fid,record,card) {
  const get=name=>card.querySelector('[data-mtx-correction-'+name+']');
  const amount=get('amount'),day=get('date'),reference=get('reference'),reason=get('reason');
  for(const field of [amount,day,reference,reason]){field.setCustomValidity('');if(!field.reportValidity())return;}
  if(!reference.value.trim()){reference.setCustomValidity('Add the supporting reference.');reference.reportValidity();return;}
  if(reason.value.trim().length<8){reason.setCustomValidity('Explain why the saved record needs changing.');reason.reportValidity();return;}
  const payload={record,amount:Number(amount.value),actual_on:day.value,reference:reference.value.trim(),reason:reason.value.trim(),expected_revision:State._mtxRecords?.[fid]?.evidence_revision};
  const button=card.querySelector('[data-mtx-correction-confirm]');button.disabled=true;
  try{
    const result=await api(`/api/tax/filings/${encodeURIComponent(fid)}/correction`,{method:'POST',body:JSON.stringify(payload)});
    if(result?.ok!==true)throw Error('unconfirmed');
    await bksLoadCard('mtx-calendar',mtxLoadCalendar);showToast('Correction saved with its earlier values.');
    const refreshed=[...document.querySelectorAll('[data-mtx-fid]')].find(el=>el.dataset.mtxFid===fid);if(refreshed){refreshed.tabIndex=-1;refreshed.focus({preventScroll:true});}
  }catch{
    if(!card.isConnected)return;button.disabled=false;
    let note=card.querySelector('[data-mtx-save-error]');if(!note){note=document.createElement('p');note.dataset.mtxSaveError='';note.setAttribute('role','alert');card.querySelector('.mtx-cal-act').append(note);}
    note.textContent='The correction was not confirmed. Your details remain here. Check the saved record before trying again.';reason.focus();
  }
}

function mtxCalendarHTML(cal) {
  const all=cal.filings||[],filter=State.taxCalendarFilter||'all',period=State.taxCalendarPeriod||'';
  const selected=all.filter(f=>(!period||f.period===period)&&(filter==='all'||filter==='needs_record'&&!mtxFilingEvidence(f).complete||filter==='supported'&&mtxFilingEvidence(f).complete));
  selected.sort((a,b)=>String(a.due_date||'').localeCompare(String(b.due_date||''))||String(a.tax_type).localeCompare(String(b.tax_type)));
  const cards = selected.map((f) => {
    const st = String(f.status || 'upcoming');
    const label = MTX_TYPE_LABEL[f.tax_type] || String(f.tax_type || '').toUpperCase();
    const next = MTX_NEXT_STATUS[st];
    const refs = [
      f.acknowledgment_url
        ? (/^https?:\/\//i.test(f.acknowledgment_url)
            ? `<a class="mtx-cal-ref" href="${esc(f.acknowledgment_url)}" target="_blank" rel="noopener">acknowledgment</a>`
            : `<span class="mtx-cal-ref" title="${esc(f.acknowledgment_url)}">acknowledgment (not a link)</span>`) : '',
      st==='paid'&&f.payment_reference
        ? `<span class="mtx-cal-ref mono-num" title="payment reference">${esc(f.payment_reference)}</span>` : '',
      // Wave-1 filed-amount capture: show what was actually filed/paid
      // beside the live computed figure, and flag drift when the books
      // moved after filing (same tolerance as the backend alarm).
      f.filed_ngn != null
        ? `<span class="mtx-cal-ref mono-num" title="amount filed">filed ${bksNGN(f.filed_ngn)}</span>` : '',
      st==='paid'&&f.paid_ngn != null
        ? `<span class="mtx-cal-ref mono-num" title="amount paid">paid ${bksNGN(f.paid_ngn)}</span>` : '',
    ].filter(Boolean).join(' ');
    const drift = (f.filed_ngn != null && f.computed_ngn != null
      && Math.abs(Number(f.computed_ngn) - Number(f.filed_ngn))
         > Math.max(1000, Math.abs(Number(f.filed_ngn)) * 0.01))
      ? `<span class="bks-chip bks-chip-amber" title="the books moved after filing">COMPUTED NOW ${bksNGN(f.computed_ngn)} — filed ${bksNGN(f.filed_ngn)}</span>`
      : '';
    return `
      <tr class="mtx-cal-card ${st === 'paid' && mtxFilingEvidence(f).complete ? 'mtx-cal-settled' : ''}" data-mtx-fid="${esc(f.id || '')}">
        <th scope="row"><div class="mtx-cal-top">
          <span class="mtx-cal-type">${esc(label)} · ${esc(String(f.period || ''))}</span>
        </div><small>Due ${esc(String(f.due_date||'Not recorded').slice(0,10))}</small></th>
        <td class="mtx-estimate"><div class="mtx-cal-amt mono-num">${f.computed_ngn != null ? bksNGN(f.computed_ngn) : 'Amount unavailable'}</div><small>Calculated from current records</small></td>
        <td>${mtxDueChip(f)}
        <p class="mtx-evidence-note">${esc(mtxFilingEvidence(f).note)}</p>
        ${refs ? `<div class="mtx-cal-refs">${refs}</div>` : ''}
        ${f.filed_at?`<p class="mtx-evidence-note">Filing date ${esc(String(f.filed_at).slice(0,10))}</p>`:''}
        ${st==='paid'&&f.paid_at?`<p class="mtx-evidence-note">Payment date ${esc(String(f.paid_at).slice(0,10))}</p>`:''}
        ${st!=='paid'&&(f.payment_reference||f.paid_ngn!=null)?'<p class="mtx-evidence-note">An earlier payment record is retained after Undo. It is not a current paid status.</p>':''}
        ${drift ? `<div class="mtx-cal-refs">${drift}</div>` : ''}
        ${mtxCorrectionHistoryHTML(f)}
        </td><td class="mtx-cal-act">
          ${(next && f.id)
            ? `<button class="bks-tool-btn mtx-cal-btn" data-mtx-adv="${esc(next)}" type="button">Mark ${esc(next)}</button>`
            : ''}
          ${(f.id && (st === 'filed' || st === 'paid'))
            ? `<button class="bks-tool-btn bks-tool-quiet mtx-cal-btn" data-mtx-undo="${st === 'paid' ? 'filed' : 'prepared'}" type="button" title="Mistyped? Walk this filing back one step">Undo</button>`
            : ''}
          ${f.id&&['filed','paid'].includes(st)&&!mtxFilingEvidence(f).complete?'<button class="bks-tool-btn mtx-cal-btn" data-mtx-adv="filed" type="button">Add filing evidence</button>':''}
          ${f.id&&(State.member?.base_role||State.member?.role)==='owner'&&!State.impersonating&&(f.filed_at||f.filed_ngn!=null||f.acknowledgment_url)?'<button class="bks-tool-btn bks-tool-quiet" data-mtx-correct="filing" type="button">Correct filing record</button>':''}
          ${f.id&&(State.member?.base_role||State.member?.role)==='owner'&&!State.impersonating&&(f.paid_at||f.paid_ngn!=null||f.payment_reference)?'<button class="bks-tool-btn bks-tool-quiet" data-mtx-correct="payment" type="button">Correct payment record</button>':''}
        </td>
      </tr>`;
  }).join('');
  const periods=[...new Set(all.map(f=>f.period).filter(Boolean))].sort().reverse();
  return `<div class="mw-tax-filters"><label>Period<select data-mtx-calendar-period><option value="">All returned periods</option>${periods.map(p=>`<option value="${esc(p)}"${p===period?' selected':''}>${esc(p)}</option>`).join('')}</select></label><label>Show<select data-mtx-calendar-filter>${[['all','Every record'],['needs_record','Needs supporting records'],['supported','Filing or payment support recorded']].map(([v,label])=>`<option value="${v}"${v===filter?' selected':''}>${label}</option>`).join('')}</select></label><p role="status">${selected.length} of ${all.length} returned records</p></div><div class="mw-tax-table-scroll"><table class="mw-tax-table"><caption>Tax records · calculated amounts, declared filing and payment kept separate</caption><thead><tr><th scope="col">Return / due date</th><th scope="col">Current estimate</th><th scope="col">Recorded support</th><th scope="col">Next step</th></tr></thead><tbody>${cards||'<tr><td colspan="4">No records match this view. This does not establish that all tax obligations are complete.</td></tr>'}</tbody></table></div>`;
}

// Delegated once per host node — the strip re-renders via innerHTML on
// every reload, so listeners live on the (stable) host, not the cards.
function mtxBindCalendar(host) {
  if (host.dataset.mtxCalBound) return;
  host.dataset.mtxCalBound = '1';
  host.addEventListener('click', mtxOnCalendarClick);
  host.addEventListener('change',e=>{
    if(!e.target.matches('[data-mtx-calendar-period],[data-mtx-calendar-filter]'))return;
    if(!mtxLeaveCorrection(host)){e.target.value=e.target.matches('[data-mtx-calendar-period]')?(State.taxCalendarPeriod||''):(State.taxCalendarFilter||'all');return;}
    if(e.target.matches('[data-mtx-calendar-period]'))State.taxCalendarPeriod=e.target.value;
    else if(e.target.matches('[data-mtx-calendar-filter]'))State.taxCalendarFilter=e.target.value;
    else return;
    const selector=e.target.matches('[data-mtx-calendar-period]')?'[data-mtx-calendar-period]':'[data-mtx-calendar-filter]';
    host.innerHTML=mtxCalendarHTML(host._moneyTaxCalendar);host.querySelector(selector)?.focus();
  });
}

function mtxOnCalendarClick(e) {
  const card = e.target.closest('[data-mtx-fid]');
  if (!card || !card.dataset.mtxFid) return;
  const fid = card.dataset.mtxFid;
  const otherDraft=document.querySelector('.mw-tax-correction[data-dirty="true"]');
  if(otherDraft&&!otherDraft.contains(e.target)&&e.target.closest('button')&&!mtxLeaveCorrection())return;
  const act = card.querySelector('.mtx-cal-act');

  const cancel = e.target.closest('[data-mtx-cancel]');
  if (cancel) {
    if(!mtxLeaveCorrection(card))return;
    bksLoadCard('mtx-calendar', mtxLoadCalendar);
    return;
  }

  const correction=e.target.closest('[data-mtx-correct]');
  if(correction&&act){
    if(!mtxLeaveCorrection())return;
    const saved=State._mtxRecords?.[fid];if(!saved)return;
    act.innerHTML=mtxCorrectionFormHTML(saved,correction.dataset.mtxCorrect);
    const draft=act.querySelector('.mw-tax-correction');
    draft._moneyScope=moneyDraftScope();draft._moneyURL=location.href;draft._moneyHistory=history.state;
    act.querySelector('input')?.focus();act.querySelectorAll('input,textarea').forEach(input=>input.addEventListener('input',()=>{input.setCustomValidity('');act.querySelector('.mw-tax-correction').dataset.dirty='true';}));return;
  }
  const correctionConfirm=e.target.closest('[data-mtx-correction-confirm]');
  if(correctionConfirm){mtxSaveCorrection(fid,correctionConfirm.dataset.mtxCorrectionConfirm,card);return;}

  const confirmBtn = e.target.closest('[data-mtx-confirm]');
  if (confirmBtn) {
    const toStatus = confirmBtn.dataset.mtxConfirm;
    const input = card.querySelector('[data-mtx-extra]');
    const v = (input?.value || '').trim();
    const extra = {};
    if (toStatus === 'filed' && v) extra.acknowledgment_url = v;
    if (toStatus === 'paid' && v) extra.payment_reference = v;
    // Dates and amounts come from the actual document, never today's estimate.
    const amtInput = card.querySelector('[data-mtx-amount]');
    const amt = amtInput?.value.trim()===''?NaN:Number(amtInput?.value);
    const dateInput=card.querySelector('[data-mtx-date]');
    const actualDate=dateInput?.value||'';
    if(['filed','paid'].includes(toStatus)&&!confirmBtn.hasAttribute('data-mtx-undo-confirm')) {
      if(!v){input?.setCustomValidity('Add the supporting reference.');input?.reportValidity();return;}
      if(!/^\d{4}-\d{2}-\d{2}$/.test(actualDate)||actualDate>mtxLagosToday()){dateInput?.setCustomValidity('Enter the actual date. It cannot be in the future.');dateInput?.reportValidity();return;}
      extra[toStatus==='filed'?'filed_on':'paid_on']=actualDate;
      if(!Number.isFinite(amt)||amt<0){amtInput?.setCustomValidity('Enter the actual amount. Zero is allowed.');amtInput?.reportValidity();return;}
    }
    if (Number.isFinite(amt) && amt >= 0) {
      if (toStatus === 'filed') extra.filed_ngn = amt;
      if (toStatus === 'paid') extra.paid_ngn = amt;
    }
    confirmBtn.disabled = true;
    mtxAdvanceFiling(fid, toStatus, extra);
    return;
  }

  const undoBtn = e.target.closest('[data-mtx-undo]');
  if (undoBtn && act) {
    const back = undoBtn.dataset.mtxUndo;
    act.innerHTML = `
      <div class="mtx-cal-inline">
        <span class="meta">Walk back to ${esc(back)}. Earlier amounts, dates and references stay in the record. This does not reverse a real filing or payment.</span>
        <button class="bks-tool-btn mtx-cal-btn" data-mtx-confirm="${esc(back)}" data-mtx-undo-confirm type="button">Confirm undo</button>
        <button class="bks-tool-btn bks-tool-quiet mtx-cal-btn" data-mtx-cancel type="button">Cancel</button>
      </div>`;
    return;
  }

  const advBtn = e.target.closest('[data-mtx-adv]');
  if (!advBtn) return;
  const next = advBtn.dataset.mtxAdv;
  if (next === 'prepared') {
    advBtn.disabled = true;
    mtxAdvanceFiling(fid, 'prepared', {});
    return;
  }
  // The declared amount stays blank until the operator supplies it.
  if (act) {
    const placeholder = next === 'filed'
      ? 'acknowledgment URL or reference (required)' : 'payment reference (required)';
    const amtLabel = next === 'filed' ? 'Amount filed (NGN)' : 'Amount paid (NGN)';
    const computedRaw = (State._mtxComputed || {})[fid];
    const savedDate=(State._mtxRecords||{})[fid]?.[next==='filed'?'filed_at':'paid_at'];
    const dateLabel=next==='filed'?'Actual filing date':'Actual payment date';
    act.innerHTML = `
      <div class="mtx-cal-inline">
        <input class="mtx-input mono-num" data-mtx-amount type="number" min="0" step="0.01"
               placeholder="${esc(amtLabel)}" aria-label="${esc(amtLabel)}"
               value="" required>
        <small>EON’s current estimate: ${computedRaw!=null?bksNGN(computedRaw):'not established'}. Enter the amount from the actual record.</small>
        <input class="mtx-input" data-mtx-extra type="text" maxlength="2000" placeholder="${esc(placeholder)}" aria-label="${esc(placeholder)}" required>
        <label class="mtx-evidence-date">${dateLabel}<input class="mtx-input" data-mtx-date type="date" aria-label="${dateLabel}" value="${savedDate?esc(String(savedDate).slice(0,10)):''}" max="${mtxLagosToday()}" required></label>
        <button class="bks-tool-btn mtx-cal-btn" data-mtx-confirm="${esc(next)}" type="button">Confirm ${esc(next)}</button>
        <button class="bks-tool-btn bks-tool-quiet mtx-cal-btn" data-mtx-cancel type="button">Cancel</button>
      </div>`;
    act.querySelector('[data-mtx-amount]')?.focus();
    act.querySelectorAll('input').forEach(input=>input.addEventListener('input',()=>input.setCustomValidity('')));
  }
}

function mtxAdvanceFiling(filingId, toStatus, extra) {
  const card=[...document.querySelectorAll('[data-mtx-fid]')].find(el=>el.dataset.mtxFid===filingId);
  const keepDraft=()=>{
    if(!card?.isConnected)return;
    card.querySelectorAll('button').forEach(b=>b.disabled=false);
    let message=card.querySelector('[data-mtx-save-error]');
    if(!message){message=document.createElement('p');message.dataset.mtxSaveError='';message.setAttribute('role','alert');card.querySelector('.mtx-cal-act')?.append(message);}
    message.textContent='The update was not confirmed. Your entered details remain here. Check the saved record before trying another update.';
    card.querySelector('[data-mtx-extra]')?.focus();
  };
  return api(`/api/tax/filings/${encodeURIComponent(filingId)}/status`, {
    method: 'POST',
    body: JSON.stringify({ status: toStatus, ...extra }),
  }).then(async (r) => {
    if (r && r.ok === true) {
      showToast(`Tax record saved · ${r.filing?.status||toStatus}`);
      await bksLoadCard('mtx-calendar', mtxLoadCalendar);
      const next=[...document.querySelectorAll('[data-mtx-fid]')].find(el=>el.dataset.mtxFid===filingId)||document.querySelector('#mtx-calendar [role="status"]');
      if(next){next.tabIndex=-1;next.focus({preventScroll:true});}
    } else {
      keepDraft();
    }
  }).catch(keepDraft);
}

// ── VAT pack ─────────────────────────────────────────────────────

function mtxLoadVat(host) {
  const period = State.taxPeriod || bksCurrentMonth();
  return api(`/api/tax/vat/${encodeURIComponent(period)}`).then((v) => {
    if (!v || v.ok === false) throw new Error(v?.error || 'VAT pack unavailable');
    const meta = document.getElementById('mtx-vat-meta');
    if (meta) meta.textContent = `${bksMonthLabel(period)} · due ${v.due_date || '—'}`;
    const o = v.output || {};
    const inp = v.input || {};
    host.innerHTML = `
      <div class="mtx-hero-wrap">
        <div class="mtx-hero mono-num">${bksNGN(v.net_position_ngn)}</div>
        <div class="mtx-hero-label">
          ${v.payable ? 'Net VAT payable to NRS' : 'Net VAT credit position'}
          · due <span class="mono-num">${esc(v.due_date || '—')}</span>
        </div>
      </div>
      <div class="bks-stmt">
        ${bksStmtRow(`Gross sales (${Number(o.orders || 0).toLocaleString('en-US')} orders)`, o.gross_sales_ngn)}
        ${bksStmtRow('Output VAT', o.output_vat_ngn, { indent: true })}
        ${bksStmtRow('Refunds (gross)', o.refunds_gross_ngn, { indent: true, paren: true })}
        ${bksStmtRow('Refund VAT netted', o.refund_vat_netted_ngn, { indent: true, paren: true })}
        ${bksStmtRow('Net output VAT', o.net_output_vat_ngn, { total: true })}
        ${bksStmtRow('Input VAT claimable', inp.claimable_vat_ngn, { paren: true })}
        ${bksStmtRow('Net position', v.net_position_ngn,
          { grand: true, tone: v.payable ? 'warn' : 'ok' })}
      </div>
      ${inp.note ? `<div class="mtx-quiet">${esc(inp.note)}</div>` : ''}
      <div class="mtx-quiet">computed from full declared revenue — all locations</div>`;
  });
}

// ── PAYE pack ────────────────────────────────────────────────────

function mtxLoadPaye(host) {
  const period = State.taxPeriod || bksCurrentMonth();
  return api(`/api/tax/paye/${encodeURIComponent(period)}`).then((p) => {
    if (!p || p.ok === false) throw new Error(p?.error || 'PAYE schedule unavailable');
    const meta = document.getElementById('mtx-paye-meta');
    if (meta) meta.textContent = `${bksMonthLabel(period)} · due ${p.due_date || '—'}`;
    const staff = p.staff || [];
    if (!staff.length) {
      // Wave-1 honesty: zero and unknown are different numbers. The old
      // copy here claimed draft runs don't populate PAYE — the backend
      // never filtered by status, so that was wrong on both ends.
      host.innerHTML = p.no_payroll_data ? `
        <div class="mtx-empty">No payroll data in EON for this month — PAYE cannot be computed.
          If staff were paid outside EON, PAYE is still owed on those salaries.
          Run payroll in People OS to compute it.</div>` : `
        <div class="mtx-empty">A payroll run exists for this month but carries no pay items yet —
          PAYE computes once items land in People OS.</div>`;
      return;
    }
    const authRows = Object.entries(p.by_authority || {}).map(([name, a]) =>
      bksStmtRow(`${name} (${Number(a.staff || 0)} staff)`, a.paye_ngn,
        { indent: true })).join('');
    const staffRows = staff.map((s) => `
      <tr>
        <td>${esc(s.name || '')}</td>
        <td>${esc(s.location || '—')}</td>
        <td class="mono-num bks-num">${bksNGN(s.gross_month_ngn)}</td>
        <td class="mono-num bks-num">${bksNGN(s.paye_month_ngn)}</td>
        <td class="mono-num bks-num">${s.annualized ? mtxPct((s.annualized.effective_rate_pct || 0) / 100) : '—'}</td>
      </tr>`).join('');
    const er = p.employer_obligations_ngn || {};
    host.innerHTML = `
      <div class="mtx-hero-wrap">
        <div class="mtx-hero mono-num">${bksNGN(p.total_paye_ngn)}</div>
        <div class="mtx-hero-label">PAYE to remit · due <span class="mono-num">${esc(p.due_date || '—')}</span></div>
      </div>
      <div class="bks-stmt">
        <div class="bks-stmt-section">By authority</div>
        ${authRows}
        ${bksStmtRow('Total PAYE', p.total_paye_ngn, { total: true })}
      </div>
      <details class="bks-stmt-details">
        <summary>${staff.length} staff — per-person schedule</summary>
        <div class="bks-scroll">
          <table class="bks-table">
            <thead><tr><th>Staff</th><th>Location</th>
              <th class="bks-num">Gross / mo</th><th class="bks-num">PAYE / mo</th>
              <th class="bks-num">Eff. rate</th></tr></thead>
            <tbody>${staffRows}</tbody>
          </table>
        </div>
      </details>
      <div class="bks-stmt">
        <div class="bks-stmt-section">Employer obligations (separate remittances)</div>
        ${bksStmtRow('Pension (employer)', er.pension_employer_ngn, { indent: true })}
        ${bksStmtRow('NSITF', er.nsitf_ngn, { indent: true })}
        ${bksStmtRow('ITF', er.itf_ngn, { indent: true })}
        ${bksStmtRow('NHF (of basic)', er.nhf_ngn, { indent: true })}
      </div>
      ${p.draft_runs_included
        ? '<div class="mtx-quiet">includes draft payroll — figures can change when the run is approved</div>' : ''}
      ${p.note ? `<div class="mtx-quiet">${esc(p.note)}</div>` : ''}`;
  });
}

// ── WHT pack ─────────────────────────────────────────────────────

// Default payment date for the Log WHT form: today when the viewed
// month is the current one, else the last day of the viewed month —
// so a row logged while reviewing March lands IN March (wave-1 fix
// for the mis-period trap: backend defaulted occurred_at to now()).
function mtxWhtDefaultDate(period) {
  const now = new Date();
  const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  if (period === cur) {
    return `${cur}-${String(now.getDate()).padStart(2, '0')}`;
  }
  const [y, m] = String(period).split('-').map(Number);
  if (!y || !m) return '';
  const lastDay = new Date(y, m, 0).getDate();
  return `${period}-${String(lastDay).padStart(2, '0')}`;
}

function mtxLoadWht(host) {
  const period = State.taxPeriod || bksCurrentMonth();
  return api(`/api/tax/wht/${encodeURIComponent(period)}`).then((w) => {
    if (!w || w.ok === false) throw new Error(w?.error || 'WHT summary unavailable');
    const meta = document.getElementById('mtx-wht-meta');
    if (meta) meta.textContent = `${bksMonthLabel(period)} · due ${w.due_date || '—'}`;
    const ded = w.deducted || {};
    const suf = w.suffered || {};
    const alerts = w.missing_deductions || [];
    const rates = (w.params_used || {}).rates || {};
    const typeOpts = Object.keys(rates).sort();
    const rowLine = (r) => `
      <div class="mtx-wht-line">
        <span class="mtx-wht-cp">${esc(r.counterparty || '')}<span class="mtx-wht-type">${esc(r.payment_type || '')}</span></span>
        <span class="mono-num">${bksNGN(r.wht_ngn)}</span>
      </div>`;
    const alertLines = alerts.map((a) => `
      <div class="mtx-alert">
        <span class="mtx-alert-main">Must deduct <span class="mono-num">${bksNGN(a.wht_to_deduct_ngn)}</span>
          — ${esc(a.counterparty || 'unknown vendor')} (${esc(a.payment_type || '')}, gross <span class="mono-num">${bksNGN(a.gross_ngn)}</span>)</span>
      </div>`).join('');
    host.innerHTML = `
      <div class="mtx-grid2">
        <div class="mtx-col">
          <div class="mtx-col-h">Deducted — must remit</div>
          <div class="mtx-col-num mono-num">${bksNGN(ded.total_ngn)}</div>
          ${Number(ded.unremitted_ngn || 0) > 0
            ? `<span class="bks-chip bks-chip-amber">UNREMITTED ${bksNGN(ded.unremitted_ngn)}</span>
               <button class="bks-tool-btn mtx-cal-btn" id="mtx-wht-remit" type="button"
                 title="Mark this month's deducted WHT as remitted to the authority">Mark month remitted</button>`
            : (Number(ded.total_ngn || 0) > 0
              ? '<span class="bks-chip bks-chip-mint">ALL REMITTED</span>' : '')}
          <div class="mtx-wht-rows">${(ded.rows || []).map(rowLine).join('')
            || '<div class="mtx-quiet">No deductions logged this month.</div>'}</div>
        </div>
        <div class="mtx-col">
          <div class="mtx-col-h">Suffered — credits</div>
          <div class="mtx-col-num mono-num">${bksNGN(suf.total_credits_ngn)}</div>
          ${Number(suf.undocumented_credits_ngn || 0) > 0
            ? `<span class="bks-chip bks-chip-amber">UNDOCUMENTED ${bksNGN(suf.undocumented_credits_ngn)}</span>`
            : '<span class="bks-chip bks-chip-mint">ALL DOCUMENTED</span>'}
          <div class="mtx-wht-rows">${(suf.rows || []).map(rowLine).join('')
            || '<div class="mtx-quiet">No credits logged this month.</div>'}</div>
          ${suf.note ? `<div class="mtx-quiet">${esc(suf.note)}</div>` : ''}
        </div>
      </div>
      ${alerts.length ? `
        <div class="mtx-alerts">
          <div class="mtx-col-h">Missing deductions — <span class="mono-num">${bksNGN(w.missing_deductions_wht_ngn)}</span> exposure</div>
          ${alertLines}
        </div>` : ''}
      <form class="mtx-form" id="mtx-wht-form">
        <select class="mtx-input" name="direction" aria-label="Direction">
          <option value="deducted">deducted</option>
          <option value="suffered">suffered</option>
        </select>
        <input class="mtx-input" name="counterparty" type="text" placeholder="Counterparty" required>
        <select class="mtx-input" name="payment_type" aria-label="Payment type">
          ${(typeOpts.length ? typeOpts : ['rent', 'professional_fees', 'contracts'])
            .map((t) => `<option value="${esc(t)}">${esc(t.replace(/_/g, ' '))}</option>`).join('')}
        </select>
        <select class="mtx-input" name="entity" aria-label="Counterparty entity"
          title="Company vs individual — rates differ (e.g. professional services 10% vs 5%)">
          <option value="company">company</option>
          <option value="individual">individual</option>
        </select>
        <input class="mtx-input" name="gross_ngn" type="number" min="0.01" step="0.01" placeholder="Gross (NGN)" required>
        <input class="mtx-input mono-num" name="occurred_on" type="date"
          value="${esc(mtxWhtDefaultDate(period))}" aria-label="Payment date"
          title="When the payment happened — the row lands in this month's ledger">
        <input class="mtx-input" name="credit_note_url" type="text" placeholder="Credit note URL (suffered)">
        <button class="bks-tool-btn" type="submit">Log WHT</button>
      </form>`;
    host.querySelector('#mtx-wht-remit')?.addEventListener('click', (e) => {
      // Wave-1 remit flow: closes the loop the vet found open — the
      // remitted flag existed since mig 266 but nothing ever set it,
      // so the UNREMITTED chip could never clear.
      const btn = e.target;
      btn.disabled = true;
      api('/api/tax/wht/remit', {
        method: 'POST', body: JSON.stringify({ period }),
      }).then((r) => {
        if (r && r.ok !== false) {
          showToast(`${r.remitted || 0} WHT row(s) marked remitted`);
          moneyRead('mtx-wht',()=>bksLoadCard('mtx-wht', mtxLoadWht),true);
        } else {
          showToast(`Remit failed: ${r?.error || 'unknown error'}`);
          btn.disabled = false;
        }
      }).catch((err) => {
        showToast(`Remit failed: ${err?.message || err}`);
        btn.disabled = false;
      });
    });
    host.querySelector('#mtx-wht-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const form = e.target;
      const payload = {
        direction: form.direction.value,
        counterparty: form.counterparty.value.trim(),
        payment_type: form.payment_type.value,
        entity: form.entity.value,
        gross_ngn: parseFloat(form.gross_ngn.value),
      };
      // Wave-1 period fix: without occurred_at the backend stamped
      // now(), so logging while viewing a PAST month wrote the row
      // into the CURRENT month and it never appeared on the card.
      const od = form.occurred_on.value;
      if (od) payload.occurred_at = od;
      const cn = form.credit_note_url.value.trim();
      if (cn) payload.credit_note_url = cn;
      if (!payload.counterparty || !Number.isFinite(payload.gross_ngn)) {
        showToast('Counterparty and gross amount are required');
        return;
      }
      const btn = form.querySelector('button[type="submit"]');
      if (btn) btn.disabled = true;
      api('/api/tax/wht', { method: 'POST', body: JSON.stringify(payload) })
        .then((r) => {
          if (r && r.ok !== false) {
            showToast('WHT row logged');
            moneyRead('mtx-wht',()=>bksLoadCard('mtx-wht', mtxLoadWht),true);
          } else {
            showToast(`WHT log failed: ${r?.error || 'unknown error'}`);
            if (btn) btn.disabled = false;
          }
        })
        .catch((err) => {
          showToast(`WHT log failed: ${err?.message || err}`);
          if (btn) btn.disabled = false;
        });
    });
  });
}

// ── CIT / PIT provision ──────────────────────────────────────────

function mtxScenarioPanel(title, scn, isCheaper, bodyRows) {
  return `
    <div class="mtx-scn ${isCheaper ? 'mtx-scn-cheaper' : ''}">
      <div class="mtx-scn-head">
        <span class="mtx-col-h">${esc(title)}</span>
        ${isCheaper ? '<span class="bks-chip bks-chip-mint">CHEAPER FORM</span>' : ''}
      </div>
      <div class="mtx-col-num mono-num">${bksNGN(scn.tax_payable_ngn)}</div>
      <div class="mtx-quiet">${esc(scn.entity || '')}</div>
      ${bodyRows}
    </div>`;
}

function mtxLoadCit(host) {
  const year = State.taxYear || mtxCurrentYear();
  return api(`/api/tax/cit/${encodeURIComponent(String(year))}`).then((c) => {
    if (!c || c.ok === false) throw new Error(c?.error || 'provision unavailable');
    const meta = document.getElementById('mtx-cit-meta');
    if (meta) meta.textContent = `FY ${year} · enterprise vs incorporated`;
    const scen = c.scenarios || {};
    const ent = scen.enterprise || {};
    const inc = scen.incorporated || {};
    const cheaper = scen.cheaper_form;
    const savings = Number(scen.incorporation_savings_ngn || 0);
    const pb = c.profit_build_ngn || {};
    const comp = ent.computation || {};

    const bandRows = (comp.bands || []).map((b) => `
      <div class="bks-stmt-row bks-stmt-indent bks-stmt-soft">
        <span class="bks-stmt-label">${mtxPct(b.rate)} on <span class="mono-num">${bksNGN(b.amount_in_band_ngn)}</span>${b.band_width_ngn == null ? ' (top band)' : ''}</span>
        <span class="bks-stmt-amt mono-num">${bksNGN(b.tax_ngn)}</span>
      </div>`).join('');
    const entBody = `
      <div class="bks-stmt">
        ${bksStmtRow('Tax before credits', ent.tax_before_credits_ngn, { indent: true })}
        ${bksStmtRow('WHT credits', ent.wht_credits_ngn, { indent: true, paren: true })}
        ${bksStmtRow(`Effective rate ${comp.effective_rate_pct != null ? mtxPct(comp.effective_rate_pct / 100) : '—'}`, ent.tax_payable_ngn, { total: true })}
      </div>
      ${(comp.bands || []).length ? `
        <details class="bks-stmt-details">
          <summary>Band-by-band computation</summary>
          ${bandRows}
        </details>` : ''}`;

    const levyRows = (inc.levies || []).map((l) =>
      bksStmtRow(`${l.name || 'Levy'} (${mtxPct(l.rate)})`, l.amount_ngn,
        { indent: true })).join('');
    const incBody = `
      <div class="bks-stmt">
        ${bksStmtRow(`CIT at ${mtxPct(inc.cit_rate)} (${inc.company_class || '—'} company)`, inc.cit_ngn, { indent: true })}
        ${levyRows}
        ${bksStmtRow('Tax before credits', inc.tax_before_credits_ngn, { indent: true })}
        ${bksStmtRow('WHT credits', inc.wht_credits_ngn, { indent: true, paren: true })}
        ${bksStmtRow('Payable', inc.tax_payable_ngn, { total: true })}
      </div>
      ${inc.minimum_tax_applied ? '<div class="mtx-quiet">Minimum tax applied (low/no profit).</div>' : ''}`;

    const deltaLine = savings > 0
      ? `Incorporating would save <span class="mono-num">${bksNGN(savings)}</span> for ${year}.`
      : savings < 0
        ? `The current enterprise form is cheaper by <span class="mono-num">${bksNGN(-savings)}</span> for ${year}.`
        : `Both forms compute to the same liability for ${year}.`;

    host.innerHTML = `
      <div class="mtx-grid2">
        ${mtxScenarioPanel('Enterprise (PIT)', ent, cheaper === 'enterprise', entBody)}
        ${mtxScenarioPanel('Incorporated (CIT)', inc, cheaper === 'incorporated', incBody)}
      </div>
      <div class="mtx-delta">${deltaLine}</div>
      <details class="bks-stmt-details">
        <summary>Profit build — assessable <span class="mono-num">${bksNGN(pb.assessable_profit)}</span></summary>
        <div class="bks-stmt">
          ${bksStmtRow('Net revenue (ex VAT)', pb.net_revenue_ex_vat)}
          ${bksStmtRow('COGS (net of reversals)', pb.cogs_net, { indent: true, paren: true })}
          ${bksStmtRow('Operating expenses', pb.opex, { indent: true, paren: true })}
          ${bksStmtRow('Payroll gross', pb.payroll_gross, { indent: true, paren: true })}
          ${bksStmtRow('Employer contributions', pb.employer_contributions, { indent: true, paren: true })}
          ${bksStmtRow('Profit before capital allowances', pb.profit_before_capital_allowances, { total: true })}
          ${bksStmtRow(`Capital allowances claimable${pb.two_thirds_restriction_applied ? ' (2/3 restricted)' : ''}`, pb.capital_allowances_claimable, { indent: true, paren: true })}
          ${bksStmtRow('Assessable profit', pb.assessable_profit, { grand: true })}
        </div>
      </details>
      ${(c.notes || []).map((n) => `<div class="mtx-quiet">${esc(n)}</div>`).join('')}`;
  });
}

// ── Minimization sweep ───────────────────────────────────────────

function mtxLoadMin(host) {
  const year = State.taxYear || mtxCurrentYear();
  return api(`/api/tax/minimization/${encodeURIComponent(String(year))}`).then((m) => {
    if (!m || m.ok === false) throw new Error(m?.error || 'minimization report unavailable');
    const meta = document.getElementById('mtx-min-meta');
    if (meta) meta.textContent = `FY ${year} · ranked by naira impact`;
    const items = (m.items || []).slice();
    const rank = (i) => (typeof i.ngn_impact === 'number' ? i.ngn_impact : -1);
    items.sort((a, b) => rank(b) - rank(a));
    const itemBlocks = items.map((it) => `
      <div class="mtx-item">
        <div class="mtx-item-top">
          <span class="mtx-impact mono-num">${typeof it.ngn_impact === 'number' ? bksNGN(it.ngn_impact) : 'checklist'}</span>
          <span class="mtx-item-name">${esc(it.item || '')}</span>
        </div>
        ${it.action ? `<div class="mtx-item-action">${esc(it.action)}</div>` : ''}
        ${it.statutory_basis ? `<div class="mtx-item-basis">${esc(it.statutory_basis)}</div>` : ''}
        ${it.documentation_needed ? `<div class="mtx-item-doc">Documentation: ${esc(it.documentation_needed)}</div>` : ''}
      </div>`).join('');
    host.innerHTML = `
      <div class="mtx-hero-wrap">
        <div class="mtx-hero mono-num">${bksNGN(m.quantified_total_ngn)}</div>
        <div class="mtx-hero-label">quantified legal-minimization total for ${year}</div>
      </div>
      ${itemBlocks || '<div class="mtx-empty">No minimization items for this year.</div>'}
      ${m.frame ? `<div class="mtx-quiet">${esc(m.frame)}</div>` : ''}`;
  });
}

// ── Capital-asset register ───────────────────────────────────────

const MTX_ASSET_CLASSES = ['plant_machinery', 'motor_vehicles',
  'furniture_fittings', 'buildings', 'computers'];

function mtxLoadAssets(host) {
  return api('/api/tax/assets').then((a) => {
    if (!a || a.ok === false) throw new Error(a?.error || 'asset register unavailable');
    const meta = document.getElementById('mtx-assets-meta');
    if (meta) {
      meta.textContent =
        `${(a.assets || []).length} assets · ${bksNGN(a.total_allowance_this_year_ngn)} allowance this year`;
    }
    const rows = (a.assets || []).map((r) => `
      <tr>
        <td>${esc(r.label || '')}</td>
        <td>${esc(String(r.asset_class || '').replace(/_/g, ' '))}</td>
        <td class="mono-num bks-num">${bksNGN(r.cost_ngn)}</td>
        <td class="mono-num">${esc(String(r.acquired_on || '').slice(0, 10))}</td>
        <td class="mono-num bks-num">${bksNGN(r.allowance_this_year_ngn)}</td>
      </tr>`).join('');
    const classes = (a.asset_classes && a.asset_classes.length)
      ? a.asset_classes : MTX_ASSET_CLASSES;
    host.innerHTML = `
      <div class="bks-scroll">
        <table class="bks-table">
          <thead><tr><th>Asset</th><th>Class</th><th class="bks-num">Cost</th>
            <th>Acquired</th><th class="bks-num">Allowance (this yr)</th></tr></thead>
          <tbody>
            ${rows || '<tr><td colspan="5">No capital assets registered — allowances are being left unclaimed.</td></tr>'}
            <tr class="bks-tb-grand">
              <td>Totals</td><td></td>
              <td class="mono-num bks-num">${bksNGN(a.total_cost_ngn)}</td><td></td>
              <td class="mono-num bks-num">${bksNGN(a.total_allowance_this_year_ngn)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <form class="mtx-form" id="mtx-asset-form">
        <input class="mtx-input" name="label" type="text" placeholder="Asset label" required>
        <select class="mtx-input" name="asset_class" aria-label="Asset class">
          ${classes.map((cl) => `<option value="${esc(cl)}">${esc(cl.replace(/_/g, ' '))}</option>`).join('')}
        </select>
        <input class="mtx-input" name="cost_ngn" type="number" min="0.01" step="0.01" placeholder="Cost (NGN)" required>
        <input class="mtx-input" name="acquired_on" type="date" aria-label="Acquired date" required>
        <button class="bks-tool-btn" type="submit">Register asset</button>
      </form>`;
    host.querySelector('#mtx-asset-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const form = e.target;
      const payload = {
        label: form.label.value.trim(),
        asset_class: form.asset_class.value,
        cost_ngn: parseFloat(form.cost_ngn.value),
        acquired_on: form.acquired_on.value,
      };
      if (!payload.label || !Number.isFinite(payload.cost_ngn) || !payload.acquired_on) {
        showToast('Label, cost and acquired date are required');
        return;
      }
      const btn = form.querySelector('button[type="submit"]');
      if (btn) btn.disabled = true;
      api('/api/tax/assets', { method: 'POST', body: JSON.stringify(payload) })
        .then((r) => {
          if (r && r.ok !== false) {
            const firstYear = r.allowance_schedule?.[0]?.total_ngn;
            showToast(firstYear != null
              ? `Asset registered — first-year allowance ${bksNGN(firstYear)}`
              : 'Asset registered');
            moneyRead('mtx-assets',()=>bksLoadCard('mtx-assets', mtxLoadAssets),true);
            // Allowances feed the provision — refresh it too.
            moneyRead('mtx-cit',()=>bksLoadCard('mtx-cit', mtxLoadCit),true);
          } else {
            showToast(`Asset register failed: ${r?.error || 'unknown error'}`);
            if (btn) btn.disabled = false;
          }
        })
        .catch((err) => {
          showToast(`Asset register failed: ${err?.message || err}`);
          if (btn) btn.disabled = false;
        });
    });
  });
}

// ── Variance check (quote vs EON) ────────────────────────────────

function mtxRenderVariance() {
  const host = document.getElementById('mtx-variance');
  if (!host) return;
  host.innerHTML = `
    <div class="mtx-form" id="mtx-var-form">
      <select class="mtx-input" id="mtx-var-type" aria-label="Tax type">
        ${Object.entries(MTX_TYPE_LABEL).map(([k, lbl]) =>
          `<option value="${esc(k)}">${esc(lbl)}</option>`).join('')}
      </select>
      <input class="mtx-input" id="mtx-var-period" type="month"
             value="${esc(State.taxPeriod || bksCurrentMonth())}" aria-label="Period">
      <input class="mtx-input" id="mtx-var-quoted" type="number" min="0" step="0.01"
             placeholder="Quoted amount (NGN)" aria-label="Quoted amount">
      <button class="bks-tool-btn" id="mtx-var-check" type="button">Check</button>
    </div>
    <div class="mtx-quiet">Paste a consultant's or officer's quoted liability — EON compares it against its own computation for the period.</div>
    <div id="mtx-var-result"></div>`;
  const typeSel = host.querySelector('#mtx-var-type');
  const periodInput = host.querySelector('#mtx-var-period');
  const isAnnual = () => ['cit', 'pit', 'dev_levy'].includes(typeSel.value);
  typeSel.addEventListener('change', () => {
    if (isAnnual()) {
      periodInput.type = 'number';
      periodInput.placeholder = 'YYYY';
      periodInput.value = String(State.taxYear || mtxCurrentYear());
    } else {
      periodInput.type = 'month';
      periodInput.value = State.taxPeriod || bksCurrentMonth();
    }
  });
  host.querySelector('#mtx-var-check').addEventListener('click', () => {
    const quoted = parseFloat(host.querySelector('#mtx-var-quoted').value);
    const period = (periodInput.value || '').trim();
    const out = host.querySelector('#mtx-var-result');
    if (!period || !Number.isFinite(quoted)) {
      out.innerHTML = '<div class="mtx-quiet">Enter a period and a quoted amount first.</div>';
      return;
    }
    out.innerHTML = bksSkel(2);
    api('/api/tax/variance', {
      method: 'POST',
      body: JSON.stringify({
        tax_type: typeSel.value, period, quoted_ngn: quoted,
      }),
    }).then((r) => {
      if (!r || r.ok === false) throw new Error(r?.error || 'variance check failed');
      const pct = Math.abs(Number(r.delta_pct || 0)).toFixed(1);
      const dir = Number(r.delta_ngn || 0) >= 0 ? 'above' : 'below';
      const figures = `
        <div class="mtx-quiet mono-num">EON ${bksNGN(r.computed_ngn)} · quoted ${bksNGN(r.quoted_ngn)} · delta ${bksNGN(r.delta_ngn)}</div>`;
      out.innerHTML = r.flagged
        ? `<div class="mtx-vres mtx-vres-bad">
             <span class="bks-chip bks-chip-red">FLAGGED</span>
             ${esc(pct)}% ${dir} EON's computed — ask for the breakdown.
             ${figures}</div>`
        : `<div class="mtx-vres mtx-vres-ok">
             <span class="bks-chip bks-chip-mint">MATCHES EON</span>
             within ${esc(String(r.threshold_pct ?? 5))}% tolerance (${esc(pct)}% ${dir}).
             ${figures}</div>`;
    }).catch((e) => {
      out.innerHTML = `<div class="bks-soft-err">couldn't check — ${esc(e?.message || '')}</div>`;
    });
  });
}

// ══════════════════════════════════════════════════════════════════
// PROCUREMENT BEGIN (prc-*) — v3.0p107. Finance requests (staff sheet
// + owner approval strip on Money) and the packaging bag triple-record
// control (rep one-tap confirm, counts, reconciliation, SKU costs).
// Backend: app/web/routes_procurement.py / app/modules/procurement.py
// (migration 267). Everything below is lazy — nothing fetches until a
// surface is visible — and fail-soft per card.
// ══════════════════════════════════════════════════════════════════

const _prcState = { templates: null, locations: null, skus: null };

const PRC_KINDS = [
  ['inventory', 'Inventory'], ['store_ops', 'Store ops'],
  ['marketing', 'Marketing'], ['logistics', 'Logistics'],
  ['other', 'Other'],
];
const PRC_BAG_LABELS = {
  bag_s: 'S', bag_m: 'M', bag_l: 'L', bag_xl: 'XL', carton: 'Carton',
};
const PRC_ROLE_LABELS = {
  inventory_manager: 'Inventory manager',
  store_manager: 'Store manager',
  marketing: 'Marketing',
};

// ── Role + permission resolution ──────────────────────────────────
// State.member comes from /api/tenants/{slug}/me: {role (label),
// base_role, custom_role (slug), permissions: ['accounting:manage'...]}.

function prcRequestAccessKey() {
  return [State.jwt || '', State.tenant?.id || State.tenant?.slug || ''].join('|');
}
async function prcLoadRequestAccess() {
  const seq = State.financeRequestAccessSeq = (State.financeRequestAccessSeq || 0) + 1;
  const key = prcRequestAccessKey();
  State.financeRequestAccess = null;
  try {
    const r = await api('/api/procurement/request-access');
    if (seq === State.financeRequestAccessSeq && key === prcRequestAccessKey() && r && r.ok === true) {
      State.financeRequestAccess = { key, allowed: r.can_request_finance === true };
    }
  } catch (_) { /* No grant means no filing; existing history remains readable. */ }
}
function prcCanRequest() {
  // Caller-bound server verdict, never the selected profile or a role label.
  const grant = State.financeRequestAccess;
  return !!grant && grant.key === prcRequestAccessKey() && grant.allowed === true;
}

function prcCanApprove() {
  const m = State.member || {};
  if (['owner', 'admin'].includes(m.base_role || m.role)) return true;
  return (m.permissions || []).includes('accounting:approve');
}

// ── Inventory product-edit approval (BATCH B) ─────────────────────
// Manager (P.INVENTORY_MANAGE) can approve/reject; assistant
// (P.INVENTORY_ASSISTANT_EDIT only — strictly weaker) can PROPOSE an
// edit which opens a manager approval task. Direct product writes stay
// manager-only. Gating reads State.member.permissions (from
// /api/tenants/{slug}/me) and fails soft when the member is unknown.
function invCanManageProducts() {
  const m = State.member || {};
  if (['owner', 'admin', 'manager'].includes(m.base_role || m.role)) return true;
  return (m.permissions || []).includes('inventory:manage');
}

function invCanProposeEdit() {
  const m = State.member || {};
  // Managers don't need the propose flow — they edit/approve directly.
  if (invCanManageProducts()) return false;
  return (m.permissions || []).includes('inventory:assistant:edit');
}

// Map the logged-in member's role/custom-role onto the template roles
// seeded by migration 267 (inventory_manager | store_manager |
// marketing). null = unknown → the sheet shows ALL templates grouped.
function prcResolveRole() {
  const m = State.member || {};
  const cands = [m.custom_role, m.role, m.base_role].filter(Boolean)
    .map((s) => String(s).toLowerCase().replace(/[\s-]+/g, '_'));
  for (const c of cands) {
    if (c.includes('inventory') || c.includes('warehouse')) return 'inventory_manager';
    if (c.includes('store')) return 'store_manager';
    if (c.includes('market')) return 'marketing';
  }
  return null;
}

// ── Small shared helpers ──────────────────────────────────────────

function prcFmtNGN(n) {
  return '₦' + Math.round(Number(n) || 0).toLocaleString('en-US');
}

function prcAge(iso) {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)}h`;
  return `${Math.round(mins / 1440)}d`;
}

// Stores list for pickers. The only frontend source with public
// locations UUIDs is /api/accounting/revenue-by-location (the
// finance_requests/packaging_counts FK target) — cached per session.
async function prcLocations() {
  if (_prcState.locations) return _prcState.locations;
  try {
    const r = await api('/api/accounting/revenue-by-location?period=last_30d');
    _prcState.locations = ((r && r.by_location) || [])
      .filter((l) => l.location_id && l.kind !== 'unmatched')
      .map((l) => ({ id: l.location_id, name: l.name }));
  } catch (_) { _prcState.locations = []; }
  return _prcState.locations;
}

async function prcSkus() {
  if (_prcState.skus) return _prcState.skus;
  try {
    const r = await api('/api/procurement/packaging/skus');
    _prcState.skus = (r && r.skus) || [];
  } catch (_) { _prcState.skus = []; }
  return _prcState.skus;
}

function prcBagSummary(bags) {
  const order = ['bag_xl', 'bag_l', 'bag_m', 'bag_s', 'carton'];
  const parts = order
    .filter((c) => (bags || {})[c] > 0)
    .map((c) => `${bags[c]}&times;${PRC_BAG_LABELS[c] || c}`);
  return parts.join(' + ');
}

// ── 1. Request finance sheet (staff-facing) ───────────────────────

function prcLineRowHTML(ln, i) {
  const total = (parseFloat(ln.qty) || 0) * (parseFloat(ln.unit_ngn) || 0);
  return `<div class="prc-line" data-i="${i}">
    <input class="mtx-input prc-line-label" data-f="label" value="${esc(ln.label || '')}" placeholder="Item"/>
    <input class="mtx-input prc-line-num" data-f="qty" type="number" min="0" step="1" inputmode="numeric" value="${ln.qty || ''}" placeholder="Qty"/>
    <input class="mtx-input prc-line-num" data-f="unit_ngn" type="number" min="0" step="any" inputmode="decimal" value="${ln.unit_ngn || ''}" placeholder="Unit ₦"/>
    <span class="prc-line-total mono-num">${prcFmtNGN(total)}</span>
    <button class="prc-line-rm" data-rm="${i}" type="button" aria-label="Remove line">&times;</button>
  </div>`;
}

async function prcOpenRequestSheet() {
  if (!prcCanRequest()) { showToast('Only managers can request finance.'); return; }
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Request finance</div><button class="csw-x">&times;</button></div>
    <div class="prc-sheet-sub">Pick a recurring item or start blank. A saved request is linked to its approval task when routing succeeds.</div>
    <div class="prc-chips" id="prc-tpl-chips"><span class="prc-quiet">Loading templates&hellip;</span></div>
    <input class="csw-search" id="prc-title" placeholder="Request title" style="margin-bottom:8px"/>
    <div class="prc-kind-row">
      <select class="mtx-input" id="prc-kind">${PRC_KINDS.map(([v, l]) => `<option value="${v}">${l}</option>`).join('')}</select>
      <select class="mtx-input" id="prc-loc"><option value="">No specific location</option></select>
    </div>
    <div class="prc-lines" id="prc-lines"></div>
    <button class="csw-mini" id="prc-add-line" type="button" style="width:100%;margin-top:6px">+ Add line</button>
    <div class="prc-total-row"><span>Total</span><span class="prc-total mono-num" id="prc-total">₦0</span></div>
    <textarea class="mtx-input prc-notes" id="prc-notes" placeholder="Notes for the approver (optional)" rows="2"></textarea>
    <button class="csw-confirm" id="prc-submit" type="button">Send for approval</button>
    <div class="prc-quiet" id="prc-msg"></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());

  let lines = [{ label: '', qty: 1, unit_ngn: 0 }];
  let tplId = null;
  const linesHost = ov.querySelector('#prc-lines');
  const totalOf = () => lines.reduce(
    (a, l) => a + (parseFloat(l.qty) || 0) * (parseFloat(l.unit_ngn) || 0), 0);
  const paint = () => {
    linesHost.innerHTML = lines.map((ln, i) => prcLineRowHTML(ln, i)).join('');
    const t = ov.querySelector('#prc-total');
    if (t) t.textContent = prcFmtNGN(totalOf());
  };
  paint();

  linesHost.addEventListener('input', (e) => {
    const row = e.target.closest('.prc-line');
    const f = e.target.dataset.f;
    if (!row || !f) return;
    const i = +row.dataset.i;
    lines[i][f] = f === 'label' ? e.target.value : (parseFloat(e.target.value) || 0);
    const lt = row.querySelector('.prc-line-total');
    if (lt) lt.textContent = prcFmtNGN((parseFloat(lines[i].qty) || 0) * (parseFloat(lines[i].unit_ngn) || 0));
    const t = ov.querySelector('#prc-total');
    if (t) t.textContent = prcFmtNGN(totalOf());
  });
  linesHost.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-rm]');
    if (!rm) return;
    lines.splice(+rm.dataset.rm, 1);
    if (!lines.length) lines = [{ label: '', qty: 1, unit_ngn: 0 }];
    paint();
  });
  ov.querySelector('#prc-add-line').addEventListener('click', () => {
    lines.push({ label: '', qty: 1, unit_ngn: 0 });
    paint();
  });

  // Stores picker (fail-soft — request can go in without a location).
  prcLocations().then((locs) => {
    const sel = ov.querySelector('#prc-loc');
    if (!sel || !locs.length) return;
    sel.innerHTML = '<option value="">No specific location</option>'
      + locs.map((l) => `<option value="${esc(l.id)}">${esc(l.name)}</option>`).join('');
  });

  // Role-aware template chips. Unknown role → all templates grouped.
  (async () => {
    const chipsHost = ov.querySelector('#prc-tpl-chips');
    const role = prcResolveRole();
    let templates = [];
    try {
      const q = role ? `?role=${encodeURIComponent(role)}` : '';
      templates = (await api('/api/procurement/templates' + q)).templates || [];
      if (!templates.length && role) {
        templates = (await api('/api/procurement/templates')).templates || [];
      }
    } catch (_) { templates = []; }
    if (!chipsHost) return;
    if (!templates.length) {
      chipsHost.innerHTML = '<span class="prc-quiet">No templates — fill the lines below.</span>';
      return;
    }
    const roles = [...new Set(templates.map((t) => t.role))];
    const grouped = roles.length > 1;
    chipsHost.innerHTML = roles.map((r) => {
      const label = grouped
        ? `<span class="prc-chip-group">${esc(PRC_ROLE_LABELS[r] || r)}</span>` : '';
      return label + templates.filter((t) => t.role === r).map((t) =>
        `<button class="prc-chip" type="button" data-tpl="${esc(t.id)}">${esc(t.title)}</button>`).join('');
    }).join('');
    chipsHost.querySelectorAll('[data-tpl]').forEach((b) => b.addEventListener('click', () => {
      const t = templates.find((x) => String(x.id) === b.dataset.tpl);
      if (!t) return;
      tplId = t.id;
      chipsHost.querySelectorAll('.prc-chip').forEach((x) => x.classList.toggle('on', x === b));
      ov.querySelector('#prc-title').value = t.title || '';
      const kindSel = ov.querySelector('#prc-kind');
      if (kindSel && t.kind) kindSel.value = t.kind;
      // Deep-copy template lines so edits never mutate the cached template.
      lines = (t.lines || []).map((l) => ({
        label: l.label || '', qty: l.qty || 0, unit_ngn: l.unit_ngn || 0,
        category_slug: l.category_slug || null,
        ...(l.sku_code ? { sku_code: l.sku_code } : {}),
      }));
      if (!lines.length) lines = [{ label: '', qty: 1, unit_ngn: 0 }];
      paint();
    }));
  })();

  ov.querySelector('#prc-submit').addEventListener('click', async () => {
    const msg = ov.querySelector('#prc-msg');
    const title = (ov.querySelector('#prc-title').value || '').trim();
    if (!title) { msg.textContent = 'Title required.'; return; }
    const outLines = lines
      .map((l) => ({
        label: (l.label || '').trim(),
        qty: parseFloat(l.qty) || 0,
        unit_ngn: parseFloat(l.unit_ngn) || 0,
        total_ngn: (parseFloat(l.qty) || 0) * (parseFloat(l.unit_ngn) || 0),
        category_slug: l.category_slug || null,
        ...(l.sku_code ? { sku_code: l.sku_code } : {}),
      }))
      .filter((l) => l.label && l.total_ngn > 0);
    if (!outLines.length) { msg.textContent = 'Add at least one line with a quantity and unit price.'; return; }
    const btn = ov.querySelector('#prc-submit');
    btn.disabled = true; btn.textContent = 'Sending…';
    try {
      const r = await api('/api/procurement/requests', {
        method: 'POST',
        body: JSON.stringify({
          title,
          kind: ov.querySelector('#prc-kind').value || 'other',
          lines: outLines,
          location_id: ov.querySelector('#prc-loc').value || null,
          template_id: tplId,
          notes: (ov.querySelector('#prc-notes').value || '').trim() || null,
        }),
      });
      if (!r || !r.ok) throw new Error((r && r.error) || 'request failed');
      ov.querySelector('.csw-sheet').innerHTML = `
        <div class="prc-success">
          <div class="prc-success-t">${r.routing_pending ? 'Request saved; approval routing needs attention.' : 'Request saved and linked for approval.'}</div>
          <div class="prc-success-m">${esc(title)} &middot; ${prcFmtNGN(totalOf())}</div>
          <button class="csw-confirm" id="prc-done" type="button">Done</button>
        </div>`;
      document.dispatchEvent(new Event('eon:procurement-changed'));
      ov.querySelector('#prc-done').addEventListener('click', () => ov.remove());
    } catch (e) {
      msg.textContent = e.message || String(e);
      btn.disabled = false; btn.textContent = 'Send for approval';
    }
  });
}

// ── 1b. "Where did my request go?" (operator 2026-08-07) ─────────
// The sheet's success panel died with the sheet and the approval task
// went to nobody, so a filed request left NO trace on the requester's
// screen — his ₦400,000 marketing request sat `pending` while My Work
// said "TODAY · 1" and didn't include it. This is the trace: the
// caller's own open requests, with the status the row actually holds.
// Reads /requests/mine (ACCOUNTING_LOG — the same tier that may file).

const PRC_STATUS_WORD = {
  pending:  'waiting on approval',
  approved: 'approved — waiting on payment',
  paid:     'paid',
  rejected: 'rejected',
};

async function prcHydrateMyRequests() {
  const host = document.getElementById('prc-mine');
  if (!host || !State.jwt) return; // Existing requester history is readable after filing eligibility changes.
  let reqs = [];
  try {
    reqs = (await api('/api/procurement/requests/mine?limit=20')).requests || [];
  } catch (_) { return; }          // fail-soft: the hero still renders
  const live = reqs; // Keep paid, rejected and receipt-outstanding history visible.
  if (!live.length) return;
  host.hidden = false;
  host.innerHTML = live.map(r => `
    <div class="prc-mine__row">
      <span class="prc-mine__t">${esc(r.title || 'Request')}</span>
      <span class="prc-mine__n mono-num">${prcFmtNGN(r.total_ngn)}</span>
      <span class="prc-mine__s prc-mine__s--${esc(r.status || 'pending')}">${esc(PRC_STATUS_WORD[r.status] || r.status || '')}${r.status === 'paid' && r.receipt_status !== 'complete' ? ' · receipt outstanding' : ''}</span>
    </div>`).join('');
}

// ── 2. Owner approval strip + packaging expandable (Money page) ───
// Mount lives in renderMoneyTabBar so it rides above the tab content
// on every Money tab. Hydrated from bindMoney; approver-only.

async function prcHydrateMoneyStrip() {
  const host = document.getElementById('prc-strip');
  const pack = document.getElementById('prc-pack');
  if (!host || !prcCanApprove()) return;

  // Packaging expandable is always available to approvers — bag costs
  // must be settable even with zero open requests. Body loads lazily
  // on first open (nothing fetches until visible).
  if (pack && pack.hidden) {
    pack.hidden = false;
    pack.addEventListener('toggle', () => {
      if (pack.open && !pack.dataset.prcLoaded) {
        pack.dataset.prcLoaded = '1';
        prcLoadPackagingPanel();
      }
    });
  }

  if(!['overview','workbench','tools'].includes(State.moneyTab||'overview')){host.hidden=true;return;}
  if(document.getElementById('money-request-history')){
    host.hidden=true;
    if(document.getElementById('mr-requests')?.open)prcLoadMoneyHistory();
    return;
  }
  let reqs = [];
  try {
    const result=await api('/api/procurement/requests?limit=100');
    if(!result||result.ok===false||!Array.isArray(result.requests))throw new Error('unavailable');
    reqs=result.requests;
  } catch (_) {
    if(!host.isConnected||!prcCanApprove())return;
    host.hidden=false;host.innerHTML='<p role="alert">Request records did not load. This does not mean there is no work waiting.</p><button data-prc-retry>Try again</button>';
    host.querySelector('[data-prc-retry]').addEventListener('click',prcHydrateMoneyStrip);return;
  }
  if(!host.isConnected||!prcCanApprove())return;
  const pending = reqs.filter((r) => r.status === 'pending');
  const approved = reqs.filter((r) => r.status === 'approved');
  // This strip is the owner's decision queue. Once payment is recorded,
  // the request leaves it; the Finance workspace keeps the paid record
  // and its honest receipt status for follow-up.
  if (!pending.length && !approved.length) {
    host.hidden = true;
    host.innerHTML = '';
    return;
  }
  const locs = await prcLocations();
  const locName = {};
  locs.forEach((l) => { locName[l.id] = l.name; });
  const metaBits = [`${pending.length} pending`];
  if (approved.length) metaBits.push(`${approved.length} to pay`);
  host.innerHTML = `
    <div class="prc-strip-head">
      <span class="prc-strip-title">Requests</span>
      <span class="prc-strip-meta">${metaBits.join(' &middot; ')}</span>
    </div>
    <div class="prc-cards">
      ${pending.map((r) => prcReqCardHTML(r, 'pending', locName)).join('')}
      ${approved.map((r) => prcReqCardHTML(r, 'approved', locName)).join('')}
    </div>`;
  host.hidden = false;
  if (!host.dataset.prcBound) {
    host.dataset.prcBound = '1';
    host.addEventListener('click', prcOnStripClick);
  }
}

// A deliberately bounded history view. Paid requests stay out of the
// approval strip while their receipt work remains reachable on Money.
function prcMoneyHistoryHTML(records,filter='all',query='',total=null) {
  const status={pending:'Awaiting approval',approved:'Approved · payment not recorded',paid:'Payment recorded',rejected:'Rejected'};
  const receipt={complete:'Receipt recorded',partial:'Some receipts missing',none:'Receipt not recorded'};
  const amount=v=>(typeof v==='number'||typeof v==='string'&&v.trim()!=='')&&Number.isFinite(Number(v));
  const filtered=records.filter(r=>(filter==='all'||filter==='receipts'&&r.status==='paid'&&r.receipt_status!=='complete'||r.status===filter)&&(!query||[r.title,r.notes,r.requester_role,...(r.lines||[]).map(l=>l.label)].join(' ').toLowerCase().includes(query.toLowerCase())));
  return `<p data-request-count role="status">${filtered.length} of ${records.length} returned requests · ${Number.isInteger(total)?`${total} in all`:'latest 100 at most'}</p><div class="mw-request-scroll"><table class="mw-request-table"><caption>Request approval does not record a payment. A payment record does not prove a receipt was supplied.</caption><thead><tr><th>Request</th><th>Amount</th><th>Payment</th><th>Receipt</th><th>Next step</th></tr></thead><tbody>${filtered.map(r=>{
    const action=r.status==='pending'?`<button class="prc-act" data-act="approve" data-id="${esc(r.id)}">Review approval</button><button class="prc-act" data-act="reject" data-id="${esc(r.id)}">Review rejection</button>`:r.status==='approved'?`<button class="prc-act" data-act="paid" data-id="${esc(r.id)}">Record payment</button>`:r.status==='paid'&&r.receipt_status!=='complete'?`<button class="prc-act" data-act="receipt" data-id="${esc(r.id)}">Add receipt</button>`:'No open action';
    return `<tr><th scope="row">${esc(r.title||'Request')}<small>${esc(String(r.created_at||'Date not returned').slice(0,10))} · ${esc(r.requester_role||'Requester not returned')}</small></th><td data-label="Amount" class="mw-request-amount">${amount(r.total_ngn)?prcFmtNGN(Number(r.total_ngn)):'Amount unavailable'}</td><td data-label="Payment">${esc(status[r.status]||'Status unavailable')}</td><td data-label="Receipt">${esc(receipt[r.receipt_status]||'Receipt status unavailable')}</td><td data-label="Next step">${action}</td></tr>`;
  }).join('')||'<tr><td colspan="5">No requests match this view. This is a view of the latest returned records.</td></tr>'}</tbody></table></div>`;
}
// Older requests page in, 100 at a time, under the server's own total. The
// filter, words and pages are kept in memory only under a named person,
// workspace and role, so returning to Money or pressing Back finds the same
// view. An email owner or admin has no view scope (null), so nothing is kept
// for them; the token subject in the draft scope tells two of them apart.
async function prcLoadMoneyHistory(more,pages) {
  const host=document.getElementById('money-request-history');if(!host||!prcCanApprove())return;
  const scope=moneyViewScope(State),kept=scope&&State.moneyRequestView?.scope===scope?State.moneyRequestView:null;State.moneyRequestView=kept;
  const who=()=>JSON.stringify([moneyViewScope(State),moneyDraftScope()]),me=who(),live=host._requestWho===me;
  if(!live)host._requestRows=null;const shown=Array.isArray(host._requestRows)?host._requestRows:null;
  const older=more===true&&!!shown&&!!host.querySelector('[data-request-older]');
  const f=live?host.querySelector('[data-request-filter]'):null,q=live?host.querySelector('[data-request-query]'):null;
  const filter=f?f.value:kept?.filter||'all',query=q?q.value:kept?.query||'';
  const generation=(host._requestGeneration||0)+1;host._requestGeneration=generation;
  const offset=older?host._requestRows.length:0,limit=older?100:Math.min(500,100*(pages||(shown?.length?Math.ceil(shown.length/100):kept?.pages||1)));
  if(older)host.querySelector('[data-request-older]').innerHTML='<p role="status">Reading older requests…</p>';
  else host.innerHTML='<p role="status">Reading request and receipt records…</p>';
  const stale=()=>{if(who()===me)return false;if(host._requestGeneration===generation){host.replaceChildren();host._requestRows=null;}return true;};
  try {
    const r=await api(`/api/procurement/requests?limit=${limit}&offset=${offset}`);
    if(stale()||!host.isConnected||!prcCanApprove()||host._requestGeneration!==generation)return;
    if(!r||r.ok===false||!Array.isArray(r.requests)||r.requests.some(x=>!x||typeof x!=='object'||x.id==null)||!Number.isInteger(r.total)||r.requests.length!==Math.min(limit,Math.max(0,r.total-offset)))throw new Error('unavailable');
    // A request added while paging shifts every older page: read again from the newest.
    if(older&&r.total!==host._requestTotal)return prcLoadMoneyHistory(false,Math.min(5,Math.ceil(host._requestRows.length/100)+1));
    const seen=new Set();
    host._requestRows=[...(older?host._requestRows:[]),...r.requests].filter(x=>!seen.has(String(x.id))&&seen.add(String(x.id)));
    host._requestTotal=r.total;host._requestWho=me;
    if(!older){
      host.innerHTML='<div class="mw-request-filters"><label>Find a request<input type="search" data-request-query placeholder="Title, purpose or item"></label><label>Show<select data-request-filter><option value="all">Every returned request</option><option value="pending">Awaiting approval</option><option value="approved">Approved · waiting for payment</option><option value="receipts">Paid · receipts missing</option><option value="paid">All recorded payments</option><option value="rejected">Rejected</option></select></label></div><div data-request-rows></div><div data-request-older></div>';
      host.querySelector('[data-request-filter]').value=filter;host.querySelector('[data-request-query]').value=query;
      host.querySelector('[data-request-filter]').addEventListener('change',()=>host._requestPaint());host.querySelector('[data-request-query]').addEventListener('input',()=>host._requestPaint());
      if(!host.dataset.bound){host.dataset.bound='true';host.addEventListener('click',prcOnStripClick);}
    }
    host._requestPaint=()=>{if(stale())return;
      const fv=host.querySelector('[data-request-filter]').value,qv=host.querySelector('[data-request-query]').value,rows=host._requestRows;
      if(scope)State.moneyRequestView={scope,filter:fv,query:qv,pages:Math.min(5,Math.max(1,Math.ceil(rows.length/100)))};
      host.querySelector('[data-request-rows]').innerHTML=prcMoneyHistoryHTML(rows,fv,qv,host._requestTotal);
      const area=host.querySelector('[data-request-older]');area.innerHTML=host._requestTotal>rows.length?'<button type="button" data-request-more>Show older requests</button>':'';
      area.querySelector('[data-request-more]')?.addEventListener('click',()=>prcLoadMoneyHistory(true));
    };
    host._requestPaint();
  } catch(_) {
    if(stale()||!host.isConnected||host._requestGeneration!==generation)return;
    if(older){
      const area=host.querySelector('[data-request-older]');
      area.innerHTML='<p role="alert">Older requests did not load. The requests above are unchanged.</p><button type="button" data-request-more>Try again</button>';
      area.querySelector('[data-request-more]').addEventListener('click',()=>prcLoadMoneyHistory(true));return;
    }
    host.innerHTML='<p role="alert">Request records did not load. This does not mean there is no work waiting.</p><button data-request-retry>Try again</button>';
    host.querySelector('[data-request-retry]').addEventListener('click',prcLoadMoneyHistory);
  }
}

function prcReceiptChip(status) {
  const cls = status === 'complete' ? 'bks-chip-mint'
    : (status === 'partial' ? 'bks-chip-amber' : 'bks-chip-red');
  const label = status === 'complete' ? 'RECEIPT COMPLETE'
    : (status === 'partial' ? 'RECEIPT PARTIAL' : 'NO RECEIPT');
  return `<span class="bks-chip ${cls}">${label}</span>`;
}

function prcReqCardHTML(r, mode, locName) {
  const lines = r.lines || [];
  const lineSummary = lines.slice(0, 2).map((l) => esc(l.label || '')).filter(Boolean).join(', ')
    + (lines.length > 2 ? ` +${lines.length - 2} more` : '');
  const loc = r.location_id ? (locName[r.location_id] || null) : null;
  const meta = [
    esc(r.requester_role || 'manager'),
    esc((r.kind || '').replace('_', ' ')),
    loc ? esc(loc) : null,
    r.created_at ? `${prcAge(r.created_at)} ago` : null,
  ].filter(Boolean).join(' &middot; ');
  let actions = '';
  if (mode === 'pending') {
    actions = `<button class="bks-tool-btn prc-act prc-act-ok" data-act="approve" data-id="${esc(r.id)}" type="button">Approve</button>
      <button class="bks-tool-btn bks-tool-quiet prc-act" data-act="reject" data-id="${esc(r.id)}" type="button">Reject</button>`;
  } else if (mode === 'approved') {
    actions = `<span class="bks-chip bks-chip-mint">APPROVED</span>
      <button class="bks-tool-btn prc-act prc-act-ok" data-act="paid" data-id="${esc(r.id)}" type="button">Record payment</button>`;
  } else {
    actions = `${prcReceiptChip(r.receipt_status || 'none')}
      <button class="bks-tool-btn prc-act" data-act="receipt" data-id="${esc(r.id)}" type="button">Upload receipt</button>`;
  }
  return `<div class="prc-card" data-rid="${esc(r.id)}">
    <div class="prc-card-top">
      <span class="prc-card-title">${esc(r.title || 'Request')}</span>
      <span class="prc-card-amt mono-num">${prcFmtNGN(r.total_ngn)}</span>
    </div>
    <div class="prc-card-meta">${meta}</div>
    ${lineSummary ? `<div class="prc-card-lines">${lineSummary}</div>` : ''}
    <div class="prc-card-actions">${actions}</div>
  </div>`;
}

async function prcOpenExistingRequest(id,jump=true) {
  if(!id || !State.jwt || !State.tenant?.id || !memberCan('accounting:view'))return;
  const token=State.jwt,tenant=State.tenant.id,view=State.view,access=JSON.stringify(State.member||{});
  const ov=cswOverlay('<div class="csw-sheet__h"><div class="csw-sheet__t">Finance request</div><button class="csw-x" aria-label="Close finance request">×</button></div><div data-finance-record role="status">Reading the existing record…</div>');
  ov.querySelector('.csw-x').addEventListener('click',()=>ov.remove());
  ov.querySelector('[role="dialog"]')?.setAttribute('aria-label','Finance request record');
  const same=()=>ov.isConnected&&State.jwt===token&&State.tenant?.id===tenant&&State.view===view&&JSON.stringify(State.member||{})===access&&memberCan('accounting:view');
  try{
    const data=await api('/api/procurement/requests/'+encodeURIComponent(id));
    if(!same()){ov.remove();return;}
    const r=data?.request,body=ov.querySelector('[data-finance-record]');
    if(!r||String(r.id)!==String(id)){body.textContent='This request could not be established. Close and refresh the list.';return;}
    const number=value=>(typeof value==='number'||typeof value==='string'&&value.trim()!=='')&&Number.isFinite(Number(value));
    const money=value=>number(value)?prcFmtNGN(Number(value)):'Amount unavailable';
    body.innerHTML=`<div class="pd-pack-section"><b>${esc(r.title||'Finance request')}</b><p>${esc(money(r.total_ngn))}</p><p>${esc(({pending:'Awaiting approval',approved:'Approved — payment not recorded',paid:'Payment recorded',rejected:'Rejected'})[r.status]||'Status unavailable')}</p><p>Record ${esc(r.id)}</p><p>Requester: ${esc(r.requester_role||'not returned')}</p></div>${(Array.isArray(r.lines)?r.lines:[]).map(line=>`<div class="pd-pack-section"><b>${esc(line.label||'Item')}</b><p>${number(line.qty)?esc(line.qty):'Quantity unavailable'} × ${esc(money(line.unit_ngn))}</p></div>`).join('')}${r.notes?`<div class="pd-pack-section"><p>${esc(r.notes)}</p></div>`:''}<p class="pos-flag__m">Approval, payment logging and receipt evidence are separate steps. Opening this record makes no decision.</p><button class="csw-mini" data-open-finance-workspace>Open Finance workspace</button>`;
    if(prcCanApprove()&&r.status==='pending'){
      body.insertAdjacentHTML('beforeend',`<div class="prc-card-actions"><button class="csw-mini prc-act" data-act="approve" data-id="${esc(r.id)}">Review approval</button><button class="csw-mini prc-act" data-act="reject" data-id="${esc(r.id)}">Review rejection</button></div>`);
      body.querySelectorAll('.prc-act').forEach(button=>button.addEventListener('click',event=>{if(same()){ov.remove();prcOnStripClick(event);}}));
    }
    // The jump keeps the person on this record: Finance opens, then the same request opens over it.
    const open=body.querySelector('[data-open-finance-workspace]');
    if(!jump)open.remove();else open.addEventListener('click',()=>{if(!same()){ov.remove();return;}ov.remove();posSetTab('finance');prcOpenExistingRequest(id,false);});
  }catch(_){if(same())ov.querySelector('[data-finance-record]').textContent='The finance request is unavailable. Close and try again.';else ov.remove();}
}

async function prcOnStripClick(e) {
  const btn = e.target.closest('.prc-act');
  if (!btn) return;
  const id = btn.dataset.id;
  const act = btn.dataset.act;
  if (act === 'approve' || act === 'reject') {
    if(btn.disabled || !prcCanApprove())return;
    btn.disabled=true;
    try{await prcConfirmDecision(id,act);}finally{btn.disabled=false;}
  } else if (act === 'paid') {
    prcOpenMarkPaid(id);
  } else if (act === 'receipt') {
    prcOpenReceiptUpload(id);
  }
}

function prcDecisionSnapshot(r) {
  return JSON.stringify([r.id,r.title,r.status,r.total_ngn,r.requester_role,r.location_id,r.lines,r.notes]);
}
async function prcConfirmDecision(id,act) {
  if(!id || !['approve','reject'].includes(act) || !State.jwt || !State.tenant?.id || !prcCanApprove())return;
  const token=State.jwt,tenant=State.tenant.id,access=JSON.stringify(State.member||{}),view=State.view;
  const ov=cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">${act==='approve'?'Review approval':'Review rejection'}</div><button class="csw-x" aria-label="Close decision review">×</button></div><div data-decision-review role="status">Reading the current request…</div>`);
  ov.querySelector('.csw-x').addEventListener('click',()=>ov.remove());
  const same=()=>ov.isConnected&&State.jwt===token&&State.tenant?.id===tenant&&State.view===view&&JSON.stringify(State.member||{})===access&&prcCanApprove();
  const read=async()=>{const d=await api('/api/procurement/requests/'+encodeURIComponent(id));return d?.request;};
  try{
    const r=await read();if(!same()){ov.remove();return;}
    const host=ov.querySelector('[data-decision-review]');
    if(!r || String(r.id)!==String(id) || r.status!=='pending'){host.textContent='This request is no longer pending or could not be read. Close and refresh.';return;}
    const snapshot=prcDecisionSnapshot(r),validAmount=(typeof r.total_ngn==='number'||typeof r.total_ngn==='string'&&r.total_ngn.trim()!=='')&&Number.isFinite(Number(r.total_ngn));
    if(act==='approve'&&!validAmount){host.textContent='The requested amount is unavailable. Approval cannot be confirmed.';return;}
    host.innerHTML=`<div class="pd-pack-section"><b>${esc(r.title||'Finance request')}</b><p>${validAmount?esc(prcFmtNGN(Number(r.total_ngn))):'Amount unavailable'}</p><p>Record ${esc(r.id)}</p></div>${(Array.isArray(r.lines)?r.lines:[]).map(line=>`<p>${esc(line.label||'Item')} · ${esc(line.qty??'Quantity unavailable')} × ${esc(line.unit_ngn??'Unit amount unavailable')}</p>`).join('')}<p>${act==='approve'?'Confirm approval of this request. No payment or expense is recorded by this decision.':'Confirm rejection of this request. No payment is recorded.'}</p>${act==='reject'?'<label>Reason (optional)<textarea data-decision-reason maxlength="1000" class="csw-input"></textarea></label>':''}<button class="csw-confirm" data-decision-confirm>Confirm ${act==='approve'?'approval':'rejection'}</button><p data-decision-result role="status"></p>`;
    let busy=false;
    host.querySelector('[data-decision-confirm]').addEventListener('click',async()=>{
      if(busy||!same())return;busy=true;
      const button=host.querySelector('[data-decision-confirm]'),result=host.querySelector('[data-decision-result]');button.disabled=true;
      let sent=false;
      try{
        const fresh=await read();if(!same()){ov.remove();return;}
        if(!fresh||String(fresh.id)!==String(id)||prcDecisionSnapshot(fresh)!==snapshot){result.textContent='The request changed while you were reviewing it. Close and review the current record.';return;}
        const reason=(host.querySelector('[data-decision-reason]')?.value||'').trim();
        const endpoint=act==='approve'?`/api/procurement/requests/${encodeURIComponent(id)}/approve`:`/api/procurement/requests/${encodeURIComponent(id)}/reject`;
        sent=true;
        const res=await api(endpoint,{method:'POST',body:JSON.stringify(act==='reject'?{reason:reason||null}:{})});
        if(!same()){ov.remove();return;}
        if(!res?.ok){result.textContent='The decision was not confirmed. Close and refresh the request before trying again.';return;}
        const final=await read();if(!same()){ov.remove();return;}
        if(!final||String(final.id)!==String(id)||final.status!==(act==='approve'?'approved':'rejected')){result.textContent='The response arrived, but the updated record could not be confirmed. Close and refresh.';return;}
        result.textContent=act==='approve'?'Approved. Payment has not been recorded.':'Rejected.';
        document.dispatchEvent(new Event('eon:procurement-changed'));prcHydrateMoneyStrip();
      }catch(_){if(same())result.textContent=sent?'The decision result is unavailable. It may have been recorded. Close and refresh before trying again.':'The request could not be re-read. No decision was sent. Close and try again.';else ov.remove();}
    });
  }catch(_){if(same())ov.querySelector('[data-decision-review]').textContent='The request is unavailable. No decision was sent.';else ov.remove();}
}

// M6b: record a payment already made (procurement.mark_paid writes one expense
// per line, then the request's progress; the two are not one step). The
// request is read fresh and shown before confirming, and read again just
// before sending. An accounts read that fails (an error, including the
// route's 503 when the database read fails, ok:false, a malformed list) stops
// recording; only a real empty list records without an account.
// After any send the button stays off: nothing here retries a payment that
// may already be recorded.
async function prcOpenMarkPaid(id) {
  if(!id || !State.jwt || !State.tenant?.id || !prcCanApprove())return;
  const token=State.jwt,tenant=State.tenant.id,view=State.view,access=JSON.stringify(State.member||{});
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Record payment</div><button class="csw-x" aria-label="Close record payment">&times;</button></div>
    <div class="prc-sheet-sub">This records a payment that has already been made. It does not send money. EON logs one expense per line; books, VAT and WHT follow the line categories.</div>
    <div data-paid-request role="status">Reading the current request…</div>
    <div class="prc-chips" id="prc-acct-chips"></div>
    <div data-paid-summary></div>
    <button class="csw-confirm" id="prc-paid-go" type="button" disabled>Record payment</button>
    <div class="prc-quiet" id="prc-paid-msg" role="status"></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const same=()=>ov.isConnected&&State.jwt===token&&State.tenant?.id===tenant&&State.view===view&&JSON.stringify(State.member||{})===access&&prcCanApprove();
  const $=sel=>ov.querySelector(sel),go=$('#prc-paid-go'),msg=$('#prc-paid-msg');
  const read=async()=>{const d=await api('/api/procurement/requests/'+encodeURIComponent(id));return d?.request;};
  const amount=v=>(typeof v==='number'||typeof v==='string'&&v.trim()!=='')&&Number.isFinite(Number(v));
  const usable=r=>r&&String(r.id)===String(id)&&r.status==='approved'&&amount(r.total_ngn)&&Array.isArray(r.lines);
  let request=null,snapshot=null,accounts=null,picked=null,busy=false,sent=false;
  const ready=()=>!busy&&!sent&&request&&Array.isArray(accounts)&&(accounts.length===0||picked!==null);
  const paint=()=>{
    go.disabled=!ready();
    $('[data-paid-summary]').innerHTML=request&&Array.isArray(accounts)?`<p>${esc(prcFmtNGN(Number(request.total_ngn)))} for “${esc(request.title||'Finance request')}”, ${picked!==null?`paid from ${esc(picked)}`:accounts.length?'choose the account it was paid from':'no account on file'}.</p>`:'';
  };
  const show=r=>{request=r;snapshot=prcDecisionSnapshot(r)+JSON.stringify(r.paid_transaction_ids??null);
    $('[data-paid-request]').innerHTML=`<div class="pd-pack-section"><b>${esc(r.title||'Finance request')}</b><p>${esc(prcFmtNGN(Number(r.total_ngn)))} · NGN · ${r.lines.length} line${r.lines.length===1?'':'s'}</p><p>Record ${esc(r.id)}</p></div>`;paint();};
  const loadAccounts=async()=>{
    const chips=$('#prc-acct-chips');accounts=null;picked=null;chips.innerHTML='<span class="prc-quiet">Loading accounts…</span>';paint();
    let d;try{d=await api('/api/accounting/accounts');}catch(_){d=null;}
    if(!same()){ov.remove();return;}
    if(!d||d.ok===false||!Array.isArray(d.accounts)||d.accounts.some(a=>!a||typeof a.name!=='string'||!a.name.trim())){
      chips.innerHTML='<span class="prc-quiet">The accounts did not load. This is not an empty list, and nothing can be recorded until they load.</span> <button class="bks-tool-btn" type="button" data-paid-accounts-retry>Try again</button>';
      chips.querySelector('[data-paid-accounts-retry]').addEventListener('click',loadAccounts);return;
    }
    accounts=d.accounts;
    chips.innerHTML=accounts.length?accounts.map((a,i)=>`<button class="prc-chip" type="button" data-acct-index="${i}">${esc(a.name)}${a.institution?' &middot; '+esc(a.institution):''}</button>`).join(''):'<span class="prc-quiet">No accounts on file — the payment is recorded without one.</span>';
    chips.querySelectorAll('[data-acct-index]').forEach(b=>b.addEventListener('click',()=>{
      if(busy||sent||!same())return;
      picked=accounts[Number(b.dataset.acctIndex)].name;
      chips.querySelectorAll('.prc-chip').forEach(x=>x.classList.toggle('on',x===b));paint();
    }));
    paint();
  };
  try{
    const r=await read();if(!same()){ov.remove();return;}
    if(!usable(r)){$('[data-paid-request]').textContent='This request is not approved for payment, or it could not be read. Nothing was recorded. Close and refresh.';return;}
    show(r);
  }catch(_){if(same())$('[data-paid-request]').textContent='The request could not be read. Nothing was recorded. Close and try again.';else ov.remove();return;}
  loadAccounts();
  const after=text=>{msg.textContent=text;};
  go.addEventListener('click', async () => {
    if(!ready()||!same())return;
    busy=true;paint();go.textContent='Recording…';
    try{
      const fresh=await read();if(!same()){ov.remove();return;}
      if(!usable(fresh)||prcDecisionSnapshot(fresh)+JSON.stringify(fresh.paid_transaction_ids??null)!==snapshot){
        busy=false;go.textContent='Record payment';
        if(usable(fresh)){show(fresh);after('The request changed while you were reviewing it. Check it again before recording.');}
        else{request=null;paint();after('This request is no longer approved for payment. Nothing was recorded. Close and refresh.');}
        return;
      }
    }catch(_){if(!same()){ov.remove();return;}busy=false;go.textContent='Record payment';paint();after('The request could not be re-read. Nothing was sent.');return;}
    sent=true;busy=false;paint();
    let r,err=null;
    try{r=await api(`/api/procurement/requests/${encodeURIComponent(id)}/paid`,{method:'POST',body:JSON.stringify({ account: picked })});}catch(e){err=e;}
    if(!same()){ov.remove();
      // Same person and workspace (a role or view change): say it may be recorded, never the result.
      if(State.jwt===token&&State.tenant?.id===tenant){showToast('A payment you sent may have been recorded. Check the request before recording it again.');document.dispatchEvent(new Event('eon:procurement-changed'));}
      return;}
    go.textContent='Record payment';
    const lines=Array.isArray(request.lines)?request.lines.length:0;
    const detail=err?.body?.detail,recorded=Array.isArray(detail?.recorded_lines)&&detail.recorded_lines.every(n=>Number.isInteger(n)&&n>=0&&n<lines)?detail.recorded_lines:null;
    const careful='Do not record this payment again until someone who runs the books has checked Book entries.';
    if(!err&&r?.ok===true&&r.status==='paid'&&Array.isArray(r.transactions)){
      ov.remove();showToast(`Payment recorded: ${r.transactions.length} expense${r.transactions.length===1?'':'s'} logged. Receipt evidence is a separate step.`);
      document.dispatchEvent(new Event('eon:procurement-changed'));prcHydrateMoneyStrip();return;
    }
    if(err?.status===400&&detail?.error==='payment_incomplete')after(`${recorded?(recorded.length?`EON confirmed ${recorded.length} of ${lines} lines as recorded; the others were not confirmed.`:'EON could not confirm any line as recorded.'):'Not every line was confirmed as recorded.'} The request stays approved. ${careful}`);
    else if(err?.status===400&&detail?.error==='payment_progress_not_saved')after(`Expenses may have been recorded${recorded?` for ${recorded.length} of ${lines} lines`:''}, but the request could not be updated, so it may still read as approved. ${careful}`);
    else if(err?.status===400&&typeof detail==='string')after(`Not recorded: ${/not.found/.test(detail)?'the request was not found.':/not approved/.test(detail)?'the request is no longer approved.':detail}`);
    else if(err?.status===403)after('Not recorded: you no longer have permission to record payments.');
    else{
      after(`EON gave no clear answer. The payment may or may not have been recorded. ${careful}`);
      msg.insertAdjacentHTML('beforeend',' <button class="bks-tool-btn" type="button" data-paid-readback>Read the request again</button>');
      msg.querySelector('[data-paid-readback]').addEventListener('click',async()=>{
        let f;try{f=await read();}catch(_){f=undefined;}
        if(!same()){ov.remove();return;}
        if(!f||String(f.id)!==String(id))after('The request could not be read. The payment may or may not have been recorded. '+careful);
        else if(f.status==='paid')after('The request now reads as paid. Close and refresh to see it.');
        else{
          // Name only a status finance_requests allows (mig 267); never claim approved for anything else.
          const now=new Map([['approved','still reads as approved'],['pending','now reads as waiting for approval'],['rejected','now reads as rejected'],['reconciled','now reads as reconciled']]).get(f.status);
          after(`${now?`The request ${now}.`:'EON could not tell what state the request is in.'} That does not prove nothing was recorded: expenses can be logged before the request is updated. ${careful}`);
        }
      });
    }
  });
}

function prcOpenReceiptUpload(id) {
  const ov = cswOverlay(`<div class="csw-sheet__h"><div class="csw-sheet__t">Upload receipt</div><button class="csw-x">&times;</button></div>
    <div class="prc-sheet-sub">The receipt stamps onto the logged transactions — no receipt means the input-VAT credit is forfeited.</div>
    <div class="prc-drop" id="prc-drop">
      <span id="prc-drop-label">Drop the receipt here or tap to choose</span>
      <input type="file" id="prc-receipt-file" accept="image/*,application/pdf"/>
    </div>
    <button class="csw-confirm" id="prc-receipt-go" type="button" disabled>Upload receipt</button>
    <div class="prc-quiet" id="prc-receipt-msg"></div>`);
  ov.querySelector('.csw-x').addEventListener('click', () => ov.remove());
  const fileInput = ov.querySelector('#prc-receipt-file');
  const drop = ov.querySelector('#prc-drop');
  const go = ov.querySelector('#prc-receipt-go');
  const sync = () => {
    const f = fileInput.files && fileInput.files[0];
    ov.querySelector('#prc-drop-label').textContent =
      f ? f.name : 'Drop the receipt here or tap to choose';
    go.disabled = !f;
  };
  fileInput.addEventListener('change', sync);
  drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('over'); });
  drop.addEventListener('dragleave', () => drop.classList.remove('over'));
  drop.addEventListener('drop', (e) => {
    e.preventDefault();
    drop.classList.remove('over');
    if (e.dataTransfer.files && e.dataTransfer.files.length) {
      fileInput.files = e.dataTransfer.files;
      sync();
    }
  });
  go.addEventListener('click', async () => {
    const f = fileInput.files && fileInput.files[0];
    if (!f) return;
    go.disabled = true; go.textContent = 'Uploading…';
    try {
      const fd = new FormData();
      fd.append('file', f);
      // FormData sets its own multipart boundary; do NOT set Content-Type
      // (same idiom as the bank-statement uploader).
      const headers = {};
      if (State.jwt) headers['Authorization'] = `Bearer ${State.jwt}`;
      if (State.tenant) headers['X-Tenant-Slug'] = State.tenant.slug;
      const resp = await fetch(API + `/api/procurement/requests/${encodeURIComponent(id)}/receipts`, {
        method: 'POST', body: fd, headers,
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data.ok === false) {
        throw new Error((typeof data.detail === 'string' && data.detail) || data.error || resp.statusText);
      }
      ov.remove();
      showToast(data.receipt_status === 'complete'
        ? 'Receipt attached — books closed for this request'
        : `Receipt attached (${data.receipt_status || 'partial'})`);
      prcHydrateMoneyStrip();
      document.dispatchEvent(new Event('eon:procurement-changed'));
    } catch (e) {
      const msg = ov.querySelector('#prc-receipt-msg');
      if (msg) msg.textContent = e.message || String(e);
      go.disabled = false; go.textContent = 'Upload receipt';
    }
  });
}

// ── Packaging panel (reconciliation + counts + SKU costs) ─────────

function prcStepperHTML(code, val, label) {
  return `<div class="prc-step" data-code="${esc(code)}">
    <span class="prc-step-l">${esc(label)}</span>
    <button class="prc-step-btn" data-d="-1" type="button" aria-label="Less">&minus;</button>
    <input class="prc-step-v mono-num" type="number" min="0" step="1" inputmode="numeric" value="${val}"/>
    <button class="prc-step-btn" data-d="1" type="button" aria-label="More">+</button>
  </div>`;
}

function prcBindSteppers(root) {
  root.querySelectorAll('.prc-step').forEach((st) => {
    st.querySelectorAll('.prc-step-btn').forEach((b) => b.addEventListener('click', () => {
      const inp = st.querySelector('.prc-step-v');
      inp.value = Math.max(0, (parseInt(inp.value, 10) || 0) + parseInt(b.dataset.d, 10));
      inp.dispatchEvent(new Event('input', { bubbles: true }));
    }));
  });
}

function prcReadSteppers(root, includeZeros) {
  const out = {};
  root.querySelectorAll('.prc-step').forEach((st) => {
    const v = parseInt(st.querySelector('.prc-step-v').value, 10) || 0;
    if (v > 0 || includeZeros) out[st.dataset.code] = v;
  });
  return out;
}

async function prcLoadPackagingPanel() {
  const body = document.getElementById('prc-pack-body');
  if (!body) return;
  body.innerHTML = '<div class="prc-quiet" style="padding:8px 2px">Loading packaging data&hellip;</div>';
  const [skus, locs] = await Promise.all([prcSkus(), prcLocations()]);
  body.innerHTML = `
    <div class="prc-pack-sec">
      <div class="prc-pack-h">Reconciliation &middot; this month</div>
      <div id="prc-recon"></div>
    </div>
    <div class="prc-pack-sec">
      <div class="prc-pack-h">Log a physical count</div>
      <div id="prc-count"></div>
    </div>
    <div class="prc-pack-sec">
      <div class="prc-pack-h">Bag costs</div>
      <div id="prc-skus"></div>
    </div>`;
  prcRenderRecon(locs);
  prcRenderCountForm(locs, skus);
  prcRenderSkuEditor(skus);
}

async function prcRenderRecon(locs) {
  const host = document.getElementById('prc-recon');
  if (!host) return;
  if (!locs.length) {
    host.innerHTML = '<div class="prc-quiet">No mapped stores yet.</div>';
    return;
  }
  host.innerHTML = bksSkel(3);
  const reports = await Promise.all(locs.map((l) =>
    api(`/api/procurement/packaging/reconciliation?location_id=${encodeURIComponent(l.id)}`)
      .catch(() => null)));
  host.innerHTML = locs.map((l, i) => {
    const rep = reports[i];
    if (!rep || !rep.ok) {
      return `<div class="prc-pack-store"><div class="prc-pack-store-h">${esc(l.name)}</div>
        <div class="prc-quiet">could not load reconciliation</div></div>`;
    }
    const rows = (rep.rows || []).filter((r) =>
      r.opening || r.purchased || r.used || r.counted);
    if (!rows.length) {
      return `<div class="prc-pack-store"><div class="prc-pack-store-h">${esc(l.name)}</div>
        <div class="prc-quiet">no packaging activity this period</div></div>`;
    }
    const note = rep.note ? `<div class="prc-quiet" style="margin-top:4px">${esc(rep.note)}</div>` : '';
    return `<div class="prc-pack-store">
      <div class="prc-pack-store-h">${esc(l.name)} <span class="prc-quiet">${esc(rep.period || '')}</span></div>
      <div class="prc-table-scroll"><table class="bks-table prc-recon-table"><thead><tr>
        <th>Bag</th><th class="bks-num">Opening</th><th class="bks-num">Purchased</th>
        <th class="bks-num">Used</th><th class="bks-num">Theoretical</th>
        <th class="bks-num">Counted</th><th class="bks-num">Var qty</th>
        <th class="bks-num">Var ₦</th><th class="bks-num">Var %</th>
      </tr></thead><tbody>
      ${rows.map((r) => {
        const hot = Math.abs(r.variance_pct || 0) > (rep.threshold_pct || 10)
          && Math.abs(r.variance_qty || 0) >= 5;
        const sev = !hot ? '' : (Math.abs(r.variance_ngn || 0) >= 100000 ? 'prc-row-red' : 'prc-row-amber');
        return `<tr class="${sev}">
          <td>${esc(PRC_BAG_LABELS[r.code] || r.code)}</td>
          <td class="bks-num">${r.opening || 0}</td>
          <td class="bks-num">${r.purchased || 0}</td>
          <td class="bks-num">${r.used || 0}</td>
          <td class="bks-num">${r.theoretical || 0}</td>
          <td class="bks-num">${r.counted || 0}</td>
          <td class="bks-num">${(r.variance_qty || 0) > 0 ? '+' : ''}${r.variance_qty || 0}</td>
          <td class="bks-num">${prcFmtNGN(r.variance_ngn)}</td>
          <td class="bks-num">${r.variance_pct || 0}%</td>
        </tr>`;
      }).join('')}</tbody></table></div>${note}</div>`;
  }).join('');
}

function prcRenderCountForm(locs, skus) {
  const host = document.getElementById('prc-count');
  if (!host) return;
  const codes = skus.length ? skus
    : Object.keys(PRC_BAG_LABELS).map((c) => ({ code: c, label: PRC_BAG_LABELS[c] }));
  host.innerHTML = `
    <select class="mtx-input" id="prc-count-loc" style="margin-bottom:8px">
      ${locs.map((l) => `<option value="${esc(l.id)}">${esc(l.name)}</option>`).join('')}
    </select>
    <div class="prc-steppers">
      ${codes.map((s) => prcStepperHTML(s.code, 0, s.label || PRC_BAG_LABELS[s.code] || s.code)).join('')}
    </div>
    <input class="mtx-input" id="prc-count-notes" placeholder="Notes (optional)" style="margin:8px 0"/>
    <button class="bks-tool-btn prc-act-ok" id="prc-count-go" type="button">Log count</button>
    <div class="prc-quiet" id="prc-count-msg"></div>`;
  prcBindSteppers(host);
  host.querySelector('#prc-count-go').addEventListener('click', async () => {
    const btn = host.querySelector('#prc-count-go');
    const msg = host.querySelector('#prc-count-msg');
    // Zeros are legitimate counts ("shelf is empty") — send every code.
    const counted = prcReadSteppers(host, true);
    btn.disabled = true; btn.textContent = 'Logging…';
    try {
      const r = await api('/api/procurement/packaging/counts', {
        method: 'POST',
        body: JSON.stringify({
          location_id: host.querySelector('#prc-count-loc').value || null,
          counted,
          notes: (host.querySelector('#prc-count-notes').value || '').trim() || null,
        }),
      });
      if (!r || !r.ok) throw new Error((r && r.error) || 'count failed');
      msg.textContent = 'Count logged — reconciliation uses it as the closing count for this period.';
      const locsNow = await prcLocations();
      prcRenderRecon(locsNow);
    } catch (e) {
      msg.textContent = e.message || String(e);
    } finally {
      btn.disabled = false; btn.textContent = 'Log count';
    }
  });
}

function prcRenderSkuEditor(skus) {
  const host = document.getElementById('prc-skus');
  if (!host) return;
  if (!skus.length) {
    host.innerHTML = '<div class="prc-quiet">could not load packaging SKUs</div>';
    return;
  }
  host.innerHTML = `
    <div class="prc-quiet" style="margin-bottom:6px">Unit costs price the bag variances — set the real purchase cost per bag (seeded at ₦0).</div>
    ${skus.map((s) => `
      <div class="prc-sku-row" data-code="${esc(s.code)}">
        <span class="prc-sku-l">${esc(s.label || s.code)} <span class="prc-quiet">fits ${s.items_capacity || 1}</span></span>
        <input class="mtx-input prc-sku-cost mono-num" type="number" min="0" step="any" inputmode="decimal" value="${Number(s.unit_cost_ngn) || 0}"/>
        <button class="bks-tool-btn prc-sku-save" type="button">Save</button>
      </div>`).join('')}
    <div class="prc-quiet" id="prc-sku-msg"></div>`;
  host.querySelectorAll('.prc-sku-save').forEach((b) => b.addEventListener('click', async () => {
    const row = b.closest('.prc-sku-row');
    const cost = parseFloat(row.querySelector('.prc-sku-cost').value) || 0;
    b.disabled = true; b.textContent = 'Saving…';
    try {
      const r = await api('/api/procurement/packaging/skus', {
        method: 'POST',
        body: JSON.stringify({ code: row.dataset.code, unit_cost_ngn: cost }),
      });
      if (!r || !r.ok) throw new Error((r && r.error) || 'save failed');
      _prcState.skus = null; // bust cache — reconciliation reprices from fresh costs
      b.textContent = 'Saved';
      setTimeout(() => { b.textContent = 'Save'; b.disabled = false; }, 1200);
    } catch (e) {
      const msg = document.getElementById('prc-sku-msg');
      if (msg) msg.textContent = e.message || String(e);
      b.textContent = 'Save'; b.disabled = false;
    }
  }));
}

// ── 3. Rep bag confirm (CS workspace, after an order is placed) ───
// One-tap bless of the computed bag split, or override with a REQUIRED
// reason. Dismissible — skipping leaves the row 'computed' (the nightly
// sweep records it anyway); no nagging, quiet auto-dismiss.

async function prcBagConfirm(orderId) {
  if (!orderId) return;
  let exp;
  try {
    exp = await api(`/api/procurement/packaging/expected/${encodeURIComponent(orderId)}`);
  } catch (_) { return; } // order not mirrored into eon_sync yet — sweep covers it
  if (!exp || !exp.ok) return;
  if (exp.usage && exp.usage.source && exp.usage.source !== 'computed') return; // already confirmed
  const expected = exp.expected || {};
  if (!Object.keys(expected).length) return;

  document.getElementById('prc-bagbar')?.remove();
  const bar = document.createElement('div');
  bar.id = 'prc-bagbar';
  bar.className = 'prc-bagbar';
  bar.innerHTML = `
    <div class="prc-bagbar-row">
      <span class="prc-bagbar-t">Packaging: ${prcBagSummary(expected)}</span>
      <button class="prc-bagbar-btn prc-bagbar-yes" id="prc-bag-confirm" type="button">Confirm</button>
      <button class="prc-bagbar-btn" id="prc-bag-change" type="button">Change</button>
      <button class="prc-bagbar-x" id="prc-bag-skip" type="button" aria-label="Skip">&times;</button>
    </div>
    <div class="prc-bagbar-edit" id="prc-bag-edit" hidden></div>`;
  document.body.appendChild(bar);
  const close = () => bar.remove();
  const timer = setTimeout(close, 60000);

  bar.querySelector('#prc-bag-skip').addEventListener('click', () => {
    clearTimeout(timer);
    close();
  });

  bar.querySelector('#prc-bag-confirm').addEventListener('click', async (e) => {
    clearTimeout(timer);
    e.target.disabled = true;
    e.target.textContent = 'Saving…';
    try {
      // Empty body = one-tap confirm of the computed split.
      const r = await api(`/api/procurement/packaging/usage/${encodeURIComponent(orderId)}`, {
        method: 'POST', body: '{}',
      });
      if (!r || !r.ok) throw new Error((r && r.error) || 'failed');
      close();
      cswToast('Packaging recorded');
    } catch (_) {
      close();
      cswToast('Could not record packaging', true);
    }
  });

  bar.querySelector('#prc-bag-change').addEventListener('click', () => {
    clearTimeout(timer);
    const edit = bar.querySelector('#prc-bag-edit');
    if (!edit.hidden) return;
    edit.hidden = false;
    const codes = Object.keys(PRC_BAG_LABELS);
    edit.innerHTML = `
      <div class="prc-steppers prc-steppers--bar">
        ${codes.map((c) => prcStepperHTML(c, expected[c] || 0, PRC_BAG_LABELS[c])).join('')}
      </div>
      <input class="mtx-input" id="prc-bag-reason" placeholder="Why is it different? (required)" style="margin:8px 0;width:100%"/>
      <div class="prc-bagbar-editrow">
        <button class="prc-bagbar-btn prc-bagbar-yes" id="prc-bag-save" type="button">Save packaging</button>
        <span class="prc-quiet" id="prc-bag-msg"></span>
      </div>`;
    prcBindSteppers(edit);
    edit.querySelector('#prc-bag-save').addEventListener('click', async (e2) => {
      const bags = prcReadSteppers(edit, false);
      const reason = (edit.querySelector('#prc-bag-reason').value || '').trim();
      const msg = edit.querySelector('#prc-bag-msg');
      const same = codes.every((c) => (bags[c] || 0) === (expected[c] || 0));
      if (!same && !reason) {
        // override_reason is REQUIRED when the bags differ from the
        // computed split — mirrors the backend guard in confirm_usage.
        msg.textContent = 'Reason required when changing the computed split.';
        return;
      }
      e2.target.disabled = true;
      e2.target.textContent = 'Saving…';
      try {
        const r = await api(`/api/procurement/packaging/usage/${encodeURIComponent(orderId)}`, {
          method: 'POST',
          body: JSON.stringify(same ? {} : { bags, override_reason: reason }),
        });
        if (!r || !r.ok) throw new Error((r && r.error) || 'failed');
        close();
        cswToast('Packaging recorded');
      } catch (err) {
        msg.textContent = err.message || String(err);
        e2.target.disabled = false;
        e2.target.textContent = 'Save packaging';
      }
    });
  });
}
// PROCUREMENT END (prc-*)


// ═══════════════════════════════════════════════════════════════════
// STAFF OS  (view key: 'staffos' · reachable at /staff or ?v=staffos)
// ───────────────────────────────────────────────────────────────────
// FAITHFUL PORT of docs/mockups/staff_os_v4_refined_2026_06_13.html
// <script>. The mockup renderer is reproduced LINE-FOR-LINE into
// sos-prefixed names (renderDay→sosRenderDay, blkStores→sosBlkStores,
// renderChat→sosRenderChat, openTask→sosOpenTask,
// renderScore→sosRenderScore, renderTeam→sosRenderTeam,
// buildTabbar→sosBuildTabbar, switchTab→sosSwitchTab,
// renderPill→sosRenderPill, openRP→sosOpenRP, setRole→sosSetRole, qa→sosQa).
// Mechanical transforms ONLY (contract BUILDER B #2):
//   (a) fn + helper names sos-prefixed (sosEsc, sosId, SOS_G, SOS_ICON,
//       SOS_DCOL, SOS_DEPT_ORDER) to avoid app.js global collisions;
//   (b) all rendered output wrapped inside the .sos-root container — the
//       view target the existing dispatch uses (renderStaffOs/bindStaffOs);
//   (c) the data swap: ROLES/REPBOARD/CUR → sosState{roster,cur,payload,tab},
//       fed by GET /api/staffos/roster + GET /api/staffos/role/{staff_id}.
//   (d) chat branch: payload.is_cs → REAL global inbox (renderCsInbox/
//       bindCsInbox); everyone else → the scw-* scoped replica.
//   (e) no emoji (Daniel rule): the mockup's check / bullet / cross / tick /
//       send glyphs become inline SVG (sosCheck / sosBullet / sosSendIcon).
// Same markup, same classes (scw-* for chat), same layout as the mockup.
// ═══════════════════════════════════════════════════════════════════

// runtime state — replaces the mockup's hardcoded ROLES / REPBOARD / CUR.
// tab default 'chat' (2026-08-10 operator directive: the chat is the main —
// a staffer opens onto their conversations, Day is one tap away).
const sosState = { roster: [], cur: null, payload: null, tab: 'chat', att: null, attBusy: false, req: null, reqKind: 'off_day' };
const SOS_LS_KEY = 'eon_staffos_staff_id';
// Last good role payload per staff id — paints instantly on open while the
// fresh one loads (the payload is seconds-slow by nature: many fan-outs).
const SOS_LS_PAYLOAD = 'eon_sos_payload_';
// a cached Day older than this is not painted at all (2026-09-02)
const SOS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
// the blocks a cached paint must not carry: money and the month header
// (payslip), the pool (bonus); and the one KPI a rep never reads (rank)
const SOS_CACHE_DROP_BLOCKS = { payslip: 1, bonus: 1 };
function sosStripCachedPayload(p) {
  try {
    const q = JSON.parse(JSON.stringify(p || {}));
    if (Array.isArray(q.kpis)) q.kpis = q.kpis.filter(k => !/^team rank/i.test(String(k && k[0] || '')));
    if (Array.isArray(q.day)) q.day = q.day.filter(b => !SOS_CACHE_DROP_BLOCKS[b && b[0]]);
    if (q.score && typeof q.score === 'object') { delete q.score.bonus; delete q.score.board; q.score.rank = null; }
    q._cached_paint = true;
    return q;
  } catch (_) { return p; }
}

// Peer props (Sprint 2 creative layer): one delegated handler — team rows
// render in several views. Server enforces self-block + daily caps; the
// giver is ALWAYS the logged-in member (never the selected role).
document.addEventListener('click', async (ev) => {
  // 2026-08-01: scoped to .sos-prop — `data-prop` is ALSO the id carrier on
  // Decisions-board proposal cards (.ccp/.idc, "prop" = proposal), and this
  // document-level catch-all was swallowing every click inside those cards:
  // the owner's Approve tap posted the CREATIVE id as a staff id and toasted
  // "Log in as staff to give props" while the decision never fired. Props
  // originate from the .sos-prop button only.
  const btn = ev.target && ev.target.closest && ev.target.closest('.sos-prop[data-prop]');
  if (!btn) return;
  ev.stopPropagation();
  if (btn.disabled) return;
  btn.disabled = true;
  try {
    const r = await api('/api/staffos/props', {
      method: 'POST',
      body: JSON.stringify({ to_staff_id: btn.getAttribute('data-prop') }),
    });
    if (r && r.ok) { sosToast(`+1 to ${r.to} — props sent`); btn.textContent = 'sent'; }
    else {
      const msg = { self_prop_blocked: 'Not to yourself', daily_cap_reached: 'Daily props cap reached',
                    already_propped_today: 'Already propped them today', staff_not_found: 'Log in as staff to give props' }[r && r.error] || 'Could not send props';
      sosToast(msg); btn.disabled = false;
    }
  } catch (e) { sosToast('Could not send props'); btn.disabled = false; }
});

const sosId = (id) => document.getElementById(id);
// No-emoji law gate (2026-08-30, build contract §4.5 + audit PRF-3): emoji
// affordances in the mounted CS workspace survive ONLY for the CS unit in a
// PIN session (their composer writes customer WhatsApp messages). Everyone
// else's chat is a staff surface. Owner/console sessions are unaffected.
function sosEmojiAllowed() {
  if (typeof State === 'undefined' || !State.isStaff) return true;
  const p = (typeof sosState !== 'undefined' && sosState.payload) || {};
  return !!p.is_cs;
}
function sosEsc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
// For a value inside an inline handler's single-quoted JS string literal:
// sosEsc alone turns ' into &#39;, which the HTML parser decodes BACK to a
// bare quote before the JS is parsed — so "CRUX Men's Tee" terminated the
// string and the button silently died (vet 2026-08-31, S12). Backslash-
// escape for JS FIRST, then sosEsc for the attribute; the parser decodes
// the entity to a quote that now arrives escaped.
function sosJs(s) { return String(s == null ? '' : s).replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\r/g, '\\r').replace(/\n/g, '\\n'); }
// Team-chat message timestamps arrive as ISO `created_at` from the backend
// (staff_chat.get_thread). Render a short HH:MM clock in the viewer's local
// (Lagos) time; tolerate already-short strings and bad input.
function sosFmtClock(v) {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{1,2}:\d{2}/.test(v)) return v;
  try { const d = new Date(v); if (isNaN(d.getTime())) return ''; return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); }
  catch (_) { return ''; }
}

// mockup's DCOL / G / DEPT_ORDER, sos-prefixed.
const SOS_DCOL = { 'Sales':'var(--blue)','Inventory':'var(--amber)','Logistics':'var(--mint)','Customer Service':'var(--vio)','Operations':'var(--lime)','Marketing':'var(--red)' };
// Design pass 01 (2026-07-11): gradients read the .sos-root tokens (which
// consume the shared --eon-* ink mode) instead of re-hardcoding the old
// drifted hexes; only moss/sage keep unique avatar-identity greens.
const SOS_G = {
  blue:'linear-gradient(135deg,var(--blue),var(--mint))', amber:'linear-gradient(135deg,var(--amber),var(--lime))',
  mint:'linear-gradient(135deg,var(--mint),var(--blue))', vio:'linear-gradient(135deg,var(--vio),var(--blue))',
  lime:'linear-gradient(135deg,var(--lime),var(--amber))', red:'linear-gradient(135deg,var(--red),var(--vio))',
  teal:'linear-gradient(135deg,var(--mint),var(--lime))', sky:'linear-gradient(135deg,var(--blue),var(--vio))',
  sun:'linear-gradient(135deg,var(--amber),var(--red))', moss:'linear-gradient(135deg,#9BBF3B,var(--mint))',
  rose:'linear-gradient(135deg,var(--red),var(--amber))', iris:'linear-gradient(135deg,var(--vio),var(--red))',
  sage:'linear-gradient(135deg,#8FD98A,var(--blue))',
};
const SOS_DEPT_ORDER = ['Sales','Inventory','Logistics','Customer Service','Operations','Marketing'];

// tab-bar stroke icons (match .tabbar button svg) — verbatim from the mockup's ICON.
const SOS_ICON = {
  day:'<svg viewBox="0 0 24 24"><path d="M3 11.5 12 4l9 7.5"/><path d="M5 10v9h14v-9"/></svg>',
  chat:'<svg viewBox="0 0 24 24"><path d="M21 12a8 8 0 0 1-11.3 7.3L4 21l1.7-5.7A8 8 0 1 1 21 12Z"/></svg>',
  tasks:'<svg viewBox="0 0 24 24"><path d="M9 6h11"/><path d="M9 12h11"/><path d="M9 18h11"/><path d="m4 6 1 1 2-2"/><path d="m4 12 1 1 2-2"/><path d="m4 18 1 1 2-2"/></svg>',
  team:'<svg viewBox="0 0 24 24"><circle cx="9" cy="8" r="3.2"/><path d="M3.5 19a5.5 5.5 0 0 1 11 0"/><path d="M16 6.2a3 3 0 0 1 0 5.6"/><path d="M17 13.5a5.2 5.2 0 0 1 3.5 5"/></svg>',
  score:'<svg viewBox="0 0 24 24"><path d="M8 4h8v4a4 4 0 0 1-8 0Z"/><path d="M16 5h3v2a3 3 0 0 1-3 3"/><path d="M8 5H5v2a3 3 0 0 0 3 3"/><path d="M10 12.5v3"/><path d="M14 12.5v3"/><path d="M8 20h8"/><path d="M9 15.5h6l1 4.5H8Z"/></svg>',
};

// no-emoji helpers (Daniel rule): the mockup's check / bullet / double-tick /
// send glyphs reproduced as inline SVG (never ship pictographic dingbats).
function sosCheck() { return '<svg viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>'; }
function sosBullet() { return '<svg viewBox="0 0 24 24" style="width:9px;height:9px;fill:currentColor;stroke:none"><circle cx="12" cy="12" r="6"/></svg>'; }
function sosTickSingle() { return '<svg viewBox="0 0 20 16"><path d="M3 9l3.5 3.5L15 4"/></svg>'; }
function sosTickDouble() { return '<svg viewBox="0 0 26 16"><path d="M2 9l3.5 3.5L14 4"/><path d="M9 12.5L18 4"/></svg>'; }
function sosSendIcon() { return '<svg viewBox="0 0 24 24"><path d="M5 12h13"/><path d="M13 6l6 6-6 6"/></svg>'; }
function sosPinIcon() { return '<svg viewBox="0 0 24 24"><path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11z"/><circle cx="12" cy="10" r="2.5"/></svg>'; }
function sosShiftIcon() { return '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>'; }

// current-role accessor — replaces the mockup's `r=()=>ROLES[CUR]`.
const sosR = () => sosState.payload || {};

// ── view shell (renders into the dispatch's #app; keeps render/bind names) ──
function renderStaffOs() {
  if(eonWorkroomEnabled())return eonWorkroomShell('staff');
  return `
  <div class="sos-root" id="sos-root">
    ${eonWorkroomReturn()}
    ${cg.active() ? cg.frame('staff') : cg.entry('staffos')}
    <canvas class="sos-horizon" id="sos-horizon" aria-hidden="true"></canvas>
    <div class="phone" id="sos-phone">
      <canvas class="sos-heroband" id="sos-heroband" aria-hidden="true"></canvas>
      <div class="top">
        <div class="wm">CRU<i>X</i> · EON</div>
        <div class="rolepill" id="sos-rolepill"></div>
      </div>
      <div id="ascent-staff-work"></div>
      <div class="view on" id="sos-v-day"><div class="sos-loading">Loading Staff OS…</div></div>
      <div class="view" id="sos-v-chat"></div>
      <div class="view" id="sos-v-tasks"></div>
      <div class="view" id="sos-v-team"></div>
      <div class="view" id="sos-v-score"></div>
      ${cg.active() ? '<div class="view" id="sos-v-sales"></div><div class="view" id="sos-v-finance"></div>' : ''}
      <div class="tabbar" id="sos-tabbar"></div>
    </div>
    <div class="rp-scrim" id="sos-rp-scrim"></div>
    <div class="rp" id="sos-rp"></div>
    <div class="sheet" id="sos-sheet"></div>
  </div>`;
}

// A staff PIN session IS one person — decode the token's sub so Staff OS
// lands on the logged-in staffer, never the roster-order fallback. (Fresh
// staff devices booted onto the FIRST MANAGER; the server clamp saved the
// data but the client kept the wrong person's id for Mine-filters etc.)
function sosMyStaffId() {
  try {
    if (!State.isStaff || !State.jwt) return null;
    const b64 = State.jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(b64));
    return claims && claims.sub ? String(claims.sub) : null;
  } catch (_) { return null; }
}

// Staff OS was a frozen snapshot: ONE role-payload fetch fed all five tabs
// with no polling, no foreground refresh and badges built exactly once, so a
// rep who opened it at 09:00 still saw 09:00 at noon (2026-07-25 vet).
// Refresh when the app comes back to the foreground, rate-limited so tab
// flipping can't hammer the payload.
// Keyboard operability (RS-05): rows painted as [role=button] responded to
// taps only. One delegated handler makes Enter/Space activate any of them,
// current and future, without touching each render site.
function sosBindKeyboardRows() {
  if (window._sosKeyBound) return;
  window._sosKeyBound = true;
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    // The key's real target (eonKeyTarget): a key typed in a box inside a
    // shadow root arrives as the host, so read the box itself; such a key
    // is never taken for a row's Enter or Space.
    const el = eonKeyTarget(e);
    if (!el || !el.closest) return;
    const row = el.closest('.sos-root [role="button"], .sos-root .task, .sos-root .chk');
    if (!row || row.tagName === 'BUTTON' || row.tagName === 'A') return;   // native already works
    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable) return;
    e.preventDefault();
    row.click();
  });
}

function sosStaffNotifyTap() {
  if (State.view === 'staffos' && !State.impersonating && eonWorkroomSession()) eonNotifyAsk();
}

async function sosWorkroomRefresh(visit = eonWorkroomVisit, initial = false) {
  const key = visit?.contextKey();
  const current = () => !!key && visit === eonWorkroomVisit && visit.contextKey() === key
    && State.view === 'staffos' && !document.hidden;
  if (!current()) return;
  if (window._sosWorkroomPending?.visit === visit) return window._sosWorkroomPending.promise;
  window._sosLastFetch = Date.now();
  const job = {visit};
  window._sosWorkroomPending = job;
  job.promise = (async () => {
    let changed = false;
    const identity = eonWorkroomSession(), sid = State.user?.staff_id;
    // Attendance status decides shift vs geo. A role preview or linked web
    // account never borrows a PIN staffer's presence; there is no GPS prompt.
    const presenceKey = identity && JSON.stringify([identity.tenantId,identity.actorId,identity.authSession]);
    const recent = window._sosPresence;
    if (State.isStaff === true && identity && !identity.impersonated && sid === identity.actorId
        && !(recent?.key === presenceKey && Date.now() - recent.at < 45000)) {
      window._sosPresence = {key:presenceKey,at:Date.now()};
      try {
        const status = await api('/api/attendance/status?staff_id=' + encodeURIComponent(sid), {_isCurrent:current});
        if (current() && status?.ok === true && status.mode === 'shift') {
          const result = await api('/api/attendance/heartbeat', {method:'POST',body:JSON.stringify({staff_id:sid}),_retried:true,_isCurrent:current});
          changed = result?.ok === true;
        }
      } catch (_) { /* unknown presence stays unknown; the records read remains available */ }
    }
    if (current() && (!initial || changed)) await visit.refresh();
  })();
  try { await job.promise; } finally { if (window._sosWorkroomPending === job) window._sosWorkroomPending = null; }
}

function sosBindForegroundRefresh() {
  if (window._sosFgBound) return;
  window._sosFgBound = true;
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    if (typeof State !== 'undefined' && State.view !== 'staffos') return;
    const last = window._sosLastFetch || 0;
    if (Date.now() - last < 45000) return;      // min interval
    if (eonWorkroomEnabled()) return sosWorkroomRefresh();
    if (sosState && sosState.cur) return sosSetRole(sosState.cur, true);
  });
}

async function bindStaffOs() {
  sosBindForegroundRefresh();
  // Shared session setup precedes either surface. The same named listener is
  // deduplicated by EventTarget, and permission is requested only on a tap.
  if (!State.impersonating && eonWorkroomSession()) {
    document.body.addEventListener('click', sosStaffNotifyTap, { once: true });
    if (!window._sosPushPending) {
      const sync = eonPushSync();
      window._sosPushPending = Promise.resolve(sync).finally(() => { window._sosPushPending = null; });
    }
  }
  if(eonWorkroomEnabled()) {
    const started = eonWorkroomBind('staff'), visit = eonWorkroomVisit;
    const result = await started;
    await sosWorkroomRefresh(visit, true);
    return result;
  }
  const chatEntry=eonWorkroomChatEntry;
  const rosterRoot=sosId('sos-root'), rosterUser=State.user, rosterTenant=State.tenant, rosterMember=State.member;
  const rosterCurrent=()=>rosterRoot?.isConnected && rosterUser===State.user && rosterTenant===State.tenant && rosterMember===State.member && !eonWorkroomEnabled();
  const chatCurrent=()=>{
    const session=eonWorkroomSession();
    return rosterCurrent() && State.view==='staffos' && eonWorkroomChatEntry===chatEntry && session && !session.impersonated
      && chatEntry?.session===JSON.stringify([session.tenantId,session.actorId,session.authSession,session.access]);
  };
  sosBindKeyboardRows();
  sosId('sos-rolepill')?.addEventListener('click', sosOpenRP);
  sosId('sos-rp-scrim')?.addEventListener('click', sosCloseRP);
  sosId('sos-sheet')?.addEventListener('click', (e) => { if (e.target.id === 'sos-sheet') sosCloseSheet(); });
  // Staff land HERE, not the CS desk — ask for notification permission on
  // their first tap (browser rule: must ride a gesture) and sync the push
  // subscription so a locked phone buzzes on customer messages + task pings.
  // Notification setup is shared above with the Workroom surface.

  if(chatEntry?.mode==='owner'){
    const baseRole=String(State.member?.base_role||State.member?.role||'').toLowerCase();
    if(State.isStaff || baseRole!=='owner' || !chatCurrent()){
      sosId('sos-v-day').innerHTML='<div class="sos-error">Your chat workspace could not be opened. Return to Workroom and refresh your records.</div>';
      return;
    }
    // Open the authenticated full desk as owner. No staff preview id reaches
    // the server, so this path cannot fall through to another person's role.
    sosState.cur=null;
    sosState.payload={owner_desk:true,full:'Owner',role:'Owner',chat_queue:'',email:null};
    sosSwitchTab('chat');
    return;
  }

  // 1) load the roster (server-authoritative matrix + dept grouping).
  try {
    const res = await api('/api/staffos/roster');
    if(!rosterCurrent())return;
    sosState.roster = (res && res.roster) || [];
  } catch (e) {
    if(!rosterCurrent())return;
    sosState.roster = [];
    sosId('sos-v-day').innerHTML = `<div class="sos-error"><b>Staff OS roster unavailable.</b><br>${sosEsc(e.message || 'Try again shortly.')}</div>`;
    return;
  }
  if (!sosState.roster.length) {
    sosId('sos-v-day').innerHTML = `<div class="sos-empty">No active staff on the roster yet.</div>`;
    return;
  }
  // 2) default: OWN row for a staff session; else last-selected (localStorage)
  //    → first manager → first row (the owner's role-switcher default).
  let pick = null;
  const mine = sosMyStaffId();
  if (mine) pick = sosState.roster.find((p) => p.staff_id === mine) || null;
  if (!pick) {
    try {
      const saved = localStorage.getItem(SOS_LS_KEY);
      if (saved) pick = sosState.roster.find((p) => p.staff_id === saved) || null;
    } catch (_) {}
  }
  if (!pick) pick = sosState.roster.find((p) => p.is_manager) || sosState.roster[0];
  if(chatEntry){
    // Explicit Chat entry must not fall through to a different roster row.
    pick=sosState.roster.find(p=>p.staff_id===chatEntry.staffId);
    if(!pick){sosId('sos-v-day').innerHTML='<div class="sos-error">Your chat workspace could not be opened. Return to Workroom and refresh your records.</div>';return;}
    if(!chatCurrent())return;
    const loaded=await sosSetRole(pick.staff_id,false,chatCurrent);
    if(loaded && chatCurrent())sosSwitchTab('chat');
    return;
  }
  await sosSetRole(pick.staff_id);
}

// ════════════════════════ DAY VIEW ═══════════════════════════════
function sosRingSVG(pct) {
  if (pct == null || typeof pct !== 'number' || !Number.isFinite(pct)) {
    return '<div class="ring2" role="status"><div class="mid"><b>—</b><span>Unavailable</span></div></div>';
  }
  const v = Math.max(0, Math.min(100, pct));
  // r=31 in a 70-unit viewBox: r + stroke-width/2 (8/2=4) = 35 = viewBox
  // half — the stroke's outer edge lands exactly on the edge, uncut. At the
  // old r=33 the arc needed 37 and the top of the lime ring was clipped
  // flat (operator report 2026-07-23). Dasharray 2π·31 ≈ 195 must match
  // .ring2 .p in staff_os.css (stroke-dasharray + sos-ringdraw keyframe).
  const off = 195 * (1 - v / 100);
  return `<div class="ring2"><svg viewBox="0 0 70 70" width="86" height="86">`
    + `<circle class="c" cx="35" cy="35" r="31"/>`
    + `<circle class="p" cx="35" cy="35" r="31" style="stroke-dashoffset:${off}"/></svg>`
    + `<div class="mid"><b>${v}%</b><span>day ring</span></div></div>`;
}
function sosDeptDot(dept) { return `<span class="dot" style="background:${SOS_DCOL[dept] || 'var(--mute)'}"></span>`; }

// ══════════════════════ B3 ATTENDANCE (check-in) ════════════════
// Renders a check-in block at the TOP of the Day view, branching on the
// LOCKED status shape's `mode` (geo|shift) — NOT on role. The server is
// authoritative: the client never decides a verdict, it only reports
// lat/lng/accuracy (geo) or pings a heartbeat (shift).
function sosAttFetch() {
  // Pull /api/attendance/status for the current staff, then repaint Day.
  const sid = sosState.cur;
  if (!sid) { sosState.att = null; return Promise.resolve(); }
  return api('/api/attendance/status?staff_id=' + encodeURIComponent(sid))
    .then((res) => { sosState.att = (res && res.ok) ? res : null; })
    .catch(() => { sosState.att = null; })
    .then(() => {
      // Only repaint if we're still on this staff + Day is mounted.
      if (sosState.cur === sid && sosId('sos-att-slot')) sosRenderAtt();
      // Shift mode: ping a presence heartbeat on load (no geolocation) — but
      // ONLY when the logged-in user is viewing their OWN Day page. Staff OS is
      // an owner-driven role switcher (staff_os.py §"no logins"); without this
      // guard, the owner opening another staffer's page POSTed a heartbeat for
      // THEM, falsely flipping the subject to "On shift · active" (Chisom bug,
      // 2026-06-19). selfStaffId is set on the viewer's own row when per-staff
      // SSO lands; until then the owner is never the subject, so this correctly
      // stops auto-marking browsed staff as present.
      const a = sosState.att;
      // Identity fix (2026-08-30): MemberContext.to_dict() never carried
      // staff_id/user_id, and sosState.selfStaffId was never assigned, so
      // selfId was ALWAYS null and the shift-mode heartbeat could never
      // fire — every desk staffer read "On shift · idle" forever. The real
      // PIN-session identity lives on State.user.staff_id (set at boot).
      // The Chisom guard does NOT survive on its own here, and an earlier
      // version of this comment wrongly said it did. On a "View as"
      // session /api/staff-login/me returns the IMPERSONATED staffer's
      // staff_id and app.js:836 sets State.user.staff_id from it — so the
      // owner's browser DOES match sid, and tapping "View as Chisom"
      // would POST /api/attendance/heartbeat, insert an attendance_events
      // row for Chisom, and show her manager "On shift · active" while she
      // is nowhere near the floor and touched nothing. That is precisely
      // the bug the original guard existed to stop; making the identity
      // work again re-opened it. Impersonation is view-only everywhere
      // else (app.js:302, 516) and must be here too.
      // NOTE: the server does not filter impersonated sessions either
      // (attendance.heartbeat inserts on the staff_id it is given). This
      // restores the client invariant this branch broke; a server-side
      // guard is a separate change and is reported on the PR.
      const selfId = (typeof State !== 'undefined'
        && ((State.user && State.user.staff_id)
            || (State.member && (State.member.staff_id || State.member.user_id))))
        || sosState.selfStaffId || null;
      const impersonating = typeof State !== 'undefined' && !!State.impersonating;
      if (a && a.mode === 'shift' && selfId && !impersonating
          && String(selfId) === String(sid)) sosAttHeartbeat();
    });
}

function sosRenderAtt() {
  const slot = sosId('sos-att-slot');
  if (slot) slot.innerHTML = sosAttBlock();
  // The async landing point IS the entrance — draw the ring here, not only
  // in sosRenderDay (which usually paints before the fetch resolves).
  sosAttMarkSeen();
}

function sosAttBlock() {
  const a = sosState.att;
  if (!a) return '';  // honest empty — no attendance for this staff
  return a.mode === 'shift' ? sosAttShiftBlock(a) : sosAttGeoBlock(a);
}

// Attendance hero ring (design pass 03): the state icon carries a ring that
// draws on entry (draw verb — dashoffset); its color rides the geofence
// state (mint in-fence / amber drifting / red missed — kill semantics).
function sosAttIc(icon) {
  return `<span class="att-ic"><svg class="att-ring" viewBox="0 0 46 46" aria-hidden="true">`
    + `<circle class="att-ring__tr" cx="23" cy="23" r="19"/>`
    + `<circle class="att-ring__pr" cx="23" cy="23" r="19"/></svg>${icon}</span>`;
}

// The ring draws once per session — later re-renders (att polls, Day tabs)
// mount with the ring already landed (.att-seen baked into the markup).
function sosAttSeenCls() { return sosState._attSeen ? ' att-seen' : ''; }
function sosAttMarkSeen() {
  if (sosState._attSeen) return;
  if (cg.active()) {
    const els = document.querySelectorAll('.sos-att');
    if (els.length) { els.forEach(el => el.classList.add('att-seen')); sosState._attSeen = true; }
    return;
  }
  requestAnimationFrame(() => requestAnimationFrame(() => {
    // Only stamp the ledger when a card is actually mounted — the att block
    // lands async (sosAttFetch), and marking on the empty slot burned the
    // one-shot before the ring existed (audit 2026-07-12: ring never drew).
    const els = document.querySelectorAll('.sos-att');
    if (!els.length) return;
    els.forEach(el => el.classList.add('att-seen'));
    sosState._attSeen = true;
  }));
}

function sosFmtTime(iso) {
  if (!iso) return '';
  try { const dt = new Date(iso); return dt.toTimeString().slice(0, 5); }
  catch (_) { return ''; }
}

// geo mode: store/warehouse staff. "Check in" -> geolocation -> server.
function sosAttGeoBlock(a) {
  const loc = a.location || {};
  const t = a.today || {};
  const learning = a.learning_mode;
  let body = '';
  let chip = '';
  let pinAff = '';
  const owner = !!(sosR().can_set_pin);

  if (t.checked_in) {
    // Already checked in today — show the server's verdict + time.
    const tm = sosFmtTime(t.at);
    const dist = (t.distance_m != null) ? Math.round(t.distance_m) : null;
    // NAME THE STORE THE DISTANCE BELONGS TO (2026-08-20). `loc` is the
    // HOME store — the pin this device checks in against next. But with
    // location change on, today's check-in may be recorded at ANOTHER
    // store, and t.distance_m is measured from THAT pin. Printing the
    // home name beside it read "Palms · 12m from pin" for a rep standing
    // at Chevron, 5km from Palms. status().today now carries the real
    // store; fall back to the home name for legacy rows.
    // When the check-in is AWAY from home, the home name is never a valid
    // stand-in: status() deliberately returns location_name=null if the
    // away store can't be read back, and falling through to loc.name would
    // print "Palms · 12m from pin (home: Palms)" for a rep 5km from Palms —
    // the exact false label this change exists to remove. Away with no name
    // = no name (verifier pass 4, 2026-08-20).
    const ciName = t.away_from_home
      ? (t.location_name || '')
      : (t.location_name || loc.name || '');
    const homeTail = (t.away_from_home && loc.name && ciName)
      ? ` (home: ${sosEsc(loc.name)})` : '';
    if (t.verdict === 'verified') {
      body = `<div class="att-state ok">${sosAttIc(sosCheck())}`
        + `<div><b>Store opened — geo-verified${tm ? ' ' + tm : ''}</b>`
        + `<span>${sosEsc(ciName)}${dist != null ? ' · ' + dist + 'm from pin' : ''}${homeTail}</span></div></div>`;
      // Chip only when points were actually awarded — status.today now
      // carries the real number (0 for a late check-in). The old `: 10`
      // fallback showed "+10 pts" to staff who earned nothing.
      if (t.method !== 'override' && t.points_awarded > 0) chip = `<span class="att-pts">+${t.points_awarded} pts</span>`;
    } else if (t.verdict === 'soft') {
      body = `<div class="att-state soft">${sosAttIc(sosPinIcon())}`
        + `<div><b>Checked in — soft${tm ? ' ' + tm : ''}</b>`
        + `<span>${dist != null ? dist + 'm out' : 'location imprecise'}`
        + `${ciName ? ' · ' + sosEsc(ciName) : ''}${homeTail} · counts in learning mode</span></div></div>`;
    } else {
      body = `<div class="att-state bad">${sosAttIc(sosPinIcon())}`
        + `<div><b>Too far to verify${tm ? ' ' + tm : ''}</b>`
        + `<span>${dist != null ? dist + 'm from ' + sosEsc(ciName || (t.away_from_home ? 'the store you checked in at' : 'store')) : 'outside the fence'}</span></div></div>`;
    }
    // Clock-out (staff feedback 2026-07-24: "no option to clock out
    // which records work hours"). Only after a check-in — hours need a
    // start edge. Multiple clock-outs allowed; last one wins.
    const busyOut = sosState.attBusy;
    if (t.checked_out_at) {
      const wm = t.worked_minutes;
      const wl = (wm != null) ? `${Math.floor(wm / 60)}h ${String(wm % 60).padStart(2, '0')}m` : '';
      body += `<div class="att-out done"><b>Clocked out ${sosFmtTime(t.checked_out_at)}</b>`
        + `${wl ? `<span> · worked ${wl} today</span>` : ''}`
        + `<button class="att-out-again" ${busyOut ? 'disabled' : ''} onclick="sosAttCheckout()">${busyOut ? '…' : 'Clock out again'}</button></div>`;
    } else {
      body += `<div class="att-out"><button class="att-btn ghost" ${busyOut ? 'disabled' : ''} onclick="sosAttCheckout()">`
        + `${busyOut ? 'Clocking out…' : 'Clock out'}</button>`
        + `<span>records your hours for today</span></div>`;
    }
  } else {
    // Not yet checked in — the action button.
    const busy = sosState.attBusy;
    // Attempts that did not land today (mig 681) — say what the device
    // actually did, in the rep's own words, so "it doesn't work" has a
    // reason: too far (with the distance), or no position from the phone.
    let attemptLine = '';
    const at = t.attempts || {};
    if (at.count > 0 && at.last) {
      const k = at.last.kind;
      const d = (at.last.distance_m != null) ? Math.round(at.last.distance_m) : null;
      const why = k === 'rejected'
        ? ('too far' + (d != null ? ' · ' + (d >= 1000 ? (d / 1000).toFixed(1) + ' km' : d + ' m') + ' from the pin' : ''))
        : k === 'geo_denied' ? 'phone refused Location — allow it for this site in your browser settings'
        : k === 'geo_timeout' ? 'no position from the phone — turn Location on, try near a window'
        : 'phone gave no position';
      attemptLine = `<div class="att-meta att-attempts" style="margin-top:6px;"><span>${at.count} ${at.count === 1 ? 'try' : 'tries'} today · ${sosEsc(why)}</span></div>`;
    }
    body = `<div class="att-cta"><button class="att-btn ${busy ? 'busy' : ''}" ${busy ? 'disabled' : ''} onclick="sosAttCheckin()">`
      + `<span class="att-ic">${sosPinIcon()}</span>${busy ? 'Checking in…' : 'Check in'}</button>`
      + `<div class="att-meta"><b>${sosEsc(loc.name || 'Your store')}</b>`
      + `<span>${learning ? 'learning mode · soft pins count' : 'geo-verified at the pin'}</span></div></div>`
      + attemptLine;
  }
  // Owner/admin: drop a precise pin when the seeded pin is still a guess.
  if (owner && loc.pin_set === false) {
    pinAff = `<div class="att-pin"><button onclick="sosAttSetPin()">${sosPinIcon()}Set precise pin</button>`
      + `<span>pin is a placeholder — drop it from inside the store</span></div>`;
  }
  return `<div class="card sos-att${sosAttSeenCls()}">`
    + `<div class="att-head"><span class="att-tag">CHECK-IN</span>${chip}</div>`
    + body + pinAff + `</div>`;
}

// shift mode: Online/HQ staff. Presence only — never geolocation (privacy).
function sosAttShiftBlock(a) {
  const onShift = !!a.on_shift;
  const active = !!a.active;
  const win = a.shift_window || a.window || '';
  const state = !onShift ? 'off' : (active ? 'on' : 'idle');
  const label = !onShift
    ? 'Off shift'
    : (active ? 'On shift · active' : 'On shift · idle');
  const sub = win ? sosEsc(win) : (onShift ? 'presence tracked this hour' : 'next window pending');
  return `<div class="card sos-att${sosAttSeenCls()}">`
    + `<div class="att-head"><span class="att-tag">SHIFT</span></div>`
    + `<div class="att-state ${state === 'on' ? 'ok' : state === 'idle' ? 'soft' : 'off'}">`
    + sosAttIc(sosShiftIcon())
    + `<div><b>${sosEsc(label)}</b><span>${sub}</span></div>`
    + (state === 'on' ? `<span class="att-live"></span>` : '')
    + `</div></div>`;
}

// A check-in that never reached the server used to die as a toast on the
// rep's screen (2026-08-18: "abdul cant mark present" — zero rows, zero
// evidence). Report it, best-effort, so the operator can read WHY from
// data (attendance_attempts, mig 681). Never blocks the UI.
function sosAttGeoFail(sid, code, message) {
  let devHash = null;
  try { devHash = localStorage.getItem('eon_device_id') || null; } catch (_e) {}
  try {
    api('/api/attendance/geo-fail', {
      method: 'POST',
      body: JSON.stringify({ staff_id: sid, code: code, message: message || null, device_hash: devHash }),
    }).catch(() => {});
  } catch (_e) {}
}

function sosAttCheckin() {
  if (sosState.attBusy) return;
  if (!navigator.geolocation) {
    sosAttGeoFail(sosState.cur, 'no_geolocation', 'navigator.geolocation missing');
    sosToast('Location not available on this device'); return;
  }
  sosState.attBusy = true; sosRenderAtt();
  const sid = sosState.cur;
  // WATCHDOG (2026-07-25, warehouse-tablet video: "it will just stay like
  // this"): the geolocation `timeout` option only counts AFTER the
  // permission prompt is answered — a suppressed or ignored prompt fires
  // NEITHER callback, so the button hatched forever with no message. Our
  // own timer resets the attempt honestly; the token makes a late
  // callback from a superseded attempt a no-op instead of a double-fire.
  const token = (sosState._attReq = (sosState._attReq || 0) + 1);
  const dog = setTimeout(() => {
    if (sosState._attReq !== token || !sosState.attBusy) return;
    sosState.attBusy = false; sosRenderAtt();
    sosAttGeoFail(sid, 3, 'watchdog: no reading in 20s (prompt unanswered or suppressed)');
    sosToast('No location reading — allow Location for this site in your browser settings, then tap Check in again');
  }, 20000);
  navigator.geolocation.getCurrentPosition((pos) => {
    clearTimeout(dog);
    if (sosState._attReq !== token) return;
    const c = pos.coords || {};
    // Second watchdog for the POST leg (2026-07-25 vet): clearing the geo
    // dog and covering nothing left a hung network call free to re-strand
    // the button — and the geo dog's "allow Location" message would have
    // been the wrong advice for a network stall. Token checks make a
    // post-watchdog response a no-op.
    const netDog = setTimeout(() => {
      if (sosState._attReq !== token || !sosState.attBusy) return;
      sosState.attBusy = false; sosRenderAtt();
      sosToast('Network is slow — the check-in may still land; give it a moment and refresh');
    }, 20000);
    // device_hash: the same per-browser id login.js mints (eon_device_id).
    // attendance_devices existed since mig 276 with ZERO rows because no
    // client ever sent this — one line, and the table starts breathing.
    let devHash = null;
    try { devHash = localStorage.getItem('eon_device_id') || null; } catch (_e) {}
    api('/api/attendance/checkin', {
      method: 'POST',
      body: JSON.stringify({ staff_id: sid, lat: c.latitude, lng: c.longitude, accuracy_m: c.accuracy, device_hash: devHash }),
    }).then((res) => {
      clearTimeout(netDog);
      if (sosState._attReq !== token) return;
      sosState.attBusy = false;
      if (res && res.message) sosToast(res.message);
      return sosAttFetch();  // refresh to the server-decided verdict
    }).catch((e) => {
      clearTimeout(netDog);
      if (sosState._attReq !== token) return;
      sosState.attBusy = false; sosRenderAtt();
      sosToast((e && e.message) || 'Check-in failed — try again');
    });
  }, (err) => {
    clearTimeout(dog);
    if (sosState._attReq !== token) return;
    sosState.attBusy = false; sosRenderAtt();
    sosAttGeoFail(sid, (err && err.code) || 2, (err && err.message) || null);
    sosToast(err && err.code === 1 ? 'Location permission denied' : 'Could not get your location');
  }, { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 });
}

// The clock-out pulse (WS-K): one tap, nobody's name on it. The row
// cannot hold an identity — the table has no staff column — and the
// trend stays silent under three voices. Skippable by design.
function sosPulseAsk() {
  const ov = document.createElement('div');
  ov.className = 'sos-pulse';
  ov.innerHTML = `<div class="sos-pulse__card">
      <div class="sec">One tap before you go — how did today feel?</div>
      <div class="sos-pulse__row">
        <button data-pulse="good">Good</button>
        <button data-pulse="okay">Okay</button>
        <button data-pulse="hard">Hard</button></div>
      <div class="sos-sub">Your answer is anonymous — no name is saved with it. You can skip.</div>
      <button class="sos-pulse__skip" data-pulse-skip>Skip</button></div>`;
  document.body.appendChild(ov);
  ov.querySelector('[data-pulse-skip]').addEventListener('click', () => ov.remove());
  ov.querySelectorAll('[data-pulse]').forEach(b => b.addEventListener('click', async () => {
    const val = b.getAttribute('data-pulse');
    try {
      await api('/api/staffos/pulse', { method: 'POST', body: JSON.stringify({ response: val, location_id: (sosR() && sosR().location_id) || null }) });
    } catch (_) { /* the answer is a gift — never an error the staffer owns */ }
    ov.remove();
    sosToast('Noted. Rest well.');
  }));
}

function sosAttCheckout() {
  if (sosState.attBusy) return;
  sosState.attBusy = true; sosRenderAtt();
  const sid = sosState.cur;
  const send = (coords) => {
    const bodyObj = { staff_id: sid };
    if (coords) { bodyObj.lat = coords.latitude; bodyObj.lng = coords.longitude; bodyObj.accuracy_m = coords.accuracy; }
    api('/api/attendance/checkout', { method: 'POST', body: JSON.stringify(bodyObj) })
      .then((res) => {
        sosState.attBusy = false;
        if (res && res.message) sosToast(res.message);
        if (res && res.ok !== false) sosPulseAsk();
        return sosAttFetch();
      }).catch((e) => {
        sosState.attBusy = false; sosRenderAtt();
        sosToast((e && e.message) || 'Clock-out failed — try again');
      });
  };
  // Coordinates are OPTIONAL for clock-out (server records regardless —
  // leaving is not the fraud vector). Geolocation failure just sends bare.
  // WATCHDOG (2026-07-25 vet — same class as the check-in hang): an
  // unanswered permission prompt fires NEITHER geolocation callback, so
  // attBusy stuck forever here too. Since coords are optional, the right
  // recovery is to clock out BARE, not to fail; the sent flag makes the
  // watchdog and a late callback mutually exclusive.
  if (!navigator.geolocation) { send(null); return; }
  let sent = false;
  const once = (coords) => { if (sent) return; sent = true; send(coords); };
  setTimeout(() => once(null), 12000);
  navigator.geolocation.getCurrentPosition(
    (pos) => once(pos.coords || null),
    () => once(null),
    { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 });
}

function sosAttSetPin() {
  if (!navigator.geolocation) { sosToast('Location not available on this device'); return; }
  const a = sosState.att, loc = (a && a.location) || {};
  if (!loc.id) { sosToast('No location to pin'); return; }
  sosToast('Reading your position…');
  // Same unanswered-prompt class as check-in/clock-out: without this the
  // "Reading your position…" toast was the last thing that ever happened.
  const dead = setTimeout(() => sosToast(
    'No location reading — allow Location for this site, then try again'), 15000);
  navigator.geolocation.getCurrentPosition((pos) => {
    clearTimeout(dead);
    const c = pos.coords || {};
    api('/api/attendance/pin', {
      method: 'POST',
      body: JSON.stringify({ location_id: loc.id, lat: c.latitude, lng: c.longitude, radius_m: loc.radius_m || 120 }),
    }).then(() => { sosToast('Precise pin set for ' + (loc.name || 'this store')); return sosAttFetch(); })
      .catch((e) => sosToast((e && e.message) || 'Could not set the pin'));
  }, () => { clearTimeout(dead); sosToast('Could not get your location for the pin'); },
    { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 });
}

function sosAttHeartbeat() {
  const sid = sosState.cur;
  if (!sid) return;
  api('/api/attendance/heartbeat', { method: 'POST', body: JSON.stringify({ staff_id: sid }) })
    .then((res) => {
      // Reflect the freshly-active state without a full refetch.
      if (res && res.ok && sosState.att && sosState.att.mode === 'shift') {
        sosState.att.active = true;
        if (sosId('sos-att-slot')) sosRenderAtt();
      }
    }).catch(() => {});
}

// ═══════════════ SHIFT REQUESTS (off-day / leave / swap) ═════════
// Staffer raises a request; off_day/leave go to Operations, swaps go to
// the coworker first (peer-accept) then Operations. Approval updates the
// roster server-side (staff_shift_exceptions). Mirrors the att-block
// async pattern: slot painted from cache, filled by sosReqFetch.
function sosReqFetch() {
  const sid = sosState.cur;
  if (!sid) { sosState.req = null; return Promise.resolve(); }
  return Promise.all([
    api('/api/staffos/shift-requests?staff_id=' + encodeURIComponent(sid)).catch(() => null),
    api('/api/staffos/leave-balance?staff_id=' + encodeURIComponent(sid)).catch(() => null),
  ]).then(([r, bal]) => {
    sosState.req = (r && r.ok) ? { mine: r.mine || [], to_accept: r.to_accept || [],
                                  balance: (bal && bal.ok) ? bal : null } : null;
    if (sosState.cur === sid && sosId('sos-req-slot')) sosRenderReq();
  });
}

function sosRenderReq() { const s = sosId('sos-req-slot'); if (s) s.innerHTML = sosReqBlock(); }

const SOS_REQ_STATUS = {
  pending_acceptance: ['Awaiting coworker', 'soft'],
  pending_manager: ['Awaiting Operations', 'soft'],
  approved: ['Approved', 'ok'],
  rejected: ['Declined', 'bad'],
  cancelled: ['Cancelled', 'off'],
  expired: ['Expired', 'off'],
};
function sosReqKindLabel(r) {
  if (r.kind === 'leave') return (r.leave_type || 'annual') + ' leave';
  if (r.kind === 'swap') return (r.swap_kind === 'swap' ? 'shift swap' : 'shift cover');
  return 'off day';
}
function sosReqDates(r) {
  const a = (r.start_date || '').slice(5), b = (r.end_date || '').slice(5);
  return a === b ? a : a + '→' + b;
}

function sosReqBlock() {
  const st = sosState.req;
  const bal = st && st.balance;
  // Honest-empty (bench catch, 2026-08-31): a partial/failed balance read
  // used to render "undefined of NaN leave days left" — worse than no
  // pill. The pill renders only when both numbers are real.
  const balTotal = bal ? (+bal.entitlement + +bal.carried_over) : NaN;
  const balPill = (bal && Number.isFinite(+bal.available) && Number.isFinite(balTotal))
    ? `<span class="sos-req-bal">${bal.available} of ${balTotal} leave days left</span>` : '';
  let rows = '';
  (st && st.to_accept || []).forEach(r => {
    rows += `<div class="sos-req-row sos-req-row--peer">`
      + `<div class="sos-req-meta"><b>Swap from a coworker</b>`
      + `<span>${sosEsc(sosReqDates(r))} · ${sosEsc(r.reason || '')}</span></div>`
      + `<div class="sos-req-acts">`
      + `<button class="sos-req-ok" onclick="sosReqRespond('${sosEsc(r.id)}',true,this)">Accept</button>`
      + `<button class="sos-req-no" onclick="sosReqRespond('${sosEsc(r.id)}',false,this)">Decline</button>`
      + `</div></div>`;
  });
  (st && st.mine || []).slice(0, 6).forEach(r => {
    const sm = SOS_REQ_STATUS[r.status] || [r.status, 'off'];
    const canCancel = r.status === 'pending_acceptance' || r.status === 'pending_manager';
    rows += `<div class="sos-req-row">`
      + `<div class="sos-req-meta"><b>${sosEsc(sosReqKindLabel(r))} · ${sosEsc(sosReqDates(r))}</b>`
      + `<span>${sosEsc(r.reason || '')}</span></div>`
      + `<span class="sos-req-badge ${sm[1]}">${sosEsc(sm[0])}</span>`
      + (canCancel ? `<button class="sos-req-x" title="Cancel" onclick="sosReqCancel('${sosEsc(r.id)}',this)">&times;</button>` : '')
      + `</div>`;
  });
  if (!rows) rows = `<div class="sos-req-empty">No requests. Tap below to ask for an off day, leave, or a shift swap.</div>`;
  return `<div class="card sos-req">`
    + `<div class="att-head"><span class="att-tag">TIME OFF &amp; SWAPS</span>${balPill}</div>`
    + rows
    + `<button class="sos-req-new" onclick="sosReqOpen()">+ New request</button>`
    + `</div>`;
}

function sosReqOpen() {
  sosState.reqKind = 'off_day';
  sosState.reqKeep = {};   // fresh sheet — no resurrected fields
  const sh = sosId('sos-sheet'); if (!sh) return;
  sh.innerHTML = sosReqSheetHTML(); sh.classList.add('on');
}
// Kind tabs re-render the whole sheet — stash the typed fields first and
// restore after, so switching Off day → Leave doesn't wipe the reason/date.
const SOS_REQ_FIELDS = ['sos-req-reason', 'sos-req-start', 'sos-req-end',
                        'sos-req-ltype', 'sos-req-cp', 'sos-req-twoway', 'sos-req-cpd'];
function sosReqSetKind(k) {
  const keep = sosState.reqKeep = sosState.reqKeep || {};
  SOS_REQ_FIELDS.forEach(id => {
    const e = sosId(id); if (!e) return;
    keep[id] = e.type === 'checkbox' ? e.checked : e.value;
  });
  sosState.reqKind = k;
  const sh = sosId('sos-sheet');
  if (sh) {
    sh.innerHTML = sosReqSheetHTML();
    SOS_REQ_FIELDS.forEach(id => {
      const e = sosId(id); if (!e || !(id in keep)) return;
      if (e.type === 'checkbox') e.checked = !!keep[id];
      else if (keep[id] !== '' && keep[id] != null) e.value = keep[id];
    });
    sosReqToggleTwoway();
  }
}

function sosReqSheetHTML() {
  const k = sosState.reqKind;
  const tab = (key, label) => `<button class="sos-req-tab ${k === key ? 'on' : ''}" onclick="sosReqSetKind('${key}')">${label}</button>`;
  // roster rows are keyed staff_id (NOT id) — using s.id made every option
  // value 'undefined' (swap always failed) and never excluded self.
  const others = (sosState.roster || []).filter(s => String(s.staff_id) !== String(sosState.cur));
  const opts = others.map(s => `<option value="${sosEsc(s.staff_id)}">${sosEsc(s.full_name || s.name || s.staff_id)}</option>`).join('');
  let body = '';
  if (k === 'leave') {
    body = `<label class="sos-req-l">Leave type</label>`
      + `<select id="sos-req-ltype" class="sos-req-in"><option value="annual">Annual (counts to your 14 days)</option>`
      + `<option value="sick">Sick</option><option value="unpaid">Unpaid</option></select>`
      + `<div class="sos-req-2"><div><label class="sos-req-l">From</label><input type="date" id="sos-req-start" class="sos-req-in"></div>`
      + `<div><label class="sos-req-l">To</label><input type="date" id="sos-req-end" class="sos-req-in"></div></div>`;
  } else if (k === 'swap') {
    body = `<label class="sos-req-l">Coworker to cover</label>`
      + `<select id="sos-req-cp" class="sos-req-in">${opts}</select>`
      + `<label class="sos-req-l">Your shift date (they cover)</label>`
      + `<input type="date" id="sos-req-start" class="sos-req-in">`
      + `<label class="sos-req-l"><input type="checkbox" id="sos-req-twoway" onchange="sosReqToggleTwoway()"> Two-way swap (I take one of theirs back)</label>`
      + `<div id="sos-req-cpd-wrap" style="display:none"><label class="sos-req-l">Their shift date you take</label>`
      + `<input type="date" id="sos-req-cpd" class="sos-req-in"></div>`;
  } else {
    body = `<label class="sos-req-l">Date</label><input type="date" id="sos-req-start" class="sos-req-in">`;
  }
  return `<div class="pane" onclick="event.stopPropagation()"><div class="grab"></div>`
    + `<div class="scw-sheet__h"><div class="scw-sheet__t">New request</div>`
    + `<button class="scw-x" onclick="sosCloseSheet()">&times;</button></div>`
    + `<div class="sos-req-tabs">${tab('off_day', 'Off day')}${tab('leave', 'Leave')}${tab('swap', 'Swap')}</div>`
    + body
    + `<label class="sos-req-l">Reason (required)</label>`
    + `<textarea id="sos-req-reason" class="sos-req-in" rows="2" placeholder="Why? Operations sees this."></textarea>`
    + `<button class="bigdone" onclick="sosReqSubmit(this)">Send request</button>`
    + `</div>`;
}
function sosReqToggleTwoway() {
  const on = sosId('sos-req-twoway') && sosId('sos-req-twoway').checked;
  const w = sosId('sos-req-cpd-wrap'); if (w) w.style.display = on ? 'block' : 'none';
}

// Double-tap guard shared by the request actions (same idea as
// sosAttCheckin's attBusy): disable the tapped button + busy label for the
// flight, restore on error. Success re-renders the block/sheet anyway.
function sosBtnBusy(btn, label) {
  if (!btn) return null;
  const prev = btn.textContent;
  btn.disabled = true;
  if (label) btn.textContent = label;
  return () => { btn.disabled = false; btn.textContent = prev; };
}

function sosReqSubmit(btn) {
  if (btn && btn.disabled) return;
  const k = sosState.reqKind;
  const val = id => { const e = sosId(id); return e ? e.value : null; };
  const reason = (val('sos-req-reason') || '').trim();
  const start = val('sos-req-start');
  if (!reason) { sosToast('A reason is required'); return; }
  if (!start) { sosToast('Pick a date'); return; }
  const payload = { staff_id: sosState.cur, kind: k, reason: reason,
                    start_date: start, end_date: val('sos-req-end') || start };
  if (k === 'leave') payload.leave_type = val('sos-req-ltype') || 'annual';
  if (k === 'swap') {
    payload.counterparty_staff_id = val('sos-req-cp');
    if (!payload.counterparty_staff_id) { sosToast('Pick a coworker'); return; }
    const twoway = sosId('sos-req-twoway') && sosId('sos-req-twoway').checked;
    payload.swap_kind = twoway ? 'swap' : 'drop';
    if (twoway) {
      payload.counterparty_date = val('sos-req-cpd');
      if (!payload.counterparty_date) { sosToast('Pick the shift date you take back'); return; }
    }
  }
  const undo = sosBtnBusy(btn, 'Sending…');
  api('/api/staffos/shift-requests', { method: 'POST', body: JSON.stringify(payload) })
    .then(() => { sosToast(k === 'swap' ? 'Sent to your coworker' : 'Sent to Operations'); sosState.reqKeep = {}; sosCloseSheet(); return sosReqFetch(); })
    .catch(e => { if (undo) undo(); sosToast((e && e.message) || 'Could not send request'); });
}

function sosReqRespond(id, accept, btn) {
  if (btn && btn.disabled) return;
  const undo = sosBtnBusy(btn, accept ? 'Accepting…' : 'Declining…');
  api('/api/staffos/shift-requests/' + encodeURIComponent(id) + '/respond',
      { method: 'POST', body: JSON.stringify({ staff_id: sosState.cur, accept: !!accept }) })
    .then(() => { sosToast(accept ? 'Accepted — sent to Operations' : 'Declined'); return sosReqFetch(); })
    .catch(e => { if (undo) undo(); sosToast((e && e.message) || 'Could not respond'); });
}
function sosReqCancel(id, btn) {
  if (btn && btn.disabled) return;
  const undo = sosBtnBusy(btn, '…');
  api('/api/staffos/shift-requests/' + encodeURIComponent(id) + '/cancel',
      { method: 'POST', body: JSON.stringify({ staff_id: sosState.cur }) })
    .then(() => { sosToast('Cancelled'); return sosReqFetch(); })
    .catch(e => { if (undo) undo(); sosToast((e && e.message) || 'Could not cancel'); });
}

function sosRenderDay() {
  const d = sosR();
  // Fresh render pass = fresh link-dest registry (it grew forever across
  // re-renders). Stale baked indices in the OTHER view's DOM are fine:
  // every view re-renders on tab switch before it can be tapped.
  window.__sosLinkDestReg = [];
  let h = '';
  // B3 — attendance check-in lands at the very TOP of Day. Filled async
  // by sosAttFetch (slot is painted empty first so Day never blanks).
  h += `<div id="sos-att-slot">${sosAttBlock()}</div>`;
  // hello + ring (lowercase display + the accent period — design pass 03)
  h += `<div class="hello"><div class="hi">${sosEsc(d.hi || '')}${d.hi ? '<i>.</i>' : ''}</div>`
    + `<div class="sub">${sosEsc(d.sub)}</div>`
    + (d.streak ? `<div class="strk"><b>${sosEsc(d.streak)}</b></div>` : '') + `</div>`;
  h += `<div class="card"><div class="ringrow">${sosRingSVG(d.ring)}<div style="flex:1">`;
  if (d.ring_off_today) h += `<div class="kv"><span>Off today</span><b>ring paused — not empty</b></div>`;
  // k = [label, value, tone?] — value ESCAPED (stored-XSS, vet S4).
  const sosKpiTone = { mint: 'var(--mint)', good: 'var(--mint)', hero: 'var(--mint)', bad: 'var(--bad)' };
  (d.kpis || []).forEach(k => {
    const tc = sosKpiTone[k[2]];
    const tone = tc ? ` style="color:${tc}"` : '';
    h += `<div class="kv"><span>${sosEsc(k[0])}</span><b${tone}>${sosEsc(k[1])}</b></div>`;
  });
  h += `</div></div></div>`;
  // quick actions
  if (d.quick && d.quick.length) {
    h += `<div class="qa">`;
    d.quick.filter(q => String(q).trim().toLowerCase() !== 'request finance' || prcCanRequest()).forEach((q, i) => { h += `<button class="${i === 0 ? 'pri' : ''}" onclick="sosQa('${sosEsc(sosJs(q))}')">${sosEsc(q)}</button>`; });
    h += `</div>`;
  }
  // day blocks + obs-history fold (tail-doors 07-27)
  let obsPlaced = false;
  (d.day || []).forEach(b => {
    h += sosRenderBlock(b[0], b[1]);
    if (b[0] === 'review' && !obsPlaced && sosState.cur) { h += sosObsFold(); obsPlaced = true; }
  });
  if (!(d.day || []).length && !(d.kpis || []).length) {
    h += `<div class="note">No live data for this role yet.</div>`;
  }
  if (!obsPlaced && sosState.cur) h += sosObsFold();
  // Time off & swaps — RESTORED 2026-07-24 (staff feedback: "can't apply
  // for leave or sick leave"). The leave engine (#1144) has been live since
  // 07-11; this slot is the staff-facing door. Painted from cache, filled
  // async by sosReqFetch below.
  h += `<div id="sos-req-slot">${sosReqBlock()}</div>`;
  sosId('sos-v-day').innerHTML = h;
  cg.decorateStaffDay();
  sosReqFetch();
  // THE RELAY's Asks shape — inserted after the check-in slot ONLY once
  // the server says the lane is armed (never reserved, never awaited).
  sosRelayLoad();
  sosVesselBind();   // WS-H: Done / Stuck on the open ask
  // Numbers behave like money (design pass 03): count-up + ring draw run
  // ONCE per view — a re-render of an already-seen Day paints final values.
  const daySeen = sosId('sos-v-day').classList.contains('sos-seen');
  if (!daySeen && !cg.motionPaused() && (!cg.active() || cg.requested())) {
    const p = sosId('sos-v-day')?.querySelector('.ring2 .p'), value = p?.style.strokeDashoffset;
    let stopped = false, frame = null, untrack = () => {};
    const finish = () => { stopped = true; if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame); if (p) p.style.strokeDashoffset = value; untrack(); };
    untrack = cg.trackMotion(finish);
    if (!stopped) frame = requestAnimationFrame(() => {
      if (stopped || cg.motionPaused()) { finish(); return; }
      if (p) p.style.strokeDashoffset = '195';
      frame = requestAnimationFrame(finish);
    });
    requestAnimationFrame(() => eonAnimNums(sosId('sos-v-day')));
  }
  sosAttMarkSeen();
}

// ══ THE CREW ROOM · the ask finds your phone ═══════════════════════
// Design contract: the rooms artifact, floor 1 — "a staffer's whole
// relay life fits in the Asks shape the Crew Room already owns: what's
// waiting for you, what you sent, and a bell that finally exists on
// this shell." (The bell is R0's `SHELL_BELL_VIEWS`, already docked to
// <body> for 'staffos'; this is the other half.)
//
// NOTHING IS RESERVED. The panel is INSERTED after the check-in slot
// once the server says the relay is armed, exactly as the Job Board's
// meter is (_tosLoadMeter's law). A dark Staff OS pays one cheap scoped
// request that answers {armed:false} without a relay read, paints
// nothing, and is the Day it was yesterday — element for element.
//
// Fire-and-forget, never awaited: the Day is what the staffer came for,
// and a slow relay must not hold it back nor a failing one blank it.
async function sosRelayLoad() {
  const host = sosId('sos-att-slot');
  if (!host) return;
  const me = sosState.cur || sosMyStaffId() || '';
  let d = null;
  try {
    d = await api('/api/staffos/relay/crew?staff_id=' + encodeURIComponent(me));
  } catch (_) { return; }
  if (!d || d.armed !== true || !d.ok) return;
  // REPLACED, never early-returned-on (the R1 verifier's F2 lesson): the
  // Day re-renders on every role switch and refresh, so a panel already
  // on screen must be swapped for the fresh reading rather than frozen
  // at its first one.
  const old = sosId('sos-relay');
  const el = elFromHTML(sosRelayHTML(d));
  if (old) old.replaceWith(el); else host.insertAdjacentElement('afterend', el);
  sosRelayBind();
}

function sosRelayAskHTML(c) {
  // ONE LIME ACT PER SCREEN — the house law holds. The Got-it tap is it;
  // everything else on the card is a chip or a ghost. The tap is YOURS:
  // it rides the one ack door (POST /tasks/{id}/transition
  // status=acknowledged) so the audit stamp names you, never "timer".
  const clk = relayClockHTML(c);
  return `<div class="sos-relay__ask" data-rlid="${sosEsc(c.task_id || '')}">
    <div class="sos-relay__top">
      <span class="sos-relay__tid">${sosEsc(c.short_id || '')}</span>${clk}</div>
    <b>${sosEsc(c.title || 'Ask')}</b>
    ${relayAskerHTML(c.requester, c.requester_is_eon, c.dept, c.store_name)}
    <div class="sos-relay__act">
      ${c.acked_at
        ? `<span class="sos-relay__done">${c.acked_by ? sosEsc(c.acked_by) + ' has it' : 'Got it'}</span>`
        : `<button class="sos-relay__ack" data-rlack="${sosEsc(c.task_id || '')}">Got it</button>`}
      <a class="sos-relay__open" href="${sosEsc(c.url || '#')}">Open the task</a>
    </div>
    ${relayRungHTML(c)}
  </div>`;
}

function sosRelaySentHTML(c) {
  // "You asked" closes the loop: your outgoing asks live under the same
  // thumb, each wearing its state chip, and closure comes back HERE
  // without you chasing it.
  const chip = c.closed
    ? '<span class="sos-relay__chip sos-relay__chip--back">returned</span>'
    : (c.acked_at
       ? '<span class="sos-relay__chip sos-relay__chip--ok">taken</span>'
       : '<span class="sos-relay__chip">waiting</span>');
  const who = c.closed
    ? (c.done_by ? `closed by ${sosEsc(c.done_by)}` : 'closed')
    : (c.acked_at
       ? (c.acked_by ? `${sosEsc(c.acked_by)} has it` : 'they have it')
       : (c.owner ? `with ${sosEsc(c.owner)}` : 'not routed yet'));
  return `<a class="sos-relay__sent" href="${sosEsc(c.url || '#')}">
    <span class="who"><b>${sosEsc(c.short_id || '')} · ${sosEsc(c.title || 'Ask')}</b>${who}</span>
    ${chip}</a>`;
}

function sosRelayHTML(d) {
  const forYou = d.for_you || [];
  const asked = d.you_asked || [];
  // HONEST EMPTIES — the Crew Room's own law, kept whole. A room with
  // nothing in it says so; it does not hide and it does not invent.
  let h = `<div class="sos-relay" id="sos-relay">`;
  h += `<div class="sec">For you</div>`;
  h += forYou.length
    ? forYou.map(sosRelayAskHTML).join('')
    : `<div class="card sos-relay__empty">Nothing is waiting on you.</div>`;
  // "You asked" IS THE HALF THAT TEACHES (2026-08-30). It used to hide
  // whenever it was empty, so the only people who ever saw it were the
  // people who had already worked out how to send a job to another team
  // — and on 2026-08-30 that was nobody. A section that appears the
  // moment you succeed and is invisible until then cannot tell you the
  // move exists. It says what it is for instead, which is the Crew
  // Room's own law: a room with nothing in it says so, it does not hide
  // and it does not invent.
  h += `<div class="sec">You asked</div>`;
  h += asked.length
    ? `<div class="card sos-relay__sentwrap">` + asked.map(sosRelaySentHTML).join('') + `</div>`
    : `<div class="card sos-relay__empty">Nothing yet. When you send a job to another team, it shows up here with the name of whoever has it.</div>`;
  return h + `</div>`;
}

function sosRelayBind() {
  document.querySelectorAll('#sos-relay [data-rlack]').forEach(b => {
    b.addEventListener('click', () => sosRelayAck(b.getAttribute('data-rlack'), b));
  });
}

async function sosRelayAck(taskId, btn) {
  if (!taskId || (btn && btn.disabled)) return;
  const undo = sosBtnBusy(btn, 'Saving…');
  try {
    // THE ONE ACK RAIL (relay R0 item 7). The web tap and the WhatsApp
    // button stamp the ack clock through the same function, or the
    // meter counts two different events under one name.
    await api('/api/tasks/' + encodeURIComponent(taskId) + '/transition', {
      method: 'POST', body: JSON.stringify({ status: 'acknowledged' }),
    });
    sosToast('Got it — they can see you have it');
    sosRelayLoad();
  } catch (e) {
    if (undo) undo();
    sosToast((e && e.message) || 'Could not save that');
  }
}

// ── block dispatcher (shared by Day + Team) ─────────────────────
function sosRenderBlock(type, b) {
  switch (type) {
    case 'stores':     return sosBlkStores(b);
    case 'exceptions': return sosBlkExceptions(b);
    case 'leavereqs':  return sosBlkLeaveReqs(b);
    case 'relaydone':  return sosBlkRelayDone(b);
    case 'ring':       return sosBlkRing(b);      // WS-H: the department's week, one ring
    case 'changed':    return sosBlkChanged(b);   // WS-H: this line changed because of you
    case 'coach':      return sosBlkCoach(b);     // WS-H: the coach's lines, one at a time
    case 'lanes':      return sosBlkLanes(b);     // WS-H: the loop, for managers
    case 'checklist':  return sosBlkChecklist(b);
    case 'bagbar':     return sosBlkBagbar(b);
    case 'capture':    return sosBlkCapture(b);
    case 'storeday':   return sosBlkStoreday(b);
    case 'morning':    return sosBlkMorning(b);
    case 'payslip':    return sosBlkPayslip(b);
    case 'day_grade':  return sosBlkDayGrade(b);
    case 'kvcard':     return sosBlkKvcard(b);
    case 'zones':      return sosBlkZones(b);
    case 'link':       return sosBlkLink(b);
    case 'run':        return sosBlkRun(b);
    case 'trackingsend': return sosBlkTrackingSend(b);
    case 'dispatchdone': return sosBlkDispatchDone(b);
    case 'gauges':     return sosBlkGauges(b);
    case 'approvals':  return sosBlkApprovals(b);
    case 'dept':       return sosBlkDept(b);
    case 'reps':       return sosBlkReps(b);
    case 'merch':      return sosBlkMerch(b);
    case 'performance': return sosBlkPerf(b);
    case 'review':     return sosBlkReview(b);
    default:           return '';
  }
}

// EON's daily CS review for the rep — a plain coaching card (coach message +
// a few strengths / things to work on). Surfaced on the CS Day so EON's
// feedback isn't invisible. Plain words only (no decorative glyphs).
function sosBlkReview(b) {
  let h = `<div class="sec">${sosEsc(b.title || 'Your daily review')}</div>`
    + `<div class="card sos-review">`;
  if (b.coach) h += `<div class="sos-review__coach">${sosEsc(b.coach)}</div>`;
  const strengths = b.strengths || [];
  const flaws = b.flaws || [];
  if (strengths.length) {
    h += `<div class="sos-review__h">What worked</div>`;
    strengths.forEach(s => { h += `<div class="sos-review__row">${sosEsc(s)}</div>`; });
  }
  if (flaws.length) {
    h += `<div class="sos-review__h">Work on next</div>`;
    flaws.forEach(s => { h += `<div class="sos-review__row">${sosEsc(s)}</div>`; });
  }
  h += `</div>`;
  return h;
}

// Observation history (tail-doors 2026-07-27) — the door for
// GET /api/staffos/observations (EON's coaching pulses: strengths / flaws /
// daily reviews from the CS coach + merchandising). Collapsed by default;
// fetches on FIRST expand only — the Day paint never pays for it. Server
// clamps staff_id to the caller for non-owner/admin (_effective_staff_id).
function sosObsFold() {
  return `<div class="sec">Observation history</div>`
    + `<div class="card sos-obs" id="sos-obs">`
    + `<div class="qa"><button onclick="sosObsToggle(this)">Show recent observations</button></div>`
    + `<div id="sos-obs-list" hidden></div></div>`;
}

async function sosObsToggle(btn) {
  const list = sosId('sos-obs-list');
  if (!list) return;
  if (!list.hidden) {
    list.hidden = true;
    btn.textContent = 'Show recent observations';
    return;
  }
  list.hidden = false;
  btn.textContent = 'Hide observations';
  if (list._loaded) return;   // lazy-once: expand again = no refetch
  list._loaded = true;
  list.innerHTML = '<div class="sm sos-loading">Loading…</div>';
  let d = null;
  try {
    d = await api('/api/staffos/observations?staff_id='
      + encodeURIComponent(sosState.cur || '') + '&limit=20');
  } catch (_) {}
  if (!d || !d.ok) {
    list._loaded = false;   // failed fetch can be retried on next expand
    list.innerHTML = '<div class="sm">Couldn\'t load observations.</div>';
    return;
  }
  const obs = d.observations || [];
  if (!obs.length) { list.innerHTML = '<div class="sm">No observations yet.</div>'; return; }
  list.innerHTML = obs.map(o => {
    const day = String(o.period_date || o.created_at || '').slice(0, 10);
    return `<div class="sos-obs__row"><span class="sos-obs__d">${sosEsc(day)}</span>`
      + `<div class="sos-obs__t">${sosEsc(o.observation || '')}</div></div>`;
  }).join('');
}

// Merchandising — a deferred card the store rep expands; the heavy per-store
// queries (top sellers / FBT / non-performers / 35-day dead stock) run on
// demand via /api/staffos/merchandising so the Day view stays fast.
function sosBlkMerch(b) {
  const sid = b.staff_id || '';
  return `<div class="sec">Merchandising — ${sosEsc(b.store || 'your store')}</div>`
    + `<div class="card sos-merch" id="sos-merch-${sosEsc(sid)}">`
    + `<div class="sm">What's selling, what to bundle, and what to clear off your floor.</div>`
    // Wrap in a .qa parent so the button picks up the .sos-root .qa flex layout
    // + .sos-root .qa button styling (staff_os.css ~98-100). Without the wrapper
    // the bare `qa pri` button rendered unstyled/misaligned and read as
    // un-clickable.
    + `<div class="qa"><button class="pri" onclick="sosMerchLoad('${sosEsc(sid)}')">Show my products</button></div>`
    + `</div>`;
}

async function sosMerchLoad(staffId) {
  const host = document.getElementById('sos-merch-' + staffId);
  if (!host) return;
  host.innerHTML = '<div class="sm">Loading…</div>';
  try {
    const d = await api('/api/staffos/merchandising?staff_id=' + encodeURIComponent(staffId));
    if (!d || !d.ok) { host.innerHTML = '<div class="sm">Couldn\'t load merchandising.</div>'; return; }
    const top = (d.top_sellers || []).slice(0, 6).map(p =>
      `<div class="sos-merch__row"><span>${sosEsc(p.product_name)}</span><b>${p.units}</b></div>`).join('') || '<div class="sm">No sales yet.</div>';
    const fbt = (d.frequently_bought_together || []).slice(0, 5).map(p =>
      `<div class="sos-merch__row"><span>${sosEsc(p.product_a)} + ${sosEsc(p.product_b)}</span><b>${p.co_orders}×</b></div>`).join('') || '<div class="sm">No pairs yet.</div>';
    // All-stores manager view: a merged FBT pair spans stores, so the manager
    // picks the target store from a dropdown, then activates. (Other aggregate
    // sections stay read-only.) Single-store rep activates into their own store.
    const allStores = !!d.all_stores;
    const storeOpts = (d.stores || []).map(s =>
      `<option value="${sosEsc(s)}">${sosEsc(s)}</option>`).join('');
    const bundles = (d.bundle_suggestions || []).slice(0, 4).map((p, i) => {
      const act = (allStores
        ? (storeOpts
            ? `<div class="sos-merch__acts"><select class="sos-merch__store" id="sos-bsel-${i}">${storeOpts}</select>`
              + `<button class="qa" onclick="sosMerchBundlePick(${i},'${sosEsc(sosJs(p.product_a))}','${sosEsc(sosJs(p.product_b))}',${p.discount_pct},this)">Activate</button></div>`
            : '')
        : `<button class="qa" onclick="sosMerchBundle('${sosEsc(sosJs(d.store))}','${sosEsc(sosJs(p.product_a))}','${sosEsc(sosJs(p.product_b))}',${p.discount_pct},this)">Activate bundle</button>`);
      return `<div class="sos-merch__dead"><div><b>${sosEsc(p.bundle_name)}</b> <span class="sm">— ${p.co_orders}× together, ${p.discount_pct}% off</span></div>`
        + act + `</div>`;
    }).join('') || '<div class="sm">No bundle plays yet.</div>';
    const np = (d.non_performers || []).slice(0, 8).map(p =>
      `<div class="sos-merch__row"><span>${sosEsc(p.product_name)}</span><span class="sm">${p.on_hand} on hand</span></div>`).join('') || '<div class="sm">Everything\'s moving.</div>';
    const dead = (d.dead_stock_14d || []).map(p => {
      const to = p.suggested_to_location || 'warehouse';
      const verb = p.suggested_action === 'transfer_to_store' ? ('Transfer to ' + sosEsc(to)) : 'Return to warehouse';
      // In the all-stores view each dead row carries its own shop_loc/store so
      // the transfer targets the right store; single-store falls back to the
      // panel's shop_loc.
      const fromLoc = p.shop_loc || d.shop_loc || '';
      const storeTag = (allStores && p.store) ? ` <span class="sm">· ${sosEsc(p.store)}</span>` : '';
      const md = (p.markdown_pct && p.markdown_price)
        ? `<button class="qa pri" onclick="sosMerchMarkdown('${sosEsc(sosJs(p.sku))}',${p.markdown_pct},${p.markdown_price},'${sosEsc(sosJs(p.id))}','${sosEsc(sosJs(p.dead_since))}',this)">Mark down ${p.markdown_pct}% to ₦${Number(p.markdown_price).toLocaleString()}</button>`
        : '';
      return `<div class="sos-merch__dead"><div><b>${sosEsc(p.product_name)}</b>${storeTag} — dead ${p.days_dead}d (${p.on_hand} on hand)</div>`
        + `<div class="sos-merch__acts">${md}`
        + `<button class="qa" onclick="sosMerchTransfer('${sosEsc(sosJs(p.sku))}',${p.on_hand},'${sosEsc(sosJs(fromLoc))}','${sosEsc(sosJs(to))}',this)">${verb}</button></div></div>`;
    }).join('') || '<div class="sm">No dead stock past 35 days. Good.</div>';
    host.innerHTML =
      `<div class="sec">Top sellers (30d)</div>${top}`
      + `<div class="sec">Often bought together — suggest the pair</div>${fbt}`
      + `<div class="sec">Bundle plays — one tap to run at the register</div>${bundles}`
      + `<div class="sec">Not moving — give these visibility</div>${np}`
      + `<div class="sec">Dead 35+ days — clear the shelf</div>${dead}`;
  } catch (_) {
    host.innerHTML = '<div class="sm">Couldn\'t load merchandising.</div>';
  }
}

async function sosMerchTransfer(sku, qty, fromStore, toLocation, btn) {
  // ONE CLICK, NO CONFIRMS (standing rule): the button names qty + both
  // stores, and a queued move is reversible by queuing the move back.
  if (btn) { btn.disabled = true; btn.textContent = 'Moving…'; }
  try {
    const d = await api('/api/staffos/merchandising/transfer', {
      method: 'POST',
      body: JSON.stringify({ sku, qty, from_store: fromStore, to_location: toLocation }),
    });
    if (btn) btn.textContent = (d && d.ok) ? 'Move queued' : 'Failed — retry';
  } catch (e) {
    // A refusal (a PIN now needed, not your store) says why: retrying
    // cannot help, so the button stays off and shows the server's words.
    const why = e && e.status === 403 && e.message;
    if (btn) { btn.disabled = !!why; btn.textContent = why || 'Failed — retry'; }
  }
}

async function sosMerchMarkdown(sku, pct, price, cardId, cardSince, btn) {
  // ONE CLICK, NO CONFIRMS (standing rule) — no blocking dialog; the button
  // label already states exactly what happens.
  if (btn) { btn.disabled = true; btn.textContent = 'Marking down…'; }
  try {
    // THE TAP CARRIES ITS CARD (first vet, round 1, 3 Oct): the row it was
    // drawn from, the streak it was drawn in and the numbers it shows. A
    // panel left open past the next sweep can then never spend a card that
    // is no longer the one on the button — the server refuses it in words.
    const d = await api('/api/staffos/merchandising/markdown', {
      method: 'POST',
      body: JSON.stringify({ sku, pct, price, card_id: cardId, card_since: cardSince }),
    });
    // A refusal with a REASON is not a failure to retry (cost floor,
    // 2026-09-01): a SKU already at or under cost can never be marked
    // down, so re-tapping does nothing forever. Say what the floor saw
    // and leave the button spent.
    if (d && !d.ok && d.floor_refusal && d.message) {
      if (btn) btn.textContent = (d.error === 'already_at_or_below_cost')
        ? 'Already at cost' : 'Price unchanged';
      sosToast(d.message);
      return;
    }
    if (btn) btn.textContent = (d && d.ok) ? 'Marked down' : 'Failed — retry';
    if (!(d && d.ok) && btn) btn.disabled = false;
    // The rail may have held a deep cut at the cost floor — the shelf
    // price is real, the percentage on the button was not.
    if (d && d.ok && d.cost_floored && d.message) sosToast(d.message);
  } catch (e) {
    // A real Shopify price write is owner/admin-only. For everyone else the
    // suggestion is still ACTIONABLE: one tap files the markdown as a task on
    // Daniel's board instead of dead-ending on "permission denied".
    if (e && e.status === 403) {
      try {
        await _tosCreateTask({
          title: `Markdown ask: ${sku} -${pct}% to ₦${Number(price).toLocaleString()}`,
          description: 'Flagged from the Staff OS merchandising card (dead stock on the floor). Approving = tap the same markdown on your own view.',
          category: 'general', priority: 'normal',
        });
        if (btn) btn.textContent = 'Sent to Daniel';
        sosToast('Price changes are Daniel\u2019s call \u2014 flagged to him');
        return;
      } catch (_) { /* fall through to retry */ }
    }
    if (btn) { btn.disabled = false; btn.textContent = 'Failed — retry'; }
  }
}

// Manager all-stores view: read the chosen target store from the row's picker,
// then activate the bundle into that store (reuses sosMerchBundle).
function sosMerchBundlePick(i, productA, productB, discountPct, btn) {
  const sel = document.getElementById('sos-bsel-' + i);
  const store = sel && sel.value;
  if (!store) { if (btn) btn.textContent = 'Pick a store'; return; }
  return sosMerchBundle(store, productA, productB, discountPct, btn);
}

async function sosMerchBundle(store, productA, productB, discountPct, btn) {
  if (btn) { btn.disabled = true; btn.textContent = 'Activating…'; }
  try {
    const d = await api('/api/staffos/merchandising/bundle', {
      method: 'POST',
      body: JSON.stringify({ store, product_a: productA, product_b: productB, discount_pct: discountPct }),
    });
    if (btn) btn.textContent = (d && d.ok) ? 'Bundle active' : 'Failed — retry';
  } catch (_) {
    if (btn) { btn.disabled = false; btn.textContent = 'Failed — retry'; }
  }
}

// Performance OS — EON measured as a third CS rep against the humans. Deferred
// like merch: the lift + leaderboard run heavy queries on demand so the Day
// stays fast. Owner/all-reps only (gated server-side in role_payload).
function sosBlkPerf(b) {
  return `<div class="sec">${sosEsc(b.title || 'EON vs reps · scoreboard')}</div>`
    + `<div class="card sos-perf" id="sos-perf">`
    + `<div class="sm">Is EON converting chats to orders as well as the reps? Rank everyone, EON included.</div>`
    + `<button class="qa pri" onclick="sosPerfLoad()">Show scoreboard</button>`
    + `</div>`;
}

function sosPerfVerdict(l) {
  const map = {
    eon_ahead: ['EON is ahead', 'good'], reps_ahead: ['Reps are ahead', 'bad'],
    too_close: ['Too close to call', ''], insufficient_data: ['Not enough data yet', ''],
  };
  const v = map[l.verdict] || ['—', ''];
  const sign = l.lift_pp > 0 ? '+' : '';
  const cov = cswPayCoverage(l.coverage);
  return `<div class="sos-perf__verdict ${v[1]}"><b>${sosEsc(v[0])}</b>`
    + `<span class="sm"> EON ${l.eon.conv_rate_pct}% vs reps ${l.humans.conv_rate_pct}% `
    + `(${sign}${l.lift_pp}pts${l.significance && l.significance.significant ? ', significant' : ''})</span>`
    + (cov ? `<div class="sm">${cov}</div>` : '')
    + `<div class="sm">${sosEsc(l.note || '')}</div></div>`;
}

async function sosPerfLoad() {
  const host = document.getElementById('sos-perf');
  if (!host) return;
  host.innerHTML = '<div class="sm">Loading…</div>';
  try {
    const [lb, lift] = await Promise.all([
      api('/api/staffos/performance/leaderboard'),
      api('/api/staffos/performance/lift'),
    ]);
    let h = '';
    if (lift && lift.ok && lift.eon && lift.eon.active) h += sosPerfVerdict(lift);
    if (lb && lb.ok && (lb.rows || []).length) {
      h += `<div class="sec">Responder leaderboard (${lb.period_days}d)</div>`;
      const lbCov = cswPayCoverage(lb.coverage);
      if (lbCov) h += `<div class="sm">${lbCov}</div>`;
      h += (lb.rows || []).map(r => {
        const vs = (r.vs_eon_conv_pp == null || r.is_eon) ? ''
          : `<span class="sm ${r.vs_eon_conv_pp >= 0 ? 'good' : 'bad'}">`
            + `${r.vs_eon_conv_pp >= 0 ? '+' : ''}${r.vs_eon_conv_pp}pts vs EON</span>`;
        const csat = r.csat != null ? `${r.csat}/5 CSAT` : 'no CSAT';
        const low = r.low_n ? ' <span class="sm">(low n)</span>' : '';
        return `<div class="sos-perf__row${r.is_eon ? ' is-eon' : ''}">`
          + `<span class="sos-perf__rk">${r.rank}</span>`
          + `<span class="sos-perf__nm"><b>${sosEsc(r.rep_name || r.rep_id)}</b>${r.is_eon ? ' · EON' : ''}${low}</span>`
          + `<span class="sos-perf__mt">${r.conv_rate_pct}% · ${csat} · score ${r.score}</span>`
          + vs + `</div>`;
      }).join('');
    } else {
      h += '<div class="sm">No responder activity yet.</div>';
    }
    host.innerHTML = h;
  } catch (_) {
    host.innerHTML = '<div class="sm">Couldn\'t load the scoreboard.</div>';
  }
}

function sosBlkStores(b) {
  let h = `<div class="sec">${sosEsc(b.title)}</div><div class="card"><div class="st4">`;
  (b.cards || []).forEach(c => {
    h += `<div class="stc"><div class="sh"><b>${sosEsc(c[0])}</b></div>`
      + `<div class="rev" style="color:${c[3] || 'var(--ink)'}">${sosEsc(c[1])}</div>`
      + `<div class="sm">${sosEsc(c[2])}</div></div>`;
  });
  return h + `</div></div>`;
}
// Leave & shift approvals on the approver's phone (complaints fix
// 2026-09-01): the queue lived only in a web side column — five requests
// sat pending a month while the bells rang at a door that wasn't there.
// Approve/decline ride the existing /api/people/shift-requests decide
// route (her PIN session holds the gate it requires).
function sosBlkRelayDone(b) {
  // WS-D (2026-09-02): the return vessel — what this person closed this
  // week. WS-H (same day, 'Staff OS, Alive'): every close draws a lane out
  // to the floor and back, lit by its outcome; the open ask rides the last
  // lane with Done / Stuck on it. Tap Done and the dot travels; the outcome
  // comes back on a clock as well as on frames (a background tab pauses
  // requestAnimationFrame — the callback must still fire).
  const items = b.items || [];
  const open = (b.open || [])[0] || null;
  let h = `<div class="sec">${sosEsc(b.title || 'Asks you closed · this week')}</div><div class="card sos-vessel" data-vessel>`;
  h += `<div class="pb-t"><b>${sosEsc(b.summary || '')}</b></div>`;
  if (items.length || open) h += sosVesselSVG(items, open);
  else if (b.last && (b.last.what || b.last.line)) {
    h += `<div class="pb-s" style="margin-top:6px">${sosEsc(b.last.what || '')}</div>`;
  }
  if (b.last && b.last.line) h += `<div class="pb-s" style="margin-top:4px">${sosEsc(b.last.line)}</div>`;
  if (open) {
    h += `<div class="pb-s sos-vessel__open"><b>${sosEsc(open.short_id || '')}</b> · ${sosEsc(open.what || '')} · open${open.ask_hours != null ? ' · ask ' + sosEsc(String(open.ask_hours)) + 'h' : ''}</div>`;
    h += `<div class="qa"><button class="pri" data-vsdone="${sosEsc(open.id)}">Done</button><button data-vsstuck="${sosEsc(open.id)}" data-vsstatus="${sosEsc(open.status || '')}">Stuck — say why</button></div>`;
  }
  return h + `</div>`;
}
function sosVesselSVG(items, open) {
  const rows = items.slice(0, 5).map(it => ({ id: it.short_id, v: it.verdict, h: it.hours, open: false }));
  if (open) rows.push({ id: open.short_id, v: 'open', h: open.ask_hours, open: true });
  const H = 18 * rows.length + 14;
  let svg = `<svg class="sos-vessel__svg" viewBox="0 0 300 ${H}" width="100%" height="${H}" aria-hidden="true">`;
  svg += `<text x="0" y="9" class="lbl">YOU</text><text x="300" y="9" text-anchor="end" class="lbl">THE FLOOR</text>`;
  rows.forEach((r, i) => {
    const y = 20 + i * 18;
    const cls = r.open ? 'lane open' : ('lane ' + (r.v === 'hit' ? 'hit' : r.v === 'partial' ? 'partial' : 'miss'));
    const d = `M 8 ${y} C 90 ${y - 4}, 210 ${y + 4}, 292 ${y}`;
    svg += `<path class="${cls}" d="${d}"${r.open ? ' id="sos-openlane"' : ''}/>`;
    // the ledger's words are 'hit' / 'partial' / 'miss'; a person reads
    // 'in time' / 'same day' / 'late', and an ask that never closed says so
    const said = r.v === 'hit' ? 'in time' : r.v === 'partial' ? 'same day' : 'late';
    const closed = r.h == null ? 'never closed' : String(r.h) + 'h · ' + said;
    svg += `<text x="12" y="${y - 6}" class="lbl">${sosEsc(r.id || '')} · ${r.open ? 'open · ask ' + sosEsc(r.h == null ? '?' : String(r.h)) + 'h' : sosEsc(closed)}</text>`;
    if (r.open) svg += `<circle id="sos-opendot" class="dot" cx="8" cy="${y}" r="3" opacity="0"/>`;
  });
  return svg + `</svg>`;
}
function sosVesselTravel(cb) {
  let fired = false, frame = null, timer = null, untrack = () => {};
  const finish = () => {
    if (fired) return; fired = true;
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
    clearTimeout(timer); untrack();
    if (dot) dot.setAttribute('opacity', '0');
    if (cb) cb();
  };
  const lane = document.getElementById('sos-openlane'), dot = document.getElementById('sos-opendot');
  if (!lane || !dot || typeof lane.getTotalLength !== 'function') { finish(); return; }
  const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || cg.motionPaused()) { finish(); return; }
  untrack = cg.trackMotion(finish);
  if (fired) return;
  lane.classList.add('lit'); dot.setAttribute('opacity', '1');
  const len = lane.getTotalLength(); let t0 = null;
  const step = (ts) => {
    if (fired || cg.motionPaused()) { finish(); return; }
    if (t0 === null) t0 = ts;
    const k = Math.min(1, (ts - t0) / (reduced ? 1 : 1100));
    const p = lane.getPointAtLength(len * (k <= 0.5 ? k * 2 : (1 - k) * 2));
    dot.setAttribute('cx', p.x); dot.setAttribute('cy', p.y);
    if (k < 1) frame = requestAnimationFrame(step); else finish();
  };
  frame = requestAnimationFrame(step);
  timer = setTimeout(finish, 1500);
}
function sosVesselBind() {
  // the ring fills to its share on the clock, after paint — the arc is drawn
  // empty and the CSS transition carries it to data-target (never rAF-only)
  const settleRings = () => {
    document.querySelectorAll('.sos-ring .arc[data-target]').forEach(a => { a.style.strokeDashoffset = a.getAttribute('data-target'); a.removeAttribute('data-target'); });
  };
  if (cg.active()) settleRings(); else setTimeout(settleRings, 80);
  document.querySelectorAll('[data-vsdone]').forEach(btn => {
    if (btn._bound) return; btn._bound = true;
    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-vsdone');
      btn.disabled = true; btn.textContent = 'Sent…';
      try {
        const d = await api(`/api/tasks/${encodeURIComponent(id)}/complete`, { method: 'POST', body: JSON.stringify({ notes: '' }) });
        if (!d) { btn.disabled = false; btn.textContent = 'Done'; showToast('Could not mark it done — try again in a moment.'); return; }
        sosVesselTravel(() => { showToast('Done — the asker sees it in one tick.'); if (typeof sosRelayLoad === 'function') sosRelayLoad(); if (typeof sosReload === 'function') sosReload(); });
      } catch (e) { btn.disabled = false; btn.textContent = 'Done'; showToast(sosAskFailSays(e)); }
    });
  });
  document.querySelectorAll('[data-vsstuck]').forEach(btn => {
    if (btn._bound) return; btn._bound = true;
    btn.addEventListener('click', () => {
      // inline, never a browser prompt (the phone's rule): the reason field
      // opens under the buttons and sends with one more tap
      const id = btn.getAttribute('data-vsstuck'); const st = btn.getAttribute('data-vsstatus') || '';
      const row = btn.closest('.qa');
      if (!row || row.nextElementSibling?.classList?.contains('sos-vessel__whyrow')) { const inp = row?.nextElementSibling?.querySelector('input'); if (inp) inp.focus(); return; }
      const why = document.createElement('div'); why.className = 'qa sos-vessel__whyrow';
      why.innerHTML = `<input class="sos-vessel__why" maxlength="160" placeholder="What is blocking it? One line — the asker reads it."><button class="pri" data-vssend="1">Send</button>`;
      row.insertAdjacentElement('afterend', why);
      const inp = why.querySelector('input'); inp.focus();
      const send = async () => {
        const text = (inp.value || '').trim();
        if (!text) { inp.focus(); return; }
        // 'blocked' is reachable from assigned / acknowledged / in progress; an
        // ask nobody has acknowledged yet is acknowledged with the note first
        const target = (st === 'open') ? 'acknowledged' : 'blocked';
        try {
          const d = await api(`/api/tasks/${encodeURIComponent(id)}/transition`, { method: 'POST', body: JSON.stringify({ status: target, note: text }) });
          if (!d) { showToast('Could not send it — try again in a moment.'); return; }
          showToast(target === 'blocked' ? 'Marked stuck — the asker reads your reason.' : 'Acknowledged with your reason — tap again once it is yours to mark stuck.');
          why.remove();
          if (typeof sosRelayLoad === 'function') sosRelayLoad(); if (typeof sosReload === 'function') sosReload();
        } catch (e) { showToast(sosAskFailSays(e)); }
      };
      why.querySelector('[data-vssend]').addEventListener('click', send);
      inp.addEventListener('keydown', ev => { if (ev.key === 'Enter') send(); });
    });
  });
}
// What a staffer reads when Done or Stuck does not go through. api() THROWS
// on every non-ok status (it never returns an error object), so the machine
// message was the only thing that ever reached the phone: 'invalid_transition'
// or a bare URL. Each case gets a sentence that says what happened and what
// to do next.
function sosAskFailSays(e) {
  const st = (e && (e.status || e.code)) || 0;
  const body = (e && e.body && e.body.detail) || {};
  const kind = String(body.error || body.message || (e && e.message) || '');
  if (/invalid_transition|not_found|already/i.test(kind) || st === 404 || st === 409)
    return 'That one already moved on — pull down to refresh.';
  if (st === 403 || /forbid|permission/i.test(kind)) return 'That ask is not yours to close.';
  if (st === 0 || /network|fetch|failed to fetch/i.test(kind))
    return 'No signal — nothing was sent yet. Try again when you are back on.';
  return 'That did not go through. Try again in a moment.';
}
function sosBlkRing(b) {
  // WS-H: one ring — the department's week, measured and kept. The ring
  // fills to the share kept on a CSS transition (no frame clock to stall).
  const r = b.ring || { measured: 0, kept: 0, pct: 0, line: '' };
  const C = 2 * Math.PI * 22;
  return `<div class="sec">${sosEsc(b.title || '')}</div><div class="card sos-ring">
    <div class="sos-ring__row">
      <svg viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
        <circle class="track" cx="28" cy="28" r="22"/>
        <circle class="arc" cx="28" cy="28" r="22" style="stroke-dasharray:${C.toFixed(1)};stroke-dashoffset:${C.toFixed(1)}" data-target="${(C * (1 - Math.max(0, Math.min(100, r.pct)) / 100)).toFixed(1)}"/>
      </svg>
      <div>${r.measured ? `<div class="sos-ring__n num">${sosEsc(String(r.kept))} of ${sosEsc(String(r.measured))}</div><div class="sos-ring__k">kept of measured</div>` : `<div class="sos-ring__n" style="font-size:16px">nothing yet</div><div class="sos-ring__k">measured this week</div>`}</div>
    </div>
    ${r.line ? `<div class="pb-s" style="margin-top:8px">${sosEsc(r.line)}</div>` : ''}
  </div>`;
}
function sosBlkChanged(b) {
  // WS-H: "this line changed because of you" — the rule, the old words,
  // the new words, and how many of this person's asks it came from.
  return `<div class="sec">This line changed because of you</div><div class="card sos-changed">
    <div class="pb-s">${sosEsc(b.line || '')}</div>
    <div class="sos-changed__was"><span class="lbl">was</span> <s>${sosEsc(b.before || '')}</s></div>
    <div class="sos-changed__now"><span class="lbl">now</span> ${sosEsc(b.after || '')}</div>
    <div class="sos-changed__meta"><span class="chip ${b.live ? 'lime' : 'mute'}">${b.live ? 'in use now' : 'not in use yet'}</span></div>
  </div>`;
}
function sosBlkCoach(b) {
  // WS-H: the coach's lines — strengths first, then the thing to fix, then
  // today's coaching — each revealed a beat after the last (CSS delay; a
  // stalled clock still lands them, the keyframe fills forward).
  let h = `<div class="sec">${sosEsc(b.title || '')}</div><div class="card sos-coach">`;
  (b.rows || []).forEach((rw, i) => {
    h += `<div class="sos-coach__line" style="animation-delay:${(i * 260)}ms"><span class="lbl">${sosEsc(rw[0])}</span><div>${sosEsc(rw[1])}</div></div>`;
  });
  return h + `</div>`;
}
function sosBlkLanes(b) {
  // WS-H: the loop, for the people who run it — six lanes, honest state.
  const pill = { proven: 'lime', waiting: 'mute', read_only: 'amber' };
  const word = { proven: 'proven', waiting: 'waiting for evidence', read_only: 'not trusted yet' };
  let h = `<div class="sec">${sosEsc(b.title || 'The loop')}</div><div class="card sos-lanes">`;
  (b.lanes || []).forEach(l => {
    h += `<div class="sos-lanes__row"><div class="sos-lanes__l"><b>${sosEsc(l.label)}</b><span class="chip ${pill[l.state] || 'mute'}">${sosEsc(word[l.state] || l.state)}</span></div><div class="pb-s">${sosEsc(l.why || '')}</div></div>`;
  });
  if (b.ledger) h += `<div class="pb-s sos-lanes__next">${sosEsc(b.ledger)}</div>`;
  if (b.next) h += `<div class="pb-s sos-lanes__next">${sosEsc(b.next)}</div>`;
  return h + `</div>`;
}
function sosBlkLeaveReqs(b) {
  // The reason was missing from this card for its whole life (operator
  // escalation 2026-09-16): she decided "David — swap · 16 Sep" with no
  // idea why he asked. The reason now has its own line, the coworker is
  // named on a swap, and the coverage warning is a sentence — it used to
  // arrive as a raw object and print "[object Object]".
  let h = `<div class="sec">${sosEsc(b.title)}</div><div class="card">`;
  (b.reqs || []).forEach(r => {
    h += `<div class="phone-block" style="margin:6px 0" data-lvreq="${sosEsc(r.id)}">
      <div class="pb-t"><b>${sosEsc(r.name)}</b> — ${sosEsc(r.what)}</div>
      <div class="pb-s" style="color:var(--mute)">${sosEsc(r.dates)} · asked ${sosEsc(r.filed)}</div>
      <div class="pb-s" style="margin-top:5px;color:var(--ink);font-size:14px;line-height:1.35;white-space:pre-wrap;overflow-wrap:anywhere">${sosEsc(r.reason || 'No reason given')}</div>
      ${r.with_who ? `<div class="pb-s" style="color:var(--mute)">with ${sosEsc(r.with_who)}${r.gives_back ? ` · takes ${sosEsc(r.gives_back)} back` : ''}</div>` : ''}
      ${r.warn ? `<div class="pb-s" style="color:var(--amber)">${sosEsc(r.warn)}</div>` : ''}
      <div class="qa" style="margin-top:8px">
        <button class="pri" data-lvok="${sosEsc(r.id)}">Approve</button>
        <button data-lvno="${sosEsc(r.id)}">Decline</button>
      </div></div>`;
  });
  return h + `</div>`;
}
async function sosLeaveDecide(id, approve) {
  try {
    const body = { approve: !!approve };
    if (!approve) body.reason = 'Declined from Staff OS';
    await api('/api/people/shift-requests/' + encodeURIComponent(id) + '/decide',
              { method: 'POST', body: JSON.stringify(body) });
    const row = document.querySelector(`[data-lvreq="${CSS.escape(id)}"]`);
    if (row) row.innerHTML = `<div class="pb-s">${approve ? 'Approved — the rota is updated.' : 'Declined — they can see why in their requests.'}</div>`;
  } catch (err) {
    showToast('Could not decide: ' + (err.body?.detail || err.message || 'try again'));
  }
}
document.addEventListener('click', (e) => {
  const ok = e.target.closest('[data-lvok]');
  if (ok) { sosLeaveDecide(ok.getAttribute('data-lvok'), true); return; }
  const no = e.target.closest('[data-lvno]');
  if (no) { sosLeaveDecide(no.getAttribute('data-lvno'), false); }
});
function sosBlkExceptions(b) {
  // Rows are [colour, headline, body] (plain text — the backend stopped
  // sending <b> markup that rendered as literal tags; vet 2026-08-31,
  // S6). A legacy 2-part [colour, text] row still renders.
  let h = `<div class="sec">${sosEsc(b.title)}</div><div class="card">`;
  (b.rows || []).forEach(rw => {
    const head = sosEsc(rw[1] || '');
    const body = rw.length > 2 ? sosEsc(rw[2] || '') : '';
    h += `<div class="exc"><span class="ed" style="background:${sosEsc(rw[0])}"></span>`
      + `<div class="et">${body ? `<b>${head}</b> ${body}` : head}</div></div>`;
  });
  return h + `</div>`;
}
function sosBlkChecklist(b) {
  // Rows are TAPPABLE (operator 2026-07-23): tap marks today's item done,
  // tap again undoes — persisted per staff per day via
  // /api/staffos/checklist/toggle. it[4] flags a signal-driven row (e.g.
  // open-on-time auto-done from the geo check-in) — those never toggle.
  const sid = b.staff_id || sosState.cur || '';
  let h = `<div class="sec">${sosEsc(b.title)}</div><div class="card">`;
  (b.items || []).forEach(it => {
    const st = it[2], cls = st === 2 ? 'done' : st === 1 ? 'now' : '';
    const dot = st === 2 ? sosCheck() : st === 1 ? sosBullet() : '';
    const auto = !!it[4];
    const tap = (!auto && sid)
      ? ` data-chk="${sosEsc(it[1])}" role="button" tabindex="0" onclick="sosChkTap('${sosEsc(sid)}',this)"`
      : '';
    // No +N chip: self-marks mint NOTHING (staff_os.checklist_toggle —
    // "Self-marks award NO points") and the scaffold's it[3] weights drift
    // from the registry that actually pays. Plain check state only.
    h += `<div class="chk ${cls}"${tap}><div class="ct">${sosEsc(it[0])}</div>`
      + `<div class="cdot">${dot}</div><div class="cl">${sosEsc(it[1])}</div>`
      + `</div>`;
  });
  return h + `</div>`;
}

function sosChkTap(staffId, el) {
  if (!el || el.__chkBusy) return;
  const label = el.getAttribute('data-chk');
  if (!label) return;
  el.__chkBusy = true;
  const wasDone = el.classList.contains('done');
  // optimistic flip; server response is the truth, error reverts
  el.classList.toggle('done', !wasDone);
  const dotEl = el.querySelector('.cdot');
  if (dotEl) dotEl.innerHTML = !wasDone ? sosCheck() : '';
  api('/api/staffos/checklist/toggle', {
    method: 'POST',
    body: JSON.stringify({ staff_id: staffId, item_label: label }),
  }).then((res) => {
    el.__chkBusy = false;
    if (!res || !res.ok) throw new Error((res && res.error) || 'toggle failed');
    const marked = !!res.marked;
    el.classList.toggle('done', marked);
    if (dotEl) dotEl.innerHTML = marked ? sosCheck() : '';
  }).catch(() => {
    el.__chkBusy = false;
    el.classList.toggle('done', wasDone);
    if (dotEl) dotEl.innerHTML = wasDone ? sosCheck() : '';
    sosToast('Could not save — try again');
  });
}
function sosBlkBagbar(b) {
  return `<div class="prc-bagbar"><div class="prc-bagbar-row">`
    + `<div class="prc-bagbar-t">${sosEsc(b.txt)}</div>`
    + `<button class="prc-bagbar-btn prc-bagbar-yes" onclick="this.closest('.prc-bagbar').remove();sosToast('Noted')">Done</button>`
    + `<button class="prc-bagbar-x" onclick="this.closest('.prc-bagbar').remove()">&times;</button>`
    + `</div></div>`;
}
// The store's OWN bar on a rep's Day (the Colleague §Move 1). Percent
// and counts only — wall.build_payload is money-gated by construction,
// and it is ONE store's own bar, never a standing (cohesion law).
function sosBlkStoreday(b) {
  const pct = (b.pct == null) ? null : Math.max(0, Math.min(100, b.pct));
  const pace = (b.pace_pct == null) ? '' :
    `<span class="sm" style="color:var(--mute)"> · pace ${b.pace_pct}%</span>`;
  const bar = (pct == null) ? '' :
    `<div class="bar"><i style="width:${pct}%"></i></div>`;
  const head = (pct == null)
    ? `${b.today_orders || 0} piece${(b.today_orders || 0) === 1 ? '' : 's'} today`
    : `${pct}% of the month bar${pace}`;
  return `<div class="sec">${sosEsc(b.title || 'Our store')}</div><div class="card"><div class="cap">`
    + `<div style="flex:1">`
    + `<div style="font-size:12.5px"><b>${head}</b> · ${b.today_orders || 0} today</div>`
    + bar
    + `<div class="sm" style="font-size:10.5px;color:var(--mute);margin-top:5px">${sosEsc(b.line || '')}</div>`
    + `</div></div></div>`;
}
function sosBlkCapture(b) {
  // Personal capture stats only — never any comparison to peers or
  // other stores (operator directive 2026-07-05: cohesive work).
  return `<div class="sec">Capture · walk-in attribution</div><div class="card"><div class="cap">`
    + `<div class="pct">${b.pct}%</div><div style="flex:1">`
    + `<div style="font-size:12.5px">${sosEsc(b.line)}</div>`
    + `<div class="bar"><i style="width:${b.pct}%"></i></div>`
    + `<div class="sm" style="font-size:10.5px;color:var(--mute);margin-top:5px">${sosEsc(b.sub)}</div>`
    + `</div></div></div>`;
}
// The store's own Day Grade (WS-G) — one letter, one streak, no
// comparisons. Coach words; the grade belongs to the STORE's day.
function sosBlkDayGrade(b) {
  const g = b || {};
  const word = g.grade === 'A' ? 'a strong day' : g.grade === 'B' ? 'a good day, one miss' : 'a day to talk about';
  return `<div class="sos-card"><div class="sec">Your store's day</div>
    <div style="display:flex;align-items:baseline;gap:10px">
      <b class="num" style="font-size:26px">${esc(g.grade || '—')}</b>
      <span>${word}${g.streak_days > 1 ? ` · ${g.streak_days} good days running` : ''}</span></div>
    <div class="sos-sub">Graded from the door, the work, the till and the pace — the whole store together, nobody singled out.</div></div>`;
}

// The Morning Page (apex build 01) — yesterday in detail, where we
// left off, one thing EON already did. Keyed to the PIN; compares
// nobody. The greeting above it is already hour-aware and theirs.
function sosBlkMorning(b) {
  const g = b || {};
  const row = r => `<div style="display:flex;gap:8px;align-items:baseline;padding:3px 0">
      <b class="num" style="${r.tone === 'ok' ? 'color:var(--ok,#35d98a)' : r.tone === 'warn' ? 'color:var(--warn,#e8b93e)' : r.tone === 'bad' ? 'color:var(--danger,#e2604f)' : ''}">${esc(r.v)}</b>
      <span>${esc(r.t)}</span></div>`;
  let h = '<div class="sos-card">';
  if ((g.yesterday || []).length) {
    h += `<div class="sec">Yesterday · ${esc(g.date_line || '')}</div>` + g.yesterday.map(row).join('');
  }
  if ((g.left_off || []).length) {
    h += `<div class="sec" style="margin-top:8px">Where we left off</div>` + g.left_off.map(row).join('');
  }
  if (g.overnight) {
    h += `<div class="sec" style="margin-top:8px">While you were away</div><div style="font-size:14px">${esc(g.overnight)}</div>
      <div class="sos-sub">Nothing was sent — it is all waiting for you.</div>`;
  }
  return h + '</div>';
}

function sosBlkPayslip(b) {
  // "My progress" — growth-first (operator directive 2026-07-05: no
  // dangling carrots). Points, level, badges. A single quiet money line
  // renders ONLY when the operator has actually funded the pool; when
  // unfunded the card contains no money words at all.
  const lvl = b.level || {};
  const badges = (b.badges || []).map(x =>
    `<span class="chip" title="${sosEsc(x.label)}">${sosEsc((x.label || '').split(' \u2014 ')[0])}</span>`).join(' ');
  let h = `<div class="sec">My progress \u00b7 ${sosEsc(b.month)}</div><div class="card">`
    + `<div class="kv"><span>Points this month</span><b>${sosEsc(b.points_mtd)} pts \u00b7 ${sosEsc(b.active_days)} active day${b.active_days === 1 ? '' : 's'}</b></div>`
    + `<div class="kv"><span>Level</span><b>${sosEsc(lvl.title || '\u2014')}${lvl.next_at ? ` \u00b7 ${sosEsc(lvl.total_points)}/${sosEsc(lvl.next_at)} to next` : ''}</b></div>`;
  if (b.pool_funded && b.projected_ngn != null) {
    h += `<div class="kv"><span>Pool share this month</span><b>\u20a6${Number(b.projected_ngn).toLocaleString()}</b></div>`;
  }
  if (badges) h += `<div style="margin-top:6px">${badges}</div>`;
  return h + `</div>`;
}
function sosBlkKvcard(b) {
  let h = `<div class="sec">${sosEsc(b.title)}</div><div class="card">`;
  (b.rows || []).forEach(rw => {
    h += `<div class="kv"><span>${sosEsc(rw[0])}</span><b>${sosEsc(rw[1])}</b></div>`;
  });
  return h + `</div>`;
}
function sosBlkZones(b) {
  let h = `<div class="sec">${sosEsc(b.title)}</div><div class="card"><div class="wz">`;
  (b.zones || []).forEach(z => {
    h += `<div class="wzc ${z[3] || ''}"><div class="wzh">${sosEsc(z[0])}</div>`
      + `<div class="wzn">${sosEsc(z[1])}</div><div class="wzs">${sosEsc(z[2])}</div></div>`;
  });
  return h + `</div></div>`;
}
function sosBlkLink(b) {
  let h = `<div class="sec">${sosEsc(b.title)}</div><div class="card">`;
  // PR-F #1 — rows are [LABEL, TITLE, PATH-or-target]. Resolve the destination
  // off rw[2] (the path, e.g. "opens /dispatch · today's run"), falling back to
  // rw[1] (the title) so token-bearing titles like "CRUX Studio" still route.
  // Resolving on the title alone (the old bug) sent "Delivery map"/"Transfer
  // page" nowhere because their token lives only in rw[2].
  // Dests are stashed PER BLOCK (2026-07-25, warehouse-tablet video): the
  // old single global was overwritten by whichever link block rendered
  // LAST — the Learn block below My pages replaced every dest with the
  // Academy's, so "pending transfers" opened /academy on every tap. Each
  // block registers its own dest array and bakes its registry index into
  // the onclick — no free-text in onclick, and blocks can't clobber
  // each other.
  window.__sosLinkDestReg = window.__sosLinkDestReg || [];
  const bi = window.__sosLinkDestReg.length;
  window.__sosLinkDestReg.push((b.rows || []).map(rw =>
    sosResolveDeepLink(rw[2]) || sosResolveDeepLink(rw[1]) || ('open ' + (rw[1] || ''))));
  (b.rows || []).forEach((rw, i) => {
    h += `<div class="lo" onclick="sosBlkLinkNav(${bi},${i})"><div class="li">${sosEsc(rw[0])}</div>`
      + `<div class="lt"><b>${sosEsc(rw[1])}</b><span>${sosEsc(rw[2])}</span></div>`
      + `<div class="la">&rsaquo;</div></div>`;
  });
  return h + `</div>`;
}
// PR-F #1 — dispatch a link-row by (block, row) against the per-block
// dest registry. A real dest navigates; an unresolved 'open <title>' falls
// back to sosQa so non-deep-link rows still acknowledge rather than
// dead-click.
function sosBlkLinkNav(bi, i) {
  const dest = ((window.__sosLinkDestReg || [])[bi] || [])[i];
  if (dest == null) return;
  if (typeof dest === 'string' && dest.indexOf('open ') === 0) { sosQa(dest); return; }
  sosNavDeep(dest);
}
// Hand the app's auth token to the embedded dispatcher iframe. iOS Safari can
// partition the iframe's localStorage so it can't read eon_jwt on its own —
// posting it on load makes the rider map work regardless (the embed only acts
// on it when it has no token of its own; same-origin target only).
function sosPostJwtToMap(ifr) {
  try {
    // A staff session never sends the stored sign-in: it is never this tab's.
    const jwt = State.isStaff ? '' : localStorage.getItem('eon_jwt');
    const tenant = localStorage.getItem('eon_tenant_slug') || '';
    if (jwt && ifr && ifr.contentWindow) {
      ifr.contentWindow.postMessage({ type: 'eon-jwt', jwt, tenant }, location.origin);
    }
  } catch (_) { /* best-effort */ }
}
function sosBlkRun(b) {
  let h = '';
  // PR-F #3 — Patrick inline map. Embed the existing /dispatch app via
  // /dispatch/embed/{rider_id} (PR-D: run.rider_id is Patrick's staff_id since
  // he is emailless — pass it verbatim). A floating chip opens the full-screen
  // PWA for actual riding. If Maps is unconfigured the backend returns the run
  // as a setup kvcard (not this 'run' block), so the iframe degrades to the
  // dispatcher's own setup card rather than blanking.
  if (b.rider_id) {
    // Token rides the URL HASH so the embedded dispatcher gets it even when
    // iOS Safari partitions the iframe's localStorage (hash is not sent to the
    // server / not logged; dispatch.js strips it on read). postMessage onload
    // stays as a belt-and-suspenders second channel.
    let hash = '';
    try {
      const j = State.isStaff ? '' : localStorage.getItem('eon_jwt');   // never the owner's in a staff session
      const t = localStorage.getItem('eon_tenant_slug') || '';
      if (j) hash = '#jwt=' + encodeURIComponent(j) + '&t=' + encodeURIComponent(t);
    } catch (_) { /* best-effort */ }
    const src = '/dispatch/embed/' + encodeURIComponent(b.rider_id) + hash;
    h += `<div class="sos-map"><iframe class="sos-map__f" src="${src}" title="Delivery route" `
      + `loading="lazy" referrerpolicy="same-origin" onload="sosPostJwtToMap(this)"></iframe>`
      + `<button class="sos-map__full" onclick="location.href='/dispatch'">Open full-screen map</button>`
      + `</div>`;
  }
  h += `<div class="sec">${sosEsc(b.title)}</div><div class="card">`;
  // custody chain
  if (b.custody && b.custody.length) {
    h += `<div class="custody">`;
    b.custody.forEach(c => {
      const cls = c[1] === 2 ? 'don' : c[1] === 1 ? 'cur' : '';
      h += `<div class="cs1 ${cls}"><div class="cd"></div>${sosEsc(c[0])}</div>`;
    });
    h += `</div>`;
  }
  // stops
  const stops = b.stops || [];
  stops.forEach(s => {
    const cls = s[3] === 2 ? 'done' : s[3] === 1 ? 'next' : '';
    h += `<div class="stop ${cls}"><div class="sn">${s[3] === 2 ? sosCheck() : sosEsc(s[0])}</div>`
      + `<div class="sx"><div class="st1">${sosEsc(s[1])}</div><div class="st2">${sosEsc(s[2])}</div></div>`
      + (s[4] ? `<div class="sr"><span class="chip lime">${sosEsc(s[4])}</span></div>` : '')
      + `</div>`;
  });
  // PR-F #5 — no-route state (FIX-2 staff_os emits a run block with rider_id +
  // empty stops when Maps is configured but dispatch_routes is empty). The
  // embed self-bootstraps its own setup card above; here we render honest-empty
  // instead of a bare card so the run section reads intentionally, not broken.
  if (!stops.length) {
    h += `<div class="sos-empty" style="padding:14px">`
      + (b.rider_id ? `No stops on today's run yet.` : `Route not set up yet.`)
      + `</div>`;
  }
  return h + `</div>`;
}
// Dispatcher queue (2026-07-23) — rider-carried Lagos orders, one-tap
// Delivered. Third-party dispatch riders don't use the rider PWA, so the
// logistics manager marks the drop here; the backend runs the same chain as
// the rider tap (Shopify fulfill + gated delivered WhatsApp + review), no
// rider points. Payload: {orders:[{order_shopify_id, order_name,
// customer_name, cluster}]}. One tap, no confirm (operator law).
function sosBlkDispatchDone(b) {
  const orders = (b && b.orders) || [];
  sosState.ddOrders = orders;
  let h = `<div class="sec">Dispatcher orders · tap when delivered</div><div class="card sos-trk">`;
  h += `<div class="sos-trk__head"><span>Out with a dispatch rider</span>`
    + `<span class="chip ${orders.length ? 'amber' : 'mute'}">${orders.length} out</span></div>`;
  h += `<div class="sos-trk__note">When the dispatcher drops an order, tap Delivered — the customer gets the delivered message and the order leaves every queue.</div>`;
  if (!orders.length) {
    h += `<div class="sos-trk__empty">No dispatcher orders out right now.</div></div>`;
    return h;
  }
  orders.forEach((o, i) => {
    const who = [o.order_name, o.customer_name].filter(Boolean).join(' · ');
    h += `<div class="sos-trk__row" id="sos-dd-row-${i}">`
      + `<div class="sos-trk__main"><div class="sos-trk__o">${sosEsc(who)}</div>`
      + (o.cluster ? `<div class="sos-trk__sub">${sosEsc(o.cluster)}</div>` : '')
      + `</div>`
      + `<button class="chip lime" id="sos-dd-btn-${i}" onclick="sosDispatchDelivered(${i})">Delivered</button>`
      + `</div>`;
  });
  return h + `</div>`;
}
async function sosDispatchDelivered(i) {
  const o = (sosState.ddOrders || [])[i];
  if (!o || !o.order_shopify_id) return;
  const btn = sosId('sos-dd-btn-' + i);
  if (btn) { btn.disabled = true; btn.textContent = 'Marking…'; }
  let res;
  try {
    res = await api('/api/dispatch/orders/' + encodeURIComponent(o.order_shopify_id) + '/delivered',
                    { method: 'POST' });
  } catch (e) {
    if (btn) { btn.disabled = false; btn.textContent = 'Delivered'; }
    sosToast('Could not mark delivered: ' + ((e && e.message) || e));
    return;
  }
  if (!res || res.ok === false) {
    if (btn) { btn.disabled = false; btn.textContent = 'Delivered'; }
    sosToast('Could not mark delivered: ' + ((res && res.error) || 'try again'));
    return;
  }
  sosToast((o.order_name || 'Order') + ' marked delivered');
  // Drop the row from the cached list + the payload, repaint Day.
  if (Array.isArray(sosState.ddOrders)) sosState.ddOrders.splice(i, 1);
  if (sosState.payload && Array.isArray(sosState.payload.day)) {
    sosState.payload.day.forEach(blk => {
      if (blk && blk[0] === 'dispatchdone' && blk[1] && Array.isArray(blk[1].orders)) {
        blk[1].orders = blk[1].orders.filter(x => x.order_shopify_id !== o.order_shopify_id);
      }
    });
  }
  sosRenderDay();
}
// PR-F #2 — trackingsend block. Out-of-Lagos carrier orders awaiting a waybill
// (PR-D payload: {pending:[{order_shopify_id, order_name, first_name, province,
// city, days_since_paid, has_phone}, ...]}). Rows are pre-sorted oldest-paid
// first by the backend; we render in order. Tap → the structured send sheet
// (reuses the Staff OS task-sheet component). pending may be [] (honest empty).
const SOS_CARRIERS = ['GIG', 'DHL', 'FedEx', 'UPS', 'Aramex', 'Other'];
function sosBlkTrackingSend(b) {
  const pending = (b && b.pending) || [];
  // Cache for the sheet (indexed) — the sheet reads order context by index so
  // a row tap doesn't have to round-trip the payload.
  sosState.trkPending = pending;
  // carrier → tracking-URL template ({waybill} substituted at preview time)
  sosState.trkUrlTemplates = (b && b.url_templates) || {};
  const cnt = pending.length;
  let h = `<div class="sec">Ship out-of-Lagos · add tracking</div><div class="card sos-trk">`;
  h += `<div class="sos-trk__head"><span>Carrier orders awaiting a waybill</span>`
    + `<span class="chip ${cnt ? 'amber' : 'mute'}">${cnt} awaiting</span></div>`;
  // Lagos rider orders never belong in the carrier queue — the backend
  // re-lanes them into Dispatch and reports how many, so the manager knows
  // where they went instead of faking a waybill for them.
  const lagosN = (b && b.lagos_count) || 0;
  if (lagosN) {
    h += `<div class="sos-trk__note">${lagosN} Lagos rider order${lagosN === 1 ? '' : 's'} moved to Dispatch — build today's route there; the rider's Delivered tap updates the customer.</div>`;
  }
  const recN = (b && b.recorded_count) || 0;
  if (recN) {
    h += `<div class="sos-trk__note">${recN} order${recN === 1 ? '' : 's'} cleared — waybill already recorded.</div>`;
  }
  if (!cnt) {
    h += `<div class="sos-trk__empty">No out-of-Lagos orders waiting on tracking.</div></div>`;
    return h;
  }
  pending.forEach((p, i) => {
    const where = sosEsc([p.city, p.province].filter(Boolean).join(', ') || p.province || p.city || '');
    const days = (p.days_since_paid != null) ? `${p.days_since_paid}d since paid` : '';
    const late = (Number(p.days_since_paid) || 0) >= 3;
    h += `<div class="sos-trk__row${late ? ' late' : ''}" onclick="sosOpenTrackingSheet(${i})">`
      + `<div class="sos-trk__main"><div class="sos-trk__o">${sosEsc(p.order_name || '')} · ${sosEsc(p.first_name || '')}</div>`
      + `<div class="sos-trk__sub">${where}${where && days ? ' · ' : ''}${sosEsc(days)}</div></div>`
      + `<div class="sos-trk__chev">&rsaquo;</div></div>`;
  });
  return h + `</div>`;
}

// Bottom sheet for one pending order — reuses the Staff OS sheet (#sos-sheet,
// the same component the task sheet uses). Carrier segmented control + waybill
// input + LIVE WhatsApp preview + the primary send. No emoji.
function sosOpenTrackingSheet(i) {
  const p = (sosState.trkPending || [])[i];
  if (!p) return;
  sosState.trkActive = { idx: i, order: p, carrier: 'GIG', waybill: '' };
  const sh = sosId('sos-sheet');
  if (!sh) return;
  sh.innerHTML = sosTrackingSheetHtml();
  sh.classList.add('on');
  // focus the waybill input after the sheet animates in
  requestAnimationFrame(() => { const inp = sosId('sos-trk-waybill'); if (inp) inp.focus(); });
}
function sosTrackingSheetHtml() {
  const a = sosState.trkActive || {};
  const p = a.order || {};
  const where = sosEsc([p.city, p.province].filter(Boolean).join(', ') || p.province || p.city || '');
  let seg = '';
  SOS_CARRIERS.forEach(c => {
    const on = (a.carrier === c) ? ' on' : '';
    seg += `<button class="sos-trk-seg__b${on}" onclick="sosTrackingPickCarrier('${c}')">${sosEsc(c)}</button>`;
  });
  const noPhone = (p.has_phone === false);
  return `<div class="pane" onclick="event.stopPropagation()"><div class="grab"></div>`
    + `<div class="scw-sheet__h"><div class="scw-sheet__t">Add tracking</div>`
    + `<button class="scw-x" onclick="sosCloseSheet()">&times;</button></div>`
    + `<div class="sos-trk-sheet__o"><b>${sosEsc(p.order_name || '')}</b>`
    + `<span>${sosEsc(p.first_name || '')}${where ? ' · ' + where : ''}</span></div>`
    + `<div class="as-lbl">CARRIER</div>`
    + `<div class="sos-trk-seg">${seg}</div>`
    + `<div class="as-lbl">WAYBILL NUMBER</div>`
    + `<input class="sos-trk-input" id="sos-trk-waybill" placeholder="Enter waybill / tracking no." autocomplete="off"`
    + ` value="${sosEsc(a.waybill || '')}" oninput="sosTrackingOnWaybill(this.value)">`
    + `<div class="as-lbl">CUSTOMER WILL RECEIVE</div>`
    + `<div class="sos-trk-prev" id="sos-trk-prev">${sosTrackingPreview()}</div>`
    + (noPhone ? `<div class="sos-trk-chip warn">No phone on this order — copy the waybill and send it another way.</div>` : '')
    + `<div class="sos-trk-result" id="sos-trk-result"></div>`
    + `<button class="bigdone" id="sos-trk-send" onclick="sosTrackingSend()">Send tracking to customer</button>`
    + `</div>`;
}
function sosTrackingPreview() {
  const a = sosState.trkActive || {};
  const p = a.order || {};
  const wb = (a.waybill || '').trim() || '‹waybill›';
  // Mirror the shipped_carrier template tone (quiet authority, no emoji).
  // The tracking LINK rides along when the carrier has one configured
  // (operator 2026-07-20) — same URL the backend stamps on the lifecycle.
  const url = sosTrackingUrl(a.carrier, wb);
  return `Hi ${sosEsc(p.first_name || 'there')}, your CRUX order ${sosEsc(p.order_name || '')} has shipped via `
    + `${sosEsc(a.carrier || 'GIG')}. Tracking: ${sosEsc(wb)}.`
    + (url ? ` Track it here: ${sosEsc(url)}` : '');
}
function sosTrackingUrl(carrier, waybill) {
  const t = (sosState.trkUrlTemplates || {})[String(carrier || 'GIG').toLowerCase()];
  if (!t || !waybill || waybill === '‹waybill›') return '';
  return t.replace('{waybill}', waybill);
}
function sosTrackingPickCarrier(c) {
  if (!sosState.trkActive) return;
  sosState.trkActive.carrier = c;
  document.querySelectorAll('#sos-sheet .sos-trk-seg__b').forEach(b => {
    b.classList.toggle('on', b.textContent === c);
  });
  const prev = sosId('sos-trk-prev'); if (prev) prev.innerHTML = sosTrackingPreview();
}
function sosTrackingOnWaybill(v) {
  if (!sosState.trkActive) return;
  sosState.trkActive.waybill = v;
  const prev = sosId('sos-trk-prev'); if (prev) prev.innerHTML = sosTrackingPreview();
}
// POST /api/dispatch/tracking/send {order_shopify_id, carrier, waybill_number}.
// Success → toast + drop the row. Non-send result (gated_off /
// template_unconfigured / no_phone) → reason chip + Copy waybill; row stays.
async function sosTrackingSend() {
  const a = sosState.trkActive;
  if (!a) return;
  const wb = (a.waybill || '').trim();
  if (!wb) { const r = sosId('sos-trk-result'); if (r) r.innerHTML = `<div class="sos-trk-chip warn">Enter a waybill number first.</div>`; return; }
  const btn = sosId('sos-trk-send');
  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
  let res;
  try {
    res = await api('/api/dispatch/tracking/send', {
      method: 'POST',
      body: JSON.stringify({
        order_shopify_id: a.order.order_shopify_id,
        carrier: a.carrier,
        waybill_number: wb,
      }),
    });
  } catch (e) {
    if (btn) { btn.disabled = false; btn.textContent = 'Send tracking to customer'; }
    const r = sosId('sos-trk-result');
    if (r) r.innerHTML = sosTrackingReasonChip((e && e.message) || 'send_failed', wb);
    return;
  }
  const status = (res && (res.send_status || res.status)) || 'sent';
  const sent = (res && res.ok !== false) && (status === 'sent' || res.sent === true);
  if (sent) {
    sosToast('Tracking sent to ' + (a.order.first_name || 'customer'));
    // Drop the row from the cached pending list + repaint Day.
    if (Array.isArray(sosState.trkPending)) sosState.trkPending.splice(a.idx, 1);
    if (sosState.payload && Array.isArray(sosState.payload.day)) {
      sosState.payload.day.forEach(blk => {
        if (blk && blk[0] === 'trackingsend' && blk[1] && Array.isArray(blk[1].pending)) {
          blk[1].pending = blk[1].pending.filter(x => x.order_shopify_id !== a.order.order_shopify_id);
        }
      });
    }
    sosCloseSheet();
    sosState.trkActive = null;
    sosRenderDay();
    return;
  }
  // Non-send result: keep the row, show why + Copy waybill.
  if (btn) { btn.disabled = false; btn.textContent = 'Send tracking to customer'; }
  const r = sosId('sos-trk-result');
  if (r) r.innerHTML = sosTrackingReasonChip(status, wb);
}
function sosTrackingReasonChip(status, waybill) {
  const reason = {
    gated_off: 'Live customer sends are paused (go-live gate). Copy the message to send it manually.',
    template_unconfigured: 'The shipping template is not approved yet. Copy the message to send it manually.',
    no_phone: 'No phone on this order. Copy the message to send it another way.',
    send_failed: 'Send failed. Copy the message and retry shortly.',
  }[status] || ('Could not send (' + sosEsc(String(status)) + '). Copy the message.');
  // Copies the FULL customer message incl. the tracking link — a manual
  // send should read exactly like the live template would.
  return `<div class="sos-trk-chip warn">${reason}</div>`
    + `<button class="sos-trk-copy" onclick="sosTrackingCopyMsg()">Copy message</button>`;
}
function sosTrackingMsgPlain() {
  const a = sosState.trkActive || {};
  const p = a.order || {};
  const wb = (a.waybill || '').trim();
  const url = sosTrackingUrl(a.carrier, wb);
  return `Hi ${p.first_name || 'there'}, your CRUX order ${p.order_name || ''} has shipped via `
    + `${a.carrier || 'GIG'}. Tracking: ${wb}.` + (url ? ` Track it here: ${url}` : '');
}
function sosTrackingCopyMsg() {
  const msg = sosTrackingMsgPlain();
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(msg).then(() => sosToast('Message copied'), () => sosToast(msg));
    } else { sosToast(msg); }
  } catch (_) { sosToast(msg); }
}
function sosTrackingCopy(wb) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(wb).then(() => sosToast('Waybill copied'), () => sosToast(wb));
    } else { sosToast(wb); }
  } catch (_) { sosToast(wb); }
}

function sosBlkGauges(b) {
  let h = `<div class="sec">${sosEsc(b.title)}</div><div class="card"><div class="scw-gauges">`;
  (b.items || []).forEach(g => {
    h += `<div class="scw-gauge ${g[2] || ''}"><b>${sosEsc(g[0])}</b><span>${sosEsc(g[1])}</span></div>`;
  });
  return h + `</div></div>`;
}
function sosBlkApprovals(b) {
  const generation = String((sosState.financeGeneration || 0) + 1);
  sosState.financeGeneration = Number(generation);
  const registry = {generation, context:sosFinanceContext(), cards:new Map()};
  sosState.financeReview = registry;
  let h = `<div class="sec">${sosEsc(b.title)}</div><div class="card">`;
  (b.cards || []).forEach(c => {
    const source = c.decision;
    const actionable = registry.context && typeof c.id === 'string' && c.id.trim() && source && String(source.id) === c.id && source.status === 'pending';
    if (actionable) registry.cards.set(String(c.id), {snapshot:prcDecisionSnapshot(source), phase:'idle', sourceCard:c});
    h += `<div class="prc-card" data-finance-review="${generation}" data-finance-request="${sosEsc(c.id || '')}"><div class="prc-card-top">`
      + `<div class="prc-card-title">${sosEsc(c.title)}</div>`
      + `<div class="prc-card-amt">${sosEsc(c.amt)}</div></div>`
      + `<div class="prc-card-meta">${sosEsc(c.meta)}</div>`
      + `<div class="prc-card-lines">${sosEsc(c.lines)}</div>`;
    if (['approved', 'rejected', 'paid'].includes(c.mode)) {
      const savedState = {approved:'APPROVED', rejected:'REJECTED', paid:'PAYMENT RECORDED'}[c.mode];
      h += `<div class="prc-card-actions"><span class="chip ${c.mode === 'rejected' ? '' : 'mint'}">${savedState}</span></div>`;
    } else {
      // The existing deliberate decision gesture stays in the card; payment
      // is a separate action. Approve = press-and-HOLD
      // (the fill completing IS the confirmation); reject = inline reason
      // field in place of the buttons — window.confirm/prompt retired.
      h += actionable
        ? `<div class="prc-card-actions">`
          + `<button class="prc-act prc-act-ok sos-hold" data-req="${sosEsc(c.id)}" data-title="${sosEsc(c.title)}"`
          + ` onpointerdown="sosHoldStart(this,event)" onpointerup="sosHoldCancel(this)"`
          + ` onpointerleave="sosHoldCancel(this)" onpointercancel="sosHoldCancel(this)"`
          + ` onkeydown="sosHoldStart(this,event)" onkeyup="sosHoldCancel(this)"`
          + ` oncontextmenu="return false" title="Hold to approve this request — payment is separate">`
          + `<i class="sos-hold__fill" aria-hidden="true"></i><span>Hold to approve</span></button>`
          + `<button class="prc-act" onclick="sosRejectAsk(this)" data-req="${sosEsc(c.id)}" data-title="${sosEsc(c.title)}">Reject</button></div>`
        : `<div class="prc-card-actions"><span class="chip">PENDING · decide on the Money page</span></div>`;
    }
    h += `<p data-finance-result role="status" tabindex="-1"></p><button class="prc-act" type="button" data-finance-check data-req="${sosEsc(c.id || '')}" onclick="sosFinanceCheck(this)" hidden>Check saved result</button></div>`;
  });
  return h + `</div>`;
}
function sosBlkDept(b) {
  let h = `<div class="sec">${sosEsc(b.title)}</div><div class="card"><div class="dcg">`;
  (b.tiles || []).forEach(t => {
    h += `<div class="dc"><div class="dn"><span class="dot" style="background:${t[1]}"></span>${sosEsc(t[0])}</div>`
      + `<div class="dv">${sosEsc(t[2])}</div><div class="dm">${sosEsc(t[3])}</div></div>`;
  });
  return h + `</div></div>`;
}
function sosBlkReps(b) {
  let h = `<div class="sec">${sosEsc(b.title)}</div><div class="card">`;
  (b.rows || []).forEach((rw) => {
    if (rw.grp) { h += `<div class="tm-grp"><span>${sosEsc(rw.grp)}</span></div>`; return; }
    const flag = rw.flag ? ` <span class="chip red">${sosEsc(rw.flag)}</span>` : '';
    h += `<div class="tm-rep">`
      + `<div class="rav" style="background:${SOS_G[rw.grad] || rw.grad || SOS_G.blue}">${sosEsc(rw.init)}</div>`
      + `<div class="rx"><div class="rn">${sosEsc(rw.n)}${flag}</div>`
      + `<div class="rs">${sosEsc(rw.role)} · ${sosEsc(rw.orders)} ord · att ${sosEsc(rw.att)} · capt ${sosEsc(rw.capt)}</div></div>`
      + `<div class="rv"><b>${sosEsc(rw.rev)}</b><span>${rw.pts == null ? '—' : sosEsc(rw.pts) + ' pts'}</span></div>`
      + ((rw.sid && String(rw.sid) !== String(sosState.cur || ''))
          ? `<button class="sos-prop" data-prop="${sosEsc(rw.sid)}" title="Props: +1 to ${sosEsc(rw.n)}">+1</button>` : '')
      + `</div>`;
  });
  return h + `</div>`;
}

// ════════════════════════ TEAM VIEW (managers) ═══════════════════
function sosRenderTeam() {
  const d = sosR();
  // Managers AND the CS lead (desk-scoped team payload) — matching the
  // tabbar gate; the two drifted and her built Team view never rendered.
  if (!(d.mgr || d.is_cs_lead) || !d.team) { sosId('sos-v-team').innerHTML = ''; return; }
  // Team's link blocks append to the same registry Day resets — appending
  // here (not resetting) keeps a Day painted in the background tappable.
  window.__sosLinkDestReg = window.__sosLinkDestReg || [];
  const n = (d.team.staff || []).length;
  let h = '';
  // Coverage strip (tail-doors 2026-07-27): GET /api/attendance/coverage was
  // built with no consumer. One card at the top of Team: per store expected /
  // present / late / missing, red-accented when an OPEN store has nobody in.
  // Gate = posIsLead() — the #1491 LEAD-ONLY pattern — which is the exact
  // client mirror of the endpoint's require(P.ANALYTICS_STAFF_VIEW).
  const covLead = typeof posIsLead === 'function' && posIsLead();
  if (covLead) h += `<div id="sos-cov-slot"></div>`;
  h += d.team.lead
    ? `<div class="sec">My desk · ${n} ${n === 1 ? 'person' : 'people'}</div>`
    : `<div class="sec">My team · ${n} report${n === 1 ? '' : 's'}</div>`;
  (d.team.blocks || []).forEach(b => { h += sosRenderBlock(b[0], b[1]); });
  // Managers only — sosQa refuses assignment for non-managers, and a
  // button that always refuses is worse than no button (vet round 2).
  if (d.mgr) h += `<button class="assignbtn" onclick="sosQa('Assign a task')">+ Assign a task to my team</button>`;
  sosId('sos-v-team').innerHTML = h;
  if (covLead) sosCovFetch();
}

// Fill the Team-tab coverage slot from /api/attendance/coverage (async so
// Team never blanks waiting on it). Honest states: loading shimmer, a plain
// "couldn't load" line on failure, "no geofenced stores" when empty.
async function sosCovFetch() {
  const slot = sosId('sos-cov-slot');
  if (!slot) return;
  slot.innerHTML = `<div class="sec">Store coverage · today</div>`
    + `<div class="card sos-cov"><div class="sm sos-loading">Checking who's in…</div></div>`;
  let d = null;
  try { d = await api('/api/attendance/coverage'); } catch (_) {}
  const s2 = sosId('sos-cov-slot');
  if (!s2) return;   // tab re-rendered away while we awaited
  if (!d || !d.ok) {
    s2.innerHTML = `<div class="sec">Store coverage · today</div>`
      + `<div class="card sos-cov"><div class="sm">Couldn't load coverage.</div></div>`;
    return;
  }
  // covering / covering_elsewhere (2026-08-20): with location change on,
  // someone can be present at a store that is not their home. The backend
  // names them on BOTH cards — the away store ("Bola, from Palms") and the
  // home store ("Bola → Chevron") — so the empty-store alarm can say where
  // its person went instead of firing with no name under it.
  const covName = c => (typeof c === 'string' ? c : (c && c.name) || '');
  const rows = (d.stores || []).map(st => {
    const present = (st.present || []).length;
    const late = (st.late || []).length;
    const missing = st.missing || [];
    const covering = st.covering || [];              // people filling in HERE
    const elsewhere = st.covering_elsewhere || [];   // home people who went out
    const dark = !!st.open_now && (st.expected || 0) > 0 && present === 0;
    const coverLine = covering.map(c => {
      const h = (c && c.home) ? ` (from ${sosEsc(c.home)})` : '';
      return sosEsc(covName(c)) + h;
    }).join(', ');
    const outLine = elsewhere.map(c => {
      const at = (c && c.at) ? sosEsc(c.at) : 'another store';
      return sosEsc(covName(c)) + ' → ' + at;
    }).join(', ');
    return `<div class="sos-cov__row${dark ? ' sos-cov__row--dark' : ''}">`
      + `<div class="sos-cov__hd"><b>${sosEsc(st.location || '—')}</b>`
      + `<span class="sos-cov__n">${present}/${st.expected || 0} in</span>`
      + (late ? `<span class="sos-cov__late">${late} late</span>` : '')
      + `</div>`
      + (missing.length ? `<div class="sos-cov__miss">Missing: ${sosEsc(missing.join(', '))}</div>` : '')
      + (coverLine ? `<div class="sos-cov__cover">Covering: ${coverLine}</div>` : '')
      + (outLine ? `<div class="sos-cov__cover">Covering elsewhere: ${outLine}</div>` : '')
      + (dark ? `<div class="sos-cov__alert">Store is open — nobody has checked in</div>` : '')
      + `</div>`;
  }).join('');
  s2.innerHTML = `<div class="sec">Store coverage · today</div>`
    + `<div class="card sos-cov">${rows || '<div class="sm">No geofenced stores yet.</div>'}</div>`;
}

// ════════════════════════ CHAT VIEW ══════════════════════════════
// ONE chat shell for EVERYONE (2026-06-18). Every staff member — CS unit,
// store-sales, logistics, inventory, marketing, ops — renders the SAME CS
// workspace (csw) inbox. There is no separate "Team-only" UI and no pinned Team
// row any more: Team lives as a LENS (a tab) inside this one shell, alongside
// the Customer lenses (Awaiting / All / Mine). The only conceptual split is
// TEAM vs CUSTOMERS.
//
// SAFETY (E1, do not regress): the Customers list is scoped SERVER-SIDE in
// /api/cs/threads from staff_os SERVER TRUTH — full desk → all; queue-locked
// (store-sales / logistics) → routed only; everyone else → EMPTY. So mounting
// the same shell for a non-CS staffer never leaks the 120-customer desk: the
// server returns only what their role allows. lockedQueue below only NARROWS
// within that scope (it can never widen it — the server ignores a widening
// queue param).
function sosRenderChat() {
  const d = sosR();
  const view = sosId('sos-v-chat');
  if (!view) return;

  // The real CS workspace, verbatim, as a TRUE FULL PAGE for every role.
  // .sos-csfull (staff_os.css) hides the Staff OS header AND the bottom footer/
  // tabbar so the inbox fills the viewport exactly like standalone /cs.
  cswState().mode = 'inbox';
  view.innerHTML = renderCsInbox();
  // queue-lock: store-sales + logistics carry a chat_queue → narrow the inbox
  // to ONLY their routed customers (the server enforces this too). Full-desk CS
  // / owner-admin / ops have no chat_queue → '' (server returns their full
  // allowed scope). A no-scope role (inventory, marketing, content) simply gets
  // an empty Customers list back from the server — the Team lens is their line.
  cswState().lockedQueue = (typeof d.chat_queue === 'string' && d.chat_queue) ? d.chat_queue : '';
  // Role-switcher: scope the CS inbox to the VIEWED staff member (sosState.cur).
  // The server clamps this to owner/admin previews and narrow-only, so an owner
  // previewing Frank (Inventory) sees Frank's empty scope — the fix for CS-rep
  // conversations bleeding into a non-CS role's Chat tab. Cleared on standalone
  // /cs (bindCsInbox path) so a real CS rep's own session is unaffected.
  cswState().asViewStaffId = sosState.cur || '';
  // "Mine" reflects threads routed/assigned to the ACTING rep (the selected
  // staff), not the authenticated owner. renderCsInbox reset asRepEmail to '';
  // set it to this person's email so the Mine count/filter matches
  // assigned_rep_email.
  // Emailless staff claim under their staff_id (#1553) — fall back to the
  // viewed staff id so the preview's Mine matches their claims too.
  cswState().asRepEmail = (d.email
    || ((sosState.roster || []).find(r => r.staff_id === sosState.cur) || {}).email
    || sosState.cur
    || '').toLowerCase();
  bindCsInbox();
  sosId('sos-root')?.classList.add('sos-csfull');
  const shellEl = document.getElementById('csw');
  shellEl?.classList.add('csw-embedded');
  if (cswState().lockedQueue) shellEl?.classList.add('csw-locked');
  else shellEl?.classList.remove('csw-locked');
  // With the footer hidden, the top-left back chevron (.csw-back, aria "Home")
  // is the ONLY way out. cloneNode strips bindCsInbox's standalone handler
  // (which did State.view='home'); rewire it to go back to the Staff OS Day/home
  // view — where the footer returns.
  const back = sosId('csw-back');
  if (back) {
    const fresh = back.cloneNode(true);
    back.parentNode.replaceChild(fresh, back);
    const toWorkroom=!!eonWorkroomChatEntry && eonWorkroomOperations && eonWorkroomAvailable();
    fresh.setAttribute('aria-label',toWorkroom?'Return to Workroom':'Return to your day');
    fresh.addEventListener('click', () => {
      if(eonWorkroomOperations && eonWorkroomAvailable() && eonWorkroomChatEntry){
        // Leave the Chat surface before rendering Workroom; no hidden polling or presence.
        sosState.tab='day';
        const s=cswState();
        if(s.poll){clearInterval(s.poll);s.poll=null;}
        cswStopRealtime();cswHeldTimerStop();
        sosClearCsFull();eonWorkroomChatEntry=null;eonWorkroomOperations=false;render();
      }else sosSwitchTab('day');
    });
  }
}

// Clear the CS full-screen embed classes so the normal Staff OS shell (header
// + standard view stack) returns. Called from every non-chat tab switch and on
// role switch. Idempotent — safe to call when not in embed.
function sosClearCsFull() {
  sosId('sos-root')?.classList.remove('sos-csfull');
  document.getElementById('csw')?.classList.remove('csw-embedded');
  // Every caller of this is a walk-out: a non-chat tab, a role switch, or the
  // landing that renders Chat eagerly and then lands on Day. Each one must let
  // go of the open conversation, or the next visit to Chat re-opens it instead
  // of showing the list. Same rule cswTabGo already applies to the desk's own
  // tabs — "a tab is a navigation OUT of wherever you stand".
  cswLeaveDesk();
}

// ════════════════════════ TEAM CHAT (folded into the ONE shell) ════
// The standalone Staff-OS "Team" section, the pinned CS team row, and the
// separate sosRenderTeamConvo path are GONE (2026-06-18). Team is now a LENS
// inside the unified CS workspace (cswRenderTeamList / cswOpenTeamThread / the
// cswTeam* family). Two thin shims remain so the rest of Staff OS still
// compiles: the Chat tab badge (sosTeamUnread) and the role-switch load
// (sosLoadTeam) both delegate to the csw team state.
function sosTeamUnread() { return (typeof cswTeamUnread === 'function') ? cswTeamUnread() : 0; }
function sosLoadTeam() { if (typeof cswLoadTeam === 'function') return cswLoadTeam(); }


// ════════════════════════ TASKS VIEW ════════════════════════════
function sosTaskRow(t, sec) {
  const rec = t.rec ? `<span class="trec">EON REC</span>` : '';
  // Points chip only when a REAL awarded-points value exists (verified
  // tasks); omit it rather than fake a flat +10 (backend sends p:null).
  const pts = (t.p != null) ? `<div class="tp">+${t.p}</div>` : '';
  // Pack tasks (2026-08-02): the confirm is ONE tap on the row itself
  // — "Packed" completes right here (the sheet stays available for
  // detail). Backend sends cta only for the pack category.
  const quick = (t.cta && !t.done && t.id)
    ? `<button class="packtap" onclick="event.stopPropagation();sosCompleteTask('${sosEsc(t.id)}')">${sosEsc(t.cta)}</button>`
    : '';
  // v3 (floor-6 grammar on the phone): the VERIFIED stamp lands ONLY on
  // ver (EON/manager confirmed) — never on a plain self-tick. Real
  // awarded points ride inside the stamp (the bare .tp chip hides under
  // it via CSS so the right edge carries ONE signal).
  const stamp = t.ver
    ? `<span class="sos-stamp">VERIFIED${t.p != null ? ' · +' + t.p : ''}</span>`
    : '';
  // Subtask progress pips — row-level glance; the sheet keeps the list.
  let pips = '';
  if (t.sub_total) {
    let dots = '';
    for (let i = 0; i < Math.min(t.sub_total, 8); i++) dots += `<i class="${i < (t.sub_done || 0) ? 'on' : ''}"></i>`;
    pips = `<div class="sos-subprog" aria-label="${t.sub_done || 0} of ${t.sub_total} subtasks done">${dots}</div>`;
  }
  // Ask EON on every card (the drawer that answers) — opens the sheet
  // with the ask input focused. Needs a real task id to converse about.
  const ask = t.id
    ? `<button class="sos-askrow" onclick="event.stopPropagation();sosOpenTask('${sec}',${t._i},1)">ask eon</button>`
    : '';
  return `<div class="task ${t.done ? 'done' : ''}" ${cg.active() ? `data-cg-state="${sosEsc(t.ver ? 'verified' : t.done ? 'review' : (t.status || 'open'))}"` : ''} onclick="sosOpenTask('${sec}',${t._i})">${stamp}`
    + `<div class="tick">${t.done ? sosCheck() : ''}</div>`
    + `<div style="flex:1"><div class="tt">${sosEsc(t.t)}${rec}</div>`
    + pips
    + `<div class="ts">${sosEsc(t.s)}${ask ? ' ' : ''}${ask}</div></div>`
    + quick + pts + `</div>`;
}
function sosRenderTasks() {
  const d = sosR(), tk = d.tasks || { over: [], today: [], up: [] };
  // index tasks so the sheet can find them
  (tk.over || []).forEach((t, i) => t._i = i);
  (tk.today || []).forEach((t, i) => t._i = i);
  (tk.up || []).forEach((t, i) => t._i = i);
  let h = '';
  // v3 load rail (floor-6 grammar): done · due · open at a glance. Due =
  // everything the clock is already pressing (overdue + today's undone);
  // open = the upcoming tail. Hidden when there is nothing to show.
  const railDone = (tk.today || []).filter(t => t.done).length;
  const railDue = (tk.over || []).length + (tk.today || []).filter(t => !t.done).length;
  const railOpen = (tk.up || []).length;
  const railN = railDone + railDue + railOpen;
  if (railN) {
    const w = n => n ? Math.max(4, Math.round(n / railN * 100)) : 0;
    h += `<div class="sos-loadrail" role="img" aria-label="${railDone} done, ${railDue} due, ${railOpen} open">`
      + `<span>${railDone} done</span>`
      + `<span class="seg"><i class="don" style="width:${w(railDone)}%"></i><i class="due" style="width:${w(railDue)}%"></i><i class="opn" style="width:${w(railOpen)}%"></i></span>`
      + `<span>${railDue} due · ${railOpen} open</span></div>`;
  }
  if ((tk.over || []).length) {
    h += `<div class="tsec over"><span>OVERDUE</span><span>${tk.over.length}</span></div>`;
    tk.over.forEach(t => h += sosTaskRow(t, 'over'));
  }
  const todayDone = (tk.today || []).filter(t => t.done).length;
  h += `<div class="tsec today"><span>TODAY</span><span>${todayDone}/${(tk.today || []).length} done</span></div>`;
  (tk.today || []).forEach(t => h += sosTaskRow(t, 'today'));
  if ((tk.up || []).length) {
    h += `<div class="tsec up"><span>UPCOMING</span><span>${tk.up.length}</span></div>`;
    tk.up.forEach(t => h += sosTaskRow(t, 'up'));
  }
  if (!(tk.over || []).length && !(tk.today || []).length && !(tk.up || []).length) {
    h += `<div class="note">No tasks assigned to you right now.</div>`;
  }
  // DONE - LAST 14 DAYS (feedback quartet 2026-07-25): completed work was
  // unreviewable the moment it was marked done. Collapsed by default so
  // history never crowds the live list; verified rows show real points.
  const dn = tk.done || [];
  if (dn.length) {
    dn.forEach((t, i) => t._i = i);
    const open = !!sosState._doneOpen;
    h += `<div class="tsec donefold" onclick="sosToggleDone()" style="cursor:pointer">`
      + `<span>${open ? '\u25be' : '\u25b8'} DONE \u00b7 LAST 14 DAYS</span><span>${dn.length}</span></div>`;
    if (open) dn.forEach(t => h += sosTaskRow(t, 'done'));
  }
  if (d.mgr) {
    h += `<button class="assignbtn" onclick="sosQa('Assign a task')">+ Assign a task</button>`;
  }
  sosId('sos-v-tasks').innerHTML = h;
}
function sosToggleDone() {
  sosState._doneOpen = !sosState._doneOpen;
  sosRenderTasks();
}
function sosOpenTask(sec, i, askFocus) {
  const t = ((sosR().tasks || {})[sec] || [])[i];
  if (!t) return;
  const dt = t.detail || {};
  let subs = '';
  (dt.subs || []).forEach(s => {
    subs += `<div class="subt"><div class="tick" style="width:18px;height:18px">${s[1] ? sosCheck() : ''}</div>${sosEsc(s[0])}</div>`;
  });
  // v3: the readonly eonq/eona pair (which the backend never filled)
  // becomes a REAL conversation — the per-task drawer that answers.
  // Staff sessions ride the no-tools staffos door; owner/manager
  // sessions ride the tooled /api/tasks drawer. History paints async.
  let eon = '';
  if (t.id) {
    eon = `<div class="askeon sos-askdrawer"><div class="lbl">ASK EON</div>`
      + `<div class="sos-ask-log" id="sos-ask-log" data-task="${sosEsc(t.id)}"></div>`
      + `<div class="sos-ask-row">`
      + `<input id="sos-ask-in" placeholder="Ask about this task…" autocomplete="off"`
      + ` onkeydown="sosAskKey(event,'${sosEsc(t.id)}')">`
      + `<button class="sos-ask-send" aria-label="Send" onclick="sosAskSend('${sosEsc(t.id)}')">${sosSendIcon()}</button>`
      + `</div></div>`;
  }
  let dep = dt.dep ? `<div class="tmeta"><div class="tm1" style="min-width:100%"><span>DEPENDENCY</span><b>${sosEsc(dt.dep)}</b></div></div>` : '';
  const h = `<div class="pane" onclick="event.stopPropagation()"><div class="grab"></div>`
    + `<div class="scw-sheet__h"><div class="scw-sheet__t">${sosEsc(t.t)}</div>`
    + `<button class="scw-x" onclick="sosCloseSheet()">&times;</button></div>`
    + `<div style="font-size:12.5px;color:var(--ink-dim);margin:4px 0 2px">${sosEsc(dt.desc)}</div>`
    + `<div class="sla"><span class="mono sos-slalbl">SLA</span>`
    + `<div class="bar"><i style="width:${dt.sla || 0}%"></i></div></div>`
    + `<div class="prc-quiet">${sosEsc(dt.slaTxt)}</div>`
    + `<div class="tmeta"><div class="tm1"><span>ASSIGNED BY</span><b>${sosEsc(dt.by)}</b></div>`
    + `<div class="tm1"><span>VERIFIED BY</span><b>${sosEsc(dt.verify)}</b></div></div>`
    + dep
    + (subs ? `<div class="as-lbl">SUBTASKS</div>${subs}` : '')
    + eon
    + (t.done
        ? `<button class="bigdone" disabled>Completed</button>`
        // Photo-verified categories (inventory_count, cash_deposit) reject
        // completion until a verification photo exists — Staff OS had no
        // camera anywhere, so those tasks were a dead end (2026-07-25 vet).
        // The capture input lives here and the CTA swaps to it when the
        // backend says a photo is required.
        : `<input type="file" accept="image/*" capture="environment"
             id="sos-verify-file" style="display:none">`
          + `<button class="bigdone" id="sos-task-cta"
               onclick="sosCompleteTask('${sosEsc(t.id || '')}')">`
          + `${sosEsc(t.cta || 'Mark done')}${t.p != null ? ' · +' + t.p + ' pts' : ''}</button>`
          + `<div class="sos-verifynote" id="sos-verifynote" hidden></div>`)
    + ((sosR().mgr && !t.done && t.id)
        ? `<div class="sos-taskadmin">`
          + `<button class="mgrghost" onclick="sosTaskEditOpen('${sec}',${i})">Fix task</button>`
          + `<button class="mgrghost danger" onclick="sosTaskCancelOpen('${sec}',${i})">Cancel task</button>`
          + `</div>`
        : '')
    + `</div>`;
  const sh = sosId('sos-sheet'); sh.innerHTML = h; sh.classList.add('on');
  if (t.id) {
    sosAskLoad(t.id);
    if (askFocus) setTimeout(() => sosId('sos-ask-in')?.focus(), 60);
  }
}
function sosCloseSheet() { const sh = sosId('sos-sheet'); if (sh) { sh.classList.remove('on'); sh.innerHTML = ''; } }

// ── Per-task Ask EON drawer (Staff OS v3) ────────────────────────
// One door per session kind: staff PIN sessions hit the staffos route
// (no tools, own-task only — server-enforced); owner/manager sessions
// hit the tooled manager drawer. Same painted thread either way.
function sosAskBase(taskId) {
  return (typeof State !== 'undefined' && State.isStaff)
    ? `/api/staffos/tasks/${taskId}/ask_eon`
    : `/api/tasks/${taskId}/ask_eon`;
}
function sosAskPaint(rows) {
  const log = sosId('sos-ask-log');
  if (!log) return;
  let h = '';
  (rows || []).forEach(m => {
    if (m.role !== 'user' && m.role !== 'assistant') return;   // tool rows stay backstage
    if (!(m.content || '').trim()) return;
    h += `<div class="sos-ask-msg ${m.role === 'user' ? 'me' : 'eon'}">${sosEsc(m.content)}</div>`;
  });
  log.innerHTML = h || `<div class="sos-ask-hint">Ask how to do this task — EON answers here.</div>`;
  log.scrollTop = log.scrollHeight;
}
async function sosAskLoad(taskId) {
  try {
    const r = await api(`${sosAskBase(taskId)}/messages`);
    sosAskPaint((r && r.messages) || []);
  } catch (_) { sosAskPaint([]); }
}
// Keyboard door for the drawer input — a named dispatcher, because the
// inline-handler export scanner (rightly) rejects handlers that don't
// start with an exported call.
function sosAskKey(ev, taskId) {
  if (ev.key === 'Enter') sosAskSend(taskId);
}
async function sosAskSend(taskId) {
  const inp = sosId('sos-ask-in');
  const log = sosId('sos-ask-log');
  const q = (inp && inp.value || '').trim();
  if (!q || !log) return;
  inp.value = '';
  log.innerHTML += `<div class="sos-ask-msg me">${sosEsc(q)}</div>`
    + `<div class="sos-ask-msg eon sos-ask-wait" id="sos-ask-wait">EON is thinking…</div>`;
  log.scrollTop = log.scrollHeight;
  try {
    const r = await api(sosAskBase(taskId), {
      method: 'POST', body: JSON.stringify({ message: q }),
    });
    sosId('sos-ask-wait')?.remove();
    log.innerHTML += `<div class="sos-ask-msg eon">${sosEsc((r && r.reply) || 'No reply')}</div>`;
  } catch (e) {
    sosId('sos-ask-wait')?.remove();
    // Rate-limit / cap refusals arrive as 400 with the reason — show it.
    log.innerHTML += `<div class="sos-ask-msg eon sos-ask-err">${sosEsc(e.message || 'Could not ask right now')}</div>`;
  }
  log.scrollTop = log.scrollHeight;
}

// ── Assign a task (manager) — a REAL create+assign FORM ──────────
// The "Assign a task" quick action used to just navigate to the whole task
// board (every task, everyone) with no create flow; the Quick Add modal only
// creates UNASSIGNED tasks EON then routes. This opens a proper assign form
// (does NOT navigate): title, assignee (the page's loaded roster), due
// date+time, priority, optional description → POST /api/tasks with
// assigned_to_staff_id + due_at populated. Manager-gated (matches sosQa).
// ── "Report something" (the Colleague §Move 2, 2026-08-30) ─────────
// POS down, no bags, AC, anything — thirty seconds, filed into the same
// team_reports ledger the staff WhatsApp line writes. Copy follows the
// voice law: the confirmation says what happens NEXT, never a
// surveillance ack, never ticket-speak.
function sosOpenReportSheet() {
  const cats = [
    ['pos', 'POS / till'], ['stock', 'Stock'],
    ['facilities', 'Store / facilities'], ['customer', 'A customer'],
    ['other', 'Something else'],
  ];
  const chips = cats.map(([v, t], i) =>
    // prc-chip, not chip: `.sos-root .chip` (staff_os.css:78) is a static
    // LABEL style — no border, no cursor, and no `.on` variant — so the
    // tapped topic looked identical to the four it wasn't. `.prc-chip` +
    // `.prc-chip.on` (staff_os.css:276-277) is the house's selectable chip,
    // already used by the other sos sheets. `sos-rep-cat` stays as the
    // query hook the pick/submit handlers read.
    `<button type="button" class="prc-chip sos-rep-cat${i === 4 ? ' on' : ''}"`
    + ` data-cat="${v}" onclick="sosPickReportCat(this)">${t}</button>`).join(' ');
  const h = `<div class="pane" onclick="event.stopPropagation()"><div class="grab"></div>`
    + `<div class="scw-sheet__h"><div class="scw-sheet__t">Report something</div>`
    + `<button class="scw-x" aria-label="Close" onclick="sosCloseSheet()">&times;</button></div>`
    + `<div class="as-lbl">WHAT KIND</div>`
    + `<div id="sos-rep-cats" class="prc-chips">${chips}</div>`
    + `<div class="as-lbl">WHAT HAPPENED</div>`
    + `<textarea id="sos-rep-text" class="sos-as-in" rows="3" `
    + `placeholder="Say it like you'd say it out loud — what's wrong, since when"></textarea>`
    + `<div id="sos-rep-msg" class="prc-quiet" style="margin-top:6px"></div>`
    + `<button class="bigdone" id="sos-rep-go" onclick="sosSubmitReport()">Send it</button>`
    + `</div>`;
  const sh = sosId('sos-sheet'); sh.innerHTML = h; sh.classList.add('on');
  setTimeout(() => sosId('sos-rep-text')?.focus(), 40);
}

function sosPickReportCat(btn) {
  document.querySelectorAll('.sos-rep-cat').forEach(b => b.classList.remove('on'));
  btn.classList.add('on');
}

async function sosSubmitReport() {
  const msg = sosId('sos-rep-msg');
  const text = (sosId('sos-rep-text')?.value || '').trim();
  if (!text) { if (msg) msg.textContent = 'Say what happened first.'; return; }
  const cat = document.querySelector('.sos-rep-cat.on')?.dataset.cat || 'other';
  const go = sosId('sos-rep-go');
  if (go) { go.disabled = true; go.textContent = 'Sending…'; }
  try {
    const r = await api('/api/staffos/report', {
      method: 'POST',
      body: JSON.stringify({ staff_id: sosState.cur, category: cat, text }),
    });
    if (r && r.ok) {
      sosCloseSheet();
      sosToast(r.next || 'Filed — it goes into the ops review.');
    } else {
      if (msg) msg.textContent = (r && r.error) || "Couldn't file that — try again.";
      if (go) { go.disabled = false; go.textContent = 'Send it'; }
    }
  } catch (_) {
    if (msg) msg.textContent = "Couldn't file that — check your connection and try again.";
    if (go) { go.disabled = false; go.textContent = 'Send it'; }
  }
}

function sosOpenAssignModal() {
  const d = sosR();
  if (!d.mgr) { sosToast('Assigning tasks is manager-only'); return; }
  const roster = sosState.roster || [];
  if (!roster.length) { sosToast('Roster unavailable — try again shortly'); return; }
  // Default due = today at 18:00 (end of shift), in the operator's local zone.
  const now = new Date();
  const dStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const opts = roster.map(p =>
    `<option value="${sosEsc(p.staff_id)}"${p.staff_id === sosState.cur ? ' selected' : ''}>`
    + `${sosEsc(p.full_name || p.name)}${p.role ? ' · ' + sosEsc(p.role) : ''}</option>`).join('');
  const prios = ['normal', 'high', 'urgent', 'low'].map(pr =>
    `<option value="${pr}"${pr === 'normal' ? ' selected' : ''}>${pr.charAt(0).toUpperCase() + pr.slice(1)}</option>`).join('');
  const h = `<div class="pane" onclick="event.stopPropagation()"><div class="grab"></div>`
    + `<div class="scw-sheet__h"><div class="scw-sheet__t">Assign a task</div>`
    + `<button class="scw-x" aria-label="Close" onclick="sosCloseSheet()">&times;</button></div>`
    + `<div class="as-lbl">TASK</div>`
    + `<input id="sos-as-title" class="sos-as-in" placeholder="What needs doing?" autocomplete="off">`
    + `<div class="as-lbl">ASSIGN TO</div>`
    + `<select id="sos-as-who" class="sos-as-in">${opts}</select>`
    + `<div class="sos-as-row"><div class="sos-as-col"><div class="as-lbl">DUE DATE</div>`
    + `<input id="sos-as-date" type="date" class="sos-as-in" value="${dStr}"></div>`
    + `<div class="sos-as-col"><div class="as-lbl">DUE TIME</div>`
    + `<input id="sos-as-time" type="time" class="sos-as-in" value="18:00"></div></div>`
    + `<div class="as-lbl">PRIORITY</div>`
    + `<select id="sos-as-prio" class="sos-as-in">${prios}</select>`
    + `<div class="as-lbl">NOTES (OPTIONAL)</div>`
    + `<textarea id="sos-as-desc" class="sos-as-in" rows="2" placeholder="Any detail the assignee needs"></textarea>`
    + `<div id="sos-as-msg" class="prc-quiet" style="margin-top:6px"></div>`
    + `<button class="bigdone" id="sos-as-go" onclick="sosSubmitAssign()">Assign task</button>`
    + `</div>`;
  const sh = sosId('sos-sheet'); sh.innerHTML = h; sh.classList.add('on');
  setTimeout(() => sosId('sos-as-title')?.focus(), 40);
}

async function sosSubmitAssign() {
  const title = (sosId('sos-as-title')?.value || '').trim();
  const msg = sosId('sos-as-msg');
  if (!title) { if (msg) msg.textContent = 'Task title is required.'; return; }
  const staffId = sosId('sos-as-who')?.value || '';
  if (!staffId) { if (msg) msg.textContent = 'Pick an assignee.'; return; }
  const date = sosId('sos-as-date')?.value || '';
  const time = sosId('sos-as-time')?.value || '';
  const prio = sosId('sos-as-prio')?.value || 'normal';
  const desc = (sosId('sos-as-desc')?.value || '').trim();
  // Build due_at from the local date+time inputs → ISO (UTC). Time without a
  // date is ignored; a date alone defaults to 18:00 local (end of shift).
  let dueAt = null;
  if (date) {
    const [y, mo, da] = date.split('-').map(Number);
    const [hh, mm] = (time || '18:00').split(':').map(Number);
    const dt = new Date(y, (mo || 1) - 1, da, hh || 18, mm || 0);
    if (!isNaN(dt.getTime())) dueAt = dt.toISOString();
  }
  const payload = { title: title, assigned_to_staff_id: staffId, priority: prio };
  if (dueAt) payload.due_at = dueAt;
  if (desc) payload.description = desc;
  const btn = sosId('sos-as-go');
  if (btn) { btn.disabled = true; btn.textContent = 'Assigning…'; }
  try {
    const res = await _tosCreateTask(payload);
    if (res && (res.ok || res.id || (res.task && res.task.id))) {
      sosToast('Task assigned');
      sosCloseSheet();
      if (sosState.cur) await sosSetRole(sosState.cur);  // refresh tasks view
    } else {
      if (msg) msg.textContent = 'Could not assign the task.';
      if (btn) { btn.disabled = false; btn.textContent = 'Assign task'; }
    }
  } catch (e) {
    if (msg) msg.textContent = (e && e.status === 403)
      ? 'You need assign permission to do this.'
      : ((e && e.message) || 'Could not assign the task.');
    if (btn) { btn.disabled = false; btn.textContent = 'Assign task'; }
  }
}

// Real "Mark done" → POST /api/tasks/{id}/complete with the staff context.
// On success, refresh the role payload so the task moves to done. The
// owner-preview (role switcher) actor is NOT the assignee, so the backend
// returns 403 not_assignee — surfaced as a toast (expected behaviour).
// Fix-or-cancel sheets (feedback quartet 2026-07-25). Managers correct a
// mistaken task (typo, wrong due date, wrong person) or withdraw it, with
// the change trailed as a task comment server-side. Fields left blank keep
// their current value; the backend diffs and only writes real changes.
function sosTaskEditOpen(sec, i) {
  const t = ((sosR().tasks || {})[sec] || [])[i];
  if (!t || !t.id) return;
  const roster = sosState.roster || [];
  const opts = ['<option value="">(keep current)</option>']
    .concat(roster.map(p =>
      `<option value="${sosEsc(p.staff_id)}">${sosEsc(p.full_name || p.name)}</option>`))
    .join('');
  const sh = sosId('sos-sheet');
  if (!sh) return;
  // .pane wrapper positions the sheet (bottom sheet on phones, centered
  // modal on desk) — every other #sos-sheet fill has it (sosOpenTask).
  sh.innerHTML = `<div class="pane" onclick="event.stopPropagation()"><div class="grab"></div>`
    + `<div class="sheetin">`
    + `<div class="as-lbl">FIX TASK</div>`
    + `<div class="tt" style="margin-bottom:10px">${sosEsc(t.t)}</div>`
    + `<label class="as-lbl">NEW TITLE (blank keeps current)</label>`
    + `<input id="sos-te-title" placeholder="${sosEsc(t.t)}">`
    + `<label class="as-lbl">NEW DUE</label>`
    + `<input id="sos-te-due" type="datetime-local">`
    + `<label class="as-lbl">REASSIGN TO</label>`
    + `<select id="sos-te-who">${opts}</select>`
    + `<button class="bigdone" onclick="sosTaskEditSave('${sosEsc(t.id)}',this)">Save fix</button>`
    + `<button class="mgrghost" style="width:100%;margin-top:8px" onclick="sosCloseSheet()">Back</button>`
    + `</div></div>`;
  sh.classList.add('on');
}
async function sosTaskEditSave(taskId, btn) {
  if (btn && btn.disabled) return;
  const body = {};
  const title = (sosId('sos-te-title') || {}).value || '';
  const due = (sosId('sos-te-due') || {}).value || '';
  const who = (sosId('sos-te-who') || {}).value || '';
  if (title.trim()) body.title = title.trim();
  if (due) { try { body.due_at = new Date(due).toISOString(); } catch (_) {} }
  if (who) body.assigned_to_staff_id = who;
  if (!Object.keys(body).length) { sosToast('Nothing to change'); return; }
  const undo = sosBtnBusy(btn, 'Saving…');
  try {
    await api('/api/tasks/' + encodeURIComponent(taskId), {
      method: 'PATCH', body: JSON.stringify(body),
    });
    sosToast('Task corrected');
    sosCloseSheet();
    if (sosState.cur) await sosSetRole(sosState.cur);
  } catch (e) {
    if (undo) undo();
    sosToast((e && e.message) || 'Could not fix task');
  }
}
function sosTaskCancelOpen(sec, i) {
  const t = ((sosR().tasks || {})[sec] || [])[i];
  if (!t || !t.id) return;
  const sh = sosId('sos-sheet');
  if (!sh) return;
  sh.innerHTML = `<div class="pane" onclick="event.stopPropagation()"><div class="grab"></div>`
    + `<div class="sheetin">`
    + `<div class="as-lbl">CANCEL TASK</div>`
    + `<div class="tt" style="margin-bottom:10px">${sosEsc(t.t)}</div>`
    + `<label class="as-lbl">WHY? (optional \u2014 the assignee sees this)</label>`
    + `<input id="sos-tc-why" placeholder="Assigned by mistake">`
    + `<button class="bigdone danger" onclick="sosTaskCancelDo('${sosEsc(t.id)}',this)">Cancel this task</button>`
    + `<button class="mgrghost" style="width:100%;margin-top:8px" onclick="sosCloseSheet()">Back</button>`
    + `</div></div>`;
  sh.classList.add('on');
}
async function sosTaskCancelDo(taskId, btn) {
  if (btn && btn.disabled) return;
  const why = ((sosId('sos-tc-why') || {}).value || '').trim();
  const undo = sosBtnBusy(btn, 'Cancelling\u2026');
  try {
    await api('/api/tasks/' + encodeURIComponent(taskId) + '/transition', {
      method: 'POST',
      body: JSON.stringify({ status: 'cancelled', note: why || null }),
    });
    sosToast('Task cancelled');
    sosCloseSheet();
    if (sosState.cur) await sosSetRole(sosState.cur);
  } catch (e) {
    if (undo) undo();
    sosToast((e && e.message) || 'Could not cancel task');
  }
}
async function sosCompleteTask(taskId) {
  if (!taskId) { sosToast('This task can\'t be completed here yet'); return; }
  // Double-tap guard on the first-tap path. Guard on state, NOT on the
  // CTA's disabled flag — the verify-photo flow re-enters here with the
  // CTA already disabled ('Marking done…') and must not bounce off.
  if (sosState.taskBusy) return;
  sosState.taskBusy = true;
  const cta = document.getElementById('sos-task-cta');
  const undo = sosBtnBusy(cta, 'Marking done…');
  try {
    await api('/api/tasks/' + encodeURIComponent(taskId) + '/complete', {
      method: 'POST',
      body: JSON.stringify({ staff_id: sosState.cur || null }),
    });
    sosState.taskBusy = false;
    sosToast('Task marked done');
    sosCloseSheet();
    if (sosState.cur) await sosSetRole(sosState.cur);  // refresh payload
  } catch (e) {
    sosState.taskBusy = false;
    if (undo) undo();
    if (e && e.status === 403) {
      sosToast('Only the assignee can complete this task');
    } else if (e && e.status === 422) {
      // Not a dead end any more: turn the CTA into the camera.
      sosAskVerifyPhoto(taskId);
    } else {
      sosToast((e && e.message) || 'Could not complete task');
    }
  }
}

// Swap the task sheet's CTA to a photo capture, upload it, then complete.
// One tap to the camera, one call to the server (the verification-photo
// endpoint stores AND registers the attachment).
function sosAskVerifyPhoto(taskId) {
  const cta = document.getElementById('sos-task-cta');
  const note = document.getElementById('sos-verifynote');
  const input = document.getElementById('sos-verify-file');
  if (!cta || !input) { sosToast('Attach a verification photo to complete'); return; }
  if (note) { note.hidden = false; note.textContent = 'This task needs a photo before it can be marked done.'; }
  cta.textContent = 'Take verification photo';
  cta.onclick = () => input.click();
  input.onchange = async () => {
    const f = input.files && input.files[0];
    if (!f) return;
    cta.disabled = true; cta.textContent = 'Uploading…';
    try {
      const fd = new FormData();
      fd.append('file', f);
      const headers = { 'Authorization': `Bearer ${State.jwt}` };
      if (State.tenant) headers['X-Tenant-Slug'] = State.tenant.slug;
      const r = await fetch(API + '/api/tasks/' + encodeURIComponent(taskId)
        + '/verification-photo', { method: 'POST', headers, body: fd });
      if (!r.ok) {
        const b = await r.json().catch(() => null);
        throw new Error((b && (b.detail && (b.detail.error || b.detail)) )
          || ('upload failed (' + r.status + ')'));
      }
      cta.textContent = 'Marking done…';
      await sosCompleteTask(taskId);       // photo now on file → passes
    } catch (err) {
      cta.disabled = false;
      cta.textContent = 'Take verification photo';
      if (note) note.textContent = String((err && err.message) || err);
    }
  };
}

// ════════════════════════ SCORE VIEW ════════════════════════════
function sosRenderScore() {
  const d = sosR(), s = d.score || {};
  const wkLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  let h = '';
  // level
  h += `<div class="card"><div class="lvl"><div class="badge">${sosEsc(s.mark || '')}</div>`
    + `<div class="t"><b>${sosEsc(s.lvl || '')}</b><span>${sosEsc(s.lvlsub || '')}</span></div></div>`
    + `<div class="xp"><i style="width:${s.xp || 0}%"></i></div></div>`;
  // week dots — gated on data (backend fills Monday→today; an empty array
  // means no gamification key / failed read, and an empty card is a lie)
  if ((s.week || []).length) {
    h += `<div class="sec">This week</div><div class="card"><div class="wkdots">`;
    s.week.forEach((w, i) => {
      h += `<div class="wkd ${w ? 'hit' : ''}"><div class="d">${w ? sosCheck() : '·'}</div><span>${wkLabels[i] || ''}</span></div>`;
    });
    h += `</div></div>`;
  }
  // ledger
  if ((s.ledger || []).length) {
    h += `<div class="sec">Today's points</div><div class="card"><div class="ledg">`;
    s.ledger.forEach(l => { h += `<div class="l"><span>${sosEsc(l[0])}</span><b>${sosEsc(l[1])}</b></div>`; });
    h += `</div></div>`;
  }
  // badges
  if ((s.badges || []).length) {
    h += `<div class="sec">Badges</div><div class="card"><div class="badges">`;
    s.badges.forEach(bd => {
      const lock = bd[3] ? 'lock' : '';
      h += `<div class="bdgx ${lock}"><div class="bm" style="background:${bd[1]}">${sosEsc(bd[0])}</div>`
        + `<div class="bl">${sosEsc(bd[2])}</div></div>`;
    });
    h += `</div></div>`;
  }
  // leaderboard
  if ((s.board || []).length) {
    h += `<div class="sec">Leaderboard</div><div class="card"><div class="lead">`;
    s.board.forEach((b, i) => {
      h += `<div class="row ${b.me ? 'me' : ''}"><div class="rk">${i + 1}</div>`
        + `<div class="av2" style="background:${SOS_G[b.g] || b.g || SOS_G.blue}">${sosEsc(b.i)}</div>`
        + `<div style="flex:1"><div class="nm2">${sosEsc(b.n)}</div><div class="st2">${sosEsc(b.s)}</div></div>`
        + `<div class="pt2">${sosEsc(b.p)}</div></div>`;
    });
    h += `</div></div>`;
  }
  // Personal bests sits BESIDE the manager board above, not instead of
  // it (restored 2026-09-09). This branch replaced the board block, which
  // removed a manager's coaching surface main's 2026-08-31 vet had
  // deliberately kept — a product decision, not a merge resolution. The
  // board renders only from s.board, which the server fills for managers
  // and the CS lead alone, so a rep still reads nobody's name but their
  // own. Bests is built for everyone, manager included.
  if (s.bests && (s.bests.best_day_points || s.bests.streak_days)) {
    const bt = s.bests;
    h += `<div class="sec">Personal bests</div><div class="card"><div class="ledg">`;
    if (bt.best_day_points) {
      const when = bt.best_day ? ` · ${sosEsc(bt.best_day)}` : '';
      h += `<div class="l"><span>Best day ever${when}</span><b>${Number(bt.best_day_points).toLocaleString()} pts</b></div>`;
    }
    if (bt.streak_days) {
      h += `<div class="l"><span>Current streak</span><b>${bt.streak_days} day${bt.streak_days === 1 ? '' : 's'}</b></div>`;
    }
    h += `</div></div>`;
  }
  // My payslip — the real statement (base, PAYE, pension), fetched from
  // the endpoint that sat doorless until 2026-08-30. Loads after paint.
  h += `<div class="sec">My payslip</div><div class="card" id="sos-payslip"><div class="sm">Loading…</div></div>`;
  // bonus — B2 live card. Backend now emits a dict (amount, sub, run-rate,
  // sparkline, top_earnable). Tolerate the legacy [amount,sub] list too.
  if (s.bonus) {
    h += sosBonusCard(s.bonus);
  }
  // how to earn
  if ((s.earn || []).length) {
    h += `<div class="sec">How to earn more</div><div class="card"><div class="earn">`;
    s.earn.forEach(e => { h += `<div class="l"><span>${sosEsc(e[0])}</span><b>${sosEsc(e[1])}</b></div>`; });
    h += `</div></div>`;
  }
  if (!h) h = `<div class="note">No score data for this role yet.</div>`;
  sosId('sos-v-score').innerHTML = h;
  // B2 — animate the bonus counter after paint.
  requestAnimationFrame(() => sosAnimateBonus());
  // Payslip after paint too: it is a network read, so calling it inside
  // the render blocks the tab on a fetch AND makes the renderer depend on
  // the loader existing. The frontend VM harness evaluates sosRenderScore
  // on its own and proved the second half — the render threw
  // "sosPayslipLoad is not defined" before the card could paint.
  requestAnimationFrame(() => sosPayslipLoad());
}

// ── My payslip (2026-08-30) ──────────────────────────────────────
// Renders the REAL pay statement from /api/staffos/payslip (self-clamped
// server-side). Honest about gaps: flags come straight from the engine.
async function sosPayslipLoad() {
  const host = sosId('sos-payslip');
  if (!host) return;
  const sid = sosState.cur;
  try {
    const d = await api(`/api/staffos/payslip?staff_id=${encodeURIComponent(sid)}`);
    if (!d || d.ok === false) {
      host.innerHTML = `<div class="sm">No statement yet for this month.</div>`;
      return;
    }
    const ngn = (v) => (v == null ? '—'
      : '₦' + Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }));
    const e = d.earnings || {}, st = d.statutory || {};
    let h = `<div class="ledg">`;
    h += `<div class="l"><span>Period</span><b>${sosEsc(d.period || '')}</b></div>`;
    h += `<div class="l"><span>Base salary</span><b>${ngn(e.base_month_ngn)}</b></div>`;
    if (Number(e.bonus_month_ngn)) h += `<div class="l"><span>Bonus</span><b>${ngn(e.bonus_month_ngn)}</b></div>`;
    if (e.housing_allowance_paid_ngn != null) h += `<div class="l"><span>Housing allowance</span><b>${ngn(e.housing_allowance_paid_ngn)}</b></div>`;
    h += `<div class="l"><span>PAYE this month</span><b>${ngn(st.paye_month_ngn)}</b></div>`;
    h += `<div class="l"><span>Pension (your 8%)</span><b>${ngn(st.pension_employee_month_ngn)}</b></div>`;
    h += `<div class="l"><span>Net payable</span><b>${ngn(d.net_payable_computed_ngn)}</b></div>`;
    h += `</div>`;
    const flags = d.flags || [];
    if (flags.includes('no_payroll_run_for_period')) {
      h += `<div class="sm">This month's run isn't drafted yet — figures are computed from your salary on record.</div>`;
    }
    if (flags.includes('rent_not_declared_no_relief')) {
      h += `<div class="sm">Declare your annual rent to Daniel to unlock rent relief on PAYE.</div>`;
    }
    host.innerHTML = h;
  } catch (_) {
    host.innerHTML = `<div class="sm">Couldn't load your payslip — try again in a moment.</div>`;
  }
}

// ── B2 bonus card ────────────────────────────────────────────────
// Tolerates both the new dict shape and the legacy [amount, sub] list.
function sosBonusCard(bonus) {
  // Legacy list → minimal card (back-compat; no run-rate enrichment).
  if (Array.isArray(bonus)) {
    return `<div class="sec">Bonus pool</div><div class="card"><div class="bonus">`
      + `<div><b>${sosEsc(bonus[0])}</b><span>${sosEsc(bonus[1])}</span></div></div></div>`;
  }
  const b = bonus || {};
  // The animated headline figure: payout naira when funded, else points.
  const target = b.configured
    ? Math.round(Number(String(b.amount || '0').replace(/[^0-9.]/g, '')) || 0)
    : (b.my_points || 0);
  const suffix = b.configured ? '' : ' pts';
  const prefix = b.configured ? '₦' : '';
  let h = `<div class="sec">Bonus pool</div><div class="card sos-bonus">`;
  h += `<div class="bonus">`
    + `<div><b class="bonus-num" data-to="${target}" data-prefix="${prefix}" data-suffix="${suffix}">${prefix}0${suffix}</b>`
    + `<span>${sosEsc(b.sub || '')}</span></div></div>`;
  // Run-rate line: each point ~ ₦x right now.
  if (b.naira_per_point != null) {
    h += `<div class="bonus-rate">each point ~ <b>₦${Number(b.naira_per_point).toLocaleString(undefined, { maximumFractionDigits: 0 })}</b> right now`
      + (b.share_pct != null ? ` · your share <b>${Number(b.share_pct).toFixed(0)}%</b>` : '') + `</div>`;
  } else if (b.share_pct != null) {
    h += `<div class="bonus-rate">your share of the pool · <b>${Number(b.share_pct).toFixed(0)}%</b> · funds at month-end</div>`;
  }
  // Month sparkline (per-day points this month).
  const spark = b.month_sparkline || [];
  if (spark.length) {
    const max = Math.max(1, ...spark.map(p => Number(p[1]) || 0));
    let bars = '';
    spark.forEach(p => {
      const v = Number(p[1]) || 0;
      const ht = Math.max(6, Math.round(100 * v / max));
      bars += `<span class="sp-b" style="height:${ht}%" title="${sosEsc(p[0])}: ${v} pts"></span>`;
    });
    h += `<div class="bonus-spark"><div class="sp-row">${bars}</div>`
      + `<div class="sp-lbl">points this month</div></div>`;
  }
  // Top-3 earnable behaviours, priced in naira at the live per-point rate.
  const top = b.top_earnable || [];
  if (top.length) {
    h += `<div class="bonus-earn"><div class="be-h">Worth most right now</div>`;
    top.forEach(t => {
      const val = (t.naira_value != null)
        ? `₦${Number(t.naira_value).toLocaleString()}`
        : `+${t.points} pts`;
      h += `<div class="be-r"><span>${sosEsc(t.behaviour)}</span><b>${val}</b></div>`;
    });
    h += `</div>`;
  }
  return h + `</div>`;
}

// Count-up animation for the bonus headline (≈900ms ease-out).
function sosAnimateBonus() {
  const el = sosId('sos-v-score') && sosId('sos-v-score').querySelector('.bonus-num');
  if (!el) return;
  const to = Number(el.getAttribute('data-to')) || 0;
  const prefix = el.getAttribute('data-prefix') || '';
  const suffix = el.getAttribute('data-suffix') || '';
  if (to <= 0) { el.textContent = prefix + '0' + suffix; return; }
  if (cg.active() && !cg.requested()) { el.textContent = prefix + to.toLocaleString() + suffix; return; }
  if (cg.motionPaused()) { el.textContent = prefix + to.toLocaleString() + suffix; return; }
  const dur = 900, t0 = performance.now();
  let frame = 0;
  const untrack = cg.trackMotion(() => {
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
    el.textContent = prefix + to.toLocaleString() + suffix;
  });
  const step = (t) => {
    if (cg.motionPaused()) { untrack(); el.textContent = prefix + to.toLocaleString() + suffix; return; }
    const k = Math.min(1, (t - t0) / dur);
    const e = 1 - Math.pow(1 - k, 3);  // ease-out cubic
    const v = Math.round(to * e);
    el.textContent = prefix + v.toLocaleString() + suffix;
    if (k < 1) frame = requestAnimationFrame(step); else untrack();
  };
  frame = requestAnimationFrame(step);
}

// ════════════════════════ TAB BAR + VIEW SWITCH ═════════════════
// Horizon stage (design pass 03, aurora anchor 2): the desktop void behind
// the phone column becomes a living horizon whose palette tracks the WAT
// clock — dawn amber before open, working mint through the day, after-hours
// violet. A rep opening the app at 22:00 SEES off-shift. Same shared module
// + low-power ladder as every other anchor; hidden on phones (CSS).
function sosHorizonPalette() {
  let h = new Date().getUTCHours() + 1;  // WAT = UTC+1, no DST
  if (h >= 24) h -= 24;
  if (h >= 5 && h < 9) return ['#F2A24C', '#C8FF4D', '#56E39F'];   // dawn
  if (h >= 9 && h < 19) return ['#56E39F', '#7AA6FF', '#C8FF4D'];  // working day
  return ['#B58CFF', '#6EA8FE', '#56E39F'];                        // after hours
}
function sosMountHorizon() {
  // Aurora anchor #8 (operator 2026-07-26): the in-column hero band phones
  // actually see — #sos-horizon below lives in the desktop gutters behind
  // the opaque .phone column and is display:none under 600px. Slim, masked,
  // behind the header only (FLAT LAW: never behind cards, data or money).
  const hb = document.getElementById('sos-heroband');
  if (hb && typeof window.mountAurora === 'function' && hb.clientWidth) {
    const curB = sosState._heroband;
    if (curB && curB._canvas === hb) { curB.setPalette(sosHorizonPalette()); }
    else {
      if (curB) { try { curB.destroy(); } catch (_) {} }
      const b = window.mountAurora(hb, sosHorizonPalette(),
        { alpha: true, vignette: false, glow: null, intensity: 0.28 });
      if (b) { b._canvas = hb; sosState._heroband = b; }
    }
  }
  const c = document.getElementById('sos-horizon');
  if (!c || typeof window.mountAurora !== 'function') return;
  if (!c.clientWidth || !c.clientHeight) return;   // display:none on phones
  const cur = sosState._horizon;
  if (cur && cur._canvas === c) { cur.setPalette(sosHorizonPalette()); return; }
  if (cur) cur.destroy();
  const h = window.mountAurora(c, sosHorizonPalette(),
    { alpha: true, vignette: false, glow: null, intensity: 0.35 });
  if (!h) return;
  h._canvas = c;
  sosState._horizon = h;
  // WAT boundary tick (audit 2026-07-12): a session left open across 9:00 /
  // 19:00 kept the stale palette — refresh every 10 minutes (uniform update,
  // no remount). One interval per session; dies quietly when the canvas goes.
  if (!sosState._horizonTick) {
    sosState._horizonTick = setInterval(() => {
      const cur = sosState._horizon;
      if (cur && document.getElementById('sos-horizon')) cur.setPalette(sosHorizonPalette());
      const band = sosState._heroband;
      if (band && document.getElementById('sos-heroband')) band.setPalette(sosHorizonPalette());
    }, 600000);
  }
}

function sosBuildTabbar() {
  const d = sosR();
  const tabs = [['day', 'Day'], ['chat', 'Chat'], ['tasks', 'Tasks']];
  // CS lead gets her desk-scoped Team tab too — the backend built
  // _cs_lead_team_blocks for her and this gate silently threw it away
  // (vet 2026-08-31, S10).
  if (d.mgr || d.is_cs_lead) tabs.push(['team', 'Team']);
  tabs.push(['score', cg.active() ? 'Progress' : 'Score']);
  if (cg.active()) tabs.push(['sales', 'Sales'], ['finance', 'Requests']);
  // overdue badge on Tasks; unread badge on Chat (unread Team @-mention
  // threads, PR-F #4). 2026-07-27 prune: the payload `chats` term is gone —
  // it always carried un:0, so the badge was sosTeamUnread() alone already.
  const over = (d.tasks && d.tasks.over) ? d.tasks.over.length : 0;
  const unread = sosTeamUnread();
  let h = '';
  tabs.forEach(t => {
    let bdg = '';
    if (t[0] === 'tasks' && over) bdg = `<span class="bdg">${over}</span>`;
    if (t[0] === 'chat' && unread) bdg = `<span class="bdg">${unread}</span>`;
    h += `<button data-tab="${t[0]}" class="${t[0] === sosState.tab ? 'on' : ''}" onclick="sosSwitchTab('${t[0]}')">`
      + `${SOS_ICON[t[0]] || SOS_ICON[t[0] === 'finance' ? 'tasks' : 'score']}<span>${t[1]}</span>${bdg}</button>`;
  });
  sosId('sos-tabbar').innerHTML = h;
}
function sosSwitchTab(tab) {
  sosState.tab = tab;
  // PR-F #1 — leaving Chat (the only tab that can mount the CS embed) restores
  // the normal shell; sosRenderChat re-applies it when re-entering on a CS rep.
  if (tab !== 'chat') sosClearCsFull();
  // views — the entrance cascade plays the FIRST time a tab is viewed;
  // every later visit swaps in with the 220ms crossfade alone (.sos-seen).
  sosState._tabSeen = sosState._tabSeen || {};
  ['day', 'chat', 'tasks', 'team', 'score', 'sales', 'finance'].forEach(v => {
    const el = sosId('sos-v-' + v); if (!el) return;
    el.classList.toggle('on', v === tab);
    if (v === tab) el.classList.toggle('sos-seen', !!sosState._tabSeen[tab]);
  });
  sosState._tabSeen[tab] = true;
  // tabbar active state
  document.querySelectorAll('#sos-tabbar button').forEach(b => {
    b.classList.toggle('on', b.getAttribute('data-tab') === tab);
  });
  // render on demand (keeps fresh)
  if (tab === 'day') sosRenderDay();
  else if (tab === 'chat') sosRenderChat();
  else if (tab === 'tasks') sosRenderTasks();
  else if (tab === 'team') sosRenderTeam();
  else if (tab === 'score') sosRenderScore();
  else if (cg.active() && ['sales', 'finance'].includes(tab)) cg.staffExtra(tab);
  // v3 motion — one vocabulary, two verbs here: the VIEW slides in on
  // every switch (260ms) and its cards PRINT like a till roll feeding
  // (380ms, staggered). Chat is skipped: the CS embed is its own
  // surface. prefers-reduced-motion lands everything printed and still
  // (staff_os.css kills both keyframes there).
  if (tab !== 'chat') sosMotionIn(sosId('sos-v-' + tab));
}

function sosMotionIn(el) {
  if (!el) return;
  el.classList.remove('sos-slide');
  void el.offsetWidth;                       // restart the animation
  el.classList.add('sos-slide');
  // Print the first paint-load of top-level blocks; deeper rows ride
  // their parent. Cap the stagger so long feeds don't crawl.
  Array.from(el.children).slice(0, 8).forEach((c, i) => {
    c.classList.remove('sos-prt');
    c.style.animationDelay = (i * 0.06) + 's';
    void c.offsetWidth;
    c.classList.add('sos-prt');
  });
}

// ════════════════════════ ROLE PILL + PICKER ════════════════════
function sosRenderPill() {
  const d = sosR();
  const tag = d.tag ? `<span class="sos-rolepill__tag">${sosEsc(d.tag)}</span>` : '';
  sosId('sos-rolepill').innerHTML = `<div class="rav" style="background:${SOS_G[d.grad] || d.grad || SOS_G.blue}">${sosEsc(d.init)}</div>`
    + `<div class="rtx"><b>${sosEsc(d.full)}</b><span>${sosEsc(d.role)}</span></div>`
    + `<span class="car">&#9662;</span>`;
}
function sosChipForDept(dept) {
  return { 'Sales': 'blue', 'Inventory': 'amber', 'Logistics': 'mint', 'Customer Service': 'vio', 'Operations': 'lime', 'Marketing': 'red' }[dept] || 'mute';
}
function sosRosterAvatar(row) {
  // prefer server-provided init/grad (contract A1); fall back so the picker
  // never blanks during the BE transition.
  const init = row.init || sosInitialsOf(row.full_name || row.full || row.name || '');
  const grad = (row.grad && SOS_G[row.grad]) || sosGradOf(row);
  return { init, grad };
}
function sosInitialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '··';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function sosGradOf(row) {
  return ({ 'Sales': SOS_G.blue, 'Inventory': SOS_G.amber, 'Logistics': SOS_G.mint, 'Customer Service': SOS_G.vio, 'Operations': SOS_G.lime, 'Marketing': SOS_G.red }[row.department]) || SOS_G.blue;
}
function sosOpenRP() {
  // Picker honesty: the server clamps every per-person read to SELF for
  // non-owner/admin callers (routes_staffos._effective_staff_id), so for a
  // staff PIN session "Tap anyone" was a lie — tapping a peer just reloaded
  // yourself. Mirror the clamp: only owner/admin get the org roster.
  const br = (State.member && (State.member.base_role || State.member.role)) || '';
  const ownerish = br === 'owner' || br === 'admin';
  let roster = sosState.roster;
  if (!ownerish) {
    const mine = sosMyStaffId() || sosState.cur;
    roster = sosState.roster.filter(p => String(p.staff_id) === String(mine));
    if (!roster.length) roster = sosState.roster.filter(p => p.staff_id === sosState.cur);
  }
  // group the roster by department, in SOS_DEPT_ORDER
  const byDept = {};
  roster.forEach(p => { (byDept[p.department] = byDept[p.department] || []).push(p); });
  let h = `<div class="grab"></div><h3>${ownerish ? 'Switch role' : 'My role'}</h3>`
    + `<div class="rpsub">${ownerish
        ? 'One app, every life — also the org chart. Tap anyone.'
        : 'Staff OS shows your own view — this is you.'}</div>`;
  SOS_DEPT_ORDER.forEach(dept => {
    const people = byDept[dept]; if (!people) return;
    h += `<div class="dept">${sosDeptDot(dept)}${sosEsc(dept)}</div>`;
    people.forEach(p => {
      const on = p.staff_id === sosState.cur ? 'on' : '';
      const tag = p.is_manager ? `<span class="tag chip ${sosChipForDept(dept)}">MGR</span>` : '';
      const av = sosRosterAvatar(p);
      h += `<div class="person ${on}" onclick="sosSetRole('${sosEsc(p.staff_id)}');sosCloseRP()">`
        + `<div class="rav" style="background:${av.grad}">${sosEsc(av.init)}</div>`
        + `<div class="nm"><b>${sosEsc(p.full_name || p.full || p.name)}</b><span>${sosEsc(p.role)}</span></div>`
        + tag + `</div>`;
    });
  });
  // any people whose department isn't in DEPT_ORDER still render (honesty: never drop staff)
  Object.keys(byDept).forEach(dept => {
    if (SOS_DEPT_ORDER.indexOf(dept) !== -1) return;
    h += `<div class="dept">${sosDeptDot(dept)}${sosEsc(dept || 'Other')}</div>`;
    byDept[dept].forEach(p => {
      const on = p.staff_id === sosState.cur ? 'on' : '';
      const av = sosRosterAvatar(p);
      h += `<div class="person ${on}" onclick="sosSetRole('${sosEsc(p.staff_id)}');sosCloseRP()">`
        + `<div class="rav" style="background:${av.grad}">${sosEsc(av.init)}</div>`
        + `<div class="nm"><b>${sosEsc(p.full_name || p.full || p.name)}</b><span>${sosEsc(p.role)}</span></div></div>`;
    });
  });
  sosId('sos-rp').innerHTML = h;
  sosId('sos-rp').classList.add('on'); sosId('sos-rp-scrim').classList.add('on');
}
function sosCloseRP() { sosId('sos-rp')?.classList.remove('on'); sosId('sos-rp-scrim')?.classList.remove('on'); }

// Keep only the 5 most-recently-viewed payloads: the owner cycling the
// whole roster on a shared device otherwise banks a payload per staffer
// in localStorage forever.
function sosPrunePayloadCache() {
  try {
    const rows = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || k.indexOf(SOS_LS_PAYLOAD) !== 0) continue;
      let t = 0;
      try { t = (JSON.parse(localStorage.getItem(k) || 'null') || {}).t || 0; } catch (_) {}
      rows.push([k, t]);
    }
    rows.sort((a, b) => b[1] - a[1]);
    rows.slice(5).forEach(r => localStorage.removeItem(r[0]));
  } catch (_) { /* best-effort hygiene */ }
}

// ════════════════════════ ROLE SWITCH + BOOT ════════════════════
// `quiet` = a background refresh (foreground return): keep the current
// screen painted and swap the data underneath instead of blanking to a
// loading line.
async function sosSetRole(staffId, quiet, isCurrent = () => true) {
  if(!isCurrent())return;
  const row = sosState.roster.find(p => p.staff_id === staffId);
  if (!row) return;
  const sameRole = quiet && sosState.cur === staffId;
  sosState.cur = staffId;
  if (!sameRole) {
    // Day on a role switch, deliberately. This branch briefly forced
    // 'chat' here to restore the 2026-08-10 directive; that was too wide
    // and is withdrawn. The directive was the DESK's, and the first-load
    // branch below already honours it correctly with the measurement
    // behind it — the desk keeps chat, everyone else wakes on their Day,
    // because a third of the roster was landing on an empty customer
    // inbox. That branch runs ONCE (_landed), so forcing chat here also
    // put an owner switching between colleagues straight into the next
    // colleague's chat workspace, which is what the canonical role-switch
    // proof guards against.
    sosState.tab = 'day';
    sosState.att = null; sosState.attBusy = false;  // reset attendance on switch
    sosState.teamThreads = []; sosState.teamActive = null;  // reset team chat on switch
    sosCloseSheet();
    sosClearCsFull();   // PR-F #1 — never carry a previous CS rep's embed into the new role
    // Clear identity-bound comparison reads before the next role fetch,
    // including its loading/error path. Existing staff task nodes are retained.
    if (cg.active()) ['day', 'chat', 'tasks', 'team', 'score', 'sales', 'finance'].forEach(v => {
      const el = sosId('sos-v-' + v);
      if (el) { el.classList.toggle('on', v === 'day'); if (v === 'sales' || v === 'finance') el.innerHTML = ''; }
    });
  }
  const ascentWork = document.getElementById('ascent-staff-work');
  if (ascentWork) mountAscentWork(ascentWork, {api, query:'?staff_id='+encodeURIComponent(staffId), identity:()=>State.jwt+'|'+State.tenant?.slug+'|'+sosState.cur, owner:(State.member?.base_role||State.member?.role)==='owner'&&!State.impersonating});
  try { localStorage.setItem(SOS_LS_KEY, staffId); } catch (_) {}

  // Cached paint (2026-07-25 vet): the payload takes seconds, and the app
  // used to show a bare "Loading…" line for all of it on every open. Paint
  // the last-known payload instantly, then swap in the fresh one.
  if (!sameRole) {
    let cached = null;
    try { cached = JSON.parse(localStorage.getItem(SOS_LS_PAYLOAD + staffId) || 'null'); } catch (_) {}
    // A CACHED PAINT IS A COURTESY, NOT A TRUTH (2026-09-02): a rep's Day
    // painted a two-month-old copy first — July's month header, July's
    // points, a pool share five times that day's, sales twice that day's,
    // and a "Team rank" a rep must never see — for the seconds before the
    // fresh page landed. So: a copy older than a day is not painted, and a
    // copy that is painted carries no rank, no month header and no money.
    if (cached && cached.p && (!cached.t || (Date.now() - cached.t) > SOS_CACHE_TTL_MS)) cached = null;
    if (cached && cached.p) {
      sosState.payload = sosStripCachedPayload(cached.p);
      sosRenderPill(); sosBuildTabbar(); sosRenderDay();
      const stale = sosId('sos-v-day');
      if (stale) stale.classList.add('sos-stale');
    } else {
      sosId('sos-v-day').innerHTML = `<div class="sos-loading">Loading ${sosEsc(row.full_name || row.name || 'staff')}…</div>`;
    }
  }

  // GET the full ROLES-shape payload for this person (contract A2).
  try {
    window._sosLastFetch = Date.now();
    const res = await api('/api/staffos/role/' + encodeURIComponent(staffId));
    if(!isCurrent() || sosState.cur!==staffId)return;
    sosState.payload = res || {};
    // Apex build 01: chat-main was the DESK's directive (2026-08-10) —
    // a third of the roster landed on an empty customer inbox. First
    // load only: the desk keeps chat; everyone else wakes on their Day.
    if (!sosState._landed) {
      sosState._landed = true;
      if (!(res && res.is_cs) && sosState.tab === 'chat') sosState.tab = 'day';
    }
    try {
      localStorage.setItem(SOS_LS_PAYLOAD + staffId,
        JSON.stringify({ t: Date.now(), p: sosState.payload }));
      sosPrunePayloadCache();
    } catch (_) {}
    sosId('sos-v-day')?.classList.remove('sos-stale');
    if (sameRole) {
      // QUIET refresh (design vet 2026-07-26, M1): same person, fresh
      // numbers — repaint only the tab the user is on and keep their place.
      // The old path fell through to the full re-render below, which reset
      // every view to Day, wiped the entrance ledger, and re-mounted the
      // chat embed: a rep reading Tasks/Score (or mid-conversation) was
      // yanked to Day every time the phone woke past the 45s gate.
      sosRenderPill();
      const t = sosState.tab;
      if (t === 'day') sosRenderDay();
      else if (t === 'tasks') sosRenderTasks();
      else if (t === 'score') sosRenderScore();
      else if (t === 'team') { sosRenderTeam(); }
      else if (cg.active() && ['sales', 'finance'].includes(t)) cg.staffExtra(t);
      // 'chat': the embed keeps itself fresh (poll + realtime) — never
      // re-mount it under the rep.
      sosBuildTabbar();   // badge counts refresh; 'on' state follows sosState.tab
      return;
    }
  } catch (e) {
    if(!isCurrent() || sosState.cur!==staffId)return;
    if (sameRole) return;            // background refresh failed — keep what's painted
    sosState.payload = null;
    sosId('sos-v-day').innerHTML = `<div class="sos-error"><b>Could not load ${sosEsc(row.full_name || row.name || 'this role')}.</b><br>${sosEsc(e.message || 'Try again shortly.')}</div>`;
    // still paint the pill so the picker remains usable
    sosState.payload = { full: row.full_name, init: (sosRosterAvatar(row).init), grad: sosRosterAvatar(row).grad, role: row.role };
    sosRenderPill();
    sosBuildTabbar();
    return;
  }

  sosRenderPill();
  sosBuildTabbar();
  // fresh role = fresh entrance ledger, reset BEFORE the eager renders —
  // sosRenderDay reads .sos-seen off the view to decide the count-up + ring
  // replay; resetting after (audit 2026-07-12) left the previous role's class
  // in place, so the CSS cascade replayed while the numbers stayed static.
  sosState._tabSeen = { day: true };
  sosState._attSeen = false;
  ['day', 'chat', 'tasks', 'team', 'score', 'sales', 'finance'].forEach(v => {
    const el = sosId('sos-v-' + v); if (el) { el.classList.toggle('on', v === 'day'); el.classList.remove('sos-seen'); }
  });
  // render all views so they are non-blank when tabbed to. The eager
  // sosRenderChat() mounts the CS inbox for a CS rep AND applies sos-csfull;
  // since we land on Day, clear the embed classes after so the Day header is
  // not wrongly hidden (sosRenderChat re-applies them when Chat is tapped).
  sosRenderDay(); sosRenderChat(); sosRenderTasks(); sosRenderScore();
  sosClearCsFull();
  sosRenderTeam();
  sosMountHorizon();
  document.querySelectorAll('#sos-tabbar button').forEach(b => {
    b.classList.toggle('on', b.getAttribute('data-tab') === 'day');
  });
  // B3 — fetch attendance status and paint the check-in block at the top
  // of Day (async; Day is already non-blank without it).
  sosAttFetch();
  // Time off & swaps renders inside sosRenderDay (the sos-req-slot), which
  // calls sosReqFetch itself — no second fetch here.
  // PR-F #4 — fetch Team @-mention threads (async; the Chat tab badge + Team
  // section / CS pinned row fill in once they land).
  sosLoadTeam();
  return true;
}

// ════════════════════════ QUICK ACTIONS / LINKS ═════════════════
// Wires the mockup's qa() stub to REAL actions (contract BUILDER B #5).
// Manager-only affordances are gated on the payload caps (mgr /
// can_request_finance) — the buttons themselves are emitted only by the
// backend's `quick`/`link`/`team` blocks, so this is a routing layer.
// Money-request decisions — the REAL procurement endpoints (approve/reject
// are ACCOUNTING_APPROVE-gated server-side; a non-approver gets the error
// toast, never a fake success).
//
// Deliberateness without dialogs (operator PROCEED, 2026-07-12): a money
// release can't be undone, so the old window.confirm/prompt pair becomes
// press-and-HOLD to approve (the fill completing IS the confirmation — a
// slip releases before it lands and nothing happens) and an INLINE reason
// row for reject. Non-blocking, one gesture, no native dialogs.
// Staff approval cards keep the reviewed source and session in memory only.
function sosFinanceContext() {
  const identity = eonWorkroomSession();
  if (!identity || identity.impersonated || State.view !== 'staffos'
      || !prcCanApprove()) return null;
  // Credentials rotate independently of the person reviewing this card.
  // Keep the actual identity, permissions and seat bound across every await.
  return JSON.stringify([!!State.isStaff, identity.tenantId, identity.tenantSlug, identity.actorId, identity.authSession, State.user,
    State.member, sosState.cur, sosState.tab]);
}
function sosFinanceTarget(reqId, btn) {
  const registry = sosState.financeReview, card = btn?.closest('.prc-card');
  const context = sosFinanceContext();
  const record = registry?.cards.get(String(reqId));
  if (!context || registry.context !== context || !record || !card?.isConnected
      || card.dataset.financeReview !== registry.generation || card.dataset.financeRequest !== String(reqId)) return null;
  return { record, registry, card, btn, reqId: String(reqId),
    same: () => card.isConnected && card.dataset.financeRequest === String(reqId)
      && card.dataset.financeReview === registry.generation
      && sosState.financeReview === registry && sosFinanceContext() === context };
}
function sosFinancePaint(target, message, mode) {
  if (!target.same()) return;
  const {card, record} = target;
  record.phase = mode;
  if (mode === 'confirmed') {
    // Keep the actual returned decision across same-person tab redraws.
    record.sourceCard.mode = record.final;
    record.sourceCard.decision = {...record.sourceCard.decision, status:record.final};
  }
  const result = card.querySelector('[data-finance-result]');
  result.textContent = message;
  card.querySelectorAll('.prc-card-actions button, .prc-reject__in').forEach(el => {
    el.disabled = mode !== 'idle';
  });
  const approve = card.querySelector('.sos-hold');
  if (approve) {
    approve.classList.remove('held', 'holding');
    approve.querySelector('span').textContent = mode === 'confirmed' && record.final === 'approved'
      ? 'Approved' : 'Hold to approve';
  }
  const reject = card.querySelector('.prc-reject__send');
  if (reject) reject.textContent = mode === 'confirmed' && record.final === 'rejected'
    ? 'Rejected' : 'Send rejection';
  const check = card.querySelector('[data-finance-check]');
  check.hidden = mode !== 'uncertain';
  check.disabled = mode !== 'uncertain';
  if (target.restoreFocus && (document.activeElement === target.btn || document.activeElement === document.body)) {
    if (mode === 'uncertain') check.focus();
    else if (mode === 'confirmed') result.focus();
  }
}
async function sosFinanceReadWrite(target, path, opts = {}) {
  if (!target.same()) throw new Error('Finance context changed');
  const method = String(opts.method || 'GET').toUpperCase();
  const controller = new AbortController();
  let timer;
  // The shared API clears its timer before JSON; keep this deadline through the body too.
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => { controller.abort(); reject(new Error('Finance request timed out')); }, 15000);
  });
  try {
    // Reads may silently refresh; an uncertain decision must never be replayed.
    return await Promise.race([api(path, {...opts, signal: controller.signal, timeoutMs:15000,
      _isCurrent:target.same, _retried:method !== 'GET'}), deadline]);
  } finally { clearTimeout(timer); }
}
async function sosFinanceDecision(reqId, act, btn, reason = '') {
  const target = sosFinanceTarget(reqId, btn);
  if (!target || !['approve', 'reject'].includes(act) || target.record.phase !== 'idle') return;
  reason = String(reason || '').trim();
  if (act === 'reject' && !reason) { sosToast('A reason is required — it goes to the requester'); return; }
  const {record} = target;
  target.restoreFocus = target.card.contains(document.activeElement);
  sosFinancePaint(target, 'Checking the current request…', 'busy');
  let sent = false;
  const path = '/api/procurement/requests/' + encodeURIComponent(reqId);
  try {
    const fresh = (await sosFinanceReadWrite(target, path))?.request;
    if (!target.same()) return;
    if (!fresh || String(fresh.id) !== String(reqId) || fresh.status !== 'pending'
        || prcDecisionSnapshot(fresh) !== record.snapshot) {
      sosFinancePaint(target, 'The request changed or could not be read. Refresh Requests before deciding.', 'changed');
      return;
    }
    const amount = fresh.total_ngn;
    if (act === 'approve' && !((typeof amount === 'number' || typeof amount === 'string' && amount.trim() !== '')
        && Number.isFinite(Number(amount)) && Number(amount) >= 0)) {
      sosFinancePaint(target, 'The requested amount is unavailable. No decision was sent.', 'changed'); return;
    }
    record.intent = act;
    sent = true;
    sosFinancePaint(target, 'Saving the decision…', 'busy');
    const response = await sosFinanceReadWrite(target, path + '/' + act,
      {method:'POST', body:act === 'reject' ? JSON.stringify({reason}) : '{}'});
    if (!target.same()) return;
    if (response?.ok !== true) {
      sosFinancePaint(target, 'The decision was not confirmed. Check the saved result before trying again.', 'uncertain'); return;
    }
    const final = (await sosFinanceReadWrite(target, path))?.request;
    if (!target.same()) return;
    if (!final || String(final.id) !== String(reqId) || final.status !== (act === 'approve' ? 'approved' : 'rejected')) {
      sosFinancePaint(target, 'The response arrived, but the saved decision could not be confirmed.', 'uncertain'); return;
    }
    record.final = final.status;
    sosFinancePaint(target, act === 'approve' ? 'Approved. Payment has not been recorded.' : 'Rejected in the saved record.', 'confirmed');
    document.dispatchEvent(new Event('eon:procurement-changed'));
  } catch (_) {
    if (target.same()) sosFinancePaint(target, sent
      ? 'The decision result is unavailable. It may have been saved. Check the saved result.'
      : 'The current request could not be read. No decision was sent. Try again.', sent ? 'uncertain' : 'idle');
  }
}
async function sosFinanceCheck(btn) {
  const target = sosFinanceTarget(btn?.dataset.req, btn);
  if (!target || target.record.phase !== 'uncertain') return;
  target.restoreFocus = target.card.contains(document.activeElement);
  sosFinancePaint(target, 'Reading the saved result…', 'busy');
  try {
    const final = (await sosFinanceReadWrite(target, '/api/procurement/requests/' + encodeURIComponent(target.reqId)))?.request;
    if (!target.same()) return;
    if (!final || String(final.id) !== target.reqId) throw new Error('Wrong request');
    if (!['approved', 'rejected', 'paid'].includes(final.status)) {
      sosFinancePaint(target, 'No saved decision is confirmed yet. Check again or refresh Requests; nothing was sent by this check.', 'uncertain'); return;
    }
    target.record.final = final.status;
    const text = {approved:'Saved record: approved. Payment has not been recorded.', rejected:'Saved record: rejected.', paid:'Saved record: payment recorded.'};
    sosFinancePaint(target, text[final.status], 'confirmed');
    document.dispatchEvent(new Event('eon:procurement-changed'));
  } catch (_) {
    if (target.same()) sosFinancePaint(target, 'The saved result is still unavailable. This check sent no decision.', 'uncertain');
  }
}

const SOS_HOLD_MS = 900;

function sosHoldStart(btn, ev) {
  if (btn.disabled || btn._holdT) return;
  // keyboard path: Enter/Space hold (ignore key auto-repeat)
  if (ev && ev.type === 'keydown') {
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    if (ev.repeat) return;
    ev.preventDefault();
  }
  const target = sosFinanceTarget(btn.dataset.req, btn);
  if (!target || target.record.phase !== 'idle') return;
  btn.classList.add('holding');
  btn._holdT = setTimeout(() => {
    btn._holdT = null;
    btn.classList.remove('holding');
    if (!target.same()) return;
    btn.classList.add('held');
    sosApproveReq(btn.dataset.req, btn.dataset.title, btn);
  }, SOS_HOLD_MS);
}
function sosHoldCancel(btn) {
  if (btn._holdT) { clearTimeout(btn._holdT); btn._holdT = null; }
  btn.classList.remove('holding');
}

async function sosApproveReq(reqId, title, btn) {
  return sosFinanceDecision(reqId, 'approve', btn);
}

// Reject: the actions row swaps to [reason input · Send rejection · Keep] —
// the reason is REQUIRED (it goes to the requester), typed in place.
function sosRejectAsk(btn) {
  const target = sosFinanceTarget(btn?.dataset.req, btn);
  if (!target || target.record.phase !== 'idle') return;
  const row = btn.closest('.prc-card-actions');
  if (!row || row.querySelector('.prc-reject')) return;
  const { req, title } = btn.dataset;
  Array.from(row.children).forEach(el => { el.hidden = true; });
  const wrap = document.createElement('div');
  wrap.className = 'prc-reject';
  wrap.innerHTML = `<input class="sos-as-in prc-reject__in" maxlength="200"
      placeholder="Reason — goes to the requester" aria-label="Rejection reason">
    <button class="prc-act prc-reject__send">Send rejection</button>
    <button class="prc-act prc-reject__keep">Keep</button>`;
  row.appendChild(wrap);
  const inp = wrap.querySelector('.prc-reject__in');
  inp.focus();
  wrap.querySelector('.prc-reject__keep').addEventListener('click', () => {
    wrap.remove();
    Array.from(row.children).forEach(el => { el.hidden = false; });
  });
  const send = wrap.querySelector('.prc-reject__send');
  const submit = () => {
    if (!target.same() || target.record.phase !== 'idle') return;
    const reason = inp.value.trim();
    if (!reason) { sosToast('A reason is required — it goes to the requester'); inp.focus(); return; }
    sosRejectReq(req, title, send, reason);
  };
  send.addEventListener('click', submit);
  inp.addEventListener('keydown', e => { if (e.key === 'Enter') submit(); });
}

async function sosRejectReq(reqId, title, btn, reason) {
  return sosFinanceDecision(reqId, 'reject', btn, reason);
}

function sosQa(label) {
  const d = sosR();
  const lc = String(label || '').toLowerCase();

  // Request finance → the existing production procurement sheet.
  if (lc === 'request finance' || lc === 'request restock' || lc === 'request finance ') {
    if (d.can_request_finance || d.mgr) {
      if (typeof prcOpenRequestSheet === 'function') { prcOpenRequestSheet(); return; }
    }
    sosToast(d.can_request_finance || d.mgr ? 'Finance request unavailable' : 'Finance requests are manager-only');
    return;
  }
  // Deep-links: "open /marketing", "open Marketing page", etc.
  if (lc.indexOf('open ') === 0) {
    if (sosNavDeep(sosResolveDeepLink(label.slice(5).trim()))) return;
  }
  if (lc === 'open my inbox') { sosSwitchTab('chat'); return; }
  // PR-F #3 — rider quick-actions (Patrick). Capturing a POD, logging a failed
  // stop, or counting a COD pouch all act on a SPECIFIC stop, whose ids live in
  // the /dispatch riding app (the run-block stops carry seq, not stop ids).
  // Route the rider there — the app's Delivered/Failed taps POST
  // /api/dispatch/stops/{id}/delivered|failed (the live engine, PR-D §5).
  if (lc === 'capture pod' || lc === 'log failed stop' || lc === 'cod pouch count') {
    location.href = '/dispatch'; return;
  }
  // "Something broke" (the Colleague §Move 2, 2026-08-30) — files into
  // the same team_reports ledger the staff WhatsApp line writes.
  if (lc === 'report something') { sosOpenReportSheet(); return; }
  // People OS.
  if (lc.indexOf('people os') !== -1) { State.view = 'people'; render(); return; }
  // Assign a task → open a real create+assign FORM (does NOT navigate to the
  // whole task board). Manager-gated; the modal posts assigned_to_staff_id +
  // due_at to POST /api/tasks.
  if (lc === 'assign a task') {
    if (!(d.mgr)) { sosToast('Assigning tasks is manager-only'); return; }
    sosOpenAssignModal(); return;
  }
  // Ask EON -> a real chat with EON. CS reps have their pinned EON line inside
  // the CS workspace (cswOpenEon) -- that is the customer-context helper and is
  // ALWAYS available to them. The main EON BUSINESS chat (goChat) surfaces
  // company data (revenue, ops, cost), so it is gated to managers+ -- matching
  // the backend POST /chat + ws("web") gate. A non-manager (non-CS) gets a
  // toast and does NOT navigate, instead of silently opening the EON page.
  if (lc === 'ask eon') {
    if (d.is_cs) {
      sosSwitchTab('chat');
      // wait for renderCsInbox to mount #csw, then open the pinned EON thread.
      let tries = 0;
      const open = () => {
        if (document.getElementById('csw') && typeof cswOpenEon === 'function') { cswOpenEon(); return; }
        if (tries++ < 20) setTimeout(open, 60);
      };
      setTimeout(open, 80);
      return;
    }
    if (!d.mgr) { sosToast('EON chat is for managers.'); return; }
    if (typeof goChat === 'function') { goChat(''); return; }
    sosToast('EON chat unavailable'); return;
  }
  // Everything else: acknowledge (real per-action wiring lands as the
  // underlying flows do; never fabricate a result).
  sosToast(label);
}
function sosResolveDeepLink(text) {
  const raw = String(text || '');
  const t = raw.toLowerCase();
  // Department Report — carries a per-staff query (?staff_id=) so we keep the
  // ORIGINAL-case substring from "/reports" to the next space (no lookbehind:
  // older Safari parse-errors on it). Falls back to the bare path.
  const ri = t.indexOf('/reports');
  if (ri !== -1) {
    const tail = raw.slice(ri).split(/\s/)[0];
    return tail || '/reports';
  }
  if (t.indexOf('/marketing') !== -1 || t.indexOf('marketing page') !== -1 || t.indexOf('reps') !== -1 || t.indexOf('walk-in') !== -1 || t.indexOf('crux studio') !== -1 || t.indexOf('studio') !== -1 || t.indexOf('allocator') !== -1) return '/marketing';
  if (t.indexOf('/academy') !== -1 || t.indexOf('academy') !== -1) return '/academy';
  if (t.indexOf('/inventory') !== -1 || t.indexOf('inventory page') !== -1) return '/inventory';
  if (t.indexOf('/brain') !== -1 || t.indexOf('brain page') !== -1 || t.indexOf('system map') !== -1) return '/engine';
  if (t.indexOf('/loop') !== -1 || t.indexOf('loop page') !== -1 || t.indexOf('the whole loop') !== -1) return '/loop';
  if (t.indexOf('warehouse') !== -1 || t.indexOf('cockpit') !== -1) return '/warehouse';
  if (t.indexOf('/dispatch') !== -1 || t.indexOf('dispatcher') !== -1) return '/dispatch';
  if (t.indexOf('/engine') !== -1 || t.indexOf('engine page') !== -1 || t.indexOf('transit map') !== -1 || t.indexOf('wired view') !== -1) return '/engine';
  if (t.indexOf('people os') !== -1 || t.indexOf('?v=hr') !== -1 || t.indexOf(' hr') !== -1) return '__people__';
  if (t.indexOf('task os') !== -1) return '__tasks__';
  if (t.indexOf('money') !== -1 || t.indexOf('books') !== -1 || t.indexOf('approvals') !== -1) return '__money__';
  if (t.indexOf('cs workspace') !== -1) return '__inbox__';
  return '';
}
// PR-F #1 — perform navigation for a resolved deep-link destination. Returns
// true if it routed (so callers can stop), false if dest was empty/unknown.
function sosNavDeep(dest) {
  if (dest === '__people__') { State.view = 'people'; render(); return true; }
  if (dest === '__money__') { State.view = 'money'; render(); return true; }
  if (dest === '__tasks__') { State.view = 'tasks'; render(); return true; }
  if (dest === '__cs__') { State.view = 'cs'; render(); return true; }
  if (dest === '__inbox__') { sosSwitchTab('chat'); return true; }
  if (dest) { location.href = dest; return true; }
  return false;
}
// scoped toast — uses the production showToast if available, else a local pill.
function sosToast(label) {
  if (typeof showToast === 'function') { showToast(label); return; }
  // .sos-toast (staff_os.css) — body-appended, reads the :root ink tokens
  // (the old inline cssText carried its own drifted hexes; design pass 03).
  const n = document.createElement('div');
  n.textContent = label;
  n.className = 'sos-toast';
  document.body.appendChild(n);
  setTimeout(() => n.remove(), 1600);
}

// expose for inline onclick handlers (the mockup uses inline onclick)
window.sosOpenRP = sosOpenRP; window.sosCloseRP = sosCloseRP; window.sosSetRole = sosSetRole;
window.sosSwitchTab = sosSwitchTab;
window.sosOpenTask = sosOpenTask; window.sosCloseSheet = sosCloseSheet;
window.sosCompleteTask = sosCompleteTask; window.sosToggleDone = sosToggleDone; window.sosTaskEditOpen = sosTaskEditOpen; window.sosTaskEditSave = sosTaskEditSave; window.sosTaskCancelOpen = sosTaskCancelOpen; window.sosTaskCancelDo = sosTaskCancelDo;
window.sosAskSend = sosAskSend; window.sosAskKey = sosAskKey;   // v3 drawer — inline onclick/onkeydown in the task sheet
window.sosOpenAssignModal = sosOpenAssignModal; window.sosSubmitAssign = sosSubmitAssign;
window.sosOpenReportSheet = sosOpenReportSheet; window.sosSubmitReport = sosSubmitReport; window.sosPickReportCat = sosPickReportCat;
window.sosQa = sosQa;
window.sosBlkLinkNav = sosBlkLinkNav; window.sosNavDeep = sosNavDeep;
// Money-request decisions (hold-to-approve + inline reject reason). The OLD
// Approve/Reject onclicks were never window-exposed — app.js is a module, so
// every tap threw ReferenceError and the buttons were dead since they
// shipped (found in the 2026-07-12 confirm/prompt redesign).
window.sosHoldStart = sosHoldStart; window.sosHoldCancel = sosHoldCancel;
window.sosRejectAsk = sosRejectAsk;
window.sosFinanceCheck = sosFinanceCheck;
// PR-F — trackingsend sheet
window.sosOpenTrackingSheet = sosOpenTrackingSheet; window.sosTrackingPickCarrier = sosTrackingPickCarrier;
window.sosTrackingOnWaybill = sosTrackingOnWaybill; window.sosTrackingSend = sosTrackingSend; window.sosDispatchDelivered = sosDispatchDelivered;
window.sosTrackingCopy = sosTrackingCopy;
// Attendance check-in + every other inline-onclick handler that was defined
// but never window-exposed — app.js is a module, so each of these buttons
// threw ReferenceError on tap and did NOTHING (operator report 2026-07-21
// "check in is not working": zero geo check-ins ever recorded; reproduced
// headless — pageerror `sosAttCheckin is not defined`). Same class as the
// 2026-07-12 money-request find; test_staffos_window_exports now pins the
// whole class: every sos* referenced from an inline handler must be here.
window.sosAttCheckin = sosAttCheckin;
window.sosAttCheckout = sosAttCheckout; window.sosAttSetPin = sosAttSetPin;
window.sosToast = sosToast;
window.sosPerfLoad = sosPerfLoad;
window.sosTrackingCopyMsg = sosTrackingCopyMsg;
window.sosMerchLoad = sosMerchLoad; window.sosMerchTransfer = sosMerchTransfer;
window.sosMerchBundle = sosMerchBundle; window.sosMerchBundlePick = sosMerchBundlePick;
window.sosMerchMarkdown = sosMerchMarkdown;
window.sosReqOpen = sosReqOpen; window.sosReqSetKind = sosReqSetKind;
window.sosReqToggleTwoway = sosReqToggleTwoway; window.sosReqSubmit = sosReqSubmit;
window.sosReqRespond = sosReqRespond; window.sosReqCancel = sosReqCancel;
window.sosChkTap = sosChkTap;
window.sosObsToggle = sosObsToggle;   // observation-history fold (tail-doors 2026-07-27)
// Team @-mention chat is now a LENS in the unified CS workspace (cswTeam*),
// bound via addEventListener — no window-scoped onclick exports needed.
// STAFF OS END (sos-* renderer · faithful mockup port)

// ── Client-error reporter (operator directive 2026-07-06) ──────────────
// Every uncaught error / unhandled rejection posts to /api/client-errors;
// the 10-min triage cron severity-ranks it and diagnoses with Opus 4.8 /
// Fable-5, escalating critical ones to alerts + Telegram. Deduped per
// session + hard send budget so a crash loop can't flood the API. The
// reporter itself must NEVER throw (it would recurse into itself).
const _ceSent = new Set();
let _ceBudget = 12;
function _reportClientError(message, stack, source) {
  try {
    if (!State?.jwt || !State?.tenant || _ceBudget <= 0) return;
    const key = String(message || '').slice(0, 120);
    if (!key || _ceSent.has(key)) return;
    _ceSent.add(key);
    _ceBudget -= 1;
    api('/api/client-errors', {
      method: 'POST',
      timeoutMs: 8000,
      body: JSON.stringify({
        message: String(message).slice(0, 500),
        stack: String(stack || '').slice(0, 4000),
        url: location.pathname + location.hash,
        source: source,
        user_agent: navigator.userAgent,
        shell_version: cswShellBooted() || 'no-sw',
      }),
    }).then((r) => {
      // WS-E: the desk heartbeat carries the newest row this device filed.
      try { if (r && r.id) sessionStorage.setItem('eon_last_client_error_id', String(r.id)); } catch (_) {}
    }).catch(() => {});
  } catch (_) { /* never recurse */ }
}
window.addEventListener('error', (e) => {
  _reportClientError(
    e.message || (e.error && e.error.message) || 'unknown error',
    e.error && e.error.stack, 'onerror');
});
window.addEventListener('unhandledrejection', (e) => {
  const r = e.reason;
  _reportClientError(
    (r && (r.message || String(r))) || 'unhandled rejection',
    r && r.stack, 'unhandledrejection');
});

// Inline-handler escape hatches (2026-07-23 usage audit): app.js is a
// module — every function an inline on*= attribute references MUST be
// exported onto window or the tap dies with a ReferenceError (5th…9th
// instances of the class: saved-report delete, ask-EON-about-ad, the
// global render retry, reprice rollback, the Staff OS map JWT post).
// tests/test_inline_handler_exports.py now enforces the WHOLE class.
window._deleteSavedReport = _deleteSavedReport;
window._createSavedReport = _createSavedReport;
window.askEonAboutAd = askEonAboutAd;
window.render = render;
window.repriceRollback = repriceRollback;
window.sosPostJwtToMap = sosPostJwtToMap;


// ═══ Wave-2 PR-A (spine vet): transaction register + budgets UI ═══
// The vet's core bookkeeping finding: 10-newest-only transactions, no
// search, no view-all, recategorize endpoint with no UI, and a review
// draft older than 10 rows could never be actioned. This register is
// the full ledger: search, filters, honest "N of M" counts
// (worked-queue law), tap-to-recategorize (the backend learns a rule
// from every correction), inline approve/dismiss.

const _regS = { q: '', filter: 'all', offset: 0, rows: [], total: 0,
                cats: null, busy: false };

function _regParams() {
  const map = {
    review: '&status=pending_review',
    uncat: '&uncategorized=true',
    income: '&kind=income',
    expense: '&kind=expense',
  };
  return map[_regS.filter] || '';
}

async function _regFetch(reset) {
  if (_regS.busy) return;
  _regS.busy = true;
  if (reset) { _regS.offset = 0; _regS.rows = []; }
  const w = moneyPeriodToWindow(State.moneyPeriod || 'this_month');
  const qs = `period=${encodeURIComponent(w.apiPeriod)}&limit=100`
    + `&offset=${_regS.offset}${_regParams()}`
    + (_regS.q ? `&q=${encodeURIComponent(_regS.q)}` : '');
  try {
    const r = await api(`/api/accounting/transactions?${qs}`);
    _regS.rows = _regS.rows.concat(r.transactions || []);
    _regS.total = r.total_count ?? _regS.rows.length;
  } catch (e) {
    // Vet 2026-07-27: a fetch failure painted as "Nothing matches" —
    // the exact empty-as-error class wave-1 was written to kill.
    showToast(`Register load failed: ${e?.message || e}`);
    _regS.error = e?.message || String(e);
    _regS.busy = false;
    _regPaint();
    return;
  }
  _regS.error = null;
  _regS.busy = false;
  _regPaint();
}

function _regRow(tx, i) {
  const isIncome = tx.kind === 'income';
  const cat = tx.transaction_categories?.name || 'Uncategorized';
  const uncat = !tx.transaction_categories;
  const pending = tx.status === 'pending_review';
  const d = String(tx.occurred_at || '').slice(0, 10);
  return `
    <button class="mreg-row" type="button" data-reg-i="${i}">
      <span class="mreg-date mono-num">${esc(d)}</span>
      <span class="mreg-main">
        <span class="mreg-desc">${esc(tx.description || tx.counterparty || '—')}</span>
        <span class="mreg-sub">${esc(tx.counterparty || '')}
          <span class="mreg-cat ${uncat ? 'warn' : ''}">${esc(cat)}</span>
          ${pending ? '<span class="mreg-cat warn">review</span>' : ''}</span>
      </span>
      <span class="mreg-amt mono-num ${isIncome ? 'in' : 'out'}">${isIncome ? '+' : '−'}${formatNaira(Math.abs(tx.amount_ngn ?? tx.amount ?? 0), true)}</span>
    </button>`;
}

function _regPaint() {
  const host = document.getElementById('mreg-body');
  if (!host) return;
  if (_regS.error) {
    host.innerHTML = `
    <div class="mreg-list"><div class="bks-error">Couldn't load the register: ${esc(_regS.error)}</div></div>
    <div class="mreg-foot"><button class="bks-tool-btn" type="button" id="mreg-retry">Retry</button></div>`;
    host.querySelector('#mreg-retry')?.addEventListener('click', () => _regFetch(true));
    return;
  }
  const shown = _regS.rows.length;
  const more = Math.max(0, _regS.total - shown);
  host.innerHTML = `
    <div class="mreg-list">${_regS.rows.map(_regRow).join('')
      || '<div class="mtx-empty">Nothing matches — clear the search or switch filters.</div>'}</div>
    <div class="mreg-foot">
      <span class="meta">${shown} of ${_regS.total}${more ? ` · ${more} more hidden` : ' · all shown'}</span>
      ${more ? '<button class="bks-tool-btn" type="button" id="mreg-more">Load 100 more</button>' : ''}
    </div>`;
  host.querySelector('#mreg-more')?.addEventListener('click', () => {
    _regS.offset += 100;
    _regFetch(false);
  });
  host.querySelectorAll('[data-reg-i]').forEach((b) =>
    b.addEventListener('click', () => _regDetail(_regS.rows[Number(b.dataset.regI)])));
}

async function _regCats() {
  if (_regS.cats) return _regS.cats;
  try {
    const r = await api('/api/accounting/categories');
    _regS.cats = r.categories || r.data || (Array.isArray(r) ? r : []);
  } catch (e) { _regS.cats = []; }
  return _regS.cats;
}

async function _regDetail(tx) {
  if (!tx) return;
  const cats = await _regCats();
  const cur = tx.transaction_categories?.slug || '';
  const pending = tx.status === 'pending_review';
  const ov = document.createElement('div');
  ov.className = 'mreg-ov';
  ov.innerHTML = `
    <div class="mreg-sheet mreg-detail" role="dialog" aria-label="Transaction detail">
      <div class="mreg-head"><span>Transaction</span>
        <button class="mreg-x" type="button" aria-label="Close">&times;</button></div>
      <div class="mreg-dbody">
        <div class="mreg-drow"><span>Amount</span><span class="mono-num">${formatNaira(tx.amount_ngn ?? tx.amount ?? 0)}</span></div>
        <div class="mreg-drow"><span>Date</span><span class="mono-num">${esc(String(tx.occurred_at || '').slice(0, 10))}</span></div>
        <div class="mreg-drow"><span>Description</span><span>${esc(tx.description || '—')}</span></div>
        <div class="mreg-drow"><span>Counterparty</span><span>${esc(tx.counterparty || '—')}</span></div>
        <div class="mreg-drow"><span>Account</span><span>${esc(tx.accounts?.name || '—')}</span></div>
        <div class="mreg-drow"><span>Status</span><span>${esc(tx.status || '—')}</span></div>
        <label class="mreg-drow"><span>Category</span>
          <select class="mtx-input" id="mreg-cat-sel">
            <option value="">Uncategorized</option>
            ${cats.map((c) => `<option value="${esc(c.slug)}" ${c.slug === cur ? 'selected' : ''}>${esc(c.name)}</option>`).join('')}
          </select></label>
      </div>
      <div class="mreg-acts">
        ${(State.member?.base_role || State.member?.role) === 'viewer' ? `
          <span class="meta">${pending ? 'Pending review — a manager approves this.' : 'Read-only view.'}</span>` : `
        ${pending ? `
          <button class="bks-tool-btn mreg-ok" type="button" data-reg-review="approve">Approve</button>
          <button class="bks-tool-btn" type="button" data-reg-review="reject">Dismiss</button>` : ''}
        <button class="bks-tool-btn mreg-save" type="button" id="mreg-save">Save category</button>`}
      </div>
    </div>`;
  const close = () => ov.remove();
  eonBackdropClose(ov, close);  // ghost-click law (2026-07-25)
  mtxEscClose(ov, close);
  ov.querySelector('.mreg-x').addEventListener('click', close);
  ov.querySelector('#mreg-save')?.addEventListener('click', async (e) => {
    const slug = ov.querySelector('#mreg-cat-sel').value;
    if (!slug) { showToast('Pick a category first'); return; }
    e.target.disabled = true;
    try {
      const r = await api(`/api/accounting/transactions/${encodeURIComponent(tx.id)}/category`, {
        method: 'PATCH', body: JSON.stringify({ category: slug }),
      });
      if (r && r.ok !== false) {
        showToast('Category saved — EON learns from this correction');
        close(); _regFetch(true); refreshMoney();
      } else { showToast(`Save failed: ${r?.error || 'unknown'}`); e.target.disabled = false; }
    } catch (err) { showToast(`Save failed: ${err?.message || err}`); e.target.disabled = false; }
  });
  ov.querySelectorAll('[data-reg-review]').forEach((b) =>
    b.addEventListener('click', async () => {
      b.disabled = true;
      try {
        const r = await api(`/api/accounting/transactions/${encodeURIComponent(tx.id)}/review`, {
          method: 'POST', body: JSON.stringify({ action: b.dataset.regReview }),
        });
        if (r && r.ok !== false) {
          showToast(b.dataset.regReview === 'approve' ? 'Approved' : 'Dismissed');
          close(); _regFetch(true); refreshMoney();
        } else { showToast(`Failed: ${r?.error || 'unknown'}`); b.disabled = false; }
      } catch (err) { showToast(`Failed: ${err?.message || err}`); b.disabled = false; }
    }));
  document.body.appendChild(ov);
}

function mtxRegOpen(filter) {
  document.querySelector('.mreg-ov.mreg-root')?.remove();
  _regS.filter = filter || 'all';
  _regS.q = '';
  const chips = [
    ['all', 'All'], ['review', 'Needs review'], ['uncat', 'Uncategorized'],
    ['income', 'Income'], ['expense', 'Expenses'],
  ];
  const ov = document.createElement('div');
  ov.className = 'mreg-ov mreg-root';
  ov.innerHTML = `
    <div class="mreg-sheet" role="dialog" aria-label="Transaction register">
      <div class="mreg-head"><span>All transactions</span>
        <button class="mreg-x" type="button" aria-label="Close">&times;</button></div>
      <input class="mtx-input mreg-search" id="mreg-q" type="search"
        placeholder="Search description or counterparty" aria-label="Search transactions">
      <div class="mreg-chips">${chips.map(([k, l]) =>
        `<button class="mreg-chip ${k === _regS.filter ? 'on' : ''}" type="button" data-reg-f="${k}">${l}</button>`).join('')}</div>
      <div id="mreg-body"><div class="mtx-empty">Loading…</div></div>
    </div>`;
  const close = () => ov.remove();
  eonBackdropClose(ov, close);  // ghost-click law (2026-07-25)
  mtxEscClose(ov, close);
  ov.querySelector('.mreg-x').addEventListener('click', close);
  let deb;
  ov.querySelector('#mreg-q').addEventListener('input', (e) => {
    clearTimeout(deb);
    deb = setTimeout(() => { _regS.q = e.target.value.trim(); _regFetch(true); }, 350);
  });
  ov.querySelectorAll('[data-reg-f]').forEach((b) =>
    b.addEventListener('click', () => {
      _regS.filter = b.dataset.regF;
      ov.querySelectorAll('[data-reg-f]').forEach((x) =>
        x.classList.toggle('on', x === b));
      _regFetch(true);
    }));
  document.body.appendChild(ov);
  _regFetch(true);
}

// Inbox chips beside "Recent": pending-review + uncategorized counts
// for the current window — each one tap-opens the register pre-filtered.
async function _mtxRegInboxCounts() {
  const slot = document.getElementById('money-reg-inbox');
  if (!slot) return;
  const w = moneyPeriodToWindow(State.moneyPeriod || 'this_month');
  const base = `period=${encodeURIComponent(w.apiPeriod)}&limit=1`;
  try {
    const [rev, unc] = await Promise.all([
      api(`/api/accounting/transactions?${base}&status=pending_review`),
      api(`/api/accounting/transactions?${base}&uncategorized=true`),
    ]);
    const bits = [];
    if ((rev.total_count || 0) > 0) {
      bits.push(`<button class="mreg-inbox warn" type="button" onclick="mtxRegOpen('review')">${rev.total_count} need review</button>`);
    }
    if ((unc.total_count || 0) > 0) {
      bits.push(`<button class="mreg-inbox" type="button" onclick="mtxRegOpen('uncat')">${unc.total_count} uncategorized</button>`);
    }
    slot.innerHTML = bits.join(' ');
  } catch (e) { /* chips are best-effort — the register itself always works */ }
}

// ══════════════════════════════════════════════════════════════════
// LEDGER-2 — Money → Ledger: the bench (operator go 2026-08-10).
// Every line explainable, editable to exactly its source's depth,
// bulk-clearable, audited. Vault rules honored: lazy (nothing fetches
// until the tab opens), fail-soft per card, mono figures right-
// aligned, generation token so a slow leg never paints a fresh tab.
// ══════════════════════════════════════════════════════════════════

// PARITY LAW (test-pinned): this matrix mirrors accounting.py
// LEDGER_CLASSIFY_FIELDS / LEDGER_BANK_FIELDS / LEDGER_FREE_FIELDS /
// LEDGER_MIRROR_SOURCES / LEDGER_FREE_SOURCES. The server enforces;
// this copy only decides which inputs render. Drift = a field the UI
// offers and the server refuses — keep them identical.
const LDG_MATRIX = {
  classify: ['category_id', 'location_id', 'remarks'],
  bank: ['category_id', 'location_id', 'remarks', 'kind', 'receipt_url'],
  free: ['category_id', 'location_id', 'remarks', 'kind', 'receipt_url',
         'amount', 'currency', 'fx_rate_to_ngn', 'occurred_at',
         'description', 'counterparty', 'account_id', 'direction'],
  mirrorSources: ['shopify', 'meta_ads', 'tiktok_ads', 'snapchat_ads'],
  freeSources: ['manual', 'chat', 'photo_receipt', 'imported'],
};

function ldgEditable(source) {
  const s = (source || '').toLowerCase();
  if (LDG_MATRIX.mirrorSources.includes(s)) return LDG_MATRIX.classify;
  if (LDG_MATRIX.freeSources.includes(s)) return LDG_MATRIX.free;
  return LDG_MATRIX.bank;   // unknown sources get the conservative tier
}

const _ldgS = {
  queue: 'review', kind: null, source: null,
  period: 'all_time', q: '', rows: [], total: 0, offset: 0,
  sel: new Set(), cats: null, gen: 0,
  // WKS-1 (the worksheet): server-driven sort + per-device hidden
  // columns + the deterministic suggestion map (row id → offer).
  sort: 'occurred_at', dir: 'desc', sugg: {},
  hide: (() => {
    try { return new Set(JSON.parse(localStorage.getItem('eon_wks_hide') || '[]')); }
    catch (e) { return new Set(); }
  })(),
  // WKS-2 (vet round): one in-flight gate for every filing path, a
  // deferred-repaint flag while a cell editor is open, the session's
  // filed-count tally, and the overnight-ink stamp (the previous
  // sitting's read time, per device — 'landed' means created_at, the
  // sync time, never a claim about when the money moved).
  filing: false, repaintQueued: false, filed: 0, flashAfter: null,
  prevSeen: (() => {
    try { return localStorage.getItem('eon_wks_seen') || null; }
    catch (e) { return null; }
  })(),
};

function renderMoneyLedger() {
  return `
    <div class="section-h money-section-h">
      <h2>Book entries.</h2>
      <span class="meta">Search, inspect and complete the accounting records</span>
    </div>
    ${renderMoneyTabBar('ledger')}
    <div class="ldg-strip" id="ldg-strip">
      <button class="ldg-tile" data-ldg-queue="review" type="button">
        <span class="ldg-tile-n mono-num" id="ldg-n-review">—</span>
        <span class="ldg-tile-l">pending review</span>
        <span class="ldg-tile-m mono-num" id="ldg-m-review"></span></button>
      <button class="ldg-tile" data-ldg-queue="uncat" type="button">
        <span class="ldg-tile-n mono-num" id="ldg-n-uncat">—</span>
        <span class="ldg-tile-l">uncategorized</span>
        <span class="ldg-tile-m mono-num" id="ldg-m-uncat"></span></button>
      <button class="ldg-tile" data-ldg-queue="_books" type="button"
              title="Unexplained bank lines — shows them in this list">
        <span class="ldg-tile-n mono-num" id="ldg-n-unex">—</span>
        <span class="ldg-tile-l">unexplained bank lines</span>
        <span class="ldg-tile-m mono-num" id="ldg-m-unex"></span></button>
      <button class="ldg-tile" data-ldg-queue="_rules" type="button"
              title="Rules — every explanation should teach one">
        <span class="ldg-tile-n mono-num" id="ldg-n-rules">—</span>
        <span class="ldg-tile-l">rules learned</span>
        <span class="ldg-tile-m mono-num"></span></button>
      <button class="ldg-tile" data-ldg-queue="_sched" type="button"
              title="Returned scheduled payments due in the next 30 days or already late; this may not include every obligation">
        <span class="ldg-tile-n mono-num" id="ldg-n-sched">—</span>
        <span class="ldg-tile-l">due or late · next 30d</span>
        <span class="ldg-tile-m mono-num" id="ldg-m-sched"></span></button>
    </div>
    <details class="mw-autonomy"><summary>What EON is doing here</summary><p>Reads the selected records and looks for category suggestions from your saved rules. Suggestions do not change the books.</p><p id="ldg-auto-status" role="status">No suggestion read is confirmed for this view yet.</p><p>You choose whether to apply a suggestion, approve an entry or save an edit. Open a row to inspect its recorded history. This panel describes this view, not unverified background work.</p></details>
    <div class="ldg-tools">
      <div class="ldg-chiprow" id="ldg-queues">
        <button class="ldg-chip" data-q="review" type="button">Review</button>
        <button class="ldg-chip" data-q="uncat" type="button">Uncategorized</button>
        <button class="ldg-chip" data-q="all" type="button">All</button>
        <button class="ldg-chip" data-q="bank" type="button">Bank lines</button>
      </div>
      <div class="ldg-chiprow" id="ldg-kinds">
        <button class="ldg-chip" data-k="in" type="button">In</button>
        <button class="ldg-chip" data-k="out" type="button">Out</button>
      </div>
      <select class="ldg-sel" id="ldg-source" aria-label="Source">
        ${[['', 'Every source'], ['bank_sync', 'Bank'], ['chat', 'Chat'],
          ['manual', 'Manual'], ['photo_receipt', 'Receipts'], ['shopify', 'Shopify'],
          ['meta_ads', 'Meta ads'], ['tiktok_ads', 'TikTok ads'],
          ['snapchat_ads', 'Snap ads'], ['imported', 'Imported']].map(([key, label]) =>
          `<option value="${key}" ${key === (_ldgS.source || '') ? 'selected' : ''}>${label}</option>`).join('')}
      </select>
      <select class="ldg-sel" id="ldg-period" aria-label="Period">
        ${[['all_time', 'All time'], ['this_month', 'This month'],
          ['last_month', 'Last month'], ['this_year', 'This year']].map(([key, label]) =>
          `<option value="${key}" ${key === _ldgS.period ? 'selected' : ''}>${label}</option>`).join('')}
      </select>
      <input class="ldg-search" id="ldg-q" type="search"
             value="${esc(_ldgS.q)}"
             placeholder="Search description or counterparty" aria-label="Search">
      <button class="ldg-btn" id="ldg-cols-btn" type="button"
              title="Show or hide worksheet columns">Columns</button>
      <button class="ldg-btn" id="ldg-rules-btn" type="button">Rules</button>
    </div>
    <div class="ldg-bulk" id="ldg-bulk" hidden>
      <span id="ldg-bulk-n" class="mono-num"></span>
      <span id="ldg-bulk-sum" class="mono-num wks-bulk-sum"
            title="Signed sum of the selected rows"></span>
      <button class="ldg-btn ldg-btn-act" id="ldg-bulk-cat" type="button">Categorize</button>
      <button class="ldg-btn" id="ldg-bulk-approve" type="button">Approve</button>
      <button class="ldg-btn" id="ldg-bulk-clear" type="button">Clear</button>
    </div>
    <div class="data-card ldg-card">
      <div id="ldg-rows" class="ldg-rows">
        <div class="skel skel-line" style="width:100%;height:44px"></div>
        <div class="skel skel-line" style="width:100%;height:44px;margin-top:8px"></div>
      </div>
      <div class="ldg-foot">
        <span id="ldg-count" class="mono-num"></span>
        <span id="wks-tot" class="mono-num wks-tot"
              title="Totals over the loaded rows — the label says when that is a partial view"></span>
        <button class="ldg-btn" id="ldg-more" type="button" hidden>Load 100 more</button>
      </div>
    </div>`;
}

// The server's reason, not a generic verb. api() already unwraps
// FastAPI's HTTPException(detail={"error": ...}) into error.message —
// the bench handlers caught the throw and showed "Bridge failed"
// where the server had said "line already ignored" (adversarial pass,
// 2026-08-10). The `else showToast(r?.error)` branches those handlers
// carried were unreachable for routes that raise.
// Chromium-executed 2026-08-10: BOTH 403 branches were wrong. A bare
// 403 (message 'API error') returned at the first guard and never
// reached the rights hint — the one case where the hint is the only
// information there is. And a 403 that DID carry a server reason
// ("manager_required_for_fact_edits") had it replaced by the generic
// hint — this helper's whole job, inverted. Real reason first; the
// hint only when there is nothing better to say.
function _ldgErr(e, fallback) {
  const m = (e && e.message) ? String(e.message) : '';
  const real = (m && m !== 'API error') ? m : '';
  if (e && e.status === 403) {
    return real || `${fallback} — you don't have rights for that`;
  }
  return real || fallback;
}

// /bank-rec/lines/{id}/ignore needs ACCOUNTING_MANAGE while /bridge
// and the list need less — the drawer rendered Ignore for everyone and
// a member's tap died as a silent 403 behind a generic toast
// (adversarial pass, 2026-08-10).
function _ldgCanManage() {
  const role = (State.member?.base_role || '').toLowerCase();
  return ['owner', 'admin', 'manager'].includes(role);
}

async function _ldgCats() {
  if (_ldgS.cats) return _ldgS.cats;
  try {
    const r = await api('/api/accounting/categories');
    _ldgS.cats = r.categories || [];
  } catch (e) {
    // NEVER cache the failure. `[]` is truthy, so `_ldgS.cats = []`
    // pinned the answer to "there are no categories" for the whole
    // life of the page after ONE dropped request — and CRUX has 42 of
    // them. Every surface then went quietly empty: the composer
    // offered no category, the cell editor showed a categorized row as
    // "uncategorized", and bulk categorize refused every slug typed
    // with "No category 'x'". An error was rendering as an empty list.
    // Returning a bare [] for THIS call only leaves the next surface
    // free to try again.
    return [];
  }
  return _ldgS.cats;
}

function _ldgParams() {
  const p = new URLSearchParams();
  p.set('period', _ldgS.period);
  p.set('limit', '100');
  p.set('offset', String(_ldgS.offset));
  if (_ldgS.queue === 'review') p.set('status', 'pending_review');
  if (_ldgS.queue === 'uncat') p.set('uncategorized', 'true');
  if (_ldgS.kind) p.set('kind', _ldgS.kind === 'in' ? 'income' : 'expense');
  if (_ldgS.source) p.set('source', _ldgS.source);
  if (_ldgS.q) p.set('q', _ldgS.q);
  if (_ldgS.sort !== 'occurred_at' || _ldgS.dir !== 'desc') {
    p.set('sort', _ldgS.sort); p.set('dir', _ldgS.dir);
  }
  return p.toString();
}

async function _ldgFetch(append) {
  const gen = ++_ldgS.gen;
  // WKS-2: selection survives a refetch — it intersects with the new
  // rows after load instead of being cleared up front, so a cell edit
  // that triggers a membership refetch no longer wipes a working set.
  if (!append) { _ldgS.offset = 0; }
  // verify round 2: one append in flight — a double-clicked "Load 100
  // more" bumped the offset twice and the superseded fetch's page was
  // silently skipped (a hole in the loaded rows)
  if (append) {
    if (_ldgS.appending) return;
    _ldgS.appending = true;
  }
  // LEDGER-3: the bank queue is its own book (bank_transactions, not
  // transactions) with its own two moves — bridge and ignore.
  if (_ldgS.queue === 'bank') {
    // the bank book carries no worksheet selection, totals, or ink —
    // clear the previous queue's, or bulk verbs act on ghosts. The
    // rows and the count go with them: they belong to the OTHER book,
    // and leaving them loaded is what let a stray _ldgPaint repaint
    // the transactions page over the bank queue. Switching back
    // re-fetches, so nothing is lost by dropping them.
    _ldgS.rows = [];
    _ldgS.total = 0;
    _ldgS.sel.clear();
    _ldgBulkPaint();
    const tot = document.getElementById('wks-tot');
    if (tot) tot.textContent = '';
    if ((State.member?.base_role || State.member?.role) === 'owner' && !State.impersonating) {
      // Review owns this queue. A later Ledger visit starts in its own book.
      _ldgS.queue = 'all';
      writeMoneyView(window.history,moneyViewScope(State),'ledger',_ldgS);
      moneyOpenView('workbench'); return;
    }
    try {
      const r = await api('/api/finance/bank-rec/unexplained?days=60');
      if (gen !== _ldgS.gen) return;
      _ldgPaintBank(r.lines || [], r.total || 0, r.hidden || 0, r.hidden_by_currency, r.hidden_unknown_amount_count);
    } catch (e) {
      if (gen !== _ldgS.gen) return;
      const host = document.getElementById('ldg-rows');
      if (host) {
        host.innerHTML = `<div class="ldg-empty">Couldn't load the bank lines — <button class="ldg-btn" id="ldg-retry" type="button">retry</button></div>`;
        document.getElementById('ldg-retry')?.addEventListener('click', () => _ldgFetch());
      }
    }
    return;
  }
  try {
    const r = await api(`/api/accounting/transactions?${_ldgParams()}`);
    if (gen !== _ldgS.gen) { _ldgS.appending = false; return; }   // a fresh tab visit owns the paint
    _ldgS.rows = append ? _ldgS.rows.concat(r.transactions || []) : (r.transactions || []);
    _ldgS.total = r.total_count || _ldgS.rows.length;
    _ldgS.sel = new Set([..._ldgS.sel].filter((id) =>
      _ldgS.rows.some((row) => row.id === id)));
    _ldgBulkPaint();
    _ldgS.appending = false;
    // non-forced: a fetch resolving while a cell editor is open defers
    // (the kernel's promise holds for refetches too); the stamp writes
    // AFTER the paint call so an abandoned fetch never marks rows seen
    _ldgPaint();
    if (_ldgS.focusId && !append) _ldgFocus(gen);
    try { localStorage.setItem('eon_wks_seen', new Date().toISOString()); }
    catch (e2) { /* per-device convenience only */ }
  } catch (e) {
    _ldgS.appending = false;
    if (gen !== _ldgS.gen) return;
    _ldgS.focusId = _ldgS.from = null;   // a named entry never opens on a later, unrelated load
    // WKS-2: a failed "Load 100 more" keeps every loaded row on the
    // glass — only a failed FIRST page gets the empty-with-retry state
    // (error ≠ empty, and error ≠ amnesia either).
    if (append) {
      _ldgS.offset = Math.max(0, _ldgS.offset - 100);
      showToast('Load more failed — tap Load more to retry');
      _ldgPaint();
      return;
    }
    const host = document.getElementById('ldg-rows');
    if (host) {
      // No inline onclick — this file loads as a module, so top-level
      // names never reach window (verdict repair #3: the dead retry).
      host.innerHTML = `<div class="ldg-empty">Couldn't load the ledger — <button class="ldg-btn" id="ldg-retry" type="button">retry</button></div>`;
      document.getElementById('ldg-retry')
        ?.addEventListener('click', () => _ldgFetch());
    }
  }
}

// ── WKS-1: the worksheet (operator directive 2026-08-30) ──
// The bench's list becomes a sheet: sticky sortable header, in-place
// cell editing to exactly the row's source depth, a composer row that
// files through the audited spine, and category offers from the SAME
// rules the sweep applies — deterministic, labeled with their
// predicate, never a model guess dressed as a fact.

const _WKS_COLS = [
  { k: 'sel',    w: '54px' },
  { k: 'date',   w: '104px', sort: 'occurred_at', label: 'Date', f: 'occurred_at' },
  { k: 'desc',   w: 'minmax(180px,2fr)', sort: 'description', label: 'Description', f: 'description' },
  { k: 'cparty', w: 'minmax(120px,1.2fr)', sort: 'counterparty', label: 'Counterparty', f: 'counterparty', hideable: true },
  { k: 'cat',    w: 'minmax(150px,1.3fr)', label: 'Category', f: 'category_id' },
  { k: 'amt',    w: '132px', sort: 'amount_ngn', label: 'Amount', f: 'amount' },
  { k: 'stat',   w: '120px', sort: 'status', label: 'Status', hideable: true },
  { k: 'open',   w: '54px' },
];

function _wksVisibleCols() {
  const cols = _WKS_COLS.filter((c) => !(c.hideable && _ldgS.hide.has(c.k)));
  // WKS-2: hiding Status must never hide the composer's out/in flip —
  // revenue stayed fileable only through that control. The flip moves
  // into the (widened) last cell when its home column is hidden.
  return _ldgS.hide.has('stat')
    ? cols.map((c) => (c.k === 'open' ? { ...c, w: '76px' } : c))
    : cols;
}

// ── WKS-2 helpers: the amount grammar + the repaint that preserves ──

// Amount shorthand — 12k, 1.2m, 4500+1200, 1,500. A strict grammar:
// anything outside it returns null and is REFUSED, never guessed (one
// extra zero is a ₦10M lie).
function _wksAmt(raw) {
  const s = String(raw || '').trim().toLowerCase()
    .replace(/,/g, '').replace(/\s+/g, '').replace(/^₦/, '');
  if (!s || !/^[\d.km+]+$/.test(s)) return null;
  let sum = 0;
  for (const p of s.split('+')) {
    const m = /^(\d+(?:\.\d+)?)(k|m)?$/.exec(p);
    if (!m) return null;
    sum += Number(m[1]) * (m[2] === 'k' ? 1e3 : m[2] === 'm' ? 1e6 : 1);
  }
  return sum > 0 ? sum : null;
}

// Vet round criticals: unconditional innerHTML rebuilds wiped
// half-typed composer drafts, destroyed the next cell's editor, and
// reset horizontal scroll. The kernel: snapshot the composer + focus +
// caret + scrollLeft before a rebuild, restore after; and a paint
// while a cell editor is open is DEFERRED (repaintQueued) unless
// forced by the editor's own close path.
function _wksCompSnap() {
  const g = (id) => document.getElementById(id);
  const ae = document.activeElement;
  return {
    vals: {
      date: g('wksc-date')?.value, desc: g('wksc-desc')?.value,
      cparty: g('wksc-cparty')?.value,
      // a parked-but-unapplied wish (options still loading) is part of
      // the draft too — losing it dropped the sticky category
      cat: g('wksc-cat')?.value || g('wksc-cat')?.dataset.want,
      amt: g('wksc-amt')?.value, kind: g('wksc-kind')?.dataset.k,
    },
    focus: ae && /^wksc-/.test(ae.id || '') ? ae.id : null,
    selStart: (ae && ae.selectionStart != null) ? ae.selectionStart : null,
    selEnd: (ae && ae.selectionEnd != null) ? ae.selectionEnd : null,
    scroll: document.querySelector('.wks-scroll')?.scrollLeft || 0,
  };
}

function _wksCompRestore(s) {
  if (!s) return;
  const g = (id) => document.getElementById(id);
  const set = (id, v) => { const el = g(id); if (el && v) el.value = v; };
  set('wksc-date', s.vals.date); set('wksc-desc', s.vals.desc);
  set('wksc-cparty', s.vals.cparty); set('wksc-amt', s.vals.amt);
  // the flip is restored BEFORE the category: the category list is cut
  // to the direction, so the other order can leave a cost category
  // parked on a money-in row
  const kb = g('wksc-kind');
  if (kb && s.vals.kind === 'in') {
    kb.dataset.k = 'in'; kb.textContent = 'in'; kb.classList.add('wks-kind-in');
  }
  const cat = g('wksc-cat');
  if (cat && s.vals.cat) {
    // options may still be loading — park the wish, the loader honors it
    cat.dataset.want = s.vals.cat;
    if (cat._wksFill) cat._wksFill();
    else if ([...cat.options].some((o) => o.value === s.vals.cat)) cat.value = s.vals.cat;
  }
  const sc = document.querySelector('.wks-scroll');
  if (sc && s.scroll) sc.scrollLeft = s.scroll;
  if (s.focus) {
    const el = g(s.focus);
    if (el) {
      el.focus();
      try {
        if (s.selStart != null && el.setSelectionRange) el.setSelectionRange(s.selStart, s.selEnd);
      } catch (e) { /* date inputs refuse ranges — focus is enough */ }
    }
  }
}

// At most one blur-commit can be pending; opening a new editor FLUSHES
// it (the typed value commits immediately) instead of letting a 120ms
// timer destroy the editor the operator just opened.
let _wksBlurTimer = 0;
let _wksPendingCommit = null;
function _wksFlushPending() {
  if (_wksPendingCommit) {
    const f = _wksPendingCommit;
    _wksPendingCommit = null;
    clearTimeout(_wksBlurTimer);
    f();
  }
}

// the rule-birth cascade, callable from EVERY path that can learn a
// rule (cell commit, drawer save, bulk categorize) — purging only the
// "rules had nothing" verdicts so on-screen twins re-ask and get their
// chip now, not after a reload
function _wksRuleBorn() {
  Object.keys(_ldgS.sugg).forEach((k) => {
    if (_ldgS.sugg[k] === null) delete _ldgS.sugg[k];
  });
  _ldgSummary();
}

function _wksCanLog() {
  return (State.member?.base_role || State.member?.role) !== 'viewer';
}

// Mirrors the server gate exactly: classification fields ride
// ACCOUNTING_LOG for every non-viewer; fact fields additionally need
// the manager tier (the server refuses beyond-classify edits without
// ACCOUNTING_MANAGE — the cell must not offer what the save refuses).
function _wksCellEditable(t, field) {
  if (t.status === 'voided') return false;
  if (!ldgEditable(t.source).includes(field)) return false;
  if (field === 'category_id') return _wksCanLog();
  return _ldgCanManage();
}

// The two HAND-PICKED category selects — the composer and the cell
// editor — may only offer categories that match the row's direction.
// Live count 2026-08-31: 42 active categories, 34 of them expense,
// 7 income, 1 transfer, and every one was offered on every row and in
// both directions of the composer flip. Filing money IN under a cost
// category is not cosmetic: the period books SUBTRACT an income row
// carrying the `cogs` category from period COGS, so one mis-picked
// row drops cost of sales by its amount and lifts gross margin by the
// same, with nothing on screen to say so. The composer made it easy —
// the category stayed selected across a flip from out to in.
// This belongs on the dropdowns, NOT on the server: 1,468 mirrored
// Shopify refund reversals (₦43.9M) are legitimately income rows on a
// cost category, and a server ban would refuse the truth.
// Those same mirrored rows open in the cell editor, so the row's
// CURRENT category is always kept in the list — a filter that dropped
// it would leave the select silently showing a value it no longer
// offers. An unrecognized kind filters nothing: better to offer too
// much than to hide the only right answer.
function _wksCatsForKind(cats, kind, curSlug) {
  const want = ['income', 'expense', 'transfer'].includes(kind) ? kind : null;
  if (!want) return cats || [];
  return (cats || []).filter((c) =>
    c.kind === want || (curSlug && c.slug === curSlug));
}

function _wksHeaderHtml(cols) {
  const cells = cols.map((c) => {
    if (c.k === 'sel') {
      return `<span class="wks-c wks-c-sel"><input type="checkbox" id="wks-selall"
        aria-label="Select all loaded rows"></span>`;
    }
    if (!c.label) return `<span class="wks-c wks-c-${c.k}"></span>`;
    if (!c.sort) return `<span class="wks-c wks-c-${c.k} wks-hlab">${c.label}</span>`;
    const on = _ldgS.sort === c.sort;
    const dirWord = _ldgS.dir === 'asc' ? 'ascending' : 'descending';
    const caret = on ? (_ldgS.dir === 'asc' ? '▴' : '▾') : '';
    return `<button type="button" class="wks-c wks-c-${c.k} wks-hlab wks-sort ${on ? 'wks-sort-on' : ''}"
      data-wks-sort="${c.sort}" aria-pressed="${on}"
      aria-label="Sort by ${c.label}${on ? `, ${dirWord}` : ', not sorted'}"
      title="Sort the whole ledger by ${c.label.toLowerCase()}">${c.label}<span class="wks-caret" aria-hidden="true">${caret}</span></button>`;
  }).join('');
  return `<div class="wks-r wks-h">${cells}</div>`;
}

function _wksComposerHtml(cols) {
  // Lagos calendar, not UTC — between 00:00 and 00:59 WAT the UTC date
  // is still yesterday, and a filing date is a statement of fact.
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Lagos' });
  const flip = `<button class="wks-kind" id="wksc-kind" type="button" data-k="out"
      aria-label="Direction: money out — activate to flip to money in"
      title="Money out or in — tap to flip">out</button>`;
  const statHidden = !cols.some((c) => c.k === 'stat');
  const inner = {
    sel: `<span class="wks-c wks-c-sel wks-plus" aria-hidden="true">+</span>`,
    date: `<span class="wks-c"><input class="wks-in mono-num" id="wksc-date" type="date"
      value="${today}" aria-label="New row date"></span>`,
    desc: `<span class="wks-c"><input class="wks-in" id="wksc-desc"
      placeholder="Log an expense — start typing" aria-label="New row description"></span>`,
    cparty: `<span class="wks-c"><input class="wks-in" id="wksc-cparty"
      placeholder="counterparty" aria-label="New row counterparty"></span>`,
    cat: `<span class="wks-c"><select class="wks-in" id="wksc-cat" aria-label="New row category">
      <option value="">category…</option></select></span>`,
    amt: `<span class="wks-c wks-c-amt"><input class="wks-in mono-num wks-amt-in" id="wksc-amt"
      type="text" inputmode="decimal" placeholder="0 · 12k · 1.2m"
      aria-label="New row amount — shorthand 12k, 1.2m, 4500+1200 accepted"><span
      class="wks-amt-echo mono-num" id="wksc-echo" aria-live="polite"></span></span>`,
    stat: `<span class="wks-c">${flip}</span>`,
    open: `<span class="wks-c wks-c-open">${statHidden ? flip : ''}<button class="wks-file" id="wksc-file" type="button"
      title="File it (Enter) — audited, source: manual" aria-label="File the new row">↵</button></span>`,
  };
  return `<div class="wks-r wks-comp" id="wks-comp">${cols.map((c) => (inner[c.k] || '<span class="wks-c"></span>').replace('<span class=','<span data-label="'+esc(c.label||'')+'" class=')).join('')}</div>`;
}

function _ldgRowHtml(t, cols) {
  cols = cols || _wksVisibleCols();
  const catName = t.transaction_categories?.name;
  const uncat = !t.category_id;
  const sug = uncat && _ldgS.sugg[t.id];
  // overnight ink: created_at past the previous sitting's stamp gets a
  // thin tick — "landed" (sync time), never a claim about the sale time
  const fresh = _ldgS.prevSeen && t.created_at && t.created_at > _ldgS.prevSeen;
  const cell = (c, body, spoken, cls = '') => {
    const ed = c.f && _wksCellEditable(t, c.f);
    const lockTitle = t.status === 'voided' ? 'Voided — restore to edit'
      : `Locked for ${esc(t.source || 'this source')} — classification only`;
    // a11y: the label carries the VALUE (aria-label overrides content,
    // so a label without it left cells mute to screen readers)
    return `<span class="wks-c wks-c-${c.k} ${cls} ${ed ? 'wks-edit' : (c.f ? 'wks-lockc' : '')}"
      data-label="${esc(c.label||'')}" ${ed ? `tabindex="0" role="button" data-wks-f="${c.f}" data-wks-id="${esc(t.id)}"
        aria-label="${c.label ? esc(c.label) : c.f}: ${esc(spoken || '—')} — Enter to edit" title="Click or Enter to edit"`
        : (c.f ? `data-wks-lock="${lockTitle}" title="${lockTitle}"` : '')}>${body}</span>`;
  };
  const amountKnown=(typeof t.amount_ngn==='number'||typeof t.amount_ngn==='string'&&t.amount_ngn.trim()!=='')&&Number.isFinite(Number(t.amount_ngn));
  const amtSpoken=amountKnown?`${t.direction==='in'?'plus':'minus'} ${Math.abs(Number(t.amount_ngn))} naira`:'Amount unavailable';
  const inner = {
    sel: `<span class="wks-c wks-c-sel"><input class="ldg-check" type="checkbox"
      data-ldg-sel="${esc(t.id)}" ${_ldgS.sel.has(t.id) ? 'checked' : ''}
      aria-label="Select ${esc(t.description || 'transaction')}"></span>`,
    date: cell(_WKS_COLS[1], `<span class="mono-num">${esc((t.occurred_at || '').slice(0, 10))}</span>`,
      (t.occurred_at || '').slice(0, 10)),
    desc: cell(_WKS_COLS[2], esc(t.description || '—'), t.description),
    cparty: cell(_WKS_COLS[3], esc(t.counterparty || '—'), t.counterparty),
    cat: cell(_WKS_COLS[4],
      `<span class="${uncat ? 'wks-uncat' : ''}">${esc(catName || 'uncategorized')}</span>` +
      (sug ? `<button type="button" class="wks-sug" data-wks-sug="${esc(t.id)}"
        aria-label="Apply EON's offer: ${esc(sug.name)} — ${esc(sug.why)}"
        title="Suggested from your rules — ${esc(sug.why)}. Apply only after checking.">Rule suggests: ${esc(sug.name)}</button>` : ''),
      catName || 'uncategorized'),
    amt: cell(_WKS_COLS[5], `<span class="mono-num ${t.direction === 'in' ? 'wks-in-amt' : ''}">${amountKnown?(t.direction==='in'?'+':'−')+ngn(Math.abs(Number(t.amount_ngn))):'Amount unavailable'}</span>`,
      amtSpoken, 'wks-c-num'),
    stat: `<span class="wks-c wks-c-stat" data-label="Status"><span class="ldg-stat ldg-stat-${esc(t.status || 'recorded')}">${esc(({pending_review:'Needs review',posted:'Posted',recorded:'Recorded',voided:'Voided',dismissed:'Dismissed',rejected:'Dismissed'})[t.status]||'Status unavailable')}</span></span>`,
    open: `<span class="wks-c wks-c-open"><button type="button" class="wks-open" data-wks-open="${esc(t.id)}"
      title="History, receipt, void — the drawer" aria-label="Open ${esc(t.description || 'transaction')}">⋯</button></span>`,
  };
  return `<div class="wks-r ${t.status === 'voided' ? 'wks-void' : ''} ${fresh ? 'wks-new' : ''}" data-wks-row="${esc(t.id)}">${
    cols.map((c) => inner[c.k] || '').join('')}</div>`;
}

function _wksTotals() {
  const slot = document.getElementById('wks-tot');
  if (!slot) return;
  if (!_ldgS.rows.length) { slot.textContent = ''; return; }
  let tin = 0; let tout = 0;let unavailable=0;
  _ldgS.rows.forEach((t) => {
    if (t.status === 'voided') return;
    const known=(typeof t.amount_ngn==='number'||typeof t.amount_ngn==='string'&&t.amount_ngn.trim()!=='')&&Number.isFinite(Number(t.amount_ngn));
    if(!known){unavailable++;return;}
    const a = Math.abs(Number(t.amount_ngn));
    if (t.direction === 'in') tin += a; else tout += a;
  });
  const net = tin - tout;
  const part = _ldgS.rows.length < _ldgS.total ? ` · of the ${_ldgS.rows.length} loaded` : '';
  // overnight ink foot: counted over LOADED non-voided rows only, and
  // the partial suffix above already names when that is a partial view
  let ink = '';
  if (_ldgS.prevSeen && _ldgS.queue !== 'bank') {
    const n = _ldgS.rows.filter((t) =>
      t.status !== 'voided' && t.created_at && t.created_at > _ldgS.prevSeen).length;
    if (n) {
      const when = new Date(_ldgS.prevSeen);
      const hh = Number.isNaN(when.getTime()) ? '' :
        ` since ${when.toLocaleString('en-GB', { timeZone: 'Africa/Lagos', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
      ink = ` · ${n} landed${hh}`;
    }
  }
  slot.textContent = `in +${ngn(tin)} · out −${ngn(tout)} · net ${net < 0 ? '−' : ''}${ngn(Math.abs(net))}${part}${ink}${unavailable?` · ${unavailable} amounts unavailable and excluded`:""}`;
}

function _ldgPaint(force) {
  const host = document.getElementById('ldg-rows');
  if (!host) return;
  // WKS-3 (verify round): the Bank lines queue is a DIFFERENT BOOK.
  // _ldgPaintBank owns #ldg-rows there and _ldgFetch's bank branch
  // never fills _ldgS.rows/_ldgS.total, so this function — which only
  // knows the transactions book — would paint the LAST transactions
  // page over the unexplained bank lines and rewrite their tally
  // ("7 of 7 · 2 on unmapped accounts" became "2 of 350 · this
  // month"), or print "Nothing here — this queue is clear" over a
  // queue still holding unexplained money. One tick in the Columns
  // menu did it (every checkbox change calls this), and so did a cell
  // PATCH resolving after a switch to Bank lines. The guard sits HERE,
  // not at the callers, so no future caller can re-open the crossover.
  if (_ldgS.queue === 'bank') return;
  // WKS-2 kernel: an open cell editor owns the glass — a background
  // repaint (an offer landing, a sibling commit resolving) queues
  // instead of destroying what the operator is typing. The editor's
  // own close path repaints with force and drains the queue.
  if (!force && host.querySelector('.wks-live')) {
    _ldgS.repaintQueued = true;
    return;
  }
  _ldgS.repaintQueued = false;
  const snap = _wksCompSnap();
  const cols = _wksVisibleCols();
  const tpl = cols.map((c) => c.w).join(' ');
  const composer = (_wksCanLog() && _ldgS.queue !== 'bank') ? _wksComposerHtml(cols) : '';
  host.innerHTML = `
    <div class="wks-scroll"><div class="wks" style="--wks-cols:${tpl}">
      ${_wksHeaderHtml(cols)}
      ${composer}
      ${_ldgS.rows.map((t) => _ldgRowHtml(t, cols)).join('')
        || '<div class="ldg-empty">Nothing here — this queue is clear.</div>'}
    </div></div>`;
  const count = document.getElementById('ldg-count');
  if (count) {
    // name the window — "18 of 18" read as cleared while 130 more sat
    // outside this month (adversarial pass, 2026-08-10)
    const win = _ldgS.period === 'all_time' ? 'all time'
      : (document.getElementById('ldg-period')?.selectedOptions?.[0]?.textContent
         || _ldgS.period || '').toLowerCase();
    count.textContent = `${_ldgS.rows.length} of ${_ldgS.total}${win ? ` · ${win}` : ''}`;
  }
  const more = document.getElementById('ldg-more');
  if (more) more.hidden = _ldgS.rows.length >= _ldgS.total;
  _wksTotals();
  _wksBind(host);
  _wksCompRestore(snap);
  // a commit or membership refetch promised a flash — deliver it on
  // the fresh row, and hand the sheet's cursor back to the cell
  if (_ldgS.flashAfter) {
    const fa = _ldgS.flashAfter;
    _ldgS.flashAfter = null;
    const sel2 = fa.field === 'category_slug' ? '.wks-c-cat' : `[data-wks-f="${fa.field}"]`;
    const cell = host.querySelector(`[data-wks-row="${CSS.escape(fa.id)}"] ${sel2}`);
    cell?.classList.add('wks-flash');
    if (!snap.focus) cell?.focus();     // never steal focus from the composer
  }
  _wksSuggestFetch();
}

function _wksBind(host) {
  host.querySelectorAll('[data-wks-sort]').forEach((b) =>
    b.addEventListener('click', () => {
      const col = b.dataset.wksSort;
      if (_ldgS.sort === col) {
        _ldgS.dir = _ldgS.dir === 'desc' ? 'asc' : 'desc';
      } else { _ldgS.sort = col; _ldgS.dir = col === 'occurred_at' ? 'desc' : 'asc'; }
      writeMoneyView(window.history,moneyViewScope(State),'ledger',_ldgS);
      _ldgFetch();
    }));
  const selall = host.querySelector('#wks-selall');
  const syncSelall = () => {
    if (!selall) return;
    const n = _ldgS.rows.filter((t) => _ldgS.sel.has(t.id)).length;
    selall.checked = n > 0 && n === _ldgS.rows.length;
    selall.indeterminate = n > 0 && n < _ldgS.rows.length;   // the third state
  };
  if (selall) {
    syncSelall();
    selall.addEventListener('change', () => {
      selall.checked ? _ldgS.rows.forEach((t) => _ldgS.sel.add(t.id)) : _ldgS.sel.clear();
      _ldgBulkPaint(); _ldgPaint();
    });
  }
  host.querySelectorAll('[data-ldg-sel]').forEach((cb) => {
    cb.addEventListener('change', () => {
      cb.checked ? _ldgS.sel.add(cb.dataset.ldgSel) : _ldgS.sel.delete(cb.dataset.ldgSel);
      _ldgBulkPaint();
      syncSelall();
    });
  });
  host.querySelectorAll('[data-wks-open]').forEach((b) =>
    b.addEventListener('click', () => _ldgOpen(b.dataset.wksOpen)));
  host.querySelectorAll('[data-wks-sug]').forEach((b) =>
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      const t = _ldgS.rows.find((r) => r.id === b.dataset.wksSug);
      const s = t && _ldgS.sugg[t.id];
      if (t && s) _wksCommit(t, 'category_slug', s.slug);
    }));
  // locked cells are not dead on touch — a tap says WHY they refuse
  host.querySelectorAll('[data-wks-lock]').forEach((el) =>
    el.addEventListener('click', () => showToast(el.dataset.wksLock)));
  const editOpen = (el) => {
    const t = _ldgS.rows.find((r) => r.id === el.dataset.wksId);
    if (t) _wksEditCell(t, el.dataset.wksF, el);
  };
  host.querySelectorAll('.wks-edit').forEach((el) => {
    el.addEventListener('click', (e) => {
      // an inner control (the EON chip, a live editor) owns its click
      if (e.target !== el && e.target.closest('button, .wks-live')) return;
      editOpen(el);
    });
    el.addEventListener('keydown', (e) => {
      // Enter on the CHIP must apply the labeled offer, never open the
      // editor over it — the chip's own handler wins (a11y vet major)
      if (e.target !== el) return;
      if ((e.key === 'Enter' || e.key === ' ') && !el.querySelector('.wks-live')) {
        e.preventDefault(); editOpen(el);
      }
    });
  });
  // the composer
  const comp = host.querySelector('#wks-comp');
  if (comp) {
    _ldgCats().then((cats) => {
      const sel = comp.querySelector('#wksc-cat');
      if (!sel || sel.options.length > 1) return;
      // the list is cut to the flip's direction (see _wksCatsForKind)
      // and REBUILT whenever the flip moves — the select is hung with
      // its own filler so the flip handler outside this closure, and
      // the post-file re-arm, can call it
      let compVm = false;
      let compPrev = '';
      const compKind = () => (comp.querySelector('#wksc-kind')?.dataset.k === 'in'
        ? 'income' : 'expense');
      const fill = (keep) => {
        sel.innerHTML = '<option value="">category…</option>'
          + _wksCatsForKind(cats, compKind(), keep).map((c) => `<option value="${esc(c.slug)}">${esc(c.name)}</option>`).join('')
          + '<option value="__new__">+ New category…</option>';
        // a snapshot restore may have parked a wish before options
        // loaded — honour it only if it still fits the direction, and
        // drop it otherwise so a flip can never carry a wrong-side
        // category into the filing
        const want = keep || sel.dataset.want || '';
        if (want) {
          delete sel.dataset.want;
          if ([...sel.options].some((o) => o.value === want)) sel.value = want;
        }
        compPrev = sel.value;
      };
      sel._wksFill = fill;
      fill();
      // keyboard-browsing onto "+ New category…" must not fire a
      // blocking prompt per arrow step — only a mouse pick prompts
      // here (keyboard users create categories via the cell editor's
      // Enter-settle path)
      sel.addEventListener('mousedown', () => { compVm = true; });
      sel.addEventListener('change', async () => {
        const m = compVm;
        compVm = false;
        if (sel.value !== '__new__') { compPrev = sel.value; return; }
        if (!m) { sel.value = compPrev; return; }
        const name = prompt('New category name:');
        sel.value = compPrev;
        if (!name || !name.trim()) return;
        try {
          // born on the side the composer is filing — a category
          // created while the flip says "in" and left at the server's
          // expense default would drop straight back out of this list
          const r = await api('/api/accounting/categories',
            { method: 'POST', body: JSON.stringify({ name: name.trim(), kind: compKind() }) });
          const slug = r.slug || r.category?.slug;
          if (!slug) { showToast(r.error || 'Category create refused'); return; }
          _ldgS.cats = null;
          cats = await _ldgCats();
          fill(slug);
        } catch (e) { showToast(_ldgErr(e, 'Category create failed')); }
      });
    });
    comp.querySelectorAll('#wksc-kind').forEach((kb) =>
      kb.addEventListener('click', () => {
        kb.dataset.k = kb.dataset.k === 'out' ? 'in' : 'out';
        kb.textContent = kb.dataset.k;
        kb.classList.toggle('wks-kind-in', kb.dataset.k === 'in');
        kb.setAttribute('aria-label', kb.dataset.k === 'in'
          ? 'Direction: money in — activate to flip to money out'
          : 'Direction: money out — activate to flip to money in');
        // the flip changes which categories are legal — rebuild the
        // list, and drop the selection if it belongs to the other
        // side. It used to stick: file a cost, flip to money in for
        // the next row, and the cost category was still sitting there.
        const cs = comp.querySelector('#wksc-cat');
        if (cs && cs._wksFill) cs._wksFill();
        else if (cs) { cs.value = ''; delete cs.dataset.want; }
      }));
    // the honest echo: shorthand expands VISIBLY before anything files
    const amtIn = comp.querySelector('#wksc-amt');
    const echo = comp.querySelector('#wksc-echo');
    amtIn?.addEventListener('input', () => {
      if (!echo) return;
      const raw = amtIn.value.trim();
      const v = _wksAmt(raw);
      const plain = /^\d+(\.\d+)?$/.test(raw.replace(/,/g, ''));
      echo.textContent = v == null
        ? (raw ? '?' : '')
        : (plain ? '' : `= ${ngn(v)}`);
    });
    comp.querySelector('#wksc-file')?.addEventListener('click', () => _wksFile());
    comp.querySelectorAll('input').forEach((i) =>
      i.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' || e.repeat) return;
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) { _wksFile({ keep: true }); return; }
        // Enter walks the row: advance to the first missing required
        // field; file only when the row is whole
        const desc = (comp.querySelector('#wksc-desc')?.value || '').trim();
        const amtOk = _wksAmt(comp.querySelector('#wksc-amt')?.value) != null;
        if (desc && amtOk) { _wksFile(); return; }
        if (!desc && !amtOk && e.target.id !== 'wksc-desc' && e.target.id !== 'wksc-amt') {
          showToast('An amount and a description make a row');
        }
        const next = !desc ? comp.querySelector('#wksc-desc')
          : comp.querySelector('#wksc-amt');
        next?.focus();
      }));
  }
}

// in-place cell editor — Enter commits, Esc reverts, blur commits.
// WKS-2: opening a new editor FLUSHES any pending blur-commit; the
// commit replaces the cell content provisionally (so the deferred
// repaint never deadlocks on a live editor); focus returns to the cell.
function _wksEditCell(t, field, cellEl) {
  if (cellEl.querySelector('.wks-live')) return;
  _wksFlushPending();
  // the flush may have repainted synchronously (invalid-value path) —
  // re-resolve the cell so the editor never mounts on a detached node
  cellEl = document.querySelector(
    `[data-wks-row="${CSS.escape(t.id)}"] [data-wks-f="${field}"]`) || cellEl;
  const closeEd = () => {
    _ldgPaint(true);
    // re-focus the cell ONLY when the paint dropped focus to body — if
    // the operator already clicked into the composer (or anywhere
    // focusable), stealing it back is worse than losing the cursor
    // (verify round 2)
    const ae = document.activeElement;
    if (!ae || ae === document.body) {
      document.querySelector(
        `[data-wks-row="${CSS.escape(t.id)}"] [data-wks-f="${field}"]`)?.focus();
    }
  };
  if (field === 'category_id') {
    _ldgCats().then((cats) => {
      // the categories fetch may have raced a paint — re-resolve the
      // cell so the select never mounts on a detached node
      cellEl = document.querySelector(
        `[data-wks-row="${CSS.escape(t.id)}"] [data-wks-f="category_id"]`) || cellEl;
      const cur = cats.find((c) => c.id === t.category_id)?.slug || '';
      // never offer on a row that already has a category — a stale
      // cache entry (categorized elsewhere) must not resurface here
      const sug = !t.category_id && _ldgS.sugg[t.id];
      // only this row's own side of the books (see _wksCatsForKind);
      // whatever it already holds stays on the list either way
      const opts = _wksCatsForKind(cats, t.kind, cur);
      cellEl.innerHTML = `<select class="wks-in wks-live" aria-label="Category">
        ${sug ? `<optgroup label="EON suggests — ${esc(sug.why)}">
          <option value="${esc(sug.slug)}">${esc(sug.name)}</option></optgroup>` : ''}
        <option value="" ${cur ? '' : 'selected'}>uncategorized</option>
        ${opts.map((c) => `<option value="${esc(c.slug)}" ${c.slug === cur ? 'selected' : ''}>${esc(c.name)}</option>`).join('')}
        <option value="__new__">+ New category…</option></select>`;
      const s = cellEl.querySelector('select');
      s.focus();
      // Vet major: a change event on a CLOSED select (ArrowDown while
      // browsing) must not book a category — and via learn_from_
      // correction it could mint a wrong auto-rule. Mouse picks (popup
      // open) commit on change; keyboard changes only mark dirty and
      // commit on Enter or blur, reading the FINAL value. Esc reverts.
      let viaMouse = false;
      let settled = false;
      s.addEventListener('mousedown', () => { viaMouse = true; });
      const settle = async () => {
        if (settled) return;
        settled = true;
        _wksPendingCommit = null;
        clearTimeout(_wksBlurTimer);
        let slug = s.value;
        if (slug === '__new__') {
          const name = prompt('New category name:');
          if (!name || !name.trim()) { closeEd(); return; }
          try {
            // born on the row's own side, or the next open of this
            // editor would filter the brand-new category straight out
            const r = await api('/api/accounting/categories',
              { method: 'POST', body: JSON.stringify({ name: name.trim(), kind: t.kind || 'expense' }) });
            slug = r.slug || r.category?.slug;
            if (!slug) { showToast(r.error || 'Category create refused'); closeEd(); return; }
            _ldgS.cats = null;
          } catch (e) { showToast(_ldgErr(e, 'Category create failed')); closeEd(); return; }
        }
        if (slug === (cur || '')) { closeEd(); return; }
        const label = [...s.options].find((o) => o.value === slug)?.textContent || slug;
        cellEl.classList.add('wks-busy');
        cellEl.textContent = label;         // provisional — repaint follows the server
        _wksCommit(t, 'category_slug', slug);
      };
      // viaMouse resets per change — one mouse touch must not turn
      // every later keyboard browse into an instant commit (verify
      // round 2); the blur-settle registers in the SAME flush slot as
      // the text editor, so opening the next cell flushes it instead
      // of a 150ms timer force-painting the new editor away.
      s.addEventListener('change', () => {
        const m = viaMouse;
        viaMouse = false;
        if (m) settle();
      });
      s.addEventListener('keydown', (e) => {
        // Disarm the mouse latch on ANY key. A mouse touch that ends
        // in no change at all — re-picking the option already
        // selected, or clicking the box a second time to shut the
        // popup — fires no `change` and no `blur`, so viaMouse stayed
        // true with the select still focused. The very next arrow key
        // on the CLOSED select then took the mouse path and booked a
        // half-browsed category on the FIRST step: a PATCH, and
        // learn_from_correction mints an auto-rule off a single
        // correction, so one stray keypress also taught every future
        // twin the wrong answer. On a closed select keydown always
        // arrives before the value moves, so this routes the whole
        // keyboard walk back to Enter/blur, which read the FINAL
        // value. A real mouse pick involves no keydown and still
        // commits on change.
        viaMouse = false;
        if (e.key === 'Enter') { e.preventDefault(); settle(); }
        if (e.key === 'Escape') {
          settled = true;
          _wksPendingCommit = null;
          clearTimeout(_wksBlurTimer);
          closeEd();
        }
      });
      s.addEventListener('blur', () => {
        _wksPendingCommit = () => {
          if (!settled && cellEl.querySelector('.wks-live')) settle();
        };
        _wksBlurTimer = setTimeout(_wksFlushPending, 150);
      });
    });
    return;
  }
  const cur = field === 'occurred_at' ? (t.occurred_at || '').slice(0, 10)
    : field === 'amount' ? String(Number(t.amount || 0))
      : (t[field] || '');
  const type = field === 'occurred_at' ? 'date' : 'text';
  const isAmt = field === 'amount';
  cellEl.innerHTML = `<input class="wks-in wks-live ${field === 'description' ? '' : 'mono-num'}"
    type="${type}" ${isAmt ? 'inputmode="decimal"' : ''}
    value="${esc(cur)}" aria-label="Edit ${field}${isAmt ? ' — shorthand 12k, 1.2m accepted' : ''}">`;
  const inp = cellEl.querySelector('input');
  inp.focus();
  if (type === 'text') inp.select();     // retype replaces — sheet reflex
  let closed = false;
  const commit = () => {
    if (closed) return; closed = true;
    _wksPendingCommit = null;
    const v = isAmt ? _wksAmt(inp.value) : inp.value.trim();
    if (isAmt && v == null && inp.value.trim()) {
      showToast(`"${inp.value.trim()}" isn't an amount — 12k, 1.2m, 4500+1200`);
      closeEd(); return;
    }
    if (v == null || String(v) === String(cur)
        || (isAmt && !(v > 0))
        || (field === 'description' && !v)
        || (type === 'date' && !inp.value)) { closeEd(); return; }
    cellEl.classList.add('wks-busy');
    cellEl.textContent = isAmt ? ngn(v) : String(v);   // provisional
    _wksCommit(t, field, v);
  };
  inp.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') { closed = true; _wksPendingCommit = null; closeEd(); }
  });
  inp.addEventListener('blur', () => {
    _wksPendingCommit = commit;
    _wksBlurTimer = setTimeout(commit, 120);
  });
}

// one field, one PATCH, one audit line — the grid never shows a value
// the server has not accepted
async function _wksCommit(t, field, value) {
  try {
    const r = await api(`/api/accounting/transactions/${encodeURIComponent(t.id)}`, {
      method: 'PATCH', body: JSON.stringify({ changes: { [field]: value } }) });
    if (r && r.ok === false) {
      showToast(r.error === 'fields_locked_for_source'
        ? `Locked for ${t.source}: ${(r.refused || []).join(', ')}`
        : (r.error || 'Edit refused'));
      _ldgPaint(); return;   // non-forced: another cell's editor may be open
    }
    if (r && r.rule_learned) {
      _wksRuleBorn();
      showToast('Rule learned — twins on this sheet get their offer now');
    }
    // WKS-2 window honesty: when the edit changes the row's MEMBERSHIP
    // (queue, period window) or its position under the active sort, a
    // local merge would leave the sheet lying — refetch instead and
    // flash the row after the fresh paint.
    const sortKey = field === 'amount' ? 'amount_ngn'
      : field === 'category_slug' ? null : field;
    const affectsSort = sortKey && _ldgS.sort === sortKey;
    const affectsQueue = field === 'occurred_at'
      || (field === 'category_slug'
          && (_ldgS.queue === 'uncat' || _ldgS.queue === 'review' || t.status === 'pending_review'));
    if (field === 'category_slug') delete _ldgS.sugg[t.id];
    if (affectsSort || affectsQueue) {
      _ldgS.flashAfter = { id: t.id, field };
      _ldgFetch(); _ldgSummary();
      return;
    }
    // merge into the LIVE row object — a refetch may have replaced
    // _ldgS.rows while the PATCH was in flight, and writing into the
    // orphaned reference left an accepted edit invisible (verify
    // round 2). Row gone from the set entirely → nothing to merge;
    // the paint below shows the truthful current rows.
    const live = _ldgS.rows.find((row) => row.id === t.id);
    if (field === 'category_slug') {
      const cats = await _ldgCats();
      const c = cats.find((x) => x.slug === value);
      if (live && c) {
        live.category_id = c.id;
        live.transaction_categories = { name: c.name, slug: c.slug };
      } else if (live) {
        // The server ACCEPTED this category and may have minted a rule
        // from it — a cache that does not know the slug means the
        // CACHE is stale (the list is read once per page, so a
        // category created in another tab or by another rep is simply
        // absent), never that the row lost its category. Writing null
        // here repainted an audited row amber "uncategorized" and the
        // glass then disagreed with the books. Drop the stale list and
        // let the server tell us what the row holds.
        _ldgS.cats = null;
        _ldgS.flashAfter = { id: t.id, field };
        _ldgFetch();
        return;
      }
    } else if (live) {
      live[field] = value;
      if (field === 'amount') live.amount_ngn = Number(value) * Number(live.fx_rate_to_ngn || 1);
    }
    // flash + cursor ride the paint that actually happens — if another
    // editor is open the repaint queues and the flash queues with it
    _ldgS.flashAfter = { id: t.id, field };
    _ldgPaint();
  } catch (e) {
    showToast(_ldgErr(e, 'Edit failed'));
    _ldgPaint();
  }
}

// composer → the audited spine (source: manual = the free tier).
// WKS-2: ONE in-flight gate for every path (Enter, click, Ctrl+Enter —
// the unguarded Enter path could file the same expense twice, or
// fifteen times on a held key); the composer re-arms after a file
// (sticky date/kind/category, description+amount ALWAYS clear so a
// blind double-Enter cannot double-file, focus back on description);
// the toast never claims a row is on a sheet it isn't on.
async function _wksFile(opts) {
  if (_ldgS.filing) return;
  const g = (id) => document.getElementById(id);
  const amt = _wksAmt(g('wksc-amt')?.value);
  const desc = (g('wksc-desc')?.value || '').trim();
  const kindIn = g('wksc-kind')?.dataset.k === 'in';
  if (amt == null || !desc) {
    const raw = (g('wksc-amt')?.value || '').trim();
    showToast(raw && amt == null && desc
      ? `"${raw}" isn't an amount — 12k, 1.2m, 4500+1200`
      : 'An amount and a description make a row');
    return;
  }
  const keep = !!(opts && opts.keep);
  const snap = keep ? _wksCompSnap() : null;
  const body = {
    amount: amt, description: desc, source: 'manual',
    counterparty: (g('wksc-cparty')?.value || '').trim() || undefined,
    occurred_at: g('wksc-date')?.value || undefined,
    category: g('wksc-cat')?.value || g('wksc-cat')?.dataset.want || undefined,
  };
  _ldgS.filing = true;
  const btn = g('wksc-file');
  if (btn) btn.disabled = true;
  try {
    const r = await api(kindIn ? '/api/accounting/revenue' : '/api/accounting/expenses',
      { method: 'POST', body: JSON.stringify(body) });
    if (r && r.ok === false) {
      showToast(r.error || 'Filing refused');
      return;
    }
    _ldgS.filed += 1;
    const newId = r?.transaction?.id;
    const stickyCat = g('wksc-cat')?.value || '';
    const stickyDate = g('wksc-date')?.value || '';
    const stickyKind = g('wksc-kind')?.dataset.k || 'out';
    // Verify round 2 CRITICAL: blank the filed fields BEFORE the fetch
    // — the preserve kernel snapshots the composer at paint time, so
    // un-cleared inputs were resurrected into the fresh composer and a
    // blind second Enter double-filed the same expense. Cleared here
    // (after the POST is accepted, never before — a refused filing
    // must not eat the typed row), the snapshot carries empties;
    // keep-mode's snap above is already taken, so Ctrl+Enter still
    // restores the full row.
    ['wksc-desc', 'wksc-amt', 'wksc-cparty'].forEach((id) => {
      const el = g(id);
      if (el) el.value = '';
    });
    const echoEl = g('wksc-echo');
    if (echoEl) echoEl.textContent = '';
    const q0 = _ldgS.queue;      // a mid-file queue switch voids the landed claim
    await _ldgFetch();
    // re-arm: repaint rebuilt the composer fresh — restore the sticky
    // fields (or, in keep-mode, the whole row for its siblings)
    if (keep && snap) {
      snap.focus = null;
      _wksCompRestore(snap);
      const cp = g('wksc-cparty');
      if (cp) { cp.focus(); cp.select(); }
    } else {
      const set = (id, v) => { const el = g(id); if (el && v) el.value = v; };
      set('wksc-date', stickyDate);
      // the flip goes back FIRST: the category list is cut to the
      // direction, so restoring a sticky category before the direction
      // could leave a cost category selected on a money-in row
      const kb = g('wksc-kind');
      if (kb && stickyKind === 'in') { kb.dataset.k = 'in'; kb.textContent = 'in'; kb.classList.add('wks-kind-in'); }
      const cat = g('wksc-cat');
      if (cat && stickyCat) {
        cat.dataset.want = stickyCat;
        if (cat._wksFill) cat._wksFill();
        else if ([...cat.options].some((o) => o.value === stickyCat)) cat.value = stickyCat;
      }
      g('wksc-desc')?.focus();
    }
    // the honest landing report: only claim the row is here if it is —
    // and never off rows a superseded (queue-switched) fetch left stale
    const landed = q0 === _ldgS.queue && newId
      && _ldgS.rows.some((row) => row.id === newId);
    const review = r?.needs_review ? ' · pending review' : '';
    const blind = r?.cash_blind ? ' · no account — cash headline blind to it' : '';
    const tally = _ldgS.filed > 1 ? ` · ${_ldgS.filed} filed this sitting` : '';
    if (landed) {
      showToast(`${kindIn ? 'Revenue' : 'Expense'} filed · audited${review}${blind}${tally}`);
      // a selector LIST resolves in document order — '.wks-c-desc,
      // .wks-c' always landed on the checkbox cell (verify round 2)
      const newRow = document.querySelector(`[data-wks-row="${CSS.escape(newId)}"]`);
      (newRow?.querySelector('.wks-c-desc')
        || newRow?.querySelector('.wks-c:not(.wks-c-sel)'))?.classList.add('wks-flash');
    } else {
      showToast(`${kindIn ? 'Revenue' : 'Expense'} filed${review}${blind} — outside this view's queue or window${tally}`);
    }
    _ldgSummary();
  } catch (e) {
    showToast(_ldgErr(e, 'Filing failed'));
  } finally {
    _ldgS.filing = false;
    const b2 = g('wksc-file');
    if (b2) b2.disabled = false;
  }
}

// deterministic offers for the loaded uncategorized rows — cached per
// id (null = the rules had nothing), generation-tokened, fail-soft
async function _wksSuggestFetch() {
  if (_ldgS.queue === 'bank') return;
  const ids = _ldgS.rows
    .filter((t) => !t.category_id && t.status !== 'voided' && !(t.id in _ldgS.sugg))
    .map((t) => t.id).slice(0, 100);
  const status=document.getElementById('ldg-auto-status');
  if (!ids.length){if(status)status.textContent='No new rule lookup is needed for the loaded rows. Any suggestions shown are from the last successful read in this session.';return;}
  if(status)status.textContent=`Checking saved rules for ${ids.length} loaded entries. No categories are being applied.`;
  const gen = _ldgS.gen;
  try {
    const r = await api('/api/accounting/transactions/suggest-categories',
      { method: 'POST', body: JSON.stringify({ ids }) });
    if (gen !== _ldgS.gen) return;
    // WKS-2: only a TRUE verdict caches — an ok:false body once cached
    // null for every id and permanently suppressed offers after one
    // transient DB error
    if (!r || r.ok !== true) throw new Error('Suggestions unavailable');
    let hit = false;
    Object.entries(r.suggestions || {}).forEach(([id, s]) => { _ldgS.sugg[id] = s; hit = true; });
    ids.forEach((id) => { if (!(id in _ldgS.sugg)) _ldgS.sugg[id] = null; });
    if (hit) _ldgPaint();
    const applied=Object.values(r.suggestions||{}).filter(Boolean).length;
    if(status?.isConnected)status.textContent=`Read saved rules for ${ids.length} loaded entries at ${new Date().toLocaleTimeString('en-NG',{hour:'2-digit',minute:'2-digit'})}. ${applied} suggestions returned; none applied. The row states the suggestion and its reason.`;
  } catch (e) {if(gen===_ldgS.gen&&status?.isConnected)status.textContent='Category suggestions did not load. No category was applied. You can still inspect and edit the records.';}
}

// columns popover — per-device, persisted, never hides an audit mark
// the queue depends on (only counterparty and status are hideable)
function _wksColsMenu() {
  const btn = document.getElementById('ldg-cols-btn');
  const open = document.querySelector('.wks-colsmenu');
  if (open) {                            // the button is a real toggle
    // Run the menu's OWN teardown, never a bare remove(): the bare
    // remove left `away` and `onEsc` bound to document with the menu
    // already detached, and every later click anywhere on the page
    // still ran close() — which then pulled focus onto the Columns
    // button. Open-and-toggle-close three times and three armed
    // listeners sat there, one focus theft each.
    if (typeof open._wksClose === 'function') { open._wksClose(true); return; }
    open.remove();
    btn?.setAttribute('aria-expanded', 'false');
    return;
  }
  if (!btn) return;
  const rect = btn.getBoundingClientRect();
  const m = document.createElement('div');
  m.className = 'wks-colsmenu';
  m.setAttribute('role', 'menu');
  m.style.top = `${rect.bottom + 6}px`;
  m.style.left = `${Math.max(8, rect.right - 180)}px`;
  m.innerHTML = _WKS_COLS.filter((c) => c.hideable).map((c) => `
    <label class="wks-colrow"><input type="checkbox" data-wks-col="${c.k}"
      ${_ldgS.hide.has(c.k) ? '' : 'checked'}> ${c.label}</label>`).join('');
  m.querySelectorAll('[data-wks-col]').forEach((cb) =>
    cb.addEventListener('change', () => {
      cb.checked ? _ldgS.hide.delete(cb.dataset.wksCol) : _ldgS.hide.add(cb.dataset.wksCol);
      try { localStorage.setItem('eon_wks_hide', JSON.stringify([..._ldgS.hide])); }
      catch (e) { /* per-device convenience only */ }
      _ldgPaint();
    }));
  document.body.appendChild(m);
  btn.setAttribute('aria-expanded', 'true');
  const close = (refocus) => {
    // The cursor goes back to the Columns button only when the
    // operator ASKED to leave (Escape, a second tap) or the menu still
    // holds focus. It used to go back unconditionally — and `away`
    // runs close() for a click ANYWHERE else, so tapping a cell to
    // edit it mounted the editor, focused it, and then this line
    // yanked focus back to the button. The editor blurred, armed its
    // blur-commit, and 120ms later closed itself: the cell simply
    // could not be opened while this menu was on screen.
    const held = m.contains(document.activeElement);
    m.remove();
    btn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', away);
    document.removeEventListener('keydown', onEsc);
    if (refocus || held) btn.focus();
  };
  m._wksClose = close;                   // the toggle path tears down too
  function away(e) {
    if (!m.contains(e.target) && e.target !== btn) close();
  }
  function onEsc(e) { if (e.key === 'Escape') close(true); }
  setTimeout(() => {
    document.addEventListener('click', away);
    document.addEventListener('keydown', onEsc);
    m.querySelector('input')?.focus();
  }, 0);
}

function _ldgBulkPaint() {
  const bar = document.getElementById('ldg-bulk');
  if (!bar) return;
  bar.hidden = _ldgS.sel.size === 0;
  const n = document.getElementById('ldg-bulk-n');
  if (n) n.textContent = `${_ldgS.sel.size} selected`;
  // WKS-1: the selection sums itself — the reflex every accountant
  // brings from a real sheet (signed: in minus out)
  const se = document.getElementById('ldg-bulk-sum');
  if (se) {
    const selected=_ldgS.rows.filter(r=>_ldgS.sel.has(r.id)&&r.status!=='voided');
    const known=selected.filter(r=>(typeof r.amount_ngn==='number'||typeof r.amount_ngn==='string'&&r.amount_ngn.trim()!=='')&&Number.isFinite(Number(r.amount_ngn)));
    const missing=selected.length-known.length;
    const sum=known.reduce((total,r)=>total+(r.direction==='in'?1:-1)*Math.abs(Number(r.amount_ngn)),0);
    se.textContent = !_ldgS.sel.size ? '' : missing&&!known.length ? `Amounts unavailable for ${missing} selected records` : `Σ ${sum<0?'−':'+'}${ngn(Math.abs(sum))}${missing?` · ${missing} amounts unavailable; subtotal only`:''}`;
  }
  const pending = _ldgS.rows.filter((r) => _ldgS.sel.has(r.id) && r.status === 'pending_review');
  const ap = document.getElementById('ldg-bulk-approve');
  if (ap) ap.hidden = pending.length === 0;
}

async function _ldgSummary() {
  try {
    const r = await api('/api/accounting/transactions/inbox-summary');
    const put = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
    put('ldg-n-review', r.pending_review.count);
    put('ldg-m-review', `₦${(r.pending_review.sum_ngn / 1e6).toFixed(1)}M`);
    put('ldg-n-uncat', r.uncategorized.count);
    put('ldg-m-uncat', `₦${(r.uncategorized.sum_ngn / 1e6).toFixed(1)}M`);
    put('ldg-n-rules', r.rules_count);
    if (r.unexplained) {
      _ldgS.bankDigestTotal = r.unexplained.count;   // all accounts
      put('ldg-n-unex', r.unexplained.count);
      put('ldg-m-unex', `₦${(r.unexplained.sum_ngn / 1e6).toFixed(1)}M`);
    }
  } catch (e) { /* tiles fail soft; the grid still works */ }
  // LEDGER-4: the scheduled tile reads the obligations composer —
  // separate fetch, separately fail-soft.
  const putScheduled = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
  try {
    const ob = await api('/api/tasks/obligations');
    if (ob?.ok !== true || !Array.isArray(ob.due)) throw new Error('Schedule unavailable');
    const known = v => v !== null && v !== undefined && typeof v !== 'boolean' && String(v).trim() !== '' && Number.isFinite(Number(v));
    if (ob.due.some(row => !row || !known(row.days))) throw new Error('Schedule dates unavailable');
    const due = ob.due.filter(row => Number(row.days) <= 30);
    const amountsKnown = due.every(row => known(row.amount_ngn) && Number(row.amount_ngn) >= 0);
    const owed = amountsKnown ? due.reduce((sum, row) => sum + Number(row.amount_ngn), 0) : null;
    putScheduled('ldg-n-sched', due.length);
    putScheduled('ldg-m-sched', owed === null ? 'Some amounts unavailable' : `${ngn(owed)} · returned schedule`);
  } catch (e) {
    putScheduled('ldg-n-sched', '—');
    putScheduled('ldg-m-sched', 'Schedule unavailable');
  }
}

// ── the drawer: field-gated editor + history ──
async function _ldgOpen(txId, outside = null, from = null) {
  const t = outside || _ldgS.rows.find((r) => r.id === txId);
  if (!t) return;
  const hasAmount=(typeof t.amount==='number'||typeof t.amount==='string'&&t.amount.trim()!=='')&&Number.isFinite(Number(t.amount));
  const opener=document.activeElement;
  const cats = await _ldgCats();
  const editable = t.status === 'voided' ? [] : ldgEditable(t.source);
  const can = (f) => editable.includes(f);
  const role = State.member?.base_role || 'member';
  const canVoid = ['owner', 'admin'].includes(role)
    && !LDG_MATRIX.mirrorSources.includes((t.source || '').toLowerCase());
  const catOpts = cats.map((c) =>
    `<option value="${esc(c.slug)}" ${c.id === t.category_id ? 'selected' : ''}>${esc(c.name)}</option>`).join('');
  const ov = document.createElement('div');
  ov.className = 'mreg-ov';
  ov.innerHTML = `
    <div class="mreg-sheet mreg-detail ldg-drawer mw-book-drawer" role="dialog" aria-label="Transaction" aria-modal="true">
      <div class="mreg-head"><span>${esc(t.description || 'Transaction')}</span>
        <button class="mreg-x" type="button" aria-label="Close">&times;</button></div>
      ${!outside ? '' : _ldgS.queue === 'all' && _ldgS.period === 'all_time' && !_ldgS.q && !_ldgS.kind && !_ldgS.source
        ? '<p class="ldg-lockline" role="status">This entry is not on the loaded page of the list. The list, its count and its page are unchanged.</p>'
        : '<p class="ldg-lockline" role="status">This entry is outside the current list filter. The list, its count and its page are unchanged. <button class="ldg-btn" type="button" data-ldg-show-in-list>Show in list</button></p>'}
      ${from ? '<p class="ldg-lockline">Opened from a bank line in Statement lines. <button class="ldg-btn" type="button" data-ldg-back-line>Back to the bank line</button></p>' : ''}
      <div class="ldg-dgrid">
        <label class="ldg-f">Category
          <select id="ldgf-category_slug" ${can('category_id') ? '' : 'disabled'}>
            <option value="">uncategorized</option>${catOpts}</select></label>
        <label class="ldg-f">Description
          <input id="ldgf-description" value="${esc(t.description || '')}" ${can('description') ? '' : 'disabled'}></label>
        <label class="ldg-f">Counterparty
          <input id="ldgf-counterparty" value="${esc(t.counterparty || '')}" ${can('counterparty') ? '' : 'disabled'}></label>
        <label class="ldg-f">Amount
          <input id="ldgf-amount" type="number" step="0.01" value="${hasAmount ? Number(t.amount) : ''}" ${can('amount') ? '' : 'disabled'}></label>
        <label class="ldg-f">Date
          <input id="ldgf-occurred_at" type="date" value="${(t.occurred_at || '').slice(0, 10)}" ${can('occurred_at') ? '' : 'disabled'}></label>
        <label class="ldg-f">Kind
          <select id="ldgf-kind" ${can('kind') ? '' : 'disabled'}>
            ${/* live transactions_kind_check is (income|expense|transfer);
                  mig 178's inventory_purchase widening never reached
                  production, so offering it returned a raw 23514 */
              ['expense', 'income', 'transfer'].map((k) =>
              `<option ${k === t.kind ? 'selected' : ''}>${k}</option>`).join('')}</select></label>
        <label class="ldg-f ldg-f-wide">Remarks
          <input id="ldgf-remarks" value="${esc(t.remarks || '')}" ${can('remarks') ? '' : 'disabled'}></label>
      </div>
      <p class="ldg-lockline">${
        t.status === 'voided' ? 'Voided — restore to edit.'
        : LDG_MATRIX.mirrorSources.includes((t.source || '').toLowerCase())
          ? `${esc(t.source)} mirror — facts locked, classification is yours.`
        : LDG_MATRIX.freeSources.includes((t.source || '').toLowerCase())
          ? 'Hand-logged — fully editable, every change audited.'
          : 'Bank truth — statement facts locked, classification is yours.'}</p>
      <section class="ldg-record-source" aria-label="Record source"><h3>Record source</h3><p>${esc(({manual:'Typed in',chat:'Saved through chat',photo_receipt:'Receipt photo',imported:'Imported record',bank_sync:'Bank feed',shopify:'Shopify record',meta_ads:'Meta ads',tiktok_ads:'TikTok ads',snapchat_ads:'Snap ads'})[t.source] || 'Source not named')} · ${esc(t.currency || 'Currency not recorded')}</p><details><summary>See source references</summary><p>Book entry: ${esc(t.id)}</p><p>Original reference: ${esc(t.source_reference || 'Not recorded')}</p><p>These identify the record. An edited description is not proof of the original bank wording.</p></details>${typeof t.receipt_url==='string'&&/^https?:\/\//i.test(t.receipt_url)?`<a href="${esc(t.receipt_url)}" target="_blank" rel="noopener noreferrer" referrerpolicy="no-referrer">Open the recorded receipt</a>`:'<p>No receipt link is recorded on this entry.</p>'}</section>
      <div class="ldg-dact">
        ${t.status === 'voided'
          ? (canVoid ? `<button class="ldg-btn" id="ldg-restore" type="button">Restore</button>` : '')
          : `<button class="ldg-btn ldg-btn-act" id="ldg-save" type="button">Save</button>`}
        ${t.status === 'pending_review'
          ? `<button class="ldg-btn" id="ldg-approve" type="button">Approve</button>
             <button class="ldg-btn" id="ldg-dismiss" type="button">Dismiss</button>` : ''}
        ${canVoid && t.status !== 'voided'
          ? `<button class="ldg-btn ldg-btn-danger" id="ldg-void" type="button">Void</button>` : ''}
      </div>
      <div data-ldg-unsaved hidden role="status"><p>Your edits are still here. Save them or discard this draft before closing.</p><button class="ldg-btn" type="button" data-ldg-discard>Discard draft and close</button></div>
      <div class="ldg-hist" id="ldg-hist"><div class="skel skel-line" style="width:60%;height:12px"></div></div>
    </div>`;
  document.body.appendChild(ov);
  const draft=()=>JSON.stringify([...ov.querySelectorAll('.ldg-dgrid input,.ldg-dgrid select')].map(el=>[el.id,el.value]));
  const originalDraft=draft();
  const unload=e=>{if(ov.isConnected&&draft()!==originalDraft){e.preventDefault();e.returnValue='';}};
  window.addEventListener('beforeunload',unload);
  const close = (discard=false) => {
    if(!ov.isConnected)return;
    if(discard!==true && draft()!==originalDraft){
      ov.querySelector('[data-ldg-unsaved]').hidden=false;
      ov.querySelector('[data-ldg-discard]').focus();return;
    }
    window.removeEventListener('beforeunload',unload);ov.remove();if(opener?.isConnected)opener.focus();
  };
  ov.querySelector('[data-ldg-discard]').addEventListener('click',()=>close(true));
  // Both leave through close(), so unsaved edits are asked about first.
  ov.querySelector('[data-ldg-show-in-list]')?.addEventListener('click',()=>{
    close();if(ov.isConnected)return;
    Object.assign(_ldgS,{period:'all_time',q:'',kind:null,source:null,focusId:t.id,from});
    for(const [id,v] of [['ldg-period','all_time'],['ldg-q',''],['ldg-source','']]){const el=document.getElementById(id);if(el)el.value=v;}
    document.querySelector('#ldg-queues .ldg-chip[data-q="all"]')?.click();
  });
  ov.querySelector('[data-ldg-back-line]')?.addEventListener('click',()=>{
    close();if(ov.isConnected||from.scope!==_viewReadScope())return;
    State.moneyReturnLine=from.line;if(from.file)State.moneyStatementFile={id:from.file,scope:from.scope};moneyOpenView('workbench');
    if(State.moneyTab!=='workbench')State.moneyReturnLine=null;
    if(State.moneyTab!=='workbench')State.moneyStatementFile=null;   // a refused return carries no file
  });
  ov.addEventListener('keydown',e=>{
    if(e.key==='Escape'){e.preventDefault();e.stopPropagation();close();return;}
    if(e.key!=='Tab')return;
    const items=[...ov.querySelectorAll('button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),a[href],[tabindex="0"]')].filter(el=>el.getClientRects().length);
    if(!items.length)return;
    if(e.shiftKey&&document.activeElement===items[0]){e.preventDefault();items.at(-1).focus();}
    else if(!e.shiftKey&&document.activeElement===items.at(-1)){e.preventDefault();items[0].focus();}
  });
  ov.querySelectorAll('input').forEach(input=>input.addEventListener('input',()=>input.setCustomValidity('')));
  ov.querySelector('.mreg-x').focus();
  ov.querySelector('.mreg-x').addEventListener('click', close);
  eonBackdropClose(ov, close);   // ghost-click law: never a bare backdrop

  // history — lazy, fail-soft
  const readHistory = () => api(`/api/accounting/transactions/${t.id}/history`).then((h) => {
    const host = ov.isConnected ? ov.querySelector('#ldg-hist') : null;
    if (!host) return;
    if(!h||h.ok===false||!Array.isArray(h.edits))throw new Error('History unavailable');
    const stamp=value=>{const d=value?new Date(value):null;return d&&!Number.isNaN(d.valueOf())?d.toISOString().slice(0,16).replace('T',' ')+' UTC':'Date not recorded';};
    const fields={amount:'Amount',amount_ngn:'Naira amount',currency:'Currency',category_id:'Category',description:'Description',counterparty:'Counterparty',occurred_at:'Transaction date',remarks:'Remarks',kind:'Type',status:'Status',receipt_url:'Receipt link',direction:'Money direction',account_id:'Account',location_id:'Location'};
    const actor=by=>by?`<small>Recorded by: ${esc(by)}</small>`:'';
    const decisions=[];
    if(h.reviewed&&typeof h.reviewed==='object')decisions.push(`<div class="ldg-hline"><strong>${h.reviewed.action==='approve'?'Approval recorded':h.reviewed.action==='reject'?'Dismissal recorded':'Review decision recorded'}</strong><span>${esc(stamp(h.reviewed.at))}</span>${actor(h.reviewed.by)}</div>`);
    if(h.voided&&typeof h.voided==='object')decisions.push(`<div class="ldg-hline"><strong>Past void record</strong><span>${esc(stamp(h.voided.at))} · ${esc(h.voided.reason || 'Reason not returned')}</span>${actor(h.voided.by)}<small>This is a historical action, not a claim about the current status.</small></div>`);
    const lines = h.edits.map(e2 => `<div class="ldg-hline"><span class="mono-num">${esc(stamp(e2.created_at))}</span><span>${esc(fields[e2.field] || 'Saved field')}<small>${esc(e2.old_value ?? 'Not recorded')} → ${esc(e2.new_value ?? 'Not recorded')}${e2.reason ? ` · ${esc(e2.reason)}` : ''}</small></span>${actor(e2.edited_by)}</div>`).join('');
    host.innerHTML = `<div class="ldg-hhead">Recorded history</div><p>Entry created: ${esc(stamp(h.created_at))}</p>${actor(h.created_by)}${decisions.join('')}` + (lines || '<div class="ldg-hline ldg-hnone">No field-edit records were returned.</div>') + `<p class="ldg-hnone">${h.edits.length} field-edit records returned${h.edits.length>=200?' · the read stops at200; older edits may exist':''}. Review and void records are shown separately when available.</p>`;
  }).catch(() => {
    const host = ov.isConnected ? ov.querySelector('#ldg-hist') : null;
    if (host) {host.innerHTML='<p role="status">Edit history did not load. This is not an empty history.</p><button type="button" class="ldg-btn">Try the history read again</button>';host.querySelector?.('button')?.addEventListener('click',readHistory);}
  });
  readHistory();

  const refresh = () => { close(true); _ldgFetch(); _ldgSummary(); };
  const draftBlocksAction = () => {
    if(draft()===originalDraft)return false;
    const note=ov.querySelector('[data-ldg-unsaved]');
    note.querySelector('p').textContent='Save your edits before changing this record’s status, or discard the draft and open it again.';
    note.hidden=false;ov.querySelector('#ldg-save')?.focus();return true;
  };
  document.getElementById('ldg-save')?.addEventListener('click', async () => {
    const changes = {};
    const read = (f, cast) => {
      const el = ov.querySelector(`#ldgf-${f}`);
      if (!el || el.disabled) return;
      const v = cast ? cast(el.value) : el.value;
      changes[f] = v;
    };
    read('category_slug');
    read('description'); read('counterparty'); read('remarks');
    read('kind');
    if (can('amount')) {
      const amountInput=ov.querySelector('#ldgf-amount');
      const value=amountInput?.value.trim();
      if(value==='') {
        if(hasAmount){amountInput.setCustomValidity('Enter the amount from the record. Blank is not zero.');amountInput.reportValidity();return;}
      } else if(!Number.isFinite(Number(value))) {
        amountInput?.setCustomValidity('Enter a valid amount.');amountInput?.reportValidity();return;
      } else changes.amount=Number(value);
    }
    if (can('occurred_at')) read('occurred_at');
    // only send real diffs — the server audits every accepted field
    if (changes.category_slug !== undefined) {
      const curSlug = _ldgS.cats?.find((c) => c.id === t.category_id)?.slug || '';
      if (changes.category_slug === curSlug) delete changes.category_slug;
      else if (changes.category_slug === '' && !t.category_id) delete changes.category_slug;
    }
    ['description', 'counterparty', 'remarks', 'kind'].forEach((f) => {
      if (changes[f] !== undefined && changes[f] === (t[f] || '')) delete changes[f];
    });
    if (changes.amount !== undefined && hasAmount && Number(changes.amount) === Number(t.amount)) delete changes.amount;
    if (changes.occurred_at !== undefined && changes.occurred_at === (t.occurred_at || '').slice(0, 10)) delete changes.occurred_at;
    if (!Object.keys(changes).length) { close(true); return; }
    try {
      const r = await api(`/api/accounting/transactions/${t.id}`, {
        method: 'PATCH', body: JSON.stringify({ changes }) });
      if (r?.ok !== true) { showToast(r?.error === 'fields_locked_for_source' ? `Locked for ${t.source}: ${(r.refused || []).join(', ')}` : (r?.error || 'The save was not confirmed. Your draft is still here.')); return; }
      showToast(r.rule_learned ? 'Saved — rule learned, future twins auto-file' : 'Saved · audited');
      if (r.rule_learned) _wksRuleBorn();   // the sheet's twins get chips too
      refresh();
    } catch (e) { showToast('Edit failed — try again'); }
  });
  document.getElementById('ldg-approve')?.addEventListener('click', async () => {
    if(draftBlocksAction())return;
    const button=ov.querySelector('#ldg-approve');if(button?.disabled)return;if(button)button.disabled=true;
    try {
      const result=await api(`/api/accounting/transactions/${t.id}/review`, {method:'POST',body:JSON.stringify({action:'approve'})});
      if(result?.ok!==true||result.status!=='recorded'){showToast('Approval was not confirmed. Read the latest record before trying again.');return;}
      showToast('Approved');refresh();
    } catch {showToast('Approval was not confirmed. Read the latest record before trying again.');}
    finally{if(button)button.disabled=false;}
  });
  document.getElementById('ldg-dismiss')?.addEventListener('click', async () => {
    if(draftBlocksAction())return;
    const button=ov.querySelector('#ldg-dismiss');if(button?.disabled)return;if(button)button.disabled=true;
    try {
      const result=await api(`/api/accounting/transactions/${t.id}/review`, {method:'POST',body:JSON.stringify({action:'reject'})});
      if(result?.ok!==true||result.status!=='voided'){showToast('Dismissal was not confirmed. Read the latest record before trying again.');return;}
      showToast('Dismissed');refresh();
    } catch {showToast('Dismissal was not confirmed. Read the latest record before trying again.');}
    finally{if(button)button.disabled=false;}
  });
  document.getElementById('ldg-void')?.addEventListener('click', async () => {
    if(draftBlocksAction())return;
    const reason = prompt('Void reason (required — lands in the audit trail):');
    if (!reason || !reason.trim()) return;
    try {
      const r = await api(`/api/accounting/transactions/${t.id}/void`, {
        method: 'POST', body: JSON.stringify({ reason: reason.trim() }) });
      if (r?.ok !== true) { showToast(r?.error || 'Void was not confirmed'); return; }
      showToast('Voided — out of the books, restorable');
      refresh();
    } catch (e) { showToast('Void failed'); }
  });
  document.getElementById('ldg-restore')?.addEventListener('click', async () => {
    try { await api(`/api/accounting/transactions/${t.id}/restore`, { method: 'POST', body: '{}' }); showToast('Restored'); refresh(); }
    catch (e) { showToast('Restore failed'); }
  });
}

// ── rules drawer + the sweep ──
async function _ldgRulesOpen() {
  const ov = document.createElement('div');
  ov.className = 'mreg-ov';
  ov.innerHTML = `
    <div class="mreg-sheet mreg-detail ldg-drawer mw-book-drawer" role="dialog" aria-label="Rules">
      <div class="mreg-head"><span>Rules — explain once</span>
        <button class="mreg-x" type="button" aria-label="Close">&times;</button></div>
      <p class="ldg-lockline">Every categorize teaches one. Operator rules (priority 50)
        beat auto-learned (100). The sweep runs rules first, then the model.</p>
      <div class="ldg-dact">
        <button class="ldg-btn ldg-btn-act" id="ldg-sweep" type="button">Run the sweep</button>
      </div>
      <div id="ldg-rules-list"><div class="skel skel-line" style="width:100%;height:40px"></div></div>
    </div>`;
  document.body.appendChild(ov);
  const close = () => ov.remove();
  ov.querySelector('.mreg-x').addEventListener('click', close);
  eonBackdropClose(ov, close);   // ghost-click law: never a bare backdrop
  const paint = async () => {
    try {
      const r = await api('/api/accounting/categorization-rules');
      const host = document.getElementById('ldg-rules-list');
      if (!host) return;
      host.innerHTML = (r.rules || []).map((ru) => `
        <div class="ldg-rule" data-rule="${esc(ru.id)}">
          <span class="ldg-rule-m">${esc(ru.match_type.replace('_', ' '))} · <b>${esc(ru.match_value)}</b></span>
          <span class="ldg-rule-c">→ ${esc(ru.category_name || ru.category_slug || '?')}</span>
          <span class="mono-num ldg-rule-h">${ru.hits_count || 0} hits${ru.auto_learned ? ' · auto' : ''}</span>
          <button class="ldg-btn ldg-btn-danger" data-rule-del="${esc(ru.id)}" type="button">Retire</button>
        </div>`).join('') || `<div class="ldg-empty">No rules yet — categorize a row and one is born.</div>`;
      host.querySelectorAll('[data-rule-del]').forEach((b) => {
        b.addEventListener('click', async () => {
          if (!confirm('Retire this rule? Past categorizations stand.')) return;
          // The server's reason, like every other bench door — a
          // refused rule write ("match_value too short …") must say
          // WHY, not just fail (adversarial pass, 2026-08-10).
          try {
            const r = await api(`/api/accounting/categorization-rules/${b.dataset.ruleDel}`, { method: 'DELETE' });
            if (r && r.ok === false) { showToast(r.error || 'Retire failed'); return; }
            paint(); _ldgSummary();
          } catch (e) { showToast(_ldgErr(e, 'Retire failed')); }
        });
      });
    } catch (e) {
      const host = document.getElementById('ldg-rules-list');
      if (host) host.innerHTML = `<div class="ldg-empty">Couldn't load rules.</div>`;
    }
  };
  paint();
  document.getElementById('ldg-sweep')?.addEventListener('click', async () => {
    const btn = document.getElementById('ldg-sweep');
    btn.disabled = true; btn.textContent = 'Sweeping…';
    try {
      const r = await api('/api/accounting/categorize-pending', {
        method: 'POST', body: JSON.stringify({ limit: 200, use_llm: true }) });
      showToast(`Sweep: ${r.scanned || 0} scanned · ${r.by_rule || 0} by rule · ${r.by_llm || 0} by model · ${r.still_uncategorized || 0} left`);
      paint(); _ldgFetch(); _ldgSummary();
    } catch (e) { showToast('Sweep failed'); }
    btn.disabled = false; btn.textContent = 'Run the sweep';
  });
}

// ── bulk ──
async function _ldgBulkCategorize() {
  const cats = await _ldgCats();
  const slug = prompt(`Category slug for ${_ldgS.sel.size} rows:\n${cats.slice(0, 20).map((c) => c.slug).join(' · ')}`);
  if (!slug) return;
  if (!cats.find((c) => c.slug === slug.trim())) { showToast(`No category '${slug.trim()}'`); return; }
  let ok = 0;
  for (const id of _ldgS.sel) {
    try {
      const r = await api(`/api/accounting/transactions/${id}`, {
        method: 'PATCH', body: JSON.stringify({ changes: { category_slug: slug.trim() } }) });
      if (r.ok !== false) ok += 1;
    } catch (e) { /* keep going — the toast reports the honest count */ }
  }
  showToast(`Categorized ${ok} of ${_ldgS.sel.size} · rules learning`);
  _wksRuleBorn();          // bulk corrections learn rules like any other
  _ldgFetch();
}

async function _ldgBulkApprove() {
  const pending = _ldgS.rows.filter((r) => _ldgS.sel.has(r.id) && r.status === 'pending_review');
  let ok = 0;
  for (const row of pending) {
    try {
      const r = await api(`/api/accounting/transactions/${row.id}/review`, {
        method: 'POST', body: JSON.stringify({ action: 'approve' }) });
      if (r.ok !== false) ok += 1;
    } catch (e) { /* honest count below */ }
  }
  showToast(`Approved ${ok} of ${pending.length}`);
  _ldgFetch(); _ldgSummary();
}

function _ldgNativeAmount(value, currency) {
  if (value == null || typeof value === 'boolean' || typeof value === 'string' && !value.trim() || !Number.isFinite(Number(value))) return 'Amount unavailable';
  const amount = Math.abs(Number(value)).toLocaleString('en-NG', {maximumFractionDigits:2});
  return /^[A-Z]{3}$/.test(currency || '') ? `${currency === 'NGN' ? '₦' : currency + ' '}${amount}` : `${amount} · currency unavailable`;
}

function _ldgPaintBank(lines, total, hidden, hiddenTotals, hiddenUnknown = 0) {
  const host = document.getElementById('ldg-rows');
  if (!host) return;
  host.innerHTML = lines.length ? lines.map((l) => `
    <div class="ldg-row ldg-row-bank" data-bank-id="${esc(l.id)}" tabindex="0"
         aria-label="Open bank line ${esc(l.narration || l.counterparty || '')}">
      <span class="ldg-check"></span>
      <span class="ldg-date mono-num">${esc(String(l.occurred_at || '').slice(0, 10))}</span>
      <span class="ldg-desc">${esc(l.narration || l.counterparty || '—')}
        <small>${esc(l.counterparty || '')}${l.counterparty ? ' · ' : ''}bank line${l.flow ? ' · ' + esc(l.flow) : ''}</small></span>
      <span class="ldg-cat ldg-cat-none">unexplained</span>
      <span class="ldg-amt mono-num ${l.direction === 'in' ? 'ldg-in' : ''}">${l.direction === 'in' ? '+' : '−'}${esc(_ldgNativeAmount(l.amount,l.currency))}</span>
      <span class="ldg-stat" style="color:var(--whale, #B58CFF);opacity:1">explain</span>
    </div>`).join('')
    : `<div class="ldg-empty">No unexplained bank lines in the 60-day window.</div>`;
  const count = document.getElementById('ldg-count');
  if (count) {
    const bits = [`${lines.length} of ${total}`];
    // The list is ranked biggest-money-first, so what the clip hides is
    // the SMALL money — but "180 hidden" alone never said whether that
    // was N50k or N47m. It was N47.5m the day this line was written.
    // A hidden count without its naira is the number that misleads.
    if (hidden > 0) {
      const amounts = hiddenTotals && typeof hiddenTotals === 'object'
        ? Object.entries(hiddenTotals).map(([cur, value]) => _ldgNativeAmount(value,cur)) : [];
      if (hiddenUnknown || !hiddenTotals) amounts.push(`${hiddenUnknown || hidden} amount(s) or currencies unavailable`);
      bits.push(`${hidden} more hidden · ${amounts.join(' · ') || 'Amounts unavailable'}`);
    }
    // Lines on accounts with no books mapping can never be listed
    // here at all — an invisible queue is the worked-queue law broken.
    const unmapped = Math.max(0, (_ldgS.bankDigestTotal || 0) - total);
    if (unmapped > 0) bits.push(`${unmapped} on unmapped accounts`);
    count.textContent = bits.join(' · ');
  }
  const more = document.getElementById('ldg-more');
  if (more) more.hidden = true;
  const byId = Object.fromEntries(lines.map((l) => [l.id, l]));
  host.querySelectorAll('.ldg-row-bank').forEach((el) => {
    const open = () => _ldgBankOpen(byId[el.dataset.bankId]);
    el.addEventListener('click', open);
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); open(); }
    });
  });
}

function _ldgBankOpen(line, { onChange, onClose, timeZone = 'Africa/Lagos' } = {}) {
  if (!line) return;
  // Review keeps the original currency. The existing booking and matching
  // path uses naira amounts, so a foreign amount must never enter it at 1:1.
  const currency = line.currency;
  const supportsBooks = currency === 'NGN';
  const amount = _ldgNativeAmount(line.amount,currency);
  let date='Date unavailable';
  try { date=new Intl.DateTimeFormat('en-GB',{day:'numeric',month:'short',year:'numeric',timeZone}).format(new Date(line.occurred_at)); } catch {}
  const previousFocus = document.activeElement;
  const ov = document.createElement(onChange ? 'dialog' : 'div');
  ov.className = onChange ? 'mreg-ov mr-accounting' : 'mreg-ov';
  ov.innerHTML = `
    <div class="mreg-sheet mreg-detail ldg-drawer mw-book-drawer" role="dialog" aria-label="Bank line" ${onChange ? 'aria-modal="true"' : ''}>
      <div class="mreg-head"><span>${esc(line.narration || line.counterparty || 'Bank line')}</span>
        <button class="mreg-x" type="button" aria-label="Close">&times;</button></div>
      <p class="ldg-lockline">${esc(date)} ·
        ${line.direction === 'in' ? '+' : '−'}${esc(amount)} ·
        statement line — choose an accounting action.</p>
      <div class="ldg-dact">
        <button class="ldg-btn ldg-btn-act" id="ldgb-explain" type="button" ${supportsBooks ? '' : 'disabled'}>Ask EON to explain</button>
        <button class="ldg-btn" id="ldgb-bridge" type="button" ${supportsBooks ? '' : 'disabled'}>Book it</button>
        ${_ldgCanManage()
          ? `<button class="ldg-btn" id="ldgb-ignore" type="button">Ignore</button>`
          : ''}
      </div>
      ${onChange ? '<p id="ldgb-status" role="status" class="mr-action-status" hidden></p>' : ''}
      ${supportsBooks ? '' : '<p class="ldg-lockline">Booking and matching currently support naira lines. This amount stays in its original currency. You can still record a reason to ignore it.</p>'}
      ${onChange && _ldgCanManage() ? `<form class="mr-ignore-form" id="ldgb-ignore-form" hidden>
        <label for="ldgb-ignore-reason">Why should EON ignore this line?</label>
        <p id="ldgb-ignore-help">Your reason is saved with it. This does not delete the statement line.</p>
        <textarea id="ldgb-ignore-reason" aria-describedby="ldgb-ignore-help" required maxlength="1000"></textarea>
        <div class="ldg-dact"><button class="ldg-btn" type="submit">Save reason &amp; ignore</button><button class="ldg-btn" type="button" id="ldgb-ignore-cancel">Keep this line</button></div>
        <p id="ldgb-ignore-status" role="status"></p></form>` : ''}
      <div id="ldgb-out"></div>
    </div>`;
  document.body.appendChild(ov);
  const close = () => { ov.remove(); if (onClose) onClose(); else if (previousFocus?.isConnected) previousFocus.focus(); };
  ov.querySelector('.mreg-x').addEventListener('click', close);
  if (onChange) {
    ov.setAttribute('aria-label', 'Bank line');
    ov.querySelector('.ldg-drawer').removeAttribute('role');
    ov.querySelector('.ldg-drawer').removeAttribute('aria-modal');
    ov.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
    ov.showModal();
  }
  ov.querySelector('.mreg-x').focus();
  if (onChange) ov.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); }
    if (event.key !== 'Tab') return;
    const controls = [...ov.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),textarea:not([disabled])')].filter(el=>el.getClientRects().length);
    const first = controls[0], last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  });
  eonBackdropClose(ov, close);   // ghost-click law: never a bare backdrop
  const refresh = () => { close(); if (onChange) onChange(); else { _ldgFetch(); _ldgSummary(); } };
  // A native dialog sits above page toasts. Keep its refusals in its own layer.
  const report = message => {
    if (onChange) {
      if (!ov.isConnected) return;
      const status = ov.querySelector('#ldgb-status');
      status.textContent = message;
      status.hidden = !message;
      if (message) status.scrollIntoView({block:'nearest'});
    } else if (message) showToast(message);
  };
  const bridge = async (categorySlug) => {
    if (!supportsBooks) return;
    report('');
    try {
      const r = await api(`/api/finance/bank-rec/lines/${line.id}/bridge`, {
        method: 'POST',
        body: JSON.stringify(categorySlug ? { category_slug: categorySlug } : {}) });
      if (r && r.ok !== false) {
        showToast(categorySlug
          ? 'Booked. It is in the Ledger now.'
          : 'Booked. It is in the Ledger now. Give it a category there.');
        refresh();
      } else report('The booking was not confirmed. Check this line before trying again.');
    } catch (e) { report('The booking was not confirmed. Check this line before trying again.'); }
  };
  let ignoring=false;
  const ignore = async (reason) => {
    if (ignoring || !reason || !reason.trim()) return;
    ignoring=true;
    const form=ov.querySelector('#ldgb-ignore-form'), submit=form?.querySelector('button[type="submit"]');
    if (submit) submit.disabled=true;
    try {
      const r=await api(`/api/finance/bank-rec/lines/${line.id}/ignore`, {method:'POST',body:JSON.stringify({reason:reason.trim()})});
      if (r && r.ok !== false) {showToast('Ignored — your reason is saved');refresh();}
      else throw new Error('unconfirmed');
    } catch {
      const message='This change was not confirmed. Your reason is still here. Check the line before trying again.';
      if(form) form.querySelector('#ldgb-ignore-status').textContent=message;else showToast(message);
    } finally {ignoring=false;if(submit?.isConnected){submit.disabled=false;submit.focus();}}
  };
  const requestIgnore = () => {
    if (!onChange) return ignore(prompt('Why should EON ignore this line? Your reason is saved with it.'));
    const form=ov.querySelector('#ldgb-ignore-form');
    if(form){form.hidden=false;form.querySelector('textarea').focus();}
  };
  ov.querySelector('#ldgb-ignore-form')?.addEventListener('submit', event=>{
    event.preventDefault();ignore(ov.querySelector('#ldgb-ignore-reason').value);
  });
  ov.querySelector('#ldgb-ignore-cancel')?.addEventListener('click',()=>{
    ov.querySelector('#ldgb-ignore-form').hidden=true;ov.querySelector('#ldgb-ignore').focus();
  });
  document.getElementById('ldgb-bridge').addEventListener('click', () => bridge(null));
  document.getElementById('ldgb-ignore')?.addEventListener('click', requestIgnore);
  document.getElementById('ldgb-explain').addEventListener('click', async () => {
    if (!supportsBooks) return;
    const out = document.getElementById('ldgb-out');
    const btn = document.getElementById('ldgb-explain');
    report('');
    btn.disabled = true; btn.textContent = 'Reading the books…';
    try {
      const r = await api(`/api/finance/bank-rec/lines/${line.id}/explain`, { method: 'POST', body: '{}' });
      btn.disabled = false; btn.textContent = 'Ask EON to explain';
      if (!r || r.ok === false) { report('EON could not finish this reading. Your line is unchanged.'); return; }
      const ev = (r.evidence || []).map((e2) => `
        <div class="ldg-hline"><span class="ldg-evtag">${esc(e2.kind)}</span>
          <span>${esc(e2.narration || e2.description || (e2.order_number ? `order #${e2.order_number}` : ''))}${e2.category ? ` → ${esc(e2.category)}` : ''}${e2.explained_as ? ` (${esc(e2.explained_as)})` : ''}</span></div>`).join('');
      const p2 = r.proposal || {};
      out.innerHTML = `
        <div class="ldg-hist" style="border-color:rgba(181,140,255,.3)">
          <div class="ldg-hhead" style="color:var(--whale, #B58CFF)">EON’s reading · ${esc(({high:'strong evidence',medium:'fairly sure',low:'needs checking'})[p2.confidence]||'needs checking')}</div>
          ${ev || '<div class="ldg-hline ldg-hnone">No matching evidence in the books.</div>'}
          <p class="ldg-lockline" style="opacity:.9">${esc(p2.read || '')}</p>
          <div class="ldg-dact">
            ${p2.action === 'ignore'
              ? `<button class="ldg-btn ldg-btn-act" id="ldgb-accept-ignore" type="button">Accept — ignore it</button>`
              : p2.action === 'match' && p2.matched_tx_id
                ? `<button class="ldg-btn ldg-btn-act" id="ldgb-accept-match" type="button">Accept — already booked</button>`
                : p2.action === 'bridge'
                  ? `<button class="ldg-btn ldg-btn-act" id="ldgb-accept-bridge" type="button">Accept — book this line</button>`
                  : `<span class="ldg-lockline">No proposal — the evidence above is all EON is sure of.</span>`}
          </div>
        </div>`;
      // The model's category is no longer computed, billed and thrown
      // away — it rides the bridge (server-validated; an unknown slug
      // falls back rather than failing the tap).
      document.getElementById('ldgb-accept-bridge')?.addEventListener(
        'click', () => bridge(p2.category_slug || null));
      document.getElementById('ldgb-accept-ignore')?.addEventListener('click', requestIgnore);
      document.getElementById('ldgb-accept-match')?.addEventListener('click', async () => {
        // THIS USED TO POST /ignore, which recorded a real booked
        // line as NOT-A-BOOK-EVENT and discarded the twin id — and
        // _precedent reads match_kind, so it taught every future line
        // with that narration to be ignored. It now points at the twin
        // through /match, which refuses a direction mismatch server-side.
        report('');
        try {
          const r2 = await api(`/api/finance/bank-rec/lines/${line.id}/match`, {
            method: 'POST',
            body: JSON.stringify({ transaction_id: p2.matched_tx_id,
                                   note: 'already booked (EON read)' }) });
          if (r2 && r2.ok !== false) { showToast('Matched. This line now points at the entry already in the Ledger.'); refresh(); }
          else report('The match was not confirmed. Check this line before trying again.');
        } catch (e) { report('The match was not confirmed. Check this line before trying again.'); }
      });
    } catch (e) {
      btn.disabled = false; btn.textContent = 'Ask EON to explain';
      report('EON could not finish this reading. Your line is unchanged.');
    }
  });
}

// Book entries, optionally on one entry. A journal opens Close & books on its
// month (there is no journal-by-id reader). A refused leave changes nothing.
// Members without Book entries keep the older register overlay.
function moneyOpenBookEntry(d = null) {
  if (d?.kind === 'journal') { moneyOpenView('books', false, {booksPeriod: d.month || bksCurrentMonth()}); return; }
  if (!d && moneyResolveView('ledger').denied) { mtxRegOpen(); return; }
  if (d && (d.kind !== 'transaction' || typeof d.id !== 'string')) return;
  const before = {queue: _ldgS.queue, focusId: _ldgS.focusId, from: _ldgS.from};
  Object.assign(_ldgS, {queue: 'all', focusId: d?.id || null,
    from: typeof d?.lineId === 'string' ? {line: d.lineId, scope: _viewReadScope(), file: typeof d.file === 'string' ? d.file : null} : null});
  moneyOpenView('ledger');
  if (State.moneyTab !== 'ledger') Object.assign(_ldgS, before);
}

// Open the entry a statement line named: in the list if it is listed, else
// read by id and opened beside the list. The list and its count never change.
async function _ldgFocus(gen) {
  const id = _ldgS.focusId, from = _ldgS.from;
  _ldgS.focusId = _ldgS.from = null;
  const here = () => State.view === 'money' && State.moneyTab === 'ledger';   // left Book entries: open nothing
  if (!here()) return;
  if (_ldgS.rows.some((r) => r.id === id)) { _ldgOpen(id, null, from); return; }
  try {
    const r = await api(`/api/accounting/transactions/${encodeURIComponent(id)}`);
    if (gen !== _ldgS.gen || !here()) return;
    if (r?.ok !== true || r.transaction?.id !== id) throw new Error('unconfirmed');
    _ldgOpen(id, r.transaction, from);
  } catch (e) {
    if (gen !== _ldgS.gen || !here()) return;
    showToast(e?.status === 404 ? "That entry can't be found." : "The entry didn't load. The list is unchanged. Try opening it again.");
  }
}

function bindMoneyLedger() {
  bindMoneyTabBar();
  const fetch=append=>{writeMoneyView(window.history,moneyViewScope(State),'ledger',_ldgS);_ldgFetch(append);};
  if (typeof prcHydrateMoneyStrip === 'function') prcHydrateMoneyStrip();
  _ldgSummary();
  // A cold entry link (moneyReadEntry) opens its entry once, for the same
  // person and view, and leaves the list's own filters as they were.
  const named = State.moneyEntryRef; State.moneyEntryRef = null;
  if (named && named.scope === _viewReadScope()) Object.assign(_ldgS, {focusId: named.id, from: null});
  fetch();
  const qs = document.querySelectorAll('#ldg-queues .ldg-chip');
  const paint = () => {
    qs.forEach((c) => c.classList.toggle('ldg-chip-on',
      (c.dataset.q === _ldgS.queue) || (c.dataset.q === 'all' && _ldgS.queue === 'all')));
    document.querySelectorAll('#ldg-kinds .ldg-chip').forEach((c) =>
      c.classList.toggle('ldg-chip-on', c.dataset.k === _ldgS.kind));
  };
  qs.forEach((c) => c.addEventListener('click', () => {
    _ldgS.queue = c.dataset.q; paint(); fetch();
  }));
  document.querySelectorAll('#ldg-kinds .ldg-chip').forEach((c) =>
    c.addEventListener('click', () => {
      _ldgS.kind = _ldgS.kind === c.dataset.k ? null : c.dataset.k;
      paint(); fetch();
    }));
  paint();
  document.getElementById('ldg-source')?.addEventListener('change', (e) => {
    _ldgS.source = e.target.value || null; fetch();
  });
  document.getElementById('ldg-period')?.addEventListener('change', (e) => {
    _ldgS.period = e.target.value; fetch();
  });
  let _qT;
  document.getElementById('ldg-q')?.addEventListener('input', (e) => {
    clearTimeout(_qT);
    _ldgS.q = e.target.value.trim();
    writeMoneyView(window.history,moneyViewScope(State),'ledger',_ldgS);
    _qT = setTimeout(() => fetch(), 350);
  });
  document.getElementById('ldg-more')?.addEventListener('click', () => {
    // one append in flight — the offset must not advance for a click
    // the fetch will refuse (verify round 2: the silent page hole)
    if (_ldgS.appending) return;
    _ldgS.offset += 100; fetch(true);
  });
  document.getElementById('ldg-rules-btn')?.addEventListener('click', _ldgRulesOpen);
  document.getElementById('ldg-cols-btn')?.addEventListener('click', _wksColsMenu);
  document.getElementById('ldg-bulk-cat')?.addEventListener('click', _ldgBulkCategorize);
  document.getElementById('ldg-bulk-approve')?.addEventListener('click', _ldgBulkApprove);
  document.getElementById('ldg-bulk-clear')?.addEventListener('click', () => {
    _ldgS.sel.clear(); _ldgBulkPaint(); _ldgPaint();
  });
  document.querySelectorAll('#ldg-strip .ldg-tile').forEach((tile) => {
    tile.addEventListener('click', () => {
      const q = tile.dataset.ldgQueue;
      if (q === '_books') { _ldgS.queue = 'bank'; paint(); fetch(); return; }
      if (q === '_rules') { _ldgRulesOpen(); return; }
      if (q === '_sched') { State.view = 'tasks'; render(); return; }
      // the list must answer the number that was tapped
      _ldgS.queue = q;
      _ldgS.period = 'all_time';
      const sel = document.getElementById('ldg-period');
      if (sel) sel.value = 'all_time';
      paint(); fetch();
    });
  });
  // keyboard: j/k walk bank-queue rows, enter opens (row focus handles
  // enter itself). WKS-2 critical fix: NEVER swallow keystrokes typed
  // into the worksheet's inputs/selects — typing "jacket" into the
  // composer was losing its j and k.
  const host = document.getElementById('ldg-rows');
  host?.addEventListener('keydown', (e) => {
    if (e.key !== 'j' && e.key !== 'k') return;
    if (e.target.matches?.('input, select, textarea, [contenteditable]')) return;
    const rows = [...host.querySelectorAll('.ldg-row')];
    if (!rows.length) return;
    const cur = rows.indexOf(document.activeElement);
    const next = e.key === 'j' ? Math.min(cur + 1, rows.length - 1) : Math.max(cur - 1, 0);
    rows[next]?.focus(); e.preventDefault();
  });
}

// ══════════════════════════════════════════════════════════════════
// LEDGER-5 — Money → Projection (operator extension 2026-08-10).
// Deterministic walk from the tenant's own history: EWMA daily level ×
// the operator-vetted seasonality factor, trailing-30d burn, dated
// commitments on their months. Assumptions in plain words; drift vs
// the last belief; forecast-vs-actual for finished months. Lazy,
// fail-soft, generation-tokened like every Money tab.
// ══════════════════════════════════════════════════════════════════

// HORIZON-1 (2026-08-28): months beyond 12 switch the tab to the far
// glass — /api/finance/horizon (measured-and-damped momentum, margin-
// aware walk, widening fan, scenarios). 3/6/12 keep the LEDGER-5 near
// walk untouched. scenario ∈ data|bear|plan (plan is owner-only; the
// server withholds the ladder from other roles, so the chip simply
// never appears for them).
const _prjS = { gen: 0, months: 6, scenario: 'data' };

function renderMoneyProjection() {
  return `

    ${renderMoneyTabBar('projection')}
    ${(State.member?.base_role || State.member?.role) === 'owner' && !State.impersonating ? renderForward() + '<a class="ascent-entry mw-growth-link" href="/ascent?tab=capital"><span>PUT THE PLAN TO WORK</span><strong>Take the next decision into Ascent.</strong><span>Review the capital plan, growth work and its evidence. Opening Ascent does not approve this scenario’s spending. Open Ascent ↗</span></a>' : ''}
    <details class="mf-legacy" ${(State.member?.base_role || State.member?.role) === 'owner' && !State.impersonating ? '' : 'open'}><summary>Weekly cash, longer forecasts & past accuracy</summary>
    <div class="ldg-strip" id="prj-tiles">
      <div class="ldg-tile"><span class="ldg-tile-n mono-num" id="prj-t-cash">—</span>
        <span class="ldg-tile-l" id="prj-t-cash-l">cash at horizon</span>
        <span class="ldg-tile-m mono-num" id="prj-t-cash-m"></span></div>
      <div class="ldg-tile"><span class="ldg-tile-n mono-num" id="prj-t-low">—</span>
        <span class="ldg-tile-l">lowest month</span>
        <span class="ldg-tile-m mono-num" id="prj-t-low-m"></span></div>
      <div class="ldg-tile"><span class="ldg-tile-n mono-num" id="prj-t-rev">—</span>
        <span class="ldg-tile-l">next month revenue</span>
        <span class="ldg-tile-m mono-num" id="prj-t-rev-m"></span></div>
      <div class="ldg-tile"><span class="ldg-tile-n mono-num" id="prj-t-run">—</span>
        <span class="ldg-tile-l">months of runway</span>
        <span class="ldg-tile-m" id="prj-t-run-m"></span></div>
    </div>
    <!-- HORIZON-1: scenario chips — live only past 12 months. The
         active chip and the selected option render FROM STATE: _prjS
         is module-level and survives tab switches, so hard-coding
         "6 months"/Data here desynced the controls from the chart on
         re-entry (adversarial review 2026-08-28). -->
    <div class="date-chips hz-scen" id="prj-scenarios" hidden>
      ${['data', 'bear', 'plan'].map((s) => `
      <button class="chip ${_prjS.scenario === s ? 'chip-active' : ''}"
              data-hz-scen="${s}" type="button" ${s === 'plan' ? 'hidden' : ''}>${s[0].toUpperCase()}${s.slice(1)}</button>`).join('')}
      <span class="meta" id="prj-scen-note"></span>
    </div>
    <div class="data-card ldg-card prj-card" id="prj-near-cash-card">
      <div class="prj-head">Cash, month by month
        <select class="ldg-sel" id="prj-months" aria-label="Horizon">
          ${[['w13', '13 weeks'], ['3', '3 months'], ['6', '6 months'],
             ['12', '12 months'], ['24', '24 months'], ['60', '5 years']].map(([v, l]) => `
          <option value="${v}" ${String(_prjS.months) === v ? 'selected' : ''}>${l}</option>`).join('')}
        </select></div>
      <div id="prj-chart" class="prj-chart">
        <div class="skel skel-line" style="width:100%;height:120px"></div>
      </div>
    </div>
    <div class="data-card ldg-card prj-card" id="prj-near-rev-card">
      <div class="prj-head">Revenue by month — with the honest band</div>
      <div id="prj-rev" class="prj-rev"></div>
    </div>
    <!-- HORIZON-1: the far glass. Two cards, hidden on near ranges —
         cash to the horizon, then the revenue fan against the plan's
         stations. The fan IS the honesty: uncertainty drawn as a
         shape that opens with distance, never a footnote. -->
    <div class="data-card ldg-card prj-card" id="prj-far-cash-card" hidden>
      <div class="prj-head"><span id="prj-far-cash-title">Cash, to the horizon</span></div>
      <div id="prj-far-cash" class="prj-chart hz-chart"></div>
    </div>
    <div class="data-card ldg-card prj-card" id="prj-far-rev-card" hidden>
      <div class="prj-head" id="prj-far-rev-head">Revenue pace — the fan</div>
      <div id="prj-far-rev" class="prj-chart hz-chart hz-fanwrap"></div>
      <div id="prj-far-legend" class="hz-legend"></div>
    </div>
    <div class="data-card prj-note" id="prj-ladder" hidden></div>
    <div class="data-card prj-note" id="prj-drivers" hidden></div>
    <div class="data-card prj-note" id="prj-assume"></div>
    <div class="data-card prj-note" id="prj-runway"></div>
    <div class="data-card prj-note" id="prj-drift"></div>
    <div class="data-card prj-note" id="prj-acc"></div></details>`;
}

// ── HORIZON-1 — the far glass (24 months / 5 years) ────────────────
//
// One timeline, month grain to 24 then quarter grain to 60: x is
// PROPORTIONAL TO TIME (month offsets), never point index, so the
// quarter points don't compress three months into one step.

// Money engine lane 4 (2026-09-02): the scoreboard is SCORED, not
// listed. A list of months with percentages made the reader do the
// arithmetic; the three figures the reader actually wants — how often
// the engine was right, how far it usually misses, and which way it
// leans — sit above the rows. The rows stay as the receipt.
function _prjScoreboard(acc, headline) {
  const all = acc || [];
  const graded = all.filter((a) => a.error_pct != null);
  if (!all.length) {
    return `<div class="prj-head">${headline}</div><p class="prj-line">No finished projected months yet — the scoreboard fills as months close.</p>`;
  }
  const within = graded.filter((a) => Math.abs(a.error_pct) <= 10).length;
  const miss = graded.reduce((t, a) => t + Math.abs(a.error_pct), 0) / graded.length;
  const bias = graded.reduce((t, a) => t + a.error_pct, 0) / graded.length;
  const lean = Math.abs(bias) < 3 ? 'no lean' : (bias > 0 ? 'believes too much' : 'believes too little');
  const rows = all.map((a) => `<p class="prj-line mono-num">${a.month.slice(0, 7)}: believed ${_hzFmt(a.projected_revenue_ngn)} → actual ${a.actual_basis_known === false ? '— (incomplete ledger)' : _hzFmt(a.actual_revenue_ngn)}${a.error_pct != null ? ` (${a.error_pct > 0 ? '+' : ''}${a.error_pct}%)` : ''}</p>`).join('');
  if (!graded.length) return `<div class="prj-head">${headline}</div><p class="prj-line">No comparable completed months yet.</p>${rows}`;
  return `<div class="prj-head">${headline}</div>
    <div class="prj-score" role="group" aria-label="How the engine scored">
      <span class="prj-score-n mono-num">${within}/${graded.length}</span><span class="prj-score-l">months within 10%</span>
      <span class="prj-score-n mono-num">${miss.toFixed(0)}%</span><span class="prj-score-l">typical miss</span>
      <span class="prj-score-n mono-num">${bias > 0 ? '+' : ''}${bias.toFixed(0)}%</span><span class="prj-score-l">${lean}</span>
    </div>${rows}`;
}

function _hzFmt(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  if (a >= 1e9) return `₦${(n / 1e9).toFixed(2)}B`;
  return `₦${(n / 1e6).toFixed(1)}M`;
}

function _hzPoints(p) {
  // Per-point monthly PACE series: months carry their revenue; a
  // quarter carries revenue/3 so the whole line reads in ₦/month.
  const pts = [];
  (p.months || []).forEach((m, i) => pts.push({
    off: i, key: m.month.slice(0, 7),
    rev: m.revenue_ngn,
    lo: m.revenue_low_ngn, hi: m.revenue_high_ngn,
    cash: m.closing_cash_ngn,
  }));
  const base = (p.months || []).length;
  // a quarter's closing cash is its LAST month's — plot and label the
  // period END, not its start (Opus vet 2026-08-29)
  (p.quarters || []).forEach((q, i) => pts.push({
    off: base + i * 3 + 2, key: q.quarter_end || q.quarter, quarter: true,
    rev: q.revenue_ngn / 3,
    lo: q.revenue_low_ngn == null ? null : q.revenue_low_ngn / 3,
    hi: q.revenue_high_ngn == null ? null : q.revenue_high_ngn / 3,
    cash: q.closing_cash_ngn,
  }));
  return pts;
}

function _hzYearTicks(pts) {
  // The quarter grain (Aug/Nov/Feb/May from a mid-year start) never
  // lands on January, so "label the Januaries" leaves the far years
  // unlabeled — mark each YEAR TRANSITION instead, whatever month the
  // grain puts there.
  const out = [];
  let year = '';
  pts.forEach((x) => {
    const y = x.key.slice(0, 4);
    if (y !== year) { if (year) out.push(x); year = y; }
  });
  return out;
}

function _hzChartCash(p, pts) {
  if (!pts.length) return _chartEmpty ? _chartEmpty('No months to project') : '';
  const w = 320, h = 130, padX = 8, padT = 12, padB = 20;
  const maxOff = pts[pts.length - 1].off || 1;
  const vals = [p.opening_cash_ngn].concat(pts.map((x) => x.cash));
  const lo = Math.min(0, ...vals), hi = Math.max(1, ...vals);
  const X = (off) => padX + ((off + 1) * (w - 2 * padX)) / (maxOff + 1);
  const Y = (v) => padT + (h - padT - padB) * (1 - (v - lo) / (hi - lo || 1));
  const path = _smoothLinePath(
    [[padX, Y(p.opening_cash_ngn)]].concat(pts.map((x) => [X(x.off), Y(x.cash)])));
  const zero = lo < 0 ? `<line x1="${padX}" x2="${w - padX}" y1="${Y(0)}" y2="${Y(0)}" stroke="var(--danger, #FF6B6B)" stroke-dasharray="2 3" stroke-width="1"/>` : '';
  const breach = p.breach_month
    ? `<text x="${w / 2}" y="${h - 6}" text-anchor="middle" class="prj-breach">cash crosses zero in ${p.breach_month.slice(0, 7)}</text>` : '';
  const labels = _hzYearTicks(pts).map((x) =>
    `<text x="${X(x.off)}" y="${h - 8}" text-anchor="middle" class="prj-xlab">${x.key.slice(0, 4)}</text>`).join('');
  return `<svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" role="img"
      aria-label="Projected cash to the horizon">
    ${zero}
    <path d="${path}" fill="none" stroke="var(--accent, #d4ff3a)"
          stroke-width="2" stroke-dasharray="5 4"/>
    <circle cx="${padX}" cy="${Y(p.opening_cash_ngn)}" r="3" fill="var(--accent, #d4ff3a)"/>
    ${labels}${breach}
  </svg>`;
}

function _hzChartFan(p, pts) {
  if (!pts.length) return _chartEmpty ? _chartEmpty('No months to project') : '';
  const w = 320, h = 150, padX = 8, padT = 12, padB = 22;
  const maxOff = pts[pts.length - 1].off || 1;
  // strictly inside the horizon — an over-edge station would draw
  // past the viewBox and clip silently
  const ladder = (p.plan_ladder || []).map((s) => ({
    off: _hzMonthOff(p, s.month), pace: s.revenue_monthly_ngn,
    label: s.label, accent: s.accent,
  })).filter((s) => s.off != null && s.off <= maxOff);
  const tops = pts.map((x) => x.hi == null ? x.rev : x.hi)
    .concat(ladder.map((s) => s.pace));
  const hi = Math.max(1, ...tops);
  const X = (off) => padX + (off * (w - 2 * padX)) / (maxOff || 1);
  const Y = (v) => padT + (h - padT - padB) * (1 - v / hi);
  // THE FAN — the band drawn as one closed shape, opening with distance.
  const banded = pts.filter((x) => x.lo != null && x.hi != null);
  let fan = '';
  if (banded.length > 1) {
    const up = banded.map((x) => `${X(x.off).toFixed(1)},${Y(x.hi).toFixed(1)}`);
    const dn = banded.slice().reverse()
      .map((x) => `${X(x.off).toFixed(1)},${Y(x.lo).toFixed(1)}`);
    fan = `<polygon points="${up.join(' ')} ${dn.join(' ')}"
      fill="var(--accent, #d4ff3a)" opacity="0.10" stroke="none"/>`;
  }
  const line = _smoothLinePath(pts.map((x) => [X(x.off), Y(x.rev)]));
  // The plan's stations — the signed ladder, base rungs joined by a
  // quiet dashed line; accent rungs (stretch / mission) stand alone.
  const baseR = ladder.filter((s) => !s.accent);
  const planLine = baseR.length > 1
    ? `<path d="M ${baseR.map((s) => `${X(s.off).toFixed(1)} ${Y(s.pace).toFixed(1)}`).join(' L ')}"
        fill="none" stroke="var(--ink-mute, #8b8b85)" stroke-width="1" stroke-dasharray="2 3"/>` : '';
  const stations = ladder.map((s) => `
    <rect x="${(X(s.off) - 2.5).toFixed(1)}" y="${(Y(s.pace) - 2.5).toFixed(1)}" width="5" height="5"
      fill="${s.accent ? 'var(--warn, #ffb347)' : 'var(--ink-mute, #8b8b85)'}"/>
    <text x="${X(s.off).toFixed(1)}" y="${(Y(s.pace) - 6).toFixed(1)}" text-anchor="middle"
      class="hz-station">${esc(s.label.replace(/\s*·.*$/, ''))}</text>`).join('');
  const labels = _hzYearTicks(pts).map((x) =>
    `<text x="${X(x.off)}" y="${h - 8}" text-anchor="middle" class="prj-xlab">${x.key.slice(0, 4)}</text>`).join('');
  return `<svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" role="img"
      aria-label="Projected monthly revenue pace with uncertainty fan${ladder.length ? ' and plan stations' : ''}">
    <g class="hz-sweep">
      ${fan}
      <path d="${line}" fill="none" stroke="var(--accent, #d4ff3a)" stroke-width="2"/>
    </g>
    ${planLine}${stations}${labels}
  </svg>`;
}

function _hzMonthOff(p, monthIso) {
  const first = ((p.months || [])[0] || {}).month;
  if (!first || !monthIso) return null;
  const off = (parseInt(monthIso.slice(0, 4), 10) - parseInt(first.slice(0, 4), 10)) * 12
    + (parseInt(monthIso.slice(5, 7), 10) - parseInt(first.slice(5, 7), 10));
  return off < 0 ? null : off;
}

function _hzDriverRows(p) {
  // The card is headed "every driver names its source", so the chip is
  // the claim. It used to downgrade only on the literal substring
  // "seed" and stamp everything else a green MEASURED — including
  // every driver that is nothing of the kind: the bear scenario's
  // NAMED −10% cut (bear_named_cut), the momentum that scenario simply
  // forces to zero (denied_bear), a seasonality reading that hit the
  // 0.70–1.50 rail and is therefore the RAIL not the reading
  // (history_detrended_clamped), and the fallback tuple printed when
  // the seasonality leg failed outright (unavailable). That last pair
  // is not theoretical: CRUX's December ratios read 2.02 / 1.91 / 1.67
  // over 2023-2025, median 1.91, so the rail binds and the live
  // payload carries history_detrended_clamped today. Allow-list what
  // may claim "measured"; everything unrecognized falls to "stated",
  // which is the honest answer for a number somebody chose.
  const chip = (src) => {
    const s = src || '';
    const label = /^measured_|^history_detrended$/.test(s) ? 'measured'
      : /seed|^static_default$|^nta_/.test(s) ? 'seeded'
      : /plan/.test(s) ? 'plan' : 'stated';
    const why = label === 'measured' ? 'Read from your own numbers.'
      : label === 'plan' ? 'From the plan you signed, not from your numbers.'
      : label === 'seeded' ? 'A standing figure we start from until your books can measure it.'
        : 'A figure chosen for this view, or a reading that hit its limit — not measured.';
    // the amber dashed chip is the only correction that survives on a
    // phone (the raw name beside it is hidden under 720px), so the
    // plain-words reason rides the chip itself
    return `<span class="hz-src ${label === 'measured' ? '' : 'hz-src-seed'}"
      title="${esc(why)}">${esc(label)}</span>
      <small class="hz-srcname">${esc(s)}</small>`;
  };
  return (p.drivers || []).map((d) => {
    const v = d.value_ngn != null ? `${_hzFmt(d.value_ngn)}/day`
      : d.value_pct != null ? `${d.value_pct > 0 && d.key === 'growth' ? '+' : ''}${d.value_pct}%` : '—';
    return `<p class="prj-line hz-driver"><span>${esc(d.label)}</span>
      <b class="mono-num">${v}</b>${chip(d.source)}</p>`;
  }).join('');
}

function _hzLadderRows(p) {
  const rows = (p.gap_vs_plan || []).filter((g) => !g.accent);
  if (!rows.length) return '';
  return `<div class="prj-head">The climb — data vs the plan you signed</div>`
    + rows.map((g) => `<p class="prj-line hz-gap mono-num">
        <span>${esc(g.label)}</span>
        <span>plan ${_hzFmt(g.plan_annual_ngn)}/yr · data ${_hzFmt(g.data_annual_ngn)}/yr</span>
        <b class="${(g.data_covers_pct || 0) >= 100 ? 'hz-ok' : ''}">${g.data_covers_pct == null ? '—' : `${g.data_covers_pct}%`}</b>
      </p>`).join('')
    + `<p class="prj-line">Coverage is the data path's deseasonalized pace at each
       station against the ladder — the gap is the climb the data hasn't earned yet.
       Stretch and mission rungs are gated aspirations; they show as amber stations
       on the fan, never as the plan line.</p>`;
}

async function _hzFetch(gen) {
  const p = await api(`/api/finance/horizon?months=${_prjS.months}&scenario=${_prjS.scenario}`);
  if (gen !== _prjS.gen) return;
  const put = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
  const pts = _hzPoints(p);
  const last = pts[pts.length - 1] || {};
  const fx = ((p.plan_ladder || [])[0] || {}).fx_ngn_per_usd;
  // When the opening cash could not be read the walk starts from ₦0
  // and the server says so with opening_cash_known:false and by
  // withholding breach_month. This glass used to ignore both and print
  // the shape as a position — a naira figure, a dollar conversion of
  // it, and the flat all-clear "no breach in view" over a suppressed
  // call. The 13-week glass beside it already refuses to. A cash level
  // built on an unknown opening is not a smaller number, it is no
  // number, and "no breach" is itself a claim.
  const cashKnown = p.opening_cash_known !== false;
  put('prj-t-cash', cashKnown ? _hzFmt(last.cash || 0) : '—');
  put('prj-t-cash-l', cashKnown ? `cash in ${last.key || '—'}` : 'cash at the end');
  put('prj-t-cash-m', cashKnown
    ? (fx ? `$${((last.cash || 0) / fx / 1e6).toFixed(2)}M @₦${Math.round(fx).toLocaleString()}/$` : '')
    : 'opening cash unknown');
  const lowest = pts.reduce((a, x) => (x.cash < a.cash ? x : a), last);
  put('prj-t-low', cashKnown ? _hzFmt(lowest.cash || 0) : '—');
  put('prj-t-low-m', cashKnown ? (lowest.key || '') : 'opening cash unknown');
  const y1 = (p.months || []).slice(0, 12).reduce((s, m) => s + (m.revenue_ngn || 0), 0);
  put('prj-t-rev', _hzFmt(y1));
  const revL = document.querySelector('#prj-tiles .ldg-tile:nth-child(3) .ldg-tile-l');
  if (revL) revL.textContent = 'next 12 months revenue';
  put('prj-t-rev-m', fx ? `$${(y1 / fx / 1e6).toFixed(2)}M` : '');
  const lastGap = (p.gap_vs_plan || []).filter((g) => !g.accent).pop();
  const runL = document.querySelector('#prj-tiles .ldg-tile:nth-child(4) .ldg-tile-l');
  if (lastGap) {
    put('prj-t-run', lastGap.data_covers_pct == null ? '—' : `${lastGap.data_covers_pct}%`);
    if (runL) runL.textContent = `of plan at ${lastGap.month.slice(0, 7)}`;
    put('prj-t-run-m', p.breach_month ? `breach ${p.breach_month.slice(0, 7)}`
      : cashKnown ? 'no breach in view' : 'opening cash unknown — no call');
  } else {
    put('prj-t-run', p.breach_month ? p.breach_month.slice(0, 7)
      : cashKnown ? 'clear' : '—');
    if (runL) {
      runL.textContent = p.breach_month ? 'cash crosses zero'
        : cashKnown ? 'no zero-crossing in view'
          : 'opening cash unknown — no call either way';
    }
    put('prj-t-run-m', p.scenario);
  }
  // reconcile scenario state with the server's authority — build()
  // silently coerces an unavailable scenario to 'data', and a chip
  // left active on a coerced payload would lie (Opus vet 2026-08-29)
  if (p.scenario && p.scenario !== _prjS.scenario) {
    _prjS.scenario = p.scenario;
    document.querySelectorAll('#prj-scenarios [data-hz-scen]').forEach((b) =>
      b.classList.toggle('chip-active', b.dataset.hzScen === p.scenario));
  }
  const cashEl = document.getElementById('prj-far-cash');
  if (cashEl) cashEl.innerHTML = _hzChartCash(p, pts);
  const revEl = document.getElementById('prj-far-rev');
  if (revEl) revEl.innerHTML = _hzChartFan(p, pts);
  const head = document.getElementById('prj-far-rev-head');
  if (head) head.textContent = (p.plan_ladder || []).length
    ? 'Revenue pace vs the ladder — the fan' : 'Revenue pace — the fan';
  const legend = document.getElementById('prj-far-legend');
  const hasBand = pts.some((x) => x.lo != null && x.hi != null);
  if (legend) legend.innerHTML = `<span><i class="hz-k hz-k-line"></i>monthly pace (seasonal, as lived)</span>`
    + (hasBand ? `<span><i class="hz-k hz-k-fan"></i>the fan — where honesty widens</span>`
       : `<span>band withheld — too little history to state one</span>`)
    + ((p.plan_ladder || []).length ? `<span><i class="hz-k hz-k-plan"></i>the signed ladder</span>` : '');
  const lad = document.getElementById('prj-ladder');
  if (lad) { const html = _hzLadderRows(p); lad.innerHTML = html; lad.hidden = !html; }
  const drv = document.getElementById('prj-drivers');
  if (drv) {
    drv.innerHTML = `<div class="prj-head">The levers — every driver names its source</div>`
      + _hzDriverRows(p);
    drv.hidden = false;
  }
  const asm = document.getElementById('prj-assume');
  if (asm) asm.innerHTML = (p.warnings || [])
      .map((wn) => `<p class="prj-line is-warn">${esc(wn)}</p>`).join('')
    + `<div class="prj-head">How this is built</div>`
    + (p.assumptions || []).map((a) => `<p class="prj-line">${esc(a)}</p>`).join('');
  const rw = document.getElementById('prj-runway');
  if (rw) rw.innerHTML = '';
  const dr = document.getElementById('prj-drift');
  if (dr) {
    if (p.drift && (p.drift.months || []).length) {
      const worst = p.drift.months.reduce((a, m) =>
        (Math.abs(m.closing_cash_delta_ngn) > Math.abs(a.closing_cash_delta_ngn) ? m : a));
      dr.innerHTML = `<div class="prj-head">Since ${esc(p.drift.prior_as_of)}</div>
        <p class="prj-line">Biggest move: ${worst.month.slice(0, 7)} closing cash
        ${worst.closing_cash_delta_ngn >= 0 ? 'up' : 'down'}
        <span class="mono-num">${_hzFmt(Math.abs(worst.closing_cash_delta_ngn))}</span>
        — the model re-reads your data nightly.</p>`;
    } else { dr.innerHTML = `<div class="prj-head">Since yesterday</div><p class="prj-line">First long-range belief — drift shows from tomorrow.</p>`; }
  }
  const ac = document.getElementById('prj-acc');
  if (ac) ac.innerHTML = _prjScoreboard(p.accuracy, 'How past long-range beliefs scored');
  // scenario chips reflect availability (plan is owner-only, server-decided)
  const planChip = document.querySelector('[data-hz-scen="plan"]');
  if (planChip) planChip.hidden = !(p.scenarios_available || []).includes('plan');
  const note = document.getElementById('prj-scen-note');
  // "level −1σ" described a method the engine no longer uses. The σ cut
  // was pulled on purpose (horizon.py: a daily residual σ is weekday-
  // vs-weekend dispersion, not level uncertainty) and replaced by a
  // flat named cut of BEAR_LEVEL_CUT = 10%. The assumptions card below
  // said "a named 10%" on the SAME screen while this line said one
  // sigma — and one sigma of the last 91 days is 63% of the daily
  // take, six times the stress actually applied. The percentage here
  // is pinned to BEAR_LEVEL_CUT by test_worksheet.py; move one and the
  // build fails until the other moves with it.
  if (note) note.textContent = p.scenario === 'bear'
    ? 'momentum denied · daily take cut 10% (a stated stress) · costs +5pts'
    : p.scenario === 'plan' ? 'the signed ladder as the path' : '';
}

// ── HORIZON-1 Tier 1 — the 13-week Monday walk ─────────────────────
async function _wkFetch(gen) {
  const p = await api('/api/finance/weekly-cash?weeks=13');
  if (gen !== _prjS.gen) return;
  const put = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
  const wks = p.weeks || [];
  const last = wks[wks.length - 1] || {};
  put('prj-t-cash', p.opening_cash_known ? _hzFmt(last.closing_ngn || 0) : '—');
  put('prj-t-cash-l', 'cash at week 13');
  put('prj-t-cash-m', p.opening_cash_known ? '' : 'opening cash unknown');
  put('prj-t-low', p.low_point ? _hzFmt(p.low_point.closing_ngn) : '—');
  const lowL = document.querySelector('#prj-tiles .ldg-tile:nth-child(2) .ldg-tile-l');
  if (lowL) lowL.textContent = 'lowest week';
  put('prj-t-low-m', p.low_point ? `w/c ${p.low_point.week_start.slice(5)}` : '');
  const next4 = wks.slice(0, 4).reduce((s, w) => s + (w.receipts_ngn || 0), 0);
  put('prj-t-rev', _hzFmt(next4));
  const revL = document.querySelector('#prj-tiles .ldg-tile:nth-child(3) .ldg-tile-l');
  if (revL) revL.textContent = 'next 4 weeks receipts';
  put('prj-t-rev-m', p.payday_weights_source === 'measured_dom'
    ? 'payday-shaped, measured' : 'flat shape — thin history');
  const v = p.variance;
  put('prj-t-run', v && v.error_pct != null
    ? `${v.error_pct > 0 ? '+' : ''}${v.error_pct}%` : '—');
  const runL = document.querySelector('#prj-tiles .ldg-tile:nth-child(4) .ldg-tile-l');
  if (runL) runL.textContent = 'last week: believed vs actual';
  put('prj-t-run-m', !v ? 'first belief — grades next Monday'
    : v.within_5pct == null ? 'not gradeable — no actual receipts yet'
      : v.within_5pct ? 'within ±5%' : 'outside ±5%');
  // the weekly ledger
  const host = document.getElementById('prj-far-cash');
  if (host) {
    const rows = wks.map((w) => {
      const outs = [
        w.payroll_ngn > 0 ? `payroll ${_hzFmt(w.payroll_ngn)}` : '',
        w.debt_ngn > 0 ? `debt ${_hzFmt(w.debt_ngn)}` : '',
        w.tax_ngn > 0 ? `tax ${_hzFmt(w.tax_ngn)}` : '',
        w.commit_out_ngn > 0 ? `commit ${_hzFmt(w.commit_out_ngn)}${w.commit_unknown_ngn > 0 ? ` (${_hzFmt(w.commit_unknown_ngn)} may already be paid)` : ''}` : '',
        w.commit_in_ngn > 0 ? `in ${_hzFmt(w.commit_in_ngn)}` : '',
      ].filter(Boolean).join(' · ');
      const neg = w.closing_ngn < 0;
      // items render whenever they exist — a week whose only dated
      // event is an INFLOW previously lost its label line
      const detail = [outs, w.items?.length
        ? esc(w.items.slice(0, 4).join(', ')) : '']
        .filter(Boolean).join(' — ');
      return `<div class="hz-wkrow mono-num">
        <span class="hz-wkc">${w.week_start.slice(5)}</span>
        <span class="hz-wkc">+${_hzFmt(w.receipts_ngn)}</span>
        <span class="hz-wkc">−${_hzFmt(w.disbursements_ngn)}</span>
        <span class="hz-wkc ${w.net_ngn < 0 ? 'hz-neg' : ''}">${w.net_ngn >= 0 ? '+' : ''}${_hzFmt(w.net_ngn)}</span>
        <b class="hz-wkc ${neg ? 'hz-neg' : ''}">${_hzFmt(w.closing_ngn)}</b>
      </div>${detail ? `<div class="hz-wkitems">${detail}</div>` : ''}`;
    }).join('');
    host.innerHTML = `<div class="hz-wkrow hz-wkhead">
        <span class="hz-wkc">w/c</span><span class="hz-wkc">in</span>
        <span class="hz-wkc">out</span><span class="hz-wkc">net</span>
        <span class="hz-wkc">closing</span></div>${rows}`
      + (p.breach_week
        ? `<p class="prj-line is-warn">The walk crosses zero in the week of ${esc(p.breach_week)}.</p>` : '');
  }
  const asm = document.getElementById('prj-assume');
  if (asm) asm.innerHTML = (p.warnings || [])
      .map((wn) => `<p class="prj-line is-warn">${esc(wn)}</p>`).join('')
    + `<div class="prj-head">How this is built</div>`
    + (p.assumptions || []).map((a) => `<p class="prj-line">${esc(a)}</p>`).join('');
  const dr = document.getElementById('prj-drift');
  if (dr) {
    dr.innerHTML = v
      ? `<div class="prj-head">The Monday reconciliation</div>
         <p class="prj-line mono-num">w/c ${esc(v.week_start)}: believed ${_hzFmt(v.believed_receipts_ngn)}
         → actual ${_hzFmt(v.actual_receipts_ngn)}${v.error_pct != null ? ` (${v.error_pct > 0 ? '+' : ''}${v.error_pct}%)` : ''}
         ${v.within_5pct == null
           ? '— not gradeable: no actual receipts landed for that week.'
           : `— ${v.within_5pct ? 'within' : 'outside'} the ±5% the practice asks near weeks to hold.`}</p>`
      : `<div class="prj-head">The Monday reconciliation</div>
         <p class="prj-line">First weekly belief — the scoreboard starts next Monday.</p>`;
  }
  ['prj-runway', 'prj-acc'].forEach((id) => {
    const el = document.getElementById(id); if (el) el.innerHTML = '';
  });
  const legend = document.getElementById('prj-far-legend');
  if (legend) legend.innerHTML = '';
}

function _prjMode(view) {
  // view ∈ 'near' | 'far' | 'weekly' — the weekly walk borrows the
  // far cash card as its ledger host (retitled), fan card dark.
  // EVERY mode-scoped bit of chrome resets HERE, before any fetch —
  // tile labels and note cards previously reset only inside each
  // mode's success path, so an error (or a switch mid-flight) left
  // the previous mode's labels and prose on screen (Opus vet
  // 2026-08-29).
  const near = view === 'near', far = view === 'far', wk = view === 'weekly';
  const show = (id, on) => { const el = document.getElementById(id); if (el) el.hidden = !on; };
  show('prj-near-cash-card', near); show('prj-near-rev-card', near);
  show('prj-far-cash-card', far || wk); show('prj-far-rev-card', far);
  show('prj-scenarios', far);
  if (!far) { show('prj-ladder', false); show('prj-drivers', false); }
  const title = document.getElementById('prj-far-cash-title');
  if (title) title.textContent = wk
    ? 'The 13 weeks — the Monday walk' : 'Cash, to the horizon';
  // tile labels per mode (values arrive from the fetch)
  const labels = near
    ? [null, 'lowest month', 'next month revenue', 'months of runway']
    : far ? [null, 'lowest month', 'next 12 months revenue', 'vs plan']
      : [null, 'lowest week', 'next 4 weeks receipts',
         'last week: believed vs actual'];
  labels.forEach((t, i) => {
    if (!t) return;
    const el = document.querySelector(
      `#prj-tiles .ldg-tile:nth-child(${i + 1}) .ldg-tile-l`);
    if (el && !el.id) el.textContent = t;
  });
  // note cards never carry a previous mode's prose
  ['prj-assume', 'prj-runway', 'prj-drift', 'prj-acc'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.innerHTML = '';
  });
  // the select lives inside the near card's head — re-home it when
  // that card is hidden
  const sel = document.getElementById('prj-months');
  const farHead = document.querySelector('#prj-far-cash-card .prj-head');
  const nearHead = document.querySelector('#prj-near-cash-card .prj-head');
  if (sel && !near && farHead && sel.parentElement !== farHead) farHead.appendChild(sel);
  if (sel && near && nearHead && sel.parentElement !== nearHead) nearHead.appendChild(sel);
}

function _prjChartCash(p) {
  const ms = p.months || [];
  if (!ms.length) return _chartEmpty ? _chartEmpty('No months to project') : '';
  const pts = [{ v: p.opening_cash_ngn }].concat(ms.map((m) => ({ v: m.closing_cash_ngn })));
  const w = 320, h = 120, padX = 8, padT = 12, padB = 20;
  const lo = Math.min(0, ...pts.map((x) => x.v));
  const hi = Math.max(1, ...pts.map((x) => x.v));
  const X = (i) => padX + (i * (w - 2 * padX)) / (pts.length - 1);
  const Y = (v) => padT + (h - padT - padB) * (1 - (v - lo) / (hi - lo || 1));
  const path = _smoothLinePath(pts.map((x, i) => [X(i), Y(x.v)]));
  const zero = lo < 0 ? `<line x1="${padX}" x2="${w - padX}" y1="${Y(0)}" y2="${Y(0)}" stroke="var(--danger, #FF6B6B)" stroke-dasharray="2 3" stroke-width="1"/>` : '';
  const breach = p.breach_month
    ? `<text x="${w / 2}" y="${h - 6}" text-anchor="middle" class="prj-breach">cash crosses zero in ${p.breach_month.slice(0, 7)}</text>`
    : '';
  const labels = ms.map((m, i) => (i % 2 === (ms.length > 6 ? 1 : 0) && ms.length > 4) ? '' :
    `<text x="${X(i + 1)}" y="${h - 8}" text-anchor="middle" class="prj-xlab">${m.month.slice(5, 7)}</text>`).join('');
  return `<svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" role="img"
      aria-label="Projected cash by month">
    ${zero}
    <path d="${path}" fill="none" stroke="var(--accent, #C8FF4D)"
          stroke-width="2" stroke-dasharray="5 4"/>
    <circle cx="${X(0)}" cy="${Y(pts[0].v)}" r="3" fill="var(--accent, #C8FF4D)"/>
    ${labels}${breach}
  </svg>`;
}

function _prjRevRows(p) {
  const ms = p.months || [];
  const hi = Math.max(1, ...ms.map((m) => m.revenue_high_ngn || m.revenue_ngn));
  return ms.map((m) => {
    const w = Math.round(100 * (m.revenue_ngn / hi));
    const lo = Math.round(100 * ((m.revenue_low_ngn || m.revenue_ngn) / hi));
    const hi2 = Math.round(100 * ((m.revenue_high_ngn || m.revenue_ngn) / hi));
    const season = m.seasonality_factor && m.seasonality_factor !== 1
      ? ` <small>×${m.seasonality_factor}</small>` : '';
    const commit = (m.commitments_out_ngn || 0) > 0
      ? `<small class="prj-commit">−₦${(m.commitments_out_ngn / 1e6).toFixed(1)}M scheduled</small>` : '';
    return `<div class="prj-rrow">
      <span class="prj-rmon mono-num">${m.month.slice(0, 7)}</span>
      <span class="prj-rbar"><i style="width:${w}%"></i>
        <b style="left:${lo}%;width:${Math.max(1, hi2 - lo)}%"></b></span>
      <span class="prj-ramt mono-num">₦${(m.revenue_ngn / 1e6).toFixed(1)}M${season}</span>
      ${commit}</div>`;
  }).join('');
}

async function _prjFetch() {
  const gen = ++_prjS.gen;
  const weekly = _prjS.months === 'w13';
  const far = !weekly && _prjS.months > 12;
  _prjMode(weekly ? 'weekly' : far ? 'far' : 'near');
  try {
    if (weekly) return await _wkFetch(gen);
    if (far) return await _hzFetch(gen);
    const p = await api(`/api/finance/projection?months=${_prjS.months}`);
    if (gen !== _prjS.gen) return;
    const put = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
    // restore the near tiles' labels (the far glass repurposes them)
    const relabel = (n, t) => {
      const el = document.querySelector(`#prj-tiles .ldg-tile:nth-child(${n}) .ldg-tile-l`);
      if (el && !el.id) el.textContent = t;
    };
    relabel(2, 'lowest month'); relabel(3, 'next month revenue');
    relabel(4, 'months of runway');
    const last = (p.months || [])[p.months.length - 1] || {};
    put('prj-t-cash', p.opening_cash_known ? `₦${((last.closing_cash_ngn || 0) / 1e6).toFixed(1)}M` : '—');
    put('prj-t-cash-l', `cash in ${(last.month || '').slice(0, 7)}`);
    const lowest = (p.months || []).reduce((a, m) => (m.closing_cash_ngn < a.closing_cash_ngn ? m : a), last);
    put('prj-t-low', p.opening_cash_known ? `₦${((lowest.closing_cash_ngn || 0) / 1e6).toFixed(1)}M` : '—');
    put('prj-t-low-m', (lowest.month || '').slice(0, 7));
    const next = (p.months || [])[1] || (p.months || [])[0] || {};
    put('prj-t-rev', `₦${((next.revenue_ngn || 0) / 1e6).toFixed(1)}M`);
    // null low/high = the model declined to state a band (too little
    // history). Rendering `|| 0` printed "₦0.0–0.0M", which reads as a
    // measured zero rather than "unknown" — say unknown.
    put('prj-t-rev-m', (next.revenue_low_ngn == null || next.revenue_high_ngn == null)
      ? 'band unavailable'
      : `₦${(next.revenue_low_ngn / 1e6).toFixed(1)}–${(next.revenue_high_ngn / 1e6).toFixed(1)}M`);
    const rm = p.runway_strip?.months_of_runway;
    const runwayLabel = rm == null
      ? (p.runway_strip?.runway_status === 'profitable' ? 'No net burn' : '—')
      : Number(rm).toFixed(1);
    put('prj-t-run', runwayLabel);
    put('prj-t-run-m', p.runway_strip?.runway_status || '');
    const chart = document.getElementById('prj-chart');
    if (chart) chart.innerHTML = _prjChartCash(p);
    const rev = document.getElementById('prj-rev');
    if (rev) rev.innerHTML = _prjRevRows(p);
    const asm = document.getElementById('prj-assume');
    // A degraded leg used to be invisible: the page stated ₦0 opening
    // cash and a zero-crossing as fact when get_cash_position had simply
    // failed. Warnings lead, above the assumptions they undercut.
    if (asm) asm.innerHTML = (p.warnings || [])
        .map((wn) => `<p class="prj-line is-warn">${esc(wn)}</p>`).join('')
      + `<div class="prj-head">How this is built</div>`
      + (p.assumptions || []).map((a) => `<p class="prj-line">${esc(a)}</p>`).join('');
    const rw = document.getElementById('prj-runway');
    if (rw) rw.innerHTML = `<div class="prj-head">The other runway numbers</div>
      <p class="prj-line mono-num">months-of-runway: ${esc(runwayLabel)} · days-cash (fixed burn): ${p.runway_strip?.days_cash_fixed_burn ?? '—'}</p>
      <p class="prj-line">${esc(p.runway_strip?.note || '')}</p>`;
    const dr = document.getElementById('prj-drift');
    if (dr) {
      if (p.drift && (p.drift.months || []).length) {
        const worst = p.drift.months.reduce((a, m) =>
          (Math.abs(m.closing_cash_delta_ngn) > Math.abs(a.closing_cash_delta_ngn) ? m : a));
        dr.innerHTML = `<div class="prj-head">Since ${esc(p.drift.prior_as_of)}</div>
          <p class="prj-line">Biggest move: ${worst.month.slice(0, 7)} closing cash
          ${worst.closing_cash_delta_ngn >= 0 ? 'up' : 'down'}
          <span class="mono-num">₦${(Math.abs(worst.closing_cash_delta_ngn) / 1e6).toFixed(1)}M</span>
          — the model re-reads your data nightly.</p>`;
      } else { dr.innerHTML = `<div class="prj-head">Since yesterday</div><p class="prj-line">First belief — drift shows from tomorrow.</p>`; }
    }
    const ac = document.getElementById('prj-acc');
    if (ac) ac.innerHTML = _prjScoreboard(p.accuracy, 'How past beliefs scored');
  } catch (e) {
    if (gen !== _prjS.gen) return;
    // error ≠ empty, on whichever glass is showing — and on the far
    // glass BOTH cards say so, or the fan card sits blank under a
    // confident header while near-view prose goes stale below
    // (adversarial review 2026-08-28).
    const chart = document.getElementById(
      (far || weekly) ? 'prj-far-cash' : 'prj-chart');
    if (chart) {
      chart.innerHTML = `<div class="ldg-empty">Couldn't build the projection — <button class="ldg-btn" id="prj-retry" type="button">retry</button></div>`;
      document.getElementById('prj-retry')?.addEventListener('click', () => _prjFetch());
    }
    if (far || weekly) {
      const rev = document.getElementById('prj-far-rev');
      if (rev) rev.innerHTML = `<div class="ldg-empty">No horizon to draw.</div>`;
      const legend = document.getElementById('prj-far-legend');
      if (legend) legend.innerHTML = '';
      ['prj-ladder', 'prj-drivers'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) { el.innerHTML = ''; el.hidden = true; }
      });
      ['prj-assume', 'prj-runway', 'prj-drift', 'prj-acc'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.innerHTML = '';
      });
    }
  }
}

function bindMoneyProjection() {
  bindMoneyTabBar();
  bindForward(document.getElementById("money-forward"), api, {registerDraft:moneyRegisterDraft});
  if (typeof prcHydrateMoneyStrip === 'function') prcHydrateMoneyStrip();
  moneyRead('prj-months',_prjFetch);
  document.getElementById('prj-months')?.addEventListener('change', (e) => {
    _prjS.months = e.target.value === 'w13'
      ? 'w13' : (parseInt(e.target.value, 10) || 6);
    _prjFetch();
  });
  document.querySelectorAll('#prj-scenarios [data-hz-scen]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (_prjS.scenario === btn.dataset.hzScen) return;
      _prjS.scenario = btn.dataset.hzScen;
      document.querySelectorAll('#prj-scenarios [data-hz-scen]').forEach((b) =>
        b.classList.toggle('chip-active', b === btn));
      _prjFetch();
    });
  });
}

// ── Budgets: create / edit / delete (rollover cron carries them) ──
async function mtxBudgetOpen(name) {
  const cats = await _regCats();
  const ov = document.createElement('div');
  ov.className = 'mreg-ov';
  ov.innerHTML = `
    <div class="mreg-sheet mreg-detail" role="dialog" aria-label="Set budget">
      <div class="mreg-head"><span>${name ? 'Edit budget' : 'Set a budget'}</span>
        <button class="mreg-x" type="button" aria-label="Close">&times;</button></div>
      <div class="mreg-dbody">
        <label class="mreg-drow"><span>Category</span>
          <select class="mtx-input" id="mbud-cat" ${name ? 'disabled' : ''}>
            ${cats.map((c) => `<option value="${esc(c.slug)}" ${name && (c.name === name || c.slug === name) ? 'selected' : ''}>${esc(c.name)}</option>`).join('')}
          </select></label>
        <label class="mreg-drow"><span>Monthly cap (NGN)</span>
          <input class="mtx-input mono-num" id="mbud-amt" type="number" min="0" step="1000" placeholder="500000"></label>
        <div class="data-stat-sub">Budgets roll into next month automatically — edit or remove any time.</div>
      </div>
      <div class="mreg-acts">
        ${name ? '<button class="bks-tool-btn" type="button" id="mbud-del">Remove budget</button>' : ''}
        <button class="bks-tool-btn mreg-save" type="button" id="mbud-save">Save</button>
      </div>
    </div>`;
  const close = () => ov.remove();
  eonBackdropClose(ov, close);  // ghost-click law (2026-07-25)
  mtxEscClose(ov, close);
  ov.querySelector('.mreg-x').addEventListener('click', close);
  ov.querySelector('#mbud-save').addEventListener('click', async (e) => {
    const slug = ov.querySelector('#mbud-cat').value;
    const amt = parseFloat(ov.querySelector('#mbud-amt').value);
    if (!slug || !Number.isFinite(amt) || amt < 0) {
      showToast('Category and amount required'); return;
    }
    e.target.disabled = true;
    try {
      const r = await api('/api/accounting/budgets', {
        method: 'PUT', body: JSON.stringify({ category: slug, amount_ngn: amt }),
      });
      if (r && r.ok !== false) { showToast('Budget saved'); close(); refreshMoney(); }
      else { showToast(`Save failed: ${r?.error || 'unknown'}`); e.target.disabled = false; }
    } catch (err) { showToast(`Save failed: ${err?.message || err}`); e.target.disabled = false; }
  });
  ov.querySelector('#mbud-del')?.addEventListener('click', async (e) => {
    e.target.disabled = true;
    try {
      const r = await api(`/api/accounting/budgets/${encodeURIComponent(name)}`, { method: 'DELETE' });
      if (r && r.ok !== false) { showToast('Budget removed'); close(); refreshMoney(); }
      else { showToast(`Remove failed: ${r?.error || 'unknown'}`); e.target.disabled = false; }
    } catch (err) { showToast(`Remove failed: ${err?.message || err}`); e.target.disabled = false; }
  });
  document.body.appendChild(ov);
}

// ── HOUSE floor 8 / DEFECT 1 — money gets logged HERE ───────────────
//
// "+ log a line" used to be three .suggest tiles that typed "I spent "
// into chat; POST /api/accounting/expenses and POST /api/accounting/
// revenue had ZERO frontend callers. This sheet is the real door: it
// posts the row itself, with source='manual' so these entries are
// distinguishable from the chat and bank_sync lanes. Both endpoints
// need P.ACCOUNTING_LOG, which every member holds — so this control
// is deliberately outside the canFinance gate.
//
// M4: what the button will do, said before it is pressed. "Today" is the
// Lagos date accounting._future_dated refuses after (no tenant zone reaches
// the client). A later date files a commitment, which only base roles
// owner/admin/manager may create (routes_tasks._require_finance; base role,
// else the fallback member role). A Money out closes only on Tasks → Command
// "settled", which sends no account: it books to
// accounting.resolve_default_cash_account or not at all. Command lists only
// Money out (obligations._commitment_rows), so no screen closes a Money in.
function mnyLogLineIntent(day, kind, now = new Date()) {
  const today = new Intl.DateTimeFormat('en-CA', {timeZone:'Africa/Lagos',year:'numeric',month:'2-digit',day:'2-digit'}).format(now);
  const stamp = /^\d{4}-\d{2}-\d{2}$/.test(day || '') ? Date.parse(day + 'T00:00:00Z') : NaN;
  const valid = Number.isFinite(stamp) && new Date(stamp).toISOString().slice(0, 10) === day;
  const date = valid ? day : today;
  const when = new Intl.DateTimeFormat('en-GB', {day:'numeric',month:'long',year:'numeric',timeZone:'UTC'}).format(new Date(date + 'T00:00:00Z'));
  if (date <= today) return {action:'log', date, today, button:'Log it',
    note:`Logs this to the books on ${when}, once EON accepts it. A category EON can't match becomes "uncategorized" and goes to the review queue.`};
  if (!['owner','admin','manager'].includes(String(State.member?.base_role || State.member?.role || '').toLowerCase())) return {action:'denied', date, today, button:"Can't schedule",
    note:`${when} is after today. Only an owner, admin or manager can schedule a later date. Pick today or an earlier date to log it now.`};
  if (kind === 'income') return {action:'schedule', date, today, button:'Add to projection',
    note:`${when} is after today, so nothing is logged. This is money you expect, not income in the books. Projections count it from now on. Nothing in EON can close it yet, not even logging the money when it arrives. So once you log it here as Money in, projections count it twice. The category is not kept.`};
  return {action:'schedule', date, today, button:'Schedule task',
    note:`${when} is after today, so this pays nothing and is not in today's books. It schedules a task for ${when}. When it is paid, mark it settled on Tasks, under Command. EON then books it to the naira bank account with the largest balance on record. You can't choose the account here. If no naira bank account has a balance on record, nothing is booked. The category is not kept.`};
}

async function mnyLogLineOpen(trigger = document.activeElement) {
  const cats = await _regCats();
  const catOpts = (kind) => {
    const list = cats.filter((c) => !c.kind || c.kind === kind);
    return (list.length ? list : cats)
      .map((c) => `<option value="${esc(c.slug || c.name)}">${esc(c.name)}</option>`)
      .join('');
  };
  const iso = mnyLogLineIntent('', 'expense').today;
  const ov = document.createElement('div');
  ov.className = 'mreg-ov';
  ov.innerHTML = `
    <div class="mreg-sheet mreg-detail" role="dialog" aria-label="Log a transaction">
      <div class="mreg-head"><span>Log a transaction</span>
        <button class="mreg-x" type="button" aria-label="Close">&times;</button></div>
      <div class="mreg-dbody">
        <div class="date-chips" id="mlog-kind">
          <button class="chip chip-active" type="button" data-log-kind="expense">Money out</button>
          <button class="chip" type="button" data-log-kind="income">Money in</button>
        </div>
        <label class="mreg-drow"><span>Amount (NGN)</span>
          <input class="mtx-input mono-num" id="mlog-amt" type="number" min="0" step="100" placeholder="25000"></label>
        <label class="mreg-drow"><span>What was it</span>
          <input class="mtx-input" id="mlog-desc" type="text" maxlength="140" placeholder="DHL — factory samples"></label>
        <label class="mreg-drow"><span>Category</span>
          <select class="mtx-input" id="mlog-cat">${catOpts('expense')}</select></label>
        <label class="mreg-drow"><span>Date</span>
          <input class="mtx-input" id="mlog-date" type="date" value="${iso}"></label>
        <div class="data-stat-sub" id="mlog-note" aria-live="polite"></div>
      </div>
      <div class="mreg-acts">
        <button class="bks-tool-btn mreg-save" type="button" id="mlog-save" aria-describedby="mlog-note">Log it</button>
      </div>
    </div>`;
  let kind = 'expense', unconfirmed = false, sending = false, shown = null;
  const intent = () => mnyLogLineIntent(ov.querySelector('#mlog-date').value, kind);
  const sync = () => {
    const next = intent(), save = ov.querySelector('#mlog-save');
    shown = `${next.action}:${next.date}`;
    ov.querySelector('#mlog-note').textContent = next.note;
    save.textContent = next.button;
    save.disabled = sending || unconfirmed || next.action === 'denied';
  };
  const close = () => {
    ov.remove();
    if (trigger?.isConnected) trigger.focus({ preventScroll: true });
  };
  eonBackdropClose(ov, close);   // ghost-click law (2026-07-25)
  mtxEscClose(ov, close);
  ov.querySelector('.mreg-x').addEventListener('click', close);
  ov.querySelectorAll('#mlog-kind .chip').forEach((chip) => {
    chip.addEventListener('click', () => {
      kind = chip.dataset.logKind;
      ov.querySelectorAll('#mlog-kind .chip').forEach((c) =>
        c.classList.toggle('chip-active', c === chip));
      ov.querySelector('#mlog-cat').innerHTML = catOpts(kind);
      sync();
    });
  });
  ov.querySelector('#mlog-date').addEventListener('input', sync);
  ov.querySelector('#mlog-date').addEventListener('change', sync);
  sync();
  // A timeout, a dropped connection or a server/gateway error (5xx) has no
  // clear answer: the row may exist. Say so and keep the button off.
  // One send at a time: a date or direction change while it is pending
  // never turns the button back on; only a refusal frees it.
  const settle = () => { sending = false; sync(); };
  const failed = (err, verb, place) => {
    if (err?.status && err.status < 500 && !err.timeout) { showToast(`${verb} failed: ${err.message || err}`); settle(); return; }
    unconfirmed = true;
    showToast(`Not confirmed: EON gave no clear answer. It may already be ${verb === 'Log' ? 'logged' : 'scheduled'}. Check ${place} before trying again.`);
  };
  ov.querySelector('#mlog-save').addEventListener('click', async (e) => {
    const amt = parseFloat(ov.querySelector('#mlog-amt').value);
    const desc = (ov.querySelector('#mlog-desc').value || '').trim();
    if (!Number.isFinite(amt) || amt <= 0 || !desc) {
      showToast('Amount and a short description are required'); return;
    }
    const plan = intent();
    if (plan.action === 'denied' || unconfirmed || sending) return;
    // Lagos midnight can pass while the form is open: never act on words
    // the form is no longer showing.
    if (`${plan.action}:${plan.date}` !== shown) { sync(); showToast('It is now a new day in Lagos, so this does something different. Read the form again, then press.'); return; }
    const day = plan.date, iso = plan.today;
    sending = true; e.target.disabled = true;
    // LEDGER-4: a FUTURE date files a commitment, never a future-dated
    // transaction — four cash readers carry no upper occurred_at bound,
    // so a future row would silently drain TODAY'S cash. The commitment
    // becomes a task on its due date and feeds the projection from now.
    if (day > iso) {
      try {
        const r = await api('/api/tasks/commitments', {
          method: 'POST',
          body: JSON.stringify({
            direction: kind === 'income' ? 'in' : 'out',
            kind: 'other',
            label: desc,
            amount_ngn: amt,
            due_date: day,
            notes: 'scheduled from the Ledger bench',
          }),
        });
        if (r && r.ok !== false) {
          showToast(`Scheduled for ${day} — a task lands that morning; it counts in projections, not today's books`);
          close();
          refreshMoney();
        } else {
          showToast(`Schedule failed: ${r?.error || 'unknown'}`);
          settle();
        }
      } catch (err) {
        failed(err, 'Schedule', 'Tasks');
      }
      return;
    }
    try {
      const path = kind === 'income'
        ? '/api/accounting/revenue' : '/api/accounting/expenses';
      const r = await api(path, {
        method: 'POST',
        body: JSON.stringify({
          amount: amt,
          description: desc,
          category: ov.querySelector('#mlog-cat').value || null,
          occurred_at: `${day}T12:00:00Z`,
          source: 'manual',
        }),
      });
      if (r && r.ok !== false) {
        showToast(r.needs_review
          ? 'Logged — sitting in review, it needs a category'
          : `Logged · ${kind === 'income' ? '+' : '−'}${formatNaira(amt, true)}`);
        close();
        refreshMoney();
      } else {
        showToast(`Log failed: ${r?.error || 'unknown'}`);
        settle();
      }
    } catch (err) {
      failed(err, 'Log', 'the ledger');
    }
  });
  document.body.appendChild(ov);
  ov.querySelector('#mlog-amt')?.focus();
}

window.mtxRegOpen = mtxRegOpen;
window.mtxBudgetOpen = mtxBudgetOpen;
window.mnyLogLineOpen = mnyLogLineOpen;


// ═══ Wave-2 PR-B: manual journal entry (spine vet) ═══
// The balance sheet has been telling the operator to 'post an opening
// inventory entry' with no way to do it. Manager-gated sheet: dated,
// memo'd, N lines (account code + debit XOR credit), must balance to
// the kobo before Save enables. source='manual' survives rebuilds.

async function _bksChartCodes() {
  if (State._bksChart) return State._bksChart;
  try {
    const r = await api('/api/accounting/books/chart');
    State._bksChart = r.chart || r.accounts || (Array.isArray(r) ? r : []);
  } catch (e) { State._bksChart = []; }
  return State._bksChart;
}

async function bksJournalOpen() {
  const chart = await _bksChartCodes();
  const opts = chart.map((c) =>
    `<option value="${esc(c.code)}">${esc(c.code)} · ${esc(c.name || '')}</option>`).join('');
  const lineRow = () => `
    <div class="bksje-line">
      <select class="mtx-input bksje-acct" aria-label="Account">${opts}</select>
      <input class="mtx-input mono-num bksje-dr" type="number" min="0" step="0.01" placeholder="Debit">
      <input class="mtx-input mono-num bksje-cr" type="number" min="0" step="0.01" placeholder="Credit">
    </div>`;
  const ov = document.createElement('div');
  ov.className = 'mreg-ov';
  ov.innerHTML = `
    <div class="mreg-sheet mreg-detail" role="dialog" aria-label="Manual journal entry">
      <div class="mreg-head"><span>Manual journal entry</span>
        <button class="mreg-x" type="button" aria-label="Close">&times;</button></div>
      <div class="mreg-dbody">
        <label class="mreg-drow"><span>Date</span>
          <input class="mtx-input mono-num" id="bksje-date" type="date"
            value="${mtxLagosToday()}"></label>
        <label class="mreg-drow"><span>Memo</span>
          <input class="mtx-input" id="bksje-memo" type="text"
            placeholder="e.g. Opening inventory balance"></label>
        <div id="bksje-lines">${lineRow()}${lineRow()}</div>
        <button class="bks-tool-btn" type="button" id="bksje-add">Add line</button>
        <div class="data-stat-sub" id="bksje-bal">Debits ₦0 · Credits ₦0</div>
      </div>
      <div class="mreg-acts">
        <button class="bks-tool-btn mreg-save" type="button" id="bksje-save" disabled>Post entry</button>
      </div>
    </div>`;
  const close = () => ov.remove();
  eonBackdropClose(ov, close);  // ghost-click law (2026-07-25)
  mtxEscClose(ov, close);
  ov.querySelector('.mreg-x').addEventListener('click', close);
  const balEl = ov.querySelector('#bksje-bal');
  const saveBtn = ov.querySelector('#bksje-save');
  const recompute = () => {
    let td = 0; let tc = 0;
    ov.querySelectorAll('.bksje-line').forEach((l) => {
      td += parseFloat(l.querySelector('.bksje-dr').value) || 0;
      tc += parseFloat(l.querySelector('.bksje-cr').value) || 0;
    });
    const balanced = td > 0 && Math.abs(td - tc) < 0.01;
    balEl.textContent = `Debits ${formatNaira(td)} · Credits ${formatNaira(tc)}`
      + (balanced ? ' · balanced' : ' · must balance to post');
    balEl.style.color = balanced ? 'var(--ok)' : 'var(--warn)';
    saveBtn.disabled = !balanced;
  };
  ov.addEventListener('input', recompute);
  ov.querySelector('#bksje-add').addEventListener('click', () => {
    ov.querySelector('#bksje-lines').insertAdjacentHTML('beforeend', lineRow());
  });
  saveBtn.addEventListener('click', async () => {
    const lines = [];
    ov.querySelectorAll('.bksje-line').forEach((l) => {
      const dr = parseFloat(l.querySelector('.bksje-dr').value) || 0;
      const cr = parseFloat(l.querySelector('.bksje-cr').value) || 0;
      if (dr > 0 || cr > 0) {
        lines.push({ account_code: l.querySelector('.bksje-acct').value,
                     debit_ngn: dr, credit_ngn: cr });
      }
    });
    saveBtn.disabled = true;
    try {
      const r = await api('/api/accounting/books/manual-entry', {
        method: 'POST',
        body: JSON.stringify({
          entry_date: ov.querySelector('#bksje-date').value,
          memo: ov.querySelector('#bksje-memo').value.trim(),
          lines,
        }),
      });
      if (r && r.ok !== false) {
        showToast(`Posted — ${r.lines} lines, ${formatNaira(r.total_ngn)}`);
        close(); bksReloadStatements();
      } else { showToast(`Post failed: ${r?.error || 'unknown'}`); saveBtn.disabled = false; }
    } catch (e) { showToast(`Post failed: ${e?.message || e}`); saveBtn.disabled = false; }
  });
  document.body.appendChild(ov);
}
window.bksJournalOpen = bksJournalOpen;

// ── Bank ↔ Books reconciliation card (Wave-2 PR-B2, mig 518) ─────────
// The vet's "two sources of financial truth" finding closes here: every
// statement line on a books-mapped account is either explained (matched
// to a transactions row or a journal cash line, or classed internal) or
// sits in a queue with two honest actions — Make it a transaction
// (→ register review inbox → books with a real cash account, not 1090
// suspense) or Ignore with a reason. Matching runs nightly; the card
// offers Run match now to managers.

async function bksLoadBankRec() {
  const data = await api('/api/finance/bank-rec/summary?days=60');
  const host = document.getElementById('bks-rec');
  if (!host) return;
  const meta = document.getElementById('bks-rec-meta');
  if (meta) meta.textContent = 'last 60 days · nightly match 04:05';
  if (data && data.ok === false) {
    // Vet 2026-07-27: {ok:false} rendered as "no statement lines yet".
    host.innerHTML = `<div class="bks-error">Bank↔books check couldn't load: ${esc(data.error || 'engine error')} · <button class="bks-tool-btn" type="button" onclick="bksLoadCard('bks-rec', bksLoadBankRec)">Retry</button></div>`;
    return;
  }
  const accts = (data && data.accounts) || [];
  const role = State.member?.base_role || State.member?.role || 'member';
  const canManage = ['owner', 'admin', 'manager'].includes(role);
  if (!accts.length) {
    host.innerHTML = `<div class="bks-empty">No statement lines in the window yet — upload bank statements from Overview &rarr; Cash.</div>`;
    return;
  }
  const rows = accts.map((a) => {
    const pct = a.books_mapped ? `${a.matched_pct}%` : '—';
    const unex = a.books_mapped
      ? (a.unexplained
        ? `<button class="bks-rec-unex" data-rec-acct="${esc(a.entity_account_id)}" type="button">${a.unexplained} unexplained</button>`
        : `<span class="bks-rec-clean">clean</span>`)
      : `<span class="bks-rec-oos" title="No EON cash account linked — out of books scope">not books-mapped</span>`;
    return `<div class="bks-rec-row">
      <span class="bks-rec-name">${esc(a.name)}</span>
      <span class="bks-rec-pct">${pct}</span>
      <span class="bks-rec-lines">${a.explained}/${a.lines} explained</span>
      ${unex}
      <span class="bks-rec-last">${esc(a.last_line || '')}</span>
    </div>`;
  }).join('');
  const totalUnex = data.unexplained_total || 0;
  host.innerHTML = `
    <div class="bks-rec-head">
      <span>${totalUnex ? `${totalUnex} line(s) need an explanation` : 'Ledger agrees with the bank in this window'}</span>
      <span class="bks-rec-headbtns">
        ${canManage ? `<button class="bks-tool-btn bks-tool-quiet" id="bks-rec-pairs" type="button">Pair own transfers</button>` : ''}
        ${canManage ? `<button class="bks-tool-btn bks-tool-quiet" id="bks-rec-run" type="button">Run match now</button>` : ''}
      </span>
    </div>
    <div class="bks-rec-grid">${rows}</div>
    <div id="bks-rec-drill"></div>`;
  document.getElementById('bks-rec-pairs')?.addEventListener('click', () => bksRecPairPreview());
  document.getElementById('bks-rec-run')?.addEventListener('click', async (ev) => {
    const b = ev.currentTarget; b.disabled = true; b.textContent = 'Matching…';
    try {
      const r = await api('/api/finance/bank-rec/run', { method: 'POST' });
      showToast(`Matched ${((r.matched_tx || 0) + (r.matched_journal || 0))} · internal ${r.internal || 0} · ${r.unexplained || 0} left`);
    } catch (e) { showToast(`Match failed: ${e.message}`); }
    moneyRead('bks-rec',()=>bksLoadCard('bks-rec', bksLoadBankRec),true);
  });
  host.querySelectorAll('[data-rec-acct]').forEach((btn) => {
    btn.addEventListener('click', () => bksRecOpenDrill(btn.dataset.recAcct));
  });
}

async function bksRecOpenDrill(entityAccountId) {
  if ((State.member?.base_role || State.member?.role) === 'owner') {
    // One owner work queue: keep original statements and saved context
    // together instead of opening the older read-and-file list here.
    const scope=moneyViewScope(State);
    writeMoneyView(window.history,scope,'review',{queue:'all_open',account_id:entityAccountId,query:'',start:'',end:'',direction:'',kind:'all',grouping:'none',groupKey:null,page:0,sort:'largest'});
    moneyOpenView('workbench',true);
    return;
  }
  const drill = document.getElementById('bks-rec-drill');
  if (!drill) return;
  drill.innerHTML = bksSkel(3);
  let data;
  try {
    data = await api(`/api/finance/bank-rec/unexplained?days=60&entity_account_id=${encodeURIComponent(entityAccountId)}`);
  } catch (e) {
    drill.innerHTML = `<div class="bks-empty">Couldn't load the queue — ${esc(e.message)}</div>`;
    return;
  }
  if(data?.ok !== true || !Array.isArray(data.lines)){drill.innerHTML='<div class="bks-empty" role="status">These statement lines could not be read. Try the account again.</div>';return;}
  const lines = data.lines;
  if (!lines.length) {
    drill.innerHTML = `<div class="bks-empty">Nothing unexplained on this account.</div>`;
    return;
  }
  const role = State.member?.base_role || State.member?.role || 'member';
  const canManage = ['owner', 'admin', 'manager'].includes(role);
  const rows = lines.map((l) => `
    <div class="bks-rec-line" data-rec-line="${esc(l.id)}">
      <span class="bks-rec-ldate">${esc(String(l.occurred_at || '').slice(0, 10))}</span>
      <span class="bks-rec-lwho">${esc(l.counterparty || l.narration || 'no narration')}</span>
      <span class="bks-rec-lamt ${l.direction === 'out' ? 'neg' : 'pos'}">${l.direction === 'out' ? '−' : '+'}${esc(_ldgNativeAmount(l.amount,l.currency))}</span>
      <span class="bks-rec-lact">
        <button class="bks-rec-bridge" data-bridge="${esc(l.id)}" type="button" ${l.currency==='NGN'?'':'disabled title="Booking requires a checked exchange rate"'}>Make it a transaction</button>
        ${canManage ? `<button class="bks-rec-ignore" data-ignore="${esc(l.id)}" type="button">Ignore</button>` : ''}
      </span>
    </div>`).join('');
  drill.innerHTML = `
    <div class="bks-rec-drillhead">Unexplained lines${data.hidden ? ` · showing ${lines.length}, ${data.hidden} more hidden` : ''}</div>
    ${rows}`;
  drill.querySelectorAll('[data-bridge]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      btn.disabled = true; btn.textContent = 'Sending…';
      try {
        await api(`/api/finance/bank-rec/lines/${btn.dataset.bridge}/bridge`, { method: 'POST' });
        showToast('In the register review inbox — approve it there and it reaches the books.');
        btn.closest('.bks-rec-line')?.remove();
        moneyRead('bks-rec',()=>bksLoadCard('bks-rec', bksLoadBankRec),true);
      } catch (e) {
        showToast(`Bridge failed: ${e.message}`);
        btn.disabled = false; btn.textContent = 'Make it a transaction';
      }
    });
  });
  drill.querySelectorAll('[data-ignore]').forEach((btn) => {
    // No native dialogs (operator law) — Ignore swaps in an inline
    // reason field; the reason is kept as the audit note.
    btn.addEventListener('click', () => {
      const cell = btn.closest('.bks-rec-lact');
      if (!cell || cell.querySelector('.bks-rec-whywrap')) return;
      const id = btn.dataset.ignore;
      const wrap = document.createElement('span');
      wrap.className = 'bks-rec-whywrap';
      wrap.innerHTML = `<input class="bks-rec-why" type="text" maxlength="120" placeholder="why? (audit note)">
        <button class="bks-rec-whygo" type="button">Ignore it</button>`;
      cell.appendChild(wrap);
      const inp = wrap.querySelector('.bks-rec-why');
      inp.focus();
      wrap.querySelector('.bks-rec-whygo').addEventListener('click', async (ev) => {
        const go = ev.currentTarget; go.disabled = true;
        try {
          await api(`/api/finance/bank-rec/lines/${id}/ignore`, { method: 'POST', body: JSON.stringify({ reason: inp.value || '' }) });
          btn.closest('.bks-rec-line')?.remove();
          moneyRead('bks-rec',()=>bksLoadCard('bks-rec', bksLoadBankRec),true);
        } catch (e) { showToast(`Ignore failed: ${e.message}`); go.disabled = false; }
      });
    });
  });
}
// ── Self-transfer pairs preview + apply (Answer Desk WS-B) ───────────
// The resolver's dry run IS the gate: the operator sees every pair —
// own transfer / owner draw / capital injection, both legs, fee — and
// nothing writes until the Settle tap (or the nightly, which never
// touches legacy stamps). Draws/injections book pending_review into
// the register inbox, never netted silently.

async function bksRecPairPreview() {
  const drill = document.getElementById('bks-rec-drill');
  if (!drill) return;
  const read = (drill._pairRead || 0) + 1; drill._pairRead = read;
  drill.innerHTML = bksSkel(3);
  let data;
  try {
    data = await api('/api/finance/bank-rec/self-transfers/dry-run?days=90&reexamine=true');
    if (!drill.isConnected || drill._pairRead !== read) return;
    const leg = l => l && typeof l.line_id === 'string' && l.line_id && typeof l.account === 'string' && l.account && typeof l.currency === 'string' && /^[A-Z]{3}$/.test(l.currency) && typeof l.amount === 'number' && Number.isFinite(l.amount) && l.amount >= 0;
    if (data?.ok !== true || !Array.isArray(data.pairs) || (data.legacy_reopen != null && !Array.isArray(data.legacy_reopen)) || data.pairs.some(p => !p || !leg(p.out) || !leg(p.in) || p.out.currency !== p.in.currency || !['internal','owner_draw','capital_injection','inter_entity'].includes(p.kind) || (p.apply_blockers != null && (!Array.isArray(p.apply_blockers) || p.apply_blockers.some(reason => typeof reason !== 'string' || !reason))))) throw Error('incomplete preview');
  } catch {
    if (drill.isConnected && drill._pairRead === read) {
      drill.innerHTML = '<div class="bks-empty" role="status">Transfer matches could not be read. This is not confirmation that there are no transfers.</div><button type="button" class="bks-tool-btn" id="bks-rec-pairs-retry">Try the read again</button>';
      document.getElementById('bks-rec-pairs-retry')?.addEventListener('click', bksRecPairPreview);
    }
    return;
  }
  const pairs = data.pairs, reopen = data.legacy_reopen || [];
  if (!pairs.length && !reopen.length) {
    drill.innerHTML = '<div class="bks-empty">No own-account transfer pairs found in the last 90 days. This read does not establish that every statement has arrived.</div>';
    return;
  }
  const KIND_WORDS = { internal:'Between accounts in the same book', owner_draw:'Business money taken by the owner', capital_injection:'Owner money added to the business', inter_entity:'Between different books' };
  const money = (value,currency) => `${currency} ${new Intl.NumberFormat('en-NG',{maximumFractionDigits:2}).format(value)}`;
  const totals = new Map(); pairs.forEach(p => totals.set(p.out.currency,(totals.get(p.out.currency)||0)+p.out.amount));
  const side = (l,direction) => `<td data-label="${direction}"><strong>${esc(l.account)}</strong><span>${esc(money(l.amount,l.currency))}</span><small>${esc(l.date || 'Date not recorded')}</small><details><summary>Original statement wording</summary><p>${esc(l.narration || 'Wording not returned')}</p><small>${esc(l.counterparty || 'Counterparty not recorded')}</small><small>Statement line: ${esc(l.line_id)}</small></details></td>`;
  const blockers=Array.isArray(data.apply_blockers)?data.apply_blockers:[];
  const heldReasons=p=>Array.isArray(p.apply_blockers)?p.apply_blockers:blockers;
  const readyCount=pairs.filter(p=>!heldReasons(p).length).length,heldCount=pairs.length-readyCount;
  const rows = pairs.map(p => `<tr><th scope="row">${esc(KIND_WORDS[p.kind])}${heldReasons(p).map(reason=>`<small>Still needs checking: ${esc(reason)}</small>`).join('')}</th>${side(p.out,'Money out')}${side(p.in,'Money in')}<td data-label="Difference">${typeof p.fee_delta==='number'&&Number.isFinite(p.fee_delta)?esc(money(p.fee_delta,p.out.currency)):'Not established'}<small>Reported difference between the two lines. Check any bank charge.</small></td></tr>`).join('');
  const draws = pairs.filter(p=>p.kind==='owner_draw'&&!heldReasons(p).length).length, injs = pairs.filter(p=>p.kind==='capital_injection'&&!heldReasons(p).length).length;
  const bookNote = draws || injs ? `<p class="mw-fine">${draws} owner withdrawal(s) and ${injs} owner contribution(s) would create entries for accounting review. They are not treated as ordinary sales or expenses by this preview.</p>` : '';
  const reopenNote = reopen.length ? `<details class="mw-fine"><summary>${reopen.length} old link(s) would reopen for explanation</summary><ul>${reopen.map(l=>`<li><strong>${esc(l.account || 'Account not returned')}</strong> · ${esc(l.date || 'Date not recorded')} · ${esc(_ldgNativeAmount(l.amount,l.currency))}<p>${esc(l.narration || 'Wording not returned')}</p><small>Statement line: ${esc(l.line_id)}</small></li>`).join('')}</ul></details>` : '';
  const canRecord=typeof data.preview_key==='string'&&/^[0-9a-f]{64}$/.test(data.preview_key)&&(readyCount>0||reopen.length>0);
  const recordNote=heldCount?`<p class="mw-fine">${heldCount} pair(s) still need checking and will stay unchanged. ${readyCount?`You can record the ${readyCount} ready match(es) separately.`:'No matches are ready to record.'}</p>`:!canRecord?'<p class="mw-fine">Recording is unavailable for this read. Read the matches again before recording.</p>':'';
  drill.innerHTML = `<div class="bks-rec-drillhead">Review own-account movements · ${pairs.length} proposed pair(s)</div><p class="mw-fine">Reading this preview does not move money or record a match. Confirm both statement lines before recording. Amounts stay in their original currencies.</p><p class="mw-fine">Outgoing amounts: ${[...totals].map(([cur,amount])=>esc(money(amount,cur))).join(' · ') || 'No new pairs'}</p><table class="mw-transfer-table"><thead><tr><th scope="col">Proposed treatment</th><th scope="col">Money out</th><th scope="col">Money in</th><th scope="col">Difference</th></tr></thead><tbody>${rows}</tbody></table>${bookNote}${reopenNote}${recordNote}<div class="bks-rec-pairact"><button class="bks-tool-btn" id="bks-rec-pairs-apply" type="button" ${canRecord?'':'disabled'}>Record ${readyCount} match(es)${reopen.length?` &amp; reopen ${reopen.length} old link(s)`: ''}</button></div>`;
  document.getElementById('bks-rec-pairs-apply')?.addEventListener('click', async ev => {
    if(!canRecord)return;
    const button=ev.currentTarget;if(button.disabled)return;button.disabled=true;button.textContent='Recording…';
    try {
      const result=await api('/api/finance/bank-rec/self-transfers/apply',{method:'POST',body:JSON.stringify({days:90,reexamine:true,preview_key:data.preview_key})});
      if(result?.ok!==true||!Number.isInteger(result.applied_pairs)||result.applied_pairs<0||result.applied_pairs>readyCount||!Number.isInteger(result.errors)||result.errors<0||(heldCount&&result.blocked_pairs!==heldCount)||(reopen.length&&(!Number.isInteger(result.legacy_reopened_count)||result.legacy_reopened_count<0||result.legacy_reopened_count>reopen.length))){
        showToast(result?.error==='preview_changed'?'The matches changed. Read them again before recording.':'Recording was not confirmed. Read the latest matches before trying again.');
        await bksRecPairPreview();return;
      }
      showToast(`Confirmed ${result.applied_pairs} of ${readyCount} matches${heldCount?` · ${heldCount} still need checking`: ''}${reopen.length?` · ${result.legacy_reopened_count} of ${reopen.length} old links reopened`:''}${result.errors?` · ${result.errors} need checking against the latest records`:''}. This does not move money.`);
      bksLoadCard('bks-rec',bksLoadBankRec);
    } catch {showToast('Recording was not confirmed. Read the latest matches before trying again.');}
    finally{if(button.isConnected){button.disabled=false;button.textContent=`Record ${readyCount} match(es)${reopen.length?` & reopen ${reopen.length} old link(s)`: ''}`;}}
  });
}

window.bksLoadBankRec = bksLoadBankRec;
// Vet 2026-07-27: new inline retry handlers need module-scope exports
// (test_inline_handler_exports recomputes the on*= set).
window.bksLoadCard = bksLoadCard;
window.refreshCashSection = refreshCashSection;
window.bksRecOpenDrill = bksRecOpenDrill;
window.bksRecPairPreview = bksRecPairPreview;

// ── Books export sheet (Wave-2 PR-C) ────────────────────────────────
// Five CSVs over the same engines the screen renders (they can never
// disagree) + the one-tap accountant email pack. Downloads are
// view-tier; emailing the pack off-platform is owner/admin/manager.

const BKS_EXPORTS = [
  ['trial_balance', 'Trial balance'],
  ['pnl', 'Profit and loss'],
  ['vat', 'VAT working (every input line)'],
  ['paye', 'PAYE schedule (per staff)'],
  ['wht', 'WHT ledger'],
];

function bksExportOpen() {
  const period = State.booksPeriod || bksCurrentMonth();
  const role = State.member?.base_role || State.member?.role || 'member';
  const canEmail = ['owner', 'admin', 'manager'].includes(role);
  const old = document.getElementById('bks-export-sheet');
  if (old) { old.remove(); return; }
  const wrap = document.createElement('div');
  wrap.id = 'bks-export-sheet';
  wrap.className = 'bks-export-sheet';
  wrap.innerHTML = `
    <div class="bks-export-head">Export · ${esc(bksMonthLabel(period))}</div>
    ${BKS_EXPORTS.map(([k, label]) => `
      <button class="bks-export-row" data-bks-export="${k}" type="button">
        <span>${esc(label)}</span><span class="bks-export-kind">CSV</span>
      </button>`).join('')}
    ${canEmail ? `
      <div class="bks-export-mail">
        <input id="bks-export-to" type="email" placeholder="accountant@…" autocomplete="email">
        <button class="bks-tool-btn" id="bks-export-send" type="button">Email pack</button>
      </div>
      <div class="bks-export-note">Sends all five as attachments from CRUX via the email ledger.</div>` : ''}
  `;
  document.getElementById('bks-export-btn')?.insertAdjacentElement('afterend', wrap);
  // Auth rides Bearer headers, so downloads go through fetch + blob —
  // a bare <a href> would 401.
  wrap.querySelectorAll('[data-bks-export]').forEach((row) => {
    row.addEventListener('click', async () => {
      row.disabled = true;
      try {
        const headers = {};
        if (State.jwt) headers['Authorization'] = `Bearer ${State.jwt}`;
        if (State.tenant) headers['X-Tenant-Slug'] = State.tenant.slug;
        const resp = await fetch(`${API}/api/accounting/books/export/${row.dataset.bksExport}.csv?period=${encodeURIComponent(period)}`, { headers });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        const cd = resp.headers.get('Content-Disposition') || '';
        const fname = (cd.match(/filename="([^"]+)"/) || [])[1]
          || `crux_${row.dataset.bksExport}_${period}.csv`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = fname; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      } catch (e) { showToast(`Export failed: ${e.message}`); }
      row.disabled = false;
    });
  });
  document.getElementById('bks-export-send')?.addEventListener('click', async (ev) => {
    const to = (document.getElementById('bks-export-to')?.value || '').trim();
    if (!to || !to.includes('@')) { showToast('Enter the accountant\'s email first.'); return; }
    const b = ev.currentTarget; b.disabled = true; b.textContent = 'Sending…';
    try {
      const r = await api('/api/accounting/books/email-pack', {
        method: 'POST',
        body: JSON.stringify({ period, to }),
      });
      showToast(`Pack sent — ${r.sent} file(s)${r.failed?.length ? ` · ${r.failed.length} failed` : ''}.`);
      wrap.remove();
    } catch (e) {
      showToast(`Send failed: ${e.message}`);
      b.disabled = false; b.textContent = 'Email pack';
    }
  });
}
window.bksExportOpen = bksExportOpen;

// ── Wave-3b rebuild (2026-08-02): TaskOS detail parity + Recurring ──
// Comments/edit/cancel land on the sheet managers actually use (they
// lived only in People OS / Staff OS); the 47 headless recurring
// templates get a visible, pausable roster.

async function _tosLoadComments(ov, t) {
  if (_trActive()) return _trLoadComments(ov, t);
  const el = ov.querySelector('#tos-comments');
  if (!el) return;
  let comments = [];
  try {
    const d = await api(`/api/tasks/${t.id}/comments`);
    comments = (d && d.comments) || [];
  } catch (e) {
    el.innerHTML = `<div class="csw-card"><div class="csw-goalmeta">Couldn't load comments — ${esc(e.message)}</div></div>`;
    return;
  }
  // Redesign 2026-08-09 (operator: "logs above 10 lines should have a
  // collapse button"). The old hard eight-row cut printed "Comments ·
  // 30" over 8 rows with no door to the other 22. Now: the latest 10
  // always show; anything earlier folds behind the animated tos-fold,
  // counted honestly on its button.
  const CMT_SHOW = 10;
  const cRow = c => `
    <div class="tos-cmt">
      <span class="tos-cmt__who">${esc(c.author_name || c.author || 'someone')}</span>
      <span class="tos-cmt__body">${esc(c.body || c.comment || '')}</span>
      <span class="tos-cmt__when">${esc(String(c.created_at || '').slice(0, 16).replace('T', ' '))}</span>
    </div>`;
  const earlier = comments.slice(0, -CMT_SHOW);
  const recent = comments.slice(-CMT_SHOW);
  const fold = earlier.length
    ? _tosFold('tos-cmt-fold', earlier.map(cRow).join(''),
               `show the ${earlier.length} earlier`, 'fold the earlier back', true)
    : '';
  el.innerHTML = `<div class="csw-card" style="margin-top:10px">
    <div class="tos-sec" style="margin-top:0">Comments${comments.length ? ` · ${comments.length}` : ''}</div>
    ${fold}${recent.map(cRow).join('') || '<div class="csw-goalmeta">No comments yet.</div>'}
    <div class="tos-cmt__form">
      <input id="tos-cmt-in" type="text" maxlength="500" placeholder="Add a note — the assignee sees it">
      <button class="csw-mini" id="tos-cmt-send">Send</button>
    </div></div>`;
  _tosBindFolds(el);
  const send = el.querySelector('#tos-cmt-send');
  send?.addEventListener('click', async () => {
    const inp = el.querySelector('#tos-cmt-in');
    const body = (inp?.value || '').trim();
    if (!body) return;
    send.disabled = true; send.textContent = 'Sending…';
    try {
      await api(`/api/tasks/${t.id}/comments`, { method: 'POST', body: JSON.stringify({ body }) });
      _tosLoadComments(ov, t);
    } catch (e) {
      showToast(`Comment failed: ${e.message}`);
      send.disabled = false; send.textContent = 'Send';
    }
  });
}

function _tosEditOpen(ov, t) {
  const host = ov.querySelector('#tos-editcard');
  if (!host) return;
  if (host.querySelector('.tos-edit')) { host.innerHTML = ''; return; }
  const due = t.due_at ? String(t.due_at).slice(0, 10) : '';
  host.innerHTML = `<div class="csw-card tos-edit" style="margin-top:8px">
    <label class="tos-edit__l">Title<input id="tos-e-title" type="text" maxlength="200" value="${esc(t.title || '')}"></label>
    <label class="tos-edit__l">Full brief<textarea id="tos-e-brief" rows="6" maxlength="10000">${esc(t.description || '')}</textarea></label>
    <label class="tos-edit__l">Due<input id="tos-e-due" type="date" value="${esc(due)}"></label>
    <label class="tos-edit__l">Priority<select id="tos-e-pri">
      ${['low', 'normal', 'high', 'urgent'].map(p => `<option value="${p}" ${((t.priority || 'normal') === p) ? 'selected' : ''}>${p}</option>`).join('')}
    </select></label>
    <div style="display:flex;gap:8px;margin-top:8px">
      <button class="csw-confirm" id="tos-e-save">Save brief</button>
      <button class="csw-mini" id="tos-e-x">Never mind</button>
    </div></div>`;
  host.querySelector('#tos-e-x').addEventListener('click', () => { host.innerHTML = ''; });
  host.querySelector('#tos-e-save').addEventListener('click', async (ev) => {
    const b = ev.currentTarget; b.disabled = true; b.textContent = 'Saving…';
    const changes = {};
    const title = host.querySelector('#tos-e-title').value.trim();
    const description = host.querySelector('#tos-e-brief').value.trim();
    const dueV = host.querySelector('#tos-e-due').value;
    const pri = host.querySelector('#tos-e-pri').value;
    if (title && title !== (t.title || '')) changes.title = title;
    if (description !== (t.description || '')) changes.description = description;
    if (dueV && dueV !== due) changes.due_at = `${dueV}T18:00:00+01:00`;
    if (pri !== (t.priority || 'normal')) changes.priority = pri;
    if (!Object.keys(changes).length) { host.innerHTML = ''; return; }
    try {
      const result=await api(`/api/tasks/${t.id}`, { method: 'PATCH', body: JSON.stringify(changes) });
      if(!result?.ok)throw Error(result?.error || 'The brief could not be saved.');
      showToast('Task fixed — the trail records what changed.');
      ov.remove(); await _tosLoad(); openTaskDetail(t.id);
    } catch (e) {
      showToast(`Fix failed: ${e.message}`);
      b.disabled = false; b.textContent = 'Save brief';
    }
  });
}

function _tosCancelOpen(ov, t) {
  const host = ov.querySelector('#tos-editcard');
  if (!host) return;
  if (host.querySelector('.tos-cancel')) { host.innerHTML = ''; return; }
  host.innerHTML = `<div class="csw-card tos-cancel" style="margin-top:8px">
    <div class="csw-goalmeta">Cancelling tells the assignee — no more silent vanishing.</div>
    <input id="tos-c-why" type="text" maxlength="200" placeholder="Why? (the assignee sees this)" style="margin-top:6px">
    <div style="display:flex;gap:8px;margin-top:8px">
      <button class="csw-confirm tos-danger" id="tos-c-go">Cancel task</button>
      <button class="csw-mini" id="tos-c-x">Keep it</button>
    </div></div>`;
  host.querySelector('#tos-c-x').addEventListener('click', () => { host.innerHTML = ''; });
  host.querySelector('#tos-c-go').addEventListener('click', async (ev) => {
    const b = ev.currentTarget; b.disabled = true; b.textContent = 'Cancelling…';
    try {
      await api(`/api/tasks/${t.id}/transition`, {
        method: 'POST',
        body: JSON.stringify({ status: 'cancelled', note: host.querySelector('#tos-c-why').value.trim() }),
      });
      showToast('Task cancelled — assignee notified.');
      ov.remove(); _tosLoad();
    } catch (e) {
      showToast(`Cancel failed: ${e.message}`);
      b.disabled = false; b.textContent = 'Cancel task';
    }
  });
}

// ── Recurring roster ────────────────────────────────────────────────

async function _tosLoadRecurring() {
  try {
    const d = await api('/api/tasks/recurring/templates');
    _tosState.recurring = { items: (d && d.items) || [], err: null };
  } catch (e) {
    _tosState.recurring = { items: [], err: e.message };
  }
}

function _tosCadenceLabel(tp) {
  const h = `${String(tp.hour_local ?? 9).padStart(2, '0')}:00`;
  if (tp.cadence === 'weekly') {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return `Weekly · ${days[tp.day_of_week_local ?? 1] || 'Mon'} ${h}`;
  }
  if (tp.cadence === 'monthly') return `Monthly · day ${tp.day_of_month || 1} · ${h}`;
  return `Daily · ${h}`;
}

function _tosRecurring() {
  const r = _tosState.recurring;
  if (!r) return `<div class="csw-card"><div class="csw-goalmeta">Loading the recurring roster…</div></div>`;
  if (r.err) return `<div class="csw-card"><div class="csw-goalmeta">Couldn't load templates — ${esc(r.err)}</div></div>`;
  const items = r.items;
  const active = items.filter(i => i.is_active !== false).length;
  const canRun = memberCan('members:change_role');
  const rows = items.map(tp => `
    <div class="tos-rec ${tp.is_active === false ? 'tos-rec--off' : ''}">
      <div class="tos-rec__main">
        <span class="tos-rec__t">${esc(tp.title || 'template')}</span>
        <span class="tos-rec__meta">${esc(_tosCadenceLabel(tp))}${tp.category ? ' · ' + esc(tp.category) : ''}</span>
      </div>
      <span class="tos-rec__pill ${tp.is_active === false ? 'off' : 'on'}">${tp.is_active === false ? 'paused' : 'active'}</span>
      ${canRun ? `<button class="csw-mini" data-rec-toggle="${esc(tp.id)}" data-rec-state="${tp.is_active === false ? '1' : '0'}">${tp.is_active === false ? 'Resume' : 'Pause'}</button>` : ''}
    </div>`).join('');
  return `<div class="csw-card">
    <div class="tos-rec__head">
      <span>${items.length} template(s) · ${active} active — these mint the routine work every hour</span>
      ${canRun ? `<button class="csw-mini" id="tos-rec-seed">Seed now</button>` : ''}
    </div>
    ${rows || '<div class="csw-goalmeta">No recurring templates yet.</div>'}
  </div>`;
}

function _tosBindRecurring() {
  document.getElementById('tos-rec-seed')?.addEventListener('click', async (ev) => {
    const b = ev.currentTarget; b.disabled = true; b.textContent = 'Seeding…';
    try {
      const r = await api('/api/tasks/recurring/seed-now', { method: 'POST' });
      showToast(`Seeded — ${r.seeded ?? 0} task(s) minted.`);
    } catch (e) { showToast(`Seed failed: ${e.message}`); }
    await _tosLoadRecurring(); _tosRenderView();
  });
  document.querySelectorAll('[data-rec-toggle]').forEach(b => b.addEventListener('click', async () => {
    const id = b.getAttribute('data-rec-toggle');
    const toActive = b.getAttribute('data-rec-state') === '1';
    b.disabled = true;
    try {
      await api(`/api/tasks/recurring/templates/${id}`, { method: 'PATCH', body: JSON.stringify({ is_active: toActive }) });
      await _tosLoadRecurring(); _tosRenderView();
    } catch (e) { showToast(`Update failed: ${e.message}`); b.disabled = false; }
  }));
}

// ── Settlement truth card (bank-rec v2) — the nightly engine's first
// door. Per-store expected-vs-landed with the unmatched-money number.
async function bksLoadSettlement() {
  const data = await api('/api/finance/settlement-recon');
  if(!data||data.ok===false||!Object.prototype.hasOwnProperty.call(data,'run'))throw new Error('Settlement records could not be read.');
  const host = document.getElementById('bks-setl');
  if (!host) return;
  const run = data && data.run;
  const meta = document.getElementById('bks-setl-meta');
  const role = State.member?.base_role || State.member?.role || 'member';
  const canRun = ['owner', 'admin', 'manager'].includes(role);
  const runBtn = canRun ? `<button class="bks-tool-btn bks-tool-quiet" id="bks-setl-run" type="button">Run now</button>` : '';
  if (!run) {
    host.innerHTML = `<div class="bks-empty">No reconciliation run yet.</div>${runBtn}`;
  } else {
    const s = run.summary || {};
    const w = s.window || [];
    if (meta) meta.textContent = `window ${w[0] || '—'} → ${w[1] || '—'} · ran ${String(run.created_at || '').slice(0, 10)}`;
    const staleDays = Math.round((Date.now() - new Date(w[1] || run.created_at)) / 86400000);
    const gaps = (s.store_gaps || []);
    // A store whose statements don't cover this window gets no figure —
    // "clean" and a naira gap are both claims the engine can't make when
    // the bank side is unread. Runs written before that check existed
    // carry no `measurable` key; those keep their old rendering.
    const blind = gaps.filter(g => g.measurable === false);
    const rows = gaps.map(g => {
      const dark = g.measurable === false;
      const verdict = dark
        ? `<span class="bks-setl-gap bks-setl-blind">can't tell yet</span>`
        : `<span class="mono bks-setl-gap">${(g.gap_ngn || 0) ? formatNaira(g.gap_ngn) + ' gap' : 'clean'}</span>`;
      return `
      <div class="bks-setl-row ${dark ? 'blind' : (Math.abs(g.gap_ngn || 0) > 1000 ? 'gap' : '')}">
        <span class="bks-setl-store">${esc(g.store || '')}</span>
        <span class="mono">${formatNaira(g.expected_ngn || 0)} expected</span>
        <span class="mono">${formatNaira(g.landed_ngn || 0)} landed${dark ? ' so far' : ''}</span>
        ${verdict}
      </div>`;
    }).join('');
    const blindNote = blind.length ? `<div class="csw-goalmeta" style="margin-top:8px">
        ${esc(blind.map(g => g.store).join(', '))}: EON hasn't been sent enough bank statements to check ${blind.length > 1 ? 'these stores' : 'this store'} yet${blind[0].covered_through ? ` — the newest one reaches ${esc(String(blind[0].covered_through))}` : ''}. Send them and this fills itself in.
      </div>` : '';
    const ct = s.channel_truth || {};
    host.innerHTML = `
      ${staleDays > 7 ? `<div class="csw-goalmeta" style="color:var(--warn)">This window ends ${staleDays} days ago — upload or wire bank statements to bring it current.</div>` : ''}
      <div class="bks-setl-head">
        <span>${s.credits_matched ?? 0}/${s.credits_processed ?? 0} credits matched${(s.unmatched_ngn || 0) ? ` · <b>${formatNaira(s.unmatched_ngn)} unmatched</b>` : ''}</span>
        ${runBtn}
      </div>
      ${rows || '<div class="bks-empty">No store rows in this window.</div>'}
      ${blindNote}
      ${ct.total_ngn ? `<div class="csw-goalmeta" style="margin-top:8px">Channel truth: walk-in ${Math.round(ct.walk_in_pct || 0)}% · web ${Math.round(ct.web_paystack_pct || 0)}% · rep ${Math.round(ct.rep_squad_pct || 0)}%</div>` : ''}`;
  }
  document.getElementById('bks-setl-run')?.addEventListener('click', async (ev) => {
    const b = ev.currentTarget; b.disabled = true; b.textContent = 'Running…';
    try { await api('/api/finance/settlement-recon/run', { method: 'POST' }); }
    catch (e) { showToast(`Run failed: ${e.message}`); }
    moneyRead('bks-setl',()=>bksLoadCard('bks-setl', bksLoadSettlement),true);
  });
}
window.bksLoadSettlement = bksLoadSettlement;

// WhatsApp pocket: A's quiet line, scoped customer file and owner controls.
// No timer. Opening a conversation/room reads once; Refresh is deliberate.
function cswContactWhen(value) {
  if (!value) return 'Not recorded';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 'Time unreadable' : new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Africa/Lagos', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
  }).format(d) + ' WAT';
}
function cswContactReason(reason) {
  const words = {
    policy_not_enabled: 'Automated contact is paused.', before_activation: 'This need predates activation. Review it; do not replay it.',
    payment_receipts_off: 'Payment receipts are off. The owner can turn them on in the quiet rules.',
    customer_stopped: 'They asked us to stop automated messages. Your own reply still sends.', rep_is_handling_it: 'A rep has this conversation.',
    care_need_comes_first: 'Resolve their care request first.', customer_replied: 'They replied. The old follow-up has ended.',
    customer_paid: 'They paid. The old follow-up has ended.', requested_need_check_due: 'Check that exact request again.',
    request_context_changed: 'The conversation changed. Check the request before contacting them.',
    specific_permission_needed: 'A specific request or offers permission is needed.',
    template_check_due: 'The current template approval needs checking.', template_not_approved: 'The current template is not confirmed approved.',
    customer_service_window_closed: 'The reply window is closed. Keep the draft.',
    one_offer_per_fourteen_days: 'Give them space. An offer already went in the last 14 days.',
    two_offers_per_thirty_days: 'Two offers in 30 days is enough.',
    leave_the_unanswered_offer_alone: 'The last delivered offer is unanswered. Leave it there.',
    another_message_has_the_slot: 'Another message already has this person’s contact slot.',
    quiet_hours: 'It is quiet time where they are.', requested_and_useful: 'They asked for this, and the exact need is current.',
    permission_and_space: 'Permission and room for one useful message.', answer_customer: 'Answer their latest message.',
    current_order_update: 'A current order update.', facts_unreadable: 'EON cannot read the facts yet.', contact_mode_changed: 'This proposal ended when its contact control changed. Review the current need.', rep_stopped_proposal: 'A rep ended this proposal.', rep_will_review: 'A rep accepted a review task. No message is scheduled.'
  };
  return words[reason] || String(reason || 'The reason is unreadable.').replaceAll('_', ' ');
}
async function cswContactLoad(cid) {
  const s = cswState();
  if (cswPlatform(s.activeThread || {}) !== 'wa') return;
  let d;
  try { d = await api(`/api/cs/threads/${encodeURIComponent(cid)}/contact/context`); }
  catch (_) { d = {ok:false, message:'EON cannot read the contact plan yet.'}; }
  if (s.active !== cid) return; // A late read must never paint another person.
  s._contact = {cid, data:d};
  if(d?.rehearsal)s._contactRehearsal=true;
  // THE ROOM ON A PHONE (vet 2026-09-24, fault 10): the See plan key moved
  // into the header's ⋯ menu. It used to hold a whole row between the
  // header and the chat — 53px of a 380px screen with the keyboard up.
  // The quiet line stays where Design A put it, as words only (and is
  // folded away while typing, cs.css); the menu always reaches the plan.
  const item = document.getElementById('csw-menu-contact');
  if (item) {
    item.hidden = false;
    item.textContent = d?.ok ? 'Contact plan' : 'Read the contact plan again';
    item.onclick = () => {
      // Same hand-off as Payment evidence: close the menu and park focus on
      // its key, so closing the plan returns the rep somewhere visible.
      const menu = document.getElementById('csw-conv-menu');
      if (menu) { menu.open = false; menu.querySelector('summary')?.focus(); }
      return d?.ok ? cswContactOpen(cid) : cswContactLoad(cid);
    };
  }
  const line = document.getElementById('csw-contactline');
  if (!line) return;
  const words = d && d.ok ? d.line : (d?.message || 'The contact plan is unreadable.');
  line.hidden = !words;
  line.innerHTML = words ? `<span>${esc(words)}</span>` : '';
}
function cswContactDialog(title, content, anchor) {
  const previous=document.getElementById('csw-contact-dialog');
  if(previous){
    if(previous.contains(anchor))anchor=previous._returnFocus;
    previous._replaced=true;previous.close();previous.remove();
  }
  const dialog = document.createElement('dialog');
  dialog._returnFocus=anchor;
  dialog.id = 'csw-contact-dialog'; dialog.className = 'csw-contact-dialog';
  dialog.setAttribute('aria-labelledby', 'csw-contact-title');
  dialog.innerHTML = `<header><div><span class="csw-contact-kicker">THE CONTACT PLAN</span><h2 id="csw-contact-title">${esc(title)}</h2></div><button type="button" aria-label="Close contact plan">Close</button></header><div class="csw-contact-content">${content}</div>`;
  dialog.querySelector('header button').onclick = () => dialog.close();
  dialog.addEventListener('close', () => { dialog.remove(); if (!dialog._replaced && anchor?.isConnected) anchor.focus(); }, {once:true});
  document.body.append(dialog); dialog.showModal();
  return dialog;
}
function cswContactOpen(cid) {
  const s = cswState(), cache = s._contact;
  if (!cache || cache.cid !== cid || s.active !== cid) return;
  const d = cache.data;
  const anchor = document.activeElement;
  const permissions = (d.permissions || []).map(p => {
    const updated = p.revoked_at && (d.permissions || []).some(next => next.context_permission_id === p.id);
    const ended = updated ? 'Updated request' : p.revoked_at ? 'Withdrawn' : p.consumed_at ? 'Used for its one message' : new Date(p.expires_at) <= new Date() ? 'Expired' : 'Open';
    return `<article class="csw-contact-row"><div class="csw-contact-rowhead"><strong>${p.scope === 'offers' ? 'Offers permission' : 'An exact request'}</strong><span>${ended}</span></div>${p.quote ? `<blockquote>“${esc(p.quote)}”</blockquote>` : '<p>The original words could not be read.</p>'}${p.context_permission_id ? `<details><summary>Earlier request</summary>${p.context_readable && p.original_quote ? `<blockquote>“${esc(p.original_quote)}”</blockquote>` : '<p>The earlier words could not be read in this view.</p>'}</details>` : ''}<p>${esc(p.scope_label||'Exact request details need checking')} · until ${esc(cswContactWhen(p.expires_at))}</p>${ended === 'Open' ? `<button type="button" data-contact-withdraw="${esc(p.id)}">${d.rehearsal ? 'Rehearse withdrawal' : 'Withdraw this request'}</button><p role="status" class="csw-contact-check"></p>` : ''}</article>`;
  }).join('');
  const pending = (d.intents || []).filter(r => ['ready','held','reserved'].includes(r.status));
  const history = (d.intents || []).filter(r => !['ready','held','reserved'].includes(r.status));
  const rows = pending.map(r => `<article class="csw-contact-row" data-contact-intent="${esc(r.id)}"><div class="csw-contact-rowhead"><strong>${esc(({order_update:'Order update',requested_followup:'Asked-for follow-up',check_in:'Check-in',offer:'An offer',rep_reply:'Your reply',service_reply:'EON reply'})[r.purpose] || 'Contact proposal')}</strong><span>${esc(({ready:'Proposed',held:'Staying quiet',reserved:'Final check'})[r.status] || 'Needs a look')}</span></div><p>${esc(cswContactReason(r.reason))}</p><p>Need expires ${esc(cswContactWhen(r.expires_at))}</p><button type="button" data-contact-check="${esc(r.id)}">Rehearse this decision</button><button type="button" data-contact-action="stop" data-intent="${esc(r.id)}">${d.rehearsal?'Rehearse stop':'Stop this proposal'}</button><button type="button" data-contact-action="review_tomorrow" data-intent="${esc(r.id)}">${d.rehearsal?'Rehearse review tomorrow':'Review tomorrow'}</button><div class="csw-contact-check" role="status" aria-live="polite"></div></article>`).join('');
  const waiting = (d.waiting_for_stock || []).map(r => `<article class="csw-contact-row"><strong>${esc(r.product_title || 'Their piece')}</strong><p>${r.variant_shopify_id ? 'Variant recorded; exact store availability still needs checking.' : 'The size is missing. Clarify the request before sending anything.'}</p><p>Asked ${esc(cswContactWhen(r.requested_at))}</p></article>`).join('');
  const receipts = history.map(r => `<article class="csw-contact-row"><div class="csw-contact-rowhead"><strong>${esc(r.receipt.label)}</strong><span>${esc(cswContactWhen(r.submitted_at || r.updated_at))}</span></div>${r.check_id ? `<p>${['completed','done','verified'].includes(r.check_status)?'Check recorded':'Owned check'} · ${esc(r.check_owner||'Owner unreadable')} · due ${esc(cswContactWhen(r.check_due_at))}.</p>`:''}${r.receipt.needs_attention && !r.check_id ? `<p>A person needs to check this.${r.receipt.check_due_at ? ` Due ${esc(cswContactWhen(r.receipt.check_due_at))}.` : ''} No automatic retry.</p><button type="button" data-contact-action="accept_check" data-intent="${esc(r.id)}">${d.rehearsal?'Rehearse taking this check':'I’ll check this'}</button><p class="csw-contact-check" role="status"></p>` : ''}<p>${esc(cswContactReason(r.reason))}</p></article>`).join('');
  const dialog = cswContactDialog(cswHouseName(s.activeThread || {}) || 'This conversation',
    `<p class="csw-contact-lead">${esc(d.line)}</p><p class="csw-contact-note">Checked ${esc(cswContactWhen(d.read_at))}. ${esc(d.coverage)}</p><section><h3>What they asked for</h3>${permissions || '<p>Nothing agreed with this customer yet.</p>'}</section><section><h3>Next, if useful</h3>${rows || '<p>Nothing planned for this customer.</p>'}</section>${waiting ? `<section><h3>Waiting for stock</h3>${waiting}</section>` : ''}<section><h3>What happened</h3>${receipts || '<p>Nothing sent from this plan yet. The chat is the full history.</p>'}</section>`, anchor);
  const ownerTools=document.createElement('div');ownerTools.className='csw-contact-actions';
  const explicit=d.owner?.note==='contact:explicit';
  // Mine to hand back; a colleague's to take over (REP WINS, round three,
  // 17 Sep). A hold frees itself after a desk day, so a rep who left
  // cannot keep EON quiet on a thread for good.
  const mine=explicit && d.can_hand_back!==false;
  ownerTools.innerHTML=`<button type="button" data-contact-owner>${d.rehearsal?'Practise: ':''}${mine?'Hand back':'Take over'}</button><button type="button" data-contact-request>Note what they asked for</button><p role="status"></p>`;
  dialog.querySelector('.csw-contact-content').prepend(ownerTools);
  if(explicit && !mine){ownerTools.querySelector('p').textContent=`${d.owner?.holder||'Another rep'} has this conversation. Taking over moves it to you.`;}
  else if(mine && d.owner?.expires_at){ownerTools.querySelector('p').textContent=`Yours until ${cswContactWhen(d.owner.expires_at)}, or until you hand it back.`;}
  ownerTools.querySelector('button').onclick=async e=>{
    const output=ownerTools.querySelector('p');
    if(d.rehearsal){output.textContent=mine?'Hand-back would leave cancelled proposals cancelled. The real owner and drafts are unchanged.':'Takeover would make EON quiet and end queued contact proposals. Your real conversation and drafts are unchanged.';return;}
    e.currentTarget.disabled=true;
    try{await api(`/api/cs/threads/${encodeURIComponent(cid)}/${mine?'contact/hand-back':'takeover'}`,{method:'POST',body:'{}'});dialog.close();await cswContactLoad(cid);cswContactOpen(cid);}
    catch(err){output.textContent=err.message||'Ownership could not be changed.';e.currentTarget.disabled=false;}
  };
  ownerTools.querySelector('[data-contact-request]').onclick=()=>cswContactRequest(cid,d.rehearsal);
  dialog.querySelectorAll('[data-contact-action]').forEach(button=>button.onclick=async()=>{
    const output=button.parentElement.querySelector('.csw-contact-check');
    const action=button.dataset.contactAction;
    if(d.rehearsal){output.textContent=action==='stop'?'This would end the proposal without changing their permission or your draft. Nothing has changed.':action==='review_tomorrow'?'This would end the proposal and give you a review task for tomorrow at 9am WAT. No message would be scheduled. The real plan is unchanged.':'This would give you a delivery check due in two hours. It would not resend or claim delivery. The real plan is unchanged.';return;}
    button.disabled=true;
    try{const result=await api(`/api/cs/threads/${encodeURIComponent(cid)}/contact/action`,{method:'POST',body:JSON.stringify({action,intent_id:button.dataset.intent})});output.textContent=result.message+(result.task?.due_at?' Due '+cswContactWhen(result.task.due_at)+'.':'');await cswContactLoad(cid);_cswPrio.at=0;cswLoadPriority();}
    catch(err){output.textContent=err.message||'The action was not recorded.';button.disabled=false;}
  });
  dialog.querySelectorAll('[data-contact-withdraw]').forEach(button=>button.onclick=async()=>{
    const output=button.parentElement.querySelector('.csw-contact-check');
    if(d.rehearsal){output.textContent='This would withdraw only this permission and end its queued proposals. The real request is unchanged.';return;}
    button.disabled=true;
    try{await api(`/api/cs/threads/${encodeURIComponent(cid)}/contact/withdraw`,{method:'POST',body:JSON.stringify({permission_id:button.dataset.contactWithdraw})});dialog.close();await cswContactLoad(cid);cswContactOpen(cid);}
    catch(err){output.textContent=err.message||'The request could not be withdrawn.';button.disabled=false;}
  });
  dialog.querySelectorAll('[data-contact-check]').forEach(button => button.onclick = async () => {
    const output = button.parentElement.querySelector('.csw-contact-check');
    button.disabled = true; output.textContent = 'Checking the current facts…';
    try {
      const result = await api('/api/cs/contact/simulate', {method:'POST', body:JSON.stringify({thread:cid, intent_id:button.dataset.contactCheck, switches:s._contactPreview || {}})});
      output.textContent = result?.ok ? `${result.message}. ${cswContactReason(result.rehearsed.reason)} Nothing was sent or reserved.` : result?.message || 'The facts could not be read. Nothing was sent.';
    } catch (_) { output.textContent = 'The facts could not be read. Nothing was sent.'; }
    finally { button.disabled = false; }
  });
}
async function cswContactRules() {
  const host = document.getElementById('csw-contact-rules');
  if (!host || !cswIsOwner()) return;
  host.hidden = false;
  host.innerHTML = '<div class="csw-card__t">The quiet rules</div><p>Reading the current controls…</p>';
  let d;
  try { d = await api('/api/cs/contact/settings'); } catch (_) { d = null; }
  if (!host.isConnected) return;
  if (!d?.ok) { host.innerHTML = '<div class="csw-card__t">The quiet rules</div><p>The controls could not be read. No zeroes or states guessed.</p><button type="button">Try again</button>'; host.querySelector('button').onclick = cswContactRules; return; }
  const state = v => v === true ? 'On' : v === false ? 'Off' : v == null ? 'Unset' : String(v);
  host.innerHTML = `<div class="csw-card__t">The quiet rules</div><p>${d.rehearsal ? 'REHEARSAL — NOTHING SENDS' : 'Saved controls · current effective state'}</p>${d.modes.map(m => `<details class="csw-contact-rule"><summary><strong>${esc(m.label)}</strong><span>Saved ${esc(state(m.saved))} · effective ${esc(state(m.effective))}</span></summary><p>${esc(m.blocker)}</p>${m.workflows.map(w => `<div class="csw-contact-workflow"><strong>${esc(w.label)}</strong><span>Saved ${esc(state(w.saved))} · effective ${esc(state(w.effective))}</span><small>${esc(w.blocker)}</small></div>`).join('')}</details>`).join('')}<p class="csw-contact-note">Emergency pause covers automated order updates, payment receipts, asked-for follow-ups, offers and EON replies. Queued proposals end when paused and do not replay on resume. In-flight messages cannot be recalled. In live mode, rep replies stay available; rehearsal blocks every delivery.</p><p class="csw-contact-note">Read ${esc(cswContactWhen(d.read_at))}</p>`;
  if(d.payment_receipts) host.append(cswContactReceipts(d.payment_receipts));
  if(d.fulfillment_tasks) {
    const note=document.createElement('p');note.className='csw-contact-note';
    note.textContent=`Fulfillment tasks · saved ${state(d.fulfillment_tasks.saved)} · effective ${state(d.rehearsal?false:d.fulfillment_tasks.effective)}. ${d.rehearsal?'Rehearsal creates no tasks. ':''}${d.fulfillment_tasks.rule}`;
    host.append(note);
  }
  const form=document.createElement('form');form.className='csw-contact-controls';
  const preview=cswState()._contactPreview||{};
  form.innerHTML='<h3>Try the controls</h3><p>Changes below apply to decision previews until explicitly saved.</p>'+[
    ['enabled','Automated contact'],['order_updates','Order updates'],['requested_followups','Asked-for follow-ups'],['offers','Offers & check-ins']
  ].map(([key,label])=>`<label><input type="checkbox" name="${key}" ${preview[key]??d.policy?.[key]?'checked':''}>${label}</label>`).join('')+
    `<div class="csw-contact-actions"><button type="button" data-contact-results>View results</button>${!d.rehearsal&&d.policy?.activated_at?'<button type="submit">Save controls</button><button type="button" data-contact-pause>Pause automated contact</button>':''}</div><p role="status" class="csw-contact-check">${d.rehearsal?'Preview only. No live setting can change in rehearsal.':'These controls do not activate a new sender.'}</p>`;
  const readControls=()=>Object.fromEntries([...form.querySelectorAll('input')].map(input=>[input.name,input.checked]));
  form.onchange=()=>{cswState()._contactPreview=readControls();form.querySelector('[role="status"]').textContent='Preview changed. Open a proposal to rehearse it against the current customer facts. Nothing was saved or sent.';};
  form.querySelector('[data-contact-results]').onclick=cswContactResults;
  const save=async values=>{const output=form.querySelector('[role="status"]');try{await api('/api/cs/contact/settings',{method:'POST',body:JSON.stringify({values,revision:d.policy.revision})});await cswContactRules();}catch(err){output.textContent=err.message||'Controls were not saved. Refresh and try again.';}};
  form.onsubmit=e=>{e.preventDefault();save(readControls());};
  form.querySelector('[data-contact-pause]')?.addEventListener('click',()=>save({enabled:false}));
  host.append(form);
}

// The owner's Payment receipts switch (26 Sep 2026, round 2 on #3553). Under
// the contact checks a receipt had no switch anyone could turn on. It turns on
// receipts and nothing else (27 Sep, Fable 5.1's vet). Two taps: the first
// shows, in the server's plain words, what the switch starts or stops; only
// the second saves. Existing card parts only, no new styles.
function cswContactReceipts(r) {
  const form=document.createElement('form');form.className='csw-contact-controls';form.dataset.contactReceipts='';
  const draw=confirming=>{
    form.dataset.confirming=confirming?'1':'';
    form.innerHTML=`<h3>${esc(r.label||'Payment receipts')}</h3><p>${esc(r.line)}</p><p class="csw-contact-note">${esc(r.note)}</p>`+
      (confirming?`<p><strong>${esc(r.confirm)}</strong></p>`:'')+
      (r.can_change?`<div class="csw-contact-actions"><button type="submit">${confirming?(r.saved?'Yes, turn them off':'Yes, turn them on'):esc(r.action)}</button>${confirming?'<button type="button" data-contact-receipts-cancel>Cancel</button>':''}</div>`:'')+
      '<p role="status" class="csw-contact-check"></p>';
    const cancel=form.querySelector('[data-contact-receipts-cancel]');
    if(cancel)cancel.onclick=()=>draw(false);
  };
  draw(false);
  form.onsubmit=async e=>{
    e.preventDefault();
    if(!r.can_change)return;
    if(!form.dataset.confirming){draw(true);return;}
    const button=form.querySelector('button[type="submit"]'),output=form.querySelector('[role="status"]');
    button.disabled=true;output.textContent='Saving…';
    try{
      await api('/api/cs/contact/settings',{method:'POST',body:JSON.stringify({receipts:!r.saved,revision:r.revision??null})});
      await cswContactRules();
    }catch(err){
      button.disabled=false;
      output.textContent=err?.status===409&&err.message?err.message:err?.timeout?'EON did not hear back. Refresh this card to see whether it changed.':'Payment receipts did not change. Refresh and try again.';
    }
  };
  return form;
}

async function cswContactResults() {
  const dialog=cswContactDialog('What reached people','<p role="status">Reading the last 30 days…</p>',document.activeElement);
  let d;try{d=await api('/api/cs/contact/results');}catch(_){d=null;}
  if(!dialog.isConnected)return;
  const host=dialog.querySelector('.csw-contact-content');
  if(!d?.ok){host.innerHTML='<p>Results are unavailable. That is different from zero messages.</p>';return;}
  const labels=[['attempts','Submission attempts'],['accepted','Provider accepted'],['reached','Reached them'],['failed','Failed'],['submission_unknown','Submission unknown'],['delivery_unconfirmed','Delivery unconfirmed'],['cancelled_before_submission','Ended before submission']];
  host.innerHTML=`<p>Last ${d.days} days · shared contact receipts${d.rehearsal?' · rehearsal adds no receipts':''}</p><table><thead><tr><th>Evidence</th><th>Count</th></tr></thead><tbody>${labels.map(([key,label])=>`<tr><th scope="row">${label}</th><td>${d.counts?.[key]??'Unknown'}</td></tr>`).join('')}</tbody></table><p>${esc(d.note)}</p><p>Incremental sales: unknown. Replies and opt-outs are not yet attributed to these attempts.</p><p class="csw-contact-note">Read ${esc(cswContactWhen(d.read_at))}</p>`;
}

async function cswContactRehearseReply(cid, text) {
  const dialog=cswContactDialog('Your reply · rehearsal',`<p class="csw-contact-lead">REHEARSAL — NOTHING SENDS</p><blockquote>${esc(text)}</blockquote><p role="status">Checking the current conversation…</p><p>Your draft stays in the composer.</p>`,document.activeElement);
  try{
    const d=await api('/api/cs/contact/simulate',{method:'POST',body:JSON.stringify({thread:cid,scenario:'rep_reply',switches:cswState()._contactPreview||{}})});
    if(dialog.isConnected)dialog.querySelector('[role="status"]').textContent=d?.ok?`${d.message}. ${cswContactReason(d.rehearsed.reason)} Nothing was submitted or reserved.`:d?.message||'The current facts could not be read.';
  }catch(_){if(dialog.isConnected)dialog.querySelector('[role="status"]').textContent='The current facts could not be read. Your draft is kept. Nothing was sent.';}
}

async function cswContactRequest(cid, rehearsal) {
  const dialog=cswContactDialog('What they asked for',`<p>One size, one store, one useful follow-up. This does not sign them up for offers.</p><form class="csw-contact-request-form"><label>Find the piece<input name="query" type="search" minlength="2" placeholder="Product name or SKU" required></label><button type="button" data-contact-search>Find sizes and stores</button><label>Exact size and store<select name="item" required><option value="">Choose after searching</option></select></label><label>Their own words<select name="source" required><option value="">Choose the customer’s request</option></select></label><label>Keep this request open for<select name="days"><option value="7">7 days</option><option value="14">14 days</option><option value="30">30 days</option></select></label><p>For a store correction, the original expiry stays put.</p><label class="csw-contact-confirm"><input type="checkbox" name="confirmed" required>They asked us to follow up about this exact piece and store.</label><button type="submit">${rehearsal?'Rehearse the request':'Record request'}</button><p role="status">${rehearsal?'REHEARSAL — the real request will not change.':'Recording this request does not queue a message.'}</p></form>`,document.activeElement);
  const form=dialog.querySelector('form'),output=form.querySelector('[role="status"]');let data={items:[],sources:[]};
  const search=async()=>{
    const q=form.elements.query.value.trim();if(q.length<2){output.textContent='Type at least two letters of the piece or SKU.';return;}
    output.textContent='Finding the exact options…';
    try{data=await api(`/api/cs/threads/${encodeURIComponent(cid)}/contact/request-options?q=${encodeURIComponent(q)}`);
      if(!dialog.isConnected)return;
      form.elements.namedItem('item').innerHTML='<option value="">Choose the exact size and store</option>'+data.items.map((r,i)=>`<option value="${i}">${esc([r.product_title,r.variant_title,r.location_name].filter(Boolean).join(' · '))}</option>`).join('');
      form.elements.source.innerHTML='<option value="">Choose the customer’s request</option>'+data.sources.map((r,i)=>`<option value="${i}">${esc(r.quote)}</option>`).join('');
      output.textContent=data.items.length?(data.has_more?'Showing 60 options. Narrow the search if your piece is missing.':'Choose the exact option and the words that asked for it.'):'No verified options matched. Keep the request as a note until the piece is clear.';
    }catch(_){output.textContent='The options could not be read. No request was recorded.';}
  };
  form.querySelector('[data-contact-search]').onclick=search;
  form.onsubmit=async e=>{e.preventDefault();if(!form.reportValidity())return;
    const item=data.items[Number(form.elements.namedItem('item').value)],source=data.sources[Number(form.elements.source.value)];
    if(!item||!source)return;
    if(rehearsal){output.textContent=`Request rehearsal: ${item.product_title} · ${item.variant_title||'selected variant'} · ${item.location_name}, for ${form.elements.days.value} days. No permission was recorded and no message was queued. The sender would still recheck the request, stock and conversation.`;return;}
    const button=form.querySelector('[type="submit"]');button.disabled=true;
    try{const result=await api(`/api/cs/threads/${encodeURIComponent(cid)}/contact/request`,{method:'POST',body:JSON.stringify({variant_id:item.variant_id,location_id:item.location_id,source_message_id:source.id,expires_at:new Date(Date.now()+Number(form.elements.days.value)*86400000).toISOString(),confirmed_customer_request:true})});output.textContent=result.message||'Request recorded. Nothing has been queued or sent.';await cswContactLoad(cid);}
    catch(err){output.textContent=err.message||'The request could not be recorded.';button.disabled=false;}
  };
}
async function cswContactUpcoming(cursor = null, previous = []) {
  const host=document.getElementById('csw-contact-upcoming'); if(!host)return;
  host.hidden=false; if(!cursor)host.innerHTML='<div class="csw-card__t">Upcoming</div><p>Reading the contact plan…</p>';
  let d; try {d=await api('/api/cs/contact/upcoming'+(cursor?'?cursor='+encodeURIComponent(cursor):''));} catch(_){d=null;}
  if(!host.isConnected)return;
  if(!d?.ok){host.innerHTML='<div class="csw-card__t">Upcoming</div><p>The contact plan could not be read. An empty queue is not assumed.</p><button type="button">Try again</button>';host.querySelector('button').onclick=cswContactUpcoming;return;}
  d.rows=[...new Map([...previous,...(d.rows||[])].map(r=>[r.id,r])).values()];
  const groups=[['delivery_check','Delivery checks'],['awaiting_delivery','Accepted · waiting for delivery'],['proposed',d.rehearsal?'Proposals · rehearsal only':'Proposals · checked again before sending']];
  host.innerHTML='<div class="csw-card__t">Upcoming</div>'+groups.map(([key,label])=>{
    const rows=(d.rows||[]).filter(r=>r.group===key);
    if(!rows.length)return '';
    return `<section><h3>${esc(label)}</h3>${rows.map(r=>`<article class="csw-contact-row"><div class="csw-contact-rowhead"><strong>${esc(r.customer_name||'Customer')}</strong><span>${esc(r.receipt?.label||r.status)}</span></div><p>${esc(cswContactReason(r.reason))}</p>${r.check_id?`<p>${['completed','done','verified'].includes(r.check_status)?'Check recorded':'Owned check'} · ${esc(r.check_owner||'Owner unreadable')} · ${r.check_due_at?'due '+esc(cswContactWhen(r.check_due_at)):'Deadline unreadable'}. Delivery status stays as shown.</p>`:''}${r.thread?`<button type="button" data-contact-thread="${esc(r.thread)}">Open conversation</button>`:'<p>The conversation link needs checking.</p>'}</article>`).join('')}</section>`;
  }).join('')+(!(d.rows||[]).length?(d.has_more?'<p>This partial view has no matching records. Open a conversation for its full plan.</p>':'<p>No shared contact proposals or receipt checks are listed.</p>'):'')+`<p class="csw-contact-note">${esc(d.coverage)} Read ${esc(cswContactWhen(d.read_at))}.${d.has_more?' More records are available.':''}</p>`+(d.next_cursor?'<button type="button" data-contact-more>Load more</button>':'');
  const more=host.querySelector('[data-contact-more]');if(more)more.onclick=()=>{more.disabled=true;more.textContent='Reading…';cswContactUpcoming(d.next_cursor,d.rows);};
  host.querySelectorAll('[data-contact-thread]').forEach(b=>b.onclick=()=>{cswState().mode='inbox';cswRenderBody();cswOpenThread(b.dataset.contactThread);});
}


function cswContactResolveCheck(task) {
  const dialog=cswContactDialog('What did the check establish?',`<p>${esc(task.reason_text||'Contact check')} · due ${esc(cswContactWhen(task.due_at))}</p><p>Recording a check never changes a provider receipt or schedules a retry.</p><form class="csw-contact-request-form"><label>Result<select name="resolution"><option value="checked_outcome_unknown">Checked; delivery is still unknown</option><option value="provider_receipt_verified">Verified delivery or failure receipt</option><option value="need_ended">The customer no longer needs this follow-up</option></select></label><label>What you checked<textarea name="note" minlength="10" maxlength="1200" required></textarea></label><button type="submit">${task.rehearsal?'Rehearse the result':'Record the check'}</button><p role="status"></p></form>`,document.activeElement);
  const form=dialog.querySelector('form');
  form.onsubmit=async e=>{e.preventDefault();if(!form.reportValidity())return;
    const output=form.querySelector('[role="status"]'),button=form.querySelector('button');if(task.rehearsal){output.textContent='REHEARSAL — this would record your investigation, while keeping delivery uncertainty and the no-retry rule intact. The real task is unchanged.';return;}button.disabled=true;
    try{const result=await api(`/api/cs/threads/${encodeURIComponent(task.zoko_customer_id)}/contact/action`,{method:'POST',body:JSON.stringify({action:'resolve_check',intent_id:task.intent_id,task_id:task.contact_task_id,resolution:form.elements.resolution.value,note:form.elements.note.value})});output.textContent=result.message;_cswPrio.at=0;await cswLoadPriority();}
    catch(err){output.textContent=err.message||'The check was not recorded.';button.disabled=false;}
  };
}
